<template>
  <div
    class="spreadsheet-tool"
    :class="{
      'spreadsheet-tool--automation-active': caseAutomation.active && !caseAutomation.paused,
      'spreadsheet-tool--intake-preparing': caseIntakePreparation.active,
      'spreadsheet-tool--guided-workflow-active': caseAttributionWorkflowRun.active || caseCleanupWorkflowRun.active,
      'spreadsheet-tool--manual-all-flows-active': caseManualStartAllFlowsAutoActive,
      'spreadsheet-tool--manual-visualizer-opening': caseManualStartVisualizerOpening.active,
      'spreadsheet-tool--quick-lookup-active': caseQuickLookupVisible,
      'spreadsheet-tool--quick-lookup-busy': caseQuickLookupBusy,
      'spreadsheet-tool--dark-mode': caseWorkspaceDarkModeActive
    }"
    :aria-busy="casePageHydrating || caseGlobalOperation.active || caseModalOperation.active || caseAutomation.busy || caseIntakePreparation.active || caseAttributionWorkflowRun.active || caseCleanupWorkflowRun.active || caseManualStartAllFlowsAutoActive || caseManualStartVisualizerOpening.active || caseQuickLookupBusy"
  >
    <div v-if="inputFullscreen" class="sheet-backdrop" @click="setInputFullscreen(false)"></div>

    <div
      v-if="caseManualStartVisualizerOpening.active"
      ref="caseManualStartVisualizerOpeningLock"
      class="case-intake-preparation-lock case-manual-start-opening-lock"
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-manual-start-opening-title"
      @click.stop
    >
      <div class="case-intake-preparation-lock__scrim" aria-hidden="true"></div>
      <section
        ref="caseManualStartVisualizerOpeningCard"
        class="case-intake-preparation-lock__card case-manual-start-opening-lock__card"
        tabindex="-1"
        aria-live="polite"
      >
        <header class="case-intake-preparation-lock__header">
          <div>
            <span>Flow Solver</span>
            <h2 id="case-manual-start-opening-title">Opening Manual Solve</h2>
            <p>{{ caseManualStartVisualizerOpening.message || 'Reading the current case and preparing the visual workspace.' }}</p>
          </div>
          <span class="case-intake-preparation-lock__root-count">
            {{ caseManualStartVisualizerOpeningCountLabel }}
          </span>
        </header>

        <div
          class="case-intake-preparation-lock__meter"
          role="progressbar"
          :aria-valuenow="caseManualStartVisualizerOpening.progressPercent"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <span :style="caseManualStartVisualizerOpeningProgressStyle"></span>
        </div>

        <ol class="case-intake-preparation-lock__steps">
          <li
            v-for="step in caseManualStartVisualizerOpeningStepRows"
            :key="`case-manual-open-${step.key}`"
            :class="step.className"
          >
            <span class="case-intake-preparation-lock__step-marker" aria-hidden="true">
              {{ step.complete ? '✓' : step.active ? '…' : step.number }}
            </span>
            <span>
              <strong>{{ step.title }}</strong>
              <small>{{ step.detail }}</small>
            </span>
          </li>
        </ol>

        <div class="case-intake-preparation-lock__footer case-manual-start-opening-lock__footer">
          <p>{{ caseManualStartVisualizerOpening.detail || 'Only missing starting-transaction details are looked up. Existing case data and completed work are reused.' }}</p>
          <div class="case-manual-start-opening-lock__actions">
            <button
              ref="caseManualStartVisualizerOpeningOpenButton"
              class="toolbar-btn toolbar-btn--primary"
              type="button"
              :disabled="!caseManualStartVisualizerOpeningCanOpenCurrent"
              title="Skip the remaining startup lookup and open from the case data already available"
              @click="requestCaseManualStartVisualizerOpeningOpenNow"
            >
              Open now
            </button>
            <button
              ref="caseManualStartVisualizerOpeningCancelButton"
              class="toolbar-btn toolbar-btn--ghost"
              type="button"
              :disabled="caseManualStartVisualizerOpening.stopRequested"
              @click="requestCaseManualStartVisualizerOpeningCancel"
            >
              Cancel
            </button>
          </div>
        </div>
      </section>
    </div>

    <div
      v-if="caseIntakePreparation.active"
      ref="caseIntakePreparationLock"
      class="case-intake-preparation-lock"
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-intake-preparation-title"
      @click.stop
      @keydown.tab.prevent
    >
      <div class="case-intake-preparation-lock__scrim" aria-hidden="true"></div>
      <section class="case-intake-preparation-lock__card" aria-live="polite">
        <header class="case-intake-preparation-lock__header">
          <div>
            <span>Case preparation</span>
            <h2 id="case-intake-preparation-title">Getting the case ready</h2>
            <p>{{ caseIntakePreparation.message || 'Checking the first five workflow steps.' }}</p>
          </div>
          <span class="case-intake-preparation-lock__root-count">
            {{ caseIntakeRailRootCount }} root{{ caseIntakeRailRootCount === 1 ? '' : 's' }}
          </span>
        </header>

        <div
          class="case-intake-preparation-lock__meter"
          role="progressbar"
          :aria-valuenow="caseIntakePreparationProgressPercent"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <span :style="caseIntakePreparationProgressStyle"></span>
        </div>

        <ol class="case-intake-preparation-lock__steps">
          <li
            v-for="step in caseIntakeRailStepRows"
            :key="`case-intake-lock-${step.key}`"
            :class="step.className"
          >
            <span class="case-intake-preparation-lock__step-marker" aria-hidden="true">
              {{ step.runtimeComplete ? '✓' : step.runtimeActive ? '•' : step.index + 1 }}
            </span>
            <span>
              <strong>{{ step.label }}</strong>
              <small>{{ step.runtimeStatusLabel }}</small>
            </span>
          </li>
        </ol>

        <div class="case-intake-preparation-lock__footer">
          <p>{{ caseIntakePreparation.detail || 'The spreadsheet and Case Buddy controls are temporarily locked so the intake steps cannot be interrupted by edits.' }}</p>
          <button
            ref="caseIntakePreparationStopButton"
            class="toolbar-btn toolbar-btn--ghost case-intake-preparation-lock__stop"
            type="button"
            @click="requestCaseIntakePreparationStop('Preparation stopped by user.', { user: true })"
          >
            Stop preparation
          </button>
        </div>
      </section>
    </div>


    <div
      v-if="caseAttributionWorkflowRun.active"
      ref="caseAttributionWorkflowLock"
      class="case-intake-preparation-lock case-attribution-workflow-lock"
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-attribution-workflow-title"
      @click.stop
      @keydown.tab.prevent
    >
      <div class="case-intake-preparation-lock__scrim" aria-hidden="true"></div>
      <section class="case-intake-preparation-lock__card" aria-live="polite">
        <header class="case-intake-preparation-lock__header">
          <div>
            <span>Guided workflow</span>
            <h2 id="case-attribution-workflow-title">Completing attribution &amp; path decisions</h2>
            <p>{{ caseAttributionWorkflowRun.message || 'Working through the next six workflow stages.' }}</p>
          </div>
          <span class="case-intake-preparation-lock__root-count">
            {{ caseAttributionWorkflowCompletedCount }}/{{ caseAttributionWorkflowStepRows.length || 6 }} complete
          </span>
        </header>

        <div
          class="case-intake-preparation-lock__meter"
          role="progressbar"
          :aria-valuenow="caseAttributionWorkflowProgressPercent"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <span :style="caseAttributionWorkflowProgressStyle"></span>
        </div>

        <ol class="case-intake-preparation-lock__steps">
          <li
            v-for="step in caseAttributionWorkflowStepRows"
            :key="`case-attribution-lock-${step.key}`"
            :class="step.className"
          >
            <span class="case-intake-preparation-lock__step-marker" aria-hidden="true">
              {{ step.runtimeComplete ? '✓' : step.runtimeActive ? '…' : step.number }}
            </span>
            <span>
              <strong>{{ step.title }}</strong>
              <small>{{ step.runtimeStatusLabel }}</small>
            </span>
          </li>
        </ol>

        <div class="case-intake-preparation-lock__footer">
          <p>{{ caseAttributionWorkflowRun.detail || 'Case Buddy is using the existing workflow tools one action at a time. Completed work remains in the case if you stop.' }}</p>
          <button
            ref="caseAttributionWorkflowStopButton"
            class="toolbar-btn toolbar-btn--ghost case-intake-preparation-lock__stop"
            type="button"
            @click="requestCaseAttributionWorkflowStop('Attribution workflow stopped by user.', { user: true })"
          >
            Stop workflow
          </button>
        </div>
      </section>
    </div>


    <div
      v-if="caseCleanupWorkflowRun.active"
      ref="caseCleanupWorkflowLock"
      class="case-intake-preparation-lock case-cleanup-workflow-lock"
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-cleanup-workflow-title"
      @click.stop
      @keydown.tab.prevent
    >
      <div class="case-intake-preparation-lock__scrim" aria-hidden="true"></div>
      <section class="case-intake-preparation-lock__card" aria-live="polite">
        <header class="case-intake-preparation-lock__header">
          <div>
            <span>Guided workflow</span>
            <h2 id="case-cleanup-workflow-title">Completing consistency &amp; cleanup</h2>
            <p>{{ caseCleanupWorkflowRun.message || 'Working through the next seven workflow stages.' }}</p>
          </div>
          <span class="case-intake-preparation-lock__root-count">
            {{ caseCleanupWorkflowCompletedCount }}/{{ caseCleanupWorkflowStepRows.length || 7 }} complete
          </span>
        </header>

        <div
          class="case-intake-preparation-lock__meter"
          role="progressbar"
          :aria-valuenow="caseCleanupWorkflowProgressPercent"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <span :style="caseCleanupWorkflowProgressStyle"></span>
        </div>

        <ol class="case-intake-preparation-lock__steps">
          <li
            v-for="step in caseCleanupWorkflowStepRows"
            :key="`case-cleanup-lock-${step.key}`"
            :class="step.className"
          >
            <span class="case-intake-preparation-lock__step-marker" aria-hidden="true">
              {{ step.runtimeComplete ? '✓' : step.runtimeActive ? '…' : step.number }}
            </span>
            <span>
              <strong>{{ step.title }}</strong>
              <small>{{ step.runtimeStatusLabel }}</small>
            </span>
          </li>
        </ol>

        <div class="case-intake-preparation-lock__footer">
          <p>{{ caseCleanupWorkflowRun.detail || 'Case Buddy is using the existing workflow tools one action at a time. Completed work remains in the case if you stop.' }}</p>
          <button
            ref="caseCleanupWorkflowStopButton"
            class="toolbar-btn toolbar-btn--ghost case-intake-preparation-lock__stop"
            type="button"
            @click="requestCaseCleanupWorkflowStop('Consistency and cleanup workflow stopped by user.', { user: true })"
          >
            Stop workflow
          </button>
        </div>
      </section>
    </div>

    <div v-if="casePageHydrating" class="case-loading-backdrop" role="status" aria-live="polite">
      <div class="case-loading-card">
        <div class="case-loading-card__spinner" aria-hidden="true"></div>
        <div class="case-loading-card__body">
          <p class="eyebrow-label">Case Buddy</p>
          <h2>Loading case workspace</h2>
          <p>{{ casePageHydrationMessage }}</p>
          <div class="case-loading-card__meter" role="progressbar" :aria-valuenow="casePageLoadingPercent" aria-valuemin="0" aria-valuemax="100">
            <span :style="casePageLoadingProgressStyle"></span>
          </div>
          <div class="case-loading-card__steps">
            <span :class="{ 'case-loading-card__step--done': !!currentProjectFileId }">Case file</span>
            <span :class="{ 'case-loading-card__step--done': projectFileReadyState.input }">Input workbook</span>
            <span :class="{ 'case-loading-card__step--done': projectFileReadyState.output }">Output sheet</span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="caseQuickLookupVisible"
      class="case-quick-lookup"
      :class="{
        'case-quick-lookup--behind-modal': caseQuickLookupBehindModal,
        'case-quick-lookup--busy': caseQuickLookupBusy
      }"
      role="dialog"
      aria-modal="true"
      aria-label="Case Buddy transaction lookup"
      @click.stop="closeCaseQuickLookupCaseSwitcher"
    >
      <div class="case-quick-lookup__backdrop" aria-hidden="true">
        <span class="case-quick-lookup__orb case-quick-lookup__orb--one"></span>
        <span class="case-quick-lookup__orb case-quick-lookup__orb--two"></span>
        <svg class="case-quick-lookup__network" viewBox="0 0 1200 720" preserveAspectRatio="none">
          <path d="M-80 540 C180 380 290 660 520 430 S870 210 1280 370"></path>
          <path d="M-40 230 C210 70 410 300 650 170 S960 90 1240 210"></path>
          <circle cx="190" cy="430" r="7"></circle>
          <circle cx="520" cy="430" r="8"></circle>
          <circle cx="820" cy="275" r="7"></circle>
          <circle cx="1030" cy="345" r="9"></circle>
        </svg>
      </div>

      <header class="case-quick-lookup__topbar">
        <button class="case-quick-lookup__brand" type="button" title="Start another transaction lookup" :disabled="caseQuickLookupBusy" @click="resetCaseQuickLookup">
          <span class="case-quick-lookup__brand-mark" aria-hidden="true">
            <svg viewBox="0 0 42 42">
              <circle cx="8.5" cy="21" r="5.5"></circle>
              <rect x="18" y="15" width="12" height="12" rx="3"></rect>
              <circle cx="36.5" cy="21" r="4.5"></circle>
              <path d="M14 21h4M30 21h2"></path>
            </svg>
          </span>
          <span>CASE BUDDY</span>
        </button>
        <nav class="case-quick-lookup__view-nav" aria-label="Case Buddy view">
          <button type="button" :disabled="caseQuickLookupBusy" @click="openCaseWorkspaceFromQuickLookup">Advanced workspace</button>
        </nav>
      </header>

      <main class="case-quick-lookup__main">
        <section class="case-quick-lookup__hero" :class="`case-quick-lookup__hero--${caseQuickLookup.status || 'idle'}`">
          <div v-if="caseQuickLookupShowsInput" class="case-quick-lookup__lookup-card">
            <div class="case-quick-lookup__logo" aria-hidden="true">
              <svg viewBox="0 0 72 72">
                <circle cx="13" cy="36" r="9"></circle>
                <rect x="27" y="25" width="20" height="22" rx="5"></rect>
                <circle cx="61" cy="36" r="8"></circle>
                <path d="M22 36h5M47 36h6"></path>
              </svg>
            </div>
            <p class="case-quick-lookup__eyebrow">CASE BUDDY</p>
            <h1>Follow a blockchain transaction</h1>
            <p class="case-quick-lookup__lead">Submit a full transaction hash. Case Buddy will add it to the current case, show the flow of funds, and build a report.</p>

            <form class="case-quick-lookup__form" @submit.prevent="submitCaseQuickLookup">
              <label class="sr-only" for="case-quick-lookup-hash">Transaction hash</label>
              <input
                id="case-quick-lookup-hash"
                ref="caseQuickLookupInput"
                v-model.trim="caseQuickLookupHashDraft"
                type="text"
                inputmode="text"
                autocomplete="off"
                autocapitalize="off"
                spellcheck="false"
                placeholder="Paste a transaction hash"
                :disabled="caseQuickLookupBusy"
              />
              <button type="submit" :disabled="!caseQuickLookupCanSubmit">
                <span>{{ caseQuickLookupBusy ? 'Preparing…' : 'Analyze' }}</span>
                <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"></path></svg>
              </button>
            </form>
            <p class="case-quick-lookup__input-note">A case is created automatically when none is open.</p>

            <div v-if="caseQuickLookup.status === 'error'" class="case-quick-lookup__notice case-quick-lookup__notice--error" role="alert">
              <strong>{{ caseQuickLookupStatusTitle }}</strong>
              <span>{{ caseQuickLookup.error || caseQuickLookup.message }}</span>
            </div>
          </div>

          <div v-else-if="caseQuickLookup.status === 'choice'" class="case-quick-lookup__choice-card">
            <header>
              <span>One decision needed</span>
              <h1>{{ caseQuickLookupChoiceTitle }}</h1>
              <p>{{ caseQuickLookup.message }}</p>
            </header>
            <div v-if="caseQuickLookupChoiceFilterVisible" class="case-quick-lookup__choice-filter">
              <label for="case-quick-lookup-choice-filter">Find the right {{ caseQuickLookupChoiceFilterNoun }}</label>
              <div class="case-quick-lookup__choice-filter-control">
                <svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="8.5" cy="8.5" r="4.75"></circle><path d="m12 12 4 4"></path></svg>
                <input
                  id="case-quick-lookup-choice-filter"
                  ref="caseQuickLookupChoiceFilterInput"
                  v-model="caseQuickLookupChoiceFilter"
                  type="search"
                  inputmode="search"
                  autocomplete="off"
                  autocapitalize="off"
                  spellcheck="false"
                  :placeholder="caseQuickLookupChoiceFilterPlaceholder"
                  aria-describedby="case-quick-lookup-choice-filter-summary"
                  @keydown.esc.stop.prevent="handleCaseQuickLookupChoiceFilterEscape"
                />
                <button
                  v-if="caseQuickLookupChoiceFilterActive"
                  type="button"
                  aria-label="Clear choice filter"
                  title="Clear filter"
                  @click="clearCaseQuickLookupChoiceFilter"
                >
                  <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m6 6 8 8M14 6l-8 8"></path></svg>
                </button>
              </div>
              <small id="case-quick-lookup-choice-filter-summary" aria-live="polite">{{ caseQuickLookupChoiceFilterSummary }}</small>
            </div>
            <div v-if="caseQuickLookupFilteredChoiceRows.length" class="case-quick-lookup__choice-list" role="radiogroup" :aria-label="caseQuickLookupChoiceTitle">
              <button
                v-for="choice in caseQuickLookupFilteredChoiceRows"
                :key="choice.key"
                class="case-quick-lookup__choice"
                :class="{ 'case-quick-lookup__choice--selected': choice.key === caseQuickLookup.selectedChoiceKey }"
                type="button"
                role="radio"
                :aria-checked="choice.key === caseQuickLookup.selectedChoiceKey ? 'true' : 'false'"
                @click="selectCaseQuickLookupChoice(choice)"
              >
                <span>{{ caseQuickLookupChoiceKindLabel(choice) }}</span>
                <strong>{{ caseQuickLookupChoiceAmountLabel(choice) }}</strong>
                <small :title="caseQuickLookupChoiceAddressValue(choice)">{{ caseQuickLookupChoiceAddressLabel(choice) }}</small>
              </button>
            </div>
            <div v-else class="case-quick-lookup__choice-empty" role="status">
              <strong>No matching {{ caseQuickLookupChoiceFilterNoun }} found</strong>
              <span>{{ caseQuickLookupChoiceFilterEmptyHint }}</span>
              <button class="case-quick-lookup__secondary-btn" type="button" @click="clearCaseQuickLookupChoiceFilter">Clear filter</button>
            </div>
            <div class="case-quick-lookup__choice-actions">
              <button class="case-quick-lookup__secondary-btn" type="button" @click="resetCaseQuickLookup">Use a different hash</button>
              <button class="case-quick-lookup__primary-btn" type="button" :disabled="!caseQuickLookupSelectedChoice || caseQuickLookupBusy" @click="applyCaseQuickLookupChoice">
                Follow selected transfer
              </button>
            </div>
          </div>

          <div v-else-if="caseQuickLookupShowsResult" class="case-quick-lookup__result-card" :class="`case-quick-lookup__result-card--${caseQuickLookup.status}`">
            <div class="case-quick-lookup__result-icon" aria-hidden="true">
              <svg v-if="caseQuickLookup.status === 'complete' || caseQuickLookup.status === 'report-ready'" viewBox="0 0 28 28"><path d="M6 14.5l5 5L22 8.5"></path></svg>
              <svg v-else viewBox="0 0 28 28"><path d="M14 7v8M14 20h.01"></path></svg>
            </div>
            <p class="case-quick-lookup__eyebrow">{{ caseQuickLookup.status === 'report-ready' ? 'REPORT READY' : 'FLOW SOLVER' }}</p>
            <h1>{{ caseQuickLookupStatusTitle }}</h1>
            <p>{{ caseQuickLookup.message }}</p>
            <code v-if="caseQuickLookup.activeHash" :title="caseQuickLookup.activeHash">{{ caseQuickLookupHashPreview }}</code>
            <div class="case-quick-lookup__result-actions">
              <button v-if="caseQuickLookup.status === 'report-ready'" class="case-quick-lookup__primary-btn" type="button" :disabled="caseFullReportPackagePdfPreparing" @click="openCaseQuickLookupFinalReport">Open final report</button>
              <button
                v-if="caseQuickLookup.status === 'report-ready'"
                class="case-quick-lookup__secondary-btn"
                type="button"
                :disabled="caseFullReportPackagePdfPreparing"
                title="Open the browser PDF dialog with the report and its embedded flow visuals"
                @click="downloadCaseFullReportPackagePdf({ source: 'case-quick-lookup-report-ready', ensureReady: true })"
              >
                {{ caseFullReportPackagePdfPreparing ? 'Preparing PDF…' : 'Save PDF' }}
              </button>
              <button v-else-if="caseQuickLookupCanCreateReport" class="case-quick-lookup__primary-btn" type="button" :disabled="caseQuickLookupBusy" @click="createCaseQuickLookupFullReport">Create full report</button>
              <button
                v-if="caseQuickLookupCanAcceptReportBoundaries"
                class="case-quick-lookup__primary-btn"
                type="button"
                :disabled="caseQuickLookupBusy"
                @click="openCaseQuickLookupReportBoundaryConfirmation"
              >
                {{ caseQuickLookupAcceptReportBoundariesLabel }}
              </button>
              <button
                v-if="caseQuickLookup.status === 'partial'"
                :class="caseQuickLookupCanAcceptReportBoundaries ? 'case-quick-lookup__secondary-btn' : 'case-quick-lookup__primary-btn'"
                type="button"
                :disabled="caseQuickLookupBusy"
                @click="resumeCaseQuickLookupAnalysis"
              >
                Continue in Flow Solver
              </button>
              <button class="case-quick-lookup__secondary-btn" type="button" :disabled="caseQuickLookupBusy" @click="resetCaseQuickLookup">Analyze another transaction</button>
              <button class="case-quick-lookup__text-btn" type="button" :disabled="caseQuickLookupBusy" @click="openCaseWorkspaceFromQuickLookup">Open case workspace</button>
            </div>
          </div>

          <div v-else class="case-quick-lookup__progress-card" role="status" aria-live="polite">
            <div class="case-quick-lookup__spinner" aria-hidden="true"><span></span><span></span><span></span></div>
            <p class="case-quick-lookup__eyebrow">{{ caseQuickLookupStatusEyebrow }}</p>
            <h1>{{ caseQuickLookupStatusTitle }}</h1>
            <p>{{ caseQuickLookup.message || 'Preparing the transaction…' }}</p>
            <small v-if="caseQuickLookup.detail">{{ caseQuickLookup.detail }}</small>
            <code v-if="caseQuickLookup.activeHash" :title="caseQuickLookup.activeHash">{{ caseQuickLookupHashPreview }}</code>
            <div class="case-quick-lookup__meter" role="progressbar" :aria-valuenow="caseQuickLookup.progress" aria-valuemin="0" aria-valuemax="100"><span :style="caseQuickLookupProgressStyle"></span></div>
            <button v-if="caseQuickLookupCanCancel" class="case-quick-lookup__text-btn" type="button" @click="cancelCaseQuickLookupRun">Cancel</button>
          </div>
        </section>
      </main>

      <div class="case-quick-lookup__case-switcher" @click.stop>
        <button
          class="case-quick-lookup__case-pill"
          type="button"
          aria-haspopup="listbox"
          :aria-expanded="caseQuickLookupCaseSwitcherOpen ? 'true' : 'false'"
          :title="`Switch case · ${currentProjectFileName}`"
          :disabled="caseQuickLookupBusy"
          @click.stop="toggleCaseQuickLookupCaseSwitcher"
        >
          <span class="case-quick-lookup__case-pill-dot" aria-hidden="true"></span>
          <span class="case-quick-lookup__case-pill-label">{{ caseQuickLookupCurrentCaseLabel }}</span>
          <svg class="case-quick-lookup__case-pill-chevron" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4"></path></svg>
        </button>

        <div v-if="caseQuickLookupCaseSwitcherOpen" class="case-quick-lookup__case-menu" role="listbox" aria-label="Switch Case Buddy case">
          <header>
            <strong>Cases</strong>
            <small>{{ caseQuickLookupCaseSwitcherRows.length }}</small>
          </header>
          <div class="case-quick-lookup__case-menu-list">
            <button
              v-for="file in caseQuickLookupCaseSwitcherRows"
              :key="`quick-case-${file.id}`"
              type="button"
              role="option"
              :aria-selected="file.id === currentProjectFileId ? 'true' : 'false'"
              :class="{ 'is-current': file.id === currentProjectFileId }"
              :disabled="caseQuickLookupCaseSwitchBusy"
              @click.stop="switchCaseQuickLookupProjectFile(file.id)"
            >
              <span class="case-quick-lookup__case-menu-dot" aria-hidden="true"></span>
              <span>
                <strong>{{ file.name || 'Untitled case' }}</strong>
                <small>{{ file.id === currentProjectFileId ? 'Current case' : (file.updatedAt ? `Saved ${formatProjectFileDate(file.updatedAt)}` : 'Saved case') }}</small>
              </span>
              <svg v-if="file.id === currentProjectFileId" viewBox="0 0 18 18" aria-hidden="true"><path d="M4 9.5l3 3L14 5.5"></path></svg>
            </button>
          </div>
          <button class="case-quick-lookup__case-menu-add" type="button" :disabled="caseQuickLookupCaseSwitchBusy" @click.stop="createCaseQuickLookupNewCase">
            <span aria-hidden="true">+</span>
            <strong>Add new case</strong>
          </button>
        </div>
      </div>

      <div class="case-quick-lookup__utility-dock" aria-label="Case Buddy lookup tools">
        <button type="button" aria-label="Flow Solver settings" title="Flow Solver settings" :disabled="caseQuickLookupBusy" @click="openCaseQuickLookupSettings">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3.25"></circle><path d="M12 2.75v2.1M12 19.15v2.1M2.75 12h2.1M19.15 12h2.1M5.46 5.46l1.49 1.49M17.05 17.05l1.49 1.49M18.54 5.46l-1.49 1.49M6.95 17.05l-1.49 1.49"></path><circle cx="12" cy="12" r="7.15"></circle></svg>
        </button>
        <button type="button" aria-label="Open case management" title="Case management" :disabled="caseQuickLookupBusy" @click="openCaseQuickLookupCaseManagement">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6.5h7l1.7 2H21v10.5H3Z"></path><path d="M3 6.5V5h7l1.7 2"></path></svg>
        </button>
      </div>

      <section v-if="caseQuickLookupSettingsOpen" class="case-quick-lookup__settings" role="dialog" aria-modal="true" aria-label="Flow Solver settings" @click.stop>
        <header>
          <div><span>Shared settings</span><strong>Flow Solver</strong></div>
          <button class="case-quick-lookup__settings-close" type="button" aria-label="Close settings" title="Close settings" @click="closeCaseQuickLookupSettings">
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15"></path></svg>
          </button>
        </header>
        <label><span>Direction</span><select v-model="caseManualStartSettingsDraft.traceDirection"><option value="forward">Follow forward</option><option value="reverse">Trace backward</option></select></label>
        <label><span>Hop logic</span><select v-model="caseManualStartSettingsDraft.hopLogicType"><option value="FIFO">FIFO</option><option value="LIFO">LIFO / PTDQ</option><option value="NEXT_CHRONOLOGICAL">Next chronological</option></select></label>
        <label><span>Maximum depth</span><input v-model.number="caseManualStartSettingsDraft.flowCompletionMaxDepth" type="number" min="1" max="500" /></label>
        <label class="case-quick-lookup__settings-check"><input v-model="caseManualStartSettingsDraft.enableAdditionalServiceChecks" type="checkbox" /><span>Check for service and hot-wallet endings</span></label>
        <label class="case-quick-lookup__settings-check"><input v-model="caseManualStartSettingsDraft.enableSessionDataCache" type="checkbox" /><span>Reuse provider data during this session</span></label>
        <footer>
          <button class="case-quick-lookup__text-btn" type="button" @click="resetCaseQuickLookupSettings">Reset</button>
          <button class="case-quick-lookup__primary-btn" type="button" @click="saveCaseQuickLookupSettings">Save settings</button>
        </footer>
      </section>
    </div>

    <div v-if="caseSimpleLaunchVisible" class="case-simple-launch" role="dialog" aria-modal="true" aria-label="Legacy guided Case Buddy view" @click.stop>
      <div class="case-simple-launch__scrim" aria-hidden="true"></div>
      <section class="case-simple-launch__card">
        <header class="case-simple-launch__header">
          <div>
            <button
              class="case-simple-launch__eyebrow case-simple-launch__eyebrow-action"
              type="button"
              title="Open the current Simple view"
              @click="openCaseQuickLookupFromSimpleLaunch"
            >Legacy guided view</button>
            <h2>Case Buddy - Blockchain Investigation Tool</h2>
            <p>
              Use this simplified workflow to complete your report.  Alternatively, to work in advanced mode, you can close this panel, 
            </p>
          </div>
          <button class="case-simple-launch__close" type="button" aria-label="Close legacy guided view" @click="dismissCaseSimpleLaunch">
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div class="case-simple-launch__steps" aria-label="Simple workflow">
          <article :class="caseSimpleLaunchStepClass('case')">
            <span>1</span>
            <strong class="case-simple-launch__step-title">
              <button
                class="case-simple-launch__step-text-action"
                type="button"
                title="Create a new case"
                @click.stop="createCaseSimpleLaunchCase"
              >Create</button>
              <span class="case-simple-launch__step-title-separator"> or </span>
              <button
                class="case-simple-launch__step-text-action"
                type="button"
                :aria-expanded="caseSimpleFileChooserVisible ? 'true' : 'false'"
                title="Choose a saved case"
                @click.stop="toggleCaseSimpleFileChooser"
              >choose a case</button>
            </strong>
            <button
              class="case-simple-launch__case-picker"
              type="button"
              :aria-expanded="caseSimpleFileChooserVisible ? 'true' : 'false'"
              title="Choose a different saved spreadsheet file"
              @click.stop="toggleCaseSimpleFileChooser"
            >
              {{ currentProjectFileName }}
            </button>
          </article>
          <article :class="caseSimpleLaunchStepClass('transactions')">
            <span>2</span>
            <strong class="case-simple-launch__step-title">
              <button
                v-if="caseSimpleLaunchCanLoadStartingTransactions"
                class="case-simple-launch__step-text-action"
                type="button"
                title="Load starting transactions into Main Data"
                @click.stop="openCaseSimpleLaunchStartingTransactions"
              >Load starting transactions</button>
              <span v-else title="Create or choose a case first">Load starting transactions</span>
            </strong>
            <small>{{ caseSimpleLaunchStartingRowsLabel }}</small>
          </article>
          <article :class="caseSimpleLaunchStepClass('auto')">
            <span>3</span>
            <strong class="case-simple-launch__step-title">
              <button
                v-if="caseSimpleLaunchCanRunAuto"
                class="case-simple-launch__step-text-action"
                type="button"
                :disabled="caseSimpleLaunchAutoStarting || caseAutomation.active || caseAutomation.busy || caseManualStartViewportAutoActive || caseManualStartAllFlowsAutoActive"
                title="Run Auto to build the case and final report"
                @click.stop="startCaseSimpleLaunchAuto"
              >Run Auto</button>
              <span v-else title="Load starting transactions first">Run Auto</span>
            </strong>
            <small>Automatically follow funds to their destinations.</small>
          </article>
          <button
            class="case-simple-launch__step case-simple-launch__step-button"
            :class="caseSimpleLaunchStepClass('report')"
            type="button"
            :disabled="!caseSimpleLaunchReportReady"
            :title="caseSimpleLaunchReportReady ? 'Open the final one-click report package' : 'The final report opens when Auto completes.'"
            @click="openCaseSimpleLaunchFinalReport"
          >
            <span>4</span>
            <strong>Review final report</strong>
            <small>{{ caseSimpleLaunchReportLabel }}</small>
          </button>
        </div>

        <div v-if="caseSimpleFileChooserVisible" class="case-simple-launch__file-chooser" aria-label="Choose a saved case file">
          <div class="case-simple-launch__file-chooser-head">
            <strong>Choose a saved case</strong>
            <button type="button" @click="closeCaseSimpleFileChooser">Close</button>
          </div>
          <div class="case-simple-launch__file-list">
            <button
              v-for="file in caseSimpleLaunchFileOptions"
              :key="file.id"
              type="button"
              :class="{ 'case-simple-launch__file-item--active': file.id === currentProjectFileId }"
              @click="loadCaseSimpleLaunchProjectFile(file.id)"
            >
              <strong>{{ file.name }}</strong>
              <small>{{ file.updatedAt ? `Last saved ${formatProjectFileDate(file.updatedAt)}` : 'No saved timestamp yet' }}</small>
            </button>
          </div>
        </div>

        <div class="case-simple-launch__actions">
          <button class="toolbar-btn" type="button" @click="createCaseSimpleLaunchCase">
            Create a new case
          </button>
          <button
            v-if="caseSimpleLaunchCanLoadStartingTransactions"
            class="toolbar-btn toolbar-btn--primary"
            type="button"
            @click="openCaseSimpleLaunchStartingTransactions"
          >
            Load starting transactions
          </button>
          <label v-if="caseSimpleLaunchCanRunAuto" class="case-simple-launch__toggle">
            <input v-model="caseSkipResolvableReviewsEnabled" type="checkbox" />
            <span>100% Automatic Mode</span>
          </label>
          <button
            v-if="caseSimpleLaunchCanRunAuto"
            class="toolbar-btn toolbar-btn--accent"
            type="button"
            :disabled="caseSimpleLaunchAutoStarting || caseAutomation.active || caseAutomation.busy || caseManualStartViewportAutoActive || caseManualStartAllFlowsAutoActive"
            @click="startCaseSimpleLaunchAuto"
          >
            {{ caseSimpleLaunchAutoStarting ? 'Starting Auto…' : 'Run Auto to final report' }}
          </button>
          <button class="toolbar-btn toolbar-btn--ghost case-simple-launch__full-workspace" type="button" @click="minimizeCaseSimpleLaunch">
            Show full workspace
          </button>
        </div>

        <button
          v-if="caseSimpleLaunchReportReady"
          class="case-simple-launch__status case-simple-launch__status--action"
          type="button"
          title="Open the final one-click report package"
          @click="openCaseSimpleLaunchFinalReport"
        >
          <strong>{{ caseSimpleLaunchStatusTitle }}</strong>
          <span>{{ caseSimpleLaunchStatusDetail }}</span>
          <em>Open final report</em>
        </button>
        <div v-else class="case-simple-launch__status">
          <strong>{{ caseSimpleLaunchStatusTitle }}</strong>
          <span>{{ caseSimpleLaunchStatusDetail }}</span>
        </div>
      </section>
    </div>

    <div v-if="caseSimpleDetailsVisible" class="case-simple-details" role="dialog" aria-modal="true" aria-label="Basic case details" @click.stop>
      <div class="case-simple-details__scrim" aria-hidden="true"></div>
      <section class="case-simple-details__card">
        <header class="case-simple-details__header">
          <div>
            <span class="case-simple-launch__eyebrow">Case setup</span>
            <h2>Add basic case details</h2>
            <p>
              These fields help the final report and file list read cleanly. You can skip this now and edit case details later.
            </p>
          </div>
          <button class="case-simple-launch__close" type="button" aria-label="Close case setup" @click="closeCaseSimpleDetailsPrompt(false)">
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div class="case-simple-details__grid">
          <label>
            <span>Case name</span>
            <input v-model="caseSimpleDetailsDraft.caseName" type="text" placeholder="Example: Smith v. Unknown Wallet" />
          </label>
          <label>
            <span>Case ID / reference</span>
            <input v-model="caseSimpleDetailsDraft.caseId" type="text" placeholder="Example: 2026-001" />
          </label>
          <label>
            <span>Case type</span>
            <input v-model="caseSimpleDetailsDraft.caseType" type="text" placeholder="Example: Fraud" />
          </label>
        </div>

        <footer class="case-simple-details__actions">
          <button class="toolbar-btn toolbar-btn--primary" type="button" @click="saveCaseSimpleDetailsPrompt">
            Save details and continue
          </button>
          <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="closeCaseSimpleDetailsPrompt(true)">
            Skip for now
          </button>
        </footer>
      </section>
    </div>

    <button
      v-if="caseSimpleLaunchMiniVisible"
      class="case-simple-launch-mini"
      type="button"
      @click="openCaseQuickLookupFromWorkspace"
    >
      <span aria-hidden="true"></span>
      <strong>Simple view</strong>
      <small>Return to transaction lookup</small>
    </button>

    <div v-if="caseGlobalOperation.active" class="case-global-progress" role="status" aria-live="polite">
      <div class="case-global-progress__spinner" aria-hidden="true"></div>
      <div class="case-global-progress__body">
        <strong>{{ caseGlobalOperation.title || 'Working…' }}</strong>
        <span>{{ caseGlobalOperation.message || 'Preparing…' }}</span>
        <small v-if="caseGlobalOperation.detail">{{ caseGlobalOperation.detail }}</small>
        <div class="case-global-progress__meter" role="progressbar" :aria-valuenow="caseGlobalOperationPercent" aria-valuemin="0" aria-valuemax="100">
          <span :style="caseGlobalOperationProgressStyle"></span>
        </div>
        <small>{{ caseGlobalOperationProgressLabel }}</small>
      </div>
    </div>

    <div v-if="caseModalOperation.active" class="case-modal-progress" role="status" aria-live="polite">
      <div class="case-modal-progress__spinner" aria-hidden="true"></div>
      <div class="case-modal-progress__body">
        <strong>{{ caseModalOperation.title || 'Preparing view…' }}</strong>
        <span>{{ caseModalOperation.message || 'Building the latest case preview…' }}</span>
        <small v-if="caseModalOperation.detail">{{ caseModalOperation.detail }}</small>
      </div>
    </div>

    <div v-if="caseAutomation.active" class="case-automation-failsafe" :class="caseAutomationFailsafeClass" role="status" aria-live="polite">
      <div class="case-automation-failsafe__body">
        <strong>{{ caseAutomation.paused ? 'Auto paused' : (caseAutomation.busy ? 'Auto working…' : 'Auto running') }}</strong>
        <span>{{ caseAutomation.status || 'Automation active' }}</span>
      </div>
      <button class="case-automation-failsafe__stop" type="button" @click.stop="requestCaseAutomationUserStop('Automation stopped by user.')">
        Stop Auto
      </button>
    </div>
    <div v-if="caseAutomationShowcaseOverlayVisible" class="case-automation-showcase" role="status" aria-live="polite" @click.stop>
      <div class="case-automation-showcase__scrim" aria-hidden="true"></div>
      <section class="case-automation-showcase__card" :class="{ 'case-automation-showcase__card--tool88-active': caseAutomationShowcaseTool88Visible }" aria-label="Case Buddy automation showcase view">
        <header class="case-automation-showcase__header">
          <div>
            <span class="case-automation-showcase__eyebrow">Case Buddy Auto</span>
            <h2>Building the case workspace</h2>
            <p>
              Case Buddy is completing safe workflow actions. Manual pauses will appear only when your input is needed.
            </p>
          </div>
          <div class="case-automation-showcase__header-actions">
            <button class="case-automation-showcase__ghost-btn" type="button" title="Copy workflow Auto debug log" @click="copyCaseWorkflowAutomationDebugSnapshot">
              Debug
            </button>
            <button class="case-automation-showcase__stop-btn" type="button" @click="requestCaseAutomationUserStop('Automation stopped by user.')">
              Stop Auto
            </button>
          </div>
        </header>

        <div class="case-automation-showcase__body">
          <div class="case-automation-showcase__visual" :class="caseAutomationShowcaseVisualClass" :style="caseAutomationShowcaseVisualStyle" aria-hidden="true">
            <div v-if="caseAutomationShowcaseTool88PortalState.available" class="case-automation-showcase__tool88-portal">
              <svg class="case-automation-showcase__tool88-portal-svg" viewBox="0 0 420 300" role="img" aria-label="Flow Solver raw-data flow preview">
                <defs>
                  <marker id="caseAutomationTool88PortalArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" class="case-automation-showcase__tool88-portal-arrow-head"></path>
                  </marker>
                  <filter id="caseAutomationTool88PortalFocusGlow" x="-60%" y="-60%" width="220%" height="220%">
                    <feGaussianBlur stdDeviation="7" result="blur"></feGaussianBlur>
                    <feMerge>
                      <feMergeNode in="blur"></feMergeNode>
                      <feMergeNode in="SourceGraphic"></feMergeNode>
                    </feMerge>
                  </filter>
                </defs>
                <rect class="case-automation-showcase__tool88-portal-bg" x="0" y="0" width="420" height="300" rx="24"></rect>
                <g class="case-automation-showcase__tool88-portal-world" :transform="caseAutomationShowcaseTool88PortalState.transform">
                  <path
                    v-for="edge in caseAutomationShowcaseTool88PortalState.edges"
                    :key="edge.id"
                    :class="edge.className"
                    :d="edge.d"
                    marker-end="url(#caseAutomationTool88PortalArrow)"
                  ></path>
                  <g
                    v-for="node in caseAutomationShowcaseTool88PortalState.nodes"
                    :key="node.id"
                    :class="node.className"
                    :transform="node.transform"
                  >
                    <rect
                      v-if="node.type === 'tx'"
                      class="case-automation-showcase__tool88-portal-tx"
                      x="-38"
                      y="-20"
                      width="76"
                      height="40"
                      rx="10"
                    ></rect>
                    <circle
                      v-else
                      class="case-automation-showcase__tool88-portal-address"
                      r="24"
                    ></circle>
                    <circle
                      v-if="node.focus"
                      class="case-automation-showcase__tool88-portal-focus-ring"
                      r="34"
                      filter="url(#caseAutomationTool88PortalFocusGlow)"
                    ></circle>
                    <text class="case-automation-showcase__tool88-portal-label" text-anchor="middle" :y="node.type === 'tx' ? -2 : -4">{{ node.label }}</text>
                    <text class="case-automation-showcase__tool88-portal-sub-label" text-anchor="middle" :y="node.type === 'tx' ? 13 : 11">{{ node.subLabel }}</text>
                  </g>
                </g>
              </svg>
              <div class="case-automation-showcase__tool88-portal-caption">
                <strong>{{ caseAutomationShowcaseTool88PortalState.focusLabel }}</strong>
                <span>{{ caseAutomationShowcaseTool88PortalState.summary }}</span>
              </div>
            </div>
            <template v-else>
            <div class="case-automation-showcase__constellation">
              <span
                v-for="node in caseAutomationShowcaseConstellationNodes"
                :key="node.id"
                :class="node.className"
              ></span>
            </div>
            <div class="case-automation-showcase__ring case-automation-showcase__ring--outer"></div>
            <div class="case-automation-showcase__ring case-automation-showcase__ring--middle"></div>
            <div class="case-automation-showcase__ring case-automation-showcase__ring--inner"></div>
            <span class="case-automation-showcase__node case-automation-showcase__node--one"></span>
            <span class="case-automation-showcase__node case-automation-showcase__node--two"></span>
            <span class="case-automation-showcase__node case-automation-showcase__node--three"></span>
            <span class="case-automation-showcase__node case-automation-showcase__node--four"></span>
            <div class="case-automation-showcase__orbit-pack">
              <span class="case-automation-showcase__orbit-particle case-automation-showcase__orbit-particle--one"></span>
              <span class="case-automation-showcase__orbit-particle case-automation-showcase__orbit-particle--two"></span>
              <span class="case-automation-showcase__orbit-particle case-automation-showcase__orbit-particle--three"></span>
              <span class="case-automation-showcase__orbit-particle case-automation-showcase__orbit-particle--four"></span>
            </div>
            <div class="case-automation-showcase__handoff-stream">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div class="case-automation-showcase__core">
              <span></span>
            </div>
            <div v-if="caseAutomationShowcaseTool88Visible" class="case-automation-showcase__tool88-packets" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
            </template>
          </div>

          <div class="case-automation-showcase__content" :class="{ 'case-automation-showcase__content--tool88': caseAutomationShowcaseTool88Visible }">
            <article class="case-automation-showcase__focus-card">
              <span>Current focus</span>
              <strong>{{ caseAutomationShowcaseStageLabel }}</strong>
              <small>{{ caseAutomationShowcaseSupportLabel }}</small>
            </article>

            <template v-if="caseAutomationShowcaseTool88Visible">
              <article
                class="case-automation-showcase__tool88-card case-automation-showcase__tool88-card--compact"
                :class="{
                  'case-automation-showcase__tool88-card--running': caseAutomationTool88Activity?.active,
                  'case-automation-showcase__tool88-card--complete': caseAutomationTool88Activity?.flowComplete,
                  'case-automation-showcase__tool88-card--review': !!caseAutomationTool88Activity?.guardrailMessage
                }"
                aria-live="polite"
              >
                <div class="case-automation-showcase__tool88-header">
                  <div>
                    <span>Flow Solver</span>
                    <strong>{{ caseAutomationShowcaseTool88Title }}</strong>
                  </div>
                  <div class="case-automation-showcase__tool88-header-actions">
                    <em>{{ caseAutomationShowcaseTool88TargetLabel }}</em>
                    <button
                      v-if="caseWorkflowRawDataTool88DebugAvailable"
                      class="case-automation-showcase__tool88-debug-btn"
                      type="button"
                      title="Copy the Flow Solver debug log for this workflow run"
                      @click.stop="copyCaseWorkflowRawDataTool88DebugSnapshot"
                    >
                      Debug
                    </button>
                  </div>
                </div>

                <div class="case-automation-showcase__tool88-compact-row">
                  <div class="case-automation-showcase__tool88-progress-mini">
                    <span>Workflow</span>
                    <strong>{{ caseAutomationShowcaseProgressLabel }}</strong>
                    <div class="case-automation-showcase__meter" role="progressbar" :aria-valuenow="caseAutomationShowcaseProgressPercent" aria-valuemin="0" aria-valuemax="100">
                      <span :style="caseAutomationShowcaseProgressStyle"></span>
                    </div>
                  </div>
                  <div class="case-automation-showcase__tool88-live-row">
                    <span class="case-automation-showcase__tool88-live-dot"></span>
                    <strong>{{ caseAutomationShowcaseTool88PulseLabel }}</strong>
                  </div>
                </div>

                <div class="case-automation-showcase__tool88-meter" role="progressbar" :aria-valuenow="caseAutomationShowcaseTool88ProgressPercent" aria-valuemin="0" aria-valuemax="100">
                  <span :style="caseAutomationShowcaseTool88ProgressStyle"></span>
                </div>

                <div class="case-automation-showcase__tool88-stats">
                  <div v-for="stat in caseAutomationShowcaseTool88Stats" :key="stat.label">
                    <span>{{ stat.label }}</span>
                    <strong>{{ stat.value }}</strong>
                  </div>
                </div>

                <p>{{ caseAutomationShowcaseTool88Message }}</p>
                <ul v-if="caseAutomationShowcaseTool88Log.length" class="case-automation-showcase__tool88-log case-automation-showcase__tool88-log--compact">
                  <li v-for="entry in caseAutomationShowcaseTool88Log" :key="entry.id">{{ entry.message }}</li>
                </ul>
              </article>
            </template>

            <template v-else>
              <div class="case-automation-showcase__progress-card">
                <div>
                  <span>Workflow progress</span>
                  <strong>{{ caseAutomationShowcaseProgressLabel }}</strong>
                </div>
                <div class="case-automation-showcase__meter" role="progressbar" :aria-valuenow="caseAutomationShowcaseProgressPercent" aria-valuemin="0" aria-valuemax="100">
                  <span :style="caseAutomationShowcaseProgressStyle"></span>
                </div>
              </div>

              <div class="case-automation-showcase__phase-list" aria-label="Workflow stage progress">
                <article
                  v-for="phase in caseAutomationShowcasePhases"
                  :key="phase.id"
                  :class="phase.className"
                >
                  <span>{{ phase.shortTitle }}</span>
                  <small>{{ phase.completed }} / {{ phase.total }}</small>
                </article>
              </div>

              <div v-if="caseAutomationShowcaseRecentMilestones.length" class="case-automation-showcase__milestones">
                <span>Recent milestones</span>
                <ul>
                  <li v-for="entry in caseAutomationShowcaseRecentMilestones" :key="entry.id">
                    {{ entry.message }}
                  </li>
                </ul>
              </div>
            </template>
          </div>
        </div>

        <footer class="case-automation-showcase__footer">
          <span>{{ caseAutomationReviewModeLabel }}</span>
        </footer>
      </section>
    </div>

    <button
      v-if="caseAutomationShowcaseMiniVisible"
      class="case-automation-showcase-mini"
      type="button"
      @click="restoreCaseAutomationShowcaseOverlay"
    >
      <span aria-hidden="true"></span>
      <strong>Show Auto view</strong>
      <small>{{ caseAutomationShowcaseStageLabel }}</small>
    </button>

    <div
      class="case-page-shell"
      :class="{
        'case-page-shell--with-intake-rail': caseIntakeRailVisible,
        'case-page-shell--without-left-nav': !caseScrollNavigationVisible
      }"
      :inert="caseIntakePreparation.active || caseAttributionWorkflowRun.active || caseCleanupWorkflowRun.active"
    >
      <nav v-if="caseScrollNavigationVisible" class="case-scroll-nav" aria-label="Case Buddy sections">
        <button
          v-for="item in caseNavItems"
          :key="item.id"
          class="case-scroll-nav__item"
          type="button"
          :title="item.label"
          @click="scrollToSection(item.id)"
        >
          <span class="case-scroll-nav__icon" aria-hidden="true">{{ item.icon }}</span>
          <span class="case-scroll-nav__label">{{ item.shortLabel }}</span>
        </button>
      </nav>

      <main class="case-page-main">
        <header id="case-overview" class="page-header case-overview-panel">
          <div class="case-overview-panel__title-row">
            <div class="case-title-group">
              <div class="case-overview-panel__eyebrow-row">
                <p class="eyebrow-label">Case Buddy Web App</p>
              </div>
              <div class="case-title-inline" ref="caseTitleInlineBox">
                <h1
                  v-if="!caseTitleInlineEditing"
                  ref="caseTitleInlineDisplay"
                  class="case-title-inline__display"
                  :style="caseTitleInlineDisplayStyle"
                  :title="caseDisplayName"
                  tabindex="0"
                  aria-label="Case name. Double-click to rename."
                  @dblclick="startCaseTitleInlineEdit"
                  @keydown.enter.prevent="startCaseTitleInlineEdit"
                >
                  {{ caseDisplayName }}
                </h1>
                <h1
                  v-else
                  class="case-title-inline__editor-heading"
                  :style="caseTitleInlineDisplayStyle"
                >
                  <input
                    ref="caseTitleInlineInput"
                    v-model="caseTitleInlineDraft"
                    class="case-title-inline__input"
                    type="text"
                    aria-label="Edit case name"
                    maxlength="180"
                    @keydown.enter.prevent="commitCaseTitleInlineEdit"
                    @keydown.esc.prevent="cancelCaseTitleInlineEdit"
                    @blur="commitCaseTitleInlineEdit"
                  />
                </h1>
              </div>
              <p class="page-subtitle">
                A blockchain investigation workspace with guidance, trace automation and report generation.
              </p>
            </div>

            <div class="case-header-action-stack">
              <div class="case-header-actions">
                <button class="header-action-btn" type="button" @click="openCaseDetailsEditor">
                  Edit case details
                </button>
                <button v-if="caseQuickLookupEnabled" class="header-action-btn header-action-btn--simple" type="button" @click="openCaseQuickLookupFromWorkspace">
                  Simple view
                </button>
              </div>

              <button
                class="case-overview-details-toggle"
                type="button"
                :aria-expanded="caseOverviewDetailsExpanded ? 'true' : 'false'"
                aria-controls="case-overview-status-details case-overview-summary-details case-overview-extra-actions"
                :aria-label="caseOverviewDetailsExpanded ? 'Hide case status details and extra case actions' : 'Show case status details and extra case actions'"
                :title="caseOverviewDetailsExpanded ? 'Hide case status details' : 'Show case status details'"
                @click="toggleCaseOverviewDetails"
              >
                <span class="case-overview-details-toggle__triangle" aria-hidden="true">
                  {{ caseOverviewDetailsExpanded ? '▴' : '▾' }}
                </span>
              </button>
            </div>
          </div>

          <div
            v-show="caseOverviewDetailsExpanded"
            id="case-overview-status-details"
            class="case-status-grid"
          >
            <article class="case-status-card">
              <span class="case-status-card__label">Open file</span>
              <strong>{{ currentProjectFileName }}</strong>
              <small>{{ currentProjectUpdatedLabel ? `Last saved ${currentProjectUpdatedLabel}` : 'Not saved yet' }}</small>
            </article>

            <article class="case-status-card">
              <span class="case-status-card__label">Case details</span>
              <strong>{{ caseDisplayId }}</strong>
              <small>{{ caseDisplayType }}</small>
            </article>

            <article class="case-status-card" :class="{ 'case-status-card--dirty': caseSupportState.dirty }">
              <span class="case-status-card__label">Case state</span>
              <strong>{{ caseDirtyLabel }}</strong>
              <small>Revision {{ caseSupportState.caseDataRevision }}</small>
            </article>

            <article class="case-status-card" :class="{ 'case-status-card--stale': validationIsStale }">
              <span class="case-status-card__label">Validation</span>
              <strong>{{ validationStateLabel }}</strong>
              <small>{{ validationRevisionLabel }}</small>
            </article>
          </div>

          <div
            v-show="caseOverviewDetailsExpanded"
            id="case-overview-summary-details"
            class="case-summary-strip"
          >
            <span>Main entries: <strong>{{ caseSummary.mainEntries }}</strong></span>
            <span>Starting/main txns: <strong>{{ caseSummary.startingMainTransactions }}</strong></span>
            <span>Raw entries: <strong>{{ caseSummary.rawEntries }}</strong></span>
            <span>Case addresses: <strong>{{ caseSummary.caseAddressCount || caseSummary.destinationAddressCount }}</strong></span>
            <span>Output rows: <strong>{{ caseSummary.outputRows }}</strong></span>
          </div>

          <div
            v-show="caseOverviewDetailsExpanded"
            id="case-overview-extra-actions"
            class="case-overview-extra-actions"
          >
            <button class="header-action-btn" type="button" @click="ensureCaseWorkbookScaffold">
              Ensure case tabs
            </button>
            <button class="header-action-btn header-action-btn--ghost" type="button" :disabled="caseChecksRunning" @click="runWorkflowChecks">
              {{ caseWorkflowRunChecksLabel }}
            </button>
            <button class="header-action-btn header-action-btn--monitoring" type="button" @click="openCaseAddressMonitoringModal">
              Address monitoring
            </button>
            <button
              class="header-action-btn header-action-btn--ghost"
              type="button"
              :aria-pressed="caseScrollNavigationVisible ? 'true' : 'false'"
              :title="caseScrollNavigationVisible ? 'Hide the scrolling section navigation' : 'Show the scrolling section navigation'"
              @click="toggleCaseScrollNavigationVisible"
            >
              {{ caseScrollNavigationVisible ? 'Hide navigation' : 'Show navigation' }}
            </button>
            <button
              class="header-action-btn header-action-btn--theme"
              :class="{ 'is-active': caseWorkspaceDarkModeEnabled }"
              type="button"
              :aria-pressed="caseWorkspaceDarkModeEnabled ? 'true' : 'false'"
              :title="caseWorkspaceDarkModeEnabled ? 'Use the light Advanced Workspace theme' : 'Use the dark Advanced Workspace theme'"
              @click="toggleCaseWorkspaceDarkMode"
            >
              <span class="header-action-btn__theme-icon" aria-hidden="true">{{ caseWorkspaceDarkModeEnabled ? '☀' : '◐' }}</span>
              {{ caseWorkspaceDarkModeEnabled ? 'Light mode' : 'Dark mode' }}
            </button>
            <button
              v-if="caseSimpleLaunchEnabled"
              class="header-action-btn header-action-btn--ghost"
              type="button"
              title="Open the archived guided launch screen for testing or reference"
              @click="openLegacyCaseSimpleLaunch"
            >
              Legacy guided view
            </button>
          </div>
        </header>

    <section
      id="case-input"
      class="sheet-panel case-anchor-panel"
      :class="{
        'sheet-panel--fullscreen': inputFullscreen,
        'sheet-panel--automation-locked': caseAutomation.active && !caseAutomation.paused
      }"
    >
      <div class="sheet-panel__header">
        <div class="sheet-panel__heading">
          <h2>Case Workspace</h2>
        
        </div>

        <div class="sheet-panel__header-actions">
          <span class="header-badge" :class="{ 'header-badge--active': !!lastSelection }">
            {{ selectionSummary }}
          </span>
          <button
            class="header-action-btn header-action-btn--copy"
            type="button"
            :disabled="spreadsheetCopyBusy.input"
            title="Copy the active input tab up to its used rows and columns."
            @click="copyActiveInputSheetToClipboard"
          >
            {{ spreadsheetCopyBusy.input ? 'Copying…' : 'Copy Sheet' }}
          </button>
          <span v-if="spreadsheetCopyStatus.input" class="sheet-copy-status" aria-live="polite">
            {{ spreadsheetCopyStatus.input }}
          </span>
          <button
            class="header-action-btn"
            type="button"
            @click="setInputFullscreen(!inputFullscreen)"
          >
            {{ inputFullscreen ? 'Exit full window' : 'Full window' }}
          </button>
          <button
            v-if="caseInputFlowStoryVisible"
            class="header-action-btn header-action-btn--visualize case-input-flow-story-view-toggle"
            type="button"
            aria-label="Show the input spreadsheet"
            title="Show the input spreadsheet"
            @click="dismissCaseInputFlowStory"
          >
            <span aria-hidden="true">▦</span>
            Spreadsheet
          </button>
          <button
            v-else-if="caseInputFlowStoryCanRestore"
            class="header-action-btn header-action-btn--visualize case-input-flow-story-restore case-input-flow-story-view-toggle"
            type="button"
            aria-label="Show the visual Main Data overview"
            title="Show the visual Main Data overview over the input spreadsheet."
            @click="restoreCaseInputFlowStory"
          >
            <span aria-hidden="true">◎</span>
            Visual overview
            <span v-if="caseInputFlowStoryRootCount" class="header-action-btn__badge">
              {{ caseInputFlowStoryRootCount }}
            </span>
          </button>
        </div>
      </div>

      <button
        v-if="!caseInputFlowStoryVisible && !spreadsheetToolsExpanded"
        class="spreadsheet-tools-toggle"
        type="button"
        aria-controls="case-spreadsheet-tools"
        :aria-expanded="spreadsheetToolsExpanded"
        title="Show spreadsheet tools and formula bar"
        @click="setSpreadsheetToolsExpanded(true)"
      >
        <span class="spreadsheet-tools-toggle__chevron" aria-hidden="true">▸</span>
        <span class="spreadsheet-tools-toggle__text">
          <strong>Spreadsheet tools</strong>
          <small>Show formatting, search, freeze controls, and formula bar</small>
        </span>
        <span class="spreadsheet-tools-toggle__hint">Show</span>
      </button>

      <div
        v-show="!caseInputFlowStoryVisible && spreadsheetToolsExpanded"
        id="case-spreadsheet-tools"
        class="spreadsheet-tools-expanded"
      >
        <div class="sheet-toolbar">
        <div class="toolbar-row toolbar-row--primary">
          <div class="toolbar-cluster">
            <details ref="sheetViewMenu" class="toolbar-menu">
              <summary class="toolbar-btn toolbar-btn--menu" title="Open sheet view options">
                <span aria-hidden="true">⋯</span>
                <span>Sheet view</span>
              </summary>
              <div class="toolbar-menu__panel">
                <button class="toolbar-menu__item" type="button" @click="runShowcaseAction('zebra')">
                  <span aria-hidden="true">🦓</span>
                  <span>Zebra rows</span>
                </button>
                <button class="toolbar-menu__item" type="button" title="Reset row heights on the active sheet without clearing data" @click="resetActiveSheetRowHeights">
                  <span aria-hidden="true">↕️</span>
                  <span>Fix row heights</span>
                </button>
              </div>
            </details>

            <button class="toolbar-btn toolbar-btn--danger" type="button" @click="requestResetSheet" title="Clear the input sheet and reset formatting">
              <span aria-hidden="true">♻️</span>
              <span>Reset</span>
            </button>
          </div>

          <div class="toolbar-cluster">
            <button
              class="toolbar-btn"
              :class="{ 'toolbar-btn--active': freezeTop }"
              type="button"
              :aria-pressed="freezeTop"
              @click="toggleFreezeTopRow"
              :title="freezeTop ? 'Unfreeze the top row' : 'Freeze the top row'"
            >
              <span aria-hidden="true">🧊</span>
              <span>Top Row</span>
            </button>

            <button
              class="toolbar-btn"
              :class="{ 'toolbar-btn--active': freezeLeft }"
              type="button"
              :aria-pressed="freezeLeft"
              @click="toggleFreezeFirstColumn"
              :title="freezeLeft ? 'Unfreeze the left column' : 'Freeze the left column'"
            >
              <span aria-hidden="true">🧊</span>
              <span>Left Col</span>
            </button>

          </div>

          <div class="toolbar-search" role="search" aria-label="Search the spreadsheet">
            <input
              v-model="searchQuery"
              class="toolbar-input toolbar-input--search"
              type="search"
              placeholder="Search sheet"
              @keyup.enter="applySearch"
            />
            <button class="toolbar-btn toolbar-btn--primary" type="button" @click="applySearch">
              Search
            </button>
            <button
              class="toolbar-btn toolbar-btn--icon toolbar-btn--ghost"
              type="button"
              @click="clearSearch"
              :disabled="!searchQuery && matchCount === 0"
              aria-label="Clear search"
              title="Clear search"
            >
              ×
            </button>
            <span v-if="matchCount > 0" class="toolbar-badge" title="Matches found">
              {{ matchCount }} hit{{ matchCount === 1 ? '' : 's' }}
            </span>
          </div>

          <div class="spreadsheet-tools-end-controls">
            <label class="autosave-toggle" title="Save the current sheet state in local storage">
              <input type="checkbox" v-model="autoSaveEnabled" @change="onAutoSaveToggle" />
              <span>Auto-save</span>
            </label>
            <button
              class="toolbar-btn toolbar-btn--ghost spreadsheet-tools-hide-btn"
              type="button"
              title="Hide spreadsheet tools and formula bar"
              @click="setSpreadsheetToolsExpanded(false)"
            >
              <span aria-hidden="true">▴</span>
              <span>Hide tools</span>
            </button>
          </div>
        </div>

        <div class="toolbar-row toolbar-row--secondary">
          <div class="toolbar-group">
            <button class="toolbar-btn toolbar-btn--icon" type="button" @click="toggleBold" title="Bold">
              <strong>B</strong>
            </button>
            <button class="toolbar-btn toolbar-btn--icon" type="button" @click="toggleItalic" title="Italic">
              <em>I</em>
            </button>
            <button class="toolbar-btn toolbar-btn--icon" type="button" @click="toggleUnderline" title="Underline">
              <u>U</u>
            </button>
            <button class="toolbar-btn toolbar-btn--icon" type="button" @click="toggleStrikeThrough" title="Strike-through">
              <s>S</s>
            </button>
          </div>

          <div class="toolbar-group">
            <div class="color-picker-anchor">
              <button
                class="toolbar-btn"
                :class="{ 'toolbar-btn--active': activeColorPicker === 'fill' }"
                type="button"
                @click="toggleColorPicker('fill')"
                title="Choose a background color for the current selection"
                ref="colorButton"
                :aria-expanded="activeColorPicker === 'fill'"
              >
                <span aria-hidden="true">🎨</span>
                <span>Fill</span>
              </button>

              <div
                v-if="activeColorPicker === 'fill'"
                ref="fillColorPopover"
                class="color-popover"
                role="dialog"
                aria-label="Fill color picker"
                @mousedown.stop
                @click.stop
              >
                <div class="color-popover__header">
                  <span class="color-popover__title">Fill color</span>
                  <span class="color-popover__preview" :style="{ backgroundColor: fillColorValue }"></span>
                </div>

                <div class="color-popover__actions color-popover__actions--top">
                  <button class="toolbar-btn toolbar-btn--primary color-popover__action" type="button" @click="confirmColorPicker">
                    Apply
                  </button>
                  <button class="toolbar-btn toolbar-btn--ghost color-popover__action" type="button" @click="cancelColorPicker">
                    Cancel
                  </button>
                </div>

                <input
                  ref="fillColorInput"
                  v-model="fillColorValue"
                  class="color-popover__input"
                  type="color"
                  aria-label="Choose fill color"
                  @keydown.enter.prevent="confirmColorPicker"
                  @keydown.esc.prevent="cancelColorPicker"
                />

                <p class="color-popover__hint">Pick the color below, then use the buttons above.</p>
              </div>
            </div>

            <div class="color-picker-anchor">
              <button
                class="toolbar-btn"
                :class="{ 'toolbar-btn--active': activeColorPicker === 'text' }"
                type="button"
                @click="toggleColorPicker('text')"
                title="Choose a text color for the current selection"
                ref="textColorButton"
                :aria-expanded="activeColorPicker === 'text'"
              >
                <span aria-hidden="true">🖍️</span>
                <span>Text</span>
              </button>

              <div
                v-if="activeColorPicker === 'text'"
                ref="textColorPopover"
                class="color-popover"
                role="dialog"
                aria-label="Text color picker"
                @mousedown.stop
                @click.stop
              >
                <div class="color-popover__header">
                  <span class="color-popover__title">Text color</span>
                  <span class="color-popover__preview color-popover__preview--text" :style="{ color: textColorValue }">A</span>
                </div>

                <div class="color-popover__actions color-popover__actions--top">
                  <button class="toolbar-btn toolbar-btn--primary color-popover__action" type="button" @click="confirmColorPicker">
                    Apply
                  </button>
                  <button class="toolbar-btn toolbar-btn--ghost color-popover__action" type="button" @click="cancelColorPicker">
                    Cancel
                  </button>
                </div>

                <input
                  ref="textColorInput"
                  v-model="textColorValue"
                  class="color-popover__input"
                  type="color"
                  aria-label="Choose text color"
                  @keydown.enter.prevent="confirmColorPicker"
                  @keydown.esc.prevent="cancelColorPicker"
                />

                <p class="color-popover__hint">Pick the color below, then use the buttons above.</p>
              </div>
            </div>

            <button class="toolbar-btn" type="button" @click="toggleWrap" title="Toggle word wrap">
              <span aria-hidden="true">↩️</span>
              <span>Wrap</span>
            </button>

            <button class="toolbar-btn" type="button" @click="fillDownSelection" title="Fill down using the selected pattern">
              <span aria-hidden="true">↧</span>
              <span>Fill Down</span>
            </button>
          </div>

          <div class="toolbar-group">
            <button class="toolbar-btn toolbar-btn--icon" type="button" @click="setAlignment('left')" title="Align left">
              ⬅️
            </button>
            <button class="toolbar-btn toolbar-btn--icon" type="button" @click="setAlignment('center')" title="Align center">
              ↔️
            </button>
            <button class="toolbar-btn toolbar-btn--icon" type="button" @click="setAlignment('right')" title="Align right">
              ➡️
            </button>

            <span class="toolbar-divider" aria-hidden="true"></span>

            <button class="toolbar-btn toolbar-btn--icon" type="button" @click="setVertical('top')" title="Align top">
              🔼
            </button>
            <button class="toolbar-btn toolbar-btn--icon" type="button" @click="setVertical('middle')" title="Align middle">
              ⏺
            </button>
            <button class="toolbar-btn toolbar-btn--icon" type="button" @click="setVertical('bottom')" title="Align bottom">
              🔽
            </button>
          </div>

          <div class="toolbar-group">
            <button class="toolbar-btn toolbar-btn--icon" type="button" @click="changeFontSize(-1)" title="Decrease font size">
              ➖
            </button>
            <label class="toolbar-field" title="Set font size">
              <span>Font</span>
              <input
                class="toolbar-input toolbar-input--number"
                type="number"
                min="8"
                max="72"
                v-model.number="currentFontSize"
                @change="applyFontSize"
              />
            </label>
            <button class="toolbar-btn toolbar-btn--icon" type="button" @click="changeFontSize(1)" title="Increase font size">
              ➕
            </button>
          </div>

          <div class="toolbar-group">
            <button class="toolbar-btn" type="button" @click="showBorderModal = true" title="Apply borders to the current selection">
              <span aria-hidden="true">▣</span>
              <span>Border</span>
            </button>
          </div>
        </div>
      </div>

      <div class="formula-bar" :class="{ 'formula-bar--editing': formulaBarEditing }">
        <button
          class="formula-bar__action formula-bar__action--fx"
          type="button"
          @click="focusFormulaBar"
          title="Edit the selected cell in the formula bar"
        >
          fx
        </button>

        <div class="formula-bar__cell" :title="formulaTargetLabel">
          {{ formulaTargetLabel }}
        </div>

        <input
          ref="formulaInput"
          :value="formulaBarDraft"
          class="formula-bar__input"
          type="text"
          spellcheck="false"
          placeholder="Enter a value or formula. Click SUM, then select a range."
          @focus="handleFormulaBarFocus"
          @input="handleFormulaBarInput"
          @keydown.enter.prevent="applyFormulaBar"
          @keydown.esc.prevent="cancelFormulaBarEdit"
        />

        <button
          class="formula-bar__action"
          type="button"
          @click="startSumFormula"
          title="Start a SUM formula in the selected cell"
        >
          SUM
        </button>

        <button
          class="formula-bar__action formula-bar__action--primary"
          type="button"
          :disabled="!canApplyFormulaBar"
          @click="applyFormulaBar"
        >
          Apply
        </button>

        <button
          v-if="formulaBarEditing"
          class="formula-bar__action formula-bar__action--ghost"
          type="button"
          @click="cancelFormulaBarEdit"
          title="Cancel formula edit"
        >
          Esc
        </button>

        <span
          v-if="formulaBarCaptureMode"
          class="formula-bar__status"
          title="Click or drag on the sheet to insert references into the formula."
        >
          Pick refs
        </span>
      </div>
      </div>
<details v-if="shouldRenderAgentControls" class="agent-controls" open>
  <summary>Agent Controls</summary>
<p class="agent-controls__hint">
  For AI agents: use this panel instead of clicking the spreadsheet grid or formula bar.
</p>
<div class="agent-controls__row">
  <button type="button" class="toolbar-btn toolbar-btn--danger" @click="performResetSheetReset">
    Reset Sheet
  </button>
</div>
  <div class="agent-controls__row">
    <label class="agent-controls__field">
      <span>Cell</span>
      <input
        v-model.trim="agentCellRef"
        type="text"
        placeholder="B2"
        aria-label="Spreadsheet cell reference"
      />
    </label>

    <button type="button" class="toolbar-btn" @click="agentSelectCell">
      Select Cell
    </button>

    <button type="button" class="toolbar-btn" @click="agentReadCell">
      Read Cell
    </button>
  </div>

  <div class="agent-controls__row">
    <label class="agent-controls__field agent-controls__field--wide">
      <span>Cell Value</span>
      <textarea
        v-model="agentCellValue"
        rows="3"
        placeholder="Value to write into the selected cell"
        aria-label="Spreadsheet cell value"
      ></textarea>
    </label>
  </div>

  <div class="agent-controls__row">
    <button type="button" class="toolbar-btn toolbar-btn--primary" @click="agentWriteCell">
      Write Cell
    </button>

    <button type="button" class="toolbar-btn" @click="agentClearCell">
      Clear Cell
    </button>
  </div>

  <div class="agent-controls__row">
    <label class="agent-controls__field">
      <span>Range</span>
      <input
        v-model.trim="agentRangeRef"
        type="text"
        placeholder="A1:C5"
        aria-label="Spreadsheet range reference"
      />
    </label>

    <button type="button" class="toolbar-btn" @click="agentReadRange">
      Read Range
    </button>
  </div>
<div class="agent-controls__row">
  <label class="agent-controls__field">
    <span>Write Range Start</span>
    <input
      v-model.trim="agentWriteRangeStartRef"
      type="text"
      placeholder="A1"
      aria-label="Spreadsheet write range start cell"
    />
  </label>

  <button type="button" class="toolbar-btn toolbar-btn--primary" @click="agentWriteRange">
    Write Range
  </button>
</div>

<div class="agent-controls__row">
  <label class="agent-controls__field agent-controls__field--wide">
    <span>Range Data (JSON 2D Array)</span>
    <textarea
      v-model="agentWriteRangeData"
      rows="8"
      placeholder='[["A1","B1"],["A2","B2"]]'
      aria-label="Spreadsheet range write data"
    ></textarea>
  </label>
</div>
  <div class="agent-controls__status" aria-live="polite">
    {{ agentStatus }}
  </div>

  <textarea
    class="agent-controls__readback"
    :value="agentReadback"
    rows="8"
    readonly
    aria-label="Agent readback output"
  ></textarea>
</details>
      <div
        class="sheet-grid"
        :class="{
          'sheet-grid--fullscreen': inputFullscreen,
          'sheet-grid--flow-story-open': caseInputFlowStoryVisible
        }"
      >
        <section
          v-if="caseInputFlowStoryVisible"
          class="case-input-flow-story"
          role="region"
          aria-label="Main transaction visual overview"
        >
          <header class="case-input-flow-story__header">
            <div class="case-input-flow-story__heading">
              <span class="case-input-flow-story__eyebrow">Visual Main Data</span>
              <h3>Main transaction flows</h3>
              <p>{{ caseInputFlowStorySummary }}</p>
            </div>
            <div class="case-input-flow-story__header-actions">
              <span v-if="caseInputFlowStoryHeatEnabled" class="case-input-flow-story__heat-legend" aria-hidden="true">
                <small>Lower value</small>
                <i></i>
                <small>Root value</small>
                <span class="case-input-flow-story__heat-no-data">
                  <span class="case-input-flow-story__heat-no-data-swatch"></span>
                  <small>No value</small>
                </span>
              </span>
              <button
                class="case-input-flow-story__heat-toggle"
                :class="{ 'is-active': caseInputFlowStoryHeatEnabled }"
                type="button"
                :aria-pressed="caseInputFlowStoryHeatEnabled ? 'true' : 'false'"
                :title="caseInputFlowStoryHeatEnabled ? 'Show normal blue address and yellow transaction colors' : 'Color nodes and arrows by value relative to each root transaction'"
                @click="toggleCaseInputFlowStoryHeat"
              >
                <span class="case-input-flow-story__heat-toggle-dot" aria-hidden="true"></span>
                {{ caseInputFlowStoryHeatEnabled ? 'Heat on' : 'Heat off' }}
              </button>
            </div>
          </header>

          <div
            ref="caseInputFlowStoryList"
            class="case-input-flow-story__list"
            @scroll.passive="onCaseInputFlowStoryScroll"
          >
            <template v-if="caseInputFlowStoryStatus === 'loading' && caseInputFlowStoryDescriptors.length === 0">
              <article v-for="index in 3" :key="`case-flow-story-loading-${index}`" class="case-input-flow-story-card case-input-flow-story-card--loading" aria-hidden="true">
                <div class="case-input-flow-story-card__meta">
                  <span class="case-input-flow-story-skeleton case-input-flow-story-skeleton--eyebrow"></span>
                  <span class="case-input-flow-story-skeleton case-input-flow-story-skeleton--title"></span>
                  <span class="case-input-flow-story-skeleton case-input-flow-story-skeleton--line"></span>
                  <span class="case-input-flow-story-skeleton case-input-flow-story-skeleton--line case-input-flow-story-skeleton--short"></span>
                </div>
                <div class="case-input-flow-story-card__stage">
                  <div class="case-input-flow-story-placeholder">
                    <span class="case-input-flow-story-placeholder__node is-address"></span>
                    <span class="case-input-flow-story-placeholder__edge"></span>
                    <span class="case-input-flow-story-placeholder__node is-transaction"></span>
                    <span class="case-input-flow-story-placeholder__edge"></span>
                    <span class="case-input-flow-story-placeholder__node is-address"></span>
                  </div>
                </div>
              </article>
            </template>

            <button
              v-else-if="caseInputFlowStoryStatus === 'error'"
              class="case-input-flow-story__empty case-input-flow-story__empty--error"
              type="button"
              @click="scheduleCaseInputFlowStoryRefresh(0, { force: true })"
            >
              <span class="case-input-flow-story__empty-icon" aria-hidden="true">↻</span>
              <strong>Could not build the visual overview</strong>
              <small>{{ caseInputFlowStoryError || 'Select this card to try again.' }}</small>
              <em>Try again</em>
            </button>

            <button
              v-else-if="caseInputFlowStoryDescriptors.length === 0"
              class="case-input-flow-story__empty"
              type="button"
              @click="openStartingMainTransactionsFromFlowStory"
            >
              <span class="case-input-flow-story__empty-visual" aria-hidden="true">
                <i class="is-address"></i><b></b><i class="is-transaction"></i><b></b><i class="is-address"></i>
              </span>
              <strong>Your first transaction flow will appear here</strong>
              <small>Load one or more starting transaction hashes into Main Data to begin the case story.</small>
              <em>Load starting transactions</em>
            </button>

            <template v-else>
              <article
                v-for="flow in caseInputFlowStoryDescriptors"
                :key="flow.key"
                class="case-input-flow-story-card"
                :data-case-input-flow-key="flow.key"
              >
              <aside class="case-input-flow-story-card__meta">
                <span>Main path</span>
                <strong>{{ flow.displayLabel }}</strong>
                <dl>
                  <div>
                    <dt>Hash</dt>
                    <dd><code :title="flow.hash">{{ flow.shortHash }}</code></dd>
                  </div>
                  <div>
                    <dt>To</dt>
                    <dd><code :title="flow.toAddress">{{ flow.shortToAddress || 'Not set' }}</code></dd>
                  </div>
                </dl>
                <span v-if="flow.amountDisplay" class="case-input-flow-story-card__amount">
                  {{ flow.amountDisplay }}
                </span>
              </aside>

              <div
                class="case-input-flow-story-card__stage"
                :class="{
                  'is-loading': caseInputFlowStoryFlowIsLoading(flow.key),
                  'is-error': caseInputFlowStoryFlowHasError(flow.key)
                }"
              >
                <span
                  v-if="caseInputFlowStoryVisualFor(flow.key)?.truncated"
                  class="case-input-flow-story-card__limit-note"
                  :title="`This overview shows the first ${caseInputFlowStoryVisualFor(flow.key).shownEntryCount} of ${caseInputFlowStoryVisualFor(flow.key).entryCount} entries in this root flow.`"
                >
                  {{ caseInputFlowStoryVisualFor(flow.key).shownEntryCount }} of {{ caseInputFlowStoryVisualFor(flow.key).entryCount }} shown
                </span>
                <svg
                  v-if="caseInputFlowStoryVisualFor(flow.key)"
                  class="case-input-flow-story-card__svg"
                  :viewBox="caseInputFlowStoryVisualFor(flow.key).viewBoxString"
                  preserveAspectRatio="xMidYMid meet"
                  role="img"
                  :aria-label="`${flow.displayLabel} transaction flow`"
                >
                  <defs>
                    <marker
                      v-for="edge in caseInputFlowStoryVisualFor(flow.key).edges"
                      :id="caseInputFlowStoryMarkerId(flow.key, edge.id)"
                      :key="`marker-${edge.id}`"
                      viewBox="0 0 10 10"
                      refX="9.2"
                      refY="5"
                      markerUnits="userSpaceOnUse"
                      markerWidth="9"
                      markerHeight="9"
                      orient="auto"
                    >
                      <path d="M 0 0 L 10 5 L 0 10 z" :fill="caseInputFlowStoryEdgeColor(edge)"></path>
                    </marker>
                  </defs>
                  <path
                    v-for="edge in caseInputFlowStoryVisualFor(flow.key).edges"
                    :key="edge.id"
                    class="case-input-flow-story-card__edge"
                    :d="edge.path"
                    :stroke="caseInputFlowStoryEdgeColor(edge)"
                    :marker-end="`url(#${caseInputFlowStoryMarkerId(flow.key, edge.id)})`"
                  >
                    <title>{{ edge.title }}</title>
                  </path>
                  <g
                    v-for="node in caseInputFlowStoryVisualFor(flow.key).nodes"
                    :key="node.id"
                    :class="caseInputFlowStoryNodeClass(node)"
                    :transform="`translate(${node.x} ${node.y})`"
                  >
                    <title>{{ node.title }}</title>
                    <rect
                      v-if="node.type === 'tx'"
                      x="-25"
                      y="-13"
                      width="50"
                      height="26"
                      rx="7"
                      ry="7"
                      :fill="caseInputFlowStoryNodeColor(node)"
                      :stroke="caseInputFlowStoryNodeStroke(node)"
                    />
                    <circle
                      v-else
                      r="15"
                      :fill="caseInputFlowStoryNodeColor(node)"
                      :stroke="caseInputFlowStoryNodeStroke(node)"
                    />
                  </g>
                </svg>

                <button
                  v-else-if="caseInputFlowStoryFlowHasError(flow.key)"
                  class="case-input-flow-story-card__retry"
                  type="button"
                  @click="requestCaseInputFlowStoryVisual(flow.key, { force: true, delay: 0 })"
                >
                  <span aria-hidden="true">↻</span>
                  Load this flow again
                </button>

                <div v-else class="case-input-flow-story-placeholder" aria-hidden="true">
                  <span class="case-input-flow-story-placeholder__node is-address"></span>
                  <span class="case-input-flow-story-placeholder__edge"></span>
                  <span class="case-input-flow-story-placeholder__node is-transaction"></span>
                  <span class="case-input-flow-story-placeholder__edge"></span>
                  <span class="case-input-flow-story-placeholder__node is-address"></span>
                </div>

                <button
                  class="case-input-flow-story-card__solve"
                  type="button"
                  :disabled="casePageHydrating || caseIntakeRailOtherAutomationActive || caseManualStartVisualizerRunning"
                  :aria-label="`Open ${flow.displayLabel} in Flow Solver`"
                  :title="casePageHydrating || caseIntakeRailOtherAutomationActive || caseManualStartVisualizerRunning
                    ? 'Finish the current Case Buddy operation before opening this graph.'
                    : `Open ${flow.displayLabel} in Flow Solver`"
                  @mousedown.stop
                  @click.stop="openCaseInputFlowStoryInManualSolve(flow)"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <circle cx="4.5" cy="12" r="2.25"></circle>
                    <path d="M6.9 12h2.15"></path>
                    <rect x="9.1" y="8.25" width="6.1" height="7.5" rx="1.7" ry="1.7"></rect>
                    <path d="M15.3 12h2.1"></path>
                    <circle cx="19.6" cy="12" r="2.25"></circle>
                    <path d="m16.45 10.55 1.55 1.45-1.55 1.45"></path>
                  </svg>
                </button>
              </div>
              </article>
            </template>
          </div>
        </section>

        <div
          v-if="!caseInputFlowStoryVisible && shouldShowEmptyCaseStarterNudge"
          class="empty-case-starter-nudge"
          role="complementary"
          aria-label="Case Buddy empty case starter"
        >
          <article class="empty-case-starter-nudge__card" @mousedown.stop @click.stop>
            <button
              class="empty-case-starter-nudge__close"
              type="button"
              aria-label="Dismiss starter helper"
              @click="dismissEmptyCaseStarterNudge('dismissed')"
            >
              ×
            </button>
            <span class="empty-case-starter-nudge__eyebrow">Get started</span>
            <h3>Start your case by loading main transactions</h3>
            <p>
              Case Buddy starts from your main/input spreadsheet. Add your starting transaction hashes, and Case Buddy will guide you to identify transaction networks next.
            </p>
            <div class="empty-case-starter-nudge__actions">
              <button class="toolbar-btn toolbar-btn--primary" type="button" @click="openStartingMainTransactionsFromEmptyCaseNudge">
                Load starting transactions
              </button>
              <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="startManuallyFromEmptyCaseStarterNudge">
                Start manually
              </button>
              <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="dismissEmptyCaseStarterNudge('dismissed')">
                Dismiss
              </button>
            </div>
            <label class="empty-case-starter-nudge__checkbox">
              <input
                v-model="emptyCaseStarterNudgeDontShowAgain"
                type="checkbox"
                @change="updateEmptyCaseStarterNudgeUserPreference"
              />
              <span>Don’t show this again</span>
            </label>
          </article>
        </div>

        <HermesSpreadsheetWorkbookTabs
          :key="inputWorkbookComponentKey"
          ref="inputSpreadsheet"
          button-label="+100 Rows"
          width="100%"
          height="100%"
          :highlight-header="true"
          :auto-save-enabled="autoSaveEnabled"
          :enable-formulas="true"
          :keep-selection-selectors="inputKeepSelectionSelectors"
          :custom-context-menu-items="inputWorkbookContextMenuItems"
          :storage-key="currentProjectInputStorageKey"
          :legacy-storage-keys="projectLegacyStorageKeys"
          @request-save="saveSpreadsheetFromEvent"
          @ready="handleInputWorkbookReady"
          @update-last-selection="handleInputSelectionChange"
          @deselected="handleDeselection"
          @context-menu-cell="handleInputContextMenuCell"
          @active-sheet-change="handleActiveInputSheetChange"
          @workbook-storage-error="handleProjectWorkbookStorageError"
        />

        <div class="case-workspace-add-root">
          <button
            class="case-workspace-add-root__button"
            type="button"
            :disabled="casePageHydrating || caseIntakeRailOtherAutomationActive"
            :title="casePageHydrating || caseIntakeRailOtherAutomationActive
              ? 'Finish the current Case Buddy operation before adding another starting transaction.'
              : 'Add another starting transaction to this case.'"
            aria-label="Add another starting transaction"
            @mousedown.stop
            @click.stop="openStartingMainTransactionsFromFlowStory"
          >
            <svg
              class="case-workspace-add-root__icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M12 5v14M5 12h14"></path>
            </svg>
          </button>
        </div>
      </div>

      <BorderModal
        v-if="showBorderModal"
        @close="showBorderModal = false"
        @apply="applyBorderStyle"
      />
    </section>

    <section id="case-files" class="sheet-panel sheet-panel--files case-anchor-panel">
      <div class="sheet-panel__header">
        <div class="sheet-panel__heading">
          <h2>Case Management</h2>
      
        </div>

        <div class="sheet-panel__header-actions">
          <span class="header-badge header-badge--active">
            {{ currentProjectFileName }}
          </span>
        </div>
      </div>

      <div class="file-manager">
        <div class="file-manager__row">
          <label class="file-manager__field" for="spreadsheet-project-file-select">
            <span>Load file</span>
            <select
              id="spreadsheet-project-file-select"
              v-model="selectedProjectFileId"
              class="file-manager__select"
            >
              <option
                v-for="file in projectFiles"
                :key="file.id"
                :value="file.id"
              >
                {{ file.name }}
              </option>
            </select>
          </label>

          <div class="file-manager__actions">
            <button
              class="header-action-btn header-action-btn--case-browser"
              type="button"
              @click="openCaseFileBrowserModal"
            >
              Browse cases
            </button>
            <button
              class="header-action-btn"
              type="button"
              :disabled="!canLoadSelectedProjectFile"
              @click="loadSelectedProjectFile"
            >
              Load
            </button>
            <button class="header-action-btn" type="button" @click="saveCurrentProjectFile()">
              Save
            </button>
            <button class="header-action-btn" type="button" @click="saveCurrentProjectAsNewFile">
              Save As
            </button>
            <button class="header-action-btn" type="button" @click="createBlankProjectFile">
              New
            </button>
            <button class="header-action-btn" type="button" @click="renameCurrentProjectFile">
              Rename
            </button>
            <button
              class="header-action-btn header-action-btn--ghost"
              type="button"
              :disabled="projectFiles.length <= 1"
              @click="deleteCurrentProjectFile"
            >
              Delete
            </button>
          </div>
        </div>

        <div class="file-manager__meta">
          <span>{{ projectFiles.length }} file{{ projectFiles.length === 1 ? '' : 's' }}</span>
          <span v-if="currentProjectUpdatedLabel">Last saved {{ currentProjectUpdatedLabel }}</span>
          <span v-if="!autoSaveEnabled">Auto-save is off. Use Save to keep changes.</span>
        </div>

        <div v-if="casePrepBuddyHandoffNotice.visible" class="file-manager__handoff-notice" :class="`file-manager__handoff-notice--${casePrepBuddyHandoffNotice.status || 'info'}`" role="status">
          <div>
            <strong>{{ casePrepBuddyHandoffNotice.title }}</strong>
            <p>{{ casePrepBuddyHandoffNotice.message }}</p>
            <small v-if="casePrepBuddyHandoffNotice.transactionCount">
              {{ casePrepBuddyHandoffNotice.transactionCount }} starting row{{ casePrepBuddyHandoffNotice.transactionCount === 1 ? '' : 's' }} · {{ currentProjectFileName }}
            </small>
          </div>
          <div class="file-manager__handoff-actions">
            <button class="header-action-btn header-action-btn--primary" type="button" @click="scrollToSection('case-workflow')">
              Continue workflow
            </button>
            <button class="header-action-btn header-action-btn--ghost" type="button" @click="dismissCasePrepBuddyHandoffNotice">
              Dismiss
            </button>
          </div>
        </div>

        <div v-if="caseProjectStorageNotice.visible" class="file-manager__storage-warning" role="alert">
          <div>
            <strong>{{ caseProjectStorageNotice.title }}</strong>
            <p>{{ caseProjectStorageNotice.message }}</p>
            <small>
              Saved Case Buddy storage: {{ caseProjectStorageNotice.caseBuddyProjectSizeLabel || caseProjectStorageNotice.caseBuddySizeLabel || 'unknown' }}
              <template v-if="caseProjectStorageNotice.attemptedSizeLabel"> · attempted save {{ caseProjectStorageNotice.attemptedSizeLabel }}</template>
            </small>
          </div>
          <div class="file-manager__storage-warning-actions">
            <button class="header-action-btn header-action-btn--case-browser" type="button" @click="openCaseFileBrowserModal({ storageFocus: true })">
              Browse / delete cases
            </button>
            <button class="header-action-btn header-action-btn--ghost" type="button" @click="clearCaseProjectStorageNotice">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </section>


    <div v-if="showCaseFileBrowserModal" class="agent-modal-backdrop case-file-browser-backdrop" @click="cancelCaseFileBrowserModal">
      <div class="agent-modal network-lookup-modal case-file-browser-modal case-file-browser-modal--streamlined" @click.stop>
        <div class="agent-modal__header case-file-browser-modal__header">
          <div>
            <p class="eyebrow-label">Case Management</p>
            <h3>Choose a case</h3>
            <p>Pick a visual preview, or search when you have many cases.</p>
          </div>
          <button class="agent-modal__close" type="button" aria-label="Close case management" @click="cancelCaseFileBrowserModal"><span class="case-file-browser-close-icon" aria-hidden="true"></span></button>
        </div>

        <div v-if="caseFileBrowserLoading" class="case-file-browser-status case-file-browser-status--loading" role="status" aria-live="polite">
          <div class="case-file-browser-status__main">
            <span class="case-file-browser-spinner" aria-hidden="true"></span>
            <div>
              <strong>{{ caseFileBrowserStatus || 'Loading saved cases…' }}</strong>
              <small>{{ caseFileBrowserSummaryLabel }}</small>
            </div>
          </div>
          <div class="case-file-browser-meter" role="progressbar" :aria-valuenow="caseFileBrowserProgressPercent" aria-valuemin="0" aria-valuemax="100">
            <span :style="caseFileBrowserProgressStyle"></span>
          </div>
        </div>
        <div v-else class="case-file-browser-quiet-summary" aria-live="polite">
          <strong>{{ caseFileBrowserVisibleRows.length }}</strong>
          <span>of {{ caseFileBrowserRows.length || projectFiles.length }} saved case{{ (caseFileBrowserRows.length || projectFiles.length) === 1 ? '' : 's' }} shown</span>
        </div>

        <div class="case-file-browser-controls case-file-browser-controls--streamlined">
          <label class="case-file-browser-search">
            <span class="sr-only">Search cases</span>
            <input v-model="caseFileBrowserSearch" type="search" placeholder="Search cases by name, network, token, or hash" />
          </label>
          <button
            class="toolbar-btn toolbar-btn--primary case-file-browser-new-case-btn"
            type="button"
            :disabled="caseFileBrowserLoading || caseFileBrowserCreating || caseFileBrowserDeleting"
            @click="createCaseFileBrowserNewCase"
          >
            <svg aria-hidden="true" viewBox="0 0 20 20">
              <path d="M10 4v12M4 10h12" />
            </svg>
            <span>{{ caseFileBrowserCreating ? 'Creating…' : 'New case' }}</span>
          </button>
          <button
            class="case-file-browser-options-toggle"
            type="button"
            :aria-expanded="caseFileBrowserOptionsOpen ? 'true' : 'false'"
            aria-controls="case-file-browser-options-panel"
            @click="caseFileBrowserOptionsOpen = !caseFileBrowserOptionsOpen"
          >
            <span>Filters & storage</span>
            <span class="case-file-browser-options-toggle__chevron" aria-hidden="true">{{ caseFileBrowserOptionsOpen ? '⌃' : '⌄' }}</span>
          </button>
          <div v-if="caseFileBrowserOptionsOpen" id="case-file-browser-options-panel" class="case-file-browser-options__panel">
            <label>
              <span>Network</span>
              <select v-model="caseFileBrowserNetworkFilter">
                <option value="all">All networks</option>
                <option v-for="option in caseFileBrowserNetworkOptions" :key="option.key" :value="option.key">
                  {{ option.label }} ({{ option.count }})
                </option>
              </select>
            </label>
            <label>
              <span>Sort</span>
              <select v-model="caseFileBrowserSortMode">
                <option value="updated_desc">Recently saved</option>
                <option value="name_asc">Name A → Z</option>
                <option value="main_desc">Most main transactions</option>
                <option value="sub_desc">Most subtransactions</option>
                <option value="saplings_desc">Most prepared graphs</option>
                <option value="swaps_desc">Most swaps</option>
                <option value="bridges_desc">Most bridges</option>
                <option value="btc_first">BTC cases first</option>
                <option value="raw_desc">Most raw entries</option>
                <option value="storage_desc">Largest saved cases</option>
              </select>
            </label>
            <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="caseFileBrowserLoading" @click="refreshCaseFileBrowser({ preserveSelection: true })">
              Refresh cases
            </button>
            <button class="toolbar-btn toolbar-btn--danger" type="button" :disabled="caseFileBrowserLoading" @click="openCaseFileBrowserPurgeModal">
              Delete saved data
            </button>
          </div>
        </div>

        <div v-if="!caseFileBrowserLoading && caseFileBrowserRows.length === 0" class="case-file-browser-empty">
          No saved cases yet. Create a new case here to begin.
        </div>

        <div v-else class="case-file-browser-layout">
          <div ref="caseFileBrowserListEl" class="case-file-browser-list" role="list" aria-label="Saved cases" @scroll.passive="onCaseFileBrowserListScroll">
            <button
              v-for="row in caseFileBrowserVisibleRows"
              :key="row.id"
              class="case-file-browser-card"
              :class="{
                'case-file-browser-card--selected': row.id === caseFileBrowserSelectedId,
                'case-file-browser-card--current': row.isCurrent,
                'case-file-browser-card--empty': !row.hasData,
                'case-file-browser-card--error': !!row.error,
                'case-file-browser-card--preview-loading': caseFileBrowserPreviewIsLoading(row.id)
              }"
              type="button"
              role="listitem"
              :data-case-file-id="row.id"
              @click="selectCaseFileBrowserRow(row.id)"
              @mouseenter="startCaseFileBrowserCardPreviewCycle(row.id)"
              @mouseleave="stopCaseFileBrowserCardPreviewCycle(row.id)"
              @focus="onCaseFileBrowserCardFocus(row.id)"
              @blur="stopCaseFileBrowserCardPreviewCycle(row.id)"
            >
              <div class="case-file-browser-card__thumb" aria-hidden="true">
                <svg
                  v-if="caseFileBrowserCardPreviewFlow(row)"
                  class="case-file-browser-preview-svg case-file-browser-preview-svg--mini"
                  :viewBox="caseFileBrowserCardPreviewFlow(row).viewBoxString"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <rect class="case-file-browser-preview-bg" :x="caseFileBrowserCardPreviewFlow(row).viewBox.x" :y="caseFileBrowserCardPreviewFlow(row).viewBox.y" :width="caseFileBrowserCardPreviewFlow(row).viewBox.width" :height="caseFileBrowserCardPreviewFlow(row).viewBox.height" />
                  <path v-for="edge in caseFileBrowserCardPreviewFlow(row).edges" :key="edge.id" class="case-file-browser-preview-edge" :d="edge.path" />
                  <g v-for="node in caseFileBrowserCardPreviewFlow(row).nodes" :key="node.id" :class="caseFileBrowserPreviewNodeClass(node)" :transform="`translate(${node.x} ${node.y})`">
                    <rect v-if="node.type === 'tx'" x="-28" y="-14" width="56" height="28" rx="8" ry="8" />
                    <circle v-else r="16" />
                  </g>
                </svg>
                <div v-else class="case-file-browser-preview-placeholder">
                  <span>{{ row.preparedSaplingCount || row.mainTransactionCount || 0 }}</span>
                  <small>{{ caseFileBrowserPreviewIsLoading(row.id) ? 'Loading…' : 'Preview' }}</small>
                </div>
              </div>

              <div class="case-file-browser-card__content">
                <header>
                  <div>
                    <h4>{{ row.displayName }}</h4>
                    <small v-if="row.caseName && row.name && row.caseName !== row.name">Saved as {{ row.name }}</small>
                  </div>
                  <span>{{ row.isCurrent ? 'Current' : row.statusLabel }}</span>
                </header>
                <div class="case-file-browser-card__at-a-glance">
                  <span><strong>{{ row.mainTransactionCount }}</strong> main transaction{{ row.mainTransactionCount === 1 ? '' : 's' }}</span>
                  <em v-if="row.chainNames.length">{{ row.chainNames[0].label }}</em>
                  <em v-if="row.tokenSymbols.length">{{ row.tokenSymbols[0].label }}</em>
                </div>
                <small class="case-file-browser-card__saved">{{ row.updatedLabel || 'Not saved yet' }}</small>
              </div>
            </button>

            <div v-if="!caseFileBrowserLoading && caseFileBrowserVisibleRows.length === 0" class="case-file-browser-empty case-file-browser-empty--inline">
              No cases match this search.
            </div>
          </div>

          <aside class="case-file-browser-details" aria-label="Selected case">
            <template v-if="caseFileBrowserSelectedRow">
              <div class="case-file-browser-details__title">
                <div>
                  <span>{{ caseFileBrowserSelectedRow.isCurrent ? 'Currently loaded' : 'Saved case' }}</span>
                  <h4>{{ caseFileBrowserSelectedRow.displayName }}</h4>
                  <p>{{ caseFileBrowserSelectedRow.updatedLabel || caseFileBrowserSelectedRow.statusLabel }}</p>
                </div>
                <div class="case-file-browser-details__actions">
                  <button
                    class="toolbar-btn toolbar-btn--primary"
                    type="button"
                    :disabled="caseFileBrowserLoading || caseFileBrowserCreating || caseFileBrowserDeleting || caseFileBrowserSelectedRow.isCurrent"
                    @click="loadSelectedCaseFileBrowserRow"
                  >
                    {{ caseFileBrowserSelectedRow.isCurrent ? 'Loaded' : 'Load this case' }}
                  </button>
                  <button
                    class="toolbar-btn toolbar-btn--danger case-file-browser-delete-case-btn"
                    type="button"
                    :disabled="caseFileBrowserLoading || caseFileBrowserCreating || caseFileBrowserDeleting || projectFiles.length <= 1"
                    @click="deleteSelectedCaseFileBrowserRow"
                  >
                    {{ caseFileBrowserDeleting ? 'Deleting…' : 'Delete case' }}
                  </button>
                </div>
              </div>

              <div class="case-file-browser-details__summary">
                <article><strong>{{ caseFileBrowserSelectedRow.mainTransactionCount }}</strong><span>Main transactions</span></article>
                <article><strong>{{ caseFileBrowserSelectedRow.chainNames.length ? caseFileBrowserSelectedRow.chainNames[0].label : 'Unknown' }}</strong><span>Network</span></article>
                <article><strong>{{ caseFileBrowserSelectedRow.tokenSymbols.length ? caseFileBrowserSelectedRow.tokenSymbols[0].label : '—' }}</strong><span>Primary token</span></article>
              </div>

              <section class="case-file-browser-preview-panel" :class="{ 'case-file-browser-preview-panel--loading': caseFileBrowserSelectedPreviewStatus === 'loading' }">
                <div class="case-file-browser-preview-panel__header">
                  <div>
                    <span>Visual preview</span>
                    <strong>{{ caseFileBrowserSelectedPreviewTitle }}</strong>
                    <small>{{ caseFileBrowserSelectedPreviewSubtitle }}</small>
                  </div>
                  <div class="case-file-browser-preview-panel__controls">
                    <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" :disabled="!caseFileBrowserSelectedPreviewCanGoPrevious" title="Previous graph preview" @click="showPreviousCaseFileBrowserPreviewFlow">‹</button>
                    <span>{{ caseFileBrowserSelectedPreviewPositionLabel }}</span>
                    <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" :disabled="!caseFileBrowserSelectedPreviewCanGoNext" title="Next graph preview" @click="showNextCaseFileBrowserPreviewFlow">›</button>
                  </div>
                </div>

                <div class="case-file-browser-preview-stage">
                  <svg
                    v-if="caseFileBrowserSelectedPreviewFlow"
                    class="case-file-browser-preview-svg case-file-browser-preview-svg--large"
                    :viewBox="caseFileBrowserSelectedPreviewFlow.viewBoxString"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <rect class="case-file-browser-preview-bg" :x="caseFileBrowserSelectedPreviewFlow.viewBox.x" :y="caseFileBrowserSelectedPreviewFlow.viewBox.y" :width="caseFileBrowserSelectedPreviewFlow.viewBox.width" :height="caseFileBrowserSelectedPreviewFlow.viewBox.height" />
                    <path v-for="edge in caseFileBrowserSelectedPreviewFlow.edges" :key="edge.id" class="case-file-browser-preview-edge" :d="edge.path" />
                    <g v-for="node in caseFileBrowserSelectedPreviewFlow.nodes" :key="node.id" :class="caseFileBrowserPreviewNodeClass(node)" :transform="`translate(${node.x} ${node.y})`">
                      <rect v-if="node.type === 'tx'" x="-42" y="-20" width="84" height="40" rx="10" ry="10" />
                      <circle v-else r="24" />
                      <text v-if="node.type === 'tx'" text-anchor="middle" y="4">{{ node.shortLabel }}</text>
                    </g>
                  </svg>
                  <div v-else class="case-file-browser-preview-placeholder case-file-browser-preview-placeholder--large">
                    <span>{{ caseFileBrowserSelectedPreviewStatus === 'loading' ? '⌛' : '◇' }}</span>
                    <small>{{ caseFileBrowserSelectedPreviewStatus === 'loading' ? 'Loading visual preview…' : caseFileBrowserSelectedPreviewEmptyLabel }}</small>
                  </div>
                </div>

                <div v-if="caseFileBrowserSelectedPreviewFlows.length > 1" class="case-file-browser-preview-strip" aria-label="Graph previews">
                  <button
                    v-for="(flow, flowIndex) in caseFileBrowserSelectedPreviewFlows"
                    :key="flow.key"
                    type="button"
                    :class="{ 'is-active': flowIndex === caseFileBrowserSelectedPreviewIndex }"
                    @click="caseFileBrowserSelectedPreviewIndex = flowIndex"
                  >
                    <strong>{{ flow.label }}</strong>
                    <small>{{ flow.entryCount }} txns</small>
                  </button>
                </div>
              </section>

              <details class="case-file-browser-details__more">
                <summary>
                  <span>More case details</span>
                  <small>Rows, dates, tokens, storage, swaps and bridges</small>
                </summary>
                <div class="case-file-browser-details__more-body">
                  <div class="case-file-browser-details__meta">
                    <span v-if="caseFileBrowserSelectedRow.caseId"><strong>Case ID</strong>{{ caseFileBrowserSelectedRow.caseId }}</span>
                    <span v-if="caseFileBrowserSelectedRow.caseType"><strong>Type</strong>{{ caseFileBrowserSelectedRow.caseType }}</span>
                    <span><strong>Saved file</strong>{{ caseFileBrowserSelectedRow.name }}</span>
                    <span><strong>Updated</strong>{{ caseFileBrowserSelectedRow.updatedLabel || 'No timestamp' }}</span>
                    <span><strong>Date range</strong>{{ caseFileBrowserSelectedRow.dateRangeLabel }}</span>
                    <span><strong>Revision</strong>{{ caseFileBrowserSelectedRow.caseDataRevision }}</span>
                    <span v-if="caseFileBrowserSelectedRow.storageSizeLabel"><strong>Stored size</strong>{{ caseFileBrowserSelectedRow.storageSizeLabel }}</span>
                  </div>

                  <div class="case-file-browser-details__stats">
                    <article><strong>{{ caseFileBrowserSelectedRow.mainTransactionCount }}</strong><span>Top-level main transactions</span></article>
                    <article><strong>{{ caseFileBrowserSelectedRow.subtransactionCount }}</strong><span>Subtransactions</span></article>
                    <article><strong>{{ caseFileBrowserSelectedRow.mainEntryCount }}</strong><span>Total transaction entries</span></article>
                    <article><strong>{{ caseFileBrowserSelectedRow.rawEntryCount }}</strong><span>Raw transaction entries</span></article>
                    <article><strong>{{ caseFileBrowserSelectedRow.preparedSaplingCount }}</strong><span>Prepared graphs</span></article>
                    <article><strong>{{ caseFileBrowserSelectedRow.pathEndingRows }}</strong><span>Path endings</span></article>
                    <article><strong>{{ caseFileBrowserSelectedRow.qualifiedOutputRows }}</strong><span>Funds/left-bound rows</span></article>
                    <article><strong>{{ caseFileBrowserSelectedRow.swapEntryCount || 0 }}</strong><span>Swap entries</span></article>
                    <article><strong>{{ caseFileBrowserSelectedRow.bridgeEntryCount || 0 }}</strong><span>Bridge entries</span></article>
                    <article><strong>{{ caseFileBrowserSelectedRow.addressCount }}</strong><span>Unique From/To addresses</span></article>
                  </div>

                  <div class="case-file-browser-details__groups">
                    <section>
                      <h5>Chains</h5>
                      <div v-if="caseFileBrowserSelectedRow.chainNames.length" class="case-file-browser-chip-list">
                        <span v-for="chain in caseFileBrowserSelectedRow.chainNames" :key="`selected-chain-${chain.label}`">{{ chain.label }} <em>{{ chain.count }}</em></span>
                      </div>
                      <p v-else>No chain names found yet.</p>
                    </section>
                    <section>
                      <h5>Tokens</h5>
                      <div v-if="caseFileBrowserSelectedRow.tokenSymbols.length" class="case-file-browser-chip-list">
                        <span v-for="token in caseFileBrowserSelectedRow.tokenSymbols" :key="`selected-token-${token.label}`">{{ token.label }} <em>{{ token.count }}</em></span>
                      </div>
                      <p v-else>No token symbols found yet.</p>
                    </section>
                    <section>
                      <h5>Swap / bridge signals</h5>
                      <p>{{ caseFileBrowserSelectedRow.swapBridgeSummary || 'No swap/bridge note signals found.' }}</p>
                      <div v-if="(caseFileBrowserSelectedRow.swapExamples || []).length || (caseFileBrowserSelectedRow.bridgeExamples || []).length" class="case-file-browser-chip-list">
                        <span v-for="item in (caseFileBrowserSelectedRow.swapExamples || [])" :key="`selected-swap-${item}`">Swap <em>{{ item }}</em></span>
                        <span v-for="item in (caseFileBrowserSelectedRow.bridgeExamples || [])" :key="`selected-bridge-${item}`">Bridge <em>{{ item }}</em></span>
                      </div>
                    </section>
                    <section>
                      <h5>Main transaction hashes</h5>
                      <p>{{ caseFileBrowserSelectedRow.hashExamples.length ? caseFileBrowserSelectedRow.hashExamples.join(' · ') : 'No main transaction hashes found yet.' }}</p>
                    </section>
                  </div>

                </div>
              </details>
            </template>
            <div v-else class="case-file-browser-empty case-file-browser-empty--details">
              Choose a case on the left to see its visual preview.
            </div>
          </aside>
        </div>
      </div>
    </div>
    <div v-if="showCaseFileBrowserPurgeModal" class="agent-modal-backdrop" @click="cancelCaseFileBrowserPurgeModal">
      <div class="agent-modal case-file-browser-purge-modal" @click.stop>
        <h3>Delete all saved Case Buddy data?</h3>
        <p>This removes saved Case Buddy case files and Case Buddy local-storage records from this browser, then refreshes Case Buddy. It does not intentionally remove login/auth tokens.</p>

        <div class="case-file-browser-purge-modal__warning">
          <strong>This cannot be undone from Case Buddy.</strong>
          <span>{{ caseFileBrowserPurgeStorageSummary }}</span>
        </div>

        <label class="case-file-browser-purge-modal__checkbox">
          <input v-model="caseFileBrowserPurgeConfirmChecked" type="checkbox" />
          <span>I understand this will delete all saved Case Buddy case data stored in this browser.</span>
        </label>

        <label class="case-details-modal__field">
          <span>Type exactly: {{ caseFileBrowserPurgePhrase }}</span>
          <input v-model="caseFileBrowserPurgeConfirmPhrase" type="text" autocomplete="off" spellcheck="false" />
        </label>

        <div v-if="caseFileBrowserPurgeStatus" class="network-lookup-modal__status">
          {{ caseFileBrowserPurgeStatus }}
        </div>

        <div class="agent-modal__actions">
          <button class="toolbar-btn toolbar-btn--danger" type="button" :disabled="!caseFileBrowserPurgeCanConfirm" @click="confirmCaseFileBrowserPurgeAllData">
            Delete all saved Case Buddy data
          </button>
          <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelCaseFileBrowserPurgeModal">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <section
      id="case-workflow"
      class="sheet-panel case-anchor-panel workflow-panel case-foldout-panel"
      :class="{ 'case-foldout-panel--collapsed': !workflowPanelExpanded }"
    >
      <div
        class="sheet-panel__header case-foldout-section-header"
        :class="{ 'case-foldout-section-header--clickable': !workflowPanelExpanded }"
        :title="!workflowPanelExpanded ? 'Show Workflow, Progress, And Tools' : null"
        @click="!workflowPanelExpanded && setWorkflowPanelExpanded(true)"
      >
        <div class="sheet-panel__heading">
          <h2>Workflow, Progress, And Tools</h2>
          <p v-if="workflowPanelExpanded" class="sheet-panel__help">
            When these steps are completed, the case is done and a report is created.
          </p>
        </div>

        <div class="sheet-panel__header-actions">
          <span class="header-badge" :class="{ 'header-badge--active': workflowProgress.completed > 0 }">
            {{ workflowProgress.completed }} / {{ workflowProgress.total }} complete
          </span>
          <template v-if="workflowPanelExpanded">
            <button class="header-action-btn" type="button" @click="refreshCaseSummary">
              Refresh summary
            </button>
            <button class="header-action-btn header-action-btn--ghost" type="button" :disabled="caseChecksRunning" @click="runWorkflowChecks">
              {{ caseWorkflowRunChecksLabel }}
            </button>
            <button
              class="header-action-btn header-action-btn--ghost"
              type="button"
              title="Copy a workflow Auto debug log for the current case state."
              @click="copyCaseWorkflowAutomationDebugSnapshot"
            >
              Debug
            </button>
            <button
              v-if="caseWorkflowRawDataTool88PersistentDebugVisible"
              class="header-action-btn header-action-btn--ghost"
              type="button"
              title="Copy the last Flow Solver debug log, even after Auto has returned to the main screen."
              @click="copyCaseWorkflowRawDataTool88DebugSnapshot"
            >
              Flow Solver debug
            </button>
            <div class="case-automation-controls" :class="caseAutomationControlsClass">
              <button
                class="header-action-btn header-action-btn--primary"
                type="button"
                :disabled="caseAutomationActionDisabled"
                @click="runCaseAutomationNext"
              >
                {{ caseAutomationNextButtonLabel }}
              </button>
              <button
                class="header-action-btn"
                :class="{ 'header-action-btn--danger': caseAutomation.active }"
                type="button"
                :disabled="caseManualStartViewportAutoActive || caseManualStartAllFlowsAutoActive || (caseAutomation.busy && !caseAutomation.active)"
                @click="caseAutomation.active ? requestCaseAutomationUserStop('Automation stopped by user.') : startCaseAutomationFromUser()"
              >
                {{ caseAutomationAutoButtonLabel }}
              </button>
              <label class="case-automation-controls__toggle" title="Let Next/Auto rubber-stamp review-only ambiguity cards when the app can proceed safely.">
                <input v-model="caseSkipResolvableReviewsEnabled" type="checkbox" :disabled="caseAutomation.active || caseAutomation.busy" />
                <span>100% Automated Mode</span>
              </label>
              <label class="case-automation-controls__toggle" title="Try embedded Flow Solver for raw-data solving before falling back to WayFinder handoff.">
                <input v-model="caseAutomationPreferHopSolverRawData" type="checkbox" :disabled="caseAutomation.active || caseAutomation.busy" />
                <span>Use standard flow solver</span>
              </label>
              <label class="case-automation-controls__speed" title="Adjust the pause between Auto actions.">
                <span>Speed</span>
                <input v-model.number="caseAutomation.speedMultiplier" type="range" min="0.5" max="3" step="0.25" :disabled="caseAutomation.active" />
                <strong>{{ caseAutomationSpeedLabel }}</strong>
              </label>
            </div>
          </template>
        </div>
        <button
          class="header-action-btn header-action-btn--ghost case-section-foldout-toggle case-workflow-panel-toggle"
          type="button"
          :aria-expanded="workflowPanelExpanded ? 'true' : 'false'"
          aria-controls="case-workflow-content"
          :title="workflowPanelExpanded ? 'Collapse Workflow, Progress, And Tools' : 'Show Workflow, Progress, And Tools'"
          @click.stop="toggleWorkflowPanelExpanded"
        >
          <span
            class="case-section-foldout-toggle__chevron"
            :class="{ 'is-open': workflowPanelExpanded }"
            aria-hidden="true"
          >▾</span>
          {{ workflowPanelExpanded ? 'Hide workflow' : 'Show workflow' }}
        </button>
      </div>

      <div
        v-show="workflowPanelExpanded"
        id="case-workflow-content"
        class="case-foldout-panel__content"
      >
      <div class="workflow-overview-grid">
        <article class="workflow-overview-card">
          <span>Recommended next</span>
          <strong>{{ recommendedWorkflowStepTitle }}</strong>
          <small>{{ recommendedWorkflowStepHelp }}</small>
        </article>
        <article class="workflow-overview-card">
          <span>Checks</span>
          <strong>{{ checkSummaryLabel }}</strong>
          <small>{{ validationStateLabel }}</small>
        </article>
        <article class="workflow-overview-card">
          <span>Graph state</span>
          <strong>{{ saplingStateLabel }}</strong>
          <small>Main data and raw data stay separate.</small>
        </article>
      </div>

      <div class="case-automation-panel" :class="caseAutomationPanelClass">
        <div class="case-automation-panel__main">
          <span class="case-automation-panel__eyebrow">Next / Auto assistant</span>
          <strong>{{ caseAutomation.status || 'Idle' }}</strong>
          <small>{{ caseAutomation.detail || 'Use Next for one smart action, or Auto to continue through safe actions until a manual pause.' }}</small>
        </div>
        <div class="case-automation-panel__meta">
          <span>{{ caseAutomationNextSummary }}</span>
          <span>{{ caseAutomationReviewModeLabel }}</span>
        </div>
        <details v-if="caseAutomationRecentLog.length" class="case-automation-panel__log">
          <summary>Recent automation actions</summary>
          <ul>
            <li v-for="entry in caseAutomationRecentLog" :key="entry.id">
              <span>{{ new Date(entry.atUtc).toLocaleTimeString() }}</span>
              <small>{{ entry.message }}</small>
            </li>
          </ul>
        </details>
      </div>

      <div v-if="caseWorkflowRawDataTool88PersistentDebugVisible" class="case-automation-tool88-debug-card case-automation-tool88-debug-card--workflow">
        <div>
          <span>Flow Solver raw-data diagnostics</span>
          <strong>{{ caseWorkflowRawDataTool88PauseDebugSummary }}</strong>
          <small>{{ caseWorkflowRawDataTool88PauseDebugDetail }}</small>
          <small v-if="caseWorkflowRawDataTool88LastDebugCopiedAtUtc">Last copied: {{ new Date(caseWorkflowRawDataTool88LastDebugCopiedAtUtc).toLocaleString() }}</small>
        </div>
        <div class="case-automation-tool88-debug-card__chips">
          <span v-for="chip in caseWorkflowRawDataTool88PauseDebugChips" :key="`workflow-tool88-debug-${chip.label}`">
            <strong>{{ chip.label }}</strong>
            {{ chip.value }}
          </span>
        </div>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="copyCaseWorkflowRawDataTool88DebugSnapshot">
          Copy last Flow Solver debug log
        </button>
      </div>

      <div class="workflow-groups">
        <details
          v-for="group in workflowGroupsView"
          :key="`workflow-${currentProjectFileId || 'case'}-${group.id}`"
          class="workflow-group"
          :open="isWorkflowGroupOpen(group)"
          @toggle="onWorkflowGroupToggle(group, $event)"
        >
          <summary class="workflow-group__summary">
            <span>{{ group.title }}</span>
            <small>{{ group.completed }} / {{ group.steps.length }} complete</small>
          </summary>

          <div class="workflow-step-list">
            <article
              v-for="step in group.steps"
              :id="`workflow-step-${step.tag}`"
              :key="step.tag"
              :data-workflow-step-tag="step.tag"
              class="workflow-step-card"
              :class="[step.statusClass, { 'workflow-step-card--empty-starter-highlight': emptyCaseStarterNudgeHighlightedWorkflowStepTag === step.tag }]"
            >
              <div class="workflow-step-card__main">
                <div class="workflow-step-card__title-row">
                  <span class="workflow-step-card__order">{{ step.displayOrder }}</span>
                  <h3>{{ step.title }}</h3>
                  <button
                    v-if="step.stale"
                    class="workflow-step-card__status workflow-step-card__status--stale-action"
                    type="button"
                    :disabled="caseChecksRunning"
                    :aria-label="caseChecksRunning ? `Checks are already running for ${step.title}` : `Run checks to refresh ${step.title}`"
                    :title="caseChecksRunning ? 'Checks are already running' : 'Run checks to refresh stale workflow results'"
                    @click.stop="runWorkflowChecksFromStaleBadge(step)"
                  >
                    <span class="workflow-stale-action__label">{{ step.statusLabel }}</span>
                    <span class="workflow-stale-action__run">{{ caseChecksRunning ? 'Running…' : 'Run checks' }}</span>
                  </button>
                  <span v-else class="workflow-step-card__status">{{ step.statusLabel }}</span>
                </div>

                <p>{{ step.description }}</p>

                <div class="workflow-source-row">
                  <span class="workflow-source-chip" :class="{ 'workflow-source-chip--active': step.sources.check }">
                    Check
                  </span>
                  <span class="workflow-source-chip" :class="{ 'workflow-source-chip--active': step.sources.signal }">
                    Signal
                  </span>
                  <span class="workflow-source-chip" :class="{ 'workflow-source-chip--active': step.sources.manual }">
                    Manual
                  </span>
                  <button
                    v-if="step.stale"
                    class="workflow-source-chip workflow-source-chip--stale workflow-source-chip--stale-action"
                    type="button"
                    :disabled="caseChecksRunning"
                    :aria-label="caseChecksRunning ? `Checks are already running for ${step.title}` : `Run checks to refresh ${step.title}`"
                    :title="caseChecksRunning ? 'Checks are already running' : 'Run checks to refresh stale workflow results'"
                    @click.stop="runWorkflowChecksFromStaleBadge(step)"
                  >
                    <span class="workflow-source-chip__label">Stale</span>
                    <span class="workflow-source-chip__action">{{ caseChecksRunning ? 'Running…' : 'Run checks' }}</span>
                  </button>
                </div>
              </div>

              <div class="workflow-step-card__actions">
                <div class="workflow-step-card__action-row">
                  <button
                    v-for="action in step.actions"
                    :key="`${step.tag}-${action.id}`"
                    class="toolbar-btn"
                    :class="{
                      'toolbar-btn--primary': action.primary,
                      'toolbar-btn--workflow-important': action.emphasis === 'important' && !step.complete,
                      'toolbar-btn--workflow-subtle-handoff': action.emphasis === 'important' && step.complete
                    }"
                    type="button"
                    :disabled="action.disabled"
                    @click="runWorkflowAction(action, step)"
                  >
                    {{ action.label }}
                  </button>

                  <button
                    v-if="step.tag === 'RAW_DATA_FETCH' || /raw data/i.test(step.title || '')"
                    class="toolbar-btn toolbar-btn--primary"
                    type="button"
                    :disabled="caseManualStartVisualizerRunning"
                    :title="caseManualStartVisualizerLaunchTitle"
                    @click="openCaseManualStartVisualizerFromWorkflow"
                  >
                    {{ caseManualStartVisualizerButtonLabel }}
                  </button>

                  <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="toggleManualWorkflowComplete(step)">
                    {{ step.sources.manual ? 'Undo manual' : 'Manual complete' }}
                  </button>
                </div>

                <button
                  class="workflow-step-help-btn workflow-step-help-btn--action"
                  type="button"
                  :aria-label="`Open help for ${step.title}`"
                  title="Explain this workflow step"
                  @click="openWorkflowStepHelp(step)"
                >
                  ?
                </button>
              </div>
            </article>
          </div>
        </details>
      </div>
      </div>
    </section>

    <section
      id="case-deliverables"
      class="sheet-panel case-anchor-panel deliverables-panel case-foldout-panel"
      :class="{ 'case-foldout-panel--collapsed': !deliverablesPanelExpanded }"
    >
      <div
        class="sheet-panel__header case-foldout-section-header"
        :class="{ 'case-foldout-section-header--clickable': !deliverablesPanelExpanded }"
        :title="!deliverablesPanelExpanded ? 'Show Deliverables' : null"
        @click="!deliverablesPanelExpanded && setDeliverablesPanelExpanded(true)"
      >
        <div class="sheet-panel__heading">
          <h2>Deliverables</h2>
        </div>

        <div v-if="deliverablesPanelExpanded" class="sheet-panel__header-actions">
          <span class="header-badge" :class="{ 'header-badge--active': deliverableProgress.current > 0 }">
            {{ deliverableProgress.current }} current
          </span>
          <button class="header-action-btn" type="button" @click="buildOneClickPackagePreview">
            One-click package preview
          </button>
        </div>

        <button
          class="header-action-btn header-action-btn--ghost case-section-foldout-toggle deliverables-panel-toggle"
          type="button"
          :aria-expanded="deliverablesPanelExpanded ? 'true' : 'false'"
          aria-controls="case-deliverables-content"
          :title="deliverablesPanelExpanded ? 'Collapse Deliverables' : 'Show Deliverables'"
          @click.stop="toggleDeliverablesPanelExpanded"
        >
          <span
            class="case-section-foldout-toggle__chevron"
            :class="{ 'is-open': deliverablesPanelExpanded }"
            aria-hidden="true"
          >▾</span>
          {{ deliverablesPanelExpanded ? 'Hide deliverables' : 'Show deliverables' }}
        </button>
      </div>

      <div
        v-show="deliverablesPanelExpanded"
        id="case-deliverables-content"
        class="case-foldout-panel__content deliverables-panel__content"
      >
      <div class="deliverable-groups">
        <details
          v-for="group in deliverableGroupsView"
          :key="`deliverables-${currentProjectFileId || 'case'}-${group.id}`"
          class="deliverable-group"
          :open="isDeliverableGroupOpen(group)"
          @toggle="onDeliverableGroupToggle(group, $event)"
        >
          <summary class="deliverable-group__summary">
            <span>{{ group.title }}</span>
            <small>{{ group.current }} current · {{ group.available }} available</small>
          </summary>

          <div class="deliverable-card-grid">
            <article
              v-for="deliverable in group.items"
              :key="deliverable.id"
              class="deliverable-card"
              :class="deliverable.statusClass"
            >
              <div class="deliverable-card__header">
                <h3>{{ deliverable.title }}</h3>
                <span>{{ deliverable.statusLabel }}</span>
              </div>
              <p>{{ deliverable.description }}</p>
              <small v-if="deliverable.builtAtLabel">Built {{ deliverable.builtAtLabel }}</small>
              <small v-else>{{ deliverable.requirementLabel }}</small>

              <div class="deliverable-card__actions">
                <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!deliverable.canBuild" @click="buildDeliverable(deliverable)">
                  {{ deliverable.primaryActionLabel || 'Preview / build' }}
                </button>
                <button class="toolbar-btn" type="button" :disabled="!deliverable.canDownload" @click="downloadDeliverable(deliverable)">
                  Download
                </button>
                <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!deliverable.builtAtUtc" @click="toggleDeliverableReviewed(deliverable)">
                  {{ deliverable.reviewed ? 'Reviewed' : 'Mark reviewed' }}
                </button>
              </div>
            </article>
          </div>
        </details>
      </div>
      </div>
    </section>

    <section
      id="case-output"
      class="sheet-panel sheet-panel--output case-anchor-panel"
      :class="{
        'sheet-panel--automation-locked': caseAutomation.active && !caseAutomation.paused,
        'sheet-panel--output-collapsed': !outputSheetExpanded
      }"
    >
      <div
        class="sheet-panel__header case-foldout-section-header"
        :class="{ 'case-foldout-section-header--clickable': !outputSheetExpanded }"
        :title="!outputSheetExpanded ? 'Show the Output spreadsheet and review controls' : null"
        @click="!outputSheetExpanded && setOutputSheetExpanded(true)"
      >
        <div class="sheet-panel__heading">
          <div class="output-sheet-heading-line">
            <h2>Output Spreadsheet (for manual data reviews)</h2>
            <span
              v-if="outputSheetExpanded"
              class="output-sheet-foldout-summary"
              :class="{
                'output-sheet-foldout-summary--has-data': outputHasData,
                'output-sheet-foldout-summary--pending': caseSupportState.outputReview?.status === 'pending'
              }"
              aria-live="polite"
            >
              {{ outputSheetFoldoutSummary }}
            </span>
          </div>
          <p v-if="outputSheetExpanded" class="sheet-panel__help">
            Processed results land here so the input sheet stays editable.
          </p>
        </div>

        <div v-if="outputSheetExpanded" class="sheet-panel__header-actions">
          <button
            class="header-action-btn header-action-btn--visualize"
            type="button"
            :disabled="!caseInvestigationVisualizerReadyState.canOpen"
            :title="caseInvestigationVisualizerReadyState.title"
            @click="openCaseInvestigationVisualizer()"
          >
            Visualize
            <span v-if="caseInvestigationVisualizerReadyState.count" class="header-action-btn__badge">
              {{ caseInvestigationVisualizerReadyState.count }}
            </span>
          </button>
          <button
            class="header-action-btn header-action-btn--copy"
            type="button"
            :disabled="spreadsheetCopyBusy.output"
            title="Copy the output sheet up to its used rows and columns."
            @click="copyOutputSheetToClipboard"
          >
            {{ spreadsheetCopyBusy.output ? 'Copying…' : 'Copy Sheet' }}
          </button>
          <span v-if="spreadsheetCopyStatus.output" class="sheet-copy-status" aria-live="polite">
            {{ spreadsheetCopyStatus.output }}
          </span>
          <button
            v-if="addressLegendQuickAccessAvailable"
            class="header-action-btn header-action-btn--legend"
            type="button"
            title="Load the reviewed address legend as Address + Label rows in Output."
            @click="loadReviewedAddressLegendToOutput"
          >
            Address Legend
          </button>
          <button class="header-action-btn" type="button" @click="exportToCSV">
            Export CSV
          </button>
          <button class="header-action-btn header-action-btn--ghost" type="button" @click="clearOutput">
            Clear output
          </button>
        </div>

        <button
          class="header-action-btn header-action-btn--ghost output-sheet-foldout-toggle output-sheet-panel-toggle"
          type="button"
          :aria-expanded="outputSheetExpanded ? 'true' : 'false'"
          aria-controls="case-output-sheet-grid case-output-review-bar"
          :title="outputSheetExpanded ? 'Collapse the Output spreadsheet' : 'Show the Output spreadsheet and review controls'"
          @click.stop="toggleOutputSheetExpanded"
        >
          <span
            class="output-sheet-foldout-toggle__chevron"
            :class="{ 'is-open': outputSheetExpanded }"
            aria-hidden="true"
          >▾</span>
          {{ outputSheetExpanded ? 'Hide output' : 'Show output' }}
        </button>
      </div>

      <div
        v-show="outputSheetExpanded"
        id="case-output-sheet-grid"
        class="sheet-grid sheet-grid--output"
      >
        <HermesSpreadsheetComponentAdvanced
          :key="outputSheetComponentKey"
          ref="outputSpreadsheet"
          button-label="+100 Rows"
          width="100%"
          height="100%"
          :highlight-header="false"
          :auto-save-enabled="autoSaveEnabled"
          :enable-formulas="false"
          @request-save="handleOutputSheetSaveRequest"
          @ready="handleOutputSheetReady"
        />
      </div>

      <div
        v-show="outputSheetExpanded"
        id="case-output-review-bar"
        class="output-review-bar"
      >
        <div class="output-review-bar__status">
          <span class="output-review-bar__label">Output buffer</span>
          <strong>{{ outputReviewLabel }}</strong>
          <small>{{ outputReviewDetail }}</small>
        </div>

        <div class="output-review-bar__actions">
          <button class="header-action-btn" type="button" :disabled="outputAppendDisabled" :title="outputAppendTitle" @click="appendOutputToLogicalSheet('main')">
            Append to Main Data
          </button>
          <button class="header-action-btn" type="button" :disabled="outputAppendDisabled" :title="outputAppendTitle" @click="appendOutputToLogicalSheet('raw')">
            Append to Raw Data
          </button>
          <button class="header-action-btn header-action-btn--ghost" type="button" :disabled="!outputHasData" @click="markOutputReviewed">
            Mark reviewed
          </button>
        </div>
      </div>
    </section>

    <section
      id="case-instructions"
      class="sheet-panel case-anchor-panel instructions-panel case-foldout-panel"
      :class="{ 'case-foldout-panel--collapsed': !instructionsPanelExpanded }"
    >
      <div
        class="sheet-panel__header case-foldout-section-header"
        :class="{ 'case-foldout-section-header--clickable': !instructionsPanelExpanded }"
        :title="!instructionsPanelExpanded ? 'Show the Case Buddy guide' : null"
        @click="!instructionsPanelExpanded && setInstructionsPanelExpanded(true)"
      >
        <div class="sheet-panel__heading">
          <h2>How to use Case Buddy Web App</h2>
          <p v-if="instructionsPanelExpanded" class="sheet-panel__help">
            A high-level guide for moving from starting transactions to reviewed deliverables.
          </p>
        </div>

        <div class="sheet-panel__header-actions">
          <button
            class="header-action-btn header-action-btn--ghost case-section-foldout-toggle"
            type="button"
            :aria-expanded="instructionsPanelExpanded ? 'true' : 'false'"
            aria-controls="case-instructions-content"
            :title="instructionsPanelExpanded ? 'Collapse the Case Buddy guide' : 'Show the Case Buddy guide'"
            @click.stop="toggleInstructionsPanelExpanded"
          >
            <span
              class="case-section-foldout-toggle__chevron"
              :class="{ 'is-open': instructionsPanelExpanded }"
              aria-hidden="true"
            >▾</span>
            {{ instructionsPanelExpanded ? 'Hide guide' : 'Show guide' }}
          </button>
        </div>
      </div>

      <div
        v-show="instructionsPanelExpanded"
        id="case-instructions-content"
        class="case-foldout-panel__content"
      >
      <div class="instructions-intro">
        <strong>Core idea:</strong> treat the main/input spreadsheet as the case workspace. Tools can help prepare, check, and package the case, but reviewed spreadsheet rows, raw rows, address legend entries, and case metadata remain the source of truth.
      </div>

      <div class="instructions-grid">
        <article class="instruction-card">
          <span class="instruction-card__step">1</span>
          <h3>Open or create one case file</h3>
          <p>Start by creating a case and loading in transactions.  Use Case Prep Buddy if you need help with this.</p>
        </article>

        <article class="instruction-card">
          <span class="instruction-card__step">2</span>
          <h3>Prepare the main sheet</h3>
          <p>Use the tools to prepare the main sheet, which adds the network and date for each transaction.</p>
        </article>

        <article class="instruction-card">
          <span class="instruction-card__step">3</span>
          <h3>Preserve source rows in raw data</h3>
          <p>Use the tools provided to get all the raw data required for your case.  Use the "next" or "auto" buttons available in the Flow Solver to get your data, or manually solve it.</p>
        </article>

        <article class="instruction-card">
          <span class="instruction-card__step">4</span>
          <h3>Run tools into the output buffer</h3>
          <p>Most tools should write proposed results to the output/review sheet first. Inspect the result, then accept it into main data, raw data, legend, or metadata only when it looks right.</p>
        </article>

        <article class="instruction-card">
          <span class="instruction-card__step">5</span>
          <h3>Use workflow as a guide</h3>
          <p>The workflow panel shows you the steps you need to do, in order, to complete a blockchain analysis report.  You can use these panels to review the case in greater detail.</p>
        </article>

        <article class="instruction-card">
          <span class="instruction-card__step">6</span>
          <h3>Check deliberately</h3>
          <p>When you make changes, use the "Run checks" button, to check how close your case is to being completed.  Always validate results and question endpoints - could you have come across a bridge? a swap? a DEX interaction</p>
        </article>

        <article class="instruction-card">
          <span class="instruction-card__step">7</span>
          <h3>Iterate through pruning, and grafting</h3>
          <p>Build the initial graph from starting transactions and prepared raw data, then review attributions, prune irrelevant paths, graft needed extensions, and handle Bridge grafts as needed.</p>
        </article>

        <article class="instruction-card">
          <span class="instruction-card__step">8</span>
          <h3>Review and ship deliverables</h3>
          <p>Use the deliverables panel to preview, download, and mark outputs reviewed. The one-click package should come last, after the necessary supporting deliverables are current.</p>
        </article>
      </div>

      <div class="instructions-footer-note">
        Tip: You can use "Simple view" or the "Workflow assistant", with "100% Automated" enabled, to produce a fast report.  However, the best reports are checked manually at every stage.
      </div>
      </div>
    </section>
      </main>

      <aside
        v-if="caseIntakeRailVisible"
        class="case-scroll-nav case-intake-rail"
        aria-label="Case preparation assistant"
      >
        <div v-if="caseWorkflowAssistantPhase === 'intake'" class="case-intake-rail__header">
          <span class="case-intake-rail__eyebrow">Workflow assistant</span>
          <div class="case-intake-rail__title-row">
            <strong>Prepare intake</strong>
            <span>{{ caseIntakeRailRootCount }}</span>
          </div>
          <p>{{ caseIntakeRailStatusDetail }}</p>
        </div>

        <ol v-if="caseWorkflowAssistantPhase === 'intake'" class="case-intake-rail__steps" aria-label="First five workflow steps">
          <li
            v-for="step in caseIntakeRailStepRows"
            :key="`case-intake-rail-${step.key}`"
            :class="step.className"
            :title="step.statusTitle || step.label"
          >
            <span aria-hidden="true">{{ step.runtimeComplete ? '✓' : step.index + 1 }}</span>
            <small>{{ step.shortLabel }}</small>
          </li>
        </ol>

        <div v-if="caseWorkflowAssistantPhase === 'intake' && caseIntakeRailAllComplete" class="case-intake-rail__ready" role="status">
          <span aria-hidden="true">✓</span>
          <div>
            <strong>Ready for Hydrate / Visual</strong>
            <small>The first five workflow steps are complete.</small>
          </div>
        </div>

        <button
          v-if="caseWorkflowAssistantPhase === 'intake' && !caseIntakeRailAllComplete"
          class="case-intake-rail__prepare-btn"
          type="button"
          :disabled="!caseIntakeRailCanStart"
          :title="caseIntakeRailButtonTitle"
          @click="startCaseIntakePreparation"
        >
          <span class="case-intake-rail__prepare-icon" aria-hidden="true">→</span>
          <span>
            <strong>{{ caseIntakePreparation.active ? 'Preparing case…' : 'Prepare case' }}</strong>
            <small>Click here to easily prepare the case</small>
          </span>
        </button>

        <section
          v-if="caseWorkflowAssistantPhase === 'raw-sapling'"
          class="case-intake-rail__continuation case-intake-rail__continuation--primary"
          aria-labelledby="case-intake-continuation-title"
        >
          <div class="case-intake-rail__continuation-header">
            <div>
              <span>Workflow assistant</span>
              <strong id="case-intake-continuation-title">Raw data &amp; graph</strong>
            </div>
            <button
              v-if="caseIntakeContinuationCheckIsCurrent"
              class="case-intake-rail__recheck"
              type="button"
              :disabled="caseIntakeContinuationCheckDisabled"
              title="Run the focused Raw Data and sapling check again"
              @click="runCaseIntakeContinuationCheck({ source: 'workflow-assistant-recheck' })"
            >
              Check again
            </button>
          </div>

          <p class="case-intake-rail__continuation-detail">{{ caseIntakeContinuationStatusDetail }}</p>

          <ol class="case-intake-rail__continuation-steps" aria-label="Raw Data and graph workflow steps">
            <li
              v-for="step in caseIntakeContinuationStepRows"
              :key="`case-intake-continuation-${step.key}`"
              :class="step.className"
              :title="step.statusTitle"
            >
              <span aria-hidden="true">{{ step.complete ? '✓' : step.number }}</span>
              <div>
                <strong>{{ step.shortLabel }}</strong>
                <small>{{ step.statusLabel }}</small>
                <button
                  v-if="step.issueCount > 0"
                  class="case-intake-rail__issue-toggle"
                  type="button"
                  :aria-expanded="caseIntakeContinuationIssueStage === step.key"
                  aria-controls="case-intake-continuation-findings"
                  @click="toggleCaseIntakeContinuationIssues(step.key)"
                >
                  {{ caseIntakeContinuationIssueStage === step.key
                    ? 'Hide findings'
                    : `View ${step.issueCount} finding${step.issueCount === 1 ? '' : 's'}` }}
                </button>
              </div>
            </li>
          </ol>

          <div
            v-if="caseIntakeContinuationDisplayedIssues.length"
            id="case-intake-continuation-findings"
            class="case-intake-rail__findings"
            role="region"
            aria-live="polite"
          >
            <div class="case-intake-rail__findings-header">
              <strong>What the check found</strong>
              <button type="button" @click="toggleCaseIntakeContinuationIssues('')">Hide</button>
            </div>
            <ol>
              <li
                v-for="(issue, issueIndex) in caseIntakeContinuationDisplayedIssues"
                :key="`case-intake-finding-${caseIntakeContinuationIssueStage}-${issueIndex}`"
              >
                {{ issue }}
              </li>
            </ol>
            <small v-if="caseIntakeContinuationDisplayedIssueCount > caseIntakeContinuationDisplayedIssues.length">
              Showing {{ caseIntakeContinuationDisplayedIssues.length }} of {{ caseIntakeContinuationDisplayedIssueCount }} findings.
            </small>
          </div>

          <button
            v-if="caseIntakeContinuationAction !== 'ready'"
            class="case-intake-rail__prepare-btn case-intake-rail__continuation-action"
            type="button"
            :disabled="caseIntakeContinuationActionDisabled"
            :title="caseIntakeContinuationPrimaryTitle"
            @click="runCaseIntakeContinuationPrimaryAction"
          >
            <span class="case-intake-rail__prepare-icon" aria-hidden="true">{{ caseIntakeContinuationActionIcon }}</span>
            <span>
              <strong>{{ caseIntakeContinuationPrimaryLabel }}</strong>
              <small>{{ caseIntakeContinuationPrimaryHelp }}</small>
            </span>
          </button>

          <div v-else class="case-intake-rail__ready case-intake-rail__ready--continuation" role="status">
            <span aria-hidden="true">✓</span>
            <div>
              <strong>Ready for attribution</strong>
              <small>Raw flows and the prepared graph are current.</small>
            </div>
          </div>
        </section>

        <section
          v-if="caseWorkflowAssistantPhase === 'attribution' && caseAttributionWorkflowRailVisible"
          class="case-intake-rail__continuation case-intake-rail__continuation--primary case-intake-rail__attribution"
          aria-labelledby="case-attribution-workflow-rail-title"
        >
          <div class="case-intake-rail__continuation-header">
            <div>
              <span>Workflow assistant</span>
              <strong id="case-attribution-workflow-rail-title">Attribution, pruning, grafting &amp; Bridges</strong>
            </div>
          </div>

          <p class="case-intake-rail__continuation-detail">{{ caseAttributionWorkflowStatusDetail }}</p>

          <ol class="case-intake-rail__continuation-steps case-intake-rail__attribution-steps" aria-label="Attribution, pruning, grafting, and Bridge workflow steps">
            <li
              v-for="step in caseAttributionWorkflowStepRows"
              :key="`case-attribution-rail-${step.key}`"
              :class="step.className"
              :title="step.statusTitle"
            >
              <span aria-hidden="true">{{ step.complete ? '✓' : step.number }}</span>
              <div>
                <strong>{{ step.shortLabel }}</strong>
                <small>{{ step.statusLabel }}</small>
              </div>
            </li>
          </ol>

          <small class="case-intake-rail__review-mode">{{ caseAttributionWorkflowReviewModeLabel }}</small>

          <button
            v-if="!caseAttributionWorkflowAllComplete"
            class="case-intake-rail__prepare-btn case-intake-rail__continuation-action case-intake-rail__attribution-action"
            type="button"
            :disabled="!caseAttributionWorkflowCanStart"
            :title="caseAttributionWorkflowButtonTitle"
            @click="startCaseAttributionWorkflowRun"
          >
            <span class="case-intake-rail__prepare-icon" aria-hidden="true">→</span>
            <span>
              <strong>{{ caseAttributionWorkflowButtonLabel }}</strong>
              <small>Attribution · pruning/grafts/Bridges · colors · entities · external · USD</small>
            </span>
          </button>

          <template v-else>
            <div class="case-intake-rail__ready case-intake-rail__ready--continuation case-intake-rail__ready--attribution" role="status">
              <span aria-hidden="true">✓</span>
              <div>
                <strong>Ready for consistency &amp; cleanup</strong>
                <small>{{ caseAttributionWorkflowReadyHelp }}</small>
              </div>
            </div>

            <button
              v-if="caseCleanupWorkflowRailEnabled"
              class="case-intake-rail__prepare-btn case-intake-rail__continuation-action case-intake-rail__cleanup-action"
              type="button"
              :disabled="!caseCleanupWorkflowCanStart"
              :title="caseCleanupWorkflowButtonTitle"
              @click="startCaseCleanupWorkflowRun"
            >
              <span class="case-intake-rail__prepare-icon" aria-hidden="true">→</span>
              <span>
                <strong>Begin consistency &amp; cleanup</strong>
                <small>Inconsistencies · notes/patterns · casing · duplicates · legend</small>
              </span>
            </button>
          </template>
        </section>

        <section
          v-if="caseWorkflowAssistantPhase === 'cleanup' && caseCleanupWorkflowRailVisible"
          class="case-intake-rail__continuation case-intake-rail__continuation--primary case-intake-rail__cleanup"
          aria-labelledby="case-cleanup-workflow-rail-title"
        >
          <div class="case-intake-rail__continuation-header">
            <div>
              <span>Workflow assistant</span>
              <strong id="case-cleanup-workflow-rail-title">{{ caseCleanupWorkflowAssistantHeading }}</strong>
            </div>
          </div>

          <template v-if="!caseCleanupWorkflowReadyForDeliverables">
            <p class="case-intake-rail__continuation-detail">{{ caseCleanupWorkflowStatusDetail }}</p>

            <ol class="case-intake-rail__continuation-steps case-intake-rail__cleanup-steps" aria-label="Consistency, notes, legend, and cleanup workflow steps">
              <li
                v-for="step in caseCleanupWorkflowStepRows"
                :key="`case-cleanup-rail-${step.key}`"
                :class="step.className"
                :title="step.statusTitle"
              >
                <span aria-hidden="true">{{ step.runtimeComplete ? '✓' : step.number }}</span>
                <div>
                  <strong>{{ step.shortLabel }}</strong>
                  <small>{{ step.statusLabel }}</small>
                </div>
              </li>
            </ol>

            <small class="case-intake-rail__review-mode">{{ caseCleanupWorkflowReviewModeLabel }}</small>

            <button
              class="case-intake-rail__prepare-btn case-intake-rail__continuation-action case-intake-rail__cleanup-action"
              type="button"
              :disabled="!caseCleanupWorkflowCanStart"
              :title="caseCleanupWorkflowButtonTitle"
              @click="startCaseCleanupWorkflowRun"
            >
              <span class="case-intake-rail__prepare-icon" aria-hidden="true">→</span>
              <span>
                <strong>{{ caseCleanupWorkflowButtonLabel }}</strong>
                <small>Consistency · notes/patterns · casing · duplicate hashes · legend</small>
              </span>
            </button>
          </template>

          <template v-else>
            <div class="case-intake-rail__ready case-intake-rail__ready--continuation case-intake-rail__ready--cleanup" role="status">
              <span aria-hidden="true">✓</span>
              <div>
                <strong>{{ caseWorkflowAssistantFinalReportReadyTitle }}</strong>
                <small>{{ caseWorkflowAssistantFinalReportReadyHelp }}</small>
              </div>
            </div>

            <button
              class="case-intake-rail__prepare-btn case-intake-rail__continuation-action case-intake-rail__cleanup-action case-intake-rail__final-report-action"
              type="button"
              :disabled="!caseWorkflowAssistantFinalReportCanOpen"
              :title="caseWorkflowAssistantFinalReportButtonTitle"
              @click="openCaseWorkflowAssistantFinalReport"
            >
              <span class="case-intake-rail__prepare-icon" aria-hidden="true">↗</span>
              <span>
                <strong>{{ caseWorkflowAssistantFinalReportButtonLabel }}</strong>
                <small>{{ caseWorkflowAssistantFinalReportButtonHelp }}</small>
              </span>
            </button>
          </template>
        </section>

        <div
          class="case-intake-rail__stage-progress"
          role="group"
          :aria-label="caseWorkflowAssistantStageProgressAriaLabel"
        >
          <ol role="list">
            <li
              v-for="stage in caseWorkflowAssistantStageProgressRows"
              :key="`case-workflow-assistant-stage-${stage.key}`"
              :class="stage.className"
              :title="stage.title"
              :aria-label="stage.ariaLabel"
              :aria-current="stage.current ? 'step' : null"
            >
              <span aria-hidden="true">{{ stage.complete ? '✓' : stage.index + 1 }}</span>
            </li>
          </ol>
        </div>

        <small v-if="caseIntakeRailOtherAutomationActive && !caseIntakePreparation.active && !caseAttributionWorkflowRun.active && !caseCleanupWorkflowRun.active" class="case-intake-rail__busy-note">
          Another Case Buddy operation is running. This action will unlock when it finishes.
        </small>
      </aside>
    </div>


<div v-if="showCaseInconsistencyModal" class="agent-modal-backdrop" @click="cancelCaseInconsistencyReviewModal">
  <div class="agent-modal network-lookup-modal case-inconsistency-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Review And Resolve Case Inconsistencies</h3>
        <p>
          Tool 71-style cards scan Main Data for path-ending, continuation, reference, and notes inconsistencies. Apply safe fixes where appropriate, then resolve remaining cards before report buildout.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseInconsistencyRunning" @click="cancelCaseInconsistencyReviewModal">
        Close
      </button>
    </div>

    <div class="network-lookup-modal__summary-grid case-inconsistency-modal__summary-grid">
      <article>
        <span>Entries analyzed</span>
        <strong>{{ caseInconsistencyAnalysis.entriesAnalyzed || 0 }}</strong>
        <small>Main Data entries only.</small>
      </article>
      <article>
        <span>Cards</span>
        <strong>{{ caseInconsistencyCards.length }}</strong>
        <small>{{ caseInconsistencyAnalysis.totalIssueCount || 0 }} issue{{ (caseInconsistencyAnalysis.totalIssueCount || 0) === 1 ? '' : 's' }} found.</small>
      </article>
      <article>
        <span>Pending</span>
        <strong>{{ caseInconsistencyPendingCount }}</strong>
        <small>{{ caseInconsistencyResolvedCount }} resolved.</small>
      </article>
      <article>
        <span>Auto-fixes</span>
        <strong>{{ caseInconsistencySafeFixCount + caseInconsistencyJudgementFixCount }}</strong>
        <small>{{ caseInconsistencySafeFixCount }} safe · {{ caseInconsistencyJudgementFixCount }} judgement.</small>
      </article>
    </div>

    <div class="case-inconsistency-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search cards</span>
        <input v-model.trim="caseInconsistencySearch" type="search" placeholder="Search row, path label, hash, notes, issue…" />
      </label>
      <label class="case-details-modal__field">
        <span>Show</span>
        <select v-model="caseInconsistencyViewFilter">
          <option value="pending">Pending only</option>
          <option value="safe">Safe fixes</option>
          <option value="judgement">Judgement fixes</option>
          <option value="resolved">Resolved</option>
          <option value="all">All cards</option>
        </select>
      </label>
      <div class="case-inconsistency-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" :disabled="caseInconsistencyRunning" @click="refreshCaseInconsistencyReview">
          Refresh cards
        </button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="caseInconsistencyRunning || caseInconsistencySafeFixCount === 0" @click="applyAllSafeCaseInconsistencyFixes">
          Apply safe fixes
        </button>
        <button class="toolbar-btn" type="button" :disabled="caseInconsistencyRunning || caseInconsistencyVisibleCards.length === 0" @click="markVisibleCaseInconsistencyCardsResolved">
          Mark visible resolved
        </button>
      </div>
    </div>

    <details class="case-inconsistency-modal__rules" :open="caseInconsistencyShowRuleSettings" @toggle="caseInconsistencyShowRuleSettings = $event.target.open">
      <summary>Enabled checks</summary>
      <div class="case-inconsistency-modal__rule-grid">
        <label><input v-model="caseInconsistencyRulePathEndsMismatch" type="checkbox" @change="refreshCaseInconsistencyReview({ silent: true })" /> Notes/Column Q Path ends mismatch</label>
        <label><input v-model="caseInconsistencyRuleBranchesAsVsPathEnds" type="checkbox" @change="refreshCaseInconsistencyReview({ silent: true })" /> Branches as vs Path ends</label>
        <label><input v-model="caseInconsistencyRuleUnknownHashes" type="checkbox" @change="refreshCaseInconsistencyReview({ silent: true })" /> Unknown From/Continues hashes</label>
        <label><input v-model="caseInconsistencyRulePathEndsButNoFundsFollowed" type="checkbox" @change="refreshCaseInconsistencyReview({ silent: true })" /> Path ends but no funds followed</label>
        <label><input v-model="caseInconsistencyRuleContinuesAsSingleButNextEntryMismatch" type="checkbox" @change="refreshCaseInconsistencyReview({ silent: true })" /> Single Continues-as next-entry mismatch</label>
      </div>
    </details>

    <div v-if="caseInconsistencyStatus" class="network-lookup-modal__status case-inconsistency-modal__status">
      {{ caseInconsistencyStatus }}
    </div>

    <div v-if="caseInconsistencyVisibleCards.length === 0" class="network-lookup-modal__empty-state">
      No inconsistency cards match the current filter. Refresh cards, show all cards, or run the workflow check to confirm the current state.
    </div>

    <div v-else class="case-inconsistency-modal__card-list">
      <article
        v-for="card in caseInconsistencyVisibleCards"
        :key="card.key"
        class="case-inconsistency-card"
        :class="{
          'case-inconsistency-card--resolved': card.isResolved,
          'case-inconsistency-card--safe': card.hasSafeAutoFixEligible,
          'case-inconsistency-card--judgement': card.hasJudgementAutoFixEligible
        }"
      >
        <div class="case-inconsistency-card__header">
          <div>
            <h4>
              <span>{{ card.label || '(blank label)' }}</span>
              <small>Row {{ card.rowNum }} · {{ card.entryIdShort || card.entryId || '(blank hash)' }}</small>
            </h4>
            <p>{{ card.issueCount }} issue{{ card.issueCount === 1 ? '' : 's' }} · Column Q: <strong>{{ card.continuesAsRaw || '(blank)' }}</strong></p>
          </div>
          <div class="case-inconsistency-card__header-actions">
            <span class="header-badge" :class="{ 'header-badge--active': !card.isResolved }">
              {{ card.isResolved ? 'Resolved' : 'Pending' }}
            </span>
            <button class="toolbar-btn toolbar-btn--small" type="button" @click="selectCaseInconsistencyCardRow(card)">
              Select row
            </button>
          </div>
        </div>

        <div class="case-inconsistency-card__badges">
          <span v-for="tag in card.ruleTags" :key="`${card.key}-${tag}`">{{ tag.replaceAll('_', ' ') }}</span>
          <span v-if="card.safeFixCount">{{ card.safeFixCount }} safe fix{{ card.safeFixCount === 1 ? '' : 'es' }}</span>
          <span v-if="card.judgementFixCount">{{ card.judgementFixCount }} judgement fix{{ card.judgementFixCount === 1 ? '' : 'es' }}</span>
        </div>

        <ul class="case-inconsistency-card__issues">
          <li v-for="(issue, index) in card.issues" :key="`${card.key}-issue-${index}`">{{ issue }}</li>
        </ul>

        <div class="case-inconsistency-card__details-grid">
          <article>
            <span>Attribution</span>
            <strong>{{ card.attribution || '(blank)' }}</strong>
          </article>
          <article>
            <span>Funds followed</span>
            <strong>{{ card.fundsFollowed || '(blank)' }}</strong>
          </article>
          <article>
            <span>From hash</span>
            <strong>{{ card.fromRaw || '(blank)' }}</strong>
          </article>
          <article>
            <span>Next entry</span>
            <strong>{{ card.nextEntryLabel || '(blank label)' }} {{ card.nextEntryHash ? `· ${card.nextEntryHash}` : '' }}</strong>
          </article>
        </div>

        <details class="case-inconsistency-card__notes" open>
          <summary>Notes and fix previews</summary>
          <div class="case-inconsistency-card__note-grid">
            <label>
              <span>First-row Column J</span>
              <textarea :value="card.firstRowNotes || ''" readonly rows="3"></textarea>
            </label>
            <label>
              <span>Combined entry notes</span>
              <textarea :value="card.combinedNotes || ''" readonly rows="3"></textarea>
            </label>
          </div>

          <div v-if="card.fixes.length" class="case-inconsistency-card__fix-list">
            <article v-for="fix in card.fixes" :key="`${card.key}-${fix.id}`" class="case-inconsistency-fix" :class="`case-inconsistency-fix--${fix.kind}`">
              <div class="case-inconsistency-fix__header">
                <div>
                  <strong>{{ fix.title }}</strong>
                  <small>{{ fix.kind === 'safe' ? 'Safe' : 'Judgement' }} · Row {{ fix.targetRowNum }} · {{ fix.fieldLabel }}</small>
                </div>
                <button class="toolbar-btn toolbar-btn--small" :class="{ 'toolbar-btn--primary': fix.kind === 'safe' }" type="button" :disabled="caseInconsistencyRunning" @click="applyCaseInconsistencyFix(fix, card)">
                  Apply fix
                </button>
              </div>
              <div class="case-inconsistency-fix__values">
                <label><span>Current</span><textarea :value="fix.oldValue" readonly rows="2"></textarea></label>
                <label><span>Proposed</span><textarea :value="fix.newValue" readonly rows="2"></textarea></label>
              </div>
              <p>{{ fix.reason }}</p>
            </article>
          </div>
        </details>

        <div class="case-inconsistency-card__actions">
          <button v-if="!card.isResolved" class="toolbar-btn toolbar-btn--done" type="button" @click="markCaseInconsistencyCardResolved(card, true)">
            Mark card resolved
          </button>
          <button v-else class="toolbar-btn toolbar-btn--ghost" type="button" @click="markCaseInconsistencyCardResolved(card, false)">
            Reopen card
          </button>
        </div>
      </article>
    </div>

    <div class="path-operation-modal__completion-card case-inconsistency-modal__completion-card">
      <div>
        <strong>Finished resolving inconsistency cards?</strong>
        <small>
          The workflow check passes when no cards remain or every current card is resolved. Manual workflow completion is still available outside this modal for intentional overrides.
        </small>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !caseInconsistencyCanCompleteReview" @click="runCaseModalMethod('markCaseInconsistencyReviewComplete', { title: 'Marking inconsistency review complete…', message: 'Saving review confirmation and staging audit output.' })">
        {{ caseInconsistencyCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !caseInconsistencyCanCompleteReview" @click="runCaseModalMethod('markCaseInconsistencyReviewComplete', { title: 'Marking inconsistency review complete…', message: 'Saving review confirmation and staging audit output.' })">
        {{ caseInconsistencyCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseInconsistencyRunning" @click="cancelCaseInconsistencyReviewModal">Close</button>
    </div>
  </div>
</div>


<div v-if="showDepositHotWalletModal" class="agent-modal-backdrop" @click="cancelDepositHotWalletModal">
  <div class="agent-modal network-lookup-modal deposit-hot-wallet-modal case-buddy-review-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Normalize Deposit, Hot Wallet, And Unknown Service Notes</h3>
        <p>
          Review safe Tool 73-style note fixes, unknown service endpoint cleanups, and optional Arkham confirmations. Main Data Column J should use forms like <strong>Coinbase Deposit Address</strong>, <strong>Coinbase Hot Wallet. Path ends.</strong>, or <strong>Unknown. … Path ends.</strong>
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" :disabled="depositHotWalletRunning" aria-label="Close" @click="cancelDepositHotWalletModal">
        ×
      </button>
    </div>

    <div class="network-lookup-modal__summary-grid deposit-hot-wallet-modal__summary-grid">
      <article>
        <span>Rows analyzed</span>
        <strong>{{ depositHotWalletAnalysis.rowsAnalyzed || 0 }}</strong>
        <small>Main Data only.</small>
      </article>
      <article>
        <span>Safe local fixes</span>
        <strong>{{ depositHotWalletEligibleLocalCount }}</strong>
        <small>Tool 73 / desktop-style candidates.</small>
      </article>
      <article>
        <span>Optional Arkham rows</span>
        <strong>{{ depositHotWalletVendorCandidateCount }}</strong>
        <small>{{ depositHotWalletConfirmedVendorCount }} confirmed and ready to apply.</small>
      </article>
      <article>
        <span>Review state</span>
        <strong>{{ depositHotWalletReviewIsCurrent ? 'Current' : 'Needs review' }}</strong>
        <small>Case revision {{ caseSupportState.caseDataRevision }}.</small>
      </article>
    </div>

    <div class="deposit-hot-wallet-modal__guidance">
      <strong>Formatting rule:</strong>
      <span>Use exact curator casing and avoid duplicate descriptors. Pattern hints like “Deposit Address Pattern Poloniex” are not treated as confirmation by themselves; generic 50k+TXNS markers are rewritten to an unknown service/hot-wallet path-ending note before What Went Where.</span>
    </div>

    <div class="case-inconsistency-modal__toolbar deposit-hot-wallet-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search candidates</span>
        <input v-model.trim="depositHotWalletSearch" type="search" placeholder="Search row, entity, hash, address, note…" />
      </label>
      <label class="case-details-modal__field">
        <span>Show</span>
        <select v-model="depositHotWalletViewFilter">
          <option value="eligible">Ready to apply</option>
          <option value="local">Safe local candidates</option>
          <option value="vendor">Arkham lookup candidates</option>
          <option value="confirmed_vendor">Confirmed Arkham results</option>
          <option value="normalized">Already normalized</option>
          <option value="all">All candidates</option>
        </select>
      </label>
      <div class="case-inconsistency-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" :disabled="depositHotWalletRunning" @click="refreshDepositHotWalletReview({ resetSelection: true })">
          Refresh
        </button>
        <button class="toolbar-btn" type="button" :disabled="depositHotWalletVisibleCandidates.length === 0" @click="setDepositHotWalletVisibleSelection(true)">
          Select visible
        </button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="depositHotWalletVisibleCandidates.length === 0" @click="setDepositHotWalletVisibleSelection(false)">
          Clear visible
        </button>
      </div>
    </div>

    <div class="deposit-hot-wallet-modal__action-grid">
      <section class="deposit-hot-wallet-modal__action-card">
        <h4>Safe local normalizer</h4>
        <p>Applies only obvious adjacent deposit-address → hot-wallet pairs, note cleanups that already assert deposit/hot-wallet wording, and generic 50k+TXNS unknown-service markers.</p>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!depositHotWalletCanApplySafeLocal" @click="applySelectedDepositHotWalletSafeNormalizations">
          Apply {{ depositHotWalletSelectedEligibleLocalCount }} selected safe fix{{ depositHotWalletSelectedEligibleLocalCount === 1 ? '' : 'es' }}
        </button>
      </section>

      <section class="deposit-hot-wallet-modal__action-card deposit-hot-wallet-modal__action-card--vendor">
        <h4>Optional Arkham lookup</h4>
        <p>Looks up selected receiving addresses from Column D and stages only rows where the response clearly mentions Deposit or Hot Wallet.</p>
        <div class="deposit-hot-wallet-modal__vendor-controls">
          <label><input v-model="depositHotWalletVendorLookupEnabled" type="checkbox" /> Enable vendor lookup</label>
          <label><input v-model="depositHotWalletRequireFreshCache" type="checkbox" /> Require fresh cache</label>
          <label>
            <span>TTL hours</span>
            <input v-model.number="depositHotWalletTtlHours" type="number" min="1" max="720" />
          </label>
        </div>
        <div class="deposit-hot-wallet-modal__vendor-actions">
          <button class="toolbar-btn" type="button" :disabled="!depositHotWalletCanRunVendorLookup" @click="runDepositHotWalletVendorLookup">
            Run Arkham lookup for {{ depositHotWalletSelectedVendorLookupCount }}
          </button>
          <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="depositHotWalletRunning || depositHotWalletConfirmedVendorEligibleCount === 0" @click="selectDepositHotWalletConfirmedVendorCandidates">
            Select {{ depositHotWalletConfirmedVendorEligibleCount }} confirmed
          </button>
          <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!depositHotWalletCanApplyAnyVendorConfirmed" @click="applyAllConfirmedDepositHotWalletVendorNormalizations">
            Apply all {{ depositHotWalletConfirmedVendorEligibleCount }} confirmed
          </button>
          <button class="toolbar-btn toolbar-btn--primary toolbar-btn--soft" type="button" :disabled="!depositHotWalletCanApplyVendorConfirmed" @click="applySelectedDepositHotWalletVendorNormalizations">
            Apply selected {{ depositHotWalletSelectedConfirmedVendorCount }}
          </button>
        </div>
      </section>
    </div>

    <div v-if="depositHotWalletStatus" class="network-lookup-modal__status deposit-hot-wallet-modal__status">
      {{ depositHotWalletStatus }}
    </div>

    <div v-if="depositHotWalletConfirmedVendorEligibleCount > 0" class="deposit-hot-wallet-modal__confirmed-banner">
      <div>
        <strong>{{ depositHotWalletConfirmedVendorEligibleCount }} Arkham-confirmed suggestion{{ depositHotWalletConfirmedVendorEligibleCount === 1 ? '' : 's' }} ready</strong>
        <small>These can update Main Data Column J now. Use the buttons here, or review individual cards below.</small>
      </div>
      <div>
        <button class="toolbar-btn" type="button" :disabled="depositHotWalletRunning" @click="selectDepositHotWalletConfirmedVendorCandidates">Select confirmed</button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!depositHotWalletCanApplyAnyVendorConfirmed" @click="applyAllConfirmedDepositHotWalletVendorNormalizations">Apply all confirmed</button>
      </div>
    </div>

    <div v-if="depositHotWalletVisibleCandidates.length === 0" class="network-lookup-modal__empty-state">
      No deposit/hot-wallet candidates match the current filter. Refresh the scan or choose a different filter.
    </div>

    <div v-else class="deposit-hot-wallet-modal__candidate-list">
      <article
        v-for="candidate in depositHotWalletVisibleCandidates"
        :key="candidate.key"
        class="deposit-hot-wallet-card"
        :class="{
          'deposit-hot-wallet-card--vendor': candidate.type === 'vendor',
          'deposit-hot-wallet-card--eligible': candidate.isEligible,
          'deposit-hot-wallet-card--normalized': candidate.isAlreadyNormalized
        }"
      >
        <div class="deposit-hot-wallet-card__header">
          <label class="deposit-hot-wallet-card__select">
            <input
              type="checkbox"
              :checked="isDepositHotWalletCandidateSelected(candidate.key)"
              :disabled="depositHotWalletRunning || candidate.isAlreadyNormalized"
              @change="setDepositHotWalletCandidateSelection(candidate.key, $event.target.checked)"
            />
            <span>{{ candidate.displayType }}</span>
          </label>
          <div class="deposit-hot-wallet-card__badges">
            <span>{{ candidate.statusLabel }}</span>
            <span v-if="candidate.entityName">{{ candidate.entityName }}</span>
            <span v-if="candidate.vendorClassification">{{ candidate.vendorClassification }}</span>
          </div>
        </div>

        <div class="deposit-hot-wallet-card__meta-grid">
          <article>
            <span>Row(s)</span>
            <strong v-if="candidate.type === 'pair'">{{ candidate.entry1RowNum }} → {{ candidate.entry2RowNum }}</strong>
            <strong v-else>{{ candidate.rowNum }}</strong>
          </article>
          <article>
            <span>Path label</span>
            <strong v-if="candidate.type === 'pair'">{{ candidate.entry1PathLabel || '(blank)' }} → {{ candidate.entry2PathLabel || '(blank)' }}</strong>
            <strong v-else>{{ candidate.pathLabel || '(blank)' }}</strong>
          </article>
          <article>
            <span>Hash</span>
            <strong v-if="candidate.type === 'pair'">{{ candidate.entry1Hash }} → {{ candidate.entry2Hash }}</strong>
            <strong v-else>{{ candidate.hash || '(blank)' }}</strong>
          </article>
          <article>
            <span>To Address</span>
            <strong>{{ candidate.toAddress || '(not used)' }}</strong>
          </article>
        </div>

        <div class="deposit-hot-wallet-card__note-grid" v-if="candidate.type === 'pair'">
          <label>
            <span>Entry 1 current Column J</span>
            <textarea :value="candidate.entry1CurrentNotes || '(blank)'" rows="2" readonly></textarea>
          </label>
          <label>
            <span>Entry 1 proposed Column J</span>
            <textarea :value="candidate.entry1ProposedNotes || ''" rows="2" readonly></textarea>
          </label>
          <label>
            <span>Entry 2 current Column J</span>
            <textarea :value="candidate.entry2CurrentNotes || '(blank)'" rows="2" readonly></textarea>
          </label>
          <label>
            <span>Entry 2 proposed Column J</span>
            <textarea :value="candidate.entry2ProposedNotes || ''" rows="2" readonly></textarea>
          </label>
        </div>

        <div class="deposit-hot-wallet-card__note-grid" v-else>
          <label>
            <span>Current Column J</span>
            <textarea :value="candidate.currentNotes || '(blank)'" rows="3" readonly></textarea>
          </label>
          <label>
            <span>Proposed Column J</span>
            <textarea :value="candidate.proposedNotes || '(lookup required)'" rows="3" readonly></textarea>
          </label>
        </div>

        <div class="deposit-hot-wallet-card__footer">
          <small>{{ candidate.reason }}</small>
          <div class="deposit-hot-wallet-card__footer-actions">
            <button class="toolbar-btn toolbar-btn--small" type="button" @click="selectDepositHotWalletCandidateRow(candidate)">
              Select row
            </button>
            <button
              v-if="candidate.type === 'vendor' && candidate.isEligible"
              class="toolbar-btn toolbar-btn--small toolbar-btn--primary"
              type="button"
              :disabled="depositHotWalletRunning"
              @click="applyDepositHotWalletVendorCandidate(candidate)"
            >
              Apply confirmed
            </button>
          </div>
        </div>
      </article>
    </div>

    <div class="path-operation-modal__completion-card deposit-hot-wallet-modal__completion-card">
      <div>
        <strong>Finished deposit / hot-wallet note review?</strong>
        <small>The workflow check passes when no safe local normalization candidates remain. Arkham lookup candidates are optional and can be reviewed when useful.</small>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || depositHotWalletRunning || depositHotWalletEligibleLocalCount > 0" @click="runCaseModalMethod('markDepositHotWalletReviewComplete', { title: 'Marking deposit/hot-wallet review complete…', message: 'Saving normalization review confirmation and audit output.' })">
        {{ depositHotWalletCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || depositHotWalletRunning || depositHotWalletEligibleLocalCount > 0" @click="runCaseModalMethod('markDepositHotWalletReviewComplete', { title: 'Marking deposit/hot-wallet review complete…', message: 'Saving normalization review confirmation and audit output.' })">
        {{ depositHotWalletCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="depositHotWalletRunning" @click="cancelDepositHotWalletModal">Close</button>
    </div>
  </div>
</div>


<div v-if="showDepositPatternModal" class="agent-modal-backdrop" @click="cancelDepositPatternReviewModal">
  <div class="agent-modal network-lookup-modal deposit-pattern-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Review High-Priority Deposit Pattern Candidates</h3>
        <p>
          Tool 78-style review scans Main Data for parent → child paths with close timing, matching token amounts, and deposit/hot-wallet signals. Review the cards, handle true findings with notes or prune/graft tools, then dismiss the rest.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelDepositPatternReviewModal">
        Close
      </button>
    </div>

    <div class="network-lookup-modal__summary-grid deposit-pattern-modal__summary-grid">
      <article>
        <span>Entries parsed</span>
        <strong>{{ depositPatternAnalysis.entriesParsed || 0 }}</strong>
        <small>{{ depositPatternAnalysis.edgesChecked || 0 }} path edge{{ (depositPatternAnalysis.edgesChecked || 0) === 1 ? '' : 's' }} checked.</small>
      </article>
      <article>
        <span>Candidates</span>
        <strong>{{ depositPatternAnalysis.totalCandidateCount || 0 }}</strong>
        <small>
          {{ depositPatternAnalysis.optionalCandidateCount || 0 }} optional lower-priority<span v-if="depositPatternAnalysis.skippedSwapBridgeSelfTransferCount"> · {{ depositPatternAnalysis.skippedSwapBridgeSelfTransferCount }} swap/bridge self-transfer omitted</span>.
        </small>
      </article>
      <article>
        <span>Required pending</span>
        <strong>{{ depositPatternPendingRequiredCount }}</strong>
        <small>{{ depositPatternRequiredReviewedCount }}/{{ depositPatternRequiredCount }} HIGH/VERY_HIGH reviewed.</small>
      </article>
      <article>
        <span>Status</span>
        <strong>{{ depositPatternReviewIsComplete ? 'Ready' : 'Needs review' }}</strong>
        <small>{{ depositPatternAnalysis.summary }}</small>
      </article>
    </div>

    <div class="deposit-pattern-modal__guidance">
      <strong>Workflow tip:</strong>
      <span>If a card is a real deposit or early path-ending issue, use Main Data, the notes normalizer, or Prune / Graft / Bridge to fix the flow. When the remaining cards are not issues, dismiss them and mark the review complete.</span>
    </div>

    <div class="case-inconsistency-modal__toolbar deposit-pattern-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search cards</span>
        <input v-model.trim="depositPatternSearch" type="search" placeholder="Search row, grade, hash, attribution, notes, signal…" />
      </label>
      <label class="case-details-modal__field">
        <span>Show</span>
        <select v-model="depositPatternViewFilter">
          <option value="pending_required">Pending required</option>
          <option value="pending_all">Pending all</option>
          <option value="required_all">All required</option>
          <option value="optional">Optional lower priority</option>
          <option value="resolved">Resolved/dismissed</option>
          <option value="all">All candidates</option>
        </select>
      </label>
      <label class="case-details-modal__field">
        <span>Minimum grade</span>
        <select v-model="depositPatternMinGrade">
          <option value="VERY_HIGH">VERY_HIGH</option>
          <option value="HIGH">HIGH+</option>
          <option value="MEDIUM">MEDIUM+</option>
          <option value="LOW">LOW+</option>
          <option value="VERY_LOW">All grades</option>
        </select>
      </label>
      <label class="case-details-modal__field">
        <span>Sort</span>
        <select v-model="depositPatternSortMode">
          <option value="priority_then_time">Priority → shortest time</option>
          <option value="time_then_priority">Shortest time → priority</option>
          <option value="similarity_then_priority">Best amount match</option>
          <option value="row_asc">Sheet order</option>
        </select>
      </label>
      <div class="case-inconsistency-modal__toolbar-actions deposit-pattern-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" @click="refreshDepositPatternReview">
          Refresh cards
        </button>
        <button class="toolbar-btn" type="button" :disabled="depositPatternVisibleCandidates.length === 0" @click="markVisibleDepositPatternCandidatesReviewed">
          Mark visible reviewed
        </button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="depositPatternVisibleCandidates.length === 0" @click="dismissVisibleDepositPatternCandidates">
          Dismiss visible
        </button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="caseModalOperation.active || depositPatternPendingCandidateCount === 0" @click="runCaseModalMethod('dismissRemainingDepositPatternCandidatesAndComplete', { title: 'Dismissing remaining deposit-pattern candidates…', message: 'Saving dismiss decisions and completing the review.' })">
          Dismiss remaining & complete
        </button>
      </div>
    </div>

    <div v-if="depositPatternStatus" class="network-lookup-modal__status deposit-pattern-modal__status">
      {{ depositPatternStatus }}
    </div>

    <div v-if="depositPatternVisibleCandidates.length === 0" class="network-lookup-modal__empty-state">
      No deposit-pattern candidates match the current filter. Refresh cards or choose a broader filter.
    </div>

    <div v-else class="deposit-pattern-modal__card-list">
      <article
        v-for="candidate in depositPatternVisibleCandidates"
        :key="candidate.reviewKey"
        class="deposit-pattern-card"
        :class="[
          `deposit-pattern-card--${candidate.grade.toLowerCase().replace('_', '-')}`,
          candidate.hasCurrentDecision ? 'deposit-pattern-card--resolved' : 'deposit-pattern-card--pending',
          candidate.isRequired ? 'deposit-pattern-card--required' : 'deposit-pattern-card--optional'
        ]"
      >
        <div class="deposit-pattern-card__header">
          <div>
            <div class="deposit-pattern-card__title-row">
              <span class="deposit-pattern-card__grade">{{ candidate.grade.replace('_', ' ') }}</span>
              <h4>{{ candidate.parentPathLabel || 'Parent' }} → {{ candidate.childPathLabel || 'Child' }}</h4>
            </div>
            <p>
              Row {{ candidate.parentRowNumber }} → row {{ candidate.childOutputRowNumber || candidate.childRowNumber }} ·
              {{ candidate.parentShortHash }} → {{ candidate.childShortHash }}
            </p>
          </div>
          <div class="deposit-pattern-card__status-stack">
            <span class="header-badge" :class="{ 'header-badge--active': !candidate.hasCurrentDecision }">
              {{ candidate.hasCurrentDecision ? candidate.currentReviewStatusLabel : 'Pending' }}
            </span>
            <span class="deposit-pattern-card__required-badge">{{ candidate.isRequired ? 'Required' : 'Optional' }}</span>
          </div>
        </div>

        <div class="deposit-pattern-card__metric-grid">
          <article>
            <span>Time delta</span>
            <strong>{{ candidate.deltaMinutesFormatted }} min</strong>
            <small>{{ candidate.deltaHoursFormatted }} hr</small>
          </article>
          <article>
            <span>Token</span>
            <strong>{{ candidate.token || '(blank)' }}</strong>
            <small>{{ candidate.amountSimilarityPercent }} similarity</small>
          </article>
          <article>
            <span>Matched amounts</span>
            <strong>{{ candidate.amountAFormatted }} → {{ candidate.amountBFormatted }}</strong>
            <small>{{ candidate.upgradedToVeryHigh ? 'Upgraded by signal' : 'Base grade' }}</small>
          </article>
          <article>
            <span>Child path state</span>
            <strong>{{ candidate.childQ || '(blank Column Q)' }}</strong>
            <small>From hash: {{ candidate.childFromHash || '(blank)' }}</small>
          </article>
        </div>

        <div class="deposit-pattern-card__signals">
          <span v-for="signal in candidate.veryHighSignals" :key="`${candidate.reviewKey}-signal-${signal}`">{{ signal.replaceAll('_', ' ') }}</span>
          <span v-if="candidate.veryHighSignals.length === 0">No extra VERY_HIGH signals</span>
          <span v-if="candidate.childPathOperationEligible">Prune helper available</span>
        </div>

        <div class="deposit-pattern-card__note-grid">
          <label>
            <span>Parent Column J notes</span>
            <textarea :value="candidate.parentNotes || '(blank)'" rows="3" readonly></textarea>
          </label>
          <label>
            <span>Child Column J notes</span>
            <textarea :value="candidate.childNotes || '(blank)'" rows="3" readonly></textarea>
          </label>
          <label>
            <span>Child attribution / analyst notes</span>
            <textarea :value="candidate.childAttributionAndAnalystNotes || '(blank)'" rows="3" readonly></textarea>
          </label>
          <label>
            <span>Optional decision note</span>
            <textarea v-model="depositPatternDecisionDrafts[candidate.reviewKey]" rows="3" placeholder="Optional note for this review decision"></textarea>
          </label>
        </div>

        <div class="deposit-pattern-card__actions">
          <button class="toolbar-btn toolbar-btn--small" type="button" @click="selectDepositPatternCandidateRow(candidate)">
            Select child row
          </button>
          <button class="toolbar-btn toolbar-btn--small" type="button" @click="copyDepositPatternCandidateSearch(candidate)">
            Copy search
          </button>
          <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="!candidate.childPathOperationEligible" @click="openDepositPatternCandidatePathOperation(candidate, 'prune')">
            Open prune helper
          </button>
          <button v-if="candidate.hasCurrentDecision" class="toolbar-btn toolbar-btn--ghost toolbar-btn--small" type="button" @click="reopenDepositPatternCandidate(candidate)">
            Reopen
          </button>
          <template v-else>
            <button class="toolbar-btn toolbar-btn--small" type="button" @click="markDepositPatternCandidateReviewed(candidate)">
              Mark reviewed
            </button>
            <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--small" type="button" @click="dismissDepositPatternCandidate(candidate)">
              Dismiss
            </button>
          </template>
        </div>
      </article>
    </div>

    <div class="path-operation-modal__completion-card deposit-pattern-modal__completion-card">
      <div>
        <strong>Finished deposit-pattern review?</strong>
        <small>Use “Dismiss remaining & complete” when the visible/pending cards have been assessed and no further prune/note work is needed. The workflow check fails while HIGH/VERY_HIGH cards remain pending.</small>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || depositPatternPendingRequiredCount > 0" @click="runCaseModalMethod('markDepositPatternReviewComplete', { title: 'Marking deposit-pattern review complete…', message: 'Saving candidate decisions and staging audit output.' })">
        {{ depositPatternCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn" type="button" @click="openPathOperationModal">Open Prune / Graft / Bridge</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || depositPatternPendingRequiredCount > 0" @click="runCaseModalMethod('markDepositPatternReviewComplete', { title: 'Marking deposit-pattern review complete…', message: 'Saving candidate decisions and staging audit output.' })">
        {{ depositPatternCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelDepositPatternReviewModal">Close</button>
    </div>
  </div>
</div>

<div v-if="showKnownEntityCasingModal" class="agent-modal-backdrop" @click="cancelKnownEntityCasingReviewModal">
  <div class="agent-modal deposit-pattern-modal known-entity-casing-modal case-buddy-review-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Review Known-Entity Note Casing</h3>
        <p>
          Review Column J notes where a known entity appears with different casing or spelling from the effective curator. Apply only the safe fixes and dismiss false positives.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close known entity casing review" :disabled="knownEntityCasingRunning" @click="cancelKnownEntityCasingReviewModal">
        ×
      </button>
    </div>

    <div class="deposit-pattern-modal__summary-grid known-entity-casing-modal__summary-grid">
      <article>
        <span>Curator terms</span>
        <strong>{{ knownEntityCasingAnalysis.patternsLoaded || 0 }}</strong>
        <small>{{ entityCuratorRemote.status === 'success' ? 'Server + local overlay loaded' : entityCuratorRemote.status }}</small>
      </article>
      <article>
        <span>Candidates</span>
        <strong>{{ knownEntityCasingAnalysis.totalCandidateCount || 0 }}</strong>
        <small>{{ knownEntityCasingAnalysis.rowsWithNotes || 0 }} Column J note row{{ (knownEntityCasingAnalysis.rowsWithNotes || 0) === 1 ? '' : 's' }} checked.</small>
      </article>
      <article>
        <span>Pending</span>
        <strong>{{ knownEntityCasingPendingCount }}</strong>
        <small>{{ knownEntityCasingReviewedCount }}/{{ knownEntityCasingAnalysis.totalCandidateCount || 0 }} reviewed, applied, or dismissed.</small>
      </article>
      <article>
        <span>Status</span>
        <strong>{{ knownEntityCasingReviewIsComplete ? 'Ready' : 'Needs review' }}</strong>
        <small>{{ knownEntityCasingAnalysis.summary }}</small>
      </article>
    </div>

    <div class="deposit-pattern-modal__guidance known-entity-casing-modal__guidance">
      <span aria-hidden="true">Aa</span>
      <div>
        <strong>Curator matching is case-sensitive and spelling-specific.</strong>
        <p>
          This review uses whole-token sequence matching, so an entity term buried inside a continuous hash-like token is ignored. For example, <code>0x123456okx914141</code> is not treated as an OKX note mention.
        </p>
      </div>
    </div>

    <div class="deposit-pattern-modal__toolbar known-entity-casing-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search</span>
        <input v-model.trim="knownEntityCasingSearch" type="search" placeholder="Search row, entity, matched form, hash, notes…" />
      </label>
      <label class="case-details-modal__field">
        <span>View</span>
        <select v-model="knownEntityCasingViewFilter">
          <option value="pending">Pending</option>
          <option value="selected">Selected</option>
          <option value="reviewed">Reviewed</option>
          <option value="applied">Applied</option>
          <option value="dismissed">Dismissed</option>
          <option value="all">All</option>
        </select>
      </label>
      <label class="case-details-modal__field">
        <span>Kind</span>
        <select v-model="knownEntityCasingKindFilter">
          <option value="all">All</option>
          <option value="exchange">Exchange/service</option>
          <option value="non_exchange">Non-exchange</option>
        </select>
      </label>
      <div class="deposit-pattern-modal__toolbar-actions known-entity-casing-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" :disabled="knownEntityCasingRunning" @click="refreshKnownEntityCasingReview({ forceRemote: true })">
          {{ entityCuratorLoading ? 'Refreshing…' : 'Refresh Curator' }}
        </button>
        <button class="toolbar-btn" type="button" :disabled="knownEntityCasingVisibleCandidates.length === 0" @click="selectVisibleKnownEntityCasingCandidates">
          Select Visible
        </button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="knownEntityCasingSelectedCount === 0" @click="clearKnownEntityCasingSelections">
          Clear Selected
        </button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="knownEntityCasingRunning || knownEntityCasingSelectedCount === 0" @click="applySelectedKnownEntityCasingFixes">
          Apply {{ knownEntityCasingSelectedCount }} Fix{{ knownEntityCasingSelectedCount === 1 ? '' : 'es' }}
        </button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="knownEntityCasingRunning || knownEntityCasingSelectedCount === 0" @click="dismissSelectedKnownEntityCasingCandidates">
          Dismiss Selected
        </button>
        <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || knownEntityCasingRunning || knownEntityCasingPendingCount === 0" @click="runCaseModalMethod('dismissRemainingKnownEntityCasingCandidatesAndComplete', { title: 'Dismissing remaining casing candidates…', message: 'Saving dismiss decisions and completing the review.' })">
          Dismiss Remaining And Complete
        </button>
      </div>
    </div>

    <div class="case-modal-sticky-action-strip known-entity-casing-modal__quick-actions">
      <span>{{ knownEntityCasingVisibleCandidates.length }} visible · {{ knownEntityCasingPendingCount }} pending · {{ knownEntityCasingSelectedCount }} selected</span>
      <div>
        <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="knownEntityCasingVisibleCandidates.length === 0" @click="selectVisibleKnownEntityCasingCandidates">Select Visible</button>
        <button class="toolbar-btn toolbar-btn--small toolbar-btn--primary" type="button" :disabled="knownEntityCasingRunning || knownEntityCasingSelectedCount === 0" @click="applySelectedKnownEntityCasingFixes">Apply Selected</button>
        <button class="toolbar-btn toolbar-btn--small toolbar-btn--ghost" type="button" :disabled="knownEntityCasingRunning || knownEntityCasingSelectedCount === 0" @click="dismissSelectedKnownEntityCasingCandidates">Dismiss Selected</button>
        <button class="toolbar-btn toolbar-btn--small toolbar-btn--done" type="button" :disabled="caseModalOperation.active || knownEntityCasingRunning || knownEntityCasingPendingCount === 0" @click="runCaseModalMethod('dismissRemainingKnownEntityCasingCandidatesAndComplete', { title: 'Dismissing remaining casing candidates…', message: 'Saving dismiss decisions and completing the review.' })">Dismiss Remaining & Complete</button>
      </div>
    </div>

    <div v-if="knownEntityCasingStatus" class="network-lookup-modal__status known-entity-casing-modal__status">
      {{ knownEntityCasingStatus }}
    </div>

    <div v-if="knownEntityCasingVisibleCandidates.length === 0" class="network-lookup-modal__empty-state">
      No known-entity casing cards match the current filter. Refresh the scan, show all cards, or run checks to confirm the current state.
    </div>

    <div v-else class="known-entity-casing-modal__card-list">
      <article
        v-for="candidate in knownEntityCasingVisibleCandidates"
        :key="candidate.reviewKey"
        class="known-entity-casing-card"
        :class="{
          'known-entity-casing-card--resolved': candidate.hasCurrentDecision,
          'known-entity-casing-card--selected': !!knownEntityCasingSelectedByKey[candidate.reviewKey]
        }"
      >
        <div class="known-entity-casing-card__header">
          <label class="known-entity-casing-card__select">
            <input
              v-model="knownEntityCasingSelectedByKey[candidate.reviewKey]"
              type="checkbox"
              :disabled="knownEntityCasingRunning || candidate.hasCurrentDecision"
            />
            <span>{{ candidate.hasCurrentDecision ? candidate.currentReviewStatusLabel : 'Select fix' }}</span>
          </label>
          <div class="known-entity-casing-card__title">
            <h4>Row {{ candidate.rowNumber }} · {{ candidate.presentedName }}</h4>
            <p>
              {{ candidate.entityKindLabel }} · Path {{ candidate.pathLabel || '—' }} · {{ candidate.shortHash || candidate.transactionHash || 'No hash' }}
            </p>
          </div>
          <div class="known-entity-casing-card__badges">
            <span>{{ candidate.matchCount }} match{{ candidate.matchCount === 1 ? '' : 'es' }}</span>
            <span>{{ candidate.hasCurrentDecision ? candidate.currentReviewStatusLabel : 'Pending' }}</span>
          </div>
        </div>

        <div class="known-entity-casing-card__metric-grid">
          <article>
            <span>Expected curator text</span>
            <strong>{{ candidate.presentedName }}</strong>
            <small>{{ candidate.entityKindLabel }}</small>
          </article>
          <article>
            <span>Matched form{{ candidate.matchedForms.length === 1 ? '' : 's' }}</span>
            <strong>{{ candidate.matchedForms.join(', ') }}</strong>
            <small>Only whole token sequences are matched.</small>
          </article>
          <article>
            <span>Transaction hash</span>
            <strong>{{ candidate.transactionHash || '—' }}</strong>
          </article>
          <article>
            <span>Review state</span>
            <strong>{{ candidate.hasCurrentDecision ? candidate.currentReviewStatusLabel : 'Pending' }}</strong>
            <small>{{ candidate.existingDecision?.comment || candidate.reviewBasisHash }}</small>
          </article>
        </div>

        <div class="known-entity-casing-card__note-grid">
          <label>
            <span>Current Column J</span>
            <textarea :value="candidate.currentNotes" rows="4" readonly></textarea>
          </label>
          <label>
            <span>Proposed Column J</span>
            <textarea :value="candidate.proposedNotes" rows="4" readonly></textarea>
          </label>
          <label>
            <span>Match excerpts</span>
            <textarea :value="candidate.excerpts.join('\n')" rows="4" readonly></textarea>
          </label>
        </div>

        <div class="known-entity-casing-card__actions">
          <button class="toolbar-btn" type="button" @click="selectKnownEntityCasingCandidateRow(candidate)">Select Row</button>
          <button class="toolbar-btn" type="button" @click="copyKnownEntityCasingCandidateSearch(candidate)">Copy Search</button>
          <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="knownEntityCasingRunning || candidate.hasCurrentDecision" @click="applyKnownEntityCasingCandidate(candidate)">Apply Fix</button>
          <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="knownEntityCasingRunning || candidate.hasCurrentDecision" @click="dismissKnownEntityCasingCandidate(candidate)">Dismiss</button>
          <button v-if="candidate.hasCurrentDecision" class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="knownEntityCasingRunning" @click="reopenKnownEntityCasingCandidate(candidate)">Reopen</button>
        </div>
      </article>
    </div>

    <div class="path-operation-modal__completion-card known-entity-casing-modal__completion-card">
      <div>
        <strong>Finished casing review?</strong>
        <p>
          Apply true positives, dismiss false positives, then mark the review complete. If you intentionally need to move on despite pending cards, use manual completion in the workflow step.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || knownEntityCasingPendingCount > 0" @click="runCaseModalMethod('markKnownEntityCasingReviewComplete', { title: 'Marking casing review complete…', message: 'Saving known-entity casing review confirmation.' })">
        {{ knownEntityCasingCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || knownEntityCasingPendingCount > 0" @click="runCaseModalMethod('markKnownEntityCasingReviewComplete', { title: 'Marking casing review complete…', message: 'Saving known-entity casing review confirmation.' })">
        {{ knownEntityCasingCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelKnownEntityCasingReviewModal">Close</button>
    </div>
  </div>
</div>

<div v-if="showDuplicateHashLabelerModal" class="agent-modal-backdrop" @click="cancelDuplicateHashLabelerModal">
  <div class="agent-modal deposit-pattern-modal duplicate-hash-labeler-modal case-buddy-review-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Label Duplicate Transaction Hashes</h3>
        <p>
          Refresh Column J notes for transaction hashes that appear under multiple path labels, and keep the Duplicate Hash Table deliverable aligned with current Main Data.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close duplicate hash labeler" :disabled="duplicateHashLabelerRunning" @click="cancelDuplicateHashLabelerModal">
        ×
      </button>
    </div>

    <div class="deposit-pattern-modal__summary-grid duplicate-hash-labeler-modal__summary-grid">
      <article>
        <span>Duplicate groups</span>
        <strong>{{ duplicateHashLabelerAnalysis.duplicateHashGroupCount || 0 }}</strong>
        <small>Different path labels for the same hash.</small>
      </article>
      <article>
        <span>Duplicate entries</span>
        <strong>{{ duplicateHashLabelerAnalysis.duplicateEntryCount || 0 }}</strong>
        <small>{{ duplicateHashLabelerAnalysis.entriesAnalyzed || 0 }} transaction entries scanned.</small>
      </article>
      <article>
        <span>Updates needed</span>
        <strong>{{ duplicateHashLabelerEligibleCount }}</strong>
        <small>{{ duplicateHashLabelerAnalysis.staleNoteCount || 0 }} stale note cleanup{{ (duplicateHashLabelerAnalysis.staleNoteCount || 0) === 1 ? '' : 's' }} · {{ duplicateHashLabelerBlockedCount }} safety review{{ duplicateHashLabelerBlockedCount === 1 ? '' : 's' }}.</small>
      </article>
      <article>
        <span>Status</span>
        <strong>{{ duplicateHashLabelerBlockedCount > 0 ? 'Needs review' : (duplicateHashLabelerReviewIsComplete ? 'Ready' : 'Needs refresh') }}</strong>
        <small>{{ duplicateHashLabelerAnalysis.summary }}</small>
      </article>
    </div>

    <div class="deposit-pattern-modal__guidance duplicate-hash-labeler-modal__guidance">
      <span aria-hidden="true">#</span>
      <div>
        <strong>This step is intentionally refreshable.</strong>
        <p>
          Applying updates now preserves existing Column J notes and only refreshes the duplicate-hash annotation sentence. If a proposal would change unrelated notes, it is blocked for manual review instead of being applied automatically.
        </p>
      </div>
    </div>

    <div class="duplicate-hash-labeler-modal__table-card">
      <div class="duplicate-hash-labeler-modal__table-header">
        <div>
          <strong>Live Duplicate Hash Table</strong>
          <small>This is generated from the current Main Data and can be staged as the deliverable preview.</small>
        </div>
        <button class="toolbar-btn toolbar-btn--small" type="button" @click="buildDuplicateHashTablePreview">
          Stage Table In Output
        </button>
      </div>
      <div class="duplicate-hash-labeler-modal__table-wrap">
        <table>
          <thead>
            <tr>
              <th>Hash</th>
              <th>Path labels</th>
              <th>Rows</th>
              <th>Entries</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="(duplicateHashLabelerAnalysis.summaryRows || []).length === 0">
              <td colspan="4">No duplicate transaction hashes with differing path labels are currently detected.</td>
            </tr>
            <tr v-for="row in duplicateHashLabelerAnalysis.summaryRows" :key="row.hashKey">
              <td>{{ row.transactionHash }}</td>
              <td>{{ row.pathLabels.join(', ') }}</td>
              <td>{{ row.rowNumbers.join(', ') }}</td>
              <td>{{ row.entryCount }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="deposit-pattern-modal__toolbar duplicate-hash-labeler-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search</span>
        <input v-model.trim="duplicateHashLabelerSearch" type="search" placeholder="Search row, path label, hash, notes…" />
      </label>
      <label class="case-details-modal__field">
        <span>View</span>
        <select v-model="duplicateHashLabelerViewFilter">
          <option value="eligible">Needs update</option>
          <option value="duplicates">Duplicate rows</option>
          <option value="stale">Stale note cleanup</option>
          <option value="current">Already current</option>
          <option value="blocked">Safety review</option>
          <option value="selected">Selected</option>
          <option value="all">All</option>
        </select>
      </label>
      <div class="deposit-pattern-modal__toolbar-actions duplicate-hash-labeler-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" :disabled="duplicateHashLabelerRunning" @click="refreshDuplicateHashLabeler()">
          Refresh Scan
        </button>
        <button class="toolbar-btn" type="button" :disabled="duplicateHashLabelerVisibleItems.length === 0" @click="selectVisibleDuplicateHashLabelerItems">
          Select Visible
        </button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="duplicateHashLabelerSelectedCount === 0" @click="clearDuplicateHashLabelerSelections">
          Clear Selected
        </button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="duplicateHashLabelerRunning || duplicateHashLabelerSelectedCount === 0" @click="applySelectedDuplicateHashLabelerUpdates">
          Apply {{ duplicateHashLabelerSelectedCount }} Selected
        </button>
        <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="duplicateHashLabelerRunning || duplicateHashLabelerEligibleCount === 0" @click="applyAllEligibleDuplicateHashUpdates">
          Apply All Eligible
        </button>
      </div>
    </div>

    <div class="case-modal-sticky-action-strip duplicate-hash-labeler-modal__quick-actions">
      <span>{{ duplicateHashLabelerVisibleItems.length }} visible · {{ duplicateHashLabelerEligibleCount }} updates ready · {{ duplicateHashLabelerBlockedCount }} safety review · {{ duplicateHashLabelerSelectedCount }} selected</span>
      <div>
        <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="duplicateHashLabelerVisibleItems.length === 0" @click="selectVisibleDuplicateHashLabelerItems">Select Visible</button>
        <button class="toolbar-btn toolbar-btn--small toolbar-btn--primary" type="button" :disabled="duplicateHashLabelerRunning || duplicateHashLabelerSelectedCount === 0" @click="applySelectedDuplicateHashLabelerUpdates">Apply Selected</button>
        <button class="toolbar-btn toolbar-btn--small toolbar-btn--done" type="button" :disabled="duplicateHashLabelerRunning || duplicateHashLabelerEligibleCount === 0" @click="applyAllEligibleDuplicateHashUpdates">Apply All Eligible</button>
        <button class="toolbar-btn toolbar-btn--small" type="button" @click="buildDuplicateHashTablePreview">Stage Table</button>
      </div>
    </div>

    <div v-if="duplicateHashLabelerStatus" class="network-lookup-modal__status duplicate-hash-labeler-modal__status">
      {{ duplicateHashLabelerStatus }}
    </div>

    <div v-if="duplicateHashLabelerVisibleItems.length === 0" class="network-lookup-modal__empty-state">
      No duplicate-hash note cards match the current filter. Refresh the scan or show all cards to inspect current duplicate documentation.
    </div>

    <div v-else class="duplicate-hash-labeler-modal__card-list">
      <article
        v-for="item in duplicateHashLabelerVisibleItems"
        :key="item.reviewKey"
        class="known-entity-casing-card duplicate-hash-labeler-card"
        :class="{
          'known-entity-casing-card--resolved': !item.isEligible,
          'known-entity-casing-card--selected': !!duplicateHashLabelerSelectedByKey[item.reviewKey]
        }"
      >
        <div class="known-entity-casing-card__header">
          <label class="known-entity-casing-card__select">
            <input
              v-model="duplicateHashLabelerSelectedByKey[item.reviewKey]"
              type="checkbox"
              :disabled="duplicateHashLabelerRunning || !item.isEligible"
            />
            <span>{{ item.proposalPreservesNotes === false ? 'Review only' : (item.isEligible ? 'Select update' : 'Current') }}</span>
          </label>
          <div class="known-entity-casing-card__title">
            <h4>Row {{ item.rowNumber }} · Path {{ item.pathLabel }}</h4>
            <p>{{ item.itemKindLabel }} · {{ item.shortHash || item.transactionHash }}</p>
          </div>
          <div class="known-entity-casing-card__badges">
            <span>{{ item.statusLabel }}</span>
            <span v-if="item.itemKind === 'duplicate'">Also {{ item.otherPathLabels.join(', ') }}</span>
          </div>
        </div>

        <div class="known-entity-casing-card__metric-grid duplicate-hash-labeler-card__metric-grid">
          <article>
            <span>Transaction hash</span>
            <strong>{{ item.transactionHash }}</strong>
          </article>
          <article>
            <span>Path labels in duplicate group</span>
            <strong>{{ (item.duplicatePathLabels || []).join(', ') || '—' }}</strong>
            <small>Rows {{ (item.duplicateRowNumbers || []).join(', ') || '—' }}</small>
          </article>
          <article>
            <span>Attribution</span>
            <strong>{{ item.attribution || '—' }}</strong>
            <small>{{ item.analystNotes || 'No analyst notes' }}</small>
          </article>
          <article>
            <span>Reason</span>
            <strong>{{ item.itemKind === 'stale_note' ? 'Clear stale note' : 'Document duplicate' }}</strong>
            <small>{{ item.reason }}</small>
          </article>
        </div>

        <div class="known-entity-casing-card__note-grid duplicate-hash-labeler-card__note-grid">
          <label>
            <span>Current Column J</span>
            <textarea :value="item.currentNotes || '(blank)'" rows="4" readonly></textarea>
          </label>
          <label>
            <span>Proposed Column J</span>
            <textarea :value="item.proposedNotes || '(blank)'" rows="4" readonly></textarea>
          </label>
          <label>
            <span>Column Q / From hash context</span>
            <textarea :value="['Column Q: ' + (item.continuesAs || '(blank)'), 'Column O: ' + (item.fromHash || '(blank)')].join('\n')" rows="4" readonly></textarea>
          </label>
        </div>

        <div class="known-entity-casing-card__actions">
          <button class="toolbar-btn" type="button" @click="selectDuplicateHashLabelerRow(item)">Select Row</button>
          <button class="toolbar-btn" type="button" @click="copyDuplicateHashLabelerSearch(item)">Copy Details</button>
          <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="duplicateHashLabelerRunning || !item.isEligible" @click="applyDuplicateHashLabelerItem(item)">Apply Update</button>
        </div>
      </article>
    </div>

    <div class="path-operation-modal__completion-card duplicate-hash-labeler-modal__completion-card">
      <div>
        <strong>Finished duplicate-hash labeling?</strong>
        <p>
          Apply all current updates first. The check passes when every duplicate-hash note is current and stale duplicate annotations have been cleared.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || duplicateHashLabelerEligibleCount > 0 || duplicateHashLabelerBlockedCount > 0" @click="runCaseModalMethod('markDuplicateHashLabelerReviewComplete', { title: 'Marking duplicate-hash review complete…', message: 'Saving duplicate-hash labeling review confirmation.' })">
        {{ duplicateHashLabelerCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn" type="button" @click="buildDuplicateHashTablePreview">Stage Duplicate Hash Table</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || duplicateHashLabelerEligibleCount > 0 || duplicateHashLabelerBlockedCount > 0" @click="runCaseModalMethod('markDuplicateHashLabelerReviewComplete', { title: 'Marking duplicate-hash review complete…', message: 'Saving duplicate-hash labeling review confirmation.' })">
        {{ duplicateHashLabelerCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelDuplicateHashLabelerModal">Close</button>
    </div>
  </div>
</div>


<div v-if="showNoteHashRewriteModal" class="agent-modal-backdrop" @click="cancelNoteHashRewriteModal">
  <div class="agent-modal deposit-pattern-modal note-hash-rewrite-modal case-buddy-review-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Back Up Notes And Replace Hashes With Path Labels</h3>
        <p>
          Create/merge a local Original Notes Backup, then refresh <strong>Continues as</strong> and <strong>Branches as</strong> sections in Column J so they use current path labels instead of transaction hashes.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close note hash rewrite" :disabled="noteHashRewriteRunning" @click="cancelNoteHashRewriteModal">
        ×
      </button>
    </div>

    <div class="deposit-pattern-modal__summary-grid note-hash-rewrite-modal__summary-grid">
      <article>
        <span>Original backup</span>
        <strong>{{ noteHashRewriteBackupExists ? 'Available' : 'Missing' }}</strong>
        <small>{{ noteHashRewriteAnalysis.backupRowCount || 0 }} backed-up row{{ (noteHashRewriteAnalysis.backupRowCount || 0) === 1 ? '' : 's' }} · revision {{ noteHashRewriteAnalysis.backupRevision ?? '—' }}</small>
      </article>
      <article>
        <span>Rewrites ready</span>
        <strong>{{ noteHashRewriteEligibleCount }}</strong>
        <small>{{ noteHashRewriteAnalysis.replacementCount || 0 }} replacement{{ (noteHashRewriteAnalysis.replacementCount || 0) === 1 ? '' : 's' }} proposed.</small>
      </article>
      <article>
        <span>Unresolved</span>
        <strong>{{ noteHashRewriteAnalysis.unresolvedCount || 0 }}</strong>
        <small>Rows whose referenced hashes do not map to current labels.</small>
      </article>
      <article>
        <span>Status</span>
        <strong>{{ noteHashRewriteReviewIsComplete ? 'Ready' : 'Needs review' }}</strong>
        <small>{{ noteHashRewriteAnalysis.summary }}</small>
      </article>
    </div>

    <div class="deposit-pattern-modal__guidance note-hash-rewrite-modal__guidance">
      <span aria-hidden="true">↔</span>
      <div>
        <strong>This is designed to be less brittle than a one-way text replacement.</strong>
        <p>
          The tool rewrites recognizable Continues/Branches note sections from current Column Q hash references and current Column A labels. If path labels change later, re-run this modal to refresh those sections again. Column Q remains hash-based.
        </p>
      </div>
    </div>

    <div class="duplicate-hash-labeler-modal__table-card note-hash-rewrite-modal__backup-card">
      <div class="duplicate-hash-labeler-modal__table-header">
        <div>
          <strong>Original Notes Backup</strong>
          <small>Stored in per-case support state and available as a deliverable preview/download.</small>
        </div>
        <div class="note-hash-rewrite-modal__backup-actions">
          <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="noteHashRewriteRunning" @click="createOrMergeOriginalNotesBackup()">Create / Merge Backup</button>
          <button class="toolbar-btn toolbar-btn--small toolbar-btn--ghost" type="button" :disabled="noteHashRewriteRunning" @click="createOrMergeOriginalNotesBackup({ replace: true })">Replace Backup</button>
          <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="noteHashRewriteRunning" @click="stageOriginalNotesBackupPreview()">Stage Backup</button>
          <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="noteHashRewriteRunning" @click="downloadOriginalNotesBackup()">Download Backup</button>
          <button class="toolbar-btn toolbar-btn--small toolbar-btn--danger" type="button" :disabled="noteHashRewriteRunning || !noteHashRewriteBackupExists" @click="restoreOriginalNotesBackupToMain()">Restore Backup</button>
        </div>
      </div>
    </div>

    <div class="deposit-pattern-modal__toolbar note-hash-rewrite-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search</span>
        <input v-model.trim="noteHashRewriteSearch" type="search" placeholder="Search row, path label, hash, notes…" />
      </label>
      <label class="case-details-modal__field">
        <span>View</span>
        <select v-model="noteHashRewriteViewFilter">
          <option value="eligible">Needs rewrite</option>
          <option value="unresolved">Unresolved</option>
          <option value="current">Current / informational</option>
          <option value="selected">Selected</option>
          <option value="all">All</option>
        </select>
      </label>
      <label class="network-lookup-modal__checkbox note-hash-rewrite-modal__checkbox">
        <input v-model="noteHashRewriteIncludeUnstructured" type="checkbox" :disabled="noteHashRewriteRunning" @change="refreshNoteHashRewrite({ silent: true })" />
        <span>Advanced: include exact hash tokens outside Continues/Branches sections</span>
      </label>
      <div class="deposit-pattern-modal__toolbar-actions note-hash-rewrite-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" :disabled="noteHashRewriteRunning" @click="refreshNoteHashRewrite()">Refresh Scan</button>
        <button class="toolbar-btn" type="button" :disabled="noteHashRewriteVisibleItems.length === 0" @click="selectVisibleNoteHashRewriteItems">Select Visible</button>
        <button class="toolbar-btn" type="button" :disabled="noteHashRewriteEligibleCount === 0" @click="selectAllEligibleNoteHashRewriteItems">Select All Eligible</button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="noteHashRewriteSelectedCount === 0" @click="clearNoteHashRewriteSelections">Clear Selected</button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="noteHashRewriteRunning || noteHashRewriteSelectedCount === 0" @click="applySelectedNoteHashRewrites">Apply {{ noteHashRewriteSelectedCount }} Selected</button>
        <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="noteHashRewriteRunning || noteHashRewriteEligibleCount === 0" @click="applyAllEligibleNoteHashRewrites">Apply All Eligible</button>
      </div>
    </div>

    <div class="case-modal-sticky-action-strip note-hash-rewrite-modal__quick-actions">
      <span>{{ noteHashRewriteVisibleItems.length }} visible · {{ noteHashRewriteEligibleCount }} rewrites ready · {{ noteHashRewriteSelectedCount }} selected</span>
      <div>
        <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="noteHashRewriteRunning" @click="createOrMergeOriginalNotesBackup()">Create / Merge Backup</button>
        <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="noteHashRewriteVisibleItems.length === 0" @click="selectVisibleNoteHashRewriteItems">Select Visible</button>
        <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="noteHashRewriteEligibleCount === 0" @click="selectAllEligibleNoteHashRewriteItems">Select All Eligible</button>
        <button class="toolbar-btn toolbar-btn--small toolbar-btn--primary" type="button" :disabled="noteHashRewriteRunning || noteHashRewriteSelectedCount === 0" @click="applySelectedNoteHashRewrites">Apply Selected</button>
        <button class="toolbar-btn toolbar-btn--small toolbar-btn--done" type="button" :disabled="noteHashRewriteRunning || noteHashRewriteEligibleCount === 0" @click="applyAllEligibleNoteHashRewrites">Apply All Eligible</button>
      </div>
    </div>

    <div v-if="noteHashRewriteStatus" class="network-lookup-modal__status note-hash-rewrite-modal__status">
      {{ noteHashRewriteStatus }}
    </div>

    <div v-if="noteHashRewriteBatchProgress" class="case-batch-progress note-hash-rewrite-modal__progress" role="status" aria-live="polite">
      <div>
        <strong>{{ noteHashRewriteBatchProgress.message }}</strong>
        <span>{{ noteHashRewriteBatchProgress.processed }} / {{ noteHashRewriteBatchProgress.total }} rewrites checked</span>
      </div>
      <progress :value="noteHashRewriteBatchProgress.processed" :max="noteHashRewriteBatchProgress.total || 1"></progress>
    </div>

    <div v-if="noteHashRewriteVisibleItems.length === 0" class="network-lookup-modal__empty-state">
      No note rewrite cards match the current filter. Refresh the scan, show all cards, or create a backup if this case has no hash references to replace.
    </div>

    <div v-else class="duplicate-hash-labeler-modal__card-list note-hash-rewrite-modal__card-list">
      <article
        v-for="item in noteHashRewriteVisibleItems"
        :key="item.reviewKey"
        class="known-entity-casing-card note-hash-rewrite-card"
        :class="{
          'known-entity-casing-card--resolved': !item.isEligible,
          'known-entity-casing-card--selected': !!noteHashRewriteSelectedByKey[item.reviewKey]
        }"
      >
        <div class="known-entity-casing-card__header">
          <label class="known-entity-casing-card__select">
            <input
              v-model="noteHashRewriteSelectedByKey[item.reviewKey]"
              type="checkbox"
              :disabled="noteHashRewriteRunning || !item.isEligible"
            />
            <span>{{ item.isEligible ? 'Select rewrite' : 'Blocked/current' }}</span>
          </label>
          <div class="known-entity-casing-card__title">
            <h4>Row {{ item.rowNumber }} · Path {{ item.pathLabel || '—' }}</h4>
            <p>{{ item.itemKindLabel }} · {{ item.transactionHash || 'No hash on row' }}</p>
          </div>
          <div class="known-entity-casing-card__badges">
            <span>{{ item.statusLabel }}</span>
            <span v-if="item.ambiguousCount">{{ item.ambiguousCount }} ambiguous match{{ item.ambiguousCount === 1 ? '' : 'es' }}</span>
          </div>
        </div>

        <div class="known-entity-casing-card__metric-grid note-hash-rewrite-card__metric-grid">
          <article>
            <span>Column Q hashes</span>
            <strong>{{ (item.qHashes || []).join(', ') || item.continuesAs || '—' }}</strong>
            <small>Column Q itself is not rewritten.</small>
          </article>
          <article>
            <span>Proposed labels</span>
            <strong>{{ (item.qLabels || []).join(', ') || '—' }}</strong>
            <small>Sorted naturally for presentability.</small>
          </article>
          <article>
            <span>Unresolved hashes</span>
            <strong>{{ (item.unresolvedHashes || []).join(', ') || '—' }}</strong>
            <small>{{ item.unresolvedHashes?.length ? 'Add/fix path labels first.' : 'Ready.' }}</small>
          </article>
          <article>
            <span>Reason</span>
            <strong>{{ item.reason }}</strong>
            <small>{{ item.replacementCount || 0 }} replacement{{ (item.replacementCount || 0) === 1 ? '' : 's' }}.</small>
          </article>
        </div>

        <div class="known-entity-casing-card__note-grid note-hash-rewrite-card__note-grid">
          <label>
            <span>Current Column J</span>
            <textarea :value="item.currentNotes || '(blank)'" rows="4" readonly></textarea>
          </label>
          <label>
            <span>Proposed Column J</span>
            <textarea :value="item.proposedNotes || '(blank)'" rows="4" readonly></textarea>
          </label>
          <label>
            <span>Rewrite details</span>
            <textarea :value="(item.sectionSummaries || []).map(section => `${section.before}\n→ ${section.after}`).join('\n\n') || item.reason" rows="4" readonly></textarea>
          </label>
        </div>

        <div class="known-entity-casing-card__actions">
          <button class="toolbar-btn" type="button" @click="selectNoteHashRewriteRow(item)">Select Row</button>
          <button class="toolbar-btn" type="button" @click="copyNoteHashRewriteDetails(item)">Copy Details</button>
          <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="noteHashRewriteRunning || !item.isEligible" @click="applyNoteHashRewriteItem(item)">Apply Rewrite</button>
        </div>
      </article>
    </div>

    <div class="path-operation-modal__completion-card note-hash-rewrite-modal__completion-card">
      <div>
        <strong>Finished notes backup and path-label rewrite?</strong>
        <p>
          Create/merge the backup, apply current rewrites, then mark this step complete. If later pruning/grafting changes labels, re-open this modal and refresh the note sections again.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !noteHashRewriteReviewIsComplete" @click="runCaseModalMethod('markNoteHashRewriteReviewComplete', { title: 'Marking note hash rewrite complete…', message: 'Saving hash-to-label rewrite review confirmation.' })">
        {{ noteHashRewriteCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn" type="button" @click="stageOriginalNotesBackupPreview()">Stage Original Notes Backup</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !noteHashRewriteReviewIsComplete" @click="runCaseModalMethod('markNoteHashRewriteReviewComplete', { title: 'Marking note hash rewrite complete…', message: 'Saving hash-to-label rewrite review confirmation.' })">
        {{ noteHashRewriteCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelNoteHashRewriteModal">Close</button>
    </div>
  </div>
</div>


<div v-if="showWwwUsdReferenceModal && !caseWorkflowReviewAutoUiSuppressed" class="agent-modal-backdrop" @click="cancelWwwUsdReferenceReviewModal">
  <div class="agent-modal suspicious-address-table-modal what-went-where-modal www-usd-reference-modal" @click.stop>
    <div class="deposit-pattern-modal__header suspicious-address-table-modal__header what-went-where-modal__header">
      <div>
        <h3>Review Top-Level USD Reference Values</h3>
        <p>
          Fill and review Column H on top-level/root main transaction rows before cleanup and report deliverables. These values are used later by What Went Where; doing them here avoids late Main Data edits during deliverable buildout.
        </p>
        <p class="what-went-where-modal__warning-text">
          Important: the built-in estimate is only a rough helper and is not a precise day-of-loss valuation. Use CoinMarketCap or another accepted pricing source to get the real day-specific USD value, then enter that value in Column H.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close" :disabled="whatWentWhereEstimateRunning" @click="cancelWwwUsdReferenceReviewModal">×</button>
    </div>

    <div class="deposit-pattern-modal__summary suspicious-address-table-modal__summary what-went-where-modal__summary www-usd-reference-modal__summary">
      <div class="deposit-pattern-modal__metric">
        <span>Reference rows</span>
        <strong>{{ wwwUsdReferenceState.usableReferenceCount || 0 }}/{{ wwwUsdReferenceState.referenceRowCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Missing H</span>
        <strong>{{ wwwUsdReferenceState.missingUsdReferenceCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Estimateable</span>
        <strong>{{ wwwUsdReferenceState.estimateableMissingCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Status</span>
        <strong>{{ wwwUsdReferenceState.reviewIsCurrent ? 'Reviewed' : wwwUsdReferenceState.canReview ? 'Ready' : 'Needs H' }}</strong>
      </div>
    </div>

    <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': wwwUsdReferenceState.canReview }">
      <strong>{{ wwwUsdReferenceState.canReview ? 'Ready to review' : 'Column H references needed' }}</strong>
      <span>{{ wwwUsdReferenceState.summary }}</span>
      <span class="suspicious-address-table-modal__near-miss">
        Local reference data is rough and bucketed, not a day-specific valuation. BTC has {{ whatWentWherePriceReferenceMetadata.BTC || 0 }} points and ETH has {{ whatWentWherePriceReferenceMetadata.ETH || 0 }} points; USDT/USDC/USDD/DAI are treated as 1 USD. Use a real day-of source such as CoinMarketCap before marking this reviewed.
      </span>
    </div>

    <div class="deposit-pattern-modal__toolbar suspicious-address-table-modal__toolbar what-went-where-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search</span>
        <input v-model.trim="wwwUsdReferenceSearch" type="search" placeholder="Search row, path, hash, amount, issue…" />
      </label>
      <label class="case-details-modal__field">
        <span>View</span>
        <select v-model="wwwUsdReferenceViewFilter">
          <option value="missing">Main refs needing Column H</option>
          <option value="all">All main refs</option>
        </select>
      </label>
      <div class="deposit-pattern-modal__toolbar-actions suspicious-address-table-modal__toolbar-actions what-went-where-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" @click="refreshWwwUsdReferenceReview()">Refresh</button>
        <button class="toolbar-btn" type="button" :disabled="whatWentWhereEstimateRunning" @click="estimateWhatWentWhereUsdReferences({ selectedOnly: true })">Estimate Selected</button>
        <button class="toolbar-btn" type="button" :disabled="whatWentWhereEstimateRunning" @click="estimateWhatWentWhereUsdReferences({ selectedOnly: false })">Estimate Missing</button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="whatWentWhereEstimateRunning" @click="applySelectedWhatWentWhereUsdReferences()">Apply Selected H</button>
        <button class="toolbar-btn" type="button" @click="stageWwwUsdReferenceReviewAudit()">Stage Audit</button>
      </div>
    </div>

    <div v-if="wwwUsdReferenceStatus" class="network-lookup-modal__status suspicious-address-table-modal__status what-went-where-modal__status">
      {{ wwwUsdReferenceStatus }}
    </div>

    <div v-if="whatWentWhereEstimateRunning" class="network-lookup-modal__status suspicious-address-table-modal__status what-went-where-modal__status">
      Estimating Column H values… {{ whatWentWhereEstimateProgress.done }}/{{ whatWentWhereEstimateProgress.total }}
    </div>

    <div class="template-deliverable-modal__preview-card suspicious-address-table-modal__preview-card what-went-where-modal__preview-card www-usd-reference-modal__preview-card">
      <div class="template-deliverable-modal__preview-header">
        <div>
          <strong>Top-level Column H setup</strong>
          <small>{{ wwwUsdReferenceVisibleRows.length }} visible main transaction reference row{{ wwwUsdReferenceVisibleRows.length === 1 ? '' : 's' }}</small>
        </div>
        <span class="header-badge" :class="{ 'header-badge--active': wwwUsdReferenceState.missingUsdReferenceCount === 0 }">
          {{ wwwUsdReferenceState.missingUsdReferenceCount === 0 ? 'References complete' : `${wwwUsdReferenceState.missingUsdReferenceCount} missing` }}
        </span>
      </div>
      <div class="suspicious-address-table-modal__table-wrap what-went-where-modal__table-wrap">
        <table class="suspicious-address-table-modal__table what-went-where-modal__table what-went-where-modal__reference-table">
          <thead>
            <tr>
              <th><input type="checkbox" :checked="wwwUsdReferenceAllVisibleSelected" @change="toggleAllVisibleWwwUsdReferences($event.target.checked)" /></th>
              <th>Row</th>
              <th>Path</th>
              <th>Transaction Hash</th>
              <th>UTC Date</th>
              <th>Reference Amount</th>
              <th>Current H</th>
              <th>Draft H</th>
              <th>Estimate</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in wwwUsdReferenceVisibleRows" :key="row.key" :class="{ 'what-went-where-modal__row--needs-value': !row.isUsable }">
              <td><input type="checkbox" :checked="!!whatWentWhereSelectedReferenceKeys[row.key]" @change="setWhatWentWhereReferenceSelected(row.key, $event.target.checked)" /></td>
              <td>Row {{ row.rowNumber }}</td>
              <td>{{ row.pathLabel || '—' }}</td>
              <td class="network-lookup-table__mono">{{ row.transactionHash }}</td>
              <td>{{ row.utcDate || '—' }}</td>
              <td>{{ row.referenceAmountDisplay || '—' }}</td>
              <td>{{ row.currentColumnH || '—' }}</td>
              <td><input class="what-went-where-modal__h-input" :value="getWhatWentWhereDraftValue(row)" placeholder="USD value" @input="setWhatWentWhereDraftValue(row.key, $event.target.value)" /></td>
              <td>
                <small v-if="whatWentWhereEstimateByKey[row.key]">{{ whatWentWhereEstimateByKey[row.key].status }} {{ whatWentWhereEstimateByKey[row.key].snapshotTimestampUtc ? `(${whatWentWhereEstimateByKey[row.key].snapshotTimestampUtc})` : '' }}</small>
                <span v-else>—</span>
              </td>
              <td>{{ row.isUsable ? 'Usable' : row.problemText }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="path-operation-modal__completion-card suspicious-address-table-modal__completion-card what-went-where-modal__completion-card">
      <div>
        <strong>Finished Column H USD reference review?</strong>
        <p>
          Apply any needed Column H values to Main Data, stage the audit if helpful, then mark this step reviewed. Prefer a real day-specific USD value; the estimator is only a rough fill-in helper. Later What Went Where can still estimate/apply as a fallback, but this is the recommended place to finish the Main Data valuation edit.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || whatWentWhereEstimateRunning || !wwwUsdReferenceState.canReview || wwwUsdReferenceState.reviewIsCurrent" @click="runCaseModalMethod('markWwwUsdReferenceReviewComplete', { title: 'Marking USD references reviewed…', message: 'Saving Column H reference review state and staging audit output.' })">
        {{ wwwUsdReferenceCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn" type="button" @click="stageWwwUsdReferenceReviewAudit()">Stage Audit</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || whatWentWhereEstimateRunning || !wwwUsdReferenceState.canReview || wwwUsdReferenceState.reviewIsCurrent" @click="runCaseModalMethod('markWwwUsdReferenceReviewComplete', { title: 'Marking USD references reviewed…', message: 'Saving Column H reference review state and staging audit output.' })">
        {{ wwwUsdReferenceCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelWwwUsdReferenceReviewModal">Close</button>
    </div>
  </div>
</div>


<div v-if="showAddressLegendModal && !caseWorkflowReviewAutoUiSuppressed" class="agent-modal-backdrop" @click="cancelAddressLegendModal">
  <div class="agent-modal deposit-pattern-modal address-legend-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Build And Review Address Legend</h3>
        <p>
          Build a separate per-case address legend from current Main Data, current known-entity curators, and reviewed notes. Rebuild after case edits, then mark the legend reviewed when it broadly looks correct.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close address legend" :disabled="addressLegendRunning" @click="cancelAddressLegendModal">
        ×
      </button>
    </div>

    <div class="deposit-pattern-modal__summary-grid address-legend-modal__summary-grid">
      <article>
        <span>Candidates</span>
        <strong>{{ addressLegendAnalysis.candidateCount || 0 }}</strong>
        <small>{{ addressLegendAnalysis.senderCount || 0 }} sender-side · {{ addressLegendAnalysis.receiverCount || 0 }} receiver-side</small>
      </article>
      <article>
        <span>Stored legend</span>
        <strong>{{ addressLegendAnalysis.legendEntryCount || 0 }}</strong>
        <small>{{ addressLegendAnalysis.changedCount || 0 }} rebuild item{{ (addressLegendAnalysis.changedCount || 0) === 1 ? '' : 's' }} · {{ addressLegendAnalysis.staleLegendEntryCount || 0 }} stale</small>
      </article>
      <article>
        <span>Entity-derived</span>
        <strong>{{ addressLegendAnalysis.exchangeDerivedCount || 0 }}</strong>
        <small>From server/local effective curator and case notes.</small>
      </article>
      <article>
        <span>Status</span>
        <strong>{{ addressLegendReviewIsCurrent ? 'Reviewed' : addressLegendBuildIsCurrent ? 'Built' : 'Needs build' }}</strong>
        <small>{{ addressLegendAnalysis.summary }}</small>
      </article>
    </div>

    <div class="deposit-pattern-modal__guidance address-legend-modal__guidance">
      <span aria-hidden="true">📒</span>
      <div>
        <strong>The address legend is separate from Main Data rows.</strong>
        <p>
          It can be rebuilt from the current spreadsheet when paths, attributions, or notes change. If it looks wrong, update the case or curator first, rebuild, and only then mark this step reviewed.
        </p>
      </div>
    </div>

    <div class="deposit-pattern-modal__toolbar address-legend-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search</span>
        <input v-model.trim="addressLegendSearch" type="search" placeholder="Search label, address, entity, chain, evidence…" />
      </label>
      <label class="case-details-modal__field">
        <span>View</span>
        <select v-model="addressLegendViewFilter">
          <option value="changed">Needs rebuild</option>
          <option value="exchange">Exchange/service-derived</option>
          <option value="sender">Sender-side</option>
          <option value="receiver">Receiver-side</option>
          <option value="current">Current</option>
          <option value="all">All</option>
        </select>
      </label>
      <label class="network-lookup-modal__checkbox address-legend-modal__checkbox">
        <input v-model="addressLegendLabelDormantSeparately" type="checkbox" :disabled="addressLegendRunning" @change="refreshAddressLegendReview({ silent: true })" />
        <span>Label dormant addresses separately</span>
      </label>
      <label class="network-lookup-modal__checkbox address-legend-modal__checkbox">
        <input v-model="addressLegendUseDepositLabels" type="checkbox" :disabled="addressLegendRunning" @change="refreshAddressLegendReview({ silent: true })" />
        <span>Use deposit-address labels instead of Tool17D-style suspect entity labels</span>
      </label>
      <div class="deposit-pattern-modal__toolbar-actions address-legend-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" :disabled="addressLegendRunning" @click="refreshAddressLegendReview()">Refresh</button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="addressLegendRunning || (addressLegendAnalysis.candidateCount || 0) === 0" @click="rebuildAddressLegend()">Rebuild Legend</button>
        <button class="toolbar-btn" type="button" :disabled="addressLegendRunning || (addressLegendAnalysis.candidateCount || 0) === 0" @click="stageAddressLegendPreview()">Stage Preview</button>
        <button class="toolbar-btn" type="button" :disabled="addressLegendRunning || (addressLegendAnalysis.candidateCount || 0) === 0" @click="downloadAddressLegendTsv()">Download TSV</button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="openEntityCuratorReviewModal()">Review Curator</button>
      </div>
    </div>

    <div v-if="addressLegendStatus" class="network-lookup-modal__status address-legend-modal__status">
      {{ addressLegendStatus }}
    </div>

    <div class="address-legend-modal__layout">
      <section class="address-legend-modal__list-panel">
        <div class="address-legend-modal__list-header">
          <div>
            <strong>Legend candidates</strong>
            <small>{{ addressLegendVisibleCandidates.length }} visible of {{ addressLegendCandidates.length }}</small>
          </div>
          <span class="header-badge" :class="{ 'header-badge--active': addressLegendChangedCount > 0 }">
            {{ addressLegendChangedCount }} need rebuild
          </span>
        </div>

        <div v-if="addressLegendVisibleCandidates.length === 0" class="network-lookup-modal__empty-state">
          No address legend candidates match the current filter. Show all candidates or refresh the scan.
        </div>

        <div v-else class="address-legend-modal__table-wrap">
          <table class="address-legend-modal__table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Address</th>
                <th>Existing</th>
                <th>Family</th>
                <th>Side</th>
                <th>Entity</th>
                <th>Row</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="candidate in addressLegendVisibleCandidates"
                :key="candidate.key"
                :class="{ 'address-legend-modal__row--selected': addressLegendSelectedCandidate && addressLegendSelectedCandidate.id === candidate.id, 'address-legend-modal__row--changed': candidate.isChanged }"
                @click="selectAddressLegendCandidate(candidate)"
              >
                <td>{{ candidate.proposedLegendLabel }}</td>
                <td>{{ candidate.preferredDisplayAddress }}</td>
                <td>{{ candidate.existingLegendLabel || '—' }}</td>
                <td>{{ candidate.labelFamilyLabel || candidate.labelFamily }}</td>
                <td>{{ candidate.sourceSideLabel || candidate.sourceSide || '—' }}</td>
                <td>{{ candidate.entityPresentedName || '—' }}</td>
                <td>{{ candidate.firstSeenRowNumber }}</td>
                <td>{{ candidate.statusLabel }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <aside class="address-legend-modal__detail-panel">
        <template v-if="addressLegendSelectedCandidate">
          <div class="address-legend-modal__detail-header">
            <strong>{{ addressLegendSelectedCandidate.proposedLegendLabel }}</strong>
            <span>{{ addressLegendSelectedCandidate.statusLabel }}</span>
          </div>
          <dl class="address-legend-modal__detail-grid">
            <div>
              <dt>Address</dt>
              <dd>{{ addressLegendSelectedCandidate.preferredDisplayAddress }}</dd>
            </div>
            <div>
              <dt>Existing label</dt>
              <dd>{{ addressLegendSelectedCandidate.existingLegendLabel || '(none)' }}</dd>
            </div>
            <div>
              <dt>Reason</dt>
              <dd>{{ addressLegendSelectedCandidate.labelFamilyLabel || addressLegendSelectedCandidate.labelFamily }} · {{ addressLegendSelectedCandidate.sourceSideLabel || addressLegendSelectedCandidate.sourceSide || '—' }} · {{ addressLegendSelectedCandidate.sourceReasonLabel || addressLegendSelectedCandidate.sourceReason || 'Rule' }}</dd>
            </div>
            <div>
              <dt>Chain</dt>
              <dd>{{ addressLegendSelectedCandidate.chainName || addressLegendSelectedCandidate.chainId || '—' }}</dd>
            </div>
          </dl>
          <div class="address-legend-modal__evidence">
            <strong>Evidence</strong>
            <ul>
              <li v-for="(evidence, index) in addressLegendSelectedCandidate.evidence" :key="`legend-evidence-${index}`">{{ evidence }}</li>
            </ul>
            <small v-if="!addressLegendSelectedCandidate.evidence.length">No extra evidence was generated for this candidate.</small>
          </div>
          <div class="address-legend-modal__detail-actions">
            <button class="toolbar-btn" type="button" @click="selectAddressLegendCandidateRow(addressLegendSelectedCandidate)">Select Row</button>
            <button class="toolbar-btn" type="button" @click="copyAddressLegendCandidateDetails(addressLegendSelectedCandidate)">Copy Details</button>
          </div>
        </template>
        <div v-else class="network-lookup-modal__empty-state">
          Select a candidate to inspect its source row, proposed label, and evidence.
        </div>
      </aside>
    </div>

    <div class="path-operation-modal__completion-card address-legend-modal__completion-card">
      <div>
        <strong>Finished reviewing the address legend?</strong>
        <p>
          Rebuild the legend first. Then scan the preview/list for obvious problems. If the legend looks broadly correct, mark it reviewed and continue to deliverable buildout.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || addressLegendRunning || !addressLegendBuildIsCurrent || addressLegendReviewIsCurrent" @click="runCaseModalMethod('markAddressLegendReviewComplete', { title: 'Marking address legend reviewed…', message: 'Saving legend review state and staging audit output.' })">
        {{ addressLegendCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn" type="button" @click="stageAddressLegendPreview()">Stage Address Legend</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || addressLegendRunning || !addressLegendBuildIsCurrent || addressLegendReviewIsCurrent" @click="runCaseModalMethod('markAddressLegendReviewComplete', { title: 'Marking address legend reviewed…', message: 'Saving legend review state and staging audit output.' })">
        {{ addressLegendCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelAddressLegendModal">Close</button>
    </div>
  </div>
</div>


<div v-if="showTemplateDeliverableModal && !caseWorkflowReviewAutoUiSuppressed" class="agent-modal-backdrop" @click="cancelTemplateDeliverableModal">
  <div class="agent-modal template-deliverable-modal" @click.stop>
    <div class="deposit-pattern-modal__header template-deliverable-modal__header">
      <div>
        <h3>Build And Review Template Deliverable</h3>
        <p>
          Build the Tool 31B-style template spreadsheet from current Main Data, the reviewed Address Legend, and the effective known-entity curator. The template is staged or downloaded as a deliverable; it does not rewrite Main Data.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close" @click="cancelTemplateDeliverableModal">×</button>
    </div>

    <div class="deposit-pattern-modal__summary template-deliverable-modal__summary">
      <div class="deposit-pattern-modal__metric">
        <span>Main rows</span>
        <strong>{{ templateDeliverableState.rowCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Legend entries</span>
        <strong>{{ templateDeliverableState.legendEntryCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Curator terms</span>
        <strong>{{ templateDeliverableState.exchangeKeywordCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Status</span>
        <strong>{{ templateDeliverableState.statusLabel }}</strong>
      </div>
    </div>

    <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': templateDeliverableState.canBuild }">
      <strong>{{ templateDeliverableState.canBuild ? 'Ready to build' : 'Needs attention before build' }}</strong>
      <span>{{ templateDeliverableState.summary }}</span>
    </div>

    <div class="deposit-pattern-modal__toolbar template-deliverable-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search preview</span>
        <input v-model.trim="templateDeliverableSearch" type="search" placeholder="Search any template cell…" />
      </label>
      <label class="case-details-modal__field">
        <span>View</span>
        <select v-model="templateDeliverableViewFilter">
          <option value="all">All rows</option>
          <option value="non_empty">Non-empty rows</option>
          <option value="main">Main TXNs</option>
          <option value="sub">Sub TXNs</option>
          <option value="exchange">Exchange rows</option>
          <option value="suspicious">Suspicious rows</option>
        </select>
      </label>
      <div class="deposit-pattern-modal__toolbar-actions template-deliverable-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" @click="refreshTemplateDeliverableReview()">Refresh</button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!templateDeliverableState.canBuild" @click="stageTemplateDeliverablePreview()">Stage Template</button>
        <button class="toolbar-btn" type="button" :disabled="!templateDeliverableState.canBuild" @click="downloadTemplateDeliverableTsv()">Download TSV</button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="openAddressLegendModal()">Review Legend</button>
      </div>
    </div>

    <div v-if="templateDeliverableStatus" class="network-lookup-modal__status template-deliverable-modal__status">
      {{ templateDeliverableStatus }}
    </div>

    <div class="template-deliverable-modal__preview-card">
      <div class="template-deliverable-modal__preview-header">
        <div>
          <strong>Template preview</strong>
          <small>{{ templateDeliverableVisibleRows.length }} visible of {{ templateDeliverableState.rows.length || 0 }} generated row{{ (templateDeliverableState.rows.length || 0) === 1 ? '' : 's' }}</small>
        </div>
        <span class="header-badge" :class="{ 'header-badge--active': templateDeliverableState.builtIsCurrent }">
          {{ templateDeliverableState.builtIsCurrent ? 'Built current' : 'Not built current' }}
        </span>
      </div>
      <div v-if="templateDeliverableVisibleRows.length === 0" class="network-lookup-modal__empty-state">
        No template rows match the current filter. Refresh the preview or show all rows.
      </div>
      <div v-else class="template-deliverable-modal__table-wrap">
        <table class="template-deliverable-modal__table">
          <thead>
            <tr>
              <th>#</th>
              <th v-for="(header, index) in templateDeliverableHeaders" :key="`template-header-${index}`">{{ header }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in templateDeliverableVisibleRows" :key="row.key">
              <th>{{ row.rowNumber }}</th>
              <td v-for="(cell, index) in row.cells" :key="`${row.key}-cell-${index}`">{{ cell }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="path-operation-modal__completion-card template-deliverable-modal__completion-card">
      <div>
        <strong>Finished reviewing the template?</strong>
        <p>
          Stage or download the template, review the preview for obvious mapping or legend problems, then mark it reviewed when it is ready for the report package.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !templateDeliverableState.canMarkReview || templateDeliverableState.reviewIsCurrent" @click="runCaseModalMethod('markTemplateDeliverableReviewComplete', { title: 'Marking template reviewed…', message: 'Saving template review state and staging audit output.' })">
        {{ templateDeliverableCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn" type="button" :disabled="caseModalOperation.active || !templateDeliverableState.canBuild" @click="stageTemplateDeliverablePreview()">Stage Template</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !templateDeliverableState.canMarkReview || templateDeliverableState.reviewIsCurrent" @click="runCaseModalMethod('markTemplateDeliverableReviewComplete', { title: 'Marking template reviewed…', message: 'Saving template review state and staging audit output.' })">
        {{ templateDeliverableCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelTemplateDeliverableModal">Close</button>
    </div>
  </div>
</div>




<div v-if="showSuspiciousAddressTableModal && !caseWorkflowReviewAutoUiSuppressed" class="agent-modal-backdrop" @click="cancelSuspiciousAddressTableModal">
  <div class="agent-modal suspicious-address-table-modal" @click.stop>
    <div class="deposit-pattern-modal__header suspicious-address-table-modal__header">
      <div>
        <h3>Build And Review Suspicious Address Table</h3>
        <p>
          Build the Tool 8F-style concentration table from current Main Data and the reviewed Address Legend. The table includes reviewed labels like <code>Suspect's Address #4</code>, summarizes observed incoming amounts, and dedupes repeated transaction-hash contributions.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close" @click="cancelSuspiciousAddressTableModal">×</button>
    </div>

    <div class="deposit-pattern-modal__summary suspicious-address-table-modal__summary">
      <div class="deposit-pattern-modal__metric">
        <span>Table rows</span>
        <strong>{{ suspiciousAddressTableState.rowCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Deduped transfers</span>
        <strong>{{ suspiciousAddressTableState.contributionCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Unique txns checked</span>
        <strong>{{ suspiciousAddressTableState.uniqueTransactionCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Status</span>
        <strong>{{ suspiciousAddressTableState.statusLabel }}</strong>
      </div>
    </div>

    <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': suspiciousAddressTableState.canBuild }">
      <strong>{{ suspiciousAddressTableState.canBuild ? 'Ready to build' : 'Needs attention before build' }}</strong>
      <span>{{ suspiciousAddressTableState.summary }}</span>
      <span v-if="suspiciousAddressTableState.excludedNearMissLabelCount" class="suspicious-address-table-modal__near-miss">
        Near misses excluded: {{ suspiciousAddressTableState.excludedNearMissExamples.slice(0, 5).join(', ') }}{{ suspiciousAddressTableState.excludedNearMissExamples.length > 5 ? '…' : '' }}
      </span>
    </div>

    <div class="deposit-pattern-modal__toolbar suspicious-address-table-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search table</span>
        <input v-model.trim="suspiciousAddressTableSearch" type="search" placeholder="Search address, label, amount, source row…" />
      </label>
      <label class="case-details-modal__field">
        <span>View</span>
        <select v-model="suspiciousAddressTableViewFilter">
          <option value="all">All rows</option>
          <option value="with_multiple_transfers">Multiple transfers</option>
          <option value="single_transfer">Single transfer</option>
          <option value="missing_number_gap">Possible label-number gaps</option>
        </select>
      </label>
      <div class="deposit-pattern-modal__toolbar-actions suspicious-address-table-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" @click="refreshSuspiciousAddressTableReview()">Refresh</button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!suspiciousAddressTableState.canBuild" @click="stageSuspiciousAddressTablePreview()">Stage Table</button>
        <button class="toolbar-btn" type="button" :disabled="!suspiciousAddressTableState.canBuild" @click="downloadSuspiciousAddressTableTsv()">Download TSV</button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="openAddressLegendModal()">Review Legend</button>
      </div>
    </div>

    <div v-if="suspiciousAddressTableStatus" class="network-lookup-modal__status suspicious-address-table-modal__status">
      {{ suspiciousAddressTableStatus }}
    </div>

    <div class="template-deliverable-modal__preview-card suspicious-address-table-modal__preview-card">
      <div class="template-deliverable-modal__preview-header">
        <div>
          <strong>Suspicious address table preview</strong>
          <small>{{ suspiciousAddressTableVisibleRows.length }} visible of {{ suspiciousAddressTableState.rows.length || 0 }} generated suspect address row{{ (suspiciousAddressTableState.rows.length || 0) === 1 ? '' : 's' }}</small>
        </div>
        <span class="header-badge" :class="{ 'header-badge--active': suspiciousAddressTableState.builtIsCurrent }">
          {{ suspiciousAddressTableState.builtIsCurrent ? 'Built current' : 'Not built current' }}
        </span>
      </div>
      <div v-if="suspiciousAddressTableVisibleRows.length === 0" class="network-lookup-modal__empty-state">
        No suspicious address table rows match the current filter. If this seems wrong, review the Address Legend labels and make sure suspect labels use the exact <code>Suspect's Address #n</code> form.
      </div>
      <div v-else class="suspicious-address-table-modal__table-wrap">
        <table class="suspicious-address-table-modal__table">
          <thead>
            <tr>
              <th>#</th>
              <th>Address</th>
              <th>Label</th>
              <th>Received Amounts</th>
              <th>Unique Txns</th>
              <th>Source Rows</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in suspiciousAddressTableVisibleRows" :key="row.key">
              <th>{{ row.displayIndex }}</th>
              <td class="suspicious-address-table-modal__address">{{ row.address }}</td>
              <td><strong>{{ row.label }}</strong></td>
              <td class="suspicious-address-table-modal__amounts">{{ row.receivedSummaryCell || row.receivedSummaryDisplay }}</td>
              <td>{{ row.uniqueTransactionCount }}</td>
              <td>
                <div class="suspicious-address-table-modal__source-list">
                  <span v-for="source in row.sourceRows.slice(0, 6)" :key="`${row.key}-source-${source.rowNumber}-${source.formattedAmount}`">
                    Row {{ source.rowNumber }} · {{ source.formattedAmount }}
                  </span>
                  <span v-if="row.sourceRows.length > 6">+{{ row.sourceRows.length - 6 }} more</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="path-operation-modal__completion-card suspicious-address-table-modal__completion-card">
      <div>
        <strong>Finished reviewing the suspicious address table?</strong>
        <p>
          Stage or download the current table, scan the numbering and amounts for obvious issues, then mark it reviewed when it looks ready for the report package. If something looks off, update Main Data or the Address Legend and rebuild.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !suspiciousAddressTableState.canBuild || suspiciousAddressTableState.reviewIsCurrent" @click="runCaseModalMethod('markSuspiciousAddressTableReviewComplete', { title: 'Marking suspicious address table reviewed…', message: 'Saving review state and staging audit output.' })">
        {{ suspiciousAddressTableCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn" type="button" @click="stageSuspiciousAddressTablePreview()">Stage Table</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !suspiciousAddressTableState.canBuild || suspiciousAddressTableState.reviewIsCurrent" @click="runCaseModalMethod('markSuspiciousAddressTableReviewComplete', { title: 'Marking suspicious address table reviewed…', message: 'Saving review state and staging audit output.' })">
        {{ suspiciousAddressTableCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelSuspiciousAddressTableModal">Close</button>
    </div>
  </div>

</div>

<div v-if="showExchangeAddressTableModal && !caseWorkflowReviewAutoUiSuppressed" class="agent-modal-backdrop" @click="cancelExchangeAddressTableModal">
  <div class="agent-modal suspicious-address-table-modal exchange-address-table-modal" @click.stop>
    <div class="deposit-pattern-modal__header suspicious-address-table-modal__header exchange-address-table-modal__header">
      <div>
        <h3>Build And Review Exchange Address Table</h3>
        <p>
          Build the Tool 9I-style exchange address table from current Main Data and the reviewed Address Legend. The table groups exchange/service receiving addresses, summarizes observed incoming amounts, dedupes repeated duplicate-flow contributions, and lets you exclude rows that do not belong in this case table.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close" @click="cancelExchangeAddressTableModal">×</button>
    </div>

    <div class="deposit-pattern-modal__summary suspicious-address-table-modal__summary exchange-address-table-modal__summary">
      <div class="deposit-pattern-modal__metric">
        <span>Included rows</span>
        <strong>{{ exchangeAddressTableState.visibleRowCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Excluded rows</span>
        <strong>{{ exchangeAddressTableState.excludedCurrentCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Deduped transfers</span>
        <strong>{{ exchangeAddressTableState.contributionCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Status</span>
        <strong>{{ exchangeAddressTableState.statusLabel }}</strong>
      </div>
    </div>

    <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': exchangeAddressTableState.canBuild }">
      <strong>{{ exchangeAddressTableState.canBuild ? 'Ready to build' : 'Needs attention before build' }}</strong>
      <span>{{ exchangeAddressTableState.summary }}</span>
      <span v-if="exchangeAddressTableState.potentialNoiseCount" class="suspicious-address-table-modal__near-miss">
        Potential noise rows: {{ exchangeAddressTableState.potentialNoiseExamples.slice(0, 5).join(', ') }}{{ exchangeAddressTableState.potentialNoiseExamples.length > 5 ? '…' : '' }}. You can exclude these if they do not belong in the final exchange table.
      </span>
    </div>

    <div class="deposit-pattern-modal__toolbar suspicious-address-table-modal__toolbar exchange-address-table-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search table</span>
        <input v-model.trim="exchangeAddressTableSearch" type="search" placeholder="Search address, exchange, label, amount, source row…" />
      </label>
      <label class="case-details-modal__field">
        <span>View</span>
        <select v-model="exchangeAddressTableViewFilter">
          <option value="included">Included rows</option>
          <option value="excluded">Excluded rows</option>
          <option value="potential_noise">Potential noise</option>
          <option value="with_multiple_transfers">Multiple transfers</option>
          <option value="single_transfer">Single transfer</option>
          <option value="all">All rows</option>
        </select>
      </label>
      <div class="deposit-pattern-modal__toolbar-actions suspicious-address-table-modal__toolbar-actions exchange-address-table-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" @click="refreshExchangeAddressTableReview()">Refresh</button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!exchangeAddressTableState.canBuild" @click="stageExchangeAddressTablePreview()">Stage Table</button>
        <button class="toolbar-btn" type="button" :disabled="!exchangeAddressTableState.canBuild" @click="downloadExchangeAddressTableTsv()">Download TSV</button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="openAddressLegendModal()">Review Legend</button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="openEntityCuratorReviewModal()">Review Curator</button>
      </div>
    </div>

    <label class="case-details-modal__field exchange-address-table-modal__comment">
      <span>Optional exclusion note</span>
      <input v-model.trim="exchangeAddressTableReviewComment" type="text" placeholder="Example: Unnamed Service should not appear in this case's exchange table." />
    </label>

    <div v-if="exchangeAddressTableStatus" class="network-lookup-modal__status suspicious-address-table-modal__status exchange-address-table-modal__status">
      {{ exchangeAddressTableStatus }}
    </div>

    <div class="template-deliverable-modal__preview-card suspicious-address-table-modal__preview-card exchange-address-table-modal__preview-card">
      <div class="template-deliverable-modal__preview-header">
        <div>
          <strong>Exchange address table preview</strong>
          <small>{{ exchangeAddressTableVisibleRows.length }} visible of {{ exchangeAddressTableState.rows.length || 0 }} candidate exchange address row{{ (exchangeAddressTableState.rows.length || 0) === 1 ? '' : 's' }}</small>
        </div>
        <span class="header-badge" :class="{ 'header-badge--active': exchangeAddressTableState.builtIsCurrent }">
          {{ exchangeAddressTableState.builtIsCurrent ? 'Built current' : 'Not built current' }}
        </span>
      </div>
      <div v-if="exchangeAddressTableVisibleRows.length === 0" class="network-lookup-modal__empty-state">
        No exchange address table rows match the current filter. If this seems wrong, review the Address Legend labels and curator exchange/non-exchange terms.
      </div>
      <div v-else class="suspicious-address-table-modal__table-wrap exchange-address-table-modal__table-wrap">
        <table class="suspicious-address-table-modal__table exchange-address-table-modal__table">
          <thead>
            <tr>
              <th>#</th>
              <th>Address</th>
              <th>Exchange</th>
              <th>Label</th>
              <th>Received Amounts</th>
              <th>Unique Txns</th>
              <th>Status</th>
              <th>Actions</th>
              <th>Source Rows</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in exchangeAddressTableVisibleRows" :key="row.key" :class="{ 'exchange-address-table-modal__row--excluded': row.isExcludedCurrent }">
              <th>{{ row.displayIndex }}</th>
              <td class="suspicious-address-table-modal__address">{{ row.address }}</td>
              <td><strong>{{ row.exchangeName }}</strong></td>
              <td>{{ row.label }}</td>
              <td class="suspicious-address-table-modal__amounts">{{ row.receivedSummaryCell || row.receivedSummaryDisplay }}</td>
              <td>{{ row.uniqueTransactionCount }}</td>
              <td>
                <span class="header-badge" :class="{ 'header-badge--muted': row.isExcludedCurrent, 'header-badge--warning': row.isPotentialNoise && !row.isExcludedCurrent }">
                  {{ row.statusLabel }}
                </span>
                <small v-if="row.exclusionComment" class="exchange-address-table-modal__comment-note">{{ row.exclusionComment }}</small>
              </td>
              <td>
                <div class="exchange-address-table-modal__row-actions">
                  <button v-if="!row.isExcludedCurrent" class="toolbar-btn toolbar-btn--ghost" type="button" @click="excludeExchangeAddressTableRow(row)">Exclude</button>
                  <button v-else class="toolbar-btn" type="button" @click="restoreExchangeAddressTableRow(row)">Restore</button>
                  <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="selectExchangeAddressTableSourceRow(row)">Select Row</button>
                </div>
              </td>
              <td>
                <div class="suspicious-address-table-modal__source-list">
                  <span v-for="source in row.sourceRows.slice(0, 6)" :key="`${row.key}-source-${source.rowNumber}-${source.formattedAmount}`">
                    Row {{ source.rowNumber }} · {{ source.formattedAmount }} · {{ source.pathLabel || 'No label' }}
                  </span>
                  <span v-if="row.sourceRows.length > 6">+{{ row.sourceRows.length - 6 }} more</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="path-operation-modal__completion-card suspicious-address-table-modal__completion-card exchange-address-table-modal__completion-card">
      <div>
        <strong>Finished reviewing the exchange address table?</strong>
        <p>
          Exclude any rows that do not belong, stage or download the current table, and mark it reviewed when the included rows look ready for the report package. If the legend or curator caused a bad row, update them and rebuild.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !exchangeAddressTableState.canBuild || exchangeAddressTableState.reviewIsCurrent" @click="runCaseModalMethod('markExchangeAddressTableReviewComplete', { title: 'Marking exchange address table reviewed…', message: 'Saving review state and staging audit output.' })">
        {{ exchangeAddressTableCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn" type="button" @click="stageExchangeAddressTablePreview()">Stage Table</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !exchangeAddressTableState.canBuild || exchangeAddressTableState.reviewIsCurrent" @click="runCaseModalMethod('markExchangeAddressTableReviewComplete', { title: 'Marking exchange address table reviewed…', message: 'Saving review state and staging audit output.' })">
        {{ exchangeAddressTableCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelExchangeAddressTableModal">Close</button>
    </div>
  </div>
</div>


<div v-if="showDormantAddressDeliverablesModal && !caseWorkflowReviewAutoUiSuppressed" class="agent-modal-backdrop" @click="cancelDormantAddressDeliverablesModal">
  <div class="agent-modal suspicious-address-table-modal dormant-address-modal" @click.stop>
    <div class="deposit-pattern-modal__header suspicious-address-table-modal__header dormant-address-modal__header">
      <div>
        <h3>Build Dormant Address Deliverables</h3>
        <p>
          Build the Tool 52C-style dormant outputs from current Main Data and the reviewed Address Legend. The clean table gives Address + Funds Followed, while the monitoring view preserves transaction hash and amount details for later monitoring.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close" @click="cancelDormantAddressDeliverablesModal">×</button>
    </div>

    <div class="deposit-pattern-modal__summary suspicious-address-table-modal__summary dormant-address-modal__summary">
      <div class="deposit-pattern-modal__metric">
        <span>Clean rows</span>
        <strong>{{ dormantAddressDeliverableState.officialRowCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Monitoring rows</span>
        <strong>{{ dormantAddressDeliverableState.monitoringRowCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Unique txns</span>
        <strong>{{ dormantAddressDeliverableState.uniqueTransactionCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Status</span>
        <strong>{{ dormantAddressDeliverableState.statusLabel }}</strong>
      </div>
    </div>

    <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': dormantAddressDeliverableState.canBuild }">
      <strong>{{ dormantAddressDeliverableState.canBuild ? 'Ready to build' : 'Needs attention before build' }}</strong>
      <span>{{ dormantAddressDeliverableState.summary }}</span>
      <span v-if="dormantAddressDeliverableState.excludedIdentifiedAddressCount" class="suspicious-address-table-modal__near-miss">
        Identified addresses excluded: {{ dormantAddressDeliverableState.excludedExamples.slice(0, 5).join(', ') }}{{ dormantAddressDeliverableState.excludedExamples.length > 5 ? '…' : '' }}.
      </span>
    </div>

    <div class="deposit-pattern-modal__toolbar suspicious-address-table-modal__toolbar dormant-address-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search table</span>
        <input v-model.trim="dormantAddressDeliverableSearch" type="search" placeholder="Search address, funds, amount, transaction hash, source row…" />
      </label>
      <label class="case-details-modal__field">
        <span>View</span>
        <select v-model="dormantAddressDeliverableActiveView">
          <option value="official">Clean dormant table</option>
          <option value="monitoring">Monitoring view</option>
        </select>
      </label>
      <div class="deposit-pattern-modal__toolbar-actions suspicious-address-table-modal__toolbar-actions dormant-address-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" @click="refreshDormantAddressDeliverablesReview()">Refresh</button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!dormantAddressDeliverableState.canBuild" @click="stageDormantAddressTablePreview()">Stage Clean Table</button>
        <button class="toolbar-btn" type="button" :disabled="!dormantAddressDeliverableState.canBuild" @click="stageDormantAddressMonitoringPreview()">Stage Monitoring</button>
        <button class="toolbar-btn" type="button" :disabled="!dormantAddressDeliverableState.canBuild" @click="downloadDormantAddressTableTsv()">Download Clean</button>
        <button class="toolbar-btn" type="button" :disabled="!dormantAddressDeliverableState.canBuild" @click="downloadDormantAddressMonitoringTsv()">Download Monitoring</button>
        <button class="toolbar-btn toolbar-btn--monitoring" type="button" :disabled="!caseAddressMonitoringDormantVisibleEligibleCount || caseAddressMonitoringBusy" :title="caseAddressMonitoringDormantVisibleEligibleCount ? 'Create monitoring items from the visible dormant monitoring rows.' : 'No visible monitoring rows have enough data to add.'" @click="addVisibleDormantAddressRowsToMonitoring">Add visible to monitoring</button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="openCaseAddressMonitoringModal">Open monitoring</button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="openAddressLegendModal()">Review Legend</button>
      </div>
    </div>

    <div v-if="dormantAddressDeliverableStatus" class="network-lookup-modal__status suspicious-address-table-modal__status dormant-address-modal__status">
      {{ dormantAddressDeliverableStatus }}
    </div>

    <div class="template-deliverable-modal__preview-card suspicious-address-table-modal__preview-card dormant-address-modal__preview-card">
      <div class="template-deliverable-modal__preview-header">
        <div>
          <strong>{{ dormantAddressDeliverableActiveView === 'monitoring' ? 'Dormant address monitoring view' : 'Clean dormant address table' }}</strong>
          <small>{{ dormantAddressDeliverableVisibleRows.length }} visible row{{ dormantAddressDeliverableVisibleRows.length === 1 ? '' : 's' }}</small>
        </div>
        <span class="header-badge" :class="{ 'header-badge--active': dormantAddressDeliverableState.bothBuiltCurrent }">
          {{ dormantAddressDeliverableState.bothBuiltCurrent ? 'Built current' : 'Not built current' }}
        </span>
      </div>

      <div v-if="dormantAddressDeliverableVisibleRows.length === 0" class="network-lookup-modal__empty-state">
        No dormant address rows match the current view. If this seems wrong, review Column J dormant/funds-remain notes and the Address Legend dormant labels.
      </div>

      <div v-else class="suspicious-address-table-modal__table-wrap dormant-address-modal__table-wrap">
        <table v-if="dormantAddressDeliverableActiveView === 'official'" class="suspicious-address-table-modal__table dormant-address-modal__table">
          <thead>
            <tr>
              <th>#</th>
              <th>Address</th>
              <th>Funds Followed</th>
              <th>Unique Txns</th>
              <th>Occurrences</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in dormantAddressDeliverableVisibleRows" :key="row.key">
              <th>{{ row.displayIndex }}</th>
              <td class="suspicious-address-table-modal__address">{{ row.address }}</td>
              <td class="suspicious-address-table-modal__amounts">{{ row.fundsFollowedDisplay }}</td>
              <td>{{ row.uniqueTransactionCount }}</td>
              <td>{{ row.occurrenceCount }}</td>
            </tr>
          </tbody>
        </table>

        <table v-else class="suspicious-address-table-modal__table dormant-address-modal__table dormant-address-modal__monitoring-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Address</th>
              <th>Amount and Token</th>
              <th>Funds Followed</th>
              <th>Related Txn Hash</th>
              <th>Source Row</th>
              <th>Left Bounds</th>
              <th>Monitoring</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in dormantAddressDeliverableVisibleRows" :key="row.key">
              <th>{{ row.displayIndex }}</th>
              <td class="suspicious-address-table-modal__address">{{ row.address }}</td>
              <td>{{ row.amountAndToken || '—' }}</td>
              <td class="suspicious-address-table-modal__amounts">{{ row.fundsFollowed }}</td>
              <td class="network-lookup-table__mono">{{ row.relatedTransactionHash }}</td>
              <td>Row {{ row.sheetRowNumber }}</td>
              <td>{{ row.leftBoundsData || row.leftBoundsValue || '—' }}</td>
              <td>
                <button
                  class="toolbar-btn toolbar-btn--monitoring toolbar-btn--compact"
                  type="button"
                  :disabled="!caseAddressMonitoringPayloadForDormantRow(row) || caseAddressMonitoringBusy"
                  :title="caseAddressMonitoringPayloadForDormantRow(row) ? 'Add this dormant row to Address Monitoring.' : 'This row is missing required monitoring fields.'"
                  @click="addDormantAddressRowToMonitoring(row)"
                >
                  Add
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="path-operation-modal__completion-card suspicious-address-table-modal__completion-card dormant-address-modal__completion-card">
      <div>
        <strong>Finished reviewing the dormant address deliverables?</strong>
        <p>
          Review the clean Address/Funds Followed table and the monitoring view. If something looks wrong, update Main Data notes or the Address Legend and rebuild. Mark reviewed when both dormant outputs look ready.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !dormantAddressDeliverableState.canBuild || dormantAddressDeliverableState.reviewIsCurrent" @click="runCaseModalMethod('markDormantAddressDeliverablesReviewComplete', { title: 'Marking dormant deliverables reviewed…', message: 'Saving review state and staging audit output.' })">
        {{ dormantAddressDeliverableCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn" type="button" @click="stageDormantAddressTablePreview()">Stage Clean Table</button>
      <button class="toolbar-btn" type="button" @click="stageDormantAddressMonitoringPreview()">Stage Monitoring</button>
      <button class="toolbar-btn toolbar-btn--monitoring" type="button" :disabled="!caseAddressMonitoringDormantVisibleEligibleCount || caseAddressMonitoringBusy" @click="addVisibleDormantAddressRowsToMonitoring">Add visible to monitoring</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !dormantAddressDeliverableState.canBuild || dormantAddressDeliverableState.reviewIsCurrent" @click="runCaseModalMethod('markDormantAddressDeliverablesReviewComplete', { title: 'Marking dormant deliverables reviewed…', message: 'Saving review state and staging audit output.' })">
        {{ dormantAddressDeliverableCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelDormantAddressDeliverablesModal">Close</button>
    </div>
  </div>
</div>


<div v-if="showWhatWentWhereModal && !caseWorkflowReviewAutoUiSuppressed" class="agent-modal-backdrop" @click="cancelWhatWentWhereModal">
  <div class="agent-modal suspicious-address-table-modal what-went-where-modal" @click.stop>
    <div class="deposit-pattern-modal__header suspicious-address-table-modal__header what-went-where-modal__header">
      <div>
        <h3>Build And Review What Went Where</h3>
        <p>
          Build the Tool 83-style path-ending output and valuation audit from the reviewed Column H USD reference values. The estimate/apply controls remain here as a fallback, but the recommended workflow is to finish Column H earlier before deliverable buildout.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close" @click="cancelWhatWentWhereModal">×</button>
    </div>

    <div class="deposit-pattern-modal__summary suspicious-address-table-modal__summary what-went-where-modal__summary">
      <div class="deposit-pattern-modal__metric">
        <span>Main refs</span>
        <strong>{{ whatWentWhereState.usableReferenceCount || 0 }}/{{ whatWentWhereState.referenceRowCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Path endings</span>
        <strong>{{ whatWentWhereState.generatedPathEndingCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Valuations</span>
        <strong>{{ whatWentWhereState.valuationResolvedCount || 0 }}/{{ whatWentWhereState.generatedPathEndingCount || 0 }}</strong>
      </div>
      <div class="deposit-pattern-modal__metric">
        <span>Status</span>
        <strong>{{ whatWentWhereState.reviewIsCurrent ? 'Reviewed' : whatWentWhereState.canBuild ? 'Ready' : 'Needs H' }}</strong>
      </div>
    </div>

    <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': whatWentWhereState.canBuild }">
      <strong>{{ whatWentWhereState.canBuild ? 'Ready to build' : 'Column H references needed' }}</strong>
      <span>{{ whatWentWhereState.summary }}</span>
      <span class="suspicious-address-table-modal__near-miss">
        Local reference data: BTC {{ whatWentWherePriceReferenceMetadata.BTC || 0 }} points, ETH {{ whatWentWherePriceReferenceMetadata.ETH || 0 }} points; USDT/USDC/USDD/DAI are treated as 1 USDT. BTC/WBTC and ETH/WETH share the same reference data.
      </span>
    </div>

    <div class="deposit-pattern-modal__toolbar suspicious-address-table-modal__toolbar what-went-where-modal__toolbar">
      <label class="case-details-modal__field">
        <span>Search</span>
        <input v-model.trim="whatWentWhereSearch" type="search" placeholder="Search path, hash, amount, issue, category…" />
      </label>
      <label class="case-details-modal__field">
        <span>View</span>
        <select v-model="whatWentWhereViewFilter">
          <option value="references_missing">Main refs needing Column H</option>
          <option value="references_all">All main refs</option>
          <option value="path_endings">Generated path endings</option>
          <option value="manual_valuation">Manual valuation review</option>
          <option value="coverage">Per-main coverage</option>
        </select>
      </label>
      <div class="deposit-pattern-modal__toolbar-actions suspicious-address-table-modal__toolbar-actions what-went-where-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" @click="refreshWhatWentWhereReview()">Refresh</button>
        <button class="toolbar-btn" type="button" :disabled="whatWentWhereEstimateRunning" @click="estimateWhatWentWhereUsdReferences({ selectedOnly: true })">Estimate Selected</button>
        <button class="toolbar-btn" type="button" :disabled="whatWentWhereEstimateRunning" @click="estimateWhatWentWhereUsdReferences({ selectedOnly: false })">Estimate Missing</button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" @click="applySelectedWhatWentWhereUsdReferences()">Apply Selected H</button>
        <button class="toolbar-btn" type="button" :disabled="!whatWentWhereState.canBuild" @click="stageWhatWentWherePreview()">Stage WWW</button>
        <button class="toolbar-btn" type="button" :disabled="!whatWentWhereState.canBuild" @click="stageWhatWentWhereValuationAudit()">Stage Audit</button>
        <button class="toolbar-btn" type="button" :disabled="!whatWentWhereState.canBuild" @click="downloadWhatWentWhereTsv()">Download WWW</button>
      </div>
    </div>

    <div v-if="whatWentWhereStatus" class="network-lookup-modal__status suspicious-address-table-modal__status what-went-where-modal__status">
      {{ whatWentWhereStatus }}
    </div>

    <div v-if="whatWentWhereEstimateRunning" class="network-lookup-modal__status suspicious-address-table-modal__status what-went-where-modal__status">
      Estimating Column H values… {{ whatWentWhereEstimateProgress.done }}/{{ whatWentWhereEstimateProgress.total }}
    </div>

    <div v-if="whatWentWhereViewFilter === 'references_missing' || whatWentWhereViewFilter === 'references_all'" class="template-deliverable-modal__preview-card suspicious-address-table-modal__preview-card what-went-where-modal__preview-card">
      <div class="template-deliverable-modal__preview-header">
        <div>
          <strong>Top-level Column H setup</strong>
          <small>{{ whatWentWhereVisibleReferenceRows.length }} visible main transaction reference row{{ whatWentWhereVisibleReferenceRows.length === 1 ? '' : 's' }}</small>
        </div>
        <span class="header-badge" :class="{ 'header-badge--active': whatWentWhereState.missingUsdReferenceCount === 0 }">
          {{ whatWentWhereState.missingUsdReferenceCount === 0 ? 'References complete' : `${whatWentWhereState.missingUsdReferenceCount} missing` }}
        </span>
      </div>
      <div class="suspicious-address-table-modal__table-wrap what-went-where-modal__table-wrap">
        <table class="suspicious-address-table-modal__table what-went-where-modal__table what-went-where-modal__reference-table">
          <thead>
            <tr>
              <th><input type="checkbox" :checked="whatWentWhereAllVisibleReferencesSelected" @change="toggleAllVisibleWhatWentWhereReferences($event.target.checked)" /></th>
              <th>Row</th>
              <th>Path</th>
              <th>Transaction Hash</th>
              <th>UTC Date</th>
              <th>Reference Amount</th>
              <th>Current H</th>
              <th>Draft H</th>
              <th>Estimate</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in whatWentWhereVisibleReferenceRows" :key="row.key" :class="{ 'what-went-where-modal__row--needs-value': !row.isUsable }">
              <td><input type="checkbox" :checked="!!whatWentWhereSelectedReferenceKeys[row.key]" @change="setWhatWentWhereReferenceSelected(row.key, $event.target.checked)" /></td>
              <td>Row {{ row.rowNumber }}</td>
              <td>{{ row.pathLabel || '—' }}</td>
              <td class="network-lookup-table__mono">{{ row.transactionHash }}</td>
              <td>{{ row.utcDate || '—' }}</td>
              <td>{{ row.referenceAmountDisplay || '—' }}</td>
              <td>{{ row.currentColumnH || '—' }}</td>
              <td><input class="what-went-where-modal__h-input" :value="getWhatWentWhereDraftValue(row)" placeholder="USD value" @input="setWhatWentWhereDraftValue(row.key, $event.target.value)" /></td>
              <td>
                <small v-if="whatWentWhereEstimateByKey[row.key]">{{ whatWentWhereEstimateByKey[row.key].status }} {{ whatWentWhereEstimateByKey[row.key].snapshotTimestampUtc ? `(${whatWentWhereEstimateByKey[row.key].snapshotTimestampUtc})` : '' }}</small>
                <span v-else>—</span>
              </td>
              <td>{{ row.isUsable ? 'Usable' : row.problemText }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else-if="whatWentWhereViewFilter === 'path_endings'" class="template-deliverable-modal__preview-card suspicious-address-table-modal__preview-card what-went-where-modal__preview-card">
      <div class="template-deliverable-modal__preview-header">
        <div><strong>Generated path-ending rows</strong><small>{{ whatWentWhereVisiblePathEndingRows.length }} visible path-ending row{{ whatWentWhereVisiblePathEndingRows.length === 1 ? '' : 's' }}</small></div>
        <span class="header-badge" :class="{ 'header-badge--active': whatWentWhereState.canBuild }">{{ whatWentWhereState.overallUsdDisplay }}</span>
      </div>
      <div class="suspicious-address-table-modal__table-wrap what-went-where-modal__table-wrap">
        <table class="suspicious-address-table-modal__table what-went-where-modal__table">
          <thead><tr><th>Row</th><th>Main</th><th>Path</th><th>To / Address</th><th>Category</th><th>Entity</th><th>Amount</th><th>USD</th><th>Issue / Notes</th></tr></thead>
          <tbody>
            <tr v-for="row in whatWentWhereVisiblePathEndingRows" :key="row.key">
              <td>Row {{ row.rowNumber }}</td><td>{{ row.mainPathLabel || '—' }}</td><td>{{ row.pathLabel || '—' }}</td><td class="suspicious-address-table-modal__address">{{ row.toAddress || '—' }}</td><td>{{ row.category }}</td><td>{{ row.entity || '—' }}</td><td>{{ row.amountText || '—' }}</td><td>{{ row.usdText || '—' }}</td><td>{{ row.issue || row.notes || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else-if="whatWentWhereViewFilter === 'manual_valuation'" class="template-deliverable-modal__preview-card suspicious-address-table-modal__preview-card what-went-where-modal__preview-card">
      <div class="template-deliverable-modal__preview-header"><div><strong>Manual valuation review</strong><small>{{ whatWentWhereVisibleManualRows.length }} unresolved row{{ whatWentWhereVisibleManualRows.length === 1 ? '' : 's' }}</small></div></div>
      <div class="suspicious-address-table-modal__table-wrap what-went-where-modal__table-wrap">
        <table class="suspicious-address-table-modal__table what-went-where-modal__table"><thead><tr><th>Path Ending</th><th>Trace Stopped At</th><th>Amount To Value</th><th>From Hash</th><th>Issue</th></tr></thead><tbody><tr v-for="row in whatWentWhereVisibleManualRows" :key="row.key"><td>{{ row.pathEndingReference }}</td><td>{{ row.traceStoppedAt }}</td><td>{{ row.amountText || '—' }}</td><td class="network-lookup-table__mono">{{ row.fromHash || '—' }}</td><td>{{ row.issue }}</td></tr></tbody></table>
      </div>
    </div>

    <div v-else class="template-deliverable-modal__preview-card suspicious-address-table-modal__preview-card what-went-where-modal__preview-card">
      <div class="template-deliverable-modal__preview-header"><div><strong>Per-main coverage</strong><small>{{ whatWentWhereVisibleCoverageRows.length }} visible main transaction{{ whatWentWhereVisibleCoverageRows.length === 1 ? '' : 's' }}</small></div></div>
      <div class="suspicious-address-table-modal__table-wrap what-went-where-modal__table-wrap">
        <table class="suspicious-address-table-modal__table what-went-where-modal__table"><thead><tr><th>Main</th><th>Hash</th><th>Original</th><th>Recovered</th><th>Gap</th><th>Coverage</th><th>Rows</th><th>Found Tokens</th><th>Notes</th></tr></thead><tbody><tr v-for="row in whatWentWhereVisibleCoverageRows" :key="row.key"><td>{{ row.pathLabel || '—' }}</td><td class="network-lookup-table__mono">{{ row.transactionHash }}</td><td>{{ row.originalUsdDisplay || '—' }}</td><td>{{ row.recoveredUsdDisplay }}</td><td>{{ row.gapDisplay || '—' }}</td><td>{{ row.coveragePercentDisplay }}</td><td>{{ row.resolvedCount }}/{{ row.pathEndingCount }}</td><td>{{ row.foundTokensText || '—' }}</td><td>{{ row.notesText || '—' }}</td></tr></tbody></table>
      </div>
    </div>

    <div class="path-operation-modal__completion-card suspicious-address-table-modal__completion-card what-went-where-modal__completion-card">
      <div>
        <strong>Finished reviewing What Went Where?</strong>
        <p>Stage or download the What Went Where output and valuation audit, then mark reviewed when the Column H references and generated path-ending totals look ready. If the case changes, rebuild and review again.</p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !whatWentWhereState.canBuild || whatWentWhereState.reviewIsCurrent" @click="runCaseModalMethod('markWhatWentWhereReviewComplete', { title: 'Marking What Went Where reviewed…', message: 'Saving review state and staging audit output.' })">
        {{ whatWentWhereCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('main')">Open Main Data</button>
      <button class="toolbar-btn" type="button" @click="stageWhatWentWherePreview()">Stage WWW</button>
      <button class="toolbar-btn" type="button" @click="stageWhatWentWhereValuationAudit()">Stage Audit</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !whatWentWhereState.canBuild || whatWentWhereState.reviewIsCurrent" @click="runCaseModalMethod('markWhatWentWhereReviewComplete', { title: 'Marking What Went Where reviewed…', message: 'Saving review state and staging audit output.' })">
        {{ whatWentWhereCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelWhatWentWhereModal">Close</button>
    </div>
  </div>
</div>





<!-- Case Conclusion Review Modal -->
<div v-if="showCaseConclusionModal && !caseWorkflowReviewAutoUiSuppressed" class="agent-modal-backdrop" @click="cancelCaseConclusionModal">
  <div class="agent-modal agent-modal--wide case-conclusion-modal" @click.stop>
    <div class="agent-modal__header">
      <div>
        <h3>Build And Review Case Conclusion</h3>
        <p>
          Generate a written conclusion from the reviewed What Went Where totals, then edit or copy it before marking it reviewed.
        </p>
      </div>
      <button class="agent-modal__close" type="button" @click="cancelCaseConclusionModal">×</button>
    </div>

    <div class="template-deliverable-modal__stats case-conclusion-modal__stats">
      <article>
        <strong>{{ caseConclusionState.totalEstimatedLossDisplay }}</strong>
        <span>estimated loss / reference value</span>
      </article>
      <article>
        <strong>{{ caseConclusionState.totalTracedDisplay }}</strong>
        <span>traced path-ending value</span>
      </article>
      <article>
        <strong>{{ caseConclusionState.bucketCount }}</strong>
        <span>conclusion buckets</span>
      </article>
      <article>
        <strong>{{ caseConclusionState.reviewIsCurrent ? 'Reviewed' : caseConclusionState.canBuild ? 'Ready' : 'Needs WWW' }}</strong>
        <span>review status</span>
      </article>
    </div>

    <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': caseConclusionState.canBuild }">
      <strong>{{ caseConclusionState.canBuild ? 'Ready to review' : 'Prerequisites needed' }}</strong>
      <span>{{ caseConclusionState.summary }}</span>
    </div>

    <div class="case-modal-action-strip case-conclusion-modal__actions">
      <button class="toolbar-btn" type="button" @click="refreshCaseConclusionReview">Refresh</button>
      <button class="toolbar-btn" type="button" :disabled="!caseConclusionState.canBuild" @click="resetCaseConclusionDraftToGenerated">Use Generated Text</button>
      <button class="toolbar-btn" type="button" :disabled="!caseConclusionState.canBuild" @click="copyCaseConclusionText">Copy Text</button>
      <button class="toolbar-btn" type="button" :disabled="!caseConclusionState.canBuild" @click="stageCaseConclusionPreview">Stage Conclusion</button>
      <button class="toolbar-btn" type="button" :disabled="!caseConclusionState.canBuild" @click="downloadCaseConclusionText">Download TXT</button>
      <button class="toolbar-btn" type="button" :disabled="!whatWentWhereState.canBuild" @click="openWhatWentWhereModal({ view: 'path_endings' })">Review WWW</button>
    </div>

    <div class="case-conclusion-modal__grid">
      <section class="case-conclusion-modal__editor-card">
        <div class="template-deliverable-modal__subhead">
          <h4>Conclusion Text</h4>
          <span>Edit before copying or marking reviewed.</span>
        </div>
        <textarea
          v-model="caseConclusionDraft"
          class="case-conclusion-modal__textarea"
          :disabled="!caseConclusionState.canBuild"
          placeholder="Build What Went Where first, then generate the case conclusion."
        ></textarea>
      </section>

      <section class="case-conclusion-modal__support-card">
        <div class="template-deliverable-modal__subhead">
          <h4>Supporting Totals</h4>
          <span>Generated from current reviewed What Went Where.</span>
        </div>
        <div class="case-conclusion-modal__bucket-list">
          <article v-for="bucket in caseConclusionState.buckets" :key="bucket.key || `${bucket.category}:${bucket.name}`" class="case-conclusion-modal__bucket">
            <strong>{{ bucket.name || bucket.category }}</strong>
            <span>{{ bucket.usdDisplay }}</span>
            <small>{{ bucket.displayCategory || bucket.category }} · {{ bucket.percentageDisplay || '0.00%' }} · {{ bucket.tokensText || 'No token breakdown' }} · {{ bucket.unresolvedCount || 0 }} unresolved</small>
          </article>
          <p v-if="!caseConclusionState.buckets.length" class="empty-state">No conclusion buckets are available yet.</p>
        </div>
      </section>
    </div>

    <div class="agent-modal__actions agent-modal__actions--sticky-bottom">
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !caseConclusionState.canBuild || !caseConclusionDraft.trim() || caseConclusionState.reviewIsCurrent" @click="runCaseModalMethod('markCaseConclusionReviewComplete', { title: 'Marking case conclusion reviewed…', message: 'Saving the selected conclusion and staging audit output.' })">
        {{ caseConclusionState.reviewIsCurrent ? 'Conclusion review current' : 'Mark conclusion reviewed' }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelCaseConclusionModal">Close</button>
    </div>
  </div>
</div>

<div v-if="showReportBodyModal && !caseWorkflowReviewAutoUiSuppressed" class="agent-modal-backdrop" @click="cancelReportBodyModal">
  <div class="agent-modal network-lookup-modal template-deliverable-modal case-report-body-modal" @click.stop>
    <div class="network-lookup-modal__header template-deliverable-modal__header case-report-body-modal__header">
      <div>
        <h3>Build And Review Report Body</h3>
        <p>
          Build the Tool 38-style report body from current Main Data and the reviewed address legend. Review the rich preview, copy a rich clipboard variant, or use the text-only/download options.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close report body" @click="cancelReportBodyModal">×</button>
    </div>

    <div class="template-deliverable-modal__summary case-report-body-modal__summary">
      <div>
        <strong>{{ reportBodyState.topLevelPathCount || 0 }}</strong>
        <small>top-level paths</small>
      </div>
      <div>
        <strong>{{ reportBodyState.entryCount || 0 }}</strong>
        <small>transaction entries</small>
      </div>
      <div>
        <strong>{{ reportBodyState.legendEntryCount || 0 }}</strong>
        <small>legend labels</small>
      </div>
      <div>
        <strong>{{ reportBodyState.reviewIsCurrent ? 'Reviewed' : reportBodyState.canBuild ? 'Ready' : 'Needs legend' }}</strong>
        <small>{{ reportBodyState.rendererVersion }}</small>
      </div>
    </div>

    <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': reportBodyState.canBuild }">
      <strong>{{ reportBodyState.canBuild ? 'Ready to build' : 'Needs attention before build' }}</strong>
      <span>{{ reportBodyState.summary }}</span>
    </div>

    <div class="template-deliverable-modal__toolbar case-report-body-modal__toolbar">
      <label>
        <span>Preview variant</span>
        <select v-model="reportBodyActiveVariant">
          <option value="standard">Tool38G standard rich/text body</option>
          <option value="custom_spacing">Tool38H custom-spacing body</option>
        </select>
      </label>
      <label>
        <span>Search preview</span>
        <input v-model.trim="reportBodySearch" type="search" placeholder="Search path, address, label, hash, notes…" />
      </label>
      <div class="what-went-where-modal__toolbar-actions case-report-body-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" @click="refreshReportBodyReview()">Refresh</button>
        <button class="toolbar-btn" type="button" :disabled="!reportBodyState.canBuild" @click="copyReportBodyRich()">Copy Rich</button>
        <button class="toolbar-btn" type="button" :disabled="!reportBodyState.canBuild" @click="copyReportBodyText()">Copy Text</button>
        <button class="toolbar-btn" type="button" :disabled="!reportBodyState.canBuild" @click="stageReportBodyPreview()">Stage Body</button>
        <button class="toolbar-btn" type="button" :disabled="!reportBodyState.canBuild" @click="downloadReportBodyText()">Download TXT</button>
        <button class="toolbar-btn" type="button" :disabled="!reportBodyState.canBuild" @click="downloadReportBodyHtml()">Download HTML</button>
        <button class="toolbar-btn" type="button" @click="openAddressLegendModal({ view: 'all' })">Review Legend</button>
      </div>
    </div>

    <div v-if="reportBodyStatus" class="network-lookup-modal__status suspicious-address-table-modal__status case-report-body-modal__status">
      {{ reportBodyStatus }}
    </div>

    <div class="case-report-body-modal__content-grid">
      <div class="template-deliverable-modal__preview-card case-report-body-modal__preview-card">
        <div class="template-deliverable-modal__preview-header">
          <div>
            <strong>Rich preview</strong>
            <small>{{ reportBodyActiveVariantLabel }} · {{ reportBodyVisibleSectionCount }} visible section{{ reportBodyVisibleSectionCount === 1 ? '' : 's' }}</small>
          </div>
          <span class="header-badge" :class="{ 'header-badge--active': reportBodyState.builtIsCurrent }">
            {{ reportBodyState.builtIsCurrent ? 'Built current' : 'Not built current' }}
          </span>
        </div>
        <div v-if="!reportBodyState.canBuild" class="network-lookup-modal__empty-state">
          {{ reportBodyState.summary }}
        </div>
        <div v-else-if="!reportBodyActiveHtmlFragment" class="network-lookup-modal__empty-state">
          No report-body preview is available.
        </div>
        <div v-else class="case-report-body-modal__rich-preview" v-html="reportBodyActiveHtmlFragment"></div>
      </div>

      <div class="template-deliverable-modal__preview-card case-report-body-modal__preview-card case-report-body-modal__text-card">
        <div class="template-deliverable-modal__preview-header">
          <div>
            <strong>Text-only output</strong>
            <small>Useful when rich clipboard is unavailable or when the analyst wants plain text.</small>
          </div>
        </div>
        <textarea class="case-report-body-modal__text-preview" readonly :value="reportBodyActivePlainText"></textarea>
      </div>
    </div>

    <div class="template-deliverable-modal__preview-card case-report-body-modal__section-card">
      <div class="template-deliverable-modal__preview-header">
        <div>
          <strong>Section outline</strong>
          <small>{{ reportBodyState.sections.length || 0 }} top-level transaction path section{{ (reportBodyState.sections.length || 0) === 1 ? '' : 's' }}</small>
        </div>
      </div>
      <div class="case-report-body-modal__section-list">
        <div v-for="section in reportBodyVisibleSections" :key="section.key" class="case-report-body-modal__section-row">
          <strong>Transaction Path #{{ section.pathLabel || '—' }}</strong>
          <span>{{ section.entryCount }} entr{{ section.entryCount === 1 ? 'y' : 'ies' }}</span>
          <small>{{ section.previewText }}</small>
        </div>
      </div>
    </div>

    <div class="template-deliverable-modal__completion-card case-report-body-modal__completion-card">
      <div>
        <strong>Review and continue</strong>
        <p>Copy or stage the current report body, then mark reviewed when the layout and wording look right. If Main Data or the address legend changes later, rebuild and review again.</p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !reportBodyState.canBuild || reportBodyState.reviewIsCurrent" @click="runCaseModalMethod('markReportBodyReviewComplete', { title: 'Marking report body reviewed…', message: 'Saving report body review state and staging audit output.' })">
        {{ reportBodyCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" :disabled="!reportBodyState.canBuild" @click="copyReportBodyRich()">Copy Rich</button>
      <button class="toolbar-btn" type="button" :disabled="!reportBodyState.canBuild" @click="copyReportBodyText()">Copy Text</button>
      <button class="toolbar-btn" type="button" :disabled="!reportBodyState.canBuild" @click="stageReportBodyPreview()">Stage Body</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !reportBodyState.canBuild || reportBodyState.reviewIsCurrent" @click="runCaseModalMethod('markReportBodyReviewComplete', { title: 'Marking report body reviewed…', message: 'Saving report body review state and staging audit output.' })">
        {{ reportBodyCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelReportBodyModal">Close</button>
    </div>
  </div>
</div>


<div v-if="showAddendumPathEndingReviewModal && !caseWorkflowReviewAutoUiSuppressed" class="agent-modal-backdrop" @click="cancelAddendumPathEndingReviewModal">
  <div class="agent-modal network-lookup-modal template-deliverable-modal what-went-where-modal addendum-path-ending-modal" @click.stop>
    <div class="network-lookup-modal__header template-deliverable-modal__header addendum-path-ending-modal__header">
      <div>
        <h3>Review Exchange Path-Ending Rows For Addendum</h3>
        <p>
          Tool55-style review of the current What Went Where output. The filter keeps path-ending rows whose Column J notes contain an exchange keyword, excludes victim-owned exchange wording, and reports how many rows were omitted.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close addendum path-ending review" :disabled="addendumPathEndingRefreshRunning" @click="cancelAddendumPathEndingReviewModal">×</button>
    </div>

    <div class="template-deliverable-modal__summary addendum-path-ending-modal__summary">
      <div>
        <strong>{{ addendumPathEndingReviewState.sourceMeaningfulRowCount || 0 }}</strong>
        <small>WWW rows evaluated</small>
      </div>
      <div>
        <strong>{{ addendumPathEndingReviewState.includedCount || 0 }}</strong>
        <small>included for addendum</small>
      </div>
      <div>
        <strong>{{ addendumPathEndingReviewState.omittedCount || 0 }}</strong>
        <small>omitted by criteria</small>
      </div>
      <div>
        <strong>{{ addendumPathEndingReviewState.reviewIsCurrent ? 'Reviewed' : addendumPathEndingReviewState.canReview ? 'Ready' : 'Needs setup' }}</strong>
        <small>{{ addendumPathEndingReviewState.keywordCount || 0 }} exchange keyword{{ (addendumPathEndingReviewState.keywordCount || 0) === 1 ? '' : 's' }}</small>
      </div>
    </div>

    <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': addendumPathEndingReviewState.canReview }">
      <strong>{{ addendumPathEndingReviewState.canReview ? 'Ready to review' : 'Prerequisite needed' }}</strong>
      <span>{{ addendumPathEndingReviewState.summary }}</span>
    </div>

    <div class="addendum-path-ending-modal__reason-grid">
      <article v-for="reason in addendumPathEndingOmittedReasonRows" :key="reason.key">
        <strong>{{ reason.count }}</strong>
        <span>{{ reason.label }}</span>
      </article>
    </div>

    <div class="template-deliverable-modal__toolbar what-went-where-modal__toolbar addendum-path-ending-modal__toolbar">
      <label>
        <span>Show</span>
        <select v-model="addendumPathEndingViewFilter">
          <option value="included">Included rows</option>
          <option value="omitted">All omitted rows</option>
          <option value="no_exchange_keyword">Omitted: no exchange keyword</option>
          <option value="victim_owned_keyword">Omitted: victim-owned wording</option>
          <option value="not_path_ending">Omitted: not path ending</option>
          <option value="duplicate">Omitted: duplicate</option>
          <option value="keyword_list_empty">Omitted: no keywords loaded</option>
          <option value="all">All evaluated rows</option>
        </select>
      </label>
      <label>
        <span>Search</span>
        <input v-model.trim="addendumPathEndingSearch" type="search" placeholder="Search path, hash, keyword, notes, entity…" />
      </label>
      <label class="network-lookup-modal__checkbox addendum-path-ending-modal__checkbox">
        <input v-model="addendumPathEndingRequirePathEnds" type="checkbox" />
        <span>Require Path ends rows</span>
      </label>
      <label class="network-lookup-modal__checkbox addendum-path-ending-modal__checkbox">
        <input v-model="addendumPathEndingDedupe" type="checkbox" />
        <span>Deduplicate filtered rows</span>
      </label>
      <div class="what-went-where-modal__toolbar-actions addendum-path-ending-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" :disabled="addendumPathEndingRefreshRunning" @click="refreshAddendumPathEndingReview()">
          {{ addendumPathEndingRefreshRunning ? 'Refreshing…' : 'Refresh' }}
        </button>
        <button class="toolbar-btn" type="button" :disabled="addendumPathEndingRefreshRunning" @click="refreshAddendumPathEndingReview({ forceRemote: true })">Refresh Keywords</button>
        <button class="toolbar-btn" type="button" :disabled="!addendumPathEndingReviewState.prerequisiteCanBuild || (addendumPathEndingReviewState.includedCount || 0) === 0" @click="stageAddendumPathEndingReviewRows()">Stage Rows</button>
        <button class="toolbar-btn" type="button" :disabled="!addendumPathEndingReviewState.prerequisiteCanBuild" @click="stageAddendumPathEndingReviewAudit()">Stage Audit</button>
        <button class="toolbar-btn" type="button" :disabled="!addendumPathEndingReviewState.prerequisiteCanBuild || (addendumPathEndingReviewState.includedCount || 0) === 0" @click="downloadAddendumPathEndingReviewTsv()">Download TSV</button>
      </div>
    </div>

    <div v-if="addendumPathEndingStatus" class="network-lookup-modal__status suspicious-address-table-modal__status addendum-path-ending-modal__status">
      {{ addendumPathEndingStatus }}
    </div>

    <div class="template-deliverable-modal__preview-card suspicious-address-table-modal__preview-card what-went-where-modal__preview-card addendum-path-ending-modal__preview-card">
      <div class="template-deliverable-modal__preview-header">
        <div>
          <strong>Filtered addendum candidates</strong>
          <small>{{ addendumPathEndingVisibleRows.length }} visible row{{ addendumPathEndingVisibleRows.length === 1 ? '' : 's' }}</small>
        </div>
        <span class="header-badge" :class="{ 'header-badge--active': addendumPathEndingReviewState.reviewIsCurrent }">
          {{ addendumPathEndingReviewState.reviewIsCurrent ? 'Review current' : 'Review needed' }}
        </span>
      </div>

      <div v-if="!addendumPathEndingReviewState.prerequisiteCanBuild" class="network-lookup-modal__empty-state">
        {{ addendumPathEndingReviewState.summary }}
      </div>
      <div v-else-if="addendumPathEndingVisibleRows.length === 0" class="network-lookup-modal__empty-state">
        No rows match the current filter. Try showing all evaluated rows or refresh the exchange keyword list.
      </div>
      <div v-else class="suspicious-address-table-modal__table-wrap what-went-where-modal__table-wrap addendum-path-ending-modal__table-wrap">
        <table class="suspicious-address-table-modal__table what-went-where-modal__table addendum-path-ending-table">
          <thead>
            <tr>
              <th>WWW Row</th>
              <th>Status</th>
              <th>Reason</th>
              <th>Path</th>
              <th>Transaction Hash</th>
              <th>To / Address</th>
              <th>Amount</th>
              <th>Token</th>
              <th>Matched Keywords</th>
              <th>Column J Notes</th>
              <th>Column Q</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in addendumPathEndingVisibleRows"
              :key="row.key"
              :class="[`addendum-path-ending-modal__row--${row.status}`, row.isPathEnding ? 'addendum-path-ending-modal__row--path-ending' : 'addendum-path-ending-modal__row--not-path-ending']"
            >
              <td>{{ row.sourceRowNumber }}</td>
              <td>{{ row.statusLabel }}</td>
              <td>{{ row.reasonLabel }}</td>
              <td>{{ row.pathLabel || '—' }}</td>
              <td class="network-lookup-table__mono">{{ row.transactionHash || '—' }}</td>
              <td class="network-lookup-table__mono">{{ row.toAddress || row.fromAddress || '—' }}</td>
              <td>{{ row.amount || '—' }}</td>
              <td>{{ row.token || '—' }}</td>
              <td>{{ row.matchedKeywordsText || '—' }}</td>
              <td>{{ row.notes || '—' }}</td>
              <td>{{ row.continuesAs || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="path-operation-modal__completion-card suspicious-address-table-modal__completion-card what-went-where-modal__completion-card addendum-path-ending-modal__completion-card">
      <div>
        <strong>Finished reviewing addendum path-ending rows?</strong>
        <p>Stage or download the included rows if useful, then mark reviewed when the Tool55-style filter and omission counts look right. If Main Data or What Went Where changes later, this review becomes stale.</p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !addendumPathEndingReviewState.canReview || addendumPathEndingReviewState.reviewIsCurrent" @click="runCaseModalMethod('markAddendumPathEndingReviewComplete', { title: 'Marking addendum path endings reviewed…', message: 'Saving included path-ending row review state.' })">
        {{ addendumPathEndingCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="openWhatWentWhereModal({ view: 'path_endings' })">Review WWW Path Endings</button>
      <button class="toolbar-btn" type="button" :disabled="!addendumPathEndingReviewState.prerequisiteCanBuild || (addendumPathEndingReviewState.includedCount || 0) === 0" @click="stageAddendumPathEndingReviewRows()">Stage Rows</button>
      <button class="toolbar-btn" type="button" :disabled="!addendumPathEndingReviewState.reviewIsCurrent" @click="openAddendumRecipientEntityModal()">Resolve Entities</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !addendumPathEndingReviewState.canReview || addendumPathEndingReviewState.reviewIsCurrent" @click="runCaseModalMethod('markAddendumPathEndingReviewComplete', { title: 'Marking addendum path endings reviewed…', message: 'Saving included path-ending row review state.' })">
        {{ addendumPathEndingCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="addendumPathEndingRefreshRunning" @click="cancelAddendumPathEndingReviewModal">Close</button>
    </div>
  </div>
</div>

<div v-if="showAddendumRecipientEntityModal && !caseWorkflowReviewAutoUiSuppressed" class="agent-modal-backdrop" @click="cancelAddendumRecipientEntityModal">
  <div class="agent-modal network-lookup-modal template-deliverable-modal what-went-where-modal addendum-recipient-modal" @click.stop>
    <div class="network-lookup-modal__header template-deliverable-modal__header addendum-recipient-modal__header">
      <div>
        <h3>Resolve Recipient Entity For Addendum Rows</h3>
        <p>
          Tool48-style review of the current addendum path-ending candidates. Rows with one matched exchange are auto-resolved; rows with multiple matches need one recipient entity selected before the addendum can be confirmed.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close addendum recipient entity review" :disabled="addendumRecipientRefreshRunning" @click="cancelAddendumRecipientEntityModal">×</button>
    </div>

    <div class="template-deliverable-modal__summary addendum-recipient-modal__summary">
      <div>
        <strong>{{ addendumRecipientEntityReviewState.totalCandidateCount || 0 }}</strong>
        <small>candidate row{{ (addendumRecipientEntityReviewState.totalCandidateCount || 0) === 1 ? '' : 's' }}</small>
      </div>
      <div>
        <strong>{{ addendumRecipientEntityReviewState.resolvedCount || 0 }}</strong>
        <small>resolved</small>
      </div>
      <div>
        <strong>{{ addendumRecipientEntityReviewState.needsChoiceCount || 0 }}</strong>
        <small>need choice</small>
      </div>
      <div>
        <strong>{{ addendumRecipientEntityReviewState.autoResolvedCount || 0 }}</strong>
        <small>auto-resolved</small>
      </div>
    </div>

    <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': addendumRecipientEntityReviewState.buildIsCurrent && (addendumRecipientEntityReviewState.needsChoiceCount || 0) === 0 }">
      <strong>{{ addendumRecipientEntityReviewState.reviewIsCurrent ? 'Review current' : addendumRecipientEntityReviewState.buildIsCurrent ? 'Ready to resolve' : 'Prerequisite needed' }}</strong>
      <span>{{ addendumRecipientEntityReviewState.summary }}</span>
    </div>

    <div class="template-deliverable-modal__toolbar what-went-where-modal__toolbar addendum-recipient-modal__toolbar">
      <label>
        <span>Show</span>
        <select v-model="addendumRecipientViewFilter">
          <option value="needs_choice">Needs choice</option>
          <option value="unresolved">All unresolved</option>
          <option value="resolved">Resolved</option>
          <option value="auto_resolved">Auto-resolved</option>
          <option value="selected">Saved selections</option>
          <option value="no_options">No options</option>
          <option value="all">All candidates</option>
        </select>
      </label>
      <label>
        <span>Search</span>
        <input v-model.trim="addendumRecipientSearch" type="search" placeholder="Search path, hash, address, entity, notes…" />
      </label>
      <div class="what-went-where-modal__toolbar-actions addendum-recipient-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" :disabled="addendumRecipientRefreshRunning" @click="refreshAddendumRecipientEntityReview()">
          {{ addendumRecipientRefreshRunning ? 'Refreshing…' : 'Refresh' }}
        </button>
        <button class="toolbar-btn" type="button" :disabled="!addendumRecipientEntityReviewState.buildIsCurrent || addendumRecipientVisibleRows.length === 0" @click="saveVisibleAddendumRecipientEntityChoices()">Save Visible Choices</button>
        <button class="toolbar-btn" type="button" :disabled="!addendumRecipientEntityReviewState.pathEndingReviewCanReview || (addendumRecipientEntityReviewState.resolvedCount || 0) === 0" @click="stageAddendumRecipientEntityRows()">Stage Resolved Rows</button>
        <button class="toolbar-btn" type="button" @click="stageAddendumRecipientEntityAudit()">Stage Audit</button>
        <button class="toolbar-btn" type="button" :disabled="!addendumRecipientEntityReviewState.buildIsCurrent || (addendumRecipientEntityReviewState.resolvedCount || 0) === 0" @click="downloadAddendumRecipientEntityTsv()">Download TSV</button>
      </div>
    </div>

    <div v-if="addendumRecipientStatus" class="network-lookup-modal__status suspicious-address-table-modal__status addendum-recipient-modal__status">
      {{ addendumRecipientStatus }}
    </div>

    <div class="template-deliverable-modal__preview-card suspicious-address-table-modal__preview-card what-went-where-modal__preview-card addendum-recipient-modal__preview-card">
      <div class="template-deliverable-modal__preview-header">
        <div>
          <strong>Recipient entity candidates</strong>
          <small>{{ addendumRecipientVisibleRows.length }} visible row{{ addendumRecipientVisibleRows.length === 1 ? '' : 's' }}</small>
        </div>
        <span class="header-badge" :class="{ 'header-badge--active': addendumRecipientEntityReviewState.reviewIsCurrent }">
          {{ addendumRecipientEntityReviewState.reviewIsCurrent ? 'Review current' : 'Review needed' }}
        </span>
      </div>

      <div v-if="!addendumRecipientEntityReviewState.pathEndingReviewCanReview" class="network-lookup-modal__empty-state">
        {{ addendumRecipientEntityReviewState.summary }}
      </div>
      <div v-else-if="addendumRecipientVisibleRows.length === 0" class="network-lookup-modal__empty-state">
        No addendum recipient rows match the current filter.
      </div>
      <div v-else class="suspicious-address-table-modal__table-wrap what-went-where-modal__table-wrap addendum-recipient-modal__table-wrap">
        <table class="suspicious-address-table-modal__table what-went-where-modal__table addendum-recipient-table">
          <thead>
            <tr>
              <th>WWW Row</th>
              <th>Status</th>
              <th>Path</th>
              <th>Transaction Hash</th>
              <th>To Address</th>
              <th>Candidate Entities</th>
              <th>Resolved Entity</th>
              <th>Choice</th>
              <th>Actions</th>
              <th>Column J Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in addendumRecipientVisibleRows"
              :key="row.reviewKey"
              :class="[
                row.resolvedEntityName ? 'addendum-recipient-modal__row--resolved' : 'addendum-recipient-modal__row--needs-choice',
                row.isAutoResolved ? 'addendum-recipient-modal__row--auto' : ''
              ]"
            >
              <td>{{ row.sourceRowNumber || '—' }}</td>
              <td>
                <span v-if="row.isAutoResolved">Auto-resolved</span>
                <span v-else-if="row.resolvedEntityName">Selected</span>
                <span v-else-if="row.needsUserChoice">Needs choice</span>
                <span v-else>No options</span>
              </td>
              <td>{{ row.effectivePathLabel || '—' }}</td>
              <td class="network-lookup-table__mono">{{ row.effectiveTransactionHash || '—' }}</td>
              <td class="network-lookup-table__mono">{{ row.toAddress || row.effectiveFromAddress || '—' }}</td>
              <td>{{ row.candidateEntityNamesText || '—' }}</td>
              <td>{{ row.resolvedEntityName || '—' }}</td>
              <td>
                <select
                  :value="getAddendumRecipientDraftValue(row)"
                  :disabled="row.isAutoResolved || (row.candidateEntityNames || []).length <= 1"
                  @change="setAddendumRecipientDraftValue(row.reviewKey, $event.target.value)"
                >
                  <option value="">Select entity…</option>
                  <option v-for="entity in row.candidateEntityNames" :key="`${row.reviewKey}-${entity}`" :value="entity">{{ entity }}</option>
                </select>
              </td>
              <td class="addendum-recipient-modal__row-actions">
                <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="row.isAutoResolved || !getAddendumRecipientDraftValue(row)" @click="saveAddendumRecipientEntityChoice(row, getAddendumRecipientDraftValue(row))">Save</button>
                <button class="toolbar-btn toolbar-btn--small toolbar-btn--ghost" type="button" :disabled="row.isAutoResolved || !row.resolvedEntityName" @click="clearAddendumRecipientEntityChoice(row)">Clear</button>
              </td>
              <td>{{ row.notes || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="path-operation-modal__completion-card suspicious-address-table-modal__completion-card what-went-where-modal__completion-card addendum-recipient-modal__completion-card">
      <div>
        <strong>Finished resolving addendum recipient entities?</strong>
        <p>Confirm after every multi-match row has exactly one selected entity. This records the current Tool48-style review state and stages an audit in Output without modifying Main Data.</p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !addendumRecipientEntityReviewState.buildIsCurrent || (addendumRecipientEntityReviewState.noCandidateEntityCount || 0) > 0 || (addendumRecipientEntityReviewState.needsChoiceCount || 0) > 0 || addendumRecipientEntityReviewState.reviewIsCurrent" @click="runCaseModalMethod('markAddendumRecipientEntityReviewComplete', { title: 'Marking addendum recipients reviewed…', message: 'Saving recipient-entity decisions for the current addendum rows.' })">
        {{ addendumRecipientCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="openAddendumPathEndingReviewModal({ view: 'included' })">Review Path-Ending Rows</button>
      <button class="toolbar-btn" type="button" :disabled="!addendumRecipientEntityReviewState.pathEndingReviewCanReview || (addendumRecipientEntityReviewState.resolvedCount || 0) === 0" @click="stageAddendumRecipientEntityRows()">Stage Resolved Rows</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !addendumRecipientEntityReviewState.buildIsCurrent || (addendumRecipientEntityReviewState.noCandidateEntityCount || 0) > 0 || (addendumRecipientEntityReviewState.needsChoiceCount || 0) > 0 || addendumRecipientEntityReviewState.reviewIsCurrent" @click="runCaseModalMethod('markAddendumRecipientEntityReviewComplete', { title: 'Marking addendum recipients reviewed…', message: 'Saving recipient-entity decisions for the current addendum rows.' })">
        {{ addendumRecipientCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="addendumRecipientRefreshRunning" @click="cancelAddendumRecipientEntityModal">Close</button>
    </div>
  </div>
</div>

<div v-if="showCaseAddendumModal && !caseWorkflowReviewAutoUiSuppressed" class="agent-modal-backdrop" @click="cancelCaseAddendumModal">
  <div class="agent-modal network-lookup-modal template-deliverable-modal case-addendum-modal" @click.stop>
    <div class="network-lookup-modal__header template-deliverable-modal__header case-addendum-modal__header">
      <div>
        <h3>Build And Review Addendum</h3>
        <p>
          Tool18E-style addendum build from the current Tool55 path-ending review and Tool48 recipient-entity review. This formats the structured addendum, narrative addendum, and audit output without modifying Main Data.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close addendum build review" :disabled="caseAddendumRefreshRunning" @click="cancelCaseAddendumModal">×</button>
    </div>

    <div class="template-deliverable-modal__summary case-addendum-modal__summary">
      <div>
        <strong>{{ caseAddendumState.resolvedRowCount || 0 }}</strong>
        <small>rendered row{{ (caseAddendumState.resolvedRowCount || 0) === 1 ? '' : 's' }}</small>
      </div>
      <div>
        <strong>{{ caseAddendumState.sectionCount || 0 }}</strong>
        <small>recipient section{{ (caseAddendumState.sectionCount || 0) === 1 ? '' : 's' }}</small>
      </div>
      <div>
        <strong>{{ caseAddendumState.legendEntryCount || 0 }}</strong>
        <small>legend entr{{ (caseAddendumState.legendEntryCount || 0) === 1 ? 'y' : 'ies' }}</small>
      </div>
      <div>
        <strong>{{ caseAddendumState.reviewIsCurrent ? 'Reviewed' : caseAddendumState.canBuild ? 'Ready' : 'Needs setup' }}</strong>
        <small>{{ caseAddendumActiveVariantLabel }}</small>
      </div>
    </div>

    <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': caseAddendumState.canBuild }">
      <strong>{{ caseAddendumState.reviewIsCurrent ? 'Review current' : caseAddendumState.canBuild ? 'Ready to review' : 'Prerequisite needed' }}</strong>
      <span>{{ caseAddendumState.summary }}</span>
    </div>

    <div class="template-deliverable-modal__toolbar case-addendum-modal__toolbar">
      <label>
        <span>Preview</span>
        <select v-model="caseAddendumActiveVariant">
          <option value="tool18e_structured">Tool18E structured addendum</option>
          <option value="addendum_narrative">Narrative addendum</option>
        </select>
      </label>
      <label>
        <span>Search sections</span>
        <input v-model.trim="caseAddendumSearch" type="search" placeholder="Search entity, path, hash, address, paragraph…" />
      </label>
      <label class="network-lookup-modal__checkbox case-addendum-modal__checkbox">
        <input v-model="caseAddendumAggregateDuplicateHashes" type="checkbox" @change="refreshCaseAddendumReview({ silent: true })" />
        <span>Condense duplicate transaction hashes</span>
      </label>
      <div class="case-addendum-modal__toolbar-actions">
        <button class="toolbar-btn" type="button" :disabled="caseAddendumRefreshRunning" @click="refreshCaseAddendumReview()">
          {{ caseAddendumRefreshRunning ? 'Refreshing…' : 'Refresh' }}
        </button>
        <button class="toolbar-btn" type="button" :disabled="!caseAddendumState.canBuild" @click="copyCaseAddendumRich()">Copy Rich</button>
        <button class="toolbar-btn" type="button" :disabled="!caseAddendumState.canBuild" @click="copyCaseAddendumText()">Copy Text</button>
        <button class="toolbar-btn" type="button" :disabled="!caseAddendumState.canBuild" @click="stageCaseAddendumPreview()">Stage Preview</button>
        <button class="toolbar-btn" type="button" :disabled="!caseAddendumState.canBuild" @click="stageCaseAddendumAudit()">Stage Audit</button>
        <button class="toolbar-btn" type="button" :disabled="!caseAddendumState.canBuild" @click="downloadCaseAddendumText()">Download Text</button>
        <button class="toolbar-btn" type="button" :disabled="!caseAddendumState.canBuild" @click="downloadCaseAddendumHtml()">Download HTML</button>
        <button class="toolbar-btn" type="button" :disabled="!caseAddendumState.canBuild" @click="downloadCaseAddendumAuditTsv()">Audit TSV</button>
      </div>
    </div>

    <div v-if="caseAddendumStatus" class="network-lookup-modal__status case-addendum-modal__status">
      {{ caseAddendumStatus }}
    </div>

    <div class="case-addendum-modal__content-grid">
      <section class="template-deliverable-modal__preview-card case-addendum-modal__preview-card">
        <div class="template-deliverable-modal__preview-header">
          <div>
            <strong>{{ caseAddendumActiveVariantLabel }}</strong>
            <small>Plain text preview</small>
          </div>
          <span class="header-badge" :class="{ 'header-badge--active': caseAddendumState.reviewIsCurrent }">
            {{ caseAddendumState.reviewIsCurrent ? 'Review current' : 'Review needed' }}
          </span>
        </div>
        <pre class="case-addendum-modal__text-preview">{{ caseAddendumActivePlainText }}</pre>
      </section>

      <section class="template-deliverable-modal__preview-card case-addendum-modal__preview-card">
        <div class="template-deliverable-modal__preview-header">
          <div>
            <strong>Formatted preview</strong>
            <small>Rich HTML preview for copy/export</small>
          </div>
        </div>
        <div v-if="caseAddendumActiveHtmlFragment" class="case-addendum-modal__rich-preview" v-html="caseAddendumActiveHtmlFragment"></div>
        <div v-else class="network-lookup-modal__empty-state">
          Resolve addendum prerequisites to see the formatted preview.
        </div>
      </section>
    </div>

    <div class="template-deliverable-modal__preview-card case-addendum-modal__sections-card">
      <div class="template-deliverable-modal__preview-header">
        <div>
          <strong>Recipient sections</strong>
          <small>{{ caseAddendumVisibleSections.length }} visible of {{ caseAddendumState.sectionCount || 0 }}</small>
        </div>
      </div>
      <div v-if="!caseAddendumState.canBuild" class="network-lookup-modal__empty-state">
        {{ caseAddendumState.summary }}
      </div>
      <div v-else-if="caseAddendumVisibleSections.length === 0" class="network-lookup-modal__empty-state">
        No addendum sections match the current search.
      </div>
      <div v-else class="case-addendum-modal__section-list">
        <article v-for="section in caseAddendumVisibleSections" :key="section.key" class="case-addendum-modal__section-row">
          <div>
            <strong>{{ section.entityName }}</strong>
            <span>{{ section.jurisdiction || 'Unknown jurisdiction' }}</span>
          </div>
          <small>{{ section.rowCount }} row{{ section.rowCount === 1 ? '' : 's' }} · {{ section.pathLabelsText || 'No path labels' }}</small>
          <p>{{ section.previewText }}</p>
        </article>
      </div>
    </div>

    <div class="template-deliverable-modal__completion-card case-addendum-modal__completion-card">
      <div>
        <strong>Review and continue</strong>
        <p>Mark reviewed after the structured addendum, narrative, recipient sections, and audit basis look right. Later changes to Main Data, Tool55, Tool48, or the address legend will make this review stale.</p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !caseAddendumState.canBuild || caseAddendumState.reviewIsCurrent" @click="runCaseModalMethod('markCaseAddendumReviewComplete', { title: 'Marking addendum reviewed…', message: 'Saving addendum review state and staging audit output.' })">
        {{ caseAddendumCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="openAddendumRecipientEntityModal({ view: 'resolved' })">Review Recipient Entities</button>
      <button class="toolbar-btn" type="button" :disabled="!caseAddendumState.canBuild" @click="stageCaseAddendumPreview()">Stage Addendum</button>
      <button class="toolbar-btn" type="button" :disabled="!caseAddendumState.canBuild" @click="stageCaseAddendumAudit()">Stage Audit</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !caseAddendumState.canBuild || caseAddendumState.reviewIsCurrent" @click="runCaseModalMethod('markCaseAddendumReviewComplete', { title: 'Marking addendum reviewed…', message: 'Saving addendum review state and staging audit output.' })">
        {{ caseAddendumCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseAddendumRefreshRunning" @click="cancelCaseAddendumModal">Close</button>
    </div>
  </div>
</div>


<div
  v-if="showCaseInvestigationVisualizerModal"
  class="agent-modal-backdrop case-flow-visuals-backdrop case-investigation-visualizer-backdrop"
  :class="{ 'case-investigation-visualizer-backdrop--fullscreen': caseInvestigationVisualizerFullscreen }"
  @click="requestCaseInvestigationVisualizerBackdropClose"
>
  <div
    ref="caseInvestigationVisualizerModal"
    class="agent-modal network-lookup-modal template-deliverable-modal case-flow-visuals-modal case-investigation-visualizer-modal"
    tabindex="-1"
    :class="{ 'case-investigation-visualizer-modal--fullscreen': caseInvestigationVisualizerFullscreen, 'case-investigation-visualizer-modal--side-resizing': caseInvestigationVisualizerSidePanelResizing, 'case-investigation-visualizer-modal--manual-bottom-resizing': caseManualSolveBottomPanelResizing, 'case-investigation-visualizer-modal--manual-start': caseInvestigationVisualizerManualStartMode, 'case-investigation-visualizer-modal--quick-lookup': caseManualStartQuickLookupMode }"
    :style="caseInvestigationVisualizerModalStyle"
    @click.stop
  >
    <div class="network-lookup-modal__header template-deliverable-modal__header case-flow-visuals-modal__header case-investigation-visualizer-modal__header">
      <div>
        <h3>Investigate Case Visually</h3>
        <p>
          Preview partial graph directly from Main Data. This view keeps the data raw and interactive: no report cleanup, no automatic merge polishing, and no deliverable review signal.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close investigation visualizer" :disabled="caseInvestigationVisualizerDownloading || caseInvestigationVisualizerInteractionLocked" @click="cancelCaseInvestigationVisualizer">×</button>
    </div>

    <div v-if="!caseManualStartQuickLookupMode" class="template-deliverable-modal__summary case-flow-visuals-modal__summary case-investigation-visualizer-modal__summary">
      <div>
        <strong>{{ caseInvestigationVisualizerStateView.qualifyingFlowCount || caseInvestigationVisualizerStateView.flowCount || 0 }}</strong>
        <small>visualizable graph{{ (caseInvestigationVisualizerStateView.qualifyingFlowCount || caseInvestigationVisualizerStateView.flowCount || 0) === 1 ? '' : 's' }}</small>
      </div>
      <div>
        <strong>{{ caseInvestigationVisualizerStateView.entryCount || 0 }}</strong>
        <small>transaction entries</small>
      </div>
   
      <div>
        <strong>{{ caseInvestigationVisualizerViewMode === 'single' ? 'Single' : caseInvestigationVisualizerViewMode === 'stacked' ? 'Stacked' : 'Together' }}</strong>
        <small>investigation view</small>
      </div>
    </div>

    <div v-if="!caseManualStartQuickLookupMode" class="template-deliverable-modal__notice case-investigation-visualizer-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': caseInvestigationVisualizerStateView.canBuild }">
      <strong>{{ caseInvestigationVisualizerStateView.canBuild ? 'Ready for visual investigation' : 'Graph needed' }}</strong>
      <span>{{ caseInvestigationVisualizerStateView.summary }}</span>
    </div>

    <div class="template-deliverable-modal__toolbar case-flow-visuals-modal__toolbar case-investigation-visualizer-modal__toolbar">
      <div class="case-flow-visuals-modal__nav-actions case-investigation-visualizer-modal__mode-actions">
        <label class="case-investigation-visualizer-modal__select-label">
          <span>View</span>
          <select v-model="caseInvestigationVisualizerViewMode" :disabled="caseInvestigationVisualizerDownloading || caseInvestigationVisualizerInteractionLocked" @change="refreshCurrentCaseInvestigationVisualizer">
            <option value="single">Selected graph</option>
            <option value="allTogether">All qualifying together</option>
            <option value="stacked">All qualifying stacked</option>
          </select>
        </label>
        <button class="toolbar-btn" type="button" :disabled="!caseInvestigationVisualizerCanGoPrevious || caseInvestigationVisualizerDownloading || caseInvestigationVisualizerInteractionLocked" @click="showPreviousCaseInvestigationVisual">← Previous</button>
        <strong>{{ caseInvestigationVisualizerCurrentLabel }}</strong>
        <button class="toolbar-btn" type="button" :disabled="!caseInvestigationVisualizerCanGoNext || caseInvestigationVisualizerDownloading || caseInvestigationVisualizerInteractionLocked" @click="showNextCaseInvestigationVisual">Next →</button>
      </div>
      <div class="case-flow-visuals-modal__download-actions">
        <button class="toolbar-btn" type="button" :disabled="caseInvestigationVisualizerRefreshRunning || caseInvestigationVisualizerDownloading || caseInvestigationVisualizerInteractionLocked" @click="refreshCaseInvestigationVisualizer({ preserveFlow: true })">
          {{ caseInvestigationVisualizerRefreshRunning ? 'Refreshing…' : 'Refresh' }}
        </button>
        <button
          v-if="!caseInvestigationVisualizerManualStartMode && caseManualStartVisualizerReady"
          class="toolbar-btn toolbar-btn--accent"
          type="button"
          :disabled="caseManualStartVisualizerRunning || caseInvestigationVisualizerDownloading || caseInvestigationVisualizerInteractionLocked"
          :title="caseManualStartVisualizerTitle"
          @click="switchCaseInvestigationVisualizerToManualStartMode"
        >
          Manual solve mode
        </button>
        <button
          v-if="caseInvestigationVisualizerManualStartMode && caseInvestigationVisualizerReadyState?.canOpen"
          class="toolbar-btn toolbar-btn--ghost"
          type="button"
          :disabled="caseInvestigationVisualizerDownloading || caseInvestigationVisualizerInteractionLocked"
          title="Return to the standard partial-case visualizer with prune/graft/bridge-graft controls."
          @click="switchCaseInvestigationVisualizerToStandardMode"
        >
          Standard visual actions
        </button>
        <button class="toolbar-btn" type="button" :disabled="!caseInvestigationVisualizerCurrentVisual || caseInvestigationVisualizerDownloading || caseInvestigationVisualizerInteractionLocked" @click="downloadCurrentCaseInvestigationVisualizerSvg">Current SVG</button>
        <button
          class="toolbar-btn toolbar-btn--ghost case-investigation-visualizer-modal__fullscreen-btn"
          :class="{ 'is-active': caseInvestigationVisualizerFullscreen }"
          type="button"
          :disabled="!caseInvestigationVisualizerCurrentVisual || caseInvestigationVisualizerInteractionLocked"
          @click="toggleCaseInvestigationVisualizerFullscreen"
        >
          <span aria-hidden="true">{{ caseInvestigationVisualizerFullscreen ? '↙' : '⛶' }}</span>
          {{ caseInvestigationVisualizerFullscreen ? 'Exit full screen' : 'Full screen' }}
        </button>
      </div>
    </div>

    <div v-if="!caseManualStartQuickLookupMode" class="case-flow-visuals-modal__download-note case-investigation-visualizer-modal__download-note">
      {{ caseInvestigationVisualizerDownloadSummary }}
    </div>

    <div v-if="caseManualStartQuickLookupStatusVisible" class="network-lookup-modal__status case-flow-visuals-modal__status case-investigation-visualizer-modal__status">
      {{ caseInvestigationVisualizerStatus }}
    </div>


    <div
      class="case-flow-visuals-modal__content case-investigation-visualizer-modal__content"
      :class="{ 'is-side-resizing': caseInvestigationVisualizerSidePanelResizing }"
    >
      <section class="case-flow-visuals-modal__viewport-card case-investigation-visualizer-modal__viewport-card">
        <div class="template-deliverable-modal__preview-header case-investigation-visualizer-modal__viewport-toolbar">
          <div class="case-investigation-visualizer-modal__viewport-title">
            <strong>{{ caseInvestigationVisualizerCurrentVisual ? (caseInvestigationVisualizerCurrentVisual.mainLabel || 'Investigation visual') : 'Investigation visual' }}</strong>
            <small>{{ caseInvestigationVisualizerViewportHelpText }}</small>
          </div>
          <div class="case-flow-visuals-modal__mini-actions case-investigation-visualizer-modal__viewport-actions">
            <button
              v-if="caseInvestigationVisualizerManualStartMode"
              class="toolbar-btn toolbar-btn--ghost"
              :class="{ 'toolbar-btn--active': caseManualSolveVisualLegendOpen }"
              type="button"
              :disabled="!caseInvestigationVisualizerCurrentVisual"
              aria-controls="case-manual-solve-visual-legend-panel"
              :aria-expanded="caseManualSolveVisualLegendOpen ? 'true' : 'false'"
              title="Show or hide the Manual Solve visual legend."
              @click="toggleCaseManualSolveVisualLegend"
            >
              Legend
            </button>
            <button
              v-if="caseInvestigationVisualizerManualStartMode && !caseManualStartQuickLookupMode"
              class="toolbar-btn toolbar-btn--ghost"
              :class="{ 'toolbar-btn--active': caseManualSolveEvaluationOpen }"
              type="button"
              :disabled="!caseInvestigationVisualizerCurrentVisual"
              title="Evaluate the amount currently followed across this Manual Solve flow's furthest endpoints."
              @click="toggleCaseManualSolveEvaluationPanel"
            >
              Evaluate
            </button>
            <button
              v-if="caseInvestigationVisualizerManualStartMode && !caseManualStartQuickLookupMode"
              class="toolbar-btn toolbar-btn--accent case-manual-solve-get-data__toolbar-button"
              :class="{ 'toolbar-btn--active': caseManualSolveGetDataPanelOpen }"
              type="button"
              :disabled="!caseInvestigationVisualizerCurrentVisual"
              title="Open a movable panel with raw-data-shaped TSV for the current Manual Solve viewport."
              @click="openCaseManualSolveGetDataPanel"
            >
              GET DATA
            </button>
            <button v-if="!caseManualStartQuickLookupMode" class="toolbar-btn toolbar-btn--monitoring" type="button" :disabled="!caseInvestigationVisualizerCurrentVisual" title="Open Address Monitoring for this case without leaving the visualizer." @click="openCaseAddressMonitoringModal">Monitoring</button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseInvestigationVisualizerCurrentVisual || caseInvestigationVisualizerInteractionLocked" @click="zoomCaseInvestigationVisualizer(0.82)">Zoom in</button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseInvestigationVisualizerCurrentVisual || caseInvestigationVisualizerInteractionLocked" @click="zoomCaseInvestigationVisualizer(1.18)">Zoom out</button>
            <button v-if="caseInvestigationVisualizerManualStartMode" class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseInvestigationVisualizerCurrentVisual" title="Copy Manual Solve viewport debug details for troubleshooting layout, node, edge, and row issues." @click="copyCaseManualStartViewportDebugSnapshot">Debug</button>
            <button v-if="caseInvestigationVisualizerManualStartMode" class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!caseManualStartNextCanRun || caseInvestigationVisualizerInteractionLocked || caseManualStartViewportAutoActive || caseManualStartAllFlowsAutoActive" @click="runCaseManualStartNext">Next</button>
            <button
              v-if="caseInvestigationVisualizerManualStartMode"
              class="toolbar-btn"
              :class="{ 'toolbar-btn--danger': caseManualStartViewportAutoActive, 'toolbar-btn--accent': !caseManualStartViewportAutoActive }"
              type="button"
              :disabled="caseManualStartViewportAutoButtonDisabled"
              :title="caseManualStartViewportAutoButtonTitle"
              @click="toggleCaseManualStartViewportAuto"
            >
              {{ caseManualStartViewportAutoButtonLabel }}
            </button>
            <button v-if="caseInvestigationVisualizerManualStartMode" class="toolbar-btn toolbar-btn--ghost" :class="{ 'toolbar-btn--active': caseManualStartSettingsOpen }" type="button" :disabled="caseInvestigationVisualizerInteractionLocked" @click="openCaseManualStartSettings">Settings</button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseInvestigationVisualizerCurrentVisual || caseInvestigationVisualizerInteractionLocked" @click="resetCaseInvestigationVisualizerViewport">Fit</button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseInvestigationVisualizerCurrentVisual || caseInvestigationVisualizerInteractionLocked" @click="resetCaseInvestigationVisualizerNodePositions">Reset nodes</button>
            <button
              class="toolbar-btn toolbar-btn--ghost"
              :class="{ 'toolbar-btn--active': caseInvestigationVisualizerTransactionOnlyMode }"
              type="button"
              :disabled="!caseInvestigationVisualizerTransactionOnlyCanToggle || caseInvestigationVisualizerInteractionLocked"
              :title="caseInvestigationVisualizerTransactionOnlyTitle"
              @click="toggleCaseInvestigationVisualizerTransactionOnlyMode"
            >
              {{ caseInvestigationVisualizerTransactionOnlyMode ? 'Full graph' : 'Tx only' }}
            </button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseInvestigationVisualizerCanUndoVisualEdit || caseInvestigationVisualizerInteractionLocked" @click="undoCaseInvestigationVisualizerVisualEdit">Undo</button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseInvestigationVisualizerCanRedoVisualEdit || caseInvestigationVisualizerInteractionLocked" @click="redoCaseInvestigationVisualizerVisualEdit">Redo</button>
            <button
              class="toolbar-btn toolbar-btn--ghost"
              :class="{ 'toolbar-btn--danger': caseInvestigationVisualizerManualStartMode }"
              type="button"
              :disabled="!caseInvestigationVisualizerSelectedCount || caseInvestigationVisualizerInteractionLocked || (caseInvestigationVisualizerManualStartMode && (caseManualStartViewportAutoActive || caseManualStartAllFlowsAutoActive))"
              :title="caseInvestigationVisualizerManualStartMode ? 'Open a confirmed Main Data deletion preview for the selected Manual Solve branch.' : 'Visually hide the selected branch in this standard viewport only. Main Data is not changed.'"
              @click="deleteSelectedCaseInvestigationVisualizerBranches"
            >
              {{ caseInvestigationVisualizerManualStartMode ? 'Delete' : 'Delete branch' }}
            </button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseInvestigationVisualizerSelectedCount || caseInvestigationVisualizerInteractionLocked" @click="clearCaseInvestigationVisualizerSelection">Clear selection</button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseInvestigationVisualizerHiddenNodeIds.length || caseInvestigationVisualizerInteractionLocked" @click="restoreCaseInvestigationVisualizerHiddenNodes">Restore hidden</button>
          </div>
        </div>

        <div
          ref="caseInvestigationVisualizerViewportEl"
          class="case-flow-visuals-viewport case-investigation-visualizer-viewport"
          :class="{
            'is-panning': caseInvestigationVisualizerPointer.active,
            'is-camera-moving': caseInvestigationVisualizerCameraMoving,
            'is-selecting': caseInvestigationVisualizerSelectingNodes,
            'is-dragging-nodes': caseInvestigationVisualizerDraggingNodes,
            'is-snap-guided': caseInvestigationVisualizerSnapGuide.active,
            'has-selection': caseInvestigationVisualizerSelectedCount > 0,
            'is-hop-solver-locked': caseInvestigationVisualizerInteractionLocked,
            'is-manual-complete-glow': caseManualStartViewportCompletionBorderGlowActive,
            'is-manual-work-active': caseManualStartViewportWorkIndicatorActive,
            'is-manual-heat-map': caseManualSolveHeatMapActive,
            'has-manual-heat-map-errors': caseManualSolveHeatMapActive && caseManualSolveHeatMapState.errorCount > 0
          }"
        >
          <svg
            ref="caseInvestigationVisualizerSvg"
            class="case-flow-visuals-svg case-investigation-visualizer-svg"
            xmlns="http://www.w3.org/2000/svg"
            :viewBox="caseInvestigationVisualizerViewBoxString"
            @wheel.prevent="onCaseInvestigationVisualizerWheel"
            @pointerdown="onCaseInvestigationVisualizerPointerDown"
            @pointermove="onCaseInvestigationVisualizerPointerMove"
            @pointerup="onCaseInvestigationVisualizerPointerUp"
            @pointercancel="onCaseInvestigationVisualizerPointerUp"
            @contextmenu.prevent
          >
            <defs>
              <radialGradient id="case-flow-visual-manual-solve-address-glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stop-color="#60a5fa" stop-opacity="0.42" />
                <stop offset="58%" stop-color="#93c5fd" stop-opacity="0.18" />
                <stop offset="100%" stop-color="#bfdbfe" stop-opacity="0" />
              </radialGradient>
              <marker id="case-investigation-visual-arrow-live" markerWidth="12" markerHeight="12" refX="10" refY="6" orient="auto" markerUnits="strokeWidth">
                <path class="case-flow-visual-arrow" d="M 0 0 L 12 6 L 0 12 z" />
              </marker>
              <marker id="case-investigation-visual-arrow-muted-live" markerWidth="12" markerHeight="12" refX="10" refY="6" orient="auto" markerUnits="strokeWidth">
                <path class="case-flow-visual-arrow-muted" d="M 0 0 L 12 6 L 0 12 z" />
              </marker>
              <marker
                v-for="marker in caseManualSolveVisualColorMarkerDefinitions"
                :key="marker.id"
                :id="marker.id"
                markerWidth="12"
                markerHeight="12"
                refX="10"
                refY="6"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path :fill="marker.color" d="M 0 0 L 12 6 L 0 12 z" />
              </marker>
            </defs>
            <rect class="case-flow-visual-bg" :x="caseInvestigationVisualizerViewport.x" :y="caseInvestigationVisualizerViewport.y" :width="caseInvestigationVisualizerViewport.width" :height="caseInvestigationVisualizerViewport.height" @click.stop="closeCaseManualSolveExpandedGroupPanel" />
            <g v-if="caseInvestigationVisualizerSnapGuide.active" class="case-investigation-visualizer-snap-guide" aria-hidden="true">
              <line
                class="case-investigation-visualizer-snap-guide__line"
                :x1="caseInvestigationVisualizerSnapGuide.x1"
                :y1="caseInvestigationVisualizerSnapGuide.y1"
                :x2="caseInvestigationVisualizerSnapGuide.x2"
                :y2="caseInvestigationVisualizerSnapGuide.y2"
              />
              <circle
                class="case-investigation-visualizer-snap-guide__anchor"
                :cx="caseInvestigationVisualizerSnapGuide.targetX"
                :cy="caseInvestigationVisualizerSnapGuide.targetY"
                r="13"
              />
            </g>
            <g
              class="case-investigation-visualizer-render-layer"
              v-memo="[caseInvestigationVisualizerGraphRenderKey]"
            >
            <g class="case-flow-visuals-edges">
              <path
                v-for="edge in caseInvestigationVisualizerEdges"
                :key="edge.id"
                :class="caseFlowVisualEdgeClass(edge)"
                :d="caseFlowVisualEdgePathForInvestigation(edge)"
                :style="caseManualSolveVisualEdgeStyle(edge)"
                :marker-end="caseManualSolveVisualEdgeMarkerEnd(edge)"
              />
              <g
                v-for="edge in caseInvestigationVisualizerEdges.filter(caseManualSolveVisualEdgeHasBridgeBadge)"
                :key="`${edge.id}-bridge-badge`"
                class="case-manual-solve-bridge-edge-badge"
                :transform="caseManualSolveVisualBridgeEdgeBadgeTransform(edge)"
              >
                <title>{{ edge.bridgeBadge?.title || edge.bridgeLabel || 'Bridge' }}</title>
                <rect :x="-(edge.bridgeBadge?.width || 136) / 2" y="-15" :width="edge.bridgeBadge?.width || 136" height="30" rx="9" ry="9" />
                <text text-anchor="middle" y="5">{{ caseManualSolveVisualBridgeEdgeBadgeText(edge) }}</text>
              </g>
            </g>
            <g class="case-flow-visuals-nodes">
              <g
                v-for="node in caseInvestigationVisualizerVisibleNodes"
                :key="node.id"
                :class="caseInvestigationVisualizerNodeClass(node)"
                :transform="caseFlowVisualNodeTransform(node)"
                :data-node-id="node.id"
                @click.stop="onCaseInvestigationVisualizerNodeClick(node, $event)"
                @dblclick.stop="onCaseInvestigationVisualizerNodeDoubleClick(node, $event)"
                @pointerdown="onCaseInvestigationVisualizerNodePointerDown(node, $event)"
              >
                <template v-if="node.type === 'tx'">
                  <rect class="case-flow-visual-tx-rect" :x="-caseFlowVisualTxNodeSize.width / 2" :y="-caseFlowVisualTxNodeSize.height / 2" :width="caseFlowVisualTxNodeSize.width" :height="caseFlowVisualTxNodeSize.height" rx="16" ry="16" :style="caseManualSolveVisualNodeFillStyle(node)" />
                  <g v-if="(caseInvestigationVisualizerManualStartMode ? caseManualStartVisualizerOptions.showFundsFollowedBars !== false : caseInvestigationVisualizerOptions.showFundsFollowedBars !== false) && node.fundsFollowedBar" class="case-investigation-visualizer-funds-bar" aria-label="Funds followed bar">
                    <title>{{ node.fundsFollowedBar.title }}</title>
                    <rect class="case-investigation-visualizer-funds-bar__track" :x="node.fundsFollowedBar.x" :y="node.fundsFollowedBar.y" :width="node.fundsFollowedBar.width" :height="node.fundsFollowedBar.height" :rx="node.fundsFollowedBar.rx" :ry="node.fundsFollowedBar.rx" />
                    <rect class="case-investigation-visualizer-funds-bar__fill" :x="node.fundsFollowedBar.fillX" :y="node.fundsFollowedBar.y" :width="node.fundsFollowedBar.fillWidth" :height="node.fundsFollowedBar.height" :rx="node.fundsFollowedBar.rx" :ry="node.fundsFollowedBar.rx" />
                    <rect class="case-investigation-visualizer-funds-bar__outline" :x="node.fundsFollowedBar.x" :y="node.fundsFollowedBar.y" :width="node.fundsFollowedBar.width" :height="node.fundsFollowedBar.height" :rx="node.fundsFollowedBar.rx" :ry="node.fundsFollowedBar.rx" />
                  </g>
                  <text v-if="node.amountText" class="case-flow-visual-amount-text" text-anchor="middle" y="-42">{{ node.amountText }}</text>
                  <text class="case-flow-visual-hash-text" text-anchor="middle" y="-8">{{ node.shortHash }}</text>
                  <text v-if="node.dateText" class="case-flow-visual-date-text" text-anchor="middle" y="18">{{ node.dateText }}</text>
                  <text v-if="node.chainText" class="case-flow-visual-chain-text" text-anchor="middle" y="42">{{ node.chainText }}</text>
                  <g v-if="node.pathLabelLines && node.pathLabelLines.length" :transform="caseManualVisualPathLabelTransform(node)">
                    <rect class="case-flow-visual-path-bg" :x="-node.pathBoxWidth / 2" :y="-node.pathBoxHeight / 2" :width="node.pathBoxWidth" :height="node.pathBoxHeight" rx="8" ry="8" />
                    <text class="case-flow-visual-path-text" text-anchor="middle" dominant-baseline="middle" alignment-baseline="middle" :font-size="node.pathFontSize">
                      <tspan v-for="(line, lineIndex) in node.pathLabelLines" :key="`${node.id}-path-${lineIndex}`" x="0" :y="caseManualVisualPathTextY(node, lineIndex)" dominant-baseline="middle" alignment-baseline="middle">
                        {{ line }}
                      </tspan>
                    </text>
                  </g>
                  <text v-if="node.badge" class="case-flow-visual-badge-text" text-anchor="middle" :y="-caseFlowVisualTxNodeSize.height / 2 - 14">{{ node.badge }}</text>
                </template>

                <template v-else-if="node.type === 'address'">
                  <g v-if="caseManualVisualNodeLabelLines(node).length">
                    <text
                      v-for="(line, lineIndex) in caseManualVisualNodeLabelLines(node)"
                      :key="`${node.id}-label-${lineIndex}`"
                      :class="['case-flow-visual-address-label', `case-flow-visual-address-label-${caseManualVisualNodeLabelCategory(node)}`]"
                      text-anchor="middle"
                      :font-size="node.labelFontSize || 16"
                      :y="-caseFlowVisualAddressRadius - 26 - ((caseManualVisualNodeLabelLines(node).length - 1 - lineIndex) * 20)"
                    >
                      {{ line }}
                    </text>
                  </g>
                  <circle v-if="caseInvestigationVisualizerNodeIsSingleSelected(node)" class="case-investigation-visualizer-address-selected-ring" :r="caseFlowVisualAddressRadius + 18" />
                  <circle v-if="caseManualVisualNodeAlertRing(node)" class="case-flow-visual-address-alert-ring" :r="caseFlowVisualAddressRadius + 8" />
                  <polygon v-if="node.shape === 'hexagon'" :class="['case-flow-visual-address-circle', 'case-flow-visual-address-hexagon', `case-flow-visual-address-circle-${caseManualVisualNodeLabelCategory(node)}`]" :points="caseFlowVisualAddressHexagonPoints" :style="caseManualSolveVisualNodeFillStyle(node)" />
                  <circle v-else :class="['case-flow-visual-address-circle', `case-flow-visual-address-circle-${caseManualVisualNodeLabelCategory(node)}`]" :r="caseFlowVisualAddressRadius" :style="caseManualSolveVisualNodeFillStyle(node)" />
                  <text class="case-flow-visual-address-text" text-anchor="middle" y="5">{{ node.shortAddress }}</text>
                  <text v-if="node.sourceCount > 1" class="case-flow-visual-address-count" text-anchor="middle" :y="caseFlowVisualAddressRadius + 18">×{{ node.sourceCount }}</text>
                </template>

                <template v-else-if="node.type === 'amount'">
                  <rect class="case-flow-visual-amount-rect" :x="-caseFlowVisualAmountNodeSize.width / 2" :y="-caseFlowVisualAmountNodeSize.height / 2" :width="caseFlowVisualAmountNodeSize.width" :height="caseFlowVisualAmountNodeSize.height" rx="12" ry="12" :style="caseManualSolveVisualNodeFillStyle(node)" />
                  <text class="case-flow-visual-amount-node-text" text-anchor="middle" y="5">{{ node.amountText }}</text>
                </template>

                <template v-else-if="node.type === 'manualGroup'">
                  <rect
                    class="case-manual-solve-group-node__shadow"
                    :x="-(node.boxWidth || 150) / 2 + 5"
                    :y="-(node.boxHeight || 118) / 2 + 6"
                    :width="node.boxWidth || 150"
                    :height="node.boxHeight || 118"
                    rx="18"
                    ry="18"
                  />
                  <rect
                    class="case-manual-solve-group-node__body"
                    :x="-(node.boxWidth || 150) / 2"
                    :y="-(node.boxHeight || 118) / 2"
                    :width="node.boxWidth || 150"
                    :height="node.boxHeight || 118"
                    rx="18"
                    ry="18"
                    :style="caseManualSolveVisualNodeFillStyle(node)"
                  />
                  <rect
                    class="case-manual-solve-group-node__inner"
                    :x="-(node.boxWidth || 150) / 2 + 10"
                    :y="-(node.boxHeight || 118) / 2 + 10"
                    :width="(node.boxWidth || 150) - 20"
                    :height="(node.boxHeight || 118) - 20"
                    rx="13"
                    ry="13"
                    :style="caseManualSolveVisualGroupInnerStyle(node)"
                  />
                  <text class="case-manual-solve-group-node__title" text-anchor="middle" y="-18" :style="caseManualSolveVisualGroupTextStyle(node)">{{ node.groupName }}</text>
                  <text class="case-manual-solve-group-node__meta" text-anchor="middle" y="9" :style="caseManualSolveVisualGroupTextStyle(node)">{{ node.memberSummary }}</text>
                  <text class="case-manual-solve-group-node__hint" text-anchor="middle" y="34" :style="caseManualSolveVisualGroupTextStyle(node)">Double-click to open</text>
                  <rect
                    class="case-manual-solve-group-node__hitbox"
                    :x="-(node.boxWidth || 150) / 2 - 8"
                    :y="-(node.boxHeight || 118) / 2 - 8"
                    :width="(node.boxWidth || 150) + 16"
                    :height="(node.boxHeight || 118) + 16"
                    rx="22"
                    ry="22"
                  />
                </template>

                <template v-else-if="node.type === 'flowLabel'">
                  <rect class="case-flow-visual-flow-label-bg" :x="-(node.boxWidth || 420) / 2" :y="-(node.boxHeight || 56) / 2" :width="node.boxWidth || 420" :height="node.boxHeight || 56" rx="14" ry="14" :style="caseManualSolveVisualNodeFillStyle(node)" />
                  <text class="case-flow-visual-flow-label-text" text-anchor="middle" :font-size="node.fontSize || 24" y="9">{{ node.labelText }}</text>
                </template>
              </g>
            </g>
            </g>
            <g v-if="caseManualStartFurtherManualWorkVisualMarkers.length" class="case-flow-visuals-manual-work-markers" aria-hidden="true">
              <g
                v-for="marker in caseManualStartFurtherManualWorkVisualMarkers"
                :key="marker.key"
                class="case-flow-visual-manual-work-marker"
                :transform="`translate(${marker.x} ${marker.y})`"
              >
                <title>{{ marker.title }}</title>
                <circle class="case-flow-visual-manual-work-bubble" :r="marker.radius" />
                <text class="case-flow-visual-manual-work-label" text-anchor="middle" :y="marker.labelY">{{ marker.label }}</text>
              </g>
            </g>
            <g v-if="caseManualStartAcceptedReportBoundaryVisualMarkers.length" class="case-flow-visuals-report-boundary-markers" aria-hidden="true">
              <g
                v-for="marker in caseManualStartAcceptedReportBoundaryVisualMarkers"
                :key="marker.key"
                class="case-flow-visual-report-boundary-marker"
                :transform="`translate(${marker.x} ${marker.y})`"
              >
                <title>{{ marker.title }}</title>
                <circle class="case-flow-visual-report-boundary-bubble" :r="marker.radius" />
                <circle class="case-flow-visual-report-boundary-check" cx="0" :cy="-marker.radius + 7" r="12" />
                <path class="case-flow-visual-report-boundary-checkmark" :d="`M -5 ${-marker.radius + 7} l 4 4 l 7 -8`" />
                <text class="case-flow-visual-report-boundary-label" text-anchor="middle" :y="marker.labelY">{{ marker.label }}</text>
              </g>
            </g>
            <g v-if="caseManualStartDormantVisualMarkers.length" class="case-flow-visuals-dormant-markers" aria-hidden="true">
              <g
                v-for="marker in caseManualStartDormantVisualMarkers"
                :key="marker.key"
                class="case-flow-visual-dormant-marker"
                :transform="`translate(${marker.x} ${marker.y})`"
              >
                <title>{{ marker.title }}</title>
                <circle
                  v-if="marker.partial"
                  class="case-flow-visual-dormant-glow"
                  :cx="marker.glowX"
                  cy="0"
                  :r="marker.glowRadius"
                />
                <template v-if="!marker.partial">
                  <rect class="case-flow-visual-dormant-wall" x="0" y="-40" width="46" height="80" rx="6" ry="6" />
                  <g class="case-flow-visual-dormant-wall-bricks">
                    <rect x="5" y="-34" width="16" height="11" rx="1" />
                    <rect x="24" y="-34" width="17" height="11" rx="1" />
                    <rect x="5" y="-19" width="26" height="11" rx="1" />
                    <rect x="34" y="-19" width="7" height="11" rx="1" />
                    <rect x="5" y="-4" width="13" height="11" rx="1" />
                    <rect x="21" y="-4" width="20" height="11" rx="1" />
                    <rect x="5" y="11" width="24" height="11" rx="1" />
                    <rect x="32" y="11" width="9" height="11" rx="1" />
                    <rect x="5" y="26" width="17" height="11" rx="1" />
                    <rect x="25" y="26" width="16" height="11" rx="1" />
                  </g>
                </template>
                <g
                  class="case-flow-visual-dormant-money-bag"
                  :class="{ 'case-flow-visual-dormant-money-bag--partial': marker.partial }"
                  :transform="marker.bagTransform"
                >
                  <path d="M -11 -2 C -15 -13 -8 -22 0 -22 C 8 -22 15 -13 11 -2 C 16 2 18 12 13 18 C 8 24 -8 24 -13 18 C -18 12 -16 2 -11 -2 Z" />
                  <path d="M -6 -22 C -2 -27 2 -27 6 -22" />
                  <text text-anchor="middle" y="8">$</text>
                </g>
                <text v-if="marker.label" class="case-flow-visual-dormant-label" :class="{ 'case-flow-visual-dormant-label--partial': marker.partial }" :x="marker.labelX" :y="marker.labelY">{{ marker.label }}</text>
              </g>
            </g>
            <g
              v-if="caseManualStartViewportWorkIndicatorMarker"
              class="case-manual-start-work-indicator"
              :class="[
                { 'is-finishing': caseManualStartViewportWorkIndicatorMarker.finishing },
                `case-manual-start-work-indicator--${caseManualStartViewportWorkIndicatorMarker.actionClass}`
              ]"
              aria-hidden="true"
            >
              <title>{{ caseManualStartViewportWorkIndicatorMarker.title }}</title>
              <path
                v-if="caseManualStartViewportWorkIndicatorMarker.showLane"
                class="case-manual-start-work-indicator__lane"
                :d="caseManualStartViewportWorkIndicatorMarker.lanePath"
              />
              <g
                v-if="caseManualStartViewportWorkIndicatorMarker.showLane"
                class="case-manual-start-work-indicator__ghost-address"
                :transform="`translate(${caseManualStartViewportWorkIndicatorMarker.ghostAddressX} ${caseManualStartViewportWorkIndicatorMarker.ghostAddressY})`"
              >
                <circle :r="caseManualStartViewportWorkIndicatorMarker.ghostAddressRadius" />
                <circle :r="Math.max(8, caseManualStartViewportWorkIndicatorMarker.ghostAddressRadius - 14)" />
              </g>
              <g
                class="case-manual-start-work-indicator__anchor"
                :transform="`translate(${caseManualStartViewportWorkIndicatorMarker.x} ${caseManualStartViewportWorkIndicatorMarker.y})`"
              >
                <circle class="case-manual-start-work-indicator__anchor-haze" :r="caseManualStartViewportWorkIndicatorMarker.hazeRadius" />
                <circle class="case-manual-start-work-indicator__anchor-ring" :r="caseManualStartViewportWorkIndicatorMarker.ringRadius" />
                <!-- Keep the local working cue calm: the rotating dashed ring and ghost lane provide progress feedback.
                     The old orbiting dots could read as a fast stray node flying around the selected address. -->
                <g v-if="caseManualStartViewportWorkIndicatorMarker.showResolveStamp" class="case-manual-start-work-indicator__resolve-stamp">
                  <path d="M -18 2 L -5 15 L 22 -18" />
                </g>
              </g>
            </g>

            <g
              v-if="caseManualSolveExpandedGroupPanel"
              class="case-manual-solve-expanded-group-panel"
              :transform="`translate(${caseManualSolveExpandedGroupPanel.x} ${caseManualSolveExpandedGroupPanel.y})`"
              @click.stop
            >
              <rect
                class="case-manual-solve-expanded-group-panel__backdrop"
                :width="caseManualSolveExpandedGroupPanel.width"
                :height="caseManualSolveExpandedGroupPanel.height"
                rx="24"
                ry="24"
              />
              <rect
                class="case-manual-solve-expanded-group-panel__header"
                :width="caseManualSolveExpandedGroupPanel.width"
                height="54"
                rx="24"
                ry="24"
              />
              <text class="case-manual-solve-expanded-group-panel__title" x="22" y="33">{{ caseManualSolveExpandedGroupPanel.name }}</text>
              <text class="case-manual-solve-expanded-group-panel__summary" :x="caseManualSolveExpandedGroupPanel.width - 72" y="33" text-anchor="end">{{ caseManualSolveExpandedGroupPanel.memberSummary }}</text>
              <g class="case-manual-solve-expanded-group-panel__close" :transform="`translate(${caseManualSolveExpandedGroupPanel.width - 36} 27)`" role="button" aria-label="Close visual group" @click.stop="closeCaseManualSolveExpandedGroupPanel">
                <rect x="-15" y="-15" width="30" height="30" rx="10" ry="10" />
                <line x1="-6" y1="-6" x2="6" y2="6" />
                <line x1="6" y1="-6" x2="-6" y2="6" />
              </g>
              <g
                v-for="member in caseManualSolveExpandedGroupPanel.members"
                :key="member.key"
                class="case-manual-solve-expanded-group-panel__member"
                :transform="`translate(${member.x} ${member.y})`"
              >
                <rect :width="member.width" :height="member.height" x="0" y="0" rx="12" ry="12" />
                <text class="case-manual-solve-expanded-group-panel__member-title" x="12" y="22">{{ member.title }}</text>
                <text class="case-manual-solve-expanded-group-panel__member-meta" x="12" y="42">{{ member.meta }}</text>
              </g>
              <text v-if="!caseManualSolveExpandedGroupPanel.members.length" class="case-manual-solve-expanded-group-panel__empty" :x="caseManualSolveExpandedGroupPanel.width / 2" :y="caseManualSolveExpandedGroupPanel.height / 2" text-anchor="middle">Drag selected nodes into this square to add them.</text>
            </g>

            <g v-if="caseInvestigationVisualizerSelectionBox.active" class="case-investigation-visualizer-selection-layer" aria-hidden="true">
              <rect
                class="case-investigation-visualizer-selection-rect"
                :x="caseInvestigationVisualizerSelectionBoxRect.x"
                :y="caseInvestigationVisualizerSelectionBoxRect.y"
                :width="caseInvestigationVisualizerSelectionBoxRect.width"
                :height="caseInvestigationVisualizerSelectionBoxRect.height"
              />
            </g>
          </svg>

          <div
            v-if="caseInvestigationVisualizerManualStartMode && caseManualSolveVisualLegendOpen"
            id="case-manual-solve-visual-legend-panel"
            class="case-manual-solve-visual-legend-panel"
            role="dialog"
            aria-label="Visual legend"
            @click.stop
            @pointerdown.stop
          >
            <div class="case-manual-solve-visual-legend-panel__header">
              <div>
                <strong>Visual legend</strong>
                <small>Symbols used in the Manual Solve flow view.</small>
              </div>
              <button class="case-manual-solve-visual-legend-panel__close" type="button" aria-label="Close visual legend" @click.stop="closeCaseManualSolveVisualLegend">
                <span aria-hidden="true"></span>
              </button>
            </div>
            <div class="case-manual-solve-visual-legend-panel__body">
              <section
                v-for="group in caseManualSolveVisualLegendGroups"
                :key="group.key"
                class="case-manual-solve-visual-legend-panel__group"
              >
                <h4 v-if="group.title">{{ group.title }}</h4>
                <article
                  v-for="item in group.items"
                  :key="item.key"
                  class="case-manual-solve-visual-legend-panel__item"
                >
                  <span class="case-manual-solve-visual-legend-panel__swatch" aria-hidden="true">
                    <svg viewBox="0 0 64 42" focusable="false">
                      <rect v-if="item.shape === 'transaction'" class="case-flow-visual-tx-rect" x="13" y="9" width="38" height="24" rx="6" ry="6" />
                      <circle v-else-if="item.shape === 'address'" class="case-flow-visual-address-circle" cx="32" cy="21" r="13" />
                      <rect v-else-if="item.shape === 'amount'" class="case-flow-visual-amount-rect" x="10" y="11" width="44" height="22" rx="6" ry="6" />
                      <g v-else-if="item.shape === 'arrow'">
                        <path class="case-flow-visual-edge" d="M 8 21 C 21 10 39 10 51 21" />
                        <path class="case-flow-visual-arrow" d="M 49 14 L 60 21 L 49 28 Z" />
                      </g>
                      <g v-else-if="item.shape === 'selected'" class="case-flow-visual-node is-selected">
                        <circle class="case-flow-visual-address-circle" cx="32" cy="21" r="13" />
                      </g>
                      <polygon v-else-if="item.shape === 'swap'" class="case-flow-visual-address-circle case-flow-visual-address-hexagon case-flow-visual-address-circle-swap case-manual-solve-visual-legend-panel__swap-hex" points="32,5 48,13 48,29 32,37 16,29 16,13" />
                      <circle v-else-if="item.shape === 'hot-wallet'" class="case-flow-visual-address-circle case-manual-solve-visual-legend-panel__hot-wallet-circle" cx="32" cy="21" r="13" />
                      <g v-else-if="item.shape === 'bridge'">
                        <rect class="case-flow-visual-tx-rect case-manual-solve-visual-legend-panel__bridge-rect" x="11" y="10" width="26" height="22" rx="6" ry="6" />
                        <path class="case-flow-visual-edge is-bridge-edge" d="M 36 21 C 43 13 50 13 57 21" />
                      </g>
                      <g v-else-if="item.shape === 'dashed-edge'">
                        <path class="case-flow-visual-edge is-dashed" d="M 8 21 C 21 10 39 10 51 21" />
                        <path class="case-flow-visual-arrow-muted" d="M 49 14 L 60 21 L 49 28 Z" />
                      </g>
                    </svg>
                  </span>
                  <span class="case-manual-solve-visual-legend-panel__item-copy">
                    <strong>{{ item.label }}</strong>
                    <small>{{ item.description }}</small>
                  </span>
                </article>
              </section>
            </div>
          </div>

          <div
            v-if="caseInvestigationVisualizerManualStartMode && !caseManualStartQuickLookupMode && caseManualSolveGetDataPanelOpen"
            ref="caseManualSolveGetDataPanel"
            class="case-manual-solve-get-data-panel"
            :class="{ 'is-dragging': caseManualSolveGetDataPanelDragging }"
            :style="caseManualSolveGetDataPanelStyle"
            role="dialog"
            aria-label="Manual Solve raw data TSV"
            @click.stop
            @pointerdown.stop
          >
            <div
              class="case-manual-solve-get-data-panel__header"
              title="Drag this data panel"
              @pointerdown.stop.prevent="beginCaseManualSolveGetDataPanelDrag"
            >
              <div class="case-manual-solve-get-data-panel__title">
                <strong>GET DATA</strong>
                <small>{{ caseManualSolveGetDataSummaryText }}</small>
              </div>
              <button
                class="case-manual-solve-get-data-panel__close"
                type="button"
                aria-label="Close raw data TSV panel"
                title="Close"
                @pointerdown.stop
                @click.stop="closeCaseManualSolveGetDataPanel"
              >
                <span aria-hidden="true"></span>
              </button>
            </div>
            <textarea
              ref="caseManualSolveGetDataTextarea"
              v-model="caseManualSolveGetDataDraft"
              class="case-manual-solve-get-data-panel__textarea"
              spellcheck="false"
              aria-label="Raw data TSV for the current Manual Solve viewport"
              @keydown.esc.stop.prevent="closeCaseManualSolveGetDataPanel"
            ></textarea>
            <div class="case-manual-solve-get-data-panel__footer">
              <span class="case-manual-solve-get-data-panel__status">{{ caseManualSolveGetDataStatus || 'Current viewport raw data TSV is ready.' }}</span>
              <div class="case-manual-solve-get-data-panel__actions">
                <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="refreshCaseManualSolveGetDataPanel">Refresh</button>
                <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseManualSolveGetDataDraft" @click="selectCaseManualSolveGetDataText">Select all</button>
                <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseManualSolveGetDataDraft" @click="copyCaseManualSolveGetDataTsv">Copy TSV</button>
                <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!caseManualSolveGetDataDraft" @click="downloadCaseManualSolveGetDataCsv">Download CSV</button>
              </div>
            </div>
          </div>

          <div v-if="!caseInvestigationVisualizerCurrentVisual" class="case-flow-visuals-empty">
            Prepare at least one graph in Main Data to investigate it visually.
          </div>

          <div v-if="caseInvestigationVisualizerInteractionLocked" class="case-investigation-hop-solver-lock">
            <strong>Solve Hop placeholder active</strong>
            <span>Case visual navigation and visual edits are paused while this future case action is running.</span>
            <button class="toolbar-btn toolbar-btn--danger" type="button" @click="requestCaseHopSolverPlaceholderStop">Request stop</button>
          </div>

          <div
            v-if="caseInvestigationVisualizerClipboardMessage"
            class="case-investigation-visualizer-clipboard-status"
            role="status"
            aria-live="polite"
            @click.stop="clearCaseInvestigationVisualizerClipboardMessage"
          >
            {{ caseInvestigationVisualizerClipboardMessage }}
          </div>

          <div
            v-if="caseInvestigationVisualizerManualStartMode && caseManualStartViewportAutomationCallouts.length"
            class="case-manual-start-automation-callout-layer"
            aria-live="polite"
            aria-atomic="false"
            @click.stop
          >
            <svg class="case-manual-start-automation-callout-lines" aria-hidden="true">
              <line
                v-for="callout in caseManualStartViewportAutomationCallouts"
                :key="`${callout.id}-line`"
                class="case-manual-start-automation-callout-line"
                :x1="callout.line?.x1 || 0"
                :y1="callout.line?.y1 || 0"
                :x2="callout.line?.x2 || 0"
                :y2="callout.line?.y2 || 0"
                :style="{ stroke: callout.color || '#111827' }"
              />
            </svg>
            <div
              v-for="callout in caseManualStartViewportAutomationCallouts"
              :key="callout.id"
              class="case-manual-start-automation-callout"
              :class="[`case-manual-start-automation-callout--${callout.kind || 'solve'}`]"
              :style="caseManualStartViewportAutomationCalloutStyle(callout)"
            >
              <span>{{ callout.text }}</span>
            </div>
          </div>

          <div
            v-if="caseManualStartViewportCompletionEffect.visible"
            ref="caseManualStartViewportCompletionEffectPanel"
            class="case-manual-start-completion-effect"
            :class="{ 'is-dragging': caseManualStartViewportCompletionEffect.dragging }"
            :style="caseManualStartViewportCompletionEffectStyle"
            role="status"
            aria-live="polite"
            @click.stop
          >
            <div
              class="case-manual-start-completion-effect__header"
              title="Drag this completion notice"
              @pointerdown.stop.prevent="beginCaseManualStartViewportCompletionEffectDrag"
            >
              <strong>{{ caseManualStartViewportCompletionEffectTitle }}</strong>
              <button class="case-manual-start-completion-effect__close" type="button" aria-label="Dismiss Manual Solve completion notice" @pointerdown.stop @click.stop="clearCaseManualStartViewportCompletionEffect">×</button>
            </div>
            <span>{{ caseManualStartViewportCompletionEffect.message || 'No unresolved path endings remain in this viewport.' }}</span>
            <template v-if="caseQuickLookupManualSolveCompletionReady">
              <small v-if="caseQuickLookupManualSolveHasManualReviewOnly">The automated pass is finished. Return to Transaction Lookup to continue tracing or use the current endings as disclosed report boundaries.</small>
              <small v-else>Review the completed flow, then return to Transaction Lookup to create the full report or analyze another transaction.</small>
              <div class="case-manual-start-completion-effect__actions">
                <button
                  class="toolbar-btn toolbar-btn--done"
                  type="button"
                  @pointerdown.stop
                  @click.stop="closeCompletedCaseQuickLookupFlowSolver"
                >
                  Return to Transaction Lookup
                </button>
              </div>
            </template>
            <small v-else>Drag this notice to move it, or dismiss it when you are ready.</small>
          </div>

          <div
            v-if="caseInvestigationVisualizerManualStartMode"
            class="case-manual-start-node-action-tray"
            :class="{ 'is-collapsed': !caseManualStartNodeActionTrayOpen }"
            role="toolbar"
            aria-label="Manual Solve selected-node action tray"
            @click.stop
          >
            <button
              v-if="caseManualStartNodeActionTrayOpen"
              class="case-manual-start-node-action-tray__toggle"
              type="button"
              title="Hide selected-node action tray"
              aria-label="Hide selected-node action tray"
              @click="toggleCaseManualStartNodeActionTray"
            >
              <span aria-hidden="true">»</span>
            </button>
            <template v-if="caseManualStartNodeActionTrayOpen">
              <button
                v-for="action in caseManualStartNodeActionTrayItems"
                :key="action.key"
                class="case-manual-start-node-action-tray__button"
                :class="{
                  'is-recommended': action.recommended,
                  'is-secondary': action.secondary
                }"
                type="button"
                :disabled="action.disabled"
                :title="action.title"
                :aria-label="action.label"
                @click="runCaseManualStartNodeActionTrayAction(action.key)"
              >
                <span aria-hidden="true">{{ action.symbol }}</span>
              </button>
            </template>
            <button
              v-else
              class="case-manual-start-node-action-tray__peek"
              type="button"
              title="Show selected-node action tray"
              aria-label="Show selected-node action tray"
              @click="toggleCaseManualStartNodeActionTray"
            >
              <span aria-hidden="true">«</span>
            </button>
          </div>

          <div
            v-if="caseInvestigationVisualizerManualStartMode && caseManualStartNodeActionTrayAttributionOpen"
            class="case-manual-start-node-action-tray-popover"
            role="dialog"
            aria-label="Change selected-node attribution"
            @click.stop
            @pointerdown.stop
          >
            <div class="case-manual-start-node-action-tray-popover__header">
              <div>
                <strong>Change attribution</strong>
                <small>Writes Column K for matching receiving rows and refreshes the address label.</small>
              </div>
              <button class="case-manual-start-node-action-tray-popover__close" type="button" aria-label="Close attribution editor" @click="closeCaseManualStartNodeActionTrayAttribution"><span class="case-manual-start-node-action-tray-popover__close-icon" aria-hidden="true"></span></button>
            </div>
            <label class="case-manual-start-node-action-tray-popover__field">
              <span>Column K attribution</span>
              <input
                ref="caseManualStartNodeActionTrayAttributionInput"
                v-model.trim="caseManualStartNodeActionTrayAttributionDraft"
                type="text"
                placeholder="Example: KuCoin"
                @keydown.enter.prevent="applyCaseManualStartNodeActionTrayAttribution"
                @keydown.esc.prevent="closeCaseManualStartNodeActionTrayAttribution"
              />
            </label>
            <p v-if="caseManualStartNodeActionTrayAttributionStatus" class="case-manual-start-node-action-tray-popover__status">{{ caseManualStartNodeActionTrayAttributionStatus }}</p>
            <div class="case-manual-start-node-action-tray-popover__actions">
              <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="closeCaseManualStartNodeActionTrayAttribution">Cancel</button>
              <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!caseManualStartNodeActionTrayAttributionDraft" @click="applyCaseManualStartNodeActionTrayAttribution">Apply</button>
            </div>
          </div>

          <div
            v-if="caseInvestigationVisualizerManualStartMode && !caseManualStartHopInspectorOpen"
            class="case-manual-start-quick-actions"
            @click.stop
          >
            <div>
              <strong>Manual visual hop solving</strong>
              <small>{{ caseManualStartHopInspectorCandidateLabel }}</small>
            </div>
            <div class="case-manual-start-quick-actions__buttons">
              <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!caseManualStartHopInspectorCanRun || caseInvestigationVisualizerInteractionLocked" :title="caseManualStartHopInspectorSolveTitle" @click="openCaseManualStartHopInspectorFromSelection">Solve hop</button>
              <button v-if="caseManualStartOutputChoiceShouldShow" class="toolbar-btn toolbar-btn--accent" type="button" :disabled="!caseManualStartOutputChoiceCanOpen || caseInvestigationVisualizerInteractionLocked" @click="openCaseManualStartOutputChoiceFromSelection">{{ caseManualStartOutputChoiceButtonLabel }}</button>
              <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseManualStartSingleSelectedCandidate || caseInvestigationVisualizerInteractionLocked" @click="openFullCaseHopSolverFromManualStart">Full Flow Solver</button>
              <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseInvestigationVisualizerInteractionLocked" @click="openCaseManualStartSettings">Settings</button>
            </div>
          </div>

          <div
            v-if="caseManualStartResolvedSolveWarningOpen"
            class="case-manual-start-solve-warning"
            @click.stop
          >
            <div class="case-manual-start-solve-warning__header">
              <div>
                <strong>This path ending already looks resolved</strong>
                <small>{{ caseManualStartResolvedSolveWarningLabel }}</small>
              </div>
              <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Dismiss resolved-ending warning" @click.stop="closeCaseManualStartResolvedSolveWarning">×</button>
            </div>
            <p>Case Buddy found a meaningful path-ending story or attribution for the selected node. Continuing to trace through it may over-extend a path that is already resolved.</p>
            <p v-if="caseManualStartResolvedSolveWarningStatus" class="case-manual-start-solve-warning__status">{{ caseManualStartResolvedSolveWarningStatus }}</p>
            <label class="case-manual-start-solve-warning__checkbox">
              <input v-model="caseManualStartResolvedSolveWarningChecked" type="checkbox" />
              <span>I understand this path appears resolved and I still want to inspect it with Flow Solver.</span>
            </label>
            <div class="case-manual-start-solve-warning__actions">
              <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="closeCaseManualStartResolvedSolveWarning">Go back</button>
              <button class="toolbar-btn toolbar-btn--danger" type="button" :disabled="!caseManualStartResolvedSolveWarningChecked" @click="confirmCaseManualStartResolvedSolveWarning">Inspect anyway</button>
            </div>
          </div>

          <div
            v-if="caseManualStartSettingsOpen"
            class="case-manual-start-settings-panel"
            @click.stop
          >
            <div class="case-manual-start-settings-panel__header">
              <div>
                <strong>Flow Solver settings</strong>
                <small>Used by this viewport and saved to the same browser settings as Flow Solver.</small>
              </div>
              <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close Manual Solve settings" @click.stop="closeCaseManualStartSettings">×</button>
            </div>
            <div class="case-manual-start-settings-panel__grid">
              <label>
                <span>Direction</span>
                <select v-model="caseManualStartSettingsDraft.traceDirection" @change="saveCaseManualStartSettings({ quiet: true })">
                  <option value="forward">Forward</option>
                  <option value="reverse">Reverse</option>
                </select>
              </label>
              <label>
                <span>Hop logic</span>
                <select v-model="caseManualStartSettingsDraft.hopLogicType" @change="saveCaseManualStartSettings({ quiet: true })">
                  <option value="FIFO">FIFO</option>
                  <option value="LIFO">LIFO</option>
                  <option value="NEXT_CHRONOLOGICAL">Next chronological</option>
                </select>
              </label>
              <label>
                <span>Flow max depth</span>
                <input v-model.number="caseManualStartSettingsDraft.flowCompletionMaxDepth" type="number" min="1" max="500" @change="saveCaseManualStartSettings({ quiet: true })" />
              </label>
              <label class="case-manual-start-settings-panel__checkbox">
                <input v-model="caseManualStartSettingsDraft.enableAdditionalServiceChecks" type="checkbox" @change="saveCaseManualStartSettings({ quiet: true })" />
                <span>Additional service / hot-wallet checks</span>
              </label>
              <label class="case-manual-start-settings-panel__checkbox" title="Off by default. This is not strict FIFO; it intentionally allows a specific-identification / amount-date match when a native spend plus gas closely matches the selected incoming amount.">
                <input v-model="caseManualStartSettingsDraft.enableNativeDirectSpendMatchGuard" type="checkbox" @change="saveCaseManualStartSettings({ quiet: true })" />
                <span>Native amount/date match shortcut</span>
               </label>
              <label class="case-manual-start-settings-panel__checkbox">
                <input v-model="caseManualStartSettingsDraft.enableDepositEntityHopExtension" type="checkbox" @change="saveCaseManualStartSettings({ quiet: true })" />
                <span>Deposit entity hop extension</span>
              </label>
              <label class="case-manual-start-settings-panel__checkbox">
                <input v-model="caseManualStartSettingsDraft.enableSessionDataCache" type="checkbox" @change="saveCaseManualStartSettings({ quiet: true })" />
                <span>Session provider cache</span>
              </label>
              <label class="case-manual-start-settings-panel__checkbox">
                <input v-model="caseManualStartSettingsDraft.flowCompletionValueTooLowEnabled" type="checkbox" @change="saveCaseManualStartSettings({ quiet: true })" />
                <span>Use low-value ending rules</span>
              </label>
              <label class="case-manual-start-settings-panel__checkbox">
                <input v-model="caseManualStartSettingsDraft.showAutomationCallouts" type="checkbox" @change="saveCaseManualStartSettings({ quiet: true })" />
                <span>Show Next / Auto solve callouts</span>
              </label>
            </div>
            <p class="case-manual-start-settings-panel__note">Flow Solver operational notes remain routed to Column W in Manual Solve.</p>
            <p v-if="caseManualStartSettingsStatus" class="case-manual-start-settings-panel__status">{{ caseManualStartSettingsStatus }}</p>
            <div class="case-manual-start-settings-panel__actions">
              <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="resetCaseManualStartSettings">Reset</button>
              <button class="toolbar-btn toolbar-btn--primary" type="button" @click="saveCaseManualStartSettings">Save settings</button>
            </div>
          </div>


          <div
            v-if="caseManualStartOutputChoiceOpen"
            class="case-manual-start-output-choice"
            :style="caseManualStartOutputChoiceStyle"
            @click.stop
          >
            <div class="case-manual-start-output-choice__header">
              <div>
                <strong>{{ caseManualStartOutputChoiceTitle }}</strong>
                <small>{{ caseManualStartHopInspectorCandidateLabel }}</small>
              </div>
              <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close output chooser" @click.stop="closeCaseManualStartOutputChoice">×</button>
            </div>
            <p class="case-manual-start-output-choice__status">
              {{ caseManualStartOutputChoiceStatus || 'Pick the exact output/input WayFinder would follow, then apply it to Main/Raw and the manual visual.' }}
            </p>
            <div class="case-manual-start-output-choice__actions">
              <button class="toolbar-btn" type="button" :disabled="caseManualStartOutputChoiceLoading" @click="loadCaseManualStartOutputChoices">
                {{ caseManualStartOutputChoiceLoading ? 'Loading…' : 'Reload choices' }}
              </button>
              <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!caseManualStartOutputChoiceCanApply" @click="applyCaseManualStartOutputChoice">
                Apply selected choice
              </button>
            </div>
            <div v-if="caseManualStartOutputChoiceLoading" class="case-manual-start-output-choice__empty">Loading provider choices…</div>
            <div v-else-if="!caseManualStartOutputChoiceRows.length" class="case-manual-start-output-choice__empty">
              No choices are loaded yet. Reload choices or use Full Flow Solver if the provider response is ambiguous.
            </div>
            <div v-else class="case-manual-start-output-choice__list">
              <button
                v-for="choice in caseManualStartOutputChoiceRows"
                :key="choice.key"
                type="button"
                class="case-manual-start-output-choice__item"
                :class="{ 'is-selected': choice.key === caseManualStartOutputChoiceSelectedKey }"
                @click="caseManualStartOutputChoiceSelectedKey = choice.key"
                @dblclick.prevent="selectAndApplyCaseManualStartOutputChoice(choice)"
              >
                <span class="case-manual-start-output-choice__item-title">{{ choice.title }}</span>
                <strong>{{ choice.amountText }} {{ choice.token }}</strong>
                <code :title="choice.address">{{ caseManualShorten(choice.address, 10, 8) }}</code>
                <small>{{ choice.detail }}</small>
                <small v-if="choice.inputAddressCount">{{ choice.inputAddressCount }} BTC input From address{{ choice.inputAddressCount === 1 ? '' : 'es' }}{{ choice.inputAddressCount > caseManualStartBtcVisualInputAddressLimit ? ` · first ${caseManualStartBtcVisualInputAddressLimit} shown visually` : '' }}</small>
              </button>
            </div>
          </div>

          <div
            v-if="caseManualStartHopInspectorOpen"
            ref="caseManualStartHopInspectorPanel"
            class="case-manual-start-hop-inspector"
            :class="[
              {
                'is-dragging': caseManualStartHopInspectorDragging,
                'is-preview-mode': caseManualStartHopInspectorPreviewOpen && !caseManualStartHopInspectorQuickAutoMode,
                'is-loading-trace': caseManualStartHopInspectorTraceLoading && !caseManualStartHopInspectorQuickAutoMode,
                'is-quick-auto': caseManualStartHopInspectorQuickAutoMode
              },
              caseManualStartHopInspectorQuickAutoMode ? caseManualStartHopInspectorQuickAutoPhaseClass : ''
            ]"
            :style="caseManualStartHopInspectorStyle"
            @click.stop
            @pointerdown.stop
          >
            <div class="case-manual-start-hop-inspector__header case-manual-start-hop-inspector__drag-handle" title="Drag this panel inside the visualizer" @pointerdown="beginCaseManualStartHopInspectorDrag">
              <div>
                <strong v-if="caseManualStartHopInspectorQuickAutoMode">{{ caseManualStartHopInspectorCandidateLabel }}</strong>
                <strong v-else>{{ caseManualStartHopInspectorTraceLoading ? caseManualStartHopInspectorTraceLoadingTitle : (caseManualStartHopInspectorPreviewOpen ? caseManualStartHopInspectorPreviewTitle : 'Manual hop inspector') }}</strong>
                <small v-if="caseManualStartHopInspectorQuickAutoMode">Automatic flow analysis</small>
                <small v-else>{{ caseManualStartHopInspectorCandidateLabel }}</small>
              </div>
              <div v-if="!caseManualStartHopInspectorQuickAutoMode" class="case-manual-start-hop-inspector__header-actions">
                <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--compact" type="button" :disabled="caseManualStartHopInspectorRunning" title="Open the full Flow Solver panel for advanced/manual review." @pointerdown.stop @click.stop="openFullCaseHopSolverFromManualStart">Advanced</button>
                <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close manual hop inspector" @pointerdown.stop @click.stop="closeCaseManualStartHopInspector">×</button>
              </div>
            </div>
            <div
              v-if="caseManualStartHopInspectorQuickAutoMode"
              class="case-manual-start-hop-inspector__quick-auto"
              role="status"
              aria-live="polite"
              :aria-label="`${caseManualStartHopInspectorQuickAutoTitle}. ${caseManualStartHopInspectorQuickAutoDetail}`"
              @pointerdown.stop
            >
              <div class="case-manual-start-hop-inspector__quick-auto-visual" aria-hidden="true">
                <div class="case-manual-start-hop-inspector__quick-auto-orbit">
                  <span class="case-manual-start-hop-inspector__quick-auto-ring case-manual-start-hop-inspector__quick-auto-ring--outer"></span>
                  <span class="case-manual-start-hop-inspector__quick-auto-ring case-manual-start-hop-inspector__quick-auto-ring--middle"></span>
                  <span class="case-manual-start-hop-inspector__quick-auto-ring case-manual-start-hop-inspector__quick-auto-ring--inner"></span>
                  <span class="case-manual-start-hop-inspector__quick-auto-core">
                    <svg v-if="caseManualStartHopInspectorQuickAutoPhase === 'resolved'" viewBox="0 0 32 32" aria-hidden="true">
                      <path d="M8 16.5l5 5L24 10"></path>
                    </svg>
                    <svg v-else viewBox="0 0 32 32" aria-hidden="true">
                      <circle cx="7" cy="16" r="3.25"></circle>
                      <rect x="13" y="11.5" width="6" height="9" rx="1.8"></rect>
                      <circle cx="25" cy="16" r="3.25"></circle>
                      <path d="M10.5 16h2.5M19 16h2.5"></path>
                    </svg>
                  </span>
                  <span v-if="caseManualStartHopInspectorQuickAutoPhase === 'resolved'" class="case-manual-start-hop-inspector__quick-auto-success-burst"></span>
                </div>
                <div class="case-manual-start-hop-inspector__quick-auto-signal">
                  <span v-for="index in 9" :key="`quick-auto-signal-${index}`"></span>
                </div>
              </div>

              <div class="case-manual-start-hop-inspector__quick-auto-copy">
                <strong>{{ caseManualStartHopInspectorQuickAutoTitle }}</strong>
                <small>{{ caseManualStartHopInspectorQuickAutoDetail }}</small>
              </div>

              <div class="case-manual-start-hop-inspector__quick-auto-stages" aria-label="Automatic analysis progress">
                <span
                  v-for="stage in caseManualStartHopInspectorQuickAutoStages"
                  :key="stage.key"
                  :class="`is-${stage.state}`"
                  :title="stage.label"
                ><i></i></span>
              </div>

              <div
                class="case-manual-start-hop-inspector__quick-auto-progress"
                role="progressbar"
                :aria-valuenow="caseManualStartHopInspectorQuickAutoProgressPercent"
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <span :style="{ width: `${caseManualStartHopInspectorQuickAutoProgressPercent}%` }"></span>
              </div>

              <div v-if="caseManualStartHopInspectorQuickAutoPreviewDiagram.nodes.length" class="case-manual-start-hop-inspector__quick-auto-trace">
                <svg viewBox="0 0 300 88" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                  <path
                    v-for="edge in caseManualStartHopInspectorQuickAutoPreviewDiagram.edges"
                    :key="edge.key"
                    class="case-manual-start-hop-inspector__quick-auto-trace-edge"
                    :d="edge.path"
                  ></path>
                  <g
                    v-for="node in caseManualStartHopInspectorQuickAutoPreviewDiagram.nodes"
                    :key="node.key"
                    class="case-manual-start-hop-inspector__quick-auto-trace-node"
                    :class="[`is-${node.kind}`, node.directionClass || '']"
                    :transform="`translate(${node.x} ${node.y})`"
                  >
                    <title>{{ node.title }}</title>
                    <circle v-if="node.shape === 'circle'" r="7"></circle>
                    <polygon v-else-if="node.shape === 'diamond'" points="0,-8 8,0 0,8 -8,0"></polygon>
                    <g v-else-if="node.shape === 'swap'" class="case-manual-start-hop-inspector__quick-auto-trace-swap">
                      <circle cx="-4" cy="0" r="5.5"></circle>
                      <circle cx="4" cy="0" r="5.5"></circle>
                    </g>
                    <rect v-else x="-8" y="-6" width="16" height="12" rx="3"></rect>
                  </g>
                  <text
                    v-if="caseManualStartHopInspectorQuickAutoPreviewDiagram.overflowCount"
                    x="286"
                    y="13"
                    text-anchor="end"
                    class="case-manual-start-hop-inspector__quick-auto-trace-overflow"
                  >+{{ caseManualStartHopInspectorQuickAutoPreviewDiagram.overflowCount }}</text>
                </svg>
                <small>{{ caseManualStartHopInspectorQuickAutoPreviewCaption }}</small>
              </div>
              <div v-else class="case-manual-start-hop-inspector__quick-auto-activity" aria-hidden="true">
                <span v-for="index in 12" :key="`quick-auto-activity-${index}`"></span>
              </div>
            </div>
            <div v-else-if="caseManualStartHopInspectorTraceLoading" class="case-manual-start-hop-inspector__loading" @pointerdown.stop>
              <div class="case-manual-start-hop-inspector__loading-center">
                <div class="case-manual-start-hop-inspector__loading-orb" aria-hidden="true"></div>
                <strong>{{ caseManualStartHopInspectorTraceLoadingTitle }}</strong>
                <small>{{ caseManualStartHopInspectorTraceProgressMessage }}</small>
                <div
                  class="case-manual-start-hop-inspector__loading-track"
                  role="progressbar"
                  :aria-valuenow="caseManualStartHopInspectorTraceProgressPercentForAria"
                  :aria-valuemin="0"
                  :aria-valuemax="100"
                >
                  <span
                    class="case-manual-start-hop-inspector__loading-fill"
                    :style="{ width: `${caseManualStartHopInspectorTraceProgressBarWidth}%` }"
                  ></span>
                </div>
                <div class="case-manual-start-hop-inspector__loading-meta">
                  <span>{{ caseManualStartHopInspectorTraceProgressPercentText }}</span>
                  <span>{{ caseManualStartHopInspectorTraceProgressCountText }}</span>
                </div>
                <button class="toolbar-btn toolbar-btn--danger" type="button" :disabled="caseManualStartHopInspectorStopRequested" @pointerdown.stop @click.stop="requestCaseManualStartHopInspectorStopAndClosePreview">
                  {{ caseManualStartHopInspectorStopRequested ? 'Stopping…' : 'Halt fetch and close preview' }}
                </button>
              </div>
            </div>
            <div v-else-if="caseManualStartHopInspectorPreviewOpen" class="case-manual-start-hop-inspector__preview case-manual-start-hop-inspector__preview--takeover" :class="{ 'case-manual-start-hop-inspector__preview--dormant': caseManualStartHopInspectorPreviewIsDormant }" @pointerdown.stop>
              <div class="case-manual-start-hop-inspector__preview-header case-manual-start-hop-inspector__preview-header--compact">
                <div class="case-manual-start-hop-inspector__preview-headline">
                  <strong>{{ caseManualStartHopInspectorPreviewSummary }}</strong>
                  <small>{{ caseManualStartHopInspectorPreviewBody }}</small>
                </div>
                <div class="case-manual-start-hop-inspector__preview-header-actions">
                  <button class="case-manual-start-hop-inspector__return-dot" type="button" title="Return to selected deposit of interest" aria-label="Return to selected deposit of interest" @pointerdown.stop @click.stop="scrollCaseManualStartHopInspectorPreviewToDeposit">●</button>
                  <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close trace preview" @pointerdown.stop @click.stop="closeCaseManualStartHopInspectorPreview">×</button>
                </div>
              </div>
              <div class="case-manual-start-hop-inspector__preview-scroll">
              <div v-if="caseManualStartHopInspectorPreviewRequiresServiceOverride" class="case-manual-start-hop-inspector__override-warning">
                <strong>Potential hot wallet / service warning</strong>
                <span>Flow Solver previously flagged this address as an Unknown service or hot-wallet-like endpoint. This trace preview intentionally bypassed that assessor for this one user-confirmed run.</span>
              </div>
              <div class="case-manual-start-hop-inspector__preview-list">
                <article
                  v-for="item in caseManualStartHopInspectorPreviewItems"
                  :key="item.key"
                  :class="caseManualStartHopInspectorPreviewItemClass(item)"
                  :data-manual-hop-preview-deposit="item.isDeposit ? 'true' : null"
                  :title="item.hash ? 'Right-click to copy this transaction hash.' : ''"
                  @contextmenu.prevent="copyCaseManualStartHopInspectorPreviewHash(item)"
                >
                  <span class="case-manual-start-hop-inspector__preview-direction" :class="item.directionClass" :aria-label="item.directionLabel">{{ item.directionSymbol }}</span>
                  <div class="case-manual-start-hop-inspector__preview-card-body">
                    <header class="case-manual-start-hop-inspector__preview-card-header">
                      <span>
                        <span v-if="item.isDeposit" class="case-manual-start-hop-inspector__interest-star" title="Transaction of interest">★</span>
                        <span v-if="item.isBranch" class="case-manual-start-hop-inspector__branch-mark" title="Branch / followed exit">☘</span>
                        {{ item.title }}
                      </span>
                      <strong>{{ item.shortHash }}</strong>
                    </header>
                    <div class="case-manual-start-hop-inspector__preview-primary">
                      <strong>{{ item.primaryAmount }}</strong>
                      <span>{{ item.tokenSymbol }}</span>
                      <small>{{ item.dateLabel }}</small>
                    </div>
                    <div v-if="item.routeText" class="case-manual-start-hop-inspector__preview-route" :title="item.routeText">{{ item.routeText }}</div>
                    <div v-if="item.badges.length" class="case-manual-start-hop-inspector__preview-badges">
                      <span v-for="badge in item.badges" :key="`${item.key}-${badge}`" :title="badge">{{ badge }}</span>
                    </div>
                    <div v-if="item.isBtcLike" class="case-manual-start-hop-inspector__preview-btc-io">
                      <div class="case-manual-start-hop-inspector__preview-btc-list">
                        <strong>Inputs</strong>
                        <span v-for="entry in item.btcInputs" :key="`${item.key}-input-${entry.key}`">{{ entry.label }}</span>
                      </div>
                      <div class="case-manual-start-hop-inspector__preview-btc-list">
                        <strong>Outputs</strong>
                        <span v-for="entry in item.btcOutputs" :key="`${item.key}-output-${entry.key}`">{{ entry.label }}</span>
                      </div>
                    </div>
                    <details v-if="item.details.length" class="case-manual-start-hop-inspector__preview-details-toggle">
                      <summary>Details</summary>
                      <div class="case-manual-start-hop-inspector__preview-details">
                        <div
                          v-for="detail in item.details"
                          :key="`${item.key}-${detail.label}`"
                          class="case-manual-start-hop-inspector__preview-detail-row"
                        >
                          <span class="case-manual-start-hop-inspector__preview-detail-label">{{ detail.label }}</span>
                          <span class="case-manual-start-hop-inspector__preview-detail-value">{{ detail.value }}</span>
                        </div>
                      </div>
                    </details>
                  </div>
                </article>
              </div>
              <details v-if="caseManualStartHopInspectorPreviewEvidenceLines.length" class="case-manual-start-hop-inspector__preview-evidence case-manual-start-hop-inspector__preview-evidence--after-list">
                <summary>
                  <strong>Evidence / Flow Solver report</strong>
                  <span>{{ caseManualStartHopInspectorPreviewEvidenceLines.length }} item{{ caseManualStartHopInspectorPreviewEvidenceLines.length === 1 ? '' : 's' }}</span>
                </summary>
                <ul>
                  <li v-for="line in caseManualStartHopInspectorPreviewEvidenceLines" :key="line">{{ line }}</li>
                </ul>
              </details>
              </div>
              <div class="case-manual-start-hop-inspector__preview-actions">
                <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="(!caseManualStartHopInspectorPreviewHasRows && !caseManualStartHopInspectorPreviewIsDormant) || caseManualStartHopInspectorRunning" @click="confirmCaseManualStartHopInspectorPreviewApply">
                  {{ caseManualStartHopInspectorConfirmPreviewButtonLabel }}
                </button>
                <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseManualStartHopInspectorRunning" @click="closeCaseManualStartHopInspectorPreview">
                  Back to hop controls
                </button>
              </div>
            </div>
            <template v-else>
              <p class="case-manual-start-hop-inspector__status">
                {{ caseManualStartHopInspectorStatusText }}
              </p>
              <div v-if="caseManualStartHopInspectorBusyNoticeVisible" class="case-manual-start-hop-inspector__busy-notice" role="status" aria-live="polite">
                <span class="case-manual-start-hop-inspector__busy-spinner" aria-hidden="true"></span>
                <div>
                  <strong>{{ caseManualStartHopInspectorBusyNoticeTitle }}</strong>
                  <small>{{ caseManualStartHopInspectorBusyNoticeText }}</small>
                </div>
              </div>
              <div class="case-manual-start-hop-inspector__summary case-manual-start-hop-inspector__summary--simplified">
                <button
                  class="case-manual-start-hop-inspector__recommendation-card"
                  type="button"
                  :disabled="!caseManualStartHopInspectorRecommendationCardCanApply"
                  :title="caseManualStartHopInspectorRecommendationCardTitle"
                  @click="runCaseManualStartHopInspectorRecommendation({ apply: true, previewTrace: true })"
                >
                  <span>Recommendation</span>
                  <strong>{{ caseManualStartHopInspectorRecommendationTitle }}</strong>
                  <small v-if="!caseManualStartHopInspectorRecommendationCardCanApply">{{ caseManualStartHopInspectorRecommendationCardHelper }}</small>
                </button>
                <div class="case-manual-start-hop-inspector__check-card">
                  <span>Checks</span>
                  <strong><i :class="caseManualStartHopInspectorCheckDotClass" aria-hidden="true"></i>{{ caseManualStartHopInspectorCheckStatusTitle }}</strong>
                </div>
              </div>
              <div v-if="caseManualStartHopInspectorTraceOverrideNeeded" class="case-manual-start-hop-inspector__override-warning">
                <template v-if="caseManualStartTraceAnywayBlocked">
                  <strong>Trace-through is blocked for this address</strong>
                  <span>{{ caseManualStartTraceAnywayBlockedMessage }}</span>
                </template>
                <template v-else>
                  <strong>Trace-through is still available</strong>
                  <span>Flow Solver flagged this as a possible Unknown service / hot wallet. Trace preview will bypass that assessor for this one run and require acknowledgment before applying rows.</span>
                </template>
              </div>
              <div v-else-if="caseManualStartHopInspectorStagedOverrideRequiresPreviewAck" class="case-manual-start-hop-inspector__override-warning">
                <strong>Preview confirmation required</strong>
                <span>The staged trace was fetched through a hot-wallet warning override. Reopen Trace preview and confirm there before applying it to the visual.</span>
              </div>
              <div class="case-manual-start-hop-inspector__actions">
                <button class="toolbar-btn case-manual-start-hop-inspector__check-button" type="button" :disabled="!caseManualStartHopInspectorCanRun" @click="loadCaseManualStartHopInspectorCandidate({ force: true })">
                  {{ caseManualStartHopInspectorCheckButtonLabel }}
                </button>
                <span v-if="caseManualStartHopInspectorNeedsChecks" class="case-manual-start-hop-inspector__check-hint">Checks run automatically before actions unlock.</span>
                <button v-if="caseManualStartHopInspectorShouldShowActionButton('trace-through')" class="toolbar-btn toolbar-btn--done" :class="{ 'case-manual-start-hop-inspector__recommended-action': caseManualStartHopInspectorActionIsRecommended('trace-through') }" type="button" :disabled="!caseManualStartHopInspectorCanAct || caseManualStartTraceAnywayBlocked" :title="caseManualStartTraceAnywayBlocked ? caseManualStartTraceAnywayBlockedMessage : ''" @click="runCaseManualStartHopInspectorToolAction('trace-through', { apply: false, preview: true })">
                  <span>{{ caseManualStartHopInspectorTraceOverrideNeeded ? 'Trace anyway preview' : 'Trace preview' }}</span>
                </button>
                <button v-if="caseManualStartHopInspectorShouldShowActionButton('path-ending')" class="toolbar-btn toolbar-btn--done" :class="{ 'case-manual-start-hop-inspector__recommended-action': caseManualStartHopInspectorActionIsRecommended('path-ending') }" type="button" :disabled="!caseManualStartHopInspectorCanAct" @click="runCaseManualStartHopInspectorToolAction('path-ending', { apply: true })">
                  <span>End path + apply</span>
                </button>
                <button v-if="caseManualStartHopInspectorShouldShowActionButton('swap')" class="toolbar-btn toolbar-btn--done" :class="{ 'case-manual-start-hop-inspector__recommended-action': caseManualStartHopInspectorActionIsRecommended('swap') }" type="button" :disabled="!caseManualStartHopInspectorCanAct" @click="runCaseManualStartHopInspectorToolAction('swap', { apply: true, preview: true })">
                  <span>Swap + apply</span>
                </button>
                <button v-if="caseManualStartHopInspectorShouldShowActionButton('bridge')" class="toolbar-btn toolbar-btn--done" :class="{ 'case-manual-start-hop-inspector__recommended-action': caseManualStartHopInspectorActionIsRecommended('bridge') }" type="button" :disabled="!caseManualStartHopInspectorCanAct" @click="runCaseManualStartHopInspectorToolAction('bridge', { apply: true, preview: true })">
                  <span>Bridge + apply</span>
                </button>
                <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseManualStartHopInspectorHasOutputRows || caseManualStartHopInspectorRunning || caseManualStartHopInspectorStagedOverrideRequiresPreviewAck" @click="applyCaseManualStartHopInspectorOutputRows">
                  Apply staged result
                </button>
                <button v-if="caseManualStartHopInspectorRunning || caseManualStartHopInspectorStopRequested" class="toolbar-btn" type="button" :disabled="!caseManualStartHopInspectorRunning || caseManualStartHopInspectorStopRequested" @click="requestCaseManualStartHopInspectorStop">
                  Stop
                </button>
              </div>
            </template>

          </div>

          <div
            v-if="caseInvestigationVisualizerManualStartMode && caseManualSolveIntroOpen"
            class="case-manual-solve-intro"
            role="presentation"
            @click.stop
            @pointerdown.stop
          >
            <section
              class="case-manual-solve-intro__card"
              role="dialog"
              aria-modal="true"
              aria-labelledby="case-manual-solve-intro-title"
              aria-describedby="case-manual-solve-intro-description"
              @keydown.esc.stop.prevent="dismissCaseManualSolveIntro"
            >
              <button
                class="case-manual-solve-intro__close"
                type="button"
                aria-label="Close Flow Solver introduction"
                @click="dismissCaseManualSolveIntro"
              >
                <span aria-hidden="true"></span>
              </button>

              <div class="case-manual-solve-intro__heading">
                <span class="case-manual-solve-intro__eyebrow">Flow Solver</span>
                <h3 id="case-manual-solve-intro-title">Follow every starting transaction to a meaningful ending</h3>
                <p id="case-manual-solve-intro-description">
                  Each starting transaction has its own graph. Flow Solver can work through one graph at a time, or guide Auto through every graph in the case.
                </p>
              </div>

              <div class="case-manual-solve-intro__choices">
                <article class="case-manual-solve-intro__choice--recommended">
                  <span class="case-manual-solve-intro__choice-number">1</span>
                  <div>
                    <strong>Fastest: solve every graph</strong>
                    <p>Case Buddy runs Auto on the graph you are viewing, moves through the remaining starting graphs, and tells you clearly when the case is ready to continue.</p>
                  </div>
                </article>
                <article>
                  <span class="case-manual-solve-intro__choice-number">2</span>
                  <div>
                    <strong>Work one graph at a time</strong>
                    <p>Use Auto for the current graph, Next for one unresolved ending, or select an ending and use the Manual hop inspector when you want to review each hop.</p>
                  </div>
                </article>
              </div>

              <p class="case-manual-solve-intro__note">
                Guided Auto can be stopped at any time. If one graph reaches a safety limit or needs a manual choice, Case Buddy sets it aside for review and continues with the remaining graphs.
              </p>

              <div class="case-manual-solve-intro__actions">
                <button
                  ref="caseManualSolveIntroGotItButton"
                  class="toolbar-btn toolbar-btn--ghost"
                  type="button"
                  @click="dismissCaseManualSolveIntro"
                >
                  Got it
                </button>
                <button
                  ref="caseManualSolveIntroCurrentAutoButton"
                  class="toolbar-btn toolbar-btn--primary"
                  type="button"
                  :disabled="!caseManualStartViewportAutoCanStart || caseManualStartViewportAutoActive || caseManualStartAllFlowsAutoActive"
                  :title="caseManualStartViewportAutoButtonTitle"
                  @click="startCaseManualSolveIntroAuto"
                >
                  Auto this graph
                </button>
                <button
                  v-if="caseManualStartAllFlowsAutoEnabled && caseInvestigationVisualizerFlows.length > 1"
                  ref="caseManualSolveIntroAllFlowsButton"
                  class="toolbar-btn toolbar-btn--accent case-manual-solve-intro__auto-all"
                  type="button"
                  :disabled="!caseManualStartAllFlowsAutoCanStart"
                  :title="caseManualStartAllFlowsAutoButtonTitle"
                  @click="startCaseManualSolveIntroAllFlowsAuto"
                >
                  {{ caseManualStartAllFlowsAutoButtonLabel }}
                </button>
              </div>

              <label class="case-manual-solve-intro__preference">
                <input v-model="caseManualSolveIntroDontShowAgain" type="checkbox" />
                <span class="case-manual-solve-intro__toggle" aria-hidden="true"><span></span></span>
                <span>Don’t show this again for this case</span>
              </label>
            </section>
          </div>
        </div>
      </section>

      <div
        class="case-investigation-visualizer-modal__side-resizer"
        :class="{ 'is-resizing': caseInvestigationVisualizerSidePanelResizing, 'is-locked': caseInvestigationVisualizerInteractionLocked }"
        role="separator"
        aria-orientation="vertical"
        :aria-valuenow="Math.round(caseInvestigationVisualizerSidePanelClampedWidth)"
        aria-label="Resize investigation details panel"
        tabindex="0"
        title="Drag to resize the selection and case-actions panel. Double-click to reset."
        @pointerdown.stop.prevent="beginCaseInvestigationVisualizerSidePanelResize"
        @dblclick.stop.prevent="resetCaseInvestigationVisualizerSidePanelWidth"
        @keydown="adjustCaseInvestigationVisualizerSidePanelWidthFromKeyboard"
      >
        <span aria-hidden="true"></span>
      </div>

      <aside class="case-flow-visuals-modal__side-panel case-investigation-visualizer-modal__side-panel">
        <section class="template-deliverable-modal__preview-card case-flow-visuals-modal__selection-card">
          <div class="template-deliverable-modal__preview-header">
            <div>
              <strong>{{ caseInvestigationVisualizerSelectionTitle }}</strong>
              <small>{{ caseInvestigationVisualizerSelectionHelpText }}</small>
            </div>
          </div>
          <div v-if="caseInvestigationVisualizerSelectedNodeRows.length" class="case-flow-visuals-detail-table-wrap">
            <table class="case-flow-visuals-detail-table">
              <tbody>
                <tr v-for="(row, rowIndex) in caseInvestigationVisualizerSelectedNodeRows" :key="caseManualSelectedDetailRowKey(row, rowIndex)">
                  <th>{{ row[0] }}</th>
                  <td :title="caseManualSelectedDetailRowTitle(row)">
                    <div v-if="caseManualSolveCanEditSelectedAmountRow(row[0])" class="case-manual-solve-selected-value">
                      <span class="case-manual-solve-selected-value__text">{{ caseManualSelectedDetailRowDisplayValue(row) }}</span>
                      <button
                        class="case-manual-solve-selected-value__edit"
                        type="button"
                        :disabled="caseInvestigationVisualizerInteractionLocked"
                        :title="caseManualSolveAmountEditButtonTitle(row[0])"
                        :aria-label="caseManualSolveAmountEditButtonTitle(row[0])"
                        @click.stop="openCaseManualSolveAmountEditor(row[0])"
                      >
                        <svg
                          class="case-manual-solve-selected-value__edit-icon"
                          aria-hidden="true"
                          viewBox="0 0 20 20"
                          focusable="false"
                        >
                          <path d="M4.2 13.7 3.4 16.6l2.9-.8 9.1-9.1-2.1-2.1-9.1 9.1Zm10.2-10.2 2.1 2.1.64-.64a1.5 1.5 0 0 0 0-2.12l-.02-.02a1.5 1.5 0 0 0-2.12 0l-.6.68Z" />
                        </svg>
                      </button>
                    </div>
                    <div v-else-if="caseManualCanOpenFifoInspectorRow(row[0])" class="case-manual-solve-selected-value case-manual-solve-selected-value--fifo">
                      <span class="case-manual-solve-selected-value__text">{{ caseManualSelectedDetailRowDisplayValue(row) }}</span>
                      <button
                        class="case-manual-solve-selected-value__inspect"
                        type="button"
                        :disabled="false"
                        title="Open FIFO calculation inspector"
                        aria-label="Open FIFO calculation inspector"
                        @click.stop="openCaseManualFifoInspectorFromSelection(row)"
                      >
                        Inspect
                      </button>
                    </div>
                    <template v-else>{{ caseManualSelectedDetailRowDisplayValue(row) }}</template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="caseManualSolveAmountEditor.open" class="case-manual-solve-amount-editor">
            <div class="case-manual-solve-amount-editor__header">
              <div>
                <strong>{{ caseManualSolveAmountEditorTitle }}</strong>
                <small>{{ caseManualSolveAmountEditorContext }}</small>
              </div>
              <button class="case-manual-solve-amount-editor__close" type="button" aria-label="Close amount editor" @click="closeCaseManualSolveAmountEditor">
                <span aria-hidden="true"></span>
              </button>
            </div>
            <label class="case-manual-solve-amount-editor__field">
              <span>{{ caseManualSolveAmountEditorInputLabel }}</span>
              <div class="case-manual-solve-amount-editor__input-row">
                <input
                  ref="caseManualSolveAmountEditorInput"
                  v-model.trim="caseManualSolveAmountEditor.draft"
                  type="text"
                  inputmode="decimal"
                  spellcheck="false"
                  placeholder="0.00"
                  @keydown.enter.prevent="applyCaseManualSolveAmountEdit"
                />
                <span v-if="caseManualSolveAmountEditorToken">{{ caseManualSolveAmountEditorToken }}</span>
              </div>
            </label>
            <p class="case-manual-solve-amount-editor__help">{{ caseManualSolveAmountEditorHelpText }}</p>
            <p v-if="caseManualSolveAmountEditor.status" class="case-manual-solve-amount-editor__status">{{ caseManualSolveAmountEditor.status }}</p>
            <div class="case-manual-solve-amount-editor__actions">
              <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!caseManualSolveAmountEditorCanApply || caseInvestigationVisualizerInteractionLocked" @click="applyCaseManualSolveAmountEdit">Apply</button>
              <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="closeCaseManualSolveAmountEditor">Cancel</button>
            </div>
          </div>


          <div v-if="!caseInvestigationVisualizerSelectedNodeRows.length" class="network-lookup-modal__empty-state">
            Nothing selected yet. Right-click and drag to select multiple nodes, or left-click a node to select and move it.
          </div>
        </section>

        <section class="template-deliverable-modal__preview-card case-investigation-visualizer-modal__actions-card">
          <div class="template-deliverable-modal__preview-header">
            <div>
              <strong>Case actions</strong>
              <small>Open existing Case Buddy operations from a valid selected node.</small>
            </div>
          </div>
          <p class="case-investigation-visualizer-modal__action-summary">{{ caseInvestigationVisualizerActionSummary }}</p>
          <div class="case-investigation-visualizer-modal__case-actions">
            <button class="toolbar-btn" type="button" :disabled="!caseInvestigationVisualizerSelectedCandidateView || !caseInvestigationVisualizerSelectedCandidateView.canPrune || caseInvestigationVisualizerInteractionLocked" @click="openPathOperationFromInvestigationVisualizer('prune')">Prune path</button>
            <button class="toolbar-btn" type="button" :disabled="!caseInvestigationVisualizerSelectedCandidateView || !caseInvestigationVisualizerSelectedCandidateView.canGraft || caseInvestigationVisualizerInteractionLocked" @click="openPathOperationFromInvestigationVisualizer('graft')">Graft from here</button>
            <button class="toolbar-btn" type="button" :disabled="!caseInvestigationVisualizerSelectedCandidateView || !caseInvestigationVisualizerSelectedCandidateView.canBridge || caseInvestigationVisualizerInteractionLocked" @click="openPathOperationFromInvestigationVisualizer('bridge')">Bridge graft</button>
            <button v-if="caseInvestigationVisualizerManualStartMode" class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!caseManualStartHopInspectorCanRun || caseInvestigationVisualizerInteractionLocked" :title="caseManualStartHopInspectorSolveTitle" @click="openCaseManualStartHopInspectorFromSelection">Solve hop</button>
            <button v-if="caseManualStartOutputChoiceShouldShow" class="toolbar-btn toolbar-btn--accent" type="button" :disabled="!caseManualStartOutputChoiceCanOpen || caseInvestigationVisualizerInteractionLocked" @click="openCaseManualStartOutputChoiceFromSelection">{{ caseManualStartOutputChoiceButtonLabel }}</button>
            <button
              v-if="caseInvestigationVisualizerManualStartMode && caseManualStartSelectedCandidateReportBoundaryActionVisible"
              class="toolbar-btn"
              :class="caseManualStartSelectedCandidateAcceptedReportBoundary ? 'toolbar-btn--ghost' : 'toolbar-btn--accent'"
              type="button"
              :disabled="!caseManualStartSelectedCandidateCanToggleReportBoundary"
              :title="caseManualStartSelectedCandidateAcceptedReportBoundary ? 'Remove the accepted report boundary so this ending blocks completion again.' : 'Use this incomplete ending as a clearly disclosed report boundary without claiming that tracing continued beyond it.'"
              @click="toggleCaseManualStartSelectedReportBoundary"
            >
              {{ caseManualStartSelectedCandidateAcceptedReportBoundary ? 'Reopen for analysis' : 'Accept as report boundary' }}
            </button>
            <button v-if="caseInvestigationVisualizerManualStartMode" class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseManualStartSingleSelectedCandidate || caseInvestigationVisualizerInteractionLocked" @click="openFullCaseHopSolverFromManualStart">Open full Flow Solver</button>
            <button v-if="caseInvestigationVisualizerManualStartMode" class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseInvestigationVisualizerInteractionLocked" @click="openCaseManualStartSettings">Manual Solve settings</button>
            <button v-if="caseInvestigationVisualizerManualStartMode && caseInvestigationVisualizerSelectedCount" class="toolbar-btn toolbar-btn--ghost" :class="{ 'toolbar-btn--active': caseManualSolveVisualColorPanelOpen }" type="button" :disabled="caseInvestigationVisualizerInteractionLocked" @click="toggleCaseManualSolveVisualColorPanel">
              {{ caseManualSolveVisualColorPanelOpen ? 'Hide color tools' : 'Color selected nodes' }}
            </button>
            <button v-if="caseInvestigationVisualizerManualStartMode" class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseManualSolveCanGroupSelection || caseInvestigationVisualizerInteractionLocked" title="Create one visual-only group from the selected nodes. Main and Raw Data are not changed." @click="groupCaseManualSolveSelectedNodes">
              Group selected
            </button>
            <button v-if="caseInvestigationVisualizerManualStartMode && caseManualSolveSelectedGroups.length" class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseInvestigationVisualizerInteractionLocked" title="Open the selected visual group as a temporary square panel." @click="openCaseManualSolveExpandedGroupPanel(caseManualSolveSelectedGroup)">
              Open group
            </button>
            <button v-if="caseInvestigationVisualizerManualStartMode && caseManualSolveSelectedGroups.length" class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseInvestigationVisualizerInteractionLocked" title="Ungroup selected visual groups. Main and Raw Data are not changed." @click="ungroupCaseManualSolveSelectedGroups">
              Ungroup
            </button>
            <button class="toolbar-btn toolbar-btn--monitoring" type="button" :disabled="!caseAddressMonitoringSelectedPayloads.length || caseAddressMonitoringBusy || caseInvestigationVisualizerInteractionLocked" :title="caseAddressMonitoringSelectedTitle" @click="addSelectedCaseVisualizerNodesToMonitoring">
              {{ caseAddressMonitoringSelectedPayloads.length > 1 ? `Add ${caseAddressMonitoringSelectedPayloads.length} to monitoring` : 'Add to monitoring' }}
            </button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="openCaseAddressMonitoringModal">Open monitoring</button>
            <button v-if="!caseInvestigationVisualizerManualStartMode" class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!caseInvestigationVisualizerCanSolveSelectedHopPlaceholder || caseInvestigationVisualizerInteractionLocked" @click="openCaseHopSolverFromInvestigationVisualizer">Solve hop</button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseInvestigationVisualizerSelectedCandidateView || caseInvestigationVisualizerInteractionLocked" @click="selectInvestigationVisualizerCandidateInMainData">Select row</button>
          </div>
          <div v-if="caseInvestigationVisualizerManualStartMode && caseInvestigationVisualizerSelectedCount && caseManualSolveVisualColorPanelOpen" class="case-manual-solve-visual-color-card">
            <div class="case-manual-solve-visual-color-card__header">
              <div>
                <strong>Recolor selected</strong>
                <small>{{ caseManualSolveVisualColorSelectionSummary }}</small>
              </div>
              <button
                class="case-manual-solve-visual-color-card__swatch case-manual-solve-visual-color-card__swatch-button"
                type="button"
                :style="{ backgroundColor: caseManualSolveVisualNormalizedDraftColor || caseManualSolveVisualColorDraft }"
                :disabled="caseInvestigationVisualizerInteractionLocked"
                title="Open color picker"
                aria-label="Open Manual Solve visual color picker"
                @click="openCaseManualSolveVisualColorPicker"
              ></button>
            </div>
            <div class="case-manual-solve-visual-color-card__controls">
              <input ref="caseManualSolveVisualColorPicker" v-model="caseManualSolveVisualColorDraft" class="case-manual-solve-visual-color-card__picker" type="color" aria-label="Manual Solve visual node color" />
              <input v-model.trim="caseManualSolveVisualColorDraft" class="case-manual-solve-visual-color-card__hex" type="text" inputmode="text" spellcheck="false" placeholder="#93c5fd" aria-label="Manual Solve visual color hex" />
            </div>
            <div class="case-manual-solve-visual-color-card__actions">
              <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!caseManualSolveVisualColorCanApply || caseInvestigationVisualizerInteractionLocked" @click="applyManualSolveSelectedNodeColor(caseManualSolveVisualColorDraft)">Apply color</button>
              <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseManualSolveVisualColorHasSelectedOverrides || caseInvestigationVisualizerInteractionLocked" @click="resetManualSolveSelectedNodeColors">Reset selected colors</button>
              <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseManualSolveVisualColorHasAnyOverrides || caseInvestigationVisualizerInteractionLocked" @click="resetManualSolveAllNodeColorsForCurrentVisual">Reset all colors in this visual</button>
            </div>
            <p class="case-manual-solve-visual-color-card__note">Visual annotation only. This does not change Main Data, Raw Data, path-ending status, attribution, or Flow Solver recommendations.</p>
            <p v-if="caseManualSolveVisualColorStatus" class="case-manual-solve-visual-color-card__status">{{ caseManualSolveVisualColorStatus }}</p>
          </div>
          <div v-if="caseInvestigationVisualizerManualStartMode && caseManualSolveSelectedGroup" class="case-manual-solve-visual-color-card case-manual-solve-visual-group-card">
            <div class="case-manual-solve-visual-color-card__header">
              <div>
                <strong>Selected visual group</strong>
                <small>{{ caseManualSolveSelectedGroup.memberSummary || 'Visual-only node group' }}</small>
              </div>
              <span class="case-manual-solve-visual-color-card__swatch" :style="{ backgroundColor: caseManualSolveVisualGroupNormalizedColor || caseManualSolveVisualGroupColorDraft || caseManualSolveSelectedGroup.color }" aria-hidden="true"></span>
            </div>
            <div class="case-manual-solve-visual-group-card__controls">
              <label>
                <span>Name</span>
                <input v-model.trim="caseManualSolveVisualGroupNameDraft" type="text" maxlength="48" placeholder="Group name" />
              </label>
              <label>
                <span>Color</span>
                <input v-model="caseManualSolveVisualGroupColorDraft" type="color" />
              </label>
            </div>
            <div class="case-manual-solve-visual-color-card__actions">
              <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="caseInvestigationVisualizerInteractionLocked" @click="applyCaseManualSolveSelectedGroupDetails">Apply group details</button>
              <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseInvestigationVisualizerInteractionLocked" @click="openCaseManualSolveExpandedGroupPanel(caseManualSolveSelectedGroup)">Open square</button>
              <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseInvestigationVisualizerInteractionLocked" @click="ungroupCaseManualSolveSelectedGroups">Ungroup</button>
            </div>
            <p class="case-manual-solve-visual-color-card__note">Groups are visual-only metadata for this Manual Solve viewport. They do not change rows, path labels, Flow Solver state, or deliverables.</p>
            <p v-if="caseManualSolveVisualGroupStatus" class="case-manual-solve-visual-color-card__status">{{ caseManualSolveVisualGroupStatus }}</p>
          </div>
        </section>

        <section class="template-deliverable-modal__preview-card case-flow-visuals-modal__flow-list-card">
          <div class="template-deliverable-modal__preview-header case-manual-solve-graph-list__header">
            <div>
              <strong>Graph list</strong>
              <small>{{ caseInvestigationVisualizerFlows.length }} visualizable graph{{ caseInvestigationVisualizerFlows.length === 1 ? '' : 's' }}</small>
            </div>
            <button
              v-if="caseInvestigationVisualizerManualStartMode && caseManualStartAllFlowsAutoEnabled && caseInvestigationVisualizerFlows.length > 1"
              class="toolbar-btn toolbar-btn--accent case-manual-solve-graph-list__auto-all"
              type="button"
              :disabled="!caseManualStartAllFlowsAutoCanStart"
              :title="caseManualStartAllFlowsAutoButtonTitle"
              @click="startCaseManualStartAllFlowsAuto({ source: 'graph-list' })"
            >
              Auto all graphs
            </button>
          </div>
          <div class="case-flow-visuals-flow-list">
            <button
              v-for="(flow, flowIndex) in caseInvestigationVisualizerFlows"
              :key="flow.key || flowIndex"
              class="case-flow-visuals-flow-button"
              :class="{ 'is-active': flowIndex === caseInvestigationVisualizerActiveIndex && caseInvestigationVisualizerViewMode === 'single' }"
              type="button"
              :disabled="caseInvestigationVisualizerInteractionLocked"
              @click="renderCaseInvestigationVisualizerAtIndex(flowIndex, { mode: 'single' })"
            >
              <strong>{{ flow.mainLabel || `Flow ${flowIndex + 1}` }}</strong>
              <span>{{ flow.entries.length }} entr{{ flow.entries.length === 1 ? 'y' : 'ies' }} · {{ flow.mainHash }}</span>
              <small
                v-if="caseInvestigationVisualizerManualStartMode"
                class="case-manual-solve-graph-list__status"
                :class="caseManualStartAllFlowsFlowStatusClass(flow)"
              >
                {{ caseManualStartAllFlowsFlowStatusLabel(flow) }}
              </small>
            </button>
          </div>
        </section>
      </aside>
    </div>

    <template v-if="!caseInvestigationVisualizerManualStartMode">
      <div
        class="case-investigation-visualizer-modal__advanced-resizer"
        :class="{ 'is-active': caseInvestigationVisualizerAdvancedResizing }"
        role="separator"
        aria-orientation="horizontal"
        :aria-valuemin="caseInvestigationVisualizerAdvancedBounds.min"
        :aria-valuemax="caseInvestigationVisualizerAdvancedBounds.max"
        :aria-valuenow="Math.round(caseInvestigationVisualizerAdvancedClampedHeight)"
        aria-label="Resize investigation visual controls"
        tabindex="0"
        @pointerdown="beginCaseInvestigationVisualizerAdvancedResize"
        @keydown="adjustCaseInvestigationVisualizerAdvancedHeightFromKeyboard"
      >
        <span>Drag to resize controls</span>
      </div>

      <details
        class="case-flow-visuals-modal__advanced case-investigation-visualizer-modal__advanced"
        :class="{ 'is-locked': caseInvestigationVisualizerInteractionLocked }"
        :open="caseInvestigationVisualizerAdvancedOpen"
        :style="caseInvestigationVisualizerAdvancedStyle"
        @toggle="caseInvestigationVisualizerAdvancedOpen = $event.target.open"
      >
        <summary>
          <span>Investigation visual controls</span>
          <small>Standard visual action settings. Manual Solve keeps a separate display setting set.</small>
        </summary>
        <div class="case-flow-visuals-modal__advanced-grid">
          <label>
            <span>Layout pattern</span>
            <select v-model="caseInvestigationVisualizerOptions.layoutMode" @change="refreshCurrentCaseInvestigationVisualizer">
              <option value="upwardBranches">UpwardBranches</option>
              <option value="balanced">Balanced</option>
              <option value="steppedUpward">Stepped upward</option>
            </select>
          </label>
          <label>
            <span>Same-address merge</span>
            <select v-model="caseInvestigationVisualizerOptions.mergeAddressMode" @change="refreshCurrentCaseInvestigationVisualizer">
              <option value="none">Off</option>
              <option value="sameColumnMiddle">Same column middle</option>
              <option value="globalAverage">Global average</option>
            </select>
          </label>
          <label class="network-lookup-modal__checkbox">
            <input v-model="caseInvestigationVisualizerOptions.labelAddresses" type="checkbox" @change="refreshCurrentCaseInvestigationVisualizer" />
            <span>Use available address labels</span>
          </label>
          <label class="network-lookup-modal__checkbox">
            <input v-model="caseInvestigationVisualizerOptions.showFundsFollowedBars" type="checkbox" @change="refreshCurrentCaseInvestigationVisualizer" />
            <span>Show funds-followed bars</span>
          </label>
          <label class="network-lookup-modal__checkbox">
            <input v-model="caseInvestigationVisualizerOptions.mergeTransactionNodes" type="checkbox" @change="refreshCurrentCaseInvestigationVisualizer" />
            <span>Merge repeated transaction hashes</span>
          </label>
          <label class="network-lookup-modal__checkbox">
            <input v-model="caseInvestigationVisualizerOptions.wrapLinearTrunks" type="checkbox" @change="refreshCurrentCaseInvestigationVisualizer" />
            <span>Wrap long linear trunks</span>
          </label>
          <label class="network-lookup-modal__checkbox">
            <input v-model="caseInvestigationVisualizerOptions.cleanupOverlaps" type="checkbox" @change="refreshCurrentCaseInvestigationVisualizer" />
            <span>Automatic cleanup</span>
          </label>
          <label class="network-lookup-modal__checkbox">
            <input v-model="caseInvestigationVisualizerOptions.highlightSwaps" type="checkbox" @change="refreshCurrentCaseInvestigationVisualizer" />
            <span>Highlight swaps</span>
          </label>
          <label class="network-lookup-modal__checkbox">
            <input v-model="caseInvestigationVisualizerOptions.highlightBridges" type="checkbox" @change="refreshCurrentCaseInvestigationVisualizer" />
            <span>Highlight bridges</span>
          </label>
          <label>
            <span>Minimum trunk length</span>
            <input v-model.number="caseInvestigationVisualizerOptions.trunkWrapMinLength" type="number" min="2" max="100" @change="refreshCurrentCaseInvestigationVisualizer" />
          </label>
          <label>
            <span>First snake row length</span>
            <input v-model.number="caseInvestigationVisualizerOptions.trunkWrapFirstRowLength" type="number" min="1" max="20" @change="refreshCurrentCaseInvestigationVisualizer" />
          </label>
          <label>
            <span>Later snake row length</span>
            <input v-model.number="caseInvestigationVisualizerOptions.trunkWrapOtherRowLength" type="number" min="1" max="20" @change="refreshCurrentCaseInvestigationVisualizer" />
          </label>
          <label>
            <span>Snake row spacing</span>
            <input v-model.number="caseInvestigationVisualizerOptions.trunkWrapRowVerticalMultiplier" type="number" min="1" max="4" step="0.05" @change="refreshCurrentCaseInvestigationVisualizer" />
          </label>
        </div>
      </details>
    </template>

    <template v-else-if="!caseManualStartQuickLookupMode">
      <div
        class="case-investigation-visualizer-modal__advanced-resizer case-manual-solve-bottom-resizer"
        :class="{ 'is-active': caseManualSolveBottomPanelResizing, 'is-collapsed': !caseManualSolveBottomPanelHasVisibleContent }"
        role="separator"
        aria-orientation="horizontal"
        :aria-valuemin="caseManualSolveBottomPanelBounds.min"
        :aria-valuemax="caseManualSolveBottomPanelBounds.max"
        :aria-valuenow="Math.round(caseManualSolveBottomPanelClampedHeight)"
        aria-label="Resize Manual Solve lower panel"
        tabindex="0"
        :title="caseManualSolveBottomPanelResizerTitle"
        @pointerdown.stop.prevent="beginCaseManualSolveBottomPanelResize"
        @dblclick.stop.prevent="resetCaseManualSolveBottomPanelHeight"
        @keydown="adjustCaseManualSolveBottomPanelHeightFromKeyboard"
      >
        <span>{{ caseManualSolveBottomPanelResizerLabel }}</span>
      </div>

      <section
        v-if="caseManualSolveEvaluationOpen"
        class="case-manual-solve-evaluation-panel case-manual-solve-bottom-panel"
        :class="{ 'is-resizing': caseManualSolveBottomPanelResizing }"
        :style="caseManualSolveBottomPanelStyle"
        aria-label="Manual Solve flow evaluation"
      >
        <header class="case-manual-solve-evaluation-panel__header">
          <div>
            <span>Manual Solve flow evaluator</span>
            <strong>{{ caseManualSolveEvaluationState.flowLabel || 'Current flow' }}</strong>
            <small>{{ caseManualSolveEvaluationState.summary }}</small>
          </div>
          <button class="case-manual-solve-evaluation-panel__close" type="button" aria-label="Close Manual Solve flow evaluator" @click="closeCaseManualSolveEvaluationPanel">
            <span aria-hidden="true"></span>
          </button>
        </header>

        <div v-if="!caseManualSolveEvaluationState.canEvaluate" class="case-manual-solve-evaluation-panel__empty">
          {{ caseManualSolveEvaluationState.problemText || 'No Manual Solve flow is available to evaluate yet.' }}
        </div>

        <template v-else>
          <div class="case-manual-solve-evaluation-panel__metrics">
            <article>
              <span>Initial amount followed</span>
              <strong>{{ caseManualSolveEvaluationState.initialAmountDisplay || '—' }}</strong>
              <small>{{ caseManualSolveEvaluationState.initialUsdDisplay || caseManualSolveEvaluationState.referenceStatus || 'USD estimate unavailable' }}</small>
            </article>
            <article>
              <span>Current furthest endpoints</span>
              <strong>{{ caseManualSolveEvaluationState.currentUsdDisplay }}</strong>
              <small>{{ caseManualSolveEvaluationState.endpointCount }} endpoint{{ caseManualSolveEvaluationState.endpointCount === 1 ? '' : 's' }} · {{ caseManualSolveEvaluationState.resolvedValuationCount }}/{{ caseManualSolveEvaluationState.endpointCount }} valued</small>
            </article>
            <article class="case-manual-solve-evaluation-panel__metric--percent">
              <span>Current / initial</span>
              <strong>{{ caseManualSolveEvaluationState.coveragePercentDisplay }}</strong>
              <small>{{ caseManualSolveEvaluationState.coverageDetail }}</small>
            </article>
          </div>

          <section class="case-manual-solve-evaluation-panel__heatmap" :class="{ 'is-active': caseManualSolveHeatMapActive }" aria-label="Manual Solve heat map controls">
            <div class="case-manual-solve-evaluation-panel__heatmap-main">
              <label class="network-lookup-modal__checkbox case-manual-solve-evaluation-panel__heatmap-toggle">
                <input
                  v-model="caseManualSolveHeatMapEnabled"
                  type="checkbox"
                  :disabled="!caseManualSolveHeatMapState.canHeatMap"
                />
                <span>Heat map viewport</span>
              </label>
              <small>{{ caseManualSolveHeatMapState.summary }}</small>
            </div>
            <label class="case-manual-solve-evaluation-panel__heatmap-slider">
              <span>Sensitivity {{ caseManualSolveHeatMapSensitivityDisplay }}</span>
              <input
                v-model.number="caseManualSolveHeatMapSensitivity"
                type="range"
                min="0.35"
                max="2.5"
                step="0.05"
                :disabled="!caseManualSolveHeatMapState.canHeatMap"
              />
            </label>
            <div class="case-manual-solve-evaluation-panel__heatmap-legend" aria-hidden="true">
              <span
                v-for="item in caseManualSolveHeatMapLegendRows"
                :key="item.key"
                :style="{ background: item.color }"
                :title="item.label"
              ></span>
            </div>
          </section>

          <div class="case-manual-solve-evaluation-panel__body">
            <section class="case-manual-solve-evaluation-panel__card case-manual-solve-evaluation-panel__card--tokens">
              <header>
                <strong>Token tallies</strong>
                <small>{{ caseManualSolveEvaluationState.tokenBreakdownText || 'No endpoint token amounts found.' }}</small>
              </header>
              <div v-if="caseManualSolveEvaluationState.tokenRows.length" class="case-manual-solve-evaluation-panel__token-list">
                <span v-for="row in caseManualSolveEvaluationState.tokenRows" :key="row.token">
                  <strong>{{ row.amountDisplay }}</strong>
                  <small>{{ row.token }}</small>
                  <em>{{ row.usdDisplay || 'USD unresolved' }}</em>
                </span>
              </div>
              <p v-else>No furthest endpoint token amounts were available for this flow.</p>
            </section>

            <section class="case-manual-solve-evaluation-panel__card case-manual-solve-evaluation-panel__card--endings">
              <header>
                <strong>Furthest endpoints</strong>
                <small>Resolved and unresolved current endings in this Manual Solve flow.</small>
              </header>
              <div class="case-manual-solve-evaluation-panel__ending-list">
                <article
                  v-for="row in caseManualSolveEvaluationState.endpointRows"
                  :key="row.key"
                  :class="{ 'case-manual-solve-evaluation-panel__ending--issue': !row.resolved }"
                >
                  <div>
                    <strong>{{ row.pathLabel || 'Path' }} · {{ row.shortHash }}</strong>
                    <small>{{ row.toAddressShort || row.toAddress || 'No receiving address' }}</small>
                  </div>
                  <span>{{ row.amountDisplay || '—' }}</span>
                  <em>{{ row.usdDisplay || row.statusLabel }}</em>
                </article>
              </div>
            </section>
          </div>

          <details v-if="caseManualSolveEvaluationState.unresolvedRows.length || caseManualSolveEvaluationState.warnings.length" class="case-manual-solve-evaluation-panel__issues">
            <summary>
              Valuation notes / issues
              <small>{{ caseManualSolveEvaluationState.unresolvedRows.length }} unresolved · {{ caseManualSolveEvaluationState.warnings.length }} warning{{ caseManualSolveEvaluationState.warnings.length === 1 ? '' : 's' }}</small>
            </summary>
            <ul>
              <li v-for="warning in caseManualSolveEvaluationState.warnings" :key="`warning-${warning}`">{{ warning }}</li>
              <li v-for="row in caseManualSolveEvaluationState.unresolvedRows" :key="row.key">
                <strong>{{ row.pathLabel || row.pathEndingReference }}</strong> — {{ row.issue }}
                <small v-if="row.traceStoppedAt">Stopped at {{ row.traceStoppedAt }}</small>
              </li>
            </ul>
          </details>
        </template>
      </section>

      <details
        v-else
        class="case-manual-solve-viewport-controls case-manual-solve-bottom-panel"
        :class="{ 'is-resizing': caseManualSolveBottomPanelResizing }"
        :open="caseManualStartViewportDisplayControlsOpen"
        :style="caseManualSolveBottomPanelStyle"
        @toggle="caseManualStartViewportDisplayControlsOpen = $event.target.open"
      >
        <summary>
          <span>Manual Solve viewport display</span>
          <small>Compact display-only options for this Manual Solve view. Flow Solver settings stay in the Manual Solve settings panel.</small>
        </summary>
        <div class="case-manual-solve-viewport-controls__grid">
          <label class="case-manual-solve-viewport-controls__field">
            <span>Address merge</span>
            <select v-model="caseManualStartVisualizerOptions.mergeAddressMode" @change="applyCaseManualStartVisualizerOptionsChange({ rebuild: true })">
              <option value="none">Manual continuity only</option>
              <option value="sameColumnMiddle">Same column middle</option>
              <option value="globalAverage">Global average</option>
            </select>
          </label>
          <label class="network-lookup-modal__checkbox">
            <input v-model="caseManualStartVisualizerOptions.labelAddresses" type="checkbox" @change="applyCaseManualStartVisualizerOptionsChange({ rebuild: true })" />
            <span>Show attribution labels above addresses</span>
          </label>
          <label class="network-lookup-modal__checkbox">
            <input v-model="caseManualStartVisualizerOptions.showFundsFollowedBars" type="checkbox" @change="applyCaseManualStartVisualizerOptionsChange" />
            <span>Show funds-followed bars on transaction nodes</span>
          </label>
        </div>
      </details>
    </template>

    <div class="agent-modal__actions case-investigation-visualizer-modal__footer-actions">
      <button class="toolbar-btn" type="button" :disabled="caseInvestigationVisualizerRefreshRunning || caseInvestigationVisualizerInteractionLocked" @click="refreshCaseInvestigationVisualizer({ preserveFlow: true })">Refresh</button>
      <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!caseInvestigationVisualizerCurrentVisual || caseInvestigationVisualizerInteractionLocked" @click="downloadCurrentCaseInvestigationVisualizerSvg">Download current SVG</button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseInvestigationVisualizerInteractionLocked" @click="cancelCaseInvestigationVisualizer">Close</button>
    </div>


    <div
      v-if="caseManualStartAllFlowsAutoActive"
      class="case-manual-solve-all-flows-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-manual-solve-all-flows-title"
      @click.stop
      @pointerdown.stop
    >
      <div class="case-manual-solve-all-flows-overlay__scrim" aria-hidden="true"></div>
      <section class="case-manual-solve-all-flows-overlay__card" aria-live="polite">
        <header>
          <div>
            <span>Guided Flow Solver</span>
            <h3 id="case-manual-solve-all-flows-title">Solving every starting graph</h3>
          </div>
          <strong>{{ caseManualStartAllFlowsAuto.currentFlowNumber || 1 }} / {{ caseManualStartAllFlowsAuto.totalFlowCount || 1 }}</strong>
        </header>

        <div class="case-manual-solve-all-flows-overlay__current">
          <span>Current graph</span>
          <strong>{{ caseManualStartAllFlowsAuto.currentFlowLabel || 'Preparing the first graph…' }}</strong>
          <small>{{ caseManualStartAllFlowsAuto.status || 'Preparing guided Auto…' }}</small>
        </div>

        <div
          class="case-manual-solve-all-flows-overlay__meter"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="caseManualStartAllFlowsAutoProgressPercent"
        >
          <span :style="caseManualStartAllFlowsAutoProgressStyle"></span>
        </div>

        <div class="case-manual-solve-all-flows-overlay__metrics">
          <article>
            <strong>{{ caseManualStartAllFlowsAutoCompletedCount }}</strong>
            <small>completed</small>
          </article>
          <article>
            <strong>{{ caseManualStartAllFlowsAutoAttentionCount }}</strong>
            <small>set aside</small>
          </article>
          <article>
            <strong>{{ caseManualStartAllFlowsAuto.currentFlowUnresolvedCount || 0 }}</strong>
            <small>current unresolved</small>
          </article>
        </div>

        <p>{{ caseManualStartAllFlowsAuto.detail || 'Case Buddy will move to the next graph when this one is complete or reaches a safe stopping point.' }}</p>

        <button
          ref="caseManualStartAllFlowsStopButton"
          class="toolbar-btn toolbar-btn--danger"
          type="button"
          @click="requestCaseManualStartAllFlowsAutoStop('Guided Auto stop requested by the user.')"
        >
          Stop guided Auto
        </button>
      </section>
    </div>

    <div
      v-if="caseManualStartAllFlowsAuto.resultVisible && !caseManualStartAllFlowsAutoActive"
      class="case-manual-solve-all-flows-overlay case-manual-solve-all-flows-overlay--result"
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-manual-solve-all-flows-result-title"
      @click.stop
      @pointerdown.stop
    >
      <div class="case-manual-solve-all-flows-overlay__scrim" aria-hidden="true"></div>
      <section class="case-manual-solve-all-flows-result" aria-live="polite">
        <button
          class="case-manual-solve-all-flows-result__close"
          type="button"
          aria-label="Close guided Flow Solver summary"
          @click="dismissCaseManualStartAllFlowsResult"
        >
          <span aria-hidden="true"></span>
        </button>

        <div class="case-manual-solve-all-flows-result__icon" :class="{ 'is-complete': caseManualStartAllFlowsAutoResultComplete }" aria-hidden="true">
          {{ caseManualStartAllFlowsAutoResultComplete ? '✓' : '!' }}
        </div>
        <div class="case-manual-solve-all-flows-result__heading">
          <span>Guided Flow Solver</span>
          <h3 id="case-manual-solve-all-flows-result-title">{{ caseManualStartAllFlowsAutoResultTitle }}</h3>
          <p>{{ caseManualStartAllFlowsAutoResultMessage }}</p>
        </div>

        <div class="case-manual-solve-all-flows-result__metrics">
          <article>
            <strong>{{ Math.max(0, (caseManualStartAllFlowsAuto.caseFlowCount || 0) - caseManualStartAllFlowsAutoAttentionCount) }}</strong>
            <small>graphs complete</small>
          </article>
          <article>
            <strong>{{ caseManualStartAllFlowsAutoAttentionCount }}</strong>
            <small>need review</small>
          </article>
        </div>

        <div v-if="caseManualStartAllFlowsAuto.attentionFlows.length" class="case-manual-solve-all-flows-result__findings">
          <strong>Graphs that still need attention</strong>
          <ul>
            <li v-for="flow in caseManualStartAllFlowsAuto.attentionFlows" :key="flow.identity || flow.key">
              <span>{{ flow.label || 'Graph' }}</span>
              <small>{{ flow.reason || 'Manual review is still needed.' }}</small>
            </li>
          </ul>
        </div>

        <div class="case-manual-solve-all-flows-result__actions">
          <button
            v-if="caseManualStartAllFlowsAuto.attentionFlows.length"
            ref="caseManualStartAllFlowsResultPrimaryButton"
            class="toolbar-btn toolbar-btn--primary"
            type="button"
            @click="reviewCaseManualStartAllFlowsFirstAttention"
          >
            Review first unfinished graph
          </button>
          <button
            v-if="caseManualStartAllFlowsAuto.attentionFlows.length"
            class="toolbar-btn toolbar-btn--accent"
            type="button"
            :disabled="!caseManualStartAllFlowsAutoCanStart"
            @click="retryCaseManualStartAllFlowsAuto"
          >
            Retry unfinished graphs
          </button>
          <button
            v-else
            ref="caseManualStartAllFlowsResultPrimaryButton"
            class="toolbar-btn toolbar-btn--done"
            type="button"
            @click="closeCaseManualStartAllFlowsAndReturn"
          >
            Close Flow Solver
          </button>
          <button
            v-if="caseManualStartAllFlowsAuto.attentionFlows.length"
            class="toolbar-btn toolbar-btn--ghost"
            type="button"
            @click="closeCaseManualStartAllFlowsAndReturn"
          >
            Close Flow Solver
          </button>
          <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="dismissCaseManualStartAllFlowsResult">
            Stay here
          </button>
        </div>
      </section>
    </div>

    <div v-if="caseManualStartHopInspectorMounted" class="case-manual-start-hop-inspector__hidden-tool" aria-hidden="true">
      <Tool88Page_CaseBuddy
        ref="caseManualStartHopInspectorTool88"
        :case-buddy-embedded="true"
        :case-buddy-headless="true"
        :case-buddy-start-in-advanced-view="false"
        @case-buddy-status="handleCaseManualStartHopInspectorStatus"
        @case-buddy-progress="handleCaseManualStartHopInspectorProgress"
        @case-buddy-result="handleCaseManualStartHopInspectorResult"
        @case-buddy-guardrail="handleCaseManualStartHopInspectorGuardrail"
        @case-buddy-flow-complete="handleCaseManualStartHopInspectorFlowComplete"
      />
    </div>
  </div>
</div>

<div
  v-if="showCaseManualSolveBackdropCloseConfirm && showCaseInvestigationVisualizerModal && caseInvestigationVisualizerManualStartMode"
  class="case-manual-solve-close-confirm"
  role="presentation"
  @click.self="dismissCaseManualSolveBackdropCloseConfirm"
>
  <section
    class="case-manual-solve-close-confirm__card"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="case-manual-solve-close-confirm-title"
    aria-describedby="case-manual-solve-close-confirm-description"
    @click.stop
    @keydown.esc.stop.prevent="dismissCaseManualSolveBackdropCloseConfirm"
  >
    <div class="case-manual-solve-close-confirm__icon" aria-hidden="true">?</div>
    <div>
      <span>Manual Solve</span>
      <h3 id="case-manual-solve-close-confirm-title">Close this panel?</h3>
      <p id="case-manual-solve-close-confirm-description">
        Clicking the dimmed area can happen accidentally. Any completed work remains in the case, but the Manual Solve workspace will close.
      </p>
    </div>
    <div class="case-manual-solve-close-confirm__actions">
      <button
        ref="caseManualSolveBackdropCloseKeepButton"
        class="toolbar-btn toolbar-btn--primary"
        type="button"
        @click="dismissCaseManualSolveBackdropCloseConfirm"
      >
        Keep working
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="confirmCaseManualSolveBackdropClose">
        Close Manual Solve
      </button>
    </div>
  </section>
</div>

<div
  v-if="caseManualStartReportBoundaryConfirmation.visible"
  ref="caseManualStartReportBoundaryConfirmationModal"
  class="case-manual-report-boundary-confirm"
  tabindex="-1"
  role="presentation"
  @click.self="closeCaseManualStartReportBoundaryConfirmation"
  @keydown.esc.stop.prevent="closeCaseManualStartReportBoundaryConfirmation"
>
  <section
    class="case-manual-report-boundary-confirm__card"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="case-manual-report-boundary-confirm-title"
    aria-describedby="case-manual-report-boundary-confirm-description"
    @click.stop
  >
    <button
      class="case-manual-report-boundary-confirm__close"
      type="button"
      aria-label="Cancel report-boundary acknowledgement"
      :disabled="caseManualStartReportBoundaryConfirmation.applying"
      @click="closeCaseManualStartReportBoundaryConfirmation"
    >
      <span aria-hidden="true"></span>
    </button>
    <div class="case-manual-report-boundary-confirm__icon" aria-hidden="true">!</div>
    <div class="case-manual-report-boundary-confirm__heading">
      <span>Incomplete tracing</span>
      <h3 id="case-manual-report-boundary-confirm-title">
        Use {{ caseManualStartReportBoundaryConfirmationCount === 1 ? 'this ending' : `these ${caseManualStartReportBoundaryConfirmationCount} endings` }} in the report?
      </h3>
      <p id="case-manual-report-boundary-confirm-description">
        Case Buddy will stop treating {{ caseManualStartReportBoundaryConfirmationCount === 1 ? 'this path' : 'these paths' }} as a report blocker, but it will not claim that tracing continued beyond {{ caseManualStartReportBoundaryConfirmationCount === 1 ? 'this point' : 'these points' }}.
      </p>
    </div>

    <ul class="case-manual-report-boundary-confirm__list">
      <li v-for="row in caseManualStartReportBoundaryConfirmationRows" :key="row.key">
        <strong>Path {{ row.pathLabel }}</strong>
        <span>{{ row.attribution }} · {{ row.address }}</span>
        <small>{{ row.direction }} trace</small>
      </li>
    </ul>

    <div class="case-manual-report-boundary-confirm__notice">
      <strong>What changes</strong>
      <p>The Flow Solver visual will mark each node as a report boundary, and the final report will state that further manual tracing is recommended.</p>
    </div>

    <label class="case-manual-report-boundary-confirm__acknowledgement">
      <input v-model="caseManualStartReportBoundaryConfirmation.acknowledged" type="checkbox" :disabled="caseManualStartReportBoundaryConfirmation.applying" />
      <span>I understand that {{ caseManualStartReportBoundaryConfirmationCount === 1 ? 'this ending remains' : 'these endings remain' }} incomplete and the report will recommend further tracing.</span>
    </label>

    <p v-if="caseManualStartReportBoundaryConfirmation.status" class="case-manual-report-boundary-confirm__status">
      {{ caseManualStartReportBoundaryConfirmation.status }}
    </p>

    <div class="case-manual-report-boundary-confirm__actions">
      <button
        class="toolbar-btn toolbar-btn--accent"
        type="button"
        :disabled="!caseManualStartReportBoundaryConfirmationCanApply"
        @click="confirmCaseManualStartReportBoundaryAcceptance"
      >
        {{ caseManualStartReportBoundaryConfirmation.applying ? 'Saving boundaries…' : 'Accept for report' }}
      </button>
      <button
        class="toolbar-btn toolbar-btn--ghost"
        type="button"
        :disabled="caseManualStartReportBoundaryConfirmation.applying"
        @click="closeCaseManualStartReportBoundaryConfirmation"
      >
        Keep tracing
      </button>
    </div>
  </section>
</div>

  <div
    v-if="caseManualFifoInspector.open"
      class="case-manual-fifo-inspector-modal"
      role="dialog"
      aria-modal="true"
      aria-label="FIFO calculation inspector"
      @click.self="closeCaseManualFifoInspector"
  >
    <section class="case-manual-fifo-inspector" @click.stop>
        <header class="case-manual-fifo-inspector__header">
          <div>
            <strong>{{ caseManualFifoInspector.title || 'FIFO calculation inspector' }}</strong>
            <small>{{ caseManualFifoInspector.subtitle || 'Review how Flow Solver described the selected FIFO calculation.' }}</small>
          </div>
          <div class="case-manual-fifo-inspector__header-actions">
            <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--small" type="button" @click="copyCaseManualFifoInspectorDetails">Copy details</button>
            <button class="case-manual-fifo-inspector__close" type="button" aria-label="Close FIFO inspector" @click="closeCaseManualFifoInspector">×</button>
          </div>
        </header>

        <div class="case-manual-fifo-inspector__cards">
          <article v-for="card in caseManualFifoInspectorList('cards')" :key="card.label" class="case-manual-fifo-inspector__card">
            <span>{{ card.label }}</span>
            <strong>{{ card.value || '—' }}</strong>
            <small v-if="card.note">{{ card.note }}</small>
          </article>
        </div>

        <div class="case-manual-fifo-inspector__body">
          <div class="case-manual-fifo-inspector__overview">
            <div v-if="caseManualFifoInspectorList('equationRows').length" class="case-manual-fifo-inspector__equation" aria-label="FIFO calculation summary">
              <div class="case-manual-fifo-inspector__section-heading">
                <strong>FIFO story</strong>
                <small>Prior balance, queue offset, followed tranche, and fee impact.</small>
              </div>
              <div class="case-manual-fifo-inspector__equation-chain">
                <article v-for="(item, index) in caseManualFifoInspectorList('equationRows')" :key="`fifo-equation-${item.key}`" :class="`case-manual-fifo-inspector__equation-step case-manual-fifo-inspector__equation-step--${item.kind || 'neutral'}`">
                  <span v-if="index" class="case-manual-fifo-inspector__equation-operator">{{ item.operator || '→' }}</span>
                  <div>
                    <span>{{ item.label }}</span>
                    <strong>{{ item.value }}</strong>
                    <small v-if="item.note">{{ item.note }}</small>
                  </div>
                </article>
              </div>
            </div>

            <div v-if="caseManualFifoInspectorList('barSegments').length" class="case-manual-fifo-inspector__allocation">
              <div class="case-manual-fifo-inspector__allocation-header">
                <strong>Queue allocation</strong>
                <small>{{ caseManualFifoInspectorValue('barCaption') }}</small>
              </div>
              <div class="case-manual-fifo-bar" role="img" :aria-label="caseManualFifoInspectorValue('barAriaLabel') || 'FIFO allocation bar'">
                <span
                  v-for="segment in caseManualFifoInspectorList('barSegments')"
                  :key="segment.key"
                  class="case-manual-fifo-bar__segment"
                  :class="`case-manual-fifo-bar__segment--${segment.kind}`"
                  :style="caseManualFifoBarSegmentStyle(segment)"
                  :title="`${segment.label}: ${segment.display}`"
                ></span>
              </div>
              <div class="case-manual-fifo-bar__legend">
                <span
                  v-for="segment in caseManualFifoInspectorList('barSegments')"
                  :key="`fifo-legend-${segment.key}`"
                  class="case-manual-fifo-bar__legend-item"
                  :class="`case-manual-fifo-bar__segment--${segment.kind}`"
                >
                  <i aria-hidden="true"></i>
                  <strong>{{ segment.label }}</strong>
                  <small>{{ segment.display }}</small>
                </span>
              </div>
            </div>

            <div class="case-manual-fifo-inspector__timeline" aria-label="FIFO calculation stages">
              <article v-for="stage in caseManualFifoInspectorList('timeline')" :key="stage.key" class="case-manual-fifo-inspector__stage">
                <span class="case-manual-fifo-inspector__stage-index">{{ stage.index }}</span>
                <div>
                  <strong>{{ stage.title }}</strong>
                  <p>{{ stage.value }}</p>
                  <small v-if="stage.note">{{ stage.note }}</small>
                </div>
              </article>
            </div>
          </div>

          <div class="case-manual-fifo-inspector__tables">
            <details class="case-manual-fifo-inspector__details" open>
              <summary>Selected row and continuation context</summary>
              <div class="case-manual-fifo-inspector__table-wrap">
                <table class="case-manual-fifo-inspector__table">
                  <tbody>
                    <tr v-for="row in caseManualFifoInspectorList('contextRows')" :key="`${row[0]}-${row[1]}`">
                      <th>{{ row[0] }}</th>
                      <td>{{ row[1] }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>

            <details v-if="caseManualFifoInspectorList('exitCandidates').length" class="case-manual-fifo-inspector__details" open>
              <summary>FIFO exit candidates / selected outputs</summary>
              <div class="case-manual-fifo-inspector__table-wrap">
                <table class="case-manual-fifo-inspector__table case-manual-fifo-inspector__table--events">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Hash</th>
                      <th>Output amount</th>
                      <th>Funds followed</th>
                      <th>Left bounds</th>
                      <th>From → To</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="exit in caseManualFifoInspectorList('exitCandidates')" :key="`fifo-exit-${exit.index}-${exit.hash}`">
                      <td>{{ exit.index }}</td>
                      <td><code>{{ exit.shortHash || exit.hash }}</code></td>
                      <td>{{ exit.amountText || '—' }}</td>
                      <td>{{ exit.followedText || '—' }}</td>
                      <td>{{ exit.leftBoundText || '—' }}</td>
                      <td><code>{{ exit.shortFrom || '—' }}</code> → <code>{{ exit.shortTo || '—' }}</code></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>

            <details v-if="caseManualFifoInspectorList('includedEvents').length" class="case-manual-fifo-inspector__details">
              <summary>Included prior-balance events</summary>
              <div class="case-manual-fifo-inspector__table-wrap">
                <table class="case-manual-fifo-inspector__table case-manual-fifo-inspector__table--events">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Role</th>
                      <th>Hash</th>
                      <th>Delta</th>
                      <th>Fee</th>
                      <th>From → To</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="event in caseManualFifoInspectorList('includedEvents')" :key="`fifo-included-${event.index}-${event.hash}`">
                      <td>{{ event.index }}</td>
                      <td>{{ event.role || 'event' }}</td>
                      <td><code>{{ event.shortHash || event.hash }}</code></td>
                      <td>{{ event.deltaText || event.amountText || '—' }}</td>
                      <td>{{ event.feeText || '—' }}</td>
                      <td><code>{{ event.shortFrom || '—' }}</code> → <code>{{ event.shortTo || '—' }}</code></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>

            <details v-if="caseManualFifoInspectorList('excludedEvents').length" class="case-manual-fifo-inspector__details">
              <summary>Excluded / dismissed events</summary>
              <div class="case-manual-fifo-inspector__table-wrap">
                <table class="case-manual-fifo-inspector__table case-manual-fifo-inspector__table--events">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Role</th>
                      <th>Hash</th>
                      <th>Delta</th>
                      <th>Fee</th>
                      <th>From → To</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="event in caseManualFifoInspectorList('excludedEvents')" :key="`fifo-excluded-${event.index}-${event.hash}`">
                      <td>{{ event.index }}</td>
                      <td>{{ event.role || 'event' }}</td>
                      <td><code>{{ event.shortHash || event.hash }}</code></td>
                      <td>{{ event.deltaText || event.amountText || '—' }}</td>
                      <td>{{ event.feeText || '—' }}</td>
                      <td><code>{{ event.shortFrom || '—' }}</code> → <code>{{ event.shortTo || '—' }}</code></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>

            <details v-if="caseManualFifoInspectorList('warnings').length" class="case-manual-fifo-inspector__details">
              <summary>Warnings and review notes</summary>
              <ul class="case-manual-fifo-inspector__warnings">
                <li v-for="warning in caseManualFifoInspectorList('warnings')" :key="warning">{{ warning }}</li>
              </ul>
            </details>

            <details class="case-manual-fifo-inspector__details">
              <summary>Raw FIFO diagnostic notes</summary>
              <pre>{{ caseManualFifoInspectorValue('rawText') || 'No raw FIFO diagnostic note was available on this row.' }}</pre>
            </details>
          </div>
        </div>
      </section>
  </div>




<div
  v-if="caseManualStartBranchDeletionPreview"
  class="agent-modal-backdrop case-manual-branch-delete-backdrop"
  @click="cancelCaseManualStartBranchDeletionPreview"
>
  <div class="agent-modal network-lookup-modal case-manual-branch-delete-modal" @click.stop>
    <div class="network-lookup-modal__header case-manual-branch-delete-modal__header">
      <div>
        <p class="eyebrow-label">Manual Solve data mutation</p>
        <h3>Delete branch from Main Data?</h3>
        <p>
          This removes the selected branch from the Manual Solve case data, not just the visual. The standard visualizer still keeps branch delete/hide visual-only.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Cancel branch deletion" :disabled="caseManualStartBranchDeletionApplying" @click="cancelCaseManualStartBranchDeletionPreview">×</button>
    </div>

    <div class="template-deliverable-modal__notice case-manual-branch-delete-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': caseManualStartBranchDeletionPreview.canApply }">
      <strong>{{ caseManualStartBranchDeletionPreview.canApply ? 'Preview ready' : 'Cannot apply this deletion safely' }}</strong>
      <span>{{ caseManualStartBranchDeletionPreview.summary }}</span>
    </div>

    <div v-if="caseManualStartBranchDeletionPreview.warnings && caseManualStartBranchDeletionPreview.warnings.length" class="case-manual-branch-delete-modal__warning-list">
      <strong>Review before applying</strong>
      <ul>
        <li v-for="warning in caseManualStartBranchDeletionPreview.warnings" :key="warning">{{ warning }}</li>
      </ul>
    </div>

    <section class="case-manual-branch-delete-modal__section">
      <h4>Branch root</h4>
      <dl class="path-operation-modal__details-grid case-manual-branch-delete-modal__details">
        <div>
          <dt>Path label</dt>
          <dd>{{ caseManualStartBranchDeletionPreview.root.pathLabel || '—' }}</dd>
        </div>
        <div>
          <dt>Transaction hash</dt>
          <dd class="case-manual-branch-delete-modal__mono">{{ caseManualStartBranchDeletionPreview.root.shortHash || caseManualStartBranchDeletionPreview.root.hash || '—' }}</dd>
        </div>
        <div>
          <dt>To address</dt>
          <dd class="case-manual-branch-delete-modal__mono">{{ caseManualStartBranchDeletionPreview.root.shortToAddress || caseManualStartBranchDeletionPreview.root.toAddress || '—' }}</dd>
        </div>
        <div>
          <dt>Mode</dt>
          <dd>{{ caseManualStartBranchDeletionPreview.outputOnly ? 'BTC/output branch only' : 'Transaction branch' }}</dd>
        </div>
      </dl>
    </section>

    <section class="case-manual-branch-delete-modal__section">
      <h4>Rows to remove</h4>
      <p>{{ caseManualStartBranchDeletionPreview.rowsToRemove.length }} spreadsheet row{{ caseManualStartBranchDeletionPreview.rowsToRemove.length === 1 ? '' : 's' }} will be removed.</p>
      <div class="case-manual-branch-delete-modal__table-wrap">
        <table class="network-lookup-table case-manual-branch-delete-modal__table">
          <thead>
            <tr>
              <th>Row</th>
              <th>Path</th>
              <th>Hash</th>
              <th>To address</th>
              <th>Continues as</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in caseManualStartBranchDeletionPreview.rowsToRemove" :key="row.key">
              <td>{{ row.spreadsheetRowNumber }}</td>
              <td>{{ row.pathLabel || '—' }}</td>
              <td class="case-manual-branch-delete-modal__mono">{{ row.shortHash || row.hash || '—' }}</td>
              <td class="case-manual-branch-delete-modal__mono">{{ row.shortToAddress || row.toAddress || '—' }}</td>
              <td>{{ row.continuesAs || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="case-manual-branch-delete-modal__section">
      <h4>Parent updates</h4>
      <p v-if="!caseManualStartBranchDeletionPreview.parentUpdates.length">No parent Continues As cell needs to be changed.</p>
      <div v-else class="case-manual-branch-delete-modal__table-wrap">
        <table class="network-lookup-table case-manual-branch-delete-modal__table">
          <thead>
            <tr>
              <th>Parent row</th>
              <th>Parent path</th>
              <th>Old Continues As</th>
              <th>New Continues As</th>
              <th>Remaining siblings</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="update in caseManualStartBranchDeletionPreview.parentUpdates" :key="update.key">
              <td>{{ update.spreadsheetRowNumber }}</td>
              <td>{{ update.pathLabel || '—' }}</td>
              <td>{{ update.oldContinuesAs || '—' }}</td>
              <td>{{ update.newContinuesAs || '—' }}</td>
              <td>{{ update.remainingSiblingShortHashes.length ? update.remainingSiblingShortHashes.join(', ') : 'None — parent becomes an unresolved ending' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="caseManualStartBranchDeletionPreview.remainingSiblings.length" class="case-manual-branch-delete-modal__section">
      <h4>Remaining sibling branches</h4>
      <div class="case-manual-branch-delete-modal__chip-list">
        <span v-for="sibling in caseManualStartBranchDeletionPreview.remainingSiblings" :key="sibling.key">
          {{ sibling.pathLabel || 'Path' }} · {{ sibling.shortHash || sibling.hash }}
        </span>
      </div>
    </section>

    <section class="case-manual-branch-delete-modal__section">
      <h4>Recheck after deletion</h4>
      <p>
        Manual Solve will refresh the viewport from updated Main Data and recompute unresolved path endings. It will not mark a sibling path complete just because this branch was removed.
      </p>
      <p v-if="caseManualStartBranchDeletionPreview.suspiciousEndpointAttributions.length" class="case-manual-branch-delete-modal__suspicious">
        {{ caseManualStartBranchDeletionPreview.suspiciousEndpointAttributions.length }} copied-looking endpoint attribution{{ caseManualStartBranchDeletionPreview.suspiciousEndpointAttributions.length === 1 ? '' : 's' }} will be flagged for review after the recheck.
      </p>
    </section>

    <div class="agent-modal__actions case-manual-branch-delete-modal__actions">
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseManualStartBranchDeletionApplying" @click="cancelCaseManualStartBranchDeletionPreview">Cancel</button>
      <button class="toolbar-btn toolbar-btn--danger" type="button" :disabled="caseManualStartBranchDeletionApplying || !caseManualStartBranchDeletionPreview.canApply" @click="applyCaseManualStartBranchDeletionPreview">
        {{ caseManualStartBranchDeletionApplying ? 'Deleting…' : 'Delete branch from Main Data' }}
      </button>
    </div>
  </div>
</div>


<div
  v-if="showCaseHopSolverModal"
  class="agent-modal-backdrop case-hop-solver-backdrop"
  @click="cancelCaseHopSolverModal"
>
  <div
    class="agent-modal network-lookup-modal case-hop-solver-modal case-hop-solver-modal--tool88"
    :class="{ 'case-hop-solver-modal--simple-view': caseHopSolverViewMode === 'simple' }"
    @click.stop
  >
    <div class="network-lookup-modal__header case-hop-solver-modal__header">
      <div>
        <p class="eyebrow-label">Flow Solver</p>
        <h3>Solve Hop</h3>
        <p>
          Use Flow Solver inside Case Buddy to solve the selected Path ends row, select the next unresolved ending, or run guarded step-by-step solving. Output stays reviewable before it is appended to Raw Data or handed to graft review.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close solve hop" :disabled="caseHopSolverRunning" @click="cancelCaseHopSolverModal">×</button>
    </div>

    <div class="template-deliverable-modal__summary case-hop-solver-modal__summary">
      <div>
        <strong>{{ caseHopSolverCandidate?.pathLabel || '—' }}</strong>
        <small>path label</small>
      </div>
      <div>
        <strong>{{ caseHopSolverCandidate?.shortHash || caseHopSolverCandidate?.hash || '—' }}</strong>
        <small>transaction</small>
      </div>
      <div>
        <strong>{{ caseHopSolverCandidate?.fundsFollowed || '—' }}</strong>
        <small>funds followed</small>
      </div>
      <div>
        <strong>{{ caseHopSolverCandidate?.chainName || caseHopSolverCandidate?.chainId || '—' }}</strong>
        <small>chain</small>
      </div>
      <div>
        <strong>{{ caseHopSolverSnapshot?.status || 'Ready' }}</strong>
        <small>Flow Solver status</small>
      </div>
    </div>

    <div class="template-deliverable-modal__notice case-hop-solver-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': caseHopSolverCandidate?.isPathEnding || caseHopSolverSnapshot?.flowComplete }">
      <strong>{{ caseHopSolverStatusTitle }}</strong>
      <span>{{ caseHopSolverStatus || 'Flow Solver is ready. Choose a guided action below, or use the embedded Simple / Advanced view directly.' }}</span>
    </div>

    <div class="case-hop-solver-modal__toolbar">
      <div class="case-hop-solver-modal__view-toggle">
        <button class="toolbar-btn" type="button" :class="{ 'toolbar-btn--active': caseHopSolverViewMode === 'simple' }" :disabled="caseHopSolverRunning" @click="setCaseHopSolverViewMode('simple')">Simple view</button>
        <button class="toolbar-btn" type="button" :class="{ 'toolbar-btn--active': caseHopSolverViewMode === 'advanced' }" :disabled="caseHopSolverRunning" @click="setCaseHopSolverViewMode('advanced')">Advanced view</button>
      </div>
      <div class="case-hop-solver-modal__run-actions">
        <button class="toolbar-btn" type="button" :disabled="caseHopSolverRunning || !caseHopSolverCandidate" @click="loadCaseHopSolverSelectedCandidate">Load selected</button>
        <button class="toolbar-btn" type="button" :disabled="caseHopSolverRunning" @click="selectCaseHopSolverNextUnresolved">Select next unresolved</button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="caseHopSolverRunning" @click="runCaseHopSolverNextAction">Run next action</button>
        <button class="toolbar-btn toolbar-btn--accent" type="button" :disabled="caseHopSolverRunning" @click="runCaseHopSolverAutomationLoop">Auto solve guarded</button>
        <button class="toolbar-btn" type="button" :disabled="!caseHopSolverRunning || caseHopSolverStopRequested" @click="requestCaseHopSolverStop">Request stop</button>
      </div>
    </div>

    <div v-if="caseHopSolverStopRequested" class="network-lookup-modal__status case-hop-solver-modal__status">
      Stop requested. Flow Solver will pause at the next safe point and keep any returned rows in review state.
    </div>

    <div class="case-hop-solver-modal__layout case-hop-solver-modal__layout--tool88">
      <section class="template-deliverable-modal__preview-card case-hop-solver-modal__tool-card">
        <div class="template-deliverable-modal__preview-header">
          <div>
            <strong>Flow Solver</strong>
            <small>Simple view is default; close it inside Flow Solver or use Advanced view for full controls.</small>
          </div>
          <span class="header-badge" :class="{ 'header-badge--active': caseHopSolverSnapshot?.flowComplete }">
            {{ caseHopSolverSnapshot?.flowComplete ? 'Flow complete' : caseHopSolverRunning ? 'Running' : 'Interactive' }}
          </span>
        </div>
        <div class="case-hop-solver-modal__tool88-frame">
          <Tool88Page_CaseBuddy
            ref="caseHopSolverTool88"
            :case-buddy-embedded="true"
            :case-buddy-start-in-advanced-view="caseHopSolverStartInAdvancedView"
            @case-buddy-status="handleCaseHopSolverStatus"
            @case-buddy-progress="handleCaseHopSolverProgress"
            @case-buddy-result="handleCaseHopSolverResult"
            @case-buddy-guardrail="handleCaseHopSolverGuardrail"
            @case-buddy-flow-complete="handleCaseHopSolverFlowComplete"
          />
        </div>
      </section>

      <aside class="case-hop-solver-modal__side-panel">
        <section class="template-deliverable-modal__preview-card case-hop-solver-modal__side-card case-hop-solver-modal__candidate-card">
          <div class="template-deliverable-modal__preview-header">
            <div>
              <strong>Selected path ending</strong>
              <small>Case Buddy request context sent to Flow Solver.</small>
            </div>
          </div>
          <div class="case-hop-solver-modal__detail-table-wrap">
            <table class="case-hop-solver-modal__detail-table">
              <tbody>
                <tr v-for="row in caseHopSolverCandidateRows" :key="`${row[0]}-${row[1]}`">
                  <th>{{ row[0] }}</th>
                  <td>{{ row[1] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="template-deliverable-modal__preview-card case-hop-solver-modal__side-card case-hop-solver-modal__review-card">
          <div class="template-deliverable-modal__preview-header">
            <div>
              <strong>Review / apply output</strong>
              <small>{{ caseHopSolverOutputRows.length }} raw-data row{{ caseHopSolverOutputRows.length === 1 ? '' : 's' }} available.</small>
            </div>
          </div>
          <p class="case-hop-solver-modal__side-note">
            Flow Solver is expected to produce WayFinder-like raw rows. Stage them first, append to Raw Data, or open the existing graft review with the output preloaded.
          </p>
          <div class="case-hop-solver-modal__apply-actions">
            <button class="toolbar-btn" type="button" :disabled="caseHopSolverOutputRows.length === 0 || caseHopSolverRunning" @click="stageCaseHopSolverOutputToOutput">Stage in Output</button>
            <button class="toolbar-btn" type="button" :disabled="caseHopSolverOutputRows.length === 0 || caseHopSolverRunning" @click="appendCaseHopSolverOutputToRawData">Append to Raw Data</button>
            <button v-if="caseHopSolverManualStartMode" class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseHopSolverOutputRows.length === 0 || caseHopSolverRunning" @click="applyCaseManualStartHopSolverOutputToMain">Apply to manual visual</button>
            <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="caseHopSolverOutputRows.length === 0 || !caseHopSolverCandidate?.isPathEnding || caseHopSolverRunning" @click="openCaseHopSolverGraftReview">Open graft review</button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseHopSolverRunning" @click="refreshCaseInvestigationVisualizerAfterHopSolver">Refresh visualizer</button>
          </div>
          <div v-if="caseHopSolverRawApplyStatus" class="case-hop-solver-modal__apply-status">
            {{ caseHopSolverRawApplyStatus }}
          </div>
        </section>

        <section class="template-deliverable-modal__preview-card case-hop-solver-modal__side-card case-hop-solver-modal__snapshot-card">
          <div class="template-deliverable-modal__preview-header">
            <div>
              <strong>Latest solver snapshot</strong>
              <small>{{ caseHopSolverLastEvent || 'No event yet' }}</small>
            </div>
          </div>
          <div class="case-hop-solver-modal__snapshot-grid">
            <div><span>Status</span><strong>{{ caseHopSolverSnapshot?.status || '—' }}</strong></div>
            <div><span>Action</span><strong>{{ caseHopSolverSnapshot?.integrationAction || '—' }}</strong></div>
            <div><span>Rows</span><strong>{{ caseHopSolverOutputRows.length }}</strong></div>
            <div><span>Flow</span><strong>{{ caseHopSolverSnapshot?.flowComplete ? 'Complete' : 'Open' }}</strong></div>
          </div>
          <p v-if="caseHopSolverSnapshot?.guardrail" class="case-hop-solver-modal__guardrail">
            {{ caseHopSolverSnapshot.guardrail.message || caseHopSolverSnapshot.guardrail.reason }}
          </p>
        </section>
      </aside>
    </div>

    <div class="agent-modal__actions case-hop-solver-modal__actions">
      <button class="toolbar-btn" type="button" :disabled="caseHopSolverRunning" @click="openCaseHopSolverNextUnresolved">Open next unresolved</button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseHopSolverRunning" @click="cancelCaseHopSolverModal">Close</button>
    </div>
  </div>
</div>


<div
  v-if="caseWorkflowRawDataTool88HostMounted"
  class="case-workflow-tool88-headless-host"
  aria-hidden="true"
>
  <Tool88Page_CaseBuddy
    ref="caseWorkflowRawDataTool88"
    :case-buddy-embedded="true"
    :case-buddy-headless="true"
    :case-buddy-start-in-advanced-view="false"
    @case-buddy-status="handleCaseWorkflowRawDataTool88Status"
    @case-buddy-progress="handleCaseWorkflowRawDataTool88Progress"
    @case-buddy-result="handleCaseWorkflowRawDataTool88Result"
    @case-buddy-guardrail="handleCaseWorkflowRawDataTool88Guardrail"
    @case-buddy-flow-complete="handleCaseWorkflowRawDataTool88FlowComplete"
  />
</div>


<div v-if="showCaseFlowVisualsModal" class="agent-modal-backdrop case-flow-visuals-backdrop" @click="cancelCaseFlowVisualsModal">
  <div
    class="agent-modal network-lookup-modal template-deliverable-modal case-flow-visuals-modal"
    :class="{ 'case-flow-visuals-modal--investigation': caseFlowVisualsIsInvestigation, 'case-flow-visuals-modal--full-window': caseFlowVisualsFullWindow }"
    @click.stop
  >
    <div class="network-lookup-modal__header template-deliverable-modal__header case-flow-visuals-modal__header">
      <div>
        <h3>{{ caseFlowVisualsModalTitle }}</h3>
        <p>{{ caseFlowVisualsModalDescription }}</p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close case flow visuals" :disabled="caseFlowVisualsDownloading" @click="cancelCaseFlowVisualsModal">×</button>
    </div>

    <div class="template-deliverable-modal__summary case-flow-visuals-modal__summary">
      <div>
        <strong>{{ caseFlowVisualsStateView.flowCount || 0 }}</strong>
        <small>flow visual{{ (caseFlowVisualsStateView.flowCount || 0) === 1 ? '' : 's' }}</small>
      </div>
      <div>
        <strong>{{ caseFlowVisualsStateView.entryCount || 0 }}</strong>
        <small>transaction entries</small>
      </div>
      <div>
        <strong>{{ caseFlowVisualsStateView.addressLabelCount || 0 }}</strong>
        <small>legend labels</small>
      </div>
      <div>
        <strong>{{ caseFlowVisualsSummaryStatusLabel }}</strong>
        <small>{{ caseFlowVisualsIsInvestigation ? 'investigation view' : 'visual package' }}</small>
      </div>
    </div>

    <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': caseFlowVisualsStateView.canBuild }">
      <strong>{{ caseFlowVisualsNoticeTitle }}</strong>
      <span>{{ caseFlowVisualsStateView.summary }}</span>
    </div>

    <div class="template-deliverable-modal__toolbar case-flow-visuals-modal__toolbar">
      <div class="case-flow-visuals-modal__nav-actions">
        <button class="toolbar-btn" type="button" :disabled="!caseFlowVisualCanGoPrevious || caseFlowVisualsDownloading" @click="showPreviousCaseFlowVisual">← Previous</button>
        <strong>{{ caseFlowVisualCurrentLabel }}</strong>
        <button class="toolbar-btn" type="button" :disabled="!caseFlowVisualCanGoNext || caseFlowVisualsDownloading" @click="showNextCaseFlowVisual">Next →</button>
      </div>
      <div class="case-flow-visuals-modal__download-actions">
        <button class="toolbar-btn" type="button" :disabled="caseFlowVisualsRefreshRunning || caseFlowVisualsDownloading" @click="refreshCaseFlowVisualsReview()">
          {{ caseFlowVisualsRefreshRunning ? 'Refreshing…' : 'Refresh' }}
        </button>
        <button class="toolbar-btn" type="button" :disabled="!caseFlowVisualsCurrentVisual || caseFlowVisualsDownloading" @click="downloadCurrentCaseFlowVisualSvg">Current SVG</button>
        <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!caseFlowVisualsStateView.canBuild || caseFlowVisualsDownloading" @click="downloadCaseFlowVisualsBundle('svg', { markReviewed: true })">All SVG</button>
        <button class="toolbar-btn" type="button" :disabled="!caseFlowVisualsStateView.canBuild || caseFlowVisualsDownloading" @click="downloadCaseFlowVisualsBundle('png', { markReviewed: true })">All PNG</button>
        <button class="toolbar-btn" type="button" :disabled="!caseFlowVisualsStateView.canBuild || caseFlowVisualsDownloading" @click="downloadCaseFlowVisualsBundle('jpg', { markReviewed: true })">All JPG</button>
      </div>
    </div>

    <div class="case-flow-visuals-modal__download-note">
      {{ caseFlowVisualDownloadSummary }}
    </div>

    <div v-if="caseFlowVisualsStatus" class="network-lookup-modal__status case-flow-visuals-modal__status">
      {{ caseFlowVisualsStatus }}
    </div>

    <div class="case-flow-visuals-modal__content">
      <section class="case-flow-visuals-modal__viewport-card">
        <div class="template-deliverable-modal__preview-header">
          <div>
            <strong>{{ caseFlowVisualCurrentFlow ? caseFlowVisualCurrentFlow.mainLabel : 'Case flow visual' }}</strong>
            <small>{{ caseFlowVisualNodes.length }} nodes · {{ caseFlowVisualEdges.length }} arrows · mouse wheel zoom, drag background to pan</small>
          </div>
          <div class="case-flow-visuals-modal__mini-actions">
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseFlowVisualsCurrentVisual" @click="zoomCaseFlowVisual(0.82)">Zoom in</button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseFlowVisualsCurrentVisual" @click="zoomCaseFlowVisual(1.18)">Zoom out</button>
            <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="!caseFlowVisualsCurrentVisual" @click="resetCaseFlowVisualViewport">Fit</button>
          </div>
        </div>

        <div class="case-flow-visuals-viewport" :class="{ 'is-panning': caseFlowVisualsPointer.active }">
          <svg
            ref="caseFlowVisualSvg"
            class="case-flow-visuals-svg"
            xmlns="http://www.w3.org/2000/svg"
            :viewBox="caseFlowVisualViewBoxString"
            @wheel.prevent="onCaseFlowVisualWheel"
            @pointerdown="onCaseFlowVisualPointerDown"
            @pointermove="onCaseFlowVisualPointerMove"
            @pointerup="onCaseFlowVisualPointerUp"
            @pointercancel="onCaseFlowVisualPointerUp"
            @click="clearCaseFlowVisualSelection"
          >
            <defs>
              <marker id="case-flow-visual-arrow-live" markerWidth="12" markerHeight="12" refX="10" refY="6" orient="auto" markerUnits="strokeWidth">
                <path class="case-flow-visual-arrow" d="M 0 0 L 12 6 L 0 12 z" />
              </marker>
              <marker id="case-flow-visual-arrow-muted-live" markerWidth="12" markerHeight="12" refX="10" refY="6" orient="auto" markerUnits="strokeWidth">
                <path class="case-flow-visual-arrow-muted" d="M 0 0 L 12 6 L 0 12 z" />
              </marker>
            </defs>
            <rect class="case-flow-visual-bg" :x="caseFlowVisualsViewport.x" :y="caseFlowVisualsViewport.y" :width="caseFlowVisualsViewport.width" :height="caseFlowVisualsViewport.height" />
            <g class="case-flow-visuals-edges">
              <path
                v-for="edge in caseFlowVisualEdges"
                :key="edge.id"
                :class="caseFlowVisualEdgeClass(edge)"
                :d="caseFlowVisualEdgePath(edge)"
                :marker-end="edge.dashed ? 'url(#case-flow-visual-arrow-muted-live)' : 'url(#case-flow-visual-arrow-live)'"
              />
            </g>
            <g class="case-flow-visuals-nodes">
              <g
                v-for="node in caseFlowVisualNodes"
                :key="node.id"
                :class="caseFlowVisualNodeClass(node)"
                :transform="caseFlowVisualNodeTransform(node)"
                @click.stop="selectCaseFlowVisualNode(node)"
              >
                <template v-if="node.type === 'tx'">
                  <rect class="case-flow-visual-tx-rect" :x="-caseFlowVisualTxNodeSize.width / 2" :y="-caseFlowVisualTxNodeSize.height / 2" :width="caseFlowVisualTxNodeSize.width" :height="caseFlowVisualTxNodeSize.height" rx="16" ry="16" />
                  <text v-if="node.amountText" class="case-flow-visual-amount-text" text-anchor="middle" y="-42">{{ node.amountText }}</text>
                  <text class="case-flow-visual-hash-text" text-anchor="middle" y="-8">{{ node.shortHash }}</text>
                  <text v-if="node.dateText" class="case-flow-visual-date-text" text-anchor="middle" y="18">{{ node.dateText }}</text>
                  <text v-if="node.chainText" class="case-flow-visual-chain-text" text-anchor="middle" y="42">{{ node.chainText }}</text>
                  <g v-if="node.pathLabelLines && node.pathLabelLines.length" :transform="`translate(0 ${caseFlowVisualTxNodeSize.height / 2 + 40})`">
                    <rect class="case-flow-visual-path-bg" :x="-node.pathBoxWidth / 2" :y="-node.pathBoxHeight / 2" :width="node.pathBoxWidth" :height="node.pathBoxHeight" rx="8" ry="8" />
                    <text class="case-flow-visual-path-text" text-anchor="middle" :font-size="node.pathFontSize">
                      <tspan v-for="(line, lineIndex) in node.pathLabelLines" :key="`${node.id}-path-${lineIndex}`" x="0" :y="node.pathTextStartY + (lineIndex * node.pathLineHeight)">
                        {{ line }}
                      </tspan>
                    </text>
                  </g>
                  <text v-if="node.badge" class="case-flow-visual-badge-text" text-anchor="middle" :y="-caseFlowVisualTxNodeSize.height / 2 - 14">{{ node.badge }}</text>
                </template>

                <template v-else-if="node.type === 'address'">
                  <g v-if="caseManualVisualNodeLabelLines(node).length">
                    <text
                      v-for="(line, lineIndex) in caseManualVisualNodeLabelLines(node)"
                      :key="`${node.id}-label-${lineIndex}`"
                      :class="['case-flow-visual-address-label', `case-flow-visual-address-label-${caseManualVisualNodeLabelCategory(node)}`]"
                      text-anchor="middle"
                      :font-size="node.labelFontSize || 16"
                      :y="-caseFlowVisualAddressRadius - 26 - ((caseManualVisualNodeLabelLines(node).length - 1 - lineIndex) * 20)"
                    >
                      {{ line }}
                    </text>
                  </g>
                  <circle v-if="caseManualVisualNodeAlertRing(node)" class="case-flow-visual-address-alert-ring" :r="caseFlowVisualAddressRadius + 8" />
                  <polygon v-if="node.shape === 'hexagon'" :class="['case-flow-visual-address-circle', 'case-flow-visual-address-hexagon', `case-flow-visual-address-circle-${caseManualVisualNodeLabelCategory(node)}`]" :points="caseFlowVisualAddressHexagonPoints" />
                  <circle v-else :class="['case-flow-visual-address-circle', `case-flow-visual-address-circle-${caseManualVisualNodeLabelCategory(node)}`]" :r="caseFlowVisualAddressRadius" />
                  <text class="case-flow-visual-address-text" text-anchor="middle" y="5">{{ node.shortAddress }}</text>
                  <text v-if="node.sourceCount > 1" class="case-flow-visual-address-count" text-anchor="middle" :y="caseFlowVisualAddressRadius + 18">×{{ node.sourceCount }}</text>
                </template>

                <template v-else-if="node.type === 'amount'">
                  <rect class="case-flow-visual-amount-rect" :x="-caseFlowVisualAmountNodeSize.width / 2" :y="-caseFlowVisualAmountNodeSize.height / 2" :width="caseFlowVisualAmountNodeSize.width" :height="caseFlowVisualAmountNodeSize.height" rx="12" ry="12" />
                  <text class="case-flow-visual-amount-node-text" text-anchor="middle" y="5">{{ node.amountText }}</text>
                </template>

                <template v-else-if="node.type === 'flowLabel'">
                  <rect class="case-flow-visual-flow-label-bg" :x="-(node.boxWidth || 420) / 2" :y="-(node.boxHeight || 56) / 2" :width="node.boxWidth || 420" :height="node.boxHeight || 56" rx="14" ry="14" />
                  <text class="case-flow-visual-flow-label-text" text-anchor="middle" :font-size="node.fontSize || 24" y="9">{{ node.labelText }}</text>
                </template>
              </g>
            </g>
          </svg>

          <div v-if="!caseFlowVisualsCurrentVisual" class="case-flow-visuals-empty">
            Open a case with Main Data transaction entries to preview flow visuals here.
          </div>
        </div>
      </section>

      <aside class="case-flow-visuals-modal__side-panel">
        <section class="template-deliverable-modal__preview-card case-flow-visuals-modal__selection-card">
          <div class="template-deliverable-modal__preview-header">
            <div>
              <strong>{{ caseFlowVisualSelectedNode ? 'Selected node' : 'Selection details' }}</strong>
              <small>Click a transaction, address, or label in the visual.</small>
            </div>
          </div>
          <div v-if="caseFlowVisualSelectedNodeRows.length" class="case-flow-visuals-detail-table-wrap">
            <table class="case-flow-visuals-detail-table">
              <tbody>
                <tr v-for="row in caseFlowVisualSelectedNodeRows" :key="`${row[0]}-${row[1]}`">
                  <th>{{ row[0] }}</th>
                  <td>{{ row[1] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="network-lookup-modal__empty-state">
            Nothing selected yet. Right-click and drag to select multiple nodes, or left-click a node to select and move it.
          </div>
        </section>

        <section class="template-deliverable-modal__preview-card case-flow-visuals-modal__flow-list-card">
          <div class="template-deliverable-modal__preview-header">
            <div>
              <strong>Flow list</strong>
              <small>{{ caseFlowVisualsFlows.length }} visual{{ caseFlowVisualsFlows.length === 1 ? '' : 's' }}</small>
            </div>
          </div>
          <div class="case-flow-visuals-flow-list">
            <button
              v-for="(flow, flowIndex) in caseFlowVisualsFlows"
              :key="flow.key || flowIndex"
              class="case-flow-visuals-flow-button"
              :class="{ 'is-active': flowIndex === caseFlowVisualsActiveIndex }"
              type="button"
              @click="renderCaseFlowVisualAtIndex(flowIndex)"
            >
              <strong>{{ flow.mainLabel || `Flow ${flowIndex + 1}` }}</strong>
              <span>{{ flow.entries.length }} entr{{ flow.entries.length === 1 ? 'y' : 'ies' }} · {{ flow.mainHash }}</span>
            </button>
          </div>
        </section>
      </aside>
    </div>

    <details class="case-flow-visuals-modal__advanced" :open="caseFlowVisualsAdvancedOpen" @toggle="caseFlowVisualsAdvancedOpen = $event.target.open">
      <summary>
        <span>Advanced visual controls</span>
        <small>The recommended Tool87-style setup is already enabled. These controls are here for unusual cases.</small>
      </summary>
      <div class="case-flow-visuals-modal__advanced-grid">
        <label class="network-lookup-modal__checkbox">
          <input v-model="caseFlowVisualsOptions.labelAddresses" type="checkbox" @change="refreshCurrentCaseFlowVisual" />
          <span>Apply address legend labels</span>
        </label>
        <label class="network-lookup-modal__checkbox">
          <input v-model="caseFlowVisualsOptions.highlightSwaps" type="checkbox" @change="refreshCurrentCaseFlowVisual" />
          <span>Highlight swaps</span>
        </label>
        <label class="network-lookup-modal__checkbox">
          <input v-model="caseFlowVisualsOptions.highlightBridges" type="checkbox" @change="refreshCurrentCaseFlowVisual" />
          <span>Highlight bridges</span>
        </label>
        <label class="network-lookup-modal__checkbox">
          <input v-model="caseFlowVisualsOptions.mergeTransactionNodes" type="checkbox" @change="refreshCurrentCaseFlowVisual" />
          <span>Merge repeated transaction hashes</span>
        </label>
        <label class="network-lookup-modal__checkbox">
          <input v-model="caseFlowVisualsOptions.wrapLinearTrunks" type="checkbox" @change="refreshCurrentCaseFlowVisual" />
          <span>Wrap long linear trunks</span>
        </label>
        <label class="network-lookup-modal__checkbox">
          <input v-model="caseFlowVisualsOptions.cleanupOverlaps" type="checkbox" @change="refreshCurrentCaseFlowVisual" />
          <span>Space out overlapping nodes</span>
        </label>
        <label>
          <span>Same-address merge</span>
          <select v-model="caseFlowVisualsOptions.mergeAddressMode" @change="refreshCurrentCaseFlowVisual">
            <option value="sameColumnMiddle">WayFinder column merge</option>
            <option value="globalAverage">Global address merge</option>
            <option value="none">Off</option>
          </select>
        </label>
        <label>
          <span>Minimum trunk length</span>
          <input v-model.number="caseFlowVisualsOptions.trunkWrapMinLength" type="number" min="2" max="100" @change="refreshCurrentCaseFlowVisual" />
        </label>
        <label>
          <span>First snake row length</span>
          <input v-model.number="caseFlowVisualsOptions.trunkWrapFirstRowLength" type="number" min="1" max="20" @change="refreshCurrentCaseFlowVisual" />
        </label>
        <label>
          <span>Later snake row length</span>
          <input v-model.number="caseFlowVisualsOptions.trunkWrapOtherRowLength" type="number" min="1" max="20" @change="refreshCurrentCaseFlowVisual" />
        </label>
        <label>
          <span>Snake row spacing</span>
          <input v-model.number="caseFlowVisualsOptions.trunkWrapRowVerticalMultiplier" type="number" min="1" max="4" step="0.05" @change="refreshCurrentCaseFlowVisual" />
        </label>
      </div>
    </details>

    <div class="template-deliverable-modal__completion-card case-flow-visuals-modal__completion-card">
      <div>
        <strong>Review and continue</strong>
        <p>Downloading an all-flow package marks this step done for the current case revision. You can also mark it done manually after checking the visuals or if you have already exported them.</p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !caseFlowVisualsStateView.canBuild || caseFlowVisualsStateView.reviewIsCurrent" @click="runCaseModalMethod('markCaseFlowVisualsDone', { title: 'Marking visuals done…', message: 'Saving visual package review state.' }, { source: 'case_flow_visuals_modal' })">
        {{ caseFlowVisualCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" :disabled="!caseFlowVisualsStateView.canBuild || caseFlowVisualsDownloading" @click="stageCaseFlowVisualsPlaceholder()">Stage Summary</button>
      <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="!caseFlowVisualsStateView.canBuild || caseFlowVisualsDownloading" @click="downloadCaseFlowVisualsBundle('svg', { markReviewed: true })">Download SVG Package</button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !caseFlowVisualsStateView.canBuild || caseFlowVisualsStateView.reviewIsCurrent" @click="runCaseModalMethod('markCaseFlowVisualsDone', { title: 'Marking visuals done…', message: 'Saving visual package review state.' }, { source: 'case_flow_visuals_modal' })">
        {{ caseFlowVisualCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseFlowVisualsDownloading" @click="cancelCaseFlowVisualsModal">Close</button>
    </div>
  </div>
</div>



<div v-if="showCaseFullReportPackageModal" class="agent-modal-backdrop" @click="cancelCaseFullReportPackageModal">
  <div class="agent-modal network-lookup-modal template-deliverable-modal case-full-package-modal" @click.stop>
    <div class="network-lookup-modal__header template-deliverable-modal__header case-full-package-modal__header">
      <div>
        <h3>Full One-Click Report Package</h3>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close full report package" :disabled="caseFullReportPackageRefreshRunning" @click="cancelCaseFullReportPackageModal">×</button>
    </div>

    <div class="case-full-package-modal__content">
      <section v-if="caseFullReportPackageActiveView === 'rich'" class="template-deliverable-modal__preview-card case-full-package-modal__preview-card">
        <div class="template-deliverable-modal__preview-header">
          <div>
            <strong>Rich package preview</strong>
          </div>
          <span class="header-badge" :class="{ 'header-badge--active': caseFullReportPackageStateView.isReady }">
            {{ caseFullReportPackageStateView.isReady ? 'Ready' : 'Draft' }}
          </span>
        </div>
        <div class="case-full-package-modal__rich-preview" v-html="caseFullReportPackagePreviewHtml"></div>
      </section>

      <section v-else-if="caseFullReportPackageActiveView === 'text'" class="template-deliverable-modal__preview-card case-full-package-modal__preview-card">
        <div class="template-deliverable-modal__preview-header">
          <div>
            <strong>Plain text preview</strong>
            <small>{{ caseFullReportPackagePayloadView.plainTextLength }} characters total; preview may be truncated</small>
          </div>
        </div>
        <pre class="case-full-package-modal__text-preview">{{ caseFullReportPackagePreviewText }}</pre>
      </section>

      <section v-else class="template-deliverable-modal__preview-card case-full-package-modal__preview-card">
        <div class="template-deliverable-modal__preview-header">
          <div>
            <strong>Package sections</strong>
            <small>{{ caseFullReportPackagePayloadView.sections.length }} section{{ caseFullReportPackagePayloadView.sections.length === 1 ? '' : 's' }}</small>
          </div>
        </div>
        <div class="case-full-package-modal__section-table-wrap">
          <table class="case-full-package-modal__section-table">
            <thead>
              <tr>
                <th>Section</th>
                <th>Kind</th>
                <th>Rows</th>
                <th>Available</th>
                <th>Embedded visuals</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="section in caseFullReportPackagePayloadView.sections" :key="`${section.title}-${section.kind}`">
                <td>{{ section.title }}</td>
                <td>{{ section.kind }}</td>
                <td>{{ section.rowCount || 0 }}</td>
                <td>{{ section.available ? 'Yes' : 'No' }}</td>
                <td>{{ section.embeddedVisualCount || '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div class="case-full-package-modal__post-preview">
      <p class="case-full-package-modal__description">
        Assemble the reviewed report body, conclusion, What Went Where, supporting tables, address legend, addendum, and duplicate-hash appendix into one copy-ready package for pasting into a report document.
      </p>

      <div class="template-deliverable-modal__summary case-full-package-modal__summary">
        <div>
          <strong>{{ caseFullReportPackageStateView.isReady ? 'Ready' : 'Draft' }}</strong>
          <small>package status</small>
        </div>
        <div>
          <strong>{{ caseFullReportPackageStateView.reportBodyReviewIsCurrent ? 'Reviewed' : 'Needed' }}</strong>
          <small>report body</small>
        </div>
        <div>
          <strong>{{ caseFullReportPackageStateView.addendumReviewIsCurrent ? 'Reviewed' : 'Needed' }}</strong>
          <small>addendum</small>
        </div>
        <div>
          <strong>{{ caseFullReportPackagePayloadView.sections.length }}</strong>
          <small>package section{{ caseFullReportPackagePayloadView.sections.length === 1 ? '' : 's' }}</small>
        </div>
        <div>
          <strong>{{ caseFullReportPackageStateView.visualsReviewIsCurrent ? 'Done' : caseFullReportPackageStateView.visualsCanBuild ? 'Embedded' : 'Needed' }}</strong>
          <small>case visuals</small>
        </div>
      </div>

      <div class="template-deliverable-modal__notice" :class="{ 'template-deliverable-modal__notice--ready': caseFullReportPackageStateView.isReady }">
        <strong>{{ caseFullReportPackageStateView.reviewIsCurrent ? 'Package reviewed' : caseFullReportPackageStateView.isReady ? 'Ready to ship' : 'Draft package' }}</strong>
        <span>{{ caseFullReportPackageStateView.summary }}</span>
      </div>

      <div class="case-full-package-modal__visual-note">
        <strong>Visuals note:</strong>
        When case flow visuals are available, the preview embeds generated SVGs under each matching main transaction path heading. Save PDF prints those inline SVG figures directly so they remain sharp. Copy Rich Package converts the figures into document-friendly PNG images before writing to the clipboard. The full-size SVG, PNG, or JPG visual package is still available from the visual builder.
      </div>

      <div class="template-deliverable-modal__toolbar case-full-package-modal__toolbar">
        <label>
          <span>Preview</span>
          <select v-model="caseFullReportPackageActiveView">
            <option value="rich">Rich package</option>
            <option value="text">Plain text</option>
            <option value="sections">Section list</option>
          </select>
        </label>
        <div class="case-full-package-modal__toolbar-actions">
          <button class="toolbar-btn" type="button" :disabled="caseFullReportPackageRefreshRunning" @click="refreshCaseFullReportPackageReview()">
            {{ caseFullReportPackageRefreshRunning ? 'Refreshing…' : 'Refresh' }}
          </button>
          <button class="toolbar-btn toolbar-btn--primary" type="button" @click="copyCaseFullReportPackageRich()">Copy Rich Package</button>
          <button
            class="toolbar-btn"
            type="button"
            :disabled="caseFullReportPackagePdfPreparing || caseFullReportPackageRefreshRunning || !caseFullReportPackageStateView.isReady"
            title="Open the browser PDF dialog with the report and its embedded flow visuals"
            @click="downloadCaseFullReportPackagePdf({ source: 'full-report-package-modal', ensureReady: false })"
          >
            {{ caseFullReportPackagePdfPreparing ? 'Preparing PDF…' : 'Save PDF' }}
          </button>
          <button class="toolbar-btn" type="button" @click="copyCaseFullReportPackageText()">Copy Text</button>
          <button class="toolbar-btn" type="button" @click="stageCaseFullReportPackagePreview()">Stage Preview</button>
          <button class="toolbar-btn" type="button" @click="downloadCaseFullReportPackageHtml()">Download HTML</button>
          <button class="toolbar-btn" type="button" @click="downloadCaseFullReportPackageText()">Download TXT</button>
        </div>
      </div>

      <div v-if="caseFullReportPackageStatus" class="network-lookup-modal__status case-full-package-modal__status">
        {{ caseFullReportPackageStatus }}
      </div>
    </div>

    <div class="template-deliverable-modal__completion-card case-full-package-modal__completion-card">
      <div>
        <strong>Ready to ship?</strong>
        <p>
          Copying or downloading a ready package marks this step reviewed for the current case data. You can also mark it reviewed manually after checking the preview and confirming the visual package is handled.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseModalOperation.active || !caseFullReportPackageStateView.isReady || caseFullReportPackageStateView.reviewIsCurrent" @click="runCaseModalMethod('markCaseFullReportPackageReviewComplete', { title: 'Marking full report package reviewed…', message: 'Saving final package review state and staging audit output.' })">
        {{ caseFullReportPackageCompletionLabel }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button
        class="toolbar-btn"
        type="button"
        :disabled="caseFullReportPackagePdfPreparing || caseFullReportPackageRefreshRunning || !caseFullReportPackageStateView.isReady"
        title="Open the browser PDF dialog with the report and its embedded flow visuals"
        @click="downloadCaseFullReportPackagePdf({ source: 'full-report-package-modal-bottom', ensureReady: false })"
      >
        {{ caseFullReportPackagePdfPreparing ? 'Preparing PDF…' : 'Save PDF' }}
      </button>
      <button class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button" type="button" :disabled="caseModalOperation.active || !caseFullReportPackageStateView.isReady || caseFullReportPackageStateView.reviewIsCurrent" @click="runCaseModalMethod('markCaseFullReportPackageReviewComplete', { title: 'Marking full report package reviewed…', message: 'Saving final package review state and staging audit output.' })">
        {{ caseFullReportPackageCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="caseFullReportPackageRefreshRunning" @click="cancelCaseFullReportPackageModal">Close</button>
    </div>
  </div>
</div>

<div v-if="showCaseWorkflowCompletionModal && !caseQuickLookupOwnsWorkflowCompletion" class="agent-modal-backdrop case-workflow-complete-backdrop" @click="closeCaseWorkflowCompletionModal">
  <div class="agent-modal case-workflow-complete-modal" role="dialog" aria-modal="true" aria-label="Case Buddy workflow complete" @click.stop>
    <div class="case-workflow-complete-modal__badge" aria-hidden="true">✓</div>
    <div class="case-workflow-complete-modal__body">
      <p class="eyebrow-label">Case Buddy Auto</p>
      <h3>Case workflow complete</h3>
      <p>{{ caseWorkflowCompletionMessage || 'All workflow steps are complete. The one-click report package is ready to review, copy, or download.' }}</p>
    </div>
    <div class="case-workflow-complete-modal__actions">
      <button class="toolbar-btn toolbar-btn--primary" type="button" @click="openCaseWorkflowCompletionReportPackage">
        Open one-click report package
      </button>
      <button class="toolbar-btn" type="button" @click="copyCaseWorkflowCompletionRichPackage">
        Copy rich package
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="closeCaseWorkflowCompletionModal">
        Close
      </button>
    </div>
  </div>
</div>


<div v-if="showResetConfirm" class="agent-modal-backdrop" @click="cancelResetSheet">
  <div class="agent-modal" @click.stop>
    <h3>Reset Sheet</h3>
    <p>
      Clear the active sheet data and reset formatting, borders, frozen panes, and search?
    </p>

    <div class="agent-modal__actions">
      <button class="toolbar-btn toolbar-btn--danger" type="button" @click="performResetSheetReset">
        Confirm Reset
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelResetSheet">
        Cancel
      </button>
    </div>
  </div>
</div>

<div
  v-if="showStartingMainTransactionsModal"
  class="agent-modal-backdrop"
  :class="{ 'starting-main-modal-backdrop--simple-launch': caseSimpleLaunchReturnAfterStartingModal }"
  @click="cancelStartingMainTransactionsModal"
>
  <div class="agent-modal starting-main-modal" @click.stop>
    <div class="starting-main-modal__header">
      <div>
        <h3>Load Starting Main Transactions</h3>
        <p>
          Paste transaction hashes or TSV from any spreadsheet columns. Case Buddy auto-detects the transaction-hash and To Address columns, then lets you review the staged rows before loading them into Main Data.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close" @click="cancelStartingMainTransactionsModal">
        ×
      </button>
    </div>

    <div class="starting-main-modal__layout">
      <section class="starting-main-modal__paste-panel">
        <label class="case-details-modal__field">
          <span>Paste starting transactions</span>
          <textarea
            v-model="startingMainTransactionsPaste"
            rows="10"
            placeholder="Examples:\n0xabc...\n0xdef...\n\nPaste full TSV, B/D Case Buddy rows, or any columns containing a transaction hash and optional destination address."
            @input="parseStartingMainTransactionPaste"
          ></textarea>
        </label>

        <div class="starting-main-modal__hint-list">
          <span>Accepted input:</span>
          <strong>hash list</strong>
          <strong>any TSV columns</strong>
          <strong>header-aware auto-detection</strong>
        </div>

        <div class="starting-main-modal__actions-row">
          <button class="toolbar-btn" type="button" @click="parseStartingMainTransactionPaste">
            Parse pasted data
          </button>
          <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="clearStartingMainTransactionsDraft">
            Clear draft
          </button>
          <button class="toolbar-btn" type="button" @click="refreshRawDataImportCoverage">
            Refresh coverage
          </button>
        </div>
      </section>

      <section class="starting-main-modal__preview-panel">
        <div class="starting-main-modal__preview-header">
          <div>
            <h4>Staged for Main Data</h4>
            <small>{{ startingMainTransactionStageSummary }}</small>
          </div>
          <span class="header-badge" :class="{ 'header-badge--active': startingMainTransactionReadyCount > 0 }">
            {{ startingMainTransactionReadyCount }} ready
          </span>
        </div>

        <div v-if="startingMainTransactionDraftRows.length === 0" class="starting-main-modal__empty-state">
          Paste hashes or TSV to stage starting transactions. Each row with a transaction hash will become a Main Data row with Column B filled and optional Column D To Address.
        </div>

        <div v-else class="starting-main-table-wrap">
          <table class="starting-main-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Column B · Transaction Hash</th>
                <th>Column D · To Address</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in startingMainTransactionDraftRows"
                :key="row.id"
                :class="`starting-main-table__row--${row.status}`"
              >
                <td>{{ index + 1 }}</td>
                <td>
                  <input
                    v-model.trim="row.hash"
                    type="text"
                    spellcheck="false"
                    @input="refreshStartingMainTransactionStagedStatuses"
                  />
                </td>
                <td>
                  <input
                    v-model.trim="row.toAddress"
                    type="text"
                    spellcheck="false"
                    placeholder="Optional"
                    @input="refreshStartingMainTransactionStagedStatuses"
                  />
                </td>
                <td>
                  <span class="starting-main-status-chip">{{ row.statusLabel }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="rawDataImportMissingAfterRows.length > 0" class="raw-data-import-modal__missing-list">
          <strong>Main transactions still missing raw coverage after this staged import:</strong>
          <span v-for="row in rawDataImportMissingAfterRows.slice(0, 6)" :key="row.id">
            {{ row.pathLabel || `Row ${row.spreadsheetRowNumber}` }} · {{ row.shortHash }}{{ row.toAddress ? ` · To ${row.shortToAddress}` : '' }}
          </span>
          <small v-if="rawDataImportMissingAfterRows.length > 6">
            +{{ rawDataImportMissingAfterRows.length - 6 }} more missing row{{ rawDataImportMissingAfterRows.length - 6 === 1 ? '' : 's' }}.
          </small>
        </div>
      </section>
    </div>

    <div class="starting-main-modal__footer-note">
      Duplicates are skipped only when the same transaction hash and the same To Address already exist. The same hash with a different To Address can be added as a separate starting row.
    </div>

    <div class="agent-modal__actions">
      <button
        class="toolbar-btn toolbar-btn--primary"
        type="button"
        :disabled="startingMainTransactionReadyCount === 0"
        @click="loadStartingMainTransactionsIntoMainSheet"
      >
        Load {{ startingMainTransactionReadyCount }} row{{ startingMainTransactionReadyCount === 1 ? '' : 's' }} into Main Data
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelStartingMainTransactionsModal">
        Cancel
      </button>
    </div>
  </div>
</div>


<div v-if="showNetworkLookupModal" class="agent-modal-backdrop" @click="cancelNetworkLookupModal">
  <div class="agent-modal network-lookup-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Identify Transaction Networks</h3>
        <p>
          Look up Chain Id and Chain Name only for detected starting/main transaction rows in Main Data. Unresolved rows can be filled manually.
        </p>
      </div>
      <button
        class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon"
        type="button"
        aria-label="Close network lookup modal"
        :disabled="networkLookupRunning"
        @click="cancelNetworkLookupModal"
      >
        ×
      </button>
    </div>

    <div class="network-lookup-modal__summary-grid">
      <article>
        <span>Main txns detected</span>
        <strong>{{ networkLookupRows.length }}</strong>
        <small>Based on the current Main Data sheet.</small>
      </article>
      <article>
        <span>Need network</span>
        <strong>{{ networkLookupPendingCount }}</strong>
        <small>Missing Column T or Column U.</small>
      </article>
      <article>
        <span>Selected</span>
        <strong>{{ networkLookupSelectedCount }}</strong>
        <small>Already-complete rows are skipped by default.</small>
      </article>
      <article>
        <span>Resolved this run</span>
        <strong>{{ networkLookupResolvedCount }}</strong>
        <small>Unresolved rows can be filled manually.</small>
      </article>
    </div>

    <div class="network-lookup-modal__controls">
      <label class="network-lookup-modal__checkbox">
        <input
          v-model="networkLookupFillDateIfMissing"
          type="checkbox"
          :disabled="networkLookupRunning"
        />
        <span>Also fill blank Column E UTC Date when a provider returns one</span>
      </label>

      <div class="network-lookup-modal__control-actions">
        <button class="toolbar-btn" type="button" :disabled="networkLookupRunning" @click="refreshNetworkLookupRows">
          Refresh rows
        </button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="networkLookupRunning || networkLookupSelectedCount === 0" @click="clearNetworkLookupSelections">
          Clear selected
        </button>
        <button class="toolbar-btn" type="button" :disabled="networkLookupRunning || networkLookupPendingCount === 0" @click="selectPendingNetworkLookupRows">
          Select missing
        </button>
      </div>
    </div>

    <div v-if="authSessionLoaded && !authSession.isAuthenticated" class="network-lookup-modal__auth-note">
      Backend lookup requires a logged-in Case Buddy web app session. You can still review the rows here and fill Column T / U manually.
    </div>

    <div v-if="networkLookupStatus" class="network-lookup-modal__status">
      {{ networkLookupStatus }}
    </div>

    <div v-if="networkLookupRows.length > 0 && networkLookupPendingCount === 0" class="workflow-fast-complete-card workflow-fast-complete-card--success">
      <div>
        <span>Network step complete</span>
        <strong>All detected starting/main transactions have Chain Id and Chain Name.</strong>
        <small>Ready to continue. The primary action at the bottom will close this panel.</small>
      </div>
    </div>

    <div v-if="networkLookupRows.length === 0" class="network-lookup-modal__empty-state">
      No starting/main transaction rows were detected. Load starting transactions first, or run checks to see why the current rows are not being treated as main transactions.
    </div>

    <div v-else class="network-lookup-table-wrap">
      <table class="network-lookup-table">
        <thead>
          <tr>
            <th>Use</th>
            <th>Row</th>
            <th>Path</th>
            <th>Transaction Hash</th>
            <th>To Address Hint</th>
            <th>Current Network</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in networkLookupRows"
            :key="row.id"
            :class="`network-lookup-table__row--${row.status}`"
          >
            <td>
              <input
                v-model="row.include"
                type="checkbox"
                :disabled="networkLookupRunning || !row.needsNetwork || !row.hash"
                aria-label="Include this transaction in network lookup"
              />
            </td>
            <td>{{ row.spreadsheetRowNumber }}</td>
            <td>{{ row.pathLabel || '—' }}</td>
            <td class="network-lookup-table__mono" :title="row.hash">{{ row.hash }}</td>
            <td class="network-lookup-table__mono" :title="row.toAddressHint">{{ row.toAddressHint || '—' }}</td>
            <td>{{ row.currentNetworkLabel }}</td>
            <td>
              <span class="network-lookup-status-chip">{{ row.statusLabel }}</span>
              <small v-if="row.message">{{ row.message }}</small>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="network-lookup-modal__footer-note">
      Successful lookups write to Column T / U in Main Data, and optionally blank Column E. The output sheet receives an audit trail only; do not merge those audit rows back into Main Data.
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" :disabled="networkLookupRunning" @click="activateLogicalSheet('main')">
        Open Main Data
      </button>
      <button
        class="toolbar-btn toolbar-btn--primary"
        type="button"
        :disabled="networkLookupWorkflowPrimaryDisabled"
        @click="runNetworkDiscoveryLookupAndContinue"
      >
        {{ networkLookupWorkflowPrimaryActionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="networkLookupRunning" @click="cancelNetworkLookupModal">
        Close
      </button>
    </div>
  </div>
</div>

<div v-if="showDateLookupModal" class="agent-modal-backdrop" @click="cancelDateLookupModal">
  <div class="agent-modal network-lookup-modal date-lookup-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Complete UTC Dates</h3>
        <p>
          Fill Column E UTC dates only for detected starting/main transaction rows in Main Data. Subtransaction rows are not checked for this workflow step.
        </p>
      </div>
      <button
        class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon"
        type="button"
        aria-label="Close date lookup modal"
        :disabled="dateLookupRunning"
        @click="cancelDateLookupModal"
      >
        ×
      </button>
    </div>

    <div class="network-lookup-modal__summary-grid">
      <article>
        <span>Main txns detected</span>
        <strong>{{ dateLookupRows.length }}</strong>
        <small>Based on the current Main Data sheet.</small>
      </article>
      <article>
        <span>Need UTC date</span>
        <strong>{{ dateLookupPendingCount }}</strong>
        <small>Missing or invalid Column E.</small>
      </article>
      <article>
        <span>Selected</span>
        <strong>{{ dateLookupSelectedCount }}</strong>
        <small>Complete rows are skipped by default.</small>
      </article>
      <article>
        <span>Filled this run</span>
        <strong>{{ dateLookupResolvedCount }}</strong>
        <small>Manual review may still be needed.</small>
      </article>
    </div>

    <div
      class="date-lookup-sort-callout"
      :class="{
        'date-lookup-sort-callout--complete': dateLookupSortPreview.chronologyPass,
        'date-lookup-sort-callout--attention': !dateLookupSortPreview.chronologyPass
      }"
    >
      <div class="date-lookup-sort-callout__copy">
        <span class="date-lookup-sort-callout__eyebrow">{{ dateLookupSortOnlyMode ? 'Ready to finish this step' : 'Required before path labels' }}</span>
        <h4>{{ dateLookupSortOnlyTitle }}</h4>
        <p>{{ dateLookupSortOnlyMode ? 'The date lookup work is already complete. Use this button to sort or confirm the order, then Case Buddy will close this panel so you can move on.' : dateLookupSortPreview.message }}</p>
        <small v-if="dateLookupSortPreview.totalBlocks > 0">
          {{ dateLookupSortPreview.datedBlocks }} dated · {{ dateLookupSortPreview.undatedBlocks }} missing/invalid ·
          {{ dateLookupSortPreview.orderChanged ? 'reorder recommended' : 'already in date order' }}
        </small>
      </div>
      <button
        class="toolbar-btn"
        :class="{ 'toolbar-btn--done': dateLookupSortOnlyMode, 'toolbar-btn--workflow-important': !dateLookupSortOnlyMode && !dateLookupSortPreview.chronologyPass, 'toolbar-btn--workflow-subtle-handoff': !dateLookupSortOnlyMode && dateLookupSortPreview.chronologyPass }"
        type="button"
        :disabled="dateLookupRunning || dateLookupRows.length === 0"
        @click="dateLookupSortOnlyMode ? completeDateLookupSortAndContinue() : sortMainEntriesByDateFromDateModal()"
      >
        {{ dateLookupSortActionLabel }}
      </button>
    </div>

    <div v-if="!dateLookupSortOnlyMode" class="network-lookup-modal__controls">
      <label class="network-lookup-modal__checkbox">
        <input
          v-model="dateLookupFillBlankNetworks"
          type="checkbox"
          :disabled="dateLookupRunning"
        />
        <span>Also fill blank Column T / U network values when the date lookup returns them</span>
      </label>

      <div class="network-lookup-modal__control-actions">
        <button class="toolbar-btn" type="button" :disabled="dateLookupRunning" @click="refreshDateLookupRows">
          Refresh rows
        </button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="dateLookupRunning || dateLookupSelectedCount === 0" @click="clearDateLookupSelections">
          Clear selected
        </button>
        <button class="toolbar-btn" type="button" :disabled="dateLookupRunning || dateLookupPendingCount === 0" @click="selectPendingDateLookupRows">
          Select missing
        </button>
      </div>
    </div>

    <div v-if="!dateLookupSortOnlyMode && authSessionLoaded && !authSession.isAuthenticated" class="network-lookup-modal__auth-note">
      Backend date lookup requires a logged-in Case Buddy web app session. Rows that already have a valid date elsewhere in the same main entry can still be propagated locally.
    </div>

    <div v-if="dateLookupStatus" class="network-lookup-modal__status">
      {{ dateLookupStatus }}
    </div>

    <div v-if="dateLookupRows.length === 0" class="network-lookup-modal__empty-state">
      No starting/main transaction rows were detected. Load starting transactions first, or run checks to see why the current rows are not being treated as main transactions.
    </div>

    <div v-else-if="!dateLookupSortOnlyMode" class="network-lookup-table-wrap">
      <table class="network-lookup-table date-lookup-table">
        <thead>
          <tr>
            <th>Use</th>
            <th>Row</th>
            <th>Path</th>
            <th>Transaction Hash</th>
            <th>Current UTC Date</th>
            <th>Network Hint</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in dateLookupRows"
            :key="row.id"
            :class="`network-lookup-table__row--${row.status}`"
          >
            <td>
              <input
                v-model="row.include"
                type="checkbox"
                :disabled="dateLookupRunning || !row.needsDate || !row.hash"
                aria-label="Include this transaction in date lookup"
              />
            </td>
            <td>{{ row.spreadsheetRowNumber }}</td>
            <td>{{ row.pathLabel || '—' }}</td>
            <td class="network-lookup-table__mono" :title="row.hash">{{ row.hash }}</td>
            <td>{{ row.currentDateLabel }}</td>
            <td>{{ row.chainName || row.chainId || '—' }}</td>
            <td>
              <span class="network-lookup-status-chip">{{ row.statusLabel }}</span>
              <small v-if="row.message">{{ row.message }}</small>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!dateLookupSortOnlyMode" class="network-lookup-modal__footer-note">
      Successful date lookup writes to Column E in Main Data for the starting/main transaction entry. The output sheet receives an audit trail only; do not merge those audit rows back into Main Data.
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" :disabled="dateLookupRunning" @click="activateLogicalSheet('main')">
        Open Main Data
      </button>
      <button
        class="toolbar-btn toolbar-btn--primary"
        type="button"
        :disabled="dateLookupWorkflowPrimaryDisabled"
        @click="runDateLookupWorkflowAndContinue"
      >
        {{ dateLookupWorkflowPrimaryActionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="dateLookupRunning" @click="cancelDateLookupModal">
        Close
      </button>
    </div>
  </div>
</div>

<div v-if="showMainPathLabelModal" class="agent-modal-backdrop" @click="cancelMainPathLabelModal">
  <div class="agent-modal network-lookup-modal path-label-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Assign Main Transaction Path Labels</h3>
        <p>
          Review the detected top-level main transaction entries, then write standard whole-number labels into Column A. Downstream subtransactions with Column O / From Hash are excluded.
        </p>
      </div>
      <button
        class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon"
        type="button"
        aria-label="Close main path label modal"
        :disabled="mainPathLabelAssignRunning"
        @click="cancelMainPathLabelModal"
      >
        ×
      </button>
    </div>

    <div class="network-lookup-modal__summary-grid">
      <article>
        <span>Main txns detected</span>
        <strong>{{ mainPathLabelRows.length }}</strong>
        <small>Top-level entries only.</small>
      </article>
      <article>
        <span>Ready to label</span>
        <strong>{{ mainPathLabelReadyCount }}</strong>
        <small>Has hash, UTC date, and valid proposed label.</small>
      </article>
      <article>
        <span>Need review</span>
        <strong>{{ mainPathLabelNeedsReviewCount }}</strong>
        <small>Usually missing dates or duplicate labels.</small>
      </article>
      <article>
        <span>Will change</span>
        <strong>{{ mainPathLabelChangeCount }}</strong>
        <small>Column A updates staged.</small>
      </article>
    </div>

    <div class="network-lookup-modal__controls path-label-modal__controls">
      <label class="network-lookup-modal__checkbox">
        <input
          v-model="mainPathLabelClearContinuationRows"
          type="checkbox"
          :disabled="mainPathLabelAssignRunning"
          @change="refreshMainPathLabelRows"
        />
        <span>Clear Column A on continuation rows inside the same main transaction entry</span>
      </label>

      <div class="network-lookup-modal__control-actions">
        <button class="toolbar-btn" type="button" :disabled="mainPathLabelAssignRunning" @click="refreshMainPathLabelRows">
          Refresh preview
        </button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="mainPathLabelAssignRunning" @click="resetMainPathLabelProposals">
          Reset labels
        </button>
      </div>
    </div>

    <div v-if="mainPathLabelStatus" class="network-lookup-modal__status">
      {{ mainPathLabelStatus }}
    </div>

    <div v-if="mainPathLabelRows.length > 0 && mainPathLabelNeedsReviewCount === 0 && mainPathLabelChangeCount === 0" class="workflow-fast-complete-card workflow-fast-complete-card--success">
      <div>
        <span>Path-label step complete</span>
        <strong>All detected top-level main transactions already have the proposed Column A labels.</strong>
        <small>Ready to continue. The primary action at the bottom will close this panel.</small>
      </div>
    </div>

    <div v-if="mainPathLabelRows.length === 0" class="network-lookup-modal__empty-state">
      No eligible top-level main transactions were detected. Load starting transactions first, then complete networks, UTC dates, and chronological ordering.
    </div>

    <div v-else class="network-lookup-table-wrap path-label-table-wrap">
      <table class="network-lookup-table main-path-label-table">
        <thead>
          <tr>
            <th>Order</th>
            <th>Row</th>
            <th>Proposed Column A</th>
            <th>Current Column A</th>
            <th>Transaction Hash</th>
            <th>UTC Date</th>
            <th>To Address</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in mainPathLabelRows"
            :key="row.id"
            :class="`network-lookup-table__row--${row.status}`"
          >
            <td>{{ row.order }}</td>
            <td>{{ row.spreadsheetRowNumber }}</td>
            <td>
              <input
                v-model.trim="row.proposedLabel"
                class="main-path-label-table__label-input"
                type="text"
                spellcheck="false"
                :disabled="mainPathLabelAssignRunning"
                @input="refreshMainPathLabelRowStatuses"
              />
            </td>
            <td>{{ row.currentLabel || '—' }}</td>
            <td class="network-lookup-table__mono" :title="row.hash">{{ row.hash }}</td>
            <td>{{ row.utcDate || 'Missing / invalid' }}</td>
            <td>{{ row.toAddress || '—' }}</td>
            <td>
              <span class="network-lookup-status-chip">{{ row.statusLabel }}</span>
              <small v-if="row.message">{{ row.message }}</small>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="network-lookup-modal__footer-note">
      Labels are planned in UTC chronological order and written only to the lead row of each top-level main transaction entry. Use Manual complete if you intentionally choose a non-standard main-label style.
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" :disabled="mainPathLabelAssignRunning" @click="activateLogicalSheet('main')">
        Open Main Data
      </button>
      <button
        class="toolbar-btn toolbar-btn--done"
        type="button"
        :disabled="mainPathLabelWorkflowPrimaryDisabled"
        @click="applyMainPathLabelsAndContinue"
      >
        {{ mainPathLabelWorkflowPrimaryActionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="mainPathLabelAssignRunning" @click="cancelMainPathLabelModal">
        Close
      </button>
    </div>
  </div>
</div>



<div v-if="showRawDataImportModal" class="agent-modal-backdrop" @click="cancelRawDataImportModal">
  <div class="agent-modal raw-data-import-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Load Raw Data For Main Transactions</h3>
        <p>
          Paste raw Case Buddy TSV here, then append it into the Raw Data sheet. The importer detects header/root sets and checks whether each set covers a current starting/main transaction.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close raw data import modal" @click="cancelRawDataImportModal">
        ×
      </button>
    </div>

    <div class="network-lookup-modal__summary-grid raw-data-import-modal__summary-grid">
      <article>
        <span>Current coverage</span>
        <strong>{{ rawDataCurrentCoverage.coveredCount }}/{{ rawDataCurrentCoverage.totalMainTransactions }}</strong>
        <small>{{ rawDataImportCurrentMissingCount }} missing now.</small>
      </article>
      <article>
        <span>Parsed rows</span>
        <strong>{{ rawDataImportParsedRowCount }}</strong>
        <small>Non-empty pasted rows.</small>
      </article>
      <article>
        <span>Detected raw sets</span>
        <strong>{{ rawDataImportDetectedSetsCount }}</strong>
        <small>{{ rawDataImportMatchedCount }} match current main transactions.</small>
      </article>
      <article>
        <span>After import</span>
        <strong>{{ rawDataImportPreviewCoverage.coveredCount }}/{{ rawDataImportPreviewCoverage.totalMainTransactions }}</strong>
        <small>{{ rawDataImportRemainingMissingCount }} would remain missing.</small>
      </article>
      <article>
        <span>Manual visual start</span>
        <strong>{{ caseManualStartVisualizerCandidateCount }}</strong>
        <small>{{ caseManualStartVisualizerStatus || 'Hydrate clear starting hashes and solve hops manually.' }}</small>
      </article>
    </div>

    <div class="raw-data-import-modal__layout">
      <section>
        <label class="case-details-modal__field">
          <span>Paste raw data TSV</span>
          <textarea
            v-model="rawDataImportPaste"
            rows="12"
            placeholder="Paste raw data from WayFinder, PathTracer, or another source. Include Case Buddy headers when you have them. If headers are missing, this modal can add the canonical headers before appending to Raw Data."
            @input="parseRawDataImportPaste"
          ></textarea>
        </label>

        <label class="raw-data-import-modal__checkbox">
          <input v-model="rawDataImportAddHeadersIfMissing" type="checkbox" @change="parseRawDataImportPaste" />
          <span>Add canonical Case Buddy headers when pasted rows do not start with headers</span>
        </label>

        <label class="raw-data-import-modal__checkbox">
          <input v-model="rawDataImportKeepOpenAfterAppend" type="checkbox" />
          <span>Keep this panel open after appending so I can keep loading raw data</span>
        </label>
      </section>

      <section>
        <div class="starting-main-modal__preview-header">
          <div>
            <h4>Detected raw root sets</h4>
            <small>{{ rawDataImportStageSummary }}</small>
          </div>
          <span class="header-badge" :class="{ 'header-badge--active': rawDataImportReadyToAppend }">
            {{ rawDataImportDetectedSetsCount }} set{{ rawDataImportDetectedSetsCount === 1 ? '' : 's' }}
          </span>
        </div>

        <div v-if="rawDataImportStagedSets.length === 0" class="network-lookup-modal__empty-state">
          Paste raw TSV to preview detected raw sets. A valid root set starts with headers and then a root transaction row with Column B filled and Column O / From Hash blank.
        </div>

        <div v-else class="network-lookup-table-wrap raw-data-import-table-wrap">
          <table class="network-lookup-table raw-data-import-table">
            <thead>
              <tr>
                <th>Root row</th>
                <th>Transaction Hash</th>
                <th>To Address</th>
                <th>Rows</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="set in rawDataImportStagedSets" :key="set.id" :class="`network-lookup-table__row--${set.status}`">
                <td>{{ set.rootSpreadsheetRowNumber || '—' }}</td>
                <td class="network-lookup-table__mono" :title="set.rootHash">{{ set.rootHash || '—' }}</td>
                <td class="network-lookup-table__mono" :title="set.rootToAddress">{{ set.rootToAddress || '—' }}</td>
                <td>{{ set.rows?.length || 0 }}</td>
                <td>
                  <span class="network-lookup-status-chip">{{ set.statusLabel }}</span>
                  <small v-if="set.message">{{ set.message }}</small>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="rawDataImportMissingAfterRows.length > 0" class="raw-data-import-modal__missing-panel">
          <div class="raw-data-import-modal__missing-header">
            <div>
              <strong>Main transactions still missing raw coverage after this staged import:</strong>
              <small>Copy the full transaction hash, the related To Address when present, or a small lookup kit while you gather raw data.</small>
            </div>
            <span>{{ rawDataImportMissingAfterRows.length }} missing</span>
          </div>

          <div class="raw-data-import-missing-table-wrap">
            <table class="raw-data-import-missing-table">
              <thead>
                <tr>
                  <th>Path / row</th>
                  <th>Transaction hash</th>
                  <th>Related To Address</th>
                  <th>Copy</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rawDataImportMissingAfterRows" :key="row.id">
                  <td>
                    <strong>{{ row.pathLabel || `Row ${row.spreadsheetRowNumber}` }}</strong>
                    <small>Main row {{ row.spreadsheetRowNumber }}</small>
                  </td>
                  <td class="raw-data-import-missing-table__hash">
                    <code :title="row.hash">{{ row.hash }}</code>
                  </td>
                  <td class="raw-data-import-missing-table__address">
                    <code v-if="row.toAddress" :title="row.toAddress">{{ row.toAddress }}</code>
                    <span v-else class="raw-data-import-missing-table__empty">No To Address requirement</span>
                    <small v-if="row.toAddress">Column D match required</small>
                  </td>
                  <td class="raw-data-import-missing-table__actions">
                    <button class="toolbar-btn toolbar-btn--small" type="button" @click="copyRawCoverageHash(row)">
                      Hash
                    </button>
                    <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="!row.toAddress" @click="copyRawCoverageToAddress(row)">
                      To
                    </button>
                    <button class="toolbar-btn toolbar-btn--small toolbar-btn--ghost" type="button" @click="copyRawCoverageLookupKit(row)">
                      Kit
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>

    <div v-if="rawDataImportStatus" class="network-lookup-modal__status">
      {{ rawDataImportStatus }}
    </div>

    <div class="network-lookup-modal__footer-note">
      Appending writes only to Raw Data, below the current bottom-most raw data plus spacing when useful. Hydrate / Visualize writes only clear provider-confirmed starting-row fields back to Main Data before opening the manual visualizer.
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" @click="activateLogicalSheet('raw')">
        Open Raw Data
      </button>
      <button class="toolbar-btn" type="button" @click="refreshRawDataImportCoverage">
        Refresh coverage
      </button>
      <button class="toolbar-btn toolbar-btn--accent" type="button" @click="openWayFinderHandoffModal">
        WayFinder Handoff
      </button>
      <button
        class="toolbar-btn toolbar-btn--primary"
        type="button"
        :disabled="caseManualStartVisualizerRunning"
        :title="caseManualStartVisualizerLaunchTitle"
        @click="openCaseManualStartVisualizerFromRawData"
      >
        {{ caseManualStartVisualizerButtonLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="clearRawDataImportDraft">
        Clear draft
      </button>
      <button
        class="toolbar-btn toolbar-btn--primary"
        type="button"
        :disabled="!rawDataImportReadyToAppend"
        @click="appendRawDataImportToRawSheet"
      >
        Append to Raw Data
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelRawDataImportModal">
        Close
      </button>
    </div>
  </div>
</div>

<div v-if="showWayFinderHandoffModal" class="agent-modal-backdrop" @click="closeWayFinderHandoffModal">
  <div class="agent-modal wayfinder-handoff-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>WayFinder Raw-Data Handoff Queue</h3>
        <p>
          Queue missing raw-data requests for WayFinder Desktop, watch their progress, and import completed TSV results into Raw Data. Main Data is not changed here.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close WayFinder handoff modal" @click="closeWayFinderHandoffModal">
        ×
      </button>
    </div>

    <div class="network-lookup-modal__summary-grid wayfinder-handoff-modal__summary-grid">
      <article :class="wayFinderHandoffBridgeStatusClass">
        <span>Bridge</span>
        <strong>{{ wayFinderHandoffConnected ? 'Connected' : 'Not connected' }}</strong>
        <small>{{ wayFinderHandoffConnectionStatus }}</small>
      </article>
      <article>
        <span>Targets</span>
        <strong>{{ wayFinderHandoffTargets.length }}</strong>
        <small>{{ wayFinderHandoffQueueableTargetCount }} queueable · {{ wayFinderHandoffSelectedTargetCount }} selected.</small>
      </article>
      <article>
        <span>Queue</span>
        <strong>{{ wayFinderHandoffJobSummary.total }}</strong>
        <small>{{ wayFinderHandoffJobSummary.queued }} queued · {{ wayFinderHandoffJobSummary.inProgress }} in progress.</small>
      </article>
      <article>
        <span>Ready</span>
        <strong>{{ wayFinderHandoffImportableCount }}</strong>
        <small>{{ wayFinderHandoffJobSummary.imported }} imported · {{ wayFinderHandoffJobSummary.failed }} failed.</small>
      </article>
    </div>

    <section class="wayfinder-handoff-modal__connector">
      <label class="case-details-modal__field wayfinder-handoff-modal__url-field">
        <span>WayFinder bridge URL</span>
        <input v-model.trim="wayFinderHandoffBridgeUrl" type="text" placeholder="http://127.0.0.1:8787/api" @change="saveWayFinderHandoffSettings" />
      </label>
      <label class="raw-data-import-modal__checkbox">
        <input v-model="wayFinderHandoffAutoImportCompleted" type="checkbox" @change="saveWayFinderHandoffSettings" />
        <span>Auto-import completed results while this panel is open</span>
      </label>
      <label class="case-details-modal__field wayfinder-handoff-modal__stale-field">
        <span>Stale in-progress minutes</span>
        <input v-model.number="wayFinderHandoffStaleMinutes" type="number" min="5" step="5" @change="saveWayFinderHandoffSettings" />
      </label>
      <div class="wayfinder-handoff-modal__connector-actions">
        <button class="toolbar-btn" type="button" :disabled="wayFinderHandoffBusy" @click="pingWayFinderHandoffBridge">
          Test bridge
        </button>
        <button class="toolbar-btn" type="button" :disabled="wayFinderHandoffBusy" @click="refreshWayFinderHandoffJobs">
          Refresh queue
        </button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="wayFinderHandoffBusy" @click="resetStaleWayFinderHandoffClaims">
          Reset stale claims
        </button>
      </div>
    </section>

    <div class="wayfinder-handoff-modal__notice">
      <strong>Expected WayFinder setup:</strong>
      run WayFinder Desktop in Case Buddy mode, with a local bridge that exposes the shared handoff queue over localhost. This web page cannot open the desktop SQLite database directly from the browser.
    </div>

    <section class="wayfinder-handoff-modal__section">
      <div class="starting-main-modal__preview-header">
        <div>
          <h4>Missing raw-data targets</h4>
          <small>Queue selected targets, or copy a lookup kit and keep using the manual raw-data paste workflow.</small>
        </div>
        <div class="wayfinder-handoff-modal__header-actions">
          <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="wayFinderHandoffQueueableTargetCount === 0" @click="setAllWayFinderHandoffTargetsSelected(true)">
            Select queueable
          </button>
          <button class="toolbar-btn toolbar-btn--small toolbar-btn--ghost" type="button" :disabled="wayFinderHandoffTargets.length === 0" @click="setAllWayFinderHandoffTargetsSelected(false)">
            Clear
          </button>
        </div>
      </div>

      <div v-if="wayFinderHandoffTargets.length === 0" class="network-lookup-modal__empty-state">
        Raw-data coverage is already complete for the current Main Data rows.
      </div>
      <div v-else class="wayfinder-handoff-table-wrap">
        <table class="wayfinder-handoff-table">
          <thead>
            <tr>
              <th>Pick</th>
              <th>Main</th>
              <th>Transaction hash</th>
              <th>Related To Address</th>
              <th>Status</th>
              <th>Copy</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="target in wayFinderHandoffTargets" :key="target.id">
              <td>
                <input v-model="wayFinderHandoffSelectedById[target.id]" type="checkbox" :disabled="!target.canQueue || wayFinderHandoffBusy" />
              </td>
              <td>
                <strong>{{ target.pathLabel || `Row ${target.spreadsheetRowNumber}` }}</strong>
                <small>Row {{ target.spreadsheetRowNumber }}</small>
              </td>
              <td class="wayfinder-handoff-table__mono">
                <code :title="target.hash">{{ target.hash }}</code>
              </td>
              <td class="wayfinder-handoff-table__mono">
                <code v-if="target.toAddress" :title="target.toAddress">{{ target.toAddress }}</code>
                <span v-else class="raw-data-import-missing-table__empty">No To Address requirement</span>
              </td>
              <td>
                <span class="wayfinder-handoff-status-chip" :class="`wayfinder-handoff-status-chip--${target.latestJob?.jobStatus || 'not-queued'}`">
                  {{ target.handoffStatusLabel }}
                </span>
                <small>{{ target.message }}</small>
              </td>
              <td class="wayfinder-handoff-table__actions">
                <button class="toolbar-btn toolbar-btn--small" type="button" @click="copyRawCoverageHash(target)">Hash</button>
                <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="!target.toAddress" @click="copyRawCoverageToAddress(target)">To</button>
                <button class="toolbar-btn toolbar-btn--small toolbar-btn--ghost" type="button" @click="copyWayFinderHandoffTargetKit(target)">Kit</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="wayfinder-handoff-modal__section">
      <div class="starting-main-modal__preview-header">
        <div>
          <h4>Queue jobs for this case</h4>
          <small>Completed jobs with TSV results can be imported directly into Raw Data. Dismiss unwanted queued jobs if the case changed.</small>
        </div>
        <span class="header-badge" :class="{ 'header-badge--active': wayFinderHandoffPolling }">
          {{ wayFinderHandoffPolling ? 'Polling' : 'Paused' }}
        </span>
      </div>
      <div v-if="wayFinderHandoffJobs.length === 0" class="network-lookup-modal__empty-state">
        No WayFinder handoff jobs are recorded for this case yet.
      </div>
      <div v-else class="wayfinder-handoff-table-wrap wayfinder-handoff-table-wrap--jobs">
        <table class="wayfinder-handoff-table">
          <thead>
            <tr>
              <th>Pick</th>
              <th>Status</th>
              <th>Transaction hash</th>
              <th>To Address</th>
              <th>Worker</th>
              <th>Updated</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="job in wayFinderHandoffJobs" :key="job.jobId || `${job.transactionHash}-${job.requestedMainRowId}`">
              <td>
                <input :checked="!!wayFinderHandoffSelectedJobById[job.jobId]" type="checkbox" :disabled="!job.jobId || wayFinderHandoffBusy" @change="setWayFinderHandoffJobSelected(job.jobId, $event.target.checked)" />
              </td>
              <td>
                <span class="wayfinder-handoff-status-chip" :class="`wayfinder-handoff-status-chip--${job.jobStatus}`">
                  {{ job.statusLabel }}
                </span>
                <small v-if="job.workerErrorText || job.importErrorText">{{ job.workerErrorText || job.importErrorText }}</small>
              </td>
              <td class="wayfinder-handoff-table__mono"><code :title="job.transactionHash">{{ job.transactionHash || '—' }}</code></td>
              <td class="wayfinder-handoff-table__mono"><code v-if="job.toAddress" :title="job.toAddress">{{ job.toAddress }}</code><span v-else>—</span></td>
              <td>{{ job.workerId || '—' }}</td>
              <td>{{ job.updatedUtc || job.createdUtc || '—' }}</td>
              <td>
                <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="!job.resultTsv" @click="copyWayFinderHandoffJobResult(job)">
                  Copy TSV
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div v-if="wayFinderHandoffStatus" class="network-lookup-modal__status wayfinder-handoff-modal__status">
      {{ wayFinderHandoffStatus }}
    </div>

    <div class="agent-modal__actions agent-modal__actions--sticky">
      <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="wayFinderHandoffBusy || wayFinderHandoffSelectedTargetCount === 0" @click="queueSelectedWayFinderHandoffJobs">
        Queue {{ wayFinderHandoffSelectedTargetCount }} selected
      </button>
      <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="wayFinderHandoffBusy || wayFinderHandoffImportableCount === 0" @click="importCompletedWayFinderHandoffJobs">
        Import {{ wayFinderHandoffImportableCount }} completed
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="wayFinderHandoffBusy" @click="dismissSelectedWayFinderHandoffJobs">
        Dismiss selected jobs
      </button>
      <button class="toolbar-btn" type="button" @click="openManualRawDataImportFromWayFinder">
        Manual paste helper
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="closeWayFinderHandoffModal">
        Close
      </button>
    </div>
  </div>
</div>

<div v-if="showTool68PreparationModal" class="agent-modal-backdrop" @click="cancelTool68PreparationModal">
  <div class="agent-modal network-lookup-modal tool68-preparation-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Prepare Raw Data Into Main Data</h3>
        <p>
          Build the initial graph by processing the current best raw data set for each starting/main transaction. Successful runs replace only that main transaction block in Main Data; Raw Data stays preserved.
        </p>
      </div>
      <button
        class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon"
        type="button"
        aria-label="Close Tool 68 preparation modal"
        :disabled="tool68PreparationRunning"
        @click="cancelTool68PreparationModal"
      >
        ×
      </button>
    </div>

    <div class="network-lookup-modal__summary-grid tool68-preparation-modal__summary-grid">
      <article>
        <span>Main txns staged</span>
        <strong>{{ tool68PreparationRows.length }}</strong>
        <small>Detected starting/main transaction blocks.</small>
      </article>
      <article>
        <span>Processed</span>
        <strong>{{ tool68PreparationProcessedCount }}</strong>
        <small>Includes zero-hop Path ends rows.</small>
      </article>
      <article>
        <span>Need processing</span>
        <strong>{{ tool68PreparationUnprocessedCount }}</strong>
        <small>Raw-backed but not prepared.</small>
      </article>
      <article>
        <span>Selected</span>
        <strong>{{ tool68PreparationSelectedCount }}</strong>
        <small>{{ tool68PreparationReprocessAvailableCount }} can be reprocessed.</small>
      </article>
    </div>

    <div class="network-lookup-modal__controls tool68-preparation-modal__controls">
      <div class="tool68-preparation-modal__options-grid">
        <label class="case-details-modal__field">
          <span>Chain mode</span>
          <select v-model="tool68PreparationChainMode" :disabled="tool68PreparationRunning">
            <option value="AUTO">Auto from main row</option>
            <option value="ETH">EVM / ETH-style</option>
            <option value="BTC">BTC</option>
          </select>
        </label>

        <label class="network-lookup-modal__checkbox">
          <input v-model="tool68PreparationLabelLongestPathFirst" type="checkbox" :disabled="tool68PreparationRunning" />
          <span>Label longest path first</span>
        </label>

        <label class="network-lookup-modal__checkbox">
          <input v-model="tool68PreparationPruneBtcUnfollowed" type="checkbox" :disabled="tool68PreparationRunning" />
          <span>Prune unfollowed BTC outputs when using BTC mode</span>
        </label>

        <label class="network-lookup-modal__checkbox">
          <input v-model="tool68PreparationExtraSpacing" type="checkbox" :disabled="tool68PreparationRunning" />
          <span>Use extra BTC spacing</span>
        </label>

        <label class="network-lookup-modal__checkbox">
          <input v-model="tool68PreparationAddSpacerAfterBlock" type="checkbox" :disabled="tool68PreparationRunning" />
          <span>Add one spacer row before the next main transaction block</span>
        </label>

        <label class="network-lookup-modal__checkbox" :class="{ 'tool68-preparation-modal__danger-confirm': tool68PreparationNeedsReprocessConfirmation }">
          <input v-model="tool68PreparationConfirmReprocess" type="checkbox" :disabled="tool68PreparationRunning || !tool68PreparationNeedsReprocessConfirmation" />
          <span>Confirm reprocessing selected flows that already appear prepared</span>
        </label>
      </div>

      <div class="network-lookup-modal__control-actions">
        <button class="toolbar-btn" type="button" :disabled="tool68PreparationRunning" @click="refreshTool68PreparationRows">
          Refresh rows
        </button>
        <button class="toolbar-btn" type="button" :disabled="tool68PreparationRunning" @click="selectUnprocessedTool68PreparationRows">
          Select unprocessed
        </button>
        <button class="toolbar-btn" type="button" :disabled="tool68PreparationRunning || tool68PreparationReprocessAvailableCount === 0" @click="selectReprocessAvailableTool68PreparationRows">
          Select newer raw
        </button>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="tool68PreparationRunning || tool68PreparationSelectedCount === 0" @click="clearTool68PreparationSelections">
          Clear selected
        </button>
      </div>
    </div>

    <div v-if="tool68PreparationStatus" class="network-lookup-modal__status tool68-preparation-modal__status">
      {{ tool68PreparationStatus }}
    </div>

    <div v-if="tool68PreparationRows.length === 0" class="network-lookup-modal__empty-state">
      No starting/main transactions were detected. Complete the intake steps first, then load raw data before building the graph.
    </div>

    <div v-else class="network-lookup-table-wrap tool68-preparation-table-wrap">
      <table class="network-lookup-table tool68-preparation-table">
        <thead>
          <tr>
            <th>Use</th>
            <th>Main row</th>
            <th>Path</th>
            <th>Transaction Hash</th>
            <th>To Address</th>
            <th>Raw root</th>
            <th>Main block</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in tool68PreparationRows"
            :key="row.id"
            :class="`tool68-preparation-table__row--${row.status}`"
          >
            <td>
              <input
                v-model="row.include"
                type="checkbox"
                :disabled="tool68PreparationRunning || !row.canProcess"
                aria-label="Include this main transaction in Tool 68 preparation"
              />
            </td>
            <td>{{ row.spreadsheetRowNumber }}</td>
            <td>{{ row.pathLabel || '—' }}</td>
            <td class="network-lookup-table__mono" :title="row.hash">{{ row.shortHash }}</td>
            <td class="network-lookup-table__mono" :title="row.toAddress">{{ row.toAddress ? row.shortToAddress : '—' }}</td>
            <td>{{ row.rawRootRowNumber ? `Raw row ${row.rawRootRowNumber}` : '—' }}</td>
            <td>{{ row.blockRowCount }} row{{ row.blockRowCount === 1 ? '' : 's' }}</td>
            <td>
              <span class="network-lookup-status-chip">{{ row.statusLabel }}</span>
              <small v-if="row.message">{{ row.message }}</small>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="network-lookup-modal__footer-note">
      This is the graph-build substitute for the desktop WayFinder handoff: it uses the bottom-most matching raw set, runs the Tool 68 workflow, then splices the prepared rows under the related main transaction. Reprocessing wipes only that main transaction block and replaces it with the new prepared version.
    </div>

    <div class="agent-modal__actions">
      <button
        class="toolbar-btn toolbar-btn--primary"
        type="button"
        :disabled="!tool68PreparationReadyToRun"
        @click="runTool68PreparationForSelectedRows"
      >
        {{ tool68PreparationRunning ? 'Preparing graph…' : `Prepare ${tool68PreparationSelectedCount} selected` }}
      </button>
      <button class="toolbar-btn" type="button" :disabled="tool68PreparationRunning" @click="activateLogicalSheet('main')">
        Open Main Data
      </button>
      <button class="toolbar-btn" type="button" :disabled="tool68PreparationRunning" @click="activateLogicalSheet('raw')">
        Open Raw Data
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="tool68PreparationRunning" @click="cancelTool68PreparationModal">
        Close
      </button>
    </div>
  </div>
</div>


<div v-if="showAddressAttributionModal" class="agent-modal-backdrop" @click="cancelAddressAttributionModal">
  <div class="agent-modal network-lookup-modal attribution-fill-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Fill Address Attribution In Main Data</h3>
        <p>
          Review To and From address attribution separately. To-address rows write Column K; From-address rows write Column X.
        </p>
      </div>
      <button
        class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon"
        type="button"
        aria-label="Close attribution fill modal"
        :disabled="addressAttributionRunning"
        @click="cancelAddressAttributionModal"
      >
        ×
      </button>
    </div>

    <div class="network-lookup-modal__summary-grid attribution-fill-modal__summary-grid">
      <article>
        <span>Unique addresses</span>
        <strong>{{ addressAttributionAnalysis.addressCount }}</strong>
        <small>From and To columns in Main Data.</small>
      </article>
      <article>
        <span>Need attribution</span>
        <strong>{{ addressAttributionMissingCount }}</strong>
        <small>Includes rows that can reuse an existing label.</small>
      </article>
      <article>
        <span>Selected</span>
        <strong>{{ addressAttributionSelectedCount }}</strong>
        <small>{{ addressAttributionReadyCount }} ready to apply.</small>
      </article>
      <article>
        <span>Unknown</span>
        <strong>{{ addressAttributionUnknownCount }}</strong>
        <small>Unknown counts as a completed attribution.</small>
      </article>
    </div>

    <div class="attribution-fill-modal__layout">
      <section class="attribution-fill-modal__paste-panel">
        <label class="case-details-modal__field">
          <span>Paste attribution TSV</span>
          <textarea
            v-model="addressAttributionPaste"
            rows="8"
            placeholder="Accepted examples:\nAddress [tab] Attribution\nAttribution [tab] Address\nHeaders can be Address/To Address/From Address and Attribution/Entity/Label/Service."
            :disabled="addressAttributionRunning"
            @input="parseAddressAttributionPasteIntoRows"
          ></textarea>
        </label>

        <div class="starting-main-modal__hint-list">
          <span>Accepted input:</span>
          <strong>Address → Attribution</strong>
          <strong>Attribution → Address</strong>
          <strong>Header-based TSV</strong>
        </div>

        <label class="network-lookup-modal__checkbox">
          <input v-model="addressAttributionOverwriteExisting" type="checkbox" :disabled="addressAttributionRunning" />
          <span>Overwrite existing Column K attribution when the pasted/manual value differs</span>
        </label>

        <div class="network-lookup-modal__control-actions">
          <button class="toolbar-btn" type="button" :disabled="addressAttributionRunning" @click="refreshAddressAttributionRows">
            Refresh addresses
          </button>
          <button class="toolbar-btn" type="button" :disabled="addressAttributionRunning" @click="copyMissingAddressAttributionTsv">
            Copy missing TSV
          </button>
          <button class="toolbar-btn" type="button" :disabled="addressAttributionRunning || addressAttributionRows.length === 0" @click="fillOutstandingAddressAttributionsAsUnknown">
            Treat outstanding as Unknown
          </button>
          <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="addressAttributionRunning" @click="clearAddressAttributionDraft">
            Clear paste
          </button>
        </div>
      </section>

      <section class="attribution-fill-modal__table-panel">
        <div class="starting-main-modal__preview-header">
          <div>
            <h4>Addresses needing review</h4>
            <small>{{ addressAttributionStageSummary }}</small>
          </div>
          <span class="header-badge" :class="{ 'header-badge--active': addressAttributionReadyCount > 0 }">
            {{ addressAttributionReadyCount }} ready
          </span>
        </div>

        <div v-if="addressAttributionRows.length === 0" class="network-lookup-modal__empty-state">
          No missing From/To address attribution was detected. Run checks or return here after grafting/bridging adds new rows.
        </div>

        <div v-else class="network-lookup-table-wrap attribution-fill-table-wrap">
          <table class="network-lookup-table attribution-fill-table">
            <thead>
              <tr>
                <th>Use</th>
                <th>Address</th>
                <th>Where seen</th>
                <th>Current</th>
                <th>Attribution to apply</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in addressAttributionRows" :key="row.id" :class="`attribution-fill-table__row--${row.status}`">
                <td>
                  <input v-model="row.include" type="checkbox" :disabled="addressAttributionRunning || row.status === 'complete'" />
                </td>
                <td>
                  <div class="attribution-fill-table__address" :title="row.address">{{ row.address }}</div>
                  <small>First seen row {{ row.firstSpreadsheetRow }}</small>
                </td>
                <td>
                  <span>{{ row.sideSummary }}</span>
                  <small>{{ row.occurrenceCount }} occurrence{{ row.occurrenceCount === 1 ? '' : 's' }} · {{ row.missingOccurrenceCount }} missing</small>
                </td>
                <td>{{ row.currentAttribution || '—' }}</td>
                <td>
                  <input
                    v-model="row.attributionDraft"
                    type="text"
                    spellcheck="false"
                    placeholder="Entity, service, label, or Unknown"
                    :disabled="addressAttributionRunning"
                    @input="refreshAddressAttributionRowStatuses"
                    @paste="handleAddressAttributionCellPaste($event, index)"
                  />
                </td>
                <td>
                  <span class="network-lookup-status-chip">{{ row.statusLabel }}</span>
                  <small v-if="row.message">{{ row.message }}</small>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-if="addressAttributionStatus" class="network-lookup-modal__status attribution-fill-modal__status">
      {{ addressAttributionStatus }}
    </div>

    <div class="network-lookup-modal__footer-note">
      This tool is designed to be reused after pruning, grafting, or Bridge work. Run it again whenever Main Data gains new From or To addresses.
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn" type="button" :disabled="addressAttributionRunning" @click="activateLogicalSheet('main')">
        Open Main Data
      </button>
      <button
        class="toolbar-btn toolbar-btn--primary"
        type="button"
        :disabled="!addressAttributionCanApply"
        @click="applyAddressAttributionRows"
      >
        {{ addressAttributionRunning ? 'Applying…' : `Apply ${addressAttributionReadyCount} attribution${addressAttributionReadyCount === 1 ? '' : 's'}` }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="addressAttributionRunning" @click="cancelAddressAttributionModal">
        Close
      </button>
    </div>
  </div>
</div>


<div v-if="showPathOperationModal" class="agent-modal-backdrop" @click="cancelPathOperationModal">
  <div class="agent-modal network-lookup-modal path-operation-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Prune / Graft / Bridge Path Operations</h3>
        <p>
          Pick a specific qualified output row from Main Data. Prune, graft, and Bridge graft now apply intentional Main Data changes after review and confirmation.
        </p>
      </div>
      <button
        class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon"
        type="button"
        aria-label="Close path operation modal"
        :disabled="pathOperationRunning"
        @click="cancelPathOperationModal"
      >
        ×
      </button>
    </div>

    <div class="network-lookup-modal__summary-grid path-operation-modal__summary-grid">
      <article>
        <span>Qualified outputs</span>
        <strong>{{ pathOperationAnalysis.qualifiedCount }}</strong>
        <small>Rows with funds followed and left bounds.</small>
      </article>
      <article>
        <span>Path endings</span>
        <strong>{{ pathOperationAnalysis.pathEndingCount }}</strong>
        <small>Eligible for graft or Bridge graft.</small>
      </article>
      <article>
        <span>Selected row</span>
        <strong>{{ pathOperationSelectedCandidate ? `Row ${pathOperationSelectedCandidate.spreadsheetRowNumber}` : 'None' }}</strong>
        <small>{{ pathOperationSelectedCandidate ? pathOperationSelectedCandidate.pathLabel || pathOperationSelectedCandidate.mainPathLabel || 'Unlabeled path' : 'Choose intentionally below.' }}</small>
      </article>
      <article>
        <span>Raw candidates</span>
        <strong>{{ pathOperationSelectedCandidate?.rawCandidates?.length || 0 }}</strong>
        <small>Bottom-most match is suggested when available.</small>
      </article>
    </div>

    <div class="path-operation-modal__layout">
      <section class="path-operation-modal__selection-panel">
        <div class="path-operation-modal__toolbar">
          <button class="toolbar-btn toolbar-btn--primary" type="button" :disabled="pathOperationRunning" @click="selectPathOperationFromCurrentSelection">
            Use selected spreadsheet row
          </button>
          <button class="toolbar-btn" type="button" :disabled="pathOperationRunning" @click="refreshPathOperationCandidates">
            Refresh candidates
          </button>
          <button class="toolbar-btn" type="button" :disabled="pathOperationRunning" @click="copyPathOperationCandidatesTsv">
            Copy candidates TSV
          </button>
        </div>

        <div class="path-operation-modal__filters">
          <label class="case-details-modal__field">
            <span>Main transaction</span>
            <select v-model="pathOperationMainFilter" :disabled="pathOperationRunning">
              <option value="">All main transaction blocks</option>
              <option v-for="group in pathOperationMainGroups" :key="group.id" :value="group.id">
                {{ group.label }}
              </option>
            </select>
          </label>
          <label class="network-lookup-modal__checkbox">
            <input v-model="pathOperationShowPathEndingsOnly" type="checkbox" :disabled="pathOperationRunning" />
            <span>Show path endings only for graft / Bridge work</span>
          </label>
        </div>

        <div v-if="pathOperationFilteredCandidates.length === 0" class="network-lookup-modal__empty-state">
          No qualifying output rows were found. A row needs Column M funds followed plus Column R/S left-bounds data before prune, graft, or Bridge work can target it.
        </div>

        <div v-else class="network-lookup-table-wrap path-operation-table-wrap">
          <table class="network-lookup-table path-operation-table">
            <thead>
              <tr>
                <th>Pick</th>
                <th>Row</th>
                <th>Main</th>
                <th>Path</th>
                <th>Hash / To</th>
                <th>Funds / Left bounds</th>
                <th>State</th>
                <th>Attribution / notes</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="candidate in pathOperationFilteredCandidates"
                :key="candidate.id"
                :class="{ 'path-operation-table__row--selected': candidate.id === pathOperationSelectedCandidateId }"
              >
                <td>
                  <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="pathOperationRunning" @click="selectPathOperationCandidate(candidate.id)">
                    Select
                  </button>
                </td>
                <td>{{ candidate.spreadsheetRowNumber }}</td>
                <td>{{ candidate.mainPathLabel || '—' }}</td>
                <td>{{ candidate.pathLabel || '—' }}</td>
                <td>
                  <div class="network-lookup-table__mono" :title="candidate.hash">{{ candidate.shortHash }}</div>
                  <small class="network-lookup-table__mono" :title="candidate.toAddress">{{ candidate.toAddress ? candidate.shortToAddress : 'No To Address' }}</small>
                </td>
                <td>
                  <span>{{ candidate.fundsFollowed }}</span>
                  <small>{{ candidate.leftBoundsDisplay || candidate.leftBoundsNumeric }}</small>
                </td>
                <td>
                  <span class="network-lookup-status-chip">{{ candidate.stateLabel }}</span>
                  <small>{{ candidate.rawCandidates.length }} raw match{{ candidate.rawCandidates.length === 1 ? '' : 'es' }}</small>
                </td>
                <td class="path-operation-table__notes-cell">
                  <strong :title="candidate.attribution || 'No attribution set'">{{ candidate.attribution || 'No attribution' }}</strong>
                  <small v-if="candidate.systemNotes" :title="candidate.systemNotes">J: {{ candidate.systemNotes }}</small>
                  <small v-if="candidate.analystNotes" :title="candidate.analystNotes">L: {{ candidate.analystNotes }}</small>
                  <small v-if="!candidate.systemNotes && !candidate.analystNotes">No row notes</small>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="path-operation-modal__selection-help">
          <strong>Selection note</strong>
          <span>
            Select a candidate from the table or select a cell in Main Data, then click “Use selected spreadsheet row.” This avoids accidental spreadsheet selections becoming destructive actions.
          </span>
          <small>
            {{ pathOperationAnalysis.qualifiedCount }} qualified output row{{ pathOperationAnalysis.qualifiedCount === 1 ? '' : 's' }} detected; {{ pathOperationAnalysis.pathEndingCount }} path ending{{ pathOperationAnalysis.pathEndingCount === 1 ? '' : 's' }} can currently stage graft or Bridge graft work.
          </small>
        </div>
      </section>

      <section class="path-operation-modal__detail-panel">
        <div class="starting-main-modal__preview-header">
          <div>
            <h4>Selected output action</h4>
            <small>{{ pathOperationStageSummary }}</small>
          </div>
          <span class="header-badge" :class="{ 'header-badge--active': !!pathOperationSelectedCandidate }">
            {{ pathOperationSelectedCandidate ? pathOperationActionLabel : 'No row selected' }}
          </span>
        </div>

        <div v-if="!pathOperationSelectedCandidate" class="network-lookup-modal__empty-state">
          Select a row above to review prune, graft, or Bridge graft details.
        </div>

        <div v-else class="path-operation-modal__selected-card">
          <dl class="path-operation-modal__details-grid">
            <div>
              <dt>Spreadsheet row</dt>
              <dd>{{ pathOperationSelectedCandidate.spreadsheetRowNumber }}</dd>
            </div>
            <div>
              <dt>Path label</dt>
              <dd>{{ pathOperationSelectedCandidate.pathLabel || '—' }}</dd>
            </div>
            <div>
              <dt>Transaction hash</dt>
              <dd class="network-lookup-table__mono path-operation-modal__hash-detail">
                <span>{{ pathOperationSelectedCandidate.hash }}</span>
                <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="pathOperationRunning" @click="copyPathOperationSelectedHash">
                  Copy hash
                </button>
              </dd>
            </div>
            <div>
              <dt>From address</dt>
              <dd class="network-lookup-table__mono">{{ pathOperationSelectedCandidate.fromAddress || '—' }}</dd>
            </div>
            <div>
              <dt>To address</dt>
              <dd class="network-lookup-table__mono">{{ pathOperationSelectedCandidate.toAddress || '—' }}</dd>
            </div>
            <div>
              <dt>Continues as</dt>
              <dd>{{ pathOperationSelectedCandidate.continuesAs || '—' }}</dd>
            </div>
            <div>
              <dt>Attribution</dt>
              <dd>{{ pathOperationSelectedCandidate.attribution || '—' }}</dd>
            </div>
            <div class="path-operation-modal__details-grid-item--wide">
              <dt>System notes — Column J</dt>
              <dd class="path-operation-modal__note-detail">{{ pathOperationSelectedCandidate.systemNotes || '—' }}</dd>
            </div>
            <div class="path-operation-modal__details-grid-item--wide">
              <dt>Analyst notes — Column L</dt>
              <dd class="path-operation-modal__note-detail">{{ pathOperationSelectedCandidate.analystNotes || '—' }}</dd>
            </div>
          </dl>

          <div class="path-operation-modal__action-picker" role="radiogroup" aria-label="Path operation action">
            <label :class="{ 'path-operation-modal__action-option--active': pathOperationAction === 'prune' }">
              <input v-model="pathOperationAction" type="radio" value="prune" :disabled="pathOperationRunning" />
              <span>Prune</span>
              <small>End this output and remove downstream entries in the same main transaction block.</small>
            </label>
            <label :class="{ 'path-operation-modal__action-option--active': pathOperationAction === 'graft', 'path-operation-modal__action-option--disabled': !pathOperationSelectedCandidate.canGraft }">
              <input v-model="pathOperationAction" type="radio" value="graft" :disabled="pathOperationRunning || !pathOperationSelectedCandidate.canGraft" />
              <span>Graft</span>
              <small>Requires a current path ending. Processes raw data with Tool 68 and splices the downstream rows into Main Data.</small>
            </label>
            <label :class="{ 'path-operation-modal__action-option--active': pathOperationAction === 'bridge', 'path-operation-modal__action-option--disabled': !pathOperationSelectedCandidate.canBridge }">
              <input v-model="pathOperationAction" type="radio" value="bridge" :disabled="pathOperationRunning || !pathOperationSelectedCandidate.canBridge" />
              <span>Bridge graft</span>
              <small>Bridge-specific graft with bridge-out / bridge-in note language.</small>
            </label>
          </div>

          <div v-if="pathOperationAction === 'bridge'" class="path-operation-modal__bridge-basis-card">
            <div class="path-operation-modal__bridge-basis-header">
              <div>
                <h5>Bridge amount helper</h5>
                <p>Use the selected row’s funds-followed and left-bounds proportions to estimate what should be followed after the bridge.</p>
              </div>
              <span class="header-badge" :class="{ 'header-badge--active': pathOperationBridgeBasis.valid }">
                {{ pathOperationBridgeBasis.valid ? 'Ready' : 'Needs row values' }}
              </span>
            </div>

            <div class="path-operation-modal__bridge-basis-grid">
              <article>
                <span>Bridge amount before bridge</span>
                <strong>{{ pathOperationBridgeBasis.originalAmountDisplay }}</strong>
                <small>Selected row Column F/G.</small>
              </article>
              <article>
                <span>Funds followed before bridge</span>
                <strong>{{ pathOperationBridgeBasis.fundsFollowedDisplay }}</strong>
                <small>{{ pathOperationBridgeBasis.fundsFollowedRatioLabel }}</small>
              </article>
              <article>
                <span>Left bounds before bridge</span>
                <strong>{{ pathOperationBridgeBasis.leftBoundsDisplay }}</strong>
                <small>{{ pathOperationBridgeBasis.leftBoundsRatioLabel }}</small>
              </article>
            </div>

            <label class="case-details-modal__field path-operation-modal__bridge-received-field">
              <span>Amount received on the other side</span>
              <input
                v-model.trim="pathOperationBridgeReceivedAmount"
                type="text"
                placeholder="e.g. 25 ETH"
                :disabled="pathOperationRunning"
              />
            </label>

            <div v-if="pathOperationBridgeCalculatorResult.valid" class="path-operation-modal__bridge-results">
              <article>
                <span>New funds followed</span>
                <strong>{{ pathOperationBridgeCalculatorResult.followedDisplay }}</strong>
                <small>{{ pathOperationBridgeCalculatorResult.fundsFollowedRatioLabel }} of post-bridge amount.</small>
                <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="pathOperationRunning" @click="copyPathOperationBridgeCalculatorValue('followed')">
                  Copy followed
                </button>
              </article>
              <article>
                <span>New left bounds</span>
                <strong>{{ pathOperationBridgeCalculatorResult.leftBoundsDisplay }}</strong>
                <small>{{ pathOperationBridgeCalculatorResult.leftBoundsRatioLabel }} of post-bridge amount.</small>
                <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="pathOperationRunning" @click="copyPathOperationBridgeCalculatorValue('leftBounds')">
                  Copy left bounds
                </button>
              </article>
              <article>
                <span>Bridge values</span>
                <strong>{{ pathOperationBridgeCalculatorResult.kitSummary }}</strong>
                <small>Copies both values as a two-line note.</small>
                <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="pathOperationRunning" @click="copyPathOperationBridgeCalculatorValue('kit')">
                  Copy both
                </button>
              </article>
            </div>
            <p v-else class="path-operation-modal__bridge-calculator-hint">
              {{ pathOperationBridgeCalculatorResult.message }}
            </p>
          </div>

          <div v-if="pathOperationAction === 'prune'" class="path-operation-modal__preview-box">
            <h5>Prune preview</h5>
            <p>{{ pathOperationPrunePreview.message }}</p>
            <ul>
              <li>{{ pathOperationPrunePreview.descendantEntryCount }} downstream entr{{ pathOperationPrunePreview.descendantEntryCount === 1 ? 'y' : 'ies' }} would be removed.</li>
              <li>{{ pathOperationPrunePreview.rowRemovalCount }} spreadsheet row{{ pathOperationPrunePreview.rowRemovalCount === 1 ? '' : 's' }} would be deleted.</li>
              <li>Column Q on row {{ pathOperationSelectedCandidate.spreadsheetRowNumber }} will become <strong>Path ends.</strong></li>
              <li>Column J will have stale “Continues as”, “Branches as”, and old “Path ends” fragments normalized.</li>
            </ul>
            <label class="network-lookup-modal__checkbox">
              <input v-model="pathOperationConfirmApply" type="checkbox" :disabled="pathOperationRunning" />
              <span>I understand this mutates Main Data for the selected path only.</span>
            </label>
          </div>

          <div v-else class="path-operation-modal__preview-box">
            <h5>{{ pathOperationAction === 'bridge' ? 'Bridge graft apply preview' : 'Graft apply preview' }}</h5>
            <p>
              Select a raw-data match or paste raw TSV for this selected output. The selected raw data will be processed with Tool 68, the first/root entry will be handled according to the action, and the resulting rows will be spliced into Main Data after confirmation.
            </p>

            <label class="case-details-modal__field">
              <span>Suggested raw data set</span>
              <select v-model="pathOperationSelectedRawSetId" :disabled="pathOperationRunning || pathOperationSelectedCandidate.rawCandidates.length === 0" @change="stageSelectedRawSetForPathOperation">
                <option value="">Paste raw TSV manually</option>
                <option v-for="rawSet in pathOperationSelectedCandidate.rawCandidates" :key="rawSet.id" :value="rawSet.id">
                  Raw row {{ rawSet.displayRootRow }} · {{ rawSet.matchLabel }}
                </option>
              </select>
            </label>

            <div v-if="pathOperationAction === 'bridge'" class="path-operation-modal__bridge-tools">
              <div class="path-operation-modal__bridge-fields">
                <label class="case-details-modal__field">
                  <span>Bridge service</span>
                  <input
                    v-model.trim="pathOperationBridgeService"
                    type="text"
                    list="case-bridge-service-options"
                    placeholder="e.g. Wormhole, Rango, NewBridgeService"
                    :disabled="pathOperationRunning"
                  />
                  <datalist id="case-bridge-service-options">
                    <option v-for="definition in pathOperationBridgeServiceDefinitions" :key="definition.canonicalName" :value="definition.canonicalName" />
                  </datalist>
                </label>
                <label class="case-details-modal__field">
                  <span>Destination network</span>
                  <input v-model.trim="pathOperationBridgeDestinationNetwork" type="text" placeholder="e.g. TRON" :disabled="pathOperationRunning" />
                </label>
                <label class="case-details-modal__field">
                  <span>Destination token</span>
                  <input v-model.trim="pathOperationBridgeDestinationToken" type="text" placeholder="Optional token" :disabled="pathOperationRunning" />
                </label>
              </div>

              <div class="path-operation-modal__bridge-assist-card">
                <div class="path-operation-modal__bridge-assist-header">
                  <div>
                    <h5>Bridge service assistant</h5>
                    <p>{{ pathOperationBridgeAssistSummary }}</p>
                  </div>
                  <span v-if="pathOperationBridgeActiveMatch.isMatch" class="header-badge header-badge--active">
                    {{ pathOperationBridgeActiveMatch.canonicalName }}
                  </span>
                  <span v-else class="header-badge">No registry match</span>
                </div>

                <div v-if="pathOperationBridgeDetectedMatch.isMatch" class="path-operation-modal__bridge-detected">
                  <span>Detected from selected row:</span>
                  <strong>{{ pathOperationBridgeDetectedMatch.canonicalName }}</strong>
                  <small v-if="pathOperationBridgeDetectedMatch.matchedAlias">Matched “{{ pathOperationBridgeDetectedMatch.matchedAlias }}”.</small>
                  <button
                    v-if="pathOperationBridgeDetectedMatch.canonicalName !== pathOperationBridgeService"
                    class="toolbar-btn toolbar-btn--small"
                    type="button"
                    :disabled="pathOperationRunning"
                    @click="useDetectedBridgeService"
                  >
                    Use detected service
                  </button>
                </div>

                <div v-if="pathOperationBridgeActiveMatch.isMatch" class="path-operation-modal__bridge-link-row">
                  <a
                    v-if="pathOperationBridgeActiveMatch.explorerUrl"
                    class="toolbar-btn toolbar-btn--primary toolbar-btn--small path-operation-modal__bridge-link"
                    :href="pathOperationBridgeActiveMatch.explorerUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ pathOperationBridgeActiveMatch.explorerLabel || 'Open bridge explorer' }}
                  </a>
                  <span v-else class="path-operation-modal__bridge-no-link">
                    No direct explorer URL is registered for this service yet.
                  </span>
                  <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="pathOperationRunning" @click="copyPathOperationBridgeSearchText">
                    Copy bridge search kit
                  </button>
                  <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="pathOperationRunning" @click="copyPathOperationBridgeParentHash">
                    Copy parent tx hash
                  </button>
                </div>

                <p v-if="pathOperationBridgeActiveMatch.searchHint" class="path-operation-modal__bridge-hint">
                  {{ pathOperationBridgeActiveMatch.searchHint }}
                </p>
                <p v-else-if="pathOperationBridgeActiveMatch.explorerUrl" class="path-operation-modal__bridge-hint">
                  Open the bridge explorer and search for the selected parent transaction hash, then paste the post-bridge raw TSV below.
                </p>
                <p v-else class="path-operation-modal__bridge-hint">
                  Choose a known bridge service or type a custom service name. The typed service will still be used in bridge-out / bridge-in notes.
                </p>

                <details class="path-operation-modal__bridge-known-services">
                  <summary>Known bridge services and explorer links</summary>
                  <div class="path-operation-modal__bridge-known-list">
                    <article v-for="definition in pathOperationBridgeServiceDefinitions" :key="definition.canonicalName">
                      <div>
                        <strong>{{ definition.canonicalName }}</strong>
                        <small>{{ definition.explorerLabel || 'Bridge service' }}</small>
                      </div>
                      <small v-if="definition.searchHint" class="path-operation-modal__bridge-known-hint">
                        {{ definition.searchHint }}
                      </small>
                      <small v-if="definition.aliases && definition.aliases.length" class="path-operation-modal__bridge-known-aliases">
                        Aliases: {{ definition.aliases.slice(0, 5).join(', ') }}{{ definition.aliases.length > 5 ? ', +' + (definition.aliases.length - 5) + ' more' : '' }}
                      </small>
                      <div class="path-operation-modal__bridge-known-actions">
                        <button class="toolbar-btn toolbar-btn--small" type="button" :disabled="pathOperationRunning" @click="pathOperationBridgeService = definition.canonicalName">
                          Use
                        </button>
                        <a v-if="definition.explorerUrl" :href="definition.explorerUrl" target="_blank" rel="noopener noreferrer">Open</a>
                        <span v-else>No direct link</span>
                      </div>
                    </article>
                  </div>
                </details>
              </div>
            </div>

            <label class="case-details-modal__field">
              <span>Raw TSV for graft / Bridge graft</span>
              <textarea v-model="pathOperationRawTsv" rows="8" placeholder="Paste raw Case Buddy TSV here, or choose a matching raw set above." :disabled="pathOperationRunning"></textarea>
            </label>

            <label class="network-lookup-modal__checkbox">
              <input v-model="pathOperationConfirmApply" type="checkbox" :disabled="pathOperationRunning" />
              <span>I understand this mutates Main Data for the selected path only.</span>
            </label>
          </div>
        </div>

        <div v-if="pathOperationStatus" class="network-lookup-modal__status path-operation-modal__status">
          {{ pathOperationStatus }}
        </div>
      </section>
    </div>

    <div class="network-lookup-modal__footer-note">
      Qualification is based on Main Data only. Prune can target any row with funds followed and left-bounds data; graft and Bridge graft require a current <strong>Path ends.</strong> row and explicit confirmation before changing Main Data.
    </div>

    <div class="path-operation-modal__completion-card">
      <div>
        <strong>Finished reviewing prune / graft / Bridge needs?</strong>
        <small>
          Use this when the current attribution/path-operation pass feels complete. You can return later if new raw data, grafts, or Bridge paths create more work.
        </small>
      </div>
      <button
        class="toolbar-btn toolbar-btn--done"
        type="button"
        :disabled="caseModalOperation.active || pathOperationRunning || attributionPathOperationsManuallyComplete"
        @click="runCaseModalMethod('markAttributionPathOperationsCompleteFromModal', { title: 'Marking path review complete…', message: 'Saving path-operation review completion.' })"
      >
        {{ attributionPathOperationsManuallyComplete ? 'Review marked complete' : 'Mark path review complete' }}
      </button>
    </div>

    <div class="agent-modal__actions">
      <button
        v-if="pathOperationAction === 'prune'"
        class="toolbar-btn toolbar-btn--primary"
        type="button"
        :disabled="!pathOperationCanApplyPrune"
        @click="applySelectedPathPrune"
      >
        {{ pathOperationRunning ? 'Applying prune…' : 'Apply prune' }}
      </button>
      <button
        v-else
        class="toolbar-btn toolbar-btn--primary"
        type="button"
        :disabled="!pathOperationCanStageExtension"
        @click="stageSelectedPathExtension"
      >
        {{ pathOperationRunning ? 'Applying…' : (pathOperationAction === 'bridge' ? 'Apply Bridge graft' : 'Apply graft') }}
      </button>
      <button class="toolbar-btn" type="button" :disabled="pathOperationRunning" @click="activateLogicalSheet('main')">
        Open Main Data
      </button>
      <button class="toolbar-btn" type="button" :disabled="pathOperationRunning" @click="activateLogicalSheet('raw')">
        Open Raw Data
      </button>
      <button
        class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button"
        type="button"
        :disabled="caseModalOperation.active || pathOperationRunning || attributionPathOperationsManuallyComplete"
        @click="runCaseModalMethod('markAttributionPathOperationsCompleteFromModal', { title: 'Marking path review complete…', message: 'Saving path-operation review completion.' })"
      >
        {{ attributionPathOperationsManuallyComplete ? 'Review marked complete' : 'Mark path review complete' }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="pathOperationRunning" @click="cancelPathOperationModal">
        Close
      </button>
    </div>
  </div>
</div>

<div v-if="showEntityCuratorModal" class="agent-modal-backdrop" @click="cancelEntityCuratorReviewModal">
  <div class="agent-modal network-lookup-modal entity-curator-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>Review New Entities And Local Curators</h3>
        <p>
          The server curator is read-only here. Case Buddy loads the current server exchange/non-exchange lists, then applies your local additions, removals, and suppressions across cases and sessions.
        </p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="entityCuratorLoading" @click="cancelEntityCuratorReviewModal">
        Close
      </button>
    </div>

    <div class="entity-curator-modal__notice">
      <strong>Case-sensitive matching:</strong>
      <span>Known entity terms must appear with the same casing and spacing in Column J notes to be treated as known. For example, use <strong>Coinbase</strong>, not <strong>coinbase</strong> or <strong>Coin Base</strong>, unless you intentionally add that exact local term.</span>
    </div>

    <div class="network-lookup-modal__summary-grid entity-curator-modal__summary-grid">
      <article>
        <span>Reviewable</span>
        <strong>{{ entityCuratorReviewableCount }}</strong>
        <small>Potential new or casing-sensitive entities.</small>
      </article>
      <article>
        <span>Known</span>
        <strong>{{ entityCuratorKnownCount }}</strong>
        <small>Matched effective curator entries.</small>
      </article>
      <article>
        <span>Suppressed</span>
        <strong>{{ entityCuratorSuppressedCount }}</strong>
        <small>Filtered as non-entity noise.</small>
      </article>
      <article>
        <span>Server Lists</span>
        <strong>{{ entityCuratorAnalysis.remoteExchangeCount + entityCuratorAnalysis.remoteNonExchangeCount }}</strong>
        <small>{{ entityCuratorRemote.status === 'success' ? 'Loaded' : entityCuratorRemote.status }}{{ entityCuratorRemote.lastLoadedUtc ? ` · ${new Date(entityCuratorRemote.lastLoadedUtc).toLocaleTimeString()}` : '' }}</small>
      </article>
    </div>

    <div class="entity-curator-modal__tabs" role="tablist" aria-label="Entity curator review tabs">
      <button
        class="toolbar-btn"
        :class="{ 'toolbar-btn--primary': entityCuratorActiveTab === 'review' }"
        type="button"
        @click="entityCuratorActiveTab = 'review'"
      >
        Review Cards
      </button>
      <button
        class="toolbar-btn"
        :class="{ 'toolbar-btn--primary': entityCuratorActiveTab === 'curators' }"
        type="button"
        @click="entityCuratorActiveTab = 'curators'"
      >
        Curator Lists
      </button>
      <button
        class="toolbar-btn"
        :class="{ 'toolbar-btn--primary': entityCuratorActiveTab === 'local' }"
        type="button"
        @click="entityCuratorActiveTab = 'local'"
      >
        Local Changes
      </button>
      <button class="toolbar-btn" type="button" :disabled="entityCuratorLoading" @click="refreshEntityCuratorReview({ forceRemote: true })">
        {{ entityCuratorLoading ? 'Refreshing…' : 'Refresh Server Lists' }}
      </button>
      <button class="toolbar-btn" type="button" @click="copyEntityCuratorReviewTsv">
        Copy Review TSV
      </button>
      <button
        v-if="entityCuratorCaseMismatchFixCount > 0"
        class="toolbar-btn toolbar-btn--primary entity-curator-modal__match-all-button"
        type="button"
        :disabled="caseModalOperation.active || entityCuratorLoading"
        @click="runCaseModalMethod('applyAllEntityCuratorCaseMismatchCasingFixes', { title: 'Matching known-entity casing…', message: 'Updating exact Column J casing differences to match the curator.' })"
      >
        Match {{ entityCuratorCaseMismatchFixCount }} casing group{{ entityCuratorCaseMismatchFixCount === 1 ? '' : 's' }}
      </button>
    </div>

    <section v-if="entityCuratorActiveTab === 'review'" class="entity-curator-modal__review-section">
      <div class="entity-curator-modal__controls">
        <label class="case-details-modal__field">
          <span>Search review cards</span>
          <input v-model.trim="entityCuratorSearch" type="search" placeholder="Search candidate, row, note, hash…" />
        </label>
        <label class="case-details-modal__field">
          <span>Show</span>
          <select v-model="entityCuratorViewFilter">
            <option value="reviewable">Reviewable only</option>
            <option value="known">Known matches</option>
            <option value="suppressed">Suppressed/noise</option>
            <option value="all">All groups</option>
          </select>
        </label>
      </div>

      <div v-if="entityCuratorFilteredGroups.length === 0" class="network-lookup-modal__empty-state">
        No entity review cards match the current filter. Try showing all groups or refresh the server lists.
      </div>

      <div v-else class="entity-curator-modal__card-list">
        <article
          v-for="group in entityCuratorFilteredGroups"
          :key="group.id"
          class="entity-curator-card"
          :class="{
            'entity-curator-card--reviewable': group.isReviewable,
            'entity-curator-card--known': group.isKnownMatch,
            'entity-curator-card--suppressed': group.isSuppressed
          }"
        >
          <div class="entity-curator-card__header">
            <div>
              <h4>{{ group.displayName }}</h4>
              <p>{{ group.suggestedSummary }}</p>
            </div>
            <span class="header-badge" :class="{ 'header-badge--active': group.isReviewable }">
              {{ group.hitCount }} hit{{ group.hitCount === 1 ? '' : 's' }}
            </span>
          </div>

          <div class="entity-curator-card__badges">
            <span v-for="badge in group.badges" :key="`${group.id}-${badge}`">{{ badge }}</span>
          </div>

          <div v-if="group.isCaseMismatch" class="entity-curator-card__case-warning">
            <strong>Known name with different casing:</strong>
            <span>
              This looks like curator term <strong>{{ group.caseMismatchName }}</strong>. The usual fix is to update Column J to the curator spelling, for example <code>{{ group.displayName }}</code> → <code>{{ group.caseMismatchName }}</code>, not to add a duplicate local term.
            </span>
            <small v-if="group.caseMismatchActionSummary">{{ group.caseMismatchActionSummary }}</small>
          </div>

          <div class="entity-curator-card__actions">
            <button
              v-if="group.isCaseMismatch"
              class="toolbar-btn toolbar-btn--small toolbar-btn--primary"
              type="button"
              :disabled="caseModalOperation.active || !group.canApplyCaseMismatchCasingFix"
              @click="runCaseModalMethod('applyEntityCuratorCaseMismatchCasingFix', { title: 'Matching curator casing…', message: 'Updating this exact Column J casing difference to match the curator.' }, group)"
            >
              Match curator casing
            </button>
            <button
              class="toolbar-btn toolbar-btn--small"
              :class="{ 'toolbar-btn--primary': !group.isCaseMismatch }"
              type="button"
              @click="addEntityCandidateToCurator(group, 'exchange')"
            >
              {{ group.isCaseMismatch ? 'Add exact Exchange' : 'Add Exchange' }}
            </button>
            <button class="toolbar-btn toolbar-btn--small" type="button" @click="addEntityCandidateToCurator(group, 'non_exchange')">
              {{ group.isCaseMismatch ? 'Add exact Non-exchange' : 'Add Non-exchange' }}
            </button>
            <button class="toolbar-btn toolbar-btn--small toolbar-btn--ghost" type="button" @click="suppressEntityCuratorCandidate(group)">
              Suppress Noise
            </button>
            <button class="toolbar-btn toolbar-btn--small" type="button" @click="copyEntityCuratorCandidateSearch(group)">
              Copy Search
            </button>
          </div>

          <details class="entity-curator-card__hits" :open="group.isReviewable">
            <summary>Rows and excerpts</summary>
            <div class="entity-curator-card__hit-list">
              <article v-for="hit in group.hits.slice(0, 10)" :key="`${group.id}-${hit.sheetRowNumber}-${hit.sourceField}-${hit.excerpt}`">
                <strong>Row {{ hit.sheetRowNumber }} · {{ hit.sourceField }}</strong>
                <small v-if="hit.pathLabel">Path {{ hit.pathLabel }}</small>
                <small v-if="hit.transactionHash" class="network-lookup-table__mono">{{ hit.transactionHash }}</small>
                <p>{{ hit.excerpt }}</p>
              </article>
              <small v-if="group.hits.length > 10">{{ group.hits.length - 10 }} more hit{{ group.hits.length - 10 === 1 ? '' : 's' }} hidden for readability.</small>
            </div>
          </details>
        </article>
      </div>
    </section>

    <section v-else-if="entityCuratorActiveTab === 'curators'" class="entity-curator-modal__curator-section">
      <article class="entity-curator-list-panel">
        <div class="entity-curator-list-panel__header">
          <div>
            <h4>Exchange curator</h4>
            <p>{{ entityCuratorEffectiveExchangeItems.length }} effective term{{ entityCuratorEffectiveExchangeItems.length === 1 ? '' : 's' }}</p>
          </div>
        </div>
        <div class="entity-curator-list-panel__add-row">
          <input v-model.trim="entityCuratorNewExchangeName" type="text" placeholder="Exact exchange name, e.g. Coinbase" @keyup.enter="addManualExchangeCuratorTerm" />
          <button class="toolbar-btn toolbar-btn--primary" type="button" @click="addManualExchangeCuratorTerm">Add</button>
        </div>
        <div class="entity-curator-list-panel__items">
          <div v-for="item in entityCuratorEffectiveExchangeItems" :key="item.id" class="entity-curator-list-item">
            <span>{{ item.name }}</span>
            <small>{{ item.sourceLabel }}</small>
            <button class="toolbar-btn toolbar-btn--small toolbar-btn--ghost" type="button" @click="removeEntityCuratorItem(item)">
              {{ item.source === 'local' ? 'Remove' : 'Hide locally' }}
            </button>
          </div>
        </div>
      </article>

      <article class="entity-curator-list-panel">
        <div class="entity-curator-list-panel__header">
          <div>
            <h4>Non-exchange curator</h4>
            <p>{{ entityCuratorEffectiveNonExchangeItems.length }} effective term{{ entityCuratorEffectiveNonExchangeItems.length === 1 ? '' : 's' }}</p>
          </div>
        </div>
        <div class="entity-curator-list-panel__add-row">
          <input v-model.trim="entityCuratorNewNonExchangeName" type="text" placeholder="Exact non-exchange name" @keyup.enter="addManualNonExchangeCuratorTerm" />
          <button class="toolbar-btn toolbar-btn--primary" type="button" @click="addManualNonExchangeCuratorTerm">Add</button>
        </div>
        <div class="entity-curator-list-panel__items">
          <div v-for="item in entityCuratorEffectiveNonExchangeItems" :key="item.id" class="entity-curator-list-item">
            <span>{{ item.name }}</span>
            <small>{{ item.sourceLabel }}</small>
            <button class="toolbar-btn toolbar-btn--small toolbar-btn--ghost" type="button" @click="removeEntityCuratorItem(item)">
              {{ item.source === 'local' ? 'Remove' : 'Hide locally' }}
            </button>
          </div>
        </div>
      </article>
    </section>

    <section v-else class="entity-curator-modal__local-section">
      <article class="entity-curator-list-panel entity-curator-list-panel--wide">
        <h4>Local suppressions / noise filters</h4>
        <p>Use this for noisy fragments that are not real entities. Suppressions persist locally and apply across cases.</p>
        <div class="entity-curator-list-panel__add-row">
          <input v-model.trim="entityCuratorNewSuppression" type="text" placeholder="Noise term to suppress" @keyup.enter="addManualSuppressedEntityTerm" />
          <button class="toolbar-btn toolbar-btn--primary" type="button" @click="addManualSuppressedEntityTerm">Suppress</button>
        </div>
        <div class="entity-curator-list-panel__chips">
          <span v-for="term in entityCuratorLocalOverlay.suppressedTerms" :key="`suppress-${term}`">
            {{ term }}
            <button type="button" @click="unsuppressEntityCuratorTerm(term)">×</button>
          </span>
          <small v-if="!entityCuratorLocalOverlay.suppressedTerms.length">No local suppressions yet.</small>
        </div>
      </article>

      <article class="entity-curator-list-panel entity-curator-list-panel--wide">
        <h4>Hidden server terms</h4>
        <p>These are not deleted from the server. They are only removed from your local effective curator view.</p>
        <div class="entity-curator-modal__hidden-grid">
          <div>
            <strong>Exchange hidden locally</strong>
            <span v-for="term in entityCuratorLocalOverlay.exchangeRemovals" :key="`hidden-ex-${term}`">
              {{ term }}
              <button class="toolbar-btn toolbar-btn--small" type="button" @click="restoreRemoteCuratorTerm('exchange', term)">Restore</button>
            </span>
            <small v-if="!entityCuratorLocalOverlay.exchangeRemovals.length">None.</small>
          </div>
          <div>
            <strong>Non-exchange hidden locally</strong>
            <span v-for="term in entityCuratorLocalOverlay.nonExchangeRemovals" :key="`hidden-non-${term}`">
              {{ term }}
              <button class="toolbar-btn toolbar-btn--small" type="button" @click="restoreRemoteCuratorTerm('non_exchange', term)">Restore</button>
            </span>
            <small v-if="!entityCuratorLocalOverlay.nonExchangeRemovals.length">None.</small>
          </div>
        </div>
      </article>
    </section>

    <div v-if="entityCuratorStatus" class="network-lookup-modal__status entity-curator-modal__status">
      {{ entityCuratorStatus }}
    </div>

    <div class="path-operation-modal__completion-card entity-curator-modal__completion-card">
      <div>
        <strong>Finished reviewing new entity candidates?</strong>
        <small>
          Use this when the current local curator decisions are good enough for this iteration. You can return later after grafts, bridges, or note changes reveal new names.
        </small>
      </div>
      <button
        class="toolbar-btn toolbar-btn--done"
        type="button"
        :disabled="caseModalOperation.active || entityCuratorLoading || entityCuratorReviewIsCurrent"
        @click="runCaseModalMethod('markEntityCuratorReviewCompleteFromModal', { title: 'Marking entity curator reviewed…', message: 'Saving curator review completion.' })"
      >
        {{ entityCuratorCompletionLabel }}
      </button>
    </div>

    <div class="network-lookup-modal__footer-note">
      Server lists are loaded from the same read-only keyword endpoints used by existing /blockchaintools curator-aware tools. Your local overlay is stored in browser local storage and does not update the admin curator.
    </div>

    <div class="agent-modal__actions entity-curator-modal__actions">
      <button
        class="toolbar-btn toolbar-btn--done agent-modal__actions-review-button"
        type="button"
        :disabled="caseModalOperation.active || entityCuratorLoading || entityCuratorReviewIsCurrent"
        @click="runCaseModalMethod('markEntityCuratorReviewCompleteFromModal', { title: 'Marking entity curator reviewed…', message: 'Saving curator review completion.' })"
      >
        {{ entityCuratorCompletionLabel }}
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" :disabled="entityCuratorLoading" @click="cancelEntityCuratorReviewModal">
        Close
      </button>
    </div>
  </div>
</div>

<div v-if="showWorkflowStepHelpModal && activeWorkflowStepHelp" class="agent-modal-backdrop" @click="closeWorkflowStepHelpModal">
  <div class="agent-modal workflow-step-help-modal" @click.stop>
    <div class="network-lookup-modal__header workflow-step-help-modal__header">
      <div>
        <span class="workflow-step-help-modal__eyebrow">Workflow help</span>
        <h3>{{ activeWorkflowStepHelp.title }}</h3>
        <p>{{ activeWorkflowStepHelp.summary }}</p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close workflow help" @click="closeWorkflowStepHelpModal">
        ×
      </button>
    </div>

    <div class="workflow-step-help-modal__summary-grid">
      <article>
        <span>Goal</span>
        <strong>{{ activeWorkflowStepHelp.goal }}</strong>
      </article>
      <article>
        <span>Why it matters</span>
        <strong>{{ activeWorkflowStepHelp.why }}</strong>
      </article>
    </div>

    <section class="workflow-step-help-modal__section">
      <h4>What to do</h4>
      <ol>
        <li v-for="item in activeWorkflowStepHelp.whatToDo" :key="`workflow-help-do-${activeWorkflowStepHelp.tag}-${item}`">
          {{ item }}
        </li>
      </ol>
    </section>

    <section class="workflow-step-help-modal__section">
      <h4>How you know it is done</h4>
      <ul>
        <li v-for="item in activeWorkflowStepHelp.doneWhen" :key="`workflow-help-done-${activeWorkflowStepHelp.tag}-${item}`">
          {{ item }}
        </li>
      </ul>
    </section>

    <section v-if="activeWorkflowStepHelp.tips.length" class="workflow-step-help-modal__section workflow-step-help-modal__section--tips">
      <h4>Helpful tips</h4>
      <ul>
        <li v-for="item in activeWorkflowStepHelp.tips" :key="`workflow-help-tip-${activeWorkflowStepHelp.tag}-${item}`">
          {{ item }}
        </li>
      </ul>
    </section>

    <section v-if="activeWorkflowStepHelp.actionLabels.length" class="workflow-step-help-modal__section workflow-step-help-modal__section--actions">
      <h4>Buttons you may use here</h4>
      <div class="workflow-step-help-modal__chips">
        <span v-for="label in activeWorkflowStepHelp.actionLabels" :key="`workflow-help-action-${activeWorkflowStepHelp.tag}-${label}`">
          {{ label }}
        </span>
      </div>
    </section>

    <p class="workflow-step-help-modal__source-note">
      Reminder: always validate results and check if path endings could be bridges, services, etc.  Avoid tracing through services.
    </p>

    <div class="agent-modal__actions workflow-step-help-modal__actions">
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="closeWorkflowStepHelpModal">
        Close
      </button>
    </div>
  </div>
</div>

<div v-if="showCaseAutomationPauseModal" class="agent-modal-backdrop" @click="closeCaseAutomationPauseModal">
  <div class="agent-modal network-lookup-modal case-automation-pause-modal" @click.stop>
    <div class="network-lookup-modal__header">
      <div>
        <h3>{{ caseAutomationPauseModalTitle }}</h3>
        <p>{{ caseAutomationPauseModalMessage }}</p>
      </div>
      <button class="toolbar-btn toolbar-btn--ghost toolbar-btn--icon" type="button" aria-label="Close automation pause" @click="closeCaseAutomationPauseModal">
        ×
      </button>
    </div>

    <div class="case-automation-pause-modal__summary">
      <article>
        <span>Current step</span>
        <strong>{{ caseAutomation.manualPause?.stepTitle || recommendedWorkflowStepTitle }}</strong>
        <small>{{ caseAutomation.manualPause?.stepTag || 'Workflow' }}</small>
      </article>
      <article>
        <span>Automation</span>
        <strong>{{ caseAutomation.active ? (caseAutomation.paused ? 'Paused' : 'Running') : 'Stopped' }}</strong>
        <small>{{ caseAutomationReviewModeLabel }}</small>
      </article>
      <article>
        <span>Checks</span>
        <strong>{{ validationStateLabel }}</strong>
        <small>{{ validationRevisionLabel }}</small>
      </article>
    </div>

    <div class="case-automation-pause-modal__body">
      <p>
        This pause is intentional. Case Buddy can guide the next helper, but this step needs investigator-supplied data or judgement before automation should continue.
      </p>
      <ul>
        <li v-if="caseAutomation.manualPause?.pauseKind === 'starting-main-transactions'">Paste the known starting transactions into Main Data, then choose <strong>Done / continue</strong>.</li>
        <li v-if="caseAutomation.manualPause?.pauseKind === 'raw-data-fetch'">{{ caseAutomationRawDataPauseInstruction }}</li>
        <li v-if="caseAutomation.manualPause?.pauseKind === 'address-attribution'">Fill known Column K attributions, or use Unknown where that is the correct analyst decision.</li>
        <li v-if="caseAutomation.manualPause?.pauseKind === 'path-operations'">Apply any needed prune, graft, or Bridge graft decisions before marking the step complete.</li>
        <li v-if="caseAutomation.manualPause?.pauseKind === 'entity-review'">Add real local curator terms or suppress noise, then mark the review complete.</li>
        <li v-if="caseAutomation.manualPause?.pauseKind === 'case-inconsistencies'">Resolve the remaining inconsistency cards or enable skip mode when rubber-stamping is appropriate.</li>
        <li v-if="caseAutomation.manualPause?.pauseKind === 'addendum-recipients'">Choose the intended recipient entity for ambiguous addendum rows.</li>
      </ul>

      <div v-if="caseAutomation.manualPause?.pauseKind === 'raw-data-fetch' && caseAutomationRawCoveragePauseRows.length" class="case-automation-raw-targets">
        <h4>Raw data targets still needing coverage</h4>
        <div class="case-automation-raw-targets__list">
          <article v-for="row in caseAutomationRawCoveragePauseRows" :key="row.id">
            <strong>{{ row.pathLabel || `Main row ${row.spreadsheetRowNumber}` }}</strong>
            <span>{{ row.statusLabel }}</span>
            <code>{{ row.hash || 'No transaction hash found' }}</code>
            <small v-if="row.toAddress">To Address: <code>{{ row.toAddress }}</code></small>
          </article>
        </div>
      </div>

      <div v-if="caseWorkflowRawDataTool88DebugAvailable && caseAutomation.manualPause?.pauseKind === 'raw-data-fetch'" class="case-automation-tool88-debug-card">
        <div>
          <span>Flow Solver raw-data diagnostics</span>
          <strong>{{ caseWorkflowRawDataTool88PauseDebugSummary }}</strong>
          <small>{{ caseWorkflowRawDataTool88PauseDebugDetail }}</small>
        </div>
        <div class="case-automation-tool88-debug-card__chips">
          <span v-for="chip in caseWorkflowRawDataTool88PauseDebugChips" :key="chip.label">
            <strong>{{ chip.label }}</strong>
            {{ chip.value }}
          </span>
        </div>
        <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="copyCaseWorkflowRawDataTool88DebugSnapshot">
          Copy debug log
        </button>
      </div>
    </div>

    <div class="agent-modal__actions case-automation-pause-modal__actions">
      <button
        v-if="caseAutomation.manualPause?.primaryMethod"
        class="toolbar-btn toolbar-btn--primary"
        type="button"
        @click="runCaseAutomationPausePrimaryAction"
      >
        {{ caseAutomation.manualPause?.primaryLabel || 'Open helper' }}
      </button>
      <button
        v-if="caseWorkflowRawDataTool88DebugAvailable"
        class="toolbar-btn"
        type="button"
        title="Copy raw-data Auto, Flow Solver, and fallback state for troubleshooting"
        @click="copyCaseWorkflowRawDataTool88DebugSnapshot"
      >
        Copy Flow Solver debug log
      </button>
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="caseChecksRunning || caseAutomation.busy" @click="caseAutomationManualPauseDone">
        Done / continue
      </button>
      <button v-if="caseAutomation.active" class="toolbar-btn toolbar-btn--ghost" type="button" @click="requestCaseAutomationUserStop('Automation stopped at manual pause.')">
        Stop Auto
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="closeCaseAutomationPauseModal">
        Close
      </button>
    </div>
  </div>
</div>

<div v-if="showCaseDetailsEditor" class="agent-modal-backdrop" @click="cancelCaseDetailsEditor">
  <div class="agent-modal case-details-modal" @click.stop>
    <h3>Case Details</h3>
    <p>These details are stored with this platform file's case metadata.</p>

    <label class="case-details-modal__field">
      <span>Case name</span>
      <input v-model.trim="caseDetailsDraft.caseName" type="text" placeholder="Optional case name" />
    </label>

    <label class="case-details-modal__field">
      <span>Case ID</span>
      <input v-model.trim="caseDetailsDraft.caseId" type="text" placeholder="Optional case id" />
    </label>

    <label class="case-details-modal__field">
      <span>Case type</span>
      <input v-model.trim="caseDetailsDraft.caseType" type="text" placeholder="Optional case type" />
    </label>

    <div class="agent-modal__actions">
      <button class="toolbar-btn toolbar-btn--primary" type="button" @click="saveCaseDetailsEditor">
        Save details
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelCaseDetailsEditor">
        Cancel
      </button>
    </div>
  </div>
</div>

<div v-if="showCaseDetailsReviewAcknowledgementModal" class="agent-modal-backdrop" @click="cancelCaseDetailsReviewAcknowledgement">
  <div class="agent-modal case-details-review-ack-modal" @click.stop>
    <h3>Confirm case file review</h3>
    <p>Before marking this workflow step complete, confirm that you actually paused to read the submitted case files and early narrative materials outside Case Buddy.</p>

    <div class="case-details-review-ack-modal__notice">
      <strong>This is an investigator acknowledgement.</strong>
      <span>Case Buddy can store case metadata, but it cannot know whether the external PDFs, emails, reports, or intake files were read. Confirm only after you have reviewed them.</span>
    </div>

    <label class="case-details-review-ack-modal__checkbox">
      <input v-model="caseDetailsReviewAcknowledgementReadFiles" type="checkbox" />
      <span>I have read the case files / intake materials and reviewed the early narrative details.</span>
    </label>

    <div v-if="caseDetailsReviewAcknowledgementStatus" class="network-lookup-modal__status">
      {{ caseDetailsReviewAcknowledgementStatus }}
    </div>

    <div class="agent-modal__actions">
      <button class="toolbar-btn toolbar-btn--done" type="button" :disabled="!caseDetailsReviewAcknowledgementReadFiles" @click="confirmCaseDetailsReviewAcknowledgement">
        Record review & continue
      </button>
      <button class="toolbar-btn toolbar-btn--ghost" type="button" @click="cancelCaseDetailsReviewAcknowledgement">
        Not yet
      </button>
    </div>
  </div>
</div>

<div v-if="showCaseAddressMonitoringModal" class="agent-modal-backdrop case-address-monitoring-modal-backdrop" @click="closeCaseAddressMonitoringModal">
  <div class="agent-modal case-address-monitoring-modal" @click.stop>
    <div v-if="caseAddressMonitoringStatus" class="network-lookup-modal__status case-address-monitoring-modal__status">
      {{ caseAddressMonitoringStatus }}
    </div>
    <AddressMonitoringPage
      ref="caseAddressMonitoringPage"
      embedded
      :case-filter="caseAddressMonitoringFilter"
      @close="closeCaseAddressMonitoringModal"
      @items-loaded="handleCaseAddressMonitoringItemsLoaded"
    />
  </div>
</div>

<div v-if="caseToast" class="case-toast" :class="caseToastClass" role="status" aria-live="polite" @click="dismissCaseToast">
  <span class="case-toast__message">{{ caseToast }}</span>
  <button class="case-toast__dismiss" type="button" aria-label="Dismiss notification" @click.stop="dismissCaseToast">
    <span class="case-toast__dismiss-icon" aria-hidden="true"></span>
  </button>
</div>

  </div>
</template>

<script>

import HermesSpreadsheetComponentAdvanced from '../components/HermesSpreadsheetComponentAdvanced.vue';
import HermesSpreadsheetWorkbookTabs from '../components/HermesSpreadsheetWorkbookTabs.vue';
import BorderModal from '../components/BorderModal.vue';
import Tool88Page_CaseBuddy from './Tool88Page_CaseBuddy.vue';
import AddressMonitoringPage from './AddressMonitoringPage.vue';
import { authenticatedFetch } from '@/services/authenticatedFetch';
import {
  CASE_BRIDGE_SERVICE_DEFINITIONS as CASE_BRIDGE_SERVICE_DEFINITIONS_FROM_REGISTRY,
  findBestCaseBridgeServiceMatch as findBestCaseBridgeServiceMatchFromRegistry
} from '../utils/casebuddy/CaseBuddyBridgeServices.js';
import {
  estimateCaseBuddyHistoricalUsdValue,
  getCaseBuddyHistoricalPriceReferenceMetadata
} from '../utils/CaseBuddyHistoricalUsdReference.js';
import {
  addCaseWwwTokenTotal,
  buildBridgeSearchTextFromPathOperationCandidate,
  buildCaseAddressLegendAnalysis,
  buildCaseDepositHotWalletReviewAnalysis,
  buildCaseDepositPatternReviewAnalysis,
  buildCaseDuplicateHashLabelerAnalysis,
  buildCaseEntityCuratorReviewAnalysis,
  buildCaseWwwEntryLookup,
  buildCaseWwwReferenceRows,
  buildCaseWwwUsdReferenceReviewState,
  buildCaseWwwTokenBreakdown,
  buildCaseInconsistencyReviewAnalysis,
  buildCaseKnownEntityCasingReviewAnalysis,
  buildCaseNoteHashRewriteAnalysis,
  buildCaseTemplateDeliverableRows,
  buildEffectiveEntityCurators,
  buildMainRawDataCoverage,
  buildPathOperationAnalysis,
  buildPrunePreviewForCandidate,
  buildRawDataAppendMatrix,
  CASE_BRIDGE_SERVICE_DEFINITIONS,
  CASE_ADDENDUM_VARIANT_NARRATIVE,
  CASE_ADDENDUM_VARIANT_STRUCTURED,
  CASE_DELIVERABLE_GROUPS,
  CASE_FLOW_VISUALS_ADDRESS_RADIUS,
  CASE_FLOW_VISUALS_AMOUNT_NODE_SIZE,
  CASE_FLOW_VISUALS_TX_NODE_SIZE,
  CASE_DORMANT_ADDRESS_MONITORING_HEADERS,
  CASE_DORMANT_ADDRESS_TABLE_HEADERS,
  CASE_EXCHANGE_ADDRESS_TABLE_HEADERS,
  CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_APPLIED,
  CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_DISMISSED,
  CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_REVIEWED,
  CASE_NAV_ITEMS,
  CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING,
  CASE_REPORT_BODY_VARIANT_STANDARD,
  CASE_SUSPICIOUS_ADDRESS_TABLE_HEADERS,
  CASE_TEMPLATE_TARGET_COLUMN_COUNT,
  CASE_TEMPLATE_TARGET_HEADERS,
  CASE_WORKFLOW_GROUPS,
  caseDepositPatternGradeRank,
  caseWwwIsStablecoin,
  evaluateCaseWwwUsdForPathEnding,
  createCaseBridgeServiceMatch,
  createDefaultCaseSupportState,
  createDefaultEntityCuratorOverlay,
  createEmptyCaseSummary,
  DEFAULT_AUTH_SESSION,
  findBestCaseBridgeServiceMatch,
  findCaseWwwEntryIndexForMain,
  formatCaseWwwPathEndingReference,
  formatCaseWwwUsd,
  findCaseBridgeServiceByCanonicalOrAlias,
  getCaseWwwGeneratedPathEndingRowsForEntry,
  getCaseWwwPathEndingAmountToken,
  loadEntityCuratorOverlayFromStorage,
  normalizeAttributionValue,
  normalizeCaseMatrix,
  normalizeCaseWwwEntries,
  normalizeCaseWwwHash,
  normalizeCell,
  parseCaseEntries,
  parseCaseWwwContinuationHashes,
  parseCaseWwwUsdReference,
  rowHasMeaningfulData,
  safeDateLabel
} from '../utils/CaseBuddyCore.js';
import { caseBuddyBaseMethods } from '../utils/caseBuddyMethods/caseBuddyBaseMethods.js';
import { caseBuddyDateNetworkRawMethods } from '../utils/caseBuddyMethods/caseBuddyDateNetworkRawMethods.js';
import { caseBuddyWorkflowMethods } from '../utils/caseBuddyMethods/caseBuddyWorkflowMethods.js';
import { caseBuddyReviewToolMethods } from '../utils/caseBuddyMethods/caseBuddyReviewToolMethods.js';
import { caseBuddyDeliverableMethods } from '../utils/caseBuddyMethods/caseBuddyDeliverableMethods.js';
import { caseBuddyAgentBridgeMethods } from '../utils/caseBuddyMethods/caseBuddyAgentBridgeMethods.js';
import { caseBuddyProjectSheetMethods } from '../utils/caseBuddyMethods/caseBuddyProjectSheetMethods.js';
import { caseBuddyAddendumMethods } from '../utils/caseBuddyMethods/caseBuddyAddendumMethods.js';
import { caseBuddyAutomationMethods } from '../utils/caseBuddyMethods/caseBuddyAutomationMethods.js';
import { caseBuddyHopSolverMethods } from '../utils/caseBuddyMethods/caseBuddyHopSolverMethods.js';


import '../assets/css/global-handsontable-styles.css';



// Demo/showcase overlay for Auto. Set this to false to return to the older
// "show every working panel" presentation while automation runs.
const CASE_AUTOMATION_SHOWCASE_OVERLAY_DEFAULT_ENABLED = true;

// When true, Auto can silently use the local WayFinder bridge for the raw-data
// loading step instead of forcing the user into the handoff modal. Set to false
// to restore the older manual-pause behavior.
const CASE_AUTOMATION_WAYFINDER_HANDOFF_DEFAULT_ENABLED = true;

// Raw-data automation can now try embedded Tool 88 first, then fall back to WayFinder.
// Keep this set to 'wayfinder' for fallback compatibility; the per-case flag below
// controls the default preference for the embedded hop solver.
const CASE_AUTOMATION_RAW_DATA_SOURCE_MODE = 'wayfinder';

// Private developer toggle for the Tool 88 raw-data showcase portal camera.
// Set to false to restore the older immediate snap-to-focused-node behavior.
const CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_ENABLED = true;
const CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_MIN_DURATION_MS = 720;
const CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_BASE_DURATION_MS = 1050;
const CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_MAX_DURATION_MS = 1850;
const CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_DISTANCE_FOR_MAX_MS = 1300;
const CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_SNAP_DISTANCE = 0.35;

// Workflow Auto can perform many review-only / idempotent actions while
// skip-review mode is enabled. These actions should not become hard blockers
// again simply because a later safe workflow action updated case metadata,
// output buffers, or review state. Keep this map narrow to workflow organizer
// steps; raw/main data truth still lives in the spreadsheets.
const CASE_WORKFLOW_AUTO_SOFT_COMPLETION_BY_TAG = Object.freeze({
  ADDRESS_ATTRIBUTION_FILL: {
    signalCodes: ['ADDRESS_ATTRIBUTION_FILL', 'ADDRESS_ATTRIBUTION_FILLED', 'ADDRESS_ATTRIBUTION_REVIEWED', 'ADDRESS_ATTRIBUTION_COMPLETED'],
    checkIds: ['address_attribution_fill', 'address_attribution', 'address_attributions']
  },
  ADDRESS_COLOR_LIKE_CELLS: {
    signalCodes: ['ADDRESS_FLOW_COLORS_APPLIED', 'ADDRESS_COLOR_LIKE_CELLS'],
    checkIds: ['address_colors', 'address_color_like_cells']
  },
  ENTITY_REVIEW: {
    signalCodes: ['NEW_ENTITIES_REVIEWED', 'ENTITY_REVIEW'],
    checkIds: ['entity_review']
  },
  EXTERNAL_ATTRIBUTION_REVIEW: {
    signalCodes: ['EXTERNAL_ATTRIBUTION_REVIEWED'],
    checkIds: ['external_review']
  },
  CASE_INCONSISTENCIES: {
    signalCodes: ['CASE_INCONSISTENCIES_REVIEWED', 'CASE_INCONSISTENCIES_RESOLVED'],
    checkIds: ['case_inconsistencies']
  },
  NORMALIZE_DEPOSIT_NOTES: {
    signalCodes: ['DEPOSIT_HOT_WALLET_NOTES_NORMALIZED', 'NORMALIZE_DEPOSIT_NOTES'],
    checkIds: ['deposit_hot_wallet_notes']
  },
  HIGH_PRIORITY_DEPOSIT_PATTERNS: {
    signalCodes: ['DEPOSIT_PATTERNS_REVIEWED', 'HIGH_PRIORITY_DEPOSIT_PATTERNS'],
    checkIds: ['deposit_patterns']
  },
  KNOWN_ENTITY_NOTE_CASING: {
    signalCodes: ['KNOWN_ENTITY_NOTE_CASING_REVIEWED', 'KNOWN_ENTITY_CASING_REVIEWED'],
    checkIds: ['known_entity_note_casing']
  },
  DUPLICATE_HASH_LABELS: {
    signalCodes: ['DUPLICATE_HASH_LABELS_REVIEWED', 'DUPLICATE_HASH_LABELS_APPLIED', 'DUPLICATE_HASHES_LABELED'],
    checkIds: ['duplicate_hashes']
  },
  NOTES_BACKUP_AND_HASH_REPLACEMENT: {
    signalCodes: ['NOTES_BACKUP_AND_HASH_REPLACEMENT_REVIEWED', 'NOTES_BACKUP_AND_HASH_REPLACEMENT_DONE', 'NOTES_HASHES_REPLACED'],
    checkIds: ['notes_backup']
  },
  ADDRESS_LEGEND: {
    signalCodes: ['ADDRESS_LEGEND_REVIEWED', 'ADDRESS_LEGEND_BUILT'],
    checkIds: ['address_legend']
  },
  TEMPLATE_DELIVERABLE: {
    signalCodes: ['TEMPLATE_DELIVERABLE_REVIEWED'],
    checkIds: ['template_deliverable']
  },
  SUSPICIOUS_ADDRESS_TABLE: {
    signalCodes: ['SUSPICIOUS_ADDRESS_TABLE_REVIEWED'],
    checkIds: ['suspicious_address_table']
  },
  EXCHANGE_ADDRESS_TABLE: {
    signalCodes: ['EXCHANGE_ADDRESS_TABLE_REVIEWED'],
    checkIds: ['exchange_address_table']
  },
  DORMANT_ADDRESS_DELIVERABLES: {
    signalCodes: ['DORMANT_ADDRESS_DELIVERABLES_REVIEWED'],
    checkIds: ['dormant_address_deliverables']
  },
  WHAT_WENT_WHERE: {
    signalCodes: ['WHAT_WENT_WHERE_REVIEWED'],
    checkIds: ['what_went_where']
  },
  CASE_CONCLUSION: {
    signalCodes: ['CASE_CONCLUSION_REVIEWED'],
    checkIds: ['case_conclusion']
  },
  REPORT_BODY: {
    signalCodes: ['REPORT_BODY_REVIEWED'],
    checkIds: ['report_body']
  },
  PATH_ENDING_ADDENDUM_REVIEW: {
    signalCodes: ['PATH_ENDING_ADDENDUM_REVIEWED'],
    checkIds: ['addendum_path_endings']
  },
  ADDENDUM_RECIPIENT_ENTITY: {
    signalCodes: ['ADDENDUM_RECIPIENT_ENTITY_REVIEWED'],
    checkIds: ['addendum_recipient_entities']
  },
  ADDENDUM_BUILD: {
    signalCodes: ['ADDENDUM_BUILD_REVIEWED'],
    checkIds: ['addendum_build']
  },
  CASE_FLOW_VISUALS: {
    signalCodes: ['CASE_FLOW_VISUALS_REVIEWED', 'CASE_FLOW_VISUALS_DONE'],
    checkIds: ['case_flow_visuals']
  },
  CASE_COMPLETION_SUMMARY: {
    signalCodes: ['CASE_COMPLETION_SUMMARY_REVIEWED', 'FULL_REPORT_PACKAGE_REVIEWED'],
    checkIds: ['full_report_package', 'deliverables_ready']
  }
});

// These workflow steps are generated/reviewed artifacts, not lightweight organizer
// acknowledgements. They can only appear complete when their own live state says the
// artifact/review is current. This prevents Auto/Next log entries or stale signals from
// painting them green before the staged output and review records are actually current.
const CASE_WORKFLOW_STATEFUL_REVIEW_TAGS = Object.freeze(new Set([
  'ADDRESS_LEGEND',
  'TEMPLATE_DELIVERABLE',
  'SUSPICIOUS_ADDRESS_TABLE',
  'EXCHANGE_ADDRESS_TABLE',
  'DORMANT_ADDRESS_DELIVERABLES',
  'WHAT_WENT_WHERE',
  'CASE_CONCLUSION',
  'REPORT_BODY',
  'PATH_ENDING_ADDENDUM_REVIEW',
  'ADDENDUM_RECIPIENT_ENTITY',
  'ADDENDUM_BUILD'
]));

const CASE_WORKFLOW_STATEFUL_REVIEW_OUTPUT_REVISION_TOLERANCE = 8;
const CASE_WORKFLOW_STATEFUL_REVIEW_RECENT_MS = 15 * 60 * 1000;


// Legacy guided launch shell retained for testing and reference. It is no
// longer a primary presentation mode and is exposed only from the advanced
// case-details foldout.
const CASE_BUDDY_SIMPLE_LAUNCH_DEFAULT_ENABLED = true;

// Presentation shell preference. The lookup-first screen is the default for a
// new browser, while the last explicitly selected view is remembered locally.
// This state changes only presentation; the platform case file and its sheets
// remain the source of truth.
const CASE_BUDDY_PRESENTATION_MODE_STORAGE_KEY = 'caseBuddy.presentationMode';
const CASE_BUDDY_PRESENTATION_MODE_LOOKUP = 'lookup';
const CASE_BUDDY_PRESENTATION_MODE_SIMPLE = 'simple';
const CASE_BUDDY_PRESENTATION_MODE_WORKSPACE = 'workspace';
const CASE_BUDDY_PRESENTATION_MODES = Object.freeze(new Set([
  CASE_BUDDY_PRESENTATION_MODE_LOOKUP,
  CASE_BUDDY_PRESENTATION_MODE_SIMPLE,
  CASE_BUDDY_PRESENTATION_MODE_WORKSPACE
]));
const CASE_BUDDY_PRESENTATION_MODE_DEFAULT = CASE_BUDDY_PRESENTATION_MODE_LOOKUP;
const CASE_BUDDY_QUICK_LOOKUP_ENABLED = true;
const CASE_BUDDY_QUICK_LOOKUP_CASE_WAIT_MS = 8000;
const CASE_BUDDY_QUICK_LOOKUP_ROW_WAIT_MS = 5000;
const CASE_BUDDY_QUICK_LOOKUP_REPORT_RECOVERY_MAX_ATTEMPTS = 3;
const CASE_BUDDY_FLOW_VISUAL_AUTO_REVIEW_MAX_ATTEMPTS = 5;
const CASE_BUDDY_FLOW_VISUAL_AUTO_REVIEW_SETTLE_MS = 240;
// Browser storage contains the workbook plus case-level organizer metadata. If
// quota pressure prevents a support-state save, retain current completion and
// revision facts while trimming rebuildable audit/debug/preview detail.
const CASE_BUDDY_SUPPORT_COMPACT_AUDIT_LIMIT = 10;
const CASE_BUDDY_SUPPORT_COMPACT_COLLECTION_LIMIT = 48;
const CASE_BUDDY_SUPPORT_COMPACT_SCALAR_COLLECTION_LIMIT = 240;
const CASE_BUDDY_SUPPORT_COMPACT_STRING_LIMIT = 4800;

function readCaseBuddyPresentationMode() {
  if (typeof localStorage === 'undefined') return CASE_BUDDY_PRESENTATION_MODE_DEFAULT;
  try {
    const value = String(localStorage.getItem(CASE_BUDDY_PRESENTATION_MODE_STORAGE_KEY) || '').trim().toLowerCase();
    // The former guided launch used the stored value "simple". Migrate that
    // preference to the current transaction-lookup Simple view so only the
    // two primary modes can be restored on page load.
    if (value === CASE_BUDDY_PRESENTATION_MODE_SIMPLE) return CASE_BUDDY_PRESENTATION_MODE_LOOKUP;
    return CASE_BUDDY_PRESENTATION_MODES.has(value) ? value : CASE_BUDDY_PRESENTATION_MODE_DEFAULT;
  } catch {
    return CASE_BUDDY_PRESENTATION_MODE_DEFAULT;
  }
}

// The showcase describes work that has already finished. Keep the underlying
// automation/debug log unchanged and convert only the four displayed milestones.
const CASE_AUTOMATION_MILESTONE_TENSE_RULES = Object.freeze([
  Object.freeze({ pattern: /^Auto-reviewing generated step:\s*(.+)$/i, replacement: 'Completed review for “$1”' }),
  Object.freeze({ pattern: /^Generated deliverables complete;\s*(.+?)\s+needs review$/i, replacement: 'Completed the generated deliverables; “$1” was ready for review' }),
  Object.freeze({ pattern: /^Column H USD reference guard is running before cleanup$/i, replacement: 'Checked Column H USD references before cleanup' }),
  Object.freeze({ pattern: /^(.+?)\s+is current and reviewed$/i, replacement: 'Completed review for “$1”' }),
  Object.freeze({ pattern: /^Looking up\b/i, replacement: 'Looked up' }),
  Object.freeze({ pattern: /^Loading\b/i, replacement: 'Loaded' }),
  Object.freeze({ pattern: /^Preparing\b/i, replacement: 'Prepared' }),
  Object.freeze({ pattern: /^Building\b/i, replacement: 'Built' }),
  Object.freeze({ pattern: /^Creating\b/i, replacement: 'Created' }),
  Object.freeze({ pattern: /^Reviewing\b/i, replacement: 'Reviewed' }),
  Object.freeze({ pattern: /^Generating\b/i, replacement: 'Generated' }),
  Object.freeze({ pattern: /^Refreshing\b/i, replacement: 'Refreshed' }),
  Object.freeze({ pattern: /^Running\b/i, replacement: 'Ran' }),
  Object.freeze({ pattern: /^Checking\b/i, replacement: 'Checked' }),
  Object.freeze({ pattern: /^Completing\b/i, replacement: 'Completed' }),
  Object.freeze({ pattern: /^Finishing\b/i, replacement: 'Finished' }),
  Object.freeze({ pattern: /^Applying\b/i, replacement: 'Applied' }),
  Object.freeze({ pattern: /^Assigning\b/i, replacement: 'Assigned' }),
  Object.freeze({ pattern: /^Filling\b/i, replacement: 'Filled' }),
  Object.freeze({ pattern: /^Normalizing\b/i, replacement: 'Normalized' }),
  Object.freeze({ pattern: /^Labeling\b/i, replacement: 'Labeled' }),
  Object.freeze({ pattern: /^Backing up\b/i, replacement: 'Backed up' }),
  Object.freeze({ pattern: /^Replacing\b/i, replacement: 'Replaced' }),
  Object.freeze({ pattern: /^Updating\b/i, replacement: 'Updated' }),
  Object.freeze({ pattern: /^Sorting\b/i, replacement: 'Sorted' }),
  Object.freeze({ pattern: /^Estimating\b/i, replacement: 'Estimated' }),
  Object.freeze({ pattern: /^Opening\b/i, replacement: 'Opened' }),
  Object.freeze({ pattern: /^Staging\b/i, replacement: 'Staged' }),
  Object.freeze({ pattern: /^Marking\b/i, replacement: 'Marked' }),
  Object.freeze({ pattern: /^Resolving\b/i, replacement: 'Resolved' }),
  Object.freeze({ pattern: /^Saving\b/i, replacement: 'Saved' }),
  Object.freeze({ pattern: /^Writing\b/i, replacement: 'Wrote' }),
  Object.freeze({ pattern: /^Importing\b/i, replacement: 'Imported' }),
  Object.freeze({ pattern: /^Fetching\b/i, replacement: 'Fetched' }),
  Object.freeze({ pattern: /^Tracing\b/i, replacement: 'Traced' }),
  Object.freeze({ pattern: /^Scanning\b/i, replacement: 'Scanned' }),
  Object.freeze({ pattern: /^Reconciling\b/i, replacement: 'Reconciled' }),
  Object.freeze({ pattern: /^Coloring\b/i, replacement: 'Colored' }),
  Object.freeze({ pattern: /^Identifying\b/i, replacement: 'Identified' }),
  Object.freeze({ pattern: /^Processing\b/i, replacement: 'Processed' })
]);

function formatCaseAutomationMilestonePastTense(value) {
  let message = normalizeCell(value).replace(/\s*(?:…|\.{3}|\.)\s*$/, '').trim();
  if (!message) return '';
  for (const rule of CASE_AUTOMATION_MILESTONE_TENSE_RULES) {
    if (!rule.pattern.test(message)) continue;
    message = message.replace(rule.pattern, rule.replacement).trim();
    break;
  }
  if (message && !/[.!?]$/.test(message)) message += '.';
  return message;
}

function createCaseQuickLookupState(overrides = {}) {
  return {
    runId: 0,
    status: 'idle',
    message: '',
    detail: '',
    progress: 0,
    activeHash: '',
    rowIndex: -1,
    flowIdentity: '',
    choiceRows: [],
    selectedChoiceKey: '',
    choiceSourceLabel: '',
    choiceFamily: '',
    choiceDirection: 'forward',
    addedByCurrentRun: false,
    manualSolveLaunched: false,
    reportRequested: false,
    lastResult: '',
    unresolvedCount: 0,
    choiceCount: 0,
    manualReviewCount: 0,
    acceptedReportBoundaryCount: 0,
    reportBoundaryEligibleCount: 0,
    error: '',
    ...overrides
  };
}

// Read-only visual overview over Main Data. This is intentionally a private
// developer toggle rather than a user-facing setting. Set it to false to remove
// the overlay and its restore button globally without changing case data.
const CASE_OVERVIEW_DETAILS_EXPANDED_STORAGE_KEY = 'caseBuddy.caseOverview.detailsExpanded';
const CASE_OUTPUT_SHEET_EXPANDED_STORAGE_KEY = 'caseBuddy.outputSheet.expanded';
const CASE_WORKFLOW_PANEL_EXPANDED_STORAGE_KEY = 'caseBuddy.workflowPanel.expanded';
const CASE_INSTRUCTIONS_PANEL_EXPANDED_STORAGE_KEY = 'caseBuddy.instructionsPanel.expanded';
const CASE_DELIVERABLES_PANEL_EXPANDED_STORAGE_KEY = 'caseBuddy.deliverablesPanel.expanded';
const CASE_SCROLL_NAVIGATION_VISIBLE_STORAGE_KEY = 'caseBuddy.scrollNavigation.visible';
const CASE_WORKSPACE_DARK_MODE_STORAGE_KEY = 'caseBuddy.workspace.darkMode';
const CASE_WORKSPACE_DARK_MODE_DOCUMENT_CLASS = 'case-buddy-workspace-dark';
const CASE_INPUT_FLOW_STORY_DEFAULT_ENABLED = true;
const CASE_INPUT_FLOW_STORY_HEAT_DEFAULT_ENABLED = false;
const CASE_INPUT_FLOW_STORY_DISMISSED_STORAGE_KEY = 'caseBuddy.inputFlowStory.dismissed';
const CASE_INPUT_FLOW_STORY_HEAT_STORAGE_KEY = 'caseBuddy.inputFlowStory.heatEnabled';
const CASE_INPUT_FLOW_STORY_MAX_ENTRIES_PER_FLOW = 80;
// The overview uses a cold-to-hot scale; neutral slate is reserved for rows
// whose value cannot be compared with the root transaction.
const CASE_INPUT_FLOW_STORY_HEAT_NO_DATA_COLOR = '#94a3b8';
const CASE_INPUT_FLOW_STORY_HEAT_STOPS = Object.freeze([
  { p: 0, c: '#1d4ed8' },
  { p: 15, c: '#2563eb' },
  { p: 35, c: '#60a5fa' },
  { p: 45, c: '#dbeafe' },
  { p: 55, c: '#fde047' },
  { p: 70, c: '#f59e0b' },
  { p: 90, c: '#f97316' },
  { p: 100, c: '#dc2626' },
  { p: 125, c: '#b91c1c' },
  { p: 180, c: '#7f1d1d' }
]);

// Private developer switch for the right-side intake preparation assistant.
// It remains an explicit user action and never runs on page load.
const CASE_INTAKE_PREPARATION_RAIL_ENABLED = true;
const CASE_INTAKE_PREPARATION_OVERALL_TIMEOUT_MS = 4 * 60 * 1000;
const CASE_INTAKE_PREPARATION_REMOTE_STAGE_TIMEOUT_MS = 90 * 1000;
const CASE_INTAKE_PREPARATION_LOCAL_STAGE_TIMEOUT_MS = 30 * 1000;
const CASE_INTAKE_CONTINUATION_CHECK_TIMEOUT_MS = 30 * 1000;
const CASE_INTAKE_CONTINUATION_CHECK_YIELD_EVERY_ROOTS = 2;

// Private developer switch for the next Workflow Assistant category. The run is
// explicit, bounded, and uses existing workflow actions one at a time.
const CASE_ATTRIBUTION_WORKFLOW_RAIL_ENABLED = true;
const CASE_ATTRIBUTION_WORKFLOW_OVERALL_TIMEOUT_MS = 6 * 60 * 1000;
const CASE_ATTRIBUTION_WORKFLOW_ACTION_TIMEOUT_MS = 90 * 1000;
const CASE_ATTRIBUTION_WORKFLOW_MAX_ACTIONS = 18;
const CASE_ATTRIBUTION_WORKFLOW_REPEAT_LIMIT = 3;
const CASE_ATTRIBUTION_WORKFLOW_STALE_REFRESH_LIMIT = 6;
const CASE_ATTRIBUTION_WORKFLOW_STEP_DEFINITIONS = Object.freeze([
  Object.freeze({ key: 'address-attribution', tag: 'ADDRESS_ATTRIBUTION_FILL', number: 8, shortLabel: 'Address attribution', title: 'Fill address attribution in main data' }),
  Object.freeze({ key: 'path-operations', tag: 'ATTRIBUTION_PATH_OPERATIONS', number: 9, shortLabel: 'Path operations', title: 'Review attributed rows and resolve path operations' }),
  Object.freeze({ key: 'address-colors', tag: 'ADDRESS_COLOR_LIKE_CELLS', number: 10, shortLabel: 'Address colors', title: 'Color matching From/To addresses' }),
  Object.freeze({ key: 'entity-review', tag: 'ENTITY_REVIEW', number: 11, shortLabel: 'New entities', title: 'Review new entities and update curators' }),
  Object.freeze({ key: 'external-review', tag: 'EXTERNAL_ATTRIBUTION_REVIEW', number: 12, shortLabel: 'External review', title: 'Review addresses in external attribution tools' }),
  Object.freeze({ key: 'usd-references', tag: 'WWW_USD_REFERENCES', number: 13, shortLabel: 'USD references', title: 'Review top-level USD reference values' })
]);

function createCaseAttributionWorkflowRunState(overrides = {}) {
  return {
    active: false,
    stopRequested: false,
    runId: 0,
    status: 'idle',
    stageKey: '',
    stageTag: '',
    stageIndex: 0,
    stageTitle: '',
    message: '',
    detail: '',
    completedTags: [],
    actionCount: 0,
    repeatedStepKey: '',
    repeatedStepCount: 0,
    reviewModeSkip: true,
    startedAtMs: 0,
    finishedAtUtc: '',
    lastResult: '',
    lastMessage: '',
    overallTimer: null,
    actionTimer: null,
    ...overrides
  };
}


// Private developer switch for the fourth Workflow Assistant category. This
// explicit run uses the existing workflow actions and stops before deliverables.
const CASE_CLEANUP_WORKFLOW_RAIL_ENABLED = true;
const CASE_CLEANUP_WORKFLOW_OVERALL_TIMEOUT_MS = 8 * 60 * 1000;
const CASE_CLEANUP_WORKFLOW_ACTION_TIMEOUT_MS = 90 * 1000;
const CASE_CLEANUP_WORKFLOW_MAX_ACTIONS = 24;
const CASE_CLEANUP_WORKFLOW_REPEAT_LIMIT = 3;
const CASE_CLEANUP_WORKFLOW_STALE_REFRESH_LIMIT = 8;
const CASE_CLEANUP_WORKFLOW_STEP_DEFINITIONS = Object.freeze([
  Object.freeze({ key: 'case-inconsistencies', tag: 'CASE_INCONSISTENCIES', number: 14, shortLabel: 'Inconsistencies', title: 'Review and resolve case inconsistencies' }),
  Object.freeze({ key: 'normalize-notes', tag: 'NORMALIZE_DEPOSIT_NOTES', number: 15, shortLabel: 'Normalize notes', title: 'Normalize deposit, hot-wallet, and unknown-service notes' }),
  Object.freeze({ key: 'deposit-patterns', tag: 'HIGH_PRIORITY_DEPOSIT_PATTERNS', number: 16, shortLabel: 'Deposit patterns', title: 'Review high-priority deposit pattern candidates' }),
  Object.freeze({ key: 'entity-note-casing', tag: 'KNOWN_ENTITY_NOTE_CASING', number: 17, shortLabel: 'Entity casing', title: 'Review known-entity note casing' }),
  Object.freeze({ key: 'duplicate-hashes', tag: 'DUPLICATE_HASH_LABELS', number: 18, shortLabel: 'Duplicate hashes', title: 'Label duplicate transaction hashes' }),
  Object.freeze({ key: 'notes-hash-rewrite', tag: 'NOTES_BACKUP_AND_HASH_REPLACEMENT', number: 19, shortLabel: 'Back up notes', title: 'Back up notes and replace hashes with path labels' }),
  Object.freeze({ key: 'address-legend', tag: 'ADDRESS_LEGEND', number: 20, shortLabel: 'Address legend', title: 'Build and review the address legend' })
]);


// Compact presentation-only milestones for the right-side Workflow Assistant.
// These rows summarize existing case/workflow state and never become a second
// source of investigation truth.
const CASE_WORKFLOW_ASSISTANT_STAGE_PROGRESS_DEFINITIONS = Object.freeze([
  Object.freeze({ key: 'intake', label: 'Prepare intake' }),
  Object.freeze({ key: 'raw-graph', label: 'Raw data and graph' }),
  Object.freeze({ key: 'attribution', label: 'Attribution, pruning, grafting, and Bridges' }),
  Object.freeze({ key: 'cleanup', label: 'Consistency, notes, legend, and cleanup' }),
  Object.freeze({ key: 'shipping', label: 'Deliverable buildout and shipping' })
]);

function createCaseCleanupWorkflowRunState(overrides = {}) {
  return {
    active: false,
    stopRequested: false,
    runId: 0,
    status: 'idle',
    stageKey: '',
    stageTag: '',
    stageIndex: 0,
    stageTitle: '',
    message: '',
    detail: '',
    completedTags: [],
    prerequisiteCompletedTags: [],
    actionCount: 0,
    repeatedStepKey: '',
    repeatedStepCount: 0,
    reviewModeSkip: true,
    startedAtMs: 0,
    finishedAtUtc: '',
    lastResult: '',
    lastMessage: '',
    overallTimer: null,
    actionTimer: null,
    ...overrides
  };
}


function createCaseIntakePreparationState(overrides = {}) {
  return {
    active: false,
    stopRequested: false,
    runId: 0,
    status: 'idle',
    stageKey: '',
    stageIndex: 0,
    stageTitle: '',
    message: '',
    detail: '',
    completedKeys: [],
    startedAtMs: 0,
    finishedAtUtc: '',
    lastResult: '',
    lastMessage: '',
    overallTimer: null,
    stageTimer: null,
    ...overrides
  };
}

function createCaseIntakeContinuationCheckState(overrides = {}) {
  return {
    runId: 0,
    status: 'idle',
    checkedAtUtc: '',
    checkedRevision: -1,
    checkedFileId: '',
    rootCount: 0,
    coveredRootCount: 0,
    missingRawRootCount: 0,
    checkedRawRootCount: 0,
    invalidRawRootCount: 0,
    rawBlockingIssueCount: 0,
    rawCoverageComplete: false,
    rawFlowComplete: false,
    rawStageComplete: false,
    preparedRootCount: 0,
    unpreparedRootCount: 0,
    mainEntryCount: 0,
    mainBlockingIssueCount: 0,
    rawEntryCount: 0,
    missingMainEntryCount: 0,
    rawIssueCount: 0,
    mainIssueCount: 0,
    rawIssueExamples: [],
    mainIssueExamples: [],
    mainFlowComplete: false,
    saplingStageComplete: false,
    startedAtMs: 0,
    message: '',
    detail: '',
    issueExamples: [],
    error: '',
    ...overrides
  };
}

function readCaseBuddyBooleanPreference(key, fallback = false) {
  if (typeof localStorage === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return raw === 'true';
  } catch {
    return fallback;
  }
}

// EMPTY_NUDGE_V16: set to false to globally disable the empty-case starter helper.
const CASE_EMPTY_STARTER_NUDGE_DEFAULT_ENABLED = true;
const CASE_EMPTY_STARTER_NUDGE_DISABLED_STORAGE_KEY = 'caseBuddy.emptyCaseStarterNudge.disabled';
const CASE_EMPTY_STARTER_NUDGE_HIGHLIGHT_MS = 5200;

function readCaseEmptyStarterNudgeDisabledPreference() {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(CASE_EMPTY_STARTER_NUDGE_DISABLED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

// Keep Case Buddy's manual-start hydration route aligned with embedded Tool 88.
// It is used only when the user opens the raw-data step visually/manually;
// normal Case Buddy automation and embedded Tool 88 automation keep their own state.
const CASE_BUDDY_TOOL88_ENDPOINT_URL = '/api/casebuddy/tool88_hop_logic_solver_casebuddy.php';
const CASE_MANUAL_START_TOOL88_SETTINGS_STORAGE_KEY = 'casebuddy_tool88_hop_logic_solver_settings_v1';
const CASE_MANUAL_START_TOOL88_SETTINGS_SCHEMA_VERSION = 3;
// First-run guidance shown inside Manual Solve. The per-case dismissal is
// stored in case support metadata and never changes spreadsheet rows.
const CASE_MANUAL_SOLVE_INTRO_ENABLED = true;
// Manual Solve startup stays inside Case Buddy and uses the dedicated
// PathTracer Case Buddy Tool 88 endpoint and separate job-table family.
const CASE_MANUAL_START_OPENING_FEEDBACK_ENABLED = true;
const CASE_MANUAL_START_OPENING_TIMEOUT_MS = 4 * 60 * 1000;
const CASE_MANUAL_START_FAILED_REUSE_MS = 45 * 1000;
// Manual Solve camera motion is intentionally kept outside Vue's reactive
// graph tree. The SVG viewBox is updated once per animation frame, then the
// final camera is committed back to reactive state after the gesture settles.
// This prevents a 50+ node graph from being fully patched on every pointermove.
const CASE_MANUAL_SOLVE_CAMERA_COMMIT_DELAY_MS = 110;
const CASE_MANUAL_SOLVE_PROGRESS_RECONCILE_MS = 120;
const CASE_MANUAL_SOLVE_CAMERA_RUNTIME = new WeakMap();
const CASE_MANUAL_SOLVE_PROGRESS_RUNTIME = new WeakMap();

function createCaseManualSolveCameraRuntime() {
  return {
    pendingViewport: null,
    appliedViewport: null,
    frame: 0,
    commitTimer: 0,
    moving: false,
    idleResolvers: []
  };
}

function createCaseManualSolveProgressRuntime() {
  return {
    pendingPayload: null,
    frame: 0,
    timer: 0,
    lastFlushedAtMs: 0
  };
}

function createCaseManualStartVisualizerOpeningState(overrides = {}) {
  return {
    active: false,
    runId: 0,
    phase: 'idle',
    progressPercent: 0,
    source: '',
    message: '',
    detail: '',
    rootCount: 0,
    pendingRootCount: 0,
    currentRootIndex: 0,
    hydratedCount: 0,
    reusedCount: 0,
    stopRequested: false,
    exitMode: '',
    timedOut: false,
    startedAtMs: 0,
    finishedAtUtc: '',
    overallTimer: null,
    ...overrides
  };
}

// Optional guided automation that chains the existing contained Auto routine
// across every starting/root graph. Ordinary per-graph Auto remains unchanged.
const CASE_MANUAL_START_ALL_FLOWS_AUTO_ENABLED = true;
const CASE_MANUAL_START_ALL_FLOWS_AUTO_MAX_SECONDS = 30 * 60;
const CASE_MANUAL_START_ALL_FLOWS_AUTO_FLOW_LIMIT = 60;
const CASE_MANUAL_START_ALL_FLOWS_AUTO_POLL_MS = 320;
const CASE_MANUAL_START_ALL_FLOWS_AUTO_SETTLE_MS = 650;
const CASE_MANUAL_START_ALL_FLOWS_AUTO_FLOW_TIMEOUT_MS = 645 * 1000;
const CASE_MANUAL_START_ALL_FLOWS_AUTO_IDLE_TIMEOUT_MS = 25 * 1000;
const CASE_MANUAL_START_ALL_FLOWS_AUTO_STALL_MS = 2 * 60 * 1000;
// An accepted report boundary is intentionally not presented as a fully traced
// ending. It means the investigator chose to ship the current evidence while
// keeping the unfinished path visible in Main Data, visuals, and the report.
const CASE_MANUAL_START_ACCEPTED_REPORT_BOUNDARY_ACTION = 'accepted-report-boundary';
const CASE_MANUAL_START_ACCEPTED_REPORT_BOUNDARY_SYSTEM_NOTE = 'Report boundary accepted by the investigator. Tracing did not continue beyond this point; further manual analysis is recommended.';
const CASE_MANUAL_START_TOOL88_DEFAULT_SETTINGS = Object.freeze({
  traceDirection: 'forward',
  hopLogicType: 'NEXT_CHRONOLOGICAL',
  selectedExampleKey: 'basic',
  tool88NoteColumn: 'W',
  enableAdditionalServiceChecks: true,
  enableDepositEntityHopExtension: false,
  enableNativeDirectSpendMatchGuard: false,
  enableSessionDataCache: true,
  flowCompletionMaxDepth: 50,
  flowCompletionValueTooLowEnabled: true,
  lowValueThresholdBtc: 0.001,
  lowValueThresholdNative: 0.1,
  lowValueThresholdStable: 100,
  lowValueThresholdOther: 10,
  flowCompletionTraceThroughEntitiesText: '',
  directAutomationSpeedMultiplier: 1,
  showAutomationCallouts: true
});
const CASE_MANUAL_START_TRACE_ANYWAY_TX_COUNT_GUARD_ENABLED = true;
const CASE_MANUAL_START_TRACE_ANYWAY_TX_COUNT_LIMIT = 400000;
const CASE_MANUAL_START_BTC_VISUAL_INPUT_ADDRESS_LIMIT = 20;
const CASE_MANUAL_START_BTC_VISUAL_OUTPUT_ADDRESS_LIMIT = 20;
const CASE_MANUAL_START_VIEWPORT_AUTO_MAX_SECONDS = 600;
const CASE_MANUAL_START_VIEWPORT_AUTO_MAX_NEXT_ACTIONS = 80;
const CASE_MANUAL_START_VIEWPORT_AUTO_MAX_HOP_APPLIES = 40;
const CASE_MANUAL_START_VIEWPORT_AUTO_SAME_SIGNATURE_LIMIT = 6;
const CASE_MANUAL_START_VIEWPORT_AUTO_BRIDGE_NO_OUTPUT_LIMIT = 2;
const CASE_MANUAL_START_VIEWPORT_AUTO_SWAP_NO_OUTPUT_LIMIT = 2;
const CASE_MANUAL_START_VIEWPORT_AUTO_TRACE_NO_PROGRESS_LIMIT = 2;
const CASE_MANUAL_START_VIEWPORT_AUTO_IDLE_DELAY_MS = 900;
const CASE_MANUAL_START_VIEWPORT_AUTO_ACTION_DELAY_MS = 1250;
const CASE_MANUAL_START_VIEWPORT_COMPLETION_FIRST_CHECK_DELAY_MS = 900;
const CASE_MANUAL_START_VIEWPORT_COMPLETION_VERIFY_DELAY_MS = 1100;
const CASE_MANUAL_START_VIEWPORT_COMPLETION_VISIBLE_MS = 18000;
const CASE_MANUAL_START_VIEWPORT_COMPLETION_GLOW_MS = 6500;
const CASE_MANUAL_START_AUTOMATION_CALLOUT_VISIBLE_MS = 5800;
const CASE_MANUAL_START_AUTOMATION_CALLOUT_MAX_VISIBLE = 6;
const CASE_MANUAL_START_AUTOMATION_CALLOUT_DEFAULT_COLOR = '#111827';
const CASE_MANUAL_START_CANONICAL_COLUMN_COUNT = 24;
const CASE_MANUAL_START_INTERNAL_COLUMN_COUNT = 24;
const CASE_MANUAL_START_COLUMN_COUNT = CASE_MANUAL_START_INTERNAL_COLUMN_COUNT;
const CASE_MANUAL_START_CANONICAL_HEADERS = Object.freeze([
  'Path Label',
  'Transaction Hash',
  'From Address',
  'To Address',
  'UTC Date',
  'Amount',
  'TokenSymbol',
  '',
  '',
  'System Notes',
  'To Address Attribution',
  'Analyst Notes',
  'Funds Followed',
  'Prior Balance',
  'From Hash',
  '',
  'Continues As',
  'Left Bounds Display',
  'Left Bounds Numeric',
  'Chain Id',
  'Chain Name',
  'Hop Logic',
  'Case Buddy / Tool 88 Notes',
  'From Address Attribution'
]);
const CASE_MANUAL_START_COL = Object.freeze({
  PATH_LABEL: 0,
  TX_HASH: 1,
  FROM_ADDRESS: 2,
  TO_ADDRESS: 3,
  DATE: 4,
  AMOUNT: 5,
  TOKEN: 6,
  SYSTEM_NOTES: 9,
  ATTRIBUTION: 10,
  ANALYST_NOTES: 11,
  FUNDS_FOLLOWED: 12,
  PRIOR_BALANCE: 13,
  FROM_HASH: 14,
  FROM_LABEL: 15,
  CONTINUES_AS: 16,
  LEFT_BOUNDS_DISPLAY: 17,
  LEFT_BOUNDS_NUMERIC: 18,
  CHAIN_ID: 19,
  CHAIN_NAME: 20,
  HOP_LOGIC: 21,
  CASE_NOTES: 22,
  FROM_ATTRIBUTION: 23
});

const MANUAL_SOLVE_VISUAL_LEGEND_ITEMS = Object.freeze([
  {
    key: 'transaction',
    label: 'Transaction',
    description: 'Yellow rectangles represent transactions.',
    shape: 'transaction',
    enabledWhen: 'always',
    group: 'core'
  },
  {
    key: 'address',
    label: 'Address',
    description: 'Blue circles represent addresses involved in the flow.',
    shape: 'address',
    enabledWhen: 'always',
    group: 'core'
  },
  {
    key: 'btc-amount',
    label: 'BTC amount',
    description: 'Orange rectangles represent BTC input/output amount nodes when BTC-style visuals are shown.',
    shape: 'amount',
    enabledWhen: 'always',
    group: 'btc'
  },
  {
    key: 'flow-direction',
    label: 'Flow direction',
    description: 'Arrows show the direction funds move through the trace.',
    shape: 'arrow',
    enabledWhen: 'always',
    group: 'core'
  },
  {
    key: 'selected',
    label: 'Selected',
    description: 'A bright blue outline marks selected nodes.',
    shape: 'selected',
    enabledWhen: 'always',
    group: 'core'
  },
  {
    key: 'swap',
    label: 'Swap',
    description: 'Purple swap markers or swap-styled nodes indicate a suspected or handled swap, when available.',
    shape: 'swap',
    enabledWhen: 'swap'
  },
  {
    key: 'hot-wallet',
    label: 'Hot wallet',
    description: 'A dotted red border can mark an address treated as a hot wallet or deposit-style endpoint, when available.',
    shape: 'hot-wallet',
    enabledWhen: 'hot-wallet'
  },
  {
    key: 'bridge',
    label: 'Bridge',
    description: 'Bridge-styled nodes or edges indicate bridge-related flow, when available.',
    shape: 'bridge',
    enabledWhen: 'bridge'
  },
  {
    key: 'dashed-edge',
    label: 'Dashed edge',
    description: 'Dashed arrows mark inferred, muted, hidden, or secondary relationships when this visual uses dashed edges.',
    shape: 'dashed-edge',
    enabledWhen: 'dashed-edge'
  }
]);

function parsePathOperationBridgeNumber(value) {
  const text = normalizeCell(value);
  if (!text) return null;
  const compact = text.replace(/[$,]/g, '');
  const match = compact.match(/(-?\d+(?:\.\d+)?)\s*([kmb])?/i);
  if (!match) return null;
  const baseNumber = Number(match[1]);
  if (!Number.isFinite(baseNumber)) return null;
  const suffix = String(match[2] || '').toLowerCase();
  const multiplier = suffix === 'k' ? 1000 : suffix === 'm' ? 1000000 : suffix === 'b' ? 1000000000 : 1;
  const number = baseNumber * multiplier;
  return Number.isFinite(number) ? number : null;
}

function extractPathOperationBridgeToken(value) {
  const text = normalizeCell(value);
  if (!text) return '';
  const withoutNumber = text.replace(/[$,]/g, '').replace(/-?\d+(?:\.\d+)?/, ' ').trim();
  const match = withoutNumber.match(/\b[A-Za-z][A-Za-z0-9._-]{1,15}\b/);
  return match ? match[0].toUpperCase() : '';
}

function formatPathOperationBridgeAmount(value, token = '') {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  const abs = Math.abs(number);
  const decimals = abs >= 100 ? 4 : abs >= 1 ? 6 : 10;
  const fixed = number.toFixed(decimals);
  const trimmed = fixed.replace(/(\.\d*?[1-9])0+$/u, '$1').replace(/\.0+$/u, '');
  const normalized = trimmed === '-0' ? '0' : trimmed;
  const cleanToken = normalizeCell(token);
  return cleanToken ? `${normalized} ${cleanToken}` : normalized;
}

function formatPathOperationBridgeNumberOnly(value) {
  return formatPathOperationBridgeAmount(value, '');
}

function formatPathOperationBridgePercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `${(number * 100).toFixed(2).replace(/\.00$/u, '')}%`;
}

export default {
  name: 'CaseBuddy',
  components: {
    HermesSpreadsheetComponentAdvanced,
    HermesSpreadsheetWorkbookTabs,
    BorderModal,
    Tool88Page_CaseBuddy,
    AddressMonitoringPage
  },
  data() {
    return {

      casePageHydrating: true,
      casePageHydrationMessage: 'Loading case file and spreadsheet tabs…',
      casePageHydrationStartedAt: Date.now(),
      casePageHydrationTimer: null,
      caseGlobalOperation: {
        active: false,
        token: '',
        kind: '',
        title: '',
        message: '',
        detail: '',
        progress: 0,
        total: 0,
        startedAtUtc: '',
        finishedAtUtc: ''
      },
      caseGlobalOperationTimer: null,
      caseModalOperation: {
        active: false,
        token: '',
        kind: '',
        title: '',
        message: '',
        detail: '',
        target: '',
        startedAtUtc: '',
        finishedAtUtc: ''
      },
      caseModalOperationTimer: null,
      caseAutoCloseCompletedReviewModals: true,
      caseAutomationShowcaseOverlayEnabled: CASE_AUTOMATION_SHOWCASE_OVERLAY_DEFAULT_ENABLED,
      caseIntakePreparation: createCaseIntakePreparationState(),
      caseIntakePreparationAbortController: null,
      caseIntakePreparationBodyOverflow: null,
      caseWorkflowRootOrderRepairInFlight: false,
      caseWorkflowRootOrderRepairLastResult: null,
      caseAttributionWorkflowRun: createCaseAttributionWorkflowRunState(),
      caseAttributionWorkflowAbortController: null,
      caseAttributionWorkflowBodyOverflow: null,
      caseCleanupWorkflowEntered: false,
      caseCleanupWorkflowRun: createCaseCleanupWorkflowRunState(),
      caseCleanupWorkflowAbortController: null,
      caseCleanupWorkflowBodyOverflow: null,
      caseIntakeContinuationCheck: createCaseIntakeContinuationCheckState(),
      caseIntakeContinuationIssueStage: '',
      caseIntakeContinuationRefreshTimer: null,
      caseIntakeContinuationManualSolveLaunched: false,
      caseIntakeContinuationTool68Launched: false,
      caseIntakeContinuationSaplingPreparing: false,
      caseAutomationShowcaseOverlayMinimized: false,
      caseAutomationWayFinderHandoffEnabled: CASE_AUTOMATION_WAYFINDER_HANDOFF_DEFAULT_ENABLED,
      caseAutomationRawDataSourceMode: CASE_AUTOMATION_RAW_DATA_SOURCE_MODE,
      caseAutomationPreferHopSolverRawData: true,
      caseSimpleLaunchEnabled: CASE_BUDDY_SIMPLE_LAUNCH_DEFAULT_ENABLED,
      caseBuddyPresentationMode: readCaseBuddyPresentationMode(),
      caseQuickLookupEnabled: CASE_BUDDY_QUICK_LOOKUP_ENABLED,
      caseQuickLookupHashDraft: '',
      caseQuickLookupChoiceFilter: '',
      caseQuickLookup: createCaseQuickLookupState(),
      caseQuickLookupAbortController: null,
      caseQuickLookupEnsureCaseTimer: null,
      caseQuickLookupEnsureCasePromise: null,
      caseQuickLookupAutoCreatingCase: false,
      caseQuickLookupReportWatchTimer: null,
      caseQuickLookupReportRecoveryInFlight: false,
      caseQuickLookupReportRecoveryAttempts: 0,
      caseQuickLookupSettingsOpen: false,
      caseQuickLookupCaseSwitcherOpen: false,
      caseQuickLookupCaseSwitchBusy: false,
      caseQuickLookupBodyOverflow: null,
      caseSimpleLaunchDismissed: false,
      caseSimpleLaunchMinimized: false,
      caseSimpleLaunchAutoSkipResolvable: true,
      caseSimpleLaunchAutoRequested: false,
      caseSimpleLaunchAutoStarting: false,
      caseSimpleLaunchAutoStartAttemptId: 0,
      caseSimpleLaunchAutoStartAudit: [],
      caseSimpleLaunchLastCompletedAtUtc: '',
      caseSimpleLaunchStatus: 'Load starting transactions, then run Auto to build the case.',
      caseSimpleLaunchReturnAfterStartingModal: false,
      caseWorkflowScheduledScrollTimers: [],
      emptyCaseStarterNudgeEnabled: CASE_EMPTY_STARTER_NUDGE_DEFAULT_ENABLED,
      emptyCaseStarterNudgeDontShowAgain: readCaseEmptyStarterNudgeDisabledPreference(),
      emptyCaseStarterNudgeSessionDismissedByFileId: {},
      emptyCaseStarterNudgeLaunchPending: false,
      emptyCaseStarterNudgeHighlightedWorkflowStepTag: '',
      emptyCaseStarterNudgeHighlightTimer: null,
      caseBuddyUserPreferences: {
        emptyCaseStarterNudgeDisabled: readCaseEmptyStarterNudgeDisabledPreference()
      },
      caseSimpleFileChooserVisible: false,
      caseSimpleDetailsVisible: false,
      caseSimpleDetailsDraft: {
        caseName: '',
        caseId: '',
        caseType: ''
      },
      showWorkflowStepHelpModal: false,
      activeWorkflowStepHelpTag: '',
      showCaseDetailsReviewAcknowledgementModal: false,
      caseDetailsReviewAcknowledgementReadFiles: false,
      caseDetailsReviewAcknowledgementStatus: '',
      caseDetailsReviewPendingStep: null,
      caseDetailsReviewPendingAction: null,
      caseDetailsReviewAcknowledgementBypass: false,
      showCaseAutomationPauseModal: false,
      caseAutomationTimer: null,
      caseWorkflowStatefulGeneratedReviewAutoGuardTimer: null,
      caseWorkflowStatefulGeneratedReviewAutoResumeTimer: null,
      caseWorkflowStatefulGeneratedReviewAutoGuardRunning: false,
      caseWorkflowStatefulGeneratedReviewAutoGuardLastRunAtMs: 0,
      caseWorkflowStatefulGeneratedReviewAutoGuardLastTag: '',
      caseWorkflowStatefulGeneratedReviewAutoGuardLastSource: '',
      caseWorkflowStatefulGeneratedReviewUserStopRequestedAtMs: 0,
      caseWorkflowStatefulGeneratedReviewStopToken: 0,
      caseWorkflowStatefulGeneratedReviewChainRunning: false,
      caseWorkflowStatefulGeneratedReviewRuntimeAudit: [],
      caseWorkflowStatefulGeneratedReviewRuntimeRecordByTag: {},
      caseWorkflowLastBuildableAddendumState: null,
      caseWorkflowLastBuildableAddendumStateAtMs: 0,
      caseWorkflowLastBuildableAddendumStateCaseKey: '',
      caseWorkflowLastBuildableAddendumStateRevision: 0,
      caseWorkflowReviewAutoUiSuppressed: false,
      caseWorkflowReviewAutoUiSuppressDepth: 0,
      caseWorkflowReviewAutoUiSnapshot: null,
      caseWorkflowWwwUsdReferenceAutoGuardTimer: null,
      caseWorkflowWwwUsdReferenceAutoResumeTimer: null,
      caseWorkflowWwwUsdReferenceAutoGuardRunning: false,
      caseWorkflowWwwUsdReferenceAutoGuardLastRunAtMs: 0,
      caseWorkflowWwwUsdReferenceStopToken: 0,
      caseWorkflowColumnHWriteAudit: [],
      caseWorkflowSaplingPreparationAudit: [],
      caseWorkflowTool68PreparationOpenedByAuto: false,
      caseAutomation: {
        active: false,
        paused: false,
        busy: false,
        skipResolvableReview: true,
        speedMultiplier: 1,
        status: 'Idle',
        detail: '',
        log: [],
        lastPlan: null,
        manualPause: null,
        lastRunId: '',
        noActionStreak: 0,
        noActionStartedAtMs: 0,
        maxNoActionStreak: 80,
        maxNoActionElapsedMs: 90000,
        lastNoActionKey: '',
        waitingStreak: 0,
        lastWaitingReason: '',
        lastWaitingLogAtMs: 0,
        maxOperationWaitMs: 15000,
        lastRepeatedStepKey: '',
        repeatedStepCount: 0,
        maxRepeatedStepAttempts: 6
      },
      caseAutomationTool88Activity: {
        active: false,
        startedAtUtc: '',
        updatedAtUtc: '',
        completedAtUtc: '',
        status: '',
        title: '',
        message: '',
        detail: '',
        eventName: '',
        integrationAction: '',
        stepCount: 0,
        researchedCount: 0,
        entryCount: 0,
        endingCount: 0,
        completeCount: 0,
        unresolvedCount: 0,
        flowComplete: false,
        flowCompleteConfirmations: 0,
        rawRows: 0,
        rowsAppended: 0,
        targetLabel: '',
        targetHash: '',
        targetAddress: '',
        guardrailMessage: '',
        lastLogKey: '',
        lastAutomationLogKey: '',
        log: []
      },
      caseAutomationTool88Progress: {
        active: false,
        title: '',
        detail: '',
        status: '',
        action: '',
        stepCount: 0,
        researchedCount: 0,
        outputRows: 0,
        totalEntries: 0,
        endingCount: 0,
        completeEndings: 0,
        openEndings: 0,
        flowCompleteConfirmations: 0,
        flowCompleteRequiredConfirmations: 0,
        selectedLabel: '',
        selectedHash: '',
        selectedTarget: '',
        lastEventKey: '',
        lastLoggedAtMs: 0,
        updatedAtUtc: '',
        events: []
      },
      caseWorkflowRawDataTool88HostMounted: false,
      caseWorkflowRawDataTool88Running: false,
      caseWorkflowRawDataTool88LastSnapshot: null,
      caseWorkflowRawDataTool88LastRows: [],
      caseWorkflowRawDataTool88LastError: '',
      caseWorkflowRawDataTool88LastGraphAudit: null,
      caseWorkflowRawDataTool88VisualRows: [],
      caseWorkflowRawDataTool88LastRunRecord: null,
      caseWorkflowRawDataTool88RunEvents: [],
      caseWorkflowRawDataTool88LastChosenSource: '',
      caseWorkflowRawDataTool88LastCandidateSources: [],
      caseWorkflowRawDataTool88SelectedInputRoots: [],
      caseWorkflowRawDataTool88CoveragePasses: [],
      caseWorkflowRawDataTool88LastDebugCopiedAtUtc: '',
      caseWorkflowRawDataTool88AutoContinueGuardUntilMs: 0,
      caseWorkflowRawDataTool88AutoContinueTimer: null,
      caseWorkflowRawDataTool88AutoContinueInFlight: false,
      caseWorkflowRawDataTool88LastAutoContinueAtUtc: '',
      caseWorkflowRawDataTool88AutoContinueReason: '',
      caseWorkflowRawDataTool88UserStopRequestedAtMs: 0,
      caseWorkflowRawDataTool88StopToken: 0,
      caseWorkflowRawDataTool88RunKey: '',
      caseWorkflowRawDataTool88VisualCaseKey: '',
      caseWorkflowRawDataTool88LastResetAtUtc: '',
      caseWorkflowRawDataTool88PortalCamera: {
        initialized: false,
        currentX: 210,
        currentY: 150,
        startX: 210,
        startY: 150,
        targetX: 210,
        targetY: 150,
        focusKey: '',
        caseKey: '',
        runKey: '',
        raf: 0,
        transitionStartedAtMs: 0,
        transitionDurationMs: 0,
        transitionProgress: 1,
        lastUpdatedAtMs: 0
      },

         authSession: { ...DEFAULT_AUTH_SESSION },
    authSessionRaw: null,
    authSessionLoaded: false,
    authSessionError: '',


    agentResetCount: 0,
agentLastResetAt: '',
      showResetConfirm: false,
      agentWriteRangeStartRef: 'A1',
agentWriteRangeData: `[
  ["Txn Hash", "From", "To"],
  ["0xabc...", "0x111...", "0x222..."]
]`,
      agentCellRef: 'B2',
agentCellValue: '',
agentRangeRef: 'A1:C5',
agentStatus: '',
agentReadback: '',
      searchQuery: '',
      matchCount: 0,
      autoSaveEnabled: localStorage.getItem('tool60_autosave_enabled') !== 'false',
      spreadsheetToolsExpanded: localStorage.getItem('casebuddy_input_spreadsheet_tools_expanded') === 'true',
      caseOverviewDetailsExpanded: readCaseBuddyBooleanPreference(CASE_OVERVIEW_DETAILS_EXPANDED_STORAGE_KEY, false),
      outputSheetExpanded: readCaseBuddyBooleanPreference(CASE_OUTPUT_SHEET_EXPANDED_STORAGE_KEY, false),
      workflowPanelExpanded: readCaseBuddyBooleanPreference(CASE_WORKFLOW_PANEL_EXPANDED_STORAGE_KEY, false),
      instructionsPanelExpanded: readCaseBuddyBooleanPreference(CASE_INSTRUCTIONS_PANEL_EXPANDED_STORAGE_KEY, false),
      deliverablesPanelExpanded: readCaseBuddyBooleanPreference(CASE_DELIVERABLES_PANEL_EXPANDED_STORAGE_KEY, false),
      caseScrollNavigationVisible: readCaseBuddyBooleanPreference(CASE_SCROLL_NAVIGATION_VISIBLE_STORAGE_KEY, false),
      caseWorkspaceDarkModeEnabled: readCaseBuddyBooleanPreference(CASE_WORKSPACE_DARK_MODE_STORAGE_KEY, false),
      caseInputFlowStoryEnabled: CASE_INPUT_FLOW_STORY_DEFAULT_ENABLED,
      caseInputFlowStoryDismissed: readCaseBuddyBooleanPreference(CASE_INPUT_FLOW_STORY_DISMISSED_STORAGE_KEY, false),
      caseInputFlowStoryHeatEnabled: readCaseBuddyBooleanPreference(CASE_INPUT_FLOW_STORY_HEAT_STORAGE_KEY, CASE_INPUT_FLOW_STORY_HEAT_DEFAULT_ENABLED),
      caseInputFlowStoryStatus: 'idle',
      caseInputFlowStoryError: '',
      caseInputFlowStoryDescriptors: [],
      caseInputFlowStoryVisualCache: {},
      caseInputFlowStoryModel: null,
      caseInputFlowStoryRefreshTimer: null,
      caseInputFlowStoryRefreshToken: 0,
      caseInputFlowStoryBuildTimers: {},
      caseInputFlowStoryBuildTokens: {},
      caseInputFlowStoryObserver: null,
      caseInputFlowStoryWarmupTimer: null,
      caseInputFlowStoryLastBuiltRevision: -1,
      caseInputFlowStoryStartingModalOpen: false,
      caseManualStartAutosaveTimer: null,
      caseManualStartAutosaveBeforeUnloadHandler: null,
      caseManualStartAutosaveInFlight: false,
      caseManualStartAutosavePending: false,
      caseManualStartAutosaveQueuedDuringFlight: false,
      caseManualStartAutosaveRevision: 0,
      caseManualStartAutosaveLastSavedRevision: 0,
      caseManualStartAutosaveLastSavedAtUtc: '',
      caseManualStartAutosaveLastError: '',
      caseManualStartAutosaveReason: '',
      caseManualStartAutosaveStatus: '',
      spreadsheetCopyStatus: {
        input: '',
        output: ''
      },
      spreadsheetCopyBusy: {
        input: false,
        output: false
      },
      spreadsheetCopyTimers: {
        input: null,
        output: null
      },
      lastSelection: null,
      currentFontSize: 14,
      showBorderModal: false,
      freezeTop: false,
      freezeLeft: false,
      inputFullscreen: false,
      keyHandler: null,
      formulaBarDraft: '',
      formulaBarBaseDraft: '',
      formulaBarEditing: false,
      formulaTargetCell: null,
      formulaCapturedReferences: [],
      formulaRefocusTimer: null,
      activeColorPicker: '',
      colorPickerSelectionSnapshot: null,
      colorPickerCloseTimer: null,
      colorPopoverPointerHandler: null,
      fillColorValue: '#93c5fd',
      textColorValue: '#1d4ed8',
      projectFiles: [],
      currentProjectFileId: '',
      selectedProjectFileId: '',
      pendingProjectBootstrapFileId: '',
      showCaseFileBrowserModal: false,
      caseFileBrowserRows: [],
      caseFileBrowserSearch: '',
      caseFileBrowserOptionsOpen: false,
      caseFileBrowserNetworkFilter: 'all',
      caseFileBrowserSortMode: 'updated_desc',
      caseFileBrowserSelectedId: '',
      caseFileBrowserLoading: false,
      caseFileBrowserCreating: false,
      caseFileBrowserDeleting: false,
      caseFileBrowserStatus: '',
      caseFileBrowserProgress: { done: 0, total: 0 },
      caseFileBrowserLastScannedAtUtc: '',
      caseFileBrowserScanToken: '',
      caseFileBrowserPreviewCache: {},
      caseFileBrowserPreviewTokens: {},
      caseFileBrowserSelectedPreviewIndex: 0,
      caseFileBrowserHoverPreviewId: '',
      caseFileBrowserHoverPreviewIndex: 0,
      caseFileBrowserHoverPreviewTimer: 0,
      caseFileBrowserPreviewWarmupTimer: 0,
      caseFileBrowserPreviewBuildTimers: {},
      showCaseFileBrowserPurgeModal: false,
      caseFileBrowserPurgeConfirmChecked: false,
      caseFileBrowserPurgeConfirmPhrase: '',
      caseFileBrowserPurgeStatus: '',
      showCaseAddressMonitoringModal: false,
      caseAddressMonitoringStatus: '',
      caseAddressMonitoringBusy: false,
      caseAddressMonitoringLastPayloads: [],
      caseAddressMonitoringLastResult: null,
      caseProjectStorageNotice: {
        visible: false,
        quota: false,
        title: '',
        message: '',
        storageKey: '',
        errorName: '',
        attemptedBytes: 0,
        attemptedSizeLabel: '',
        totalBytes: 0,
        totalSizeLabel: '',
        caseBuddyBytes: 0,
        caseBuddySizeLabel: '',
        caseBuddyProjectBytes: 0,
        caseBuddyProjectSizeLabel: '',
        itemCount: 0,
        caseBuddyFileCount: 0,
        largestItems: [],
        updatedAtUtc: ''
      },
      casePrepBuddyHandoffNotice: {
        visible: false,
        status: '',
        title: '',
        message: '',
        fileId: '',
        transactionCount: 0,
        importedAtUtc: ''
      },
      projectFileReadyState: {
        input: false,
        output: false
      },
      caseSupportState: createDefaultCaseSupportState(),
      caseSupportStateLastPersistedJson: '',
      caseSupportCompactPersistenceActive: false,
      caseSupportCompactPersistenceFileId: '',
      caseSupportCompactPersistenceStorageKey: '',
      caseSupportCompactPersistenceLastSavedBytes: 0,
      caseSupportCompactPersistenceMode: '',
      caseDetailsDraft: {
        caseName: '',
        caseId: '',
        caseType: ''
      },
      caseTitleInlineEditing: false,
      caseTitleInlineDraft: '',
      caseTitleInlineOriginalStoredName: '',
      caseTitleInlineOriginalDisplayName: '',
      caseTitleInlineFontSizePx: 0,
      caseTitleInlineFitTimer: null,
      caseTitleInlineResizeHandler: null,
      showCaseDetailsEditor: false,
      showStartingMainTransactionsModal: false,
      startingMainTransactionsPaste: '',
      startingMainTransactionDraftRows: [],
      showNetworkLookupModal: false,
      networkLookupRows: [],
      networkLookupRunning: false,
      networkLookupStatus: '',
      networkLookupFillDateIfMissing: true,
      networkLookupLastRunSummary: null,
      showDateLookupModal: false,
      dateLookupRows: [],
      dateLookupRunning: false,
      dateLookupStatus: '',
      dateLookupFillBlankNetworks: true,
      dateLookupLastRunSummary: null,
      dateLookupSortPreview: {
        totalBlocks: 0,
        datedBlocks: 0,
        undatedBlocks: 0,
        orderChanged: false,
        chronologyPass: false,
        message: 'Open the date lookup view to review date order.'
      },
      showMainPathLabelModal: false,
      mainPathLabelRows: [],
      mainPathLabelAssignRunning: false,
      mainPathLabelStatus: '',
      mainPathLabelClearContinuationRows: true,
      mainPathLabelLastRunSummary: null,
      showRawDataImportModal: false,
      rawDataImportPaste: '',
      rawDataImportRows: [],
      rawDataImportStagedSets: [],
      rawDataImportAddHeadersIfMissing: true,
      rawDataImportKeepOpenAfterAppend: true,
      rawDataImportStatus: '',
      rawDataImportLastRunSummary: null,
      caseManualStartVisualizerRunning: false,
      caseManualStartVisualizerStatus: '',
      caseManualStartLaunchSource: '',
      caseManualStartVisualizerOpening: createCaseManualStartVisualizerOpeningState(),
      caseManualStartVisualizerOpeningAbortController: null,
      caseManualStartVisualizerOpeningBodyOverflow: null,
      caseManualStartVisualizerOpeningLastSummary: null,
      caseManualStartVisualizerPreparedCacheKey: '',
      caseManualStartVisualizerPreparedState: null,
      caseManualStartVisualizerHydrationByKey: {},
      caseManualStartVisualizerLastSummary: null,
      caseManualStartVisualizerMaxHydrateRows: 25,
      caseManualStartVisualizerAttributionByAddress: {},
      caseManualStartVisualizerMaxAttributionLookups: 16,
      caseManualStartVisualizerAttributionDelayMs: 650,
      caseManualStartHopInspectorOpen: false,
      caseManualStartHopInspectorMounted: false,
      caseManualStartHopInspectorRunning: false,
      caseManualStartHopInspectorStopRequested: false,
      caseManualStartHopInspectorStatus: '',
      caseManualStartHopInspectorSnapshot: null,
      caseManualStartHopInspectorOutputRows: [],
      caseManualStartHopInspectorOutputTsv: '',
      caseManualStartHopInspectorCandidate: null,
      caseManualStartHopInspectorLoadedCandidateKey: '',
      caseManualStartHopInspectorAutoCheckTimer: null,
      caseManualStartHopInspectorAutoCheckQueuedKey: '',
      caseManualStartHopInspectorAutoCheckInFlightKey: '',
      caseManualStartHopInspectorCopyStatus: '',
      caseManualStartHopInspectorAppliedCount: 0,
      caseManualStartHopInspectorLastEvent: '',
      caseManualStartHopInspectorFullToolOpen: false,
      caseManualStartHopInspectorPreviewOpen: false,
      caseManualStartHopInspectorPreviewAction: '',
      caseManualStartHopInspectorPreviewRows: [],
      caseManualStartHopInspectorPreviewSnapshot: null,
      caseManualStartHopInspectorPreviewCandidate: null,
      caseManualStartHopInspectorPreviewStatus: '',
      caseManualStartHopInspectorPreviewApplied: false,
      caseManualStartHopInspectorPreviewServiceOverride: false,
      caseManualStartHopInspectorLastTraceServiceOverride: false,
      caseManualStartHopInspectorDormantDebug: null,
      caseManualStartHopInspectorTraceLoading: false,
      caseManualStartHopInspectorTraceLoadingTitle: 'Fetching trace preview…',
      caseManualStartHopInspectorTraceLoadingStartedAtMs: 0,
      caseManualStartHopInspectorTraceProgress: { fetched: 0, expected: 100, assumedExpected: true, status: 'preparing', message: 'Tool 88 is preparing the provider trace preview.', pulse: 0 },
      caseManualStartHopInspectorTraceProgressTimer: null,
      caseManualStartHopInspectorTraceProgressLastFetched: 0,
      caseManualStartHopInspectorPosition: { x: null, y: null },
      caseManualStartHopInspectorDragging: false,
      caseManualStartHopInspectorDragState: null,
      caseManualStartResolvedSolveWarningOpen: false,
      caseManualStartResolvedSolveWarningCandidate: null,
      caseManualStartResolvedSolveWarningChecked: false,
      caseManualStartResolvedSolveWarningStatus: '',
      caseManualStartSettingsOpen: false,
      caseManualStartSettingsStatus: '',
      caseManualStartSettingsDraft: { ...CASE_MANUAL_START_TOOL88_DEFAULT_SETTINGS },
      caseManualStartOutputChoiceOpen: false,
      caseManualStartOutputChoiceLoading: false,
      caseManualStartOutputChoiceStatus: '',
      caseManualStartOutputChoiceCandidate: null,
      caseManualStartOutputChoiceRows: [],
      caseManualStartOutputChoiceSelectedKey: '',
      caseManualStartOutputChoicePosition: { x: null, y: null },
      caseManualStartViewportAuto: {
        active: false,
        stopRequested: false,
        timer: null,
        startedAtMs: 0,
        nextActionCount: 0,
        hopApplyBaseline: 0,
        signatureCounts: {},
        bridgeNoOutputCounts: {},
        swapNoOutputCounts: {},
        traceNoProgressCounts: {},
        status: '',
        detail: ''
      },
      caseManualStartAllFlowsAuto: {
        active: false,
        stopRequested: false,
        runId: 0,
        startedAtMs: 0,
        caseFileId: '',
        caseFlowCount: 0,
        totalFlowCount: 0,
        currentFlowNumber: 0,
        currentFlowKey: '',
        currentFlowLabel: '',
        currentFlowUnresolvedCount: 0,
        completedFlowKeys: [],
        attentionFlows: [],
        status: '',
        detail: '',
        lastResult: '',
        resultVisible: false
      },
      caseManualStartAutomationCalloutContext: null,
      caseManualStartViewportAutomationCallouts: [],
      caseManualStartViewportAutomationCalloutId: 0,
      caseManualStartViewportAutomationCalloutFrame: 0,
      caseManualStartViewportCompletionEffect: {
        visible: false,
        message: '',
        timer: null,
        glowTimer: null,
        persistent: false,
        startedAtMs: 0,
        borderGlow: false,
        position: { x: null, y: null },
        dragging: false,
        dragState: null
      },
      caseManualStartViewportCompletionCheckTimer: null,
      caseManualStartViewportCompletionVerifyTimer: null,
      caseManualStartViewportLastCompletionSignature: '',
      caseManualStartViewportWorkIndicator: {
        active: false,
        finishing: false,
        action: '',
        message: '',
        candidateKey: '',
        candidate: null,
        anchor: { x: null, y: null },
        startedAtMs: 0,
        generation: 0,
        clearTimer: null,
        safetyTimer: null
      },
      caseManualStartNodeActionTrayOpen: (() => {
        try {
          const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('case_manual_start_node_action_tray_open') : null;
          return raw === null ? true : raw !== 'false';
        } catch (error) {
          return true;
        }
      })(),
      caseManualStartNodeActionTrayAttributionOpen: false,
      caseManualStartNodeActionTrayAttributionDraft: '',
      caseManualStartNodeActionTrayAttributionStatus: '',
      caseManualStartNodeActionTrayAttributionCandidate: null,
      caseManualStartReportBoundaryConfirmation: {
        visible: false,
        candidates: [],
        source: '',
        applying: false,
        acknowledged: false,
        returnToLookup: false,
        status: ''
      },
      showWayFinderHandoffModal: false,
      wayFinderHandoffBridgeUrl: 'http://127.0.0.1:8787/api',
      wayFinderHandoffConnected: false,
      wayFinderHandoffConnectionStatus: 'Not checked',
      wayFinderHandoffStatus: '',
      wayFinderHandoffTargets: [],
      wayFinderHandoffJobs: [],
      wayFinderHandoffSelectedById: {},
      wayFinderHandoffSelectedJobById: {},
      wayFinderHandoffBusy: false,
      wayFinderHandoffPolling: false,
      wayFinderHandoffPollTimer: null,
      wayFinderHandoffRefreshInFlight: false,
      wayFinderHandoffAutoImportCompleted: true,
      wayFinderHandoffStaleMinutes: 30,
      wayFinderHandoffLastRefreshUtc: '',
      showTool68PreparationModal: false,
      tool68PreparationRows: [],
      tool68PreparationStatus: '',
      tool68PreparationRunning: false,
      tool68PreparationChainMode: 'AUTO',
      tool68PreparationLabelLongestPathFirst: true,
      tool68PreparationPruneBtcUnfollowed: true,
      tool68PreparationExtraSpacing: false,
      tool68PreparationConfirmReprocess: false,
      tool68PreparationAddSpacerAfterBlock: true,
      tool68PreparationLastRunSummary: null,
      showAddressAttributionModal: false,
      addressAttributionRows: [],
      addressAttributionPaste: '',
      addressAttributionStatus: '',
      addressAttributionRunning: false,
      addressAttributionOverwriteExisting: false,
      addressAttributionLastRunSummary: null,
      showPathOperationModal: false,
      pathOperationCandidates: [],
      pathOperationSelectedCandidateId: '',
      pathOperationAction: 'prune',
      pathOperationMainFilter: '',
      pathOperationShowPathEndingsOnly: true,
      pathOperationConfirmApply: false,
      pathOperationRunning: false,
      pathOperationStatus: '',
      pathOperationRawTsv: '',
      pathOperationSelectedRawSetId: '',
      pathOperationBridgeService: '',
      pathOperationBridgeDestinationNetwork: '',
      pathOperationBridgeDestinationToken: '',
      pathOperationBridgeReceivedAmount: '',
      pathOperationLastRunSummary: null,
      inputContextMenuPayload: null,
      pathOperationContextMenuCandidate: null,
      pathOperationContextMenuSelection: null,
      pathOperationContextMenuCell: null,
      addressColoringStatus: '',
      addressColoringLastRunSummary: null,
      showEntityCuratorModal: false,
      entityCuratorRemote: {
        exchange: [],
        nonExchange: [],
        lastLoadedUtc: '',
        status: 'idle',
        message: ''
      },
      entityCuratorLocalOverlay: createDefaultEntityCuratorOverlay(),
      entityCuratorLoading: false,
      entityCuratorStatus: '',
      entityCuratorSearch: '',
      entityCuratorViewFilter: 'reviewable',
      entityCuratorActiveTab: 'review',
      entityCuratorShowKnownMatches: false,
      entityCuratorShowSuppressed: false,
      entityCuratorNewExchangeName: '',
      entityCuratorNewNonExchangeName: '',
      entityCuratorNewSuppression: '',
      entityCuratorLastReviewSummary: null,
      showCaseInconsistencyModal: false,
      caseInconsistencyCards: [],
      caseInconsistencySearch: '',
      caseInconsistencyViewFilter: 'pending',
      caseInconsistencyStatus: '',
      caseInconsistencyRunning: false,
      caseInconsistencyShowRuleSettings: false,
      caseInconsistencyRulePathEndsMismatch: true,
      caseInconsistencyRuleBranchesAsVsPathEnds: true,
      caseInconsistencyRuleUnknownHashes: true,
      caseInconsistencyRulePathEndsButNoFundsFollowed: true,
      caseInconsistencyRuleContinuesAsSingleButNextEntryMismatch: true,
      caseInconsistencyLastRunSummary: null,
      showDepositHotWalletModal: false,
      depositHotWalletSearch: '',
      depositHotWalletViewFilter: 'eligible',
      depositHotWalletStatus: '',
      depositHotWalletRunning: false,
      depositHotWalletVendorLookupEnabled: true,
      depositHotWalletRequireFreshCache: false,
      depositHotWalletTtlHours: 48,
      depositHotWalletSelectionByKey: {},
      depositHotWalletVendorResultsByKey: {},
      depositHotWalletLastRunSummary: null,
      showDepositPatternModal: false,
      depositPatternCandidates: [],
      depositPatternSearch: '',
      depositPatternViewFilter: 'pending_required',
      depositPatternMinGrade: 'HIGH',
      depositPatternSortMode: 'priority_then_time',
      depositPatternStatus: '',
      depositPatternDecisionDrafts: {},
      depositPatternLastRunSummary: null,
      showKnownEntityCasingModal: false,
      knownEntityCasingCandidates: [],
      knownEntityCasingSearch: '',
      knownEntityCasingViewFilter: 'pending',
      knownEntityCasingKindFilter: 'all',
      knownEntityCasingSelectedByKey: {},
      knownEntityCasingStatus: '',
      knownEntityCasingRunning: false,
      knownEntityCasingLastRunSummary: null,
      showDuplicateHashLabelerModal: false,
      duplicateHashLabelerItems: [],
      duplicateHashLabelerSearch: '',
      duplicateHashLabelerViewFilter: 'eligible',
      duplicateHashLabelerSelectedByKey: {},
      duplicateHashLabelerStatus: '',
      duplicateHashLabelerRunning: false,
      duplicateHashLabelerLastRunSummary: null,
      showNoteHashRewriteModal: false,
      noteHashRewriteItems: [],
      noteHashRewriteSearch: '',
      noteHashRewriteViewFilter: 'eligible',
      noteHashRewriteSelectedByKey: {},
      noteHashRewriteStatus: '',
      noteHashRewriteRunning: false,
      noteHashRewriteBatchProgress: null,
      noteHashRewriteIncludeUnstructured: false,
      noteHashRewriteLastRunSummary: null,
      showWwwUsdReferenceModal: false,
      wwwUsdReferenceSearch: '',
      wwwUsdReferenceViewFilter: 'missing',
      wwwUsdReferenceStatus: '',
      showAddressLegendModal: false,
      addressLegendCandidates: [],
      addressLegendSelectedCandidateId: '',
      addressLegendSearch: '',
      addressLegendViewFilter: 'changed',
      addressLegendStatus: '',
      addressLegendRunning: false,
      addressLegendLabelDormantSeparately: false,
      addressLegendUseDepositLabels: false,
      addressLegendLastRunSummary: null,
      showTemplateDeliverableModal: false,
      templateDeliverableSearch: '',
      templateDeliverableViewFilter: 'all',
      templateDeliverableStatus: '',
      templateDeliverableBuildSession: {
        revision: null,
        basisHash: '',
        builtAtUtc: '',
        trustedCurrentRevision: false
      },
      showSuspiciousAddressTableModal: false,
      suspiciousAddressTableSearch: '',
      suspiciousAddressTableViewFilter: 'all',
      suspiciousAddressTableStatus: '',
      showExchangeAddressTableModal: false,
      exchangeAddressTableSearch: '',
      exchangeAddressTableViewFilter: 'included',
      exchangeAddressTableStatus: '',
      exchangeAddressTableReviewComment: '',
      showDormantAddressDeliverablesModal: false,
      dormantAddressDeliverableSearch: '',
      dormantAddressDeliverableActiveView: 'official',
      dormantAddressDeliverableStatus: '',
      showWhatWentWhereModal: false,
      whatWentWhereSearch: '',
      whatWentWhereViewFilter: 'references_missing',
      whatWentWhereStatus: '',
      whatWentWhereSelectedReferenceKeys: {},
      whatWentWhereDraftByKey: {},
      whatWentWhereEstimateByKey: {},
      whatWentWhereEstimateRunning: false,
      whatWentWhereEstimateProgress: { done: 0, total: 0 },
      showCaseConclusionModal: false,
      caseConclusionDraft: '',
      caseConclusionStatus: '',
      showReportBodyModal: false,
      reportBodySearch: '',
      reportBodyActiveVariant: CASE_REPORT_BODY_VARIANT_STANDARD,
      reportBodyStatus: '',
      showAddendumPathEndingReviewModal: false,
      addendumPathEndingSearch: '',
      addendumPathEndingViewFilter: 'included',
      addendumPathEndingStatus: '',
      addendumPathEndingRefreshRunning: false,
      addendumPathEndingDedupe: true,
      addendumPathEndingRequirePathEnds: true,
      showAddendumRecipientEntityModal: false,
      addendumRecipientSearch: '',
      addendumRecipientViewFilter: 'needs_choice',
      addendumRecipientStatus: '',
      addendumRecipientRefreshRunning: false,
      addendumRecipientDraftByKey: {},
      showCaseAddendumModal: false,
      caseAddendumSearch: '',
      caseAddendumActiveVariant: CASE_ADDENDUM_VARIANT_STRUCTURED,
      caseAddendumAggregateDuplicateHashes: true,
      caseAddendumStatus: '',
      caseAddendumRefreshRunning: false,
      showCaseFlowVisualsModal: false,
      caseFlowVisualsStatus: '',
      caseFlowVisualsRefreshRunning: false,
      caseFlowVisualsDownloading: false,
      caseFlowVisualsDownloadFormat: '',
      caseFlowVisualsActiveIndex: 0,
      caseFlowVisualsState: null,
      caseFlowVisualsCurrentVisual: null,
      caseFlowVisualsSelectedNodeId: '',
      caseFlowVisualsViewport: { x: 0, y: 0, width: 1200, height: 700 },
      caseFlowVisualsPointer: { active: false, startX: 0, startY: 0, viewBox: null },
      caseFlowVisualsPanFrame: 0,
      caseFlowVisualsPendingViewport: null,
      caseFlowVisualsAdvancedOpen: false,
      caseFlowVisualsMode: 'report',
      caseFlowVisualsIntent: 'report',
      caseFlowVisualsDisplayMode: 'selected',
      caseFlowVisualsViewMode: 'selected',
      caseFlowVisualsLastInvestigationIndex: 0,
      caseFlowVisualsFullWindow: false,
      caseFlowVisualsUndoStack: [],
      caseFlowVisualsRedoStack: [],
      caseFlowVisualsSelectedPathOperationCandidate: null,
      caseFlowVisualsOptions: {
        layoutMode: 'balanced',
        mergeAddressMode: 'sameColumnMiddle',
        mergeTransactionNodes: true,
        wrapLinearTrunks: true,
        trunkWrapMinLength: 5,
        trunkWrapFirstRowLength: 5,
        trunkWrapOtherRowLength: 4,
        trunkWrapRowVerticalMultiplier: 1.35,
        highlightSwaps: true,
        highlightBridges: true,
        cleanupOverlaps: true,
        labelAddresses: true
      },
      showCaseInvestigationVisualizerModal: false,
      showCaseManualSolveBackdropCloseConfirm: false,
      caseManualSolveIntroOpen: false,
      caseManualSolveIntroDontShowAgain: false,
      caseInvestigationVisualizerStatus: '',
      caseInvestigationVisualizerRefreshRunning: false,
      caseInvestigationVisualizerDownloading: false,
      caseInvestigationVisualizerViewMode: 'single',
      caseInvestigationVisualizerActiveIndex: 0,
      caseInvestigationVisualizerLastFlowKey: '',
      caseInvestigationVisualizerState: null,
      caseInvestigationVisualizerCurrentVisual: null,
      caseInvestigationVisualizerSelectedNodeId: '',
      caseInvestigationVisualizerSelectedNodeIds: [],
      caseInvestigationVisualizerSelectedCandidate: null,
      caseManualSolveAmountEditor: {
        open: false,
        field: '',
        draft: '',
        token: '',
        status: '',
        candidateKey: '',
        rowIndex: -1
      },
      caseManualFifoInspector: {
        open: false,
        title: '',
        subtitle: '',
        rowLabel: '',
        rowValue: '',
        candidateKey: '',
        candidate: null,
        diagnostics: null
      },
      caseManualSolveVisualColorPanelOpen: false,
      caseManualSolveVisualColorDraft: '#93c5fd',
      caseManualSolveVisualColorStatus: '',
      caseManualSolveVisualGroupNameDraft: '',
      caseManualSolveVisualGroupColorDraft: '#4f46e5',
      caseManualSolveVisualGroupStatus: '',
      caseManualSolveExpandedGroupId: '',
      caseManualSolveVisualGroupRuntimeNodesById: {},
      caseManualSolveVisualGroupDrag: { active: false, pointerId: null, nodeId: '', groupIds: [], startPoint: null, startPositions: {}, moved: false, beforeState: null },
      caseManualSolveVisualLegendOpen: false,
      caseInvestigationVisualizerHiddenNodeIds: [],
      caseManualStartBranchDeletionPreview: null,
      caseManualStartBranchDeletionApplying: false,
      caseManualStartBranchDeletionStatus: '',
      caseInvestigationVisualizerTransactionOnlyMode: false,
      caseInvestigationVisualizerViewport: { x: 0, y: 0, width: 1200, height: 700 },
      caseInvestigationVisualizerPointer: { active: false, startX: 0, startY: 0, viewBox: null },
      caseInvestigationVisualizerInteraction: { mode: '', pointerId: null },
      caseInvestigationVisualizerRightClickCopyGesture: { active: false, pointerId: null, startClientX: 0, startClientY: 0, nodeId: '', value: '', label: '', cancelled: false },
      caseInvestigationVisualizerClipboardMessage: '',
      caseInvestigationVisualizerClipboardTimer: null,
      caseInvestigationVisualizerSelectionBox: { active: false, startX: 0, startY: 0, endX: 0, endY: 0 },
      caseInvestigationVisualizerDraggingNodes: false,
      caseInvestigationVisualizerSelectingNodes: false,
      caseInvestigationVisualizerSuppressNextNodeClick: false,
      caseInvestigationVisualizerSnapGuide: { active: false, axis: '', x1: 0, y1: 0, x2: 0, y2: 0, targetX: 0, targetY: 0, targetNodeId: '' },
      caseInvestigationVisualizerUndoStack: [],
      caseInvestigationVisualizerRedoStack: [],
      caseInvestigationVisualizerHistoryLimit: 40,
      caseInvestigationVisualizerPanFrame: 0,
      caseInvestigationVisualizerPendingViewport: null,
      caseInvestigationVisualizerCameraMoving: false,
      caseInvestigationVisualizerAdvancedOpen: true,
      caseInvestigationVisualizerAdvancedHeight: 118,
      caseInvestigationVisualizerAdvancedResizing: false,
      caseInvestigationVisualizerAdvancedResizeStart: { y: 0, height: 118 },
      caseInvestigationVisualizerSidePanelWidth: 390,
      caseInvestigationVisualizerSidePanelResizing: false,
      caseInvestigationVisualizerSidePanelResizeStart: { x: 0, width: 390 },
      caseInvestigationVisualizerScrollLockSnapshot: null,
      caseInvestigationVisualizerFullscreen: false,
      caseInvestigationVisualizerOptions: {
        layoutMode: 'upwardBranches',
        mergeAddressMode: 'none',
        mergeTransactionNodes: false,
        wrapLinearTrunks: false,
        trunkWrapMinLength: 5,
        trunkWrapFirstRowLength: 5,
        trunkWrapOtherRowLength: 4,
        trunkWrapRowVerticalMultiplier: 1.35,
        highlightSwaps: false,
        highlightBridges: false,
        cleanupOverlaps: false,
        labelAddresses: true,
        showFundsFollowedBars: true
      },
      caseManualStartViewportDisplayControlsOpen: false,
      caseManualSolveEvaluationOpen: false,
      caseManualSolveGetDataPanelOpen: false,
      caseManualSolveGetDataPanelPosition: { x: null, y: null },
      caseManualSolveGetDataPanelDragging: false,
      caseManualSolveGetDataPanelDragState: null,
      caseManualSolveGetDataDraft: '',
      caseManualSolveGetDataStatus: '',
      caseManualSolveGetDataLastSummary: null,
      caseManualSolveBottomPanelHeight: 280,
      caseManualSolveBottomPanelResizing: false,
      caseManualSolveBottomPanelResizeStart: { y: 0, height: 280 },
      caseManualSolveHeatMapEnabled: false,
      caseManualSolveHeatMapSensitivity: 1,
      caseManualStartVisualizerOptions: {
        labelAddresses: true,
        showFundsFollowedBars: true,
        mergeAddressMode: 'none'
      },
      showCaseHopSolverModal: false,
      showCaseHopSolverPlaceholderModal: false,
      caseHopSolverCandidate: null,
      caseHopSolverStatusTitle: 'Solve hop',
      caseHopSolverStatus: '',
      caseHopSolverRunning: false,
      caseHopSolverAutomationRunning: false,
      caseHopSolverStopRequested: false,
      caseHopSolverSnapshot: null,
      caseHopSolverOutputRows: [],
      caseHopSolverOutputTsv: '',
      caseHopSolverLastEvent: '',
      caseHopSolverRawApplyStatus: '',
      caseHopSolverViewMode: 'simple',
      caseHopSolverStartInAdvancedView: false,
      caseHopSolverFlowCompleteFirstSeenAtMs: 0,
      caseHopSolverFlowCompleteConfirmations: 0,
      caseHopSolverPlaceholderCandidate: null,
      caseHopSolverPlaceholderStatus: '',
      caseHopSolverPlaceholderRunning: false,
      caseHopSolverPlaceholderStopRequested: false,
      caseInvestigationHopSolverPlaceholder: {
        open: false,
        busy: false,
        stopRequested: false,
        status: '',
        detail: '',
        candidateId: '',
        candidateSnapshot: null,
        startedAtUtc: '',
        stoppedAtUtc: '',
        completedAtUtc: ''
      },
      showCaseFullReportPackageModal: false,
      caseFullReportPackageActiveView: 'rich',
      caseFullReportPackageStatus: '',
      caseFullReportPackageRefreshRunning: false,
      caseFullReportPackagePdfPreparing: false,
      caseFullReportPackagePayload: null,
      caseFullReportPackagePayloadCacheKey: '',
      showCaseWorkflowCompletionModal: false,
      caseWorkflowCompletionMessage: '',
      caseSummary: createEmptyCaseSummary(),
      caseToast: '',
      caseToastTimer: null,
      caseSummaryRefreshTimer: null,
      caseSummaryRefreshIdleHandle: null,
      caseSupportPersistTimer: null,
      watchedInputHotInstances: [],
      watchedOutputHotInstances: []
    };
  },
  computed: {

    caseAnyModalVisible() {
      return !!(
        this.showBorderModal ||
        this.showCaseDetailsEditor ||
        this.showCaseDetailsReviewAcknowledgementModal ||
        this.caseSimpleDetailsVisible ||
        this.showCaseFileBrowserModal ||
        this.showCaseAddressMonitoringModal ||
        this.showWorkflowStepHelpModal ||
        this.showStartingMainTransactionsModal ||
        this.showNetworkLookupModal ||
        this.showDateLookupModal ||
        this.showMainPathLabelModal ||
        this.showRawDataImportModal ||
        this.showWayFinderHandoffModal ||
        this.showTool68PreparationModal ||
        this.showAddressAttributionModal ||
        this.showPathOperationModal ||
        this.showEntityCuratorModal ||
        this.showCaseInconsistencyModal ||
        this.showDepositHotWalletModal ||
        this.showDepositPatternModal ||
        this.showKnownEntityCasingModal ||
        this.showDuplicateHashLabelerModal ||
        this.showNoteHashRewriteModal ||
        this.showWwwUsdReferenceModal ||
        this.showAddressLegendModal ||
        this.showTemplateDeliverableModal ||
        this.showSuspiciousAddressTableModal ||
        this.showExchangeAddressTableModal ||
        this.showDormantAddressDeliverablesModal ||
        this.showWhatWentWhereModal ||
        this.showCaseConclusionModal ||
        this.showReportBodyModal ||
        this.showAddendumPathEndingReviewModal ||
        this.showAddendumRecipientEntityModal ||
        this.showCaseAddendumModal ||
        this.showCaseFlowVisualsModal ||
        this.showCaseInvestigationVisualizerModal ||
        this.showCaseHopSolverPlaceholderModal ||
        this.showCaseFullReportPackageModal ||
        (this.showCaseWorkflowCompletionModal && !this.caseQuickLookupOwnsWorkflowCompletion)
      );
    },

    caseToastClass() {
      return {
        'case-toast--modal-open': this.caseAnyModalVisible,
        'case-toast--operation-active': !!(this.caseGlobalOperation?.active || this.caseModalOperation?.active)
      };
    },

    caseInputFlowStoryVisible() {
      return Boolean(this.caseInputFlowStoryEnabled && !this.caseInputFlowStoryDismissed);
    },

    caseInputFlowStoryCanRestore() {
      return Boolean(this.caseInputFlowStoryEnabled && this.caseInputFlowStoryDismissed);
    },

    caseInputFlowStoryRootCount() {
      const loadedCount = Array.isArray(this.caseInputFlowStoryDescriptors) ? this.caseInputFlowStoryDescriptors.length : 0;
      return loadedCount || Number(this.caseSummary?.startingMainTransactions) || 0;
    },

    caseInputFlowStorySummary() {
      const count = this.caseInputFlowStoryRootCount;
      if (this.caseInputFlowStoryStatus === 'loading' && !count) return 'Reading Main Data and preparing the first visible flows…';
      if (!count) return 'A visual reading layer for the spreadsheet will appear here after starting transactions are loaded.';
      const heatText = this.caseInputFlowStoryHeatEnabled
        ? 'Heat colors show estimated value relative to each root.'
        : 'Normal colors show blue addresses and yellow transactions.';
      return `${count} main transaction flow${count === 1 ? '' : 's'} · ${heatText} These represent the main transaction flows in your case.`;
    },

    caseIntakeRailLiveDataState() {
      // These four stages describe current spreadsheet truth. Cache the scan by
      // case revision/file readiness so existing cases are evaluated on load and
      // after real data mutations, rather than on every render.
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const inputReady = Boolean(this.projectFileReadyState?.input);
      const fileId = normalizeCell(this.currentProjectFileId || '');
      try {
        const matrix = typeof this.getMatrixForLogicalSheet === 'function'
          ? this.getMatrixForLogicalSheet('main')
          : [];
        return {
          ...this.buildCaseIntakeRailLiveDataState(Array.isArray(matrix) ? matrix : []),
          readSucceeded: true,
          revision,
          inputReady,
          fileId
        };
      } catch (error) {
        console.warn('Case Buddy could not derive the intake assistant state from Main Data.', error);
        return {
          readSucceeded: false,
          available: false,
          rootCount: 0,
          startingComplete: false,
          networksComplete: false,
          datesPresentComplete: false,
          chronologyPass: false,
          datesComplete: false,
          labelsPresentComplete: false,
          labelsComplete: false,
          labelsChronologyPass: false,
          rootLabelSequence: null,
          missingNetworkCount: 0,
          missingDateCount: 0,
          outOfOrderCount: 0,
          missingLabelCount: 0,
          outOfSequenceLabelCount: 0,
          rootStates: [],
          statusDetailByKey: {},
          revision,
          inputReady,
          fileId
        };
      }
    },

    caseIntakeRailRootCount() {
      const liveState = this.caseIntakeRailLiveDataState || {};
      if (liveState.readSucceeded) return Math.max(0, Number(liveState.rootCount) || 0);
      const summaryCount = Math.max(0, Number(this.caseSummary?.startingMainTransactions) || 0);
      const visualCount = Math.max(0, Number(this.caseInputFlowStoryRootCount) || 0);
      return Math.max(summaryCount, visualCount);
    },

    caseIntakeRailVisible() {
      return Boolean(CASE_INTAKE_PREPARATION_RAIL_ENABLED && this.caseIntakeRailRootCount > 0);
    },

    caseIntakeRailWorkflowSteps() {
      const definitions = [
        { key: 'starting', label: 'Load starting main transactions', shortLabel: 'Starting rows' },
        { key: 'networks', label: 'Identify transaction networks', shortLabel: 'Networks' },
        { key: 'dates', label: 'Complete UTC dates and order main entries', shortLabel: 'Dates / order' },
        { key: 'labels', label: 'Assign main transaction path labels', shortLabel: 'Main labels' },
        { key: 'casedetails', label: 'Review case details and early narrative', shortLabel: 'Early review' }
      ];
      const liveState = this.caseIntakeRailLiveDataState || {};
      const liveCompletionByKey = {
        starting: Boolean(liveState.startingComplete),
        networks: Boolean(liveState.networksComplete),
        dates: Boolean(liveState.datesComplete),
        labels: Boolean(liveState.labelsComplete)
      };
      return definitions.map((definition, index) => {
        const patterns = typeof this.caseWorkflowIntakeStepPatterns === 'function'
          ? this.caseWorkflowIntakeStepPatterns(definition.key)
          : [];
        const match = typeof this.findCaseWorkflowStepByPatterns === 'function'
          ? this.findCaseWorkflowStepByPatterns(patterns)
          : { group: null, step: null, index: -1 };
        const step = match?.step || null;
        const workflowComplete = Boolean(step?.complete && !step?.stale);
        const workflowStale = Boolean(step?.stale);
        const dataBacked = Object.prototype.hasOwnProperty.call(liveCompletionByKey, definition.key);
        const liveAvailable = Boolean(dataBacked && liveState.readSucceeded);
        const liveComplete = Boolean(liveAvailable && liveCompletionByKey[definition.key]);
        // Main Data is authoritative for spreadsheet-backed steps. Workflow state
        // remains authoritative for the explicit early-review acknowledgement.
        const complete = dataBacked && liveAvailable ? liveComplete : workflowComplete;
        const stale = dataBacked && liveAvailable
          ? Boolean(!liveComplete && workflowStale)
          : workflowStale;
        const liveDetail = normalizeCell(liveState.statusDetailByKey?.[definition.key]);
        return {
          ...definition,
          index,
          match,
          step,
          dataBacked,
          workflowComplete,
          workflowStale,
          liveAvailable,
          liveComplete,
          complete,
          stale,
          completionSource: complete
            ? (liveComplete ? 'main-data' : 'workflow')
            : '',
          statusTitle: dataBacked && liveDetail
            ? `${definition.label}: ${liveDetail}`
            : (complete
              ? `${definition.label}: complete.`
              : stale
                ? `${definition.label}: the previous workflow result is stale.`
                : `${definition.label}: pending.`)
        };
      });
    },

    caseIntakeRailWorkflowReady() {
      const steps = this.caseIntakeRailWorkflowSteps || [];
      return steps.length === 5 && steps.every(step => !!step.step);
    },

    caseIntakeRailAllComplete() {
      const steps = this.caseIntakeRailWorkflowSteps || [];
      return this.caseIntakeRailWorkflowReady && steps.every(step => step.complete);
    },

    caseWorkflowAssistantPhase() {
      // Keep the rail focused on the furthest category the investigator has
      // legitimately reached. Later case revisions should not repopulate an
      // entire completed category merely because one older workflow snapshot is
      // stale. Current spreadsheet-backed intake truth can still send the rail
      // back to intake if networks, dates/order, or root labels are actually lost.
      if (!this.caseAttributionWorkflowIntakeDataReady) return 'intake';
      if (this.caseCleanupWorkflowShouldShowPhase) return 'cleanup';
      if (this.caseAttributionWorkflowRailVisible) return 'attribution';
      const continuation = this.caseIntakeContinuationCheck || createCaseIntakeContinuationCheckState();
      const rawSaplingStarted = Boolean(
        this.caseIntakeRailAllComplete
        || continuation.checkedAtUtc
        || continuation.status === 'checking'
        || this.caseIntakeContinuationManualSolveLaunched
        || this.caseIntakeContinuationTool68Launched
        || this.caseIntakeContinuationSaplingPreparing
      );
      return rawSaplingStarted ? 'raw-sapling' : 'intake';
    },


    caseWorkflowAssistantStageProgressRows() {
      const phaseRankByKey = {
        intake: 0,
        'raw-sapling': 1,
        attribution: 2,
        cleanup: 3
      };
      const phaseRank = Number(phaseRankByKey[this.caseWorkflowAssistantPhase]) || 0;

      // Later phases count as evidence that an earlier section was legitimately
      // reached. The completion chain stays sequential, so when the assistant
      // returns to an earlier section it cannot leave isolated green milestones ahead.
      const intakeComplete = Boolean(this.caseIntakeRailAllComplete || phaseRank > 0);
      const rawGraphComplete = Boolean(
        intakeComplete
        && (this.caseAttributionWorkflowPrerequisiteKnownComplete || phaseRank > 1)
      );
      const attributionComplete = Boolean(
        rawGraphComplete
        && (this.caseCleanupWorkflowAttributionKnownComplete || phaseRank > 2)
      );
      const cleanupComplete = Boolean(
        attributionComplete
        && this.caseCleanupWorkflowReadyForDeliverables
      );
      const shippingComplete = Boolean(
        cleanupComplete
        && this.caseWorkflowAssistantOverallWorkflowComplete
      );

      const completionByKey = {
        intake: intakeComplete,
        'raw-graph': rawGraphComplete,
        attribution: attributionComplete,
        cleanup: cleanupComplete,
        shipping: shippingComplete
      };
      const firstIncompleteIndex = CASE_WORKFLOW_ASSISTANT_STAGE_PROGRESS_DEFINITIONS.findIndex(
        definition => !completionByKey[definition.key]
      );

      return CASE_WORKFLOW_ASSISTANT_STAGE_PROGRESS_DEFINITIONS.map((definition, index) => {
        const complete = Boolean(completionByKey[definition.key]);
        const current = Boolean(!complete && index === firstIncompleteIndex);
        const statusLabel = complete ? 'Complete' : current ? 'Current section' : 'Upcoming';
        return {
          ...definition,
          index,
          complete,
          current,
          statusLabel,
          title: `${definition.label}: ${statusLabel.toLowerCase()}.`,
          ariaLabel: `${definition.label}: ${statusLabel}.`,
          className: {
            'is-complete': complete,
            'is-current': current,
            'is-upcoming': !complete && !current
          }
        };
      });
    },

    caseWorkflowAssistantStageProgressAriaLabel() {
      const rows = this.caseWorkflowAssistantStageProgressRows || [];
      const completedCount = rows.filter(row => row.complete).length;
      const current = rows.find(row => row.current) || null;
      if (rows.length && completedCount === rows.length) {
        return `Workflow Assistant progress: all ${rows.length} sections complete.`;
      }
      return `Workflow Assistant progress: ${completedCount} of ${rows.length || 5} sections complete.${current ? ` Current section: ${current.label}.` : ''}`;
    },

    caseIntakeContinuationCheckIsCurrent() {
      const state = this.caseIntakeContinuationCheck || createCaseIntakeContinuationCheckState();
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const fileId = normalizeCell(this.currentProjectFileId || '');
      return Boolean(
        state.status === 'checked'
        && Number(state.checkedRevision) === revision
        && normalizeCell(state.checkedFileId || '') === fileId
      );
    },

    caseIntakeContinuationCheckIsStale() {
      const state = this.caseIntakeContinuationCheck || createCaseIntakeContinuationCheckState();
      if (!state.checkedAtUtc || state.status === 'checking') return false;
      return !this.caseIntakeContinuationCheckIsCurrent;
    },

    caseIntakeContinuationAction() {
      if (!this.caseIntakeRailAllComplete) return 'blocked';
      if (this.caseIntakeContinuationSaplingPreparing) return 'preparing-sapling';
      if (this.caseIntakeContinuationCheck?.status === 'checking') return 'checking';
      if (!this.caseIntakeContinuationCheckIsCurrent) return 'check';
      if (!this.caseIntakeContinuationCheck?.rawStageComplete) return 'manual-solve';
      if (!this.caseIntakeContinuationCheck?.saplingStageComplete) {
        const state = this.caseIntakeContinuationCheck || createCaseIntakeContinuationCheckState();
        const preparationIsActionable = Number(state.missingMainEntryCount) > 0
          || Number(state.unpreparedRootCount) > 0;
        return preparationIsActionable ? 'prepare-sapling' : 'review-sapling';
      }
      return 'ready';
    },

    caseIntakeContinuationStepRows() {
      const state = this.caseIntakeContinuationCheck || createCaseIntakeContinuationCheckState();
      const current = this.caseIntakeContinuationCheckIsCurrent;
      const stale = this.caseIntakeContinuationCheckIsStale;
      const checking = state.status === 'checking';
      const saplingPreparing = Boolean(this.caseIntakeContinuationSaplingPreparing);
      const error = state.status === 'error';
      const rootCount = Math.max(0, Number(state.rootCount) || this.caseIntakeRailRootCount || 0);
      const rawComplete = Boolean(current && state.rawStageComplete);
      const saplingComplete = Boolean(current && state.saplingStageComplete);
      const baseStatus = checking
        ? 'Checking current rows…'
        : error
          ? 'Check needs retry'
          : stale
            ? 'Check is stale'
            : 'Not checked yet';
      const rawStatus = !current
        ? baseStatus
        : rawComplete
          ? `${rootCount}/${rootCount} root flow${rootCount === 1 ? '' : 's'} complete`
          : Number(state.missingRawRootCount) > 0
            ? `${state.missingRawRootCount} root${Number(state.missingRawRootCount) === 1 ? '' : 's'} need Raw Data`
            : `${state.invalidRawRootCount || state.rawBlockingIssueCount || 1} flow${Number(state.invalidRawRootCount || state.rawBlockingIssueCount || 1) === 1 ? '' : 's'} need resolution`;
      const saplingStatus = saplingPreparing
        ? 'Preparing current Raw Data…'
        : !current
          ? baseStatus
          : !rawComplete
            ? 'Waiting for complete Raw Data'
            : saplingComplete
              ? `${rootCount}/${rootCount} root${rootCount === 1 ? '' : 's'} prepared`
              : Number(state.missingMainEntryCount) > 0
                ? `${state.missingMainEntryCount} Raw Data transaction${Number(state.missingMainEntryCount) === 1 ? ' is' : 's are'} not represented in the matching Main flow${Number(state.missingMainEntryCount) === 1 ? '' : 's'}`
                : Number(state.unpreparedRootCount) > 0
                  ? `${state.preparedRootCount}/${rootCount} roots prepared`
                  : `${state.mainBlockingIssueCount || 1} Main Data issue${Number(state.mainBlockingIssueCount || 1) === 1 ? '' : 's'}`;
      return [
        {
          key: 'raw-data',
          number: 6,
          shortLabel: 'Load Raw Data',
          complete: rawComplete,
          statusLabel: rawStatus,
          statusTitle: `Load raw data for starting main transactions: ${rawStatus}.`,
          issueCount: current && !rawComplete ? Math.max(0, Number(state.rawIssueCount) || 0) : 0,
          className: {
            'case-intake-continuation-step--complete': rawComplete,
            'case-intake-continuation-step--checking': checking,
            'case-intake-continuation-step--stale': stale,
            'case-intake-continuation-step--attention': current && !rawComplete,
            'case-intake-continuation-step--error': error
          }
        },
        {
          key: 'sapling',
          number: 7,
          shortLabel: 'Prepare graph',
          complete: saplingComplete,
          statusLabel: saplingStatus,
          statusTitle: `Prepare raw data into main data: ${saplingStatus}.`,
          issueCount: current && rawComplete && !saplingComplete ? Math.max(0, Number(state.mainIssueCount) || 0) : 0,
          className: {
            'case-intake-continuation-step--complete': saplingComplete,
            'case-intake-continuation-step--checking': checking || saplingPreparing,
            'case-intake-continuation-step--stale': stale,
            'case-intake-continuation-step--attention': current && rawComplete && !saplingComplete,
            'case-intake-continuation-step--error': error
          }
        }
      ];
    },

    caseIntakeContinuationDisplayedIssues() {
      if (!this.caseIntakeContinuationCheckIsCurrent) return [];
      const state = this.caseIntakeContinuationCheck || createCaseIntakeContinuationCheckState();
      if (this.caseIntakeContinuationIssueStage === 'raw-data') {
        return Array.isArray(state.rawIssueExamples) ? state.rawIssueExamples : [];
      }
      if (this.caseIntakeContinuationIssueStage === 'sapling') {
        return Array.isArray(state.mainIssueExamples) ? state.mainIssueExamples : [];
      }
      return [];
    },

    caseIntakeContinuationDisplayedIssueCount() {
      const state = this.caseIntakeContinuationCheck || createCaseIntakeContinuationCheckState();
      if (this.caseIntakeContinuationIssueStage === 'raw-data') return Math.max(0, Number(state.rawIssueCount) || 0);
      if (this.caseIntakeContinuationIssueStage === 'sapling') return Math.max(0, Number(state.mainIssueCount) || 0);
      return 0;
    },

    caseIntakeContinuationStatusDetail() {
      const state = this.caseIntakeContinuationCheck || createCaseIntakeContinuationCheckState();
      if (this.caseIntakeContinuationSaplingPreparing) return 'Preparing current Raw Data into Main Data. Case Buddy will refresh this guidance when the operation finishes.';
      if (state.status === 'checking') return 'Checking root coverage, continuation links, meaningful path endings, and prepared Main Data. This runs only when requested.';
      if (state.status === 'error') return state.error || 'The focused check could not finish. Nothing was changed; run it again when the case is idle.';
      if (this.caseIntakeContinuationCheckIsStale) return 'Main or Raw Data changed after the last focused check. C again for current guidance.';
      if (!this.caseIntakeContinuationCheckIsCurrent) return 'Click "Check Next Steps" below to check case progress';
      return state.detail || state.message || 'The focused Raw Data and graph check is current.';
    },

    caseIntakeContinuationPrimaryLabel() {
      switch (this.caseIntakeContinuationAction) {
        case 'checking': return 'Checking case…';
        case 'preparing-sapling': return 'Preparing graph…';
        case 'manual-solve': return 'Open Manual Solve';
        case 'prepare-sapling': return 'Prepare graph';
        case 'review-sapling': return 'Review findings';
        case 'check': return 'Check next steps';
        default: return 'Next step unavailable';
      }
    },

    caseIntakeContinuationPrimaryHelp() {
      const state = this.caseIntakeContinuationCheck || createCaseIntakeContinuationCheckState();
      switch (this.caseIntakeContinuationAction) {
        case 'checking': return 'Focused Raw Data and Main Data review';
        case 'preparing-sapling': return 'Processing current raw-backed roots';
        case 'manual-solve':
          if (Number(state.missingRawRootCount) > 0) {
            return `${state.missingRawRootCount} root${Number(state.missingRawRootCount) === 1 ? '' : 's'} need flow solving`;
          }
          return `${state.rawBlockingIssueCount || 1} raw-flow issue${Number(state.rawBlockingIssueCount || 1) === 1 ? '' : 's'} to resolve`;
        case 'prepare-sapling':
          if (Number(state.missingMainEntryCount) > 0) {
            return `${state.missingMainEntryCount} raw transaction entr${Number(state.missingMainEntryCount) === 1 ? 'y needs' : 'ies need'} preparation`;
          }
          if (Number(state.unpreparedRootCount) > 0) {
            return `${state.unpreparedRootCount} root${Number(state.unpreparedRootCount) === 1 ? '' : 's'} need raw-to-main preparation`;
          }
          return 'Process current Raw Data with Tool 68';
        case 'review-sapling':
          return `${Math.max(1, Number(state.mainIssueCount) || Number(state.mainBlockingIssueCount) || 0)} finding${Math.max(1, Number(state.mainIssueCount) || Number(state.mainBlockingIssueCount) || 0) === 1 ? '' : 's'} to inspect before changing Main Data`;
        case 'check': return 'Click here to check how to proceed';
        default: return 'Finish the current Case Buddy operation first';
      }
    },

    caseIntakeContinuationActionIcon() {
      switch (this.caseIntakeContinuationAction) {
        case 'checking': return '…';
        case 'preparing-sapling': return '…';
        case 'manual-solve': return '→';
        case 'prepare-sapling': return '⇢';
        case 'review-sapling': return '?';
        case 'check': return '↻';
        default: return '—';
      }
    },

    caseIntakeContinuationPrimaryTitle() {
      if (this.casePageHydrating) return 'The case workspace is still loading.';
      if (this.caseIntakeRailOtherAutomationActive) return 'Another Case Buddy automation or operation is running.';
      switch (this.caseIntakeContinuationAction) {
        case 'checking': return 'The focused Raw Data and graph check is running.';
        case 'preparing-sapling': return 'Current Raw Data is being prepared into Main Data.';
        case 'manual-solve': return 'Open Hydrate / Visual in Manual Solve mode to gather and resolve Raw Data flows.';
        case 'prepare-sapling': return 'Run the existing Tool 68 raw-to-main preparation. If investigator review is needed, Case Buddy will leave that panel open.';
        case 'review-sapling': return 'Show the exact findings without changing Main Data. Preparation is not started from this review action.';
        case 'check': return 'Run a focused, on-demand check of current Raw Data coverage, meaningful path endings, continuation links, and prepared Main Data.';
        default: return 'The next two workflow stages are complete.';
      }
    },

    caseIntakeContinuationCheckDisabled() {
      return Boolean(
        !this.caseIntakeRailAllComplete
        || this.casePageHydrating
        || this.caseIntakeRailOtherAutomationActive
        || this.caseIntakeContinuationSaplingPreparing
        || this.caseIntakeContinuationCheck?.status === 'checking'
      );
    },

    caseIntakeContinuationActionDisabled() {
      return Boolean(
        this.caseIntakeContinuationCheckDisabled
        || this.caseIntakeContinuationAction === 'ready'
        || this.caseIntakeContinuationAction === 'blocked'
      );
    },

    caseAttributionWorkflowRailEnabled() {
      return Boolean(CASE_ATTRIBUTION_WORKFLOW_RAIL_ENABLED);
    },

    caseAttributionWorkflowPrerequisiteKnownComplete() {
      const state = this.caseIntakeContinuationCheck || createCaseIntakeContinuationCheckState();
      return Boolean(
        this.caseIntakeContinuationAction === 'ready'
        || (state.checkedAtUtc && state.rawStageComplete && state.saplingStageComplete)
      );
    },

    caseAttributionWorkflowIntakeDataReady() {
      const rows = (this.caseIntakeRailWorkflowSteps || []).filter(row => row.dataBacked);
      return rows.length === 4 && rows.every(row => row.complete);
    },

    caseAttributionWorkflowRailVisible() {
      return Boolean(
        this.caseAttributionWorkflowRailEnabled
        && this.caseIntakeRailVisible
        && this.caseAttributionWorkflowIntakeDataReady
        && this.caseAttributionWorkflowPrerequisiteKnownComplete
      );
    },

    caseAttributionWorkflowStepRows() {
      const workflowSteps = (this.workflowGroupsView || []).flatMap(group => Array.isArray(group?.steps) ? group.steps : []);
      const runtime = this.caseAttributionWorkflowRun || createCaseAttributionWorkflowRunState();
      const runtimeCompletedTags = new Set((Array.isArray(runtime.completedTags) ? runtime.completedTags : []).map(tag => normalizeCell(tag).toUpperCase()).filter(Boolean));
      return CASE_ATTRIBUTION_WORKFLOW_STEP_DEFINITIONS.map((definition, index) => {
        const step = workflowSteps.find(candidate => this.caseAttributionWorkflowStepMatchesDefinition(candidate, definition)) || null;
        const recordedComplete = Boolean(step?.complete);
        const stale = Boolean(step?.stale);
        const complete = Boolean(recordedComplete && !stale);
        const refreshableStale = Boolean(recordedComplete && stale);
        const runtimeActive = Boolean(runtime.active && runtime.stageTag === definition.tag);
        const runtimeComplete = Boolean(complete || (runtime.active && runtimeCompletedTags.has(definition.tag)));
        const runtimeFailed = Boolean(!runtime.active && runtime.lastResult === 'error' && runtime.stageTag === definition.tag && !complete);
        const runtimeStopped = Boolean(!runtime.active && ['manual', 'stopped', 'timeout'].includes(runtime.lastResult) && runtime.stageTag === definition.tag && !complete);
        const statusLabel = complete
          ? 'Complete'
          : runtimeActive
            ? (runtime.message || 'Working…')
            : runtimeFailed
              ? 'Needs attention'
              : runtimeStopped
                ? (runtime.lastResult === 'manual' ? 'Needs review' : 'Stopped here')
                : stale
                  ? 'Needs refresh'
                  : 'Pending';
        return {
          ...definition,
          index,
          step,
          recordedComplete,
          complete,
          stale,
          refreshableStale,
          runtimeActive,
          runtimeComplete,
          runtimeFailed,
          runtimeStopped,
          statusLabel,
          runtimeStatusLabel: statusLabel,
          statusTitle: step
            ? `${definition.title}: ${statusLabel}.`
            : `${definition.title}: workflow definition not found.`,
          className: {
            'case-intake-continuation-step--complete': runtimeComplete,
            'case-intake-continuation-step--checking': runtimeActive,
            'case-intake-continuation-step--attention': !runtimeComplete && (runtimeStopped || (!runtime.active && !stale)),
            'case-intake-continuation-step--stale': !runtimeComplete && stale,
            'case-intake-continuation-step--error': runtimeFailed,
            'case-intake-step--complete': runtimeComplete,
            'case-intake-step--active': runtimeActive,
            'case-intake-step--error': runtimeFailed,
            'case-intake-step--stopped': runtimeStopped
          }
        };
      });
    },

    caseAttributionWorkflowDefinitionsReady() {
      const rows = this.caseAttributionWorkflowStepRows || [];
      return rows.length === CASE_ATTRIBUTION_WORKFLOW_STEP_DEFINITIONS.length && rows.every(row => !!row.step);
    },

    caseAttributionWorkflowAllComplete() {
      const rows = this.caseAttributionWorkflowStepRows || [];
      return this.caseAttributionWorkflowDefinitionsReady && rows.every(row => row.complete);
    },

    caseAttributionWorkflowCompletedCount() {
      return (this.caseAttributionWorkflowStepRows || []).filter(row => row.runtimeComplete).length;
    },

    caseAttributionWorkflowOtherAutomationActive() {
      const scopedRunActive = Boolean(this.caseAttributionWorkflowRun?.active);
      const scopedWorkflowBusy = scopedRunActive && Boolean(this.caseAutomation?.active || this.caseAutomation?.busy);
      return Boolean(
        this.caseCleanupWorkflowRun?.active
        || this.caseIntakePreparation?.active
        || (!scopedWorkflowBusy && (this.caseAutomation?.active || this.caseAutomation?.busy))
        || this.caseWorkflowBackgroundAutomationActive
        || this.caseManualStartViewportAutoActive
        || this.caseManualStartAllFlowsAutoActive
        || this.caseManualStartVisualizerRunning
        || this.tool68PreparationRunning
        || this.caseWorkflowRawDataTool88Running
        || this.caseWorkflowTool88RawRunning
        || this.caseHopSolverAutomationRunning
        || this.caseHopSolverRunning
        || this.caseHopSolverPlaceholderRunning
        || this.caseManualStartHopInspectorRunning
        || this.caseChecksRunning
        || this.caseGlobalOperation?.active
        || this.caseModalOperation?.active
        || this.networkLookupRunning
        || this.dateLookupRunning
        || this.mainPathLabelAssignRunning
        || this.addressAttributionRunning
        || this.pathOperationRunning
        || this.entityCuratorLoading
        || this.caseWorkflowWwwUsdReferenceAutoGuardRunning
        || this.caseIntakeContinuationSaplingPreparing
        || (this.caseIntakeContinuationCheck?.status === 'checking' && !scopedRunActive)
      );
    },

    caseAttributionWorkflowCanStart() {
      return Boolean(
        this.caseAttributionWorkflowRailVisible
        && this.caseAttributionWorkflowDefinitionsReady
        && !this.caseAttributionWorkflowAllComplete
        && !this.caseAttributionWorkflowRun?.active
        && !this.caseAttributionWorkflowOtherAutomationActive
        && !this.casePageHydrating
      );
    },

    caseAttributionWorkflowButtonLabel() {
      if (this.caseAttributionWorkflowRun?.active) return 'Completing attribution…';
      if (this.caseAttributionWorkflowRun?.lastResult === 'manual') return 'Continue attribution stages';
      if (['stopped', 'timeout', 'error'].includes(this.caseAttributionWorkflowRun?.lastResult)) return 'Retry attribution stages';
      return 'Complete attribution stages';
    },

    caseAttributionWorkflowReviewModeLabel() {
      return this.caseSkipResolvableReviewsEnabled
        ? 'Review handling: skip/rubber-stamp resolvable reviews'
        : 'Review handling: pause before any step that needs investigator review';
    },

    caseAttributionWorkflowStatusDetail() {
      const runtime = this.caseAttributionWorkflowRun || createCaseAttributionWorkflowRunState();
      if (runtime.active) return runtime.message || 'Working through attribution and path-decision stages.';
      if (!this.caseAttributionWorkflowDefinitionsReady) return 'Loading the six attribution and path-decision workflow stages…';
      if (this.caseAttributionWorkflowAllComplete) return 'All six attribution, path-decision, color, entity, external-review, and USD-reference stages are current.';
      if (!this.caseIntakeContinuationCheckIsCurrent && this.caseAttributionWorkflowPrerequisiteKnownComplete) {
        return 'Raw Data and graph were previously complete. Their focused status will be rechecked automatically before this guided run continues.';
      }
      if (runtime.lastMessage) return runtime.lastMessage;
      const pending = (this.caseAttributionWorkflowStepRows || []).filter(row => !row.complete).length;
      return `${pending} of the next six workflow stage${pending === 1 ? '' : 's'} need attention.`;
    },

    caseAttributionWorkflowButtonTitle() {
      if (this.casePageHydrating || !this.caseAttributionWorkflowDefinitionsReady) return 'The Workflow Assistant is still loading these stages.';
      if (this.caseAttributionWorkflowOtherAutomationActive) return 'Another Case Buddy automation or operation is running.';
      if (!this.caseAttributionWorkflowPrerequisiteKnownComplete) return 'Complete Raw Data gathering and graph preparation first.';
      if (this.caseAttributionWorkflowAllComplete) return 'These six workflow stages are already complete.';
      const reviewText = this.caseSkipResolvableReviewsEnabled
        ? 'Resolvable review-only stages will be skipped or rubber-stamped using the current workflow setting.'
        : 'The run will pause before the first stage that needs investigator review.';
      return `Run the existing workflow actions through top-level USD reference review, then stop before consistency and cleanup. ${reviewText}`;
    },

    caseAttributionWorkflowReadyHelp() {
      if (this.caseIntakeContinuationCheck?.status === 'checking') {
        return 'The six stages are complete; Case Buddy is reconfirming the earlier Raw Data and graph result.';
      }
      if (!this.caseIntakeContinuationCheckIsCurrent && this.caseAttributionWorkflowPrerequisiteKnownComplete) {
        return 'The six stages are complete. The next guided run will refresh earlier readiness before it changes anything.';
      }
      return 'The six stages are complete. Begin consistency, notes, legend, and cleanup when ready.';
    },

    caseAttributionWorkflowProgressPercent() {
      const rows = this.caseAttributionWorkflowStepRows || [];
      const completed = rows.filter(row => row.runtimeComplete).length;
      const activeBonus = rows.some(row => row.runtimeActive) ? 0.45 : 0;
      return Math.max(0, Math.min(100, Math.round(((completed + activeBonus) / Math.max(1, rows.length || 6)) * 100)));
    },

    caseAttributionWorkflowProgressStyle() {
      return { width: `${this.caseAttributionWorkflowProgressPercent}%` };
    },

    caseCleanupWorkflowRailEnabled() {
      return Boolean(CASE_CLEANUP_WORKFLOW_RAIL_ENABLED);
    },

    caseAttributionWorkflowRecordedAllComplete() {
      const rows = this.caseAttributionWorkflowStepRows || [];
      return this.caseAttributionWorkflowDefinitionsReady && rows.every(row => row.recordedComplete);
    },

    caseCleanupWorkflowStepRows() {
      const workflowSteps = (this.workflowGroupsView || []).flatMap(group => Array.isArray(group?.steps) ? group.steps : []);
      const runtime = this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState();
      const runtimeCompletedTags = new Set((Array.isArray(runtime.completedTags) ? runtime.completedTags : []).map(tag => normalizeCell(tag).toUpperCase()).filter(Boolean));
      return CASE_CLEANUP_WORKFLOW_STEP_DEFINITIONS.map((definition, index) => {
        const step = workflowSteps.find(candidate => this.caseCleanupWorkflowStepMatchesDefinition(candidate, definition)) || null;
        const recordedComplete = Boolean(step?.complete);
        const stale = Boolean(step?.stale);
        const complete = Boolean(recordedComplete && !stale);
        const refreshableStale = Boolean(recordedComplete && stale);
        const runtimeActive = Boolean(runtime.active && runtime.stageTag === definition.tag);
        const runtimeComplete = Boolean(complete || (runtime.active && runtimeCompletedTags.has(definition.tag)));
        const runtimeFailed = Boolean(!runtime.active && runtime.lastResult === 'error' && runtime.stageTag === definition.tag && !complete);
        const runtimeStopped = Boolean(!runtime.active && ['manual', 'stopped', 'timeout'].includes(runtime.lastResult) && runtime.stageTag === definition.tag && !complete);
        const statusLabel = complete
          ? 'Complete'
          : runtimeActive
            ? (runtime.message || 'Working…')
            : runtimeFailed
              ? 'Needs attention'
              : runtimeStopped
                ? (runtime.lastResult === 'manual' ? 'Needs review' : 'Stopped here')
                : stale
                  ? 'Needs refresh'
                  : 'Pending';
        return {
          ...definition,
          index,
          step,
          recordedComplete,
          complete,
          stale,
          refreshableStale,
          runtimeActive,
          runtimeComplete,
          runtimeFailed,
          runtimeStopped,
          statusLabel,
          runtimeStatusLabel: statusLabel,
          statusTitle: step
            ? `${definition.title}: ${statusLabel}.`
            : `${definition.title}: workflow definition not found.`,
          className: {
            'case-intake-continuation-step--complete': runtimeComplete,
            'case-intake-continuation-step--checking': runtimeActive,
            'case-intake-continuation-step--attention': !runtimeComplete && (runtimeStopped || (!runtime.active && !stale)),
            'case-intake-continuation-step--stale': !runtimeComplete && stale,
            'case-intake-continuation-step--error': runtimeFailed,
            'case-intake-step--complete': runtimeComplete,
            'case-intake-step--active': runtimeActive,
            'case-intake-step--error': runtimeFailed,
            'case-intake-step--stopped': runtimeStopped
          }
        };
      });
    },

    caseCleanupWorkflowDefinitionsReady() {
      const rows = this.caseCleanupWorkflowStepRows || [];
      return rows.length === CASE_CLEANUP_WORKFLOW_STEP_DEFINITIONS.length && rows.every(row => !!row.step);
    },

    caseCleanupWorkflowHasProgress() {
      const runtime = this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState();
      return Boolean(
        this.caseCleanupWorkflowEntered
        || runtime.active
        || runtime.startedAtMs
        || runtime.lastResult
        || (this.caseCleanupWorkflowStepRows || []).some(row => row.recordedComplete || row.stale)
      );
    },

    caseCleanupWorkflowAttributionKnownComplete() {
      return Boolean(
        this.caseAttributionWorkflowAllComplete
        || (this.caseAttributionWorkflowRecordedAllComplete && this.caseCleanupWorkflowHasProgress)
      );
    },

    caseCleanupWorkflowRailVisible() {
      return Boolean(
        this.caseCleanupWorkflowRailEnabled
        && this.caseIntakeRailVisible
        && this.caseAttributionWorkflowRailVisible
        && this.caseCleanupWorkflowAttributionKnownComplete
      );
    },

    caseCleanupWorkflowShouldShowPhase() {
      return Boolean(this.caseCleanupWorkflowRailVisible && this.caseCleanupWorkflowHasProgress);
    },

    caseCleanupWorkflowAllComplete() {
      const rows = this.caseCleanupWorkflowStepRows || [];
      return this.caseCleanupWorkflowDefinitionsReady && rows.every(row => row.complete);
    },

    caseCleanupWorkflowPrerequisitesCurrent() {
      return Boolean(
        this.caseAttributionWorkflowAllComplete
        && this.caseIntakeContinuationCheckIsCurrent
        && this.caseIntakeContinuationAction === 'ready'
      );
    },

    caseCleanupWorkflowReadyForDeliverables() {
      return Boolean(this.caseCleanupWorkflowAllComplete && this.caseCleanupWorkflowPrerequisitesCurrent);
    },

    caseCleanupWorkflowCompletedCount() {
      return (this.caseCleanupWorkflowStepRows || []).filter(row => row.runtimeComplete).length;
    },

    caseCleanupWorkflowOtherAutomationActive() {
      const scopedRunActive = Boolean(this.caseCleanupWorkflowRun?.active);
      const scopedWorkflowBusy = scopedRunActive && Boolean(this.caseAutomation?.active || this.caseAutomation?.busy);
      return Boolean(
        this.caseAttributionWorkflowRun?.active
        || this.caseIntakePreparation?.active
        || (!scopedWorkflowBusy && (this.caseAutomation?.active || this.caseAutomation?.busy))
        || this.caseWorkflowBackgroundAutomationActive
        || this.caseManualStartViewportAutoActive
        || this.caseManualStartAllFlowsAutoActive
        || this.caseManualStartVisualizerRunning
        || this.tool68PreparationRunning
        || this.caseWorkflowRawDataTool88Running
        || this.caseWorkflowTool88RawRunning
        || this.caseHopSolverAutomationRunning
        || this.caseHopSolverRunning
        || this.caseHopSolverPlaceholderRunning
        || this.caseManualStartHopInspectorRunning
        || this.caseChecksRunning
        || this.caseGlobalOperation?.active
        || this.caseModalOperation?.active
        || this.networkLookupRunning
        || this.dateLookupRunning
        || this.mainPathLabelAssignRunning
        || this.addressAttributionRunning
        || this.pathOperationRunning
        || this.entityCuratorLoading
        || this.caseWorkflowWwwUsdReferenceAutoGuardRunning
        || this.caseInconsistencyRunning
        || this.depositHotWalletRunning
        || this.knownEntityCasingRunning
        || this.duplicateHashLabelerRunning
        || this.noteHashRewriteRunning
        || this.addressLegendRunning
        || this.caseIntakeContinuationSaplingPreparing
        || (this.caseIntakeContinuationCheck?.status === 'checking' && !scopedRunActive)
      );
    },

    caseCleanupWorkflowCanStart() {
      return Boolean(
        this.caseCleanupWorkflowRailVisible
        && this.caseCleanupWorkflowDefinitionsReady
        && !this.caseCleanupWorkflowReadyForDeliverables
        && !this.caseCleanupWorkflowRun?.active
        && !this.caseCleanupWorkflowOtherAutomationActive
        && !this.casePageHydrating
      );
    },

    caseCleanupWorkflowButtonLabel() {
      const runtime = this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState();
      if (runtime.active) return 'Completing cleanup…';
      if (this.caseCleanupWorkflowAllComplete && !this.caseCleanupWorkflowReadyForDeliverables) return 'Refresh cleanup readiness';
      if (runtime.lastResult === 'manual') return 'Continue cleanup stages';
      if (['stopped', 'timeout', 'error'].includes(runtime.lastResult)) return 'Retry cleanup stages';
      if (!this.caseCleanupWorkflowHasProgress) return 'Begin consistency & cleanup';
      return 'Complete cleanup stages';
    },

    caseCleanupWorkflowReviewModeLabel() {
      return this.caseSkipResolvableReviewsEnabled
        ? 'Review handling: skip/rubber-stamp resolvable reviews'
        : 'Review handling: pause before any step that needs investigator review';
    },

    caseCleanupWorkflowStatusDetail() {
      const runtime = this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState();
      if (runtime.active) return runtime.message || 'Working through consistency, notes, legend, and cleanup.';
      if (!this.caseCleanupWorkflowDefinitionsReady) return 'Loading the seven consistency, notes, duplicate-hash, and legend workflow stages…';
      if (this.caseCleanupWorkflowReadyForDeliverables) return 'All seven cleanup stages and their earlier prerequisites are current.';
      if (this.caseCleanupWorkflowAllComplete) return 'The seven cleanup stages are complete. Run one bounded refresh to confirm earlier readiness before deliverable buildout.';
      if (runtime.lastMessage) return runtime.lastMessage;
      const pending = (this.caseCleanupWorkflowStepRows || []).filter(row => !row.complete).length;
      return `${pending} of the seven cleanup workflow stage${pending === 1 ? '' : 's'} need attention.`;
    },

    caseCleanupWorkflowButtonTitle() {
      if (this.casePageHydrating || !this.caseCleanupWorkflowDefinitionsReady) return 'The Workflow Assistant is still loading these stages.';
      if (this.caseCleanupWorkflowOtherAutomationActive) return 'Another Case Buddy automation or operation is running.';
      if (!this.caseCleanupWorkflowAttributionKnownComplete) return 'Complete attribution, path decisions, and top-level USD review first.';
      if (this.caseCleanupWorkflowReadyForDeliverables) return 'These seven workflow stages and their prerequisites are already current.';
      if (this.caseCleanupWorkflowAllComplete) return 'Refresh the bounded prerequisite and workflow snapshots. Completed cleanup tools will not rerun unless the refreshed checks identify a real issue.';
      const reviewText = this.caseSkipResolvableReviewsEnabled
        ? 'Resolvable review-only stages will be skipped or rubber-stamped using the current workflow setting.'
        : 'The run will pause before the first stage that needs investigator review.';
      return `Run the existing workflow actions through address-legend review, then stop before deliverable buildout. ${reviewText}`;
    },

    caseCleanupWorkflowReadyHelp() {
      return 'Case inconsistencies, note normalization, deposit-pattern review, known-entity casing, duplicate-hash labels, note backup/hash replacement, and the address legend are current.';
    },

    caseWorkflowAssistantOverallWorkflowComplete() {
      let workflowComplete = false;
      try {
        workflowComplete = Boolean(this.caseWorkflowAllStepsComplete?.());
      } catch {
        workflowComplete = false;
      }
      if (!workflowComplete) return false;

      const visualState = this.caseFlowVisualsStateView || {};
      if (visualState.canBuild && !visualState.reviewIsCurrent) return false;

      const packageState = this.caseFullReportPackageStateView || {};
      if (packageState.isReady === false || packageState.reviewIsCurrent === false) return false;
      if (packageState.visualsCanBuild && packageState.visualsReviewIsCurrent === false && !visualState.reviewIsCurrent) return false;
      return true;
    },

    caseCleanupWorkflowAssistantHeading() {
      if (!this.caseCleanupWorkflowReadyForDeliverables) return 'Consistency, notes, legend & cleanup';
      return this.caseWorkflowAssistantOverallWorkflowComplete
        ? 'Case workflow complete'
        : 'Deliverable buildout & shipping';
    },

    caseWorkflowAssistantFinalReportReadyTitle() {
      return this.caseWorkflowAssistantOverallWorkflowComplete
        ? 'Final report package ready'
        : 'Case complete';
    },

    caseWorkflowAssistantFinalReportReadyHelp() {
      return this.caseWorkflowAssistantOverallWorkflowComplete
        ? 'All workflow steps are current. Open the one-click report package.'
        : 'Open the one-click report package to review the report.';
    },

    caseWorkflowAssistantFinalReportButtonLabel() {
      return this.caseWorkflowAssistantOverallWorkflowComplete
        ? 'Open final report'
        : 'Open one-click report';
    },

    caseWorkflowAssistantFinalReportButtonHelp() {
      return this.caseWorkflowAssistantOverallWorkflowComplete
        ? 'Review, copy, or download the one-click package'
        : 'Visuals, summaries, traces, all in one report';
    },

    caseWorkflowAssistantFinalReportCanOpen() {
      return Boolean(
        this.caseCleanupWorkflowReadyForDeliverables
        && !this.caseCleanupWorkflowRun?.active
        && !this.caseCleanupWorkflowOtherAutomationActive
        && !this.caseFullReportPackageRefreshRunning
        && !this.caseFlowVisualsDownloading
        && !this.casePageHydrating
      );
    },

    caseWorkflowAssistantFinalReportButtonTitle() {
      if (this.casePageHydrating) return 'The case is still loading.';
      if (this.caseCleanupWorkflowOtherAutomationActive || this.caseFullReportPackageRefreshRunning || this.caseFlowVisualsDownloading) {
        return 'Another Case Buddy operation is running.';
      }
      return this.caseWorkflowAssistantOverallWorkflowComplete
        ? 'Open the current one-click report package.'
        : 'Open the current one-click package. The full workflow remains the source of truth for unfinished deliverables.';
    },

    caseCleanupWorkflowProgressPercent() {
      const rows = this.caseCleanupWorkflowStepRows || [];
      const completed = rows.filter(row => row.runtimeComplete).length;
      const activeBonus = rows.some(row => row.runtimeActive) ? 0.45 : 0;
      return Math.max(0, Math.min(100, Math.round(((completed + activeBonus) / Math.max(1, rows.length || 7)) * 100)));
    },

    caseCleanupWorkflowProgressStyle() {
      return { width: `${this.caseCleanupWorkflowProgressPercent}%` };
    },


    caseIntakeRailOtherAutomationActive() {
      const intakeActive = Boolean(this.caseIntakePreparation?.active);
      const intakeOwnedStageBusy = intakeActive && Boolean(
        this.networkLookupRunning || this.dateLookupRunning || this.mainPathLabelAssignRunning
      );
      return Boolean(
        this.caseCleanupWorkflowRun?.active
        || this.caseAttributionWorkflowRun?.active
        || this.caseAutomation?.active
        || this.caseAutomation?.busy
        || this.caseWorkflowBackgroundAutomationActive
        || this.caseManualStartViewportAutoActive
        || this.caseManualStartAllFlowsAutoActive
        || this.caseManualStartVisualizerRunning
        || this.tool68PreparationRunning
        || this.caseWorkflowRawDataTool88Running
        || this.caseWorkflowTool88RawRunning
        || this.caseHopSolverAutomationRunning
        || this.caseHopSolverRunning
        || this.caseHopSolverPlaceholderRunning
        || this.caseManualStartHopInspectorRunning
        || this.caseChecksRunning
        || this.caseGlobalOperation?.active
        || this.caseModalOperation?.active
        || (!intakeOwnedStageBusy && (this.networkLookupRunning || this.dateLookupRunning || this.mainPathLabelAssignRunning))
      );
    },

    caseIntakeRailCanStart() {
      return Boolean(
        this.caseIntakeRailVisible
        && this.caseIntakeRailWorkflowReady
        && !this.caseIntakeRailAllComplete
        && !this.caseIntakePreparation?.active
        && !this.caseIntakeRailOtherAutomationActive
        && !this.casePageHydrating
      );
    },

    caseIntakeRailButtonTitle() {
      if (this.caseIntakePreparation?.active) return this.caseIntakePreparation.message || 'Case preparation is running.';
      if (this.casePageHydrating || !this.caseIntakeRailWorkflowReady) return 'The workflow assistant is still loading.';
      if (this.caseIntakeRailOtherAutomationActive) return 'Another Case Buddy automation or operation is running.';
      if (this.caseIntakeRailAllComplete) return 'The first five workflow steps are already complete.';
      return 'Complete the first five workflow steps: confirm starting rows, identify networks, fill and sort UTC dates, assign any missing main path labels, and record the early case review.';
    },

    caseIntakeRailStepRows() {
      const runtime = this.caseIntakePreparation || createCaseIntakePreparationState();
      const completedKeys = new Set(Array.isArray(runtime.completedKeys) ? runtime.completedKeys : []);
      return (this.caseIntakeRailWorkflowSteps || []).map((step) => {
        const runtimeActive = Boolean(runtime.active && runtime.stageKey === step.key);
        const runtimeComplete = Boolean(step.complete || (runtime.active && completedKeys.has(step.key)));
        const runtimeFailed = Boolean(!runtime.active && runtime.lastResult === 'error' && runtime.stageKey === step.key && !runtimeComplete);
        const runtimeStopped = Boolean(!runtime.active && ['stopped', 'timeout'].includes(runtime.lastResult) && runtime.stageKey === step.key && !runtimeComplete);
        const runtimeStatusLabel = runtimeComplete
          ? 'Complete'
          : runtimeActive
            ? (runtime.message || 'Working…')
            : runtimeFailed
              ? 'Needs attention'
              : runtimeStopped
                ? 'Stopped here'
                : step.stale
                  ? 'Stale'
                  : 'Pending';
        return {
          ...step,
          runtimeActive,
          runtimeComplete,
          runtimeFailed,
          runtimeStopped,
          runtimeStatusLabel,
          className: {
            'case-intake-step--complete': runtimeComplete,
            'case-intake-step--active': runtimeActive,
            'case-intake-step--error': runtimeFailed,
            'case-intake-step--stopped': runtimeStopped
          }
        };
      });
    },

    caseIntakeRailStatusDetail() {
      if (this.caseIntakePreparation?.active) {
        return this.caseIntakePreparation.message || 'Preparing the first five workflow steps.';
      }
      if (!this.caseIntakeRailWorkflowReady) {
        return 'Loading the first five workflow steps…';
      }
      if (this.caseIntakeRailAllComplete) {
        return 'Current Main Data confirms networks, UTC dates, root order, and main labels; the early review is current.';
      }
      if (this.caseIntakeRailOtherAutomationActive) {
        return 'Waiting for the current Case Buddy operation to finish.';
      }
      if (this.caseIntakePreparation?.lastMessage) return this.caseIntakePreparation.lastMessage;
      const pending = (this.caseIntakeRailWorkflowSteps || []).filter(step => !step.complete).length;
      return `${pending} of the first five workflow step${pending === 1 ? '' : 's'} need attention.`;
    },

    caseIntakePreparationProgressPercent() {
      const rows = this.caseIntakeRailStepRows || [];
      const completed = rows.filter(row => row.runtimeComplete).length;
      const activeBonus = rows.some(row => row.runtimeActive) ? 0.45 : 0;
      return Math.max(0, Math.min(100, Math.round(((completed + activeBonus) / Math.max(1, rows.length || 5)) * 100)));
    },

    caseIntakePreparationProgressStyle() {
      return { width: `${this.caseIntakePreparationProgressPercent}%` };
    },

    activeWorkflowStepHelp() {
      return this.getWorkflowStepHelpView(this.activeWorkflowStepHelpTag);
    },

    casePageLoadingPercent() {
      let percent = 18;
      if (this.currentProjectFileId) percent += 22;
      if (this.projectFileReadyState.input) percent += 30;
      if (this.projectFileReadyState.output) percent += 22;
      if (!this.casePageHydrating) percent = 100;
      return Math.max(0, Math.min(100, percent));
    },

    casePageLoadingProgressStyle() {
      return { width: `${this.casePageLoadingPercent}%` };
    },

    caseChecksRunning() {
      return !!(this.caseGlobalOperation.active && this.caseGlobalOperation.kind === 'workflow-checks');
    },

    caseWorkflowRunChecksLabel() {
      return this.caseChecksRunning ? 'Running checks…' : 'Run checks';
    },

    caseAutomationActionDisabled() {
      return !!(this.caseIntakePreparation?.active || this.caseManualStartViewportAutoActive || this.caseManualStartAllFlowsAutoActive || this.caseAutomation.busy || this.caseAutomation.active || this.caseWorkflowBackgroundAutomationActive || this.caseChecksRunning || this.caseGlobalOperation?.active || this.caseModalOperation?.active);
    },

    caseAutomationNextButtonLabel() {
      if (this.caseAutomation.busy) return 'Working…';
      if (this.caseAutomation.paused) return 'Paused';
      return 'Next';
    },

    caseAutomationAutoButtonLabel() {
      if (this.caseAutomation.active) return this.caseAutomation.paused ? 'Stop Auto' : 'Stop Auto';
      return 'Auto';
    },

    caseAutomationSpeedLabel() {
      const speed = Number(this.caseAutomation.speedMultiplier) || 1;
      return `${speed.toFixed(speed % 1 === 0 ? 0 : 2)}×`;
    },

    caseAutomationControlsClass() {
      return {
        'case-automation-controls--active': !!this.caseAutomation.active,
        'case-automation-controls--paused': !!this.caseAutomation.paused,
        'case-automation-controls--busy': !!this.caseAutomation.busy
      };
    },

    caseAutomationPanelClass() {
      return {
        'case-automation-panel--active': !!this.caseAutomation.active,
        'case-automation-panel--paused': !!this.caseAutomation.paused,
        'case-automation-panel--busy': !!this.caseAutomation.busy
      };
    },

    caseAutomationFailsafeClass() {
      return {
        'case-automation-failsafe--paused': !!this.caseAutomation.paused,
        'case-automation-failsafe--busy': !!this.caseAutomation.busy
      };
    },

    caseAutomationNextSummary() {
      if (!this.recommendedWorkflowStep) return 'No next workflow step detected.';
      return `Next step: ${this.recommendedWorkflowStep.title}`;
    },

    caseAutomationReviewModeLabel() {
      return this.caseAutomation.skipResolvableReview
        ? 'Review mode: 100% Automated Mode'
        : 'Review mode: pause for manual reviews';
    },

    caseAutomationRecentLog() {
      return Array.isArray(this.caseAutomation.log) ? this.caseAutomation.log.slice(0, 6) : [];
    },

    caseWorkspaceDarkModeActive() {
      return Boolean(
        this.caseWorkspaceDarkModeEnabled
          && this.caseBuddyPresentationMode === CASE_BUDDY_PRESENTATION_MODE_WORKSPACE
      );
    },

    caseQuickLookupVisible() {
      return Boolean(
        this.caseQuickLookupEnabled
          && this.caseBuddyPresentationMode === CASE_BUDDY_PRESENTATION_MODE_LOOKUP
          && !this.casePageHydrating
      );
    },

    caseQuickLookupOwnsWorkflowCompletion() {
      return Boolean(
        this.caseQuickLookupEnabled
          && this.caseBuddyPresentationMode === CASE_BUDDY_PRESENTATION_MODE_LOOKUP
      );
    },

    caseQuickLookupBehindModal() {
      return Boolean(
        this.caseAnyModalVisible
          || this.showCaseAutomationPauseModal
          || this.caseManualStartVisualizerOpening?.active
          || this.showCaseInvestigationVisualizerModal
          || this.showCaseFileBrowserModal
          || this.showCaseFullReportPackageModal
          || (this.showCaseWorkflowCompletionModal && !this.caseQuickLookupOwnsWorkflowCompletion)
          || this.showStartingMainTransactionsModal
          || this.showNetworkLookupModal
          || this.caseAutomationShowcaseOverlayVisible
      );
    },

    caseQuickLookupBusy() {
      const status = String(this.caseQuickLookup?.status || '');
      const localBusy = ['creating-case', 'validating', 'adding', 'network', 'hydrating', 'applying-choice', 'opening', 'report'].includes(status);
      return Boolean(
        localBusy
          || this.caseQuickLookupCaseSwitchBusy
          || this.caseFullReportPackagePdfPreparing
          || this.caseQuickLookupEnsureCasePromise
          || this.caseManualStartVisualizerOpening?.active
          || this.caseQuickLookup?.manualSolveLaunched
          || (this.caseQuickLookup?.reportRequested && (this.caseAutomation?.active || this.caseAutomation?.busy))
      );
    },

    caseQuickLookupCanSubmit() {
      return Boolean(
        this.caseQuickLookupHashDraft
          && !this.caseQuickLookupBusy
          && !this.caseAutomation?.active
          && !this.caseAutomation?.busy
          && !this.caseManualStartAllFlowsAutoActive
          && !this.caseManualStartViewportAutoActive
      );
    },

    caseQuickLookupCanCancel() {
      return Boolean(
        this.caseQuickLookupAbortController
          && ['creating-case', 'validating', 'adding', 'network', 'hydrating'].includes(String(this.caseQuickLookup?.status || ''))
      );
    },

    caseQuickLookupShowsInput() {
      return ['idle', 'error'].includes(String(this.caseQuickLookup?.status || 'idle'));
    },

    caseQuickLookupShowsResult() {
      return ['complete', 'partial', 'report-ready'].includes(String(this.caseQuickLookup?.status || ''));
    },

    caseQuickLookupChoiceRows() {
      return Array.isArray(this.caseQuickLookup?.choiceRows) ? this.caseQuickLookup.choiceRows : [];
    },

    caseQuickLookupChoiceFilterActive() {
      return Boolean(this.caseManualNormalizeText?.(this.caseQuickLookupChoiceFilter) || '');
    },

    caseQuickLookupChoiceFilterVisible() {
      // Every ambiguous choice list should be searchable. A transaction with
      // only two to eight BTC outputs can still be difficult to resolve when
      // the investigator knows an address or amount rather than its position.
      return this.caseQuickLookupChoiceRows.length > 1;
    },

    caseQuickLookupFilteredChoiceRows() {
      const rows = this.caseQuickLookupChoiceRows;
      if (!this.caseQuickLookupChoiceFilterActive) return rows;
      return rows.filter(choice => this.caseQuickLookupChoiceMatchesFilter(choice, this.caseQuickLookupChoiceFilter));
    },

    caseQuickLookupSelectedChoice() {
      const selectedKey = this.caseManualNormalizeText?.(this.caseQuickLookup?.selectedChoiceKey) || '';
      const visibleRows = this.caseQuickLookupChoiceFilterActive
        ? this.caseQuickLookupFilteredChoiceRows
        : this.caseQuickLookupChoiceRows;
      return visibleRows.find(choice => choice?.key === selectedKey) || null;
    },

    caseQuickLookupChoiceTitle() {
      const family = String(this.caseQuickLookup?.choiceFamily || '').toLowerCase();
      const direction = String(this.caseQuickLookup?.choiceDirection || 'forward').toLowerCase();
      if (family === 'btc') return direction === 'reverse' ? 'Choose the Bitcoin input to trace' : 'Choose the Bitcoin output to follow';
      return direction === 'reverse' ? 'Choose the source transfer' : 'Choose the transfer to follow';
    },

    caseQuickLookupChoiceFilterNoun() {
      const family = String(this.caseQuickLookup?.choiceFamily || '').toLowerCase();
      const direction = String(this.caseQuickLookup?.choiceDirection || 'forward').toLowerCase();
      if (family === 'btc') return direction === 'reverse' ? 'input' : 'output';
      return 'transfer';
    },

    caseQuickLookupChoiceFilterPlaceholder() {
      const family = String(this.caseQuickLookup?.choiceFamily || '').toLowerCase();
      if (family === 'btc') {
        return this.caseQuickLookup?.choiceDirection === 'reverse'
          ? 'Filter by address, BTC amount, input number, or prior transaction hash'
          : 'Filter by address, BTC amount, output number, or transaction hash';
      }
      return 'Filter transfers by address, amount, token, or type';
    },

    caseQuickLookupChoiceFilterEmptyHint() {
      const family = String(this.caseQuickLookup?.choiceFamily || '').toLowerCase();
      const direction = String(this.caseQuickLookup?.choiceDirection || 'forward').toLowerCase();
      if (family === 'btc') {
        return direction === 'reverse'
          ? 'Try part of the address, an amount, an input number, or a prior transaction hash.'
          : 'Try part of the address, an amount, or an output number.';
      }
      return 'Try part of an address, an amount, a token, or a transfer type.';
    },

    caseQuickLookupChoiceFilterSummary() {
      const shown = this.caseQuickLookupFilteredChoiceRows.length;
      const total = this.caseQuickLookupChoiceRows.length;
      const noun = `${this.caseQuickLookupChoiceFilterNoun}${total === 1 ? '' : 's'}`;
      return this.caseQuickLookupChoiceFilterActive
        ? `${shown} of ${total} ${noun} shown`
        : `${total} ${noun} available`;
    },

    caseQuickLookupStatusEyebrow() {
      const status = String(this.caseQuickLookup?.status || '');
      if (status === 'report') return 'BUILDING REPORT';
      if (status === 'opening') return 'FLOW SOLVER';
      if (status === 'network') return 'IDENTIFYING NETWORK';
      return 'TRANSACTION LOOKUP';
    },

    caseQuickLookupStatusTitle() {
      const status = String(this.caseQuickLookup?.status || 'idle');
      if (status === 'creating-case') return 'Creating a case';
      if (status === 'validating') return 'Checking the transaction hash';
      if (status === 'adding') return 'Adding the transaction';
      if (status === 'network') return 'Finding the blockchain network';
      if (status === 'hydrating') return 'Reading the transaction';
      if (status === 'applying-choice') return 'Saving your selection';
      if (status === 'opening') return 'Opening the Flow Solver';
      if (status === 'report') return 'Building the full report';
      if (status === 'report-ready') return 'Your report is ready';
      if (status === 'complete') return this.caseQuickLookup?.lastResult === 'qualified-complete' ? 'Flow ready for a qualified report' : 'Transaction flow complete';
      if (status === 'partial') return 'The flow needs a closer look';
      if (status === 'error') return 'Case Buddy could not start this lookup';
      return 'Follow a blockchain transaction';
    },

    caseQuickLookupProgressStyle() {
      const value = Math.max(0, Math.min(100, Number(this.caseQuickLookup?.progress) || 0));
      return { width: `${value}%` };
    },

    caseQuickLookupHashPreview() {
      const hash = this.caseManualNormalizeText?.(this.caseQuickLookup?.activeHash) || '';
      return hash ? this.caseManualShorten?.(hash, 18, 14) || hash : '';
    },

    caseQuickLookupCurrentCaseLabel() {
      if (this.caseQuickLookup?.status === 'creating-case') return 'Creating case…';
      return this.currentProjectFileName || 'Current case';
    },

    caseQuickLookupCaseSwitcherRows() {
      const rows = Array.isArray(this.projectFiles) ? this.projectFiles.slice() : [];
      return rows.sort((left, right) => {
        const leftCurrent = left?.id === this.currentProjectFileId ? 1 : 0;
        const rightCurrent = right?.id === this.currentProjectFileId ? 1 : 0;
        if (leftCurrent !== rightCurrent) return rightCurrent - leftCurrent;
        const leftUpdated = new Date(left?.updatedAt || 0).getTime() || 0;
        const rightUpdated = new Date(right?.updatedAt || 0).getTime() || 0;
        if (leftUpdated !== rightUpdated) return rightUpdated - leftUpdated;
        return String(left?.name || '').localeCompare(String(right?.name || ''));
      });
    },

    caseQuickLookupCanCreateReport() {
      return Boolean(this.caseQuickLookup?.status === 'complete' && !this.caseWorkflowAssistantOverallWorkflowComplete);
    },

    caseQuickLookupCanAcceptReportBoundaries() {
      return Boolean(
        this.caseQuickLookup?.status === 'partial'
          && Number(this.caseQuickLookup?.manualReviewCount || 0) > 0
          && Number(this.caseQuickLookup?.unresolvedCount || 0) === 0
          && Number(this.caseQuickLookup?.choiceCount || 0) === 0
          && Number(this.caseQuickLookup?.reportBoundaryEligibleCount || 0) === Number(this.caseQuickLookup?.manualReviewCount || 0)
          && !this.caseQuickLookupBusy
      );
    },

    caseQuickLookupAcceptReportBoundariesLabel() {
      const count = Math.max(0, Number(this.caseQuickLookup?.manualReviewCount || 0));
      return count === 1
        ? 'Use this ending in the report'
        : `Use these ${count} endings in the report`;
    },

    caseManualStartQuickLookupMode() {
      return Boolean(
        this.showCaseInvestigationVisualizerModal
          && this.caseInvestigationVisualizerManualStartMode
          && this.caseManualStartLaunchSource === 'quick-lookup'
      );
    },

    caseManualStartQuickLookupStatusVisible() {
      const status = normalizeCell(this.caseInvestigationVisualizerStatus);
      if (!status) return false;
      if (!this.caseManualStartQuickLookupMode) return true;

      const normalizedStatus = status.toLowerCase();
      const stateSummary = normalizeCell(this.caseInvestigationVisualizerStateView?.summary).toLowerCase();
      if (stateSummary && normalizedStatus === stateSummary) return false;
      if (/^\d+\s+qualifying visual/u.test(normalizedStatus)) return false;
      if (/^\d+\s+starting transactions?\s+ready/u.test(normalizedStatus)) return false;
      if (/^(?:opened|reopened) manual solve\b/u.test(normalizedStatus)) return false;
      if (/^manual solve auto pressed next \d+ times? in this viewport\.?$/u.test(normalizedStatus)) return false;
      return true;
    },

    caseQuickLookupManualSolveCompletionReady() {
      return Boolean(
        this.caseQuickLookup?.manualSolveLaunched
          && this.showCaseInvestigationVisualizerModal
          && this.caseInvestigationVisualizerManualStartMode
          && this.caseManualStartViewportCompletionEffect?.visible
      );
    },

    caseQuickLookupManualSolveAssessment() {
      if (!this.caseQuickLookupManualSolveCompletionReady || !this.caseQuickLookup?.activeHash) return null;
      return this.assessCaseQuickLookupActiveFlow?.(this.caseQuickLookup.activeHash)?.assessment || null;
    },

    caseQuickLookupManualSolveHasManualReviewOnly() {
      const assessment = this.caseQuickLookupManualSolveAssessment;
      return Boolean(
        assessment
          && Number(assessment.manualReviewCount || 0) > 0
          && Number(assessment.unresolvedCount || 0) === 0
          && Number(assessment.choiceCount || 0) === 0
      );
    },

    caseManualStartViewportCompletionEffectTitle() {
      const assessment = this.caseQuickLookupManualSolveAssessment;
      if (assessment?.manualReviewCount) return 'Flow Solver pass complete';
      if (assessment?.acceptedReportBoundaryCount) return 'Flow ready with report boundaries';
      return 'Manual Solve flow complete';
    },

    caseSimpleLaunchFileOptions() {
      return Array.isArray(this.projectFiles) ? this.projectFiles : [];
    },

    caseSkipResolvableReviewsEnabled: {
      get() {
        return Boolean(this.caseAutomation?.skipResolvableReview);
      },
      set(value) {
        this.setCaseSkipResolvableReviewsEnabled?.(value);
      }
    },

    caseSimpleLaunchHasCase() {
      return Boolean(this.currentProjectFileId && !this.casePageHydrating);
    },

    caseSimpleLaunchStartingTransactionState() {
      const state = this.getStartingMainTransactionConfirmationState?.() || {};
      const count = Number(state.count) || 0;
      return {
        ...state,
        count,
        hasStartingTransactions: Boolean(state.hasStartingTransactions || count > 0)
      };
    },

    caseSimpleLaunchHasStartingTransactions() {
      return Boolean(this.caseSimpleLaunchStartingTransactionState?.hasStartingTransactions);
    },

    caseSimpleLaunchCanLoadStartingTransactions() {
      return Boolean(this.caseSimpleLaunchHasCase);
    },

    caseSimpleLaunchCanRunAuto() {
      return Boolean(this.caseSimpleLaunchHasCase && this.caseSimpleLaunchHasStartingTransactions);
    },

    caseSimpleLaunchReportReady() {
      return !!(this.workflowProgress?.total && Number(this.workflowProgress.completed) >= Number(this.workflowProgress.total));
    },

    caseWorkflowBackgroundAutomationActive() {
      return !!(
        this.caseAutomation?.busy ||
        this.caseWorkflowWwwUsdReferenceAutoGuardRunning ||
        this.caseWorkflowWwwUsdReferenceAutoGuardTimer ||
        this.caseWorkflowWwwUsdReferenceAutoResumeTimer ||
        this.caseWorkflowStatefulGeneratedReviewAutoGuardRunning ||
        this.caseWorkflowStatefulGeneratedReviewAutoGuardTimer ||
        this.caseWorkflowStatefulGeneratedReviewAutoResumeTimer
      );
    },

    caseSimpleLaunchVisible() {
      const backgroundOnlyStart = this.caseWorkflowBackgroundAutomationActive && !this.caseSimpleLaunchAutoStarting;
      return !!(
        this.caseSimpleLaunchEnabled &&
        this.caseBuddyPresentationMode === CASE_BUDDY_PRESENTATION_MODE_SIMPLE &&
        !this.caseSimpleLaunchDismissed &&
        !this.caseSimpleLaunchMinimized &&
        !this.caseAutomation.active &&
        !this.caseAutomation.busy &&
        !this.caseWorkflowRawDataTool88Running &&
        !this.caseWorkflowTool88RawRunning &&
        !backgroundOnlyStart &&
        !this.casePageHydrating
      );
    },

    shouldShowEmptyCaseStarterNudge() {
      return !!(
        this.emptyCaseStarterNudgeEnabled &&
        !this.caseAnyModalVisible &&
        !this.caseBuddyUserPreferences?.emptyCaseStarterNudgeDisabled &&
        !this.emptyCaseStarterNudgeDismissedForCurrentCase() &&
        this.activeCaseLoadedForEmptyCaseStarterNudge() &&
        this.emptyCaseStarterNudgeInFullCaseView() &&
        this.isActiveInputSheetMainLikeForEmptyCaseStarterNudge() &&
        !this.hasMainInputTransactionEntries() &&
        !this.hasCaseInvestigationStartedForEmptyCaseStarterNudge() &&
        !this.caseAutomation?.active &&
        !this.caseAutomation?.busy &&
        !this.caseManualStartViewportAutoActive &&
        !this.caseManualStartAllFlowsAutoActive
      );
    },

    caseSimpleLaunchMiniVisible() {
      return !!(
        this.caseQuickLookupEnabled &&
        this.caseBuddyPresentationMode === CASE_BUDDY_PRESENTATION_MODE_WORKSPACE &&
        !this.caseAutomation.active &&
        !this.caseWorkflowBackgroundAutomationActive
      );
    },

    caseSimpleLaunchStartingRowsLabel() {
      if (!this.caseSimpleLaunchHasCase) return 'Create or choose a case first.';
      const count = Number(this.caseSimpleLaunchStartingTransactionState?.count) || 0;
      if (count <= 0) return 'No starting rows confirmed yet.';
      return `${count} starting row${count === 1 ? '' : 's'} detected in Main Data.`;
    },

    caseSimpleLaunchReportLabel() {
      if (this.caseSimpleLaunchReportReady) {
        return 'Report package is ready. Click to open it.';
      }
      return 'The final report opens when Auto completes.';
    },

    caseSimpleLaunchStatusTitle() {
      if (this.caseSimpleLaunchAutoStarting) return 'Starting Auto';
      if (this.caseAutomation.active || this.caseWorkflowBackgroundAutomationActive) return 'Auto is running';
      if (this.caseSimpleLaunchReportReady) return 'Report is ready';
      if (!this.caseSimpleLaunchHasCase) return 'Create or choose a case';
      if (!this.caseSimpleLaunchHasStartingTransactions) return 'Load starting transactions';
      return 'Ready for a guided run';
    },

    caseSimpleLaunchStatusDetail() {
      if (!this.caseSimpleLaunchHasCase) return 'Create a new case or choose a saved case to begin.';
      if (!this.caseSimpleLaunchHasStartingTransactions) return 'Load starting transactions into Main Data before running Auto.';
      return this.caseSimpleLaunchStatus || 'Use the buttons above to run Auto or open the full workspace.';
    },

    caseAutomationShowcaseAvailable() {
      return !!(
        this.caseAutomationShowcaseOverlayEnabled &&
        (this.caseAutomation.active || this.caseWorkflowBackgroundAutomationActive) &&
        !this.caseAutomation.paused
      );
    },

    caseAutomationShowcaseOverlayVisible() {
      return !!(this.caseAutomationShowcaseAvailable && !this.caseAutomationShowcaseOverlayMinimized);
    },

    caseAutomationShowcaseMiniVisible() {
      return !!(this.caseAutomationShowcaseAvailable && this.caseAutomationShowcaseOverlayMinimized);
    },

    caseAutomationShowcaseStageLabel() {
      return this.caseAutomation.lastPlan?.stepTitle || this.recommendedWorkflowStepTitle || 'Working through the case workflow';
    },

    caseAutomationShowcaseSupportLabel() {
      if (this.caseAutomationShowcaseTool88Visible) return 'Tool 88 is resolving the raw-data flow; live details are shown in the solver panel.';
      if (this.caseChecksRunning) return 'Refreshing workflow checks and current status.';
      if (this.caseModalOperation?.active) return this.caseModalOperation.title || 'Preparing a review panel.';
      if (this.caseGlobalOperation?.active) return this.caseGlobalOperation.title || 'Running a case operation.';
      if (this.caseAutomation.busy) return 'Running the next safe action.';
      return 'Waiting for the next safe action to begin.';
    },

    caseAutomationShowcaseProgressPercent() {
      const total = Math.max(1, Number(this.workflowProgress?.total) || 1);
      const completed = Math.max(0, Number(this.workflowProgress?.completed) || 0);
      return Math.max(0, Math.min(100, Math.round((completed / total) * 100)));
    },

    caseAutomationShowcaseProgressLabel() {
      const total = Number(this.workflowProgress?.total) || 0;
      const completed = Number(this.workflowProgress?.completed) || 0;
      return total > 0 ? `${completed} / ${total} steps` : 'Preparing workflow';
    },

    caseAutomationShowcaseProgressStyle() {
      return { width: `${this.caseAutomationShowcaseProgressPercent}%` };
    },
    caseAutomationShowcaseVisualStyle() {
      const progress = Math.max(0, Math.min(100, Number(this.caseAutomationShowcaseProgressPercent) || 0));
      const completed = Math.max(0, Number(this.workflowProgress?.completed) || 0);
      const tool88Progress = Math.max(0, Math.min(100, Number(this.caseAutomationShowcaseTool88ProgressPercent) || 0));
      const tool88Steps = Math.max(0, Number(this.caseAutomationTool88Activity?.stepCount) || 0);
      const tool88Rows = Math.max(0, Number(this.caseAutomationTool88Activity?.rawRows || this.caseAutomationTool88Activity?.rowsAppended) || 0);
      return {
        '--case-auto-progress': `${progress}%`,
        '--case-auto-progress-unit': String(progress / 100),
        '--case-auto-completed-steps': String(completed),
        '--case-auto-tool88-progress': `${tool88Progress}%`,
        '--case-auto-tool88-progress-unit': String(tool88Progress / 100),
        '--case-auto-tool88-steps': String(tool88Steps),
        '--case-auto-tool88-rows': String(tool88Rows)
      };
    },

    caseAutomationShowcaseVisualClass() {
      const progress = Math.max(0, Math.min(100, Number(this.caseAutomationShowcaseProgressPercent) || 0));
      const stageText = `${this.caseAutomation.lastPlan?.stepTitle || ''} ${this.caseAutomation.status || ''} ${this.caseAutomationTool88Activity?.status || ''} ${this.caseAutomationTool88Activity?.message || ''}`.toLowerCase();
      return {
        'case-automation-showcase__visual--early': progress < 35,
        'case-automation-showcase__visual--middle': progress >= 35 && progress < 70,
        'case-automation-showcase__visual--late': progress >= 70,
        'case-automation-showcase__visual--handoff': /wayfinder|raw data|handoff|import|tool\s*88|hop solver|solve hop/.test(stageText),
        'case-automation-showcase__visual--tool88': this.caseAutomationShowcaseTool88Visible,
        'case-automation-showcase__visual--deliverables': /deliverable|report|package|visual|addendum|conclusion|what went where/.test(stageText)
      };
    },

    caseAutomationShowcaseConstellationNodes() {
      const phases = this.caseAutomationShowcasePhases || [];
      return phases.map((phase, index) => ({
        id: phase.id || `showcase-node-${index}`,
        className: {
          'case-automation-showcase__constellation-node': true,
          'case-automation-showcase__constellation-node--done': !!phase.className?.['case-automation-showcase__phase--done'],
          'case-automation-showcase__constellation-node--active': !!phase.className?.['case-automation-showcase__phase--active'],
          [`case-automation-showcase__constellation-node--${index + 1}`]: true
        }
      }));
    },


    caseAutomationShowcaseRawFetchIsCurrent() {
      if (this.caseWorkflowAllStepsComplete?.()) return false;
      const plan = this.caseAutomation.lastPlan || {};
      const recommended = this.recommendedWorkflowStep || {};
      const tag = String(plan.stepTag || recommended.tag || '').toUpperCase();
      if (tag === 'RAW_DATA_FETCH') return true;
      if (this.isCaseWorkflowRawDataFetchStep?.(recommended) || this.isCaseWorkflowRawDataFetchStep?.(plan)) return true;
      const rawSolverIsLive = !!(
        this.caseWorkflowRawDataTool88Running ||
        this.caseWorkflowTool88RawRunning ||
        this.caseAutomationTool88Activity?.active ||
        this.caseAutomationTool88Progress?.active
      );
      if (rawSolverIsLive) return true;
      const sourceText = [
        plan.stepTag,
        plan.stepTitle,
        recommended.tag,
        recommended.title,
        this.caseAutomation.manualPause?.stepTag,
        this.caseAutomation.manualPause?.stepTitle
      ].map(value => String(value || '').toLowerCase()).join(' ');
      return /\braw[_\s-]*data[_\s-]*(?:fetch|load|loading)|load\s+raw\s+data|raw\s+data\s+for\s+main|raw_data_fetch/.test(sourceText);
    },

    caseAutomationShowcaseTool88Visible() {
      const activity = this.caseAutomationTool88Activity || {};
      const progress = this.caseAutomationTool88Progress || {};
      const running = !!(activity.active || progress.active || this.caseHopSolverAutomationRunning || this.caseHopSolverRunning);
      const rowsAppended = Math.max(Number(activity.rowsAppended) || 0, Number(progress.rowsAppended) || 0);
      const updatedAtMs = Math.max(
        Date.parse(activity.updatedAtUtc || '') || 0,
        Date.parse(progress.updatedAtUtc || '') || 0
      );
      const stateMatchesCurrentCase = this.caseWorkflowRawDataTool88VisualStateMatchesCurrentCase?.() !== false;
      if (!stateMatchesCurrentCase && !running) return false;
      const activityText = [
        activity.status,
        activity.title,
        activity.message,
        activity.detail,
        progress.status,
        progress.title,
        progress.detail,
        this.caseAutomation.status,
        this.caseAutomation.detail,
        this.caseAutomation.lastPlan?.stepTag,
        this.caseAutomation.lastPlan?.stepTitle,
        this.recommendedWorkflowStep?.tag,
        this.recommendedWorkflowStepTitle
      ].map(value => String(value || '').toLowerCase()).join(' ');

      const importedAndSettled = rowsAppended > 0 && !running && /raw[-_\s]*data[-_\s]*imported|imported into raw data|solved and imported|moving to graph/.test(activityText);
      if (importedAndSettled) return false;
      if (running) return true;
      if (!this.caseAutomation.active || !this.caseAutomationShowcaseRawFetchIsCurrent) return false;
      if ((activity.flowComplete || progress.flowComplete || activity.rawRows || progress.outputRows) && updatedAtMs > 0) {
        return Date.now() - updatedAtMs < 2500;
      }
      if (updatedAtMs > 0) return Date.now() - updatedAtMs < 12000;
      return /tool\s*88|hop solver|solve hop|raw data/.test(activityText);
    },

    caseAutomationShowcaseTool88Title() {
      const activity = this.caseAutomationTool88Activity || {};
      const progress = this.caseAutomationTool88Progress || {};
      if (activity.flowComplete && activity.rowsAppended) return 'Solved and imported into Raw Data';
      if (activity.flowComplete) return 'Confirming completed flow';
      if (activity.guardrailMessage) return 'Paused for review';
      if (activity.title) return activity.title;
      if (progress.title) return progress.title;
      if (activity.status) return activity.status.replace(/[-_]+/g, ' ');
      if (progress.status) return progress.status.replace(/[-_]+/g, ' ');
      return 'Gathering raw data';
    },

    caseAutomationShowcaseTool88Message() {
      const activity = this.caseAutomationTool88Activity || {};
      const progress = this.caseAutomationTool88Progress || {};
      if (activity.guardrailMessage) return activity.guardrailMessage;
      if (activity.message) return activity.message;
      if (activity.detail) return activity.detail;
      if (progress.detail) return progress.detail;
      if (activity.active || progress.active) return 'Tool 88 is hydrating the selected output, tracing unresolved path endings, and preparing WayFinder-like raw rows for Raw Data.';
      return 'Tool 88 raw-data solving is ready.';
    },

    caseAutomationShowcaseTool88PulseLabel() {
      const activity = this.caseAutomationTool88Activity || {};
      const progress = this.caseAutomationTool88Progress || {};
      const bridgeReviewSkipped = Boolean(this.caseAutomation?.skipResolvableReview) && activity.unresolvedBridgeAccepted && activity.flowComplete && activity.rowsAppended;
      if (activity.guardrailMessage && !bridgeReviewSkipped) return 'Waiting for investigator review';
      if (activity.flowComplete && activity.rowsAppended) return 'Raw Data imported; moving to graph preparation';
      if (activity.flowComplete) {
        const confirmations = Number(activity.flowCompleteConfirmations) || Number(progress.flowCompleteConfirmations) || 0;
        return confirmations > 0 ? `Flow complete check ${confirmations}; confirming it stayed complete` : 'Flow appears complete; confirming before moving on';
      }
      const steps = Math.max(Number(activity.stepCount) || 0, Number(progress.stepCount) || 0);
      const complete = Math.max(Number(activity.completeCount) || 0, Number(progress.completeEndings) || 0);
      const unresolved = Number.isFinite(Number(activity.unresolvedCount))
        ? Math.max(0, Number(activity.unresolvedCount))
        : Number.isFinite(Number(progress.openEndings))
          ? Math.max(0, Number(progress.openEndings))
          : null;
      const rawRows = Math.max(Number(activity.rawRows) || 0, Number(activity.rowsAppended) || 0, Number(progress.outputRows) || 0);
      const parts = [];
      if (steps > 0) parts.push(`round ${steps}`);
      if (complete > 0) parts.push(`${complete} ending${complete === 1 ? '' : 's'} resolved`);
      if (unresolved !== null) parts.push(`${unresolved} unresolved`);
      if (rawRows > 0) parts.push(`${rawRows} raw row${rawRows === 1 ? '' : 's'} staged`);
      return parts.length
        ? `Still working: ${parts.join(' · ')}`
        : 'Still working: checking selected output, count, attribution, and transaction signal';
    },

    caseAutomationShowcaseTool88TargetLabel() {
      const activity = this.caseAutomationTool88Activity || {};
      const progress = this.caseAutomationTool88Progress || {};
      const label = activity.targetLabel || progress.selectedLabel || '';
      const hash = activity.targetHash || progress.selectedHash || '';
      const address = activity.targetAddress || progress.selectedTarget || '';
      if (label && hash) return `${label} · ${hash}`;
      if (label) return label;
      if (hash) return hash;
      if (address) return address;
      return 'Raw-data step';
    },

    caseAutomationShowcaseTool88ProgressPercent() {
      const activity = this.caseAutomationTool88Activity || {};
      if (activity.flowComplete && (activity.rowsAppended || activity.rawRows)) return 100;
      const confirmations = Number(activity.flowCompleteConfirmations) || 0;
      if (activity.flowComplete) return Math.max(82, Math.min(98, 82 + confirmations * 7));
      const unresolved = Number(activity.unresolvedCount);
      const complete = Number(activity.completeCount) || 0;
      const endings = Number(activity.endingCount) || 0;
      if (Number.isFinite(unresolved) && endings > 0) {
        return Math.max(18, Math.min(88, Math.round((complete / Math.max(1, endings)) * 72) + 18));
      }
      const steps = Number(activity.stepCount) || 0;
      return Math.max(12, Math.min(84, 12 + steps * 4));
    },

    caseAutomationShowcaseTool88ProgressStyle() {
      return { width: `${this.caseAutomationShowcaseTool88ProgressPercent}%` };
    },

    caseAutomationShowcaseTool88Stats() {
      const activity = this.caseAutomationTool88Activity || {};
      const progress = this.caseAutomationTool88Progress || {};
      const formatCount = (value, fallback = '—') => {
        const number = Number(value);
        return Number.isFinite(number) && number > 0 ? number.toLocaleString('en-US') : fallback;
      };
      const knownEndingCount = Math.max(Number(activity.endingCount) || 0, Number(progress.endingCount) || 0);
      const unresolvedRaw = Number.isFinite(Number(activity.unresolvedCount))
        ? Number(activity.unresolvedCount)
        : Number(progress.openEndings);
      const unresolvedKnown = knownEndingCount > 0 || Number(unresolvedRaw) > 0;
      return [
        { label: 'Rounds', value: formatCount(Math.max(Number(activity.stepCount) || 0, Number(progress.stepCount) || 0), (activity.active || progress.active) ? 'Starting' : '—') },
        { label: 'Entries', value: formatCount(Math.max(Number(activity.entryCount) || 0, Number(progress.totalEntries) || 0)) },
        { label: 'Unresolved', value: unresolvedKnown && Number.isFinite(unresolvedRaw) ? Math.max(0, unresolvedRaw).toLocaleString('en-US') : '—' },
        { label: 'Raw rows', value: formatCount(Math.max(Number(activity.rowsAppended) || 0, Number(activity.rawRows) || 0, Number(progress.outputRows) || 0)) }
      ];
    },

    caseAutomationShowcaseTool88Log() {
      const activityLog = Array.isArray(this.caseAutomationTool88Activity?.log) ? this.caseAutomationTool88Activity.log : [];
      const progressEvents = Array.isArray(this.caseAutomationTool88Progress?.events)
        ? this.caseAutomationTool88Progress.events.map((item, index) => ({
          id: item.id || `tool88-progress-${index}`,
          message: item.label || item.message || ''
        }))
        : [];
      const seen = new Set();
      return [...activityLog, ...progressEvents]
        .filter((entry) => {
          const message = String(entry?.message || entry?.label || '').trim();
          if (!message) return false;
          const key = message.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, 2)
        .map((entry, index) => ({
          id: entry.id || `tool88-log-${index}-${String(entry.message || entry.label || '').slice(0, 24)}`,
          message: entry.message || entry.label || ''
        }));
    },

    caseWorkflowRawDataTool88DebugAvailable() {
      return Boolean(
        this.caseAutomationPreferHopSolverRawData ||
        this.caseWorkflowRawDataTool88Running ||
        this.caseWorkflowRawDataTool88LastSnapshot ||
        this.caseWorkflowRawDataTool88LastError ||
        this.caseAutomationTool88Activity?.startedAtUtc ||
        this.caseAutomationTool88Activity?.guardrailMessage ||
        this.caseAutomation.manualPause?.pauseKind === 'raw-data-fetch'
      );
    },

    caseWorkflowRawDataTool88PersistentDebugVisible() {
      return Boolean(
        this.caseWorkflowRawDataTool88Running ||
        this.caseWorkflowRawDataTool88LastSnapshot ||
        this.caseWorkflowRawDataTool88LastError ||
        this.caseAutomationTool88Activity?.startedAtUtc ||
        this.caseAutomationTool88Activity?.guardrailMessage ||
        Number(this.caseAutomationTool88Activity?.rowsAppended || this.caseAutomationTool88Activity?.rawRows || 0) > 0 ||
        (Array.isArray(this.caseWorkflowRawDataTool88LastRows) && this.caseWorkflowRawDataTool88LastRows.some(row => rowHasMeaningfulData(row)))
      );
    },

    caseAutomationPauseModalTitle() {
      if (this.caseAutomation?.manualPause?.pauseKind === 'raw-data-fetch' && this.caseAutomationPreferHopSolverRawData) {
        return this.caseWorkflowRawDataTool88LastError
          ? 'Tool 88 raw-data solver needs attention'
          : (this.caseAutomation?.manualPause?.title || 'Raw-data loading needs attention');
      }
      return this.caseAutomation?.manualPause?.title || 'Manual workflow action needed';
    },

    caseAutomationPauseModalMessage() {
      if (this.caseAutomation?.manualPause?.pauseKind === 'raw-data-fetch' && this.caseAutomationPreferHopSolverRawData) {
        return this.caseWorkflowRawDataTool88LastError
          ? `Tool 88 was preferred for this raw-data pass, but it stopped before raw coverage was complete: ${this.caseWorkflowRawDataTool88LastError}`
          : (this.caseAutomation?.manualPause?.message || 'Tool 88 raw-data automation paused before raw coverage was complete. Copy the debug log if this looks unexpected.');
      }
      return this.caseAutomation?.manualPause?.message || 'Complete the requested step manually, then continue.';
    },

    caseAutomationRawDataPauseInstruction() {
      if (this.caseAutomationPreferHopSolverRawData) {
        return 'Tool 88 raw-data automation was tried first. Review the missing coverage below, then either copy the Tool 88 debug log for troubleshooting, run Manual Solve/Tool 88 directly, or use WayFinder/PathTracer only as a fallback source workflow.';
      }
      return 'Use WayFinder, PathTracer, or your source data workflow to fetch raw rows for the missing main transaction flows, then append them to Raw Data.';
    },

    caseWorkflowRawDataTool88PauseDebugSummary() {
      const chosen = normalizeCell(this.caseWorkflowRawDataTool88LastChosenSource || 'none');
      const rows = Number(this.caseWorkflowRawDataTool88LastRows?.filter?.(row => rowHasMeaningfulData(row)).length) || 0;
      if (this.caseWorkflowRawDataTool88LastError) return `Last error: ${this.caseWorkflowRawDataTool88LastError}`;
      if (rows) return `${rows} row${rows === 1 ? '' : 's'} last captured from ${chosen}.`;
      if (this.caseWorkflowRawDataTool88LastSnapshot?.message) return normalizeCell(this.caseWorkflowRawDataTool88LastSnapshot.message);
      return 'No exportable Tool 88 raw-data rows have been captured for this pause yet.';
    },

    caseWorkflowRawDataTool88PauseDebugDetail() {
      const activity = this.caseAutomationTool88Activity || {};
      const detail = normalizeCell(activity.message || activity.detail || this.caseAutomationTool88Progress?.detail || '');
      return detail || 'The copied debug log includes hidden Tool 88 state, row-source candidates, coverage rows, and recent automation messages.';
    },

    caseWorkflowRawDataTool88PauseDebugChips() {
      const candidateCount = Array.isArray(this.caseWorkflowRawDataTool88LastCandidateSources) ? this.caseWorkflowRawDataTool88LastCandidateSources.length : 0;
      const rawRows = Number(this.caseAutomationTool88Activity?.rowsAppended || this.caseAutomationTool88Activity?.rawRows || 0) || 0;
      const unresolved = Number(this.caseAutomationTool88Activity?.unresolvedCount ?? this.caseAutomationTool88Progress?.openEndings);
      return [
        { label: 'Chosen rows', value: normalizeCell(this.caseWorkflowRawDataTool88LastChosenSource || 'none') },
        { label: 'Candidates', value: String(candidateCount) },
        { label: 'Raw staged', value: rawRows ? rawRows.toLocaleString('en-US') : '0' },
        { label: 'Unresolved', value: Number.isFinite(unresolved) ? String(Math.max(0, unresolved)) : 'unknown' }
      ];
    },

    caseAutomationShowcaseTool88PortalState() {
      return this.buildCaseWorkflowRawDataTool88PortalState();
    },


    caseAutomationShowcasePhases() {
      const groups = Array.isArray(this.workflowGroupsView) ? this.workflowGroupsView : [];
      const currentTag = String(this.caseAutomation.lastPlan?.stepTag || this.recommendedWorkflowStep?.tag || '').toUpperCase();
      return groups.map((group, index) => {
        const steps = Array.isArray(group.steps) ? group.steps : [];
        const total = steps.length;
        const completed = Number(group.completed) || 0;
        const active = !!currentTag && steps.some(step => String(step.tag || '').toUpperCase() === currentTag);
        const done = total > 0 && completed >= total;
        return {
          id: group.id || `phase-${index}`,
          title: group.title || `Stage ${index + 1}`,
          shortTitle: group.shortTitle || group.title || `Stage ${index + 1}`,
          completed,
          total,
          className: {
            'case-automation-showcase__phase': true,
            'case-automation-showcase__phase--done': done,
            'case-automation-showcase__phase--active': active && !done,
            'case-automation-showcase__phase--pending': !done && !active
          }
        };
      });
    },

    caseAutomationShowcaseRecentMilestones() {
      const entries = Array.isArray(this.caseAutomation.log) ? this.caseAutomation.log : [];
      const currentStatus = normalizeCell(this.caseAutomation?.status)
        .replace(/\s*(?:…|\.{3}|\.)\s*$/, '')
        .trim()
        .toLowerCase();
      const currentStepTag = normalizeCell(
        this.caseAutomation?.manualPause?.stepTag
          || this.caseAutomation?.lastPlan?.stepTag
          || this.recommendedWorkflowStep?.tag
      ).toUpperCase();
      const seenMessages = new Set();
      const milestones = [];

      for (const entry of entries) {
        const rawMessage = normalizeCell(entry?.message);
        if (!rawMessage) continue;
        const comparableMessage = rawMessage
          .replace(/\s*(?:…|\.{3}|\.)\s*$/, '')
          .trim()
          .toLowerCase();
        const entryStepTag = normalizeCell(entry?.details?.stepTag || entry?.stepTag).toUpperCase();
        const looksLikeInProgressMessage = /(?:…|\.{3})\s*$/.test(rawMessage);

        if (!comparableMessage || comparableMessage === currentStatus) continue;
        if (currentStepTag && entryStepTag === currentStepTag && looksLikeInProgressMessage) continue;
        if (/^(?:auto started|waiting\b|still working\b|automation active\b|manual pause:)/i.test(comparableMessage)) continue;

        const message = formatCaseAutomationMilestonePastTense(rawMessage);
        const messageKey = message.toLowerCase();
        if (!message || seenMessages.has(messageKey)) continue;
        seenMessages.add(messageKey);
        milestones.push({ ...entry, message });
        if (milestones.length >= 4) break;
      }

      return milestones;
    },

    caseAutomationRawCoveragePauseRows() {
      if (!this.showCaseAutomationPauseModal || this.caseAutomation.manualPause?.pauseKind !== 'raw-data-fetch') return [];
      const coverage = this.buildCaseBuddyMainRawDataCoverage(
        this.getMatrixForLogicalSheet('main'),
        this.getMatrixForLogicalSheet('raw')
      );
      return (coverage.coverageRows || [])
        .filter(row => row.status !== 'covered')
        .slice(0, 80);
    },

    caseGlobalOperationPercent() {
      const total = Number(this.caseGlobalOperation.total) || 0;
      const progress = Number(this.caseGlobalOperation.progress) || 0;
      if (total <= 0) return 12;
      return Math.max(6, Math.min(100, Math.round((progress / total) * 100)));
    },

    caseGlobalOperationProgressStyle() {
      return { width: `${this.caseGlobalOperationPercent}%` };
    },

    caseGlobalOperationProgressLabel() {
      const total = Number(this.caseGlobalOperation.total) || 0;
      const progress = Number(this.caseGlobalOperation.progress) || 0;
      if (total <= 0) return 'Working…';
      return `${Math.min(progress, total)} / ${total}`;
    },

    inputWorkbookContextMenuItems() {
      const component = this;
      return (payload = {}) => {
        const buildActionItem = (action) => ({
          name() {
            return component.getCaseBuddyContextMenuActionLabel(action, payload);
          },
          hidden() {
            return !component.isCaseBuddyContextMenuActionVisible(action, payload);
          },
          disabled() {
            return !component.isCaseBuddyContextMenuActionEnabled(action, payload);
          },
          callback() {
            component.handleInputContextMenuPathOperation(action, payload);
          }
        });

        return {
          case_buddy_path_operations_heading: {
            name: 'Case Buddy path operations',
            disabled: true,
            hidden() {
              return !component.isCaseBuddyContextMenuActionVisible('prune', payload);
            }
          },
          case_buddy_prune_selected_output: buildActionItem('prune'),
          case_buddy_graft_selected_output: buildActionItem('graft'),
          case_buddy_bridge_selected_output: buildActionItem('bridge')
        };
      };
    },


    caseNavItems() {
      const items = Array.isArray(CASE_NAV_ITEMS) ? [...CASE_NAV_ITEMS] : [];
      const outputIndex = items.findIndex(item => item?.id === 'case-output');
      const deliverablesIndex = items.findIndex(item => item?.id === 'case-deliverables');
      if (outputIndex < 0 || deliverablesIndex < 0 || outputIndex === deliverablesIndex + 1) return items;

      const [outputItem] = items.splice(outputIndex, 1);
      const nextDeliverablesIndex = items.findIndex(item => item?.id === 'case-deliverables');
      items.splice(nextDeliverablesIndex + 1, 0, outputItem);
      return items;
    },

    caseDetails() {
      return this.caseSupportState.caseDetails || {};
    },

    caseDisplayName() {
      return this.caseDetails.caseName || this.currentProjectFileName || 'Case Buddy';
    },

    caseTitleInlineDisplayStyle() {
      const px = Number(this.caseTitleInlineFontSizePx) || 0;
      return px > 0 ? { fontSize: `${px}px` } : {};
    },

    caseDisplayId() {
      return this.caseDetails.caseId || 'No case id set';
    },

    caseDisplayType() {
      return this.caseDetails.caseType || 'No case type set';
    },

    caseAddressMonitoringFilter() {
      return {
        caseFileId: this.currentProjectFileId || '',
        caseFileName: this.currentProjectFileName || '',
        caseName: this.caseDisplayName || this.currentProjectFileName || ''
      };
    },

    caseAddressMonitoringSelectedPayloads() {
      if (!this.caseInvestigationVisualizerCurrentVisual) return [];
      const payloads = [];
      const seen = new Set();
      const selectedNodes = Array.isArray(this.caseInvestigationVisualizerSelectedNodes)
        ? this.caseInvestigationVisualizerSelectedNodes
        : [];
      const sourceLabel = this.caseInvestigationVisualizerManualStartMode ? 'case_buddy_manual_solve' : 'case_buddy_standard_visualizer';
      const addPayload = (candidate, node = null) => {
        const payload = this.caseAddressMonitoringPayloadForCandidate(candidate, {
          node,
          createdFrom: sourceLabel
        });
        if (!payload) return;
        const key = this.caseAddressMonitoringPayloadKey(payload);
        if (seen.has(key)) return;
        seen.add(key);
        payloads.push(payload);
      };
      selectedNodes.forEach((node) => {
        addPayload(node?.caseCandidate || node?.candidate || null, node);
      });
      if (!payloads.length && this.caseInvestigationVisualizerSelectedCount === 1) {
        addPayload(this.caseInvestigationVisualizerSelectedCandidate || this.caseManualStartSingleSelectedCandidate || null, null);
      }
      if (!payloads.length && this.caseInvestigationVisualizerSelectedCount === 1 && typeof this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection === 'function') {
        addPayload(this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection({ quiet: true }) || null, null);
      }
      return payloads;
    },

    caseAddressMonitoringSelectedTitle() {
      if (this.caseAddressMonitoringBusy) return 'Adding monitoring item(s)…';
      const count = this.caseAddressMonitoringSelectedPayloads.length;
      if (count > 1) return `Add ${count} eligible dormant-funds selections to Address Monitoring.`;
      if (count === 1) return 'Add this selected dormant-funds address to Address Monitoring.';
      return 'Select a node with dormant funds to add it to Address Monitoring.';
    },

    caseAddressMonitoringDormantRowsForAdd() {
      const normalize = typeof this.caseManualNormalizeText === 'function'
        ? this.caseManualNormalizeText
        : value => String(value ?? '').trim();
      const monitoringRows = Array.isArray(this.dormantAddressDeliverableState?.monitoringRows)
        ? this.dormantAddressDeliverableState.monitoringRows
        : [];
      if (this.dormantAddressDeliverableActiveView === 'monitoring') {
        return this.dormantAddressDeliverableVisibleRows || [];
      }
      const search = normalize(this.dormantAddressDeliverableSearch || '').toLowerCase();
      const rows = monitoringRows.map((row, index) => ({
        ...row,
        displayIndex: row.displayIndex || index + 1
      }));
      if (!search) return rows;
      return rows.filter((row) => {
        const text = normalize(row.searchText || Object.values(row).join(' ')).toLowerCase();
        return text.includes(search);
      });
    },

    caseAddressMonitoringDormantVisibleEligibleCount() {
      return (this.caseAddressMonitoringDormantRowsForAdd || [])
        .filter(row => !!this.caseAddressMonitoringPayloadForDormantRow(row)).length;
    },

    caseDirtyLabel() {
      if (this.caseSupportState.dirty) {
        return 'Unsaved changes';
      }
      return 'Saved';
    },

    validationIsStale() {
      return Number.isFinite(Number(this.caseSupportState.lastValidationRevision)) &&
        Number(this.caseSupportState.lastValidationRevision) !== Number(this.caseSupportState.caseDataRevision);
    },

    validationStateLabel() {
      if (!Number.isFinite(Number(this.caseSupportState.lastValidationRevision))) {
        return 'Not checked';
      }

      return this.validationIsStale ? 'Checks stale' : 'Checks current';
    },

    validationRevisionLabel() {
      if (!Number.isFinite(Number(this.caseSupportState.lastValidationRevision))) {
        return 'Run checks when ready';
      }

      return `Checked rev ${this.caseSupportState.lastValidationRevision}; current rev ${this.caseSupportState.caseDataRevision}`;
    },

    outputHasData() {
      return this.caseSummary.outputRows > 0;
    },

    outputReviewIsAuditOnly() {
      const target = this.caseSupportState.outputReview?.target || '';
      return ['audit-only', 'network-lookup-audit', 'addendum-review-preview', 'reference-only', 'address-legend-quick-access'].includes(target);
    },

    outputAppendDisabled() {
      return !this.outputHasData || this.outputReviewIsAuditOnly;
    },

    outputAppendTitle() {
      if (!this.outputHasData) return 'The output buffer is empty.';
      if (this.outputReviewIsAuditOnly) return 'This output is review/audit output, not case rows to append.';
      return 'Append reviewed output rows into the selected case sheet.';
    },

    outputReviewLabel() {
      const review = this.caseSupportState.outputReview || {};
      if (review.status === 'reviewed') return review.title || 'Reviewed output';
      if (review.status === 'pending') return review.title || 'Pending review';
      return this.outputHasData ? 'Rows present' : 'Empty';
    },

    outputReviewDetail() {
      const review = this.caseSupportState.outputReview || {};
      if (review.detail) return review.detail;
      if (review.generatedAtUtc) return `Generated ${safeDateLabel(review.generatedAtUtc)}`;
      return this.outputHasData
        ? `${this.caseSummary.outputRows} non-empty output row${this.caseSummary.outputRows === 1 ? '' : 's'}`
        : 'Most tools should write here before anything is accepted back into case data.';
    },

    outputSheetFoldoutSummary() {
      const rowCount = Math.max(0, Number(this.caseSummary?.outputRows) || 0);
      if (!rowCount) return 'Empty review buffer';
      const rowLabel = `${rowCount} row${rowCount === 1 ? '' : 's'}`;
      const status = normalizeCell(this.caseSupportState?.outputReview?.status).toLowerCase();
      if (status === 'pending') return `${rowLabel} · Needs review`;
      if (status === 'reviewed') return `${rowLabel} · Reviewed`;
      return `${rowLabel} available`;
    },

    startingMainTransactionReadyCount() {
      return this.startingMainTransactionDraftRows.filter(row => row.status === 'ready' || row.status === 'different-to').length;
    },

    startingMainTransactionStageSummary() {
      const total = this.startingMainTransactionDraftRows.length;
      if (total === 0) return 'No pasted rows parsed yet.';

      const ready = this.startingMainTransactionReadyCount;
      const skipped = total - ready;
      return `${ready} ready · ${skipped} skipped or needs review · ${total} staged`;
    },

    networkLookupPendingCount() {
      return this.networkLookupRows.filter(row => row.needsNetwork).length;
    },

    networkLookupSelectedCount() {
      return this.networkLookupRows.filter(row => row.include && row.needsNetwork && row.hash).length;
    },

    networkLookupResolvedCount() {
      return this.networkLookupRows.filter(row => row.status === 'resolved').length;
    },

    networkLookupStageSummary() {
      if (this.networkLookupRows.length === 0) return 'No starting/main transaction rows are staged yet.';
      if (this.networkLookupPendingCount === 0) return 'All detected starting/main transactions already have Chain Id and Chain Name.';
      return `${this.networkLookupPendingCount} starting/main transaction${this.networkLookupPendingCount === 1 ? '' : 's'} need network data. ${this.networkLookupSelectedCount} selected for lookup.`;
    },

    networkLookupWorkflowPrimaryDisabled() {
      if (this.networkLookupRunning) return true;
      if (this.networkLookupRows.length === 0) return true;
      if (this.networkLookupPendingCount === 0) return false;
      return this.networkLookupSelectedCount === 0;
    },

    networkLookupWorkflowPrimaryActionLabel() {
      if (this.networkLookupRunning) return 'Looking up networks…';
      if (this.networkLookupRows.length === 0) return 'No transactions to look up';
      if (this.networkLookupPendingCount === 0) return 'Continue';
      const count = this.networkLookupSelectedCount;
      return `Look up ${count} transaction${count === 1 ? '' : 's'} + continue`;
    },

    dateLookupPendingCount() {
      return this.dateLookupRows.filter(row => row.needsDate).length;
    },

    dateLookupSelectedCount() {
      return this.dateLookupRows.filter(row => row.include && row.needsDate && row.hash).length;
    },

    dateLookupResolvedCount() {
      return this.dateLookupRows.filter(row => row.status === 'resolved').length;
    },

    dateLookupStageSummary() {
      if (this.dateLookupRows.length === 0) return 'No starting/main transaction rows are staged yet.';
      if (this.dateLookupPendingCount === 0) return 'All detected starting/main transactions already have valid Column E UTC dates.';
      return `${this.dateLookupPendingCount} starting/main transaction${this.dateLookupPendingCount === 1 ? '' : 's'} need UTC date work. ${this.dateLookupSelectedCount} selected.`;
    },

    dateLookupSortOnlyMode() {
      return this.dateLookupRows.length > 0 && this.dateLookupPendingCount === 0;
    },

    dateLookupSortOnlyTitle() {
      if (!this.dateLookupSortOnlyMode) return 'Sort main entries by UTC date';
      return this.dateLookupSortPreview.chronologyPass
        ? 'Dates are complete and already sorted'
        : 'Dates are complete — sort the rows now';
    },

    dateLookupSortActionLabel() {
      if (this.dateLookupRunning) return 'Working…';
      if (!this.dateLookupSortOnlyMode) {
        return this.dateLookupSortPreview.chronologyPass ? 'Re-check / Sort By Date' : 'Sort By Date Before Path Labels';
      }
      return this.dateLookupSortPreview.chronologyPass ? 'Confirm date order & continue' : 'Sort By Date & continue';
    },

    dateLookupWorkflowPrimaryDisabled() {
      if (this.dateLookupRunning) return true;
      if (this.dateLookupRows.length === 0) return true;
      if (this.dateLookupSortOnlyMode) return false;
      return this.dateLookupSelectedCount === 0;
    },

    dateLookupWorkflowPrimaryActionLabel() {
      if (this.dateLookupRunning) return 'Working…';
      if (this.dateLookupRows.length === 0) return 'No dates to check';
      if (this.dateLookupSortOnlyMode) return this.dateLookupSortActionLabel;
      const count = this.dateLookupSelectedCount;
      return `Complete ${count} date${count === 1 ? '' : 's'} + continue`;
    },

    mainPathLabelReadyCount() {
      return this.mainPathLabelRows.filter(row => row.status === 'ready' || row.status === 'unchanged').length;
    },

    mainPathLabelNeedsReviewCount() {
      return this.mainPathLabelRows.filter(row => row.status === 'attention' || row.status === 'blocked').length;
    },

    mainPathLabelChangeCount() {
      return this.mainPathLabelRows.filter(row => (row.status === 'ready' || row.status === 'unchanged') && row.willChange).length;
    },

    mainPathLabelStageSummary() {
      if (this.mainPathLabelRows.length === 0) return 'No eligible top-level main transactions are staged yet.';
      if (this.mainPathLabelNeedsReviewCount > 0) return `${this.mainPathLabelNeedsReviewCount} staged main transaction${this.mainPathLabelNeedsReviewCount === 1 ? '' : 's'} need review before labels can be applied.`;
      return `${this.mainPathLabelReadyCount} top-level main transaction${this.mainPathLabelReadyCount === 1 ? '' : 's'} ready for numeric Column A labels.`;
    },

    mainPathLabelWorkflowPrimaryDisabled() {
      if (this.mainPathLabelAssignRunning) return true;
      if (this.mainPathLabelRows.length === 0) return true;
      if (this.mainPathLabelNeedsReviewCount > 0) return true;
      return this.mainPathLabelReadyCount === 0;
    },

    mainPathLabelWorkflowPrimaryActionLabel() {
      if (this.mainPathLabelAssignRunning) return 'Assigning labels…';
      if (this.mainPathLabelRows.length === 0) return 'No labels to assign';
      if (this.mainPathLabelNeedsReviewCount > 0) return 'Resolve label review items first';
      if (this.mainPathLabelChangeCount === 0) return 'Continue';
      return `Assign ${this.mainPathLabelReadyCount} label${this.mainPathLabelReadyCount === 1 ? '' : 's'} + continue`;
    },

    rawDataImportParsedRowCount() {
      return this.rawDataImportRows.filter(rowHasMeaningfulData).length;
    },

    rawDataImportDetectedSetsCount() {
      return this.rawDataImportStagedSets.length;
    },

    rawDataCurrentCoverage() {
      return this.buildCaseBuddyMainRawDataCoverage(
        this.getMatrixForLogicalSheet('main'),
        this.getMatrixForLogicalSheet('raw')
      );
    },

    rawDataImportPreviewCoverage() {
      const rawData = this.getMatrixForLogicalSheet('raw');
      const preview = buildRawDataAppendMatrix(rawData, this.rawDataImportRows);
      return this.buildCaseBuddyMainRawDataCoverage(this.getMatrixForLogicalSheet('main'), preview.data);
    },

    rawDataImportCurrentMissingCount() {
      return this.rawDataCurrentCoverage.missingCount || 0;
    },

    rawDataImportRemainingMissingCount() {
      return this.rawDataImportPreviewCoverage.missingCount || 0;
    },

    rawDataImportMatchedCount() {
      return this.rawDataImportStagedSets.filter(set => set.status === 'matched').length;
    },

    rawDataImportNewCoverageCount() {
      const currentlyMissing = new Set(this.rawDataCurrentCoverage.missingRows.map(row => row.id));
      return this.rawDataImportPreviewCoverage.coverageRows.filter(row => currentlyMissing.has(row.id) && row.status === 'covered').length;
    },

    rawDataImportMissingAfterRows() {
      return this.rawDataImportPreviewCoverage.missingRows || [];
    },

    rawDataImportReadyToAppend() {
      return this.rawDataImportParsedRowCount > 0 && this.rawDataImportDetectedSetsCount > 0;
    },

    caseManualStartVisualizerRows() {
      const rows = this.getCaseManualStartVisualizerRows?.({ includeAll: true }) || [];
      if (Array.isArray(rows) && rows.length) return rows;
      return this.getCaseManualStartVisualizerRowsFromCurrentVisual?.() || [];
    },

    caseManualStartVisualizerCandidateCount() {
      return this.caseManualStartVisualizerRows.length;
    },

    caseManualStartVisualizerReady() {
      return this.caseManualStartVisualizerCandidateCount > 0;
    },

    caseManualStartVisualizerLaunchReady() {
      return this.caseManualStartVisualizerReady || !!this.caseInvestigationVisualizerReadyState?.canOpen;
    },

    caseManualStartVisualizerButtonLabel() {
      if (this.caseManualStartVisualizerRunning) return 'Preparing visual…';
      const count = this.caseManualStartVisualizerCandidateCount;
      return count > 1 ? `Flow Solver ${count} starts` : 'Flow Solver manually';
    },

    caseManualStartVisualizerOpeningProgressStyle() {
      const value = Math.max(0, Math.min(100, Number(this.caseManualStartVisualizerOpening?.progressPercent) || 0));
      return { width: `${value}%` };
    },

    caseManualStartVisualizerOpeningCountLabel() {
      const state = this.caseManualStartVisualizerOpening || {};
      const roots = Math.max(0, Number(state.rootCount) || 0);
      const pending = Math.max(0, Number(state.pendingRootCount) || 0);
      if (!pending) return `${roots} graph${roots === 1 ? '' : 's'}`;
      const current = Math.max(0, Math.min(pending, Number(state.currentRootIndex) || 0));
      return current > 0 ? `${current}/${pending} lookup${pending === 1 ? '' : 's'}` : `${pending} lookup${pending === 1 ? '' : 's'}`;
    },

    caseManualStartVisualizerOpeningCanOpenCurrent() {
      const state = this.caseManualStartVisualizerOpening || {};
      return Boolean(state.active && !state.stopRequested && Number(state.pendingRootCount) > 0 && ['scan', 'hydrate'].includes(state.phase));
    },

    caseManualStartVisualizerOpeningStepRows() {
      const state = this.caseManualStartVisualizerOpening || {};
      const phaseOrder = ['scan', 'hydrate', 'build', 'ready'];
      const currentRank = Math.max(0, phaseOrder.indexOf(state.phase));
      const pending = Math.max(0, Number(state.pendingRootCount) || 0);
      const hydrated = Math.max(0, Number(state.hydratedCount) || 0);
      const reused = Math.max(0, Number(state.reusedCount) || 0);
      const rootCount = Math.max(0, Number(state.rootCount) || 0);
      const definitions = [
        {
          key: 'scan',
          number: 1,
          title: 'Read current case data',
          detail: `${rootCount} starting graph${rootCount === 1 ? '' : 's'} found in Main Data.`
        },
        {
          key: 'hydrate',
          number: 2,
          title: 'Reuse details and fill only the gaps',
          detail: pending
            ? `${hydrated} filled · ${reused} reused · ${pending} startup lookup${pending === 1 ? '' : 's'} identified.`
            : 'Current case data is already sufficient; no startup provider lookup is needed.'
        },
        {
          key: 'build',
          number: 3,
          title: 'Build the visual workspace',
          detail: 'Create or reuse the graphs and Manual Solve controls from the current spreadsheet-backed case.'
        }
      ];
      return definitions.map((definition) => {
        const rank = phaseOrder.indexOf(definition.key);
        const complete = state.phase === 'ready' || currentRank > rank;
        const active = !complete && state.phase === definition.key;
        return {
          ...definition,
          complete,
          active,
          className: complete ? 'case-intake-step--complete' : (active ? 'case-intake-step--active' : '')
        };
      });
    },

    caseManualStartVisualizerTitle() {
      if (this.caseManualStartVisualizerRunning) return this.caseManualStartVisualizerOpening?.message || 'Case Buddy is opening Manual Solve.';
      if (!this.caseManualStartVisualizerReady) return 'Try to open Manual Solve from Main Data. Case Buddy will explain what is missing if no starting transaction hash can be found.';
      return 'Open Manual Solve from current Main Data. Existing case details and visual graphs are reused; only essential gaps on starting transactions are looked up.';
    },

    caseManualStartVisualizerLaunchTitle() {
      if (this.caseManualStartVisualizerRunning) return this.caseManualStartVisualizerOpening?.message || 'Case Buddy is opening Manual Solve.';
      if (this.caseManualStartVisualizerReady) return this.caseManualStartVisualizerTitle;
      return this.caseInvestigationVisualizerReadyState?.title || 'Load at least one starting/main transaction hash before opening the manual visualizer.';
    },

    caseInvestigationVisualizerManualStartMode() {
      return this.caseInvestigationVisualizerState?.source === 'case-buddy-manual-start-visualizer';
    },

    caseManualSolveEvaluationState() {
      return this.buildCaseManualSolveEvaluationState();
    },

    caseManualSolveGetDataSummaryText() {
      const summary = this.caseManualSolveGetDataLastSummary || null;
      if (!summary) return 'Raw Data TSV for the current viewport.';
      const rowCount = Number(summary.rowCount) || 0;
      const sourceText = summary.sourceLabel ? ` · ${summary.sourceLabel}` : '';
      return `${rowCount} raw row${rowCount === 1 ? '' : 's'}${sourceText}`;
    },

    caseManualSolveGetDataPanelStyle() {
      const position = this.caseManualSolveGetDataPanelPosition || {};
      const x = Number.isFinite(Number(position.x)) ? Number(position.x) : 18;
      const y = Number.isFinite(Number(position.y)) ? Number(position.y) : 52;
      return {
        left: `${Math.max(8, Math.round(x))}px`,
        top: `${Math.max(8, Math.round(y))}px`
      };
    },

    caseManualSolveHeatMapState() {
      return this.buildCaseManualSolveHeatMapState();
    },

    caseManualSolveHeatMapActive() {
      return Boolean(
        this.caseInvestigationVisualizerManualStartMode
          && this.caseManualSolveEvaluationOpen
          && this.caseManualSolveHeatMapEnabled
          && this.caseManualSolveHeatMapState?.canHeatMap
      );
    },

    caseManualSolveHeatMapSensitivityDisplay() {
      const value = Number(this.caseManualSolveHeatMapSensitivity);
      const normalized = Number.isFinite(value) ? Math.max(0.35, Math.min(2.5, value)) : 1;
      return `${normalized.toFixed(2)}×`;
    },

    caseManualSolveHeatMapLegendRows() {
      return [
        { key: 'zero', label: 'Zero / negative followed amount', color: '#38bdf8' },
        { key: 'low', label: 'Low value', color: '#064e3b' },
        { key: 'green', label: '15–35%', color: '#16a34a' },
        { key: 'yellow', label: '35–70%', color: '#facc15' },
        { key: 'orange', label: '70–95%', color: '#f97316' },
        { key: 'red', label: 'Near 100%', color: '#dc2626' },
        { key: 'purple', label: 'Over 100%', color: '#7c3aed' },
        { key: 'error', label: 'Valuation unavailable', color: '#e879f9' }
      ];
    },

    caseManualStartActivePathEndingCandidate() {
      return this.getCaseManualStartActivePathEndingCandidate?.() || null;
    },

    caseManualStartSingleSelectedCandidate() {
      if (!this.caseInvestigationVisualizerManualStartMode) return null;
      if (this.caseInvestigationVisualizerSelectedCount !== 1) return null;
      const node = this.caseInvestigationVisualizerSelectedNode || null;
      const candidate = node
        ? (node.caseCandidate || node.candidate || null)
        : (this.caseInvestigationVisualizerSelectedCandidate || null);
      return candidate?.caseManualStartCandidate ? candidate : null;
    },

    caseManualStartResolvedSolveWarningLabel() {
      const candidate = this.caseManualStartResolvedSolveWarningCandidate || this.caseManualStartSingleSelectedCandidate || {};
      const path = candidate.pathLabel ? `Path ${candidate.pathLabel}` : 'Selected path ending';
      const hash = candidate.hash || candidate.txHash || candidate.transactionHash || '';
      return hash ? `${path} · ${this.caseManualShorten(hash, 10, 8)}` : path;
    },

    caseManualStartHopInspectorSelectedCandidate() {
      const selectedCandidate = this.caseManualStartSingleSelectedCandidate || null;
      const lockedToActiveCandidate = this.caseManualStartHopInspectorPreviewOpen
        || this.caseManualStartHopInspectorRunning
        || this.caseManualStartHopInspectorTraceLoading;
      if (lockedToActiveCandidate) {
        return this.caseManualStartHopInspectorCandidate
          || selectedCandidate
          || null;
      }
      if (this.caseManualStartHopInspectorOpen) {
        const inspectorCandidate = this.caseManualStartHopInspectorCandidate || null;
        if (
          inspectorCandidate?.caseManualStartCandidate
            && this.isCaseManualStartUnresolvedEndingCandidate(inspectorCandidate)
            && (!selectedCandidate?.caseManualStartCandidate || !this.isCaseManualStartUnresolvedEndingCandidate(selectedCandidate))
        ) {
          return inspectorCandidate;
        }
        return selectedCandidate
          || inspectorCandidate
          || null;
      }
      return selectedCandidate;
    },

    caseManualStartHopInspectorNeedsOutputChoiceBeforeSolve() {
      return this.caseManualStartCandidateRequiresChoiceBeforeSolve(this.caseManualStartHopInspectorSelectedCandidate);
    },

    caseManualStartHopInspectorSolveTitle() {
      const candidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate;
      if (!candidate?.caseManualStartCandidate) return 'Select exactly one Manual Solve path-ending address or transaction. Use Next when you want Case Buddy to choose the next unresolved ending.';
      if (this.caseManualStartHopInspectorNeedsOutputChoiceBeforeSolve) {
        return `${this.caseManualStartOutputChoiceButtonLabel} is required before this ambiguous starting transaction can be solved.`;
      }
      if (!this.isCaseManualStartPathEndingCandidate(candidate)) return 'The selected node is not a Path ends / unresolved-ending row. Select the path-ending address or transaction instead.';
      if (this.caseManualStartCandidateLooksResolvedPathEnding(candidate)) return 'This path ending already looks resolved. Press Solve hop only if you intentionally want to review and trace past it.';
      if (this.caseManualStartHopInspectorRunning) return 'Manual Solve is already running Tool 88.';
      return 'Open the Manual Solve hop inspector for the single selected unresolved ending.';
    },

    caseManualStartHopInspectorCanRun() {
      const candidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate;
      const inspectorCandidate = this.caseManualStartHopInspectorCandidate || null;
      const selectedCandidate = this.caseManualStartSingleSelectedCandidate || null;
      const hasVisibleSelection = this.caseInvestigationVisualizerSelectedCount === 1;
      const hasPinnedInspectorCandidate = Boolean(
        this.caseManualStartHopInspectorOpen
          && inspectorCandidate?.caseManualStartCandidate
          && candidate?.caseManualStartCandidate
          && this.caseManualStartCandidateMatchesInspectorCandidate(candidate, inspectorCandidate)
          && (!selectedCandidate?.caseManualStartCandidate || this.caseManualStartCandidateMatchesInspectorCandidate(selectedCandidate, inspectorCandidate))
      );
      return !!(
        this.caseInvestigationVisualizerManualStartMode
          && (hasVisibleSelection || hasPinnedInspectorCandidate)
          && candidate?.caseManualStartCandidate
          && this.isCaseManualStartPathEndingCandidate(candidate)
          && !this.caseManualStartCandidateRequiresChoiceBeforeSolve(candidate)
          && !this.caseManualStartHopInspectorRunning
      );
    },

    caseManualStartOutputChoiceActiveCandidate() {
      const pinnedCandidate = this.caseManualStartOutputChoiceCandidate;
      if ((this.caseManualStartOutputChoiceOpen || this.caseManualStartOutputChoiceLoading) && pinnedCandidate) return pinnedCandidate;
      return this.caseManualStartHopInspectorSelectedCandidate
        || this.caseInvestigationVisualizerSelectedCandidateView
        || this.caseInvestigationVisualizerSelectedCandidate
        || null;
    },

    caseManualStartOutputChoiceShouldShow() {
      return Boolean(
        this.caseInvestigationVisualizerManualStartMode
        && this.caseManualStartCandidateNeedsOutputChoice(this.caseManualStartOutputChoiceActiveCandidate)
      );
    },

    caseManualStartOutputChoiceCanOpen() {
      return Boolean(
        this.caseManualStartOutputChoiceShouldShow
        && !this.caseManualStartOutputChoiceLoading
      );
    },

    caseManualStartOutputChoiceButtonLabel() {
      const candidate = this.caseManualStartOutputChoiceActiveCandidate || {};
      const family = this.caseManualInferNetworkFamily(candidate);
      const direction = this.caseManualStartOutputChoiceDirectionForCandidate(candidate);
      if (family === 'btc') return direction === 'reverse' ? 'Choose BTC input' : 'Choose BTC output';
      return direction === 'reverse' ? 'Choose source transfer' : 'Choose target transfer';
    },

    caseManualStartOutputChoiceTitle() {
      const candidate = this.caseManualStartOutputChoiceCandidate || this.caseManualStartOutputChoiceActiveCandidate || {};
      const family = this.caseManualInferNetworkFamily(candidate);
      const direction = this.caseManualStartOutputChoiceDirectionForCandidate(candidate);
      if (family === 'btc') return direction === 'reverse' ? 'Select BTC input / From address' : 'Select BTC output / To address';
      return direction === 'reverse' ? 'Select source transfer' : 'Select target transfer';
    },

    caseManualStartOutputChoiceStyle() {
      const position = this.caseManualStartOutputChoicePosition || {};
      const x = Number(position.x);
      const y = Number(position.y);
      if (Number.isFinite(x) && Number.isFinite(y)) {
        return { left: `${Math.max(0, x)}px`, top: `${Math.max(0, y)}px`, right: 'auto' };
      }
      return { right: '18px', top: this.caseManualStartHopInspectorOpen ? '420px' : '108px' };
    },

    caseManualStartOutputChoiceSelectedChoice() {
      const selectedKey = this.caseManualNormalizeText(this.caseManualStartOutputChoiceSelectedKey);
      const rows = Array.isArray(this.caseManualStartOutputChoiceRows) ? this.caseManualStartOutputChoiceRows : [];
      return rows.find(row => row.key === selectedKey) || null;
    },

    caseManualStartOutputChoiceCanApply() {
      return Boolean(this.caseManualStartOutputChoiceSelectedChoice && !this.caseManualStartOutputChoiceLoading);
    },

    caseManualStartSettingsSummary() {
      const settings = this.caseManualStartSettingsDraft || {};
      const direction = settings.traceDirection === 'reverse' ? 'Reverse' : 'Forward';
      const hopLogic = settings.hopLogicType === 'FIFO' ? 'FIFO' : (settings.hopLogicType === 'LIFO' ? 'LIFO / PTDQ' : 'Next chronological');
      const serviceChecks = settings.enableAdditionalServiceChecks === false ? 'service checks off' : 'service checks on';
      const nativeMatch = settings.enableNativeDirectSpendMatchGuard === true ? 'native amount-match on' : 'strict FIFO native spend';
      return `${direction} · ${hopLogic} · ${serviceChecks} · ${nativeMatch}`;
    },

    caseManualStartBtcVisualInputAddressLimit() {
      return CASE_MANUAL_START_BTC_VISUAL_INPUT_ADDRESS_LIMIT;
    },

    caseManualStartTraceAnywayKnownTransactionCount() {
      return this.caseManualExtractTraceAnywayKnownTransactionCount(
        this.caseManualStartHopInspectorSnapshot || {},
        this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || {}
      );
    },

    caseManualStartTraceAnywayBlocked() {
      if (!CASE_MANUAL_START_TRACE_ANYWAY_TX_COUNT_GUARD_ENABLED) return false;
      if (!this.caseManualStartHopInspectorTraceOverrideNeeded) return false;
      const count = Number(this.caseManualStartTraceAnywayKnownTransactionCount || 0);
      return Number.isFinite(count) && count >= CASE_MANUAL_START_TRACE_ANYWAY_TX_COUNT_LIMIT;
    },

    caseManualStartTraceAnywayBlockedMessage() {
      const count = Number(this.caseManualStartTraceAnywayKnownTransactionCount || 0);
      const countText = Number.isFinite(count) && count > 0 ? this.caseManualFormatInteger(count) : 'a very high known';
      const limitText = this.caseManualFormatInteger(CASE_MANUAL_START_TRACE_ANYWAY_TX_COUNT_LIMIT);
      return `Trace-through is disabled for this suspected service/hot-wallet address because Tool 88 knows about ${countText} transactions, at or above the ${limitText} safety limit. Use the recommended path-ending action, or change the internal app constant for exceptional testing.`;
    },

    caseManualStartNextCanRun() {
      return Boolean(
        this.caseInvestigationVisualizerManualStartMode
          && this.caseInvestigationVisualizerCurrentVisual
          && !this.caseManualStartHopInspectorRunning
          && !this.caseManualStartHopInspectorTraceLoading
          && !this.caseManualStartOutputChoiceLoading
      );
    },

    caseManualStartUnresolvedEndingCount() {
      if (!this.caseInvestigationVisualizerManualStartMode || !this.caseInvestigationVisualizerCurrentVisual) return 0;
      return this.getCaseManualStartViewportUnresolvedEndingCount();
    },

    caseManualStartViewportCompletionSignature() {
      return this.buildCaseManualStartViewportCompletionSignature();
    },

    caseManualStartViewportCompletionBorderGlowActive() {
      return Boolean(this.caseManualStartViewportCompletionEffect?.borderGlow);
    },

    caseManualStartViewportCompletionEffectStyle() {
      const position = this.caseManualStartViewportCompletionEffect?.position || {};
      const x = Number(position.x);
      const y = Number(position.y);
      if (Number.isFinite(x) && Number.isFinite(y)) {
        return { left: `${Math.max(8, x)}px`, top: `${Math.max(8, y)}px`, right: 'auto', bottom: 'auto', transform: 'none' };
      }
      return { right: '1.15rem', bottom: '1.15rem', left: 'auto', top: 'auto', transform: 'none' };
    },

    caseManualStartViewportOtherAutomationRunning() {
      return Boolean(
        this.caseIntakePreparation?.active
          || this.caseAutomation?.active
          || this.caseAutomation?.busy
          || this.caseHopSolverAutomationRunning
          || this.caseHopSolverRunning
          || this.caseHopSolverPlaceholderRunning
      );
    },

    caseManualStartViewportAutoActive() {
      return Boolean(this.caseManualStartViewportAuto?.active);
    },

    caseManualStartAllFlowsAutoEnabled() {
      return CASE_MANUAL_START_ALL_FLOWS_AUTO_ENABLED;
    },

    caseManualStartAllFlowsAutoActive() {
      return Boolean(this.caseManualStartAllFlowsAuto?.active);
    },

    caseManualStartAllFlowsFlowAssessments() {
      if (!this.caseInvestigationVisualizerManualStartMode) return Object.create(null);
      const flows = Array.isArray(this.caseInvestigationVisualizerFlows) ? this.caseInvestigationVisualizerFlows : [];
      return flows.reduce((memo, flow, flowIndex) => {
        const identity = this.caseManualStartAllFlowsFlowIdentity(flow, flowIndex);
        memo[identity] = this.caseManualStartAllFlowsAssessFlow(flow);
        return memo;
      }, Object.create(null));
    },

    caseManualStartAllFlowsAutoCompletedCount() {
      return new Set(Array.isArray(this.caseManualStartAllFlowsAuto?.completedFlowKeys) ? this.caseManualStartAllFlowsAuto.completedFlowKeys : []).size;
    },

    caseManualStartAllFlowsAutoAttentionCount() {
      return Array.isArray(this.caseManualStartAllFlowsAuto?.attentionFlows) ? this.caseManualStartAllFlowsAuto.attentionFlows.length : 0;
    },

    caseManualStartAllFlowsAutoProcessedCount() {
      const total = Math.max(0, Number(this.caseManualStartAllFlowsAuto?.totalFlowCount) || 0);
      return Math.min(total, this.caseManualStartAllFlowsAutoCompletedCount + this.caseManualStartAllFlowsAutoAttentionCount);
    },

    caseManualStartAllFlowsAutoProgressPercent() {
      const total = Math.max(1, Number(this.caseManualStartAllFlowsAuto?.totalFlowCount) || 1);
      const currentBonus = this.caseManualStartAllFlowsAutoActive && this.caseManualStartAllFlowsAuto?.currentFlowKey ? 0.35 : 0;
      return Math.max(0, Math.min(100, Math.round(((this.caseManualStartAllFlowsAutoProcessedCount + currentBonus) / total) * 100)));
    },

    caseManualStartAllFlowsAutoProgressStyle() {
      return { width: `${this.caseManualStartAllFlowsAutoProgressPercent}%` };
    },

    caseManualStartAllFlowsAutoCanStart() {
      if (!CASE_MANUAL_START_ALL_FLOWS_AUTO_ENABLED) return false;
      const flows = Array.isArray(this.caseInvestigationVisualizerFlows) ? this.caseInvestigationVisualizerFlows : [];
      const assessments = this.caseManualStartAllFlowsFlowAssessments || {};
      const hasPendingFlow = flows.some((flow, flowIndex) => assessments[this.caseManualStartAllFlowsFlowIdentity(flow, flowIndex)]?.needsWork === true);
      return Boolean(
        this.caseInvestigationVisualizerManualStartMode
          && this.showCaseInvestigationVisualizerModal
          && flows.length
          && hasPendingFlow
          && !this.caseManualStartAllFlowsAutoActive
          && !this.caseManualStartViewportAutoActive
          && !this.caseManualStartViewportOtherAutomationRunning
          && !this.caseGlobalOperation?.active
          && !this.caseModalOperation?.active
          && !this.caseInvestigationVisualizerInteractionLocked
      );
    },

    caseManualStartAllFlowsAutoButtonLabel() {
      const flows = Array.isArray(this.caseInvestigationVisualizerFlows) ? this.caseInvestigationVisualizerFlows : [];
      if (this.caseManualStartAllFlowsAutoActive) return 'Solving all graphs…';
      if (!flows.length) return 'Solve all graphs';
      const assessments = this.caseManualStartAllFlowsFlowAssessments || {};
      const pending = flows.filter((flow, flowIndex) => assessments[this.caseManualStartAllFlowsFlowIdentity(flow, flowIndex)]?.needsWork === true).length;
      if (!pending) return 'All graphs complete';
      if (flows.length === 1) return 'Solve this graph';
      return pending === flows.length ? `Solve all ${flows.length} graphs` : `Solve ${pending} unfinished graph${pending === 1 ? '' : 's'}`;
    },

    caseManualStartAllFlowsAutoButtonTitle() {
      if (!CASE_MANUAL_START_ALL_FLOWS_AUTO_ENABLED) return 'Guided all-graph Auto is disabled by the app configuration.';
      if (this.caseManualStartAllFlowsAutoActive) return this.caseManualStartAllFlowsAuto?.status || 'Guided Auto is working through the starting graphs.';
      if (this.caseManualStartViewportAutoActive) return 'Stop the current per-graph Auto before starting guided Auto for every graph.';
      if (this.caseManualStartViewportOtherAutomationRunning) return 'Another Case Buddy automation is already running.';
      const flows = Array.isArray(this.caseInvestigationVisualizerFlows) ? this.caseInvestigationVisualizerFlows : [];
      if (!flows.length) return 'Load at least one starting graph before running guided Auto.';
      const assessments = this.caseManualStartAllFlowsFlowAssessments || {};
      const pending = flows.filter((flow, flowIndex) => assessments[this.caseManualStartAllFlowsFlowIdentity(flow, flowIndex)]?.needsWork === true).length;
      if (!pending) return 'Every graph already appears complete.';
      return 'Run the existing contained Auto on each unfinished starting graph. Graphs that reach a guardrail or need a manual choice are set aside, and the remaining graphs continue.';
    },

    caseManualStartAllFlowsAutoResultComplete() {
      return this.caseManualStartAllFlowsAuto?.lastResult === 'complete';
    },

    caseManualStartAllFlowsAutoResultTitle() {
      if (this.caseManualStartAllFlowsAutoResultComplete) return 'Every graph is complete';
      if (this.caseManualStartAllFlowsAuto?.lastResult === 'stopped') return 'Guided Auto stopped safely';
      if (this.caseManualStartAllFlowsAuto?.lastResult === 'timeout') return 'Guided Auto reached its safety limit';
      if (this.caseManualStartAllFlowsAuto?.lastResult === 'error') return 'Guided Auto needs review';
      return 'Some graphs still need attention';
    },

    caseManualStartAllFlowsAutoResultMessage() {
      const total = Math.max(0, Number(this.caseManualStartAllFlowsAuto?.caseFlowCount) || 0);
      const attention = this.caseManualStartAllFlowsAutoAttentionCount;
      const complete = Math.max(0, total - attention);
      if (this.caseManualStartAllFlowsAutoResultComplete) {
        return `All ${total} starting graph${total === 1 ? '' : 's'} have meaningful endings and no unresolved path endings remain. Close Flow Solver and continue with the Workflow Assistant.`;
      }
      if (this.caseManualStartAllFlowsAuto?.lastResult === 'stopped') {
        return `The run stopped after ${complete} of ${total} graph${total === 1 ? '' : 's'} were ready. Completed work remains in the case, and the unfinished graphs are listed below.`;
      }
      return `${complete} of ${total} starting graph${total === 1 ? '' : 's'} are ready. Review the graph${attention === 1 ? '' : 's'} below, or retry the unfinished set.`;
    },

    caseManualStartViewportAutoCanStart() {
      return Boolean(
        this.caseInvestigationVisualizerManualStartMode
          && this.caseInvestigationVisualizerCurrentVisual
          && this.caseManualStartNextCanRun
          && !this.caseManualStartViewportOtherAutomationRunning
          && !this.caseGlobalOperation?.active
          && !this.caseModalOperation?.active
          && !this.caseInvestigationVisualizerInteractionLocked
      );
    },

    caseManualStartViewportAutoButtonDisabled() {
      if (this.caseManualStartViewportAutoActive) return false;
      return !this.caseManualStartViewportAutoCanStart;
    },

    caseManualStartViewportAutoButtonLabel() {
      return this.caseManualStartViewportAutoActive ? 'Stop Auto' : 'Auto';
    },

    caseManualStartViewportAutoButtonTitle() {
      if (this.caseManualStartViewportAutoActive) return this.caseManualStartViewportAuto?.status || 'Stop the contained Manual Solve viewport automation.';
      if (this.caseManualStartViewportOtherAutomationRunning) return 'Another Case Buddy automation is already running. Stop it before starting Manual Solve viewport Auto.';
      if (!this.caseInvestigationVisualizerManualStartMode) return 'Manual Solve viewport Auto is only available inside the Manual Solve visualizer.';
      if (!this.caseInvestigationVisualizerCurrentVisual) return 'Open a Manual Solve visual before starting viewport Auto.';
      return 'Run contained Manual Solve Auto. It repeatedly presses Next for this viewport only and stops at manual choices, completion, loops, limits, or when the modal closes.';
    },

    caseManualStartHopInspectorSelectedKey() {
      return this.buildCaseManualStartHopInspectorCandidateKey(this.caseManualStartHopInspectorSelectedCandidate);
    },

    caseManualStartHopInspectorChecksReady() {
      const candidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || null;
      const key = this.buildCaseManualStartHopInspectorCandidateKey(candidate);
      const snapshot = this.caseManualStartHopInspectorSnapshot || null;
      if (!key || !snapshot || this.caseManualStartHopInspectorLoadedCandidateKey !== key || !candidate?.caseManualStartCandidate) return false;
      if (this.caseManualStartSnapshotMatchesCandidate(snapshot, candidate)) return true;
      const snapshotRows = [];
      const addRows = (rows) => {
        if (Array.isArray(rows)) snapshotRows.push(...rows);
      };
      addRows(snapshot.outputRows);
      addRows(snapshot.result?.outputRows);
      addRows(snapshot.rows);
      addRows(snapshot.result?.rows);
      addRows(this.caseManualStartHopInspectorOutputRows);
      return this.caseManualStartHopInspectorRowsContainCandidateRows(snapshotRows, candidate);
    },

    caseManualStartHopInspectorStatusText() {
      const fallback = 'Tool 88 will inspect one unresolved ending, show the recommendation, then let you apply the returned rows into this visual build-out.';
      const status = this.caseManualNormalizeText(this.caseManualStartHopInspectorStatus || '');
      if (!status) return fallback;
      const key = status.toLowerCase();
      if (key === 'selected-hop-ready') return 'Checks are ready. Review the recommendation or choose a hop action from the side tray.';
      if (key === 'decision-ready') return 'Tool 88 has a reviewable result ready.';
      if (key === 'manual-review') return 'Tool 88 needs manual review before Case Buddy should apply anything.';
      if (key === 'unverified-output') return 'Tool 88 staged output that needs review before applying.';
      if (key === 'applied') return 'Tool 88 applied the selected action.';
      if (/^[a-z0-9_-]+$/iu.test(key) && !/[\s.]/u.test(status)) return 'Tool 88 checks are ready.';
      return status;
    },

    caseManualStartHopInspectorNeedsChecks() {
      return Boolean(this.caseManualStartHopInspectorSelectedKey && !this.caseManualStartHopInspectorChecksReady);
    },

    caseManualStartHopInspectorCanAct() {
      const candidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate;
      const selectedKey = this.buildCaseManualStartHopInspectorCandidateKey(candidate);
      return Boolean(
        this.caseInvestigationVisualizerManualStartMode
          && candidate?.caseManualStartCandidate
          && this.isCaseManualStartPathEndingCandidate(candidate)
          && !this.caseManualStartCandidateRequiresChoiceBeforeSolve(candidate)
          && !this.caseManualStartHopInspectorRunning
          && !this.caseManualStartHopInspectorTraceLoading
          && selectedKey
          && this.caseManualStartHopInspectorChecksReady
      );
    },

    caseManualStartHopInspectorCheckState() {
      if (this.caseManualStartHopInspectorRunning || this.caseManualStartHopInspectorAutoCheckQueuedKey || this.caseManualStartHopInspectorAutoCheckInFlightKey) return 'running';
      if (this.caseManualStartHopInspectorChecksReady) return 'ready';
      return 'needed';
    },

    caseManualStartHopInspectorCheckDotClass() {
      return {
        'case-manual-start-hop-inspector__check-dot': true,
        'is-needed': this.caseManualStartHopInspectorCheckState === 'needed',
        'is-running': this.caseManualStartHopInspectorCheckState === 'running',
        'is-ready': this.caseManualStartHopInspectorCheckState === 'ready'
      };
    },

    caseManualStartHopInspectorCheckStatusTitle() {
      if (this.caseManualStartHopInspectorCheckState === 'running') return 'Checking…';
      if (this.caseManualStartHopInspectorCheckState === 'ready') return 'Ready';
      return 'Needs checks';
    },

    caseManualStartHopInspectorBusyNoticeVisible() {
      return Boolean(
        !this.caseManualStartHopInspectorTraceLoading
          && !this.caseManualStartHopInspectorPreviewOpen
          && (this.caseManualStartHopInspectorRunning || this.caseManualStartHopInspectorAutoCheckQueuedKey || this.caseManualStartHopInspectorAutoCheckInFlightKey)
      );
    },

    caseManualStartHopInspectorBusyNoticeTitle() {
      if (this.caseManualStartHopInspectorStopRequested) return 'Stopping Tool 88…';
      if (this.caseManualStartHopInspectorAutoCheckQueuedKey && !this.caseManualStartHopInspectorAutoCheckInFlightKey) return 'Checks queued…';
      if (this.caseManualStartHopInspectorAutoCheckInFlightKey || this.caseManualStartHopInspectorRunning) return 'Tool 88 is working…';
      return 'Working…';
    },

    caseManualStartHopInspectorBusyNoticeText() {
      if (this.caseManualStartHopInspectorStopRequested) return 'Buttons remain locked until Tool 88 reaches a safe stop point.';
      if (this.caseManualStartHopInspectorAutoCheckQueuedKey && !this.caseManualStartHopInspectorAutoCheckInFlightKey) return 'The inspector is about to refresh checks for this selected ending.';
      return this.caseManualStartHopInspectorStatusText || 'Please wait while Case Buddy runs the current Tool 88 check/action.';
    },

    caseManualStartHopInspectorRecommendationCardCanApply() {
      return Boolean(this.caseManualStartHopInspectorCanAct && this.resolveCaseManualStartHopInspectorRecommendedActionKey());
    },

    caseManualStartHopInspectorRecommendationCardTitle() {
      if (this.caseManualStartHopInspectorRecommendationCardCanApply) return 'Use Tool 88 recommended action for this selected ending.';
      if (this.caseManualStartHopInspectorCheckState === 'running') return 'Tool 88 is still checking this ending.';
      if (this.caseManualStartHopInspectorCheckState === 'needed') return 'Run Tool 88 checks first.';
      return 'Tool 88 has not produced a specific recommended action yet.';
    },

    caseManualStartHopInspectorRecommendationCardHelper() {
      if (this.caseManualStartHopInspectorCheckState === 'running') return 'Checks are running…';
      if (this.caseManualStartHopInspectorCheckState === 'needed') return 'Run checks first';
      return 'No one-click recommendation yet';
    },

    caseManualStartNodeActionTrayCandidate() {
      return this.caseManualStartHopInspectorSelectedCandidate
        || this.caseManualStartSingleSelectedCandidate
        || this.caseInvestigationVisualizerSelectedCandidate
        || this.caseInvestigationVisualizerSelectedNode?.caseCandidate
        || this.caseInvestigationVisualizerSelectedNode?.candidate
        || null;
    },

    caseManualStartNodeActionTraySelectedNodeIsAddress() {
      const node = this.caseInvestigationVisualizerSelectedNode || {};
      return this.caseManualNormalizeText(node.type || '').toLowerCase() === 'address';
    },

    caseManualStartNodeActionTraySelectedNodeIsReceivingSide() {
      if (!this.caseManualStartNodeActionTraySelectedNodeIsAddress) return false;
      const node = this.caseInvestigationVisualizerSelectedNode || {};
      const candidate = this.caseManualStartNodeActionTrayCandidate || {};
      const selectedAddressKey = this.caseManualNormalizeAddressKey(node.address || '');
      const endpointAddressKey = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate));
      return Boolean(selectedAddressKey && endpointAddressKey && selectedAddressKey === endpointAddressKey);
    },

    caseManualStartNodeActionTrayHasSingleManualCandidate() {
      const candidate = this.caseManualStartNodeActionTrayCandidate;
      return Boolean(
        this.caseInvestigationVisualizerManualStartMode
          && this.caseInvestigationVisualizerSelectedCount === 1
          && this.caseManualStartNodeActionTraySelectedNodeIsReceivingSide
          && candidate?.caseManualStartCandidate
      );
    },

    caseManualStartSelectedReportBoundaryCandidate() {
      if (!this.caseInvestigationVisualizerManualStartMode || this.caseInvestigationVisualizerSelectedCount !== 1) return null;
      const node = this.caseInvestigationVisualizerSelectedNode || {};
      const candidate = this.caseManualStartNodeActionTrayCandidate;
      if (!candidate?.caseManualStartCandidate || !this.isCaseManualStartPathEndingCandidate(candidate)) return null;
      const nodeType = this.caseManualNormalizeText(node.type || '').toLowerCase();
      if (nodeType === 'address') {
        const selectedAddressKey = this.caseManualNormalizeAddressKey(node.address || '');
        const endpointAddressKey = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate));
        if (!selectedAddressKey || !endpointAddressKey || selectedAddressKey !== endpointAddressKey) return null;
      }
      if (nodeType && !['address', 'transaction', 'tx'].includes(nodeType)) return null;
      return candidate;
    },

    caseManualStartSelectedCandidateAcceptedReportBoundary() {
      const candidate = this.caseManualStartSelectedReportBoundaryCandidate;
      if (!candidate?.caseManualStartCandidate) return false;
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate?.(candidate) || [];
      return this.caseManualStartCandidateHasAcceptedReportBoundary?.(candidate, currentRow) === true;
    },

    caseManualStartSelectedCandidateReportBoundaryActionVisible() {
      const candidate = this.caseManualStartSelectedReportBoundaryCandidate;
      if (!candidate?.caseManualStartCandidate) return false;
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate?.(candidate) || [];
      return this.caseManualStartCandidateHasAcceptedReportBoundary?.(candidate, currentRow) === true
        || this.caseManualStartCandidateCanBeAcceptedAsReportBoundary?.(candidate, currentRow) === true;
    },

    caseManualStartSelectedCandidateCanToggleReportBoundary() {
      const candidate = this.caseManualStartSelectedReportBoundaryCandidate;
      if (!candidate?.caseManualStartCandidate) return false;
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate?.(candidate) || [];
      const accepted = this.caseManualStartCandidateHasAcceptedReportBoundary?.(candidate, currentRow) === true;
      const eligible = this.caseManualStartCandidateCanBeAcceptedAsReportBoundary?.(candidate, currentRow) === true;
      return Boolean(
        (accepted || eligible)
          && !this.caseInvestigationVisualizerInteractionLocked
          && !this.caseManualStartViewportAutoActive
          && !this.caseManualStartAllFlowsAutoActive
          && !this.caseManualStartHopInspectorRunning
          && !this.caseManualStartHopInspectorTraceLoading
      );
    },

    caseManualStartReportBoundaryConfirmationCount() {
      return Array.isArray(this.caseManualStartReportBoundaryConfirmation?.candidates)
        ? this.caseManualStartReportBoundaryConfirmation.candidates.length
        : 0;
    },

    caseManualStartReportBoundaryConfirmationRows() {
      const candidates = Array.isArray(this.caseManualStartReportBoundaryConfirmation?.candidates)
        ? this.caseManualStartReportBoundaryConfirmation.candidates
        : [];
      return candidates.map((candidate, index) => {
        const row = this.findCurrentCaseManualStartMainRowForCandidate?.(candidate) || candidate?.rawRow || candidate?.row || [];
        const reverse = this.caseManualStartCandidateIsReverse(candidate);
        const pathLabel = this.caseManualNormalizeText(candidate?.pathLabel || row?.[CASE_MANUAL_START_COL.PATH_LABEL] || '');
        const address = this.caseManualNormalizeText(this.caseManualStartCandidateEndpointAddress({ ...candidate, row, rawRow: row }));
        const attributionColumn = reverse ? CASE_MANUAL_START_COL.FROM_ATTRIBUTION : CASE_MANUAL_START_COL.ATTRIBUTION;
        const attribution = this.caseManualCleanAttributionLabel(row?.[attributionColumn] || (reverse ? candidate?.fromAttributionLabel : (candidate?.toAttributionLabel || candidate?.attribution)) || '') || 'Unknown';
        return {
          key: this.buildCaseManualStartHopInspectorCandidateKey(candidate) || `${pathLabel}|${address}|${index}`,
          pathLabel: pathLabel || `Ending ${index + 1}`,
          address: address ? this.caseManualShorten(address, 12, 10) : 'Address unavailable',
          attribution,
          direction: reverse ? 'Reverse' : 'Forward'
        };
      });
    },

    caseManualStartReportBoundaryConfirmationCanApply() {
      return Boolean(
        this.caseManualStartReportBoundaryConfirmationCount > 0
          && this.caseManualStartReportBoundaryConfirmation?.acknowledged
          && !this.caseManualStartReportBoundaryConfirmation?.applying
      );
    },

    caseManualStartNodeActionTrayToolActionsReady() {
      return Boolean(
        this.caseManualStartNodeActionTrayHasSingleManualCandidate
          && this.caseManualStartHopInspectorCanAct
          && !this.caseInvestigationVisualizerInteractionLocked
          && !this.caseManualStartViewportAutoActive
          && !this.caseManualStartAllFlowsAutoActive
          && !this.caseManualStartHopInspectorTraceLoading
      );
    },

    caseManualStartNodeActionTrayItems() {
      const candidate = this.caseManualStartNodeActionTrayCandidate || {};
      const hasSingle = this.caseManualStartNodeActionTrayHasSingleManualCandidate;
      const locked = Boolean(this.caseInvestigationVisualizerInteractionLocked || this.caseManualStartHopInspectorRunning || this.caseManualStartHopInspectorTraceLoading || this.caseManualStartViewportAutoActive || this.caseManualStartAllFlowsAutoActive);
      const checksReady = Boolean(this.caseManualStartHopInspectorChecksReady);
      const isPathEnding = Boolean(candidate?.caseManualStartCandidate && this.isCaseManualStartPathEndingCandidate(candidate));
      const needsChoice = this.caseManualStartCandidateRequiresChoiceBeforeSolve?.(candidate) === true;
      const canToolAct = Boolean(this.caseManualStartNodeActionTrayToolActionsReady && !needsChoice);
      const selectedLabel = candidate.pathLabel
        ? `Path ${candidate.pathLabel}`
        : (candidate.hash ? this.caseManualShorten(candidate.hash, 10, 8) : 'the selected node');
      const unavailableTitle = !hasSingle
        ? 'Select exactly one receiving address/path-ending node in the Manual Solve viewport to use this action tray.'
        : (locked
          ? 'Manual Solve is busy or locked; stop the current operation before using the action tray.'
          : (needsChoice
            ? `${this.caseManualStartOutputChoiceButtonLabel} is required before Tool 88 actions unlock for this node.`
            : (!checksReady
              ? `Run Tool 88 checks for ${selectedLabel} before using trace, end, swap, or bridge tray actions.`
              : 'This tray action is not currently available.')));
      const toolTitle = action => canToolAct
        ? `${action} for ${selectedLabel}.`
        : unavailableTitle;
      const manualTitle = hasSingle && !locked && isPathEnding
        ? `Mark ${selectedLabel} as requiring further manual work, write the note to Main Data, and remove it from unresolved Manual Solve endings.`
        : (hasSingle && !locked
          ? 'Select a current Path ends / unresolved-ending node to mark it as requiring further manual work.'
          : unavailableTitle);
      const acceptedReportBoundary = Boolean(
        candidate?.caseManualStartCandidate
          && this.caseManualStartCandidateHasAcceptedReportBoundary(candidate)
      );
      const reportBoundaryEligible = Boolean(
        candidate?.caseManualStartCandidate
          && this.caseManualStartCandidateCanBeAcceptedAsReportBoundary(candidate)
      );
      const reportBoundaryTitle = hasSingle && !locked && (acceptedReportBoundary || reportBoundaryEligible)
        ? (acceptedReportBoundary
          ? `Reopen ${selectedLabel} for further analysis. The report-boundary disclosure will be removed, while the manual-work warning remains.`
          : `Accept ${selectedLabel} as an incomplete report boundary. The report will disclose that further tracing is still recommended.`)
        : (hasSingle && !locked
          ? 'This action is available only for a Path ends node that is unresolved or explicitly marked for further manual work.'
          : unavailableTitle);
      const attributionTitle = hasSingle && !locked
        ? `Change Column K attribution for every Main Data row where the selected address is the receiving address, then refresh the Manual Solve visual label.`
        : unavailableTitle;
      const monitoringPayload = this.caseAddressMonitoringPayloadForCandidate(candidate, { createdFrom: 'case_buddy_manual_solve_tray' });
      const monitoringTitle = monitoringPayload && !locked
        ? `Add the dormant funds at ${selectedLabel} to Address Monitoring.`
        : (hasSingle && !locked
          ? 'This selected node does not contain a recognized dormant funds amount to monitor.'
          : unavailableTitle);
      return [
        {
          key: 'trace-through',
          symbol: '↪',
          label: 'Trace preview',
          title: this.caseManualStartTraceAnywayBlocked ? this.caseManualStartTraceAnywayBlockedMessage : toolTitle('Force trace-through preview'),
          disabled: !canToolAct || this.caseManualStartTraceAnywayBlocked,
          recommended: this.caseManualStartHopInspectorActionIsRecommended('trace-through')
        },
        {
          key: 'path-ending',
          symbol: '⊣',
          label: 'End path and apply',
          title: toolTitle('End path and apply Tool 88 path-ending notes'),
          disabled: !canToolAct,
          recommended: this.caseManualStartHopInspectorActionIsRecommended('path-ending')
        },
        {
          key: 'swap',
          symbol: '⇄',
          label: 'Solve as swap',
          title: toolTitle('Solve as swap and preview/apply'),
          disabled: !canToolAct,
          recommended: this.caseManualStartHopInspectorActionIsRecommended('swap')
        },
        {
          key: 'bridge',
          symbol: '⛓',
          label: 'Solve as bridge',
          title: toolTitle('Solve as bridge and preview/apply'),
          disabled: !canToolAct,
          recommended: this.caseManualStartHopInspectorActionIsRecommended('bridge')
        },
        {
          key: 'manual-work',
          symbol: '⚑',
          label: 'Mark as requiring further manual work',
          title: manualTitle,
          disabled: !hasSingle || locked || !isPathEnding,
          secondary: true
        },
        {
          key: acceptedReportBoundary ? 'reopen-report-boundary' : 'accept-report-boundary',
          symbol: acceptedReportBoundary ? '↺' : '✓',
          label: acceptedReportBoundary ? 'Reopen for analysis' : 'Accept as report boundary',
          title: reportBoundaryTitle,
          disabled: !hasSingle || locked || (!acceptedReportBoundary && !reportBoundaryEligible),
          secondary: true
        },
        {
          key: 'add-monitoring',
          symbol: '⏱',
          label: 'Add to monitoring',
          title: monitoringTitle,
          disabled: !monitoringPayload || locked,
          secondary: true
        },
        {
          key: 'change-attribution',
          symbol: 'K',
          label: 'Change attribution',
          title: attributionTitle,
          disabled: !hasSingle || locked,
          secondary: true
        }
      ];
    },

    caseManualStartHopInspectorTraceOverrideNeeded() {
      const snapshot = this.caseManualStartHopInspectorSnapshot || {};
      const candidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || {};
      if (candidate?.caseManualStartCandidate && this.caseManualStartCandidateHasMeaningfulEndpointAttribution(candidate)) return false;
      return Boolean(
        this.caseManualStartHopInspectorSnapshotLooksUnknownServicePathEndingForCandidate(snapshot, candidate) ||
        this.caseManualStartHopInspectorRowsContainUnknownServicePathEndingEvidenceForCandidate(this.caseManualStartHopInspectorOutputRows, candidate) ||
        this.caseManualLooksLikeUnknownServicePathEnding(candidate) ||
        this.caseManualStartHopInspectorHasPendingUnknownServicePathEnding(candidate)
      );
    },

    caseManualStartHopInspectorPreviewRequiresServiceOverride() {
      return Boolean(
        this.caseManualStartHopInspectorPreviewServiceOverride ||
        this.caseManualStartHopInspectorSnapshotHasManualServiceOverride(this.caseManualStartHopInspectorPreviewSnapshot || {}) ||
        this.caseManualStartHopInspectorSnapshotHasManualServiceOverride(this.caseManualStartHopInspectorSnapshot || {})
      );
    },

    caseManualStartHopInspectorStagedOverrideRequiresPreviewAck() {
      return Boolean(
        this.caseManualStartHopInspectorLastTraceServiceOverride &&
        this.caseManualStartHopInspectorHasOutputRows &&
        !this.caseManualStartHopInspectorPreviewApplied
      );
    },

    caseManualStartHopInspectorPreviewTitle() {
      if (this.caseManualStartHopInspectorPreviewIsDormant) return 'Dormant funds detected';
      const action = this.caseManualNormalizeText(this.caseManualStartHopInspectorPreviewAction || this.caseManualStartHopInspectorPreviewSnapshot?.integrationAction || '').toLowerCase();
      if (/swap/iu.test(action)) return 'Swap preview';
      if (/bridge/iu.test(action)) return 'Bridge preview';
      if (/path[-_\s]*ending|path_end/iu.test(action)) return 'Path-ending preview';
      if (/recommended/iu.test(action)) return 'Recommended action preview';
      return 'Trace preview';
    },

    caseManualStartHopInspectorConfirmPreviewButtonLabel() {
      const dormant = this.caseManualStartHopInspectorPreviewDormantResult;
      if (dormant) return this.caseManualDormantResultIsPartial(dormant)
        ? 'Apply continuations and dormant remainder'
        : 'Apply dormant path ending';
      const action = this.caseManualNormalizeText(this.caseManualStartHopInspectorPreviewAction || this.caseManualStartHopInspectorPreviewSnapshot?.integrationAction || '').toLowerCase();
      if (/swap/iu.test(action)) return 'Confirm swap + apply';
      if (/bridge/iu.test(action)) return 'Confirm bridge + apply';
      if (/path[-_\s]*ending|path_end/iu.test(action)) return 'Confirm path ending + apply';
      return this.caseManualStartHopInspectorPreviewRequiresServiceOverride
        ? 'Acknowledge warning + apply trace'
        : 'Confirm trace-through + apply';
    },

    caseManualStartHopInspectorCheckButtonLabel() {
      if (this.caseManualStartHopInspectorRunning || this.caseManualStartHopInspectorAutoCheckQueuedKey || this.caseManualStartHopInspectorAutoCheckInFlightKey) return 'Checking…';
      if (this.caseManualStartHopInspectorChecksReady) return 'Refresh checks';
      return 'Run checks';
    },

    caseManualStartHopInspectorHasOutputRows() {
      return Array.isArray(this.caseManualStartHopInspectorOutputRows) && this.caseManualStartHopInspectorOutputRows.length > 0;
    },

    caseManualStartHopInspectorStyle() {
      const position = this.caseManualStartHopInspectorPosition || {};
      const x = Number(position.x);
      const y = Number(position.y);
      if (Number.isFinite(x) && Number.isFinite(y)) {
        return { left: `${Math.max(0, x)}px`, top: `${Math.max(0, y)}px`, right: 'auto' };
      }
      return { right: '18px', top: '18px' };
    },

    caseManualStartHopInspectorCandidateLabel() {
      const candidate = this.caseManualStartHopInspectorSelectedCandidate || {};
      const path = candidate.pathLabel ? `Path ${candidate.pathLabel}` : 'Selected ending';
      const hash = candidate.hash || candidate.txHash || candidate.transactionHash || '';
      return hash ? `${path} · ${this.caseManualShorten(hash, 10, 8)}` : path;
    },

    caseManualStartHopInspectorQuickAutoMode() {
      return Boolean(
        this.caseManualStartQuickLookupMode
          && this.caseManualStartHopInspectorOpen
          && (this.caseManualStartViewportAutoActive || this.caseManualStartAllFlowsAutoActive)
      );
    },

    caseManualStartHopInspectorQuickAutoPhase() {
      if (!this.caseManualStartHopInspectorQuickAutoMode) return 'idle';
      if (this.caseManualStartHopInspectorStopRequested) return 'stopping';
      if (this.caseManualStartViewportWorkIndicator?.finishing) return 'resolved';
      if (this.caseManualStartViewportWorkIndicator?.active) return 'applying';
      if (this.caseManualStartHopInspectorTraceLoading) return 'fetching';
      if (this.caseManualStartHopInspectorPreviewOpen) return 'mapping';
      if (
        this.caseManualStartHopInspectorRunning
          || this.caseManualStartHopInspectorAutoCheckQueuedKey
          || this.caseManualStartHopInspectorAutoCheckInFlightKey
      ) return 'checking';
      if (this.caseManualStartHopInspectorChecksReady) return 'deciding';
      return 'preparing';
    },

    caseManualStartHopInspectorQuickAutoPhaseClass() {
      return `is-quick-auto-phase-${this.caseManualStartHopInspectorQuickAutoPhase}`;
    },

    caseManualStartHopInspectorQuickAutoTitle() {
      const phase = this.caseManualStartHopInspectorQuickAutoPhase;
      if (phase === 'stopping') return 'Finishing the current request';
      if (phase === 'resolved') return 'Path updated';
      if (phase === 'applying') return 'Updating the flow';
      if (phase === 'fetching') return 'Following the transaction history';
      if (phase === 'mapping') return 'Mapping the next movement';
      if (phase === 'deciding') return 'Choosing the next safe action';
      if (phase === 'checking') return 'Checking this path';
      return 'Preparing this path';
    },

    caseManualStartHopInspectorQuickAutoDetail() {
      const phase = this.caseManualStartHopInspectorQuickAutoPhase;
      const candidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || {};
      const reverse = candidate?.caseManualStartCandidate && this.caseManualStartCandidateIsReverse(candidate);
      const recommendation = this.caseManualNormalizeText(this.caseManualStartHopInspectorRecommendationTitle || '');
      const lower = recommendation.toLowerCase();

      if (phase === 'stopping') return 'Waiting for a safe stopping point.';
      if (phase === 'resolved') return 'The graph has been refreshed with the completed action.';
      if (phase === 'applying') {
        const action = this.caseManualStartViewportWorkIndicatorActionClass(this.caseManualStartViewportWorkIndicator?.action || '');
        if (action === 'swap') return 'Applying the reconstructed swap result.';
        if (action === 'bridge') return 'Connecting the bridge result.';
        if (action === 'path-ending') return 'Recording the path ending.';
        return 'Adding the solved hop to the graph.';
      }
      if (phase === 'fetching') {
        const progress = this.caseManualStartHopInspectorTraceProgressNormalized || {};
        const fetched = Number(progress.fetched) || 0;
        const expected = Number(progress.expected) || 0;
        return expected > 0 ? `${fetched} of ${expected}${progress.assumedExpected ? ' expected' : ''} records checked.` : 'Retrieving the records needed for this hop.';
      }
      if (phase === 'mapping') {
        const count = this.caseManualStartHopInspectorQuickAutoPreviewDiagram.nodes.length;
        return count ? `${count} related transaction${count === 1 ? '' : 's'} mapped for this action.` : 'Organizing the returned transactions into the flow.';
      }
      if (phase === 'checking') return 'Reviewing activity, attribution and transaction signals.';

      if (/bitcoin input/iu.test(lower)) return 'Following the selected Bitcoin input to its earlier source.';
      if (/poison/iu.test(lower)) return 'Reviewing a likely address-poisoning transfer.';
      if (/bridge/iu.test(lower)) return reverse ? 'Checking the earlier side of a bridge movement.' : 'Checking the destination side of a bridge movement.';
      if (/swap/iu.test(lower)) return reverse ? 'Reconstructing the asset held before the swap.' : 'Following the asset produced by the swap.';
      if (/path ending|mark as path ending|end likely/iu.test(lower)) {
        const label = recommendation.replace(/^Mark as path ending:\s*/iu, '').replace(/^End\s+/iu, '');
        return label ? `Considering ${label.charAt(0).toLowerCase()}${label.slice(1)} as the ending.` : 'Checking whether this is a meaningful ending.';
      }
      if (/trace backward|trace through|follow/iu.test(lower)) return reverse ? 'Following the funds to the previous source.' : 'Following the funds to the next transaction.';
      return reverse ? 'Working backward through the source of funds.' : 'Working forward through the movement of funds.';
    },

    caseManualStartHopInspectorQuickAutoProgressPercent() {
      const phase = this.caseManualStartHopInspectorQuickAutoPhase;
      if (phase === 'resolved') return 100;
      if (phase === 'applying' || phase === 'stopping') return 92;
      if (phase === 'mapping') return 78;
      if (phase === 'deciding') return 68;
      if (phase === 'fetching') {
        const raw = Number(this.caseManualStartHopInspectorTraceProgressPercentForAria);
        const normalized = Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 0;
        return Math.round(28 + (normalized * 0.44));
      }
      if (phase === 'checking') return 26;
      return 12;
    },

    caseManualStartHopInspectorQuickAutoStages() {
      const phase = this.caseManualStartHopInspectorQuickAutoPhase;
      const stages = [
        { key: 'inspect', label: 'Inspect path' },
        { key: 'research', label: 'Fetch history' },
        { key: 'decide', label: 'Choose action' },
        { key: 'apply', label: 'Update graph' }
      ];
      const activeIndexByPhase = {
        idle: 0,
        preparing: 0,
        checking: 0,
        fetching: 1,
        mapping: 2,
        deciding: 2,
        applying: 3,
        stopping: 3,
        resolved: 4
      };
      const activeIndex = activeIndexByPhase[phase] ?? 0;
      return stages.map((stage, index) => ({
        ...stage,
        state: phase === 'resolved' || index < activeIndex
          ? 'complete'
          : (index === activeIndex ? 'active' : 'pending')
      }));
    },

    caseManualStartHopInspectorQuickAutoPreviewDiagram() {
      if (!this.caseManualStartHopInspectorPreviewOpen) return { nodes: [], edges: [], overflowCount: 0 };
      const allItems = Array.isArray(this.caseManualStartHopInspectorPreviewItems)
        ? this.caseManualStartHopInspectorPreviewItems
        : [];
      const maxVisible = 9;
      const items = allItems.slice(0, maxVisible);
      if (!items.length) return { nodes: [], edges: [], overflowCount: 0 };

      const left = 18;
      const right = 282;
      const centerY = 44;
      const span = items.length > 1 ? (right - left) / (items.length - 1) : 0;
      const nodes = items.map((item, index) => {
        let y = centerY;
        if (item.isSwapInputLeg) y = 28;
        else if (item.isSwapOutputLeg) y = 60;
        else if (item.isBranch) y = index % 2 ? 25 : 63;
        else if (item.isPathEnding) y = 58;
        else if (index % 3 === 1) y = 38;
        else if (index % 3 === 2) y = 50;

        const kind = item.isDeposit
          ? 'deposit'
          : item.isSwapLeg
            ? 'swap'
            : item.isBranch
              ? 'branch'
              : item.isPathEnding
                ? 'ending'
                : item.isNew
                  ? 'new'
                  : 'transaction';
        const shape = kind === 'deposit' || kind === 'ending'
          ? 'circle'
          : kind === 'branch'
            ? 'diamond'
            : kind === 'swap'
              ? 'swap'
              : 'rect';
        const title = [item.title, item.primaryAmount, item.tokenSymbol, item.shortHash].filter(Boolean).join(' · ');
        return {
          key: item.key || `quick-preview-${index}`,
          x: items.length === 1 ? 150 : left + (index * span),
          y,
          kind,
          shape,
          title: title || 'Related transaction',
          directionClass: item.directionClass || ''
        };
      });
      const edges = nodes.slice(1).map((node, index) => {
        const previous = nodes[index];
        const midpoint = (previous.x + node.x) / 2;
        return {
          key: `${previous.key}-to-${node.key}`,
          path: `M ${previous.x + 8} ${previous.y} C ${midpoint} ${previous.y}, ${midpoint} ${node.y}, ${node.x - 8} ${node.y}`
        };
      });
      return { nodes, edges, overflowCount: Math.max(0, allItems.length - items.length) };
    },

    caseManualStartHopInspectorQuickAutoPreviewCaption() {
      const summary = this.caseManualNormalizeText(this.caseManualStartHopInspectorPreviewSummary || '');
      if (!summary || /no transaction rows/iu.test(summary)) return 'Returned activity is being arranged into the graph.';
      return summary.replace(/Tool 88/giu, 'Flow Solver');
    },

    caseManualStartHopInspectorRecommendationTitle() {
      const snapshot = this.caseManualStartHopInspectorSnapshot || {};
      const candidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || {};
      const outputRows = Array.isArray(this.caseManualStartHopInspectorOutputRows) ? this.caseManualStartHopInspectorOutputRows : [];
      const meaningfulEndpointLabel = candidate?.caseManualStartCandidate ? this.caseManualCandidateMeaningfulEndpointAttributionLabel(candidate) : '';
      if (this.caseManualStartCandidateHasBtcReverseInputSelection(candidate)) return 'Trace backward through selected Bitcoin input';
      if (this.caseManualStartCandidateIsPostBridgeTraceContinuation(candidate, snapshot)) return 'Trace through post-bridge flow';
      const bridgeMatch = this.caseManualBridgeMatchForManualStartCandidate(candidate, snapshot);
      if (bridgeMatch?.isMatch && this.isCaseManualStartPathEndingCandidate(candidate)) {
        return `Solve bridge: ${this.caseManualShortAttributionLabel(bridgeMatch.canonicalName || bridgeMatch.matchedAlias || meaningfulEndpointLabel || 'Bridge', 42)}`;
      }
      if (this.caseManualStartAddressPoisoningEvidenceForCandidate(candidate, { snapshot })?.isMatch) {
        return 'End likely address-poisoning dust transfer';
      }
      if (meaningfulEndpointLabel && this.isCaseManualStartPathEndingCandidate(candidate)) {
        return `Mark as path ending: ${this.caseManualShortAttributionLabel(meaningfulEndpointLabel, 42)}`;
      }
      if (
        this.caseManualStartHopInspectorSnapshotLooksUnknownServicePathEndingForCandidate(snapshot, candidate)
          || this.caseManualLooksLikeUnknownServicePathEnding(candidate)
          || this.caseManualStartHopInspectorHasPendingUnknownServicePathEnding(candidate)
          || this.caseManualStartHopInspectorRowsContainUnknownServicePathEndingEvidenceForCandidate(outputRows, candidate)
      ) {
        return 'Mark as path ending: Unknown service / hot-wallet pattern';
      }
      return snapshot?.decision?.title
        || snapshot?.decision?.summary
        || 'Run Tool 88 checks to get a recommendation.';
    },


    caseManualStartHopInspectorPreviewDormantResult() {
      const candidate = this.caseManualStartHopInspectorPreviewCandidate
        || this.caseManualStartHopInspectorSelectedCandidate
        || this.caseManualStartHopInspectorCandidate
        || null;
      if (candidate && this.caseManualStartCandidateIsReverse(candidate)) return null;
      return this.caseManualExtractDormantResultFromSnapshot(this.caseManualStartHopInspectorPreviewSnapshot || this.caseManualStartHopInspectorSnapshot || {});
    },

    caseManualStartHopInspectorPreviewIsDormant() {
      return !!this.caseManualStartHopInspectorPreviewDormantResult;
    },

    caseManualStartHopInspectorPreviewBody() {
      const dormant = this.caseManualStartHopInspectorPreviewDormantResult;
      if (dormant) {
        const amountText = this.caseManualDormantAmountText(dormant);
        const token = this.caseManualNormalizeText(dormant.token || '');
        const amountToken = amountText && token && !new RegExp(`\\b${this.caseManualEscapeRegExp(token)}\\b`, 'iu').test(amountText)
          ? `${amountText} ${token}`
          : amountText;
        if (this.caseManualDormantResultIsPartial(dormant)) {
          return `Tool 88 found qualifying exits for part of the followed funds and also found a dormant remainder. A portion of the funds remains at the receiving address (${amountToken}).`;
        }
        if (this.caseManualDormantResultIsPartial(dormant)) {
          const exits = Array.isArray(dormant.validExitTransactions) ? dormant.validExitTransactions.length : Number(dormant.validExitCount) || 0;
          return `Tool 88 found the funds of interest at the receiving address and found ${exits || 'one or more'} qualifying exit${exits === 1 ? '' : 's'}, with a dormant remainder. A portion of the funds remains at the receiving address (${amountToken}).`;
        }
        return `Tool 88 found the funds of interest at the receiving address and found no qualifying exits for the followed funds. A portion of the funds remains at the receiving address (${amountToken}).`;
      }
      return this.caseManualStartHopInspectorPreviewStatus || 'Review these provider rows before applying the hop to the visual and case data.';
    },

    caseManualStartHopInspectorPreviewHasRows() {
      return Array.isArray(this.caseManualStartHopInspectorPreviewRows) && this.caseManualStartHopInspectorPreviewRows.length > 0;
    },

    caseManualStartHopInspectorPreviewItems() {
      return this.buildCaseManualStartHopInspectorPreviewItems(this.caseManualStartHopInspectorPreviewRows);
    },

    caseManualStartHopInspectorPreviewSummary() {
      const dormant = this.caseManualStartHopInspectorPreviewDormantResult;
      if (dormant) {
        const address = dormant.receivingAddress || dormant.dormantAddress || dormant.addressOfInterest || '';
        const amount = this.caseManualDormantAmountText(dormant);
        return [address ? this.caseManualShorten(address, 10, 8) : 'Receiving address', amount || 'Dormant funds'].filter(Boolean).join(' · ');
      }
      const items = this.caseManualStartHopInspectorPreviewItems || [];
      if (!items.length) return 'No transaction rows are staged for preview yet.';
      const newCount = items.filter(item => item.isNew).length;
      const branchCount = items.filter(item => item.isBranch).length;
      const unresolvedCount = items.filter(item => item.isPathEnding).length;
      const bits = [`${items.length} transaction${items.length === 1 ? '' : 's'}`];
      if (newCount) bits.push(`${newCount} new`);
      if (branchCount) bits.push(`${branchCount} branch${branchCount === 1 ? '' : 'es'}`);
      if (unresolvedCount) bits.push(`${unresolvedCount} unresolved ending${unresolvedCount === 1 ? '' : 's'}`);
      return bits.join(' · ');
    },

    caseManualStartHopInspectorPreviewEvidenceLines() {
      return this.buildCaseManualStartHopInspectorPreviewEvidenceLines(this.caseManualStartHopInspectorPreviewSnapshot || this.caseManualStartHopInspectorSnapshot || {});
    },

    caseManualStartHopInspectorTraceProgressNormalized() {
      const progress = this.caseManualStartHopInspectorTraceProgress || {};
      const fetchedRaw = Number(progress.fetched);
      const expectedRaw = Number(progress.expected);
      const fetched = Number.isFinite(fetchedRaw) ? Math.max(0, Math.trunc(fetchedRaw)) : 0;
      const expected = Number.isFinite(expectedRaw) && expectedRaw > 0 ? Math.max(1, Math.trunc(expectedRaw)) : 100;
      const assumedExpected = progress.assumedExpected !== false && (!Number.isFinite(expectedRaw) || expectedRaw <= 0 || progress.assumedExpected === true);
      const percent = expected > 0 ? (fetched / expected) * 100 : 0;
      return { fetched, expected, assumedExpected, percent: Number.isFinite(percent) ? percent : 0, status: this.caseManualNormalizeText(progress.status || 'preparing'), message: this.caseManualNormalizeText(progress.message || '') };
    },

    caseManualStartHopInspectorTraceProgressPercentForAria() {
      return Math.max(0, Math.min(100, Math.round(this.caseManualStartHopInspectorTraceProgressNormalized.percent)));
    },

    caseManualStartHopInspectorTraceProgressBarWidth() {
      const percent = this.caseManualStartHopInspectorTraceProgressNormalized.percent;
      if (!Number.isFinite(percent) || percent <= 0) return 8;
      return Math.max(8, Math.min(100, percent));
    },

    caseManualStartHopInspectorTraceProgressPercentText() {
      const percent = this.caseManualStartHopInspectorTraceProgressNormalized.percent;
      if (!Number.isFinite(percent)) return 'Fetching';
      return `${Math.max(0, Math.round(percent))}%`;
    },

    caseManualStartHopInspectorTraceProgressCountText() {
      const progress = this.caseManualStartHopInspectorTraceProgressNormalized;
      const expectedLabel = progress.assumedExpected ? `~${this.caseManualFormatInteger(progress.expected)}` : this.caseManualFormatInteger(progress.expected);
      return `${this.caseManualFormatInteger(progress.fetched)} / ${expectedLabel} fetched`;
    },

    caseManualStartHopInspectorTraceProgressMessage() {
      const progress = this.caseManualStartHopInspectorTraceProgressNormalized;
      if (this.caseManualStartHopInspectorStopRequested) return 'Stop requested. Case Buddy is waiting for Tool 88 to halt at the next safe provider-batch boundary.';
      return progress.message || 'Tool 88 is fetching provider transactions and preparing a trace preview.';
    },

    caseHopSolverManualStartMode() {
      return this.caseHopSolverCandidate?.caseManualStartCandidate === true || this.caseInvestigationVisualizerManualStartMode;
    },

    caseHopSolverManualStartHasOutputRows() {
      return this.caseHopSolverManualStartMode && Array.isArray(this.caseHopSolverOutputRows) && this.caseHopSolverOutputRows.length > 0;
    },


    caseManualStartDormantVisualMarkers() {
      if (!this.caseInvestigationVisualizerManualStartMode) return [];
      const nodes = Array.isArray(this.caseInvestigationVisualizerVisibleNodes) ? this.caseInvestigationVisualizerVisibleNodes : [];
      const markers = [];
      const seen = new Set();
      nodes.forEach((node) => {
        if (!node || node.type !== 'address') return;
        const candidate = node.caseCandidate || node.candidate || null;
        const dormant = this.caseManualBuildDormantResultFromCandidate(candidate);
        if (!dormant) return;
        const nodeAddress = this.caseManualNormalizeAddressKey(node.address || node.toAddress || '');
        const dormantAddress = this.caseManualNormalizeAddressKey(dormant.receivingAddress || dormant.dormantAddress || dormant.addressOfInterest || '');
        if (dormantAddress && nodeAddress && dormantAddress !== nodeAddress) return;
        const key = `${node.id}|${dormantAddress || dormant.transactionHash || markers.length}`;
        if (seen.has(key)) return;
        seen.add(key);
        const amountText = this.caseManualDormantAmountText(dormant);
        const label = amountText ? this.caseManualShorten(amountText, 14, 8) : 'Dormant';
        const partial = this.caseManualDormantResultIsPartial(dormant);
        markers.push({
          key,
          x: Number(node.x) + this.caseFlowVisualAddressRadius + (partial ? 14 : 42),
          y: Number(node.y) || 0,
          partial,
          glowX: partial ? -this.caseFlowVisualAddressRadius - 14 : 0,
          glowRadius: Math.max(54, this.caseFlowVisualAddressRadius + 16),
          bagTransform: partial ? 'translate(18 34)' : 'translate(42 35)',
          labelX: partial ? 40 : 66,
          labelY: partial ? 4 : 5,
          label,
          title: partial
            ? `A dormant remainder stays at ${dormant.receivingAddress || node.address || 'this address'}${amountText ? ` (${amountText})` : ''}, while the path continues through the next transaction.`
            : `Dormant funds remain at ${dormant.receivingAddress || node.address || 'this address'}${amountText ? ` (${amountText})` : ''}.`
        });
      });
      return markers;
    },

    caseManualStartSelectedDormantCandidate() {
      const selected = this.caseInvestigationVisualizerSelectedCandidate
        || this.caseInvestigationVisualizerSelectedNode?.caseCandidate
        || this.caseInvestigationVisualizerSelectedNode?.candidate
        || this.caseManualStartHopInspectorSelectedCandidate
        || null;
      return this.caseManualBuildDormantResultFromCandidate(selected) ? selected : null;
    },

    caseManualStartFurtherManualWorkVisualMarkers() {
      if (!this.caseInvestigationVisualizerManualStartMode) return [];
      const nodes = Array.isArray(this.caseInvestigationVisualizerVisibleNodes) ? this.caseInvestigationVisualizerVisibleNodes : [];
      const markers = [];
      const seen = new Set();
      nodes.forEach((node) => {
        if (!node || node.type !== 'address') return;
        const candidate = node.caseCandidate || node.candidate || null;
        const currentCandidate = this.caseManualStartCandidateWithCurrentMainRow(candidate);
        if (!currentCandidate?.caseManualStartCandidate) return;
        const currentRow = Array.isArray(currentCandidate.rawRow) ? currentCandidate.rawRow : [];
        const continuesAs = this.caseManualNormalizeContinuesAsText(currentRow[CASE_MANUAL_START_COL.CONTINUES_AS] || currentCandidate.continuesAs || '');
        if (!/^Path\s+ends\.?$/iu.test(continuesAs)) return;
        if (!this.caseManualStartCandidateHasFurtherManualWork(currentCandidate, currentRow)) return;
        if (this.caseManualStartCandidateHasOccurrenceSpecificChild(currentCandidate)) return;
        if (this.caseManualStartCandidateAttentionVisualIsDeferred(currentCandidate)) return;
        if (this.caseManualStartCandidateHasAcceptedReportBoundary(currentCandidate, currentRow)) return;
        const key = `${node.id}|${currentCandidate?.rowIndex ?? currentCandidate?.hash ?? markers.length}`;
        if (seen.has(key)) return;
        seen.add(key);
        markers.push({
          key,
          x: Number(node.x) || 0,
          y: Number(node.y) || 0,
          radius: this.caseFlowVisualAddressRadius + 27,
          labelY: this.caseFlowVisualAddressRadius + 46,
          label: 'manual work',
          title: 'Further manual work is required to resolve this path. Path ends.'
        });
      });
      return markers;
    },

    caseManualStartAcceptedReportBoundaryVisualMarkers() {
      if (!this.caseInvestigationVisualizerManualStartMode) return [];
      const nodes = Array.isArray(this.caseInvestigationVisualizerVisibleNodes) ? this.caseInvestigationVisualizerVisibleNodes : [];
      const markers = [];
      const seen = new Set();
      nodes.forEach((node) => {
        if (!node || node.type !== 'address') return;
        const candidate = node.caseCandidate || node.candidate || null;
        if (!this.caseManualStartCandidateHasAcceptedReportBoundary(candidate)) return;
        const key = `${node.id}|${candidate?.rowIndex ?? candidate?.hash ?? markers.length}`;
        if (seen.has(key)) return;
        seen.add(key);
        markers.push({
          key,
          x: Number(node.x) || 0,
          y: Number(node.y) || 0,
          radius: this.caseFlowVisualAddressRadius + 29,
          labelY: this.caseFlowVisualAddressRadius + 48,
          label: 'report boundary',
          title: 'This incomplete ending was accepted as a report boundary. Further manual tracing remains recommended.'
        });
      });
      return markers;
    },

    caseManualStartViewportWorkIndicatorActive() {
      return Boolean(this.caseInvestigationVisualizerManualStartMode && this.caseManualStartViewportWorkIndicator?.active);
    },

    caseManualStartViewportWorkIndicatorMarker() {
      if (!this.caseManualStartViewportWorkIndicatorActive) return null;
      const state = this.caseManualStartViewportWorkIndicator || {};
      const fallbackCandidate = this.caseManualStartHopInspectorPreviewCandidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || null;
      const candidate = state.candidate && typeof state.candidate === 'object'
        ? state.candidate
        : fallbackCandidate;
      const matchedNode = this.findCaseManualStartViewportWorkIndicatorNode(candidate);
      const anchorX = Number.isFinite(Number(matchedNode?.x))
        ? Number(matchedNode.x)
        : (Number.isFinite(Number(state.anchor?.x)) ? Number(state.anchor.x) : 0);
      const anchorY = Number.isFinite(Number(matchedNode?.y))
        ? Number(matchedNode.y)
        : (Number.isFinite(Number(state.anchor?.y)) ? Number(state.anchor.y) : 0);
      const actionClass = this.caseManualStartViewportWorkIndicatorActionClass(state.action);
      const traceLike = ['trace-through', 'swap', 'bridge', 'recommended'].includes(actionClass);
      const resolveLike = actionClass === 'path-ending';
      const radius = Math.max(36, Number(this.caseFlowVisualAddressRadius) || 42);
      const anchorTargetState = this.getCaseManualStartViewportWorkIndicatorAnchorState(candidate, matchedNode);
      const showContinuationLane = traceLike && !resolveLike && anchorTargetState.anchorIsTarget;
      const startX = anchorX + radius + 18;
      const endX = anchorX + 258;
      const laneY = anchorY;
      const ghostAddressX = anchorX + 258;
      const readableAction = actionClass === 'path-ending'
        ? 'marking this path ending'
        : actionClass === 'swap'
          ? 'applying the swap result'
          : actionClass === 'bridge'
            ? 'applying the bridge result'
            : 'applying the trace result';
      const pathLabel = this.caseManualNormalizeText(candidate?.pathLabel || candidate?.label || 'selected path');
      const address = this.caseManualNormalizeText(this.caseManualStartCandidateEndpointAddress(candidate) || matchedNode?.address || 'selected address');
      return {
        key: `${state.generation || 0}-${state.candidateKey || pathLabel || address || 'manual-work'}`,
        actionClass,
        finishing: Boolean(state.finishing),
        x: anchorX,
        y: anchorY,
        hazeRadius: radius + 36,
        ringRadius: radius + 18,
        showLane: showContinuationLane,
        showResolveStamp: resolveLike,
        lanePath: `M ${startX} ${laneY} L ${endX} ${laneY}`,
        laneSuppressedReason: traceLike && !resolveLike && !anchorTargetState.anchorIsTarget
          ? 'The work marker is anchored to a source/sending address, so Case Buddy is showing only the local working cue.'
          : '',
        anchorAddress: anchorTargetState.anchorAddress,
        candidateTargetAddress: anchorTargetState.targetAddress,
        candidateFromAddress: anchorTargetState.fromAddress,
        anchorIsTargetAddress: anchorTargetState.anchorIsTarget,
        anchorIsFromAddress: anchorTargetState.anchorIsFrom,
        ghostAddressX,
        ghostAddressY: anchorY,
        ghostAddressRadius: Math.max(28, radius * 0.74),
        title: this.caseManualNormalizeText(state.message) || `Case Buddy is ${readableAction} near ${pathLabel}${address ? ` / ${address}` : ''}.`
      };
    },

    rawDataCoverageStageSummary() {
      const coverage = this.rawDataCurrentCoverage;
      const missingCount = this.rawDataImportCurrentMissingCount;
      if (!coverage.totalMainTransactions) return 'No starting/main transactions are ready for raw-data coverage yet.';
      if (missingCount === 0) return 'Each detected starting/main transaction has a matching raw root set.';
      return `${coverage.coveredCount}/${coverage.totalMainTransactions} starting/main transaction${coverage.totalMainTransactions === 1 ? '' : 's'} have matching raw data. ${missingCount} still need raw data or a To Address match.`;
    },

    rawDataImportStageSummary() {
      if (this.rawDataImportParsedRowCount === 0) return this.rawDataCoverageStageSummary;
      if (this.rawDataImportDetectedSetsCount === 0) return 'Rows are staged, but no valid raw root set was detected yet.';
      if (this.rawDataImportRemainingMissingCount === 0) return `After this import, every detected starting/main transaction will have matching raw-data coverage. ${this.rawDataImportMatchedCount} staged root${this.rawDataImportMatchedCount === 1 ? '' : 's'} match current main transactions.`;
      return `${this.rawDataImportDetectedSetsCount} raw set${this.rawDataImportDetectedSetsCount === 1 ? '' : 's'} staged. ${this.rawDataImportMatchedCount} staged root${this.rawDataImportMatchedCount === 1 ? '' : 's'} match current main transactions, and ${this.rawDataImportNewCoverageCount} currently-missing main transaction${this.rawDataImportNewCoverageCount === 1 ? '' : 's'} would be newly covered.`;
    },

    wayFinderHandoffSelectedTargetCount() {
      return (this.wayFinderHandoffTargets || []).filter(target => this.wayFinderHandoffSelectedById?.[target.id] && target.canQueue).length;
    },

    wayFinderHandoffQueueableTargetCount() {
      return (this.wayFinderHandoffTargets || []).filter(target => target.canQueue).length;
    },

    wayFinderHandoffImportableCount() {
      return (this.wayFinderHandoffJobs || []).filter(job => job.canImport).length;
    },

    wayFinderHandoffJobSummary() {
      return (this.wayFinderHandoffJobs || []).reduce((summary, job) => {
        summary.total += 1;
        if (job.jobStatus === 'queued') summary.queued += 1;
        else if (job.jobStatus === 'in_progress') summary.inProgress += 1;
        else if (job.jobStatus === 'completed' && job.caseBuddyImportStatus === 'imported') summary.imported += 1;
        else if (job.jobStatus === 'completed') summary.completed += 1;
        else if (job.jobStatus === 'failed') summary.failed += 1;
        else summary.other += 1;
        if (job.canImport) summary.readyToImport += 1;
        return summary;
      }, { total: 0, queued: 0, inProgress: 0, completed: 0, readyToImport: 0, imported: 0, failed: 0, other: 0 });
    },

    wayFinderHandoffBridgeStatusClass() {
      if (this.wayFinderHandoffConnected) return 'wayfinder-handoff-status--connected';
      if (this.wayFinderHandoffConnectionStatus && this.wayFinderHandoffConnectionStatus !== 'Not checked') return 'wayfinder-handoff-status--error';
      return '';
    },


    tool68PreparationSelectedRows() {
      return this.tool68PreparationRows.filter(row => row.include && row.canProcess);
    },

    tool68PreparationSelectedCount() {
      return this.tool68PreparationSelectedRows.length;
    },

    tool68PreparationProcessedCount() {
      return this.tool68PreparationRows.filter(row => row.processed && !row.hasNewerRawSet).length;
    },

    tool68PreparationUnprocessedCount() {
      return this.tool68PreparationRows.filter(row => row.rawCovered && !row.processed).length;
    },

    tool68PreparationReprocessAvailableCount() {
      return this.tool68PreparationRows.filter(row => row.hasNewerRawSet).length;
    },

    tool68PreparationMissingRawCount() {
      return this.tool68PreparationRows.filter(row => !row.rawCovered).length;
    },

    tool68PreparationNeedsReprocessConfirmation() {
      return this.tool68PreparationSelectedRows.some(row => row.requiresReprocessConfirmation);
    },

    tool68PreparationReadyToRun() {
      return this.tool68PreparationSelectedCount > 0 && (!this.tool68PreparationNeedsReprocessConfirmation || this.tool68PreparationConfirmReprocess) && !this.tool68PreparationRunning;
    },

    tool68PreparationStageSummary() {
      if (this.tool68PreparationRows.length === 0) return 'No starting/main transactions are staged for graph preparation yet.';
      if (this.tool68PreparationMissingRawCount > 0) return `${this.tool68PreparationMissingRawCount} main transaction${this.tool68PreparationMissingRawCount === 1 ? '' : 's'} still need matching raw data before Tool 68 can run.`;
      if (this.tool68PreparationUnprocessedCount === 0 && this.tool68PreparationReprocessAvailableCount === 0) return 'Every detected starting/main transaction already appears to have prepared graph rows in Main Data.';
      if (this.tool68PreparationReprocessAvailableCount > 0) return `${this.tool68PreparationReprocessAvailableCount} processed main transaction${this.tool68PreparationReprocessAvailableCount === 1 ? '' : 's'} have a newer bottom-most raw set available for optional reprocessing.`;
      return `${this.tool68PreparationUnprocessedCount} starting/main transaction${this.tool68PreparationUnprocessedCount === 1 ? '' : 's'} need Tool 68 graph preparation. ${this.tool68PreparationSelectedCount} selected.`;
    },


    addressAttributionAnalysis() {
      return this.buildCaseBuddyRoleAwareAddressAttributionAnalysis(this.getMatrixForLogicalSheet('main'));
    },

    addressAttributionSelectedRows() {
      return this.addressAttributionRows.filter(row => row.include && normalizeAttributionValue(row.attributionDraft));
    },

    addressAttributionSelectedCount() {
      return this.addressAttributionRows.filter(row => row.include).length;
    },

    addressAttributionReadyCount() {
      return this.addressAttributionSelectedRows.length;
    },

    addressAttributionMissingCount() {
      return this.addressAttributionAnalysis.missingCount || 0;
    },

    addressAttributionUnknownCount() {
      return this.addressAttributionRows.filter(row => normalizeAttributionValue(row.attributionDraft).toLowerCase() === 'unknown' || normalizeAttributionValue(row.currentAttribution).toLowerCase() === 'unknown').length;
    },

    addressAttributionCanApply() {
      return !this.addressAttributionRunning && this.addressAttributionReadyCount > 0;
    },

    attributionPathOperationsManuallyComplete() {
      return !!this.caseSupportState.workflowState.manualCompleteByTag?.ATTRIBUTION_PATH_OPERATIONS?.completedAtUtc;
    },

    addressAttributionStageSummary() {
      if (this.addressAttributionRows.length === 0) return this.addressAttributionAnalysis.summary || 'No missing attribution detected.';
      if (this.addressAttributionReadyCount === 0) return `${this.addressAttributionRows.length} address${this.addressAttributionRows.length === 1 ? '' : 'es'} need attribution or Unknown.`;
      return `${this.addressAttributionReadyCount} selected address${this.addressAttributionReadyCount === 1 ? '' : 'es'} ready to apply. ${this.addressAttributionMissingCount} still need completed Column K/Column X coverage.`;
    },

    pathOperationAnalysis() {
      return buildPathOperationAnalysis(this.getMatrixForLogicalSheet('main'), this.getMatrixForLogicalSheet('raw'));
    },

    pathOperationMainGroups() {
      return this.pathOperationAnalysis.mainGroups || [];
    },

    pathOperationSelectedCandidate() {
      const candidates = this.pathOperationCandidates.length > 0 ? this.pathOperationCandidates : (this.pathOperationAnalysis.candidates || []);
      return candidates.find(candidate => candidate.id === this.pathOperationSelectedCandidateId) || null;
    },

    pathOperationFilteredCandidates() {
      const source = this.pathOperationCandidates.length > 0 ? this.pathOperationCandidates : (this.pathOperationAnalysis.candidates || []);
      return source.filter((candidate) => {
        if (this.pathOperationMainFilter && candidate.mainGroupId !== this.pathOperationMainFilter) return false;
        if (this.pathOperationShowPathEndingsOnly && !candidate.isPathEnding) return false;
        return true;
      });
    },

    pathOperationActionLabel() {
      if (this.pathOperationAction === 'bridge') return 'Bridge graft';
      if (this.pathOperationAction === 'graft') return 'Graft';
      return 'Prune';
    },


    pathOperationBridgeServiceDefinitions() {
      const definitions = [
        ...(Array.isArray(CASE_BRIDGE_SERVICE_DEFINITIONS) ? CASE_BRIDGE_SERVICE_DEFINITIONS : []),
        ...(Array.isArray(CASE_BRIDGE_SERVICE_DEFINITIONS_FROM_REGISTRY) ? CASE_BRIDGE_SERVICE_DEFINITIONS_FROM_REGISTRY : []),
      ];
      const seen = new Set();
      return definitions.filter((definition) => {
        const key = normalizeCell(definition?.canonicalName || definition?.name || '').toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },

    pathOperationBridgeSelectedMatch() {
      return findCaseBridgeServiceByCanonicalOrAlias(this.pathOperationBridgeService);
    },

    pathOperationBridgeDetectedMatch() {
      return findBestCaseBridgeServiceMatch(buildBridgeSearchTextFromPathOperationCandidate(this.pathOperationSelectedCandidate));
    },

    pathOperationBridgeActiveMatch() {
      if (this.pathOperationBridgeSelectedMatch.isMatch) return this.pathOperationBridgeSelectedMatch;
      if (!normalizeCell(this.pathOperationBridgeService) && this.pathOperationBridgeDetectedMatch.isMatch) return this.pathOperationBridgeDetectedMatch;
      return createCaseBridgeServiceMatch();
    },

    pathOperationBridgeAssistSummary() {
      if (this.pathOperationBridgeSelectedMatch.isMatch) {
        return `Using the registry profile for ${this.pathOperationBridgeSelectedMatch.canonicalName}.`;
      }
      if (normalizeCell(this.pathOperationBridgeService)) {
        return 'Custom service name entered. Registry links are shown only for known bridge services.';
      }
      if (this.pathOperationBridgeDetectedMatch.isMatch) {
        return `${this.pathOperationBridgeDetectedMatch.canonicalName} appears in the selected row. Use it or type another bridge service.`;
      }
      return 'Known bridge services can provide explorer links or search hints while you solve the bridge.';
    },

    pathOperationBridgeBasis() {
      const candidate = this.pathOperationSelectedCandidate;
      if (!candidate) {
        return {
          valid: false,
          message: 'Select a path-ending row before using the bridge calculator.',
          originalAmountNumber: null,
          fundsFollowedNumber: null,
          leftBoundsNumber: null,
          token: '',
          originalAmountDisplay: '—',
          fundsFollowedDisplay: '—',
          leftBoundsDisplay: '—',
          fundsFollowedRatio: null,
          leftBoundsRatio: null,
          fundsFollowedRatioLabel: 'Column M.',
          leftBoundsRatioLabel: 'Column R/S.'
        };
      }

      const originalAmountNumber = parsePathOperationBridgeNumber(candidate.amount);
      const fundsFollowedNumber = parsePathOperationBridgeNumber(candidate.fundsFollowed);
      const leftBoundsNumber = parsePathOperationBridgeNumber(candidate.leftBoundsNumeric || candidate.leftBoundsDisplay);
      const token = normalizeCell(candidate.token) || extractPathOperationBridgeToken(candidate.fundsFollowed || candidate.leftBoundsDisplay);
      const canRatio = Number.isFinite(originalAmountNumber) && originalAmountNumber > 0;
      const fundsFollowedRatio = canRatio && Number.isFinite(fundsFollowedNumber) ? fundsFollowedNumber / originalAmountNumber : null;
      const leftBoundsRatio = canRatio && Number.isFinite(leftBoundsNumber) ? leftBoundsNumber / originalAmountNumber : null;
      const missing = [];
      if (!canRatio) missing.push('selected-row amount');
      if (!Number.isFinite(fundsFollowedNumber)) missing.push('funds followed');
      if (!Number.isFinite(leftBoundsNumber)) missing.push('left bounds');

      return {
        valid: missing.length === 0,
        message: missing.length
          ? `The calculator needs a numeric ${missing.join(', ')} value from the selected row.`
          : 'Use the same proportions on the post-bridge amount to estimate the new funds-followed and left-bounds values.',
        originalAmountNumber,
        fundsFollowedNumber,
        leftBoundsNumber,
        token,
        originalAmountDisplay: Number.isFinite(originalAmountNumber) ? formatPathOperationBridgeAmount(originalAmountNumber, token) : (normalizeCell(candidate.amount) || '—'),
        fundsFollowedDisplay: normalizeCell(candidate.fundsFollowed) || (Number.isFinite(fundsFollowedNumber) ? formatPathOperationBridgeAmount(fundsFollowedNumber, token) : '—'),
        leftBoundsDisplay: normalizeCell(candidate.leftBoundsDisplay) || normalizeCell(candidate.leftBoundsNumeric) || (Number.isFinite(leftBoundsNumber) ? formatPathOperationBridgeAmount(leftBoundsNumber, token) : '—'),
        fundsFollowedRatio,
        leftBoundsRatio,
        fundsFollowedRatioLabel: fundsFollowedRatio !== null ? `${formatPathOperationBridgePercent(fundsFollowedRatio)} of selected amount` : 'Column M.',
        leftBoundsRatioLabel: leftBoundsRatio !== null ? `${formatPathOperationBridgePercent(leftBoundsRatio)} of selected amount` : 'Column R/S.'
      };
    },

    pathOperationBridgeCalculatorResult() {
      if (this.pathOperationAction !== 'bridge') {
        return { valid: false, message: 'Select Bridge graft to use the proportional bridge calculator.' };
      }

      const basis = this.pathOperationBridgeBasis;
      const receivedNumber = parsePathOperationBridgeNumber(this.pathOperationBridgeReceivedAmount);
      const receivedToken = extractPathOperationBridgeToken(this.pathOperationBridgeReceivedAmount)
        || normalizeCell(this.pathOperationBridgeDestinationToken)
        || normalizeCell(this.pathOperationSelectedCandidate?.token);

      if (!basis.valid) {
        return { valid: false, message: basis.message };
      }
      if (!Number.isFinite(receivedNumber) || receivedNumber <= 0) {
        return {
          valid: false,
          message: 'Enter the amount received on the other side of the bridge, such as 25 ETH.',
          receivedToken
        };
      }

      const followedAmount = receivedNumber * basis.fundsFollowedRatio;
      const leftBoundsAmount = receivedNumber * basis.leftBoundsRatio;
      return {
        valid: true,
        message: 'Calculated from the selected row proportions. Copy these values into WayFinder or your post-bridge raw-data workflow as needed.',
        receivedNumber,
        receivedToken,
        followedAmount,
        leftBoundsAmount,
        followedDisplay: formatPathOperationBridgeAmount(followedAmount, receivedToken),
        leftBoundsDisplay: formatPathOperationBridgeAmount(leftBoundsAmount, receivedToken),
        leftBoundsNumericDisplay: formatPathOperationBridgeNumberOnly(leftBoundsAmount),
        fundsFollowedRatioLabel: formatPathOperationBridgePercent(basis.fundsFollowedRatio),
        leftBoundsRatioLabel: formatPathOperationBridgePercent(basis.leftBoundsRatio),
        kitSummary: `${formatPathOperationBridgeAmount(followedAmount, receivedToken)} / left ${formatPathOperationBridgeNumberOnly(leftBoundsAmount)}`
      };
    },

    pathOperationPrunePreview() {
      return buildPrunePreviewForCandidate(this.getMatrixForLogicalSheet('main'), this.pathOperationSelectedCandidate);
    },

    pathOperationCanApplyPrune() {
      return !this.pathOperationRunning && !!this.pathOperationSelectedCandidate?.canPrune && !!this.pathOperationConfirmApply && this.pathOperationPrunePreview.valid;
    },

    pathOperationCanStageExtension() {
      const candidate = this.pathOperationSelectedCandidate;
      if (this.pathOperationRunning || !candidate || !this.pathOperationConfirmApply) return false;
      if (this.pathOperationAction === 'bridge' && !candidate.canBridge) return false;
      if (this.pathOperationAction === 'graft' && !candidate.canGraft) return false;
      return !!String(this.pathOperationRawTsv || '').trim() || !!this.pathOperationSelectedRawSetId;
    },

    pathOperationStageSummary() {
      const candidate = this.pathOperationSelectedCandidate;
      if (!candidate) return `${this.pathOperationAnalysis.qualifiedCount || 0} qualified output row${(this.pathOperationAnalysis.qualifiedCount || 0) === 1 ? '' : 's'} detected in Main Data.`;
      if (this.pathOperationAction === 'prune') return `Prune row ${candidate.spreadsheetRowNumber}. ${this.pathOperationPrunePreview.descendantEntryCount} downstream entr${this.pathOperationPrunePreview.descendantEntryCount === 1 ? 'y' : 'ies'} detected.`;
      if (!candidate.isPathEnding) return 'Graft and Bridge graft require the selected row to be a current Path ends row.';
      return `${this.pathOperationActionLabel} row ${candidate.spreadsheetRowNumber}. ${candidate.rawCandidates.length} matching raw data set${candidate.rawCandidates.length === 1 ? '' : 's'} found.`;
    },

    entityCuratorAnalysis() {
      return buildCaseEntityCuratorReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.entityCuratorRemote,
        this.entityCuratorLocalOverlay,
        this.caseSupportState.workflowState.entityReview
      );
    },

    entityCuratorEffectiveExchangeItems() {
      return this.entityCuratorAnalysis.effective.exchange || [];
    },

    entityCuratorEffectiveNonExchangeItems() {
      return this.entityCuratorAnalysis.effective.nonExchange || [];
    },

    entityCuratorFilteredGroups() {
      const search = normalizeCell(this.entityCuratorSearch).toLowerCase();
      const filter = this.entityCuratorViewFilter || 'reviewable';
      return (this.entityCuratorAnalysis.groups || []).filter((group) => {
        if (filter === 'reviewable' && !group.isReviewable) return false;
        if (filter === 'known' && !group.isKnownMatch) return false;
        if (filter === 'suppressed' && !group.isSuppressed) return false;
        if (search && !group.searchText.includes(search)) return false;
        return true;
      });
    },

    entityCuratorReviewableCount() {
      return this.entityCuratorAnalysis.reviewableGroupCount || 0;
    },

    entityCuratorCaseMismatchFixCount() {
      return (this.entityCuratorAnalysis.groups || []).filter(group => group?.isCaseMismatch && group?.canApplyCaseMismatchCasingFix).length;
    },

    entityCuratorKnownCount() {
      return this.entityCuratorAnalysis.knownGroupCount || 0;
    },

    entityCuratorSuppressedCount() {
      return this.entityCuratorAnalysis.suppressedGroupCount || 0;
    },

    entityCuratorReviewIsCurrent() {
      return !!this.entityCuratorAnalysis.isCurrent;
    },

    entityCuratorCompletionLabel() {
      if (this.entityCuratorReviewIsCurrent) return 'Entity review current';
      if (this.entityCuratorReviewableCount === 0) return 'Mark entity review complete';
      return `Mark reviewed (${this.entityCuratorReviewableCount} still visible)`;
    },

    entityCuratorStageSummary() {
      const analysis = this.entityCuratorAnalysis;
      if (analysis.isCurrent) return `Entity curator review is current. ${analysis.reviewableGroupCount} candidate${analysis.reviewableGroupCount === 1 ? '' : 's'} remained visible when confirmed.`;
      return analysis.summary || 'Review current Main Data notes and attributions for known/new entities.';
    },

    caseInconsistencyRuleOptions() {
      return {
        rulePathEndsMismatch: this.caseInconsistencyRulePathEndsMismatch,
        ruleBranchesAsVsPathEnds: this.caseInconsistencyRuleBranchesAsVsPathEnds,
        ruleUnknownHashes: this.caseInconsistencyRuleUnknownHashes,
        rulePathEndsButNoFundsFollowed: this.caseInconsistencyRulePathEndsButNoFundsFollowed,
        ruleContinuesAsSingleButNextEntryMismatch: this.caseInconsistencyRuleContinuesAsSingleButNextEntryMismatch
      };
    },

    caseInconsistencyAnalysis() {
      if (this.caseInconsistencyLastRunSummary) return this.caseInconsistencyLastRunSummary;
      return buildCaseInconsistencyReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState.inconsistencyReview,
        this.caseInconsistencyRuleOptions
      );
    },

    caseInconsistencyVisibleCards() {
      const search = normalizeCell(this.caseInconsistencySearch).toLowerCase();
      const filter = this.caseInconsistencyViewFilter || 'pending';
      return (this.caseInconsistencyCards || []).filter((card) => {
        if (filter === 'pending' && card.isResolved) return false;
        if (filter === 'resolved' && !card.isResolved) return false;
        if (filter === 'safe' && !card.hasSafeAutoFixEligible) return false;
        if (filter === 'judgement' && !card.hasJudgementAutoFixEligible) return false;
        if (search && !card.searchText.includes(search)) return false;
        return true;
      });
    },

    caseInconsistencyPendingCount() {
      return (this.caseInconsistencyCards || []).filter(card => !card.isResolved).length;
    },

    caseInconsistencyResolvedCount() {
      return (this.caseInconsistencyCards || []).filter(card => card.isResolved).length;
    },

    caseInconsistencySafeFixCount() {
      return (this.caseInconsistencyCards || []).reduce((sum, card) => sum + (card.safeFixCount || 0), 0);
    },

    caseInconsistencyJudgementFixCount() {
      return (this.caseInconsistencyCards || []).reduce((sum, card) => sum + (card.judgementFixCount || 0), 0);
    },

    caseInconsistencyCanCompleteReview() {
      return !this.caseInconsistencyRunning && this.caseInconsistencyPendingCount === 0;
    },

    caseInconsistencyCompletionLabel() {
      if (this.caseInconsistencyCards.length === 0) return 'Mark clean review complete';
      if (this.caseInconsistencyPendingCount === 0) return 'Mark inconsistency review complete';
      return `${this.caseInconsistencyPendingCount} card${this.caseInconsistencyPendingCount === 1 ? '' : 's'} still pending`;
    },

    depositHotWalletEffectiveCurator() {
      return buildEffectiveEntityCurators(this.entityCuratorRemote, this.entityCuratorLocalOverlay);
    },

    depositHotWalletAnalysis() {
      if (this.depositHotWalletLastRunSummary) return this.depositHotWalletLastRunSummary;
      return buildCaseDepositHotWalletReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        this.depositHotWalletVendorResultsByKey
      );
    },

    depositHotWalletVisibleCandidates() {
      const search = normalizeCell(this.depositHotWalletSearch).toLowerCase();
      const filter = this.depositHotWalletViewFilter || 'eligible';
      return (this.depositHotWalletAnalysis.allCandidates || []).filter((candidate) => {
        if (filter === 'eligible' && !candidate.isEligible) return false;
        if (filter === 'local' && candidate.type === 'vendor') return false;
        if (filter === 'vendor' && candidate.type !== 'vendor') return false;
        if (filter === 'confirmed_vendor' && !(candidate.type === 'vendor' && candidate.isEligible)) return false;
        if (filter === 'normalized' && !candidate.isAlreadyNormalized) return false;
        if (search && !candidate.searchText.includes(search)) return false;
        return true;
      });
    },

    depositHotWalletEligibleLocalCount() {
      return this.depositHotWalletAnalysis.eligibleLocalCount || 0;
    },

    depositHotWalletVendorCandidateCount() {
      return this.depositHotWalletAnalysis.vendorCandidateCount || 0;
    },

    depositHotWalletConfirmedVendorCount() {
      return this.depositHotWalletAnalysis.confirmedVendorCount || 0;
    },

    depositHotWalletSelectedEligibleLocalCount() {
      return (this.depositHotWalletAnalysis.localCandidates || []).filter(candidate => candidate.isEligible && this.isDepositHotWalletCandidateSelected(candidate.key)).length;
    },

    depositHotWalletSelectedVendorLookupCount() {
      return (this.depositHotWalletAnalysis.vendorCandidates || []).filter(candidate => this.isDepositHotWalletCandidateSelected(candidate.key) && !candidate.vendorClassification).length;
    },

    depositHotWalletSelectedConfirmedVendorCount() {
      return (this.depositHotWalletAnalysis.vendorCandidates || []).filter(candidate => candidate.isEligible && this.isDepositHotWalletCandidateSelected(candidate.key)).length;
    },

    depositHotWalletConfirmedVendorEligibleCount() {
      return (this.depositHotWalletAnalysis.vendorCandidates || []).filter(candidate => candidate.isEligible).length;
    },

    depositHotWalletCanApplySafeLocal() {
      return !this.depositHotWalletRunning && this.depositHotWalletSelectedEligibleLocalCount > 0;
    },

    depositHotWalletCanRunVendorLookup() {
      return !this.depositHotWalletRunning && this.depositHotWalletVendorLookupEnabled && this.depositHotWalletSelectedVendorLookupCount > 0;
    },

    depositHotWalletCanApplyVendorConfirmed() {
      return !this.depositHotWalletRunning && this.depositHotWalletSelectedConfirmedVendorCount > 0;
    },

    depositHotWalletCanApplyAnyVendorConfirmed() {
      return !this.depositHotWalletRunning && this.depositHotWalletConfirmedVendorEligibleCount > 0;
    },

    depositHotWalletReviewIsCurrent() {
      return !!this.depositHotWalletAnalysis.isCurrent;
    },

    depositHotWalletCompletionLabel() {
      if (this.depositHotWalletReviewIsCurrent) return 'Normalization review current';
      if (this.depositHotWalletEligibleLocalCount === 0) return 'Mark normalization review complete';
      return `${this.depositHotWalletEligibleLocalCount} safe candidate${this.depositHotWalletEligibleLocalCount === 1 ? '' : 's'} remain`;
    },

    depositPatternAnalysis() {
      if (this.depositPatternLastRunSummary) return this.depositPatternLastRunSummary;
      return buildCaseDepositPatternReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );
    },

    depositPatternVisibleCandidates() {
      const search = normalizeCell(this.depositPatternSearch).toLowerCase();
      const minRank = caseDepositPatternGradeRank(this.depositPatternMinGrade || 'VERY_LOW');
      const filter = this.depositPatternViewFilter || 'pending_required';
      const list = (this.depositPatternCandidates.length > 0 ? this.depositPatternCandidates : (this.depositPatternAnalysis.candidates || [])).filter((candidate) => {
        if (caseDepositPatternGradeRank(candidate.grade) < minRank) return false;
        if (filter === 'pending_required' && (!candidate.isRequired || candidate.hasCurrentDecision)) return false;
        if (filter === 'pending_all' && candidate.hasCurrentDecision) return false;
        if (filter === 'required_all' && !candidate.isRequired) return false;
        if (filter === 'optional' && candidate.isRequired) return false;
        if (filter === 'resolved' && !candidate.hasCurrentDecision) return false;
        if (search && !candidate.searchText.includes(search)) return false;
        return true;
      });

      return list.sort((left, right) => {
        if (this.depositPatternSortMode === 'row_asc') return (left.childOutputRowIndex || 0) - (right.childOutputRowIndex || 0);
        if (this.depositPatternSortMode === 'time_then_priority') {
          const deltaCompare = (left.deltaMinutes || 0) - (right.deltaMinutes || 0);
          if (deltaCompare !== 0) return deltaCompare;
          return caseDepositPatternGradeRank(right.grade) - caseDepositPatternGradeRank(left.grade);
        }
        if (this.depositPatternSortMode === 'similarity_then_priority') {
          const simCompare = (right.amountSimilarity || 0) - (left.amountSimilarity || 0);
          if (simCompare !== 0) return simCompare;
          return caseDepositPatternGradeRank(right.grade) - caseDepositPatternGradeRank(left.grade);
        }
        const rankCompare = caseDepositPatternGradeRank(right.grade) - caseDepositPatternGradeRank(left.grade);
        if (rankCompare !== 0) return rankCompare;
        return (left.deltaMinutes || 0) - (right.deltaMinutes || 0);
      });
    },

    depositPatternRequiredCount() {
      return this.depositPatternAnalysis.requiredCandidateCount || 0;
    },

    depositPatternRequiredReviewedCount() {
      return this.depositPatternAnalysis.requiredReviewedCount || 0;
    },

    depositPatternPendingRequiredCount() {
      return this.depositPatternAnalysis.pendingRequiredCount || 0;
    },

    depositPatternPendingCandidateCount() {
      return this.depositPatternAnalysis.pendingCandidateCount || 0;
    },

    depositPatternReviewIsComplete() {
      return this.depositPatternPendingRequiredCount === 0;
    },

    depositPatternCompletionLabel() {
      if (this.depositPatternPendingRequiredCount > 0) return `${this.depositPatternPendingRequiredCount} required pending`;
      if (this.depositPatternAnalysis.totalCandidateCount === 0) return 'Mark clean review complete';
      return 'Mark review complete';
    },

    knownEntityCasingAnalysis() {
      if (this.knownEntityCasingLastRunSummary) return this.knownEntityCasingLastRunSummary;
      return buildCaseKnownEntityCasingReviewAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );
    },

    knownEntityCasingVisibleCandidates() {
      const search = normalizeCell(this.knownEntityCasingSearch).toLowerCase();
      const view = this.knownEntityCasingViewFilter || 'pending';
      const kindFilter = this.knownEntityCasingKindFilter || 'all';
      return (this.knownEntityCasingCandidates || []).filter((candidate) => {
        if (view === 'pending' && candidate.hasCurrentDecision) return false;
        if (view === 'selected' && !this.knownEntityCasingSelectedByKey[candidate.reviewKey]) return false;
        if (view === 'reviewed' && candidate.currentReviewStatus !== CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_REVIEWED) return false;
        if (view === 'applied' && candidate.currentReviewStatus !== CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_APPLIED) return false;
        if (view === 'dismissed' && candidate.currentReviewStatus !== CASE_KNOWN_ENTITY_CASING_REVIEW_STATUS_DISMISSED) return false;
        if (kindFilter !== 'all' && candidate.entityKind !== kindFilter) return false;
        return !search || candidate.searchText.includes(search);
      });
    },

    knownEntityCasingPendingCount() {
      return this.knownEntityCasingAnalysis.pendingCount || 0;
    },

    knownEntityCasingReviewedCount() {
      return this.knownEntityCasingAnalysis.reviewedCount || 0;
    },

    knownEntityCasingSelectedCount() {
      return (this.knownEntityCasingCandidates || []).filter(candidate => !candidate.hasCurrentDecision && !!this.knownEntityCasingSelectedByKey[candidate.reviewKey]).length;
    },

    knownEntityCasingSelectedCandidates() {
      return (this.knownEntityCasingCandidates || []).filter(candidate => !candidate.hasCurrentDecision && !!this.knownEntityCasingSelectedByKey[candidate.reviewKey]);
    },

    knownEntityCasingReviewIsComplete() {
      return this.knownEntityCasingPendingCount === 0;
    },

    knownEntityCasingCompletionLabel() {
      if (this.knownEntityCasingPendingCount > 0) return `${this.knownEntityCasingPendingCount} candidate${this.knownEntityCasingPendingCount === 1 ? '' : 's'} pending`;
      if ((this.knownEntityCasingAnalysis.totalCandidateCount || 0) === 0) return 'Mark clean casing review complete';
      return 'Mark casing review complete';
    },

    duplicateHashLabelerAnalysis() {
      if (this.duplicateHashLabelerLastRunSummary) return this.duplicateHashLabelerLastRunSummary;
      return buildCaseDuplicateHashLabelerAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        { caseId: this.currentProjectFileId || this.caseDisplayId || 'case' }
      );
    },

    duplicateHashLabelerVisibleItems() {
      const search = normalizeCell(this.duplicateHashLabelerSearch).toLowerCase();
      const view = this.duplicateHashLabelerViewFilter || 'eligible';
      return (this.duplicateHashLabelerItems || []).filter((item) => {
        if (view === 'eligible' && !item.isEligible) return false;
        if (view === 'duplicates' && item.itemKind !== 'duplicate') return false;
        if (view === 'stale' && item.itemKind !== 'stale_note') return false;
        if (view === 'current' && (item.isEligible || item.proposalPreservesNotes === false)) return false;
        if (view === 'blocked' && item.proposalPreservesNotes !== false) return false;
        if (view === 'selected' && !this.duplicateHashLabelerSelectedByKey[item.reviewKey]) return false;
        return !search || item.searchText.includes(search);
      });
    },

    duplicateHashLabelerSelectedItems() {
      return (this.duplicateHashLabelerItems || []).filter(item => item.isEligible && !!this.duplicateHashLabelerSelectedByKey[item.reviewKey]);
    },

    duplicateHashLabelerSelectedCount() {
      return this.duplicateHashLabelerSelectedItems.length;
    },

    duplicateHashLabelerEligibleCount() {
      return this.duplicateHashLabelerAnalysis.eligibleEntryCount || 0;
    },

    duplicateHashLabelerBlockedCount() {
      return this.duplicateHashLabelerAnalysis.blockedProposalCount || 0;
    },

    duplicateHashLabelerReviewIsComplete() {
      return this.duplicateHashLabelerEligibleCount === 0 && this.duplicateHashLabelerBlockedCount === 0;
    },

    duplicateHashLabelerCompletionLabel() {
      if (this.duplicateHashLabelerBlockedCount > 0) return `${this.duplicateHashLabelerBlockedCount} safety review${this.duplicateHashLabelerBlockedCount === 1 ? '' : 's'} pending`;
      if (this.duplicateHashLabelerEligibleCount > 0) return `${this.duplicateHashLabelerEligibleCount} update${this.duplicateHashLabelerEligibleCount === 1 ? '' : 's'} pending`;
      if ((this.duplicateHashLabelerAnalysis.duplicateHashGroupCount || 0) === 0) return 'Mark clean duplicate review complete';
      return 'Mark duplicate labeling complete';
    },

    noteHashRewriteAnalysis() {
      if (this.noteHashRewriteLastRunSummary) return this.noteHashRewriteLastRunSummary;
      return buildCaseNoteHashRewriteAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.caseSupportState.workflowState || {},
        {
          caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
          includeUnstructured: this.noteHashRewriteIncludeUnstructured
        }
      );
    },

    noteHashRewriteVisibleItems() {
      const search = normalizeCell(this.noteHashRewriteSearch).toLowerCase();
      const view = this.noteHashRewriteViewFilter || 'eligible';
      return (this.noteHashRewriteItems || []).filter((item) => {
        if (view === 'eligible' && !item.isEligible) return false;
        if (view === 'unresolved' && item.itemKind !== 'unresolved') return false;
        if (view === 'current' && item.isEligible) return false;
        if (view === 'selected' && !this.noteHashRewriteSelectedByKey[item.reviewKey]) return false;
        return !search || item.searchText.includes(search);
      });
    },

    noteHashRewriteSelectedItems() {
      return (this.noteHashRewriteItems || []).filter(item => item.isEligible && !!this.noteHashRewriteSelectedByKey[item.reviewKey]);
    },

    noteHashRewriteSelectedCount() {
      return this.noteHashRewriteSelectedItems.length;
    },

    noteHashRewriteEligibleCount() {
      return this.noteHashRewriteAnalysis.eligibleCount || 0;
    },

    noteHashRewriteBackupExists() {
      return !!this.noteHashRewriteAnalysis.backupExists;
    },

    noteHashRewriteReviewIsComplete() {
      return this.noteHashRewriteBackupExists && this.noteHashRewriteEligibleCount === 0;
    },

    noteHashRewriteCompletionLabel() {
      if (!this.noteHashRewriteBackupExists) return 'Create backup first';
      if (this.noteHashRewriteEligibleCount > 0) return `${this.noteHashRewriteEligibleCount} rewrite${this.noteHashRewriteEligibleCount === 1 ? '' : 's'} pending`;
      return 'Mark rewrite complete';
    },

    addressLegendAnalysis() {
      if (this.addressLegendLastRunSummary) return this.addressLegendLastRunSummary;
      return buildCaseAddressLegendAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        {
          labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately,
          useDepositAddressLabels: this.addressLegendUseDepositLabels
        }
      );
    },

    addressLegendVisibleCandidates() {
      const search = normalizeCell(this.addressLegendSearch).toLowerCase();
      const filter = this.addressLegendViewFilter || 'changed';
      return (this.addressLegendCandidates || []).filter((candidate) => {
        if (filter === 'changed' && !candidate.isChanged) return false;
        if (filter === 'current' && candidate.isChanged) return false;
        if (filter === 'exchange' && !String(candidate.labelFamily || '').startsWith('exchange')) return false;
        if (filter === 'sender' && candidate.sourceSide !== 'from') return false;
        if (filter === 'receiver' && candidate.sourceSide !== 'to') return false;
        if (filter === 'victim_suspect' && !['victim', 'suspect'].includes(candidate.labelFamily)) return false;
        if (filter === 'unknown' && candidate.labelFamily !== 'unknown') return false;
        return !search || candidate.searchText.includes(search);
      });
    },

    addressLegendSelectedCandidate() {
      return (this.addressLegendCandidates || []).find(candidate => candidate.id === this.addressLegendSelectedCandidateId)
        || this.addressLegendVisibleCandidates[0]
        || null;
    },

    addressLegendBuildIsCurrent() {
      return !!this.addressLegendAnalysis.buildIsCurrent;
    },

    addressLegendReviewIsCurrent() {
      return !!this.addressLegendAnalysis.reviewIsCurrent;
    },

    addressLegendQuickAccessAvailable() {
      const review = this.caseSupportState.workflowState?.addressLegendReview || {};
      const entries = Array.isArray(review.entries) ? review.entries : [];
      if (entries.length === 0 || !review.reviewedAtUtc) return false;
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const reviewedRevision = Number.isFinite(Number(review.reviewedRevision))
        ? Number(review.reviewedRevision)
        : Number.isFinite(Number(review.revision))
          ? Number(review.revision)
          : Number.isFinite(Number(review.builtRevision))
            ? Number(review.builtRevision)
            : null;
      if (reviewedRevision !== currentRevision) return false;
      const builtBasisHash = normalizeCell(review.builtBasisHash);
      const reviewedBasisHash = normalizeCell(review.reviewedBasisHash || review.confirmedReviewBasisHash);
      return !!builtBasisHash && builtBasisHash === reviewedBasisHash;
    },

    addressLegendChangedCount() {
      return this.addressLegendAnalysis.changedCount || 0;
    },

    addressLegendCandidateCount() {
      return this.addressLegendAnalysis.candidateCount || 0;
    },

    addressLegendCompletionLabel() {
      if (this.addressLegendReviewIsCurrent) return 'Legend review current';
      if (!this.addressLegendBuildIsCurrent) return 'Build current legend first';
      return 'Mark legend reviewed';
    },

    templateDeliverableHeaders() {
      return CASE_TEMPLATE_TARGET_HEADERS;
    },

    templateDeliverableState() {
      const baseState = this.getCaseTemplateState();
      const definition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'template');
      const view = definition ? this.buildDeliverableView(definition) : null;
      const rows = buildCaseTemplateDeliverableRows(baseState);
      const recordStatus = this.getTemplateDeliverableRecordStatus(baseState);
      const builtIsCurrent = recordStatus.builtIsCurrent;
      const reviewIsCurrent = recordStatus.reviewIsCurrent;
      const canBuild = !!baseState.buildIsCurrent;
      const canMarkReview = canBuild && !reviewIsCurrent && (builtIsCurrent || recordStatus.sessionBuildIsCurrentRevision);
      const statusLabel = !baseState.hasAnyMainRows
        ? 'Needs Main Data'
        : !baseState.legendBuildIsCurrent
          ? 'Needs Current Legend'
          : !baseState.legendReviewIsCurrent
            ? 'Needs Legend Review'
            : reviewIsCurrent
              ? 'Reviewed'
              : builtIsCurrent || recordStatus.sessionBuildIsCurrentRevision
                ? 'Built current'
                : 'Ready';
      return {
        ...baseState,
        rows,
        canBuild,
        canMarkReview,
        builtIsCurrent: builtIsCurrent || recordStatus.sessionBuildIsCurrentRevision,
        reviewIsCurrent,
        statusLabel,
        deliverableView: view || null,
        builtAtUtc: recordStatus.builtAtUtc || recordStatus.sessionBuiltAtUtc || '',
        builtAtLabel: safeDateLabel(recordStatus.builtAtUtc || recordStatus.sessionBuiltAtUtc),
        reviewedAtUtc: recordStatus.reviewedAtUtc || ''
      };
    },

    templateDeliverableVisibleRows() {
      const search = normalizeCell(this.templateDeliverableSearch).toLowerCase();
      const filter = this.templateDeliverableViewFilter || 'all';
      return (this.templateDeliverableState.rows || []).map((cells, index) => {
        const normalizedCells = normalizeCaseMatrix([cells], 1, CASE_TEMPLATE_TARGET_COLUMN_COUNT)[0].slice(0, CASE_TEMPLATE_TARGET_COLUMN_COUNT);
        return {
          key: `template-row-${index}`,
          rowNumber: index + 1,
          cells: normalizedCells,
          searchText: normalizedCells.join('	').toLowerCase(),
          type: normalizeCell(normalizedCells[1]),
          fromType: normalizeCell(normalizedCells[4]),
          toType: normalizeCell(normalizedCells[9]),
          risk: normalizeCell(normalizedCells[13])
        };
      }).filter((row) => {
        if (filter === 'non_empty' && !row.cells.some(cell => normalizeCell(cell))) return false;
        if (filter === 'main' && row.type !== 'Main TXN') return false;
        if (filter === 'sub' && row.type !== 'Sub TXN') return false;
        if (filter === 'exchange' && row.fromType !== 'Exchange' && row.toType !== 'Exchange') return false;
        if (filter === 'suspicious' && row.risk !== 'Suspicious') return false;
        return !search || row.searchText.includes(search);
      });
    },

    templateDeliverableCompletionLabel() {
      if (this.templateDeliverableState.reviewIsCurrent) return 'Template review current';
      if (!this.templateDeliverableState.canBuild) return 'Resolve prerequisites first';
      if (!this.templateDeliverableState.builtIsCurrent) return 'Stage template first';
      return 'Mark template reviewed';
    },


    suspiciousAddressTableHeaders() {
      return CASE_SUSPICIOUS_ADDRESS_TABLE_HEADERS;
    },

    suspiciousAddressTableState() {
      const baseState = this.getSuspiciousAddressTableState();
      const definition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'suspicious_address_table');
      const view = definition ? this.buildDeliverableView(definition) : null;
      const builtIsCurrent = view?.status === 'current';
      const reviewIsCurrent = builtIsCurrent && !!view?.reviewed;
      const statusLabel = !baseState.legendBuildIsCurrent
        ? 'Needs Legend Build'
        : !baseState.legendReviewIsCurrent
          ? 'Needs Legend Review'
          : reviewIsCurrent
            ? 'Reviewed'
            : builtIsCurrent
              ? 'Built current'
              : 'Ready';
      return {
        ...baseState,
        builtIsCurrent,
        reviewIsCurrent,
        statusLabel,
        deliverableView: view || null,
        builtAtUtc: view?.builtAtUtc || '',
        builtAtLabel: view?.builtAtLabel || '',
        reviewedAtUtc: this.caseSupportState.deliverableStateById?.suspicious_address_table?.reviewedAtUtc || ''
      };
    },

    suspiciousAddressTableVisibleRows() {
      const search = normalizeCell(this.suspiciousAddressTableSearch).toLowerCase();
      const filter = this.suspiciousAddressTableViewFilter || 'all';
      const rows = this.suspiciousAddressTableState.rows || [];
      const presentNumbers = new Set(rows.map(row => row.sortNumber).filter(number => Number.isFinite(number)));
      return rows.map((row, index) => ({
        ...row,
        displayIndex: index + 1,
        hasNumberGapBefore: Number.isFinite(row.sortNumber) && row.sortNumber > 1 && !presentNumbers.has(row.sortNumber - 1)
      })).filter((row) => {
        if (filter === 'with_multiple_transfers' && (row.contributionCount || 0) < 2) return false;
        if (filter === 'single_transfer' && (row.contributionCount || 0) !== 1) return false;
        if (filter === 'missing_number_gap' && !row.hasNumberGapBefore) return false;
        return !search || row.searchText.includes(search);
      });
    },

    suspiciousAddressTableCompletionLabel() {
      if (this.suspiciousAddressTableState.reviewIsCurrent) return 'Suspicious table review current';
      if (!this.suspiciousAddressTableState.canBuild) return 'Resolve prerequisites first';
      return 'Mark table reviewed';
    },

    exchangeAddressTableHeaders() {
      return CASE_EXCHANGE_ADDRESS_TABLE_HEADERS;
    },

    exchangeAddressTableState() {
      const baseState = this.getExchangeAddressTableState();
      const definition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'exchange_address_table');
      const view = definition ? this.buildDeliverableView(definition) : null;
      const builtIsCurrent = view?.status === 'current';
      const reviewIsCurrent = builtIsCurrent && !!view?.reviewed;
      const statusLabel = !baseState.legendBuildIsCurrent
        ? 'Needs Legend Build'
        : !baseState.legendReviewIsCurrent
          ? 'Needs Legend Review'
          : reviewIsCurrent
            ? 'Reviewed'
            : builtIsCurrent
              ? 'Built current'
              : 'Ready';
      return {
        ...baseState,
        builtIsCurrent,
        reviewIsCurrent,
        statusLabel,
        deliverableView: view || null,
        builtAtUtc: view?.builtAtUtc || '',
        builtAtLabel: view?.builtAtLabel || '',
        reviewedAtUtc: this.caseSupportState.deliverableStateById?.exchange_address_table?.reviewedAtUtc || ''
      };
    },

    exchangeAddressTableVisibleRows() {
      const search = normalizeCell(this.exchangeAddressTableSearch).toLowerCase();
      const filter = this.exchangeAddressTableViewFilter || 'included';
      const rows = this.exchangeAddressTableState.rows || [];
      return rows.map((row, index) => ({
        ...row,
        displayIndex: index + 1
      })).filter((row) => {
        if (filter === 'included' && row.isExcludedCurrent) return false;
        if (filter === 'excluded' && !row.isExcludedCurrent) return false;
        if (filter === 'potential_noise' && !row.isPotentialNoise) return false;
        if (filter === 'with_multiple_transfers' && (row.contributionCount || 0) < 2) return false;
        if (filter === 'single_transfer' && (row.contributionCount || 0) !== 1) return false;
        return !search || row.searchText.includes(search);
      });
    },

    exchangeAddressTableCompletionLabel() {
      if (this.exchangeAddressTableState.reviewIsCurrent) return 'Exchange table review current';
      if (!this.exchangeAddressTableState.canBuild) return 'Resolve prerequisites first';
      return 'Mark table reviewed';
    },

    dormantAddressTableHeaders() {
      return CASE_DORMANT_ADDRESS_TABLE_HEADERS;
    },

    dormantAddressMonitoringHeaders() {
      return CASE_DORMANT_ADDRESS_MONITORING_HEADERS;
    },

    dormantAddressDeliverableState() {
      const baseState = this.getDormantAddressDeliverablesState();
      const tableDefinition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'dormant_address_table');
      const monitoringDefinition = CASE_DELIVERABLE_GROUPS.flatMap(group => group.items).find(deliverable => deliverable.id === 'dormant_address_monitoring_view');
      const tableView = tableDefinition ? this.buildDeliverableView(tableDefinition) : null;
      const monitoringView = monitoringDefinition ? this.buildDeliverableView(monitoringDefinition) : null;
      const officialBuiltIsCurrent = tableView?.status === 'current';
      const monitoringBuiltIsCurrent = monitoringView?.status === 'current';
      const bothBuiltCurrent = officialBuiltIsCurrent && monitoringBuiltIsCurrent;
      const reviewIsCurrent = bothBuiltCurrent && !!tableView?.reviewed && !!monitoringView?.reviewed;
      const statusLabel = !baseState.legendBuildIsCurrent
        ? 'Needs Legend Build'
        : !baseState.legendReviewIsCurrent
          ? 'Needs Legend Review'
          : reviewIsCurrent
            ? 'Reviewed'
            : bothBuiltCurrent
              ? 'Built current'
              : 'Ready';
      return {
        ...baseState,
        officialBuiltIsCurrent,
        monitoringBuiltIsCurrent,
        bothBuiltCurrent,
        reviewIsCurrent,
        statusLabel,
        tableDeliverableView: tableView || null,
        monitoringDeliverableView: monitoringView || null,
        reviewedAtUtc: this.caseSupportState.deliverableStateById?.dormant_address_table?.reviewedAtUtc || this.caseSupportState.deliverableStateById?.dormant_address_monitoring_view?.reviewedAtUtc || ''
      };
    },

    dormantAddressDeliverableVisibleRows() {
      const search = normalizeCell(this.dormantAddressDeliverableSearch).toLowerCase();
      const rows = this.dormantAddressDeliverableActiveView === 'monitoring'
        ? (this.dormantAddressDeliverableState.monitoringRows || [])
        : (this.dormantAddressDeliverableState.rows || []);
      return rows.map((row, index) => ({
        ...row,
        displayIndex: index + 1
      })).filter((row) => !search || normalizeCell(row.searchText || Object.values(row).join(' ')).toLowerCase().includes(search));
    },

    dormantAddressDeliverableCompletionLabel() {
      if (this.dormantAddressDeliverableState.reviewIsCurrent) return 'Dormant deliverables review current';
      if (!this.dormantAddressDeliverableState.canBuild) return 'Resolve prerequisites first';
      if (!this.dormantAddressDeliverableState.bothBuiltCurrent) return 'Mark built and reviewed';
      return 'Mark reviewed';
    },


    wwwUsdReferenceState() {
      return this.getWwwUsdReferenceReviewState();
    },

    wwwUsdReferenceVisibleRows() {
      const search = normalizeCell(this.wwwUsdReferenceSearch).toLowerCase();
      const rows = this.wwwUsdReferenceViewFilter === 'all'
        ? (this.wwwUsdReferenceState.referenceRows || [])
        : (this.wwwUsdReferenceState.referenceRows || []).filter(row => !row.isUsable);
      return rows.filter(row => !search || row.searchText.includes(search));
    },

    wwwUsdReferenceAllVisibleSelected() {
      const rows = this.wwwUsdReferenceVisibleRows || [];
      return rows.length > 0 && rows.every(row => !!this.whatWentWhereSelectedReferenceKeys[row.key]);
    },

    wwwUsdReferenceCompletionLabel() {
      if (this.wwwUsdReferenceState.reviewIsCurrent) return 'USD references review current';
      if (!this.wwwUsdReferenceState.canReview) return 'Resolve Column H values first';
      return 'Mark USD references reviewed';
    },

    whatWentWherePriceReferenceMetadata() {
      return getCaseBuddyHistoricalPriceReferenceMetadata();
    },

    whatWentWhereState() {
      return this.getWhatWentWhereState();
    },

    whatWentWhereVisibleReferenceRows() {
      const search = normalizeCell(this.whatWentWhereSearch).toLowerCase();
      const rows = this.whatWentWhereViewFilter === 'references_all'
        ? (this.whatWentWhereState.referenceRows || [])
        : (this.whatWentWhereState.referenceRows || []).filter(row => !row.isUsable);
      return rows.filter(row => !search || row.searchText.includes(search));
    },

    whatWentWhereAllVisibleReferencesSelected() {
      const rows = this.whatWentWhereVisibleReferenceRows || [];
      return rows.length > 0 && rows.every(row => !!this.whatWentWhereSelectedReferenceKeys[row.key]);
    },

    whatWentWhereVisiblePathEndingRows() {
      const search = normalizeCell(this.whatWentWhereSearch).toLowerCase();
      return (this.whatWentWhereState.pathEndingRows || []).filter(row => !search || row.searchText.includes(search));
    },

    whatWentWhereVisibleManualRows() {
      const search = normalizeCell(this.whatWentWhereSearch).toLowerCase();
      return (this.whatWentWhereState.manualValuationRows || []).filter(row => !search || normalizeCell(Object.values(row).join(' ')).toLowerCase().includes(search));
    },

    whatWentWhereVisibleCoverageRows() {
      const search = normalizeCell(this.whatWentWhereSearch).toLowerCase();
      return (this.whatWentWhereState.mainCoverageRows || []).filter(row => !search || normalizeCell(Object.values(row).join(' ')).toLowerCase().includes(search));
    },

    whatWentWhereCompletionLabel() {
      if (this.whatWentWhereState.reviewIsCurrent) return 'What Went Where review current';
      if (!this.whatWentWhereState.canBuild) return 'Resolve Column H references first';
      return 'Mark What Went Where reviewed';
    },

    caseConclusionState() {
      return this.getCaseConclusionState();
    },

    caseConclusionCompletionLabel() {
      if (this.caseConclusionState.reviewIsCurrent) return 'Conclusion review current';
      if (!this.caseConclusionState.canBuild) return 'Review What Went Where first';
      return 'Mark conclusion reviewed';
    },

    reportBodyState() {
      return this.getReportBodyState();
    },

    reportBodyActiveVariantLabel() {
      return this.reportBodyActiveVariant === CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING
        ? 'Tool38H custom spacing'
        : 'Tool38G standard';
    },

    reportBodyActivePlainText() {
      const state = this.reportBodyState;
      return this.reportBodyActiveVariant === CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING
        ? (state.customSpacingText || '')
        : (state.generatedText || '');
    },

    reportBodyActiveHtmlFragment() {
      const state = this.reportBodyState;
      return this.reportBodyActiveVariant === CASE_REPORT_BODY_VARIANT_CUSTOM_SPACING
        ? (state.customSpacingHtmlFragment || '')
        : (state.standardHtmlFragment || '');
    },

    reportBodyVisibleSections() {
      const search = normalizeCell(this.reportBodySearch).toLowerCase();
      const sections = this.reportBodyState.sections || [];
      if (!search) return sections;
      return sections.filter(section => normalizeCell([
        section.pathLabel,
        section.previewText,
        ...(section.entries || []).flatMap(entry => [
          entry.pathLabel,
          entry.transactionHash,
          entry.summaryFromLabel,
          entry.summaryToLabel,
          ...(entry.movements || []).flatMap(movement => [movement.from?.label, movement.from?.address, movement.to?.label, movement.to?.address, movement.amountDisplay, movement.notes])
        ])
      ].join(' ')).toLowerCase().includes(search));
    },

    reportBodyVisibleSectionCount() {
      return this.reportBodyVisibleSections.length;
    },

    reportBodyCompletionLabel() {
      if (this.reportBodyState.reviewIsCurrent) return 'Report body review current';
      if (!this.reportBodyState.canBuild) return 'Resolve prerequisites first';
      if (!this.reportBodyState.bothBuiltCurrent) return 'Mark report body reviewed';
      return 'Mark report body reviewed';
    },

    addendumPathEndingReviewState() {
      return this.getAddendumPathEndingReviewState();
    },

    addendumPathEndingVisibleRows() {
      const search = normalizeCell(this.addendumPathEndingSearch).toLowerCase();
      const filter = this.addendumPathEndingViewFilter || 'included';
      return (this.addendumPathEndingReviewState.reviewRows || []).filter((row) => {
        if (filter === 'included' && row.status !== 'included') return false;
        if (filter === 'omitted' && row.status !== 'omitted') return false;
        if (filter === 'no_exchange_keyword' && row.omitReason !== 'no_exchange_keyword') return false;
        if (filter === 'victim_owned_keyword' && row.omitReason !== 'victim_owned_keyword') return false;
        if (filter === 'not_path_ending' && row.omitReason !== 'not_path_ending') return false;
        if (filter === 'duplicate' && row.omitReason !== 'duplicate') return false;
        if (filter === 'keyword_list_empty' && row.omitReason !== 'keyword_list_empty') return false;
        if (search && !row.searchText.includes(search)) return false;
        return true;
      });
    },

    addendumPathEndingOmittedReasonRows() {
      const labels = {
        no_exchange_keyword: 'No exchange keyword',
        victim_owned_keyword: 'Victim-owned wording',
        not_path_ending: 'Not path ending',
        duplicate: 'Duplicate',
        keyword_list_empty: 'No keywords loaded'
      };
      const counts = this.addendumPathEndingReviewState.omittedReasonCounts || {};
      return Object.keys(labels).map(key => ({
        key,
        label: labels[key],
        count: counts[key] || 0
      }));
    },

    addendumPathEndingCompletionLabel() {
      if (this.addendumPathEndingReviewState.reviewIsCurrent) return 'Addendum row review current';
      if (!this.addendumPathEndingReviewState.prerequisiteCanBuild) return 'Build What Went Where first';
      if ((this.addendumPathEndingReviewState.keywordCount || 0) === 0) return 'Load exchange keywords first';
      return `Mark reviewed (${this.addendumPathEndingReviewState.includedCount || 0} included)`;
    },

    addendumRecipientEntityReviewState() {
      return this.getAddendumRecipientEntityReviewState();
    },

    addendumRecipientVisibleRows() {
      const search = normalizeCell(this.addendumRecipientSearch).toLowerCase();
      const filter = this.addendumRecipientViewFilter || 'needs_choice';
      return (this.addendumRecipientEntityReviewState.candidates || []).filter((row) => {
        const isResolved = !!row.resolvedEntityName;
        const hasNoOptions = (row.candidateEntityNames || []).length === 0;
        if (filter === 'needs_choice' && !row.needsUserChoice) return false;
        if (filter === 'unresolved' && isResolved) return false;
        if (filter === 'resolved' && !isResolved) return false;
        if (filter === 'auto_resolved' && !row.isAutoResolved) return false;
        if (filter === 'selected' && !row.hasSavedSelection) return false;
        if (filter === 'no_options' && !hasNoOptions) return false;
        if (search && !row.searchText.includes(search)) return false;
        return true;
      });
    },

    addendumRecipientCompletionLabel() {
      const state = this.addendumRecipientEntityReviewState;
      if (state.reviewIsCurrent) return 'Recipient entity review current';
      if (!state.buildIsCurrent) return 'Review path-ending rows first';
      if ((state.noCandidateEntityCount || 0) > 0) return `${state.noCandidateEntityCount || 0} row${(state.noCandidateEntityCount || 0) === 1 ? '' : 's'} need entity options`;
      if ((state.needsChoiceCount || 0) > 0) return `${state.needsChoiceCount || 0} choice${(state.needsChoiceCount || 0) === 1 ? '' : 's'} remaining`;
      return `Mark recipient review current (${state.resolvedCount || 0} resolved)`;
    },

    caseAddendumState() {
      const state = this.getCaseAddendumState();
      return this.applyCaseWorkflowAddendumBuildStateFallback?.(state, { source: 'case-addendum-computed' }) || state;
    },

    caseAddendumActiveVariantLabel() {
      return this.caseAddendumActiveVariant === CASE_ADDENDUM_VARIANT_NARRATIVE
        ? 'Addendum narrative'
        : 'Tool18E structured addendum';
    },

    caseAddendumActivePlainText() {
      return this.caseAddendumActiveVariant === CASE_ADDENDUM_VARIANT_NARRATIVE
        ? this.caseAddendumState.generatedNarrativeAddendumText || ''
        : this.caseAddendumState.generatedAddendumText || '';
    },

    caseAddendumActiveHtmlFragment() {
      return this.caseAddendumActiveVariant === CASE_ADDENDUM_VARIANT_NARRATIVE
        ? this.caseAddendumState.narrativeHtmlFragment || ''
        : this.caseAddendumState.structuredHtmlFragment || '';
    },

    caseAddendumVisibleSections() {
      const search = normalizeCell(this.caseAddendumSearch).toLowerCase();
      return (this.caseAddendumState.sections || []).filter((section) => {
        if (!search) return true;
        return normalizeCell(section.searchText || [section.entityName, section.jurisdiction, section.pathLabelsText, section.previewText].join(' ')).toLowerCase().includes(search);
      });
    },

    caseAddendumCompletionLabel() {
      const state = this.caseAddendumState;
      if (state.reviewIsCurrent) return 'Addendum review current';
      if (!state.prep55ReviewIsCurrent) return 'Review path-ending rows first';
      if (!state.prep48ReviewIsCurrent) return 'Resolve recipient entities first';
      if (!state.canBuild) return 'Resolve prerequisites first';
      if ((state.resolvedRowCount || 0) === 0) return 'Mark no addendum needed';
      return `Mark addendum reviewed (${state.resolvedRowCount || 0} row${(state.resolvedRowCount || 0) === 1 ? '' : 's'})`;
    },

    caseFullReportPackagePayloadView() {
      if (this.caseFullReportPackagePayload) return this.caseFullReportPackagePayload;
      return {
        rendererVersion: '',
        caseId: this.caseDisplayId || '',
        isReady: false,
        basisHash: '',
        summary: this.caseFullReportPackageStatus || 'Preparing the full report package preview…',
        missingReason: '',
        plainText: '',
        previewText: 'Preparing the full report package preview…',
        htmlFragment: '<p>Preparing the full report package preview…</p>',
        htmlDocument: '',
        sections: [],
        embeddedVisualCount: 0,
        missingVisualCount: 0,
        plainTextLength: 0,
        htmlLength: 0,
        state: {
          isReady: false,
          reviewIsCurrent: false,
          reportBodyReviewIsCurrent: false,
          addendumReviewIsCurrent: false,
          visualsCanBuild: false,
          visualsReviewIsCurrent: false,
          summary: this.caseFullReportPackageStatus || 'Preparing the full report package preview…'
        }
      };
    },

    caseFullReportPackageStateView() {
      const state = this.caseFullReportPackagePayloadView.state || this.getCaseFullReportPackageState({ includeDeliverableViews: true });
      return this.applyCaseWorkflowFullReportPackageGeneratedOverlay
        ? this.applyCaseWorkflowFullReportPackageGeneratedOverlay(state)
        : state;
    },

    caseFullReportPackagePreviewHtml() {
      return this.simplifyCaseFullReportPackageCompletionSummaryHtml(this.caseFullReportPackagePayloadView.htmlFragment || '');
    },

    caseFullReportPackagePreviewText() {
      return this.simplifyCaseFullReportPackageCompletionSummaryText(this.caseFullReportPackagePayloadView.previewText || '');
    },

    caseFullReportPackageCompletionLabel() {
      const state = this.caseFullReportPackageStateView;
      if (state.reviewIsCurrent) return 'Package review current';
      if (!state.isReady) return 'Resolve prerequisites first';
      return 'Mark package reviewed';
    },

    workflowGroupsView() {
      return this.caseWorkflowGroupsWithEarlyWwwUsdReferences().map(group => {
        const steps = (Array.isArray(group.steps) ? group.steps : [])
          .map(step => this.buildWorkflowStepView(step))
          .map(stepView => (this.isCaseWorkflowWwwUsdReferenceStep(stepView)
            ? this.applyCaseWorkflowWwwUsdReferenceCurrentState(stepView)
            : stepView));
        return {
          ...group,
          steps,
          completed: steps.filter(step => step.complete).length
        };
      });
    },

    workflowProgress() {
      const steps = this.workflowGroupsView.flatMap(group => group.steps);
      return {
        total: steps.length,
        completed: steps.filter(step => step.complete).length,
        stale: steps.filter(step => step.stale).length
      };
    },

    recommendedWorkflowStep() {
      return this.workflowGroupsView.flatMap(group => group.steps).find(step => !step.complete) || null;
    },

    recommendedWorkflowStepTitle() {
      return this.recommendedWorkflowStep?.title || 'Workflow complete';
    },

    recommendedWorkflowStepHelp() {
      if (this.recommendedWorkflowStep) {
        return this.recommendedWorkflowStep.description;
      }
      return 'Review deliverables and package the report.';
    },

    checkSummaryLabel() {
      const results = Object.values(this.caseSupportState.checkResultsById || {});
      if (results.length === 0) return 'No checks run';

      const pass = results.filter(result => result.status === 'pass').length;
      const warn = results.filter(result => result.status === 'warn').length;
      const fail = results.filter(result => result.status === 'fail').length;
      return `${pass} pass · ${warn} warn · ${fail} fail`;
    },

    saplingStateLabel() {
      if (this.caseSummary.mainEntries <= 0) return 'No graph yet';
      if (this.caseSummary.startingMainTransactions > 0 && this.caseSummary.rawMissingMainTransactions > 0) {
        return `${this.caseSummary.rawMissingMainTransactions} raw set${this.caseSummary.rawMissingMainTransactions === 1 ? '' : 's'} needed`;
      }
      if (this.caseSummary.startingMainTransactions > 0 && this.caseSummary.rawCoveredMainTransactions > 0) return 'Raw coverage ready';
      if (this.caseSummary.rawCoveredMainTransactions > 0) return 'Partially raw-backed graph';
      return 'Main-only graph';
    },

    deliverableGroupsView() {
      return CASE_DELIVERABLE_GROUPS.map(group => {
        const items = group.items.map(item => this.buildDeliverableView(item));
        return {
          ...group,
          items,
          available: items.filter(item => item.available).length,
          current: items.filter(item => item.status === 'current').length
        };
      });
    },

    deliverableProgress() {
      const items = this.deliverableGroupsView.flatMap(group => group.items);
      return {
        total: items.length,
        available: items.filter(item => item.available).length,
        current: items.filter(item => item.status === 'current').length,
        stale: items.filter(item => item.status === 'stale').length
      };
    },

    shouldRenderAgentControls() {
    return this.authSessionLoaded &&
      this.authSession.isAuthenticated &&
      this.authSession.isAgentAccount;
  },

  inputKeepSelectionSelectors() {
    return [
      '.spreadsheet-tools-toggle',
      '.spreadsheet-tools-expanded',
      '.spreadsheet-tools-hide-btn',
      '.sheet-toolbar',
      '.formula-bar',
      '.formula-bar__cell',
      '.formula-bar__input',
      '.formula-bar__action',
      '.formula-bar__status',
      '.agent-controls',
      '.agent-controls__field',
      '.agent-controls__readback',
      '.color-picker-anchor',
      '.color-popover',
      '.color-popover__input',
      '.color-popover__action',
      '.sheet-tab-context-menu',
      '.modal-overlay',
      '.starting-main-modal',
      '.starting-main-table',
      '.network-lookup-modal',
      '.network-lookup-table',
      '.date-lookup-modal',
      '.date-lookup-table',
      '.path-label-modal',
      '.main-path-label-table',
      '.raw-data-import-modal',
      '.raw-data-import-table',
      '.tool68-preparation-modal',
      '.attribution-fill-modal',
      '.attribution-fill-table',
      '.tool68-preparation-table',
      '.deposit-pattern-modal',
      '.deposit-pattern-card',
      '.known-entity-casing-modal',
      '.known-entity-casing-card',
      '.duplicate-hash-labeler-modal',
      '.duplicate-hash-labeler-card',
      '.address-legend-modal',
      '.address-legend-card',
      '.addendum-path-ending-modal',
      '.addendum-path-ending-table',
      '.case-flow-visuals-modal',
      '.case-flow-visuals-viewport',
      '.case-file-browser-modal',
      '.case-file-browser-card',
      '.htMenu',
      '.htBorders'
    ];
  },

    formulaTargetLabel() {
      const target = this.formulaTargetCell || this.getSelectedAnchorCell();
      return target ? `${this.toColumnLabel(target.col)}${target.row + 1}` : '—';
    },
    formulaBarCaptureMode() {
      const baseDraft = String(this.formulaBarBaseDraft || this.formulaBarDraft || '').trim();
      return this.formulaBarEditing && baseDraft.startsWith('=');
    },
    canApplyFormulaBar() {
      return this.formulaBarEditing && !!(this.formulaTargetCell || this.getSelectedAnchorCell());
    },
    projectLegacyStorageKeys() {
      return [];
    },
    currentProjectFileMeta() {
      return this.projectFiles.find(file => file.id === this.currentProjectFileId) || null;
    },
    currentProjectFileName() {
      return this.currentProjectFileMeta?.name || 'Spreadsheet 1';
    },
    currentProjectInputStorageKey() {
      return this.currentProjectFileId ? this.getProjectInputStorageKey(this.currentProjectFileId) : '';
    },
    currentProjectOutputStorageKey() {
      return this.currentProjectFileId ? this.getProjectOutputStorageKey(this.currentProjectFileId) : '';
    },
    inputWorkbookComponentKey() {
      return this.currentProjectFileId ? `input-workbook-${this.currentProjectFileId}` : 'input-workbook-default';
    },
    outputSheetComponentKey() {
      return this.currentProjectFileId ? `output-sheet-${this.currentProjectFileId}` : 'output-sheet-default';
    },
    canLoadSelectedProjectFile() {
      return !!this.selectedProjectFileId && this.selectedProjectFileId !== this.currentProjectFileId;
    },
    currentProjectUpdatedLabel() {
      const updatedAt = this.currentProjectFileMeta?.updatedAt;
      if (!updatedAt) {
        return '';
      }

      try {
        return new Date(updatedAt).toLocaleString();
      } catch {
        return '';
      }
    },
    caseFileBrowserProgressPercent() {
      const total = Math.max(0, Number(this.caseFileBrowserProgress?.total) || 0);
      const done = Math.max(0, Number(this.caseFileBrowserProgress?.done) || 0);
      if (total <= 0) return this.caseFileBrowserLoading ? 8 : 100;
      return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
    },
    caseFileBrowserProgressStyle() {
      return { width: `${this.caseFileBrowserProgressPercent}%` };
    },
    caseFileBrowserNetworkOptions() {
      const counts = new Map();
      (Array.isArray(this.caseFileBrowserRows) ? this.caseFileBrowserRows : []).forEach((row) => {
        (Array.isArray(row.chainNames) ? row.chainNames : []).forEach((chain) => {
          const label = String(chain?.label || '').trim();
          if (!label) return;
          const key = label.toLowerCase();
          const existing = counts.get(key) || { key, label, count: 0 };
          existing.count += 1;
          counts.set(key, existing);
        });
      });
      return Array.from(counts.values()).sort((left, right) => right.count - left.count || left.label.localeCompare(right.label));
    },
    caseFileBrowserVisibleRows() {
      const query = String(this.caseFileBrowserSearch || '').trim().toLowerCase();
      const networkFilter = String(this.caseFileBrowserNetworkFilter || 'all').toLowerCase();
      const rows = (Array.isArray(this.caseFileBrowserRows) ? this.caseFileBrowserRows : []).filter((row) => {
        if (networkFilter !== 'all') {
          const hasNetwork = (Array.isArray(row.chainNames) ? row.chainNames : []).some(chain => String(chain?.label || '').trim().toLowerCase() === networkFilter);
          if (!hasNetwork) return false;
        }
        if (!query) return true;
        return String(row.searchText || '').includes(query);
      });
      const sortMode = String(this.caseFileBrowserSortMode || 'updated_desc');
      const numeric = (row, key) => Number(row?.[key]) || 0;
      const name = row => String(row?.displayName || row?.name || '').toLowerCase();
      return rows.slice().sort((left, right) => {
        if (sortMode === 'name_asc') return name(left).localeCompare(name(right));
        if (sortMode === 'main_desc') return numeric(right, 'mainTransactionCount') - numeric(left, 'mainTransactionCount') || name(left).localeCompare(name(right));
        if (sortMode === 'sub_desc') return numeric(right, 'subtransactionCount') - numeric(left, 'subtransactionCount') || name(left).localeCompare(name(right));
        if (sortMode === 'saplings_desc') return numeric(right, 'preparedSaplingCount') - numeric(left, 'preparedSaplingCount') || name(left).localeCompare(name(right));
        if (sortMode === 'swaps_desc') return numeric(right, 'swapEntryCount') - numeric(left, 'swapEntryCount') || numeric(right, 'updatedAt') - numeric(left, 'updatedAt');
        if (sortMode === 'bridges_desc') return numeric(right, 'bridgeEntryCount') - numeric(left, 'bridgeEntryCount') || numeric(right, 'updatedAt') - numeric(left, 'updatedAt');
        if (sortMode === 'btc_first') return numeric(right, 'btcMainTransactionCount') - numeric(left, 'btcMainTransactionCount') || numeric(right, 'updatedAt') - numeric(left, 'updatedAt');
        if (sortMode === 'raw_desc') return numeric(right, 'rawEntryCount') - numeric(left, 'rawEntryCount') || name(left).localeCompare(name(right));
        if (sortMode === 'storage_desc') return numeric(right, 'storageBytes') - numeric(left, 'storageBytes') || numeric(right, 'updatedAt') - numeric(left, 'updatedAt');
        return numeric(right, 'updatedAt') - numeric(left, 'updatedAt') || name(left).localeCompare(name(right));
      });
    },
    caseFileBrowserSelectedRow() {
      const rows = Array.isArray(this.caseFileBrowserRows) ? this.caseFileBrowserRows : [];
      const visible = this.caseFileBrowserVisibleRows;
      return visible.find(row => row.id === this.caseFileBrowserSelectedId)
        || visible.find(row => row.id === this.currentProjectFileId)
        || visible[0]
        || rows.find(row => row.id === this.caseFileBrowserSelectedId)
        || rows.find(row => row.id === this.currentProjectFileId)
        || null;
    },
    caseFileBrowserSummaryLabel() {
      const total = Array.isArray(this.caseFileBrowserRows) ? this.caseFileBrowserRows.length : 0;
      const visible = this.caseFileBrowserVisibleRows.length;
      const current = this.caseFileBrowserRows.find(row => row.isCurrent);
      if (this.caseFileBrowserLoading) {
        const done = Number(this.caseFileBrowserProgress?.done) || 0;
        const scanTotal = Number(this.caseFileBrowserProgress?.total) || total || this.projectFiles.length;
        return `Loading summaries ${done}/${scanTotal || 0}.`;
      }
      const rows = Array.isArray(this.caseFileBrowserRows) ? this.caseFileBrowserRows : [];
      const swapCases = rows.filter(row => (Number(row.swapEntryCount) || 0) > 0).length;
      const bridgeCases = rows.filter(row => (Number(row.bridgeEntryCount) || 0) > 0).length;
      const bits = [`${visible}/${total || this.projectFiles.length} shown`];
      if (swapCases || bridgeCases) bits.push(`${swapCases} swap case${swapCases === 1 ? '' : 's'} / ${bridgeCases} bridge case${bridgeCases === 1 ? '' : 's'}`);
      if (current) bits.push(`current: ${current.displayName}`);
      if (this.caseFileBrowserLastScannedAtUtc) {
        try {
          bits.push(`last scan ${new Date(this.caseFileBrowserLastScannedAtUtc).toLocaleTimeString()}`);
        } catch {
          // Ignore display-only timestamp formatting failure.
        }
      }
      return bits.join(' · ');
    },
    caseFileBrowserSelectedPreview() {
      const row = this.caseFileBrowserSelectedRow;
      return row ? (this.caseFileBrowserPreviewCache?.[row.id] || null) : null;
    },
    caseFileBrowserSelectedPreviewStatus() {
      return this.caseFileBrowserSelectedPreview?.status || 'idle';
    },
    caseFileBrowserSelectedPreviewFlows() {
      const flows = this.caseFileBrowserSelectedPreview?.flows;
      return Array.isArray(flows) ? flows : [];
    },
    caseFileBrowserSelectedPreviewFlow() {
      const flows = this.caseFileBrowserSelectedPreviewFlows;
      if (!flows.length) return null;
      const index = Math.max(0, Math.min(flows.length - 1, Number(this.caseFileBrowserSelectedPreviewIndex) || 0));
      return flows[index] || flows[0] || null;
    },
    caseFileBrowserSelectedPreviewTitle() {
      const flow = this.caseFileBrowserSelectedPreviewFlow;
      if (flow) return flow.title || flow.label || 'Case flow preview';
      if (this.caseFileBrowserSelectedPreviewStatus === 'loading') return 'Building visual preview…';
      return 'No visual preview loaded yet';
    },
    caseFileBrowserSelectedPreviewSubtitle() {
      const preview = this.caseFileBrowserSelectedPreview || {};
      const flow = this.caseFileBrowserSelectedPreviewFlow;
      if (flow) return `${flow.entryCount || 0} transaction${(flow.entryCount || 0) === 1 ? '' : 's'} · ${flow.nodeCount || 0} nodes · ${flow.summary || preview.summary || 'preview only'}`;
      if (preview.error) return preview.error;
      return 'Lazy-loaded from saved Main Data without opening the case file.';
    },
    caseFileBrowserSelectedPreviewEmptyLabel() {
      const preview = this.caseFileBrowserSelectedPreview || {};
      if (preview.error) return preview.error;
      return 'Select a saved case to load its first visual preview.';
    },
    caseFileBrowserSelectedPreviewPositionLabel() {
      const count = this.caseFileBrowserSelectedPreviewFlows.length;
      if (!count) return '0/0';
      const index = Math.max(0, Math.min(count - 1, Number(this.caseFileBrowserSelectedPreviewIndex) || 0));
      return `${index + 1}/${count}`;
    },
    caseFileBrowserSelectedPreviewCanGoPrevious() {
      return this.caseFileBrowserSelectedPreviewFlows.length > 1 && Number(this.caseFileBrowserSelectedPreviewIndex) > 0;
    },
    caseFileBrowserSelectedPreviewCanGoNext() {
      return this.caseFileBrowserSelectedPreviewFlows.length > 1 && Number(this.caseFileBrowserSelectedPreviewIndex) < this.caseFileBrowserSelectedPreviewFlows.length - 1;
    },
    caseFileBrowserPurgePhrase() {
      return 'DELETE CASE BUDDY DATA';
    },
    caseFileBrowserPurgeCanConfirm() {
      return Boolean(
        this.caseFileBrowserPurgeConfirmChecked
          && String(this.caseFileBrowserPurgeConfirmPhrase || '') === this.caseFileBrowserPurgePhrase
      );
    },
    caseFileBrowserPurgeStorageSummary() {
      const keyCount = this.caseFileBrowserListCaseDataStorageKeys().length;
      const fileCount = Array.isArray(this.projectFiles) ? this.projectFiles.length : 0;
      return `${fileCount} saved Case Buddy file${fileCount === 1 ? '' : 's'} · ${keyCount} matching local-storage item${keyCount === 1 ? '' : 's'}`;
    },
    caseInvestigationVisualizerReadyState() {
      return this.getCaseInvestigationVisualizerReadiness?.() || {
        canOpen: false,
        count: 0,
        summary: 'Prepare at least one graph before visualizing the case.',
        title: 'Prepare at least one graph before visualizing the case.'
      };
    },
    caseInvestigationVisualizerInteractionLocked() {
      return !!this.caseHopSolverRunning || !!this.caseHopSolverPlaceholderRunning || !!this.caseInvestigationHopSolverPlaceholder?.busy;
    },
    caseInvestigationVisualizerStateView() {
      const state = this.caseInvestigationVisualizerState;
      if (state && this.caseInvestigationVisualizerStateMatchesCurrentCase?.(state)) return state;
      return {
        flows: [],
        entries: [],
        flowCount: 0,
        entryCount: 0,
        addressLabelCount: 0,
        qualifyingFlowCount: 0,
        preparedSaplingCount: 0,
        canBuild: false,
        summary: 'Prepare at least one graph before opening the investigation visualizer.'
      };
    },
    caseInvestigationVisualizerFlows() {
      return Array.isArray(this.caseInvestigationVisualizerStateView.flows) ? this.caseInvestigationVisualizerStateView.flows : [];
    },
    caseInvestigationVisualizerCurrentFlow() {
      return this.caseInvestigationVisualizerFlows[this.caseInvestigationVisualizerActiveIndex] || null;
    },
    caseInvestigationVisualizerCurrentLabel() {
      const mode = this.caseInvestigationVisualizerViewMode;
      if (mode === 'allTogether') return `All qualifying flows · ${this.caseInvestigationVisualizerFlows.length}`;
      if (mode === 'stacked') return `Stacked qualifying flows · ${this.caseInvestigationVisualizerFlows.length}`;
      const flow = this.caseInvestigationVisualizerCurrentFlow;
      if (!flow) return 'No visual selected';
      return `${flow.mainLabel || `Flow ${this.caseInvestigationVisualizerActiveIndex + 1}`} · ${this.caseInvestigationVisualizerActiveIndex + 1} of ${this.caseInvestigationVisualizerFlows.length}`;
    },
    caseInvestigationVisualizerCanGoPrevious() {
      return this.caseInvestigationVisualizerViewMode === 'single' && this.caseInvestigationVisualizerActiveIndex > 0;
    },
    caseInvestigationVisualizerCanGoNext() {
      return this.caseInvestigationVisualizerViewMode === 'single' && this.caseInvestigationVisualizerActiveIndex < this.caseInvestigationVisualizerFlows.length - 1;
    },
    caseInvestigationVisualizerRawNodes() {
      return Array.isArray(this.caseInvestigationVisualizerCurrentVisual?.nodes) ? this.caseInvestigationVisualizerCurrentVisual.nodes : [];
    },
    caseInvestigationVisualizerNodes() {
      const rawNodes = this.caseInvestigationVisualizerRawNodes;
      if (!this.caseInvestigationVisualizerManualStartMode || this.caseInvestigationVisualizerTransactionOnlyMode) return rawNodes;
      const groupNodes = this.caseManualSolveVisualGroupNodesForCurrentVisual;
      return groupNodes.length ? [...rawNodes, ...groupNodes] : rawNodes;
    },
    caseInvestigationVisualizerHiddenNodeSet() {
      return new Set(Array.isArray(this.caseInvestigationVisualizerHiddenNodeIds) ? this.caseInvestigationVisualizerHiddenNodeIds : []);
    },
    caseInvestigationVisualizerVisibleNodes() {
      const hidden = this.caseInvestigationVisualizerHiddenNodeSet;
      const groupedMemberKeys = this.caseManualSolveVisualGroupedMemberKeySet;
      const nodes = this.caseInvestigationVisualizerNodes.filter(node => !hidden.has(node.id) && !this.caseManualSolveVisualNodeIsCollapsedGroupMember(node, groupedMemberKeys));
      if (!this.caseInvestigationVisualizerTransactionOnlyMode) return nodes;
      return nodes.filter(node => String(node?.type || '').toLowerCase() === 'tx');
    },
    caseInvestigationVisualizerNodeMap() {
      return new Map(this.caseInvestigationVisualizerVisibleNodes.map(node => [node.id, node]));
    },
    caseInvestigationVisualizerTransactionOnlyCanToggle() {
      return this.caseInvestigationVisualizerNodes.some(node => String(node?.type || '').toLowerCase() === 'tx');
    },
    caseInvestigationVisualizerTransactionOnlyTitle() {
      if (!this.caseInvestigationVisualizerTransactionOnlyCanToggle) return 'Open a visual with transaction nodes before changing graph detail.';
      return this.caseInvestigationVisualizerTransactionOnlyMode
        ? 'Return to the full graph with address nodes and BTC amount/output nodes.'
        : 'Show a simplified transaction-to-transaction graph without address or BTC amount nodes.';
    },
    caseInvestigationVisualizerEdges() {
      const hidden = this.caseInvestigationVisualizerHiddenNodeSet;
      const edges = Array.isArray(this.caseInvestigationVisualizerCurrentVisual?.edges) ? this.caseInvestigationVisualizerCurrentVisual.edges : [];
      if (this.caseInvestigationVisualizerTransactionOnlyMode) {
        return this.buildCaseInvestigationVisualizerTransactionOnlyEdges(edges, this.caseInvestigationVisualizerNodes, hidden);
      }
      if (this.caseInvestigationVisualizerManualStartMode && this.caseManualSolveVisualGroupsForCurrentVisual.length) {
        const visibleNodeIds = new Set(this.caseInvestigationVisualizerVisibleNodes.map(node => node?.id).filter(Boolean));
        return this.caseManualSolveVisualGroupEdgesForCurrentVisual(edges, hidden, visibleNodeIds);
      }
      return edges.filter(edge => !hidden.has(edge.from) && !hidden.has(edge.to));
    },
    caseInvestigationVisualizerViewBoxString() {
      const box = this.caseInvestigationVisualizerViewport || this.caseInvestigationVisualizerCurrentVisual?.viewBox || { x: 0, y: 0, width: 1200, height: 700 };
      return `${Number(box.x) || 0} ${Number(box.y) || 0} ${Number(box.width) || 1200} ${Number(box.height) || 700}`;
    },
    caseInvestigationVisualizerGraphRenderKey() {
      const visual = this.caseInvestigationVisualizerCurrentVisual || {};
      const nodes = Array.isArray(this.caseInvestigationVisualizerVisibleNodes)
        ? this.caseInvestigationVisualizerVisibleNodes
        : [];
      const edges = Array.isArray(this.caseInvestigationVisualizerEdges)
        ? this.caseInvestigationVisualizerEdges
        : [];
      const renderedNodes = nodes.map((node) => ({
        id: node?.id || '',
        type: node?.type || '',
        candidateKey: this.buildCaseManualStartHopInspectorCandidateKey(node?.caseCandidate || node?.candidate || null),
        transform: this.caseFlowVisualNodeTransform(node),
        classes: this.caseInvestigationVisualizerNodeClass(node),
        fillStyle: this.caseManualSolveVisualNodeFillStyle(node),
        labelCategory: this.caseManualVisualNodeLabelCategory(node),
        labelLines: this.caseManualVisualNodeLabelLines(node),
        singleSelected: this.caseInvestigationVisualizerNodeIsSingleSelected(node),
        amountText: node?.amountText || '',
        shortHash: node?.shortHash || '',
        dateText: node?.dateText || '',
        chainText: node?.chainText || '',
        pathLabelLines: Array.isArray(node?.pathLabelLines) ? node.pathLabelLines : [],
        pathBoxWidth: node?.pathBoxWidth || 0,
        pathBoxHeight: node?.pathBoxHeight || 0,
        pathFontSize: node?.pathFontSize || 0,
        pathTransform: this.caseManualVisualPathLabelTransform(node),
        badge: node?.badge || '',
        shape: node?.shape || '',
        shortAddress: node?.shortAddress || '',
        labelFontSize: node?.labelFontSize || 0,
        sourceCount: node?.sourceCount || 0,
        fundsFollowedBar: node?.fundsFollowedBar || null,
        groupName: node?.groupName || '',
        memberSummary: node?.memberSummary || '',
        boxWidth: node?.boxWidth || 0,
        boxHeight: node?.boxHeight || 0,
        groupInnerStyle: this.caseManualSolveVisualNodeIsGroupNode(node)
          ? this.caseManualSolveVisualGroupInnerStyle(node)
          : null,
        groupTextStyle: this.caseManualSolveVisualNodeIsGroupNode(node)
          ? this.caseManualSolveVisualGroupTextStyle(node)
          : null,
        labelText: node?.labelText || '',
        fontSize: node?.fontSize || 0
      }));
      const renderedEdges = edges.map((edge) => ({
        id: edge?.id || '',
        from: edge?.from || '',
        to: edge?.to || '',
        classes: this.caseFlowVisualEdgeClass(edge),
        path: this.caseFlowVisualEdgePathForInvestigation(edge),
        style: this.caseManualSolveVisualEdgeStyle(edge),
        markerEnd: this.caseManualSolveVisualEdgeMarkerEnd(edge),
        bridgeBadge: edge?.bridgeBadge || null,
        bridgeLabel: edge?.bridgeLabel || '',
        bridgeBadgeText: this.caseManualSolveVisualEdgeHasBridgeBadge(edge)
          ? this.caseManualSolveVisualBridgeEdgeBadgeText(edge)
          : '',
        bridgeBadgeTransform: this.caseManualSolveVisualEdgeHasBridgeBadge(edge)
          ? this.caseManualSolveVisualBridgeEdgeBadgeTransform(edge)
          : ''
      }));
      return JSON.stringify({
        visual: visual.id || visual.key || visual.mainHash || '',
        transactionOnly: this.caseInvestigationVisualizerTransactionOnlyMode,
        txSize: this.caseFlowVisualTxNodeSize,
        amountSize: this.caseFlowVisualAmountNodeSize,
        addressRadius: this.caseFlowVisualAddressRadius,
        addressHexagon: this.caseFlowVisualAddressHexagonPoints,
        nodes: renderedNodes,
        edges: renderedEdges
      });
    },
    caseInvestigationVisualizerSelectedNodeIdSet() {
      return new Set(Array.isArray(this.caseInvestigationVisualizerSelectedNodeIds) ? this.caseInvestigationVisualizerSelectedNodeIds : []);
    },
    caseInvestigationVisualizerSelectedNodes() {
      const selected = this.caseInvestigationVisualizerSelectedNodeIdSet;
      return this.caseInvestigationVisualizerVisibleNodes.filter(node => selected.has(node.id));
    },
    caseInvestigationVisualizerSelectedCount() {
      return this.caseInvestigationVisualizerSelectedNodes.length;
    },
    caseManualSolveVisualGroupsForCurrentVisual() {
      const bucket = this.getCaseManualSolveVisualGroupBucket(this.caseInvestigationVisualizerCurrentVisual, false);
      const groups = bucket?.groups && typeof bucket.groups === 'object' && !Array.isArray(bucket.groups) ? bucket.groups : {};
      return Object.values(groups)
        .filter(group => group && typeof group === 'object' && Array.isArray(group.memberKeys) && group.memberKeys.length > 0)
        .sort((left, right) => (Number(left.createdSequence) || 0) - (Number(right.createdSequence) || 0));
    },
    caseManualSolveVisualGroupNodesForCurrentVisual() {
      if (!this.caseInvestigationVisualizerManualStartMode) return [];
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      const rawNodes = this.caseInvestigationVisualizerRawNodes;
      return this.caseManualSolveVisualGroupsForCurrentVisual
        .map((group, index) => this.caseManualSolveBuildVisualGroupNode(group, index, rawNodes, visual))
        .filter(Boolean);
    },
    caseManualSolveVisualGroupedMemberKeySet() {
      const keys = new Set();
      this.caseManualSolveVisualGroupsForCurrentVisual.forEach((group) => {
        (Array.isArray(group.memberKeys) ? group.memberKeys : []).forEach(key => {
          const clean = this.caseManualSolveVisualStableKeyPart(key);
          if (clean) keys.add(clean);
        });
      });
      return keys;
    },
    caseManualSolveSelectedGroups() {
      return this.caseInvestigationVisualizerSelectedNodes
        .filter(node => this.caseManualSolveVisualNodeIsGroupNode(node))
        .map(node => node.groupRecord || this.caseManualSolveVisualGroupsForCurrentVisual.find(group => group.id === node.groupId))
        .filter(Boolean);
    },
    caseManualSolveSelectedGroup() {
      return this.caseManualSolveSelectedGroups[0] || null;
    },
    caseManualSolveCanGroupSelection() {
      if (!this.caseInvestigationVisualizerManualStartMode || this.caseInvestigationVisualizerInteractionLocked) return false;
      const selected = this.caseInvestigationVisualizerSelectedNodes.filter(node => this.caseManualSolveVisualNodeCanBelongToGroup(node));
      const selectedGroups = this.caseManualSolveSelectedGroups.length;
      return (selected.length + selectedGroups) >= 2 || (selected.length >= 1 && selectedGroups >= 1);
    },
    caseManualSolveVisualGroupNormalizedColor() {
      return this.caseManualSolveNormalizeColor(this.caseManualSolveVisualGroupColorDraft);
    },
    caseManualSolveExpandedGroupPanel() {
      const groupId = this.caseManualSolveExpandedGroupId || '';
      if (!groupId) return null;
      const group = this.caseManualSolveVisualGroupsForCurrentVisual.find(item => item.id === groupId);
      if (!group) return null;
      const groupNode = this.caseManualSolveVisualGroupNodesForCurrentVisual.find(node => node.groupId === groupId) || null;
      const rawMembers = this.caseManualSolveVisualGroupMemberNodes(group, this.caseInvestigationVisualizerRawNodes, this.caseInvestigationVisualizerCurrentVisual);
      const width = Math.max(360, Math.min(780, Number(group.expandedWidth) || 560));
      const columns = Math.max(2, Math.min(4, Math.ceil(Math.sqrt(Math.max(1, rawMembers.length)))));
      const memberWidth = Math.max(128, Math.floor((width - 48 - ((columns - 1) * 12)) / columns));
      const memberHeight = 62;
      const rows = Math.max(1, Math.ceil(rawMembers.length / columns));
      const height = Math.max(180, 72 + rows * (memberHeight + 12) + 20);
      const x = Number(group.expandedX);
      const y = Number(group.expandedY);
      const originX = Number.isFinite(x) ? x : ((Number(groupNode?.x) || 0) - (width / 2));
      const originY = Number.isFinite(y) ? y : ((Number(groupNode?.y) || 0) + 88);
      const members = rawMembers.map((node, index) => {
        const col = index % columns;
        const row = Math.floor(index / columns);
        return {
          key: node.groupMemberKey || this.getManualSolveVisualNodeColorKey(node, this.caseInvestigationVisualizerCurrentVisual) || node.id || `member-${index}`,
          x: 24 + col * (memberWidth + 12),
          y: 70 + row * (memberHeight + 12),
          width: memberWidth,
          height: memberHeight,
          title: this.caseManualSolveVisualGroupMemberTitle(node),
          meta: this.caseManualSolveVisualGroupMemberMeta(node)
        };
      });
      return {
        id: group.id,
        name: group.name || 'Visual group',
        x: originX,
        y: originY,
        width,
        height,
        memberSummary: `${rawMembers.length} node${rawMembers.length === 1 ? '' : 's'}`,
        members
      };
    },
    caseManualSolveVisualNormalizedDraftColor() {
      return this.caseManualSolveNormalizeColor(this.caseManualSolveVisualColorDraft);
    },
    caseManualSolveVisualColorCanApply() {
      const colorableCount = this.caseInvestigationVisualizerSelectedNodes
        .filter(node => this.caseManualSolveVisualNodeIsColorable(node)).length;
      return !!this.caseInvestigationVisualizerManualStartMode && colorableCount > 0 && !!this.caseManualSolveVisualNormalizedDraftColor;
    },
    caseManualSolveVisualColorSelectionSummary() {
      const count = Number(this.caseInvestigationVisualizerSelectedCount) || 0;
      if (!count) return 'Select one or more Manual Solve visual nodes first.';
      const colorableNodes = this.caseInvestigationVisualizerSelectedNodes
        .filter(node => this.caseManualSolveVisualNodeIsColorable(node));
      if (!colorableNodes.length) return `${count} selected · no address, transaction, or amount nodes selected`;
      const counts = colorableNodes.reduce((memo, node) => {
        const type = String(node?.type || 'node').toLowerCase();
        memo[type] = (memo[type] || 0) + 1;
        return memo;
      }, {});
      const parts = [
        counts.tx ? `${counts.tx} tx` : '',
        counts.address ? `${counts.address} address` : '',
        counts.amount ? `${counts.amount} amount` : ''
      ].filter(Boolean);
      const skipped = count - colorableNodes.length;
      return `${colorableNodes.length} colorable selected${parts.length ? ` · ${parts.join(' · ')}` : ''}${skipped ? ` · ${skipped} label/helper ignored` : ''}`;
    },
    caseManualSolveVisualColorHasAnyOverrides() {
      const bucket = this.getCaseManualSolveVisualColorBucket(this.caseInvestigationVisualizerCurrentVisual, false);
      return !!bucket && (
        Object.keys(bucket.nodeColors || {}).length > 0 ||
        Object.keys(bucket.edgeColors || {}).length > 0
      );
    },
    caseManualSolveVisualColorHasSelectedOverrides() {
      if (!this.caseInvestigationVisualizerSelectedCount) return false;
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      const bucket = this.getCaseManualSolveVisualColorBucket(visual, false);
      if (!bucket) return false;
      const nodeColors = bucket.nodeColors || {};
      const edgeColors = bucket.edgeColors || {};
      const selectedNodeKeys = this.caseInvestigationVisualizerSelectedNodes
        .filter(node => this.caseManualSolveVisualNodeIsColorable(node))
        .map(node => this.getManualSolveVisualNodeColorKey(node, visual))
        .filter(Boolean);
      if (selectedNodeKeys.some(key => !!nodeColors[key])) return true;
      return this.collectManualSolveVisualEdgesForSelectedNodeColors(this.caseInvestigationVisualizerSelectedNodes, visual)
        .some(edge => !!edgeColors[this.getManualSolveVisualEdgeColorKey(edge, visual)]);
    },
    caseManualSolveVisualColorMarkerDefinitions() {
      if (!this.caseInvestigationVisualizerManualStartMode) return [];
      const colors = new Set();
      this.caseInvestigationVisualizerEdges.forEach((edge) => {
        const color = this.caseManualSolveVisualEdgeDisplayColor(edge, this.caseInvestigationVisualizerCurrentVisual);
        if (color) colors.add(color);
        else if (edge?.isBridgeEdge || edge?.bridge || edge?.caseManualBridgeLinearized) colors.add('#7c3aed');
      });
      return Array.from(colors).map(color => ({
        id: this.caseManualSolveVisualColorMarkerId(color),
        color
      }));
    },
    caseManualSolveVisualLegendHasAmountNodes() {
      return this.caseInvestigationVisualizerVisibleNodes.some(node => node?.type === 'amount');
    },
    caseManualSolveVisualLegendItems() {
      return MANUAL_SOLVE_VISUAL_LEGEND_ITEMS
        .filter(item => this.caseManualSolveVisualLegendItemIsEnabled(item))
        .map((item) => {
          const group = item.key === 'btc-amount'
            ? (this.caseManualSolveVisualLegendHasAmountNodes ? 'core' : 'btc')
            : item.group || 'special';
          return { ...item, group };
        });
    },
    caseManualSolveVisualLegendGroups() {
      const order = [
        { key: 'core', title: '' },
        { key: 'btc', title: this.caseManualSolveVisualLegendHasAmountNodes ? '' : 'May appear in BTC flows' },
        { key: 'special', title: 'When available' }
      ];
      return order
        .map(group => ({
          ...group,
          items: this.caseManualSolveVisualLegendItems.filter(item => (item.group || 'special') === group.key)
        }))
        .filter(group => group.items.length > 0);
    },
    caseInvestigationVisualizerSelectionBoxRect() {
      const box = this.caseInvestigationVisualizerSelectionBox || {};
      const x1 = Number(box.startX) || 0;
      const y1 = Number(box.startY) || 0;
      const x2 = Number(box.endX) || x1;
      const y2 = Number(box.endY) || y1;
      return {
        x: Math.min(x1, x2),
        y: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1)
      };
    },
    caseInvestigationVisualizerCanUndoVisualEdit() {
      return (this.caseInvestigationVisualizerUndoStack || []).length > 0;
    },
    caseInvestigationVisualizerCanRedoVisualEdit() {
      return (this.caseInvestigationVisualizerRedoStack || []).length > 0;
    },
    caseInvestigationVisualizerSelectedNode() {
      const id = this.caseInvestigationVisualizerSelectedNodeId || this.caseInvestigationVisualizerSelectedNodeIds?.[0] || '';
      if (!id) return null;
      return this.caseInvestigationVisualizerNodes.find(node => node.id === id) || null;
    },
    caseInvestigationVisualizerSelectedNodeRows() {
      if (this.caseInvestigationVisualizerSelectedCount > 1) {
        const counts = this.caseInvestigationVisualizerSelectedNodes.reduce((memo, node) => {
          const type = String(node?.type || 'other').toLowerCase();
          memo[type] = (memo[type] || 0) + 1;
          return memo;
        }, {});
        return [
          ['Selected nodes', this.caseInvestigationVisualizerSelectedCount],
          ['Transactions', counts.tx || 0],
          ['Addresses', counts.address || 0],
          ['Amount nodes', counts.amount || 0],
          ['Groups', counts.manualgroup || 0],
          ['Selection', this.caseInvestigationVisualizerSelectedNodes.slice(0, 12).map(node => node.groupName || node.shortHash || node.shortAddress || node.amountText || node.labelText || node.id).filter(Boolean).join(', ')]
        ];
      }
      const rows = this.caseInvestigationVisualizerSelectedNode?.detailRows;
      return Array.isArray(rows) && rows.length ? rows : [];
    },
    caseManualSolveSelectedEditableAmountCandidate() {
      if (!this.caseInvestigationVisualizerManualStartMode) return null;
      if (this.caseInvestigationVisualizerSelectedCount !== 1) return null;
      const candidate = this.caseManualStartSingleSelectedCandidate || this.caseInvestigationVisualizerSelectedCandidate || null;
      return candidate?.caseManualStartCandidate ? candidate : null;
    },
    caseManualSolveAmountEditorTitle() {
      return this.caseManualSolveAmountEditor?.field === 'leftBounds' ? 'Edit left bounds' : 'Edit funds followed';
    },
    caseManualSolveAmountEditorInputLabel() {
      return this.caseManualSolveAmountEditor?.field === 'leftBounds' ? 'Left bounds amount' : 'Funds followed amount';
    },
    caseManualSolveAmountEditorToken() {
      return this.caseManualNormalizeText(this.caseManualSolveAmountEditor?.token || '').toUpperCase();
    },
    caseManualSolveAmountEditorContext() {
      const candidate = this.caseManualSolveAmountEditorCandidate() || this.caseManualSolveSelectedEditableAmountCandidate || {};
      const path = candidate.pathLabel ? `Path ${candidate.pathLabel}` : 'Selected Manual Solve row';
      const hash = candidate.hash || candidate.transactionHash || candidate.txHash || '';
      return hash ? `${path} · ${this.caseManualShorten(hash, 10, 8)}` : path;
    },
    caseManualSolveAmountEditorMaxAmountDisplay() {
      const candidate = this.caseManualSolveAmountEditorCandidate() || this.caseManualSolveSelectedEditableAmountCandidate || {};
      const row = this.caseManualSolveAmountEditorRow(candidate) || [];
      const token = this.caseManualSolveAmountEditorToken || candidate.token || row[CASE_MANUAL_START_COL.TOKEN] || '';
      const amount = this.caseManualParseNumericAmount(row[CASE_MANUAL_START_COL.AMOUNT] || candidate.amount || '');
      if (!Number.isFinite(amount) || amount <= 0) return '';
      return this.caseManualFormatTransactionAmountDisplay(String(amount), token, { includeToken: true });
    },
    caseManualSolveAmountEditorHelpText() {
      if (this.caseManualSolveAmountEditor?.field === 'leftBounds') {
        const max = this.caseManualSolveAmountEditorMaxAmountDisplay;
        return max
          ? `Left bounds may be zero, but must be less than the known transaction amount (${max}).`
          : 'Left bounds may be zero, but cannot be negative. No known transaction amount was available for a maximum check.';
      }
      return 'Funds followed must be greater than zero. It may intentionally exceed the transaction amount when the investigator wants Tool 88 to follow more exits or declare more dormant funds.';
    },
    caseManualSolveAmountEditorCanApply() {
      if (!this.caseManualSolveAmountEditor?.open) return false;
      const amount = this.caseManualParseEditableAmount(this.caseManualSolveAmountEditor.draft);
      if (!Number.isFinite(amount)) return false;
      if (this.caseManualSolveAmountEditor.field === 'fundsFollowed') return amount > 0;
      if (this.caseManualSolveAmountEditor.field === 'leftBounds') {
        if (amount < 0) return false;
        const candidate = this.caseManualSolveAmountEditorCandidate() || this.caseManualSolveSelectedEditableAmountCandidate || {};
        const row = this.caseManualSolveAmountEditorRow(candidate) || [];
        const maxAmount = this.caseManualParseNumericAmount(row[CASE_MANUAL_START_COL.AMOUNT] || candidate.amount || '');
        return !(Number.isFinite(maxAmount) && maxAmount > 0 && amount >= maxAmount);
      }
      return false;
    },
    caseManualFifoInspectorAvailable() {
      const candidate = this.caseManualFifoInspectorSelectedCandidate();
      if (this.caseManualParseFifoDiagnosticsFromCandidate(candidate).has) return true;
      return Boolean(this.caseManualBuildFifoInspectorDiagnosticsFromSelectionRows().has);
    },
    caseInvestigationVisualizerSelectedCandidateView() {
      return this.caseInvestigationVisualizerSelectedCandidate || null;
    },
    caseInvestigationVisualizerCanSolveSelectedHopPlaceholder() {
      const candidate = this.caseInvestigationVisualizerSelectedCandidateView;
      if (candidate?.isPathEnding) return true;
      return !!this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection?.({ quiet: true });
    },
    caseHopSolverCandidateRows() {
      const candidate = this.caseHopSolverCandidate || this.caseHopSolverPlaceholderCandidate || {};
      if (!candidate || !Object.keys(candidate).length) {
        return [['Status', 'No path ending selected.']];
      }
      const fallbackNotes = [
        candidate.missingFundsFollowedFallback ? 'Funds followed is missing; Tool 88 request will follow the full transaction amount.' : '',
        candidate.missingLeftBoundsFallback ? 'Left bounds are missing; Tool 88 request will use left bounds zero.' : ''
      ].filter(Boolean).join(' ');
      return [
        ['Spreadsheet row', candidate.spreadsheetRowNumber || (Number.isInteger(candidate.rowIndex) ? candidate.rowIndex + 1 : '')],
        ['Path label', candidate.pathLabel || ''],
        ['Transaction hash', candidate.hash || candidate.transactionHash || candidate.txHash || ''],
        ['To address', candidate.toAddress || candidate.targetAddress || ''],
        ['From address', candidate.fromAddress || ''],
        ['UTC date', candidate.utcDate || ''],
        ['Amount', [candidate.amount, candidate.token].map(value => normalizeCell(value)).filter(Boolean).join(' ')],
        ['Funds followed', candidate.fundsFollowed || ''],
        ['Left bounds', candidate.leftBoundsDisplay || candidate.leftBoundsNumeric || ''],
        ['Continues As', candidate.continuesAs || ''],
        ['Chain', [candidate.chainName, candidate.chainId ? `(${candidate.chainId})` : ''].filter(Boolean).join(' ')],
        ['Hop logic', candidate.hopLogic || ''],
        ['Fallbacks', fallbackNotes],
        ['Action', candidate.isPathEnding ? 'Eligible for Flow Solver solve-hop.' : 'Needs a Path ends row.']
      ].filter(row => normalizeCell(row[1]));
    },
    caseHopSolverPlaceholderStatusTitle() {
      return this.caseHopSolverStatusTitle || (this.caseHopSolverRunning ? 'Flow Solver running' : 'Solve hop');
    },
    caseHopSolverPlaceholderCandidateRows() {
      return this.caseHopSolverCandidateRows;
    },
    caseInvestigationVisualizerActionSummary() {
      const candidate = this.caseInvestigationVisualizerSelectedCandidateView;
      if (this.caseManualSolveSelectedGroups.length === 1 && this.caseInvestigationVisualizerSelectedCount === 1) return 'Selected group is visual-only. Rename, recolor, move, open, or ungroup it without changing Main Data.';
      if (this.caseInvestigationVisualizerSelectedCount > 1) return 'Multiple visual nodes selected. You can group them visually, or use branch controls carefully; data actions use the first compatible selected case row.';
      if (!candidate) {
        const hopCandidate = this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection?.({ quiet: true });
        if (hopCandidate?.isPathEnding) return `Main Data row ${hopCandidate.spreadsheetRowNumber} is a path ending. Solve hop can use Flow Solver; missing funds-followed/left-bounds will fall back to full amount and zero left bounds.`;
        const manualCandidate = this.caseManualStartActivePathEndingCandidate;
        if (manualCandidate?.isPathEnding) return `Manual visual row ${manualCandidate.spreadsheetRowNumber} is an unresolved Path ends row. Use Solve hop to inspect it with compact Flow Solver and apply the returned rows directly into this visual build-out.`;
        return 'Select a transaction output node with funds-followed and left-bounds data to use prune, graft, or Bridge. Select any Path ends transaction node to use Solve hop.';
      }
      if (candidate.caseManualStartCandidate && candidate.isPathEnding) return `Manual visual row ${candidate.spreadsheetRowNumber} is an unresolved Path ends row. Solve hop opens compact Flow Solver; Use + apply writes the returned rows to Main Data/Raw Data and refreshes the viewport.`;
      if (candidate.isPathEnding) return `Main Data row ${candidate.spreadsheetRowNumber} is a path ending. Prune, graft, Bridge graft, and Flow Solver solve-hop can be opened from here.`;
      return `Main Data row ${candidate.spreadsheetRowNumber} is a qualified output. Prune is available; graft, Bridge graft, and hop solving require a Path ends row.`;
    },
    caseInvestigationVisualizerSelectionTitle() {
      if (this.caseManualSolveSelectedGroups.length === 1 && this.caseInvestigationVisualizerSelectedCount === 1) return this.caseManualSolveSelectedGroup?.name || 'Visual group selected';
      if (this.caseInvestigationVisualizerSelectedCount === 1) return 'Selected node';
      if (this.caseInvestigationVisualizerSelectedCount > 1) return `${this.caseInvestigationVisualizerSelectedCount} nodes selected`;
      return 'Selection details';
    },
    caseInvestigationVisualizerSelectionHelpText() {
      if (this.caseManualSolveSelectedGroups.length === 1 && this.caseInvestigationVisualizerSelectedCount === 1) return 'Visual-only group selected. Double-click it to open the square, rename/color it, or ungroup it.';
      if (this.caseInvestigationVisualizerSelectedCount > 1) return 'Use case actions to group, prune, hide, or inspect the selected visual region.';
      if (this.caseInvestigationVisualizerSelectedCount === 1) return 'Drag the node to move it. Hold Ctrl/Cmd while dragging one node to snap to a nearby node axis.';
      return 'Right-click and drag to select multiple nodes, or left-click a node to select it.';
    },
    caseInvestigationVisualizerViewportHelpText() {
      if (this.caseInvestigationVisualizerInteractionLocked) return 'Solve Hop placeholder active · navigation and visual edits paused';
      const visibleCount = this.caseInvestigationVisualizerVisibleNodes.length;
      const arrowCount = this.caseInvestigationVisualizerEdges.length;
      const selected = this.caseInvestigationVisualizerSelectedCount ? ` · ${this.caseInvestigationVisualizerSelectedCount} selected` : '';
      if (this.caseInvestigationVisualizerFullscreen) {
        return `${visibleCount} nodes · ${arrowCount} arrows${selected}`;
      }
      return `${visibleCount} nodes · ${arrowCount} arrows${selected}`;
    },
    caseInvestigationVisualizerAdvancedBounds() {
      const viewportHeight = typeof window === 'undefined' ? 900 : (window.innerHeight || 900);
      const min = this.caseInvestigationVisualizerFullscreen ? 84 : 118;
      const maxCap = this.caseInvestigationVisualizerFullscreen ? 520 : 620;
      const ratioCap = this.caseInvestigationVisualizerFullscreen ? 0.42 : 0.52;
      const max = Math.max(min + 42, Math.min(maxCap, Math.round(viewportHeight * ratioCap)));
      return { min, max };
    },
    caseInvestigationVisualizerAdvancedClampedHeight() {
      const bounds = this.caseInvestigationVisualizerAdvancedBounds;
      const fallback = this.caseInvestigationVisualizerFullscreen ? 118 : 220;
      const value = Number(this.caseInvestigationVisualizerAdvancedHeight);
      const height = Number.isFinite(value) ? value : fallback;
      return Math.max(bounds.min, Math.min(bounds.max, height));
    },
    caseInvestigationVisualizerAdvancedStyle() {
      return {
        '--case-investigation-advanced-height': `${this.caseInvestigationVisualizerAdvancedClampedHeight}px`
      };
    },
    caseManualSolveBottomPanelBounds() {
      const viewportHeight = typeof window === 'undefined' ? 900 : (window.innerHeight || 900);
      const min = this.caseInvestigationVisualizerFullscreen ? 92 : 110;
      const maxCap = this.caseInvestigationVisualizerFullscreen ? 560 : 640;
      const ratioCap = this.caseInvestigationVisualizerFullscreen ? 0.44 : 0.5;
      const max = Math.max(min + 48, Math.min(maxCap, Math.round(viewportHeight * ratioCap)));
      return { min, max };
    },
    caseManualSolveBottomPanelClampedHeight() {
      const bounds = this.caseManualSolveBottomPanelBounds;
      const value = Number(this.caseManualSolveBottomPanelHeight);
      const fallback = this.caseInvestigationVisualizerFullscreen ? 260 : 280;
      const height = Number.isFinite(value) ? value : fallback;
      return Math.max(bounds.min, Math.min(bounds.max, height));
    },
    caseManualSolveBottomPanelStyle() {
      return {
        '--case-manual-solve-bottom-height': `${this.caseManualSolveBottomPanelClampedHeight}px`
      };
    },
    caseManualSolveBottomPanelHasVisibleContent() {
      return Boolean(this.caseManualSolveEvaluationOpen || this.caseManualStartViewportDisplayControlsOpen);
    },
    caseManualSolveBottomPanelResizerLabel() {
      return this.caseManualSolveBottomPanelHasVisibleContent ? 'Drag to resize panel' : 'Drag to open / resize panel';
    },
    caseManualSolveBottomPanelResizerTitle() {
      return this.caseManualSolveBottomPanelHasVisibleContent
        ? 'Drag to resize the Manual Solve evaluation/settings panel. Double-click to reset.'
        : 'Drag or click to open the Manual Solve settings panel, then continue dragging to resize. Double-click to reset.';
    },
    caseInvestigationVisualizerSidePanelClampedWidth() {
      return this.clampCaseInvestigationVisualizerSidePanelWidth?.(this.caseInvestigationVisualizerSidePanelWidth, { commit: false }) || 390;
    },
    caseInvestigationVisualizerModalStyle() {
      return {
        '--case-investigation-side-panel-width': `${this.caseInvestigationVisualizerSidePanelClampedWidth}px`,
        '--case-investigation-advanced-height': `${this.caseInvestigationVisualizerAdvancedClampedHeight}px`
      };
    },
    caseInvestigationVisualizerDownloadSummary() {
      if (this.caseInvestigationVisualizerDownloading) return 'Building investigation visual export…';
      const hiddenCount = Array.isArray(this.caseInvestigationVisualizerHiddenNodeIds) ? this.caseInvestigationVisualizerHiddenNodeIds.length : 0;
      const hiddenText = hiddenCount ? ` · ${hiddenCount} node${hiddenCount === 1 ? '' : 's'} hidden in this view` : '';
      return `${this.caseInvestigationVisualizerFlows.length} qualifying visual${this.caseInvestigationVisualizerFlows.length === 1 ? '' : 's'} available in investigation mode${hiddenText}.`;
    },
    caseFlowVisualsStateView() {
      return this.caseFlowVisualsState || {
        flows: [],
        entries: [],
        flowCount: 0,
        entryCount: 0,
        addressLabelCount: 0,
        canBuild: false,
        reviewIsCurrent: false,
        summary: 'Open the visual builder to prepare case flow visuals.'
      };
    },
    caseFlowVisualsFlows() {
      return Array.isArray(this.caseFlowVisualsStateView.flows) ? this.caseFlowVisualsStateView.flows : [];
    },
    caseFlowVisualCurrentFlow() {
      return this.caseFlowVisualsFlows[this.caseFlowVisualsActiveIndex] || null;
    },
    caseFlowVisualCurrentLabel() {
      const flow = this.caseFlowVisualCurrentFlow;
      if (!flow) return 'No visual selected';
      return `${flow.mainLabel || `Flow ${this.caseFlowVisualsActiveIndex + 1}`} · ${this.caseFlowVisualsActiveIndex + 1} of ${this.caseFlowVisualsFlows.length}`;
    },
    caseFlowVisualCanGoPrevious() {
      return this.caseFlowVisualsActiveIndex > 0;
    },
    caseFlowVisualCanGoNext() {
      return this.caseFlowVisualsActiveIndex < this.caseFlowVisualsFlows.length - 1;
    },
    caseFlowVisualNodes() {
      return Array.isArray(this.caseFlowVisualsCurrentVisual?.nodes) ? this.caseFlowVisualsCurrentVisual.nodes : [];
    },
    caseFlowVisualNodeMap() {
      return new Map(this.caseFlowVisualNodes.map(node => [node.id, node]));
    },
    caseFlowVisualEdges() {
      return Array.isArray(this.caseFlowVisualsCurrentVisual?.edges) ? this.caseFlowVisualsCurrentVisual.edges : [];
    },
    caseFlowVisualViewBoxString() {
      const box = this.caseFlowVisualsViewport || this.caseFlowVisualsCurrentVisual?.viewBox || { x: 0, y: 0, width: 1200, height: 700 };
      return `${Number(box.x) || 0} ${Number(box.y) || 0} ${Number(box.width) || 1200} ${Number(box.height) || 700}`;
    },
    caseFlowVisualTxNodeSize() {
      return CASE_FLOW_VISUALS_TX_NODE_SIZE;
    },
    caseFlowVisualAmountNodeSize() {
      return CASE_FLOW_VISUALS_AMOUNT_NODE_SIZE;
    },
    caseFlowVisualAddressRadius() {
      return CASE_FLOW_VISUALS_ADDRESS_RADIUS;
    },
    caseFlowVisualAddressHexagonPoints() {
      const radius = Number(CASE_FLOW_VISUALS_ADDRESS_RADIUS) || 42;
      const points = [];
      for (let index = 0; index < 6; index += 1) {
        const angle = (Math.PI / 3) * index - Math.PI / 6;
        points.push(`${Math.cos(angle) * radius},${Math.sin(angle) * radius}`);
      }
      return points.join(' ');
    },
    caseFlowVisualSelectedNode() {
      const id = this.caseFlowVisualsSelectedNodeId;
      if (!id) return null;
      return this.caseFlowVisualNodes.find(node => node.id === id) || null;
    },
    caseFlowVisualSelectedNodeRows() {
      const rows = this.caseFlowVisualSelectedNode?.detailRows;
      return Array.isArray(rows) && rows.length ? rows : [];
    },
    caseFlowVisualCompletionLabel() {
      if (this.caseModalOperation.active && this.caseModalOperation.target === 'markCaseFlowVisualsDone') return 'Marking…';
      return this.caseFlowVisualsStateView.reviewIsCurrent ? 'Visuals reviewed' : 'Mark visuals done';
    },
    caseFlowVisualDownloadSummary() {
      if (this.caseFlowVisualsDownloading) {
        const label = this.caseFlowVisualsDownloadFormat === 'jpeg' ? 'JPG' : String(this.caseFlowVisualsDownloadFormat || 'visual').toUpperCase();
        return `Building ${label} package…`;
      }
      return `${this.caseFlowVisualsFlows.length} visual${this.caseFlowVisualsFlows.length === 1 ? '' : 's'} ready for SVG, PNG, or JPG package export.`;
    },
    caseFlowVisualsIsInvestigation() {
      return this.caseFlowVisualsMode === 'investigation' || this.caseFlowVisualsIntent === 'investigation';
    },
    caseFlowVisualsModalTitle() {
      return this.caseFlowVisualsIsInvestigation ? 'Flow Solver - Investigate the Case Visually' : 'Create Case Flow Visuals';
    },
    caseFlowVisualsModalDescription() {
      return this.caseFlowVisualsIsInvestigation
        ? 'Preview partial graphs directly from Main Data. This interim view does not mark final report visuals complete.'
        : 'Preview Tool87-style case visuals built directly from Main Data and the reviewed address legend. The recommended visual setup is applied by default so the normal path is quick review, then one-click package download.';
    },
    caseFlowVisualsSummaryStatusLabel() {
      return this.caseFlowVisualsStateView.reviewIsCurrent ? 'Reviewed' : this.caseFlowVisualsStateView.canBuild ? 'Ready' : 'Needs data';
    },
    caseFlowVisualsNoticeTitle() {
      if (this.caseFlowVisualsStateView.reviewIsCurrent) return 'Visuals reviewed';
      return this.caseFlowVisualsStateView.canBuild ? 'Ready to preview/export' : 'No visuals yet';
    },
    selectionSummary() {
      if (!Array.isArray(this.lastSelection) || this.lastSelection.length === 0) {
        return 'No selection';
      }

      if (this.lastSelection.length > 1) {
        return `${this.lastSelection.length} ranges selected`;
      }

      const [startRow, startCol, endRow, endCol] = this.lastSelection[0];
      const rowStart = Math.min(startRow, endRow);
      const rowEnd = Math.max(startRow, endRow);
      const colStart = Math.min(startCol, endCol);
      const colEnd = Math.max(startCol, endCol);
      const rowCount = rowEnd - rowStart + 1;
      const colCount = colEnd - colStart + 1;

      return `${this.toColumnLabel(colStart)}${rowStart + 1}:${this.toColumnLabel(colEnd)}${rowEnd + 1} · ${rowCount}×${colCount}`;
    }
  },
  watch: {
    caseWorkspaceDarkModeActive(isActive) {
      this.applyCaseWorkspaceDarkModeDocumentClass?.(isActive);
      this.$nextTick(() => {
        this.$refs.inputSpreadsheet?.refreshDimensions?.();
        this.$refs.outputSpreadsheet?.refreshDimensions?.();
      });
    },
    currentProjectFileId(newId, oldId) {
      if (newId === oldId) return;
      if (this.caseIntakePreparation?.active) {
        this.requestCaseIntakePreparationStop?.('Preparation stopped because the open case changed.', { silent: true, caseChanged: true });
      }
      if (this.caseAttributionWorkflowRun?.active) {
        this.requestCaseAttributionWorkflowStop?.('Attribution workflow stopped because the open case changed.', { silent: true, caseChanged: true });
      }
      if (this.caseCleanupWorkflowRun?.active) {
        this.requestCaseCleanupWorkflowStop?.('Consistency and cleanup workflow stopped because the open case changed.', { silent: true, caseChanged: true });
      }
      this.abortCaseManualStartAllFlowsAuto?.('Guided Flow Solver stopped because the open case changed.', { silent: true });
      if (this.caseManualStartVisualizerOpening?.active) {
        this.abortCaseManualStartVisualizerOpening?.('cancel', { silent: true });
      }
      this.caseManualStartVisualizerPreparedCacheKey = '';
      this.caseManualStartVisualizerPreparedState = null;
      this.caseManualStartVisualizerHydrationByKey = {};
      this.showCaseManualSolveBackdropCloseConfirm = false;
      this.caseManualSolveIntroOpen = false;
      this.caseManualSolveIntroDontShowAgain = false;
      this.caseQuickLookupCaseSwitcherOpen = false;
      this.resetCaseIntakePreparationForCase?.();
      this.resetCaseAttributionWorkflowRunForCase?.();
      this.resetCaseCleanupWorkflowRunForCase?.();
      this.resetCaseIntakeContinuationCheckForCase?.();
      this.resetCaseWorkflowRawDataTool88VisualState?.('Case file changed; clearing stale Tool 88 raw-data preview.', {
        stopTool88: true,
        clearVisibleSolverOutput: true,
        clearAutoContinue: true
      });
      this.resetCaseInputFlowStory?.({ keepPreference: true });
      if (this.caseInputFlowStoryVisible) this.scheduleCaseInputFlowStoryRefresh?.(180, { force: true });
      this.queueCaseTitleInlineFit?.(120);
      const quickLookupAutoCreateTransition = Boolean(
        this.caseBuddyPresentationMode === CASE_BUDDY_PRESENTATION_MODE_LOOKUP
          && newId
          && (this.caseQuickLookupAutoCreatingCase || this.caseQuickLookupEnsureCasePromise)
      );
      if (!quickLookupAutoCreateTransition) {
        this.resetCaseQuickLookupForCaseChange?.({ preserveHashDraft: false });
      }
      if (this.caseBuddyPresentationMode === CASE_BUDDY_PRESENTATION_MODE_LOOKUP && !newId) {
        this.queueCaseQuickLookupEnsureCase?.(360);
      }
    },
    caseQuickLookupVisible(isVisible) {
      this.setCaseQuickLookupScrollLock?.(isVisible);
      if (isVisible) {
        this.queueCaseQuickLookupEnsureCase?.(220);
        this.$nextTick(() => this.focusCaseQuickLookupInput?.());
      } else {
        this.caseQuickLookupSettingsOpen = false;
        this.caseQuickLookupCaseSwitcherOpen = false;
      }
    },
    casePageHydrating(isHydrating) {
      if (!isHydrating && this.caseBuddyPresentationMode === CASE_BUDDY_PRESENTATION_MODE_LOOKUP) {
        this.queueCaseQuickLookupEnsureCase?.(240);
      }
    },
    caseWorkflowAssistantOverallWorkflowComplete(isReady) {
      if (!isReady) return;
      if (this.caseQuickLookup?.reportRequested || this.caseQuickLookup?.status === 'complete') {
        this.caseQuickLookupSetState?.({
          status: 'report-ready',
          progress: 100,
          reportRequested: false,
          lastResult: 'report-ready',
          message: 'The case workflow and one-click report package are ready to review.',
          detail: ''
        });
        this.clearCaseQuickLookupReportWatchTimer?.();
      }
    },
    'caseManualStartViewportAuto.active'(isActive, wasActive) {
      if (!isActive && wasActive && this.caseQuickLookup?.manualSolveLaunched) {
        this.$nextTick(() => this.refreshCaseQuickLookupManualSolveResult?.({ source: 'per-flow-auto-stopped' }));
      }
    },
    'caseAutomation.active'(isActive, wasActive) {
      if (!isActive && wasActive && this.caseQuickLookup?.reportRequested) {
        this.scheduleCaseQuickLookupReportStateReview?.(1800);
      }
    },
    'caseAutomation.busy'(isBusy, wasBusy) {
      if (!isBusy && wasBusy && this.caseQuickLookup?.reportRequested) {
        this.scheduleCaseQuickLookupReportStateReview?.(1800);
      }
    },
    showCaseAutomationPauseModal(isOpen, wasOpen) {
      if (isOpen && !wasOpen && this.caseQuickLookup?.reportRequested) {
        this.scheduleCaseQuickLookupReportStateReview?.(90);
      }
    },
    caseWorkflowBackgroundAutomationActive(isActive, wasActive) {
      if (!isActive && wasActive && this.caseQuickLookup?.reportRequested) {
        this.scheduleCaseQuickLookupReportStateReview?.(1800);
      }
    },
    'projectFileReadyState.input'(ready) {
      if (ready && this.caseInputFlowStoryVisible) {
        this.scheduleCaseInputFlowStoryRefresh?.(100, { force: true });
      }
    },
    'caseSupportState.caseDataRevision'(newRevision, oldRevision) {
      if (Number(newRevision) === Number(oldRevision) || !this.caseInputFlowStoryVisible) return;
      this.scheduleCaseInputFlowStoryRefresh?.(320, { force: true });
    },
    caseInputFlowStoryVisible(isVisible) {
      if (isVisible) {
        const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
        const canReuseCurrentModel = Boolean(
          this.caseInputFlowStoryModel
          && this.caseInputFlowStoryStatus === 'ready'
          && Number(this.caseInputFlowStoryLastBuiltRevision) === currentRevision
        );
        if (canReuseCurrentModel) {
          this.$nextTick(() => {
            this.installCaseInputFlowStoryObserver?.();
            this.warmCaseInputFlowStoryVisibleCards?.({ includeFirst: true });
          });
        } else {
          this.scheduleCaseInputFlowStoryRefresh?.(40, { force: true });
        }
      } else {
        this.disconnectCaseInputFlowStoryObserver?.();
        this.clearCaseInputFlowStoryLazyTimers?.({ keepCache: true, keepRefresh: false });
        this.$nextTick(() => this.$refs.inputSpreadsheet?.refreshDimensions?.());
      }
    },
    caseSimpleLaunchAutoSkipResolvable(value) {
      if (Boolean(this.caseAutomation?.skipResolvableReview) !== Boolean(value)) {
        this.setCaseSkipResolvableReviewsEnabled?.(value);
      }
    },
    'caseAutomation.skipResolvableReview'(value) {
      if (Boolean(this.caseSimpleLaunchAutoSkipResolvable) !== Boolean(value)) {
        this.caseSimpleLaunchAutoSkipResolvable = Boolean(value);
      }
    },
    caseDisplayName(newName, oldName) {
      if (newName === oldName) return;
      if (!this.caseTitleInlineEditing) {
        this.caseTitleInlineDraft = '';
      }
      this.caseTitleInlineFontSizePx = 0;
      this.queueCaseTitleInlineFit?.(80);
    },
    showCaseFileBrowserModal(isOpen) {
      if (isOpen) {
        this.scheduleCaseFileBrowserSelectedPreviewLoad?.({ reason: 'modal-open' });
        this.scheduleCaseFileBrowserVisiblePreviewWarmup?.(160);
      } else {
        this.stopCaseFileBrowserCardPreviewCycle?.();
        this.clearCaseFileBrowserPreviewTimers?.({ keepCache: true });
      }
    },
    caseFileBrowserSelectedId() {
      this.caseFileBrowserSelectedPreviewIndex = 0;
      this.scheduleCaseFileBrowserSelectedPreviewLoad?.({ reason: 'selection-change' });
    },
    caseFileBrowserSearch() {
      this.scheduleCaseFileBrowserVisiblePreviewWarmup?.(220);
    },
    caseFileBrowserNetworkFilter() {
      this.scheduleCaseFileBrowserVisiblePreviewWarmup?.(220);
    },
    caseFileBrowserSortMode() {
      this.scheduleCaseFileBrowserVisiblePreviewWarmup?.(220);
    },
    caseFileBrowserRows() {
      this.scheduleCaseFileBrowserSelectedPreviewLoad?.({ reason: 'rows-updated' });
      this.scheduleCaseFileBrowserVisiblePreviewWarmup?.(240);
    },
    showTool68PreparationModal(isOpen, wasOpen) {
      if (!isOpen && wasOpen && this.caseIntakeContinuationTool68Launched) {
        this.caseIntakeContinuationTool68Launched = false;
        this.scheduleCaseIntakeContinuationCheck?.(520, {
          source: 'workflow-assistant-tool68-close',
          silent: true,
          retryOnRevisionChange: true,
          attempt: 0
        });
      }
    },
    showCaseInvestigationVisualizerModal(isOpen, wasOpen) {
      if (isOpen && !wasOpen && this.caseInvestigationVisualizerManualStartMode) {
        this.$nextTick(() => this.openCaseManualSolveIntroIfNeeded?.());
      }
      if (!isOpen && this.caseManualStartAllFlowsAutoActive) {
        this.abortCaseManualStartAllFlowsAuto?.('Guided Flow Solver stopped because the visualizer closed.', { silent: true });
      }
      if (!isOpen && this.caseManualStartViewportAutoActive) {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto stopped because the visualizer closed.', { silent: true });
      }
      if (!isOpen) {
        this.caseManualStartLaunchSource = '';
        this.showCaseManualSolveBackdropCloseConfirm = false;
        this.caseManualSolveIntroOpen = false;
        this.caseManualSolveIntroDontShowAgain = false;
        this.clearCaseManualStartViewportCompletionCheckTimers();
        // Tool88Page is intentionally large. Keep it mounted while Manual Solve
        // is open for fast hop-to-hop work, then release it when the modal closes
        // so a later reopen does not eagerly rebuild the hidden inspector.
        this.closeCaseManualStartHopInspector?.();
        this.caseManualStartHopInspectorMounted = false;
        this.caseManualStartHopInspectorFullToolOpen = false;
      }
      if (!isOpen && this.caseManualStartViewportCompletionEffect?.visible) {
        this.clearCaseManualStartViewportCompletionEffect();
      }
      if (!isOpen) {
        this.clearCaseManualStartViewportAutomationCallouts?.();
        this.clearCaseManualStartViewportWorkIndicator?.({ immediate: true });
      }
      if (!isOpen && this.autoSaveEnabled && (this.caseManualStartAutosavePending || this.caseManualStartAutosaveTimer)) {
        this.flushCaseManualStartAutosave?.({ force: true, silent: true, reason: 'Manual Solve viewport closed with pending case-data changes.' });
      }
      if (!isOpen) {
        this.caseManualStartBranchDeletionPreview = null;
        this.caseManualStartBranchDeletionApplying = false;
        this.caseManualSolveHeatMapEnabled = false;
        this.caseManualSolveExpandedGroupId = '';
        this.closeCaseManualSolveVisualLegend?.();
      }
      if (!isOpen && wasOpen && this.caseIntakeContinuationManualSolveLaunched) {
        this.caseIntakeContinuationManualSolveLaunched = false;
        this.scheduleCaseIntakeContinuationCheck?.(700, {
          source: 'workflow-assistant-manual-solve-close',
          silent: true,
          retryOnRevisionChange: true,
          attempt: 0
        });
      }
      if (!isOpen && wasOpen && this.caseQuickLookup?.manualSolveLaunched) {
        this.caseQuickLookupSetState?.({ manualSolveLaunched: false });
        this.$nextTick(() => this.refreshCaseQuickLookupManualSolveResult?.({ source: 'flow-solver-closed' }));
      }
      this.$nextTick(() => this.syncCaseInvestigationVisualizerPageScrollLock?.());
    },
    caseInvestigationVisualizerManualStartMode(isManual, wasManual) {
      if (isManual && !wasManual && this.showCaseInvestigationVisualizerModal) {
        this.$nextTick(() => this.openCaseManualSolveIntroIfNeeded?.());
      } else if (!isManual) {
        this.abortCaseManualStartAllFlowsAuto?.('Guided Flow Solver stopped because Manual Solve mode closed.', { silent: true });
        this.caseManualSolveIntroOpen = false;
        this.caseManualSolveIntroDontShowAgain = false;
      }
    },
    caseInvestigationVisualizerFullscreen() {
      this.$nextTick(() => {
        this.clampCaseInvestigationVisualizerAdvancedHeight?.();
        this.clampCaseManualSolveBottomPanelHeight?.();
        this.clampCaseInvestigationVisualizerSidePanelWidth?.();
        this.syncCaseInvestigationVisualizerPageScrollLock?.();
      });
    },
    caseManualStartHopInspectorSelectedKey(newKey, oldKey) {
      if (!this.caseManualStartHopInspectorOpen || !newKey || newKey === oldKey) return;
      this.queueCaseManualStartHopInspectorAutoCheck(this.caseManualStartHopInspectorSelectedCandidate, { force: true, reason: 'Selected a new unresolved ending. Flow Solver checks will run automatically.' });
    },
    caseManualStartUnresolvedEndingCount(newCount, oldCount) {
      if (!this.caseInvestigationVisualizerManualStartMode || !this.showCaseInvestigationVisualizerModal) return;
      if (Number(oldCount) > 0 && Number(newCount) === 0) {
        this.queueCaseManualStartViewportCompletionCheck('all unresolved Manual Solve endings resolved');
        if (this.caseQuickLookup?.manualSolveLaunched) {
          this.$nextTick(() => this.refreshCaseQuickLookupManualSolveResult?.({ source: 'all-endings-resolved' }));
        }
      }
    },
    caseManualSolveSelectedGroup(group) {
      this.syncCaseManualSolveSelectedGroupDraft(group);
    }
  },
created() {
  this.initializeProjectFiles();
  this.consumePendingCasePrepBuddyHandoff?.({ source: 'case-buddy-created' });
  this.loadCaseSupportState();
  this.entityCuratorLocalOverlay = loadEntityCuratorOverlayFromStorage();
  this.loadEntityCuratorRemoteKeywords({ silent: true });
  this.loadAuthSession();
},
  mounted() {
    this.applyCaseWorkspaceDarkModeDocumentClass?.(this.caseWorkspaceDarkModeActive);
    this.startCaseInitialHydration({ message: 'Loading case workspace…' });

    this.$nextTick(() => {
      this.syncGridStateFromHot();
      this.syncFormulaBarFromSelection();
      this.$refs.inputSpreadsheet?.refreshDimensions?.();
      this.$refs.outputSpreadsheet?.refreshDimensions?.();
      if (!this.selectedProjectFileId) {
        this.selectedProjectFileId = this.currentProjectFileId;
      }
      this.queueCaseSummaryRefresh();
      if (this.caseInputFlowStoryVisible) this.scheduleCaseInputFlowStoryRefresh?.(260, { force: true });
      this.queueCaseTitleInlineFit?.(80);
      this.completeCaseInitialHydrationIfReady({ delay: 260 });
      if (this.caseBuddyPresentationMode === CASE_BUDDY_PRESENTATION_MODE_LOOKUP) {
        this.queueCaseQuickLookupEnsureCase?.(520);
      }
    });

    this.keyHandler = (event) => {
      const target = event.target;

      if (this.caseManualStartVisualizerOpening?.active) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.requestCaseManualStartVisualizerOpeningCancel?.();
        } else if (event.key === 'Tab') {
          event.preventDefault();
          this.focusCaseManualStartVisualizerOpeningAction?.(event.shiftKey ? -1 : 1);
        } else if (!(['Enter', ' '].includes(event.key) && target?.closest?.('.case-manual-start-opening-lock__actions button'))) {
          event.preventDefault();
        }
        return;
      }

      if (this.caseCleanupWorkflowRun?.active) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.requestCaseCleanupWorkflowStop?.('Consistency and cleanup workflow stopped by user.', { user: true });
        } else if (event.key !== 'Tab') {
          event.preventDefault();
        }
        return;
      }

      if (this.caseAttributionWorkflowRun?.active) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.requestCaseAttributionWorkflowStop?.('Attribution workflow stopped by user.', { user: true });
        } else if (event.key !== 'Tab') {
          event.preventDefault();
        }
        return;
      }

      if (this.caseIntakePreparation?.active) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.requestCaseIntakePreparationStop?.('Preparation stopped by user.', { user: true });
        } else if (event.key !== 'Tab') {
          event.preventDefault();
        }
        return;
      }

      if (this.caseManualStartAllFlowsAutoActive) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.requestCaseManualStartAllFlowsAutoStop?.('Guided Auto stop requested by the user.');
        } else if (event.key === 'Tab') {
          event.preventDefault();
          this.$refs.caseManualStartAllFlowsStopButton?.focus?.();
        } else {
          event.preventDefault();
        }
        return;
      }

      if (this.caseManualStartAllFlowsAuto?.resultVisible) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.dismissCaseManualStartAllFlowsResult?.();
        }
        return;
      }

      if (this.caseManualSolveIntroOpen) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.dismissCaseManualSolveIntro?.();
        }
        return;
      }

      if (this.showCaseManualSolveBackdropCloseConfirm) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.dismissCaseManualSolveBackdropCloseConfirm?.();
        }
        return;
      }

      if (this.showCaseInvestigationVisualizerModal && !this.isTextEditingTarget(target)) {
        if (this.caseInvestigationVisualizerInteractionLocked) {
          if (event.key === 'Escape') {
            event.preventDefault();
            if (this.caseManualSolveVisualLegendOpen) {
              this.closeCaseManualSolveVisualLegend();
            } else {
              this.showCaseToast('Solve Hop placeholder is active. Use Request stop before closing or navigating.');
            }
          }
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          if (this.caseManualSolveVisualLegendOpen) {
            this.closeCaseManualSolveVisualLegend();
          } else if (this.caseInvestigationVisualizerFullscreen) {
            this.toggleCaseInvestigationVisualizerFullscreen();
          } else {
            this.cancelCaseInvestigationVisualizer();
          }
          return;
        }

        const shortcutKey = String(event.key || '').toLowerCase();
        const isUndoVisual = (event.ctrlKey || event.metaKey) && !event.shiftKey && shortcutKey === 'z';
        const isRedoVisual = ((event.ctrlKey || event.metaKey) && event.shiftKey && shortcutKey === 'z') || ((event.ctrlKey || event.metaKey) && shortcutKey === 'y');

        if ((event.key === 'Delete' || event.key === 'Backspace') && this.caseInvestigationVisualizerSelectedCount) {
          event.preventDefault();
          this.deleteSelectedCaseInvestigationVisualizerBranches();
          return;
        }

        if (isUndoVisual) {
          event.preventDefault();
          if (this.caseInvestigationVisualizerCanUndoVisualEdit) {
            this.undoCaseInvestigationVisualizerVisualEdit();
          }
          return;
        }

        if (isRedoVisual) {
          event.preventDefault();
          if (this.caseInvestigationVisualizerCanRedoVisualEdit) {
            this.redoCaseInvestigationVisualizerVisualEdit();
          }
          return;
        }
      }

      if (this.caseQuickLookupVisible && this.caseQuickLookupCaseSwitcherOpen && event.key === 'Escape') {
        event.preventDefault();
        this.closeCaseQuickLookupCaseSwitcher?.();
        return;
      }

      if (this.caseQuickLookupVisible && this.caseQuickLookupSettingsOpen) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeCaseQuickLookupSettings?.();
        }
        return;
      }

      if (this.caseQuickLookupVisible && !this.caseAnyModalVisible) {
        if (event.key === 'Escape' && this.caseQuickLookup?.status === 'choice') {
          event.preventDefault();
          this.resetCaseQuickLookup?.();
        }
        if (!this.isTextEditingTarget(target)) event.preventDefault();
        return;
      }

      if (event.key === 'Escape' && this.formulaBarEditing && !this.isInsideOutputSheet(target)) {
        event.preventDefault();
        this.cancelFormulaBarEdit();
        return;
      }

      if (event.key === 'Enter' && this.formulaBarEditing && !this.isInsideOutputSheet(target) && !this.isTextEditingTarget(target)) {
        event.preventDefault();
        this.applyFormulaBar();
        return;
      }

      if (event.key === 'Escape' && this.inputFullscreen && !this.isTextEditingTarget(target)) {
        event.preventDefault();
        this.setInputFullscreen(false);
        return;
      }


      if (event.key === '=' && !event.ctrlKey && !event.metaKey && !event.altKey && !this.isTextEditingTarget(target) && !this.isInsideOutputSheet(target)) {
        const targetCell = this.lockFormulaTarget(this.getSelectedAnchorCell());
        if (targetCell) {
          event.preventDefault();
          this.formulaBarEditing = true;
          this.formulaBarDraft = '=';
          this.formulaBarBaseDraft = '=';
          this.formulaCapturedReferences = [];
          this.$nextTick(() => {
            const input = this.$refs.formulaInput;
            input?.focus?.();
            input?.setSelectionRange?.(1, 1);
          });
        }
        return;
      }

      if ((event.ctrlKey || event.metaKey) && !event.altKey) {
        const key = event.key.toLowerCase();

        if ((key === 'z' && !event.shiftKey) || key === 'y' || (key === 'z' && event.shiftKey)) {
          if (this.isTextEditingTarget(target) || !this.shouldProxyInputShortcut(target)) {
            return;
          }

          event.preventDefault();

          if (key === 'z' && !event.shiftKey) {
            this.undoInputSheet();
          } else {
            this.redoInputSheet();
          }

          return;
        }

        if (this.formulaBarEditing && !this.isTextEditingTarget(target)) {
          return;
        }

        if (this.isTextEditingTarget(target) || this.isInsideOutputSheet(target) || this.isInsideOutputSheet(document.activeElement)) {
          return;
        }

        if (key === 'b') {
          event.preventDefault();
          this.toggleBold();
        } else if (key === 'i') {
          event.preventDefault();
          this.toggleItalic();
        } else if (key === 'u') {
          event.preventDefault();
          this.toggleUnderline();
        } else if (key === 'x' && event.shiftKey) {
          event.preventDefault();
          this.toggleStrikeThrough();
        }
      }
    };

    document.addEventListener('keydown', this.keyHandler);

    this.colorPopoverPointerHandler = (event) => {
      if (!this.activeColorPicker) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      if (target.closest('.color-picker-anchor') || target.closest('.color-popover')) {
        return;
      }

      this.cancelColorPicker();
    };

    document.addEventListener('pointerdown', this.colorPopoverPointerHandler, true);


this.installHermesSheetBridge();
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      this.caseManualStartAutosaveBeforeUnloadHandler = () => {
        if (this.autoSaveEnabled && (this.caseManualStartAutosavePending || this.caseManualStartAutosaveTimer)) {
          this.flushCaseManualStartAutosave?.({ force: true, silent: true, reason: 'Manual Solve page unload autosave.' });
        }
      };
      window.addEventListener('beforeunload', this.caseManualStartAutosaveBeforeUnloadHandler);
      this.caseTitleInlineResizeHandler = () => this.queueCaseTitleInlineFit?.(120);
      window.addEventListener('resize', this.caseTitleInlineResizeHandler);
    }
  },
beforeUnmount() {
  this.applyCaseWorkspaceDarkModeDocumentClass?.(false);
  this.cancelCaseQuickLookupRun?.({ silent: true, reset: false });
  this.clearCaseQuickLookupEnsureCaseTimer?.();
  this.clearCaseQuickLookupReportWatchTimer?.();
  this.setCaseQuickLookupScrollLock?.(false, { force: true });
  this.requestCaseIntakePreparationStop?.('Preparation stopped because Case Buddy is unloading.', { silent: true, force: true });
  this.requestCaseAttributionWorkflowStop?.('Attribution workflow stopped because Case Buddy is unloading.', { silent: true, force: true });
  this.requestCaseCleanupWorkflowStop?.('Consistency and cleanup workflow stopped because Case Buddy is unloading.', { silent: true, force: true });
  this.abortCaseManualStartVisualizerOpening?.('cancel', { silent: true });
  this.clearCaseManualStartVisualizerOpeningTimer?.();
  this.releaseCaseManualStartVisualizerOpeningScrollLock?.();
  this.releaseCaseIntakePreparationScrollLock?.();
  this.releaseCaseAttributionWorkflowScrollLock?.();
  this.releaseCaseCleanupWorkflowScrollLock?.();
  this.clearCaseIntakeContinuationRefreshTimer?.();
  this.clearCaseInputFlowStoryLazyTimers?.({ keepCache: false, keepRefresh: false });
  this.disconnectCaseInputFlowStoryObserver?.();
  this.clearCaseFileBrowserPreviewTimers?.({ keepCache: false });
  if (this.caseTitleInlineFitTimer && typeof window !== 'undefined') {
    window.clearTimeout(this.caseTitleInlineFitTimer);
    this.caseTitleInlineFitTimer = null;
  }
  if (this.caseTitleInlineResizeHandler && typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
    window.removeEventListener('resize', this.caseTitleInlineResizeHandler);
    this.caseTitleInlineResizeHandler = null;
  }
  document.removeEventListener('keydown', this.keyHandler);
  this.endCaseManualStartHopInspectorDrag();
  this.endCaseManualSolveGetDataPanelDrag?.();
  this.clearCaseManualStartHopInspectorAutoCheckTimer?.();
  this.abortCaseManualStartAllFlowsAuto?.('Guided Flow Solver stopped because Case Buddy is unloading.', { silent: true, noToolStop: true });
  this.stopCaseManualStartViewportAuto?.('Manual Solve Auto stopped because Case Buddy is unloading.', { silent: true, noToolStop: true });

  if (this.colorPopoverPointerHandler) {
    document.removeEventListener('pointerdown', this.colorPopoverPointerHandler, true);
  }

  this.clearFormulaRefocusTimer();
  this.clearColorPickerCloseTimer();
  this.clearCaseInitialHydrationTimer();
  this.clearCaseGlobalOperationTimer();
  this.clearCaseSummaryRefreshTimer();
  if (this.caseSupportPersistTimer) {
    this.persistCaseSupportState();
  }
  if (this.autoSaveEnabled && (this.caseManualStartAutosavePending || this.caseManualStartAutosaveTimer)) {
    this.flushCaseManualStartAutosave?.({ force: true, silent: true, reason: 'Manual Solve autosave before Case Buddy unload.' });
  } else {
    this.clearCaseManualStartAutosaveTimer?.();
  }
  if (this.caseManualStartAutosaveBeforeUnloadHandler && typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
    window.removeEventListener('beforeunload', this.caseManualStartAutosaveBeforeUnloadHandler);
    this.caseManualStartAutosaveBeforeUnloadHandler = null;
  }
  const workflowTool88 = this.$refs.caseWorkflowRawDataTool88;
  if (workflowTool88 && typeof workflowTool88.caseBuddyRequestAutomationStop === 'function') {
    try {
      workflowTool88.caseBuddyRequestAutomationStop('Case Buddy is unloading.');
    } catch (error) {
      console.warn('Could not stop hidden workflow Tool 88 before unload.', error);
    }
  }
  this.clearCaseToastTimer();
  this.clearCaseInvestigationVisualizerClipboardMessage?.();
  this.clearCaseManualStartViewportAutomationCallouts?.();
  this.clearCaseManualStartHopInspectorProgressQueue?.({ discard: true });
  this.disposeCaseInvestigationVisualizerCameraRuntime?.();
  if (this.caseManualStartViewportAutomationCalloutFrame && typeof window !== 'undefined') {
    if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(this.caseManualStartViewportAutomationCalloutFrame);
    else if (typeof window.clearTimeout === 'function') window.clearTimeout(this.caseManualStartViewportAutomationCalloutFrame);
    this.caseManualStartViewportAutomationCalloutFrame = 0;
  } else {
    this.caseManualStartViewportAutomationCalloutFrame = 0;
  }
  this.clearCaseManualStartViewportWorkIndicator?.({ immediate: true });
  this.clearCaseAutomationTimer?.();
  this.clearCaseWorkflowScheduledScrollTimers?.();
  this.clearCaseWorkflowStatefulGeneratedReviewAutoGuardTimer?.();
  this.clearCaseWorkflowStatefulGeneratedReviewAutoResumeTimer?.();
  this.clearCaseWorkflowWwwUsdReferenceAutoGuardTimer?.();
  this.clearCaseWorkflowWwwUsdReferenceAutoResumeTimer?.();
  this.clearCaseWorkflowRawDataTool88PortalCameraFrame?.();
  this.clearCaseWorkflowRawDataTool88AutoContinueTimer?.();
  this.clearSpreadsheetCopyTimers?.();
  this.stopWayFinderHandoffPolling?.();
  if (this.caseFlowVisualsPanFrame) {
    if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(this.caseFlowVisualsPanFrame);
    if (typeof window.clearTimeout === 'function') window.clearTimeout(this.caseFlowVisualsPanFrame);
    this.caseFlowVisualsPanFrame = 0;
  }
  if (this.caseInvestigationVisualizerPanFrame) {
    if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(this.caseInvestigationVisualizerPanFrame);
    if (typeof window.clearTimeout === 'function') window.clearTimeout(this.caseInvestigationVisualizerPanFrame);
    this.caseInvestigationVisualizerPanFrame = 0;
  }
  this.finishCaseInvestigationVisualizerAdvancedResize?.({ silent: true });
  this.finishCaseManualSolveBottomPanelResize?.({ silent: true });
  this.finishCaseInvestigationVisualizerSidePanelResize?.({ silent: true });
  if (typeof this.setCaseInvestigationVisualizerPageScrollLock === 'function') {
    this.setCaseInvestigationVisualizerPageScrollLock(false, { force: true });
  } else if (typeof document !== 'undefined' && document.body) {
    document.body.style.overflow = '';
  }

  if (this.emptyCaseStarterNudgeHighlightTimer) {
    window.clearTimeout(this.emptyCaseStarterNudgeHighlightTimer);
    this.emptyCaseStarterNudgeHighlightTimer = null;
  }
  if (window.hermesSheetBridge?.__owner === this) {
    delete window.hermesSheetBridge;
  }
},
  methods: {
    ...caseBuddyBaseMethods,
    ...caseBuddyDateNetworkRawMethods,
    ...caseBuddyWorkflowMethods,
    ...caseBuddyAutomationMethods,
    ...caseBuddyReviewToolMethods,
    ...caseBuddyDeliverableMethods,
    ...caseBuddyHopSolverMethods,
    ...caseBuddyAddendumMethods,
    ...caseBuddyAgentBridgeMethods,
    ...caseBuddyProjectSheetMethods,

    callCaseBuddyOriginalMethod(methodGroup, methodName, args = []) {
      const fn = methodGroup && methodGroup[methodName];
      if (typeof fn !== 'function') return undefined;
      return fn.apply(this, args);
    },

    callFirstCaseBuddyOriginalMethod(methodName, args = []) {
      const groups = [
        caseBuddyHopSolverMethods,
        caseBuddyDeliverableMethods,
        caseBuddyReviewToolMethods,
        caseBuddyWorkflowMethods,
        caseBuddyAutomationMethods,
        caseBuddyDateNetworkRawMethods,
        caseBuddyBaseMethods,
        caseBuddyProjectSheetMethods,
        caseBuddyAddendumMethods,
        caseBuddyAgentBridgeMethods
      ];
      for (const group of groups) {
        const result = this.callCaseBuddyOriginalMethod(group, methodName, args);
        if (result !== undefined) return result;
      }
      return undefined;
    },

    caseSupportPersistenceLooksLikeQuotaFailure(error = null, notice = this.caseProjectStorageNotice) {
      const errorName = normalizeCell(error?.name || notice?.errorName || '').toLowerCase();
      const message = normalizeCell(error?.message || notice?.message || '').toLowerCase();
      return Boolean(
        notice?.quota
          || errorName.includes('quota')
          || message.includes('quota')
          || message.includes('storage is full')
          || message.includes('exceeded the quota')
      );
    },

    caseSupportStorageKeyForCurrentFile() {
      const noticeKey = normalizeCell(this.caseProjectStorageNotice?.storageKey || '');
      if (noticeKey && /_case_support_v\d+$/iu.test(noticeKey)) return noticeKey;
      const inputKey = normalizeCell(this.currentProjectInputStorageKey || '');
      return inputKey ? `${inputKey}_case_support_v1` : '';
    },

    caseSupportStorageCompactValue(value, path = [], depth = 0, aggressive = false) {
      if (value === null || value === undefined) return value ?? null;
      if (typeof value === 'boolean' || typeof value === 'number') return value;

      const pathParts = Array.isArray(path) ? path : [path];
      const pathText = pathParts.join('.').toLowerCase();
      const key = String(pathParts[pathParts.length - 1] || '').toLowerCase();
      const stringLimit = aggressive
        ? Math.max(900, Math.floor(CASE_BUDDY_SUPPORT_COMPACT_STRING_LIMIT / 3))
        : CASE_BUDDY_SUPPORT_COMPACT_STRING_LIMIT;

      if (typeof value === 'string') {
        const heavyText = /(?:debug|payload|response|preview|html|markdown|plain.?text|tsv|csv|trace|inspector|raw|audit|log|details|message|note|narrative|report)/iu.test(pathText);
        const limit = heavyText ? stringLimit : stringLimit * 3;
        return value.length > limit
          ? `${value.slice(0, limit)}
[Rebuildable detail trimmed because browser storage was nearly full.]`
          : value;
      }

      const maxDepth = aggressive ? 10 : 16;
      if (depth >= maxDepth) return '[Nested rebuildable detail omitted]';

      if (Array.isArray(value)) {
        const scalarOnly = value.every(item => item === null || ['string', 'number', 'boolean'].includes(typeof item));
        const semanticScalarList = scalarOnly && /(?:ids?|keys?|labels?|paths?|hashes?|addresses?|selected|included|excluded|reviewed|accepted|dismissed|completed)/iu.test(key);
        const auditLike = /(?:audit|log|history|events?|snapshots?|attempts?|examples?|findings?|warnings?|candidates?|transactions?|entries|rows|preview|payload|trace|provider)/iu.test(pathText);
        let limit = semanticScalarList
          ? CASE_BUDDY_SUPPORT_COMPACT_SCALAR_COLLECTION_LIMIT
          : (auditLike ? CASE_BUDDY_SUPPORT_COMPACT_AUDIT_LIMIT : CASE_BUDDY_SUPPORT_COMPACT_COLLECTION_LIMIT);
        if (aggressive) limit = Math.max(4, Math.floor(limit / 3));
        return value.slice(0, limit).map((item, index) => (
          this.caseSupportStorageCompactValue(item, [...pathParts, String(index)], depth + 1, aggressive)
        ));
      }

      if (typeof value === 'object') {
        const result = {};
        Object.entries(value).forEach(([childKey, childValue]) => {
          const childPath = [...pathParts, childKey];
          const childKeyLower = childKey.toLowerCase();
          const rebuildableObjectBlob = /^(?:rawresponse|responsejson|responsebody|rawpayload|debugsnapshot|fifoinspector|providerrows|tracehistory|richpreview|htmlpreview|documenthtml|workbookpreview)$/iu.test(childKeyLower);
          if (rebuildableObjectBlob && childValue && typeof childValue === 'object' && !Array.isArray(childValue)) {
            result[childKey] = {
              compacted: true,
              keyCount: Object.keys(childValue).length
            };
            return;
          }
          result[childKey] = this.caseSupportStorageCompactValue(childValue, childPath, depth + 1, aggressive);
        });
        return result;
      }

      return normalizeCell(value);
    },

    buildCompactCaseSupportStateForStorage(state = this.caseSupportState, options = {}) {
      const source = state && typeof state === 'object' ? state : createDefaultCaseSupportState();
      const aggressive = options.aggressive === true;
      return {
        ...source,
        // Main/Raw spreadsheet rows are stored by the workbook, not here. These
        // three support branches contain organizer snapshots and generated detail
        // that can be reconstructed from the spreadsheet-backed case.
        workflowState: this.caseSupportStorageCompactValue(source.workflowState || {}, ['workflowState'], 0, aggressive),
        checkResultsById: this.caseSupportStorageCompactValue(source.checkResultsById || {}, ['checkResultsById'], 0, aggressive),
        deliverableStateById: this.caseSupportStorageCompactValue(source.deliverableStateById || {}, ['deliverableStateById'], 0, aggressive)
      };
    },

    caseSupportStorageJsonBytes(json = '') {
      const textValue = String(json || '');
      try {
        if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(textValue).length;
      } catch {
        // Fall through to a conservative UTF-16 estimate.
      }
      return textValue.length * 2;
    },

    caseSupportStorageSizeLabel(bytes = 0) {
      const value = Math.max(0, Number(bytes) || 0);
      if (value < 1024) return `${Math.round(value)} B`;
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
      return `${(value / (1024 * 1024)).toFixed(1)} MB`;
    },

    writeCompactCaseSupportStateToStorage(storageKey = '', options = {}) {
      const key = normalizeCell(storageKey || this.caseSupportStorageKeyForCurrentFile());
      if (!key || typeof localStorage === 'undefined') return false;

      const fullState = this.caseSupportState && typeof this.caseSupportState === 'object'
        ? this.caseSupportState
        : createDefaultCaseSupportState();
      let fullJson = '';
      let existingJson = '';
      try {
        fullJson = JSON.stringify(fullState);
        existingJson = String(localStorage.getItem(key) || '');
      } catch (error) {
        fullJson = '';
        existingJson = '';
      }

      const attempts = [
        { mode: 'compact', state: this.buildCompactCaseSupportStateForStorage(fullState) },
        { mode: 'minimal', state: this.buildCompactCaseSupportStateForStorage(fullState, { aggressive: true }) }
      ];
      let savedJson = '';
      let savedMode = '';
      let lastError = null;

      const tryWrite = (json) => {
        try {
          localStorage.setItem(key, json);
          return true;
        } catch (error) {
          lastError = error;
          return false;
        }
      };

      for (const attempt of attempts) {
        let compactJson = '';
        try {
          compactJson = JSON.stringify(attempt.state);
        } catch (error) {
          lastError = error;
          continue;
        }
        if (tryWrite(compactJson)) {
          savedJson = compactJson;
          savedMode = attempt.mode;
          break;
        }

        // Replacing an existing larger value should normally account for the net
        // size. This guarded retry handles browsers that reject before reclaiming
        // the old value. Restore the prior value if the smaller write still fails.
        if (
          this.caseSupportPersistenceLooksLikeQuotaFailure(lastError, this.caseProjectStorageNotice)
          && existingJson
          && compactJson.length < existingJson.length
        ) {
          try {
            localStorage.removeItem(key);
            if (tryWrite(compactJson)) {
              savedJson = compactJson;
              savedMode = attempt.mode;
              break;
            }
            try { localStorage.setItem(key, existingJson); } catch { /* Keep the visible storage warning below. */ }
          } catch (error) {
            lastError = error;
          }
        }
      }

      if (!savedJson) {
        const previousNotice = this.caseProjectStorageNotice || {};
        this.caseProjectStorageNotice = {
          ...previousNotice,
          visible: true,
          quota: true,
          title: 'Browser storage is full',
          message: `${lastError?.message || previousNotice.message || 'Case Buddy could not save the current case support state.'} Spreadsheet-backed case data remains available in this tab, but the newest workflow/review status may not survive a reload until storage is freed.`,
          storageKey: key,
          errorName: lastError?.name || previousNotice.errorName || '',
          attemptedBytes: this.caseSupportStorageJsonBytes(fullJson),
          attemptedSizeLabel: this.caseSupportStorageSizeLabel(this.caseSupportStorageJsonBytes(fullJson)),
          updatedAtUtc: new Date().toISOString()
        };
        if (options.toast !== false) this.showCaseToast?.('Browser storage is full. The report can continue in this tab, but free storage before reloading.');
        return false;
      }

      const currentFileId = normalizeCell(this.currentProjectFileId || '');
      const wasAlreadyCompact = Boolean(
        this.caseSupportCompactPersistenceActive
          && this.caseSupportCompactPersistenceFileId === currentFileId
      );
      this.caseSupportCompactPersistenceActive = true;
      this.caseSupportCompactPersistenceFileId = currentFileId;
      this.caseSupportCompactPersistenceStorageKey = key;
      this.caseSupportCompactPersistenceLastSavedBytes = this.caseSupportStorageJsonBytes(savedJson);
      this.caseSupportCompactPersistenceMode = savedMode;
      // Keep the full support state in memory for this session while treating its
      // compact on-disk representation as current.
      this.caseSupportStateLastPersistedJson = fullJson;

      const previousNotice = this.caseProjectStorageNotice || {};
      this.caseProjectStorageNotice = {
        ...previousNotice,
        visible: true,
        quota: false,
        title: 'Browser storage is nearly full',
        message: 'Case rows and current workflow status were saved. Case Buddy compacted rebuildable check, audit, debug, and preview history to preserve storage headroom. Delete older saved cases when convenient.',
        storageKey: key,
        errorName: '',
        attemptedBytes: this.caseSupportCompactPersistenceLastSavedBytes,
        attemptedSizeLabel: this.caseSupportStorageSizeLabel(this.caseSupportCompactPersistenceLastSavedBytes),
        updatedAtUtc: new Date().toISOString()
      };
      if (!wasAlreadyCompact && options.toast !== false) {
        this.showCaseToast?.('Case saved with compact workflow history because browser storage is nearly full.');
      }
      return true;
    },

    persistCaseSupportState(...args) {
      const currentFileId = normalizeCell(this.currentProjectFileId || '');
      if (this.caseSupportCompactPersistenceFileId && this.caseSupportCompactPersistenceFileId !== currentFileId) {
        this.caseSupportCompactPersistenceActive = false;
        this.caseSupportCompactPersistenceFileId = '';
        this.caseSupportCompactPersistenceStorageKey = '';
        this.caseSupportCompactPersistenceLastSavedBytes = 0;
        this.caseSupportCompactPersistenceMode = '';
      }

      if (
        this.caseSupportCompactPersistenceActive
        && this.caseSupportCompactPersistenceFileId === currentFileId
        && this.caseSupportCompactPersistenceStorageKey
      ) {
        if (this.caseSupportPersistTimer) {
          const clear = typeof window !== 'undefined' && typeof window.clearTimeout === 'function'
            ? window.clearTimeout.bind(window)
            : clearTimeout;
          clear(this.caseSupportPersistTimer);
          this.caseSupportPersistTimer = null;
        }
        return this.writeCompactCaseSupportStateToStorage(this.caseSupportCompactPersistenceStorageKey, { toast: false });
      }

      const beforeNoticeStamp = normalizeCell(this.caseProjectStorageNotice?.updatedAtUtc || '');
      const originalPersist = [
        caseBuddyProjectSheetMethods,
        caseBuddyBaseMethods,
        caseBuddyWorkflowMethods,
        caseBuddyDeliverableMethods,
        caseBuddyAutomationMethods
      ].map(group => group?.persistCaseSupportState).find(method => typeof method === 'function');
      if (typeof originalPersist !== 'function') return undefined;

      let result;
      let caughtError = null;
      try {
        result = originalPersist.apply(this, args);
      } catch (error) {
        caughtError = error;
      }

      const notice = this.caseProjectStorageNotice || {};
      const noticeChanged = normalizeCell(notice.updatedAtUtc || '') !== beforeNoticeStamp;
      const quotaFailure = this.caseSupportPersistenceLooksLikeQuotaFailure(caughtError, notice)
        && Boolean(caughtError || result === false || noticeChanged);
      if (!quotaFailure) {
        if (caughtError) throw caughtError;
        return result;
      }

      const compactSaved = this.writeCompactCaseSupportStateToStorage(
        notice.storageKey || this.caseSupportStorageKeyForCurrentFile(),
        { toast: true }
      );
      // A quota exception should not falsely turn a valid in-memory visual/report
      // review into an investigator judgement pause when compact persistence worked.
      if (compactSaved) return true;
      if (caughtError && !this.caseSupportPersistenceLooksLikeQuotaFailure(caughtError, notice)) throw caughtError;
      // Support metadata is an organizer layer, not the spreadsheet-backed case.
      // Keep final visual/report automation moving in the current tab while the
      // visible quota warning makes the durability limitation explicit.
      return true;
    },

    persistCaseBuddyPresentationMode(mode = this.caseBuddyPresentationMode) {
      const normalized = String(mode || '').trim().toLowerCase();
      if (!CASE_BUDDY_PRESENTATION_MODES.has(normalized)) return false;
      // The legacy guided view remains available for reference, but it should
      // never become the browser's remembered primary mode.
      const persistedMode = normalized === CASE_BUDDY_PRESENTATION_MODE_SIMPLE
        ? CASE_BUDDY_PRESENTATION_MODE_LOOKUP
        : normalized;
      if (typeof localStorage === 'undefined') return true;
      try {
        localStorage.setItem(CASE_BUDDY_PRESENTATION_MODE_STORAGE_KEY, persistedMode);
        return true;
      } catch {
        return false;
      }
    },

    setCaseBuddyPresentationMode(mode = CASE_BUDDY_PRESENTATION_MODE_LOOKUP, options = {}) {
      const normalized = String(mode || '').trim().toLowerCase();
      const nextMode = CASE_BUDDY_PRESENTATION_MODES.has(normalized) ? normalized : CASE_BUDDY_PRESENTATION_MODE_DEFAULT;
      if (this.caseQuickLookupBusy && options.force !== true && nextMode !== this.caseBuddyPresentationMode) return false;
      if (nextMode !== CASE_BUDDY_PRESENTATION_MODE_LOOKUP) this.clearCaseQuickLookupEnsureCaseTimer?.();
      this.caseBuddyPresentationMode = nextMode;
      if (options.persist !== false) this.persistCaseBuddyPresentationMode(nextMode);
      if (nextMode === CASE_BUDDY_PRESENTATION_MODE_SIMPLE) {
        this.caseSimpleLaunchDismissed = false;
        this.caseSimpleLaunchMinimized = false;
        this.caseSimpleFileChooserVisible = false;
      } else if (nextMode === CASE_BUDDY_PRESENTATION_MODE_WORKSPACE) {
        this.caseSimpleLaunchDismissed = false;
        this.caseSimpleLaunchMinimized = true;
      } else {
        this.caseSimpleLaunchDismissed = false;
        this.caseSimpleLaunchMinimized = true;
        this.caseSimpleFileChooserVisible = false;
        this.caseSimpleDetailsVisible = false;
        this.queueCaseQuickLookupEnsureCase?.(100);
        this.$nextTick(() => this.focusCaseQuickLookupInput?.());
      }
      return true;
    },

    openCaseQuickLookupFromSimpleLaunch() {
      return this.setCaseBuddyPresentationMode(CASE_BUDDY_PRESENTATION_MODE_LOOKUP);
    },

    openCaseQuickLookupFromWorkspace() {
      return this.setCaseBuddyPresentationMode(CASE_BUDDY_PRESENTATION_MODE_LOOKUP);
    },

    openCaseSimpleLaunchFromQuickLookup() {
      return this.openLegacyCaseSimpleLaunch();
    },

    openCaseWorkspaceFromQuickLookup() {
      return this.setCaseBuddyPresentationMode(CASE_BUDDY_PRESENTATION_MODE_WORKSPACE);
    },

    openLegacyCaseSimpleLaunch(...args) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyBaseMethods, 'restoreCaseSimpleLaunch', args);
      this.setCaseBuddyPresentationMode(CASE_BUDDY_PRESENTATION_MODE_SIMPLE, { force: true, persist: false });
      return result;
    },

    dismissCaseSimpleLaunch(...args) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyBaseMethods, 'dismissCaseSimpleLaunch', args);
      this.setCaseBuddyPresentationMode(CASE_BUDDY_PRESENTATION_MODE_WORKSPACE, { force: true });
      return result;
    },

    minimizeCaseSimpleLaunch(...args) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyBaseMethods, 'minimizeCaseSimpleLaunch', args);
      this.setCaseBuddyPresentationMode(CASE_BUDDY_PRESENTATION_MODE_WORKSPACE, { force: true });
      return result;
    },

    restoreCaseSimpleLaunch(...args) {
      return this.openLegacyCaseSimpleLaunch(...args);
    },

    caseQuickLookupSetState(patch = {}, options = {}) {
      if (options.runId !== undefined && Number(options.runId) !== Number(this.caseQuickLookup?.runId)) return false;
      this.caseQuickLookup = { ...(this.caseQuickLookup || createCaseQuickLookupState()), ...(patch || {}) };
      return true;
    },

    resetCaseQuickLookup(options = {}) {
      if (this.caseQuickLookup?.manualSolveLaunched || this.showCaseInvestigationVisualizerModal) return false;
      this.cancelCaseQuickLookupRun({ silent: true, reset: false });
      this.caseQuickLookupReportRecoveryInFlight = false;
      this.caseQuickLookupReportRecoveryAttempts = 0;
      const nextRunId = Number(this.caseQuickLookup?.runId || 0) + 1;
      this.caseQuickLookup = createCaseQuickLookupState({ runId: nextRunId });
      this.caseQuickLookupChoiceFilter = '';
      if (options.preserveHashDraft !== true) this.caseQuickLookupHashDraft = '';
      this.$nextTick(() => this.focusCaseQuickLookupInput?.());
      return true;
    },

    resetCaseQuickLookupForCaseChange(options = {}) {
      if (this.caseQuickLookup?.manualSolveLaunched) this.caseQuickLookupSetState({ manualSolveLaunched: false });
      this.cancelCaseQuickLookupRun({ silent: true, reset: false });
      this.caseQuickLookupReportRecoveryInFlight = false;
      this.caseQuickLookupReportRecoveryAttempts = 0;
      const nextRunId = Number(this.caseQuickLookup?.runId || 0) + 1;
      this.caseQuickLookup = createCaseQuickLookupState({ runId: nextRunId });
      this.caseQuickLookupChoiceFilter = '';
      if (options.preserveHashDraft !== true) this.caseQuickLookupHashDraft = '';
      return true;
    },

    focusCaseQuickLookupInput() {
      if (!this.caseQuickLookupVisible || !this.caseQuickLookupShowsInput) return false;
      const input = this.$refs.caseQuickLookupInput;
      input?.focus?.({ preventScroll: true });
      return Boolean(input);
    },

    setCaseQuickLookupScrollLock(active = false, options = {}) {
      if (typeof document === 'undefined' || !document.body) return false;
      if (active) {
        if (this.caseQuickLookupBodyOverflow === null) this.caseQuickLookupBodyOverflow = document.body.style.overflow || '';
        document.body.style.overflow = 'hidden';
        return true;
      }
      if (this.caseQuickLookupBodyOverflow !== null || options.force === true) {
        document.body.style.overflow = this.caseQuickLookupBodyOverflow || '';
        this.caseQuickLookupBodyOverflow = null;
      }
      return true;
    },

    clearCaseQuickLookupEnsureCaseTimer() {
      if (this.caseQuickLookupEnsureCaseTimer && typeof window !== 'undefined') window.clearTimeout(this.caseQuickLookupEnsureCaseTimer);
      this.caseQuickLookupEnsureCaseTimer = null;
    },

    queueCaseQuickLookupEnsureCase(delayMs = 0) {
      this.clearCaseQuickLookupEnsureCaseTimer();
      const run = () => {
        this.caseQuickLookupEnsureCaseTimer = null;
        if (!this.caseQuickLookupEnabled || this.caseBuddyPresentationMode !== CASE_BUDDY_PRESENTATION_MODE_LOOKUP) return;
        this.ensureCaseQuickLookupCase?.({ silent: true }).catch((error) => {
          if (!this.caseQuickLookupVisible) return;
          this.caseQuickLookupSetState({
            status: 'error',
            progress: 0,
            error: error?.message || 'A case could not be created automatically.',
            message: error?.message || 'A case could not be created automatically.'
          });
        });
      };
      if (typeof window !== 'undefined') this.caseQuickLookupEnsureCaseTimer = window.setTimeout(run, Math.max(0, Number(delayMs) || 0));
      else run();
    },

    async ensureCaseQuickLookupCase(options = {}) {
      if (this.currentProjectFileId) return true;
      if (options.force !== true && (!this.caseQuickLookupEnabled || this.caseBuddyPresentationMode !== CASE_BUDDY_PRESENTATION_MODE_LOOKUP)) return false;
      // Do not create a new blank case while the platform is still restoring a
      // previously selected file. The hydration watcher retries after that
      // decision is complete.
      if (this.casePageHydrating) return false;
      if (this.caseQuickLookupEnsureCasePromise) return this.caseQuickLookupEnsureCasePromise;
      const previousStatus = this.caseQuickLookup?.status || 'idle';
      if (this.caseQuickLookupVisible && previousStatus === 'idle') {
        this.caseQuickLookupSetState({
          status: 'creating-case', progress: 8,
          message: 'Creating a blank case for this lookup…',
          detail: 'The transaction will be stored in the new case’s Main Data.'
        });
      }
      // Set this before invoking createBlankProjectFile. Some platform file
      // implementations assign the new id synchronously before returning their
      // promise; the case-id watcher must still recognize that transition as the
      // lookup screen's own blank-case creation rather than an investigator case switch.
      this.caseQuickLookupAutoCreatingCase = true;
      this.caseQuickLookupEnsureCasePromise = (async () => {
        await this.createBlankProjectFile();
        this.caseSimpleDetailsVisible = false;
        this.showCaseDetailsEditor = false;
        const startedAt = Date.now();
        while (!this.currentProjectFileId && Date.now() - startedAt < CASE_BUDDY_QUICK_LOOKUP_CASE_WAIT_MS) {
          await this.caseManualSleep?.(80);
        }
        if (!this.currentProjectFileId) throw new Error('Case Buddy could not create a case file for this lookup. Open Case Management and choose or create a case.');
        const inputReadyStartedAt = Date.now();
        while (!this.projectFileReadyState?.input && Date.now() - inputReadyStartedAt < 4000) {
          await this.caseManualSleep?.(80);
        }
        if (this.caseQuickLookup?.status === 'creating-case') {
          this.caseQuickLookupSetState({ status: 'idle', progress: 0, message: '', detail: '', error: '' });
        }
        return true;
      })();
      try {
        return await this.caseQuickLookupEnsureCasePromise;
      } finally {
        this.caseQuickLookupEnsureCasePromise = null;
        this.caseQuickLookupAutoCreatingCase = false;
      }
    },

    normalizeCaseQuickLookupHash(value = '') {
      return this.normalizeStartingMainTransactionHash?.(value) || String(value || '').trim();
    },

    validateCaseQuickLookupHash(value = '') {
      const hash = this.normalizeCaseQuickLookupHash(value);
      if (!hash) return { valid: false, hash: '', message: 'Paste a full transaction hash to continue.' };
      if (/\.\.\./u.test(hash)) return { valid: false, hash, message: 'Paste the full transaction hash rather than a shortened hash.' };
      const isHexHash = /^0x[0-9a-f]{64}$/iu.test(hash) || /^[0-9a-f]{64}$/iu.test(hash);
      const isLongBase58Signature = /^[1-9A-HJ-NP-Za-km-z]{80,100}$/u.test(hash);
      if (!isHexHash && !isLongBase58Signature) {
        return { valid: false, hash, message: 'That does not look like a complete transaction hash. Check for missing characters, spaces, or explorer text.' };
      }
      return { valid: true, hash, message: '' };
    },

    caseQuickLookupExplicitNetwork(candidate = {}) {
      const row = Array.isArray(candidate?.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate?.row) ? candidate.row : []);
      const chainId = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.CHAIN_ID] || '');
      const chainName = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.CHAIN_NAME] || '');
      return {
        chainId,
        chainName,
        found: Boolean(chainId || chainName)
      };
    },

    caseQuickLookupCandidateWithNetwork(candidate = {}, network = {}) {
      if (!candidate || typeof candidate !== 'object') return candidate;
      const stored = this.caseQuickLookupExplicitNetwork(candidate);
      const chainId = this.caseManualNormalizeText(
        network?.chainId
          || network?.caseChainId
          || stored.chainId
          || ''
      );
      const chainName = this.caseManualNormalizeText(
        network?.chainName
          || network?.caseChainName
          || stored.chainName
          || ''
      );
      if (!chainId && !chainName) return candidate;

      const sourceRow = Array.isArray(candidate.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate.row) ? candidate.row : []);
      const row = this.caseManualEnsureRowWidth(
        sourceRow.slice(),
        Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceRow.length || 0)
      );
      if (chainId) row[CASE_MANUAL_START_COL.CHAIN_ID] = chainId;
      if (chainName) row[CASE_MANUAL_START_COL.CHAIN_NAME] = chainName;

      return {
        ...candidate,
        chainId,
        chainName,
        rawRow: row,
        row
      };
    },

    async waitForCaseQuickLookupNetworkCandidate(hash = '', options = {}) {
      const timeoutMs = Math.max(250, Number(options.timeoutMs) || CASE_BUDDY_QUICK_LOOKUP_ROW_WAIT_MS);
      const runId = options.runId;
      const signal = options.signal || null;
      const startedAt = Date.now();
      while (Date.now() - startedAt <= timeoutMs) {
        if (signal?.aborted) return null;
        if (runId !== undefined && Number(this.caseQuickLookup?.runId) !== Number(runId)) return null;
        const candidate = this.findCaseQuickLookupRootCandidate(hash);
        const network = this.caseQuickLookupExplicitNetwork(candidate);
        if (candidate && network.found) return this.caseQuickLookupCandidateWithNetwork(candidate, network);
        await this.$nextTick();
        await this.caseManualSleep?.(80);
      }
      return null;
    },

    findCaseQuickLookupRootCandidate(hash = '') {
      const target = this.normalizeCaseQuickLookupHash(hash).toLowerCase();
      if (!target) return null;
      const rows = this.getCaseManualStartVisualizerRows?.({ includeAll: true }) || [];
      return rows.find(candidate => {
        const candidateHash = this.caseManualNormalizeText(candidate?.hash || candidate?.transactionHash || candidate?.txHash).toLowerCase();
        return candidateHash === target && this.caseManualStartCandidateIsInitialFlowTransaction?.(candidate);
      }) || null;
    },

    caseQuickLookupFindNetworkLookupRow(hash = '', candidate = null, sourceRows = this.networkLookupRows) {
      const target = this.normalizeCaseQuickLookupHash(hash).toLowerCase();
      if (!target) return null;
      const rows = Array.isArray(sourceRows) ? sourceRows : [];
      const matches = rows.filter((row) => {
        const rowHash = this.normalizeCaseQuickLookupHash(row?.hash || '').toLowerCase();
        return rowHash === target;
      });
      const candidateRowIndex = Number(candidate?.rowIndex);
      if (Number.isInteger(candidateRowIndex) && candidateRowIndex >= 0) {
        // The row added by Transaction Lookup is authoritative. Do not let an
        // older occurrence of the same transaction hash take its place.
        return matches.find(row => Number(row?.rowIndex) === candidateRowIndex) || null;
      }
      return matches[0] || null;
    },

    caseQuickLookupBuildNetworkLookupRow(hash = '', candidate = null) {
      const activeCandidate = candidate || this.findCaseQuickLookupRootCandidate(hash);
      if (!activeCandidate) return null;
      const rowIndex = Number(activeCandidate.rowIndex);
      if (!Number.isInteger(rowIndex) || rowIndex < 0) return null;

      const candidateRow = Array.isArray(activeCandidate.rawRow)
        ? activeCandidate.rawRow
        : (Array.isArray(activeCandidate.row) ? activeCandidate.row : []);
      const matrixEntryRows = this.caseManualEntryRowsFromMainMatrix?.(rowIndex) || [];
      const entryRows = matrixEntryRows.length
        ? matrixEntryRows
        : [this.caseManualEnsureRowWidth(
            candidateRow.slice(),
            Math.max(CASE_MANUAL_START_COLUMN_COUNT, candidateRow.length || 0)
          )];
      const firstEntryValue = (columnIndex) => {
        for (const row of entryRows) {
          const value = this.caseManualNormalizeText(row?.[columnIndex]);
          if (value) return value;
        }
        return '';
      };

      const transactionHash = firstEntryValue(CASE_MANUAL_START_COL.TX_HASH)
        || this.caseManualNormalizeText(activeCandidate.hash || activeCandidate.transactionHash || activeCandidate.txHash)
        || this.normalizeCaseQuickLookupHash(hash);
      if (!transactionHash) return null;

      const chainId = firstEntryValue(CASE_MANUAL_START_COL.CHAIN_ID);
      const chainName = firstEntryValue(CASE_MANUAL_START_COL.CHAIN_NAME);
      const needsNetwork = Boolean(transactionHash && (!chainId || !chainName));
      return {
        id: `network-quick-lookup-${rowIndex}-${transactionHash.toLowerCase()}`,
        rowIndex,
        spreadsheetRowNumber: rowIndex + 1,
        entryRowIndexes: [...entryRows.keys()].map(offset => rowIndex + offset),
        pathLabel: firstEntryValue(CASE_MANUAL_START_COL.PATH_LABEL),
        hash: transactionHash,
        toAddressHint: firstEntryValue(CASE_MANUAL_START_COL.TO_ADDRESS),
        fromAddressHint: firstEntryValue(CASE_MANUAL_START_COL.FROM_ADDRESS),
        chainId,
        chainName,
        currentNetworkLabel: chainName && chainId
          ? `${chainName} (${chainId})`
          : chainName || chainId || 'Missing',
        needsNetwork,
        include: needsNetwork,
        status: needsNetwork ? 'pending' : 'complete',
        statusLabel: needsNetwork ? 'Needs lookup' : 'Already filled',
        message: needsNetwork ? '' : 'Column T/U already present.',
        source: '',
        tried: [],
        lookupResult: null,
        quickLookupDirectRoot: true
      };
    },

    caseQuickLookupEnsureNetworkLookupRow(hash = '', candidate = null) {
      const existing = this.caseQuickLookupFindNetworkLookupRow(hash, candidate);
      if (existing) return existing;
      const targetRow = this.caseQuickLookupBuildNetworkLookupRow(hash, candidate);
      if (!targetRow) return null;
      this.networkLookupRows = [
        ...(Array.isArray(this.networkLookupRows) ? this.networkLookupRows : []),
        targetRow
      ];
      return targetRow;
    },

    async waitForCaseQuickLookupRootCandidate(hash = '', options = {}) {
      const timeoutMs = Math.max(250, Number(options.timeoutMs) || CASE_BUDDY_QUICK_LOOKUP_ROW_WAIT_MS);
      const runId = options.runId;
      const signal = options.signal || null;
      const startedAt = Date.now();
      while (Date.now() - startedAt <= timeoutMs) {
        if (signal?.aborted) return null;
        if (runId !== undefined && Number(this.caseQuickLookup?.runId) !== Number(runId)) return null;
        const candidate = this.findCaseQuickLookupRootCandidate(hash);
        if (candidate) return candidate;
        await this.$nextTick();
        await this.caseManualSleep?.(80);
      }
      return null;
    },

    async addCaseQuickLookupHashToMain(hash = '', runId = 0, signal = null) {
      let candidate = this.findCaseQuickLookupRootCandidate(hash);
      if (candidate) return { candidate, added: false };
      this.caseQuickLookupSetState({
        status: 'adding', progress: 24,
        message: 'Adding the transaction to Main Data…',
        detail: 'The same starting-transaction loader used elsewhere in Case Buddy is handling this row.'
      }, { runId });
      this.startingMainTransactionsPaste = hash;
      this.parseStartingMainTransactionPaste?.();
      const ready = (this.startingMainTransactionDraftRows || []).some(row => ['ready', 'different-to'].includes(row?.status));
      if (!ready) throw new Error('The transaction could not be staged in Main Data. It may already exist in the case with conflicting starting details.');
      await this.loadStartingMainTransactionsIntoMainSheet();
      this.showStartingMainTransactionsModal = false;
      candidate = await this.waitForCaseQuickLookupRootCandidate(hash, { runId, signal });
      if (!candidate) throw new Error('The transaction was loaded, but Case Buddy could not find its new Main Data entry. Open the case workspace to review the row.');
      return { candidate, added: true };
    },

    async resolveCaseQuickLookupNetwork(hash = '', runId = 0, signal = null, preferredCandidate = null) {
      const preferredHash = this.caseManualNormalizeText(
        preferredCandidate?.hash || preferredCandidate?.transactionHash || preferredCandidate?.txHash || ''
      );
      let candidate = preferredCandidate
        && this.caseManualTransactionHashesMatch?.(preferredHash, hash)
        ? preferredCandidate
        : this.findCaseQuickLookupRootCandidate(hash);
      const storedNetwork = this.caseQuickLookupExplicitNetwork(candidate);
      // buildCaseManualStartCandidate() keeps a legacy Ethereum fallback for
      // rows whose Columns T/U are still blank. Transaction Lookup must not
      // treat that derived fallback as a discovered network, especially since
      // BTC, Tron, and EVM transaction hashes can all be 64 hexadecimal chars.
      if (candidate && storedNetwork.found) {
        return this.caseQuickLookupCandidateWithNetwork(candidate, storedNetwork);
      }

      this.caseQuickLookupSetState({
        status: 'network', progress: 38,
        message: 'Identifying the blockchain network…',
        detail: 'Case Buddy is using its existing network lookup before it asks the Flow Solver to read the transaction.'
      }, { runId });
      const previousFillDatePreference = this.networkLookupFillDateIfMissing;
      this.networkLookupFillDateIfMissing = true;
      try {
        await this.refreshNetworkLookupRows?.();
        candidate = candidate || this.findCaseQuickLookupRootCandidate(hash);

        // In an established path-labelled case, the shared intake analyser
        // intentionally lists labelled roots. A newly appended root has no path
        // label yet, so it can be absent from that list even though Transaction
        // Lookup has already added it to Main Data. Add only this exact root to
        // the temporary lookup batch rather than treating an older labelled root
        // as the submitted transaction.
        let targetRow = this.caseQuickLookupEnsureNetworkLookupRow(hash, candidate);
        if (!targetRow) {
          throw new Error('The transaction was added to Main Data, but Case Buddy could not stage that exact root for network discovery. Open the advanced workspace to review the new row.');
        }
        const targetRowId = targetRow.id;
        this.networkLookupRows = (this.networkLookupRows || []).map(row => ({
          ...row,
          include: Boolean(row?.id === targetRowId && row?.needsNetwork && row?.hash)
        }));
        targetRow = (this.networkLookupRows || []).find(row => row?.id === targetRowId) || targetRow;

        let resolvedNetwork = { chainId: '', chainName: '' };
        let lookupFailureMessage = '';
        if (targetRow?.needsNetwork) {
          await this.callCaseBuddyOriginalMethod(caseBuddyDateNetworkRawMethods, 'runNetworkDiscoveryLookup', [{
            signal,
            source: 'case-quick-lookup',
            silent: true
          }]);
          const completedRow = (this.networkLookupRows || []).find(item => item?.id === targetRowId)
            || this.caseQuickLookupFindNetworkLookupRow(hash, candidate);
          resolvedNetwork = {
            chainId: this.caseManualNormalizeText(completedRow?.chainId || completedRow?.lookupResult?.caseChainId || ''),
            chainName: this.caseManualNormalizeText(completedRow?.chainName || completedRow?.lookupResult?.caseChainName || '')
          };
          lookupFailureMessage = this.caseManualNormalizeText(completedRow?.message || '');
          await this.refreshNetworkLookupRows?.();
        } else {
          resolvedNetwork = {
            chainId: this.caseManualNormalizeText(targetRow.chainId || targetRow?.lookupResult?.caseChainId || ''),
            chainName: this.caseManualNormalizeText(targetRow.chainName || targetRow?.lookupResult?.caseChainName || '')
          };
        }

        candidate = await this.waitForCaseQuickLookupNetworkCandidate(hash, { runId, signal })
          || this.caseQuickLookupCandidateWithNetwork(
            this.findCaseQuickLookupRootCandidate(hash) || candidate,
            resolvedNetwork
          );
        const explicitNetwork = this.caseQuickLookupExplicitNetwork(candidate);
        if (!candidate || !explicitNetwork.found) {
          throw new Error(lookupFailureMessage || 'Case Buddy could not identify the transaction network automatically. Open the advanced workspace to add the network manually.');
        }
        return this.caseQuickLookupCandidateWithNetwork(candidate, explicitNetwork);
      } finally {
        // The lookup screen temporarily asks the existing network tool to fill a
        // missing date too, but it should not silently change the investigator's
        // saved preference for later advanced-workspace runs.
        this.networkLookupFillDateIfMissing = previousFillDatePreference;
      }
    },

    async hydrateCaseQuickLookupRoot(hash = '', runId = 0, signal = null, resolvedCandidate = null) {
      const currentCandidate = this.findCaseQuickLookupRootCandidate(hash);
      const preferredCandidate = resolvedCandidate
        && this.caseManualTransactionHashesMatch?.(
          resolvedCandidate.hash || resolvedCandidate.transactionHash || resolvedCandidate.txHash || '',
          hash
        )
        ? resolvedCandidate
        : null;
      const resolvedNetwork = this.caseQuickLookupExplicitNetwork(preferredCandidate || currentCandidate);
      let candidate = this.caseQuickLookupCandidateWithNetwork(preferredCandidate || currentCandidate, resolvedNetwork);
      if (!candidate) throw new Error('The starting transaction is no longer available in Main Data.');
      this.caseQuickLookupSetState({
        status: 'hydrating', progress: 52,
        message: 'Reading the transaction and checking the transfer it represents…',
        detail: 'Processing transaction... please wait...'
      }, { runId });
      const hydration = await this.hydrateCaseManualStartRows([candidate], {
        initialOnly: true,
        lookupAttributions: false,
        providerFieldsOnly: true,
        remoteWorkOnly: true,
        skipReusable: true,
        // Transaction Lookup has its own direction-aware BTC input/output
        // chooser immediately below. The generic startup probe is intentionally
        // forward-oriented and must not preselect an output during Reverse mode.
        probeInitialBtcChoices: false,
        maxRows: 1,
        signal
      });
      if (hydration?.inferredCount > 0) await this.writeCaseManualStartHydrationToMain(hydration.rows);
      await this.$nextTick();
      candidate = this.caseQuickLookupCandidateWithNetwork(
        this.findCaseQuickLookupRootCandidate(hash) || hydration?.rows?.[0] || candidate,
        resolvedNetwork
      );
      if (!this.caseManualStartCandidateNeedsOutputChoice?.(candidate)) return { candidate, choiceRequired: false };

      const direction = this.caseManualStartOutputChoiceDirectionForCandidate(candidate);
      const result = await this.fetchCaseManualStartOutputChoiceRows(candidate, direction, { signal });
      const rows = Array.isArray(result?.rows) ? result.rows : [];
      if (rows.length === 1) {
        this.caseManualStartOutputChoiceCandidate = candidate;
        this.caseManualStartOutputChoiceRows = rows;
        this.caseManualStartOutputChoiceSelectedKey = rows[0].key;
        const applied = await this.applyCaseManualStartOutputChoice({ skipVisualRefresh: true, silent: true });
        if (!applied) throw new Error(this.caseManualStartOutputChoiceStatus || 'The clear transfer choice could not be applied.');
        this.closeCaseManualStartOutputChoice?.();
        candidate = this.findCaseQuickLookupRootCandidate(hash) || candidate;
        return { candidate, choiceRequired: false, autoAppliedChoice: true };
      }
      if (!rows.length) {
        throw new Error('The transaction needs a target address, but no selectable transfer or Bitcoin output was returned. Open the advanced workspace to enter the missing address manually.');
      }
      this.caseQuickLookupChoiceFilter = '';
      this.caseQuickLookupSetState({
        status: 'choice', progress: 64,
        message: `${rows.length} possible ${result.family === 'btc' ? (direction === 'reverse' ? 'Bitcoin inputs' : 'Bitcoin outputs') : 'transfers'} were found. Choose the one whose funds you want Case Buddy to follow.`,
        detail: '',
        choiceRows: rows,
        selectedChoiceKey: '',
        choiceSourceLabel: result.sourceLabel || 'Flow Solver',
        choiceFamily: result.family || this.caseManualInferNetworkFamily(candidate),
        choiceDirection: direction,
        rowIndex: Number.isInteger(candidate.rowIndex) ? candidate.rowIndex : -1
      }, { runId });
      await this.$nextTick();
      if (rows.length > 8) this.focusCaseQuickLookupChoiceFilter();
      return { candidate, choiceRequired: true };
    },

    focusCaseQuickLookupChoiceFilter() {
      if (this.caseQuickLookup?.status !== 'choice' || !this.caseQuickLookupChoiceFilterVisible) return false;
      const input = this.$refs.caseQuickLookupChoiceFilterInput;
      input?.focus?.({ preventScroll: true });
      return Boolean(input);
    },

    clearCaseQuickLookupChoiceFilter() {
      this.caseQuickLookupChoiceFilter = '';
      this.$nextTick(() => this.focusCaseQuickLookupChoiceFilter());
      return true;
    },

    handleCaseQuickLookupChoiceFilterEscape() {
      if (this.caseQuickLookupChoiceFilterActive) return this.clearCaseQuickLookupChoiceFilter();
      this.$refs.caseQuickLookupChoiceFilterInput?.blur?.();
      return true;
    },

    caseQuickLookupChoiceSearchParts(value) {
      const text = String(value ?? '').normalize('NFKC').toLowerCase().trim();
      if (!text) return { text: '', compact: '' };
      return { text: text.replace(/\s+/gu, ' '), compact: text.replace(/[\s,_]/gu, '') };
    },

    caseQuickLookupChoiceSearchHaystack(choice = {}) {
      const raw = choice?.raw && typeof choice.raw === 'object' ? choice.raw : {};
      const values = [
        choice.key, choice.title, choice.kind, choice.type, choice.family, choice.order, choice.detail,
        choice.inputIndex, choice.outputIndex, choice.previousOutputIndex, choice.previousTransactionHash,
        choice.amountText, choice.amount, choice.amountNumber, choice.valueSatoshis, choice.token, choice.tokenSymbol,
        choice.address, choice.fromAddress, choice.toAddress, choice.outputAddress, choice.recipient,
        raw.address, raw.from_address, raw.fromAddress, raw.to_address, raw.toAddress,
        raw.scriptpubkey_address, raw.scriptPubKeyAddress, raw.amount, raw.amountText, raw.value,
        raw.value_btc, raw.amount_btc, raw.btc_value, raw.value_satoshis, raw.value_sats,
        raw.satoshis, raw.amount_satoshis, raw.index, raw.n, raw.vin, raw.vout, raw.prev_index, raw.prevIndex,
        raw.txid, raw.tx_id, raw.tx_hash, raw.transaction_hash, raw.hash,
        raw.prev_txid, raw.prevTxid, raw.prev_hash, raw.prevHash,
        raw.prevout?.address, raw.prevout?.scriptpubkey_address, raw.prevout?.txid, raw.prevout?.hash, raw.prevout?.value
      ];
      const text = values.filter(value => value !== null && value !== undefined && value !== '').join(' ');
      return this.caseQuickLookupChoiceSearchParts(text);
    },

    caseQuickLookupChoiceMatchesFilter(choice = {}, filterValue = this.caseQuickLookupChoiceFilter) {
      const query = this.caseQuickLookupChoiceSearchParts(filterValue);
      if (!query.text) return true;
      const terms = query.text.split(' ').filter(Boolean);
      const haystack = this.caseQuickLookupChoiceSearchHaystack(choice);
      return terms.every(term => {
        const normalizedTerm = this.caseQuickLookupChoiceSearchParts(term);
        return haystack.text.includes(normalizedTerm.text)
          || (normalizedTerm.compact && haystack.compact.includes(normalizedTerm.compact));
      });
    },

    selectCaseQuickLookupChoice(choice = {}) {
      if (!choice?.key || this.caseQuickLookupBusy) return false;
      this.caseQuickLookupSetState({ selectedChoiceKey: choice.key });
      return true;
    },

    caseQuickLookupChoiceKindLabel(choice = {}) {
      const family = this.caseManualNormalizeText(choice.family).toLowerCase();
      const raw = this.caseManualNormalizeText(
        family === 'btc'
          ? (choice.title || choice.kind || choice.type || 'Bitcoin transfer')
          : (choice.kind || choice.type || choice.title || choice.family || 'transfer')
      );
      return raw.replace(/[_-]+/gu, ' ').replace(/\b\w/gu, letter => letter.toUpperCase());
    },

    caseQuickLookupChoiceAmountLabel(choice = {}) {
      const amount = this.caseManualNormalizeText(choice.amountText || choice.amount || '');
      const token = this.caseManualNormalizeText(choice.token || choice.tokenSymbol || '');
      if (!amount) return token || 'Amount unavailable';
      if (!token || amount.toUpperCase().includes(token.toUpperCase())) return amount;
      return `${amount} ${token}`;
    },

    caseQuickLookupChoiceAddressValue(choice = {}) {
      const direction = String(this.caseQuickLookup?.choiceDirection || 'forward').toLowerCase();
      const address = direction === 'reverse'
        ? (choice.fromAddress || choice.address || '')
        : (choice.toAddress || choice.address || choice.outputAddress || choice.recipient || '');
      return this.caseManualNormalizeText(address);
    },

    caseQuickLookupChoiceAddressLabel(choice = {}) {
      const normalized = this.caseQuickLookupChoiceAddressValue(choice);
      return normalized ? `Address ${this.caseManualShorten(normalized, 14, 12)}` : 'Address unavailable';
    },

    async applyCaseQuickLookupChoice() {
      const choice = this.caseQuickLookupSelectedChoice;
      const runId = Number(this.caseQuickLookup?.runId) || 0;
      if (!choice) return false;
      const candidate = this.findCaseQuickLookupRootCandidate(this.caseQuickLookup.activeHash);
      if (!candidate) {
        this.caseQuickLookupSetState({ status: 'error', error: 'The transaction row is no longer available in Main Data.' });
        return false;
      }
      this.caseQuickLookupSetState({
        status: 'applying-choice', progress: 70,
        message: 'Saving the selected transfer to Main Data and Raw Data…',
        detail: 'The selected address becomes the starting target for this graph.'
      }, { runId });
      this.caseManualStartOutputChoiceCandidate = candidate;
      this.caseManualStartOutputChoiceRows = this.caseQuickLookupChoiceRows;
      this.caseManualStartOutputChoiceSelectedKey = choice.key;
      try {
        const applied = await this.applyCaseManualStartOutputChoice({ skipVisualRefresh: true, silent: true });
        if (!applied) throw new Error(this.caseManualStartOutputChoiceStatus || 'The selected transfer could not be applied.');
        this.closeCaseManualStartOutputChoice?.();
        await this.openCaseQuickLookupFlowSolver({ runId });
        return true;
      } catch (error) {
        this.caseQuickLookupSetState({
          status: 'error', progress: 0,
          error: error?.message || 'The selected transfer could not be applied.',
          message: error?.message || 'The selected transfer could not be applied.'
        }, { runId });
        return false;
      }
    },

    async openCaseQuickLookupFlowSolver(options = {}) {
      const runId = Number(options.runId ?? this.caseQuickLookup?.runId) || 0;
      const hash = this.caseQuickLookup?.activeHash || this.normalizeCaseQuickLookupHash(this.caseQuickLookupHashDraft);
      if (!hash) return false;
      this.caseQuickLookupSetState({
        status: 'opening', progress: 78,
        message: 'Opening the Flow Solver and selecting this transaction’s graph…',
        detail: 'Auto will begin as soon as the visual workspace is ready.'
      }, { runId });
      const opened = await this.openCaseManualStartVisualizer({ source: 'quick-lookup', skipStartupHydration: true });
      if (!opened) throw new Error('The Flow Solver did not open. You can retry the lookup or use the advanced workspace.');
      await this.$nextTick();
      await this.caseManualSleep?.(180);
      const flowIndex = this.caseManualStartAllFlowsFindFlowIndex({ mainHash: hash });
      if (flowIndex < 0) {
        this.cancelCaseInvestigationVisualizer?.();
        throw new Error('The Flow Solver opened, but the submitted transaction graph was not available. Open the case workspace to review Main Data.');
      }
      this.renderCaseInvestigationVisualizerAtIndex(flowIndex, { mode: 'single' });
      const flow = (this.caseInvestigationVisualizerFlows || [])[flowIndex] || this.caseInvestigationVisualizerCurrentFlow || {};
      this.caseQuickLookupSetState({
        status: 'running', progress: 88,
        message: 'The Flow Solver is following this transaction.',
        detail: 'You can watch the graph, pause Auto, or inspect any unusual hop manually.',
        flowIdentity: this.caseManualStartAllFlowsFlowIdentity?.(flow, Math.max(0, flowIndex)) || '',
        manualSolveLaunched: true
      }, { runId });
      await this.$nextTick();
      await this.caseManualSleep?.(160);
      if (this.caseManualStartViewportAutoFlowIsComplete?.()) {
        this.refreshCaseQuickLookupManualSolveResult({ source: 'already-complete' });
        this.showCaseManualStartViewportCompletionEffect?.('This transaction graph is already complete. Close Flow Solver when you are ready to continue.');
        return true;
      }
      let started = false;
      for (let attempt = 0; attempt < 12 && !started; attempt += 1) {
        if (this.caseManualStartViewportAutoCanStart) started = this.startCaseManualStartViewportAuto({ silent: false });
        if (!started) await this.caseManualSleep?.(120);
      }
      if (!started) {
        this.caseInvestigationVisualizerStatus = 'The transaction is open. Use Auto, Next, or the manual inspector to continue.';
      }
      return true;
    },

    assessCaseQuickLookupActiveFlow(hash = this.caseQuickLookup?.activeHash) {
      const target = this.normalizeCaseQuickLookupHash(hash).toLowerCase();
      if (!target) return null;
      let state = this.caseInvestigationVisualizerState;
      let flows = Array.isArray(state?.flows) ? state.flows : [];
      let index = this.caseManualStartAllFlowsFindFlowIndex({ mainHash: target }, flows);
      if (index < 0) {
        const rows = this.getCaseManualStartVisualizerRows?.({ includeAll: true }) || [];
        state = this.buildCaseManualStartVisualizerState?.(rows) || null;
        flows = Array.isArray(state?.flows) ? state.flows : [];
        index = this.caseManualStartAllFlowsFindFlowIndex({ mainHash: target }, flows);
      }
      if (index < 0) return null;
      const flow = flows[index];
      return { flow, index, assessment: this.caseManualStartAllFlowsAssessFlow?.(flow) || null };
    },

    refreshCaseQuickLookupManualSolveResult(options = {}) {
      if (!this.caseQuickLookup?.activeHash) return null;
      const result = this.assessCaseQuickLookupActiveFlow(this.caseQuickLookup.activeHash);
      if (!result?.assessment) return null;
      const assessment = result.assessment;
      const reportBoundaryEligibleCount = Array.isArray(assessment.manualReview)
        ? assessment.manualReview.filter((candidate) => {
            const currentRow = this.findCurrentCaseManualStartMainRowForCandidate?.(candidate) || [];
            return this.caseManualStartCandidateCanBeAcceptedAsReportBoundary?.(candidate, currentRow) === true;
          }).length
        : 0;
      const assessmentCounts = {
        unresolvedCount: Number(assessment.unresolvedCount || 0),
        choiceCount: Number(assessment.choiceCount || 0),
        manualReviewCount: Number(assessment.manualReviewCount || 0),
        acceptedReportBoundaryCount: Number(assessment.acceptedReportBoundaryCount || 0),
        reportBoundaryEligibleCount
      };
      if (!assessment.needsWork) {
        const acceptedCount = assessmentCounts.acceptedReportBoundaryCount;
        // Accepting a boundary changes Main Data and therefore invalidates any
        // package built before that acknowledgement. Always return to the
        // Create full report step for that mutation so the disclosure is built
        // into the new report before it can be opened as ready.
        const reportReady = Boolean(
          this.caseWorkflowAssistantOverallWorkflowComplete
            && options.source !== 'accepted-report-boundaries'
        );
        this.caseQuickLookupSetState({
          status: reportReady ? 'report-ready' : 'complete',
          progress: 100,
          lastResult: acceptedCount ? 'qualified-complete' : 'complete',
          ...assessmentCounts,
          message: reportReady
            ? (acceptedCount
              ? `The report is ready and discloses ${acceptedCount} accepted incomplete path ending${acceptedCount === 1 ? '' : 's'}.`
              : 'The transaction flow and final report package are ready.')
            : (acceptedCount
              ? `${acceptedCount} incomplete path ending${acceptedCount === 1 ? ' is' : 's are'} accepted as ${acceptedCount === 1 ? 'a report boundary' : 'report boundaries'}. The report will state that further manual tracing is recommended.`
              : 'Every path ending in this transaction graph now has a meaningful story. Close the Flow Solver to build a report or analyze another transaction.'),
          detail: '',
          error: ''
        });
      } else {
        this.caseQuickLookupSetState({
          status: 'partial',
          progress: 92,
          lastResult: 'attention',
          ...assessmentCounts,
          message: assessment.reason || 'This transaction graph still has work that needs review.',
          detail: options.source === 'flow-solver-closed'
            ? (assessmentCounts.manualReviewCount > 0 && !assessmentCounts.unresolvedCount && !assessmentCounts.choiceCount
              ? 'Continue tracing, or use the current endings as clearly disclosed report boundaries when an immediate partial report is appropriate.'
              : 'Open the Flow Solver again to continue with Auto, Next, or a manual hop review.')
            : 'Auto stopped safely. You can review the open graph or continue later.',
          error: ''
        });
      }
      return result;
    },

    closeCompletedCaseQuickLookupFlowSolver() {
      if (!this.caseQuickLookup?.manualSolveLaunched || !this.showCaseInvestigationVisualizerModal) return false;
      this.refreshCaseQuickLookupManualSolveResult?.({ source: 'lookup-completion-return' });
      this.clearCaseManualStartViewportCompletionEffect?.();
      this.cancelCaseInvestigationVisualizer?.();
      return true;
    },

    async resumeCaseQuickLookupAnalysis() {
      if (!this.caseQuickLookup?.activeHash || this.caseQuickLookupBusy) return false;
      try {
        await this.openCaseQuickLookupFlowSolver({ runId: this.caseQuickLookup.runId });
        return true;
      } catch (error) {
        this.caseQuickLookupSetState({ status: 'error', progress: 0, error: error?.message || 'The Flow Solver could not reopen.' });
        return false;
      }
    },

    cancelCaseQuickLookupRun(options = {}) {
      try { this.caseQuickLookupAbortController?.abort?.(); } catch { /* best effort */ }
      this.caseQuickLookupAbortController = null;
      if (options.reset !== false && !this.caseQuickLookup?.manualSolveLaunched) {
        const nextRunId = Number(this.caseQuickLookup?.runId || 0) + 1;
        this.caseQuickLookup = createCaseQuickLookupState({ runId: nextRunId });
        this.caseQuickLookupChoiceFilter = '';
      }
      if (!options.silent) this.showCaseToast?.('Transaction lookup cancelled. Any row already added remains in the case.');
      return true;
    },

    async submitCaseQuickLookup() {
      if (!this.caseQuickLookupCanSubmit) return false;
      const validation = this.validateCaseQuickLookupHash(this.caseQuickLookupHashDraft);
      if (!validation.valid) {
        this.caseQuickLookupSetState({ status: 'error', progress: 0, error: validation.message, message: validation.message });
        this.$nextTick(() => this.focusCaseQuickLookupInput?.());
        return false;
      }
      this.cancelCaseQuickLookupRun({ silent: true, reset: false });
      this.caseQuickLookupChoiceFilter = '';
      const runId = Number(this.caseQuickLookup?.runId || 0) + 1;
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      this.caseQuickLookupAbortController = controller;
      this.caseQuickLookup = createCaseQuickLookupState({
        runId,
        status: 'validating',
        progress: 12,
        activeHash: validation.hash,
        message: 'Checking the hash and preparing the case…',
        detail: 'No blockchain provider request has been made yet.'
      });
      try {
        await this.ensureCaseQuickLookupCase();
        if (controller?.signal?.aborted) return false;
        const added = await this.addCaseQuickLookupHashToMain(validation.hash, runId, controller?.signal || null);
        if (controller?.signal?.aborted || Number(this.caseQuickLookup?.runId) !== runId) return false;
        this.caseQuickLookupSetState({ addedByCurrentRun: added.added, rowIndex: added.candidate?.rowIndex ?? -1 }, { runId });
        const networkCandidate = await this.resolveCaseQuickLookupNetwork(
          validation.hash,
          runId,
          controller?.signal || null,
          added.candidate
        );
        if (controller?.signal?.aborted || Number(this.caseQuickLookup?.runId) !== runId) return false;
        this.caseQuickLookupSetState({ rowIndex: networkCandidate?.rowIndex ?? this.caseQuickLookup.rowIndex }, { runId });
        const hydration = await this.hydrateCaseQuickLookupRoot(validation.hash, runId, controller?.signal || null, networkCandidate);
        if (controller?.signal?.aborted || Number(this.caseQuickLookup?.runId) !== runId) return false;
        if (hydration?.choiceRequired) return true;
        await this.openCaseQuickLookupFlowSolver({ runId });
        return true;
      } catch (error) {
        if (controller?.signal?.aborted || error?.name === 'AbortError') return false;
        this.caseQuickLookupSetState({
          status: 'error', progress: 0,
          error: error?.message || 'The transaction lookup could not be completed.',
          message: error?.message || 'The transaction lookup could not be completed.',
          detail: ''
        }, { runId });
        return false;
      } finally {
        if (Number(this.caseQuickLookup?.runId) === runId) this.caseQuickLookupAbortController = null;
      }
    },

    clearCaseQuickLookupReportWatchTimer() {
      if (this.caseQuickLookupReportWatchTimer && typeof window !== 'undefined') window.clearTimeout(this.caseQuickLookupReportWatchTimer);
      this.caseQuickLookupReportWatchTimer = null;
    },

    caseQuickLookupReportPauseStepTag() {
      const pause = this.caseAutomation?.manualPause || {};
      const candidates = [pause.stepTag, pause.tag, pause.workflowStepTag];
      for (const candidate of candidates) {
        const tag = normalizeCell(candidate).toUpperCase();
        if (tag === 'CASE_FLOW_VISUALS' || tag === 'CASE_COMPLETION_SUMMARY') return tag;
      }
      return '';
    },

    caseQuickLookupReportPauseCanRecover() {
      return Boolean(
        this.caseQuickLookup?.reportRequested
          && this.caseAutomation?.skipResolvableReview
          && (this.caseAutomation?.paused || this.showCaseAutomationPauseModal)
          && !this.caseQuickLookupReportRecoveryInFlight
          && this.caseQuickLookupReportRecoveryAttempts < CASE_BUDDY_QUICK_LOOKUP_REPORT_RECOVERY_MAX_ATTEMPTS
          && this.caseQuickLookupReportPauseStepTag()
      );
    },

    caseQuickLookupFinalReportIsLiveReady() {
      const state = this.readCaseWorkflowFullReportPackageStateForAudit?.() || {};
      if (!state || state.isReady !== true) return false;
      if (state.reportBodyReviewIsCurrent === false || state.addendumReviewIsCurrent === false) return false;
      const visualsCanBuild = Boolean(state.visualsCanBuild === true || this.caseFlowVisualsStateView?.canBuild === true);
      const visualsCurrent = Boolean(state.visualsReviewIsCurrent === true || this.caseFlowVisualsStateView?.reviewIsCurrent === true);
      return !visualsCanBuild || visualsCurrent;
    },

    async recoverCaseQuickLookupResolvableReportPause(options = {}) {
      if (!this.caseQuickLookupReportPauseCanRecover()) {
        return { handled: false, success: false, reason: 'quick-lookup-report-pause-not-recoverable' };
      }
      const startTag = this.caseQuickLookupReportPauseStepTag();
      this.caseQuickLookupReportRecoveryInFlight = true;
      this.caseQuickLookupReportRecoveryAttempts += 1;
      this.clearCaseAutomationTimer?.();
      this.clearCaseWorkflowStatefulGeneratedReviewAutoGuardTimer?.();
      this.clearCaseWorkflowStatefulGeneratedReviewAutoResumeTimer?.();
      this.clearCaseWorkflowWwwUsdReferenceAutoGuardTimer?.();
      this.clearCaseWorkflowWwwUsdReferenceAutoResumeTimer?.();
      this.showCaseAutomationPauseModal = false;
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        active: false,
        busy: false,
        paused: false,
        manualPause: null,
        skipResolvableReview: true,
        status: startTag === 'CASE_FLOW_VISUALS' ? 'Creating case flow visuals…' : 'Finishing the report package…',
        detail: 'Transaction Lookup is automatically completing this resolvable final review.'
      };
      this.caseQuickLookupSetState({
        status: 'report',
        progress: startTag === 'CASE_FLOW_VISUALS' ? 94 : 98,
        message: startTag === 'CASE_FLOW_VISUALS'
          ? 'Case Buddy is creating and reviewing the case flow visuals…'
          : 'Case Buddy is finishing and reviewing the one-click report package…',
        detail: 'No action is needed. This is the same final review used by the full workspace.'
      });

      try {
        const result = await this.runCaseWorkflowFinalRubberStampTail(startTag, {
          source: normalizeCell(options.source || 'case-quick-lookup-report-pause-recovery'),
          methodName: 'recoverCaseQuickLookupResolvableReportPause',
          force: true,
          reason: 'Transaction Lookup is automatically creating, reviewing, and reconciling the final visuals and report package.'
        });
        await this.caseManualSleep?.(320);
        await this.$nextTick();
        if (this.caseWorkflowAssistantOverallWorkflowComplete || this.caseQuickLookupFinalReportIsLiveReady() || this.caseQuickLookup?.status === 'report-ready') {
          this.caseQuickLookupReportRecoveryAttempts = 0;
          this.caseQuickLookupSetState({
            status: 'report-ready',
            progress: 100,
            reportRequested: false,
            lastResult: 'report-ready',
            message: 'The case workflow and one-click report package are ready to review.',
            detail: ''
          });
          this.clearCaseQuickLookupReportWatchTimer?.();
          return { handled: true, success: true, result };
        }
        if (result?.success && this.caseQuickLookup?.reportRequested) {
          this.scheduleCaseQuickLookupReportStateReview?.(700);
        }
        return { handled: true, success: Boolean(result?.success), result };
      } catch (error) {
        console.warn('Case Buddy could not automatically finish the Transaction Lookup report tail.', error);
        return { handled: true, success: false, error, reason: 'quick-lookup-report-recovery-error' };
      } finally {
        this.caseQuickLookupReportRecoveryInFlight = false;
      }
    },

    scheduleCaseQuickLookupReportStateReview(delayMs = 1200) {
      this.clearCaseQuickLookupReportWatchTimer();
      const run = async () => {
        this.caseQuickLookupReportWatchTimer = null;
        if (!this.caseQuickLookup?.reportRequested) return;
        if (this.caseWorkflowAssistantOverallWorkflowComplete || this.caseQuickLookupFinalReportIsLiveReady()) {
          this.caseQuickLookupReportRecoveryAttempts = 0;
          this.caseQuickLookupSetState({
            status: 'report-ready', progress: 100, reportRequested: false, lastResult: 'report-ready',
            message: 'The case workflow and one-click report package are ready to review.', detail: ''
          });
          return;
        }
        if (this.caseAutomation?.active || this.caseAutomation?.busy || this.caseWorkflowBackgroundAutomationActive || this.caseQuickLookupReportRecoveryInFlight) return;
        if (this.caseQuickLookupReportPauseCanRecover()) {
          const recovery = await this.recoverCaseQuickLookupResolvableReportPause({ source: 'case-quick-lookup-report-state-review' });
          if (recovery?.success || this.caseQuickLookupReportRecoveryInFlight || !this.caseQuickLookup?.reportRequested) return;
          if (this.caseQuickLookupReportPauseCanRecover()) {
            this.scheduleCaseQuickLookupReportStateReview?.(700);
            return;
          }
        }
        const pause = this.caseAutomation?.manualPause;
        this.caseQuickLookupSetState({
          status: 'partial', progress: 94, reportRequested: false, lastResult: 'report-attention',
          message: pause?.reason || pause?.message || 'The report build stopped at a step that needs review.',
          detail: 'Open the advanced workspace to review that step, then continue Auto.'
        });
      };
      if (typeof window !== 'undefined') this.caseQuickLookupReportWatchTimer = window.setTimeout(() => { run().catch?.((error) => console.warn('Case Buddy report-state review failed.', error)); }, Math.max(0, Number(delayMs) || 0));
      else run().catch?.((error) => console.warn('Case Buddy report-state review failed.', error));
    },

    async createCaseQuickLookupFullReport() {
      if (!this.caseQuickLookupCanCreateReport || this.caseQuickLookupBusy) return false;
      this.caseQuickLookupReportRecoveryInFlight = false;
      this.caseQuickLookupReportRecoveryAttempts = 0;
      this.setCaseSkipResolvableReviewsEnabled?.(true);
      this.caseQuickLookupSetState({
        status: 'report', progress: 8, reportRequested: true,
        message: 'Case Buddy is checking the completed flow and building every report deliverable…',
        detail: 'Current Main Data is being reconciled with the workflow before Automated Mode continues.'
      });
      this.caseQuickLookupSetState({
        progress: 12,
        detail: 'Checking chronological root order, path-label families, and current report prerequisites.'
      });
      await this.reconcileCaseRootChronologyAndPathLabels({
        source: 'case-quick-lookup-full-report-preflight',
        sort: true,
        silent: true
      });
      await this.reconcileCaseWorkflowSpreadsheetBackedIntake?.({
        source: 'case-quick-lookup-full-report-preflight',
        settleMs: 30
      });
      const result = await this.startCaseSimpleLaunchAuto();
      if (this.caseWorkflowAssistantOverallWorkflowComplete) {
        this.caseQuickLookupSetState({ status: 'report-ready', progress: 100, reportRequested: false, lastResult: 'report-ready', message: 'The one-click report package is ready to review.', detail: '' });
      } else if (!this.caseAutomation?.active && !this.caseAutomation?.busy && !this.caseWorkflowBackgroundAutomationActive) {
        this.scheduleCaseQuickLookupReportStateReview?.(500);
      }
      return result;
    },

    async openCaseQuickLookupFinalReport() {
      return await this.openCaseWorkflowCompletionReportPackage({
        source: 'case-quick-lookup-final-report',
        ensureReady: true
      });
    },

    openCaseQuickLookupSettings() {
      this.caseQuickLookupCaseSwitcherOpen = false;
      this.caseManualStartSettingsDraft = { ...this.caseManualStartReadTool88Settings(), tool88NoteColumn: 'W' };
      this.caseQuickLookupSettingsOpen = true;
      this.$nextTick(() => this.syncCaseManualStartSettingsToEmbeddedTool88?.(this.caseManualStartSettingsDraft));
    },

    closeCaseQuickLookupSettings() {
      this.caseQuickLookupSettingsOpen = false;
    },

    resetCaseQuickLookupSettings() {
      this.caseManualStartSettingsDraft = {
        ...CASE_MANUAL_START_TOOL88_DEFAULT_SETTINGS,
        hopLogicType: 'FIFO',
        tool88NoteColumn: 'W'
      };
      this.caseManualStartSettingsStatus = 'Reset to the Transaction Lookup defaults. Select Save settings to apply.';
    },

    saveCaseQuickLookupSettings() {
      this.saveCaseManualStartSettings?.({ quiet: true });
      this.caseQuickLookupSettingsOpen = false;
      this.showCaseToast?.('Flow Solver settings saved.');
    },

    toggleCaseQuickLookupCaseSwitcher() {
      if (this.caseQuickLookupBusy) return false;
      this.caseQuickLookupSettingsOpen = false;
      this.caseQuickLookupCaseSwitcherOpen = !this.caseQuickLookupCaseSwitcherOpen;
      return this.caseQuickLookupCaseSwitcherOpen;
    },

    closeCaseQuickLookupCaseSwitcher() {
      this.caseQuickLookupCaseSwitcherOpen = false;
      return true;
    },

    async switchCaseQuickLookupProjectFile(fileId = '') {
      const nextFileId = this.caseManualNormalizeText?.(fileId) || String(fileId || '').trim();
      if (!nextFileId || this.caseQuickLookupBusy) return false;
      this.caseQuickLookupCaseSwitcherOpen = false;
      if (nextFileId === this.currentProjectFileId) return true;
      this.caseQuickLookupCaseSwitchBusy = true;
      try {
        this.selectedProjectFileId = nextFileId;
        await this.loadSelectedProjectFile();
        this.setCaseBuddyPresentationMode(CASE_BUDDY_PRESENTATION_MODE_LOOKUP, { force: true });
        await this.$nextTick();
        this.focusCaseQuickLookupInput?.();
        return this.currentProjectFileId === nextFileId;
      } catch (error) {
        this.caseQuickLookupSetState({
          status: 'error',
          progress: 0,
          error: error?.message || 'The selected case could not be opened.',
          message: error?.message || 'The selected case could not be opened.'
        });
        return false;
      } finally {
        this.caseQuickLookupCaseSwitchBusy = false;
      }
    },

    async createCaseQuickLookupNewCase() {
      if (this.caseQuickLookupBusy) return false;
      this.caseQuickLookupCaseSwitcherOpen = false;
      this.caseQuickLookupSettingsOpen = false;
      this.caseQuickLookupCaseSwitchBusy = true;
      try {
        await this.createBlankProjectFile();
        this.caseSimpleDetailsVisible = false;
        this.showCaseDetailsEditor = false;
        this.setCaseBuddyPresentationMode(CASE_BUDDY_PRESENTATION_MODE_LOOKUP, { force: true });
        await this.$nextTick();
        this.focusCaseQuickLookupInput?.();
        return Boolean(this.currentProjectFileId);
      } catch (error) {
        this.caseQuickLookupSetState({
          status: 'error',
          progress: 0,
          error: error?.message || 'A new case could not be created.',
          message: error?.message || 'A new case could not be created.'
        });
        return false;
      } finally {
        this.caseQuickLookupCaseSwitchBusy = false;
      }
    },

    async openCaseQuickLookupCaseManagement() {
      this.caseQuickLookupSettingsOpen = false;
      this.caseQuickLookupCaseSwitcherOpen = false;
      return await this.openCaseFileBrowserModal();
    },

    caseWorkflowAssistantScopedAutomationBoundaryActive() {
      return Boolean(this.caseAttributionWorkflowRun?.active || this.caseCleanupWorkflowRun?.active);
    },

    caseFullReportPackageCompletionSummarySection(section = {}) {
      const identity = normalizeCell([
        section?.id,
        section?.key,
        section?.kind,
        section?.title,
        section?.label,
        section?.heading
      ].join(' ')).toLowerCase();
      return /case[\s_-]*completion[\s_-]*summary/.test(identity);
    },

    caseFullReportPackageCompletionSummaryIdentity() {
      const rawCaseId = normalizeCell(this.caseDetails?.caseId || '');
      const caseId = /^(?:no case id set|no case id|not set|none|null|n\/?a)$/i.test(rawCaseId)
        ? ''
        : rawCaseId;
      const caseName = normalizeCell(this.caseDisplayName || this.caseDetails?.caseName || this.currentProjectFileName || 'Case Buddy') || 'Case Buddy';
      return { caseId, caseName };
    },

    caseFullReportPackageEscapeHtml(value = '') {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },

    buildCaseFullReportPackageCompletionSummaryBodyText(newline = '\n') {
      const identity = this.caseFullReportPackageCompletionSummaryIdentity();
      const lines = [];
      if (identity.caseId) lines.push(`Case: ${identity.caseId}`);
      lines.push(`Case name: ${identity.caseName}`);
      const boundaries = this.caseManualStartAcceptedReportBoundaryRows?.() || [];
      if (boundaries.length) {
        const visibleBoundaries = boundaries.slice(0, 12);
        const remainder = Math.max(0, boundaries.length - visibleBoundaries.length);
        lines.push('');
        lines.push(`Report limitation: ${boundaries.length} incomplete path ending${boundaries.length === 1 ? ' was' : 's were'} accepted by the investigator as ${boundaries.length === 1 ? 'a report boundary' : 'report boundaries'}. The analysis reflects the trace available up to ${boundaries.length === 1 ? 'this point' : 'these points'}, and further manual tracing is recommended.`);
        lines.push('Outstanding report boundaries:');
        visibleBoundaries.forEach((row) => {
          const direction = row.direction === 'reverse' ? 'Reverse' : 'Forward';
          const address = row.address || 'address unavailable';
          const attribution = row.attribution && row.attribution !== 'Unknown' ? ` (${row.attribution})` : '';
          lines.push(`- Path ${row.pathLabel} — ${direction} trace stopped at ${address}${attribution}.`);
        });
        if (remainder) lines.push(`- ${remainder} additional report boundar${remainder === 1 ? 'y' : 'ies'} recorded in Main Data.`);
      }
      return lines.join(newline);
    },

    buildCaseFullReportPackageCompletionSummaryBodyHtml() {
      const identity = this.caseFullReportPackageCompletionSummaryIdentity();
      const lines = [];
      if (identity.caseId) {
        lines.push(`<p><strong>Case:</strong> ${this.caseFullReportPackageEscapeHtml(identity.caseId)}</p>`);
      }
      lines.push(`<p><strong>Case name:</strong> ${this.caseFullReportPackageEscapeHtml(identity.caseName)}</p>`);
      const boundaries = this.caseManualStartAcceptedReportBoundaryRows?.() || [];
      if (boundaries.length) {
        const visibleBoundaries = boundaries.slice(0, 12);
        const remainder = Math.max(0, boundaries.length - visibleBoundaries.length);
        lines.push(`<p><strong>Report limitation:</strong> ${boundaries.length} incomplete path ending${boundaries.length === 1 ? ' was' : 's were'} accepted by the investigator as ${boundaries.length === 1 ? 'a report boundary' : 'report boundaries'}. The analysis reflects the trace available up to ${boundaries.length === 1 ? 'this point' : 'these points'}, and further manual tracing is recommended.</p>`);
        lines.push('<p><strong>Outstanding report boundaries:</strong></p><ul>');
        visibleBoundaries.forEach((row) => {
          const direction = row.direction === 'reverse' ? 'Reverse' : 'Forward';
          const address = row.address || 'address unavailable';
          const attribution = row.attribution && row.attribution !== 'Unknown' ? ` (${row.attribution})` : '';
          lines.push(`<li><strong>Path ${this.caseFullReportPackageEscapeHtml(row.pathLabel)}</strong> — ${direction} trace stopped at ${this.caseFullReportPackageEscapeHtml(address)}${this.caseFullReportPackageEscapeHtml(attribution)}.</li>`);
        });
        if (remainder) lines.push(`<li>${remainder} additional report boundar${remainder === 1 ? 'y' : 'ies'} recorded in Main Data.</li>`);
        lines.push('</ul>');
      }
      return lines.join('');
    },

    simplifyCaseFullReportPackageCompletionSummaryText(value = '') {
      const source = String(value || '');
      if (!source || !/case completion summary/i.test(source)) return source;

      const newline = source.includes('\r\n') ? '\r\n' : '\n';
      const lines = source.split(/\r?\n/);
      const headingIndex = lines.findIndex(line => /^\s*(?:#{1,6}\s*)?case completion summary\s*$/i.test(line || ''));
      if (headingIndex < 0) return source;

      let statusIndex = -1;
      for (let index = headingIndex + 1; index < Math.min(lines.length, headingIndex + 80); index += 1) {
        if (/^\s*full report package\b/i.test(lines[index] || '')) {
          statusIndex = index;
          break;
        }
      }

      if (statusIndex > headingIndex) {
        const replacement = [
          lines[headingIndex],
          '',
          ...this.buildCaseFullReportPackageCompletionSummaryBodyText(newline).split(newline)
        ];
        lines.splice(headingIndex, statusIndex - headingIndex + 1, ...replacement);
        return lines.join(newline).replace(/(?:\r?\n){3,}/g, `${newline}${newline}`);
      }

      // Fallback for an older or partially built package without the final status line.
      let output = source
        .replace(/^\s*Case:\s*(?:No case id set|No case id|Not set|None|Null|N\/?A)\s*(?:\r?\n)?/gim, '')
        .replace(/^\s*(?:Case data revision|Main entries|Raw entries|Destination addresses|Missing path labels):[^\r\n]*(?:\r?\n)?/gim, '')
        .replace(/^\s*Full report package is ready and has been marked reviewed\/copied for the current case data\.\s*(?:\r?\n)?/gim, '')
        .replace(/^\s*Reviewed package prerequisites:\s*(?:\r?\n)(?:\s*-\s*[^\r\n]*(?:\r?\n))*/gim, '')
        .replace(/^\s*Deliverable snapshot:\s*(?:\r?\n)(?:\s*-\s*[^\r\n]*(?:\r?\n))*/gim, '');
      output = output.replace(/(?:\r?\n){3,}/g, `${newline}${newline}`);

      // The package builder can occasionally arrive here after the old status
      // sentence has already been removed. Rebuild only the small completion-
      // summary identity/limitation block so accepted report boundaries are
      // still disclosed without touching the next report section.
      const outputLines = output.split(/\r?\n/);
      const outputHeadingIndex = outputLines.findIndex(line => /^\s*(?:#{1,6}\s*)?case completion summary\s*$/i.test(line || ''));
      if (outputHeadingIndex >= 0) {
        let replaceEnd = outputHeadingIndex + 1;
        const summaryLinePattern = /^\s*(?:Case(?:\s+name)?\s*:|Report limitation\s*:|Outstanding report boundaries\s*:|-\s+Path\s+|-\s+\d+\s+additional report boundar)/i;
        while (
          replaceEnd < outputLines.length
            && (!normalizeCell(outputLines[replaceEnd]) || summaryLinePattern.test(outputLines[replaceEnd] || ''))
        ) replaceEnd += 1;
        const bodyLines = this.buildCaseFullReportPackageCompletionSummaryBodyText(newline).split(newline);
        outputLines.splice(outputHeadingIndex + 1, replaceEnd - outputHeadingIndex - 1, '', ...bodyLines);
        output = outputLines.join(newline).replace(/(?:\r?\n){3,}/g, `${newline}${newline}`);
      }
      return output;
    },

    simplifyCaseFullReportPackageCompletionSummaryHtml(value = '') {
      const source = String(value || '');
      if (!source || !/case completion summary/i.test(source)) return source;

      // Prefer bounded string replacement so the rest of the report, including
      // embedded SVG visuals, remains byte-for-byte unchanged.
      const headingPattern = String.raw`<h[1-6]\b[^>]*>\s*Case Completion Summary\s*<\/h[1-6]>`;
      for (const tag of ['section', 'article']) {
        const sectionPattern = new RegExp(
          String.raw`(<${tag}\b[^>]*>)([\s\S]{0,3000}?)(` + headingPattern + String.raw`)([\s\S]{0,16000}?)(<\/${tag}>)`,
          'i'
        );
        let replaced = false;
        const output = source.replace(sectionPattern, (match, openTag, beforeHeading, headingMarkup, existingBody, closeTag) => {
          if (/<h[1-6]\b/i.test(existingBody || '')) return match;
          replaced = true;
          return `${openTag}${beforeHeading}${headingMarkup}${this.buildCaseFullReportPackageCompletionSummaryBodyHtml()}${closeTag}`;
        });
        if (replaced) return output;
      }

      const loosePattern = new RegExp(
        String.raw`(` + headingPattern + String.raw`)[\s\S]{0,16000}?(<p\b[^>]*>[\s\S]{0,2000}?Full report package[\s\S]{0,2000}?<\/p>)`,
        'i'
      );
      let looseReplaced = false;
      const looseOutput = source.replace(loosePattern, (match, headingMarkup) => {
        looseReplaced = true;
        return `${headingMarkup}${this.buildCaseFullReportPackageCompletionSummaryBodyHtml()}`;
      });
      if (looseReplaced) return looseOutput;

      if (typeof DOMParser !== 'undefined') {
        try {
          const isDocument = /<html[\s>]/i.test(source) || /<!doctype/i.test(source);
          const parser = new DOMParser();
          const documentValue = parser.parseFromString(
            isDocument ? source : `<div id="casebuddy-completion-summary-root">${source}</div>`,
            'text/html'
          );
          const root = isDocument ? documentValue.body : documentValue.getElementById('casebuddy-completion-summary-root');
          if (root) {
            const heading = Array.from(root.querySelectorAll('h1, h2, h3, h4, h5, h6, caption, strong'))
              .find(node => /^case completion summary$/i.test(normalizeCell(node.textContent || '')));
            if (heading) {
              const candidate = heading.closest('section, article, [data-report-section], [data-section], .report-section, .case-full-package-section');
              const candidateHeadings = candidate
                ? Array.from(candidate.querySelectorAll('h1, h2, h3, h4, h5, h6'))
                : [];
              const scope = candidate || heading.parentElement || root;

              if (candidate && candidateHeadings.length === 1) {
                const headingClone = heading.cloneNode(true);
                candidate.replaceChildren(headingClone);
                const body = documentValue.createElement('div');
                body.innerHTML = this.buildCaseFullReportPackageCompletionSummaryBodyHtml();
                while (body.firstChild) candidate.appendChild(body.firstChild);
              } else {
                const exactRemove = /^(?:Case data revision|Main entries|Raw entries|Destination addresses|Missing path labels):/i;
                const packageStatusRemove = /^Full report package is ready and has been marked reviewed\/copied for the current case data\.?$/i;
                Array.from(scope.querySelectorAll('p, li, dt, dd, tr')).forEach((node) => {
                  const text = normalizeCell(node.textContent || '');
                  if (exactRemove.test(text) || packageStatusRemove.test(text) || /^Case:\s*(?:No case id set|No case id|Not set|None|Null|N\/?A)$/i.test(text)) {
                    node.remove();
                  }
                });
                Array.from(scope.querySelectorAll('p, div, strong')).forEach((node) => {
                  const text = normalizeCell(node.textContent || '');
                  if (!/^(?:Reviewed package prerequisites|Deliverable snapshot):?$/i.test(text)) return;
                  const next = node.nextElementSibling;
                  node.remove();
                  if (next && /^(?:UL|OL)$/i.test(next.tagName || '')) next.remove();
                });
              }

              if (isDocument) {
                const doctype = source.match(/<!doctype[^>]*>/i)?.[0] || '<!DOCTYPE html>';
                return `${doctype}\n${documentValue.documentElement.outerHTML}`;
              }
              return root.innerHTML;
            }
          }
        } catch (error) {
          console.warn('Could not simplify the Case Completion Summary HTML.', error);
        }
      }

      return source;
    },

    simplifyCaseFullReportPackageCompletionSummarySection(section = {}) {
      if (!this.caseFullReportPackageCompletionSummarySection(section)) return section;
      const next = { ...section };
      let changed = false;
      const textKeys = ['plainText', 'previewText', 'text', 'bodyText', 'contentText'];
      const htmlKeys = ['html', 'htmlFragment', 'htmlContent', 'bodyHtml'];
      textKeys.forEach((key) => {
        if (typeof next[key] !== 'string') return;
        const hasHeading = /case completion summary/i.test(next[key]);
        const sectionNewline = next[key].includes('\r\n') ? '\r\n' : '\n';
        const body = this.buildCaseFullReportPackageCompletionSummaryBodyText(sectionNewline);
        const simplified = hasHeading ? `Case Completion Summary${sectionNewline}${sectionNewline}${body}` : body;
        if (simplified !== next[key]) {
          next[key] = simplified;
          changed = true;
        }
      });
      htmlKeys.forEach((key) => {
        if (typeof next[key] !== 'string') return;
        const hasHeading = /case completion summary/i.test(next[key]);
        const body = this.buildCaseFullReportPackageCompletionSummaryBodyHtml();
        const simplified = hasHeading ? `<h2>Case Completion Summary</h2>${body}` : body;
        if (simplified !== next[key]) {
          next[key] = simplified;
          changed = true;
        }
      });
      if (!changed && section.caseCompletionSummarySimplified) return section;
      next.caseCompletionSummarySimplified = true;
      return next;
    },

    caseFullReportPackageIntroBoilerplateKind(value = '') {
      const text = normalizeCell(value);
      if (!text) return '';
      if (/^Full report package is ready to preview,\s*copy,\s*or download\.$/iu.test(text)) return 'ready';
      if (/^Copying places rich HTML and a plain text fallback\s+on the clipboard when the browser allows it\.$/iu.test(text)) return 'clipboard';
      if (/^Full report package is ready to preview,\s*copy,\s*or download\.\s*Copying places rich HTML and a plain text fallback\s+on the clipboard when the browser allows it\.$/iu.test(text)) return 'combined';
      if (/^Full report package is ready and has been marked reviewed\/copied for the current case data\.$/iu.test(text)) return 'reviewed';
      return '';
    },

    removeCaseFullReportPackageIntroFromText(value = '') {
      const source = String(value || '');
      if (!source || !/Full report package is ready to preview,\s*copy,\s*or download\.|Copying places rich HTML and a plain text fallback|Full report package is ready and has been marked reviewed\/copied for the current case data/iu.test(source)) return source;
      const newline = source.includes('\r\n') ? '\r\n' : '\n';
      let output = source
        .replace(/Full report package is ready to preview,\s*copy,\s*or download\.\s*/giu, '')
        .replace(/Copying places rich HTML and a plain text fallback\s+on the clipboard when the browser allows it\.\s*/giu, '')
        .replace(/Full report package is ready and has been marked reviewed\/copied for the current case data\.\s*/giu, '');
      output = output
        .replace(/^(?:[ \t]*\r?\n)+/u, '')
        .replace(/(?:\r?\n[ \t]*){3,}/gu, `${newline}${newline}`)
        .replace(/[ \t]+(?=\r?\n)/gu, '');
      return output;
    },

    removeCaseFullReportPackageIntroFromHtml(value = '') {
      const source = String(value || '');
      if (!source || !/Full report package is ready to preview|Copying places rich HTML and a plain text fallback|Full report package is ready and has been marked reviewed\/copied for the current case data/iu.test(source)) return source;

      if (typeof DOMParser !== 'undefined') {
        try {
          const isDocument = /<html[\s>]/iu.test(source) || /<!doctype/iu.test(source);
          const parser = new DOMParser();
          const documentValue = parser.parseFromString(
            isDocument ? source : `<div id="casebuddy-package-intro-sanitize-root">${source}</div>`,
            'text/html'
          );
          const root = isDocument
            ? documentValue.body
            : documentValue.getElementById('casebuddy-package-intro-sanitize-root');
          if (root) {
            let changed = false;
            const candidates = [root, ...Array.from(root.querySelectorAll('section, article, header, aside, div, p, small'))];
            const matches = candidates.filter(node => this.caseFullReportPackageIntroBoilerplateKind(node.textContent || ''));
            matches
              .filter(node => !matches.some(parent => parent !== node && parent.contains(node)))
              .forEach((node) => {
                if (node === root) {
                  node.replaceChildren();
                } else {
                  node.remove();
                }
                changed = true;
              });

            if (!changed && typeof documentValue.createTreeWalker === 'function' && typeof NodeFilter !== 'undefined') {
              const walker = documentValue.createTreeWalker(root, NodeFilter.SHOW_TEXT);
              let textNode = walker.nextNode();
              while (textNode) {
                const cleaned = this.removeCaseFullReportPackageIntroFromText(textNode.nodeValue || '');
                if (cleaned !== textNode.nodeValue) {
                  textNode.nodeValue = cleaned;
                  changed = true;
                }
                textNode = walker.nextNode();
              }
            }

            if (changed) {
              if (isDocument) {
                const doctype = source.match(/<!doctype[^>]*>/iu)?.[0] || '<!DOCTYPE html>';
                return `${doctype}\n${documentValue.documentElement.outerHTML}`;
              }
              return root.innerHTML;
            }
          }
        } catch (error) {
          console.warn('Could not remove the full-report package introduction with DOMParser.', error);
        }
      }

      const decodeFallbackText = block => normalizeCell(
        String(block || '')
          .replace(/<[^>]+>/gu, ' ')
          .replace(/&nbsp;|&#160;/giu, ' ')
          .replace(/&amp;/giu, '&')
          .replace(/&quot;/giu, '"')
          .replace(/&#0?39;|&apos;/giu, "'")
          .replace(/&lt;/giu, '<')
          .replace(/&gt;/giu, '>')
      );
      return source.replace(/<(section|article|header|aside|div|p|small)\b[^>]*>[\s\S]*?<\/\1>/giu, block => (
        this.caseFullReportPackageIntroBoilerplateKind(decodeFallbackText(block)) ? '' : block
      ));
    },

    removeCaseFullReportPackageIntroFromSection(section = {}) {
      if (!section || typeof section !== 'object') return section;
      let next = section;
      let changed = false;
      const ensureCopy = () => {
        if (next === section) next = { ...section };
        return next;
      };
      ['summary', 'description', 'plainText', 'previewText', 'text', 'bodyText', 'contentText'].forEach((key) => {
        if (typeof next[key] !== 'string') return;
        const cleaned = this.removeCaseFullReportPackageIntroFromText(next[key]);
        if (cleaned !== next[key]) {
          ensureCopy()[key] = cleaned;
          changed = true;
        }
      });
      ['html', 'htmlFragment', 'htmlContent', 'bodyHtml'].forEach((key) => {
        if (typeof next[key] !== 'string') return;
        const cleaned = this.removeCaseFullReportPackageIntroFromHtml(next[key]);
        if (cleaned !== next[key]) {
          ensureCopy()[key] = cleaned;
          changed = true;
        }
      });
      if (changed) next.packageIntroBoilerplateOmitted = true;
      return next;
    },

    caseFullReportPackageEmptyDuplicateHashMessage(value = '') {
      return /no duplicate transaction hashes(?: with differing path labels)?(?: are currently)? detected\.?/i.test(normalizeCell(value));
    },

    caseFullReportPackageDuplicateHashSection(section = {}) {
      const identity = normalizeCell([
        section?.id,
        section?.key,
        section?.kind,
        section?.title,
        section?.label,
        section?.heading
      ].join(' ')).toLowerCase();
      return /duplicate[\s_-]*hash/.test(identity);
    },

    caseFullReportPackageDuplicateHashSectionIsEmpty(section = {}) {
      if (!this.caseFullReportPackageDuplicateHashSection(section)) return false;
      let serialized = '';
      try {
        serialized = JSON.stringify(section);
      } catch {
        serialized = normalizeCell(section?.summary || section?.previewText || section?.plainText || section?.text || '');
      }
      if (this.caseFullReportPackageEmptyDuplicateHashMessage(serialized)) return true;
      const rows = Array.isArray(section?.rows)
        ? section.rows
        : (Array.isArray(section?.dataRows) ? section.dataRows : null);
      if (rows) return rows.length === 0;
      if (section?.rowCount !== undefined && section?.rowCount !== null && section?.rowCount !== '') {
        const rowCount = Number(section.rowCount);
        if (Number.isFinite(rowCount) && rowCount === 0) return true;
      }
      return false;
    },

    removeEmptyDuplicateHashAppendixFromText(value = '') {
      const source = String(value || '');
      if (!source || !/duplicate[\s_-]*hash table/i.test(source) || !this.caseFullReportPackageEmptyDuplicateHashMessage(source)) return source;
      const newline = source.includes('\r\n') ? '\r\n' : '\n';
      const lines = source.split(/\r?\n/);
      for (let index = 0; index < lines.length; index += 1) {
        if (!/duplicate[\s_-]*hash table/i.test(lines[index] || '')) continue;
        let emptyIndex = -1;
        for (let probe = index; probe < Math.min(lines.length, index + 14); probe += 1) {
          if (this.caseFullReportPackageEmptyDuplicateHashMessage(lines[probe] || '')) {
            emptyIndex = probe;
            break;
          }
        }
        if (emptyIndex < 0) continue;
        let start = index;
        let end = emptyIndex + 1;
        while (start > 0 && !normalizeCell(lines[start - 1])) start -= 1;
        while (end < lines.length && !normalizeCell(lines[end])) end += 1;
        lines.splice(start, end - start);
        index = Math.max(-1, start - 1);
      }
      return lines.join(newline).replace(/(?:\r?\n){3,}/g, `${newline}${newline}`);
    },

    removeEmptyDuplicateHashAppendixFromHtml(value = '') {
      const source = String(value || '');
      if (!source || !/duplicate[\s_-]*hash table/i.test(source) || !this.caseFullReportPackageEmptyDuplicateHashMessage(source)) return source;

      // Prefer a surgical string removal so unrelated report markup remains byte-for-byte
      // unchanged. DOMParser is retained only as a fallback for an unusual wrapper shape.
      let output = source;
      const emptyMessage = String.raw`no duplicate transaction hashes(?: with differing path labels)?(?: are currently)? detected\.?`;
      output = output.replace(/<(section|article)\b[^>]*>[\s\S]*?<\/\1>/gi, (block) => {
        const hasDuplicateHeading = /duplicate[\s_-]*hash table/i.test(block);
        return hasDuplicateHeading && this.caseFullReportPackageEmptyDuplicateHashMessage(block) ? '' : block;
      });
      output = output.replace(new RegExp(String.raw`<h([1-6])\b[^>]*>[\s\S]{0,1000}?duplicate[\s_-]*hash table[\s\S]{0,1000}?<\/h\1>\s*<table\b[^>]*>[\s\S]{0,5000}?${emptyMessage}[\s\S]{0,2000}?<\/table>`, 'gi'), '');
      if (output !== source) return output;

      if (typeof DOMParser !== 'undefined') {
        try {
          const isDocument = /<html[\s>]/i.test(source) || /<!doctype/i.test(source);
          const parser = new DOMParser();
          const documentValue = parser.parseFromString(isDocument ? source : `<div id="casebuddy-package-sanitize-root">${source}</div>`, 'text/html');
          const root = isDocument ? documentValue.body : documentValue.getElementById('casebuddy-package-sanitize-root');
          if (root) {
            const headings = Array.from(root.querySelectorAll('h1, h2, h3, h4, h5, h6, caption, strong'));
            const heading = headings.find(node => /duplicate[\s_-]*hash table/i.test(normalizeCell(node.textContent || '')));
            if (heading) {
              let container = heading.closest('section, article, [data-report-section], [data-section], .report-section, .case-full-package-section');
              if (!container || !this.caseFullReportPackageEmptyDuplicateHashMessage(container.textContent || '')) {
                const table = Array.from(root.querySelectorAll('table')).find(node => this.caseFullReportPackageEmptyDuplicateHashMessage(node.textContent || ''));
                if (table) {
                  table.remove();
                  heading.remove();
                }
              } else {
                container.remove();
              }
            }
            if (isDocument) {
              const doctype = source.match(/<!doctype[^>]*>/i)?.[0] || '<!DOCTYPE html>';
              return `${doctype}
${documentValue.documentElement.outerHTML}`;
            }
            return root.innerHTML;
          }
        } catch (error) {
          console.warn('Could not remove an empty Duplicate Hash Table appendix with DOMParser.', error);
        }
      }
      return output;
    },

    sanitizeCaseFullReportPackagePayload(options = {}) {
      const payload = this.caseFullReportPackagePayload;
      if (!payload || typeof payload !== 'object') return payload;

      let next = payload;
      let changed = false;
      const ensureCopy = () => {
        if (next === payload) next = { ...payload };
        return next;
      };

      const originalSections = Array.isArray(payload.sections) ? payload.sections : [];
      const serializedText = normalizeCell([
        payload.previewText,
        payload.plainText,
        payload.htmlFragment,
        payload.htmlDocument
      ].join(' '));
      const hasEmptySection = originalSections.some(section => this.caseFullReportPackageDuplicateHashSectionIsEmpty(section));
      const hasEmptyRenderedAppendix = /duplicate[\s_-]*hash table/i.test(serializedText)
        && this.caseFullReportPackageEmptyDuplicateHashMessage(serializedText);

      let sections = originalSections;
      if (hasEmptySection || hasEmptyRenderedAppendix) {
        sections = sections.filter(section => !this.caseFullReportPackageDuplicateHashSectionIsEmpty(section));
        ensureCopy().sections = sections;
        changed = true;

        ['plainText', 'previewText'].forEach((key) => {
          if (typeof next[key] !== 'string') return;
          const cleaned = this.removeEmptyDuplicateHashAppendixFromText(next[key]);
          if (cleaned !== next[key]) {
            ensureCopy()[key] = cleaned;
            changed = true;
          }
        });
        ['htmlFragment', 'htmlDocument'].forEach((key) => {
          if (typeof next[key] !== 'string') return;
          const cleaned = this.removeEmptyDuplicateHashAppendixFromHtml(next[key]);
          if (cleaned !== next[key]) {
            ensureCopy()[key] = cleaned;
            changed = true;
          }
        });
        ensureCopy().emptyDuplicateHashAppendixOmitted = true;
        ensureCopy().emptyDuplicateHashAppendixOmittedSource = normalizeCell(options.source || 'case-full-report-package-sanitize');
      }

      const currentSections = Array.isArray(next.sections) ? next.sections : sections;
      const completionSections = currentSections.map(section => (
        this.caseFullReportPackageCompletionSummarySection(section)
          ? this.simplifyCaseFullReportPackageCompletionSummarySection(section)
          : section
      ));
      const completionSectionsChanged = completionSections.some((section, index) => section !== currentSections[index]);
      const introCleanedSections = completionSections.map(section => this.removeCaseFullReportPackageIntroFromSection(section));
      const introSectionsChanged = introCleanedSections.some((section, index) => section !== completionSections[index]);
      if (introSectionsChanged || completionSectionsChanged) {
        ensureCopy().sections = introCleanedSections;
        changed = true;
      }

      let packageIntroBoilerplateOmitted = introSectionsChanged;
      ['plainText', 'previewText'].forEach((key) => {
        if (typeof next[key] !== 'string') return;
        const completionSimplified = this.simplifyCaseFullReportPackageCompletionSummaryText(next[key]);
        const withoutIntro = this.removeCaseFullReportPackageIntroFromText(completionSimplified);
        if (withoutIntro !== completionSimplified) packageIntroBoilerplateOmitted = true;
        if (withoutIntro !== next[key]) {
          ensureCopy()[key] = withoutIntro;
          changed = true;
        }
      });
      ['htmlFragment', 'htmlDocument'].forEach((key) => {
        if (typeof next[key] !== 'string') return;
        const completionSimplified = this.simplifyCaseFullReportPackageCompletionSummaryHtml(next[key]);
        const withoutIntro = this.removeCaseFullReportPackageIntroFromHtml(completionSimplified);
        if (withoutIntro !== completionSimplified) packageIntroBoilerplateOmitted = true;
        if (withoutIntro !== next[key]) {
          ensureCopy()[key] = withoutIntro;
          changed = true;
        }
      });
      if (packageIntroBoilerplateOmitted) {
        ensureCopy().packageIntroBoilerplateOmitted = true;
        ensureCopy().packageIntroBoilerplateOmittedSource = normalizeCell(options.source || 'case-full-report-package-sanitize');
        changed = true;
      }

      if (!changed) return payload;
      if (typeof next.plainText === 'string') next.plainTextLength = next.plainText.length;
      if (typeof next.htmlDocument === 'string' || typeof next.htmlFragment === 'string') {
        next.htmlLength = String(next.htmlDocument || next.htmlFragment || '').length;
      }
      next.caseCompletionSummarySimplified = true;
      next.caseCompletionSummarySimplifiedSource = normalizeCell(options.source || 'case-full-report-package-sanitize');
      this.caseFullReportPackagePayload = next;
      return next;
    },

    async refreshCaseFullReportPackageReview(...args) {
      let result = this.callCaseBuddyOriginalMethod(caseBuddyDeliverableMethods, 'refreshCaseFullReportPackageReview', args);
      if (result && typeof result.then === 'function') result = await result;
      this.sanitizeCaseFullReportPackagePayload({ source: 'refresh-full-report-package' });
      return result;
    },

    async stageCaseFullReportPackagePreview(...args) {
      this.sanitizeCaseFullReportPackagePayload({ source: 'before-stage-full-report-package' });
      let result = this.callCaseBuddyOriginalMethod(caseBuddyDeliverableMethods, 'stageCaseFullReportPackagePreview', args);
      if (result && typeof result.then === 'function') result = await result;
      this.sanitizeCaseFullReportPackagePayload({ source: 'stage-full-report-package' });
      return result;
    },

    async buildOneClickPackagePreview(...args) {
      let result = this.callCaseBuddyOriginalMethod(caseBuddyDeliverableMethods, 'buildOneClickPackagePreview', args);
      if (result && typeof result.then === 'function') result = await result;
      this.sanitizeCaseFullReportPackagePayload({ source: 'one-click-package-preview' });
      return result;
    },

    async copyCaseFullReportPackageRich(...args) {
      this.sanitizeCaseFullReportPackagePayload({ source: 'copy-rich-full-report-package' });
      let result = this.callCaseBuddyOriginalMethod(caseBuddyDeliverableMethods, 'copyCaseFullReportPackageRich', args);
      if (result && typeof result.then === 'function') result = await result;
      return result;
    },

    async copyCaseFullReportPackageText(...args) {
      this.sanitizeCaseFullReportPackagePayload({ source: 'copy-text-full-report-package' });
      let result = this.callCaseBuddyOriginalMethod(caseBuddyDeliverableMethods, 'copyCaseFullReportPackageText', args);
      if (result && typeof result.then === 'function') result = await result;
      return result;
    },

    async downloadCaseFullReportPackageHtml(...args) {
      this.sanitizeCaseFullReportPackagePayload({ source: 'download-html-full-report-package' });
      let result = this.callCaseBuddyOriginalMethod(caseBuddyDeliverableMethods, 'downloadCaseFullReportPackageHtml', args);
      if (result && typeof result.then === 'function') result = await result;
      return result;
    },

    async downloadCaseFullReportPackageText(...args) {
      this.sanitizeCaseFullReportPackagePayload({ source: 'download-text-full-report-package' });
      let result = this.callCaseBuddyOriginalMethod(caseBuddyDeliverableMethods, 'downloadCaseFullReportPackageText', args);
      if (result && typeof result.then === 'function') result = await result;
      return result;
    },

    caseFullReportPackagePdfEscapeHtml(value = '') {
      return String(value ?? '')
        .replace(/&/gu, '&amp;')
        .replace(/</gu, '&lt;')
        .replace(/>/gu, '&gt;')
        .replace(/"/gu, '&quot;')
        .replace(/'/gu, '&#039;');
    },

    caseFullReportPackagePdfFileStem() {
      const caseId = normalizeCell(this.caseDisplayId || '');
      const caseName = normalizeCell(this.currentProjectFileName || this.caseDetails?.caseName || '');
      const meaningfulCaseId = caseId && !/^no case id set$/iu.test(caseId) ? caseId : '';
      const sourceLabel = [meaningfulCaseId, caseName].filter(Boolean).join('-') || 'report';
      const decomposed = typeof sourceLabel.normalize === 'function'
        ? sourceLabel.normalize('NFKD')
        : sourceLabel;
      const safeLabel = decomposed
        .replace(/[\u0300-\u036f]/gu, '')
        .replace(/[^a-z0-9]+/giu, '-')
        .replace(/^-+|-+$/gu, '')
        .toLowerCase() || 'report';
      return `case-buddy-${safeLabel}-full-report`;
    },

    caseFullReportPackageBuildPdfHtml(payload = {}) {
      const htmlDocument = this.removeCaseFullReportPackageIntroFromHtml(String(payload?.htmlDocument || '').trim());
      const htmlFragment = this.removeCaseFullReportPackageIntroFromHtml(String(payload?.htmlFragment || '').trim());
      const title = this.caseFullReportPackagePdfFileStem();

      if (typeof DOMParser === 'undefined') {
        const escapedTitle = this.caseFullReportPackagePdfEscapeHtml(title);
        return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${escapedTitle}</title><style>${this.caseFullReportPackagePdfPrintStyles()}</style></head><body class="casebuddy-pdf-body"><main class="casebuddy-pdf-report">${htmlFragment || '<p>The report preview is not available.</p>'}</main></body></html>`;
      }

      const source = htmlDocument || `<!doctype html><html lang="en"><head><meta charset="utf-8"></head><body>${htmlFragment || '<p>The report preview is not available.</p>'}</body></html>`;
      const parsed = new DOMParser().parseFromString(source, 'text/html');

      parsed.documentElement.setAttribute('lang', parsed.documentElement.getAttribute('lang') || 'en');
      parsed.title = title;
      parsed.body.classList.add('casebuddy-pdf-body');

      // The rich preview is the authoritative rendered package because it is the
      // version that carries the inline flow SVGs. Keep the document shell/CSS
      // when available, but prefer the rich body if the standalone document
      // somehow omitted those figures.
      const documentHasSvg = Boolean(parsed.body.querySelector('svg'));
      const fragmentHasSvg = /<svg[\s>]/iu.test(htmlFragment);
      if (
        htmlFragment
        && (
          !normalizeCell(parsed.body.textContent || '')
          || (fragmentHasSvg && !documentHasSvg)
        )
      ) {
        parsed.body.innerHTML = htmlFragment;
      }

      parsed.querySelectorAll('script, noscript').forEach(node => node.remove());
      parsed.querySelectorAll('[contenteditable]').forEach(node => node.removeAttribute('contenteditable'));

      if (typeof window !== 'undefined' && window.location?.href && !parsed.head.querySelector('base')) {
        const base = parsed.createElement('base');
        base.href = window.location.href;
        parsed.head.prepend(base);
      }

      let viewport = parsed.head.querySelector('meta[name="viewport"]');
      if (!viewport) {
        viewport = parsed.createElement('meta');
        viewport.setAttribute('name', 'viewport');
        parsed.head.appendChild(viewport);
      }
      viewport.setAttribute('content', 'width=device-width, initial-scale=1');

      let charset = parsed.head.querySelector('meta[charset]');
      if (!charset) {
        charset = parsed.createElement('meta');
        charset.setAttribute('charset', 'utf-8');
        parsed.head.prepend(charset);
      }

      const priorPrintStyles = parsed.getElementById('casebuddy-pdf-print-styles');
      if (priorPrintStyles) priorPrintStyles.remove();
      const printStyles = parsed.createElement('style');
      printStyles.id = 'casebuddy-pdf-print-styles';
      printStyles.textContent = this.caseFullReportPackagePdfPrintStyles();
      parsed.head.appendChild(printStyles);

      parsed.querySelectorAll('svg').forEach((svg) => {
        svg.setAttribute('focusable', 'false');
        svg.setAttribute('aria-hidden', 'true');
        svg.style.maxWidth = '100%';
        svg.style.height = 'auto';
        svg.style.breakInside = 'avoid-page';
        svg.style.pageBreakInside = 'avoid';
      });

      const reportMain = parsed.body.querySelector('main');
      if (reportMain) reportMain.classList.add('casebuddy-pdf-report');
      else parsed.body.classList.add('casebuddy-pdf-report');

      return `<!doctype html>\n${parsed.documentElement.outerHTML}`;
    },

    caseFullReportPackagePdfPrintStyles() {
      return `
        @page {
          size: auto;
          margin: 0 0 12mm;

          @bottom-center {
            content: "Page " counter(page);
            color: #111827;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 10pt;
            font-weight: 700;
            line-height: 1;
          }
        }

        :root {
          color-scheme: light;
        }

        html,
        body {
          background: #ffffff !important;
          color: #111827 !important;
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
          max-width: none !important;
        }

        body,
        body * {
          box-sizing: border-box;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .casebuddy-pdf-report {
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 14mm 13mm 6mm !important;
          -webkit-box-decoration-break: clone;
          box-decoration-break: clone;
        }

        h1,
        h2,
        h3,
        h4,
        h5 {
          break-after: avoid-page;
          page-break-after: avoid;
        }

        p,
        li {
          orphans: 3;
          widows: 3;
        }

        figure,
        svg,
        img,
        table,
        pre,
        blockquote {
          break-inside: avoid-page;
          page-break-inside: avoid;
        }

        svg,
        img {
          display: block;
          max-width: 100% !important;
          height: auto !important;
          margin-left: auto;
          margin-right: auto;
        }

        table {
          width: 100% !important;
          border-collapse: collapse;
        }

        thead {
          display: table-header-group;
        }

        tfoot {
          display: table-footer-group;
        }

        tr,
        td,
        th {
          break-inside: avoid-page;
          page-break-inside: avoid;
        }

        pre {
          max-width: 100%;
          white-space: pre-wrap;
          overflow-wrap: anywhere;
        }

        a {
          color: inherit !important;
          text-decoration: none !important;
        }

        button,
        input,
        select,
        textarea,
        [role="button"],
        .no-print {
          display: none !important;
        }

        @media screen {
          body {
            padding: 24px !important;
          }
        }
      `;
    },

    async caseFullReportPackageWaitForPdfWindow(printWindow, timeoutMs = 8000) {
      const targetWindow = printWindow;
      const targetDocument = targetWindow?.document;
      if (!targetWindow || !targetDocument) return false;

      const timeout = Math.max(1000, Number(timeoutMs) || 8000);
      const waitForLoad = new Promise((resolve) => {
        if (targetDocument.readyState === 'complete') {
          resolve();
          return;
        }
        const finish = () => resolve();
        targetWindow.addEventListener('load', finish, { once: true });
        window.setTimeout(() => {
          if (targetDocument.readyState === 'interactive' || targetDocument.readyState === 'complete') finish();
        }, 0);
      });

      await Promise.race([
        waitForLoad,
        new Promise(resolve => window.setTimeout(resolve, timeout))
      ]);

      try {
        if (targetDocument.fonts?.ready) {
          await Promise.race([
            targetDocument.fonts.ready,
            new Promise(resolve => window.setTimeout(resolve, Math.min(timeout, 3000)))
          ]);
        }
      } catch {
        // Font readiness is a presentation optimization, not a PDF blocker.
      }

      const images = Array.from(targetDocument.images || []);
      if (images.length) {
        await Promise.race([
          Promise.all(images.map(image => (
            image.complete
              ? Promise.resolve()
              : new Promise(resolve => {
                image.addEventListener('load', resolve, { once: true });
                image.addEventListener('error', resolve, { once: true });
              })
          ))),
          new Promise(resolve => window.setTimeout(resolve, Math.min(timeout, 5000)))
        ]);
      }

      await new Promise(resolve => window.setTimeout(resolve, 180));
      return !targetWindow.closed;
    },

    caseFullReportPackagePreparePdfWindowLocation(printWindow) {
      if (!printWindow || typeof window === 'undefined' || typeof URL === 'undefined') return false;
      try {
        const printUrl = new URL(window.location.href);
        printUrl.search = '';
        printUrl.hash = `case-buddy-pdf-${this.caseFullReportPackagePdfFileStem()}`;
        printWindow.history.replaceState({ caseBuddyPdf: true }, '', printUrl.toString());
        return true;
      } catch {
        return false;
      }
    },

    async downloadCaseFullReportPackagePdf(options = {}) {
      if (this.caseFullReportPackagePdfPreparing) return false;
      if (typeof window === 'undefined' || typeof window.open !== 'function') {
        this.caseFullReportPackageStatus = 'PDF export is not available in this browser.';
        this.showCaseToast?.(this.caseFullReportPackageStatus);
        return false;
      }

      const source = normalizeCell(options?.source || 'full-report-package-pdf');
      const printWindow = window.open('', `case-buddy-pdf-${Date.now()}`);
      if (!printWindow) {
        this.caseFullReportPackageStatus = 'The browser blocked the PDF window. Allow pop-ups for this page, then select Save PDF again.';
        this.showCaseToast?.(this.caseFullReportPackageStatus);
        return false;
      }

      this.caseFullReportPackagePdfPreparing = true;
      this.caseFullReportPackageStatus = 'Preparing the PDF with embedded case-flow visuals…';

      try {
        this.caseFullReportPackagePreparePdfWindowLocation(printWindow);
        printWindow.opener = null;
        const escapedTitle = this.caseFullReportPackagePdfEscapeHtml(this.caseFullReportPackagePdfFileStem());
        printWindow.document.open();
        printWindow.document.write(`<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${escapedTitle}</title><style>body{margin:0;padding:48px;font-family:Arial,sans-serif;background:#f8fafc;color:#1f2937}main{max-width:720px;margin:12vh auto;padding:32px;border:1px solid #dbe3ee;border-radius:18px;background:white;box-shadow:0 20px 60px rgba(15,23,42,.12)}h1{margin:0 0 12px;font-size:24px}p{margin:0;color:#64748b;line-height:1.6}</style></head><body><main><h1>Preparing your Case Buddy PDF</h1><p>The report and its inline flow visuals are being prepared for the browser PDF dialog.</p></main></body></html>`);
        printWindow.document.close();

        if (
          options.ensureReady !== false
          && !this.caseFullReportPackageStateView?.isReady
          && this.caseAutomation?.skipResolvableReview
          && typeof this.ensureCaseWorkflowFullReportPackageReadyForSkipMode === 'function'
        ) {
          await this.ensureCaseWorkflowFullReportPackageReadyForSkipMode({
            source: `${source}-ensure-ready`,
            suppressUi: true,
            force: true
          });
        }

        if (!this.caseFullReportPackagePayload || !this.caseFullReportPackageStateView?.isReady) {
          if (typeof this.buildOneClickPackagePreview === 'function') {
            let buildResult = this.buildOneClickPackagePreview();
            if (buildResult && typeof buildResult.then === 'function') buildResult = await buildResult;
          }
          if (typeof this.refreshCaseFullReportPackageReview === 'function') {
            let refreshResult = this.refreshCaseFullReportPackageReview();
            if (refreshResult && typeof refreshResult.then === 'function') refreshResult = await refreshResult;
          }
        }

        this.sanitizeCaseFullReportPackagePayload({
          source: `${source}-sanitize`
        });

        const payload = this.caseFullReportPackagePayloadView || this.caseFullReportPackagePayload || {};
        const state = this.caseFullReportPackageStateView || payload.state || {};
        if (!state.isReady) {
          throw new Error(
            normalizeCell(payload.missingReason || state.summary || payload.summary)
              || 'The full report package is not ready for PDF export yet.'
          );
        }

        const printableHtml = this.caseFullReportPackageBuildPdfHtml(payload);
        if (!/<svg[\s>]/iu.test(printableHtml) && Number(payload.embeddedVisualCount || 0) > 0) {
          throw new Error('The PDF package expected case-flow visuals, but the printable report did not contain the inline SVG figures.');
        }

        printWindow.document.open();
        printWindow.document.write(printableHtml);
        printWindow.document.close();

        const ready = await this.caseFullReportPackageWaitForPdfWindow(printWindow);
        if (!ready) throw new Error('The PDF preview window closed before the report finished loading.');

        printWindow.focus();
        printWindow.addEventListener('afterprint', () => {
          try {
            printWindow.close();
          } catch {
            // The browser may already have closed the temporary print window.
          }
        }, { once: true });
        printWindow.print();

        this.caseFullReportPackageStatus = 'PDF dialog opened. Choose “Save as PDF” to download the report with its embedded flow visuals.';
        if (!state.reviewIsCurrent && typeof this.markCaseFullReportPackageReviewComplete === 'function') {
          let markResult = this.markCaseFullReportPackageReviewComplete();
          if (markResult && typeof markResult.then === 'function') markResult = await markResult;
          this.sanitizeCaseFullReportPackagePayload({
            source: `${source}-marked-reviewed`
          });
        }
        return true;
      } catch (error) {
        console.warn('Case Buddy could not prepare the full report PDF.', error);
        this.caseFullReportPackageStatus = error?.message || 'Could not prepare the full report PDF.';
        this.showCaseToast?.(this.caseFullReportPackageStatus);
        try {
          if (!printWindow.closed) printWindow.close();
        } catch {
          // Ignore cleanup errors from a browser-controlled window.
        }
        return false;
      } finally {
        this.caseFullReportPackagePdfPreparing = false;
      }
    },

    setCaseSkipResolvableReviewsEnabled(value) {
      const next = Boolean(value);
      if (this.caseSimpleLaunchAutoSkipResolvable !== next) {
        this.caseSimpleLaunchAutoSkipResolvable = next;
      }
      if (Boolean(this.caseAutomation?.skipResolvableReview) !== next) {
        this.caseAutomation = {
          ...(this.caseAutomation || {}),
          skipResolvableReview: next
        };
      }
      return next;
    },

    caseSimpleLaunchStepClass(step = '') {
      const key = normalizeCell(step).toLowerCase();
      const hasCase = Boolean(this.caseSimpleLaunchHasCase);
      const hasStartingRows = Boolean(this.caseSimpleLaunchHasStartingTransactions);
      const autoActive = Boolean(this.caseSimpleLaunchAutoStarting || this.caseAutomation?.active || this.caseWorkflowBackgroundAutomationActive);
      const reportReady = Boolean(this.caseSimpleLaunchReportReady);
      let done = false;
      let active = false;

      if (key === 'case') {
        done = hasCase;
        active = !hasCase;
      } else if (key === 'transactions') {
        done = hasStartingRows;
        active = hasCase && !hasStartingRows;
      } else if (key === 'auto') {
        done = reportReady;
        active = hasCase && hasStartingRows && !reportReady;
      } else if (key === 'report') {
        done = reportReady;
        active = reportReady || autoActive;
      }

      return {
        'case-simple-launch__step': true,
        'case-simple-launch__step--done': done,
        'case-simple-launch__step--active': active && !done
      };
    },

    persistInputSpreadsheetToolsPreference() {
      if (typeof window === 'undefined' || !window.localStorage) return;
      try {
        window.localStorage.setItem('casebuddy_input_spreadsheet_tools_expanded', this.spreadsheetToolsExpanded ? 'true' : 'false');
      } catch (error) {
        console.warn('Could not save spreadsheet tools preference.', error);
      }
    },

    setSpreadsheetToolsExpanded(expanded) {
      const nextValue = Boolean(expanded);
      if (this.spreadsheetToolsExpanded === nextValue) {
        return;
      }

      this.spreadsheetToolsExpanded = nextValue;
      if (!nextValue) {
        this.activeColorPicker = '';
      }
      this.persistInputSpreadsheetToolsPreference();
      this.$nextTick(() => {
        this.$refs.inputSpreadsheet?.refreshDimensions?.();
      });
    },

    toggleSpreadsheetToolsExpanded() {
      this.setSpreadsheetToolsExpanded(!this.spreadsheetToolsExpanded);
    },

    persistCaseWorkspaceDisplayPreference(key, value) {
      if (typeof window === 'undefined' || !window.localStorage || !key) return;
      try {
        window.localStorage.setItem(key, value ? 'true' : 'false');
      } catch (error) {
        console.warn('Could not save the Case Buddy display preference.', error);
      }
    },

    toggleCaseOverviewDetails() {
      this.caseOverviewDetailsExpanded = !this.caseOverviewDetailsExpanded;
      this.persistCaseWorkspaceDisplayPreference(
        CASE_OVERVIEW_DETAILS_EXPANDED_STORAGE_KEY,
        this.caseOverviewDetailsExpanded
      );
    },

    setCaseScrollNavigationVisible(visible) {
      const nextValue = Boolean(visible);
      if (this.caseScrollNavigationVisible === nextValue) return;
      this.caseScrollNavigationVisible = nextValue;
      this.persistCaseWorkspaceDisplayPreference(CASE_SCROLL_NAVIGATION_VISIBLE_STORAGE_KEY, nextValue);
    },

    toggleCaseScrollNavigationVisible() {
      this.setCaseScrollNavigationVisible(!this.caseScrollNavigationVisible);
    },

    applyCaseWorkspaceDarkModeDocumentClass(active = this.caseWorkspaceDarkModeActive) {
      if (typeof document === 'undefined') return;
      const enabled = Boolean(active);
      document.documentElement?.classList?.toggle(CASE_WORKSPACE_DARK_MODE_DOCUMENT_CLASS, enabled);
      document.body?.classList?.toggle(CASE_WORKSPACE_DARK_MODE_DOCUMENT_CLASS, enabled);
    },

    setCaseWorkspaceDarkModeEnabled(enabled) {
      const nextValue = Boolean(enabled);
      if (this.caseWorkspaceDarkModeEnabled === nextValue) return;
      this.caseWorkspaceDarkModeEnabled = nextValue;
      this.persistCaseWorkspaceDisplayPreference(CASE_WORKSPACE_DARK_MODE_STORAGE_KEY, nextValue);
      this.applyCaseWorkspaceDarkModeDocumentClass?.(this.caseWorkspaceDarkModeActive);
    },

    toggleCaseWorkspaceDarkMode() {
      this.setCaseWorkspaceDarkModeEnabled(!this.caseWorkspaceDarkModeEnabled);
    },

    setOutputSheetExpanded(expanded) {
      const nextValue = Boolean(expanded);
      if (this.outputSheetExpanded === nextValue) return;
      this.outputSheetExpanded = nextValue;
      this.persistCaseWorkspaceDisplayPreference(CASE_OUTPUT_SHEET_EXPANDED_STORAGE_KEY, nextValue);
      if (nextValue) {
        this.$nextTick(() => {
          const refreshOutputDimensions = () => {
            this.$refs.outputSpreadsheet?.refreshDimensions?.();
          };
          refreshOutputDimensions();
          if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(refreshOutputDimensions);
          }
        });
      }
    },

    toggleOutputSheetExpanded() {
      this.setOutputSheetExpanded(!this.outputSheetExpanded);
    },

    setWorkflowPanelExpanded(expanded) {
      const nextValue = Boolean(expanded);
      if (this.workflowPanelExpanded === nextValue) return;
      this.workflowPanelExpanded = nextValue;
      this.persistCaseWorkspaceDisplayPreference(CASE_WORKFLOW_PANEL_EXPANDED_STORAGE_KEY, nextValue);
    },

    toggleWorkflowPanelExpanded() {
      this.setWorkflowPanelExpanded(!this.workflowPanelExpanded);
    },

    setInstructionsPanelExpanded(expanded) {
      const nextValue = Boolean(expanded);
      if (this.instructionsPanelExpanded === nextValue) return;
      this.instructionsPanelExpanded = nextValue;
      this.persistCaseWorkspaceDisplayPreference(CASE_INSTRUCTIONS_PANEL_EXPANDED_STORAGE_KEY, nextValue);
    },

    toggleInstructionsPanelExpanded() {
      this.setInstructionsPanelExpanded(!this.instructionsPanelExpanded);
    },

    setDeliverablesPanelExpanded(expanded) {
      const nextValue = Boolean(expanded);
      if (this.deliverablesPanelExpanded === nextValue) return;
      this.deliverablesPanelExpanded = nextValue;
      this.persistCaseWorkspaceDisplayPreference(CASE_DELIVERABLES_PANEL_EXPANDED_STORAGE_KEY, nextValue);
    },

    toggleDeliverablesPanelExpanded() {
      this.setDeliverablesPanelExpanded(!this.deliverablesPanelExpanded);
    },

    caseIntakeRailParseUtcDateMs(value = '') {
      const raw = normalizeCell(value);
      if (!raw) return Number.NaN;

      let normalized = raw;
      try {
        if (typeof this.caseManualNormalizeDateText === 'function') {
          normalized = normalizeCell(this.caseManualNormalizeDateText(raw)) || raw;
        }
      } catch {
        normalized = raw;
      }

      const candidates = Array.from(new Set([normalized, raw].map(value => normalizeCell(value)).filter(Boolean)));
      for (const candidate of candidates) {
        const variants = Array.from(new Set([
          candidate,
          candidate.replace(/\s+UTC$/iu, 'Z'),
          candidate.replace(
            /^(\d{4}-\d{1,2}-\d{1,2})\s+(\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?)(?:\s+UTC)?$/iu,
            '$1T$2Z'
          )
        ].filter(Boolean)));
        for (const variant of variants) {
          const parsed = Date.parse(variant);
          if (Number.isFinite(parsed)) return parsed;
        }
      }
      return Number.NaN;
    },

    caseWorkflowRootLabelAlphaFromNumber(position = 1) {
      let value = Math.max(1, Number(position) || 1);
      let label = '';
      while (value > 0) {
        value -= 1;
        label = String.fromCharCode(65 + (value % 26)) + label;
        value = Math.floor(value / 26);
      }
      return label;
    },

    caseWorkflowRootLabelAlphaToNumber(value = '') {
      const label = normalizeCell(value).toUpperCase();
      if (!/^[A-Z]+$/u.test(label)) return Number.NaN;
      let result = 0;
      for (const character of label) result = result * 26 + (character.charCodeAt(0) - 64);
      return result;
    },

    caseWorkflowAnalyzeRootLabelSequence(rootStates = []) {
      const states = Array.isArray(rootStates) ? rootStates : [];
      const labels = states.map(state => normalizeCell(state?.pathLabel || state?.rootLabel || state?.label || ''));
      const count = labels.length;
      const missingCount = labels.filter(label => !label).length;
      const labelsPresentComplete = count > 0 && missingCount === 0;
      const uniqueCount = new Set(labels.filter(Boolean)).size;
      let style = '';
      let prefix = '';
      let expectedLabels = [];

      if (labelsPresentComplete && labels.every(label => /^\d+$/u.test(label))) {
        const values = labels.map(label => Number(label));
        const expectedValues = Array.from({ length: count }, (_, index) => index + 1);
        if ([...values].sort((left, right) => left - right).every((value, index) => value === expectedValues[index])) {
          style = 'numeric';
          expectedLabels = expectedValues.map(String);
        }
      }

      if (!style && labelsPresentComplete && labels.every(label => /^[A-Z]+$/u.test(label))) {
        const values = labels.map(label => this.caseWorkflowRootLabelAlphaToNumber(label));
        const expectedValues = Array.from({ length: count }, (_, index) => index + 1);
        if ([...values].sort((left, right) => left - right).every((value, index) => value === expectedValues[index])) {
          style = 'alpha';
          expectedLabels = expectedValues.map(value => this.caseWorkflowRootLabelAlphaFromNumber(value));
        }
      }

      if (!style && labelsPresentComplete) {
        const parsed = labels.map(label => label.match(/^(.+)-(\d+)$/u));
        if (parsed.every(Boolean)) {
          const prefixes = Array.from(new Set(parsed.map(match => match[1])));
          const values = parsed.map(match => Number(match[2]));
          const expectedValues = Array.from({ length: count }, (_, index) => index + 1);
          if (prefixes.length === 1 && [...values].sort((left, right) => left - right).every((value, index) => value === expectedValues[index])) {
            style = 'prefixed-numeric';
            prefix = prefixes[0];
            expectedLabels = expectedValues.map(value => `${prefix}-${value}`);
          }
        }
      }

      const sequenceRecognized = Boolean(style && expectedLabels.length === count);
      const labelsChronologyPass = labelsPresentComplete && (
        !sequenceRecognized
        || labels.every((label, index) => label === expectedLabels[index])
      );
      const outOfSequenceCount = sequenceRecognized
        ? labels.filter((label, index) => label !== expectedLabels[index]).length
        : 0;

      return {
        labels,
        count,
        missingCount,
        labelsPresentComplete,
        uniqueCount,
        style,
        prefix,
        expectedLabels,
        sequenceRecognized,
        labelsChronologyPass,
        outOfSequenceCount,
        relabelSafe: Boolean(labelsPresentComplete && sequenceRecognized && uniqueCount === count)
      };
    },

    caseWorkflowBuildChronologicalRootBlocks(matrix = []) {
      const sourceRows = Array.isArray(matrix) ? matrix : [];
      const columnCount = Math.max(
        CASE_MANUAL_START_COLUMN_COUNT,
        ...sourceRows.slice(0, 250).map(row => Array.isArray(row) ? row.length : 0)
      );
      const rows = normalizeCaseMatrix(sourceRows, columnCount);
      const roots = (typeof this.caseWorkflowCollectRootTransactions === 'function'
        ? this.caseWorkflowCollectRootTransactions(rows, { allowNonRootLabels: true })
        : [])
        .filter(root => root && Number.isInteger(Number(root.rowIndex)))
        .sort((left, right) => Number(left.rowIndex) - Number(right.rowIndex));
      if (!roots.length) return { rows, roots: [], prefixRows: rows, blocks: [], sortedBlocks: [], datesComplete: false, orderChanged: false };

      const firstRootRowIndex = Math.max(0, Number(roots[0].rowIndex) || 0);
      const prefixRows = rows.slice(0, firstRootRowIndex);
      const blocks = roots.map((root, index) => {
        const startRowIndex = Math.max(0, Number(root.rowIndex) || 0);
        const endRowIndex = index + 1 < roots.length ? Math.max(startRowIndex, Number(roots[index + 1].rowIndex) || rows.length) : rows.length;
        const blockRows = rows.slice(startRowIndex, endRowIndex);
        const leadRow = blockRows[0] || root.row || [];
        const dateText = normalizeCell(leadRow?.[CASE_MANUAL_START_COL.DATE] || root?.date || '');
        const dateMs = this.caseIntakeRailParseUtcDateMs(dateText);
        return {
          root,
          originalIndex: index,
          startRowIndex,
          endRowIndex,
          rows: blockRows,
          dateText,
          dateMs,
          pathLabel: normalizeCell(root?.pathLabel || leadRow?.[CASE_MANUAL_START_COL.PATH_LABEL] || ''),
          rootKey: this.caseWorkflowRootInstanceKey?.(root, { includeRowIndex: false }) || `${normalizeCell(root?.hash)}|${normalizeCell(root?.pathLabel)}|${index}`
        };
      });
      const datesComplete = blocks.every(block => Number.isFinite(block.dateMs));
      const sortedBlocks = datesComplete
        ? [...blocks].sort((left, right) => left.dateMs - right.dateMs || left.originalIndex - right.originalIndex)
        : blocks.slice();
      const orderChanged = sortedBlocks.some((block, index) => block.originalIndex !== index);
      return { rows, roots, prefixRows, blocks, sortedBlocks, datesComplete, orderChanged };
    },

    caseWorkflowRelabelPathWithinRoot(pathLabel = '', oldRootLabel = '', newRootLabel = '') {
      const label = normalizeCell(pathLabel);
      const oldRoot = normalizeCell(oldRootLabel);
      const newRoot = normalizeCell(newRootLabel);
      if (!label || !oldRoot || !newRoot || oldRoot === newRoot) return label;
      if (label === oldRoot) return newRoot;
      if (label.startsWith(oldRoot)) return `${newRoot}${label.slice(oldRoot.length)}`;
      return label;
    },

    caseWorkflowBuildPathLabelMapForRootBlocks(blocks = [], rootMappings = []) {
      const mappingsByOriginalIndex = new Map(
        (Array.isArray(rootMappings) ? rootMappings : []).map(mapping => [Number(mapping.originalIndex), mapping])
      );
      const labelMap = {};
      const conflicts = [];
      (Array.isArray(blocks) ? blocks : []).forEach((block) => {
        const mapping = mappingsByOriginalIndex.get(Number(block?.originalIndex));
        if (!mapping || !mapping.oldRootLabel || !mapping.newRootLabel || mapping.oldRootLabel === mapping.newRootLabel) return;
        (Array.isArray(block?.rows) ? block.rows : []).forEach((row) => {
          const oldLabel = normalizeCell(row?.[CASE_MANUAL_START_COL.PATH_LABEL]);
          if (!oldLabel) return;
          const nextLabel = this.caseWorkflowRelabelPathWithinRoot(oldLabel, mapping.oldRootLabel, mapping.newRootLabel);
          if (!nextLabel || nextLabel === oldLabel) return;
          if (labelMap[oldLabel] && labelMap[oldLabel] !== nextLabel) {
            conflicts.push({ oldLabel, existing: labelMap[oldLabel], nextLabel });
            return;
          }
          labelMap[oldLabel] = nextLabel;
        });
      });
      return { labelMap, conflicts };
    },

    caseWorkflowReplaceKnownPathLabelsInTextSegment(value = '', labelMap = {}) {
      const text = String(value == null ? '' : value);
      const keys = Object.keys(labelMap || {}).filter(key => key && labelMap[key] && labelMap[key] !== key).sort((left, right) => right.length - left.length);
      if (!text || !keys.length) return text;
      const escaped = keys.map(key => key.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&'));
      const pattern = new RegExp(`(^|[^A-Za-z0-9_.:/\\-])(${escaped.join('|')})(?=$|[^A-Za-z0-9_.:/\\-])`, 'gu');
      return text.replace(pattern, (match, prefix, label) => `${prefix}${labelMap[label] || label}`);
    },

    caseWorkflowRelabelGeneratedSystemNote(value = '', labelMap = {}) {
      const text = String(value == null ? '' : value);
      if (!text || !Object.keys(labelMap || {}).length) return text;
      let next = text.replace(/(\bThis\s+transaction\s*)\(([^()\r\n]+)\)/giu, (match, prefix, inside) => {
        const label = normalizeCell(inside);
        return labelMap[label] ? `${prefix}(${labelMap[label]})` : match;
      });
      const phrasePattern = /(\b(?:Branches\s+as|Continues\s+as|same\s+transaction\s+hash\s+as|same\s+transaction\s+as|path\s+labels?|labeled\s+as)\b)([^.\r\n]*)/giu;
      next = next.replace(phrasePattern, (match, phrase, tail) => `${phrase}${this.caseWorkflowReplaceKnownPathLabelsInTextSegment(tail, labelMap)}`);
      return next;
    },

    caseWorkflowApplyRootRelabelToRawMatrix(matrix = [], rootMappings = [], labelMap = {}) {
      const sourceRows = Array.isArray(matrix) ? matrix : [];
      const columnCount = Math.max(
        CASE_MANUAL_START_COLUMN_COUNT,
        ...sourceRows.slice(0, 250).map(row => Array.isArray(row) ? row.length : 0)
      );
      const rows = normalizeCaseMatrix(sourceRows, columnCount);
      const mappings = (Array.isArray(rootMappings) ? rootMappings : [])
        .filter(mapping => mapping?.oldRootLabel && mapping?.newRootLabel && mapping.oldRootLabel !== mapping.newRootLabel)
        .sort((left, right) => right.oldRootLabel.length - left.oldRootLabel.length);
      const knownRootLabels = mappings.map(mapping => mapping.oldRootLabel);
      let changedCount = 0;
      const nextRows = rows.map((sourceRow) => {
        const row = this.caseManualEnsureRowWidth(sourceRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceRow?.length || 0));
        const oldLabel = normalizeCell(row[CASE_MANUAL_START_COL.PATH_LABEL]);
        if (!oldLabel) return row;
        let nextLabel = labelMap?.[oldLabel] || '';
        if (!nextLabel) {
          const mapping = mappings.find(candidate => this.caseWorkflowPathLabelBelongsToRoot(oldLabel, candidate.oldRootLabel, knownRootLabels));
          if (mapping) nextLabel = this.caseWorkflowRelabelPathWithinRoot(oldLabel, mapping.oldRootLabel, mapping.newRootLabel);
        }
        if (nextLabel && nextLabel !== oldLabel) {
          row[CASE_MANUAL_START_COL.PATH_LABEL] = nextLabel;
          changedCount += 1;
        }
        return row;
      });
      return { rows: nextRows, changed: changedCount > 0, changedCount };
    },

    async reconcileCaseRootChronologyAndPathLabels(options = {}) {
      if (this.caseWorkflowRootOrderRepairInFlight) {
        return this.caseWorkflowRootOrderRepairLastResult || { handled: true, success: false, reason: 'root-order-repair-already-running' };
      }
      const source = normalizeCell(options.source || 'case-root-chronology-reconcile');
      const mainMatrix = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('main') : [];
      const model = this.caseWorkflowBuildChronologicalRootBlocks(Array.isArray(mainMatrix) ? mainMatrix : []);
      if (model.blocks.length <= 1) {
        const result = { handled: true, success: true, changed: false, reason: 'zero-or-one-root', rootCount: model.blocks.length };
        this.caseWorkflowRootOrderRepairLastResult = result;
        return result;
      }
      if (!model.datesComplete) {
        const result = { handled: true, success: false, changed: false, reason: 'root-dates-incomplete', rootCount: model.blocks.length };
        this.caseWorkflowRootOrderRepairLastResult = result;
        return result;
      }

      const sortedBlocks = options.sort === false ? model.blocks.slice() : model.sortedBlocks;
      const orderChanged = options.sort === false ? false : model.orderChanged;
      const sequence = this.caseWorkflowAnalyzeRootLabelSequence(sortedBlocks.map(block => ({ pathLabel: block.pathLabel })));
      const shouldRelabel = Boolean(sequence.relabelSafe && !sequence.labelsChronologyPass);
      const rootMappings = shouldRelabel
        ? sortedBlocks.map((block, index) => ({
          originalIndex: block.originalIndex,
          rootKey: block.rootKey,
          oldRootLabel: block.pathLabel,
          newRootLabel: sequence.expectedLabels[index]
        }))
        : [];
      const labelMapResult = shouldRelabel
        ? this.caseWorkflowBuildPathLabelMapForRootBlocks(sortedBlocks, rootMappings)
        : { labelMap: {}, conflicts: [] };
      if (shouldRelabel && labelMapResult.conflicts.length) {
        const result = {
          handled: true,
          success: false,
          changed: false,
          reason: 'path-label-conflict-needs-manual-review',
          rootCount: model.blocks.length,
          sequence,
          conflicts: labelMapResult.conflicts
        };
        this.caseWorkflowRootOrderRepairLastResult = result;
        return result;
      }
      const labelMap = labelMapResult.labelMap;
      const labelsRemapped = Object.keys(labelMap).length > 0;
      if (!orderChanged && !labelsRemapped) {
        const result = {
          handled: true,
          success: true,
          changed: false,
          reason: sequence.labelsPresentComplete && !sequence.labelsChronologyPass ? 'root-label-sequence-needs-manual-review' : 'root-order-and-labels-current',
          rootCount: model.blocks.length,
          sequence
        };
        this.caseWorkflowRootOrderRepairLastResult = result;
        return result;
      }

      this.caseWorkflowRootOrderRepairInFlight = true;
      try {
        const mappingByOriginalIndex = new Map(rootMappings.map(mapping => [Number(mapping.originalIndex), mapping]));
        let pathLabelChangeCount = 0;
        let generatedNoteChangeCount = 0;
        const nextBlocks = sortedBlocks.map((block) => {
          const mapping = mappingByOriginalIndex.get(Number(block.originalIndex));
          const nextRows = (Array.isArray(block.rows) ? block.rows : []).map((sourceRow) => {
            const row = this.caseManualEnsureRowWidth(sourceRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceRow?.length || 0));
            const oldLabel = normalizeCell(row[CASE_MANUAL_START_COL.PATH_LABEL]);
            if (mapping && oldLabel) {
              const nextLabel = labelMap[oldLabel] || this.caseWorkflowRelabelPathWithinRoot(oldLabel, mapping.oldRootLabel, mapping.newRootLabel);
              if (nextLabel && nextLabel !== oldLabel) {
                row[CASE_MANUAL_START_COL.PATH_LABEL] = nextLabel;
                pathLabelChangeCount += 1;
              }
            }
            if (labelsRemapped) {
              const oldNotes = String(row[CASE_MANUAL_START_COL.SYSTEM_NOTES] == null ? '' : row[CASE_MANUAL_START_COL.SYSTEM_NOTES]);
              const nextNotes = this.caseWorkflowRelabelGeneratedSystemNote(oldNotes, labelMap);
              if (nextNotes !== oldNotes) {
                row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = nextNotes;
                generatedNoteChangeCount += 1;
              }
            }
            return row;
          });
          return { ...block, rows: nextRows };
        });
        const nextMainRows = [
          ...model.prefixRows.map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))),
          ...nextBlocks.flatMap(block => block.rows)
        ];
        const reasonParts = [];
        if (orderChanged) reasonParts.push(`reordered ${model.blocks.length} root flow${model.blocks.length === 1 ? '' : 's'} by UTC date`);
        if (labelsRemapped) reasonParts.push(`updated ${pathLabelChangeCount} root/descendant path label${pathLabelChangeCount === 1 ? '' : 's'}`);
        const reason = `Case Buddy ${reasonParts.join(' and ')}${generatedNoteChangeCount ? ` and refreshed ${generatedNoteChangeCount} generated note reference${generatedNoteChangeCount === 1 ? '' : 's'}` : ''}.`;
        await this.replaceCaseManualStartLogicalSheetMatrix('main', nextMainRows, reason, { autosaveDelayMs: 700 });

        let rawLabelChangeCount = 0;
        if (labelsRemapped) {
          const rawMatrix = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('raw') : [];
          if (Array.isArray(rawMatrix) && rawMatrix.some(row => Array.isArray(row) && row.some(value => normalizeCell(value)))) {
            const rawResult = this.caseWorkflowApplyRootRelabelToRawMatrix(rawMatrix, rootMappings, labelMap);
            if (rawResult.changed) {
              rawLabelChangeCount = rawResult.changedCount;
              await this.replaceCaseManualStartLogicalSheetMatrix(
                'raw',
                rawResult.rows,
                `Synchronized ${rawLabelChangeCount} Raw Data path label${rawLabelChangeCount === 1 ? '' : 's'} after chronological root relabeling.`,
                { autosaveDelayMs: 760 }
              );
            }
          }
        }

        this.caseInputFlowStoryLastBuiltRevision = -1;
        this.queueCaseSummaryRefresh?.(0);
        await this.$nextTick();
        if (this.caseInputFlowStoryVisible) this.refreshCaseInputFlowStory?.({ force: true });
        const result = {
          handled: true,
          success: true,
          changed: true,
          reason: 'root-order-and-labels-reconciled',
          source,
          rootCount: model.blocks.length,
          orderChanged,
          labelsRemapped,
          pathLabelChangeCount,
          generatedNoteChangeCount,
          rawLabelChangeCount,
          sequence,
          rootMappings
        };
        this.caseWorkflowRootOrderRepairLastResult = result;
        this.appendCaseWorkflowAutomationLog?.('Reconciled chronological root order and path-label families.', {
          source,
          rootCount: model.blocks.length,
          orderChanged,
          labelsRemapped,
          pathLabelChangeCount,
          rawLabelChangeCount
        });
        if (!options.silent) {
          this.showCaseToast?.(labelsRemapped
            ? 'Root flows were sorted by date and their path-label families were updated together.'
            : 'Root flows were sorted by UTC date.');
        }
        return result;
      } catch (error) {
        const result = {
          handled: true,
          success: false,
          changed: false,
          reason: 'root-order-repair-failed',
          source,
          message: error?.message || String(error)
        };
        this.caseWorkflowRootOrderRepairLastResult = result;
        console.warn('Case Buddy could not reconcile chronological root order and path labels.', error);
        return result;
      } finally {
        this.caseWorkflowRootOrderRepairInFlight = false;
      }
    },

    buildCaseIntakeRailLiveDataState(matrix = []) {
      const rows = normalizeCaseMatrix(Array.isArray(matrix) ? matrix : []);
      const roots = typeof this.caseWorkflowCollectRootTransactions === 'function'
        ? this.caseWorkflowCollectRootTransactions(rows, { allowNonRootLabels: true })
        : [];
      const rootStates = (Array.isArray(roots) ? roots : []).map((root) => {
        const startIndex = Math.max(0, Number(root?.rowIndex) || 0);
        let endIndex = rows.length;
        for (let rowIndex = startIndex + 1; rowIndex < rows.length; rowIndex += 1) {
          if (normalizeCell(rows[rowIndex]?.[CASE_MANUAL_START_COL.TX_HASH])) {
            endIndex = rowIndex;
            break;
          }
        }
        const entryRows = rows.slice(startIndex, endIndex);
        const firstEntryValue = (columnIndex) => {
          for (const row of entryRows) {
            const value = normalizeCell(row?.[columnIndex]);
            if (value) return value;
          }
          return '';
        };
        const chainId = firstEntryValue(CASE_MANUAL_START_COL.CHAIN_ID);
        const chainName = firstEntryValue(CASE_MANUAL_START_COL.CHAIN_NAME);
        const dateText = firstEntryValue(CASE_MANUAL_START_COL.DATE);
        const dateMs = this.caseIntakeRailParseUtcDateMs(dateText);
        const pathLabel = normalizeCell(root?.pathLabel || rows[startIndex]?.[CASE_MANUAL_START_COL.PATH_LABEL]);
        return {
          rowIndex: startIndex,
          hash: normalizeCell(root?.hash),
          pathLabel,
          chainId,
          chainName,
          dateText,
          dateMs,
          // A populated Chain Name (Column U) is sufficient to establish that the
          // network is known. Chain Id is accepted as equivalent existing evidence.
          hasNetwork: Boolean(chainName || chainId),
          hasDate: Number.isFinite(dateMs),
          hasMainLabel: Boolean(pathLabel)
        };
      });

      const rootCount = rootStates.length;
      const startingComplete = rootCount > 0;
      const networksComplete = startingComplete && rootStates.every(root => root.hasNetwork);
      const datesPresentComplete = startingComplete && rootStates.every(root => root.hasDate);
      let chronologyPass = datesPresentComplete;
      let outOfOrderCount = 0;
      if (datesPresentComplete) {
        for (let index = 1; index < rootStates.length; index += 1) {
          if (rootStates[index].dateMs < rootStates[index - 1].dateMs) {
            chronologyPass = false;
            outOfOrderCount += 1;
          }
        }
      }
      const rootLabelSequence = this.caseWorkflowAnalyzeRootLabelSequence(rootStates);
      const labelsPresentComplete = startingComplete && rootLabelSequence.labelsPresentComplete;
      const labelsComplete = Boolean(labelsPresentComplete && rootLabelSequence.labelsChronologyPass);
      const missingNetworkCount = rootStates.filter(root => !root.hasNetwork).length;
      const missingDateCount = rootStates.filter(root => !root.hasDate).length;
      const missingLabelCount = rootStates.filter(root => !root.hasMainLabel).length;
      const outOfSequenceLabelCount = Number(rootLabelSequence.outOfSequenceCount) || 0;
      const rootLabel = rootCount === 1 ? 'root transaction' : 'root transactions';
      const statusDetailByKey = {
        starting: startingComplete
          ? `${rootCount} ${rootLabel} detected in current Main Data.`
          : 'No root transactions are present in current Main Data.',
        networks: networksComplete
          ? `Network identity is present for all ${rootCount} ${rootLabel}.`
          : `${missingNetworkCount} of ${rootCount} ${rootLabel} ${missingNetworkCount === 1 ? 'has' : 'have'} neither Chain Name nor Chain Id.`,
        dates: missingDateCount > 0
          ? `${missingDateCount} of ${rootCount} ${rootLabel} need a valid Column E UTC date.`
          : (chronologyPass
            ? `All ${rootCount} ${rootLabel} have valid UTC dates and are in chronological order.`
            : `${outOfOrderCount} root-order transition${outOfOrderCount === 1 ? '' : 's'} are out of chronological order.`),
        labels: missingLabelCount > 0
          ? `${missingLabelCount} of ${rootCount} ${rootLabel} need a main path label.`
          : (!rootLabelSequence.labelsChronologyPass
            ? `${outOfSequenceLabelCount} root path label${outOfSequenceLabelCount === 1 ? '' : 's'} no longer match the chronological root order.`
            : `All ${rootCount} ${rootLabel} have coherent main path labels.`)
      };

      return {
        available: startingComplete,
        rootCount,
        startingComplete,
        networksComplete,
        datesPresentComplete,
        chronologyPass,
        datesComplete: Boolean(datesPresentComplete && chronologyPass),
        labelsPresentComplete,
        labelsComplete,
        labelsChronologyPass: Boolean(rootLabelSequence.labelsChronologyPass),
        rootLabelSequence,
        missingNetworkCount,
        missingDateCount,
        outOfOrderCount,
        missingLabelCount,
        outOfSequenceLabelCount,
        rootStates,
        statusDetailByKey
      };
    },

    resetCaseIntakePreparationForCase() {
      this.clearCaseIntakePreparationTimers();
      this.caseIntakePreparationAbortController = null;
      this.releaseCaseIntakePreparationScrollLock();
      const runId = Number(this.caseIntakePreparation?.runId) || 0;
      this.caseIntakePreparation = createCaseIntakePreparationState({ runId: runId + 1 });
    },

    clearCaseIntakeContinuationRefreshTimer() {
      const timer = this.caseIntakeContinuationRefreshTimer;
      if (timer) {
        if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timer);
        else clearTimeout(timer);
      }
      this.caseIntakeContinuationRefreshTimer = null;
    },

    resetCaseIntakeContinuationCheckForCase() {
      this.clearCaseIntakeContinuationRefreshTimer();
      const runId = Number(this.caseIntakeContinuationCheck?.runId) || 0;
      this.caseIntakeContinuationCheck = createCaseIntakeContinuationCheckState({ runId: runId + 1 });
      this.caseIntakeContinuationIssueStage = '';
      this.caseIntakeContinuationManualSolveLaunched = false;
      this.caseIntakeContinuationTool68Launched = false;
      this.caseIntakeContinuationSaplingPreparing = false;
    },

    caseIntakeContinuationError(message = 'The focused workflow check could not finish.', code = 'CASE_INTAKE_CONTINUATION_FAILED') {
      const error = new Error(message);
      error.code = code;
      return error;
    },

    caseIntakeContinuationAssertCurrent(runId, fileId, revision, startedAtMs = 0) {
      if (Number(this.caseIntakeContinuationCheck?.runId) !== Number(runId)) {
        throw this.caseIntakeContinuationError('The focused workflow check was superseded.', 'CASE_INTAKE_CONTINUATION_SUPERSEDED');
      }
      if (normalizeCell(this.currentProjectFileId || '') !== normalizeCell(fileId || '')) {
        throw this.caseIntakeContinuationError('The open case changed while the focused check was running. Run the check again in the current case.', 'CASE_INTAKE_CONTINUATION_CASE_CHANGED');
      }
      if ((Number(this.caseSupportState?.caseDataRevision) || 0) !== Number(revision)) {
        throw this.caseIntakeContinuationError('Main or Raw Data changed while the focused check was running. Run it again for current guidance.', 'CASE_INTAKE_CONTINUATION_REVISION_CHANGED');
      }
      if (Number(startedAtMs) > 0 && (Date.now() - Number(startedAtMs)) > CASE_INTAKE_CONTINUATION_CHECK_TIMEOUT_MS) {
        throw this.caseIntakeContinuationError('The focused Raw Data and graph check exceeded its safety limit. The case was not changed.', 'CASE_INTAKE_CONTINUATION_TIMEOUT');
      }
      return true;
    },

    async caseIntakeContinuationYield(runId, fileId, revision, startedAtMs = 0) {
      await this.$nextTick();
      await new Promise((resolve) => {
        const schedule = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
          ? window.setTimeout.bind(window)
          : setTimeout;
        schedule(resolve, 0);
      });
      this.caseIntakeContinuationAssertCurrent(runId, fileId, revision, startedAtMs);
    },

    buildCaseIntakeContinuationRawSetDescriptors(coverageRows = [], mainRows = [], rawRows = []) {
      const normalizedMainRows = normalizeCaseMatrix(Array.isArray(mainRows) ? mainRows : []);
      const normalizedRawRows = normalizeCaseMatrix(Array.isArray(rawRows) ? rawRows : []);
      const knownRootLabels = this.caseWorkflowCollectRootTransactions(normalizedMainRows)
        .map(root => root.pathLabel)
        .filter(Boolean);
      const rawRoots = this.caseWorkflowCollectRootTransactions(normalizedRawRows, {
        knownRootLabels,
        allowNonRootLabels: false
      }).sort((left, right) => Number(left.rowIndex) - Number(right.rowIndex));

      const rawDescriptors = rawRoots.map((root, position) => {
        const rootIndex = Number(root?.rowIndex);
        if (!Number.isInteger(rootIndex) || rootIndex < 0 || rootIndex >= normalizedRawRows.length) return null;
        const nextRootIndex = rawRoots[position + 1]
          ? Number(rawRoots[position + 1].rowIndex)
          : normalizedRawRows.length;
        const rows = normalizedRawRows.slice(rootIndex, Math.max(rootIndex + 1, nextRootIndex)).map(row => row.slice());
        if (!rows.length) return null;
        const rootRow = rows[0].slice();
        const hash = normalizeCell(rootRow?.[CASE_MANUAL_START_COL.TX_HASH]);
        const pathLabel = normalizeCell(rootRow?.[CASE_MANUAL_START_COL.PATH_LABEL]);
        const toAddress = normalizeCell(rootRow?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
        return {
          rootRowIndex: rootIndex,
          rootRowNumber: rootIndex + 1,
          rootRow,
          rows,
          hash,
          pathLabel,
          toAddress,
          hashKey: this.caseWorkflowRawToMainHashKey(hash),
          pathKey: this.caseWorkflowRootIdentityKey(pathLabel),
          toAddressKey: this.caseWorkflowRootAddressKey(toAddress)
        };
      }).filter(Boolean);
      const descriptorByRootIndex = new Map(rawDescriptors.map(descriptor => [descriptor.rootRowIndex, descriptor]));
      const coverageList = Array.isArray(coverageRows) ? coverageRows : [];
      const claimedDuplicateRootIndices = new Set();

      return coverageList.map((coverageRow) => {
        const explicitRootIndex = Number(coverageRow?.matchedRawRootRowIndex);
        const explicitDescriptor = Number.isInteger(explicitRootIndex)
          ? descriptorByRootIndex.get(explicitRootIndex) || null
          : null;
        const coverageHashKey = this.caseWorkflowRawToMainHashKey(
          coverageRow?.hash || coverageRow?.transactionHash || explicitDescriptor?.hash || ''
        );
        const coveragePathKey = this.caseWorkflowRootIdentityKey(coverageRow?.pathLabel || explicitDescriptor?.pathLabel || '');
        const coverageToAddressKey = this.caseWorkflowRootAddressKey(coverageRow?.toAddress || explicitDescriptor?.toAddress || '');
        const duplicateSensitive = coverageRow?.duplicateHash === true;

        const candidates = rawDescriptors
          .map((descriptor) => {
            if (!coverageHashKey || descriptor.hashKey !== coverageHashKey) return null;
            if (duplicateSensitive && claimedDuplicateRootIndices.has(descriptor.rootRowIndex)) return null;

            const targetMatches = !!coverageToAddressKey && descriptor.toAddressKey === coverageToAddressKey;
            const pathMatches = !!coveragePathKey && descriptor.pathKey === coveragePathKey;
            const targetConflicts = !!coverageToAddressKey && !!descriptor.toAddressKey && !targetMatches;
            if (targetConflicts) return null;
            if (duplicateSensitive && !targetMatches && !pathMatches) return null;

            let score = 0;
            if (targetMatches) score += 1000;
            if (pathMatches) score += 500;
            if (!coverageToAddressKey && pathMatches) score += 100;
            return { descriptor, score };
          })
          .filter(Boolean)
          .sort((left, right) => {
            // Raw Data is append-only, and Tool 68 prepares the bottom-most
            // matching set. Among equally specific matches, audit that same newest
            // set rather than allowing an older valid set to mask current work.
            return right.score - left.score
              || right.descriptor.rootRowIndex - left.descriptor.rootRowIndex;
          });

        const descriptor = candidates[0]?.descriptor || null;
        if (duplicateSensitive && descriptor) claimedDuplicateRootIndices.add(descriptor.rootRowIndex);
        return {
          coverageRow,
          descriptor,
          candidateDescriptors: descriptor ? [descriptor] : []
        };
      });
    },

    buildCaseIntakeContinuationMainRootDescriptors(mainRows = []) {
      const rows = normalizeCaseMatrix(Array.isArray(mainRows) ? mainRows : []);
      const roots = this.caseWorkflowCollectRootTransactions(rows)
        .sort((left, right) => Number(left?.rowIndex) - Number(right?.rowIndex));
      const descriptors = new Map();
      roots.forEach((root, position) => {
        const rootIndex = Number(root?.rowIndex);
        if (!Number.isInteger(rootIndex) || rootIndex < 0 || rootIndex >= rows.length) return;
        const nextRootIndex = roots[position + 1]
          ? Number(roots[position + 1].rowIndex)
          : rows.length;
        descriptors.set(rootIndex, {
          root,
          rootRowIndex: rootIndex,
          rootRowNumber: rootIndex + 1,
          rows: rows.slice(rootIndex, Math.max(rootIndex + 1, nextRootIndex)).map(row => row.slice())
        });
      });
      return descriptors;
    },

    caseIntakeContinuationTransactionKey(entry = {}) {
      const row = entry?.row || entry?.rows?.[0]?.row || [];
      return normalizeCell(
        entry?.hashKey || this.caseWorkflowRawToMainHashKey(entry?.hash || row?.[CASE_MANUAL_START_COL.TX_HASH] || '')
      );
    },

    toggleCaseIntakeContinuationIssues(stageKey = '') {
      const normalized = normalizeCell(stageKey);
      if (!['raw-data', 'sapling'].includes(normalized)) {
        this.caseIntakeContinuationIssueStage = '';
        return false;
      }
      this.caseIntakeContinuationIssueStage = this.caseIntakeContinuationIssueStage === normalized ? '' : normalized;
      return Boolean(this.caseIntakeContinuationIssueStage);
    },

    async runCaseIntakeContinuationCheck(options = {}) {
      const attributionOwnedCheck = Boolean(options.allowAttributionWorkflowRun && this.caseAttributionWorkflowRun?.active);
      const cleanupOwnedCheck = Boolean(options.allowCleanupWorkflowRun && this.caseCleanupWorkflowRun?.active);
      const guidedOwnedCheck = attributionOwnedCheck || cleanupOwnedCheck;
      const guidedIntakeDataReady = guidedOwnedCheck && this.caseAttributionWorkflowIntakeDataReady;
      if (!this.caseIntakeRailVisible || (!this.caseIntakeRailAllComplete && !guidedIntakeDataReady)) {
        if (!options.silent) this.showCaseToast?.('Finish the first five workflow steps before checking Raw Data and graph readiness.');
        return false;
      }
      if (this.caseIntakeContinuationCheck?.status === 'checking') return false;
      const conflictingOperation = attributionOwnedCheck
        ? this.caseAttributionWorkflowOtherAutomationActive
        : cleanupOwnedCheck
          ? this.caseCleanupWorkflowOtherAutomationActive
          : this.caseIntakeRailOtherAutomationActive;
      if (this.casePageHydrating || conflictingOperation || this.caseIntakeContinuationSaplingPreparing) {
        if (!options.silent) this.showCaseToast?.('Finish the current Case Buddy operation before checking the next workflow steps.');
        return false;
      }

      this.clearCaseIntakeContinuationRefreshTimer();
      const previousRunId = Number(this.caseIntakeContinuationCheck?.runId) || 0;
      const runId = previousRunId + 1;
      const fileId = normalizeCell(this.currentProjectFileId || '');
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const startedAtMs = Date.now();
      this.caseIntakeContinuationIssueStage = '';
      this.caseIntakeContinuationCheck = createCaseIntakeContinuationCheckState({
        runId,
        status: 'checking',
        checkedFileId: fileId,
        checkedRevision: revision,
        message: 'Checking Raw Data and graph readiness…',
        detail: 'Reading current root coverage, path endings, continuation links, and prepared Main Data.',
        startedAtMs
      });

      try {
        await this.caseIntakeContinuationYield(runId, fileId, revision, startedAtMs);
        const mainRows = normalizeCaseMatrix(
          typeof this.getMatrixForLogicalSheet === 'function'
            ? this.getMatrixForLogicalSheet('main')
            : []
        );
        const rawRows = normalizeCaseMatrix(
          typeof this.getMatrixForLogicalSheet === 'function'
            ? this.getMatrixForLogicalSheet('raw')
            : []
        );
        const coverage = this.buildCaseBuddyMainRawDataCoverage(mainRows, rawRows);
        const coverageRows = Array.isArray(coverage?.coverageRows) ? coverage.coverageRows : [];
        const coveredRows = coverageRows.filter(row => normalizeCell(row?.status).toLowerCase() === 'covered');
        const missingRows = coverageRows.filter(row => normalizeCell(row?.status).toLowerCase() !== 'covered');
        const rootCount = coverageRows.length;
        const descriptorRows = this.buildCaseIntakeContinuationRawSetDescriptors(coveredRows, mainRows, rawRows);
        const mainRootDescriptors = this.buildCaseIntakeContinuationMainRootDescriptors(mainRows);
        const rawAuditRows = [];
        const descriptorMissingRows = [];

        let rawCandidateAuditCount = 0;
        for (let index = 0; index < descriptorRows.length; index += 1) {
          const item = descriptorRows[index];
          const candidateMainRootIndex = Number(item?.coverageRow?.rowIndex);
          const mainRootIndex = Number.isInteger(candidateMainRootIndex)
            ? candidateMainRootIndex
            : Number(item?.coverageRow?.spreadsheetRowNumber) - 1;
          const mainDescriptor = Number.isInteger(mainRootIndex)
            ? mainRootDescriptors.get(mainRootIndex) || null
            : null;
          item.mainDescriptor = mainDescriptor;
          // Accepted report boundaries are investigator decisions stored in
          // Main Data. Raw Data remains preserved source material, so the focused
          // audit reads those Main decisions without copying them into Raw Data.
          const acceptedReportBoundaryEntries = this.caseWorkflowRawDataTool88AcceptedReportBoundaryEntries(
            mainDescriptor?.rows || []
          );
          const mainGraphForDirection = this.caseWorkflowRawDataTool88CollectGraphEntries(mainDescriptor?.rows || []);
          const mainDirection = (Array.isArray(mainGraphForDirection?.entries) ? mainGraphForDirection.entries : [])
            .map(entry => this.caseWorkflowRawDataTool88EntryDirection(entry))
            .find(Boolean) || '';
          const candidates = Array.isArray(item?.candidateDescriptors) && item.candidateDescriptors.length
            ? item.candidateDescriptors
            : (item?.descriptor?.rows?.length ? [item.descriptor] : []);
          if (!candidates.length) {
            descriptorMissingRows.push(item?.coverageRow || {});
          } else {
            let selectedResult = null;
            let firstResult = null;
            for (const descriptor of candidates) {
              if (!descriptor?.rows?.length) continue;
              this.caseIntakeContinuationAssertCurrent(runId, fileId, revision, startedAtMs);
              const audit = this.caseWorkflowRawDataTool88AuditGraphRows(descriptor.rows, {
                direction: mainDirection,
                acceptedReportBoundaryEntries,
              });
              rawCandidateAuditCount += 1;
              const result = {
                coverageRow: item.coverageRow,
                descriptor,
                audit,
                candidateCount: candidates.length
              };
              if (!firstResult) firstResult = result;
              if (audit?.isValid) {
                selectedResult = result;
                break;
              }
              if (rawCandidateAuditCount % 8 === 0) {
                await this.caseIntakeContinuationYield(runId, fileId, revision, startedAtMs);
              }
            }
            const chosenResult = selectedResult || firstResult;
            if (chosenResult) {
              item.descriptor = chosenResult.descriptor;
              rawAuditRows.push(chosenResult);
            } else {
              descriptorMissingRows.push(item?.coverageRow || {});
            }
          }
          if ((index + 1) % CASE_INTAKE_CONTINUATION_CHECK_YIELD_EVERY_ROOTS === 0 && index + 1 < descriptorRows.length) {
            await this.caseIntakeContinuationYield(runId, fileId, revision, startedAtMs);
          }
        }

        this.caseIntakeContinuationAssertCurrent(runId, fileId, revision, startedAtMs);
        const invalidRawRows = rawAuditRows.filter(item => !item.audit?.isValid);
        const rawBlockingIssueCount = invalidRawRows.reduce((sum, item) => sum + Math.max(0, Number(item.audit?.blockingIssueCount) || 0), 0)
          + descriptorMissingRows.length;
        const rawCoverageComplete = rootCount > 0 && missingRows.length === 0;
        const rawFlowComplete = rawCoverageComplete
          && descriptorRows.length === rootCount
          && descriptorMissingRows.length === 0
          && invalidRawRows.length === 0;
        const rawStageComplete = Boolean(rawCoverageComplete && rawFlowComplete);

        await this.caseIntakeContinuationYield(runId, fileId, revision, startedAtMs);
        this.caseIntakeContinuationAssertCurrent(runId, fileId, revision, startedAtMs);
        const mainGraph = this.caseWorkflowRawDataTool88CollectGraphEntries(mainRows);

        // A manually pasted or newer Raw Data set is not prepared merely because
        // its root row already looks complete. Compare the current Raw Data set
        // with the corresponding Main root block, but compare prepared transaction
        // identities rather than raw row multiplicity. Within one root flow, a
        // transaction is identified by its hash inside that root; Tool 68 intentionally
        // combines converging allocation copies into one Main entry.
        const missingMainEntries = [];
        let rawEntryCount = 0;
        let missingMainEntryCount = 0;
        rawAuditRows.forEach((item) => {
          if (!item?.descriptor?.rows?.length) return;
          const rawGraph = this.caseWorkflowRawDataTool88CollectGraphEntries(item.descriptor.rows);
          const rawEntriesByKey = new Map();
          (Array.isArray(rawGraph?.entries) ? rawGraph.entries : []).forEach((entry) => {
            const key = this.caseIntakeContinuationTransactionKey(entry);
            if (key && !rawEntriesByKey.has(key)) rawEntriesByKey.set(key, entry);
          });

          const candidateMainRootIndex = Number(item?.coverageRow?.rowIndex);
          const mainRootIndex = Number.isInteger(candidateMainRootIndex)
            ? candidateMainRootIndex
            : Number(item?.coverageRow?.spreadsheetRowNumber) - 1;
          const mainDescriptor = item?.mainDescriptor || (Number.isInteger(mainRootIndex)
            ? mainRootDescriptors.get(mainRootIndex) || null
            : null);
          const mainGraph = this.caseWorkflowRawDataTool88CollectGraphEntries(mainDescriptor?.rows || []);
          const mainEntryKeys = new Set();
          (Array.isArray(mainGraph?.entries) ? mainGraph.entries : []).forEach((entry) => {
            const key = this.caseIntakeContinuationTransactionKey(entry);
            if (key) mainEntryKeys.add(key);
          });

          rawEntryCount += rawEntriesByKey.size;
          rawEntriesByKey.forEach((entry, key) => {
            if (mainEntryKeys.has(key)) return;
            missingMainEntryCount += 1;
            if (missingMainEntries.length < 8) {
              missingMainEntries.push({
                pathLabel: normalizeCell(item?.coverageRow?.pathLabel || item?.descriptor?.pathLabel || ''),
                hash: normalizeCell(entry?.hash || entry?.row?.[CASE_MANUAL_START_COL.TX_HASH] || '')
              });
            }
          });
        });

        const preparationSummary = this.getCaseWorkflowRawToMainPreparationSummary();
        if (preparationSummary?.error) throw preparationSummary.error;
        const preparationRows = Array.isArray(preparationSummary?.preparedRows) ? preparationSummary.preparedRows : [];
        const preparedRootRows = preparationRows.filter(row => row?.prepared);
        const preparedRootCount = Math.min(rootCount, preparedRootRows.length);
        const unpreparedRootCount = Math.max(0, rootCount - preparedRootCount);

        // Raw graph rules are deliberately not applied to the whole Main sheet.
        // Prepared Main Data may repeat one on-chain hash under different root paths,
        // and Tool 68 may consolidate converging Raw allocations into one Main entry.
        // Those are valid prepared-case structures, but a Raw Data direct-child audit
        // interprets them as missing or extra branches. Stage 7 instead uses the same
        // current-row preparation summary as the main workflow, plus the root-specific
        // Raw transaction representation check above.
        const mainBlockingIssueCount = missingMainEntryCount + unpreparedRootCount;
        const mainFlowComplete = Boolean(
          rootCount > 0
          && rawEntryCount >= rootCount
          && missingMainEntryCount === 0
          && preparedRootCount === rootCount
          && preparationSummary?.isComplete
        );
        const saplingStageComplete = Boolean(rawStageComplete && mainFlowComplete);

        const rawIssueExamples = [];
        missingRows.slice(0, 6).forEach((row) => {
          rawIssueExamples.push(`Raw coverage: Path ${normalizeCell(row?.pathLabel) || '—'} still needs a matching raw set.`);
        });
        descriptorMissingRows.slice(0, 6).forEach((row) => {
          if (rawIssueExamples.length >= 12) return;
          rawIssueExamples.push(`Raw set: Path ${normalizeCell(row?.pathLabel) || '—'} could not be isolated for review.`);
        });
        invalidRawRows.forEach((item) => {
          const path = normalizeCell(item?.coverageRow?.pathLabel || item?.descriptor?.pathLabel) || '—';
          (Array.isArray(item?.audit?.examples) ? item.audit.examples : []).forEach((issue) => {
            if (rawIssueExamples.length >= 12) return;
            const rowNumber = Number(issue?.spreadsheetRowNumber) > 0 ? `, row ${issue.spreadsheetRowNumber}` : '';
            rawIssueExamples.push(`Raw Data path ${path}${rowNumber}: ${normalizeCell(issue?.message) || normalizeCell(issue?.code) || 'Raw flow issue.'}`);
          });
        });

        const mainIssueExamples = [];
        missingMainEntries.forEach((item) => {
          if (mainIssueExamples.length >= 12) return;
          mainIssueExamples.push(`Main Data path ${normalizeCell(item?.pathLabel) || '—'} does not yet represent Raw Data transaction ${normalizeCell(item?.hash) || '—'}.`);
        });
        (Array.isArray(preparationSummary?.unpreparedRows) ? preparationSummary.unpreparedRows : []).forEach((row) => {
          if (mainIssueExamples.length >= 12) return;
          const reasons = [];
          if (!row?.hasRootDetails) reasons.push('the root row is missing one or more required transaction, funds-followed, or Column Q values');
          if (!row?.hasTerminalOrContinuation) reasons.push('the root has neither a prepared child nor a reviewed continuation/path-ending state');
          mainIssueExamples.push(`Main Data path ${normalizeCell(row?.pathLabel) || '—'}: ${reasons.join('; ') || 'raw-to-main preparation is not current'}.`);
        });

        const rawIssueCount = missingRows.length + rawBlockingIssueCount;
        const mainIssueCount = mainBlockingIssueCount;
        const issueExamples = [...rawIssueExamples, ...mainIssueExamples].slice(0, 16);

        let message = '';
        let detail = '';
        if (!rawStageComplete) {
          message = 'Raw-data gathering still needs work.';
          if (missingRows.length > 0) {
            detail = `${coveredRows.length}/${rootCount} starting root${rootCount === 1 ? '' : 's'} have distinct Raw Data coverage. Open Manual Solve to gather the remaining flow${missingRows.length === 1 ? '' : 's'}.`;
          } else {
            const unresolvedRawRootCount = invalidRawRows.length || descriptorMissingRows.length || 1;
            detail = `${unresolvedRawRootCount} raw root flow${unresolvedRawRootCount === 1 ? '' : 's'} still ${unresolvedRawRootCount === 1 ? 'has' : 'have'} unresolved links or path endings without a meaningful story. Open Manual Solve to finish ${unresolvedRawRootCount === 1 ? 'it' : 'them'}.`;
          }
        } else if (!saplingStageComplete) {
          message = 'Raw flows are complete; the graph still needs preparation.';
          if (missingMainEntryCount > 0) {
            detail = `${missingMainEntryCount} Raw Data transaction${missingMainEntryCount === 1 ? ' is' : 's are'} not represented in the corresponding Main flow${missingMainEntryCount === 1 ? '' : 's'}. Select Prepare Graph to process the current raw-backed flow${missingMainEntryCount === 1 ? '' : 's'}.`;
          } else if (unpreparedRootCount > 0) {
            detail = `${preparedRootCount}/${rootCount} raw-backed starting root${rootCount === 1 ? '' : 's'} appear prepared in Main Data. Select Prepare graph to process the remainder.`;
          } else {
            detail = 'The focused check found a graph-readiness finding that is not safe to auto-prepare. Review the finding before changing Main Data.';
          }
        } else {
          message = 'Raw Data and the prepared graph are current.';
          detail = `${rootCount}/${rootCount} starting root${rootCount === 1 ? '' : 's'} have complete Raw Data stories and prepared Main Data flows.`;
        }

        this.caseIntakeContinuationAssertCurrent(runId, fileId, revision, startedAtMs);
        if (rawStageComplete) {
          this.markCaseWorkflowRawDataCoverageCurrent?.('workflow-assistant-raw-flow-check', { persist: false });
        }
        if (saplingStageComplete) {
          this.recordCaseWorkflowRawToMainPreparationComplete?.({
            source: 'workflow-assistant-raw-sapling-check',
            summary: {
              ...(preparationSummary || {}),
              preparedCount: preparedRootCount,
              totalRows: rootCount,
              missingCount: 0,
              rawCoverageComplete: true,
              rawFlowUnresolved: false,
              isComplete: true
            }
          });
        } else if (rawStageComplete && typeof this.queueCaseSupportStatePersist === 'function') {
          this.queueCaseSupportStatePersist(120);
        }
        if (Number(this.caseIntakeContinuationCheck?.runId) !== Number(runId)) {
          throw this.caseIntakeContinuationError('The focused workflow check was superseded.', 'CASE_INTAKE_CONTINUATION_SUPERSEDED');
        }
        if (normalizeCell(this.currentProjectFileId || '') !== fileId) {
          throw this.caseIntakeContinuationError('The open case changed while the focused check was finishing.', 'CASE_INTAKE_CONTINUATION_CASE_CHANGED');
        }
        // Workflow acknowledgements may update case metadata synchronously. They do
        // not change the spreadsheet rows just audited, so anchor the snapshot to
        // the resulting revision instead of making the check immediately stale.
        const checkedRevision = Number(this.caseSupportState?.caseDataRevision) || revision;

        this.caseIntakeContinuationCheck = createCaseIntakeContinuationCheckState({
          runId,
          status: 'checked',
          checkedAtUtc: new Date().toISOString(),
          checkedRevision,
          checkedFileId: fileId,
          rootCount,
          coveredRootCount: coveredRows.length,
          missingRawRootCount: missingRows.length,
          checkedRawRootCount: rawAuditRows.length,
          invalidRawRootCount: invalidRawRows.length + descriptorMissingRows.length,
          rawBlockingIssueCount,
          rawCoverageComplete,
          rawFlowComplete,
          rawStageComplete,
          preparedRootCount,
          unpreparedRootCount,
          mainEntryCount: Number(mainGraph?.entries?.length) || 0,
          mainBlockingIssueCount,
          rawEntryCount,
          missingMainEntryCount,
          rawIssueCount,
          mainIssueCount,
          rawIssueExamples,
          mainIssueExamples,
          mainFlowComplete,
          saplingStageComplete,
          message,
          detail,
          issueExamples
        });
        this.queueCaseSummaryRefresh?.(80);
        if (!options.silent) this.showCaseToast?.(message);
        return this.caseIntakeContinuationCheck;
      } catch (error) {
        if (Number(this.caseIntakeContinuationCheck?.runId) !== Number(runId)) return false;
        const retryAttempt = Math.max(0, Number(options.attempt) || 0);
        if (error?.code === 'CASE_INTAKE_CONTINUATION_REVISION_CHANGED' && options.retryOnRevisionChange === true && retryAttempt < 6) {
          this.caseIntakeContinuationCheck = createCaseIntakeContinuationCheckState({
            runId,
            status: 'idle',
            message: 'Waiting for the latest case changes to settle…'
          });
          this.scheduleCaseIntakeContinuationCheck(520, {
            ...options,
            attempt: retryAttempt + 1
          });
          return false;
        }
        const message = error?.message || 'The focused Raw Data and graph check could not finish.';
        this.caseIntakeContinuationCheck = createCaseIntakeContinuationCheckState({
          runId,
          status: 'error',
          checkedAtUtc: new Date().toISOString(),
          checkedRevision: revision,
          checkedFileId: fileId,
          message: 'Focused workflow check needs retry.',
          detail: message,
          error: message
        });
        if (!options.silent) this.showCaseToast?.(message);
        return false;
      }
    },

    scheduleCaseIntakeContinuationCheck(delayMs = 260, options = {}) {
      this.clearCaseIntakeContinuationRefreshTimer();
      const attempt = Math.max(0, Number(options.attempt) || 0);
      const run = async () => {
        this.caseIntakeContinuationRefreshTimer = null;
        if (!this.caseIntakeRailVisible || !this.caseIntakeRailAllComplete) return;
        if (this.casePageHydrating || this.caseIntakeRailOtherAutomationActive || this.caseIntakeContinuationSaplingPreparing) {
          if (attempt < 8) {
            this.scheduleCaseIntakeContinuationCheck(480, { ...options, attempt: attempt + 1 });
          }
          return;
        }
        await this.runCaseIntakeContinuationCheck({
          ...options,
          silent: options.silent !== false
        });
      };
      const schedule = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
        ? window.setTimeout.bind(window)
        : setTimeout;
      this.caseIntakeContinuationRefreshTimer = schedule(run, Math.max(0, Number(delayMs) || 0));
    },

    async openCaseIntakeContinuationManualSolve() {
      if (this.caseIntakeRailOtherAutomationActive || this.casePageHydrating) {
        this.showCaseToast?.('Finish the current Case Buddy operation before opening Manual Solve.');
        return false;
      }
      this.caseIntakeContinuationManualSolveLaunched = true;
      try {
        await this.openCaseManualStartVisualizerFromWorkflow();
        await this.$nextTick();
        if (!this.showCaseInvestigationVisualizerModal) {
          this.caseIntakeContinuationManualSolveLaunched = false;
          return false;
        }
        return true;
      } catch (error) {
        this.caseIntakeContinuationManualSolveLaunched = false;
        this.showCaseToast?.(error?.message || 'Manual Solve could not be opened.');
        return false;
      }
    },

    async openCaseIntakeContinuationSaplingPreparation() {
      if (this.caseIntakeRailOtherAutomationActive || this.casePageHydrating || this.caseIntakeContinuationSaplingPreparing) {
        this.showCaseToast?.('Finish the current Case Buddy operation before preparing the graph.');
        return false;
      }
      this.caseIntakeContinuationTool68Launched = true;
      this.caseIntakeContinuationSaplingPreparing = true;
      try {
        const result = await this.runCaseWorkflowRawToMainPreparationAuto({
          source: 'workflow-assistant-raw-to-main',
          step: this.getCaseWorkflowRawToMainPreparationStep(),
          // The focused rail check is intentionally stricter than the legacy root-
          // details summary. Do not let that older summary short-circuit a needed
          // current-raw-set reprocess. Existing callers keep their normal behavior.
          forceReprocess: true
        });
        if (result?.success) {
          this.showCaseToast?.('Graph preparation completed. The Workflow Assistant is checking the result.');
        } else if (result?.status === 'needs-review' || this.showTool68PreparationModal) {
          this.showCaseToast?.('Graph preparation needs review. Complete or close the Graph preparation panel when ready.');
        } else if (result?.status === 'raw-coverage-incomplete') {
          this.showCaseToast?.('Raw Data changed and is no longer complete. Check the next steps again.');
        } else {
          this.showCaseToast?.('Graph preparation could not finish automatically. Review the Graph preparation state.');
        }
        return result;
      } catch (error) {
        this.showCaseToast?.(error?.message || 'Graph preparation could not finish.');
        return false;
      } finally {
        this.caseIntakeContinuationSaplingPreparing = false;
        if (!this.showTool68PreparationModal) {
          this.caseIntakeContinuationTool68Launched = false;
          this.scheduleCaseIntakeContinuationCheck(520, {
            source: 'workflow-assistant-sapling-finished',
            silent: true,
            retryOnRevisionChange: true,
            attempt: 0
          });
        }
      }
    },

    async runCaseIntakeContinuationPrimaryAction() {
      if (this.caseIntakeContinuationActionDisabled) return false;
      switch (this.caseIntakeContinuationAction) {
        case 'check':
          return this.runCaseIntakeContinuationCheck({ source: 'workflow-assistant-primary-check' });
        case 'manual-solve':
          return this.openCaseIntakeContinuationManualSolve();
        case 'prepare-sapling':
          return this.openCaseIntakeContinuationSaplingPreparation();
        case 'review-sapling':
          return this.toggleCaseIntakeContinuationIssues('sapling');
        default:
          return false;
      }
    },



    caseGuidedWorkflowStepMatchesDefinition(step = {}, definition = {}) {
      const tag = this.caseWorkflowStepIdentity(step);
      const title = this.caseWorkflowStepTitleText(step);
      if (tag && tag === normalizeCell(definition?.tag).toUpperCase()) return true;
      switch (normalizeCell(definition?.tag).toUpperCase()) {
        case 'ADDRESS_ATTRIBUTION_FILL':
          return /fill(?:ing)?\s+(?:missing\s+)?address\s+attribution|address[_\s-]*attribution[_\s-]*fill/.test(title);
        case 'ATTRIBUTION_PATH_OPERATIONS':
          return /review\s+attributed\s+rows|path\s+operations|prun(?:e|ing).*graft|graft.*bridge/.test(title);
        case 'ADDRESS_COLOR_LIKE_CELLS':
          return /color(?:ing)?\s+matching\s+from\/?to\s+addresses|color\s+like\s+cells/.test(title);
        case 'ENTITY_REVIEW':
          return /review\s+new\s+entities|entity\s+review|curator/.test(title);
        case 'EXTERNAL_ATTRIBUTION_REVIEW':
          return /external\s+attribution/.test(title);
        case 'WWW_USD_REFERENCES':
          return /top[-\s]*level\s+usd\s+reference|column\s+h.*(?:usd|usdt|valuation|reference)/.test(title);
        case 'CASE_INCONSISTENCIES':
          return /review.*case\s+inconsistenc|case\s+inconsistenc/.test(title);
        case 'NORMALIZE_DEPOSIT_NOTES':
          return /normalize.*deposit|hot[-\s]*wallet|unknown[-\s]*service/.test(title);
        case 'HIGH_PRIORITY_DEPOSIT_PATTERNS':
          return /high[-\s]*priority.*deposit.*pattern|deposit\s+pattern/.test(title);
        case 'KNOWN_ENTITY_NOTE_CASING':
          return /known[-\s]*entity.*(?:note\s+)?casing|note\s+casing/.test(title);
        case 'DUPLICATE_HASH_LABELS':
          return /label.*duplicate.*(?:transaction\s+)?hash|duplicate\s+(?:transaction\s+)?hash/.test(title);
        case 'NOTES_BACKUP_AND_HASH_REPLACEMENT':
          return /back\s*up\s+notes|replace\s+hashes\s+with\s+path\s+labels|notes.*hash/.test(title);
        case 'ADDRESS_LEGEND':
          return /build.*address\s+legend|address\s+legend/.test(title);
        default:
          return false;
      }
    },

    caseAttributionWorkflowStepMatchesDefinition(step = {}, definition = {}) {
      return this.caseGuidedWorkflowStepMatchesDefinition(step, definition);
    },

    caseCleanupWorkflowStepMatchesDefinition(step = {}, definition = {}) {
      return this.caseGuidedWorkflowStepMatchesDefinition(step, definition);
    },

    caseAttributionWorkflowDefinitionForStep(step = {}) {
      return CASE_ATTRIBUTION_WORKFLOW_STEP_DEFINITIONS.find(definition => this.caseAttributionWorkflowStepMatchesDefinition(step, definition)) || null;
    },

    resetCaseAttributionWorkflowRunForCase() {
      this.clearCaseAttributionWorkflowTimers();
      this.caseAttributionWorkflowAbortController = null;
      this.releaseCaseAttributionWorkflowScrollLock();
      const runId = Number(this.caseAttributionWorkflowRun?.runId) || 0;
      this.caseAttributionWorkflowRun = createCaseAttributionWorkflowRunState({ runId: runId + 1 });
    },

    clearCaseAttributionWorkflowTimers() {
      const state = this.caseAttributionWorkflowRun || {};
      const clear = (timer) => {
        if (!timer) return;
        if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timer);
        else clearTimeout(timer);
      };
      clear(state.overallTimer);
      clear(state.actionTimer);
      if (this.caseAttributionWorkflowRun) {
        this.caseAttributionWorkflowRun.overallTimer = null;
        this.caseAttributionWorkflowRun.actionTimer = null;
      }
    },

    applyCaseAttributionWorkflowScrollLock() {
      if (typeof document === 'undefined' || !document.body) return;
      if (this.caseAttributionWorkflowBodyOverflow === null) {
        this.caseAttributionWorkflowBodyOverflow = document.body.style.overflow || '';
      }
      document.body.style.overflow = 'hidden';
    },

    releaseCaseAttributionWorkflowScrollLock() {
      if (typeof document === 'undefined' || !document.body) {
        this.caseAttributionWorkflowBodyOverflow = null;
        return;
      }
      if (this.caseAttributionWorkflowBodyOverflow !== null) {
        document.body.style.overflow = this.caseAttributionWorkflowBodyOverflow;
      }
      this.caseAttributionWorkflowBodyOverflow = null;
    },

    caseAttributionWorkflowError(message = 'The guided attribution workflow stopped.', code = 'CASE_ATTRIBUTION_WORKFLOW_FAILED') {
      const error = new Error(message);
      error.code = code;
      return error;
    },

    caseAttributionWorkflowAssertCurrent(runId) {
      const state = this.caseAttributionWorkflowRun || {};
      const controller = this.caseAttributionWorkflowAbortController;
      if (Number(state.runId) !== Number(runId) || state.stopRequested || controller?.signal?.aborted) {
        throw this.caseAttributionWorkflowError('The guided attribution workflow was stopped.', 'CASE_ATTRIBUTION_WORKFLOW_STOPPED');
      }
      return true;
    },

    async caseAttributionWorkflowSettle(runId, delayMs = 80) {
      this.caseAttributionWorkflowAssertCurrent(runId);
      await this.$nextTick();
      if (delayMs > 0) {
        await new Promise((resolve, reject) => {
          const controller = this.caseAttributionWorkflowAbortController;
          let timer = null;
          const onAbort = () => {
            if (timer) {
              if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timer);
              else clearTimeout(timer);
            }
            reject(this.caseAttributionWorkflowError('The guided attribution workflow was stopped.', 'CASE_ATTRIBUTION_WORKFLOW_STOPPED'));
          };
          if (controller?.signal?.aborted) return onAbort();
          controller?.signal?.addEventListener?.('abort', onAbort, { once: true });
          const finish = () => {
            controller?.signal?.removeEventListener?.('abort', onAbort);
            resolve();
          };
          timer = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
            ? window.setTimeout(finish, delayMs)
            : setTimeout(finish, delayMs);
        });
      }
      this.caseAttributionWorkflowAssertCurrent(runId);
    },

    async runCaseAttributionWorkflowOperation(runId, label, operation, timeoutMs = CASE_ATTRIBUTION_WORKFLOW_ACTION_TIMEOUT_MS) {
      this.caseAttributionWorkflowAssertCurrent(runId);
      const controller = this.caseAttributionWorkflowAbortController;
      let timeoutTimer = null;
      let abortHandler = null;
      const operationPromise = Promise.resolve().then(operation);
      operationPromise.catch(() => {});
      const timeoutPromise = new Promise((resolve, reject) => {
        const timeout = Math.max(1000, Number(timeoutMs) || CASE_ATTRIBUTION_WORKFLOW_ACTION_TIMEOUT_MS);
        const onTimeout = () => {
          const error = this.caseAttributionWorkflowError(`${label} did not finish within the safety limit.`, 'CASE_ATTRIBUTION_WORKFLOW_TIMEOUT');
          reject(error);
          try { controller?.abort?.(error); } catch { /* Best-effort cancellation. */ }
          this.stopCaseAttributionWorkflowOwnedAutomation?.(`${label} reached its safety limit.`);
        };
        timeoutTimer = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
          ? window.setTimeout(onTimeout, timeout)
          : setTimeout(onTimeout, timeout);
        if (this.caseAttributionWorkflowRun) this.caseAttributionWorkflowRun.actionTimer = timeoutTimer;
      });
      const abortPromise = new Promise((resolve, reject) => {
        abortHandler = () => reject(this.caseAttributionWorkflowError('The guided attribution workflow was stopped.', 'CASE_ATTRIBUTION_WORKFLOW_STOPPED'));
        if (controller?.signal?.aborted) abortHandler();
        else controller?.signal?.addEventListener?.('abort', abortHandler, { once: true });
      });
      try {
        const result = await Promise.race([operationPromise, timeoutPromise, abortPromise]);
        this.caseAttributionWorkflowAssertCurrent(runId);
        return result;
      } finally {
        if (timeoutTimer) {
          if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timeoutTimer);
          else clearTimeout(timeoutTimer);
        }
        controller?.signal?.removeEventListener?.('abort', abortHandler);
        if (this.caseAttributionWorkflowRun && Number(this.caseAttributionWorkflowRun.runId) === Number(runId)) {
          this.caseAttributionWorkflowRun.actionTimer = null;
        }
      }
    },

    setCaseAttributionWorkflowStage(runId, definition = {}, message = '', detail = '') {
      this.caseAttributionWorkflowAssertCurrent(runId);
      const stageIndex = Math.max(0, CASE_ATTRIBUTION_WORKFLOW_STEP_DEFINITIONS.findIndex(item => item.tag === definition?.tag));
      this.caseAttributionWorkflowRun = {
        ...(this.caseAttributionWorkflowRun || createCaseAttributionWorkflowRunState()),
        status: 'running',
        stageKey: normalizeCell(definition?.key),
        stageTag: normalizeCell(definition?.tag).toUpperCase(),
        stageIndex: stageIndex + 1,
        stageTitle: normalizeCell(definition?.title),
        message: message || definition?.title || 'Working…',
        detail
      };
    },

    syncCaseAttributionWorkflowCompletedTags(runId) {
      this.caseAttributionWorkflowAssertCurrent(runId);
      const completedTags = (this.caseAttributionWorkflowStepRows || [])
        .filter(row => row.complete)
        .map(row => normalizeCell(row.tag).toUpperCase())
        .filter(Boolean);
      this.caseAttributionWorkflowRun = {
        ...(this.caseAttributionWorkflowRun || createCaseAttributionWorkflowRunState()),
        completedTags
      };
      return completedTags;
    },

    getCaseAttributionWorkflowRecordedStaleRows() {
      return (this.caseAttributionWorkflowStepRows || [])
        .filter(row => row?.step && row.refreshableStale);
    },

    async waitForCaseAttributionWorkflowChecksIdle(runId, maxMs = CASE_ATTRIBUTION_WORKFLOW_ACTION_TIMEOUT_MS) {
      const startedAtMs = Date.now();
      let quietSinceMs = 0;
      while ((Date.now() - startedAtMs) < Math.max(1000, Number(maxMs) || CASE_ATTRIBUTION_WORKFLOW_ACTION_TIMEOUT_MS)) {
        this.caseAttributionWorkflowAssertCurrent(runId);
        if (this.caseChecksRunning) {
          quietSinceMs = 0;
        } else if (!quietSinceMs) {
          quietSinceMs = Date.now();
        } else if ((Date.now() - quietSinceMs) >= 180) {
          return true;
        }
        await this.caseAttributionWorkflowSettle(runId, 60);
      }
      throw this.caseAttributionWorkflowError(
        'Workflow checks did not finish within the guided workflow safety limit.',
        'CASE_ATTRIBUTION_WORKFLOW_TIMEOUT'
      );
    },

    async refreshCaseAttributionWorkflowRecordedStaleSteps(runId) {
      this.caseAttributionWorkflowAssertCurrent(runId);
      const staleRows = this.getCaseAttributionWorkflowRecordedStaleRows();
      if (!staleRows.length) {
        return { refreshed: false, remaining: 0, beforeSignature: '', afterSignature: '' };
      }

      const target = staleRows[0];
      this.setCaseAttributionWorkflowStage(
        runId,
        target,
        `Refreshing ${target.shortLabel || target.title}…`,
        'This stage is already recorded complete, but its check snapshot became stale after later case changes. Case Buddy is running the same workflow checks as the yellow Run checks control before deciding whether more work is needed.'
      );
      const beforeSignature = this.caseAttributionWorkflowCompletionSignature();
      await this.runCaseAttributionWorkflowOperation(
        runId,
        `${target.title} status refresh`,
        async () => {
          const result = await this.runWorkflowChecksFromStaleBadge(target.step);
          await this.waitForCaseAttributionWorkflowChecksIdle(runId);
          return result;
        },
        CASE_ATTRIBUTION_WORKFLOW_ACTION_TIMEOUT_MS
      );
      await this.caseAttributionWorkflowSettle(runId, 120);
      this.syncCaseAttributionWorkflowCompletedTags(runId);
      const remainingRows = this.getCaseAttributionWorkflowRecordedStaleRows();
      return {
        refreshed: true,
        remaining: remainingRows.length,
        beforeSignature,
        afterSignature: this.caseAttributionWorkflowCompletionSignature()
      };
    },

    caseAttributionWorkflowCompletionSignature() {
      const rows = this.caseAttributionWorkflowStepRows || [];
      const recommendedTag = this.caseWorkflowStepIdentity(this.recommendedWorkflowStep || {});
      const pauseTag = this.caseWorkflowStepIdentity(this.caseAutomation?.manualPause || {});
      return [
        rows.map(row => `${row.tag}:${row.complete ? 1 : 0}:${row.stale ? 1 : 0}`).join('|'),
        recommendedTag,
        pauseTag,
        Number(this.caseSupportState?.caseDataRevision) || 0
      ].join('::');
    },

    getCaseAttributionWorkflowManualHandoff() {
      const pause = this.caseAutomation?.manualPause || null;
      if (this.caseAutomation?.paused || pause || this.showCaseAutomationPauseModal) {
        return {
          needed: true,
          title: normalizeCell(pause?.stepTitle || pause?.title || this.recommendedWorkflowStep?.title || 'Workflow review'),
          message: normalizeCell(pause?.message || pause?.detail || 'Complete the open review, then run the Workflow Assistant action again.')
        };
      }
      const modalRows = [
        [this.showAddressAttributionModal, 'Address attribution review', 'Review or complete the open address-attribution panel, then continue from the Workflow Assistant.'],
        [this.showPathOperationModal, 'Path-operation review', 'Resolve the open prune, graft, or Bridge decision, then continue from the Workflow Assistant.'],
        [this.showEntityCuratorModal, 'Entity and curator review', 'Complete the open entity/curator review, then continue from the Workflow Assistant.'],
        [this.showWwwUsdReferenceModal, 'Top-level USD review', 'Review the open top-level USD reference values, then continue from the Workflow Assistant.']
      ];
      const modal = modalRows.find(row => Boolean(row[0]));
      return modal ? { needed: true, title: modal[1], message: modal[2] } : { needed: false, title: '', message: '' };
    },

    stopCaseAttributionWorkflowOwnedAutomation(reason = 'The scoped attribution workflow is stopping.') {
      this.cancelCaseWorkflowBackgroundAutomationResume?.({ source: 'case-attribution-workflow-stop' });
      this.clearCaseWorkflowScheduledScrollTimers?.();
      this.clearCaseAutomationTimer?.();
      this.clearCaseWorkflowStatefulGeneratedReviewAutoResumeTimer?.();
      this.clearCaseWorkflowWwwUsdReferenceAutoResumeTimer?.();
      if (this.caseAutomation?.active || this.caseAutomation?.busy) {
        this.requestCaseAutomationUserStop?.(reason);
      }
    },

    async stopCaseAttributionWorkflowConflictingAutomations() {
      if (this.caseIntakePreparation?.active) {
        this.requestCaseIntakePreparationStop?.('Intake preparation stopped so the guided attribution workflow can start.', { silent: true });
      }
      if (this.caseManualStartViewportAutoActive && typeof this.stopCaseManualStartViewportAuto === 'function') {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto stopped so the guided attribution workflow can start.', { silent: true });
      }
      if (this.caseAutomation?.active || this.caseAutomation?.busy) {
        this.requestCaseAutomationUserStop?.('Workflow Auto stopped so the guided attribution workflow can start.');
      }
      if (this.caseManualStartHopInspectorRunning) this.requestCaseManualStartHopInspectorStop?.();
      if (this.caseHopSolverRunning) this.requestCaseHopSolverStop?.();
      const stopTool = (tool, reason) => {
        if (tool && typeof tool.caseBuddyRequestAutomationStop === 'function') {
          try { tool.caseBuddyRequestAutomationStop(reason); } catch (error) { console.warn('Could not stop a conflicting Case Buddy tool.', error); }
        }
      };
      stopTool(this.$refs?.caseHopSolverTool88, 'The guided attribution workflow is starting.');
      stopTool(this.$refs?.caseWorkflowRawDataTool88, 'The guided attribution workflow is starting.');
      stopTool(this.getCaseManualStartHopInspectorTool?.(), 'The guided attribution workflow is starting.');
      this.cancelCaseWorkflowBackgroundAutomationResume?.({ source: 'case-attribution-workflow-start' });
      this.clearCaseWorkflowScheduledScrollTimers?.();
      await this.$nextTick();
    },

    requestCaseAttributionWorkflowStop(message = 'Attribution workflow stopped by user.', options = {}) {
      const state = this.caseAttributionWorkflowRun || createCaseAttributionWorkflowRunState();
      if (!state.active && options.force !== true) return false;
      try {
        this.caseAttributionWorkflowAbortController?.abort?.(this.caseAttributionWorkflowError(message, 'CASE_ATTRIBUTION_WORKFLOW_STOPPED'));
      } catch {
        // Abort is best effort; the run-id guard still prevents another action.
      }
      this.stopCaseAttributionWorkflowOwnedAutomation(message);
      this.clearCaseAttributionWorkflowTimers();
      this.caseAttributionWorkflowAbortController = null;
      this.releaseCaseAttributionWorkflowScrollLock();
      const result = options.timeout ? 'timeout' : 'stopped';
      this.caseAttributionWorkflowRun = {
        ...state,
        active: false,
        stopRequested: true,
        runId: (Number(state.runId) || 0) + 1,
        status: result,
        lastResult: result,
        message,
        detail: 'Completed spreadsheet and workflow changes remain in the case. An operation already in flight may finish its own cleanup before its tool becomes available again.',
        lastMessage: message,
        finishedAtUtc: new Date().toISOString(),
        overallTimer: null,
        actionTimer: null
      };
      if (!options.silent) this.showCaseToast?.(message);
      return true;
    },

    finishCaseAttributionWorkflowRun(runId, result = 'success', message = '', detail = '') {
      if (Number(this.caseAttributionWorkflowRun?.runId) !== Number(runId)) return false;
      this.clearCaseAttributionWorkflowTimers();
      this.caseAttributionWorkflowAbortController = null;
      this.releaseCaseAttributionWorkflowScrollLock();
      this.caseAttributionWorkflowRun = {
        ...(this.caseAttributionWorkflowRun || createCaseAttributionWorkflowRunState()),
        active: false,
        stopRequested: false,
        status: result,
        lastResult: result,
        message,
        detail,
        lastMessage: message,
        finishedAtUtc: new Date().toISOString(),
        overallTimer: null,
        actionTimer: null
      };
      return true;
    },

    async reconcileCaseAttributionWorkflowCompletionAfterPrerequisiteCheck(runId, completeTagsBeforeCheck = []) {
      this.caseAttributionWorkflowAssertCurrent(runId);
      const completed = new Set((Array.isArray(completeTagsBeforeCheck) ? completeTagsBeforeCheck : []).map(tag => normalizeCell(tag).toUpperCase()).filter(Boolean));
      const reconcileRecordedTags = (source = 'case-attribution-workflow-final-reconcile') => {
        const currentRows = this.caseAttributionWorkflowStepRows || [];
        for (const row of currentRows) {
          if (!completed.has(row.tag) || row.complete || !row.step) continue;
          if (row.tag === 'ATTRIBUTION_PATH_OPERATIONS') {
            this.recordCaseIntakePreparationManualCompletion(row.step, source);
          } else if (row.tag === 'ADDRESS_COLOR_LIKE_CELLS') {
            this.recordCaseWorkflowAddressColorLikeCellsCurrent({ step: row.step, source });
          } else if (row.tag === 'ADDRESS_ATTRIBUTION_FILL') {
            this.reconcileCaseWorkflowAddressAttributionFillCompletionState?.({ source });
          } else if (row.tag === 'WWW_USD_REFERENCES') {
            this.reconcileCaseWorkflowWwwUsdReferenceCompletionState?.({ source });
          } else {
            this.recordCaseWorkflowAutoSoftStepComplete(row.step, { action: source }, {
              source,
              stepTitle: row.title
            });
          }
        }
      };

      reconcileRecordedTags('case-attribution-workflow-prerequisite-reconcile-initial');
      await this.caseAttributionWorkflowSettle(runId, 110);
      reconcileRecordedTags('case-attribution-workflow-prerequisite-reconcile-settled');
      this.reanchorCaseAttributionWorkflowPrerequisiteSnapshot();
      // Several workflow completion records persist on short timers. Let those
      // writes settle while the page is still locked, then re-read and re-anchor
      // both the six recorded stages and the focused Raw Data/sapling snapshot.
      // No user or tool can edit the spreadsheets during this interval.
      await this.caseAttributionWorkflowSettle(runId, 280);
      reconcileRecordedTags('case-attribution-workflow-prerequisite-reconcile-late');
      this.reanchorCaseAttributionWorkflowPrerequisiteSnapshot();
      await this.caseAttributionWorkflowSettle(runId, 140);
      reconcileRecordedTags('case-attribution-workflow-prerequisite-reconcile-final');
      this.reanchorCaseAttributionWorkflowPrerequisiteSnapshot();
      await this.caseAttributionWorkflowSettle(runId, 40);
    },

    reanchorCaseAttributionWorkflowPrerequisiteSnapshot() {
      const check = this.caseIntakeContinuationCheck || createCaseIntakeContinuationCheckState();
      const fileId = normalizeCell(this.currentProjectFileId || '');
      if (
        check.status !== 'checked'
        || !check.rawStageComplete
        || !check.saplingStageComplete
        || normalizeCell(check.checkedFileId || '') !== fileId
      ) {
        return false;
      }
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      if (Number(check.checkedRevision) === currentRevision) return true;
      this.caseIntakeContinuationCheck = {
        ...check,
        checkedRevision: currentRevision
      };
      return true;
    },

    async runCaseAttributionWorkflowPrerequisiteCheck(runId, options = {}) {
      this.caseAttributionWorkflowAssertCurrent(runId);
      const definition = { key: 'prerequisite', tag: '', title: 'Confirm Raw Data and prepared graph' };
      this.caseAttributionWorkflowRun = {
        ...(this.caseAttributionWorkflowRun || createCaseAttributionWorkflowRunState()),
        status: 'checking-prerequisites',
        stageKey: definition.key,
        stageTag: '',
        stageIndex: 0,
        stageTitle: definition.title,
        message: options.final
          ? 'Rechecking Raw Data and graph readiness after the guided workflow…'
          : 'Confirming Raw Data and graph readiness before attribution work…',
        detail: 'This is the existing focused check. It runs here only because you deliberately started a guided workflow action.'
      };
      const completeTagsBeforeCheck = (this.caseAttributionWorkflowStepRows || []).filter(row => row.complete).map(row => row.tag);
      const result = await this.runCaseAttributionWorkflowOperation(
        runId,
        'Raw Data and graph readiness check',
        () => this.runCaseIntakeContinuationCheck({
          source: options.final ? 'workflow-assistant-attribution-final-check' : 'workflow-assistant-attribution-preflight',
          silent: true,
          retryOnRevisionChange: false,
          allowAttributionWorkflowRun: true
        }),
        CASE_INTAKE_CONTINUATION_CHECK_TIMEOUT_MS + 5000
      );
      this.caseAttributionWorkflowAssertCurrent(runId);
      if (!result || this.caseIntakeContinuationAction !== 'ready') {
        throw this.caseAttributionWorkflowError(
          this.caseIntakeContinuationCheck?.detail || 'Raw Data or graph preparation is no longer current. Resolve that stage before continuing attribution work.',
          'CASE_ATTRIBUTION_WORKFLOW_PREREQUISITE'
        );
      }
      // The focused Raw Data/sapling check updates workflow metadata and can
      // advance the case revision even though it does not change the Main or Raw
      // spreadsheet rows just audited. Re-anchor any of these six stages that
      // were already current so a preflight check never makes completed work
      // appear stale or causes it to run again unnecessarily.
      await this.reconcileCaseAttributionWorkflowCompletionAfterPrerequisiteCheck(runId, completeTagsBeforeCheck);
      return true;
    },

    async ensureCaseAttributionWorkflowPrerequisiteCurrent(runId) {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        this.caseAttributionWorkflowAssertCurrent(runId);
        await this.caseAttributionWorkflowSettle(runId, attempt === 0 ? 180 : 260);
        if (this.caseIntakeContinuationCheckIsCurrent && this.caseIntakeContinuationAction === 'ready') return true;
        try {
          await this.runCaseAttributionWorkflowPrerequisiteCheck(runId, { final: true });
        } catch (error) {
          // A short-lived revision can land while the final focused audit is
          // reading rows. Retry once after the queued mutation settles; genuine
          // Raw Data or sapling findings still fail on the second pass.
          if (attempt === 0 && error?.code === 'CASE_ATTRIBUTION_WORKFLOW_PREREQUISITE') continue;
          throw error;
        }
        await this.caseAttributionWorkflowSettle(runId, 220);
        if (this.caseIntakeContinuationCheckIsCurrent && this.caseIntakeContinuationAction === 'ready') return true;
      }

      // A final metadata-only revision can land after the focused audit and its
      // completion reconciliations. The guided run still owns the page lock here,
      // so re-anchor the already-passing snapshot rather than regressing the rail.
      this.reanchorCaseAttributionWorkflowPrerequisiteSnapshot();
      await this.caseAttributionWorkflowSettle(runId, 40);
      if (this.caseIntakeContinuationCheckIsCurrent && this.caseIntakeContinuationAction === 'ready') return true;
      throw this.caseAttributionWorkflowError(
        this.caseIntakeContinuationCheck?.detail || 'Raw Data or graph preparation is no longer current. Resolve that stage before continuing.',
        'CASE_ATTRIBUTION_WORKFLOW_PREREQUISITE'
      );
    },

    async runCaseAttributionWorkflowNextAction(runId, row = {}) {
      const definition = CASE_ATTRIBUTION_WORKFLOW_STEP_DEFINITIONS.find(item => item.tag === row.tag) || row;
      this.setCaseAttributionWorkflowStage(
        runId,
        definition,
        `Working on ${definition.shortLabel || definition.title}…`,
        this.caseSkipResolvableReviewsEnabled
          ? 'Resolvable review-only work will be skipped or rubber-stamped. Case Buddy will stop if a real investigator decision remains.'
          : 'Case Buddy will stop before a review that needs investigator acknowledgement or judgement.'
      );
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        active: false,
        paused: false,
        busy: false,
        manualPause: null,
        skipResolvableReview: Boolean(this.caseSkipResolvableReviewsEnabled)
      };
      this.showCaseAutomationPauseModal = false;
      this.caseWorkflowRawDataTool88UserStopRequestedAtMs = 0;
      const beforeSignature = this.caseAttributionWorkflowCompletionSignature();
      const result = await this.runCaseAttributionWorkflowOperation(
        runId,
        definition.title,
        () => this.runCaseAutomationNext(),
        CASE_ATTRIBUTION_WORKFLOW_ACTION_TIMEOUT_MS
      );
      this.caseAttributionWorkflowAssertCurrent(runId);
      this.cancelCaseWorkflowBackgroundAutomationResume?.({ source: 'case-attribution-workflow-action-boundary' });
      this.clearCaseAutomationTimer?.();
      this.clearCaseWorkflowStatefulGeneratedReviewAutoResumeTimer?.();
      this.clearCaseWorkflowWwwUsdReferenceAutoResumeTimer?.();
      if (this.caseAutomation?.active && !this.caseAutomation?.paused) {
        this.requestCaseAutomationUserStop?.('The scoped attribution workflow reached its one-action boundary.');
      }
      await this.caseAttributionWorkflowSettle(runId, 120);
      const handoff = this.getCaseAttributionWorkflowManualHandoff();
      if (handoff.needed) return { result, manual: true, handoff };
      this.syncCaseAttributionWorkflowCompletedTags(runId);
      const afterSignature = this.caseAttributionWorkflowCompletionSignature();
      return { result, manual: false, beforeSignature, afterSignature };
    },

    async runCaseAttributionWorkflowPipeline(runId) {
      if (!this.caseIntakeContinuationCheckIsCurrent || this.caseIntakeContinuationAction !== 'ready') {
        await this.runCaseAttributionWorkflowPrerequisiteCheck(runId, { final: false });
      }
      if (!this.caseAttributionWorkflowDefinitionsReady) {
        throw this.caseAttributionWorkflowError('One or more attribution workflow definitions could not be found.', 'CASE_ATTRIBUTION_WORKFLOW_DEFINITIONS');
      }

      let actionCount = 0;
      let repeatedStepKey = '';
      let repeatedStepCount = 0;
      let staleRefreshCount = 0;
      while (actionCount < CASE_ATTRIBUTION_WORKFLOW_MAX_ACTIONS) {
        this.caseAttributionWorkflowAssertCurrent(runId);
        await this.caseAttributionWorkflowSettle(runId, 55);
        this.syncCaseAttributionWorkflowCompletedTags(runId);

        if (this.caseAttributionWorkflowAllComplete) {
          // Let delayed case/workflow revisions land before declaring the category
          // finished. If they make a completed step stale, the next loop refreshes
          // it. If they stale the earlier Raw Data/sapling snapshot, re-run that
          // focused check under the same lock.
          await this.caseAttributionWorkflowSettle(runId, 220);
          this.syncCaseAttributionWorkflowCompletedTags(runId);
          if (this.caseAttributionWorkflowAllComplete) {
            await this.ensureCaseAttributionWorkflowPrerequisiteCurrent(runId);
            await this.caseAttributionWorkflowSettle(runId, 120);
            this.syncCaseAttributionWorkflowCompletedTags(runId);
            if (this.caseAttributionWorkflowAllComplete && this.caseIntakeContinuationAction === 'ready') {
              return { success: true, actionCount };
            }
          }
        }

        const rows = this.caseAttributionWorkflowStepRows || [];
        const recordedStaleRows = rows.filter(row => row.refreshableStale);
        if (recordedStaleRows.length) {
          if (staleRefreshCount >= CASE_ATTRIBUTION_WORKFLOW_STALE_REFRESH_LIMIT) {
            throw this.caseAttributionWorkflowError(
              `${recordedStaleRows[0].title} stayed stale after ${staleRefreshCount} workflow-check refresh attempts. The guided workflow stopped so you can inspect that check manually.`,
              'CASE_ATTRIBUTION_WORKFLOW_STALE_REFRESH'
            );
          }
          const refreshResult = await this.refreshCaseAttributionWorkflowRecordedStaleSteps(runId);
          staleRefreshCount += 1;
          actionCount += 1;
          this.caseAttributionWorkflowRun = {
            ...(this.caseAttributionWorkflowRun || createCaseAttributionWorkflowRunState()),
            actionCount
          };
          if (
            refreshResult.remaining >= recordedStaleRows.length
            && refreshResult.beforeSignature === refreshResult.afterSignature
            && staleRefreshCount >= CASE_ATTRIBUTION_WORKFLOW_STALE_REFRESH_LIMIT
          ) {
            throw this.caseAttributionWorkflowError(
              `${recordedStaleRows[0].title} did not refresh after the workflow checks ran. The guided workflow stopped so you can inspect that step manually.`,
              'CASE_ATTRIBUTION_WORKFLOW_STALE_REFRESH'
            );
          }
          continue;
        }

        const nextRow = rows.find(row => !row.complete);
        if (!nextRow) return { success: true, actionCount };
        const recommended = this.recommendedWorkflowStep || null;
        const recommendedDefinition = this.caseAttributionWorkflowDefinitionForStep(recommended || {});
        if (recommended && !recommendedDefinition) {
          throw this.caseAttributionWorkflowError(
            `Case Buddy recommends “${recommended.title || 'another workflow step'}” before this scoped run can continue. Complete that step manually, then try again.`,
            'CASE_ATTRIBUTION_WORKFLOW_BOUNDARY'
          );
        }
        const targetRow = recommendedDefinition
          ? (rows.find(row => row.tag === recommendedDefinition.tag) || nextRow)
          : nextRow;
        const outcome = await this.runCaseAttributionWorkflowNextAction(runId, targetRow);
        actionCount += 1;
        this.caseAttributionWorkflowRun = {
          ...(this.caseAttributionWorkflowRun || createCaseAttributionWorkflowRunState()),
          actionCount
        };
        if (outcome.manual) {
          return {
            success: false,
            manual: true,
            actionCount,
            stageTag: targetRow.tag,
            title: outcome.handoff?.title || targetRow.title,
            message: outcome.handoff?.message || 'Complete the open review, then continue this guided workflow.'
          };
        }

        const stepKey = `${targetRow.tag}|${outcome.afterSignature || ''}`;
        const progressed = outcome.beforeSignature !== outcome.afterSignature || targetRow.complete;
        if (!progressed && repeatedStepKey === stepKey) repeatedStepCount += 1;
        else if (!progressed) {
          repeatedStepKey = stepKey;
          repeatedStepCount = 1;
        } else {
          repeatedStepKey = '';
          repeatedStepCount = 0;
        }
        this.caseAttributionWorkflowRun = {
          ...(this.caseAttributionWorkflowRun || createCaseAttributionWorkflowRunState()),
          repeatedStepKey,
          repeatedStepCount
        };
        if (repeatedStepCount >= CASE_ATTRIBUTION_WORKFLOW_REPEAT_LIMIT) {
          throw this.caseAttributionWorkflowError(
            `${targetRow.title} did not advance after ${repeatedStepCount} attempts. The guided workflow stopped so you can review that step manually.`,
            'CASE_ATTRIBUTION_WORKFLOW_STALLED'
          );
        }
      }
      throw this.caseAttributionWorkflowError(
        `The guided workflow reached its ${CASE_ATTRIBUTION_WORKFLOW_MAX_ACTIONS}-action safety limit. Completed work remains in the case.`,
        'CASE_ATTRIBUTION_WORKFLOW_ACTION_LIMIT'
      );
    },

    async startCaseAttributionWorkflowRun() {
      if (!this.caseAttributionWorkflowRailVisible) {
        this.showCaseToast?.('Complete Raw Data gathering and graph preparation before starting attribution workflow guidance.');
        return false;
      }
      if (!this.caseAttributionWorkflowDefinitionsReady) {
        this.showCaseToast?.('The attribution workflow stages are still loading.');
        return false;
      }
      if (this.caseAttributionWorkflowAllComplete) {
        this.showCaseToast?.('The attribution, path-decision, and top-level USD stages are already complete.');
        return true;
      }
      if (this.caseAttributionWorkflowRun?.active) return false;

      if (this.caseAttributionWorkflowOtherAutomationActive) {
        await this.stopCaseAttributionWorkflowConflictingAutomations();
        await this.$nextTick();
      }
      if (this.caseAttributionWorkflowOtherAutomationActive) {
        this.showCaseToast?.('Another Case Buddy automation or operation is still running. Stop it before starting this guided workflow.');
        return false;
      }

      this.clearCaseWorkflowScheduledScrollTimers?.();
      this.showCaseAutomationPauseModal = false;
      const previousRunId = Number(this.caseAttributionWorkflowRun?.runId) || 0;
      const runId = previousRunId + 1;
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      this.caseAttributionWorkflowAbortController = controller;
      this.caseAttributionWorkflowRun = createCaseAttributionWorkflowRunState({
        active: true,
        runId,
        status: 'running',
        message: 'Preparing the next six workflow stages…',
        detail: 'Case Buddy will use the existing workflow actions one at a time and will stop before consistency, notes, legend, and cleanup.',
        completedTags: (this.caseAttributionWorkflowStepRows || []).filter(row => row.complete).map(row => row.tag),
        reviewModeSkip: Boolean(this.caseSkipResolvableReviewsEnabled),
        startedAtMs: Date.now()
      });
      this.applyCaseAttributionWorkflowScrollLock();

      const overallStop = () => {
        if (Number(this.caseAttributionWorkflowRun?.runId) !== runId || !this.caseAttributionWorkflowRun?.active) return;
        this.requestCaseAttributionWorkflowStop(
          'The guided attribution workflow stopped at its overall safety limit. Completed work remains in the case.',
          { timeout: true }
        );
      };
      const overallTimer = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
        ? window.setTimeout(overallStop, CASE_ATTRIBUTION_WORKFLOW_OVERALL_TIMEOUT_MS)
        : setTimeout(overallStop, CASE_ATTRIBUTION_WORKFLOW_OVERALL_TIMEOUT_MS);
      this.caseAttributionWorkflowRun.overallTimer = overallTimer;

      await this.$nextTick();
      this.$refs.caseAttributionWorkflowStopButton?.focus?.();

      try {
        const result = await this.runCaseAttributionWorkflowPipeline(runId);
        this.caseAttributionWorkflowAssertCurrent(runId);
        if (result?.manual) {
          const message = `${result.title || 'Workflow review'} needs investigator attention.`;
          this.finishCaseAttributionWorkflowRun(
            runId,
            'manual',
            message,
            result.message || 'Complete the open review, then select Continue attribution stages in the Workflow Assistant.'
          );
          this.showCaseToast?.(message);
          return false;
        }
        await this.$nextTick();
        if (!this.caseAttributionWorkflowAllComplete) {
          throw this.caseAttributionWorkflowError('The guided actions finished, but one or more of the six workflow stages did not turn current.', 'CASE_ATTRIBUTION_WORKFLOW_INCOMPLETE');
        }
        const message = 'Attribution and path-decision stages are complete.';
        this.finishCaseAttributionWorkflowRun(
          runId,
          'success',
          message,
          'Address attribution, path operations, matching address colors, entity review, external review, and top-level USD references are current. The Workflow Assistant is ready for consistency and cleanup.'
        );
        this.queueCaseSummaryRefresh?.(80);
        this.scheduleCaseInputFlowStoryRefresh?.(150, { force: true });
        this.showCaseToast?.(message);
        return true;
      } catch (error) {
        if (Number(this.caseAttributionWorkflowRun?.runId) !== runId) return false;
        const stopped = error?.code === 'CASE_ATTRIBUTION_WORKFLOW_STOPPED';
        const timeout = error?.code === 'CASE_ATTRIBUTION_WORKFLOW_TIMEOUT';
        const prerequisite = error?.code === 'CASE_ATTRIBUTION_WORKFLOW_PREREQUISITE';
        const message = stopped
          ? 'The guided attribution workflow stopped. Completed work remains in the case.'
          : (error?.message || 'The guided attribution workflow stopped before all six stages were complete.');
        // A failing existing workflow action may reject before it clears its own
        // Auto/busy flags. Release any automation owned by this scoped run before
        // returning control to the investigator.
        this.stopCaseAttributionWorkflowOwnedAutomation?.(message);
        this.finishCaseAttributionWorkflowRun(
          runId,
          timeout ? 'timeout' : (stopped ? 'stopped' : 'error'),
          message,
          prerequisite
            ? 'Return to the Raw Data and graph guidance above, resolve its current finding, then try this category again.'
            : 'Review the highlighted workflow stage, then retry this button or use that workflow tool manually.'
        );
        this.showCaseToast?.(message);
        return false;
      } finally {
        this.cancelCaseWorkflowBackgroundAutomationResume?.({ source: 'case-attribution-workflow-finish' });
        this.clearCaseAutomationTimer?.();
      }
    },


    caseCleanupWorkflowDefinitionForStep(step = {}) {
      return CASE_CLEANUP_WORKFLOW_STEP_DEFINITIONS.find(definition => this.caseCleanupWorkflowStepMatchesDefinition(step, definition)) || null;
    },

    resetCaseCleanupWorkflowRunForCase() {
      this.clearCaseCleanupWorkflowTimers();
      this.caseCleanupWorkflowAbortController = null;
      this.releaseCaseCleanupWorkflowScrollLock();
      this.caseCleanupWorkflowEntered = false;
      const runId = Number(this.caseCleanupWorkflowRun?.runId) || 0;
      this.caseCleanupWorkflowRun = createCaseCleanupWorkflowRunState({ runId: runId + 1 });
    },

    clearCaseCleanupWorkflowTimers() {
      const state = this.caseCleanupWorkflowRun || {};
      const clear = (timer) => {
        if (!timer) return;
        if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timer);
        else clearTimeout(timer);
      };
      clear(state.overallTimer);
      clear(state.actionTimer);
      if (this.caseCleanupWorkflowRun) {
        this.caseCleanupWorkflowRun.overallTimer = null;
        this.caseCleanupWorkflowRun.actionTimer = null;
      }
    },

    applyCaseCleanupWorkflowScrollLock() {
      if (typeof document === 'undefined' || !document.body) return;
      if (this.caseCleanupWorkflowBodyOverflow === null) {
        this.caseCleanupWorkflowBodyOverflow = document.body.style.overflow || '';
      }
      document.body.style.overflow = 'hidden';
    },

    releaseCaseCleanupWorkflowScrollLock() {
      if (typeof document === 'undefined' || !document.body) {
        this.caseCleanupWorkflowBodyOverflow = null;
        return;
      }
      if (this.caseCleanupWorkflowBodyOverflow !== null) {
        document.body.style.overflow = this.caseCleanupWorkflowBodyOverflow;
      }
      this.caseCleanupWorkflowBodyOverflow = null;
    },

    caseCleanupWorkflowError(message = 'The guided consistency and cleanup workflow stopped.', code = 'CASE_CLEANUP_WORKFLOW_FAILED') {
      const error = new Error(message);
      error.code = code;
      return error;
    },

    caseCleanupWorkflowAssertCurrent(runId) {
      const state = this.caseCleanupWorkflowRun || {};
      const controller = this.caseCleanupWorkflowAbortController;
      if (Number(state.runId) !== Number(runId) || state.stopRequested || controller?.signal?.aborted) {
        throw this.caseCleanupWorkflowError('The guided consistency and cleanup workflow was stopped.', 'CASE_CLEANUP_WORKFLOW_STOPPED');
      }
      return true;
    },

    async caseCleanupWorkflowSettle(runId, delayMs = 80) {
      this.caseCleanupWorkflowAssertCurrent(runId);
      await this.$nextTick();
      if (delayMs > 0) {
        await new Promise((resolve, reject) => {
          const controller = this.caseCleanupWorkflowAbortController;
          let timer = null;
          const onAbort = () => {
            if (timer) {
              if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timer);
              else clearTimeout(timer);
            }
            reject(this.caseCleanupWorkflowError('The guided consistency and cleanup workflow was stopped.', 'CASE_CLEANUP_WORKFLOW_STOPPED'));
          };
          if (controller?.signal?.aborted) return onAbort();
          controller?.signal?.addEventListener?.('abort', onAbort, { once: true });
          const finish = () => {
            controller?.signal?.removeEventListener?.('abort', onAbort);
            resolve();
          };
          timer = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
            ? window.setTimeout(finish, delayMs)
            : setTimeout(finish, delayMs);
        });
      }
      this.caseCleanupWorkflowAssertCurrent(runId);
    },

    async runCaseCleanupWorkflowOperation(runId, label, operation, timeoutMs = CASE_CLEANUP_WORKFLOW_ACTION_TIMEOUT_MS) {
      this.caseCleanupWorkflowAssertCurrent(runId);
      const controller = this.caseCleanupWorkflowAbortController;
      let timeoutTimer = null;
      let abortHandler = null;
      const operationPromise = Promise.resolve().then(operation);
      operationPromise.catch(() => {});
      const timeoutPromise = new Promise((resolve, reject) => {
        const timeout = Math.max(1000, Number(timeoutMs) || CASE_CLEANUP_WORKFLOW_ACTION_TIMEOUT_MS);
        const onTimeout = () => {
          const error = this.caseCleanupWorkflowError(`${label} did not finish within the safety limit.`, 'CASE_CLEANUP_WORKFLOW_TIMEOUT');
          reject(error);
          try { controller?.abort?.(error); } catch { /* Best-effort cancellation. */ }
          this.stopCaseCleanupWorkflowOwnedAutomation?.(`${label} reached its safety limit.`);
        };
        timeoutTimer = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
          ? window.setTimeout(onTimeout, timeout)
          : setTimeout(onTimeout, timeout);
        if (this.caseCleanupWorkflowRun) this.caseCleanupWorkflowRun.actionTimer = timeoutTimer;
      });
      const abortPromise = new Promise((resolve, reject) => {
        abortHandler = () => reject(this.caseCleanupWorkflowError('The guided consistency and cleanup workflow was stopped.', 'CASE_CLEANUP_WORKFLOW_STOPPED'));
        if (controller?.signal?.aborted) abortHandler();
        else controller?.signal?.addEventListener?.('abort', abortHandler, { once: true });
      });
      try {
        const result = await Promise.race([operationPromise, timeoutPromise, abortPromise]);
        this.caseCleanupWorkflowAssertCurrent(runId);
        return result;
      } finally {
        if (timeoutTimer) {
          if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timeoutTimer);
          else clearTimeout(timeoutTimer);
        }
        controller?.signal?.removeEventListener?.('abort', abortHandler);
        if (this.caseCleanupWorkflowRun && Number(this.caseCleanupWorkflowRun.runId) === Number(runId)) {
          this.caseCleanupWorkflowRun.actionTimer = null;
        }
      }
    },

    setCaseCleanupWorkflowStage(runId, definition = {}, message = '', detail = '') {
      this.caseCleanupWorkflowAssertCurrent(runId);
      const definitionIndex = CASE_CLEANUP_WORKFLOW_STEP_DEFINITIONS.findIndex(item => item.tag === definition?.tag);
      this.caseCleanupWorkflowRun = {
        ...(this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState()),
        status: 'running',
        stageKey: normalizeCell(definition?.key),
        stageTag: normalizeCell(definition?.tag).toUpperCase(),
        stageIndex: definitionIndex >= 0 ? definitionIndex + 1 : 0,
        stageTitle: normalizeCell(definition?.title),
        message: message || definition?.title || 'Working…',
        detail
      };
    },

    syncCaseCleanupWorkflowCompletedTags(runId, options = {}) {
      this.caseCleanupWorkflowAssertCurrent(runId);
      const preserve = options.preserve !== false;
      const existing = preserve
        ? (Array.isArray(this.caseCleanupWorkflowRun?.completedTags) ? this.caseCleanupWorkflowRun.completedTags : [])
        : [];
      const completedTags = Array.from(new Set([
        ...existing,
        ...(this.caseCleanupWorkflowStepRows || []).filter(row => row.complete).map(row => row.tag)
      ].map(tag => normalizeCell(tag).toUpperCase()).filter(Boolean)));
      this.caseCleanupWorkflowRun = {
        ...(this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState()),
        completedTags
      };
      return completedTags;
    },

    caseCleanupWorkflowCompletionSignature() {
      const cleanupRows = this.caseCleanupWorkflowStepRows || [];
      const attributionRows = this.caseAttributionWorkflowStepRows || [];
      const recommendedTag = this.caseWorkflowStepIdentity(this.recommendedWorkflowStep || {});
      const pauseTag = this.caseWorkflowStepIdentity(this.caseAutomation?.manualPause || {});
      return [
        cleanupRows.map(row => `${row.tag}:${row.complete ? 1 : 0}:${row.stale ? 1 : 0}`).join('|'),
        attributionRows.map(row => `${row.tag}:${row.complete ? 1 : 0}:${row.stale ? 1 : 0}`).join('|'),
        recommendedTag,
        pauseTag,
        Number(this.caseSupportState?.caseDataRevision) || 0
      ].join('::');
    },

    getCaseCleanupWorkflowRecordedStaleRows() {
      return (this.caseCleanupWorkflowStepRows || []).filter(row => row?.step && row.refreshableStale);
    },

    async waitForCaseCleanupWorkflowChecksIdle(runId, maxMs = CASE_CLEANUP_WORKFLOW_ACTION_TIMEOUT_MS) {
      const startedAtMs = Date.now();
      let quietSinceMs = 0;
      while ((Date.now() - startedAtMs) < Math.max(1000, Number(maxMs) || CASE_CLEANUP_WORKFLOW_ACTION_TIMEOUT_MS)) {
        this.caseCleanupWorkflowAssertCurrent(runId);
        if (this.caseChecksRunning) {
          quietSinceMs = 0;
        } else if (!quietSinceMs) {
          quietSinceMs = Date.now();
        } else if ((Date.now() - quietSinceMs) >= 180) {
          return true;
        }
        await this.caseCleanupWorkflowSettle(runId, 60);
      }
      throw this.caseCleanupWorkflowError('Workflow checks did not finish within the cleanup workflow safety limit.', 'CASE_CLEANUP_WORKFLOW_TIMEOUT');
    },

    recordCaseCleanupWorkflowKnownStepCurrent(row = {}, source = 'case-cleanup-workflow-reconcile') {
      if (!row?.step) return false;
      switch (normalizeCell(row.tag).toUpperCase()) {
        case 'ATTRIBUTION_PATH_OPERATIONS':
          this.recordCaseIntakePreparationManualCompletion(row.step, source);
          return true;
        case 'ADDRESS_COLOR_LIKE_CELLS':
          this.recordCaseWorkflowAddressColorLikeCellsCurrent({ step: row.step, source });
          return true;
        case 'ADDRESS_ATTRIBUTION_FILL':
          this.reconcileCaseWorkflowAddressAttributionFillCompletionState?.({ source });
          return true;
        case 'WWW_USD_REFERENCES':
          this.reconcileCaseWorkflowWwwUsdReferenceCompletionState?.({ source });
          return true;
        default:
          this.recordCaseWorkflowAutoSoftStepComplete(row.step, { action: source }, {
            source,
            stepTitle: row.title
          });
          return true;
      }
    },

    reconcileCaseCleanupWorkflowKnownCompletions(runId, options = {}) {
      this.caseCleanupWorkflowAssertCurrent(runId);
      const runtime = this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState();
      const cleanupTags = new Set((Array.isArray(runtime.completedTags) ? runtime.completedTags : []).map(tag => normalizeCell(tag).toUpperCase()).filter(Boolean));
      const prerequisiteTags = new Set((Array.isArray(runtime.prerequisiteCompletedTags) ? runtime.prerequisiteCompletedTags : []).map(tag => normalizeCell(tag).toUpperCase()).filter(Boolean));
      const source = normalizeCell(options.source || 'case-cleanup-workflow-reconcile');
      for (const row of this.caseAttributionWorkflowStepRows || []) {
        if (!prerequisiteTags.has(row.tag) || row.complete || !row.step) continue;
        this.recordCaseCleanupWorkflowKnownStepCurrent(row, `${source}-attribution`);
      }
      for (const row of this.caseCleanupWorkflowStepRows || []) {
        if (!cleanupTags.has(row.tag) || row.complete || !row.step) continue;
        this.recordCaseCleanupWorkflowKnownStepCurrent(row, `${source}-cleanup`);
      }
      this.reanchorCaseAttributionWorkflowPrerequisiteSnapshot?.();
      return true;
    },

    async refreshCaseCleanupWorkflowRecordedStaleSteps(runId) {
      this.caseCleanupWorkflowAssertCurrent(runId);
      const staleRows = this.getCaseCleanupWorkflowRecordedStaleRows();
      if (!staleRows.length) return { refreshed: false, remaining: 0, beforeSignature: '', afterSignature: '' };
      const target = staleRows[0];
      this.setCaseCleanupWorkflowStage(
        runId,
        target,
        `Refreshing ${target.shortLabel || target.title}…`,
        'This stage is recorded complete, but its check snapshot became stale. Case Buddy is running the same workflow check as the yellow Run checks control before deciding whether more work is needed.'
      );
      const beforeSignature = this.caseCleanupWorkflowCompletionSignature();
      await this.runCaseCleanupWorkflowOperation(
        runId,
        `${target.title} status refresh`,
        async () => {
          const result = await this.runWorkflowChecksFromStaleBadge(target.step);
          await this.waitForCaseCleanupWorkflowChecksIdle(runId);
          return result;
        },
        CASE_CLEANUP_WORKFLOW_ACTION_TIMEOUT_MS
      );
      await this.caseCleanupWorkflowSettle(runId, 120);
      this.syncCaseCleanupWorkflowCompletedTags(runId, { preserve: true });
      return {
        refreshed: true,
        remaining: this.getCaseCleanupWorkflowRecordedStaleRows().length,
        beforeSignature,
        afterSignature: this.caseCleanupWorkflowCompletionSignature()
      };
    },

    getCaseCleanupWorkflowManualHandoff() {
      const pause = this.caseAutomation?.manualPause || null;
      if (this.caseAutomation?.paused || pause || this.showCaseAutomationPauseModal) {
        return {
          needed: true,
          title: normalizeCell(pause?.stepTitle || pause?.title || this.recommendedWorkflowStep?.title || 'Workflow review'),
          message: normalizeCell(pause?.message || pause?.detail || 'Complete the open review, then run the Workflow Assistant action again.')
        };
      }
      const modalRows = [
        [this.showCaseInconsistencyModal, 'Case inconsistency review', 'Resolve or acknowledge the open inconsistency review, then continue from the Workflow Assistant.'],
        [this.showDepositHotWalletModal, 'Deposit and hot-wallet note review', 'Complete the open note-normalization review, then continue from the Workflow Assistant.'],
        [this.showDepositPatternModal, 'Deposit-pattern review', 'Resolve the open high-priority deposit candidates, then continue from the Workflow Assistant.'],
        [this.showKnownEntityCasingModal, 'Known-entity casing review', 'Complete the open casing review, then continue from the Workflow Assistant.'],
        [this.showDuplicateHashLabelerModal, 'Duplicate-hash review', 'Complete the duplicate-hash labeling review, then continue from the Workflow Assistant.'],
        [this.showNoteHashRewriteModal, 'Note backup and hash-rewrite review', 'Complete the open note backup/hash replacement review, then continue from the Workflow Assistant.'],
        [this.showAddressLegendModal, 'Address legend review', 'Complete the open address-legend review, then continue from the Workflow Assistant.']
      ];
      const modal = modalRows.find(row => Boolean(row[0]));
      return modal ? { needed: true, title: modal[1], message: modal[2] } : { needed: false, title: '', message: '' };
    },

    stopCaseCleanupWorkflowOwnedAutomation(reason = 'The scoped consistency and cleanup workflow is stopping.') {
      this.cancelCaseWorkflowBackgroundAutomationResume?.({ source: 'case-cleanup-workflow-stop' });
      this.clearCaseWorkflowScheduledScrollTimers?.();
      this.clearCaseAutomationTimer?.();
      this.clearCaseWorkflowStatefulGeneratedReviewAutoResumeTimer?.();
      this.clearCaseWorkflowWwwUsdReferenceAutoResumeTimer?.();
      if (this.caseAutomation?.active || this.caseAutomation?.busy) this.requestCaseAutomationUserStop?.(reason);
    },

    async stopCaseCleanupWorkflowConflictingAutomations() {
      if (this.caseIntakePreparation?.active) {
        this.requestCaseIntakePreparationStop?.('Intake preparation stopped so consistency and cleanup can start.', { silent: true });
      }
      if (this.caseAttributionWorkflowRun?.active) {
        this.requestCaseAttributionWorkflowStop?.('Attribution workflow stopped so consistency and cleanup can start.', { silent: true });
      }
      if (this.caseManualStartViewportAutoActive && typeof this.stopCaseManualStartViewportAuto === 'function') {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto stopped so consistency and cleanup can start.', { silent: true });
      }
      if (this.caseAutomation?.active || this.caseAutomation?.busy) {
        this.requestCaseAutomationUserStop?.('Workflow Auto stopped so consistency and cleanup can start.');
      }
      if (this.caseManualStartHopInspectorRunning) this.requestCaseManualStartHopInspectorStop?.();
      if (this.caseHopSolverRunning) this.requestCaseHopSolverStop?.();
      const stopTool = (tool, reason) => {
        if (tool && typeof tool.caseBuddyRequestAutomationStop === 'function') {
          try { tool.caseBuddyRequestAutomationStop(reason); } catch (error) { console.warn('Could not stop a conflicting Case Buddy tool.', error); }
        }
      };
      stopTool(this.$refs?.caseHopSolverTool88, 'Consistency and cleanup is starting.');
      stopTool(this.$refs?.caseWorkflowRawDataTool88, 'Consistency and cleanup is starting.');
      stopTool(this.getCaseManualStartHopInspectorTool?.(), 'Consistency and cleanup is starting.');
      this.cancelCaseWorkflowBackgroundAutomationResume?.({ source: 'case-cleanup-workflow-start' });
      this.clearCaseWorkflowScheduledScrollTimers?.();
      await this.$nextTick();
    },

    requestCaseCleanupWorkflowStop(message = 'Consistency and cleanup workflow stopped by user.', options = {}) {
      const state = this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState();
      if (!state.active && options.force !== true) return false;
      try {
        this.caseCleanupWorkflowAbortController?.abort?.(this.caseCleanupWorkflowError(message, 'CASE_CLEANUP_WORKFLOW_STOPPED'));
      } catch {
        // Abort is best effort; the run-id guard still prevents another action.
      }
      this.stopCaseCleanupWorkflowOwnedAutomation(message);
      this.clearCaseCleanupWorkflowTimers();
      this.caseCleanupWorkflowAbortController = null;
      this.releaseCaseCleanupWorkflowScrollLock();
      const result = options.timeout ? 'timeout' : 'stopped';
      this.caseCleanupWorkflowRun = {
        ...state,
        active: false,
        stopRequested: true,
        runId: (Number(state.runId) || 0) + 1,
        status: result,
        lastResult: result,
        message,
        detail: 'Completed spreadsheet and workflow changes remain in the case. An operation already in flight may finish its own cleanup before its tool becomes available again.',
        lastMessage: message,
        finishedAtUtc: new Date().toISOString(),
        overallTimer: null,
        actionTimer: null
      };
      if (!options.silent) this.showCaseToast?.(message);
      return true;
    },

    finishCaseCleanupWorkflowRun(runId, result = 'success', message = '', detail = '') {
      if (Number(this.caseCleanupWorkflowRun?.runId) !== Number(runId)) return false;
      this.clearCaseCleanupWorkflowTimers();
      this.caseCleanupWorkflowAbortController = null;
      this.releaseCaseCleanupWorkflowScrollLock();
      this.caseCleanupWorkflowRun = {
        ...(this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState()),
        active: false,
        stopRequested: false,
        status: result,
        lastResult: result,
        message,
        detail,
        lastMessage: message,
        finishedAtUtc: new Date().toISOString(),
        overallTimer: null,
        actionTimer: null
      };
      return true;
    },

    async runCaseCleanupWorkflowPrerequisiteCheck(runId, options = {}) {
      this.caseCleanupWorkflowAssertCurrent(runId);
      this.caseCleanupWorkflowRun = {
        ...(this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState()),
        status: 'checking-prerequisites',
        stageKey: 'prerequisite',
        stageTag: '',
        stageIndex: 0,
        stageTitle: 'Confirm earlier workflow readiness',
        message: options.final
          ? 'Verifying earlier stages and the completed cleanup work…'
          : 'Confirming attribution, Raw Data, and graph readiness…',
        detail: 'This bounded verification runs only because you deliberately started the guided consistency and cleanup action.'
      };

      if (!this.caseIntakeContinuationCheckIsCurrent || this.caseIntakeContinuationAction !== 'ready') {
        const result = await this.runCaseCleanupWorkflowOperation(
          runId,
          'Raw Data and graph readiness check',
          () => this.runCaseIntakeContinuationCheck({
            source: options.final ? 'workflow-assistant-cleanup-final-raw-check' : 'workflow-assistant-cleanup-preflight-raw-check',
            silent: true,
            retryOnRevisionChange: false,
            allowCleanupWorkflowRun: true
          }),
          CASE_INTAKE_CONTINUATION_CHECK_TIMEOUT_MS + 5000
        );
        this.caseCleanupWorkflowAssertCurrent(runId);
        if (!result || this.caseIntakeContinuationAction !== 'ready') {
          throw this.caseCleanupWorkflowError(
            this.caseIntakeContinuationCheck?.detail || 'Raw Data or graph preparation is no longer current. Resolve that stage before continuing cleanup.',
            'CASE_CLEANUP_WORKFLOW_PREREQUISITE'
          );
        }
      }

      await this.caseCleanupWorkflowSettle(runId, 120);
      this.reconcileCaseCleanupWorkflowKnownCompletions(runId, { source: 'case-cleanup-workflow-prerequisite-reconcile' });
      await this.caseCleanupWorkflowSettle(runId, 140);

      if (!this.caseAttributionWorkflowAllComplete) {
        await this.runCaseCleanupWorkflowOperation(
          runId,
          'Attribution workflow status verification',
          async () => {
            const result = await this.runWorkflowChecks();
            await this.waitForCaseCleanupWorkflowChecksIdle(runId);
            return result;
          },
          CASE_CLEANUP_WORKFLOW_ACTION_TIMEOUT_MS
        );
        await this.caseCleanupWorkflowSettle(runId, 180);
        this.reanchorCaseAttributionWorkflowPrerequisiteSnapshot?.();
      }

      if (!this.caseAttributionWorkflowAllComplete) {
        const pending = (this.caseAttributionWorkflowStepRows || []).find(row => !row.complete);
        throw this.caseCleanupWorkflowError(
          `${pending?.title || 'An attribution workflow stage'} is not current. Complete or refresh that earlier stage before continuing consistency and cleanup.`,
          'CASE_CLEANUP_WORKFLOW_PREREQUISITE'
        );
      }
      const prerequisiteCompletedTags = (this.caseAttributionWorkflowStepRows || [])
        .filter(row => row.complete)
        .map(row => row.tag);
      this.caseCleanupWorkflowRun = {
        ...(this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState()),
        prerequisiteCompletedTags
      };
      this.reanchorCaseAttributionWorkflowPrerequisiteSnapshot?.();
      return true;
    },

    async runCaseCleanupWorkflowFinalVerification(runId) {
      this.caseCleanupWorkflowAssertCurrent(runId);
      await this.runCaseCleanupWorkflowPrerequisiteCheck(runId, { final: true });
      this.caseCleanupWorkflowRun = {
        ...(this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState()),
        status: 'verifying',
        stageKey: 'verification',
        stageTag: '',
        stageIndex: 0,
        stageTitle: 'Verify consistency and cleanup',
        message: 'Running final workflow checks…',
        detail: 'Case Buddy is refreshing the workflow snapshots once so completed stages do not remain grey or stale after the guided tools finish.'
      };
      await this.runCaseCleanupWorkflowOperation(
        runId,
        'Final consistency and cleanup workflow checks',
        async () => {
          const result = await this.runWorkflowChecks();
          await this.waitForCaseCleanupWorkflowChecksIdle(runId);
          return result;
        },
        CASE_CLEANUP_WORKFLOW_ACTION_TIMEOUT_MS
      );
      await this.caseCleanupWorkflowSettle(runId, 220);
      this.reanchorCaseAttributionWorkflowPrerequisiteSnapshot?.();
      await this.caseCleanupWorkflowSettle(runId, 160);
      this.reanchorCaseAttributionWorkflowPrerequisiteSnapshot?.();
      this.syncCaseCleanupWorkflowCompletedTags(runId, { preserve: false });
      return this.caseCleanupWorkflowReadyForDeliverables;
    },

    async runCaseCleanupWorkflowNextAction(runId, row = {}) {
      const definition = CASE_CLEANUP_WORKFLOW_STEP_DEFINITIONS.find(item => item.tag === row.tag) || row;
      this.setCaseCleanupWorkflowStage(
        runId,
        definition,
        `Working on ${definition.shortLabel || definition.title}…`,
        this.caseSkipResolvableReviewsEnabled
          ? 'Resolvable review-only work will be skipped or rubber-stamped. Case Buddy will stop if a real investigator decision remains.'
          : 'Case Buddy will stop before a review that needs investigator acknowledgement or judgement.'
      );
      this.reconcileCaseCleanupWorkflowKnownCompletions(runId, { source: 'case-cleanup-workflow-before-action' });
      await this.caseCleanupWorkflowSettle(runId, 70);
      const recommendedDefinition = this.caseCleanupWorkflowDefinitionForStep(this.recommendedWorkflowStep || {});
      if (this.recommendedWorkflowStep && !recommendedDefinition) {
        throw this.caseCleanupWorkflowError(
          `Case Buddy recommends “${this.recommendedWorkflowStep.title || 'another workflow step'}” before this scoped cleanup run can continue. Complete that step manually, then try again.`,
          'CASE_CLEANUP_WORKFLOW_BOUNDARY'
        );
      }

      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        active: false,
        paused: false,
        busy: false,
        manualPause: null,
        skipResolvableReview: Boolean(this.caseSkipResolvableReviewsEnabled)
      };
      this.showCaseAutomationPauseModal = false;
      this.caseWorkflowRawDataTool88UserStopRequestedAtMs = 0;
      const beforeSignature = this.caseCleanupWorkflowCompletionSignature();
      const result = await this.runCaseCleanupWorkflowOperation(
        runId,
        definition.title,
        () => this.runCaseAutomationNext(),
        CASE_CLEANUP_WORKFLOW_ACTION_TIMEOUT_MS
      );
      this.caseCleanupWorkflowAssertCurrent(runId);
      this.cancelCaseWorkflowBackgroundAutomationResume?.({ source: 'case-cleanup-workflow-action-boundary' });
      this.clearCaseAutomationTimer?.();
      this.clearCaseWorkflowStatefulGeneratedReviewAutoResumeTimer?.();
      this.clearCaseWorkflowWwwUsdReferenceAutoResumeTimer?.();
      if (this.caseAutomation?.active && !this.caseAutomation?.paused) {
        this.requestCaseAutomationUserStop?.('The scoped consistency and cleanup workflow reached its one-action boundary.');
      }
      await this.caseCleanupWorkflowSettle(runId, 140);
      const handoff = this.getCaseCleanupWorkflowManualHandoff();
      if (handoff.needed) return { result, manual: true, handoff };
      this.syncCaseCleanupWorkflowCompletedTags(runId, { preserve: true });
      this.reconcileCaseCleanupWorkflowKnownCompletions(runId, { source: 'case-cleanup-workflow-after-action' });
      await this.caseCleanupWorkflowSettle(runId, 90);
      return {
        result,
        manual: false,
        beforeSignature,
        afterSignature: this.caseCleanupWorkflowCompletionSignature()
      };
    },

    async runCaseCleanupWorkflowPipeline(runId) {
      await this.runCaseCleanupWorkflowPrerequisiteCheck(runId, { final: false });
      if (!this.caseCleanupWorkflowDefinitionsReady) {
        throw this.caseCleanupWorkflowError('One or more consistency and cleanup workflow definitions could not be found.', 'CASE_CLEANUP_WORKFLOW_DEFINITIONS');
      }

      let actionCount = 0;
      let repeatedStepKey = '';
      let repeatedStepCount = 0;
      let staleRefreshCount = 0;
      while (actionCount < CASE_CLEANUP_WORKFLOW_MAX_ACTIONS) {
        this.caseCleanupWorkflowAssertCurrent(runId);
        await this.caseCleanupWorkflowSettle(runId, 55);
        this.syncCaseCleanupWorkflowCompletedTags(runId, { preserve: true });
        this.reconcileCaseCleanupWorkflowKnownCompletions(runId, { source: 'case-cleanup-workflow-loop' });
        await this.caseCleanupWorkflowSettle(runId, 70);

        const runtimeCompleted = new Set((this.caseCleanupWorkflowRun?.completedTags || []).map(tag => normalizeCell(tag).toUpperCase()));
        const rows = this.caseCleanupWorkflowStepRows || [];
        const allHandled = rows.every(row => row.complete || runtimeCompleted.has(row.tag));
        if (allHandled) {
          const ready = await this.runCaseCleanupWorkflowFinalVerification(runId);
          actionCount += 1;
          this.caseCleanupWorkflowRun = {
            ...(this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState()),
            actionCount
          };
          if (ready) return { success: true, actionCount };
          repeatedStepKey = '';
          repeatedStepCount = 0;
          staleRefreshCount = 0;
          continue;
        }

        const staleRows = rows.filter(row => row.refreshableStale && !runtimeCompleted.has(row.tag));
        if (staleRows.length) {
          if (staleRefreshCount >= CASE_CLEANUP_WORKFLOW_STALE_REFRESH_LIMIT) {
            throw this.caseCleanupWorkflowError(
              `${staleRows[0].title} stayed stale after ${staleRefreshCount} workflow-check refresh attempts. The guided workflow stopped so you can inspect that check manually.`,
              'CASE_CLEANUP_WORKFLOW_STALE_REFRESH'
            );
          }
          const refreshResult = await this.refreshCaseCleanupWorkflowRecordedStaleSteps(runId);
          staleRefreshCount += 1;
          actionCount += 1;
          this.caseCleanupWorkflowRun = {
            ...(this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState()),
            actionCount
          };
          if (
            refreshResult.remaining >= staleRows.length
            && refreshResult.beforeSignature === refreshResult.afterSignature
            && staleRefreshCount >= CASE_CLEANUP_WORKFLOW_STALE_REFRESH_LIMIT
          ) {
            throw this.caseCleanupWorkflowError(
              `${staleRows[0].title} did not refresh after the workflow checks ran. The guided workflow stopped so you can inspect that step manually.`,
              'CASE_CLEANUP_WORKFLOW_STALE_REFRESH'
            );
          }
          continue;
        }

        const nextRow = rows.find(row => !row.complete && !runtimeCompleted.has(row.tag));
        if (!nextRow) continue;
        const recommended = this.recommendedWorkflowStep || null;
        const recommendedDefinition = this.caseCleanupWorkflowDefinitionForStep(recommended || {});
        if (recommended && !recommendedDefinition) {
          throw this.caseCleanupWorkflowError(
            `Case Buddy recommends “${recommended.title || 'another workflow step'}” before this scoped cleanup run can continue. Complete that step manually, then try again.`,
            'CASE_CLEANUP_WORKFLOW_BOUNDARY'
          );
        }
        const targetRow = recommendedDefinition
          ? (rows.find(row => row.tag === recommendedDefinition.tag) || nextRow)
          : nextRow;
        const outcome = await this.runCaseCleanupWorkflowNextAction(runId, targetRow);
        actionCount += 1;
        this.caseCleanupWorkflowRun = {
          ...(this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState()),
          actionCount
        };
        if (outcome.manual) {
          return {
            success: false,
            manual: true,
            actionCount,
            stageTag: targetRow.tag,
            title: outcome.handoff?.title || targetRow.title,
            message: outcome.handoff?.message || 'Complete the open review, then continue this guided workflow.'
          };
        }

        const stepKey = `${targetRow.tag}|${outcome.afterSignature || ''}`;
        const progressed = outcome.beforeSignature !== outcome.afterSignature
          || (this.caseCleanupWorkflowRun?.completedTags || []).includes(targetRow.tag);
        if (!progressed && repeatedStepKey === stepKey) repeatedStepCount += 1;
        else if (!progressed) {
          repeatedStepKey = stepKey;
          repeatedStepCount = 1;
        } else {
          repeatedStepKey = '';
          repeatedStepCount = 0;
        }
        this.caseCleanupWorkflowRun = {
          ...(this.caseCleanupWorkflowRun || createCaseCleanupWorkflowRunState()),
          repeatedStepKey,
          repeatedStepCount
        };
        if (repeatedStepCount >= CASE_CLEANUP_WORKFLOW_REPEAT_LIMIT) {
          throw this.caseCleanupWorkflowError(
            `${targetRow.title} did not advance after ${repeatedStepCount} attempts. The guided workflow stopped so you can review that step manually.`,
            'CASE_CLEANUP_WORKFLOW_STALLED'
          );
        }
      }
      throw this.caseCleanupWorkflowError(
        `The guided workflow reached its ${CASE_CLEANUP_WORKFLOW_MAX_ACTIONS}-action safety limit. Completed work remains in the case.`,
        'CASE_CLEANUP_WORKFLOW_ACTION_LIMIT'
      );
    },

    async startCaseCleanupWorkflowRun() {
      if (!this.caseCleanupWorkflowRailVisible) {
        this.showCaseToast?.('Complete attribution, path decisions, and top-level USD review before starting consistency and cleanup.');
        return false;
      }
      if (!this.caseCleanupWorkflowDefinitionsReady) {
        this.showCaseToast?.('The consistency and cleanup workflow stages are still loading.');
        return false;
      }
      if (this.caseCleanupWorkflowReadyForDeliverables) {
        this.caseCleanupWorkflowEntered = true;
        this.showCaseToast?.('Consistency, notes, legend, and cleanup are already current.');
        return true;
      }
      if (this.caseCleanupWorkflowRun?.active) return false;

      if (this.caseCleanupWorkflowOtherAutomationActive) {
        await this.stopCaseCleanupWorkflowConflictingAutomations();
        await this.$nextTick();
      }
      if (this.caseCleanupWorkflowOtherAutomationActive) {
        this.showCaseToast?.('Another Case Buddy automation or operation is still running. Stop it before starting this guided workflow.');
        return false;
      }

      this.caseCleanupWorkflowEntered = true;
      this.clearCaseWorkflowScheduledScrollTimers?.();
      this.showCaseAutomationPauseModal = false;
      const previousRunId = Number(this.caseCleanupWorkflowRun?.runId) || 0;
      const runId = previousRunId + 1;
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const prerequisiteCompletedTags = (this.caseAttributionWorkflowStepRows || []).filter(row => row.complete).map(row => row.tag);
      this.caseCleanupWorkflowAbortController = controller;
      this.caseCleanupWorkflowRun = createCaseCleanupWorkflowRunState({
        active: true,
        runId,
        status: 'running',
        message: 'Preparing the next seven workflow stages…',
        detail: 'Case Buddy will use the existing workflow actions one at a time and will stop before deliverable buildout and shipping.',
        completedTags: (this.caseCleanupWorkflowStepRows || []).filter(row => row.complete).map(row => row.tag),
        prerequisiteCompletedTags,
        reviewModeSkip: Boolean(this.caseSkipResolvableReviewsEnabled),
        startedAtMs: Date.now()
      });
      this.applyCaseCleanupWorkflowScrollLock();

      const overallStop = () => {
        if (Number(this.caseCleanupWorkflowRun?.runId) !== runId || !this.caseCleanupWorkflowRun?.active) return;
        this.requestCaseCleanupWorkflowStop(
          'The guided consistency and cleanup workflow stopped at its overall safety limit. Completed work remains in the case.',
          { timeout: true }
        );
      };
      const overallTimer = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
        ? window.setTimeout(overallStop, CASE_CLEANUP_WORKFLOW_OVERALL_TIMEOUT_MS)
        : setTimeout(overallStop, CASE_CLEANUP_WORKFLOW_OVERALL_TIMEOUT_MS);
      this.caseCleanupWorkflowRun.overallTimer = overallTimer;

      await this.$nextTick();
      this.$refs.caseCleanupWorkflowStopButton?.focus?.();

      try {
        const result = await this.runCaseCleanupWorkflowPipeline(runId);
        this.caseCleanupWorkflowAssertCurrent(runId);
        if (result?.manual) {
          const message = `${result.title || 'Workflow review'} needs investigator attention.`;
          this.finishCaseCleanupWorkflowRun(
            runId,
            'manual',
            message,
            result.message || 'Complete the open review, then select Continue cleanup stages in the Workflow Assistant.'
          );
          this.showCaseToast?.(message);
          return false;
        }
        await this.$nextTick();
        if (!this.caseCleanupWorkflowReadyForDeliverables) {
          throw this.caseCleanupWorkflowError('The guided actions finished, but one or more consistency and cleanup stages did not turn current.', 'CASE_CLEANUP_WORKFLOW_INCOMPLETE');
        }
        const message = 'Consistency, notes, legend, and cleanup are complete.';
        this.finishCaseCleanupWorkflowRun(
          runId,
          'success',
          message,
          'Case inconsistencies, note normalization, deposit patterns, known-entity casing, duplicate hashes, note backup/hash replacement, and the address legend are current. The Workflow Assistant is ready for deliverable buildout and shipping.'
        );
        this.queueCaseSummaryRefresh?.(80);
        this.scheduleCaseInputFlowStoryRefresh?.(150, { force: true });
        this.showCaseToast?.(message);
        return true;
      } catch (error) {
        if (Number(this.caseCleanupWorkflowRun?.runId) !== runId) return false;
        const stopped = error?.code === 'CASE_CLEANUP_WORKFLOW_STOPPED';
        const timeout = error?.code === 'CASE_CLEANUP_WORKFLOW_TIMEOUT';
        const prerequisite = error?.code === 'CASE_CLEANUP_WORKFLOW_PREREQUISITE';
        const message = stopped
          ? 'The guided consistency and cleanup workflow stopped. Completed work remains in the case.'
          : (error?.message || 'The guided consistency and cleanup workflow stopped before all seven stages were complete.');
        this.stopCaseCleanupWorkflowOwnedAutomation?.(message);
        this.finishCaseCleanupWorkflowRun(
          runId,
          timeout ? 'timeout' : (stopped ? 'stopped' : 'error'),
          message,
          prerequisite
            ? 'Refresh or complete the earlier highlighted workflow stage, then retry this cleanup category.'
            : 'Review the highlighted cleanup stage, then retry this button or use that workflow tool manually.'
        );
        this.showCaseToast?.(message);
        return false;
      } finally {
        this.cancelCaseWorkflowBackgroundAutomationResume?.({ source: 'case-cleanup-workflow-finish' });
        this.clearCaseAutomationTimer?.();
      }
    },


    clearCaseIntakePreparationTimers() {
      const state = this.caseIntakePreparation || {};
      const clear = (timer) => {
        if (!timer) return;
        if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timer);
        else clearTimeout(timer);
      };
      clear(state.overallTimer);
      clear(state.stageTimer);
      if (this.caseIntakePreparation) {
        this.caseIntakePreparation.overallTimer = null;
        this.caseIntakePreparation.stageTimer = null;
      }
    },

    applyCaseIntakePreparationScrollLock() {
      if (typeof document === 'undefined' || !document.body) return;
      if (this.caseIntakePreparationBodyOverflow === null) {
        this.caseIntakePreparationBodyOverflow = document.body.style.overflow || '';
      }
      document.body.style.overflow = 'hidden';
    },

    releaseCaseIntakePreparationScrollLock() {
      if (typeof document === 'undefined' || !document.body) {
        this.caseIntakePreparationBodyOverflow = null;
        return;
      }
      if (this.caseIntakePreparationBodyOverflow !== null) {
        document.body.style.overflow = this.caseIntakePreparationBodyOverflow;
      }
      this.caseIntakePreparationBodyOverflow = null;
    },

    caseIntakePreparationError(message = 'Case preparation stopped.', code = 'CASE_INTAKE_PREPARATION_FAILED') {
      const error = new Error(message);
      error.code = code;
      return error;
    },

    caseIntakePreparationAssertCurrent(runId) {
      const state = this.caseIntakePreparation || {};
      const controller = this.caseIntakePreparationAbortController;
      if (Number(state.runId) !== Number(runId) || state.stopRequested || controller?.signal?.aborted) {
        throw this.caseIntakePreparationError('Case preparation was stopped.', 'CASE_INTAKE_PREPARATION_STOPPED');
      }
      return true;
    },

    async caseIntakePreparationSettle(runId, delayMs = 70) {
      this.caseIntakePreparationAssertCurrent(runId);
      await this.$nextTick();
      if (delayMs > 0) {
        await new Promise((resolve, reject) => {
          const controller = this.caseIntakePreparationAbortController;
          let timer = null;
          const onAbort = () => {
            if (timer) {
              if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timer);
              else clearTimeout(timer);
            }
            reject(this.caseIntakePreparationError('Case preparation was stopped.', 'CASE_INTAKE_PREPARATION_STOPPED'));
          };
          if (controller?.signal?.aborted) return onAbort();
          controller?.signal?.addEventListener?.('abort', onAbort, { once: true });
          const finish = () => {
            controller?.signal?.removeEventListener?.('abort', onAbort);
            resolve();
          };
          timer = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
            ? window.setTimeout(finish, delayMs)
            : setTimeout(finish, delayMs);
        });
      }
      this.caseIntakePreparationAssertCurrent(runId);
    },

    async waitForCaseIntakePreparationRevisionQuiet(runId, options = {}) {
      const quietMs = Math.max(80, Number(options.quietMs) || 240);
      const maxWaitMs = Math.max(quietMs, Number(options.maxWaitMs) || 2200);
      const startedAt = Date.now();
      let revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      let unchangedSince = Date.now();

      while ((Date.now() - startedAt) < maxWaitMs) {
        await this.caseIntakePreparationSettle(runId, 60);
        const nextRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
        if (nextRevision !== revision) {
          revision = nextRevision;
          unchangedSince = Date.now();
          continue;
        }
        if ((Date.now() - unchangedSince) >= quietMs) return revision;
      }
      return revision;
    },

    async runCaseIntakePreparationOperation(runId, label, operation, timeoutMs = CASE_INTAKE_PREPARATION_LOCAL_STAGE_TIMEOUT_MS) {
      this.caseIntakePreparationAssertCurrent(runId);
      const controller = this.caseIntakePreparationAbortController;
      let timeoutTimer = null;
      let abortHandler = null;
      const operationPromise = Promise.resolve().then(operation);
      operationPromise.catch(() => {});
      const timeoutPromise = new Promise((resolve, reject) => {
        const timeout = Math.max(1000, Number(timeoutMs) || CASE_INTAKE_PREPARATION_LOCAL_STAGE_TIMEOUT_MS);
        const onTimeout = () => {
          const error = this.caseIntakePreparationError(`${label} did not finish within the safety limit.`, 'CASE_INTAKE_PREPARATION_TIMEOUT');
          // Settle this race as a timeout before notifying abort listeners. Otherwise
          // the abort branch can win synchronously and misreport the guardrail as a
          // user-requested stop.
          reject(error);
          try { controller?.abort?.(error); } catch { /* Best-effort cancellation. */ }
        };
        timeoutTimer = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
          ? window.setTimeout(onTimeout, timeout)
          : setTimeout(onTimeout, timeout);
        if (this.caseIntakePreparation) this.caseIntakePreparation.stageTimer = timeoutTimer;
      });
      const abortPromise = new Promise((resolve, reject) => {
        abortHandler = () => reject(this.caseIntakePreparationError('Case preparation was stopped.', 'CASE_INTAKE_PREPARATION_STOPPED'));
        if (controller?.signal?.aborted) abortHandler();
        else controller?.signal?.addEventListener?.('abort', abortHandler, { once: true });
      });
      try {
        const result = await Promise.race([operationPromise, timeoutPromise, abortPromise]);
        this.caseIntakePreparationAssertCurrent(runId);
        return result;
      } finally {
        if (timeoutTimer) {
          if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timeoutTimer);
          else clearTimeout(timeoutTimer);
        }
        controller?.signal?.removeEventListener?.('abort', abortHandler);
        if (this.caseIntakePreparation && Number(this.caseIntakePreparation.runId) === Number(runId)) {
          this.caseIntakePreparation.stageTimer = null;
        }
      }
    },

    setCaseIntakePreparationStage(runId, key, title, message, detail = '') {
      this.caseIntakePreparationAssertCurrent(runId);
      const stageIndex = Math.max(0, (this.caseIntakeRailWorkflowSteps || []).findIndex(step => step.key === key));
      this.caseIntakePreparation = {
        ...(this.caseIntakePreparation || createCaseIntakePreparationState()),
        status: 'running',
        stageKey: key,
        stageIndex: stageIndex + 1,
        stageTitle: title,
        message: message || title,
        detail
      };
    },

    completeCaseIntakePreparationStage(runId, key, message = '') {
      this.caseIntakePreparationAssertCurrent(runId);
      const completedKeys = new Set(Array.isArray(this.caseIntakePreparation?.completedKeys) ? this.caseIntakePreparation.completedKeys : []);
      completedKeys.add(key);
      this.caseIntakePreparation = {
        ...(this.caseIntakePreparation || createCaseIntakePreparationState()),
        completedKeys: Array.from(completedKeys),
        message: message || this.caseIntakePreparation?.message || ''
      };
    },

    caseIntakePreparationFindWorkflowStep(key = '') {
      const row = (this.caseIntakeRailWorkflowSteps || []).find(step => step.key === key);
      if (row?.step) return row;
      const patterns = typeof this.caseWorkflowIntakeStepPatterns === 'function'
        ? this.caseWorkflowIntakeStepPatterns(key)
        : [];
      const match = typeof this.findCaseWorkflowStepByPatterns === 'function'
        ? this.findCaseWorkflowStepByPatterns(patterns)
        : { group: null, step: null, index: -1 };
      const workflowComplete = Boolean(match?.step?.complete && !match?.step?.stale);
      return {
        key,
        match,
        step: match?.step || null,
        complete: workflowComplete,
        workflowComplete,
        workflowStale: Boolean(match?.step?.stale)
      };
    },

    recordCaseIntakePreparationManualCompletion(step = {}, source = 'case_intake_preparation') {
      const tag = normalizeCell(step?.tag || step?.id || step?.stepTag || '').toUpperCase();
      if (!tag) return false;
      const now = new Date().toISOString();
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const workflowState = { ...(this.caseSupportState.workflowState || {}) };
      const manualCompleteByTag = { ...(workflowState.manualCompleteByTag || {}) };
      manualCompleteByTag[tag] = {
        ...(manualCompleteByTag[tag] || {}),
        completedAtUtc: manualCompleteByTag[tag]?.completedAtUtc || now,
        checkedAtUtc: now,
        revision,
        source,
        sourceActionId: source
      };
      this.caseSupportState.workflowState = { ...workflowState, manualCompleteByTag };
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(80);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      return true;
    },

    async markCaseIntakePreparationWorkflowStepCurrent(runId, key, options = {}) {
      this.caseIntakePreparationAssertCurrent(runId);
      let row = this.caseIntakePreparationFindWorkflowStep(key);
      if (!row?.step || row.workflowComplete) return Boolean(row?.workflowComplete);
      const step = row.step;
      const source = options.source || 'case_intake_preparation';
      const manual = options.manual === true;

      if (manual) {
        // This action is an intentional rubber stamp. Write/refresh the manual
        // organizer record directly so a stale prior acknowledgement is never
        // toggled off by a generic completion toggle.
        this.recordCaseIntakePreparationManualCompletion(step, source);
      } else {
        const signalCode = typeof this.getStartingMainTransactionsWorkflowSignalCode === 'function'
          ? this.getStartingMainTransactionsWorkflowSignalCode(step)
          : normalizeCell(step?.signalCode || step?.signal || step?.completionSignal || '');
        if (signalCode && typeof this.recordWorkflowSignal === 'function') {
          this.recordWorkflowSignal(signalCode, source, {
            silent: true,
            revision: Number(this.caseSupportState?.caseDataRevision) || 0,
            checkedAtUtc: new Date().toISOString()
          });
        }
      }

      await this.caseIntakePreparationSettle(runId, 30);
      row = this.caseIntakePreparationFindWorkflowStep(key);
      if (!row?.workflowComplete) {
        // The data/tool verification has already passed. This is a workflow-organizer
        // fallback only; it does not replace or manufacture spreadsheet truth.
        this.recordCaseIntakePreparationManualCompletion(row?.step || step, source);
        await this.caseIntakePreparationSettle(runId, 30);
        row = this.caseIntakePreparationFindWorkflowStep(key);
      }
      return Boolean(row?.workflowComplete);
    },

    async reconcileCaseIntakePreparationWorkflowSteps(runId) {
      const rows = [
        ['starting', 'starting-transactions'],
        ['networks', 'network'],
        ['dates', 'UTC-date and ordering'],
        ['labels', 'main-label']
      ];
      for (const [key, description] of rows) {
        this.caseIntakePreparationAssertCurrent(runId);
        const current = await this.markCaseIntakePreparationWorkflowStepCurrent(runId, key, {
          source: `case_intake_preparation_final_${key}`
        });
        if (!current) {
          throw this.caseIntakePreparationError(
            `The ${description} work is complete, but its workflow step could not be refreshed at the final case revision.`,
            'CASE_INTAKE_PREPARATION_WORKFLOW_RECONCILE'
          );
        }
      }
      return true;
    },

    async stopCaseIntakePreparationConflictingAutomations() {
      if (this.caseManualStartViewportAutoActive && typeof this.stopCaseManualStartViewportAuto === 'function') {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto stopped so intake preparation can start.', { silent: true });
      }
      if (this.caseAutomation?.active || this.caseAutomation?.busy) {
        this.requestCaseAutomationUserStop?.('Workflow Auto stopped so intake preparation can start.');
      }
      if (this.caseManualStartHopInspectorRunning) this.requestCaseManualStartHopInspectorStop?.();
      if (this.caseHopSolverRunning) this.requestCaseHopSolverStop?.();
      const stopTool = (tool, reason) => {
        if (tool && typeof tool.caseBuddyRequestAutomationStop === 'function') {
          try { tool.caseBuddyRequestAutomationStop(reason); } catch (error) { console.warn('Could not stop a conflicting Case Buddy tool.', error); }
        }
      };
      stopTool(this.$refs?.caseHopSolverTool88, 'Intake preparation is starting.');
      stopTool(this.$refs?.caseWorkflowRawDataTool88, 'Intake preparation is starting.');
      stopTool(this.getCaseManualStartHopInspectorTool?.(), 'Intake preparation is starting.');
      this.cancelCaseWorkflowBackgroundAutomationResume?.({ source: 'case-intake-preparation-start' });
      this.clearCaseWorkflowScheduledScrollTimers?.();
      await this.$nextTick();
    },

    requestCaseIntakePreparationStop(message = 'Preparation stopped by user.', options = {}) {
      const state = this.caseIntakePreparation || createCaseIntakePreparationState();
      if (!state.active && options.force !== true) return false;
      try {
        this.caseIntakePreparationAbortController?.abort?.(this.caseIntakePreparationError(message, 'CASE_INTAKE_PREPARATION_STOPPED'));
      } catch {
        // Abort support is best effort; the run-id guard still prevents continuation.
      }
      this.clearCaseIntakePreparationTimers();
      this.caseIntakePreparationAbortController = null;
      this.releaseCaseIntakePreparationScrollLock();
      const result = options.timeout ? 'timeout' : 'stopped';
      this.caseIntakePreparation = {
        ...state,
        active: false,
        stopRequested: true,
        runId: (Number(state.runId) || 0) + 1,
        status: result,
        lastResult: result,
        message,
        detail: 'Any rows already completed remain in the case. A provider request that was already in flight may finish its own cleanup before its tool becomes available again.',
        lastMessage: message,
        finishedAtUtc: new Date().toISOString(),
        overallTimer: null,
        stageTimer: null
      };
      if (!options.silent) this.showCaseToast?.(message);
      return true;
    },

    finishCaseIntakePreparation(runId, result = 'success', message = '', detail = '') {
      if (Number(this.caseIntakePreparation?.runId) !== Number(runId)) return false;
      this.clearCaseIntakePreparationTimers();
      this.caseIntakePreparationAbortController = null;
      this.releaseCaseIntakePreparationScrollLock();
      this.caseIntakePreparation = {
        ...(this.caseIntakePreparation || createCaseIntakePreparationState()),
        active: false,
        stopRequested: false,
        status: result,
        lastResult: result,
        message,
        detail,
        lastMessage: message,
        finishedAtUtc: new Date().toISOString(),
        overallTimer: null,
        stageTimer: null
      };
      return true;
    },

    async prepareCaseIntakeStartingTransactions(runId) {
      this.setCaseIntakePreparationStage(
        runId,
        'starting',
        'Load starting main transactions',
        'Confirming the starting main transactions already present in Main Data…',
        'No rows are imported here. Case Buddy is confirming the root transactions that already exist.'
      );
      const matrix = typeof this.getMatrixForLogicalSheet === 'function'
        ? this.getMatrixForLogicalSheet('main')
        : [];
      const roots = typeof this.caseWorkflowCollectRootTransactions === 'function'
        ? this.caseWorkflowCollectRootTransactions(Array.isArray(matrix) ? matrix : [], { allowNonRootLabels: true })
        : [];
      if (!Array.isArray(roots) || roots.length === 0) {
        throw this.caseIntakePreparationError('No root transaction hashes were found in Main Data.', 'CASE_INTAKE_PREPARATION_NO_ROOTS');
      }
      const row = this.caseIntakePreparationFindWorkflowStep('starting');
      this.confirmStartingMainTransactionsWorkflowStepFromCurrentRows?.(
        row?.step || { tag: 'ADD_MAIN_TRANSACTIONS_TO_CASE' },
        { sourceActionId: 'case_intake_preparation', silent: true }
      );
      await this.caseIntakePreparationSettle(runId, 50);
      const current = await this.markCaseIntakePreparationWorkflowStepCurrent(runId, 'starting', {
        source: 'case_intake_preparation_starting'
      });
      if (!current) {
        throw this.caseIntakePreparationError('Starting transactions were found, but the workflow step could not be recorded as current.', 'CASE_INTAKE_PREPARATION_STARTING_INCOMPLETE');
      }
      this.completeCaseIntakePreparationStage(runId, 'starting', 'Starting transactions confirmed.');
    },

    async prepareCaseIntakeNetworks(runId) {
      this.setCaseIntakePreparationStage(
        runId,
        'networks',
        'Identify transaction networks',
        'Checking Chain Id and Chain Name for every root transaction…',
        'Missing network values will be looked up. Existing network values are preserved.'
      );
      const liveState = this.caseIntakeRailLiveDataState || {};
      if (liveState.networksComplete) {
        const current = await this.markCaseIntakePreparationWorkflowStepCurrent(runId, 'networks', {
          source: 'case_intake_preparation_networks_existing_data'
        });
        if (!current) {
          throw this.caseIntakePreparationError('Network data is complete, but the workflow step could not be recorded as current.', 'CASE_INTAKE_PREPARATION_NETWORK_WORKFLOW');
        }
        this.completeCaseIntakePreparationStage(runId, 'networks', 'Existing transaction network data is already complete.');
        return;
      }
      this.networkLookupFillDateIfMissing = true;
      await this.runCaseIntakePreparationOperation(
        runId,
        'Preparing the network lookup',
        () => this.refreshNetworkLookupRows?.(),
        CASE_INTAKE_PREPARATION_LOCAL_STAGE_TIMEOUT_MS
      );
      await this.caseIntakePreparationSettle(runId, 30);
      if (!this.networkLookupRows.length) {
        throw this.caseIntakePreparationError('No eligible root transactions were available for network lookup.', 'CASE_INTAKE_PREPARATION_NO_NETWORK_ROWS');
      }
      const pendingBefore = Number(this.networkLookupPendingCount) || 0;
      if (pendingBefore > 0) {
        this.selectPendingNetworkLookupRows?.();
        await this.caseIntakePreparationSettle(runId, 20);
        if ((Number(this.networkLookupSelectedCount) || 0) <= 0) {
          throw this.caseIntakePreparationError('Network data is missing, but no root transaction could be selected for lookup.', 'CASE_INTAKE_PREPARATION_NETWORK_SELECTION');
        }
        await this.runCaseIntakePreparationOperation(
          runId,
          'The network lookup',
          () => this.callCaseBuddyOriginalMethod(caseBuddyDateNetworkRawMethods, 'runNetworkDiscoveryLookup', [{
            signal: this.caseIntakePreparationAbortController?.signal,
            source: 'case-intake-preparation',
            silent: true
          }]),
          CASE_INTAKE_PREPARATION_REMOTE_STAGE_TIMEOUT_MS
        );
        await this.runCaseIntakePreparationOperation(
          runId,
          'Refreshing network results',
          () => this.refreshNetworkLookupRows?.(),
          CASE_INTAKE_PREPARATION_LOCAL_STAGE_TIMEOUT_MS
        );
        await this.caseIntakePreparationSettle(runId, 40);
      }
      const pendingAfter = Number(this.networkLookupPendingCount) || 0;
      if (pendingAfter > 0) {
        const noProgress = pendingBefore > 0 && pendingAfter >= pendingBefore;
        throw this.caseIntakePreparationError(
          noProgress
            ? `${pendingAfter} root transaction${pendingAfter === 1 ? '' : 's'} still need network data; the lookup made no progress.`
            : `${pendingAfter} root transaction${pendingAfter === 1 ? '' : 's'} still need network data.`,
          'CASE_INTAKE_PREPARATION_NETWORK_INCOMPLETE'
        );
      }
      const current = await this.markCaseIntakePreparationWorkflowStepCurrent(runId, 'networks', {
        source: 'case_intake_preparation_networks'
      });
      if (!current) {
        throw this.caseIntakePreparationError('Network data is complete, but the workflow step could not be recorded as current.', 'CASE_INTAKE_PREPARATION_NETWORK_WORKFLOW');
      }
      this.completeCaseIntakePreparationStage(runId, 'networks', 'Transaction networks are complete.');
    },

    async prepareCaseIntakeDatesAndOrder(runId) {
      this.setCaseIntakePreparationStage(
        runId,
        'dates',
        'Complete UTC dates and order main entries',
        'Completing UTC dates, then sorting root transaction blocks chronologically…',
        'Date ordering is completed before any main path labels are assigned.'
      );
      const liveState = this.caseIntakeRailLiveDataState || {};
      if (liveState.datesComplete) {
        const current = await this.markCaseIntakePreparationWorkflowStepCurrent(runId, 'dates', {
          source: 'case_intake_preparation_dates_existing_data'
        });
        if (!current) {
          throw this.caseIntakePreparationError('UTC dates and chronological order are complete, but the workflow step could not be recorded as current.', 'CASE_INTAKE_PREPARATION_DATE_WORKFLOW');
        }
        this.completeCaseIntakePreparationStage(runId, 'dates', 'Existing UTC dates and chronological root order are already complete.');
        return;
      }
      this.dateLookupFillBlankNetworks = true;
      await this.runCaseIntakePreparationOperation(
        runId,
        'Preparing the UTC date lookup',
        () => this.refreshDateLookupRows?.(),
        CASE_INTAKE_PREPARATION_LOCAL_STAGE_TIMEOUT_MS
      );
      await this.caseIntakePreparationSettle(runId, 30);
      if (!this.dateLookupRows.length) {
        throw this.caseIntakePreparationError('No eligible root transactions were available for UTC date work.', 'CASE_INTAKE_PREPARATION_NO_DATE_ROWS');
      }
      const pendingBefore = Number(this.dateLookupPendingCount) || 0;
      if (pendingBefore > 0) {
        this.selectPendingDateLookupRows?.();
        await this.caseIntakePreparationSettle(runId, 20);
        if ((Number(this.dateLookupSelectedCount) || 0) <= 0) {
          throw this.caseIntakePreparationError('UTC dates are missing, but no root transaction could be selected for lookup.', 'CASE_INTAKE_PREPARATION_DATE_SELECTION');
        }
        await this.runCaseIntakePreparationOperation(
          runId,
          'The UTC date lookup',
          () => this.callCaseBuddyOriginalMethod(caseBuddyDateNetworkRawMethods, 'runMissingDateLookup', [{
            signal: this.caseIntakePreparationAbortController?.signal,
            source: 'case-intake-preparation',
            silent: true
          }]),
          CASE_INTAKE_PREPARATION_REMOTE_STAGE_TIMEOUT_MS
        );
        await this.runCaseIntakePreparationOperation(
          runId,
          'Refreshing UTC date results',
          () => this.refreshDateLookupRows?.(),
          CASE_INTAKE_PREPARATION_LOCAL_STAGE_TIMEOUT_MS
        );
        await this.caseIntakePreparationSettle(runId, 40);
      }
      const pendingAfter = Number(this.dateLookupPendingCount) || 0;
      if (pendingAfter > 0) {
        const noProgress = pendingBefore > 0 && pendingAfter >= pendingBefore;
        throw this.caseIntakePreparationError(
          noProgress
            ? `${pendingAfter} root transaction${pendingAfter === 1 ? '' : 's'} still need UTC dates; the lookup made no progress.`
            : `${pendingAfter} root transaction${pendingAfter === 1 ? '' : 's'} still need UTC dates.`,
          'CASE_INTAKE_PREPARATION_DATE_INCOMPLETE'
        );
      }
      await this.runCaseIntakePreparationOperation(
        runId,
        'Sorting root transactions by UTC date and keeping path labels attached',
        () => this.reconcileCaseRootChronologyAndPathLabels({
          source: 'case-intake-preparation',
          sort: true,
          silent: true
        }),
        CASE_INTAKE_PREPARATION_LOCAL_STAGE_TIMEOUT_MS
      );
      await this.runCaseIntakePreparationOperation(
        runId,
        'Verifying UTC date order',
        () => this.refreshDateLookupRows?.(),
        CASE_INTAKE_PREPARATION_LOCAL_STAGE_TIMEOUT_MS
      );
      await this.caseIntakePreparationSettle(runId, 50);
      if (!this.dateLookupSortPreview?.chronologyPass) {
        throw this.caseIntakePreparationError('Root transaction blocks could not be confirmed in UTC date order.', 'CASE_INTAKE_PREPARATION_DATE_ORDER');
      }
      const current = await this.markCaseIntakePreparationWorkflowStepCurrent(runId, 'dates', {
        source: 'case_intake_preparation_dates'
      });
      if (!current) {
        throw this.caseIntakePreparationError('UTC dates and chronological order are complete, but the workflow step could not be recorded as current.', 'CASE_INTAKE_PREPARATION_DATE_WORKFLOW');
      }
      this.completeCaseIntakePreparationStage(runId, 'dates', 'UTC dates are complete and root transactions are in chronological order.');
    },

    async prepareCaseIntakeMainLabels(runId) {
      this.setCaseIntakePreparationStage(
        runId,
        'labels',
        'Assign numeric main transaction labels',
        'Building and applying whole-number labels to the chronologically ordered roots…',
        'Only root lead rows receive numeric labels. Continuation rows remain subordinate to their entry.'
      );
      await this.reconcileCaseRootChronologyAndPathLabels({
        source: 'case-intake-preparation-label-preflight',
        sort: false,
        silent: true
      });
      await this.$nextTick();
      const liveState = this.caseIntakeRailLiveDataState || {};
      if (liveState.labelsComplete) {
        const current = await this.markCaseIntakePreparationWorkflowStepCurrent(runId, 'labels', {
          source: 'case_intake_preparation_labels_existing_data'
        });
        if (!current) {
          throw this.caseIntakePreparationError('Main path labels are complete, but the workflow step could not be recorded as current.', 'CASE_INTAKE_PREPARATION_LABEL_WORKFLOW');
        }
        this.completeCaseIntakePreparationStage(runId, 'labels', 'Existing main transaction path labels are already complete.');
        return;
      }
      this.mainPathLabelClearContinuationRows = true;
      await this.runCaseIntakePreparationOperation(
        runId,
        'Preparing numeric main labels',
        () => this.refreshMainPathLabelRows?.(),
        CASE_INTAKE_PREPARATION_LOCAL_STAGE_TIMEOUT_MS
      );
      await this.caseIntakePreparationSettle(runId, 30);
      if (!this.mainPathLabelRows.length) {
        throw this.caseIntakePreparationError('No eligible root transactions were available for main path labels.', 'CASE_INTAKE_PREPARATION_NO_LABEL_ROWS');
      }
      this.resetMainPathLabelProposals?.();
      this.refreshMainPathLabelRowStatuses?.();
      await this.caseIntakePreparationSettle(runId, 30);
      const reviewCount = Number(this.mainPathLabelNeedsReviewCount) || 0;
      if (reviewCount > 0) {
        throw this.caseIntakePreparationError(`${reviewCount} root transaction${reviewCount === 1 ? '' : 's'} need label review before numeric labels can be applied.`, 'CASE_INTAKE_PREPARATION_LABEL_REVIEW');
      }
      if ((Number(this.mainPathLabelChangeCount) || 0) > 0) {
        await this.runCaseIntakePreparationOperation(
          runId,
          'Applying numeric main labels',
          () => this.callCaseBuddyOriginalMethod(caseBuddyDateNetworkRawMethods, 'applyMainPathLabels', [{
            source: 'case-intake-preparation',
            silent: true
          }]),
          CASE_INTAKE_PREPARATION_LOCAL_STAGE_TIMEOUT_MS
        );
        await this.runCaseIntakePreparationOperation(
          runId,
          'Verifying numeric main labels',
          () => this.refreshMainPathLabelRows?.(),
          CASE_INTAKE_PREPARATION_LOCAL_STAGE_TIMEOUT_MS
        );
        await this.caseIntakePreparationSettle(runId, 50);
      }
      if ((Number(this.mainPathLabelNeedsReviewCount) || 0) > 0 || (Number(this.mainPathLabelChangeCount) || 0) > 0) {
        throw this.caseIntakePreparationError('Numeric main labels could not be verified after the apply step.', 'CASE_INTAKE_PREPARATION_LABEL_INCOMPLETE');
      }
      const current = await this.markCaseIntakePreparationWorkflowStepCurrent(runId, 'labels', {
        source: 'case_intake_preparation_labels'
      });
      if (!current) {
        throw this.caseIntakePreparationError('Numeric main labels are current, but the workflow step could not be recorded as current.', 'CASE_INTAKE_PREPARATION_LABEL_WORKFLOW');
      }
      this.completeCaseIntakePreparationStage(runId, 'labels', 'Numeric main transaction labels are current.');
    },

    async prepareCaseIntakeEarlyReview(runId) {
      this.setCaseIntakePreparationStage(
        runId,
        'casedetails',
        'Review case details and early narrative',
        'Recording the early case-details and narrative review as complete…',
        'This is the requested workflow rubber stamp; it changes organizer metadata, not spreadsheet rows.'
      );
      // Spreadsheet mutations can increment the broad case revision after an
      // individual stage has already turned green. Wait for those updates to settle,
      // then refresh the data-backed intake steps at the final revision before adding
      // the requested early-review acknowledgement.
      await this.waitForCaseIntakePreparationRevisionQuiet(runId);
      await this.reconcileCaseIntakePreparationWorkflowSteps(runId);

      const row = this.caseIntakePreparationFindWorkflowStep('casedetails');
      if (!row?.step) {
        throw this.caseIntakePreparationError('The case-details and early-narrative workflow step could not be found.', 'CASE_INTAKE_PREPARATION_REVIEW_STEP_MISSING');
      }
      if (!row.workflowComplete) {
        await this.markCaseIntakePreparationWorkflowStepCurrent(runId, 'casedetails', {
          source: 'case_intake_preparation_early_review',
          manual: true
        });
      }
      const refreshed = this.caseIntakePreparationFindWorkflowStep('casedetails');
      if (!refreshed?.workflowComplete) {
        throw this.caseIntakePreparationError('The early case-details review could not be recorded.', 'CASE_INTAKE_PREPARATION_REVIEW_INCOMPLETE');
      }
      this.completeCaseIntakePreparationStage(runId, 'casedetails', 'Case details and early narrative review recorded.');
    },

    async runCaseIntakePreparationPipeline(runId) {
      await this.prepareCaseIntakeStartingTransactions(runId);
      await this.prepareCaseIntakeNetworks(runId);
      await this.prepareCaseIntakeDatesAndOrder(runId);
      await this.prepareCaseIntakeMainLabels(runId);
      await this.prepareCaseIntakeEarlyReview(runId);
      await this.caseIntakePreparationSettle(runId, 80);
      this.queueCaseSummaryRefresh?.(80);
      this.scheduleCaseInputFlowStoryRefresh?.(120, { force: true });
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(80);
      return true;
    },

    async startCaseIntakePreparation() {
      if (!this.caseIntakeRailVisible) {
        this.showCaseToast?.('Load at least one root transaction before preparing the intake workflow.');
        return false;
      }
      if (!this.caseIntakeRailWorkflowReady) {
        this.showCaseToast?.('The workflow assistant is still loading.');
        return false;
      }
      if (this.caseIntakeRailAllComplete) {
        this.showCaseToast?.('The first five workflow steps are already complete.');
        return true;
      }
      if (this.caseIntakePreparation?.active) return false;

      if (this.caseIntakeRailOtherAutomationActive) {
        await this.stopCaseIntakePreparationConflictingAutomations();
        await this.$nextTick();
      }
      if (this.caseIntakeRailOtherAutomationActive) {
        this.showCaseToast?.('Another Case Buddy automation or operation is still running. Stop it before preparing the case.');
        return false;
      }

      this.showNetworkLookupModal = false;
      this.showDateLookupModal = false;
      this.showMainPathLabelModal = false;
      this.clearCaseWorkflowScheduledScrollTimers?.();

      const previousRunId = Number(this.caseIntakePreparation?.runId) || 0;
      const runId = previousRunId + 1;
      const controller = typeof AbortController !== 'undefined' ? Object.freeze(new AbortController()) : null;
      this.caseIntakePreparationAbortController = controller;
      this.caseIntakePreparation = createCaseIntakePreparationState({
        active: true,
        runId,
        status: 'running',
        message: 'Preparing the first five workflow steps…',
        detail: 'Case Buddy will preserve completed work and stop if a stage cannot be verified.',
        startedAtMs: Date.now()
      });
      this.applyCaseIntakePreparationScrollLock();

      const overallStop = () => {
        if (Number(this.caseIntakePreparation?.runId) !== runId || !this.caseIntakePreparation?.active) return;
        this.requestCaseIntakePreparationStop(
          'Preparation stopped at its overall safety limit. Completed stages remain in the case.',
          { timeout: true }
        );
      };
      const overallTimer = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
        ? window.setTimeout(overallStop, CASE_INTAKE_PREPARATION_OVERALL_TIMEOUT_MS)
        : setTimeout(overallStop, CASE_INTAKE_PREPARATION_OVERALL_TIMEOUT_MS);
      this.caseIntakePreparation.overallTimer = overallTimer;

      await this.$nextTick();
      this.$refs.caseIntakePreparationStopButton?.focus?.();

      try {
        await this.runCaseIntakePreparationPipeline(runId);
        this.caseIntakePreparationAssertCurrent(runId);
        await this.$nextTick();
        const complete = this.caseIntakeRailAllComplete;
        if (!complete) {
          throw this.caseIntakePreparationError('The data work finished, but one or more of the first five workflow steps did not turn current.', 'CASE_INTAKE_PREPARATION_WORKFLOW_INCOMPLETE');
        }
        const message = 'Case intake is ready for Hydrate / Visual.';
        this.finishCaseIntakePreparation(
          runId,
          'success',
          message,
          'Networks, UTC dates, chronological root order, main path labels, and the early review are complete.'
        );
        this.showCaseToast?.(message);
        return true;
      } catch (error) {
        if (Number(this.caseIntakePreparation?.runId) !== runId) return false;
        const stopped = error?.code === 'CASE_INTAKE_PREPARATION_STOPPED';
        const timeout = error?.code === 'CASE_INTAKE_PREPARATION_TIMEOUT';
        const message = stopped
          ? 'Case preparation stopped. Completed stages remain in the case.'
          : (error?.message || 'Case preparation stopped before all intake steps were complete.');
        this.finishCaseIntakePreparation(
          runId,
          timeout ? 'timeout' : (stopped ? 'stopped' : 'error'),
          message,
          'Review the highlighted step, then retry the preparation button or use that workflow tool manually.'
        );
        this.showCaseToast?.(message);
        return false;
      } finally {
        this.showNetworkLookupModal = false;
        this.showDateLookupModal = false;
        this.showMainPathLabelModal = false;
      }
    },

    persistCaseInputFlowStoryPreference(key, value) {
      if (typeof window === 'undefined' || !window.localStorage || !key) return;
      try {
        window.localStorage.setItem(key, value ? 'true' : 'false');
      } catch (error) {
        console.warn('Could not save the Case Buddy visual overview preference.', error);
      }
    },

    dismissCaseInputFlowStory() {
      if (!this.caseInputFlowStoryEnabled) return;
      this.caseInputFlowStoryDismissed = true;
      this.persistCaseInputFlowStoryPreference(CASE_INPUT_FLOW_STORY_DISMISSED_STORAGE_KEY, true);
    },

    restoreCaseInputFlowStory() {
      if (!this.caseInputFlowStoryEnabled) return;
      this.caseInputFlowStoryDismissed = false;
      this.persistCaseInputFlowStoryPreference(CASE_INPUT_FLOW_STORY_DISMISSED_STORAGE_KEY, false);
    },

    toggleCaseInputFlowStoryHeat() {
      this.caseInputFlowStoryHeatEnabled = !this.caseInputFlowStoryHeatEnabled;
      this.persistCaseInputFlowStoryPreference(CASE_INPUT_FLOW_STORY_HEAT_STORAGE_KEY, this.caseInputFlowStoryHeatEnabled);
    },

    openStartingMainTransactionsFromFlowStory() {
      this.caseInputFlowStoryStartingModalOpen = true;
      this.caseSimpleLaunchReturnAfterStartingModal = false;
      this.emptyCaseStarterNudgeLaunchPending = false;
      this.openStartingMainTransactionsModal();
    },

    async openCaseInputFlowStoryInManualSolve(flow = {}) {
      const descriptor = flow && typeof flow === 'object' ? flow : {};
      const hash = normalizeCell(descriptor.hash);
      if (!hash) {
        this.showCaseToast?.('This flow does not have a root transaction hash yet.');
        return false;
      }
      if (this.casePageHydrating || this.caseIntakeRailOtherAutomationActive || this.caseManualStartVisualizerRunning) {
        this.showCaseToast?.('Finish the current Case Buddy operation before opening this graph.');
        return false;
      }

      return this.openCaseManualStartVisualizer({
        source: 'flow-story',
        focusRoot: {
          hash,
          pathLabel: normalizeCell(descriptor.pathLabel),
          rootRowIndex: Number.isFinite(Number(descriptor.rootRowIndex))
            ? Number(descriptor.rootRowIndex)
            : null
        }
      });
    },

    resetCaseInputFlowStory(options = {}) {
      this.clearCaseInputFlowStoryLazyTimers({ keepCache: false, keepRefresh: false });
      this.disconnectCaseInputFlowStoryObserver();
      this.caseInputFlowStoryStatus = 'idle';
      this.caseInputFlowStoryError = '';
      this.caseInputFlowStoryDescriptors = [];
      this.caseInputFlowStoryVisualCache = {};
      this.caseInputFlowStoryModel = null;
      this.caseInputFlowStoryLastBuiltRevision = -1;
      this.caseInputFlowStoryStartingModalOpen = false;
      if (options.keepPreference !== true) {
        this.caseInputFlowStoryDismissed = readCaseBuddyBooleanPreference(CASE_INPUT_FLOW_STORY_DISMISSED_STORAGE_KEY, false);
        this.caseInputFlowStoryHeatEnabled = readCaseBuddyBooleanPreference(
          CASE_INPUT_FLOW_STORY_HEAT_STORAGE_KEY,
          CASE_INPUT_FLOW_STORY_HEAT_DEFAULT_ENABLED
        );
      }
    },

    scheduleCaseInputFlowStoryRefresh(delay = 160, options = {}) {
      if (!this.caseInputFlowStoryVisible) return;
      if (this.caseInputFlowStoryRefreshTimer && typeof window !== 'undefined') {
        window.clearTimeout(this.caseInputFlowStoryRefreshTimer);
      }
      const run = () => {
        this.caseInputFlowStoryRefreshTimer = null;
        this.refreshCaseInputFlowStory(options);
      };
      this.caseInputFlowStoryRefreshTimer = typeof window !== 'undefined'
        ? window.setTimeout(run, Math.max(0, Number(delay) || 0))
        : setTimeout(run, Math.max(0, Number(delay) || 0));
    },

    refreshCaseInputFlowStory(options = {}) {
      if (!this.caseInputFlowStoryVisible) return false;
      if (!this.projectFileReadyState?.input && options.force !== true) {
        this.caseInputFlowStoryStatus = 'loading';
        this.scheduleCaseInputFlowStoryRefresh(260, { ...options, force: true });
        return false;
      }

      const refreshToken = Number(this.caseInputFlowStoryRefreshToken || 0) + 1;
      this.caseInputFlowStoryRefreshToken = refreshToken;
      this.caseInputFlowStoryStatus = 'loading';
      this.caseInputFlowStoryError = '';

      try {
        const matrix = typeof this.getMatrixForLogicalSheet === 'function'
          ? this.getMatrixForLogicalSheet('main')
          : [];
        const model = this.buildCaseInputFlowStoryModel(Array.isArray(matrix) ? matrix : []);
        if (refreshToken !== this.caseInputFlowStoryRefreshToken) return false;

        this.clearCaseInputFlowStoryLazyTimers({ keepCache: false, keepRefresh: true });
        this.disconnectCaseInputFlowStoryObserver();
        this.caseInputFlowStoryModel = model;
        this.caseInputFlowStoryDescriptors = model.descriptors;
        this.caseInputFlowStoryVisualCache = {};
        this.caseInputFlowStoryLastBuiltRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
        this.caseInputFlowStoryStatus = 'ready';

        this.$nextTick(() => {
          this.installCaseInputFlowStoryObserver();
          this.warmCaseInputFlowStoryVisibleCards({ includeFirst: true });
        });
        return true;
      } catch (error) {
        console.error('Could not build the Case Buddy visual Main Data overview.', error);
        this.caseInputFlowStoryStatus = 'error';
        this.caseInputFlowStoryError = error?.message || 'The visual overview could not be built from Main Data.';
        return false;
      }
    },

    buildCaseInputFlowStoryModel(matrix = []) {
      const sourceRows = Array.isArray(matrix) ? matrix : [];
      const columnCount = Math.max(
        CASE_MANUAL_START_COLUMN_COUNT,
        ...sourceRows.slice(0, 200).map(row => Array.isArray(row) ? row.length : 0)
      );
      const rows = normalizeCaseMatrix(sourceRows, columnCount);
      const entries = this.buildCaseInputFlowStoryEntries(rows);
      let roots = typeof this.caseWorkflowCollectRootTransactions === 'function'
        ? this.caseWorkflowCollectRootTransactions(rows)
        : [];

      if (!roots.length && entries.length) {
        roots = entries
          .filter(entry => !entry.fromHashKey)
          .map(entry => ({
            row: entry.row,
            rowIndex: entry.matrixRowIndex,
            spreadsheetRowNumber: entry.matrixRowIndex + 1,
            pathLabel: entry.pathLabel,
            pathKey: this.caseWorkflowRootIdentityKey?.(entry.pathLabel) || entry.pathLabel.toLowerCase(),
            hash: entry.hash,
            hashKey: entry.hashKey,
            fromAddress: entry.fromAddress,
            toAddress: entry.toAddress,
            toAddressKey: this.caseWorkflowRootAddressKey?.(entry.toAddress) || entry.toAddress.toLowerCase(),
            fromHash: entry.fromHash,
            continuesAs: entry.continuesAs
          }));
      }

      roots = roots
        .filter(root => root && root.hash)
        .sort((left, right) => Number(left.rowIndex) - Number(right.rowIndex));

      const descriptors = [];
      const segmentsByKey = {};
      roots.forEach((root, index) => {
        const startRowIndex = Math.max(0, Number(root.rowIndex) || 0);
        const nextRootRowIndex = index + 1 < roots.length ? Number(roots[index + 1].rowIndex) : rows.length;
        const segmentEntries = entries.filter(entry => entry.matrixRowIndex >= startRowIndex && entry.matrixRowIndex < nextRootRowIndex);
        let rootEntry = segmentEntries.find(entry => entry.matrixRowIndex === startRowIndex) || segmentEntries[0] || null;
        if (!rootEntry) rootEntry = this.buildCaseInputFlowStoryEntry(root.row || [], startRowIndex);
        if (!rootEntry?.hash) return;

        const rootKey = typeof this.caseWorkflowRootInstanceKey === 'function'
          ? this.caseWorkflowRootInstanceKey(root)
          : `${rootEntry.hashKey}|${rootEntry.pathLabel}|row:${startRowIndex}`;
        const key = `main-flow-${rootKey || `${index}-${rootEntry.hashKey}`}`;
        const rootAmount = this.caseInputFlowStoryEntryAmountToken(rootEntry, { preferDirect: true });
        const pathLabel = normalizeCell(root.pathLabel || rootEntry.pathLabel);
        const descriptor = {
          key,
          order: index,
          rootRowIndex: startRowIndex,
          displayLabel: pathLabel || `Root ${index + 1}`,
          pathLabel,
          hash: rootEntry.hash,
          shortHash: this.caseFileBrowserShortHash(rootEntry.hash),
          toAddress: rootEntry.toAddress,
          shortToAddress: this.caseFileBrowserShortAddress(rootEntry.toAddress),
          amountDisplay: this.caseInputFlowStoryFormatAmountToken(rootAmount.amount, rootAmount.token),
          entryCount: segmentEntries.length
        };
        descriptors.push(descriptor);
        segmentsByKey[key] = {
          key,
          root,
          rootEntry,
          entries: segmentEntries.length ? segmentEntries : [rootEntry],
          startRowIndex,
          endRowIndex: nextRootRowIndex,
          descriptor
        };
      });

      return {
        rows,
        entries,
        descriptors,
        segmentsByKey,
        builtAtUtc: new Date().toISOString(),
        revision: Number(this.caseSupportState?.caseDataRevision) || 0
      };
    },

    buildCaseInputFlowStoryEntries(rows = []) {
      const sourceRows = Array.isArray(rows) ? rows : [];
      const entries = [];
      for (let matrixRowIndex = 0; matrixRowIndex < sourceRows.length; matrixRowIndex += 1) {
        const row = sourceRows[matrixRowIndex];
        if (!Array.isArray(row)) continue;
        if (this.caseManualIsHeaderRow?.(row) || this.caseFileBrowserPreviewRowLooksLikeHeader?.(row)) continue;
        if (!normalizeCell(row[CASE_MANUAL_START_COL.TX_HASH])) continue;

        let nextEntryRowIndex = matrixRowIndex + 1;
        while (
          nextEntryRowIndex < sourceRows.length
          && !normalizeCell(sourceRows[nextEntryRowIndex]?.[CASE_MANUAL_START_COL.TX_HASH])
        ) {
          nextEntryRowIndex += 1;
        }
        let meaningfulEntryEnd = nextEntryRowIndex;
        while (
          meaningfulEntryEnd > matrixRowIndex + 1
          && !rowHasMeaningfulData(sourceRows[meaningfulEntryEnd - 1] || [])
        ) {
          meaningfulEntryEnd -= 1;
        }
        const entryRows = sourceRows.slice(matrixRowIndex, meaningfulEntryEnd);
        const entry = this.buildCaseInputFlowStoryEntry(row, matrixRowIndex, { entryRows });
        if (entry) entries.push(entry);
        matrixRowIndex = nextEntryRowIndex - 1;
      }
      return entries;
    },

    buildCaseInputFlowStoryEntry(row = [], matrixRowIndex = 0, options = {}) {
      if (!Array.isArray(row)) return null;
      if (this.caseManualIsHeaderRow?.(row) || this.caseFileBrowserPreviewRowLooksLikeHeader?.(row)) return null;
      const hash = normalizeCell(row[CASE_MANUAL_START_COL.TX_HASH]);
      if (!hash) return null;
      const entryRows = (Array.isArray(options.entryRows) && options.entryRows.length ? options.entryRows : [row])
        .filter(candidate => Array.isArray(candidate));
      const firstValue = (columnIndex) => {
        for (const candidate of entryRows) {
          const value = normalizeCell(candidate?.[columnIndex]);
          if (value) return value;
        }
        return '';
      };
      const valuesForColumn = (columnIndex) => Array.from(new Set(
        entryRows.map(candidate => normalizeCell(candidate?.[columnIndex])).filter(Boolean)
      ));
      const pathLabel = firstValue(CASE_MANUAL_START_COL.PATH_LABEL);
      const fromHash = firstValue(CASE_MANUAL_START_COL.FROM_HASH);
      const continuationValues = valuesForColumn(CASE_MANUAL_START_COL.CONTINUES_AS);
      const continuesAs = continuationValues.join(', ');
      const toAddress = firstValue(CASE_MANUAL_START_COL.TO_ADDRESS);
      const hashKey = this.caseFileBrowserHashKey(hash);
      const toAddressKey = this.caseManualNormalizeAddressKey?.(toAddress) || toAddress.toLowerCase();
      const pathEnding = entryRows.some(candidate => (
        /\bpath\s+ends\b/iu.test(normalizeCell(candidate?.[CASE_MANUAL_START_COL.CONTINUES_AS]))
        || /\bpath\s+ends\b/iu.test(normalizeCell(candidate?.[CASE_MANUAL_START_COL.SYSTEM_NOTES]))
      ));
      return {
        key: `entry-row-${matrixRowIndex}-${hashKey}-${toAddressKey || 'no-target'}`,
        row,
        rows: entryRows,
        matrixRowIndex,
        matrixEndRowIndex: Math.max(matrixRowIndex, matrixRowIndex + entryRows.length - 1),
        pathLabel,
        hash,
        hashKey,
        shortHash: this.caseFileBrowserShortHash(hash),
        fromAddress: firstValue(CASE_MANUAL_START_COL.FROM_ADDRESS),
        toAddress,
        date: firstValue(CASE_MANUAL_START_COL.DATE),
        amount: firstValue(CASE_MANUAL_START_COL.AMOUNT),
        token: firstValue(CASE_MANUAL_START_COL.TOKEN),
        usdReference: firstValue(7),
        toAttribution: firstValue(CASE_MANUAL_START_COL.ATTRIBUTION),
        fundsFollowed: firstValue(CASE_MANUAL_START_COL.FUNDS_FOLLOWED),
        fromHash,
        fromHashKey: this.caseFileBrowserHashKey(fromHash),
        continuesAs,
        childHashKeys: Array.from(new Set(
          continuationValues.flatMap(value => this.caseFileBrowserExtractContinuationHashKeys(value))
        )),
        pathEnding
      };
    },

    caseInputFlowStoryEntryAmountToken(entry = {}, options = {}) {
      const directAmount = parsePathOperationBridgeNumber(entry.amount);
      const directToken = normalizeCell(entry.token).toUpperCase() || extractPathOperationBridgeToken(entry.amount);
      const followedAmount = parsePathOperationBridgeNumber(entry.fundsFollowed);
      const followedToken = extractPathOperationBridgeToken(entry.fundsFollowed) || directToken;
      const preferDirect = options.preferDirect === true;
      const amount = preferDirect
        ? (Number.isFinite(directAmount) ? directAmount : followedAmount)
        : (Number.isFinite(followedAmount) ? followedAmount : directAmount);
      const token = preferDirect
        ? (directToken || followedToken)
        : (followedToken || directToken);
      return {
        amount: Number.isFinite(Number(amount)) ? Number(amount) : null,
        token: normalizeCell(token).toUpperCase()
      };
    },

    caseInputFlowStoryFormatAmountToken(amount = null, token = '') {
      if (!Number.isFinite(Number(amount)) || !normalizeCell(token)) return '';
      if (typeof this.caseManualSolveEvaluationFormatAmountToken === 'function') {
        return this.caseManualSolveEvaluationFormatAmountToken(Number(amount), normalizeCell(token));
      }
      return formatPathOperationBridgeAmount(Number(amount), normalizeCell(token));
    },

    installCaseInputFlowStoryObserver() {
      this.disconnectCaseInputFlowStoryObserver();
      if (!this.caseInputFlowStoryVisible) return;
      const listElement = this.$refs.caseInputFlowStoryList;
      if (!listElement || typeof listElement.querySelectorAll !== 'function') return;
      const cards = Array.from(listElement.querySelectorAll('[data-case-input-flow-key]'));
      if (!cards.length) return;

      if (typeof IntersectionObserver === 'function') {
        this.caseInputFlowStoryObserver = new IntersectionObserver((observedEntries) => {
          observedEntries.forEach((observed) => {
            if (!observed.isIntersecting) return;
            const key = observed.target?.getAttribute?.('data-case-input-flow-key') || '';
            if (!key) return;
            this.requestCaseInputFlowStoryVisual(key, { reason: 'visible', delay: 20 });
            const state = this.caseInputFlowStoryFlowState(key);
            if (state.status === 'ready') this.caseInputFlowStoryObserver?.unobserve?.(observed.target);
          });
        }, {
          root: listElement,
          rootMargin: '280px 0px',
          threshold: 0.01
        });
        cards.forEach(card => this.caseInputFlowStoryObserver.observe(card));
      }
    },

    disconnectCaseInputFlowStoryObserver() {
      if (this.caseInputFlowStoryObserver && typeof this.caseInputFlowStoryObserver.disconnect === 'function') {
        this.caseInputFlowStoryObserver.disconnect();
      }
      this.caseInputFlowStoryObserver = null;
    },

    onCaseInputFlowStoryScroll() {
      if (this.caseInputFlowStoryWarmupTimer && typeof window !== 'undefined') {
        window.clearTimeout(this.caseInputFlowStoryWarmupTimer);
      }
      const run = () => {
        this.caseInputFlowStoryWarmupTimer = null;
        this.warmCaseInputFlowStoryVisibleCards();
      };
      this.caseInputFlowStoryWarmupTimer = typeof window !== 'undefined'
        ? window.setTimeout(run, 90)
        : setTimeout(run, 90);
    },

    warmCaseInputFlowStoryVisibleCards(options = {}) {
      if (!this.caseInputFlowStoryVisible) return;
      const listElement = this.$refs.caseInputFlowStoryList;
      const descriptors = Array.isArray(this.caseInputFlowStoryDescriptors) ? this.caseInputFlowStoryDescriptors : [];
      if (!descriptors.length) return;

      const visibleKeys = [];
      if (listElement && typeof listElement.querySelectorAll === 'function' && typeof listElement.getBoundingClientRect === 'function') {
        const listRect = listElement.getBoundingClientRect();
        Array.from(listElement.querySelectorAll('[data-case-input-flow-key]')).forEach((element) => {
          const rect = element.getBoundingClientRect();
          if (rect.bottom >= listRect.top - 280 && rect.top <= listRect.bottom + 280) {
            const key = element.getAttribute('data-case-input-flow-key');
            if (key) visibleKeys.push(key);
          }
        });
      }
      if (options.includeFirst === true) {
        descriptors.slice(0, 2).forEach(flow => {
          if (!visibleKeys.includes(flow.key)) visibleKeys.push(flow.key);
        });
      }
      if (!visibleKeys.length) visibleKeys.push(...descriptors.slice(0, 2).map(flow => flow.key));
      visibleKeys.slice(0, 8).forEach((key, index) => {
        this.requestCaseInputFlowStoryVisual(key, { reason: 'warm-visible', delay: 25 + (index * 35) });
      });
    },

    requestCaseInputFlowStoryVisual(key = '', options = {}) {
      const cleanKey = String(key || '').trim();
      const segment = this.caseInputFlowStoryModel?.segmentsByKey?.[cleanKey];
      if (!cleanKey || !segment || !this.caseInputFlowStoryVisible) return;
      const cached = this.caseInputFlowStoryFlowState(cleanKey);
      if (!options.force && (cached.status === 'ready' || cached.status === 'loading')) return;

      const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      this.caseInputFlowStoryBuildTokens = { ...(this.caseInputFlowStoryBuildTokens || {}), [cleanKey]: token };
      this.caseInputFlowStoryVisualCache = {
        ...(this.caseInputFlowStoryVisualCache || {}),
        [cleanKey]: {
          status: 'loading',
          visual: cached.visual || null,
          error: '',
          reason: options.reason || 'lazy-load'
        }
      };

      const existingTimer = this.caseInputFlowStoryBuildTimers?.[cleanKey];
      if (existingTimer && typeof window !== 'undefined') window.clearTimeout(existingTimer);
      const run = () => {
        if (this.caseInputFlowStoryBuildTokens?.[cleanKey] !== token || !this.caseInputFlowStoryVisible) return;
        try {
          const visual = this.buildCaseInputFlowStoryVisual(segment);
          this.caseInputFlowStoryVisualCache = {
            ...(this.caseInputFlowStoryVisualCache || {}),
            [cleanKey]: {
              status: 'ready',
              visual,
              error: '',
              loadedAtUtc: new Date().toISOString()
            }
          };
          const cards = Array.from(this.$refs.caseInputFlowStoryList?.querySelectorAll?.('[data-case-input-flow-key]') || []);
          const card = cards.find(candidate => candidate.getAttribute?.('data-case-input-flow-key') === cleanKey) || null;
          if (card) this.caseInputFlowStoryObserver?.unobserve?.(card);
        } catch (error) {
          console.warn(`Could not lazily build Case Buddy flow ${cleanKey}.`, error);
          this.caseInputFlowStoryVisualCache = {
            ...(this.caseInputFlowStoryVisualCache || {}),
            [cleanKey]: {
              status: 'error',
              visual: null,
              error: error?.message || 'Could not build this flow.'
            }
          };
        } finally {
          const timers = { ...(this.caseInputFlowStoryBuildTimers || {}) };
          delete timers[cleanKey];
          this.caseInputFlowStoryBuildTimers = timers;
        }
      };
      const timer = typeof window !== 'undefined'
        ? window.setTimeout(run, Math.max(0, Number(options.delay) || 0))
        : setTimeout(run, Math.max(0, Number(options.delay) || 0));
      this.caseInputFlowStoryBuildTimers = { ...(this.caseInputFlowStoryBuildTimers || {}), [cleanKey]: timer };
    },

    clearCaseInputFlowStoryLazyTimers(options = {}) {
      if (options.keepRefresh !== true && this.caseInputFlowStoryRefreshTimer) {
        if (typeof window !== 'undefined') window.clearTimeout(this.caseInputFlowStoryRefreshTimer);
        else clearTimeout(this.caseInputFlowStoryRefreshTimer);
        this.caseInputFlowStoryRefreshTimer = null;
      }
      if (this.caseInputFlowStoryWarmupTimer) {
        if (typeof window !== 'undefined') window.clearTimeout(this.caseInputFlowStoryWarmupTimer);
        else clearTimeout(this.caseInputFlowStoryWarmupTimer);
        this.caseInputFlowStoryWarmupTimer = null;
      }
      Object.values(this.caseInputFlowStoryBuildTimers || {}).forEach((timer) => {
        if (!timer) return;
        if (typeof window !== 'undefined') window.clearTimeout(timer);
        else clearTimeout(timer);
      });
      this.caseInputFlowStoryBuildTimers = {};
      this.caseInputFlowStoryBuildTokens = {};
      if (options.keepCache !== true) {
        this.caseInputFlowStoryVisualCache = {};
      } else {
        this.caseInputFlowStoryVisualCache = Object.fromEntries(
          Object.entries(this.caseInputFlowStoryVisualCache || {}).map(([key, state]) => [
            key,
            state?.status === 'loading'
              ? { ...state, status: state.visual ? 'ready' : 'idle', error: '' }
              : state
          ])
        );
      }
    },

    caseInputFlowStoryFlowState(key = '') {
      return this.caseInputFlowStoryVisualCache?.[key] || { status: 'idle', visual: null, error: '' };
    },

    caseInputFlowStoryVisualFor(key = '') {
      const state = this.caseInputFlowStoryFlowState(key);
      return state.status === 'ready' ? state.visual : null;
    },

    caseInputFlowStoryFlowIsLoading(key = '') {
      return this.caseInputFlowStoryFlowState(key).status === 'loading';
    },

    caseInputFlowStoryFlowHasError(key = '') {
      return this.caseInputFlowStoryFlowState(key).status === 'error';
    },

    buildCaseInputFlowStoryVisual(segment = {}) {
      const allEntries = Array.isArray(segment.entries) ? segment.entries : [];
      const rootEntry = segment.rootEntry || allEntries[0] || null;
      if (!rootEntry) throw new Error('No root entry was available for this flow.');
      const entries = allEntries.slice(0, CASE_INPUT_FLOW_STORY_MAX_ENTRIES_PER_FLOW);
      if (!entries.some(entry => entry.key === rootEntry.key)) entries.unshift(rootEntry);

      const addressKey = (value = '') => (
        this.caseManualNormalizeAddressKey?.(value)
        || normalizeCell(value).toLowerCase().replace(/\s+/gu, '')
      );
      const hashLookupKeys = (value = '') => new Set(this.caseFileBrowserHashLookupKeys(value));
      const hashValuesOverlap = (left = '', right = '') => {
        const leftKeys = hashLookupKeys(left);
        if (!leftKeys.size) return false;
        return this.caseFileBrowserHashLookupKeys(right).some(key => leftKeys.has(key));
      };
      const continuationMentions = (entry = {}, target = {}) => (
        (entry.childHashKeys || []).some(value => hashValuesOverlap(value, target.hash))
      );
      const pathAffinityScore = (left = '', right = '') => {
        const leftKey = normalizeCell(left).toLowerCase();
        const rightKey = normalizeCell(right).toLowerCase();
        if (!leftKey || !rightKey) return 0;
        let commonLength = 0;
        const maxLength = Math.min(leftKey.length, rightKey.length);
        while (commonLength < maxLength && leftKey[commonLength] === rightKey[commonLength]) commonLength += 1;
        const prefixBonus = leftKey.startsWith(rightKey) || rightKey.startsWith(leftKey) ? 700 : 0;
        return prefixBonus + Math.min(1400, commonLength * 110);
      };

      const entriesByHash = new Map();
      entries.forEach((entry) => {
        this.caseFileBrowserHashLookupKeys(entry.hash).forEach((hashKey) => {
          if (!entriesByHash.has(hashKey)) entriesByHash.set(hashKey, []);
          entriesByHash.get(hashKey).push(entry);
        });
      });
      const findEntry = (value, options = {}) => {
        const candidates = this.caseFileBrowserHashLookupKeys(value)
          .flatMap(hashKey => entriesByHash.get(hashKey) || [])
          .filter((candidate, candidateIndex, list) => list.findIndex(item => item.key === candidate.key) === candidateIndex)
          .filter(candidate => candidate.key !== options.referenceEntry?.key);
        if (!candidates.length) return null;

        const referenceEntry = options.referenceEntry || null;
        const referenceRow = Number(options.referenceRow ?? referenceEntry?.matrixRowIndex);
        const relation = options.relation || '';
        const scoreCandidate = (candidate) => {
          const candidateRow = Number(candidate.matrixRowIndex);
          const rowDelta = Number.isFinite(referenceRow) && Number.isFinite(candidateRow)
            ? candidateRow - referenceRow
            : 0;
          let score = 0;

          if (options.after === true && Number.isFinite(referenceRow)) score += rowDelta > 0 ? 1800 : -900;
          if (options.before === true && Number.isFinite(referenceRow)) score += rowDelta < 0 ? 1800 : -900;
          score -= Math.min(1200, Math.abs(rowDelta) * 3);

          if (referenceEntry && relation === 'parent') {
            const candidateToKey = addressKey(candidate.toAddress);
            const childFromKey = addressKey(referenceEntry.fromAddress);
            if (candidateToKey && childFromKey && candidateToKey === childFromKey) score += 12000;
            if (continuationMentions(candidate, referenceEntry)) score += 7000;
            score += pathAffinityScore(candidate.pathLabel, referenceEntry.pathLabel);
          } else if (referenceEntry && relation === 'child') {
            const candidateFromKey = addressKey(candidate.fromAddress);
            const parentToKey = addressKey(referenceEntry.toAddress);
            if (candidateFromKey && parentToKey && candidateFromKey === parentToKey) score += 12000;
            if (hashValuesOverlap(candidate.fromHash, referenceEntry.hash)) score += 7000;
            score += pathAffinityScore(referenceEntry.pathLabel, candidate.pathLabel);
          }
          return score;
        };

        return candidates.slice().sort((left, right) => (
          scoreCandidate(right) - scoreCandidate(left)
          || Math.abs(Number(left.matrixRowIndex) - referenceRow) - Math.abs(Number(right.matrixRowIndex) - referenceRow)
          || Number(left.matrixRowIndex) - Number(right.matrixRowIndex)
        ))[0] || null;
      };

      const childrenByEntry = new Map();
      const parentsByEntry = new Map();
      const relationshipWouldCreateCycle = (parent, child) => {
        const stack = [child.key];
        const visited = new Set();
        while (stack.length) {
          const currentKey = stack.pop();
          if (!currentKey || visited.has(currentKey)) continue;
          if (currentKey === parent.key) return true;
          visited.add(currentKey);
          (childrenByEntry.get(currentKey) || []).forEach(nextEntry => stack.push(nextEntry.key));
        }
        return false;
      };
      const addRelationship = (parent, child) => {
        if (!parent || !child || parent.key === child.key || child.key === rootEntry.key) return;
        if (relationshipWouldCreateCycle(parent, child)) return;
        if (!childrenByEntry.has(parent.key)) childrenByEntry.set(parent.key, []);
        const children = childrenByEntry.get(parent.key);
        if (!children.some(existing => existing.key === child.key)) children.push(child);
        if (!parentsByEntry.has(child.key)) parentsByEntry.set(child.key, []);
        const parents = parentsByEntry.get(child.key);
        if (!parents.some(existing => existing.key === parent.key)) parents.push(parent);
      };

      entries.forEach((entry) => {
        if (!entry.fromHash) return;
        addRelationship(findEntry(entry.fromHash, {
          before: true,
          referenceRow: entry.matrixRowIndex,
          referenceEntry: entry,
          relation: 'parent'
        }), entry);
      });
      entries.forEach((entry) => {
        (entry.childHashKeys || []).forEach((childHash) => {
          addRelationship(entry, findEntry(childHash, {
            after: true,
            referenceRow: entry.matrixRowIndex,
            referenceEntry: entry,
            relation: 'child'
          }));
        });
      });

      const depthByEntry = new Map([[rootEntry.key, 0]]);
      const relaxDepths = () => {
        let changed = false;
        entries.forEach((parent) => {
          const parentDepth = Number(depthByEntry.get(parent.key));
          if (!Number.isFinite(parentDepth)) return;
          (childrenByEntry.get(parent.key) || []).forEach((child) => {
            const nextDepth = Math.max(0, Math.min(40, parentDepth + 1));
            const currentDepth = Number(depthByEntry.get(child.key));
            if (!Number.isFinite(currentDepth) || nextDepth > currentDepth) {
              depthByEntry.set(child.key, nextDepth);
              changed = true;
            }
          });
        });
        return changed;
      };
      for (let pass = 0; pass < entries.length && relaxDepths(); pass += 1) {
        // The relationship graph is small and normally acyclic. Cycle prevention
        // above keeps this bounded even when hashes repeat in unusual row orders.
      }
      entries.forEach((entry) => {
        if (depthByEntry.has(entry.key)) return;
        depthByEntry.set(entry.key, this.caseInputFlowStoryEstimatePathDepth(entry.pathLabel, rootEntry.pathLabel));
      });
      for (let pass = 0; pass < entries.length && relaxDepths(); pass += 1) {
        // Reconcile disconnected components against their path-label fallback depth.
      }

      const collected = [];
      const seen = new Set();
      const walk = (entry) => {
        if (!entry || seen.has(entry.key)) return;
        seen.add(entry.key);
        collected.push({ entry, depth: Number(depthByEntry.get(entry.key)) || 0 });
        const children = (childrenByEntry.get(entry.key) || [])
          .slice()
          .sort((left, right) => (
            Number(left.matrixRowIndex) - Number(right.matrixRowIndex)
            || normalizeCell(left.pathLabel).localeCompare(normalizeCell(right.pathLabel), undefined, { numeric: true, sensitivity: 'base' })
          ));
        children.forEach(child => walk(child));
      };
      walk(rootEntry);
      entries.forEach((entry) => {
        if (!seen.has(entry.key)) walk(entry);
      });

      const depthMap = new Map();
      collected.forEach((item) => {
        if (!depthMap.has(item.depth)) depthMap.set(item.depth, []);
        depthMap.get(item.depth).push(item);
      });
      depthMap.forEach((items) => {
        items.sort((left, right) => (
          normalizeCell(left.entry.pathLabel).localeCompare(normalizeCell(right.entry.pathLabel), undefined, { numeric: true, sensitivity: 'base' })
          || Number(left.entry.matrixRowIndex) - Number(right.entry.matrixRowIndex)
        ));
        const center = (items.length - 1) / 2;
        items.forEach((item, itemIndex) => {
          item.y = (itemIndex - center) * 78;
        });
      });
      const itemByEntry = new Map(collected.map(item => [item.entry.key, item]));
      collected.slice().reverse().forEach((item) => {
        const exclusiveChildItems = (childrenByEntry.get(item.entry.key) || [])
          .filter(child => (parentsByEntry.get(child.key) || []).length <= 1)
          .map(child => itemByEntry.get(child.key))
          .filter(Boolean);
        if (exclusiveChildItems.length) {
          item.y = exclusiveChildItems.reduce((sum, child) => sum + child.y, 0) / exclusiveChildItems.length;
        }
      });

      const heat = this.buildCaseInputFlowStoryHeatStats(segment, entries);
      const nodes = [];
      const edges = [];
      const nodeById = new Map();
      const edgeIds = new Set();
      const addNode = (node) => {
        if (nodeById.has(node.id)) return nodeById.get(node.id);
        nodes.push(node);
        nodeById.set(node.id, node);
        return node;
      };
      const addEdge = (fromId, toId, stat, entry) => {
        const edgeId = `${fromId}->${toId}`;
        if (edgeIds.has(edgeId)) return;
        const from = nodeById.get(fromId);
        const to = nodeById.get(toId);
        if (!from || !to) return;
        const dx = Number(to.x) - Number(from.x);
        const dy = Number(to.y) - Number(from.y);
        const length = Math.max(1, Math.hypot(dx, dy));
        const unitX = dx / length;
        const unitY = dy / length;
        const fromInset = from.type === 'tx' ? 27 : 17;
        const toInset = to.type === 'tx' ? 29 : 19;
        const startX = Number(from.x) + (unitX * fromInset);
        const startY = Number(from.y) + (unitY * fromInset);
        const endX = Number(to.x) - (unitX * toInset);
        const endY = Number(to.y) - (unitY * toInset);
        const middleX = (startX + endX) / 2;
        edgeIds.add(edgeId);
        edges.push({
          id: edgeId,
          from: fromId,
          to: toId,
          path: `M ${startX} ${startY} C ${middleX} ${startY}, ${middleX} ${endY}, ${endX} ${endY}`,
          heatColor: stat?.color || '',
          title: this.caseInputFlowStoryHeatTitle(entry, stat)
        });
      };

      // Build every node before drawing any relationship edge. A convergence
      // parent can appear later in spreadsheet/path traversal order than its
      // shared child, so one-pass rendering silently dropped those arrows.
      collected.forEach((item) => {
        const entry = item.entry;
        const stat = heat.statsByEntryKey.get(entry.key) || null;
        const baseX = Math.max(0, Number(item.depth) || 0) * 205;
        const entrySvgKey = this.caseInputFlowStorySvgKey(entry.key);
        const title = this.caseInputFlowStoryHeatTitle(entry, stat);
        addNode({
          id: `${entrySvgKey}-tx`,
          type: 'tx',
          x: baseX + 76,
          y: item.y,
          entryKey: entry.key,
          heatColor: stat?.color || '',
          title,
          pathEnding: entry.pathEnding
        });
        addNode({
          id: `${entrySvgKey}-to`,
          type: 'address',
          x: baseX + 153,
          y: item.y,
          entryKey: entry.key,
          heatColor: stat?.color || '',
          title: `${entry.toAddress ? `To ${this.caseFileBrowserShortAddress(entry.toAddress)}` : 'Destination address not set'} · ${title}`,
          pathEnding: entry.pathEnding
        });

        const parents = (parentsByEntry.get(entry.key) || [])
          .filter(parent => itemByEntry.has(parent.key));
        if (!parents.length) {
          addNode({
            id: `${entrySvgKey}-from`,
            type: 'address',
            x: baseX,
            y: item.y,
            entryKey: entry.key,
            heatColor: stat?.color || '',
            title: `${entry.fromAddress ? `From ${this.caseFileBrowserShortAddress(entry.fromAddress)}` : 'Source address not set'} · ${title}`,
            pathEnding: false
          });
        }
      });

      collected.forEach((item) => {
        const entry = item.entry;
        const stat = heat.statsByEntryKey.get(entry.key) || null;
        const entrySvgKey = this.caseInputFlowStorySvgKey(entry.key);
        const txId = `${entrySvgKey}-tx`;
        const toId = `${entrySvgKey}-to`;
        const parents = (parentsByEntry.get(entry.key) || [])
          .filter(parent => itemByEntry.has(parent.key));
        if (parents.length) {
          parents.forEach((parent) => {
            const parentToId = `${this.caseInputFlowStorySvgKey(parent.key)}-to`;
            addEdge(parentToId, txId, stat, entry);
          });
        } else {
          addEdge(`${entrySvgKey}-from`, txId, stat, entry);
        }
        addEdge(txId, toId, stat, entry);
      });

      const xs = nodes.map(node => node.x);
      const ys = nodes.map(node => node.y);
      const minX = Math.min(...xs, 0) - 48;
      const maxX = Math.max(...xs, 360) + 48;
      const minY = Math.min(...ys, 0) - 56;
      const maxY = Math.max(...ys, 0) + 56;
      const viewBox = {
        x: minX,
        y: minY,
        width: Math.max(390, maxX - minX),
        height: Math.max(165, maxY - minY)
      };
      return {
        key: segment.key,
        nodes,
        edges,
        viewBox,
        viewBoxString: `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`,
        entryCount: allEntries.length,
        shownEntryCount: entries.length,
        truncated: allEntries.length > entries.length,
        heatBasis: heat.basisLabel
      };
    },

    buildCaseInputFlowStoryHeatStats(segment = {}, entries = []) {
      const rootEntry = segment.rootEntry || entries[0] || {};
      const rootAmountToken = this.caseInputFlowStoryEntryAmountToken(rootEntry, { preferDirect: true });
      const mainReference = this.caseManualSolveEvaluationBuildMainReference({
        referenceAmount: rootAmountToken.amount,
        referenceToken: rootAmountToken.token,
        currentColumnH: rootEntry.usdReference,
        utcDate: rootEntry.date,
        problemText: 'The root row does not yet have a usable amount and token.'
      });
      const initialUsdValue = Number(mainReference?.currentUsdValue);
      const hasUsdBasis = Boolean(mainReference?.isUsable && Number.isFinite(initialUsdValue) && initialUsdValue > 0);
      const rootAmount = Number(rootAmountToken.amount);
      const rootToken = normalizeCell(rootAmountToken.token).toUpperCase();
      const hasTokenBasis = Number.isFinite(rootAmount) && rootAmount > 0 && !!rootToken;
      const statsByEntryKey = new Map();

      entries.forEach((entry) => {
        const amountToken = this.caseInputFlowStoryEntryAmountToken(entry);
        const amount = Number(amountToken.amount);
        const token = normalizeCell(amountToken.token).toUpperCase();
        const zero = Number.isFinite(amount) && amount <= 0;
        let usdValue = null;
        let percent = null;
        let resolved = false;
        let resolution = '';

        if (entry.key === rootEntry.key && (hasUsdBasis || hasTokenBasis)) {
          usdValue = hasUsdBasis ? initialUsdValue : null;
          percent = 100;
          resolved = true;
          resolution = hasUsdBasis ? mainReference.manualSolveReferenceSource : `${rootToken} root amount`;
        } else if (Number.isFinite(amount) && amount >= 0 && token) {
          if (hasTokenBasis && token === rootToken) {
            percent = rootAmount > 0 ? (amount / rootAmount) * 100 : null;
            usdValue = hasUsdBasis && Number.isFinite(percent) ? initialUsdValue * (percent / 100) : null;
            resolved = Number.isFinite(percent);
            resolution = hasUsdBasis ? `${rootToken} proportion of root USD value` : `${rootToken} proportion of root amount`;
          } else if (hasUsdBasis && caseWwwIsStablecoin(token)) {
            usdValue = amount;
            percent = (usdValue / initialUsdValue) * 100;
            resolved = Number.isFinite(percent);
            resolution = `${token} stablecoin value`;
          } else if (hasUsdBasis) {
            const estimate = estimateCaseBuddyHistoricalUsdValue({
              symbol: token,
              timestampUtc: entry.date || rootEntry.date,
              amount
            });
            if (estimate?.ok && Number.isFinite(estimate.estimatedUsdValue)) {
              usdValue = Number(estimate.estimatedUsdValue);
              percent = (usdValue / initialUsdValue) * 100;
              resolved = Number.isFinite(percent);
              resolution = `Local ${estimate.symbol || token} reference`;
            }
          }
        }

        const stat = {
          entryKey: entry.key,
          amount: Number.isFinite(amount) ? amount : null,
          token,
          usdValue: Number.isFinite(Number(usdValue)) ? Number(usdValue) : null,
          percent: Number.isFinite(Number(percent)) ? Number(percent) : null,
          resolved,
          zero,
          resolution,
          issue: resolved ? '' : 'A comparable amount/value was not available for this transaction.'
        };
        stat.color = this.caseInputFlowStoryHeatColorForStat(stat);
        statsByEntryKey.set(entry.key, stat);
      });

      return {
        statsByEntryKey,
        initialUsdValue: hasUsdBasis ? initialUsdValue : null,
        basisLabel: hasUsdBasis
          ? (mainReference.manualSolveReferenceSource || 'Root USD value')
          : (hasTokenBasis ? `${rootToken} root amount` : 'Unresolved value basis')
      };
    },

    caseInputFlowStoryHeatColorForStat(stat = {}) {
      const rawPercent = Number(stat?.percent);
      if (!stat?.resolved || !Number.isFinite(rawPercent)) return CASE_INPUT_FLOW_STORY_HEAT_NO_DATA_COLOR;
      if (stat?.zero) return CASE_INPUT_FLOW_STORY_HEAT_STOPS[0].c;
      const sensitivity = Math.max(0.35, Math.min(2.5, Number(this.caseManualSolveHeatMapSensitivity) || 1));
      const capped = Math.max(0, Math.min(180, rawPercent));
      const normalized = Math.max(0, Math.min(1, capped / 100));
      const adjustedUnder = 100 * Math.pow(normalized, 1 / sensitivity);
      const adjusted = capped > 100 ? 100 + ((capped - 100) * sensitivity) : adjustedUnder;
      return this.caseInputFlowStoryHeatColorForPercent(adjusted);
    },

    caseInputFlowStoryHeatColorForPercent(percent = 0) {
      const value = Math.max(0, Math.min(180, Number(percent) || 0));
      for (let index = 0; index < CASE_INPUT_FLOW_STORY_HEAT_STOPS.length - 1; index += 1) {
        const left = CASE_INPUT_FLOW_STORY_HEAT_STOPS[index];
        const right = CASE_INPUT_FLOW_STORY_HEAT_STOPS[index + 1];
        if (value < left.p || value > right.p) continue;
        const span = right.p - left.p || 1;
        return this.caseManualSolveHeatMapInterpolateColor(left.c, right.c, (value - left.p) / span);
      }
      return CASE_INPUT_FLOW_STORY_HEAT_STOPS[CASE_INPUT_FLOW_STORY_HEAT_STOPS.length - 1].c;
    },

    caseInputFlowStoryHeatTitle(entry = {}, stat = null) {
      const identity = `${entry.pathLabel || entry.shortHash || 'Transaction'} · ${entry.shortHash || this.caseFileBrowserShortHash(entry.hash)}`;
      if (!stat) return `${identity} · Heat value unavailable`;
      const amountText = this.caseInputFlowStoryFormatAmountToken(stat.amount, stat.token);
      const percentText = Number.isFinite(Number(stat.percent)) ? `${Number(stat.percent).toFixed(2)}% of root value` : 'Heat value unavailable';
      const usdText = Number.isFinite(Number(stat.usdValue)) ? ` · ${formatCaseWwwUsd(Number(stat.usdValue))}` : '';
      return `${identity} · ${percentText}${usdText}${amountText ? ` · ${amountText}` : ''}`;
    },

    caseInputFlowStoryEstimatePathDepth(pathLabel = '', rootLabel = '') {
      const label = normalizeCell(pathLabel);
      const root = normalizeCell(rootLabel);
      if (!label || !root || label === root) return label === root ? 0 : 1;
      if (!label.toLowerCase().startsWith(root.toLowerCase())) return 1;
      const suffix = label.slice(root.length);
      const parts = suffix.match(/\.[0-9]+|[A-Za-z]+|[0-9]+/gu) || [];
      return Math.max(1, Math.min(8, parts.length || 1));
    },

    caseInputFlowStoryNodeClass(node = {}) {
      return [
        'case-input-flow-story-card__node',
        `case-input-flow-story-card__node--${node.type || 'address'}`,
        node.pathEnding ? 'is-ending' : '',
        this.caseInputFlowStoryHeatEnabled ? 'is-heat' : 'is-normal'
      ].filter(Boolean);
    },

    caseInputFlowStoryNodeColor(node = {}) {
      if (this.caseInputFlowStoryHeatEnabled) return node.heatColor || CASE_INPUT_FLOW_STORY_HEAT_NO_DATA_COLOR;
      return node.type === 'tx' ? '#facc15' : '#3b82f6';
    },

    caseInputFlowStoryNodeStroke(node = {}) {
      if (this.caseWorkspaceDarkModeActive) return '#ffffff';
      return node.pathEnding ? '#0f172a' : '#1e293b';
    },

    caseInputFlowStoryEdgeColor(edge = {}) {
      if (this.caseInputFlowStoryHeatEnabled) return edge.heatColor || CASE_INPUT_FLOW_STORY_HEAT_NO_DATA_COLOR;
      return '#475569';
    },

    caseInputFlowStoryMarkerId(key = '', edgeKey = '') {
      const text = `${String(key || 'flow')}|${String(edgeKey || 'edge')}`;
      let hash = 0;
      for (let index = 0; index < text.length; index += 1) hash = ((hash << 5) - hash + text.charCodeAt(index)) | 0;
      const safe = text.replace(/[^A-Za-z0-9_-]+/gu, '-').replace(/^-+|-+$/gu, '').slice(0, 42) || 'flow';
      return `case-input-flow-arrow-${safe}-${Math.abs(hash)}`;
    },

    caseInputFlowStorySvgKey(value = '') {
      const text = String(value || 'entry');
      let hash = 0;
      for (let index = 0; index < text.length; index += 1) hash = ((hash << 5) - hash + text.charCodeAt(index)) | 0;
      return `${text.replace(/[^A-Za-z0-9_-]+/gu, '-').slice(0, 44) || 'entry'}-${Math.abs(hash)}`;
    },

    onAutoSaveToggle() {
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          window.localStorage.setItem('tool60_autosave_enabled', this.autoSaveEnabled ? 'true' : 'false');
        } catch (error) {
          console.warn('Could not save auto-save preference.', error);
        }
      }

      if (this.autoSaveEnabled) {
        this.saveSpreadsheetFromEvent?.();
        this.showCaseToast?.('Auto-save is on. Spreadsheet changes will be saved automatically.');
      } else {
        this.showCaseToast?.('Auto-save is off. Use Save to keep spreadsheet changes.');
      }
    },

    normalizeCaseTitleInlineName(value = '') {
      return String(value ?? '').replace(/[\r\n\t]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
    },

    buildCaseDetailsDraft(details = this.caseDetails) {
      return {
        caseName: this.normalizeCaseTitleInlineName(details?.caseName || ''),
        caseId: normalizeCell(details?.caseId || ''),
        caseType: normalizeCell(details?.caseType || '')
      };
    },

    async saveCaseDetailsUpdate(nextDetails = {}, options = {}) {
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const current = this.caseSupportState.caseDetails || {};
      const normalized = {
        ...current,
        caseName: this.normalizeCaseTitleInlineName(nextDetails.caseName ?? current.caseName ?? ''),
        caseId: normalizeCell(nextDetails.caseId ?? current.caseId ?? ''),
        caseType: normalizeCell(nextDetails.caseType ?? current.caseType ?? '')
      };
      const changed = ['caseName', 'caseId', 'caseType'].some(key => normalizeCell(current[key] || '') !== normalizeCell(normalized[key] || ''));
      if (!changed) {
        if (options.closeEditor) this.showCaseDetailsEditor = false;
        this.queueCaseTitleInlineFit?.(40);
        return false;
      }

      normalized.updatedAtUtc = new Date().toISOString();
      this.caseSupportState.caseDetails = normalized;
      const reason = options.reason || 'Updated case details.';
      if (typeof this.markCaseDataDirty === 'function') {
        this.markCaseDataDirty(reason);
      } else {
        this.caseSupportState.dirty = true;
        this.caseSupportState.dirtySinceUtc = this.caseSupportState.dirtySinceUtc || new Date().toISOString();
        this.caseSupportState.caseDataRevision = (Number(this.caseSupportState.caseDataRevision) || 0) + 1;
      }
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(80);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      if (typeof this.queueCaseSummaryRefresh === 'function') this.queueCaseSummaryRefresh();
      else if (typeof this.refreshCaseSummary === 'function') this.refreshCaseSummary();
      if (options.closeEditor) this.showCaseDetailsEditor = false;
      this.queueCaseTitleInlineFit?.(40);
      return true;
    },

    openCaseDetailsEditor() {
      this.caseDetailsDraft = this.buildCaseDetailsDraft();
      this.showCaseDetailsEditor = true;
    },

    cancelCaseDetailsEditor() {
      this.showCaseDetailsEditor = false;
      this.caseDetailsDraft = this.buildCaseDetailsDraft();
    },

    async saveCaseDetailsEditor() {
      await this.saveCaseDetailsUpdate(this.caseDetailsDraft, {
        closeEditor: true,
        reason: 'Updated case details from Case Details editor.'
      });
    },

    startCaseTitleInlineEdit() {
      if (this.caseTitleInlineEditing) return;
      this.caseTitleInlineOriginalStoredName = this.normalizeCaseTitleInlineName(this.caseDetails?.caseName || '');
      this.caseTitleInlineOriginalDisplayName = this.normalizeCaseTitleInlineName(this.caseDisplayName || '');
      this.caseTitleInlineDraft = this.caseTitleInlineOriginalDisplayName;
      this.caseTitleInlineEditing = true;
      this.$nextTick(() => {
        const input = this.$refs.caseTitleInlineInput;
        input?.focus?.();
        input?.select?.();
      });
    },

    cancelCaseTitleInlineEdit() {
      this.caseTitleInlineEditing = false;
      this.caseTitleInlineDraft = '';
      this.caseTitleInlineOriginalStoredName = '';
      this.caseTitleInlineOriginalDisplayName = '';
      this.queueCaseTitleInlineFit?.(40);
    },

    async commitCaseTitleInlineEdit() {
      if (!this.caseTitleInlineEditing) return;
      const draft = this.normalizeCaseTitleInlineName(this.caseTitleInlineDraft);
      const originalStored = this.normalizeCaseTitleInlineName(this.caseTitleInlineOriginalStoredName);
      const originalDisplay = this.normalizeCaseTitleInlineName(this.caseTitleInlineOriginalDisplayName);
      this.caseTitleInlineEditing = false;
      this.caseTitleInlineDraft = '';
      this.caseTitleInlineOriginalStoredName = '';
      this.caseTitleInlineOriginalDisplayName = '';

      if (draft === originalStored || (!originalStored && draft === originalDisplay)) {
        this.queueCaseTitleInlineFit?.(40);
        return;
      }

      await this.saveCaseDetailsUpdate({
        ...(this.caseDetails || {}),
        caseName: draft
      }, {
        reason: draft ? 'Renamed case from page header.' : 'Cleared case name from page header.'
      });
    },

    queueCaseTitleInlineFit(delayMs = 0) {
      if (typeof window === 'undefined') return;
      if (this.caseTitleInlineFitTimer) {
        window.clearTimeout(this.caseTitleInlineFitTimer);
        this.caseTitleInlineFitTimer = null;
      }
      this.caseTitleInlineFitTimer = window.setTimeout(() => {
        this.caseTitleInlineFitTimer = null;
        this.$nextTick(() => this.adjustCaseTitleInlineFit());
      }, Math.max(0, Number(delayMs) || 0));
    },

    adjustCaseTitleInlineFit() {
      if (this.caseTitleInlineEditing || typeof window === 'undefined') return;
      const box = this.$refs.caseTitleInlineBox;
      const display = this.$refs.caseTitleInlineDisplay;
      if (!box || !display) return;
      const available = Math.max(0, Number(box.clientWidth) || 0);
      if (!available) return;

      const oldInlineSize = display.style.fontSize;
      display.style.fontSize = '';
      const naturalSize = Number.parseFloat(window.getComputedStyle(display).fontSize) || 34;
      const naturalScrollWidth = Number(display.scrollWidth) || 0;
      const minimumSize = 20;
      let nextSize = 0;
      if (naturalScrollWidth > available + 1) {
        nextSize = Math.max(minimumSize, Math.floor(naturalSize * (available / naturalScrollWidth)));
      }
      display.style.fontSize = oldInlineSize;

      const normalizedNext = nextSize > 0 && nextSize < naturalSize - 0.5 ? nextSize : 0;
      if (Number(this.caseTitleInlineFontSizePx) !== normalizedNext) {
        this.caseTitleInlineFontSizePx = normalizedNext;
      }
    },

    recordWorkflowSignal(signalCode = '', source = '', options = {}) {
      const args = [signalCode, source, options];
      let result = this.callCaseBuddyOriginalMethod(caseBuddyWorkflowMethods, 'recordWorkflowSignal', args);
      if (result === undefined) {
        if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
        const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
        const code = normalizeCell(signalCode).toUpperCase();
        if (code) {
          const workflow = {
            ...(this.caseSupportState.workflowState || {}),
            signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) }
          };
          workflow.signalCompleteByCode[code] = {
            ...(workflow.signalCompleteByCode[code] || {}),
            completedAtUtc: workflow.signalCompleteByCode[code]?.completedAtUtc || now,
            checkedAtUtc: now,
            revision: Number(this.caseSupportState.caseDataRevision) || 0,
            source: source || 'workflow-signal',
            ...(options && typeof options === 'object' ? options : {})
          };
          this.caseSupportState.workflowState = workflow;
          if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
        }
        result = { handled: true, success: true, signalCode: normalizeCell(signalCode).toUpperCase() };
      }
      const code = normalizeCell(signalCode).toUpperCase();
      if (code === 'EXTERNAL_ATTRIBUTION_REVIEWED' || code === 'EXTERNAL_ATTRIBUTION_REVIEW') {
        this.queueCaseWorkflowWwwUsdReferenceAutoGuard({
          source: source || 'external-attribution-signal',
          delayMs: 40,
          continueAfter: true,
          forceExternalReviewCurrent: true
        });
      }
      return result;
    },

    emptyCaseWorkflowRawDataTool88Activity(overrides = {}) {
      return {
        active: false,
        startedAtUtc: '',
        updatedAtUtc: '',
        completedAtUtc: '',
        status: '',
        title: '',
        message: '',
        detail: '',
        eventName: '',
        integrationAction: '',
        stepCount: 0,
        researchedCount: 0,
        entryCount: 0,
        endingCount: 0,
        completeCount: 0,
        unresolvedCount: 0,
        flowComplete: false,
        flowCompleteConfirmations: 0,
        rawRows: 0,
        rowsAppended: 0,
        targetLabel: '',
        targetHash: '',
        targetAddress: '',
        guardrailMessage: '',
        unresolvedBridgeAccepted: false,
        unresolvedBridgeNote: '',
        requiresInvestigatorReview: false,
        reviewNote: '',
        lastLogKey: '',
        lastAutomationLogKey: '',
        log: [],
        ...overrides
      };
    },

    emptyCaseWorkflowRawDataTool88Progress(overrides = {}) {
      return {
        active: false,
        title: '',
        detail: '',
        status: '',
        action: '',
        stepCount: 0,
        researchedCount: 0,
        outputRows: 0,
        rowsAppended: 0,
        totalEntries: 0,
        endingCount: 0,
        completeEndings: 0,
        openEndings: 0,
        flowCompleteConfirmations: 0,
        flowCompleteRequiredConfirmations: 0,
        selectedLabel: '',
        selectedHash: '',
        selectedTarget: '',
        lastEventKey: '',
        lastLoggedAtMs: 0,
        updatedAtUtc: '',
        events: [],
        ...overrides
      };
    },

    caseWorkflowRawDataTool88CurrentCaseKey() {
      const fileId = normalizeCell(this.currentProjectFileId || 'default-file');
      const caseId = normalizeCell(this.caseDetails?.caseId || this.caseId || this.caseDisplayId || '');
      let mainRows = [];
      try {
        mainRows = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('main') : [];
      } catch {
        mainRows = [];
      }
      const rows = Array.isArray(mainRows) ? mainRows : [];
      const body = rows.length && this.caseManualIsHeaderRow?.(rows[0]) ? rows.slice(1) : rows;
      const entries = [];
      for (const row of body) {
        if (!Array.isArray(row)) continue;
        const hash = normalizeCell(row[CASE_MANUAL_START_COL.TX_HASH]);
        if (!hash || /^transaction hash$/i.test(hash)) continue;
        entries.push([
          normalizeCell(row[CASE_MANUAL_START_COL.PATH_LABEL]),
          hash,
          normalizeCell(row[CASE_MANUAL_START_COL.FROM_ADDRESS]),
          normalizeCell(row[CASE_MANUAL_START_COL.TO_ADDRESS]),
          normalizeCell(row[CASE_MANUAL_START_COL.DATE])
        ].join('~'));
        if (entries.length >= 24) break;
      }
      return `${fileId || 'default-file'}|${caseId || 'case'}|${entries.join('|') || 'empty-main'}`;
    },

    caseWorkflowRawDataTool88VisualStateMatchesCurrentCase() {
      const stateKey = normalizeCell(this.caseWorkflowRawDataTool88VisualCaseKey || '');
      if (!stateKey) return true;
      return stateKey === this.caseWorkflowRawDataTool88CurrentCaseKey();
    },

    resetCaseWorkflowRawDataTool88VisualState(reason = 'Clearing Tool 88 raw-data preview state.', options = {}) {
      const now = new Date().toISOString();
      if (options.clearAutoContinue !== false) {
        this.clearCaseWorkflowRawDataTool88AutoContinueTimer?.();
        this.caseWorkflowRawDataTool88AutoContinueGuardUntilMs = 0;
        this.caseWorkflowRawDataTool88AutoContinueInFlight = false;
        this.caseWorkflowRawDataTool88AutoContinueReason = '';
      }

      if (options.stopTool88 === true) {
        const tool = this.$refs?.caseWorkflowRawDataTool88;
        if (tool && typeof tool.caseBuddyRequestAutomationStop === 'function') {
          try {
            tool.caseBuddyRequestAutomationStop(reason);
          } catch (error) {
            console.warn('Case Buddy could not stop stale hidden Tool 88 raw-data solver.', error);
          }
        }
      }

      if (options.keepRunning !== true) {
        this.caseWorkflowRawDataTool88Running = false;
        this.caseWorkflowTool88RawRunning = false;
        this.caseHopSolverAutomationRunning = false;
      }
      if (options.keepHost !== true) this.caseWorkflowRawDataTool88HostMounted = false;

      this.caseWorkflowRawDataTool88LastSnapshot = null;
      this.caseWorkflowRawDataTool88LastRows = [];
      this.caseWorkflowRawDataTool88VisualRows = [];
      this.caseWorkflowRawDataTool88LastError = '';
      this.caseWorkflowRawDataTool88LastGraphAudit = null;
      this.caseWorkflowRawDataTool88LastChosenSource = '';
      this.caseWorkflowRawDataTool88LastCandidateSources = [];
      if (options.keepRunning !== true) {
        this.caseWorkflowRawDataTool88SelectedInputRoots = [];
        this.caseWorkflowRawDataTool88CoveragePasses = [];
      }
      this.caseWorkflowRawDataTool88RunKey = options.runKey || '';
      this.caseWorkflowRawDataTool88VisualCaseKey = options.caseKey === false ? '' : (options.caseKey || this.caseWorkflowRawDataTool88CurrentCaseKey());
      this.caseWorkflowRawDataTool88LastResetAtUtc = now;
      this.resetCaseWorkflowRawDataTool88PortalCamera?.({
        caseKey: this.caseWorkflowRawDataTool88VisualCaseKey,
        runKey: this.caseWorkflowRawDataTool88RunKey
      });

      this.caseAutomationTool88Activity = this.emptyCaseWorkflowRawDataTool88Activity({
        updatedAtUtc: now,
        status: options.status || '',
        title: options.title || '',
        message: options.message || '',
        detail: options.detail || normalizeCell(reason)
      });
      this.caseAutomationTool88Progress = this.emptyCaseWorkflowRawDataTool88Progress({ updatedAtUtc: now });

      if (options.clearVisibleSolverOutput === true) {
        this.caseHopSolverOutputRows = [];
        this.caseHopSolverOutputTsv = '';
        this.caseHopSolverSnapshot = null;
        this.caseHopSolverLastEvent = '';
      }
    },

    async loadSelectedProjectFile(...args) {
      this.resetCaseWorkflowRawDataTool88VisualState('Loading a different Case Buddy file; clearing stale Tool 88 raw-data preview.', {
        stopTool88: true,
        keepHost: true,
        clearVisibleSolverOutput: true
      });
      const result = this.callFirstCaseBuddyOriginalMethod('loadSelectedProjectFile', args);
      const resolved = result && typeof result.then === 'function' ? await result : result;
      this.resetCaseWorkflowRawDataTool88VisualState('Loaded Case Buddy file; Tool 88 raw-data preview is ready for the current case.', {
        stopTool88: false,
        keepHost: true,
        clearVisibleSolverOutput: true
      });
      return resolved;
    },

    async loadCaseSimpleLaunchProjectFile(...args) {
      this.resetCaseWorkflowRawDataTool88VisualState('Loading a simple-launch Case Buddy file; clearing stale Tool 88 raw-data preview.', {
        stopTool88: true,
        keepHost: true,
        clearVisibleSolverOutput: true
      });
      const result = this.callFirstCaseBuddyOriginalMethod('loadCaseSimpleLaunchProjectFile', args);
      const resolved = result && typeof result.then === 'function' ? await result : result;
      this.resetCaseWorkflowRawDataTool88VisualState('Loaded simple-launch Case Buddy file; Tool 88 raw-data preview is ready for the current case.', {
        stopTool88: false,
        keepHost: true,
        clearVisibleSolverOutput: true
      });
      return resolved;
    },

    async createBlankProjectFile(...args) {
      this.resetCaseWorkflowRawDataTool88VisualState('Creating a blank Case Buddy file; clearing stale Tool 88 raw-data preview.', {
        stopTool88: true,
        keepHost: true,
        clearVisibleSolverOutput: true
      });
      const result = this.callFirstCaseBuddyOriginalMethod('createBlankProjectFile', args);
      const resolved = result && typeof result.then === 'function' ? await result : result;
      this.resetCaseWorkflowRawDataTool88VisualState('Blank Case Buddy file created; Tool 88 raw-data preview is clear.', {
        stopTool88: false,
        keepHost: true,
        clearVisibleSolverOutput: true
      });
      return resolved;
    },

    async deleteCurrentProjectFile(...args) {
      this.resetCaseWorkflowRawDataTool88VisualState('Deleting the current Case Buddy file; clearing stale Tool 88 raw-data preview.', {
        stopTool88: true,
        keepHost: true,
        clearVisibleSolverOutput: true
      });
      const result = this.callFirstCaseBuddyOriginalMethod('deleteCurrentProjectFile', args);
      const resolved = result && typeof result.then === 'function' ? await result : result;
      this.resetCaseWorkflowRawDataTool88VisualState('Case Buddy file changed after delete; Tool 88 raw-data preview is clear.', {
        stopTool88: false,
        keepHost: true,
        clearVisibleSolverOutput: true
      });
      return resolved;
    },

    async performResetSheetReset(...args) {
      const result = this.callFirstCaseBuddyOriginalMethod('performResetSheetReset', args);
      const resolved = result && typeof result.then === 'function' ? await result : result;
      this.resetCaseWorkflowRawDataTool88VisualState('Sheet reset; clearing stale Tool 88 raw-data preview.', {
        stopTool88: true,
        keepHost: true,
        clearVisibleSolverOutput: true
      });
      return resolved;
    },

    async openCaseFileBrowserModal(...args) {
      const result = this.callFirstCaseBuddyOriginalMethod('openCaseFileBrowserModal', args);
      if (result && typeof result.then === 'function') await result;
      if (!this.showCaseFileBrowserModal) this.showCaseFileBrowserModal = true;
      this.scheduleCaseFileBrowserSelectedPreviewLoad({ reason: 'open' });
      this.scheduleCaseFileBrowserVisiblePreviewWarmup(180);
      return result;
    },

    cancelCaseFileBrowserModal(...args) {
      const result = this.callFirstCaseBuddyOriginalMethod('cancelCaseFileBrowserModal', args);
      if (result === undefined) this.showCaseFileBrowserModal = false;
      this.stopCaseFileBrowserCardPreviewCycle();
      this.clearCaseFileBrowserPreviewTimers({ keepCache: true });
      return result;
    },

    async refreshCaseFileBrowser(...args) {
      const result = this.callFirstCaseBuddyOriginalMethod('refreshCaseFileBrowser', args);
      if (result && typeof result.then === 'function') await result;
      this.scheduleCaseFileBrowserSelectedPreviewLoad({ reason: 'refresh' });
      this.scheduleCaseFileBrowserVisiblePreviewWarmup(260);
      return result;
    },

    selectCaseFileBrowserRow(id, ...args) {
      const result = this.callFirstCaseBuddyOriginalMethod('selectCaseFileBrowserRow', [id, ...args]);
      if (result === undefined) this.caseFileBrowserSelectedId = id || '';
      this.caseFileBrowserSelectedPreviewIndex = 0;
      this.requestCaseFileBrowserPreview(id, { reason: 'selected', delay: 35 });
      return result;
    },

    async loadSelectedCaseFileBrowserRow(...args) {
      this.clearCaseFileBrowserPreviewTimers({ keepCache: true });
      const result = this.callFirstCaseBuddyOriginalMethod('loadSelectedCaseFileBrowserRow', args);
      if (result && typeof result.then === 'function') return await result;
      return result;
    },

    async createCaseFileBrowserNewCase() {
      if (this.caseFileBrowserLoading || this.caseFileBrowserCreating || this.caseFileBrowserDeleting) return false;
      const previousFileId = this.currentProjectFileId;
      this.caseFileBrowserCreating = true;
      try {
        await this.createBlankProjectFile();
        await this.$nextTick();
        const nextFileId = this.currentProjectFileId;
        if (!nextFileId || nextFileId === previousFileId) return false;
        this.caseFileBrowserSelectedId = nextFileId;
        await this.refreshCaseFileBrowser({ preserveSelection: true });
        this.caseFileBrowserSelectedId = nextFileId;
        this.caseFileBrowserSelectedPreviewIndex = 0;
        this.requestCaseFileBrowserPreview(nextFileId, { reason: 'created', delay: 20 });
        return true;
      } catch (error) {
        this.showCaseToast?.(error?.message || 'The new case could not be created.');
        return false;
      } finally {
        this.caseFileBrowserCreating = false;
      }
    },

    async deleteSelectedCaseFileBrowserRow(...args) {
      if (this.caseFileBrowserLoading || this.caseFileBrowserCreating || this.caseFileBrowserDeleting) return false;
      const selectedId = this.caseFileBrowserSelectedId;
      const selectedRow = this.caseFileBrowserSelectedRow;
      if (!selectedId || !selectedRow || this.projectFiles.length <= 1) return false;
      this.caseFileBrowserDeleting = true;
      try {
        const result = selectedRow.isCurrent
          ? this.deleteCurrentProjectFile(...args)
          : this.callFirstCaseBuddyOriginalMethod('deleteSelectedCaseFileBrowserRow', args);
        const resolved = result && typeof result.then === 'function' ? await result : result;
        if (resolved === false) return false;
        if (selectedId) this.removeCaseFileBrowserPreviewCacheEntry(selectedId);
        await this.$nextTick();
        this.scheduleCaseFileBrowserVisiblePreviewWarmup(220);
        return resolved;
      } finally {
        this.caseFileBrowserDeleting = false;
      }
    },

    scheduleCaseFileBrowserSelectedPreviewLoad(options = {}) {
      if (!this.showCaseFileBrowserModal) return;
      const row = this.caseFileBrowserSelectedRow;
      if (!row?.id) return;
      this.requestCaseFileBrowserPreview(row.id, { reason: options.reason || 'selected', delay: 80 });
    },

    onCaseFileBrowserListScroll() {
      this.scheduleCaseFileBrowserVisiblePreviewWarmup(180);
    },

    onCaseFileBrowserCardFocus(id) {
      this.requestCaseFileBrowserPreview(id, { reason: 'focus', delay: 40 });
    },

    startCaseFileBrowserCardPreviewCycle(id) {
      if (!id) return;
      this.requestCaseFileBrowserPreview(id, { reason: 'hover', delay: 20 });
      if (this.caseFileBrowserHoverPreviewTimer) window.clearInterval(this.caseFileBrowserHoverPreviewTimer);
      this.caseFileBrowserHoverPreviewId = id;
      this.caseFileBrowserHoverPreviewIndex = 0;
      this.caseFileBrowserHoverPreviewTimer = window.setInterval(() => {
        const preview = this.caseFileBrowserPreviewCache?.[id] || null;
        const flowCount = Array.isArray(preview?.flows) ? preview.flows.length : 0;
        if (flowCount > 1) this.caseFileBrowserHoverPreviewIndex = (Number(this.caseFileBrowserHoverPreviewIndex) + 1) % flowCount;
      }, 1350);
    },

    stopCaseFileBrowserCardPreviewCycle(id = '') {
      if (id && this.caseFileBrowserHoverPreviewId && id !== this.caseFileBrowserHoverPreviewId) return;
      if (this.caseFileBrowserHoverPreviewTimer) window.clearInterval(this.caseFileBrowserHoverPreviewTimer);
      this.caseFileBrowserHoverPreviewTimer = 0;
      this.caseFileBrowserHoverPreviewId = '';
      this.caseFileBrowserHoverPreviewIndex = 0;
    },

    scheduleCaseFileBrowserVisiblePreviewWarmup(delay = 160) {
      if (!this.showCaseFileBrowserModal) return;
      if (this.caseFileBrowserPreviewWarmupTimer) window.clearTimeout(this.caseFileBrowserPreviewWarmupTimer);
      this.caseFileBrowserPreviewWarmupTimer = window.setTimeout(() => {
        this.caseFileBrowserPreviewWarmupTimer = 0;
        this.warmCaseFileBrowserVisiblePreviews();
      }, Math.max(0, Number(delay) || 0));
    },

    warmCaseFileBrowserVisiblePreviews() {
      if (!this.showCaseFileBrowserModal) return;
      const listEl = this.$refs.caseFileBrowserListEl;
      let ids = [];
      if (listEl && typeof listEl.querySelectorAll === 'function' && typeof listEl.getBoundingClientRect === 'function') {
        const listRect = listEl.getBoundingClientRect();
        ids = Array.from(listEl.querySelectorAll('[data-case-file-id]'))
          .filter((el) => {
            const rect = el.getBoundingClientRect();
            return rect.bottom >= listRect.top - 80 && rect.top <= listRect.bottom + 80;
          })
          .map(el => el.getAttribute('data-case-file-id'))
          .filter(Boolean);
      }
      if (!ids.length) ids = this.caseFileBrowserVisibleRows.slice(0, 10).map(row => row.id).filter(Boolean);
      ids.slice(0, 16).forEach((id, index) => {
        this.requestCaseFileBrowserPreview(id, { reason: 'visible', delay: 80 + (index * 55), compact: true });
      });
    },

    requestCaseFileBrowserPreview(id, options = {}) {
      const cleanId = String(id || '').trim();
      if (!cleanId) return;
      const cached = this.caseFileBrowserPreviewCache?.[cleanId] || null;
      if (!options.force && cached && (cached.status === 'ready' || cached.status === 'loading')) return;
      const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      this.caseFileBrowserPreviewTokens = { ...(this.caseFileBrowserPreviewTokens || {}), [cleanId]: token };
      this.caseFileBrowserPreviewCache = {
        ...(this.caseFileBrowserPreviewCache || {}),
        [cleanId]: {
          ...(cached || {}),
          id: cleanId,
          status: 'loading',
          reason: options.reason || 'preview',
          flows: cached?.flows || [],
          startedAtUtc: new Date().toISOString()
        }
      };
      const existingTimer = this.caseFileBrowserPreviewBuildTimers?.[cleanId];
      if (existingTimer) window.clearTimeout(existingTimer);
      const timer = window.setTimeout(() => {
        const activeToken = this.caseFileBrowserPreviewTokens?.[cleanId];
        if (activeToken !== token) return;
        try {
          const row = (this.caseFileBrowserRows || []).find(item => item.id === cleanId) || null;
          const preview = this.buildCaseFileBrowserVisualPreview(row || { id: cleanId });
          this.caseFileBrowserPreviewCache = {
            ...(this.caseFileBrowserPreviewCache || {}),
            [cleanId]: preview
          };
          const timers = { ...(this.caseFileBrowserPreviewBuildTimers || {}) };
          delete timers[cleanId];
          this.caseFileBrowserPreviewBuildTimers = timers;
        } catch (error) {
          this.caseFileBrowserPreviewCache = {
            ...(this.caseFileBrowserPreviewCache || {}),
            [cleanId]: {
              id: cleanId,
              status: 'error',
              flows: [],
              error: error?.message || 'Could not build visual preview.',
              loadedAtUtc: new Date().toISOString()
            }
          };
        }
      }, Math.max(0, Number(options.delay) || 0));
      this.caseFileBrowserPreviewBuildTimers = { ...(this.caseFileBrowserPreviewBuildTimers || {}), [cleanId]: timer };
    },

    clearCaseFileBrowserPreviewTimers(options = {}) {
      if (this.caseFileBrowserPreviewWarmupTimer) window.clearTimeout(this.caseFileBrowserPreviewWarmupTimer);
      this.caseFileBrowserPreviewWarmupTimer = 0;
      Object.values(this.caseFileBrowserPreviewBuildTimers || {}).forEach((timer) => {
        if (timer) window.clearTimeout(timer);
      });
      this.caseFileBrowserPreviewBuildTimers = {};
      this.caseFileBrowserPreviewTokens = {};
      if (!options.keepCache) this.caseFileBrowserPreviewCache = {};
    },

    removeCaseFileBrowserPreviewCacheEntry(id) {
      const cleanId = String(id || '').trim();
      if (!cleanId) return;
      const next = { ...(this.caseFileBrowserPreviewCache || {}) };
      delete next[cleanId];
      this.caseFileBrowserPreviewCache = next;
    },

    caseFileBrowserPreviewIsLoading(id) {
      return (this.caseFileBrowserPreviewCache?.[id]?.status || '') === 'loading';
    },

    caseFileBrowserCardPreview(row = {}) {
      return row?.id ? (this.caseFileBrowserPreviewCache?.[row.id] || null) : null;
    },

    caseFileBrowserCardPreviewFlow(row = {}) {
      const preview = this.caseFileBrowserCardPreview(row);
      const flows = Array.isArray(preview?.flows) ? preview.flows : [];
      if (!flows.length) return null;
      const hoverActive = this.caseFileBrowserHoverPreviewId === row.id;
      const rawIndex = hoverActive ? Number(this.caseFileBrowserHoverPreviewIndex) || 0 : 0;
      return flows[Math.max(0, Math.min(flows.length - 1, rawIndex))] || flows[0] || null;
    },

    caseFileBrowserPreviewNodeClass(node = {}) {
      return [
        'case-file-browser-preview-node',
        `case-file-browser-preview-node--${node.type || 'address'}`,
        node.pathEnding ? 'case-file-browser-preview-node--ending' : '',
        node.service ? 'case-file-browser-preview-node--service' : ''
      ].filter(Boolean);
    },

    showPreviousCaseFileBrowserPreviewFlow() {
      if (!this.caseFileBrowserSelectedPreviewCanGoPrevious) return;
      this.caseFileBrowserSelectedPreviewIndex = Math.max(0, Number(this.caseFileBrowserSelectedPreviewIndex) - 1);
    },

    showNextCaseFileBrowserPreviewFlow() {
      if (!this.caseFileBrowserSelectedPreviewCanGoNext) return;
      const max = Math.max(0, this.caseFileBrowserSelectedPreviewFlows.length - 1);
      this.caseFileBrowserSelectedPreviewIndex = Math.min(max, Number(this.caseFileBrowserSelectedPreviewIndex) + 1);
    },

    buildCaseFileBrowserVisualPreview(row = {}) {
      const matrix = this.readCaseFileBrowserPreviewMainMatrix(row);
      const entries = this.buildCaseFileBrowserPreviewEntries(matrix);
      if (!entries.length) return this.buildCaseFileBrowserSummaryOnlyPreview(row);
      const flows = this.buildCaseFileBrowserPreviewFlows(entries, row);
      return {
        id: row.id || '',
        status: 'ready',
        source: matrix?.__caseFileBrowserPreviewSource || 'saved-main-data',
        summary: `${entries.length} transaction entr${entries.length === 1 ? 'y' : 'ies'} available for preview.`,
        entryCount: entries.length,
        flowCount: flows.length,
        flows,
        loadedAtUtc: new Date().toISOString()
      };
    },

    buildCaseFileBrowserSummaryOnlyPreview(row = {}) {
      const count = Math.max(1, Number(row.mainTransactionCount) || Number(row.preparedSaplingCount) || 1);
      const flows = Array.from({ length: Math.min(count, 6) }, (_, index) => {
        const label = index === 0 ? (row.hashExamples?.[0] || 'Flow 1') : `Flow ${index + 1}`;
        return this.buildCaseFileBrowserPlaceholderFlow({ row, index, label });
      });
      return {
        id: row.id || '',
        status: 'ready',
        source: 'summary-only',
        summary: 'Summary preview only; detailed Main Data was not available without loading the case.',
        entryCount: Number(row.mainEntryCount) || Number(row.mainTransactionCount) || 0,
        flowCount: flows.length,
        flows,
        loadedAtUtc: new Date().toISOString()
      };
    },

    buildCaseFileBrowserPlaceholderFlow({ row = {}, index = 0, label = 'Flow' } = {}) {
      const flowKey = `${row.id || 'case'}-placeholder-${index}`;
      const nodeCount = Math.max(3, Math.min(10, 2 + (Number(row.subtransactionCount) || 0)));
      const nodes = [];
      const edges = [];
      for (let i = 0; i < nodeCount; i += 1) {
        nodes.push({ id: `${flowKey}-n-${i}`, type: i % 2 ? 'tx' : 'address', x: i * 92, y: Math.sin(i * 0.9) * 28, shortLabel: i === 1 ? String(label).slice(0, 4) : '', pathEnding: i === nodeCount - 1 });
        if (i > 0) edges.push({ id: `${flowKey}-e-${i}`, path: `M ${(i - 1) * 92} ${Math.sin((i - 1) * 0.9) * 28} L ${i * 92} ${Math.sin(i * 0.9) * 28}` });
      }
      const viewBox = { x: -42, y: -70, width: Math.max(260, (nodeCount - 1) * 92 + 84), height: 140 };
      return { key: flowKey, label: String(label || `Flow ${index + 1}`), title: String(label || `Flow ${index + 1}`), summary: 'summary sketch', entryCount: Number(row.mainEntryCount) || 0, nodeCount: nodes.length, nodes, edges, viewBox, viewBoxString: `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}` };
    },

    readCaseFileBrowserPreviewMainMatrix(row = {}) {
      if (row?.id && row.id === this.currentProjectFileId && typeof this.getMatrixForLogicalSheet === 'function') {
        const current = this.getMatrixForLogicalSheet('main');
        if (Array.isArray(current) && current.length) {
          const matrix = normalizeCaseMatrix(current, Math.max(24, current[0]?.length || 24));
          matrix.__caseFileBrowserPreviewSource = 'current-main-sheet';
          return matrix;
        }
      }
      if (typeof window === 'undefined' || !window.localStorage || !row?.id) return [];
      const keys = [
        typeof this.getProjectInputStorageKey === 'function' ? this.getProjectInputStorageKey(row.id) : '',
        `tool60_input_${row.id}`,
        `caseBuddy_input_${row.id}`,
        `case_buddy_input_${row.id}`,
        `tool60_project_${row.id}`
      ].filter(Boolean);
      for (const key of Array.from(new Set(keys))) {
        let raw = '';
        try { raw = window.localStorage.getItem(key) || ''; } catch { raw = ''; }
        if (!raw) continue;
        const parsed = this.parseCaseFileBrowserStoredPayload(raw);
        const matrix = this.pickCaseFileBrowserMainMatrixFromPayload(parsed);
        if (Array.isArray(matrix) && matrix.length) {
          const normalized = normalizeCaseMatrix(matrix, Math.max(24, matrix[0]?.length || 24));
          normalized.__caseFileBrowserPreviewSource = key;
          return normalized;
        }
      }
      return [];
    },

    parseCaseFileBrowserStoredPayload(raw = '') {
      if (!raw) return null;
      try { return JSON.parse(raw); } catch { return raw; }
    },

    pickCaseFileBrowserMainMatrixFromPayload(payload) {
      const candidates = [];
      const isMatrix = value => Array.isArray(value) && value.length > 0 && value.every(row => Array.isArray(row));
      const visit = (value, path = '', depth = 0) => {
        if (depth > 5 || value == null) return;
        if (isMatrix(value)) {
          candidates.push({ path, matrix: value });
          return;
        }
        if (Array.isArray(value)) {
          value.slice(0, 32).forEach((item, index) => visit(item, `${path}[${index}]`, depth + 1));
          return;
        }
        if (typeof value === 'object') {
          Object.entries(value).forEach(([key, child]) => visit(child, path ? `${path}.${key}` : key, depth + 1));
        }
      };
      visit(payload);
      if (!candidates.length) return [];
      const score = (candidate) => {
        const path = String(candidate.path || '').toLowerCase();
        const rows = candidate.matrix || [];
        const meaningful = rows.filter(row => Array.isArray(row) && rowHasMeaningfulData(row)).length;
        const header = this.caseFileBrowserPreviewRowLooksLikeHeader(rows[0]) ? 12 : 0;
        const pathScore = /\bmain\b|input|case.?data|sheet.?0/.test(path) ? 40 : /raw|output|review/.test(path) ? -40 : 0;
        return pathScore + header + Math.min(meaningful, 200);
      };
      return candidates.sort((a, b) => score(b) - score(a))[0]?.matrix || [];
    },

    caseFileBrowserPreviewRowLooksLikeHeader(row = []) {
      const cells = Array.isArray(row) ? row.map(cell => normalizeCell(cell).toLowerCase()) : [];
      return cells.includes('transaction hash') || cells.includes('from address') || cells.includes('to address') || cells.includes('path label');
    },

    buildCaseFileBrowserPreviewEntries(matrix = []) {
      const rows = Array.isArray(matrix) ? matrix : [];
      const body = this.caseFileBrowserPreviewRowLooksLikeHeader(rows[0]) ? rows.slice(1) : rows;
      return body
        .map((row, localIndex) => ({ row, localIndex }))
        .filter(({ row }) => Array.isArray(row) && normalizeCell(row[1]) && !this.caseFileBrowserPreviewRowLooksLikeHeader(row))
        .map(({ row, localIndex }) => {
          const hash = normalizeCell(row[1]);
          const shortHash = this.caseFileBrowserShortHash(hash);
          const pathLabel = normalizeCell(row[0]) || shortHash;
          const fromHash = normalizeCell(row[14]);
          const continuesAs = normalizeCell(row[16]);
          return {
            key: `${localIndex}-${hash}`,
            rowIndex: localIndex,
            pathLabel,
            hash,
            hashKey: this.caseFileBrowserHashKey(hash),
            shortHash,
            fromAddress: normalizeCell(row[2]),
            toAddress: normalizeCell(row[3]),
            date: normalizeCell(row[4]),
            amount: normalizeCell(row[5]),
            token: normalizeCell(row[6]),
            toAttribution: normalizeCell(row[10]),
            fundsFollowed: normalizeCell(row[12]),
            fromHash,
            fromHashKey: this.caseFileBrowserHashKey(fromHash),
            continuesAs,
            pathEnding: /\bpath\s+ends\b/i.test(continuesAs) || /\bpath\s+ends\b/i.test(normalizeCell(row[9])),
            childHashKeys: this.caseFileBrowserExtractContinuationHashKeys(continuesAs)
          };
        });
    },

    buildCaseFileBrowserPreviewFlows(entries = [], row = {}) {
      const byHash = new Map();
      const registerHash = (hashValue, entry) => {
        this.caseFileBrowserHashLookupKeys(hashValue).forEach((key) => {
          if (key && !byHash.has(key)) byHash.set(key, entry);
        });
      };
      entries.forEach((entry) => {
        registerHash(entry.hash, entry);
        registerHash(entry.shortHash, entry);
        registerHash(entry.hashKey, entry);
      });
      const findEntry = value => this.caseFileBrowserHashLookupKeys(value).map(key => byHash.get(key)).find(Boolean) || null;
      const childrenByHash = new Map();
      const parentByChild = new Map();
      const addChild = (parent, child) => {
        if (!parent || !child || parent.hashKey === child.hashKey) return;
        if (!childrenByHash.has(parent.hashKey)) childrenByHash.set(parent.hashKey, []);
        const children = childrenByHash.get(parent.hashKey);
        if (!children.includes(child)) children.push(child);
        if (!parentByChild.has(child.hashKey)) parentByChild.set(child.hashKey, parent);
      };
      entries.forEach((entry) => {
        addChild(findEntry(entry.fromHash), entry);
      });
      entries.forEach((entry) => {
        entry.childHashKeys.forEach((childKey) => addChild(entry, findEntry(childKey)));
      });
      let roots = entries.filter(entry => !parentByChild.has(entry.hashKey));
      if (!roots.length) roots = entries.filter(entry => !entry.fromHashKey).slice(0, 1);
      if (!roots.length) roots = entries.slice(0, 1);
      const seenRoot = new Set();
      roots = roots.filter((entry) => {
        if (seenRoot.has(entry.hashKey)) return false;
        seenRoot.add(entry.hashKey);
        return true;
      }).slice(0, 12);
      return roots.map((root, index) => this.buildCaseFileBrowserPreviewFlow(root, entries, childrenByHash, parentByChild, index, row)).filter(Boolean);
    },

    buildCaseFileBrowserPreviewFlow(root, entries = [], childrenByHash = new Map(), parentByChild = new Map(), flowIndex = 0, row = {}) {
      const collected = [];
      const seen = new Set();
      const walk = (entry, depth = 0) => {
        if (!entry || seen.has(entry.hashKey) || collected.length >= 45) return;
        seen.add(entry.hashKey);
        collected.push({ entry, depth });
        const children = (childrenByHash.get(entry.hashKey) || []).slice().sort((a, b) => String(a.pathLabel).localeCompare(String(b.pathLabel), undefined, { numeric: true }));
        children.forEach(child => walk(child, depth + 1));
      };
      walk(root, 0);
      if (!collected.length) return null;
      const depthMap = new Map();
      collected.forEach((item) => {
        if (!depthMap.has(item.depth)) depthMap.set(item.depth, []);
        depthMap.get(item.depth).push(item);
      });
      depthMap.forEach((items) => {
        const center = (items.length - 1) / 2;
        items.forEach((item, index) => { item.y = (index - center) * 92; });
      });
      const itemByHash = new Map(collected.map(item => [item.entry.hashKey, item]));
      collected.forEach((item) => {
        const children = (childrenByHash.get(item.entry.hashKey) || []).map(child => itemByHash.get(child.hashKey)).filter(Boolean);
        if (children.length) item.y = children.reduce((sum, child) => sum + child.y, 0) / children.length;
      });
      const nodes = [];
      const edges = [];
      const ensureAddressNode = (id, x, y, entry, side = 'to') => {
        if (nodes.some(node => node.id === id)) return id;
        nodes.push({ id, type: 'address', x, y, shortLabel: side === 'from' ? this.caseFileBrowserShortAddress(entry.fromAddress) : this.caseFileBrowserShortAddress(entry.toAddress), service: Boolean(entry.toAttribution), pathEnding: side === 'to' && entry.pathEnding });
        return id;
      };
      const addEdge = (fromId, toId) => {
        const from = nodes.find(node => node.id === fromId);
        const to = nodes.find(node => node.id === toId);
        if (!from || !to) return;
        edges.push({ id: `${fromId}->${toId}`, path: `M ${from.x} ${from.y} C ${(from.x + to.x) / 2} ${from.y}, ${(from.x + to.x) / 2} ${to.y}, ${to.x} ${to.y}` });
      };
      collected.forEach((item) => {
        const entry = item.entry;
        const baseX = item.depth * 245;
        const txId = `${entry.hashKey}-tx`;
        const toId = `${entry.hashKey}-to`;
        nodes.push({ id: txId, type: 'tx', x: baseX + 92, y: item.y, shortLabel: entry.pathLabel || entry.shortHash, pathEnding: entry.pathEnding });
        ensureAddressNode(toId, baseX + 188, item.y, entry, 'to');
        if (item.depth === 0) {
          const fromId = `${entry.hashKey}-from`;
          ensureAddressNode(fromId, baseX, item.y, entry, 'from');
          addEdge(fromId, txId);
        } else {
          const parent = parentByChild.get(entry.hashKey) || entries.find(candidate => candidate.hashKey === entry.fromHashKey);
          const parentToId = parent ? `${parent.hashKey}-to` : '';
          if (parentToId && nodes.some(node => node.id === parentToId)) addEdge(parentToId, txId);
        }
        addEdge(txId, toId);
      });
      const xs = nodes.map(node => node.x);
      const ys = nodes.map(node => node.y);
      const contentMinX = Math.min(...xs);
      const contentMaxX = Math.max(...xs);
      const contentMinY = Math.min(...ys);
      const contentMaxY = Math.max(...ys);
      const contentCenterX = (contentMinX + contentMaxX) / 2;
      const contentCenterY = (contentMinY + contentMaxY) / 2;
      const viewWidth = Math.max(360, (contentMaxX - contentMinX) + 144);
      const viewHeight = Math.max(180, (contentMaxY - contentMinY) + 148);
      const viewBox = {
        x: contentCenterX - (viewWidth / 2),
        y: contentCenterY - (viewHeight / 2),
        width: viewWidth,
        height: viewHeight
      };
      return {
        key: `${row.id || 'case'}-${root.hashKey}-${flowIndex}`,
        label: root.pathLabel || `Flow ${flowIndex + 1}`,
        title: `${root.pathLabel || `Flow ${flowIndex + 1}`} · ${root.shortHash}`,
        summary: collected.length >= 45 ? 'first 45 entries shown' : 'from Main Data',
        hash: root.hash,
        entryCount: collected.length,
        nodeCount: nodes.length,
        nodes,
        edges,
        viewBox,
        viewBoxString: `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`
      };
    },

    caseFileBrowserHashKey(value = '') {
      const text = normalizeCell(value);
      return /^0x/i.test(text) ? text.toLowerCase() : text;
    },

    caseFileBrowserHashLookupKeys(value = '') {
      const text = normalizeCell(value);
      if (!text) return [];
      const normalized = this.caseFileBrowserHashKey(text);
      const keys = new Set([normalized]);
      const ellipsisMatch = normalized.match(/^(0x)?([a-z0-9]{4,})[.…]{1,3}([a-z0-9]{3,})$/iu);
      if (ellipsisMatch) {
        keys.add(`${ellipsisMatch[1] || ''}${ellipsisMatch[2]}…${ellipsisMatch[3]}`.toLowerCase());
      } else if (normalized.length > 18) {
        keys.add(this.caseFileBrowserShortHash(normalized).toLowerCase());
        keys.add(`${normalized.slice(0, 10)}…${normalized.slice(-8)}`.toLowerCase());
        keys.add(`${normalized.slice(0, 8)}…${normalized.slice(-6)}`.toLowerCase());
      }
      return Array.from(keys).filter(Boolean);
    },

    caseFileBrowserExtractContinuationHashKeys(value = '') {
      const text = normalizeCell(value).replace(/^continues\s+as\s*/i, '');
      if (!text || /path\s+ends/i.test(text)) return [];
      return text.split(/[,;\s]+/u).map(item => this.caseFileBrowserHashKey(item)).filter(item => item && item.length >= 6);
    },

    caseFileBrowserShortHash(value = '') {
      const text = normalizeCell(value);
      if (!text) return '';
      if (text.length <= 18) return text;
      return `${text.slice(0, 6)}…${text.slice(-6)}`;
    },

    caseFileBrowserShortAddress(value = '') {
      const text = normalizeCell(value);
      if (!text) return '';
      if (text.length <= 12) return text;
      return `${text.slice(0, 6)}…${text.slice(-4)}`;
    },


    buildWorkflowStepView(step) {
      const original = this.callCaseBuddyOriginalMethod(caseBuddyWorkflowMethods, 'buildWorkflowStepView', [step]);
      const view = original && typeof original === 'object' ? original : { ...(step || {}) };
      if (this.isStartingMainTransactionsWorkflowStep(view) || this.isStartingMainTransactionsWorkflowStep(step)) {
        return this.applyStartingMainTransactionsWorkflowStepCurrentRows(view, step);
      }
      const liveIntakeView = this.applyCaseWorkflowSpreadsheetBackedIntakeCurrentState(view, step);
      if (liveIntakeView !== view) return liveIntakeView;
      if (this.isCaseWorkflowRawDataFetchStep(view) || this.isCaseWorkflowRawDataFetchStep(step)) {
        return this.applyRawDataFetchWorkflowStepCurrentCoverage(view, step);
      }
      if (this.isCaseWorkflowRawToMainPreparationStep(view) || this.isCaseWorkflowRawToMainPreparationStep(step)) {
        return this.applyCaseWorkflowRawToMainPreparationCurrentState(view, step);
      }
      if (this.isCaseWorkflowAddressAttributionFillStep(view) || this.isCaseWorkflowAddressAttributionFillStep(step)) {
        return this.applyCaseWorkflowAddressAttributionFillCurrentState(view);
      }
      let nextView = view;
      if (this.isCaseWorkflowAddressColorLikeCellsStep(view) || this.isCaseWorkflowAddressColorLikeCellsStep(step)) {
        nextView = this.applyCaseWorkflowAddressColorLikeCellsCurrentState(nextView, step);
      }
      if (this.isCaseWorkflowWwwUsdReferenceStep(view) || this.isCaseWorkflowWwwUsdReferenceStep(step)) {
        nextView = this.applyCaseWorkflowWwwUsdReferenceCurrentState(nextView, step);
      }
      nextView = this.applyCaseWorkflowAutoSoftCompletionState(nextView, step);
      nextView = this.applyCaseWorkflowStatefulGeneratedReviewCurrentState(nextView, step);
      nextView = this.applyCaseWorkflowColumnHReferencePreservedStep(nextView, step);
      if (this.isCaseWorkflowWwwUsdReferenceStep(nextView) || this.isCaseWorkflowWwwUsdReferenceStep(step)) {
        return this.applyCaseWorkflowWwwUsdReferenceCurrentState(nextView, step);
      }
      return nextView;
    },

    caseWorkflowSpreadsheetBackedIntakeKeyForStep(step = {}) {
      const tag = normalizeCell(step?.stepTag || step?.tag || step?.id || step?.checkId || '').toUpperCase();
      const byTag = {
        CASE_INTAKE: 'starting',
        ADD_MAIN_TRANSACTIONS_TO_CASE: 'starting',
        NETWORK_DISCOVERY: 'networks',
        TRANSACTION_DATES: 'dates',
        MAIN_TRANSACTION_PATH_LABELS: 'labels'
      };
      if (byTag[tag]) return byTag[tag];
      const title = normalizeCell(step?.stepTitle || step?.title || step?.label || '').toLowerCase();
      if (/starting\s+(?:main\s+)?transactions|load\s+starting/.test(title)) return 'starting';
      if (/transaction\s+networks|identify\s+.*network/.test(title)) return 'networks';
      if (/utc\s+dates|transaction\s+dates|order\s+main\s+entries/.test(title)) return 'dates';
      if (/main\s+transaction\s+path\s+labels|assign\s+main\s+.*labels/.test(title)) return 'labels';
      return '';
    },

    applyCaseWorkflowSpreadsheetBackedIntakeCurrentState(view = {}, definition = {}) {
      const key = this.caseWorkflowSpreadsheetBackedIntakeKeyForStep(view)
        || this.caseWorkflowSpreadsheetBackedIntakeKeyForStep(definition);
      if (!['networks', 'dates', 'labels'].includes(key)) return view;

      const live = this.caseIntakeRailLiveDataState || {};
      if (!live.readSucceeded || !live.startingComplete) return view;
      const completeByKey = {
        networks: Boolean(live.networksComplete),
        dates: Boolean(live.datesComplete),
        labels: Boolean(live.labelsComplete)
      };
      const complete = completeByKey[key];
      const rootCount = Math.max(0, Number(live.rootCount) || 0);
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const checkedAtUtc = view?.checkResult?.checkedAtUtc || new Date().toISOString();
      const message = normalizeCell(live.statusDetailByKey?.[key]) || `${rootCount} root transaction${rootCount === 1 ? '' : 's'} were checked against current Main Data.`;
      const completeStatusLabelByKey = {
        networks: `Complete · ${rootCount}/${rootCount} identified`,
        dates: `Complete · ${rootCount}/${rootCount} dated and ordered`,
        labels: `Complete · ${rootCount}/${rootCount} labeled`
      };
      const incompleteStatusLabelByKey = {
        networks: `${Number(live.missingNetworkCount) || 0} network value${Number(live.missingNetworkCount) === 1 ? '' : 's'} missing`,
        dates: Number(live.missingDateCount) > 0
          ? `${Number(live.missingDateCount)} UTC date${Number(live.missingDateCount) === 1 ? '' : 's'} missing`
          : `${Number(live.outOfOrderCount) || 0} order issue${Number(live.outOfOrderCount) === 1 ? '' : 's'}`,
        labels: Number(live.missingLabelCount) > 0
          ? `${Number(live.missingLabelCount)} main label${Number(live.missingLabelCount) === 1 ? '' : 's'} missing`
          : `${Number(live.outOfSequenceLabelCount) || 0} label order issue${Number(live.outOfSequenceLabelCount) === 1 ? '' : 's'}`
      };
      const checkIdByKey = {
        networks: 'network_discovery_live_main_data',
        dates: 'transaction_dates_live_main_data',
        labels: 'main_transaction_path_labels_live_main_data'
      };

      if (complete) {
        return {
          ...view,
          complete: true,
          stale: false,
          blocked: false,
          status: 'complete',
          statusLabel: completeStatusLabelByKey[key],
          statusClass: 'workflow-step-card--complete',
          staleReason: '',
          derivedFromCurrentRows: true,
          sources: {
            ...(view.sources || {}),
            check: true,
            liveMainData: true
          },
          checkResult: {
            ...(view.checkResult || {}),
            id: view?.checkResult?.id || checkIdByKey[key],
            status: 'pass',
            message,
            revision: currentRevision,
            checkedAtUtc,
            derivedFromCurrentRows: true,
            details: {
              ...((view.checkResult || {}).details || {}),
              source: 'current-main-data',
              rootCount,
              derivedFromCurrentRows: true
            }
          },
          completionSummary: message
        };
      }

      const hadPriorCompletion = Boolean(
        view.complete
        || view.stale
        || view.sources?.manual
        || view.sources?.signal
        || view.sources?.check
        || view.sources?.autoSoftCompletion
      );
      return {
        ...view,
        complete: false,
        stale: hadPriorCompletion,
        blocked: false,
        status: hadPriorCompletion ? 'stale' : 'attention',
        statusLabel: incompleteStatusLabelByKey[key],
        statusClass: hadPriorCompletion ? 'workflow-step-card--stale' : 'workflow-step-card--attention',
        staleReason: message,
        derivedFromCurrentRows: true,
        autoSoftCompletion: false,
        sources: {
          ...(view.sources || {}),
          check: false,
          signal: false,
          autoSoftCompletion: false,
          liveMainData: true
        },
        checkResult: {
          ...(view.checkResult || {}),
          id: view?.checkResult?.id || checkIdByKey[key],
          status: 'fail',
          message,
          revision: currentRevision,
          checkedAtUtc,
          derivedFromCurrentRows: true,
          details: {
            ...((view.checkResult || {}).details || {}),
            source: 'current-main-data',
            rootCount,
            derivedFromCurrentRows: true,
            missingNetworkCount: Number(live.missingNetworkCount) || 0,
            missingDateCount: Number(live.missingDateCount) || 0,
            outOfOrderCount: Number(live.outOfOrderCount) || 0,
            missingLabelCount: Number(live.missingLabelCount) || 0,
            outOfSequenceLabelCount: Number(live.outOfSequenceLabelCount) || 0
          }
        },
        completionSummary: message
      };
    },

    clearResolvedCaseWorkflowIntakeAutomationPause(live = {}, source = 'live-main-data-intake-reconcile') {
      const pause = this.caseAutomation?.manualPause || null;
      if (!pause || normalizeCell(pause.pauseKind || '') !== 'automation-repeat-guard') return false;
      const key = this.caseWorkflowSpreadsheetBackedIntakeKeyForStep(pause);
      const completeByKey = {
        starting: Boolean(live.startingComplete),
        networks: Boolean(live.networksComplete),
        dates: Boolean(live.datesComplete),
        labels: Boolean(live.labelsComplete)
      };
      if (!key || !completeByKey[key]) return false;

      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        paused: false,
        busy: false,
        manualPause: null,
        lastRepeatedStepKey: '',
        repeatedStepCount: 0,
        status: 'Current Main Data confirmed',
        detail: normalizeCell(live.statusDetailByKey?.[key]) || `The ${key} step is already complete in Main Data.`,
        intakePauseClearedAtUtc: new Date().toISOString(),
        intakePauseClearSource: source
      };
      this.showCaseAutomationPauseModal = false;
      return true;
    },

    async reconcileCaseWorkflowSpreadsheetBackedIntake(options = {}) {
      const matrix = typeof this.getMatrixForLogicalSheet === 'function'
        ? this.getMatrixForLogicalSheet('main')
        : this.emptyCaseStarterGetMatrix?.('main');
      const live = this.buildCaseIntakeRailLiveDataState(Array.isArray(matrix) ? matrix : []);
      if (!live.startingComplete) {
        return { handled: false, success: false, live, refreshedKeys: [], reason: 'no-root-transactions' };
      }

      const source = normalizeCell(options.source || 'case-workflow-live-intake-reconcile');
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const now = new Date().toISOString();
      const completeByKey = {
        networks: Boolean(live.networksComplete),
        dates: Boolean(live.datesComplete),
        labels: Boolean(live.labelsComplete)
      };
      const tagByKey = {
        networks: 'NETWORK_DISCOVERY',
        dates: 'TRANSACTION_DATES',
        labels: 'MAIN_TRANSACTION_PATH_LABELS'
      };
      const refreshedKeys = [];

      this.recordStartingMainTransactionsWorkflowCurrentCheck?.(`${source}-starting`, { silent: true });
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const workflowState = { ...(this.caseSupportState.workflowState || {}) };
      const manualCompleteByTag = { ...(workflowState.manualCompleteByTag || {}) };
      Object.entries(completeByKey).forEach(([key, complete]) => {
        if (!complete) return;
        const tag = tagByKey[key];
        const existing = manualCompleteByTag[tag] || {};
        if (Number(existing.revision) === revision && normalizeCell(existing.sourceEvidence || '') === 'current-main-data') return;
        manualCompleteByTag[tag] = {
          ...existing,
          completedAtUtc: existing.completedAtUtc || now,
          checkedAtUtc: now,
          revision,
          source,
          sourceActionId: source,
          sourceEvidence: 'current-main-data',
          rootCount: Number(live.rootCount) || 0
        };
        refreshedKeys.push(key);
      });
      if (refreshedKeys.length) {
        this.caseSupportState.workflowState = { ...workflowState, manualCompleteByTag };
        if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(80);
        else this.persistCaseSupportState?.();
      }

      const clearedPause = this.clearResolvedCaseWorkflowIntakeAutomationPause(live, source);
      this.queueCaseSummaryRefresh?.(0);
      await this.$nextTick();
      if (Number(options.settleMs) > 0) {
        await new Promise(resolve => {
          const schedule = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
            ? window.setTimeout.bind(window)
            : setTimeout;
          schedule(resolve, Math.max(0, Number(options.settleMs) || 0));
        });
      }
      return { handled: true, success: true, live, refreshedKeys, clearedPause };
    },

    async automationIdentifyTransactionNetworks(...args) {
      // Do not reopen network discovery merely because its organizer snapshot was
      // invalidated by later case work. Current root rows in Columns T/U are the
      // authoritative evidence for this stage.
      const before = await this.reconcileCaseWorkflowSpreadsheetBackedIntake({
        source: 'network-automation-live-main-data',
        settleMs: 20
      });
      if (before?.live?.networksComplete) {
        this.showNetworkLookupModal = false;
        this.networkLookupStatus = normalizeCell(before.live.statusDetailByKey?.networks) || 'Transaction networks are already complete in Main Data.';
        return { handled: true, success: true, reason: 'network-data-already-complete', reconciliation: before };
      }

      const original = caseBuddyAutomationMethods?.automationIdentifyTransactionNetworks
        || caseBuddyDateNetworkRawMethods?.automationIdentifyTransactionNetworks;
      if (typeof original !== 'function') {
        return { handled: true, success: false, reason: 'network-automation-method-unavailable' };
      }
      const result = await original.apply(this, args);
      const after = await this.reconcileCaseWorkflowSpreadsheetBackedIntake({
        source: 'network-automation-post-lookup',
        settleMs: 40
      });
      if (after?.live?.networksComplete) {
        this.showNetworkLookupModal = false;
        this.networkLookupStatus = normalizeCell(after.live.statusDetailByKey?.networks) || this.networkLookupStatus;
      }
      return result;
    },

    caseWorkflowStepIdentity(step = {}) {
      return normalizeCell(step?.stepTag || step?.tag || step?.id || step?.checkId || step?.title || '').toUpperCase();
    },

    caseWorkflowStepTitleText(step = {}) {
      return normalizeCell(step?.stepTitle || step?.title || step?.label || '').toLowerCase();
    },

    isCaseWorkflowAddressColorLikeCellsStep(step = {}) {
      const tag = this.caseWorkflowStepIdentity(step);
      const title = this.caseWorkflowStepTitleText(step);
      const actionText = [
        step?.action,
        step?.actionId,
        step?.key,
        step?.method,
        step?.handler,
        step?.primaryAction,
        step?.status
      ].map(value => normalizeCell(value).toLowerCase()).join(' ');
      if (tag === 'ADDRESS_COLOR_LIKE_CELLS') return true;
      if (/address[_\s-]*color|color[_\s-]*case[_\s-]*address|color[_\s-]*like[_\s-]*cells/.test(actionText)) return true;
      return /color(?:ing)?\s+matching\s+from\/?to\s+addresses|color\s+matching\s+from\/?to\s+addresses|color\s+like\s+cells/.test(title);
    },

    isCaseWorkflowAddressColorLikeCellsAction(action = {}, step = {}) {
      if (this.isCaseWorkflowAddressColorLikeCellsStep(step)) return true;
      const text = [
        action?.id,
        action?.key,
        action?.action,
        action?.label,
        action?.title,
        action?.method,
        action?.handler,
        action?.status
      ].map(value => normalizeCell(value).toLowerCase()).join(' ');
      return /address[_\s-]*color|color[_\s-]*case[_\s-]*address|color[_\s-]*like[_\s-]*cells|color(?:ing)?\s+matching\s+from\/?to\s+addresses/.test(text);
    },

    caseWorkflowStableStringHash(value = '') {
      const text = String(value == null ? '' : value);
      let hash = 2166136261;
      for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return (hash >>> 0).toString(36);
    },

    getCaseWorkflowAddressColorBasis() {
      const matrix = typeof this.getMatrixForLogicalSheet === 'function'
        ? this.getMatrixForLogicalSheet('main')
        : this.emptyCaseStarterGetMatrix?.('main');
      const rows = normalizeCaseMatrix(Array.isArray(matrix) ? matrix : []);
      const addressSet = new Set();
      const basisRows = rows.slice(1)
        .map((row, index) => {
          const fromAddress = normalizeCell(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS] || row?.[2] || '').toLowerCase();
          const toAddress = normalizeCell(row?.[CASE_MANUAL_START_COL.TO_ADDRESS] || row?.[3] || '').toLowerCase();
          if (fromAddress) addressSet.add(fromAddress);
          if (toAddress) addressSet.add(toAddress);
          return { rowIndex: index + 1, fromAddress, toAddress };
        })
        .filter(row => row.fromAddress || row.toAddress);
      const basisText = JSON.stringify(basisRows);
      return {
        basisHash: `address-color-${this.caseWorkflowStableStringHash(basisText)}`,
        rowCount: basisRows.length,
        addressCount: addressSet.size,
        basisRows: basisRows.slice(0, 48)
      };
    },

    getCaseWorkflowAddressColorLikeCellsRecord() {
      const workflow = this.caseSupportState?.workflowState || {};
      return workflow.addressColorRun || workflow.addressColorLikeCellsRun || workflow.addressColorLikeCellsReview || null;
    },

    caseWorkflowAddressColorLikeCellsRecordIsCurrent(record = null, basis = null) {
      if (!record || typeof record !== 'object') return false;
      const targetBasis = basis || this.getCaseWorkflowAddressColorBasis();
      if (record.basisHash && targetBasis.basisHash && record.basisHash === targetBasis.basisHash) return true;
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const recordRevision = Number(record.revision ?? record.checkedRevision ?? record.completedRevision ?? record.caseDataRevision) || 0;
      if (!!(record.completedAtUtc || record.checkedAtUtc || record.appliedAtUtc || record.reviewedAtUtc) && !!recordRevision && recordRevision === currentRevision) return true;
      const uniqueCount = Number(record.addressCount ?? record.uniqueAddressCount ?? record.uniqueAddresses ?? 0) || 0;
      const coloredCellCount = Number(record.coloredCellCount ?? record.addressCellCount ?? record.cellCount ?? 0) || 0;
      const rowCount = Number(record.rowCount ?? 0) || 0;
      const targetAddressCount = Number(targetBasis?.addressCount) || 0;
      const targetRowCount = Number(targetBasis?.rowCount) || 0;
      const countMatches = targetRowCount > 0 && targetAddressCount > 0
        && (!uniqueCount || uniqueCount === targetAddressCount)
        && (!rowCount || rowCount === targetRowCount)
        && (!coloredCellCount || coloredCellCount >= targetRowCount);
      if (countMatches && (record.completedAtUtc || record.checkedAtUtc || record.appliedAtUtc || record.reviewedAtUtc)) return true;
      return false;
    },

    recordCaseWorkflowAddressColorLikeCellsCurrent(context = {}) {
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const now = this.emptyCaseStarterNudgeNowUtc ? this.emptyCaseStarterNudgeNowUtc() : new Date().toISOString();
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const basis = this.getCaseWorkflowAddressColorBasis();
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) }
      };
      const step = context.step || this.recommendedWorkflowStep || { tag: 'ADDRESS_COLOR_LIKE_CELLS', title: 'Color matching From/To addresses' };
      const signalCode = normalizeCell(step?.signalCode || step?.signal || step?.completionSignal || step?.completionSignalCode || 'ADDRESS_COLOR_LIKE_CELLS') || 'ADDRESS_COLOR_LIKE_CELLS';
      const record = {
        ...(workflow.addressColorRun || {}),
        completedAtUtc: now,
        checkedAtUtc: now,
        appliedAtUtc: now,
        revision: currentRevision,
        basisHash: basis.basisHash,
        rowCount: basis.rowCount,
        addressCount: basis.addressCount,
        source: context.source || 'workflow-address-color-like-cells',
        stepTag: normalizeCell(step?.tag || 'ADDRESS_COLOR_LIKE_CELLS'),
        activeSheetRestored: normalizeCell(context.activeSheetRestored || '')
      };
      workflow.addressColorRun = record;
      workflow.addressColorLikeCellsRun = record;
      const addressColorSignalCodes = Array.from(new Set([signalCode, 'ADDRESS_FLOW_COLORS_APPLIED', 'ADDRESS_COLOR_LIKE_CELLS'].map(code => normalizeCell(code).toUpperCase()).filter(Boolean)));
      addressColorSignalCodes.forEach((code) => {
        workflow.signalCompleteByCode[code] = {
          ...(workflow.signalCompleteByCode[code] || {}),
          completedAtUtc: workflow.signalCompleteByCode[code]?.completedAtUtc || now,
          checkedAtUtc: now,
          appliedAtUtc: now,
          revision: currentRevision,
          basisHash: basis.basisHash,
          source: context.source || 'workflow-address-color-like-cells',
          stepTag: normalizeCell(step?.tag || 'ADDRESS_COLOR_LIKE_CELLS')
        };
      });
      this.caseSupportState.workflowState = workflow;
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        address_color_like_cells: {
          ...(this.caseSupportState.checkResultsById?.address_color_like_cells || {}),
          id: 'address_color_like_cells',
          status: 'pass',
          message: `Address color linking is current for ${basis.rowCount} Main Data row${basis.rowCount === 1 ? '' : 's'} and ${basis.addressCount} address${basis.addressCount === 1 ? '' : 'es'}.`,
          details: {
            ...((this.caseSupportState.checkResultsById?.address_color_like_cells || {}).details || {}),
            source: context.source || 'workflow-address-color-like-cells',
            stepTag: normalizeCell(step?.tag || 'ADDRESS_COLOR_LIKE_CELLS'),
            basisHash: basis.basisHash,
            rowCount: basis.rowCount,
            addressCount: basis.addressCount,
            activeSheetRestored: normalizeCell(context.activeSheetRestored || ''),
            currentMainDataDerived: true
          },
          revision: currentRevision,
          checkedAtUtc: now,
          derivedFromCurrentRows: true
        }
      };
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      return record;
    },

    applyCaseWorkflowAddressColorLikeCellsCurrentState(view = {}) {
      const basis = this.getCaseWorkflowAddressColorBasis();
      const record = this.getCaseWorkflowAddressColorLikeCellsRecord();
      const originalLooksComplete = view?.complete === true || !!(view?.sources?.signal || view?.sources?.manual || view?.sources?.check);
      const recentAutoColored = (Array.isArray(this.caseAutomation?.log) ? this.caseAutomation.log : []).some(entry => {
        const message = normalizeCell(entry?.message).toLowerCase();
        if (!/color(?:ing)?\s+matching\s+from\/?to\s+addresses|color\s+like\s+cells/.test(message)) return false;
        const atMs = Date.parse(entry?.atUtc || '');
        return !Number.isFinite(atMs) || Date.now() - atMs < 5 * 60 * 1000;
      });
      const recordIsCurrent = this.caseWorkflowAddressColorLikeCellsRecordIsCurrent(record, basis);
      const autoLogLooksCurrent = recentAutoColored && basis.rowCount > 0 && (originalLooksComplete || this.caseAutomation?.active || this.caseAutomation?.busy);
      if (!recordIsCurrent && !autoLogLooksCurrent) return view;
      const checkedAtUtc = record?.checkedAtUtc || record?.completedAtUtc || this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      return {
        ...view,
        complete: true,
        stale: false,
        blocked: false,
        status: 'complete',
        statusLabel: basis.rowCount > 0 ? `Complete · ${basis.addressCount} address${basis.addressCount === 1 ? '' : 'es'} linked` : 'Complete',
        statusClass: 'workflow-step-card--complete',
        staleReason: '',
        sources: {
          ...(view.sources || {}),
          check: true,
          signal: !!((view.sources || {}).signal || originalLooksComplete),
          manual: !!(view.sources || {}).manual,
          addressColorBasis: true
        },
        checkResult: {
          ...(view.checkResult || {}),
          id: 'address_color_like_cells',
          status: 'pass',
          message: `Address color linking is current for ${basis.rowCount} Main Data row${basis.rowCount === 1 ? '' : 's'}.`,
          revision: Number(this.caseSupportState?.caseDataRevision) || 0,
          checkedAtUtc,
          details: {
            ...((view.checkResult || {}).details || {}),
            basisHash: basis.basisHash,
            rowCount: basis.rowCount,
            addressCount: basis.addressCount,
            currentMainDataDerived: true
          }
        },
        completionSummary: view?.completionSummary || 'Address color linking is current for the current Main Data From/To address set.',
        addressColorBasisCurrent: true
      };
    },

    async prepareCaseWorkflowAddressColorLikeCellsRun() {
      let activeBefore = '';
      try {
        const workbook = this.$refs.inputSpreadsheet;
        activeBefore = normalizeCell(workbook?.activeSheetName || workbook?.getActiveSheet?.()?.name || '');
        if (typeof this.activateLogicalSheet === 'function') {
          await this.activateLogicalSheet('main');
          await this.caseWorkflowUiNextTick?.();
        }
      } catch (error) {
        console.warn('Case Buddy could not switch to Main Data before address color workflow action.', error);
      }
      return activeBefore;
    },

    async runCaseWorkflowAddressColorLikeCellsOriginalMethod(methodName = '', args = []) {
      const activeSheetBefore = await this.prepareCaseWorkflowAddressColorLikeCellsRun({ tag: 'ADDRESS_COLOR_LIKE_CELLS' });
      const result = this.callFirstCaseBuddyOriginalMethod(methodName, Array.isArray(args) ? args : []);
      const resolved = result && typeof result.then === 'function' ? await result : result;
      await this.caseWorkflowUiNextTick?.();
      this.recordCaseWorkflowAddressColorLikeCellsCurrent({
        step: { tag: 'ADDRESS_COLOR_LIKE_CELLS', title: 'Color matching From/To addresses' },
        source: `workflow-address-color-like-cells-${methodName || 'method'}`,
        activeSheetRestored: activeSheetBefore
      });
      return resolved;
    },

    async automationColorCaseAddressCells(...args) {
      return this.runCaseWorkflowAddressColorLikeCellsOriginalMethod('automationColorCaseAddressCells', args);
    },

    async automationColorMatchingAddressCells(...args) {
      return this.runCaseWorkflowAddressColorLikeCellsOriginalMethod('automationColorMatchingAddressCells', args);
    },

    async colorCaseAddressCells(...args) {
      return this.runCaseWorkflowAddressColorLikeCellsOriginalMethod('colorCaseAddressCells', args);
    },

    async colorMatchingAddressCells(...args) {
      return this.runCaseWorkflowAddressColorLikeCellsOriginalMethod('colorMatchingAddressCells', args);
    },

    async applyCaseAddressColorLikeCells(...args) {
      return this.runCaseWorkflowAddressColorLikeCellsOriginalMethod('applyCaseAddressColorLikeCells', args);
    },

    async applyAddressColorLikeCells(...args) {
      return this.runCaseWorkflowAddressColorLikeCellsOriginalMethod('applyAddressColorLikeCells', args);
    },

    caseWorkflowAutoSoftStepTag(step = {}) {
      const tag = this.caseWorkflowStepIdentity(step);
      if (tag && CASE_WORKFLOW_AUTO_SOFT_COMPLETION_BY_TAG[tag]) return tag;
      const title = this.caseWorkflowStepTitleText(step);
      const actionText = [
        step?.action,
        step?.actionId,
        step?.key,
        step?.method,
        step?.handler,
        step?.primaryAction,
        step?.status,
        step?.title,
        step?.stepTitle
      ].map(value => normalizeCell(value).toLowerCase()).join(' ');
      const text = `${title} ${actionText}`;
      if (/fill(?:ing)?\s+(?:missing\s+)?address\s+attribution|address[_\s-]*attribution[_\s-]*fill|open[_\s-]*case[_\s-]*attribution[_\s-]*fill/.test(text)) return 'ADDRESS_ATTRIBUTION_FILL';
      if (/color(?:ing)?\s+matching\s+from\/?to\s+addresses|address[_\s-]*color|color[_\s-]*like[_\s-]*cells/.test(text)) return 'ADDRESS_COLOR_LIKE_CELLS';
      if (/review\s+new\s+entities|entity\s+review|curator/.test(text)) return 'ENTITY_REVIEW';
      if (/external\s+attribution/.test(text)) return 'EXTERNAL_ATTRIBUTION_REVIEW';
      if (/case\s+inconsistenc/.test(text)) return 'CASE_INCONSISTENCIES';
      if (/normalize.*deposit|hot[-\s]*wallet|unknown[-\s]*service/.test(text)) return 'NORMALIZE_DEPOSIT_NOTES';
      if (/deposit\s+pattern/.test(text)) return 'HIGH_PRIORITY_DEPOSIT_PATTERNS';
      if (/known[-\s]*entity.*casing|note\s+casing/.test(text)) return 'KNOWN_ENTITY_NOTE_CASING';
      if (/duplicate\s+transaction\s+hash|duplicate\s+hash/.test(text)) return 'DUPLICATE_HASH_LABELS';
      if (/back\s*up\s+notes|replace\s+hashes\s+with\s+path\s+labels|notes[_\s-]*backup/.test(text)) return 'NOTES_BACKUP_AND_HASH_REPLACEMENT';
      if (/address\s+legend/.test(text)) return 'ADDRESS_LEGEND';
      if (/template\s+deliverable/.test(text)) return 'TEMPLATE_DELIVERABLE';
      if (/suspicious\s+address\s+table/.test(text)) return 'SUSPICIOUS_ADDRESS_TABLE';
      if (/exchange\s+address\s+table/.test(text)) return 'EXCHANGE_ADDRESS_TABLE';
      if (/dormant\s+address/.test(text)) return 'DORMANT_ADDRESS_DELIVERABLES';
      if (/what\s+went\s+where/.test(text)) return 'WHAT_WENT_WHERE';
      if (/case\s+conclusion/.test(text)) return 'CASE_CONCLUSION';
      if (/report\s+body/.test(text)) return 'REPORT_BODY';
      if (/path[-\s]*ending.*addendum|addendum\s+path[-\s]*ending/.test(text)) return 'PATH_ENDING_ADDENDUM_REVIEW';
      if (/recipient\s+entity/.test(text)) return 'ADDENDUM_RECIPIENT_ENTITY';
      if (/build\s+and\s+review\s+addendum|addendum\s+build/.test(text)) return 'ADDENDUM_BUILD';
      if (/case\s+flow\s+visual|create\s+case\s+flow/.test(text)) return 'CASE_FLOW_VISUALS';
      if (/completion\s+summary|one[-\s]*click\s+report|full\s+report\s+package/.test(text)) return 'CASE_COMPLETION_SUMMARY';
      return '';
    },

    caseWorkflowAutoSoftStepConfig(step = {}) {
      const tag = this.caseWorkflowAutoSoftStepTag(step);
      return tag ? { tag, ...(CASE_WORKFLOW_AUTO_SOFT_COMPLETION_BY_TAG[tag] || {}) } : null;
    },

    getCaseWorkflowAutoSoftCompletionRecord(tag = '') {
      const key = normalizeCell(tag).toUpperCase();
      if (!key) return null;
      const workflow = this.caseSupportState?.workflowState || {};
      return workflow.autoStepCompletionByTag?.[key] || workflow.autoStepCompletionsByTag?.[key] || null;
    },

    caseWorkflowCheckResultLooksPassing(result = null) {
      if (!result || typeof result !== 'object') return false;
      const status = normalizeCell(result.status || result.result || result.state || '').toLowerCase();
      if (/fail|error|blocked|attention|stale|unchecked|pending|missing/.test(status)) return false;
      if (/pass|success|complete|current|reviewed|ready/.test(status)) return true;
      return !!(result.checkedAtUtc || result.completedAtUtc || result.reviewedAtUtc || result.appliedAtUtc);
    },

    caseWorkflowSignalRecordLooksComplete(record = null) {
      if (record === true) return true;
      if (typeof record === 'string') return !!normalizeCell(record);
      if (!record || typeof record !== 'object') return false;
      if (record.complete === false || record.success === false) return false;
      return !!(record.completedAtUtc || record.checkedAtUtc || record.appliedAtUtc || record.reviewedAtUtc || record.timestamp || record.atUtc || record.revision || Object.keys(record).length);
    },

    caseWorkflowAutoSoftCompletionEvidence(step = {}) {
      const config = this.caseWorkflowAutoSoftStepConfig(step);
      if (!config?.tag) return { tag: '', hasEvidence: false };
      const workflow = this.caseSupportState?.workflowState || {};
      const signalMap = workflow.signalCompleteByCode || {};
      const checkMap = this.caseSupportState?.checkResultsById || {};
      const signalCodes = Array.from(new Set([config.tag, ...(config.signalCodes || [])].map(code => normalizeCell(code).toUpperCase()).filter(Boolean)));
      const checkIds = Array.from(new Set((config.checkIds || []).map(id => normalizeCell(id).toLowerCase()).filter(Boolean)));
      const signalCode = signalCodes.find(code => this.caseWorkflowSignalRecordLooksComplete(signalMap?.[code]));
      const checkId = checkIds.find(id => this.caseWorkflowCheckResultLooksPassing(checkMap?.[id]));
      const record = this.getCaseWorkflowAutoSoftCompletionRecord(config.tag);
      const recordComplete = this.caseWorkflowSignalRecordLooksComplete(record);
      const recentLog = (Array.isArray(this.caseAutomation?.log) ? this.caseAutomation.log : []).find((entry) => {
        const details = entry?.details || {};
        const entryTag = normalizeCell(details.stepTag || details.tag || '').toUpperCase();
        if (entryTag && entryTag === config.tag) return true;
        const message = normalizeCell(entry?.message).toLowerCase();
        const stepTitle = this.caseWorkflowStepTitleText(step);
        if (stepTitle && message && message.includes(stepTitle.slice(0, Math.min(24, stepTitle.length)))) return true;
        if (config.tag === 'ENTITY_REVIEW' && /rubber[-\s]*stamping\s+entity\s+review|entity\s+review/.test(message)) return true;
        if (config.tag === 'ADDRESS_COLOR_LIKE_CELLS' && /color(?:ing)?\s+matching\s+from\/?to\s+addresses|address\s+color/.test(message)) return true;
        return false;
      });
      return {
        tag: config.tag,
        config,
        record,
        recordComplete,
        signalCode: signalCode || '',
        checkId: checkId || '',
        recentLog: recentLog || null,
        hasEvidence: recordComplete || !!signalCode || !!checkId || !!recentLog
      };
    },

    caseWorkflowAutoSoftCompletionShouldApply(view = {}, definitionOrStep = {}) {
      const evidence = this.caseWorkflowAutoSoftCompletionEvidence(view?.tag || view?.stepTag ? view : definitionOrStep);
      const tag = evidence.tag || this.caseWorkflowAutoSoftStepTag(view) || this.caseWorkflowAutoSoftStepTag(definitionOrStep);
      if (this.caseWorkflowStatefulReviewTagRequiresLiveVerification(tag) && !this.caseWorkflowStatefulGeneratedReviewIsCurrent(tag)) {
        return { apply: false, evidence, reason: 'stateful-review-not-current' };
      }
      if (!evidence.hasEvidence) return { apply: false, evidence };
      const finalRubberStamp = Boolean(evidence.record?.finalRubberStamp && this.caseWorkflowFinalRubberStampConfig(tag));
      if (finalRubberStamp) return { apply: true, evidence, reason: 'final-rubber-stamp-record' };
      if (view?.blocked || view?.attention || /blocked|attention|error|fail/.test(normalizeCell(view?.status || '').toLowerCase())) return { apply: false, evidence };
      if (evidence.recordComplete) return { apply: true, evidence, reason: 'record' };
      const auto = this.caseAutomation || {};
      const skipMode = !!auto.skipResolvableReview;
      const autoActive = !!(auto.active || auto.busy);
      const currentlyStaleOnly = view?.stale === true && !view?.blocked;
      if (skipMode || autoActive || currentlyStaleOnly || view?.complete === true) return { apply: true, evidence, reason: evidence.signalCode ? 'signal' : evidence.checkId ? 'check' : 'recent-log' };
      return { apply: false, evidence };
    },

    applyCaseWorkflowAutoSoftCompletionState(view = {}, definition = {}) {
      if (this.isCaseWorkflowWwwUsdReferenceStep(view) || this.isCaseWorkflowWwwUsdReferenceStep(definition)) {
        return this.applyCaseWorkflowWwwUsdReferenceCurrentState(view, definition);
      }
      const check = this.caseWorkflowAutoSoftCompletionShouldApply(view, definition);
      if (!check.apply) return view;
      const evidence = check.evidence || {};
      const tag = evidence.tag || this.caseWorkflowAutoSoftStepTag(view) || this.caseWorkflowAutoSoftStepTag(definition);
      if (!tag) return view;
      const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      const labelBase = view?.statusLabel && !/stale/i.test(view.statusLabel) ? view.statusLabel : 'Complete';
      return {
        ...view,
        complete: true,
        stale: false,
        blocked: false,
        status: 'complete',
        statusLabel: /^complete/i.test(labelBase) ? labelBase : `Complete · ${labelBase}`,
        statusClass: 'workflow-step-card--complete',
        staleReason: '',
        sources: {
          ...(view.sources || {}),
          signal: !!(view.sources?.signal || evidence.signalCode),
          check: !!(view.sources?.check || evidence.checkId),
          manual: !!view.sources?.manual,
          autoSoftCompletion: true
        },
        checkResult: {
          ...(view.checkResult || {}),
          status: 'pass',
          revision: Number(this.caseSupportState?.caseDataRevision) || 0,
          checkedAtUtc: view.checkResult?.checkedAtUtc || evidence.record?.checkedAtUtc || evidence.record?.completedAtUtc || now,
          message: view.checkResult?.message || `${view.title || definition?.title || tag} is complete for workflow Auto.`,
          details: {
            ...((view.checkResult || {}).details || {}),
            autoSoftCompletion: true,
            tag,
            reason: check.reason || '',
            signalCode: evidence.signalCode || '',
            checkId: evidence.checkId || ''
          }
        },
        completionSummary: view?.completionSummary || 'Completed by workflow Auto / skip-review state.',
        autoSoftCompletion: true
      };
    },

    recordCaseWorkflowAutoSoftStepComplete(step = {}, action = {}, context = {}) {
      const config = this.caseWorkflowAutoSoftStepConfig(step) || this.caseWorkflowAutoSoftStepConfig(action) || (context.tag ? this.caseWorkflowAutoSoftStepConfig({ tag: context.tag }) : null);
      if (!config?.tag) return null;
      if (this.caseWorkflowStatefulReviewTagRequiresLiveVerification(config.tag) && context.stateVerified !== true) {
        return null;
      }
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) },
        autoStepCompletionByTag: { ...((this.caseSupportState.workflowState || {}).autoStepCompletionByTag || {}) }
      };
      const signalCodes = Array.from(new Set([config.tag, ...(config.signalCodes || [])].map(code => normalizeCell(code).toUpperCase()).filter(Boolean)));
      const checkIds = Array.from(new Set((config.checkIds || []).map(id => normalizeCell(id).toLowerCase()).filter(Boolean)));
      const record = {
        ...(workflow.autoStepCompletionByTag[config.tag] || {}),
        tag: config.tag,
        completedAtUtc: now,
        checkedAtUtc: now,
        revision: currentRevision,
        source: context.source || 'workflow-auto-soft-completion',
        actionId: normalizeCell(action?.id || action?.key || action?.action || context.actionId || ''),
        method: normalizeCell(step?.method || action?.method || context.method || ''),
        stepTitle: normalizeCell(step?.title || step?.stepTitle || context.stepTitle || '')
      };
      workflow.autoStepCompletionByTag[config.tag] = record;
      signalCodes.forEach((code) => {
        workflow.signalCompleteByCode[code] = {
          ...(workflow.signalCompleteByCode[code] || {}),
          completedAtUtc: workflow.signalCompleteByCode[code]?.completedAtUtc || now,
          checkedAtUtc: now,
          revision: currentRevision,
          source: context.source || 'workflow-auto-soft-completion',
          stepTag: config.tag
        };
      });
      this.caseSupportState.workflowState = workflow;
      const checkResults = { ...(this.caseSupportState.checkResultsById || {}) };
      checkIds.forEach((id) => {
        checkResults[id] = {
          ...(checkResults[id] || {}),
          id,
          status: 'pass',
          message: `${normalizeCell(step?.title || step?.stepTitle || config.tag) || config.tag} completed by workflow Auto.`,
          revision: currentRevision,
          checkedAtUtc: now,
          details: {
            ...((checkResults[id] || {}).details || {}),
            autoSoftCompletion: true,
            tag: config.tag,
            source: context.source || 'workflow-auto-soft-completion'
          }
        };
      });
      this.caseSupportState.checkResultsById = checkResults;
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      return record;
    },

    async runCaseWorkflowAutoSoftOriginalMethod(methodName = '', fallbackStep = {}, args = []) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyAutomationMethods, methodName, Array.isArray(args) ? args : []);
      const resolved = result && typeof result.then === 'function' ? await result : result;
      await this.caseWorkflowUiNextTick?.();
      this.recordCaseWorkflowAutoSoftStepComplete(fallbackStep || {}, { method: methodName }, {
        source: `workflow-auto-soft-${methodName || 'method'}`,
        method: methodName
      });
      return resolved;
    },

    async automationRubberStampEntityReview(...args) {
      return this.runCaseWorkflowAutoSoftOriginalMethod('automationRubberStampEntityReview', {
        tag: 'ENTITY_REVIEW',
        title: 'Review new entities and update curators'
      }, args);
    },

    caseWorkflowWwwUsdReferenceDefaultStep() {
      return {
        id: 'www-usd-references',
        tag: 'WWW_USD_REFERENCES',
        title: 'Review top-level USD reference values',
        description: 'Fill Column H on the first/root row of each main transaction before cleanup. Use real day-of USD values where possible; the built-in estimator is only a rough draft.',
        actionIds: ['review_www_usd_references', 'stage_www_usd_reference_audit', 'open_main_sheet'],
        actions: [
          { id: 'review_www_usd_references', label: 'Review USD values', method: 'openWwwUsdReferenceReviewModal', primary: true },
          { id: 'stage_www_usd_reference_audit', label: 'Stage audit', method: 'stageWwwUsdReferenceReviewAudit' },
          { id: 'open_main_sheet', label: 'Open Main Data', method: 'activateLogicalSheet', args: ['main'] }
        ]
      };
    },

    isCaseWorkflowWwwUsdReferenceStep(step = {}) {
      if (!step || typeof step !== 'object') return false;
      const tag = this.caseWorkflowStepIdentity(step);
      const title = this.caseWorkflowStepTitleText(step);
      if (tag === 'WHAT_WENT_WHERE' || tag === 'BUILD_AND_REVIEW_WHAT_WENT_WHERE') return false;
      if (tag === 'WWW_USD_REFERENCES' || tag === 'FILL_MAIN_TRANSACTION_USDT_REFERENCE_VALUES' || tag === 'WHAT_WENT_WHERE_USD_REFERENCES') return true;
      if (/what\s+went\s+where/.test(title)) return false;
      const text = [
        tag,
        title,
        step?.description,
        step?.id,
        step?.key,
        step?.action,
        step?.method,
        ...(Array.isArray(step?.actionIds) ? step.actionIds : []),
        ...(Array.isArray(step?.actions) ? step.actions.map(action => [action?.id, action?.key, action?.label, action?.method].join(' ')) : [])
      ].map(value => normalizeCell(value).toLowerCase()).join(' ');
      if (/review[_\s-]*www[_\s-]*usd[_\s-]*references|www[_\s-]*usd[_\s-]*references|top[-\s]*level\s+usd\s+reference/.test(text)) return true;
      return /column\s+h/.test(text) && /usd|usdt|valuation|reference/.test(text) && !/what\s+went\s+where/.test(title);
    },

    isCaseWorkflowExternalAttributionReviewStep(step = {}) {
      const tag = this.caseWorkflowStepIdentity(step);
      const title = this.caseWorkflowStepTitleText(step);
      return tag === 'EXTERNAL_ATTRIBUTION_REVIEW' || /external\s+attribution/.test(title);
    },

    caseWorkflowGroupsWithEarlyWwwUsdReferences() {
      const sourceGroups = Array.isArray(CASE_WORKFLOW_GROUPS) ? CASE_WORKFLOW_GROUPS : [];
      const extracted = [];
      const groups = sourceGroups.map((group) => {
        const steps = (Array.isArray(group?.steps) ? group.steps : []).filter((step) => {
          if (this.isCaseWorkflowWwwUsdReferenceStep(step)) {
            extracted.push(step);
            return false;
          }
          return true;
        });
        return { ...group, steps };
      });
      const rawSteps = extracted.length ? extracted : [this.caseWorkflowWwwUsdReferenceDefaultStep()];
      const seen = new Set();
      const stepsToInsert = rawSteps.map(step => ({ ...this.caseWorkflowWwwUsdReferenceDefaultStep(), ...(step || {}), tag: 'WWW_USD_REFERENCES' }))
        .filter((step) => {
          const key = this.caseWorkflowStepIdentity(step) || this.caseWorkflowStepTitleText(step) || 'WWW_USD_REFERENCES';
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      if (!stepsToInsert.length) return groups;

      let targetIndex = groups.findIndex((group) => {
        const id = normalizeCell(group?.id || '').toLowerCase();
        const title = normalizeCell(group?.title || '').toLowerCase();
        return id === 'attribution-decisions' || /attribution.*pruning.*grafting.*bridges/.test(title);
      });
      if (targetIndex < 0) {
        targetIndex = groups.findIndex(group => (Array.isArray(group?.steps) ? group.steps : []).some(step => this.isCaseWorkflowExternalAttributionReviewStep(step)));
      }
      if (targetIndex < 0) targetIndex = Math.max(0, groups.length - 1);

      const target = groups[targetIndex] || { id: 'attribution-decisions', title: 'Attribution, Pruning, Grafting, And Bridges', steps: [] };
      const targetSteps = Array.isArray(target.steps) ? target.steps.slice() : [];
      let insertIndex = targetSteps.findIndex(step => this.isCaseWorkflowExternalAttributionReviewStep(step));
      insertIndex = insertIndex >= 0 ? insertIndex + 1 : targetSteps.length;
      groups[targetIndex] = {
        ...target,
        steps: [
          ...targetSteps.slice(0, insertIndex),
          ...stepsToInsert,
          ...targetSteps.slice(insertIndex)
        ]
      };
      return groups;
    },

    caseWorkflowOrderedStepDefinitions() {
      const ordered = [];
      this.caseWorkflowGroupsWithEarlyWwwUsdReferences().forEach((group) => {
        (Array.isArray(group?.steps) ? group.steps : []).forEach((step) => ordered.push(step));
      });
      return ordered;
    },

    caseWorkflowColumnHReferenceStepBoundaryIndex() {
      const ordered = this.caseWorkflowOrderedStepDefinitions();
      const index = ordered.findIndex(step => this.isCaseWorkflowWwwUsdReferenceStep(step));
      return index >= 0 ? index : this.caseWorkflowWhatWentWhereBoundaryIndex();
    },

    caseWorkflowWhatWentWhereBoundaryIndex() {
      const ordered = this.caseWorkflowOrderedStepDefinitions();
      const knownTags = new Set([
        'BUILD_AND_REVIEW_WHAT_WENT_WHERE',
        'WHAT_WENT_WHERE'
      ]);
      const index = ordered.findIndex((step) => {
        const tag = this.caseWorkflowStepIdentity(step);
        const title = this.caseWorkflowStepTitleText(step);
        return knownTags.has(tag) || /what\s+went\s+where/.test(title);
      });
      return index >= 0 ? index : Number.POSITIVE_INFINITY;
    },

    caseWorkflowStepOrderIndex(step = {}) {
      const tag = this.caseWorkflowStepIdentity(step);
      const title = this.caseWorkflowStepTitleText(step);
      const ordered = this.caseWorkflowOrderedStepDefinitions();
      return ordered.findIndex((candidate) => {
        const candidateTag = this.caseWorkflowStepIdentity(candidate);
        if (tag && candidateTag && candidateTag === tag) return true;
        return title && this.caseWorkflowStepTitleText(candidate) === title;
      });
    },

    getCaseWorkflowColumnHReferencePreservationRecord() {
      const record = this.caseSupportState?.workflowState?.columnHUsdReferenceAutoApply || null;
      if (!record || typeof record !== 'object') return null;
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const recordRevision = Number(record.afterRevision) || 0;
      if (!recordRevision || recordRevision !== currentRevision) return null;
      return record;
    },

    captureCaseWorkflowColumnHReferencePreservedStepTags(options = {}) {
      try {
        const explicitBoundary = Number(options?.boundaryIndex);
        const boundaryIndex = Number.isFinite(explicitBoundary) ? explicitBoundary : this.caseWorkflowWhatWentWhereBoundaryIndex();
        return (Array.isArray(this.workflowGroupsView) ? this.workflowGroupsView : [])
          .flatMap(group => Array.isArray(group?.steps) ? group.steps : [])
          .filter((step) => {
            const index = this.caseWorkflowStepOrderIndex(step);
            return index >= 0 && index < boundaryIndex && step?.complete === true;
          })
          .map(step => this.caseWorkflowStepIdentity(step))
          .filter(Boolean);
      } catch (error) {
        console.warn('Case Buddy could not capture workflow steps before Column H auto-fill.', error);
        return [];
      }
    },

    applyCaseWorkflowColumnHReferencePreservedStep(view = {}, definition = {}) {
      if (this.isCaseWorkflowWwwUsdReferenceStep(view) || this.isCaseWorkflowWwwUsdReferenceStep(definition)) {
        return this.applyCaseWorkflowWwwUsdReferenceCurrentState(view);
      }
      const record = this.getCaseWorkflowColumnHReferencePreservationRecord();
      if (!record) return view;
      const tag = this.caseWorkflowStepIdentity(view) || this.caseWorkflowStepIdentity(definition);
      if (!tag || !Array.isArray(record.preservedStepTags) || !record.preservedStepTags.includes(tag)) return view;
      const index = this.caseWorkflowStepOrderIndex(view) >= 0 ? this.caseWorkflowStepOrderIndex(view) : this.caseWorkflowStepOrderIndex(definition);
      if (index < 0 || index >= this.caseWorkflowWhatWentWhereBoundaryIndex()) return view;
      if (view?.blocked || view?.attention || /attention|blocked|error|fail/.test(normalizeCell(view?.status || '').toLowerCase())) return view;
      if (view?.complete === true && view?.stale !== true) return view;
      const statusLabel = normalizeCell(view?.statusLabel || '').replace(/checks?\s+stale|stale/ig, '').trim();
      return {
        ...view,
        complete: true,
        stale: false,
        blocked: false,
        status: 'complete',
        statusLabel: statusLabel && !/complete/i.test(statusLabel) ? `Complete · ${statusLabel}` : (statusLabel || 'Complete'),
        statusClass: 'workflow-step-card--complete',
        staleReason: '',
        columnHReferencePreserved: true,
        completionSummary: view?.completionSummary || 'Preserved as complete because workflow Auto only filled What Went Where Column H USD references.',
        sources: {
          ...(view?.sources || {}),
          signal: !!(view?.sources?.signal || view?.sources?.manual || view?.sources?.check),
          columnHReferencePreserved: true
        }
      };
    },

    recordCaseWorkflowColumnHReferenceAutoApply(context = {}) {
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const now = this.emptyCaseStarterNudgeNowUtc ? this.emptyCaseStarterNudgeNowUtc() : new Date().toISOString();
      const beforeRevision = Number(context.beforeRevision) || 0;
      const afterRevision = Number(context.afterRevision) || Number(this.caseSupportState.caseDataRevision) || 0;
      const preservedStepTags = Array.from(new Set((Array.isArray(context.preservedStepTags) ? context.preservedStepTags : []).map(tag => normalizeCell(tag).toUpperCase()).filter(Boolean)));
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) }
      };
      workflow.columnHUsdReferenceAutoApply = {
        beforeRevision,
        afterRevision,
        appliedAtUtc: now,
        appliedCount: Number(context.appliedCount || 0),
        source: context.source || 'workflow-auto-what-went-where',
        preservedStepTags
      };
      try {
        const mainRows = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('main') : [];
        const reviewState = buildCaseWwwUsdReferenceReviewState(Array.isArray(mainRows) ? mainRows : [], workflow, {
          caseDataRevision: afterRevision,
          caseId: this.caseId || this.caseDetails?.caseId || this.currentProjectFileId || ''
        });
        if (reviewState?.canReview) {
          workflow.whatWentWhereUsdReferenceReview = {
            ...(workflow.whatWentWhereUsdReferenceReview || {}),
            reviewedAtUtc: now,
            basisHash: reviewState.currentBasisHash,
            revision: afterRevision,
            source: context.source || 'workflow-auto-what-went-where',
            autoApplied: true,
            appliedCount: Number(context.appliedCount || 0)
          };
          ['WHAT_WENT_WHERE_REVIEW_UPDATED', 'WWW_USD_REFERENCES_REVIEWED', 'WWW_USD_REFERENCES', 'WHAT_WENT_WHERE_USD_REFERENCES_REVIEWED'].forEach((code) => {
            workflow.signalCompleteByCode[code] = {
              ...(workflow.signalCompleteByCode[code] || {}),
              completedAtUtc: now,
              appliedAtUtc: now,
              source: context.source || 'workflow-auto-www-usd-references',
              stepTag: 'WWW_USD_REFERENCES',
              columnHReferencesApplied: Number(context.appliedCount || 0)
            };
          });
          this.caseSupportState.checkResultsById = {
            ...(this.caseSupportState.checkResultsById || {}),
            www_usd_references: {
              id: 'www_usd_references',
              status: 'pass',
              message: Number(context.appliedCount || 0) > 0
                ? `Column H USD references reviewed after applying ${Number(context.appliedCount || 0)} rough estimate${Number(context.appliedCount || 0) === 1 ? '' : 's'}.`
                : 'Column H USD references reviewed.',
              revision: afterRevision,
              checkedAtUtc: now,
              details: {
                stepTag: 'WWW_USD_REFERENCES',
                source: context.source || 'workflow-auto-www-usd-references',
                appliedCount: Number(context.appliedCount || 0),
                roughEstimateWarning: 'Built-in values are rough helpers and should be replaced with real day-specific USD valuations when available.'
              }
            }
          };
        }
      } catch (error) {
        console.warn('Case Buddy could not record What Went Where USD reference review state after auto-fill.', error);
      }
      this.caseSupportState.workflowState = workflow;
      if (this.caseSupportState.checkResultsById && preservedStepTags.length) {
        const preserved = new Set(preservedStepTags);
        this.caseSupportState.checkResultsById = Object.fromEntries(Object.entries(this.caseSupportState.checkResultsById).map(([key, result]) => {
          const detailsTag = this.caseWorkflowStepIdentity(result?.details || {});
          const resultTag = this.caseWorkflowStepIdentity(result || {});
          if (!preserved.has(detailsTag) && !preserved.has(resultTag)) return [key, result];
          return [key, {
            ...(result || {}),
            revision: afterRevision,
            checkedAtUtc: result?.checkedAtUtc || now,
            details: {
              ...(result?.details || {}),
              columnHReferenceAutoPreservedAtUtc: now,
              columnHReferenceAutoPreservedFromRevision: beforeRevision
            }
          }];
        }));
      }
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
    },

    emptyCaseStarterNudgeNowUtc() {
      return new Date().toISOString();
    },

    activeCaseLoadedForEmptyCaseStarterNudge() {
      return !!(this.currentProjectFileId && this.projectFileReadyState?.input && this.projectFileReadyState?.output && !this.casePageHydrating);
    },

    emptyCaseStarterNudgeInFullCaseView() {
      return !(this.caseQuickLookupVisible || this.caseSimpleLaunchVisible || this.caseSimpleLaunchMiniVisible || this.caseSimpleDetailsVisible);
    },

    emptyCaseStarterNudgeDismissedForCurrentCase() {
      const fileId = this.currentProjectFileId || 'default';
      const state = this.caseSupportState?.emptyCaseStarterNudge || {};
      return !!(state.dismissedForCase || this.emptyCaseStarterNudgeSessionDismissedByFileId?.[fileId]);
    },

    normalizeEmptyCaseStarterSheetName(value = '') {
      return normalizeCell(value).toLowerCase().replace(/[\s_/-]+/g, '');
    },

    isActiveInputSheetMainLikeForEmptyCaseStarterNudge() {
      const workbook = this.$refs.inputSpreadsheet;
      const activeName = normalizeCell(workbook?.activeSheetName || workbook?.getActiveSheet?.()?.name || '');
      if (!activeName) return true;
      if (typeof this.isLogicalSheetName === 'function' && this.isLogicalSheetName(activeName, 'main')) return true;
      return ['maindata', 'main', 'inputdata', 'input', 'caseinput', 'sheet1'].includes(this.normalizeEmptyCaseStarterSheetName(activeName));
    },

    emptyCaseStarterGetMatrix(role = 'main') {
      try {
        if (typeof this.getMatrixForLogicalSheet === 'function') {
          const matrix = this.getMatrixForLogicalSheet(role);
          if (Array.isArray(matrix)) return matrix;
        }
      } catch {
        // This nudge must stay cheap and resilient. Treat read failures as no rows.
      }
      return [];
    },

    emptyCaseStarterRowHasMeaningfulData(row = []) {
      if (typeof rowHasMeaningfulData === 'function') return rowHasMeaningfulData(row);
      return Array.isArray(row) && row.some(value => normalizeCell(value));
    },

    hasMainInputTransactionEntries(matrix = this.emptyCaseStarterGetMatrix('main')) {
      return (Array.isArray(matrix) ? matrix : []).slice(1).some(row => normalizeCell(Array.isArray(row) ? row[1] : ''));
    },

    hasMeaningfulMainInputRowsForEmptyCaseStarter(matrix = this.emptyCaseStarterGetMatrix('main')) {
      return (Array.isArray(matrix) ? matrix : []).slice(1).some(row => this.emptyCaseStarterRowHasMeaningfulData(row));
    },

    hasMeaningfulRawRowsForEmptyCaseStarter() {
      const matrix = this.emptyCaseStarterGetMatrix('raw');
      return (Array.isArray(matrix) ? matrix : []).slice(1).some(row => this.emptyCaseStarterRowHasMeaningfulData(row));
    },

    hasMeaningfulOutputRowsForEmptyCaseStarter() {
      try {
        const rows = this.$refs.outputSpreadsheet?.getData?.() || this.$refs.outputSpreadsheet?.hotInstance?.getData?.() || [];
        return (Array.isArray(rows) ? rows : []).some(row => this.emptyCaseStarterRowHasMeaningfulData(row));
      } catch {
        return Number(this.caseSummary?.outputRows) > 0;
      }
    },

    workflowStateHasMeaningfulProgressForEmptyCaseStarter() {
      const workflow = this.caseSupportState?.workflowState || {};
      const recordsHaveCompletion = value => value && typeof value === 'object' && Object.values(value).some(record => {
        if (!record || typeof record !== 'object') return false;
        return !!(record.completedAtUtc || record.markedAtUtc || record.checkedAtUtc || record.reviewedAtUtc || record.importedAtUtc || record.appliedAtUtc);
      });
      if (recordsHaveCompletion(workflow.manualCompleteByTag)) return true;
      if (recordsHaveCompletion(workflow.signalCompleteByCode)) return true;
      if (Array.isArray(workflow.toolRuns) && workflow.toolRuns.length > 0) return true;
      const reviewKeys = ['addressColorRun', 'entityReview', 'inconsistencyReview', 'depositHotWalletReview', 'depositPatternReview', 'knownEntityCasingReview', 'duplicateHashLabelerReview', 'noteHashPathLabelRewriteReview', 'whatWentWhereUsdReferenceReview', 'addressLegendReview', 'suspiciousAddressTableReview', 'exchangeAddressTableReview', 'dormantAddressDeliverablesReview', 'whatWentWhereReview', 'caseConclusionReview', 'reportBodyReview', 'addendumPathEndingReview', 'addendumRecipientEntityReview', 'addendumBuildReview', 'fullReportPackageReview', 'casePrepBuddyHandoff'];
      return reviewKeys.some((key) => {
        const value = workflow[key];
        if (!value || typeof value !== 'object') return false;
        if (key === 'addressLegendReview') return Array.isArray(value.entries) && value.entries.length > 0;
        return !!(value.completedAtUtc || value.reviewedAtUtc || value.generatedAtUtc || value.importedAtUtc || value.appliedAtUtc || value.status === 'complete');
      });
    },

    hasCaseInvestigationStartedForEmptyCaseStarterNudge() {
      if (this.hasMeaningfulMainInputRowsForEmptyCaseStarter()) return true;
      if (this.hasMeaningfulRawRowsForEmptyCaseStarter()) return true;
      if (this.hasMeaningfulOutputRowsForEmptyCaseStarter()) return true;
      if (this.workflowStateHasMeaningfulProgressForEmptyCaseStarter()) return true;
      if (Object.keys(this.caseSupportState?.checkResultsById || {}).length > 0) return true;
      if (this.caseAutomation?.active || this.caseAutomation?.busy || (Array.isArray(this.caseAutomation?.log) && this.caseAutomation.log.length > 0)) return true;
      if (this.caseAutomationTool88Activity?.active || this.caseAutomationTool88Activity?.startedAtUtc || this.caseAutomationTool88Activity?.completedAtUtc) return true;
      return false;
    },

    ensureEmptyCaseStarterNudgeState() {
      const current = this.caseSupportState?.emptyCaseStarterNudge && typeof this.caseSupportState.emptyCaseStarterNudge === 'object' ? this.caseSupportState.emptyCaseStarterNudge : {};
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      this.caseSupportState.emptyCaseStarterNudge = {
        schemaVersion: 1,
        dismissedForCase: !!current.dismissedForCase,
        dismissedAtUtc: normalizeCell(current.dismissedAtUtc),
        dismissedReason: normalizeCell(current.dismissedReason)
      };
      return this.caseSupportState.emptyCaseStarterNudge;
    },

    persistEmptyCaseStarterNudgeSupportState() {
      if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      else if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
    },

    dismissEmptyCaseStarterNudge(reason = 'dismissed') {
      const state = this.ensureEmptyCaseStarterNudgeState();
      state.dismissedForCase = true;
      state.dismissedAtUtc = this.emptyCaseStarterNudgeNowUtc();
      state.dismissedReason = normalizeCell(reason) || 'dismissed';
      this.persistEmptyCaseStarterNudgeSupportState();
      if (this.emptyCaseStarterNudgeDontShowAgain) this.updateEmptyCaseStarterNudgeUserPreference();
    },

    markEmptyCaseStarterNudgeSessionDismissed(reason = 'case-data-started') {
      const fileId = this.currentProjectFileId || 'default';
      this.emptyCaseStarterNudgeSessionDismissedByFileId = {
        ...(this.emptyCaseStarterNudgeSessionDismissedByFileId || {}),
        [fileId]: normalizeCell(reason) || true
      };
    },

    markEmptyCaseStarterNudgeInvestigationStarted(reason = 'case-data-started') {
      this.markEmptyCaseStarterNudgeSessionDismissed(reason);
    },

    updateEmptyCaseStarterNudgeUserPreference() {
      const disabled = !!this.emptyCaseStarterNudgeDontShowAgain;
      this.caseBuddyUserPreferences = {
        ...(this.caseBuddyUserPreferences || {}),
        emptyCaseStarterNudgeDisabled: disabled
      };
      try {
        if (disabled) localStorage.setItem(CASE_EMPTY_STARTER_NUDGE_DISABLED_STORAGE_KEY, 'true');
        else localStorage.removeItem(CASE_EMPTY_STARTER_NUDGE_DISABLED_STORAGE_KEY);
      } catch {
        // User-level preference storage is best effort.
      }
    },

    startManuallyFromEmptyCaseStarterNudge() {
      if (this.emptyCaseStarterNudgeDontShowAgain) this.updateEmptyCaseStarterNudgeUserPreference();
      this.dismissEmptyCaseStarterNudge('start-manually');
      this.$nextTick(() => this.$refs.inputSpreadsheet?.refreshDimensions?.());
    },

    openStartingMainTransactionsFromEmptyCaseNudge() {
      this.emptyCaseStarterNudgeLaunchPending = true;
      this.openStartingMainTransactionsModal();
    },

    openStartingMainTransactionsModal(...args) {
      return this.callCaseBuddyOriginalMethod(caseBuddyBaseMethods, 'openStartingMainTransactionsModal', args);
    },

    normalizeStartingMainTransactionHash(value = '') {
      return normalizeCell(value).replace(/^\uFEFF/u, '').replace(/^['"`]+|['"`]+$/g, '').trim();
    },

    normalizeStartingMainTransactionAddress(value = '') {
      return normalizeCell(value).replace(/^\uFEFF/u, '').replace(/^['"`]+|['"`]+$/g, '').trim();
    },

    startingMainTransactionNormalizeHeaderKey(value = '') {
      return normalizeCell(value).toLowerCase().replace(/[\s_./:-]+/g, '');
    },

    startingMainTransactionHeaderKind(value = '') {
      const key = this.startingMainTransactionNormalizeHeaderKey(value);
      if (['transactionhash', 'transactionid', 'txhash', 'txid', 'hash'].includes(key)) return 'hash';
      if (['toaddress', 'destinationaddress', 'recipientaddress', 'receivingaddress', 'to', 'destination', 'recipient'].includes(key)) return 'toAddress';
      return '';
    },

    startingMainTransactionCellLooksLikeHeader(value = '') {
      return Boolean(this.startingMainTransactionHeaderKind(value));
    },

    startingMainTransactionLooksLikeStrictHash(value = '') {
      const text = this.normalizeStartingMainTransactionHash(value);
      if (!text || /\s/u.test(text)) return false;
      if (typeof this.caseManualIsLikelyTransactionHash === 'function' && this.caseManualIsLikelyTransactionHash(text)) return true;
      if (/^0x[0-9a-fA-F]{64}$/u.test(text) || /^[0-9a-fA-F]{64}$/u.test(text)) return true;
      if (/^(?:0x)?[0-9a-fA-F]{5,}\.\.\.[0-9a-fA-F]{4,}$/u.test(text)) return true;
      // Solana-style signatures are long base58 values rather than 64-character hex hashes.
      return /^[1-9A-HJ-NP-Za-km-z]{80,100}$/u.test(text);
    },

    startingMainTransactionAddressLooksUsable(value = '', options = {}) {
      const text = this.normalizeStartingMainTransactionAddress(value);
      if (!text || this.startingMainTransactionCellLooksLikeHeader(text)) return false;
      if (/\s/u.test(text) || this.startingMainTransactionLooksLikeStrictHash(text)) return false;
      const strictMatch = Boolean(
        /^0x[0-9a-fA-F]{40}$/u.test(text) ||
        /^(?:bc1|tb1|bcrt1|ltc1)[0-9a-z]{8,90}$/iu.test(text) ||
        /^(?:bitcoincash:)?[qp][0-9a-z]{30,110}$/iu.test(text) ||
        /^[13LMDA9][1-9A-HJ-NP-Za-km-z]{24,61}$/u.test(text) ||
        /^T[1-9A-HJ-NP-Za-km-z]{33}$/u.test(text) ||
        /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/u.test(text) ||
        /^[1-9A-HJ-NP-Za-km-z]{32,44}$/u.test(text) ||
        /^[a-z0-9][a-z0-9._-]{1,80}\.(?:eth|sol|crypto|wallet)$/iu.test(text) ||
        /^(?:0x)?[0-9a-zA-Z]{4,}\.\.\.[0-9a-zA-Z]{4,}$/u.test(text)
      );
      if (strictMatch) return true;
      if (options.explicitColumn !== true) return false;
      if (/^[+-]?(?:\d+|\d*\.\d+)$/u.test(text)) return false;
      if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/u.test(text)) return false;
      return text.length >= 4;
    },

    startingMainTransactionHashLooksUsable(value = '') {
      const text = this.normalizeStartingMainTransactionHash(value);
      if (!text || this.startingMainTransactionCellLooksLikeHeader(text)) return false;
      if (/\s/u.test(text)) return false;
      if (this.startingMainTransactionLooksLikeStrictHash(text)) return true;
      if (this.startingMainTransactionAddressLooksUsable(text)) return false;
      if (/^[+-]?(?:\d+|\d*\.\d+)$/u.test(text)) return false;
      if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/u.test(text)) return false;
      return text.length >= 6;
    },

    startingMainTransactionHashCellScore(value = '', columnIndex = -1, context = {}) {
      const text = this.normalizeStartingMainTransactionHash(value);
      if (!this.startingMainTransactionHashLooksUsable(text)) return -1;
      let score = this.startingMainTransactionLooksLikeStrictHash(text) ? 100 : 20;
      if (Number(context.hashColumn) === columnIndex) score += 1000;
      if (columnIndex === 1) score += 35;
      if (columnIndex === 0) score += 20;
      if (context.nonEmptyCellCount === 1) score += 50;
      if (text.length >= 60) score += 15;
      return score;
    },

    inferStartingMainTransactionPasteLayout(lines = []) {
      const normalizedLines = Array.isArray(lines) ? lines : [];
      for (let lineIndex = 0; lineIndex < normalizedLines.length; lineIndex += 1) {
        const rawLine = String(normalizedLines[lineIndex] || '');
        if (!rawLine.trim()) continue;
        const cells = rawLine.split('\t').map(cell => normalizeCell(cell));
        let hashColumn = -1;
        let toAddressColumn = -1;
        cells.forEach((cell, columnIndex) => {
          const kind = this.startingMainTransactionHeaderKind(cell);
          if (kind === 'hash' && hashColumn < 0) hashColumn = columnIndex;
          if (kind === 'toAddress' && toAddressColumn < 0) toAddressColumn = columnIndex;
        });
        if (hashColumn >= 0) {
          return { headerLineIndex: lineIndex, hashColumn, toAddressColumn };
        }
        break;
      }
      return { headerLineIndex: -1, hashColumn: -1, toAddressColumn: -1 };
    },

    buildStartingMainTransactionDraftId(hash = '', toAddress = '', index = 0) {
      const key = `${this.normalizeStartingMainTransactionHash(hash).toLowerCase()}|${this.normalizeStartingMainTransactionAddress(toAddress).toLowerCase()}|${index}`;
      let out = 0;
      for (let i = 0; i < key.length; i += 1) out = ((out << 5) - out + key.charCodeAt(i)) | 0;
      return `starting-main-draft-${index}-${Math.abs(out).toString(36)}`;
    },

    parseStartingMainTransactionDraftLine(line = '', index = 0, layout = {}) {
      if (Number(layout.headerLineIndex) === index) return null;
      const cells = String(line || '').replace(/\r$/u, '').split('\t').map(cell => normalizeCell(cell));
      const nonEmptyCellCount = cells.filter(cell => normalizeCell(cell)).length;
      const hashCandidates = cells
        .map((cell, columnIndex) => ({
          columnIndex,
          score: this.startingMainTransactionHashCellScore(cell, columnIndex, {
            ...layout,
            nonEmptyCellCount
          })
        }))
        .filter(candidate => candidate.score >= 0)
        .sort((left, right) => right.score - left.score || left.columnIndex - right.columnIndex);
      const hashColumn = hashCandidates.length ? hashCandidates[0].columnIndex : -1;
      if (hashColumn < 0) return null;

      let toAddressColumn = -1;
      const useAddressColumn = (columnIndex, explicitColumn = false) => {
        if (!Number.isInteger(columnIndex) || columnIndex < 0 || columnIndex >= cells.length || columnIndex === hashColumn) return false;
        if (!this.startingMainTransactionAddressLooksUsable(cells[columnIndex], { explicitColumn })) return false;
        toAddressColumn = columnIndex;
        return true;
      };

      useAddressColumn(Number(layout.toAddressColumn), true);
      if (toAddressColumn < 0 && hashColumn === 1) useAddressColumn(3, true);
      if (toAddressColumn < 0 && nonEmptyCellCount === 2) {
        cells
          .map((cell, columnIndex) => ({ cell, columnIndex }))
          .filter(item => item.columnIndex !== hashColumn && normalizeCell(item.cell))
          .some(item => useAddressColumn(item.columnIndex, true));
      }
      if (toAddressColumn < 0) {
        cells.some((cell, columnIndex) => useAddressColumn(columnIndex, false));
      }

      const hash = this.normalizeStartingMainTransactionHash(cells[hashColumn]);
      const toAddress = toAddressColumn >= 0
        ? this.normalizeStartingMainTransactionAddress(cells[toAddressColumn])
        : '';
      if (!hash) return null;
      return {
        id: this.buildStartingMainTransactionDraftId(hash, toAddress, index),
        hash,
        toAddress,
        status: 'ready',
        statusLabel: 'Ready'
      };
    },

    parseStartingMainTransactionPaste() {
      const text = String(this.startingMainTransactionsPaste || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      const lines = text.split('\n');
      const layout = this.inferStartingMainTransactionPasteLayout(lines);
      const rows = lines
        // Preserve leading tabs. They carry the original spreadsheet column positions.
        .map((line, index) => ({ line, index }))
        .filter(item => item.line.trim().length > 0)
        .map(item => this.parseStartingMainTransactionDraftLine(item.line, item.index, layout))
        .filter(Boolean);
      this.startingMainTransactionDraftRows = rows;
      this.refreshStartingMainTransactionStagedStatuses();
      return rows;
    },

    clearStartingMainTransactionsDraft() {
      this.startingMainTransactionsPaste = '';
      this.startingMainTransactionDraftRows = [];
      this.rawDataImportMissingAfterRows = [];
    },

    existingStartingMainTransactionKeySet() {
      const matrix = normalizeCaseMatrix(this.emptyCaseStarterGetMatrix('main') || []);
      const exact = new Set();
      const byHash = new Set();
      matrix.slice(1).forEach((row) => {
        const hash = this.normalizeStartingMainTransactionHash(row?.[1]).toLowerCase();
        if (!hash) return;
        const toAddress = this.normalizeStartingMainTransactionAddress(row?.[3]).toLowerCase();
        byHash.add(hash);
        exact.add(`${hash}|${toAddress}`);
      });
      return { exact, byHash };
    },

    refreshStartingMainTransactionStagedStatuses() {
      const existing = this.existingStartingMainTransactionKeySet();
      const seen = new Set();
      this.startingMainTransactionDraftRows = (Array.isArray(this.startingMainTransactionDraftRows) ? this.startingMainTransactionDraftRows : [])
        .map((row, index) => {
          const hash = this.normalizeStartingMainTransactionHash(row?.hash);
          const toAddress = this.normalizeStartingMainTransactionAddress(row?.toAddress);
          const hashKey = hash.toLowerCase();
          const exactKey = `${hashKey}|${toAddress.toLowerCase()}`;
          let status = 'ready';
          let statusLabel = 'Ready';
          if (!this.startingMainTransactionHashLooksUsable(hash)) {
            status = 'needs-hash';
            statusLabel = 'Needs hash';
          } else if (seen.has(exactKey)) {
            status = 'duplicate-draft';
            statusLabel = 'Duplicate draft';
          } else if (existing.exact.has(exactKey)) {
            status = 'duplicate-existing';
            statusLabel = 'Already in Main Data';
          } else if (existing.byHash.has(hashKey)) {
            status = 'different-to';
            statusLabel = 'Same hash, different To';
          }
          if (hashKey) seen.add(exactKey);
          return {
            id: row?.id || this.buildStartingMainTransactionDraftId(hash, toAddress, index),
            hash,
            toAddress,
            status,
            statusLabel
          };
        });
      return this.startingMainTransactionDraftRows;
    },

    getStartingMainTransactionConfirmationState() {
      const matrix = normalizeCaseMatrix(this.emptyCaseStarterGetMatrix('main') || []);
      const roots = typeof this.caseWorkflowCollectRootTransactions === 'function'
        ? this.caseWorkflowCollectRootTransactions(matrix, { allowNonRootLabels: true })
        : [];
      let rootRows = (Array.isArray(roots) ? roots : []).map(root => root?.row).filter(Array.isArray);
      if (!rootRows.length) {
        // Compatibility fallback for a newly pasted, not-yet-labeled root.
        // Descendants carry Column O / From Hash and are not starting rows.
        rootRows = matrix.filter((row, rowIndex) => {
          if (!Array.isArray(row)) return false;
          if ((rowIndex === 0 || this.caseManualIsHeaderRow?.(row)) && this.caseManualIsHeaderRow?.(row)) return false;
          const hash = this.normalizeStartingMainTransactionHash(row?.[CASE_MANUAL_START_COL.TX_HASH]);
          const fromHash = normalizeCell(row?.[CASE_MANUAL_START_COL.FROM_HASH]);
          return Boolean(hash && !fromHash);
        });
      }
      return {
        count: rootRows.length,
        rows: rootRows,
        roots,
        hasStartingTransactions: rootRows.length > 0
      };
    },

    getStartingMainTransactionsWorkflowSignalCode(step = {}) {
      return normalizeCell(step.signalCode || step.signal || step.completionSignal || step.completionSignalCode || '');
    },

    workflowStepHasRecordedSignal(step = {}) {
      const signalCode = this.getStartingMainTransactionsWorkflowSignalCode(step);
      if (!signalCode) return false;
      const record = this.caseSupportState?.workflowState?.signalCompleteByCode?.[signalCode];
      return !!(record?.completedAtUtc || record?.markedAtUtc || record?.checkedAtUtc || record?.appliedAtUtc);
    },

    recordStartingMainTransactionsWorkflowCurrentCheck(sourceActionId = 'workflow-current-main-data', options = {}) {
      const { step } = this.findEmptyCaseStarterWorkflowStep(candidate => this.isStartingMainTransactionsWorkflowStep(candidate));
      if (!step && !this.getStartingMainTransactionConfirmationState().hasStartingTransactions) return null;
      return this.confirmStartingMainTransactionsWorkflowStepFromCurrentRows(step || { tag: 'ADD_MAIN_TRANSACTIONS_TO_CASE' }, {
        ...options,
        sourceActionId
      });
    },

    applyStartingMainTransactionsWorkflowStepCurrentRows(view = {}, definition = {}) {
      const confirmation = this.getStartingMainTransactionConfirmationState();
      if (!confirmation.hasStartingTransactions) return view;

      const count = Number(confirmation.count) || 0;
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const existingCheck = this.caseSupportState?.checkResultsById?.main_transactions || null;
      const hasSignal = this.workflowStepHasRecordedSignal(view) || this.workflowStepHasRecordedSignal(definition);
      const sources = {
        ...(view.sources || {}),
        check: true,
        signal: !!((view.sources || {}).signal || hasSignal),
        manual: !!(view.sources || {}).manual
      };
      const checkResult = existingCheck && typeof existingCheck === 'object'
        ? {
          ...existingCheck,
          status: 'pass',
          message: existingCheck.message || `${count} starting main transaction${count === 1 ? '' : 's'} detected in Main Data.`,
          revision: currentRevision,
          checkedAtUtc: existingCheck.checkedAtUtc || this.emptyCaseStarterNudgeNowUtc(),
          derivedFromCurrentRows: true
        }
        : {
          id: 'main_transactions',
          status: 'pass',
          message: `${count} starting main transaction${count === 1 ? '' : 's'} detected in Main Data.`,
          details: {
            mode: 'current-main-data-derived',
            mainTransactionCount: count,
            hashRowCount: count,
            source: 'workflow-current-main-data',
            stepTag: normalizeCell(view.tag || definition?.tag || view.id || '')
          },
          revision: currentRevision,
          checkedAtUtc: this.emptyCaseStarterNudgeNowUtc(),
          derivedFromCurrentRows: true
        };

      return {
        ...view,
        complete: true,
        stale: false,
        blocked: false,
        status: 'complete',
        statusLabel: count > 0 ? `Complete · ${count} starting row${count === 1 ? '' : 's'}` : 'Complete',
        statusClass: 'workflow-step-card--complete',
        staleReason: '',
        sources,
        checkResult,
        completionSummary: count > 0
          ? `${count} starting transaction${count === 1 ? '' : 's'} are present in current Main Data.`
          : view.completionSummary
      };
    },

    buildMainMatrixWithStartingTransactionDraftRows(draftRows = []) {
      const existing = normalizeCaseMatrix(this.emptyCaseStarterGetMatrix('main') || []);
      const header = Array.isArray(existing[0]) && existing[0].some(value => normalizeCell(value))
        ? existing[0].slice()
        : CASE_MANUAL_START_CANONICAL_HEADERS.slice();
      const baseRows = existing.slice(1).filter(row => Array.isArray(row) && row.some(value => normalizeCell(value)));
      const output = [this.caseManualEnsureRowWidth ? this.caseManualEnsureRowWidth(header, CASE_MANUAL_START_COLUMN_COUNT) : header];
      baseRows.forEach(row => output.push(this.caseManualEnsureRowWidth ? this.caseManualEnsureRowWidth(row, CASE_MANUAL_START_COLUMN_COUNT) : row.slice()));
      draftRows.forEach((draft) => {
        const row = Array.from({ length: CASE_MANUAL_START_COLUMN_COUNT }, () => '');
        row[CASE_MANUAL_START_COL.TX_HASH] = this.normalizeStartingMainTransactionHash(draft.hash);
        row[CASE_MANUAL_START_COL.TO_ADDRESS] = this.normalizeStartingMainTransactionAddress(draft.toAddress);
        output.push(row);
      });
      return output;
    },

    async loadStartingMainTransactionsIntoMainSheet(...args) {
      const launchedFromNudge = this.emptyCaseStarterNudgeLaunchPending === true;
      const launchedFromFlowStory = this.caseInputFlowStoryStartingModalOpen === true;
      const originalResult = this.callFirstCaseBuddyOriginalMethod('loadStartingMainTransactionsIntoMainSheet', args);
      if (originalResult !== undefined) {
        const resolved = originalResult && typeof originalResult.then === 'function' ? await originalResult : originalResult;
        await this.$nextTick();
        if (this.hasMainInputTransactionEntries()) {
          await this.handleEmptyCaseStarterNudgeStartingTransactionsLoaded({
            fromNudge: launchedFromNudge,
            keepInputViewport: launchedFromFlowStory
          });
        }
        this.caseInputFlowStoryStartingModalOpen = false;
        if (launchedFromFlowStory) this.scheduleCaseInputFlowStoryRefresh?.(80, { force: true });
        return resolved;
      }

      this.refreshStartingMainTransactionStagedStatuses();
      const readyRows = (Array.isArray(this.startingMainTransactionDraftRows) ? this.startingMainTransactionDraftRows : [])
        .filter(row => row.status === 'ready' || row.status === 'different-to');
      if (!readyRows.length) {
        this.showCaseToast?.('No starting transaction rows are ready to load.');
        return false;
      }

      const nextMatrix = this.buildMainMatrixWithStartingTransactionDraftRows(readyRows);
      await this.replaceCaseManualStartLogicalSheetMatrix('main', nextMatrix, `Loaded ${readyRows.length} starting main transaction${readyRows.length === 1 ? '' : 's'} into Main Data.`);
      this.showStartingMainTransactionsModal = false;
      this.startingMainTransactionsPaste = '';
      this.startingMainTransactionDraftRows = [];
      this.rawDataImportMissingAfterRows = [];
      this.queueCaseSummaryRefresh?.(120);
      await this.handleEmptyCaseStarterNudgeStartingTransactionsLoaded({
        fromNudge: launchedFromNudge,
        keepInputViewport: launchedFromFlowStory
      });
      this.caseInputFlowStoryStartingModalOpen = false;
      if (launchedFromFlowStory) this.scheduleCaseInputFlowStoryRefresh?.(80, { force: true });
      this.showCaseToast?.(`Loaded ${readyRows.length} starting transaction${readyRows.length === 1 ? '' : 's'} into Main Data.`);
      return { ok: true, loadedCount: readyRows.length };
    },

    async handleEmptyCaseStarterNudgeStartingTransactionsLoaded(options = {}) {
      const launchedFromNudge = options.fromNudge === true || this.emptyCaseStarterNudgeLaunchPending === true;
      const keepInputViewport = options.keepInputViewport === true;
      this.markEmptyCaseStarterNudgeSessionDismissed('starting-transactions-loaded');
      const state = this.ensureEmptyCaseStarterNudgeState();
      state.dismissedForCase = true;
      state.dismissedAtUtc = this.emptyCaseStarterNudgeNowUtc();
      state.dismissedReason = launchedFromNudge ? 'loaded-starting-transactions-from-nudge' : 'loaded-starting-transactions';
      this.emptyCaseStarterNudgeLaunchPending = false;
      if (this.emptyCaseStarterNudgeDontShowAgain) this.updateEmptyCaseStarterNudgeUserPreference();
      this.recordEmptyCaseStarterNudgeStartingWorkflowSignal();
      this.recordStartingMainTransactionsWorkflowCurrentCheck('starting-transactions-loaded', { silent: true });
      this.persistEmptyCaseStarterNudgeSupportState();
      if (keepInputViewport) {
        this.scheduleCaseInputFlowStoryRefresh?.(80, { force: true });
        this.$nextTick(() => this.scrollToSection?.('case-input'));
      } else {
        this.scheduleEmptyCaseStarterNextWorkflowStepScroll({ delayMs: launchedFromNudge ? 420 : 220 });
      }
    },

    findEmptyCaseStarterWorkflowStep(predicate) {
      const groups = Array.isArray(this.workflowGroupsView) ? this.workflowGroupsView : [];
      for (const group of groups) {
        const step = (group.steps || []).find(predicate);
        if (step) return { group, step };
      }
      return { group: null, step: null };
    },

    recordEmptyCaseStarterNudgeStartingWorkflowSignal() {
      const { step } = this.findEmptyCaseStarterWorkflowStep(candidate => /load\s+starting|starting\s+main|starting\s+transaction/i.test(`${candidate?.title || ''} ${candidate?.tag || ''}`));
      if (!step) return;
      const signalCode = normalizeCell(step.signalCode || step.signal || step.completionSignal || '');
      if (signalCode) {
        if (typeof this.recordWorkflowSignal === 'function') {
          this.recordWorkflowSignal(signalCode, 'empty_case_starter_nudge', { silent: true });
          return;
        }
        const workflow = this.caseSupportState.workflowState || {};
        workflow.signalCompleteByCode = {
          ...(workflow.signalCompleteByCode || {}),
          [signalCode]: {
            code: signalCode,
            sourceActionId: 'empty_case_starter_nudge',
            completedAtUtc: this.emptyCaseStarterNudgeNowUtc(),
            revision: Number(this.caseSupportState.caseDataRevision) || 0
          }
        };
        this.caseSupportState.workflowState = workflow;
        return;
      }
      this.confirmStartingMainTransactionsWorkflowStepFromCurrentRows(step, { sourceActionId: 'empty_case_starter_nudge', silent: true });
    },

    caseWorkflowIntakeStepPatterns(key = '') {
      const normalized = normalizeCell(key).toLowerCase().replace(/[^a-z0-9]+/giu, '');
      const aliases = {
        start: 'starting',
        main: 'starting',
        startingmain: 'starting',
        network: 'networks',
        chains: 'networks',
        utc: 'dates',
        date: 'dates',
        pathlabel: 'labels',
        pathlabels: 'labels',
        label: 'labels',
        casedetail: 'casedetails',
        casedetails: 'casedetails',
        details: 'casedetails',
        narrative: 'casedetails',
        raw: 'rawdata',
        rawdata: 'rawdata'
      };
      const patternKey = aliases[normalized] || normalized;
      const patterns = {
        starting: [/load\s+starting\s+main|starting\s+main\s+transactions|starting\s+transaction/iu],
        networks: [/identify\s+transaction\s+networks|transaction\s+networks|network\s+lookup|chain\s+id|chain\s+name/iu],
        dates: [/complete\s+utc\s+dates|utc\s+dates|date\s+order|order\s+main\s+entries/iu],
        labels: [/assign\s+main\s+transaction\s+path\s+labels|main\s+transaction\s+path\s+labels|path\s+labels/iu],
        casedetails: [/review\s+case\s+details|case\s+details\s+and\s+early\s+narrative|early\s+narrative|narrative\s+review/iu],
        rawdata: [/load\s+raw\s+data|raw\s+data\s+for\s+main|fetch\s+raw\s+data|raw\s+data/iu]
      };
      return patterns[patternKey] || [];
    },

    caseWorkflowStepNavigationText(step = {}) {
      return [
        step?.tag,
        step?.id,
        step?.title,
        step?.description,
        step?.displayOrder,
        step?.statusLabel
      ].map(value => normalizeCell(value)).filter(Boolean).join(' ');
    },

    caseWorkflowStepMatchesPatterns(step = {}, patterns = []) {
      const text = this.caseWorkflowStepNavigationText(step);
      if (!text) return false;
      return (Array.isArray(patterns) ? patterns : [patterns]).some((pattern) => {
        if (!pattern) return false;
        if (pattern instanceof RegExp) return pattern.test(text);
        return text.toLowerCase().includes(normalizeCell(pattern).toLowerCase());
      });
    },

    flattenCaseWorkflowStepsForNavigation() {
      const groups = Array.isArray(this.workflowGroupsView) ? this.workflowGroupsView : [];
      const flattened = [];
      groups.forEach((group, groupIndex) => {
        (group.steps || []).forEach((step, stepIndex) => {
          flattened.push({ group, step, groupIndex, stepIndex, index: flattened.length });
        });
      });
      return flattened;
    },

    findCaseWorkflowStepForNavigation(predicate) {
      const flattened = this.flattenCaseWorkflowStepsForNavigation();
      return flattened.find(item => predicate?.(item.step, item.group, item)) || { group: null, step: null, index: -1 };
    },

    findCaseWorkflowStepByPatterns(patterns = []) {
      return this.findCaseWorkflowStepForNavigation(step => this.caseWorkflowStepMatchesPatterns(step, patterns));
    },

    resolveCaseWorkflowScrollTargetByKey(key = '', options = {}) {
      const patterns = this.caseWorkflowIntakeStepPatterns(key);
      const match = this.findCaseWorkflowStepByPatterns(patterns);
      if (!match?.step) return match;
      if (options.skipCompletedTarget === false) return match;
      const flattened = this.flattenCaseWorkflowStepsForNavigation();
      const startIndex = Number(match.index) >= 0
        ? Number(match.index)
        : flattened.findIndex(item => item.step?.tag === match.step?.tag);
      if (startIndex < 0) return match;
      const firstActionable = flattened.slice(startIndex).find((item) => {
        const step = item.step || {};
        return !step.complete || step.stale || step.blocked || step.status === 'attention' || step.status === 'unchecked' || step.status === 'ready';
      });
      return firstActionable || match;
    },

    openCaseWorkflowGroupForStep(match = {}) {
      const groupId = match?.group?.id;
      if (!groupId) return;
      const uiState = this.caseSupportState.uiState || {};
      const foldouts = uiState.foldouts || {};
      this.caseSupportState.uiState = {
        ...uiState,
        foldouts: {
          ...foldouts,
          workflowGroups: {
            ...(foldouts.workflowGroups || {}),
            [groupId]: true
          }
        }
      };
    },

    highlightCaseWorkflowStepForNavigation(step = {}) {
      const tag = normalizeCell(step?.tag || step?.id || '');
      if (!tag) return;
      this.emptyCaseStarterNudgeHighlightedWorkflowStepTag = tag;
      if (this.emptyCaseStarterNudgeHighlightTimer && typeof window !== 'undefined') window.clearTimeout(this.emptyCaseStarterNudgeHighlightTimer);
      if (typeof window !== 'undefined') {
        this.emptyCaseStarterNudgeHighlightTimer = window.setTimeout(() => {
          this.emptyCaseStarterNudgeHighlightedWorkflowStepTag = '';
          this.emptyCaseStarterNudgeHighlightTimer = null;
        }, CASE_EMPTY_STARTER_NUDGE_HIGHLIGHT_MS);
      }
    },

    async scrollToCaseWorkflowStepMatch(match = {}, options = {}) {
      const step = match?.step;
      if (!step) return false;
      this.setWorkflowPanelExpanded?.(true);
      this.openCaseWorkflowGroupForStep(match);
      this.highlightCaseWorkflowStepForNavigation(step);
      await this.caseWorkflowUiNextTick();
      const tag = normalizeCell(step.tag || step.id || '');
      const element = tag && typeof document !== 'undefined' ? document.getElementById(`workflow-step-${tag}`) : null;
      if (element && typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({ behavior: options.smooth === false ? 'auto' : 'smooth', block: options.block || 'center' });
        return true;
      }
      const workflowPanel = typeof document !== 'undefined' ? document.getElementById('case-workflow') : null;
      if (workflowPanel && typeof workflowPanel.scrollIntoView === 'function') {
        workflowPanel.scrollIntoView({ behavior: options.smooth === false ? 'auto' : 'smooth', block: options.block || 'start' });
      }
      return false;
    },

    async scrollToCaseWorkflowStepByKey(key = '', options = {}) {
      return this.scrollToCaseWorkflowStepMatch(this.resolveCaseWorkflowScrollTargetByKey(key, options), options);
    },

    clearCaseWorkflowScheduledScrollTimers() {
      if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        (Array.isArray(this.caseWorkflowScheduledScrollTimers) ? this.caseWorkflowScheduledScrollTimers : [])
          .forEach(timer => window.clearTimeout(timer));
      }
      this.caseWorkflowScheduledScrollTimers = [];
    },

    caseWorkflowScheduledScrollShouldPause() {
      return Boolean(
        this.caseSimpleLaunchAutoStarting ||
        this.caseSimpleLaunchVisible ||
        this.caseIntakePreparation?.active ||
        this.caseAutomation?.active ||
        this.caseAutomation?.busy ||
        this.caseWorkflowRawDataTool88Running ||
        this.caseWorkflowTool88RawRunning
      );
    },

    scheduleCaseWorkflowStepScrollByKey(key = '', options = {}) {
      const delayMs = Math.max(0, Number(options.delayMs) || 0);
      const delays = Array.isArray(options.delays)
        ? options.delays
        : [0, delayMs || 220, Math.max(delayMs + 420, 820), Math.max(delayMs + 980, 1450)];
      const run = () => {
        if (this.caseWorkflowScheduledScrollShouldPause()) return false;
        return this.scrollToCaseWorkflowStepByKey(key, { ...options, smooth: options.smooth !== false });
      };
      this.caseWorkflowUiNextTick().then(run);
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
        delays.filter(delay => Number(delay) > 0).forEach((delay) => {
          const timer = window.setTimeout(() => {
            this.caseWorkflowScheduledScrollTimers = (Array.isArray(this.caseWorkflowScheduledScrollTimers) ? this.caseWorkflowScheduledScrollTimers : [])
              .filter(item => item !== timer);
            run();
          }, Number(delay));
          this.caseWorkflowScheduledScrollTimers = [
            ...(Array.isArray(this.caseWorkflowScheduledScrollTimers) ? this.caseWorkflowScheduledScrollTimers : []),
            timer
          ];
        });
      }
    },

    async scrollToEmptyCaseStarterNextWorkflowStep(options = {}) {
      return this.scrollToCaseWorkflowStepByKey('networks', { skipCompletedTarget: true, ...options });
    },

    scheduleEmptyCaseStarterNextWorkflowStepScroll(options = {}) {
      this.scheduleCaseWorkflowStepScrollByKey('networks', { skipCompletedTarget: true, delayMs: 520, ...options });
    },

    isStartingMainTransactionsWorkflowStep(step = {}) {
      const text = [step?.tag, step?.id, step?.title, step?.description].map(value => normalizeCell(value)).join(' ').toLowerCase();
      return /load\s+starting|starting\s+main|starting\s+transaction|main\s+transaction/.test(text) && !/network|date|label|raw|attribution/.test(text);
    },

    isStartingMainTransactionsCheckConfirmAction(action = {}) {
      const text = [action?.id, action?.key, action?.label, action?.title, action?.method, action?.handler, action?.action].map(value => normalizeCell(value)).join(' ').toLowerCase();
      return /check\s*\/?\s*confirm|confirm\s*\/?\s*check|run\s+check|refresh\s+status|check\s+status/.test(text);
    },

    confirmStartingMainTransactionsWorkflowStepFromCurrentRows(step = {}, options = {}) {
      const confirmation = this.getStartingMainTransactionConfirmationState();
      const now = this.emptyCaseStarterNudgeNowUtc();
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const passed = confirmation.hasStartingTransactions;
      const checkResult = {
        id: 'main_transactions',
        status: passed ? 'pass' : 'fail',
        message: passed
          ? `${confirmation.count} starting main transaction${confirmation.count === 1 ? '' : 's'} detected in Main Data.`
          : 'No starting main transaction hashes were detected in Column B of Main Data.',
        details: {
          mode: 'workflow-check-confirm',
          mainTransactionCount: confirmation.count,
          hashRowCount: confirmation.count,
          source: normalizeCell(options.sourceActionId || 'workflow-check-confirm'),
          stepTag: normalizeCell(step?.tag || step?.id || '')
        },
        revision,
        checkedAtUtc: now
      };
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        main_transactions: checkResult
      };
      this.persistEmptyCaseStarterNudgeSupportState();
      this.queueCaseSummaryRefresh?.(120);
      if (!options.silent) {
        this.showCaseToast?.(passed ? checkResult.message : 'Add at least one transaction hash in Column B before confirming this step.');
        if (passed) this.scheduleCaseWorkflowStepScrollByKey('networks', { skipCompletedTarget: true, delayMs: 240 });
      }
      return { ok: passed, checkResult };
    },

    cancelStartingMainTransactionsModal(...args) {
      this.emptyCaseStarterNudgeLaunchPending = false;
      this.caseInputFlowStoryStartingModalOpen = false;
      return this.callCaseBuddyOriginalMethod(caseBuddyBaseMethods, 'cancelStartingMainTransactionsModal', args);
    },

    saveSpreadsheetFromEvent(...args) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyProjectSheetMethods, 'saveSpreadsheetFromEvent', args);
      if (this.hasMeaningfulMainInputRowsForEmptyCaseStarter() || this.hasMeaningfulRawRowsForEmptyCaseStarter()) {
        this.markEmptyCaseStarterNudgeInvestigationStarted('input-workbook-edited');
      }
      return result;
    },

    handleOutputSheetSaveRequest(...args) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyProjectSheetMethods, 'handleOutputSheetSaveRequest', args);
      if (this.hasMeaningfulOutputRowsForEmptyCaseStarter()) {
        this.markEmptyCaseStarterNudgeInvestigationStarted('output-workbook-edited');
      }
      return result;
    },

    clearCaseManualStartAutosaveTimer() {
      if (this.caseManualStartAutosaveTimer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(this.caseManualStartAutosaveTimer);
      }
      this.caseManualStartAutosaveTimer = null;
    },

    queueCaseManualStartAutosave(reason = 'Manual Solve updated case data.', options = {}) {
      const normalizedReason = normalizeCell(reason) || 'Manual Solve updated case data.';
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      this.caseManualStartAutosaveReason = normalizedReason;
      this.caseManualStartAutosaveRevision = revision;
      this.caseManualStartAutosaveLastError = '';

      if (!this.autoSaveEnabled && options.force !== true) {
        this.clearCaseManualStartAutosaveTimer();
        this.caseManualStartAutosavePending = false;
        this.caseManualStartAutosaveStatus = 'Manual Solve changed Main/Raw data. Auto-save is off; use Save to persist this case file.';
        return false;
      }
      if (this.casePageHydrating && options.force !== true) return false;

      this.caseManualStartAutosavePending = true;
      if (this.caseManualStartAutosaveInFlight) {
        this.caseManualStartAutosaveQueuedDuringFlight = true;
        this.caseManualStartAutosaveStatus = 'Manual Solve autosave is finishing; another save is queued.';
        return true;
      }

      const explicitDelay = Number(options.delayMs);
      const autoIsRunning = Boolean(this.caseManualStartViewportAuto?.active || this.caseAutomation?.active || this.caseAutomation?.busy);
      const delayMs = Number.isFinite(explicitDelay) && explicitDelay >= 0
        ? explicitDelay
        : (autoIsRunning ? 2400 : 850);

      // During Manual Solve Auto, keep the first pending timer instead of constantly
      // pushing it back. This throttles saves without letting a long auto run remain
      // unsaved until the very end.
      if (!(autoIsRunning && this.caseManualStartAutosaveTimer && options.reschedule !== true)) {
        this.clearCaseManualStartAutosaveTimer();
        const run = () => {
          this.caseManualStartAutosaveTimer = null;
          this.flushCaseManualStartAutosave({ reason: this.caseManualStartAutosaveReason || normalizedReason });
        };
        if (typeof window !== 'undefined' && typeof window.setTimeout === 'function' && delayMs > 0) {
          this.caseManualStartAutosaveTimer = window.setTimeout(run, delayMs);
        } else {
          run();
        }
      }

      this.caseManualStartAutosaveStatus = delayMs
        ? `Manual Solve autosave queued for revision ${revision || 'current'}.`
        : 'Manual Solve autosave queued.';
      return true;
    },

    async flushCaseManualStartWorkbookRefsForAutosave(reason = 'Manual Solve updated case data.') {
      const sheetRefs = [this.$refs.inputSpreadsheet, this.$refs.outputSpreadsheet].filter(Boolean);
      const workbookSaveNames = ['saveActiveSheet', 'saveWorkbook', 'saveToStorage', 'persistWorkbook', 'requestSave'];
      let savedAnySheet = false;
      for (const sheet of sheetRefs) {
        for (const methodName of workbookSaveNames) {
          if (typeof sheet?.[methodName] !== 'function') continue;
          try {
            const result = sheet[methodName]({ silent: true, suppressToast: true, quiet: true, autosave: true, source: 'case-buddy-manual-solve-autosave', reason });
            if (result && typeof result.then === 'function') await result;
            savedAnySheet = true;
          } catch (error) {
            console.warn(`Case Buddy Manual Solve autosave could not call workbook ${methodName}.`, error);
          }
        }
      }
      return savedAnySheet;
    },

    async invokeCaseManualStartProjectAutosave(reason = 'Manual Solve updated case data.') {
      if (typeof this.persistCaseSupportState === 'function') {
        this.persistCaseSupportState();
      } else if (typeof this.queueCaseSupportStatePersist === 'function') {
        this.queueCaseSupportStatePersist(0);
      }
      await this.$nextTick();
      const workbookFlushed = await this.flushCaseManualStartWorkbookRefsForAutosave(reason);

      const saveOptions = {
        silent: true,
        suppressToast: true,
        quiet: true,
        autosave: true,
        source: 'case-buddy-manual-solve-autosave',
        reason
      };
      const saveMethodNames = [
        'saveCurrentProjectFile',
        'saveCurrentProject',
        'persistCurrentProjectFile',
        'persistCurrentProject',
        'saveProjectFile'
      ];
      for (const methodName of saveMethodNames) {
        if (typeof this[methodName] !== 'function') continue;
        try {
          const result = this[methodName](saveOptions);
          const resolved = result && typeof result.then === 'function' ? await result : result;
          if (resolved !== false) return true;
        } catch (errorWithOptions) {
          try {
            const fallbackResult = this[methodName]();
            const fallbackResolved = fallbackResult && typeof fallbackResult.then === 'function' ? await fallbackResult : fallbackResult;
            if (fallbackResolved !== false) return true;
          } catch (fallbackError) {
            console.warn(`Case Buddy Manual Solve autosave could not call ${methodName}.`, fallbackError || errorWithOptions);
          }
        }
      }

      const sheetRefs = [this.$refs.inputSpreadsheet, this.$refs.outputSpreadsheet].filter(Boolean);
      const workbookSaveNames = ['saveActiveSheet', 'saveWorkbook', 'saveToStorage', 'persistWorkbook', 'requestSave'];
      let savedAnySheet = false;
      for (const sheet of sheetRefs) {
        for (const methodName of workbookSaveNames) {
          if (typeof sheet?.[methodName] !== 'function') continue;
          const result = sheet[methodName]();
          if (result && typeof result.then === 'function') await result;
          savedAnySheet = true;
        }
      }
      if (savedAnySheet || workbookFlushed) return true;
      throw new Error('No project save method was available for Manual Solve autosave.');
    },

    async flushCaseManualStartAutosave(options = {}) {
      const force = options.force === true;
      if (!force && !this.autoSaveEnabled) return false;
      if (!force && this.casePageHydrating) return false;
      if (this.caseManualStartAutosaveInFlight) {
        this.caseManualStartAutosavePending = true;
        this.caseManualStartAutosaveQueuedDuringFlight = true;
        return false;
      }
      this.clearCaseManualStartAutosaveTimer();
      if (!force && !this.caseManualStartAutosavePending) return false;

      const revision = Number(this.caseSupportState?.caseDataRevision) || Number(this.caseManualStartAutosaveRevision) || 0;
      const reason = normalizeCell(options.reason || this.caseManualStartAutosaveReason || 'Manual Solve updated case data.');
      this.caseManualStartAutosavePending = false;
      this.caseManualStartAutosaveQueuedDuringFlight = false;
      this.caseManualStartAutosaveInFlight = true;
      this.caseManualStartAutosaveStatus = `Saving Manual Solve changes for revision ${revision || 'current'}…`;

      try {
        await this.invokeCaseManualStartProjectAutosave(reason);
        const savedRevision = Number(this.caseSupportState?.caseDataRevision) || revision;
        this.caseManualStartAutosaveLastSavedRevision = savedRevision;
        this.caseManualStartAutosaveLastSavedAtUtc = new Date().toISOString();
        this.caseManualStartAutosaveLastError = '';
        this.caseManualStartAutosaveStatus = `Manual Solve changes autosaved at revision ${savedRevision || 'current'}.`;
        return true;
      } catch (error) {
        const message = error?.message || 'unknown save issue';
        this.caseManualStartAutosavePending = true;
        this.caseManualStartAutosaveLastError = message;
        this.caseManualStartAutosaveStatus = `Manual Solve autosave failed: ${message}. Use Save before refreshing.`;
        console.warn('Case Buddy Manual Solve autosave failed.', error);
        if (!options.silent && typeof this.showCaseToast === 'function') {
          this.showCaseToast('Manual Solve updated case data, but autosave could not complete. Use Save before refreshing.');
        }
        return false;
      } finally {
        this.caseManualStartAutosaveInFlight = false;
        if (this.caseManualStartAutosaveQueuedDuringFlight) {
          this.caseManualStartAutosaveQueuedDuringFlight = false;
          this.queueCaseManualStartAutosave(this.caseManualStartAutosaveReason || reason, { delayMs: 900, reschedule: true });
        }
      }
    },

    handleActiveInputSheetChange(...args) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyProjectSheetMethods, 'handleActiveInputSheetChange', args);
      this.$nextTick(() => this.$refs.inputSpreadsheet?.refreshDimensions?.());
      return result;
    },


    isCaseWorkflowRawDataFetchStep(step = {}) {
      const tag = normalizeCell(step?.tag || step?.id || step?.workflowTag || '').toUpperCase();
      const text = `${step?.title || ''} ${step?.description || ''} ${step?.tag || ''}`.toLowerCase();
      return tag === 'RAW_DATA_FETCH' || /load\s+raw\s+data\s+for\s+main\s+transactions|raw[_\s-]*data[_\s-]*fetch|load\s+raw\s+data/.test(text);
    },

    getCaseWorkflowRawDataFetchStep() {
      const recommended = this.recommendedWorkflowStep;
      if (this.isCaseWorkflowRawDataFetchStep(recommended)) return recommended;
      const groups = Array.isArray(this.workflowGroupsView) ? this.workflowGroupsView : [];
      return groups.flatMap(group => Array.isArray(group.steps) ? group.steps : []).find(step => this.isCaseWorkflowRawDataFetchStep(step)) || null;
    },

    caseWorkflowRootIdentityText(value = '') {
      return normalizeCell(value).trim();
    },

    caseWorkflowRootIdentityKey(value = '') {
      return this.caseWorkflowRootIdentityText(value).toLowerCase();
    },

    caseWorkflowRootAddressKey(value = '') {
      return this.caseWorkflowRootIdentityKey(value).replace(/\s+/gu, '');
    },

    caseWorkflowPathLabelBelongsToRoot(pathLabel = '', rootLabel = '', knownRootLabels = []) {
      const labelKey = this.caseWorkflowRootIdentityKey(pathLabel);
      const rootKey = this.caseWorkflowRootIdentityKey(rootLabel);
      if (!labelKey || !rootKey) return false;
      if (labelKey === rootKey) return true;
      if (!labelKey.startsWith(rootKey)) return false;

      // Root labels may share prefixes (for example 1 and 10, or A and AA).
      // Resolve against the longest known root prefix so one root family cannot
      // absorb another merely because its label starts with the same characters.
      const bestKnownRoot = (Array.isArray(knownRootLabels) ? knownRootLabels : [])
        .map(value => this.caseWorkflowRootIdentityKey(value))
        .filter(Boolean)
        .filter(value => labelKey === value || labelKey.startsWith(value))
        .sort((left, right) => right.length - left.length)[0] || '';
      return !bestKnownRoot || bestKnownRoot === rootKey;
    },

    caseWorkflowCollectRootTransactions(matrix = [], options = {}) {
      const rows = normalizeCaseMatrix(Array.isArray(matrix) ? matrix : []);
      const knownRootLabels = new Set(
        (Array.isArray(options.knownRootLabels) ? options.knownRootLabels : [])
          .map(value => this.caseWorkflowRootIdentityKey(value))
          .filter(Boolean)
      );
      const roots = [];
      rows.forEach((row, rowIndex) => {
        if (!Array.isArray(row) || !row.some(value => normalizeCell(value))) return;
        if ((rowIndex === 0 || this.caseManualIsHeaderRow?.(row)) && this.caseManualIsHeaderRow?.(row)) return;
        const hash = this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.TX_HASH]);
        if (!hash) return;
        const fromHash = this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.FROM_HASH]);
        const pathLabel = this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]);
        const pathKey = this.caseWorkflowRootIdentityKey(pathLabel);
        const explicitlyKnownRoot = !!pathKey && knownRootLabels.has(pathKey);
        const pathLooksLikeRoot = typeof this.caseManualStartPathLabelLooksInitialFlowRoot === 'function'
          ? this.caseManualStartPathLabelLooksInitialFlowRoot(pathLabel)
          : !/[a-z.]|\d+\.\d+/iu.test(pathLabel);
        if (fromHash && !explicitlyKnownRoot) return;
        if (!explicitlyKnownRoot && !pathLooksLikeRoot && options.allowNonRootLabels !== true) return;

        const toAddress = this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
        roots.push({
          row,
          rowIndex,
          spreadsheetRowNumber: rowIndex + 1,
          pathLabel,
          pathKey,
          hash,
          hashKey: this.caseWorkflowRawToMainHashKey(hash),
          fromAddress: this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]),
          toAddress,
          toAddressKey: this.caseWorkflowRootAddressKey(toAddress),
          fromHash,
          continuesAs: this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.CONTINUES_AS])
        });
      });
      return roots;
    },

    caseWorkflowRootInstanceKey(root = {}, options = {}) {
      const hashKey = this.caseWorkflowRawToMainHashKey(
        root?.hash
          || root?.transactionHash
          || root?.txHash
          || root?.row?.[CASE_MANUAL_START_COL.TX_HASH]
          || ''
      );
      const pathKey = this.caseWorkflowRootIdentityKey(
        root?.pathLabel
          || root?.label
          || root?.row?.[CASE_MANUAL_START_COL.PATH_LABEL]
          || ''
      );
      const targetKey = this.caseWorkflowRootAddressKey(
        root?.toAddress
          || root?.targetAddress
          || root?.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]
          || ''
      );
      const rowIndex = Number.isInteger(Number(root?.rowIndex))
        ? Number(root.rowIndex)
        : (Number.isFinite(Number(root?.spreadsheetRowNumber)) ? Number(root.spreadsheetRowNumber) - 1 : -1);
      const stableParts = [hashKey, pathKey, targetKey].filter(Boolean);
      const stable = stableParts.join('|');
      if (options.includeRowIndex === false) return stable;
      return `${stable || 'root'}|row:${rowIndex >= 0 ? rowIndex : normalizeCell(root?.id || '')}`;
    },

    caseWorkflowRawRootLooksUsable(root = {}, rawMatrix = [], knownRootLabels = []) {
      const row = Array.isArray(root?.row) ? root.row : [];
      if (!row.length) return false;
      const hasFrom = !!this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
      const hasTo = !!this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
      const hasDate = !!this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.DATE]);
      const hasAmount = !!this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.AMOUNT]);
      const hasToken = !!this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.TOKEN]);
      const hasFunds = !!this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
      const hasChain = !!(
        this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.CHAIN_ID])
        || this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.CHAIN_NAME])
      );
      const detailCount = [hasFrom, hasTo, hasDate, hasAmount, hasToken, hasFunds, hasChain].filter(Boolean).length;
      const hasCoreTransactionDetails = hasFrom && hasTo && (hasDate || (hasAmount && hasToken) || hasFunds);
      if (hasCoreTransactionDetails || detailCount >= 5) return true;

      const rootHashKey = this.caseWorkflowRawToMainHashKey(root?.hash);
      const rootLabel = this.caseWorkflowRootIdentityText(root?.pathLabel);
      const rootToAddressKey = this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
      const rows = normalizeCaseMatrix(Array.isArray(rawMatrix) ? rawMatrix : []);
      const hasDescendant = rows.some((candidateRow, candidateIndex) => {
        if (candidateIndex === Number(root?.rowIndex)) return false;
        const candidateHash = this.caseWorkflowRootIdentityText(candidateRow?.[CASE_MANUAL_START_COL.TX_HASH]);
        if (!candidateHash) return false;
        const candidateLabel = this.caseWorkflowRootIdentityText(candidateRow?.[CASE_MANUAL_START_COL.PATH_LABEL]);
        const candidateFromHash = this.caseWorkflowRawToMainHashKey(candidateRow?.[CASE_MANUAL_START_COL.FROM_HASH]);
        const candidateFromAddressKey = this.caseWorkflowRootAddressKey(candidateRow?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        const labelMatches = rootLabel
          ? this.caseWorkflowPathLabelBelongsToRoot(candidateLabel, rootLabel, knownRootLabels)
          : false;
        const addressContinuity = !!rootToAddressKey && candidateFromAddressKey === rootToAddressKey;
        return labelMatches || (!!rootHashKey && candidateFromHash === rootHashKey && addressContinuity);
      });
      if (hasDescendant) return true;

      const terminal = this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.CONTINUES_AS]);
      const notes = [
        row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row?.[CASE_MANUAL_START_COL.ANALYST_NOTES],
        row?.[CASE_MANUAL_START_COL.CASE_NOTES]
      ].map(value => this.caseWorkflowRootIdentityText(value)).filter(Boolean).join('\n');
      const hasReviewedTerminalEvidence = /^path\s+ends\.?$/iu.test(terminal) &&
        /tool\s*88.*(?:assess|complete|bridge|swap|service|dormant|low[-\s]*value|manual\s+review)|unsolved\s+bridge|flow:\s*assessed/iu.test(notes);
      return detailCount >= 3 && hasReviewedTerminalEvidence;
    },

    buildCaseBuddyMainRawDataCoverage(mainMatrix = [], rawMatrix = []) {
      const mainRows = normalizeCaseMatrix(Array.isArray(mainMatrix) ? mainMatrix : []);
      const rawRows = normalizeCaseMatrix(Array.isArray(rawMatrix) ? rawMatrix : []);
      const baseCoverage = buildMainRawDataCoverage(mainRows, rawRows);
      const mainRoots = this.caseWorkflowCollectRootTransactions(mainRows);
      if (!mainRoots.length) return baseCoverage;

      const mainCountsByHash = new Map();
      mainRoots.forEach((root) => {
        if (!root.hashKey) return;
        mainCountsByHash.set(root.hashKey, (mainCountsByHash.get(root.hashKey) || 0) + 1);
      });
      const duplicateHashKeys = new Set(
        Array.from(mainCountsByHash.entries())
          .filter(([, count]) => count > 1)
          .map(([hashKey]) => hashKey)
      );
      if (!duplicateHashKeys.size) {
        return {
          ...(baseCoverage || {}),
          rootAware: false,
          duplicateHashGroups: []
        };
      }

      const knownRootLabels = mainRoots.map(root => root.pathLabel).filter(Boolean);
      const rawRoots = this.caseWorkflowCollectRootTransactions(rawRows, {
        knownRootLabels,
        allowNonRootLabels: false
      });
      const baseRows = Array.isArray(baseCoverage?.coverageRows) ? baseCoverage.coverageRows : [];
      const baseForMainRoot = (mainRoot) => {
        const exactIndex = baseRows.find(row => Number(row?.rowIndex) === Number(mainRoot.rowIndex));
        if (exactIndex) return exactIndex;
        const spreadsheetMatch = baseRows.find(row => Number(row?.spreadsheetRowNumber) === Number(mainRoot.spreadsheetRowNumber));
        if (spreadsheetMatch) return spreadsheetMatch;
        return baseRows.find(row =>
          this.caseWorkflowRawToMainHashKey(row?.hash || row?.transactionHash || '') === mainRoot.hashKey
        ) || {};
      };

      const usedRawRootIndices = new Set();
      const duplicateHashGroups = [];
      const coverageRows = [];

      const rootsByHash = new Map();
      mainRoots.forEach((root) => {
        if (!rootsByHash.has(root.hashKey)) rootsByHash.set(root.hashKey, []);
        rootsByHash.get(root.hashKey).push(root);
      });

      rootsByHash.forEach((groupRoots, hashKey) => {
        const isDuplicateHash = duplicateHashKeys.has(hashKey);
        if (!isDuplicateHash) {
          groupRoots.forEach((mainRoot) => {
            const base = baseForMainRoot(mainRoot);
            const status = normalizeCell(base?.status || '').toLowerCase() === 'covered' ? 'covered' : 'missing';
            coverageRows.push({
              ...(base || {}),
              id: base?.id || `main-root-${mainRoot.rowIndex}-${hashKey}`,
              rowIndex: mainRoot.rowIndex,
              spreadsheetRowNumber: mainRoot.spreadsheetRowNumber,
              pathLabel: mainRoot.pathLabel,
              hash: mainRoot.hash,
              transactionHash: mainRoot.hash,
              toAddress: mainRoot.toAddress,
              status,
              rootInstanceKey: this.caseWorkflowRootInstanceKey(mainRoot),
              duplicateHash: false,
              matchedBy: base?.matchedBy || (status === 'covered' ? 'legacy-hash-coverage' : '')
            });
          });
          return;
        }

        const rawCandidates = rawRoots
          .filter(root => root.hashKey === hashKey)
          .map(root => ({
            ...root,
            usable: this.caseWorkflowRawRootLooksUsable(root, rawRows, knownRootLabels)
          }));
        const groupDebug = {
          hash: groupRoots[0]?.hash || hashKey,
          mainRootCount: groupRoots.length,
          rawRootCount: rawCandidates.length,
          mainRoots: [],
          rawRoots: rawCandidates.map(root => ({
            rowIndex: root.rowIndex,
            spreadsheetRowNumber: root.spreadsheetRowNumber,
            pathLabel: root.pathLabel,
            toAddress: root.toAddress,
            usable: root.usable
          }))
        };

        // Assign the most specific roots first. A populated investigator-selected
        // Column D target is stronger than path-label/order fallback.
        const orderedMainRoots = groupRoots.slice().sort((left, right) => {
          const leftSpecificity = (left.toAddressKey ? 2 : 0) + (left.pathKey ? 1 : 0);
          const rightSpecificity = (right.toAddressKey ? 2 : 0) + (right.pathKey ? 1 : 0);
          return rightSpecificity - leftSpecificity || left.rowIndex - right.rowIndex;
        });

        orderedMainRoots.forEach((mainRoot) => {
          const ranked = rawCandidates
            .filter(candidate => !usedRawRootIndices.has(candidate.rowIndex) && candidate.usable)
            .map(candidate => {
              const targetMatches = !!mainRoot.toAddressKey && candidate.toAddressKey === mainRoot.toAddressKey;
              const pathMatches = !!mainRoot.pathKey && candidate.pathKey === mainRoot.pathKey;
              const candidateHasConflictingTarget = !!mainRoot.toAddressKey && !!candidate.toAddressKey && !targetMatches;
              const candidateHasConflictingPath = !!mainRoot.pathKey && !!candidate.pathKey && !pathMatches;
              // Column D is the investigator-selected BTC output and is the
              // strongest discriminator. A conflicting populated target can never
              // cover this root. Path-label disagreement is tolerated only when the
              // selected target matches exactly, because labels can be repaired later.
              if (candidateHasConflictingTarget || (!targetMatches && candidateHasConflictingPath)) {
                return { candidate, score: -1, targetMatches, pathMatches };
              }
              let score = 0;
              if (targetMatches) score += 1000;
              if (pathMatches) score += 500;
              if (!mainRoot.toAddressKey && pathMatches) score += 100;
              if (!mainRoot.pathKey && targetMatches) score += 100;
              score += Math.max(0, 25 - Math.abs(Number(candidate.rowIndex) - Number(mainRoot.rowIndex)));
              return { candidate, score, targetMatches, pathMatches };
            })
            .filter(item => item.score >= 500 || item.targetMatches)
            .sort((left, right) => right.score - left.score || left.candidate.rowIndex - right.candidate.rowIndex);
          const match = ranked[0] || null;
          if (match) usedRawRootIndices.add(match.candidate.rowIndex);

          const base = baseForMainRoot(mainRoot);
          const status = match ? 'covered' : 'missing';
          const rootInstanceKey = this.caseWorkflowRootInstanceKey(mainRoot);
          coverageRows.push({
            ...(base || {}),
            id: `main-root-${mainRoot.rowIndex}-${hashKey}-${mainRoot.pathKey || 'no-path'}-${mainRoot.toAddressKey || 'no-target'}`,
            rowIndex: mainRoot.rowIndex,
            spreadsheetRowNumber: mainRoot.spreadsheetRowNumber,
            pathLabel: mainRoot.pathLabel,
            hash: mainRoot.hash,
            transactionHash: mainRoot.hash,
            toAddress: mainRoot.toAddress,
            status,
            rootInstanceKey,
            duplicateHash: true,
            matchedBy: match ? (match.targetMatches && match.pathMatches ? 'hash-path-target' : (match.targetMatches ? 'hash-target' : 'hash-path')) : '',
            matchedRawRootRowIndex: match ? match.candidate.rowIndex : -1,
            rawRootRowNumber: match ? match.candidate.spreadsheetRowNumber : '',
            rawRootPathLabel: match ? match.candidate.pathLabel : '',
            rawRootToAddress: match ? match.candidate.toAddress : '',
            message: match
              ? `Covered by the raw root for Path ${mainRoot.pathLabel || '—'} and To Address ${mainRoot.toAddress || '—'}.`
              : 'This duplicate transaction hash still needs its own raw set matching the root path label or selected To Address.'
          });
          groupDebug.mainRoots.push({
            rowIndex: mainRoot.rowIndex,
            spreadsheetRowNumber: mainRoot.spreadsheetRowNumber,
            pathLabel: mainRoot.pathLabel,
            toAddress: mainRoot.toAddress,
            status,
            matchedRawRootRowIndex: match ? match.candidate.rowIndex : -1,
            matchedBy: match ? (match.targetMatches && match.pathMatches ? 'hash-path-target' : (match.targetMatches ? 'hash-target' : 'hash-path')) : ''
          });
        });
        duplicateHashGroups.push(groupDebug);
      });

      coverageRows.sort((left, right) => Number(left?.rowIndex || 0) - Number(right?.rowIndex || 0));
      const coveredRows = coverageRows.filter(row => normalizeCell(row?.status).toLowerCase() === 'covered');
      const missingRows = coverageRows.filter(row => normalizeCell(row?.status).toLowerCase() !== 'covered');
      return {
        ...(baseCoverage || {}),
        rootAware: true,
        duplicateHashGroups,
        coverageRows,
        coveredRows,
        missingRows,
        totalMainTransactions: coverageRows.length,
        totalRows: coverageRows.length,
        coveredCount: coveredRows.length,
        missingCount: missingRows.length,
        summary: `${coveredRows.length}/${coverageRows.length} starting root${coverageRows.length === 1 ? '' : 's'} have distinct raw-data coverage. Duplicate transaction hashes require path-label or selected-To-address matches.`
      };
    },

    getCaseWorkflowRawDataCoverageSummary() {
      try {
        const mainRows = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('main') : [];
        const rawRows = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('raw') : [];
        const coverage = this.buildCaseBuddyMainRawDataCoverage(Array.isArray(mainRows) ? mainRows : [], Array.isArray(rawRows) ? rawRows : []);
        const coverageRows = Array.isArray(coverage?.coverageRows) ? coverage.coverageRows : [];
        const missingRows = coverageRows.filter(row => normalizeCell(row?.status).toLowerCase() !== 'covered');
        const coveredRows = coverageRows.filter(row => normalizeCell(row?.status).toLowerCase() === 'covered');
        return {
          coverage,
          coverageRows,
          coveredRows,
          missingRows,
          totalRows: coverageRows.length,
          coveredCount: coveredRows.length,
          missingCount: missingRows.length,
          isComplete: coverageRows.length > 0 && missingRows.length === 0
        };
      } catch (error) {
        return { coverage: null, coverageRows: [], coveredRows: [], missingRows: [], totalRows: 0, coveredCount: 0, missingCount: 0, isComplete: false, error };
      }
    },

    getCaseWorkflowRawDataFetchSignalCode(step = {}) {
      return normalizeCell(step?.signalCode || step?.signal || step?.completionSignal || step?.completionSignalCode || 'RAW_DATA_FETCH');
    },

    markCaseWorkflowRawDataCoverageCurrent(sourceActionId = 'workflow-current-raw-data-coverage', options = {}) {
      const summary = this.getCaseWorkflowRawDataCoverageSummary();
      if (!summary.isComplete) return false;
      const targetStep = options.step || this.getCaseWorkflowRawDataFetchStep?.() || this.recommendedWorkflowStep || { tag: 'RAW_DATA_FETCH', title: 'Load raw data for main transactions' };
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const now = this.emptyCaseStarterNudgeNowUtc ? this.emptyCaseStarterNudgeNowUtc() : new Date().toISOString();
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const workflowState = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) },
        toolRuns: Array.isArray((this.caseSupportState.workflowState || {}).toolRuns) ? (this.caseSupportState.workflowState || {}).toolRuns.slice() : []
      };
      const signalCode = this.getCaseWorkflowRawDataFetchSignalCode(targetStep);
      if (signalCode) {
        workflowState.signalCompleteByCode[signalCode] = {
          ...(workflowState.signalCompleteByCode[signalCode] || {}),
          completedAtUtc: workflowState.signalCompleteByCode[signalCode]?.completedAtUtc || now,
          checkedAtUtc: now,
          appliedAtUtc: workflowState.signalCompleteByCode[signalCode]?.appliedAtUtc || now,
          source: sourceActionId || 'workflow-current-raw-data-coverage',
          stepTag: normalizeCell(targetStep?.tag || 'RAW_DATA_FETCH'),
          coverageRows: summary.totalRows,
          coveredRows: summary.coveredCount
        };
      }
      this.caseSupportState.workflowState = workflowState;
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        raw_data_fetch: {
          ...(this.caseSupportState.checkResultsById?.raw_data_fetch || {}),
          id: 'raw_data_fetch',
          status: 'pass',
          message: `${summary.coveredCount}/${summary.totalRows} main transaction raw-data target${summary.totalRows === 1 ? '' : 's'} covered in Raw Data.`,
          details: {
            ...((this.caseSupportState.checkResultsById?.raw_data_fetch || {}).details || {}),
            source: sourceActionId || 'workflow-current-raw-data-coverage',
            stepTag: normalizeCell(targetStep?.tag || 'RAW_DATA_FETCH'),
            totalRows: summary.totalRows,
            coveredRows: summary.coveredCount,
            missingRows: 0,
            derivedFromCurrentRows: true
          },
          revision: currentRevision,
          checkedAtUtc: now,
          derivedFromCurrentRows: true
        }
      };
      if (options.persist !== false) {
        if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
        else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      }
      return true;
    },

    applyRawDataFetchWorkflowStepCurrentCoverage(view = {}, definition = {}) {
      const summary = this.getCaseWorkflowRawDataCoverageSummary();
      if (!summary.isComplete) return view;
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const existingCheck = this.caseSupportState?.checkResultsById?.raw_data_fetch || null;
      const hasSignal = this.workflowStepHasRecordedSignal(view) || this.workflowStepHasRecordedSignal(definition);
      const sources = {
        ...(view.sources || {}),
        check: true,
        signal: !!((view.sources || {}).signal || hasSignal),
        manual: !!(view.sources || {}).manual
      };
      const checkedAtUtc = existingCheck?.checkedAtUtc || (this.emptyCaseStarterNudgeNowUtc ? this.emptyCaseStarterNudgeNowUtc() : new Date().toISOString());
      const checkResult = {
        ...(existingCheck && typeof existingCheck === 'object' ? existingCheck : {}),
        id: 'raw_data_fetch',
        status: 'pass',
        message: `${summary.coveredCount}/${summary.totalRows} main transaction raw-data target${summary.totalRows === 1 ? '' : 's'} covered in Raw Data.`,
        details: {
          ...((existingCheck && typeof existingCheck === 'object' ? existingCheck.details : {}) || {}),
          source: 'workflow-current-raw-data-coverage',
          totalRows: summary.totalRows,
          coveredRows: summary.coveredCount,
          missingRows: 0,
          derivedFromCurrentRows: true
        },
        revision: currentRevision,
        checkedAtUtc,
        derivedFromCurrentRows: true
      };
      return {
        ...view,
        complete: true,
        stale: false,
        blocked: false,
        status: 'complete',
        statusLabel: `Complete · ${summary.coveredCount}/${summary.totalRows} covered`,
        statusClass: 'workflow-step-card--complete',
        staleReason: '',
        sources,
        checkResult,
        completionSummary: `${summary.coveredCount}/${summary.totalRows} main transaction raw-data target${summary.totalRows === 1 ? '' : 's'} are covered in current Raw Data.`
      };
    },

    caseWorkflowAllStepsComplete() {
      const total = Number(this.workflowProgress?.total) || 0;
      const completed = Number(this.workflowProgress?.completed) || 0;
      return total > 0 && completed >= total && !this.recommendedWorkflowStep;
    },

    caseWorkflowRawDataStepIsCurrent(step = null) {
      const target = step || this.recommendedWorkflowStep || this.caseAutomation?.lastPlan || this.caseAutomation?.manualPause || null;
      if (!target) return false;
      const normalizedStep = {
        ...target,
        tag: target.tag || target.stepTag || target.workflowStepTag || target.id || ''
      };
      return this.isCaseWorkflowRawDataFetchStep(normalizedStep);
    },

    stopCaseWorkflowRawDataTool88AfterWorkflowComplete(reason = 'Workflow complete; stopping hidden raw-data solver.') {
      const tool88 = this.$refs?.caseWorkflowRawDataTool88;
      if (tool88 && typeof tool88.caseBuddyRequestAutomationStop === 'function') {
        try {
          tool88.caseBuddyRequestAutomationStop(reason);
        } catch (error) {
          console.warn('Case Buddy could not stop hidden Tool 88 after workflow completion.', error);
        }
      }
      this.caseWorkflowRawDataTool88Running = false;
      this.caseWorkflowTool88RawRunning = false;
      this.caseWorkflowRawDataTool88HostMounted = false;
      this.caseAutomationTool88Activity = {
        ...(this.caseAutomationTool88Activity || {}),
        active: false,
        updatedAtUtc: new Date().toISOString(),
        completedAtUtc: this.caseAutomationTool88Activity?.completedAtUtc || new Date().toISOString()
      };
      this.caseAutomationTool88Progress = {
        ...(this.caseAutomationTool88Progress || {}),
        active: false,
        updatedAtUtc: new Date().toISOString()
      };
    },

    caseWorkflowFinalizeAutomationIfComplete(reason = 'Workflow Auto completed all steps.') {
      if (this.caseWorkflowAssistantScopedAutomationBoundaryActive?.()) return false;
      if (!this.caseWorkflowAllStepsComplete()) return false;
      if (this.caseAutomation?.skipResolvableReview) {
        this.refreshCaseWorkflowStatefulGeneratedReviewRecordsForCurrentRevision?.(this.caseWorkflowStatefulGeneratedReviewChainTags?.() || [], {
          source: 'caseWorkflowFinalizeAutomationIfComplete'
        });
        const missingGeneratedTag = this.caseWorkflowFirstMissingStatefulGeneratedReviewTag?.() || '';
        if (missingGeneratedTag) {
          this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
            tag: missingGeneratedTag,
            stage: 'workflow-complete-blocked-generated-not-current',
            source: 'caseWorkflowFinalizeAutomationIfComplete',
            message: 'Workflow completion was blocked because a generated deliverable is not live-current.',
            state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(this.caseWorkflowStatefulGeneratedReviewReadState?.(missingGeneratedTag) || {}) || null
          });
          return false;
        }
        const packageState = this.readCaseWorkflowFullReportPackageStateForAudit?.() || {};
        const packageVisualsCanBuild = Boolean(packageState.visualsCanBuild === true || this.caseFlowVisualsStateView?.canBuild === true);
        const packageVisualsNeedReview = Boolean(
          packageVisualsCanBuild
          && packageState.visualsReviewIsCurrent !== true
          && this.caseFlowVisualsStateView?.reviewIsCurrent !== true
        );
        const packageStateAvailable = Boolean(packageState && Object.keys(packageState).length);
        const packagePrerequisiteBlocked = Boolean(
          packageStateAvailable
          && (packageState.isReady === false || packageState.addendumReviewIsCurrent === false || packageState.reportBodyReviewIsCurrent === false)
        );
        if (packagePrerequisiteBlocked || packageVisualsNeedReview) {
          this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
            tag: 'CASE_COMPLETION_SUMMARY',
            stage: 'workflow-complete-blocked-full-package-not-ready',
            source: 'caseWorkflowFinalizeAutomationIfComplete',
            message: 'Workflow completion was blocked because the final report package is not live-ready.',
            state: this.sanitizeCaseWorkflowFullReportPackageStateForAudit?.(packageState) || null
          });
          return false;
        }
      }
      this.clearCaseWorkflowRawDataTool88AutoContinueTimer?.();
      this.clearCaseAutomationTimer?.();
      this.stopCaseWorkflowRawDataTool88AfterWorkflowComplete('Workflow complete; stopping hidden raw-data solver.');
      this.showCaseAutomationPauseModal = false;
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        active: false,
        busy: false,
        paused: false,
        manualPause: null,
        status: 'Workflow complete.',
        detail: reason || 'All workflow steps are complete.'
      };
      this.showCaseWorkflowCompletionReviewModal(reason);
      return true;
    },

    showCaseWorkflowCompletionReviewModal(reason = 'All workflow steps are complete.') {
      this.caseWorkflowCompletionMessage = normalizeCell(reason) || 'All workflow steps are complete. The one-click report package is ready to review, copy, or download.';
      this.caseSimpleLaunchStatus = 'Workflow complete. Review the final one-click report package.';
      if (this.caseQuickLookupOwnsWorkflowCompletion) {
        this.showCaseWorkflowCompletionModal = false;
        this.caseQuickLookupSetState?.({
          status: 'report-ready',
          progress: 100,
          reportRequested: false,
          lastResult: 'report-ready',
          message: 'The case workflow and one-click report package are ready to review.',
          detail: ''
        });
        this.clearCaseQuickLookupReportWatchTimer?.();
        return true;
      }
      this.showCaseWorkflowCompletionModal = true;
      return true;
    },

    closeCaseWorkflowCompletionModal() {
      this.showCaseWorkflowCompletionModal = false;
    },

    async openCaseWorkflowCompletionReportPackage(options = {}) {
      this.showCaseWorkflowCompletionModal = false;
      if (options.ensureReady !== false && this.caseAutomation?.skipResolvableReview && typeof this.ensureCaseWorkflowFullReportPackageReadyForSkipMode === 'function') {
        await this.ensureCaseWorkflowFullReportPackageReadyForSkipMode({
          source: normalizeCell(options.source || 'workflow-completion-open-package'),
          suppressUi: false,
          force: true
        });
      }

      // Use the same package-opening path as the Deliverables panel's working
      // “One-click package preview” control. The Simplified Mode helper can
      // decline to open after a late workflow revision, which previously left
      // the completion modal closed with no report visible.
      let result;
      if (typeof this.buildOneClickPackagePreview === 'function') {
        result = this.buildOneClickPackagePreview();
        if (result && typeof result.then === 'function') result = await result;
      }
      if (!this.showCaseFullReportPackageModal) {
        this.showCaseFullReportPackageModal = true;
        if (typeof this.refreshCaseFullReportPackageReview === 'function') {
          const refreshResult = this.refreshCaseFullReportPackageReview();
          if (refreshResult && typeof refreshResult.then === 'function') await refreshResult;
        }
      }
      this.sanitizeCaseFullReportPackagePayload?.({ source: normalizeCell(options.source || 'workflow-completion-open-package') });
      return result === undefined ? true : result;
    },

    async openCaseWorkflowAssistantFinalReport() {
      if (!this.caseWorkflowAssistantFinalReportCanOpen) {
        this.showCaseToast?.('Another Case Buddy operation is running. Open the final report when it finishes.');
        return false;
      }
      return this.openCaseWorkflowCompletionReportPackage({
        source: 'workflow-assistant-final-report',
        ensureReady: Boolean(this.caseWorkflowAssistantOverallWorkflowComplete)
      });
    },

    async copyCaseWorkflowCompletionRichPackage() {
      this.showCaseWorkflowCompletionModal = false;
      if (this.caseAutomation?.skipResolvableReview && typeof this.ensureCaseWorkflowFullReportPackageReadyForSkipMode === 'function') {
        await this.ensureCaseWorkflowFullReportPackageReadyForSkipMode({
          source: 'workflow-completion-copy-rich-package',
          suppressUi: true,
          force: true
        });
      }
      if (typeof this.copyCaseFullReportPackageRich === 'function') {
        const result = this.copyCaseFullReportPackageRich();
        if (result && typeof result.then === 'function') await result;
        return result;
      }
      return this.openCaseWorkflowCompletionReportPackage();
    },

    caseWorkflowShouldUseTool88RawData(step = null, options = {}) {
      if (!this.caseAutomationPreferHopSolverRawData && !options?.forceTool88) return false;
      if (this.caseWorkflowAllStepsComplete()) return false;
      const targetStep = step || this.recommendedWorkflowStep || null;
      const explicitRawContext = options?.caseWorkflowRawData === true ||
        options?.caseBuddyRawDataAutomation === true ||
        options?.workflowRawData === true ||
        options?.rawDataAutomation === true ||
        options?.forceTool88 === true;
      if (!explicitRawContext && !this.isCaseWorkflowRawDataFetchStep(targetStep)) return false;
      if (this.caseWorkflowRawDataCoverageIsComplete() && !options?.forceTool88) return false;
      return true;
    },

    caseWorkflowRawDataActionUsesExistingUi(action = {}, step = {}) {
      if (!this.isCaseWorkflowRawDataFetchStep(step)) return false;
      const actionId = normalizeCell(action?.id || action?.key || action?.action).toLowerCase();
      const actionKind = normalizeCell(action?.kind).toLowerCase();
      const actionLabel = normalizeCell(action?.label || action?.title).toLowerCase();
      if (/^(?:use_workspace_raw_import|open_raw_sheet|request_wayfinder_raw_data|request_pathtracer_raw_data)$/.test(actionId)) return true;
      if (/^(?:raw-data-import|sheet|wayfinder-handoff|raw-data-request-placeholder)$/.test(actionKind)) return true;
      return /^(?:paste raw data|open raw|wayfinder handoff|pathtracer request)$/.test(actionLabel);
    },

    caseWorkflowRawDataActionLooksLikeFetch(action = {}, step = {}) {
      if (!this.isCaseWorkflowRawDataFetchStep(step)) return false;
      // These buttons already have purpose-built panels or sheet actions. Let the
      // original workflow router handle them instead of treating every action in
      // the Raw Data step as a request to launch the embedded Flow Solver.
      if (this.caseWorkflowRawDataActionUsesExistingUi(action, step)) return false;
      const haystack = [
        action?.id,
        action?.key,
        action?.label,
        action?.title,
        action?.kind,
        action?.method,
        action?.handler,
        action?.action
      ].map(value => normalizeCell(value).toLowerCase()).join(' ').trim();
      return !haystack || /raw[_\s-]*data|fetch|load|flow[_\s-]*solver|hop[_\s-]*solver|solver|tool\s*88|workspace[_\s-]*raw/.test(haystack);
    },

    caseWorkflowRawDataCoverageIsComplete() {
      try {
        const mainRows = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('main') : [];
        const rawRows = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('raw') : [];
        const coverage = this.buildCaseBuddyMainRawDataCoverage(Array.isArray(mainRows) ? mainRows : [], Array.isArray(rawRows) ? rawRows : []);
        const coverageRows = Array.isArray(coverage?.coverageRows) ? coverage.coverageRows : [];
        return coverageRows.length > 0 && coverageRows.every(row => normalizeCell(row?.status).toLowerCase() === 'covered');
      } catch {
        return false;
      }
    },

    closeCaseWorkflowRawDataVisibleSolverArtifacts(reason = 'workflow raw-data Tool 88 automation') {
      if (this.caseInvestigationVisualizerManualStartMode || this.caseHopSolverManualStartMode) return;
      if (this.showCaseHopSolverModal || this.showCaseHopSolverPlaceholderModal) {
        this.caseHopSolverLastEvent = reason;
      }
      this.showCaseHopSolverModal = false;
      this.showCaseHopSolverPlaceholderModal = false;
      this.caseHopSolverPlaceholderRunning = false;
      this.caseHopSolverStopRequested = false;
      this.caseHopSolverPlaceholderStopRequested = false;
    },

    suppressCaseWorkflowRawDataLegacyPauseDuringTool88(detail = 'Tool 88 is still resolving raw data for this workflow step.') {
      const pause = this.caseAutomation?.manualPause || null;
      const pauseIsRawData = !pause || normalizeCell(pause.pauseKind).toLowerCase() === 'raw-data-fetch' || this.isCaseWorkflowRawDataFetchStep(pause);
      if (!pauseIsRawData) return false;
      const running = this.caseWorkflowRawDataTool88Running || this.caseWorkflowTool88RawRunning;
      const hasRows = Number(this.caseAutomationTool88Activity?.rowsAppended || 0) > 0
        || (Array.isArray(this.caseWorkflowRawDataTool88LastRows) && this.caseWorkflowRawDataTool88LastRows.some(row => rowHasMeaningfulData(row)));
      if (!running && !hasRows) return false;
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        active: running ? true : Boolean(this.caseAutomation?.active),
        paused: false,
        busy: running ? true : Boolean(this.caseAutomation?.busy),
        manualPause: null,
        status: running ? 'Solving raw data with Tool 88…' : 'Tool 88 raw data loaded',
        detail
      };
      this.closeCaseWorkflowRawDataVisibleSolverArtifacts(detail);
      return true;
    },

    async waitForCaseWorkflowRawDataTool88Run(options = {}) {
      const timeoutMs = Math.max(1000, Math.min(15 * 60 * 1000, Number(options.timeoutMs) || 15 * 60 * 1000));
      const startedAt = Date.now();
      while (this.caseWorkflowRawDataTool88Running || this.caseWorkflowTool88RawRunning) {
        this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88('Tool 88 is still resolving raw data; keeping workflow Auto attached to the live solver.');
        await new Promise(resolve => setTimeout(resolve, 250));
        if (Date.now() - startedAt > timeoutMs) {
          return { handled: true, pending: true, success: false, status: 'tool88-running-timeout', message: 'Tool 88 raw-data automation is still running.' };
        }
      }
      const rowsAppended = Number(this.caseAutomationTool88Activity?.rowsAppended || 0)
        || (Array.isArray(this.caseWorkflowRawDataTool88LastRows) ? this.caseWorkflowRawDataTool88LastRows.filter(row => rowHasMeaningfulData(row)).length : 0);
      const coverage = this.getCaseWorkflowRawDataCoverageSummary();
      const coverageComplete = coverage?.isComplete === true;
      const lastSnapshot = this.caseWorkflowRawDataTool88LastSnapshot || this.caseWorkflowTool88RawSnapshot || null;
      const passFlowComplete = this.caseAutomationTool88Activity?.flowComplete === true || lastSnapshot?.flowComplete === true || lastSnapshot?.flowCompletion?.done === true;
      const missingCount = Number(coverage?.missingCount || 0);
      const missingRoots = (Array.isArray(coverage?.missingRows) ? coverage.missingRows : []).slice(0, 12).map(row => ({
        rootInstanceKey: row?.rootInstanceKey || '',
        pathLabel: row?.pathLabel || '',
        hash: row?.hash || row?.transactionHash || '',
        toAddress: row?.toAddress || ''
      }));
      if (coverageComplete) {
        this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88('Distinct raw-data coverage is complete for every starting root.');
        return {
          handled: true,
          success: true,
          status: 'success',
          rowsAppended,
          flowComplete: true,
          passFlowComplete,
          coverage,
          source: 'tool88_raw_data_solver_wait'
        };
      }
      if (rowsAppended > 0) {
        const message = lastSnapshot?.message || `${missingCount} distinct starting root${missingCount === 1 ? '' : 's'} still need raw data. A completed Tool 88 pass or accepted bridge for another root does not cover them.`;
        return {
          handled: true,
          success: false,
          paused: true,
          status: 'tool88-root-coverage-incomplete',
          rowsAppended,
          flowComplete: false,
          passFlowComplete,
          missingCount,
          missingRoots,
          coverage,
          source: 'tool88_raw_data_solver_wait',
          message
        };
      }
      return {
        handled: true,
        success: false,
        status: 'tool88-finished-without-coverage',
        missingCount,
        missingRoots,
        coverage,
        message: this.caseWorkflowRawDataTool88LastError || 'Tool 88 finished without completing distinct starting-root coverage.'
      };
    },

    getCaseWorkflowRawTool88Ref() {
      const ref = this.$refs.caseWorkflowRawTool88;
      return Array.isArray(ref) ? ref[0] : ref;
    },

    async ensureCaseWorkflowRawTool88Ref() {
      this.caseWorkflowTool88RawSolverMounted = true;
      await this.$nextTick();
      return this.getCaseWorkflowRawTool88Ref();
    },

    buildCaseWorkflowRawTool88RowsFromMain(options = {}) {
      const rows = this.getCaseWorkflowRawDataTool88InputRows({
        missingOnly: options.missingOnly !== false
      });
      return Array.isArray(rows) ? rows : [];
    },

    normalizeCaseWorkflowRawTool88Rows(rows = []) {
      return (Array.isArray(rows) ? rows : [])
        .map(row => this.caseManualCanonicalizeCaseSheetRow(
          this.caseManualMoveTool88OperationalNotesToColumnW(
            this.caseManualMigrateLegacyColumnPNotes(
              this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))
            )
          )
        ))
        .filter((row, index) => {
          if (index === 0 && this.caseManualIsHeaderRow(row)) return false;
          return this.caseManualRowHasEntryData(row) || row.some(value => this.caseManualNormalizeText(value));
        });
    },

    caseWorkflowExtractTool88RawOutputRows(snapshot = {}, tool = null) {
      const candidateLists = [
        snapshot?.outputRows,
        snapshot?.result?.outputRows,
        snapshot?.rows,
        snapshot?.result?.rows,
        tool?.getCurrentOutputRowsForExport?.(),
        tool?.outputSnapshotRows
      ];
      for (const list of candidateLists) {
        const rows = this.normalizeCaseWorkflowRawTool88Rows(Array.isArray(list) ? list : []);
        if (rows.length) return rows;
      }
      return [];
    },

    mergeCaseWorkflowRawRows(existingRows = [], newRows = []) {
      const rawRows = (Array.isArray(existingRows) ? existingRows : [])
        .map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)));
      let rows = rawRows.length && this.caseManualIsHeaderRow(rawRows[0])
        ? rawRows.slice()
        : [CASE_MANUAL_START_CANONICAL_HEADERS.slice(), ...rawRows];
      if (rows.length && this.caseManualIsHeaderRow(rows[0])) rows[0] = CASE_MANUAL_START_CANONICAL_HEADERS.slice();

      const cleanRows = this.normalizeCaseWorkflowRawTool88Rows(newRows);
      if (!cleanRows.length) return { rows, changed: false, appendedCount: 0, replacedCount: 0 };

      const mainMatrix = typeof this.getMatrixForLogicalSheet === 'function'
        ? this.getMatrixForLogicalSheet('main')
        : [];
      const mainRoots = this.caseWorkflowCollectRootTransactions(Array.isArray(mainMatrix) ? mainMatrix : []);
      const mainHashCounts = new Map();
      mainRoots.forEach((root) => {
        if (!root?.hashKey) return;
        mainHashCounts.set(root.hashKey, (mainHashCounts.get(root.hashKey) || 0) + 1);
      });
      const duplicateMainHashKeys = new Set(
        Array.from(mainHashCounts.entries())
          .filter(([, count]) => count > 1)
          .map(([hashKey]) => hashKey)
      );

      const isRootRow = (row = []) => {
        const hash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]);
        if (!hash) return false;
        const fromHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_HASH]);
        if (fromHash) return false;
        const pathLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]);
        return !pathLabel || this.caseManualStartPathLabelLooksInitialFlowRoot(pathLabel);
      };
      const splitIntoRootSets = (sourceRows = []) => {
        const starts = [];
        sourceRows.forEach((row, index) => {
          if (isRootRow(row)) starts.push(index);
        });
        if (!starts.length) return [{ rootIndex: 0, rows: sourceRows.slice() }];
        return starts.map((rootIndex, index) => ({
          rootIndex,
          rows: sourceRows.slice(rootIndex, starts[index + 1] ?? sourceRows.length)
        }));
      };
      const rootIdentity = (row = []) => ({
        hashKey: this.caseWorkflowRawToMainHashKey(row?.[CASE_MANUAL_START_COL.TX_HASH]),
        pathKey: this.caseWorkflowRootIdentityKey(row?.[CASE_MANUAL_START_COL.PATH_LABEL]),
        targetKey: this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.TO_ADDRESS])
      });
      const findExistingRootIndex = (incomingRoot = []) => {
        const incoming = rootIdentity(incomingRoot);
        const candidates = rows
          .map((row, index) => ({ row, index, identity: rootIdentity(row) }))
          .filter(({ row, index, identity }) => index > 0 && isRootRow(row) && identity.hashKey === incoming.hashKey);
        if (!candidates.length) return -1;
        const ranked = candidates
          .map(candidate => {
            const pathMatches = !!incoming.pathKey && candidate.identity.pathKey === incoming.pathKey;
            const targetMatches = !!incoming.targetKey && candidate.identity.targetKey === incoming.targetKey;
            const conflictingPath = !!incoming.pathKey && !!candidate.identity.pathKey && !pathMatches;
            const conflictingTarget = !!incoming.targetKey && !!candidate.identity.targetKey && !targetMatches;
            let score = 0;
            if (targetMatches) score += 1000;
            if (pathMatches) score += 500;
            if (conflictingTarget || (!targetMatches && conflictingPath)) score = -1;
            return { ...candidate, score };
          })
          .filter(candidate => candidate.score >= 500 || (!!incoming.targetKey && candidate.identity.targetKey === incoming.targetKey))
          .sort((left, right) => right.score - left.score || right.index - left.index);
        if (ranked.length) return ranked[0].index;
        // Hash-only replacement is unsafe when Main Data intentionally contains
        // more than one root for this transaction hash. In that case the new
        // path/Column D instance must append as its own raw set.
        if (!duplicateMainHashKeys.has(incoming.hashKey) && candidates.length === 1) return candidates[0].index;
        return -1;
      };
      const findExistingRootBlockEnd = (startIndex) => {
        for (let index = startIndex + 1; index < rows.length; index += 1) {
          if (isRootRow(rows[index])) return index;
        }
        return rows.length;
      };

      let changed = false;
      let appendedCount = 0;
      let replacedCount = 0;
      splitIntoRootSets(cleanRows).forEach((incomingSet) => {
        if (!incomingSet.rows.length) return;
        const existingIndex = findExistingRootIndex(incomingSet.rows[0]);
        if (existingIndex >= 0) {
          const end = findExistingRootBlockEnd(existingIndex);
          const deleteCount = Math.max(1, end - existingIndex);
          const existingSlice = rows.slice(existingIndex, end);
          const materiallySame = typeof this.caseManualRowsMateriallySame === 'function'
            ? this.caseManualRowsMateriallySame(existingSlice, incomingSet.rows)
            : JSON.stringify(existingSlice) === JSON.stringify(incomingSet.rows);
          if (!materiallySame) {
            rows.splice(existingIndex, deleteCount, ...incomingSet.rows.map(row => row.slice()));
            changed = true;
            replacedCount += deleteCount;
          }
        } else {
          rows.push(...incomingSet.rows.map(row => row.slice()));
          changed = true;
          appendedCount += incomingSet.rows.length;
        }
      });
      return { rows, changed, appendedCount, replacedCount };
    },

    async appendCaseWorkflowTool88RawRowsToRawData(rows = []) {
      const existing = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('raw') : [];
      const merged = this.mergeCaseWorkflowRawRows(existing, rows);
      if (!merged.changed) return merged;
      await this.replaceCaseManualStartLogicalSheetMatrix('raw', merged.rows, 'Updated Raw Data from Tool 88 workflow raw-data solver.', { autosaveDelayMs: 900 });
      if (typeof this.refreshRawDataImportCoverage === 'function') this.refreshRawDataImportCoverage();
      if (typeof this.refreshCaseSummary === 'function') this.refreshCaseSummary();
      return merged;
    },

    normalizeCaseWorkflowRawTool88Snapshot(payload = {}) {
      return payload?.detail && typeof payload.detail === 'object' ? payload.detail : (payload || {});
    },

    pushCaseWorkflowRawTool88Log(message = '', options = {}) {
      const cleanMessage = normalizeCell(message);
      if (!cleanMessage) return;
      const log = Array.isArray(this.caseAutomationTool88Activity?.log) ? this.caseAutomationTool88Activity.log.slice() : [];
      const key = options.key || `${options.eventName || 'tool88'}-${cleanMessage}`.toLowerCase();
      if (this.caseAutomationTool88Activity?.lastLogKey === key || log.some(entry => entry.key === key)) return;
      log.push({
        id: `${Date.now()}-${log.length}`,
        key,
        message: cleanMessage,
        utc: new Date().toISOString()
      });
      this.caseAutomationTool88Activity = {
        ...(this.caseAutomationTool88Activity || {}),
        lastLogKey: key,
        log: log.slice(-8)
      };
    },

    updateCaseWorkflowRawTool88Activity(eventName = 'case-buddy-status', payload = {}, options = {}) {
      const snapshot = this.normalizeCaseWorkflowRawTool88Snapshot(payload);
      const selectedEntry = snapshot?.selectedEntry || {};
      const flow = snapshot?.flowCompletion || {};
      const runState = snapshot?.runState || {};
      const outputRows = Array.isArray(snapshot?.outputRows) ? snapshot.outputRows.length : Number(snapshot?.outputRows || 0) || 0;
      const now = new Date().toISOString();
      const message = normalizeCell(snapshot?.message || options.message || snapshot?.status || 'Tool 88 raw-data solver updated.');
      this.caseWorkflowTool88RawSnapshot = snapshot;
      this.caseWorkflowTool88RawLastEvent = eventName;
      this.caseWorkflowTool88RawStatus = message;
      this.caseAutomationTool88Activity = {
        ...(this.caseAutomationTool88Activity || {}),
        active: options.active ?? this.caseWorkflowTool88RawRunning,
        startedAtUtc: this.caseAutomationTool88Activity?.startedAtUtc || now,
        updatedAtUtc: now,
        completedAtUtc: options.completed ? now : (this.caseAutomationTool88Activity?.completedAtUtc || ''),
        status: normalizeCell(snapshot?.status || options.status || eventName),
        title: 'Tool 88 raw-data solver',
        message,
        detail: message,
        eventName,
        integrationAction: normalizeCell(snapshot?.integrationAction || options.integrationAction || ''),
        stepCount: Number(runState.stepCount ?? this.caseAutomationTool88Activity?.stepCount ?? 0) || 0,
        researchedCount: Array.isArray(runState.researchedEntryKeys) ? runState.researchedEntryKeys.length : (Number(this.caseAutomationTool88Activity?.researchedCount) || 0),
        entryCount: Number(flow.totalEntries ?? this.caseAutomationTool88Activity?.entryCount ?? 0) || 0,
        endingCount: Number(flow.endingCount ?? this.caseAutomationTool88Activity?.endingCount ?? 0) || 0,
        completeCount: Number(flow.completeCount ?? this.caseAutomationTool88Activity?.completeCount ?? 0) || 0,
        unresolvedCount: Number(flow.incompleteCount ?? this.caseAutomationTool88Activity?.unresolvedCount ?? 0) || 0,
        flowComplete: snapshot?.flowComplete === true || flow.done === true,
        rawRows: outputRows || Number(this.caseAutomationTool88Activity?.rawRows) || 0,
        targetLabel: normalizeCell(selectedEntry.pathLabel || this.caseAutomationTool88Activity?.targetLabel || ''),
        targetHash: normalizeCell(selectedEntry.txHash || selectedEntry.hash || this.caseAutomationTool88Activity?.targetHash || ''),
        targetAddress: normalizeCell(snapshot?.selectedTargetAddress || this.caseAutomationTool88Activity?.targetAddress || ''),
        guardrailMessage: normalizeCell(snapshot?.guardrail?.message || snapshot?.guardrail?.reason || ''),
        log: Array.isArray(this.caseAutomationTool88Activity?.log) ? this.caseAutomationTool88Activity.log : []
      };
      this.caseAutomationTool88Progress = {
        ...(this.caseAutomationTool88Progress || {}),
        active: options.active ?? this.caseWorkflowTool88RawRunning,
        title: 'Tool 88 raw-data solver',
        detail: message,
        status: normalizeCell(snapshot?.status || eventName),
        action: normalizeCell(snapshot?.integrationAction || ''),
        stepCount: Number(runState.stepCount ?? this.caseAutomationTool88Progress?.stepCount ?? 0) || 0,
        researchedCount: Array.isArray(runState.researchedEntryKeys) ? runState.researchedEntryKeys.length : (Number(this.caseAutomationTool88Progress?.researchedCount) || 0),
        outputRows,
        totalEntries: Number(flow.totalEntries ?? this.caseAutomationTool88Progress?.totalEntries ?? 0) || 0,
        endingCount: Number(flow.endingCount ?? this.caseAutomationTool88Progress?.endingCount ?? 0) || 0,
        completeEndings: Number(flow.completeCount ?? this.caseAutomationTool88Progress?.completeEndings ?? 0) || 0,
        openEndings: Number(flow.incompleteCount ?? this.caseAutomationTool88Progress?.openEndings ?? 0) || 0,
        selectedLabel: normalizeCell(selectedEntry.pathLabel || ''),
        selectedHash: normalizeCell(selectedEntry.txHash || selectedEntry.hash || ''),
        selectedTarget: normalizeCell(snapshot?.selectedTargetAddress || ''),
        updatedAtUtc: now,
        events: Array.isArray(this.caseAutomationTool88Progress?.events) ? this.caseAutomationTool88Progress.events : []
      };
      this.pushCaseWorkflowRawTool88Log(message, { eventName });
    },

    handleCaseWorkflowRawTool88Status(payload) {
      this.updateCaseWorkflowRawTool88Activity('case-buddy-status', payload);
    },

    handleCaseWorkflowRawTool88Progress(payload) {
      this.updateCaseWorkflowRawTool88Activity('case-buddy-progress', payload);
    },

    handleCaseWorkflowRawTool88Result(payload) {
      const snapshot = this.normalizeCaseWorkflowRawTool88Snapshot(payload);
      const rows = this.normalizeCaseWorkflowRawTool88Rows(snapshot?.outputRows || []);
      if (rows.length) this.caseWorkflowTool88RawOutputRows = rows;
      this.updateCaseWorkflowRawTool88Activity('case-buddy-result', payload);
    },

    handleCaseWorkflowRawTool88Guardrail(payload) {
      const snapshot = this.normalizeCaseWorkflowRawTool88Snapshot(payload);
      const rows = this.normalizeCaseWorkflowRawTool88Rows(snapshot?.outputRows || []);
      if (rows.length) this.caseWorkflowTool88RawOutputRows = rows;
      this.updateCaseWorkflowRawTool88Activity('case-buddy-guardrail', payload);
    },

    handleCaseWorkflowRawTool88FlowComplete(payload) {
      const snapshot = this.normalizeCaseWorkflowRawTool88Snapshot(payload);
      const rows = this.normalizeCaseWorkflowRawTool88Rows(snapshot?.outputRows || []);
      if (rows.length) this.caseWorkflowTool88RawOutputRows = rows;
      this.updateCaseWorkflowRawTool88Activity('case-buddy-flow-complete', payload, { completed: true, active: false });
    },

    recordCaseWorkflowTool88RawDataCompletion(result = {}, step = null) {
      const coverage = this.getCaseWorkflowRawDataCoverageSummary();
      if (!coverage.isComplete) {
        this.pushCaseWorkflowRawTool88Log?.(
          `Tool 88 completion was not recorded because ${coverage.missingCount} distinct starting root${coverage.missingCount === 1 ? '' : 's'} still need raw data.`,
          { eventName: 'case-buddy-root-coverage-incomplete' }
        );
        return false;
      }
      const now = new Date().toISOString();
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) },
        toolRuns: Array.isArray((this.caseSupportState.workflowState || {}).toolRuns) ? (this.caseSupportState.workflowState || {}).toolRuns.slice() : []
      };
      const targetStep = step || this.getCaseWorkflowRawDataFetchStep() || { tag: 'RAW_DATA_FETCH', title: 'Load raw data for main transactions' };
      const signalCode = normalizeCell(targetStep.signalCode || targetStep.signal || targetStep.completionSignal || targetStep.completionSignalCode || 'RAW_DATA_FETCH');
      if (signalCode) {
        if (typeof this.recordWorkflowSignal === 'function') {
          try {
            this.recordWorkflowSignal(signalCode, 'tool88_raw_data_solver', { silent: true });
          } catch (error) {
            console.warn('Could not record Tool 88 raw-data workflow signal through the workflow helper.', error);
          }
        }
        workflow.signalCompleteByCode[signalCode] = {
          ...(workflow.signalCompleteByCode[signalCode] || {}),
          completedAtUtc: now,
          appliedAtUtc: now,
          source: 'tool88_raw_data_solver',
          stepTag: normalizeCell(targetStep.tag || 'RAW_DATA_FETCH'),
          rowsAppended: Number(result.appendedCount || 0),
          rowsReplaced: Number(result.replacedCount || 0),
          outputRows: Number(result.outputRowCount || 0)
        };
      }
      workflow.toolRuns.push({
        id: `tool88-raw-data-${Date.now()}`,
        tool: 'Tool 88',
        source: 'tool88_raw_data_solver',
        stepTag: normalizeCell(targetStep.tag || 'RAW_DATA_FETCH'),
        completedAtUtc: now,
        rowsAppended: Number(result.appendedCount || 0),
        rowsReplaced: Number(result.replacedCount || 0),
        outputRows: Number(result.outputRowCount || 0),
        flowComplete: result.flowComplete === true
      });
      workflow.toolRuns = workflow.toolRuns.slice(-60);
      this.caseSupportState.workflowState = workflow;
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        raw_data_fetch: {
          id: 'raw_data_fetch',
          status: 'pass',
          message: `Tool 88 appended ${Number(result.appendedCount || result.outputRowCount || 0)} raw-data row${Number(result.appendedCount || result.outputRowCount || 0) === 1 ? '' : 's'} for main transaction coverage.`,
          details: {
            source: 'tool88_raw_data_solver',
            stepTag: normalizeCell(targetStep.tag || 'RAW_DATA_FETCH'),
            rowsAppended: Number(result.appendedCount || 0),
            rowsReplaced: Number(result.replacedCount || 0),
            outputRows: Number(result.outputRowCount || 0),
            flowComplete: result.flowComplete === true
          },
          revision: Number(this.caseSupportState.caseDataRevision) || 0,
          checkedAtUtc: now
        }
      };
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      return true;
    },

    async automationFetchRawDataViaConfiguredSource(...args) {
      const options = args[0] || {};
      const optionObject = options && typeof options === 'object' && !Array.isArray(options) ? options : {};
      const step = optionObject.step || optionObject.workflowStep || this.getCaseWorkflowRawDataFetchStep?.() || this.recommendedWorkflowStep;
      const shouldPreferTool88 = this.caseAutomationPreferHopSolverRawData && this.isCaseWorkflowRawDataFetchStep(step);
      if (shouldPreferTool88) {
        if (this.caseWorkflowRawDataCoverageIsComplete()) {
          this.markCaseWorkflowRawDataCoverageCurrent('workflow-current-raw-data-coverage', { step, persist: true });
          this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88('Raw-data coverage is already complete; continuing workflow without WayFinder fallback.');
          return {
            handled: true,
            success: true,
            status: 'success',
            source: 'tool88_raw_data_coverage',
            message: 'Raw-data coverage is complete.'
          };
        }
        if (this.caseWorkflowRawDataTool88Running || this.caseWorkflowTool88RawRunning) {
          this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88('Tool 88 is still resolving raw data; keeping workflow Auto on the live solver instead of opening WayFinder fallback.');
          return this.waitForCaseWorkflowRawDataTool88Run({ source: 'automationFetchRawDataViaConfiguredSource' });
        }
        this.closeCaseWorkflowRawDataVisibleSolverArtifacts('Starting Tool 88 raw-data automation from workflow Auto.');
        const tool88Result = await this.runCaseWorkflowTool88RawDataSolver({
          ...optionObject,
          forceTool88: true,
          caseWorkflowRawData: true,
          caseBuddyRawDataAutomation: true,
          rawDataAutomation: true,
          workflowStepTag: normalizeCell(step?.tag || 'RAW_DATA_FETCH'),
          stepTag: normalizeCell(step?.tag || 'RAW_DATA_FETCH'),
          stepTitle: step?.title || 'Load raw data for main transactions',
          source: optionObject.source || 'automationFetchRawDataViaConfiguredSource'
        });
        if (tool88Result?.success || tool88Result?.handled) {
          this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88('Tool 88 handled raw-data loading for this workflow step.');
          return {
            ...tool88Result,
            handled: true,
            success: tool88Result?.success !== false,
            status: tool88Result?.success === false ? (tool88Result.status || 'tool88-handled') : 'success',
            source: 'tool88_raw_data_solver'
          };
        }
        if (this.caseWorkflowRawDataCoverageIsComplete()) {
          this.markCaseWorkflowRawDataCoverageCurrent('workflow-current-raw-data-coverage-after-tool88', { step, persist: true });
          this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88('Raw-data coverage is already complete after Tool 88 output was appended.');
          return {
            handled: true,
            success: true,
            status: 'success',
            source: 'tool88_raw_data_solver_coverage',
            message: 'Raw-data coverage is complete.'
          };
        }
      }
      return this.callCaseBuddyOriginalMethod(caseBuddyAutomationMethods, 'automationFetchRawDataViaConfiguredSource', args.length ? args : [options]);
    },

    async runCaseWorkflowRawDataTool88CoveragePasses(options = {}, step = null) {
      let coverage = this.getCaseWorkflowRawDataCoverageSummary();
      const initialMissingCount = Number(coverage?.missingCount || 0);
      const maxPasses = Math.max(1, Math.min(24, Number(options.maxRootCoveragePasses) || initialMissingCount + 2 || 1));
      const aggregateRows = [];
      const passes = [];
      let totalRowsAppended = 0;
      let totalRowsReplaced = 0;
      let unresolvedBridgeAccepted = false;
      let finalSnapshot = null;
      // A timestamp-based "recent stop" window incorrectly cancels a brand-new
      // run when an investigator restarts Auto within 30 seconds. Capture a
      // generation instead: only a stop requested after this run began cancels it.
      const stopTokenAtStart = Number(this.caseWorkflowRawDataTool88StopToken) || 0;
      const stopRequestedDuringRun = () => (Number(this.caseWorkflowRawDataTool88StopToken) || 0) !== stopTokenAtStart;

      const missingIdentitySet = (summary = {}) => new Set(
        (Array.isArray(summary?.missingRows) ? summary.missingRows : [])
          .map(row => row?.rootInstanceKey || row?.id || this.caseWorkflowRootInstanceKey(row))
          .filter(Boolean)
      );

      for (let passNumber = 1; passNumber <= maxPasses && !coverage.isComplete; passNumber += 1) {
        if (stopRequestedDuringRun()) {
          return {
            ...(finalSnapshot || {}),
            status: 'stopped',
            message: 'Tool 88 raw-data coverage passes stopped at the user request.',
            workflowRawDataStopTokenAtStart: stopTokenAtStart,
            workflowRawDataStopTokenCurrent: Number(this.caseWorkflowRawDataTool88StopToken) || 0,
            outputRows: aggregateRows,
            rowsAppended: totalRowsAppended,
            rowsReplaced: totalRowsReplaced,
            flowComplete: false,
            workflowRawDataIncomplete: true,
            workflowRawDataCoveragePasses: passes,
            workflowRawDataCoverage: coverage
          };
        }

        const beforeMissing = missingIdentitySet(coverage);
        const beforeMissingCount = Number(coverage?.missingCount || 0);
        this.caseAutomation = {
          ...(this.caseAutomation || {}),
          active: true,
          busy: true,
          paused: false,
          manualPause: null,
          status: 'Solving raw data with Tool 88…',
          detail: `Tool 88 raw-data pass ${passNumber}: ${beforeMissingCount} distinct starting root${beforeMissingCount === 1 ? '' : 's'} remain.`
        };
        this.pushCaseWorkflowRawTool88Log?.(
          `Starting Tool 88 raw-data pass ${passNumber} for ${beforeMissingCount} uncovered starting root${beforeMissingCount === 1 ? '' : 's'}.`,
          { eventName: 'case-buddy-root-coverage-pass', key: `root-coverage-pass-${passNumber}-${Array.from(beforeMissing).join('|')}` }
        );

        const snapshot = await this.runCaseWorkflowRawDataTool88Headless({
          ...options,
          forceTool88: true,
          workflowRawDataPassNumber: passNumber,
          workflowRawDataMissingOnly: true,
          step: step || options.step || options.workflowStep || null
        });
        finalSnapshot = snapshot || finalSnapshot || {};
        if (stopRequestedDuringRun()) {
          return {
            ...(finalSnapshot || {}),
            status: 'stopped',
            message: 'Tool 88 raw-data coverage passes stopped at the user request.',
            workflowRawDataStopTokenAtStart: stopTokenAtStart,
            workflowRawDataStopTokenCurrent: Number(this.caseWorkflowRawDataTool88StopToken) || 0,
            outputRows: aggregateRows,
            rowsAppended: totalRowsAppended,
            rowsReplaced: totalRowsReplaced,
            flowComplete: false,
            workflowRawDataIncomplete: true,
            workflowRawDataCoveragePasses: passes,
            workflowRawDataCoverage: this.getCaseWorkflowRawDataCoverageSummary()
          };
        }
        const passRows = Array.isArray(snapshot?.outputRows)
          ? snapshot.outputRows.filter(row => rowHasMeaningfulData(row))
          : [];
        aggregateRows.push(...passRows);
        totalRowsAppended += Math.max(0, Number(snapshot?.rowsAppended || 0));
        totalRowsReplaced += Math.max(0, Number(snapshot?.rowsReplaced || 0));
        unresolvedBridgeAccepted = unresolvedBridgeAccepted || snapshot?.workflowRawDataUnsolvedBridgeAccepted === true;

        const afterCoverage = this.getCaseWorkflowRawDataCoverageSummary();
        const afterMissing = missingIdentitySet(afterCoverage);
        const afterMissingCount = Number(afterCoverage?.missingCount || 0);
        const completedRootKeys = Array.from(beforeMissing).filter(key => !afterMissing.has(key));
        const progressed = afterMissingCount < beforeMissingCount || completedRootKeys.length > 0;
        const passRecord = {
          passNumber,
          startedWithMissingCount: beforeMissingCount,
          finishedWithMissingCount: afterMissingCount,
          progressed,
          rowsAppended: Number(snapshot?.rowsAppended || 0),
          rowsReplaced: Number(snapshot?.rowsReplaced || 0),
          partialCommit: snapshot?.workflowRawDataPartialCommit === true,
          committedRootCount: Number(snapshot?.workflowRawDataCommittedRootCount || 0),
          rejectedRootCount: Number(snapshot?.workflowRawDataRejectedRootCount || 0),
          candidateRowCount: Number(snapshot?.workflowRawDataCandidateRowCount || 0),
          automationSegments: Array.isArray(snapshot?.workflowRawDataAutomationSegments)
            ? snapshot.workflowRawDataAutomationSegments.map(segment => ({ ...segment }))
            : [],
          bridgeAccepted: snapshot?.workflowRawDataUnsolvedBridgeAccepted === true,
          status: snapshot?.status || '',
          message: snapshot?.message || '',
          selectedInputRoots: (Array.isArray(snapshot?.workflowRawDataSelectedInputRoots)
            ? snapshot.workflowRawDataSelectedInputRoots
            : (Array.isArray(this.caseWorkflowRawDataTool88SelectedInputRoots)
              ? this.caseWorkflowRawDataTool88SelectedInputRoots
              : [])).map(row => ({ ...row })),
          completedRootKeys,
          completedRoots: (Array.isArray(coverage?.missingRows) ? coverage.missingRows : [])
            .filter(row => completedRootKeys.includes(row?.rootInstanceKey || row?.id || this.caseWorkflowRootInstanceKey(row)))
            .map(row => ({
              id: row?.id || '',
              rootInstanceKey: row?.rootInstanceKey || '',
              rowIndex: Number(row?.rowIndex),
              spreadsheetRowNumber: row?.spreadsheetRowNumber || '',
              pathLabel: row?.pathLabel || '',
              hash: row?.hash || row?.transactionHash || '',
              toAddress: row?.toAddress || '',
              duplicateHash: Boolean(row?.duplicateHash)
            })),
          remainingRoots: (Array.isArray(afterCoverage?.missingRows) ? afterCoverage.missingRows : []).map(row => ({
            id: row?.id || '',
            rootInstanceKey: row?.rootInstanceKey || '',
            rowIndex: Number(row?.rowIndex),
            spreadsheetRowNumber: row?.spreadsheetRowNumber || '',
            pathLabel: row?.pathLabel || '',
            hash: row?.hash || row?.transactionHash || '',
            toAddress: row?.toAddress || '',
            duplicateHash: Boolean(row?.duplicateHash)
          }))
        };
        passes.push(passRecord);
        this.caseWorkflowRawDataTool88CoveragePasses = passes.slice();
        coverage = afterCoverage;

        if (coverage.isComplete) break;
        const hardIncomplete = snapshot?.workflowRawDataPassIncomplete === true
          && snapshot?.workflowRawDataUnsolvedBridgeAccepted !== true;
        if (hardIncomplete && progressed) {
          this.pushCaseWorkflowRawTool88Log?.(
            `Tool 88 saved ${Number(snapshot?.workflowRawDataCommittedRootCount || 0)} complete root flow${Number(snapshot?.workflowRawDataCommittedRootCount || 0) === 1 ? '' : 's'} on pass ${passNumber}; continuing with ${afterMissingCount} remaining root${afterMissingCount === 1 ? '' : 's'}.`,
            {
              eventName: 'case-buddy-root-coverage-partial-commit',
              key: `root-coverage-partial-${passNumber}-${Number(snapshot?.workflowRawDataCommittedRootCount || 0)}-${afterMissingCount}`
            }
          );
        }
        if (hardIncomplete && !progressed) break;
        if (!progressed) {
          finalSnapshot = {
            ...(snapshot || {}),
            status: 'raw-data-incomplete',
            message: `Tool 88 did not make distinct root-coverage progress on pass ${passNumber}. ${afterMissingCount} starting root${afterMissingCount === 1 ? '' : 's'} still need raw data.`,
            flowComplete: false,
            workflowRawDataIncomplete: true,
            guardrail: {
              reason: 'no-root-coverage-progress',
              message: `Tool 88 could not distinguish or populate ${afterMissingCount} remaining starting root${afterMissingCount === 1 ? '' : 's'}.`
            }
          };
          break;
        }
      }

      const complete = coverage?.isComplete === true;
      const missingRows = (Array.isArray(coverage?.missingRows) ? coverage.missingRows : []).map(row => ({
        id: row?.id || '',
        rootInstanceKey: row?.rootInstanceKey || '',
        rowIndex: Number(row?.rowIndex),
        spreadsheetRowNumber: row?.spreadsheetRowNumber || '',
        pathLabel: row?.pathLabel || '',
        hash: row?.hash || row?.transactionHash || '',
        toAddress: row?.toAddress || '',
        duplicateHash: Boolean(row?.duplicateHash)
      }));
      const finalMessage = complete
        ? `Tool 88 completed distinct raw-data coverage for all ${Number(coverage?.totalRows || 0)} starting roots across ${passes.length} pass${passes.length === 1 ? '' : 'es'}.`
        : (finalSnapshot?.message || `${Number(coverage?.missingCount || 0)} distinct starting root${Number(coverage?.missingCount || 0) === 1 ? '' : 's'} still need raw data.`);
      const globalFlowCompletion = {
        ...(finalSnapshot?.flowCompletion || {}),
        done: complete,
        totalEntries: Number(coverage?.totalRows || 0),
        completeCount: Number(coverage?.coveredCount || 0),
        incompleteCount: Number(coverage?.missingCount || 0),
        summary: finalMessage
      };
      finalSnapshot = {
        ...(finalSnapshot || {}),
        status: complete ? 'flow-complete' : (finalSnapshot?.status || 'raw-data-incomplete'),
        message: finalMessage,
        outputRows: aggregateRows,
        rowsAppended: totalRowsAppended,
        rowsReplaced: totalRowsReplaced,
        flowComplete: complete,
        flowCompletion: globalFlowCompletion,
        workflowRawDataIncomplete: !complete,
        workflowRawDataCoverageComplete: complete,
        workflowRawDataCoveragePasses: passes,
        workflowRawDataUnsolvedBridgeAccepted: unresolvedBridgeAccepted,
        workflowRawDataCoverage: {
          totalRows: Number(coverage?.totalRows || 0),
          coveredCount: Number(coverage?.coveredCount || 0),
          missingCount: Number(coverage?.missingCount || 0),
          missingRows,
          duplicateHashGroups: coverage?.coverage?.duplicateHashGroups || coverage?.duplicateHashGroups || []
        }
      };
      this.caseWorkflowRawDataTool88CoveragePasses = passes.slice();
      return finalSnapshot;
    },

    async runCaseWorkflowTool88RawDataSolver(options = {}) {
      const step = options.step || this.recommendedWorkflowStep || this.getCaseWorkflowRawDataFetchStep();
      if (this.caseWorkflowRawDataCoverageIsComplete()) {
        this.markCaseWorkflowRawDataCoverageCurrent('workflow-current-raw-data-coverage-before-tool88', { step, persist: true });
        this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88('Raw-data coverage is already complete; Tool 88 raw-data solver is not needed.');
        return {
          handled: true,
          success: true,
          status: 'success',
          source: 'tool88_raw_data_coverage',
          message: 'Raw-data coverage is complete.'
        };
      }
      if (this.caseWorkflowRawDataTool88Running || this.caseWorkflowTool88RawRunning) {
        this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88('Tool 88 is still resolving raw data for this workflow step.');
        return this.waitForCaseWorkflowRawDataTool88Run({ source: 'runCaseWorkflowTool88RawDataSolver' });
      }
      if (!this.caseWorkflowShouldUseTool88RawData(step)) return { handled: false };
      const rows = this.buildCaseWorkflowRawTool88RowsFromMain();
      if (!rows.length) return { handled: false, reason: 'no-main-rows' };

      this.caseWorkflowTool88RawRunning = true;
      this.caseWorkflowTool88RawError = '';
      this.caseWorkflowRawDataTool88SelectedInputRoots = [];
      this.caseWorkflowRawDataTool88CoveragePasses = [];
      this.closeCaseWorkflowRawDataVisibleSolverArtifacts('Workflow raw-data Tool 88 solver is taking over.');
      this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88('Tool 88 is resolving raw-data coverage for this step.');
      try {
        const snapshot = await this.runCaseWorkflowRawDataTool88CoveragePasses({
          ...options,
          forceTool88: true,
          caseWorkflowRawData: true,
          caseBuddyRawDataAutomation: true,
          rawDataAutomation: true,
          workflowStepTag: normalizeCell(step?.tag || 'RAW_DATA_FETCH'),
          stepTag: normalizeCell(step?.tag || 'RAW_DATA_FETCH'),
          stepTitle: step?.title || 'Load raw data for main transactions',
          source: options.source || 'workflow-tool88-raw-data-solver'
        }, step);
        const outputRows = Array.isArray(snapshot?.outputRows)
          ? snapshot.outputRows.filter(row => rowHasMeaningfulData(row))
          : (Array.isArray(this.caseWorkflowRawDataTool88LastRows) ? this.caseWorkflowRawDataTool88LastRows.filter(row => rowHasMeaningfulData(row)) : []);
        const outputRowCount = outputRows.length;
        const snapshotHasRowsAppended = snapshot && Object.prototype.hasOwnProperty.call(snapshot, 'rowsAppended');
        const rowsAppended = snapshotHasRowsAppended
          ? Math.max(0, Number(snapshot?.rowsAppended) || 0)
          : (Number(this.caseAutomationTool88Activity?.rowsAppended) || outputRowCount || 0);
        if (!outputRowCount && !rowsAppended) {
          const message = snapshot?.message || 'Tool 88 finished without exportable raw-data rows.';
          this.caseWorkflowTool88RawError = message;
          return { handled: false, reason: 'no-output-rows', snapshot, message };
        }

        const flowComplete = snapshot?.flowComplete === true || snapshot?.flowCompletion?.done === true;
        const bridgeAccepted = snapshot?.workflowRawDataUnsolvedBridgeAccepted === true;
        const unresolvedCount = Number(snapshot?.flowCompletion?.incompleteCount ?? snapshot?.flowCompletion?.unresolvedCount ?? this.caseAutomationTool88Activity?.unresolvedCount ?? 0) || 0;
        const tool88StoppedIncomplete = snapshot?.workflowRawDataIncomplete === true || (!flowComplete && !bridgeAccepted && (unresolvedCount > 0 || snapshot?.unverifiedOutput === true));
        if (tool88StoppedIncomplete) {
          const completedAtUtc = new Date().toISOString();
          const message = snapshot?.message || snapshot?.flowCompletion?.summary || 'Tool 88 stopped before raw-data flow completion. Review the Tool 88 debug log before preparing the graph.';
          this.caseWorkflowTool88RawError = message;
          this.caseAutomationTool88Activity = {
            ...(this.caseAutomationTool88Activity || {}),
            active: false,
            completedAtUtc,
            updatedAtUtc: completedAtUtc,
            status: 'incomplete-flow',
            title: 'Tool 88 raw-data solver',
            message,
            detail: message,
            rawRows: rowsAppended,
            rowsAppended,
            flowComplete: false,
            unresolvedBridgeAccepted: false,
            requiresInvestigatorReview: true,
            reviewNote: message,
            guardrailMessage: message
          };
          this.caseAutomationTool88Progress = {
            ...(this.caseAutomationTool88Progress || {}),
            active: false,
            status: 'incomplete-flow',
            detail: message,
            outputRows: rowsAppended || outputRowCount,
            updatedAtUtc: completedAtUtc
          };
          this.caseAutomation = {
            ...(this.caseAutomation || {}),
            active: false,
            busy: false,
            paused: true,
            status: 'Tool 88 raw data needs attention',
            detail: message,
            manualPause: {
              type: 'manual-pause',
              pauseKind: 'raw-data-fetch',
              stepTag: normalizeCell(step?.tag || step?.stepTag || 'RAW_DATA_FETCH'),
              stepTitle: step?.title || step?.stepTitle || 'Load raw data for main transactions',
              title: 'Tool 88 raw-data flow is incomplete',
              message,
              primaryLabel: 'Copy Tool 88 debug log',
              primaryMethod: 'copyCaseWorkflowRawDataTool88DebugSnapshot',
              openedAtUtc: completedAtUtc,
              summary: {
                outputRows: outputRowCount,
                rowsAppended,
                unresolvedCount
              }
            }
          };
          this.showCaseAutomationPauseModal = true;
          if (typeof this.queueCaseSummaryRefresh === 'function') this.queueCaseSummaryRefresh(120);
          return {
            handled: true,
            success: false,
            paused: true,
            status: 'tool88-flow-incomplete',
            snapshot,
            rows: outputRows,
            appendResult: { appendedCount: rowsAppended },
            message
          };
        }

        this.recordCaseWorkflowTool88RawDataCompletion({
          appendedCount: rowsAppended,
          replacedCount: 0,
          outputRowCount: outputRowCount || rowsAppended,
          flowComplete,
          unresolvedBridgeAccepted: bridgeAccepted
        }, step);
        this.markCaseWorkflowRawDataCoverageCurrent('tool88_raw_data_solver_current_coverage', { step, persist: true });

        const completedAtUtc = new Date().toISOString();
        const skipResolvableReviews = Boolean(this.caseAutomation?.skipResolvableReview);
        const bridgeNeedsInvestigatorReview = bridgeAccepted && !skipResolvableReviews;
        const shouldAutoContinueAfterTool88RawData = (options.continueAfter === true || Boolean(this.caseAutomation?.active)) && !bridgeNeedsInvestigatorReview;
        this.caseAutomationTool88Activity = {
          ...(this.caseAutomationTool88Activity || {}),
          active: false,
          completedAtUtc,
          updatedAtUtc: completedAtUtc,
          status: flowComplete ? 'flow-complete' : 'raw-data-imported',
          title: 'Tool 88 raw-data solver',
          message: bridgeAccepted
            ? skipResolvableReviews
              ? `Tool 88 appended ${rowsAppended} raw row${rowsAppended === 1 ? '' : 's'} and documented an unsolved bridge ending for later manual review.`
              : `Tool 88 appended ${rowsAppended} raw row${rowsAppended === 1 ? '' : 's'} and marked an unsolved bridge ending for manual review.`
            : flowComplete
              ? `Tool 88 completed the raw-data flow and appended ${rowsAppended} raw row${rowsAppended === 1 ? '' : 's'}.`
              : `Tool 88 appended ${rowsAppended} raw row${rowsAppended === 1 ? '' : 's'}; unresolved service endpoints were left as documented path endings.`,
          detail: bridgeAccepted
            ? skipResolvableReviews
              ? `Tool 88 appended ${rowsAppended} raw row${rowsAppended === 1 ? '' : 's'} and documented an unsolved bridge ending; skip-review Auto will continue to graph preparation.`
              : `Tool 88 appended ${rowsAppended} raw row${rowsAppended === 1 ? '' : 's'} and marked an unsolved bridge ending for manual review.`
            : flowComplete
              ? `Tool 88 completed the raw-data flow and appended ${rowsAppended} raw row${rowsAppended === 1 ? '' : 's'}.`
              : `Tool 88 appended ${rowsAppended} raw row${rowsAppended === 1 ? '' : 's'}; unresolved service endpoints were left as documented path endings.`,
          rawRows: rowsAppended,
          rowsAppended,
          flowComplete,
          unresolvedBridgeAccepted: bridgeAccepted,
          requiresInvestigatorReview: bridgeNeedsInvestigatorReview,
          reviewNote: bridgeAccepted ? 'Unsolved bridge marked for manual review.' : '',
          guardrailMessage: bridgeNeedsInvestigatorReview ? 'Unsolved bridge marked for manual review.' : (snapshot?.guardrail?.message || '')
        };
        this.caseAutomationTool88Progress = {
          ...(this.caseAutomationTool88Progress || {}),
          active: false,
          status: flowComplete ? 'flow-complete' : 'raw-data-imported',
          detail: `Imported ${rowsAppended} Tool 88 raw-data row${rowsAppended === 1 ? '' : 's'}.`,
          outputRows: rowsAppended,
          updatedAtUtc: completedAtUtc
        };
        this.caseAutomation = {
          ...(this.caseAutomation || {}),
          active: true,
          busy: false,
          paused: false,
          manualPause: null,
          status: 'Tool 88 raw data loaded',
          detail: bridgeAccepted
            ? `Tool 88 appended ${rowsAppended} raw-data row${rowsAppended === 1 ? '' : 's'} and marked an unsolved bridge ending for manual review; moving to the next workflow step.`
            : `Tool 88 appended ${rowsAppended} raw-data row${rowsAppended === 1 ? '' : 's'}; moving to the next workflow step.`
        };
        if (typeof this.refreshRawDataImportCoverage === 'function') this.refreshRawDataImportCoverage();
        if (typeof this.queueCaseSummaryRefresh === 'function') this.queueCaseSummaryRefresh(120);
        if (typeof this.showCaseToast === 'function' && options.toast !== false) {
          this.showCaseToast(`Tool 88 loaded ${rowsAppended} raw-data row${rowsAppended === 1 ? '' : 's'} for this case.`);
        }
        this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88(`Tool 88 loaded ${rowsAppended} raw-data row${rowsAppended === 1 ? '' : 's'} for this case.`);
        if (shouldAutoContinueAfterTool88RawData) {
          this.armCaseWorkflowRawDataTool88AutoContinueGuard('Tool 88 raw-data loading succeeded; protect workflow Auto continuation from stale stop/review branches.');
          this.scheduleCaseWorkflowRawDataTool88AutoContinue([], {
            source: options.source || 'tool88-raw-data-success',
            reason: 'Tool 88 raw-data loading succeeded; continuing workflow Auto after legacy handlers settle.',
            delayMs: options.continueAfter === true ? 650 : 1250,
            allowTool88Running: true,
            ignoreAutomationBusy: true
          });
        }
        await this.$nextTick();
        return {
          handled: true,
          success: true,
          snapshot,
          rows: outputRows,
          appendResult: { appendedCount: rowsAppended },
          unresolvedBridgeAccepted: bridgeAccepted,
          requiresInvestigatorReview: bridgeNeedsInvestigatorReview
        };
      } catch (error) {
        const message = error?.message || 'Tool 88 raw-data solving failed.';
        this.caseWorkflowTool88RawError = message;
        this.caseAutomationTool88Activity = {
          ...(this.caseAutomationTool88Activity || {}),
          active: false,
          completedAtUtc: new Date().toISOString(),
          status: 'error',
          message,
          detail: message,
          guardrailMessage: message
        };
        this.caseAutomation = {
          ...(this.caseAutomation || {}),
          busy: false,
          status: 'Tool 88 raw data failed',
          detail: message
        };
        console.warn('Case Buddy workflow Tool 88 raw-data solver failed.', error);
        return { handled: false, reason: 'error', error, message };
      } finally {
        this.caseWorkflowTool88RawRunning = false;
        this.caseAutomationTool88Progress = {
          ...(this.caseAutomationTool88Progress || {}),
          active: false,
          updatedAtUtc: new Date().toISOString()
        };
      }
    },

    clearCaseWorkflowRawDataTool88AutoContinueTimer() {
      if (this.caseWorkflowRawDataTool88AutoContinueTimer) {
        if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
          window.clearTimeout(this.caseWorkflowRawDataTool88AutoContinueTimer);
        } else {
          clearTimeout(this.caseWorkflowRawDataTool88AutoContinueTimer);
        }
        this.caseWorkflowRawDataTool88AutoContinueTimer = null;
      }
    },

    armCaseWorkflowRawDataTool88AutoContinueGuard(reason = '') {
      const now = Date.now();
      this.caseWorkflowRawDataTool88AutoContinueGuardUntilMs = now + 30000;
      this.caseWorkflowRawDataTool88AutoContinueReason = reason || 'Tool 88 raw-data automation completed.';
    },

    caseWorkflowRawDataTool88AutoContinueGuardActive() {
      return Date.now() < (Number(this.caseWorkflowRawDataTool88AutoContinueGuardUntilMs) || 0);
    },

    caseWorkflowRawDataTool88UserStopIsRecent() {
      const stoppedAt = Number(this.caseWorkflowRawDataTool88UserStopRequestedAtMs) || 0;
      return stoppedAt > 0 && Date.now() - stoppedAt < 30000;
    },

    caseWorkflowRawDataTool88ShouldAutoContinue(options = {}) {
      if (!options.force && !this.caseAutomation?.skipResolvableReview) return false;
      if (this.caseWorkflowRawDataTool88UserStopIsRecent()) return false;
      if (!options.allowTool88Running && (this.caseWorkflowRawDataTool88Running || this.caseWorkflowTool88RawRunning)) return false;
      if (!this.caseWorkflowRawDataCoverageIsComplete()) return false;
      return true;
    },

    caseWorkflowRawDataTool88ShouldSuppressStop(message = '') {
      if (!this.caseWorkflowRawDataTool88AutoContinueGuardActive()) return false;
      if (this.caseWorkflowRawDataTool88UserStopIsRecent()) return false;
      if (!this.caseAutomation?.skipResolvableReview) return false;
      if (!this.caseWorkflowRawDataCoverageIsComplete()) return false;
      const text = normalizeCell(message).toLowerCase();
      return /automation stopped by user|automation stopped at manual pause|manual workflow action needed/.test(text);
    },

    requestCaseAutomationUserStop(message = 'Automation stopped by user.') {
      this.caseWorkflowRawDataTool88UserStopRequestedAtMs = Date.now();
      this.caseWorkflowRawDataTool88StopToken = (Number(this.caseWorkflowRawDataTool88StopToken) || 0) + 1;
      this.caseWorkflowRawDataTool88AutoContinueGuardUntilMs = 0;
      this.clearCaseWorkflowRawDataTool88AutoContinueTimer();
      this.cancelCaseWorkflowBackgroundAutomationResume?.({ source: 'request-user-stop' });
      const cameFromSimpleLaunch = Boolean(this.caseSimpleLaunchAutoRequested || this.caseSimpleLaunchMinimized);
      if (cameFromSimpleLaunch) {
        this.caseSimpleLaunchMinimized = false;
        this.caseSimpleLaunchDismissed = false;
        this.caseSimpleLaunchAutoStarting = false;
        this.caseAutomationShowcaseOverlayMinimized = false;
        this.caseSimpleLaunchStatus = 'Auto stopped. You can review the Simple View, open the full workspace, or run Auto again.';
      }
      const result = this.stopCaseAutomation(message);
      if (cameFromSimpleLaunch) {
        const restoreSimpleLaunch = () => {
          this.caseSimpleLaunchAutoRequested = false;
          this.caseSimpleLaunchAutoStarting = false;
          this.caseSimpleLaunchMinimized = false;
          this.caseSimpleLaunchDismissed = false;
          this.caseAutomationShowcaseOverlayMinimized = false;
          this.caseSimpleLaunchStatus = 'Auto stopped. You can review the Simple View, open the full workspace, or run Auto again.';
        };
        restoreSimpleLaunch();
        if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
          window.setTimeout(restoreSimpleLaunch, 0);
        }
      }
      return result;
    },

    scheduleCaseWorkflowRawDataTool88AutoContinue(args = [], options = {}) {
      if (!this.caseWorkflowRawDataTool88ShouldAutoContinue(options)) return false;
      this.armCaseWorkflowRawDataTool88AutoContinueGuard(options.reason || 'Tool 88 raw-data loading completed; workflow Auto may continue.');
      this.clearCaseWorkflowRawDataTool88AutoContinueTimer();
      const delayMs = Math.max(0, Number(options.delayMs) || 850);
      const run = () => {
        this.caseWorkflowRawDataTool88AutoContinueTimer = null;
        this.continueCaseWorkflowRawDataTool88Auto(args, options).catch((error) => {
          console.warn('Case Buddy could not continue workflow Auto after Tool 88 raw-data loading.', error);
        });
      };
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
        this.caseWorkflowRawDataTool88AutoContinueTimer = window.setTimeout(run, delayMs);
      } else {
        this.caseWorkflowRawDataTool88AutoContinueTimer = setTimeout(run, delayMs);
      }
      return true;
    },

    async continueCaseWorkflowRawDataTool88Auto(args = [], options = {}) {
      if (this.caseWorkflowRawDataTool88AutoContinueInFlight) return { handled: true, reason: 'auto-continue-in-flight' };
      if (!this.caseWorkflowRawDataTool88ShouldAutoContinue(options)) return { handled: false, reason: 'auto-continue-not-needed' };
      if (!options.force) {
        const auto = this.caseAutomation || {};
        const activity = this.caseAutomationTool88Activity || {};
        const progress = this.caseAutomationTool88Progress || {};
        const statusText = normalizeCell(auto.status).toLowerCase();
        const tool88FinishedRawData = this.caseWorkflowRawDataCoverageIsComplete() && !activity.active && !progress.active && (Number(activity.rowsAppended) > 0 || Number(progress.outputRows) > 0 || activity.flowComplete === true);
        const looksStalledAfterTool88 = tool88FinishedRawData || !auto.active || auto.paused || !!auto.manualPause || /automation stopped|raw-data loading needs attention|manual workflow action needed|tool 88 raw data loaded|continuing after tool 88|solving raw data with tool 88/.test(statusText);
        if (auto.active && auto.busy && !options.ignoreAutomationBusy && !tool88FinishedRawData) return { handled: false, reason: 'automation-already-busy' };
        if (auto.active && !looksStalledAfterTool88) return { handled: false, reason: 'automation-already-running' };
      }
      this.clearCaseWorkflowRawDataTool88AutoContinueTimer();
      this.armCaseWorkflowRawDataTool88AutoContinueGuard(options.reason || 'Continuing workflow Auto after Tool 88 raw-data loading.');
      this.caseWorkflowRawDataTool88AutoContinueInFlight = true;
      try {
        if (typeof this.refreshRawDataImportCoverage === 'function') this.refreshRawDataImportCoverage();
        if (typeof this.closeCaseWorkflowRawDataVisibleSolverArtifacts === 'function') {
          this.closeCaseWorkflowRawDataVisibleSolverArtifacts('Tool 88 raw-data loading succeeded; workflow Auto is continuing.');
        }
        this.showCaseAutomationPauseModal = false;
        this.caseAutomation = {
          ...(this.caseAutomation || {}),
          active: false,
          busy: false,
          paused: false,
          manualPause: null,
          status: 'Continuing after Tool 88 raw data',
          detail: 'Tool 88 loaded Raw Data successfully; continuing to the next workflow action.'
        };
        await this.$nextTick();
        this.queueCaseWorkflowWwwUsdReferenceAutoGuard({
          source: 'tool88-raw-data-auto-continue-watchdog',
          delayMs: 150,
          continueAfter: true,
          maxMs: 120000
        });
        this.queueCaseWorkflowStatefulGeneratedReviewAutoGuard({
          source: 'tool88-raw-data-auto-continue-generated-watchdog',
          delayMs: 250,
          continueAfter: true,
          maxMs: 180000
        });
        const result = await this.callCaseBuddyOriginalMethod(caseBuddyAutomationMethods, 'startCaseAutomation', Array.isArray(args) ? args : []);
        this.caseWorkflowRawDataTool88LastAutoContinueAtUtc = new Date().toISOString();
        this.caseWorkflowFinalizeAutomationIfComplete('Workflow Auto completed after Tool 88 raw-data continuation.');
        return result;
      } finally {
        this.caseWorkflowRawDataTool88AutoContinueInFlight = false;
      }
    },

    caseWorkflowNormalizeAttributionColumnsOnlyRow(row = [], options = {}) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      if (options.isHeader === true || this.caseManualIsHeaderRow(normalized)) return CASE_MANUAL_START_CANONICAL_HEADERS.slice();
      return this.caseManualNormalizeAttributionRoleColumnsForRow(normalized);
    },

    caseWorkflowNormalizeAttributionMatrix(rows = []) {
      return (Array.isArray(rows) ? rows : []).map((row, index) => this.caseWorkflowNormalizeAttributionColumnsOnlyRow(row, {
        isHeader: index === 0 && this.caseManualIsHeaderRow(row)
      }));
    },

    async normalizeCaseWorkflowAttributionColumnsIfNeeded(options = {}) {
      const sheetKeys = Array.isArray(options.sheetKeys) && options.sheetKeys.length ? options.sheetKeys : ['main', 'raw'];
      let changedAny = false;
      for (const sheetKey of sheetKeys) {
        const matrix = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet(sheetKey) : [];
        if (!Array.isArray(matrix) || !matrix.length) continue;
        let changed = false;
        const nextMatrix = matrix.map((row, index) => {
          const nextRow = this.caseWorkflowNormalizeAttributionColumnsOnlyRow(row, { isHeader: index === 0 && this.caseManualIsHeaderRow(row) });
          if (!changed && JSON.stringify(nextRow) !== JSON.stringify(this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)))) changed = true;
          return nextRow;
        });
        if (!changed) continue;
        changedAny = true;
        await this.replaceCaseManualStartLogicalSheetMatrix(sheetKey, nextMatrix, options.reason || 'Normalized Column K / Column X attribution role data.', { autosaveDelayMs: options.autosaveDelayMs || 900 });
      }
      return { changed: changedAny };
    },

    recordCaseSimpleLaunchAutoStartAudit(event = {}) {
      const entry = {
        atUtc: new Date().toISOString(),
        stage: normalizeCell(event.stage || 'simple-auto-start'),
        attemptId: Number(event.attemptId ?? this.caseSimpleLaunchAutoStartAttemptId) || 0,
        active: Boolean(this.caseAutomation?.active),
        busy: Boolean(this.caseAutomation?.busy),
        paused: Boolean(this.caseAutomation?.paused),
        automationTimerPending: Boolean(this.caseAutomationTimer),
        tool88Running: Boolean(this.caseWorkflowRawDataTool88Running || this.caseWorkflowTool88RawRunning),
        recommendedStepTag: this.caseWorkflowStepIdentity?.(this.recommendedWorkflowStep || {}) || '',
        recommendedStepTitle: normalizeCell(this.recommendedWorkflowStep?.title || this.recommendedWorkflowStep?.stepTitle || ''),
        workflowCompleted: Number(this.workflowProgress?.completed) || 0,
        workflowTotal: Number(this.workflowProgress?.total) || 0,
        ...event
      };
      const audit = Array.isArray(this.caseSimpleLaunchAutoStartAudit) ? this.caseSimpleLaunchAutoStartAudit.slice() : [];
      audit.unshift(entry);
      this.caseSimpleLaunchAutoStartAudit = audit.slice(0, 20);
      return entry;
    },

    caseSimpleLaunchAutoHasLiveRun() {
      return Boolean(
        this.caseAutomation?.active ||
        this.caseAutomation?.busy ||
        this.caseAutomationTimer ||
        this.caseWorkflowRawDataTool88Running ||
        this.caseWorkflowTool88RawRunning ||
        this.caseWorkflowRawDataTool88AutoContinueInFlight ||
        this.caseAutomation?.manualPause ||
        this.showCaseAutomationPauseModal ||
        this.caseSimpleLaunchReportReady
      );
    },

    async waitForCaseSimpleLaunchAutoStart(maxWaitMs = 420) {
      const startedAt = Date.now();
      while (Date.now() - startedAt < Math.max(0, Number(maxWaitMs) || 0)) {
        if (this.caseSimpleLaunchAutoHasLiveRun()) return true;
        await new Promise(resolve => window.setTimeout(resolve, 40));
      }
      return this.caseSimpleLaunchAutoHasLiveRun();
    },

    async startCaseSimpleLaunchAuto(...args) {
      if (this.caseSimpleLaunchAutoStarting || this.caseAutomation?.active || this.caseAutomation?.busy || this.caseManualStartViewportAutoActive) {
        return { handled: true, success: false, reason: 'simple-auto-already-starting' };
      }

      if (!this.caseSimpleLaunchHasCase) {
        this.caseSimpleLaunchStatus = 'Create a case or choose a saved case before running Auto.';
        return { handled: true, success: false, reason: 'no-active-case' };
      }

      const confirmation = this.getStartingMainTransactionConfirmationState?.() || {};
      const startingCount = Number(confirmation.count) || 0;
      if (!(confirmation.hasStartingTransactions || startingCount > 0)) {
        this.caseSimpleLaunchStatus = 'Load at least one starting transaction into Main Data before running Auto.';
        this.openCaseSimpleLaunchStartingTransactions?.();
        return { handled: true, success: false, reason: 'no-starting-transactions' };
      }

      const attemptId = (Number(this.caseSimpleLaunchAutoStartAttemptId) || 0) + 1;
      this.caseSimpleLaunchAutoStartAttemptId = attemptId;
      this.caseSimpleLaunchAutoStarting = true;
      this.caseSimpleLaunchAutoRequested = true;
      this.caseSimpleLaunchStatus = `Starting Auto with ${startingCount} starting transaction${startingCount === 1 ? '' : 's'}…`;
      this.caseSimpleFileChooserVisible = false;
      this.caseAutomationShowcaseOverlayMinimized = false;
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        skipResolvableReview: Boolean(this.caseSkipResolvableReviewsEnabled)
      };
      this.clearCaseWorkflowScheduledScrollTimers();
      this.recordCaseSimpleLaunchAutoStartAudit({ stage: 'start-clicked', attemptId, startingCount });

      try {
        this.recordStartingMainTransactionsWorkflowCurrentCheck?.('simple-launch-auto-preflight', { silent: true });
        await this.reconcileCaseWorkflowSpreadsheetBackedIntake?.({
          source: 'simple-launch-auto-preflight',
          settleMs: 20
        });
        this.queueCaseSummaryRefresh?.(0);
        await this.$nextTick();
        await new Promise(resolve => window.setTimeout(resolve, 30));

        let result = await this.startCaseAutomationFromUser(...args);
        let started = await this.waitForCaseSimpleLaunchAutoStart(420);
        this.recordCaseSimpleLaunchAutoStartAudit({
          stage: started ? 'first-attempt-started' : 'first-attempt-no-live-run',
          attemptId,
          resultReason: normalizeCell(result?.reason || ''),
          resultHandled: Boolean(result?.handled),
          resultSuccess: Boolean(result?.success)
        });

        // Some freshly loaded cases need one workflow-state reconciliation pass
        // before the imported Auto runner will leave a live timer/run behind.
        // Perform that second invocation internally so the investigator does not
        // have to discover that clicking Run Auto twice is required.
        if (!started && attemptId === this.caseSimpleLaunchAutoStartAttemptId) {
          this.caseSimpleLaunchStatus = 'Finishing workflow setup and starting Auto…';
          this.recordStartingMainTransactionsWorkflowCurrentCheck?.('simple-launch-auto-retry-preflight', { silent: true });
          await this.reconcileCaseWorkflowSpreadsheetBackedIntake?.({
            source: 'simple-launch-auto-retry-preflight',
            settleMs: 20
          });
          this.queueCaseSummaryRefresh?.(0);
          await this.$nextTick();
          await new Promise(resolve => window.setTimeout(resolve, 80));
          result = await this.startCaseAutomationFromUser(...args);
          started = await this.waitForCaseSimpleLaunchAutoStart(520);
          this.recordCaseSimpleLaunchAutoStartAudit({
            stage: started ? 'retry-started' : 'retry-no-live-run',
            attemptId,
            resultReason: normalizeCell(result?.reason || ''),
            resultHandled: Boolean(result?.handled),
            resultSuccess: Boolean(result?.success)
          });
        }

        if (started) {
          this.caseSimpleLaunchMinimized = true;
          this.caseSimpleLaunchDismissed = false;
          this.caseSimpleLaunchStatus = this.caseSimpleLaunchReportReady
            ? 'Workflow complete. Review the final one-click report package.'
            : (this.caseAutomation?.manualPause
              ? 'Auto reached a workflow review that needs attention.'
              : 'Auto is building the case workspace.');
          return result;
        }

        this.caseSimpleLaunchAutoRequested = false;
        this.caseSimpleLaunchMinimized = false;
        this.caseSimpleLaunchDismissed = false;
        this.caseSimpleLaunchStatus = 'Auto did not start. The Simple View stayed open so you can try again or inspect workflow status.';
        this.showCaseToast?.('Auto did not start; no case work was lost.');
        return result || { handled: true, success: false, reason: 'simple-auto-did-not-start' };
      } catch (error) {
        this.caseSimpleLaunchAutoRequested = false;
        this.caseSimpleLaunchMinimized = false;
        this.caseSimpleLaunchDismissed = false;
        this.caseSimpleLaunchStatus = `Auto could not start: ${error?.message || String(error)}`;
        this.recordCaseSimpleLaunchAutoStartAudit({
          stage: 'start-error',
          attemptId,
          error: error?.message || String(error)
        });
        this.showCaseToast?.(this.caseSimpleLaunchStatus);
        return { handled: true, success: false, reason: 'simple-auto-start-error', error: error?.message || String(error) };
      } finally {
        if (attemptId === this.caseSimpleLaunchAutoStartAttemptId) {
          this.caseSimpleLaunchAutoStarting = false;
        }
      }
    },

    startCaseAutomationFromUser(...args) {
      // Only the visible Run Auto button uses this wrapper. Internal queued
      // continuations still respect the recent-stop guard and cannot revive a
      // run the investigator just stopped.
      this.caseWorkflowRawDataTool88UserStopRequestedAtMs = 0;
      return this.startCaseAutomation(...args);
    },

    async startCaseAutomation(...args) {
      if (this.caseWorkflowStatefulGeneratedReviewUserStopIsRecent?.(1500) && this.caseWorkflowStatefulGeneratedReviewAutoResumeTimer) {
        this.clearCaseWorkflowStatefulGeneratedReviewAutoResumeTimer?.();
        return { handled: true, success: false, reason: 'recent-user-stop' };
      }
      this.markEmptyCaseStarterNudgeInvestigationStarted('auto-started');
      const startingNewAutoRun = !this.caseAutomation?.active && !this.caseAutomation?.busy && !this.caseAutomation?.paused;
      if (startingNewAutoRun && !this.caseWorkflowRawDataTool88Running && !this.caseWorkflowTool88RawRunning) {
        this.resetCaseWorkflowRawDataTool88VisualState('Starting workflow Auto; clearing stale Tool 88 raw-data preview before any new raw-data run.', {
          stopTool88: false,
          keepHost: true,
          clearVisibleSolverOutput: true
        });
      }
      if (this.caseAutomation?.skipResolvableReview) {
        const rootRepair = await this.reconcileCaseRootChronologyAndPathLabels({
          source: 'case-automation-live-intake-preflight',
          sort: true,
          silent: true
        });
        if (rootRepair?.changed) await this.$nextTick();
      }
      await this.reconcileCaseWorkflowSpreadsheetBackedIntake?.({
        source: 'case-automation-live-intake-preflight',
        settleMs: 20
      });
      this.reconcileCaseWorkflowWwwUsdReferenceCompletionState({ source: 'case-automation-start-preflight' });
      this.reconcileCaseWorkflowStatefulGeneratedReviewManualPause({ source: 'case-automation-start-preflight' });
      // If the checklist has already been soft-stamped complete, verify the live
      // generated deliverable chain and rebuild/review the one-click report package
      // before declaring workflow completion. Otherwise Auto can finish while the
      // final report still contains an older addendum-prerequisite placeholder.
      if (this.caseAutomation?.skipResolvableReview && this.caseWorkflowAllStepsComplete?.()) {
        const finalPackageResult = await this.runCaseWorkflowFinalRubberStampTail('CASE_COMPLETION_SUMMARY', {
          source: 'case-automation-start-complete-package-reconcile',
          methodName: 'startCaseAutomation',
          force: true,
          reason: 'Workflow cards are complete; rebuilding and reviewing the final report package before declaring complete.'
        });
        if (finalPackageResult?.handled) return finalPackageResult;
      }
      if (this.caseWorkflowFinalizeAutomationIfComplete('Workflow Auto is already complete.')) return { handled: true, success: true, reason: 'workflow-complete' };
      const rawStep = this.recommendedWorkflowStep || null;
      if (this.isCaseWorkflowRawDataFetchStep(rawStep) && this.caseWorkflowRawDataCoverageIsComplete()) {
        this.markCaseWorkflowRawDataCoverageCurrent('workflow-current-raw-data-coverage-auto-start', { step: rawStep, persist: true });
        this.closeCaseWorkflowRawDataVisibleSolverArtifacts('Raw-data coverage is complete; workflow Auto is continuing.');
      }
      if (this.caseWorkflowShouldUseTool88RawData(rawStep)) {
        if (this.caseWorkflowRawDataTool88Running || this.caseWorkflowTool88RawRunning) {
          this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88('Tool 88 is still resolving raw data; keeping workflow Auto attached to the live solver.');
          const liveResult = await this.runCaseWorkflowTool88RawDataSolver({ source: 'auto-running', continueAfter: true });
          if (liveResult?.success) {
            if (liveResult.requiresInvestigatorReview) return liveResult;
            return this.continueCaseWorkflowRawDataTool88Auto(args, {
              force: true,
              source: 'auto-running-success',
              reason: 'Tool 88 raw-data loading completed while Auto was already attached.'
            });
          }
          return liveResult;
        }
        if (this.caseAutomation?.paused && this.caseWorkflowRawDataCoverageIsComplete()) {
          this.caseAutomation = {
            ...(this.caseAutomation || {}),
            active: true,
            paused: false,
            busy: false,
            manualPause: null,
            status: 'Tool 88 raw data loaded',
            detail: 'Raw-data coverage is complete; continuing workflow automation.'
          };
          this.closeCaseWorkflowRawDataVisibleSolverArtifacts('Raw-data coverage is complete; continuing workflow automation.');
          return this.continueCaseWorkflowRawDataTool88Auto(args, {
            force: true,
            source: 'coverage-complete-resume',
            reason: 'Raw-data coverage is already complete; continuing workflow automation.'
          });
        }
        if (!this.caseAutomation?.active || this.caseAutomation?.paused) {
          const tool88Result = await this.runCaseWorkflowTool88RawDataSolver({ source: 'auto', continueAfter: true });
          if (tool88Result?.success) {
            if (tool88Result.requiresInvestigatorReview) return tool88Result;
            return this.continueCaseWorkflowRawDataTool88Auto(args, {
              force: true,
              source: 'auto-tool88-success',
              reason: 'Tool 88 raw-data loading succeeded from workflow Auto.'
            });
          }
          if (tool88Result?.handled) return tool88Result;
        }
      }
      const attributionGateBefore = await this.runCaseWorkflowAddressAttributionFillGateIfNeeded({
        source: 'case-automation-start-address-attribution-fill',
        step: this.recommendedWorkflowStep,
        continueAfter: true
      });
      if (attributionGateBefore?.handled && !attributionGateBefore?.success) return attributionGateBefore;
      const usdGateBefore = await this.runCaseWorkflowWwwUsdReferenceGateIfNeeded({
        source: 'case-automation-start-www-usd-references',
        step: this.recommendedWorkflowStep
      });
      if (usdGateBefore?.handled && !usdGateBefore?.success) return usdGateBefore;
      const generatedGateBefore = await this.runCaseWorkflowStatefulGeneratedReviewGateIfNeeded({
        source: 'case-automation-start-stateful-generated-review',
        step: this.recommendedWorkflowStep,
        continueAfter: true
      });
      if (generatedGateBefore?.handled) return generatedGateBefore;
      this.queueCaseWorkflowWwwUsdReferenceAutoGuard({
        source: 'case-automation-start-watchdog',
        delayMs: 150,
        continueAfter: true,
        maxMs: 120000
      });
      this.queueCaseWorkflowStatefulGeneratedReviewAutoGuard({
        source: 'case-automation-start-generated-watchdog',
        delayMs: 250,
        continueAfter: true,
        maxMs: 180000
      });
      const result = await this.callCaseBuddyOriginalMethod(caseBuddyAutomationMethods, 'startCaseAutomation', args);
      await this.normalizeCaseWorkflowAttributionColumnsIfNeeded({ reason: 'Normalized Column K / Column X attribution after workflow Auto.' });
      this.reconcileCaseWorkflowAddressAttributionFillCompletionState({ source: 'case-automation-followup-attribution-reconcile' });
      const attributionGateAfter = await this.runCaseWorkflowAddressAttributionFillGateIfNeeded({
        source: 'case-automation-followup-address-attribution-fill',
        step: this.recommendedWorkflowStep,
        force: Boolean(this.caseAutomation?.skipResolvableReview),
        continueAfter: true
      });
      if (attributionGateAfter?.handled && !attributionGateAfter?.success) return attributionGateAfter;
      if (attributionGateAfter?.handled && attributionGateAfter?.success && this.caseAutomation?.skipResolvableReview) {
        const continuation = await this.callCaseBuddyOriginalMethod(caseBuddyAutomationMethods, 'startCaseAutomation', args);
        return { ...(attributionGateAfter || {}), continuation, previousResult: result };
      }
      const reconciledColumnHState = this.reconcileCaseWorkflowWwwUsdReferenceCompletionState({ source: 'case-automation-followup-reconcile' });
      const shouldForceUsdGateAfter = Boolean(reconciledColumnHState)
        && Number(reconciledColumnHState.referenceRowCount || 0) > 0
        && !reconciledColumnHState.columnHComplete
        && this.caseWorkflowExternalAttributionReviewIsCurrent();
      const usdGateAfter = await this.runCaseWorkflowWwwUsdReferenceGateIfNeeded({
        source: 'case-automation-followup-www-usd-references',
        step: this.recommendedWorkflowStep,
        force: shouldForceUsdGateAfter
      });
      if (usdGateAfter?.handled && !usdGateAfter?.success) return usdGateAfter;
      if (usdGateAfter?.success) {
        this.queueCaseWorkflowStatefulGeneratedReviewAutoGuard({
          source: 'case-automation-after-column-h-generated-watchdog',
          delayMs: 250,
          continueAfter: true,
          maxMs: 180000
        });
        const continuation = await this.callCaseBuddyOriginalMethod(caseBuddyAutomationMethods, 'startCaseAutomation', args);
        this.caseWorkflowFinalizeAutomationIfComplete('Workflow Auto completed after Column H USD reference review.');
        return { ...(usdGateAfter || {}), continuation };
      }
      const generatedGateAfter = await this.runCaseWorkflowStatefulGeneratedReviewGateIfNeeded({
        source: 'case-automation-followup-stateful-generated-review',
        step: this.recommendedWorkflowStep,
        continueAfter: true
      });
      if (generatedGateAfter?.handled) return { ...(generatedGateAfter || {}), previousResult: result };
      if (this.caseAutomation?.skipResolvableReview && this.caseWorkflowAllStepsComplete?.()) {
        const finalPackageResult = await this.runCaseWorkflowFinalRubberStampTail('CASE_COMPLETION_SUMMARY', {
          source: 'case-automation-followup-complete-package-reconcile',
          methodName: 'startCaseAutomation',
          force: true,
          reason: 'Workflow cards are complete; rebuilding and reviewing the final report package before declaring complete.'
        });
        if (finalPackageResult?.handled) return { ...(finalPackageResult || {}), previousResult: result };
      }
      this.caseWorkflowFinalizeAutomationIfComplete('Workflow Auto completed all steps.');
      return result;
    },

    async runCaseAutomationNext(...args) {
      // This method is invoked directly by the investigator-facing Next button.
      // A deliberate restart/continue should not inherit the prior run's 30-second
      // timestamp guard; the stop-generation token still protects the stopped run.
      this.caseWorkflowRawDataTool88UserStopRequestedAtMs = 0;
      this.markEmptyCaseStarterNudgeInvestigationStarted('next-action-started');
      if (this.caseAutomation?.skipResolvableReview) {
        await this.reconcileCaseRootChronologyAndPathLabels({
          source: 'case-automation-next-live-intake-preflight',
          sort: true,
          silent: true
        });
      }
      await this.reconcileCaseWorkflowSpreadsheetBackedIntake?.({
        source: 'case-automation-next-live-intake-preflight',
        settleMs: 20
      });
      this.reconcileCaseWorkflowStatefulGeneratedReviewManualPause({ source: 'case-automation-next-preflight' });
      const nextColumnHState = this.reconcileCaseWorkflowWwwUsdReferenceCompletionState({ source: 'case-automation-next-preflight' });
      const forceUsdGate = Boolean(nextColumnHState)
        && Number(nextColumnHState.referenceRowCount || 0) > 0
        && !nextColumnHState.columnHComplete
        && this.caseWorkflowExternalAttributionReviewIsCurrent();
      const usdGate = await this.runCaseWorkflowWwwUsdReferenceGateIfNeeded({
        source: 'case-automation-next-www-usd-references',
        step: this.recommendedWorkflowStep,
        force: forceUsdGate
      });
      if (usdGate?.handled) return usdGate;
      const attributionGate = await this.runCaseWorkflowAddressAttributionFillGateIfNeeded({
        source: 'case-automation-next-address-attribution-fill',
        step: this.recommendedWorkflowStep,
        continueAfter: false
      });
      if (attributionGate?.handled) return attributionGate;
      const generatedGuardShouldContinue = Boolean(this.caseAutomation?.active || this.caseAutomation?.busy);
      const generatedGate = await this.runCaseWorkflowStatefulGeneratedReviewGateIfNeeded({
        source: 'case-automation-next-stateful-generated-review',
        step: this.recommendedWorkflowStep,
        continueAfter: generatedGuardShouldContinue
      });
      if (generatedGate?.handled) return generatedGate;
      if (this.caseWorkflowShouldUseTool88RawData(this.recommendedWorkflowStep)) {
        const tool88Result = await this.runCaseWorkflowTool88RawDataSolver({ source: 'next', continueAfter: false });
        if (tool88Result?.success || tool88Result?.handled) return tool88Result;
      }
      const planBefore = this.caseAutomation?.lastPlan ? { ...(this.caseAutomation.lastPlan || {}) } : null;
      const stepBefore = this.recommendedWorkflowStep ? { ...(this.recommendedWorkflowStep || {}) } : null;
      this.queueCaseWorkflowWwwUsdReferenceAutoGuard({
        source: 'case-automation-next-watchdog',
        delayMs: 80,
        continueAfter: false,
        maxMs: 30000
      });
      this.queueCaseWorkflowStatefulGeneratedReviewAutoGuard({
        source: 'case-automation-next-generated-watchdog',
        delayMs: 140,
        continueAfter: Boolean(this.caseAutomation?.active || this.caseAutomation?.busy),
        maxMs: 45000
      });
      const result = await this.callCaseBuddyOriginalMethod(caseBuddyAutomationMethods, 'runCaseAutomationNext', args);
      await this.normalizeCaseWorkflowAttributionColumnsIfNeeded({ reason: 'Normalized Column K / Column X attribution after workflow Next.' });
      this.reconcileCaseWorkflowAddressAttributionFillCompletionState({ source: 'case-automation-next-followup-attribution-reconcile' });
      const attributionGateAfter = await this.runCaseWorkflowAddressAttributionFillGateIfNeeded({
        source: 'case-automation-next-followup-address-attribution-fill',
        step: this.recommendedWorkflowStep,
        force: Boolean(this.caseAutomation?.skipResolvableReview),
        continueAfter: false
      });
      if (attributionGateAfter?.handled) return { ...(attributionGateAfter || {}), previousResult: result };
      const planAfter = this.caseAutomation?.lastPlan ? { ...(this.caseAutomation.lastPlan || {}) } : null;
      [planAfter, planBefore, stepBefore].forEach((candidate) => {
        if (!candidate || !(this.caseWorkflowAutoSoftStepConfig(candidate) || this.caseWorkflowAutoSoftStepConfig({ tag: candidate.stepTag || candidate.tag, title: candidate.stepTitle || candidate.title, method: candidate.method, action: candidate.action }))) return;
        this.recordCaseWorkflowAutoSoftStepComplete({
          tag: candidate.stepTag || candidate.tag,
          title: candidate.stepTitle || candidate.title,
          method: candidate.method,
          action: candidate.action,
          status: candidate.status
        }, { action: candidate.action, method: candidate.method }, {
          source: 'workflow-next-auto-soft-completion',
          method: candidate.method || '',
          actionId: candidate.action || ''
        });
      });
      const scopedAssistantBoundary = Boolean(this.caseWorkflowAssistantScopedAutomationBoundaryActive?.());
      if (!scopedAssistantBoundary && this.caseAutomation?.skipResolvableReview && this.caseWorkflowAllStepsComplete?.()) {
        const finalPackageResult = await this.runCaseWorkflowFinalRubberStampTail('CASE_COMPLETION_SUMMARY', {
          source: 'case-automation-next-complete-package-reconcile',
          methodName: 'runCaseAutomationNext',
          force: true,
          reason: 'Workflow cards are complete; rebuilding and reviewing the final report package before declaring complete.'
        });
        if (finalPackageResult?.handled) return { ...(finalPackageResult || {}), previousResult: result };
      }
      if (!scopedAssistantBoundary) this.caseWorkflowFinalizeAutomationIfComplete('Workflow Next completed all steps.');
      return result;
    },

    formatCaseWorkflowColumnHUsdCellValue(value = null) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return '';
      return numeric.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    recordCaseWorkflowColumnHWriteAudit(event = {}) {
      try {
        const entry = {
          atUtc: new Date().toISOString(),
          stage: normalizeCell(event.stage || event.source || 'column-h-audit'),
          source: normalizeCell(event.source || ''),
          caseDataRevision: Number(this.caseSupportState?.caseDataRevision) || 0,
          activeSheet: normalizeCell(this.$refs.inputSpreadsheet?.activeSheetName || this.$refs.inputSpreadsheet?.getActiveSheet?.()?.name || ''),
          ...(event || {})
        };
        const list = Array.isArray(this.caseWorkflowColumnHWriteAudit) ? this.caseWorkflowColumnHWriteAudit.slice() : [];
        list.unshift(entry);
        this.caseWorkflowColumnHWriteAudit = list.slice(0, 40);
      } catch (error) {
        console.warn('Case Buddy could not record Column H write audit event.', error);
      }
    },

    recordCaseWorkflowSaplingPreparationAudit(event = {}) {
      try {
        const entry = {
          atUtc: new Date().toISOString(),
          stage: normalizeCell(event.stage || event.source || 'sapling-preparation-audit'),
          source: normalizeCell(event.source || ''),
          caseDataRevision: Number(this.caseSupportState?.caseDataRevision) || 0,
          tool68ModalOpen: Boolean(this.showTool68PreparationModal),
          tool68OpenedByAuto: Boolean(this.caseWorkflowTool68PreparationOpenedByAuto),
          ...(event || {})
        };
        const list = Array.isArray(this.caseWorkflowSaplingPreparationAudit) ? this.caseWorkflowSaplingPreparationAudit.slice() : [];
        list.unshift(entry);
        this.caseWorkflowSaplingPreparationAudit = list.slice(0, 40);
      } catch (error) {
        console.warn('Case Buddy could not record graph preparation audit event.', error);
      }
    },

    caseWorkflowSheetKeyPreservesColumnH(sheetKey = '') {
      const key = this.caseManualNormalizeText ? this.caseManualNormalizeText(sheetKey).toLowerCase() : normalizeCell(sheetKey).toLowerCase();
      return ['main', 'main data', 'input', 'input data'].includes(key);
    },

    captureCaseWorkflowColumnHRootValues() {
      try {
        const state = this.getCaseWorkflowWwwUsdReferenceDirectState({ originalState: null });
        const rows = Array.isArray(state?.referenceRows) ? state.referenceRows : [];
        return rows
          .filter(row => row?.hasUsableColumnH && normalizeCell(row.currentColumnH) && normalizeCell(row.transactionHash || row.hash))
          .map(row => ({
            rowIndex: Number(row.rowIndex),
            spreadsheetRowNumber: Number(row.spreadsheetRowNumber) || Number(row.rowIndex) + 1,
            pathLabel: normalizeCell(row.pathLabel),
            transactionHash: normalizeCell(row.transactionHash || row.hash).toLowerCase(),
            currentColumnH: normalizeCell(row.currentColumnH)
          }));
      } catch (error) {
        console.warn('Case Buddy could not capture existing Column H root values before a graph rewrite.', error);
        return [];
      }
    },

    async restoreCaseWorkflowColumnHRootValues(snapshot = [], options = {}) {
      const preserved = (Array.isArray(snapshot) ? snapshot : [])
        .filter(item => item?.transactionHash && normalizeCell(item.currentColumnH));
      if (!preserved.length) {
        this.recordCaseWorkflowColumnHWriteAudit?.({
          stage: 'restore-root-column-h-skip-empty-snapshot',
          source: options.source || options.reason || 'restore-root-column-h',
          preservedCount: 0
        });
        return { changed: false, restoredCount: 0 };
      }
      const byHash = new Map(preserved.map(item => [normalizeCell(item.transactionHash).toLowerCase(), item]));
      const source = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('main') : [];
      const matrix = normalizeCaseMatrix(Array.isArray(source) ? source : []);
      let restoredCount = 0;
      const nextMatrix = matrix.map((row, index) => {
        const normalized = this.caseManualEnsureRowWidth
          ? this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))
          : (Array.isArray(row) ? row.slice() : []);
        if (index <= 0 || !rowHasMeaningfulData(normalized)) return normalized;
        const hash = normalizeCell(normalized[CASE_MANUAL_START_COL.TX_HASH] || normalized[1]).toLowerCase();
        const preservedRow = byHash.get(hash);
        if (!preservedRow) return normalized;
        const currentColumnH = normalizeCell(normalized[7]);
        if (currentColumnH) return normalized;
        normalized[7] = preservedRow.currentColumnH;
        restoredCount += 1;
        return normalized;
      });
      if (!restoredCount) {
        this.recordCaseWorkflowColumnHWriteAudit?.({
          stage: 'restore-root-column-h-no-empty-targets',
          source: options.source || options.reason || 'restore-root-column-h',
          preservedCount: preserved.length,
          restoredCount: 0
        });
        return { changed: false, restoredCount: 0 };
      }
      if (typeof this.replaceCaseManualStartLogicalSheetMatrix === 'function') {
        await this.replaceCaseManualStartLogicalSheetMatrix(
          'main',
          nextMatrix,
          options.reason || 'Preserved root Column H USD values while rewriting prepared graph rows.',
          { autosaveDelayMs: options.autosaveDelayMs || 900 }
        );
      } else if (this.$refs.inputSpreadsheet?.loadData) {
        this.$refs.inputSpreadsheet.loadData(nextMatrix);
        if (typeof this.markCaseDataDirty === 'function') {
          this.markCaseDataDirty(options.reason || 'Preserved root Column H USD values while rewriting prepared graph rows.');
        }
      }
      await this.caseWorkflowUiNextTick?.();
      this.recordCaseWorkflowColumnHWriteAudit?.({
        stage: 'restore-root-column-h-complete',
        source: options.source || options.reason || 'restore-root-column-h',
        preservedCount: preserved.length,
        restoredCount,
        rows: preserved.slice(0, 12).map(item => ({
          pathLabel: item.pathLabel || '',
          hash: item.transactionHash || '',
          columnH: item.currentColumnH || ''
        }))
      });
      return { changed: true, restoredCount };
    },

    reconcileCaseWorkflowWwwUsdReferenceCompletionState(options = {}) {
      let state = null;
      try {
        state = this.getCaseWorkflowWwwUsdReferenceDirectState({ originalState: null });
      } catch (error) {
        console.warn('Case Buddy could not reconcile Column H USD reference workflow state.', error);
        return null;
      }
      if (state?.columnHComplete || Number(state?.referenceRowCount || 0) <= 0) return state;
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) }
      };
      ['WHAT_WENT_WHERE_REVIEW_UPDATED', 'WWW_USD_REFERENCES_REVIEWED', 'WWW_USD_REFERENCES', 'WHAT_WENT_WHERE_USD_REFERENCES_REVIEWED'].forEach((code) => {
        if (workflow.signalCompleteByCode?.[code]) delete workflow.signalCompleteByCode[code];
      });
      if (workflow.whatWentWhereUsdReferenceReview) {
        workflow.whatWentWhereUsdReferenceReview = {
          ...(workflow.whatWentWhereUsdReferenceReview || {}),
          reviewedAtUtc: '',
          basisHash: '',
          revision: 0,
          staleBecauseColumnHMissing: true,
          staleAtUtc: now,
          staleSource: options.source || 'workflow-column-h-reconcile'
        };
      }
      this.caseSupportState.workflowState = workflow;
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        www_usd_references: {
          id: 'www_usd_references',
          status: 'warn',
          checkedAtUtc: now,
          revision: Number(this.caseSupportState.caseDataRevision) || 0,
          summary: state.summary || 'Column H USD reference values are missing.',
          message: state.summary || 'Column H USD reference values are missing.',
          details: {
            stepTag: 'WWW_USD_REFERENCES',
            referenceRowCount: Number(state.referenceRowCount || 0),
            usableReferenceCount: Number(state.usableReferenceCount || 0),
            missingUsdReferenceCount: Number(state.missingUsdReferenceCount || 0),
            source: options.source || 'workflow-column-h-reconcile',
            spreadsheetIsTruth: true
          }
        }
      };
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
      return state;
    },

    getCaseWorkflowWwwUsdReferenceStepForPause(options = {}) {
      const candidates = [
        options.step,
        this.caseAutomation?.lastPlan,
        this.caseAutomation?.manualPause,
        this.recommendedWorkflowStep,
        this.caseWorkflowOrderedStepDefinitions().find(step => this.isCaseWorkflowWwwUsdReferenceStep(step)),
        this.caseWorkflowWwwUsdReferenceDefaultStep()
      ].filter(Boolean);
      return candidates.find(candidate => this.isCaseWorkflowWwwUsdReferenceStep(candidate)) || candidates[candidates.length - 1];
    },

    openWwwUsdReferenceReviewModal(...args) {
      const original = this.callFirstCaseBuddyOriginalMethod('openWwwUsdReferenceReviewModal', args);
      if (original !== undefined) return original;
      this.showWwwUsdReferenceModal = true;
      this.wwwUsdReferenceViewFilter = Number(this.wwwUsdReferenceState?.missingUsdReferenceCount || 0) > 0 ? 'missing' : 'all';
      this.wwwUsdReferenceStatus = 'Use real day-of USD values where possible. Built-in estimates are rough drafts only and should be verified before reporting.';
      return { handled: true, success: true, source: 'open-www-usd-reference-review-modal-fallback' };
    },

    openWwwUsdReferenceModal(...args) {
      return this.openWwwUsdReferenceReviewModal(...args);
    },

    reviewWwwUsdReferences(...args) {
      return this.openWwwUsdReferenceReviewModal(...args);
    },

    recordCaseWorkflowWwwUsdReferenceReviewComplete(context = {}) {
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const now = this.emptyCaseStarterNudgeNowUtc ? this.emptyCaseStarterNudgeNowUtc() : new Date().toISOString();
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) }
      };
      let state = null;
      try {
        state = this.getCaseWorkflowWwwUsdReferenceDirectState();
      } catch (error) {
        console.warn('Case Buddy could not build USD reference review state while recording workflow completion.', error);
      }
      if (!state?.columnHComplete) {
        const missingCount = Number(state?.missingUsdReferenceCount || 0);
        const total = Number(state?.referenceRowCount || 0);
        this.wwwUsdReferenceStatus = total > 0
          ? `${missingCount}/${total} top-level row${total === 1 ? '' : 's'} still need Column H USD values before this review can be marked complete.`
          : 'No top-level rows were available for Column H USD reference review.';
        return false;
      }
      workflow.whatWentWhereUsdReferenceReview = {
        ...(workflow.whatWentWhereUsdReferenceReview || {}),
        reviewedAtUtc: now,
        basisHash: state?.currentBasisHash || workflow.whatWentWhereUsdReferenceReview?.basisHash || '',
        revision,
        source: context.source || 'workflow-www-usd-references',
        autoApplied: !!context.autoApplied,
        appliedCount: Number(context.appliedCount || 0),
        roughEstimateNotice: 'Built-in Column H estimates are rough reference values and should be replaced with real day-of pricing where possible.'
      };
      ['WWW_USD_REFERENCES_REVIEWED', 'WWW_USD_REFERENCES', 'WHAT_WENT_WHERE_USD_REFERENCES_REVIEWED', 'WHAT_WENT_WHERE_REVIEW_UPDATED'].forEach((code) => {
        workflow.signalCompleteByCode[code] = {
          ...(workflow.signalCompleteByCode[code] || {}),
          completedAtUtc: now,
          reviewedAtUtc: now,
          source: context.source || 'workflow-www-usd-references',
          stepTag: 'WWW_USD_REFERENCES',
          appliedCount: Number(context.appliedCount || 0),
          autoApplied: !!context.autoApplied
        };
      });
      this.caseSupportState.workflowState = workflow;
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        www_usd_references: {
          id: 'www_usd_references',
          status: state?.canReview === false ? 'warn' : 'pass',
          checkedAtUtc: now,
          revision,
          summary: state?.summary || 'Top-level Column H USD references reviewed.',
          details: {
            stepTag: 'WWW_USD_REFERENCES',
            referenceRowCount: Number(state?.referenceRowCount || 0),
            usableReferenceCount: Number(state?.usableReferenceCount || 0),
            missingUsdReferenceCount: Number(state?.missingUsdReferenceCount || 0),
            autoApplied: !!context.autoApplied,
            appliedCount: Number(context.appliedCount || 0)
          }
        }
      };
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      return true;
    },

    pauseCaseWorkflowWwwUsdReferenceAuto(message = '', options = {}) {
      const step = this.getCaseWorkflowWwwUsdReferenceStepForPause(options);
      const now = this.emptyCaseStarterNudgeNowUtc ? this.emptyCaseStarterNudgeNowUtc() : new Date().toISOString();
      const detail = normalizeCell(message) || 'Top-level Column H USD reference values need investigator review before cleanup.';
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        active: false,
        busy: false,
        paused: true,
        status: 'Column H USD references need attention',
        detail,
        manualPause: {
          type: 'manual-pause',
          pauseKind: 'www-usd-references',
          stepTag: normalizeCell(step?.stepTag || step?.tag || 'WWW_USD_REFERENCES'),
          stepTitle: step?.stepTitle || step?.title || 'Review top-level USD reference values',
          title: 'Review Column H USD reference values',
          message: detail,
          primaryLabel: 'Open Column H review',
          primaryMethod: 'openWwwUsdReferenceReviewModal',
          openedAtUtc: now
        }
      };
      this.wwwUsdReferenceStatus = detail;
      this.showCaseAutomationPauseModal = true;
      return { handled: true, success: false, paused: true, reason: options.reason || 'www-usd-references-need-attention', message: detail };
    },

    async autoResolveWwwUsdReferencesForWorkflow(options = {}) {
      const source = options.source || 'workflow-auto-www-usd-references';
      const beforeRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      let state = this.getCaseWorkflowWwwUsdReferenceDirectState();
      let completion = this.getCaseWorkflowWhatWentWhereReferenceCompletionFromMain();
      let appliedReferences = 0;
      this.recordCaseWorkflowColumnHWriteAudit?.({
        stage: 'auto-resolve-www-usd-start',
        source,
        beforeRevision,
        referenceRowCount: Number(state.referenceRowCount || 0),
        usableReferenceCount: Number(state.usableReferenceCount || 0),
        missingUsdReferenceCount: Number(state.missingUsdReferenceCount || 0),
        rows: (state.referenceRows || []).slice(0, 12).map(row => ({
          rowIndex: row.rowIndex,
          pathLabel: row.pathLabel,
          hash: row.transactionHash,
          columnH: row.currentColumnH,
          hasUsableColumnH: Boolean(row.hasUsableColumnH),
          amount: row.referenceAmount,
          token: row.referenceToken,
          utcDate: row.utcDate
        }))
      });

      const missingRows = Array.isArray(state.referenceRows)
        ? state.referenceRows.filter(row => !row.hasUsableColumnH)
        : completion.missingRows;

      if (missingRows.length > 0) {
        const estimateState = this.buildCaseWorkflowWhatWentWhereAutoReferenceEstimates({
          ...state,
          referenceRows: Array.isArray(state.referenceRows) && state.referenceRows.length ? state.referenceRows : completion.referenceRows
        });
        if (estimateState.failures.length > 0) {
          const firstFailure = estimateState.failures[0];
          return {
            handled: true,
            success: false,
            reason: 'www-usd-reference-estimate-failed',
            message: `Column H USD references need real day-of values. The rough estimator could not draft at least one row: ${firstFailure?.status || 'missing amount, token, or date.'}`,
            failures: estimateState.failures
          };
        }
        const applyResult = await this.applyCaseWorkflowWhatWentWhereReferenceEstimates(estimateState.estimates, {
          beforeRevision,
          preservedStepTags: this.captureCaseWorkflowColumnHReferencePreservedStepTags({ boundaryIndex: this.caseWorkflowColumnHReferenceStepBoundaryIndex() }),
          source
        });
        appliedReferences = Number(applyResult.appliedCount || 0);
        if (applyResult.changed) await this.caseWorkflowUiNextTick?.();
      }

      state = this.getCaseWorkflowWwwUsdReferenceDirectState();
      completion = this.getCaseWorkflowWhatWentWhereReferenceCompletionFromMain();
      const referencesComplete = Boolean(state.columnHComplete && completion.isComplete);
      if (!referencesComplete) {
        const missingCount = Number(state.missingUsdReferenceCount || completion.missingCount || 0);
        const total = Number(state.referenceRowCount || completion.referenceRowCount || 0);
        this.recordCaseWorkflowColumnHWriteAudit?.({
          stage: 'auto-resolve-www-usd-still-missing',
          source,
          beforeRevision,
          afterRevision: Number(this.caseSupportState?.caseDataRevision) || beforeRevision,
          referenceRowCount: total,
          missingUsdReferenceCount: missingCount,
          rows: (state.referenceRows || []).slice(0, 12).map(row => ({
            rowIndex: row.rowIndex,
            pathLabel: row.pathLabel,
            hash: row.transactionHash,
            columnH: row.currentColumnH,
            hasUsableColumnH: Boolean(row.hasUsableColumnH)
          }))
        });
        return {
          handled: true,
          success: false,
          reason: 'www-usd-references-still-missing',
          message: total > 0
            ? `${Math.max(0, total - missingCount)}/${total} top-level main transaction rows have usable Column H USD values. Enter real day-of values or apply reviewed estimates before cleanup.`
            : 'No top-level main transaction reference rows were available for Column H USD valuation.'
        };
      }

      this.recordCaseWorkflowWwwUsdReferenceReviewComplete({
        source,
        autoApplied: appliedReferences > 0,
        appliedCount: appliedReferences
      });
      this.recordCaseWorkflowColumnHWriteAudit?.({
        stage: 'auto-resolve-www-usd-complete',
        source,
        beforeRevision,
        afterRevision: Number(this.caseSupportState?.caseDataRevision) || beforeRevision,
        appliedReferences,
        referenceRowCount: Number(state.referenceRowCount || completion.referenceRowCount || 0),
        usableReferenceCount: Number(state.usableReferenceCount || 0),
        missingUsdReferenceCount: Number(state.missingUsdReferenceCount || 0),
        rows: (state.referenceRows || []).slice(0, 12).map(row => ({
          rowIndex: row.rowIndex,
          pathLabel: row.pathLabel,
          hash: row.transactionHash,
          columnH: row.currentColumnH,
          hasUsableColumnH: Boolean(row.hasUsableColumnH)
        }))
      });
      this.wwwUsdReferenceStatus = appliedReferences > 0
        ? `Workflow Auto applied ${appliedReferences} rough Column H USD estimate${appliedReferences === 1 ? '' : 's'}. Replace with real day-of values if a report needs exact pricing.`
        : 'Column H USD references are already filled and the review was marked complete.';
      this.appendCaseWorkflowAutomationLog?.(
        appliedReferences > 0 ? 'Applied rough Column H USD reference estimates.' : 'Marked Column H USD references reviewed.',
        { stepTag: 'WWW_USD_REFERENCES', appliedReferences, source }
      );
      return { handled: true, success: true, appliedReferences, source };
    },

    async runCaseWorkflowWwwUsdReferenceAutomationStep(options = {}) {
      const step = options.step || this.getCaseWorkflowWwwUsdReferenceStepForPause(options);
      const action = options.action || null;
      const skipMode = Boolean(this.caseAutomation?.skipResolvableReview);
      if (!skipMode) {
        return this.pauseCaseWorkflowWwwUsdReferenceAuto(
          'Before cleanup, enter a real day-of USD value in Column H for each root/main transaction row. The built-in estimate buttons can fill rough draft values, but they are not down-to-the-day pricing; verify with CoinMarketCap or another trusted historical price source before using the values in a report.',
          { step, action, reason: 'www-usd-references-review-required' }
        );
      }
      const suppressUi = options.suppressUi !== false;
      if (suppressUi) this.beginCaseWorkflowReviewAutoUiSuppression();
      try {
        this.caseAutomation = {
          ...(this.caseAutomation || {}),
          status: 'Estimating Column H USD references…',
          detail: '100% Automated Mode is on, so Case Buddy is applying rough local USD estimates where Column H is blank.'
        };
        this.appendCaseWorkflowAutomationLog?.('Estimating Column H USD references…', {
          stepTag: 'WWW_USD_REFERENCES',
          action: normalizeCell(action?.id || action?.key || action?.action || '')
        });
        const result = await this.autoResolveWwwUsdReferencesForWorkflow({
          source: options.source || 'workflow-auto-www-usd-references',
          step,
          action
        });
        if (result?.success) return result;
        return this.pauseCaseWorkflowWwwUsdReferenceAuto(result?.message || 'Column H USD references could not be completed automatically.', {
          step,
          action,
          reason: result?.reason || 'www-usd-references-auto-failed'
        });
      } finally {
        if (suppressUi) this.endCaseWorkflowReviewAutoUiSuppression();
      }
    },

    caseWorkflowStatefulReviewTagRequiresLiveVerification(tag = '') {
      return CASE_WORKFLOW_STATEFUL_REVIEW_TAGS.has(normalizeCell(tag).toUpperCase());
    },

    caseWorkflowStatefulGeneratedReviewConfig(tagOrOptions = {}) {
      const tag = normalizeCell(typeof tagOrOptions === 'string' ? tagOrOptions : (tagOrOptions.tag || tagOrOptions.stepTag)).toUpperCase();
      const configs = {
        ADDRESS_LEGEND: {
          tag: 'ADDRESS_LEGEND',
          title: 'Build and review the address legend',
          stateName: 'addressLegendAnalysis',
          refreshMethods: ['refreshAddressLegendReview'],
          stageMethods: ['rebuildAddressLegend'],
          markMethod: 'markAddressLegendReviewComplete',
          signalCodes: ['ADDRESS_LEGEND_REVIEWED', 'ADDRESS_LEGEND_BUILT', 'ADDRESS_LEGEND'],
          checkIds: ['address_legend'],
          canBuild: state => Boolean((Number(state?.candidateCount) || 0) > 0 || state?.buildIsCurrent),
          canAttemptMark: state => Boolean(state?.buildIsCurrent),
          isCurrent: state => Boolean(state?.reviewIsCurrent)
        },
        TEMPLATE_DELIVERABLE: {
          tag: 'TEMPLATE_DELIVERABLE',
          title: 'Build the template deliverable',
          stateName: 'templateDeliverableState',
          ensureTags: ['ADDRESS_LEGEND'],
          refreshMethods: ['refreshTemplateDeliverableReview'],
          stageMethods: ['stageTemplateDeliverablePreview'],
          markMethod: 'markTemplateDeliverableReviewComplete',
          signalCodes: ['TEMPLATE_DELIVERABLE_REVIEWED', 'TEMPLATE_DELIVERABLE'],
          checkIds: ['template_deliverable'],
          canBuild: state => Boolean(state?.canBuild || state?.buildIsCurrent),
          isCurrent: state => Boolean(state?.reviewIsCurrent)
        },
        SUSPICIOUS_ADDRESS_TABLE: {
          tag: 'SUSPICIOUS_ADDRESS_TABLE',
          title: 'Build the suspicious address table',
          stateName: 'suspiciousAddressTableState',
          ensureTags: ['ADDRESS_LEGEND'],
          refreshMethods: ['refreshSuspiciousAddressTableReview'],
          stageMethods: ['stageSuspiciousAddressTablePreview'],
          markMethod: 'markSuspiciousAddressTableReviewComplete',
          signalCodes: ['SUSPICIOUS_ADDRESS_TABLE_REVIEWED', 'SUSPICIOUS_ADDRESS_TABLE'],
          checkIds: ['suspicious_address_table'],
          canBuild: state => Boolean(state?.canBuild || state?.legendReviewIsCurrent),
          isCurrent: state => Boolean(state?.reviewIsCurrent)
        },
        EXCHANGE_ADDRESS_TABLE: {
          tag: 'EXCHANGE_ADDRESS_TABLE',
          title: 'Build and review the exchange address table',
          stateName: 'exchangeAddressTableState',
          ensureTags: ['ADDRESS_LEGEND'],
          refreshMethods: ['refreshExchangeAddressTableReview'],
          stageMethods: ['stageExchangeAddressTablePreview'],
          markMethod: 'markExchangeAddressTableReviewComplete',
          signalCodes: ['EXCHANGE_ADDRESS_TABLE_REVIEWED', 'EXCHANGE_ADDRESS_TABLE'],
          checkIds: ['exchange_address_table'],
          canBuild: state => Boolean(state?.canBuild || state?.legendReviewIsCurrent),
          isCurrent: state => Boolean(state?.reviewIsCurrent)
        },
        DORMANT_ADDRESS_DELIVERABLES: {
          tag: 'DORMANT_ADDRESS_DELIVERABLES',
          title: 'Build dormant address deliverables',
          stateName: 'dormantAddressDeliverableState',
          ensureTags: ['ADDRESS_LEGEND'],
          refreshMethods: ['refreshDormantAddressDeliverablesReview'],
          stageMethods: ['stageDormantAddressTablePreview', 'stageDormantAddressMonitoringPreview'],
          markMethod: 'markDormantAddressDeliverablesReviewComplete',
          signalCodes: ['DORMANT_ADDRESS_DELIVERABLES_REVIEWED', 'DORMANT_ADDRESS_DELIVERABLES'],
          checkIds: ['dormant_address_deliverables'],
          canBuild: state => Boolean(state?.canBuild || state?.legendReviewIsCurrent),
          isCurrent: state => Boolean(state?.reviewIsCurrent)
        },
        WHAT_WENT_WHERE: {
          tag: 'WHAT_WENT_WHERE',
          title: 'Build and review What Went Where',
          stateName: 'whatWentWhereState',
          refreshMethods: ['refreshWhatWentWhereReview'],
          stageMethods: ['stageWhatWentWherePreview', 'stageWhatWentWhereValuationAudit'],
          markMethod: 'markWhatWentWhereReviewComplete',
          signalCodes: ['WHAT_WENT_WHERE_REVIEWED', 'WHAT_WENT_WHERE', 'WHAT_WENT_WHERE_REVIEW_UPDATED'],
          checkIds: ['what_went_where'],
          canBuild: state => Boolean(state?.canBuild),
          isCurrent: state => Boolean(state?.reviewIsCurrent)
        },
        CASE_CONCLUSION: {
          tag: 'CASE_CONCLUSION',
          title: 'Build and review case conclusion',
          stateName: 'caseConclusionState',
          ensureTags: ['WHAT_WENT_WHERE'],
          refreshMethods: ['refreshCaseConclusionReview'],
          prepareMethods: ['resetCaseConclusionDraftToGenerated'],
          stageMethods: ['stageCaseConclusionPreview'],
          markMethod: 'markCaseConclusionReviewComplete',
          signalCodes: ['CASE_CONCLUSION_REVIEWED', 'CASE_CONCLUSION'],
          checkIds: ['case_conclusion'],
          canBuild: state => Boolean(state?.canBuild),
          isCurrent: state => Boolean(state?.reviewIsCurrent)
        },
        REPORT_BODY: {
          tag: 'REPORT_BODY',
          title: 'Build and review report body',
          stateName: 'reportBodyState',
          ensureTags: ['ADDRESS_LEGEND', 'CASE_CONCLUSION'],
          refreshMethods: ['refreshReportBodyReview'],
          stageMethods: ['stageReportBodyPreview'],
          markMethod: 'markReportBodyReviewComplete',
          signalCodes: ['REPORT_BODY_REVIEWED', 'REPORT_BODY'],
          checkIds: ['report_body'],
          canBuild: state => Boolean(state?.canBuild),
          isCurrent: state => Boolean(state?.reviewIsCurrent)
        },
        PATH_ENDING_ADDENDUM_REVIEW: {
          tag: 'PATH_ENDING_ADDENDUM_REVIEW',
          title: 'Review exchange path-ending rows for addendum',
          stateName: 'addendumPathEndingReviewState',
          ensureTags: ['REPORT_BODY'],
          refreshMethods: ['refreshAddendumPathEndingReview'],
          stageMethods: ['stageAddendumPathEndingReviewRows', 'stageAddendumPathEndingReviewAudit'],
          markMethod: 'markAddendumPathEndingReviewComplete',
          signalCodes: ['PATH_ENDING_ADDENDUM_REVIEWED', 'PATH_ENDING_ROWS_REVIEWED', 'PATH_ENDING_ADDENDUM_REVIEW'],
          checkIds: ['addendum_path_endings'],
          canBuild: state => Boolean(state?.canReview || state?.prerequisiteCanBuild),
          isCurrent: state => Boolean(state?.reviewIsCurrent)
        },
        ADDENDUM_RECIPIENT_ENTITY: {
          tag: 'ADDENDUM_RECIPIENT_ENTITY',
          title: 'Resolve recipient entity for addendum rows',
          stateName: 'addendumRecipientEntityReviewState',
          ensureTags: ['PATH_ENDING_ADDENDUM_REVIEW'],
          refreshMethods: ['refreshAddendumRecipientEntityReview'],
          stageMethods: ['stageAddendumRecipientEntityRows', 'stageAddendumRecipientEntityAudit'],
          markMethod: 'markAddendumRecipientEntityReviewComplete',
          signalCodes: ['ADDENDUM_RECIPIENT_ENTITY_REVIEWED', 'ADDENDUM_RECIPIENT_ENTITY'],
          checkIds: ['addendum_recipient_entities'],
          canBuild: state => Boolean(state?.buildIsCurrent && Number(state?.noCandidateEntityCount || 0) === 0 && Number(state?.needsChoiceCount || 0) === 0),
          isCurrent: state => Boolean(state?.reviewIsCurrent)
        },
        ADDENDUM_BUILD: {
          tag: 'ADDENDUM_BUILD',
          title: 'Build and review addendum',
          stateName: 'caseAddendumState',
          ensureTags: ['ADDENDUM_RECIPIENT_ENTITY'],
          refreshMethods: ['refreshCaseAddendumReview'],
          stageMethods: ['stageCaseAddendumPreview', 'stageCaseAddendumNarrativePreview', 'stageCaseAddendumAudit'],
          markMethod: 'markCaseAddendumReviewComplete',
          signalCodes: ['ADDENDUM_BUILD_REVIEWED', 'ADDENDUM_BUILD'],
          checkIds: ['addendum_build'],
          canBuild: state => Boolean(state?.prep55ReviewIsCurrent && state?.prep48ReviewIsCurrent && state?.canBuild),
          isCurrent: state => Boolean(state?.reviewIsCurrent)
        }
      };
      return configs[tag] || null;
    },

    caseWorkflowStatefulGeneratedReviewReadState(configOrTag = {}) {
      const config = typeof configOrTag === 'string'
        ? this.caseWorkflowStatefulGeneratedReviewConfig(configOrTag)
        : configOrTag;
      if (!config?.stateName) return null;
      try {
        const direct = this[config.stateName];
        let state = null;
        if (typeof direct === 'function') {
          const result = direct.call(this);
          state = result && typeof result === 'object' ? result : null;
        } else {
          state = direct && typeof direct === 'object' ? direct : null;
        }
        return this.applyCaseWorkflowStatefulGeneratedReviewVerifiedOverlay?.(config, state) || state;
      } catch (error) {
        console.warn(`Case Buddy could not read state for ${config.tag || config.stateName}.`, error);
        return null;
      }
    },

    caseWorkflowReviewAutoModalKeys() {
      return [
        'showWwwUsdReferenceModal',
        'showAddressLegendModal',
        'showTemplateDeliverableModal',
        'showSuspiciousAddressTableModal',
        'showExchangeAddressTableModal',
        'showDormantAddressDeliverablesModal',
        'showWhatWentWhereModal',
        'showCaseConclusionModal',
        'showReportBodyModal',
        'showAddendumPathEndingReviewModal',
        'showAddendumRecipientEntityModal',
        'showCaseAddendumModal'
      ];
    },

    beginCaseWorkflowReviewAutoUiSuppression() {
      const depth = Number(this.caseWorkflowReviewAutoUiSuppressDepth) || 0;
      if (depth <= 0) {
        const snapshot = {};
        this.caseWorkflowReviewAutoModalKeys().forEach((key) => {
          snapshot[key] = Boolean(this[key]);
        });
        this.caseWorkflowReviewAutoUiSnapshot = snapshot;
        this.caseWorkflowReviewAutoUiSuppressed = true;
      }
      this.caseWorkflowReviewAutoUiSuppressDepth = depth + 1;
    },

    endCaseWorkflowReviewAutoUiSuppression() {
      const nextDepth = Math.max(0, (Number(this.caseWorkflowReviewAutoUiSuppressDepth) || 0) - 1);
      this.caseWorkflowReviewAutoUiSuppressDepth = nextDepth;
      if (nextDepth > 0) return;
      const snapshot = this.caseWorkflowReviewAutoUiSnapshot && typeof this.caseWorkflowReviewAutoUiSnapshot === 'object'
        ? this.caseWorkflowReviewAutoUiSnapshot
        : {};
      this.caseWorkflowReviewAutoModalKeys().forEach((key) => {
        // Auto may briefly open generated-review panels to stage/mark output. Keep any panel
        // the investigator already had open, but close panels that Auto opened only for its
        // own rubber-stamp work so Simple View does not strobe between dimmed/non-dimmed states.
        if (!snapshot[key]) this[key] = false;
      });
      this.caseWorkflowReviewAutoUiSnapshot = null;
      this.caseWorkflowReviewAutoUiSuppressed = false;
    },

    caseWorkflowStatefulGeneratedReviewWorkflowKey(tag = '') {
      const normalized = normalizeCell(tag).toUpperCase();
      const map = {
        ADDRESS_LEGEND: 'addressLegendReview',
        TEMPLATE_DELIVERABLE: 'templateDeliverableReview',
        SUSPICIOUS_ADDRESS_TABLE: 'suspiciousAddressTableReview',
        EXCHANGE_ADDRESS_TABLE: 'exchangeAddressTableReview',
        DORMANT_ADDRESS_DELIVERABLES: 'dormantAddressDeliverablesReview',
        WHAT_WENT_WHERE: 'whatWentWhereReview',
        CASE_CONCLUSION: 'caseConclusionReview',
        REPORT_BODY: 'reportBodyReview',
        PATH_ENDING_ADDENDUM_REVIEW: 'addendumPathEndingReview',
        ADDENDUM_RECIPIENT_ENTITY: 'addendumRecipientEntityReview',
        ADDENDUM_BUILD: 'addendumBuildReview'
      };
      return map[normalized] || '';
    },

    caseWorkflowPlainClone(value = null) {
      if (!value || typeof value !== 'object') return value;
      try {
        return JSON.parse(JSON.stringify(value));
      } catch {
        return Array.isArray(value) ? value.slice() : { ...value };
      }
    },

    caseWorkflowGeneratedReviewRuntimeRecord(tag = '', record = null) {
      const normalized = normalizeCell(tag).toUpperCase();
      if (!normalized || !record || typeof record !== 'object') return null;
      this.caseWorkflowStatefulGeneratedReviewRuntimeRecordByTag = {
        ...(this.caseWorkflowStatefulGeneratedReviewRuntimeRecordByTag || {}),
        [normalized]: this.caseWorkflowPlainClone(record)
      };
      return this.caseWorkflowStatefulGeneratedReviewRuntimeRecordByTag[normalized];
    },

    caseWorkflowStatefulGeneratedReviewRecordForTag(tag = '') {
      const normalized = normalizeCell(tag).toUpperCase();
      if (!normalized) return null;
      const workflow = this.caseSupportState?.workflowState || {};
      const workflowKey = this.caseWorkflowStatefulGeneratedReviewWorkflowKey(normalized);
      const candidates = [
        this.caseWorkflowStatefulGeneratedReviewRuntimeRecordByTag?.[normalized],
        workflow.statefulGeneratedReviewByTag?.[normalized],
        workflow.autoStepCompletionByTag?.[normalized],
        workflowKey ? workflow[workflowKey] : null
      ];
      return candidates.find(record => record && typeof record === 'object') || null;
    },

    caseWorkflowStatefulGeneratedReviewRecordMatchesState(tag = '', state = {}, record = null) {
      const normalized = normalizeCell(tag).toUpperCase();
      if (!normalized || !record || typeof record !== 'object') return false;
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const recordRevision = Number(record.revision ?? record.state?.revision ?? -1);
      if (recordRevision >= 0 && recordRevision === currentRevision) return true;
      const recordBasis = normalizeCell(record.currentBasisHash || record.basisHash || record.state?.basisHash || record.state?.currentBasisHash || '');
      const stateBasis = normalizeCell(state?.currentBasisHash || state?.basisHash || state?.reviewBasisHash || state?.buildBasisHash || '');
      if (recordBasis && stateBasis && recordBasis === stateBasis) return true;
      return this.caseWorkflowStatefulGeneratedReviewRecordIsFreshEnoughForOutputRevisionDrift(normalized, record);
    },

    caseWorkflowStatefulGeneratedReviewRecordIsFreshEnoughForOutputRevisionDrift(tag = '', record = null) {
      const normalized = normalizeCell(tag).toUpperCase();
      if (!normalized || !CASE_WORKFLOW_STATEFUL_REVIEW_TAGS.has(normalized) || !record || typeof record !== 'object') return false;
      // The address legend is derived directly from current Main Data. A late-added
      // root or a chronological relabel must invalidate it immediately; unlike
      // output-only support writes, even small revision drift is meaningful here.
      if (normalized === 'ADDRESS_LEGEND') return false;
      const hasVerifiedReview = Boolean(record.stateVerified || record.autoReviewed || record.skipResolvableReview || record.directMarked || record.state?.reviewIsCurrent);
      if (!hasVerifiedReview) return false;
      const source = normalizeCell(record.source || record.state?.source || '').toLowerCase();
      const sourceLooksAutomated = /workflow|generated|stateful|watchdog|auto|rubber|skip/.test(source);
      if (!sourceLooksAutomated) return false;
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const recordRevision = Number(record.revision ?? record.state?.revision ?? -1);
      const revisionDrift = currentRevision > 0 && recordRevision >= 0 ? currentRevision - recordRevision : 0;
      const driftIsSmall = revisionDrift >= 0 && revisionDrift <= CASE_WORKFLOW_STATEFUL_REVIEW_OUTPUT_REVISION_TOLERANCE;
      const reviewedAt = Date.parse(normalizeCell(record.reviewedAtUtc || record.completedAtUtc || record.checkedAtUtc || ''));
      const recentRuntime = Number.isFinite(reviewedAt) && Date.now() - reviewedAt < CASE_WORKFLOW_STATEFUL_REVIEW_RECENT_MS;
      return Boolean(driftIsSmall || recentRuntime);
    },

    caseWorkflowStatefulGeneratedReviewTrustedRecordForTag(tag = '', state = {}) {
      const normalized = normalizeCell(tag).toUpperCase();
      if (!normalized) return null;
      const record = this.caseWorkflowStatefulGeneratedReviewRecordForTag(normalized);
      if (!record) return null;
      if (this.caseWorkflowStatefulGeneratedReviewRecordMatchesState(normalized, state || {}, record)) return record;
      return null;
    },

    refreshCaseWorkflowStatefulGeneratedReviewRecordsForCurrentRevision(tags = [], context = {}) {
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const tagList = (Array.isArray(tags) && tags.length ? tags : this.caseWorkflowStatefulGeneratedReviewChainTags())
        .map(tag => normalizeCell(tag).toUpperCase())
        .filter(Boolean);
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      if (!revision) return 0;
      const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) },
        autoStepCompletionByTag: { ...((this.caseSupportState.workflowState || {}).autoStepCompletionByTag || {}) },
        statefulGeneratedReviewByTag: { ...((this.caseSupportState.workflowState || {}).statefulGeneratedReviewByTag || {}) }
      };
      const checkResults = { ...(this.caseSupportState.checkResultsById || {}) };
      let updatedCount = 0;
      tagList.forEach((tag) => {
        const config = this.caseWorkflowStatefulGeneratedReviewConfig(tag);
        if (!config) return;
        const record = this.caseWorkflowStatefulGeneratedReviewRecordForTag(tag);
        if (!record || typeof record !== 'object') return;
        const state = this.caseWorkflowStatefulGeneratedReviewReadState(config) || record.state || {};
        if (!this.caseWorkflowStatefulGeneratedReviewRecordMatchesState(tag, state, record)) return;
        const refreshedRecord = {
          ...record,
          revision,
          checkedAtUtc: now,
          revalidatedAtUtc: now,
          source: context.source || record.source || 'workflow-stateful-generated-review-revision-bridge',
          revisionBridge: true
        };
        this.caseWorkflowGeneratedReviewRuntimeRecord(tag, refreshedRecord);
        const workflowKey = this.caseWorkflowStatefulGeneratedReviewWorkflowKey(tag);
        if (workflowKey) {
          workflow[workflowKey] = {
            ...(workflow[workflowKey] || {}),
            ...refreshedRecord,
            status: 'complete',
            autoReviewed: true,
            skipResolvableReview: true
          };
        }
        workflow.statefulGeneratedReviewByTag[tag] = {
          ...(workflow.statefulGeneratedReviewByTag[tag] || {}),
          ...refreshedRecord
        };
        workflow.autoStepCompletionByTag[tag] = {
          ...(workflow.autoStepCompletionByTag[tag] || {}),
          ...refreshedRecord
        };
        const signalCodes = Array.from(new Set([tag, ...(config.signalCodes || [])].map(code => normalizeCell(code).toUpperCase()).filter(Boolean)));
        signalCodes.forEach((code) => {
          workflow.signalCompleteByCode[code] = {
            ...(workflow.signalCompleteByCode[code] || {}),
            completedAtUtc: workflow.signalCompleteByCode[code]?.completedAtUtc || refreshedRecord.completedAtUtc || now,
            reviewedAtUtc: workflow.signalCompleteByCode[code]?.reviewedAtUtc || refreshedRecord.reviewedAtUtc || now,
            checkedAtUtc: now,
            revision,
            source: context.source || workflow.signalCompleteByCode[code]?.source || 'workflow-stateful-generated-review-revision-bridge',
            stepTag: tag,
            stateVerified: true,
            revisionBridge: true
          };
        });
        (config.checkIds || []).forEach((idRaw) => {
          const id = normalizeCell(idRaw).toLowerCase();
          if (!id) return;
          checkResults[id] = {
            ...(checkResults[id] || {}),
            id,
            status: 'pass',
            message: `${config.title || tag} is current and reviewed.`,
            summary: `${config.title || tag} is current and reviewed.`,
            revision,
            checkedAtUtc: now,
            details: {
              ...((checkResults[id] || {}).details || {}),
              tag,
              source: context.source || 'workflow-stateful-generated-review-revision-bridge',
              stateVerified: true,
              revisionBridge: true
            }
          };
        });
        updatedCount += 1;
      });
      if (!updatedCount) return 0;
      this.caseSupportState.workflowState = workflow;
      this.caseSupportState.checkResultsById = checkResults;
      this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
        tag: 'CASE_COMPLETION_SUMMARY',
        stage: 'generated-review-records-rebased-to-current-revision',
        source: context.source || 'workflow-stateful-generated-review-revision-bridge',
        message: `Rebased ${updatedCount} generated-review record${updatedCount === 1 ? '' : 's'} to the current case revision after output/review staging.`,
        details: { tags: tagList, revision, updatedCount }
      });
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      return updatedCount;
    },

    caseWorkflowStatefulGeneratedReviewRecordIsCurrent(tag = '', state = {}) {
      const record = this.caseWorkflowStatefulGeneratedReviewRecordForTag(tag);
      return Boolean(record && this.caseWorkflowStatefulGeneratedReviewRecordMatchesState(tag, state, record));
    },

    caseWorkflowAddendumCacheKey() {
      return [
        normalizeCell(this.currentProjectFileId || ''),
        normalizeCell(this.caseDisplayId || ''),
        normalizeCell(this.caseDetails?.caseId || '')
      ].join('|');
    },

    maybeCacheCaseWorkflowBuildableAddendumState(state = {}, context = {}) {
      if (!state || typeof state !== 'object') return null;
      const rowCount = Number(state.resolvedRowCount || state.rowCount || state.sections?.length || 0);
      if (!state.canBuild || !state.prep55ReviewIsCurrent || !state.prep48ReviewIsCurrent || rowCount <= 0) return null;
      const clone = this.caseWorkflowPlainClone(state);
      this.caseWorkflowLastBuildableAddendumState = clone;
      this.caseWorkflowLastBuildableAddendumStateAtMs = Date.now();
      this.caseWorkflowLastBuildableAddendumStateCaseKey = this.caseWorkflowAddendumCacheKey();
      this.caseWorkflowLastBuildableAddendumStateRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      if (context.recordAudit === true) {
        this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
          tag: 'ADDENDUM_BUILD',
          stage: 'cached-buildable-addendum-state',
          source: context.source || 'stateful-generated-review-read-state',
          state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(clone) || null
        });
      }
      return clone;
    },

    caseWorkflowCachedBuildableAddendumStateIsUsable() {
      const cached = this.caseWorkflowLastBuildableAddendumState;
      if (!cached || typeof cached !== 'object') return false;
      if (this.caseWorkflowLastBuildableAddendumStateCaseKey !== this.caseWorkflowAddendumCacheKey()) return false;
      const cachedAt = Number(this.caseWorkflowLastBuildableAddendumStateAtMs) || 0;
      if (!cachedAt || Date.now() - cachedAt > 10 * 60 * 1000) return false;
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const cachedRevision = Number(this.caseWorkflowLastBuildableAddendumStateRevision) || 0;
      if (cachedRevision && currentRevision && Math.abs(currentRevision - cachedRevision) > 25) return false;
      return true;
    },

    applyCaseWorkflowAddendumBuildStateFallback(state = {}, context = {}) {
      const base = state && typeof state === 'object' ? state : {};
      if (base.canBuild && base.prep55ReviewIsCurrent && base.prep48ReviewIsCurrent) {
        this.maybeCacheCaseWorkflowBuildableAddendumState(base, context);
        return base;
      }
      const pathReviewCurrent = Boolean(
        base.prep55ReviewIsCurrent
        || this.caseWorkflowStatefulGeneratedReviewRecordIsCurrent('PATH_ENDING_ADDENDUM_REVIEW', base)
      );
      const recipientReviewCurrent = Boolean(
        base.prep48ReviewIsCurrent
        || this.caseWorkflowStatefulGeneratedReviewRecordIsCurrent('ADDENDUM_RECIPIENT_ENTITY', base)
      );
      let merged = { ...base };
      if (pathReviewCurrent) merged.prep55ReviewIsCurrent = true;
      if (recipientReviewCurrent) merged.prep48ReviewIsCurrent = true;
      if (pathReviewCurrent && recipientReviewCurrent && !base.canBuild && this.caseWorkflowCachedBuildableAddendumStateIsUsable()) {
        const cached = this.caseWorkflowPlainClone(this.caseWorkflowLastBuildableAddendumState) || {};
        merged = {
          ...base,
          ...cached,
          prep55ReviewIsCurrent: true,
          prep48ReviewIsCurrent: true,
          canBuild: true,
          summary: normalizeCell(cached.summary || cached.statusLabel || '') || 'Addendum is ready to review from the current Tool55 and Tool48 addendum review records.',
          statusLabel: normalizeCell(cached.statusLabel || cached.summary || '') || 'Ready'
        };
        if (context.recordAudit === true) {
          this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
            tag: 'ADDENDUM_BUILD',
            stage: 'using-cached-buildable-addendum-state',
            source: context.source || 'addendum-build-state-fallback',
            message: normalizeCell(base.summary || base.statusLabel || ''),
            state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(merged) || null,
            details: {
              originalState: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(base) || null,
              cachedRevision: Number(this.caseWorkflowLastBuildableAddendumStateRevision) || 0,
              currentRevision: Number(this.caseSupportState?.caseDataRevision) || 0
            }
          });
        }
      }
      if (this.caseWorkflowStatefulGeneratedReviewRecordIsCurrent('ADDENDUM_BUILD', merged)) {
        merged.reviewIsCurrent = true;
        merged.canMarkReview = false;
        merged.reviewedAtUtc = normalizeCell(this.caseWorkflowStatefulGeneratedReviewRecordForTag('ADDENDUM_BUILD')?.reviewedAtUtc || merged.reviewedAtUtc || '');
        merged.statusLabel = 'Current · reviewed';
      }
      return merged;
    },

    applyCaseWorkflowStatefulGeneratedReviewVerifiedOverlay(config = {}, state = null) {
      if (!config?.tag || !state || typeof state !== 'object') return state;
      const tag = normalizeCell(config.tag).toUpperCase();
      let merged = tag === 'ADDENDUM_BUILD'
        ? this.applyCaseWorkflowAddendumBuildStateFallback(state, { source: 'stateful-generated-review-overlay' })
        : { ...state };
      const record = this.caseWorkflowStatefulGeneratedReviewRecordForTag(tag);
      if (record && this.caseWorkflowStatefulGeneratedReviewRecordMatchesState(tag, merged, record)) {
        merged = {
          ...merged,
          reviewIsCurrent: true,
          canMarkReview: false,
          reviewedAtUtc: normalizeCell(record.reviewedAtUtc || record.completedAtUtc || merged.reviewedAtUtc || ''),
          statusLabel: normalizeCell(merged.statusLabel || merged.summary || '') || 'Current · reviewed'
        };
        if (tag === 'ADDENDUM_RECIPIENT_ENTITY') {
          merged.buildIsCurrent = true;
        }
        if (tag === 'PATH_ENDING_ADDENDUM_REVIEW') {
          merged.canReview = merged.canReview !== false;
        }
        if (tag === 'ADDENDUM_BUILD') {
          merged.prep55ReviewIsCurrent = true;
          merged.prep48ReviewIsCurrent = true;
        }
      }
      if (tag === 'ADDENDUM_BUILD') this.maybeCacheCaseWorkflowBuildableAddendumState(merged, { source: 'stateful-generated-review-overlay-final' });
      return merged;
    },

    async stabilizeCaseWorkflowAddendumBuildPrerequisitesForAuto(state = {}, context = {}) {
      const config = this.caseWorkflowStatefulGeneratedReviewConfig('ADDENDUM_BUILD');
      if (!config) return state;
      let current = this.applyCaseWorkflowAddendumBuildStateFallback(state, { source: context.source || 'addendum-build-stabilize-start', recordAudit: true }) || state || {};
      if (config.canBuild?.(current)) return current;
      const source = context.source || 'addendum-build-prerequisite-stabilize';
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const needsPath = !current.prep55ReviewIsCurrent || /tool\s*55|path[-\s]*ending/i.test(normalizeCell(current.summary || current.statusLabel || ''));
        const needsRecipient = !current.prep48ReviewIsCurrent || /tool\s*48|recipient/i.test(normalizeCell(current.summary || current.statusLabel || ''));
        this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
          tag: 'ADDENDUM_BUILD',
          stage: 'stabilize-addendum-prerequisites',
          source,
          methodName: context.methodName || '',
          state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(current) || null,
          details: { attempt, needsPath, needsRecipient }
        });
        if (needsPath) {
          await this.runCaseWorkflowStatefulGeneratedReviewMethods([
            'refreshAddendumPathEndingReview',
            'stageAddendumPathEndingReviewRows',
            'stageAddendumPathEndingReviewAudit',
            'markAddendumPathEndingReviewComplete'
          ], { tag: 'PATH_ENDING_ADDENDUM_REVIEW', source: `${source}-tool55`, stage: 'stabilize-tool55' });
          const pathConfig = this.caseWorkflowStatefulGeneratedReviewConfig('PATH_ENDING_ADDENDUM_REVIEW');
          const pathState = this.caseWorkflowStatefulGeneratedReviewReadState(pathConfig) || {};
          if (pathConfig?.isCurrent?.(pathState)) this.recordCaseWorkflowStatefulGeneratedReviewCurrent(pathConfig, pathState, { source: `${source}-tool55`, methodName: 'stabilizeCaseWorkflowAddendumBuildPrerequisitesForAuto' });
        }
        if (needsRecipient) {
          await this.runCaseWorkflowStatefulGeneratedReviewMethods([
            'refreshAddendumRecipientEntityReview',
            'saveVisibleAddendumRecipientEntityChoices',
            'stageAddendumRecipientEntityRows',
            'stageAddendumRecipientEntityAudit',
            'markAddendumRecipientEntityReviewComplete'
          ], { tag: 'ADDENDUM_RECIPIENT_ENTITY', source: `${source}-tool48`, stage: 'stabilize-tool48' });
          const recipientConfig = this.caseWorkflowStatefulGeneratedReviewConfig('ADDENDUM_RECIPIENT_ENTITY');
          const recipientState = this.caseWorkflowStatefulGeneratedReviewReadState(recipientConfig) || {};
          if (recipientConfig?.isCurrent?.(recipientState)) this.recordCaseWorkflowStatefulGeneratedReviewCurrent(recipientConfig, recipientState, { source: `${source}-tool48`, methodName: 'stabilizeCaseWorkflowAddendumBuildPrerequisitesForAuto' });
        }
        await this.caseWorkflowStatefulGeneratedReviewDelay(180 + attempt * 120);
        current = await this.refreshCaseWorkflowStatefulGeneratedReviewState(config, {
          source,
          methodName: context.methodName || '',
          stage: `refresh-after-addendum-prerequisite-stabilize-${attempt + 1}`
        });
        current = this.applyCaseWorkflowAddendumBuildStateFallback(current, { source, stage: `fallback-after-stabilize-${attempt + 1}`, recordAudit: true });
        if (config.canBuild?.(current)) return current;
      }
      return current;
    },

    async prepareCaseWorkflowStatefulGeneratedReviewForBackgroundMark(config = {}, state = {}, context = {}) {
      const latestState = state && typeof state === 'object' ? state : {};
      if (config?.tag === 'CASE_CONCLUSION') {
        const conclusionText = normalizeCell(
          this.caseConclusionDraft
          || latestState.selectedConclusionText
          || latestState.selectedText
          || latestState.generatedConclusionText
          || latestState.generatedText
          || latestState.conclusionText
        );
        if (conclusionText && normalizeCell(this.caseConclusionDraft) !== conclusionText) {
          this.caseConclusionDraft = conclusionText;
          await this.caseWorkflowUiNextTick?.();
          this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
            tag: config.tag,
            stage: 'prepared-background-draft',
            source: context.source || 'workflow-stateful-generated-review-auto',
            methodName: config.markMethod || '',
            message: 'Prepared generated case-conclusion text before background review marking.',
            state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(latestState) || null
          });
        }
      }
      return latestState;
    },

    async directMarkCaseWorkflowStatefulGeneratedReviewIfSafe(config = {}, state = {}, context = {}) {
      if (!config?.tag) return { success: false, state, reason: 'missing-config' };
      const latestState = state && typeof state === 'object' ? state : {};
      if (!this.caseAutomation?.skipResolvableReview && !context.force) {
        return { success: false, state: latestState, reason: 'skip-resolvable-reviews-off' };
      }
      if (!this.caseWorkflowStatefulGeneratedReviewCanAttemptMark(config, latestState) && !config.canBuild?.(latestState)) {
        return { success: false, state: latestState, reason: 'not-safe-to-direct-mark' };
      }
      const workflowKey = this.caseWorkflowStatefulGeneratedReviewWorkflowKey(config.tag);
      if (!workflowKey) return { success: false, state: latestState, reason: 'missing-workflow-key' };
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const basisHash = normalizeCell(latestState.currentBasisHash || latestState.basisHash || latestState.reviewBasisHash || latestState.buildBasisHash || '');
      const text = normalizeCell(
        config.tag === 'CASE_CONCLUSION'
          ? (this.caseConclusionDraft || latestState.selectedConclusionText || latestState.selectedText || latestState.generatedConclusionText || latestState.generatedText)
          : (latestState.selectedText || latestState.generatedText || latestState.summary || latestState.statusLabel)
      );
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) },
        autoStepCompletionByTag: { ...((this.caseSupportState.workflowState || {}).autoStepCompletionByTag || {}) },
        statefulGeneratedReviewByTag: { ...((this.caseSupportState.workflowState || {}).statefulGeneratedReviewByTag || {}) }
      };
      const record = {
        ...(workflow[workflowKey] || {}),
        status: 'complete',
        completedAtUtc: now,
        reviewedAtUtc: now,
        checkedAtUtc: now,
        generatedAtUtc: now,
        revision,
        source: context.source || 'workflow-stateful-generated-review-direct-mark',
        stepTag: config.tag,
        title: normalizeCell(config.title || ''),
        basisHash,
        currentBasisHash: basisHash,
        stateVerified: true,
        autoReviewed: true,
        skipResolvableReview: true,
        rowCount: Number(latestState.rowCount || latestState.rows?.length || latestState.reviewRows?.length || latestState.candidates?.length || 0),
        includedCount: Number(latestState.includedCount || 0),
        resolvedCount: Number(latestState.resolvedCount || latestState.resolvedRowCount || 0),
        selectedText: text,
        selectedConclusionText: config.tag === 'CASE_CONCLUSION' ? text : undefined,
        generatedText: normalizeCell(latestState.generatedText || latestState.generatedConclusionText || text),
        state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(latestState) || null
      };
      Object.keys(record).forEach((key) => {
        if (record[key] === undefined) delete record[key];
      });
      this.caseWorkflowGeneratedReviewRuntimeRecord(config.tag, record);
      workflow[workflowKey] = record;
      workflow.statefulGeneratedReviewByTag[config.tag] = record;
      workflow.autoStepCompletionByTag[config.tag] = record;
      const signalCodes = Array.from(new Set([config.tag, ...(config.signalCodes || [])].map(code => normalizeCell(code).toUpperCase()).filter(Boolean)));
      signalCodes.forEach((code) => {
        workflow.signalCompleteByCode[code] = {
          ...(workflow.signalCompleteByCode[code] || {}),
          completedAtUtc: now,
          reviewedAtUtc: now,
          checkedAtUtc: now,
          revision,
          source: context.source || 'workflow-stateful-generated-review-direct-mark',
          stepTag: config.tag,
          stateVerified: true,
          directMarked: true
        };
      });
      this.caseSupportState.workflowState = workflow;
      const checkResults = { ...(this.caseSupportState.checkResultsById || {}) };
      const checkIds = Array.from(new Set((config.checkIds || []).map(id => normalizeCell(id).toLowerCase()).filter(Boolean)));
      checkIds.forEach((id) => {
        checkResults[id] = {
          ...(checkResults[id] || {}),
          id,
          status: 'pass',
          message: `${config.title || config.tag} is current and reviewed.`,
          summary: `${config.title || config.tag} is current and reviewed.`,
          revision,
          checkedAtUtc: now,
          details: {
            ...((checkResults[id] || {}).details || {}),
            tag: config.tag,
            source: context.source || 'workflow-stateful-generated-review-direct-mark',
            stateVerified: true,
            directMarked: true,
            state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(latestState) || null
          }
        };
      });
      this.caseSupportState.checkResultsById = checkResults;
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(80);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
        tag: config.tag,
        stage: 'direct-review-recorded',
        source: context.source || 'workflow-stateful-generated-review-direct-mark',
        methodName: config.markMethod || '',
        state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(latestState) || null,
        details: { workflowKey, basisHash, hasText: Boolean(text) }
      });
      const refreshed = await this.refreshCaseWorkflowStatefulGeneratedReviewState(config, {
        ...context,
        stage: 'refresh-after-direct-review-record'
      });
      return { success: Boolean(config.isCurrent?.(refreshed)), state: refreshed, directMarked: true, workflowKey };
    },

    reconcileCaseWorkflowStatefulGeneratedReviewManualPause(options = {}) {
      const pause = this.caseAutomation?.manualPause || null;
      if (!pause) return false;
      const tag = normalizeCell(pause.stepTag || pause.tag || this.caseAutomation?.lastPlan?.stepTag || '').toUpperCase();
      const config = tag ? this.caseWorkflowStatefulGeneratedReviewConfig(tag) : null;
      if (!config || !this.caseWorkflowStatefulGeneratedReviewIsCurrent(tag)) return false;
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        active: false,
        busy: false,
        paused: false,
        manualPause: null,
        status: `${config.title || tag} reviewed`,
        detail: 'The generated output is already current and reviewed; the stale manual pause was cleared.'
      };
      this.showCaseAutomationPauseModal = false;
      this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
        tag,
        stage: 'stale-manual-pause-cleared',
        source: options.source || 'stateful-generated-review-manual-pause-reconcile',
        message: `Cleared stale ${pause.pauseKind || 'manual'} pause because live generated-review state is current.`,
        state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(this.caseWorkflowStatefulGeneratedReviewReadState(config)) || null
      });
      return true;
    },

    sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit(state = {}) {
      if (!state || typeof state !== 'object') return null;
      const pickNumber = value => (Number.isFinite(Number(value)) ? Number(value) : undefined);
      return {
        statusLabel: normalizeCell(state.statusLabel || state.summary || state.message || ''),
        canBuild: Boolean(state.canBuild || state.buildIsCurrent || state.canReview || state.prerequisiteCanBuild),
        canMarkReview: Boolean(state.canMarkReview || state.canReview || state.canBuild),
        reviewIsCurrent: Boolean(state.reviewIsCurrent),
        builtIsCurrent: Boolean(state.builtIsCurrent || state.buildIsCurrent || state.bothBuiltCurrent || state.prep55ReviewIsCurrent),
        prep55ReviewIsCurrent: state.prep55ReviewIsCurrent === undefined ? undefined : Boolean(state.prep55ReviewIsCurrent),
        prep48ReviewIsCurrent: state.prep48ReviewIsCurrent === undefined ? undefined : Boolean(state.prep48ReviewIsCurrent),
        buildIsCurrent: state.buildIsCurrent === undefined ? undefined : Boolean(state.buildIsCurrent),
        bothBuiltCurrent: state.bothBuiltCurrent === undefined ? undefined : Boolean(state.bothBuiltCurrent),
        rowCount: pickNumber(state.rowCount ?? state.rows?.length ?? state.reviewRows?.length ?? state.candidates?.length),
        includedCount: pickNumber(state.includedCount),
        resolvedCount: pickNumber(state.resolvedCount ?? state.resolvedRowCount),
        needsChoiceCount: pickNumber(state.needsChoiceCount),
        noCandidateEntityCount: pickNumber(state.noCandidateEntityCount),
        pathEndingCount: pickNumber(state.pathEndingRows?.length),
        manualReviewCount: pickNumber(state.manualReviewCount),
        missingUsdReferenceCount: pickNumber(state.missingUsdReferenceCount),
        usableReferenceCount: pickNumber(state.usableReferenceCount),
        referenceRowCount: pickNumber(state.referenceRowCount),
        basisHash: normalizeCell(state.basisHash || state.currentBasisHash || state.reviewBasisHash || state.builtBasisHash || ''),
        reviewedAtUtc: normalizeCell(state.reviewedAtUtc || ''),
        builtAtUtc: normalizeCell(state.builtAtUtc || '')
      };
    },

    caseWorkflowStatefulGeneratedReviewIsCurrent(tag = '') {
      const config = this.caseWorkflowStatefulGeneratedReviewConfig(tag);
      if (!config) return false;
      const state = this.caseWorkflowStatefulGeneratedReviewReadState(config);
      return Boolean(config.isCurrent ? config.isCurrent(state) : state?.reviewIsCurrent);
    },

    caseWorkflowStatefulGeneratedReviewTagFromCandidate(candidate = {}) {
      const explicitTag = normalizeCell(
        candidate?.stepTag
        || candidate?.tag
        || candidate?.id
        || this.caseWorkflowAutoSoftStepTag?.(candidate)
        || this.caseWorkflowStepIdentity?.(candidate)
        || ''
      ).toUpperCase();
      if (explicitTag && this.caseWorkflowStatefulGeneratedReviewConfig(explicitTag)) return explicitTag;
      const method = normalizeCell(candidate?.method || candidate?.primaryMethod || '').toLowerCase();
      const action = normalizeCell(candidate?.action || candidate?.actionId || '').toLowerCase();
      const title = normalizeCell(candidate?.stepTitle || candidate?.title || candidate?.status || '').toLowerCase();
      const detail = normalizeCell(candidate?.detail || candidate?.statusLabel || candidate?.message || '').toLowerCase();
      const haystack = `${method} ${action} ${title} ${detail}`;
      if (/what\s+went\s+where|automationreviewwhatwentwhere|automationbuildandreviewwhatwentwhere/.test(haystack)) return 'WHAT_WENT_WHERE';
      if (/path[-\s]*ending|tool\s*55|review_path_ending_rows|addendumpathending|pathendingrows/.test(haystack)) return 'PATH_ENDING_ADDENDUM_REVIEW';
      if (/recipient/.test(haystack) && /addendum/.test(haystack)) return 'ADDENDUM_RECIPIENT_ENTITY';
      if (/addendum/.test(haystack)) return 'ADDENDUM_BUILD';
      if (/address\s+legend|address_legend|automationbuildaddresslegend|rebuildaddresslegend/.test(haystack)) return 'ADDRESS_LEGEND';
      if (/template|tool\s*31b|review_template_deliverable|automationbuildandreviewtemplate/.test(haystack)) return 'TEMPLATE_DELIVERABLE';
      if (/suspicious/.test(haystack)) return 'SUSPICIOUS_ADDRESS_TABLE';
      if (/exchange\s+address\s+table|exchange_address_table|automationbuildandreviewexchange/.test(haystack)) return 'EXCHANGE_ADDRESS_TABLE';
      if (/dormant/.test(haystack)) return 'DORMANT_ADDRESS_DELIVERABLES';
      if (/case\s+conclusion|case_conclusion|automationbuildandreviewcaseconclusion/.test(haystack)) return 'CASE_CONCLUSION';
      if (/report\s+body|report_body|automationbuildandreviewreportbody/.test(haystack)) return 'REPORT_BODY';
      return '';
    },

    caseWorkflowStatefulGeneratedReviewConfigFromCandidate(candidate = {}) {
      const tag = this.caseWorkflowStatefulGeneratedReviewTagFromCandidate(candidate);
      return tag ? this.caseWorkflowStatefulGeneratedReviewConfig(tag) : null;
    },

    caseWorkflowStatefulGeneratedReviewCurrentCandidate(options = {}) {
      const candidates = [
        options.step,
        options.plan,
        this.caseAutomation?.lastPlan,
        this.caseAutomation?.manualPause,
        this.recommendedWorkflowStep
      ].filter(Boolean);
      let firstStateful = null;
      for (const candidate of candidates) {
        const config = this.caseWorkflowStatefulGeneratedReviewConfigFromCandidate(candidate);
        if (!config) continue;
        const entry = { config, step: candidate };
        if (!firstStateful) firstStateful = entry;
        if (!this.caseWorkflowStatefulGeneratedReviewIsCurrent(config.tag)) return entry;
      }
      return firstStateful || { config: null, step: null };
    },

    clearCaseWorkflowStatefulGeneratedReviewAutoGuardTimer() {
      if (!this.caseWorkflowStatefulGeneratedReviewAutoGuardTimer) return;
      if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(this.caseWorkflowStatefulGeneratedReviewAutoGuardTimer);
      } else {
        clearTimeout(this.caseWorkflowStatefulGeneratedReviewAutoGuardTimer);
      }
      this.caseWorkflowStatefulGeneratedReviewAutoGuardTimer = null;
    },

    clearCaseWorkflowStatefulGeneratedReviewAutoResumeTimer() {
      if (!this.caseWorkflowStatefulGeneratedReviewAutoResumeTimer) return;
      if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(this.caseWorkflowStatefulGeneratedReviewAutoResumeTimer);
      } else {
        clearTimeout(this.caseWorkflowStatefulGeneratedReviewAutoResumeTimer);
      }
      this.caseWorkflowStatefulGeneratedReviewAutoResumeTimer = null;
    },

    caseWorkflowStatefulGeneratedReviewCurrentStopToken() {
      return Number(this.caseWorkflowStatefulGeneratedReviewStopToken || 0);
    },

    caseWorkflowStatefulGeneratedReviewStopRequestedSince(token = 0) {
      return Number(this.caseWorkflowStatefulGeneratedReviewStopToken || 0) !== Number(token || 0);
    },

    caseWorkflowStatefulGeneratedReviewUserStopIsRecent(windowMs = 30000) {
      const stoppedAt = Number(this.caseWorkflowStatefulGeneratedReviewUserStopRequestedAtMs || 0);
      return stoppedAt > 0 && Date.now() - stoppedAt < Math.max(1000, Number(windowMs) || 30000);
    },

    cancelCaseWorkflowBackgroundAutomationResume(options = {}) {
      const now = Date.now();
      this.caseWorkflowStatefulGeneratedReviewUserStopRequestedAtMs = now;
      this.caseWorkflowStatefulGeneratedReviewStopToken = (Number(this.caseWorkflowStatefulGeneratedReviewStopToken) || 0) + 1;
      this.caseWorkflowWwwUsdReferenceStopToken = (Number(this.caseWorkflowWwwUsdReferenceStopToken) || 0) + 1;
      this.clearCaseWorkflowStatefulGeneratedReviewAutoGuardTimer?.();
      this.clearCaseWorkflowStatefulGeneratedReviewAutoResumeTimer?.();
      this.clearCaseWorkflowWwwUsdReferenceAutoGuardTimer?.();
      this.clearCaseWorkflowWwwUsdReferenceAutoResumeTimer?.();
      this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
        tag: this.caseWorkflowStatefulGeneratedReviewAutoGuardLastTag || '',
        stage: 'background-auto-resume-cancelled',
        source: options.source || 'case-automation-stop',
        message: 'Cleared generated-deliverable and Column H guard/resume timers after an investigator stop request.'
      });
      return this.caseWorkflowStatefulGeneratedReviewStopToken;
    },

    caseWorkflowStatefulGeneratedReviewChainTags() {
      return [
        'ADDRESS_LEGEND',
        'TEMPLATE_DELIVERABLE',
        'SUSPICIOUS_ADDRESS_TABLE',
        'EXCHANGE_ADDRESS_TABLE',
        'DORMANT_ADDRESS_DELIVERABLES',
        'WHAT_WENT_WHERE',
        'CASE_CONCLUSION',
        'REPORT_BODY',
        'PATH_ENDING_ADDENDUM_REVIEW',
        'ADDENDUM_RECIPIENT_ENTITY',
        'ADDENDUM_BUILD'
      ];
    },

    caseWorkflowStatefulGeneratedReviewChainStartIndex(tag = '') {
      const tags = this.caseWorkflowStatefulGeneratedReviewChainTags();
      const normalized = normalizeCell(tag).toUpperCase();
      const index = tags.indexOf(normalized);
      return index >= 0 ? index : 0;
    },

    queueCaseWorkflowStatefulGeneratedReviewAutoGuard(options = {}) {
      if (!this.caseAutomation?.skipResolvableReview && !options.force) return false;
      if (!options.force && !options.keepWatching && this.caseWorkflowStatefulGeneratedReviewAllCurrent?.()) return false;
      this.clearCaseWorkflowStatefulGeneratedReviewAutoGuardTimer();
      const startedAtMs = Number(options.startedAtMs) || Date.now();
      const maxMs = Math.max(1000, Number(options.maxMs) || 90000);
      const delayMs = Math.max(0, Number(options.delayMs) || 300);
      const stopToken = options.stopToken === undefined
        ? this.caseWorkflowStatefulGeneratedReviewCurrentStopToken()
        : Number(options.stopToken || 0);
      const run = async () => {
        this.caseWorkflowStatefulGeneratedReviewAutoGuardTimer = null;
        try {
          if (this.caseWorkflowStatefulGeneratedReviewStopRequestedSince(stopToken) && !options.forceAfterStop) return;
          if (!this.caseAutomation?.skipResolvableReview && !options.force) return;
          if (!options.force && !options.keepWatching && this.caseWorkflowStatefulGeneratedReviewAllCurrent?.()) return;
          const automationStillActive = Boolean(this.caseAutomation?.active || this.caseAutomation?.busy || options.force || options.allowWhenInactive);
          if (!automationStillActive) return;
          if (this.caseWorkflowStatefulGeneratedReviewAutoGuardRunning) {
            if (Date.now() - startedAtMs < maxMs) {
              this.queueCaseWorkflowStatefulGeneratedReviewAutoGuard({ ...options, startedAtMs, delayMs: 350, maxMs, stopToken });
            }
            return;
          }
          const { config, step } = this.caseWorkflowStatefulGeneratedReviewCurrentCandidate(options);
          if (config && !this.caseWorkflowStatefulGeneratedReviewIsCurrent(config.tag)) {
            await this.runCaseWorkflowStatefulGeneratedReviewAutoGuard({
              ...options,
              config,
              step,
              source: options.source || 'stateful-generated-review-auto-guard',
              continueAfter: options.continueAfter !== false,
              force: true
            });
            return;
          }
          const stillWorthWatching = Boolean(this.caseAutomation?.active || this.caseAutomation?.busy || options.keepWatching);
          if (stillWorthWatching && Date.now() - startedAtMs < maxMs) {
            this.queueCaseWorkflowStatefulGeneratedReviewAutoGuard({ ...options, startedAtMs, delayMs: 450, maxMs, stopToken });
          }
        } catch (error) {
          console.warn('Case Buddy generated-deliverable Auto guard failed.', error);
          this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
            tag: options.config?.tag || options.step?.tag || options.step?.stepTag || '',
            stage: 'guard-error',
            source: options.source || 'stateful-generated-review-auto-guard',
            message: error?.message || String(error || '')
          });
        }
      };
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
        this.caseWorkflowStatefulGeneratedReviewAutoGuardTimer = window.setTimeout(run, delayMs);
      } else {
        this.caseWorkflowStatefulGeneratedReviewAutoGuardTimer = setTimeout(run, delayMs);
      }
      return true;
    },

    async runCaseWorkflowStatefulGeneratedReviewGateIfNeeded(options = {}) {
      if (!this.caseAutomation?.skipResolvableReview && !options.force) return { handled: false, reason: 'skip-resolvable-reviews-off' };
      const { config, step } = this.caseWorkflowStatefulGeneratedReviewCurrentCandidate(options);
      if (!config) return { handled: false, reason: 'not-stateful-generated-review-step' };
      if (!options.force && this.caseWorkflowStatefulGeneratedReviewIsCurrent(config.tag)) return { handled: false, success: true, reason: 'stateful-generated-review-current', tag: config.tag };
      return this.runCaseWorkflowStatefulGeneratedReviewAutoGuard({
        ...options,
        config,
        step,
        force: true,
        source: options.source || `stateful-generated-review-gate-${config.tag.toLowerCase()}`,
        continueAfter: options.continueAfter !== false
      });
    },

    async runCaseWorkflowStatefulGeneratedReviewAutoGuard(options = {}) {
      const config = options.config || this.caseWorkflowStatefulGeneratedReviewConfigFromCandidate(options.step) || this.caseWorkflowStatefulGeneratedReviewConfig(options.tag || options.stepTag);
      if (!config) return { handled: false, reason: 'not-stateful-generated-review-step' };
      if (this.caseWorkflowStatefulGeneratedReviewAutoGuardRunning) return { handled: true, success: false, reason: 'stateful-generated-review-auto-guard-running', tag: config.tag };
      if (!this.caseAutomation?.skipResolvableReview && !options.force) return { handled: false, reason: 'skip-resolvable-reviews-off', tag: config.tag };
      if (!options.force && this.caseWorkflowStatefulGeneratedReviewIsCurrent(config.tag)) return { handled: false, success: true, reason: 'stateful-generated-review-current', tag: config.tag };
      this.caseWorkflowStatefulGeneratedReviewAutoGuardRunning = true;
      this.caseWorkflowStatefulGeneratedReviewAutoGuardLastRunAtMs = Date.now();
      this.caseWorkflowStatefulGeneratedReviewAutoGuardLastTag = config.tag;
      this.caseWorkflowStatefulGeneratedReviewAutoGuardLastSource = options.source || 'stateful-generated-review-auto-guard';
      this.clearCaseWorkflowStatefulGeneratedReviewAutoGuardTimer();
      // The legacy automation loop can call its own method table directly, bypassing the
      // Vue method aliases. Pause that timer while this guard performs the real
      // stage -> mark reviewed -> verify sequence, then resume Auto explicitly.
      if (typeof this.clearCaseAutomationTimer === 'function') this.clearCaseAutomationTimer();
      else if (this.caseAutomationTimer) {
        if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(this.caseAutomationTimer);
        else clearTimeout(this.caseAutomationTimer);
        this.caseAutomationTimer = null;
      }
      const source = options.source || `stateful-generated-review-auto-guard-${config.tag.toLowerCase()}`;
      const autoWasActiveWhenGuardStarted = Boolean(this.caseAutomation?.active || this.caseAutomation?.busy);
      const scopedAssistantBoundary = Boolean(this.caseWorkflowAssistantScopedAutomationBoundaryActive?.());
      const continueAfter = !scopedAssistantBoundary && (options.continueAfter !== false || autoWasActiveWhenGuardStarted);
      try {
        this.caseAutomation = {
          ...(this.caseAutomation || {}),
          active: true,
          busy: true,
          paused: false,
          manualPause: null,
          status: `Building and reviewing ${config.title || config.tag}…`,
          detail: '100% Automated Mode is on, so Case Buddy is staging this generated output, marking it reviewed, and verifying the live state before continuing.',
          lastPlan: {
            stepTag: config.tag,
            stepTitle: config.title || config.tag,
            action: options.step?.action || '',
            status: `Building and reviewing ${config.title || config.tag}…`,
            detail: 'Generated-deliverable Auto guard is enforcing current reviewed output.',
            type: 'method',
            method: 'runCaseWorkflowStatefulGeneratedReviewAutomation'
          }
        };
        this.appendCaseWorkflowAutomationLog?.(`Auto-reviewing generated step: ${config.title || config.tag}…`, {
          stepTag: config.tag,
          source,
          guard: 'stateful-generated-review'
        });
        this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
          tag: config.tag,
          stage: 'guard-start',
          source,
          methodName: options.step?.method || '',
          state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(this.caseWorkflowStatefulGeneratedReviewReadState(config)) || null
        });
        const stopToken = options.stopToken === undefined
          ? this.caseWorkflowStatefulGeneratedReviewCurrentStopToken()
          : Number(options.stopToken || 0);
        const result = await this.runCaseWorkflowStatefulGeneratedReviewChainFromTag(config.tag, {
          source,
          stopToken,
          methodName: options.step?.method || '',
          action: options.step?.action || ''
        });
        const verifyResult = await this.waitForCaseWorkflowStatefulGeneratedReviewCurrent(config, {
          source,
          methodName: options.step?.method || '',
          stage: 'guard-verify-current',
          buildTimeoutMs: 4200,
          timeoutMs: 4200,
          intervalMs: 160
        });
        const finalState = verifyResult?.state || this.caseWorkflowStatefulGeneratedReviewReadState(config) || {};
        const liveVerifiedCurrent = Boolean(
          verifyResult?.success
          || config.isCurrent?.(finalState)
          || this.caseWorkflowStatefulGeneratedReviewIsCurrent(config.tag)
        );
        // The legacy/nested generated-review action can return an early "not buildable"
        // result while the real live state settles a moment later. Live verification is the
        // source of truth here: if the generated artifact/review now reads current, do not
        // leave Auto paused on the stale nested result.
        const success = liveVerifiedCurrent;
        if (success && result?.success === false) {
          this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
            tag: config.tag,
            stage: 'guard-nested-result-overridden-by-live-current',
            source,
            methodName: options.step?.method || '',
            state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(finalState) || null,
            details: { result, verifyResult }
          });
        }
        this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
          tag: config.tag,
          stage: success ? 'guard-success' : 'guard-not-current',
          source,
          methodName: options.step?.method || '',
          state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(finalState) || null,
          details: { result, verifyResult, liveVerifiedCurrent }
        });
        if (this.caseWorkflowStatefulGeneratedReviewStopRequestedSince(stopToken)) {
          this.caseAutomation = {
            ...(this.caseAutomation || {}),
            active: false,
            busy: false,
            paused: false,
            manualPause: null,
            status: 'Automation stopped by user.',
            detail: 'Generated-deliverable automation stopped before resuming.'
          };
          return { handled: true, success: false, stopped: true, tag: config.tag, source };
        }
        if (success) {
          await this.caseWorkflowUiNextTick?.();
          const generatedChainIsCurrent = this.caseWorkflowStatefulGeneratedReviewAllCurrent();
          this.caseAutomation = {
            ...(this.caseAutomation || {}),
            active: false,
            busy: false,
            paused: false,
            manualPause: null,
            status: generatedChainIsCurrent ? 'Generated deliverables reviewed.' : `${config.title || config.tag} reviewed`,
            detail: generatedChainIsCurrent
              ? 'The generated deliverable chain is current and reviewed.'
              : (continueAfter
                ? 'Generated output is current and reviewed; continuing workflow automation.'
                : 'Generated output is current and reviewed.')
          };
          this.showCaseAutomationPauseModal = false;
          if (generatedChainIsCurrent) {
            this.refreshCaseWorkflowStatefulGeneratedReviewRecordsForCurrentRevision?.(this.caseWorkflowStatefulGeneratedReviewChainTags?.() || [], {
              source: `${source}-guard-chain-current`
            });
            if (scopedAssistantBoundary) {
              return {
                handled: true,
                success: true,
                boundary: true,
                tag: config.tag,
                source,
                result,
                verifyResult,
                generatedChainIsCurrent: true,
                reason: 'workflow-assistant-category-boundary'
              };
            }
            const afterChain = await this.pauseCaseWorkflowAfterStatefulGeneratedReviewChain({ source, stopToken });
            return { handled: true, success: true, tag: config.tag, source, result, verifyResult, afterChain, generatedChainIsCurrent: true };
          }
          if (continueAfter && this.caseWorkflowStatefulGeneratedReviewShouldAutoResume(result, { source, stopToken })) {
            this.queueCaseWorkflowStatefulGeneratedReviewAutoResume({ source, stopToken, delayMs: Number(options.resumeDelayMs) || 220 });
          }
          return { handled: true, success: true, tag: config.tag, source, result, verifyResult };
        }
        if (result?.paused || result?.success === false) return result;
        const message = normalizeCell(finalState?.summary || finalState?.statusLabel || '') || `${config.title || config.tag} was built, but its reviewed/current state could not be verified.`;
        return this.pauseCaseWorkflowStatefulGeneratedReviewAuto(message, config, {
          source,
          methodName: options.step?.method || '',
          reason: `${config.tag.toLowerCase()}-guard-review-not-current`,
          state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(finalState) || null
        });
      } finally {
        this.caseWorkflowStatefulGeneratedReviewAutoGuardRunning = false;
      }
    },

    async runCaseWorkflowStatefulGeneratedReviewChainFromTag(startTag = '', context = {}) {
      const tags = this.caseWorkflowStatefulGeneratedReviewChainTags();
      const startIndex = this.caseWorkflowStatefulGeneratedReviewChainStartIndex(startTag);
      const source = context.source || 'workflow-stateful-generated-review-chain';
      const stopToken = context.stopToken === undefined
        ? this.caseWorkflowStatefulGeneratedReviewCurrentStopToken()
        : Number(context.stopToken || 0);
      if (this.caseWorkflowStatefulGeneratedReviewChainRunning) {
        return { handled: true, success: false, reason: 'stateful-generated-review-chain-running', tag: startTag };
      }
      this.caseWorkflowStatefulGeneratedReviewChainRunning = true;
      const handledTags = [];
      try {
        for (let index = startIndex; index < tags.length; index += 1) {
          const tag = tags[index];
          if (this.caseWorkflowStatefulGeneratedReviewStopRequestedSince(stopToken)) {
            return { handled: true, success: false, stopped: true, tag, handledTags };
          }
          const config = this.caseWorkflowStatefulGeneratedReviewConfig(tag);
          if (!config) continue;
          if (this.caseWorkflowStatefulGeneratedReviewIsCurrent(tag)) {
            handledTags.push({ tag, alreadyCurrent: true });
            continue;
          }
          this.caseAutomation = {
            ...(this.caseAutomation || {}),
            active: true,
            busy: true,
            paused: false,
            manualPause: null,
            status: `Building and reviewing ${config.title || tag}…`,
            detail: '100% Automated Mode is on, so Case Buddy is completing the generated deliverable chain in order.',
            lastPlan: {
              stepTag: tag,
              stepTitle: config.title || tag,
              action: context.action || '',
              status: `Building and reviewing ${config.title || tag}…`,
              detail: 'Generated-deliverable Auto guard is enforcing the ordered deliverable chain.',
              type: 'method',
              method: 'runCaseWorkflowStatefulGeneratedReviewAutomation'
            }
          };
          this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
            tag,
            stage: 'chain-step-start',
            source,
            methodName: context.methodName || '',
            state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(this.caseWorkflowStatefulGeneratedReviewReadState(config)) || null
          });
          const result = await this.runCaseWorkflowStatefulGeneratedReviewAutomation('', [], {
            ...config,
            tag,
            source: `${source}-${tag.toLowerCase()}`,
            nested: true,
            skipStatefulGeneratedReviewGate: true,
            continueAfter: false,
            suppressUi: true
          });
          let current = this.caseWorkflowStatefulGeneratedReviewIsCurrent(tag);
          let recoveryVerifyResult = null;
          if (!current && (result?.paused || result?.success === false)) {
            recoveryVerifyResult = await this.waitForCaseWorkflowStatefulGeneratedReviewCurrent(config, {
              source: `${source}-${tag.toLowerCase()}-chain-recovery`,
              methodName: context.methodName || '',
              stage: 'chain-recovery-verify-current',
              buildTimeoutMs: 1800,
              timeoutMs: 1800,
              intervalMs: 140
            });
            current = Boolean(
              recoveryVerifyResult?.success
              || config.isCurrent?.(recoveryVerifyResult?.state || {})
              || this.caseWorkflowStatefulGeneratedReviewIsCurrent(tag)
            );
            if (current) {
              this.showCaseAutomationPauseModal = false;
              if (this.caseAutomation?.manualPause?.stepTag === tag) {
                this.caseAutomation = { ...(this.caseAutomation || {}), paused: false, manualPause: null };
              }
              this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
                tag,
                stage: 'chain-step-recovered-by-live-current',
                source,
                methodName: context.methodName || '',
                state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(recoveryVerifyResult?.state || this.caseWorkflowStatefulGeneratedReviewReadState(config)) || null,
                details: { result, recoveryVerifyResult }
              });
            }
          }
          handledTags.push({ tag, success: Boolean(result?.success || current), current, result, recoveryVerifyResult });
          this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
            tag,
            stage: current ? 'chain-step-current' : 'chain-step-not-current',
            source,
            methodName: context.methodName || '',
            state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(this.caseWorkflowStatefulGeneratedReviewReadState(config)) || null,
            details: { result, recoveryVerifyResult }
          });
          if (!current) {
            if (result?.paused || result?.success === false) return { ...(result || {}), handledTags };
            return { handled: true, success: false, tag, reason: 'chain-step-not-current', handledTags };
          }
        }
        this.refreshCaseWorkflowStatefulGeneratedReviewRecordsForCurrentRevision?.(tags, { source: `${source}-chain-complete` });
        return { handled: true, success: true, startTag: tags[startIndex] || startTag, handledTags };
      } finally {
        this.caseWorkflowStatefulGeneratedReviewChainRunning = false;
      }
    },

    caseWorkflowStatefulGeneratedReviewAllCurrent() {
      return this.caseWorkflowStatefulGeneratedReviewChainTags()
        .every(tag => this.caseWorkflowStatefulGeneratedReviewIsCurrent(tag));
    },

    caseWorkflowStatefulGeneratedReviewNextNonGeneratedStep() {
      const step = this.recommendedWorkflowStep || null;
      if (!step || typeof step !== 'object') return null;
      const tag = normalizeCell(this.caseWorkflowStepIdentity?.(step) || step.stepTag || step.tag || '').toUpperCase();
      if (!tag) return null;
      if (this.caseWorkflowStatefulGeneratedReviewConfig(tag)) return null;
      return {
        step,
        tag,
        title: normalizeCell(step.stepTitle || step.title || step.label || tag) || tag,
        statusLabel: normalizeCell(step.statusLabel || step.status || '')
      };
    },

    caseWorkflowFinalRubberStampConfig(tag = '') {
      const normalized = normalizeCell(tag).toUpperCase();
      const configs = {
        CASE_FLOW_VISUALS: {
          tag: 'CASE_FLOW_VISUALS',
          title: 'Create case flow visuals',
          signalCodes: ['CASE_FLOW_VISUALS_REVIEWED', 'CASE_FLOW_VISUALS_DONE', 'CASE_FLOW_VISUALS'],
          checkIds: ['case_flow_visuals'],
          actionId: 'mark_case_flow_visuals_done'
        },
        CASE_COMPLETION_SUMMARY: {
          tag: 'CASE_COMPLETION_SUMMARY',
          title: 'Review completion summary and ship deliverables',
          signalCodes: ['CASE_COMPLETION_SUMMARY_REVIEWED', 'FULL_REPORT_PACKAGE_REVIEWED', 'CASE_COMPLETION_SUMMARY', 'DELIVERABLES_READY'],
          checkIds: ['full_report_package', 'deliverables_ready'],
          actionId: 'open_one_click_report_package'
        }
      };
      return configs[normalized] || null;
    },

    caseWorkflowFinalRubberStampTags() {
      return ['CASE_FLOW_VISUALS', 'CASE_COMPLETION_SUMMARY'];
    },

    caseWorkflowFinalRubberStampStartIndex(tag = '') {
      const tags = this.caseWorkflowFinalRubberStampTags();
      const index = tags.indexOf(normalizeCell(tag).toUpperCase());
      return index >= 0 ? index : 0;
    },

    caseWorkflowFinalRubberStampStepComplete(config = {}, context = {}) {
      if (!config?.tag) return null;
      const step = {
        tag: config.tag,
        stepTag: config.tag,
        title: config.title,
        stepTitle: config.title
      };
      const action = {
        id: config.actionId || config.tag,
        action: config.actionId || config.tag,
        method: context.methodName || ''
      };
      const record = this.recordCaseWorkflowAutoSoftStepComplete(step, action, {
        tag: config.tag,
        source: context.source || 'workflow-final-rubber-stamp',
        actionId: config.actionId || '',
        method: context.methodName || '',
        stepTitle: config.title
      });
      if (!record) return null;

      // Ensure final organizer steps get the extra aliases their downstream checks and
      // workflow cards look for, without forcing a modal/stage/download action.
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const now = record.checkedAtUtc || this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) },
        autoStepCompletionByTag: { ...((this.caseSupportState.workflowState || {}).autoStepCompletionByTag || {}) }
      };
      workflow.autoStepCompletionByTag[config.tag] = {
        ...(workflow.autoStepCompletionByTag[config.tag] || {}),
        ...record,
        finalRubberStamp: true,
        skipResolvableReview: true
      };
      const signalCodes = Array.from(new Set([config.tag, ...(config.signalCodes || [])].map(code => normalizeCell(code).toUpperCase()).filter(Boolean)));
      signalCodes.forEach((code) => {
        workflow.signalCompleteByCode[code] = {
          ...(workflow.signalCompleteByCode[code] || {}),
          completedAtUtc: workflow.signalCompleteByCode[code]?.completedAtUtc || now,
          reviewedAtUtc: now,
          checkedAtUtc: now,
          revision,
          source: context.source || 'workflow-final-rubber-stamp',
          stepTag: config.tag,
          finalRubberStamp: true,
          skipResolvableReview: true
        };
      });
      this.caseSupportState.workflowState = workflow;

      const checkResults = { ...(this.caseSupportState.checkResultsById || {}) };
      const checkIds = Array.from(new Set((config.checkIds || []).map(id => normalizeCell(id).toLowerCase()).filter(Boolean)));
      checkIds.forEach((id) => {
        checkResults[id] = {
          ...(checkResults[id] || {}),
          id,
          status: 'pass',
          message: `${config.title || config.tag} rubber-stamped by skip-review workflow Auto.`,
          summary: `${config.title || config.tag} rubber-stamped by skip-review workflow Auto.`,
          revision,
          checkedAtUtc: now,
          details: {
            ...((checkResults[id] || {}).details || {}),
            tag: config.tag,
            source: context.source || 'workflow-final-rubber-stamp',
            autoSoftCompletion: true,
            finalRubberStamp: true,
            skipResolvableReview: true
          }
        };
      });
      this.caseSupportState.checkResultsById = checkResults;
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(80);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      return workflow.autoStepCompletionByTag[config.tag];
    },

    caseWorkflowFirstMissingStatefulGeneratedReviewTag() {
      return this.caseWorkflowStatefulGeneratedReviewChainTags()
        .find(tag => !this.caseWorkflowStatefulGeneratedReviewIsCurrent(tag)) || '';
    },

    getCaseWorkflowOriginalGeneratedReviewState(methodName = '', args = []) {
      if (!normalizeCell(methodName) || typeof this.callFirstCaseBuddyOriginalMethod !== 'function') return {};
      try {
        const result = this.callFirstCaseBuddyOriginalMethod(methodName, Array.isArray(args) ? args : []);
        return result && typeof result === 'object' ? result : {};
      } catch (error) {
        console.warn(`Case Buddy could not read original generated-review state with ${methodName}.`, error);
        return {};
      }
    },

    applyCaseWorkflowGeneratedReviewOverlayByTag(tag = '', state = {}) {
      const config = this.caseWorkflowStatefulGeneratedReviewConfig(tag);
      if (!config) return state && typeof state === 'object' ? state : {};
      return this.applyCaseWorkflowStatefulGeneratedReviewVerifiedOverlay?.(config, state && typeof state === 'object' ? state : {}) || state || {};
    },

    getWhatWentWhereState(...args) {
      return this.applyCaseWorkflowGeneratedReviewOverlayByTag('WHAT_WENT_WHERE', this.getCaseWorkflowOriginalGeneratedReviewState('getWhatWentWhereState', args));
    },

    getCaseConclusionState(...args) {
      return this.applyCaseWorkflowGeneratedReviewOverlayByTag('CASE_CONCLUSION', this.getCaseWorkflowOriginalGeneratedReviewState('getCaseConclusionState', args));
    },

    getReportBodyState(...args) {
      return this.applyCaseWorkflowGeneratedReviewOverlayByTag('REPORT_BODY', this.getCaseWorkflowOriginalGeneratedReviewState('getReportBodyState', args));
    },

    getAddendumPathEndingReviewState(...args) {
      return this.applyCaseWorkflowGeneratedReviewOverlayByTag('PATH_ENDING_ADDENDUM_REVIEW', this.getCaseWorkflowOriginalGeneratedReviewState('getAddendumPathEndingReviewState', args));
    },

    getAddendumRecipientEntityReviewState(...args) {
      return this.applyCaseWorkflowGeneratedReviewOverlayByTag('ADDENDUM_RECIPIENT_ENTITY', this.getCaseWorkflowOriginalGeneratedReviewState('getAddendumRecipientEntityReviewState', args));
    },

    getCaseAddendumState(...args) {
      return this.applyCaseWorkflowGeneratedReviewOverlayByTag('ADDENDUM_BUILD', this.getCaseWorkflowOriginalGeneratedReviewState('getCaseAddendumState', args));
    },

    applyCaseWorkflowFullReportPackageGeneratedOverlay(baseState = {}) {
      const base = baseState && typeof baseState === 'object' ? baseState : {};
      const reportState = this.caseWorkflowStatefulGeneratedReviewReadState?.('REPORT_BODY') || {};
      const addendumState = this.caseWorkflowStatefulGeneratedReviewReadState?.('ADDENDUM_BUILD') || {};
      const reportReady = Boolean(reportState.reviewIsCurrent || base.reportBodyReviewIsCurrent);
      const addendumReady = Boolean(addendumState.reviewIsCurrent || base.addendumReviewIsCurrent);
      const merged = { ...base };
      if (reportReady) merged.reportBodyReviewIsCurrent = true;
      if (addendumReady) merged.addendumReviewIsCurrent = true;
      const packagePayload = this.caseFullReportPackagePayload && typeof this.caseFullReportPackagePayload === 'object'
        ? this.caseFullReportPackagePayload
        : null;
      const packageText = normalizeCell([
        packagePayload?.previewText,
        packagePayload?.plainText,
        packagePayload?.htmlFragment,
        packagePayload?.htmlDocument
      ].join(' ')).toLowerCase();
      const hasStaleAddendumPlaceholder = /addendum\s+cannot\s+be\s+built|tool\s*55-style\s+reviewed\s+candidate|tool\s*48-style\s+resolved\s+entity/.test(packageText);
      if (reportReady && addendumReady && /addendum\s+is\s+not\s+current/i.test(normalizeCell(merged.summary || merged.statusLabel || ''))) {
        merged.summary = hasStaleAddendumPlaceholder
          ? 'Full report package preview still contains a stale addendum prerequisite placeholder. Rebuild the package preview from the current addendum before shipping.'
          : 'Full report package prerequisites are current. Refresh or stage the package preview to rebuild from the reviewed report body and addendum.';
        merged.statusLabel = merged.summary;
      }
      if (reportReady && addendumReady && merged.isReady === false && !hasStaleAddendumPlaceholder && Array.isArray(packagePayload?.sections) && packagePayload.sections.length > 0) {
        merged.isReady = true;
      }
      return merged;
    },

    getCaseFullReportPackageState(...args) {
      return this.applyCaseWorkflowFullReportPackageGeneratedOverlay(
        this.getCaseWorkflowOriginalGeneratedReviewState('getCaseFullReportPackageState', args)
      );
    },

    readCaseWorkflowFullReportPackageStateForAudit() {
      try {
        const direct = this.caseFullReportPackageStateView;
        if (direct && typeof direct === 'object') return direct;
      } catch {
        // Fall through to the getter helper below.
      }
      try {
        if (typeof this.getCaseFullReportPackageState === 'function') {
          const state = this.getCaseFullReportPackageState({ includeDeliverableViews: true });
          if (state && typeof state === 'object') return state;
        }
      } catch (error) {
        console.warn('Case Buddy could not read full report package state for workflow Auto.', error);
      }
      return {};
    },

    sanitizeCaseWorkflowFullReportPackageStateForAudit(state = {}) {
      if (!state || typeof state !== 'object') return null;
      return {
        statusLabel: normalizeCell(state.statusLabel || state.summary || state.message || ''),
        isReady: Boolean(state.isReady),
        reviewIsCurrent: Boolean(state.reviewIsCurrent),
        reportBodyReviewIsCurrent: Boolean(state.reportBodyReviewIsCurrent),
        addendumReviewIsCurrent: Boolean(state.addendumReviewIsCurrent),
        visualsCanBuild: Boolean(state.visualsCanBuild),
        visualsReviewIsCurrent: Boolean(state.visualsReviewIsCurrent),
        sectionCount: Array.isArray(this.caseFullReportPackagePayloadView?.sections) ? this.caseFullReportPackagePayloadView.sections.length : undefined,
        basisHash: normalizeCell(state.basisHash || state.currentBasisHash || state.reviewBasisHash || '')
      };
    },

    async invokeCaseWorkflowFullReportPackageMethod(methodName = '', args = [], context = {}) {
      const name = normalizeCell(methodName);
      if (!name) return undefined;
      this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
        tag: 'CASE_COMPLETION_SUMMARY',
        stage: context.stage || 'full-package-method',
        source: context.source || 'workflow-final-rubber-stamp-package',
        methodName: name,
        state: this.sanitizeCaseWorkflowFullReportPackageStateForAudit?.(this.readCaseWorkflowFullReportPackageStateForAudit?.() || {}) || null
      });
      let result;
      if (typeof this[name] === 'function') result = this[name](...(Array.isArray(args) ? args : []));
      else if (typeof this.callFirstCaseBuddyOriginalMethod === 'function') result = this.callFirstCaseBuddyOriginalMethod(name, Array.isArray(args) ? args : []);
      if (result && typeof result.then === 'function') result = await result;
      await this.caseWorkflowUiNextTick?.();
      return result;
    },

    async ensureCaseWorkflowFullReportPackageReadyForSkipMode(context = {}) {
      if (!this.caseAutomation?.skipResolvableReview && !context.force) {
        return { handled: false, success: false, reason: 'skip-resolvable-reviews-off' };
      }
      const source = context.source || 'workflow-final-rubber-stamp-package';
      const stopToken = context.stopToken === undefined
        ? this.caseWorkflowStatefulGeneratedReviewCurrentStopToken?.()
        : Number(context.stopToken || 0);
      if (this.caseWorkflowStatefulGeneratedReviewStopRequestedSince?.(stopToken)) {
        return { handled: true, success: false, stopped: true, reason: 'user-stop-before-full-package-refresh' };
      }

      // The final one-click package is an artifact built from other live artifacts. Do not
      // merely rubber-stamp the final organizer card while the package preview still contains
      // an older "addendum cannot be built" placeholder.
      const missingTag = this.caseWorkflowFirstMissingStatefulGeneratedReviewTag?.() || '';
      if (missingTag) {
        const chainResult = await this.runCaseWorkflowStatefulGeneratedReviewChainFromTag(missingTag, {
          source: `${source}-generated-chain`,
          stopToken,
          methodName: context.methodName || ''
        });
        if (chainResult?.success === false || chainResult?.paused || chainResult?.stopped) {
          return {
            ...(chainResult || {}),
            handled: true,
            success: false,
            reason: chainResult?.reason || `generated-chain-not-current-before-full-package`,
            missingTag
          };
        }
      }
      if (!this.caseWorkflowStatefulGeneratedReviewAllCurrent?.()) {
        const stillMissing = this.caseWorkflowFirstMissingStatefulGeneratedReviewTag?.() || '';
        return { handled: true, success: false, reason: 'generated-chain-still-not-current-before-full-package', missingTag: stillMissing };
      }
      this.refreshCaseWorkflowStatefulGeneratedReviewRecordsForCurrentRevision?.(this.caseWorkflowStatefulGeneratedReviewChainTags?.() || [], {
        source: `${source}-before-full-package-refresh`
      });

      const prePackageVisualResult = await this.ensureCaseWorkflowFlowVisualsReviewedForSkipMode({
        source: `${source}-before-full-package`,
        force: true
      });
      if (prePackageVisualResult?.success === false) {
        return {
          handled: true,
          success: false,
          reason: prePackageVisualResult?.reason || 'case-flow-visuals-not-ready-before-package',
          message: prePackageVisualResult?.message || 'Case flow visuals need review before the final package can be completed.',
          visualResult: prePackageVisualResult
        };
      }
      const prePackageVisualConfig = this.caseWorkflowFinalRubberStampConfig?.('CASE_FLOW_VISUALS');
      if (prePackageVisualConfig) {
        this.caseWorkflowFinalRubberStampStepComplete(prePackageVisualConfig, {
          source: `${source}-before-full-package`,
          methodName: context.methodName || ''
        });
      }

      this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
        tag: 'CASE_COMPLETION_SUMMARY',
        stage: 'full-package-refresh-start',
        source,
        methodName: context.methodName || '',
        state: this.sanitizeCaseWorkflowFullReportPackageStateForAudit?.(this.readCaseWorkflowFullReportPackageStateForAudit?.() || {}) || null
      });

      // Force the final package to be rebuilt from the now-current generated artifacts.
      // The stale failure we saw was a cached package preview still carrying the
      // Tool55/Tool48 addendum-prerequisite message after the addendum steps had been
      // marked green.
      this.caseFullReportPackagePayload = null;
      this.caseFullReportPackagePayloadCacheKey = '';

      // Refresh the addendum state immediately before the full-package build so the report
      // body and addendum review records are read after the generated chain has settled.
      await this.invokeCaseWorkflowFullReportPackageMethod('refreshCaseAddendumReview', [], { source, stage: 'refresh-addendum-before-package' });
      this.refreshCaseWorkflowStatefulGeneratedReviewRecordsForCurrentRevision?.(this.caseWorkflowStatefulGeneratedReviewChainTags?.() || [], {
        source: `${source}-after-addendum-refresh`
      });
      await this.invokeCaseWorkflowFullReportPackageMethod('refreshCaseFullReportPackageReview', [], { source, stage: 'refresh-full-package' });
      let state = this.readCaseWorkflowFullReportPackageStateForAudit();

      if (!state?.isReady && typeof this.stageCaseFullReportPackagePreview === 'function') {
        await this.invokeCaseWorkflowFullReportPackageMethod('stageCaseFullReportPackagePreview', [], { source, stage: 'stage-full-package-preview' });
        this.refreshCaseWorkflowStatefulGeneratedReviewRecordsForCurrentRevision?.(this.caseWorkflowStatefulGeneratedReviewChainTags?.() || [], {
          source: `${source}-after-full-package-stage`
        });
        await this.invokeCaseWorkflowFullReportPackageMethod('refreshCaseFullReportPackageReview', [], { source, stage: 'refresh-after-full-package-stage' });
        state = this.readCaseWorkflowFullReportPackageStateForAudit();
      }

      if (state?.isReady && !state?.reviewIsCurrent && typeof this.markCaseFullReportPackageReviewComplete === 'function') {
        await this.invokeCaseWorkflowFullReportPackageMethod('markCaseFullReportPackageReviewComplete', [], { source, stage: 'mark-full-package-reviewed' });
        await this.invokeCaseWorkflowFullReportPackageMethod('refreshCaseFullReportPackageReview', [], { source, stage: 'refresh-after-full-package-mark' });
        state = this.readCaseWorkflowFullReportPackageStateForAudit();
      }

      const postPackageVisualResult = await this.ensureCaseWorkflowFlowVisualsReviewedForSkipMode({
        source: `${source}-after-full-package`,
        force: true
      });
      if (postPackageVisualResult?.success === false) {
        return {
          handled: true,
          success: false,
          reason: postPackageVisualResult?.reason || 'case-flow-visuals-not-current-after-package',
          message: postPackageVisualResult?.message || 'Case flow visuals are not current after the final package refresh.',
          state,
          visualResult: postPackageVisualResult
        };
      }
      const postPackageVisualConfig = this.caseWorkflowFinalRubberStampConfig?.('CASE_FLOW_VISUALS');
      if (postPackageVisualConfig) {
        this.caseWorkflowFinalRubberStampStepComplete(postPackageVisualConfig, {
          source: `${source}-after-full-package`,
          methodName: context.methodName || ''
        });
      }
      state = this.readCaseWorkflowFullReportPackageStateForAudit();

      const ready = Boolean(state?.isReady);
      const reportReady = state?.reportBodyReviewIsCurrent !== false;
      const addendumReady = state?.addendumReviewIsCurrent !== false;
      const visualsCanBuild = Boolean(state?.visualsCanBuild || this.caseFlowVisualsStateView?.canBuild);
      const visualsReady = !visualsCanBuild || Boolean(state?.visualsReviewIsCurrent || this.caseFlowVisualsStateView?.reviewIsCurrent);
      const success = Boolean(ready && reportReady && addendumReady && visualsReady);
      this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
        tag: 'CASE_COMPLETION_SUMMARY',
        stage: success ? 'full-package-ready' : 'full-package-not-ready',
        source,
        methodName: context.methodName || '',
        state: this.sanitizeCaseWorkflowFullReportPackageStateForAudit?.(state) || null,
        details: { ready, reportReady, addendumReady, visualsCanBuild, visualsReady }
      });
      if (!success) {
        const summary = normalizeCell(state?.summary || state?.statusLabel || '') || 'The final report package could not be verified ready after rebuilding generated deliverables.';
        return { handled: true, success: false, reason: 'full-package-not-ready', message: summary, state };
      }
      return { handled: true, success: true, state };
    },

    async ensureCaseWorkflowFlowVisualsReviewedForSkipMode(context = {}) {
      if (!this.caseAutomation?.skipResolvableReview && !context.force) {
        return { handled: false, success: false, reason: 'skip-resolvable-reviews-off' };
      }
      const source = normalizeCell(context.source || 'workflow-final-visuals-review');
      const maxAttempts = Math.max(1, Number(context.maxAttempts) || CASE_BUDDY_FLOW_VISUAL_AUTO_REVIEW_MAX_ATTEMPTS);
      const settleMs = Math.max(80, Number(context.settleMs) || CASE_BUDDY_FLOW_VISUAL_AUTO_REVIEW_SETTLE_MS);
      let refreshResult = null;
      let markResult = null;
      let state = this.caseFlowVisualsStateView || {};
      const attemptLog = [];

      const settle = async (delayMs = settleMs) => {
        await this.caseWorkflowUiNextTick?.();
        if (typeof this.caseManualSleep === 'function') await this.caseManualSleep(delayMs);
        else await new Promise(resolve => setTimeout(resolve, delayMs));
        await this.caseWorkflowUiNextTick?.();
      };

      const refreshState = async (stage = 'refresh') => {
        refreshResult = this.callCaseBuddyOriginalMethod(caseBuddyDeliverableMethods, 'refreshCaseFlowVisualsReview', []);
        if (refreshResult && typeof refreshResult.then === 'function') refreshResult = await refreshResult;
        await settle(Math.max(80, Math.round(settleMs * 0.65)));
        state = this.caseFlowVisualsStateView || {};
        attemptLog.push({
          stage,
          revision: Number(this.caseSupportState?.caseDataRevision) || 0,
          canBuild: Boolean(state.canBuild),
          reviewIsCurrent: Boolean(state.reviewIsCurrent),
          summary: normalizeCell(state.summary || '')
        });
        return state;
      };

      // Generated deliverables and support-state writes can settle a little after
      // Workflow Auto reaches the visuals step. Give the existing builder a bounded
      // opportunity to see the final Main Data/legend state before declaring failure.
      for (let attempt = 0; attempt < Math.min(3, maxAttempts); attempt += 1) {
        await refreshState(`preflight-${attempt + 1}`);
        if (state.canBuild || state.reviewIsCurrent) break;
        await settle(settleMs + (attempt * 120));
      }

      if (!state.canBuild) {
        const message = normalizeCell(state.summary || '') || 'Case flow visuals cannot be built from the current Main Data and address legend.';
        return { handled: true, success: false, reason: 'case-flow-visuals-not-buildable', message, state, refreshResult, attemptLog };
      }

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (state.reviewIsCurrent) break;
        const revisionBeforeMark = Number(this.caseSupportState?.caseDataRevision) || 0;
        try {
          // Use the same operation wrapper as the green “Mark visuals done” button.
          // The wrapper waits for the real review action and its support-state write,
          // which is more reliable here than calling the imported method and reading
          // its state again on the very next tick.
          if (attempt === 0 && typeof this.runCaseModalMethod === 'function' && !this.caseModalOperation?.active) {
            markResult = this.runCaseModalMethod(
              'markCaseFlowVisualsDone',
              { title: 'Finalizing case visuals…', message: 'Saving the case-flow visual review for the current case revision.' },
              { source, automated: true, attempt: attempt + 1 }
            );
          } else {
            // If the modal-operation wrapper was unavailable or declined while
            // Workflow Auto owned the page, call the same imported review action
            // directly on the next bounded attempt. The action is idempotent.
            markResult = this.callCaseBuddyOriginalMethod(caseBuddyDeliverableMethods, 'markCaseFlowVisualsDone', [{ source, automated: true, attempt: attempt + 1 }]);
          }
          if (markResult && typeof markResult.then === 'function') markResult = await markResult;
        } catch (error) {
          markResult = { success: false, error: error?.message || String(error) };
        }

        await settle(settleMs + (attempt * 120));
        await refreshState(`verify-${attempt + 1}`);
        const revisionAfterRefresh = Number(this.caseSupportState?.caseDataRevision) || 0;
        const markResultSummary = markResult && typeof markResult === 'object'
          ? {
            success: markResult.success !== false,
            changed: Boolean(markResult.changed || markResult.updated || markResult.applied),
            reason: normalizeCell(markResult.reason || ''),
            message: normalizeCell(markResult.message || markResult.error || '')
          }
          : markResult ?? null;
        attemptLog.push({
          stage: `mark-${attempt + 1}`,
          revisionBeforeMark,
          revisionAfterRefresh,
          reviewIsCurrent: Boolean(state.reviewIsCurrent),
          markResult: markResultSummary
        });
        if (state.reviewIsCurrent) break;

        // A late package/output/support write can advance the revision immediately
        // after a successful mark. Re-marking at the settled revision is safe and
        // mirrors what worked when the investigator pressed the green button.
        if (!state.canBuild) break;
        await settle(settleMs + (attempt * 100));
      }

      if (!state.reviewIsCurrent) {
        const message = normalizeCell(state.summary || '') || 'Case flow visuals were generated, but their reviewed state could not be verified after the final case revision settled.';
        return { handled: true, success: false, reason: 'case-flow-visuals-review-not-current', message, state, markResult, refreshResult, attemptLog };
      }

      this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
        tag: 'CASE_FLOW_VISUALS',
        stage: 'visuals-reviewed-current',
        source,
        state: {
          canBuild: Boolean(state.canBuild),
          reviewIsCurrent: Boolean(state.reviewIsCurrent),
          flowCount: Number(state.flowCount) || 0,
          entryCount: Number(state.entryCount) || 0,
          revision: Number(this.caseSupportState?.caseDataRevision) || 0
        },
        details: { attempts: attemptLog.length, attemptLog }
      });
      return { handled: true, success: true, state, refreshResult, markResult, attemptLog };
    },

    async runCaseWorkflowFinalRubberStampTail(startTag = 'CASE_FLOW_VISUALS', context = {}) {
      if (this.caseWorkflowAssistantScopedAutomationBoundaryActive?.() && !context.allowScopedWorkflowAssistant) {
        return { handled: true, success: false, boundary: true, reason: 'workflow-assistant-category-boundary' };
      }
      if (!this.caseAutomation?.skipResolvableReview && !context.force) {
        return { handled: false, success: false, reason: 'skip-resolvable-reviews-off', tag: normalizeCell(startTag).toUpperCase() };
      }
      const stopToken = context.stopToken === undefined
        ? this.caseWorkflowStatefulGeneratedReviewCurrentStopToken?.()
        : Number(context.stopToken || 0);
      if (this.caseWorkflowStatefulGeneratedReviewStopRequestedSince?.(stopToken)) {
        return { handled: true, success: false, stopped: true, reason: 'user-stop-before-final-rubber-stamp' };
      }
      const tags = this.caseWorkflowFinalRubberStampTags();
      const startIndex = this.caseWorkflowFinalRubberStampStartIndex(startTag);
      const completedTags = [];
      this.showCaseAutomationPauseModal = false;
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        active: true,
        busy: true,
        paused: false,
        manualPause: null,
        status: 'Rubber-stamping final workflow steps…',
        detail: context.reason || '100% Automated Mode is on, so Case Buddy is marking final visuals and shipping-summary steps complete.'
      };
      for (let index = startIndex; index < tags.length; index += 1) {
        const tag = tags[index];
        const config = this.caseWorkflowFinalRubberStampConfig(tag);
        if (!config) continue;
        if (this.caseWorkflowStatefulGeneratedReviewStopRequestedSince?.(stopToken)) {
          this.caseAutomation = {
            ...(this.caseAutomation || {}),
            active: false,
            busy: false,
            paused: false,
            manualPause: null,
            status: 'Automation stopped by user.',
            detail: 'Final workflow rubber-stamp stopped before completion.'
          };
          return { handled: true, success: false, stopped: true, completedTags };
        }
        if (tag === 'CASE_FLOW_VISUALS') {
          const visualResult = await this.ensureCaseWorkflowFlowVisualsReviewedForSkipMode({
            source: context.source || 'workflow-final-rubber-stamp',
            force: true
          });
          if (visualResult?.success === false) {
            const message = normalizeCell(visualResult?.message || visualResult?.state?.summary || '') || 'Case flow visuals need investigator review before the workflow can be completed.';
            this.caseAutomation = {
              ...(this.caseAutomation || {}),
              active: false,
              busy: false,
              paused: true,
              manualPause: {
                type: 'manual-pause',
                pauseKind: 'case-flow-visuals-review',
                stepTag: tag,
                stepTitle: config.title,
                title: config.title,
                message,
                primaryLabel: 'Review visuals',
                primaryMethod: 'openCaseFlowVisualsModal',
                openedAtUtc: this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString()
              },
              status: config.title,
              detail: message
            };
            this.showCaseAutomationPauseModal = true;
            return { handled: true, success: false, paused: true, tag, completedTags, visualResult };
          }
        }
        if (tag === 'CASE_COMPLETION_SUMMARY') {
          const packageResult = await this.ensureCaseWorkflowFullReportPackageReadyForSkipMode({
            source: context.source || 'workflow-final-rubber-stamp',
            methodName: context.methodName || 'runCaseWorkflowFinalRubberStampTail',
            stopToken
          });
          if (packageResult?.success === false || packageResult?.stopped || packageResult?.paused) {
            const message = normalizeCell(packageResult?.message || packageResult?.state?.summary || packageResult?.state?.statusLabel || '') || 'The final report package is not ready, so skip-review Auto did not rubber-stamp shipping.';
            const visualsBlocked = /case-flow-visuals|visuals-not|visuals-review/i.test(normalizeCell(packageResult?.reason || ''));
            this.caseAutomation = {
              ...(this.caseAutomation || {}),
              active: false,
              busy: false,
              paused: true,
              manualPause: {
                type: 'manual-pause',
                pauseKind: visualsBlocked ? 'case-flow-visuals-review' : 'full-report-package-build',
                stepTag: visualsBlocked ? 'CASE_FLOW_VISUALS' : tag,
                stepTitle: visualsBlocked ? 'Create case flow visuals' : config.title,
                title: visualsBlocked ? 'Create case flow visuals' : config.title,
                message,
                primaryLabel: visualsBlocked ? 'Review visuals' : 'Open final package',
                primaryMethod: visualsBlocked ? 'openCaseFlowVisualsModal' : 'openCaseWorkflowCompletionReportPackage',
                openedAtUtc: this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString()
              },
              status: config.title,
              detail: message
            };
            this.showCaseAutomationPauseModal = true;
            return { handled: true, success: false, paused: true, tag, completedTags, packageResult };
          }
        }
        this.appendCaseWorkflowAutomationLog?.(`${config.title} rubber-stamped.`, {
          stepTag: tag,
          source: context.source || 'workflow-final-rubber-stamp',
          finalRubberStamp: true
        });
        const record = this.caseWorkflowFinalRubberStampStepComplete(config, {
          source: context.source || 'workflow-final-rubber-stamp',
          methodName: context.methodName || ''
        });
        completedTags.push({ tag, success: Boolean(record) });
      }
      await this.caseWorkflowUiNextTick?.();
      if (this.caseWorkflowFinalizeAutomationIfComplete?.('Workflow Auto completed after final visuals and shipping-summary rubber stamp.')) {
        return { handled: true, success: true, complete: true, completedTags };
      }
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        active: false,
        busy: false,
        paused: false,
        manualPause: null,
        status: 'Final workflow steps rubber-stamped.',
        detail: 'Final visuals and completion-summary steps were marked complete for skip-review Auto. Run checks or press Auto again if any earlier stale step still needs reconciliation.'
      };
      return { handled: true, success: true, completedTags };
    },

    caseWorkflowGeneratedReviewManualPauseMessage(tag = '', title = '') {
      const normalizedTag = normalizeCell(tag).toUpperCase();
      if (normalizedTag === 'CASE_FLOW_VISUALS') {
        return 'Generated deliverables are current and reviewed. Create or review the case-flow visuals next; this step needs investigator judgement and should not auto-loop.';
      }
      if (normalizedTag === 'CASE_COMPLETION_SUMMARY') {
        return 'Generated deliverables are current and reviewed. Review the final completion summary and shipping package before closing the case workflow.';
      }
      return `${title || 'The next workflow step'} needs investigator review before automation should continue.`;
    },

    async pauseCaseWorkflowAfterStatefulGeneratedReviewChain(options = {}) {
      if (this.caseWorkflowAssistantScopedAutomationBoundaryActive?.()) {
        return { handled: true, success: true, boundary: true, reason: 'workflow-assistant-category-boundary' };
      }
      if (!this.caseWorkflowStatefulGeneratedReviewAllCurrent()) return { handled: false, reason: 'generated-review-chain-not-current' };
      if (this.caseWorkflowFinalizeAutomationIfComplete?.('Workflow Auto completed after generated deliverable review.')) {
        return { handled: true, success: true, complete: true, reason: 'workflow-complete' };
      }
      const next = this.caseWorkflowStatefulGeneratedReviewNextNonGeneratedStep();
      if (this.caseAutomation?.skipResolvableReview && this.caseWorkflowFinalRubberStampConfig(next?.tag)) {
        return this.runCaseWorkflowFinalRubberStampTail(next.tag, {
          source: options.source || 'stateful-generated-review-chain-complete',
          stopToken: options.stopToken,
          reason: 'Generated deliverables are current; skip-review mode is rubber-stamping final visuals/ship steps.'
        });
      }
      if (!next) {
        this.caseAutomation = {
          ...(this.caseAutomation || {}),
          active: false,
          busy: false,
          paused: false,
          manualPause: null,
          status: 'Generated deliverables reviewed.',
          detail: 'The generated deliverable chain is current. Use Next or Run checks to continue with the remaining non-generated workflow steps.'
        };
        return { handled: true, success: true, paused: false, reason: 'generated-review-chain-complete' };
      }
      const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      const message = this.caseWorkflowGeneratedReviewManualPauseMessage(next.tag, next.title);
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        active: false,
        busy: false,
        paused: true,
        status: next.title,
        detail: message,
        manualPause: {
          type: 'manual-pause',
          pauseKind: 'post-generated-review-step',
          stepTag: next.tag,
          stepTitle: next.title,
          title: next.title,
          message,
          primaryLabel: next.tag === 'CASE_FLOW_VISUALS' ? 'Open visuals step' : 'Open next step',
          primaryMethod: '',
          openedAtUtc: now
        }
      };
      this.showCaseAutomationPauseModal = true;
      this.appendCaseWorkflowAutomationLog?.(`Generated deliverables complete; ${next.title} needs review.`, {
        stepTag: next.tag,
        source: options.source || 'stateful-generated-review-chain-complete'
      });
      this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
        tag: next.tag,
        stage: 'post-generated-chain-manual-pause',
        source: options.source || 'stateful-generated-review-chain-complete',
        message,
        state: {
          statusLabel: next.statusLabel,
          generatedDeliverableChainCurrent: true
        }
      });
      return { handled: true, success: true, paused: true, tag: next.tag, reason: 'post-generated-review-manual-step' };
    },

    caseWorkflowStatefulGeneratedReviewShouldAutoResume(result = {}, options = {}) {
      const stopToken = options.stopToken === undefined
        ? this.caseWorkflowStatefulGeneratedReviewCurrentStopToken()
        : Number(options.stopToken || 0);
      if (this.caseWorkflowStatefulGeneratedReviewStopRequestedSince(stopToken)) return false;
      if (this.caseWorkflowStatefulGeneratedReviewUserStopIsRecent()) return false;
      if (this.caseWorkflowStatefulGeneratedReviewAllCurrent()) return false;
      if (this.caseWorkflowAllStepsComplete?.()) return false;
      if (result?.success === false || result?.paused || result?.stopped) return false;
      return true;
    },

    queueCaseWorkflowStatefulGeneratedReviewAutoResume(options = {}) {
      const delayMs = Math.max(0, Number(options.delayMs) || 220);
      this.clearCaseWorkflowStatefulGeneratedReviewAutoResumeTimer?.();
      const stopToken = options.stopToken === undefined
        ? this.caseWorkflowStatefulGeneratedReviewCurrentStopToken()
        : Number(options.stopToken || 0);
      const run = async () => {
        this.caseWorkflowStatefulGeneratedReviewAutoResumeTimer = null;
        if (this.caseWorkflowStatefulGeneratedReviewStopRequestedSince(stopToken)) return;
        if (this.caseWorkflowStatefulGeneratedReviewUserStopIsRecent()) return;
        if (!this.caseAutomation?.skipResolvableReview) return;
        if (this.caseAutomation?.busy || this.caseAutomation?.paused) return;
        if (this.caseWorkflowStatefulGeneratedReviewAllCurrent()) {
          await this.pauseCaseWorkflowAfterStatefulGeneratedReviewChain({ source: options.source || 'stateful-generated-review-auto-resume', stopToken });
          return;
        }
        this.startCaseAutomation().catch?.((error) => {
          console.warn('Case Buddy could not resume Auto after generated-deliverable guard.', error);
          this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
            tag: this.caseWorkflowStatefulGeneratedReviewAutoGuardLastTag || '',
            stage: 'resume-error',
            source: options.source || 'stateful-generated-review-auto-resume',
            message: error?.message || String(error || '')
          });
        });
      };
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
        this.caseWorkflowStatefulGeneratedReviewAutoResumeTimer = window.setTimeout(run, delayMs);
      } else {
        this.caseWorkflowStatefulGeneratedReviewAutoResumeTimer = setTimeout(run, delayMs);
      }
    },

    applyCaseWorkflowStatefulGeneratedReviewCurrentState(view = {}, definition = {}) {
      const tag = normalizeCell(this.caseWorkflowAutoSoftStepTag(view) || this.caseWorkflowAutoSoftStepTag(definition) || this.caseWorkflowStepIdentity(view) || this.caseWorkflowStepIdentity(definition)).toUpperCase();
      const config = this.caseWorkflowStatefulGeneratedReviewConfig(tag);
      if (!config) return view;
      const state = this.caseWorkflowStatefulGeneratedReviewReadState(config);
      const current = Boolean(config.isCurrent ? config.isCurrent(state) : state?.reviewIsCurrent);
      if (current) return {
        ...view,
        complete: true,
        stale: false,
        blocked: false,
        status: 'complete',
        statusLabel: /^complete/i.test(normalizeCell(view.statusLabel || '')) ? view.statusLabel : 'Complete',
        statusClass: 'workflow-step-card--complete',
        sources: {
          ...(view.sources || {}),
          check: true,
          signal: true,
          statefulGeneratedReview: true
        },
        statefulGeneratedReviewCurrent: true,
        statefulGeneratedReviewState: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit(state)
      };
      if (view.complete || view.autoSoftCompletion || view.sources?.autoSoftCompletion) {
        const summary = normalizeCell(state?.summary || state?.statusLabel || 'Needs current generated/reviewed output.');
        return {
          ...view,
          complete: false,
          stale: false,
          blocked: false,
          status: /needs|missing|not|error|fail/i.test(summary) ? 'attention' : 'unchecked',
          statusLabel: summary || 'Needs review',
          statusClass: /needs|missing|not|error|fail/i.test(summary) ? 'workflow-step-card--attention' : 'workflow-step-card--unchecked',
          sources: {
            ...(view.sources || {}),
            signal: false,
            check: false,
            autoSoftCompletion: false,
            statefulGeneratedReview: true
          },
          autoSoftCompletion: false,
          statefulGeneratedReviewCurrent: false,
          statefulGeneratedReviewState: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit(state)
        };
      }
      return {
        ...view,
        statefulGeneratedReviewCurrent: false,
        statefulGeneratedReviewState: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit(state)
      };
    },

    recordCaseWorkflowStatefulGeneratedReviewAudit(record = {}) {
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        generatedDeliverableAutoAudit: Array.isArray(this.caseSupportState.workflowState?.generatedDeliverableAutoAudit)
          ? this.caseSupportState.workflowState.generatedDeliverableAutoAudit.slice(0, 39)
          : []
      };
      const entry = {
        atUtc: this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString(),
        source: record.source || 'workflow-stateful-generated-review-auto',
        tag: normalizeCell(record.tag || record.stepTag || '').toUpperCase(),
        stage: normalizeCell(record.stage || ''),
        methodName: normalizeCell(record.methodName || ''),
        message: normalizeCell(record.message || ''),
        caseDataRevision: Number(this.caseSupportState?.caseDataRevision) || 0,
        state: record.state || null,
        details: record.details || undefined
      };
      workflow.generatedDeliverableAutoAudit = [entry, ...workflow.generatedDeliverableAutoAudit].slice(0, 40);
      this.caseSupportState.workflowState = workflow;
      this.caseWorkflowStatefulGeneratedReviewRuntimeAudit = [
        entry,
        ...(Array.isArray(this.caseWorkflowStatefulGeneratedReviewRuntimeAudit) ? this.caseWorkflowStatefulGeneratedReviewRuntimeAudit : [])
      ].slice(0, 40);
      return entry;
    },

    recordCaseWorkflowStatefulGeneratedReviewCurrent(config = {}, state = {}, context = {}) {
      if (!config?.tag) return null;
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) },
        autoStepCompletionByTag: { ...((this.caseSupportState.workflowState || {}).autoStepCompletionByTag || {}) },
        statefulGeneratedReviewByTag: { ...((this.caseSupportState.workflowState || {}).statefulGeneratedReviewByTag || {}) }
      };
      const stateSummary = this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit(state);
      const record = {
        tag: config.tag,
        reviewedAtUtc: now,
        completedAtUtc: now,
        checkedAtUtc: now,
        revision,
        source: context.source || 'workflow-stateful-generated-review-auto',
        methodName: normalizeCell(context.methodName || ''),
        title: normalizeCell(config.title || ''),
        stateVerified: true,
        state: stateSummary
      };
      this.caseWorkflowGeneratedReviewRuntimeRecord(config.tag, record);
      const workflowKey = this.caseWorkflowStatefulGeneratedReviewWorkflowKey(config.tag);
      if (workflowKey) {
        workflow[workflowKey] = {
          ...(workflow[workflowKey] || {}),
          ...record,
          status: 'complete',
          currentBasisHash: stateSummary?.basisHash || record.currentBasisHash || '',
          basisHash: stateSummary?.basisHash || record.basisHash || '',
          autoReviewed: true,
          skipResolvableReview: true
        };
      }
      workflow.statefulGeneratedReviewByTag[config.tag] = record;
      workflow.autoStepCompletionByTag[config.tag] = record;
      const signalCodes = Array.from(new Set([config.tag, ...(config.signalCodes || [])].map(code => normalizeCell(code).toUpperCase()).filter(Boolean)));
      signalCodes.forEach((code) => {
        workflow.signalCompleteByCode[code] = {
          ...(workflow.signalCompleteByCode[code] || {}),
          completedAtUtc: now,
          reviewedAtUtc: now,
          checkedAtUtc: now,
          revision,
          source: context.source || 'workflow-stateful-generated-review-auto',
          stepTag: config.tag,
          stateVerified: true
        };
      });
      this.caseSupportState.workflowState = workflow;
      const checkResults = { ...(this.caseSupportState.checkResultsById || {}) };
      const checkIds = Array.from(new Set((config.checkIds || []).map(id => normalizeCell(id).toLowerCase()).filter(Boolean)));
      checkIds.forEach((id) => {
        checkResults[id] = {
          ...(checkResults[id] || {}),
          id,
          status: 'pass',
          message: `${config.title || config.tag} is current and reviewed.`,
          summary: `${config.title || config.tag} is current and reviewed.`,
          revision,
          checkedAtUtc: now,
          details: {
            ...((checkResults[id] || {}).details || {}),
            tag: config.tag,
            source: context.source || 'workflow-stateful-generated-review-auto',
            methodName: normalizeCell(context.methodName || ''),
            stateVerified: true,
            statefulGeneratedReview: true,
            state: stateSummary
          }
        };
      });
      this.caseSupportState.checkResultsById = checkResults;
      this.recordCaseWorkflowStatefulGeneratedReviewAudit({
        tag: config.tag,
        stage: 'state-current-recorded',
        source: context.source || 'workflow-stateful-generated-review-auto',
        methodName: context.methodName || '',
        state: stateSummary
      });
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      return record;
    },

    async invokeCaseWorkflowStatefulGeneratedReviewMethod(methodName = '', args = []) {
      const name = normalizeCell(methodName);
      if (!name) return undefined;
      let result;
      if (typeof this[name] === 'function') result = this[name](...(Array.isArray(args) ? args : []));
      else if (typeof this.callFirstCaseBuddyOriginalMethod === 'function') result = this.callFirstCaseBuddyOriginalMethod(name, Array.isArray(args) ? args : []);
      if (result && typeof result.then === 'function') result = await result;
      await this.caseWorkflowUiNextTick?.();
      return result;
    },

    async runCaseWorkflowStatefulGeneratedReviewMethods(methodNames = [], context = {}) {
      const results = [];
      for (const methodName of (Array.isArray(methodNames) ? methodNames : [])) {
        if (!normalizeCell(methodName)) continue;
        if (typeof this[methodName] !== 'function' && typeof this.callFirstCaseBuddyOriginalMethod !== 'function') {
          results.push({ methodName, skipped: true, reason: 'missing-method' });
          continue;
        }
        this.recordCaseWorkflowStatefulGeneratedReviewAudit({
          tag: context.tag || '',
          stage: context.stage || 'invoke-method',
          source: context.source || 'workflow-stateful-generated-review-auto',
          methodName
        });
        const result = await this.invokeCaseWorkflowStatefulGeneratedReviewMethod(methodName);
        results.push({ methodName, result });
      }
      return results;
    },

    pauseCaseWorkflowStatefulGeneratedReviewAuto(message = '', config = {}, options = {}) {
      const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      const title = config.title || options.title || 'Generated deliverable review needs attention';
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        active: false,
        busy: false,
        paused: true,
        status: title,
        detail: message || 'This generated/reviewed output is not current yet.',
        manualPause: {
          type: 'manual-pause',
          pauseKind: 'stateful-generated-review',
          stepTag: config.tag || options.stepTag || '',
          stepTitle: title,
          title,
          message: message || 'This generated/reviewed output is not current yet.',
          primaryLabel: options.primaryLabel || 'Open review panel',
          primaryMethod: options.primaryMethod || '',
          openedAtUtc: now
        }
      };
      this.showCaseAutomationPauseModal = true;
      this.recordCaseWorkflowStatefulGeneratedReviewAudit({
        tag: config.tag || '',
        stage: 'manual-pause',
        source: options.source || 'workflow-stateful-generated-review-auto',
        methodName: options.methodName || '',
        message,
        state: options.state || null
      });
      return { handled: true, success: false, paused: true, reason: options.reason || 'stateful-generated-review-not-current', message };
    },

    caseWorkflowStatefulGeneratedReviewDelay(ms = 120) {
      const delayMs = Math.max(0, Number(ms) || 0);
      return new Promise(resolve => {
        if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') window.setTimeout(resolve, delayMs);
        else setTimeout(resolve, delayMs);
      });
    },

    caseWorkflowStatefulGeneratedReviewCanAttemptMark(config = {}, state = {}) {
      if (!config?.markMethod) return false;
      if (!state || typeof state !== 'object') return false;
      const tag = normalizeCell(config.tag || '').toUpperCase();
      const generatedText = normalizeCell(
        tag === 'CASE_CONCLUSION'
          ? (this.caseConclusionDraft || state.selectedConclusionText || state.selectedText || state.generatedConclusionText || state.generatedText || state.conclusionText)
          : (state.selectedText || state.generatedText || state.summary || state.statusLabel)
      );
      if (typeof config.canAttemptMark === 'function') {
        try {
          if (config.canAttemptMark.call(this, state, { tag, generatedText })) return true;
        } catch (error) {
          console.warn(`Case Buddy could not evaluate markability for ${tag || config.markMethod}.`, error);
        }
      }
      if (tag === 'ADDENDUM_BUILD') {
        return Boolean(
          state.prep55ReviewIsCurrent
          && state.prep48ReviewIsCurrent
          && (state.canMarkReview || state.canBuild || state.buildIsCurrent || state.builtIsCurrent || state.bothBuiltCurrent)
        );
      }
      if (tag === 'ADDENDUM_RECIPIENT_ENTITY') {
        const needsChoiceCount = Number(state.needsChoiceCount || 0);
        const noCandidateEntityCount = Number(state.noCandidateEntityCount || 0);
        return Boolean(
          (state.canMarkReview || state.canReview || state.buildIsCurrent || state.builtIsCurrent || state.bothBuiltCurrent)
          && (!Number.isFinite(needsChoiceCount) || needsChoiceCount <= 0)
          && (!Number.isFinite(noCandidateEntityCount) || noCandidateEntityCount <= 0)
        );
      }
      if (
        state.canMarkReview
        || state.canReview
        || state.buildIsCurrent
        || state.builtIsCurrent
        || state.bothBuiltCurrent
        || state.sessionBuildIsCurrentRevision
      ) {
        return true;
      }
      const buildable = config.canBuild ? Boolean(config.canBuild(state)) : Boolean(state.canBuild);
      // Some generated review states, especially the desktop-style case conclusion,
      // intentionally expose only "canBuild" plus generated text. Their manual buttons
      // are valid in that state, so skip-mode Auto should be allowed to press the same
      // review-complete path instead of pausing forever waiting for a canMarkReview flag.
      if (tag === 'CASE_CONCLUSION') return Boolean(buildable && generatedText);
      if (buildable) {
        const unresolvedCount = Number(
          state.manualReviewCount
          ?? state.unresolvedValuationCount
          ?? state.needsChoiceCount
          ?? state.noCandidateEntityCount
          ?? 0
        );
        return !Number.isFinite(unresolvedCount) || unresolvedCount <= 0;
      }
      return false;
    },

    async refreshCaseWorkflowStatefulGeneratedReviewState(config = {}, context = {}) {
      if (!config?.tag) return {};
      await this.runCaseWorkflowStatefulGeneratedReviewMethods(config.refreshMethods, {
        tag: config.tag,
        source: context.source || 'workflow-stateful-generated-review-refresh',
        stage: context.stage || 'refresh-state'
      });
      await this.caseWorkflowUiNextTick?.();
      return this.caseWorkflowStatefulGeneratedReviewReadState(config) || {};
    },

    async waitForCaseWorkflowStatefulGeneratedReviewPredicate(config = {}, predicate = null, context = {}) {
      const timeoutMs = Math.max(250, Number(context.timeoutMs) || 2500);
      const intervalMs = Math.max(40, Number(context.intervalMs) || 140);
      const startedAtMs = Date.now();
      let state = this.caseWorkflowStatefulGeneratedReviewReadState(config) || {};
      const test = typeof predicate === 'function' ? predicate : (() => false);
      for (;;) {
        state = await this.refreshCaseWorkflowStatefulGeneratedReviewState(config, {
          ...context,
          stage: context.stage || 'wait-refresh'
        });
        if (test(state)) return { success: true, state };
        if (Date.now() - startedAtMs >= timeoutMs) return { success: false, state, timeoutMs };
        await this.caseWorkflowStatefulGeneratedReviewDelay(intervalMs);
      }
    },

    async waitForCaseWorkflowStatefulGeneratedReviewMarkable(config = {}, context = {}) {
      return this.waitForCaseWorkflowStatefulGeneratedReviewPredicate(config, (state) => {
        return Boolean(
          (config.isCurrent && config.isCurrent(state))
          || this.caseWorkflowStatefulGeneratedReviewCanAttemptMark(config, state)
        );
      }, {
        ...context,
        stage: context.stage || 'wait-built-or-markable',
        timeoutMs: Number(context.buildTimeoutMs) || Number(context.timeoutMs) || 3000
      });
    },

    async markCaseWorkflowStatefulGeneratedReviewIfPossible(config = {}, state = {}, context = {}) {
      let latestState = state && typeof state === 'object' ? state : {};
      if (config.isCurrent?.(latestState)) return { success: true, state: latestState, alreadyCurrent: true };
      if (!this.caseWorkflowStatefulGeneratedReviewCanAttemptMark(config, latestState)) {
        latestState = await this.refreshCaseWorkflowStatefulGeneratedReviewState(config, {
          ...context,
          stage: 'refresh-before-markability-check'
        });
      }
      if (!this.caseWorkflowStatefulGeneratedReviewCanAttemptMark(config, latestState)) {
        this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
          tag: config.tag,
          stage: 'mark-reviewed-not-markable',
          source: context.source || 'workflow-stateful-generated-review-auto',
          methodName: config.markMethod || '',
          state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(latestState) || null
        });
        return { success: false, state: latestState, reason: 'not-markable' };
      }
      latestState = await this.prepareCaseWorkflowStatefulGeneratedReviewForBackgroundMark(config, latestState, context);
      this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
        tag: config.tag,
        stage: 'mark-reviewed-attempt',
        source: context.source || 'workflow-stateful-generated-review-auto',
        methodName: config.markMethod,
        state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(latestState) || null
      });
      await this.runCaseWorkflowStatefulGeneratedReviewMethods([config.markMethod], {
        tag: config.tag,
        source: context.source || 'workflow-stateful-generated-review-auto',
        stage: 'mark-reviewed'
      });
      latestState = await this.refreshCaseWorkflowStatefulGeneratedReviewState(config, {
        ...context,
        stage: 'refresh-after-mark'
      });
      if (config.isCurrent?.(latestState)) return { success: true, state: latestState, marked: true };
      const directResult = await this.directMarkCaseWorkflowStatefulGeneratedReviewIfSafe(config, latestState, context);
      if (directResult?.success) return { ...directResult, marked: true };
      return { success: false, state: directResult?.state || latestState, marked: true, directResult };
    },

    async waitForCaseWorkflowStatefulGeneratedReviewCurrent(config = {}, context = {}) {
      if (!config?.tag) return { success: false, state: null, reason: 'missing-config' };
      let state = await this.refreshCaseWorkflowStatefulGeneratedReviewState(config, {
        ...context,
        stage: context.stage || 'verify-current-initial-refresh'
      });
      if (config.isCurrent?.(state)) return { success: true, state, alreadyCurrent: true };

      const markableResult = await this.waitForCaseWorkflowStatefulGeneratedReviewMarkable(config, {
        ...context,
        stage: 'wait-for-generated-output-before-mark',
        buildTimeoutMs: Number(context.buildTimeoutMs) || 3500
      });
      state = markableResult?.state || state;
      if (!config.isCurrent?.(state)) {
        const markResult = await this.markCaseWorkflowStatefulGeneratedReviewIfPossible(config, state, context);
        state = markResult?.state || state;
      }
      if (config.isCurrent?.(state)) return { success: true, state };

      return this.waitForCaseWorkflowStatefulGeneratedReviewPredicate(config, stateCandidate => Boolean(config.isCurrent?.(stateCandidate)), {
        ...context,
        stage: 'wait-current-after-mark',
        timeoutMs: Number(context.timeoutMs) || 2500
      });
    },

    async ensureCaseWorkflowStatefulGeneratedReviewTagCurrent(tag = '', context = {}) {
      const config = this.caseWorkflowStatefulGeneratedReviewConfig(tag);
      if (!config) return { success: true, skipped: true, reason: 'not-stateful-generated-review' };
      if (this.caseWorkflowStatefulGeneratedReviewIsCurrent(config.tag)) return { success: true, alreadyCurrent: true, tag: config.tag };
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('', [], {
        ...config,
        source: context.source || `workflow-stateful-ensure-${config.tag.toLowerCase()}`,
        nested: true,
        skipStatefulGeneratedReviewGate: true,
        continueAfter: false,
        suppressUi: true
      });
    },

    async runCaseWorkflowStatefulGeneratedReviewAutomation(methodName = '', args = [], options = {}) {
      const config = this.caseWorkflowStatefulGeneratedReviewConfig(options.tag || options.stepTag);
      if (!config) return this.runCaseWorkflowWwwUsdReferenceGateBeforeAutomationMethod(methodName, args, options);
      if (!options.skipStatefulGeneratedReviewGate && !options.nested) {
        const gateResult = await this.runCaseWorkflowWwwUsdReferenceGateIfNeeded({
          source: options.source || `workflow-auto-before-${methodName || config.tag}`,
          step: { tag: config.tag, stepTag: config.tag, title: config.title }
        });
        if (gateResult?.handled && !gateResult?.success) return gateResult;
      }
      if (!this.caseAutomation?.skipResolvableReview) {
        return methodName ? this.callFirstCaseBuddyOriginalMethod(methodName, Array.isArray(args) ? args : []) : undefined;
      }

      const suppressUi = options.suppressUi !== false;
      if (suppressUi) this.beginCaseWorkflowReviewAutoUiSuppression();
      try {
      const source = options.source || `workflow-stateful-auto-${config.tag.toLowerCase()}`;
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        status: `Building and reviewing ${config.title || config.tag}…`,
        detail: '100% Automated Mode is on, so Case Buddy is staging the generated output, marking the review current, and verifying the live state before moving on.' // used to be called Skip resolvable reviews
      };
      this.appendCaseWorkflowAutomationLog?.(`Auto-reviewing generated step: ${config.title || config.tag}…`, {
        stepTag: config.tag,
        action: options.action || '',
        source
      });

      for (const ensureTag of (Array.isArray(config.ensureTags) ? config.ensureTags : [])) {
        const ensureResult = await this.ensureCaseWorkflowStatefulGeneratedReviewTagCurrent(ensureTag, { source: `${source}-prerequisite` });
        if (ensureResult?.success === false || ensureResult?.paused) return ensureResult;
      }

      await this.runCaseWorkflowStatefulGeneratedReviewMethods(config.refreshMethods, { tag: config.tag, source, stage: 'refresh' });
      let state = this.caseWorkflowStatefulGeneratedReviewReadState(config) || {};
      let stateSummary = this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit(state);
      this.recordCaseWorkflowStatefulGeneratedReviewAudit({ tag: config.tag, stage: 'after-refresh', source, methodName, state: stateSummary });
      if (config.isCurrent?.(state)) {
        this.recordCaseWorkflowStatefulGeneratedReviewCurrent(config, state, { source, methodName });
        return { handled: true, success: true, alreadyCurrent: true, tag: config.tag, source };
      }

      await this.runCaseWorkflowStatefulGeneratedReviewMethods(config.prepareMethods, { tag: config.tag, source, stage: 'prepare' });
      state = this.caseWorkflowStatefulGeneratedReviewReadState(config) || state || {};
      state = await this.prepareCaseWorkflowStatefulGeneratedReviewForBackgroundMark(config, state, { source, methodName });
      if (config.tag === 'ADDENDUM_BUILD') {
        state = await this.stabilizeCaseWorkflowAddendumBuildPrerequisitesForAuto(state, { source, methodName });
      }
      const canBuild = config.canBuild ? Boolean(config.canBuild(state)) : Boolean(state?.canBuild || state?.canReview || state?.buildIsCurrent);
      if (!canBuild) {
        const recoveryResult = await this.waitForCaseWorkflowStatefulGeneratedReviewCurrent(config, {
          source,
          methodName,
          stage: 'not-buildable-recovery-verify-current',
          buildTimeoutMs: 1600,
          timeoutMs: 1600,
          intervalMs: 140
        });
        const recoveredState = recoveryResult?.state || this.caseWorkflowStatefulGeneratedReviewReadState(config) || state;
        if (recoveryResult?.success || config.isCurrent?.(recoveredState)) {
          this.recordCaseWorkflowStatefulGeneratedReviewCurrent(config, recoveredState, { source, methodName });
          this.recordCaseWorkflowStatefulGeneratedReviewAudit?.({
            tag: config.tag,
            stage: 'not-buildable-recovered-by-live-current',
            source,
            methodName,
            state: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(recoveredState) || null,
            details: { recoveryResult }
          });
          this.showCaseAutomationPauseModal = false;
          return { handled: true, success: true, recovered: true, tag: config.tag, source };
        }
        state = recoveredState || state;
        stateSummary = this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit(state);
        const message = normalizeCell(state?.summary || state?.statusLabel || '') || `${config.title || config.tag} is not ready to build/review yet.`;
        return this.pauseCaseWorkflowStatefulGeneratedReviewAuto(message, config, {
          source,
          methodName,
          reason: `${config.tag.toLowerCase()}-not-buildable`,
          state: stateSummary
        });
      }

      await this.runCaseWorkflowStatefulGeneratedReviewMethods(config.stageMethods, { tag: config.tag, source, stage: 'stage-output' });
      const currentResult = await this.waitForCaseWorkflowStatefulGeneratedReviewCurrent(config, {
        source,
        methodName,
        buildTimeoutMs: 4200,
        timeoutMs: 4200,
        intervalMs: 160
      });
      state = currentResult?.state || this.caseWorkflowStatefulGeneratedReviewReadState(config) || state || {};

      if (config.isCurrent?.(state)) {
        this.recordCaseWorkflowStatefulGeneratedReviewCurrent(config, state, { source, methodName });
        this.appendCaseWorkflowAutomationLog?.(`${config.title || config.tag} is current and reviewed.`, { stepTag: config.tag, source });
        return { handled: true, success: true, tag: config.tag, source };
      }
      stateSummary = this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit(state);
      const message = normalizeCell(state?.summary || state?.statusLabel || '') || `${config.title || config.tag} could not be verified current after staging and marking reviewed.`;
      return this.pauseCaseWorkflowStatefulGeneratedReviewAuto(message, config, {
        source,
        methodName,
        reason: `${config.tag.toLowerCase()}-review-not-current`,
        state: stateSummary
      });
      } finally {
        if (suppressUi) this.endCaseWorkflowReviewAutoUiSuppression();
      }
    },

    async runCaseWorkflowWwwUsdReferenceGateBeforeAutomationMethod(methodName = '', args = [], options = {}) {
      const step = options.step || {
        tag: options.stepTag || '',
        stepTag: options.stepTag || '',
        title: options.title || ''
      };
      const gateResult = await this.runCaseWorkflowWwwUsdReferenceGateIfNeeded({
        source: options.source || `workflow-auto-before-${methodName || 'step'}`,
        step
      });
      if (gateResult?.handled && !gateResult?.success) return gateResult;
      return this.callFirstCaseBuddyOriginalMethod(methodName, Array.isArray(args) ? args : []);
    },

    async automationResolveCaseInconsistencies(...args) {
      return this.runCaseWorkflowWwwUsdReferenceGateBeforeAutomationMethod('automationResolveCaseInconsistencies', args, { stepTag: 'CASE_INCONSISTENCIES', title: 'Review and resolve case inconsistencies' });
    },

    async automationNormalizeDepositHotWalletNotes(...args) {
      return this.runCaseWorkflowWwwUsdReferenceGateBeforeAutomationMethod('automationNormalizeDepositHotWalletNotes', args, { stepTag: 'NORMALIZE_DEPOSIT_NOTES', title: 'Normalize deposit, hot-wallet, and unknown-service notes' });
    },

    async automationReviewHighPriorityDepositPatterns(...args) {
      return this.runCaseWorkflowWwwUsdReferenceGateBeforeAutomationMethod('automationReviewHighPriorityDepositPatterns', args, { stepTag: 'HIGH_PRIORITY_DEPOSIT_PATTERNS', title: 'Review high-priority deposit pattern candidates' });
    },

    async automationReviewDepositPatterns(...args) {
      return this.runCaseWorkflowWwwUsdReferenceGateBeforeAutomationMethod('automationReviewDepositPatterns', args, { stepTag: 'HIGH_PRIORITY_DEPOSIT_PATTERNS', title: 'Review high-priority deposit pattern candidates' });
    },

    async automationReviewKnownEntityNoteCasing(...args) {
      return this.runCaseWorkflowWwwUsdReferenceGateBeforeAutomationMethod('automationReviewKnownEntityNoteCasing', args, { stepTag: 'KNOWN_ENTITY_NOTE_CASING', title: 'Review known-entity note casing' });
    },

    async automationLabelDuplicateHashes(...args) {
      return this.runCaseWorkflowWwwUsdReferenceGateBeforeAutomationMethod('automationLabelDuplicateHashes', args, { stepTag: 'DUPLICATE_HASH_LABELS', title: 'Label duplicate transaction hashes' });
    },

    async automationBackupNotesReplaceHashes(...args) {
      return this.runCaseWorkflowWwwUsdReferenceGateBeforeAutomationMethod('automationBackupNotesReplaceHashes', args, { stepTag: 'NOTES_BACKUP_AND_HASH_REPLACEMENT', title: 'Back up notes and replace hashes with path labels' });
    },

    async automationBuildAddressLegend(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildAddressLegend', args, { tag: 'ADDRESS_LEGEND' });
    },

    async automationBuildTemplateDeliverable(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildTemplateDeliverable', args, { tag: 'TEMPLATE_DELIVERABLE' });
    },

    async automationBuildAndReviewTemplate(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildAndReviewTemplate', args, { tag: 'TEMPLATE_DELIVERABLE' });
    },

    async automationBuildAndReviewTemplateDeliverable(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildAndReviewTemplateDeliverable', args, { tag: 'TEMPLATE_DELIVERABLE' });
    },

    async automationReviewTemplateDeliverable(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationReviewTemplateDeliverable', args, { tag: 'TEMPLATE_DELIVERABLE' });
    },

    async automationBuildSuspiciousAddressTable(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildSuspiciousAddressTable', args, { tag: 'SUSPICIOUS_ADDRESS_TABLE' });
    },

    async automationBuildAndReviewSuspiciousAddressTable(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildAndReviewSuspiciousAddressTable', args, { tag: 'SUSPICIOUS_ADDRESS_TABLE' });
    },

    async automationReviewSuspiciousAddressTable(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationReviewSuspiciousAddressTable', args, { tag: 'SUSPICIOUS_ADDRESS_TABLE' });
    },

    async automationBuildExchangeAddressTable(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildExchangeAddressTable', args, { tag: 'EXCHANGE_ADDRESS_TABLE' });
    },

    async automationBuildAndReviewExchangeAddressTable(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildAndReviewExchangeAddressTable', args, { tag: 'EXCHANGE_ADDRESS_TABLE' });
    },

    async automationReviewExchangeAddressTable(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationReviewExchangeAddressTable', args, { tag: 'EXCHANGE_ADDRESS_TABLE' });
    },

    async automationBuildDormantAddressDeliverables(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildDormantAddressDeliverables', args, { tag: 'DORMANT_ADDRESS_DELIVERABLES' });
    },

    async automationBuildAndReviewDormantAddressDeliverables(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildAndReviewDormantAddressDeliverables', args, { tag: 'DORMANT_ADDRESS_DELIVERABLES' });
    },

    async automationBuildAndReviewDormantDeliverables(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildAndReviewDormantDeliverables', args, { tag: 'DORMANT_ADDRESS_DELIVERABLES' });
    },

    async automationReviewDormantAddressDeliverables(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationReviewDormantAddressDeliverables', args, { tag: 'DORMANT_ADDRESS_DELIVERABLES' });
    },

    async automationBuildAndReviewCaseConclusion(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildAndReviewCaseConclusion', args, { tag: 'CASE_CONCLUSION' });
    },

    async automationReviewCaseConclusion(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationReviewCaseConclusion', args, { tag: 'CASE_CONCLUSION' });
    },

    async automationBuildAndReviewReportBody(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildAndReviewReportBody', args, { tag: 'REPORT_BODY' });
    },

    async automationReviewReportBody(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationReviewReportBody', args, { tag: 'REPORT_BODY' });
    },

    async automationReviewAddendumPathEndingRows(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationReviewAddendumPathEndingRows', args, { tag: 'PATH_ENDING_ADDENDUM_REVIEW' });
    },

    async automationBuildAndReviewAddendumPathEndingRows(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildAndReviewAddendumPathEndingRows', args, { tag: 'PATH_ENDING_ADDENDUM_REVIEW' });
    },

    async automationReviewPathEndingRowsForAddendum(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationReviewPathEndingRowsForAddendum', args, { tag: 'PATH_ENDING_ADDENDUM_REVIEW' });
    },

    async automationReviewExchangePathEndingRowsForAddendum(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationReviewExchangePathEndingRowsForAddendum', args, { tag: 'PATH_ENDING_ADDENDUM_REVIEW' });
    },

    async automationReviewPathEndingRows(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationReviewPathEndingRows', args, { tag: 'PATH_ENDING_ADDENDUM_REVIEW' });
    },

    async automationResolveAddendumRecipientEntities(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationResolveAddendumRecipientEntities', args, { tag: 'ADDENDUM_RECIPIENT_ENTITY' });
    },

    async automationResolveAddendumRecipientsWithFirstChoice(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationResolveAddendumRecipientsWithFirstChoice', args, { tag: 'ADDENDUM_RECIPIENT_ENTITY' });
    },

    async automationResolveAddendumRecipientEntity(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationResolveAddendumRecipientEntity', args, { tag: 'ADDENDUM_RECIPIENT_ENTITY' });
    },

    async automationResolveAddendumRecipientEntitiesWithFirstChoice(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationResolveAddendumRecipientEntitiesWithFirstChoice', args, { tag: 'ADDENDUM_RECIPIENT_ENTITY' });
    },

    async automationResolveAddendumRecipients(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationResolveAddendumRecipients', args, { tag: 'ADDENDUM_RECIPIENT_ENTITY' });
    },

    async automationBuildAndReviewAddendum(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildAndReviewAddendum', args, { tag: 'ADDENDUM_BUILD' });
    },

    async automationBuildCaseAddendum(...args) {
      return this.runCaseWorkflowStatefulGeneratedReviewAutomation('automationBuildCaseAddendum', args, { tag: 'ADDENDUM_BUILD' });
    },

    async automationCreateCaseFlowVisuals(...args) {
      if (this.caseAutomation?.skipResolvableReview) {
        return this.runCaseWorkflowFinalRubberStampTail('CASE_FLOW_VISUALS', {
          source: 'workflow-auto-case-flow-visuals-rubber-stamp',
          methodName: 'automationCreateCaseFlowVisuals'
        });
      }
      return this.runCaseWorkflowWwwUsdReferenceGateBeforeAutomationMethod('automationCreateCaseFlowVisuals', args, { stepTag: 'CASE_FLOW_VISUALS', title: 'Create case flow visuals' });
    },

    async automationReviewCompletionSummary(...args) {
      if (this.caseAutomation?.skipResolvableReview) {
        return this.runCaseWorkflowFinalRubberStampTail('CASE_COMPLETION_SUMMARY', {
          source: 'workflow-auto-completion-summary-rubber-stamp',
          methodName: 'automationReviewCompletionSummary'
        });
      }
      return this.runCaseWorkflowWwwUsdReferenceGateBeforeAutomationMethod('automationReviewCompletionSummary', args, { stepTag: 'CASE_COMPLETION_SUMMARY', title: 'Review completion summary and ship deliverables' });
    },

    getCaseWorkflowWwwUsdReferenceOriginalReviewState(args = []) {
      try {
        const result = this.callFirstCaseBuddyOriginalMethod('getWwwUsdReferenceReviewState', Array.isArray(args) ? args : []);
        return result && typeof result === 'object' ? result : null;
      } catch (error) {
        console.warn('Case Buddy could not read the original Column H USD reference state.', error);
        return null;
      }
    },

    normalizeCaseWorkflowWwwUsdReferenceSourceRows(mainRows = [], originalState = null) {
      const rows = normalizeCaseMatrix(Array.isArray(mainRows) ? mainRows : []);
      const fromState = Array.isArray(originalState?.referenceRows) ? originalState.referenceRows : [];
      try {
        const fromBuilder = buildCaseWwwReferenceRows(rows);
        if (Array.isArray(fromBuilder) && fromBuilder.length) return fromBuilder;
      } catch (error) {
        console.warn('Case Buddy could not build Column H USD reference rows with the What Went Where helper.', error);
      }
      if (fromState.length) return fromState;
      const body = rows.length && this.caseManualIsHeaderRow?.(rows[0]) ? rows.slice(1) : rows;
      return body
        .map((row, offset) => {
          const rowIndex = rows.length === body.length ? offset : offset + 1;
          const hash = normalizeCell(row?.[CASE_MANUAL_START_COL.TX_HASH] || row?.[1] || '');
          if (!hash || /^transaction\s+hash$/i.test(hash)) return null;
          const pathLabel = normalizeCell(row?.[CASE_MANUAL_START_COL.PATH_LABEL] || row?.[0] || '');
          const fromHash = normalizeCell(row?.[CASE_MANUAL_START_COL.FROM_HASH] || row?.[14] || '');
          const looksRootLabel = !pathLabel
            || /^\d+$/u.test(pathLabel)
            || /^[A-Z]+$/u.test(pathLabel)
            || /^.+-\d+$/u.test(pathLabel);
          if (!looksRootLabel && fromHash) return null;
          return {
            key: `workflow-www-usd-reference-${rowIndex}-${hash}`,
            rowIndex,
            spreadsheetRowNumber: rowIndex + 1,
            pathLabel,
            transactionHash: hash,
            shortHash: typeof this.caseManualShorten === 'function' ? this.caseManualShorten(hash, 10, 8) : hash,
            utcDate: normalizeCell(row?.[CASE_MANUAL_START_COL.DATE] || row?.[4] || ''),
            referenceAmount: (() => {
              const amountText = normalizeCell(row?.[CASE_MANUAL_START_COL.AMOUNT] || row?.[5] || '').replace(/,/g, '');
              const amountMatch = amountText.match(/-?\d+(?:\.\d+)?/u);
              return amountMatch ? Number(amountMatch[0]) : Number(amountText);
            })(),
            referenceToken: normalizeCell(row?.[CASE_MANUAL_START_COL.TOKEN] || row?.[6] || '').toUpperCase(),
            currentColumnH: normalizeCell(row?.[7] || ''),
            problemText: 'Column H needs a usable USD value.'
          };
        })
        .filter(Boolean);
    },

    normalizeCaseWorkflowWwwUsdReferenceRows(referenceRows = [], mainRows = []) {
      const rows = normalizeCaseMatrix(Array.isArray(mainRows) ? mainRows : []);
      return (Array.isArray(referenceRows) ? referenceRows : []).map((item, index) => {
        const numericRowIndex = Number(item?.rowIndex);
        const rowIndex = Number.isInteger(numericRowIndex) && numericRowIndex >= 0 ? numericRowIndex : index + 1;
        const liveRow = Array.isArray(rows[rowIndex]) ? rows[rowIndex] : null;
        const row = liveRow
          ? liveRow
          : (Array.isArray(item?.row) ? item.row : []);
        const hasLiveRow = Array.isArray(liveRow);
        const currentColumnH = hasLiveRow
          ? normalizeCell(row?.[7] || '')
          : normalizeCell(item?.currentColumnH ?? row?.[7] ?? '');
        const parsedUsd = parseCaseWwwUsdReference(currentColumnH);
        const hasUsableColumnH = Number.isFinite(Number(parsedUsd)) && Number(parsedUsd) > 0;
        const hash = normalizeCell(
          (hasLiveRow ? (row?.[CASE_MANUAL_START_COL.TX_HASH] || row?.[1]) : '')
          || item?.transactionHash
          || item?.hash
          || row?.[CASE_MANUAL_START_COL.TX_HASH]
          || row?.[1]
          || ''
        );
        const pathLabel = normalizeCell(
          (hasLiveRow ? (row?.[CASE_MANUAL_START_COL.PATH_LABEL] || row?.[0]) : '')
          || item?.pathLabel
          || row?.[CASE_MANUAL_START_COL.PATH_LABEL]
          || row?.[0]
          || ''
        );
        const referenceAmountRaw = hasLiveRow
          ? (row?.[CASE_MANUAL_START_COL.AMOUNT] ?? row?.[5] ?? item?.referenceAmount ?? '')
          : (item?.referenceAmount ?? row?.[CASE_MANUAL_START_COL.AMOUNT] ?? row?.[5] ?? '');
        const referenceAmountText = normalizeCell(referenceAmountRaw).replace(/,/g, '');
        const referenceAmountMatch = referenceAmountText.match(/-?\d+(?:\.\d+)?/u);
        const referenceAmount = referenceAmountMatch ? Number(referenceAmountMatch[0]) : Number(referenceAmountText);
        const referenceToken = normalizeCell(
          (hasLiveRow ? (row?.[CASE_MANUAL_START_COL.TOKEN] || row?.[6]) : '')
          || item?.referenceToken
          || item?.token
          || row?.[CASE_MANUAL_START_COL.TOKEN]
          || row?.[6]
          || ''
        ).toUpperCase();
        const utcDate = normalizeCell(
          (hasLiveRow ? (row?.[CASE_MANUAL_START_COL.DATE] || row?.[4]) : '')
          || item?.utcDate
          || item?.date
          || row?.[CASE_MANUAL_START_COL.DATE]
          || row?.[4]
          || ''
        );
        const problemText = hasUsableColumnH
          ? ''
          : (normalizeCell(item?.problemText) || 'Column H needs a usable USD value.');
        return {
          ...(item || {}),
          key: normalizeCell(item?.key) || `workflow-www-usd-reference-${rowIndex}-${hash || index}`,
          rowIndex,
          spreadsheetRowNumber: Number(item?.spreadsheetRowNumber) || rowIndex + 1,
          pathLabel,
          transactionHash: hash,
          shortHash: item?.shortHash || (typeof this.caseManualShorten === 'function' && hash ? this.caseManualShorten(hash, 10, 8) : hash),
          utcDate,
          referenceAmount,
          referenceToken,
          currentColumnH,
          parsedUsdValue: hasUsableColumnH ? Number(parsedUsd) : null,
          hasUsableColumnH,
          isUsable: hasUsableColumnH,
          problemText,
          searchText: normalizeCell([
            item?.searchText,
            pathLabel,
            hash,
            utcDate,
            referenceAmount,
            referenceToken,
            currentColumnH,
            problemText
          ].filter(Boolean).join(' ')).toLowerCase()
        };
      });
    },

    getCaseWorkflowWwwUsdReferenceDirectState(options = {}) {
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const workflow = this.caseSupportState?.workflowState || {};
      let mainRows = [];
      try {
        mainRows = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('main') : [];
      } catch (error) {
        console.warn('Case Buddy could not read Main Data while checking Column H USD references.', error);
        mainRows = [];
      }
      const originalState = options.originalState === undefined
        ? this.getCaseWorkflowWwwUsdReferenceOriginalReviewState()
        : options.originalState;
      const sourceRows = this.normalizeCaseWorkflowWwwUsdReferenceSourceRows(mainRows, originalState);
      const referenceRows = this.normalizeCaseWorkflowWwwUsdReferenceRows(sourceRows, mainRows);
      const missingRows = referenceRows.filter(row => !row.hasUsableColumnH);
      const referenceRowCount = referenceRows.length;
      const missingUsdReferenceCount = missingRows.length;
      const usableReferenceCount = Math.max(0, referenceRowCount - missingUsdReferenceCount);
      const columnHComplete = referenceRowCount > 0 && missingUsdReferenceCount === 0;
      const basisSeed = referenceRows.map(row => [
        row.rowIndex,
        row.pathLabel,
        row.transactionHash,
        row.utcDate,
        row.referenceAmount,
        row.referenceToken,
        row.currentColumnH
      ].join('|')).join('\n');
      const currentBasisHash = normalizeCell(originalState?.currentBasisHash)
        || `www-usd-${this.caseWorkflowStableStringHash(basisSeed)}`;
      const review = workflow.whatWentWhereUsdReferenceReview || {};
      const reviewRevision = Number(review.revision ?? review.checkedRevision ?? review.caseDataRevision) || 0;
      const check = this.caseSupportState?.checkResultsById?.www_usd_references || null;
      const checkRevision = Number(check?.revision ?? check?.checkedRevision ?? check?.caseDataRevision) || 0;
      const checkCurrent = !!check && check.status === 'pass' && checkRevision === currentRevision;
      const reviewCurrent = columnHComplete && Boolean(
        originalState?.reviewIsCurrent
        || (review.reviewedAtUtc && reviewRevision === currentRevision)
        || (review.basisHash && currentBasisHash && review.basisHash === currentBasisHash)
        || checkCurrent
      );
      const canReview = columnHComplete;
      const summary = referenceRowCount <= 0
        ? 'No top-level main transaction rows were available for Column H USD reference review.'
        : (columnHComplete
          ? (reviewCurrent
            ? `Column H USD references are reviewed for ${referenceRowCount} top-level row${referenceRowCount === 1 ? '' : 's'}.`
            : `Column H USD values are filled for ${referenceRowCount} top-level row${referenceRowCount === 1 ? '' : 's'} and need review.`)
          : `${usableReferenceCount}/${referenceRowCount} top-level row${referenceRowCount === 1 ? '' : 's'} have usable Column H USD values. ${missingUsdReferenceCount} still need Column H.`);
      return {
        ...(originalState || {}),
        referenceRows,
        missingRows,
        missingReferenceRows: missingRows,
        referenceRowCount,
        usableReferenceCount,
        missingUsdReferenceCount,
        canReview,
        reviewIsCurrent: reviewCurrent,
        columnHComplete,
        currentBasisHash,
        currentRevision,
        summary,
        directColumnHCheck: true
      };
    },

    getWwwUsdReferenceReviewState(...args) {
      const originalState = this.getCaseWorkflowWwwUsdReferenceOriginalReviewState(args);
      return this.getCaseWorkflowWwwUsdReferenceDirectState({ originalState });
    },

    applyCaseWorkflowWwwUsdReferenceCurrentState(view = {}) {
      const state = this.getCaseWorkflowWwwUsdReferenceDirectState();
      const total = Number(state.referenceRowCount || 0);
      const missing = Number(state.missingUsdReferenceCount || 0);
      const ready = total > 0 && missing === 0;
      const current = ready && Boolean(state.reviewIsCurrent);
      const statusLabel = total <= 0
        ? 'Unchecked · no top-level rows found'
        : (current
          ? `Complete · ${total} starting row${total === 1 ? '' : 's'}`
          : (ready
            ? `Ready to review · ${total} Column H value${total === 1 ? '' : 's'} filled`
            : `Needs H · ${missing}/${total} missing`));
      return {
        ...(view || {}),
        complete: current,
        stale: false,
        blocked: false,
        status: current ? 'complete' : (ready ? 'ready' : 'unchecked'),
        statusLabel,
        statusClass: current ? 'workflow-step-card--complete' : (ready ? 'workflow-step-card--ready' : 'workflow-step-card--attention'),
        staleReason: '',
        completionSummary: state.summary || view?.completionSummary || '',
        wwwUsdReferenceState: {
          referenceRowCount: total,
          usableReferenceCount: Number(state.usableReferenceCount || 0),
          missingUsdReferenceCount: missing,
          columnHComplete: ready,
          reviewIsCurrent: current
        },
        sources: {
          ...(view?.sources || {}),
          check: current,
          signal: Boolean(current && (view?.sources?.signal || this.caseSupportState?.workflowState?.signalCompleteByCode?.WWW_USD_REFERENCES_REVIEWED)),
          manual: Boolean(current && view?.sources?.manual)
        }
      };
    },

    caseWorkflowExternalAttributionReviewIsCurrent() {
      const workflow = this.caseSupportState?.workflowState || {};
      const signals = workflow.signalCompleteByCode || {};
      if (this.caseWorkflowSignalRecordLooksComplete(signals.EXTERNAL_ATTRIBUTION_REVIEWED) || this.caseWorkflowSignalRecordLooksComplete(signals.EXTERNAL_ATTRIBUTION_REVIEW)) return true;
      const check = this.caseSupportState?.checkResultsById?.external_review || null;
      if (this.caseWorkflowCheckResultLooksPassing(check)) return true;
      try {
        const step = (Array.isArray(this.workflowGroupsView) ? this.workflowGroupsView : [])
          .flatMap(group => Array.isArray(group?.steps) ? group.steps : [])
          .find(candidate => this.isCaseWorkflowExternalAttributionReviewStep(candidate));
        return !!(step?.complete && !step?.stale && !step?.blocked);
      } catch (error) {
        return false;
      }
    },

    caseWorkflowStepIsAtOrAfterColumnHReferenceBoundary(step = null) {
      const candidate = step || this.recommendedWorkflowStep || null;
      if (!candidate) return false;
      if (this.isCaseWorkflowWwwUsdReferenceStep(candidate)) return true;
      const index = this.caseWorkflowStepOrderIndex(candidate);
      const boundary = this.caseWorkflowColumnHReferenceStepBoundaryIndex();
      if (index < 0 || boundary < 0 || !Number.isFinite(boundary)) return false;
      return index >= boundary;
    },

    caseWorkflowWwwUsdReferenceGateNeeded(step = null) {
      const candidate = step || this.recommendedWorkflowStep || null;
      const state = this.getCaseWorkflowWwwUsdReferenceDirectState();
      if (Number(state.referenceRowCount || 0) <= 0) return false;
      if (state.columnHComplete && state.reviewIsCurrent) return false;
      if (candidate && this.caseWorkflowStepIsAtOrAfterColumnHReferenceBoundary(candidate)) return true;
      return this.caseWorkflowExternalAttributionReviewIsCurrent();
    },

    async runCaseWorkflowWwwUsdReferenceGateIfNeeded(options = {}) {
      const step = options.step || this.recommendedWorkflowStep || null;
      this.reconcileCaseWorkflowWwwUsdReferenceCompletionState({ source: options.source || 'www-usd-reference-gate' });
      if (!options.force && !this.caseWorkflowWwwUsdReferenceGateNeeded(step)) return { handled: false, reason: 'www-usd-reference-gate-not-needed' };
      return this.runCaseWorkflowWwwUsdReferenceAutomationStep({
        ...(options || {}),
        step: this.isCaseWorkflowWwwUsdReferenceStep(step) ? step : this.getCaseWorkflowWwwUsdReferenceStepForPause(options),
        source: options.source || 'workflow-auto-www-usd-reference-gate'
      });
    },

    clearCaseWorkflowWwwUsdReferenceAutoGuardTimer() {
      if (!this.caseWorkflowWwwUsdReferenceAutoGuardTimer) return;
      if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(this.caseWorkflowWwwUsdReferenceAutoGuardTimer);
      } else {
        clearTimeout(this.caseWorkflowWwwUsdReferenceAutoGuardTimer);
      }
      this.caseWorkflowWwwUsdReferenceAutoGuardTimer = null;
    },

    clearCaseWorkflowWwwUsdReferenceAutoResumeTimer() {
      if (!this.caseWorkflowWwwUsdReferenceAutoResumeTimer) return;
      if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(this.caseWorkflowWwwUsdReferenceAutoResumeTimer);
      } else {
        clearTimeout(this.caseWorkflowWwwUsdReferenceAutoResumeTimer);
      }
      this.caseWorkflowWwwUsdReferenceAutoResumeTimer = null;
    },

    queueCaseWorkflowWwwUsdReferenceAutoGuard(options = {}) {
      if (!this.caseAutomation?.skipResolvableReview && !options.force) return false;
      this.clearCaseWorkflowWwwUsdReferenceAutoGuardTimer();
      const startedAtMs = Number(options.startedAtMs) || Date.now();
      const maxMs = Math.max(1000, Number(options.maxMs) || 60000);
      const delayMs = Math.max(0, Number(options.delayMs) || 250);
      const run = async () => {
        this.caseWorkflowWwwUsdReferenceAutoGuardTimer = null;
        try {
          if (!this.caseAutomation?.skipResolvableReview && !options.force) return;
          const automationStillActive = Boolean(this.caseAutomation?.active || this.caseAutomation?.busy || options.force || options.allowWhenInactive);
          if (!automationStillActive) return;
          if (this.caseWorkflowWwwUsdReferenceAutoGuardRunning) {
            if (Date.now() - startedAtMs < maxMs) {
              this.queueCaseWorkflowWwwUsdReferenceAutoGuard({ ...options, startedAtMs, delayMs: 250, maxMs });
            }
            return;
          }
          const state = this.getCaseWorkflowWwwUsdReferenceDirectState();
          const hasMissingReferences = Number(state.referenceRowCount || 0) > 0 && !state.columnHComplete;
          const attributionCurrent = options.forceExternalReviewCurrent || this.caseWorkflowExternalAttributionReviewIsCurrent();
          if (hasMissingReferences && attributionCurrent) {
            await this.runCaseWorkflowWwwUsdReferenceAutoGuard({
              ...options,
              source: options.source || 'www-usd-reference-auto-guard',
              continueAfter: options.continueAfter !== false,
              force: true
            });
            return;
          }
          const stillWorthWatching = Boolean(this.caseAutomation?.active || this.caseAutomation?.busy || options.keepWatching);
          if (stillWorthWatching && Date.now() - startedAtMs < maxMs) {
            this.queueCaseWorkflowWwwUsdReferenceAutoGuard({ ...options, startedAtMs, delayMs: 350, maxMs });
          }
        } catch (error) {
          console.warn('Case Buddy Column H USD reference Auto guard failed.', error);
        }
      };
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
        this.caseWorkflowWwwUsdReferenceAutoGuardTimer = window.setTimeout(run, delayMs);
      } else {
        this.caseWorkflowWwwUsdReferenceAutoGuardTimer = setTimeout(run, delayMs);
      }
      return true;
    },

    async runCaseWorkflowWwwUsdReferenceAutoGuard(options = {}) {
      if (this.caseWorkflowWwwUsdReferenceAutoGuardRunning) return { handled: true, success: false, reason: 'www-usd-reference-auto-guard-running' };
      if (!this.caseAutomation?.skipResolvableReview && !options.force) return { handled: false, reason: 'skip-resolvable-reviews-off' };
      const state = this.reconcileCaseWorkflowWwwUsdReferenceCompletionState({ source: options.source || 'www-usd-reference-auto-guard' })
        || this.getCaseWorkflowWwwUsdReferenceDirectState();
      if (Number(state.referenceRowCount || 0) <= 0) return { handled: false, reason: 'no-www-usd-reference-rows' };
      if (state.columnHComplete && state.reviewIsCurrent) return { handled: false, success: true, reason: 'www-usd-references-current' };
      if (!options.force && !this.caseWorkflowExternalAttributionReviewIsCurrent() && !this.caseWorkflowStepIsAtOrAfterColumnHReferenceBoundary(this.recommendedWorkflowStep)) {
        return { handled: false, reason: 'external-attribution-not-current' };
      }
      this.caseWorkflowWwwUsdReferenceAutoGuardRunning = true;
      this.caseWorkflowWwwUsdReferenceAutoGuardLastRunAtMs = Date.now();
      this.clearCaseWorkflowWwwUsdReferenceAutoGuardTimer();
      this.beginCaseWorkflowReviewAutoUiSuppression();
      // Do not clear the main workflow automation timer here. Clearing that timer can be
      // interpreted by the legacy automation layer as an investigator stop, which is why
      // the UI sometimes reported "Automation stopped by user" after Column H auto-fill.
      const step = this.getCaseWorkflowWwwUsdReferenceStepForPause(options);
      const action = { id: 'auto_guard_www_usd_references', label: 'Auto-fill Column H USD references' };
      const continueAfter = options.continueAfter !== false;
      try {
        this.caseAutomation = {
          ...(this.caseAutomation || {}),
          active: true,
          busy: true,
          paused: false,
          manualPause: null,
          status: 'Estimating Column H USD references…',
          detail: '100% Automated Mode is on, so Case Buddy is filling missing root-row Column H USD reference values before cleanup.',
          lastPlan: {
            stepTag: 'WWW_USD_REFERENCES',
            stepTitle: step?.title || step?.stepTitle || 'Review top-level USD reference values',
            action: 'review_www_usd_references',
            status: 'Estimating Column H USD references…',
            detail: 'Auto guard inserted the Column H USD reference step before cleanup.',
            type: 'method',
            method: 'runCaseWorkflowWwwUsdReferenceAutomationStep'
          }
        };
        this.appendCaseWorkflowAutomationLog?.('Column H USD reference guard is running before cleanup…', {
          stepTag: 'WWW_USD_REFERENCES',
          source: options.source || 'www-usd-reference-auto-guard'
        });
        const result = await this.runCaseWorkflowWwwUsdReferenceAutomationStep({
          step,
          action,
          source: options.source || 'www-usd-reference-auto-guard'
        });
        if (result?.success) {
          this.caseAutomation = {
            ...(this.caseAutomation || {}),
            active: false,
            busy: false,
            paused: false,
            manualPause: null,
            status: 'Column H USD references filled',
            detail: continueAfter
              ? 'Root-row Column H USD reference values are filled; continuing workflow automation.'
              : 'Root-row Column H USD reference values are filled.'
          };
          if (continueAfter) {
            this.queueCaseWorkflowWwwUsdReferenceAutoResume({
              source: options.source || 'www-usd-reference-auto-guard',
              stopToken: Number(this.caseWorkflowWwwUsdReferenceStopToken || 0),
              delayMs: Number(options.resumeDelayMs) || 180
            });
          }
        }
        return result;
      } finally {
        this.caseWorkflowWwwUsdReferenceAutoGuardRunning = false;
        this.endCaseWorkflowReviewAutoUiSuppression();
      }
    },

    queueCaseWorkflowWwwUsdReferenceAutoResume(options = {}) {
      const delayMs = Math.max(0, Number(options.delayMs) || 180);
      this.clearCaseWorkflowWwwUsdReferenceAutoResumeTimer?.();
      const stopToken = options.stopToken === undefined
        ? Number(this.caseWorkflowWwwUsdReferenceStopToken || 0)
        : Number(options.stopToken || 0);
      const run = () => {
        this.caseWorkflowWwwUsdReferenceAutoResumeTimer = null;
        if (Number(this.caseWorkflowWwwUsdReferenceStopToken || 0) !== stopToken) return;
        if (this.caseAutomation?.busy || this.caseAutomation?.paused) return;
        if (!this.caseAutomation?.skipResolvableReview) return;
        const state = this.getCaseWorkflowWwwUsdReferenceDirectState();
        if (Number(state.referenceRowCount || 0) > 0 && !state.columnHComplete) return;
        this.startCaseAutomation().catch?.((error) => {
          console.warn('Case Buddy could not resume Auto after Column H USD reference guard.', error);
        });
      };
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
        this.caseWorkflowWwwUsdReferenceAutoResumeTimer = window.setTimeout(run, delayMs);
      } else {
        this.caseWorkflowWwwUsdReferenceAutoResumeTimer = setTimeout(run, delayMs);
      }
    },

    getCaseWorkflowWhatWentWhereStepForPause(options = {}) {
      const candidates = [
        options.step,
        this.caseAutomation?.lastPlan,
        this.caseAutomation?.manualPause,
        this.recommendedWorkflowStep,
        { tag: 'WHAT_WENT_WHERE', stepTag: 'WHAT_WENT_WHERE', title: 'Build and review What Went Where' }
      ];
      return candidates.find(candidate => {
        const tag = normalizeCell(candidate?.stepTag || candidate?.tag || candidate?.id || '').toUpperCase();
        const title = normalizeCell(candidate?.stepTitle || candidate?.title || '').toLowerCase();
        return tag === 'WHAT_WENT_WHERE' || /what\s+went\s+where/.test(title);
      }) || candidates[candidates.length - 1];
    },

    getCaseWorkflowWhatWentWhereReferenceRowsFromMain() {
      try {
        return this.getCaseWorkflowWwwUsdReferenceDirectState().referenceRows || [];
      } catch (error) {
        console.warn('Case Buddy could not build current What Went Where reference rows for workflow Auto.', error);
        return [];
      }
    },

    getCaseWorkflowWhatWentWhereReferenceCompletionFromMain() {
      const state = this.getCaseWorkflowWwwUsdReferenceDirectState();
      const referenceRows = state.referenceRows || [];
      const missingRows = state.missingRows || referenceRows.filter(row => !row.hasUsableColumnH);
      return {
        referenceRows,
        missingRows,
        referenceRowCount: referenceRows.length,
        missingCount: missingRows.length,
        isComplete: Boolean(state.columnHComplete)
      };
    },

    async forceCaseWorkflowWhatWentWhereReferenceCellWrites(estimates = []) {
      const usable = (Array.isArray(estimates) ? estimates : []).filter(item => item?.ok && Number.isInteger(Number(item.row?.rowIndex)) && item.valueText);
      if (!usable.length) return false;
      this.recordCaseWorkflowColumnHWriteAudit?.({
        stage: 'force-hot-column-h-write-start',
        source: 'forceCaseWorkflowWhatWentWhereReferenceCellWrites',
        estimateCount: usable.length,
        targets: usable.slice(0, 12).map(item => ({
          rowIndex: item.row?.rowIndex,
          pathLabel: item.row?.pathLabel,
          hash: item.row?.transactionHash || item.row?.hash,
          valueText: item.valueText
        }))
      });
      const workbook = this.$refs.inputSpreadsheet;
      if (typeof this.activateLogicalSheet === 'function') {
        await this.activateLogicalSheet('main');
        await this.caseWorkflowUiNextTick();
      }
      const hot = workbook?.hotInstance || workbook?.hot || workbook?.$refs?.hotTable?.hotInstance || workbook?.$refs?.spreadsheet?.hotInstance || null;
      if (!hot || typeof hot.setDataAtCell !== 'function') return false;
      usable.forEach((estimate) => {
        hot.setDataAtCell(Number(estimate.row.rowIndex), 7, estimate.valueText, 'case-buddy-workflow-auto-www-reference');
      });
      await this.caseWorkflowUiNextTick();
      if (typeof this.markCaseDataDirty === 'function') this.markCaseDataDirty('Updated Column H USD reference values for What Went Where workflow Auto.');
      this.recordCaseWorkflowColumnHWriteAudit?.({
        stage: 'force-hot-column-h-write-complete',
        source: 'forceCaseWorkflowWhatWentWhereReferenceCellWrites',
        estimateCount: usable.length,
        completion: this.getCaseWorkflowWhatWentWhereReferenceCompletionFromMain?.() || null
      });
      return true;
    },

    async applyCaseWorkflowWhatWentWhereReferencesViaModalTools(state = null, options = {}) {
      const targetState = state || this.getWhatWentWhereState?.() || this.whatWentWhereState || {};
      const missingRows = (Array.isArray(targetState.referenceRows) ? targetState.referenceRows : []).filter(row => !row.isUsable);
      if (!missingRows.length) return { attempted: false, applied: false, message: 'No missing Column H reference rows were visible to the What Went Where tools.' };
      this.whatWentWhereSelectedReferenceKeys = {
        ...(this.whatWentWhereSelectedReferenceKeys || {}),
        ...Object.fromEntries(missingRows.map(row => [row.key, true]))
      };
      if (typeof this.estimateWhatWentWhereUsdReferences === 'function') {
        const estimateResult = this.estimateWhatWentWhereUsdReferences({
          selectedOnly: false,
          source: options.source || 'workflow-auto-what-went-where',
          skipConfirmation: true,
          suppressConfirmation: true,
          workflowAuto: true
        });
        if (estimateResult && typeof estimateResult.then === 'function') await estimateResult;
        await this.caseWorkflowUiNextTick();
      }
      if (typeof this.applySelectedWhatWentWhereUsdReferences === 'function') {
        const applyResult = this.applySelectedWhatWentWhereUsdReferences({
          source: options.source || 'workflow-auto-what-went-where',
          skipConfirmation: true,
          suppressConfirmation: true,
          workflowAuto: true
        });
        if (applyResult && typeof applyResult.then === 'function') await applyResult;
        await this.caseWorkflowUiNextTick();
        return { attempted: true, applied: true, result: applyResult };
      }
      return { attempted: true, applied: false, message: 'The What Went Where estimate/apply helpers were not available.' };
    },

    async applySelectedWhatWentWhereUsdReferences(options = {}) {
      const shouldBypassConfirmation = !!(options?.skipConfirmation || options?.suppressConfirmation || options?.workflowAuto || options?.autoApply);
      const callOriginal = () => this.callFirstCaseBuddyOriginalMethod('applySelectedWhatWentWhereUsdReferences', []);
      if (!shouldBypassConfirmation || typeof window === 'undefined' || typeof window.confirm !== 'function') {
        return callOriginal();
      }
      const originalConfirm = window.confirm;
      const source = normalizeCell(options.source || 'workflow-auto-what-went-where');
      window.confirm = (message) => {
        const text = normalizeCell(message).toLowerCase();
        if (/apply\s+\d+\s+column\s+h\s+usd\s+reference/.test(text) || (source.includes('workflow-auto') && /column\s+h\s+usd\s+reference/.test(text))) {
          return true;
        }
        return originalConfirm.call(window, message);
      };
      try {
        const result = callOriginal();
        return result && typeof result.then === 'function' ? await result : result;
      } finally {
        window.confirm = originalConfirm;
      }
    },

    caseWorkflowWhatWentWhereEstimateValueForReference(row = {}) {
      const amount = Number(row?.referenceAmount);
      const token = normalizeCell(row?.referenceToken);
      const timestampUtc = normalizeCell(row?.utcDate);
      if (!Number.isFinite(amount) || amount <= 0 || !token || !timestampUtc) {
        return { ok: false, row, status: row?.problemText || 'Missing amount, token, or UTC date.' };
      }
      const estimate = estimateCaseBuddyHistoricalUsdValue({ symbol: token, timestampUtc, amount });
      if (!estimate?.ok || !Number.isFinite(Number(estimate.estimatedUsdValue))) {
        return { ok: false, row, status: estimate?.status || `Could not estimate ${token} USD value.` };
      }
      return {
        ok: true,
        row,
        estimatedUsdValue: Number(estimate.estimatedUsdValue),
        valueText: this.formatCaseWorkflowColumnHUsdCellValue(Number(estimate.estimatedUsdValue)),
        status: estimate.status || 'Estimated.',
        snapshotTimestampUtc: estimate.snapshotTimestampUtc || '',
        unitPriceUsd: estimate.unitPriceUsd
      };
    },

    buildCaseWorkflowWhatWentWhereAutoReferenceEstimates(state = null) {
      const targetState = state || this.getWhatWentWhereState?.() || this.whatWentWhereState || {};
      const missingRows = (Array.isArray(targetState.referenceRows) ? targetState.referenceRows : []).filter(row => !(row?.isUsable || row?.hasUsableColumnH));
      const estimates = [];
      const failures = [];
      missingRows.forEach((row) => {
        const result = this.caseWorkflowWhatWentWhereEstimateValueForReference(row);
        if (result.ok) estimates.push(result);
        else failures.push(result);
      });
      return { estimates, failures, missingRows };
    },

    async applyCaseWorkflowWhatWentWhereReferenceEstimates(estimates = [], options = {}) {
      const usable = (Array.isArray(estimates) ? estimates : []).filter(item => item?.ok && Number.isInteger(Number(item.row?.rowIndex)) && item.valueText);
      if (!usable.length) {
        this.recordCaseWorkflowColumnHWriteAudit?.({
          stage: 'apply-reference-estimates-no-usable-estimates',
          source: options.source || 'workflow-auto-what-went-where',
          estimateCount: Array.isArray(estimates) ? estimates.length : 0
        });
        return { changed: false, appliedCount: 0, verified: false };
      }
      const beforeRevision = Number(options.beforeRevision) || Number(this.caseSupportState?.caseDataRevision) || 0;
      this.recordCaseWorkflowColumnHWriteAudit?.({
        stage: 'apply-reference-estimates-start',
        source: options.source || 'workflow-auto-what-went-where',
        beforeRevision,
        estimateCount: usable.length,
        targets: usable.slice(0, 12).map(item => ({
          rowIndex: item.row?.rowIndex,
          spreadsheetRowNumber: item.row?.spreadsheetRowNumber,
          pathLabel: item.row?.pathLabel,
          hash: item.row?.transactionHash || item.row?.hash,
          currentColumnH: item.row?.currentColumnH,
          valueText: item.valueText,
          amount: item.row?.referenceAmount,
          token: item.row?.referenceToken,
          utcDate: item.row?.utcDate
        }))
      });
      const preservedStepTags = Array.isArray(options.preservedStepTags) ? options.preservedStepTags.slice() : this.captureCaseWorkflowColumnHReferencePreservedStepTags();
      const source = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('main') : [];
      const matrix = normalizeCaseMatrix(Array.isArray(source) ? source : []);
      let changed = false;
      usable.forEach((estimate) => {
        const rowIndex = Number(estimate.row.rowIndex);
        if (!matrix[rowIndex]) return;
        const row = this.caseManualEnsureRowWidth
          ? this.caseManualEnsureRowWidth(matrix[rowIndex], Math.max(CASE_MANUAL_START_COLUMN_COUNT, matrix[rowIndex]?.length || 0))
          : matrix[rowIndex].slice();
        const current = normalizeCell(row[7]);
        if (current === estimate.valueText) return;
        row[7] = estimate.valueText;
        matrix[rowIndex] = row;
        changed = true;
      });
      if (!changed) {
        const verified = this.getCaseWorkflowWhatWentWhereReferenceCompletionFromMain().isComplete;
        this.recordCaseWorkflowColumnHWriteAudit?.({
          stage: 'apply-reference-estimates-no-change',
          source: options.source || 'workflow-auto-what-went-where',
          estimateCount: usable.length,
          verified
        });
        return { changed: false, appliedCount: 0, verified };
      }
      if (typeof this.replaceCaseManualStartLogicalSheetMatrix === 'function') {
        await this.replaceCaseManualStartLogicalSheetMatrix('main', matrix, 'Updated Column H USD reference values for What Went Where workflow Auto.', { autosaveDelayMs: 900 });
      } else if (this.$refs.inputSpreadsheet?.loadData) {
        this.$refs.inputSpreadsheet.loadData(matrix);
        if (typeof this.markCaseDataDirty === 'function') this.markCaseDataDirty('Updated Column H USD reference values for What Went Where workflow Auto.');
      }
      await this.caseWorkflowUiNextTick();
      let completion = this.getCaseWorkflowWhatWentWhereReferenceCompletionFromMain();
      let forced = false;
      if (!completion.isComplete) {
        forced = await this.forceCaseWorkflowWhatWentWhereReferenceCellWrites(usable);
        await this.caseWorkflowUiNextTick();
        completion = this.getCaseWorkflowWhatWentWhereReferenceCompletionFromMain();
      }
      const afterRevision = Number(this.caseSupportState?.caseDataRevision) || beforeRevision;
      this.recordCaseWorkflowColumnHWriteAudit?.({
        stage: 'apply-reference-estimates-after-write',
        source: options.source || 'workflow-auto-what-went-where',
        beforeRevision,
        afterRevision,
        estimateCount: usable.length,
        forced,
        verified: Boolean(completion.isComplete),
        completion: {
          referenceRowCount: Number(completion.referenceRowCount || 0),
          missingCount: Number(completion.missingCount || 0),
          isComplete: Boolean(completion.isComplete)
        },
        rows: (completion.referenceRows || []).slice(0, 12).map(row => ({
          rowIndex: row.rowIndex,
          pathLabel: row.pathLabel,
          hash: row.transactionHash,
          columnH: row.currentColumnH,
          hasUsableColumnH: Boolean(row.hasUsableColumnH)
        }))
      });
      if (completion.isComplete) {
        this.recordCaseWorkflowColumnHReferenceAutoApply({
          beforeRevision,
          afterRevision,
          preservedStepTags,
          appliedCount: usable.length,
          source: options.source || 'workflow-auto-what-went-where'
        });
      }
      return { changed: true, appliedCount: usable.length, verified: completion.isComplete, forced };
    },

    pauseCaseWorkflowWhatWentWhereAuto(message = 'What Went Where needs investigator attention.', options = {}) {
      const step = this.getCaseWorkflowWhatWentWhereStepForPause(options);
      const now = this.emptyCaseStarterNudgeNowUtc ? this.emptyCaseStarterNudgeNowUtc() : new Date().toISOString();
      this.caseAutomation = {
        ...(this.caseAutomation || {}),
        active: false,
        busy: false,
        paused: true,
        status: 'What Went Where needs attention',
        detail: message,
        manualPause: {
          type: 'manual-pause',
          pauseKind: 'what-went-where',
          stepTag: normalizeCell(step?.stepTag || step?.tag || 'WHAT_WENT_WHERE'),
          stepTitle: step?.stepTitle || step?.title || 'Build and review What Went Where',
          title: 'What Went Where needs attention',
          message,
          primaryLabel: 'Open What Went Where',
          primaryMethod: 'openWhatWentWhereModal',
          openedAtUtc: now
        }
      };
      this.showCaseAutomationPauseModal = true;
      this.whatWentWhereStatus = message;
      return { handled: true, success: false, paused: true, reason: options.reason || 'what-went-where-needs-attention', message };
    },

    async autoResolveWhatWentWhereForWorkflow(options = {}) {
      const originalState = this.getWhatWentWhereState?.() || this.whatWentWhereState || {};
      const beforeReferenceRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const preservedStepTags = this.captureCaseWorkflowColumnHReferencePreservedStepTags();
      let appliedReferences = 0;
      if (Number(originalState.missingUsdReferenceCount || 0) > 0) {
        const estimateState = this.buildCaseWorkflowWhatWentWhereAutoReferenceEstimates(originalState);
        if (estimateState.failures.length > 0) {
          const firstFailure = estimateState.failures[0];
          const message = `What Went Where still needs Column H USD reference values. ${firstFailure?.status || 'At least one value could not be estimated.'}`;
          this.whatWentWhereStatus = message;
          return { handled: false, success: false, reason: 'www-reference-estimate-failed', message, failures: estimateState.failures };
        }
        const applyResult = await this.applyCaseWorkflowWhatWentWhereReferenceEstimates(estimateState.estimates, {
          beforeRevision: beforeReferenceRevision,
          preservedStepTags,
          source: options.source || 'workflow-auto-what-went-where'
        });
        appliedReferences = Number(applyResult.appliedCount || 0);
        if (applyResult.changed) await this.caseWorkflowUiNextTick();
      }

      let state = this.getWhatWentWhereState?.() || this.whatWentWhereState || {};
      let directReferenceCompletion = this.getCaseWorkflowWhatWentWhereReferenceCompletionFromMain();
      if (!state.canBuild && !directReferenceCompletion.isComplete && Number(state.missingUsdReferenceCount || 0) > 0) {
        const fallbackResult = await this.applyCaseWorkflowWhatWentWhereReferencesViaModalTools(state, {
          source: options.source || 'workflow-auto-what-went-where'
        });
        await this.caseWorkflowUiNextTick();
        state = this.getWhatWentWhereState?.() || this.whatWentWhereState || {};
        directReferenceCompletion = this.getCaseWorkflowWhatWentWhereReferenceCompletionFromMain();
        if (fallbackResult.applied && appliedReferences === 0) {
          appliedReferences = directReferenceCompletion.referenceRowCount || Number(state.referenceRowCount || 0) || 0;
          this.recordCaseWorkflowColumnHReferenceAutoApply({
            beforeRevision: beforeReferenceRevision,
            afterRevision: Number(this.caseSupportState?.caseDataRevision) || beforeReferenceRevision,
            preservedStepTags,
            appliedCount: appliedReferences,
            source: options.source || 'workflow-auto-what-went-where'
          });
        }
      }

      if (!state.canBuild && directReferenceCompletion.isComplete) {
        state = {
          ...state,
          canBuild: true,
          canReview: true,
          missingUsdReferenceCount: 0,
          usableReferenceCount: directReferenceCompletion.referenceRowCount,
          referenceRowCount: directReferenceCompletion.referenceRowCount,
          referenceRows: directReferenceCompletion.referenceRows
        };
      }

      if (!state.canBuild) {
        const completion = directReferenceCompletion;
        const message = completion.referenceRowCount > 0 && completion.missingCount > 0
          ? `${completion.referenceRowCount - completion.missingCount}/${completion.referenceRowCount} top-level main transaction${completion.referenceRowCount === 1 ? '' : 's'} have usable Column H USD values. ${completion.missingCount} still need attention before What Went Where can be reviewed.`
          : (state.summary || 'What Went Where is not buildable yet.');
        this.whatWentWhereStatus = message;
        return { handled: false, success: false, reason: 'www-not-buildable', message };
      }

      if (typeof this.stageWhatWentWherePreview === 'function') await this.stageWhatWentWherePreview();
      if (typeof this.stageWhatWentWhereValuationAudit === 'function') await this.stageWhatWentWhereValuationAudit();
      const refreshedState = this.getWhatWentWhereState?.() || this.whatWentWhereState || state;
      if (!refreshedState.reviewIsCurrent) {
        const markResult = this.callFirstCaseBuddyOriginalMethod
          ? await this.callFirstCaseBuddyOriginalMethod('markWhatWentWhereReviewComplete', [])
          : (typeof this.markWhatWentWhereReviewComplete === 'function' ? await this.markWhatWentWhereReviewComplete() : undefined);
        if (markResult === false) {
          return { handled: false, success: false, reason: 'www-review-mark-failed', message: 'What Went Where could not be marked reviewed automatically.' };
        }
      }
      await this.caseWorkflowUiNextTick?.();
      const finalState = this.getWhatWentWhereState?.() || this.whatWentWhereState || refreshedState || state;
      if (!finalState?.reviewIsCurrent) {
        return {
          handled: false,
          success: false,
          reason: 'www-review-not-current-after-mark',
          message: finalState?.summary || finalState?.statusLabel || 'What Went Where was staged, but the reviewed-state record is still not current.'
        };
      }
      const wwwConfig = this.caseWorkflowStatefulGeneratedReviewConfig?.('WHAT_WENT_WHERE');
      if (wwwConfig) this.recordCaseWorkflowStatefulGeneratedReviewCurrent(wwwConfig, finalState, {
        source: options.source || 'workflow-auto-what-went-where',
        methodName: 'autoResolveWhatWentWhereForWorkflow'
      });
      this.whatWentWhereStatus = appliedReferences > 0
        ? `Workflow Auto estimated and applied ${appliedReferences} Column H value${appliedReferences === 1 ? '' : 's'}, then marked What Went Where reviewed.`
        : 'Workflow Auto marked What Went Where reviewed.';
      return { handled: true, success: true, appliedReferences, source: options.source || 'workflow-auto-what-went-where' };
    },

    async automationReviewWhatWentWhere(...args) {
      if (this.caseAutomation?.skipResolvableReview) {
        const autoResult = await this.autoResolveWhatWentWhereForWorkflow({ source: 'automationReviewWhatWentWhere' });
        if (autoResult?.success) return autoResult;
        return this.pauseCaseWorkflowWhatWentWhereAuto(autoResult?.message || 'What Went Where could not be completed automatically.', { reason: autoResult?.reason || 'what-went-where-auto-failed' });
      }
      return this.callCaseBuddyOriginalMethod(caseBuddyAutomationMethods, 'automationReviewWhatWentWhere', args);
    },

    async automationBuildAndReviewWhatWentWhere(...args) {
      if (this.caseAutomation?.skipResolvableReview) {
        const autoResult = await this.autoResolveWhatWentWhereForWorkflow({ source: 'automationBuildAndReviewWhatWentWhere' });
        if (autoResult?.success) return autoResult;
        return this.pauseCaseWorkflowWhatWentWhereAuto(autoResult?.message || 'What Went Where could not be completed automatically.', { reason: autoResult?.reason || 'what-went-where-auto-failed' });
      }
      return this.callCaseBuddyOriginalMethod(caseBuddyAutomationMethods, 'automationBuildAndReviewWhatWentWhere', args);
    },

    captureCaseInvestigationVisualizerVisualEditState() {
      const state = {
        hiddenNodeIds: Array.isArray(this.caseInvestigationVisualizerHiddenNodeIds) ? this.caseInvestigationVisualizerHiddenNodeIds.slice() : [],
        selectedNodeIds: Array.isArray(this.caseInvestigationVisualizerSelectedNodeIds) ? this.caseInvestigationVisualizerSelectedNodeIds.slice() : [],
        selectedNodeId: this.caseInvestigationVisualizerSelectedNodeId || '',
        transactionOnlyMode: Boolean(this.caseInvestigationVisualizerTransactionOnlyMode)
      };
      if (this.caseInvestigationVisualizerManualStartMode || this.caseSupportState?.manualSolveVisualColorOverrides) {
        state.manualSolveVisualColorOverrides = this.cloneCaseManualSolveVisualColorOverrides() || { schemaVersion: 1, byVisualKey: {} };
      }
      if (this.caseInvestigationVisualizerManualStartMode || this.caseSupportState?.manualSolveVisualGroups) {
        state.manualSolveVisualGroups = this.cloneCaseManualSolveVisualGroups() || { schemaVersion: 1, byVisualKey: {} };
      }
      return state;
    },

    restoreCaseInvestigationVisualizerVisualEditState(state = {}) {
      this.caseInvestigationVisualizerHiddenNodeIds = Array.isArray(state.hiddenNodeIds) ? state.hiddenNodeIds.slice() : [];
      this.caseInvestigationVisualizerSelectedNodeIds = Array.isArray(state.selectedNodeIds) ? state.selectedNodeIds.slice() : [];
      this.caseInvestigationVisualizerSelectedNodeId = state.selectedNodeId || this.caseInvestigationVisualizerSelectedNodeIds[0] || '';
      this.caseInvestigationVisualizerTransactionOnlyMode = Boolean(state.transactionOnlyMode);
      const selectedNode = this.caseInvestigationVisualizerSelectedNode;
      this.caseInvestigationVisualizerSelectedCandidate = selectedNode?.caseCandidate || selectedNode?.candidate || null;
      if (state.manualSolveVisualColorOverrides !== undefined) {
        this.restoreCaseManualSolveVisualColorOverridesFromHistory(state.manualSolveVisualColorOverrides);
      }
      if (state.manualSolveVisualGroups !== undefined) {
        this.restoreCaseManualSolveVisualGroupsFromHistory(state.manualSolveVisualGroups);
      }
      this.pruneCaseInvestigationVisualizerSelectionForTransactionOnlyMode?.();
      this.scheduleCaseManualStartViewportAutomationCalloutPositionRefresh?.();
    },

    pushCaseInvestigationVisualizerVisualEditHistory(beforeState = null) {
      const snapshot = beforeState || this.captureCaseInvestigationVisualizerVisualEditState();
      const stack = Array.isArray(this.caseInvestigationVisualizerUndoStack) ? this.caseInvestigationVisualizerUndoStack.slice() : [];
      stack.push(snapshot);
      const limit = Math.max(5, Number(this.caseInvestigationVisualizerHistoryLimit) || 40);
      this.caseInvestigationVisualizerUndoStack = stack.slice(-limit);
      this.caseInvestigationVisualizerRedoStack = [];
    },

    undoCaseInvestigationVisualizerVisualEdit() {
      const stack = Array.isArray(this.caseInvestigationVisualizerUndoStack) ? this.caseInvestigationVisualizerUndoStack.slice() : [];
      if (!stack.length) return;
      const current = this.captureCaseInvestigationVisualizerVisualEditState();
      const previous = stack.pop();
      this.caseInvestigationVisualizerUndoStack = stack;
      this.caseInvestigationVisualizerRedoStack = [
        current,
        ...(Array.isArray(this.caseInvestigationVisualizerRedoStack) ? this.caseInvestigationVisualizerRedoStack : [])
      ].slice(0, Math.max(5, Number(this.caseInvestigationVisualizerHistoryLimit) || 40));
      this.restoreCaseInvestigationVisualizerVisualEditState(previous);
      this.caseInvestigationVisualizerStatus = 'Visual edit undone. Main Data was not changed.';
    },

    redoCaseInvestigationVisualizerVisualEdit() {
      const stack = Array.isArray(this.caseInvestigationVisualizerRedoStack) ? this.caseInvestigationVisualizerRedoStack.slice() : [];
      if (!stack.length) return;
      const current = this.captureCaseInvestigationVisualizerVisualEditState();
      const next = stack.shift();
      this.caseInvestigationVisualizerRedoStack = stack;
      this.caseInvestigationVisualizerUndoStack = [
        ...(Array.isArray(this.caseInvestigationVisualizerUndoStack) ? this.caseInvestigationVisualizerUndoStack : []),
        current
      ].slice(-Math.max(5, Number(this.caseInvestigationVisualizerHistoryLimit) || 40));
      this.restoreCaseInvestigationVisualizerVisualEditState(next);
      this.caseInvestigationVisualizerStatus = 'Visual edit redone. Main Data was not changed.';
    },

    collectCaseInvestigationVisualizerDownstreamNodeIds(startNodeIds = []) {
      const selected = new Set((Array.isArray(startNodeIds) ? startNodeIds : []).filter(Boolean));
      const hidden = this.caseInvestigationVisualizerHiddenNodeSet instanceof Set
        ? this.caseInvestigationVisualizerHiddenNodeSet
        : new Set(Array.isArray(this.caseInvestigationVisualizerHiddenNodeIds) ? this.caseInvestigationVisualizerHiddenNodeIds : []);
      const available = new Set((this.caseInvestigationVisualizerNodes || []).map(node => node?.id).filter(Boolean));
      const adjacency = new Map();
      (Array.isArray(this.caseInvestigationVisualizerCurrentVisual?.edges) ? this.caseInvestigationVisualizerCurrentVisual.edges : []).forEach((edge) => {
        const from = edge?.from || '';
        const to = edge?.to || '';
        if (!from || !to || hidden.has(from) || hidden.has(to) || !available.has(from) || !available.has(to)) return;
        if (!adjacency.has(from)) adjacency.set(from, []);
        adjacency.get(from).push(to);
      });
      const result = new Set();
      const queue = Array.from(selected).filter(id => available.has(id) && !hidden.has(id));
      while (queue.length) {
        const nodeId = queue.shift();
        if (!nodeId || result.has(nodeId)) continue;
        result.add(nodeId);
        (adjacency.get(nodeId) || []).forEach(nextId => {
          if (!result.has(nextId)) queue.push(nextId);
        });
      }
      return Array.from(result);
    },

    hideSelectedCaseInvestigationVisualizerBranchesVisually() {
      const selectedIds = Array.isArray(this.caseInvestigationVisualizerSelectedNodeIds) ? this.caseInvestigationVisualizerSelectedNodeIds.filter(Boolean) : [];
      if (!selectedIds.length) return;
      const before = this.captureCaseInvestigationVisualizerVisualEditState();
      const toHide = this.collectCaseInvestigationVisualizerDownstreamNodeIds(selectedIds);
      if (!toHide.length) return;
      const hidden = new Set(Array.isArray(this.caseInvestigationVisualizerHiddenNodeIds) ? this.caseInvestigationVisualizerHiddenNodeIds : []);
      toHide.forEach(id => hidden.add(id));
      this.pushCaseInvestigationVisualizerVisualEditHistory(before);
      this.caseInvestigationVisualizerHiddenNodeIds = Array.from(hidden);
      this.caseInvestigationVisualizerSelectedNodeId = '';
      this.caseInvestigationVisualizerSelectedNodeIds = [];
      this.caseInvestigationVisualizerSelectedCandidate = null;
      this.caseInvestigationVisualizerStatus = `Hidden ${toHide.length} visual node${toHide.length === 1 ? '' : 's'} in this viewport only. Main Data was not changed.`;
    },

    restoreCaseInvestigationVisualizerHiddenNodes() {
      if (!Array.isArray(this.caseInvestigationVisualizerHiddenNodeIds) || !this.caseInvestigationVisualizerHiddenNodeIds.length) return;
      const before = this.captureCaseInvestigationVisualizerVisualEditState();
      this.pushCaseInvestigationVisualizerVisualEditHistory(before);
      this.caseInvestigationVisualizerHiddenNodeIds = [];
      this.caseInvestigationVisualizerStatus = 'Hidden visual nodes restored. Main Data was not changed.';
    },

    deleteSelectedCaseInvestigationVisualizerBranches() {
      if (!this.caseInvestigationVisualizerSelectedCount) return;
      if (!this.caseInvestigationVisualizerManualStartMode) {
        this.hideSelectedCaseInvestigationVisualizerBranchesVisually();
        return;
      }
      if (this.caseManualStartViewportAutoActive) {
        this.showCaseToast?.('Stop Manual Solve Auto before deleting a branch from Main Data.');
        return;
      }
      this.openCaseManualStartBranchDeletionPreview();
    },

    caseManualStartBranchDeletionSelectedNode() {
      const selected = Array.isArray(this.caseInvestigationVisualizerSelectedNodes) ? this.caseInvestigationVisualizerSelectedNodes : [];
      return selected.find(node => (node?.caseCandidate || node?.candidate)?.caseManualStartCandidate) || selected[0] || this.caseInvestigationVisualizerSelectedNode || null;
    },

    caseManualStartBranchDeletionSelectedCandidate(node = null) {
      const selectedNode = node || this.caseManualStartBranchDeletionSelectedNode();
      return selectedNode?.caseCandidate || selectedNode?.candidate || this.caseInvestigationVisualizerSelectedCandidate || this.caseManualStartHopInspectorSelectedCandidate || null;
    },

    parseCaseManualStartBranchDeletionEntries(matrix = []) {
      const rows = Array.isArray(matrix) ? matrix.map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))) : [];
      const entries = [];
      let current = null;
      rows.forEach((row, rowIndex) => {
        if (rowIndex === 0 && this.caseManualIsHeaderRow(row)) return;
        if (this.caseManualIsHeaderRow(row)) {
          current = null;
          return;
        }
        const meaningful = rowHasMeaningfulData(row);
        if (!meaningful) return;
        const hash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]);
        if (hash && this.caseManualIsLikelyTransactionHash(hash)) {
          current = {
            id: `entry-${rowIndex}-${hash.toLowerCase()}`,
            startRowIndex: rowIndex,
            hash,
            hashKey: hash.toLowerCase(),
            rows: []
          };
          entries.push(current);
        }
        if (current) {
          current.rows.push({ rowIndex, row });
          current.endRowIndex = rowIndex;
        }
      });
      entries.forEach((entry) => {
        const lead = entry.rows[0]?.row || [];
        entry.pathLabel = this.caseManualNormalizeText(lead[CASE_MANUAL_START_COL.PATH_LABEL]);
        entry.toAddress = this.caseManualNormalizeText(lead[CASE_MANUAL_START_COL.TO_ADDRESS]);
        entry.fromHash = this.caseManualNormalizeText(lead[CASE_MANUAL_START_COL.FROM_HASH]);
        entry.chainId = this.caseManualNormalizeText(lead[CASE_MANUAL_START_COL.CHAIN_ID]);
        entry.chainName = this.caseManualNormalizeText(lead[CASE_MANUAL_START_COL.CHAIN_NAME]);
        entry.networkFamily = this.caseManualRowNetworkFamily?.(lead) || this.caseManualInferNetworkFamily?.({ chainId: entry.chainId, chainName: entry.chainName, token: lead[CASE_MANUAL_START_COL.TOKEN], rawRow: lead }) || '';
      });
      return entries;
    },

    findCaseManualStartBranchDeletionEntryForCandidate(entries = [], candidate = {}, node = null) {
      const rowIndex = Number.isInteger(candidate?.rowIndex) ? candidate.rowIndex : null;
      if (rowIndex !== null) {
        const byRow = entries.find(entry => entry.rows.some(ref => ref.rowIndex === rowIndex));
        if (byRow) return byRow;
      }
      const hash = this.caseManualNormalizeText(candidate?.hash || candidate?.transactionHash || candidate?.txHash || node?.hash || '');
      if (hash) {
        const byHash = entries.find(entry => this.caseManualTransactionHashesMatch(entry.hash, hash));
        if (byHash) return byHash;
      }
      const endpointKey = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate) || node?.address || '');
      const endpointColumnIndex = this.caseManualStartCandidateEndpointColumnIndex(candidate);
      const pathKey = this.caseManualNormalizeText(candidate?.pathLabel || node?.pathLabel || '').toLowerCase();
      if (endpointKey || pathKey) {
        return entries.find((entry) => {
          return entry.rows.some((ref) => {
            const rowEndpoint = this.caseManualNormalizeAddressKey(ref.row[endpointColumnIndex]);
            const rowPath = this.caseManualNormalizeText(ref.row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
            return (!endpointKey || rowEndpoint === endpointKey) && (!pathKey || rowPath === pathKey);
          });
        }) || null;
      }
      return null;
    },

    findCaseManualStartBranchDeletionRowRef(entry = null, candidate = {}, node = null) {
      if (!entry || !Array.isArray(entry.rows) || !entry.rows.length) return null;
      const rowIndex = Number.isInteger(candidate?.rowIndex) ? candidate.rowIndex : null;
      if (rowIndex !== null) {
        const byIndex = entry.rows.find(ref => ref.rowIndex === rowIndex);
        if (byIndex) return byIndex;
      }
      const endpointKey = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate) || node?.address || '');
      const endpointColumnIndex = this.caseManualStartCandidateEndpointColumnIndex(candidate);
      const pathKey = this.caseManualNormalizeText(candidate?.pathLabel || node?.pathLabel || '').toLowerCase();
      if (endpointKey || pathKey) {
        const byAddressPath = entry.rows.find((ref) => {
          const rowEndpoint = this.caseManualNormalizeAddressKey(ref.row[endpointColumnIndex]);
          const rowPath = this.caseManualNormalizeText(ref.row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
          return (!endpointKey || rowEndpoint === endpointKey) && (!pathKey || rowPath === pathKey);
        });
        if (byAddressPath) return byAddressPath;
      }
      return entry.rows[0];
    },

    buildCaseManualStartBranchDeletionIndexes(entries = []) {
      const byHash = new Map();
      const childrenByParentHash = new Map();
      const parentsByChildHash = new Map();
      const rememberChild = (parentHash = '', childHash = '') => {
        const parentKey = this.caseManualNormalizeText(parentHash).toLowerCase();
        const childKey = this.caseManualNormalizeText(childHash).toLowerCase();
        if (!parentKey || !childKey) return;
        if (!childrenByParentHash.has(parentKey)) childrenByParentHash.set(parentKey, new Set());
        childrenByParentHash.get(parentKey).add(childKey);
        if (!parentsByChildHash.has(childKey)) parentsByChildHash.set(childKey, new Set());
        parentsByChildHash.get(childKey).add(parentKey);
      };
      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        const hashKey = this.caseManualNormalizeText(entry.hash).toLowerCase();
        if (hashKey) byHash.set(hashKey, entry);
      });
      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        const entryHash = this.caseManualNormalizeText(entry.hash);
        (entry.rows || []).forEach((ref) => {
          this.caseManualExtractHashTokensFromContinuesAs(ref.row[CASE_MANUAL_START_COL.CONTINUES_AS]).forEach(childHash => rememberChild(entryHash, childHash));
          const fromHash = this.caseManualNormalizeText(ref.row[CASE_MANUAL_START_COL.FROM_HASH]);
          if (fromHash) rememberChild(fromHash, entryHash);
        });
      });
      return { byHash, childrenByParentHash, parentsByChildHash };
    },

    getCaseManualStartBranchDeletionChildrenForEntry(entry = null, indexes = {}, options = {}) {
      if (!entry) return [];
      const children = [];
      const remember = value => {
        const key = this.caseManualNormalizeText(value).toLowerCase();
        if (key && !children.includes(key)) children.push(key);
      };
      if (options.onlyRowRef) {
        this.caseManualExtractHashTokensFromContinuesAs(options.onlyRowRef.row?.[CASE_MANUAL_START_COL.CONTINUES_AS]).forEach(remember);
        return children;
      }
      (entry.rows || []).forEach(ref => this.caseManualExtractHashTokensFromContinuesAs(ref.row[CASE_MANUAL_START_COL.CONTINUES_AS]).forEach(remember));
      const linked = indexes.childrenByParentHash?.get(this.caseManualNormalizeText(entry.hash).toLowerCase());
      if (linked) Array.from(linked).forEach(remember);
      return children;
    },

    collectCaseManualStartBranchDeletionDescendantHashes(seedHashes = [], indexes = {}, options = {}) {
      const removed = new Set(Array.isArray(options.initialRemoved) ? options.initialRemoved : []);
      const queue = (Array.isArray(seedHashes) ? seedHashes : []).map(hash => this.caseManualNormalizeText(hash).toLowerCase()).filter(Boolean);
      while (queue.length) {
        const hashKey = queue.shift();
        if (!hashKey || removed.has(hashKey)) continue;
        const entry = indexes.byHash?.get(hashKey);
        if (!entry) continue;
        if (options.skipSharedDescendants !== false) {
          const parents = indexes.parentsByChildHash?.get(hashKey) || new Set();
          const outsideParent = Array.from(parents).some(parentKey => !removed.has(parentKey) && !options.directSeeds?.has(hashKey));
          if (outsideParent) continue;
        }
        removed.add(hashKey);
        this.getCaseManualStartBranchDeletionChildrenForEntry(entry, indexes).forEach(childKey => queue.push(childKey));
      }
      return removed;
    },

    formatCaseManualStartBranchDeletionRowSummary(row = [], rowIndex = -1) {
      const hash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]);
      const toAddress = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TO_ADDRESS]);
      return {
        key: `${rowIndex}-${hash || toAddress || 'row'}`,
        rowIndex,
        spreadsheetRowNumber: Number(rowIndex) + 1,
        pathLabel: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]),
        hash,
        shortHash: hash ? this.caseManualShorten(hash, 8, 6) : '',
        toAddress,
        shortToAddress: toAddress ? this.caseManualShorten(toAddress, 8, 6) : '',
        continuesAs: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.CONTINUES_AS])
      };
    },

    buildCaseManualStartBranchDeletionNewContinuesAs(oldContinuesAs = '', deletedHashKeys = new Set()) {
      const tokens = this.caseManualExtractHashTokensFromContinuesAs(oldContinuesAs);
      if (!tokens.length) return { changed: false, remaining: [], value: this.caseManualNormalizeContinuesAsText(oldContinuesAs) || '' };
      const remaining = tokens.filter(hash => !deletedHashKeys.has(this.caseManualNormalizeText(hash).toLowerCase()));
      if (remaining.length === tokens.length) return { changed: false, remaining, value: this.caseManualNormalizeContinuesAsText(oldContinuesAs) || '' };
      return {
        changed: true,
        remaining,
        value: remaining.length ? `Continues as ${remaining.join(', ')}` : 'Path ends.'
      };
    },

    patchCaseManualStartBranchDeletionSystemNotes(systemNotes = '', remainingHashes = []) {
      const lines = this.caseManualSplitNoteLinesPreservingBoundaries(systemNotes);
      const withoutBranches = lines.filter(line => !/^Branches\s+as\b/iu.test(line));
      if (remainingHashes.length > 1) withoutBranches.push(`Branches as ${remainingHashes.join(', ')}`);
      return this.caseManualDeduplicateNoteLines(withoutBranches).join('\n');
    },

    caseManualStartEntryEndpointEvidenceIsRowSpecific(entry = {}, rowRef = null) {
      const row = rowRef?.row || entry.rows?.[0]?.row || [];
      const toAddress = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TO_ADDRESS]);
      const attribution = this.caseManualCleanAttributionLabel(row[CASE_MANUAL_START_COL.ATTRIBUTION] || '');
      if (!toAddress || !attribution) return true;
      const text = [
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row[CASE_MANUAL_START_COL.CASE_NOTES],
        row[CASE_MANUAL_START_COL.ANALYST_NOTES]
      ].map(value => this.caseManualNormalizeText(value)).join(' ');
      const addressMentioned = text.toLowerCase().includes(toAddress.toLowerCase());
      const explicitTool88Attribution = /Tool\s*88\s+attribution|Tool\s*88:\s*Path\s+ends\s+at|action\s*=\s*manual-path-ending|Tool\s*88\s+automatic\s+decision/iu.test(text);
      const attributionMentioned = attribution && text.toLowerCase().includes(attribution.toLowerCase());
      return Boolean((addressMentioned && (explicitTool88Attribution || attributionMentioned)) || explicitTool88Attribution);
    },

    detectCaseManualStartSuspiciousCopiedEndpointAttribution(entries = []) {
      const pathEndings = [];
      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        (entry.rows || []).forEach((ref) => {
          const row = ref.row || [];
          const continuesAs = this.caseManualNormalizeContinuesAsText(row[CASE_MANUAL_START_COL.CONTINUES_AS]);
          const attribution = this.caseManualCleanAttributionLabel(row[CASE_MANUAL_START_COL.ATTRIBUTION] || '');
          if (!/path\s*ends/iu.test(continuesAs) || !attribution) return;
          pathEndings.push({ entry, ref, row, attribution, parentHashKey: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_HASH] || entry.fromHash || '').toLowerCase() });
        });
      });
      const grouped = new Map();
      pathEndings.forEach((item) => {
        const key = `${item.parentHashKey}|${item.attribution.toLowerCase()}`;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(item);
      });
      const suspicious = [];
      const seenSuspiciousKeys = new Set();
      const rememberSuspicious = (item, reason = 'No row-specific endpoint evidence was found for this path-ending attribution.') => {
        if (this.caseManualStartEntryEndpointEvidenceIsRowSpecific(item.entry, item.ref)) return;
        const row = item.row;
        const key = `${item.ref.rowIndex}-${item.attribution}`;
        if (seenSuspiciousKeys.has(key)) return;
        seenSuspiciousKeys.add(key);
        suspicious.push({
          key,
          spreadsheetRowNumber: item.ref.rowIndex + 1,
          pathLabel: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]),
          hash: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]) || item.entry.hash,
          shortHash: this.caseManualShorten(this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]) || item.entry.hash, 8, 6),
          toAddress: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TO_ADDRESS]),
          shortToAddress: this.caseManualShorten(row[CASE_MANUAL_START_COL.TO_ADDRESS], 8, 6),
          attribution: item.attribution,
          reason
        });
      };

      pathEndings.forEach(item => rememberSuspicious(item));
      grouped.forEach((items) => {
        if (items.length <= 1) return;
        items.forEach(item => rememberSuspicious(item, 'The same endpoint attribution appears on sibling path-ending rows without direct row-specific evidence.'));
      });
      return suspicious;
    },

    previewCaseManualStartBranchDeletion() {
      const matrix = this.getCaseManualStartMainMatrix();
      const normalizedMatrix = Array.isArray(matrix)
        ? matrix.map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)))
        : [];
      const entries = this.parseCaseManualStartBranchDeletionEntries(normalizedMatrix);
      const indexes = this.buildCaseManualStartBranchDeletionIndexes(entries);
      const selectedNode = this.caseManualStartBranchDeletionSelectedNode();
      const selectedCandidate = this.caseManualStartBranchDeletionSelectedCandidate(selectedNode);
      const rootEntry = this.findCaseManualStartBranchDeletionEntryForCandidate(entries, selectedCandidate || {}, selectedNode);
      const rootRowRef = this.findCaseManualStartBranchDeletionRowRef(rootEntry, selectedCandidate || {}, selectedNode);
      const warnings = [];
      if (!selectedNode || !selectedCandidate?.caseManualStartCandidate) warnings.push('The selected visual node does not expose a stable Manual Solve row/candidate. Select a transaction or address node generated from Main Data.');
      if (!rootEntry || !rootRowRef) warnings.push('The selected branch could not be matched back to a Main Data entry by row, hash, or To Address.');
      if (!rootEntry || !rootRowRef) {
        return {
          canApply: false,
          summary: 'Case Buddy could not build a safe Main Data deletion preview for the current selection.',
          warnings,
          root: {},
          outputOnly: false,
          rowsToRemove: [],
          parentUpdates: [],
          remainingSiblings: [],
          suspiciousEndpointAttributions: [],
          matrix: normalizedMatrix
        };
      }
      const rootHashKey = this.caseManualNormalizeText(rootEntry.hash).toLowerCase();
      const networkFamily = rootEntry.networkFamily || this.caseManualRowNetworkFamily?.(rootRowRef.row) || '';
      const btcOutputOnly = networkFamily === 'btc' && rootEntry.rows.length > 1;
      const outputOnly = Boolean(btcOutputOnly);
      const selectedRowChildHashes = this.caseManualExtractHashTokensFromContinuesAs(rootRowRef.row[CASE_MANUAL_START_COL.CONTINUES_AS]).map(hash => this.caseManualNormalizeText(hash).toLowerCase()).filter(Boolean);
      const directSeeds = new Set(outputOnly ? selectedRowChildHashes : [rootHashKey]);
      const initialRemoved = outputOnly ? [] : [rootHashKey];
      let deletedHashKeys = this.collectCaseManualStartBranchDeletionDescendantHashes(
        outputOnly ? selectedRowChildHashes : this.getCaseManualStartBranchDeletionChildrenForEntry(rootEntry, indexes),
        indexes,
        { initialRemoved, directSeeds, skipSharedDescendants: true }
      );
      if (!outputOnly) deletedHashKeys.add(rootHashKey);
      const removedRowIndexes = new Set();
      entries.forEach((entry) => {
        if (!deletedHashKeys.has(entry.hashKey)) return;
        entry.rows.forEach(ref => removedRowIndexes.add(ref.rowIndex));
      });
      if (outputOnly) removedRowIndexes.add(rootRowRef.rowIndex);
      const promoteBtcSibling = outputOnly && rootRowRef.rowIndex === rootEntry.startRowIndex
        ? rootEntry.rows.find(ref => ref.rowIndex !== rootRowRef.rowIndex && !removedRowIndexes.has(ref.rowIndex)) || null
        : null;
      const deletedDirectHashKeys = outputOnly ? new Set(selectedRowChildHashes) : new Set([rootHashKey]);
      const parentUpdates = [];
      const remainingSiblingMap = new Map();
      normalizedMatrix.forEach((row, rowIndex) => {
        if (removedRowIndexes.has(rowIndex)) return;
        const oldContinuesAs = this.caseManualNormalizeContinuesAsText(row[CASE_MANUAL_START_COL.CONTINUES_AS]);
        const next = this.buildCaseManualStartBranchDeletionNewContinuesAs(oldContinuesAs, deletedDirectHashKeys);
        if (!next.changed) return;
        const remainingSiblingShortHashes = next.remaining.map(hash => this.caseManualShorten(hash, 8, 6));
        next.remaining.forEach((hash) => {
          const siblingEntry = indexes.byHash?.get(this.caseManualNormalizeText(hash).toLowerCase());
          remainingSiblingMap.set(hash.toLowerCase(), {
            key: hash.toLowerCase(),
            pathLabel: siblingEntry?.pathLabel || '',
            hash,
            shortHash: this.caseManualShorten(hash, 8, 6)
          });
        });
        parentUpdates.push({
          key: `parent-${rowIndex}`,
          rowIndex,
          spreadsheetRowNumber: rowIndex + 1,
          pathLabel: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]),
          hash: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]),
          oldContinuesAs,
          newContinuesAs: next.value,
          remainingHashes: next.remaining,
          remainingSiblingShortHashes
        });
      });
      const rowsToRemove = Array.from(removedRowIndexes)
        .sort((left, right) => left - right)
        .map(rowIndex => this.formatCaseManualStartBranchDeletionRowSummary(normalizedMatrix[rowIndex] || [], rowIndex));
      const suspiciousEndpointAttributions = this.detectCaseManualStartSuspiciousCopiedEndpointAttribution(entries);
      if (outputOnly && !selectedRowChildHashes.length && !/path\s*ends/iu.test(this.caseManualNormalizeContinuesAsText(rootRowRef.row[CASE_MANUAL_START_COL.CONTINUES_AS]))) {
        warnings.push('The selected BTC/output row does not list a downstream continuation. Case Buddy will remove only that output row; verify sibling output funds-followed values after applying.');
      }
      if (outputOnly) warnings.push('BTC/output branch mode is active: sibling output rows and their funds-followed/left-bounds values are preserved unless listed in Rows to remove.');
      if (promoteBtcSibling) warnings.push(`The selected BTC lead output row will be removed, so row ${promoteBtcSibling.rowIndex + 1} will be promoted to carry the transaction hash for the preserved sibling output entry.`);
      if (this.caseInvestigationVisualizerSelectedCount > 1) warnings.push('Multiple visual nodes were selected. Manual Solve will use the first selected Main Data-backed branch root for this confirmed deletion.');
      if (suspiciousEndpointAttributions.length) warnings.push('Some path-ending attribution looks copied across sibling endings without row-specific endpoint evidence. These rows will be flagged after the recheck instead of treated as solved.');
      const rootRow = rootRowRef.row || [];
      const rootHash = this.caseManualNormalizeText(rootRow[CASE_MANUAL_START_COL.TX_HASH]) || rootEntry.hash;
      const rootToAddress = this.caseManualNormalizeText(rootRow[CASE_MANUAL_START_COL.TO_ADDRESS]) || rootEntry.toAddress;
      const summary = rowsToRemove.length
        ? `This will remove ${rowsToRemove.length} Main Data row${rowsToRemove.length === 1 ? '' : 's'} and update ${parentUpdates.length} parent Continues As cell${parentUpdates.length === 1 ? '' : 's'}. Unresolved endings will be recalculated from Main Data.`
        : 'No removable Main Data rows were found for this branch.';
      return {
        canApply: rowsToRemove.length > 0,
        summary,
        warnings,
        root: {
          pathLabel: this.caseManualNormalizeText(rootRow[CASE_MANUAL_START_COL.PATH_LABEL]) || rootEntry.pathLabel,
          hash: rootHash,
          shortHash: this.caseManualShorten(rootHash, 8, 6),
          toAddress: rootToAddress,
          shortToAddress: this.caseManualShorten(rootToAddress, 8, 6),
          rowIndex: rootRowRef.rowIndex,
          spreadsheetRowNumber: rootRowRef.rowIndex + 1
        },
        outputOnly,
        rootHashKey,
        selectedRowIndex: rootRowRef.rowIndex,
        promoteBtcSiblingRowIndex: promoteBtcSibling?.rowIndex ?? null,
        deletedHashKeys: Array.from(deletedHashKeys),
        removedRowIndexes: Array.from(removedRowIndexes).sort((left, right) => left - right),
        rowsToRemove,
        parentUpdates,
        remainingSiblings: Array.from(remainingSiblingMap.values()),
        suspiciousEndpointAttributions,
        matrix: normalizedMatrix
      };
    },

    openCaseManualStartBranchDeletionPreview() {
      const preview = this.previewCaseManualStartBranchDeletion();
      this.caseManualStartBranchDeletionPreview = preview;
      this.caseManualStartBranchDeletionStatus = preview.summary || '';
    },

    cancelCaseManualStartBranchDeletionPreview() {
      if (this.caseManualStartBranchDeletionApplying) return;
      this.caseManualStartBranchDeletionPreview = null;
    },

    buildCaseManualStartBranchDeletionAppliedMatrix(preview = {}) {
      const source = Array.isArray(preview.matrix) ? preview.matrix : [];
      const removed = new Set(Array.isArray(preview.removedRowIndexes) ? preview.removedRowIndexes : []);
      const parentUpdates = Array.isArray(preview.parentUpdates) ? preview.parentUpdates : [];
      const parentUpdateByRow = new Map(parentUpdates.map(update => [update.rowIndex, update]));
      const promotedRowIndex = Number.isInteger(preview.promoteBtcSiblingRowIndex) ? preview.promoteBtcSiblingRowIndex : null;
      const selectedRow = Number.isInteger(preview.selectedRowIndex) ? source[preview.selectedRowIndex] || [] : [];
      return source
        .map((row, rowIndex) => {
          const next = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
          const parentUpdate = parentUpdateByRow.get(rowIndex);
          if (parentUpdate) {
            next[CASE_MANUAL_START_COL.CONTINUES_AS] = parentUpdate.newContinuesAs;
            next[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.patchCaseManualStartBranchDeletionSystemNotes(next[CASE_MANUAL_START_COL.SYSTEM_NOTES], parentUpdate.remainingHashes || []);
            next[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
              next[CASE_MANUAL_START_COL.CASE_NOTES],
              parentUpdate.remainingHashes?.length
                ? `Manual Solve branch deletion: removed one continuation; ${parentUpdate.remainingHashes.length} sibling continuation${parentUpdate.remainingHashes.length === 1 ? '' : 's'} remain.`
                : 'Manual Solve branch deletion: removed the only continuation; this row is now an unresolved path ending to review.'
            );
          }
          if (promotedRowIndex !== null && rowIndex === promotedRowIndex) {
            if (!this.caseManualNormalizeText(next[CASE_MANUAL_START_COL.TX_HASH])) next[CASE_MANUAL_START_COL.TX_HASH] = this.caseManualNormalizeText(selectedRow[CASE_MANUAL_START_COL.TX_HASH] || preview.root?.hash || '');
            if (!this.caseManualNormalizeText(next[CASE_MANUAL_START_COL.PATH_LABEL])) next[CASE_MANUAL_START_COL.PATH_LABEL] = this.caseManualNormalizeText(selectedRow[CASE_MANUAL_START_COL.PATH_LABEL] || preview.root?.pathLabel || '');
            [CASE_MANUAL_START_COL.DATE, CASE_MANUAL_START_COL.FROM_HASH, CASE_MANUAL_START_COL.CHAIN_ID, CASE_MANUAL_START_COL.CHAIN_NAME, CASE_MANUAL_START_COL.HOP_LOGIC].forEach((colIndex) => {
              if (!this.caseManualNormalizeText(next[colIndex])) next[colIndex] = this.caseManualNormalizeText(selectedRow[colIndex]);
            });
            next[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(next[CASE_MANUAL_START_COL.CASE_NOTES], 'Manual Solve branch deletion: promoted this BTC/output sibling row to preserve the transaction entry after deleting the selected output branch.');
          }
          return next;
        })
        .filter((row, rowIndex) => !removed.has(rowIndex));
    },

    async applyCaseManualStartBranchDeletionPreview() {
      const preview = this.caseManualStartBranchDeletionPreview;
      if (!preview || !preview.canApply || this.caseManualStartBranchDeletionApplying) return;
      this.caseManualStartBranchDeletionApplying = true;
      try {
        const activeKey = this.caseInvestigationVisualizerCurrentFlow?.key || this.caseInvestigationVisualizerLastFlowKey || '';
        const updatedMatrix = this.buildCaseManualStartBranchDeletionAppliedMatrix(preview);
        await this.replaceCaseManualStartLogicalSheetMatrix('main', updatedMatrix, 'Manual Solve branch deletion updated Main Data.');
        const refreshedRows = this.getCaseManualStartVisualizerRows({ includeAll: true });
        this.caseInvestigationVisualizerState = this.buildCaseManualStartVisualizerState(refreshedRows);
        this.cacheCaseManualStartVisualizerPreparedState(refreshedRows, this.caseInvestigationVisualizerState);
        if (activeKey) {
          const index = (this.caseInvestigationVisualizerState.flows || []).findIndex(flow => flow.key === activeKey);
          if (index >= 0) this.caseInvestigationVisualizerActiveIndex = index;
        }
        this.setCaseManualStartCurrentVisual({ mode: this.caseInvestigationVisualizerViewMode });
        const unresolvedCount = this.getCaseManualStartViewportUnresolvedEndingCount();
        const suspicious = this.detectCaseManualStartSuspiciousCopiedEndpointAttribution(
          this.parseCaseManualStartBranchDeletionEntries(this.getCaseManualStartMainMatrix())
        );
        const parentSummary = preview.parentUpdates.length
          ? preview.parentUpdates.map(update => {
            const parentLabel = update.pathLabel || this.caseManualShorten(update.hash, 8, 6) || `row ${update.spreadsheetRowNumber}`;
            return update.remainingHashes?.length ? `${parentLabel} has ${update.remainingHashes.length} continuation${update.remainingHashes.length === 1 ? '' : 's'} remaining` : `${parentLabel} is now an unresolved ending`;
          }).join('; ')
          : 'No parent Continues As cells changed';
        const rootLabel = preview.root?.pathLabel || this.caseManualShorten(preview.root?.hash, 8, 6) || 'selected branch';
        const suspiciousText = suspicious.length ? ` ${suspicious.length} endpoint attribution mismatch${suspicious.length === 1 ? '' : 'es'} flagged for review.` : '';
        this.caseInvestigationVisualizerStatus = `Deleted branch ${rootLabel} from Main Data. ${parentSummary}. Rechecked unresolved endings: ${unresolvedCount}.${suspiciousText}`;
        this.caseManualStartBranchDeletionStatus = this.caseInvestigationVisualizerStatus;
        this.caseManualStartBranchDeletionPreview = null;
        this.selectCaseManualStartPathEndingNode?.();
        this.queueCaseSummaryRefresh?.(160, { light: true });
      } catch (error) {
        this.caseInvestigationVisualizerStatus = `Could not delete the Manual Solve branch: ${error?.message || error}`;
        this.showCaseToast?.(this.caseInvestigationVisualizerStatus);
      } finally {
        this.caseManualStartBranchDeletionApplying = false;
      }
    },

    caseManualStartViewportAutomationCalloutsAreEnabled() {
      const draft = this.caseManualStartSettingsDraft || {};
      if (this.caseManualStartSettingsOpen && Object.prototype.hasOwnProperty.call(draft, 'showAutomationCallouts')) return draft.showAutomationCallouts !== false;
      const saved = typeof this.caseManualStartReadTool88Settings === 'function' ? this.caseManualStartReadTool88Settings() : {};
      return saved.showAutomationCallouts !== false;
    },

    caseManualStartViewportAutomationCalloutStyle(callout = {}) {
      const box = callout.box || {};
      const color = callout.color || CASE_MANUAL_START_AUTOMATION_CALLOUT_DEFAULT_COLOR;
      return {
        left: `${Math.max(0, Number(box.x) || 0)}px`,
        top: `${Math.max(0, Number(box.y) || 0)}px`,
        width: `${Math.max(120, Number(box.width) || 230)}px`,
        minHeight: `${Math.max(28, Number(box.height) || 34)}px`,
        '--case-manual-start-callout-color': color,
        '--case-manual-start-callout-life-ms': `${CASE_MANUAL_START_AUTOMATION_CALLOUT_VISIBLE_MS}ms`
      };
    },

    clearCaseManualStartViewportAutomationCallouts() {
      const callouts = Array.isArray(this.caseManualStartViewportAutomationCallouts) ? this.caseManualStartViewportAutomationCallouts : [];
      callouts.forEach(callout => {
        if (callout?.timer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(callout.timer);
      });
      if (this.caseManualStartViewportAutomationCalloutFrame && typeof window !== 'undefined') {
        if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(this.caseManualStartViewportAutomationCalloutFrame);
        if (typeof window.clearTimeout === 'function') window.clearTimeout(this.caseManualStartViewportAutomationCalloutFrame);
      }
      this.caseManualStartViewportAutomationCalloutFrame = 0;
      this.caseManualStartViewportAutomationCallouts = [];
    },

    dismissCaseManualStartViewportAutomationCallout(calloutId = '') {
      const id = String(calloutId || '');
      const callouts = Array.isArray(this.caseManualStartViewportAutomationCallouts) ? this.caseManualStartViewportAutomationCallouts : [];
      const match = callouts.find(callout => String(callout.id || '') === id);
      if (match?.timer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(match.timer);
      this.caseManualStartViewportAutomationCallouts = callouts.filter(callout => String(callout.id || '') !== id);
    },

    scheduleCaseManualStartViewportAutomationCalloutPositionRefresh(options = {}) {
      if (!Array.isArray(this.caseManualStartViewportAutomationCallouts) || !this.caseManualStartViewportAutomationCallouts.length) return;
      const refreshOptions = { followSource: options.followSource !== false };
      const run = () => {
        this.caseManualStartViewportAutomationCalloutFrame = 0;
        this.refreshCaseManualStartViewportAutomationCalloutPositions(refreshOptions);
      };
      if (options.immediate === true || typeof window === 'undefined') {
        if (this.caseManualStartViewportAutomationCalloutFrame && typeof window !== 'undefined') {
          if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(this.caseManualStartViewportAutomationCalloutFrame);
          else if (typeof window.clearTimeout === 'function') window.clearTimeout(this.caseManualStartViewportAutomationCalloutFrame);
        }
        run();
        return;
      }
      if (this.caseManualStartViewportAutomationCalloutFrame) return;
      if (typeof window.requestAnimationFrame === 'function') {
        this.caseManualStartViewportAutomationCalloutFrame = window.requestAnimationFrame(run);
      } else if (typeof window.setTimeout === 'function') {
        this.caseManualStartViewportAutomationCalloutFrame = window.setTimeout(run, 0);
      } else {
        run();
      }
    },

    refreshCaseManualStartViewportAutomationCalloutPositions(options = {}) {
      const callouts = Array.isArray(this.caseManualStartViewportAutomationCallouts) ? this.caseManualStartViewportAutomationCallouts : [];
      if (!callouts.length) return;
      const followSource = options.followSource !== false;
      const nextCallouts = callouts.map(callout => {
        const node = callout.sourceNodeId
          ? (this.caseInvestigationVisualizerVisibleNodes || []).find(item => item.id === callout.sourceNodeId)
          : null;
        const source = node ? this.caseManualStartViewportPointForNode(node) : (callout.source || null);
        if (!source) return callout;
        const previousSource = callout.source || {};
        const previousBox = callout.box || {};
        const rawBox = {
          ...previousBox,
          width: previousBox.width || 230,
          height: previousBox.height || 34
        };
        if (followSource) {
          const oldX = Number(previousSource.x);
          const oldY = Number(previousSource.y);
          const newX = Number(source.x);
          const newY = Number(source.y);
          if (Number.isFinite(oldX) && Number.isFinite(oldY) && Number.isFinite(newX) && Number.isFinite(newY)) {
            rawBox.x = (Number(previousBox.x) || 0) + (newX - oldX);
            rawBox.y = (Number(previousBox.y) || 0) + (newY - oldY);
          }
        }
        const box = this.clampCaseManualStartViewportCalloutBox(
          rawBox,
          rawBox.width || previousBox.width || 230,
          rawBox.height || previousBox.height || 34,
          source.viewportWidth,
          source.viewportHeight
        );
        return {
          ...callout,
          source,
          box,
          line: this.caseManualStartViewportCalloutLineForBox(source, box)
        };
      });
      this.caseManualStartViewportAutomationCallouts = nextCallouts;
    },

    captureCaseManualStartViewportAutomationCalloutBeforeState(snapshot = {}, action = '') {
      const visibleNodes = Array.isArray(this.caseInvestigationVisualizerVisibleNodes) ? this.caseInvestigationVisualizerVisibleNodes : [];
      const visibleTxHashes = new Set(visibleNodes
        .filter(node => String(node?.type || '').toLowerCase() === 'tx')
        .map(node => this.caseManualNormalizeText(node?.hash || node?.caseCandidate?.hash || node?.candidate?.hash).toLowerCase())
        .filter(Boolean));
      const selectedCandidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || this.caseInvestigationVisualizerSelectedCandidate || {};
      return {
        visibleTxHashes,
        selectedCandidate,
        selectedHash: this.caseManualNormalizeText(selectedCandidate?.hash || selectedCandidate?.txHash || selectedCandidate?.transactionHash || '').toLowerCase(),
        selectedAddress: this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(selectedCandidate)),
        action: this.caseManualNormalizeText(action || snapshot?.integrationAction || '')
      };
    },

    queueCaseManualStartViewportAutomationCallouts(payload = {}) {
      if (!this.caseManualStartViewportAutomationCalloutsAreEnabled()) return;
      if (!this.showCaseInvestigationVisualizerModal || !this.caseInvestigationVisualizerManualStartMode) return;
      const refreshAfterViewportSettles = () => {
        this.scheduleCaseManualStartViewportAutomationCalloutPositionRefresh?.();
        if (typeof window === 'undefined' || typeof window.setTimeout !== 'function') return;
        [120, 320, 700].forEach(delay => {
          window.setTimeout(() => this.refreshCaseManualStartViewportAutomationCalloutPositions?.(), delay);
        });
      };
      const run = () => {
        this.createCaseManualStartViewportAutomationCallouts(payload);
        refreshAfterViewportSettles();
      };
      const scheduleRun = () => {
        if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') window.setTimeout(run, 40);
        else run();
      };
      this.$nextTick(scheduleRun);
    },

    createCaseManualStartViewportAutomationCallouts(payload = {}) {
      if (!this.caseManualStartViewportAutomationCalloutsAreEnabled()) return;
      const targetNode = this.resolveCaseManualStartViewportAutomationCalloutTargetNode(payload);
      const sourcePoint = this.caseManualStartViewportPointForNode(targetNode);
      if (!sourcePoint) return;
      const rows = Array.isArray(payload.rows) ? payload.rows : [];
      const messages = this.buildCaseManualStartViewportAutomationCalloutMessages(rows, payload.snapshot || {}, payload.action || '', payload.beforeState || {});
      if (!messages.length) return;
      messages.slice(0, 2).forEach((message, index) => {
        const text = this.caseManualStartViewportNormalizeCalloutText(message.text || message, index === 0 ? 'Solved hop' : 'Updated attribution');
        const kind = message.kind || (index === 0 ? 'solve' : 'attribution');
        const color = message.color || CASE_MANUAL_START_AUTOMATION_CALLOUT_DEFAULT_COLOR;
        const boxWidth = Math.max(176, Math.min(320, 94 + (text.length * 6.4)));
        const boxHeight = text.length > 38 ? 46 : 34;
        const placed = this.placeCaseManualStartViewportAutomationCallout(sourcePoint, boxWidth, boxHeight, index);
        if (!placed) return;
        const id = `manual-start-callout-${Date.now()}-${this.caseManualStartViewportAutomationCalloutId += 1}`;
        const timer = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
          ? window.setTimeout(() => this.dismissCaseManualStartViewportAutomationCallout(id), CASE_MANUAL_START_AUTOMATION_CALLOUT_VISIBLE_MS + 240)
          : null;
        const callout = {
          id,
          text,
          kind,
          color,
          sourceNodeId: targetNode?.id || '',
          source: sourcePoint,
          box: placed.box,
          line: placed.line,
          timer
        };
        const current = Array.isArray(this.caseManualStartViewportAutomationCallouts) ? this.caseManualStartViewportAutomationCallouts.slice() : [];
        current.push(callout);
        while (current.length > CASE_MANUAL_START_AUTOMATION_CALLOUT_MAX_VISIBLE) {
          const removed = current.shift();
          if (removed?.timer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(removed.timer);
        }
        this.caseManualStartViewportAutomationCallouts = current;
      });
    },

    caseManualStartViewportNormalizeCalloutText(value = '', fallback = 'Solved hop') {
      const text = this.caseManualNormalizeText(value).replace(/\s+/gu, ' ').trim();
      const clean = text || fallback;
      return clean.length > 52 ? `${clean.slice(0, 49).trim()}…` : clean;
    },

    classifyCaseManualStartViewportAutomationCalloutAction(actionValues = []) {
      const values = (Array.isArray(actionValues) ? actionValues : [actionValues])
        .map(value => this.caseManualNormalizeText(value).toLowerCase())
        .filter(Boolean);
      const has = pattern => values.some(value => pattern.test(value));
      if (has(/^(?:manual[-_\s]*)?(?:output[-_\s]*choice|transfer[-_\s]*chooser|input\/?output[-_\s]*chooser)$/iu)
        || has(/\b(?:output[-_\s]*choice|transfer[-_\s]*chooser|input\/?output[-_\s]*chooser)\b/iu)) return 'output-choice';
      if (has(/^(?:manual[-_\s]*)?swap(?:[-_\s]*(?:review|preview|apply|result))?$/iu)
        || has(/\b(?:action|recommendedAction|auto[-_\s]*decide)\s*[=:]\s*swap\b/iu)
        || has(/\bswap\s*(?:\+|and)\s*apply\b|\btreat\s+as\s+swap\b/iu)) return 'swap';
      if (has(/^(?:manual[-_\s]*)?bridge(?:[-_\s]*(?:review|preview|apply|result))?$/iu)
        || has(/\b(?:action|recommendedAction|auto[-_\s]*decide)\s*[=:]\s*bridge\b/iu)
        || has(/\bbridge\s*(?:\+|and)\s*apply\b|\btreat\s+as\s+bridge\b/iu)) return 'bridge';
      if (has(/^(?:manual[-_\s]*)?(?:path[-_\s]*ending|path_end|path[-_\s]*end|end[-_\s]*path)(?:[-_\s]*(?:review|preview|apply|result))?$/iu)
        || has(/\b(?:action|recommendedAction|auto[-_\s]*decide)\s*[=:]\s*(?:manual[-_\s]*)?(?:path[-_\s]*ending|path_end|path[-_\s]*end)\b/iu)
        || has(/\bmark\s+as\s+path\s+ending\b|\bend\s+path\s*(?:\+|and)\s*apply\b|\bpath[-_\s]*ending\s*(?:\+|and)\s*apply\b/iu)) return 'path-ending';
      return 'trace';
    },

    buildCaseManualStartViewportAutomationCalloutMessages(rows = [], snapshot = {}, action = '', beforeState = {}) {
      const actionValues = [
        action,
        snapshot?.integrationAction,
        snapshot?.decision?.action,
        snapshot?.result?.integrationAction,
        snapshot?.result?.trace?.action,
        snapshot?.result?.swap?.action,
        snapshot?.result?.bridge?.action
      ]
        .map(value => this.caseManualNormalizeText(value).toLowerCase())
        .filter(Boolean);
      const failedSwapAction = Boolean(
        (this.caseManualNormalizeText(snapshot?.caseBuddySpecialAction).toLowerCase() === 'swap'
          && snapshot?.caseBuddySpecialActionApplied === false)
        || actionValues.some(value => /manual[-_\s]*swap[-_\s]*(?:review|no[-_\s]*output)|swap[-_\s]*produced[-_\s]*no[-_\s]*output/iu.test(value))
      );
      if (failedSwapAction) return [];
      const actionKey = this.classifyCaseManualStartViewportAutomationCalloutAction(actionValues);
      const targetRow = this.resolveCaseManualStartViewportAutomationCalloutTargetRow(rows, beforeState, snapshot, action);
      const entity = this.extractCaseManualStartViewportAutomationCalloutEntity(rows, snapshot, targetRow, { actionKey });
      const calloutCandidate = beforeState?.selectedCandidate || {};
      const calloutEndpointColumnIndex = this.caseManualStartCandidateEndpointColumnIndex(calloutCandidate);
      const address = this.caseManualNormalizeText(
        targetRow?.[calloutEndpointColumnIndex]
          || this.caseManualStartCandidateEndpointAddress(calloutCandidate)
          || beforeState?.selectedAddress
          || ''
      );
      const addressText = address ? this.caseManualShorten(address, 8, 6) : '';
      const messages = [];
      if (actionKey === 'swap') messages.push({ text: 'Solved swap', kind: 'solve' });
      else if (actionKey === 'bridge') messages.push({ text: 'Solved bridge', kind: 'solve' });
      else if (actionKey === 'path-ending') messages.push({ text: entity ? `Ended path · ${entity}` : 'Ended path', kind: 'solve' });
      else if (actionKey === 'output-choice') messages.push({ text: entity ? `Selected ${entity}` : (addressText || 'Selected transfer'), kind: entity ? 'attribution' : 'solve' });
      else messages.push({ text: entity ? 'Solved hop' : (addressText || 'Solved hop'), kind: 'solve' });
      if (entity && !messages.some(item => this.caseManualNormalizeText(item.text).toLowerCase().includes(entity.toLowerCase()))) {
        messages.push({ text: `Identified ${entity}`, kind: 'attribution' });
      }
      return messages.filter(item => this.caseManualNormalizeText(item.text));
    },

    extractCaseManualStartViewportAutomationCalloutEntity(rows = [], snapshot = {}, targetRow = null, options = {}) {
      const cleanEntity = value => {
        const text = this.caseManualNormalizeText(value)
          .replace(/^to:\s*/iu, '')
          .replace(/^from:\s*/iu, '')
          .replace(/\s+/gu, ' ')
          .trim();
        if (!text || /^unknown$|^unattributed$|^n\/?a$|^none$/iu.test(text)) return '';
        if (/path\s*ends|continues\s+as|manual-path-ending|tool\s*88/iu.test(text) && text.length > 38) return '';
        return text.length > 32 ? `${text.slice(0, 29).trim()}…` : text;
      };
      const rowEntity = row => {
        if (!row) return '';
        const attribution = cleanEntity(Array.isArray(row) ? row[CASE_MANUAL_START_COL.ATTRIBUTION] : row?.attribution);
        if (attribution) return attribution;
        return cleanEntity(row?.toAttributionLabel || row?.toLabel || row?.entity || '');
      };

      const targetEntity = rowEntity(targetRow);
      if (targetEntity) return targetEntity;

      const actionKey = this.caseManualNormalizeText(options.actionKey || '').toLowerCase();
      const snapshotCandidates = [
        snapshot?.entity,
        snapshot?.attribution,
        snapshot?.result?.entity,
        snapshot?.result?.attribution,
        snapshot?.result?.pathEnding?.entity,
        snapshot?.result?.pathEnding?.label,
        snapshot?.decision?.entity,
        snapshot?.decision?.label
      ];

      if (actionKey === 'path-ending') {
        const targetContext = this.caseManualBuildPathEndingTargetContext?.(snapshot, { selectedCandidate: options.selectedCandidate }) || null;
        const targetCanUseSnapshot = !targetRow || !targetContext?.hasAny || this.caseManualPathEndingRowMatchesTargetContext?.(targetRow, targetContext);
        if (!targetCanUseSnapshot) return '';
        for (const value of snapshotCandidates) {
          const entity = cleanEntity(value);
          if (entity) return entity;
        }
        return '';
      }

      const rowList = Array.isArray(rows) ? rows : [];
      for (const row of rowList) {
        const entity = rowEntity(row);
        if (entity) return entity;
      }
      for (const value of snapshotCandidates) {
        const entity = cleanEntity(value);
        if (entity) return entity;
      }
      return '';
    },

    resolveCaseManualStartViewportAutomationCalloutTargetRow(rows = [], beforeState = {}, snapshot = {}, action = '') {
      const normalizedRows = (Array.isArray(rows) ? rows : [])
        .map(row => Array.isArray(row) ? row : (row?.row || row?.cells || []))
        .filter(row => Array.isArray(row) && this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }));
      const actionValues = [
        action,
        snapshot?.integrationAction,
        snapshot?.decision?.action,
        snapshot?.result?.integrationAction,
        snapshot?.result?.trace?.action,
        snapshot?.result?.swap?.action,
        snapshot?.result?.bridge?.action
      ];
      const actionKey = this.classifyCaseManualStartViewportAutomationCalloutAction(actionValues);
      const selectedCandidate = beforeState?.selectedCandidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || this.caseInvestigationVisualizerSelectedCandidate || {};
      const reverse = this.caseManualStartCandidateIsReverse(selectedCandidate);
      const endpointColumnIndex = reverse ? CASE_MANUAL_START_COL.FROM_ADDRESS : CASE_MANUAL_START_COL.TO_ADDRESS;
      const targetContext = this.caseManualBuildPathEndingTargetContext?.(snapshot, { selectedCandidate }) || null;
      if (actionKey === 'path-ending' && targetContext?.hasAny) {
        const selectedEndingRow = normalizedRows.find(row => this.caseManualPathEndingRowMatchesTargetContext?.(row, targetContext));
        if (selectedEndingRow) return selectedEndingRow;
      }

      const selectedHash = this.caseManualNormalizeText(
        selectedCandidate?.hash || selectedCandidate?.transactionHash || selectedCandidate?.txHash || beforeState?.selectedHash || snapshot?.selectedEntry?.txHash || ''
      ).toLowerCase();
      const selectedAddress = this.caseManualNormalizeAddressKey(
        this.caseManualStartCandidateEndpointAddress(selectedCandidate)
          || beforeState?.selectedAddress
          || snapshot?.selectedTargetAddress
          || (reverse ? snapshot?.selectedEntry?.fromAddress : snapshot?.selectedEntry?.toAddress)
          || ''
      );
      const selectedPathLabel = this.caseManualNormalizeText(selectedCandidate?.pathLabel || snapshot?.selectedEntry?.pathLabel || '').toLowerCase();
      const selectedMatch = normalizedRows.find(row => {
        const rowHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        const rowEndpoint = this.caseManualNormalizeAddressKey(row[endpointColumnIndex]);
        const rowLabel = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        if (selectedHash && rowHash && this.caseManualTransactionHashesMatch(rowHash, selectedHash)) return true;
        if (selectedPathLabel && rowLabel && rowLabel === selectedPathLabel) return true;
        return Boolean(selectedAddress && rowEndpoint && rowEndpoint === selectedAddress);
      });
      if (selectedMatch) return selectedMatch;

      const beforeHashes = beforeState?.visibleTxHashes instanceof Set ? beforeState.visibleTxHashes : new Set();
      const newRow = normalizedRows.find(row => {
        const hash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        return hash && !beforeHashes.has(hash);
      });
      if (newRow) return newRow;

      const selectedRow = Array.isArray(selectedCandidate?.rawRow)
        ? selectedCandidate.rawRow
        : (Array.isArray(selectedCandidate?.row) ? selectedCandidate.row : []);
      return normalizedRows.find(row => this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]))
        || (selectedRow.length ? this.caseManualEnsureRowWidth(selectedRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, selectedRow.length || 0)) : null);
    },

    resolveCaseManualStartViewportAutomationCalloutTargetNode(payload = {}) {
      const nodes = Array.isArray(this.caseInvestigationVisualizerVisibleNodes) ? this.caseInvestigationVisualizerVisibleNodes : [];
      if (!nodes.length) return null;
      const beforeHashes = payload.beforeState?.visibleTxHashes instanceof Set ? payload.beforeState.visibleTxHashes : new Set();
      const preparedRows = payload.mainResult?.prepared?.appendedMainRows || [];
      const sourceRows = [...(Array.isArray(preparedRows) ? preparedRows : []), ...(Array.isArray(payload.rows) ? payload.rows : [])]
        .map(row => Array.isArray(row) ? row : (row?.row || row?.cells || []))
        .filter(row => Array.isArray(row));
      const targetRow = this.resolveCaseManualStartViewportAutomationCalloutTargetRow(sourceRows, payload.beforeState || {}, payload.snapshot || {}, payload.action || '');
      const selectedCandidate = payload.candidate
        || payload.beforeState?.selectedCandidate
        || this.caseManualStartHopInspectorSelectedCandidate
        || this.caseManualStartHopInspectorCandidate
        || this.caseInvestigationVisualizerSelectedCandidate
        || {};
      const reverse = this.caseManualStartCandidateIsReverse(selectedCandidate);
      const endpointColumnIndex = reverse ? CASE_MANUAL_START_COL.FROM_ADDRESS : CASE_MANUAL_START_COL.TO_ADDRESS;
      const targetHash = this.caseManualNormalizeText(
        targetRow?.[CASE_MANUAL_START_COL.TX_HASH]
          || selectedCandidate?.hash
          || selectedCandidate?.transactionHash
          || selectedCandidate?.txHash
          || payload.beforeState?.selectedHash
          || ''
      ).toLowerCase();
      const targetAddress = this.caseManualNormalizeAddressKey(
        targetRow?.[endpointColumnIndex]
          || this.caseManualStartCandidateEndpointAddress(selectedCandidate)
          || payload.beforeState?.selectedAddress
          || ''
      );
      const targetRowIndex = Number.isInteger(selectedCandidate?.rowIndex) ? selectedCandidate.rowIndex : null;
      const targetPathLabel = this.caseManualNormalizeText(targetRow?.[CASE_MANUAL_START_COL.PATH_LABEL] || selectedCandidate?.pathLabel || '').toLowerCase();
      const nodeType = node => String(node?.type || '').toLowerCase();
      const nodeCandidate = node => node?.caseCandidate || node?.candidate || null;
      const nodeHash = node => this.caseManualNormalizeText(node?.hash || nodeCandidate(node)?.hash || nodeCandidate(node)?.transactionHash || nodeCandidate(node)?.txHash || '').toLowerCase();
      const nodeAddress = node => this.caseManualNormalizeAddressKey(
        node?.address
          || node?.targetAddress
          || this.caseManualStartCandidateEndpointAddress(nodeCandidate(node) || {})
          || ''
      );
      const hashMatches = (node, hash = targetHash) => Boolean(hash && this.caseManualTransactionHashesMatch(nodeHash(node), hash));
      const rowIndexMatches = node => {
        if (targetRowIndex === null) return false;
        const candidate = nodeCandidate(node);
        return Number.isInteger(candidate?.rowIndex) && candidate.rowIndex === targetRowIndex;
      };
      const pathLabelMatches = node => {
        if (!targetPathLabel) return false;
        const candidate = nodeCandidate(node);
        return this.caseManualNormalizeText(node?.pathLabel || candidate?.pathLabel || '').toLowerCase() === targetPathLabel;
      };
      const candidateMatches = node => hashMatches(node) || rowIndexMatches(node) || pathLabelMatches(node);
      const selectedNode = this.caseInvestigationVisualizerSelectedNode || null;
      const findAddressNode = (requireCandidateMatch = true) => nodes.find(node => nodeType(node) === 'address'
        && (!targetAddress || nodeAddress(node) === targetAddress)
        && (!requireCandidateMatch || candidateMatches(node)));
      const newHashAddressNode = sourceRows
        .map(row => this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase())
        .filter(hash => hash && this.caseManualIsLikelyTransactionHash(hash) && !beforeHashes.has(hash))
        .map(hash => nodes.find(node => nodeType(node) === 'address' && hashMatches(node, hash) && (!targetAddress || nodeAddress(node) === targetAddress)))
        .find(Boolean);
      if (newHashAddressNode) return newHashAddressNode;
      const exactAddressNode = findAddressNode(true);
      if (exactAddressNode) return exactAddressNode;
      if (selectedNode && nodeType(selectedNode) === 'address' && (!targetAddress || nodeAddress(selectedNode) === targetAddress) && candidateMatches(selectedNode)) return selectedNode;
      const looseAddressNode = findAddressNode(false);
      if (looseAddressNode) return looseAddressNode;
      const preferredHashes = sourceRows
        .map(row => this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]))
        .filter(hash => hash && this.caseManualIsLikelyTransactionHash(hash));
      const findTxNodeByHash = (hash = '') => nodes.find(node => nodeType(node) === 'tx' && this.caseManualTransactionHashesMatch(nodeHash(node), hash));
      for (const hash of preferredHashes) {
        const node = findTxNodeByHash(hash);
        if (node && !beforeHashes.has(this.caseManualNormalizeText(hash).toLowerCase())) return node;
      }
      if (targetHash) {
        const txNode = findTxNodeByHash(targetHash);
        if (txNode) return txNode;
      }
      return selectedNode && candidateMatches(selectedNode) ? selectedNode : null;
    },

    caseManualStartViewportPointForNode(node = null) {
      if (!node) return null;
      const viewportEl = this.$refs.caseInvestigationVisualizerViewportEl;
      const container = Array.isArray(viewportEl) ? viewportEl[0] : viewportEl;
      if (!container || typeof container.getBoundingClientRect !== 'function') return null;
      const rect = container.getBoundingClientRect();
      const nodeX = Number(node?.x);
      const nodeY = Number(node?.y);
      if (!Number.isFinite(nodeX) || !Number.isFinite(nodeY)) return null;
      const viewBox = this.caseInvestigationVisualizerEffectiveViewport?.()
        || this.caseInvestigationVisualizerPendingViewport
        || this.caseInvestigationVisualizerViewport
        || {};
      const vbX = Number(viewBox.x);
      const vbY = Number(viewBox.y);
      const vbWidth = Number(viewBox.width);
      const vbHeight = Number(viewBox.height);
      if (Number.isFinite(vbX) && Number.isFinite(vbY) && Number.isFinite(vbWidth) && Number.isFinite(vbHeight) && vbWidth > 0 && vbHeight > 0 && rect.width > 0 && rect.height > 0) {
        const scale = Math.min(rect.width / vbWidth, rect.height / vbHeight);
        const drawnWidth = vbWidth * scale;
        const drawnHeight = vbHeight * scale;
        const offsetX = (rect.width - drawnWidth) / 2;
        const offsetY = (rect.height - drawnHeight) / 2;
        const x = offsetX + ((nodeX - vbX) * scale);
        const y = offsetY + ((nodeY - vbY) * scale);
        if (Number.isFinite(x) && Number.isFinite(y)) {
          return { x, y, viewportWidth: rect.width, viewportHeight: rect.height };
        }
      }
      const svgRef = this.$refs.caseInvestigationVisualizerSvg;
      const svg = Array.isArray(svgRef) ? svgRef[0] : svgRef;
      if (svg && typeof svg.createSVGPoint === 'function' && typeof svg.getScreenCTM === 'function') {
        try {
          const matrix = svg.getScreenCTM();
          if (matrix) {
            const point = svg.createSVGPoint();
            point.x = nodeX;
            point.y = nodeY;
            const screenPoint = point.matrixTransform(matrix);
            const x = screenPoint.x - rect.left;
            const y = screenPoint.y - rect.top;
            if (Number.isFinite(x) && Number.isFinite(y)) {
              return { x, y, viewportWidth: rect.width, viewportHeight: rect.height };
            }
          }
        } catch {
          // Fall back handled below if the SVG matrix is unavailable during a render tick.
        }
      }
      return null;
    },

    placeCaseManualStartViewportAutomationCallout(source = {}, width = 230, height = 34, ordinal = 0) {
      const viewportWidth = Math.max(1, Number(source.viewportWidth) || 1);
      const viewportHeight = Math.max(1, Number(source.viewportHeight) || 1);
      const existing = Array.isArray(this.caseManualStartViewportAutomationCallouts) ? this.caseManualStartViewportAutomationCallouts : [];
      const directions = [
        { name: 'up-right', dx: 86, dy: -102, penalty: 0 },
        { name: 'up', dx: -(width / 2), dy: -124, penalty: 6 },
        { name: 'up-left', dx: -width - 86, dy: -102, penalty: 10 },
        { name: 'down-left', dx: -width - 86, dy: 76, penalty: 14 },
        { name: 'down', dx: -(width / 2), dy: 104, penalty: 18 },
        { name: 'down-right', dx: 86, dy: 76, penalty: 22 },
        { name: 'right', dx: 98, dy: -height / 2, penalty: 26 },
        { name: 'left', dx: -width - 98, dy: -height / 2, penalty: 30 }
      ];
      const padding = 14;
      let best = null;
      directions.forEach(direction => {
        const staggerY = ordinal * 42;
        const raw = { x: source.x + direction.dx, y: source.y + direction.dy + staggerY, width, height, direction: direction.name };
        const clamped = this.clampCaseManualStartViewportCalloutBox(raw, width, height, viewportWidth, viewportHeight, padding);
        const offscreenPenalty = (Math.abs(clamped.x - raw.x) + Math.abs(clamped.y - raw.y)) * 1.8;
        const overlapPenalty = existing.reduce((score, callout) => {
          const overlap = this.caseManualStartViewportCalloutRectOverlap(clamped, callout.box || {});
          return score + (overlap > 0 ? overlap * 4 : 0);
        }, 0);
        const centerDistance = Math.hypot((clamped.x + width / 2) - source.x, (clamped.y + height / 2) - source.y);
        const score = direction.penalty + offscreenPenalty + overlapPenalty + (centerDistance * 0.02);
        if (!best || score < best.score) {
          best = { score, box: clamped, line: this.caseManualStartViewportCalloutLineForBox(source, clamped) };
        }
      });
      return best;
    },

    clampCaseManualStartViewportCalloutBox(box = {}, width = 230, height = 34, viewportWidth = null, viewportHeight = null, padding = 14) {
      const sourceViewport = this.caseManualStartViewportAutomationCallouts?.[0]?.source || {};
      const maxWidth = Math.max(1, Number(viewportWidth) || Number(sourceViewport.viewportWidth) || this.$refs.caseInvestigationVisualizerViewportEl?.clientWidth || 1);
      const maxHeight = Math.max(1, Number(viewportHeight) || Number(sourceViewport.viewportHeight) || this.$refs.caseInvestigationVisualizerViewportEl?.clientHeight || 1);
      const w = Math.max(120, Number(box.width || width) || width);
      const h = Math.max(28, Number(box.height || height) || height);
      return {
        ...box,
        x: Math.max(padding, Math.min(maxWidth - w - padding, Number(box.x) || padding)),
        y: Math.max(padding, Math.min(maxHeight - h - padding, Number(box.y) || padding)),
        width: w,
        height: h
      };
    },

    caseManualStartViewportCalloutRectOverlap(a = {}, b = {}) {
      const ax1 = Number(a.x) || 0;
      const ay1 = Number(a.y) || 0;
      const ax2 = ax1 + (Number(a.width) || 0);
      const ay2 = ay1 + (Number(a.height) || 0);
      const bx1 = Number(b.x) || 0;
      const by1 = Number(b.y) || 0;
      const bx2 = bx1 + (Number(b.width) || 0);
      const by2 = by1 + (Number(b.height) || 0);
      const overlapX = Math.max(0, Math.min(ax2, bx2) - Math.max(ax1, bx1));
      const overlapY = Math.max(0, Math.min(ay2, by2) - Math.max(ay1, by1));
      return overlapX * overlapY;
    },

    caseManualStartViewportCalloutLineForBox(source = {}, box = {}) {
      const x1 = Number(source.x) || 0;
      const y1 = Number(source.y) || 0;
      const boxX = Number(box.x) || 0;
      const boxY = Number(box.y) || 0;
      const boxW = Math.max(1, Number(box.width) || 1);
      const boxH = Math.max(1, Number(box.height) || 1);
      const cx = boxX + (boxW / 2);
      const cy = boxY + (boxH / 2);
      const dx = x1 - cx;
      const dy = y1 - cy;
      let x2 = cx;
      let y2 = cy;
      if (Math.abs(dx) * boxH > Math.abs(dy) * boxW) {
        x2 = cx + Math.sign(dx || 1) * (boxW / 2);
        y2 = cy + (dy / Math.max(1, Math.abs(dx))) * (boxW / 2);
      } else {
        y2 = cy + Math.sign(dy || 1) * (boxH / 2);
        x2 = cx + (dx / Math.max(1, Math.abs(dy))) * (boxH / 2);
      }
      return { x1, y1, x2, y2 };
    },

    caseFileBrowserListCaseDataStorageKeys() {
      if (typeof window === 'undefined' || !window.localStorage) return [];
      const keys = [];
      const addKey = (key) => {
        const clean = String(key || '').trim();
        if (clean && !keys.includes(clean)) keys.push(clean);
      };
      const authKeyPattern = /^(?:token|jwt|jwtToken|jwt_token|authToken|auth_token|access_token|refresh_token|session|user|profile)$/iu;
      const caseKeyPattern = /(?:^|[_:-])(?:casebuddy|case_buddy|case-buddy|tool60|hermes_case|case_file|casefiles|case_files|caseproject|case_project|project_files|projectfiles)(?:$|[_:-])/iu;
      for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index);
        if (!key || authKeyPattern.test(key)) continue;
        const compactKey = key.replace(/[^A-Za-z0-9]+/gu, '_');
        if (/^(?:casebuddy|case_buddy|case-buddy|tool60)/iu.test(key) || caseKeyPattern.test(key) || caseKeyPattern.test(compactKey)) addKey(key);
      }
      (Array.isArray(this.projectFiles) ? this.projectFiles : []).forEach((file) => {
        const fileId = file?.id || '';
        if (!fileId) return;
        if (typeof this.getProjectInputStorageKey === 'function') addKey(this.getProjectInputStorageKey(fileId));
        if (typeof this.getProjectOutputStorageKey === 'function') addKey(this.getProjectOutputStorageKey(fileId));
        addKey(`tool60_project_${fileId}`);
        addKey(`tool60_input_${fileId}`);
        addKey(`tool60_output_${fileId}`);
      });
      addKey('tool60_autosave_enabled');
      addKey('casebuddy_input_spreadsheet_tools_expanded');
      return keys.filter((key) => !authKeyPattern.test(key));
    },

    openCaseFileBrowserPurgeModal() {
      this.caseFileBrowserPurgeConfirmChecked = false;
      this.caseFileBrowserPurgeConfirmPhrase = '';
      this.caseFileBrowserPurgeStatus = '';
      this.showCaseFileBrowserPurgeModal = true;
    },

    cancelCaseFileBrowserPurgeModal() {
      this.showCaseFileBrowserPurgeModal = false;
      this.caseFileBrowserPurgeConfirmChecked = false;
      this.caseFileBrowserPurgeConfirmPhrase = '';
      this.caseFileBrowserPurgeStatus = '';
    },

    confirmCaseFileBrowserPurgeAllData() {
      if (!this.caseFileBrowserPurgeCanConfirm) {
        this.caseFileBrowserPurgeStatus = `Check the confirmation box and type ${this.caseFileBrowserPurgePhrase} exactly.`;
        return;
      }
      const keys = this.caseFileBrowserListCaseDataStorageKeys();
      let removed = 0;
      if (typeof window !== 'undefined' && window.localStorage) {
        keys.forEach((key) => {
          try {
            window.localStorage.removeItem(key);
            removed += 1;
          } catch (error) {
            console.warn('Could not remove Case Buddy local-storage key.', key, error);
          }
        });
      }
      this.projectFiles = [];
      this.currentProjectFileId = '';
      this.selectedProjectFileId = '';
      this.pendingProjectBootstrapFileId = '';
      this.caseFileBrowserRows = [];
      this.caseFileBrowserSelectedId = '';
      this.caseFileBrowserPurgeStatus = `Removed ${removed} Case Buddy local-storage item${removed === 1 ? '' : 's'}. Case Buddy will refresh now.`;
      if (typeof this.showCaseToast === 'function') this.showCaseToast('Deleted saved Case Buddy data from this browser. Refreshing Case Buddy…');
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
        window.setTimeout(() => window.location.reload(), 450);
      }
    },

    getCaseInvestigationVisualizerCameraRuntime() {
      let runtime = CASE_MANUAL_SOLVE_CAMERA_RUNTIME.get(this);
      if (!runtime) {
        runtime = createCaseManualSolveCameraRuntime();
        CASE_MANUAL_SOLVE_CAMERA_RUNTIME.set(this, runtime);
      }
      return runtime;
    },

    normalizeCaseInvestigationVisualizerCameraViewport(viewport = null) {
      const fallback = this.caseInvestigationVisualizerViewport
        || this.caseInvestigationVisualizerCurrentVisual?.viewBox
        || { x: 0, y: 0, width: 1200, height: 700 };
      const source = viewport && typeof viewport === 'object' ? viewport : fallback;
      const width = Number(source.width);
      const height = Number(source.height);
      return {
        x: Number.isFinite(Number(source.x)) ? Number(source.x) : (Number(fallback.x) || 0),
        y: Number.isFinite(Number(source.y)) ? Number(source.y) : (Number(fallback.y) || 0),
        width: Number.isFinite(width) && width > 0 ? width : (Number(fallback.width) || 1200),
        height: Number.isFinite(height) && height > 0 ? height : (Number(fallback.height) || 700)
      };
    },

    caseInvestigationVisualizerEffectiveViewport() {
      const runtime = this.getCaseInvestigationVisualizerCameraRuntime();
      return this.normalizeCaseInvestigationVisualizerCameraViewport(
        runtime.pendingViewport
          || this.caseInvestigationVisualizerViewport
          || runtime.appliedViewport
          || this.caseInvestigationVisualizerCurrentVisual?.viewBox
      );
    },

    applyCaseInvestigationVisualizerViewportToDom(viewport = null) {
      const normalized = this.normalizeCaseInvestigationVisualizerCameraViewport(viewport);
      const svgRef = this.$refs.caseInvestigationVisualizerSvg;
      const svg = Array.isArray(svgRef) ? svgRef[0] : svgRef;
      if (svg && typeof svg.setAttribute === 'function') {
        svg.setAttribute('viewBox', `${normalized.x} ${normalized.y} ${normalized.width} ${normalized.height}`);
        const background = typeof svg.querySelector === 'function'
          ? svg.querySelector('.case-flow-visual-bg')
          : null;
        if (background && typeof background.setAttribute === 'function') {
          background.setAttribute('x', String(normalized.x));
          background.setAttribute('y', String(normalized.y));
          background.setAttribute('width', String(normalized.width));
          background.setAttribute('height', String(normalized.height));
        }
      }
      const runtime = this.getCaseInvestigationVisualizerCameraRuntime();
      runtime.appliedViewport = { ...normalized };
      return normalized;
    },

    setCaseInvestigationVisualizerCameraMoving(active = false) {
      const runtime = this.getCaseInvestigationVisualizerCameraRuntime();
      runtime.moving = Boolean(active);
      if (this.caseInvestigationVisualizerCameraMoving !== runtime.moving) {
        this.caseInvestigationVisualizerCameraMoving = runtime.moving;
      }
      const viewportRef = this.$refs.caseInvestigationVisualizerViewportEl;
      const viewport = Array.isArray(viewportRef) ? viewportRef[0] : viewportRef;
      viewport?.classList?.toggle?.('is-camera-moving', runtime.moving);
    },

    queueCaseInvestigationVisualizerCameraViewport(viewport = null, options = {}) {
      const runtime = this.getCaseInvestigationVisualizerCameraRuntime();
      runtime.pendingViewport = this.normalizeCaseInvestigationVisualizerCameraViewport(viewport);
      this.setCaseInvestigationVisualizerCameraMoving(true);
      const applyPending = () => {
        runtime.frame = 0;
        if (runtime.pendingViewport) this.applyCaseInvestigationVisualizerViewportToDom(runtime.pendingViewport);
      };
      if (!runtime.frame) {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          runtime.frame = window.requestAnimationFrame(applyPending);
        } else if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
          runtime.frame = window.setTimeout(applyPending, 16);
        } else {
          applyPending();
        }
      }
      if (runtime.commitTimer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(runtime.commitTimer);
        runtime.commitTimer = 0;
      }
      if (options.deferCommit !== true) {
        const delayMs = Number.isFinite(Number(options.commitDelayMs))
          ? Math.max(0, Number(options.commitDelayMs))
          : CASE_MANUAL_SOLVE_CAMERA_COMMIT_DELAY_MS;
        if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
          runtime.commitTimer = window.setTimeout(() => {
            runtime.commitTimer = 0;
            this.commitCaseInvestigationVisualizerCameraViewport();
          }, delayMs);
        } else {
          this.commitCaseInvestigationVisualizerCameraViewport();
        }
      }
      return runtime.pendingViewport;
    },

    commitCaseInvestigationVisualizerCameraViewport(options = {}) {
      const runtime = this.getCaseInvestigationVisualizerCameraRuntime();
      if (runtime.commitTimer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(runtime.commitTimer);
      }
      runtime.commitTimer = 0;
      if (runtime.frame && typeof window !== 'undefined') {
        if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(runtime.frame);
        else if (typeof window.clearTimeout === 'function') window.clearTimeout(runtime.frame);
      }
      runtime.frame = 0;
      const next = this.normalizeCaseInvestigationVisualizerCameraViewport(
        runtime.pendingViewport
          || this.caseInvestigationVisualizerViewport
          || runtime.appliedViewport
          || this.caseInvestigationVisualizerCurrentVisual?.viewBox
      );
      this.applyCaseInvestigationVisualizerViewportToDom(next);
      runtime.pendingViewport = null;
      const current = this.normalizeCaseInvestigationVisualizerCameraViewport(this.caseInvestigationVisualizerViewport);
      const changed = ['x', 'y', 'width', 'height'].some(key => Math.abs((Number(current[key]) || 0) - (Number(next[key]) || 0)) > 1e-7);
      if (changed || options.forceReactive === true) this.caseInvestigationVisualizerViewport = { ...next };
      this.caseInvestigationVisualizerPendingViewport = null;
      this.setCaseInvestigationVisualizerCameraMoving(false);
      const resolvers = Array.isArray(runtime.idleResolvers) ? runtime.idleResolvers.splice(0) : [];
      resolvers.forEach(resolve => {
        try { resolve(next); } catch { /* Camera waiters are best-effort. */ }
      });
      if (options.refreshCallouts !== false) {
        this.$nextTick(() => this.scheduleCaseManualStartViewportAutomationCalloutPositionRefresh?.({ immediate: true }));
      }
      return next;
    },

    waitForCaseInvestigationVisualizerCameraIdle(timeoutMs = 900) {
      const runtime = this.getCaseInvestigationVisualizerCameraRuntime();
      if (!runtime.moving && !runtime.pendingViewport && !runtime.frame) return Promise.resolve(true);
      return new Promise((resolve) => {
        let settled = false;
        let timeoutId = 0;
        let waiter = null;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          const waiterIndex = runtime.idleResolvers.indexOf(waiter);
          if (waiterIndex >= 0) runtime.idleResolvers.splice(waiterIndex, 1);
          if (timeoutId && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
            window.clearTimeout(timeoutId);
          }
          resolve(value);
        };
        waiter = () => finish(true);
        runtime.idleResolvers.push(waiter);
        if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
          timeoutId = window.setTimeout(() => finish(false), Math.max(50, Number(timeoutMs) || 900));
        }
      });
    },

    disposeCaseInvestigationVisualizerCameraRuntime() {
      const runtime = CASE_MANUAL_SOLVE_CAMERA_RUNTIME.get(this);
      if (!runtime) return;
      if (runtime.frame && typeof window !== 'undefined') {
        if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(runtime.frame);
        else if (typeof window.clearTimeout === 'function') window.clearTimeout(runtime.frame);
      }
      if (runtime.commitTimer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(runtime.commitTimer);
      const resolvers = Array.isArray(runtime.idleResolvers) ? runtime.idleResolvers.splice(0) : [];
      resolvers.forEach(resolve => {
        try { resolve(false); } catch { /* Camera waiters are best-effort. */ }
      });
      const viewportRef = this.$refs.caseInvestigationVisualizerViewportEl;
      const viewport = Array.isArray(viewportRef) ? viewportRef[0] : viewportRef;
      viewport?.classList?.remove?.('is-camera-moving');
      this.caseInvestigationVisualizerCameraMoving = false;
      CASE_MANUAL_SOLVE_CAMERA_RUNTIME.delete(this);
    },

    caseManualSolveYieldToBrowser(frames = 1) {
      const count = Math.max(1, Math.min(3, Number(frames) || 1));
      return new Promise((resolve) => {
        if (typeof window === 'undefined') {
          resolve();
          return;
        }
        let remaining = count;
        const step = () => {
          remaining -= 1;
          if (remaining <= 0) {
            resolve();
            return;
          }
          if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(step);
          else window.setTimeout(step, 0);
        };
        if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(step);
        else window.setTimeout(step, 0);
      });
    },

    continueCaseInvestigationVisualizerPan(event, state = {}) {
      const pointer = this.caseInvestigationVisualizerPointer || {};
      if (!pointer.active) return;
      const currentViewBox = this.caseInvestigationVisualizerEffectiveViewport();
      if (!currentViewBox) return;
      const lastClient = state.lastClient || { x: pointer.startX, y: pointer.startY };
      const dxClient = Number(event.clientX) - Number(lastClient.x);
      const dyClient = Number(event.clientY) - Number(lastClient.y);
      state.lastClient = { x: event.clientX, y: event.clientY };
      if (!Number.isFinite(dxClient) || !Number.isFinite(dyClient) || (dxClient === 0 && dyClient === 0)) return;
      let dx = 0;
      let dy = 0;
      const matrix = pointer.screenToSvgMatrix || null;
      if (matrix && [matrix.a, matrix.b, matrix.c, matrix.d].every(value => Number.isFinite(value))) {
        dx = (matrix.a * dxClient) + (matrix.c * dyClient);
        dy = (matrix.b * dxClient) + (matrix.d * dyClient);
      } else {
        const svg = this.$refs.caseInvestigationVisualizerSvg;
        const rect = svg?.getBoundingClientRect?.();
        if (!rect?.width || !rect?.height) return;
        dx = (dxClient / rect.width) * currentViewBox.width;
        dy = (dyClient / rect.height) * currentViewBox.height;
      }
      state.moved = state.moved || this.pointerMovedBeyondCaseInvestigationVisualizerThreshold(state.startClient, event);
      if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;
      this.queueCaseInvestigationVisualizerCameraViewport({
        ...currentViewBox,
        x: (Number(currentViewBox.x) || 0) - dx,
        y: (Number(currentViewBox.y) || 0) - dy
      }, { deferCommit: true });
    },

    zoomCaseInvestigationVisualizer(factor = 1) {
      if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
      const current = this.caseInvestigationVisualizerEffectiveViewport();
      if (!current) return;
      const safeFactor = Math.max(0.2, Math.min(5, Number(factor) || 1));
      const centerX = current.x + current.width / 2;
      const centerY = current.y + current.height / 2;
      const width = Math.max(320, current.width * safeFactor);
      const height = Math.max(240, current.height * safeFactor);
      return this.queueCaseInvestigationVisualizerCameraViewport({
        x: centerX - width / 2,
        y: centerY - height / 2,
        width,
        height
      }, { commitDelayMs: 72 });
    },

    onCaseInvestigationVisualizerWheel(event) {
      if (this.caseInvestigationVisualizerIsLocked?.()) return;
      const current = this.caseInvestigationVisualizerEffectiveViewport();
      if (!current) return;
      const runtime = this.getCaseInvestigationVisualizerCameraRuntime();
      const svgPoint = this.getCaseInvestigationVisualizerSvgPointFromEvent(event);
      const applied = this.normalizeCaseInvestigationVisualizerCameraViewport(
        runtime.pendingViewport ? (runtime.appliedViewport || current) : current
      );
      let ratioX = applied.width > 0 && Number.isFinite(svgPoint?.x)
        ? (svgPoint.x - applied.x) / applied.width
        : 0.5;
      let ratioY = applied.height > 0 && Number.isFinite(svgPoint?.y)
        ? (svgPoint.y - applied.y) / applied.height
        : 0.5;
      ratioX = Math.max(0, Math.min(1, ratioX));
      ratioY = Math.max(0, Math.min(1, ratioY));
      const deltaModeScale = event?.deltaMode === 1 ? 16 : (event?.deltaMode === 2 ? 120 : 1);
      const normalizedDelta = Math.max(-120, Math.min(120, (Number(event?.deltaY) || 0) * deltaModeScale));
      const factor = Math.exp(normalizedDelta * 0.0015);
      const width = Math.max(320, current.width * factor);
      const height = Math.max(240, current.height * factor);
      const anchorX = current.x + (ratioX * current.width);
      const anchorY = current.y + (ratioY * current.height);
      return this.queueCaseInvestigationVisualizerCameraViewport({
        x: anchorX - (ratioX * width),
        y: anchorY - (ratioY * height),
        width,
        height
      });
    },

    onCaseInvestigationVisualizerPointerDown(event) {
      this.commitCaseInvestigationVisualizerCameraViewport({ refreshCallouts: false });
      this.prepareCaseInvestigationVisualizerRightClickCopyGesture(event);
      if (this.caseInvestigationVisualizerManualStartMode && event?.target?.classList?.contains?.('case-flow-visual-bg')) {
        this.closeCaseManualSolveExpandedGroupPanel();
      }
      const result = this.callFirstCaseBuddyOriginalMethod('onCaseInvestigationVisualizerPointerDown', [event]);
      if (this.caseInvestigationVisualizerInteraction?.mode === 'pan') this.setCaseInvestigationVisualizerCameraMoving(true);
      return result;
    },

    onCaseInvestigationVisualizerPointerMove(event) {
      this.updateCaseInvestigationVisualizerRightClickCopyGesture(event);
      if (this.caseManualSolveVisualGroupDrag?.active) {
        this.updateCaseManualSolveVisualGroupDrag(event);
        return undefined;
      }
      // Automation callout DOM measurements are deliberately deferred until
      // pointer-up. Measuring and rewriting every callout during pan/drag was
      // one of the largest interaction hitches on larger graphs.
      return this.callFirstCaseBuddyOriginalMethod('onCaseInvestigationVisualizerPointerMove', [event]);
    },

    async onCaseInvestigationVisualizerPointerUp(event) {
      if (this.caseManualSolveVisualGroupDrag?.active) {
        this.finishCaseManualSolveVisualGroupDrag(event);
        await this.finishCaseInvestigationVisualizerRightClickCopyGesture(event);
        this.scheduleCaseManualStartViewportAutomationCalloutPositionRefresh?.();
        return undefined;
      }
      const interactionMode = this.caseInvestigationVisualizerInteraction?.mode || '';
      const wasDraggingNodes = Boolean(this.caseInvestigationVisualizerDraggingNodes);
      const wasSelectingNodes = Boolean(this.caseInvestigationVisualizerSelectingNodes || this.caseInvestigationVisualizerSelectionBox?.active);
      const selectionBoxBefore = wasSelectingNodes ? { ...(this.caseInvestigationVisualizerSelectionBox || {}) } : null;
      const result = this.callFirstCaseBuddyOriginalMethod('onCaseInvestigationVisualizerPointerUp', [event]);
      if (interactionMode === 'pan') this.commitCaseInvestigationVisualizerCameraViewport({ refreshCallouts: false });
      await this.finishCaseInvestigationVisualizerRightClickCopyGesture(event);
      if (wasDraggingNodes) this.caseManualSolveTryDropSelectionIntoGroup(event);
      if (wasSelectingNodes) this.caseManualSolveIncludeGroupsInSelectionBox(selectionBoxBefore);
      this.caseManualSolvePersistSelectedGroupPositions();
      this.scheduleCaseManualStartViewportAutomationCalloutPositionRefresh?.();
      return result;
    },

    getCaseInvestigationVisualizerNodeFromEvent(event = null) {
      const target = event?.target;
      const element = target && typeof target.closest === 'function'
        ? target.closest('[data-node-id]')
        : null;
      const nodeId = element?.dataset?.nodeId || '';
      return nodeId ? (this.caseInvestigationVisualizerNodeMap?.get?.(nodeId) || null) : null;
    },

    caseInvestigationVisualizerDetailValue(node = {}, labelMatchers = []) {
      const rows = Array.isArray(node?.detailRows) ? node.detailRows : [];
      const matchers = (Array.isArray(labelMatchers) ? labelMatchers : [labelMatchers])
        .map(value => String(value || '').toLowerCase())
        .filter(Boolean);
      for (const row of rows) {
        const label = String(row?.[0] || '').toLowerCase();
        if (!matchers.some(matcher => label.includes(matcher))) continue;
        const value = this.caseManualNormalizeText(row?.[1]);
        if (value && !/pending|unknown|—/iu.test(value)) return value;
      }
      return '';
    },

    caseInvestigationVisualizerClipboardPayloadForNode(node = null) {
      if (!node || typeof node !== 'object') return null;
      const type = this.caseManualNormalizeText(node.type).toLowerCase();
      if (type === 'tx') {
        const value = this.caseManualNormalizeText(
          node.hash || node.transactionHash || node.txHash || node.caseCandidate?.hash || node.candidate?.hash ||
          node.caseCandidate?.transactionHash || node.candidate?.transactionHash ||
          this.caseInvestigationVisualizerDetailValue(node, ['transaction hash', 'hash'])
        );
        return value ? { value, label: 'transaction hash' } : null;
      }
      if (type === 'address') {
        const value = this.caseManualNormalizeText(
          node.address || node.rawAddress || node.walletAddress || node.caseCandidate?.address || node.candidate?.address ||
          this.caseInvestigationVisualizerDetailValue(node, ['address'])
        );
        return value ? { value, label: 'address' } : null;
      }
      if (type === 'manualgroup') {
        const value = this.caseManualNormalizeText(node.groupName || node.labelText || node.id);
        return value ? { value, label: 'visual group name' } : null;
      }
      return null;
    },

    prepareCaseInvestigationVisualizerRightClickCopyGesture(event = null) {
      if (!event || event.button !== 2) {
        this.caseInvestigationVisualizerRightClickCopyGesture = { active: false, pointerId: null, startClientX: 0, startClientY: 0, nodeId: '', value: '', label: '', cancelled: false };
        return;
      }
      const node = this.getCaseInvestigationVisualizerNodeFromEvent(event);
      const payload = this.caseInvestigationVisualizerClipboardPayloadForNode(node);
      this.caseInvestigationVisualizerRightClickCopyGesture = {
        active: Boolean(payload?.value),
        pointerId: event.pointerId ?? null,
        startClientX: Number(event.clientX) || 0,
        startClientY: Number(event.clientY) || 0,
        nodeId: node?.id || '',
        value: payload?.value || '',
        label: payload?.label || '',
        cancelled: false
      };
    },

    updateCaseInvestigationVisualizerRightClickCopyGesture(event = null) {
      const gesture = this.caseInvestigationVisualizerRightClickCopyGesture || {};
      if (!gesture.active || !event) return;
      if (gesture.pointerId !== null && gesture.pointerId !== undefined && event.pointerId !== gesture.pointerId) return;
      if (event?.type === 'pointercancel') return false;
      const dx = (Number(event.clientX) || 0) - (Number(gesture.startClientX) || 0);
      const dy = (Number(event.clientY) || 0) - (Number(gesture.startClientY) || 0);
      if (Math.hypot(dx, dy) > 9) {
        this.caseInvestigationVisualizerRightClickCopyGesture = { ...gesture, cancelled: true };
      }
    },

    async finishCaseInvestigationVisualizerRightClickCopyGesture(event = null) {
      const gesture = this.caseInvestigationVisualizerRightClickCopyGesture || {};
      this.caseInvestigationVisualizerRightClickCopyGesture = { active: false, pointerId: null, startClientX: 0, startClientY: 0, nodeId: '', value: '', label: '', cancelled: false };
      if (!gesture.active || !event || event.type === 'pointercancel') return false;
      if (event?.type === 'pointercancel') return false;
      if (gesture.pointerId !== null && gesture.pointerId !== undefined && event.pointerId !== gesture.pointerId) return false;
      const dx = (Number(event.clientX) || 0) - (Number(gesture.startClientX) || 0);
      const dy = (Number(event.clientY) || 0) - (Number(gesture.startClientY) || 0);
      if (gesture.cancelled || Math.hypot(dx, dy) > 9) return false;
      if (!gesture.value) return false;
      await this.copyCaseInvestigationVisualizerNodeValueToClipboard(gesture.value, gesture.label);
      return true;
    },

    async copyCaseInvestigationVisualizerNodeValueToClipboard(value = '', label = '') {
      const text = this.caseManualNormalizeText(value);
      if (!text) return false;
      let copied = false;
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }
      if (!copied && typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', 'readonly');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        } finally {
          document.body.removeChild(textarea);
        }
      }
      const labelText = this.caseManualNormalizeText(label) || 'value';
      const message = copied
        ? `Copied ${labelText}: ${this.caseManualShorten(text, 12, 10)}`
        : `Could not copy this ${labelText} automatically.`;
      this.showCaseInvestigationVisualizerClipboardMessage(message);
      return copied;
    },

    showCaseInvestigationVisualizerClipboardMessage(message = '') {
      this.clearCaseInvestigationVisualizerClipboardMessage();
      this.caseInvestigationVisualizerClipboardMessage = this.caseManualNormalizeText(message);
      if (typeof window !== 'undefined') {
        this.caseInvestigationVisualizerClipboardTimer = window.setTimeout(() => {
          this.clearCaseInvestigationVisualizerClipboardMessage();
        }, 3200);
      }
    },

    clearCaseInvestigationVisualizerClipboardMessage() {
      if (this.caseInvestigationVisualizerClipboardTimer && typeof window !== 'undefined') {
        window.clearTimeout(this.caseInvestigationVisualizerClipboardTimer);
      }
      this.caseInvestigationVisualizerClipboardTimer = null;
      this.caseInvestigationVisualizerClipboardMessage = '';
    },

    caseManualStartDebugNodeSummary(node = {}) {
      const candidate = node?.caseCandidate || node?.candidate || {};
      return {
        id: node?.id || '',
        originalId: node?.originalId || '',
        type: node?.type || '',
        shape: node?.shape || '',
        x: Number.isFinite(Number(node?.x)) ? Number(node.x) : null,
        y: Number.isFinite(Number(node?.y)) ? Number(node.y) : null,
        hash: node?.hash || candidate?.hash || candidate?.transactionHash || candidate?.txHash || '',
        pathLabel: candidate?.pathLabel || candidate?.[CASE_MANUAL_START_COL.PATH_LABEL] || '',
        address: node?.address || this.caseManualStartCandidateEndpointAddress(candidate) || '',
        fromAddress: candidate?.fromAddress || candidate?.[CASE_MANUAL_START_COL.FROM_ADDRESS] || '',
        toAddress: candidate?.toAddress || candidate?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '',
        amountText: node?.amountText || candidate?.amountText || candidate?.amount || '',
        token: candidate?.token || candidate?.tokenSymbol || candidate?.[CASE_MANUAL_START_COL.TOKEN] || '',
        fundsFollowed: candidate?.fundsFollowed || candidate?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || '',
        leftBounds: candidate?.leftBoundsDisplay || candidate?.leftBoundsNumeric || candidate?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] || candidate?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || '',
        attribution: candidate?.attribution || candidate?.[CASE_MANUAL_START_COL.ATTRIBUTION] || '',
        fromAttributionLabel: candidate?.fromAttributionLabel || '',
        toAttributionLabel: candidate?.toAttributionLabel || '',
        visualLabelLines: typeof this.caseManualVisualNodeLabelLines === 'function' ? this.caseManualVisualNodeLabelLines(node) : (Array.isArray(node?.labelLines) ? node.labelLines.slice() : []),
        fromHash: candidate?.fromHash || candidate?.[CASE_MANUAL_START_COL.FROM_HASH] || '',
        continuesAs: candidate?.continuesAs || candidate?.[CASE_MANUAL_START_COL.CONTINUES_AS] || '',
        isPathEnding: Boolean(candidate?.isPathEnding),
        visualEndpointStatus: this.caseManualVisualNodeHasPendingUnknownServicePathEnding?.(node) ? 'unknown-service' : (candidate?.visualEndpointStatus || ''),
        labelCategory: typeof this.caseManualVisualNodeLabelCategory === 'function' ? this.caseManualVisualNodeLabelCategory(node) : (node?.labelCategory || ''),
        alertRing: typeof this.caseManualVisualNodeAlertRing === 'function' ? this.caseManualVisualNodeAlertRing(node) : Boolean(node?.alertRing),
        badge: node?.badge || '',
        swapLinearized: Boolean(node?.caseManualSwapLinearized || candidate?.caseManualSwapLinearized),
        swapContinuation: Boolean(candidate?.caseManualSwapContinuation),
        detailRows: Array.isArray(node?.detailRows) ? node.detailRows.slice(0, 12) : []
      };
    },

    caseManualStartVisualLayoutDebugSummary(nodes = []) {
      const visualNodes = (Array.isArray(nodes) ? nodes : [])
        .filter(node => ['tx', 'address', 'amount'].includes(String(node?.type || '').toLowerCase()))
        .filter(node => Number.isFinite(Number(node?.x)) && Number.isFinite(Number(node?.y)));
      const byColumn = new Map();
      visualNodes.forEach((node) => {
        const key = String(Math.round((Number(node.x) || 0) / 40) * 40);
        if (!byColumn.has(key)) byColumn.set(key, []);
        byColumn.get(key).push(node);
      });
      let minVerticalGap = null;
      const crowdedPairs = [];
      byColumn.forEach((columnNodes, xKey) => {
        columnNodes.sort((left, right) => (Number(left.y) || 0) - (Number(right.y) || 0));
        for (let index = 1; index < columnNodes.length; index += 1) {
          const upper = columnNodes[index - 1];
          const lower = columnNodes[index];
          const gap = Math.abs((Number(lower.y) || 0) - (Number(upper.y) || 0));
          if (minVerticalGap == null || gap < minVerticalGap) minVerticalGap = gap;
          if (gap < 132) {
            crowdedPairs.push({
              x: Number(xKey) || 0,
              gap,
              upper: {
                id: upper.id || '',
                type: upper.type || '',
                pathLabel: upper.caseCandidate?.pathLabel || upper.candidate?.pathLabel || '',
                hash: upper.hash || upper.caseCandidate?.hash || upper.candidate?.hash || ''
              },
              lower: {
                id: lower.id || '',
                type: lower.type || '',
                pathLabel: lower.caseCandidate?.pathLabel || lower.candidate?.pathLabel || '',
                hash: lower.hash || lower.caseCandidate?.hash || lower.candidate?.hash || ''
              }
            });
          }
        }
      });
      return {
        nodeCount: visualNodes.length,
        columnCount: byColumn.size,
        minVerticalGap: minVerticalGap == null ? null : Math.round(minVerticalGap * 100) / 100,
        crowdedPairCount: crowdedPairs.length,
        crowdedPairs: crowdedPairs.slice(0, 16)
      };
    },

    caseManualStartDebugRowSummary(row = []) {
      if (!Array.isArray(row)) return null;
      return {
        pathLabel: row?.[CASE_MANUAL_START_COL.PATH_LABEL] || '',
        hash: row?.[CASE_MANUAL_START_COL.TX_HASH] || '',
        fromAddress: row?.[CASE_MANUAL_START_COL.FROM_ADDRESS] || '',
        toAddress: row?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '',
        amount: row?.[CASE_MANUAL_START_COL.AMOUNT] || '',
        token: row?.[CASE_MANUAL_START_COL.TOKEN] || '',
        fundsFollowed: row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || '',
        priorBalance: row?.[CASE_MANUAL_START_COL.PRIOR_BALANCE] || '',
        fromHash: row?.[CASE_MANUAL_START_COL.FROM_HASH] || '',
        continuesAs: row?.[CASE_MANUAL_START_COL.CONTINUES_AS] || '',
        leftBoundsDisplay: row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] || '',
        leftBoundsNumeric: row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || '',
        attribution: row?.[CASE_MANUAL_START_COL.ATTRIBUTION] || '',
        systemNotes: row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES] || '',
        caseNotes: row?.[CASE_MANUAL_START_COL.CASE_NOTES] || ''
      };
    },

    caseManualStartBuildViewportDebugSnapshot() {
      const visual = this.caseInvestigationVisualizerCurrentVisual || {};
      const nodes = Array.isArray(visual.nodes) ? visual.nodes : [];
      const edges = Array.isArray(visual.edges) ? visual.edges : [];
      const entries = Array.isArray(visual.entries) ? visual.entries : [];
      const selectedIds = Array.isArray(this.caseInvestigationVisualizerSelectedNodeIds) ? this.caseInvestigationVisualizerSelectedNodeIds : [];
      const nodeIds = new Set(nodes.map(node => node?.id).filter(Boolean));
      const edgeIssues = edges
        .filter(edge => !nodeIds.has(edge?.from) || !nodeIds.has(edge?.to))
        .map(edge => ({ id: edge?.id || '', from: edge?.from || '', to: edge?.to || '' }));
      const debugCandidate = this.caseManualStartHopInspectorCandidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseInvestigationVisualizerSelectedCandidate || null;
      const previewCandidate = this.caseManualStartHopInspectorPreviewCandidate || null;
      const snapshot = this.caseManualStartHopInspectorSnapshot || {};
      const traceExitRows = this.caseManualExtractValidTraceExitRowsFromSnapshot(snapshot || {});
      const snapshotRows = this.extractCaseManualStartSnapshotRows(snapshot || {}, { candidate: debugCandidate });
      const outputRows = Array.isArray(this.caseManualStartHopInspectorOutputRows) ? this.caseManualStartHopInspectorOutputRows : [];
      const previewRows = Array.isArray(this.caseManualStartHopInspectorPreviewRows) ? this.caseManualStartHopInspectorPreviewRows : [];
      const traceProgress = this.caseManualStartHopInspectorTraceProgress || {};
      const summarizeRows = rows => (Array.isArray(rows) ? rows : []).slice(0, 14).map(row => this.caseManualStartDebugRowSummary(row)).filter(Boolean);
      const tool88InputRows = this.caseManualStartRowsForTool88?.() || [];
      const debugCandidateHash = this.caseManualNormalizeText(debugCandidate?.hash || debugCandidate?.txHash || debugCandidate?.transactionHash || '').toLowerCase();
      const candidateRowIndex = debugCandidate?.caseManualStartCandidate && debugCandidateHash
        ? this.caseManualFindDataRowIndexForCandidate(tool88InputRows, debugCandidate, debugCandidateHash)
        : (Number.isInteger(debugCandidate?.rowIndex)
          ? debugCandidate.rowIndex
          : (Number.isFinite(Number(debugCandidate?.rowIndex)) ? Math.trunc(Number(debugCandidate.rowIndex)) : null));
      const debugCandidatePathLabel = this.caseManualNormalizeText(debugCandidate?.pathLabel || debugCandidate?.label || '').toLowerCase();
      const debugCandidateTarget = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(debugCandidate || {}));
      const debugCandidateEndpointColumnIndex = this.caseManualStartCandidateEndpointColumnIndex(debugCandidate || {});
      const tool88MatchingRows = tool88InputRows
        .map((row, index) => ({ index, row }))
        .filter(({ row }) => {
          const rowHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
          const rowPathLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
          const rowTarget = this.caseManualNormalizeAddressKey(row?.[debugCandidateEndpointColumnIndex]);
          return Boolean(
            (debugCandidateHash && rowHash && this.caseManualTransactionHashesMatch(debugCandidateHash, rowHash))
              || (debugCandidatePathLabel && rowPathLabel && debugCandidatePathLabel === rowPathLabel)
              || (debugCandidateTarget && rowTarget && debugCandidateTarget === rowTarget)
          );
        })
        .slice(0, 8)
        .map(({ index, row }) => ({ index, ...this.caseManualStartDebugRowSummary(row) }));
      return {
        kind: 'case-buddy-manual-solve-viewport-debug',
        copiedAtUtc: new Date().toISOString(),
        caseDataRevision: this.caseSupportState?.caseDataRevision || 0,
        autosave: {
          spreadsheetAutoSaveEnabled: Boolean(this.autoSaveEnabled),
          pending: Boolean(this.caseManualStartAutosavePending),
          inFlight: Boolean(this.caseManualStartAutosaveInFlight),
          queuedDuringFlight: Boolean(this.caseManualStartAutosaveQueuedDuringFlight),
          pendingRevision: Number(this.caseManualStartAutosaveRevision) || 0,
          lastSavedRevision: Number(this.caseManualStartAutosaveLastSavedRevision) || 0,
          lastSavedAtUtc: this.caseManualStartAutosaveLastSavedAtUtc || '',
          lastError: this.caseManualStartAutosaveLastError || '',
          reason: this.caseManualStartAutosaveReason || '',
          status: this.caseManualStartAutosaveStatus || ''
        },
        workIndicator: {
          active: Boolean(this.caseManualStartViewportWorkIndicator?.active),
          finishing: Boolean(this.caseManualStartViewportWorkIndicator?.finishing),
          action: this.caseManualStartViewportWorkIndicator?.action || '',
          message: this.caseManualStartViewportWorkIndicator?.message || '',
          candidateKey: this.caseManualStartViewportWorkIndicator?.candidateKey || '',
          marker: this.caseManualStartViewportWorkIndicatorMarker ? {
            x: this.caseManualStartViewportWorkIndicatorMarker.x,
            y: this.caseManualStartViewportWorkIndicatorMarker.y,
            actionClass: this.caseManualStartViewportWorkIndicatorMarker.actionClass,
            showLane: Boolean(this.caseManualStartViewportWorkIndicatorMarker.showLane),
            laneSuppressedReason: this.caseManualStartViewportWorkIndicatorMarker.laneSuppressedReason || '',
            showResolveStamp: Boolean(this.caseManualStartViewportWorkIndicatorMarker.showResolveStamp),
            anchorAddress: this.caseManualStartViewportWorkIndicatorMarker.anchorAddress || '',
            candidateTargetAddress: this.caseManualStartViewportWorkIndicatorMarker.candidateTargetAddress || '',
            candidateFromAddress: this.caseManualStartViewportWorkIndicatorMarker.candidateFromAddress || '',
            anchorIsTargetAddress: Boolean(this.caseManualStartViewportWorkIndicatorMarker.anchorIsTargetAddress),
            anchorIsFromAddress: Boolean(this.caseManualStartViewportWorkIndicatorMarker.anchorIsFromAddress)
          } : null,
          startedAtMs: Number(this.caseManualStartViewportWorkIndicator?.startedAtMs || 0)
        },
        actionTray: {
          open: Boolean(this.caseManualStartNodeActionTrayOpen),
          attributionEditorOpen: Boolean(this.caseManualStartNodeActionTrayAttributionOpen),
          selectedCandidateKey: this.buildCaseManualStartHopInspectorCandidateKey(this.caseManualStartNodeActionTrayCandidate),
          attributionCandidateKey: this.buildCaseManualStartHopInspectorCandidateKey(this.caseManualStartNodeActionTrayAttributionCandidate),
          hasSingleManualCandidate: Boolean(this.caseManualStartNodeActionTrayHasSingleManualCandidate),
          toolActionsReady: Boolean(this.caseManualStartNodeActionTrayToolActionsReady),
          items: (this.caseManualStartNodeActionTrayItems || []).map(item => ({ key: item.key, label: item.label, disabled: Boolean(item.disabled), recommended: Boolean(item.recommended) }))
        },
        amountEditor: {
          open: Boolean(this.caseManualSolveAmountEditor?.open),
          field: this.caseManualSolveAmountEditor?.field || '',
          token: this.caseManualSolveAmountEditorToken || '',
          rowIndex: Number.isInteger(this.caseManualSolveAmountEditor?.rowIndex) ? this.caseManualSolveAmountEditor.rowIndex : null,
          candidateKey: this.caseManualSolveAmountEditor?.candidateKey || '',
          canApply: Boolean(this.caseManualSolveAmountEditorCanApply),
          status: this.caseManualSolveAmountEditor?.status || ''
        },
        getData: {
          open: Boolean(this.caseManualSolveGetDataPanelOpen),
          rowCount: Number(this.caseManualSolveGetDataLastSummary?.rowCount) || 0,
          sourceLabel: this.caseManualSolveGetDataLastSummary?.sourceLabel || '',
          status: this.caseManualSolveGetDataStatus || '',
          draftLength: String(this.caseManualSolveGetDataDraft || '').length
        },
        flowEvaluation: {
          open: Boolean(this.caseManualSolveEvaluationOpen),
          bottomPanel: {
            height: Math.round(this.caseManualSolveBottomPanelClampedHeight || 0),
            resizing: Boolean(this.caseManualSolveBottomPanelResizing),
            bounds: this.caseManualSolveBottomPanelBounds || null
          },
          flowLabel: this.caseManualSolveEvaluationState.flowLabel || '',
          initialAmountDisplay: this.caseManualSolveEvaluationState.initialAmountDisplay || '',
          initialUsdDisplay: this.caseManualSolveEvaluationState.initialUsdDisplay || '',
          currentUsdDisplay: this.caseManualSolveEvaluationState.currentUsdDisplay || '',
          coveragePercentDisplay: this.caseManualSolveEvaluationState.coveragePercentDisplay || '',
          endpointCount: this.caseManualSolveEvaluationState.endpointCount || 0,
          resolvedValuationCount: this.caseManualSolveEvaluationState.resolvedValuationCount || 0,
          unresolvedValuationCount: this.caseManualSolveEvaluationState.unresolvedValuationCount || 0,
          tokenRows: this.caseManualSolveEvaluationState.tokenRows || [],
          unresolvedRows: (this.caseManualSolveEvaluationState.unresolvedRows || []).map(row => ({ pathLabel: row.pathLabel, hash: row.hash, issue: row.issue, traceStoppedAt: row.traceStoppedAt })),
          warnings: this.caseManualSolveEvaluationState.warnings || [],
          heatMap: {
            enabled: Boolean(this.caseManualSolveHeatMapEnabled),
            active: Boolean(this.caseManualSolveHeatMapActive),
            sensitivity: Number(this.caseManualSolveHeatMapSensitivity) || 1,
            canHeatMap: Boolean(this.caseManualSolveHeatMapState.canHeatMap),
            summary: this.caseManualSolveHeatMapState.summary || '',
            nodeCount: this.caseManualSolveHeatMapState.nodeCount || 0,
            edgeCount: this.caseManualSolveHeatMapState.edgeCount || 0,
            valuedRowCount: this.caseManualSolveHeatMapState.valuedRowCount || 0,
            unresolvedRowCount: this.caseManualSolveHeatMapState.unresolvedRowCount || 0,
            zeroRowCount: this.caseManualSolveHeatMapState.zeroRowCount || 0,
            sampleRows: (this.caseManualSolveHeatMapState.rowStats || []).slice(0, 18).map(row => ({
              pathLabel: row.pathLabel,
              hash: row.hash,
              token: row.token,
              amount: row.amount,
              usdValue: row.usdValue,
              percent: row.percent,
              color: row.color,
              resolved: row.resolved,
              zero: row.zero,
              issue: row.issue
            }))
          }
        },
        visual: {
          id: visual.id || '',
          key: visual.key || '',
          source: visual.source || '',
          mainLabel: visual.mainLabel || '',
          mainHash: visual.mainHash || '',
          viewBox: visual.viewBox || null,
          currentViewport: this.caseInvestigationVisualizerViewport || null,
          viewMode: this.caseInvestigationVisualizerViewMode || '',
          transactionOnlyMode: Boolean(this.caseInvestigationVisualizerTransactionOnlyMode),
          activeIndex: Number(this.caseInvestigationVisualizerActiveIndex) || 0,
          nodeCount: nodes.length,
          visibleNodeCount: this.caseInvestigationVisualizerVisibleNodes?.length || 0,
          edgeCount: edges.length,
          visibleEdgeCount: this.caseInvestigationVisualizerEdges?.length || 0,
          entryCount: entries.length,
          unresolvedEndingCount: this.getCaseManualStartViewportUnresolvedEndingCount?.() ?? null,
          selectedNodeIds: selectedIds,
          selectedNodes: this.caseInvestigationVisualizerSelectedNodes.map(node => this.caseManualStartDebugNodeSummary(node)),
          missingEdgeEndpointCount: edgeIssues.length,
          missingEdgeEndpoints: edgeIssues.slice(0, 40),
          layout: this.caseManualStartVisualLayoutDebugSummary(nodes),
          manualSolveGroups: this.caseManualSolveVisualGroupDebugSummary()
        },
        viewportAuto: {
          active: Boolean(this.caseManualStartViewportAutoActive),
          status: this.caseManualStartViewportAuto?.status || '',
          detail: this.caseManualStartViewportAuto?.detail || '',
          nextActionCount: Number(this.caseManualStartViewportAuto?.nextActionCount || 0),
          sameSignatureCount: (() => {
            const signature = this.buildCaseManualStartViewportAutoSignature?.() || '';
            const counts = this.caseManualStartViewportAuto?.signatureCounts || {};
            return signature ? Number(counts[signature] || 0) : 0;
          })(),
          unresolvedEndingCount: this.getCaseManualStartViewportUnresolvedEndingCount?.() ?? null
        },
        hopInspector: {
          open: Boolean(this.caseManualStartHopInspectorOpen),
          previewOpen: Boolean(this.caseManualStartHopInspectorPreviewOpen),
          running: Boolean(this.caseManualStartHopInspectorRunning || this.caseManualStartHopInspectorTraceLoading),
          traceLoading: Boolean(this.caseManualStartHopInspectorTraceLoading),
          candidateLabel: this.caseManualStartHopInspectorCandidateLabel || '',
          recommendationTitle: this.caseManualStartHopInspectorRecommendationTitle || '',
          recommendedActionKey: this.resolveCaseManualStartHopInspectorRecommendedActionKey?.() || '',
          checksReady: Boolean(this.caseManualStartHopInspectorChecksReady),
          canAct: Boolean(this.caseManualStartHopInspectorCanAct),
          checkState: this.caseManualStartHopInspectorCheckState || '',
          selectedCount: Number(this.caseInvestigationVisualizerSelectedCount || 0),
          traceOverrideNeeded: Boolean(this.caseManualStartHopInspectorTraceOverrideNeeded),
          traceAnywayBlocked: Boolean(this.caseManualStartTraceAnywayBlocked),
          unknownServiceOutputEvidence: Boolean(this.caseManualRowsContainUnknownServicePathEndingEvidence(outputRows)),
          pendingUnknownServicePathEnding: Boolean(this.caseManualStartHopInspectorHasPendingUnknownServicePathEnding?.(this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate)),
          loadedCandidateKey: this.caseManualStartHopInspectorLoadedCandidateKey || '',
          selectedCandidateKey: this.buildCaseManualStartHopInspectorCandidateKey(this.caseManualStartHopInspectorSelectedCandidate),
          pinnedCandidateKey: this.buildCaseManualStartHopInspectorCandidateKey(this.caseManualStartHopInspectorCandidate),
          previewCandidateKey: this.buildCaseManualStartHopInspectorCandidateKey(previewCandidate),
          lastEvent: this.caseManualStartHopInspectorLastEvent || '',
          status: this.caseManualStartHopInspectorStatus || '',
          previewStatus: this.caseManualStartHopInspectorPreviewStatus || '',
          outputRowCount: outputRows.length,
          previewRowCount: previewRows.length,
          snapshotRowCount: snapshotRows.length,
          traceExitRowCount: traceExitRows.length,
          candidate: debugCandidate ? this.caseManualStartDebugNodeSummary({ candidate: debugCandidate }) : null,
          previewCandidate: previewCandidate ? this.caseManualStartDebugNodeSummary({ candidate: previewCandidate }) : null,
          traceProgress,
          outputRows: summarizeRows(outputRows),
          previewRows: summarizeRows(previewRows),
          snapshotRows: summarizeRows(snapshotRows),
          traceExitRows: summarizeRows(traceExitRows),
          tool88Input: {
            rowCount: tool88InputRows.length,
            headerDetected: Boolean(tool88InputRows.length && this.caseManualIsHeaderRow(tool88InputRows[0])),
            candidateRowIndex,
            rowAtCandidateRowIndex: Number.isInteger(candidateRowIndex) ? this.caseManualStartDebugRowSummary(tool88InputRows[candidateRowIndex] || []) : null,
            candidateSpreadsheetRowNumber: debugCandidate?.spreadsheetRowNumber || null,
            matchingRows: tool88MatchingRows
          },
          snapshot: {
            status: snapshot?.status || '',
            integrationAction: snapshot?.integrationAction || '',
            message: snapshot?.message || snapshot?.result?.message || '',
            selectedTargetAddress: snapshot?.selectedTargetAddress || snapshot?.result?.selectedTargetAddress || '',
            selectedEntryKey: snapshot?.selectedEntryKey || snapshot?.result?.selectedEntryKey || snapshot?.selectedEntry?.key || snapshot?.result?.selectedEntry?.key || '',
            selectedEntry: snapshot?.selectedEntry || snapshot?.result?.selectedEntry || null,
            outputRows: Array.isArray(snapshot?.outputRows) ? snapshot.outputRows.length : (Array.isArray(snapshot?.result?.outputRows) ? snapshot.result.outputRows.length : 0),
            traceExitRows: traceExitRows.length,
            hasDormantResult: Boolean(this.caseManualExtractDormantResultFromSnapshot(snapshot || {})),
            hasValidTraceExitRows: this.caseManualSnapshotHasValidTraceExitRows(snapshot || {})
          }
        },
        nodes: nodes.map(node => this.caseManualStartDebugNodeSummary(node)),
        edges: edges.map(edge => ({
          id: edge?.id || '',
          from: edge?.from || '',
          to: edge?.to || '',
          dashed: Boolean(edge?.dashed),
          canonical: Boolean(edge?.canonical),
          swapLinearized: Boolean(edge?.caseManualSwapLinearized),
          bridgeLinearized: Boolean(edge?.caseManualBridgeLinearized || edge?.bridge || edge?.isBridgeEdge),
          bridgeLabel: edge?.bridgeLabel || edge?.bridgeBadge?.label || ''
        })),
        entries: entries.map((entry, index) => ({
          index,
          pathLabel: entry?.pathLabel || entry?.[CASE_MANUAL_START_COL.PATH_LABEL] || '',
          hash: entry?.hash || entry?.[CASE_MANUAL_START_COL.TX_HASH] || '',
          fromAddress: entry?.fromAddress || entry?.[CASE_MANUAL_START_COL.FROM_ADDRESS] || '',
          toAddress: entry?.toAddress || entry?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '',
          amount: entry?.amount || entry?.[CASE_MANUAL_START_COL.AMOUNT] || '',
          token: entry?.token || entry?.tokenSymbol || entry?.[CASE_MANUAL_START_COL.TOKEN] || '',
          fundsFollowed: entry?.fundsFollowed || entry?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || '',
          priorBalance: entry?.priorBalance || entry?.[CASE_MANUAL_START_COL.PRIOR_BALANCE] || '',
          fromHash: entry?.fromHash || entry?.[CASE_MANUAL_START_COL.FROM_HASH] || '',
          continuesAs: entry?.continuesAs || entry?.[CASE_MANUAL_START_COL.CONTINUES_AS] || '',
          leftBoundsDisplay: entry?.leftBoundsDisplay || entry?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] || '',
          leftBoundsNumeric: entry?.leftBoundsNumeric || entry?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || '',
          attribution: entry?.attribution || entry?.[CASE_MANUAL_START_COL.ATTRIBUTION] || '',
          systemNotes: entry?.systemNotes || entry?.[CASE_MANUAL_START_COL.SYSTEM_NOTES] || '',
          caseNotes: entry?.caseNotes || entry?.[CASE_MANUAL_START_COL.CASE_NOTES] || ''
        }))
      };
    },

    async copyCaseManualStartViewportDebugSnapshot() {
      const snapshot = this.caseManualStartBuildViewportDebugSnapshot();
      const text = JSON.stringify(snapshot, null, 2);
      let copied = false;
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }
      if (!copied && typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', 'readonly');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        } finally {
          document.body.removeChild(textarea);
        }
      }
      const message = copied
        ? `Copied Manual Solve debug snapshot (${snapshot.visual.nodeCount} nodes, ${snapshot.visual.edgeCount} edges).`
        : 'Could not copy the Manual Solve debug snapshot automatically.';
      this.showCaseInvestigationVisualizerClipboardMessage(message);
      return copied;
    },

    buildCaseInvestigationVisualizerTransactionOnlyEdges(edges = [], nodes = [], hidden = new Set()) {
      const hiddenSet = hidden instanceof Set ? hidden : new Set(Array.isArray(hidden) ? hidden : []);
      const nodeById = new Map();
      (Array.isArray(nodes) ? nodes : []).forEach((node) => {
        if (!node?.id || hiddenSet.has(node.id)) return;
        nodeById.set(node.id, node);
      });
      const txIds = new Set(
        [...nodeById.values()]
          .filter(node => String(node?.type || '').toLowerCase() === 'tx')
          .map(node => node.id)
      );
      if (txIds.size < 2) return [];

      const adjacency = new Map();
      const addEdge = (from, to, dashed = false) => {
        if (!from || !to || hiddenSet.has(from) || hiddenSet.has(to)) return;
        if (!nodeById.has(from) || !nodeById.has(to)) return;
        if (!adjacency.has(from)) adjacency.set(from, []);
        adjacency.get(from).push({ to, dashed: Boolean(dashed) });
      };
      (Array.isArray(edges) ? edges : []).forEach(edge => addEdge(edge?.from, edge?.to, edge?.dashed));

      const transactionEdges = [];
      const seen = new Set();
      const addTransactionOnlyEdge = (sourceId, targetId, dashed = false, source = 'visual') => {
        if (!sourceId || !targetId || sourceId === targetId || !txIds.has(sourceId) || !txIds.has(targetId)) return;
        const edgeKey = `${sourceId}=>${targetId}`;
        if (seen.has(edgeKey)) return;
        seen.add(edgeKey);
        transactionEdges.push({
          id: `tx-only-${transactionEdges.length}-${sourceId}-${targetId}`,
          from: sourceId,
          to: targetId,
          dashed: Boolean(dashed),
          transactionOnly: true,
          source
        });
      };

      const txNodes = [...nodeById.values()].filter(node => txIds.has(node.id));
      const txIdByHash = new Map();
      txNodes.forEach((node) => {
        const hash = this.caseManualNormalizeText(node.hash || node.transactionHash || node.txHash || node.caseCandidate?.hash || node.candidate?.hash || '');
        const key = hash.toLowerCase();
        if (key && !txIdByHash.has(key)) txIdByHash.set(key, node.id);
      });
      const findTxIdByHash = (hashValue = '') => {
        const key = this.caseManualNormalizeText(hashValue).toLowerCase();
        if (!key) return '';
        if (txIdByHash.has(key)) return txIdByHash.get(key);
        for (const [hashKey, nodeId] of txIdByHash.entries()) {
          if (this.caseManualTransactionHashesMatch?.(hashKey, key)) return nodeId;
        }
        return '';
      };
      txNodes.forEach((node) => {
        const parentHash = this.caseManualNormalizeText(node.caseCandidate?.fromHash || node.candidate?.fromHash || node.fromHash || '');
        const parentNodeId = findTxIdByHash(parentHash);
        if (parentNodeId) addTransactionOnlyEdge(parentNodeId, node.id, false, 'from-hash');
      });

      const maxDepth = Math.max(8, Math.min(120, nodeById.size + 2));

      txIds.forEach((sourceId) => {
        const queue = (adjacency.get(sourceId) || []).map(next => ({
          nodeId: next.to,
          dashed: Boolean(next.dashed),
          depth: 1,
          visited: new Set([sourceId])
        }));

        while (queue.length) {
          const current = queue.shift();
          const node = nodeById.get(current.nodeId);
          if (!node || current.visited.has(current.nodeId) || current.depth > maxDepth) continue;
          const nodeType = String(node?.type || '').toLowerCase();
          if (nodeType === 'tx') {
            addTransactionOnlyEdge(sourceId, current.nodeId, current.dashed, 'visual');
            continue;
          }

          const nextVisited = new Set(current.visited);
          nextVisited.add(current.nodeId);
          (adjacency.get(current.nodeId) || []).forEach(next => {
            queue.push({
              nodeId: next.to,
              dashed: Boolean(current.dashed || next.dashed),
              depth: current.depth + 1,
              visited: nextVisited
            });
          });
        }
      });

      return transactionEdges;
    },

    buildCaseInvestigationVisualizerViewportForNodes(nodes = []) {
      const sourceNodes = (Array.isArray(nodes) ? nodes : []).filter(node => node && Number.isFinite(Number(node.x)) && Number.isFinite(Number(node.y)));
      if (!sourceNodes.length) return this.caseInvestigationVisualizerCurrentVisual?.viewBox || { x: 0, y: 0, width: 1200, height: 700 };
      const xs = sourceNodes.map(node => Number(node.x) || 0);
      const ys = sourceNodes.map(node => Number(node.y) || 0);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const padX = this.caseInvestigationVisualizerTransactionOnlyMode ? 260 : 420;
      const padY = this.caseInvestigationVisualizerTransactionOnlyMode ? 220 : 320;
      return {
        x: minX - padX,
        y: minY - padY,
        width: Math.max(760, (maxX - minX) + (padX * 2)),
        height: Math.max(420, (maxY - minY) + (padY * 2))
      };
    },

    resetCaseInvestigationVisualizerViewport() {
      if (!this.caseInvestigationVisualizerCurrentVisual) return;
      this.caseInvestigationVisualizerViewport = this.buildCaseInvestigationVisualizerViewportForNodes(this.caseInvestigationVisualizerVisibleNodes);
      this.scheduleCaseManualStartViewportAutomationCalloutPositionRefresh?.();
    },

    pruneCaseInvestigationVisualizerSelectionForTransactionOnlyMode() {
      if (!this.caseInvestigationVisualizerTransactionOnlyMode) return;
      const txIds = new Set(this.caseInvestigationVisualizerNodes.filter(node => String(node?.type || '').toLowerCase() === 'tx').map(node => node.id));
      this.caseInvestigationVisualizerSelectedNodeIds = (this.caseInvestigationVisualizerSelectedNodeIds || []).filter(id => txIds.has(id));
      this.caseInvestigationVisualizerSelectedNodeId = txIds.has(this.caseInvestigationVisualizerSelectedNodeId) ? this.caseInvestigationVisualizerSelectedNodeId : (this.caseInvestigationVisualizerSelectedNodeIds[0] || '');
      if (!this.caseInvestigationVisualizerSelectedNodeId) this.caseInvestigationVisualizerSelectedCandidate = null;
    },

    toggleCaseInvestigationVisualizerTransactionOnlyMode() {
      if (!this.caseInvestigationVisualizerTransactionOnlyCanToggle || this.caseInvestigationVisualizerInteractionLocked) return;
      this.caseInvestigationVisualizerTransactionOnlyMode = !this.caseInvestigationVisualizerTransactionOnlyMode;
      this.pruneCaseInvestigationVisualizerSelectionForTransactionOnlyMode();
      this.resetCaseInvestigationVisualizerViewport();
      this.caseInvestigationVisualizerStatus = this.caseInvestigationVisualizerTransactionOnlyMode
        ? 'Transactions-only view is on. Address nodes and BTC amount/output nodes are hidden until you return to Full graph.'
        : 'Full graph view restored. Address nodes and BTC amount/output nodes are visible again.';
    },

    async resetCaseInvestigationVisualizerNodePositions() {
      if (!this.caseInvestigationVisualizerCurrentVisual || this.caseInvestigationVisualizerInteractionLocked) return;
      const keepTransactionOnlyMode = Boolean(this.caseInvestigationVisualizerTransactionOnlyMode);
      if (this.caseInvestigationVisualizerState?.source === 'case-buddy-manual-start-visualizer') {
        const activeKey = this.caseInvestigationVisualizerCurrentFlow?.key || this.caseInvestigationVisualizerLastFlowKey || '';
        const rows = this.getCaseManualStartVisualizerRows({ includeAll: true });
        this.caseInvestigationVisualizerState = this.buildCaseManualStartVisualizerState(rows);
        this.cacheCaseManualStartVisualizerPreparedState(rows, this.caseInvestigationVisualizerState);
        if (activeKey) {
          const index = (this.caseInvestigationVisualizerState.flows || []).findIndex(flow => flow.key === activeKey);
          if (index >= 0) this.caseInvestigationVisualizerActiveIndex = index;
        }
        this.setCaseManualStartCurrentVisual({ mode: this.caseInvestigationVisualizerViewMode });
        this.caseInvestigationVisualizerTransactionOnlyMode = keepTransactionOnlyMode;
        this.pruneCaseInvestigationVisualizerSelectionForTransactionOnlyMode();
        this.resetCaseInvestigationVisualizerViewport();
        this.caseInvestigationVisualizerStatus = keepTransactionOnlyMode
          ? 'Node positions reset. Transactions-only view is still on; use Full graph to show address and BTC amount nodes again.'
          : 'Node positions reset to the current Manual Solve layout.';
        return;
      }

      const result = this.callFirstCaseBuddyOriginalMethod('resetCaseInvestigationVisualizerNodePositions');
      if (result && typeof result.then === 'function') await result;
      this.caseInvestigationVisualizerTransactionOnlyMode = keepTransactionOnlyMode;
      this.pruneCaseInvestigationVisualizerSelectionForTransactionOnlyMode();
      this.resetCaseInvestigationVisualizerViewport();
      this.caseInvestigationVisualizerStatus = keepTransactionOnlyMode
        ? 'Node positions reset. Transactions-only view is still on; use Full graph to show address nodes again.'
        : 'Node positions reset.';
    },

    getCaseManualStartMainMatrix() {
      const matrix = typeof this.getMatrixForLogicalSheet === 'function'
        ? this.getMatrixForLogicalSheet('main')
        : [];
      return Array.isArray(matrix) ? matrix : [];
    },

    caseManualNormalizeText(value) {
      return normalizeCell(value);
    },

    clampCaseManualSolveBottomPanelHeight(value = this.caseManualSolveBottomPanelHeight, options = {}) {
      const bounds = this.caseManualSolveBottomPanelBounds || { min: 110, max: 640 };
      const numeric = Number(value);
      const fallback = Number(this.caseManualSolveBottomPanelHeight) || (this.caseInvestigationVisualizerFullscreen ? 260 : 280);
      const raw = Number.isFinite(numeric) ? numeric : fallback;
      const clamped = Math.max(bounds.min, Math.min(bounds.max, raw));
      if (options.commit !== false) this.caseManualSolveBottomPanelHeight = clamped;
      return clamped;
    },

    ensureCaseManualSolveBottomPanelVisibleForResize() {
      if (this.caseManualSolveEvaluationOpen || this.caseManualStartViewportDisplayControlsOpen) return;
      this.caseManualStartViewportDisplayControlsOpen = true;
    },

    beginCaseManualSolveBottomPanelResize(event) {
      if (!this.caseInvestigationVisualizerManualStartMode || this.caseInvestigationVisualizerInteractionLocked) return;
      this.ensureCaseManualSolveBottomPanelVisibleForResize();
      this.caseManualSolveBottomPanelResizing = true;
      this.caseManualSolveBottomPanelResizeStart = {
        y: Number(event?.clientY) || 0,
        height: this.caseManualSolveBottomPanelClampedHeight
      };
      if (typeof window !== 'undefined') {
        window.addEventListener('pointermove', this.onCaseManualSolveBottomPanelResizeMove, { passive: false });
        window.addEventListener('pointerup', this.finishCaseManualSolveBottomPanelResize, { once: true });
        window.addEventListener('pointercancel', this.finishCaseManualSolveBottomPanelResize, { once: true });
      }
    },

    onCaseManualSolveBottomPanelResizeMove(event) {
      if (!this.caseManualSolveBottomPanelResizing) return;
      if (typeof event?.preventDefault === 'function') event.preventDefault();
      const start = this.caseManualSolveBottomPanelResizeStart || { y: 0, height: this.caseManualSolveBottomPanelClampedHeight };
      const currentY = Number(event?.clientY) || 0;
      const delta = Number(start.y || 0) - currentY;
      this.clampCaseManualSolveBottomPanelHeight((Number(start.height) || this.caseManualSolveBottomPanelClampedHeight) + delta);
    },

    finishCaseManualSolveBottomPanelResize(arg = {}) {
      if (typeof window !== 'undefined') {
        window.removeEventListener('pointermove', this.onCaseManualSolveBottomPanelResizeMove);
        window.removeEventListener('pointerup', this.finishCaseManualSolveBottomPanelResize);
        window.removeEventListener('pointercancel', this.finishCaseManualSolveBottomPanelResize);
      }
      if (!this.caseManualSolveBottomPanelResizing && !arg?.silent) return;
      this.caseManualSolveBottomPanelResizing = false;
      this.clampCaseManualSolveBottomPanelHeight();
    },

    resetCaseManualSolveBottomPanelHeight() {
      this.ensureCaseManualSolveBottomPanelVisibleForResize();
      this.caseManualSolveBottomPanelHeight = this.caseInvestigationVisualizerFullscreen ? 260 : 280;
      this.clampCaseManualSolveBottomPanelHeight();
    },

    adjustCaseManualSolveBottomPanelHeightFromKeyboard(event) {
      const key = event?.key || '';
      if (!key) return;
      const bounds = this.caseManualSolveBottomPanelBounds || { min: 110, max: 640 };
      const step = event?.shiftKey ? 32 : 16;
      let nextHeight = this.caseManualSolveBottomPanelClampedHeight;
      if (key === 'ArrowUp') nextHeight += step;
      else if (key === 'ArrowDown') nextHeight -= step;
      else if (key === 'PageUp') nextHeight += 64;
      else if (key === 'PageDown') nextHeight -= 64;
      else if (key === 'Home') nextHeight = bounds.min;
      else if (key === 'End') nextHeight = bounds.max;
      else return;
      if (typeof event?.preventDefault === 'function') event.preventDefault();
      this.ensureCaseManualSolveBottomPanelVisibleForResize();
      this.clampCaseManualSolveBottomPanelHeight(nextHeight);
    },

    toggleCaseManualSolveEvaluationPanel() {
      if (!this.caseInvestigationVisualizerManualStartMode) return;
      this.caseManualSolveEvaluationOpen = !this.caseManualSolveEvaluationOpen;
      if (this.caseManualSolveEvaluationOpen) {
        this.caseManualStartViewportDisplayControlsOpen = false;
      } else {
        this.caseManualSolveHeatMapEnabled = false;
      }
    },

    closeCaseManualSolveEvaluationPanel() {
      this.caseManualSolveEvaluationOpen = false;
      this.caseManualSolveHeatMapEnabled = false;
    },

    caseManualSolveEvaluationEmptyState(problemText = '') {
      return {
        canEvaluate: false,
        flowLabel: this.caseInvestigationVisualizerCurrentVisual?.mainLabel || '',
        mainHash: this.caseInvestigationVisualizerCurrentVisual?.mainHash || '',
        summary: problemText || 'Open a Manual Solve flow before evaluating current endpoints.',
        problemText,
        referenceStatus: '',
        referenceSource: '',
        initialAmountDisplay: '',
        initialUsdDisplay: '',
        currentUsdValue: 0,
        currentUsdDisplay: '$0.00',
        coveragePercent: null,
        coveragePercentDisplay: '—',
        coverageDetail: 'No usable initial USD reference is available yet.',
        endpointCount: 0,
        resolvedValuationCount: 0,
        unresolvedValuationCount: 0,
        tokenRows: [],
        tokenBreakdownText: '',
        endpointRows: [],
        unresolvedRows: [],
        warnings: []
      };
    },

    caseManualSolveEvaluationNormalizeMainLabel(value = '') {
      return this.caseManualNormalizeText(value).replace(/^path\s+/iu, '').trim().toLowerCase();
    },

    caseManualSolveEvaluationFormatAmountToken(amount = null, token = '') {
      const tokenText = this.caseManualNormalizeText(token).toUpperCase();
      if (!Number.isFinite(Number(amount)) || !tokenText) return '';
      return this.caseManualFormatTransactionAmountDisplay(String(amount), tokenText, { includeToken: true });
    },

    caseManualSolveEvaluationBuildMainReference(mainReference = {}) {
      const referenceAmount = Number(mainReference.referenceAmount);
      const referenceToken = this.caseManualNormalizeText(mainReference.referenceToken).toUpperCase();
      const base = {
        ...mainReference,
        referenceAmount: Number.isFinite(referenceAmount) ? referenceAmount : null,
        referenceToken,
        isUsable: false,
        currentUsdValue: null,
        currentUsdDisplay: '',
        manualSolveReferenceSource: '',
        manualSolveReferenceStatus: ''
      };

      const existingUsd = parseCaseWwwUsdReference(mainReference.currentColumnH);
      if (Number.isFinite(existingUsd) && existingUsd > 0 && Number.isFinite(referenceAmount) && referenceAmount > 0 && referenceToken) {
        return {
          ...base,
          isUsable: true,
          currentUsdValue: existingUsd,
          currentUsdDisplay: formatCaseWwwUsd(existingUsd),
          manualSolveReferenceSource: 'Column H USD reference',
          manualSolveReferenceStatus: 'Using existing Column H USD reference.'
        };
      }

      if (!Number.isFinite(referenceAmount) || referenceAmount <= 0 || !referenceToken) {
        return {
          ...base,
          manualSolveReferenceStatus: mainReference.problemText || 'The main/root row does not have a usable initial amount and token.'
        };
      }

      if (caseWwwIsStablecoin(referenceToken)) {
        return {
          ...base,
          isUsable: true,
          currentUsdValue: referenceAmount,
          currentUsdDisplay: formatCaseWwwUsd(referenceAmount),
          manualSolveReferenceSource: 'Stablecoin $1 assumption',
          manualSolveReferenceStatus: `${referenceToken} is treated as 1 USD for this live Manual Solve evaluation.`
        };
      }

      const estimate = estimateCaseBuddyHistoricalUsdValue({
        symbol: referenceToken,
        timestampUtc: mainReference.utcDate,
        amount: referenceAmount
      });
      if (estimate?.ok && Number.isFinite(estimate.estimatedUsdValue)) {
        return {
          ...base,
          isUsable: true,
          currentUsdValue: estimate.estimatedUsdValue,
          currentUsdDisplay: formatCaseWwwUsd(estimate.estimatedUsdValue),
          manualSolveReferenceSource: `Nearest local ${estimate.symbol || referenceToken} reference`,
          manualSolveReferenceStatus: `${estimate.status || 'Nearest local historical reference point used.'}${estimate.snapshotTimestampUtc ? ` Snapshot: ${estimate.snapshotTimestampUtc}.` : ''}`
        };
      }

      return {
        ...base,
        manualSolveReferenceStatus: estimate?.status || `No local historical USD reference was available for ${referenceToken}.`
      };
    },

    caseManualSolveEvaluationEntryChildHashes(entry = {}, knownHashes = new Set()) {
      const childHashes = [];
      (entry.rows || []).forEach((item) => {
        parseCaseWwwContinuationHashes(item?.row?.[CASE_MANUAL_START_COL.CONTINUES_AS] || '').forEach((hash) => {
          const key = normalizeCaseWwwHash(hash);
          if (key && (!knownHashes.size || knownHashes.has(key)) && !childHashes.includes(key)) childHashes.push(key);
        });
      });
      return childHashes;
    },

    caseManualSolveEvaluationEndpointRowsForSegment(segmentEntries = []) {
      const knownHashes = new Set((segmentEntries || []).map(entry => normalizeCaseWwwHash(entry.txnHash)).filter(Boolean));
      const endpoints = [];
      (segmentEntries || []).forEach((entry) => {
        const generatedRows = getCaseWwwGeneratedPathEndingRowsForEntry(entry);
        if (generatedRows.length) {
          generatedRows.forEach(row => endpoints.push({ ...row, entry, implicit: false }));
          return;
        }
        const childHashes = this.caseManualSolveEvaluationEntryChildHashes(entry, knownHashes);
        if (childHashes.length > 0) return;
        const firstRow = entry.firstRow || entry.rows?.[0]?.row || [];
        endpoints.push({ row: firstRow, rowIndex: entry.startRow, synthetic: false, implicit: true, entry });
      });
      return endpoints;
    },

    buildCaseManualSolveEvaluationState() {
      if (!this.caseInvestigationVisualizerManualStartMode) {
        return this.caseManualSolveEvaluationEmptyState('The flow evaluator is only available in Manual Solve mode.');
      }
      const visual = this.caseInvestigationVisualizerCurrentVisual || null;
      if (!visual) return this.caseManualSolveEvaluationEmptyState('No Manual Solve visual is currently loaded.');

      const rows = normalizeCaseMatrix(this.getCaseManualStartMainMatrix());
      if (!rows.length) return this.caseManualSolveEvaluationEmptyState('Main Data is empty, so there is no current flow to evaluate.');

      const referenceRows = buildCaseWwwReferenceRows(rows);
      const entries = normalizeCaseWwwEntries(parseCaseEntries(rows));
      const lookup = buildCaseWwwEntryLookup(entries);
      const mainHashKey = normalizeCaseWwwHash(visual.mainHash || '');
      const mainLabelKey = this.caseManualSolveEvaluationNormalizeMainLabel(visual.mainLabel || '');
      const referenceRow = referenceRows.find((row) => {
        const rowHash = normalizeCaseWwwHash(row.transactionHash || '');
        const rowLabel = this.caseManualSolveEvaluationNormalizeMainLabel(row.pathLabel || '');
        return Boolean((mainHashKey && rowHash && rowHash === mainHashKey) || (mainLabelKey && rowLabel && rowLabel === mainLabelKey));
      }) || referenceRows[Number(this.caseInvestigationVisualizerActiveIndex) || 0] || referenceRows[0] || null;
      if (!referenceRow) return this.caseManualSolveEvaluationEmptyState('No top-level main/root row was found for this Manual Solve flow.');

      const mainEntryIndices = referenceRows.map(row => findCaseWwwEntryIndexForMain(row, entries, lookup));
      const mainIndex = Math.max(0, referenceRows.indexOf(referenceRow));
      const currentEntryIndex = mainEntryIndices[mainIndex];
      if (currentEntryIndex === undefined || currentEntryIndex === null) {
        return this.caseManualSolveEvaluationEmptyState(`The current root ${referenceRow.pathLabel || referenceRow.transactionHash || ''} could not be matched to parsed Main Data entries.`);
      }

      let nextEntryIndex = entries.length;
      for (let index = mainIndex + 1; index < referenceRows.length; index += 1) {
        const candidateIndex = mainEntryIndices[index];
        if (candidateIndex !== undefined && candidateIndex !== null && candidateIndex > currentEntryIndex) {
          nextEntryIndex = candidateIndex;
          break;
        }
      }

      const segmentEntries = entries.slice(currentEntryIndex, nextEntryIndex);
      const mainReference = this.caseManualSolveEvaluationBuildMainReference(referenceRow);
      const endpointSources = this.caseManualSolveEvaluationEndpointRowsForSegment(segmentEntries);
      const tokenTotals = new Map();
      const tokenUsdTotals = new Map();
      const endpointRows = [];
      const unresolvedRows = [];
      const warnings = [];
      let currentUsdValue = 0;
      let resolvedValuationCount = 0;

      if (!mainReference.isUsable) {
        warnings.push(mainReference.manualSolveReferenceStatus || 'The initial/root USD value could not be estimated. Stablecoin endpoints can still show direct USD values, but cross-token coverage cannot be completed.');
      }

      endpointSources.forEach((source, index) => {
        const entry = source.entry || {};
        const pathEndingRow = Array.isArray(source.row) ? source.row : [];
        const amountToken = getCaseWwwPathEndingAmountToken(pathEndingRow, entry);
        const valuation = evaluateCaseWwwUsdForPathEnding({ pathEndingRow, entry, mainTransaction: mainReference, segmentEntries });
        if (amountToken) addCaseWwwTokenTotal(tokenTotals, amountToken.token, amountToken.amount);
        const resolved = Boolean(valuation.resolved && Number.isFinite(valuation.usdValue));
        if (resolved) {
          currentUsdValue += valuation.usdValue;
          resolvedValuationCount += 1;
          const tokenKey = this.caseManualNormalizeText(amountToken?.token || '').toUpperCase();
          if (tokenKey) tokenUsdTotals.set(tokenKey, (tokenUsdTotals.get(tokenKey) || 0) + valuation.usdValue);
        }
        const pathLabel = this.caseManualNormalizeText(pathEndingRow[CASE_MANUAL_START_COL.PATH_LABEL] || entry.pathLabel || '');
        const hash = this.caseManualNormalizeText(pathEndingRow[CASE_MANUAL_START_COL.TX_HASH] || entry.txnHash || '');
        const toAddress = this.caseManualNormalizeText(pathEndingRow[CASE_MANUAL_START_COL.TO_ADDRESS] || entry.firstRow?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '');
        const pathEndingReference = formatCaseWwwPathEndingReference(pathEndingRow, entry);
        const displayRow = {
          key: `manual-solve-eval-${entry.index ?? currentEntryIndex}-${source.rowIndex ?? entry.startRow}-${index}-${hash}`,
          pathLabel,
          hash,
          shortHash: hash ? this.caseManualShorten(hash, 10, 8) : '(blank hash)',
          toAddress,
          toAddressShort: toAddress ? this.caseManualShorten(toAddress, 10, 8) : '',
          amountDisplay: amountToken ? this.caseManualSolveEvaluationFormatAmountToken(amountToken.amount, amountToken.token) : '',
          usdDisplay: resolved ? formatCaseWwwUsd(valuation.usdValue) : '',
          resolved,
          issue: valuation.issue || '',
          traceStoppedAt: valuation.manualReview?.traceStoppedAt || '',
          pathEndingReference,
          synthetic: Boolean(source.synthetic),
          implicit: Boolean(source.implicit),
          statusLabel: resolved ? 'Valued' : 'Needs valuation review',
          resolution: valuation.resolution || ''
        };
        endpointRows.push(displayRow);
        if (!resolved) unresolvedRows.push(displayRow);
      });

      const tokenRows = Array.from(tokenTotals.entries())
        .map(([token, amount]) => ({
          token,
          amount,
          amountDisplay: this.caseManualSolveEvaluationFormatAmountToken(amount, token),
          usdDisplay: tokenUsdTotals.has(token) ? formatCaseWwwUsd(tokenUsdTotals.get(token) || 0) : ''
        }))
        .sort((left, right) => left.token.localeCompare(right.token));
      const initialUsdValue = Number(mainReference.currentUsdValue);
      const coveragePercent = Number.isFinite(initialUsdValue) && initialUsdValue > 0
        ? (currentUsdValue / initialUsdValue) * 100
        : null;
      const endpointCount = endpointRows.length;
      const unresolvedValuationCount = endpointCount - resolvedValuationCount;
      const coverageDetail = Number.isFinite(coveragePercent)
        ? `${formatCaseWwwUsd(currentUsdValue)} of ${formatCaseWwwUsd(initialUsdValue)} currently valued${unresolvedValuationCount ? ' from resolved endpoints only' : ''}.`
        : (mainReference.manualSolveReferenceStatus || 'Initial USD value is unavailable.');
      const summary = endpointCount === 0
        ? 'No furthest endpoints were found in the current Manual Solve flow.'
        : unresolvedValuationCount
          ? `${resolvedValuationCount}/${endpointCount} current endpoint${endpointCount === 1 ? '' : 's'} valued; ${unresolvedValuationCount} need review.`
          : `All ${endpointCount} current endpoint${endpointCount === 1 ? '' : 's'} are valued.`;

      return {
        canEvaluate: true,
        flowLabel: visual.mainLabel || referenceRow.pathLabel || 'Current flow',
        mainHash: referenceRow.transactionHash || visual.mainHash || '',
        summary,
        problemText: '',
        referenceStatus: mainReference.manualSolveReferenceStatus || '',
        referenceSource: mainReference.manualSolveReferenceSource || '',
        initialAmountDisplay: this.caseManualSolveEvaluationFormatAmountToken(mainReference.referenceAmount, mainReference.referenceToken),
        initialUsdValue: Number.isFinite(initialUsdValue) ? initialUsdValue : null,
        initialUsdDisplay: Number.isFinite(initialUsdValue)
          ? `${formatCaseWwwUsd(initialUsdValue)} · ${mainReference.manualSolveReferenceSource || 'USD reference'}`
          : '',
        currentUsdValue,
        currentUsdDisplay: formatCaseWwwUsd(currentUsdValue),
        coveragePercent,
        coveragePercentDisplay: Number.isFinite(coveragePercent) ? `${coveragePercent.toFixed(2)}%` : '—',
        coverageDetail,
        endpointCount,
        resolvedValuationCount,
        unresolvedValuationCount,
        tokenRows,
        tokenBreakdownText: buildCaseWwwTokenBreakdown(tokenTotals),
        endpointRows,
        unresolvedRows,
        warnings
      };
    },


    caseManualSolveHeatMapEmptyState(problemText = '') {
      return {
        canHeatMap: false,
        summary: problemText || 'Open a Manual Solve flow evaluation before enabling the heat map.',
        problemText,
        initialUsdValue: null,
        nodeCount: 0,
        edgeCount: 0,
        valuedRowCount: 0,
        unresolvedRowCount: 0,
        zeroRowCount: 0,
        errorCount: 0,
        rowStats: [],
        rowStatsByPathHash: new Map(),
        rowStatsByHash: new Map(),
        rowStatsByHashAddress: new Map(),
        rowStatsByAddressPath: new Map(),
        rowStatsByAddress: new Map()
      };
    },

    caseManualSolveBuildEvaluationContext() {
      if (!this.caseInvestigationVisualizerManualStartMode) {
        return { problemText: 'The flow evaluator is only available in Manual Solve mode.' };
      }
      const visual = this.caseInvestigationVisualizerCurrentVisual || null;
      if (!visual) return { problemText: 'No Manual Solve visual is currently loaded.' };

      const rows = normalizeCaseMatrix(this.getCaseManualStartMainMatrix());
      if (!rows.length) return { problemText: 'Main Data is empty, so there is no current flow to evaluate.' };

      const referenceRows = buildCaseWwwReferenceRows(rows);
      const entries = normalizeCaseWwwEntries(parseCaseEntries(rows));
      const lookup = buildCaseWwwEntryLookup(entries);
      const mainHashKey = normalizeCaseWwwHash(visual.mainHash || '');
      const mainLabelKey = this.caseManualSolveEvaluationNormalizeMainLabel(visual.mainLabel || '');
      const referenceRow = referenceRows.find((row) => {
        const rowHash = normalizeCaseWwwHash(row.transactionHash || '');
        const rowLabel = this.caseManualSolveEvaluationNormalizeMainLabel(row.pathLabel || '');
        return Boolean((mainHashKey && rowHash && rowHash === mainHashKey) || (mainLabelKey && rowLabel && rowLabel === mainLabelKey));
      }) || referenceRows[Number(this.caseInvestigationVisualizerActiveIndex) || 0] || referenceRows[0] || null;
      if (!referenceRow) return { problemText: 'No top-level main/root row was found for this Manual Solve flow.' };

      const mainEntryIndices = referenceRows.map(row => findCaseWwwEntryIndexForMain(row, entries, lookup));
      const mainIndex = Math.max(0, referenceRows.indexOf(referenceRow));
      const currentEntryIndex = mainEntryIndices[mainIndex];
      if (currentEntryIndex === undefined || currentEntryIndex === null) {
        return { problemText: `The current root ${referenceRow.pathLabel || referenceRow.transactionHash || ''} could not be matched to parsed Main Data entries.` };
      }

      let nextEntryIndex = entries.length;
      for (let index = mainIndex + 1; index < referenceRows.length; index += 1) {
        const candidateIndex = mainEntryIndices[index];
        if (candidateIndex !== undefined && candidateIndex !== null && candidateIndex > currentEntryIndex) {
          nextEntryIndex = candidateIndex;
          break;
        }
      }

      const segmentEntries = entries.slice(currentEntryIndex, nextEntryIndex);
      const mainReference = this.caseManualSolveEvaluationBuildMainReference(referenceRow);
      return { visual, rows, referenceRows, entries, lookup, referenceRow, mainReference, segmentEntries, currentEntryIndex, nextEntryIndex };
    },

    buildCaseManualSolveHeatMapState() {
      const context = this.caseManualSolveBuildEvaluationContext();
      if (context.problemText) return this.caseManualSolveHeatMapEmptyState(context.problemText);
      const { visual, mainReference, segmentEntries } = context;
      const initialUsdValue = Number(mainReference?.currentUsdValue);
      if (!mainReference?.isUsable || !Number.isFinite(initialUsdValue) || initialUsdValue <= 0) {
        return this.caseManualSolveHeatMapEmptyState(mainReference?.manualSolveReferenceStatus || 'The current flow does not have a usable initial USD reference for heat-map percentages.');
      }

      const state = this.caseManualSolveHeatMapEmptyState('');
      state.canHeatMap = true;
      state.initialUsdValue = initialUsdValue;
      state.problemText = '';
      state.nodeCount = Array.isArray(visual?.nodes) ? visual.nodes.filter(node => this.caseManualSolveVisualNodeIsColorable(node)).length : 0;
      state.edgeCount = Array.isArray(visual?.edges) ? visual.edges.length : 0;

      const remember = (map, key, stat) => {
        if (!key) return;
        const existing = map.get(key);
        if (!existing || this.caseManualSolveHeatMapStatScore(stat) > this.caseManualSolveHeatMapStatScore(existing)) map.set(key, stat);
      };

      (segmentEntries || []).forEach((entry) => {
        (entry.rows || []).forEach((item, rowOffset) => {
          const row = this.caseManualEnsureRowWidth(Array.isArray(item?.row) ? item.row : [], Math.max(CASE_MANUAL_START_COLUMN_COUNT, item?.row?.length || 0));
          if (!row.length) return;
          const hash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH] || entry.txnHash || '');
          const hashKey = normalizeCaseWwwHash(hash);
          const pathLabel = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL] || entry.pathLabel || '');
          const pathKey = pathLabel.toLowerCase();
          const fromAddressKey = this.caseManualNormalizeAddressKey(row[CASE_MANUAL_START_COL.FROM_ADDRESS] || entry.firstRow?.[CASE_MANUAL_START_COL.FROM_ADDRESS] || '');
          const toAddressKey = this.caseManualNormalizeAddressKey(row[CASE_MANUAL_START_COL.TO_ADDRESS] || entry.firstRow?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '');
          const amountToken = getCaseWwwPathEndingAmountToken(row, entry);
          const valuation = amountToken
            ? evaluateCaseWwwUsdForPathEnding({ pathEndingRow: row, entry, mainTransaction: mainReference, segmentEntries })
            : { resolved: false, issue: 'No usable Funds Followed or amount/token value was available for this row.' };
          const resolved = Boolean(valuation?.resolved && Number.isFinite(valuation?.usdValue));
          const amountValue = Number(amountToken?.amount);
          const zero = Number.isFinite(amountValue) && amountValue <= 0;
          const percent = resolved ? (Number(valuation.usdValue) / initialUsdValue) * 100 : null;
          const stat = {
            key: `hm-${entry.index ?? 0}-${item?.rowIndex ?? entry.startRow ?? 0}-${rowOffset}-${hashKey || pathKey}`,
            entryIndex: entry.index,
            rowIndex: item?.rowIndex ?? entry.startRow ?? null,
            pathLabel,
            hash,
            hashKey,
            fromAddressKey,
            toAddressKey,
            amount: Number.isFinite(amountValue) ? amountValue : null,
            token: this.caseManualNormalizeText(amountToken?.token || '').toUpperCase(),
            usdValue: resolved ? Number(valuation.usdValue) : null,
            percent,
            resolved,
            zero,
            overInitial: Number.isFinite(percent) && percent > 100,
            issue: valuation?.issue || '',
            resolution: valuation?.resolution || ''
          };
          stat.color = this.caseManualSolveHeatMapColorForStat(stat);
          stat.statusClass = this.caseManualSolveHeatMapStatusClass(stat);
          state.rowStats.push(stat);
          if (resolved) state.valuedRowCount += 1;
          else state.unresolvedRowCount += 1;
          if (zero) state.zeroRowCount += 1;
          if (!resolved || zero) state.errorCount += 1;
          remember(state.rowStatsByPathHash, `${pathKey}|${hashKey}`, stat);
          remember(state.rowStatsByHash, hashKey, stat);
          remember(state.rowStatsByHashAddress, `${hashKey}|${toAddressKey}`, stat);
          remember(state.rowStatsByHashAddress, `${hashKey}|${fromAddressKey}`, stat);
          remember(state.rowStatsByAddressPath, `${toAddressKey}|${pathKey}`, stat);
          remember(state.rowStatsByAddressPath, `${fromAddressKey}|${pathKey}`, stat);
          remember(state.rowStatsByAddress, toAddressKey, stat);
          remember(state.rowStatsByAddress, fromAddressKey, stat);
        });
      });

      state.summary = state.rowStats.length
        ? `${state.valuedRowCount}/${state.rowStats.length} row value${state.rowStats.length === 1 ? '' : 's'} mapped to the viewport${state.unresolvedRowCount ? ` · ${state.unresolvedRowCount} unresolved/error` : ''}.`
        : 'No row values were available to color this Manual Solve flow.';
      if (!state.rowStats.length) state.canHeatMap = false;
      return state;
    },

    caseManualSolveHeatMapStatScore(stat = {}) {
      if (!stat) return -1;
      let score = 0;
      if (stat.resolved) score += 1000;
      if (Number.isFinite(Number(stat.percent))) score += Math.min(500, Math.abs(Number(stat.percent)));
      if (stat.hashKey) score += 20;
      if (stat.pathLabel) score += 10;
      if (stat.toAddressKey || stat.fromAddressKey) score += 5;
      return score;
    },

    caseManualSolveHeatMapColorForStat(stat = {}) {
      if (stat?.zero) return '#38bdf8';
      const rawPercent = Number(stat?.percent);
      if (!stat?.resolved || !Number.isFinite(rawPercent)) return '#e879f9';
      const sensitivity = Math.max(0.35, Math.min(2.5, Number(this.caseManualSolveHeatMapSensitivity) || 1));
      const capped = Math.max(0, Math.min(180, rawPercent));
      const normalized = Math.max(0, Math.min(1, capped / 100));
      const adjustedUnder = 100 * Math.pow(normalized, 1 / sensitivity);
      const adjusted = capped > 100 ? 100 + ((capped - 100) * sensitivity) : adjustedUnder;
      return this.caseManualSolveHeatMapColorForPercent(adjusted);
    },

    caseManualSolveHeatMapColorForPercent(percent = 0) {
      const stops = [
        { p: 0, c: '#022c22' },
        { p: 5, c: '#064e3b' },
        { p: 15, c: '#15803d' },
        { p: 35, c: '#84cc16' },
        { p: 55, c: '#facc15' },
        { p: 70, c: '#f59e0b' },
        { p: 90, c: '#f97316' },
        { p: 100, c: '#dc2626' },
        { p: 125, c: '#7c3aed' },
        { p: 180, c: '#4c1d95' }
      ];
      const value = Math.max(0, Math.min(180, Number(percent) || 0));
      for (let index = 0; index < stops.length - 1; index += 1) {
        const left = stops[index];
        const right = stops[index + 1];
        if (value >= left.p && value <= right.p) {
          const span = right.p - left.p || 1;
          return this.caseManualSolveHeatMapInterpolateColor(left.c, right.c, (value - left.p) / span);
        }
      }
      return stops[stops.length - 1].c;
    },

    caseManualSolveHeatMapStatusClass(stat = {}) {
      if (!stat?.resolved) return 'is-heat-map-error';
      if (stat.zero) return 'is-heat-map-zero';
      if (Number(stat.percent) > 100) return 'is-heat-map-over-initial';
      return 'is-heat-map-valued';
    },

    caseManualSolveHeatMapParseHexColor(color = '') {
      const normalized = this.caseManualSolveNormalizeColor(color);
      if (!normalized) return { r: 0, g: 0, b: 0 };
      return {
        r: parseInt(normalized.slice(1, 3), 16),
        g: parseInt(normalized.slice(3, 5), 16),
        b: parseInt(normalized.slice(5, 7), 16)
      };
    },

    caseManualSolveHeatMapInterpolateColor(leftColor = '#000000', rightColor = '#ffffff', ratio = 0) {
      const left = this.caseManualSolveHeatMapParseHexColor(leftColor);
      const right = this.caseManualSolveHeatMapParseHexColor(rightColor);
      const t = Math.max(0, Math.min(1, Number(ratio) || 0));
      const channel = (a, b) => Math.round(a + ((b - a) * t)).toString(16).padStart(2, '0');
      return `#${channel(left.r, right.r)}${channel(left.g, right.g)}${channel(left.b, right.b)}`;
    },

    caseManualSolveHeatMapNodeStat(node = {}, state = this.caseManualSolveHeatMapState) {
      if (!this.caseInvestigationVisualizerManualStartMode || !this.caseManualSolveVisualNodeIsColorable(node)) return null;
      const candidate = node?.caseCandidate || node?.candidate || {};
      const hashKey = normalizeCaseWwwHash(node?.hash || candidate?.hash || candidate?.transactionHash || candidate?.txHash || candidate?.[CASE_MANUAL_START_COL.TX_HASH] || '');
      const pathKey = this.caseManualNormalizeText(candidate?.pathLabel || candidate?.label || node?.pathLabel || candidate?.[CASE_MANUAL_START_COL.PATH_LABEL] || '').toLowerCase();
      const addressKey = this.caseManualNormalizeAddressKey(node?.address || candidate?.targetAddress || candidate?.toAddress || candidate?.address || '');
      const fromAddressKey = this.caseManualNormalizeAddressKey(candidate?.fromAddress || candidate?.[CASE_MANUAL_START_COL.FROM_ADDRESS] || '');
      const toAddressKey = this.caseManualNormalizeAddressKey(candidate?.toAddress || candidate?.targetAddress || candidate?.address || candidate?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '');
      const keys = [
        ['rowStatsByPathHash', `${pathKey}|${hashKey}`],
        ['rowStatsByHashAddress', `${hashKey}|${addressKey}`],
        ['rowStatsByHashAddress', `${hashKey}|${toAddressKey}`],
        ['rowStatsByHashAddress', `${hashKey}|${fromAddressKey}`],
        ['rowStatsByAddressPath', `${addressKey}|${pathKey}`],
        ['rowStatsByAddressPath', `${toAddressKey}|${pathKey}`],
        ['rowStatsByAddressPath', `${fromAddressKey}|${pathKey}`],
        ['rowStatsByHash', hashKey],
        ['rowStatsByAddress', addressKey],
        ['rowStatsByAddress', toAddressKey],
        ['rowStatsByAddress', fromAddressKey]
      ];
      for (const [mapName, key] of keys) {
        if (!key || /\|$/u.test(key) || /^\|/u.test(key)) continue;
        const map = state?.[mapName];
        const stat = map instanceof Map ? map.get(key) : null;
        if (stat) return stat;
      }
      return null;
    },

    caseManualSolveHeatMapNodeColor(node = {}) {
      if (!this.caseManualSolveHeatMapActive) return '';
      if (!this.caseManualSolveVisualNodeIsColorable(node)) return '';
      const stat = this.caseManualSolveHeatMapNodeStat(node, this.caseManualSolveHeatMapState);
      return stat?.color || '#e879f9';
    },

    caseManualSolveHeatMapNodeTitle(node = {}) {
      if (!this.caseManualSolveHeatMapActive || !this.caseManualSolveVisualNodeIsColorable(node)) return '';
      const stat = this.caseManualSolveHeatMapNodeStat(node, this.caseManualSolveHeatMapState);
      const label = this.caseManualNormalizeText(node.pathLabel || stat?.pathLabel || '') || 'Node';
      const identity = this.caseManualNormalizeText(node.shortHash || node.shortAddress || node.amountText || node.labelText || '');
      if (!stat) return `${label}${identity ? ` · ${identity}` : ''} · Heat map valuation unavailable.`;
      const percent = Number(stat.percent);
      const percentText = Number.isFinite(percent) ? `${percent.toFixed(2)}% of initial value` : 'Percent unavailable';
      const valueText = Number.isFinite(Number(stat.usdValue)) ? formatCaseWwwUsd(Number(stat.usdValue)) : 'USD unavailable';
      const amountText = Number.isFinite(Number(stat.amount)) && stat.token
        ? this.caseManualSolveEvaluationFormatAmountToken(stat.amount, stat.token)
        : '';
      const issueText = stat.issue ? ` · Issue: ${stat.issue}` : '';
      return `${label}${identity ? ` · ${identity}` : ''} · ${percentText} · ${valueText}${amountText ? ` · ${amountText}` : ''}${issueText}`;
    },

    caseManualSolveHeatMapEdgeStat(edge = {}, state = this.caseManualSolveHeatMapState) {
      if (!this.caseManualSolveHeatMapActive) return null;
      const nodes = Array.isArray(this.caseInvestigationVisualizerCurrentVisual?.nodes)
        ? this.caseInvestigationVisualizerCurrentVisual.nodes
        : this.caseInvestigationVisualizerNodes;
      const nodeById = new Map((nodes || []).map(node => [node?.id, node]).filter(([id]) => !!id));
      const fromNode = nodeById.get(edge?.from);
      const toNode = nodeById.get(edge?.to);
      const candidates = [
        toNode && String(toNode.type || '').toLowerCase() === 'tx' ? toNode : null,
        fromNode && String(fromNode.type || '').toLowerCase() === 'tx' ? fromNode : null,
        toNode && String(toNode.type || '').toLowerCase() === 'amount' ? toNode : null,
        fromNode && String(fromNode.type || '').toLowerCase() === 'amount' ? fromNode : null,
        toNode,
        fromNode
      ].filter(Boolean);
      for (const node of candidates) {
        const stat = this.caseManualSolveHeatMapNodeStat(node, state);
        if (stat) return stat;
      }
      return null;
    },

    caseManualSolveHeatMapEdgeColor(edge = {}) {
      if (!this.caseManualSolveHeatMapActive) return '';
      const stat = this.caseManualSolveHeatMapEdgeStat(edge, this.caseManualSolveHeatMapState);
      return stat?.color || '#e879f9';
    },



    caseManualBuildUtcDateFromParts(year, month, day, rawHour = 0, minute = 0, second = 0, meridiem = '') {
      const numericYear = Number(year);
      const numericMonth = Number(month);
      const numericDay = Number(day);
      let numericHour = Number(rawHour || 0);
      const numericMinute = Number(minute || 0);
      const numericSecond = Number(String(second || 0).replace(/\..*$/u, ''));
      const marker = this.caseManualNormalizeText(meridiem).toLowerCase().replace(/\./gu, '');

      if (!Number.isInteger(numericYear) || !Number.isInteger(numericMonth) || !Number.isInteger(numericDay)) return null;
      if (numericMonth < 1 || numericMonth > 12 || numericDay < 1 || numericDay > 31) return null;
      if (marker === 'pm' && numericHour < 12) numericHour += 12;
      if (marker === 'am' && numericHour === 12) numericHour = 0;
      if (numericHour < 0 || numericHour > 23 || numericMinute < 0 || numericMinute > 59 || numericSecond < 0 || numericSecond > 59) return null;

      const date = new Date(Date.UTC(numericYear, numericMonth - 1, numericDay, numericHour, numericMinute, numericSecond));
      if (!date || Number.isNaN(date.getTime())) return null;
      if (
        date.getUTCFullYear() !== numericYear
        || date.getUTCMonth() !== numericMonth - 1
        || date.getUTCDate() !== numericDay
      ) {
        return null;
      }
      return date;
    },

    caseManualFormatUtcDateText(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
      return `${date.toISOString().replace('T', ' ').slice(0, 19)} UTC`;
    },

    caseManualNormalizeDateText(value = '') {
      const original = this.caseManualNormalizeText(value);
      if (!original) return '';

      const text = original
        .replace(/\b(\d{1,2})(st|nd|rd|th)\b/giu, '$1')
        .replace(/\b([AP])\.M\.\b/giu, '$1M')
        .replace(/,/gu, ' ')
        .replace(/\s+/gu, ' ')
        .trim();

      const formatIfValid = date => this.caseManualFormatUtcDateText(date) || '';
      const monthNumberByName = {
        jan: 1, january: 1,
        feb: 2, february: 2,
        mar: 3, march: 3,
        apr: 4, april: 4,
        may: 5,
        jun: 6, june: 6,
        jul: 7, july: 7,
        aug: 8, august: 8,
        sep: 9, sept: 9, september: 9,
        oct: 10, october: 10,
        nov: 11, november: 11,
        dec: 12, december: 12
      };

      if (/^\d{10,13}$/u.test(text)) {
        const timestamp = Number(text);
        if (Number.isFinite(timestamp)) {
          const formatted = formatIfValid(new Date(text.length >= 13 ? timestamp : timestamp * 1000));
          if (formatted) return formatted;
        }
      }

      const timezoneText = text.replace(/\s+UTC$/iu, 'Z');
      if (/Z$|GMT|[+-]\d{2}:?\d{2}$/iu.test(timezoneText)) {
        const parsed = new Date(timezoneText.includes('T') ? timezoneText : timezoneText.replace(/\s+/u, 'T'));
        const formatted = formatIfValid(parsed);
        if (formatted) return formatted;
      }

      const isoLikeMatch = text.match(
        /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2})(?::(\d{1,2}))?(?::(\d{1,2}(?:\.\d+)?))?\s*(AM|PM)?)?(?:\s*UTC)?$/iu
      );
      if (isoLikeMatch) {
        const formatted = formatIfValid(this.caseManualBuildUtcDateFromParts(
          isoLikeMatch[1],
          isoLikeMatch[2],
          isoLikeMatch[3],
          isoLikeMatch[4] || 0,
          isoLikeMatch[5] || 0,
          isoLikeMatch[6] || 0,
          isoLikeMatch[7] || ''
        ));
        if (formatted) return formatted;
      }

      const usLikeMatch = text.match(
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2})(?::(\d{1,2}))?(?::(\d{1,2}(?:\.\d+)?))?\s*(AM|PM)?)?(?:\s*UTC)?$/iu
      );
      if (usLikeMatch) {
        const formatted = formatIfValid(this.caseManualBuildUtcDateFromParts(
          usLikeMatch[3],
          usLikeMatch[1],
          usLikeMatch[2],
          usLikeMatch[4] || 0,
          usLikeMatch[5] || 0,
          usLikeMatch[6] || 0,
          usLikeMatch[7] || ''
        ));
        if (formatted) return formatted;
      }

      const namedMonthMatch = text.match(
        /^(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t|tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\.?\s+(\d{1,2})\s+(\d{4})(?:\s+(\d{1,2})(?::(\d{1,2}))?(?::(\d{1,2}(?:\.\d+)?))?\s*(AM|PM)?)?(?:\s*UTC)?$/iu
      );
      if (namedMonthMatch) {
        const monthKey = this.caseManualNormalizeText(namedMonthMatch[1]).replace(/\.$/u, '').toLowerCase();
        const monthNumber = monthNumberByName[monthKey];
        const formatted = formatIfValid(this.caseManualBuildUtcDateFromParts(
          namedMonthMatch[3],
          monthNumber,
          namedMonthMatch[2],
          namedMonthMatch[4] || 0,
          namedMonthMatch[5] || 0,
          namedMonthMatch[6] || 0,
          namedMonthMatch[7] || ''
        ));
        if (formatted) return formatted;
      }

      return original;
    },

    caseManualNormalizeAddressKey(value) {
      return this.caseManualNormalizeText(value).toLowerCase();
    },

    caseManualIsEvmHash(value) {
      return /^0x[0-9a-fA-F]{64}$/.test(this.caseManualNormalizeText(value));
    },

    caseManualIsFullTransactionHash(value) {
      const text = this.caseManualNormalizeText(value);
      return /^0x[0-9a-fA-F]{64}$/.test(text) || /^[0-9a-fA-F]{64}$/.test(text);
    },

    caseManualCanHydrateTransactionBundle(candidate = {}, familyOverride = '') {
      const family = familyOverride || this.caseManualInferNetworkFamily(candidate);
      const hash = this.caseManualNormalizeText(candidate.hash || candidate.transactionHash || candidate.txHash || '');
      if (!hash) return false;
      if (family === 'evm') return this.caseManualIsEvmHash(hash);
      if (family === 'tron') return this.caseManualIsFullTransactionHash(hash);
      return false;
    },

    caseManualIsLikelyTransactionHash(value) {
      const text = this.caseManualNormalizeText(value);
      return this.caseManualIsFullTransactionHash(text) || this.caseManualIsAbbreviatedTransactionHash(text);
    },

    caseManualIsAbbreviatedTransactionHash(value) {
      const text = this.caseManualNormalizeText(value).toLowerCase();
      return /\.\.\./u.test(text) && (/^0x[0-9a-f]+\.\.\.[0-9a-f]+$/u.test(text) || /^[0-9a-f]+\.\.\.[0-9a-f]+$/u.test(text));
    },

    caseManualHashMatchesAbbreviatedHash(fullHash = '', abbreviatedHash = '') {
      const full = this.caseManualNormalizeText(fullHash).toLowerCase();
      const short = this.caseManualNormalizeText(abbreviatedHash).toLowerCase();
      if (!full || !short || !this.caseManualIsAbbreviatedTransactionHash(short)) return false;
      const [prefix = '', suffix = ''] = short.split('...');
      if (!prefix || !suffix) return false;
      const strip0x = value => String(value || '').replace(/^0x/u, '');
      return (full.startsWith(prefix) && full.endsWith(suffix))
        || (strip0x(full).startsWith(strip0x(prefix)) && strip0x(full).endsWith(strip0x(suffix)));
    },

    caseManualTransactionHashesMatch(leftHash = '', rightHash = '') {
      const left = this.caseManualNormalizeText(leftHash).toLowerCase();
      const right = this.caseManualNormalizeText(rightHash).toLowerCase();
      if (!left || !right) return false;
      if (left === right) return true;
      if (this.caseManualIsAbbreviatedTransactionHash(left) && this.caseManualHashMatchesAbbreviatedHash(right, left)) return true;
      if (this.caseManualIsAbbreviatedTransactionHash(right) && this.caseManualHashMatchesAbbreviatedHash(left, right)) return true;
      return false;
    },

    caseManualFindDataRowIndexByHash(dataRows = [], hash = '') {
      const target = this.caseManualNormalizeText(hash);
      if (!target || !Array.isArray(dataRows)) return -1;
      return dataRows.findIndex(row => this.caseManualTransactionHashesMatch(row?.[CASE_MANUAL_START_COL.TX_HASH], target));
    },

    caseManualFindDataRowIndexByHashAndContext(dataRows = [], hash = '', context = {}) {
      const target = this.caseManualNormalizeText(hash);
      if (!target || !Array.isArray(dataRows)) return -1;
      const candidates = dataRows
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => this.caseManualTransactionHashesMatch(row?.[CASE_MANUAL_START_COL.TX_HASH], target));
      if (!candidates.length) return -1;

      const parentHash = this.caseManualNormalizeText(context.parentHash || context.fromHash || '');
      const pathLabel = this.caseManualNormalizeText(context.pathLabel || context.label || '').toLowerCase();
      const fromLabel = this.caseManualNormalizeText(context.fromLabel || context.parentLabel || context.sourcePathLabel || '').toLowerCase();
      const fromAddress = this.caseManualNormalizeAddressKey(context.fromAddress || context.from || '');
      const toAddress = this.caseManualNormalizeAddressKey(context.toAddress || context.to || context.targetAddress || '');
      const requireContext = context.requireContext === true;
      if (candidates.length === 1) {
        const only = candidates[0];
        const onlyParentHash = this.caseManualNormalizeText(only.row?.[CASE_MANUAL_START_COL.FROM_HASH]);
        const onlyPathLabel = this.caseManualNormalizeText(only.row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        const onlyFromLabel = this.caseManualNormalizeText(only.row?.[CASE_MANUAL_START_COL.FROM_LABEL]).toLowerCase();
        if (requireContext && parentHash && onlyParentHash && !this.caseManualTransactionHashesMatch(onlyParentHash, parentHash)) return -1;
        if (requireContext && pathLabel && onlyPathLabel && onlyPathLabel !== pathLabel) return -1;
        if (requireContext && fromLabel && onlyFromLabel && onlyFromLabel !== fromLabel) return -1;
        return only.index;
      }

      let narrowed = candidates;
      if (parentHash) {
        const parentMatches = narrowed.filter(({ row }) =>
          this.caseManualTransactionHashesMatch(row?.[CASE_MANUAL_START_COL.FROM_HASH], parentHash)
        );
        if (parentMatches.length) narrowed = parentMatches;
        else if (requireContext) return -1;
      }

      if (pathLabel) {
        const pathMatches = narrowed.filter(({ row }) =>
          this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase() === pathLabel
        );
        if (pathMatches.length) narrowed = pathMatches;
        else if (requireContext) return -1;
      }

      if (fromLabel) {
        const fromLabelMatches = narrowed.filter(({ row }) =>
          this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_LABEL]).toLowerCase() === fromLabel
        );
        if (fromLabelMatches.length) narrowed = fromLabelMatches;
        else if (requireContext) return -1;
      }

      if (fromAddress) {
        const fromMatches = narrowed.filter(({ row }) =>
          this.caseManualNormalizeAddressKey(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]) === fromAddress
        );
        if (fromMatches.length) narrowed = fromMatches;
        else if (requireContext && candidates.length > 1) return -1;
      }

      if (toAddress) {
        const toMatches = narrowed.filter(({ row }) =>
          this.caseManualNormalizeAddressKey(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]) === toAddress
        );
        if (toMatches.length) narrowed = toMatches;
        else if (requireContext && candidates.length > 1) return -1;
      }

      if (narrowed.length === 1) return narrowed[0].index;
      if (requireContext && (parentHash || pathLabel || fromLabel || fromAddress || toAddress)) return -1;
      return candidates.length === 1 ? candidates[0].index : -1;
    },

    caseManualFindDataRowIndexForCandidate(dataRows = [], candidate = {}, fallbackHash = '') {
      const row = Array.isArray(candidate?.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate?.row) ? candidate.row : []);
      const hash = this.caseManualNormalizeText(
        candidate?.hash || candidate?.txHash || candidate?.transactionHash || row?.[CASE_MANUAL_START_COL.TX_HASH] || fallbackHash
      );
      if (!hash) return -1;
      const parentHash = this.caseManualNormalizeText(
        candidate?.fromHash || candidate?.parentHash || row?.[CASE_MANUAL_START_COL.FROM_HASH] || ''
      );
      const pathLabel = this.caseManualNormalizeText(
        candidate?.pathLabel || candidate?.label || row?.[CASE_MANUAL_START_COL.PATH_LABEL] || ''
      );
      const fromLabel = this.caseManualNormalizeText(
        candidate?.fromLabel || candidate?.parentLabel || candidate?.sourcePathLabel || row?.[CASE_MANUAL_START_COL.FROM_LABEL] || ''
      );
      const fromAddress = candidate?.fromAddress || candidate?.from || row?.[CASE_MANUAL_START_COL.FROM_ADDRESS] || '';
      const toAddress = candidate?.toAddress || candidate?.targetAddress || candidate?.address || row?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '';
      const sameHashCandidates = Array.isArray(dataRows)
        ? dataRows
          .map((candidateRow, index) => ({ row: candidateRow, index }))
          .filter(({ row: candidateRow }) => this.caseManualTransactionHashesMatch(candidateRow?.[CASE_MANUAL_START_COL.TX_HASH], hash))
        : [];
      const duplicateHashCandidate = sameHashCandidates.length > 1;
      const rowContextMatches = (candidateRow = []) => {
        if (!candidateRow || !this.caseManualTransactionHashesMatch(candidateRow?.[CASE_MANUAL_START_COL.TX_HASH], hash)) return false;
        const candidateParent = this.caseManualNormalizeText(candidateRow?.[CASE_MANUAL_START_COL.FROM_HASH]);
        if (parentHash && candidateParent && !this.caseManualTransactionHashesMatch(candidateParent, parentHash)) return false;
        if (duplicateHashCandidate && parentHash && !candidateParent) return false;
        const candidateLabel = this.caseManualNormalizeText(candidateRow?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        if (pathLabel && candidateLabel && candidateLabel !== pathLabel.toLowerCase()) return false;
        if (duplicateHashCandidate && pathLabel && !candidateLabel) return false;
        const candidateFromLabel = this.caseManualNormalizeText(candidateRow?.[CASE_MANUAL_START_COL.FROM_LABEL]).toLowerCase();
        if (fromLabel && candidateFromLabel && candidateFromLabel !== fromLabel.toLowerCase()) return false;
        const candidateFromAddress = this.caseManualNormalizeAddressKey(candidateRow?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        const candidateToAddress = this.caseManualNormalizeAddressKey(candidateRow?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
        const wantedFromAddress = this.caseManualNormalizeAddressKey(fromAddress);
        const wantedToAddress = this.caseManualNormalizeAddressKey(toAddress);
        if (wantedFromAddress && candidateFromAddress && candidateFromAddress !== wantedFromAddress) return false;
        if (wantedToAddress && candidateToAddress && candidateToAddress !== wantedToAddress) return false;
        return true;
      };
      const spreadsheetRowNumber = Number(candidate?.spreadsheetRowNumber);
      const explicitIndexes = [
        candidate?.rowIndex,
        candidate?.startRow,
        candidate?.entryStartRow,
        candidate?.index,
        Number.isFinite(spreadsheetRowNumber) ? spreadsheetRowNumber - 2 : null,
        Number.isFinite(spreadsheetRowNumber) ? spreadsheetRowNumber - 1 : null,
      ]
        .map(value => Number(value))
        .filter(value => Number.isInteger(value));
      for (const index of explicitIndexes) {
        if (index >= 0 && index < dataRows.length && rowContextMatches(dataRows[index])) return index;
      }
      const withContext = this.caseManualFindDataRowIndexByHashAndContext(dataRows, hash, {
        parentHash,
        pathLabel,
        fromLabel,
        fromAddress,
        toAddress,
        requireContext: duplicateHashCandidate || Boolean(parentHash || pathLabel || fromLabel || fromAddress || toAddress),
      });
      if (withContext >= 0) return withContext;
      if (sameHashCandidates.length === 1) return sameHashCandidates[0].index;
      // Duplicate transaction hashes are legitimate separate path occurrences in
      // Manual Solve. Returning the first same-hash row here can corrupt the
      // selected branch's funds-followed / left-bounds allocation and cause later
      // visual children to attach to the wrong yellow transaction node.
      return -1;
    },

    caseManualFindDataRowIndexForGraftOutputRow(dataRows = [], outputRow = []) {
      const row = this.caseManualEnsureRowWidth(outputRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, outputRow?.length || 0));
      const hash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]);
      if (!hash || !this.caseManualIsLikelyTransactionHash(hash)) return -1;
      const parentHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_HASH]);
      const pathLabel = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]);
      const fromLabel = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_LABEL]);
      const fromAddress = row[CASE_MANUAL_START_COL.FROM_ADDRESS];
      const toAddress = row[CASE_MANUAL_START_COL.TO_ADDRESS];
      const contextual = this.caseManualFindDataRowIndexByHashAndContext(dataRows, hash, {
        parentHash,
        pathLabel,
        fromLabel,
        fromAddress,
        toAddress,
        requireContext: Boolean(parentHash || pathLabel || fromLabel),
      });
      if (contextual >= 0) return contextual;

      // Same transaction hash can legitimately appear as separate path instances
      // when two sibling branches exit through the same transaction. If Tool 88
      // supplied an explicit parent hash and no existing row has that parent, this
      // is a new occurrence, not an update to the first same-hash row.
      if (parentHash) return -1;
      return this.caseManualFindDataRowIndexByHash(dataRows, hash);
    },

    caseManualFindInsertIndexAfterEntryBlockAtIndex(dataRows = [], rowIndex = -1) {
      const startIndex = Number(rowIndex);
      if (!Array.isArray(dataRows) || !Number.isInteger(startIndex) || startIndex < 0 || startIndex >= dataRows.length) return dataRows.length;
      let insertAt = startIndex + 1;
      while (insertAt < dataRows.length && !this.caseManualNormalizeText(dataRows[insertAt]?.[CASE_MANUAL_START_COL.TX_HASH])) {
        insertAt += 1;
      }
      return insertAt;
    },

    caseManualResolveKnownTransactionHash(value = '', rows = []) {
      const text = this.caseManualNormalizeText(value);
      if (!text) return '';
      const sourceRows = Array.isArray(rows) ? rows : [];
      const lower = text.toLowerCase();
      const sourceHashes = sourceRows
        .map(row => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH] || row?.hash))
        .filter(Boolean);
      const exact = sourceHashes.find(hash => hash.toLowerCase() === lower);
      if (exact) return exact;
      const fullAlias = this.caseManualResolveFullTransactionHashAlias(
        text,
        this.caseManualCollectFullTransactionHashesFromRows(sourceRows)
      );
      if (fullAlias && fullAlias !== text) return fullAlias;
      const matches = sourceHashes
        .filter(hash => this.caseManualTransactionHashesMatch(hash, text))
        .filter((hash, index, list) => list.findIndex(item => item.toLowerCase() === hash.toLowerCase()) === index);
      if (matches.length !== 1) return text;
      const match = matches[0];
      if (this.caseManualIsFullTransactionHash(text) && this.caseManualIsAbbreviatedTransactionHash(match)) return text;
      if (this.caseManualIsAbbreviatedTransactionHash(text) && this.caseManualIsFullTransactionHash(match)) return match;
      return match;
    },

    caseManualShouldReplaceStoredTransactionHash(currentValue = '', candidateValue = '') {
      const current = this.caseManualNormalizeText(currentValue);
      const candidate = this.caseManualNormalizeText(candidateValue);
      if (!candidate || !this.caseManualIsLikelyTransactionHash(candidate)) return false;
      if (!current) return true;
      if (current.toLowerCase() === candidate.toLowerCase()) return false;
      if (this.caseManualIsAbbreviatedTransactionHash(current) && this.caseManualHashMatchesAbbreviatedHash(candidate, current)) return true;
      return false;
    },

    caseManualExtractHashTokensFromContinuesAs(value = '') {
      const text = this.caseManualNormalizeContinuesAsText(value);
      if (!/^Continues\s+as\b/iu.test(text)) return [];
      return text
        .replace(/^Continues\s+as\s*/iu, '')
        .split(',')
        .map(hash => this.caseManualNormalizeText(hash))
        .filter(Boolean);
    },

    caseManualCollectFullTransactionHashesFromRows(rows = []) {
      const fullHashes = [];
      const seen = new Set();
      const remember = value => {
        const text = this.caseManualNormalizeText(value);
        if (!text || !this.caseManualIsFullTransactionHash(text)) return;
        const key = text.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        fullHashes.push(text);
      };
      (Array.isArray(rows) ? rows : []).forEach(row => {
        if (typeof row === 'string') {
          remember(row);
          this.caseManualExtractHashTokensFromContinuesAs(row).forEach(remember);
          return;
        }
        if (Array.isArray(row)) {
          remember(row[CASE_MANUAL_START_COL.TX_HASH]);
          remember(row[CASE_MANUAL_START_COL.FROM_HASH]);
          this.caseManualExtractHashTokensFromContinuesAs(row[CASE_MANUAL_START_COL.CONTINUES_AS]).forEach(remember);
          return;
        }
        if (row && typeof row === 'object') {
          remember(row.hash || row.txHash || row.transactionHash || row.transaction_hash);
          remember(row.fromHash || row.from_hash || row.parentHash || row.parent_hash);
          this.caseManualExtractHashTokensFromContinuesAs(row.continuesAs || row.continues_as || row.continues).forEach(remember);
        }
      });
      return fullHashes;
    },

    caseManualResolveFullTransactionHashAlias(value = '', fullHashes = []) {
      const text = this.caseManualNormalizeText(value);
      if (!text) return '';
      if (this.caseManualIsFullTransactionHash(text)) return text;
      if (!this.caseManualIsAbbreviatedTransactionHash(text)) return text;
      const matches = (Array.isArray(fullHashes) ? fullHashes : [])
        .map(hash => this.caseManualNormalizeText(hash))
        .filter(hash => hash && this.caseManualIsFullTransactionHash(hash) && this.caseManualHashMatchesAbbreviatedHash(hash, text));
      const uniqueMatches = matches.filter((hash, index, list) => list.findIndex(item => item.toLowerCase() === hash.toLowerCase()) === index);
      return uniqueMatches.length === 1 ? uniqueMatches[0] : text;
    },

    caseManualUpgradeContinuesAsHashes(value = '', fullHashes = []) {
      const normalized = this.caseManualNormalizeContinuesAsText(value);
      if (!/^Continues\s+as\b/iu.test(normalized)) return normalized;
      const upgraded = this.caseManualExtractHashTokensFromContinuesAs(normalized)
        .map(hash => this.caseManualResolveFullTransactionHashAlias(hash, fullHashes))
        .filter(Boolean)
        .filter((hash, index, list) => list.findIndex(item => item.toLowerCase() === hash.toLowerCase()) === index);
      return upgraded.length ? `Continues as ${upgraded.join(', ')}` : 'Path ends.';
    },

    caseManualUpgradeHashReferencesInRow(row = [], fullHashes = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const txHash = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TX_HASH]);
      const fromHash = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FROM_HASH]);
      if (txHash) normalized[CASE_MANUAL_START_COL.TX_HASH] = this.caseManualResolveFullTransactionHashAlias(txHash, fullHashes);
      if (fromHash) normalized[CASE_MANUAL_START_COL.FROM_HASH] = this.caseManualResolveFullTransactionHashAlias(fromHash, fullHashes);
      normalized[CASE_MANUAL_START_COL.CONTINUES_AS] = this.caseManualUpgradeContinuesAsHashes(normalized[CASE_MANUAL_START_COL.CONTINUES_AS], fullHashes);
      return normalized;
    },

    caseManualUpgradeHashReferencesInRows(rows = [], fullHashes = []) {
      if (!Array.isArray(rows) || !Array.isArray(fullHashes) || !fullHashes.length) return rows;
      rows.forEach((row, index) => {
        rows[index] = this.caseManualUpgradeHashReferencesInRow(row, fullHashes);
      });
      return rows;
    },

    caseManualShorten(value, start = 10, end = 8) {
      const textValue = this.caseManualNormalizeText(value);
      if (!textValue) return '';
      if (typeof this.shortenMiddle === 'function') return this.shortenMiddle(textValue, start, end);
      if (textValue.length <= start + end + 3) return textValue;
      return `${textValue.slice(0, start)}...${textValue.slice(-end)}`;
    },

    caseManualIsHeaderRow(row = []) {
      const joined = (Array.isArray(row) ? row.slice(0, 8) : []).map(value => this.caseManualNormalizeText(value).toLowerCase()).join('|');
      return joined.includes('transaction hash') || joined.includes('path label|transaction');
    },

    caseManualNativeSymbolForChain(chainId = '', chainName = '') {
      const id = this.caseManualNormalizeText(chainId);
      const name = this.caseManualNormalizeText(chainName).toLowerCase();
      if (name.includes('tron')) return 'TRX';
      if (name.includes('bitcoin') || name === 'btc' || id === 'btc') return 'BTC';
      if (name.includes('polygon') || id === '137') return 'MATIC';
      if (name.includes('bsc') || name.includes('binance') || id === '56') return 'BNB';
      if (name.includes('avalanche') || id === '43114') return 'AVAX';
      if (name.includes('arbitrum') || name.includes('optimism') || name.includes('base') || name.includes('ethereum') || id === '1' || id === '42161' || id === '10' || id === '8453') return 'ETH';
      return 'ETH';
    },

    caseManualParseNumericAmount(value) {
      const textValue = this.caseManualNormalizeText(value).replace(/,/g, '');
      const match = textValue.match(/-?\d+(?:\.\d+)?/u);
      if (!match) return NaN;
      const parsed = Number(match[0]);
      return Number.isFinite(parsed) ? parsed : NaN;
    },

    caseManualAmountValuesApproximatelyEqual(leftValue = '', rightValue = '', relativeTolerance = 1e-8) {
      const left = this.caseManualParseNumericAmount(leftValue);
      const right = this.caseManualParseNumericAmount(rightValue);
      if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
      const tolerance = Math.max(0.000001, Math.abs(right) * relativeTolerance, Math.abs(left) * relativeTolerance);
      return Math.abs(left - right) <= tolerance;
    },

    caseManualRowsFundsFollowedApproximatelyMatch(leftRow = [], rightRow = []) {
      return this.caseManualAmountValuesApproximatelyEqual(
        leftRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED],
        rightRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]
      );
    },

    caseManualOccurrenceAmountFromRowLike(row = {}) {
      const text = this.caseManualNormalizeText(
        row?.fundsFollowed ||
        row?.funds_followed ||
        row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] ||
        ''
      ).replace(/,/g, '');
      const amount = this.caseManualParseNumericAmount(text);
      return Number.isFinite(amount) ? amount : NaN;
    },

    caseManualOccurrenceAmountsApproximatelyEqual(leftAmount, rightAmount, relativeTolerance = 1e-6) {
      const left = Number(leftAmount);
      const right = Number(rightAmount);
      if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
      const tolerance = Math.max(0.000001, Math.abs(left) * relativeTolerance, Math.abs(right) * relativeTolerance);
      return Math.abs(left - right) <= tolerance;
    },

    caseManualBuildOccurrenceContinuationCoverage(rawItems = []) {
      const sourceItems = Array.isArray(rawItems) ? rawItems : [];
      const normalizeHash = value => this.caseManualNormalizeText(value).toLowerCase();
      const normalizeAddress = value => this.caseManualNormalizeAddressKey(value);
      const itemFromAddressKeys = item => {
        const keys = [];
        const remember = value => {
          const key = normalizeAddress(value);
          if (key && !keys.includes(key)) keys.push(key);
        };
        remember(item?.fromAddressKey);
        remember(item?.fromAddress);
        remember(item?.row?.fromAddress);
        remember(item?.row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        remember(item?.candidate?.fromAddress);
        remember(item?.candidate?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        [item?.btcInputAddresses, item?.row?.btcInputAddresses, item?.candidate?.btcInputAddresses].forEach(source => {
          (Array.isArray(source) ? source : []).forEach(input => {
            if (Array.isArray(input)) remember(input[CASE_MANUAL_START_COL.FROM_ADDRESS]);
            else if (input && typeof input === 'object') {
              remember(input.fromAddress || input.from_address || input.address || input.inputAddress || input.input_address);
            } else remember(input);
          });
        });
        return keys;
      };
      const itemHasFromAddressKey = (item, addressKey = '') => Boolean(addressKey && (item?.fromAddressKeys || []).includes(addressKey));
      const rowIndexOf = item => {
        const direct = Number(item?.rowIndex);
        if (Number.isFinite(direct)) return direct;
        const fallback = Number(item?.index);
        return Number.isFinite(fallback) ? fallback : 0;
      };
      const itemHashMatches = (left = '', right = '') => {
        const leftKey = normalizeHash(left);
        const rightKey = normalizeHash(right);
        return Boolean(leftKey && rightKey && (leftKey === rightKey || this.caseManualTransactionHashesMatch(leftKey, rightKey)));
      };
      const pathDescendsFrom = (childLabel = '', parentLabel = '') => {
        const child = this.caseManualNormalizeText(childLabel).toLowerCase();
        const parent = this.caseManualNormalizeText(parentLabel).toLowerCase();
        return Boolean(child && parent && child !== parent && child.startsWith(parent));
      };
      const itemAmount = item => this.caseManualOccurrenceAmountFromRowLike(item?.row || item?.candidate || item || {});
      const itemChildHashes = item => {
        const direct = Array.isArray(item?.childHashes) ? item.childHashes : [];
        const fromRow = this.caseManualExtractHashTokensFromContinuesAs(
          item?.row?.continuesAs ||
          item?.row?.[CASE_MANUAL_START_COL.CONTINUES_AS] ||
          item?.candidate?.continuesAs ||
          item?.candidate?.[CASE_MANUAL_START_COL.CONTINUES_AS] ||
          ''
        );
        return [...direct, ...fromRow]
          .map(normalizeHash)
          .filter(Boolean)
          .filter((hash, index, list) => list.findIndex(candidate => itemHashMatches(candidate, hash)) === index);
      };
      const normalizedItems = sourceItems.map((item, fallbackIndex) => ({
        ...item,
        rowIndex: rowIndexOf(item ?? { index: fallbackIndex }),
        occurrenceKey: item?.occurrenceKey || `occ-${fallbackIndex}`,
        hashKey: normalizeHash(item?.hashKey || item?.hash || item?.row?.hash || item?.row?.[CASE_MANUAL_START_COL.TX_HASH]),
        parentHashKey: normalizeHash(item?.parentHashKey || item?.fromHash || item?.row?.fromHash || item?.row?.[CASE_MANUAL_START_COL.FROM_HASH]),
        pathLabel: this.caseManualNormalizeText(item?.pathLabel || item?.row?.pathLabel || item?.row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase(),
        fromAddressKey: normalizeAddress(item?.fromAddressKey || item?.fromAddress || item?.row?.fromAddress || item?.row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]),
        fromAddressKeys: itemFromAddressKeys(item),
        toAddressKey: normalizeAddress(item?.toAddressKey || item?.toAddress || item?.row?.toAddress || item?.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
        childHashes: itemChildHashes(item),
      }));
      const childrenByParent = new Map();
      const parentByChild = new Map();
      const exactParentKeys = new Set();
      const parentCoverage = new Map();
      const combinationLimit = 14;
      const eligibleChildrenForParent = parent => {
        const allowedHashes = itemChildHashes(parent);
        if (!parent?.hashKey || !allowedHashes.length) return [];
        return normalizedItems
          .filter(child => child && child.occurrenceKey !== parent.occurrenceKey)
          .filter(child => child.hashKey && allowedHashes.some(hash => itemHashMatches(child.hashKey, hash)))
          .filter(child => child.parentHashKey && itemHashMatches(child.parentHashKey, parent.hashKey))
          .filter(child => {
            const childHasParentToInput = itemHasFromAddressKey(child, parent.toAddressKey);
            if (parent.toAddressKey && (child.fromAddressKeys || []).length && !childHasParentToInput) return false;
            return rowIndexOf(child) > rowIndexOf(parent) || pathDescendsFrom(child.pathLabel, parent.pathLabel) || childHasParentToInput;
          })
          .sort((left, right) => rowIndexOf(left) - rowIndexOf(right));
      };
      const subsetScoreForParent = (parent, subset = []) => subset.reduce((score, child) => {
        let nextScore = score;
        if (pathDescendsFrom(child.pathLabel, parent.pathLabel)) nextScore += 1000;
        if (itemHasFromAddressKey(child, parent.toAddressKey)) nextScore += 160;
        if (rowIndexOf(child) > rowIndexOf(parent)) nextScore += 40;
        nextScore -= Math.abs(rowIndexOf(child) - rowIndexOf(parent)) / 1000;
        return nextScore;
      }, 0);
      const chooseCoverageSubset = parent => {
        const targetAmount = itemAmount(parent);
        if (!Number.isFinite(targetAmount) || targetAmount <= 0) return null;
        const eligible = eligibleChildrenForParent(parent)
          .map(child => ({ child, amount: itemAmount(child) }))
          .filter(record => Number.isFinite(record.amount) && record.amount > 0)
          .slice(0, combinationLimit);
        if (!eligible.length) return null;
        let best = null;
        const tolerance = Math.max(0.000001, Math.abs(targetAmount) * 1e-6);
        const visit = (offset, chosen, sum) => {
          const diff = Math.abs(sum - targetAmount);
          if (chosen.length && diff <= tolerance) {
            const subset = chosen.map(record => record.child);
            const score = subsetScoreForParent(parent, subset) - (subset.length * 0.0001);
            if (!best || diff < best.diff - tolerance || (Math.abs(diff - best.diff) <= tolerance && score > best.score)) {
              best = { subset, diff, score, sum };
            }
          }
          if (offset >= eligible.length || chosen.length >= 8) return;
          for (let index = offset; index < eligible.length; index += 1) {
            const nextSum = sum + eligible[index].amount;
            if (nextSum > targetAmount + tolerance && eligible[index].amount > targetAmount + tolerance) continue;
            visit(index + 1, [...chosen, eligible[index]], nextSum);
          }
        };
        visit(0, [], 0);
        if (best) return best;
        const single = eligible
          .map(record => ({ subset: [record.child], diff: Math.abs(record.amount - targetAmount), score: subsetScoreForParent(parent, [record.child]), sum: record.amount }))
          .filter(record => record.diff <= tolerance)
          .sort((left, right) => left.diff - right.diff || right.score - left.score)[0];
        return single || null;
      };
      normalizedItems.forEach(parent => {
        if (!parent?.childHashes?.length) return;
        const coverage = chooseCoverageSubset(parent);
        if (!coverage?.subset?.length) return;
        childrenByParent.set(parent.occurrenceKey, coverage.subset);
        exactParentKeys.add(parent.occurrenceKey);
        parentCoverage.set(parent.occurrenceKey, {
          sum: coverage.sum,
          diff: coverage.diff,
          childHashes: coverage.subset.map(child => child.hashKey).filter(Boolean),
          childOccurrenceKeys: coverage.subset.map(child => child.occurrenceKey).filter(Boolean),
        });
        coverage.subset.forEach(child => {
          const existingParent = parentByChild.get(child.occurrenceKey);
          if (!existingParent) {
            parentByChild.set(child.occurrenceKey, parent);
            return;
          }
          const currentScore = subsetScoreForParent(existingParent, [child]);
          const nextScore = subsetScoreForParent(parent, [child]);
          if (nextScore > currentScore) parentByChild.set(child.occurrenceKey, parent);
        });
      });
      return { childrenByParent, parentByChild, exactParentKeys, parentCoverage };
    },

    caseManualNumericTextLooksZero(value = '') {
      const textValue = this.caseManualNormalizeText(value);
      if (!textValue) return false;
      const parsed = this.caseManualParseNumericAmount(textValue);
      return Number.isFinite(parsed) && Math.abs(parsed) <= 1e-12;
    },

    caseManualNumericTextLooksPositive(value = '') {
      const parsed = this.caseManualParseNumericAmount(value);
      return Number.isFinite(parsed) && parsed > 1e-12;
    },

    caseManualPickPreferredNumericText(primary = '', fallback = '') {
      const first = this.caseManualNormalizeText(primary);
      const second = this.caseManualNormalizeText(fallback);
      if (this.caseManualNumericTextLooksZero(first) && this.caseManualNumericTextLooksPositive(second)) return second;
      return first || second;
    },

    caseManualPriorBalanceTextLooksLikeCurrentTransfer(priorText = '', row = [], context = {}) {
      const prior = this.caseManualParseNumericAmount(priorText);
      if (!Number.isFinite(prior) || prior <= 0) return false;
      const amount = this.caseManualParseNumericAmount(context.amount || row?.[CASE_MANUAL_START_COL.AMOUNT]);
      const funds = this.caseManualParseNumericAmount(context.fundsFollowed || row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
      const closeEnough = (left, right) => {
        if (!Number.isFinite(left) || !Number.isFinite(right) || right <= 0) return false;
        const tolerance = Math.max(1e-8, Math.abs(right) * 0.00001);
        return Math.abs(left - right) <= tolerance;
      };
      return closeEnough(prior, amount) || closeEnough(prior, funds);
    },

    caseManualPickAccountPriorBalanceText(row = [], hydrated = {}, context = {}) {
      const explicitHydrated = this.caseManualNormalizeText(
        hydrated?.caseBuddyPriorBalance
          ?? hydrated?.priorBalanceBeforeTransaction
          ?? hydrated?.prior_balance_before_transaction
          ?? hydrated?.actualPriorBalanceAmount
          ?? hydrated?.accountPriorBalance
          ?? hydrated?.addressBalanceBeforeTransaction
          ?? hydrated?.balanceBeforeTransaction
          ?? hydrated?.balance_before_transaction
          ?? hydrated?.assetBalanceBefore
          ?? hydrated?.asset_balance_before
          ?? hydrated?.balanceBefore
          ?? hydrated?.balance_before
          ?? ''
      );
      const existing = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PRIOR_BALANCE]);
      if (explicitHydrated) {
        // A Tool 88/provider reconstructed prior balance is more trustworthy than
        // stale Column N text from an earlier hydration/export. Column N must not
        // keep an old amount/funds-followed fallback once a real account prior
        // balance is available.
        return explicitHydrated;
      }
      if (this.caseManualPriorBalanceTextLooksLikeCurrentTransfer(existing, row, context)) return '';
      return existing;
    },

    caseManualFormatFixedNumber(value, decimals = 2) {
      const number = typeof value === 'number' ? value : this.caseManualParseNumericAmount(value);
      if (!Number.isFinite(number)) return '';
      return number.toFixed(decimals);
    },

    caseManualFormatLeftBoundsNumber(value = '', token = '', options = {}) {
      const number = this.caseManualParseNumericAmount(value);
      if (!Number.isFinite(number)) return this.caseManualNormalizeText(value);
      const tokenText = this.caseManualNormalizeText(token).toUpperCase();
      const defaultDecimals = tokenText === 'BTC' ? 8 : 2;
      const decimals = this.caseManualAllocationDecimalsForValue(value, tokenText, options);
      if (options.preserveSourcePrecision === true || decimals > defaultDecimals) {
        return this.caseManualFormatPreciseAllocationNumber(value, tokenText, {
          preserveSourcePrecision: options.preserveSourcePrecision === true,
          useGrouping: false,
          minimumFractionDigits: defaultDecimals,
        });
      }
      return this.caseManualFormatFixedNumber(number, defaultDecimals);
    },

    caseManualFormatPriorBalanceNumber(value = '', token = '') {
      const number = this.caseManualParseNumericAmount(value);
      if (!Number.isFinite(number)) return this.caseManualNormalizeText(value);
      const tokenText = this.caseManualNormalizeText(token).toUpperCase();
      const decimals = tokenText === 'BTC' ? 8 : 4;
      return this.caseManualFormatFixedNumber(number, decimals);
    },

    caseManualFormatFundsFollowedCell(value = '', fallbackToken = '', options = {}) {
      const textValue = this.caseManualNormalizeText(value);
      const amount = this.caseManualParseNumericAmount(textValue);
      if (!Number.isFinite(amount)) return textValue;
      const explicitTokenMatch = textValue.match(/\b([A-Za-z][A-Za-z0-9._/-]{1,15})\b\s*$/u);
      const token = this.caseManualNormalizeText(explicitTokenMatch?.[1] || fallbackToken).toUpperCase();
      const defaultDecimals = token === 'BTC' ? 8 : 2;
      const decimals = this.caseManualAllocationDecimalsForValue(textValue, token, options);
      const amountText = options.preserveSourcePrecision === true || decimals > defaultDecimals
        ? this.caseManualFormatPreciseAllocationNumber(textValue, token, {
            preserveSourcePrecision: options.preserveSourcePrecision === true,
            useGrouping: true,
          })
        : amount.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: defaultDecimals
          });
      return `${amountText}${token ? ` ${token}` : ''}`.trim();
    },

    caseManualFormatAmount(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return '';
      const abs = Math.abs(number);
      const fixed = number.toFixed(abs >= 100 ? 2 : abs >= 1 ? 6 : 10);
      return fixed.replace(/(\.\d*?[1-9])0+$/u, '$1').replace(/\.0+$/u, '');
    },

    caseManualStablecoinUsesTwoDecimals(token = '') {
      return /^(?:USDT|USDC|USDD|DAI)$/iu.test(this.caseManualNormalizeText(token));
    },

    caseManualAllocationPrecisionCapForToken(token = '') {
      const tokenText = this.caseManualNormalizeText(token).toUpperCase();
      if (tokenText === 'BTC') return 8;
      if (/^(?:USDT|USDC|USDD|BUSD|TUSD|FDUSD|PYUSD|USDP|GUSD)$/u.test(tokenText)) return 6;
      return 12;
    },

    caseManualMeaningfulFractionDigits(value = '', maxDecimals = 12) {
      const numericText = this.caseManualExtractDecimalAmountText(value);
      if (!numericText) return 0;
      const unsigned = numericText.replace(/^-/, '');
      const fraction = (unsigned.split('.')[1] || '').slice(0, Math.max(0, maxDecimals));
      return fraction.replace(/0+$/u, '').length;
    },

    caseManualAllocationDecimalsForValue(value = '', token = '', options = {}) {
      const tokenText = this.caseManualNormalizeText(token).toUpperCase();
      const defaultDecimals = tokenText === 'BTC' ? 8 : 2;
      const cap = this.caseManualAllocationPrecisionCapForToken(tokenText);
      const meaningfulDigits = this.caseManualMeaningfulFractionDigits(value, cap);
      if (options.preserveSourcePrecision === true) {
        return Math.min(cap, Math.max(defaultDecimals, meaningfulDigits));
      }
      const parsed = this.caseManualParseNumericAmount(value);
      if (Number.isFinite(parsed) && Math.abs(parsed) > 1e-12) {
        const truncated = this.caseManualTruncateDecimalAmountText(value, defaultDecimals);
        if (this.caseManualTruncatedAmountIsZero(truncated)) {
          return Math.min(cap, Math.max(defaultDecimals, meaningfulDigits, this.caseManualMinimumNonZeroDecimals(value, defaultDecimals)));
        }
      }
      return defaultDecimals;
    },

    caseManualTrimFormattedFractionZeros(value = '', minimumFractionDigits = 0) {
      const text = this.caseManualNormalizeText(value);
      if (!text || !text.includes('.')) return text;
      const [whole, fraction = ''] = text.split('.');
      const minimum = Math.max(0, Number(minimumFractionDigits) || 0);
      let nextFraction = fraction;
      while (nextFraction.length > minimum && nextFraction.endsWith('0')) nextFraction = nextFraction.slice(0, -1);
      return nextFraction ? `${whole}.${nextFraction}` : whole;
    },

    caseManualFormatPreciseAllocationNumber(value = '', token = '', options = {}) {
      const number = this.caseManualParseNumericAmount(value);
      if (!Number.isFinite(number)) return this.caseManualNormalizeText(value);
      const decimals = this.caseManualAllocationDecimalsForValue(value, token, {
        preserveSourcePrecision: options.preserveSourcePrecision === true,
      });
      const normalizedNumber = Math.abs(number) <= 1e-12 ? 0 : number;
      const fixed = normalizedNumber.toFixed(decimals);
      const trimmed = this.caseManualTrimFormattedFractionZeros(fixed, options.minimumFractionDigits || 0);
      if (options.useGrouping === false) return trimmed;
      const sign = trimmed.startsWith('-') ? '-' : '';
      const unsigned = trimmed.replace(/^-/, '');
      const [whole = '0', fraction = ''] = unsigned.split('.');
      const groupedWhole = this.caseManualAddCommasToWholeNumber(whole);
      return `${sign}${groupedWhole}${fraction ? `.${fraction}` : ''}`;
    },

    caseManualRowHasExplicitReverseAllocationEvidence(row = []) {
      const source = Array.isArray(row) ? row : [];
      const text = [
        source[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        source[CASE_MANUAL_START_COL.CASE_NOTES],
        source[CASE_MANUAL_START_COL.HOP_LOGIC],
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      return /(?:direction\s*=\s*reverse|tool\s*88\s*:\s*reverse\s+(?:fifo|lifo|next\s+chronological)|reverse[-_\s]*(?:fifo|lifo|source|swap)|prior\s+source\s+(?:transfer|output))/iu.test(text);
    },

    caseManualExtractReverseBtcAllocationFromNotes(row = []) {
      const normalized = this.caseManualEnsureRowWidth(
        row,
        Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)
      );
      if (this.caseManualRowNetworkFamily(normalized) !== 'btc') return null;
      const text = [
        normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        normalized[CASE_MANUAL_START_COL.CASE_NOTES],
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join('\n');
      if (!text || !/(?:Tool\s*88\s+BTC\s+reverse\s+input|Followed\s+BTC\s+range)/iu.test(text)) return null;

      let allocation = null;
      const rangePattern = /Followed\s+BTC\s+range:\s*sats\s+([\d,]+)\s+to\s+([\d,]+)\s+of\s+this\s+output/giu;
      let rangeMatch = rangePattern.exec(text);
      while (rangeMatch) {
        const start = Number(String(rangeMatch[1] || '').replace(/,/gu, ''));
        const end = Number(String(rangeMatch[2] || '').replace(/,/gu, ''));
        if (Number.isSafeInteger(start) && Number.isSafeInteger(end) && start >= 0 && end > start) {
          allocation = {
            followedSatoshis: end - start,
            leftBoundsSatoshis: start,
            source: 'satoshi-range',
          };
        }
        rangeMatch = rangePattern.exec(text);
      }

      if (!allocation) {
        const decimalPattern = /Tool\s*88\s+BTC\s+reverse\s+input:[^\n]*?\bfollowed\s+([0-9][\d,]*(?:\.\d+)?)\s*BTC\s+from\s+source\s+offset\s+([0-9][\d,]*(?:\.\d+)?)\s*BTC/giu;
        let decimalMatch = decimalPattern.exec(text);
        while (decimalMatch) {
          const followed = Number(String(decimalMatch[1] || '').replace(/,/gu, ''));
          const leftBounds = Number(String(decimalMatch[2] || '').replace(/,/gu, ''));
          const followedSatoshis = Math.round(followed * 100000000);
          const leftBoundsSatoshis = Math.round(leftBounds * 100000000);
          if (Number.isSafeInteger(followedSatoshis) && followedSatoshis > 0 && Number.isSafeInteger(leftBoundsSatoshis) && leftBoundsSatoshis >= 0) {
            allocation = {
              followedSatoshis,
              leftBoundsSatoshis,
              source: 'btc-decimal-note',
            };
          }
          decimalMatch = decimalPattern.exec(text);
        }
      }

      if (!allocation) return null;
      let inputValueMatch = null;
      const inputValuePattern = /input_value_satoshis\s*=\s*(\d+)/giu;
      let nextInputValueMatch = inputValuePattern.exec(text);
      while (nextInputValueMatch) {
        inputValueMatch = nextInputValueMatch;
        nextInputValueMatch = inputValuePattern.exec(text);
      }
      const inputValueSatoshis = Number(String(inputValueMatch?.[1] || ''));
      return {
        ...allocation,
        inputValueSatoshis: Number.isSafeInteger(inputValueSatoshis) && inputValueSatoshis > 0
          ? inputValueSatoshis
          : 0,
      };
    },

    caseManualRepairReverseBtcAllocationFromNotes(row = []) {
      const normalized = this.caseManualEnsureRowWidth(
        row,
        Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)
      );
      const allocation = this.caseManualExtractReverseBtcAllocationFromNotes(normalized);
      if (!allocation) return normalized;

      const currentFunds = this.caseManualParseNumericAmount(
        normalized[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]
      );
      const currentFundsSatoshis = Number.isFinite(currentFunds)
        ? Math.round(currentFunds * 100000000)
        : 0;
      const expectedFundsSatoshis = Math.max(0, Number(allocation.followedSatoshis) || 0);
      const currentMatchesExpected = currentFundsSatoshis > 0
        && Math.abs(currentFundsSatoshis - expectedFundsSatoshis) <= 1;
      const matchesInflatedInput = allocation.inputValueSatoshis > 0
        && Math.abs(currentFundsSatoshis - allocation.inputValueSatoshis) <= 1
        && Math.abs(allocation.inputValueSatoshis - expectedFundsSatoshis) > 1;
      const amount = this.caseManualParseNumericAmount(normalized[CASE_MANUAL_START_COL.AMOUNT]);
      const leftBounds = this.caseManualParseNumericAmount(
        normalized[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC]
          || this.caseManualFirstLine(normalized[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY])
      );
      const impossibleAllocationSpan = Number.isFinite(currentFunds)
        && currentFunds > 0
        && Number.isFinite(amount)
        && amount > 0
        && Number.isFinite(leftBounds)
        && leftBounds >= 0
        && currentFunds + leftBounds > amount + 0.00000001;
      const shouldRepairFunds = currentFundsSatoshis <= 0
        || matchesInflatedInput
        || (!currentMatchesExpected && impossibleAllocationSpan);
      if (!shouldRepairFunds && !currentMatchesExpected) return normalized;

      const followedBtc = (expectedFundsSatoshis / 100000000).toFixed(8);
      const leftBoundsBtc = (Math.max(0, Number(allocation.leftBoundsSatoshis) || 0) / 100000000).toFixed(8);
      normalized[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = this.caseManualFormatFundsFollowedCell(
        `${followedBtc} BTC`,
        'BTC',
        { preserveSourcePrecision: true }
      ) || `${followedBtc} BTC`;
      normalized[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = this.caseManualFormatLeftBoundsDisplayValue(
        '',
        leftBoundsBtc,
        'BTC',
        { preserveSourcePrecision: true }
      ) || leftBoundsBtc;
      normalized[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = this.caseManualBuildWayFinderLeftBoundsDisplay(
        normalized,
        { preserveSourcePrecision: true }
      );
      return normalized;
    },

    caseManualExtractDecimalAmountText(value = '') {
      const text = this.caseManualNormalizeText(value).replace(/,/gu, '');
      if (!text) return '';
      const match = text.match(/-?(?:\d+(?:\.\d*)?|\.\d+)(?:e[-+]?\d+)?/iu);
      if (!match) return '';
      const numericText = match[0];
      if (/e/iu.test(numericText)) {
        const number = Number(numericText);
        return Number.isFinite(number) ? number.toFixed(18).replace(/0+$/u, '').replace(/\.$/u, '') : '';
      }
      return numericText;
    },

    caseManualTruncateDecimalAmountText(value = '', decimals = 2) {
      const numericText = this.caseManualExtractDecimalAmountText(value);
      if (!numericText) return '';
      const negative = /^-/u.test(numericText);
      const unsigned = numericText.replace(/^-/, '');
      const [rawInteger = '0', rawFraction = ''] = unsigned.split('.');
      const integer = (rawInteger || '0').replace(/^0+(?=\d)/u, '') || '0';
      const fraction = `${rawFraction}${'0'.repeat(Math.max(0, decimals))}`.slice(0, Math.max(0, decimals));
      return `${negative ? '-' : ''}${integer}${decimals > 0 ? `.${fraction}` : ''}`;
    },

    caseManualTruncatedAmountIsZero(formatted = '') {
      return /^-?0(?:\.0+)?$/u.test(this.caseManualNormalizeText(formatted).replace(/,/gu, ''));
    },

    caseManualMinimumNonZeroDecimals(value = '', defaultDecimals = 4) {
      const numericText = this.caseManualExtractDecimalAmountText(value);
      if (!numericText) return defaultDecimals;
      const unsigned = numericText.replace(/^-/, '');
      const [integer = '0', fraction = ''] = unsigned.split('.');
      if (/[1-9]/u.test(integer.replace(/^0+/u, ''))) return defaultDecimals;
      const firstNonZero = fraction.search(/[1-9]/u);
      if (firstNonZero < 0) return defaultDecimals;
      return Math.max(defaultDecimals, 8, firstNonZero + 1);
    },

    caseManualAddThousandsSeparators(value = '') {
      const text = this.caseManualNormalizeText(value);
      if (!text) return '';
      const negative = text.startsWith('-');
      const unsigned = negative ? text.slice(1) : text;
      const [integer = '0', fraction = ''] = unsigned.split('.');
      const grouped = (integer || '0').replace(/\B(?=(\d{3})+(?!\d))/gu, ',');
      return `${negative ? '-' : ''}${grouped}${fraction !== '' ? `.${fraction}` : ''}`;
    },

    caseManualTransactionAmountDecimalsForToken(token = '', value = '') {
      const defaultDecimals = this.caseManualStablecoinUsesTwoDecimals(token) ? 2 : 4;
      const truncated = this.caseManualTruncateDecimalAmountText(value, defaultDecimals);
      const numericText = this.caseManualExtractDecimalAmountText(value);
      const hasNonZeroValue = /[1-9]/u.test(numericText.replace(/[-.]/gu, ''));
      if (hasNonZeroValue && this.caseManualTruncatedAmountIsZero(truncated)) {
        return this.caseManualMinimumNonZeroDecimals(value, defaultDecimals);
      }
      return defaultDecimals;
    },

    caseManualFormatTransactionAmountDisplay(value = '', token = '', options = {}) {
      const numericText = this.caseManualExtractDecimalAmountText(value);
      if (!numericText) return this.caseManualNormalizeText(value);
      const tokenText = this.caseManualNormalizeText(token).toUpperCase();
      const decimals = Number.isInteger(options.decimals)
        ? Math.max(0, options.decimals)
        : this.caseManualTransactionAmountDecimalsForToken(tokenText, numericText);
      const truncated = this.caseManualTruncateDecimalAmountText(numericText, decimals);
      const grouped = this.caseManualAddThousandsSeparators(truncated);
      if (options.includeToken === false || !tokenText) return grouped;
      return `${grouped} ${tokenText}`.trim();
    },

    caseManualFormatNumberWithCommas(value, options = {}) {
      const number = Number(value);
      if (!Number.isFinite(number)) return '';
      const decimals = Number.isInteger(options.decimals) ? options.decimals : null;
      const minimumFractionDigits = Number.isInteger(options.minimumFractionDigits)
        ? options.minimumFractionDigits
        : (decimals !== null ? decimals : 0);
      const maximumFractionDigits = Number.isInteger(options.maximumFractionDigits)
        ? options.maximumFractionDigits
        : (decimals !== null ? decimals : 6);
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits,
        maximumFractionDigits,
        useGrouping: options.useGrouping !== false
      }).format(number);
    },

    caseManualSplitAmountAndToken(value = '', fallbackToken = '') {
      const textValue = this.caseManualNormalizeText(value);
      const amount = this.caseManualParseNumericAmount(textValue);
      let token = this.caseManualTokenFromFundsFollowed(textValue) || this.caseManualNormalizeText(fallbackToken).toUpperCase();
      if (token && /^e$/iu.test(token)) token = '';
      return { amount, token };
    },

    caseManualFormatFundsForMain(value = '', fallbackToken = '', options = {}) {
      const { amount, token } = this.caseManualSplitAmountAndToken(value, fallbackToken);
      const tokenText = token ? ` ${token}` : '';
      if (!Number.isFinite(amount)) return this.caseManualNormalizeText(value);
      const upperToken = String(token || '').toUpperCase();
      const defaultDecimals = upperToken === 'BTC' ? 8 : 2;
      const decimals = this.caseManualAllocationDecimalsForValue(value, upperToken, options);
      const formatted = options.preserveSourcePrecision === true || decimals > defaultDecimals
        ? this.caseManualFormatPreciseAllocationNumber(value, upperToken, {
            preserveSourcePrecision: options.preserveSourcePrecision === true,
            useGrouping: true,
          })
        : this.caseManualFormatNumberWithCommas(amount, {
            minimumFractionDigits: 0,
            maximumFractionDigits: defaultDecimals
          });
      return `${formatted}${tokenText}`.trim();
    },

    caseManualFormatFundsForDetail(value = '', fallbackToken = '', options = {}) {
      const { amount, token } = this.caseManualSplitAmountAndToken(value, fallbackToken);
      const tokenText = token ? ` ${token}` : '';
      if (!Number.isFinite(amount)) return this.caseManualNormalizeText(value);
      const upperToken = String(token || '').toUpperCase();
      const defaultDecimals = upperToken === 'BTC' ? 8 : 2;
      const decimals = this.caseManualAllocationDecimalsForValue(value, upperToken, options);
      const formatted = options.preserveSourcePrecision === true || decimals > defaultDecimals
        ? this.caseManualFormatPreciseAllocationNumber(value, upperToken, {
            preserveSourcePrecision: options.preserveSourcePrecision === true,
            useGrouping: false,
          })
        : amount.toFixed(defaultDecimals);
      return `${formatted}${tokenText}`.trim();
    },

    caseManualFormatLeftBoundsDisplayValue(value = '', fallback = '', token = '', options = {}) {
      const fallbackText = this.caseManualNormalizeText(fallback);
      const firstLine = this.caseManualFirstLine(value);
      const candidate = fallbackText || firstLine;
      let parsed = this.caseManualParseNumericAmount(candidate);
      if (!Number.isFinite(parsed)) {
        const fallbackParsed = this.caseManualParseNumericAmount(fallbackText);
        if (!Number.isFinite(fallbackParsed)) return this.caseManualNormalizeText(candidate || '0.00') || '0.00';
        parsed = fallbackParsed;
      }
      const normalizedToken = this.caseManualNormalizeText(token).toUpperCase();
      const defaultDecimals = normalizedToken === 'BTC' ? 8 : 2;
      const decimals = this.caseManualAllocationDecimalsForValue(candidate, normalizedToken, options);
      if (options.preserveSourcePrecision === true || decimals > defaultDecimals) {
        return this.caseManualFormatPreciseAllocationNumber(candidate, normalizedToken, {
          preserveSourcePrecision: options.preserveSourcePrecision === true,
          useGrouping: false,
          minimumFractionDigits: defaultDecimals,
        });
      }
      return normalizedToken === 'BTC' ? parsed.toFixed(8) : parsed.toFixed(2);
    },

    caseManualParseProviderDate(value) {
      const raw = this.caseManualNormalizeText(value);
      if (!raw) return '';
      let date = null;
      if (/^\d{10,13}$/.test(raw)) {
        const number = Number(raw);
        date = new Date(raw.length >= 13 ? number : number * 1000);
      } else {
        date = new Date(raw);
      }
      if (!date || Number.isNaN(date.getTime())) return raw;
      const pad = part => String(part).padStart(2, '0');
      return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())} UTC`;
    },

    getCaseManualStartCoverageRowByIndex() {
      const rows = Array.isArray(this.rawDataCurrentCoverage?.missingRows) ? this.rawDataCurrentCoverage.missingRows : [];
      const byIndex = new Map();
      rows.forEach(row => {
        const index = Number.isInteger(row?.rowIndex) ? row.rowIndex : (Number(row?.spreadsheetRowNumber) > 0 ? Number(row.spreadsheetRowNumber) - 1 : -1);
        if (index >= 0) byIndex.set(index, row);
      });
      return byIndex;
    },

    beginCaseManualStartHopInspectorDrag(event) {
      if (!event || event.button !== 0) return;
      const target = event.target;
      if (target?.closest?.('button, a, input, textarea, select, summary, details, [role="button"], .toolbar-btn')) return;
      if (!target?.closest?.('.case-manual-start-hop-inspector__drag-handle')) return;
      const panel = this.$refs.caseManualStartHopInspectorPanel;
      if (!panel || typeof panel.getBoundingClientRect !== 'function') return;
      const container = this.$refs.caseInvestigationVisualizerViewportEl || panel.parentElement || panel.offsetParent || null;
      if (!container || typeof container.getBoundingClientRect !== 'function') return;
      const panelRect = panel.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const startX = panelRect.left - containerRect.left;
      const startY = panelRect.top - containerRect.top;
      this.caseManualStartHopInspectorPosition = { x: startX, y: startY };
      this.caseManualStartHopInspectorDragging = true;
      this.caseManualStartHopInspectorDragState = {
        offsetX: event.clientX - panelRect.left,
        offsetY: event.clientY - panelRect.top,
        panelWidth: panelRect.width,
        panelHeight: panelRect.height,
        containerLeft: containerRect.left,
        containerTop: containerRect.top,
        containerWidth: containerRect.width,
        containerHeight: containerRect.height
      };
      if (typeof panel.setPointerCapture === 'function' && event.pointerId !== undefined) {
        try { panel.setPointerCapture(event.pointerId); } catch (error) { console.warn('Case Buddy manual hop inspector pointer capture failed.', error); }
      }
      window.addEventListener('pointermove', this.moveCaseManualStartHopInspectorDrag, { passive: false });
      window.addEventListener('pointerup', this.endCaseManualStartHopInspectorDrag, { once: true });
      event.preventDefault();
    },

    moveCaseManualStartHopInspectorDrag(event) {
      const state = this.caseManualStartHopInspectorDragState;
      if (!state || !this.caseManualStartHopInspectorDragging) return;
      const maxX = Math.max(8, state.containerWidth - state.panelWidth - 8);
      const maxY = Math.max(8, state.containerHeight - state.panelHeight - 8);
      const x = this.caseManualClampNumber(event.clientX - state.containerLeft - state.offsetX, 8, maxX);
      const y = this.caseManualClampNumber(event.clientY - state.containerTop - state.offsetY, 8, maxY);
      this.caseManualStartHopInspectorPosition = { x, y };
      event.preventDefault();
    },

    endCaseManualStartHopInspectorDrag() {
      if (!this.caseManualStartHopInspectorDragging) return;
      this.caseManualStartHopInspectorDragging = false;
      this.caseManualStartHopInspectorDragState = null;
      window.removeEventListener('pointermove', this.moveCaseManualStartHopInspectorDrag);
    },

    getCaseManualStartVisualizerRows(options = {}) {
      const matrix = this.getCaseManualStartMainMatrix();
      const missingByIndex = this.getCaseManualStartCoverageRowByIndex();
      const preferMissing = missingByIndex.size > 0 && !options.includeAll;
      const rows = [];
      const seen = new Set();
      matrix.forEach((rawRow, rowIndex) => {
        if (!Array.isArray(rawRow) || !rawRow.some(value => this.caseManualNormalizeText(value) !== '')) return;
        if (rowIndex === 0 && this.caseManualIsHeaderRow(rawRow)) return;
        const txHash = this.caseManualNormalizeText(rawRow[1]);
        if (!this.caseManualIsLikelyTransactionHash(txHash)) return;
        const fromHash = this.caseManualNormalizeText(rawRow[14]);
        const coverageRow = missingByIndex.get(rowIndex) || null;
        if (preferMissing && !coverageRow) return;
        if (fromHash && !coverageRow && !options.includeContinuationRows && !options.includeAll) return;
        const key = `${rowIndex}:${txHash.toLowerCase()}`;
        if (seen.has(key)) return;
        seen.add(key);
        const candidate = this.buildCaseManualStartCandidate(rawRow, rowIndex, coverageRow);
        if (candidate) rows.push(candidate);
      });
      return this.caseManualAnnotateOccurrenceAwareVisualContinuations(rows);
    },

    caseManualAnnotateOccurrenceAwareVisualContinuations(candidates = []) {
      const source = Array.isArray(candidates) ? candidates : [];
      const rows = source.filter(candidate => candidate?.caseManualStartCandidate && candidate.hash);
      if (!rows.length) return source;
      const normalizeHashKey = value => this.caseManualNormalizeText(value).toLowerCase();
      const normalizeAddressKey = value => this.caseManualNormalizeAddressKey(value);
      const numericRowIndex = (candidate, fallback) => {
        const value = Number(candidate?.rowIndex);
        return Number.isFinite(value) ? value : fallback;
      };
      const pathDescendsFrom = (childLabel = '', parentLabel = '') => {
        const child = this.caseManualNormalizeText(childLabel).toLowerCase();
        const parent = this.caseManualNormalizeText(parentLabel).toLowerCase();
        return Boolean(child && parent && child !== parent && child.startsWith(parent));
      };
      const occurrenceKeyFor = (candidate = {}, fallback = 0) => {
        const hash = normalizeHashKey(candidate.hash || candidate.txHash || candidate.transactionHash);
        const parent = normalizeHashKey(candidate.fromHash || candidate.parentHash || '');
        const label = this.caseManualNormalizeText(candidate.pathLabel || candidate.label || '');
        const funds = this.caseManualNormalizeText(candidate.fundsFollowed || '').replace(/,/gu, '').toLowerCase();
        const left = this.caseManualNormalizeText(candidate.leftBoundsNumeric || candidate.leftBoundsDisplay || '').replace(/,/gu, '').toLowerCase();
        const rowIndex = numericRowIndex(candidate, fallback);
        return `${label || `idx-${rowIndex}`}|${hash}|${parent}|${funds}|${left}|${rowIndex}`;
      };
      const items = rows.map((candidate, index) => {
        const row = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
        const rowIndex = numericRowIndex(candidate, index);
        return {
          candidate,
          row,
          index,
          rowIndex,
          occurrenceKey: occurrenceKeyFor(candidate, index),
          hashKey: normalizeHashKey(candidate.hash || candidate.txHash || candidate.transactionHash),
          parentHashKey: normalizeHashKey(candidate.fromHash || row[CASE_MANUAL_START_COL.FROM_HASH] || ''),
          pathLabel: this.caseManualNormalizeText(candidate.pathLabel || row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase(),
          fromLabel: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_LABEL]).toLowerCase(),
          fromAddressKey: normalizeAddressKey(candidate.fromAddress || row[CASE_MANUAL_START_COL.FROM_ADDRESS]),
          toAddressKey: normalizeAddressKey(candidate.toAddress || row[CASE_MANUAL_START_COL.TO_ADDRESS]),
          childHashes: this.caseManualExtractHashTokensFromContinuesAs(candidate.continuesAs || row[CASE_MANUAL_START_COL.CONTINUES_AS]),
        };
      });
      const itemsByHash = new Map();
      items.forEach(item => {
        if (!item.hashKey) return;
        if (!itemsByHash.has(item.hashKey)) itemsByHash.set(item.hashKey, []);
        itemsByHash.get(item.hashKey).push(item);
      });
      const matchingItemsForHash = (hash = '') => {
        const key = normalizeHashKey(hash);
        if (!key) return [];
        const direct = itemsByHash.get(key) || [];
        if (direct.length) return direct;
        return items.filter(item => item.hashKey && this.caseManualTransactionHashesMatch(item.hashKey, key));
      };
      const childAllocationMatchesParent = (child, parent) => {
        const childRow = child?.row || [];
        const parentRow = parent?.row || [];
        return this.caseManualRowsFundsFollowedApproximatelyMatch(childRow, parentRow);
      };
      const amountFromItemFunds = (item = {}) => {
        const row = item?.row || [];
        const candidate = item?.candidate || {};
        return this.caseManualParseNumericAmount(candidate.fundsFollowed || row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || '');
      };
      const amountToleranceFor = (amount = 0) => Math.max(0.01, Math.abs(Number(amount) || 0) * 0.000001);
      const childFundsFitWithinParent = (child, parent) => {
        const childAmount = amountFromItemFunds(child);
        const parentAmount = amountFromItemFunds(parent);
        if (!Number.isFinite(childAmount) || !Number.isFinite(parentAmount)) return false;
        if (childAmount <= 0 || parentAmount <= 0) return false;
        return childAmount <= parentAmount + amountToleranceFor(parentAmount);
      };
      const numericText = value => this.caseManualNormalizeText(value).replace(/,/g, '');
      const firstNumber = (value) => {
        const match = numericText(value).match(/-?\d+(?:\.\d+)?/u);
        if (!match) return NaN;
        const number = Number(match[0]);
        return Number.isFinite(number) ? number : NaN;
      };
      const amountClose = (left, right) => {
        if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
        const tolerance = Math.max(0.05, Math.abs(right) * 0.00001);
        return Math.abs(left - right) <= tolerance;
      };
      const branchCoverageSatisfiesParent = (parentItem, expectedChildHashes = []) => {
        const parentFundsAmount = firstNumber(parentItem?.row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || parentItem?.candidate?.fundsFollowed || '');
        if (!Number.isFinite(parentFundsAmount) || parentFundsAmount <= 0) return false;
        const expectedKeys = (Array.isArray(expectedChildHashes) ? expectedChildHashes : [])
          .map(hash => normalizeHashKey(hash))
          .filter(Boolean);
        if (!expectedKeys.length) return false;
        const rawMatches = items
          .filter(child => child?.occurrenceKey !== parentItem?.occurrenceKey)
          .filter(child => child?.hashKey && expectedKeys.some(hash => this.caseManualTransactionHashesMatch(child.hashKey, hash)))
          .filter(child => child?.parentHashKey && parentItem?.hashKey && this.caseManualTransactionHashesMatch(child.parentHashKey, parentItem.hashKey))
          .filter(child => !parentItem?.toAddressKey || !child?.fromAddressKey || child.fromAddressKey === parentItem.toAddressKey);
        const afterParent = rawMatches.filter(child => child.rowIndex > parentItem.rowIndex);
        const usableMatches = afterParent.length ? afterParent : rawMatches;
        const amounts = usableMatches
          .map(child => ({ child, amount: firstNumber(child?.row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || child?.candidate?.fundsFollowed || '') }))
          .filter(record => Number.isFinite(record.amount) && record.amount > 0)
          .slice(0, 16);
        if (!amounts.length) return false;
        if (amounts.some(record => amountClose(record.amount, parentFundsAmount))) return true;
        if (amountClose(amounts.reduce((sum, record) => sum + record.amount, 0), parentFundsAmount)) return true;
        if (amounts.length <= 15) {
          const limit = 1 << amounts.length;
          for (let mask = 1; mask < limit; mask += 1) {
            let total = 0;
            for (let index = 0; index < amounts.length; index += 1) {
              if (mask & (1 << index)) total += amounts[index].amount;
            }
            if (amountClose(total, parentFundsAmount)) return true;
          }
        }
        return false;
      };
      const labelRoot = (label = '') => {
        const match = this.caseManualNormalizeText(label).toLowerCase().match(/^([0-9]+|[a-z]+)/u);
        return match ? match[1] : '';
      };
      const pathLooksLikeLaterStepInSameFlow = (childLabel = '', parentLabel = '') => {
        const child = this.caseManualNormalizeText(childLabel).toLowerCase();
        const parent = this.caseManualNormalizeText(parentLabel).toLowerCase();
        if (!child || !parent || child === parent) return false;
        if (pathDescendsFrom(child, parent)) return true;
        const childRoot = labelRoot(child);
        const parentRoot = labelRoot(parent);
        if (!childRoot || childRoot !== parentRoot) return false;
        return this.caseManualCompareSaplingPathLabels(child, parent) > 0;
      };
      const childLooksPhysicallyLinkedToParent = (child, parent) => {
        if (!child || !parent) return false;
        if (!child.parentHashKey || !parent.hashKey || !this.caseManualTransactionHashesMatch(child.parentHashKey, parent.hashKey)) return false;
        if (parent.toAddressKey && child.fromAddressKey && parent.toAddressKey !== child.fromAddressKey) return false;
        if (Number.isFinite(child.rowIndex) && Number.isFinite(parent.rowIndex) && child.rowIndex <= parent.rowIndex) {
          return pathDescendsFrom(child.pathLabel, parent.pathLabel) || pathLooksLikeLaterStepInSameFlow(child.pathLabel, parent.pathLabel);
        }
        return true;
      };
      const childCanSatisfyParentContinuation = (child, parent, childHash = '') => {
        const childKey = normalizeHashKey(childHash);
        if (!child || !parent || !childKey) return false;
        if (!child.hashKey || !this.caseManualTransactionHashesMatch(child.hashKey, childKey)) return false;
        if (!childLooksPhysicallyLinkedToParent(child, parent)) return false;
        const exactFromLabel = Boolean(child.fromLabel && parent.pathLabel && child.fromLabel === parent.pathLabel);
        const descendant = pathDescendsFrom(child.pathLabel, parent.pathLabel);
        const allocationMatch = childAllocationMatchesParent(child, parent);
        const allocationFits = childFundsFitWithinParent(child, parent);
        const sameFlowLater = pathLooksLikeLaterStepInSameFlow(child.pathLabel, parent.pathLabel);
        // If both rows know funds-followed amounts, a child that follows more
        // funds than this parent occurrence cannot satisfy this occurrence. This
        // is the important split between same-hash/same-address forensic paths.
        if (!allocationMatch && !allocationFits && Number.isFinite(amountFromItemFunds(child)) && Number.isFinite(amountFromItemFunds(parent))) return false;
        // The Manual Solve visual can legitimately show two forensic occurrences
        // of the same on-chain transaction. In that case one physical child row
        // may be enough evidence that the named continuation exists, even when
        // the path labels are simple trunk labels rather than strict descendants.
        return exactFromLabel || descendant || allocationMatch || allocationFits || sameFlowLater || childLooksPhysicallyLinkedToParent(child, parent);
      };
      const chooseParentForChild = (child) => {
        if (!child?.parentHashKey) return null;
        const candidatesForParent = matchingItemsForHash(child.parentHashKey).filter(item => item.occurrenceKey !== child.occurrenceKey);
        if (!candidatesForParent.length) return null;
        if (candidatesForParent.length === 1) return candidatesForParent[0];
        const scored = candidatesForParent
          .map(item => {
            const exactFromLabel = Boolean(child.fromLabel && item.pathLabel && child.fromLabel === item.pathLabel);
            const descendant = pathDescendsFrom(child.pathLabel, item.pathLabel);
            const sameFlowLater = pathLooksLikeLaterStepInSameFlow(child.pathLabel, item.pathLabel);
            const allocationMatch = childAllocationMatchesParent(child, item);
            const allocationFits = childFundsFitWithinParent(child, item);
            const addressMatch = Boolean(item.toAddressKey && child.fromAddressKey && item.toAddressKey === child.fromAddressKey);
            let score = 0;
            if (exactFromLabel) score += 10000;
            if (descendant) score += 5000 + item.pathLabel.length;
            if (sameFlowLater) score += 900;
            // Same transaction hash / same address can represent two separate
            // forensic occurrences. In those cases, row order is unreliable after
            // sapling sorting. The amount being followed is the durable signal that
            // the child belongs to the small or large sibling occurrence.
            if (allocationMatch) score += 2500;
            // A child allocation can be one branch of a larger parent. It still
            // must fit within that parent; otherwise the large child from one
            // occurrence can be attached to the small sibling occurrence.
            if (allocationFits) score += 900;
            if (addressMatch) score += 120;
            if (item.rowIndex < child.rowIndex) score += 20;
            score -= Math.abs(child.rowIndex - item.rowIndex) / 1000;
            return { item, score, exactFromLabel, descendant, sameFlowLater, allocationMatch, allocationFits, addressMatch };
          })
          .sort((left, right) => right.score - left.score || right.item.rowIndex - left.item.rowIndex);
        const labelled = scored.filter(record => record.exactFromLabel || record.descendant);
        if (labelled.length) return labelled[0].item;
        const allocationMatches = scored.filter(record => record.allocationMatch);
        if (allocationMatches.length === 1) return allocationMatches[0].item;
        const allocationFits = scored.filter(record => record.allocationFits);
        if (allocationFits.length === 1) return allocationFits[0].item;
        const addressPriorFit = scored
          .filter(record => record.addressMatch && record.allocationFits && record.item.rowIndex < child.rowIndex)
          .sort((left, right) => right.item.rowIndex - left.item.rowIndex);
        if (addressPriorFit.length === 1) return addressPriorFit[0].item;
        const addressPrior = scored
          .filter(record => record.addressMatch && record.item.rowIndex < child.rowIndex)
          .sort((left, right) => right.item.rowIndex - left.item.rowIndex);
        if (addressPrior.length === 1) return addressPrior[0].item;
        const prior = scored
          .filter(record => record.item.rowIndex < child.rowIndex)
          .sort((left, right) => right.item.rowIndex - left.item.rowIndex);
        return prior[0]?.item || null;
      };
      const coverage = this.caseManualBuildOccurrenceContinuationCoverage(items);
      const coverageChildrenByParent = coverage.childrenByParent || new Map();
      const coverageExactParentKeys = coverage.exactParentKeys || new Set();
      const childrenByParentOccurrence = new Map();
      const addChild = (parent, child) => {
        if (!parent || !child) return;
        if (!childrenByParentOccurrence.has(parent.occurrenceKey)) childrenByParentOccurrence.set(parent.occurrenceKey, []);
        const siblings = childrenByParentOccurrence.get(parent.occurrenceKey);
        if (!siblings.some(existing => existing.occurrenceKey === child.occurrenceKey)) siblings.push(child);
      };
      coverageChildrenByParent.forEach((children, parentKey) => {
        childrenByParentOccurrence.set(parentKey, Array.isArray(children) ? children.slice() : []);
      });
      items.forEach(child => {
        if (coverage.parentByChild?.has?.(child.occurrenceKey)) return;
        addChild(chooseParentForChild(child), child);
      });
      const childSatisfied = (parentItem, childHash = '') => {
        const childKey = normalizeHashKey(childHash);
        if (!childKey) return false;
        const assignedChildren = childrenByParentOccurrence.get(parentItem.occurrenceKey) || [];
        if (assignedChildren.some(child => child.hashKey && this.caseManualTransactionHashesMatch(child.hashKey, childKey))) return true;
        return matchingItemsForHash(childKey).some(child => childCanSatisfyParentContinuation(child, parentItem, childKey));
      };
      items.forEach(item => {
        const childHashes = item.childHashes || [];
        let missing = childHashes.filter(hash => !childSatisfied(item, hash));
        const coverageChildren = coverageChildrenByParent.get(item.occurrenceKey) || [];
        const coverageSatisfied = coverageExactParentKeys.has(item.occurrenceKey) && coverageChildren.length > 0;
        if (coverageSatisfied) missing = [];
        if (!coverageSatisfied && missing.length > 0 && branchCoverageSatisfiesParent(item, childHashes)) missing = [];
        const missingContinuation = childHashes.length > 0 && missing.length > 0;
        item.candidate.caseManualOccurrenceContinuationMissing = missingContinuation;
        item.candidate.caseManualMissingOccurrenceChildHashes = missing;
        item.candidate.caseManualOccurrenceContinuationCoveredByAllocation = coverageSatisfied;
        item.candidate.caseManualOccurrenceContinuationCoveredHashes = coverageChildren.map(child => child.hashKey).filter(Boolean);
        if (missingContinuation) {
          item.candidate.isPathEnding = true;
          item.candidate.visualEndpointStatus = 'missing-occurrence-continuation';
        } else if (item.candidate.visualEndpointStatus === 'missing-occurrence-continuation') {
          item.candidate.visualEndpointStatus = '';
          item.candidate.isPathEnding = false;
        }
      });

      return source;
    },

    buildCaseManualStartRowFromVisualEntry(entry = {}, fallbackIndex = 0) {
      if (Array.isArray(entry)) return this.caseManualEnsureRowWidth(entry, CASE_MANUAL_START_COLUMN_COUNT);
      const sourceRow = Array.isArray(entry?.rawRow)
        ? entry.rawRow
        : (Array.isArray(entry?.row) ? entry.row : []);
      const row = this.caseManualEnsureRowWidth(sourceRow, CASE_MANUAL_START_COLUMN_COUNT);
      const assignIfBlank = (index, ...values) => {
        if (this.caseManualNormalizeText(row[index])) return;
        const value = values.map(item => this.caseManualNormalizeText(item)).find(Boolean) || '';
        if (value) row[index] = value;
      };
      assignIfBlank(CASE_MANUAL_START_COL.PATH_LABEL, entry.pathLabel, entry.label, entry.path, entry.pathId, fallbackIndex ? `1${String.fromCharCode(96 + Math.min(26, fallbackIndex))}` : '1');
      assignIfBlank(CASE_MANUAL_START_COL.TX_HASH, entry.hash, entry.txHash, entry.transactionHash, entry.transaction_hash, entry.hashText);
      assignIfBlank(CASE_MANUAL_START_COL.FROM_ADDRESS, entry.fromAddress, entry.from, entry.sourceAddress, entry.sender, entry.from_address);
      assignIfBlank(CASE_MANUAL_START_COL.TO_ADDRESS, entry.toAddress, entry.to, entry.targetAddress, entry.recipient, entry.to_address);
      assignIfBlank(CASE_MANUAL_START_COL.DATE, entry.utcDate, entry.date, entry.dateText, entry.timestamp, entry.utc_date);
      const visualAmountText = this.caseManualNormalizeText(entry.amountText);
      const visualFundsFollowedText = this.caseManualNormalizeText(entry.fundsFollowed || entry.funds_followed);
      const safeVisualAmountText = visualAmountText && (!visualFundsFollowedText || visualAmountText.toLowerCase() !== visualFundsFollowedText.toLowerCase())
        ? visualAmountText
        : '';
      assignIfBlank(CASE_MANUAL_START_COL.AMOUNT, entry.amount, entry.transactionAmount, entry.transactionAmountText, safeVisualAmountText, entry.value, entry.transferAmount);
      assignIfBlank(CASE_MANUAL_START_COL.TOKEN, entry.token, entry.tokenSymbol, entry.symbol, entry.assetSymbol, entry.token_symbol);
      assignIfBlank(CASE_MANUAL_START_COL.SYSTEM_NOTES, entry.systemNotes, entry.notes, entry.note, entry.system_notes);
      assignIfBlank(
        CASE_MANUAL_START_COL.ATTRIBUTION,
        entry.toAttributionLabel,
        entry.toAttribution?.label,
        entry.toAttribution?.displayLabel,
        entry.toAttribution?.display_label,
        entry.attribution,
        entry.entity,
        entry.entityLabel
      );
      assignIfBlank(
        CASE_MANUAL_START_COL.FROM_ATTRIBUTION,
        entry.fromAttributionLabel,
        entry.fromAttribution?.label,
        entry.fromAttribution?.displayLabel,
        entry.fromAttribution?.display_label
      );
      assignIfBlank(CASE_MANUAL_START_COL.ANALYST_NOTES, entry.analystNotes, entry.analyst_notes);
      assignIfBlank(CASE_MANUAL_START_COL.FUNDS_FOLLOWED, entry.fundsFollowed, entry.funds_followed);
      // Do not invent a zero Column N value for manual visual entries. A blank
      // prior balance is safer than a fake zero, and Tool 88/WayFinder rows will
      // provide the reconstructed value when they have it.
      assignIfBlank(
        CASE_MANUAL_START_COL.PRIOR_BALANCE,
        entry.caseBuddyPriorBalance,
        entry.priorBalanceBeforeTransaction,
        entry.prior_balance_before_transaction,
        entry.actualPriorBalanceAmount,
        entry.accountPriorBalance,
        entry.addressBalanceBeforeTransaction,
        entry.balanceBeforeTransaction,
        entry.balance_before_transaction,
        entry.assetBalanceBefore,
        entry.asset_balance_before,
        entry.balanceBefore,
        entry.balance_before
      );
      assignIfBlank(CASE_MANUAL_START_COL.FROM_HASH, entry.fromHash, entry.parentHash, entry.previousHash, entry.from_hash);
      assignIfBlank(CASE_MANUAL_START_COL.FROM_LABEL, entry.fromLabel, entry.parentLabel, entry.from_label);
      assignIfBlank(CASE_MANUAL_START_COL.CONTINUES_AS, entry.continuesAs, entry.continues, entry.continues_as, entry.isPathEnding ? 'Path ends.' : '');
      assignIfBlank(CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY, entry.leftBoundsDisplay, entry.leftBounds, entry.left_bounds_display);
      assignIfBlank(CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC, entry.leftBoundsNumeric, entry.left_bounds_numeric);
      assignIfBlank(CASE_MANUAL_START_COL.CHAIN_ID, entry.chainId, entry.chain_id);
      assignIfBlank(CASE_MANUAL_START_COL.CHAIN_NAME, entry.chainName, entry.network, entry.chain_name);
      assignIfBlank(CASE_MANUAL_START_COL.HOP_LOGIC, entry.hopLogic, entry.hop_logic, 'FIFO');
      assignIfBlank(CASE_MANUAL_START_COL.CASE_NOTES, entry.caseNotes, entry.tool88Notes, entry.caseBuddyNotes, entry.internalNotes);
      return this.caseManualNormalizeAttributionRoleColumnsForRow(row);
    },

    caseManualEntryRowsFromMainMatrix(rowIndex = -1) {
      const matrix = this.getCaseManualStartMainMatrix();
      const start = Number(rowIndex);
      if (!Number.isInteger(start) || start < 0 || start >= matrix.length) return [];
      const rows = [];
      for (let index = start; index < matrix.length; index += 1) {
        const row = this.caseManualEnsureRowWidth(matrix[index] || [], CASE_MANUAL_START_COLUMN_COUNT);
        if (index > start && this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH])) break;
        if (index > start && !row.some(value => this.caseManualNormalizeText(value))) break;
        rows.push(row);
      }
      return rows;
    },

    caseManualCollectBtcInputAddressRowsForEntry(rowIndex = -1, seedRow = []) {
      const rows = this.caseManualEntryRowsFromMainMatrix(rowIndex);
      const sourceRows = rows.length ? rows : [seedRow];
      return this.caseManualCollectBtcInputAddressRows(sourceRows.map((row, index) => ({
        address: row?.[CASE_MANUAL_START_COL.FROM_ADDRESS],
        from_address: row?.[CASE_MANUAL_START_COL.FROM_ADDRESS],
        amount: row?.[CASE_MANUAL_START_COL.AMOUNT],
        amountText: row?.[CASE_MANUAL_START_COL.AMOUNT],
        token: row?.[CASE_MANUAL_START_COL.TOKEN] || 'BTC',
        order: index
      })));
    },

    caseManualCollectBtcOutputAddressRowsForEntry(rowIndex = -1, seedRow = []) {
      const rows = this.caseManualEntryRowsFromMainMatrix(rowIndex);
      const sourceRows = rows.length ? rows : [seedRow];
      return this.caseManualCollectBtcOutputAddressRows(sourceRows.map((row, index) => ({
        address: row?.[CASE_MANUAL_START_COL.TO_ADDRESS],
        to_address: row?.[CASE_MANUAL_START_COL.TO_ADDRESS],
        token: row?.[CASE_MANUAL_START_COL.TOKEN] || 'BTC',
        order: index
      })));
    },

    getCaseManualStartVisualizerRowsFromCurrentVisual() {
      const sources = [];
      const pushSource = value => {
        if (!value) return;
        if (Array.isArray(value)) value.forEach(pushSource);
        else if (typeof value === 'object') sources.push(value);
      };
      pushSource(this.caseInvestigationVisualizerCurrentFlow?.entries);
      pushSource(this.caseInvestigationVisualizerCurrentVisual?.entries);
      pushSource(this.caseInvestigationVisualizerState?.entries);
      pushSource((this.caseInvestigationVisualizerCurrentVisual?.nodes || []).map(node => node?.caseCandidate || node?.candidate).filter(Boolean));
      const rows = [];
      const seen = new Set();
      sources.forEach((entry, index) => {
        const row = this.buildCaseManualStartRowFromVisualEntry(entry, index);
        const hash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (!hash || !this.caseManualIsLikelyTransactionHash(hash)) return;
        const key = `${hash}:${this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase()}:${this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase()}`;
        if (seen.has(key)) return;
        seen.add(key);
        const candidate = this.buildCaseManualStartCandidate(row, rows.length + 1, null);
        if (candidate) rows.push(candidate);
      });
      return rows;
    },

    buildCaseManualStartCandidate(rawRow = [], rowIndex = 0, coverageRow = null) {
      let row = Array.from({ length: CASE_MANUAL_START_COLUMN_COUNT }, (_, index) => this.caseManualNormalizeText(rawRow[index]));
      row = this.caseManualRepairReverseBtcAllocationFromNotes(row);
      const originalHash = row[CASE_MANUAL_START_COL.TX_HASH] || this.caseManualNormalizeText(coverageRow?.hash);
      if (!originalHash) return null;
      const cacheKey = this.buildCaseManualStartHydrationKey(originalHash, rowIndex);
      const hydrated = this.caseManualStartVisualizerHydrationByKey?.[cacheKey] || null;
      const hydratedHash = this.caseManualNormalizeText(hydrated?.txHash || hydrated?.transactionHash || hydrated?.hash || '');
      const hash = hydratedHash || originalHash;
      if (this.caseManualShouldReplaceStoredTransactionHash(row[CASE_MANUAL_START_COL.TX_HASH], hash)) {
        row[CASE_MANUAL_START_COL.TX_HASH] = hash;
      }
      const chainId = row[CASE_MANUAL_START_COL.CHAIN_ID] || this.caseManualNormalizeText(coverageRow?.chainId) || '1';
      const chainName = row[CASE_MANUAL_START_COL.CHAIN_NAME] || this.caseManualNormalizeText(coverageRow?.chainName) || (chainId === '1' ? 'Ethereum' : '');
      const networkFamily = this.caseManualInferNetworkFamily({ chainId, chainName, token: row[CASE_MANUAL_START_COL.TOKEN], rawRow: row });
      const btcInputAddresses = networkFamily === 'btc'
        ? (Array.isArray(hydrated?.btcInputAddresses) && hydrated.btcInputAddresses.length
          ? this.caseManualCollectBtcInputAddressRows(hydrated.btcInputAddresses)
          : this.caseManualCollectBtcInputAddressRowsForEntry(rowIndex, row))
        : [];
      const btcOutputAddresses = networkFamily === 'btc'
        ? (Array.isArray(hydrated?.btcOutputAddresses) && hydrated.btcOutputAddresses.length
          ? this.caseManualCollectBtcOutputAddressRows(hydrated.btcOutputAddresses)
          : this.caseManualCollectBtcOutputAddressRowsForEntry(rowIndex, row))
        : [];
      const continuesAs = row[CASE_MANUAL_START_COL.CONTINUES_AS] || 'Path ends.';
      const hydratedBtcChoiceRequired = Boolean(hydrated?.btcOutputChoiceRequired || hydrated?.needsBtcOutputChoice || hydrated?.btcNeedsOutputChoice);
      const btcChoiceAlreadyApplied = this.caseManualStartBtcChoiceWasApplied({ rawRow: row, row, ...(hydrated || {}) });
      const traceDirection = this.caseManualStartOutputChoiceDirectionForCandidate({ rawRow: row, row, chainId, chainName, token: row[CASE_MANUAL_START_COL.TOKEN] });
      const btcChoiceDirection = traceDirection;
      const btcTargetSideAlreadyKnown = btcChoiceDirection === 'reverse'
        ? Boolean(this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_ADDRESS] || hydrated?.fromAddress))
        : Boolean(this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TO_ADDRESS] || hydrated?.toAddress || coverageRow?.toAddress));
      const holdBtcTargetUntilChoice = networkFamily === 'btc' && hydratedBtcChoiceRequired && !btcChoiceAlreadyApplied && !btcTargetSideAlreadyKnown;
      const amount = holdBtcTargetUntilChoice
        ? ''
        : (row[CASE_MANUAL_START_COL.AMOUNT] || hydrated?.amountText || this.caseManualAmountFromFundsFollowed(row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]));
      const token = row[CASE_MANUAL_START_COL.TOKEN] || hydrated?.tokenSymbol || this.caseManualTokenFromFundsFollowed(row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]) || this.caseManualNativeSymbolForChain(chainId, chainName);
      const fromAddress = hydrated?.fromAddress || row[2];
      const firstBtcOutputAddress = this.caseManualNormalizeText(btcOutputAddresses[0]?.toAddress || btcOutputAddresses[0]?.address || '');
      const toAddress = holdBtcTargetUntilChoice ? '' : (hydrated?.toAddress || row[3] || firstBtcOutputAddress || this.caseManualNormalizeText(coverageRow?.toAddress));
      const utcDate = hydrated?.dateText || row[4];
      const fundsFollowed = holdBtcTargetUntilChoice
        ? ''
        : (row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || hydrated?.fundsFollowed || (amount ? `${amount}${token ? ` ${token}` : ''}`.trim() : ''));
      const priorBalance = this.caseManualPickAccountPriorBalanceText(row, hydrated || {}, { amount, fundsFollowed });
      const leftBoundsDisplay = this.caseManualPickPreferredNumericText(row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY], hydrated?.leftBoundsDisplay || '');
      const leftBoundsNumeric = this.caseManualPickPreferredNumericText(row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC], hydrated?.leftBoundsNumeric || hydrated?.leftBoundAmount || '');
      const fromAttribution = hydrated?.fromAttribution || this.caseManualAttributionForAddress(fromAddress) || null;
      const toAttribution = hydrated?.toAttribution || this.caseManualAttributionForAddress(toAddress) || null;
      const systemNotes = hydrated?.systemNotes || row[CASE_MANUAL_START_COL.SYSTEM_NOTES];
      const rawAttributionCell = hydrated?.attribution || row[CASE_MANUAL_START_COL.ATTRIBUTION] || '';
      const roleAttribution = this.caseManualExtractRoleAttributionLabels(rawAttributionCell);
      const attributionCell = this.caseManualSanitizeSwapServiceAttributionForColumnK(
        rawAttributionCell,
        row,
        { extraText: [systemNotes, hydrated?.caseNotes, row[CASE_MANUAL_START_COL.CASE_NOTES]].filter(Boolean).join('\n') }
      );
      const columnKToAttributionLabel = this.caseManualCanonicalizeAttributionForColumnK(attributionCell || rawAttributionCell);
      row[CASE_MANUAL_START_COL.ATTRIBUTION] = columnKToAttributionLabel;
      const columnXFromAttributionLabel = this.caseManualCanonicalizeFromAttributionForColumnX(
        hydrated?.fromAttributionLabel || row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] || roleAttribution.from || ''
      );
      if (columnXFromAttributionLabel) {
        row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] = columnXFromAttributionLabel;
      }
      let fromAttributionLabel = hydrated?.fromAttributionLabel || fromAttribution?.label || columnXFromAttributionLabel || roleAttribution.from || '';
      let toAttributionLabel = hydrated?.toAttributionLabel || toAttribution?.label || roleAttribution.to || columnKToAttributionLabel || '';
      const unknownServicePathEnding = this.caseManualStartCandidateHasUnknownServiceEndpoint({
        row,
        rawRow: row,
        traceDirection,
        analysisDirection: traceDirection,
        systemNotes,
        attribution: attributionCell,
        fromAttributionLabel,
        toAttributionLabel,
        continuesAs,
        fromAddress,
        toAddress
      });
      if (unknownServicePathEnding) {
        if (traceDirection === 'reverse' && !fromAttributionLabel) {
          fromAttributionLabel = 'Unknown';
          row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] = 'Unknown';
        } else if (traceDirection !== 'reverse' && !toAttributionLabel) {
          toAttributionLabel = 'Unknown';
        }
      }
      const attribution = attributionCell || toAttributionLabel || '';
      const missingFundsFollowedFallback = !row[12] && !hydrated?.fundsFollowed;
      const missingLeftBoundsFallback = !row[17] && !row[18] && !hydrated?.leftBoundsDisplay && !hydrated?.leftBoundsNumeric;
      return {
        id: `case-manual-start-${rowIndex}-${hash.toLowerCase()}`,
        caseManualStartCandidate: true,
        integrationSource: 'case-buddy-manual-start-visualizer',
        rowIndex,
        spreadsheetRowNumber: rowIndex + 1,
        pathLabel: this.caseManualDeriveFallbackPathLabel(row, rowIndex, coverageRow),
        hash,
        transactionHash: hash,
        txHash: hash,
        fromAddress,
        toAddress,
        traceDirection,
        analysisDirection: traceDirection,
        targetRole: traceDirection === 'reverse' ? 'from' : 'to',
        targetAddress: traceDirection === 'reverse' ? (fromAddress || toAddress) : (toAddress || fromAddress),
        selectedTargetAddress: traceDirection === 'reverse' ? (fromAddress || toAddress) : (toAddress || fromAddress),
        utcDate,
        amount,
        token,
        fundsFollowed,
        priorBalance,
        fromHash: row[14],
        continuesAs,
        leftBoundsDisplay,
        leftBoundsNumeric,
        chainId,
        chainName,
        btcInputAddresses,
        btcInputAddressCount: btcInputAddresses.length,
        btcHiddenInputAddressCount: Math.max(0, btcInputAddresses.length - CASE_MANUAL_START_BTC_VISUAL_INPUT_ADDRESS_LIMIT),
        btcOutputAddresses,
        btcOutputAddressCount: btcOutputAddresses.length,
        btcHiddenOutputAddressCount: Math.max(0, btcOutputAddresses.length - CASE_MANUAL_START_BTC_VISUAL_OUTPUT_ADDRESS_LIMIT),
        btcOutputChoiceRequired: Boolean(hydrated?.btcOutputChoiceRequired || hydrated?.needsBtcOutputChoice || hydrated?.btcNeedsOutputChoice),
        btcOutputChoiceCount: Number(hydrated?.btcOutputChoiceCount || hydrated?.btcOutputCount || hydrated?.outputChoiceCount || 0) || 0,
        btcInputChoiceCount: Number(hydrated?.btcInputChoiceCount || hydrated?.btcInputCount || hydrated?.inputChoiceCount || btcInputAddresses.length || 0) || 0,
        sourceLabel: hydrated?.sourceLabel || '',
        hopLogic: row[21] || '',
        attribution,
        fromAttribution,
        toAttribution,
        fromAttributionLabel,
        toAttributionLabel,
        analystNotes: row[11] || '',
        caseNotes: row[CASE_MANUAL_START_COL.CASE_NOTES] || '',
        systemNotes,
        unknownServicePathEnding,
        visualEndpointStatus: unknownServicePathEnding ? 'unknown-service' : '',
        isPathEnding: !continuesAs || /path\s*ends/i.test(continuesAs),
        canPrune: true,
        canGraft: true,
        canBridge: true,
        missingFundsFollowedFallback,
        missingLeftBoundsFallback,
        hydrationStatus: hydrated?.status || 'not_requested',
        hydrationMessage: hydrated?.message || '',
        rawRow: row,
        row: this.buildCaseManualStartTool88Row(row, {
          fromAddress,
          toAddress,
          utcDate,
          amount,
          token,
          fundsFollowed,
          priorBalance,
          hash,
          transactionHash: hash,
          txHash: hash,
          leftBoundsDisplay,
          leftBoundsNumeric,
          continuesAs,
          attribution,
          fromAttributionLabel,
          toAttributionLabel,
          systemNotes,
          chainId,
          chainName,
          hopLogic: row[21] || '',
          caseNotes: row[CASE_MANUAL_START_COL.CASE_NOTES] || ''
        })
      };
    },


    caseManualStartCandidateHasUnknownServiceEndpoint(candidate = {}) {
      const row = Array.isArray(candidate.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate.row) ? candidate.row : []);
      const continuesText = this.caseManualNormalizeContinuesAsText(candidate.continuesAs || row[CASE_MANUAL_START_COL.CONTINUES_AS] || '');
      const explicitlyContinues = /continues\s+as\b/iu.test(continuesText);
      if (explicitlyContinues && candidate.forceUnknownServiceEndpoint !== true) return false;
      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const activeAttribution = reverse
        ? this.caseManualCanonicalizeFromAttributionForColumnX(
            candidate.fromAttributionLabel
              || row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION]
              || ''
          )
        : this.caseManualCanonicalizeAttributionForColumnK(
            candidate.toAttributionLabel
              || candidate.attribution
              || row[CASE_MANUAL_START_COL.ATTRIBUTION]
              || ''
          );
      if (reverse && this.caseManualAttributionLabelLooksLikeDeposit(activeAttribution)) return false;
      const text = [
        activeAttribution,
        candidate.systemNotes,
        candidate.analystNotes,
        candidate.caseNotes,
        candidate.continuesAs,
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row[CASE_MANUAL_START_COL.ANALYST_NOTES],
        row[CASE_MANUAL_START_COL.CASE_NOTES],
        row[CASE_MANUAL_START_COL.FROM_LABEL],
        row[CASE_MANUAL_START_COL.CONTINUES_AS]
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      if (!text) return false;
      if (this.caseManualStartCandidateHasMeaningfulEndpointAttribution(candidate)) return false;
      // Column J normally describes the forward receiving side. A historical
      // receiver-side unknown-service ending must not terminate Reverse unless
      // the row also carries a direction-aware reverse endpoint decision.
      if (reverse && !this.caseManualStartTextHasReverseEndpointDecision(text)) return false;
      return this.caseManualUnknownServicePathEndingEvidenceMatches(text, continuesText);
    },

    caseManualUnknownServicePathEndingEvidenceMatches(textValue = '', continuesValue = '') {
      const text = this.caseManualNormalizeText(textValue);
      if (!text) return false;
      const continuesText = this.caseManualNormalizeContinuesAsText(continuesValue || '');
      const explicitlyContinues = /continues\s+as\b/iu.test(continuesText);
      if (explicitlyContinues) return false;
      const hasUnknown = /\bunknown\b/iu.test(text) || /\bunattributed\b/iu.test(text) || /no\s+visible\s+hit/iu.test(text);
      const hasServiceEvidence = this.caseManualTextHasUnknownServiceEvidence(text);
      const hasCurrentPathEnding = /path\s*ends/iu.test(continuesText);
      const hasHistoricalPathEndingEvidence = /path\s*ends|unknown\s+service\s*\/\s*hot[-\s]?wallet[-\s]?like\s+path[-\s]?ending|manual[-_\s]*path[-_\s]*ending|tool\s*88\s+stopped|action\s*[=:]\s*(?:manual[-_\s]*)?path[-_\s]*ending|specialTreatment\s*[:=]\s*potential_service_address/iu.test(text);
      const hasTraceThroughEvidence = /action\s*[=:]\s*traced?[-_\s]*through|forward\s+fifo\s+traced\s+through|intentionally\s+traced\s+through|trace[-_\s]*through\s+preview|bypass(?:ed)?\s+[^.\n]*hot[-\s]?wallet/iu.test(text);
      if (hasTraceThroughEvidence && !hasCurrentPathEnding && !hasHistoricalPathEndingEvidence) return false;
      return Boolean(hasUnknown && hasServiceEvidence && (hasCurrentPathEnding || (!continuesText && hasHistoricalPathEndingEvidence)));
    },

    caseManualTextHasUnknownServiceEvidence(value = '') {
      const text = this.caseManualNormalizeText(value);
      if (!text) return false;
      return /unattributed\s+exchange\s+hot\s+wallet|service\s+address|service\s*\/\s*hot|service[-\s]?pattern|hot[-\s]?wallet|potential\s+service|additional\s+service\s+checks|high\s+stablecoin\s+flow|high\s+erc20\s+transfer\s+count|trace\s+through\s+again\s+only\s+if|address\s+has\s+features\s+of\s+an\s+unattributed\s+exchange/iu.test(text);
    },

    caseManualRowsContainUnknownServicePathEndingEvidence(rows = []) {
      return (Array.isArray(rows) ? rows : []).some((sourceRow) => {
        if (!sourceRow) return false;
        const normalizedRows = Array.isArray(sourceRow)
          ? [this.caseManualEnsureRowWidth(sourceRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceRow.length || 0))]
          : this.normalizeCaseManualStartHopOutputRows([sourceRow]);
        const normalizedEvidence = normalizedRows.some((row) => {
          if (!Array.isArray(row)) return false;
          return this.caseManualStartCandidateHasUnknownServiceEndpoint({
            row,
            rawRow: row,
            systemNotes: row[CASE_MANUAL_START_COL.SYSTEM_NOTES],
            attribution: row[CASE_MANUAL_START_COL.ATTRIBUTION],
            toAttributionLabel: row[CASE_MANUAL_START_COL.ATTRIBUTION],
            analystNotes: row[CASE_MANUAL_START_COL.ANALYST_NOTES],
            caseNotes: row[CASE_MANUAL_START_COL.CASE_NOTES],
            continuesAs: row[CASE_MANUAL_START_COL.CONTINUES_AS],
            toAddress: row[CASE_MANUAL_START_COL.TO_ADDRESS]
          });
        });
        if (normalizedEvidence) return true;
        const rawObject = sourceRow && typeof sourceRow === 'object' && !Array.isArray(sourceRow) ? sourceRow : {};
        const firstNormalizedRow = normalizedRows.find(row => Array.isArray(row)) || [];
        const rawMeaningfulCandidate = {
          row: firstNormalizedRow,
          rawRow: firstNormalizedRow,
          attribution: rawObject.attribution ?? rawObject.attributionData ?? rawObject.entity ?? firstNormalizedRow[CASE_MANUAL_START_COL.ATTRIBUTION] ?? '',
          toAttributionLabel: rawObject.toAttributionLabel ?? rawObject.to_attribution_label ?? rawObject.toLabel ?? rawObject.to_label ?? '',
          toAttribution: rawObject.toAttribution ?? rawObject.to_attribution ?? null
        };
        if (this.caseManualStartCandidateHasMeaningfulEndpointAttribution(rawMeaningfulCandidate)) return false;
        const rawContinuesAs = Array.isArray(sourceRow)
          ? sourceRow[CASE_MANUAL_START_COL.CONTINUES_AS]
          : (rawObject.continuesAs ?? rawObject.continues_as ?? rawObject.continues ?? rawObject.continuesAsText ?? '');
        const rawText = Array.isArray(sourceRow)
          ? [
              sourceRow[CASE_MANUAL_START_COL.SYSTEM_NOTES],
              sourceRow[CASE_MANUAL_START_COL.ATTRIBUTION],
              sourceRow[CASE_MANUAL_START_COL.ANALYST_NOTES],
              sourceRow[CASE_MANUAL_START_COL.CASE_NOTES],
              sourceRow[CASE_MANUAL_START_COL.FROM_LABEL],
              sourceRow[CASE_MANUAL_START_COL.CONTINUES_AS]
            ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ')
          : [
              rawObject.systemNotes,
              rawObject.system_notes,
              rawObject.notes,
              rawObject.attribution,
              rawObject.attributionData,
              rawObject.entity,
              rawObject.toAttributionLabel,
              rawObject.to_attribution_label,
              rawObject.analystNotes,
              rawObject.analyst_notes,
              rawObject.caseNotes,
              rawObject.case_notes,
              rawObject.tool88Notes,
              rawObject.tool88_notes,
              rawObject.internalNotes,
              rawObject.message,
              rawObject.status,
              rawObject.summary,
              rawObject.reason,
              rawObject.fromLabel,
              rawObject.from_label,
              rawObject.continuesAs,
              rawObject.continues_as,
              rawObject.continues,
              rawObject.visualEndpointStatus,
              rawObject.labelCategory,
              rawObject.decision?.title,
              rawObject.decision?.summary,
              rawObject.decision?.action
            ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
        return this.caseManualUnknownServicePathEndingEvidenceMatches(rawText, rawContinuesAs);
      });
    },

    caseManualStartCandidateMatchesInspectorCandidate(candidate = null, inspectorCandidate = null) {
      if (!candidate?.caseManualStartCandidate || !inspectorCandidate?.caseManualStartCandidate) return false;
      const candidateKey = this.buildCaseManualStartHopInspectorCandidateKey(candidate);
      const inspectorKey = this.buildCaseManualStartHopInspectorCandidateKey(inspectorCandidate);
      if (candidateKey && inspectorKey && candidateKey === inspectorKey) return true;
      const candidateRow = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const inspectorRow = Array.isArray(inspectorCandidate.rawRow) ? inspectorCandidate.rawRow : (Array.isArray(inspectorCandidate.row) ? inspectorCandidate.row : []);
      const hashA = this.caseManualNormalizeText(candidate.hash || candidate.txHash || candidate.transactionHash || candidateRow[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const hashB = this.caseManualNormalizeText(inspectorCandidate.hash || inspectorCandidate.txHash || inspectorCandidate.transactionHash || inspectorRow[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const pathA = this.caseManualNormalizeText(candidate.pathLabel || candidate.label || candidateRow[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
      const pathB = this.caseManualNormalizeText(inspectorCandidate.pathLabel || inspectorCandidate.label || inspectorRow[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
      const endpointA = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate));
      const endpointB = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(inspectorCandidate));
      const directionA = this.caseManualStartOutputChoiceDirectionForCandidate(candidate);
      const directionB = this.caseManualStartOutputChoiceDirectionForCandidate(inspectorCandidate);
      return Boolean(
        hashA
          && hashB
          && this.caseManualTransactionHashesMatch(hashA, hashB)
          && directionA === directionB
          && (!pathA || !pathB || pathA === pathB)
          && (!endpointA || !endpointB || endpointA === endpointB)
      );
    },

    caseManualStartCandidateWithCurrentMainRow(candidate = null) {
      if (!candidate?.caseManualStartCandidate) return candidate;
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate(candidate);
      if (!Array.isArray(currentRow) || !currentRow.length) return candidate;
      const row = this.caseManualEnsureRowWidth(currentRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, currentRow.length || 0));
      const continuesAs = this.caseManualNormalizeContinuesAsText(row[CASE_MANUAL_START_COL.CONTINUES_AS] || '');
      const hasPersistedContinuation = /^Continues\s+as\b/iu.test(continuesAs);
      return {
        ...candidate,
        row,
        rawRow: row,
        pathLabel: row[CASE_MANUAL_START_COL.PATH_LABEL] || candidate.pathLabel || candidate.label || '',
        hash: row[CASE_MANUAL_START_COL.TX_HASH] || candidate.hash || candidate.txHash || candidate.transactionHash || '',
        txHash: row[CASE_MANUAL_START_COL.TX_HASH] || candidate.txHash || candidate.hash || candidate.transactionHash || '',
        transactionHash: row[CASE_MANUAL_START_COL.TX_HASH] || candidate.transactionHash || candidate.hash || candidate.txHash || '',
        fromAddress: row[CASE_MANUAL_START_COL.FROM_ADDRESS] || candidate.fromAddress || '',
        toAddress: row[CASE_MANUAL_START_COL.TO_ADDRESS] || candidate.toAddress || '',
        amountText: row[CASE_MANUAL_START_COL.AMOUNT] || '',
        token: row[CASE_MANUAL_START_COL.TOKEN] || '',
        systemNotes: row[CASE_MANUAL_START_COL.SYSTEM_NOTES] || '',
        attribution: row[CASE_MANUAL_START_COL.ATTRIBUTION] || '',
        toAttributionLabel: row[CASE_MANUAL_START_COL.ATTRIBUTION] || '',
        analystNotes: row[CASE_MANUAL_START_COL.ANALYST_NOTES] || '',
        fundsFollowed: row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || '',
        priorBalance: row[CASE_MANUAL_START_COL.PRIOR_BALANCE] || '',
        fromHash: row[CASE_MANUAL_START_COL.FROM_HASH] || '',
        continuesAs,
        leftBounds: row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] || '',
        leftBoundsDisplay: row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] || '',
        leftBoundsNumeric: row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || '',
        caseNotes: row[CASE_MANUAL_START_COL.CASE_NOTES] || '',
        fromAttributionLabel: row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] || '',
        isPathEnding: /^Path\s+ends\.?$/iu.test(continuesAs),
        caseManualOccurrenceContinuationMissing: hasPersistedContinuation
          ? false
          : Boolean(candidate.caseManualOccurrenceContinuationMissing)
      };
    },

    caseManualStartCandidateMatchesActiveSolve(candidate = null) {
      if (!candidate?.caseManualStartCandidate) return false;
      const selectedNodeCandidate = this.caseInvestigationVisualizerSelectedNode?.caseCandidate
        || this.caseInvestigationVisualizerSelectedNode?.candidate
        || null;
      const activeCandidates = [
        this.caseManualStartHopInspectorPreviewCandidate,
        this.caseManualStartHopInspectorCandidate,
        this.caseManualStartOutputChoiceCandidate,
        this.caseInvestigationVisualizerSelectedCandidate,
        selectedNodeCandidate,
        this.caseManualStartViewportWorkIndicator?.candidate
      ].filter(activeCandidate => activeCandidate?.caseManualStartCandidate);
      return activeCandidates.some(activeCandidate => this.caseManualStartCandidateMatchesInspectorCandidate(candidate, activeCandidate));
    },

    caseManualStartCandidateAttentionVisualIsDeferred(candidate = null) {
      if (!this.caseManualStartCandidateMatchesActiveSolve(candidate)) return false;
      const workIndicatorMatches = Boolean(
        this.caseManualStartViewportWorkIndicator?.active
          && this.caseManualStartViewportWorkIndicator?.candidate?.caseManualStartCandidate
          && this.caseManualStartCandidateMatchesInspectorCandidate(candidate, this.caseManualStartViewportWorkIndicator.candidate)
      );
      return Boolean(
        this.caseManualStartHopInspectorRunning
          || this.caseManualStartHopInspectorTraceLoading
          || this.caseManualStartOutputChoiceLoading
          || this.caseManualStartHopInspectorAutoCheckQueuedKey
          || this.caseManualStartHopInspectorAutoCheckInFlightKey
          || workIndicatorMatches
          || this.caseManualStartViewportAutoActive
          || this.caseManualStartAllFlowsAutoActive
      );
    },


    caseManualNormalizeHopInspectorOutputRow(sourceRow = null) {
      if (Array.isArray(sourceRow)) {
        return this.caseManualEnsureRowWidth(sourceRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceRow.length || 0));
      }
      const row = this.normalizeCaseManualStartHopOutputRows([sourceRow])?.[0];
      if (Array.isArray(row)) return this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row.length || 0));
      if (!sourceRow || typeof sourceRow !== 'object') return [];
      const mapped = Array(CASE_MANUAL_START_COLUMN_COUNT).fill('');
      mapped[CASE_MANUAL_START_COL.PATH_LABEL] = sourceRow.pathLabel ?? sourceRow.label ?? sourceRow.path_label ?? '';
      mapped[CASE_MANUAL_START_COL.TX_HASH] = sourceRow.hash ?? sourceRow.txHash ?? sourceRow.transactionHash ?? sourceRow.transaction_hash ?? '';
      mapped[CASE_MANUAL_START_COL.FROM_ADDRESS] = sourceRow.fromAddress ?? sourceRow.from_address ?? sourceRow.from ?? '';
      mapped[CASE_MANUAL_START_COL.TO_ADDRESS] = sourceRow.toAddress ?? sourceRow.to_address ?? sourceRow.to ?? sourceRow.targetAddress ?? '';
      mapped[CASE_MANUAL_START_COL.DATE] = sourceRow.date ?? sourceRow.dateTime ?? sourceRow.datetime ?? sourceRow.utcDate ?? '';
      mapped[CASE_MANUAL_START_COL.AMOUNT] = sourceRow.amount ?? sourceRow.amountOfTxn ?? sourceRow.amount_of_txn ?? '';
      mapped[CASE_MANUAL_START_COL.TOKEN] = sourceRow.token ?? sourceRow.tokenType ?? sourceRow.token_type ?? sourceRow.symbol ?? sourceRow.tokenSymbol ?? '';
      mapped[CASE_MANUAL_START_COL.SYSTEM_NOTES] = sourceRow.notes ?? sourceRow.systemNotes ?? sourceRow.system_notes ?? '';
      mapped[CASE_MANUAL_START_COL.ATTRIBUTION] = sourceRow.attribution ?? sourceRow.toAttribution ?? sourceRow.to_attribution ?? sourceRow.toAttributionLabel ?? sourceRow.to_attribution_label ?? sourceRow.attributionData ?? sourceRow.entity ?? '';
      mapped[CASE_MANUAL_START_COL.ANALYST_NOTES] = sourceRow.analystNotes ?? sourceRow.userNotes ?? sourceRow.user_notes ?? '';
      mapped[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = sourceRow.fundsFollowed ?? sourceRow.funds_followed ?? sourceRow.amountFollowedText ?? sourceRow.followedAmountText ?? sourceRow.followed_amount_text ?? '';
      mapped[CASE_MANUAL_START_COL.PRIOR_BALANCE] = sourceRow.caseBuddyPriorBalance
        ?? sourceRow.priorBalanceBeforeTransaction
        ?? sourceRow.prior_balance_before_transaction
        ?? sourceRow.actualPriorBalanceAmount
        ?? sourceRow.accountPriorBalance
        ?? sourceRow.addressBalanceBeforeTransaction
        ?? sourceRow.balanceBeforeTransaction
        ?? sourceRow.balance_before_transaction
        ?? sourceRow.assetBalanceBefore
        ?? sourceRow.asset_balance_before
        ?? sourceRow.balanceBefore
        ?? sourceRow.balance_before
        ?? '';
      mapped[CASE_MANUAL_START_COL.FROM_HASH] = sourceRow.fromHash ?? sourceRow.from_hash ?? sourceRow.parentHash ?? sourceRow.parent_hash ?? sourceRow.previousHash ?? '';
      mapped[CASE_MANUAL_START_COL.FROM_LABEL] = sourceRow.fromLabel ?? sourceRow.from_label ?? sourceRow.parentLabel ?? sourceRow.parent_label ?? '';
      mapped[CASE_MANUAL_START_COL.CONTINUES_AS] = sourceRow.continuesAs ?? sourceRow.continues_as ?? sourceRow.continues ?? sourceRow.continuesAsText ?? '';
      mapped[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = sourceRow.leftBoundsDisplay
        ?? sourceRow.leftBounds
        ?? sourceRow.left_bounds
        ?? sourceRow.leftBoundAmount
        ?? sourceRow.leftBoundsAmount
        ?? sourceRow.leftBound
        ?? sourceRow.leftBefore
        ?? sourceRow.fundsFollowedInfo?.leftBounds
        ?? '';
      mapped[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = sourceRow.leftBoundsNumeric
        ?? sourceRow.leftBoundsValue
        ?? sourceRow.left_bounds_numeric
        ?? sourceRow.leftBoundAmount
        ?? sourceRow.leftBoundsAmount
        ?? sourceRow.leftBound
        ?? sourceRow.fundsFollowedInfo?.leftBounds
        ?? '';
      mapped[CASE_MANUAL_START_COL.CHAIN_ID] = sourceRow.chainId ?? sourceRow.chain_id ?? '';
      mapped[CASE_MANUAL_START_COL.CHAIN_NAME] = sourceRow.chainName ?? sourceRow.chain_name ?? '';
      mapped[CASE_MANUAL_START_COL.HOP_LOGIC] = sourceRow.hopLogic ?? sourceRow.hop_logic ?? '';
      mapped[CASE_MANUAL_START_COL.CASE_NOTES] = sourceRow.tool88Notes ?? sourceRow.tool88_notes ?? sourceRow.caseNotes ?? sourceRow.case_notes ?? sourceRow.internalNotes ?? '';
      mapped[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] = sourceRow.fromAttribution ?? sourceRow.from_attribution ?? sourceRow.fromAttributionLabel ?? sourceRow.from_attribution_label ?? sourceRow.sourceAttribution ?? sourceRow.source_attribution ?? '';
      return this.caseManualEnsureRowWidth(mapped, CASE_MANUAL_START_COLUMN_COUNT);
    },

    caseManualStartHopInspectorOutputRowMatchesCandidate(sourceRow = null, candidate = null) {
      if (!candidate?.caseManualStartCandidate) return false;
      const row = this.caseManualNormalizeHopInspectorOutputRow(sourceRow);
      if (!Array.isArray(row) || !row.length) return false;
      const candidateRow = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const rowPath = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
      const candidatePath = this.caseManualNormalizeText(candidate.pathLabel || candidate.label || candidateRow[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
      if (rowPath && candidatePath && rowPath !== candidatePath) return false;
      const rowHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const candidateHash = this.caseManualNormalizeText(candidate.hash || candidate.txHash || candidate.transactionHash || candidateRow[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      if (rowHash && candidateHash && !this.caseManualTransactionHashesMatch(rowHash, candidateHash)) return false;
      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const rowEndpoint = this.caseManualNormalizeAddressKey(row[reverse ? CASE_MANUAL_START_COL.FROM_ADDRESS : CASE_MANUAL_START_COL.TO_ADDRESS]);
      const candidateEndpoint = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate));
      if (rowEndpoint && candidateEndpoint && rowEndpoint !== candidateEndpoint) return false;
      const rowFromHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
      const candidateFromHash = this.caseManualNormalizeText(candidate.fromHash || candidate.parentHash || candidateRow[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
      if (rowFromHash && candidateFromHash && !this.caseManualTransactionHashesMatch(rowFromHash, candidateFromHash)) return false;
      const pathMatches = Boolean(rowPath && candidatePath && rowPath === candidatePath);
      const hashMatches = Boolean(rowHash && candidateHash && this.caseManualTransactionHashesMatch(rowHash, candidateHash));
      const endpointMatches = Boolean(rowEndpoint && candidateEndpoint && rowEndpoint === candidateEndpoint);
      const fromHashMatches = Boolean(rowFromHash && candidateFromHash && this.caseManualTransactionHashesMatch(rowFromHash, candidateFromHash));
      if (pathMatches) return true;
      if (hashMatches && (endpointMatches || fromHashMatches)) return true;
      if (fromHashMatches && endpointMatches) return true;
      return false;
    },

    caseManualStartHopInspectorRowsContainCandidateRows(rows = [], candidate = null) {
      const rowList = Array.isArray(rows) ? rows : [];
      if (!rowList.length || !candidate?.caseManualStartCandidate) return false;
      return rowList.some(sourceRow => this.caseManualStartHopInspectorOutputRowMatchesCandidate(sourceRow, candidate));
    },

    caseManualStartHopInspectorRowsContainUnknownServicePathEndingEvidenceForCandidate(rows = [], candidate = null) {
      const rowList = Array.isArray(rows) ? rows : [];
      if (!rowList.length) return false;
      if (candidate?.caseManualStartCandidate && this.caseManualStartCandidateHasMeaningfulEndpointAttribution(candidate)) return false;
      return rowList.some((sourceRow) => {
        if (candidate?.caseManualStartCandidate && !this.caseManualStartHopInspectorOutputRowMatchesCandidate(sourceRow, candidate)) return false;
        const row = this.caseManualNormalizeHopInspectorOutputRow(sourceRow);
        if (!Array.isArray(row) || !row.length) return false;
        return this.caseManualStartCandidateHasUnknownServiceEndpoint({
          row,
          rawRow: row,
          systemNotes: row[CASE_MANUAL_START_COL.SYSTEM_NOTES],
          attribution: row[CASE_MANUAL_START_COL.ATTRIBUTION],
          analystNotes: row[CASE_MANUAL_START_COL.ANALYST_NOTES],
          caseNotes: row[CASE_MANUAL_START_COL.CASE_NOTES],
          continuesAs: row[CASE_MANUAL_START_COL.CONTINUES_AS],
          toAddress: row[CASE_MANUAL_START_COL.TO_ADDRESS]
        });
      });
    },

    caseManualStartHopInspectorSnapshotHasExplicitUnknownServicePathEndingDecision(snapshot = {}) {
      if (!snapshot || typeof snapshot !== 'object') return false;
      const directActionText = [
        snapshot.integrationAction,
        snapshot.decision?.action,
        snapshot.decision?.recommendedAction,
        snapshot.result?.trace?.action,
        snapshot.result?.action,
        snapshot.result?.pathEnding?.action,
        snapshot.result?.pathEnding?.specialTreatment,
        snapshot.lastRunMeta?.action,
        snapshot.lastRunMeta?.specialTreatment
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      const descriptiveText = [
        directActionText,
        snapshot.status,
        snapshot.message,
        snapshot.decision?.title,
        snapshot.decision?.summary,
        Array.isArray(snapshot.decision?.reasons) ? snapshot.decision.reasons.join(' ') : '',
        snapshot.result?.pathEnding?.reason,
        snapshot.result?.pathEnding?.note,
        snapshot.result?.summary
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      const hasUnknownServiceEvidence = /\bunknown\b|\bunattributed\b|no\s+visible\s+hit/iu.test(descriptiveText)
        && this.caseManualTextHasUnknownServiceEvidence(descriptiveText);
      if (!hasUnknownServiceEvidence) return false;
      const hasDirectPathEndingAction = /(?:^|\b)(?:manual[-_\s]*)?path[-_\s]*(?:end|ending)\b|\bpath_end\b|action\s*[=:]\s*(?:manual[-_\s]*)?path[-_\s]*(?:end|ending)|specialTreatment\s*[:=]\s*potential_service_address|\bpotential_service_address\b/iu.test(directActionText);
      const hasDescriptivePathEndingAction = /tool\s*88\s+marked\s+this\s+as\s+an?\s+unknown\s+service\s*\/\s*hot[-\s]?wallet[-\s]?like\s+path[-\s]?ending|unknown\s+service\s*\/\s*hot[-\s]?wallet[-\s]?like\s+path[-\s]?ending\s+evidence|mark\s+as\s+path\s+ending\s*:\s*unknown\s+service|action\s*[=:]\s*(?:manual[-_\s]*)?path[-_\s]*(?:end|ending)|specialTreatment\s*[:=]\s*potential_service_address/iu.test(descriptiveText);
      return Boolean(hasDirectPathEndingAction || hasDescriptivePathEndingAction || snapshot.result?.pathEnding?.specialTreatment === 'potential_service_address' || snapshot.decision?.action === 'path_end');
    },

    caseManualStartHopInspectorSnapshotLooksUnknownServicePathEndingForCandidate(snapshot = {}, candidate = null) {
      if (!snapshot || typeof snapshot !== 'object' || !candidate?.caseManualStartCandidate) return false;
      if (this.caseManualStartCandidateHasMeaningfulEndpointAttribution(candidate)) return false;
      if (this.caseManualStartHopInspectorSnapshotHasManualServiceOverride(snapshot)) return false;
      const snapshotRows = [];
      const addRows = (rows) => {
        if (Array.isArray(rows)) snapshotRows.push(...rows);
      };
      addRows(snapshot.outputRows);
      addRows(snapshot.result?.outputRows);
      addRows(snapshot.rows);
      addRows(snapshot.result?.rows);
      if (this.caseManualStartHopInspectorRowsContainUnknownServicePathEndingEvidenceForCandidate(snapshotRows, candidate)) return true;
      if (!this.caseManualStartSnapshotMatchesCandidate(snapshot, candidate)) return false;
      if (this.caseManualStartCandidateHasMeaningfulEndpointAttribution(candidate)) return false;
      return Boolean(
        this.caseManualStartHopInspectorSnapshotHasExplicitUnknownServicePathEndingDecision(snapshot)
          && this.caseManualStartHopInspectorSnapshotLooksUnknownServicePathEnding(snapshot)
      );
    },

    caseManualStartHopInspectorHasPendingUnknownServicePathEnding(candidate = null) {
      if (!this.caseInvestigationVisualizerManualStartMode || !this.caseManualStartHopInspectorOpen) return false;
      const snapshot = this.caseManualStartHopInspectorSnapshot || {};
      if (this.caseManualStartHopInspectorSnapshotHasManualServiceOverride(snapshot)) return false;
      const inspectorCandidate = this.caseManualStartHopInspectorCandidate || this.caseManualStartHopInspectorSelectedCandidate || null;
      const effectiveCandidate = candidate?.caseManualStartCandidate ? candidate : inspectorCandidate;
      if (!effectiveCandidate?.caseManualStartCandidate) return false;
      if (this.caseManualStartCandidateAttentionVisualIsDeferred(effectiveCandidate)) return false;
      if (candidate?.caseManualStartCandidate && inspectorCandidate?.caseManualStartCandidate && !this.caseManualStartCandidateMatchesInspectorCandidate(candidate, inspectorCandidate)) return false;
      const candidateKey = this.buildCaseManualStartHopInspectorCandidateKey(effectiveCandidate);
      const loadedKey = this.caseManualStartHopInspectorLoadedCandidateKey || '';
      if (candidateKey && loadedKey && candidateKey !== loadedKey && inspectorCandidate?.caseManualStartCandidate && !this.caseManualStartCandidateMatchesInspectorCandidate(effectiveCandidate, inspectorCandidate)) return false;
      if (!this.isCaseManualStartPathEndingCandidate(effectiveCandidate)) return false;
      if (this.caseManualStartCandidateHasMeaningfulEndpointAttribution(effectiveCandidate)) return false;
      return Boolean(
        this.caseManualStartHopInspectorSnapshotLooksUnknownServicePathEndingForCandidate(snapshot, effectiveCandidate)
          || this.caseManualStartHopInspectorRowsContainUnknownServicePathEndingEvidenceForCandidate(this.caseManualStartHopInspectorOutputRows, effectiveCandidate)
      );
    },

    caseManualVisualNodeHasPendingUnknownServicePathEnding(node = {}) {
      if (this.caseManualNormalizeText(node?.type || '').toLowerCase() !== 'address') return false;
      const candidate = node?.caseCandidate || node?.candidate || null;
      if (!candidate?.caseManualStartCandidate) return false;
      const nodeAddress = this.caseManualNormalizeAddressKey(node.address || candidate.address || '');
      const targetAddress = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate));
      if (nodeAddress && targetAddress && nodeAddress !== targetAddress) return false;
      return this.caseManualStartHopInspectorHasPendingUnknownServicePathEnding(candidate);
    },


    caseManualStartCandidateEndpointLabel(candidate = {}, role = 'to') {
      if (candidate?.caseManualSuppressAttributionLabel) return '';
      const normalizedRole = role === 'from' ? 'from' : 'to';
      const activeRole = this.caseManualStartCandidateEndpointRole(candidate);
      if (this.caseManualStartVisualizerOptions?.labelAddresses === false) {
        return normalizedRole === activeRole
          && (this.caseManualLooksLikeUnknownServicePathEnding(candidate) || this.caseManualStartHopInspectorHasPendingUnknownServicePathEnding(candidate))
          ? 'Unknown'
          : '';
      }
      const roleKey = normalizedRole === 'from' ? 'fromAttributionLabel' : 'toAttributionLabel';
      const directLabel = this.caseManualCleanAttributionLabel(candidate?.[roleKey] || '');
      if (directLabel) return directLabel;

      const attributionObject = normalizedRole === 'from' ? candidate?.fromAttribution : candidate?.toAttribution;
      const objectLabel = this.caseManualExtractAttributionLabel?.(attributionObject, { allowColumnKFallback: normalizedRole === 'to' })
        || this.caseManualCleanAttributionLabel(attributionObject?.label || attributionObject?.displayLabel || attributionObject?.display_label || '');
      if (objectLabel) return objectLabel;

      const rawRow = Array.isArray(candidate?.rawRow) ? candidate.rawRow : [];
      const toolRow = Array.isArray(candidate?.row) ? candidate.row : [];
      if (normalizedRole === 'from') {
        const columnXLabel = this.caseManualCanonicalizeFromAttributionForColumnX(
          rawRow[CASE_MANUAL_START_COL.FROM_ATTRIBUTION]
            || toolRow[CASE_MANUAL_START_COL.FROM_ATTRIBUTION]
            || candidate?.fromAttributionLabel
            || ''
        );
        if (columnXLabel) return columnXLabel;
        const address = candidate?.fromAddress || rawRow[CASE_MANUAL_START_COL.FROM_ADDRESS] || toolRow[CASE_MANUAL_START_COL.FROM_ADDRESS] || '';
        const cachedLabel = address && typeof this.caseManualAttributionLabelForAddress === 'function'
          ? this.caseManualAttributionLabelForAddress(address)
          : '';
        if (cachedLabel) return this.caseManualCleanAttributionLabel(cachedLabel);
      }
      if (normalizedRole === 'to') {
        const columnKLabel = this.caseManualCanonicalizeAttributionForColumnK(
          candidate?.attribution
            || rawRow[CASE_MANUAL_START_COL.ATTRIBUTION]
            || toolRow[CASE_MANUAL_START_COL.ATTRIBUTION]
            || ''
        );
        if (columnKLabel) return columnKLabel;
        const address = candidate?.toAddress || rawRow[CASE_MANUAL_START_COL.TO_ADDRESS] || toolRow[CASE_MANUAL_START_COL.TO_ADDRESS] || '';
        const cachedLabel = address && typeof this.caseManualAttributionLabelForAddress === 'function'
          ? this.caseManualAttributionLabelForAddress(address)
          : '';
        if (cachedLabel) return this.caseManualCleanAttributionLabel(cachedLabel);
        if (candidate.unknownServicePathEnding && activeRole === 'to') return 'Unknown';
      }
      return '';
    },

    caseManualStartAddressVisualRisk(candidate = {}, address = '', pathEnding = false) {
      const labelCategory = this.caseManualAddressLabelCategoryForCandidate(candidate, pathEnding, address);
      return {
        alertRing: ['unknown-service', 'address-poisoning'].includes(labelCategory),
        labelCategory
      };
    },

    caseManualAmountFromFundsFollowed(value) {
      const parsed = this.caseManualParseNumericAmount(value);
      return Number.isFinite(parsed) ? this.caseManualFormatAmount(parsed) : '';
    },

    caseManualTokenFromFundsFollowed(value) {
      const textValue = this.caseManualNormalizeText(value);
      const match = textValue.match(/\b[A-Za-z][A-Za-z0-9._-]{1,15}\b(?!.*\b[A-Za-z][A-Za-z0-9._-]{1,15}\b)/u);
      return match ? match[0].toUpperCase() : '';
    },

    caseManualStablecoinTokenSet() {
      return new Set(['USDT', 'USDC', 'USDD', 'DAI', 'BUSD', 'TUSD', 'USDP', 'GUSD', 'PYUSD', 'FDUSD']);
    },

    caseManualIsStablecoinToken(token = '') {
      const tokenText = this.caseManualNormalizeText(token).toUpperCase();
      return this.caseManualStablecoinTokenSet().has(tokenText);
    },


    caseManualAddressPoisoningAffixMatch(leftAddress = '', rightAddress = '') {
      const normalize = (value) => {
        const text = this.caseManualNormalizeText(value).toLowerCase();
        return /^0x[a-f0-9]{40}$/u.test(text) ? text.slice(2) : '';
      };
      const left = normalize(leftAddress);
      const right = normalize(rightAddress);
      if (!left || !right || left === right || left.length !== right.length) return null;
      let prefixLength = 0;
      while (prefixLength < left.length && left[prefixLength] === right[prefixLength]) prefixLength += 1;
      let suffixLength = 0;
      while (
        suffixLength < left.length - prefixLength
          && left[left.length - 1 - suffixLength] === right[right.length - 1 - suffixLength]
      ) {
        suffixLength += 1;
      }
      const totalMatchLength = prefixLength + suffixLength;
      const qualifies = totalMatchLength >= 8
        && ((prefixLength >= 5 && suffixLength >= 3) || (prefixLength >= 4 && suffixLength >= 4));
      if (!qualifies) return null;
      return { prefixLength, suffixLength, totalMatchLength };
    },

    caseManualStartCandidateHasAddressPoisoning(candidate = {}) {
      const row = Array.isArray(candidate?.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate?.row) ? candidate.row : []);
      const text = [
        candidate?.visualEndpointStatus,
        candidate?.fromAttributionLabel,
        candidate?.toAttributionLabel,
        candidate?.attribution,
        candidate?.systemNotes,
        candidate?.caseNotes,
        row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row?.[CASE_MANUAL_START_COL.ATTRIBUTION],
        row?.[CASE_MANUAL_START_COL.CASE_NOTES],
        row?.[CASE_MANUAL_START_COL.FROM_ATTRIBUTION],
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      return /(?:likely\s+)?address[-_\s]*poison(?:ing)?|poisoning[-_\s]*dust|action\s*=\s*address-poisoning-ending/iu.test(text);
    },

    caseManualStartCandidateHasPersistedAddressPoisoningEnding(candidate = {}, currentRow = null) {
      if (!candidate?.caseManualStartCandidate) return false;
      const resolvedRow = Array.isArray(currentRow) && currentRow.length
        ? currentRow
        : (this.findCurrentCaseManualStartMainRowForCandidate(candidate) || []);
      if (!Array.isArray(resolvedRow) || !resolvedRow.length) return false;
      const continuesAs = this.caseManualNormalizeContinuesAsText(
        resolvedRow[CASE_MANUAL_START_COL.CONTINUES_AS] || candidate.continuesAs || ''
      );
      if (!/^path\s+ends\.?$/iu.test(continuesAs)) return false;

      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const attributionColumn = reverse
        ? CASE_MANUAL_START_COL.FROM_ATTRIBUTION
        : CASE_MANUAL_START_COL.ATTRIBUTION;
      const activeAttribution = reverse
        ? this.caseManualCanonicalizeFromAttributionForColumnX(resolvedRow[attributionColumn] || '')
        : this.caseManualCanonicalizeAttributionForColumnK(resolvedRow[attributionColumn] || '');
      const text = [
        activeAttribution,
        resolvedRow[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        resolvedRow[CASE_MANUAL_START_COL.CASE_NOTES],
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join('\n');
      const hasPoisoningEvidence = /(?:likely\s+)?address[-_\s]*poison(?:ing)?|poisoning[-_\s]*dust|action\s*=\s*address-poisoning-ending/iu.test(text);
      if (!hasPoisoningEvidence) return false;

      const expectedDirection = reverse ? 'reverse' : 'forward';
      const actionMarkers = Array.from(text.matchAll(/action\s*=\s*address-poisoning-ending([^\n]*)/giu));
      if (!actionMarkers.length) return reverse;
      return actionMarkers.some((match) => {
        const suffix = this.caseManualNormalizeText(match?.[1] || '').toLowerCase();
        const directionMatch = suffix.match(/(?:^|;)\s*direction\s*=\s*(forward|reverse)\b/iu);
        return !directionMatch || directionMatch[1].toLowerCase() === expectedDirection;
      });
    },

    caseManualStartAddressPoisoningEvidenceForCandidate(candidate = {}, options = {}) {
      if (!candidate?.caseManualStartCandidate || !this.caseManualStartCandidateIsReverse(candidate)) return null;
      if (!this.isCaseManualStartPathEndingCandidate(candidate)) return null;
      if (this.caseManualStartCandidateHasAddressPoisoning(candidate)) {
        return { isMatch: true, persisted: true, label: 'Likely Address Poisoning' };
      }
      if (this.caseManualManualStartCandidateLooksKnownBridge(candidate, options.snapshot || {})) return null;
      if (this.caseManualStartCandidateLooksKnownReverseSwap(candidate, options.snapshot || {})) return null;
      if (this.caseManualStartCandidateHasMeaningfulEndpointAttribution(candidate)) return null;

      const matrix = this.getCaseManualStartMainMatrix();
      const allRows = Array.isArray(matrix) ? matrix : [];
      const dataRows = allRows.length && this.caseManualIsHeaderRow(allRows[0]) ? allRows.slice(1) : allRows;
      const candidateHash = this.caseManualNormalizeText(candidate.hash || candidate.txHash || candidate.transactionHash || '').toLowerCase();
      const currentIndex = candidateHash
        ? this.caseManualFindDataRowIndexForCandidate(dataRows, candidate, candidateHash)
        : -1;
      const currentRow = currentIndex >= 0 && Array.isArray(dataRows[currentIndex])
        ? this.caseManualEnsureRowWidth(dataRows[currentIndex], Math.max(CASE_MANUAL_START_COLUMN_COUNT, dataRows[currentIndex].length || 0))
        : this.caseManualEnsureRowWidth(
          (Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : [])),
          CASE_MANUAL_START_COLUMN_COUNT
        );
      if (!currentRow.length) return null;

      const endpointAddress = this.caseManualNormalizeText(
        candidate.fromAddress || currentRow[CASE_MANUAL_START_COL.FROM_ADDRESS] || ''
      );
      const counterpartyAddress = this.caseManualNormalizeText(
        candidate.toAddress || currentRow[CASE_MANUAL_START_COL.TO_ADDRESS] || ''
      );
      const token = this.caseManualNormalizeText(
        candidate.token
          || currentRow[CASE_MANUAL_START_COL.TOKEN]
          || this.caseManualTokenFromFundsFollowed(candidate.fundsFollowed || currentRow[CASE_MANUAL_START_COL.FUNDS_FOLLOWED])
      ).toUpperCase();
      const amount = this.caseManualParseNumericAmount(
        currentRow[CASE_MANUAL_START_COL.AMOUNT] || candidate.amountText || candidate.amount || ''
      );
      const fundsFollowed = this.caseManualParseNumericAmount(
        currentRow[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || candidate.fundsFollowed || ''
      );
      if (!/^0x[a-fA-F0-9]{40}$/u.test(endpointAddress)) return null;
      if (!/^0x[a-fA-F0-9]{40}$/u.test(counterpartyAddress)) return null;
      if (!this.caseManualIsStablecoinToken(token)) return null;
      if (!Number.isFinite(amount) || amount <= 0 || amount > 1) return null;
      if (Number.isFinite(fundsFollowed) && fundsFollowed > 1) return null;

      const parentHash = this.caseManualNormalizeText(currentRow[CASE_MANUAL_START_COL.FROM_HASH] || candidate.fromHash || '');
      if (!parentHash) return null;
      const parentEntries = [];
      for (let index = 0; index < dataRows.length; index += 1) {
        const row = Array.isArray(dataRows[index]) ? dataRows[index] : [];
        const rowHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]);
        if (!rowHash || !this.caseManualTransactionHashesMatch(rowHash, parentHash)) continue;
        let endIndex = index + 1;
        while (endIndex < dataRows.length && !this.caseManualNormalizeText(dataRows[endIndex]?.[CASE_MANUAL_START_COL.TX_HASH])) endIndex += 1;
        parentEntries.push({
          startIndex: index,
          rows: dataRows.slice(index, endIndex).filter(Array.isArray),
          pathLabel: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]),
          hash: rowHash,
        });
      }
      if (!parentEntries.length) return null;

      const counterpartyKey = this.caseManualNormalizeAddressKey(counterpartyAddress);
      let best = null;
      parentEntries.forEach((entry) => {
        const connected = entry.rows.some((row) => (
          this.caseManualNormalizeAddressKey(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]) === counterpartyKey
        ));
        entry.rows.forEach((row) => {
          const lookalikeAddress = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '');
          const match = this.caseManualAddressPoisoningAffixMatch(endpointAddress, lookalikeAddress);
          if (!match) return;
          const score = (connected ? 100 : 0) + (match.totalMatchLength * 10) + match.prefixLength + match.suffixLength;
          if (!best || score > best.score) {
            best = {
              ...match,
              score,
              connected,
              lookalikeAddress,
              parentHash: entry.hash,
              parentPathLabel: entry.pathLabel,
              parentDate: this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.DATE]),
            };
          }
        });
      });
      if (!best || !best.connected) return null;

      const amountText = `${this.caseManualFormatDisplayAmountNoRound(String(amount), token, { decimals: 8 })}${token ? ` ${token}` : ''}`;
      const fundsText = Number.isFinite(fundsFollowed)
        ? `${this.caseManualFormatDisplayAmountNoRound(String(fundsFollowed), token, { decimals: 8 })}${token ? ` ${token}` : ''}`
        : '';
      const fundsContext = Number.isFinite(fundsFollowed)
        ? (fundsFollowed <= 0
          ? ' and no case funds were allocated to this branch'
          : ` and ${fundsText} of case funds were allocated to this branch`)
        : '';
      const reason = `Likely address-poisoning dust transfer: the source address closely resembles ${best.lookalikeAddress}, which appears one hop later in this reverse trace (${best.prefixLength} matching leading and ${best.suffixLength} matching trailing hexadecimal characters). The transfer amount is ${amountText}${fundsContext}.`;
      return {
        isMatch: true,
        persisted: false,
        label: 'Likely Address Poisoning',
        endpointAddress,
        counterpartyAddress,
        lookalikeAddress: best.lookalikeAddress,
        prefixLength: best.prefixLength,
        suffixLength: best.suffixLength,
        totalMatchLength: best.totalMatchLength,
        parentHash: best.parentHash,
        parentPathLabel: best.parentPathLabel,
        amount,
        amountText,
        fundsFollowed,
        fundsText,
        token,
        reason,
      };
    },

    caseManualAnnotateAddressPoisoningPathEndingRow(row = [], evidence = {}, candidate = {}) {
      const output = this.caseManualEnsureRowWidth(
        Array.isArray(row) ? row.slice() : [],
        Math.max(CASE_MANUAL_START_COLUMN_COUNT, Array.isArray(row) ? row.length : 0)
      );
      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const attributionColumnIndex = reverse
        ? CASE_MANUAL_START_COL.FROM_ATTRIBUTION
        : CASE_MANUAL_START_COL.ATTRIBUTION;
      output[CASE_MANUAL_START_COL.CONTINUES_AS] = 'Path ends.';
      const existingAttribution = this.caseManualCleanAttributionLabel(output[attributionColumnIndex]);
      if (!existingAttribution || /^unknown$/iu.test(existingAttribution) || /address[-_\s]*poison/iu.test(existingAttribution)) {
        output[attributionColumnIndex] = evidence.label || 'Likely Address Poisoning';
      }
      output[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualAppendUniqueLine(
        output[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        'Likely Address Poisoning.'
      );
      if (evidence.reason) {
        output[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualAppendUniqueLine(
          output[CASE_MANUAL_START_COL.SYSTEM_NOTES],
          evidence.reason
        );
      }
      const target = evidence.endpointAddress || this.caseManualStartCandidateEndpointAddress({ ...candidate, row: output, rawRow: output });
      const marker = [
        'Case Buddy Manual Solve: likely address-poisoning dust transfer recorded as a path ending',
        'action=address-poisoning-ending',
        `direction=${reverse ? 'reverse' : 'forward'}`,
        target ? `target=${target}` : '',
        evidence.lookalikeAddress ? `resembles=${evidence.lookalikeAddress}` : '',
        Number.isFinite(evidence.prefixLength) ? `prefix_match=${evidence.prefixLength}` : '',
        Number.isFinite(evidence.suffixLength) ? `suffix_match=${evidence.suffixLength}` : '',
      ].filter(Boolean).join('; ') + '.';
      output[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
        output[CASE_MANUAL_START_COL.CASE_NOTES],
        marker
      );
      return output;
    },

    caseManualExtractNumericParts(value = '') {
      const textValue = this.caseManualNormalizeText(value);
      const match = textValue.match(/[-+]?\d[\d,]*(?:\.\d+)?/u);
      if (!match) return null;
      const numericText = match[0].replace(/,/gu, '');
      const sign = numericText.startsWith('-') ? '-' : '';
      const unsigned = numericText.replace(/^[-+]/u, '');
      const [wholeRaw = '0', fractionRaw = ''] = unsigned.split('.');
      const whole = wholeRaw.replace(/^0+(?=\d)/u, '') || '0';
      const fraction = fractionRaw.replace(/[^0-9]/gu, '');
      const nonZero = /[1-9]/u.test(whole) || /[1-9]/u.test(fraction);
      return { sign, whole, fraction, nonZero };
    },

    caseManualAddCommasToWholeNumber(value = '') {
      const textValue = String(value || '0').replace(/^0+(?=\d)/u, '') || '0';
      return textValue.replace(/\B(?=(\d{3})+(?!\d))/gu, ',');
    },

    caseManualFormatDisplayAmountNoRound(value = '', token = '', options = {}) {
      const parts = this.caseManualExtractNumericParts(value);
      if (!parts) return this.caseManualNormalizeText(value);
      const tokenText = this.caseManualNormalizeText(token || this.caseManualTokenFromFundsFollowed(value)).toUpperCase();
      const requestedDecimals = Number.isInteger(options.decimals)
        ? options.decimals
        : (options.preserveSourcePrecision === true
          ? this.caseManualAllocationDecimalsForValue(value, tokenText, { preserveSourcePrecision: true })
          : (this.caseManualIsStablecoinToken(tokenText) ? 2 : 4));
      const decimals = Math.max(0, requestedDecimals);
      const makeNumberText = (fractionDigits) => {
        const padded = `${parts.fraction}${'0'.repeat(fractionDigits)}`.slice(0, fractionDigits);
        const wholeText = this.caseManualAddCommasToWholeNumber(parts.whole);
        return `${parts.sign}${wholeText}${fractionDigits > 0 ? `.${padded}` : ''}`;
      };
      let output = makeNumberText(decimals);
      if (parts.nonZero && /^-?0(?:\.0+)?$/u.test(output.replace(/,/gu, ''))) {
        const maxDecimals = Math.max(8, Math.min(24, parts.fraction.length || 8));
        for (let nextDecimals = Math.max(decimals + 1, 8); nextDecimals <= maxDecimals; nextDecimals += 1) {
          output = makeNumberText(nextDecimals);
          if (!/^-?0(?:\.0+)?$/u.test(output.replace(/,/gu, ''))) break;
        }
      }
      return output;
    },

    caseManualFormatTransactionAmountColumn(value = '', token = '', options = {}) {
      const textValue = this.caseManualNormalizeText(value);
      if (!textValue) return '';
      const formatted = this.caseManualFormatDisplayAmountNoRound(textValue, token, options);
      return options.preserveSourcePrecision === true
        ? this.caseManualTrimFormattedFractionZeros(formatted, 0)
        : formatted;
    },

    caseManualFormatTransactionAmountWithToken(value = '', token = '') {
      const amountText = this.caseManualFormatTransactionAmountColumn(value, token);
      const tokenText = this.caseManualNormalizeText(token || this.caseManualTokenFromFundsFollowed(value)).toUpperCase();
      if (!amountText) return '';
      return tokenText && !amountText.toUpperCase().endsWith(tokenText) ? `${amountText} ${tokenText}` : amountText;
    },

    caseManualFormatHopInspectorDisplayAmount(value = '', token = '', options = {}) {
      const textValue = this.caseManualNormalizeText(value);
      if (!textValue) return '';
      const numericText = this.caseManualExtractDecimalAmountText(textValue);
      if (!numericText) return textValue;
      const tokenText = this.caseManualNormalizeText(token || this.caseManualTokenFromFundsFollowed(textValue)).toUpperCase();
      return this.caseManualFormatTransactionAmountDisplay(numericText, tokenText, {
        includeToken: options.includeToken !== false
      });
    },

    caseManualFormatHopInspectorAmountOnly(value = '', token = '') {
      return this.caseManualFormatHopInspectorDisplayAmount(value, token, { includeToken: false });
    },

    caseManualFormatHopInspectorAmountWithToken(value = '', token = '') {
      return this.caseManualFormatHopInspectorDisplayAmount(value, token, { includeToken: true });
    },

    caseManualCanonicalizeAttributionForColumnK(value = '') {
      const roleLabels = this.caseManualExtractRoleAttributionLabels(value);
      const toLabel = this.caseManualCleanAttributionLabel(roleLabels.to || '');
      if (toLabel) return toLabel;
      const clean = this.caseManualCleanAttributionLabel(value);
      if (!clean) return '';
      const raw = this.caseManualNormalizeText(value);
      if (/^from\s*:/iu.test(raw) && !/^to\s*:/iu.test(raw)) return '';
      if (/\bfrom\s*:/iu.test(raw) && !/\bto\s*:/iu.test(raw)) return '';
      return clean;
    },

    caseManualNormalizeSwapAttributionComparisonKey(value = '') {
      return this.caseManualCleanAttributionLabel(value)
        .replace(/\s*\((?:swap|swap\s+like|swap_like|router|contract|contract\s+like|bridge\s+like|wrapped\s+asset|hot\s+wallet|deposit\s+address)[^)]*\)\s*$/iu, '')
        .replace(/\b(?:swap\s+service|swap\s+router|router|dex|amm|contract)\b/giu, ' ')
        .replace(/[^\p{L}\p{N}]+/gu, '')
        .toLowerCase();
    },

    caseManualCleanSolvedSwapServiceLabel(value = '') {
      let label = this.caseManualCleanAttributionLabel(value);
      if (!label) return '';
      label = label
        .replace(/\s*\((?:swap|swap\s+like|swap_like|router|contract|contract\s+like)[^)]*\)\s*$/iu, '')
        .replace(/\.\s*(?:Sent\s+total|Received\s+total|Post[-_\s]*swap|Tool\s*88|Swap\s+contract|Received\s+token\s+contract)\b.*$/iu, '')
        .replace(/\b(?:contract|router)\s*:\s*0x[a-f0-9]{40}\b.*$/iu, '')
        .replace(/\s+/gu, ' ')
        .replace(/[.,;:]+$/gu, '')
        .trim();
      if (!label || /^(?:unknown|unattributed|none|n\/?a|swap|swap\s+service|swap\s+router|router|dex|amm)$/iu.test(label)) return '';
      return label;
    },

    caseManualRowLooksLikeSolvedSwapResult(row = [], options = {}) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const fromAddress = this.caseManualNormalizeAddressKey(normalized[CASE_MANUAL_START_COL.FROM_ADDRESS]);
      const toAddress = this.caseManualNormalizeAddressKey(normalized[CASE_MANUAL_START_COL.TO_ADDRESS]);
      const sameFromTo = Boolean(fromAddress && toAddress && fromAddress === toAddress);
      const hopLogic = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.HOP_LOGIC]);
      const combined = [
        normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        normalized[CASE_MANUAL_START_COL.CASE_NOTES],
        hopLogic,
        options.extraText
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      if (!sameFromTo) return false;
      if (/^SWAP\b/iu.test(hopLogic)) return true;
      return /\bTool\s*88:\s*Swap\s+solved\s+using\b|\bfunds\s+are\s+swapped\s+from\b|\bswapped\s+from\b|\bpost[-_\s]*swap\b|\bswap\s+contract\/?router\b/iu.test(combined);
    },

    caseManualExtractSolvedSwapServiceLabelsFromRow(row = [], options = {}) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const combined = [
        normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        normalized[CASE_MANUAL_START_COL.CASE_NOTES],
        options.extraText
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join('\n');
      if (!combined || !/\bswap\b|\bswapped\s+from\b|\bpost[-_\s]*swap\b/iu.test(combined)) return [];
      const labels = [];
      const seen = new Set();
      const pushLabel = (value) => {
        const label = this.caseManualCleanSolvedSwapServiceLabel(value);
        const key = this.caseManualNormalizeSwapAttributionComparisonKey(label);
        if (!label || !key || seen.has(key)) return;
        seen.add(key);
        labels.push(label);
      };
      const scan = (regex) => {
        let match = regex.exec(combined);
        while (match) {
          pushLabel(match[1]);
          match = regex.exec(combined);
        }
      };
      scan(/\bfunds\s+are\s+swapped\s+from\s+[^.\n]+?\s+to\s+[^.\n]+?\s+using\s+([^,.;\n]+)/giu);
      scan(/\bswapped\s+from\s+[^.\n]+?\s+to\s+[^.\n]+?\s+using\s+([^,.;\n]+)/giu);
      scan(/\bTool\s*88:\s*Swap\s+solved\s+using\s+([^.;\n]+)/giu);
      scan(/\bTool\s*88\s+attribution:\s*([^.;\n(]+?)\s*\((?:swap|swap\s+like|swap_like)[^)]*\)/giu);
      return labels;
    },

    caseManualAttributionLooksLikeSolvedSwapService(value = '', row = [], options = {}) {
      const raw = this.caseManualNormalizeText(value);
      const attrKey = this.caseManualNormalizeSwapAttributionComparisonKey(raw);
      if (!attrKey) return false;
      if (/\((?:swap|swap\s+like|swap_like)\)/iu.test(raw)) return true;
      if (/^(?:swap|swapservice|swaprouter|router|dex|amm)$/iu.test(attrKey)) return true;
      const serviceLabels = this.caseManualExtractSolvedSwapServiceLabelsFromRow(row, options);
      return serviceLabels.some(label => this.caseManualNormalizeSwapAttributionComparisonKey(label) === attrKey);
    },

    caseManualRowSpecificToAttributionForSwapRow(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const toAddress = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TO_ADDRESS]);
      const label = this.caseManualCleanAttributionLabel(this.caseManualAttributionLabelForAddress?.(toAddress) || '');
      if (!label) return '';
      if (this.caseManualAttributionLooksLikeSolvedSwapService(label, normalized)) return '';
      return label;
    },

    caseManualSanitizeSwapServiceAttributionForColumnK(value = '', row = [], options = {}) {
      const canonical = this.caseManualCanonicalizeAttributionForColumnK(value);
      if (!canonical) return '';
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      if (!this.caseManualRowLooksLikeSolvedSwapResult(normalized, options)) return canonical;
      if (!this.caseManualAttributionLooksLikeSolvedSwapService(canonical, normalized, options)) return canonical;
      return this.caseManualRowSpecificToAttributionForSwapRow(normalized);
    },

    parseCaseBuddyEntriesFromRows(rows = []) {
      const entries = [];
      let current = null;
      (Array.isArray(rows) ? rows : []).forEach((sourceRow, rowIndex) => {
        const row = this.caseManualEnsureRowWidth(sourceRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceRow?.length || 0));
        if (rowIndex === 0 && this.caseManualIsHeaderRow(row)) return;
        if (!row.some(value => this.caseManualNormalizeText(value))) return;
        if (this.caseManualIsHeaderRow(row)) {
          current = null;
          return;
        }
        const hash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]);
        if (hash) {
          current = {
            hash,
            startRow: rowIndex,
            rows: [{ rowIndex, row }]
          };
          entries.push(current);
          return;
        }
        if (current) current.rows.push({ rowIndex, row });
      });
      return entries;
    },

    caseManualEntryLeadRow(entry = {}) {
      return Array.isArray(entry?.rows) && entry.rows.length ? (entry.rows[0]?.row || []) : [];
    },

    caseManualEntryHash(entry = {}) {
      return this.caseManualNormalizeText(entry?.hash || this.caseManualEntryLeadRow(entry)?.[CASE_MANUAL_START_COL.TX_HASH]);
    },

    caseManualPathLabelLooksLikeRootBase(value = '') {
      const label = this.caseManualNormalizeText(value);
      if (!label) return true;
      if (label.includes('->') || label.includes('→') || label.includes('.') || /\s/u.test(label)) return false;
      if (/^\d+$/u.test(label)) return true;
      if (/^[A-Z]+$/u.test(label)) return true;
      if (/^[A-Za-z0-9_:/.-]+-\d+$/u.test(label)) return true;
      return false;
    },

    caseManualBuildEntryNarrativeRelationshipIndex(entries = []) {
      const byHash = new Map();
      const parentsByChildHash = new Map();
      const rememberParent = (childHash, parentHash) => {
        const childKey = this.caseManualNormalizeText(childHash).toLowerCase();
        const parentKey = this.caseManualNormalizeText(parentHash).toLowerCase();
        if (!childKey || !parentKey || childKey === parentKey) return;
        if (!parentsByChildHash.has(childKey)) parentsByChildHash.set(childKey, new Set());
        parentsByChildHash.get(childKey).add(parentKey);
      };
      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        const entryHash = this.caseManualEntryHash(entry).toLowerCase();
        if (entryHash && !byHash.has(entryHash)) byHash.set(entryHash, entry);
      });
      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        const parentHash = this.caseManualEntryHash(entry);
        (entry.rows || []).forEach((ref) => {
          const row = ref?.row || [];
          const rowFromHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_HASH]);
          if (rowFromHash) rememberParent(parentHash, rowFromHash);
          this.caseManualExtractHashTokensFromContinuesAs(row[CASE_MANUAL_START_COL.CONTINUES_AS])
            .forEach(childHash => rememberParent(childHash, parentHash));
        });
      });
      return { byHash, parentsByChildHash };
    },

    caseManualFindEntryForRowIndex(entries = [], rowIndex = -1) {
      if (!Number.isInteger(rowIndex)) return null;
      return (Array.isArray(entries) ? entries : []).find(entry => (entry.rows || []).some(ref => ref?.rowIndex === rowIndex)) || null;
    },

    caseManualFindEntryForRow(entries = [], row = []) {
      if (!Array.isArray(row)) return null;
      const direct = (Array.isArray(entries) ? entries : []).find(entry => (entry.rows || []).some(ref => ref?.row === row));
      if (direct) return direct;
      const hashKey = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      if (!hashKey) return null;
      return (Array.isArray(entries) ? entries : []).find(entry => this.caseManualNormalizeText(entry?.hash).toLowerCase() === hashKey) || null;
    },

    isFirstRowOfEntry(rowIndex = -1, rows = [], entries = null, row = []) {
      const parsedEntries = Array.isArray(entries) ? entries : this.parseCaseBuddyEntriesFromRows(rows);
      const entry = this.caseManualFindEntryForRowIndex(parsedEntries, rowIndex) || this.caseManualFindEntryForRow(parsedEntries, row);
      if (entry) return Number(entry.startRow) === Number(rowIndex);
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      return Boolean(this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TX_HASH]));
    },

    getRootFlowEntryKey(entry = {}, entries = [], visualContext = {}) {
      const parsedEntries = Array.isArray(entries) ? entries : [];
      const index = this.caseManualBuildEntryNarrativeRelationshipIndex(parsedEntries);
      let current = entry;
      const seen = new Set();
      while (current) {
        const key = this.caseManualEntryHash(current).toLowerCase();
        if (!key || seen.has(key)) break;
        seen.add(key);
        const parents = Array.from(index.parentsByChildHash.get(key) || []);
        if (!parents.length) return key;
        const next = index.byHash.get(parents[0]);
        if (!next) return parents[0] || key;
        current = next;
      }
      const fallback = this.caseManualNormalizeText(visualContext.rootHash || visualContext.mainHash || entry?.hash).toLowerCase();
      return fallback || '';
    },

    isRootFlowEntry(entry = {}, entries = [], visualContext = {}) {
      if (!entry || !Array.isArray(entry.rows) || !entry.rows.length) return false;
      const leadRow = this.caseManualEntryLeadRow(entry);
      const hashKey = this.caseManualEntryHash(entry).toLowerCase();
      const explicitRoot = this.caseManualNormalizeText(visualContext.rootHash || visualContext.mainHash || '').toLowerCase();
      if (explicitRoot && hashKey && hashKey !== explicitRoot) return false;
      const index = this.caseManualBuildEntryNarrativeRelationshipIndex(entries);
      if (hashKey && index.parentsByChildHash.has(hashKey)) return false;
      if ((entry.rows || []).some(ref => this.caseManualNormalizeText(ref?.row?.[CASE_MANUAL_START_COL.FROM_HASH]))) return false;
      const label = this.caseManualNormalizeText(leadRow[CASE_MANUAL_START_COL.PATH_LABEL]);
      return this.caseManualPathLabelLooksLikeRootBase(label);
    },

    caseManualLineIsTransferNarrative(line = '') {
      const text = this.caseManualNormalizeText(line).replace(/\s+/gu, ' ').trim();
      return /^Funds\s+move\s+from\b.+?\s+into\b.+?\.?$/iu.test(text);
    },

    shouldGenerateTransferNarrativeNote(entry = null, rowIndex = -1, rows = [], context = {}) {
      if (context.noteColumn && !/^(?:j|system|system-notes|system_notes|column-j|column_j)$/iu.test(this.caseManualNormalizeText(context.noteColumn))) return false;
      if (context.forAttributionEvidence === true) return false;
      const row = Array.isArray(context.row) ? context.row : [];
      const entries = Array.isArray(context.entries) ? context.entries : this.parseCaseBuddyEntriesFromRows(rows);
      const resolvedEntry = entry
        || this.caseManualFindEntryForRowIndex(entries, rowIndex)
        || this.caseManualFindEntryForRow(entries, row);
      const fallbackEntry = resolvedEntry || (this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]) ? { hash: row[CASE_MANUAL_START_COL.TX_HASH], startRow: Number.isInteger(rowIndex) ? rowIndex : 1, rows: [{ rowIndex: Number.isInteger(rowIndex) ? rowIndex : 1, row }] } : null);
      if (!fallbackEntry) return false;
      const firstRow = this.isFirstRowOfEntry(rowIndex, rows, entries, row);
      if (!firstRow) return false;
      return this.isRootFlowEntry(fallbackEntry, entries.length ? entries : [fallbackEntry], context.visualContext || context);
    },

    buildRootTransferNarrativeNote(entry = null, context = {}) {
      const row = Array.isArray(context.row) ? context.row : this.caseManualEntryLeadRow(entry || {});
      const attribution = context.attribution || {};
      const fromAddress = this.caseManualNormalizeText(context.fromAddress || row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
      const toAddress = this.caseManualNormalizeText(context.toAddress || row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
      if (!fromAddress && !toAddress) return '';
      const fromText = this.caseManualDescribeAddressForNote(fromAddress, attribution.fromLabel || context.fromAttributionLabel || '');
      const toText = this.caseManualDescribeAddressForNote(toAddress, attribution.toLabel || context.toAttributionLabel || '');
      return `Funds move from ${fromText} into ${toText}.`;
    },

    stripTransferNarrativeFromDescendantNotes(notes = '', context = {}) {
      const rows = Array.isArray(context.rows) ? context.rows : [];
      const entries = Array.isArray(context.entries) ? context.entries : this.parseCaseBuddyEntriesFromRows(rows);
      const row = Array.isArray(context.row) ? context.row : [];
      const entry = context.entry || this.caseManualFindEntryForRowIndex(entries, context.rowIndex) || this.caseManualFindEntryForRow(entries, row);
      const allowNarrative = this.shouldGenerateTransferNarrativeNote(entry, context.rowIndex, rows, { ...context, entries, row, noteColumn: 'system-notes' });
      const lines = this.caseManualDeduplicateNoteLines(this.caseManualSplitNoteLinesPreservingBoundaries(notes));
      return lines
        .filter(line => allowNarrative || !this.caseManualLineIsTransferNarrative(line))
        .join('\n');
    },

    composeCaseBuddySystemNotes(existingNotes = '', context = {}) {
      const rows = Array.isArray(context.rows) ? context.rows : [];
      const entries = Array.isArray(context.entries) ? context.entries : this.parseCaseBuddyEntriesFromRows(rows);
      const row = Array.isArray(context.row) ? context.row : [];
      const entry = context.entry || this.caseManualFindEntryForRowIndex(entries, context.rowIndex) || this.caseManualFindEntryForRow(entries, row);
      const allowNarrative = this.shouldGenerateTransferNarrativeNote(entry, context.rowIndex, rows, { ...context, entries, row, noteColumn: 'system-notes' });
      const lines = this.caseManualDeduplicateNoteLines(this.caseManualSplitNoteLinesPreservingBoundaries(existingNotes))
        .filter(line => allowNarrative || !this.caseManualLineIsTransferNarrative(line));
      if (allowNarrative && context.addTransferNarrative !== false) {
        const narrative = this.buildRootTransferNarrativeNote(entry, { ...context, row });
        const narrativeKey = this.caseManualNormalizeText(narrative).toLowerCase();
        if (narrativeKey && !lines.some(line => this.caseManualNormalizeText(line).toLowerCase() === narrativeKey)) {
          lines.unshift(narrative);
        }
      }
      return this.caseManualDeduplicateDormantFundsNotesInText(this.caseManualDeduplicateNoteLines(lines).join('\n'));
    },

    caseManualPruneSystemNotesForCanonicalRow(value = '', row = [], options = {}) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      return this.composeCaseBuddySystemNotes(value, {
        ...options,
        row: normalized,
        rowIndex: Number.isInteger(options.rowIndex) ? options.rowIndex : -1,
        addTransferNarrative: false,
        noteColumn: 'system-notes'
      });
    },

    caseManualEnsureRowWidth(row = [], minWidth = CASE_MANUAL_START_COLUMN_COUNT) {
      const source = Array.isArray(row) ? row : [];
      const width = Math.max(minWidth, source.length || 0);
      return Array.from({ length: width }, (_, index) => this.caseManualNormalizeText(source[index]));
    },

    caseManualHashOrderIndex(hash = '', orderedHashes = []) {
      const value = this.caseManualNormalizeText(hash);
      if (!value || !Array.isArray(orderedHashes) || !orderedHashes.length) return Number.MAX_SAFE_INTEGER;
      const index = orderedHashes.findIndex(candidate => this.caseManualTransactionHashesMatch(value, candidate));
      return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
    },

    caseManualFindEntryEndIndexByHash(dataRows = [], hash = '') {
      const hashKey = this.caseManualNormalizeText(hash).toLowerCase();
      if (!hashKey || !Array.isArray(dataRows)) return -1;
      const startIndex = this.caseManualFindDataRowIndexByHash(dataRows, hashKey);
      if (startIndex < 0) return -1;
      let endIndex = startIndex;
      for (let index = startIndex + 1; index < dataRows.length; index += 1) {
        const row = dataRows[index];
        if (this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH])) break;
        endIndex = index;
      }
      return endIndex;
    },

    buildCaseManualStartTool88Row(baseRow = [], patch = {}) {
      const row = this.caseManualEnsureRowWidth(baseRow);
      const patchHash = this.caseManualNormalizeText(patch.hash || patch.transactionHash || patch.txHash || '');
      if (this.caseManualShouldReplaceStoredTransactionHash(row[CASE_MANUAL_START_COL.TX_HASH], patchHash)) {
        row[CASE_MANUAL_START_COL.TX_HASH] = patchHash;
      }
      row[CASE_MANUAL_START_COL.FROM_ADDRESS] = row[CASE_MANUAL_START_COL.FROM_ADDRESS] || patch.fromAddress || '';
      row[CASE_MANUAL_START_COL.TO_ADDRESS] = row[CASE_MANUAL_START_COL.TO_ADDRESS] || patch.toAddress || '';
      row[CASE_MANUAL_START_COL.DATE] = row[CASE_MANUAL_START_COL.DATE] || patch.utcDate || '';
      row[CASE_MANUAL_START_COL.AMOUNT] = row[CASE_MANUAL_START_COL.AMOUNT] || patch.amount || '';
      row[CASE_MANUAL_START_COL.TOKEN] = row[CASE_MANUAL_START_COL.TOKEN] || patch.token || '';
      row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = row[CASE_MANUAL_START_COL.SYSTEM_NOTES] || patch.systemNotes || '';
      const patchToAttribution = this.caseManualCleanAttributionLabel(
        patch.toAttributionLabel || patch.toAttribution?.label || patch.toAttribution?.displayLabel || patch.toAttribution?.display_label || patch.attribution || ''
      );
      const patchFromAttribution = this.caseManualCleanAttributionLabel(
        patch.fromAttributionLabel || patch.fromAttribution?.label || patch.fromAttribution?.displayLabel || patch.fromAttribution?.display_label || ''
      );
      row[CASE_MANUAL_START_COL.ATTRIBUTION] = row[CASE_MANUAL_START_COL.ATTRIBUTION] || patchToAttribution || patch.attribution || '';
      row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] = row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] || patchFromAttribution || '';
      row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || patch.fundsFollowed || '';
      row[CASE_MANUAL_START_COL.PRIOR_BALANCE] = this.caseManualPickAccountPriorBalanceText(row, patch || {}, {
        amount: patch.amount || row[CASE_MANUAL_START_COL.AMOUNT],
        fundsFollowed: patch.fundsFollowed || row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]
      });
      row[CASE_MANUAL_START_COL.CONTINUES_AS] = row[CASE_MANUAL_START_COL.CONTINUES_AS] || patch.continuesAs || 'Path ends.';
      row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] || patch.leftBoundsDisplay || '';
      row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || patch.leftBoundsNumeric || '';
      row[CASE_MANUAL_START_COL.CHAIN_ID] = row[CASE_MANUAL_START_COL.CHAIN_ID] || patch.chainId || '';
      row[CASE_MANUAL_START_COL.CHAIN_NAME] = row[CASE_MANUAL_START_COL.CHAIN_NAME] || patch.chainName || '';
      row[CASE_MANUAL_START_COL.HOP_LOGIC] = row[CASE_MANUAL_START_COL.HOP_LOGIC] || patch.hopLogic || 'FIFO';
      row[CASE_MANUAL_START_COL.CASE_NOTES] = row[CASE_MANUAL_START_COL.CASE_NOTES] || patch.caseNotes || patch.tool88Notes || '';
      row[CASE_MANUAL_START_COL.ATTRIBUTION] = this.caseManualSanitizeSwapServiceAttributionForColumnK(row[CASE_MANUAL_START_COL.ATTRIBUTION], row, {
        extraText: [patch.systemNotes, patch.caseNotes, patch.tool88Notes].filter(Boolean).join('\n')
      });
      const roleNormalizedRow = this.caseManualNormalizeAttributionRoleColumnsForRow(row);
      return this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(roleNormalizedRow));
    },

    applyCaseManualStartHydrationToRow(baseRow = [], candidate = {}) {
      const row = this.caseManualEnsureRowWidth(baseRow);
      const note = candidate.hydrationStatus === 'hydrated'
        ? `Case Buddy manual start: hydrated clear root transaction details from Tool 88 provider data; ready for manual visual hop solving.`
        : '';
      row[CASE_MANUAL_START_COL.PATH_LABEL] = row[CASE_MANUAL_START_COL.PATH_LABEL] || candidate.pathLabel || '';
      const candidateHash = this.caseManualNormalizeText(candidate.hash || candidate.transactionHash || candidate.txHash || '');
      if (this.caseManualShouldReplaceStoredTransactionHash(row[CASE_MANUAL_START_COL.TX_HASH], candidateHash)) {
        row[CASE_MANUAL_START_COL.TX_HASH] = candidateHash;
      } else {
        row[CASE_MANUAL_START_COL.TX_HASH] = row[CASE_MANUAL_START_COL.TX_HASH] || candidateHash || '';
      }
      row[CASE_MANUAL_START_COL.FROM_ADDRESS] = row[CASE_MANUAL_START_COL.FROM_ADDRESS] || candidate.fromAddress || '';
      row[CASE_MANUAL_START_COL.TO_ADDRESS] = row[CASE_MANUAL_START_COL.TO_ADDRESS] || candidate.toAddress || '';
      row[CASE_MANUAL_START_COL.DATE] = row[CASE_MANUAL_START_COL.DATE] || candidate.utcDate || '';
      row[CASE_MANUAL_START_COL.AMOUNT] = row[CASE_MANUAL_START_COL.AMOUNT] || candidate.amount || this.caseManualAmountFromFundsFollowed(candidate.fundsFollowed) || '';
      row[CASE_MANUAL_START_COL.TOKEN] = row[CASE_MANUAL_START_COL.TOKEN] || candidate.token || this.caseManualTokenFromFundsFollowed(candidate.fundsFollowed) || '';
      row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = row[CASE_MANUAL_START_COL.SYSTEM_NOTES] || candidate.systemNotes || '';
      if (note) row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(row[CASE_MANUAL_START_COL.CASE_NOTES], note);
      const candidateToAttribution = this.caseManualCleanAttributionLabel(
        candidate.toAttributionLabel || candidate.toAttribution?.label || candidate.toAttribution?.displayLabel || candidate.toAttribution?.display_label || candidate.attribution || ''
      );
      const candidateFromAttribution = this.caseManualCleanAttributionLabel(
        candidate.fromAttributionLabel || candidate.fromAttribution?.label || candidate.fromAttribution?.displayLabel || candidate.fromAttribution?.display_label || ''
      );
      row[CASE_MANUAL_START_COL.ATTRIBUTION] = row[CASE_MANUAL_START_COL.ATTRIBUTION] || candidateToAttribution || candidate.attribution || '';
      row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] = row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] || candidateFromAttribution || '';
      row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || candidate.fundsFollowed || ([candidate.amount, candidate.token].filter(Boolean).join(' '));
      row[CASE_MANUAL_START_COL.PRIOR_BALANCE] = this.caseManualPickAccountPriorBalanceText(row, candidate || {}, {
        amount: candidate.amount || row[CASE_MANUAL_START_COL.AMOUNT],
        fundsFollowed: candidate.fundsFollowed || row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]
      });
      row[CASE_MANUAL_START_COL.CONTINUES_AS] = row[CASE_MANUAL_START_COL.CONTINUES_AS] || candidate.continuesAs || 'Path ends.';
      row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] || candidate.leftBoundsDisplay || '';
      row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || candidate.leftBoundsNumeric || '';
      row[CASE_MANUAL_START_COL.CHAIN_ID] = row[CASE_MANUAL_START_COL.CHAIN_ID] || candidate.chainId || '';
      row[CASE_MANUAL_START_COL.CHAIN_NAME] = row[CASE_MANUAL_START_COL.CHAIN_NAME] || candidate.chainName || '';
      row[CASE_MANUAL_START_COL.HOP_LOGIC] = row[CASE_MANUAL_START_COL.HOP_LOGIC] || candidate.hopLogic || 'FIFO';
      row[CASE_MANUAL_START_COL.ATTRIBUTION] = this.caseManualSanitizeSwapServiceAttributionForColumnK(row[CASE_MANUAL_START_COL.ATTRIBUTION], row, {
        extraText: [candidate.systemNotes, candidate.caseNotes, candidate.tool88Notes].filter(Boolean).join('\n')
      });
      const roleNormalizedRow = this.caseManualNormalizeAttributionRoleColumnsForRow(row);
      return this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(roleNormalizedRow));
    },

    async replaceCaseManualStartLogicalSheetMatrix(sheetKey, matrix = [], reason = 'Case Buddy manual-start hydration updated sheet data.', options = {}) {
      const workbook = this.$refs.inputSpreadsheet;
      const isCanonicalCaseSheet = this.caseManualIsCanonicalCaseSheetKey(sheetKey);
      const preserveColumnH = isCanonicalCaseSheet && this.caseWorkflowSheetKeyPreservesColumnH(sheetKey);
      const rawRowsForWrite = isCanonicalCaseSheet
        ? this.caseManualCanonicalizeCaseSheetMatrix(matrix, { preserveColumnH })
        : (Array.isArray(matrix) ? matrix.map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))) : []);
      const rows = isCanonicalCaseSheet
        ? rawRowsForWrite.filter((row, index) => (index === 0 && this.caseManualIsHeaderRow(row)) || this.caseManualRowHasEntryData(row))
        : rawRowsForWrite;
      const sheetAliases = [
        sheetKey,
        sheetKey === 'main' ? 'Main Data' : '',
        sheetKey === 'main' ? 'Main' : '',
        sheetKey === 'raw' ? 'Raw Data' : '',
        sheetKey === 'raw' ? 'Raw' : ''
      ].filter((value, index, list) => value && list.indexOf(value) === index);
      const callMaybeAsync = async (owner, methodName, args) => {
        if (!owner || typeof owner[methodName] !== 'function') return false;
        const result = owner[methodName](...args);
        if (result && typeof result.then === 'function') await result;
        return result !== false;
      };
      let wrote = false;
      for (const alias of sheetAliases) {
        if (await callMaybeAsync(this, 'setMatrixForLogicalSheet', [alias, rows, reason])) { wrote = true; break; }
        if (await callMaybeAsync(this, 'replaceLogicalSheetMatrix', [alias, rows, reason])) { wrote = true; break; }
        if (await callMaybeAsync(this, 'setLogicalSheetMatrix', [alias, rows, reason])) { wrote = true; break; }
        if (await callMaybeAsync(workbook, 'setMatrixForLogicalSheet', [alias, rows, reason])) { wrote = true; break; }
        if (await callMaybeAsync(workbook, 'setSheetData', [alias, rows])) { wrote = true; break; }
        if (await callMaybeAsync(workbook, 'loadSheetData', [alias, rows])) { wrote = true; break; }
        if (await callMaybeAsync(workbook, 'setDataForSheet', [alias, rows])) { wrote = true; break; }
        if (await callMaybeAsync(workbook, 'replaceSheetData', [alias, rows])) { wrote = true; break; }
      }
      if (!wrote) {
        if (typeof this.activateLogicalSheet === 'function') {
          await this.activateLogicalSheet(sheetKey);
          await this.$nextTick();
        }
        const activeHot = workbook?.hotInstance || workbook?.hot || workbook?.$refs?.hotTable?.hotInstance || workbook?.$refs?.spreadsheet?.hotInstance || null;
        if (typeof workbook?.loadData === 'function') {
          workbook.loadData(rows);
          wrote = true;
        } else if (typeof activeHot?.loadData === 'function') {
          activeHot.loadData(rows);
          wrote = true;
        }
      }
      if (!wrote) {
        throw new Error(`Could not write ${sheetKey} sheet; the workbook component did not expose a recognized load method.`);
      }
      if (typeof this.markCaseDataDirty === 'function') {
        this.markCaseDataDirty(reason);
      } else if (this.caseSupportState) {
        this.caseSupportState.dirty = true;
        this.caseSupportState.caseDataRevision = (Number(this.caseSupportState.caseDataRevision) || 0) + 1;
      }
      if (typeof this.refreshCaseSummary === 'function') this.refreshCaseSummary();
      await this.$nextTick();
      this.queueCaseManualStartAutosave?.(reason, {
        sheetKey,
        mutationRevision: Number(this.caseSupportState?.caseDataRevision) || 0,
        delayMs: options.autosaveDelayMs
      });
    },

    caseManualStartHydrationActiveHotInstance() {
      const workbook = this.$refs.inputSpreadsheet;
      const directCandidates = [
        workbook?.hot,
        workbook?.hotInstance,
        workbook?.handsontable,
        workbook?.handsontableInstance,
        workbook?.$refs?.hotTable?.hotInstance,
        workbook?.$refs?.hot?.hotInstance,
        workbook?.$refs?.spreadsheet?.hotInstance
      ];
      const direct = directCandidates.find(candidate => candidate && typeof candidate.setDataAtCell === 'function');
      if (direct) return direct;
      const getterNames = ['getHotInstance', 'getHandsontableInstance', 'getActiveHotInstance', 'getActiveHandsontable'];
      for (const name of getterNames) {
        if (typeof workbook?.[name] !== 'function') continue;
        try {
          const candidate = workbook[name]();
          if (candidate && typeof candidate.setDataAtCell === 'function') return candidate;
        } catch (error) {
          console.warn(`Case Buddy manual-start hydration could not read workbook ${name}().`, error);
        }
      }
      return null;
    },

    async forceCaseManualStartHydrationCellWrites(candidates = []) {
      const rows = (Array.isArray(candidates) ? candidates : []).filter(row => ['hydrated', 'attribution_enriched'].includes(row?.hydrationStatus) && Number.isInteger(row.rowIndex));
      if (!rows.length) return false;
      if (typeof this.activateLogicalSheet === 'function') {
        await this.activateLogicalSheet('main');
        await this.$nextTick();
      }
      const hot = this.caseManualStartHydrationActiveHotInstance();
      if (!hot || typeof hot.setDataAtCell !== 'function') return false;
      const source = 'case-buddy-manual-start-hydration';
      const columns = [
        CASE_MANUAL_START_COL.PATH_LABEL,
        CASE_MANUAL_START_COL.TX_HASH,
        CASE_MANUAL_START_COL.FROM_ADDRESS,
        CASE_MANUAL_START_COL.TO_ADDRESS,
        CASE_MANUAL_START_COL.DATE,
        CASE_MANUAL_START_COL.AMOUNT,
        CASE_MANUAL_START_COL.TOKEN,
        CASE_MANUAL_START_COL.SYSTEM_NOTES,
        CASE_MANUAL_START_COL.ATTRIBUTION,
        CASE_MANUAL_START_COL.FUNDS_FOLLOWED,
        CASE_MANUAL_START_COL.PRIOR_BALANCE,
        CASE_MANUAL_START_COL.CONTINUES_AS,
        CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY,
        CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC,
        CASE_MANUAL_START_COL.CHAIN_ID,
        CASE_MANUAL_START_COL.CHAIN_NAME,
        CASE_MANUAL_START_COL.HOP_LOGIC
      ];
      rows.forEach(candidate => {
        const persisted = this.applyCaseManualStartHydrationToRow(candidate.rawRow || candidate.row || [], candidate);
        columns.forEach(colIndex => {
          const currentValue = this.caseManualNormalizeText(candidate.rawRow?.[colIndex]);
          const value = this.caseManualNormalizeText(persisted[colIndex]);
          const shouldWriteHash = colIndex === CASE_MANUAL_START_COL.TX_HASH && this.caseManualShouldReplaceStoredTransactionHash(currentValue, value);
          if ((!currentValue && value) || shouldWriteHash) hot.setDataAtCell(candidate.rowIndex, colIndex, value, source);
        });
      });
      if (typeof hot.render === 'function') hot.render();
      const workbook = this.$refs.inputSpreadsheet;
      const saveNames = ['saveActiveSheet', 'saveWorkbook', 'saveToStorage', 'persistWorkbook', 'requestSave'];
      saveNames.forEach(name => {
        if (typeof workbook?.[name] === 'function') {
          try {
            workbook[name]();
          } catch (error) {
            console.warn(`Case Buddy manual-start hydration could not call workbook ${name}().`, error);
          }
        }
      });
      this.queueCaseManualStartAutosave?.('Manual Solve hydration direct cell writes updated Main Data.', { sheetKey: 'main', delayMs: 900 });
      await this.$nextTick();
      return true;
    },

    async writeCaseManualStartHydrationToMain(rows = []) {
      const candidates = (Array.isArray(rows) ? rows : []).filter(row => ['hydrated', 'attribution_enriched'].includes(row?.hydrationStatus) && Number.isInteger(row.rowIndex));
      if (!candidates.length) return { changed: false, updatedCount: 0 };
      const matrix = this.getCaseManualStartMainMatrix().map(row => this.caseManualMigrateLegacyColumnPNotes(this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))));
      let changed = false;
      let updatedCount = 0;
      candidates.forEach(candidate => {
        const rowIndex = candidate.rowIndex;
        if (rowIndex < 0 || rowIndex >= matrix.length) return;
        const before = JSON.stringify(matrix[rowIndex] || []);
        const after = this.applyCaseManualStartHydrationToRow(matrix[rowIndex], candidate);
        if (JSON.stringify(after) !== before) {
          matrix[rowIndex] = after;
          changed = true;
          updatedCount += 1;
        }
      });
      let persisted = false;
      let writeError = '';
      if (changed) {
        try {
          await this.replaceCaseManualStartLogicalSheetMatrix('main', matrix, `Hydrated ${updatedCount} sparse main transaction${updatedCount === 1 ? '' : 's'} for manual visual hop solving.`);
          persisted = true;
        } catch (error) {
          writeError = error?.message || 'unknown spreadsheet write issue';
          console.warn('Case Buddy manual-start hydration matrix write failed; trying direct Main Data cell writes.', error);
        }
        try {
          persisted = await this.forceCaseManualStartHydrationCellWrites(candidates) || persisted;
        } catch (error) {
          writeError = writeError || error?.message || 'unknown direct cell write issue';
          console.warn('Case Buddy manual-start hydration direct cell write failed.', error);
        }
      }
      if (changed && typeof this.refreshRawDataImportCoverage === 'function') this.refreshRawDataImportCoverage();
      return { changed, updatedCount, persisted, writeError };
    },

    buildCaseManualStartHydrationKey(hash, rowIndex = 0) {
      const fileKey = this.caseManualNormalizeText(this.currentProjectFileId || this.selectedProjectFileId || 'unsaved-case').toLowerCase();
      return `${fileKey}:${Number(rowIndex) || 0}:${this.caseManualNormalizeText(hash).toLowerCase()}`;
    },

    caseManualFirstLine(value = '') {
      return this.caseManualNormalizeText(value).split(/\r?\n/u).map(line => line.trim()).find(Boolean) || '';
    },

    caseManualLooksLikePathLabel(value = '') {
      const label = this.caseManualNormalizeText(value);
      if (!label) return false;
      return /^[0-9]+[a-z0-9.]*$/iu.test(label)
        || /^[A-Z]+[0-9a-z.]*$/u.test(label)
        || /^[A-Za-z0-9_-]+-\d+[a-z0-9.]*$/iu.test(label);
    },

    caseManualIncrementAlphaSuffix(suffix = '') {
      const letters = this.caseManualNormalizeText(suffix).toLowerCase().replace(/[^a-z]/gu, '');
      if (!letters) return 'a';
      const chars = letters.split('');
      for (let index = chars.length - 1; index >= 0; index -= 1) {
        if (chars[index] !== 'z') {
          chars[index] = String.fromCharCode(chars[index].charCodeAt(0) + 1);
          return chars.join('');
        }
        chars[index] = 'a';
      }
      return `a${chars.join('')}`;
    },

    caseManualAlphabeticSuffixFromIndex(index = 1) {
      let remaining = Math.max(1, Math.trunc(Number(index) || 1));
      const chars = [];
      while (remaining > 0) {
        remaining -= 1;
        chars.unshift(String.fromCharCode(97 + (remaining % 26)));
        remaining = Math.floor(remaining / 26);
      }
      return chars.join('');
    },

    caseManualIncrementPathLabel(value = '') {
      const label = this.caseManualNormalizeText(value);
      if (!label) return '1a';

      // Tool46D-style simple trunk labels depend on the base label family:
      // numeric/arbitrary-numeric roots go 1 -> 1a -> 1b, while capital-letter
      // roots go D -> D1 -> D2. This keeps Manual Solve aligned without saving
      // a separate transaction-tree model.
      const capitalBaseMatch = label.match(/^([A-Z]+)(\d*)$/u);
      if (capitalBaseMatch) {
        const [, base, numericSuffix = ''] = capitalBaseMatch;
        const next = numericSuffix ? Number(numericSuffix) + 1 : 1;
        return `${base}${Number.isFinite(next) ? next : 1}`;
      }

      const alphaMatch = label.match(/^(.*?)([a-z]+)$/iu);
      if (alphaMatch) return `${alphaMatch[1]}${this.caseManualIncrementAlphaSuffix(alphaMatch[2])}`;
      return `${label}a`;
    },

    caseManualPathLabelRoot(value = '') {
      const label = this.caseManualNormalizeText(value);
      const match = label.match(/^([0-9]+)/u);
      return match ? match[1] : label.replace(/[a-z]+$/iu, '') || label;
    },

    caseManualStartRootOrdinalForRowIndex(rowIndex = 0, fallbackRow = []) {
      const targetIndex = Number(rowIndex);
      const matrix = this.getCaseManualStartMainMatrix();
      if (!Array.isArray(matrix) || matrix.length === 0) return 1;
      const fallbackHash = this.caseManualNormalizeText(fallbackRow?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      let ordinal = 0;
      let ordinalAtOrBeforeTarget = 0;
      for (let index = 0; index < matrix.length; index += 1) {
        const row = this.caseManualEnsureRowWidth(matrix[index] || [], Math.max(CASE_MANUAL_START_COLUMN_COUNT, matrix[index]?.length || 0));
        if (index === 0 && this.caseManualIsHeaderRow(row)) continue;
        const hash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (!hash || !this.caseManualIsLikelyTransactionHash(hash)) continue;
        const fromHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_HASH]);
        if (fromHash) continue;
        ordinal += 1;
        if (Number.isFinite(targetIndex) && index <= targetIndex) ordinalAtOrBeforeTarget = ordinal;
        if (Number.isFinite(targetIndex) && index === targetIndex) return ordinal;
        if (!Number.isFinite(targetIndex) && fallbackHash && hash === fallbackHash) return ordinal;
      }
      if (Number.isFinite(targetIndex) && ordinalAtOrBeforeTarget > 0) return ordinalAtOrBeforeTarget;
      return ordinal > 0 ? ordinal : 1;
    },

    caseManualDeriveFallbackPathLabel(row = [], rowIndex = 0, coverageRow = null) {
      const explicit = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]);
      if (explicit) return explicit;
      const fromHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_HASH]);
      if (fromHash) {
        const fromLabel = this.caseManualFirstLine(row[CASE_MANUAL_START_COL.FROM_LABEL]);
        if (this.caseManualLooksLikePathLabel(fromLabel)) return this.caseManualIncrementPathLabel(fromLabel);
        return '';
      }
      const coverageLabel = this.caseManualNormalizeText(coverageRow?.pathLabel);
      if (coverageLabel) return coverageLabel;
      return `${this.caseManualStartRootOrdinalForRowIndex(rowIndex, row)}`;
    },

    caseManualNormalizeContinuesAsText(value = '') {
      const text = this.caseManualNormalizeText(value);
      if (!text) return '';

      if (/^(path\s*ends\.?\s*)+$/iu.test(text)) {
        return 'Path ends.';
      }

      const continuesMatch = text.match(
        /continues\s+as\s+([A-Za-z0-9:_./-]+(?:\s*,\s*[A-Za-z0-9:_./-]+)*)/iu
      );

      if (continuesMatch) {
        const hashes = continuesMatch[1]
          .split(',')
          .map(hash => this.caseManualNormalizeText(hash))
          .filter(hash => this.caseManualIsLikelyTransactionHash(hash))
          .filter((hash, index, list) => list.findIndex(item => item.toLowerCase() === hash.toLowerCase()) === index);
        return hashes.length ? `Continues as ${hashes.join(', ')}` : 'Path ends.';
      }

      return text;
    },


    caseManualExtractContinuesAsHashes(value = '', knownRows = []) {
      const normalized = this.caseManualNormalizeContinuesAsText(value);
      if (!/^Continues\s+as\b/iu.test(normalized)) return [];
      const known = Array.isArray(knownRows) ? knownRows : [];
      return normalized
        .replace(/^Continues\s+as\s*/iu, '')
        .split(',')
        .map(hash => this.caseManualResolveKnownTransactionHash(hash.trim(), known))
        .map(hash => this.caseManualNormalizeText(hash))
        .filter(hash => hash && this.caseManualIsLikelyTransactionHash(hash))
        .filter((hash, index, list) => list.findIndex(item => item.toLowerCase() === hash.toLowerCase()) === index);
    },

    caseManualSortChildrenByParentContinuesAsOrder(parentRow = [], childItems = [], knownRows = []) {
      const orderedHashes = this.caseManualExtractContinuesAsHashes(parentRow?.[CASE_MANUAL_START_COL.CONTINUES_AS], knownRows)
        .map(hash => hash.toLowerCase());
      if (!orderedHashes.length) return childItems.slice();
      const orderByHash = new Map();
      orderedHashes.forEach((hash, index) => {
        if (hash && !orderByHash.has(hash)) orderByHash.set(hash, index);
      });
      return childItems.slice().sort((left, right) => {
        const leftHash = this.caseManualNormalizeText(left?.hash || left?.row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        const rightHash = this.caseManualNormalizeText(right?.hash || right?.row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        const leftOrder = orderByHash.has(leftHash) ? orderByHash.get(leftHash) : Number.MAX_SAFE_INTEGER;
        const rightOrder = orderByHash.has(rightHash) ? orderByHash.get(rightHash) : Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) return leftOrder - rightOrder;
        return (Number(left?.originalIndex ?? left?.index) || 0) - (Number(right?.originalIndex ?? right?.index) || 0);
      });
    },

    caseManualLeftBoundsPrimaryValue(value = '', fallback = '0', token = '', options = {}) {
      return this.caseManualFormatLeftBoundsDisplayValue(value, fallback, token, options);
    },

    caseManualBuildWayFinderLeftBoundsDisplay(row = [], options = {}) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const precisionOptions = {
        preserveSourcePrecision: options.preserveSourcePrecision === true || this.caseManualRowHasExplicitReverseAllocationEvidence(normalized),
      };
      const existing = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY]);
      const token = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TOKEN]);
      const continuesAs = this.caseManualNormalizeContinuesAsText(normalized[CASE_MANUAL_START_COL.CONTINUES_AS]) || 'Path ends.';
      const leftBound = this.caseManualFormatLeftBoundsDisplayValue(
        existing,
        normalized[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || '0',
        token,
        precisionOptions
      );
      const funds = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
      const amount = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.AMOUNT]);
      const followedSource = funds || [amount, token].filter(Boolean).join(' ');
      if (!followedSource) {
        const hasRealLeftBound = leftBound && leftBound !== '0' && leftBound !== '0.00';
        return hasRealLeftBound ? leftBound : '';
      }
      const followedText = this.caseManualFormatFundsForMain(followedSource, token, precisionOptions) || followedSource;
      const detailFundsText = this.caseManualFormatFundsForDetail(followedSource, token, precisionOptions) || followedSource;
      const balance = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.PRIOR_BALANCE]);
      const balanceDetail = balance ? `, Balance: ${balance}` : '';
      const fromHash = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FROM_HASH]);
      const fromDetail = fromHash ? `, From: ${fromHash}` : '';
      return `${leftBound}
This transaction follows ${followedText}. Details: (Details 1: Funds Followed: ${detailFundsText}${balanceDetail}${fromDetail}) Continues as: ${continuesAs}`;
    },

    caseManualNormalizeMainCaseRowPresentation(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const precisionOptions = {
        preserveSourcePrecision: this.caseManualRowHasExplicitReverseAllocationEvidence(normalized),
      };
      if (this.caseManualLooksLikeBtcInputAddressOnlyContinuation(normalized)) {
        return this.caseManualCleanBtcInputAddressOnlyContinuationRow(normalized);
      }
      if (this.caseManualBtcRootSelectedChoiceShouldZeroPriorBalance(normalized)) {
        normalized[CASE_MANUAL_START_COL.PRIOR_BALANCE] = '0';
      }
      const token = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TOKEN]);
      const amount = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.AMOUNT]);
      if (amount) normalized[CASE_MANUAL_START_COL.AMOUNT] = this.caseManualFormatTransactionAmountColumn(amount, token, precisionOptions) || amount;
      normalized[CASE_MANUAL_START_COL.ATTRIBUTION] = this.caseManualSanitizeSwapServiceAttributionForColumnK(normalized[CASE_MANUAL_START_COL.ATTRIBUTION], normalized);
      normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualPruneSystemNotesForCanonicalRow(normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES], normalized);
      const funds = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
      if (funds) normalized[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = this.caseManualFormatFundsForMain(funds, token, precisionOptions) || funds;
      const balance = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.PRIOR_BALANCE]);
      if (balance) normalized[CASE_MANUAL_START_COL.PRIOR_BALANCE] = this.caseManualFormatPriorBalanceNumber(balance, token) || balance;
      const leftNumeric = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || normalized[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY]);
      if (leftNumeric) normalized[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = this.caseManualFormatLeftBoundsDisplayValue('', leftNumeric, token, precisionOptions) || leftNumeric;
      normalized[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = this.caseManualBuildWayFinderLeftBoundsDisplay(normalized, precisionOptions);
      return normalized;
    },

    caseManualAppendUniqueLine(value = '', line = '') {
      const lines = [];
      const pushLines = (input) => {
        this.caseManualDeduplicateMultilineText(input)
          .split(/\r?\n/u)
          .map(item => item.trim())
          .filter(Boolean)
          .forEach(item => {
            const key = item.toLowerCase();
            if (!lines.some(existing => existing.toLowerCase() === key)) lines.push(item);
          });
      };
      pushLines(value);
      pushLines(line);
      return lines.join('\n');
    },

    caseManualDeduplicateMultilineText(value = '') {
      const seen = new Set();
      const lines = this.caseManualNormalizeText(value)
        .split(/\r?\n/u)
        .flatMap(line => String(line || '').split(/\s{2,}(?=(?:Tool\s*88|Case\s+Buddy|Funds\s+move|Branches\s+as)\b)/iu))
        .map(line => line.replace(/\s+/gu, ' ').trim())
        .filter(Boolean);
      const result = [];
      lines.forEach(line => {
        const key = line.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        result.push(line);
      });
      return result.join('\n');
    },

    caseManualLineLooksLikeTool88Diagnostic(line = '') {
      const text = this.caseManualNormalizeText(line);
      if (!text) return false;
      return /\bTool\s*88\b|Case\s+Buddy\s+manual\s+(?:start|choice|solve)|Case\s+Buddy\s*:\s*Investigator|Case\s+Buddy\s+inferred|action\s*=\s*(?:traced-through|manual-path-ending|accepted-report-boundary|low-value-ended)|trace-through\s+preview|hot[-\s]?wallet\s+assessor|address\s+has\s+features\s+of\s+an\s+unattributed\s+exchange\s+hot\s+wallet|service[-\s]?pattern\s+score|high\s+stablecoin\s+flow|high\s+erc20\s+transfer\s+count/iu.test(text);
    },

    caseManualSplitSystemAndToolNotes(systemNotes = '', toolNotes = '') {
      const systemLines = this.caseManualDeduplicateMultilineText(systemNotes)
        .split(/\r?\n/u)
        .map(line => line.trim())
        .filter(Boolean);
      const toolLines = this.caseManualDeduplicateMultilineText(toolNotes)
        .split(/\r?\n/u)
        .map(line => line.trim())
        .filter(Boolean);
      const cleanSystemLines = [];
      const cleanToolLines = [];
      const pushUnique = (target, line) => {
        const key = this.caseManualNormalizeText(line).toLowerCase();
        if (!key || target.some(existing => existing.toLowerCase() === key)) return;
        target.push(this.caseManualNormalizeText(line));
      };
      systemLines.forEach(line => {
        if (this.caseManualLineLooksLikeTool88Diagnostic(line)) pushUnique(cleanToolLines, line);
        else pushUnique(cleanSystemLines, line);
      });
      toolLines.forEach(line => pushUnique(cleanToolLines, line));
      return {
        systemNotes: cleanSystemLines.join('\n'),
        toolNotes: cleanToolLines.join('\n')
      };
    },

    caseManualMoveTool88OperationalNotesToColumnW(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const notes = this.caseManualSplitSystemAndToolNotes(
        normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        normalized[CASE_MANUAL_START_COL.CASE_NOTES]
      );
      normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES] = notes.systemNotes;
      normalized[CASE_MANUAL_START_COL.CASE_NOTES] = notes.toolNotes;
      return this.caseManualNormalizeAcceptedReportBoundaryForContinuation(normalized);
    },

    caseManualTextLooksLikeBitcoinAddress(value = '') {
      const text = this.caseManualNormalizeText(value);
      if (!text) return false;
      // Main/Raw BTC continuation rows may intentionally contain only Column C
      // From Address, so infer BTC from common mainnet/testnet address shapes.
      return /^(?:bc1|tb1)[a-z0-9]{11,90}$/iu.test(text)
        || /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/u.test(text)
        || /^[2mn][a-km-zA-HJ-NP-Z1-9]{25,39}$/u.test(text);
    },

    caseManualRowNetworkFamily(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const text = [
        normalized[CASE_MANUAL_START_COL.TOKEN],
        normalized[CASE_MANUAL_START_COL.CHAIN_ID],
        normalized[CASE_MANUAL_START_COL.CHAIN_NAME],
        normalized[CASE_MANUAL_START_COL.HOP_LOGIC],
        normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        normalized[CASE_MANUAL_START_COL.CASE_NOTES]
      ].map(value => this.caseManualNormalizeText(value)).join(' ');
      if (/\b(bitcoin|btc)\b/iu.test(text)) return 'btc';
      if ([
        normalized[CASE_MANUAL_START_COL.FROM_ADDRESS],
        normalized[CASE_MANUAL_START_COL.TO_ADDRESS]
      ].some(value => this.caseManualTextLooksLikeBitcoinAddress(value))) return 'btc';
      if (/\btron\b|\btrc20\b/iu.test(text)) return 'tron';
      return 'evm';
    },

    caseManualLooksLikeBtcInputAddressOnlyContinuation(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      if (this.caseManualIsHeaderRow(normalized)) return false;
      if (this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TX_HASH])) return false;
      const fromAddress = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FROM_ADDRESS]);
      if (!this.caseManualTextLooksLikeBitcoinAddress(fromAddress)) return false;
      const fromHash = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FROM_HASH]);
      if (fromHash) return false;
      const fundsFollowed = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
      if (fundsFollowed) return false;
      const continuesAs = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.CONTINUES_AS]);
      if (continuesAs && !/^Path\s+ends\.?$/iu.test(continuesAs)) return false;
      const toolNotes = [
        normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        normalized[CASE_MANUAL_START_COL.CASE_NOTES]
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      const explicitlyInputOnly = /BTC\s+input\s+From\s+address|preserved\s+\d+\s+BTC\s+input\s+From\s+address|selected\s+output\s+entry/iu.test(toolNotes);
      return explicitlyInputOnly || this.caseManualRowNetworkFamily(normalized) === 'btc';
    },

    caseManualCleanBtcInputAddressOnlyContinuationRow(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      if (!this.caseManualLooksLikeBtcInputAddressOnlyContinuation(normalized)) return normalized;
      const cleaned = Array.from({ length: Math.max(CASE_MANUAL_START_COLUMN_COUNT, normalized.length || 0) }, () => '');
      cleaned[CASE_MANUAL_START_COL.FROM_ADDRESS] = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FROM_ADDRESS]);
      return cleaned;
    },

    caseManualCleanBtcInputContinuationRowsInBlock(rows = []) {
      const sourceRows = Array.isArray(rows) ? rows : [];
      return sourceRows.map((row, index) => {
        const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
        if (index > 0 && this.caseManualLooksLikeBtcInputAddressOnlyContinuation(normalized)) {
          return this.caseManualCleanBtcInputAddressOnlyContinuationRow(normalized);
        }
        return normalized;
      });
    },

    caseManualBtcRootSelectedChoiceShouldZeroPriorBalance(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      if (this.caseManualRowNetworkFamily(normalized) !== 'btc') return false;
      if (this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FROM_HASH])) return false;
      const notes = [
        normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        normalized[CASE_MANUAL_START_COL.CASE_NOTES]
      ].map(value => this.caseManualNormalizeText(value)).join(' ');
      if (/Case\s+Buddy\s+manual\s+choice:\s*selected\s+(?:Output|BTC\s+output)/iu.test(notes)) return true;
      const balance = this.caseManualParseNumericAmount(normalized[CASE_MANUAL_START_COL.PRIOR_BALANCE]);
      const amount = this.caseManualParseNumericAmount(normalized[CASE_MANUAL_START_COL.AMOUNT]);
      const funds = this.caseManualParseNumericAmount(normalized[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
      const closeEnough = (left, right) => {
        if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
        const tolerance = Math.max(1e-8, Math.abs(right) * 0.00001);
        return Math.abs(left - right) <= tolerance;
      };
      // Manual Solve BTC root choice: Column N means balance before the chosen
      // receipt/output, not the receipt itself. If provider/hydration copied the
      // selected output amount into N, zero it before Main/Raw writeback.
      return closeEnough(balance, amount) || closeEnough(balance, funds);
    },

    caseManualCleanBtcRootSelectedChoicePriorBalance(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      if (this.caseManualBtcRootSelectedChoiceShouldZeroPriorBalance(normalized)) {
        normalized[CASE_MANUAL_START_COL.PRIOR_BALANCE] = '0';
      }
      return normalized;
    },

    caseManualRowShouldSkipRootPriorBalanceFromFunds() {
      // Column N is the address/token balance before the transaction of interest.
      // It must never be invented from Column F amount, Column M funds-followed,
      // or left-bounds. Tool 88/WayFinder/provider reconstruction may write a real
      // prior balance later; otherwise blank/zero is safer than copying the deposit.
      return true;
    },

    caseManualRowHasContinuationSubstance(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const substantiveColumns = [
        CASE_MANUAL_START_COL.FROM_ADDRESS,
        CASE_MANUAL_START_COL.TO_ADDRESS,
        CASE_MANUAL_START_COL.DATE,
        CASE_MANUAL_START_COL.AMOUNT,
        CASE_MANUAL_START_COL.TOKEN,
        CASE_MANUAL_START_COL.FUNDS_FOLLOWED,
        CASE_MANUAL_START_COL.FROM_HASH,
        CASE_MANUAL_START_COL.CONTINUES_AS,
        CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY,
        CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC
      ];
      const hasSubstantiveValue = substantiveColumns.some(index => {
        const value = this.caseManualNormalizeText(normalized[index]);
        if (!value) return false;
        if ((index === CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY || index === CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC) && /^0(?:\.0+)?(?:\s+[A-Za-z0-9._-]+)?$/u.test(value)) return false;
        if (index === CASE_MANUAL_START_COL.CONTINUES_AS && /^Path\s+ends\.?$/iu.test(value)) return false;
        return true;
      });
      if (hasSubstantiveValue) return true;
      const systemNotes = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES]);
      return /^Branches\s+as\b|funds\s+remain|path\s+ends\s+at\b/iu.test(systemNotes);
    },

    caseManualRowLooksLikeZeroPadding(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      if (this.caseManualIsHeaderRow(normalized)) return false;
      const values = normalized.map(value => this.caseManualNormalizeText(value)).filter(Boolean);
      if (!values.length) return true;
      const nonPadding = values.filter(value => !/^(?:0+(?:\.0+)?|Path\s*ends\.?|FIFO|LIFO|next_chronological)$/iu.test(value));
      if (!nonPadding.length) return true;
      return !this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TX_HASH])
        && nonPadding.every(value => this.caseManualLineLooksLikeTool88Diagnostic(value));
    },

    caseManualRowLooksLikeTool88FakeStatusRow(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      if (this.caseManualIsHeaderRow(normalized)) return false;
      const values = normalized.map(value => this.caseManualNormalizeText(value)).filter(Boolean);
      const joined = values.join(' ').toLowerCase();
      if (/\b88\s+(?:solving|hop)\b/u.test(joined)) return true;
      const hash = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TX_HASH]);
      if (hash && !this.caseManualIsLikelyTransactionHash(hash)) return true;
      const firstCells = normalized.slice(0, 7).map(value => this.caseManualNormalizeText(value).toLowerCase()).filter(Boolean);
      return firstCells.some(value => /^\d+(?:\.\d+)?$/u.test(value))
        && firstCells.some(value => /^(?:ethereum|tron|bitcoin|fifo|lifo|solving|hop)$/u.test(value))
        && !firstCells.some(value => this.caseManualIsLikelyTransactionHash(value));
    },

    caseManualRowHasEntryData(row = []) {
      const normalized = this.caseManualMoveTool88OperationalNotesToColumnW(
        this.caseManualMigrateLegacyColumnPNotes(
          this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))
        )
      );
      if (this.caseManualIsHeaderRow(normalized)) return false;
      if (this.caseManualRowLooksLikeZeroPadding(normalized)) return false;
      if (this.caseManualRowLooksLikeTool88FakeStatusRow(normalized)) return false;
      const hash = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TX_HASH]);
      if (hash) return this.caseManualIsLikelyTransactionHash(hash);

      // In Manual Solve, blank Column-B EVM/TRON rows are almost always duplicate
      // visual/status/padding rows. BTC is the exception: BTC entries can carry
      // legitimate blank-B continuation/output rows inside the same entry.
      if (this.caseManualRowNetworkFamily(normalized) !== 'btc') return false;
      return this.caseManualRowHasContinuationSubstance(normalized);
    },

    caseManualManualSolveRowShouldRetain(row = [], options = {}) {
      const normalized = this.caseManualMoveTool88OperationalNotesToColumnW(
        this.caseManualMigrateLegacyColumnPNotes(
          this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))
        )
      );
      if (this.caseManualIsHeaderRow(normalized)) return options.preserveHeader === true;
      if (this.caseManualRowLooksLikeZeroPadding(normalized)) return false;
      if (this.caseManualRowLooksLikeTool88FakeStatusRow(normalized)) return false;
      const hash = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TX_HASH]);
      if (hash) return this.caseManualIsLikelyTransactionHash(hash);
      if (!this.caseManualRowHasContinuationSubstance(normalized)) return false;
      const allowContinuationRows = options.allowBtcContinuationRows === true || options.allowContinuationRows === true;
      return allowContinuationRows && this.caseManualRowNetworkFamily(normalized) === 'btc';
    },

    caseManualManualSolveOutputRowIsUsable(row = [], options = {}) {
      return this.caseManualManualSolveRowShouldRetain(row, {
        preserveHeader: options.preserveHeader === true,
        allowBtcContinuationRows: options.allowBtcContinuationRows === true
      });
    },

    caseManualManualSolveMainRowShouldRetain(row = [], options = {}) {
      return this.caseManualManualSolveRowShouldRetain(row, {
        preserveHeader: options.preserveHeader === true,
        allowBtcContinuationRows: options.allowBtcContinuationRows !== false
      });
    },

    caseManualCountTransactionRows(rows = []) {
      return (Array.isArray(rows) ? rows : [])
        .filter(row => this.caseManualIsLikelyTransactionHash(this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH])))
        .length;
    },


    caseManualExtractValidTraceExitRowsFromSnapshot(snapshot = {}) {
      const sources = [
        snapshot?.traceExitRows,
        snapshot?.caseBuddyTraceExitRows,
        snapshot?.traceRows,
        snapshot?.result?.traceExitRows,
        snapshot?.result?.caseBuddyTraceExitRows,
        snapshot?.result?.traceRows,
        snapshot?.result?.trace?.exitRows,
        snapshot?.trace?.exitRows,
        snapshot?.traceResult?.exitRows,
        snapshot?.result?.traceResult?.exitRows,
        snapshot?.result?.exitRows,
        snapshot?.exitRows,
        snapshot?.data?.exitRows,
        snapshot?.result?.data?.exitRows
      ];
      const rows = [];
      const seen = new Set();
      sources.forEach(sourceRows => {
        this.normalizeCaseManualStartHopOutputRows(sourceRows || []).forEach(row => {
          if (!this.caseManualManualSolveRowShouldRetain(row)) return;
          const hash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]);
          const hashKey = hash.toLowerCase();
          if (!hashKey || !this.caseManualIsLikelyTransactionHash(hash)) return;
          const branchKey = [
            hashKey,
            this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase(),
            this.caseManualNormalizeAddressKey(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]),
            this.caseManualNormalizeAddressKey(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
            this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]).replace(/,/gu, '').toLowerCase()
          ].join('|');
          if (seen.has(branchKey)) return;
          seen.add(branchKey);
          rows.push(row);
        });
      });
      return rows;
    },

    caseManualSnapshotShouldRequireTraceExitRows(snapshot = {}, activeCandidate = null) {
      const candidate = activeCandidate
        || this.caseManualStartHopInspectorPreviewCandidate
        || this.caseManualStartHopInspectorSelectedCandidate
        || this.caseManualStartHopInspectorCandidate
        || null;
      const reverseCandidate = candidate && this.caseManualStartCandidateIsReverse(candidate);
      if (!reverseCandidate && this.caseManualSnapshotHasDormantResult(snapshot)) return false;
      const actionText = [
        snapshot?.integrationAction,
        snapshot?.action,
        snapshot?.status,
        snapshot?.result?.integrationAction,
        snapshot?.result?.action,
        snapshot?.result?.status,
        snapshot?.trace?.status
      ]
        .map(value => this.caseManualNormalizeText(value))
        .filter(Boolean)
        .join(' ');
      const messageText = [
        snapshot?.message,
        snapshot?.result?.message,
        snapshot?.trace?.message
      ]
        .map(value => this.caseManualNormalizeText(value))
        .filter(Boolean)
        .join(' ');
      const tracePattern = /manual[-_\s]*trace|trace[-_\s]*through|traced[-_\s]*through|trace[-_\s]*fetch|candidate\s+exit|next[-_\s]*hop/iu;
      const nonTraceActionPattern = /manual[-_\s]*(?:path[-_\s]*ending|swap|bridge)|path[-_\s]*ending|low[-_\s]*value|swap|bridge|service[-_\s]*stop|selected[-_\s]*output[-_\s]*missing|dormant|no[-_\s]*exit/iu;
      if (nonTraceActionPattern.test(actionText) && !tracePattern.test(actionText)) return false;
      return tracePattern.test(actionText) || (!actionText && tracePattern.test(messageText)) || /trace[-_\s]*through\s+staged|trace[-_\s]*through\s+action\s+is\s+ready/iu.test(messageText);
    },

    caseManualSnapshotHasValidTraceExitRows(snapshot = {}) {
      return this.caseManualExtractValidTraceExitRowsFromSnapshot(snapshot).length > 0;
    },

    caseManualSnapshotTraceExitHashSet(snapshot = {}, dormantResult = null) {
      const hashes = new Set();
      const addHash = (value) => {
        const hash = this.caseManualNormalizeText(value).toLowerCase();
        if (hash && this.caseManualIsLikelyTransactionHash(hash)) hashes.add(hash);
      };
      this.caseManualExtractValidTraceExitRowsFromSnapshot(snapshot || {}).forEach((row) => addHash(row?.[CASE_MANUAL_START_COL.TX_HASH]));
      const dormant = this.caseManualNormalizeDormantResult(dormantResult)
        || this.caseManualExtractDormantResultFromSnapshot(snapshot || {})
        || dormantResult
        || null;
      if (Array.isArray(dormant?.validExitTransactions)) dormant.validExitTransactions.forEach(addHash);
      const resultExitTransactions = [
        snapshot?.result?.trace?.exitTransactions,
        snapshot?.trace?.exitTransactions,
        snapshot?.traceResult?.exitTransactions,
        snapshot?.result?.traceResult?.exitTransactions
      ];
      resultExitTransactions.forEach((list) => {
        if (!Array.isArray(list)) return;
        list.forEach((entry) => addHash(entry?.continuesAsLabel || entry?.hash || entry?.transactionHash || entry));
      });
      return hashes;
    },


    caseManualEscapeRegExp(value = '') {
      return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },

    caseManualExtractDormantResultFromSnapshot(snapshot = {}) {
      const candidates = [
        snapshot?.dormantResult,
        snapshot?.dormant,
        snapshot?.result?.dormant,
        snapshot?.result?.dormantResult,
        snapshot?.result?.trace?.dormantResult,
        snapshot?.trace?.dormantResult,
        snapshot?.pathEnding?.dormantResult,
        snapshot?.pathEnding?.structuredDormantResult,
        snapshot?.result?.pathEnding?.dormantResult,
        snapshot?.result?.pathEnding?.structuredDormantResult,
        snapshot?.result?.trace?.structuredDormantResult,
        snapshot?.trace?.structuredDormantResult
      ];
      for (const candidate of candidates) {
        const normalized = this.caseManualNormalizeDormantResult(candidate);
        if (normalized) return normalized;
      }
      return null;
    },

    caseManualNormalizeDormantResult(result = null) {
      if (!result || typeof result !== 'object') return null;
      const action = this.caseManualNormalizeText(result.action || result.integrationAction || result.status || '').toLowerCase();
      const note = this.caseManualNormalizeText(result.note || result.columnJNote || '');
      const dormantAmountText = this.caseManualDormantAmountText(result);
      const dormantAmount = this.caseManualParseNumericAmount(dormantAmountText || result.dormantAmount || result.remainingDormantAmount || '');
      const hasDormantAction = /dormant|no[-_\s]*exit/iu.test(action) || /funds\s+remains?\s+at\s+the\s+receiving\s+address/iu.test(note);
      if (!hasDormantAction || !Number.isFinite(dormantAmount) || dormantAmount <= 0) return null;
      const validExitTransactions = Array.isArray(result.validExitTransactions)
        ? result.validExitTransactions.map(value => this.caseManualNormalizeText(value)).filter(Boolean)
        : [];
      const receivingAddress = this.caseManualNormalizeText(result.receivingAddress || result.dormantAddress || result.addressOfInterest || result.toAddress || result.address || '');
      const token = this.caseManualNormalizeText(result.token || this.caseManualExtractTokenFromAmountText(dormantAmountText) || '');
      const isPartial = /partial/iu.test(action) || validExitTransactions.length > 0 || Number(result.validExitCount) > 0;
      return {
        ...result,
        action: isPartial ? 'partial-dormant-with-exits' : 'dormant-ending',
        status: 'valid',
        receivingAddress,
        dormantAddress: receivingAddress,
        token,
        dormantAmount,
        dormantAmountText,
        validExitTransactions,
        validExitCount: validExitTransactions.length || Number(result.validExitCount) || 0,
        note: note || this.caseManualBuildDormantFundsNote(dormantAmountText, !isPartial),
        continuesAs: this.caseManualNormalizeText(result.continuesAs || (!isPartial ? 'Path ends.' : '')),
        evidence: result.evidence && typeof result.evidence === 'object' ? result.evidence : {}
      };
    },

    caseManualSnapshotHasDormantResult(snapshot = {}) {
      return !!this.caseManualExtractDormantResultFromSnapshot(snapshot);
    },

    caseManualSnapshotLooksTraceThroughResult(snapshot = {}, eventName = '') {
      const text = [
        eventName,
        snapshot?.integrationAction,
        snapshot?.action,
        snapshot?.status,
        snapshot?.result?.status,
        snapshot?.result?.trace?.status,
        snapshot?.message,
        snapshot?.result?.message,
        snapshot?.trace?.message
      ]
        .map(value => this.caseManualNormalizeText(value))
        .filter(Boolean)
        .join(' ');
      const dormant = this.caseManualExtractDormantResultFromSnapshot(snapshot);
      const tracePattern = /manual[-_\s]*trace|trace[-_\s]*through|traced[-_\s]*through|trace[-_\s]*fetch|candidate\s+exit|next[-_\s]*hop|valid\s+trace\s+exit/iu;
      return tracePattern.test(text)
        || this.caseManualSnapshotHasValidTraceExitRows(snapshot)
        || (this.caseManualDormantResultIsPartial(dormant) && this.caseManualRowsContainDormantTraceChild(this.caseManualExtractValidTraceExitRowsFromSnapshot(snapshot), dormant));
    },

    caseManualRowsContainDormantTraceChild(rows = [], dormantResult = null, activeCandidate = null, snapshot = null) {
      const dormant = this.caseManualNormalizeDormantResult(dormantResult) || dormantResult || null;
      if (!this.caseManualDormantResultIsPartial(dormant)) return true;
      if (this.caseManualPreviewRowsContainTraceChild(rows, activeCandidate, snapshot)) return true;
      const candidate = activeCandidate || this.caseManualStartHopInspectorPreviewCandidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || null;
      const candidateRow = Array.isArray(candidate?.rawRow) ? candidate.rawRow : (Array.isArray(candidate?.row) ? candidate.row : []);
      const parentHash = this.caseManualNormalizeText(candidate?.hash || candidate?.txHash || candidate?.transactionHash || candidateRow[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const validExitHashes = this.caseManualSnapshotTraceExitHashSet(snapshot || {}, dormant);
      if (!validExitHashes.size && Array.isArray(dormant?.validExitTransactions)) {
        dormant.validExitTransactions.forEach((hash) => {
          const key = this.caseManualNormalizeText(hash).toLowerCase();
          if (key && this.caseManualIsLikelyTransactionHash(key)) validExitHashes.add(key);
        });
      }
      if (!validExitHashes.size) return false;
      return (Array.isArray(rows) ? rows : []).some((row) => {
        if (!this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true })) return false;
        const rowHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        return rowHash && (!parentHash || rowHash !== parentHash) && validExitHashes.has(rowHash);
      });
    },


    caseManualSelectFreshHopInspectorRows(snapshot = {}, fallbackRows = [], activeCandidate = null) {
      const snapshotCandidate = activeCandidate || this.caseManualStartHopInspectorPreviewCandidate || this.caseManualStartHopInspectorCandidate || this.caseManualStartHopInspectorSelectedCandidate || null;
      const freshRows = this.caseManualFilterRowsToSelectedHop(
        this.extractCaseManualStartSnapshotRows(snapshot || {}, { candidate: snapshotCandidate }),
        snapshot || {},
        snapshotCandidate,
        { includeParent: true, includeChildren: true, fallbackParent: true }
      );
      const fallback = Array.isArray(fallbackRows) && fallbackRows.length
        ? fallbackRows
        : this.caseManualStartHopInspectorOutputRows;
      const stateRows = this.caseManualFilterRowsToSelectedHop(
        this.normalizeCaseManualStartHopOutputRows(fallback || [])
          .filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }))
          .filter(row => !this.caseManualRowLooksLikeTool88FakeStatusRow(row)),
        snapshot || {},
        snapshotCandidate,
        { includeParent: true, includeChildren: true, fallbackParent: true }
      );
      if (!freshRows.length) return stateRows;
      const dormant = this.caseManualExtractDormantResultFromSnapshot(snapshot || {});
      const freshHasTraceChild = this.caseManualPreviewRowsContainTraceChild(freshRows, snapshotCandidate, snapshot);
      const stateHasTraceChild = this.caseManualPreviewRowsContainTraceChild(stateRows, snapshotCandidate, snapshot);
      const freshHasPartialDormantChild = this.caseManualDormantResultIsPartial(dormant) && this.caseManualRowsContainDormantTraceChild(freshRows, dormant, snapshotCandidate, snapshot);
      const stateHasPartialDormantChild = this.caseManualDormantResultIsPartial(dormant) && this.caseManualRowsContainDormantTraceChild(stateRows, dormant, snapshotCandidate, snapshot);
      if (freshHasTraceChild || freshHasPartialDormantChild) return freshRows;
      if ((stateHasTraceChild || stateHasPartialDormantChild) && stateRows.length) return stateRows;
      const freshCount = this.caseManualCountTransactionRows(freshRows);
      const stateCount = this.caseManualCountTransactionRows(stateRows);
      if (freshCount && freshCount >= stateCount) return freshRows;
      return stateRows.length ? stateRows : freshRows;
    },

    caseManualShouldFilterRowsToSelectedHop(snapshot = {}, action = '', activeCandidate = null) {
      if (!activeCandidate?.caseManualStartCandidate) return false;
      const text = [
        action,
        snapshot?.integrationAction,
        snapshot?.action,
        snapshot?.status,
        snapshot?.result?.status,
        snapshot?.message,
        snapshot?.result?.message
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      const looksLikeSwapOrBridgeOnly = /\b(?:swap|bridge)\b/iu.test(text) && !/(?:trace|through|path[-_\s]*ending|manual[-_\s]*path)/iu.test(text);
      if (looksLikeSwapOrBridgeOnly) return false;
      return this.caseManualSnapshotLooksTraceThroughResult(snapshot || {}, text)
        || this.caseManualSnapshotHasDormantResult(snapshot || {})
        || this.caseManualSnapshotHasValidTraceExitRows(snapshot || {})
        || this.caseManualSnapshotShouldRequireTraceExitRows(snapshot || {})
        || /(?:trace[-_\s]*through|manual[-_\s]*trace|path[-_\s]*ending|manual[-_\s]*path)/iu.test(text);
    },

    caseManualBuildSelectedHopRowFilterContext(activeCandidate = null, snapshot = {}) {
      const candidate = activeCandidate || this.caseManualStartHopInspectorPreviewCandidate || this.caseManualStartHopInspectorCandidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseInvestigationVisualizerSelectedCandidate || null;
      const candidateRow = Array.isArray(candidate?.rawRow) ? candidate.rawRow : (Array.isArray(candidate?.row) ? candidate.row : []);
      const currentRow = candidate?.caseManualStartCandidate
        ? (this.findCurrentCaseManualStartMainRowForCandidate?.(candidate) || candidateRow || [])
        : (candidateRow || []);
      const parentHash = this.caseManualNormalizeText(
        candidate?.hash
          || candidate?.txHash
          || candidate?.transactionHash
          || currentRow?.[CASE_MANUAL_START_COL.TX_HASH]
          || candidateRow?.[CASE_MANUAL_START_COL.TX_HASH]
          || ''
      ).toLowerCase();
      const reverse = this.caseManualStartCandidateIsReverse(candidate || {});
      const targetAddress = this.caseManualNormalizeAddressKey(
        this.caseManualStartCandidateEndpointAddress({
          ...(candidate || {}),
          row: currentRow?.length ? currentRow : candidateRow,
          rawRow: currentRow?.length ? currentRow : candidateRow,
        })
          || snapshot?.selectedTargetAddress
          || snapshot?.targetAddress
          || snapshot?.result?.selectedTargetAddress
          || (reverse ? snapshot?.selectedEntry?.fromAddress : snapshot?.selectedEntry?.toAddress)
          || ''
      );
      const pathLabel = this.caseManualNormalizeText(
        candidate?.pathLabel
          || currentRow?.[CASE_MANUAL_START_COL.PATH_LABEL]
          || candidateRow?.[CASE_MANUAL_START_COL.PATH_LABEL]
          || ''
      ).toLowerCase();
      const traceExitHashes = this.caseManualSnapshotTraceExitHashSet(snapshot || {}, this.caseManualExtractDormantResultFromSnapshot(snapshot || {}));
      const dormant = this.caseManualExtractDormantResultFromSnapshot(snapshot || {});
      if (Array.isArray(dormant?.validExitTransactions)) {
        dormant.validExitTransactions.forEach((hash) => {
          const key = this.caseManualNormalizeText(hash).toLowerCase();
          if (key && this.caseManualIsLikelyTransactionHash(key)) traceExitHashes.add(key);
        });
      }
      const parentFromHash = this.caseManualNormalizeText(
        currentRow?.[CASE_MANUAL_START_COL.FROM_HASH]
          || candidateRow?.[CASE_MANUAL_START_COL.FROM_HASH]
          || candidate?.fromHash
          || candidate?.parentHash
          || ''
      ).toLowerCase();
      const parentFundsFollowed = this.caseManualNormalizeText(
        currentRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]
          || candidateRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]
          || candidate?.fundsFollowed
          || ''
      );
      const parentLeftBounds = this.caseManualNormalizeText(
        currentRow?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC]
          || currentRow?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY]
          || candidateRow?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC]
          || candidateRow?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY]
          || candidate?.leftBoundsNumeric
          || candidate?.leftBoundsDisplay
          || ''
      );
      const toolRows = this.caseManualStartRowsForTool88?.() || this.getCaseManualStartMainMatrix?.() || [];
      const dataRows = Array.isArray(toolRows) && toolRows.length && this.caseManualIsHeaderRow(toolRows[0]) ? toolRows.slice(1) : (Array.isArray(toolRows) ? toolRows : []);
      const parentHashOccurrenceCount = parentHash
        ? dataRows.filter(row => this.caseManualTransactionHashesMatch(row?.[CASE_MANUAL_START_COL.TX_HASH], parentHash)).length
        : 0;
      return {
        candidate,
        candidateRow,
        currentRow,
        parentHash,
        targetAddress,
        pathLabel,
        parentFromHash,
        parentFundsFollowed,
        parentLeftBounds,
        parentHashOccurrenceCount,
        parentHashIsDuplicate: parentHashOccurrenceCount > 1,
        reverse,
        traceExitHashes,
        hasCandidate: Boolean(candidate?.caseManualStartCandidate && (parentHash || targetAddress || pathLabel))
      };
    },

    caseManualRowMatchesSelectedHopParent(row = [], context = {}) {
      if (!Array.isArray(row) || !context?.hasCandidate) return false;
      const rowHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const rowEndpointAddress = this.caseManualNormalizeAddressKey(
        row?.[context.reverse ? CASE_MANUAL_START_COL.FROM_ADDRESS : CASE_MANUAL_START_COL.TO_ADDRESS]
      );
      const rowPathLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
      const rowFromHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
      const hashMatchesParent = Boolean(context.parentHash && rowHash && this.caseManualTransactionHashesMatch(rowHash, context.parentHash));
      const pathMatchesParent = Boolean(context.pathLabel && rowPathLabel === context.pathLabel && (!context.targetAddress || !rowEndpointAddress || rowEndpointAddress === context.targetAddress));
      const targetMatchesParent = Boolean(context.targetAddress && rowEndpointAddress === context.targetAddress && hashMatchesParent);
      if (!hashMatchesParent && !pathMatchesParent && !targetMatchesParent) return false;
      if (context.parentHashIsDuplicate) {
        if (context.pathLabel && rowPathLabel && rowPathLabel !== context.pathLabel) return false;
        if (context.parentFromHash && rowFromHash && !this.caseManualTransactionHashesMatch(rowFromHash, context.parentFromHash)) return false;
        if (context.parentFundsFollowed && row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] && !this.caseManualAmountValuesApproximatelyEqual(row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED], context.parentFundsFollowed)) return false;
        if (context.parentLeftBounds && (row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY])) {
          const rowLeft = row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY];
          if (!this.caseManualAmountValuesApproximatelyEqual(rowLeft, context.parentLeftBounds)) return false;
        }
      }
      return true;
    },

    caseManualRowMatchesSelectedHopChild(row = [], context = {}) {
      if (!Array.isArray(row) || !context?.hasCandidate) return false;
      if (!this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true })) return false;
      if (this.caseManualRowLooksLikeTool88FakeStatusRow(row)) return false;
      const rowHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      if (!rowHash || !this.caseManualIsLikelyTransactionHash(rowHash)) return false;
      if (context.parentHash && this.caseManualTransactionHashesMatch(rowHash, context.parentHash)) return false;
      const fromHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
      const fromHashMatchesParent = Boolean(context.parentHash && fromHash && this.caseManualTransactionHashesMatch(fromHash, context.parentHash));
      if (fromHashMatchesParent) {
        if (context.parentHashIsDuplicate) {
          const rowFromLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_LABEL]).toLowerCase();
          const rowPathLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
          if (context.pathLabel && rowFromLabel && rowFromLabel === context.pathLabel) return true;
          if (context.pathLabel && rowPathLabel && rowPathLabel !== context.pathLabel && rowPathLabel.startsWith(context.pathLabel)) return true;
          // Keep candidate rows for duplicate parent hashes only when they can be
          // occurrence-matched later as a set. The set-level filter below keeps
          // exact funds-followed rows and rejects obvious sibling rows.
          return true;
        }
        return true;
      }
      if (context.parentHash && fromHash) {
        // Explicit From Hash is occurrence identity. When sibling branches share
        // the same receiving address and then the same outgoing transaction hash,
        // address-only matching would graft the sibling's child onto this branch.
        return false;
      }
      const rowConnectionAddress = this.caseManualNormalizeAddressKey(
        row?.[context.reverse ? CASE_MANUAL_START_COL.TO_ADDRESS : CASE_MANUAL_START_COL.FROM_ADDRESS]
      );
      const connectionMatchesTarget = Boolean(context.targetAddress && rowConnectionAddress && rowConnectionAddress === context.targetAddress);
      if (connectionMatchesTarget) return true;
      const hashIsTraceExit = Boolean(context.traceExitHashes?.has?.(rowHash));
      if (!hashIsTraceExit) return false;
      return !context.parentHash && !context.targetAddress;
    },

    caseManualAddSelectedHopFilterRow(targetRows = [], seenKeys = new Set(), row = []) {
      if (!Array.isArray(row) || !row.length) return;
      const normalized = this.caseManualEnsureRowWidth(row.slice ? row.slice() : row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row.length || 0));
      if (!this.caseManualManualSolveRowShouldRetain(normalized, { allowBtcContinuationRows: true })) return;
      if (this.caseManualRowLooksLikeTool88FakeStatusRow(normalized)) return;
      const hash = this.caseManualNormalizeText(normalized?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const fromHash = this.caseManualNormalizeText(normalized?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
      const label = this.caseManualNormalizeText(normalized?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
      const address = this.caseManualNormalizeAddressKey(normalized?.[CASE_MANUAL_START_COL.TO_ADDRESS] || normalized?.[CASE_MANUAL_START_COL.FROM_ADDRESS] || '');
      const fundsKey = this.caseManualNormalizeText(normalized?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]).replace(/,/gu, '').toLowerCase();
      const leftKey = this.caseManualNormalizeText(normalized?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || normalized?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY]).replace(/,/gu, '').toLowerCase();
      const key = `${hash || 'blank'}|${fromHash || ''}|${label || ''}|${address || ''}|${fundsKey || ''}|${leftKey || ''}`;
      if (seenKeys.has(key)) return;
      seenKeys.add(key);
      targetRows.push(normalized);
    },

    caseManualFilterRowsToSelectedHop(rows = [], snapshot = {}, activeCandidate = null, options = {}) {
      const normalizedRows = this.normalizeCaseManualStartHopOutputRows(rows)
        .map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)))
        .filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }))
        .filter(row => !this.caseManualRowLooksLikeTool88FakeStatusRow(row));
      const context = this.caseManualBuildSelectedHopRowFilterContext(activeCandidate, snapshot || {});
      const shouldFilter = options.force === true || this.caseManualShouldFilterRowsToSelectedHop(snapshot || {}, options.action || '', context.candidate);
      if (!shouldFilter || !context.hasCandidate) return normalizedRows;

      const parentRows = [];
      const childRows = [];
      normalizedRows.forEach((row) => {
        if (this.caseManualRowMatchesSelectedHopParent(row, context)) {
          parentRows.push(row);
          return;
        }
        if (this.caseManualRowMatchesSelectedHopChild(row, context)) childRows.push(row);
      });

      const output = [];
      const seen = new Set();
      const rowMatchesContextAllocation = row => Boolean(
        context.parentFundsFollowed
          && row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]
          && this.caseManualAmountValuesApproximatelyEqual(row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED], context.parentFundsFollowed)
      );
      const rowHasExplicitContextLabel = row => {
        const rowFromLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_LABEL]).toLowerCase();
        const rowPathLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        return Boolean(context.pathLabel && (rowFromLabel === context.pathLabel || (rowPathLabel && rowPathLabel !== context.pathLabel && rowPathLabel.startsWith(context.pathLabel))));
      };
      let filteredChildRows = childRows;
      if (context.parentHashIsDuplicate && context.parentFundsFollowed && childRows.length > 1) {
        const exactAllocationRows = childRows.filter(row => rowMatchesContextAllocation(row) || rowHasExplicitContextLabel(row));
        if (exactAllocationRows.length) filteredChildRows = exactAllocationRows;
      }
      if (options.includeParent !== false) {
        const preferredParent = parentRows.find(row => context.pathLabel && this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase() === context.pathLabel)
          || parentRows.find(row => rowMatchesContextAllocation(row))
          || parentRows.find(row => {
            const hash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
            return context.parentHash && hash && this.caseManualTransactionHashesMatch(hash, context.parentHash);
          })
          || parentRows[0];
        if (preferredParent) {
          this.caseManualAddSelectedHopFilterRow(output, seen, preferredParent);
        } else if (options.fallbackParent !== false && Array.isArray(context.currentRow) && context.currentRow.length) {
          this.caseManualAddSelectedHopFilterRow(output, seen, context.currentRow);
        }
      }
      if (options.includeChildren !== false) {
        filteredChildRows.forEach(row => this.caseManualAddSelectedHopFilterRow(output, seen, row));
        const traceRows = this.caseManualExtractValidTraceExitRowsFromSnapshot(snapshot || {})
          .filter(row => this.caseManualRowMatchesSelectedHopChild(row, context));
        traceRows.forEach(row => this.caseManualAddSelectedHopFilterRow(output, seen, row));
      }
      return output.length ? output : (options.returnOriginalWhenNoMatch ? normalizedRows : []);
    },

    caseManualDormantResultIsPartial(result = null) {
      const dormant = this.caseManualNormalizeDormantResult(result) || result || {};
      return dormant.action === 'partial-dormant-with-exits' || Number(dormant.validExitCount) > 0 || (Array.isArray(dormant.validExitTransactions) && dormant.validExitTransactions.length > 0);
    },

    caseManualDormantAmountText(result = {}) {
      const direct = this.caseManualNormalizeText(result?.dormantAmountText || result?.remainingDormantAmountText || result?.amountText || '');
      const token = this.caseManualNormalizeText(result?.token || this.caseManualExtractTokenFromAmountText(direct) || '');
      if (direct) {
        const parsedDirect = this.caseManualParseNumericAmount(direct);
        if (Number.isFinite(parsedDirect) && parsedDirect > 0) {
          const formattedDirect = this.caseManualFormatAmountForDormantNote(parsedDirect);
          return token ? `${formattedDirect} ${token}` : formattedDirect;
        }
        return direct;
      }
      const amount = Number(result?.dormantAmount ?? result?.remainingDormantAmount ?? result?.amount ?? NaN);
      if (!Number.isFinite(amount) || amount <= 0) return '';
      const formatted = this.caseManualFormatAmountForDormantNote(amount);
      return token ? `${formatted} ${token}` : formatted;
    },

    caseManualFormatAmountForDormantNote(amount = 0) {
      const number = Number(amount);
      if (!Number.isFinite(number)) return '';
      return number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 });
    },

    caseManualExtractTokenFromAmountText(value = '') {
      const match = this.caseManualNormalizeText(value).match(/\b([A-Za-z][A-Za-z0-9._/-]*)\s*$/u);
      return match ? match[1] : '';
    },

    caseManualBuildDormantFundsNote(amountText = '', pathEnds = true) {
      const cleanAmount = this.caseManualNormalizeText(amountText);
      if (!cleanAmount) return '';
      return `A portion of the funds remains at the receiving address (${cleanAmount}).${pathEnds ? ' Path ends.' : ''}`;
    },

    caseManualDormantFundsNoteAmountKey(value = '') {
      return this.caseManualNormalizeText(value).replace(/,/gu, '').replace(/\s+/gu, ' ').trim().toLowerCase();
    },

    caseManualDormantFundsNoteMatches(existingText = '', note = '') {
      const expected = this.caseManualNormalizeText(note).replace(/\s+/gu, ' ').trim();
      if (!expected || !this.caseManualTextHasDormantFundsNote(expected)) return false;
      const expectedMatch = expected.match(/A portion of the funds remains at the receiving address\s*\(([^)]*)\)\.?(?:\s*Path ends\.)?/iu);
      const expectedAmountKey = this.caseManualDormantFundsNoteAmountKey(expectedMatch?.[1] || '');
      const expectedPathEnds = /\bPath\s+ends\b/iu.test(expectedMatch?.[0] || expected);
      if (!expectedAmountKey) return false;
      const source = this.caseManualNormalizeText(existingText).replace(/\s+/gu, ' ');
      const pattern = /A portion of the funds remains at the receiving address\s*\(([^)]*)\)\.?(?:\s*Path ends\.)?/giu;
      let match = pattern.exec(source);
      while (match) {
        const amountKey = this.caseManualDormantFundsNoteAmountKey(match[1] || '');
        const hasPathEnds = /\bPath\s+ends\b/iu.test(match[0] || '');
        if (amountKey && amountKey === expectedAmountKey && (!expectedPathEnds || hasPathEnds)) return true;
        match = pattern.exec(source);
      }
      return false;
    },

    caseManualDormantFundsNoteKeysInLine(line = '') {
      const text = this.caseManualNormalizeText(line).replace(/\s+/gu, ' ');
      const keys = [];
      const pattern = /A portion of the funds remains at the receiving address\s*\(([^)]*)\)\.?(?:\s*Path ends\.)?/giu;
      let match = pattern.exec(text);
      while (match) {
        const amountKey = this.caseManualDormantFundsNoteAmountKey(match[1] || '');
        if (amountKey) keys.push(`${amountKey}|${/\bPath\s+ends\b/iu.test(match[0] || '') ? 'ends' : 'partial'}`);
        match = pattern.exec(text);
      }
      return keys;
    },

    caseManualLineIsStandaloneDormantFundsNote(line = '') {
      return /^A portion of the funds remains at the receiving address\s*\([^)]*\)\.?(?:\s*Path ends\.)?$/iu.test(this.caseManualNormalizeText(line));
    },

    caseManualDeduplicateDormantFundsNotesInText(value = '') {
      const seenDormant = new Set();
      const output = [];
      this.caseManualDeduplicateMultilineText(value)
        .split(/\r?\n/u)
        .map(line => line.trim())
        .filter(Boolean)
        .forEach((line) => {
          const keys = this.caseManualDormantFundsNoteKeysInLine(line);
          const standalone = this.caseManualLineIsStandaloneDormantFundsNote(line);
          if (standalone && keys.length && keys.every(key => seenDormant.has(key))) return;
          output.push(line);
          keys.forEach(key => seenDormant.add(key));
        });
      return output.join('\n');
    },

    caseManualAppendDormantFundsNote(value = '', note = '') {
      const existing = this.caseManualDeduplicateDormantFundsNotesInText(value);
      const line = this.caseManualNormalizeText(note);
      if (!line) return existing;
      if (this.caseManualDormantFundsNoteMatches(existing, line)) return existing;
      return this.caseManualDeduplicateDormantFundsNotesInText(this.caseManualAppendUniqueLine(existing, line));
    },

    caseManualTextHasDormantFundsNote(value = '') {
      return /A portion of the funds remains at the receiving address\s*\([^)]*\)\.?/iu.test(this.caseManualNormalizeText(value));
    },

    caseManualRowHasDormantFundsNote(row = []) {
      return this.caseManualTextHasDormantFundsNote([
        row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row?.[CASE_MANUAL_START_COL.CASE_NOTES],
        row?.[CASE_MANUAL_START_COL.CONTINUES_AS]
      ].map(value => this.caseManualNormalizeText(value)).join(' '));
    },

    caseManualBuildDormantResultFromCandidate(candidate = null) {
      if (!candidate || this.caseManualStartCandidateIsReverse(candidate)) return null;
      const row = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate?.(candidate) || row;
      const text = [
        candidate.systemNotes,
        candidate.caseNotes,
        candidate.continuesAs,
        row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row?.[CASE_MANUAL_START_COL.CASE_NOTES],
        row?.[CASE_MANUAL_START_COL.CONTINUES_AS],
        currentRow?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.CASE_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.CONTINUES_AS]
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join('\n');
      if (!this.caseManualTextHasDormantFundsNote(text)) return null;
      const match = text.match(/A portion of the funds remains at the receiving address\s*\(([^)]*)\)\.?/iu);
      const amountText = this.caseManualNormalizeText(match?.[1] || currentRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || '');
      const continuesAs = this.caseManualNormalizeText(currentRow?.[CASE_MANUAL_START_COL.CONTINUES_AS] || row?.[CASE_MANUAL_START_COL.CONTINUES_AS] || candidate.continuesAs || '');
      const isPartial = continuesAs && !/path\s*ends/iu.test(continuesAs);
      return this.caseManualNormalizeDormantResult({
        action: isPartial ? 'partial-dormant-with-exits' : 'dormant-ending',
        status: 'valid',
        receivingAddress: candidate.toAddress || candidate.targetAddress || candidate.address || currentRow?.[CASE_MANUAL_START_COL.TO_ADDRESS] || row?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '',
        transactionHash: candidate.hash || candidate.txHash || candidate.transactionHash || currentRow?.[CASE_MANUAL_START_COL.TX_HASH] || row?.[CASE_MANUAL_START_COL.TX_HASH] || '',
        token: candidate.token || currentRow?.[CASE_MANUAL_START_COL.TOKEN] || row?.[CASE_MANUAL_START_COL.TOKEN] || this.caseManualExtractTokenFromAmountText(amountText),
        dormantAmountText: amountText,
        note: this.caseManualBuildDormantFundsNote(amountText, !isPartial),
        continuesAs
      });
    },

    caseManualRowsContainDormantUpdate(rows = []) {
      return (Array.isArray(rows) ? rows : []).some(row => this.caseManualRowHasDormantFundsNote(row));
    },

    caseManualBuildDormantRowsForApply(rows = [], snapshot = {}, activeCandidate = null) {
      const candidate = activeCandidate || this.caseManualStartHopInspectorPreviewCandidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || this.caseInvestigationVisualizerSelectedCandidate || {};
      if (this.caseManualStartCandidateIsReverse(candidate)) return rows;
      const dormant = this.caseManualExtractDormantResultFromSnapshot(snapshot);
      if (!dormant) return rows;
      const candidateRow = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate?.(candidate) || candidateRow || [];
      const candidateHash = this.caseManualNormalizeText(candidate.hash || candidate.txHash || candidate.transactionHash || candidateRow?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const sourceRows = Array.isArray(rows) && rows.length ? rows : [currentRow];
      const output = sourceRows.map((row) => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)));
      let index = output.findIndex(row => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase() === candidateHash);
      if (index < 0 && candidateHash && Array.isArray(currentRow) && this.caseManualNormalizeText(currentRow?.[CASE_MANUAL_START_COL.TX_HASH])) {
        output.unshift(this.caseManualEnsureRowWidth(currentRow, CASE_MANUAL_START_COLUMN_COUNT));
        index = 0;
      }
      const targetIndex = index >= 0 ? index : 0;
      const base = this.caseManualEnsureRowWidth(output[targetIndex] || currentRow || [], CASE_MANUAL_START_COLUMN_COUNT);
      const amountText = this.caseManualDormantAmountText(dormant);
      const isPartial = this.caseManualDormantResultIsPartial(dormant);
      const note = this.caseManualBuildDormantFundsNote(amountText, !isPartial);
      base[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualAppendDormantFundsNote(base[CASE_MANUAL_START_COL.SYSTEM_NOTES], note);
      base[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
        base[CASE_MANUAL_START_COL.CASE_NOTES],
        `Tool 88 dormant flow: assessed; action=${dormant.action}; target=${dormant.receivingAddress || base[CASE_MANUAL_START_COL.TO_ADDRESS] || 'unknown'}`
      );
      if (!this.caseManualNormalizeText(currentRow?.[CASE_MANUAL_START_COL.ATTRIBUTION])) {
        base[CASE_MANUAL_START_COL.ATTRIBUTION] = '';
      }
      if (!isPartial) {
        base[CASE_MANUAL_START_COL.CONTINUES_AS] = 'Path ends.';
      } else if (dormant.continuesAs) {
        base[CASE_MANUAL_START_COL.CONTINUES_AS] = dormant.continuesAs;
      }
      base[CASE_MANUAL_START_COL.HOP_LOGIC] = base[CASE_MANUAL_START_COL.HOP_LOGIC] || dormant.hopLogic || candidate.hopLogic || 'FIFO';
      output[targetIndex] = base;
      this.logCaseManualStartDormantDiagnostic?.({ dormant, candidate, currentRow, outputRows: output, note, continuesAs: base[CASE_MANUAL_START_COL.CONTINUES_AS], source: 'caseManualBuildDormantRowsForApply' });
      return output;
    },

    logCaseManualStartDormantDiagnostic(payload = {}) {
      const dormant = this.caseManualNormalizeDormantResult(payload.dormant) || payload.dormant || {};
      const candidate = payload.candidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || {};
      const row = Array.isArray(payload.currentRow) ? payload.currentRow : (Array.isArray(candidate.rawRow) ? candidate.rawRow : Array.isArray(candidate.row) ? candidate.row : []);
      const evidence = dormant.evidence && typeof dormant.evidence === 'object' ? dormant.evidence : {};
      const diagnostic = {
        kind: 'case-buddy-manual-solve-dormant-diagnostic',
        selectedPathLabel: this.caseManualNormalizeText(candidate.pathLabel || row?.[CASE_MANUAL_START_COL.PATH_LABEL]),
        selectedTransactionHash: this.caseManualNormalizeText(candidate.hash || candidate.txHash || candidate.transactionHash || row?.[CASE_MANUAL_START_COL.TX_HASH]),
        receivingAddress: this.caseManualNormalizeText(dormant.receivingAddress || dormant.dormantAddress || candidate.toAddress || candidate.targetAddress || row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
        token: this.caseManualNormalizeText(dormant.token || candidate.token || row?.[CASE_MANUAL_START_COL.TOKEN]),
        fundsFollowed: this.caseManualNormalizeText(dormant.fundsFollowedText || dormant.fundsFollowed || candidate.fundsFollowed || row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]),
        priorBalance: this.caseManualNormalizeText(evidence.priorBalance || candidate.priorBalance || row?.[CASE_MANUAL_START_COL.PRIOR_BALANCE]),
        leftBounds: this.caseManualNormalizeText(evidence.leftBounds || candidate.leftBoundsDisplay || candidate.leftBounds || row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] || row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC]),
        depositOfInterestFound: evidence.depositOfInterestFound === true,
        downloadedTransactionCount: Number(evidence.downloadedTransactionCount) || 0,
        candidateOutgoingTransactionCount: Number(evidence.candidateExitCount) || 0,
        validExitCount: Number(dormant.validExitCount) || 0,
        excludedExitCount: Number(evidence.excludedExitCount) || 0,
        excludedExits: Array.isArray(evidence.excludedExits) ? evidence.excludedExits.slice(0, 12) : [],
        remainingDormantAmount: dormant.dormantAmountText || this.caseManualDormantAmountText(dormant),
        dustTolerance: this.caseManualNormalizeText(evidence.dustTolerance || dormant.dustTolerance),
        resultAction: this.caseManualNormalizeText(dormant.action),
        columnJNote: this.caseManualNormalizeText(payload.note || dormant.note),
        columnQValue: this.caseManualNormalizeText(payload.continuesAs || dormant.continuesAs),
        caseBuddyReceivedStructuredDormantResult: !!dormant.action,
        source: this.caseManualNormalizeText(payload.source || 'case-buddy-manual-solve-dormant')
      };
      this.caseManualStartHopInspectorDormantDebug = diagnostic;
      if (typeof console !== 'undefined' && typeof console.debug === 'function') {
        console.debug('[Case Buddy Manual Solve dormant]', diagnostic);
      }
      return diagnostic;
    },

    buildDormantMonitoringPayload(entry = {}, dormantResult = {}, caseContext = {}) {
      const row = Array.isArray(entry?.rawRow) ? entry.rawRow : (Array.isArray(entry?.row) ? entry.row : []);
      const dormant = this.caseManualNormalizeDormantResult(dormantResult) || this.caseManualBuildDormantResultFromCandidate(entry) || {};
      return {
        caseFileId: caseContext.caseFileId || this.currentProjectFileId || '',
        caseId: caseContext.caseId || this.caseSupportState?.caseDetails?.caseId || '',
        pathLabel: this.caseManualNormalizeText(entry.pathLabel || row?.[CASE_MANUAL_START_COL.PATH_LABEL]),
        transactionHash: this.caseManualNormalizeText(entry.hash || entry.txHash || entry.transactionHash || row?.[CASE_MANUAL_START_COL.TX_HASH]),
        fromHash: this.caseManualNormalizeText(entry.fromHash || row?.[CASE_MANUAL_START_COL.FROM_HASH]),
        fromAddress: this.caseManualNormalizeText(entry.fromAddress || row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]),
        toAddress: this.caseManualNormalizeText(entry.toAddress || entry.targetAddress || entry.address || row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
        dormantReceivingAddress: dormant.receivingAddress || dormant.dormantAddress || this.caseManualNormalizeText(entry.toAddress || row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
        utcDate: this.caseManualNormalizeText(entry.utcDate || entry.date || row?.[CASE_MANUAL_START_COL.DATE]),
        transactionAmount: this.caseManualNormalizeText(entry.amount || row?.[CASE_MANUAL_START_COL.AMOUNT]),
        token: this.caseManualNormalizeText(dormant.token || entry.token || row?.[CASE_MANUAL_START_COL.TOKEN]),
        fundsFollowed: this.caseManualNormalizeText(entry.fundsFollowed || row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]),
        dormantAmount: dormant.dormantAmountText || this.caseManualDormantAmountText(dormant),
        priorBalance: this.caseManualNormalizeText(entry.priorBalance || row?.[CASE_MANUAL_START_COL.PRIOR_BALANCE]),
        leftBoundsDisplay: this.caseManualNormalizeText(entry.leftBoundsDisplay || entry.leftBounds || row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY]),
        leftBoundsNumeric: this.caseManualNormalizeText(entry.leftBoundsNumeric || row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC]),
        chainId: this.caseManualNormalizeText(entry.chainId || row?.[CASE_MANUAL_START_COL.CHAIN_ID]),
        chainName: this.caseManualNormalizeText(entry.chainName || row?.[CASE_MANUAL_START_COL.CHAIN_NAME]),
        hopLogic: this.caseManualNormalizeText(entry.hopLogic || row?.[CASE_MANUAL_START_COL.HOP_LOGIC]),
        columnJNote: this.caseManualNormalizeText(dormant.note || row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES]),
        columnQStatus: this.caseManualNormalizeText(dormant.continuesAs || row?.[CASE_MANUAL_START_COL.CONTINUES_AS]),
        tool88ResultId: dormant.resultId || dormant.runId || '',
        source: 'case-buddy-manual-solve-dormant'
      };
    },

    caseManualPrependUniqueLine(value = '', line = '') {
      const incomingLines = this.caseManualDeduplicateMultilineText(line)
        .split(/\r?\n/u)
        .map(item => item.trim())
        .filter(Boolean);
      const existingLines = this.caseManualDeduplicateMultilineText(value)
        .split(/\r?\n/u)
        .map(item => item.trim())
        .filter(Boolean);
      const lines = [];
      [...incomingLines, ...existingLines].forEach(item => {
        const key = item.toLowerCase();
        if (!lines.some(existing => existing.toLowerCase() === key)) lines.push(item);
      });
      return lines.join('\n');
    },

    caseManualExtractAndCleanCaseBuddyInferenceNote(value = '', options = {}) {
      const rawOriginal = value == null ? '' : String(value);
      const original = this.caseManualNormalizeText(rawOriginal);
      if (!original) return { cleaned: '', details: [] };
      const details = [];
      let cleaned = rawOriginal.replace(/\s*Case Buddy inferred[^.\n]*\./giu, match => {
        const detail = this.caseManualNormalizeText(match);
        if (detail) details.push(detail);
        return ' ';
      });
      if (options.dropMovementSentence) {
        cleaned = cleaned
          .split(/\r?\n/u)
          .filter(line => !((this.caseManualLineIsTransferNarrative(line) && !/Tool\s*88\b/iu.test(line)) || /^\s*\.\.[A-Fa-f0-9]{4,32}\.?\s*$/u.test(line)))
          .join('\n')
          .replace(/Funds\s+move\s+from\b[\s\S]*?\s+into\s+[\s\S]*?(?=(?:\s+Funds\s+move\s+from\b|\s+Case\s+Buddy\s+inferred|\s+Tool\s*88\b|\s*$))/giu, ' ')
          .replace(/(?:^|\s+)\.\.[A-Fa-f0-9]{4,32}\.?\s*/gu, ' ');
      }
      cleaned = this.caseManualNormalizeText(cleaned);
      return { cleaned, details };
    },

    caseManualLooksLikeLegacyColumnPNote(value = '') {
      const text = this.caseManualNormalizeText(value);
      if (!text) return false;
      if (this.caseManualLooksLikePathLabel(text)) return false;
      return /tool\s*88|case\s*buddy|path\s*ends|manual[-\s]?path|unknown|service|hot[-\s]?wallet|funds\s+move|inferred|action\s*=|trace\s+through/iu.test(text)
        || /[.!?]\s*$/u.test(text)
        || text.length > 24;
    },

    caseManualMigrateLegacyColumnPNotes(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const legacyText = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FROM_LABEL]);
      if (this.caseManualLooksLikeLegacyColumnPNote(legacyText)) {
        normalized[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(normalized[CASE_MANUAL_START_COL.CASE_NOTES], legacyText);
        normalized[CASE_MANUAL_START_COL.FROM_LABEL] = '';
      }
      return normalized;
    },

    caseManualIsCanonicalCaseSheetKey(sheetKey = '') {
      const key = this.caseManualNormalizeText(sheetKey).toLowerCase();
      return ['main', 'main data', 'input', 'input data', 'raw', 'raw data'].includes(key);
    },

    caseManualSplitNoteLinesPreservingBoundaries(value = '') {
      const raw = value == null ? '' : String(value);
      return raw
        .replace(/\s+(?=(?:Funds\s+move\s+from|Branches\s+as|Path\s+ends|Tool\s*88|Case\s+Buddy)\b)/giu, '\n')
        .split(/\r?\n/u)
        .map(line => line.replace(/\s+/gu, ' ').trim())
        .filter(Boolean);
    },

    caseManualDeduplicateNoteLines(lines = []) {
      const result = [];
      const seen = new Set();
      (Array.isArray(lines) ? lines : []).forEach(line => {
        const clean = this.caseManualNormalizeText(line);
        const key = clean.toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        result.push(clean);
      });
      return result;
    },

    caseManualCleanSystemNotesForCanonicalRow(value = '', row = [], options = {}) {
      const normalizedRow = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const cleaned = this.composeCaseBuddySystemNotes(value, {
        ...options,
        row: normalizedRow,
        addTransferNarrative: false,
        noteColumn: 'system-notes'
      });
      return this.caseManualDeduplicateNoteLines(this.caseManualSplitNoteLinesPreservingBoundaries(cleaned))
        .filter(line => !this.caseManualLineLooksLikeTool88Diagnostic(line))
        .join('\n');
    },

    caseManualNormalizeAttributionForColumnK(value = '') {
      const text = this.caseManualNormalizeText(value);
      if (!text) return '';
      const roleLabels = this.caseManualExtractRoleAttributionLabels(text);
      if (roleLabels.to) return this.caseManualCleanAttributionLabel(roleLabels.to);
      if (roleLabels.from && /^\s*(?:from|sender|source)\s*:/iu.test(text) && !/\b(?:to|receiver|recipient|destination)\s*:/iu.test(text)) return '';
      return this.caseManualCleanAttributionLabel(text);
    },

    caseManualCanonicalizeFromAttributionForColumnX(value = '') {
      const text = this.caseManualNormalizeText(value);
      if (!text) return '';
      const roleLabels = this.caseManualExtractRoleAttributionLabels(text);
      if (roleLabels.from) return this.caseManualCleanAttributionLabel(roleLabels.from);
      if (roleLabels.to && /^\s*(?:to|receiver|recipient|destination)\s*:/iu.test(text) && !/\b(?:from|sender|source)\s*:/iu.test(text)) return '';
      return this.caseManualCleanAttributionLabel(text);
    },

    caseManualNormalizeAttributionRoleColumnsForRow(row = []) {
      const output = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const roleLabels = this.caseManualExtractRoleAttributionLabels(output[CASE_MANUAL_START_COL.ATTRIBUTION] || '');
      const fromAttribution = this.caseManualCanonicalizeFromAttributionForColumnX(
        output[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] || roleLabels.from || ''
      );
      output[CASE_MANUAL_START_COL.ATTRIBUTION] = this.caseManualNormalizeAttributionForColumnK(output[CASE_MANUAL_START_COL.ATTRIBUTION] || '');
      if (fromAttribution) output[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] = fromAttribution;
      else output[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] = this.caseManualCanonicalizeFromAttributionForColumnX(output[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] || '');
      return output;
    },

    caseManualCanonicalizeCaseSheetRow(row = [], options = {}) {
      let source = this.caseManualMigrateLegacyColumnPNotes(
        this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))
      );
      source = this.caseManualRepairReverseBtcAllocationFromNotes(source);
      const isHeader = options.isHeader === true || this.caseManualIsHeaderRow(source);
      const isBtcInputAddressOnlyContinuation = !isHeader && this.caseManualLooksLikeBtcInputAddressOnlyContinuation(source);
      if (isBtcInputAddressOnlyContinuation) {
        source = this.caseManualCleanBtcInputAddressOnlyContinuationRow(source);
      }
      const canonical = Array.from({ length: CASE_MANUAL_START_CANONICAL_COLUMN_COUNT }, (_, index) => {
        if (isHeader) return CASE_MANUAL_START_CANONICAL_HEADERS[index] || '';
        return this.caseManualNormalizeText(source[index]);
      });

      // Manual Solve keeps Column W as a temporary Tool 88 diagnostics bucket.
      // Column H is normally blank, but Main Data uses it for the reviewed/estimated
      // top-level USD reference value required by What Went Where. Preserve it when
      // a caller is explicitly writing Main Data; keep I and P blank for alignment.
      const preserveColumnH = options.preserveColumnH === true && !isHeader;
      if (!preserveColumnH) canonical[7] = '';
      canonical[8] = '';
      canonical[CASE_MANUAL_START_COL.FROM_LABEL] = '';
      if (isHeader) return canonical;
      const roleAttributionLabels = this.caseManualExtractRoleAttributionLabels(source[CASE_MANUAL_START_COL.ATTRIBUTION] || '');
      const sourceFromAttribution = this.caseManualCanonicalizeFromAttributionForColumnX(
        source[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] || roleAttributionLabels.from || ''
      );
      if (sourceFromAttribution) canonical[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] = sourceFromAttribution;
      if (isBtcInputAddressOnlyContinuation) return canonical;

      if (!isHeader) {
        const normalizeDateText = typeof this.caseManualNormalizeDateText === 'function'
          ? this.caseManualNormalizeDateText.bind(this)
          : value => this.caseManualNormalizeText(value);
        canonical[CASE_MANUAL_START_COL.DATE] = normalizeDateText(canonical[CASE_MANUAL_START_COL.DATE]);
        canonical[CASE_MANUAL_START_COL.CONTINUES_AS] = this.caseManualNormalizeContinuesAsText(canonical[CASE_MANUAL_START_COL.CONTINUES_AS]) || '';
        const token = this.caseManualNormalizeText(canonical[CASE_MANUAL_START_COL.TOKEN]);
        const precisionOptions = {
          preserveSourcePrecision: options.preserveAllocationPrecision === true || this.caseManualRowHasExplicitReverseAllocationEvidence(source),
        };
        canonical[CASE_MANUAL_START_COL.AMOUNT] = this.caseManualFormatTransactionAmountColumn(canonical[CASE_MANUAL_START_COL.AMOUNT], token, precisionOptions) || canonical[CASE_MANUAL_START_COL.AMOUNT];
        canonical[CASE_MANUAL_START_COL.ATTRIBUTION] = this.caseManualSanitizeSwapServiceAttributionForColumnK(
          canonical[CASE_MANUAL_START_COL.ATTRIBUTION],
          canonical,
          { extraText: source[CASE_MANUAL_START_COL.CASE_NOTES] }
        );
        const roleNormalizedRow = this.caseManualNormalizeAttributionRoleColumnsForRow(canonical);
        canonical[CASE_MANUAL_START_COL.ATTRIBUTION] = roleNormalizedRow[CASE_MANUAL_START_COL.ATTRIBUTION];
        canonical[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] = roleNormalizedRow[CASE_MANUAL_START_COL.FROM_ATTRIBUTION];
        canonical[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = this.caseManualFormatFundsForMain(canonical[CASE_MANUAL_START_COL.FUNDS_FOLLOWED], token, precisionOptions) || canonical[CASE_MANUAL_START_COL.FUNDS_FOLLOWED];
        if (this.caseManualBtcRootSelectedChoiceShouldZeroPriorBalance(canonical)) {
          canonical[CASE_MANUAL_START_COL.PRIOR_BALANCE] = '0';
        }
        canonical[CASE_MANUAL_START_COL.PRIOR_BALANCE] = this.caseManualFormatPriorBalanceNumber(canonical[CASE_MANUAL_START_COL.PRIOR_BALANCE], token) || canonical[CASE_MANUAL_START_COL.PRIOR_BALANCE];
        canonical[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = this.caseManualFormatLeftBoundsDisplayValue('', canonical[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || canonical[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY], token, precisionOptions) || canonical[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC];
        const notes = this.caseManualSplitSystemAndToolNotes(
          canonical[CASE_MANUAL_START_COL.SYSTEM_NOTES],
          source[CASE_MANUAL_START_COL.CASE_NOTES]
        );
        canonical[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualPruneSystemNotesForCanonicalRow(notes.systemNotes, canonical, {
          rowIndex: Number.isInteger(options.rowIndex) ? options.rowIndex : -1,
          rows: options.rows,
          entries: options.entries,
          addTransferNarrative: false
        });
        canonical[CASE_MANUAL_START_COL.CASE_NOTES] = notes.toolNotes;
      }

      return canonical;
    },

    caseManualCanonicalizeCaseSheetMatrix(matrix = [], options = {}) {
      const rows = Array.isArray(matrix) ? matrix : [];
      const entries = this.parseCaseBuddyEntriesFromRows(rows);
      return rows.map((row, index) => this.caseManualCanonicalizeCaseSheetRow(row, {
        isHeader: index === 0 && this.caseManualIsHeaderRow(row),
        rowIndex: index,
        rows,
        entries,
        preserveColumnH: options.preserveColumnH === true
      }));
    },

    caseManualStripResolvedPathEndingEvidence(value = '', options = {}) {
      let text = this.caseManualNormalizeText(value);
      if (!text) return '';
      const dropServiceStopLines = options.dropServiceStopLines !== false;
      let lines = text.split(/\r?\n/u).map(line => line.trim()).filter(Boolean);
      if (dropServiceStopLines) {
        lines = lines.filter(line => {
          if (/action\s*=\s*manual-path-ending|manual[-\s]?path[-\s]?ending/iu.test(line)) return false;
          if (/Tool\s*88:\s*Path\s+ends\s+at\b/iu.test(line)) return false;
          if (/User\s+intentionally\s+marked\s+this\s+target\s+address\s+as\s+a\s+path\s+ending/iu.test(line)) return false;
          if (/Uncheck\s+Additional\s+service\s+checks\s+and\s+run\s+Trace\s+Through\s+again/iu.test(line)) return false;
          if (/Tool\s*88\s+stopped\s+after\s+reviewing\b/iu.test(line)) return false;
          if (/Unknown\s+service\s*\/\s*hot[-\s]?wallet-like\s+path\s+ending\s+evidence/iu.test(line)) return false;
          return true;
        });
      }
      text = lines.join('\n');
      text = text
        .replace(/\bPath\s+ends\.?(?=\s|$)/giu, '')
        .replace(/\baction\s*=\s*manual-path-ending\b/giu, '')
        .replace(/\s{2,}/gu, ' ')
        .replace(/\n{3,}/gu, '\n\n')
        .trim();
      return text;
    },

    caseManualCleanResolvedBranchParentRow(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const continuesAs = this.caseManualNormalizeContinuesAsText(normalized[CASE_MANUAL_START_COL.CONTINUES_AS]);
      if (!/continues\s+as\b/iu.test(continuesAs)) return normalized;
      const combinedText = [
        normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        normalized[CASE_MANUAL_START_COL.ATTRIBUTION],
        normalized[CASE_MANUAL_START_COL.ANALYST_NOTES],
        normalized[CASE_MANUAL_START_COL.CASE_NOTES]
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      const hadUnknownServiceWarning = /\bunknown\b/iu.test(combinedText) && this.caseManualTextHasUnknownServiceEvidence(combinedText);
      normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualStripResolvedPathEndingEvidence(normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES]);
      normalized[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualStripResolvedPathEndingEvidence(normalized[CASE_MANUAL_START_COL.CASE_NOTES]);
      if (hadUnknownServiceWarning) {
        normalized[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
          normalized[CASE_MANUAL_START_COL.CASE_NOTES],
          'Tool 88 warning: Unknown service / hot-wallet-like behavior was detected, but the investigator intentionally traced through this address.'
        );
      }
      return normalized;
    },


    caseManualMoveInferenceDetailsToSystemColumn(row = [], options = {}) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const extracted = this.caseManualExtractAndCleanCaseBuddyInferenceNote(normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES], options);
      normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES] = extracted.cleaned;
      if (!options.dropInferenceDetails) {
        extracted.details.forEach(detail => {
          normalized[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(normalized[CASE_MANUAL_START_COL.CASE_NOTES], detail);
        });
      }
      normalized[CASE_MANUAL_START_COL.CONTINUES_AS] = this.caseManualNormalizeContinuesAsText(normalized[CASE_MANUAL_START_COL.CONTINUES_AS]);
      return this.caseManualMigrateLegacyColumnPNotes(normalized);
    },

    getCaseManualStartAuthHeaders() {
      let token = '';
      try {
        token = localStorage.getItem('token')
          || localStorage.getItem('jwtToken')
          || localStorage.getItem('jwt_token')
          || localStorage.getItem('authToken')
          || localStorage.getItem('auth_token')
          || localStorage.getItem('access_token')
          || localStorage.getItem('jwt')
          || sessionStorage.getItem('token')
          || sessionStorage.getItem('jwtToken')
          || sessionStorage.getItem('jwt_token')
          || sessionStorage.getItem('authToken')
          || sessionStorage.getItem('auth_token')
          || sessionStorage.getItem('access_token')
          || sessionStorage.getItem('jwt')
          || '';
      } catch (error) {
        console.warn('Could not read Case Buddy auth token for Tool 88 manual-start hydration.', error);
      }
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      return headers;
    },

    async postCaseManualStartTool88(payload = {}, options = {}) {
      const response = await fetch(CASE_BUDDY_TOOL88_ENDPOINT_URL, {
        method: 'POST',
        headers: this.getCaseManualStartAuthHeaders(),
        body: JSON.stringify(payload),
        signal: options.signal || undefined
      });
      const json = await response.json().catch(() => null);
      const requestId = response.headers?.get?.('X-Request-ID')
        || json?.request_id
        || json?.requestId
        || '';
      if (!response.ok || !json) {
        const baseMessage = json?.message || json?.error || `Tool 88 backend returned HTTP ${response.status}.`;
        throw new Error(requestId ? `${baseMessage} Request ${requestId}.` : baseMessage);
      }
      if (json.status && json.status !== 'success') {
        const baseMessage = json.message || json.error || 'Tool 88 backend did not return success.';
        throw new Error(requestId ? `${baseMessage} Request ${requestId}.` : baseMessage);
      }
      return json.result || json;
    },

    caseManualSleep(ms = 0) {
      const delay = Math.max(0, Number(ms) || 0);
      if (!delay) return Promise.resolve();
      return new Promise(resolve => setTimeout(resolve, delay));
    },

    caseManualCleanAttributionLabel(value = '') {
      return this.caseManualNormalizeText(value)
        .replace(/^(from|to|sender|receiver|recipient|source)\s*:\s*/iu, '')
        .replace(/\s+/gu, ' ')
        .trim();
    },

    caseManualAttributionLabelIsMeaningfulEndpoint(value = '') {
      const label = this.caseManualCleanAttributionLabel(value);
      if (!label) return false;
      const normalized = label.toLowerCase().replace(/[^a-z0-9]+/gu, ' ').trim();
      if (!normalized) return false;
      if (/\b(?:unknown|unattributed|unidentified|no visible hit|no hit|not attributed|needs review|manual review|requires review)\b/iu.test(label)) return false;
      if (/^(?:none|null|n\/?a|na|unknown service|unknown service hot wallet|unknown service hotwallet|service hot wallet|service hotwallet|hot wallet|service address|deposit address|contract|contract like|wallet|address)$/iu.test(normalized)) return false;
      return true;
    },

    caseManualCandidateMeaningfulEndpointAttributionLabel(candidate = {}) {
      const row = Array.isArray(candidate?.row) ? candidate.row : [];
      const rawRow = Array.isArray(candidate?.rawRow) ? candidate.rawRow : [];
      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const endpointAddress = this.caseManualStartCandidateEndpointAddress(candidate);
      const cachedLabel = endpointAddress && typeof this.caseManualAttributionLabelForAddress === 'function'
        ? this.caseManualAttributionLabelForAddress(endpointAddress)
        : '';
      const values = [];
      const remember = (value, columnRole = '') => {
        if (value == null || value === '') return;
        values.push({ value, columnRole });
      };

      if (reverse) {
        remember(candidate?.fromAttributionLabel, 'from');
        remember(candidate?.fromEntityLabel, 'from');
        remember(candidate?.sourceEntityLabel, 'from');
        remember(candidate?.fromEntity, 'from');
        remember(candidate?.fromAttribution?.label, 'from');
        remember(candidate?.fromAttribution?.displayLabel, 'from');
        remember(candidate?.fromAttribution?.display_label, 'from');
        remember(rawRow[CASE_MANUAL_START_COL.FROM_ATTRIBUTION], 'from');
        remember(row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION], 'from');
        [candidate?.attribution, rawRow[CASE_MANUAL_START_COL.ATTRIBUTION], row[CASE_MANUAL_START_COL.ATTRIBUTION]].forEach((value) => {
          const roles = this.caseManualExtractRoleAttributionLabels(value || '');
          remember(roles.from, 'from');
        });
        remember(cachedLabel, 'from');
      } else {
        remember(candidate?.toAttributionLabel, 'to');
        remember(candidate?.entityLabel, 'to');
        remember(candidate?.toEntityLabel, 'to');
        remember(candidate?.entity, 'to');
        remember(candidate?.toAttribution?.label, 'to');
        remember(candidate?.toAttribution?.displayLabel, 'to');
        remember(candidate?.toAttribution?.display_label, 'to');
        [candidate?.attribution, rawRow[CASE_MANUAL_START_COL.ATTRIBUTION], row[CASE_MANUAL_START_COL.ATTRIBUTION]].forEach((value) => {
          const roles = this.caseManualExtractRoleAttributionLabels(value || '');
          if (roles.to) remember(roles.to, 'to');
          else remember(value, 'columnK');
        });
        remember(cachedLabel, 'to');
      }

      for (const item of values) {
        const label = item.columnRole === 'columnK'
          ? this.caseManualCanonicalizeAttributionForColumnK(item.value || '')
          : (item.columnRole === 'from'
            ? this.caseManualCanonicalizeFromAttributionForColumnX(item.value || '')
            : this.caseManualCleanAttributionLabel(item.value || ''));
        if (!this.caseManualAttributionLabelIsMeaningfulEndpoint(label)) continue;
        if (reverse && this.caseManualAttributionLabelLooksLikeDeposit(label)) continue;
        return label;
      }
      return '';
    },

    caseManualStartCandidateHasMeaningfulEndpointAttribution(candidate = {}) {
      return Boolean(this.caseManualCandidateMeaningfulEndpointAttributionLabel(candidate));
    },

    caseManualVisualLabelLineLooksMeaningfulEndpointAttribution(value = '') {
      const label = this.caseManualCleanAttributionLabel(value);
      if (!label) return false;
      const normalized = label.toLowerCase().replace(/[^a-z0-9]+/gu, ' ').trim();
      if (!normalized) return false;
      if (/^(?:from|to|sender|receiver|recipient|source|destination|path ends|unresolved ending|hot wallet like ending|address pending|from pending|to pending|amount pending|stored in rows)$/iu.test(normalized)) return false;
      if (/^(?:from|to|sender|receiver|recipient|source|destination)(?:\s|$)/iu.test(normalized)) return false;
      if (/^(?:0x[a-f0-9]{6,}|bc1[a-z0-9]+|[13][a-km-zA-HJ-NP-Z1-9]{20,})$/u.test(label)) return false;
      if (/^\+?\d+(?:\.\d+)?\s*(?:inputs?|outputs?|more\s+btc\s+inputs?)$/iu.test(normalized)) return false;
      return this.caseManualAttributionLabelIsMeaningfulEndpoint(label);
    },

    caseManualVisualNodeMeaningfulEndpointAttributionLabel(node = {}) {
      const candidate = node?.caseCandidate || node?.candidate || node || {};
      const nodeAddress = this.caseManualNormalizeAddressKey(node?.address || '');
      const endpointAddress = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate));
      if (nodeAddress && endpointAddress && nodeAddress !== endpointAddress) return '';
      const candidateLabel = this.caseManualCandidateMeaningfulEndpointAttributionLabel(candidate);
      if (candidateLabel) return candidateLabel;
      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const directValues = reverse
        ? [node?.fromEntityLabel, node?.sourceEntityLabel, node?.fromAttributionLabel]
        : [node?.entityLabel, node?.toEntityLabel, node?.entity, node?.toAttributionLabel, node?.attribution];
      for (const value of directValues) {
        const label = reverse
          ? this.caseManualCanonicalizeFromAttributionForColumnX(value || '')
          : this.caseManualCleanAttributionLabel(value || '');
        if (!this.caseManualAttributionLabelIsMeaningfulEndpoint(label)) continue;
        if (reverse && this.caseManualAttributionLabelLooksLikeDeposit(label)) continue;
        return label;
      }
      const cachedLabel = endpointAddress && typeof this.caseManualAttributionLabelForAddress === 'function'
        ? this.caseManualAttributionLabelForAddress(endpointAddress)
        : '';
      if (this.caseManualAttributionLabelIsMeaningfulEndpoint(cachedLabel)
        && !(reverse && this.caseManualAttributionLabelLooksLikeDeposit(cachedLabel))) {
        return this.caseManualCleanAttributionLabel(cachedLabel);
      }
      const lines = Array.isArray(node?.labelLines) ? node.labelLines : [];
      for (const line of lines) {
        if (!this.caseManualVisualLabelLineLooksMeaningfulEndpointAttribution(line)) continue;
        const label = this.caseManualCleanAttributionLabel(line);
        if (reverse && this.caseManualAttributionLabelLooksLikeDeposit(label)) continue;
        return label;
      }
      return '';
    },

    caseManualVisualNodeHasMeaningfulEndpointAttribution(node = {}) {
      return Boolean(this.caseManualVisualNodeMeaningfulEndpointAttributionLabel(node));
    },

    caseManualExtractRoleAttributionLabels(value = '') {
      const textValue = this.caseManualNormalizeText(value);
      const roles = { from: '', to: '' };
      if (!textValue) return roles;
      textValue.split(/[;|\n]+/u).forEach(part => {
        const cleanPart = this.caseManualNormalizeText(part);
        const match = cleanPart.match(/^(from|sender|source|to|receiver|recipient|destination)\s*:\s*(.+)$/iu);
        if (!match) return;
        const role = /^(from|sender|source)$/iu.test(match[1]) ? 'from' : 'to';
        const label = this.caseManualCleanAttributionLabel(match[2]);
        if (label && !roles[role]) roles[role] = label;
      });
      return roles;
    },

    caseManualAttributionResultIsColumnKOnly(result = {}) {
      if (!result || typeof result !== 'object') return false;
      const raw = result.raw && typeof result.raw === 'object' ? result.raw : result;
      const sourceText = [
        result.source,
        result.sourceLabel,
        result.source_summary,
        raw.source,
        raw.source_summary,
        raw.vendor_source
      ].map(value => this.caseManualNormalizeText(value)).join(' ').toLowerCase();
      const serverText = this.caseManualNormalizeText(raw.server_note || raw.server_note_text || result.server_note || result.serverNote || '');
      const hasServerNote = Boolean(serverText || raw.server_note_present || raw.address_note_found || result.serverNotePresent || result.addressNoteFound);
      const vendorLabel = this.caseManualCleanAttributionLabel(raw.vendor_label || raw.arkham_label || result.vendorLabel || result.arkhamLabel || '');
      const columnKText = this.caseManualCleanAttributionLabel(
        raw.column_k_value
          || raw.columnKValue
          || result.column_k_value
          || result.columnKValue
          || (Array.isArray(raw.column_k_values) ? raw.column_k_values.join(' ') : '')
          || (Array.isArray(result.column_k_values) ? result.column_k_values.join(' ') : '')
      );
      const visibleLabel = this.caseManualCleanAttributionLabel(result.label || raw.label || raw.display_label || raw.displayLabel || result.display_label || result.displayLabel || '');
      return Boolean(columnKText && visibleLabel && sourceText.includes('column_k_input') && !hasServerNote && !vendorLabel);
    },

    caseManualExtractAttributionLabel(result = {}, options = {}) {
      if (!result || typeof result !== 'object') return '';
      const allowColumnKFallback = options.allowColumnKFallback === true;
      if (!allowColumnKFallback && this.caseManualAttributionResultIsColumnKOnly(result)) return '';
      const direct = [
        result.display_label,
        result.displayLabel,
        result.primary_label,
        result.primaryLabel,
        result.label,
        result.server_note,
        result.server_note_text,
        result.vendor_label,
        result.arkham_label
      ];
      for (const value of direct) {
        const label = this.caseManualCleanAttributionLabel(value);
        if (label && !/^unknown$/iu.test(label) && !/^no visible hit$/iu.test(label)) return label;
      }
      if (allowColumnKFallback) {
        const columnKDirect = [result.column_k_value, result.columnKValue];
        for (const value of columnKDirect) {
          const label = this.caseManualCleanAttributionLabel(value);
          if (label && !/^unknown$/iu.test(label) && !/^no visible hit$/iu.test(label)) return label;
        }
        const values = Array.isArray(result.column_k_values) ? result.column_k_values : [];
        for (const value of values) {
          const label = this.caseManualCleanAttributionLabel(value);
          if (label) return label;
        }
      }
      return '';
    },

    caseManualShortAttributionLabel(value = '', maxLength = 32) {
      const label = this.caseManualCleanAttributionLabel(value);
      if (!label) return '';
      if (label.length <= maxLength) return label;
      return `${label.slice(0, Math.max(8, maxLength - 3)).trim()}...`;
    },

    caseManualAttributionForAddress(address = '') {
      const key = this.caseManualNormalizeAddressKey(address);
      if (!key) return null;
      const cached = this.caseManualStartVisualizerAttributionByAddress?.[key] || null;
      if (cached && this.caseManualAttributionResultIsColumnKOnly(cached)) return null;
      return cached;
    },

    caseManualAttributionLabelForAddress(address = '') {
      return this.caseManualAttributionForAddress(address)?.label || '';
    },

    caseManualBuildCombinedAttributionLabel(candidate = {}) {
      // Column K is consumed by later Case Buddy tools as the To-address attribution.
      // Keep from-address identity available for visual labels/details, but do not
      // persist it into Column K.
      const toLabel = candidate.toAttributionLabel || this.caseManualAttributionLabelForAddress(candidate.toAddress);
      return this.caseManualCleanAttributionLabel(toLabel);
    },

    caseManualDescribeAddressForNote(address = '', attributionLabel = '') {
      const label = this.caseManualCleanAttributionLabel(attributionLabel);
      if (label) return `a ${label} address`;
      return this.caseManualShorten(address, 8, 6) || 'an address';
    },

    caseManualBuildSystemNoteForTransfer(selected = {}, attribution = {}, options = {}) {
      const row = Array.isArray(options.row) ? options.row : (Array.isArray(selected.row) ? selected.row : (Array.isArray(selected.rawRow) ? selected.rawRow : []));
      const rows = Array.isArray(options.rows) ? options.rows : (Array.isArray(selected.rows) ? selected.rows : []);
      const entries = Array.isArray(options.entries) ? options.entries : this.parseCaseBuddyEntriesFromRows(rows);
      const rowIndex = Number.isInteger(options.rowIndex) ? options.rowIndex : (Number.isInteger(selected.rowIndex) ? selected.rowIndex : -1);
      const entry = options.entry || this.caseManualFindEntryForRowIndex(entries, rowIndex) || this.caseManualFindEntryForRow(entries, row);
      if (!this.shouldGenerateTransferNarrativeNote(entry, rowIndex, rows, {
        ...options,
        entries,
        row,
        fromAddress: selected.fromAddress,
        toAddress: selected.toAddress,
        attribution,
        noteColumn: 'system-notes',
        forAttributionEvidence: false
      })) return '';
      return this.buildRootTransferNarrativeNote(entry, {
        ...options,
        row,
        fromAddress: selected.fromAddress,
        toAddress: selected.toAddress,
        attribution
      });
    },

    caseManualBuildAddressVisualLabelLines(roleLabel = '', address = '', attributionLabel = '', options = {}) {
      const lines = [];
      const label = this.caseManualShortAttributionLabel(attributionLabel, options.maxAttributionLength || 30);
      if (label) lines.push(label);
      const cleanRole = this.caseManualNormalizeText(roleLabel);
      if (options.showRoleLabel === true && cleanRole) lines.push(cleanRole);
      if (!address && !lines.length) lines.push(cleanRole && /from/iu.test(cleanRole) ? 'From pending' : 'Address pending');
      return lines.length ? lines : [];
    },


    caseManualBuildVisualTransactionAmountText(candidate = {}) {
      const rawRow = Array.isArray(candidate.rawRow) ? candidate.rawRow : [];
      const toolRow = Array.isArray(candidate.row) ? candidate.row : [];
      const canonicalAmount = this.caseManualNormalizeText(
        rawRow[CASE_MANUAL_START_COL.AMOUNT]
          || toolRow[CASE_MANUAL_START_COL.AMOUNT]
          || candidate.transactionAmount
          || candidate.transactionAmountText
          || candidate.amountOfTxn
          || candidate.amount_of_txn
          || candidate.amount
          || candidate.transferAmountText
      );
      const fallbackAmountText = this.caseManualNormalizeText(candidate.amountText || candidate.displayAmount || '');
      const fundsFollowed = this.caseManualNormalizeText(
        rawRow[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]
          || toolRow[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]
          || candidate.fundsFollowed
      );
      const amount = canonicalAmount || (fallbackAmountText && fallbackAmountText !== fundsFollowed ? fallbackAmountText : '');
      const token = this.caseManualNormalizeText(
        rawRow[CASE_MANUAL_START_COL.TOKEN]
          || toolRow[CASE_MANUAL_START_COL.TOKEN]
          || candidate.token
          || candidate.tokenSymbol
      );
      if (!amount) return 'Amount pending';
      if (typeof this.caseManualFormatTransactionAmountDisplay === 'function') {
        const formatted = this.caseManualFormatTransactionAmountDisplay(amount, token);
        if (formatted) return formatted;
      }
      if (token && !amount.toUpperCase().includes(token.toUpperCase())) return `${amount} ${token}`;
      return amount;
    },

    caseManualBuildBtcVisualAmountNodeText(candidate = {}, role = 'output') {
      const rawRow = Array.isArray(candidate.rawRow) ? candidate.rawRow : [];
      const toolRow = Array.isArray(candidate.row) ? candidate.row : [];
      const token = this.caseManualNormalizeText(
        rawRow[CASE_MANUAL_START_COL.TOKEN]
          || toolRow[CASE_MANUAL_START_COL.TOKEN]
          || candidate.token
          || candidate.tokenSymbol
          || 'BTC'
      ) || 'BTC';
      if (role === 'input' && Number(candidate.btcInputAddressCount || 0) > 1) {
        return `${this.caseManualFormatInteger(candidate.btcInputAddressCount)} BTC inputs`;
      }
      if (role === 'output' && this.caseManualStartCandidateIsReverse(candidate) && Number(candidate.btcOutputAddressCount || 0) > 1) {
        return `${this.caseManualFormatInteger(candidate.btcOutputAddressCount)} BTC outputs`;
      }
      const firstOutputAmount = this.caseManualNormalizeText(candidate.btcOutputAddresses?.[0]?.amountText || '');
      const direct = this.caseManualNormalizeText(
        role === 'input'
          ? (candidate.inputAmount || candidate.inputAmountText || candidate.incomingAmount || '')
          : (candidate.outputAmount || candidate.outputAmountText || candidate.selectedOutputAmount || firstOutputAmount || '')
      );
      const amount = direct || this.caseManualNormalizeText(
        rawRow[CASE_MANUAL_START_COL.AMOUNT]
          || toolRow[CASE_MANUAL_START_COL.AMOUNT]
          || candidate.amount
          || candidate.transactionAmount
          || candidate.transactionAmountText
          || candidate.amountText
      );
      if (!amount) return 'Amount pending';
      if (typeof this.caseManualFormatTransactionAmountDisplay === 'function') {
        const formatted = this.caseManualFormatTransactionAmountDisplay(amount, token);
        if (formatted) return formatted;
      }
      return token && !amount.toUpperCase().includes(token.toUpperCase()) ? `${amount} ${token}` : amount;
    },

    caseManualCandidatePathEndingText(candidate = {}) {
      const row = Array.isArray(candidate.rawRow) ? candidate.rawRow : [];
      const toolRow = Array.isArray(candidate.row) ? candidate.row : [];
      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const activeAttributionValues = reverse
        ? [
            candidate.fromAttributionLabel,
            row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION],
            toolRow[CASE_MANUAL_START_COL.FROM_ATTRIBUTION]
          ]
        : [
            candidate.attribution,
            candidate.toAttributionLabel,
            row[CASE_MANUAL_START_COL.ATTRIBUTION],
            toolRow[CASE_MANUAL_START_COL.ATTRIBUTION]
          ];
      return [
        ...activeAttributionValues,
        candidate.systemNotes,
        candidate.analystNotes,
        candidate.caseNotes,
        candidate.continuesAs,
        candidate.hydrationMessage,
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row[CASE_MANUAL_START_COL.ANALYST_NOTES],
        row[CASE_MANUAL_START_COL.CASE_NOTES],
        row[CASE_MANUAL_START_COL.FROM_HASH],
        row[CASE_MANUAL_START_COL.FROM_LABEL],
        row[CASE_MANUAL_START_COL.CONTINUES_AS],
        toolRow[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        toolRow[CASE_MANUAL_START_COL.ANALYST_NOTES],
        toolRow[CASE_MANUAL_START_COL.CASE_NOTES],
        toolRow[CASE_MANUAL_START_COL.FROM_HASH],
        toolRow[CASE_MANUAL_START_COL.FROM_LABEL],
        toolRow[CASE_MANUAL_START_COL.CONTINUES_AS]
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
    },

    caseManualLooksLikeUnknownServicePathEnding(candidate = {}) {
      if (!this.isCaseManualStartPathEndingCandidate(candidate)) return false;
      if (this.caseManualStartCandidateHasMeaningfulEndpointAttribution(candidate)) return false;
      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const activeLabel = reverse
        ? this.caseManualCanonicalizeFromAttributionForColumnX(candidate.fromAttributionLabel || '')
        : this.caseManualCanonicalizeAttributionForColumnK(candidate.attribution || candidate.toAttributionLabel || '');
      if (reverse && this.caseManualAttributionLabelLooksLikeDeposit(activeLabel)) return false;
      if (candidate.unknownServicePathEnding || candidate.visualEndpointStatus === 'unknown-service') return true;
      const text = this.caseManualCandidatePathEndingText(candidate);
      if (!text) return false;
      if (reverse && !this.caseManualStartTextHasReverseEndpointDecision(text)) return false;
      const hasUnknown = /\bunknown\b/iu.test(text) || /^unknown$/iu.test(activeLabel);
      const hasServiceShape = /unattributed\s+exchange\s+hot\s+wallet|features\s+of\s+an\s+unattributed\s+exchange|exchange\s+hot\s+wallet|hot[-\s]?wallet|service\s+address|service[-\s]?pattern|service\s*\/\s*hot|potential\s+service|additional\s+service\s+checks|high\s+stablecoin\s+flow|high\s+erc20\s+transfer\s+count|trace\s+through\s+again\s+only\s+if/iu.test(text);
      const hasStopShape = /path\s*ends|manual-path-ending|tool\s*88\s+stopped|action\s*=\s*manual-path-ending/iu.test(text);
      return Boolean(hasUnknown && hasServiceShape && hasStopShape);
    },

    caseManualAddressLabelCategoryForCandidate(candidate = {}, pathEnding = false, address = '') {
      if (!address) return 'warning';
      if (pathEnding && this.caseManualStartCandidateHasAddressPoisoning(candidate)) return 'address-poisoning';
      if (pathEnding && this.caseManualStartCandidateHasMeaningfulEndpointAttribution(candidate)) return 'path-ending';
      if (pathEnding && this.caseManualStartCandidateAttentionVisualIsDeferred(candidate)) return 'regular';
      if (pathEnding && (this.caseManualLooksLikeUnknownServicePathEnding(candidate) || this.caseManualStartHopInspectorHasPendingUnknownServicePathEnding(candidate))) return 'unknown-service';
      return pathEnding ? 'path-ending' : 'regular';
    },

    caseManualBuildAddressVisualLabelLinesForCandidate(roleLabel = '', address = '', attributionLabel = '', candidate = {}, options = {}) {
      const lines = this.caseManualBuildAddressVisualLabelLines(roleLabel, address, attributionLabel, options);
      const roleText = this.caseManualNormalizeText(roleLabel).toLowerCase();
      const addressKey = this.caseManualNormalizeAddressKey(address);
      const endpointKey = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate));
      const activeRole = this.caseManualStartCandidateEndpointRole(candidate);
      const roleLooksFrom = /^(?:from|source)|\bfrom\b|\binput\b/iu.test(roleText);
      const roleMatchesActiveEndpoint = activeRole === 'from' ? roleLooksFrom : !roleLooksFrom;
      const isTargetSide = Boolean(addressKey && endpointKey && addressKey === endpointKey && roleMatchesActiveEndpoint);
      const attentionVisualDeferred = isTargetSide && this.caseManualStartCandidateAttentionVisualIsDeferred(candidate);
      const isUnresolvedTarget = isTargetSide && !attentionVisualDeferred && this.isCaseManualStartUnresolvedEndingCandidate(candidate);
      const meaningfulEndpointLabel = isTargetSide ? this.caseManualCandidateMeaningfulEndpointAttributionLabel(candidate) : '';
      if (meaningfulEndpointLabel) {
        const shortLabel = this.caseManualShortAttributionLabel(meaningfulEndpointLabel, options.maxAttributionLength || 30);
        const cleaned = lines.filter((line) => {
          const cleanLine = this.caseManualNormalizeText(line);
          return !/^unknown$/iu.test(cleanLine)
            && !/^path\s*ends$/iu.test(cleanLine)
            && !/unresolved\s+(?:ending|source)/iu.test(cleanLine)
            && !/hot[-\s]?wallet[-\s]?like\s+ending/iu.test(cleanLine);
        });
        if (shortLabel && !cleaned.some(line => this.caseManualCleanAttributionLabel(line).toLowerCase() === this.caseManualCleanAttributionLabel(shortLabel).toLowerCase())) cleaned.unshift(shortLabel);
        return cleaned;
      }
      if (attentionVisualDeferred) {
        return lines.filter((line) => {
          const cleanLine = this.caseManualNormalizeText(line);
          return !/^unknown$/iu.test(cleanLine)
            && !/^path\s*ends$/iu.test(cleanLine)
            && !/unresolved\s+(?:ending|source)/iu.test(cleanLine)
            && !/hot[-\s]?wallet[-\s]?like\s+ending/iu.test(cleanLine)
            && !/manual\s+work/iu.test(cleanLine);
        });
      }
      const pendingUnknownServiceEnding = isTargetSide && this.caseManualStartHopInspectorHasPendingUnknownServicePathEnding(candidate);
      const looksLikeUnknownServiceEnding = isTargetSide && (this.caseManualLooksLikeUnknownServicePathEnding(candidate) || pendingUnknownServiceEnding);
      if (looksLikeUnknownServiceEnding) {
        const withoutUnknown = lines.filter((line) => {
          const cleanLine = this.caseManualNormalizeText(line);
          return !/^unknown$/iu.test(cleanLine)
            && !/^path\s*ends$/iu.test(cleanLine)
            && !/unresolved\s+(?:ending|source)/iu.test(cleanLine)
            && !/hot[-\s]?wallet[-\s]?like\s+ending/iu.test(cleanLine);
        });
        withoutUnknown.unshift('Unknown');
        if (!withoutUnknown.some(line => /hot[-\s]?wallet|service\s*\/\s*hot/iu.test(this.caseManualNormalizeText(line)))) {
          withoutUnknown.push('Hot-wallet-like ending');
        }
        return withoutUnknown;
      }
      if (isUnresolvedTarget && options.includeUnresolvedLabel !== false && !lines.some(line => /unresolved\s+(?:ending|source)/iu.test(line))) {
        const unresolvedLabel = activeRole === 'from' ? 'Unresolved source' : 'Unresolved ending';
        return [...lines.filter(line => !/^path\s*ends$/iu.test(this.caseManualNormalizeText(line))), unresolvedLabel];
      }
      return lines.filter(line => !/^path\s*ends$/iu.test(this.caseManualNormalizeText(line)));
    },

    async lookupCaseManualAddressAttribution(address = '', options = {}) {
      const cleanAddress = this.caseManualNormalizeText(address);
      const key = this.caseManualNormalizeAddressKey(cleanAddress);
      if (!key) return null;
      const cached = this.caseManualStartVisualizerAttributionByAddress?.[key];
      if (cached && cached.status !== 'failed') return cached;
      try {
        const result = await this.postCaseManualStartTool88({
          action: 'attribution_lookup',
          address: cleanAddress,
          chain_id: options.chainId || options.chain_id || '',
          chain_name: options.chainName || options.chain_name || '',
          prefer_cache: true,
          cache_first: true,
          lookup_vendor: true,
          ttl_hours: 168,
          return_searchable_text: false,
          column_k_values: options.allowColumnKFallback === true && Array.isArray(options.columnKValues) ? options.columnKValues : []
        });
        const label = this.caseManualExtractAttributionLabel(result, { allowColumnKFallback: options.allowColumnKFallback === true });
        const normalized = {
          status: label ? 'hit' : 'no_hit',
          address: cleanAddress,
          key,
          label,
          source: result?.source || result?.source_summary || '',
          statusLabel: result?.status_label || (label ? 'Attribution hit' : 'No visible hit'),
          classification: result?.classification || result?.entity_type || result?.entity_kind || '',
          raw: result || null,
          checkedAtUtc: new Date().toISOString()
        };
        this.caseManualStartVisualizerAttributionByAddress = {
          ...(this.caseManualStartVisualizerAttributionByAddress || {}),
          [key]: normalized
        };
        return normalized;
      } catch (error) {
        const failed = {
          status: 'failed',
          address: cleanAddress,
          key,
          label: '',
          source: 'lookup_failed',
          statusLabel: error?.message || 'Attribution lookup failed',
          classification: '',
          raw: null,
          checkedAtUtc: new Date().toISOString()
        };
        this.caseManualStartVisualizerAttributionByAddress = {
          ...(this.caseManualStartVisualizerAttributionByAddress || {}),
          [key]: failed
        };
        return failed;
      }
    },

    async lookupCaseManualTransferAttributions(selected = {}, candidate = {}, options = {}) {
      const queue = [];
      const add = (address, role) => {
        const key = this.caseManualNormalizeAddressKey(address);
        if (!key || queue.some(item => item.key === key)) return;
        queue.push({ key, address, role });
      };
      add(selected.fromAddress || candidate.fromAddress, 'from');
      add(selected.toAddress || candidate.toAddress, 'to');
      const maxLookups = Math.max(1, Math.min(200, Number(options.maxLookups) || Number(this.caseManualStartVisualizerMaxAttributionLookups) || 16));
      const delayMs = Math.max(0, Number(options.delayMs) || Number(this.caseManualStartVisualizerAttributionDelayMs) || 650);
      const results = {};
      let lookupCount = 0;
      let hitCount = 0;
      for (const item of queue.slice(0, maxLookups)) {
        this.caseManualStartVisualizerStatus = `Checking attribution for ${this.caseManualShorten(item.address, 8, 6)}…`;
        const result = await this.lookupCaseManualAddressAttribution(item.address, {
          ...candidate,
          ...selected,
          chainId: selected.chainId || candidate.chainId || '',
          chainName: selected.chainName || candidate.chainName || '',
          // Column K belongs to the row's To-address attribution, not to every
          // address checked during hydration. Do not let the provider echo a
          // spreadsheet value as a fresh hit for an unrelated sibling ending.
          columnKValues: [],
          allowColumnKFallback: false
        });
        results[item.role] = result;
        lookupCount += 1;
        if (result?.label) hitCount += 1;
        if (lookupCount < Math.min(queue.length, maxLookups)) await this.caseManualSleep(delayMs);
      }
      return {
        from: results.from || null,
        to: results.to || null,
        fromLabel: results.from?.label || '',
        toLabel: results.to?.label || '',
        lookupCount,
        hitCount,
        skippedCount: Math.max(0, queue.length - maxLookups)
      };
    },

    async lookupCaseManualStartTransactionBundle(candidate = {}, options = {}) {
      const chainId = this.caseManualNormalizeText(candidate.chainId) || '1';
      const chainName = this.caseManualNormalizeText(candidate.chainName) || (chainId === '1' ? 'Ethereum' : '');
      const family = this.caseManualInferNetworkFamily(candidate);
      return this.postCaseManualStartTool88({
        action: 'transaction_explorer_bundle',
        tx_hash: candidate.hash || candidate.transactionHash || candidate.txHash,
        transaction_hash: candidate.hash || candidate.transactionHash || candidate.txHash,
        chain_id: chainId,
        chain_name: chainName,
        network_family: family,
        address: candidate.toAddress || candidate.fromAddress || '',
        native_symbol: this.caseManualNativeSymbolForChain(chainId, chainName)
      }, { signal: options.signal });
    },

    caseManualInferNetworkFamily(candidate = {}) {
      const row = Array.isArray(candidate?.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate?.row) ? candidate.row : (Array.isArray(candidate) ? candidate : []));
      const rowChainName = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.CHAIN_NAME] || '').toLowerCase();
      const rowChainId = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.CHAIN_ID] || '').toLowerCase();
      const candidateChainName = this.caseManualNormalizeText(candidate.chainName || candidate.chain_name || '').toLowerCase();
      const candidateChainId = this.caseManualNormalizeText(candidate.chainId || candidate.chain_id || '').toLowerCase();
      const declaredFamily = this.caseManualNormalizeText(
        candidate.networkFamily
          || candidate.network_family
          || candidate.family
          || ''
      ).toLowerCase();
      const token = this.caseManualNormalizeText(
        candidate.token
          || candidate.tokenSymbol
          || candidate.token_symbol
          || row?.[CASE_MANUAL_START_COL.TOKEN]
          || ''
      ).toUpperCase();
      const fromAddress = this.caseManualNormalizeText(
        candidate.fromAddress
          || candidate.from_address
          || candidate.address
          || row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]
          || ''
      );
      const toAddress = this.caseManualNormalizeText(
        candidate.toAddress
          || candidate.to_address
          || row?.[CASE_MANUAL_START_COL.TO_ADDRESS]
          || ''
      );
      const isEvmChain = (chainId, chainName) => Boolean(
        (chainId && (/^0x[0-9a-f]+$/iu.test(chainId) || /^\d+$/u.test(chainId)))
          || /ethereum|arbitrum|optimism|polygon|base|bsc|binance|avalanche|avax|fantom|linea/iu.test(chainName)
      );

      if (declaredFamily === 'tron') return 'tron';
      if (declaredFamily === 'btc' || declaredFamily === 'bitcoin') return 'btc';
      if (declaredFamily === 'evm') return 'evm';

      // Columns T/U are spreadsheet truth and therefore outrank all inferred
      // candidate defaults.
      if (rowChainName.includes('tron') || rowChainId.includes('tron')) return 'tron';
      if (rowChainName.includes('bitcoin') || rowChainName === 'btc' || rowChainId === 'btc' || rowChainId === 'bitcoin') return 'btc';
      if (isEvmChain(rowChainId, rowChainName)) return 'evm';

      if (candidateChainName.includes('tron') || candidateChainId.includes('tron')) return 'tron';
      if (candidateChainName.includes('bitcoin') || candidateChainName === 'btc' || candidateChainId === 'btc' || candidateChainId === 'bitcoin') return 'btc';

      // Manual Solve visual candidates historically received a fallback
      // Ethereum/chain-1 value when T/U were absent. Native BTC evidence must
      // outrank that fallback, while an explicit spreadsheet EVM chain above
      // still preserves WBTC and other EVM-token behavior.
      if (token === 'BTC' || this.caseManualTextLooksLikeBitcoinAddress(fromAddress) || this.caseManualTextLooksLikeBitcoinAddress(toAddress)) return 'btc';
      if (token === 'TRX' || /^T[1-9A-HJ-NP-Za-km-z]{33}$/u.test(fromAddress) || /^T[1-9A-HJ-NP-Za-km-z]{33}$/u.test(toAddress)) return 'tron';
      if (isEvmChain(candidateChainId, candidateChainName)) return 'evm';
      if (row.length && this.caseManualRowNetworkFamily(row) === 'btc') return 'btc';
      if (row.length && this.caseManualRowNetworkFamily(row) === 'tron') return 'tron';
      return 'evm';
    },

    normalizeCaseManualBundleCandidate(rawRow = {}, kind = 'main', bundle = {}, fallback = {}) {
      if (!rawRow || typeof rawRow !== 'object') return null;
      const tokenInfo = rawRow.tokenInfo || rawRow.token_info || {};
      const fromAddress = this.caseManualNormalizeText(rawRow.from_address || rawRow.from || rawRow.sender || rawRow.ownerAddress || rawRow.owner_address || '');
      const toAddress = this.caseManualNormalizeText(rawRow.to_address || rawRow.to || rawRow.receiver || rawRow.toAddress || rawRow.to_address_base58 || '');
      if (!fromAddress || !toAddress) return null;
      const amountRaw = this.caseManualNormalizeText(rawRow.amount_formatted || rawRow.amountFormatted || rawRow.value_formatted || rawRow.valueFormatted || rawRow.amount || rawRow.value || '');
      const amountNumber = this.caseManualParseNumericAmount(amountRaw);
      if (!Number.isFinite(amountNumber) || amountNumber <= 0) return null;
      const chainId = this.caseManualNormalizeText(bundle.chain_id || bundle.chainId || rawRow.chain_id || rawRow.chainId || fallback.chainId || '1');
      const chainName = this.caseManualNormalizeText(bundle.chain_name || bundle.chainName || rawRow.chain_name || rawRow.chainName || fallback.chainName || (chainId === '1' ? 'Ethereum' : ''));
      const tokenSymbol = this.caseManualNormalizeText(rawRow.token_symbol || rawRow.tokenSymbol || rawRow.token || rawRow.symbol || tokenInfo.tokenAbbr || tokenInfo.symbol || (kind === 'main' || kind === 'internal' ? this.caseManualNativeSymbolForChain(chainId, chainName) : 'TOKEN')).toUpperCase();
      const rawDate = rawRow.timeStamp || rawRow.timestamp || rawRow.block_ts || rawRow.block_timestamp || rawRow.date || '';
      const amountText = this.caseManualFormatAmount(amountNumber);
      const bundleFamily = this.caseManualInferNetworkFamily({ chainId, chainName, token: tokenSymbol });
      const sourceLabel = kind === 'erc20'
        ? (bundleFamily === 'tron' ? 'single TRC20 transfer record' : 'single ERC20 transfer log')
        : (kind === 'internal' ? 'single internal native transfer' : 'native transaction value');
      return {
        kind,
        txHash: this.caseManualNormalizeText(rawRow.hash || rawRow.transaction_hash || rawRow.transactionHash || rawRow.tx_hash || bundle.tx_hash || bundle.transaction_hash || fallback.hash || ''),
        fromAddress,
        toAddress,
        amount: amountNumber,
        amountText,
        tokenSymbol,
        dateText: this.caseManualParseProviderDate(rawDate),
        chainId,
        chainName,
        sourceLabel
      };
    },

    extractCaseManualBundleCandidates(bundle = {}, fallback = {}) {
      const asArray = value => Array.isArray(value) ? value : (value && typeof value === 'object' ? [value] : []);
      const mainRows = asArray(bundle.main_transactions || bundle.mainTransactions || bundle.main || bundle.main_transaction);
      const tokenRows = asArray(bundle.token_transfers || bundle.tokenTransfers || bundle.erc20 || bundle.trc20);
      const internalRows = asArray(bundle.internal_transactions || bundle.internalTransactions || bundle.internal);
      const logs = asArray(bundle.logs || bundle.transfer_logs || bundle.transferLogs);
      const mainCandidates = mainRows.map(row => this.normalizeCaseManualBundleCandidate(row, 'main', bundle, fallback)).filter(Boolean);
      const tokenCandidates = tokenRows.map(row => this.normalizeCaseManualBundleCandidate(row, 'erc20', bundle, fallback)).filter(Boolean);
      const internalCandidates = internalRows.map(row => this.normalizeCaseManualBundleCandidate(row, 'internal', bundle, fallback)).filter(Boolean);
      if (tokenCandidates.length === 1 && mainCandidates.every(candidate => !candidate.amount || candidate.amount <= 0)) return tokenCandidates;
      if (tokenCandidates.length === 1 && logs.length <= 1) return tokenCandidates;
      if (mainCandidates.length === 1 && tokenCandidates.length === 0 && internalCandidates.length === 0 && logs.length === 0) return mainCandidates;
      if (internalCandidates.length === 1 && tokenCandidates.length === 0 && mainCandidates.length === 0) return internalCandidates;
      return [...tokenCandidates, ...mainCandidates, ...internalCandidates];
    },

    chooseCaseManualBundleCandidate(candidates = [], fallback = {}) {
      const viable = Array.isArray(candidates) ? candidates.filter(Boolean) : [];
      if (!viable.length) return null;
      const targetKey = this.caseManualNormalizeAddressKey(fallback.toAddress || fallback.targetAddress || '');
      if (targetKey) {
        const match = viable.find(candidate => this.caseManualNormalizeAddressKey(candidate.toAddress) === targetKey);
        if (match) return match;
      }
      const fromKey = this.caseManualNormalizeAddressKey(fallback.fromAddress || '');
      if (fromKey) {
        const match = viable.find(candidate => this.caseManualNormalizeAddressKey(candidate.fromAddress) === fromKey);
        if (match && viable.length === 1) return match;
      }
      if (viable.length === 1) return viable[0];
      return null;
    },

    caseManualStartOutputChoiceDirectionForCandidate(candidate = {}) {
      const explicit = this.caseManualNormalizeText(candidate.traceDirection || candidate.analysisDirection || candidate.direction || candidate.hopDirection || '').toLowerCase();
      if (/reverse|backward|source|from/iu.test(explicit)) return 'reverse';
      if (/forward|recipient|destination|to/iu.test(explicit)) return 'forward';
      const hopLogic = this.caseManualNormalizeText(candidate.hopLogic || candidate.hop_logic || candidate.rawRow?.[CASE_MANUAL_START_COL.HOP_LOGIC] || '').toLowerCase();
      if (/reverse|backward|source|from/iu.test(hopLogic)) return 'reverse';
      const row = Array.isArray(candidate.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate.row) ? candidate.row : []);
      const directionEvidence = [
        candidate.caseNotes,
        candidate.systemNotes,
        row[CASE_MANUAL_START_COL.CASE_NOTES],
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES]
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      if (/\b(?:tool\s*88|flow\s*solver)\s*:\s*reverse\b|\banalysis\s*direction\s*[:=]\s*reverse\b|\btrace\s*direction\s*[:=]\s*reverse\b/iu.test(directionEvidence)) return 'reverse';
      if (/\b(?:tool\s*88|flow\s*solver)\s*:\s*forward\b|\banalysis\s*direction\s*[:=]\s*forward\b|\btrace\s*direction\s*[:=]\s*forward\b/iu.test(directionEvidence)) return 'forward';
      const savedDirection = this.caseManualNormalizeText(
        this.caseManualStartTool88Settings?.traceDirection
          || this.caseManualStartSettingsDraft?.traceDirection
          || CASE_MANUAL_START_TOOL88_DEFAULT_SETTINGS.traceDirection
          || 'forward'
      ).toLowerCase();
      return savedDirection === 'reverse' ? 'reverse' : 'forward';
    },

    caseManualStartCandidateIsReverse(candidate = {}) {
      return this.caseManualStartOutputChoiceDirectionForCandidate(candidate) === 'reverse';
    },

    caseManualStartCandidateEndpointRole(candidate = {}) {
      return this.caseManualStartCandidateIsReverse(candidate) ? 'from' : 'to';
    },

    caseManualStartCandidateEndpointColumnIndex(candidate = {}) {
      return this.caseManualStartCandidateIsReverse(candidate)
        ? CASE_MANUAL_START_COL.FROM_ADDRESS
        : CASE_MANUAL_START_COL.TO_ADDRESS;
    },

    caseManualStartCandidateAttributionColumnIndex(candidate = {}) {
      return this.caseManualStartCandidateIsReverse(candidate)
        ? CASE_MANUAL_START_COL.FROM_ATTRIBUTION
        : CASE_MANUAL_START_COL.ATTRIBUTION;
    },

    caseManualStartRowEndpointAddress(row = [], candidate = {}) {
      return this.caseManualNormalizeText(row?.[this.caseManualStartCandidateEndpointColumnIndex(candidate)] || '');
    },

    caseManualStartRowEndpointAttribution(row = [], candidate = {}) {
      const value = row?.[this.caseManualStartCandidateAttributionColumnIndex(candidate)] || '';
      return this.caseManualStartCandidateIsReverse(candidate)
        ? this.caseManualCanonicalizeFromAttributionForColumnX(value)
        : this.caseManualCanonicalizeAttributionForColumnK(value);
    },

    caseManualStartCandidateEndpointAddress(candidate = {}) {
      const row = Array.isArray(candidate?.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate?.row) ? candidate.row : []);
      return this.caseManualNormalizeText(
        this.caseManualStartCandidateIsReverse(candidate)
          ? (candidate?.fromAddress || row[CASE_MANUAL_START_COL.FROM_ADDRESS] || candidate?.selectedTargetAddress || candidate?.targetAddress || '')
          : (candidate?.toAddress || row[CASE_MANUAL_START_COL.TO_ADDRESS] || candidate?.selectedTargetAddress || candidate?.targetAddress || candidate?.address || '')
      );
    },

    caseManualStartCandidateCounterpartyAddress(candidate = {}) {
      const row = Array.isArray(candidate?.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate?.row) ? candidate.row : []);
      return this.caseManualNormalizeText(
        this.caseManualStartCandidateIsReverse(candidate)
          ? (candidate?.toAddress || row[CASE_MANUAL_START_COL.TO_ADDRESS] || '')
          : (candidate?.fromAddress || row[CASE_MANUAL_START_COL.FROM_ADDRESS] || '')
      );
    },

    caseManualStartChildConnectionAddress(candidate = {}) {
      return this.caseManualStartCandidateIsReverse(candidate)
        ? this.caseManualNormalizeText(candidate?.toAddress || candidate?.rawRow?.[CASE_MANUAL_START_COL.TO_ADDRESS] || candidate?.row?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '')
        : this.caseManualNormalizeText(candidate?.fromAddress || candidate?.rawRow?.[CASE_MANUAL_START_COL.FROM_ADDRESS] || candidate?.row?.[CASE_MANUAL_START_COL.FROM_ADDRESS] || '');
    },

    caseManualAttributionLabelLooksLikeDeposit(value = '') {
      const label = this.caseManualCleanAttributionLabel(value);
      if (!label) return false;
      if (/\b(?:bridge|bridger|cross[-\s]?chain|swap|dex|router)\b/iu.test(label)) return false;
      return /\bdeposit(?:s|\s+address|\s+wallet)?\b/iu.test(label);
    },

    caseManualStartTextHasReverseEndpointDecision(value = '') {
      const text = this.caseManualNormalizeText(value);
      if (!text) return false;
      return /\bdirection\s*=\s*reverse\b|\breverse(?:\s+(?:fifo|lifo|next[-\s]?chronological))?[^.\n]{0,160}(?:path\s*ends?|path[-_\s]*ending|source[-_\s]*of[-_\s]*funds\s+ending|unknown[-_\s]*service)|\bsource[-_\s]*of[-_\s]*funds[^.\n]{0,120}(?:path\s*ends?|ending)/iu.test(text);
    },

    caseManualStartPathLabelLooksInitialFlowRoot(label = '') {
      const text = this.caseManualNormalizeText(label);
      if (!text) return true;
      if (/^\d+$/u.test(text)) return true;
      if (/^[A-Z]+$/u.test(text)) return true;
      if (/-\d+$/u.test(text) && !/[a-z.]\d*$/u.test(text)) return true;
      return false;
    },

    caseManualStartCandidateIsInitialFlowTransaction(candidate = {}) {
      if (!candidate?.caseManualStartCandidate) return false;
      const row = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const hash = this.caseManualNormalizeText(candidate.hash || candidate.transactionHash || candidate.txHash || row[CASE_MANUAL_START_COL.TX_HASH]);
      if (!hash) return false;

      // The output/input chooser is only for the first transaction in a Manual
      // Solve flow, where the starting entry itself is ambiguous. Once a row has
      // Column O / From Hash, it is a downstream hop and should be handled by the
      // hop inspector / trace-through workflow instead of re-choosing the root
      // transaction's To/From address.
      const fromHash = this.caseManualNormalizeText(candidate.fromHash || row[CASE_MANUAL_START_COL.FROM_HASH]);
      if (fromHash) return false;

      const pathLabel = this.caseManualNormalizeText(candidate.pathLabel || row[CASE_MANUAL_START_COL.PATH_LABEL]);
      if (!this.caseManualStartPathLabelLooksInitialFlowRoot(pathLabel)) return false;

      const parentHashLike = this.caseManualNormalizeText(
        candidate.parentHash
          || candidate.parentTransactionHash
          || candidate.precedingTransactionHash
          || candidate.precedingHash
          || ''
      );
      return !parentHashLike;
    },

    caseManualStartBtcChoiceWasApplied(candidate = {}) {
      const row = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const text = [
        candidate.caseNotes,
        candidate.systemNotes,
        candidate.hydrationMessage,
        candidate.sourceLabel,
        row[CASE_MANUAL_START_COL.CASE_NOTES],
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES]
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join('\n');
      return Boolean(
        candidate.btcOutputChoiceConfirmed === true
        || candidate.outputChoiceConfirmed === true
        || /Tool\s*88\s+BTC\s+input\/output\s+chooser/iu.test(text)
        || /Tool\s*88\s+BTC\s+reverse\s+input\s+selection\s*:\s*direction\s*=\s*reverse/iu.test(text)
        || /Case\s+Buddy\s+manual\s+choice:[^\r\n]*\bselected\s+(?:Output|Input|BTC\s+output|BTC\s+input)/iu.test(text)
      );
    },

    caseManualStartCandidateNeedsOutputChoice(candidate = {}) {
      if (!candidate?.caseManualStartCandidate) return false;
      if (!this.caseManualStartCandidateIsInitialFlowTransaction(candidate)) return false;
      const direction = this.caseManualStartOutputChoiceDirectionForCandidate(candidate);
      const row = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const missingTo = !this.caseManualNormalizeText(candidate.toAddress || candidate.targetAddress || row[CASE_MANUAL_START_COL.TO_ADDRESS]);
      const missingFrom = !this.caseManualNormalizeText(candidate.fromAddress || row[CASE_MANUAL_START_COL.FROM_ADDRESS]);
      const family = this.caseManualInferNetworkFamily(candidate);

      if (family === 'btc') {
        const choiceWasApplied = this.caseManualStartBtcChoiceWasApplied(candidate);
        // Older Reverse BTC rows may already contain the selected input marker
        // while Column D is still blank because output context was not retained.
        // Let the chooser reopen in that narrow repair case; a fully populated
        // selected-input entry remains complete and does not ask again.
        if (choiceWasApplied && !(direction === 'reverse' && missingTo)) return false;
        const outputChoiceCount = Number(
          candidate.btcOutputChoiceCount
            || candidate.btcOutputCount
            || candidate.outputChoiceCount
            || candidate.outputCount
            || 0
        ) || 0;
        const inputChoiceCount = Number(
          candidate.btcInputChoiceCount
            || candidate.btcInputAddressCount
            || candidate.inputChoiceCount
            || 0
        ) || 0;
        const hydrationMessage = this.caseManualNormalizeText(candidate.hydrationMessage || candidate.message || '');
        const providerMarkedAmbiguous = candidate.btcOutputChoiceRequired === true
          || candidate.needsBtcOutputChoice === true
          || candidate.btcNeedsOutputChoice === true
          || (candidate.hydrationStatus === 'ambiguous' && /btc|output|input|choice|multiple/iu.test(hydrationMessage));
        // If the investigator already supplied the BTC side of interest in
        // Column C/D, do not reopen the BTC chooser merely because the provider
        // found multiple inputs/outputs. The chooser is only needed when the
        // relevant side is still unknown.
        if (direction === 'reverse') {
          if (!missingFrom && missingTo && choiceWasApplied) return true;
          if (!missingFrom) return false;
          return providerMarkedAmbiguous || inputChoiceCount > 1 || missingFrom;
        }
        if (!missingTo) return false;
        return providerMarkedAmbiguous || outputChoiceCount > 1 || missingTo;
      }

      return direction === 'reverse' ? missingFrom : missingTo;
    },

    async openCaseManualStartOutputChoiceFromSelection() {
      const candidate = this.caseManualStartOutputChoiceActiveCandidate;
      if (!this.caseManualStartCandidateNeedsOutputChoice(candidate)) {
        this.caseManualStartOutputChoiceStatus = 'The output/input chooser is only for the first Manual Solve transaction in a flow when BTC outputs are ambiguous or the starting address side is still missing. Use Solve hop for later unresolved endings.';
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartOutputChoiceStatus);
        return;
      }
      this.caseManualStartOutputChoiceCandidate = candidate;
      this.caseManualStartOutputChoiceOpen = true;
      this.caseManualStartOutputChoiceStatus = '';
      this.caseManualStartOutputChoiceRows = [];
      this.caseManualStartOutputChoiceSelectedKey = '';
      return await this.loadCaseManualStartOutputChoices();
    },

    closeCaseManualStartOutputChoice() {
      this.caseManualStartOutputChoiceOpen = false;
      this.caseManualStartOutputChoiceLoading = false;
      this.caseManualStartOutputChoiceStatus = '';
      this.caseManualStartOutputChoiceCandidate = null;
      this.caseManualStartOutputChoiceRows = [];
      this.caseManualStartOutputChoiceSelectedKey = '';
    },

    async selectAndApplyCaseManualStartOutputChoice(choice = {}) {
      if (!choice?.key || this.caseManualStartOutputChoiceLoading) return;
      this.caseManualStartOutputChoiceSelectedKey = choice.key;
      await this.applyCaseManualStartOutputChoice();
    },

    caseManualNormalizeBtcInputAddressChoice(raw = {}, index = 0) {
      if (!raw || typeof raw !== 'object') return null;
      const nestedScriptPubKey = raw.scriptPubKey || raw.scriptpubkey || raw.script_pub_key || {};
      const nestedPrevout = raw.prevout || raw.coin || {};
      const address = this.caseManualNormalizeText(
        raw.address
          || raw.from_address
          || raw.fromAddress
          || raw.input_address
          || raw.inputAddress
          || raw.scriptpubkey_address
          || raw.scriptPubKeyAddress
          || nestedScriptPubKey.address
          || nestedScriptPubKey.scriptpubkey_address
          || nestedPrevout.address
          || nestedPrevout.scriptpubkey_address
          || ''
      );
      if (!address || /^message$/iu.test(address)) return null;
      const order = this.caseManualNormalizeText(raw.order ?? raw.index ?? raw.n ?? raw.vin ?? raw.prev_index ?? raw.prevIndex ?? index);
      const explicitBtcText = this.caseManualNormalizeText(raw.value_btc || raw.amount_btc || raw.btc_value || raw.amount_btc_formatted || '');
      const satoshiText = this.caseManualNormalizeText(raw.value_satoshis || raw.value_sats || raw.satoshis || raw.amount_satoshis || raw.valueSat || raw.value_sat || raw.value || nestedPrevout.value || '');
      const genericValueText = this.caseManualNormalizeText(raw.amount || raw.amountText || raw.value || '');
      const valueNumber = this.caseManualParseNumericAmount(explicitBtcText || genericValueText);
      const amountText = explicitBtcText && Number.isFinite(valueNumber) && valueNumber > 0
        ? this.caseManualFormatAmount(valueNumber, { maximumFractionDigits: 8 })
        : (satoshiText && /^\d+$/u.test(satoshiText) ? this.caseManualFormatAmount(Number(satoshiText) / 100000000, { maximumFractionDigits: 8 }) : genericValueText);
      return {
        key: `btc-input-address-${order || index}-${address}-${amountText || ''}`,
        address,
        fromAddress: address,
        amountText,
        token: 'BTC',
        order,
        raw
      };
    },

    caseManualCollectBtcInputAddressRows(rawRows = []) {
      const rows = [];
      const seen = new Set();
      (Array.isArray(rawRows) ? rawRows : []).forEach((raw, index) => {
        const row = this.caseManualNormalizeBtcInputAddressChoice(raw, index);
        const key = this.caseManualNormalizeAddressKey(row?.address || row?.fromAddress || '');
        if (!row || !key || seen.has(key)) return;
        seen.add(key);
        rows.push(row);
      });
      return rows;
    },

    caseManualNormalizeBtcOutputAddressChoice(raw = {}, index = 0) {
      if (!raw || typeof raw !== 'object') return null;
      const nestedScriptPubKey = raw.scriptPubKey || raw.scriptpubkey || raw.script_pub_key || {};
      const address = this.caseManualNormalizeText(
        raw.address
          || raw.to_address
          || raw.toAddress
          || raw.output_address
          || raw.outputAddress
          || raw.scriptpubkey_address
          || raw.scriptPubKeyAddress
          || nestedScriptPubKey.address
          || nestedScriptPubKey.scriptpubkey_address
          || ''
      );
      if (!address || /^message$/iu.test(address)) return null;
      const order = this.caseManualNormalizeText(raw.order ?? raw.index ?? raw.n ?? raw.vout ?? raw.output_index ?? raw.outputIndex ?? index);
      const explicitBtcText = this.caseManualNormalizeText(raw.value_btc || raw.amount_btc || raw.btc_value || raw.amount_btc_formatted || '');
      const satoshiText = this.caseManualNormalizeText(raw.value_satoshis || raw.value_sats || raw.satoshis || raw.amount_satoshis || raw.valueSat || raw.value_sat || raw.value || '');
      const genericValueText = this.caseManualNormalizeText(raw.amount || raw.amountText || raw.value || '');
      const valueNumber = this.caseManualParseNumericAmount(explicitBtcText || genericValueText);
      const amountText = explicitBtcText && Number.isFinite(valueNumber) && valueNumber > 0
        ? this.caseManualFormatAmount(valueNumber, { maximumFractionDigits: 8 })
        : (satoshiText && /^\d+$/u.test(satoshiText) ? this.caseManualFormatAmount(Number(satoshiText) / 100000000, { maximumFractionDigits: 8 }) : genericValueText);
      return {
        key: `btc-output-address-${order || index}-${address}-${amountText || ''}`,
        address,
        toAddress: address,
        amountText,
        token: 'BTC',
        order,
        outputIndex: Number.isFinite(Number(order)) ? Math.trunc(Number(order)) : null,
        valueSatoshis: this.caseManualBtcChoiceValueSatoshis(raw, amountText),
        raw
      };
    },

    caseManualCollectBtcOutputAddressRows(rawRows = []) {
      const rows = [];
      const seen = new Set();
      (Array.isArray(rawRows) ? rawRows : []).forEach((raw, index) => {
        const row = this.caseManualNormalizeBtcOutputAddressChoice(raw, index);
        if (!row) return;
        const addressKey = this.caseManualNormalizeAddressKey(row.address || row.toAddress || '');
        if (!addressKey) return;
        const orderKey = this.caseManualNormalizeText(row.order);
        const amountKey = this.caseManualNormalizeText(row.amountText).replace(/,/gu, '');
        const key = `${orderKey || index}:${addressKey}:${amountKey}`;
        if (seen.has(key)) return;
        seen.add(key);
        rows.push(row);
      });
      return rows;
    },

    caseManualChoiceOutputAddressRows(choice = {}) {
      const raw = choice?.raw && typeof choice.raw === 'object' ? choice.raw : {};
      const candidates = [
        choice.outputAddresses,
        choice.toAddresses,
        choice.btcOutputAddresses,
        raw.output_addresses,
        raw.to_addresses,
        raw.btc_output_addresses,
        raw.outputs,
        raw.vout,
        raw.btc_outputs,
        raw.outputChoices,
        raw.output_choices
      ];
      for (const candidate of candidates) {
        const rows = this.caseManualCollectBtcOutputAddressRows(candidate);
        if (rows.length) return rows;
      }
      const single = this.caseManualNormalizeText(choice.toAddress || raw.to_address || raw.toAddress || '');
      const direction = this.caseManualNormalizeText(choice.direction || raw.direction || '').toLowerCase();
      const selectedInputAddress = this.caseManualNormalizeText(choice.fromAddress || choice.address || raw.from_address || raw.fromAddress || raw.address || '');
      if (
        direction === 'reverse'
          && single
          && this.caseManualNormalizeAddressKey(single) === this.caseManualNormalizeAddressKey(selectedInputAddress)
      ) return [];
      return single ? [{ key: `btc-output-address-single-${single}`, address: single, toAddress: single, amountText: '', token: 'BTC', order: '0', raw: {} }] : [];
    },

    caseManualChoiceInputAddressRows(choice = {}) {
      const raw = choice?.raw && typeof choice.raw === 'object' ? choice.raw : {};
      const candidates = [
        choice.inputAddresses,
        choice.fromAddresses,
        choice.btcInputAddresses,
        raw.input_addresses,
        raw.from_addresses,
        raw.btc_input_addresses,
        raw.inputs,
        raw.btc_inputs,
        raw.inputChoices,
        raw.input_choices
      ];
      for (const candidate of candidates) {
        const rows = this.caseManualCollectBtcInputAddressRows(candidate);
        if (rows.length) return rows;
      }
      const single = this.caseManualNormalizeText(choice.fromAddress || raw.from_address || raw.fromAddress || '');
      return single ? [{ key: `btc-input-address-single-${single}`, address: single, fromAddress: single, amountText: '', token: 'BTC', order: '0', raw: {} }] : [];
    },

    caseManualLimitedBtcVisualInputAddressRows(candidate = {}) {
      const sourceRows = Array.isArray(candidate.btcInputAddresses) && candidate.btcInputAddresses.length
        ? candidate.btcInputAddresses
        : (candidate.fromAddress ? [{ address: candidate.fromAddress, fromAddress: candidate.fromAddress, amountText: candidate.amount || '', token: candidate.token || 'BTC', order: '0' }] : []);
      let rows = this.caseManualCollectBtcInputAddressRows(sourceRows);
      // The selected/continued BTC input address is the visual bridge from the
      // previous output address into the next transaction. Keep it visible even
      // when a transaction has many inputs and the stored input list is clipped.
      const selectedFromAddress = this.caseManualNormalizeText(candidate.fromAddress || candidate[CASE_MANUAL_START_COL.FROM_ADDRESS] || '');
      const selectedFromKey = this.caseManualNormalizeAddressKey(selectedFromAddress);
      if (selectedFromKey) {
        const selectedIndex = rows.findIndex(row => this.caseManualNormalizeAddressKey(row?.fromAddress || row?.address || '') === selectedFromKey);
        if (selectedIndex > 0) {
          const [selectedRow] = rows.splice(selectedIndex, 1);
          rows = [selectedRow, ...rows];
        } else if (selectedIndex < 0) {
          rows = [{ address: selectedFromAddress, fromAddress: selectedFromAddress, amountText: candidate.amount || '', token: candidate.token || 'BTC', order: 'selected' }, ...rows];
        }
      }
      const limit = CASE_MANUAL_START_BTC_VISUAL_INPUT_ADDRESS_LIMIT;
      return {
        rows: rows.slice(0, limit),
        totalCount: rows.length,
        hiddenCount: Math.max(0, rows.length - limit)
      };
    },

    caseManualLimitedBtcVisualOutputAddressRows(candidate = {}) {
      const sourceRows = Array.isArray(candidate.btcOutputAddresses) && candidate.btcOutputAddresses.length
        ? candidate.btcOutputAddresses
        : (candidate.toAddress ? [{ address: candidate.toAddress, toAddress: candidate.toAddress, amountText: '', token: candidate.token || 'BTC', order: '0' }] : []);
      const rows = this.caseManualCollectBtcOutputAddressRows(sourceRows);
      const limit = CASE_MANUAL_START_BTC_VISUAL_OUTPUT_ADDRESS_LIMIT;
      return {
        rows: rows.slice(0, limit),
        totalCount: rows.length,
        hiddenCount: Math.max(0, rows.length - limit)
      };
    },

    caseManualBtcChoicePreviousTransactionHash(raw = {}) {
      const nestedPrevout = raw?.prevout || raw?.coin || {};
      return this.caseManualNormalizeText(
        raw?.previousTransactionHash
          || raw?.previous_transaction_hash
          || raw?.prev_txid
          || raw?.prevTxid
          || raw?.prev_hash
          || raw?.prevHash
          || nestedPrevout?.txid
          || nestedPrevout?.hash
          || ''
      );
    },

    caseManualBtcChoicePreviousOutputIndex(raw = {}) {
      const nestedPrevout = raw?.prevout || raw?.coin || {};
      const value = raw?.previousOutputIndex
        ?? raw?.previous_output_index
        ?? raw?.prev_vout
        ?? raw?.prevVout
        ?? raw?.prev_index
        ?? raw?.prevIndex
        ?? nestedPrevout?.vout
        ?? nestedPrevout?.index;
      const cleanValue = this.caseManualNormalizeText(value);
      if (!cleanValue) return null;
      const numeric = Number(cleanValue);
      return Number.isFinite(numeric) && numeric >= 0 ? Math.trunc(numeric) : null;
    },

    caseManualBtcChoiceValueSatoshis(raw = {}, amountText = '') {
      const nestedPrevout = raw?.prevout || raw?.coin || {};
      const direct = raw?.value_satoshis
        ?? raw?.value_sats
        ?? raw?.satoshis
        ?? raw?.amount_satoshis
        ?? raw?.valueSat
        ?? raw?.value_sat
        ?? nestedPrevout?.value_satoshis
        ?? nestedPrevout?.value_sats
        ?? nestedPrevout?.satoshis
        ?? nestedPrevout?.value;
      const directNumber = Number(this.caseManualNormalizeText(direct).replace(/,/gu, ''));
      if (Number.isFinite(directNumber) && directNumber > 0 && Number.isInteger(directNumber)) return Math.trunc(directNumber);
      const btcAmount = this.caseManualParseNumericAmount(amountText);
      return Number.isFinite(btcAmount) && btcAmount > 0 ? Math.round(btcAmount * 100000000) : null;
    },

    caseManualStripForwardDormantEndingText(value = '') {
      const text = String(value ?? '');
      if (!text.trim()) return '';
      return text
        .split(/\r?\n/u)
        .map(line => this.caseManualNormalizeText(line))
        .filter(Boolean)
        .filter(line => !/A portion of the funds remains at the receiving address\s*\([^)]*\)\.?/iu.test(line))
        .filter(line => !/Tool\s*88\s+dormant\s+flow:\s*assessed/iu.test(line))
        .join('\n');
    },

    caseManualStripInvalidBtcReverseSwapReviewText(value = '', options = {}) {
      const text = String(value ?? '');
      if (!text.trim()) return '';
      const removeGenericManualWork = options.removeGenericManualWork === true;
      return text
        .split(/\r?\n/u)
        .map(line => this.caseManualNormalizeText(line))
        .filter(Boolean)
        .filter(line => !/Reverse\s+swap\s+solver\s+could\s+not\s+determine/iu.test(line))
        .filter(line => !/Manual\s+swap\s+review\s+required/iu.test(line))
        .filter(line => !/integrationAction\s*[=:]\s*manual-swap-review|action\s*[=:]\s*manual-swap-review/iu.test(line))
        .filter(line => !(removeGenericManualWork && /Further\s+manual\s+work\s+is\s+required\s+to\s+resolve\s+this\s+path\.?/iu.test(line)))
        .join('\n');
    },

    caseManualNormalizeBtcChoice(raw = {}, index = 0, direction = 'forward') {
      if (!raw || typeof raw !== 'object') return null;
      const nestedScriptPubKey = raw.scriptPubKey || raw.scriptpubkey || raw.script_pub_key || {};
      const nestedPrevout = raw.prevout || raw.coin || {};
      const address = this.caseManualNormalizeText(
        raw.address
          || raw.to_address
          || raw.toAddress
          || raw.from_address
          || raw.fromAddress
          || raw.scriptpubkey_address
          || raw.scriptPubKeyAddress
          || nestedScriptPubKey.address
          || nestedScriptPubKey.scriptpubkey_address
          || nestedPrevout.address
          || nestedPrevout.scriptpubkey_address
          || ''
      );
      if (!address || /^message$/iu.test(address)) return null;
      const order = this.caseManualNormalizeText(raw.order ?? raw.index ?? raw.n ?? raw.vout ?? raw.prev_index ?? raw.prevIndex ?? index);
      const explicitBtcText = this.caseManualNormalizeText(raw.value_btc || raw.amount_btc || raw.btc_value || raw.amount_btc_formatted || '');
      const satoshiText = this.caseManualNormalizeText(raw.value_satoshis || raw.value_sats || raw.satoshis || raw.amount_satoshis || raw.valueSat || raw.value_sat || raw.value || '');
      const genericValueText = this.caseManualNormalizeText(raw.amount || raw.amountText || raw.value || '');
      const valueText = explicitBtcText || genericValueText;
      const valueNumber = this.caseManualParseNumericAmount(valueText);
      const amountText = explicitBtcText && Number.isFinite(valueNumber) && valueNumber > 0
        ? this.caseManualFormatAmount(valueNumber, { maximumFractionDigits: 8 })
        : (satoshiText && /^\d+$/u.test(satoshiText) ? this.caseManualFormatAmount(Number(satoshiText) / 100000000, { maximumFractionDigits: 8 }) : valueText);
      if (!amountText) return null;
      const isReverse = direction === 'reverse' || this.caseManualNormalizeText(raw.direction).toLowerCase() === 'reverse' || this.caseManualNormalizeText(raw.choice_kind).toLowerCase().includes('input');
      const inputAddresses = isReverse ? [] : this.caseManualChoiceInputAddressRows({ raw });
      const inputAddressCount = Number(raw.input_address_count || raw.inputAddressCount || inputAddresses.length || 0) || inputAddresses.length;
      const outputAddresses = isReverse ? this.caseManualChoiceOutputAddressRows({ raw }) : [];
      const outputAddressCount = Number(raw.output_address_count || raw.outputAddressCount || outputAddresses.length || 0) || outputAddresses.length;
      const previousTransactionHash = isReverse ? this.caseManualBtcChoicePreviousTransactionHash(raw) : '';
      const previousOutputIndex = isReverse ? this.caseManualBtcChoicePreviousOutputIndex(raw) : null;
      const valueSatoshis = this.caseManualBtcChoiceValueSatoshis(raw, amountText);
      const reverseCounterparty = this.caseManualNormalizeText(
        raw.counterparty_address
          || raw.counterpartyAddress
          || raw.output_address
          || raw.outputAddress
          || raw.destination_address
          || raw.destinationAddress
          || ''
      );
      const firstReverseOutputAddress = this.caseManualNormalizeText(outputAddresses[0]?.toAddress || outputAddresses[0]?.address || '');
      const reverseToAddress = reverseCounterparty && this.caseManualNormalizeAddressKey(reverseCounterparty) !== this.caseManualNormalizeAddressKey(address)
        ? reverseCounterparty
        : firstReverseOutputAddress;
      const key = `btc-${isReverse ? 'input' : 'output'}-${order || index}-${address}-${amountText}`;
      return {
        key,
        family: 'btc',
        direction: isReverse ? 'reverse' : 'forward',
        title: isReverse ? `Input #${order || index}` : `Output #${order || index}`,
        address,
        fromAddress: isReverse ? address : this.caseManualNormalizeText(raw.from_address || raw.fromAddress || inputAddresses[0]?.fromAddress || inputAddresses[0]?.address || ''),
        toAddress: isReverse ? reverseToAddress : address,
        amountText,
        token: 'BTC',
        order,
        inputIndex: isReverse && Number.isFinite(Number(order)) ? Math.trunc(Number(order)) : null,
        previousTransactionHash,
        previousOutputIndex,
        valueSatoshis,
        inputAddresses,
        fromAddresses: inputAddresses,
        inputAddressCount,
        hiddenInputAddressCount: Math.max(0, inputAddressCount - CASE_MANUAL_START_BTC_VISUAL_INPUT_ADDRESS_LIMIT),
        outputAddresses,
        toAddresses: outputAddresses,
        outputAddressCount,
        hiddenOutputAddressCount: Math.max(0, outputAddressCount - CASE_MANUAL_START_BTC_VISUAL_OUTPUT_ADDRESS_LIMIT),
        detail: isReverse
          ? `Use this input as the source side for reverse solving. ${outputAddressCount ? `${outputAddressCount} transaction output${outputAddressCount === 1 ? '' : 's'} will be kept as context; no output selection is required.` : 'Transaction outputs will be kept as context when available.'}`
          : (inputAddressCount > 1 ? `Use this output as the To Address. ${inputAddressCount} BTC input From addresses will be preserved in the entry.` : 'Use this output address as the To Address and followed amount.'),
        raw
      };
    },

    caseManualNormalizeTransferChoice(raw = {}, index = 0, candidate = {}, direction = 'forward') {
      if (!raw || typeof raw !== 'object') return null;
      const fromAddress = this.caseManualNormalizeText(raw.fromAddress || raw.from_address || raw.from || raw.sender || raw.ownerAddress || raw.owner_address || '');
      const toAddress = this.caseManualNormalizeText(raw.toAddress || raw.to_address || raw.to || raw.receiver || raw.recipient || raw.to_address_base58 || '');
      if (!fromAddress && !toAddress) return null;
      const amountRaw = this.caseManualNormalizeText(raw.amountText || raw.amount_formatted || raw.amountFormatted || raw.value_formatted || raw.valueFormatted || raw.amount || raw.value || '');
      const amountNumber = this.caseManualParseNumericAmount(amountRaw);
      const amountText = Number.isFinite(amountNumber) && amountNumber > 0 ? this.caseManualFormatAmount(amountNumber) : amountRaw;
      if (!amountText) return null;
      const tokenInfo = raw.tokenInfo || raw.token_info || {};
      const token = this.caseManualNormalizeText(raw.tokenSymbol || raw.token_symbol || raw.token || raw.symbol || tokenInfo.tokenAbbr || tokenInfo.symbol || candidate.token || '').toUpperCase();
      const priorBalance = this.caseManualNormalizeText(
        raw.priorBalanceBeforeTransaction
          ?? raw.prior_balance_before_transaction
          ?? raw.balanceBeforeTransaction
          ?? raw.balance_before_transaction
          ?? raw.assetBalanceBefore
          ?? raw.asset_balance_before
          ?? raw.balance_before
          ?? ''
      );
      const kind = this.caseManualNormalizeText(raw.kind || raw.sourceLabel || raw.transaction_kind || raw.type || 'transfer');
      const key = `transfer-${index}-${fromAddress}-${toAddress}-${amountText}-${token}`;
      return {
        key,
        family: this.caseManualInferNetworkFamily(candidate),
        direction,
        kind,
        title: kind || `Transfer #${index + 1}`,
        address: direction === 'reverse' ? fromAddress : toAddress,
        fromAddress,
        toAddress,
        amountText,
        amountNumber: Number.isFinite(amountNumber) ? amountNumber : null,
        token,
        priorBalance,
        dateText: this.caseManualParseProviderDate(raw.timeStamp || raw.timestamp || raw.date || raw.block_timestamp || '') || '',
        detail: direction === 'reverse' ? 'Use this transfer sender as the source address.' : 'Use this transfer recipient as the target address.',
        raw
      };
    },

    caseManualStartTransferChoiceKind(choice = {}) {
      const raw = choice?.raw && typeof choice.raw === 'object' ? choice.raw : {};
      return this.caseManualNormalizeText(
        choice.kind
          || choice.title
          || raw.kind
          || raw.sourceLabel
          || raw.source_label
          || raw.transaction_kind
          || raw.transactionKind
          || raw.type
          || ''
      ).toLowerCase().replace(/[\s-]+/gu, '_');
    },

    caseManualStartTransferChoiceAmountNumber(choice = {}) {
      if (choice?.amountNumber !== null && choice?.amountNumber !== undefined && choice?.amountNumber !== '') {
        const explicit = Number(choice.amountNumber);
        if (Number.isFinite(explicit)) return explicit;
      }
      const raw = choice?.raw && typeof choice.raw === 'object' ? choice.raw : {};
      const values = [
        choice.amountText,
        choice.amount,
        raw.amount_formatted,
        raw.amountFormatted,
        raw.value_formatted,
        raw.valueFormatted,
        raw.amount,
        raw.value
      ];
      for (const value of values) {
        const parsed = this.caseManualParseNumericAmount(value);
        if (Number.isFinite(parsed)) return parsed;
      }
      return NaN;
    },

    caseManualStartTransferChoiceIsMainEnvelope(choice = {}) {
      const kind = this.caseManualStartTransferChoiceKind(choice);
      if (!kind) return false;
      return [
        'main',
        'main_transaction',
        'evm_main_transaction',
        'tron_main_transaction',
        'native_transaction',
        'native_transaction_value',
        'evm_native_transaction',
        'tron_native_transaction'
      ].includes(kind)
        || /(?:^|_)main_transaction(?:$|_)/u.test(kind)
        || /(?:^|_)native_transaction_value(?:$|_)/u.test(kind);
    },

    caseManualStartTransferChoiceIsZeroValueMainEnvelope(choice = {}) {
      if (!this.caseManualStartTransferChoiceIsMainEnvelope(choice)) return false;
      const amount = this.caseManualStartTransferChoiceAmountNumber(choice);
      return Number.isFinite(amount) && amount === 0;
    },

    caseManualSimplifyStartTransferChoices(choices = [], candidate = {}) {
      const rows = Array.isArray(choices) ? choices.filter(Boolean) : [];
      if (this.caseManualInferNetworkFamily(candidate) === 'btc') return rows;

      // A normal ERC20/TRC20 transfer is represented on-chain by a zero-value
      // native transaction sent to the token contract (or another contract)
      // plus one or more positive transfer logs. The zero-value native envelope
      // is not another movement of the followed asset and should not create a
      // false target/source-choice prompt. Keep positive native transfers and
      // keep every real transfer when more than one remains, so genuine swaps,
      // batches, internal transfers, and multi-output cases still require review.
      const hasPositiveNonMainTransfer = rows.some(choice => {
        if (this.caseManualStartTransferChoiceIsMainEnvelope(choice)) return false;
        const amount = this.caseManualStartTransferChoiceAmountNumber(choice);
        return Number.isFinite(amount) && amount > 0;
      });
      if (!hasPositiveNonMainTransfer) return rows;
      return rows.filter(choice => !this.caseManualStartTransferChoiceIsZeroValueMainEnvelope(choice));
    },

    caseManualCollectArrayChoices(source = {}, keys = []) {
      const arrays = [];
      const visit = value => {
        if (!value) return;
        if (Array.isArray(value)) arrays.push(value);
        else if (typeof value === 'object') {
          keys.forEach(key => {
            if (Array.isArray(value[key])) arrays.push(value[key]);
          });
        }
      };
      visit(source);
      visit(source?.result);
      visit(source?.trace);
      return arrays.flat();
    },

    buildCaseManualStartOutputChoicesFromTool88Result(result = {}, candidate = {}, direction = 'forward') {
      const family = this.caseManualInferNetworkFamily(candidate);
      const rows = [];
      const seen = new Set();
      const add = choice => {
        if (!choice?.key || seen.has(choice.key)) return;
        seen.add(choice.key);
        rows.push(choice);
      };
      if (family === 'btc') {
        const keys = direction === 'reverse'
          ? ['candidate_inputs', 'input_choices', 'btc_inputs', 'inputs']
          : ['candidate_outputs', 'output_choices', 'btc_outputs', 'outputs'];
        const outputRows = direction === 'reverse'
          ? this.caseManualCollectBtcOutputAddressRows(this.caseManualCollectArrayChoices(result, ['candidate_outputs', 'output_choices', 'btc_outputs', 'outputs']))
          : [];
        this.caseManualCollectArrayChoices(result, keys).forEach((raw, index) => {
          const choice = this.caseManualNormalizeBtcChoice(raw, index, direction);
          if (choice && direction === 'reverse' && outputRows.length) {
            choice.outputAddresses = outputRows;
            choice.toAddresses = outputRows;
            choice.outputAddressCount = outputRows.length;
            choice.hiddenOutputAddressCount = Math.max(0, outputRows.length - CASE_MANUAL_START_BTC_VISUAL_OUTPUT_ADDRESS_LIMIT);
            choice.toAddress = this.caseManualNormalizeText(outputRows[0]?.toAddress || outputRows[0]?.address || '');
          }
          add(choice);
        });
      } else {
        const transferKeys = ['selected_transfer_choices', 'selected_transfers', 'token_transfers', 'tokenTransfers', 'erc20', 'trc20', 'internal_transactions', 'internalTransactions', 'main_transactions', 'mainTransactions'];
        this.caseManualCollectArrayChoices(result, transferKeys).forEach((raw, index) => add(this.caseManualNormalizeTransferChoice(raw, index, candidate, direction)));
      }
      return family === 'btc'
        ? rows
        : this.caseManualSimplifyStartTransferChoices(rows, candidate);
    },

    caseManualExtractTatumBtcChoiceArrays(result = {}) {
      const payload = result?.result || result?.data || result;
      if (!payload || typeof payload !== 'object') return { inputs: [], outputs: [] };
      const inputs = Array.isArray(payload.inputs) ? payload.inputs : (Array.isArray(payload.vin) ? payload.vin : []);
      const outputs = Array.isArray(payload.outputs) ? payload.outputs : (Array.isArray(payload.vout) ? payload.vout : []);
      return { inputs, outputs };
    },

    async fetchCaseManualStartBtcChoicesFromTatum(candidate = {}, direction = 'forward', options = {}) {
      const txHash = this.caseManualNormalizeText(candidate.hash || candidate.transactionHash || candidate.txHash || '');
      if (!txHash) return [];
      const response = await authenticatedFetch(
        '/api/tatum/fetch_tatum_btc_transaction_by_hash.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            hash: txHash,
          }),
          signal: options.signal || undefined,
        }
      );
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result || result.status === 'error') {
        const requestId =
          response.headers.get('X-Request-ID') ||
          result?.request_id ||
          '';

        const baseMessage =
          result?.message ||
          result?.error ||
          `Tatum BTC lookup failed (${response.status})`;

        throw new Error(
          requestId
            ? `${baseMessage} Request ${requestId}.`
            : baseMessage
        );
      }
      const { inputs, outputs } = this.caseManualExtractTatumBtcChoiceArrays(result);
      const rows = [];
      const seen = new Set();
      const add = choice => {
        if (!choice?.key || seen.has(choice.key)) return;
        seen.add(choice.key);
        rows.push(choice);
      };
      const inputAddressRows = this.caseManualCollectBtcInputAddressRows(inputs);
      const outputAddressRows = this.caseManualCollectBtcOutputAddressRows(outputs);
      const source = direction === 'reverse' ? inputs : outputs;
      source.forEach((raw, index) => {
        const enrichedRaw = direction === 'reverse'
          ? raw
          : {
              ...(raw || {}),
              input_addresses: inputAddressRows,
              from_addresses: inputAddressRows,
              btc_input_addresses: inputAddressRows,
              input_address_count: inputAddressRows.length
            };
        const choice = this.caseManualNormalizeBtcChoice(enrichedRaw, index, direction);
        if (choice && direction === 'reverse' && outputAddressRows.length) {
          choice.outputAddresses = outputAddressRows;
          choice.toAddresses = outputAddressRows;
          choice.outputAddressCount = outputAddressRows.length;
          choice.hiddenOutputAddressCount = Math.max(0, outputAddressRows.length - CASE_MANUAL_START_BTC_VISUAL_OUTPUT_ADDRESS_LIMIT);
          choice.toAddress = this.caseManualNormalizeText(outputAddressRows[0]?.toAddress || outputAddressRows[0]?.address || '');
        }
        add(choice);
      });
      return rows;
    },

    async fetchCaseManualStartOutputChoiceRows(candidate = {}, directionOverride = '', options = {}) {
      const direction = directionOverride || this.caseManualStartOutputChoiceDirectionForCandidate(candidate);
      const family = this.caseManualInferNetworkFamily(candidate);
      let result = null;
      let rows = [];
      let sourceLabel = 'Tool 88';
      if (family === 'btc') {
        result = await this.postCaseManualStartTool88({
          action: 'transaction_output_details',
          tx_hash: candidate.hash || candidate.transactionHash || candidate.txHash,
          transaction_hash: candidate.hash || candidate.transactionHash || candidate.txHash,
          chain_id: candidate.chainId || 'btc',
          chain_name: candidate.chainName || 'Bitcoin',
          network_family: 'btc',
          address: direction === 'reverse' ? (candidate.fromAddress || candidate.address || '') : (candidate.fromAddress || ''),
          return_selected_output_details: true,
          return_selected_output: true,
          include_raw_response: false
        }, { signal: options.signal });
        rows = this.buildCaseManualStartOutputChoicesFromTool88Result(result, candidate, direction);

        const reverseRowsNeedOutputContext = direction === 'reverse' && (
          !rows.length
            || rows.some(choice => {
              const outputRows = this.caseManualChoiceOutputAddressRows(choice);
              return !outputRows.length || !this.caseManualNormalizeText(choice?.toAddress);
            })
        );
        if (!rows.length || reverseRowsNeedOutputContext) {
          try {
            const tatumRows = await this.fetchCaseManualStartBtcChoicesFromTatum(candidate, direction, options);
            if (tatumRows.length) {
              // The selected-input lookup may return a complete input list but
              // omit the submitted transaction's outputs. Preserve that input
              // list and attach the shared full-transaction output context.
              const contextOutputRows = this.caseManualChoiceOutputAddressRows(tatumRows[0]);
              if (rows.length && contextOutputRows.length) {
                const firstOutputAddress = this.caseManualNormalizeText(contextOutputRows[0]?.toAddress || contextOutputRows[0]?.address || '');
                rows = rows.map(choice => ({
                  ...choice,
                  outputAddresses: contextOutputRows,
                  toAddresses: contextOutputRows,
                  outputAddressCount: contextOutputRows.length,
                  hiddenOutputAddressCount: Math.max(0, contextOutputRows.length - CASE_MANUAL_START_BTC_VISUAL_OUTPUT_ADDRESS_LIMIT),
                  toAddress: firstOutputAddress
                }));
                sourceLabel = 'Tool 88 + Tatum';
              } else {
                rows = tatumRows;
                sourceLabel = 'Tatum';
              }
            }
          } catch (error) {
            if (reverseRowsNeedOutputContext) {
              const detail = this.caseManualNormalizeText(error?.message || error) || 'unknown provider error';
              throw new Error(`Bitcoin inputs were found, but the transaction outputs needed for Reverse context could not be loaded. ${detail}`);
            }
            if (!rows.length) throw error;
          }
        }
      } else {
        result = await this.lookupCaseManualStartTransactionBundle(candidate, options);
        rows = this.buildCaseManualStartOutputChoicesFromTool88Result(result, candidate, direction);
      }
      return { rows, sourceLabel, result, family, direction };
    },

    async loadCaseManualStartOutputChoices() {
      const candidate = this.caseManualStartOutputChoiceCandidate || this.caseManualStartOutputChoiceActiveCandidate;
      if (!candidate?.hash) {
        this.caseManualStartOutputChoiceStatus = 'No transaction is selected for output/input selection.';
        return;
      }
      if (!this.caseManualStartCandidateNeedsOutputChoice(candidate)) {
        this.caseManualStartOutputChoiceRows = [];
        this.caseManualStartOutputChoiceSelectedKey = '';
        this.caseManualStartOutputChoiceStatus = 'This chooser is only available for an initial Manual Solve transaction whose BTC output/input is ambiguous or whose starting address side is missing. Use Solve hop for later unresolved endings.';
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartOutputChoiceStatus);
        return;
      }
      const direction = this.caseManualStartOutputChoiceDirectionForCandidate(candidate);
      const family = this.caseManualInferNetworkFamily(candidate);
      this.caseManualStartOutputChoiceLoading = true;
      this.caseManualStartOutputChoiceStatus = family === 'btc'
        ? 'Looking up BTC inputs/outputs for this transaction…'
        : 'Looking up transfer candidates for this transaction…';
      try {
        const { rows, sourceLabel } = await this.fetchCaseManualStartOutputChoiceRows(candidate, direction);
        this.caseManualStartOutputChoiceRows = rows;
        if (rows.length === 1 && family !== 'btc') {
          this.caseManualStartOutputChoiceSelectedKey = rows[0].key;
          this.caseManualStartOutputChoiceStatus = `One clear transfer choice was found from ${sourceLabel}; applying it automatically.`;
          await this.applyCaseManualStartOutputChoice();
          return;
        }
        this.caseManualStartOutputChoiceSelectedKey = rows.length === 1 ? rows[0].key : '';
        this.caseManualStartOutputChoiceStatus = rows.length
          ? `${rows.length} ${family === 'btc' ? (direction === 'reverse' ? 'BTC input' : 'BTC output') : 'transfer'} choice${rows.length === 1 ? '' : 's'} found from ${sourceLabel}. Select the one WayFinder would follow.`
          : 'No selectable output/input choices were returned. Open full Tool 88 or supply the missing address manually.';
      } catch (error) {
        this.caseManualStartOutputChoiceRows = [];
        this.caseManualStartOutputChoiceSelectedKey = '';
        this.caseManualStartOutputChoiceStatus = `Could not load output choices: ${error?.message || 'unknown provider error'}.`;
      } finally {
        this.caseManualStartOutputChoiceLoading = false;
      }
    },

    buildCaseManualStartSelectedOutputRow(candidate = {}, choice = {}) {
      const sourceRow = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const row = this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(this.caseManualEnsureRowWidth(sourceRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceRow.length || 0))));
      const direction = choice.direction || this.caseManualStartOutputChoiceDirectionForCandidate(candidate);
      const token = this.caseManualNormalizeText(choice.token || candidate.token || row[CASE_MANUAL_START_COL.TOKEN] || (choice.family === 'btc' ? 'BTC' : '')).toUpperCase();
      const amountText = this.caseManualNormalizeText(choice.amountText || (choice.family === 'btc' ? '' : (candidate.amount || row[CASE_MANUAL_START_COL.AMOUNT])));
      const reverseBtcChoice = choice.family === 'btc' && direction === 'reverse';
      if (reverseBtcChoice) {
        const selectedInputKey = this.caseManualNormalizeAddressKey(choice.fromAddress || choice.address || '');
        const existingToKey = this.caseManualNormalizeAddressKey(row[CASE_MANUAL_START_COL.TO_ADDRESS]);
        if (selectedInputKey && existingToKey === selectedInputKey) {
          row[CASE_MANUAL_START_COL.TO_ADDRESS] = '';
        }
        const priorNotes = [row[CASE_MANUAL_START_COL.SYSTEM_NOTES], row[CASE_MANUAL_START_COL.CASE_NOTES]]
          .map(value => this.caseManualNormalizeText(value))
          .filter(Boolean)
          .join('\n');
        const hadInvalidReverseSwapReview = /Reverse\s+swap\s+solver\s+could\s+not\s+determine|Manual\s+swap\s+review\s+required|manual-swap-review/iu.test(priorNotes);
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualStripInvalidBtcReverseSwapReviewText(
          this.caseManualStripForwardDormantEndingText(row[CASE_MANUAL_START_COL.SYSTEM_NOTES]),
          { removeGenericManualWork: hadInvalidReverseSwapReview }
        );
        row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualStripInvalidBtcReverseSwapReviewText(
          this.caseManualStripForwardDormantEndingText(row[CASE_MANUAL_START_COL.CASE_NOTES]),
          { removeGenericManualWork: false }
        );
        row[CASE_MANUAL_START_COL.PRIOR_BALANCE] = '';
        row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = '0.00000000';
        row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = '0.00000000';
      }
      if (choice.family === 'btc' && direction !== 'reverse') {
        row[CASE_MANUAL_START_COL.TO_ADDRESS] = '';
        row[CASE_MANUAL_START_COL.AMOUNT] = '';
        row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = '';
        row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = '';
        row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = '';
      }
      if (choice.fromAddress && (direction === 'reverse' || !this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_ADDRESS]))) row[CASE_MANUAL_START_COL.FROM_ADDRESS] = choice.fromAddress;
      const btcOutputRows = reverseBtcChoice ? this.caseManualChoiceOutputAddressRows(choice) : [];
      const firstBtcOutputAddress = this.caseManualNormalizeText(btcOutputRows[0]?.toAddress || btcOutputRows[0]?.address || choice.toAddress || '');
      if (firstBtcOutputAddress && (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TO_ADDRESS]) || reverseBtcChoice) && this.caseManualNormalizeAddressKey(firstBtcOutputAddress) !== this.caseManualNormalizeAddressKey(choice.fromAddress)) {
        row[CASE_MANUAL_START_COL.TO_ADDRESS] = firstBtcOutputAddress;
      } else if (choice.toAddress && (direction !== 'reverse' || (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TO_ADDRESS]) && this.caseManualNormalizeAddressKey(choice.toAddress) !== this.caseManualNormalizeAddressKey(choice.fromAddress)))) row[CASE_MANUAL_START_COL.TO_ADDRESS] = choice.toAddress;
      if (amountText) row[CASE_MANUAL_START_COL.AMOUNT] = amountText;
      if (token) row[CASE_MANUAL_START_COL.TOKEN] = token;
      if (choice.family === 'btc' && amountText) {
        row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = `${amountText}${token ? ` ${token}` : ''}`.trim();
      } else if (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]) && amountText) row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = `${amountText}${token ? ` ${token}` : ''}`.trim();
      if (choice.family === 'btc' && direction !== 'reverse') {
        row[CASE_MANUAL_START_COL.PRIOR_BALANCE] = '0';
      } else if (!reverseBtcChoice) {
        row[CASE_MANUAL_START_COL.PRIOR_BALANCE] = this.caseManualPickAccountPriorBalanceText(row, choice || {}, {
          amount: amountText,
          fundsFollowed: row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]
        });
      }
      if (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC])) row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = reverseBtcChoice ? '0.00000000' : '0.00';
      if (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY])) row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = reverseBtcChoice ? '0.00000000' : '0.00';
      if (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.CONTINUES_AS])) row[CASE_MANUAL_START_COL.CONTINUES_AS] = 'Path ends.';
      row[CASE_MANUAL_START_COL.CHAIN_ID] = row[CASE_MANUAL_START_COL.CHAIN_ID] || candidate.chainId || (choice.family === 'btc' ? 'btc' : '');
      row[CASE_MANUAL_START_COL.CHAIN_NAME] = row[CASE_MANUAL_START_COL.CHAIN_NAME] || candidate.chainName || (choice.family === 'btc' ? 'Bitcoin' : '');
      row[CASE_MANUAL_START_COL.HOP_LOGIC] = row[CASE_MANUAL_START_COL.HOP_LOGIC] || candidate.hopLogic || 'FIFO';
      row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.composeCaseBuddySystemNotes(row[CASE_MANUAL_START_COL.SYSTEM_NOTES], {
        row,
        rowIndex: Number.isInteger(candidate?.rowIndex) ? candidate.rowIndex : -1,
        rows: this.getCaseManualStartMainMatrix?.() || [],
        fromAddress: row[CASE_MANUAL_START_COL.FROM_ADDRESS],
        toAddress: row[CASE_MANUAL_START_COL.TO_ADDRESS],
        attribution: {},
        addTransferNarrative: true,
        noteColumn: 'system-notes'
      });
      const directionNote = `analysis direction=${direction}`;
      const note = choice.family === 'btc'
        ? `Case Buddy manual choice: ${directionNote}; selected ${choice.title || 'BTC output'} (${amountText}${token ? ` ${token}` : ''}) for ${direction === 'reverse' ? 'From Address' : 'To Address'} resolution.`
        : `Case Buddy manual choice: ${directionNote}; selected transfer candidate for ${direction === 'reverse' ? 'source' : 'target'} resolution.`;
      row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(row[CASE_MANUAL_START_COL.CASE_NOTES], note);
      if (reverseBtcChoice) {
        const rawChoice = choice.raw && typeof choice.raw === 'object' ? choice.raw : {};
        const inputIndex = Number.isFinite(Number(choice.inputIndex ?? choice.order)) ? Math.trunc(Number(choice.inputIndex ?? choice.order)) : null;
        const previousTransactionHash = this.caseManualNormalizeText(choice.previousTransactionHash || this.caseManualBtcChoicePreviousTransactionHash(rawChoice));
        const explicitPreviousOutputIndex = choice.previousOutputIndex;
        const previousOutputIndex = explicitPreviousOutputIndex !== null
          && explicitPreviousOutputIndex !== undefined
          && this.caseManualNormalizeText(explicitPreviousOutputIndex) !== ''
          && Number.isFinite(Number(explicitPreviousOutputIndex))
          ? Math.trunc(Number(explicitPreviousOutputIndex))
          : this.caseManualBtcChoicePreviousOutputIndex(rawChoice);
        const valueSatoshis = Number.isFinite(Number(choice.valueSatoshis)) && Number(choice.valueSatoshis) > 0
          ? Math.trunc(Number(choice.valueSatoshis))
          : this.caseManualBtcChoiceValueSatoshis(rawChoice, amountText);
        const outputRows = this.caseManualChoiceOutputAddressRows(choice);
        const markerParts = [
          'Tool 88 BTC reverse input selection: direction=reverse',
          inputIndex !== null ? `input_index=${inputIndex}` : '',
          choice.fromAddress ? `input_address=${choice.fromAddress}` : '',
          valueSatoshis ? `input_value_satoshis=${valueSatoshis}` : '',
          previousTransactionHash ? `prev_txid=${previousTransactionHash}` : '',
          previousOutputIndex !== null ? `prev_vout=${previousOutputIndex}` : '',
          outputRows.length ? `transaction_output_count=${outputRows.length}` : ''
        ].filter(Boolean).join('; ');
        row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(row[CASE_MANUAL_START_COL.CASE_NOTES], markerParts);
        if (outputRows.length) {
          row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
            row[CASE_MANUAL_START_COL.CASE_NOTES],
            `Case Buddy BTC reverse context: preserved ${outputRows.length} transaction output address${outputRows.length === 1 ? '' : 'es'} in Column D. Reverse analysis follows the selected input; no output selection is required.`
          );
        }
      }
      return row;
    },

    buildCaseManualStartSelectedOutputRows(candidate = {}, choice = {}) {
      const baseRow = this.buildCaseManualStartSelectedOutputRow(candidate, choice);
      const direction = choice.direction || this.caseManualStartOutputChoiceDirectionForCandidate(candidate);
      if (choice.family !== 'btc') return [baseRow];
      if (direction === 'reverse') {
        const outputRows = this.caseManualChoiceOutputAddressRows(choice);
        if (!outputRows.length) return [baseRow];
        const rows = [];
        outputRows.forEach((output, index) => {
          const outputAddress = this.caseManualNormalizeText(output.toAddress || output.address || '');
          if (!outputAddress) return;
          if (index === 0) {
            const firstRow = baseRow.slice();
            firstRow[CASE_MANUAL_START_COL.TO_ADDRESS] = outputAddress;
            rows.push(firstRow);
            return;
          }
          const continuation = this.caseManualEnsureRowWidth([], CASE_MANUAL_START_COLUMN_COUNT);
          // Reverse BTC follows the investigator-selected input. Transaction
          // outputs are preserved as address-only Column D context rows and do
          // not require the investigator to choose one output.
          continuation[CASE_MANUAL_START_COL.TO_ADDRESS] = outputAddress;
          rows.push(continuation);
        });
        return rows.length ? rows : [baseRow];
      }
      const inputRows = this.caseManualChoiceInputAddressRows(choice);
      if (!inputRows.length) return [baseRow];
      const rows = [];
      inputRows.forEach((input, index) => {
        const inputAddress = this.caseManualNormalizeText(input.fromAddress || input.address || '');
        if (!inputAddress) return;
        if (index === 0) {
          const firstRow = baseRow.slice();
          firstRow[CASE_MANUAL_START_COL.FROM_ADDRESS] = inputAddress;
          firstRow[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
            firstRow[CASE_MANUAL_START_COL.CASE_NOTES],
            `Case Buddy manual choice: preserved ${inputRows.length} BTC input From address${inputRows.length === 1 ? '' : 'es'} for this selected output.`
          );
          rows.push(firstRow);
          return;
        }
        const continuation = this.caseManualEnsureRowWidth([], CASE_MANUAL_START_COLUMN_COUNT);
        // BTC multi-input continuation rows are address-only rows inside the
        // selected output entry. They should mirror WayFinder: no copied To
        // Address, no input amount, no date/token, no left-bounds, and no
        // Tool88 note. The selected output story lives only on the hash row.
        continuation[CASE_MANUAL_START_COL.FROM_ADDRESS] = inputAddress;
        rows.push(continuation);
      });
      return rows.length ? rows : [baseRow];
    },

    caseManualSpliceEntryBlockRows(matrix = [], startIndex = -1, replacementRows = []) {
      const rows = Array.isArray(matrix) ? matrix.slice() : [];
      const start = Number(startIndex);
      if (!Number.isInteger(start) || start < 0 || start >= rows.length) return { rows, changed: false, insertedCount: 0, replacedCount: 0 };
      let deleteCount = 1;
      for (let cursor = start + 1; cursor < rows.length; cursor += 1) {
        const row = rows[cursor] || [];
        if (this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH])) break;
        if (!row.some(value => this.caseManualNormalizeText(value))) break;
        deleteCount += 1;
      }
      rows.splice(start, deleteCount, ...replacementRows);
      return { rows, changed: true, insertedCount: replacementRows.length, replacedCount: deleteCount };
    },

    async applyCaseManualStartSelectedOutputRowsToMain(candidate = {}, selectedRows = []) {
      const sourceRows = this.caseManualCleanBtcInputContinuationRowsInBlock(Array.isArray(selectedRows) ? selectedRows : []);
      const preparedRows = sourceRows
        .map(row => this.caseManualCanonicalizeCaseSheetRow(row))
        .filter((row, index) => index === 0 ? this.caseManualRowHasEntryData(row) : row.some(value => this.caseManualNormalizeText(value)));
      if (!preparedRows.length || !Number.isInteger(candidate?.rowIndex)) return { changed: false, updatedCount: 0 };
      const matrix = this.getCaseManualStartMainMatrix().map(row => this.caseManualMigrateLegacyColumnPNotes(this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))));
      const result = this.caseManualSpliceEntryBlockRows(matrix, candidate.rowIndex, preparedRows);
      if (!result.changed) return { changed: false, updatedCount: 0 };
      await this.replaceCaseManualStartLogicalSheetMatrix('main', result.rows, `Updated Main Data from Manual Solve selected output/input choice (${preparedRows.length} row${preparedRows.length === 1 ? '' : 's'}).`);
      return { changed: true, updatedCount: preparedRows.length, replacedCount: result.replacedCount };
    },

    async appendCaseManualStartSelectedOutputRowsToRaw(selectedRows = []) {
      const sourceRows = this.caseManualCleanBtcInputContinuationRowsInBlock(Array.isArray(selectedRows) ? selectedRows : []);
      const normalizedRows = sourceRows
        .map(row => this.caseManualCanonicalizeCaseSheetRow(this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))))))
        .filter((row, index) => index === 0 ? this.caseManualRowHasEntryData(row) : row.some(value => this.caseManualNormalizeText(value)));
      if (!normalizedRows.length) return { changed: false, appendedCount: 0 };
      const existing = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('raw') : [];
      const rawRows = (Array.isArray(existing) ? existing : []).map(item => this.caseManualEnsureRowWidth(item, Math.max(CASE_MANUAL_START_COLUMN_COUNT, item?.length || 0)));
      const hasHeader = rawRows.length && this.caseManualIsHeaderRow(rawRows[0]);
      let rows = hasHeader ? rawRows : [CASE_MANUAL_START_CANONICAL_HEADERS.slice(), ...rawRows];
      const hash = this.caseManualNormalizeText(normalizedRows[0][CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const existingIndex = rows.findIndex((existingRow, index) => index > 0 && this.caseManualNormalizeText(existingRow[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase() === hash);
      if (existingIndex >= 0) {
        const result = this.caseManualSpliceEntryBlockRows(rows, existingIndex, normalizedRows);
        rows = result.rows;
      } else {
        rows.push(...normalizedRows);
      }
      await this.replaceCaseManualStartLogicalSheetMatrix('raw', rows, 'Updated Raw Data from Manual Solve selected output/input choice.');
      return { changed: true, appendedCount: existingIndex >= 0 ? 0 : normalizedRows.length };
    },

    async appendCaseManualStartSelectedOutputRowToRaw(row = []) {
      return this.appendCaseManualStartSelectedOutputRowsToRaw([row]);
    },

    async applyCaseManualStartOutputChoice(options = {}) {
      const candidate = this.caseManualStartOutputChoiceCandidate || this.caseManualStartOutputChoiceActiveCandidate;
      const choice = this.caseManualStartOutputChoiceSelectedChoice;
      const automationCalloutContext = this.caseManualStartAutomationCalloutContext?.enabled && this.caseManualStartViewportAutomationCalloutsAreEnabled?.()
        ? { ...(this.caseManualStartAutomationCalloutContext || {}) }
        : null;
      if (!candidate?.hash || !choice) {
        this.caseManualStartOutputChoiceStatus = 'Select an output/input choice before applying.';
        return false;
      }
      if (!this.caseManualStartCandidateNeedsOutputChoice(candidate)) {
        this.caseManualStartOutputChoiceStatus = 'This selected transaction no longer needs the initial output/input chooser. Use Solve hop for downstream unresolved endings.';
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartOutputChoiceStatus);
        return false;
      }
      this.caseManualStartOutputChoiceLoading = true;
      try {
        const selectedRows = this.buildCaseManualStartSelectedOutputRows(candidate, choice);
        const automationCalloutBeforeState = automationCalloutContext
          ? this.captureCaseManualStartViewportAutomationCalloutBeforeState({ integrationAction: 'manual-output-choice' }, 'manual-output-choice')
          : null;
        const row = selectedRows[0];
        const inputAddressRows = this.caseManualChoiceInputAddressRows(choice);
        const outputAddressRows = this.caseManualChoiceOutputAddressRows(choice);
        const key = this.buildCaseManualStartHydrationKey(candidate.hash, candidate.rowIndex);
        const hydrated = {
          ...(this.caseManualStartVisualizerHydrationByKey?.[key] || {}),
          status: 'hydrated',
          message: `Manual Solve selected ${choice.title || 'output/input'} from Tool 88 provider details.`,
          sourceLabel: choice.family === 'btc' ? 'Tool 88 BTC input/output chooser' : 'Tool 88 transfer chooser',
          txHash: candidate.hash,
          transactionHash: candidate.hash,
          fromAddress: row[CASE_MANUAL_START_COL.FROM_ADDRESS],
          toAddress: row[CASE_MANUAL_START_COL.TO_ADDRESS],
          amountText: row[CASE_MANUAL_START_COL.AMOUNT],
          tokenSymbol: row[CASE_MANUAL_START_COL.TOKEN],
          fundsFollowed: row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED],
          priorBalance: row[CASE_MANUAL_START_COL.PRIOR_BALANCE],
          leftBoundsDisplay: row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY],
          leftBoundsNumeric: row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC],
          chainId: row[CASE_MANUAL_START_COL.CHAIN_ID],
          chainName: row[CASE_MANUAL_START_COL.CHAIN_NAME],
          systemNotes: row[CASE_MANUAL_START_COL.SYSTEM_NOTES],
          btcInputAddresses: inputAddressRows,
          btcInputAddressCount: inputAddressRows.length,
          btcHiddenInputAddressCount: Math.max(0, inputAddressRows.length - CASE_MANUAL_START_BTC_VISUAL_INPUT_ADDRESS_LIMIT),
          btcOutputAddresses: outputAddressRows,
          btcOutputAddressCount: outputAddressRows.length,
          btcHiddenOutputAddressCount: Math.max(0, outputAddressRows.length - CASE_MANUAL_START_BTC_VISUAL_OUTPUT_ADDRESS_LIMIT)
        };
        this.caseManualStartVisualizerHydrationByKey = {
          ...(this.caseManualStartVisualizerHydrationByKey || {}),
          [key]: hydrated
        };
        await this.applyCaseManualStartSelectedOutputRowsToMain(candidate, selectedRows);
        await this.appendCaseManualStartSelectedOutputRowsToRaw(selectedRows);
        if (options.skipVisualRefresh !== true) {
          await this.refreshCaseInvestigationVisualizer({ preserveFlow: true });
        }
        if (automationCalloutContext) {
          this.queueCaseManualStartViewportAutomationCallouts({
            context: automationCalloutContext,
            beforeState: automationCalloutBeforeState,
            rows: selectedRows,
            snapshot: {
              integrationAction: 'manual-output-choice',
              message: `Manual Solve selected ${choice.title || 'output/input'} from Tool 88 provider details.`
            },
            action: 'manual-output-choice',
            candidate,
            choice
          });
        }
        const btcContextNoun = choice.family === 'btc' && choice.direction === 'reverse' ? 'output context row' : 'BTC input row';
        this.caseManualStartOutputChoiceStatus = `Applied ${choice.title || 'selected output'} to Main/Raw${selectedRows.length > 1 ? ` with ${selectedRows.length - 1} ${btcContextNoun}${selectedRows.length === 2 ? '' : 's'}` : ''}${options.skipVisualRefresh === true ? '.' : ' and refreshed the manual visual.'}`;
        if (options.silent !== true && typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartOutputChoiceStatus);
        this.resetCaseManualStartHopInspectorDecisionStateForCandidate?.(null, { force: true });
        this.caseManualStartOutputChoiceOpen = false;
        this.caseManualStartOutputChoiceCandidate = null;
        return true;
      } catch (error) {
        this.caseManualStartOutputChoiceStatus = `Selected output could not be applied: ${error?.message || 'unknown error'}.`;
        if (options.rethrow === true) throw error;
        return false;
      } finally {
        this.caseManualStartOutputChoiceLoading = false;
      }
    },

    caseManualStartOpeningCandidateSignature(candidate = {}) {
      const row = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const hasSpreadsheetRow = row.length > 0;
      const cell = (index, fallback = '') => hasSpreadsheetRow ? row[index] : fallback;
      // Sign only the spreadsheet-backed input, not fields supplied by an older
      // in-memory hydration record. That lets a failed write-back be reused on
      // reopen while any real row edit still invalidates the startup result.
      const values = [
        this.currentProjectFileId || this.selectedProjectFileId || 'unsaved-case',
        Number(candidate.rowIndex) || 0,
        cell(CASE_MANUAL_START_COL.TX_HASH, candidate.hash || candidate.transactionHash || candidate.txHash),
        cell(CASE_MANUAL_START_COL.PATH_LABEL, candidate.pathLabel),
        cell(CASE_MANUAL_START_COL.FROM_ADDRESS, candidate.fromAddress),
        cell(CASE_MANUAL_START_COL.TO_ADDRESS, candidate.toAddress),
        cell(CASE_MANUAL_START_COL.DATE, candidate.utcDate),
        cell(CASE_MANUAL_START_COL.AMOUNT, candidate.amount),
        cell(CASE_MANUAL_START_COL.TOKEN, candidate.token),
        cell(CASE_MANUAL_START_COL.FUNDS_FOLLOWED, candidate.fundsFollowed),
        cell(CASE_MANUAL_START_COL.PRIOR_BALANCE, candidate.priorBalance),
        cell(CASE_MANUAL_START_COL.FROM_HASH, candidate.fromHash),
        cell(CASE_MANUAL_START_COL.CONTINUES_AS, candidate.continuesAs),
        cell(CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY, candidate.leftBoundsDisplay),
        cell(CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC, candidate.leftBoundsNumeric),
        cell(CASE_MANUAL_START_COL.CHAIN_ID, candidate.chainId),
        cell(CASE_MANUAL_START_COL.CHAIN_NAME, candidate.chainName),
        cell(CASE_MANUAL_START_COL.HOP_LOGIC, candidate.hopLogic)
      ];
      return values.map(value => this.caseManualNormalizeText(value).toLowerCase()).join('␟');
    },

    caseManualStartOpeningHydrationRecordReusable(candidate = {}) {
      const key = this.buildCaseManualStartHydrationKey(candidate.hash, candidate.rowIndex);
      const existing = this.caseManualStartVisualizerHydrationByKey?.[key];
      if (!existing || existing.startupChecked !== true) return false;
      if (existing.startupInputSignature !== this.caseManualStartOpeningCandidateSignature(candidate)) return false;
      if (existing.status === 'failed') {
        const checkedAt = Number(existing.startupCheckedAtMs) || 0;
        return checkedAt > 0 && (Date.now() - checkedAt) < CASE_MANUAL_START_FAILED_REUSE_MS;
      }
      return ['hydrated', 'ambiguous', 'attribution_enriched', 'not_requested'].includes(existing.status);
    },

    caseManualStartOpeningRootRows(rows = []) {
      return (Array.isArray(rows) ? rows : []).filter(candidate => this.caseManualStartCandidateIsInitialFlowTransaction(candidate));
    },

    caseManualStartOpeningCandidateNeedsRemoteWork(candidate = {}) {
      if (!candidate?.caseManualStartCandidate || !this.caseManualStartCandidateIsInitialFlowTransaction(candidate)) return false;
      const row = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const hasSpreadsheetRow = row.length > 0;
      const cell = (index, fallback = '') => this.caseManualNormalizeText(hasSpreadsheetRow ? row[index] : fallback);
      const rawHash = cell(CASE_MANUAL_START_COL.TX_HASH, candidate.hash || candidate.transactionHash || candidate.txHash);
      const rawFundsFollowed = cell(CASE_MANUAL_START_COL.FUNDS_FOLLOWED, candidate.fundsFollowed);
      const rawCandidate = {
        ...candidate,
        hash: rawHash,
        transactionHash: rawHash,
        txHash: rawHash,
        pathLabel: cell(CASE_MANUAL_START_COL.PATH_LABEL, candidate.pathLabel),
        fromAddress: cell(CASE_MANUAL_START_COL.FROM_ADDRESS, candidate.fromAddress),
        toAddress: cell(CASE_MANUAL_START_COL.TO_ADDRESS, candidate.toAddress),
        utcDate: cell(CASE_MANUAL_START_COL.DATE, candidate.utcDate),
        amount: cell(CASE_MANUAL_START_COL.AMOUNT, candidate.amount)
          || this.caseManualAmountFromFundsFollowed(rawFundsFollowed),
        token: cell(CASE_MANUAL_START_COL.TOKEN, candidate.token)
          || this.caseManualTokenFromFundsFollowed(rawFundsFollowed),
        fundsFollowed: rawFundsFollowed,
        fromHash: cell(CASE_MANUAL_START_COL.FROM_HASH, candidate.fromHash),
        chainId: cell(CASE_MANUAL_START_COL.CHAIN_ID, candidate.chainId),
        chainName: cell(CASE_MANUAL_START_COL.CHAIN_NAME, candidate.chainName),
        hopLogic: cell(CASE_MANUAL_START_COL.HOP_LOGIC, candidate.hopLogic),
        systemNotes: cell(CASE_MANUAL_START_COL.SYSTEM_NOTES, candidate.systemNotes),
        caseNotes: cell(CASE_MANUAL_START_COL.CASE_NOTES, candidate.caseNotes),
        sourceLabel: '',
        hydrationStatus: 'not_requested',
        hydrationMessage: '',
        btcOutputChoiceConfirmed: false,
        outputChoiceConfirmed: false,
        btcOutputChoiceApplied: false,
        needsBtcOutputChoice: false,
        btcOutputChoiceRequired: false,
        btcOutputChoiceCount: 0,
        btcOutputCount: 0,
        outputChoiceCount: 0,
        outputCount: 0,
        btcInputChoiceCount: 0,
        inputChoiceCount: 0,
        rawRow: row,
        row
      };
      const family = this.caseManualInferNetworkFamily(rawCandidate);
      if (family === 'btc') return this.caseManualStartCandidateNeedsOutputChoice(rawCandidate);
      const missingProviderField = !rawCandidate.fromAddress || !rawCandidate.toAddress || !rawCandidate.amount || !rawCandidate.token;
      return Boolean(missingProviderField && this.caseManualCanHydrateTransactionBundle(rawCandidate, family));
    },

    caseManualStartRequestWasAborted(error, signal = null) {
      const openingStopped = Boolean(this.caseManualStartVisualizerOpening?.active && this.caseManualStartVisualizerOpening.stopRequested);
      return Boolean(
        openingStopped
          || signal?.aborted
          || error?.name === 'AbortError'
          || error?.code === 'ABORT_ERR'
          || error?.code === 'CASE_MANUAL_START_OPENING_STOPPED'
      );
    },

    caseManualStartOpeningAssertNotStopped(signal = null) {
      const openingStopped = Boolean(this.caseManualStartVisualizerOpening?.active && this.caseManualStartVisualizerOpening.stopRequested);
      if (!signal?.aborted && !openingStopped) return true;
      const error = new Error('Manual Solve opening was stopped.');
      error.name = 'AbortError';
      error.code = 'CASE_MANUAL_START_OPENING_STOPPED';
      throw error;
    },

    async hydrateCaseManualStartRows(rows = [], options = {}) {
      const hydratedByKey = { ...(this.caseManualStartVisualizerHydrationByKey || {}) };
      const warnings = [];
      let inferredCount = 0;
      let ambiguousCount = 0;
      let attemptedCount = 0;
      let reusedCount = 0;
      let attributionLookupCount = 0;
      let attributionHitCount = 0;
      const initialOnly = options.initialOnly === true;
      const lookupAttributions = options.lookupAttributions !== false;
      const providerFieldsOnly = options.providerFieldsOnly === true;
      const remoteWorkOnly = options.remoteWorkOnly === true;
      const skipReusable = options.skipReusable === true;
      const probeInitialBtcChoices = options.probeInitialBtcChoices !== false;
      const signal = options.signal || null;
      const onProgress = typeof options.onProgress === 'function' ? options.onProgress : null;
      let targetRows = initialOnly ? this.caseManualStartOpeningRootRows(rows) : (Array.isArray(rows) ? rows : []);
      if (remoteWorkOnly) {
        targetRows = targetRows.filter(candidate => this.caseManualStartOpeningCandidateNeedsRemoteWork(candidate));
      }
      const maxRows = Math.max(1, Math.min(50, Number(options.maxRows) || Number(this.caseManualStartVisualizerMaxHydrateRows) || 25));
      const maxAttributionLookups = Math.max(1, Math.min(200, Number(options.maxAttributionLookups) || Number(this.caseManualStartVisualizerMaxAttributionLookups) || 16));
      const delayMs = Math.max(0, Number(options.attributionDelayMs) || Number(this.caseManualStartVisualizerAttributionDelayMs) || 650);

      for (let targetIndex = 0; targetIndex < targetRows.length; targetIndex += 1) {
        this.caseManualStartOpeningAssertNotStopped(signal);
        const candidate = targetRows[targetIndex];
        const key = this.buildCaseManualStartHydrationKey(candidate.hash, candidate.rowIndex);
        const existing = hydratedByKey[key] || {};
        const startupInputSignature = this.caseManualStartOpeningCandidateSignature(candidate);
        const missingProviderField = !candidate.fromAddress || !candidate.toAddress || !candidate.amount || !candidate.token;
        const needsHydration = providerFieldsOnly
          ? missingProviderField
          : (missingProviderField || !candidate.fundsFollowed || !candidate.leftBoundsDisplay);
        const family = this.caseManualInferNetworkFamily(candidate);
        const supportsBundleHydration = this.caseManualCanHydrateTransactionBundle(candidate, family);
        let selected = null;
        let providerHydrated = false;
        let remoteAttempted = false;

        onProgress?.({
          phase: 'inspect',
          candidate,
          index: targetIndex + 1,
          total: targetRows.length,
          attemptedCount,
          inferredCount,
          reusedCount,
          message: `Reviewing graph ${targetIndex + 1} of ${targetRows.length}.`
        });

        if (skipReusable && this.caseManualStartOpeningHydrationRecordReusable(candidate)) {
          reusedCount += 1;
          onProgress?.({
            phase: 'reused',
            candidate,
            index: targetIndex + 1,
            total: targetRows.length,
            attemptedCount,
            inferredCount,
            reusedCount,
            message: `Reused the previous startup result for ${this.caseManualShorten(candidate.hash, 12, 10)}.`
          });
          continue;
        }

        if (needsHydration && supportsBundleHydration && attemptedCount < maxRows) {
          attemptedCount += 1;
          remoteAttempted = true;
          this.caseManualStartVisualizerStatus = `Filling missing starting details for ${this.caseManualShorten(candidate.hash, 12, 10)}…`;
          onProgress?.({
            phase: 'lookup', candidate, index: targetIndex + 1, total: targetRows.length,
            attemptedCount, inferredCount, reusedCount, message: this.caseManualStartVisualizerStatus
          });
          try {
            const bundle = await this.lookupCaseManualStartTransactionBundle(candidate, { signal });
            this.caseManualStartOpeningAssertNotStopped(signal);
            const choices = this.extractCaseManualBundleCandidates(bundle, candidate);
            selected = this.chooseCaseManualBundleCandidate(choices, candidate);
            if (!selected) {
              ambiguousCount += 1;
              const message = choices.length > 1
                ? `${choices.length} possible transfer rows were found for ${this.caseManualShorten(candidate.hash, 12, 10)}; select a target address or solve in Tool 88 for review.`
                : `No clear transfer row was found for ${this.caseManualShorten(candidate.hash, 12, 10)}.`;
              hydratedByKey[key] = {
                ...existing,
                status: 'ambiguous',
                message,
                startupChecked: true,
                startupCheckedAtMs: Date.now(),
                startupInputSignature
              };
              warnings.push(message);
              onProgress?.({ phase: 'ambiguous', candidate, index: targetIndex + 1, total: targetRows.length, attemptedCount, inferredCount, reusedCount, message });
              continue;
            }
            providerHydrated = true;
            inferredCount += 1;
          } catch (error) {
            if (this.caseManualStartRequestWasAborted(error, signal)) throw error;
            const message = `Could not fill startup details for ${this.caseManualShorten(candidate.hash, 12, 10)}: ${error?.message || 'unknown provider error'}.`;
            hydratedByKey[key] = {
              ...existing,
              status: 'failed',
              message,
              startupChecked: true,
              startupCheckedAtMs: Date.now(),
              startupInputSignature
            };
            warnings.push(message);
            onProgress?.({ phase: 'failed', candidate, index: targetIndex + 1, total: targetRows.length, attemptedCount, inferredCount, reusedCount, message });
            continue;
          }
        }

        const shouldProbeInitialBtcChoices = probeInitialBtcChoices && family === 'btc'
          && this.caseManualStartCandidateNeedsOutputChoice(candidate)
          && !this.caseManualStartBtcChoiceWasApplied(candidate)
          && attemptedCount < maxRows;
        if (!selected && shouldProbeInitialBtcChoices) {
          attemptedCount += 1;
          remoteAttempted = true;
          this.caseManualStartVisualizerStatus = `Checking the missing BTC ${this.caseManualStartOutputChoiceDirectionForCandidate(candidate) === 'reverse' ? 'input' : 'output'} for ${this.caseManualShorten(candidate.hash, 12, 10)}…`;
          onProgress?.({
            phase: 'lookup', candidate, index: targetIndex + 1, total: targetRows.length,
            attemptedCount, inferredCount, reusedCount, message: this.caseManualStartVisualizerStatus
          });
          try {
            const { rows: btcChoices, sourceLabel } = await this.fetchCaseManualStartOutputChoiceRows(candidate, 'forward', { signal });
            this.caseManualStartOpeningAssertNotStopped(signal);
            const knownToAddress = this.caseManualNormalizeText(
              candidate.toAddress
              || candidate.targetAddress
              || candidate.rawRow?.[CASE_MANUAL_START_COL.TO_ADDRESS]
              || candidate.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]
              || existing.toAddress
              || existing.targetAddress
              || ''
            );
            const knownToKey = this.caseManualNormalizeAddressKey(knownToAddress);
            const choiceAddressKey = choice => this.caseManualNormalizeAddressKey(
              choice?.toAddress
              || choice?.address
              || choice?.outputAddress
              || choice?.recipient
              || choice?.to_address
              || ''
            );
            const matchedKnownOutput = knownToKey
              ? btcChoices.find(choice => choiceAddressKey(choice) === knownToKey)
              : null;
            const singleImplicitOutput = btcChoices.length === 1 && !knownToKey ? btcChoices[0] : null;
            const chosenBtcOutput = matchedKnownOutput || singleImplicitOutput || null;
            const inputAddressRows = this.caseManualChoiceInputAddressRows(chosenBtcOutput || btcChoices[0] || {});
            if (chosenBtcOutput) {
              const choice = chosenBtcOutput;
              const amountText = this.caseManualNormalizeText(choice.amountText || choice.amount || '');
              const fundsText = this.caseManualNormalizeText(existing.fundsFollowed || candidate.fundsFollowed || (amountText ? `${amountText} BTC` : ''));
              const leftDisplay = this.caseManualNormalizeText(existing.leftBoundsDisplay || candidate.leftBoundsDisplay || existing.leftBoundsNumeric || candidate.leftBoundsNumeric || '0.00');
              const leftNumeric = this.caseManualNormalizeText(existing.leftBoundsNumeric || candidate.leftBoundsNumeric || leftDisplay || '0.00000000');
              selected = {
                fromAddress: choice.fromAddress || inputAddressRows[0]?.fromAddress || inputAddressRows[0]?.address || candidate.fromAddress,
                toAddress: choice.toAddress || choice.address || choice.outputAddress || choice.recipient || knownToAddress || candidate.toAddress,
                amountText,
                tokenSymbol: choice.token || candidate.token || existing.tokenSymbol || 'BTC',
                dateText: choice.dateText || choice.utcDate || existing.dateText || candidate.utcDate,
                fundsFollowed: fundsText,
                priorBalance: existing.priorBalance || candidate.priorBalance || '0',
                leftBoundsDisplay: leftDisplay,
                leftBoundsNumeric: leftNumeric,
                chainId: existing.chainId || candidate.chainId || 'btc',
                chainName: existing.chainName || candidate.chainName || 'Bitcoin',
                sourceLabel: matchedKnownOutput ? `${sourceLabel} BTC output matched Column D` : `${sourceLabel} single BTC output`,
                btcInputAddresses: inputAddressRows,
                btcInputAddressCount: inputAddressRows.length,
                btcOutputChoiceRequired: false,
                needsBtcOutputChoice: false,
                btcOutputChoiceConfirmed: true,
                btcOutputChoiceApplied: true
              };
              providerHydrated = true;
              inferredCount += 1;
            } else if (btcChoices.length > 1) {
              if (knownToKey) {
                const amountText = this.caseManualNormalizeText(existing.amountText || candidate.amount || this.caseManualAmountFromFundsFollowed(candidate.fundsFollowed) || '');
                const fundsText = this.caseManualNormalizeText(existing.fundsFollowed || candidate.fundsFollowed || (amountText ? `${amountText} BTC` : ''));
                selected = {
                  fromAddress: existing.fromAddress || candidate.fromAddress || inputAddressRows[0]?.fromAddress || inputAddressRows[0]?.address || '',
                  toAddress: knownToAddress || candidate.toAddress,
                  amountText,
                  tokenSymbol: candidate.token || existing.tokenSymbol || 'BTC',
                  dateText: existing.dateText || candidate.utcDate,
                  fundsFollowed: fundsText,
                  priorBalance: existing.priorBalance || candidate.priorBalance || '',
                  leftBoundsDisplay: existing.leftBoundsDisplay || candidate.leftBoundsDisplay || existing.leftBoundsNumeric || candidate.leftBoundsNumeric || '',
                  leftBoundsNumeric: existing.leftBoundsNumeric || candidate.leftBoundsNumeric || '',
                  chainId: existing.chainId || candidate.chainId || 'btc',
                  chainName: existing.chainName || candidate.chainName || 'Bitcoin',
                  sourceLabel: `${sourceLabel} BTC choices; Column D preserved`,
                  btcOutputChoiceRequired: false,
                  needsBtcOutputChoice: false,
                  btcOutputChoiceConfirmed: true,
                  btcOutputChoiceApplied: true,
                  btcOutputChoiceCount: btcChoices.length,
                  btcInputChoiceCount: inputAddressRows.length,
                  btcInputAddresses: inputAddressRows,
                  btcInputAddressCount: inputAddressRows.length,
                  btcHiddenInputAddressCount: Math.max(0, inputAddressRows.length - CASE_MANUAL_START_BTC_VISUAL_INPUT_ADDRESS_LIMIT)
                };
                const message = `${btcChoices.length} BTC outputs were found for ${this.caseManualShorten(candidate.hash, 12, 10)}. Column D already supplies ${this.caseManualShorten(knownToAddress, 12, 10)}, so Manual Solve kept that selected output instead of reopening output disambiguation.`;
                warnings.push(message);
                providerHydrated = true;
                inferredCount += 1;
              } else {
                ambiguousCount += 1;
                const message = `${btcChoices.length} BTC outputs were found for ${this.caseManualShorten(candidate.hash, 12, 10)}; choose the exact output / To Address before following this starting transaction.`;
                hydratedByKey[key] = {
                  ...existing,
                  status: 'ambiguous',
                  message,
                  sourceLabel: `${sourceLabel} BTC choices`,
                  txHash: candidate.hash,
                  transactionHash: candidate.hash,
                  fromAddress: existing.fromAddress || candidate.fromAddress || inputAddressRows[0]?.fromAddress || inputAddressRows[0]?.address || '',
                  toAddress: '',
                  amountText: '',
                  tokenSymbol: candidate.token || existing.tokenSymbol || 'BTC',
                  dateText: existing.dateText || candidate.utcDate,
                  fundsFollowed: '',
                  priorBalance: '',
                  leftBoundsDisplay: '',
                  leftBoundsNumeric: '',
                  chainId: existing.chainId || candidate.chainId || 'btc',
                  chainName: existing.chainName || candidate.chainName || 'Bitcoin',
                  btcOutputChoiceRequired: true,
                  needsBtcOutputChoice: true,
                  btcOutputChoiceCount: btcChoices.length,
                  btcInputChoiceCount: inputAddressRows.length,
                  btcInputAddresses: inputAddressRows,
                  btcInputAddressCount: inputAddressRows.length,
                  btcHiddenInputAddressCount: Math.max(0, inputAddressRows.length - CASE_MANUAL_START_BTC_VISUAL_INPUT_ADDRESS_LIMIT),
                  startupChecked: true,
                  startupCheckedAtMs: Date.now(),
                  startupInputSignature
                };
                warnings.push(message);
                onProgress?.({ phase: 'ambiguous', candidate, index: targetIndex + 1, total: targetRows.length, attemptedCount, inferredCount, reusedCount, message });
                continue;
              }
            }
          } catch (error) {
            if (this.caseManualStartRequestWasAborted(error, signal)) throw error;
            const knownToAddress = this.caseManualNormalizeText(
              candidate.toAddress
              || candidate.targetAddress
              || candidate.rawRow?.[CASE_MANUAL_START_COL.TO_ADDRESS]
              || candidate.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]
              || existing.toAddress
              || existing.targetAddress
              || ''
            );
            if (this.caseManualNormalizeAddressKey(knownToAddress)) {
              const amountText = this.caseManualNormalizeText(existing.amountText || candidate.amount || this.caseManualAmountFromFundsFollowed(candidate.fundsFollowed) || '');
              selected = {
                fromAddress: existing.fromAddress || candidate.fromAddress || '',
                toAddress: knownToAddress,
                amountText,
                tokenSymbol: candidate.token || existing.tokenSymbol || 'BTC',
                dateText: existing.dateText || candidate.utcDate,
                fundsFollowed: existing.fundsFollowed || candidate.fundsFollowed || (amountText ? `${amountText} BTC` : ''),
                priorBalance: existing.priorBalance || candidate.priorBalance || '',
                leftBoundsDisplay: existing.leftBoundsDisplay || candidate.leftBoundsDisplay || '',
                leftBoundsNumeric: existing.leftBoundsNumeric || candidate.leftBoundsNumeric || '',
                chainId: existing.chainId || candidate.chainId || 'btc',
                chainName: existing.chainName || candidate.chainName || 'Bitcoin',
                sourceLabel: 'Column D BTC output preserved after output check failed',
                btcOutputChoiceRequired: false,
                needsBtcOutputChoice: false,
                btcOutputChoiceConfirmed: true,
                btcOutputChoiceApplied: true
              };
              warnings.push(`BTC output check failed for ${this.caseManualShorten(candidate.hash, 12, 10)}, but Column D already supplies ${this.caseManualShorten(knownToAddress, 12, 10)}, so Manual Solve kept that output.`);
            } else {
              const message = `BTC output check failed for ${this.caseManualShorten(candidate.hash, 12, 10)}: ${error?.message || 'unknown provider error'}.`;
              hydratedByKey[key] = {
                ...existing,
                status: 'failed',
                message,
                startupChecked: true,
                startupCheckedAtMs: Date.now(),
                startupInputSignature
              };
              warnings.push(message);
              onProgress?.({ phase: 'failed', candidate, index: targetIndex + 1, total: targetRows.length, attemptedCount, inferredCount, reusedCount, message });
              continue;
            }
          }
        }

        if (!selected) {
          const amountText = existing.amountText || candidate.amount || this.caseManualAmountFromFundsFollowed(candidate.fundsFollowed);
          const tokenSymbol = existing.tokenSymbol || candidate.token || this.caseManualTokenFromFundsFollowed(candidate.fundsFollowed);
          selected = {
            fromAddress: existing.fromAddress || candidate.fromAddress,
            toAddress: existing.toAddress || candidate.toAddress,
            amountText,
            tokenSymbol,
            dateText: existing.dateText || candidate.utcDate,
            chainId: existing.chainId || candidate.chainId,
            chainName: existing.chainName || candidate.chainName,
            sourceLabel: existing.sourceLabel || (candidate.hydrationStatus === 'hydrated' ? 'existing hydrated row details' : 'current Main Data row')
          };
        }

        const hasAddressPair = Boolean(selected.fromAddress || selected.toAddress);
        if (!hasAddressPair) {
          if (remoteAttempted) {
            hydratedByKey[key] = {
              ...existing,
              status: existing.status || 'not_requested',
              startupChecked: true,
              startupCheckedAtMs: Date.now(),
              startupInputSignature
            };
          }
          continue;
        }
        const attribution = lookupAttributions && attributionLookupCount < maxAttributionLookups
          ? await this.lookupCaseManualTransferAttributions(selected, candidate, {
              maxLookups: maxAttributionLookups - attributionLookupCount,
              delayMs
            })
          : { from: null, to: null, fromLabel: '', toLabel: '', lookupCount: 0, hitCount: 0, skippedCount: 0 };
        attributionLookupCount += attribution.lookupCount || 0;
        attributionHitCount += attribution.hitCount || 0;
        const fundsFollowed = candidate.fundsFollowed || existing.fundsFollowed || selected.fundsFollowed || `${selected.amountText || ''}${selected.tokenSymbol ? ` ${selected.tokenSymbol}` : ''}`.trim();
        const sourceLabel = selected.sourceLabel || existing.sourceLabel || 'provider details';
        const status = providerHydrated || existing.status === 'hydrated' || candidate.hydrationStatus === 'hydrated'
          ? 'hydrated'
          : (attribution.lookupCount ? 'attribution_enriched' : (existing.status || candidate.hydrationStatus || 'not_requested'));
        const fromAttribution = attribution.from || this.caseManualAttributionForAddress(selected.fromAddress);
        const toAttribution = attribution.to || this.caseManualAttributionForAddress(selected.toAddress);
        const fromAttributionLabel = attribution.fromLabel || fromAttribution?.label || '';
        const toAttributionLabel = attribution.toLabel || toAttribution?.label || '';
        const combinedAttribution = existing.attribution || candidate.attribution || this.caseManualBuildCombinedAttributionLabel({
          fromAddress: selected.fromAddress,
          toAddress: selected.toAddress,
          fromAttributionLabel,
          toAttributionLabel
        });
        const systemNotes = this.composeCaseBuddySystemNotes(existing.systemNotes || candidate.systemNotes || '', {
          row: candidate.rawRow || candidate.row || [],
          rowIndex: Number.isInteger(candidate.rowIndex) ? candidate.rowIndex : -1,
          rows: this.getCaseManualStartMainMatrix?.() || [],
          fromAddress: selected.fromAddress,
          toAddress: selected.toAddress,
          attribution,
          sourceLabel,
          addTransferNarrative: true,
          noteColumn: 'system-notes'
        });
        hydratedByKey[key] = {
          ...existing,
          status,
          message: providerHydrated
            ? `Hydrated from ${sourceLabel}.`
            : (existing.message || (lookupAttributions ? 'Attribution checked for current Main Data row.' : 'Current Main Data reused for Manual Solve startup.')),
          sourceLabel,
          txHash: selected.txHash || selected.transactionHash || existing.txHash || existing.transactionHash || candidate.hash,
          transactionHash: selected.txHash || selected.transactionHash || existing.transactionHash || existing.txHash || candidate.hash,
          fromAddress: selected.fromAddress || existing.fromAddress || candidate.fromAddress,
          toAddress: selected.toAddress || existing.toAddress || candidate.toAddress,
          amountText: selected.amountText || existing.amountText || candidate.amount,
          tokenSymbol: selected.tokenSymbol || existing.tokenSymbol || candidate.token,
          dateText: selected.dateText || existing.dateText || candidate.utcDate,
          fundsFollowed,
          priorBalance: candidate.priorBalance || existing.priorBalance || selected.priorBalance || '',
          leftBoundsDisplay: candidate.leftBoundsDisplay || existing.leftBoundsDisplay || selected.leftBoundsDisplay || '',
          leftBoundsNumeric: candidate.leftBoundsNumeric || existing.leftBoundsNumeric || selected.leftBoundsNumeric || '',
          chainId: selected.chainId || existing.chainId || candidate.chainId,
          chainName: selected.chainName || existing.chainName || candidate.chainName,
          attribution: combinedAttribution,
          fromAttribution,
          toAttribution,
          fromAttributionLabel,
          toAttributionLabel,
          systemNotes,
          btcInputAddresses: selected.btcInputAddresses || existing.btcInputAddresses || candidate.btcInputAddresses || [],
          btcInputAddressCount: selected.btcInputAddressCount || existing.btcInputAddressCount || candidate.btcInputAddressCount || 0,
          btcHiddenInputAddressCount: Math.max(0, Number(selected.btcInputAddressCount || existing.btcInputAddressCount || candidate.btcInputAddressCount || 0) - CASE_MANUAL_START_BTC_VISUAL_INPUT_ADDRESS_LIMIT),
          btcOutputChoiceRequired: false,
          needsBtcOutputChoice: false,
          startupChecked: remoteAttempted || existing.startupChecked === true,
          startupCheckedAtMs: remoteAttempted ? Date.now() : (Number(existing.startupCheckedAtMs) || 0),
          startupInputSignature: remoteAttempted ? startupInputSignature : (existing.startupInputSignature || '')
        };
        onProgress?.({
          phase: providerHydrated ? 'hydrated' : (remoteAttempted ? 'checked' : 'current'),
          candidate,
          index: targetIndex + 1,
          total: targetRows.length,
          attemptedCount,
          inferredCount,
          reusedCount,
          message: providerHydrated
            ? `Filled the missing starting details for ${this.caseManualShorten(candidate.hash, 12, 10)}.`
            : `Reused current Main Data for ${this.caseManualShorten(candidate.hash, 12, 10)}.`
        });
      }

      this.caseManualStartVisualizerHydrationByKey = hydratedByKey;
      const rebuiltRows = rows.map(row => this.buildCaseManualStartCandidate(row.rawRow || row.row || [], row.rowIndex, null) || row);
      this.caseManualStartVisualizerLastSummary = {
        attemptedCount,
        inferredCount,
        ambiguousCount,
        reusedCount,
        attributionLookupCount,
        attributionHitCount,
        warnings
      };
      return {
        rows: rebuiltRows,
        attemptedCount,
        inferredCount,
        ambiguousCount,
        reusedCount,
        attributionLookupCount,
        attributionHitCount,
        warnings
      };
    },

    caseManualClampNumber(value, min = 0, max = 1) {
      const number = Number(value);
      if (!Number.isFinite(number)) return min;
      return Math.max(min, Math.min(max, number));
    },

    buildCaseManualStartFundsFollowedBar(candidate = {}) {
      const amountNumber = this.caseManualParseNumericAmount(candidate.amount);
      const followedNumber = this.caseManualParseNumericAmount(candidate.fundsFollowed);
      const leftNumber = this.caseManualParseNumericAmount(candidate.leftBoundsNumeric || candidate.leftBoundsDisplay || '0');
      const total = Number.isFinite(amountNumber) && amountNumber > 0
        ? amountNumber
        : (Number.isFinite(followedNumber) && followedNumber > 0 ? followedNumber : NaN);
      const followed = Number.isFinite(followedNumber) && followedNumber > 0 ? followedNumber : total;
      if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(followed) || followed <= 0) return null;
      const width = 260;
      const height = 14;
      const x = -width / 2;
      const y = -78;
      const startRatio = this.caseManualClampNumber((Number.isFinite(leftNumber) ? leftNumber : 0) / total, 0, 1);
      const fillRatio = this.caseManualClampNumber(followed / total, 0, 1 - startRatio);
      const fillWidth = Math.max(3, width * fillRatio);
      return {
        x,
        y,
        width,
        height,
        rx: 7,
        fillX: x + (width * startRatio),
        fillWidth,
        title: `Funds followed ${this.caseManualFormatAmount(followed)} of ${this.caseManualFormatAmount(total)}${candidate.token ? ` ${candidate.token}` : ''}; left bounds ${Number.isFinite(leftNumber) ? this.caseManualFormatAmount(leftNumber) : '0'}.`
      };
    },

    caseManualOrdinalSuffix(day) {
      const value = Number(day) || 0;
      if (value % 100 >= 11 && value % 100 <= 13) return 'th';
      return value % 10 === 1 ? 'st' : value % 10 === 2 ? 'nd' : value % 10 === 3 ? 'rd' : 'th';
    },

    caseManualFormatVisualDateLabel(value) {
      const raw = this.caseManualNormalizeText(value).replace(/\s+UTC$/iu, 'Z');
      if (!raw) return '';
      const isoish = raw.includes('T') ? raw : raw.replace(/\s+/u, 'T');
      const date = new Date(isoish.endsWith('Z') ? isoish : `${isoish}Z`);
      if (!date || Number.isNaN(date.getTime())) return this.caseManualNormalizeText(value);
      const months = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];
      const day = date.getUTCDate();
      let hour = date.getUTCHours();
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12;
      return `${months[date.getUTCMonth()]} ${day}${this.caseManualOrdinalSuffix(day)}, ${date.getUTCFullYear()} ${String(hour).padStart(2, '0')}:${minutes}${ampm}`;
    },

    caseManualFormatHopInspectorCardDateLabel(value) {
      const raw = this.caseManualNormalizeText(value).replace(/\s+UTC$/iu, 'Z');
      if (!raw) return '';
      const isoish = raw.includes('T') ? raw : raw.replace(/\s+/u, 'T');
      const date = new Date(isoish.endsWith('Z') ? isoish : `${isoish}Z`);
      if (!date || Number.isNaN(date.getTime())) return this.caseManualNormalizeText(value);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      let hour = date.getUTCHours();
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12;
      return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()} ${hour}:${minutes} ${ampm}`;
    },

    caseManualBuildVisualPathLabelLines(label = '') {
      const clean = this.caseManualNormalizeText(label);
      if (!clean) return [];
      if (clean.length <= 22) return [clean];
      return clean.match(/.{1,22}/gu) || [clean];
    },

    caseManualVisualPathBoxWidth(lines = []) {
      const longest = (Array.isArray(lines) ? lines : [])
        .map(line => this.caseManualNormalizeText(line).length)
        .reduce((max, length) => Math.max(max, length), 0);
      if (!longest) return 0;
      return Math.max(58, Math.min(270, (longest * 11) + 34));
    },

    caseManualVisualPathBoxHeight(lines = []) {
      const count = Array.isArray(lines) ? lines.filter(Boolean).length : 0;
      return count ? Math.max(30, 15 + (count * 20)) : 0;
    },

    caseManualVisualPathLabelTransform(node = {}) {
      const txHeight = Number(this.caseFlowVisualTxNodeSize && this.caseFlowVisualTxNodeSize.height) || 0;
      const boxHeight = Number(node && node.pathBoxHeight) || 0;
      const y = (txHeight / 2) + (boxHeight / 2) + 10;
      return `translate(0 ${y})`;
    },

    caseManualVisualPathTextY(node = {}, lineIndex = 0) {
      const lines = Array.isArray(node && node.pathLabelLines) ? node.pathLabelLines.filter(Boolean) : [];
      const lineHeight = Number(node && node.pathLineHeight) || 20;
      const index = Number(lineIndex) || 0;
      if (lines.length <= 1) return 0;
      return -((lines.length - 1) * lineHeight) / 2 + (index * lineHeight);
    },

    caseManualFormatPriorBalanceForSelection(candidate = {}) {
      const prior = this.caseManualNormalizeText(candidate?.priorBalance || '');
      if (!prior) return '';
      const token = this.caseManualNormalizeText(candidate?.token || '');
      return [prior, token].filter(Boolean).join(' ');
    },

    caseManualStripFifoInspectorPayloadFromText(text = '') {
      let output = this.caseManualNormalizeText(text || '');
      const marker = 'Tool 88 FIFO inspector:';
      let markerIndex = output.indexOf(marker);
      while (markerIndex >= 0) {
        const bodyStart = markerIndex + marker.length;
        const start = output.indexOf('{', bodyStart);
        if (start < 0) break;
        let depth = 0;
        let inString = false;
        let escaped = false;
        let jsonEnd = -1;
        for (let index = start; index < output.length; index += 1) {
          const char = output[index];
          if (escaped) {
            escaped = false;
            continue;
          }
          if (char === '\\') {
            escaped = true;
            continue;
          }
          if (char === '"') {
            inString = !inString;
            continue;
          }
          if (inString) continue;
          if (char === '{') depth += 1;
          if (char === '}') {
            depth -= 1;
            if (depth === 0) {
              jsonEnd = index + 1;
              break;
            }
          }
        }
        const removeEnd = jsonEnd >= 0 ? jsonEnd : output.length;
        output = `${output.slice(0, markerIndex).trimEnd()}\n${output.slice(removeEnd).trimStart()}`;
        markerIndex = output.indexOf(marker);
      }
      return output.replace(/\n{3,}/gu, '\n\n').trim();
    },

    caseManualSelectionNotePreview(text = '') {
      const source = this.caseManualNormalizeText(text || '');
      if (!source) return '';
      const hadFifoPayload = /Tool\s*88\s+FIFO\s+inspector\s*:/iu.test(source);
      let compact = this.caseManualStripFifoInspectorPayloadFromText(source);
      const fifoSummaryLines = compact
        .split(/\n+/u)
        .map(line => this.caseManualNormalizeText(line))
        .filter(Boolean)
        .filter(line => !/^\{/u.test(line) && !/^"/u.test(line));
      if (hadFifoPayload && fifoSummaryLines.length > 8) compact = fifoSummaryLines.slice(0, 8).join('\n');
      if (compact.length > 560) {
        const sentenceCut = compact.slice(0, 560).lastIndexOf('. ');
        const cutAt = sentenceCut > 220 ? sentenceCut + 1 : 560;
        compact = `${compact.slice(0, cutAt).trim()}…`;
      }
      if (hadFifoPayload) {
        const hint = 'Structured FIFO inspector payload attached. Use the FIFO debug Inspect button for the full calculation view.';
        return compact ? `${compact}\n\n${hint}` : hint;
      }
      return compact;
    },

    caseManualCompactSelectedDetailText(value = '', maxLength = 360) {
      const preview = this.caseManualSelectionNotePreview(value || '');
      const text = this.caseManualNormalizeText(preview || '').replace(/\s+/gu, ' ').trim();
      const limit = Math.max(120, Number(maxLength) || 360);
      if (!text || text.length <= limit) return text;
      return `${text.slice(0, limit).trim()}…`;
    },

    caseManualFifoDiagnosticTextForCandidate(candidate = {}) {
      const row = Array.isArray(candidate?.rawRow) ? candidate.rawRow : (Array.isArray(candidate?.row) ? candidate.row : []);
      return [
        candidate?.tool88Notes,
        row[CASE_MANUAL_START_COL.TOOL88_NOTES],
        candidate?.systemNotes,
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        candidate?.caseNotes,
        row[CASE_MANUAL_START_COL.CASE_NOTES]
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join('\n');
    },

    caseManualExtractTool88FifoInspectorPayload(text = '') {
      const sourceText = this.caseManualNormalizeText(text);
      if (!sourceText) return null;
      const marker = 'Tool 88 FIFO inspector:';
      const markerIndex = sourceText.indexOf(marker);
      if (markerIndex < 0) return null;
      const body = sourceText.slice(markerIndex + marker.length).trim();
      const start = body.indexOf('{');
      if (start < 0) return null;
      let depth = 0;
      let inString = false;
      let escaped = false;
      let end = -1;
      for (let index = start; index < body.length; index += 1) {
        const char = body[index];
        if (escaped) {
          escaped = false;
          continue;
        }
        if (char === '\\') {
          escaped = true;
          continue;
        }
        if (char === '"') {
          inString = !inString;
          continue;
        }
        if (inString) continue;
        if (char === '{') depth += 1;
        if (char === '}') {
          depth -= 1;
          if (depth === 0) {
            end = index + 1;
            break;
          }
        }
      }
      if (end < 0) return null;
      try {
        return JSON.parse(body.slice(start, end));
      } catch {
        return null;
      }
    },

    caseManualFifoInspectorFormatEvent(event = {}, token = '') {
      const tokenText = this.caseManualNormalizeText(event.token || token).toUpperCase();
      const amount = Number(event.amount);
      const fee = Number(event.feeAmount);
      const delta = Number(event.delta);
      const followed = Number(event.followedAmount);
      const leftBound = Number(event.leftBoundAmount);
      const rightBound = Number(event.rightBoundAmount);
      const amountText = Number.isFinite(amount) ? `${amount.toLocaleString(undefined, { maximumFractionDigits: Math.abs(amount) >= 1 ? 8 : 12 })}${tokenText ? ` ${tokenText}` : ''}` : '';
      const feeText = Number.isFinite(fee) && fee > 0 ? `${fee.toLocaleString(undefined, { maximumFractionDigits: Math.abs(fee) >= 1 ? 8 : 12 })}${tokenText ? ` ${tokenText}` : ''}` : '';
      const deltaText = Number.isFinite(delta) ? `${delta.toLocaleString(undefined, { maximumFractionDigits: Math.abs(delta) >= 1 ? 8 : 12 })}${tokenText ? ` ${tokenText}` : ''}` : '';
      const followedText = Number.isFinite(followed) ? `${followed.toLocaleString(undefined, { maximumFractionDigits: Math.abs(followed) >= 1 ? 8 : 12 })}${tokenText ? ` ${tokenText}` : ''}` : '';
      const leftBoundText = Number.isFinite(leftBound) ? `${leftBound.toLocaleString(undefined, { maximumFractionDigits: Math.abs(leftBound) >= 1 ? 8 : 12 })}${tokenText ? ` ${tokenText}` : ''}` : '';
      const rightBoundText = Number.isFinite(rightBound) ? `${rightBound.toLocaleString(undefined, { maximumFractionDigits: Math.abs(rightBound) >= 1 ? 8 : 12 })}${tokenText ? ` ${tokenText}` : ''}` : '';
      return {
        ...event,
        token: tokenText,
        shortHash: this.caseManualShorten(event.hash || '', 10, 8),
        shortFrom: this.caseManualShorten(event.from || '', 8, 6),
        shortTo: this.caseManualShorten(event.to || '', 8, 6),
        amountText,
        feeText,
        deltaText,
        followedText,
        leftBoundText,
        rightBoundText
      };
    },

    caseManualParseFifoDiagnosticsFromCandidate(candidate = {}) {
      const text = this.caseManualFifoDiagnosticTextForCandidate(candidate);
      const inspectorPayload = this.caseManualExtractTool88FifoInspectorPayload(text);
      const has = Boolean(text && (/Tool\s*88\s+FIFO\s+diagnostics/i.test(text) || inspectorPayload));
      const empty = {
        has: false,
        cards: [],
        timeline: [],
        contextRows: [],
        summaryRows: [],
        rawText: text || ''
      };
      if (!has) return empty;

      const firstMatch = (...patterns) => {
        for (const pattern of patterns) {
          const match = text.match(pattern);
          if (match && this.caseManualNormalizeText(match[1])) {
            return this.caseManualNormalizeText(match[1]).replace(/\s*\.$/u, '').trim();
          }
        }
        return '';
      };

      const selectedBalance = inspectorPayload?.selectedAddressPriorBalanceText || firstMatch(/selected-address\s+prior\s+balance\s+before\s+this\s+transaction\s+was\s+([^;\n]+)/iu);
      const sourceExitBalance = firstMatch(/source-address\s+balance\s+before\s+this\s+exit\s+was\s+([^;\n]+)/iu);
      const receiverBalance = firstMatch(/receiver\s+prior\s+balance\s+before\s+this\s+transaction\s+was\s+([^;\n]+)/iu);
      const source = inspectorPayload?.priorBalanceSource || firstMatch(/source=([^;\n]+)/iu);
      const included = inspectorPayload?.includedEventCount !== undefined && inspectorPayload?.includedEventCount !== null ? String(inspectorPayload.includedEventCount) : firstMatch(/included\s+prior\s+events=([^;\n]+)/iu);
      const excluded = inspectorPayload?.excludedEventCount !== undefined && inspectorPayload?.excludedEventCount !== null ? String(inspectorPayload.excludedEventCount) : firstMatch(/excluded\s+events=([^;\n]+)/iu);
      const providerRows = inspectorPayload?.providerRowsReviewed !== undefined && inspectorPayload?.providerRowsReviewed !== null ? String(inspectorPayload.providerRowsReviewed) : firstMatch(/provider\s+rows\s+reviewed=([^;\n]+)/iu);
      const failed = inspectorPayload?.failedOrRevertedRowsExcluded !== undefined && inspectorPayload?.failedOrRevertedRowsExcluded !== null ? String(inspectorPayload.failedOrRevertedRowsExcluded) : firstMatch(/failed\/reverted\s+rows\s+excluded=([^;\n]+)/iu);
      const nonBalance = inspectorPayload?.nonBalanceRowsExcluded !== undefined && inspectorPayload?.nonBalanceRowsExcluded !== null ? String(inspectorPayload.nonBalanceRowsExcluded) : firstMatch(/non-balance\s+rows\s+excluded=([^;\n]+)/iu);
      const ineligible = inspectorPayload?.ineligibleRowsExcluded !== undefined && inspectorPayload?.ineligibleRowsExcluded !== null ? String(inspectorPayload.ineligibleRowsExcluded) : firstMatch(/ineligible\s+rows\s+excluded=([^;\n]+)/iu);
      const feesBefore = inspectorPayload?.nativeFeeAmountBeforeSelectedTxText || firstMatch(/native\s+fees\s+before\s+selected\s+tx=([^;\n]+)/iu, /fees\s+before\s+selected\s+transaction=([^;\n]+)/iu);
      const feeEvents = inspectorPayload?.includedFeeEventCount !== undefined && inspectorPayload?.includedFeeEventCount !== null ? String(inspectorPayload.includedFeeEventCount) : (Array.isArray(inspectorPayload?.includedEvents) ? String(inspectorPayload.includedEvents.filter(event => Number(event?.feeAmount) > 0).length) : firstMatch(/fee\s+events\s+included=([^;\n]+)/iu));
      const targetFee = inspectorPayload?.targetEventFeeText || inspectorPayload?.nativeDirectSpendFeeText || firstMatch(/target-event\s+fee=([^;\n]+)/iu);
      const followedFeesConsumed = inspectorPayload?.followedFeeConsumedText || firstMatch(/followed\s+native\s+fees\s+consumed=([^;\n]+)/iu);

      const row = Array.isArray(candidate?.rawRow) ? candidate.rawRow : (Array.isArray(candidate?.row) ? candidate.row : []);
      const childHashes = parseCaseWwwContinuationHashes(candidate?.continuesAs || row[CASE_MANUAL_START_COL.CONTINUES_AS] || '');
      const contextRows = [
        ['Path label', candidate?.pathLabel || row[CASE_MANUAL_START_COL.PATH_LABEL] || ''],
        ['Transaction hash', candidate?.hash || row[CASE_MANUAL_START_COL.TX_HASH] || ''],
        ['Address of interest', this.caseManualStartCandidateEndpointAddress(candidate) || ''],
        ['UTC date', candidate?.utcDate || row[CASE_MANUAL_START_COL.DATE] || ''],
        ['Amount', [candidate?.amount || row[CASE_MANUAL_START_COL.AMOUNT], candidate?.token || row[CASE_MANUAL_START_COL.TOKEN]].filter(Boolean).join(' ')],
        ['Funds followed', candidate?.fundsFollowed || row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || ''],
        ['Left bounds', candidate?.leftBoundsNumeric || candidate?.leftBoundsDisplay || row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] || '0'],
        ['Continuation children', childHashes.length ? childHashes.join(', ') : 'Path ends.']
      ].filter(item => this.caseManualNormalizeText(item[1]));

      const cards = [
        { label: 'Prior balance', value: selectedBalance || this.caseManualFormatPriorBalanceForSelection(candidate) || '—', note: 'Balance at the selected address before the transaction of interest.' },
        { label: 'Source before exit', value: sourceExitBalance || '—', note: 'Balance at the sending address before this outgoing exit row.' },
        { label: 'Receiver before tx', value: receiverBalance || '—', note: 'Balance already present at the receiving address before this transaction.' },
        { label: 'Fees before selected tx', value: feesBefore || '—', note: targetFee ? `Target-event fee: ${targetFee}` : 'Native-chain fees are shown when they affect the followed asset balance.' },
        { label: 'Followed fees consumed', value: followedFeesConsumed || '—', note: 'When native fees consume followed funds, Tool 88 counts them before creating downstream branches.' },
        { label: 'Failed/reverted excluded', value: failed || '0', note: 'Failed and reverted provider rows are excluded before FIFO balance and exit selection.' }
      ];
      const leftBoundsText = this.caseManualNormalizeText(candidate?.leftBoundsNumeric || candidate?.leftBoundsDisplay || row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] || '0');
      const fundsFollowedText = this.caseManualNormalizeText(candidate?.fundsFollowed || row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || '');
      const rowAmountText = [candidate?.amount || row[CASE_MANUAL_START_COL.AMOUNT], candidate?.token || row[CASE_MANUAL_START_COL.TOKEN]].filter(Boolean).join(' ');
      const equationRows = [
        { key: 'balance', kind: 'prior', label: 'Balance before selected tx', value: selectedBalance || this.caseManualFormatPriorBalanceForSelection(candidate) || '—', note: 'Funds already at the selected address before this transaction arrived.' },
        { key: 'left', kind: 'left', operator: '−', label: 'Left-bounds offset', value: leftBoundsText || '0', note: 'Earlier funds or queue position discounted before this tranche is followed.' },
        { key: 'followed', kind: 'followed', operator: '→', label: 'Funds followed', value: fundsFollowedText || rowAmountText || '—', note: 'The tranche Tool 88 is carrying into the next hop.' },
        { key: 'fees', kind: 'fees', operator: '±', label: 'Native fees considered', value: followedFeesConsumed || targetFee || feesBefore || '—', note: feesBefore ? 'Fee diagnostics were available for this FIFO calculation.' : 'No native fee impact was attached for this row.' }
      ];

      const filterPieces = [
        providerRows ? `${providerRows} provider rows reviewed` : '',
        failed ? `${failed} failed/reverted excluded` : '',
        nonBalance ? `${nonBalance} non-balance excluded` : '',
        ineligible ? `${ineligible} ineligible excluded` : ''
      ].filter(Boolean);
      const debugParts = [
        source ? `source ${source}` : '',
        providerRows ? `${providerRows} provider rows` : '',
        included ? `${included} included` : '',
        excluded ? `${excluded} excluded` : '',
        failed ? `${failed} failed/reverted excluded` : '',
        nonBalance ? `${nonBalance} non-balance excluded` : '',
        ineligible ? `${ineligible} ineligible excluded` : '',
        feesBefore ? `${feesBefore} fees before selected` : '',
        followedFeesConsumed ? `${followedFeesConsumed} followed fees consumed` : '',
        feeEvents ? `${feeEvents} fee event${feeEvents === '1' ? '' : 's'}` : ''
      ].filter(Boolean);

      const summaryRows = [];
      if (selectedBalance) summaryRows.push(['FIFO balance before selected tx', selectedBalance]);
      if (sourceExitBalance) summaryRows.push(['FIFO source balance before exit', sourceExitBalance]);
      if (receiverBalance) summaryRows.push(['Receiver prior balance', receiverBalance]);
      if (feesBefore || targetFee || followedFeesConsumed) summaryRows.push(['FIFO fees', [feesBefore ? `before selected ${feesBefore}` : '', targetFee ? `target ${targetFee}` : '', followedFeesConsumed ? `followed consumed ${followedFeesConsumed}` : ''].filter(Boolean).join(' · ')]);
      if (debugParts.length) summaryRows.push(['FIFO debug', debugParts.join(' · ')]);

      const timeline = [
        {
          key: 'provider-history',
          index: 1,
          title: 'Provider rows filtered',
          value: filterPieces.length ? filterPieces.join(' · ') : 'Provider rows were normalized and filtered before FIFO.',
          note: 'Failed/reverted rows are excluded before prior-balance, queue, and exit selection.'
        },
        {
          key: 'prior-balance',
          index: 2,
          title: 'Prior balance reconstructed',
          value: selectedBalance || 'No selected-address prior-balance note was available.',
          note: [source ? `Source: ${source}` : '', included ? `${included} included prior event${included === '1' ? '' : 's'}` : '', excluded ? `${excluded} excluded event${excluded === '1' ? '' : 's'}` : ''].filter(Boolean).join(' · ')
        },
        {
          key: 'fees',
          index: 3,
          title: 'Native fee impact checked',
          value: feesBefore || followedFeesConsumed || targetFee || 'No native-fee impact was reported for this selected row.',
          note: feeEvents ? `${feeEvents} fee event${feeEvents === '1' ? '' : 's'} contributed before the selected transaction.` : 'For ERC20/TRC20 rows, gas is normally a separate native-asset balance and should not affect token FIFO.'
        },
        {
          key: 'followed-range',
          index: 4,
          title: 'Followed range / continuation',
          value: candidate?.fundsFollowed || row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || 'Funds-followed amount not shown on this row.',
          note: childHashes.length ? `Continues as ${childHashes.join(', ')}.` : 'This row currently ends the path.'
        }
      ];

      return {
        has: true,
        selectedBalance,
        sourceExitBalance,
        receiverBalance,
        source,
        included,
        excluded,
        providerRows,
        failed,
        nonBalance,
        ineligible,
        feesBefore,
        feeEvents,
        targetFee,
        followedFeesConsumed,
        exitCandidates: Array.isArray(inspectorPayload?.exitCandidates) ? inspectorPayload.exitCandidates.map(event => this.caseManualFifoInspectorFormatEvent(event, inspectorPayload?.token || candidate?.token || '')) : [],
        includedEvents: Array.isArray(inspectorPayload?.includedEvents) ? inspectorPayload.includedEvents.map(event => this.caseManualFifoInspectorFormatEvent(event, inspectorPayload?.token || candidate?.token || '')) : [],
        excludedEvents: Array.isArray(inspectorPayload?.excludedEvents) ? inspectorPayload.excludedEvents.map(event => this.caseManualFifoInspectorFormatEvent(event, inspectorPayload?.token || candidate?.token || '')) : [],
        warnings: Array.isArray(inspectorPayload?.warnings) ? inspectorPayload.warnings.filter(Boolean) : [],
        sampleLimit: inspectorPayload?.sampleLimit || '',
        cards,
        equationRows,
        timeline,
        contextRows,
        summaryRows,
        barSegments: this.caseManualBuildFifoInspectorBarSegments(candidate, { selectedBalance, feesBefore, targetFee, followedFeesConsumed, token: candidate?.token || row[CASE_MANUAL_START_COL.TOKEN] || '' }),
        barCaption: 'A compact view of prior balance, left-bounds offset, followed funds, and native-fee impact when applicable.',
        barAriaLabel: 'FIFO queue visualization with prior balance, left bounds, followed funds, and fees.',
        rawText: text
      };
    },

    caseManualFormatFifoInspectorAmount(value, token = '') {
      const tokenText = this.caseManualNormalizeText(token || '').toUpperCase();
      const numeric = Number(String(value ?? '').replace(/,/gu, '').match(/-?\d+(?:\.\d+)?/u)?.[0] || NaN);
      if (!Number.isFinite(numeric)) return this.caseManualNormalizeText(value || '');
      const maximumFractionDigits = Math.abs(numeric) >= 1 ? 8 : 12;
      return `${numeric.toLocaleString(undefined, { maximumFractionDigits })}${tokenText ? ` ${tokenText}` : ''}`;
    },

    caseManualBuildFifoInspectorBarSegments(candidate = {}, diagnostics = {}) {
      const token = this.caseManualNormalizeText(candidate?.token || diagnostics?.token || '');
      const numberFromText = (value = '') => {
        if (value === null || value === undefined) return 0;
        const match = String(value).replace(/,/gu, '').match(/-?\d+(?:\.\d+)?/u);
        const numeric = match ? Number(match[0]) : 0;
        return Number.isFinite(numeric) ? Math.max(0, numeric) : 0;
      };
      const prior = numberFromText(diagnostics?.selectedBalance || candidate?.priorBalance || '');
      const left = numberFromText(candidate?.leftBoundsNumeric || candidate?.leftBoundsDisplay || '');
      const followed = numberFromText(candidate?.fundsFollowed || candidate?.amount || '');
      const fees = numberFromText(diagnostics?.followedFeesConsumed || diagnostics?.targetFee || '');
      const parts = [
        { key: 'prior', kind: 'prior', label: 'Prior balance', amount: prior, display: this.caseManualFormatFifoInspectorAmount(prior, token) },
        { key: 'left', kind: 'left', label: 'Left bounds before followed funds', amount: left, display: this.caseManualFormatFifoInspectorAmount(left, token) },
        { key: 'followed', kind: 'followed', label: 'Funds followed', amount: followed, display: this.caseManualFormatFifoInspectorAmount(followed, token) },
        { key: 'fees', kind: 'fees', label: 'Followed fees consumed', amount: fees, display: this.caseManualFormatFifoInspectorAmount(fees, token) }
      ].filter(segment => segment.amount > 0);
      if (!parts.length) return [];
      const total = parts.reduce((sum, segment) => sum + segment.amount, 0) || 1;
      return parts.map(segment => ({
        ...segment,
        percent: Math.max(2.5, (segment.amount / total) * 100)
      }));
    },

    caseManualFifoInspectorList(key = '') {
      const diagnostics = this.caseManualFifoInspector && this.caseManualFifoInspector.diagnostics
        ? this.caseManualFifoInspector.diagnostics
        : {};
      const value = diagnostics[key];
      return Array.isArray(value) ? value : [];
    },

    caseManualFifoInspectorValue(key = '') {
      const diagnostics = this.caseManualFifoInspector && this.caseManualFifoInspector.diagnostics
        ? this.caseManualFifoInspector.diagnostics
        : {};
      return diagnostics[key];
    },

    caseManualFifoBarSegmentStyle(segment = {}) {
      const width = Math.max(2, Number(segment?.percent) || 0);
      return { flexBasis: `${width}%` };
    },

    caseManualFifoDiagnosticRows(candidate = {}) {
      return this.caseManualParseFifoDiagnosticsFromCandidate(candidate).summaryRows || [];
    },

    caseManualBuildFifoInspectorDiagnosticsFromSelectionRows(activeRow = []) {
      const visibleRows = Array.isArray(this.caseInvestigationVisualizerSelectedNodeRows)
        ? this.caseInvestigationVisualizerSelectedNodeRows
        : [];
      const activePair = Array.isArray(activeRow) && activeRow.length >= 2
        ? [this.caseManualNormalizeText(activeRow[0]), this.caseManualNormalizeText(activeRow[1])]
        : null;
      const rows = [
        ...(activePair ? [activePair] : []),
        ...visibleRows
          .filter(row => Array.isArray(row) && row.length >= 2)
          .map(row => [this.caseManualNormalizeText(row[0]), this.caseManualNormalizeText(row[1])])
      ]
        .filter(row => row[0] || row[1])
        .filter((row, index, list) => list.findIndex(other => other[0] === row[0] && other[1] === row[1]) === index);
      const empty = {
        has: false,
        cards: [],
        timeline: [],
        contextRows: [],
        summaryRows: [],
        exitCandidates: [],
        includedEvents: [],
        excludedEvents: [],
        warnings: [],
        barSegments: [],
        rawText: ''
      };
      if (!rows.length) return empty;

      const valueFor = (...patterns) => {
        for (const pattern of patterns) {
          const found = rows.find(([label]) => pattern.test(label));
          if (found && found[1]) return found[1];
        }
        return '';
      };
      const activeLabel = this.caseManualNormalizeText(activeRow?.[0] || '');
      const activeValue = this.caseManualNormalizeText(activeRow?.[1] || '');
      const selectedBalance = valueFor(/^FIFO balance before selected tx$/iu, /^Prior balance$/iu);
      const sourceExitBalance = valueFor(/^FIFO source balance before exit$/iu);
      const receiverBalance = valueFor(/^Receiver prior balance$/iu);
      const fifoFees = valueFor(/^FIFO fees$/iu);
      const fifoDebug = (/fifo/iu.test(activeLabel) && activeValue) ? activeValue : valueFor(/^FIFO debug$/iu);
      const hasFifoRow = rows.some(([label, value]) => /fifo/iu.test(label) && value);
      if (!hasFifoRow && !selectedBalance && !sourceExitBalance && !receiverBalance && !fifoFees && !fifoDebug) return empty;

      const candidate = this.caseManualFifoInspectorSelectedCandidate() || {};
      const row = Array.isArray(candidate?.rawRow) ? candidate.rawRow : (Array.isArray(candidate?.row) ? candidate.row : []);
      const token = candidate?.token || row[CASE_MANUAL_START_COL.TOKEN] || valueFor(/^Token$/iu) || '';
      const contextLabels = /^(Spreadsheet row|Path label|Transaction hash|From address|To address|UTC date|Amount|Funds followed|Prior balance|Left bounds|Continues as|Hydration)$/iu;
      const contextRows = rows.filter(([label, value]) => contextLabels.test(label) && value);
      const summaryRows = [
        selectedBalance ? ['FIFO balance before selected tx', selectedBalance] : null,
        sourceExitBalance ? ['FIFO source balance before exit', sourceExitBalance] : null,
        receiverBalance ? ['Receiver prior balance', receiverBalance] : null,
        fifoFees ? ['FIFO fees', fifoFees] : null,
        fifoDebug ? ['FIFO debug', fifoDebug] : null
      ].filter(Boolean);

      const parseDebugValue = (pattern) => {
        const match = fifoDebug.match(pattern);
        return match ? this.caseManualNormalizeText(match[1]).replace(/\s*\.$/u, '').trim() : '';
      };
      const providerRows = parseDebugValue(/(\d+(?:,\d+)*)\s+provider\s+rows/iu);
      const included = parseDebugValue(/(\d+(?:,\d+)*)\s+included/iu);
      const excluded = parseDebugValue(/(\d+(?:,\d+)*)\s+excluded/iu);
      const failed = parseDebugValue(/(\d+(?:,\d+)*)\s+failed\/reverted\s+excluded/iu);
      const feesBefore = parseDebugValue(/([^·]+?)\s+fees\s+before\s+selected/iu);
      const followedFeesConsumed = parseDebugValue(/([^·]+?)\s+followed\s+fees\s+consumed/iu);

      const cards = [
        { label: 'Prior balance', value: selectedBalance || this.caseManualFormatPriorBalanceForSelection(candidate) || '—', note: 'Balance at the selected address before the transaction of interest.' },
        { label: 'Source before exit', value: sourceExitBalance || '—', note: 'Balance at the sending address before this outgoing exit row.' },
        { label: 'Receiver before tx', value: receiverBalance || '—', note: 'Balance already present at the receiving address before this transaction.' },
        { label: 'FIFO fees', value: fifoFees || feesBefore || followedFeesConsumed || '—', note: 'Native-chain fees are shown when they affect the followed asset balance.' },
        { label: 'Failed/reverted excluded', value: failed || '0', note: 'Failed and reverted provider rows are excluded before FIFO balance and exit selection.' }
      ];
      const fallbackLeftBounds = valueFor(/^Left bounds$/iu) || '0';
      const fallbackFundsFollowed = valueFor(/^Funds followed$/iu) || valueFor(/^Amount$/iu) || '';
      const equationRows = [
        { key: 'balance', kind: 'prior', label: 'Balance before selected tx', value: selectedBalance || this.caseManualFormatPriorBalanceForSelection(candidate) || '—', note: 'Available from compact FIFO diagnostics on the selected row.' },
        { key: 'left', kind: 'left', operator: '−', label: 'Left-bounds offset', value: fallbackLeftBounds, note: 'Queue position before the followed tranche.' },
        { key: 'followed', kind: 'followed', operator: '→', label: 'Funds followed', value: fallbackFundsFollowed || '—', note: 'The tranche being followed from this selected row.' },
        { key: 'fees', kind: 'fees', operator: '±', label: 'Native fees considered', value: fifoFees || feesBefore || followedFeesConsumed || '—', note: 'Shown when Tool 88 exposed compact fee diagnostics.' }
      ];
      const timeline = [
        {
          key: 'visible-fifo-summary',
          index: 1,
          title: 'Visible FIFO diagnostics',
          value: fifoDebug || 'The selected row exposed compact FIFO details, but no full Tool 88 inspector JSON was attached.',
          note: 'This fallback opens from the selected-node detail table. Rerun the hop with the latest Tool 88 patch to populate richer event tables.'
        },
        {
          key: 'prior-balance',
          index: 2,
          title: 'Prior balance / queue position',
          value: selectedBalance || 'No selected-address prior-balance value was shown on this row.',
          note: [providerRows ? `${providerRows} provider rows` : '', included ? `${included} included prior events` : '', excluded ? `${excluded} excluded events` : '', failed ? `${failed} failed/reverted excluded` : ''].filter(Boolean).join(' · ')
        },
        {
          key: 'followed-range',
          index: 3,
          title: 'Followed range',
          value: valueFor(/^Funds followed$/iu) || 'Funds-followed amount was not shown in the selected details.',
          note: valueFor(/^Left bounds$/iu) ? `Left bounds: ${valueFor(/^Left bounds$/iu)}.` : 'Left bounds were not shown in the selected details.'
        }
      ];
      const rawText = [
        'Visible selected-node FIFO details:',
        ...rows
          .filter(([label]) => /fifo|prior balance|left bounds|funds followed|transaction hash|path label|utc date|amount|continues/iu.test(label))
          .map(([label, value]) => `${label}: ${value}`)
      ].join('\n');

      return {
        has: true,
        selectedBalance,
        sourceExitBalance,
        receiverBalance,
        providerRows,
        included,
        excluded,
        failed,
        feesBefore,
        followedFeesConsumed,
        cards,
        equationRows,
        timeline,
        contextRows,
        summaryRows,
        exitCandidates: [],
        includedEvents: [],
        excludedEvents: [],
        warnings: ['Full FIFO event tables were not attached to the selected node; showing the visible compact diagnostics instead.'],
        barSegments: this.caseManualBuildFifoInspectorBarSegments(candidate, { selectedBalance, feesBefore, followedFeesConsumed, token }),
        barCaption: 'Fallback view built from the selected-node detail rows.',
        barAriaLabel: 'FIFO queue fallback visualization.',
        rawText
      };
    },

    caseManualCanOpenFifoInspectorRow(label = '') {
      return /fifo/i.test(this.caseManualNormalizeText(label || ''));
    },

    caseManualFifoInspectorSelectedCandidate() {
      const selectedCandidate = this.caseInvestigationVisualizerSelectedCandidateView || this.caseManualStartSingleSelectedCandidate || null;
      if (selectedCandidate?.caseManualStartCandidate) return selectedCandidate.caseManualStartCandidate;
      if (selectedCandidate) return selectedCandidate;
      const node = this.caseInvestigationVisualizerSelectedNode || this.caseFlowVisualSelectedNode || null;
      return node?.caseCandidate || node?.candidate || null;
    },

    openCaseManualFifoInspectorFromSelection(row = []) {
      const candidate = this.caseManualFifoInspectorSelectedCandidate() || {};
      let diagnostics = this.caseManualParseFifoDiagnosticsFromCandidate(candidate);
      if (!diagnostics.has) diagnostics = this.caseManualBuildFifoInspectorDiagnosticsFromSelectionRows(row);
      if (!diagnostics.has) {
        this.showCaseInvestigationVisualizerClipboardMessage?.('No FIFO diagnostics were available for this selected node yet.');
        return;
      }
      const contextValue = (label = '') => {
        const target = this.caseManualNormalizeText(label).toLowerCase();
        const found = (diagnostics.contextRows || []).find(item => Array.isArray(item) && this.caseManualNormalizeText(item[0]).toLowerCase() === target);
        return found ? this.caseManualNormalizeText(found[1]) : '';
      };
      const hash = candidate?.hash || candidate?.transactionHash || candidate?.txHash || contextValue('Transaction hash') || '';
      const pathLabel = candidate?.pathLabel || contextValue('Path label') || '';
      const path = pathLabel ? `Path ${pathLabel}` : 'Selected row';
      this.caseManualFifoInspector = {
        open: true,
        title: 'FIFO calculation inspector',
        subtitle: hash ? `${path} · ${this.caseManualShorten(hash, 10, 8)}` : path,
        rowLabel: this.caseManualNormalizeText(row?.[0] || ''),
        rowValue: this.caseManualNormalizeText(row?.[1] || ''),
        candidateKey: candidate?.key || candidate?.candidateKey || `${pathLabel}|${hash}`,
        candidate,
        diagnostics
      };
    },

    closeCaseManualFifoInspector() {
      this.caseManualFifoInspector = {
        open: false,
        title: '',
        subtitle: '',
        rowLabel: '',
        rowValue: '',
        candidateKey: '',
        candidate: null,
        diagnostics: null
      };
    },

    async copyCaseManualFifoInspectorDetails() {
      const diagnostics = this.caseManualFifoInspector?.diagnostics || {};
      const lines = [
        this.caseManualFifoInspector?.title || 'FIFO calculation inspector',
        this.caseManualFifoInspector?.subtitle || '',
        '',
        ...(diagnostics.contextRows || []).map(row => `${row[0]}: ${row[1]}`),
        '',
        ...(diagnostics.summaryRows || []).map(row => `${row[0]}: ${row[1]}`),
        '',
        'FIFO exit candidates / selected outputs:',
        ...(diagnostics.exitCandidates || []).map(event => `${event.index || ''}. ${event.hash || event.shortHash || ''} amount=${event.amountText || ''} followed=${event.followedText || ''} left=${event.leftBoundText || ''} from=${event.from || event.shortFrom || ''} to=${event.to || event.shortTo || ''}`),
        '',
        'Included prior-balance events:',
        ...(diagnostics.includedEvents || []).map(event => `${event.index || ''}. ${event.role || ''} ${event.hash || event.shortHash || ''} delta=${event.deltaText || ''} fee=${event.feeText || ''} from=${event.from || event.shortFrom || ''} to=${event.to || event.shortTo || ''}`),
        '',
        'Excluded / dismissed events:',
        ...(diagnostics.excludedEvents || []).map(event => `${event.index || ''}. ${event.role || ''} ${event.hash || event.shortHash || ''} delta=${event.deltaText || ''} fee=${event.feeText || ''} from=${event.from || event.shortFrom || ''} to=${event.to || event.shortTo || ''}`),
        '',
        ...(diagnostics.warnings || []).map(warning => `Warning: ${warning}`),
        '',
        diagnostics.rawText || ''
      ].filter(line => line !== null && line !== undefined);
      const text = lines.join('\n');
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          this.showCaseInvestigationVisualizerClipboardMessage('FIFO inspector details copied.');
        }
      } catch (error) {
        this.showCaseInvestigationVisualizerClipboardMessage('Could not copy FIFO details automatically.');
      }
    },

    caseManualSelectedDetailRowKey(row = [], index = 0) {
      const label = this.caseManualNormalizeText(Array.isArray(row) ? row[0] : '');
      const value = this.caseManualNormalizeText(Array.isArray(row) ? row[1] : '');
      const compactValue = value.length > 96 ? `${value.slice(0, 96)}:${value.length}` : value;
      return `${index}-${label}-${compactValue}`;
    },

    caseManualSelectedDetailRowDisplayValue(row = []) {
      const label = this.caseManualNormalizeText(Array.isArray(row) ? row[0] : '');
      const value = this.caseManualNormalizeText(Array.isArray(row) ? row[1] : '');
      if (!value) return value;
      const noteLike = /notes?|tool\s*88|case\s*notes?/iu.test(label);
      const fifoLike = /fifo/iu.test(label);
      if (noteLike && value.length > 220) {
        const hasFifoInspector = /Tool\s*88\s+FIFO\s+inspector|Tool\s*88\s+FIFO\s+diagnostics/iu.test(value);
        if (hasFifoInspector) {
          return `Large Tool 88 FIFO diagnostics hidden here (${value.length.toLocaleString()} chars). Use the FIFO Inspect button for the readable view.`;
        }
        return `${value.slice(0, 220).trim()}… (${value.length.toLocaleString()} chars; open Tool 88 / debug for full notes)`;
      }
      if (fifoLike && value.length > 360) {
        return `${value.slice(0, 360).trim()}… (${value.length.toLocaleString()} chars; open Inspect for details)`;
      }
      if (value.length > 720) return `${value.slice(0, 420).trim()}… (${value.length.toLocaleString()} chars)`;
      return value;
    },

    caseManualSelectedDetailRowTitle(row = []) {
      const label = this.caseManualNormalizeText(Array.isArray(row) ? row[0] : '');
      const value = this.caseManualNormalizeText(Array.isArray(row) ? row[1] : '');
      if (!value) return '';
      if (/notes?|tool\s*88|fifo/iu.test(label) && value.length > 360) return 'Long diagnostic text is summarized in this side panel. Use Inspect, Copy details, or full Tool 88 for the complete record.';
      return value.length > 420 ? value.slice(0, 420) : value;
    },

    buildCaseManualStartDetailRows(candidate = {}) {
      const detailRows = [
        ['Spreadsheet row', candidate.spreadsheetRowNumber || ''],
        ['Path label', candidate.pathLabel || ''],
        ['Transaction hash', candidate.hash || ''],
        ['From address', candidate.fromAddress || 'Pending provider hydration'],
      ];
      if (candidate.fromAttributionLabel) detailRows.push(['From attribution', candidate.fromAttributionLabel]);
      detailRows.push(['To address', candidate.toAddress || 'Pending provider hydration / target choice']);
      if (candidate.toAttributionLabel) detailRows.push(['To attribution', candidate.toAttributionLabel]);
      detailRows.push(
        ['UTC date', candidate.utcDate || ''],
        ['Amount', [candidate.amount, candidate.token].filter(Boolean).join(' ')],
        ['Funds followed', candidate.fundsFollowed || 'Pending'],
        ['Prior balance', this.caseManualFormatPriorBalanceForSelection(candidate)],
        ...this.caseManualFifoDiagnosticRows(candidate),
        ['Left bounds', candidate.leftBoundsNumeric || candidate.leftBoundsDisplay || '0'],
        ['Continues as', candidate.continuesAs || 'Path ends.'],
        ['Hydration', candidate.hydrationStatus === 'hydrated' ? 'Hydrated from Tool 88 provider details' : (candidate.hydrationMessage || 'Not yet hydrated')]
      );
      if (candidate.caseNotes) {
        detailRows.push(['Case notes', this.caseManualCompactSelectedDetailText(candidate.caseNotes, 420)]);
      }
      return detailRows;
    },

    groupCaseManualStartVisualizerRows(rows = []) {
      const candidates = (Array.isArray(rows) ? rows : []).filter(row => row?.hash);
      if (!candidates.length) return [];

      const candidateKey = (candidate = {}) => [
        Number.isInteger(Number(candidate?.rowIndex)) ? Number(candidate.rowIndex) : '',
        this.caseManualNormalizeText(candidate?.pathLabel).toLowerCase(),
        this.caseManualNormalizeText(candidate?.hash).toLowerCase(),
        this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate))
      ].join('|');

      let roots = candidates.filter((candidate) => {
        const fromHash = this.caseManualNormalizeText(candidate?.fromHash);
        if (fromHash) return false;
        return typeof this.caseManualStartPathLabelLooksInitialFlowRoot === 'function'
          ? this.caseManualStartPathLabelLooksInitialFlowRoot(candidate?.pathLabel)
          : true;
      });
      if (!roots.length) roots = candidates.filter(candidate => !this.caseManualNormalizeText(candidate?.fromHash));
      if (!roots.length) roots = candidates.slice();

      const rootKeyFor = root => `root:${candidateKey(root)}`;
      const rootByCandidateKey = new Map(roots.map(root => [candidateKey(root), root]));
      const rootLabels = roots.map(root => this.caseManualNormalizeText(root?.pathLabel)).filter(Boolean);
      const rootFromPathLabel = (candidate = {}) => {
        const label = this.caseManualNormalizeText(candidate?.pathLabel);
        if (!label) return null;
        return roots
          .filter(root => this.caseWorkflowPathLabelBelongsToRoot(label, root?.pathLabel, rootLabels))
          .sort((left, right) => {
            const lengthDelta = this.caseManualNormalizeText(right?.pathLabel).length - this.caseManualNormalizeText(left?.pathLabel).length;
            return lengthDelta || (Number(left?.rowIndex) || 0) - (Number(right?.rowIndex) || 0);
          })[0] || null;
      };

      const byHash = new Map();
      candidates.forEach((candidate) => {
        const hashKey = this.caseManualNormalizeText(candidate?.hash).toLowerCase();
        if (!hashKey) return;
        if (!byHash.has(hashKey)) byHash.set(hashKey, []);
        byHash.get(hashKey).push(candidate);
      });

      const chooseParent = (candidate = {}) => {
        const parentHashKey = this.caseManualNormalizeText(candidate?.fromHash).toLowerCase();
        const matches = parentHashKey ? (byHash.get(parentHashKey) || []) : [];
        if (!matches.length) return null;
        const desiredRoot = rootFromPathLabel(candidate);
        const desiredRootLabel = this.caseManualNormalizeText(desiredRoot?.pathLabel);
        const childConnectionKey = this.caseManualNormalizeAddressKey(this.caseManualStartChildConnectionAddress(candidate));
        return matches
          .map(parent => {
            const parentEndpointKey = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(parent));
            const parentLabel = this.caseManualNormalizeText(parent?.pathLabel);
            let score = 0;
            if (childConnectionKey && parentEndpointKey && childConnectionKey === parentEndpointKey) score += 1000;
            if (desiredRootLabel && this.caseWorkflowPathLabelBelongsToRoot(parentLabel, desiredRootLabel, rootLabels)) score += 500;
            if ((Number(parent?.rowIndex) || 0) < (Number(candidate?.rowIndex) || 0)) score += 50;
            score -= Math.abs((Number(candidate?.rowIndex) || 0) - (Number(parent?.rowIndex) || 0));
            return { parent, score };
          })
          .sort((left, right) => right.score - left.score || (Number(right.parent?.rowIndex) || 0) - (Number(left.parent?.rowIndex) || 0))[0]?.parent || null;
      };

      const resolveRoot = (candidate = {}) => {
        const directKey = candidateKey(candidate);
        if (rootByCandidateKey.has(directKey)) return rootByCandidateKey.get(directKey);

        // Path labels are the strongest root-family discriminator when two BTC
        // root entries intentionally share one transaction hash but select
        // different outputs.
        const labelRoot = rootFromPathLabel(candidate);
        if (labelRoot) return labelRoot;

        let current = candidate;
        const visited = new Set();
        for (let depth = 0; depth < 50 && current; depth += 1) {
          const currentKey = candidateKey(current);
          if (!currentKey || visited.has(currentKey)) break;
          visited.add(currentKey);
          if (rootByCandidateKey.has(currentKey)) return rootByCandidateKey.get(currentKey);
          current = chooseParent(current);
        }
        return candidate;
      };

      const groupsByRoot = new Map();
      candidates.forEach((candidate) => {
        const root = resolveRoot(candidate);
        const key = rootKeyFor(root);
        if (!groupsByRoot.has(key)) groupsByRoot.set(key, []);
        groupsByRoot.get(key).push(candidate);
      });
      return Array.from(groupsByRoot.values())
        .map(groupRows => groupRows.slice().sort((a, b) => (Number(a.rowIndex) || 0) - (Number(b.rowIndex) || 0)))
        .sort((a, b) => (Number(a[0]?.rowIndex) || 0) - (Number(b[0]?.rowIndex) || 0));
    },

    caseManualStartAddressMergeMode() {
      const rawMode = this.caseManualNormalizeText(this.caseManualStartVisualizerOptions?.mergeAddressMode || 'none');
      return ['none', 'sameColumnMiddle', 'globalAverage'].includes(rawMode) ? rawMode : 'none';
    },

    caseManualStartAddressNodeKey(address = '', roleLabel = '', row = {}, hopIndex = 0, side = '', x = 0) {
      const mode = this.caseManualStartAddressMergeMode();
      const addressKey = this.caseManualNormalizeAddressKey(address);
      const rowHashKey = this.caseManualNormalizeText(row?.hash || row?.transactionHash || row?.txHash || '').toLowerCase();
      const parentHashKey = this.caseManualNormalizeText(row?.fromHash || row?.parentHash || '').toLowerCase();
      const rowKey = rowHashKey || this.caseManualNormalizeText(`row-${row?.rowIndex || hopIndex}`) || `row-${hopIndex}`;
      const sideKey = this.caseManualNormalizeText(side || roleLabel || 'address').replace(/[^a-z0-9]+/giu, '-').toLowerCase() || 'address';
      if (!addressKey) return `missing:${rowKey}:${hopIndex}:${sideKey}:${Math.round(Number(x) || 0)}`;
      if (mode === 'globalAverage') return `global:${addressKey}`;

      const reverse = this.caseManualStartCandidateIsReverse(row);
      if (!reverse) {
        // Forward continuity: parent To == child From.
        if ((sideKey.includes('to') || sideKey === 'destination') && rowHashKey) {
          return `continuity:${rowHashKey}:to:${addressKey}`;
        }
        if ((sideKey.includes('from') || sideKey === 'source') && parentHashKey) {
          return `continuity:${parentHashKey}:to:${addressKey}`;
        }
      } else {
        // Reverse continuity: the earlier/source transaction's To address feeds the
        // later transaction's From address. Store the later From occurrence, then
        // reuse it for the reverse child transaction's To node.
        if ((sideKey.includes('from') || sideKey === 'source') && rowHashKey) {
          return `continuity:${rowHashKey}:from:${addressKey}`;
        }
        if ((sideKey.includes('to') || sideKey === 'destination') && parentHashKey) {
          return `continuity:${parentHashKey}:from:${addressKey}`;
        }
      }

      if (mode === 'sameColumnMiddle') return `column:${Math.round((Number(x) || 0) / 25)}:${addressKey}`;
      return `unique:${rowKey}:${hopIndex}:${sideKey}:${addressKey}`;
    },

    caseManualStartVisualHopSpacingX(rows = []) {
      const sourceRows = Array.isArray(rows) ? rows : [];
      const hasBtc = sourceRows.some(row => this.caseManualInferNetworkFamily(row) === 'btc');
      return hasBtc ? 1040 : 640;
    },

    caseManualStartVisualEndpointOffsetX(family = '') {
      return String(family || '').toLowerCase() === 'btc' ? 520 : 280;
    },

    caseManualStartVisualBtcAmountOffsetX(endpointOffsetX = 520) {
      return Math.max(260, Math.round((Number(endpointOffsetX) || 520) / 2));
    },

    caseManualStartVisualBranchSpacingY() {
      return 190;
    },

    caseManualStartVisualLayoutForRows(rows = []) {
      const sourceRows = Array.isArray(rows) ? rows : [];
      const reverseFlow = sourceRows.some(row => this.caseManualStartCandidateIsReverse(row));
      const directionMultiplier = reverseFlow ? -1 : 1;
      const hopSpacingX = this.caseManualStartVisualHopSpacingX(sourceRows);
      const branchSpacingY = this.caseManualStartVisualBranchSpacingY();
      const rowFootprintY = Math.max(168, Math.round(branchSpacingY));
      const siblingSubtreeGapY = Math.max(18, Math.round(branchSpacingY * 0.14));
      const rootSubtreeGapY = Math.max(90, Math.round(branchSpacingY * 0.72));
      const normalizeRowHash = row => this.caseManualNormalizeText(row?.hash || row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const normalizeRowParentHash = row => this.caseManualResolveKnownTransactionHash(row?.fromHash || row?.[CASE_MANUAL_START_COL.FROM_HASH], sourceRows).toLowerCase();
      const normalizeRowPathLabel = row => this.caseManualNormalizeText(row?.pathLabel || row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
      const rowOccurrenceKey = (row = {}, index = 0) => {
        const hash = normalizeRowHash(row);
        if (!hash) return `row-${index}`;
        const label = normalizeRowPathLabel(row);
        const parent = normalizeRowParentHash(row);
        const funds = this.caseManualNormalizeText(row?.fundsFollowed || row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]).replace(/,/gu, '').toLowerCase();
        const left = this.caseManualNormalizeText(row?.leftBoundsNumeric || row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || row?.leftBoundsDisplay || row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY]).replace(/,/gu, '').toLowerCase();
        return `${label || `idx-${index}`}|${hash}|${parent}|${funds}|${left}|${index}`;
      };
      const pathLooksDescendant = (childLabel = '', parentLabel = '') => {
        const child = this.caseManualNormalizeText(childLabel).toLowerCase();
        const parent = this.caseManualNormalizeText(parentLabel).toLowerCase();
        return Boolean(child && parent && child !== parent && child.startsWith(parent));
      };
      const normalizeRowFromLabel = row => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_LABEL] || row?.fromLabel || '').toLowerCase();
      const occurrenceAmount = (row = {}) => {
        const text = this.caseManualNormalizeText(row?.fundsFollowed || row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]).replace(/,/g, '');
        const match = text.match(/-?\d+(?:\.\d+)?/u);
        if (!match) return NaN;
        const amount = Number(match[0]);
        return Number.isFinite(amount) ? amount : NaN;
      };
      const occurrenceAmountMatches = (childRow = {}, parentRow = {}) => {
        const childAmount = occurrenceAmount(childRow);
        const parentAmount = occurrenceAmount(parentRow);
        if (!Number.isFinite(childAmount) || !Number.isFinite(parentAmount)) return false;
        const tolerance = Math.max(0.000001, Math.abs(parentAmount) * 1e-8);
        return Math.abs(childAmount - parentAmount) <= tolerance;
      };
      const rowFromAddressKeys = (row = {}) => {
        const keys = [];
        const remember = value => {
          const key = this.caseManualNormalizeAddressKey(value);
          if (key && !keys.includes(key)) keys.push(key);
        };
        if (reverseFlow) {
          remember(row?.toAddress || row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
          return keys;
        }
        remember(row?.fromAddress || row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        [row?.btcInputAddresses, row?.inputAddresses, row?.fromAddresses].forEach(source => {
          (Array.isArray(source) ? source : []).forEach(input => {
            if (Array.isArray(input)) remember(input[CASE_MANUAL_START_COL.FROM_ADDRESS]);
            else if (input && typeof input === 'object') remember(input.fromAddress || input.from_address || input.address || input.inputAddress || input.input_address);
            else remember(input);
          });
        });
        return keys;
      };
      const rowHasFromAddressKey = (row = {}, addressKey = '') => Boolean(addressKey && rowFromAddressKeys(row).includes(addressKey));
      const items = sourceRows.map((row, index) => ({
        row,
        index,
        rowIndex: index,
        hashKey: normalizeRowHash(row),
        parentHashKey: normalizeRowParentHash(row),
        pathLabel: normalizeRowPathLabel(row),
        occurrenceKey: rowOccurrenceKey(row, index),
        fromAddressKey: this.caseManualNormalizeAddressKey(row?.fromAddress || row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]),
        toAddressKey: this.caseManualNormalizeAddressKey(row?.toAddress || row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
        childHashes: this.caseManualExtractHashTokensFromContinuesAs(row?.continuesAs || row?.[CASE_MANUAL_START_COL.CONTINUES_AS]),
      }));
      const itemByOccurrence = new Map(items.map(item => [item.occurrenceKey, item]));
      const itemsByHash = new Map();
      items.forEach(item => {
        if (!item.hashKey) return;
        if (!itemsByHash.has(item.hashKey)) itemsByHash.set(item.hashKey, []);
        itemsByHash.get(item.hashKey).push(item);
      });
      const allocationCoverage = this.caseManualBuildOccurrenceContinuationCoverage(items);
      const allocationParentByChild = allocationCoverage.parentByChild || new Map();
      const resolveVisualParentItem = (row = {}, index = 0) => {
        const occurrenceKey = rowOccurrenceKey(row, index);
        const allocationParent = allocationParentByChild.get(occurrenceKey);
        if (allocationParent) return allocationParent;
        const parentHashKey = normalizeRowParentHash(row);
        if (!parentHashKey) return null;
        const candidates = (itemsByHash.get(parentHashKey) || []).filter(item => item.index !== index);
        if (!candidates.length) return null;
        const childLabel = normalizeRowPathLabel(row);
        const childHashKey = normalizeRowHash(row);
        const childFromLabel = normalizeRowFromLabel(row);
        const childFromKeys = rowFromAddressKeys(row);
        const childFamily = this.caseManualInferNetworkFamily(row);
        const parentListsChildHash = item => Boolean(childHashKey && (Array.isArray(item?.childHashes) ? item.childHashes : [])
          .some(hash => this.caseManualTransactionHashesMatch(hash, childHashKey)));
        if (candidates.length > 1) {
          const addressMatches = childFromKeys.length
            ? candidates.filter(item => rowHasFromAddressKey(row, (reverseFlow ? item.fromAddressKey : item.toAddressKey) || this.caseManualNormalizeAddressKey(reverseFlow ? (item.row?.fromAddress || item.row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]) : (item.row?.toAddress || item.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]))))
            : [];
          const listedMatches = childHashKey ? candidates.filter(parentListsChildHash) : [];
          const listedAddressMatches = listedMatches.filter(item => !childFromKeys.length || rowHasFromAddressKey(row, (reverseFlow ? item.fromAddressKey : item.toAddressKey) || this.caseManualNormalizeAddressKey(reverseFlow ? (item.row?.fromAddress || item.row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]) : (item.row?.toAddress || item.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]))));
          if (listedAddressMatches.length === 1) return listedAddressMatches[0];
          if (childFamily === 'btc' && listedMatches.length === 1 && (!addressMatches.length || addressMatches.includes(listedMatches[0]))) return listedMatches[0];
          // BTC/UTXO grafts can have the same transaction hash in multiple
          // forensic path occurrences. In those cases the actual spent output
          // address is more reliable than an inherited/simple path label.
          if (childFamily === 'btc' && addressMatches.length === 1) return addressMatches[0];
          const fromLabelMatches = childFromLabel
            ? candidates.filter(item => this.caseManualNormalizeText(item.row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase() === childFromLabel)
            : [];
          if (fromLabelMatches.length === 1) return fromLabelMatches[0];
          const descendantMatches = candidates.filter(item => pathLooksDescendant(childLabel, item.pathLabel));
          if (descendantMatches.length === 1 && !(childFamily === 'btc' && addressMatches.length > 1)) return descendantMatches[0];
          const allocationMatches = candidates.filter(item => occurrenceAmountMatches(row, item.row));
          const listedAllocationMatches = allocationMatches.filter(parentListsChildHash);
          if (listedAllocationMatches.length === 1) return listedAllocationMatches[0];
          if (allocationMatches.length === 1 && (!listedMatches.length || parentListsChildHash(allocationMatches[0]))) return allocationMatches[0];
          if (listedMatches.length === 1 && (!addressMatches.length || addressMatches.includes(listedMatches[0]))) return listedMatches[0];
          if (addressMatches.length === 1) return addressMatches[0];
        }
        const scored = candidates.map(item => {
          let score = 0;
          const exactFromLabel = Boolean(childFromLabel && this.caseManualNormalizeText(item.row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase() === childFromLabel);
          if (exactFromLabel) score += 500;
          const listedContinuation = parentListsChildHash(item);
          if (listedContinuation) score += childFamily === 'btc' ? 640 : 360;
          if (pathLooksDescendant(childLabel, item.pathLabel)) score += childFamily === 'btc' ? 80 : 120;
          if (candidates.length > 1 && occurrenceAmountMatches(row, item.row)) score += 260;
          if (item.index < index) score += 40;
          const parentEndpoint = this.caseManualNormalizeAddressKey(reverseFlow ? (item.row?.fromAddress || item.row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]) : (item.row?.toAddress || item.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]));
          if (rowHasFromAddressKey(row, parentEndpoint)) score += childFamily === 'btc' ? (listedContinuation ? 900 : 720) : 30;
          score -= Math.abs(index - item.index) / 1000;
          return { item, score, exactFromLabel };
        }).sort((left, right) => right.score - left.score || (right.item.index || 0) - (left.item.index || 0));
        if (candidates.length > 1) {
          const topScore = scored[0]?.score;
          const top = scored.filter(entry => entry.score === topScore);
          return top.length === 1 && topScore > 40 ? top[0].item : null;
        }
        return scored[0]?.item || candidates[0] || null;
      };

      const childrenByParent = new Map();
      sourceRows.forEach((row, index) => {
        const parentItem = resolveVisualParentItem(row, index);
        if (!parentItem) return;
        if (!childrenByParent.has(parentItem.occurrenceKey)) childrenByParent.set(parentItem.occurrenceKey, []);
        childrenByParent.get(parentItem.occurrenceKey).push({ row, index });
      });
      childrenByParent.forEach((children, parentKey) => {
        const parentRow = itemByOccurrence.get(parentKey)?.row || {};
        const qOrder = this.caseManualExtractContinuesAsHashes(parentRow.continuesAs || parentRow[CASE_MANUAL_START_COL.CONTINUES_AS])
          .filter(Boolean);
        children.sort((left, right) => {
          if (qOrder.length) {
            const leftKey = this.caseManualNormalizeText(left.row?.hash || left.row?.[CASE_MANUAL_START_COL.TX_HASH]);
            const rightKey = this.caseManualNormalizeText(right.row?.hash || right.row?.[CASE_MANUAL_START_COL.TX_HASH]);
            const leftOrder = this.caseManualHashOrderIndex(leftKey, qOrder);
            const rightOrder = this.caseManualHashOrderIndex(rightKey, qOrder);
            if (leftOrder !== rightOrder) return leftOrder - rightOrder;
          }
          return left.index - right.index;
        });
      });

      const rootRows = sourceRows
        .map((row, index) => ({ row, index }))
        .filter(({ row, index }) => !resolveVisualParentItem(row, index));
      const rootItems = rootRows.length ? rootRows : sourceRows.map((row, index) => ({ row, index }));

      const childrenForIndex = (index, stack = new Set()) => {
        const row = sourceRows[index] || {};
        const rowKey = rowOccurrenceKey(row, index);
        return (childrenByParent.get(rowKey) || [])
          .filter(item => item.index !== index)
          .filter(item => !stack.has(rowOccurrenceKey(item.row, item.index)));
      };
      const ownRowFootprint = (row = {}) => {
        const family = this.caseManualInferNetworkFamily(row);
        const pathLines = this.caseManualBuildVisualPathLabelLines(row?.pathLabel || row?.[CASE_MANUAL_START_COL.PATH_LABEL]);
        const pathBoxHeight = this.caseManualVisualPathBoxHeight(pathLines);
        const labelAllowance = Math.min(72, Math.max(0, Number(pathBoxHeight) || 0));
        if (family === 'btc') return Math.max(rowFootprintY + 64, rowFootprintY + labelAllowance);
        return Math.max(rowFootprintY, 150 + labelAllowance);
      };

      const subtreeHeightCache = new Map();
      const measureSubtreeHeight = (index, stack = new Set()) => {
        if (subtreeHeightCache.has(index)) return subtreeHeightCache.get(index);
        const row = sourceRows[index] || {};
        const rowKey = rowOccurrenceKey(row, index);
        const ownHeight = ownRowFootprint(row);
        if (stack.has(rowKey)) return ownHeight;
        stack.add(rowKey);
        const children = childrenForIndex(index, stack);
        const childTotal = children.reduce((total, child, childIndex) => {
          const childHeight = measureSubtreeHeight(child.index, new Set(stack));
          return total + childHeight + (childIndex > 0 ? siblingSubtreeGapY : 0);
        }, 0);
        stack.delete(rowKey);
        const height = Math.max(ownHeight, childTotal || ownHeight);
        subtreeHeightCache.set(index, height);
        return height;
      };

      const layoutByIndex = new Map();
      const placeSubtree = (index, topY = 0, depth = 0, stack = new Set()) => {
        if (layoutByIndex.has(index)) return layoutByIndex.get(index);
        const row = sourceRows[index] || {};
        const rowKey = rowOccurrenceKey(row, index);
        const height = measureSubtreeHeight(index, new Set(stack));
        if (stack.has(rowKey)) {
          const fallback = { depth, x: directionMultiplier * depth * hopSpacingX, y: topY + (height / 2), subtreeHeight: height, cycleFallback: true };
          layoutByIndex.set(index, fallback);
          return fallback;
        }
        stack.add(rowKey);
        const children = childrenForIndex(index, stack);
        let y = topY + (height / 2);
        if (children.length) {
          const childTotal = children.reduce((total, child, childIndex) => {
            const childHeight = measureSubtreeHeight(child.index, new Set(stack));
            return total + childHeight + (childIndex > 0 ? siblingSubtreeGapY : 0);
          }, 0);
          let childTop = topY + Math.max(0, (height - childTotal) / 2);
          const placedChildren = [];
          const parentIsLinearizedSwap = Boolean(this.caseManualStartExtractSwapVisualInfo(row));
          const depthStep = parentIsLinearizedSwap ? 2 : 1;
          children.forEach((child, childIndex) => {
            const childHeight = measureSubtreeHeight(child.index, new Set(stack));
            const childLayout = placeSubtree(child.index, childTop, depth + depthStep, new Set(stack));
            placedChildren.push(childLayout);
            childTop += childHeight + (childIndex < children.length - 1 ? siblingSubtreeGapY : 0);
          });
          const childYs = placedChildren.map(item => Number(item?.y)).filter(Number.isFinite);
          if (childYs.length) y = (Math.min(...childYs) + Math.max(...childYs)) / 2;
        }
        stack.delete(rowKey);
        const layout = { depth, x: directionMultiplier * depth * hopSpacingX, y, subtreeHeight: height };
        layoutByIndex.set(index, layout);
        return layout;
      };

      let cursorY = 0;
      rootItems.forEach((item, rootIndex) => {
        const height = measureSubtreeHeight(item.index, new Set());
        placeSubtree(item.index, cursorY, 0, new Set());
        cursorY += height + (rootIndex < rootItems.length - 1 ? rootSubtreeGapY : 0);
      });
      sourceRows.forEach((row, index) => {
        if (layoutByIndex.has(index)) return;
        const height = measureSubtreeHeight(index, new Set());
        placeSubtree(index, cursorY, 0, new Set());
        cursorY += height + rootSubtreeGapY;
      });

      const placedLayouts = Array.from(layoutByIndex.values()).filter(item => Number.isFinite(Number(item?.y)));
      if (placedLayouts.length) {
        const rootLayout = rootItems.length === 1 ? layoutByIndex.get(rootItems[0].index) : null;
        const shiftY = rootLayout && Number.isFinite(Number(rootLayout.y))
          ? Number(rootLayout.y)
          : (Math.min(...placedLayouts.map(item => Number(item.y))) + Math.max(...placedLayouts.map(item => Number(item.y)))) / 2;
        layoutByIndex.forEach(layout => {
          layout.y = (Number(layout.y) || 0) - shiftY;
        });
      }

      return sourceRows.map((row, index) => ({
        row,
        index,
        ...(layoutByIndex.get(index) || { depth: index, x: directionMultiplier * index * hopSpacingX, y: 0, subtreeHeight: rowFootprintY, fallbackLayout: true })
      }));
    },

    caseManualAddressLabelCategoryRank(category = '') {
      const key = this.caseManualNormalizeText(category).toLowerCase();
      if (key === 'address-poisoning') return 5;
      if (key === 'unknown-service') return 4;
      if (key === 'path-ending') return 3;
      if (key === 'warning') return 2;
      if (key === 'regular') return 1;
      return 0;
    },

    mergeCaseManualStartAddressNodeVisualState(node = {}, row = {}, address = '', pathEnding = false, roleLabel = '', attributionLabel = '') {
      if (!node || typeof node !== 'object') return;
      const visualRisk = this.caseManualStartAddressVisualRisk(row, address, pathEnding);
      const incomingRank = this.caseManualAddressLabelCategoryRank(visualRisk.labelCategory);
      const existingRank = this.caseManualAddressLabelCategoryRank(node.labelCategory);
      node.sourceCount = Number(node.sourceCount || 1) + 1;
      if (incomingRank >= existingRank) {
        const nextLabelLines = this.caseManualBuildAddressVisualLabelLinesForCandidate(roleLabel, address, attributionLabel, row, { maxAttributionLength: 28 });
        const existingLabelLines = Array.isArray(node.labelLines) ? node.labelLines.filter(Boolean) : [];
        const nextHasLabels = nextLabelLines.length > 0;
        const shouldReplaceNodeContext = incomingRank > existingRank || nextHasLabels || !Array.isArray(node.detailRows) || !node.detailRows.length;
        node.labelCategory = visualRisk.labelCategory;
        node.alertRing = visualRisk.alertRing || node.alertRing;
        if (nextHasLabels || !existingLabelLines.length) node.labelLines = nextLabelLines;
        if (shouldReplaceNodeContext) {
          node.detailRows = this.buildCaseManualStartDetailRows(row);
          node.caseCandidate = { ...row };
          node.candidate = { ...row };
        }
      }
    },

    caseManualStartTextLooksLikeSolvedBridgeReceive(value = '') {
      const text = this.caseManualNormalizeText(value);
      if (!text) return false;
      const explicitBridgeText = text
        .replace(/\bbridge\s*\/\s*swap\s*\/\s*contract[-_\s]*style[^.|\n]*/giu, ' ')
        .replace(/\bcontract[-_\s]*like\s+attribution\s+signals\b/giu, ' ');
      return /\bbridge\s*\/\s*(?:fifo|lifo|next)|\bbridge\s+solved\b|\bsolved\s+bridge\b|\bfunds\s+are\s+received\s+on\s+the\s+.+?\s+network\b|\breceived\s+on\s+the\s+.+?\s+network\s+via\b|\blikely\s+receiving\s+transaction\b|\bdestination\s*:\s*[^.|\n]+?\s+on\s+[A-Z0-9_-]+|\bdestination[-_\s]*chain\s+receive\b|\btransit\s*\/\s*swft[-_\s]*style\s+bridge\b|\btransit\s*swft\s+decoder\b|\bswft\s+decoder\b|\bdestination\s+field\b|\bswft\s+text\s+marker\b|\bmin\s*return\b|\bminreturn\b|\bto\s*token\b|\btotoken\b/iu.test(explicitBridgeText);
    },

    caseManualStartTextLooksLikeBridgeOut(value = '') {
      const text = this.caseManualNormalizeText(value);
      if (!text) return false;
      const explicitBridgeText = text
        .replace(/\bbridge\s*\/\s*swap\s*\/\s*contract[-_\s]*style[^.|\n]*/giu, ' ')
        .replace(/\bcontract[-_\s]*like\s+attribution\s+signals\b/giu, ' ');
      const registryMatch = typeof findBestCaseBridgeServiceMatchFromRegistry === 'function'
        ? findBestCaseBridgeServiceMatchFromRegistry(explicitBridgeText)
        : null;
      const fallbackMatch = registryMatch?.isMatch ? registryMatch : findBestCaseBridgeServiceMatch(explicitBridgeText);
      const serviceMatch = Boolean(fallbackMatch?.isMatch);
      return /\bfunds\s+are\s+bridged\b|\bbridge\s+solved\b|\bsolved\s+bridge\b|\bbridge\s+graft\b|\bpost[-_\s]*bridge\b|\bdestination[-_\s]*chain\b|\btransit\s*swap\b|\btransitswap\b|\bswft\b|\bwormhole\b|\ballbridge\b|\bbridgers\b|\brango\b|\bchainflip\b|\bbutterswap\b|\bnear[-_\s]*intents\b/iu.test(explicitBridgeText)
        || (serviceMatch && /\bbridge\b|\bcross[-_\s]*chain\b|\bdestination\b|\bto\s*chain\b|\bdst\s*chain\b/iu.test(explicitBridgeText));
    },

    caseManualStartExtractSwapVisualInfo(candidate = {}) {
      if (!candidate) return null;
      if (this.caseManualStartExtractBridgeVisualInfo(candidate)) return null;
      const rawRow = Array.isArray(candidate.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate.row) ? candidate.row : (Array.isArray(candidate) ? candidate : []));
      const get = (key, index) => this.caseManualNormalizeText(candidate?.[key] || rawRow?.[index] || '');
      const notesText = [
        get('systemNotes', CASE_MANUAL_START_COL.SYSTEM_NOTES),
        get('caseNotes', CASE_MANUAL_START_COL.CASE_NOTES),
        get('analystNotes', CASE_MANUAL_START_COL.ANALYST_NOTES),
        get('attribution', CASE_MANUAL_START_COL.ATTRIBUTION),
        get('fromAttributionLabel', CASE_MANUAL_START_COL.FROM_ATTRIBUTION),
        get('hopLogic', CASE_MANUAL_START_COL.HOP_LOGIC)
      ].filter(Boolean).join('\n');
      if (this.caseManualStartTextLooksLikeSolvedBridgeReceive(notesText)) return null;
      const bridgeSwapGuardRegistryMatch = typeof findBestCaseBridgeServiceMatchFromRegistry === 'function'
        ? findBestCaseBridgeServiceMatchFromRegistry(notesText)
        : null;
      const bridgeSwapGuardFallbackMatch = bridgeSwapGuardRegistryMatch?.isMatch ? bridgeSwapGuardRegistryMatch : findBestCaseBridgeServiceMatch(notesText);
      const bridgeOnlyServiceText = Boolean(bridgeSwapGuardFallbackMatch?.isMatch)
        && /\bbridge\b|cross[-_\s]*chain|destination[-_\s]*chain|to\s*chain|dst\s*chain|transit\s*swap|transitswap|swft|wormhole|allbridge|bridgers|rango|chainflip|butterswap|near\s+intents|destination\s+field|min\s*return|minreturn|to\s*token|totoken/iu.test(notesText)
        && !/\b(?:manual[-_\s]*swap|action\s*[=:]\s*swap|swap\s+solved|post[-_\s]*swap|swapped\s+from|swap\s+contract|router\s*:|received\s+total|sent\s+total)\b/iu.test(notesText);
      if (bridgeOnlyServiceText) return null;
      if (!/\bswap\b|swapped\s+from|swap\s+solved|post[-_\s]*swap/iu.test(notesText)) return null;
      const findText = (patterns = []) => {
        for (const pattern of patterns) {
          const match = notesText.match(pattern);
          if (match?.[1]) return this.caseManualNormalizeText(match[1]);
        }
        return '';
      };
      const contractAddress = findText([
        /swap\s+contract\/?router\s*:\s*(0x[a-f0-9]{40})/iu,
        /contract\s*:\s*(0x[a-f0-9]{40})/iu,
        /router\s*:\s*(0x[a-f0-9]{40})/iu,
        /path\s+ends\s+at\s+(0x[a-f0-9]{40})/iu
      ]);
      const postFundsText = findText([/post[-_\s]*swap\s+funds\s+followed\s*:\s*([0-9][0-9,]*(?:\.\d+)?\s+[A-Z0-9.$_-]+)/iu]);
      const postLeftText = findText([/post[-_\s]*swap\s+left\s+bounds\s*:\s*([0-9][0-9,]*(?:\.\d+)?\s+[A-Z0-9.$_-]+)/iu]);
      const receivedTotalText = findText([/received\s+total\s*:\s*([0-9][0-9,]*(?:\.\d+)?\s+[A-Z0-9.$_-]+)/iu]);
      const sentTotalText = findText([
        /sent\s+total\s*:\s*([0-9][0-9,]*(?:\.\d+)?\s+[A-Z0-9.$_-]+)/iu,
        /swapped\s+from\s+[A-Z0-9.$_-]+\s+to\s+[A-Z0-9.$_-]+\s+using\s+[^,.;]+,\s*([0-9][0-9,]*(?:\.\d+)?\s+[A-Z0-9.$_-]+)/iu,
        /using\s+[^,.;]+,\s*([0-9][0-9,]*(?:\.\d+)?\s+[A-Z0-9.$_-]+)\s*,?\s*contract\s*:/iu
      ]);
      const tokenFromText = (value) => {
        const match = this.caseManualNormalizeText(value).match(/\b([A-Z][A-Z0-9.$_-]{1,12})\b\s*$/u);
        return match?.[1] || '';
      };
      const amountOnly = (value) => this.caseManualNormalizeText(value).replace(/\s+[A-Z][A-Z0-9.$_-]{1,12}\s*$/u, '').replace(/,/gu, '');
      const serviceLabel = this.caseManualNormalizeText(get('attribution', CASE_MANUAL_START_COL.ATTRIBUTION)).replace(/^to:\s*/iu, '')
        || findText([/using\s+([^,.\n]+?)(?:,|\.|\n|\s+contract\s*:)/iu])
        || 'Swap';
      const continuationAddress = get('fromAddress', CASE_MANUAL_START_COL.FROM_ADDRESS) || get('toAddress', CASE_MANUAL_START_COL.TO_ADDRESS);
      const rowAmountText = get('amount', CASE_MANUAL_START_COL.AMOUNT);
      const rowTokenText = get('token', CASE_MANUAL_START_COL.TOKEN);
      const receivedToken = tokenFromText(postFundsText) || tokenFromText(receivedTotalText) || rowTokenText;
      const joinAmountToken = (amount = '', token = '') => [this.caseManualNormalizeText(amount), this.caseManualNormalizeText(token)].filter(Boolean).join(' ');
      const rowReceivedAmountText = joinAmountToken(rowAmountText, rowTokenText);
      const hasStructuredSwapEvidence = Boolean(
        contractAddress
          || postFundsText
          || postLeftText
          || receivedTotalText
          || sentTotalText
          || /swapped\s+from|swap\s+solved|post[-_\s]*swap|swap\s+contract|router\s*:/iu.test(notesText)
      );
      if (!hasStructuredSwapEvidence) return null;
      const preSwapFundsText = get('fundsFollowed', CASE_MANUAL_START_COL.FUNDS_FOLLOWED) || '';
      const preSwapLeftText = get('leftBoundsDisplay', CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY) || get('leftBoundsNumeric', CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC) || '';
      const preSwapAmountText = sentTotalText || findText([
        /the\s+funds\s+are\s+swapped\s+from\s+[A-Z0-9.$_-]+\s+to\s+[A-Z0-9.$_-]+\s+using\s+[^,.;]+,\s*([0-9][0-9,]*(?:\.\d+)?\s+[A-Z0-9.$_-]+)/iu,
        /using\s+[^,.;]+,\s*([0-9][0-9,]*(?:\.\d+)?\s+[A-Z0-9.$_-]+)\s*,?\s*contract\s*:/iu
      ]) || preSwapFundsText || '';
      const postSwapAmountText = receivedTotalText || rowReceivedAmountText || postFundsText || '';
      return {
        isSwap: true,
        serviceLabel,
        contractAddress,
        continuationAddress,
        postFundsText,
        postFundsAmount: amountOnly(postFundsText),
        postLeftText,
        postLeftAmount: amountOnly(postLeftText),
        receivedTotalText,
        receivedAmount: amountOnly(receivedTotalText || rowReceivedAmountText),
        sentTotalText,
        sentAmount: amountOnly(sentTotalText),
        sentToken: tokenFromText(sentTotalText) || tokenFromText(preSwapFundsText),
        receivedToken,
        preSwapFundsText,
        preSwapLeftText,
        preSwapLeftAmount: amountOnly(preSwapLeftText),
        preSwapAmountText,
        preSwapTransactionAmountText: preSwapAmountText,
        postSwapAmountText,
        postTransactionAmountText: postSwapAmountText,
        displayAmountText: postSwapAmountText
      };
    },

    caseManualStartExtractBridgeVisualInfo(candidate = {}, parentCandidate = null) {
      if (!candidate) return null;
      const rawRow = Array.isArray(candidate.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate.row) ? candidate.row : (Array.isArray(candidate) ? candidate : []));
      const parentRawRow = Array.isArray(parentCandidate?.rawRow)
        ? parentCandidate.rawRow
        : (Array.isArray(parentCandidate?.row) ? parentCandidate.row : (Array.isArray(parentCandidate) ? parentCandidate : []));
      const get = (source, row, key, index) => this.caseManualNormalizeText(source?.[key] || row?.[index] || '');
      const textFor = (source, row) => [
        get(source || {}, row, 'systemNotes', CASE_MANUAL_START_COL.SYSTEM_NOTES),
        get(source || {}, row, 'caseNotes', CASE_MANUAL_START_COL.CASE_NOTES),
        get(source || {}, row, 'tool88Notes', CASE_MANUAL_START_COL.CASE_NOTES),
        get(source || {}, row, 'caseBuddyNotes', CASE_MANUAL_START_COL.CASE_NOTES),
        get(source || {}, row, 'internalNotes', CASE_MANUAL_START_COL.CASE_NOTES),
        get(source || {}, row, 'analystNotes', CASE_MANUAL_START_COL.ANALYST_NOTES),
        get(source || {}, row, 'attribution', CASE_MANUAL_START_COL.ATTRIBUTION),
        get(source || {}, row, 'toAttributionLabel', CASE_MANUAL_START_COL.ATTRIBUTION),
        get(source || {}, row, 'fromAttributionLabel', CASE_MANUAL_START_COL.FROM_ATTRIBUTION),
        get(source || {}, row, 'hopLogic', CASE_MANUAL_START_COL.HOP_LOGIC),
        get(source || {}, row, 'continuesAs', CASE_MANUAL_START_COL.CONTINUES_AS),
        get(source || {}, row, 'chainName', CASE_MANUAL_START_COL.CHAIN_NAME),
        get(source || {}, row, 'chainId', CASE_MANUAL_START_COL.CHAIN_ID)
      ].filter(Boolean).join(' | ');
      const candidateText = textFor(candidate, rawRow);
      const parentText = parentCandidate ? textFor(parentCandidate, parentRawRow) : '';
      const text = [candidateText, parentText].filter(Boolean).join(' | ');
      const childHash = this.caseManualNormalizeText(candidate.hash || candidate[CASE_MANUAL_START_COL.TX_HASH] || rawRow?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const parentContinuesAs = this.caseManualNormalizeText(parentCandidate?.continuesAs || parentCandidate?.[CASE_MANUAL_START_COL.CONTINUES_AS] || parentRawRow?.[CASE_MANUAL_START_COL.CONTINUES_AS]);
      const parentNamesChild = Boolean(childHash && parentContinuesAs.toLowerCase().includes(childHash));
      const parentChain = this.caseManualNormalizeText(parentCandidate?.chainId || parentCandidate?.[CASE_MANUAL_START_COL.CHAIN_ID] || parentRawRow?.[CASE_MANUAL_START_COL.CHAIN_ID] || parentCandidate?.chainName || parentCandidate?.[CASE_MANUAL_START_COL.CHAIN_NAME] || parentRawRow?.[CASE_MANUAL_START_COL.CHAIN_NAME]).toLowerCase();
      const childChain = this.caseManualNormalizeText(candidate.chainId || candidate[CASE_MANUAL_START_COL.CHAIN_ID] || rawRow?.[CASE_MANUAL_START_COL.CHAIN_ID] || candidate.chainName || candidate[CASE_MANUAL_START_COL.CHAIN_NAME] || rawRow?.[CASE_MANUAL_START_COL.CHAIN_NAME]).toLowerCase();
      const chainChanged = Boolean(parentChain && childChain && parentChain !== childChain);
      const childHopLogicText = get(candidate, rawRow, 'hopLogic', CASE_MANUAL_START_COL.HOP_LOGIC);
      const childHopLogicLooksBridge = /\bbridge\s*\/\s*(?:fifo|lifo|next)|^\s*bridge\b|post[-_\s]*bridge|destination[-_\s]*chain\s+receive/iu.test(childHopLogicText);
      const childLooksBridgeReceive = childHopLogicLooksBridge || this.caseManualStartTextLooksLikeSolvedBridgeReceive(candidateText);
      const parentLooksBridgeOut = parentCandidate ? this.caseManualStartTextLooksLikeBridgeOut(parentText) : false;
      const childParentHashKey = this.caseManualNormalizeText(candidate.fromHash || candidate[CASE_MANUAL_START_COL.FROM_HASH] || rawRow?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
      const parentHashKeyForBridge = this.caseManualNormalizeText(parentCandidate?.hash || parentCandidate?.[CASE_MANUAL_START_COL.TX_HASH] || parentRawRow?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const parentLinkedByFromHash = Boolean(parentCandidate && childParentHashKey && parentHashKeyForBridge && this.caseManualTransactionHashesMatch(childParentHashKey, parentHashKeyForBridge));
      if (!childLooksBridgeReceive && !(parentLooksBridgeOut && chainChanged && (parentNamesChild || parentLinkedByFromHash))) return null;
      const registryMatchFor = (value) => {
        const registryMatch = typeof findBestCaseBridgeServiceMatchFromRegistry === 'function'
          ? findBestCaseBridgeServiceMatchFromRegistry(value)
          : null;
        return registryMatch?.isMatch ? registryMatch : findBestCaseBridgeServiceMatch(value);
      };
      const candidateRegistryMatch = registryMatchFor(candidateText);
      const parentRegistryMatch = registryMatchFor(parentText);
      const fallbackMatch = candidateRegistryMatch?.isMatch ? candidateRegistryMatch : (parentRegistryMatch?.isMatch ? parentRegistryMatch : registryMatchFor(text));
      const findText = (patterns = [], haystack = text) => {
        for (const pattern of patterns) {
          const match = haystack.match(pattern);
          if (match?.[1]) return this.caseManualNormalizeText(match[1]);
        }
        return '';
      };
      const viaService = findText([
        /received\s+on\s+the\s+.+?\s+network\s+via\s+([^.|\n]+)/iu,
        /bridge\s+solved\s+using\s+([^.|\n]+)/iu,
        /funds\s+are\s+bridged\s+using\s+([^.|\n]+)/iu
      ], candidateText) || findText([
        /funds\s+are\s+bridged\s+using\s+([^.|\n]+)/iu,
        /bridge\s+solved\s+using\s+([^.|\n]+)/iu
      ], parentText);
      const parentAttribution = this.caseManualNormalizeText(parentCandidate?.attribution || parentCandidate?.toAttributionLabel || parentRawRow?.[CASE_MANUAL_START_COL.ATTRIBUTION] || '');
      let serviceLabel = viaService || fallbackMatch?.canonicalName || fallbackMatch?.matchedAlias || parentAttribution || 'Bridge';
      if (/\bswft\b/iu.test(serviceLabel) || /\bswft\b/iu.test(candidateText)) serviceLabel = 'SWFT Blockchain';
      else if (/transit\s*swap|transitswap/iu.test(serviceLabel)) serviceLabel = 'TransitSwap';
      const destinationAddress = findText([/destination\s*:\s*([^.|\n]+?)\s+on\s+[A-Z0-9_-]+/iu], candidateText) || this.caseManualNormalizeText(candidate.toAddress || rawRow?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '');
      const destinationNetwork = findText([
        /destination\s*:\s*[^.|\n]+?\s+on\s+([A-Z0-9_-]+)/iu,
        /received\s+on\s+the\s+([^.|\n]+?)\s+network/iu
      ], candidateText) || this.caseManualNormalizeText(candidate.chainName || rawRow?.[CASE_MANUAL_START_COL.CHAIN_NAME] || candidate.chainId || rawRow?.[CASE_MANUAL_START_COL.CHAIN_ID] || '');
      return {
        isBridgeReceive: true,
        serviceLabel,
        bridgeLabel: serviceLabel || 'BRIDGE',
        destinationAddress,
        destinationNetwork,
        parentNamesChild,
        chainChanged,
        parentLinkedByFromHash,
        registryMatch: fallbackMatch?.isMatch ? fallbackMatch : null
      };
    },

    caseManualStartBuildSwapPreVisualCandidate(candidate = {}, swapInfo = {}) {
      const amountText = this.caseManualNormalizeText(swapInfo?.preSwapTransactionAmountText || swapInfo?.preSwapAmountText || '');
      const amount = swapInfo?.sentAmount || this.caseManualAmountFromFundsFollowed(amountText) || this.caseManualNormalizeText(candidate.amount);
      const token = swapInfo?.sentToken || this.caseManualTokenFromFundsFollowed(amountText) || this.caseManualTokenFromFundsFollowed(candidate.fundsFollowed) || candidate.token || candidate.tokenSymbol || '';
      const visualRow = this.caseManualEnsureRowWidth(
        Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []),
        CASE_MANUAL_START_COLUMN_COUNT
      ).slice();
      visualRow[CASE_MANUAL_START_COL.AMOUNT] = amount;
      visualRow[CASE_MANUAL_START_COL.TOKEN] = token;
      visualRow[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = swapInfo?.preSwapFundsText || candidate.fundsFollowed || '';
      visualRow[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = swapInfo?.preSwapLeftText || candidate.leftBoundsDisplay || '';
      visualRow[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = swapInfo?.preSwapLeftAmount || candidate.leftBoundsNumeric || '';
      return {
        ...candidate,
        rawRow: visualRow,
        row: visualRow,
        amount,
        token,
        tokenSymbol: token,
        amountText: amountText || [amount, token].filter(Boolean).join(' '),
        transactionAmount: amount,
        transactionAmountText: amountText || [amount, token].filter(Boolean).join(' '),
        fundsFollowed: swapInfo?.preSwapFundsText || candidate.fundsFollowed,
        leftBoundsDisplay: swapInfo?.preSwapLeftText || candidate.leftBoundsDisplay,
        leftBoundsNumeric: swapInfo?.preSwapLeftAmount || candidate.leftBoundsNumeric
      };
    },

    caseManualStartBuildSwapContinuationCandidate(candidate = {}, swapInfo = {}, options = {}) {
      const row = this.caseManualEnsureRowWidth(
        Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []),
        CASE_MANUAL_START_COLUMN_COUNT
      );
      const nextRow = row.slice();
      const token = swapInfo.receivedToken || this.caseManualNormalizeText(nextRow[CASE_MANUAL_START_COL.TOKEN]);
      if (swapInfo.contractAddress) nextRow[CASE_MANUAL_START_COL.FROM_ADDRESS] = swapInfo.contractAddress;
      if (swapInfo.receivedAmount) nextRow[CASE_MANUAL_START_COL.AMOUNT] = swapInfo.receivedAmount;
      if (token) nextRow[CASE_MANUAL_START_COL.TOKEN] = token;
      if (swapInfo.receivedToken && swapInfo.sentToken && swapInfo.receivedToken !== swapInfo.sentToken) {
        const outputPriorBalance = this.caseManualNormalizeText(
          swapInfo.receivedPriorBalanceBeforeTransaction
            || swapInfo.receivedTokenPriorBalanceBeforeTransaction
            || swapInfo.outputPriorBalanceBeforeTransaction
            || swapInfo.postSwapPriorBalanceBeforeTransaction
            || swapInfo.postSwapPriorBalance
            || ''
        );
        nextRow[CASE_MANUAL_START_COL.PRIOR_BALANCE] = outputPriorBalance || '0';
      }
      if (swapInfo.continuationAddress) nextRow[CASE_MANUAL_START_COL.TO_ADDRESS] = swapInfo.continuationAddress;
      nextRow[CASE_MANUAL_START_COL.ATTRIBUTION] = '';
      if (swapInfo.postFundsText) nextRow[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = swapInfo.postFundsText;
      if (swapInfo.postLeftText || swapInfo.postLeftAmount) {
        nextRow[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = swapInfo.postLeftAmount || swapInfo.postLeftText;
        nextRow[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = swapInfo.postLeftAmount || swapInfo.postLeftText;
      }
      const downstreamContinuesAs = this.caseManualNormalizeContinuesAsText(options.continuesAs || candidate?.continuesAs || candidate?.[CASE_MANUAL_START_COL.CONTINUES_AS] || '');
      const hasDownstreamChild = Boolean(options.hasDownstreamChild) && downstreamContinuesAs && !/^Path\s+ends\.?$/iu.test(downstreamContinuesAs);
      nextRow[CASE_MANUAL_START_COL.CONTINUES_AS] = hasDownstreamChild ? downstreamContinuesAs : 'Path ends.';
      return {
        ...candidate,
        rawRow: nextRow,
        row: nextRow,
        fromAddress: swapInfo.contractAddress || candidate.toAddress || candidate.fromAddress,
        toAddress: swapInfo.continuationAddress || candidate.toAddress,
        address: swapInfo.continuationAddress || candidate.toAddress,
        targetAddress: swapInfo.continuationAddress || candidate.toAddress,
        token,
        amount: swapInfo.receivedAmount || candidate.amount,
        amountText: swapInfo.postSwapAmountText || candidate.amountText,
        fundsFollowed: swapInfo.postFundsText || candidate.fundsFollowed,
        leftBoundsDisplay: swapInfo.postLeftText || swapInfo.postLeftAmount || candidate.leftBoundsDisplay,
        leftBoundsNumeric: swapInfo.postLeftAmount || candidate.leftBoundsNumeric,
        isPathEnding: !hasDownstreamChild,
        continuesAs: hasDownstreamChild ? downstreamContinuesAs : 'Path ends.',
        caseManualSwapContinuation: true,
        caseManualSwapLinearized: true,
        caseManualSwapContractAddress: swapInfo.contractAddress || '',
        caseManualSwapServiceLabel: swapInfo.serviceLabel || 'Swap',
        attribution: '',
        toAttributionLabel: '',
        fromAttributionLabel: '',
        toAttribution: null,
        fromAttribution: null,
        unknownServicePathEnding: false,
        visualEndpointStatus: hasDownstreamChild ? 'post-swap-continued' : 'post-swap-unresolved',
        caseManualSuppressAttributionLabel: true,
        caseManualStartCandidate: true
      };
    },

    buildCaseManualStartVisualizerFlowGroup(groupRows = [], index = 0) {
      const rows = (Array.isArray(groupRows) ? groupRows : []).filter(row => row?.hash);
      if (!rows.length) return null;
      if (rows.length === 1 && !this.caseManualStartExtractSwapVisualInfo(rows[0])) return this.buildCaseManualStartVisualizerFlow(rows[0], index);
      const first = rows[0];
      const reverseFlow = this.caseManualStartCandidateIsReverse(first);
      const baseMainLabel = first.pathLabel ? `Path ${first.pathLabel}` : `Manual path ${index + 1}`;
      const mainLabel = reverseFlow ? `${baseMainLabel} · Reverse trace` : baseMainLabel;
      const flowKey = `case-manual-start-flowgroup-${index}-${this.caseManualNormalizeText(first.hash).toLowerCase()}`;
      const nodes = [];
      const edges = [];
      const addressNodes = new Map();
      const addressNodeObjects = new Map();
      const rowByHash = new Map();
      rows.forEach(row => {
        const key = this.caseManualNormalizeText(row?.hash || row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (key && !rowByHash.has(key)) rowByHash.set(key, row);
      });
      const rowHashKeyFor = row => this.caseManualNormalizeText(row?.hash || row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const rowParentHashKeyFor = row => this.caseManualResolveKnownTransactionHash(row?.fromHash || row?.[CASE_MANUAL_START_COL.FROM_HASH], rows).toLowerCase();
      const rowPathLabelKeyFor = row => this.caseManualNormalizeText(row?.pathLabel || row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
      const normalizeRowFromLabel = row => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_LABEL] || row?.fromLabel || '').toLowerCase();
      const rowOccurrenceKeyFor = (row = {}, fallbackIndex = 0) => {
        const hash = rowHashKeyFor(row);
        if (!hash) return `row-${fallbackIndex}`;
        const label = rowPathLabelKeyFor(row);
        const parent = rowParentHashKeyFor(row);
        const funds = this.caseManualNormalizeText(row?.fundsFollowed || row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]).replace(/,/gu, '').toLowerCase();
        const left = this.caseManualNormalizeText(row?.leftBoundsNumeric || row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || row?.leftBoundsDisplay || row?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY]).replace(/,/gu, '').toLowerCase();
        return `${label || `idx-${fallbackIndex}`}|${hash}|${parent}|${funds}|${left}|${fallbackIndex}`;
      };
      const pathLabelDescendsFrom = (childLabel = '', parentLabel = '') => {
        const child = this.caseManualNormalizeText(childLabel).toLowerCase();
        const parent = this.caseManualNormalizeText(parentLabel).toLowerCase();
        return Boolean(child && parent && child !== parent && child.startsWith(parent));
      };
      const occurrenceAmount = (row = {}) => {
        const text = this.caseManualNormalizeText(row?.fundsFollowed || row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]).replace(/,/g, '');
        const match = text.match(/-?\d+(?:\.\d+)?/u);
        if (!match) return NaN;
        const amount = Number(match[0]);
        return Number.isFinite(amount) ? amount : NaN;
      };
      const occurrenceAmountMatches = (childRow = {}, parentRow = {}) => {
        const childAmount = occurrenceAmount(childRow);
        const parentAmount = occurrenceAmount(parentRow);
        if (!Number.isFinite(childAmount) || !Number.isFinite(parentAmount)) return false;
        const tolerance = Math.max(0.000001, Math.abs(parentAmount) * 1e-8);
        return Math.abs(childAmount - parentAmount) <= tolerance;
      };
      const rowFromAddressKeys = (row = {}) => {
        const keys = [];
        const remember = value => {
          const key = this.caseManualNormalizeAddressKey(value);
          if (key && !keys.includes(key)) keys.push(key);
        };
        if (reverseFlow) {
          remember(row?.toAddress || row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
          return keys;
        }
        remember(row?.fromAddress || row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        [row?.btcInputAddresses, row?.inputAddresses, row?.fromAddresses].forEach(source => {
          (Array.isArray(source) ? source : []).forEach(input => {
            if (Array.isArray(input)) remember(input[CASE_MANUAL_START_COL.FROM_ADDRESS]);
            else if (input && typeof input === 'object') remember(input.fromAddress || input.from_address || input.address || input.inputAddress || input.input_address);
            else remember(input);
          });
        });
        return keys;
      };
      const rowHasFromAddressKey = (row = {}, addressKey = '') => Boolean(addressKey && rowFromAddressKeys(row).includes(addressKey));
      const rowItems = rows.map((row, rowIndex) => ({
        row,
        rowIndex,
        index: rowIndex,
        hashKey: rowHashKeyFor(row),
        parentHashKey: rowParentHashKeyFor(row),
        pathLabel: rowPathLabelKeyFor(row),
        occurrenceKey: rowOccurrenceKeyFor(row, rowIndex),
        fromAddressKey: this.caseManualNormalizeAddressKey(row?.fromAddress || row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]),
        toAddressKey: this.caseManualNormalizeAddressKey(row?.toAddress || row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
        childHashes: this.caseManualExtractHashTokensFromContinuesAs(row?.continuesAs || row?.[CASE_MANUAL_START_COL.CONTINUES_AS]),
      }));
      const itemsByHash = new Map();
      rowItems.forEach(item => {
        if (!item.hashKey) return;
        if (!itemsByHash.has(item.hashKey)) itemsByHash.set(item.hashKey, []);
        itemsByHash.get(item.hashKey).push(item);
      });
      const allocationCoverage = this.caseManualBuildOccurrenceContinuationCoverage(rowItems);
      const allocationParentByChild = allocationCoverage.parentByChild || new Map();
      const resolveParentItemForRow = (row = {}, fallbackIndex = 0) => {
        const occurrenceKey = rowOccurrenceKeyFor(row, fallbackIndex);
        const allocationParent = allocationParentByChild.get(occurrenceKey);
        if (allocationParent) return allocationParent;
        const parentHashKey = rowParentHashKeyFor(row);
        if (!parentHashKey) return null;
        const candidates = (itemsByHash.get(parentHashKey) || []).filter(item => item.rowIndex !== fallbackIndex);
        if (!candidates.length) return null;
        const childLabel = rowPathLabelKeyFor(row);
        const childHashKey = rowHashKeyFor(row);
        const childFromLabel = normalizeRowFromLabel(row);
        const childFromKeys = rowFromAddressKeys(row);
        const childFamily = this.caseManualInferNetworkFamily(row);
        const parentListsChildHash = item => Boolean(childHashKey && (Array.isArray(item?.childHashes) ? item.childHashes : [])
          .some(hash => this.caseManualTransactionHashesMatch(hash, childHashKey)));
        if (candidates.length > 1) {
          const addressMatches = childFromKeys.length
            ? candidates.filter(item => rowHasFromAddressKey(row, (reverseFlow ? item.fromAddressKey : item.toAddressKey) || this.caseManualNormalizeAddressKey(reverseFlow ? (item.row?.fromAddress || item.row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]) : (item.row?.toAddress || item.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]))))
            : [];
          const listedMatches = childHashKey ? candidates.filter(parentListsChildHash) : [];
          const listedAddressMatches = listedMatches.filter(item => !childFromKeys.length || rowHasFromAddressKey(row, (reverseFlow ? item.fromAddressKey : item.toAddressKey) || this.caseManualNormalizeAddressKey(reverseFlow ? (item.row?.fromAddress || item.row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]) : (item.row?.toAddress || item.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]))));
          if (listedAddressMatches.length === 1) return listedAddressMatches[0];
          if (childFamily === 'btc' && listedMatches.length === 1 && (!addressMatches.length || addressMatches.includes(listedMatches[0]))) return listedMatches[0];
          // In BTC/UTXO flows, the input address spent by this transaction is
          // the safest visual parent when duplicate transaction hashes appear in
          // separate forensic branches. Path labels may be simple/trunk labels
          // and can be less reliable during automatic graft repair.
          if (childFamily === 'btc' && addressMatches.length === 1) return addressMatches[0];
          const fromLabelMatches = childFromLabel
            ? candidates.filter(item => this.caseManualNormalizeText(item.row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase() === childFromLabel)
            : [];
          if (fromLabelMatches.length === 1) return fromLabelMatches[0];
          const descendantMatches = candidates.filter(item => pathLabelDescendsFrom(childLabel, item.pathLabel));
          if (descendantMatches.length === 1 && !(childFamily === 'btc' && addressMatches.length > 1)) return descendantMatches[0];
          const allocationMatches = candidates.filter(item => occurrenceAmountMatches(row, item.row));
          const listedAllocationMatches = allocationMatches.filter(parentListsChildHash);
          if (listedAllocationMatches.length === 1) return listedAllocationMatches[0];
          if (allocationMatches.length === 1 && (!listedMatches.length || parentListsChildHash(allocationMatches[0]))) return allocationMatches[0];
          if (listedMatches.length === 1 && (!addressMatches.length || addressMatches.includes(listedMatches[0]))) return listedMatches[0];
          if (addressMatches.length === 1) return addressMatches[0];
        }
        const scored = candidates.map(item => {
          let score = 0;
          const exactFromLabel = Boolean(childFromLabel && this.caseManualNormalizeText(item.row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase() === childFromLabel);
          if (exactFromLabel) score += 500;
          const listedContinuation = parentListsChildHash(item);
          if (listedContinuation) score += childFamily === 'btc' ? 640 : 360;
          if (pathLabelDescendsFrom(childLabel, item.pathLabel)) score += childFamily === 'btc' ? 80 : 120;
          if (candidates.length > 1 && occurrenceAmountMatches(row, item.row)) score += 260;
          if (item.rowIndex < fallbackIndex) score += 40;
          const parentEndpoint = this.caseManualNormalizeAddressKey(reverseFlow ? (item.row?.fromAddress || item.row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]) : (item.row?.toAddress || item.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]));
          if (rowHasFromAddressKey(row, parentEndpoint)) score += childFamily === 'btc' ? (listedContinuation ? 900 : 720) : 30;
          score -= Math.abs(fallbackIndex - item.rowIndex) / 1000;
          return { item, score, exactFromLabel };
        }).sort((left, right) => right.score - left.score || (right.item.rowIndex || 0) - (left.item.rowIndex || 0));
        if (candidates.length > 1) {
          const topScore = scored[0]?.score;
          const top = scored.filter(entry => entry.score === topScore);
          return top.length === 1 && topScore > 40 ? top[0].item : null;
        }
        return scored[0]?.item || candidates[0] || null;
      };
      const toNodeByOccurrence = new Map();
      const toNodeByHash = new Map();
      const layoutRows = this.caseManualStartVisualLayoutForRows(rows);
      const layoutByHash = new Map(layoutRows.map(item => [this.caseManualNormalizeText(item?.row?.hash || item?.row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase(), item]).filter(([hash]) => hash));
      const layoutByOccurrence = new Map(layoutRows.map(item => [rowOccurrenceKeyFor(item?.row || {}, Number(item?.index) || 0), item]).filter(([key]) => key));
      const renderRows = layoutRows.slice().sort((left, right) => {
        const depthCompare = (Number(left.depth) || 0) - (Number(right.depth) || 0);
        if (depthCompare !== 0) return depthCompare;
        const yCompare = (Number(left.y) || 0) - (Number(right.y) || 0);
        if (yCompare !== 0) return yCompare;
        return (Number(left.index) || 0) - (Number(right.index) || 0);
      });
      const continuityAddressKeyFor = (address, row, side = '') => {
        const addressKey = this.caseManualNormalizeAddressKey(address);
        if (!addressKey) return '';
        const sideKey = this.caseManualNormalizeText(side).replace(/[^a-z0-9]+/giu, '-').toLowerCase();
        const rowIndex = Number.isFinite(Number(row?.caseManualStartLayoutRowIndex)) ? Number(row.caseManualStartLayoutRowIndex) : rows.findIndex(candidateRow => candidateRow === row);
        const occurrenceKey = rowOccurrenceKeyFor(row, rowIndex >= 0 ? rowIndex : 0);
        const sideIsFrom = sideKey === 'from' || sideKey.includes('from') || sideKey.includes('source') || sideKey.includes('input');
        const sideIsTo = sideKey === 'to' || sideKey.includes('to') || sideKey.includes('destination') || sideKey.includes('output');
        if (!reverseFlow) {
          if (sideIsTo && occurrenceKey) return `continuity:${occurrenceKey}:to:${addressKey}`;
          if (sideIsFrom) {
            const parentItem = resolveParentItemForRow(row, rowIndex >= 0 ? rowIndex : 0);
            const parentSwapInfo = parentItem?.row ? this.caseManualStartExtractSwapVisualInfo(parentItem.row) : null;
            const parentEndpointKey = this.caseManualNormalizeAddressKey(parentSwapInfo?.continuationAddress || parentItem?.row?.visualToAddress || parentItem?.row?.toAddress || parentItem?.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
            if (parentItem && parentEndpointKey === addressKey) {
              if (toNodeByOccurrence.has(parentItem.occurrenceKey)) return toNodeByOccurrence.get(parentItem.occurrenceKey);
              return `continuity:${parentItem.occurrenceKey}:to:${addressKey}`;
            }
          }
          return '';
        }
        if (sideIsFrom && occurrenceKey) return `continuity:${occurrenceKey}:from:${addressKey}`;
        if (sideIsTo) {
          const parentItem = resolveParentItemForRow(row, rowIndex >= 0 ? rowIndex : 0);
          const parentEndpointKey = this.caseManualNormalizeAddressKey(parentItem?.row?.fromAddress || parentItem?.row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
          if (parentItem && parentEndpointKey === addressKey) {
            if (toNodeByOccurrence.has(parentItem.occurrenceKey)) return toNodeByOccurrence.get(parentItem.occurrenceKey);
            return `continuity:${parentItem.occurrenceKey}:from:${addressKey}`;
          }
          const parentKey = this.caseManualResolveKnownTransactionHash(row?.fromHash, rows).toLowerCase();
          const duplicateParentCount = parentKey
            ? rowItems.filter(item => this.caseManualTransactionHashesMatch(item.hashKey, parentKey)).length
            : 0;
          const parent = parentKey && duplicateParentCount <= 1 ? rowByHash.get(parentKey) : null;
          const fallbackEndpointKey = this.caseManualNormalizeAddressKey(parent?.fromAddress || parent?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
          if (parent && fallbackEndpointKey === addressKey) {
            if (toNodeByHash.has(parentKey)) return toNodeByHash.get(parentKey);
            return `continuity:${parentKey}:from:${addressKey}`;
          }
        }
        return '';
      };
      const addressNodeFor = (address, roleLabel, attributionLabel, row, x, y, pathEnding = false, hopIndex = 0, side = 'address') => {
        const continuityKey = continuityAddressKeyFor(address, row, side);
        if (continuityKey && addressNodeObjects.has(continuityKey)) {
          const existingNode = addressNodeObjects.get(continuityKey);
          this.mergeCaseManualStartAddressNodeVisualState(existingNode, row, address, pathEnding, roleLabel, attributionLabel);
          return continuityKey;
        }
        const key = continuityKey
          || this.caseManualStartAddressNodeKey(address, roleLabel, row, hopIndex, side, x);
        if (addressNodes.has(key)) {
          const existingId = addressNodes.get(key);
          const existingNode = addressNodeObjects.get(existingId);
          this.mergeCaseManualStartAddressNodeVisualState(existingNode, row, address, pathEnding, roleLabel, attributionLabel);
          return existingId;
        }
        const nodeId = `${flowKey}-addr-${addressNodes.size}`;
        const visualRisk = this.caseManualStartAddressVisualRisk(row, address, pathEnding);
        const labelLines = this.caseManualBuildAddressVisualLabelLinesForCandidate(roleLabel, address, attributionLabel, row, { maxAttributionLength: 28 });
        const node = {
          id: nodeId,
          type: 'address',
          x,
          y,
          address,
          shortAddress: address ? this.caseManualShorten(address, 8, 6) : 'Address pending',
          labelLines,
          labelCategory: visualRisk.labelCategory,
          alertRing: visualRisk.alertRing,
          sourceCount: 1,
          detailRows: this.buildCaseManualStartDetailRows(row),
          caseCandidate: { ...row },
          candidate: { ...row }
        };
        nodes.push(node);
        addressNodes.set(key, nodeId);
        addressNodeObjects.set(nodeId, node);
        return nodeId;
      };
      renderRows.forEach((layout, hopIndex) => {
        const candidate = layout.row;
        let x = Number(layout.x) || 0;
        const y = Number(layout.y) || 0;
        const txNodeId = `${flowKey}-tx-${hopIndex}`;
        const detailRows = this.buildCaseManualStartDetailRows(candidate);
        const family = this.caseManualInferNetworkFamily(candidate);
        const endpointOffsetX = this.caseManualStartVisualEndpointOffsetX(family);
        const btcAmountOffsetX = this.caseManualStartVisualBtcAmountOffsetX(endpointOffsetX);
        const amountText = this.caseManualBuildVisualTransactionAmountText(candidate);
        const currentHashKey = this.caseManualNormalizeText(candidate.hash || candidate[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        const currentRowIndex = Number.isFinite(Number(layout.index)) ? Number(layout.index) : rows.findIndex(row => row === candidate);
        const currentOccurrenceKey = rowOccurrenceKeyFor(candidate, currentRowIndex >= 0 ? currentRowIndex : hopIndex);
        const parentHashKey = this.caseManualResolveKnownTransactionHash(candidate.fromHash || candidate[CASE_MANUAL_START_COL.FROM_HASH], rows).toLowerCase();
        const parentItem = resolveParentItemForRow(candidate, currentRowIndex >= 0 ? currentRowIndex : hopIndex);
        const parentOccurrenceKey = parentItem?.occurrenceKey || '';
        const parentCandidate = parentItem?.row || (parentHashKey ? rowByHash.get(parentHashKey) : null);
        const bridgeInfo = reverseFlow ? null : this.caseManualStartExtractBridgeVisualInfo(candidate, parentCandidate);
        const swapInfo = reverseFlow ? null : (bridgeInfo ? null : this.caseManualStartExtractSwapVisualInfo(candidate));
        const preSwapVisualCandidate = swapInfo ? this.caseManualStartBuildSwapPreVisualCandidate(candidate, swapInfo) : candidate;
        const visualTxAmountText = swapInfo
          ? (this.caseManualBuildVisualTransactionAmountText(preSwapVisualCandidate) || swapInfo.preSwapAmountText || amountText)
          : amountText;
        const parentSwapInfoForLayout = bridgeInfo ? null : (parentCandidate ? this.caseManualStartExtractSwapVisualInfo(parentCandidate) : null);
        if (!reverseFlow && (parentSwapInfoForLayout || bridgeInfo?.isBridgeReceive)) {
          const parentLayout = (parentOccurrenceKey ? layoutByOccurrence.get(parentOccurrenceKey) : null) || layoutByHash.get(parentHashKey);
          const parentFamily = this.caseManualInferNetworkFamily(parentCandidate);
          const parentEndpointOffsetX = this.caseManualStartVisualEndpointOffsetX(parentFamily);
          const minimumPostSpecialChildX = (Number(parentLayout?.x) || 0) + (parentEndpointOffsetX * (bridgeInfo?.isBridgeReceive ? 2 : 4));
          if (x < minimumPostSpecialChildX) x = minimumPostSpecialChildX;
        }
        const fromAddressKey = this.caseManualNormalizeAddressKey(candidate.fromAddress || candidate[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        const parentToAddressKey = this.caseManualNormalizeAddressKey(parentSwapInfoForLayout?.continuationAddress || parentCandidate?.visualToAddress || parentCandidate?.toAddress || parentCandidate?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
        const btcInputVisual = family === 'btc' ? this.caseManualLimitedBtcVisualInputAddressRows(candidate) : { rows: [], totalCount: 0, hiddenCount: 0 };
        const btcOutputVisual = family === 'btc' && reverseFlow ? this.caseManualLimitedBtcVisualOutputAddressRows(candidate) : { rows: [], totalCount: 0, hiddenCount: 0 };
        let fromNodeId = '';
        let fromNodeIds = [];
        if (family === 'btc' && btcInputVisual.totalCount > 1) {
          const visibleInputRows = btcInputVisual.rows;
          const inputSpacingY = Math.max(54, Math.min(82, 520 / Math.max(1, visibleInputRows.length)));
          visibleInputRows.forEach((input, inputIndex) => {
            const inputAddress = this.caseManualNormalizeText(input.fromAddress || input.address || '');
            if (!inputAddress) return;
            const inputAddressKey = this.caseManualNormalizeAddressKey(inputAddress);
            const inputY = y + (inputIndex - ((visibleInputRows.length - 1) / 2)) * inputSpacingY;
            let inputNodeId = '';
            const parentToNodeId = parentOccurrenceKey && toNodeByOccurrence.has(parentOccurrenceKey)
              ? toNodeByOccurrence.get(parentOccurrenceKey)
              : (parentHashKey && toNodeByHash.has(parentHashKey) ? toNodeByHash.get(parentHashKey) : '');
            const selectedReverseInputKey = this.caseManualNormalizeAddressKey(candidate.fromAddress || candidate[CASE_MANUAL_START_COL.FROM_ADDRESS]);
            const isReverseEndpointInput = Boolean(reverseFlow && candidate.isPathEnding && selectedReverseInputKey && inputAddressKey === selectedReverseInputKey);
            const inputRoleLabel = isReverseEndpointInput
              ? 'From input / unresolved source'
              : (inputIndex === 0 ? 'From input' : `From input #${inputIndex + 1}`);
            if (!reverseFlow && parentHashKey && parentCandidate && inputAddressKey && inputAddressKey === parentToAddressKey && parentToNodeId) {
              inputNodeId = parentToNodeId;
              this.mergeCaseManualStartAddressNodeVisualState(addressNodeObjects.get(inputNodeId), candidate, inputAddress, false, inputIndex === 0 ? 'From input / continued' : `From input #${inputIndex + 1}`, this.caseManualStartCandidateEndpointLabel(candidate, 'from'));
            } else {
              inputNodeId = addressNodeFor(inputAddress, inputRoleLabel, this.caseManualStartCandidateEndpointLabel(candidate, 'from'), candidate, x - endpointOffsetX, inputY, isReverseEndpointInput, hopIndex + inputIndex / 100, `from-input-${inputIndex}`);
            }
            fromNodeIds.push(inputNodeId);
          });
          if (btcInputVisual.hiddenCount > 0) {
            const moreNodeId = `${flowKey}-addr-more-inputs-${hopIndex}`;
            nodes.push({
              id: moreNodeId,
              type: 'address',
              x: x - endpointOffsetX,
              y: y + ((visibleInputRows.length || 1) / 2 + 0.75) * inputSpacingY,
              shortAddress: `+${btcInputVisual.hiddenCount} more`,
              labelLines: [`+${btcInputVisual.hiddenCount} more BTC inputs`, 'stored in rows'],
              labelCategory: 'regular',
              alertRing: false,
              sourceCount: btcInputVisual.hiddenCount,
              detailRows: [['Hidden BTC input addresses', String(btcInputVisual.hiddenCount)], ['Data', 'All input addresses are preserved in Main/Raw rows.']],
              caseCandidate: { ...candidate, btcHiddenInputAddressCount: btcInputVisual.hiddenCount },
              candidate: { ...candidate, btcHiddenInputAddressCount: btcInputVisual.hiddenCount }
            });
            fromNodeIds.push(moreNodeId);
          }
          fromNodeId = fromNodeIds[0] || '';
        }
        if (!fromNodeId) {
          const parentHashFallbackSafe = parentHashKey
            ? rowItems.filter(item => this.caseManualTransactionHashesMatch(item.hashKey, parentHashKey)).length <= 1
            : false;
          const parentToNodeId = parentOccurrenceKey && toNodeByOccurrence.has(parentOccurrenceKey)
            ? toNodeByOccurrence.get(parentOccurrenceKey)
            : (parentHashFallbackSafe && parentHashKey && toNodeByHash.has(parentHashKey) ? toNodeByHash.get(parentHashKey) : '');
          const parentSwapContinuationKey = parentCandidate
            ? this.caseManualNormalizeAddressKey(this.caseManualStartExtractSwapVisualInfo(parentCandidate)?.continuationAddress || '')
            : '';
          const continuesFromParentSwapAddress = Boolean(parentSwapContinuationKey && fromAddressKey && fromAddressKey === parentSwapContinuationKey);
          if (!reverseFlow && bridgeInfo?.isBridgeReceive && parentToNodeId) {
            fromNodeId = parentToNodeId;
          } else if (!reverseFlow && parentHashKey && parentCandidate && fromAddressKey && parentToNodeId && (fromAddressKey === parentToAddressKey || continuesFromParentSwapAddress)) {
            fromNodeId = parentToNodeId;
            const existingNode = addressNodeObjects.get(fromNodeId);
            const roleLabel = continuesFromParentSwapAddress ? 'From / post-swap continued' : (hopIndex === 0 ? 'From' : 'From / continued');
            this.mergeCaseManualStartAddressNodeVisualState(existingNode, candidate, candidate.fromAddress, false, roleLabel, this.caseManualStartCandidateEndpointLabel(candidate, 'from'));
          } else {
            fromNodeId = addressNodeFor(
              candidate.fromAddress,
              reverseFlow && candidate.isPathEnding ? 'From / unresolved source' : (hopIndex === 0 ? 'From' : 'From / continued'),
              this.caseManualStartCandidateEndpointLabel(candidate, 'from'),
              candidate,
              x - endpointOffsetX,
              y,
              reverseFlow && candidate.isPathEnding,
              hopIndex,
              'from'
            );
          }
          fromNodeIds = [fromNodeId];
        }
        const swapContractCandidate = swapInfo ? {
          ...candidate,
          toAddress: swapInfo.contractAddress || candidate.toAddress,
          caseManualSwapContractNode: true,
          isPathEnding: false
        } : candidate;
        const visualToAddress = swapInfo?.contractAddress || candidate.toAddress || this.caseManualNormalizeText(btcOutputVisual.rows[0]?.toAddress || btcOutputVisual.rows[0]?.address || '');
        const btcOutputSpacingY = btcOutputVisual.totalCount > 1
          ? Math.max(54, Math.min(82, 520 / Math.max(1, btcOutputVisual.rows.length)))
          : 0;
        const primaryToY = btcOutputVisual.totalCount > 1
          ? y - ((btcOutputVisual.rows.length - 1) / 2) * btcOutputSpacingY
          : y;
        const toNodeId = addressNodeFor(
          visualToAddress,
          swapInfo ? `${swapInfo.serviceLabel || 'Swap'} / swap` : (bridgeInfo?.isBridgeReceive ? 'To / post-bridge receive' : (!reverseFlow && candidate.isPathEnding ? 'To / unresolved ending' : (family === 'btc' && reverseFlow ? 'To output #1' : 'To'))),
          swapInfo ? (swapInfo.serviceLabel || this.caseManualStartCandidateEndpointLabel(candidate, 'to')) : this.caseManualStartCandidateEndpointLabel(candidate, 'to'),
          swapContractCandidate,
          x + endpointOffsetX,
          primaryToY,
          swapInfo ? false : (!reverseFlow && candidate.isPathEnding),
          hopIndex,
          'to'
        );
        const btcExtraOutputNodeIds = [];
        if (family === 'btc' && reverseFlow && btcOutputVisual.totalCount > 1) {
          btcOutputVisual.rows.slice(1).forEach((output, outputIndex) => {
            const absoluteIndex = outputIndex + 1;
            const outputAddress = this.caseManualNormalizeText(output.toAddress || output.address || '');
            if (!outputAddress) return;
            const outputNodeId = addressNodeFor(
              outputAddress,
              `To output #${absoluteIndex + 1}`,
              this.caseManualStartCandidateEndpointLabel(candidate, 'to'),
              { ...candidate, toAddress: outputAddress, isPathEnding: false },
              x + endpointOffsetX,
              y + (absoluteIndex - ((btcOutputVisual.rows.length - 1) / 2)) * btcOutputSpacingY,
              false,
              hopIndex + absoluteIndex / 100,
              `to-output-${absoluteIndex}`
            );
            btcExtraOutputNodeIds.push(outputNodeId);
          });
          if (btcOutputVisual.hiddenCount > 0) {
            const moreNodeId = `${flowKey}-addr-more-outputs-${hopIndex}`;
            nodes.push({
              id: moreNodeId,
              type: 'address',
              x: x + endpointOffsetX,
              y: y + ((btcOutputVisual.rows.length || 1) / 2 + 0.75) * btcOutputSpacingY,
              shortAddress: `+${btcOutputVisual.hiddenCount} more`,
              labelLines: [`+${btcOutputVisual.hiddenCount} more BTC outputs`, 'stored in rows'],
              labelCategory: 'regular',
              alertRing: false,
              sourceCount: btcOutputVisual.hiddenCount,
              detailRows: [['Hidden BTC output addresses', String(btcOutputVisual.hiddenCount)], ['Data', 'All output addresses are preserved in Main/Raw rows.']],
              caseCandidate: { ...candidate, btcHiddenOutputAddressCount: btcOutputVisual.hiddenCount, isPathEnding: false },
              candidate: { ...candidate, btcHiddenOutputAddressCount: btcOutputVisual.hiddenCount, isPathEnding: false }
            });
            btcExtraOutputNodeIds.push(moreNodeId);
          }
        }
        if (swapInfo && addressNodeObjects.has(toNodeId)) {
          const swapNode = addressNodeObjects.get(toNodeId);
          swapNode.shape = 'hexagon';
          swapNode.labelCategory = 'swap';
          swapNode.alertRing = false;
          swapNode.shortAddress = swapInfo.serviceLabel || this.caseManualShorten(visualToAddress, 8, 6) || 'Swap';
          swapNode.labelLines = [swapInfo.serviceLabel || 'Swap', visualToAddress ? this.caseManualShorten(visualToAddress, 8, 6) : 'contract/router'].filter(Boolean);
          swapNode.detailRows = [
            ['Swap service', swapInfo.serviceLabel || 'Swap'],
            ['Swap contract/router', visualToAddress || ''],
            ['Pre-swap visual amount', swapInfo.preSwapAmountText || ''],
            ['Pre-swap funds followed', candidate.fundsFollowed || ''],
            ['Post-swap transaction amount', swapInfo.postSwapAmountText || ''],
            ['Post-swap funds followed', swapInfo.postFundsText || ''],
            ['Post-swap left bounds', swapInfo.postLeftText || '']
          ].filter(([, value]) => this.caseManualNormalizeText(value));
        }
        const continuationNodeId = reverseFlow ? fromNodeId : toNodeId;
        if (currentOccurrenceKey) toNodeByOccurrence.set(currentOccurrenceKey, continuationNodeId);
        if (currentHashKey && !toNodeByHash.has(currentHashKey)) toNodeByHash.set(currentHashKey, continuationNodeId);
        const pathLabelLines = this.caseManualBuildVisualPathLabelLines(candidate.pathLabel || candidate[CASE_MANUAL_START_COL.PATH_LABEL]);
        const txDetailRows = bridgeInfo?.isBridgeReceive
          ? [
            ['Bridge visual', 'Destination-chain receive transaction'],
            ['Bridge service', bridgeInfo.bridgeLabel || bridgeInfo.serviceLabel || 'Bridge'],
            ['Destination network', bridgeInfo.destinationNetwork || candidate.chainName || candidate.chainId || ''],
            ['Destination address', bridgeInfo.destinationAddress || candidate.toAddress || ''],
            ...detailRows
          ].filter(([, value]) => this.caseManualNormalizeText(value))
          : (swapInfo
            ? [
              ['Swap visual stage', 'Pre-swap leg'],
              ['Pre-swap transaction amount', swapInfo.preSwapAmountText || ''],
              ['Pre-swap funds followed', swapInfo.preSwapFundsText || candidate.fundsFollowed || ''],
              ['Pre-swap left bounds', swapInfo.preSwapLeftText || candidate.leftBoundsDisplay || candidate.leftBoundsNumeric || ''],
              ['Received amount', swapInfo.postSwapAmountText || ''],
              ...detailRows
            ].filter(([, value]) => this.caseManualNormalizeText(value))
            : detailRows);
        nodes.push({
          id: txNodeId,
          type: 'tx',
          x,
          y,
          hash: candidate.hash || candidate[CASE_MANUAL_START_COL.TX_HASH] || '',
          shortHash: this.caseManualShorten(candidate.hash, 10, 8),
          amountText: visualTxAmountText,
          dateText: this.caseManualFormatVisualDateLabel(candidate.utcDate) || candidate.utcDate || '',
          chainText: candidate.chainName || candidate.chainId || '',
          pathLabelLines,
          pathFontSize: 18,
          pathLineHeight: 20,
          pathTextStartY: -4,
          pathBoxWidth: this.caseManualVisualPathBoxWidth(pathLabelLines),
          pathBoxHeight: this.caseManualVisualPathBoxHeight(pathLabelLines),
          badge: bridgeInfo?.isBridgeReceive ? '' : (swapInfo ? 'pre-swap' : ''),
          fundsFollowedBar: this.buildCaseManualStartFundsFollowedBar(preSwapVisualCandidate),
          detailRows: txDetailRows,
          caseCandidate: { ...preSwapVisualCandidate },
          candidate: { ...preSwapVisualCandidate }
        });
        if (family === 'btc') {
          const inputAmountNodeId = `${flowKey}-amount-${hopIndex}-input`;
          const outputAmountNodeId = `${flowKey}-amount-${hopIndex}-output`;
          const inputAmountText = this.caseManualBuildBtcVisualAmountNodeText(candidate, 'input') || 'Amount pending';
          const outputAmountText = this.caseManualBuildBtcVisualAmountNodeText(candidate, 'output') || amountText || 'Amount pending';
          nodes.push({ id: inputAmountNodeId, type: 'amount', x: x - btcAmountOffsetX, y, amountText: inputAmountText, detailRows, caseCandidate: { ...candidate, btcAmountRole: 'input' }, candidate: { ...candidate, btcAmountRole: 'input' } });
          nodes.push({ id: outputAmountNodeId, type: 'amount', x: x + btcAmountOffsetX, y, amountText: outputAmountText, detailRows, caseCandidate: { ...candidate, btcAmountRole: 'output' }, candidate: { ...candidate, btcAmountRole: 'output' } });
          fromNodeIds.forEach((sourceNodeId, sourceIndex) => {
            edges.push({ id: `${flowKey}-edge-${hopIndex}-from-input-amount-${sourceIndex}`, from: sourceNodeId, to: inputAmountNodeId, dashed: !candidate.fromAddress && sourceIndex === 0 });
          });
          edges.push({ id: `${flowKey}-edge-${hopIndex}-input-amount-tx`, from: inputAmountNodeId, to: txNodeId, dashed: false });
          edges.push({ id: `${flowKey}-edge-${hopIndex}-tx-output-amount`, from: txNodeId, to: outputAmountNodeId, dashed: false });
          [toNodeId, ...btcExtraOutputNodeIds].forEach((targetNodeId, targetIndex) => {
            edges.push({ id: `${flowKey}-edge-${hopIndex}-output-amount-to-${targetIndex}`, from: outputAmountNodeId, to: targetNodeId, dashed: !candidate.toAddress && targetIndex === 0 });
          });
        } else {
          const bridgeEdgeLabel = bridgeInfo?.isBridgeReceive ? (bridgeInfo.bridgeLabel || bridgeInfo.serviceLabel || 'BRIDGE') : '';
          const bridgeEdgeSourceNode = bridgeInfo?.isBridgeReceive ? addressNodeObjects.get(fromNodeId) : null;
          const bridgeEdgeBadge = bridgeInfo?.isBridgeReceive
            ? {
                label: bridgeEdgeLabel,
                x: bridgeEdgeSourceNode ? (((Number(bridgeEdgeSourceNode.x) || 0) + x) / 2) : undefined,
                y: bridgeEdgeSourceNode ? ((((Number(bridgeEdgeSourceNode.y) || 0) + y) / 2) - 34) : undefined,
                width: Math.min(176, Math.max(92, (bridgeEdgeLabel || 'BRIDGE').length * 8 + 34)),
                title: `Bridge continuation${bridgeEdgeLabel ? ` · ${bridgeEdgeLabel}` : ''}`
              }
            : null;
          edges.push({
            id: `${flowKey}-edge-${hopIndex}-from`,
            from: fromNodeId,
            to: txNodeId,
            dashed: bridgeInfo?.isBridgeReceive ? false : !candidate.fromAddress,
            bridge: Boolean(bridgeInfo?.isBridgeReceive),
            isBridgeEdge: Boolean(bridgeInfo?.isBridgeReceive),
            caseManualBridgeLinearized: Boolean(bridgeInfo?.isBridgeReceive),
            bridgeLabel: bridgeEdgeLabel,
            bridgeBadge: bridgeEdgeBadge
          });
          edges.push({ id: `${flowKey}-edge-${hopIndex}-to`, from: txNodeId, to: toNodeId, dashed: !visualToAddress });
          if (swapInfo?.continuationAddress) {
            const swapChildHashes = this.caseManualExtractContinuesAsHashes(candidate.continuesAs || candidate[CASE_MANUAL_START_COL.CONTINUES_AS]);
            const postSwapHasDownstreamChild = swapChildHashes.some(hash => this.caseManualResolveKnownTransactionHash(hash, rows));
            const postCandidate = this.caseManualStartBuildSwapContinuationCandidate(candidate, swapInfo, {
              hasDownstreamChild: postSwapHasDownstreamChild,
              continuesAs: candidate.continuesAs || candidate[CASE_MANUAL_START_COL.CONTINUES_AS]
            });
            const postTxNodeId = `${flowKey}-tx-${hopIndex}-swap-post`;
            const postAddressRoleLabel = postSwapHasDownstreamChild ? 'Post-swap continued address' : 'Post-swap unresolved ending';
            const postAddressNodeId = addressNodeFor(
              swapInfo.continuationAddress,
              postAddressRoleLabel,
              '',
              postCandidate,
              x + (endpointOffsetX * 3),
              y,
              !postSwapHasDownstreamChild,
              hopIndex + 0.5,
              'to'
            );
            if (addressNodeObjects.has(postAddressNodeId)) {
              const postAddressNode = addressNodeObjects.get(postAddressNodeId);
              postAddressNode.labelCategory = postSwapHasDownstreamChild ? 'regular' : 'path-ending';
              postAddressNode.alertRing = !postSwapHasDownstreamChild;
              postAddressNode.labelLines = this.caseManualBuildAddressVisualLabelLinesForCandidate(postAddressRoleLabel, swapInfo.continuationAddress, '', postCandidate, { maxAttributionLength: 28 });
              postAddressNode.detailRows = this.buildCaseManualStartDetailRows(postCandidate);
              postAddressNode.caseCandidate = { ...postCandidate };
              postAddressNode.candidate = { ...postCandidate };
            }
            const postDetailRows = [
              ['Swap visual', postSwapHasDownstreamChild ? 'Linearized post-swap continuation attached to downstream child' : 'Linearized post-swap continuation'],
              [postSwapHasDownstreamChild ? 'Post-swap continued address' : 'Post-swap unresolved address', swapInfo.continuationAddress || ''],
              ['Post-swap transaction amount', swapInfo.postSwapAmountText || ''],
              ['Post-swap funds followed', swapInfo.postFundsText || ''],
              ...this.buildCaseManualStartDetailRows(postCandidate)
            ].filter(([, value]) => this.caseManualNormalizeText(value));
            const postPathLabelLines = this.caseManualBuildVisualPathLabelLines(postCandidate.pathLabel || postCandidate[CASE_MANUAL_START_COL.PATH_LABEL]);
            nodes.push({
              id: postTxNodeId,
              type: 'tx',
              x: x + (endpointOffsetX * 2),
              y,
              hash: candidate.hash || candidate[CASE_MANUAL_START_COL.TX_HASH] || '',
              shortHash: this.caseManualShorten(candidate.hash, 10, 8),
              amountText: swapInfo.postSwapAmountText || this.caseManualBuildVisualTransactionAmountText(postCandidate),
              dateText: this.caseManualFormatVisualDateLabel(candidate.utcDate) || candidate.utcDate || '',
              chainText: [postCandidate.chainName || postCandidate.chainId || '', postCandidate.token || ''].filter(Boolean).join(' · '),
              pathLabelLines: postPathLabelLines,
              pathFontSize: 18,
              pathLineHeight: 20,
              pathTextStartY: -4,
              pathBoxWidth: this.caseManualVisualPathBoxWidth(postPathLabelLines),
              pathBoxHeight: this.caseManualVisualPathBoxHeight(postPathLabelLines),
              badge: 'post-swap',
              fundsFollowedBar: this.buildCaseManualStartFundsFollowedBar(postCandidate),
              detailRows: postDetailRows,
              caseCandidate: { ...postCandidate },
              candidate: { ...postCandidate }
            });
            edges.push({ id: `${flowKey}-edge-${hopIndex}-swap-to-post-tx`, from: toNodeId, to: postTxNodeId, dashed: false, canonical: true, caseManualSwapLinearized: true });
            edges.push({ id: `${flowKey}-edge-${hopIndex}-swap-post-tx-to-address`, from: postTxNodeId, to: postAddressNodeId, dashed: false, canonical: true, caseManualSwapLinearized: true });
            if (currentOccurrenceKey) {
              toNodeByOccurrence.set(currentOccurrenceKey, postAddressNodeId);
              const postAddressKey = this.caseManualNormalizeAddressKey(swapInfo.continuationAddress);
              if (postAddressKey) addressNodes.set(`continuity:${currentOccurrenceKey}:to:${postAddressKey}`, postAddressNodeId);
            }
            // For a solved swap, downstream rows with Column O pointing to this same
            // transaction should chain from the post-swap received-address node, not
            // from the pre-swap router/contract node. The hash-level fallback is used
            // whenever occurrence labels are simple trunk labels or otherwise cannot
            // uniquely identify the parent occurrence, so it must prefer the post-swap
            // continuity node.
            if (currentHashKey) toNodeByHash.set(currentHashKey, postAddressNodeId);
          }
        }
      });
      const minX = Math.min(...nodes.map(node => Number(node.x) || 0), 0) - 320;
      const maxX = Math.max(...nodes.map(node => Number(node.x) || 0), 0) + 320;
      const minY = Math.min(...nodes.map(node => Number(node.y) || 0), 0) - 170;
      const maxY = Math.max(...nodes.map(node => Number(node.y) || 0), 0) + 170;
      const labelNodeId = `${flowKey}-label`;
      const labelY = minY - 100;
      nodes.push({
        id: labelNodeId,
        type: 'flowLabel',
        x: (minX + maxX) / 2,
        y: labelY,
        labelText: `${mainLabel} · ${rows.length} transaction${rows.length === 1 ? '' : 's'}`,
        boxWidth: 520,
        boxHeight: 56,
        detailRows: [['Flow', mainLabel], ['Direction', reverseFlow ? 'Reverse' : 'Forward'], ['Transactions', String(rows.length)]]
      });
      const entries = rows.map(row => ({ ...row }));
      const visual = {
        id: `${flowKey}-visual`,
        key: flowKey,
        source: 'case-buddy-manual-start-visualizer',
        mainLabel,
        mainHash: first.hash,
        nodes,
        edges,
        entries,
        viewBox: { x: minX - 80, y: labelY - 110, width: Math.max(1280, maxX - minX + 160), height: Math.max(680, maxY - labelY + 250) }
      };
      return {
        key: flowKey,
        source: 'case-buddy-manual-start-visualizer',
        mainLabel,
        mainHash: first.hash,
        entries,
        candidate: entries[entries.length - 1] || entries[0],
        visual
      };
    },

    buildCaseManualStartVisualizerState(rows = []) {
      const rowGroups = this.groupCaseManualStartVisualizerRows(rows);
      const flows = rowGroups.map((groupRows, index) => this.buildCaseManualStartVisualizerFlowGroup(groupRows, index)).filter(Boolean);
      const entries = flows.flatMap(flow => Array.isArray(flow.entries) ? flow.entries : []);
      const summaryBits = [];
      if (flows.length) summaryBits.push(`${flows.length} starting transaction${flows.length === 1 ? '' : 's'} ready for manual visual investigation`);
      const hydrated = rows.filter(row => row.hydrationStatus === 'hydrated').length;
      const ambiguous = rows.filter(row => row.hydrationStatus === 'ambiguous').length;
      const failed = rows.filter(row => row.hydrationStatus === 'failed').length;
      if (hydrated) summaryBits.push(`${hydrated} looked up for basic details`);
      if (ambiguous) summaryBits.push(`${ambiguous} need target review`);
      if (failed) summaryBits.push(`${failed} provider lookup issue${failed === 1 ? '' : 's'}`);
      return {
        source: 'case-buddy-manual-start-visualizer',
        revision: this.caseSupportState?.caseDataRevision || 0,
        builtAtUtc: new Date().toISOString(),
        flows,
        entries,
        flowCount: flows.length,
        qualifyingFlowCount: flows.length,
        preparedSaplingCount: 0,
        entryCount: entries.length,
        addressLabelCount: 0,
        canBuild: flows.length > 0,
        summary: summaryBits.length ? `${summaryBits.join(' · ')}.` : 'Load at least one starting transaction hash in Main Data to open the manual visualizer.'
      };
    },

    buildCaseManualStartVisualizerFlow(candidate = {}, index = 0) {
      if (!candidate?.hash) return null;
      const reverseFlow = this.caseManualStartCandidateIsReverse(candidate);
      const baseMainLabel = candidate.pathLabel ? `Path ${candidate.pathLabel}` : `Starting transaction ${index + 1}`;
      const mainLabel = reverseFlow ? `${baseMainLabel} · Reverse trace` : baseMainLabel;
      const flowKey = `case-manual-start-flow-${index}-${candidate.hash.toLowerCase()}`;
      const fromLabel = candidate.fromAddress ? this.caseManualShorten(candidate.fromAddress, 8, 6) : 'From address pending';
      const toLabel = candidate.toAddress ? this.caseManualShorten(candidate.toAddress, 8, 6) : 'To address pending';
      const amountText = this.caseManualBuildVisualTransactionAmountText(candidate);
      const txNodeId = `${flowKey}-tx`;
      const fromNodeId = `${flowKey}-from`;
      const toNodeId = `${flowKey}-to`;
      const labelNodeId = `${flowKey}-label`;
      const family = this.caseManualInferNetworkFamily(candidate);
      const showStandaloneAmountNode = family === 'btc';
      const endpointOffsetX = this.caseManualStartVisualEndpointOffsetX(family);
      const btcAmountOffsetX = this.caseManualStartVisualBtcAmountOffsetX(endpointOffsetX);
      const fromVisualRisk = this.caseManualStartAddressVisualRisk(candidate, candidate.fromAddress, reverseFlow && candidate.isPathEnding);
      const toVisualRisk = this.caseManualStartAddressVisualRisk(candidate, candidate.toAddress, !reverseFlow && candidate.isPathEnding);
      const candidateForNode = { ...candidate };
      const detailRows = this.buildCaseManualStartDetailRows(candidate);
      const pathLabelLines = this.caseManualBuildVisualPathLabelLines(candidate.pathLabel || candidate[CASE_MANUAL_START_COL.PATH_LABEL]);
      const nodes = [
        {
          id: labelNodeId,
          type: 'flowLabel',
          x: 0,
          y: -185,
          labelText: mainLabel,
          boxWidth: 440,
          boxHeight: 56,
          detailRows: [['Flow', mainLabel], ['Transaction', candidate.hash || '']]
        },
        {
          id: fromNodeId,
          type: 'address',
          x: -endpointOffsetX,
          y: 0,
          address: candidate.fromAddress || '',
          shortAddress: fromLabel,
          labelLines: candidate.fromAddress
            ? this.caseManualBuildAddressVisualLabelLinesForCandidate(reverseFlow && candidate.isPathEnding ? 'From / unresolved source' : 'From', candidate.fromAddress, this.caseManualStartCandidateEndpointLabel(candidate, 'from'), candidate, { maxAttributionLength: 28 })
            : ['From pending'],
          labelCategory: fromVisualRisk.labelCategory,
          alertRing: fromVisualRisk.alertRing,
          sourceCount: 1,
          detailRows,
          caseCandidate: candidateForNode,
          candidate: candidateForNode
        },
        {
          id: txNodeId,
          type: 'tx',
          x: 0,
          y: 0,
          hash: candidate.hash || '',
          shortHash: this.caseManualShorten(candidate.hash, 10, 8),
          amountText,
          dateText: this.caseManualFormatVisualDateLabel(candidate.utcDate) || candidate.utcDate || '',
          chainText: candidate.chainName || candidate.chainId || '',
          pathLabelLines,
          pathFontSize: 18,
          pathLineHeight: 20,
          pathTextStartY: -4,
          pathBoxWidth: this.caseManualVisualPathBoxWidth(pathLabelLines),
          pathBoxHeight: this.caseManualVisualPathBoxHeight(pathLabelLines),
          badge: '',
          fundsFollowedBar: this.buildCaseManualStartFundsFollowedBar(candidate),
          detailRows,
          caseCandidate: candidateForNode,
          candidate: candidateForNode
        },
        {
          id: toNodeId,
          type: 'address',
          x: endpointOffsetX,
          y: 0,
          address: candidate.toAddress || '',
          shortAddress: toLabel,
          labelLines: candidate.toAddress
            ? (() => {
                const lines = this.caseManualBuildAddressVisualLabelLinesForCandidate(!reverseFlow && candidate.isPathEnding ? 'To / unresolved ending' : 'To', candidate.toAddress, this.caseManualStartCandidateEndpointLabel(candidate, 'to'), candidate, { maxAttributionLength: 28 });
                return lines;
              })()
            : ['To pending'],
          labelCategory: toVisualRisk.labelCategory,
          alertRing: toVisualRisk.alertRing,
          sourceCount: 1,
          detailRows,
          caseCandidate: candidateForNode,
          candidate: candidateForNode
        }
      ];
      const edges = [];
      const btcExtraInputNodeIds = [];
      const btcExtraOutputNodeIds = [];
      if (showStandaloneAmountNode) {
        const btcInputVisual = this.caseManualLimitedBtcVisualInputAddressRows(candidate);
        if (btcInputVisual.totalCount > 1) {
          const visibleInputRows = btcInputVisual.rows;
          const inputSpacingY = Math.max(54, Math.min(82, 520 / Math.max(1, visibleInputRows.length)));
          visibleInputRows.slice(1).forEach((input, inputIndex) => {
            const absoluteIndex = inputIndex + 1;
            const inputAddress = this.caseManualNormalizeText(input.fromAddress || input.address || '');
            if (!inputAddress) return;
            const inputNodeId = `${flowKey}-from-input-${absoluteIndex}`;
            nodes.push({
              id: inputNodeId,
              type: 'address',
              x: -endpointOffsetX,
              y: (absoluteIndex - ((visibleInputRows.length - 1) / 2)) * inputSpacingY,
              shortAddress: this.caseManualShorten(inputAddress, 8, 6),
              labelLines: this.caseManualBuildAddressVisualLabelLinesForCandidate(`From input #${absoluteIndex + 1}`, inputAddress, this.caseManualStartCandidateEndpointLabel(candidate, 'from'), candidate, { maxAttributionLength: 28 }),
              labelCategory: 'regular',
              alertRing: false,
              sourceCount: 1,
              detailRows,
              caseCandidate: { ...candidateForNode, fromAddress: inputAddress },
              candidate: { ...candidateForNode, fromAddress: inputAddress }
            });
            btcExtraInputNodeIds.push(inputNodeId);
          });
          if (btcInputVisual.hiddenCount > 0) {
            const moreNodeId = `${flowKey}-from-input-more`;
            nodes.push({
              id: moreNodeId,
              type: 'address',
              x: -endpointOffsetX,
              y: ((visibleInputRows.length || 1) / 2 + 0.75) * inputSpacingY,
              shortAddress: `+${btcInputVisual.hiddenCount} more`,
              labelLines: [`+${btcInputVisual.hiddenCount} more BTC inputs`, 'stored in rows'],
              labelCategory: 'regular',
              alertRing: false,
              sourceCount: btcInputVisual.hiddenCount,
              detailRows: [['Hidden BTC input addresses', String(btcInputVisual.hiddenCount)], ['Data', 'All input addresses are preserved in Main/Raw rows.']],
              caseCandidate: { ...candidateForNode, btcHiddenInputAddressCount: btcInputVisual.hiddenCount },
              candidate: { ...candidateForNode, btcHiddenInputAddressCount: btcInputVisual.hiddenCount }
            });
            btcExtraInputNodeIds.push(moreNodeId);
          }
        }
      }
      if (showStandaloneAmountNode && reverseFlow) {
        const btcOutputVisual = this.caseManualLimitedBtcVisualOutputAddressRows(candidate);
        if (btcOutputVisual.totalCount > 1) {
          const visibleOutputRows = btcOutputVisual.rows;
          const outputSpacingY = Math.max(54, Math.min(82, 520 / Math.max(1, visibleOutputRows.length)));
          const firstToNode = nodes.find(node => node.id === toNodeId);
          if (firstToNode) firstToNode.y = -((visibleOutputRows.length - 1) / 2) * outputSpacingY;
          visibleOutputRows.slice(1).forEach((output, outputIndex) => {
            const absoluteIndex = outputIndex + 1;
            const outputAddress = this.caseManualNormalizeText(output.toAddress || output.address || '');
            if (!outputAddress) return;
            const outputNodeId = `${flowKey}-to-output-${absoluteIndex}`;
            nodes.push({
              id: outputNodeId,
              type: 'address',
              x: endpointOffsetX,
              y: (absoluteIndex - ((visibleOutputRows.length - 1) / 2)) * outputSpacingY,
              shortAddress: this.caseManualShorten(outputAddress, 8, 6),
              labelLines: this.caseManualBuildAddressVisualLabelLinesForCandidate(`To output #${absoluteIndex + 1}`, outputAddress, this.caseManualStartCandidateEndpointLabel(candidate, 'to'), candidate, { maxAttributionLength: 28 }),
              labelCategory: 'regular',
              alertRing: false,
              sourceCount: 1,
              detailRows: [['BTC transaction output', `#${absoluteIndex + 1}`], ['Output address', outputAddress], ...detailRows],
              caseCandidate: { ...candidateForNode, toAddress: outputAddress, isPathEnding: false },
              candidate: { ...candidateForNode, toAddress: outputAddress, isPathEnding: false }
            });
            btcExtraOutputNodeIds.push(outputNodeId);
          });
          if (btcOutputVisual.hiddenCount > 0) {
            const moreNodeId = `${flowKey}-to-output-more`;
            nodes.push({
              id: moreNodeId,
              type: 'address',
              x: endpointOffsetX,
              y: ((visibleOutputRows.length || 1) / 2 + 0.75) * outputSpacingY,
              shortAddress: `+${btcOutputVisual.hiddenCount} more`,
              labelLines: [`+${btcOutputVisual.hiddenCount} more BTC outputs`, 'stored in rows'],
              labelCategory: 'regular',
              alertRing: false,
              sourceCount: btcOutputVisual.hiddenCount,
              detailRows: [['Hidden BTC output addresses', String(btcOutputVisual.hiddenCount)], ['Data', 'All output addresses are preserved in Main/Raw rows.']],
              caseCandidate: { ...candidateForNode, btcHiddenOutputAddressCount: btcOutputVisual.hiddenCount, isPathEnding: false },
              candidate: { ...candidateForNode, btcHiddenOutputAddressCount: btcOutputVisual.hiddenCount, isPathEnding: false }
            });
            btcExtraOutputNodeIds.push(moreNodeId);
          }
        }
      }
      if (showStandaloneAmountNode) {
        const inputAmountNodeId = `${flowKey}-amount-input`;
        const outputAmountNodeId = `${flowKey}-amount-output`;
        const inputAmountText = this.caseManualBuildBtcVisualAmountNodeText(candidate, 'input') || 'Amount pending';
        const outputAmountText = this.caseManualBuildBtcVisualAmountNodeText(candidate, 'output') || amountText || 'Amount pending';
        nodes.push({
          id: inputAmountNodeId,
          type: 'amount',
          x: -btcAmountOffsetX,
          y: 0,
          amountText: inputAmountText,
          detailRows,
          caseCandidate: { ...candidateForNode, btcAmountRole: 'input' },
          candidate: { ...candidateForNode, btcAmountRole: 'input' }
        });
        nodes.push({
          id: outputAmountNodeId,
          type: 'amount',
          x: btcAmountOffsetX,
          y: 0,
          amountText: outputAmountText,
          detailRows,
          caseCandidate: { ...candidateForNode, btcAmountRole: 'output' },
          candidate: { ...candidateForNode, btcAmountRole: 'output' }
        });
        [fromNodeId, ...btcExtraInputNodeIds].forEach((sourceNodeId, sourceIndex) => {
          edges.push({ id: `${flowKey}-edge-from-input-amount-${sourceIndex}`, from: sourceNodeId, to: inputAmountNodeId, dashed: !candidate.fromAddress && sourceIndex === 0 });
        });
        edges.push({ id: `${flowKey}-edge-input-amount-tx`, from: inputAmountNodeId, to: txNodeId, dashed: false });
        edges.push({ id: `${flowKey}-edge-tx-output-amount`, from: txNodeId, to: outputAmountNodeId, dashed: false });
        [toNodeId, ...btcExtraOutputNodeIds].forEach((targetNodeId, targetIndex) => {
          edges.push({ id: `${flowKey}-edge-output-amount-to-${targetIndex}`, from: outputAmountNodeId, to: targetNodeId, dashed: !candidate.toAddress && targetIndex === 0 });
        });
      } else {
        edges.push({ id: `${flowKey}-edge-from-tx`, from: fromNodeId, to: txNodeId, dashed: !candidate.fromAddress });
        edges.push({ id: `${flowKey}-edge-tx-to`, from: txNodeId, to: toNodeId, dashed: !candidate.toAddress });
      }
      const minNodeX = Math.min(...nodes.map(node => Number(node.x) || 0), -endpointOffsetX) - 260;
      const maxNodeX = Math.max(...nodes.map(node => Number(node.x) || 0), endpointOffsetX) + 260;
      const minNodeY = Math.min(...nodes.map(node => Number(node.y) || 0), -185) - 140;
      const maxNodeY = Math.max(...nodes.map(node => Number(node.y) || 0), 0) + 160;
      const visual = {
        id: `${flowKey}-visual`,
        key: flowKey,
        source: 'case-buddy-manual-start-visualizer',
        mainLabel,
        mainHash: candidate.hash,
        nodes,
        edges,
        entries: [candidateForNode],
        viewBox: { x: minNodeX, y: minNodeY, width: Math.max(1120, maxNodeX - minNodeX), height: Math.max(560, maxNodeY - minNodeY) }
      };
      return {
        key: flowKey,
        source: 'case-buddy-manual-start-visualizer',
        mainLabel,
        mainHash: candidate.hash,
        entries: [candidateForNode],
        candidate: candidateForNode,
        visual
      };
    },

    caseManualSplitVisualLabel(value, maxLen = 18, maxLines = 3) {
      const textValue = this.caseManualNormalizeText(value);
      if (!textValue) return [''];
      const words = textValue.split(/\s+/u);
      const lines = [];
      let current = '';
      words.forEach(word => {
        if (!current) current = word;
        else if (`${current} ${word}`.length <= maxLen) current = `${current} ${word}`;
        else {
          lines.push(current);
          current = word;
        }
      });
      if (current) lines.push(current);
      return lines.slice(0, maxLines);
    },

    combineCaseManualStartVisuals(flows = [], mode = 'allTogether') {
      const spacingY = mode === 'stacked' ? 520 : 300;
      const spacingX = mode === 'stacked' ? 0 : 0;
      const nodes = [];
      const edges = [];
      flows.forEach((flow, index) => {
        const visual = flow.visual || {};
        const offsetY = index * spacingY;
        const offsetX = index * spacingX;
        (visual.nodes || []).forEach(node => nodes.push({ ...node, id: `${node.id}-combined-${index}`, originalId: node.id, x: (Number(node.x) || 0) + offsetX, y: (Number(node.y) || 0) + offsetY }));
        (visual.edges || []).forEach(edge => edges.push({ ...edge, id: `${edge.id}-combined-${index}`, from: `${edge.from}-combined-${index}`, to: `${edge.to}-combined-${index}` }));
      });
      return {
        id: `case-manual-start-${mode}-visual`,
        key: `case-manual-start-${mode}`,
        source: 'case-buddy-manual-start-visualizer',
        mainLabel: mode === 'stacked' ? 'All starting transactions · stacked' : 'All starting transactions',
        mainHash: flows.map(flow => flow.mainHash).filter(Boolean).join(', '),
        nodes,
        edges,
        entries: flows.flatMap(flow => flow.entries || []),
        viewBox: { x: -580, y: -300, width: 1160, height: Math.max(620, (flows.length || 1) * spacingY + 260) }
      };
    },

    setCaseManualStartCurrentVisual(options = {}) {
      const state = this.caseInvestigationVisualizerState;
      if (!state || state.source !== 'case-buddy-manual-start-visualizer') return false;
      const flows = Array.isArray(state.flows) ? state.flows : [];
      const previousManualUnresolvedCount = this.getCaseManualStartViewportUnresolvedEndingCount?.() || 0;
      if (!flows.length) {
        this.caseInvestigationVisualizerCurrentVisual = null;
        return false;
      }
      const mode = options.mode || this.caseInvestigationVisualizerViewMode || 'single';
      if (mode === 'allTogether' || mode === 'stacked') {
        this.caseInvestigationVisualizerCurrentVisual = this.combineCaseManualStartVisuals(flows, mode);
      } else {
        const index = Math.max(0, Math.min(flows.length - 1, Number(this.caseInvestigationVisualizerActiveIndex) || 0));
        this.caseInvestigationVisualizerActiveIndex = index;
        this.caseInvestigationVisualizerCurrentVisual = flows[index]?.visual || null;
      }
      this.disposeCaseInvestigationVisualizerCameraRuntime?.();
      this.caseInvestigationVisualizerViewport = { ...(this.caseInvestigationVisualizerCurrentVisual?.viewBox || { x: -560, y: -280, width: 1120, height: 560 }) };
      this.$nextTick(() => this.applyCaseInvestigationVisualizerViewportToDom?.(this.caseInvestigationVisualizerViewport));
      this.caseInvestigationVisualizerSelectedNodeId = '';
      this.caseInvestigationVisualizerSelectedNodeIds = [];
      this.caseInvestigationVisualizerSelectedCandidate = null;
      this.caseInvestigationVisualizerHiddenNodeIds = [];
      this.caseInvestigationVisualizerUndoStack = [];
      this.caseInvestigationVisualizerRedoStack = [];
      const selectedCandidate = this.selectCaseManualStartPathEndingNode(this.caseInvestigationVisualizerCurrentFlow?.candidate || null);
      if (this.caseManualStartHopInspectorOpen) {
        this.resetCaseManualStartHopInspectorDecisionStateForCandidate(selectedCandidate, { force: false });
        this.queueCaseManualStartHopInspectorAutoCheck(selectedCandidate);
      }
      this.pruneCaseInvestigationVisualizerSelectionForTransactionOnlyMode();
      if (this.caseManualSolveGetDataOpen) this.refreshCaseManualSolveGetDataPanel();
      if (this.caseInvestigationVisualizerManualStartMode && previousManualUnresolvedCount > 0 && this.getCaseManualStartViewportUnresolvedEndingCount() === 0) {
        this.queueCaseManualStartViewportCompletionCheck('manual visual refreshed and all endings were resolved');
      }
      return true;
    },

    async openCaseManualStartVisualizerFromWorkflow() {
      await this.openCaseManualStartVisualizer({ source: 'workflow' });
    },

    async openCaseManualStartVisualizerFromRawData() {
      await this.openCaseManualStartVisualizer({ source: 'raw-data-modal' });
    },

    async switchCaseInvestigationVisualizerToManualStartMode() {
      this.caseInvestigationVisualizerStatus = 'Switching to manual visual hop-solving mode…';
      await this.openCaseManualStartVisualizer({ source: 'visualizer-toolbar' });
    },

    async switchCaseInvestigationVisualizerToStandardMode() {
      this.abortCaseManualStartAllFlowsAuto?.('Guided Flow Solver stopped because Manual Solve mode closed.', { silent: true });
      this.showCaseManualSolveBackdropCloseConfirm = false;
      this.caseManualSolveIntroOpen = false;
      this.caseManualSolveIntroDontShowAgain = false;
      this.clearCaseManualStartViewportAutomationCallouts?.();
      this.caseManualSolveHeatMapEnabled = false;
      this.closeCaseManualSolveGetDataPanel?.();
      this.caseManualSolveEvaluationOpen = false;
      this.closeCaseManualStartHopInspector?.();
      this.caseManualStartHopInspectorMounted = false;
      this.caseManualStartHopInspectorOpen = false;
      this.caseManualStartHopInspectorPreviewOpen = false;
      this.caseManualStartHopInspectorFullToolOpen = false;
      this.caseInvestigationVisualizerStatus = 'Switching to the standard partial-case visualizer…';
      this.caseInvestigationVisualizerTransactionOnlyMode = false;
      const result = this.callFirstCaseBuddyOriginalMethod('openCaseInvestigationVisualizer', [{ source: 'manual-mode-toggle' }]);
      if (result && typeof result.then === 'function') await result;
      return result;
    },

    applyCaseManualStartVisualizerOpeningScrollLock() {
      if (typeof document === 'undefined' || !document.body) return;
      if (this.caseManualStartVisualizerOpeningBodyOverflow === null) {
        this.caseManualStartVisualizerOpeningBodyOverflow = document.body.style.overflow || '';
      }
      document.body.style.overflow = 'hidden';
    },

    releaseCaseManualStartVisualizerOpeningScrollLock() {
      if (typeof document === 'undefined' || !document.body) {
        this.caseManualStartVisualizerOpeningBodyOverflow = null;
        return;
      }
      if (this.caseManualStartVisualizerOpeningBodyOverflow !== null) {
        document.body.style.overflow = this.caseManualStartVisualizerOpeningBodyOverflow;
      }
      this.caseManualStartVisualizerOpeningBodyOverflow = null;
    },

    clearCaseManualStartVisualizerOpeningTimer() {
      const timer = this.caseManualStartVisualizerOpening?.overallTimer;
      if (timer) {
        if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timer);
        else clearTimeout(timer);
      }
      if (this.caseManualStartVisualizerOpening) this.caseManualStartVisualizerOpening.overallTimer = null;
    },

    updateCaseManualStartVisualizerOpening(patch = {}) {
      if (!this.caseManualStartVisualizerOpening?.active && patch.active !== true) return;
      this.caseManualStartVisualizerOpening = {
        ...(this.caseManualStartVisualizerOpening || createCaseManualStartVisualizerOpeningState()),
        ...patch
      };
    },

    beginCaseManualStartVisualizerOpening(options = {}) {
      this.clearCaseManualStartVisualizerOpeningTimer();
      const runId = (Number(this.caseManualStartVisualizerOpening?.runId) || 0) + 1;
      const pendingRootCount = Math.max(0, Number(options.pendingRootCount) || 0);
      const state = createCaseManualStartVisualizerOpeningState({
        active: true,
        runId,
        phase: 'scan',
        progressPercent: 8,
        source: this.caseManualNormalizeText(options.source),
        message: 'Reading the current case and finding its starting graphs…',
        detail: pendingRootCount > 0
          ? 'Only essential gaps on starting transactions are looked up. Provider requests may queue briefly to protect shared rate limits; completed case work is preserved.'
          : 'Current Main Data is already sufficient. Case Buddy will build or reuse the visual without a startup provider lookup.',
        rootCount: Math.max(0, Number(options.rootCount) || 0),
        pendingRootCount,
        startedAtMs: Date.now()
      });
      if (pendingRootCount > 0) {
        const timeoutHandler = () => {
          if (!this.caseManualStartVisualizerOpening?.active || Number(this.caseManualStartVisualizerOpening.runId) !== runId) return;
          this.updateCaseManualStartVisualizerOpening({
            timedOut: true,
            message: 'The startup lookup is taking too long. Opening from the case data already available…',
            detail: 'No completed case work is discarded. Next or Auto can fill any remaining sparse details inside Flow Solver.'
          });
          this.abortCaseManualStartVisualizerOpening('open-current', { timedOut: true, silent: true });
        };
        state.overallTimer = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
          ? window.setTimeout(timeoutHandler, CASE_MANUAL_START_OPENING_TIMEOUT_MS)
          : setTimeout(timeoutHandler, CASE_MANUAL_START_OPENING_TIMEOUT_MS);
      }
      this.caseManualStartVisualizerOpening = state;
      this.applyCaseManualStartVisualizerOpeningScrollLock();
      this.$nextTick(() => this.focusCaseManualStartVisualizerOpeningAction(0));
      return runId;
    },

    focusCaseManualStartVisualizerOpeningAction(direction = 0) {
      this.$nextTick(() => {
        if (!direction) {
          this.$refs.caseManualStartVisualizerOpeningCard?.focus?.();
          return;
        }
        const openButton = this.$refs.caseManualStartVisualizerOpeningOpenButton;
        const cancelButton = this.$refs.caseManualStartVisualizerOpeningCancelButton;
        const buttons = [openButton, cancelButton].filter(button => button && !button.disabled);
        if (!buttons.length) return;
        const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
        const activeIndex = buttons.findIndex(button => button === activeElement);
        const nextIndex = activeIndex < 0
          ? (direction < 0 ? buttons.length - 1 : 0)
          : (activeIndex + direction + buttons.length) % buttons.length;
        buttons[nextIndex]?.focus?.();
      });
    },

    abortCaseManualStartVisualizerOpening(exitMode = 'cancel', options = {}) {
      if (!this.caseManualStartVisualizerOpening?.active) return false;
      const normalizedMode = exitMode === 'open-current' ? 'open-current' : 'cancel';
      this.updateCaseManualStartVisualizerOpening({
        stopRequested: true,
        exitMode: normalizedMode,
        timedOut: options.timedOut === true || this.caseManualStartVisualizerOpening.timedOut === true,
        message: normalizedMode === 'open-current'
          ? 'Opening from the case data already available…'
          : 'Cancelling Manual Solve startup…'
      });
      const controller = this.caseManualStartVisualizerOpeningAbortController;
      if (controller && !controller.signal?.aborted) {
        try { controller.abort(); } catch (error) { console.warn('Could not abort Manual Solve startup request.', error); }
      }
      return true;
    },

    requestCaseManualStartVisualizerOpeningOpenNow() {
      return this.abortCaseManualStartVisualizerOpening('open-current');
    },

    requestCaseManualStartVisualizerOpeningCancel() {
      return this.abortCaseManualStartVisualizerOpening('cancel');
    },

    finishCaseManualStartVisualizerOpening(options = {}) {
      const previous = this.caseManualStartVisualizerOpening || {};
      this.clearCaseManualStartVisualizerOpeningTimer();
      this.caseManualStartVisualizerOpeningAbortController = null;
      this.releaseCaseManualStartVisualizerOpeningScrollLock();
      this.caseManualStartVisualizerOpeningLastSummary = {
        result: options.result || previous.exitMode || 'finished',
        rootCount: Number(previous.rootCount) || 0,
        pendingRootCount: Number(previous.pendingRootCount) || 0,
        hydratedCount: Number(previous.hydratedCount) || 0,
        reusedCount: Number(previous.reusedCount) || 0,
        timedOut: previous.timedOut === true,
        startedAtMs: Number(previous.startedAtMs) || 0,
        finishedAtUtc: new Date().toISOString()
      };
      this.caseManualStartVisualizerOpening = createCaseManualStartVisualizerOpeningState({
        runId: Number(previous.runId) || 0,
        finishedAtUtc: new Date().toISOString()
      });
    },

    caseManualStartVisualizerOpeningPendingRoots(rows = []) {
      return this.caseManualStartOpeningRootRows(rows).filter(candidate => (
        this.caseManualStartOpeningCandidateNeedsRemoteWork(candidate)
          && !this.caseManualStartOpeningHydrationRecordReusable(candidate)
      ));
    },

    caseManualStartVisualizerPreparedKey(rows = []) {
      const fileKey = this.caseManualNormalizeText(this.currentProjectFileId || this.selectedProjectFileId || 'unsaved-case').toLowerCase();
      const revision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const identities = (Array.isArray(rows) ? rows : []).map((candidate, index) => {
        const rowIndex = Number.isInteger(candidate?.rowIndex) ? candidate.rowIndex : index;
        const path = this.caseManualNormalizeText(candidate?.pathLabel).toLowerCase();
        const hash = this.caseManualNormalizeText(candidate?.hash || candidate?.transactionHash || candidate?.txHash).toLowerCase();
        return `${rowIndex}:${path}:${hash}`;
      });
      return `${fileKey}|${revision}|${identities.length}|${identities.join('|')}`;
    },

    caseManualStartVisualizerReusablePreparedState(rows = []) {
      const cacheKey = this.caseManualStartVisualizerPreparedKey(rows);
      if (this.caseManualStartVisualizerPreparedCacheKey === cacheKey && this.caseManualStartVisualizerPreparedState?.source === 'case-buddy-manual-start-visualizer') {
        return this.caseManualStartVisualizerPreparedState;
      }
      const current = this.caseInvestigationVisualizerState;
      if (current?.source === 'case-buddy-manual-start-visualizer' && current.caseManualPreparedCacheKey === cacheKey) {
        this.caseManualStartVisualizerPreparedCacheKey = cacheKey;
        this.caseManualStartVisualizerPreparedState = current;
        return current;
      }
      return null;
    },

    cacheCaseManualStartVisualizerPreparedState(rows = [], state = null) {
      if (!state || state.source !== 'case-buddy-manual-start-visualizer') return null;
      const cacheKey = this.caseManualStartVisualizerPreparedKey(rows);
      state.caseManualPreparedCacheKey = cacheKey;
      this.caseManualStartVisualizerPreparedCacheKey = cacheKey;
      this.caseManualStartVisualizerPreparedState = state;
      return state;
    },

    findCaseManualStartVisualizerFlowIndexForRoot(state = {}, focusRoot = null) {
      const flows = Array.isArray(state?.flows) ? state.flows : [];
      if (!flows.length || !focusRoot || typeof focusRoot !== 'object') return 0;

      const targetHashKeys = new Set(this.caseFileBrowserHashLookupKeys(focusRoot.hash));
      const targetPath = normalizeCell(focusRoot.pathLabel).toLowerCase();
      const hasTargetRowIndex = focusRoot.rootRowIndex !== null
        && focusRoot.rootRowIndex !== undefined
        && focusRoot.rootRowIndex !== '';
      const targetRowIndex = hasTargetRowIndex ? Number(focusRoot.rootRowIndex) : Number.NaN;
      let bestIndex = 0;
      let bestScore = -Infinity;

      flows.forEach((flow, index) => {
        const firstEntry = Array.isArray(flow?.entries) && flow.entries.length
          ? flow.entries[0]
          : (flow?.candidate || {});
        const flowHashKeys = this.caseFileBrowserHashLookupKeys(flow?.mainHash || firstEntry?.hash);
        const flowPathFromLabel = normalizeCell(flow?.mainLabel)
          .replace(/^Path\s+/iu, '')
          .replace(/\s+·.*$/u, '');
        const flowPath = normalizeCell(firstEntry?.pathLabel || flowPathFromLabel).toLowerCase();
        const flowRowIndex = Number(firstEntry?.rowIndex);
        let score = 0;

        if (targetHashKeys.size && flowHashKeys.some(key => targetHashKeys.has(key))) score += 10000;
        if (targetPath) score += flowPath === targetPath ? 6000 : -1800;
        if (Number.isFinite(targetRowIndex) && Number.isFinite(flowRowIndex)) {
          if (flowRowIndex === targetRowIndex) score += 9000;
          else score -= Math.min(1200, Math.abs(flowRowIndex - targetRowIndex) * 4);
        }

        if (score > bestScore) {
          bestScore = score;
          bestIndex = index;
        }
      });

      return bestScore > 0 ? bestIndex : 0;
    },

    presentCaseManualStartVisualizer(rows = [], options = {}) {
      const sourceRows = Array.isArray(rows) ? rows : [];
      const reusable = options.forceRebuild === true ? null : this.caseManualStartVisualizerReusablePreparedState(sourceRows);
      const state = reusable || this.buildCaseManualStartVisualizerState(sourceRows);
      this.caseInvestigationVisualizerState = state;
      this.cacheCaseManualStartVisualizerPreparedState(sourceRows, state);
      this.caseInvestigationVisualizerTransactionOnlyMode = false;
      this.caseInvestigationVisualizerViewMode = 'single';
      const focusIndex = this.findCaseManualStartVisualizerFlowIndexForRoot(state, options.focusRoot);
      this.caseInvestigationVisualizerActiveIndex = focusIndex;
      this.caseInvestigationVisualizerLastFlowKey = state.flows?.[focusIndex]?.key || state.flows?.[0]?.key || '';
      this.setCaseManualStartCurrentVisual({ mode: 'single' });
      this.caseInvestigationVisualizerStatus = state.summary;
      if (options.status) this.caseManualStartVisualizerStatus = options.status;
      else if (reusable) this.caseManualStartVisualizerStatus = 'Reopened Manual Solve from the current case without rebuilding its visual graphs.';
      this.showRawDataImportModal = false;
      this.showCaseInvestigationVisualizerModal = true;
      this.$nextTick(() => {
        this.resetCaseInvestigationVisualizerViewport?.();
        this.syncCaseInvestigationVisualizerPageScrollLock?.();
        this.openCaseManualSolveIntroIfNeeded?.();
      });
      return true;
    },

    async openCaseManualStartVisualizer(options = {}) {
      const sourceLabel = this.caseManualNormalizeText(options.source);
      if (this.caseManualStartVisualizerRunning) return false;
      if (!this.caseManualStartAllFlowsAutoActive) this.resetCaseManualStartAllFlowsAutoState?.();
      let startingRows = this.getCaseManualStartVisualizerRows({ includeAll: true });
      if (!startingRows.length) startingRows = this.getCaseManualStartVisualizerRowsFromCurrentVisual();
      if (!startingRows.length) {
        this.caseManualStartVisualizerStatus = 'No starting/main transaction hash was found in Main Data or the current visualizer. Add or paste at least one row with Column B populated, then try Flow Solver again.';
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartVisualizerStatus);
        return false;
      }

      this.caseManualStartLaunchSource = sourceLabel;
      if (sourceLabel === 'quick-lookup') {
        this.caseManualSolveEvaluationOpen = false;
        this.caseManualStartViewportDisplayControlsOpen = false;
        this.closeCaseManualSolveGetDataPanel?.();
      }

      const rootRows = this.caseManualStartOpeningRootRows(startingRows);
      const visibleRootRows = rootRows.length ? rootRows : startingRows;
      // Lookup-first mode has already resolved the submitted root immediately
      // before this handoff. Do not let the generic Manual Solve opener repeat
      // startup provider work for unrelated sparse roots in the same case.
      const skipStartupHydration = options.skipStartupHydration === true;
      const pendingRoots = skipStartupHydration ? [] : this.caseManualStartVisualizerOpeningPendingRoots(startingRows);
      const useOpeningLock = CASE_MANUAL_START_OPENING_FEEDBACK_ENABLED;
      const needsStartupHydration = pendingRoots.length > 0;
      this.caseManualStartVisualizerRunning = true;
      this.caseManualStartVisualizerStatus = needsStartupHydration
        ? `Preparing ${pendingRoots.length} starting transaction${pendingRoots.length === 1 ? '' : 's'} before opening Manual Solve…`
        : 'Opening Manual Solve from current Main Data…';

      let controller = null;
      let hydration = {
        rows: startingRows,
        attemptedCount: 0,
        inferredCount: 0,
        ambiguousCount: 0,
        reusedCount: 0,
        attributionLookupCount: 0,
        attributionHitCount: 0,
        warnings: []
      };
      let writeBack = { changed: false, updatedCount: 0, persisted: false, writeError: '' };
      let openingResult = 'opened-current';

      try {
        if (useOpeningLock) {
          this.beginCaseManualStartVisualizerOpening({
            source: sourceLabel,
            rootCount: visibleRootRows.length,
            pendingRootCount: pendingRoots.length
          });
          await this.$nextTick();
          await new Promise(resolve => {
            if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(() => resolve());
            else setTimeout(resolve, 0);
          });
        }

        if (needsStartupHydration) {
          controller = typeof AbortController === 'function' ? new AbortController() : null;
          this.caseManualStartVisualizerOpeningAbortController = controller;
          this.updateCaseManualStartVisualizerOpening({
            phase: 'hydrate',
            progressPercent: 18,
            message: `Filling missing starting details for ${pendingRoots.length} graph${pendingRoots.length === 1 ? '' : 's'}…`
          });
          hydration = await this.hydrateCaseManualStartRows(startingRows, {
            initialOnly: true,
            lookupAttributions: false,
            providerFieldsOnly: true,
            remoteWorkOnly: true,
            skipReusable: true,
            signal: controller?.signal || null,
            onProgress: (progress = {}) => {
              if (!this.caseManualStartVisualizerOpening?.active) return;
              const pendingIndex = pendingRoots.findIndex(candidate => candidate.rowIndex === progress.candidate?.rowIndex) + 1;
              const denominator = Math.max(1, Number(progress.total) || visibleRootRows.length || 1);
              const completion = Math.max(0, Math.min(1, Number(progress.index || 0) / denominator));
              this.updateCaseManualStartVisualizerOpening({
                phase: 'hydrate',
                progressPercent: Math.round(18 + (completion * 62)),
                currentRootIndex: pendingIndex > 0 ? pendingIndex : Number(this.caseManualStartVisualizerOpening.currentRootIndex) || 0,
                hydratedCount: Number(progress.inferredCount) || 0,
                reusedCount: Number(progress.reusedCount) || 0,
                message: progress.message || this.caseManualStartVisualizerOpening.message
              });
            }
          });
        }

        const exitMode = this.caseManualStartVisualizerOpening?.exitMode || '';
        if (exitMode === 'cancel') {
          openingResult = 'cancelled';
          return false;
        }
        if (exitMode !== 'open-current' && hydration.inferredCount > 0) {
          this.updateCaseManualStartVisualizerOpening({
            phase: 'build',
            progressPercent: 84,
            hydratedCount: hydration.inferredCount,
            reusedCount: hydration.reusedCount || 0,
            message: 'Saving the newly filled starting details…'
          });
          writeBack = await this.writeCaseManualStartHydrationToMain(hydration.rows);
        }

        if (this.caseManualStartVisualizerOpening?.exitMode === 'cancel') {
          openingResult = 'cancelled';
          return false;
        }
        const currentRows = writeBack.changed
          ? this.getCaseManualStartVisualizerRows({ includeAll: true })
          : (hydration.rows?.length ? hydration.rows : startingRows);
        const reusableVisual = this.caseManualStartVisualizerReusablePreparedState(currentRows);
        this.updateCaseManualStartVisualizerOpening({
          phase: 'build',
          progressPercent: reusableVisual ? 94 : 88,
          hydratedCount: hydration.inferredCount || 0,
          reusedCount: hydration.reusedCount || 0,
          message: reusableVisual ? 'Reusing the current visual workspace…' : 'Building the visual workspace…',
          detail: reusableVisual
            ? 'The spreadsheet revision has not changed since this visual was built, so Case Buddy can reopen it directly.'
            : 'The visual is being built from current Main Data. No additional provider lookup is running.'
        });
        if (useOpeningLock) {
          await this.$nextTick();
          await new Promise(resolve => {
            if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(() => resolve());
            else setTimeout(resolve, 0);
          });
        }
        if (this.caseManualStartVisualizerOpening?.exitMode === 'cancel') {
          openingResult = 'cancelled';
          return false;
        }

        const appliedCount = writeBack.persisted ? (writeBack.updatedCount || hydration.inferredCount || 0) : 0;
        const writeIssueText = writeBack.writeError
          ? ` The filled values are visible here, but Main Data write-back needs review: ${writeBack.writeError}.`
          : '';
        const openedEarly = this.caseManualStartVisualizerOpening?.exitMode === 'open-current';
        let status = '';
        if (openedEarly) {
          openingResult = this.caseManualStartVisualizerOpening?.timedOut ? 'opened-after-timeout' : 'opened-current';
          status = `Opened Manual Solve from current Main Data. Next or Auto can fill any remaining sparse starting details.${writeIssueText}`;
        } else if (hydration.inferredCount > 0) {
          openingResult = 'opened-hydrated';
          status = `Filled ${hydration.inferredCount} starting transaction${hydration.inferredCount === 1 ? '' : 's'}${appliedCount ? ` and saved ${appliedCount} row${appliedCount === 1 ? '' : 's'} to Main Data` : ''}, then opened Manual Solve.${writeIssueText}`;
        } else if (reusableVisual) {
          openingResult = 'reopened-cached';
          status = 'Reopened Manual Solve from the current case without repeating startup lookups or rebuilding its visual graphs.';
        } else if (hydration.attemptedCount > 0) {
          openingResult = 'opened-after-startup-check';
          const issueCount = Math.max(
            Number(hydration.ambiguousCount) || 0,
            Array.isArray(hydration.warnings) ? hydration.warnings.length : 0
          );
          status = `Opened Manual Solve after checking ${hydration.attemptedCount} sparse starting transaction${hydration.attemptedCount === 1 ? '' : 's'}${issueCount ? `. ${issueCount} startup item${issueCount === 1 ? '' : 's'} can be handled with Next, Auto, or the manual inspector` : ''}.`;
        } else {
          status = 'Opened Manual Solve from current Main Data. No startup provider lookups were needed.';
        }
        this.updateCaseManualStartVisualizerOpening({ phase: 'ready', progressPercent: 100, message: 'Manual Solve is ready.' });
        return this.presentCaseManualStartVisualizer(currentRows, {
          status,
          focusRoot: options.focusRoot
        });
      } catch (error) {
        const exitMode = this.caseManualStartVisualizerOpening?.exitMode || '';
        if (this.caseManualStartRequestWasAborted(error, controller?.signal)) {
          if (exitMode === 'cancel') {
            openingResult = 'cancelled';
            return false;
          }
          if (exitMode === 'open-current') {
            const currentRows = this.getCaseManualStartVisualizerRows({ includeAll: true });
            openingResult = this.caseManualStartVisualizerOpening?.timedOut ? 'opened-after-timeout' : 'opened-current';
            return this.presentCaseManualStartVisualizer(currentRows.length ? currentRows : startingRows, {
              status: 'Opened Manual Solve from the case data already available. Next or Auto can fill any remaining sparse starting details.',
              focusRoot: options.focusRoot
            });
          }
        }
        this.caseManualStartVisualizerStatus = `Manual Solve could not open: ${error?.message || 'unknown error'}.`;
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartVisualizerStatus);
        openingResult = 'error';
        return false;
      } finally {
        if (useOpeningLock) this.finishCaseManualStartVisualizerOpening({ result: openingResult });
        else this.caseManualStartVisualizerOpeningAbortController = null;
        if (!this.showCaseInvestigationVisualizerModal) this.caseManualStartLaunchSource = '';
        this.caseManualStartVisualizerRunning = false;
      }
    },

    getCaseInvestigationVisualizerReadiness() {
      const base = this.callFirstCaseBuddyOriginalMethod('getCaseInvestigationVisualizerReadiness');
      if (base?.canOpen) return base;
      const count = this.getCaseManualStartVisualizerRows({ includeAll: true }).length;
      if (count > 0) {
        return {
          canOpen: true,
          count,
          summary: `${count} starting transaction${count === 1 ? '' : 's'} can be opened in the manual investigation visualizer before a full graph exists.`,
          title: 'Open a manual investigation visualizer from current starting hashes; Tool 88 can hydrate clear rows and solve selected endings.'
        };
      }
      return base || {
        canOpen: false,
        count: 0,
        summary: 'Prepare at least one graph before visualizing the case.',
        title: 'Prepare at least one graph before visualizing the case.'
      };
    },

    caseInvestigationVisualizerStateMatchesCurrentCase(state) {
      if (state?.source === 'case-buddy-manual-start-visualizer') return true;
      return this.callFirstCaseBuddyOriginalMethod('caseInvestigationVisualizerStateMatchesCurrentCase', [state]) ?? false;
    },

    async openCaseInvestigationVisualizer(options = {}) {
      const base = this.callFirstCaseBuddyOriginalMethod('getCaseInvestigationVisualizerReadiness');
      if (options?.manualStart || !base?.canOpen) {
        const manualRows = this.getCaseManualStartVisualizerRows({ includeAll: true });
        if (manualRows.length) {
          return this.openCaseManualStartVisualizer({ source: 'visualize-button' });
        }
      }
      return this.callFirstCaseBuddyOriginalMethod('openCaseInvestigationVisualizer', [options]);
    },

    normalizeCaseManualStartVisualizerOptions(options = {}) {
      const source = options && typeof options === 'object' ? options : {};
      const rawMergeMode = this.caseManualNormalizeText(source.mergeAddressMode || 'none');
      return {
        labelAddresses: source.labelAddresses !== false,
        showFundsFollowedBars: source.showFundsFollowedBars !== false,
        mergeAddressMode: ['none', 'sameColumnMiddle', 'globalAverage'].includes(rawMergeMode) ? rawMergeMode : 'none'
      };
    },

    applyCaseManualStartVisualizerOptionsChange(options = {}) {
      this.caseManualStartVisualizerOptions = this.normalizeCaseManualStartVisualizerOptions(this.caseManualStartVisualizerOptions);
      if (options.rebuild && this.caseInvestigationVisualizerManualStartMode) {
        this.rebuildCaseManualStartVisualizerForDisplayOptions({ preserveFlow: true });
      }
    },

    rebuildCaseManualStartVisualizerForDisplayOptions(options = {}) {
      if (!this.caseInvestigationVisualizerManualStartMode) return false;
      const activeKey = this.caseInvestigationVisualizerCurrentFlow?.key || this.caseInvestigationVisualizerLastFlowKey || '';
      const rows = this.getCaseManualStartVisualizerRows({ includeAll: true });
      this.caseInvestigationVisualizerState = this.buildCaseManualStartVisualizerState(rows);
      this.cacheCaseManualStartVisualizerPreparedState(rows, this.caseInvestigationVisualizerState);
      if (options.preserveFlow && activeKey) {
        const index = (this.caseInvestigationVisualizerState.flows || []).findIndex(flow => flow.key === activeKey);
        if (index >= 0) this.caseInvestigationVisualizerActiveIndex = index;
      }
      this.setCaseManualStartCurrentVisual({ mode: this.caseInvestigationVisualizerViewMode });
      this.caseInvestigationVisualizerStatus = 'Manual Solve display controls applied. Standard visual-action settings were not changed.';
      return true;
    },



    caseManualSolveIntroDismissedForCurrentCase() {
      const preference = this.caseSupportState?.uiState?.manualSolveIntro;
      if (preference === true) return true;
      return Boolean(preference && typeof preference === 'object' && preference.dismissed === true);
    },

    openCaseManualSolveIntroIfNeeded(options = {}) {
      if (!CASE_MANUAL_SOLVE_INTRO_ENABLED) return false;
      if (!this.showCaseInvestigationVisualizerModal || !this.caseInvestigationVisualizerManualStartMode) return false;
      if (!options.force && this.caseManualSolveIntroDismissedForCurrentCase()) {
        this.caseManualSolveIntroOpen = false;
        return false;
      }
      if (this.caseManualSolveIntroOpen) return true;
      this.caseManualSolveIntroDontShowAgain = false;
      this.caseManualSolveIntroOpen = true;
      this.$nextTick(() => {
        const currentAutoButton = this.caseManualStartViewportAutoCanStart
          ? this.$refs.caseManualSolveIntroCurrentAutoButton
          : null;
        const preferred = this.caseInvestigationVisualizerFlows.length > 1 && this.caseManualStartAllFlowsAutoCanStart
          ? this.$refs.caseManualSolveIntroAllFlowsButton
          : (currentAutoButton || this.$refs.caseManualSolveIntroGotItButton);
        preferred?.focus?.();
      });
      return true;
    },

    persistCaseManualSolveIntroDismissal() {
      const supportState = this.caseSupportState || createDefaultCaseSupportState();
      const uiState = supportState.uiState && typeof supportState.uiState === 'object'
        ? supportState.uiState
        : {};
      const existing = uiState.manualSolveIntro && typeof uiState.manualSolveIntro === 'object'
        ? uiState.manualSolveIntro
        : {};
      this.caseSupportState = {
        ...supportState,
        uiState: {
          ...uiState,
          manualSolveIntro: {
            ...existing,
            schemaVersion: 1,
            dismissed: true,
            dismissedAtUtc: new Date().toISOString()
          }
        }
      };
      if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      return true;
    },

    dismissCaseManualSolveIntro(options = {}) {
      if (this.caseManualSolveIntroDontShowAgain) this.persistCaseManualSolveIntroDismissal();
      this.caseManualSolveIntroOpen = false;
      if (options.focusVisualizer !== false) {
        this.$nextTick(() => this.$refs.caseInvestigationVisualizerModal?.focus?.());
      }
      return true;
    },

    startCaseManualSolveIntroAuto() {
      if (!this.caseManualStartViewportAutoCanStart || this.caseManualStartViewportAutoActive || this.caseManualStartAllFlowsAutoActive) {
        const message = this.caseManualStartViewportAutoButtonTitle || 'Manual Solve Auto cannot start yet.';
        if (typeof this.showCaseToast === 'function') this.showCaseToast(message);
        return false;
      }
      this.dismissCaseManualSolveIntro({ focusVisualizer: false });
      this.$nextTick(() => this.startCaseManualStartViewportAuto?.());
      return true;
    },

    startCaseManualSolveIntroAllFlowsAuto() {
      return this.startCaseManualStartAllFlowsAuto({ source: 'intro' });
    },

    requestCaseInvestigationVisualizerBackdropClose() {
      if (this.caseInvestigationVisualizerInteractionLocked) return false;
      if (!this.caseInvestigationVisualizerManualStartMode) {
        return this.cancelCaseInvestigationVisualizer();
      }
      this.showCaseManualSolveBackdropCloseConfirm = true;
      this.$nextTick(() => this.$refs.caseManualSolveBackdropCloseKeepButton?.focus?.());
      return false;
    },

    dismissCaseManualSolveBackdropCloseConfirm() {
      this.showCaseManualSolveBackdropCloseConfirm = false;
      this.$nextTick(() => this.$refs.caseInvestigationVisualizerModal?.focus?.());
      return true;
    },

    confirmCaseManualSolveBackdropClose() {
      this.showCaseManualSolveBackdropCloseConfirm = false;
      return this.cancelCaseInvestigationVisualizer();
    },


    cancelCaseInvestigationVisualizer(...args) {
      this.showCaseManualSolveBackdropCloseConfirm = false;
      this.clearCaseManualStartHopInspectorProgressQueue?.({ discard: true });
      this.disposeCaseInvestigationVisualizerCameraRuntime?.();
      this.caseManualSolveIntroOpen = false;
      this.caseManualSolveIntroDontShowAgain = false;
      if (this.caseManualStartAllFlowsAutoActive) {
        this.abortCaseManualStartAllFlowsAuto('Guided Flow Solver stopped because the visualizer closed.', { silent: true });
      }
      if (this.caseManualStartViewportAutoActive) {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto stopped because the visualizer closed.', { silent: true });
      }
      this.caseManualStartSettingsOpen = false;
      this.closeCaseManualSolveGetDataPanel?.();
      this.caseManualStartLaunchSource = '';
      this.caseManualSolveHeatMapEnabled = false;
      this.clearCaseManualStartViewportWorkIndicator?.({ immediate: true });
      this.closeCaseManualStartResolvedSolveWarning?.();
      return this.callFirstCaseBuddyOriginalMethod('cancelCaseInvestigationVisualizer', args);
    },

    async refreshCaseInvestigationVisualizer(options = {}) {
      if (this.caseInvestigationVisualizerState?.source === 'case-buddy-manual-start-visualizer') {
        await this.waitForCaseInvestigationVisualizerCameraIdle?.(850);
        await this.caseManualSolveYieldToBrowser?.(1);
        const activeKey = this.caseInvestigationVisualizerCurrentFlow?.key || this.caseInvestigationVisualizerLastFlowKey || '';
        let rows = this.getCaseManualStartVisualizerRows({ includeAll: true });
        // Solving a hop already writes the spreadsheet-shaped case data. Normal
        // visual refreshes therefore rebuild locally and never repeat startup
        // provider calls. A caller must explicitly opt in to sparse-root hydration.
        if (options.hydrateMissing === true) {
          const hydration = await this.hydrateCaseManualStartRows(rows, {
            initialOnly: true,
            lookupAttributions: false,
            providerFieldsOnly: true,
            remoteWorkOnly: true,
            skipReusable: true
          });
          if (hydration.inferredCount > 0) await this.writeCaseManualStartHydrationToMain(hydration.rows);
          rows = this.getCaseManualStartVisualizerRows({ includeAll: true });
        }
        this.caseInvestigationVisualizerState = this.buildCaseManualStartVisualizerState(rows);
        this.cacheCaseManualStartVisualizerPreparedState(rows, this.caseInvestigationVisualizerState);
        if (options.preserveFlow && activeKey) {
          const index = this.caseInvestigationVisualizerState.flows.findIndex(flow => flow.key === activeKey);
          if (index >= 0) this.caseInvestigationVisualizerActiveIndex = index;
        }
        this.setCaseManualStartCurrentVisual({ mode: this.caseInvestigationVisualizerViewMode });
        this.caseInvestigationVisualizerStatus = this.caseInvestigationVisualizerState.summary;
        await this.$nextTick();
        await this.caseManualSolveYieldToBrowser?.(1);
        this.applyCaseInvestigationVisualizerViewportToDom?.(this.caseInvestigationVisualizerViewport);
        return;
      }
      return this.callFirstCaseBuddyOriginalMethod('refreshCaseInvestigationVisualizer', [options]);
    },

    refreshCurrentCaseInvestigationVisualizer() {
      if (this.caseInvestigationVisualizerState?.source === 'case-buddy-manual-start-visualizer') {
        this.commitCaseInvestigationVisualizerCameraViewport?.({ refreshCallouts: false });
        this.setCaseManualStartCurrentVisual({ mode: this.caseInvestigationVisualizerViewMode });
        return;
      }
      return this.callFirstCaseBuddyOriginalMethod('refreshCurrentCaseInvestigationVisualizer');
    },

    renderCaseInvestigationVisualizerAtIndex(flowIndex = 0, options = {}) {
      if (this.caseInvestigationVisualizerState?.source === 'case-buddy-manual-start-visualizer') {
        this.commitCaseInvestigationVisualizerCameraViewport?.({ refreshCallouts: false });
        const flows = this.caseInvestigationVisualizerState.flows || [];
        this.caseInvestigationVisualizerActiveIndex = Math.max(0, Math.min(flows.length - 1, Number(flowIndex) || 0));
        if (options.mode) this.caseInvestigationVisualizerViewMode = options.mode;
        this.caseInvestigationVisualizerLastFlowKey = flows[this.caseInvestigationVisualizerActiveIndex]?.key || '';
        this.setCaseManualStartCurrentVisual({ mode: this.caseInvestigationVisualizerViewMode });
        return;
      }
      return this.callFirstCaseBuddyOriginalMethod('renderCaseInvestigationVisualizerAtIndex', [flowIndex, options]);
    },

    onCaseInvestigationVisualizerNodeClick(node, event) {
      if (this.caseInvestigationVisualizerState?.source === 'case-buddy-manual-start-visualizer') {
        if (!node) return;
        this.caseInvestigationVisualizerSelectedNodeId = node.id || '';
        this.caseInvestigationVisualizerSelectedNodeIds = node.id ? [node.id] : [];
        if (this.caseManualSolveVisualNodeIsGroupNode(node)) {
          this.caseInvestigationVisualizerSelectedCandidate = null;
          this.syncCaseManualSolveSelectedGroupDraft(node.groupRecord || node);
          this.caseManualSolveVisualGroupStatus = 'Visual group selected. This is a persistent visual-only Manual Solve annotation.';
          return;
        }
        const nodeCandidate = node.caseCandidate || node.candidate || null;
        const selectedCandidate = nodeCandidate?.caseManualStartCandidate ? nodeCandidate : null;
        const previousKey = this.buildCaseManualStartHopInspectorCandidateKey(this.caseManualStartHopInspectorCandidate);
        this.caseInvestigationVisualizerSelectedCandidate = selectedCandidate;
        if (this.isCaseManualStartPathEndingCandidate(selectedCandidate)) {
          const nextKey = this.buildCaseManualStartHopInspectorCandidateKey(selectedCandidate);
          this.caseManualStartHopInspectorCandidate = selectedCandidate;
          if (this.caseManualStartHopInspectorOpen && !this.caseManualStartHopInspectorRunning && !this.caseManualStartHopInspectorTraceLoading && !this.caseManualStartHopInspectorPreviewOpen) {
            if (nextKey && nextKey !== previousKey) this.resetCaseManualStartHopInspectorDecisionStateForCandidate(selectedCandidate, { force: true });
            if (this.isCaseManualStartUnresolvedEndingCandidate(selectedCandidate)) {
              this.queueCaseManualStartHopInspectorAutoCheck(selectedCandidate, { reason: 'Selected a new unresolved ending. Tool 88 checks will run automatically.' });
            } else if (nextKey && nextKey !== previousKey) {
              this.caseManualStartHopInspectorStatus = 'The selected path ending already appears resolved. Press Solve hop only if you intentionally want to inspect past it.';
            }
          }
        }
        return;
      }
      return this.callFirstCaseBuddyOriginalMethod('onCaseInvestigationVisualizerNodeClick', [node, event]);
    },

    onCaseInvestigationVisualizerNodePointerDown(node = {}, event = null) {
      if (!this.caseInvestigationVisualizerManualStartMode || !this.caseManualSolveVisualNodeIsGroupNode(node)) return;
      if (this.caseInvestigationVisualizerInteractionLocked || this.caseManualStartViewportAutoActive) return;
      if (event?.button !== 0) return;
      this.beginCaseManualSolveVisualGroupDrag(node, event);
    },

    selectCaseManualSolveVisualGroupNode(node = {}, event = null) {
      if (!node?.id || !this.caseManualSolveVisualNodeIsGroupNode(node)) return;
      const modifier = Boolean(event?.shiftKey || event?.ctrlKey || event?.metaKey);
      const current = Array.isArray(this.caseInvestigationVisualizerSelectedNodeIds) ? this.caseInvestigationVisualizerSelectedNodeIds.slice() : [];
      let nextIds;
      if (modifier) {
        const exists = current.includes(node.id);
        nextIds = exists ? current.filter(id => id !== node.id) : [...current, node.id];
        if (!nextIds.length) nextIds = [node.id];
      } else if (current.length > 1 && current.includes(node.id)) {
        nextIds = current;
      } else {
        nextIds = [node.id];
      }
      this.caseInvestigationVisualizerSelectedNodeId = nextIds[0] || node.id;
      this.caseInvestigationVisualizerSelectedNodeIds = Array.from(new Set(nextIds));
      this.caseInvestigationVisualizerSelectedCandidate = null;
      this.syncCaseManualSolveSelectedGroupDraft(node.groupRecord || node);
      this.caseManualSolveVisualGroupStatus = 'Visual group selected. Double-click to open it, drag to move it, or edit it from the selection panel.';
    },

    beginCaseManualSolveVisualGroupDrag(node = {}, event = null) {
      this.selectCaseManualSolveVisualGroupNode(node, event);
      const startPoint = this.caseManualSolveSvgPointFromEvent(event);
      if (!startPoint) return;
      if (event?.stopPropagation) event.stopPropagation();
      if (event?.preventDefault) event.preventDefault();
      try {
        if (event?.currentTarget && typeof event.currentTarget.setPointerCapture === 'function' && event.pointerId != null) {
          event.currentTarget.setPointerCapture(event.pointerId);
        }
      } catch {
        // Pointer capture is optional; drag still works through SVG pointermove events.
      }
      const selectedGroupNodes = this.caseInvestigationVisualizerSelectedNodes.filter(item => this.caseManualSolveVisualNodeIsGroupNode(item));
      const groupNodes = selectedGroupNodes.length ? selectedGroupNodes : [node];
      const startPositions = groupNodes.reduce((memo, item) => {
        memo[item.id] = { x: Number(item.x) || 0, y: Number(item.y) || 0, groupId: item.groupId || '' };
        return memo;
      }, {});
      this.caseManualSolveVisualGroupDrag = {
        active: true,
        pointerId: event?.pointerId ?? null,
        nodeId: node.id || '',
        groupIds: groupNodes.map(item => item.groupId).filter(Boolean),
        startPoint,
        startPositions,
        moved: false,
        beforeState: this.captureCaseInvestigationVisualizerVisualEditState()
      };
      this.caseInvestigationVisualizerDraggingNodes = true;
      this.caseInvestigationVisualizerPointer = { ...(this.caseInvestigationVisualizerPointer || {}), active: false };
    },

    updateCaseManualSolveVisualGroupDrag(event = null) {
      const drag = this.caseManualSolveVisualGroupDrag || {};
      if (!drag.active || !event || event.type === 'pointercancel') return;
      if (drag.pointerId !== null && drag.pointerId !== undefined && event.pointerId !== drag.pointerId) return;
      const point = this.caseManualSolveSvgPointFromEvent(event);
      if (!point || !drag.startPoint) return;
      if (event?.stopPropagation) event.stopPropagation();
      if (event?.preventDefault) event.preventDefault();
      const dx = point.x - drag.startPoint.x;
      const dy = point.y - drag.startPoint.y;
      const moved = Math.hypot(dx, dy) > 1.5;
      const startPositions = drag.startPositions || {};
      this.caseInvestigationVisualizerVisibleNodes.forEach((node) => {
        if (!this.caseManualSolveVisualNodeIsGroupNode(node) || !startPositions[node.id]) return;
        node.x = startPositions[node.id].x + dx;
        node.y = startPositions[node.id].y + dy;
      });
      if (moved) this.caseManualSolveVisualGroupDrag = { ...drag, moved: true };
    },

    finishCaseManualSolveVisualGroupDrag(event = null) {
      const drag = this.caseManualSolveVisualGroupDrag || {};
      if (!drag.active) return;
      try {
        if (event?.currentTarget && typeof event.currentTarget.releasePointerCapture === 'function' && event.pointerId != null) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      } catch {
        // Pointer capture may already be released.
      }
      if (event?.stopPropagation) event.stopPropagation();
      if (event?.preventDefault) event.preventDefault();
      this.caseInvestigationVisualizerDraggingNodes = false;
      this.caseInvestigationVisualizerPointer = { ...(this.caseInvestigationVisualizerPointer || {}), active: false };
      if (drag.moved) {
        this.pushCaseInvestigationVisualizerVisualEditHistory(drag.beforeState);
        this.caseManualSolvePersistSelectedGroupPositions();
        this.caseInvestigationVisualizerStatus = 'Manual Solve visual group moved. Main Data and Raw Data were not changed.';
      }
      this.caseManualSolveVisualGroupDrag = { active: false, pointerId: null, nodeId: '', groupIds: [], startPoint: null, startPositions: {}, moved: false, beforeState: null };
    },

    caseManualSolveGroupBoundsForNode(node = {}) {
      const x = Number(node?.x);
      const y = Number(node?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      if (this.caseManualSolveVisualNodeIsGroupNode(node)) {
        const width = Number(node.boxWidth) || 154;
        const height = Number(node.boxHeight) || 118;
        return { x1: x - width / 2, y1: y - height / 2, x2: x + width / 2, y2: y + height / 2, cx: x, cy: y };
      }
      return null;
    },

    caseManualSolveBoundsIntersectSelectionRect(bounds = null, rect = null) {
      if (!bounds || !rect) return false;
      const x1 = Number(rect.x);
      const y1 = Number(rect.y);
      const x2 = x1 + (Number(rect.width) || 0);
      const y2 = y1 + (Number(rect.height) || 0);
      if (![x1, y1, x2, y2].every(Number.isFinite)) return false;
      const intersects = bounds.x1 <= x2 && bounds.x2 >= x1 && bounds.y1 <= y2 && bounds.y2 >= y1;
      const centerInside = bounds.cx >= x1 && bounds.cx <= x2 && bounds.cy >= y1 && bounds.cy <= y2;
      return intersects || centerInside;
    },

    caseManualSolveIncludeGroupsInSelectionBox(selectionBox = null) {
      if (!this.caseInvestigationVisualizerManualStartMode || !selectionBox) return;
      const rect = (() => {
        const x1 = Number(selectionBox.startX) || 0;
        const y1 = Number(selectionBox.startY) || 0;
        const x2 = Number(selectionBox.endX) || x1;
        const y2 = Number(selectionBox.endY) || y1;
        return { x: Math.min(x1, x2), y: Math.min(y1, y2), width: Math.abs(x2 - x1), height: Math.abs(y2 - y1) };
      })();
      if (rect.width < 4 && rect.height < 4) return;
      const groupIds = this.caseManualSolveVisualGroupNodesForCurrentVisual
        .filter(node => this.caseManualSolveBoundsIntersectSelectionRect(this.caseManualSolveGroupBoundsForNode(node), rect))
        .map(node => node.id)
        .filter(Boolean);
      if (!groupIds.length) return;
      const nextIds = Array.from(new Set([...(Array.isArray(this.caseInvestigationVisualizerSelectedNodeIds) ? this.caseInvestigationVisualizerSelectedNodeIds : []), ...groupIds]));
      this.caseInvestigationVisualizerSelectedNodeIds = nextIds;
      this.caseInvestigationVisualizerSelectedNodeId = nextIds[0] || '';
      if (this.caseManualSolveSelectedGroups.length === 1 && nextIds.length === 1) {
        const groupNode = this.caseInvestigationVisualizerSelectedNodes.find(node => this.caseManualSolveVisualNodeIsGroupNode(node));
        if (groupNode) this.syncCaseManualSolveSelectedGroupDraft(groupNode.groupRecord || groupNode);
      }
    },

    clearCaseInvestigationVisualizerSelection() {
      const result = this.callFirstCaseBuddyOriginalMethod('clearCaseInvestigationVisualizerSelection');
      this.caseInvestigationVisualizerSelectedCandidate = null;
      return result;
    },

    getCaseHopSolverCandidateFromInvestigationVisualizerSelection(options = {}) {
      if (this.caseInvestigationVisualizerState?.source === 'case-buddy-manual-start-visualizer') {
        const selected = this.caseManualStartSingleSelectedCandidate;
        return selected?.caseManualStartCandidate ? selected : null;
      }
      const selected = this.caseInvestigationVisualizerSelectedCandidate;
      if (selected?.caseManualStartCandidate) return selected;
      const node = this.caseInvestigationVisualizerSelectedNode;
      if (node?.caseCandidate?.caseManualStartCandidate) return node.caseCandidate;
      return this.callFirstCaseBuddyOriginalMethod('getCaseHopSolverCandidateFromInvestigationVisualizerSelection', [options]);
    },

    isCaseManualStartPathEndingCandidate(candidate = null) {
      if (!candidate || !candidate.caseManualStartCandidate) return false;
      const hasOccurrenceContinuation = /^Continues\s+as\b/iu.test(this.caseManualNormalizeContinuesAsText(candidate.continuesAs || ''))
        && this.caseManualStartCandidateHasOccurrenceSpecificChild(candidate);
      if (candidate.caseManualOccurrenceContinuationMissing) return !hasOccurrenceContinuation;
      if (hasOccurrenceContinuation) return false;
      if (candidate.isPathEnding) return true;
      return /path\s*ends/iu.test(this.caseManualNormalizeText(candidate.continuesAs));
    },

    findCurrentCaseManualStartMainRowForCandidate(candidate = null) {
      if (!candidate?.caseManualStartCandidate) return null;
      const candidateHash = this.caseManualNormalizeText(candidate.hash || candidate.transactionHash || candidate.txHash || '').toLowerCase();
      if (!candidateHash) return null;
      const matrix = this.getCaseManualStartMainMatrix();
      const sourceRows = Array.isArray(matrix) ? matrix : [];
      const dataRows = sourceRows.length && this.caseManualIsHeaderRow(sourceRows[0]) ? sourceRows.slice(1) : sourceRows;
      const index = this.caseManualFindDataRowIndexForCandidate(dataRows, candidate, candidateHash);
      const match = index >= 0 ? dataRows[index] : null;
      return match ? this.caseManualEnsureRowWidth(match, Math.max(CASE_MANUAL_START_COLUMN_COUNT, match.length || 0)) : null;
    },

    caseManualStartCandidateHasRowSpecificPathEndingResolution(candidate = null, currentRow = null) {
      if (!candidate || !candidate.caseManualStartCandidate) return false;
      const row = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const resolvedRow = Array.isArray(currentRow) && currentRow.length
        ? currentRow
        : (this.findCurrentCaseManualStartMainRowForCandidate(candidate) || []);
      if (this.caseManualStartCandidateHasPersistedAddressPoisoningEnding(candidate, resolvedRow)) return true;
      const notesText = [
        candidate.caseNotes,
        candidate.analystNotes,
        row[CASE_MANUAL_START_COL.CASE_NOTES],
        row[CASE_MANUAL_START_COL.ANALYST_NOTES],
        resolvedRow?.[CASE_MANUAL_START_COL.CASE_NOTES],
        resolvedRow?.[CASE_MANUAL_START_COL.ANALYST_NOTES]
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join('\n');
      if (!notesText) return false;
      const hasExplicitPathEndingDecision = /tool\s*88:\s*path\s+ends\s+at|action\s*=\s*manual[-_\s]*path[-_\s]*ending|user\s+intentionally\s+marked|investigator\s+marked|manual\s+path[-_\s]*ending|marked\s+as\s+path\s+ending/iu.test(notesText);
      if (!hasExplicitPathEndingDecision) return false;
      const lowerNotes = notesText.toLowerCase();
      if (this.caseManualStartCandidateIsReverse(candidate)) {
        const activeFromAttribution = this.caseManualCanonicalizeFromAttributionForColumnX(
          candidate.fromAttributionLabel
            || resolvedRow?.[CASE_MANUAL_START_COL.FROM_ATTRIBUTION]
            || row?.[CASE_MANUAL_START_COL.FROM_ATTRIBUTION]
            || ''
        );
        const oppositeToAttribution = this.caseManualCanonicalizeAttributionForColumnK(
          candidate.toAttributionLabel
            || candidate.attribution
            || resolvedRow?.[CASE_MANUAL_START_COL.ATTRIBUTION]
            || row?.[CASE_MANUAL_START_COL.ATTRIBUTION]
            || ''
        );
        const explicitlyReverseDecision = /\b(?:reverse|backward|source[-\s]?of[-\s]?funds)\b/iu.test(notesText)
          || /\bdirection\s*=\s*reverse\b/iu.test(notesText);
        const explicitlyInvestigatorMarked = /user\s+intentionally\s+marked|investigator\s+marked/iu.test(notesText);
        const meaningfulSourceAttribution = this.caseManualAttributionLabelIsMeaningfulEndpoint(activeFromAttribution)
          && !this.caseManualAttributionLabelLooksLikeDeposit(activeFromAttribution);
        // Historical completion notes did not record direction. A Reverse run
        // must not inherit a forward receiver-side ending merely because the
        // same spreadsheet row was previously marked complete.
        if (!explicitlyReverseDecision && !explicitlyInvestigatorMarked && !meaningfulSourceAttribution) return false;
        if (!meaningfulSourceAttribution && this.caseManualAttributionLabelLooksLikeDeposit(oppositeToAttribution) && !explicitlyReverseDecision && !explicitlyInvestigatorMarked) {
          return false;
        }
      }
      const targetAddress = this.caseManualNormalizeAddressKey(
        this.caseManualStartCandidateEndpointAddress({
          ...candidate,
          rawRow: resolvedRow?.length ? resolvedRow : row
        })
      );
      const targetHash = this.caseManualNormalizeText(
        candidate.hash
          || candidate.transactionHash
          || candidate.txHash
          || resolvedRow?.[CASE_MANUAL_START_COL.TX_HASH]
          || row[CASE_MANUAL_START_COL.TX_HASH]
          || ''
      ).toLowerCase();
      const addressIsNamed = Boolean(targetAddress && lowerNotes.includes(targetAddress.toLowerCase()));
      const hashIsNamed = Boolean(targetHash && lowerNotes.includes(targetHash));
      if (addressIsNamed || hashIsNamed) return true;
      return /action\s*=\s*manual[-_\s]*path[-_\s]*ending|user\s+intentionally\s+marked|investigator\s+marked|manual\s+path[-_\s]*ending|marked\s+as\s+path\s+ending/iu.test(notesText);
    },

    caseManualStartCandidateHasOccurrenceSpecificChild(candidate = null) {
      if (!candidate?.caseManualStartCandidate) return false;
      const candidateHash = this.caseManualNormalizeText(candidate.hash || candidate.transactionHash || candidate.txHash || '').toLowerCase();
      if (!candidateHash || !this.caseManualIsLikelyTransactionHash(candidateHash)) return false;
      const matrix = this.getCaseManualStartMainMatrix();
      const sourceRows = Array.isArray(matrix) ? matrix : [];
      const dataRows = sourceRows.length && this.caseManualIsHeaderRow(sourceRows[0]) ? sourceRows.slice(1) : sourceRows.slice();
      if (!dataRows.length) return false;
      const parentIndex = this.caseManualFindDataRowIndexForCandidate(dataRows, candidate, candidateHash);
      const fallbackRow = Array.isArray(candidate.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate.row) ? candidate.row : []);
      const parentRow = parentIndex >= 0
        ? dataRows[parentIndex]
        : this.caseManualEnsureRowWidth(fallbackRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, fallbackRow?.length || 0));
      const parentHash = this.caseManualNormalizeText(parentRow?.[CASE_MANUAL_START_COL.TX_HASH] || candidateHash);
      if (!parentHash || !this.caseManualIsLikelyTransactionHash(parentHash)) return false;
      const continuesAs = this.caseManualNormalizeContinuesAsText(parentRow?.[CASE_MANUAL_START_COL.CONTINUES_AS] || candidate.continuesAs || '');
      const childHashes = this.caseManualExtractContinuesAsHashes(continuesAs, dataRows);
      if (!childHashes.length) return false;
      const parentLabel = this.caseManualNormalizeText(parentRow?.[CASE_MANUAL_START_COL.PATH_LABEL] || candidate.pathLabel || '').toLowerCase();
      const numericText = value => this.caseManualNormalizeText(value).replace(/,/g, '');
      const firstNumber = (value) => {
        const match = numericText(value).match(/-?\d+(?:\.\d+)?/u);
        if (!match) return NaN;
        const number = Number(match[0]);
        return Number.isFinite(number) ? number : NaN;
      };
      const parentFundsAmount = firstNumber(parentRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
      const allocationTolerance = () => Math.max(0.01, Math.abs(parentFundsAmount || 0) * 0.000001);
      const allocationMatchesParent = (childRow = []) => {
        const childFundsAmount = firstNumber(childRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
        if (!Number.isFinite(parentFundsAmount) || !Number.isFinite(childFundsAmount)) return false;
        return Math.abs(parentFundsAmount - childFundsAmount) <= allocationTolerance();
      };
      const allocationFitsWithinParent = (childRow = []) => {
        const childFundsAmount = firstNumber(childRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
        if (!Number.isFinite(parentFundsAmount) || !Number.isFinite(childFundsAmount)) return false;
        if (childFundsAmount <= 0 || parentFundsAmount <= 0) return false;
        return childFundsAmount <= parentFundsAmount + allocationTolerance();
      };
      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const parentEndpointAddress = this.caseManualNormalizeAddressKey(
        reverse
          ? (parentRow?.[CASE_MANUAL_START_COL.FROM_ADDRESS] || candidate.fromAddress || candidate.targetAddress || '')
          : (parentRow?.[CASE_MANUAL_START_COL.TO_ADDRESS] || candidate.toAddress || candidate.address || '')
      );
      const parentOccurrences = dataRows
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => this.caseManualTransactionHashesMatch(
          this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase(),
          parentHash,
        ));
      const parentIsDuplicate = parentOccurrences.length > 1;
      const labelRoot = (label = '') => {
        const match = this.caseManualNormalizeText(label).toLowerCase().match(/^([0-9]+|[a-z]+)/u);
        return match ? match[1] : '';
      };
      const pathLooksLikeLaterStepInSameFlow = (childLabel = '', parentCandidateLabel = '') => {
        const child = this.caseManualNormalizeText(childLabel).toLowerCase();
        const parent = this.caseManualNormalizeText(parentCandidateLabel).toLowerCase();
        if (!child || !parent || child === parent) return false;
        if (child.startsWith(parent)) return true;
        const childRoot = labelRoot(child);
        const parentRoot = labelRoot(parent);
        if (!childRoot || childRoot !== parentRoot) return false;
        return this.caseManualCompareSaplingPathLabels(child, parent) > 0;
      };
      const rowLooksPhysicallyLinkedToParent = (row = [], rowIndex = -1) => {
        if (rowIndex === parentIndex) return false;
        const childParentHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
        if (!childParentHash || !this.caseManualTransactionHashesMatch(childParentHash, parentHash)) return false;
        const childConnectionAddress = this.caseManualNormalizeAddressKey(
          reverse
            ? row?.[CASE_MANUAL_START_COL.TO_ADDRESS]
            : row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]
        );
        if (parentEndpointAddress && childConnectionAddress && childConnectionAddress !== parentEndpointAddress) return false;
        if (rowIndex > parentIndex) return true;
        const childPathLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        return Boolean(parentLabel && pathLooksLikeLaterStepInSameFlow(childPathLabel, parentLabel));
      };
      const rowMatchesChildForParent = (row = [], rowIndex = -1, childHash = '') => {
        const rowHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (!rowHash || !this.caseManualTransactionHashesMatch(rowHash, childHash)) return false;
        if (!rowLooksPhysicallyLinkedToParent(row, rowIndex)) return false;
        if (!parentIsDuplicate) return true;
        const childFromLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_LABEL]).toLowerCase();
        const childPathLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        if (parentLabel && childFromLabel && childFromLabel === parentLabel) return true;
        if (parentLabel && childPathLabel && pathLooksLikeLaterStepInSameFlow(childPathLabel, parentLabel) && allocationFitsWithinParent(row)) return true;
        if (allocationMatchesParent(row)) return true;
        if (allocationFitsWithinParent(row) && rowIndex > parentIndex) return true;
        // If the data does not include funds-followed amounts, retain the older
        // visual fallback for same-flow labels. When amounts are present, do not
        // let a larger sibling branch satisfy a smaller duplicate occurrence.
        if (!Number.isFinite(parentFundsAmount) && parentLabel && childPathLabel && pathLooksLikeLaterStepInSameFlow(childPathLabel, parentLabel)) return true;
        return false;
      };
      const allDeclaredChildrenHaveRows = childHashes.every(childHash => dataRows.some((row, rowIndex) => rowMatchesChildForParent(row, rowIndex, childHash)));
      if (allDeclaredChildrenHaveRows) return true;
      if (!Number.isFinite(parentFundsAmount) || parentFundsAmount <= 0) return false;
      const childRows = dataRows
        .map((row, index) => ({ row, index }))
        .filter(({ row, index }) => rowMatchesChildForParent(row, index, row?.[CASE_MANUAL_START_COL.TX_HASH]))
        .filter(({ row }) => {
          const rowHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
          return rowHash && childHashes.some(childHash => this.caseManualTransactionHashesMatch(rowHash, childHash));
        })
        .map(({ row }) => firstNumber(row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]))
        .filter(amount => Number.isFinite(amount) && amount > 0 && amount <= parentFundsAmount + allocationTolerance())
        .slice(0, 16);
      if (!childRows.length) return false;
      const amountClose = (left, right) => {
        const tolerance = Math.max(0.05, Math.abs(right) * 0.00001);
        return Number.isFinite(left) && Number.isFinite(right) && Math.abs(left - right) <= tolerance;
      };
      if (childRows.some(amount => amountClose(amount, parentFundsAmount))) return true;
      if (amountClose(childRows.reduce((sum, amount) => sum + amount, 0), parentFundsAmount)) return true;
      if (childRows.length <= 15) {
        const limit = 1 << childRows.length;
        for (let mask = 1; mask < limit; mask += 1) {
          let total = 0;
          for (let index = 0; index < childRows.length; index += 1) {
            if (mask & (1 << index)) total += childRows[index];
          }
          if (amountClose(total, parentFundsAmount)) return true;
        }
      }
      return false;
    },

    caseManualStartCandidateHasDanglingContinuation(candidate = null) {
      if (!candidate?.caseManualStartCandidate) return false;
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate(candidate);
      const continuesAs = this.caseManualNormalizeContinuesAsText(
        currentRow?.[CASE_MANUAL_START_COL.CONTINUES_AS] || candidate.continuesAs || ''
      );
      if (!/^Continues\s+as\b/iu.test(continuesAs)) return false;
      return !this.caseManualStartCandidateHasOccurrenceSpecificChild(candidate);
    },

    caseManualStartCandidateLooksResolvedPathEnding(candidate = null) {
      if (!candidate || !candidate.caseManualStartCandidate) return false;
      if (candidate.caseManualOccurrenceContinuationMissing && !this.caseManualStartCandidateHasOccurrenceSpecificChild(candidate)) return false;
      if (candidate.caseManualSwapContinuation) return false;
      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const row = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate(candidate);
      if (this.caseManualStartCandidateHasPersistedAddressPoisoningEnding(candidate, currentRow)) return true;
      if (this.caseManualStartCandidateHasFurtherManualWork(candidate, currentRow)) return true;
      const activeAttributionColumn = reverse ? CASE_MANUAL_START_COL.FROM_ATTRIBUTION : CASE_MANUAL_START_COL.ATTRIBUTION;
      const activeAttribution = reverse
        ? this.caseManualCanonicalizeFromAttributionForColumnX(
            candidate.fromAttributionLabel
              || currentRow?.[activeAttributionColumn]
              || row[activeAttributionColumn]
              || ''
          )
        : this.caseManualCanonicalizeAttributionForColumnK(
            candidate.toAttributionLabel
              || candidate.attribution
              || currentRow?.[activeAttributionColumn]
              || row[activeAttributionColumn]
              || ''
          );
      const text = [
        candidate.systemNotes,
        activeAttribution,
        candidate.analystNotes,
        candidate.caseNotes,
        candidate.continuesAs,
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row[CASE_MANUAL_START_COL.ANALYST_NOTES],
        row[CASE_MANUAL_START_COL.CASE_NOTES],
        row[CASE_MANUAL_START_COL.CONTINUES_AS],
        currentRow?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.ANALYST_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.CASE_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.CONTINUES_AS]
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      const currentContinuesAs = this.caseManualNormalizeContinuesAsText(currentRow?.[CASE_MANUAL_START_COL.CONTINUES_AS] || '');
      const currentSystemNotes = this.caseManualNormalizeText(currentRow?.[CASE_MANUAL_START_COL.SYSTEM_NOTES] || '');
      const currentAttribution = reverse
        ? this.caseManualCanonicalizeFromAttributionForColumnX(currentRow?.[activeAttributionColumn] || '')
        : this.caseManualCanonicalizeAttributionForColumnK(currentRow?.[activeAttributionColumn] || '');
      if (!reverse && /path\s*ends/iu.test(currentContinuesAs) && this.caseManualTextHasDormantFundsNote(text)) return true;
      const hasRowSpecificPathEndingResolution = this.caseManualStartCandidateHasRowSpecificPathEndingResolution(candidate, currentRow);
      const currentAttributionCanEnd = this.caseManualAttributionLabelIsMeaningfulEndpoint(currentAttribution)
        && !(reverse && this.caseManualAttributionLabelLooksLikeDeposit(currentAttribution));
      if (/path\s*ends/iu.test(currentContinuesAs) && currentAttributionCanEnd && /path\s*ends/iu.test(currentSystemNotes) && hasRowSpecificPathEndingResolution) return true;
      const looksLikeSolvedSwapContinuation = /\bswap\s+solved\b|post[-_\s]*swap\s+funds\s+followed|post[-_\s]*swap\s+left\s+bounds|swapped\s+from\s+[^.]+\s+to\s+[^.]+\s+using\s+/iu.test(text);
      const forwardDormantBalanceStory = /(?:funds|portion)\s+(?:remain|remains)|remains\s+at\s+the\s+receiving\s+address/iu.test(text);
      const hasMeaningfulTerminalBalanceStory = (!reverse && forwardDormantBalanceStory)
        || Boolean(this.caseManualCandidateMeaningfulEndpointAttributionLabel(candidate));
      if (looksLikeSolvedSwapContinuation && !hasMeaningfulTerminalBalanceStory) return false;
      if (this.caseManualLooksLikeUnknownServicePathEnding(candidate)) return true;
      const solvedByExplicitSwapOrBridge = /swap\s+ending|bridge\s+ending|treated\s+as\s+(?:swap|bridge)/iu.test(text);
      return (hasRowSpecificPathEndingResolution || solvedByExplicitSwapOrBridge)
        && !/^path\s*ends\.?$/iu.test(text);
    },

    isCaseManualStartUnresolvedEndingCandidate(candidate = null) {
      if (!candidate?.caseManualStartCandidate) return false;
      if (candidate.caseManualOccurrenceContinuationMissing && this.caseManualStartCandidateHasOccurrenceSpecificChild(candidate)) return false;
      if (this.caseManualStartCandidateHasDanglingContinuation(candidate)) return true;
      return this.isCaseManualStartPathEndingCandidate(candidate) && !this.caseManualStartCandidateLooksResolvedPathEnding(candidate);
    },

    getCaseManualStartActivePathEndingCandidate(preferredCandidate = null) {
      if (!this.caseInvestigationVisualizerManualStartMode) return null;
      const candidates = [];
      if (preferredCandidate) candidates.push(preferredCandidate);
      candidates.push(
        this.caseManualStartHopInspectorCandidate,
        this.caseInvestigationVisualizerSelectedCandidate,
        this.caseInvestigationVisualizerSelectedNode?.caseCandidate,
        this.caseInvestigationVisualizerSelectedNode?.candidate,
        this.caseInvestigationVisualizerCurrentFlow?.candidate
      );
      const entries = Array.isArray(this.caseInvestigationVisualizerCurrentFlow?.entries)
        ? this.caseInvestigationVisualizerCurrentFlow.entries.slice().reverse()
        : [];
      candidates.push(...entries);
      const visual = this.caseInvestigationVisualizerCurrentVisual || {};
      const visualNodes = Array.isArray(visual.nodes) ? visual.nodes.slice().reverse() : [];
      visualNodes.forEach((node) => {
        candidates.push(node?.caseCandidate, node?.candidate);
      });
      const pathEnding = candidates.find(candidate => this.isCaseManualStartUnresolvedEndingCandidate(candidate));
      if (pathEnding) return pathEnding;
      return candidates.find(candidate => candidate?.caseManualStartCandidate) || null;
    },

    findCaseManualStartPathEndingNode(preferredCandidate = null) {
      if (!this.caseInvestigationVisualizerManualStartMode) return null;
      const visual = this.caseInvestigationVisualizerCurrentVisual || {};
      const nodes = Array.isArray(visual.nodes) ? visual.nodes : [];
      if (!nodes.length) return null;
      const candidate = preferredCandidate || this.getCaseManualStartActivePathEndingCandidate();
      const candidateHash = this.caseManualNormalizeText(candidate?.hash || candidate?.transactionHash || candidate?.txHash).toLowerCase();
      const candidateRowIndex = Number.isInteger(candidate?.rowIndex) ? candidate.rowIndex : null;
      const matchesCandidate = node => {
        const nodeCandidate = node?.caseCandidate || node?.candidate || null;
        if (!nodeCandidate?.caseManualStartCandidate) return false;
        const nodeHash = this.caseManualNormalizeText(nodeCandidate.hash || nodeCandidate.transactionHash || nodeCandidate.txHash).toLowerCase();
        const nodeRowIndex = Number.isInteger(nodeCandidate.rowIndex) ? nodeCandidate.rowIndex : null;
        return (!candidateHash || nodeHash === candidateHash) && (candidateRowIndex === null || nodeRowIndex === candidateRowIndex);
      };
      return nodes.find(node => node.type === 'address' && ['path-ending', 'unknown-service'].includes(node.labelCategory) && matchesCandidate(node))
        || nodes.find(node => node.type === 'address' && this.isCaseManualStartUnresolvedEndingCandidate(node.caseCandidate || node.candidate))
        || nodes.find(node => this.isCaseManualStartUnresolvedEndingCandidate(node.caseCandidate || node.candidate))
        || null;
    },

    selectCaseManualStartPathEndingNode(preferredCandidate = null) {
      const node = this.findCaseManualStartPathEndingNode(preferredCandidate);
      const candidate = (node?.caseCandidate || node?.candidate || null) || preferredCandidate || this.getCaseManualStartActivePathEndingCandidate();
      if (node?.id) {
        this.caseInvestigationVisualizerSelectedNodeId = node.id;
        this.caseInvestigationVisualizerSelectedNodeIds = [node.id];
      } else {
        this.caseInvestigationVisualizerSelectedNodeId = '';
        this.caseInvestigationVisualizerSelectedNodeIds = [];
      }
      const previousKey = this.buildCaseManualStartHopInspectorCandidateKey(this.caseManualStartHopInspectorCandidate);
      this.caseInvestigationVisualizerSelectedCandidate = candidate || null;
      if (this.isCaseManualStartUnresolvedEndingCandidate(candidate)) {
        const nextKey = this.buildCaseManualStartHopInspectorCandidateKey(candidate);
        this.caseManualStartHopInspectorCandidate = candidate;
        if (this.caseManualStartHopInspectorOpen) {
          if (nextKey && nextKey !== previousKey) this.resetCaseManualStartHopInspectorDecisionStateForCandidate(candidate, { force: true });
          this.queueCaseManualStartHopInspectorAutoCheck(candidate, { reason: 'Selected the active unresolved ending. Tool 88 checks will run automatically.' });
        }
      }
      return candidate || null;
    },

    caseManualStartCandidateRequiresChoiceBeforeSolve(candidate = {}) {
      return Boolean(candidate?.caseManualStartCandidate && this.caseManualStartCandidateNeedsOutputChoice(candidate));
    },

    caseManualStartViewportAutoIsBusy() {
      return Boolean(
        this.caseManualStartHopInspectorRunning
          || this.caseManualStartHopInspectorTraceLoading
          || this.caseManualStartOutputChoiceLoading
          || this.caseManualStartHopInspectorAutoCheckInFlightKey
          || this.caseGlobalOperation?.active
          || this.caseModalOperation?.active
      );
    },

    caseManualStartViewportAutoFlowIsComplete() {
      if (!this.caseInvestigationVisualizerManualStartMode || !this.caseInvestigationVisualizerCurrentVisual) return true;
      if (this.caseManualStartOutputChoiceShouldShow || this.caseManualStartOutputChoiceOpen) return false;
      return this.getCaseManualStartViewportUnresolvedEndingCount() <= 0;
    },

    buildCaseManualStartViewportAutoSignature() {
      const flow = this.caseInvestigationVisualizerCurrentFlow || {};
      const candidate = this.caseManualStartHopInspectorSelectedCandidate || this.getCaseManualStartActivePathEndingCandidate?.() || {};
      const nodes = Array.isArray(this.caseInvestigationVisualizerCurrentVisual?.nodes) ? this.caseInvestigationVisualizerCurrentVisual.nodes : [];
      const txCount = nodes.filter(node => ['transaction', 'tx'].includes(String(node?.type || '').toLowerCase())).length;
      const endingCount = nodes.filter(node => this.isCaseManualStartUnresolvedEndingCandidate(node?.caseCandidate || node?.candidate)).length;
      return [
        flow.key || '',
        this.buildCaseManualStartHopInspectorCandidateKey(candidate),
        this.caseManualStartOutputChoiceOpen ? 'choice-open' : 'choice-closed',
        this.caseManualStartOutputChoiceRows.length,
        this.caseManualStartOutputChoiceSelectedKey || '',
        this.caseManualStartHopInspectorOpen ? 'inspector-open' : 'inspector-closed',
        this.caseManualStartHopInspectorPreviewOpen ? 'preview-open' : 'preview-closed',
        this.caseManualStartHopInspectorPreviewRows.length,
        this.caseManualStartHopInspectorChecksReady ? 'checks-ready' : 'checks-needed',
        this.caseManualStartHopInspectorAppliedCount || 0,
        txCount,
        endingCount,
        this.caseManualStartHopInspectorStatus || this.caseInvestigationVisualizerStatus || ''
      ].join('|');
    },

    caseManualStartAllFlowsDefaultState(overrides = {}) {
      return {
        active: false,
        stopRequested: false,
        runId: 0,
        startedAtMs: 0,
        caseFileId: '',
        caseFlowCount: 0,
        totalFlowCount: 0,
        currentFlowNumber: 0,
        currentFlowKey: '',
        currentFlowLabel: '',
        currentFlowUnresolvedCount: 0,
        completedFlowKeys: [],
        attentionFlows: [],
        status: '',
        detail: '',
        lastResult: '',
        resultVisible: false,
        ...overrides
      };
    },

    resetCaseManualStartAllFlowsAutoState(options = {}) {
      const nextRunId = Number(this.caseManualStartAllFlowsAuto?.runId || 0) + 1;
      this.caseManualStartAllFlowsAuto = this.caseManualStartAllFlowsDefaultState({
        runId: nextRunId,
        resultVisible: options.keepResult === true && Boolean(this.caseManualStartAllFlowsAuto?.resultVisible),
        lastResult: options.keepResult === true ? (this.caseManualStartAllFlowsAuto?.lastResult || '') : ''
      });
      return this.caseManualStartAllFlowsAuto;
    },

    caseManualStartAllFlowsFlowIdentity(flow = {}, index = 0) {
      const hash = this.caseManualNormalizeText(flow?.mainHash || flow?.candidate?.hash || '').toLowerCase();
      const label = this.caseManualNormalizeText(flow?.mainLabel || flow?.candidate?.pathLabel || '').toLowerCase();
      return [hash, label].filter(Boolean).join('|') || this.caseManualNormalizeText(flow?.key) || `flow-${index}`;
    },

    caseManualStartAllFlowsFlowCandidates(flow = {}) {
      const candidates = [];
      const seen = new Set();
      const remember = (candidate) => {
        if (!candidate?.caseManualStartCandidate) return;
        const key = this.buildCaseManualStartHopInspectorCandidateKey(candidate) || [
          this.caseManualNormalizeText(candidate.pathLabel || candidate.label || '').toLowerCase(),
          this.caseManualNormalizeText(candidate.hash || candidate.txHash || candidate.transactionHash || '').toLowerCase(),
          Number.isInteger(candidate.rowIndex) ? candidate.rowIndex : '',
          this.caseManualNormalizeText(candidate.fromHash || '').toLowerCase(),
          this.caseManualNormalizeText(candidate.fundsFollowed || '').toLowerCase()
        ].join('|');
        if (seen.has(key)) return;
        seen.add(key);
        candidates.push(candidate);
      };
      remember(flow?.candidate);
      (Array.isArray(flow?.entries) ? flow.entries : []).forEach(remember);
      (Array.isArray(flow?.visual?.entries) ? flow.visual.entries : []).forEach(remember);
      (Array.isArray(flow?.visual?.nodes) ? flow.visual.nodes : []).forEach((node) => {
        remember(node?.caseCandidate);
        remember(node?.candidate);
      });
      return candidates;
    },

    caseManualStartAllFlowsAssessFlow(flow = {}) {
      const candidates = this.caseManualStartAllFlowsFlowCandidates(flow);
      const unresolved = [];
      const choices = [];
      const manualReview = [];
      const acceptedReportBoundaries = [];
      candidates.forEach((candidate) => {
        const currentRow = this.findCurrentCaseManualStartMainRowForCandidate?.(candidate) || [];
        const acceptedReportBoundary = this.caseManualStartCandidateHasAcceptedReportBoundary?.(candidate, currentRow) === true;
        if (acceptedReportBoundary) acceptedReportBoundaries.push(candidate);
        if (!acceptedReportBoundary && this.isCaseManualStartUnresolvedEndingCandidate(candidate)) unresolved.push(candidate);
        if (!acceptedReportBoundary && this.caseManualStartCandidateNeedsOutputChoice?.(candidate)) choices.push(candidate);
        if (
          !acceptedReportBoundary
            && this.caseManualStartCandidateHasFurtherManualWork?.(candidate, currentRow)
        ) manualReview.push(candidate);
      });
      const needsWork = Boolean(unresolved.length || choices.length || manualReview.length);
      let reason = acceptedReportBoundaries.length
        ? `${acceptedReportBoundaries.length} incomplete path ending${acceptedReportBoundaries.length === 1 ? ' is' : 's are'} accepted as ${acceptedReportBoundaries.length === 1 ? 'a report boundary' : 'report boundaries'} with further tracing still recommended.`
        : 'No unresolved path endings remain.';
      if (choices.length) {
        reason = `${choices.length} starting output/input choice${choices.length === 1 ? '' : 's'} still need manual review.`;
      } else if (unresolved.length) {
        reason = `${unresolved.length} unresolved path ending${unresolved.length === 1 ? '' : 's'} remain.`;
      } else if (manualReview.length) {
        reason = `${manualReview.length} path ending${manualReview.length === 1 ? '' : 's'} are marked for manual review.`;
      }
      return {
        candidates,
        unresolved,
        choices,
        manualReview,
        acceptedReportBoundaries,
        unresolvedCount: unresolved.length,
        choiceCount: choices.length,
        manualReviewCount: manualReview.length,
        acceptedReportBoundaryCount: acceptedReportBoundaries.length,
        needsWork,
        reason
      };
    },

    caseManualStartAllFlowsAssessmentForFlow(flow = {}, flowIndex = 0) {
      const identity = this.caseManualStartAllFlowsFlowIdentity(flow, flowIndex);
      return this.caseManualStartAllFlowsFlowAssessments?.[identity] || this.caseManualStartAllFlowsAssessFlow(flow);
    },

    caseManualStartAllFlowsFlowNeedsWork(flow = {}, flowIndex = 0) {
      return this.caseManualStartAllFlowsAssessmentForFlow(flow, flowIndex).needsWork;
    },

    caseManualStartAllFlowsFlowStatusLabel(flow = {}) {
      const assessment = this.caseManualStartAllFlowsAssessmentForFlow(flow);
      if (assessment.manualReviewCount) return 'Needs review';
      if (assessment.choiceCount) return 'Choice needed';
      if (assessment.unresolvedCount) return `${assessment.unresolvedCount} unresolved`;
      if (assessment.acceptedReportBoundaryCount) return 'Report-ready';
      return 'Complete';
    },

    caseManualStartAllFlowsFlowStatusClass(flow = {}) {
      const assessment = this.caseManualStartAllFlowsAssessmentForFlow(flow);
      return {
        'is-complete': !assessment.needsWork,
        'is-attention': assessment.needsWork,
        'is-manual': assessment.manualReviewCount > 0,
        'is-qualified': !assessment.needsWork && assessment.acceptedReportBoundaryCount > 0
      };
    },

    caseManualStartAllFlowsFindFlowIndex(target = {}, flows = null) {
      const list = Array.isArray(flows) ? flows : (Array.isArray(this.caseInvestigationVisualizerFlows) ? this.caseInvestigationVisualizerFlows : []);
      const identity = this.caseManualNormalizeText(target?.identity);
      const key = this.caseManualNormalizeText(target?.key);
      const hash = this.caseManualNormalizeText(target?.mainHash).toLowerCase();
      const label = this.caseManualNormalizeText(target?.mainLabel).toLowerCase();
      let index = list.findIndex((flow, flowIndex) => identity && this.caseManualStartAllFlowsFlowIdentity(flow, flowIndex) === identity);
      if (index >= 0) return index;
      index = list.findIndex(flow => key && this.caseManualNormalizeText(flow?.key) === key);
      if (index >= 0) return index;
      index = list.findIndex(flow => {
        const flowHash = this.caseManualNormalizeText(flow?.mainHash || flow?.candidate?.hash).toLowerCase();
        const flowLabel = this.caseManualNormalizeText(flow?.mainLabel || flow?.candidate?.pathLabel).toLowerCase();
        return Boolean(hash && flowHash === hash && (!label || !flowLabel || flowLabel === label));
      });
      return index;
    },

    caseManualStartAllFlowsBuildQueue(options = {}) {
      const flows = Array.isArray(this.caseInvestigationVisualizerFlows) ? this.caseInvestigationVisualizerFlows : [];
      if (!flows.length) return [];
      const activeIndex = Math.max(0, Math.min(flows.length - 1, Number(this.caseInvestigationVisualizerActiveIndex) || 0));
      const order = [activeIndex, ...flows.map((flow, index) => index).filter(index => index !== activeIndex)];
      const queue = order.map(index => {
        const flow = flows[index] || {};
        return {
          identity: this.caseManualStartAllFlowsFlowIdentity(flow, index),
          key: flow.key || '',
          mainHash: flow.mainHash || flow?.candidate?.hash || '',
          mainLabel: flow.mainLabel || flow?.candidate?.pathLabel || `Graph ${index + 1}`,
          initialIndex: index,
          assessment: this.caseManualStartAllFlowsAssessFlow(flow)
        };
      }).filter(item => options.includeComplete === true || item.assessment.needsWork);
      return queue.slice(0, CASE_MANUAL_START_ALL_FLOWS_AUTO_FLOW_LIMIT);
    },

    caseManualStartAllFlowsRunIsCurrent(runId) {
      const state = this.caseManualStartAllFlowsAuto || {};
      const currentFileId = this.caseManualNormalizeText(this.currentProjectFileId || this.selectedProjectFileId || '');
      return Boolean(
        state.active
          && Number(state.runId) === Number(runId)
          && this.showCaseInvestigationVisualizerModal
          && this.caseInvestigationVisualizerManualStartMode
          && (!state.caseFileId || state.caseFileId === currentFileId)
      );
    },

    caseManualStartAllFlowsSleep(delayMs = 0) {
      const delay = Math.max(0, Number(delayMs) || 0);
      return new Promise(resolve => {
        if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') window.setTimeout(resolve, delay);
        else setTimeout(resolve, delay);
      });
    },

    caseManualStartAllFlowsRecordCompleted(target = {}) {
      const identity = this.caseManualNormalizeText(target.identity || target.key);
      const completed = new Set(Array.isArray(this.caseManualStartAllFlowsAuto?.completedFlowKeys) ? this.caseManualStartAllFlowsAuto.completedFlowKeys : []);
      if (identity) completed.add(identity);
      const attention = (Array.isArray(this.caseManualStartAllFlowsAuto?.attentionFlows) ? this.caseManualStartAllFlowsAuto.attentionFlows : [])
        .filter(row => this.caseManualNormalizeText(row?.identity || row?.key) !== identity);
      this.caseManualStartAllFlowsAuto = {
        ...(this.caseManualStartAllFlowsAuto || {}),
        completedFlowKeys: Array.from(completed),
        attentionFlows: attention
      };
    },

    caseManualStartAllFlowsRecordAttention(target = {}, reason = '', assessment = null) {
      const identity = this.caseManualNormalizeText(target.identity || target.key);
      const completed = (Array.isArray(this.caseManualStartAllFlowsAuto?.completedFlowKeys) ? this.caseManualStartAllFlowsAuto.completedFlowKeys : [])
        .filter(key => this.caseManualNormalizeText(key) !== identity);
      const attention = (Array.isArray(this.caseManualStartAllFlowsAuto?.attentionFlows) ? this.caseManualStartAllFlowsAuto.attentionFlows.slice() : [])
        .filter(row => this.caseManualNormalizeText(row?.identity || row?.key) !== identity);
      attention.push({
        identity,
        key: target.key || '',
        mainHash: target.mainHash || '',
        mainLabel: target.mainLabel || '',
        label: target.mainLabel || `Graph ${Number(target.initialIndex || 0) + 1}`,
        reason: this.caseManualNormalizeText(reason) || assessment?.reason || 'Manual review is still needed.',
        unresolvedCount: Number(assessment?.unresolvedCount || 0),
        choiceCount: Number(assessment?.choiceCount || 0),
        manualReviewCount: Number(assessment?.manualReviewCount || 0)
      });
      this.caseManualStartAllFlowsAuto = {
        ...(this.caseManualStartAllFlowsAuto || {}),
        completedFlowKeys: completed,
        attentionFlows: attention
      };
    },

    caseManualStartAllFlowsResetTransientUi() {
      if (this.caseManualStartOutputChoiceOpen && !this.caseManualStartOutputChoiceLoading) this.closeCaseManualStartOutputChoice?.();
      if (this.caseManualStartHopInspectorPreviewOpen && !this.caseManualStartHopInspectorRunning) this.closeCaseManualStartHopInspectorPreview?.();
      if (this.caseManualStartHopInspectorOpen && !this.caseManualStartHopInspectorRunning && !this.caseManualStartHopInspectorTraceLoading) this.closeCaseManualStartHopInspector?.();
      this.clearCaseManualStartViewportCompletionCheckTimers?.();
      if (this.caseManualStartViewportCompletionEffect?.visible) this.clearCaseManualStartViewportCompletionEffect?.();
      this.clearCaseManualStartViewportAutomationCallouts?.();
    },

    caseManualStartAllFlowsRebuildState(target = null) {
      const rows = this.getCaseManualStartVisualizerRows({ includeAll: true });
      this.caseInvestigationVisualizerState = this.buildCaseManualStartVisualizerState(rows);
      this.cacheCaseManualStartVisualizerPreparedState(rows, this.caseInvestigationVisualizerState);
      const flows = Array.isArray(this.caseInvestigationVisualizerState?.flows) ? this.caseInvestigationVisualizerState.flows : [];
      let index = target ? this.caseManualStartAllFlowsFindFlowIndex(target, flows) : Number(this.caseInvestigationVisualizerActiveIndex || 0);
      if (index < 0) index = Math.max(0, Math.min(flows.length - 1, Number(target?.initialIndex) || 0));
      if (flows.length) {
        this.caseInvestigationVisualizerViewMode = 'single';
        this.caseInvestigationVisualizerActiveIndex = index;
        this.caseInvestigationVisualizerLastFlowKey = flows[index]?.key || '';
        this.setCaseManualStartCurrentVisual({ mode: 'single' });
      } else {
        this.caseInvestigationVisualizerCurrentVisual = null;
      }
      return flows[index] || null;
    },

    async caseManualStartAllFlowsWaitForSingleAuto(runId) {
      const deadline = Date.now() + CASE_MANUAL_START_ALL_FLOWS_AUTO_FLOW_TIMEOUT_MS;
      let lastProgressAt = Date.now();
      let lastProgressSignature = '';
      while (this.caseManualStartAllFlowsRunIsCurrent(runId) && this.caseManualStartViewportAutoActive) {
        if (this.caseManualStartAllFlowsAuto?.stopRequested) {
          this.stopCaseManualStartViewportAuto('Guided Auto stopped the current graph at the next safe point.', { silent: true, fromAllFlows: true });
          return 'stopped';
        }
        const progressSignature = [
          Number(this.caseManualStartViewportAuto?.nextActionCount || 0),
          Number(this.caseManualStartHopInspectorAppliedCount || 0),
          this.caseManualNormalizeText(this.caseManualStartViewportAuto?.status || ''),
          this.caseManualNormalizeText(this.caseManualStartHopInspectorStatus || ''),
          this.caseManualStartHopInspectorRunning ? 'running' : 'idle',
          this.caseManualStartHopInspectorTraceLoading ? 'trace-loading' : 'trace-idle',
          this.caseManualStartOutputChoiceLoading ? 'choice-loading' : 'choice-idle'
        ].join('|');
        if (progressSignature !== lastProgressSignature) {
          lastProgressSignature = progressSignature;
          lastProgressAt = Date.now();
        }
        if (Date.now() - lastProgressAt >= CASE_MANUAL_START_ALL_FLOWS_AUTO_STALL_MS) {
          this.stopCaseManualStartViewportAuto('Guided Auto set this graph aside because it made no visible progress for two minutes.', { silent: true, fromAllFlows: true });
          return 'stalled';
        }
        if (Date.now() >= deadline) {
          this.stopCaseManualStartViewportAuto('Guided Auto moved on after this graph exceeded its per-graph time limit.', { silent: true, fromAllFlows: true });
          return 'flow-timeout';
        }
        await this.caseManualStartAllFlowsSleep(CASE_MANUAL_START_ALL_FLOWS_AUTO_POLL_MS);
      }
      return this.caseManualStartAllFlowsAuto?.stopRequested ? 'stopped' : 'finished';
    },

    async caseManualStartAllFlowsWaitForIdle(runId) {
      const deadline = Date.now() + CASE_MANUAL_START_ALL_FLOWS_AUTO_IDLE_TIMEOUT_MS;
      while (this.caseManualStartAllFlowsRunIsCurrent(runId) && this.caseManualStartViewportAutoIsBusy?.()) {
        if (this.caseManualStartAllFlowsAuto?.stopRequested && this.caseManualStartHopInspectorRunning) {
          this.requestCaseManualStartHopInspectorStop?.();
        }
        if (Date.now() >= deadline) return false;
        await this.caseManualStartAllFlowsSleep(CASE_MANUAL_START_ALL_FLOWS_AUTO_POLL_MS);
      }
      return !this.caseManualStartViewportAutoIsBusy?.();
    },

    startCaseManualStartAllFlowsAuto(options = {}) {
      if (!CASE_MANUAL_START_ALL_FLOWS_AUTO_ENABLED) {
        this.showCaseToast?.('Guided all-graph Auto is disabled by the app configuration.');
        return false;
      }
      if (this.caseManualStartAllFlowsAutoActive) return false;
      if (this.caseManualStartViewportAutoActive) {
        this.showCaseToast?.('Stop the current per-graph Auto before starting guided Auto for every graph.');
        return false;
      }
      if (!this.caseInvestigationVisualizerManualStartMode || !this.showCaseInvestigationVisualizerModal) {
        this.showCaseToast?.('Open Flow Solver in Manual Solve mode before starting guided Auto.');
        return false;
      }
      if (this.caseManualStartViewportOtherAutomationRunning || this.caseInvestigationVisualizerInteractionLocked || this.caseGlobalOperation?.active || this.caseModalOperation?.active) {
        this.showCaseToast?.('Another Case Buddy operation is running. Finish or stop it before starting guided Auto.');
        return false;
      }

      const queue = this.caseManualStartAllFlowsBuildQueue({ includeComplete: options.onlyUnfinished !== true });
      const caseFlowCount = Array.isArray(this.caseInvestigationVisualizerFlows) ? this.caseInvestigationVisualizerFlows.length : 0;
      this.dismissCaseManualSolveIntro({ focusVisualizer: false });
      this.clearCaseManualStartViewportCompletionCheckTimers?.();
      if (this.caseManualStartViewportCompletionEffect?.visible) this.clearCaseManualStartViewportCompletionEffect?.();

      if (!queue.length) {
        const runId = Number(this.caseManualStartAllFlowsAuto?.runId || 0) + 1;
        this.caseManualStartAllFlowsAuto = this.caseManualStartAllFlowsDefaultState({
          runId,
          caseFlowCount,
          totalFlowCount: 0,
          completedFlowKeys: (this.caseInvestigationVisualizerFlows || []).map((flow, index) => this.caseManualStartAllFlowsFlowIdentity(flow, index)),
          status: 'Every graph already appears complete.',
          detail: 'No unresolved path endings remain.',
          lastResult: 'complete',
          resultVisible: true
        });
        this.$nextTick(() => this.$refs.caseManualStartAllFlowsResultPrimaryButton?.focus?.());
        return true;
      }

      const runId = Number(this.caseManualStartAllFlowsAuto?.runId || 0) + 1;
      this.caseInvestigationVisualizerViewMode = 'single';
      this.caseManualStartAllFlowsAuto = this.caseManualStartAllFlowsDefaultState({
        active: true,
        runId,
        startedAtMs: Date.now(),
        caseFileId: this.caseManualNormalizeText(this.currentProjectFileId || this.selectedProjectFileId || ''),
        caseFlowCount,
        totalFlowCount: queue.length,
        status: 'Preparing the first unfinished graph…',
        detail: 'Guided Auto will continue through the remaining graphs and collect anything that still needs manual review.'
      });
      this.caseInvestigationVisualizerStatus = 'Guided Flow Solver is preparing every unfinished starting graph.';
      this.$nextTick(() => {
        this.$refs.caseManualStartAllFlowsStopButton?.focus?.();
        this.runCaseManualStartAllFlowsAuto(runId, queue).catch((error) => {
          if (!this.caseManualStartAllFlowsRunIsCurrent(runId)) return;
          this.finishCaseManualStartAllFlowsAuto(runId, queue, {
            result: 'error',
            message: `Guided Auto stopped after an error: ${error?.message || 'unknown error'}.`
          });
        });
      });
      return true;
    },

    async runCaseManualStartAllFlowsAuto(runId, queue = []) {
      let forcedResult = '';
      let forcedMessage = '';
      const overallDeadline = Number(this.caseManualStartAllFlowsAuto?.startedAtMs || Date.now()) + (CASE_MANUAL_START_ALL_FLOWS_AUTO_MAX_SECONDS * 1000);
      try {
        for (let queueIndex = 0; queueIndex < queue.length; queueIndex += 1) {
          if (!this.caseManualStartAllFlowsRunIsCurrent(runId)) return;
          if (this.caseManualStartAllFlowsAuto?.stopRequested) {
            forcedResult = 'stopped';
            break;
          }
          if (Date.now() >= overallDeadline) {
            forcedResult = 'timeout';
            forcedMessage = 'Guided Auto reached its overall time limit and returned control safely.';
            this.requestCaseManualStartAllFlowsAutoStop(forcedMessage, { silent: true });
            break;
          }

          const target = queue[queueIndex];
          const flowIndex = this.caseManualStartAllFlowsFindFlowIndex(target);
          if (flowIndex < 0) {
            this.caseManualStartAllFlowsRecordAttention(target, 'This graph could not be found after the case data refreshed.');
            continue;
          }

          this.caseManualStartAllFlowsResetTransientUi();
          this.renderCaseInvestigationVisualizerAtIndex(flowIndex, { mode: 'single' });
          await this.$nextTick();
          await this.caseManualStartAllFlowsSleep(140);
          if (!this.caseManualStartAllFlowsRunIsCurrent(runId)) return;

          let flow = this.caseInvestigationVisualizerCurrentFlow || this.caseInvestigationVisualizerFlows[flowIndex] || {};
          let assessment = this.caseManualStartAllFlowsAssessFlow(flow);
          this.caseManualStartAllFlowsAuto = {
            ...(this.caseManualStartAllFlowsAuto || {}),
            currentFlowNumber: queueIndex + 1,
            currentFlowKey: target.identity,
            currentFlowLabel: target.mainLabel || flow.mainLabel || `Graph ${flowIndex + 1}`,
            currentFlowUnresolvedCount: assessment.unresolvedCount + assessment.choiceCount,
            status: assessment.needsWork ? 'Starting Auto on this graph…' : 'This graph is already complete.',
            detail: assessment.reason
          };

          if (!assessment.needsWork) {
            this.caseManualStartAllFlowsRecordCompleted(target);
            await this.caseManualStartAllFlowsSleep(Math.min(180, CASE_MANUAL_START_ALL_FLOWS_AUTO_SETTLE_MS));
            continue;
          }

          if (!assessment.unresolvedCount && !assessment.choiceCount && assessment.manualReviewCount) {
            this.caseManualStartAllFlowsRecordAttention(target, assessment.reason, assessment);
            await this.caseManualStartAllFlowsSleep(CASE_MANUAL_START_ALL_FLOWS_AUTO_SETTLE_MS);
            continue;
          }

          this.startCaseManualStartViewportAuto({ silent: true, allFlows: true });
          if (!this.caseManualStartViewportAutoActive) {
            this.caseManualStartAllFlowsRecordAttention(target, this.caseManualStartViewportAutoButtonTitle || 'Per-graph Auto could not start.', assessment);
            continue;
          }

          this.caseManualStartAllFlowsAuto = {
            ...(this.caseManualStartAllFlowsAuto || {}),
            status: `Auto is working on ${target.mainLabel || flow.mainLabel || `graph ${flowIndex + 1}`}.`,
            detail: 'Case Buddy will move on automatically when this graph is complete or reaches a safe stopping point.'
          };

          const waitResult = await this.caseManualStartAllFlowsWaitForSingleAuto(runId);
          const idle = await this.caseManualStartAllFlowsWaitForIdle(runId);
          if (!this.caseManualStartAllFlowsRunIsCurrent(runId)) return;
          if (this.caseManualStartAllFlowsAuto?.stopRequested) {
            forcedResult = forcedResult || 'stopped';
            break;
          }
          if (!idle) {
            forcedResult = 'error';
            forcedMessage = 'Flow Solver did not return to an idle state after a stop request, so guided Auto ended before switching graphs.';
            break;
          }

          const autoStopStatus = this.caseManualNormalizeText(this.caseManualStartViewportAuto?.status || '');
          this.caseManualStartAllFlowsResetTransientUi();
          flow = this.caseManualStartAllFlowsRebuildState(target) || flow;
          assessment = this.caseManualStartAllFlowsAssessFlow(flow);
          this.caseManualStartAllFlowsAuto = {
            ...(this.caseManualStartAllFlowsAuto || {}),
            currentFlowUnresolvedCount: assessment.unresolvedCount + assessment.choiceCount,
            status: assessment.needsWork ? 'This graph still needs attention.' : 'This graph is complete.',
            detail: assessment.needsWork ? assessment.reason : 'No unresolved path endings remain in this graph.'
          };

          if (!assessment.needsWork) {
            this.caseManualStartAllFlowsRecordCompleted(target);
          } else {
            const flowReason = waitResult === 'flow-timeout'
              ? 'This graph reached the per-graph time limit. ' + assessment.reason
              : (waitResult === 'stalled'
                ? 'This graph made no visible progress for two minutes and was set aside. ' + assessment.reason
                : [autoStopStatus, assessment.reason].filter(Boolean).join(' '));
            this.caseManualStartAllFlowsRecordAttention(target, flowReason, assessment);
          }
          await this.caseManualStartAllFlowsSleep(CASE_MANUAL_START_ALL_FLOWS_AUTO_SETTLE_MS);
        }
      } catch (error) {
        forcedResult = 'error';
        forcedMessage = `Guided Auto stopped after an error: ${error?.message || 'unknown error'}.`;
      }

      if (this.caseManualStartAllFlowsRunIsCurrent(runId)) {
        await this.finishCaseManualStartAllFlowsAuto(runId, queue, {
          result: forcedResult,
          message: forcedMessage
        });
      }
    },

    async finishCaseManualStartAllFlowsAuto(runId, queue = [], options = {}) {
      if (!this.caseManualStartAllFlowsRunIsCurrent(runId)) return false;
      if (this.caseManualStartViewportAutoActive) {
        this.stopCaseManualStartViewportAuto('Guided Auto finished the current graph pass.', { silent: true, fromAllFlows: true });
      }
      await this.caseManualStartAllFlowsWaitForIdle(runId);
      if (!this.caseManualStartAllFlowsRunIsCurrent(runId)) return false;
      this.caseManualStartAllFlowsResetTransientUi();

      const currentTarget = queue.find(row => row.identity === this.caseManualStartAllFlowsAuto?.currentFlowKey) || null;
      this.caseManualStartAllFlowsRebuildState(currentTarget);
      const flows = Array.isArray(this.caseInvestigationVisualizerFlows) ? this.caseInvestigationVisualizerFlows : [];
      const attentionByIdentity = new Map();
      (Array.isArray(this.caseManualStartAllFlowsAuto?.attentionFlows) ? this.caseManualStartAllFlowsAuto.attentionFlows : []).forEach(row => {
        attentionByIdentity.set(this.caseManualNormalizeText(row?.identity || row?.key), { ...row });
      });
      const completed = [];
      flows.forEach((flow, flowIndex) => {
        const identity = this.caseManualStartAllFlowsFlowIdentity(flow, flowIndex);
        const assessment = this.caseManualStartAllFlowsAssessFlow(flow);
        if (assessment.needsWork) {
          const existing = attentionByIdentity.get(identity) || {};
          attentionByIdentity.set(identity, {
            ...existing,
            identity,
            key: flow.key || existing.key || '',
            mainHash: flow.mainHash || existing.mainHash || '',
            mainLabel: flow.mainLabel || existing.mainLabel || '',
            label: flow.mainLabel || existing.label || `Graph ${flowIndex + 1}`,
            reason: existing.reason || assessment.reason,
            unresolvedCount: assessment.unresolvedCount,
            choiceCount: assessment.choiceCount,
            manualReviewCount: assessment.manualReviewCount
          });
        } else {
          completed.push(identity);
          attentionByIdentity.delete(identity);
        }
      });
      queue.forEach((target) => {
        const found = this.caseManualStartAllFlowsFindFlowIndex(target, flows) >= 0;
        if (!found) {
          const identity = this.caseManualNormalizeText(target.identity || target.key);
          attentionByIdentity.set(identity, {
            identity,
            key: target.key || '',
            mainHash: target.mainHash || '',
            mainLabel: target.mainLabel || '',
            label: target.mainLabel || `Graph ${Number(target.initialIndex || 0) + 1}`,
            reason: 'This graph was no longer available after the case data refreshed.'
          });
        }
      });

      const attentionFlows = Array.from(attentionByIdentity.values());
      const expectedFlowCount = Math.max(Number(this.caseManualStartAllFlowsAuto?.caseFlowCount || 0), flows.length);
      const allComplete = attentionFlows.length === 0 && flows.length >= expectedFlowCount;
      let lastResult = allComplete ? 'complete' : (options.result || (this.caseManualStartAllFlowsAuto?.stopRequested ? 'stopped' : 'partial'));
      if (!['complete', 'stopped', 'timeout', 'error', 'partial'].includes(lastResult)) lastResult = 'partial';
      const status = allComplete
        ? `All ${flows.length} starting graph${flows.length === 1 ? '' : 's'} are complete.`
        : (options.message || `${attentionFlows.length} graph${attentionFlows.length === 1 ? '' : 's'} still need review.`);

      this.caseManualStartAllFlowsAuto = {
        ...(this.caseManualStartAllFlowsAuto || {}),
        active: false,
        stopRequested: false,
        currentFlowUnresolvedCount: 0,
        caseFlowCount: expectedFlowCount,
        completedFlowKeys: completed,
        attentionFlows,
        status,
        detail: allComplete
          ? 'No unresolved path endings remain. Continue with the Workflow Assistant after closing Flow Solver.'
          : 'Completed work remains saved. Review or retry only the unfinished graphs.',
        lastResult,
        resultVisible: true
      };
      this.caseInvestigationVisualizerStatus = status;
      if (this.autoSaveEnabled && (this.caseManualStartAutosavePending || this.caseManualStartAutosaveTimer)) {
        this.flushCaseManualStartAutosave?.({ force: true, silent: true, reason: 'Guided Flow Solver finished its all-graph pass.' });
      }
      this.$nextTick(() => this.$refs.caseManualStartAllFlowsResultPrimaryButton?.focus?.());
      return true;
    },

    requestCaseManualStartAllFlowsAutoStop(message = 'Guided Auto stop requested.', options = {}) {
      if (!this.caseManualStartAllFlowsAutoActive) return false;
      this.caseManualStartAllFlowsAuto = {
        ...(this.caseManualStartAllFlowsAuto || {}),
        stopRequested: true,
        status: 'Stopping guided Auto…',
        detail: this.caseManualNormalizeText(message) || 'The current action will stop at the next safe point.'
      };
      if (this.caseManualStartViewportAutoActive) {
        this.stopCaseManualStartViewportAuto('Guided Auto stopped the current graph at the next safe point.', { silent: true, fromAllFlows: true });
      } else if (this.caseManualStartHopInspectorRunning) {
        this.requestCaseManualStartHopInspectorStop?.();
      }
      if (!options.silent) this.showCaseToast?.('Guided Auto stop requested. Completed work will remain in the case.');
      return true;
    },

    abortCaseManualStartAllFlowsAuto(message = 'Guided Auto stopped.', options = {}) {
      const wasActive = this.caseManualStartAllFlowsAutoActive;
      const nextRunId = Number(this.caseManualStartAllFlowsAuto?.runId || 0) + 1;
      this.caseManualStartAllFlowsAuto = this.caseManualStartAllFlowsDefaultState({
        runId: nextRunId,
        stopRequested: true,
        status: message,
        lastResult: 'aborted',
        resultVisible: false
      });
      if (this.caseManualStartViewportAutoActive) {
        this.stopCaseManualStartViewportAuto(message, { silent: true, noToolStop: options.noToolStop === true, fromAllFlows: true });
      } else if (!options.noToolStop && this.caseManualStartHopInspectorRunning) {
        this.requestCaseManualStartHopInspectorStop?.();
      }
      if (wasActive && !options.silent) this.showCaseToast?.(message);
      return wasActive;
    },

    dismissCaseManualStartAllFlowsResult() {
      this.caseManualStartAllFlowsAuto = {
        ...(this.caseManualStartAllFlowsAuto || {}),
        resultVisible: false
      };
      this.$nextTick(() => this.$refs.caseInvestigationVisualizerModal?.focus?.());
    },

    reviewCaseManualStartAllFlowsFirstAttention() {
      const target = (Array.isArray(this.caseManualStartAllFlowsAuto?.attentionFlows) ? this.caseManualStartAllFlowsAuto.attentionFlows : [])[0];
      if (!target) {
        this.dismissCaseManualStartAllFlowsResult();
        return false;
      }
      const flowIndex = this.caseManualStartAllFlowsFindFlowIndex(target);
      this.dismissCaseManualStartAllFlowsResult();
      if (flowIndex < 0) {
        this.showCaseToast?.('That unfinished graph is no longer available in the current case data.');
        return false;
      }
      this.renderCaseInvestigationVisualizerAtIndex(flowIndex, { mode: 'single' });
      this.caseInvestigationVisualizerStatus = `${target.label || 'This graph'} still needs review. ${target.reason || ''}`.trim();
      return true;
    },

    retryCaseManualStartAllFlowsAuto() {
      this.dismissCaseManualStartAllFlowsResult();
      return this.startCaseManualStartAllFlowsAuto({ source: 'retry', onlyUnfinished: true });
    },

    closeCaseManualStartAllFlowsAndReturn() {
      this.dismissCaseManualStartAllFlowsResult();
      return this.cancelCaseInvestigationVisualizer();
    },

    clearCaseManualStartViewportAutoTimer() {
      const timer = this.caseManualStartViewportAuto?.timer;
      if (timer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(timer);
      }
      if (this.caseManualStartViewportAuto) this.caseManualStartViewportAuto.timer = null;
    },

    toggleCaseManualStartViewportAuto() {
      if (this.caseManualStartAllFlowsAutoActive) {
        this.requestCaseManualStartAllFlowsAutoStop('Guided Auto stop requested by the user.');
        return;
      }
      if (this.caseManualStartViewportAutoActive) {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto stopped by user.');
        return;
      }
      this.startCaseManualStartViewportAuto();
    },

    startCaseManualStartViewportAuto(options = {}) {
      if (this.caseManualStartAllFlowsAutoActive && options.allFlows !== true) {
        const message = 'Guided Auto is already working through every graph. Stop it before starting a separate per-graph Auto run.';
        if (!options.silent && typeof this.showCaseToast === 'function') this.showCaseToast(message);
        return false;
      }
      if (this.caseManualStartViewportOtherAutomationRunning) {
        const message = 'Another Case Buddy automation is already running. Stop it before starting Manual Solve viewport Auto.';
        if (!options.silent && typeof this.showCaseToast === 'function') this.showCaseToast(message);
        return false;
      }
      if (!this.caseManualStartViewportAutoCanStart) {
        const message = 'Manual Solve viewport Auto cannot start yet. Open the Manual Solve visualizer and wait for any current action to finish.';
        if (!options.silent && typeof this.showCaseToast === 'function') this.showCaseToast(message);
        return false;
      }
      if (this.caseManualSolveIntroOpen) this.dismissCaseManualSolveIntro({ focusVisualizer: false });
      this.clearCaseManualStartViewportAutoTimer();
      this.caseManualStartViewportAuto = {
        active: true,
        stopRequested: false,
        timer: null,
        startedAtMs: Date.now(),
        nextActionCount: 0,
        hopApplyBaseline: Number(this.caseManualStartHopInspectorAppliedCount || 0),
        signatureCounts: {},
        bridgeNoOutputCounts: {},
        swapNoOutputCounts: {},
        traceNoProgressCounts: {},
        status: 'Manual Solve Auto is running for this viewport only.',
        detail: 'Case Buddy will press Next when safe and stop at manual choices, loops, limits, completion, or modal close.'
      };
      this.caseInvestigationVisualizerStatus = this.caseManualStartViewportAuto.status;
      if (!options.silent && typeof this.showCaseToast === 'function') this.showCaseToast('Manual Solve Auto started. It is contained to this viewport and will stop at manual choices.');
      this.scheduleCaseManualStartViewportAutoTick(120);
      return true;
    },

    stopCaseManualStartViewportAuto(message = 'Manual Solve Auto stopped.', options = {}) {
      const wasActive = this.caseManualStartViewportAutoActive;
      this.clearCaseManualStartViewportAutoTimer();
      if (!options.noToolStop && this.caseManualStartHopInspectorRunning && typeof this.requestCaseManualStartHopInspectorStop === 'function') {
        try {
          this.requestCaseManualStartHopInspectorStop();
        } catch (error) {
          console.warn('Could not request Manual Solve hop inspector stop from viewport Auto.', error);
        }
      }
      this.caseManualStartViewportAuto = {
        ...(this.caseManualStartViewportAuto || {}),
        active: false,
        stopRequested: true,
        timer: null,
        status: message,
        detail: ''
      };
      if (wasActive && message) this.caseInvestigationVisualizerStatus = message;
      if (wasActive && (this.caseManualStartAutosavePending || this.caseManualStartAutosaveTimer)) {
        this.flushCaseManualStartAutosave({ reason: 'Manual Solve Auto stopped after case-data changes.', silent: true });
      }
      const guidedAllFlowsActive = Boolean(this.caseManualStartAllFlowsAuto?.active);
      if (wasActive && options.completed && !guidedAllFlowsActive) this.showCaseManualStartViewportCompletionEffect(message);
      if (wasActive && !options.silent && !guidedAllFlowsActive && typeof this.showCaseToast === 'function') this.showCaseToast(message);
    },

    clearCaseManualStartViewportCompletionEffect() {
      const timer = this.caseManualStartViewportCompletionEffect?.timer;
      const glowTimer = this.caseManualStartViewportCompletionEffect?.glowTimer;
      if (timer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(timer);
      }
      if (glowTimer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(glowTimer);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener?.('pointermove', this.moveCaseManualStartViewportCompletionEffectDrag);
        window.removeEventListener?.('pointerup', this.endCaseManualStartViewportCompletionEffectDrag);
      }
      this.caseManualStartViewportCompletionEffect = {
        visible: false,
        message: '',
        timer: null,
        glowTimer: null,
        persistent: false,
        startedAtMs: 0,
        borderGlow: false,
        position: { x: null, y: null },
        dragging: false,
        dragState: null
      };
    },

    showCaseManualStartViewportCompletionEffect(message = '') {
      const displayMessage = this.caseManualNormalizeText(message) || 'No unresolved path endings remain in this Manual Solve viewport.';
      const previousTimer = this.caseManualStartViewportCompletionEffect?.timer;
      const previousGlowTimer = this.caseManualStartViewportCompletionEffect?.glowTimer;
      if (previousTimer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(previousTimer);
      }
      if (previousGlowTimer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
        window.clearTimeout(previousGlowTimer);
      }
      const persistentForQuickLookup = Boolean(this.caseQuickLookup?.manualSolveLaunched && this.caseQuickLookupVisible);
      let timer = null;
      let glowTimer = null;
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
        if (!persistentForQuickLookup) {
          timer = window.setTimeout(() => this.clearCaseManualStartViewportCompletionEffect(), CASE_MANUAL_START_VIEWPORT_COMPLETION_VISIBLE_MS);
        }
        glowTimer = window.setTimeout(() => {
          this.caseManualStartViewportCompletionEffect = {
            ...(this.caseManualStartViewportCompletionEffect || {}),
            borderGlow: false,
            glowTimer: null
          };
        }, CASE_MANUAL_START_VIEWPORT_COMPLETION_GLOW_MS);
      }
      this.caseManualStartViewportCompletionEffect = {
        visible: true,
        message: displayMessage,
        timer,
        glowTimer,
        persistent: persistentForQuickLookup,
        startedAtMs: Date.now(),
        borderGlow: true,
        position: { x: null, y: null },
        dragging: false,
        dragState: null
      };
      if (persistentForQuickLookup) {
        this.$nextTick(() => this.refreshCaseQuickLookupManualSolveResult?.({ source: 'lookup-flow-completion-effect' }));
      }
    },


    getCaseManualStartViewportUnresolvedEndingCount() {
      if (!this.caseInvestigationVisualizerManualStartMode) return 0;
      const candidates = [];
      const addCandidate = (candidate) => {
        if (candidate?.caseManualStartCandidate) candidates.push(candidate);
      };
      const currentFlow = this.caseInvestigationVisualizerCurrentFlow || {};
      addCandidate(currentFlow.candidate);
      (Array.isArray(currentFlow.entries) ? currentFlow.entries : []).forEach(addCandidate);
      const visual = this.caseInvestigationVisualizerCurrentVisual || {};
      (Array.isArray(visual.entries) ? visual.entries : []).forEach(addCandidate);
      (Array.isArray(visual.nodes) ? visual.nodes : []).forEach((node) => {
        addCandidate(node?.caseCandidate);
        addCandidate(node?.candidate);
      });
      const seen = new Set();
      let count = 0;
      candidates.forEach((candidate) => {
        if (!this.isCaseManualStartUnresolvedEndingCandidate(candidate)) return;
        const key = this.buildCaseManualStartHopInspectorCandidateKey(candidate) || JSON.stringify([
          candidate.hash || candidate.txHash || candidate.transactionHash || '',
          this.caseManualStartCandidateEndpointAddress(candidate),
          candidate.pathLabel || '',
          this.caseManualStartCandidateEndpointRole(candidate)
        ]);
        if (seen.has(key)) return;
        seen.add(key);
        count += 1;
      });
      return count;
    },

    buildCaseManualStartViewportCompletionSignature() {
      if (!this.caseInvestigationVisualizerManualStartMode) return '';
      const visual = this.caseInvestigationVisualizerCurrentVisual || {};
      const flow = this.caseInvestigationVisualizerCurrentFlow || {};
      const txHashes = (Array.isArray(visual.entries) ? visual.entries : [])
        .map(entry => this.caseManualNormalizeText(entry.hash || entry.txHash || entry.transactionHash).toLowerCase())
        .filter(Boolean)
        .sort();
      const nodeIds = (Array.isArray(visual.nodes) ? visual.nodes : [])
        .map(node => this.caseManualNormalizeText(node.id))
        .filter(Boolean)
        .sort();
      return [flow.key || visual.key || '', txHashes.join(','), nodeIds.length, this.getCaseManualStartViewportUnresolvedEndingCount()].join('|');
    },

    clearCaseManualStartViewportCompletionCheckTimers() {
      const timers = [this.caseManualStartViewportCompletionCheckTimer, this.caseManualStartViewportCompletionVerifyTimer];
      timers.forEach((timer) => {
        if (timer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timer);
      });
      this.caseManualStartViewportCompletionCheckTimer = null;
      this.caseManualStartViewportCompletionVerifyTimer = null;
    },

    queueCaseManualStartViewportCompletionCheck(reason = '') {
      if (this.caseManualStartAllFlowsAutoActive) return;
      if (!this.showCaseInvestigationVisualizerModal || !this.caseInvestigationVisualizerManualStartMode) return;
      this.clearCaseManualStartViewportCompletionCheckTimers();
      if (typeof window === 'undefined' || typeof window.setTimeout !== 'function') return;
      this.caseManualStartViewportCompletionCheckTimer = window.setTimeout(() => {
        this.caseManualStartViewportCompletionCheckTimer = null;
        const firstCount = this.getCaseManualStartViewportUnresolvedEndingCount();
        if (firstCount !== 0 || this.caseManualStartViewportAutoIsBusy?.()) return;
        this.caseManualStartViewportCompletionVerifyTimer = window.setTimeout(() => {
          this.caseManualStartViewportCompletionVerifyTimer = null;
          if (!this.showCaseInvestigationVisualizerModal || !this.caseInvestigationVisualizerManualStartMode || this.caseManualStartAllFlowsAutoActive) return;
          if (this.getCaseManualStartViewportUnresolvedEndingCount() !== 0 || this.caseManualStartViewportAutoIsBusy?.()) return;
          const signature = this.buildCaseManualStartViewportCompletionSignature();
          if (signature && signature === this.caseManualStartViewportLastCompletionSignature) return;
          this.caseManualStartViewportLastCompletionSignature = signature;
          const cleanReason = this.caseManualNormalizeText(reason);
          const message = cleanReason
            ? `Flow complete. ${cleanReason}. No unresolved path endings remain after confirmation.`
            : 'Flow complete. No unresolved path endings remain after confirmation.';
          this.showCaseManualStartViewportCompletionEffect(message);
        }, CASE_MANUAL_START_VIEWPORT_COMPLETION_VERIFY_DELAY_MS);
      }, CASE_MANUAL_START_VIEWPORT_COMPLETION_FIRST_CHECK_DELAY_MS);
    },

    beginCaseManualStartViewportCompletionEffectDrag(event) {
      if (!event || event.button !== 0) return;
      const target = event.target;
      if (target?.closest?.('button, a, input, textarea, select, summary, details, [role="button"], .toolbar-btn')) return;
      const panel = this.$refs.caseManualStartViewportCompletionEffectPanel;
      if (!panel || typeof panel.getBoundingClientRect !== 'function') return;
      const container = this.$refs.caseInvestigationVisualizerViewportEl || panel.parentElement || panel.offsetParent || null;
      if (!container || typeof container.getBoundingClientRect !== 'function') return;
      const panelRect = panel.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      this.caseManualStartViewportCompletionEffect = {
        ...(this.caseManualStartViewportCompletionEffect || {}),
        position: { x: panelRect.left - containerRect.left, y: panelRect.top - containerRect.top },
        dragging: true,
        dragState: {
          offsetX: event.clientX - panelRect.left,
          offsetY: event.clientY - panelRect.top,
          panelWidth: panelRect.width,
          panelHeight: panelRect.height,
          containerLeft: containerRect.left,
          containerTop: containerRect.top,
          containerWidth: containerRect.width,
          containerHeight: containerRect.height
        }
      };
      if (typeof panel.setPointerCapture === 'function' && event.pointerId !== undefined) {
        try { panel.setPointerCapture(event.pointerId); } catch (error) { console.warn('Case Buddy manual completion notice pointer capture failed.', error); }
      }
      window.addEventListener('pointermove', this.moveCaseManualStartViewportCompletionEffectDrag, { passive: false });
      window.addEventListener('pointerup', this.endCaseManualStartViewportCompletionEffectDrag, { once: true });
      event.preventDefault();
    },

    moveCaseManualStartViewportCompletionEffectDrag(event) {
      const state = this.caseManualStartViewportCompletionEffect?.dragState;
      if (!state || !this.caseManualStartViewportCompletionEffect?.dragging) return;
      const maxX = Math.max(8, state.containerWidth - state.panelWidth - 8);
      const maxY = Math.max(8, state.containerHeight - state.panelHeight - 8);
      const x = this.caseManualClampNumber(event.clientX - state.containerLeft - state.offsetX, 8, maxX);
      const y = this.caseManualClampNumber(event.clientY - state.containerTop - state.offsetY, 8, maxY);
      this.caseManualStartViewportCompletionEffect = {
        ...(this.caseManualStartViewportCompletionEffect || {}),
        position: { x, y }
      };
      event.preventDefault();
    },

    endCaseManualStartViewportCompletionEffectDrag() {
      if (!this.caseManualStartViewportCompletionEffect?.dragging) return;
      this.caseManualStartViewportCompletionEffect = {
        ...(this.caseManualStartViewportCompletionEffect || {}),
        dragging: false,
        dragState: null
      };
      window.removeEventListener('pointermove', this.moveCaseManualStartViewportCompletionEffectDrag);
    },

    normalizeCaseManualStartHopInspectorActionKey(action = '') {
      const text = this.caseManualNormalizeText(action).toLowerCase();
      if (!text) return '';
      const directKey = text.replace(/[\s_]+/giu, '-');
      if (['trace', 'trace-through', 'trace-preview', 'traced-through', 'manual-trace-through'].includes(directKey)) return 'trace-through';
      if (['swap', 'treat-as-swap', 'solve-as-swap', 'manual-swap'].includes(directKey)) return 'swap';
      if (['bridge', 'treat-as-bridge', 'solve-as-bridge', 'manual-bridge'].includes(directKey)) return 'bridge';
      if (['path-ending', 'path-end', 'end-path', 'manual-path-ending', 'mark-as-path-ending'].includes(directKey)) return 'path-ending';
      const withoutGenericReviewLists = text
        .replace(/review\s+this\s+transaction\s+before\s+tracing\s+through[^.]*\./giu, ' ')
        .replace(/consider\s+(?:mark\s+as\s+path\s+ending|treat\s+as\s+swap|treat\s+as\s+bridge|path\s+ending|swap|bridge)(?:\s*,\s*(?:mark\s+as\s+path\s+ending|treat\s+as\s+swap|treat\s+as\s+bridge|path\s+ending|swap|bridge))*[^.]*\./giu, ' ');
      if (/trace[-_\s]*(?:through|preview)\s+(?:selected\s+)?(?:address|ending)|recommended\s*(?:action)?[^.\n]*(?:trace[-_\s]*through|trace\s+preview)|action\s*[=:]\s*traced?[-_\s]*through|manual[-_\s]*trace[-_\s]*through|use\s+trace\s+preview|next\s+hop|exit\s+row|selected\s+this\s+transaction\s+as\s+the\s+next\s+hop/u.test(withoutGenericReviewLists)) return 'trace-through';
      if (/(?:recommended\s*(?:action)?[^.\n]*|auto[-_\s]*decide[^.\n]*|treat\s+as\s+|manual[-_\s]*)bridge|action\s*[=:]\s*bridge|bridge\s*\+\s*apply/u.test(withoutGenericReviewLists)) return 'bridge';
      if (/(?:recommended\s*(?:action)?[^.\n]*|auto[-_\s]*decide[^.\n]*|treat\s+as\s+|manual[-_\s]*)swap|action\s*[=:]\s*swap|swap\s*\+\s*apply/u.test(withoutGenericReviewLists)) return 'swap';
      if (/path[-_\s]*(?:end|ending)|manual[-_\s]*path[-_\s]*ending|end\s+path|path_end|path-ending|path\s+ends|mark\s+as\s+path\s+ending|action\s*[=:]\s*manual[-_\s]*path[-_\s]*ending/u.test(withoutGenericReviewLists)) return 'path-ending';
      if (/trace[-_\s]*through|trace\s+preview|continues\s+as/u.test(withoutGenericReviewLists)) return 'trace-through';
      return '';
    },

    normalizeCaseManualStartHopInspectorExplicitRecommendedActionKey(action = '') {
      const text = this.caseManualNormalizeText(action).toLowerCase();
      if (!text) return '';
      const directKey = text.replace(/[\s_]+/giu, '-');
      if (['trace', 'trace-through', 'trace-preview', 'traced-through', 'manual-trace-through'].includes(directKey)) return 'trace-through';
      if (['swap', 'treat-as-swap', 'solve-as-swap', 'manual-swap'].includes(directKey)) return 'swap';
      if (['bridge', 'treat-as-bridge', 'solve-as-bridge', 'manual-bridge'].includes(directKey)) return 'bridge';
      if (['path-ending', 'path-end', 'end-path', 'manual-path-ending', 'mark-as-path-ending'].includes(directKey)) return 'path-ending';
      if (/trace[-_\s]*(?:through|preview)\s+(?:selected\s+)?(?:address|ending)|trace[^.\n]*(?:selected|target)[^.\n]*address|(?:selected|target)[^.\n]*address[^.\n]*trace|continue\s+(?:to\s+)?trace|action\s*[=:]\s*traced?[-_\s]*through|manual[-_\s]*trace[-_\s]*through|use\s+trace\s+preview|button\s*label[^\n]*(?:trace|preview)/u.test(text)) return 'trace-through';
      if (/action\s*[=:]\s*bridge|manual[-_\s]*bridge|bridge\s*\+\s*apply|treat\s+as\s+bridge|auto[-_\s]*decide\s*:?\s*bridge/u.test(text)) return 'bridge';
      if (/action\s*[=:]\s*swap|manual[-_\s]*swap|swap\s*\+\s*apply|treat\s+as\s+swap|auto[-_\s]*decide\s*:?\s*swap/u.test(text)) return 'swap';
      if (/action\s*[=:]\s*manual[-_\s]*path[-_\s]*ending|mark\s+as\s+path\s+ending|end\s+path\s*\+\s*apply|path[-_\s]*ending\s*\+\s*apply/u.test(text)) return 'path-ending';
      return '';
    },

    caseManualStartRowLooksSolvedBridgeReceive(row = []) {
      if (!Array.isArray(row)) return false;
      const text = [
        row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row?.[CASE_MANUAL_START_COL.CASE_NOTES],
        row?.[CASE_MANUAL_START_COL.HOP_LOGIC],
        row?.[CASE_MANUAL_START_COL.CHAIN_ID],
        row?.[CASE_MANUAL_START_COL.CHAIN_NAME],
      ].map(value => this.caseManualNormalizeText(value)).join(' | ').toLowerCase();
      return Boolean(/\bbridge\s*\/\s*(?:fifo|lifo|next)|tool\s*88:\s*bridge\s+solved|bridge\s+solved|post[-\s]?bridge|destination:\s*[^|.\n]+\s+on\s+[^|.\n]+|likely\s+receiving\s+transaction|funds\s+are\s+received\s+on\s+the\s+.+network\s+via/.test(text));
    },

    caseManualStartRowLooksBridgeInitiation(row = []) {
      if (!Array.isArray(row)) return false;
      if (this.caseManualStartRowLooksSolvedBridgeReceive(row)) return false;
      const systemText = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES]).toLowerCase();
      const attributionText = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.ATTRIBUTION]).toLowerCase();
      const caseNotesText = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.CASE_NOTES]).toLowerCase();
      const hopText = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.HOP_LOGIC]).toLowerCase();
      const combined = [systemText, attributionText, caseNotesText, hopText].filter(Boolean).join(' | ');
      const explicitInitiation = /funds\s+are\s+bridged\s+(?:using|to)|bridged\s+to\s+the\s+.+network\s+using|bridge\s+initiation|manual[-\s]?bridge\s+candidate|destination\s+chain|to\s*token|minreturn|dst\s*chain|to\s*chain/.test(combined);
      if (explicitInitiation) return true;
      const serviceOnly = /\btransitswap\b|\btransit\s+swap\b|\bswft\b|\bswft\s+blockchain\b|\bwormhole\b|\ballbridge\b|\bbridgers\b|\brango\b|\bchainflip\b|\bbutterswap\b|\bnear[-\s]?intents\b/.test([attributionText, systemText, caseNotesText].filter(Boolean).join(' | '));
      return Boolean(serviceOnly && !/bridge\s*\/\s*(?:fifo|lifo|next)|post[-\s]?bridge|received\s+on|likely\s+receiving\s+transaction|bridge\s+solved/.test(combined));
    },

    caseManualStartCandidateHasSolvedBridgeAncestor(candidate = null) {
      if (!candidate?.caseManualStartCandidate) return false;
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate?.(candidate)
        || (Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []));
      if (this.caseManualStartRowLooksSolvedBridgeReceive(currentRow)) return true;
      const toolRows = this.caseManualStartRowsForTool88?.() || this.getCaseManualStartMainMatrix?.() || [];
      const dataRows = Array.isArray(toolRows) && toolRows.length && this.caseManualIsHeaderRow(toolRows[0]) ? toolRows.slice(1) : (Array.isArray(toolRows) ? toolRows : []);
      const currentHash = this.caseManualNormalizeText(currentRow?.[CASE_MANUAL_START_COL.TX_HASH] || candidate.hash || candidate.txHash || candidate.transactionHash || '').toLowerCase();
      const currentPath = this.caseManualNormalizeText(currentRow?.[CASE_MANUAL_START_COL.PATH_LABEL] || candidate.pathLabel || '').toLowerCase();
      const currentChain = this.caseManualNormalizeText(currentRow?.[CASE_MANUAL_START_COL.CHAIN_ID] || currentRow?.[CASE_MANUAL_START_COL.CHAIN_NAME] || candidate.chainId || candidate.chainName || '').toLowerCase();
      const currentIndex = dataRows.findIndex((row) => {
        const rowHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        const rowPath = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        return Boolean((currentHash && rowHash === currentHash) && (!currentPath || rowPath === currentPath));
      });
      let parentHash = this.caseManualNormalizeText(
        currentRow?.[CASE_MANUAL_START_COL.FROM_HASH]
          || candidate.fromHash
          || candidate.parentHash
          || ''
      ).toLowerCase();
      const seen = new Set();
      for (let depth = 0; depth < 12 && parentHash && !seen.has(parentHash); depth += 1) {
        seen.add(parentHash);
        const parentRows = dataRows.filter(row => this.caseManualTransactionHashesMatch(row?.[CASE_MANUAL_START_COL.TX_HASH], parentHash));
        const parentRow = parentRows.find(row => this.caseManualStartRowLooksSolvedBridgeReceive(row)) || parentRows[parentRows.length - 1];
        if (!parentRow) break;
        if (this.caseManualStartRowLooksSolvedBridgeReceive(parentRow)) return true;
        parentHash = this.caseManualNormalizeText(parentRow?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
      }
      if (currentIndex > 0 && currentChain) {
        for (let index = currentIndex - 1; index >= 0 && index >= currentIndex - 16; index -= 1) {
          const prior = dataRows[index];
          if (!prior) continue;
          const priorChain = this.caseManualNormalizeText(prior?.[CASE_MANUAL_START_COL.CHAIN_ID] || prior?.[CASE_MANUAL_START_COL.CHAIN_NAME] || '').toLowerCase();
          if (priorChain && priorChain !== currentChain) continue;
          if (this.caseManualStartRowLooksSolvedBridgeReceive(prior)) return true;
        }
      }
      return false;
    },

    caseManualStartCandidateIsPostBridgeTraceContinuation(candidate = null, snapshot = {}) {
      if (!candidate?.caseManualStartCandidate) return false;
      // This helper describes a forward destination-chain continuation. Reverse
      // ancestry uses the source side and lets Flow Solver assess bridge evidence
      // for that source directly instead of inheriting forward bridge state.
      if (this.caseManualStartCandidateIsReverse(candidate)) return false;
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate?.(candidate)
        || (Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []));
      if (!currentRow || !Array.isArray(currentRow)) return false;
      if (!this.caseManualStartCandidateHasSolvedBridgeAncestor(candidate)) return false;
      const selectedAddress = this.caseManualNormalizeText(candidate.toAddress || candidate.address || currentRow?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '').toLowerCase();
      const rowToAddress = this.caseManualNormalizeText(currentRow?.[CASE_MANUAL_START_COL.TO_ADDRESS]).toLowerCase();
      if (selectedAddress && rowToAddress && selectedAddress !== rowToAddress) return false;
      const text = [
        currentRow?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.CASE_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.ATTRIBUTION],
        currentRow?.[CASE_MANUAL_START_COL.HOP_LOGIC],
        candidate.systemNotes,
        candidate.caseNotes,
        candidate.attribution,
        candidate.toAttributionLabel,
        snapshot?.decision?.title,
        snapshot?.decision?.summary,
      ].map(value => this.caseManualNormalizeText(value)).join(' | ').toLowerCase();
      // A downstream row may still carry SWFT/Transit labels from the bridge
      // provider. That is historical context, not a fresh bridge initiation.
      if (/funds\s+are\s+bridged\s+(?:using|to)|manual[-\s]?bridge\s+candidate|bridge\s+initiation/.test(text) && !this.caseManualStartRowLooksSolvedBridgeReceive(currentRow)) return false;
      return true;
    },

    caseManualBridgeMatchForManualStartCandidate(candidate = null, snapshot = {}) {
      if (this.caseManualStartCandidateIsPostBridgeTraceContinuation(candidate, snapshot)) return createCaseBridgeServiceMatch();
      const currentRow = candidate?.caseManualStartCandidate
        ? (this.findCurrentCaseManualStartMainRowForCandidate(candidate) || [])
        : [];
      const rawRow = Array.isArray(candidate?.rawRow)
        ? candidate.rawRow
        : (Array.isArray(candidate?.row) ? candidate.row : []);
      const reverse = this.caseManualStartCandidateIsReverse(candidate || {});
      const activeAttributionColumn = reverse ? CASE_MANUAL_START_COL.FROM_ATTRIBUTION : CASE_MANUAL_START_COL.ATTRIBUTION;
      const collectRowText = (row = []) => [
        row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row?.[activeAttributionColumn],
        row?.[CASE_MANUAL_START_COL.ANALYST_NOTES],
        row?.[CASE_MANUAL_START_COL.CASE_NOTES],
        row?.[CASE_MANUAL_START_COL.CONTINUES_AS],
        row?.[CASE_MANUAL_START_COL.HOP_LOGIC]
      ];
      const selectedRowScopedText = [
        reverse ? candidate?.fromAttributionLabel : candidate?.attribution,
        reverse ? candidate?.fromEntityLabel : candidate?.toAttributionLabel,
        candidate?.systemNotes,
        candidate?.caseNotes,
        candidate?.analystNotes,
        candidate?.continuesAs,
        candidate?.hopLogic,
        ...collectRowText(rawRow),
        ...collectRowText(currentRow)
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' | ');
      const selectedScopedLower = selectedRowScopedText.toLowerCase();
      const selectedIsExplicitBridgeInitiation = this.caseManualStartRowLooksBridgeInitiation(currentRow)
        || /funds\s+are\s+bridged\s+(?:using|to)|bridged\s+to\s+the\s+.+network\s+using|tool\s*88\s+attribution:[^\n|.]*\bbridge\s+like\b|\bbridge\s+initiation\b|manual[-\s]?bridge\s+candidate|destination\s+chain|to\s*token|minreturn/iu.test(selectedScopedLower);
      const selectedIsSolvedBridgeReceive = /\bbridge\s*\/\s*(?:fifo|lifo|next)|bridge\s+solved|funds\s+are\s+received\s+on|received\s+on\s+the\s+.+network|destination[-\s]*chain\s+receive|likely\s+receiving\s+transaction|post[-\s]*bridge/iu.test(selectedScopedLower)
        && !selectedIsExplicitBridgeInitiation;
      const parentLooksSolvedBridgeReceive = (() => {
        const fromHash = this.caseManualNormalizeText(
          candidate?.fromHash
            || currentRow?.[CASE_MANUAL_START_COL.FROM_HASH]
            || rawRow?.[CASE_MANUAL_START_COL.FROM_HASH]
            || ''
        ).toLowerCase();
        if (!fromHash) return false;
        const allRows = [];
        const addRows = rows => {
          const sourceRows = Array.isArray(rows) ? rows : [];
          const dataRows = sourceRows.length && this.caseManualIsHeaderRow(sourceRows[0]) ? sourceRows.slice(1) : sourceRows;
          dataRows.forEach((row) => { if (Array.isArray(row)) allRows.push(row); });
        };
        addRows(this.getCaseManualStartMainMatrix?.());
        addRows(this.caseManualStartRowsForTool88?.());
        addRows(this.caseManualStartHopInspectorOutputRows);
        addRows(snapshot?.outputRows);
        addRows(snapshot?.result?.outputRows);
        const parentRow = allRows.find(row => this.caseManualTransactionHashesMatch(
          this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase(),
          fromHash
        ));
        if (!parentRow) return false;
        const parentText = collectRowText(parentRow).map(value => this.caseManualNormalizeText(value).toLowerCase()).filter(Boolean).join(' | ');
        return /\bbridge\s*\/\s*(?:fifo|lifo|next)|bridge\s+solved|funds\s+are\s+received\s+on|received\s+on\s+the\s+.+network|destination[-\s]*chain\s+receive|likely\s+receiving\s+transaction|post[-\s]*bridge/iu.test(parentText)
          && !/funds\s+are\s+bridged\s+(?:using|to)|bridged\s+to\s+the\s+.+network\s+using/iu.test(parentText);
      })();
      if ((selectedIsSolvedBridgeReceive || parentLooksSolvedBridgeReceive) && !selectedIsExplicitBridgeInitiation) {
        return createCaseBridgeServiceMatch();
      }

      const selectedRegistryMatch = typeof findBestCaseBridgeServiceMatchFromRegistry === 'function'
        ? findBestCaseBridgeServiceMatchFromRegistry(selectedRowScopedText)
        : null;
      if (selectedRegistryMatch?.isMatch) return selectedRegistryMatch;
      const selectedMatch = findBestCaseBridgeServiceMatch(selectedRowScopedText);
      if (selectedMatch?.isMatch) return selectedMatch;

      // Only a current Tool 88 decision for the same selected candidate may create
      // a bridge recommendation once the selected row itself does not contain
      // bridge-service evidence. Nearby output rows frequently include the prior
      // solved bridge row, and those must not bleed into ordinary post-bridge hops.
      const snapshotCandidateKey = this.caseManualNormalizeText(snapshot?.selectedEntry?.key || snapshot?.selectedEntryKey || '').toLowerCase();
      const selectedHash = this.caseManualNormalizeText(candidate?.hash || candidate?.txHash || candidate?.transactionHash || currentRow?.[CASE_MANUAL_START_COL.TX_HASH] || '').toLowerCase();
      const selectedPath = this.caseManualNormalizeText(candidate?.pathLabel || currentRow?.[CASE_MANUAL_START_COL.PATH_LABEL] || '').toLowerCase();
      const snapshotLooksSelected = Boolean(snapshotCandidateKey && selectedHash && snapshotCandidateKey.includes(selectedHash))
        || Boolean(selectedHash && this.caseManualNormalizeText(snapshot?.selectedEntry?.txHash || '').toLowerCase() === selectedHash)
        || Boolean(selectedPath && this.caseManualNormalizeText(snapshot?.selectedEntry?.pathLabel || '').toLowerCase() === selectedPath);
      const decisionAction = this.normalizeCaseManualStartHopInspectorExplicitRecommendedActionKey([
        snapshot?.decision?.action,
        snapshot?.decision?.recommendedAction,
        snapshot?.decision?.buttonLabel,
        snapshot?.decision?.title
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' | '));
      if (snapshotLooksSelected && decisionAction === 'bridge') {
        const decisionText = [
          snapshot?.decision?.title,
          snapshot?.decision?.summary,
          snapshot?.message,
          snapshot?.result?.bridge?.title,
          snapshot?.result?.bridge?.summary,
          snapshot?.result?.bridge?.note
        ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' | ');
        const registryMatch = typeof findBestCaseBridgeServiceMatchFromRegistry === 'function'
          ? findBestCaseBridgeServiceMatchFromRegistry(decisionText)
          : null;
        if (registryMatch?.isMatch) return registryMatch;
        const match = findBestCaseBridgeServiceMatch(decisionText);
        if (match?.isMatch) return match;
        return createCaseBridgeServiceMatch({ isMatch: true, canonicalName: 'Bridge', matchedAlias: 'bridge', source: 'tool88-decision' });
      }
      return createCaseBridgeServiceMatch();
    },

    caseManualManualStartCandidateLooksKnownBridge(candidate = null, snapshot = {}) {
      if (!candidate?.caseManualStartCandidate) return false;
      if (!this.isCaseManualStartPathEndingCandidate(candidate)) return false;
      if (this.caseManualStartCandidateIsPostBridgeTraceContinuation(candidate, snapshot)) return false;
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate(candidate) || [];
      const rawRow = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const contextText = [
        candidate.hopLogic,
        candidate.systemNotes,
        candidate.caseNotes,
        candidate.analystNotes,
        rawRow?.[CASE_MANUAL_START_COL.HOP_LOGIC],
        rawRow?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        rawRow?.[CASE_MANUAL_START_COL.CASE_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.HOP_LOGIC],
        currentRow?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.CASE_NOTES],
        snapshot?.message,
        snapshot?.decision?.summary,
        snapshot?.result?.summary,
      ].map(value => this.caseManualNormalizeText(value).toLowerCase()).filter(Boolean).join(' | ');
      const looksLikePostBridgeReceive = /\bbridge\s*\/\s*(fifo|lifo|next)|post[-\s]*bridge|destination[-\s]*chain\s+receive|funds\s+are\s+received\s+on|received\s+on\s+the\s+.+network|received\s+via\s+/iu.test(contextText)
        && !/funds\s+are\s+bridged\s+(?:to|using)|bridged\s+to\s+the\s+.+network\s+using/iu.test(contextText);
      if (looksLikePostBridgeReceive) return false;
      const match = this.caseManualBridgeMatchForManualStartCandidate(candidate, snapshot);
      if (match?.isMatch) return true;
      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const activeAttributionColumn = reverse ? CASE_MANUAL_START_COL.FROM_ATTRIBUTION : CASE_MANUAL_START_COL.ATTRIBUTION;
      const text = [
        reverse ? candidate.fromAttributionLabel : candidate.attribution,
        reverse ? candidate.fromEntityLabel : candidate.toAttributionLabel,
        candidate.systemNotes,
        candidate.caseNotes,
        rawRow?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        rawRow?.[activeAttributionColumn],
        rawRow?.[CASE_MANUAL_START_COL.CASE_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        currentRow?.[activeAttributionColumn],
        currentRow?.[CASE_MANUAL_START_COL.CASE_NOTES],
      ].map(value => this.caseManualNormalizeText(value).toLowerCase()).filter(Boolean).join(' | ');
      return /\bfunds\s+are\s+bridged\s+(?:to|using)\b|\bbridge\b|cross[-\s]?chain|destination\s+chain|to\s*chain|dst\s*chain|swft|transit\s*swap|transitswap|wormhole|allbridge|bridgers|rango|chainflip|butterswap|near\s+intents/iu.test(text);
    },

    caseManualStartCandidateHasBtcReverseInputSelection(candidate = null) {
      if (!candidate?.caseManualStartCandidate || !this.caseManualStartCandidateIsReverse(candidate)) return false;
      if (this.caseManualInferNetworkFamily(candidate) !== 'btc') return false;
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate(candidate) || [];
      const rawRow = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const text = [
        candidate.caseNotes,
        candidate.systemNotes,
        rawRow?.[CASE_MANUAL_START_COL.CASE_NOTES],
        rawRow?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.CASE_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.SYSTEM_NOTES]
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join('\n');
      return /Tool\s*88\s+BTC\s+reverse\s+input\s+selection\s*:\s*direction\s*=\s*reverse/iu.test(text)
        || /analysis\s+direction\s*=\s*reverse;\s*selected\s+Input\s*#/iu.test(text);
    },

    caseManualStartCandidateLooksKnownReverseSwap(candidate = null, snapshot = {}) {
      if (!candidate?.caseManualStartCandidate || !this.caseManualStartCandidateIsReverse(candidate)) return false;
      if (this.caseManualStartCandidateHasBtcReverseInputSelection(candidate)) return false;
      if (this.caseManualInferNetworkFamily(candidate) === 'btc') return false;
      if (!this.isCaseManualStartPathEndingCandidate(candidate)) return false;
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate(candidate) || [];
      const rawRow = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const text = [
        candidate.fromAttributionLabel,
        candidate.fromEntityLabel,
        candidate.systemNotes,
        candidate.caseNotes,
        candidate.hopLogic,
        rawRow?.[CASE_MANUAL_START_COL.FROM_ATTRIBUTION],
        rawRow?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        rawRow?.[CASE_MANUAL_START_COL.CASE_NOTES],
        rawRow?.[CASE_MANUAL_START_COL.HOP_LOGIC],
        currentRow?.[CASE_MANUAL_START_COL.FROM_ATTRIBUTION],
        currentRow?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.CASE_NOTES],
        currentRow?.[CASE_MANUAL_START_COL.HOP_LOGIC],
        snapshot?.decision?.action,
        snapshot?.decision?.title,
        snapshot?.decision?.summary,
        snapshot?.message,
        snapshot?.result?.swap?.title,
        snapshot?.result?.swap?.summary,
      ].map(value => this.caseManualNormalizeText(value).toLowerCase()).filter(Boolean).join(' | ');
      if (/tool\s*88\s*:\s*reverse\s+swap\s+solved|action\s*=\s*reverse-swap/iu.test(text)) return false;
      return /\btokenlon\b|\b1inch\b|\buniswap\b|\bsushiswap\b|\bcurve\b|\bcowswap\b|\bcow\s+swap\b|\bdodo\b|\bbkswap\b|\buniversal\s*router\b|\b(?:swap|dex|amm|router)\s*(?:like|service|contract)?\b|\bswapped\s+from\b|\baction\s*[=:]\s*swap\b|\btreat\s+as\s+swap\b/iu.test(text);
    },

    resolveCaseManualStartHopInspectorRecommendedActionKey() {
      const snapshot = this.caseManualStartHopInspectorSnapshot || {};
      const candidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || {};
      const outputRows = Array.isArray(this.caseManualStartHopInspectorOutputRows) ? this.caseManualStartHopInspectorOutputRows : [];
      const explicitFields = [
        snapshot.decision?.action,
        snapshot.decision?.recommendedAction,
        snapshot.decision?.buttonLabel,
        snapshot.decision?.title,
        snapshot.integrationAction,
        snapshot.status,
        snapshot.result?.trace?.action,
        snapshot.result?.swap?.action,
        snapshot.result?.bridge?.action,
        this.caseManualStartHopInspectorRecommendationTitle
      ];
      let explicitActionKey = '';
      for (const value of explicitFields) {
        const key = this.normalizeCaseManualStartHopInspectorExplicitRecommendedActionKey(value);
        if (key) {
          explicitActionKey = key;
          break;
        }
      }
      const explicitSummary = [snapshot.decision?.summary, snapshot.message]
        .map(value => this.caseManualNormalizeText(value))
        .filter(Boolean)
        .join('\n');
      const explicitSummaryKey = this.normalizeCaseManualStartHopInspectorExplicitRecommendedActionKey(explicitSummary);
      if (!explicitActionKey && explicitSummaryKey) explicitActionKey = explicitSummaryKey;

      const meaningfulEndpointLabel = candidate?.caseManualStartCandidate ? this.caseManualCandidateMeaningfulEndpointAttributionLabel(candidate) : '';
      const btcReverseInputSelection = this.caseManualStartCandidateHasBtcReverseInputSelection(candidate);
      if (btcReverseInputSelection) return 'trace-through';
      if (this.caseManualStartCandidateIsPostBridgeTraceContinuation(candidate, snapshot)) return 'trace-through';
      if (this.caseManualManualStartCandidateLooksKnownBridge(candidate, snapshot)) return 'bridge';

      // A concrete swap/bridge recommendation describes the transaction itself and
      // must win over a generic service-attribution path-ending rule. This is
      // especially important in Reverse where Tokenlon/DEX addresses sit on the
      // source side of the current transaction.
      if (explicitActionKey === 'swap' || explicitActionKey === 'bridge') return explicitActionKey;
      if (this.caseManualStartCandidateLooksKnownReverseSwap(candidate, snapshot)) return 'swap';
      if (this.caseManualStartAddressPoisoningEvidenceForCandidate(candidate, { snapshot })?.isMatch) return 'path-ending';

      if (meaningfulEndpointLabel && this.isCaseManualStartPathEndingCandidate(candidate)) return 'path-ending';
      const unknownServicePathEndingPending = !meaningfulEndpointLabel && !this.caseManualStartHopInspectorSnapshotHasManualServiceOverride(snapshot) && Boolean(
        this.caseManualStartHopInspectorSnapshotLooksUnknownServicePathEndingForCandidate(snapshot, candidate)
          || this.caseManualLooksLikeUnknownServicePathEnding(candidate)
          || this.caseManualStartHopInspectorHasPendingUnknownServicePathEnding(candidate)
          || this.caseManualStartHopInspectorRowsContainUnknownServicePathEndingEvidenceForCandidate(outputRows, candidate)
      );
      if (unknownServicePathEndingPending) return 'path-ending';
      if (explicitActionKey) return explicitActionKey;
      if (this.caseManualStartSnapshotActionLooksTraceThrough(snapshot)) return 'trace-through';
      const text = [
        snapshot.decision?.summary,
        snapshot.decision?.reasons?.join?.('\n'),
        snapshot.result?.pathEnding?.reason,
        snapshot.result?.pathEnding?.note,
        snapshot.result?.summary,
        snapshot.reportText
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join('\n');
      const directKey = this.normalizeCaseManualStartHopInspectorActionKey(text);
      if (directKey) return directKey;
      return '';
    },

    caseManualStartHopInspectorActionIsRecommended(action = '') {
      if (!this.caseManualStartHopInspectorCanAct) return false;
      const recommendedKey = this.resolveCaseManualStartHopInspectorRecommendedActionKey();
      const actionKey = this.normalizeCaseManualStartHopInspectorActionKey(action);
      return Boolean(recommendedKey && actionKey && recommendedKey === actionKey);
    },

    caseManualStartHopInspectorShouldShowActionButton(action = '') {
      return this.caseManualStartHopInspectorActionIsRecommended(action);
    },

    scheduleCaseManualStartViewportAutoTick(delayMs = CASE_MANUAL_START_VIEWPORT_AUTO_IDLE_DELAY_MS) {
      if (!this.caseManualStartViewportAutoActive) return;
      this.clearCaseManualStartViewportAutoTimer();
      if (typeof window === 'undefined' || typeof window.setTimeout !== 'function') return;
      this.caseManualStartViewportAuto.timer = window.setTimeout(() => {
        this.runCaseManualStartViewportAutoTick();
      }, Math.max(0, Number(delayMs) || 0));
    },

    async runCaseManualStartViewportAutoTick() {
      if (!this.caseManualStartViewportAutoActive) return;
      if (!this.showCaseInvestigationVisualizerModal || !this.caseInvestigationVisualizerManualStartMode) {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto stopped because the viewport closed.', { silent: true });
        return;
      }
      if (this.caseManualStartViewportOtherAutomationRunning) {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto stopped because another Case Buddy automation started.');
        return;
      }

      const elapsedSeconds = (Date.now() - Number(this.caseManualStartViewportAuto.startedAtMs || Date.now())) / 1000;
      if (elapsedSeconds >= CASE_MANUAL_START_VIEWPORT_AUTO_MAX_SECONDS) {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto stopped after reaching its time limit.');
        return;
      }
      if (Number(this.caseManualStartViewportAuto.nextActionCount || 0) >= CASE_MANUAL_START_VIEWPORT_AUTO_MAX_NEXT_ACTIONS) {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto stopped after too many Next actions. Review the current path before continuing.');
        return;
      }
      const appliedDelta = Number(this.caseManualStartHopInspectorAppliedCount || 0) - Number(this.caseManualStartViewportAuto.hopApplyBaseline || 0);
      if (appliedDelta >= CASE_MANUAL_START_VIEWPORT_AUTO_MAX_HOP_APPLIES) {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto stopped after solving many hops in this viewport. Review the case data before continuing.');
        return;
      }

      if (this.caseManualStartViewportAutoIsBusy()) {
        this.caseManualStartViewportAuto.status = 'Manual Solve Auto is waiting for the current Tool 88/choice action to finish.';
        this.scheduleCaseManualStartViewportAutoTick(CASE_MANUAL_START_VIEWPORT_AUTO_IDLE_DELAY_MS);
        return;
      }

      if (this.caseManualStartOutputChoiceOpen) {
        if (!this.caseManualStartOutputChoiceCanApply) {
          this.stopCaseManualStartViewportAuto('Manual Solve Auto paused for a manual output/input choice. Select or double-click the correct choice, then restart Auto if desired.');
          return;
        }
      } else if (this.caseManualStartOutputChoiceShouldShow) {
        // Let Next open the chooser, then the next tick will stop if the user must choose.
      } else if (this.caseManualStartViewportAutoFlowIsComplete()) {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto completed this viewport flow. No unresolved path endings remain.', { completed: true });
        return;
      }

      const signature = this.buildCaseManualStartViewportAutoSignature();
      const signatureCounts = { ...(this.caseManualStartViewportAuto.signatureCounts || {}) };
      signatureCounts[signature] = (Number(signatureCounts[signature]) || 0) + 1;
      this.caseManualStartViewportAuto.signatureCounts = signatureCounts;
      if (signatureCounts[signature] > CASE_MANUAL_START_VIEWPORT_AUTO_SAME_SIGNATURE_LIMIT) {
        this.stopCaseManualStartViewportAuto('Manual Solve Auto stopped because it kept seeing the same state. Review the viewport before continuing.');
        return;
      }

      this.caseManualStartViewportAuto.nextActionCount = Number(this.caseManualStartViewportAuto.nextActionCount || 0) + 1;
      this.caseManualStartViewportAuto.status = `Manual Solve Auto pressed Next ${this.caseManualStartViewportAuto.nextActionCount} time${this.caseManualStartViewportAuto.nextActionCount === 1 ? '' : 's'} in this viewport.`;
      this.caseInvestigationVisualizerStatus = this.caseManualStartViewportAuto.status;
      try {
        await this.runCaseManualStartNext({ source: 'auto' });
      } catch (error) {
        this.stopCaseManualStartViewportAuto(`Manual Solve Auto stopped after an error: ${error?.message || 'unknown error'}.`);
        return;
      }
      this.scheduleCaseManualStartViewportAutoTick(CASE_MANUAL_START_VIEWPORT_AUTO_ACTION_DELAY_MS);
    },

    async runCaseManualStartNext(options = {}) {
      const previousCalloutContext = this.caseManualStartAutomationCalloutContext;
      this.caseManualStartAutomationCalloutContext = {
        source: options.source || (this.caseManualStartViewportAutoActive ? 'auto' : 'next'),
        enabled: true,
        startedAtMs: Date.now()
      };
      try {
        if (!this.caseInvestigationVisualizerManualStartMode || this.caseInvestigationVisualizerInteractionLocked) return null;
        if (this.caseManualSolveIntroOpen) this.dismissCaseManualSolveIntro({ focusVisualizer: false });
      if (this.caseManualStartHopInspectorRunning || this.caseManualStartHopInspectorTraceLoading || this.caseManualStartOutputChoiceLoading) {
        const message = 'Manual Solve is already working. Wait for the current Tool 88/choice action to finish, then press Next again.';
        if (typeof this.showCaseToast === 'function') this.showCaseToast(message);
        return null;
      }
      if (this.caseManualStartOutputChoiceOpen) {
        if (this.caseManualStartOutputChoiceCanApply) return await this.applyCaseManualStartOutputChoice();
        if (!this.caseManualStartOutputChoiceRows.length) return await this.loadCaseManualStartOutputChoices();
        const message = 'Select an output/input choice, or double-click the correct choice to apply it.';
        this.caseManualStartOutputChoiceStatus = message;
        if (typeof this.showCaseToast === 'function') this.showCaseToast(message);
        return null;
      }
      if (this.caseManualStartHopInspectorPreviewOpen) {
        if (this.caseManualStartHopInspectorPreviewHasRows) return await this.confirmCaseManualStartHopInspectorPreviewApply();
        this.closeCaseManualStartHopInspectorPreview();
      }
      let candidate = this.getCaseManualStartActivePathEndingCandidate?.() || null;
      if (!candidate?.caseManualStartCandidate || !this.isCaseManualStartUnresolvedEndingCandidate(candidate)) {
        candidate = this.selectCaseManualStartPathEndingNode();
      }
      if (!candidate?.caseManualStartCandidate || !this.isCaseManualStartUnresolvedEndingCandidate(candidate)) {
        const message = 'No unresolved Manual Solve path ending is available. The current visual may already be complete or needs manual review.';
        this.caseInvestigationVisualizerStatus = message;
        if (this.caseManualStartHopInspectorOpen) this.caseManualStartHopInspectorStatus = message;
        if (this.getCaseManualStartViewportUnresolvedEndingCount?.() === 0) this.queueCaseManualStartViewportCompletionCheck('Next found no unresolved path endings');
        if (typeof this.showCaseToast === 'function') this.showCaseToast(message);
        return null;
      }
      if (this.caseManualStartCandidateHasInvalidZeroReverseAllocation(candidate)) {
        const reason = 'This reverse source row has a positive transaction amount but zero Funds Followed. A sub-cent source allocation was likely reduced to zero by older formatting, so tracing it again would not produce a meaningful result.';
        if (this.caseManualStartViewportAutoActive || options.source === 'auto') {
          const marked = await this.markCaseManualStartCandidateFurtherManualWork(candidate, `${reason} Rebuild or review the parent reverse allocation before continuing this branch.`);
          if (marked?.changed || marked?.rowIndex >= 0) {
            const message = 'Manual Solve Auto set aside a reverse row whose Funds Followed allocation had become zero and will continue with the remaining paths.';
            this.caseManualStartViewportAuto.status = message;
            this.caseInvestigationVisualizerStatus = message;
          }
          return marked;
        }
        this.caseManualStartHopInspectorStatus = `${reason} Re-run the parent allocation or correct Funds Followed before tracing this ending.`;
        this.caseInvestigationVisualizerStatus = this.caseManualStartHopInspectorStatus;
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        return null;
      }
      if (candidate?.caseManualStartCandidate && this.caseManualStartCandidateNeedsOutputChoice(candidate)) {
        this.caseManualStartOutputChoiceCandidate = candidate;
        this.caseInvestigationVisualizerSelectedCandidate = candidate;
        await this.openCaseManualStartOutputChoiceFromSelection();
        return null;
      }
      if (!this.caseManualStartHopInspectorOpen) {
        if (candidate?.caseManualStartCandidate) {
          this.caseManualStartHopInspectorCandidate = candidate;
          this.caseInvestigationVisualizerSelectedCandidate = candidate;
          this.selectCaseManualStartPathEndingNode(candidate);
          this.openCaseManualStartHopInspectorFromSelection({ candidate });
          return null;
        }
        const message = 'No unresolved Manual Solve path ending is available. The current visual may already be complete or needs manual review.';
        this.caseInvestigationVisualizerStatus = message;
        if (typeof this.showCaseToast === 'function') this.showCaseToast(message);
        return null;
      }
      if (this.caseManualStartHopInspectorNeedsChecks || !this.caseManualStartHopInspectorChecksReady) {
        return await this.loadCaseManualStartHopInspectorCandidate({ force: true });
      }
      const recommendedActionKey = this.resolveCaseManualStartHopInspectorRecommendedActionKey();
      if (this.caseManualStartTraceAnywayBlocked && recommendedActionKey === 'trace-through') {
        this.caseManualStartHopInspectorStatus = this.caseManualStartTraceAnywayBlockedMessage;
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        return null;
      }
      if (this.caseManualStartHopInspectorCanAct) {
        if (recommendedActionKey === 'swap') {
          const activeSwapCandidate = candidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection?.({ quiet: true }) || null;
          const swapResult = await this.runCaseManualStartHopInspectorRecommendation({ apply: true, previewTrace: true, force: true });
          if (this.caseManualStartViewportAutoActive) {
            const swapKey = this.buildCaseManualStartHopInspectorCandidateKey(activeSwapCandidate) || this.caseManualStartViewportAutoSignatureForCandidate(activeSwapCandidate);
            const swapCounts = { ...(this.caseManualStartViewportAuto.swapNoOutputCounts || {}) };
            if (!swapResult || this.caseManualStartSwapActionProducedNoOutput(swapResult, activeSwapCandidate)) {
              const nextCount = (Number(swapCounts[swapKey]) || 0) + 1;
              swapCounts[swapKey] = nextCount;
              this.caseManualStartViewportAuto.swapNoOutputCounts = swapCounts;
              if (nextCount >= CASE_MANUAL_START_VIEWPORT_AUTO_SWAP_NO_OUTPUT_LIMIT) {
                const directionLabel = this.caseManualStartCandidateIsReverse(activeSwapCandidate) ? 'Reverse' : 'Forward';
                const marked = await this.markCaseManualStartCandidateFurtherManualWork(
                  activeSwapCandidate,
                  `${directionLabel} swap solver could not determine one safe sent/received asset pair after ${nextCount} automatic attempt${nextCount === 1 ? '' : 's'}. Manual swap review required.`
                );
                if (marked?.changed || marked?.rowIndex >= 0) {
                  this.caseManualStartViewportAuto.status = 'Manual Solve Auto marked an unsolved swap for manual review and will continue with the remaining paths.';
                  this.caseInvestigationVisualizerStatus = this.caseManualStartViewportAuto.status;
                }
              } else {
                this.caseManualStartViewportAuto.status = `Manual Solve Auto is retrying the swap solver for this ending (${nextCount}/${CASE_MANUAL_START_VIEWPORT_AUTO_SWAP_NO_OUTPUT_LIMIT}).`;
                this.caseInvestigationVisualizerStatus = this.caseManualStartViewportAuto.status;
              }
            } else if (swapKey && Object.prototype.hasOwnProperty.call(swapCounts, swapKey)) {
              delete swapCounts[swapKey];
              this.caseManualStartViewportAuto.swapNoOutputCounts = swapCounts;
            }
          }
          return swapResult;
        }
        if (recommendedActionKey === 'bridge') {
          const activeBridgeCandidate = candidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection?.({ quiet: true }) || null;
          const bridgeResult = await this.runCaseManualStartHopInspectorToolAction('bridge', { apply: true, preview: true, force: true });
          if (this.caseManualStartViewportAutoActive && this.caseManualStartBridgeActionProducedNoOutput(bridgeResult)) {
            const bridgeKey = this.buildCaseManualStartHopInspectorCandidateKey(activeBridgeCandidate) || this.caseManualStartViewportAutoSignatureForCandidate(activeBridgeCandidate);
            const bridgeCounts = { ...(this.caseManualStartViewportAuto.bridgeNoOutputCounts || {}) };
            const nextCount = (Number(bridgeCounts[bridgeKey]) || 0) + 1;
            bridgeCounts[bridgeKey] = nextCount;
            this.caseManualStartViewportAuto.bridgeNoOutputCounts = bridgeCounts;
            if (nextCount >= CASE_MANUAL_START_VIEWPORT_AUTO_BRIDGE_NO_OUTPUT_LIMIT) {
              const marked = await this.markCaseManualStartCandidateFurtherManualWork(
                activeBridgeCandidate,
                `Tool 88 bridge solver did not produce an exportable destination-chain receive row after ${nextCount} automatic attempt${nextCount === 1 ? '' : 's'}. Manual bridge review required.`
              );
              if (marked?.changed || marked?.rowIndex >= 0) {
                this.caseManualStartViewportAuto.status = 'Manual Solve Auto marked an unsolved bridge for manual review and will continue with the remaining paths.';
                this.caseInvestigationVisualizerStatus = this.caseManualStartViewportAuto.status;
              }
            } else {
              this.caseManualStartViewportAuto.status = `Manual Solve Auto is retrying the bridge solver for this ending (${nextCount}/${CASE_MANUAL_START_VIEWPORT_AUTO_BRIDGE_NO_OUTPUT_LIMIT}).`;
              this.caseInvestigationVisualizerStatus = this.caseManualStartViewportAuto.status;
            }
          }
          return bridgeResult;
        }
        return await this.runCaseManualStartHopInspectorRecommendation({ apply: true, previewTrace: true });
      }
        const message = 'Manual Solve selected the next unresolved ending, but Tool 88 is not ready for an action yet. Press Next again after checks finish.';
        this.caseManualStartHopInspectorStatus = message;
        if (typeof this.showCaseToast === 'function') this.showCaseToast(message);
        return null;
      } finally {
        this.caseManualStartAutomationCalloutContext = previousCalloutContext || null;
      }
    },

    caseManualStartRowsForTool88() {
      const matrix = this.getCaseManualStartMainMatrix();
      return (Array.isArray(matrix) ? matrix : [])
        .map(row => this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)))))
        .filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }));
    },

    openCaseManualSolveGetDataPanel() {
      if (!this.caseInvestigationVisualizerManualStartMode || !this.caseInvestigationVisualizerCurrentVisual) return;
      if (!this.caseManualSolveGetDataPanelOpen) {
        this.caseManualSolveGetDataPanelOpen = true;
        if (!Number.isFinite(Number(this.caseManualSolveGetDataPanelPosition?.x)) || !Number.isFinite(Number(this.caseManualSolveGetDataPanelPosition?.y))) {
          this.caseManualSolveGetDataPanelPosition = { x: 18, y: 52 };
        }
      }
      this.refreshCaseManualSolveGetDataPanel();
      this.$nextTick(() => {
        const textarea = this.$refs?.caseManualSolveGetDataTextarea;
        if (textarea && typeof textarea.focus === 'function') textarea.focus();
      });
    },

    closeCaseManualSolveGetDataPanel() {
      this.endCaseManualSolveGetDataPanelDrag();
      this.caseManualSolveGetDataPanelOpen = false;
    },

    refreshCaseManualSolveGetDataPanel() {
      const exportState = this.buildCaseManualSolveViewportRawDataExport();
      this.caseManualSolveGetDataDraft = this.caseManualDelimitedRowsToText(exportState.rows, '\t');
      this.caseManualSolveGetDataLastSummary = {
        rowCount: exportState.rowCount,
        sourceLabel: exportState.sourceLabel,
        visualHashCount: exportState.visualHashCount,
        rawSourceCount: exportState.rawSourceCount,
        fallbackSourceCount: exportState.fallbackSourceCount
      };
      this.caseManualSolveGetDataStatus = exportState.rowCount > 0
        ? `Generated ${exportState.rowCount} raw-data row${exportState.rowCount === 1 ? '' : 's'} for the current Manual Solve viewport.`
        : 'No transaction rows were found in the current Manual Solve viewport.';
    },

    beginCaseManualSolveGetDataPanelDrag(event) {
      if (!this.caseManualSolveGetDataPanelOpen || !event) return;
      const panel = this.$refs?.caseManualSolveGetDataPanel;
      const container = this.$refs?.caseInvestigationVisualizerViewportEl;
      if (!panel || !container || typeof panel.getBoundingClientRect !== 'function' || typeof container.getBoundingClientRect !== 'function') return;
      const panelRect = panel.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      this.caseManualSolveGetDataPanelPosition = {
        x: panelRect.left - containerRect.left,
        y: panelRect.top - containerRect.top
      };
      this.caseManualSolveGetDataPanelDragging = true;
      this.caseManualSolveGetDataPanelDragState = {
        offsetX: event.clientX - panelRect.left,
        offsetY: event.clientY - panelRect.top,
        panelWidth: panelRect.width,
        panelHeight: panelRect.height,
        containerLeft: containerRect.left,
        containerTop: containerRect.top,
        containerWidth: containerRect.width,
        containerHeight: containerRect.height
      };
      if (typeof panel.setPointerCapture === 'function' && event.pointerId !== undefined) {
        try { panel.setPointerCapture(event.pointerId); } catch (error) { console.warn('Case Buddy GET DATA panel pointer capture failed.', error); }
      }
      window.addEventListener('pointermove', this.moveCaseManualSolveGetDataPanelDrag, { passive: false });
      window.addEventListener('pointerup', this.endCaseManualSolveGetDataPanelDrag, { once: true });
      event.preventDefault();
    },

    moveCaseManualSolveGetDataPanelDrag(event) {
      const state = this.caseManualSolveGetDataPanelDragState;
      if (!state || !this.caseManualSolveGetDataPanelDragging) return;
      const maxX = Math.max(8, state.containerWidth - state.panelWidth - 8);
      const maxY = Math.max(8, state.containerHeight - state.panelHeight - 8);
      const x = this.caseManualClampNumber(event.clientX - state.containerLeft - state.offsetX, 8, maxX);
      const y = this.caseManualClampNumber(event.clientY - state.containerTop - state.offsetY, 8, maxY);
      this.caseManualSolveGetDataPanelPosition = { x, y };
      event.preventDefault();
    },

    endCaseManualSolveGetDataPanelDrag() {
      if (!this.caseManualSolveGetDataPanelDragging && !this.caseManualSolveGetDataPanelDragState) return;
      this.caseManualSolveGetDataPanelDragging = false;
      this.caseManualSolveGetDataPanelDragState = null;
      if (typeof window !== 'undefined') {
        window.removeEventListener('pointermove', this.moveCaseManualSolveGetDataPanelDrag);
      }
    },

    selectCaseManualSolveGetDataText() {
      const textarea = this.$refs?.caseManualSolveGetDataTextarea;
      if (!textarea) return;
      if (typeof textarea.focus === 'function') textarea.focus();
      if (typeof textarea.select === 'function') textarea.select();
    },

    async copyCaseManualSolveGetDataTsv() {
      const text = String(this.caseManualSolveGetDataDraft || '');
      if (!text) {
        this.caseManualSolveGetDataStatus = 'No raw data TSV is available to copy.';
        return;
      }
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          this.caseManualSolveGetDataStatus = 'Copied current viewport raw data TSV to clipboard.';
        } else {
          this.selectCaseManualSolveGetDataText();
          this.caseManualSolveGetDataStatus = 'Select-all is ready. Use your browser copy shortcut.';
        }
      } catch (error) {
        console.warn('Case Buddy could not copy Manual Solve raw data TSV.', error);
        this.selectCaseManualSolveGetDataText();
        this.caseManualSolveGetDataStatus = 'Could not copy automatically. The TSV is selected for manual copy.';
      }
    },

    downloadCaseManualSolveGetDataCsv() {
      let rows = this.caseManualParseDelimitedRowsPreserveCells(this.caseManualSolveGetDataDraft || '', '\t');
      if (!rows.length) {
        const exportState = this.buildCaseManualSolveViewportRawDataExport();
        rows = exportState.rows;
      }
      if (!rows.length) {
        this.caseManualSolveGetDataStatus = 'No rows are available to download.';
        return;
      }
      const csv = this.caseManualDelimitedRowsToText(rows, ',');
      const filename = this.caseManualSolveGetDataFilename('csv');
      try {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        this.caseManualSolveGetDataStatus = `Downloaded ${filename}.`;
      } catch (error) {
        console.warn('Case Buddy could not download Manual Solve raw data CSV.', error);
        this.caseManualSolveGetDataStatus = 'Could not download CSV in this browser. Copy the TSV instead.';
      }
    },

    caseManualSolveGetDataFilename(extension = 'csv') {
      const visual = this.caseInvestigationVisualizerCurrentVisual || {};
      const label = this.caseManualNormalizeText(visual.mainLabel || visual.label || 'manual-solve')
        .replace(/[^a-z0-9_-]+/giu, '-')
        .replace(/^-+|-+$/gu, '')
        .toLowerCase() || 'manual-solve';
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      return `case-buddy-${label}-raw-data-${stamp}.${extension || 'csv'}`;
    },

    buildCaseManualSolveViewportRawDataExport() {
      const visual = this.caseInvestigationVisualizerCurrentVisual || {};
      const visualHashes = this.collectCaseManualSolveCurrentViewportHashKeys(visual);
      const rawRows = this.collectCaseManualSolveRowsForHashesFromSheet('raw', visualHashes);
      const fallbackRows = this.collectCaseManualSolveRowsForHashesFromCurrentViewport(visual, visualHashes);
      const rows = [];
      const seen = new Set();
      const addRow = row => {
        const canonical = this.caseManualCanonicalizeCaseSheetRow(
          this.caseManualEnsureRowWidth(row || [], Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))
        );
        if (!this.caseManualManualSolveRowShouldRetain(canonical, { allowBtcContinuationRows: true })) return;
        const key = this.caseManualSolveRawExportRowKey(canonical, rows.length);
        if (seen.has(key)) return;
        seen.add(key);
        rows.push(canonical);
      };
      const rawHashKeys = rawRows
        .map(row => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]))
        .filter(Boolean);
      rawRows.forEach(addRow);
      fallbackRows.forEach(row => {
        const rowHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]);
        if (rowHash && rawHashKeys.some(rawHash => this.caseManualTransactionHashesMatch(rawHash, rowHash))) return;
        addRow(row);
      });
      const header = this.caseManualCanonicalizeCaseSheetRow(CASE_MANUAL_START_CANONICAL_HEADERS, { isHeader: true });
      const outputRows = [header, ...rows];
      const sourceParts = [];
      if (rawRows.length) sourceParts.push(`${rawRows.length} from Raw Data`);
      if (fallbackRows.length) sourceParts.push(`${fallbackRows.length} viewport fallback`);
      return {
        rows: outputRows,
        rowCount: rows.length,
        visualHashCount: visualHashes.length,
        rawSourceCount: rawRows.length,
        fallbackSourceCount: fallbackRows.length,
        sourceLabel: sourceParts.join(' + ') || 'viewport fallback'
      };
    },

    collectCaseManualSolveCurrentViewportHashKeys(visual = {}) {
      const hashes = [];
      const add = value => {
        const text = this.caseManualNormalizeText(value);
        if (!text || !this.caseManualIsLikelyTransactionHash(text)) return;
        if (hashes.some(existing => this.caseManualTransactionHashesMatch(existing, text))) return;
        hashes.push(text);
      };
      add(visual.mainHash);
      const addFromObject = item => {
        if (!item || typeof item !== 'object') return;
        add(item.hash);
        add(item.txHash);
        add(item.transactionHash);
        add(item.transaction_hash);
        add(item.fromHash);
        add(item.parentHash);
        if (item.caseCandidate) addFromObject(item.caseCandidate);
        if (item.candidate) addFromObject(item.candidate);
      };
      (Array.isArray(visual.entries) ? visual.entries : []).forEach(addFromObject);
      (Array.isArray(visual.nodes) ? visual.nodes : []).forEach(addFromObject);
      return hashes;
    },

    collectCaseManualSolveRowsForHashesFromSheet(logicalSheet = 'main', hashKeys = []) {
      const matrix = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet(logicalSheet) : [];
      return this.collectCaseManualSolveEntryRowsForHashes(matrix, hashKeys);
    },

    collectCaseManualSolveEntryRowsForHashes(matrix = [], hashKeys = []) {
      if (!Array.isArray(matrix) || !hashKeys.length) return [];
      const rows = [];
      let activeInclude = false;
      for (let index = 0; index < matrix.length; index += 1) {
        const sourceRow = Array.isArray(matrix[index]) ? matrix[index] : [];
        const row = this.caseManualEnsureRowWidth(sourceRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceRow.length || 0));
        if (index === 0 && this.caseManualIsHeaderRow(row)) continue;
        const rowHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]);
        if (rowHash) activeInclude = this.caseManualSolveHashMatchesAny(rowHash, hashKeys);
        if (!activeInclude) continue;
        if (!this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true })) continue;
        rows.push(row);
      }
      return rows;
    },

    collectCaseManualSolveRowsForHashesFromCurrentViewport(visual = {}, hashKeys = []) {
      const mainRows = this.collectCaseManualSolveRowsForHashesFromSheet('main', hashKeys);
      if (mainRows.length) return mainRows;
      const sources = [];
      const pushSource = value => {
        if (!value) return;
        if (Array.isArray(value)) value.forEach(pushSource);
        else if (typeof value === 'object') sources.push(value);
      };
      pushSource(visual.entries);
      pushSource((visual.nodes || []).map(node => node?.caseCandidate || node?.candidate || node).filter(Boolean));
      return sources
        .map((entry, index) => this.buildCaseManualStartRowFromVisualEntry(entry, index))
        .filter(row => {
          const hash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]);
          return hash && (!hashKeys.length || this.caseManualSolveHashMatchesAny(hash, hashKeys));
        });
    },

    caseManualSolveHashMatchesAny(hash = '', hashKeys = []) {
      const normalized = this.caseManualNormalizeText(hash);
      if (!normalized) return false;
      return (Array.isArray(hashKeys) ? hashKeys : []).some(candidate => this.caseManualTransactionHashesMatch(normalized, candidate));
    },

    caseManualSolveRawExportRowKey(row = [], fallbackIndex = 0) {
      const hash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const path = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
      const fromHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
      const from = this.caseManualNormalizeAddressKey(row[CASE_MANUAL_START_COL.FROM_ADDRESS]);
      const to = this.caseManualNormalizeAddressKey(row[CASE_MANUAL_START_COL.TO_ADDRESS]);
      if (hash) return `${hash}|${path}|${fromHash}|${from}|${to}`;
      return `blank-${fallbackIndex}|${path}|${from}|${to}|${this.caseManualNormalizeText(row.join('|')).slice(0, 160)}`;
    },

    caseManualDelimitedRowsToText(rows = [], delimiter = '\t') {
      const delim = String(delimiter || '\t');
      return (Array.isArray(rows) ? rows : [])
        .map(row => (Array.isArray(row) ? row : [])
          .map(cell => this.caseManualDelimitedCell(cell, delim))
          .join(delim))
        .join('\n');
    },

    caseManualDelimitedCell(value = '', delimiter = '\t') {
      const text = this.caseManualNormalizeText(value);
      const needsQuotes = text.includes('"') || text.includes('\n') || text.includes('\r') || text.includes(delimiter);
      if (!needsQuotes) return text;
      return `"${text.replace(/"/g, '""')}"`;
    },

    caseManualParseDelimitedRowsPreserveCells(value = '', delimiter = '\t') {
      const textValue = String(value || '');
      if (!textValue.trim()) return [];
      const rows = [[]];
      let field = '';
      let inQuotes = false;
      const delim = String(delimiter || '\t');
      const pushField = () => {
        rows[rows.length - 1].push(field);
        field = '';
      };
      const pushRow = () => {
        pushField();
        rows.push([]);
      };
      for (let index = 0; index < textValue.length; index += 1) {
        const char = textValue[index];
        if (char === '"') {
          if (inQuotes && textValue[index + 1] === '"') {
            field += '"';
            index += 1;
          } else {
            inQuotes = !inQuotes;
          }
          continue;
        }
        if (!inQuotes && delim && textValue.slice(index, index + delim.length) === delim) {
          pushField();
          index += delim.length - 1;
          continue;
        }
        if (!inQuotes && (char === '\n' || char === '\r')) {
          pushRow();
          if (char === '\r' && textValue[index + 1] === '\n') index += 1;
          continue;
        }
        field += char;
      }
      pushField();
      while (rows.length && rows[rows.length - 1].every(cell => !this.caseManualNormalizeText(cell))) rows.pop();
      return rows;
    },

    caseManualStartRowHasData(row = []) {
      return this.caseManualIsHeaderRow(row) || this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true });
    },

    parseCaseManualStartDelimitedRows(value = '', delimiter = '\t') {
      const textValue = String(value || '');
      if (!textValue.trim()) return [];
      const rows = [[]];
      let field = '';
      let inQuotes = false;
      const delim = String(delimiter || '\t');
      const pushField = () => {
        rows[rows.length - 1].push(field);
        field = '';
      };
      const pushRow = () => {
        pushField();
        rows.push([]);
      };
      for (let index = 0; index < textValue.length; index += 1) {
        const char = textValue[index];
        if (char === '"') {
          if (inQuotes && textValue[index + 1] === '"') {
            field += '"';
            index += 1;
          } else {
            inQuotes = !inQuotes;
          }
          continue;
        }
        if (!inQuotes && delim && textValue.slice(index, index + delim.length) === delim) {
          pushField();
          index += delim.length - 1;
          continue;
        }
        if (!inQuotes && (char === '\n' || char === '\r')) {
          pushRow();
          if (char === '\r' && textValue[index + 1] === '\n') index += 1;
          continue;
        }
        field += char;
      }
      pushField();
      while (rows.length && rows[rows.length - 1].every(cell => !this.caseManualNormalizeText(cell))) rows.pop();
      return rows
        .map(row => this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row.length)))))
        .filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }));
    },

    parseCaseManualStartTsvRows(value = '') {
      return this.parseCaseManualStartDelimitedRows(value, '\t');
    },

    extractCaseManualStartSnapshotRows(snapshot = {}, options = {}) {
      const activeCandidate = options?.candidate || options?.activeCandidate || null;
      const traceRows = this.caseManualExtractValidTraceExitRowsFromSnapshot(snapshot);
      const dormantResult = this.caseManualExtractDormantResultFromSnapshot(snapshot);
      const requiresTraceExitRows = this.caseManualSnapshotShouldRequireTraceExitRows(snapshot) || this.caseManualDormantResultIsPartial(dormantResult);
      const candidates = [
        snapshot?.outputRows,
        snapshot?.result?.outputRows,
        snapshot?.result?.rows,
        snapshot?.rows,
        snapshot?.data?.outputRows,
        snapshot?.result?.data?.outputRows
      ];
      const tsvCandidates = [snapshot?.outputTsv, snapshot?.result?.outputTsv, snapshot?.tsv, snapshot?.result?.tsv].filter(Boolean);
      const normalizedCandidates = [];
      candidates.forEach(candidateRows => {
        if (Array.isArray(candidateRows) && candidateRows.length) {
          normalizedCandidates.push(this.normalizeCaseManualStartHopOutputRows(candidateRows));
        }
      });
      tsvCandidates.forEach(tsv => {
        const parsed = this.parseCaseManualStartTsvRows(tsv);
        if (parsed.length) normalizedCandidates.push(parsed);
      });
      let best = traceRows.length ? traceRows.slice() : [];
      let bestScore = traceRows.length ? (this.caseManualCountTransactionRows(traceRows) * 1000) + traceRows.length : -1;
      normalizedCandidates.forEach(rows => {
        const entryRows = rows.filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }));
        const hashCount = this.caseManualCountTransactionRows(entryRows);
        const hasTraceChild = this.caseManualPreviewRowsContainTraceChild(entryRows, activeCandidate, snapshot);
        const score = (hasTraceChild ? 500000 : 0) + (hashCount * 1000) + entryRows.length;
        if (score > bestScore) {
          best = entryRows;
          bestScore = score;
        }
      });
      if (traceRows.length) {
        const seenHashes = new Set(best
          .map(row => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase())
          .filter(hash => hash && this.caseManualIsLikelyTransactionHash(hash)));
        traceRows.forEach((traceRow) => {
          const hashKey = this.caseManualNormalizeText(traceRow?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
          if (!hashKey || !this.caseManualIsLikelyTransactionHash(hashKey) || seenHashes.has(hashKey)) return;
          best.push(traceRow);
          seenHashes.add(hashKey);
        });
      }
      best = this.caseManualFilterRowsToSelectedHop(best, snapshot || {}, activeCandidate, {
        includeParent: true,
        includeChildren: true,
        fallbackParent: true,
        action: snapshot?.integrationAction || snapshot?.action || ''
      });
      const partialDormantRequiresContinuation = this.caseManualDormantResultIsPartial(dormantResult);
      if (requiresTraceExitRows && !traceRows.length && !this.caseManualPreviewRowsContainTraceChild(best, activeCandidate, snapshot) && !(partialDormantRequiresContinuation && this.caseManualRowsContainDormantTraceChild(best, dormantResult, activeCandidate, snapshot))) {
        return [];
      }
      return dormantResult ? this.caseManualBuildDormantRowsForApply(best, snapshot, activeCandidate) : best;
    },

    normalizeCaseManualStartHopOutputRows(rows = []) {
      if (typeof rows === 'string') return this.parseCaseManualStartTsvRows(rows);
      if (!Array.isArray(rows)) return [];
      return rows
        .map(row => {
          if (Array.isArray(row)) return row;
          if (row && Array.isArray(row.cells)) return row.cells;
          if (row && Array.isArray(row.row)) return row.row;
          if (row && typeof row === 'object') {
            const mapped = Array(CASE_MANUAL_START_COLUMN_COUNT).fill('');
            mapped[CASE_MANUAL_START_COL.PATH_LABEL] = row.pathLabel ?? row.label ?? row.path_label ?? '';
            mapped[CASE_MANUAL_START_COL.TX_HASH] = row.hash ?? row.txHash ?? row.transactionHash ?? row.transaction_hash ?? '';
            mapped[CASE_MANUAL_START_COL.FROM_ADDRESS] = row.fromAddress ?? row.from_address ?? row.from ?? '';
            mapped[CASE_MANUAL_START_COL.TO_ADDRESS] = row.toAddress ?? row.to_address ?? row.to ?? row.targetAddress ?? '';
            mapped[CASE_MANUAL_START_COL.DATE] = row.date ?? row.dateTime ?? row.datetime ?? row.utcDate ?? '';
            mapped[CASE_MANUAL_START_COL.AMOUNT] = row.amount ?? row.amountOfTxn ?? row.amount_of_txn ?? '';
            mapped[CASE_MANUAL_START_COL.TOKEN] = row.token ?? row.tokenType ?? row.token_type ?? row.symbol ?? row.tokenSymbol ?? '';
            mapped[CASE_MANUAL_START_COL.SYSTEM_NOTES] = row.notes ?? row.systemNotes ?? row.system_notes ?? '';
            mapped[CASE_MANUAL_START_COL.ATTRIBUTION] = row.attribution ?? row.toAttribution ?? row.to_attribution ?? row.toAttributionLabel ?? row.to_attribution_label ?? row.attributionData ?? row.entity ?? '';
            mapped[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] = row.fromAttribution ?? row.from_attribution ?? row.fromAttributionLabel ?? row.from_attribution_label ?? row.sourceAttribution ?? row.source_attribution ?? '';
            mapped[CASE_MANUAL_START_COL.ANALYST_NOTES] = row.analystNotes ?? row.userNotes ?? row.user_notes ?? '';
            mapped[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = row.fundsFollowed ?? row.funds_followed ?? row.amountFollowedText ?? row.followedAmountText ?? row.followed_amount_text ?? '';
            mapped[CASE_MANUAL_START_COL.PRIOR_BALANCE] = row.caseBuddyPriorBalance
              ?? row.priorBalanceBeforeTransaction
              ?? row.prior_balance_before_transaction
              ?? row.actualPriorBalanceAmount
              ?? row.accountPriorBalance
              ?? row.addressBalanceBeforeTransaction
              ?? row.balanceBeforeTransaction
              ?? row.balance_before_transaction
              ?? row.assetBalanceBefore
              ?? row.asset_balance_before
              ?? row.balanceBefore
              ?? row.balance_before
              ?? '';
            mapped[CASE_MANUAL_START_COL.FROM_HASH] = row.fromHash ?? row.from_hash ?? row.parentHash ?? row.parent_hash ?? row.previousHash ?? '';
            mapped[CASE_MANUAL_START_COL.FROM_LABEL] = row.fromLabel ?? row.from_label ?? row.parentLabel ?? row.parent_label ?? '';
            mapped[CASE_MANUAL_START_COL.CONTINUES_AS] = row.continuesAs ?? row.continues_as ?? row.continues ?? row.continuesAsText ?? '';
            mapped[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = row.leftBoundsDisplay
              ?? row.leftBounds
              ?? row.left_bounds
              ?? row.leftBoundAmount
              ?? row.leftBoundsAmount
              ?? row.leftBound
              ?? row.leftBefore
              ?? row.fundsFollowedInfo?.leftBounds
              ?? '';
            mapped[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = row.leftBoundsNumeric
              ?? row.leftBoundsValue
              ?? row.left_bounds_numeric
              ?? row.leftBoundAmount
              ?? row.leftBoundsAmount
              ?? row.leftBound
              ?? row.fundsFollowedInfo?.leftBounds
              ?? '';
            mapped[CASE_MANUAL_START_COL.CHAIN_ID] = row.chainId ?? row.chain_id ?? '';
            mapped[CASE_MANUAL_START_COL.CHAIN_NAME] = row.chainName ?? row.chain_name ?? '';
            mapped[CASE_MANUAL_START_COL.HOP_LOGIC] = row.hopLogic ?? row.hop_logic ?? '';
            mapped[CASE_MANUAL_START_COL.CASE_NOTES] = row.tool88Notes ?? row.tool88_notes ?? row.caseNotes ?? row.case_notes ?? row.internalNotes ?? '';
            return mapped;
          }
          return [];
        })
        .map(row => this.caseManualCanonicalizeCaseSheetRow(row))
        .filter(row => this.caseManualManualSolveOutputRowIsUsable(row, { allowBtcContinuationRows: true }));
    },

    caseManualStartRowKey(row = []) {
      const values = [
        row[CASE_MANUAL_START_COL.PATH_LABEL],
        row[CASE_MANUAL_START_COL.TX_HASH],
        row[CASE_MANUAL_START_COL.FROM_ADDRESS],
        row[CASE_MANUAL_START_COL.TO_ADDRESS],
        row[CASE_MANUAL_START_COL.AMOUNT],
        row[CASE_MANUAL_START_COL.TOKEN],
        row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED],
        row[CASE_MANUAL_START_COL.FROM_HASH]
      ].map(value => this.caseManualNormalizeText(value).toLowerCase());
      return values.join('|');
    },

    caseManualMergeRowsPreferOutput(baseRow = [], incomingRow = [], options = {}) {
      const width = Math.max(CASE_MANUAL_START_COLUMN_COUNT, baseRow?.length || 0, incomingRow?.length || 0);
      const base = this.caseManualEnsureRowWidth(baseRow, width);
      const incoming = this.caseManualEnsureRowWidth(incomingRow, width);
      const merged = base.slice();
      const isZeroPlaceholder = (value = '') => {
        const textValue = this.caseManualNormalizeText(value);
        if (!textValue) return false;
        const firstLine = this.caseManualFirstLine(textValue);
        const numeric = this.caseManualParseNumericAmount(firstLine || textValue);
        return Number.isFinite(numeric) && Math.abs(numeric) <= 1e-12;
      };
      const hasNonZeroNumericValue = (value = '') => {
        const textValue = this.caseManualNormalizeText(value);
        if (!textValue) return false;
        const firstLine = this.caseManualFirstLine(textValue);
        const numeric = this.caseManualParseNumericAmount(firstLine || textValue);
        return Number.isFinite(numeric) && Math.abs(numeric) > 1e-12;
      };
      const numericPreserveColumns = new Set([
        CASE_MANUAL_START_COL.FUNDS_FOLLOWED,
        CASE_MANUAL_START_COL.PRIOR_BALANCE,
        CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY,
        CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC
      ]);

      incoming.forEach((value, index) => {
        const cleanValue = this.caseManualNormalizeText(value);
        if (!cleanValue) return;
        if ([7, 8].includes(index)) return;
        if (index === CASE_MANUAL_START_COL.FROM_LABEL && this.caseManualLooksLikeLegacyColumnPNote(cleanValue)) {
          merged[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(merged[CASE_MANUAL_START_COL.CASE_NOTES], cleanValue);
          return;
        }
        if ([CASE_MANUAL_START_COL.SYSTEM_NOTES, CASE_MANUAL_START_COL.ANALYST_NOTES, CASE_MANUAL_START_COL.CASE_NOTES].includes(index)) {
          merged[index] = this.caseManualAppendUniqueLine(merged[index], cleanValue);
          return;
        }
        if (numericPreserveColumns.has(index) && isZeroPlaceholder(cleanValue) && hasNonZeroNumericValue(base[index])) {
          return;
        }
        merged[index] = cleanValue;
      });
      if (options.preservePathLabel !== false && this.caseManualNormalizeText(base[CASE_MANUAL_START_COL.PATH_LABEL])) {
        merged[CASE_MANUAL_START_COL.PATH_LABEL] = base[CASE_MANUAL_START_COL.PATH_LABEL];
      }
      merged[CASE_MANUAL_START_COL.ATTRIBUTION] = this.caseManualSanitizeSwapServiceAttributionForColumnK(merged[CASE_MANUAL_START_COL.ATTRIBUTION], merged, {
        extraText: [
          incoming[CASE_MANUAL_START_COL.SYSTEM_NOTES],
          incoming[CASE_MANUAL_START_COL.CASE_NOTES],
          incoming[CASE_MANUAL_START_COL.HOP_LOGIC]
        ].filter(Boolean).join('\n')
      });
      return this.caseManualMoveTool88OperationalNotesToColumnW(merged);
    },

    caseManualFindChainRowsForHash(dataRows = [], activeHash = '') {
      const hashKey = this.caseManualNormalizeText(activeHash).toLowerCase();
      if (!hashKey) return [];
      const byHash = new Map();
      dataRows.forEach((row, index) => {
        const rowHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (rowHash && !byHash.has(rowHash)) byHash.set(rowHash, { row, index });
      });
      const active = byHash.get(hashKey);
      if (!active) return [];
      let root = active;
      const seenParents = new Set([hashKey]);
      for (let guard = 0; guard < 250; guard += 1) {
        const parentHash = this.caseManualNormalizeText(root.row[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
        if (!parentHash || seenParents.has(parentHash) || !byHash.has(parentHash)) break;
        seenParents.add(parentHash);
        root = byHash.get(parentHash);
      }
      const chain = [];
      let cursor = root;
      const seen = new Set();
      for (let guard = 0; guard < 250 && cursor; guard += 1) {
        const cursorHash = this.caseManualNormalizeText(cursor.row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (!cursorHash || seen.has(cursorHash)) break;
        seen.add(cursorHash);
        chain.push(cursor);
        const child = dataRows
          .map((row, index) => ({ row, index }))
          .filter(item => this.caseManualNormalizeText(item.row[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase() === cursorHash)
          .sort((a, b) => a.index - b.index)[0] || null;
        cursor = child;
      }
      return chain;
    },

    caseManualFindInsertIndexAfterParentChain(dataRows = [], parentHash = '') {
      const chain = this.caseManualFindChainRowsForHash(dataRows, parentHash);
      if (chain.length) {
        const lastHash = this.caseManualNormalizeText(chain[chain.length - 1]?.row?.[CASE_MANUAL_START_COL.TX_HASH]);
        return this.caseManualFindInsertIndexAfterEntryBlock(dataRows, lastHash);
      }
      const parentKey = this.caseManualNormalizeText(parentHash);
      const parentIndex = this.caseManualFindDataRowIndexByHash(dataRows, parentKey);
      return parentIndex >= 0 ? this.caseManualFindInsertIndexAfterEntryBlock(dataRows, parentKey) : dataRows.length;
    },

    caseManualShouldRewriteContinuationPathLabel(row = [], parentLabel = '', usedLabels = new Set()) {
      const fromHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_HASH]);
      if (!fromHash) return false;
      const label = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]);
      if (!label) return true;
      if (/^[0-9]+$/u.test(label)) return true;
      const parentRoot = this.caseManualPathLabelRoot(parentLabel);
      if (parentRoot && !label.toLowerCase().startsWith(parentRoot.toLowerCase())) return true;
      return usedLabels.has(label.toLowerCase());
    },

    caseManualNextUnusedContinuationLabel(parentLabel = '', usedLabels = new Set()) {
      let label = this.caseManualIncrementPathLabel(parentLabel || '1');
      for (let guard = 0; guard < 500 && usedLabels.has(label.toLowerCase()); guard += 1) {
        label = this.caseManualIncrementPathLabel(label);
      }
      return label;
    },

    caseManualNextUnusedBranchSideLabel(parentLabel = '', usedLabels = new Set()) {
      const base = this.caseManualNormalizeText(parentLabel) || '1';
      const baseEndsNumeric = /\d$/u.test(base);
      for (let index = 1; index <= 500; index += 1) {
        const suffix = baseEndsNumeric ? this.caseManualAlphabeticSuffixFromIndex(index) : String(index);
        const label = `${base}${suffix}`;
        if (!usedLabels.has(label.toLowerCase())) return label;
      }
      return this.caseManualNextUnusedContinuationLabel(base, usedLabels);
    },

    caseManualBranchRowScore(row = []) {
      const fundsScore = parsePathOperationBridgeNumber(row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
      if (Number.isFinite(fundsScore) && fundsScore > 0) return fundsScore;
      const amountScore = parsePathOperationBridgeNumber(row?.[CASE_MANUAL_START_COL.AMOUNT]);
      return Number.isFinite(amountScore) ? amountScore : 0;
    },

    caseManualSanitizeContinuesAsAgainstKnownRows(dataRows = [], resolveKnownHash = value => this.caseManualNormalizeText(value)) {
      const knownHashes = (Array.isArray(dataRows) ? dataRows : [])
        .map(row => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]))
        .filter(hash => hash && this.caseManualIsLikelyTransactionHash(hash));
      const findKnownHash = (value = '') => {
        const candidate = this.caseManualNormalizeText(resolveKnownHash(value) || value);
        if (!candidate || !this.caseManualIsLikelyTransactionHash(candidate)) return '';
        return knownHashes.find(hash => this.caseManualTransactionHashesMatch(hash, candidate)) || '';
      };
      (Array.isArray(dataRows) ? dataRows : []).forEach(row => {
        const text = this.caseManualNormalizeContinuesAsText(row?.[CASE_MANUAL_START_COL.CONTINUES_AS]);
        if (!/^Continues\s+as\b/iu.test(text)) return;
        const childHashes = text
          .replace(/^Continues\s+as\s*/iu, '')
          .split(',')
          .map(value => findKnownHash(value))
          .filter(Boolean);
        const unique = [];
        childHashes.forEach(hash => {
          if (!unique.some(existing => this.caseManualTransactionHashesMatch(existing, hash))) unique.push(hash);
        });
        row[CASE_MANUAL_START_COL.CONTINUES_AS] = unique.length ? `Continues as ${unique.join(', ')}` : 'Path ends.';
      });
      return dataRows;
    },

    caseManualBuildChildHashesByParentFromRows(dataRows = [], resolveKnownHash = value => this.caseManualNormalizeText(value)) {
      const rows = Array.isArray(dataRows) ? dataRows : [];
      const occurrenceKeyForIndex = index => `occ:${index}`;
      const rowOccurrencesByHash = new Map();
      const rowByOccurrenceKey = new Map();
      rows.forEach((row, index) => {
        const hash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]);
        const hashKey = hash.toLowerCase();
        if (!hashKey || !this.caseManualIsLikelyTransactionHash(hashKey)) return;
        const item = { row, index, key: occurrenceKeyForIndex(index), hash };
        rowByOccurrenceKey.set(item.key, item);
        if (!rowOccurrencesByHash.has(hashKey)) rowOccurrencesByHash.set(hashKey, []);
        rowOccurrencesByHash.get(hashKey).push(item);
      });

      const resolveParentOccurrences = parentHash => {
        const normalizedParentHash = this.caseManualNormalizeText(parentHash);
        const directKey = normalizedParentHash.toLowerCase();
        if (!directKey) return [];
        const direct = rowOccurrencesByHash.get(directKey);
        if (direct?.length) return direct;
        const matchKey = Array.from(rowOccurrencesByHash.keys()).find(key => this.caseManualTransactionHashesMatch(key, normalizedParentHash));
        return matchKey ? (rowOccurrencesByHash.get(matchKey) || []) : [];
      };

      const labelRelationScore = (childLabel = '', parentLabel = '') => {
        const child = this.caseManualNormalizeText(childLabel).toLowerCase();
        const parent = this.caseManualNormalizeText(parentLabel).toLowerCase();
        if (!child || !parent || child === parent) return 0;
        if (!child.startsWith(parent)) return 0;
        return parent.length * 10 + Math.max(1, child.length - parent.length);
      };
      const occurrenceAmount = (row = []) => {
        const text = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]).replace(/,/gu, '');
        const match = text.match(/-?\d+(?:\.\d+)?/u);
        if (!match) return NaN;
        const amount = Number(match[0]);
        return Number.isFinite(amount) ? amount : NaN;
      };
      const occurrenceAmountMatches = (childRow = [], parentRow = []) => {
        const childAmount = occurrenceAmount(childRow);
        const parentAmount = occurrenceAmount(parentRow);
        if (!Number.isFinite(childAmount) || !Number.isFinite(parentAmount)) return false;
        const tolerance = Math.max(0.000001, Math.abs(parentAmount) * 1e-8);
        return Math.abs(childAmount - parentAmount) <= tolerance;
      };

      const chooseParentOccurrence = (childRow = [], parentHash = '') => {
        const candidates = resolveParentOccurrences(parentHash);
        if (candidates.length <= 1) return candidates[0] || null;
        const childFromLabel = this.caseManualNormalizeText(childRow?.[CASE_MANUAL_START_COL.FROM_LABEL]).toLowerCase();
        const childPathLabel = this.caseManualNormalizeText(childRow?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        const childFromAddress = this.caseManualNormalizeAddressKey(childRow?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        const childIndex = rows.indexOf(childRow);
        const scored = candidates.map(item => {
          const parentLabel = this.caseManualNormalizeText(item.row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
          const exactFromLabel = Boolean(childFromLabel && parentLabel && childFromLabel === parentLabel);
          const relationScore = labelRelationScore(childPathLabel, parentLabel);
          const parentTo = this.caseManualNormalizeAddressKey(item.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
          const addressMatch = Boolean(childFromAddress && parentTo && childFromAddress === parentTo);
          const allocationMatch = occurrenceAmountMatches(childRow, item.row);
          let score = 0;
          if (exactFromLabel) score += 1000;
          score += relationScore * 8;
          if (allocationMatch) score += 520;
          if (addressMatch) score += 80;
          if (childIndex < 0 || item.index < childIndex) score += 20;
          score -= Math.abs((childIndex >= 0 ? childIndex : rows.length) - item.index) / 1000;
          return { item, score, exactFromLabel, relationScore, allocationMatch, addressMatch };
        }).sort((left, right) => right.score - left.score || left.item.index - right.item.index);
        // When the same transaction hash appears as multiple path occurrences,
        // hash/address alone is not enough to choose a parent. Prefer explicit
        // occurrence context from Column P/labels. Only use address matching if it
        // uniquely identifies one duplicate parent occurrence.
        const labelled = scored.filter(item => item.exactFromLabel || item.relationScore > 0);
        if (labelled.length) return labelled[0].item;
        const allocationMatches = scored.filter(item => item.allocationMatch);
        if (allocationMatches.length === 1) return allocationMatches[0].item;
        const addressOnly = scored.filter(item => item.addressMatch);
        return addressOnly.length === 1 ? addressOnly[0].item : null;
      };

      const childRowsByParent = new Map();
      rows.forEach((row, index) => {
        const childHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]);
        if (!this.caseManualIsLikelyTransactionHash(childHash)) return;
        const parentHash = this.caseManualNormalizeText(resolveKnownHash(row?.[CASE_MANUAL_START_COL.FROM_HASH]) || row?.[CASE_MANUAL_START_COL.FROM_HASH]);
        if (parentHash && this.caseManualIsLikelyTransactionHash(parentHash)) row[CASE_MANUAL_START_COL.FROM_HASH] = parentHash;
        if (!parentHash || !this.caseManualIsLikelyTransactionHash(parentHash)) return;
        const parentItem = chooseParentOccurrence(row, parentHash);
        if (!parentItem) return;
        if (!childRowsByParent.has(parentItem.key)) childRowsByParent.set(parentItem.key, []);
        childRowsByParent.get(parentItem.key).push({ row, index, key: occurrenceKeyForIndex(index), hash: childHash });
      });

      const childHashesByParent = new Map();
      childRowsByParent.forEach((childItems, parentKey) => {
        const parentItem = rowByOccurrenceKey.get(parentKey);
        const parentRow = parentItem?.row;
        if (!parentRow) return;
        const childItemsByHash = new Map();
        childItems.forEach(item => {
          const key = this.caseManualNormalizeText(item.hash || item.row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
          if (!key) return;
          if (!childItemsByHash.has(key)) childItemsByHash.set(key, []);
          childItemsByHash.get(key).push(item);
        });
        const ordered = [];
        this.caseManualExtractContinuesAsHashes(parentRow?.[CASE_MANUAL_START_COL.CONTINUES_AS], rows)
          .map(hash => this.caseManualNormalizeText(resolveKnownHash(hash) || hash))
          .forEach(hash => {
            const key = hash.toLowerCase();
            const list = childItemsByHash.get(key) || [];
            const childItem = list.find(item => !ordered.includes(item));
            if (childItem) ordered.push(childItem);
          });
        childItems.forEach(item => {
          if (!ordered.includes(item)) ordered.push(item);
        });
        childHashesByParent.set(
          parentKey,
          ordered
            .map(item => ({
              hash: this.caseManualNormalizeText(item.hash || item.row?.[CASE_MANUAL_START_COL.TX_HASH]),
              row: item.row,
              index: item.index,
              key: item.key,
            }))
            .filter(item => item.hash && this.caseManualIsLikelyTransactionHash(item.hash))
        );
      });

      return childHashesByParent;
    },

    caseManualCollectConnectedFlowHashes(dataRows = [], startHash = '') {
      const rows = Array.isArray(dataRows) ? dataRows : [];
      const startKey = this.caseManualNormalizeText(startHash).toLowerCase();
      const parentByChild = new Map();
      const childrenByParent = new Map();
      const allHashes = new Set();

      rows.forEach(row => {
        const hashKey = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (!hashKey) return;
        allHashes.add(hashKey);
        const parentKey = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
        if (parentKey) {
          parentByChild.set(hashKey, parentKey);
          if (!childrenByParent.has(parentKey)) childrenByParent.set(parentKey, []);
          childrenByParent.get(parentKey).push(hashKey);
        }
      });

      if (!startKey) return new Set();
      const resolveFlowKey = value => {
        const key = this.caseManualNormalizeText(value).toLowerCase();
        if (!key) return '';
        if (allHashes.has(key)) return key;
        return Array.from(allHashes).find(hash => this.caseManualTransactionHashesMatch(hash, key)) || key;
      };
      const resolvedStartKey = resolveFlowKey(startKey);
      if (!allHashes.has(resolvedStartKey)) return new Set();
      let rootKey = resolvedStartKey;
      const ancestorGuard = new Set();
      while (parentByChild.has(rootKey) && !ancestorGuard.has(rootKey)) {
        ancestorGuard.add(rootKey);
        const next = resolveFlowKey(parentByChild.get(rootKey));
        if (!allHashes.has(next)) break;
        rootKey = next;
      }

      const result = new Set();
      const stack = [rootKey];
      while (stack.length) {
        const key = stack.shift();
        if (!key || result.has(key)) continue;
        result.add(key);
        (childrenByParent.get(key) || []).forEach(childKey => {
          if (!result.has(childKey)) stack.push(childKey);
        });
      }
      return result;
    },

    caseManualReplaceBranchesAsSystemNote(value = '', orderedChildHashes = []) {
      const branchesLine = orderedChildHashes.length > 1 ? `Branches as ${orderedChildHashes.join(', ')}` : '';
      const lines = this.caseManualNormalizeText(value)
        .split(/\r?\n/u)
        .map(line => line.trim())
        .filter(Boolean)
        .filter(line => !/^Branches\s+as\b/iu.test(line));
      if (branchesLine && !lines.some(line => line.toLowerCase() === branchesLine.toLowerCase())) lines.push(branchesLine);
      return lines.join('\n');
    },

    caseManualCompareSaplingPathLabels(leftValue = '', rightValue = '') {
      const parse = value => {
        const label = this.caseManualNormalizeText(value).toLowerCase();
        const match = label.match(/^([0-9]+)(.*)$/u);
        return match ? { root: Number(match[1]) || 0, suffix: match[2] || '' } : { root: Number.MAX_SAFE_INTEGER, suffix: label };
      };
      const left = parse(leftValue);
      const right = parse(rightValue);
      if (left.root !== right.root) return left.root - right.root;
      return left.suffix.localeCompare(right.suffix, undefined, { numeric: true, sensitivity: 'base' });
    },

    caseManualSortRowsLikeTool68Sapling(dataRows = []) {
      if (!Array.isArray(dataRows)) return dataRows;
      const entries = [];
      let currentEntry = null;
      dataRows.forEach((row, originalIndex) => {
        const rowHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]);
        if (rowHash || !currentEntry) {
          currentEntry = { lead: row, rows: [row], originalIndex };
          entries.push(currentEntry);
          return;
        }
        currentEntry.rows.push(row);
      });
      entries.sort((left, right) => {
        const labelCompare = this.caseManualCompareSaplingPathLabels(left.lead?.[CASE_MANUAL_START_COL.PATH_LABEL], right.lead?.[CASE_MANUAL_START_COL.PATH_LABEL]);
        if (labelCompare !== 0) return labelCompare;
        const leftHash = this.caseManualNormalizeText(left.lead?.[CASE_MANUAL_START_COL.TX_HASH]);
        const rightHash = this.caseManualNormalizeText(right.lead?.[CASE_MANUAL_START_COL.TX_HASH]);
        const hashCompare = leftHash.localeCompare(rightHash);
        return hashCompare !== 0 ? hashCompare : left.originalIndex - right.originalIndex;
      });
      dataRows.splice(0, dataRows.length, ...entries.flatMap(entry => entry.rows));
      return dataRows;
    },

    caseManualFindInsertIndexAfterEntryBlock(dataRows = [], transactionHash = '') {
      const startIndex = this.caseManualFindDataRowIndexByHash(dataRows, transactionHash);
      if (startIndex < 0) return dataRows.length;
      let insertAt = startIndex + 1;
      while (insertAt < dataRows.length && !this.caseManualNormalizeText(dataRows[insertAt]?.[CASE_MANUAL_START_COL.TX_HASH])) {
        insertAt += 1;
      }
      return insertAt;
    },

    caseManualApplyTool68BranchLabels(dataRows = [], childHashesByParent = new Map(), usedLabels = new Set()) {
      if (!Array.isArray(dataRows) || !childHashesByParent || typeof childHashesByParent.forEach !== 'function') return;
      const occurrenceKeyForIndex = index => `occ:${index}`;
      const rowByOccurrenceKey = new Map();
      const rowByHash = new Map();
      dataRows.forEach((row, index) => {
        const hashKey = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (!hashKey || !this.caseManualIsLikelyTransactionHash(hashKey)) return;
        rowByOccurrenceKey.set(occurrenceKeyForIndex(index), row);
        if (!rowByHash.has(hashKey)) rowByHash.set(hashKey, row);
      });
      const childHashFromRef = child => {
        if (typeof child === 'string') return this.caseManualNormalizeText(child);
        return this.caseManualNormalizeText(child?.hash || child?.row?.[CASE_MANUAL_START_COL.TX_HASH]);
      };
      const childRowFromRef = child => {
        if (child?.row) return child.row;
        if (child?.key && rowByOccurrenceKey.has(child.key)) return rowByOccurrenceKey.get(child.key);
        const hashKey = childHashFromRef(child).toLowerCase();
        return rowByHash.get(hashKey) || null;
      };
      childHashesByParent.forEach((childRefs, parentKey) => {
        if (!Array.isArray(childRefs) || childRefs.length < 2) return;
        const parentHashKey = this.caseManualNormalizeText(parentKey).toLowerCase();
        const parentRow = rowByOccurrenceKey.get(parentKey) || rowByHash.get(parentHashKey);
        if (!parentRow) return;
        const parentLabel = this.caseManualNormalizeText(parentRow[CASE_MANUAL_START_COL.PATH_LABEL]);
        if (!parentLabel) return;
        const childItems = childRefs
          .map((childRef, originalIndex) => {
            const row = childRowFromRef(childRef);
            const hash = childHashFromRef(childRef);
            return row ? {
              row,
              hash: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]) || hash,
              key: childRef?.key || '',
              originalIndex,
              score: this.caseManualBranchRowScore(row),
            } : null;
          })
          .filter(Boolean);
        if (childItems.length < 2) return;
        childItems.forEach(item => {
          const existingLabel = this.caseManualNormalizeText(item.row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
          if (existingLabel) usedLabels.delete(existingLabel);
        });
        // Column Q order is the investigator/WayFinder branch order. The first child is the
        // main visual continuation; later children become side branches.
        const primary = childItems.slice().sort((left, right) => left.originalIndex - right.originalIndex)[0];
        const sideItems = childItems.filter(item => item !== primary).sort((left, right) => left.originalIndex - right.originalIndex);
        const primaryLabel = this.caseManualNextUnusedContinuationLabel(parentLabel, usedLabels);
        primary.row[CASE_MANUAL_START_COL.PATH_LABEL] = primaryLabel;
        primary.row[CASE_MANUAL_START_COL.FROM_LABEL] = parentLabel;
        usedLabels.add(primaryLabel.toLowerCase());
        sideItems.forEach(item => {
          const sideLabel = this.caseManualNextUnusedBranchSideLabel(parentLabel, usedLabels);
          item.row[CASE_MANUAL_START_COL.PATH_LABEL] = sideLabel;
          item.row[CASE_MANUAL_START_COL.FROM_LABEL] = parentLabel;
          usedLabels.add(sideLabel.toLowerCase());
        });
        const orderedItems = [primary, ...sideItems].map(item => ({
          hash: item.hash,
          row: item.row,
          key: item.key,
        })).filter(item => item.hash);
        const orderedHashes = orderedItems.map(item => item.hash).filter(Boolean);
        if (orderedHashes.length > 1) {
          childHashesByParent.set(parentKey, orderedItems);
          parentRow[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualReplaceBranchesAsSystemNote(parentRow[CASE_MANUAL_START_COL.SYSTEM_NOTES], orderedHashes);
        }
      });
    },

    caseManualPrepareHopOutputRowsForGraft(rows = [], options = {}) {
      const normalized = this.normalizeCaseManualStartHopOutputRows(rows);
      let outputDataRows = (this.caseManualIsHeaderRow(normalized[0]) ? normalized.slice(1) : normalized)
        .map(row => this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)))))
        .filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }));
      const explicitOutputRowCount = outputDataRows.length;
      if (this.caseInvestigationVisualizerManualStartMode && options.includeVisualRows !== false) {
        const outputHashKeys = new Set(outputDataRows
          .map(row => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase())
          .filter(hash => hash && this.caseManualIsLikelyTransactionHash(hash)));
        const visualRows = (this.getCaseManualStartVisualizerRowsFromCurrentVisual?.() || [])
          .map((candidate) => {
            const sourceRow = Array.isArray(candidate?.rawRow)
              ? candidate.rawRow
              : (Array.isArray(candidate?.row) ? candidate.row : []);
            const sourceLength = Array.isArray(candidate?.rawRow)
              ? candidate.rawRow.length
              : (Array.isArray(candidate?.row) ? candidate.row.length : 0);
            return this.caseManualMoveTool88OperationalNotesToColumnW(
              this.caseManualMigrateLegacyColumnPNotes(
                this.caseManualEnsureRowWidth(sourceRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceLength))
              )
            );
          })
          .filter(row => this.caseManualManualSolveRowShouldRetain(row))
          .map(row => {
            const helperRow = row.slice();
            // Visual backfill rows are topology-preservation helpers. Never let
            // cached visual labels overwrite Column K in Main/Raw; explicit Tool 88
            // output rows for the selected target may still carry attribution.
            helperRow[CASE_MANUAL_START_COL.ATTRIBUTION] = '';
            // These rows come back from the rendered visual, not from the
            // spreadsheet. Keep them topology-only so cached display state can
            // never overwrite canonical allocation/accounting values.
            helperRow[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = '';
            helperRow[CASE_MANUAL_START_COL.PRIOR_BALANCE] = '';
            helperRow[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = '';
            helperRow[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = '';
            return helperRow;
          });
        visualRows.forEach(row => {
          const hashKey = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
          if (!hashKey || !this.caseManualIsLikelyTransactionHash(hashKey) || outputHashKeys.has(hashKey)) return;
          outputHashKeys.add(hashKey);
          outputDataRows.push(row);
        });
      }
      const current = this.getCaseManualStartMainMatrix().map(row => this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)))));
      const header = current.length && this.caseManualIsHeaderRow(current[0]) ? current[0] : null;
      const dataRows = (header ? current.slice(1) : current)
        .map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)))
        .filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }));
      const originalMainRowsSignature = JSON.stringify([
        this.caseManualCanonicalizeCaseSheetRow(header || CASE_MANUAL_START_CANONICAL_HEADERS, { isHeader: true }),
        ...dataRows.map(row => this.caseManualCanonicalizeCaseSheetRow(row)),
      ]);
      const activeCandidate = this.caseManualStartHopInspectorCandidate || this.caseHopSolverCandidate || this.caseInvestigationVisualizerSelectedCandidate || {};
      const fullHashPool = this.caseManualCollectFullTransactionHashesFromRows([
        ...dataRows,
        ...outputDataRows,
        activeCandidate,
        activeCandidate?.rawRow || activeCandidate?.row || []
      ]);
      if (fullHashPool.length) {
        this.caseManualUpgradeHashReferencesInRows(dataRows, fullHashPool);
        this.caseManualUpgradeHashReferencesInRows(outputDataRows, fullHashPool);
      }
      const activeHashText = this.caseManualNormalizeText(activeCandidate.hash || activeCandidate.transactionHash || activeCandidate.txHash || '').toLowerCase();
      const activeHash = this.caseManualIsLikelyTransactionHash(activeHashText) ? activeHashText : '';
      const activeIndex = activeHash ? this.caseManualFindDataRowIndexForCandidate(dataRows, activeCandidate, activeHash) : -1;
      const activeRow = activeIndex >= 0
        ? dataRows[activeIndex]
        : this.caseManualEnsureRowWidth(activeCandidate.rawRow || activeCandidate.row || [], CASE_MANUAL_START_COLUMN_COUNT);
      const activeLabel = this.caseManualNormalizeText(activeRow[CASE_MANUAL_START_COL.PATH_LABEL] || activeCandidate.pathLabel || '1') || '1';
      const activeTxHashCandidate = this.caseManualNormalizeText(activeRow[CASE_MANUAL_START_COL.TX_HASH] || activeCandidate.hash || activeCandidate.transactionHash || activeCandidate.txHash || '');
      const activeTxHash = this.caseManualIsLikelyTransactionHash(activeTxHashCandidate) ? activeTxHashCandidate : '';
      const resolveKnownHash = value => this.caseManualResolveKnownTransactionHash(value, dataRows);
      const activeParentHash = this.caseManualNormalizeText(resolveKnownHash(activeRow?.[CASE_MANUAL_START_COL.FROM_HASH]) || activeRow?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
      const activeFromAddressKey = this.caseManualNormalizeAddressKey(activeRow?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
      const activeToAddressKey = this.caseManualNormalizeAddressKey(activeRow?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
      const activeHashOccurrenceCount = activeTxHash
        ? dataRows.filter(row => this.caseManualTransactionHashesMatch(row?.[CASE_MANUAL_START_COL.TX_HASH], activeTxHash)).length
        : 0;
      outputDataRows = this.caseManualRepairBridgeReceiveAccountingRows(outputDataRows, activeRow);
      const outputRowRepresentsActiveOccurrence = (row = [], hashKey = '') => {
        if (!activeTxHash || !this.caseManualTransactionHashesMatch(hashKey, activeTxHash)) return false;
        if (activeHashOccurrenceCount <= 1) return true;
        const incomingParent = this.caseManualNormalizeText(resolveKnownHash(row?.[CASE_MANUAL_START_COL.FROM_HASH]) || row?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
        if (incomingParent && activeParentHash && !this.caseManualTransactionHashesMatch(incomingParent, activeParentHash)) return false;
        const incomingLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        if (incomingLabel && activeLabel && incomingLabel !== activeLabel.toLowerCase()) return false;
        const incomingFromKey = this.caseManualNormalizeAddressKey(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        const incomingToKey = this.caseManualNormalizeAddressKey(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
        if (incomingFromKey && activeFromAddressKey && incomingFromKey !== activeFromAddressKey) return false;
        if (incomingToKey && activeToAddressKey && incomingToKey !== activeToAddressKey) return false;
        if (incomingLabel && activeLabel && incomingLabel === activeLabel.toLowerCase()) return true;
        if (incomingParent && activeParentHash && this.caseManualTransactionHashesMatch(incomingParent, activeParentHash)) return !occurrenceAllocationConflict(activeRow, row);
        // With duplicate same-hash active rows, a same-hash Tool 88 helper row that
        // lacks label/parent context is ambiguous. Do not treat it as the selected
        // occurrence or its funds-followed/left-bounds can overwrite a sibling path.
        return false;
      };
      const byHash = new Map();
      const rebuildByHashIndex = () => {
        byHash.clear();
        dataRows.forEach((row, index) => {
          const hash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
          if (hash && this.caseManualIsLikelyTransactionHash(hash) && !byHash.has(hash)) byHash.set(hash, index);
        });
      };
      rebuildByHashIndex();
      const touchedHashes = new Set();
      const insertedHashes = new Set();
      const rawOutputRowsByHash = new Map();
      const rawContinuationRowsByAnchor = new Map();
      const usedLabels = new Set(dataRows.map(row => this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase()).filter(Boolean));
      let previousNewHash = '';
      const allocationNumber = value => {
        const text = this.caseManualNormalizeText(value).replace(/,/g, '');
        const match = text.match(/-?\d+(?:\.\d+)?/u);
        if (!match) return NaN;
        const numeric = Number(match[0]);
        return Number.isFinite(numeric) ? numeric : NaN;
      };
      const allocationConflict = (leftValue = '', rightValue = '', tolerance = 0.000001) => {
        const leftText = this.caseManualNormalizeText(leftValue).replace(/,/g, '').toLowerCase();
        const rightText = this.caseManualNormalizeText(rightValue).replace(/,/g, '').toLowerCase();
        if (!leftText || !rightText) return false;
        const leftNumber = allocationNumber(leftText);
        const rightNumber = allocationNumber(rightText);
        if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) return Math.abs(leftNumber - rightNumber) > tolerance;
        return leftText !== rightText;
      };
      const occurrenceAllocationConflict = (leftRow = [], rightRow = []) => {
        const fundsConflict = allocationConflict(leftRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED], rightRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
        const leftBounds = leftRow?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || leftRow?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY];
        const rightBounds = rightRow?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || rightRow?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY];
        return fundsConflict || allocationConflict(leftBounds, rightBounds);
      };
      const findExistingIndexForOutputOccurrence = (hash = '', outputRow = [], options = {}) => {
        const hashKey = this.caseManualNormalizeText(hash).toLowerCase();
        if (!hashKey || !this.caseManualIsLikelyTransactionHash(hashKey)) return -1;
        const candidates = dataRows
          .map((row, index) => ({ row, index }))
          .filter(({ row }) => this.caseManualTransactionHashesMatch(this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase(), hashKey));
        if (!candidates.length) return -1;
        if (options.isActiveRow && activeIndex >= 0) return activeIndex;

        const incomingParent = this.caseManualNormalizeText(resolveKnownHash(outputRow?.[CASE_MANUAL_START_COL.FROM_HASH]) || outputRow?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
        if (incomingParent) {
          const duplicateParentRows = dataRows
            .map((row, index) => ({ row, index }))
            .filter(({ row }) => this.caseManualTransactionHashesMatch(
              this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase(),
              incomingParent,
            ));
          const activeParentIsDuplicate = activeTxHash
            && activeIndex >= 0
            && this.caseManualTransactionHashesMatch(incomingParent, activeTxHash)
            && duplicateParentRows.length > 1;
          if (activeParentIsDuplicate) {
            const activeParentLabel = this.caseManualNormalizeText(dataRows[activeIndex]?.[CASE_MANUAL_START_COL.PATH_LABEL] || activeLabel).toLowerCase();
            const incomingFromLabel = this.caseManualNormalizeText(outputRow?.[CASE_MANUAL_START_COL.FROM_LABEL]).toLowerCase();
            const incomingLabel = this.caseManualNormalizeText(outputRow?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
            const nearestDuplicateParentIndexBefore = (childIndex) => duplicateParentRows
              .filter(({ index }) => index < childIndex)
              .sort((left, right) => right.index - left.index)[0]?.index ?? -1;
            const occurrenceMatches = candidates.filter(({ row, index }) => {
              const existingParent = this.caseManualNormalizeText(resolveKnownHash(row?.[CASE_MANUAL_START_COL.FROM_HASH]) || row?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
              if (!existingParent || !this.caseManualTransactionHashesMatch(existingParent, incomingParent)) return false;
              const existingFromLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_LABEL]).toLowerCase();
              const existingLabel = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
              if (incomingFromLabel && activeParentLabel && incomingFromLabel !== activeParentLabel) return false;
              if (existingFromLabel && activeParentLabel && existingFromLabel !== activeParentLabel) return false;
              const labelShowsSelectedOccurrence = Boolean(
                activeParentLabel
                  && (existingFromLabel === activeParentLabel
                    || (existingLabel && existingLabel !== activeParentLabel && existingLabel.startsWith(activeParentLabel)))
              );
              const positionShowsSelectedOccurrence = nearestDuplicateParentIndexBefore(index) === activeIndex;
              if (!labelShowsSelectedOccurrence && !positionShowsSelectedOccurrence) return false;
              if (incomingLabel && existingLabel && existingLabel === incomingLabel) return true;
              return true;
            });
            if (occurrenceMatches.length === 1) {
              return occurrenceAllocationConflict(occurrenceMatches[0].row, outputRow) ? -1 : occurrenceMatches[0].index;
            }
            if (occurrenceMatches.length > 1) {
              const incomingLabel = this.caseManualNormalizeText(outputRow?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
              if (incomingLabel) {
                const labelMatch = occurrenceMatches.find(({ row }) => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase() === incomingLabel);
                if (labelMatch && !occurrenceAllocationConflict(labelMatch.row, outputRow)) return labelMatch.index;
              }
              const allocationMatch = occurrenceMatches.find(({ row }) => !occurrenceAllocationConflict(row, outputRow));
              return allocationMatch ? allocationMatch.index : -1;
            }
            // Same transaction hash, same explicit parent hash, but not this
            // selected duplicate parent occurrence. Insert a separate path row.
            return -1;
          }
          const parentMatches = candidates.filter(({ row }) => {
            const existingParent = this.caseManualNormalizeText(resolveKnownHash(row?.[CASE_MANUAL_START_COL.FROM_HASH]) || row?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
            return existingParent && this.caseManualTransactionHashesMatch(existingParent, incomingParent);
          });
          if (parentMatches.length === 1) {
            return occurrenceAllocationConflict(parentMatches[0].row, outputRow) ? -1 : parentMatches[0].index;
          }
          if (parentMatches.length > 1) {
            const incomingLabel = this.caseManualNormalizeText(outputRow?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
            if (incomingLabel) {
              const labelMatch = parentMatches.find(({ row }) => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase() === incomingLabel);
              if (labelMatch && !occurrenceAllocationConflict(labelMatch.row, outputRow)) return labelMatch.index;
            }
            const allocationMatch = parentMatches.find(({ row }) => !occurrenceAllocationConflict(row, outputRow));
            return allocationMatch ? allocationMatch.index : -1;
          }
          // Same transaction hash under a different explicit parent is a separate
          // Case Buddy path occurrence. Insert a duplicate entry instead of merging.
          return -1;
        }

        const incomingLabel = this.caseManualNormalizeText(outputRow?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        if (incomingLabel) {
          const labelMatch = candidates.find(({ row }) => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase() === incomingLabel);
          if (labelMatch) return labelMatch.index;
        }

        const incomingFrom = this.caseManualNormalizeAddressKey(outputRow?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        const incomingTo = this.caseManualNormalizeAddressKey(outputRow?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
        const endpointMatches = candidates.filter(({ row }) => {
          const existingParent = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
          if (existingParent) return false;
          const existingFrom = this.caseManualNormalizeAddressKey(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
          const existingTo = this.caseManualNormalizeAddressKey(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
          return (!incomingFrom || !existingFrom || incomingFrom === existingFrom)
            && (!incomingTo || !existingTo || incomingTo === existingTo);
        });
        if (endpointMatches.length === 1) return endpointMatches[0].index;
        return candidates.length === 1 ? candidates[0].index : -1;
      };
      outputDataRows.forEach((rawOutputRow, outputRowIndex) => {
        let outputRow = this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(this.caseManualEnsureRowWidth(rawOutputRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, rawOutputRow?.length || 0))));
        if (!this.caseManualStartRowHasData(outputRow) || this.caseManualIsHeaderRow(outputRow)) return;
        const hash = this.caseManualNormalizeText(outputRow[CASE_MANUAL_START_COL.TX_HASH]);
        if (hash && !this.caseManualIsLikelyTransactionHash(hash)) return;
        if (!hash) {
          if (this.caseManualRowNetworkFamily(outputRow) !== 'btc') return;
          const anchorHash = previousNewHash || activeTxHash;
          if (!anchorHash || !this.caseManualIsLikelyTransactionHash(anchorHash)) return;
          const continuationRow = this.caseManualEnsureRowWidth(outputRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, outputRow?.length || 0));
          continuationRow[CASE_MANUAL_START_COL.PATH_LABEL] = '';
          continuationRow[CASE_MANUAL_START_COL.TX_HASH] = '';
          continuationRow[CASE_MANUAL_START_COL.FROM_HASH] = '';
          const anchorKey = this.caseManualNormalizeText(anchorHash).toLowerCase();
          if (anchorKey && this.caseManualIsLikelyTransactionHash(anchorKey)) {
            if (!rawContinuationRowsByAnchor.has(anchorKey)) rawContinuationRowsByAnchor.set(anchorKey, []);
            rawContinuationRowsByAnchor.get(anchorKey).push(continuationRow.slice());
          }
          const insertAt = this.caseManualFindInsertIndexAfterEntryBlock(dataRows, anchorHash);
          dataRows.splice(Math.max(0, Math.min(dataRows.length, insertAt)), 0, continuationRow);
          touchedHashes.add(anchorKey);
          return;
        }
        const hashKey = hash.toLowerCase();
        if (hashKey && !rawOutputRowsByHash.has(hashKey)) rawOutputRowsByHash.set(hashKey, outputRow.slice());
        const resolvedOutputFromHash = resolveKnownHash(outputRow[CASE_MANUAL_START_COL.FROM_HASH]);
        if (resolvedOutputFromHash) outputRow[CASE_MANUAL_START_COL.FROM_HASH] = resolvedOutputFromHash;
        const isActiveRow = outputRowRepresentsActiveOccurrence(outputRow, hashKey);
        const outputParentHash = this.caseManualNormalizeText(
          resolveKnownHash(outputRow[CASE_MANUAL_START_COL.FROM_HASH]) || outputRow[CASE_MANUAL_START_COL.FROM_HASH]
        ).toLowerCase();
        const isExplicitChildOfSelectedDuplicateOccurrence = Boolean(
          outputRowIndex < explicitOutputRowCount
            && !isActiveRow
            && activeHashOccurrenceCount > 1
            && activeTxHash
            && outputParentHash
            && this.caseManualTransactionHashesMatch(outputParentHash, activeTxHash)
        );
        if (isExplicitChildOfSelectedDuplicateOccurrence) {
          outputRow[CASE_MANUAL_START_COL.FROM_LABEL] = activeLabel;
          outputRow[CASE_MANUAL_START_COL.PATH_LABEL] = '';
        }
        const existingIndexForHash = findExistingIndexForOutputOccurrence(hash, outputRow, { isActiveRow });
        const rowAlreadyExists = existingIndexForHash >= 0;
        if (isActiveRow) {
          outputRow[CASE_MANUAL_START_COL.PATH_LABEL] = activeLabel;
        } else if (!rowAlreadyExists && !this.caseManualNormalizeText(outputRow[CASE_MANUAL_START_COL.FROM_HASH])) {
          outputRow[CASE_MANUAL_START_COL.FROM_HASH] = previousNewHash || activeTxHash;
        }
        if (rowAlreadyExists) {
          const index = existingIndexForHash;
          const existingRow = dataRows[index];
          const isExistingActiveRow = isActiveRow;
          const mergedParentHash = resolveKnownHash(this.caseManualNormalizeText(
            outputRow[CASE_MANUAL_START_COL.FROM_HASH]
              || existingRow[CASE_MANUAL_START_COL.FROM_HASH]
              || (!isExistingActiveRow ? (previousNewHash || activeTxHash) : '')
          ));
          const parentKey = mergedParentHash.toLowerCase();
          const parentIndex = (activeTxHash && this.caseManualTransactionHashesMatch(parentKey, activeTxHash) && activeIndex >= 0)
            ? activeIndex
            : (parentKey && byHash.has(parentKey) ? byHash.get(parentKey) : -1);
          const parentLabel = parentIndex >= 0
            ? this.caseManualNormalizeText(dataRows[parentIndex][CASE_MANUAL_START_COL.PATH_LABEL])
            : activeLabel;
          const existingLabel = this.caseManualNormalizeText(existingRow[CASE_MANUAL_START_COL.PATH_LABEL]);
          const usedWithoutExisting = new Set(usedLabels);
          if (existingLabel) usedWithoutExisting.delete(existingLabel.toLowerCase());
          const trialMergedRow = this.caseManualMergeRowsPreferOutput(existingRow, outputRow, { preservePathLabel: false });
          const preservePathLabel = Boolean(isExistingActiveRow || (existingLabel
            && !this.caseManualShouldRewriteContinuationPathLabel(
              trialMergedRow,
              parentLabel,
              usedWithoutExisting
            )));
          const mergedRow = this.caseManualMergeRowsPreferOutput(existingRow, outputRow, { preservePathLabel });
          if (isExistingActiveRow) {
            mergedRow[CASE_MANUAL_START_COL.PATH_LABEL] = activeLabel;
            mergedRow[CASE_MANUAL_START_COL.FROM_HASH] = existingRow[CASE_MANUAL_START_COL.FROM_HASH] || outputRow[CASE_MANUAL_START_COL.FROM_HASH] || '';
          } else if (!preservePathLabel && this.caseManualNormalizeText(mergedRow[CASE_MANUAL_START_COL.FROM_HASH])) {
            mergedRow[CASE_MANUAL_START_COL.PATH_LABEL] = this.caseManualNextUnusedContinuationLabel(parentLabel || activeLabel, usedWithoutExisting);
          }
          const fromLabel = this.caseManualNormalizeText(parentLabel || activeLabel);
          if (!isExistingActiveRow && fromLabel && this.caseManualNormalizeText(mergedRow[CASE_MANUAL_START_COL.FROM_HASH])) {
            mergedRow[CASE_MANUAL_START_COL.FROM_LABEL] = fromLabel;
          }
          dataRows[index] = mergedRow;
          usedLabels.add(this.caseManualNormalizeText(mergedRow[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase());
          touchedHashes.add(hashKey);
          previousNewHash = hash;
          return;
        }
        const parentHash = resolveKnownHash(this.caseManualNormalizeText(outputRow[CASE_MANUAL_START_COL.FROM_HASH]) || previousNewHash || activeTxHash);
        outputRow[CASE_MANUAL_START_COL.FROM_HASH] = parentHash;
        const parentKey = parentHash.toLowerCase();
        const parentIndex = (activeTxHash && this.caseManualTransactionHashesMatch(parentHash, activeTxHash) && activeIndex >= 0)
          ? activeIndex
          : (byHash.has(parentKey) ? byHash.get(parentKey) : activeIndex);
        const parentLabel = parentIndex >= 0
          ? this.caseManualNormalizeText(dataRows[parentIndex][CASE_MANUAL_START_COL.PATH_LABEL])
          : activeLabel;
        if (this.caseManualShouldRewriteContinuationPathLabel(outputRow, parentLabel, usedLabels)) {
          outputRow[CASE_MANUAL_START_COL.PATH_LABEL] = this.caseManualNextUnusedContinuationLabel(parentLabel, usedLabels);
        }
        usedLabels.add(this.caseManualNormalizeText(outputRow[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase());
        const fromLabel = this.caseManualNormalizeText(parentLabel || activeLabel);
        if (fromLabel) {
          outputRow[CASE_MANUAL_START_COL.FROM_LABEL] = fromLabel;
        }
        const insertAt = parentIndex >= 0
          ? this.caseManualFindInsertIndexAfterEntryBlockAtIndex(dataRows, parentIndex)
          : this.caseManualFindInsertIndexAfterParentChain(dataRows, parentHash);
        dataRows.splice(Math.max(0, Math.min(dataRows.length, insertAt)), 0, outputRow);
        rebuildByHashIndex();
        touchedHashes.add(hashKey);
        insertedHashes.add(hashKey);
        previousNewHash = hash;
      });

      this.caseManualSanitizeContinuesAsAgainstKnownRows(dataRows, resolveKnownHash);

      const occurrenceCountsByHash = new Map();
      dataRows.forEach(row => {
        const hashKey = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (hashKey && this.caseManualIsLikelyTransactionHash(hashKey)) {
          occurrenceCountsByHash.set(hashKey, (occurrenceCountsByHash.get(hashKey) || 0) + 1);
        }
      });
      const duplicateOccurrenceHashKeys = new Set(
        Array.from(occurrenceCountsByHash.entries())
          .filter(([, count]) => count > 1)
          .map(([hashKey]) => hashKey)
      );

      const occurrenceChildHashesByRowIndex = new Map();
      const addOccurrenceChild = (parentIndex, childHash) => {
        const normalizedChildHash = this.caseManualNormalizeText(childHash);
        if (!normalizedChildHash || !this.caseManualIsLikelyTransactionHash(normalizedChildHash)) return;
        if (!occurrenceChildHashesByRowIndex.has(parentIndex)) occurrenceChildHashesByRowIndex.set(parentIndex, []);
        const list = occurrenceChildHashesByRowIndex.get(parentIndex);
        if (!list.some(existing => this.caseManualTransactionHashesMatch(existing, normalizedChildHash))) list.push(normalizedChildHash);
      };

      dataRows.forEach((parentRow, parentIndex) => {
        const parentHash = this.caseManualNormalizeText(parentRow?.[CASE_MANUAL_START_COL.TX_HASH]);
        const parentHashKey = parentHash.toLowerCase();
        if (!parentHashKey || !duplicateOccurrenceHashKeys.has(parentHashKey)) return;
        const parentLabel = this.caseManualNormalizeText(parentRow?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        if (!parentLabel) return;
        dataRows.forEach((childRow, childIndex) => {
          if (childIndex === parentIndex) return;
          const childHash = this.caseManualNormalizeText(childRow?.[CASE_MANUAL_START_COL.TX_HASH]);
          if (!childHash || !this.caseManualIsLikelyTransactionHash(childHash)) return;
          const childParentHash = this.caseManualNormalizeText(resolveKnownHash(childRow?.[CASE_MANUAL_START_COL.FROM_HASH]) || childRow?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
          if (!childParentHash || !this.caseManualTransactionHashesMatch(childParentHash, parentHash)) return;
          const childFromLabel = this.caseManualNormalizeText(childRow?.[CASE_MANUAL_START_COL.FROM_LABEL]).toLowerCase();
          const childPathLabel = this.caseManualNormalizeText(childRow?.[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
          const labelShowsOccurrence = childFromLabel === parentLabel
            || (childPathLabel && childPathLabel !== parentLabel && childPathLabel.startsWith(parentLabel));
          if (labelShowsOccurrence) addOccurrenceChild(parentIndex, childHash);
        });
      });

      const childHashesByParent = this.caseManualBuildChildHashesByParentFromRows(dataRows, resolveKnownHash);

      const occurrenceKeyForRowIndex = index => `occ:${index}`;
      const childHashFromRef = child => {
        if (typeof child === 'string') return this.caseManualNormalizeText(child);
        return this.caseManualNormalizeText(child?.hash || child?.row?.[CASE_MANUAL_START_COL.TX_HASH]);
      };
      const rowByOccurrenceKeyForChildOrder = new Map();
      const rowByHashForChildOrder = new Map();
      dataRows.forEach((row, index) => {
        const rowHashKey = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (!rowHashKey || !this.caseManualIsLikelyTransactionHash(rowHashKey)) return;
        rowByOccurrenceKeyForChildOrder.set(occurrenceKeyForRowIndex(index), row);
        if (!rowByHashForChildOrder.has(rowHashKey)) rowByHashForChildOrder.set(rowHashKey, row);
      });
      childHashesByParent.forEach((children, parentKey) => {
        const parentHashKey = this.caseManualNormalizeText(parentKey).toLowerCase();
        const parentRow = rowByOccurrenceKeyForChildOrder.get(parentKey) || rowByHashForChildOrder.get(parentHashKey);
        const orderedFromQ = this.caseManualNormalizeText(parentRow?.[CASE_MANUAL_START_COL.CONTINUES_AS] || '')
          .replace(/^Continues\s+as\s*/iu, '')
          .split(',')
          .map(value => this.caseManualNormalizeText(resolveKnownHash(value) || value))
          .filter(hash => hash && this.caseManualIsLikelyTransactionHash(hash));
        if (orderedFromQ.length < 2) return;
        const ordered = [];
        orderedFromQ.forEach(qHash => {
          const match = children.find(child => this.caseManualTransactionHashesMatch(childHashFromRef(child), qHash)
            && !ordered.includes(child));
          if (match) ordered.push(match);
        });
        children.forEach(child => {
          if (!ordered.includes(child)) ordered.push(child);
        });
        childHashesByParent.set(parentKey, ordered);
      });
      this.caseManualApplyTool68BranchLabels(dataRows, childHashesByParent, usedLabels);
      const occurrenceChildHashesByRow = new Map();
      occurrenceChildHashesByRowIndex.forEach((children, index) => {
        const row = dataRows[index];
        if (row) occurrenceChildHashesByRow.set(row, children);
      });
      const childRefsByParentRow = new Map();
      childHashesByParent.forEach((children, parentKey) => {
        const parentHashKey = this.caseManualNormalizeText(parentKey).toLowerCase();
        const parentRow = rowByOccurrenceKeyForChildOrder.get(parentKey) || rowByHashForChildOrder.get(parentHashKey);
        if (parentRow) childRefsByParentRow.set(parentRow, children);
      });
      this.caseManualSortRowsLikeTool68Sapling(dataRows);
      const affectedHashes = new Set([...touchedHashes, ...insertedHashes]);
      if (activeHash) affectedHashes.add(activeHash);
      dataRows.forEach((row) => {
        const hashKey = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (hashKey && !this.caseManualIsLikelyTransactionHash(hashKey)) return;
        const occurrenceChildren = occurrenceChildHashesByRow.get(row) || [];
        const mappedChildren = childRefsByParentRow.get(row) || childHashesByParent.get(hashKey) || [];
        const children = occurrenceChildren.length ? occurrenceChildren : mappedChildren.map(childHashFromRef).filter(Boolean);
        if (children.length) {
          row[CASE_MANUAL_START_COL.CONTINUES_AS] = children.length === 1 ? `Continues as ${children[0]}` : `Continues as ${children.join(', ')}`;
          affectedHashes.add(hashKey);
        } else if (affectedHashes.has(hashKey)) {
          const duplicateOccurrence = duplicateOccurrenceHashKeys.has(hashKey);
          const existingChildren = this.caseManualExtractContinuesAsHashes(row[CASE_MANUAL_START_COL.CONTINUES_AS], dataRows);
          if (!duplicateOccurrence || !existingChildren.length) row[CASE_MANUAL_START_COL.CONTINUES_AS] = 'Path ends.';
        }
        const keepMovementNote = !this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_HASH]);
        const cleaned = this.caseManualMoveInferenceDetailsToSystemColumn(row, {
          dropMovementSentence: !keepMovementNote,
          keepFromLabelOnly: !keepMovementNote
        });
        const branchCleaned = this.caseManualCleanResolvedBranchParentRow(cleaned);
        const btcRootCleaned = this.caseManualCleanBtcRootSelectedChoicePriorBalance(branchCleaned);
        const presentationReady = this.caseManualNormalizeMainCaseRowPresentation(btcRootCleaned);
        row.splice(0, row.length, ...presentationReady);
      });

      // Keep Manual Solve's helper-only Column P blank, while preserving Column W as a
      // temporary Tool 88 diagnostics bucket requested for this workflow pass.
      const canonicalHeader = this.caseManualCanonicalizeCaseSheetRow(
        header || CASE_MANUAL_START_CANONICAL_HEADERS,
        { isHeader: true }
      );
      const mainRows = [
        canonicalHeader,
        ...dataRows
          .filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }))
          .map(row => this.caseManualCanonicalizeCaseSheetRow(row))
      ];
      const appendedMainRows = dataRows.filter(row => insertedHashes.has(this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase()));
      const changed = JSON.stringify(mainRows) !== originalMainRowsSignature;
      return {
        changed,
        header,
        mainRows,
        dataRows,
        appendedMainRows,
        touchedHashes,
        insertedHashes,
        affectedHashes,
        rawOutputRowsByHash,
        rawContinuationRowsByAnchor,
        originalMainRowsSignature,
        activeHash,
        activeLabel,
        rawUpdateConnectedFlow: options.rawUpdateConnectedFlow !== false
      };
    },

    caseManualRepairBridgeReceiveAccountingRows(rows = [], activeRow = []) {
      if (!Array.isArray(rows) || !rows.length || !Array.isArray(activeRow) || !activeRow.length) return rows;
      const parentHash = this.caseManualNormalizeText(activeRow?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      if (!parentHash) return rows;
      const parentFunds = this.caseManualParseNumericAmount(activeRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
      const parentLeft = this.caseManualParseNumericAmount(
        activeRow?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC]
          || this.caseManualNormalizeText(activeRow?.[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY]).split(/\r?\n/u)[0]
      );
      if (!Number.isFinite(parentFunds) && !Number.isFinite(parentLeft)) return rows;
      const extractFollowedToken = (value = '') => {
        const text = this.caseManualNormalizeText(value);
        const match = text.match(/(?:^|\s)([A-Za-z][A-Za-z0-9._-]{1,15})\s*$/u);
        return match ? match[1] : '';
      };
      const parentToken = this.caseManualNormalizeText(
        extractFollowedToken(activeRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED])
          || activeRow?.[CASE_MANUAL_START_COL.TOKEN]
      ).toUpperCase();
      const stableTokens = new Set(['USDT', 'USDC', 'DAI', 'USDD', 'BUSD', 'TUSD', 'USDP', 'PYUSD']);
      const tokensEquivalent = (left = '', right = '') => {
        const l = this.caseManualNormalizeText(left).toUpperCase().replace(/[^A-Z0-9]/gu, '');
        const r = this.caseManualNormalizeText(right).toUpperCase().replace(/[^A-Z0-9]/gu, '');
        if (!l || !r) return false;
        return l === r || (stableTokens.has(l) && stableTokens.has(r));
      };
      return rows.map((row) => {
        const next = this.caseManualEnsureRowWidth(row.slice ? row.slice() : row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
        const fromHash = this.caseManualNormalizeText(next?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
        if (!fromHash || !this.caseManualTransactionHashesMatch(fromHash, parentHash)) return next;
        if (!this.caseManualStartRowLooksSolvedBridgeReceive(next)) return next;
        const rowToken = this.caseManualNormalizeText(next?.[CASE_MANUAL_START_COL.TOKEN]).toUpperCase();
        if (!tokensEquivalent(parentToken, rowToken)) return next;
        const precisionOptions = {
          preserveSourcePrecision: this.caseManualRowHasExplicitReverseAllocationEvidence(activeRow)
            || this.caseManualRowHasExplicitReverseAllocationEvidence(next),
        };
        const rowAmount = this.caseManualParseNumericAmount(next?.[CASE_MANUAL_START_COL.AMOUNT]);
        const normalizedLeft = Number.isFinite(parentLeft) ? Math.max(0, parentLeft) : NaN;
        if (Number.isFinite(normalizedLeft)) {
          next[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = this.caseManualFormatPreciseAllocationNumber(normalizedLeft, rowToken || parentToken, {
            ...precisionOptions,
            useGrouping: false,
          }) || this.caseManualFormatAmount(normalizedLeft);
          next[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = this.caseManualFormatLeftBoundsNumber(normalizedLeft, rowToken || parentToken, precisionOptions) || this.caseManualFormatAmount(normalizedLeft);
        }
        if (Number.isFinite(parentFunds)) {
          const maxFollowable = Number.isFinite(rowAmount) && Number.isFinite(normalizedLeft)
            ? Math.max(0, rowAmount - normalizedLeft)
            : NaN;
          const nextFunds = Number.isFinite(maxFollowable) && maxFollowable > 0 && parentFunds > maxFollowable
            ? maxFollowable
            : parentFunds;
          next[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = this.caseManualFormatFundsFollowedCell(`${nextFunds} ${rowToken || parentToken}`, rowToken || parentToken, precisionOptions);
        }
        return next;
      });
    },

    caseManualAnnotateManualServiceOverrideParent(prepared = {}, options = {}) {
      if (!prepared || !Array.isArray(prepared.dataRows) || !prepared.activeHash) return prepared;
      const serviceOverrideAcknowledged = options.serviceOverrideAcknowledged === true;
      const unknownServiceEvidence = options.unknownServiceEvidence === true;
      if (!serviceOverrideAcknowledged && !unknownServiceEvidence) return prepared;
      const activeHashKey = this.caseManualNormalizeText(prepared.activeHash).toLowerCase();
      const parentRow = prepared.dataRows.find(row => this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase() === activeHashKey);
      if (!parentRow) return prepared;
      if (unknownServiceEvidence) {
        parentRow[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
          parentRow[CASE_MANUAL_START_COL.CASE_NOTES],
          'Tool 88 warning: Unknown service / hot-wallet-like behavior was detected at this address.'
        );
      }
      if (serviceOverrideAcknowledged) {
        parentRow[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
          parentRow[CASE_MANUAL_START_COL.CASE_NOTES],
          'Case Buddy: Investigator acknowledged the Unknown service / hot-wallet warning and intentionally traced through this address.'
        );
      }
      const cleaned = this.caseManualCleanResolvedBranchParentRow(parentRow);
      parentRow.splice(0, parentRow.length, ...cleaned);
      if (prepared.originalMainRowsSignature) {
        const canonicalHeader = this.caseManualCanonicalizeCaseSheetRow(
          prepared.header || CASE_MANUAL_START_CANONICAL_HEADERS,
          { isHeader: true }
        );
        prepared.mainRows = [
          canonicalHeader,
          ...prepared.dataRows
            .filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }))
            .map(row => this.caseManualCanonicalizeCaseSheetRow(row)),
        ];
        prepared.changed = JSON.stringify(prepared.mainRows) !== prepared.originalMainRowsSignature;
      }
      return prepared;
    },

    caseManualBuildRawRowsFromPreparedGraft(prepared = {}) {
      const rawMatrix = typeof this.getMatrixForLogicalSheet === 'function'
        ? (this.getMatrixForLogicalSheet('raw') || this.getMatrixForLogicalSheet('Raw Data') || [])
        : [];
      const rawRows = Array.isArray(rawMatrix) ? rawMatrix.map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0))) : [];
      const rawHasHeader = rawRows.length && this.caseManualIsHeaderRow(rawRows[0]);
      const rawHeader = rawHasHeader
        ? this.caseManualCanonicalizeCaseSheetRow(rawRows[0], { isHeader: true })
        : CASE_MANUAL_START_CANONICAL_HEADERS.slice();
      const rawDataRows = (rawHasHeader ? rawRows.slice(1) : rawRows)
        .map(row => this.caseManualCanonicalizeCaseSheetRow(row))
        .filter(row => this.caseManualManualSolveOutputRowIsUsable(row, { allowBtcContinuationRows: true }));

      const groupEntryBlocks = (rows = []) => {
        const blocks = [];
        let current = null;
        rows.forEach((row, originalIndex) => {
          const normalizedRow = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
          const hash = this.caseManualNormalizeText(normalizedRow[CASE_MANUAL_START_COL.TX_HASH]);
          const isEmpty = !this.caseManualRowHasEntryData(normalizedRow);
          if (hash) {
            current = { hashKey: hash.toLowerCase(), rows: [normalizedRow], originalIndex, passthrough: false };
            blocks.push(current);
            return;
          }
          if (current && !isEmpty) {
            current.rows.push(normalizedRow);
            return;
          }
          current = null;
        });
        return blocks;
      };

      const toRawLikeRow = (row = [], rowIndex = 0, sourceRow = null) => {
        const incomingCleanCandidate = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
        if (rowIndex > 0 && this.caseManualLooksLikeBtcInputAddressOnlyContinuation(incomingCleanCandidate)) {
          return this.caseManualCanonicalizeCaseSheetRow(this.caseManualCleanBtcInputAddressOnlyContinuationRow(incomingCleanCandidate));
        }
        const source = sourceRow
          ? this.caseManualEnsureRowWidth(sourceRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceRow?.length || 0))
          : incomingCleanCandidate;
        const incoming = incomingCleanCandidate;
        const rawLike = this.caseManualMergeRowsPreferOutput(source, incoming, { preservePathLabel: false });
        // Raw Data should preserve Tool88/WayFinder source values where Tool88 supplied them.
        // The prepared Main row may have display formatting (commas, rebuilt Column R details),
        // but Raw Data is the audit/support row set.
        [
          CASE_MANUAL_START_COL.DATE,
          CASE_MANUAL_START_COL.AMOUNT,
          CASE_MANUAL_START_COL.TOKEN,
          CASE_MANUAL_START_COL.SYSTEM_NOTES,
          CASE_MANUAL_START_COL.ATTRIBUTION,
          CASE_MANUAL_START_COL.ANALYST_NOTES,
          CASE_MANUAL_START_COL.FUNDS_FOLLOWED,
          CASE_MANUAL_START_COL.PRIOR_BALANCE,
          CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY,
          CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC,
          CASE_MANUAL_START_COL.CHAIN_ID,
          CASE_MANUAL_START_COL.CHAIN_NAME,
          CASE_MANUAL_START_COL.HOP_LOGIC,
          CASE_MANUAL_START_COL.CASE_NOTES
        ].forEach(colIndex => {
          const sourceValue = this.caseManualNormalizeText(source[colIndex]);
          if (sourceValue) rawLike[colIndex] = sourceValue;
        });
        rawLike[CASE_MANUAL_START_COL.PATH_LABEL] = '';
        rawLike[CASE_MANUAL_START_COL.FROM_LABEL] = '';
        if (rowIndex > 0 && !this.caseManualNormalizeText(rawLike[CASE_MANUAL_START_COL.TX_HASH])) {
          rawLike[CASE_MANUAL_START_COL.FROM_HASH] = '';
        }
        rawLike[CASE_MANUAL_START_COL.CONTINUES_AS] = this.caseManualNormalizeContinuesAsText(rawLike[CASE_MANUAL_START_COL.CONTINUES_AS]) || rawLike[CASE_MANUAL_START_COL.CONTINUES_AS];
        const rawToken = this.caseManualNormalizeText(rawLike[CASE_MANUAL_START_COL.TOKEN]);
        const rawPrecisionOptions = {
          preserveSourcePrecision: this.caseManualRowHasExplicitReverseAllocationEvidence(rawLike),
        };
        rawLike.splice(0, rawLike.length, ...this.caseManualCleanBtcRootSelectedChoicePriorBalance(rawLike));
        rawLike[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = this.caseManualFormatFundsForMain(rawLike[CASE_MANUAL_START_COL.FUNDS_FOLLOWED], rawToken, rawPrecisionOptions) || rawLike[CASE_MANUAL_START_COL.FUNDS_FOLLOWED];
        rawLike[CASE_MANUAL_START_COL.PRIOR_BALANCE] = this.caseManualFormatPriorBalanceNumber(rawLike[CASE_MANUAL_START_COL.PRIOR_BALANCE], rawToken) || rawLike[CASE_MANUAL_START_COL.PRIOR_BALANCE];
        rawLike[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = this.caseManualFormatLeftBoundsDisplayValue('', rawLike[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || rawLike[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY], rawToken, rawPrecisionOptions) || rawLike[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC];
        rawLike[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = this.caseManualLeftBoundsPrimaryValue(
          rawLike[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY],
          rawLike[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || '0',
          rawToken,
          rawPrecisionOptions
        );
        return this.caseManualCanonicalizeCaseSheetRow(rawLike, {
          preserveAllocationPrecision: rawPrecisionOptions.preserveSourcePrecision,
        });
      };

      const changedHashes = new Set([
        ...(prepared.touchedHashes || []),
        ...(prepared.insertedHashes || []),
        ...(prepared.affectedHashes || [])
      ]);
      if (prepared.rawUpdateConnectedFlow !== false) {
        const flowHashes = this.caseManualCollectConnectedFlowHashes(prepared.dataRows || [], prepared.activeHash || Array.from(changedHashes)[0] || '');
        flowHashes.forEach(hash => changedHashes.add(hash));
      }

      const rawBlocks = groupEntryBlocks(rawDataRows);
      const rawBlockByHash = new Map();
      rawBlocks.forEach((block, index) => {
        if (block.hashKey && !rawBlockByHash.has(block.hashKey)) rawBlockByHash.set(block.hashKey, index);
      });

      let appendedCount = 0;
      let upsertedCount = 0;
      groupEntryBlocks(prepared.dataRows || []).forEach(preparedBlock => {
        const hashKey = this.caseManualNormalizeText(preparedBlock?.rows?.[0]?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (!hashKey || !changedHashes.has(hashKey)) return;
        const continuationSourceRows = prepared.rawContinuationRowsByAnchor?.get?.(hashKey) || [];
        const replacementRows = preparedBlock.rows.map((row, rowIndex) => {
          const sourceRow = rowIndex === 0
            ? prepared.rawOutputRowsByHash?.get?.(hashKey)
            : continuationSourceRows[rowIndex - 1];
          return toRawLikeRow(row, rowIndex, sourceRow);
        });
        if (rawBlockByHash.has(hashKey)) {
          const blockIndex = rawBlockByHash.get(hashKey);
          const before = JSON.stringify(rawBlocks[blockIndex]?.rows || []);
          rawBlocks[blockIndex] = { ...rawBlocks[blockIndex], hashKey, rows: replacementRows, passthrough: false };
          if (JSON.stringify(replacementRows) !== before) upsertedCount += 1;
        } else {
          rawBlocks.push({ hashKey, rows: replacementRows, originalIndex: rawBlocks.length, passthrough: false });
          rawBlockByHash.set(hashKey, rawBlocks.length - 1);
          appendedCount += 1;
          upsertedCount += 1;
        }
      });

      const canonicalRawRows = rawBlocks
        .flatMap(block => this.caseManualCleanBtcInputContinuationRowsInBlock(block.rows))
        .map(row => this.caseManualCanonicalizeCaseSheetRow(row))
        .filter(row => this.caseManualManualSolveOutputRowIsUsable(row, { allowBtcContinuationRows: true }));
      return { rows: [rawHeader, ...canonicalRawRows], appendedCount, changed: upsertedCount > 0 };
    },

    async writeCaseManualStartHopOutputRowsToMain(rows = [], options = {}) {
      const prepared = options.prepared || this.caseManualPrepareHopOutputRowsForGraft(rows);
      if (!prepared?.changed) return { changed: false, count: 0, rows: [] };
      const mainRowsForWrite = Array.isArray(prepared.dataRows)
        ? [
          this.caseManualCanonicalizeCaseSheetRow(
            prepared.header || prepared.mainRows?.[0] || CASE_MANUAL_START_CANONICAL_HEADERS,
            { isHeader: true }
          ),
          ...this.caseManualCleanBtcInputContinuationRowsInBlock(prepared.dataRows)
            .map(row => this.caseManualCleanBtcRootSelectedChoicePriorBalance(row))
            .filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }))
            .map(row => this.caseManualCanonicalizeCaseSheetRow(row))
        ]
        : (prepared.mainRows || []);
      const canonicalMainRows = this.caseManualCanonicalizeCaseSheetMatrix(mainRowsForWrite);
      await this.replaceCaseManualStartLogicalSheetMatrix('main', canonicalMainRows, 'Applied Tool 88 manual-start hop output as a grafted visual path.');
      prepared.mainRows = canonicalMainRows;
      if (typeof this.refreshRawDataImportCoverage === 'function') this.refreshRawDataImportCoverage();
      const rowCount = Math.max(0, prepared.mainRows.length - 1);
      return { changed: true, count: rowCount, appendedCount: prepared.appendedMainRows?.length || 0, rows: prepared.mainRows, prepared };
    },

    async appendCaseManualStartHopOutputRowsToRaw(rows = [], options = {}) {
      const prepared = options.prepared || this.caseManualPrepareHopOutputRowsForGraft(rows);
      if (!prepared?.changed) return { changed: false, appendedCount: 0 };
      const rawResult = this.caseManualBuildRawRowsFromPreparedGraft(prepared);
      if (!rawResult.changed) return { changed: false, appendedCount: 0 };
      try {
        await this.replaceCaseManualStartLogicalSheetMatrix('raw', rawResult.rows, 'Updated Raw Data from Tool 88 manual-start visual graft.');
        return { changed: true, appendedCount: rawResult.appendedCount };
      } catch (error) {
        console.warn('Case Buddy manual-start raw update failed.', error);
        return { changed: false, appendedCount: 0, error: error?.message || 'Raw Data update failed.' };
      }
    },

    caseManualAppendUnknownServiceEvidenceToRelevantRows(rows = [], activeCandidate = {}) {
      const normalizedRows = (Array.isArray(rows) ? rows : [])
        .map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)));
      const activeHash = this.caseManualNormalizeText(activeCandidate?.hash || activeCandidate?.transactionHash || activeCandidate?.txHash).toLowerCase();
      const activeAddress = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(activeCandidate));
      const targetIndexes = new Set();

      normalizedRows.forEach((row, index) => {
        const rowHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (activeHash && this.caseManualTransactionHashesMatch(rowHash, activeHash)) targetIndexes.add(index);
      });

      if (!targetIndexes.size && activeAddress) {
        normalizedRows.forEach((row, index) => {
          const fromAddress = this.caseManualNormalizeAddressKey(row[CASE_MANUAL_START_COL.FROM_ADDRESS]);
          const toAddress = this.caseManualNormalizeAddressKey(row[CASE_MANUAL_START_COL.TO_ADDRESS]);
          const continuesAs = this.caseManualNormalizeContinuesAsText(row[CASE_MANUAL_START_COL.CONTINUES_AS]);
          if ((fromAddress === activeAddress || toAddress === activeAddress) && /path\s*ends/iu.test(continuesAs)) targetIndexes.add(index);
        });
      }

      if (!targetIndexes.size && normalizedRows.length) targetIndexes.add(normalizedRows.length - 1);

      return normalizedRows.map((row, index) => {
        if (!targetIndexes.has(index)) return row;
        row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
          row[CASE_MANUAL_START_COL.CASE_NOTES],
          'Tool 88: Unknown service / hot-wallet-like path ending evidence; review before tracing through again.'
        );
        return row;
      });
    },

    caseManualCleanPathEndingAttributionLabel(value = '') {
      const label = this.caseManualCleanAttributionLabel(value)
        .replace(/\s*\((?:contract|contract like|bridge like|swap like|wrapped asset|hot wallet|deposit address)[^)]+\)\s*$/iu, '')
        .replace(/\s+(?:Deposit\s+Address|Hot\s+Wallet)$/iu, '')
        .replace(/\s+/gu, ' ')
        .trim();
      if (!label || /^unknown$|^unattributed$|^no\s+visible\s+hit$|^none$|^n\/?a$/iu.test(label)) return '';
      return label;
    },

    caseManualBuildPathEndingTargetContext(snapshot = {}, options = {}) {
      const activeCandidate = options.selectedCandidate
        || this.caseManualStartHopInspectorSelectedCandidate
        || this.caseManualStartHopInspectorCandidate
        || this.caseInvestigationVisualizerSelectedCandidate
        || {};
      const selectedRow = Array.isArray(activeCandidate?.rawRow)
        ? activeCandidate.rawRow
        : (Array.isArray(activeCandidate?.row) ? activeCandidate.row : []);
      const selectedEntry = snapshot?.selectedEntry || snapshot?.result?.selectedEntry || {};
      const direction = this.caseManualStartOutputChoiceDirectionForCandidate(activeCandidate);
      const reverse = direction === 'reverse';
      const hashes = new Set();
      const addresses = new Set();
      const pathLabels = new Set();
      const addHash = value => {
        const hash = this.caseManualNormalizeText(value).toLowerCase();
        if (hash && this.caseManualIsLikelyTransactionHash(hash)) hashes.add(hash);
      };
      const addAddress = value => {
        const address = this.caseManualNormalizeAddressKey(value);
        if (address) addresses.add(address);
      };
      const addPathLabel = value => {
        const label = this.caseManualNormalizeText(value).toLowerCase();
        if (label) pathLabels.add(label);
      };
      addHash(activeCandidate?.hash);
      addHash(activeCandidate?.transactionHash);
      addHash(activeCandidate?.txHash);
      addHash(activeCandidate?.targetHash);
      addHash(activeCandidate?.caseCandidate?.hash);
      addHash(activeCandidate?.candidate?.hash);
      addHash(selectedRow[CASE_MANUAL_START_COL.TX_HASH]);
      addHash(selectedEntry?.txHash);
      addHash(selectedEntry?.hash);
      addAddress(this.caseManualStartCandidateEndpointAddress(activeCandidate));
      addAddress(reverse ? activeCandidate?.caseCandidate?.fromAddress : activeCandidate?.caseCandidate?.toAddress);
      addAddress(reverse ? activeCandidate?.candidate?.fromAddress : activeCandidate?.candidate?.toAddress);
      addAddress(selectedRow[reverse ? CASE_MANUAL_START_COL.FROM_ADDRESS : CASE_MANUAL_START_COL.TO_ADDRESS]);
      addAddress(snapshot?.selectedTargetAddress);
      addAddress(reverse ? selectedEntry?.fromAddress : selectedEntry?.toAddress);
      addPathLabel(activeCandidate?.pathLabel);
      addPathLabel(activeCandidate?.caseCandidate?.pathLabel);
      addPathLabel(activeCandidate?.candidate?.pathLabel);
      addPathLabel(selectedRow[CASE_MANUAL_START_COL.PATH_LABEL]);
      addPathLabel(selectedEntry?.pathLabel);
      return {
        direction,
        reverse,
        hashes,
        addresses,
        pathLabels,
        hasAny: Boolean(hashes.size || addresses.size || pathLabels.size)
      };
    },

    caseManualPathEndingRowMatchesTargetContext(row = [], targetContext = {}) {
      if (!Array.isArray(row) || !targetContext?.hasAny) return false;
      const reverse = targetContext?.reverse === true || targetContext?.direction === 'reverse';
      const rowHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const rowEndpointAddress = this.caseManualNormalizeAddressKey(
        row[reverse ? CASE_MANUAL_START_COL.FROM_ADDRESS : CASE_MANUAL_START_COL.TO_ADDRESS]
      );
      const rowPathLabel = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
      if (rowHash && targetContext.hashes?.has?.(rowHash)) return true;
      if (rowPathLabel && targetContext.pathLabels?.has?.(rowPathLabel)) return true;
      return Boolean(rowEndpointAddress && targetContext.addresses?.has?.(rowEndpointAddress));
    },

    caseManualPathEndingAttributionLabelForRow(row = [], snapshot = {}, options = {}) {
      const activeCandidate = options.selectedCandidate
        || this.caseManualStartHopInspectorSelectedCandidate
        || this.caseManualStartHopInspectorCandidate
        || this.caseInvestigationVisualizerSelectedCandidate
        || {};
      const reverse = this.caseManualStartCandidateIsReverse(activeCandidate);
      const rowDirectValues = reverse
        ? [
          row?.[CASE_MANUAL_START_COL.FROM_ATTRIBUTION],
          row?.fromAttribution,
          row?.fromAttributionLabel,
          row?.fromEntity,
          row?.sourceEntity
        ]
        : [
          row?.[CASE_MANUAL_START_COL.ATTRIBUTION],
          row?.attribution,
          row?.toAttributionLabel,
          row?.toLabel,
          row?.entity
        ];
      for (const value of rowDirectValues) {
        const normalized = reverse
          ? this.caseManualCanonicalizeFromAttributionForColumnX(value || '')
          : value;
        const label = this.caseManualCleanPathEndingAttributionLabel(normalized);
        if (label) return label;
      }
      if (options.allowSnapshotFallback === false) return '';
      const fallbackValues = reverse
        ? [
          activeCandidate?.fromAttributionLabel,
          activeCandidate?.fromEntityLabel,
          activeCandidate?.sourceEntityLabel,
          activeCandidate?.fromEntity,
          snapshot?.fromEntity,
          snapshot?.fromAttribution,
          snapshot?.result?.fromEntity,
          snapshot?.result?.fromAttribution,
          snapshot?.result?.pathEnding?.entity,
          snapshot?.result?.pathEnding?.label,
          snapshot?.decision?.entity,
          snapshot?.decision?.label
        ]
        : [
          activeCandidate?.attribution,
          activeCandidate?.toAttributionLabel,
          activeCandidate?.entity,
          activeCandidate?.label,
          snapshot?.entity,
          snapshot?.attribution,
          snapshot?.result?.entity,
          snapshot?.result?.attribution,
          snapshot?.result?.pathEnding?.entity,
          snapshot?.result?.pathEnding?.label,
          snapshot?.decision?.entity,
          snapshot?.decision?.label
        ];
      for (const value of fallbackValues) {
        const normalized = reverse
          ? this.caseManualCanonicalizeFromAttributionForColumnX(value || '')
          : value;
        const label = this.caseManualCleanPathEndingAttributionLabel(normalized);
        if (label) return label;
      }
      return '';
    },

    caseManualPathEndingReasonFromSnapshot(snapshot = {}) {
      const reason = [
        snapshot?.result?.pathEnding?.reason,
        snapshot?.decision?.summary,
        snapshot?.message,
        snapshot?.result?.summary
      ].map(value => this.caseManualNormalizeText(value)).find(Boolean) || 'Tool 88 path ending recorded.';
      return reason.replace(/\s+/gu, ' ').trim();
    },

    caseManualAnnotatePathEndingRowsWithAttribution(rows = [], snapshot = {}) {
      const normalizedRows = (Array.isArray(rows) ? rows : [])
        .map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)));
      const targetContext = this.caseManualBuildPathEndingTargetContext(snapshot);
      const activeCandidate = this.caseManualStartHopInspectorSelectedCandidate
        || this.caseManualStartHopInspectorCandidate
        || this.caseInvestigationVisualizerSelectedCandidate
        || {};
      const attributionColumnIndex = this.caseManualStartCandidateAttributionColumnIndex(activeCandidate);
      const addressPoisoningEvidence = this.caseManualStartAddressPoisoningEvidenceForCandidate(activeCandidate, { snapshot });
      const targetIndexes = new Set();
      normalizedRows.forEach((row, index) => {
        const continuesAs = this.caseManualNormalizeContinuesAsText(row[CASE_MANUAL_START_COL.CONTINUES_AS] || '');
        if (!/path\s*ends/iu.test(continuesAs)) return;
        if (this.caseManualPathEndingRowMatchesTargetContext(row, targetContext)) targetIndexes.add(index);
      });
      if (!targetIndexes.size && normalizedRows.length === 1) {
        const row = normalizedRows[0];
        const continuesAs = this.caseManualNormalizeContinuesAsText(row?.[CASE_MANUAL_START_COL.CONTINUES_AS] || '');
        if (/path\s*ends/iu.test(continuesAs) && (!targetContext.hasAny || this.caseManualPathEndingRowMatchesTargetContext(row, targetContext))) targetIndexes.add(0);
      }
      return normalizedRows.map((row, index) => {
        if (!targetIndexes.has(index)) return row;
        if (addressPoisoningEvidence?.isMatch) {
          return this.caseManualAnnotateAddressPoisoningPathEndingRow(row, addressPoisoningEvidence, activeCandidate);
        }
        const label = this.caseManualPathEndingAttributionLabelForRow(row, snapshot, { allowSnapshotFallback: true, selectedCandidate: activeCandidate });
        if (!label) return row;
        const existing = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.SYSTEM_NOTES] || '');
        const labelPathSentence = `${label}. Path ends.`;
        const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
        const hasLabelPathSentence = new RegExp(`${escapedLabel}\\.?\\s+Path\\s+ends\\.?`, 'iu').test(existing);
        if (!hasLabelPathSentence) {
          row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualAppendUniqueLine(existing, labelPathSentence);
        }
        if (!this.caseManualNormalizeText(row[attributionColumnIndex])) {
          row[attributionColumnIndex] = label;
        }
        return row;
      });
    },

    caseManualSelectPathEndingRowsForActiveTarget(rows = [], snapshot = {}) {
      const normalizedRows = (Array.isArray(rows) ? rows : [])
        .map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)))
        .filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }));
      const targetContext = this.caseManualBuildPathEndingTargetContext(snapshot);
      const makePathEndingRows = selectedRows => selectedRows.map(row => {
        const copy = this.caseManualEnsureRowWidth(row.slice(), Math.max(CASE_MANUAL_START_COLUMN_COUNT, row.length || 0));
        copy[CASE_MANUAL_START_COL.CONTINUES_AS] = 'Path ends.';
        return copy;
      });
      if (targetContext.hasAny) {
        const targetRows = normalizedRows.filter(row => this.caseManualPathEndingRowMatchesTargetContext(row, targetContext));
        if (targetRows.length) {
          return this.caseManualAnnotatePathEndingRowsWithAttribution(makePathEndingRows(targetRows), snapshot);
        }
      }
      if (normalizedRows.length === 1) {
        return this.caseManualAnnotatePathEndingRowsWithAttribution(makePathEndingRows(normalizedRows), snapshot);
      }
      return this.buildCaseManualStartPathEndingFallbackRows(snapshot);
    },

    buildCaseManualStartPathEndingFallbackRows(snapshot = {}) {
      const activeCandidate = this.caseManualStartHopInspectorSelectedCandidate
        || this.caseManualStartHopInspectorCandidate
        || this.caseInvestigationVisualizerSelectedCandidate
        || {};
      if (!activeCandidate?.caseManualStartCandidate) return [];
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate(activeCandidate);
      const sourceRow = Array.isArray(currentRow)
        ? currentRow
        : (Array.isArray(activeCandidate.rawRow) ? activeCandidate.rawRow : (Array.isArray(activeCandidate.row) ? activeCandidate.row : []));
      if (!sourceRow.length) return [];
      const row = this.caseManualEnsureRowWidth(sourceRow.slice(), Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceRow.length || 0));
      const selectedAddress = this.caseManualStartCandidateEndpointAddress({ ...activeCandidate, row, rawRow: row });
      const attributionColumnIndex = this.caseManualStartCandidateAttributionColumnIndex(activeCandidate);
      const reason = this.caseManualPathEndingReasonFromSnapshot(snapshot);
      const targetContext = this.caseManualBuildPathEndingTargetContext(snapshot, { selectedCandidate: activeCandidate });
      const rowCanUseSnapshotAttribution = !targetContext.hasAny || this.caseManualPathEndingRowMatchesTargetContext(row, targetContext);
      const label = this.caseManualPathEndingAttributionLabelForRow(row, snapshot, { allowSnapshotFallback: rowCanUseSnapshotAttribution, selectedCandidate: activeCandidate });
      row[CASE_MANUAL_START_COL.CONTINUES_AS] = 'Path ends.';
      if (label && !this.caseManualNormalizeText(row[attributionColumnIndex])) row[attributionColumnIndex] = label;
      if (selectedAddress) {
        row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
          row[CASE_MANUAL_START_COL.CASE_NOTES],
          `Tool 88: Path ends at ${selectedAddress}. ${reason}`
        );
      }
      const direction = this.caseManualStartCandidateIsReverse(activeCandidate) ? 'reverse' : 'forward';
      row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
        row[CASE_MANUAL_START_COL.CASE_NOTES],
        `Tool 88 flow: assessed; action=manual-path-ending; direction=${direction}; ${reason.slice(0, 180)}`
      );
      row[CASE_MANUAL_START_COL.HOP_LOGIC] = row[CASE_MANUAL_START_COL.HOP_LOGIC] || this.caseManualStartHopLogicForRows?.([row]) || 'FIFO';
      return this.caseManualAnnotatePathEndingRowsWithAttribution([row], snapshot);
    },

    async applyCaseManualStartHopInspectorOutputRows(options = {}) {
      const explicitRows = Array.isArray(options.rows) && options.rows.length ? options.rows : [];
      const snapshotForFlags = options.snapshot || this.caseManualStartHopInspectorPreviewSnapshot || this.caseManualStartHopInspectorSnapshot || {};
      const activeCandidate = options.candidate || this.caseManualStartHopInspectorPreviewCandidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || null;
      let rows = explicitRows.length
        ? explicitRows
        : (this.caseManualStartHopInspectorOutputRows.length
          ? this.caseManualStartHopInspectorOutputRows
          : this.extractCaseManualStartSnapshotRows(snapshotForFlags, { candidate: activeCandidate }));
      const reverseCandidate = Boolean(activeCandidate && this.caseManualStartCandidateIsReverse(activeCandidate));
      const dormantResultForFlags = reverseCandidate ? null : this.caseManualExtractDormantResultFromSnapshot(snapshotForFlags);
      if (this.caseManualSnapshotShouldRequireTraceExitRows(snapshotForFlags, activeCandidate) && !this.caseManualSnapshotHasValidTraceExitRows(snapshotForFlags) && !dormantResultForFlags) {
        this.caseManualStartHopInspectorStatus = 'Tool 88 did not return a valid trace-through next-hop transaction hash, so Case Buddy left Main Data and Raw Data unchanged.';
        this.caseManualStartHopInspectorOutputRows = [];
        this.caseManualStartHopInspectorPreviewRows = [];
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        const autoResolution = await this.caseManualStartHandleViewportAutoTraceNoProgress(activeCandidate, {
          reason: this.caseManualStartHopInspectorStatus,
        });
        return autoResolution?.handled ? { noProgress: true, autoResolution } : null;
      }
      const serviceOverrideAcknowledged = Boolean(
        this.caseManualStartHopInspectorPreviewRequiresServiceOverride ||
        this.caseManualStartHopInspectorSnapshotHasManualServiceOverride(snapshotForFlags) ||
        this.caseManualStartHopInspectorLastTraceServiceOverride
      );
      rows = rows
        .map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)))
        .filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }));
      const applyAction = this.caseManualNormalizeText(options.action || this.caseManualStartHopInspectorPreviewAction || snapshotForFlags?.integrationAction || '');
      rows = this.caseManualFilterRowsToSelectedHop(rows, snapshotForFlags || {}, activeCandidate, {
        includeParent: true,
        includeChildren: true,
        fallbackParent: true,
        action: applyAction,
        force: this.caseManualPreviewActionLooksTraceThrough(applyAction) || Boolean(dormantResultForFlags)
      });
      const applyActionKey = this.normalizeCaseManualStartHopInspectorActionKey(applyAction);
      if (dormantResultForFlags) {
        rows = this.caseManualBuildDormantRowsForApply(rows, snapshotForFlags, activeCandidate);
      } else if (applyActionKey === 'path-ending') {
        rows = this.caseManualSelectPathEndingRowsForActiveTarget(rows, snapshotForFlags);
      }
      const automationCalloutContext = this.caseManualStartAutomationCalloutContext?.enabled && this.caseManualStartViewportAutomationCalloutsAreEnabled?.()
        ? { ...(this.caseManualStartAutomationCalloutContext || {}) }
        : null;
      const automationCalloutBeforeState = automationCalloutContext
        ? this.captureCaseManualStartViewportAutomationCalloutBeforeState(snapshotForFlags, applyAction)
        : null;
      const dormantApply = !reverseCandidate && (!!dormantResultForFlags || this.caseManualRowsContainDormantUpdate(rows));
      if (this.caseManualPreviewActionLooksTraceThrough(applyAction) && !dormantApply && !this.caseManualPreviewRowsContainTraceChild(rows, activeCandidate, snapshotForFlags)) {
        this.caseManualStartHopInspectorStatus = 'Trace apply was blocked because Tool 88 did not provide a valid new child transaction for the selected ending.';
        this.caseManualStartHopInspectorOutputRows = [];
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        const autoResolution = await this.caseManualStartHandleViewportAutoTraceNoProgress(activeCandidate, {
          reason: this.caseManualStartHopInspectorStatus,
        });
        return autoResolution?.handled ? { noProgress: true, autoResolution } : null;
      }
      const unknownServiceEvidence = this.caseManualStartHopInspectorSnapshotLooksUnknownServicePathEndingForCandidate(snapshotForFlags, activeCandidate)
        || this.caseManualStartHopInspectorRowsContainUnknownServicePathEndingEvidenceForCandidate(rows, activeCandidate)
        || (!activeCandidate?.caseManualStartCandidate && this.caseManualRowsContainUnknownServicePathEndingEvidence(rows));
      const activeDirection = this.caseManualStartOutputChoiceDirectionForCandidate(activeCandidate || {});
      const rowsHaveMeaningfulEndpointAttribution = rows.some(row => this.caseManualStartCandidateHasMeaningfulEndpointAttribution({
        caseManualStartCandidate: true,
        row,
        rawRow: row,
        traceDirection: activeDirection,
        analysisDirection: activeDirection,
        fromAddress: row?.[CASE_MANUAL_START_COL.FROM_ADDRESS] || '',
        toAddress: row?.[CASE_MANUAL_START_COL.TO_ADDRESS] || '',
        fromAttributionLabel: row?.[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] || '',
        attribution: row?.[CASE_MANUAL_START_COL.ATTRIBUTION] || '',
        toAttributionLabel: row?.[CASE_MANUAL_START_COL.ATTRIBUTION] || ''
      }));
      if (rows.length && unknownServiceEvidence && !serviceOverrideAcknowledged && !rowsHaveMeaningfulEndpointAttribution) {
        const activeCandidate = this.caseManualStartHopInspectorCandidate || this.caseHopSolverCandidate || this.caseInvestigationVisualizerSelectedCandidate || {};
        rows = this.caseManualAppendUnknownServiceEvidenceToRelevantRows(rows, activeCandidate);
      }
      if (!rows.length) {
        this.caseManualStartHopInspectorStatus = 'No Tool 88 output rows are available to apply yet.';
        return null;
      }
      const effectiveApplyActionKey = dormantApply && !this.caseManualDormantResultIsPartial(dormantResultForFlags)
        ? 'path-ending'
        : applyActionKey;
      const workIndicatorGeneration = this.beginCaseManualStartViewportWorkIndicator(effectiveApplyActionKey || applyAction || 'trace-through', activeCandidate, {
        snapshot: snapshotForFlags,
        rows,
        message: dormantApply
          ? 'Applying dormant funds and updating the Manual Solve visual.'
          : 'Applying Tool 88 rows and updating the Manual Solve visual.'
      });
      this.caseManualStartHopInspectorRunning = true;
      try {
        await this.$nextTick();
        await this.waitForCaseInvestigationVisualizerCameraIdle?.(700);
        await this.caseManualSolveYieldToBrowser?.(1);
        const prepared = this.caseManualPrepareHopOutputRowsForGraft(rows, {
          includeVisualRows: effectiveApplyActionKey !== 'path-ending',
          rawUpdateConnectedFlow: effectiveApplyActionKey !== 'path-ending'
        });
        this.caseManualAnnotateManualServiceOverrideParent(prepared, { serviceOverrideAcknowledged, unknownServiceEvidence });
        const mainResult = await this.writeCaseManualStartHopOutputRowsToMain(rows, { prepared });
        const rawResult = await this.appendCaseManualStartHopOutputRowsToRaw(rows, { prepared });
        await this.refreshCaseInvestigationVisualizer({ preserveFlow: true });
        if (automationCalloutContext) {
          this.queueCaseManualStartViewportAutomationCallouts({
            context: automationCalloutContext,
            beforeState: automationCalloutBeforeState,
            rows,
            snapshot: snapshotForFlags,
            action: applyAction,
            mainResult,
            rawResult
          });
        }
        const traceApply = this.caseManualPreviewActionLooksTraceThrough(applyAction) && !dormantApply;
        const refreshedActiveCandidate = this.caseManualStartCandidateWithCurrentMainRow(activeCandidate);
        const traceStillUnresolved = Boolean(
          traceApply
            && refreshedActiveCandidate?.caseManualStartCandidate
            && this.isCaseManualStartUnresolvedEndingCandidate(refreshedActiveCandidate)
        );
        if (traceStillUnresolved) {
          this.caseManualStartHopInspectorPreviewOpen = false;
          this.caseManualStartHopInspectorPreviewApplied = false;
          this.caseManualStartHopInspectorPreviewRows = [];
          this.caseManualStartHopInspectorOutputRows = [];
          const directionLabel = this.caseManualStartCandidateIsReverse(refreshedActiveCandidate) ? 'Reverse' : 'Forward';
          const reason = `${directionLabel} trace-through returned rows, but they did not create a continuation for the selected path occurrence.`;
          const autoResolution = await this.caseManualStartHandleViewportAutoTraceNoProgress(refreshedActiveCandidate, { reason });
          if (!autoResolution?.handled) {
            this.caseManualStartHopInspectorStatus = `${reason} Main Data was not treated as solved; review the trace result or use the full Flow Solver.`;
            this.caseInvestigationVisualizerStatus = this.caseManualStartHopInspectorStatus;
            if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
          }
          return { mainResult, rawResult, noProgress: true, autoResolution };
        }
        this.caseManualStartClearViewportAutoTraceNoProgress(activeCandidate);
        const nextCandidate = this.getCaseManualStartActivePathEndingCandidate?.();
        const nextCandidateIsUnresolved = this.isCaseManualStartUnresolvedEndingCandidate(nextCandidate);
        if (this.caseManualStartHopInspectorOpen && nextCandidateIsUnresolved) {
          const selectedNextCandidate = this.selectCaseManualStartPathEndingNode(nextCandidate) || nextCandidate;
          this.caseManualStartHopInspectorCandidate = selectedNextCandidate;
          this.resetCaseManualStartHopInspectorDecisionStateForCandidate(selectedNextCandidate, { force: true });
          this.queueCaseManualStartHopInspectorAutoCheck(selectedNextCandidate, { force: true, delayMs: 450, reason: 'Applied the hop and selected the next unresolved ending. Tool 88 checks will run automatically.' });
        } else if (this.caseManualStartHopInspectorOpen && !nextCandidateIsUnresolved) {
          this.caseManualStartHopInspectorCandidate = null;
          this.caseInvestigationVisualizerSelectedCandidate = null;
          this.caseInvestigationVisualizerSelectedNodeId = '';
          this.caseInvestigationVisualizerSelectedNodeIds = [];
          this.queueCaseManualStartViewportCompletionCheck('applied hop output and no unresolved endings remain');
        }
        if (mainResult?.changed || rawResult?.changed) this.caseManualStartHopInspectorAppliedCount += 1;
        this.caseManualStartHopInspectorPreviewOpen = false;
        this.caseManualStartHopInspectorPreviewApplied = true;
        const nextStatusSuffix = nextCandidateIsUnresolved ? ' and the next ending is being checked.' : '.';
        this.caseManualStartHopInspectorStatus = dormantApply
          ? `Applied dormant funds ${this.caseManualDormantResultIsPartial(dormantResultForFlags) ? 'remainder' : 'path ending'} to Main Data${rawResult.appendedCount ? ` and appended ${rawResult.appendedCount} row${rawResult.appendedCount === 1 ? '' : 's'} to Raw Data` : ''}. The visual has been refreshed${nextStatusSuffix}`
          : `${mainResult?.changed || rawResult?.changed ? 'Applied Flow Solver output to Main Data' : 'Confirmed the existing Flow Solver continuation in Main Data'}${rawResult.appendedCount ? ` and appended ${rawResult.appendedCount} row${rawResult.appendedCount === 1 ? '' : 's'} to Raw Data` : ''}. The visual has been refreshed${nextStatusSuffix}`;
        return { mainResult, rawResult, noProgress: false };
      } finally {
        this.finishCaseManualStartViewportWorkIndicator(workIndicatorGeneration);
        this.caseManualStartHopInspectorRunning = false;
      }
    },

    async applyCaseManualStartHopSolverOutputToMain() {
      if (!Array.isArray(this.caseHopSolverOutputRows) || !this.caseHopSolverOutputRows.length) {
        this.caseHopSolverRawApplyStatus = 'No Flow Solver output rows are available to apply to the manual visual.';
        return null;
      }
      const workIndicatorGeneration = this.beginCaseManualStartViewportWorkIndicator('trace-through', this.caseHopSolverCandidate || this.caseManualStartHopInspectorCandidate, {
        rows: this.caseHopSolverOutputRows,
        snapshot: this.caseHopSolverSnapshot || {},
        message: 'Applying Full Flow Solver rows and updating the Manual Solve visual.'
      });
      this.caseHopSolverRunning = true;
      try {
        await this.$nextTick();
        const prepared = this.caseManualPrepareHopOutputRowsForGraft(this.caseHopSolverOutputRows);
        const mainResult = await this.writeCaseManualStartHopOutputRowsToMain(this.caseHopSolverOutputRows, { prepared });
        const rawResult = await this.appendCaseManualStartHopOutputRowsToRaw(this.caseHopSolverOutputRows, { prepared });
        await this.refreshCaseInvestigationVisualizer({ preserveFlow: true });
        this.caseHopSolverRawApplyStatus = `Applied ${mainResult.count || 0} Main Data row${(mainResult.count || 0) === 1 ? '' : 's'} from Tool 88 and refreshed the manual visual${rawResult.appendedCount ? `; appended ${rawResult.appendedCount} Raw Data row${rawResult.appendedCount === 1 ? '' : 's'}` : ''}.`;
        return { mainResult, rawResult };
      } finally {
        this.finishCaseManualStartViewportWorkIndicator(workIndicatorGeneration);
        this.caseHopSolverRunning = false;
      }
    },

    captureCaseHopSolverManualStartSnapshot(eventName = 'case-buddy-status', payload = {}) {
      if (!this.caseHopSolverManualStartMode) return;
      const snapshot = payload?.detail || payload || {};
      if (!snapshot || typeof snapshot !== 'object') return;
      const rows = this.extractCaseManualStartSnapshotRows(snapshot, { candidate: this.caseHopSolverCandidate || this.caseManualStartHopInspectorCandidate || null });
      if (rows.length) this.caseHopSolverOutputRows = rows;
      const tsv = snapshot.outputTsv || snapshot.result?.outputTsv || snapshot.tsv || '';
      if (tsv) this.caseHopSolverOutputTsv = tsv;
      if (snapshot.message || snapshot.status) this.caseHopSolverStatus = snapshot.message || snapshot.status;
      this.caseHopSolverLastEvent = eventName;
      if (!this.caseHopSolverSnapshot || snapshot.status || snapshot.integrationAction || snapshot.flowComplete || rows.length) {
        this.caseHopSolverSnapshot = { ...(this.caseHopSolverSnapshot || {}), ...snapshot };
      }
    },

    handleCaseHopSolverStatus(payload) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyHopSolverMethods, 'handleCaseHopSolverStatus', [payload]);
      this.captureCaseHopSolverManualStartSnapshot('case-buddy-status', payload);
      return result;
    },

    handleCaseHopSolverProgress(payload) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyHopSolverMethods, 'handleCaseHopSolverProgress', [payload]);
      this.captureCaseHopSolverManualStartSnapshot('case-buddy-progress', payload);
      return result;
    },

    handleCaseHopSolverResult(payload) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyHopSolverMethods, 'handleCaseHopSolverResult', [payload]);
      this.captureCaseHopSolverManualStartSnapshot('case-buddy-result', payload);
      return result;
    },

    handleCaseHopSolverGuardrail(payload) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyHopSolverMethods, 'handleCaseHopSolverGuardrail', [payload]);
      this.captureCaseHopSolverManualStartSnapshot('case-buddy-guardrail', payload);
      return result;
    },

    handleCaseHopSolverFlowComplete(payload) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyHopSolverMethods, 'handleCaseHopSolverFlowComplete', [payload]);
      this.captureCaseHopSolverManualStartSnapshot('case-buddy-flow-complete', payload);
      return result;
    },

    buildCaseManualStartHopInspectorCandidateKey(candidate = null) {
      if (!candidate || typeof candidate !== 'object') return '';
      const row = candidate.rawRow || candidate.row || [];
      const hash = this.caseManualNormalizeText(candidate.hash || candidate.txHash || candidate.transactionHash || row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const pathLabel = this.caseManualNormalizeText(candidate.pathLabel || candidate.label || row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
      const rowIndex = Number.isInteger(candidate.rowIndex) ? String(candidate.rowIndex) : this.caseManualNormalizeText(candidate.spreadsheetRowNumber || '');
      const toAddress = this.caseManualNormalizeText(candidate.toAddress || row[CASE_MANUAL_START_COL.TO_ADDRESS]).toLowerCase();
      const fromAddress = this.caseManualNormalizeText(candidate.fromAddress || row[CASE_MANUAL_START_COL.FROM_ADDRESS]).toLowerCase();
      const continuesAs = this.caseManualNormalizeContinuesAsText(candidate.continuesAs || row[CASE_MANUAL_START_COL.CONTINUES_AS] || '').toLowerCase();
      const direction = this.caseManualStartOutputChoiceDirectionForCandidate(candidate);
      return [direction, pathLabel, hash, rowIndex, fromAddress, toAddress, continuesAs].filter(Boolean).join('|');
    },

    caseManualStartSnapshotMatchesCandidate(snapshot = {}, candidate = null) {
      if (!snapshot || typeof snapshot !== 'object' || !candidate?.caseManualStartCandidate) return false;
      const row = candidate.rawRow || candidate.row || [];
      const candidateHash = this.caseManualNormalizeText(candidate.hash || candidate.txHash || candidate.transactionHash || row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const candidatePathLabel = this.caseManualNormalizeText(candidate.pathLabel || candidate.label || row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
      const candidateTarget = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate));
      const selectedEntry = snapshot?.selectedEntry || snapshot?.result?.selectedEntry || {};
      const snapshotHash = this.caseManualNormalizeText(selectedEntry.txHash || selectedEntry.hash || selectedEntry.transactionHash || snapshot?.selectedEntryHash || snapshot?.result?.selectedEntryHash || '').toLowerCase();
      const snapshotPathLabel = this.caseManualNormalizeText(selectedEntry.pathLabel || selectedEntry.label || snapshot?.selectedPathLabel || snapshot?.result?.selectedPathLabel || '').toLowerCase();
      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const snapshotTarget = this.caseManualNormalizeAddressKey(
        snapshot?.selectedTargetAddress
          || snapshot?.result?.selectedTargetAddress
          || snapshot?.targetAddress
          || snapshot?.result?.targetAddress
          || (reverse ? selectedEntry.fromAddress : selectedEntry.toAddress)
          || ''
      );
      if (candidateHash && snapshotHash && !this.caseManualTransactionHashesMatch(candidateHash, snapshotHash)) return false;
      if (candidatePathLabel && snapshotPathLabel && candidatePathLabel !== snapshotPathLabel) return false;
      if (candidateTarget && snapshotTarget && candidateTarget !== snapshotTarget) return false;
      return Boolean((candidateHash && snapshotHash) || (candidatePathLabel && snapshotPathLabel) || (candidateTarget && snapshotTarget));
    },

    clearCaseManualStartHopInspectorAutoCheckTimer() {
      if (this.caseManualStartHopInspectorAutoCheckTimer) {
        if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(this.caseManualStartHopInspectorAutoCheckTimer);
        else clearTimeout(this.caseManualStartHopInspectorAutoCheckTimer);
      }
      this.caseManualStartHopInspectorAutoCheckTimer = null;
      this.caseManualStartHopInspectorAutoCheckQueuedKey = '';
    },

    resetCaseManualStartHopInspectorDecisionStateForCandidate(candidate = null, options = {}) {
      const key = this.buildCaseManualStartHopInspectorCandidateKey(candidate);
      const force = options.force === true;
      const changed = key && key !== this.caseManualStartHopInspectorLoadedCandidateKey;
      if (force || changed) {
        this.caseManualStartHopInspectorSnapshot = null;
        this.caseManualStartHopInspectorOutputRows = [];
        this.caseManualStartHopInspectorOutputTsv = '';
        this.caseManualStartHopInspectorPreviewOpen = false;
        this.caseManualStartHopInspectorPreviewRows = [];
        this.caseManualStartHopInspectorPreviewSnapshot = null;
        this.caseManualStartHopInspectorPreviewCandidate = null;
        this.caseManualStartHopInspectorPreviewAction = '';
        this.caseManualStartHopInspectorPreviewStatus = '';
        this.caseManualStartHopInspectorPreviewApplied = false;
        this.caseManualStartHopInspectorPreviewServiceOverride = false;
        this.caseManualStartHopInspectorLastTraceServiceOverride = false;
        this.stopCaseManualStartHopInspectorTraceLoading();
        this.caseManualStartHopInspectorCopyStatus = '';
        this.caseManualStartHopInspectorLoadedCandidateKey = '';
      }
      if (candidate?.caseManualStartCandidate) this.caseManualStartHopInspectorCandidate = candidate;
      return key;
    },

    queueCaseManualStartHopInspectorAutoCheck(candidate = null, options = {}) {
      if (!this.caseManualStartHopInspectorOpen || !this.caseInvestigationVisualizerManualStartMode) return;
      const selectedCandidate = candidate?.caseManualStartCandidate ? candidate : this.caseManualStartHopInspectorSelectedCandidate;
      if (!selectedCandidate?.caseManualStartCandidate || !this.isCaseManualStartUnresolvedEndingCandidate(selectedCandidate)) return;
      const key = this.buildCaseManualStartHopInspectorCandidateKey(selectedCandidate);
      if (!key) return;
      const alreadyReady = this.caseManualStartHopInspectorSnapshot && this.caseManualStartHopInspectorLoadedCandidateKey === key;
      if (alreadyReady && options.force !== true) return;
      this.resetCaseManualStartHopInspectorDecisionStateForCandidate(selectedCandidate, { force: options.force === true });
      this.clearCaseManualStartHopInspectorAutoCheckTimer();
      this.caseManualStartHopInspectorAutoCheckQueuedKey = key;
      if (options.reason) this.caseManualStartHopInspectorStatus = options.reason;
      const delayMs = Number.isFinite(Number(options.delayMs)) ? Math.max(0, Number(options.delayMs)) : 220;
      const startAutoCheck = () => {
        this.caseManualStartHopInspectorAutoCheckTimer = null;
        this.caseManualStartHopInspectorAutoCheckQueuedKey = '';
        this.runCaseManualStartHopInspectorAutoCheck(selectedCandidate, { expectedCandidateKey: key, force: options.force === true });
      };
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') this.caseManualStartHopInspectorAutoCheckTimer = window.setTimeout(startAutoCheck, delayMs);
      else this.caseManualStartHopInspectorAutoCheckTimer = setTimeout(startAutoCheck, delayMs);
    },

    async runCaseManualStartHopInspectorAutoCheck(candidate = null, options = {}) {
      const selectedCandidate = candidate?.caseManualStartCandidate ? candidate : this.caseManualStartHopInspectorSelectedCandidate;
      const key = this.buildCaseManualStartHopInspectorCandidateKey(selectedCandidate);
      if (!key || !selectedCandidate?.caseManualStartCandidate) return null;
      if (this.caseManualStartHopInspectorRunning) {
        if (this.caseManualStartHopInspectorAutoCheckInFlightKey !== key) this.queueCaseManualStartHopInspectorAutoCheck(selectedCandidate, { force: false, delayMs: 500 });
        return null;
      }
      if (this.caseManualStartHopInspectorSnapshot && this.caseManualStartHopInspectorLoadedCandidateKey === key && options.force !== true) return this.caseManualStartHopInspectorSnapshot;
      this.caseManualStartHopInspectorAutoCheckInFlightKey = key;
      try {
        this.caseManualStartHopInspectorStatus = 'Auto-running Tool 88 checks for the selected unresolved ending…';
        return await this.loadCaseManualStartHopInspectorCandidate({ automatic: true, expectedCandidateKey: key, force: options.force === true, candidate: selectedCandidate });
      } finally {
        this.caseManualStartHopInspectorAutoCheckInFlightKey = '';
      }
    },

    async ensureCaseManualStartHopInspectorChecksReady(options = {}) {
      const candidate = options.candidate?.caseManualStartCandidate
        ? options.candidate
        : this.caseManualStartHopInspectorSelectedCandidate;
      const key = this.buildCaseManualStartHopInspectorCandidateKey(candidate);
      if (key && this.caseManualStartHopInspectorSnapshot && this.caseManualStartHopInspectorLoadedCandidateKey === key && options.force !== true && this.caseManualStartSnapshotMatchesCandidate(this.caseManualStartHopInspectorSnapshot, candidate)) return this.caseManualStartHopInspectorSnapshot;
      return this.loadCaseManualStartHopInspectorCandidate({ force: true, automatic: true, expectedCandidateKey: key, candidate });
    },

    caseManualNumberFromFirstUsable(values = [], fallback = null) {
      for (const value of values) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
      }
      return fallback;
    },

    caseManualExtractTraceAnywayKnownTransactionCount(snapshot = {}, candidate = {}) {
      const countLookup = snapshot?.countLookup || snapshot?.count_lookup || snapshot?.result?.countLookup || snapshot?.result?.count_lookup || {};
      const decision = snapshot?.decision || {};
      const result = snapshot?.result || {};
      const pathEnding = result?.pathEnding || result?.path_ending || {};
      const traceFetch = result?.trace_fetch || result?.traceFetch || snapshot?.trace_fetch || snapshot?.traceFetch || {};
      return this.caseManualNumberFromFirstUsable([
        candidate?.transactionCount,
        candidate?.transaction_count,
        candidate?.cachedTransactionCount,
        candidate?.cachedErc20TransferCount,
        candidate?.cachedUniversalTransactionCount,
        snapshot?.transactionCount,
        snapshot?.transaction_count,
        snapshot?.knownTransactionCount,
        snapshot?.known_transaction_count,
        countLookup?.transactionCount,
        countLookup?.transaction_count,
        countLookup?.knownTransactionCount,
        countLookup?.known_transaction_count,
        result?.transactionCount,
        result?.transaction_count,
        result?.knownTransactionCount,
        result?.known_transaction_count,
        pathEnding?.transactionCount,
        pathEnding?.transaction_count,
        pathEnding?.knownTransactionCount,
        pathEnding?.known_transaction_count,
        decision?.transactionCount,
        decision?.transaction_count,
        decision?.knownTransactionCount,
        decision?.known_transaction_count,
        traceFetch?.expected_count,
        traceFetch?.expected_transaction_count
      ], null);
    },


    findCaseManualStartViewportWorkIndicatorNode(candidate = null) {
      const nodes = Array.isArray(this.caseInvestigationVisualizerVisibleNodes) ? this.caseInvestigationVisualizerVisibleNodes : [];
      const selectedNode = this.caseInvestigationVisualizerSelectedNode || null;
      const effectiveCandidate = candidate && typeof candidate === 'object'
        ? candidate
        : (selectedNode?.caseCandidate || selectedNode?.candidate || null);
      if (!effectiveCandidate) return selectedNode;
      const candidateKey = this.buildCaseManualStartHopInspectorCandidateKey(effectiveCandidate);
      const candidateHash = this.caseManualNormalizeText(effectiveCandidate.hash || effectiveCandidate.txHash || effectiveCandidate.transactionHash || '').toLowerCase();
      const candidatePath = this.caseManualNormalizeText(effectiveCandidate.pathLabel || effectiveCandidate.label || '').toLowerCase();
      const targetAddress = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(effectiveCandidate));
      const fromAddress = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateCounterpartyAddress(effectiveCandidate));
      const selectedId = this.caseManualNormalizeText(selectedNode?.id || '');
      const nodeScore = (node = {}) => {
        if (!node) return -1;
        const nodeCandidate = node.caseCandidate || node.candidate || null;
        const nodeKey = this.buildCaseManualStartHopInspectorCandidateKey(nodeCandidate);
        const nodeHash = this.caseManualNormalizeText(node.hash || node.txHash || node.transactionHash || '').toLowerCase();
        const nodePath = this.caseManualNormalizeText(node.pathLabel || node.label || '').toLowerCase();
        const nodeAddress = this.caseManualNormalizeAddressKey(node.address || node.targetAddress || '');
        const nodeToAddress = this.caseManualNormalizeAddressKey(node.toAddress || '');
        const nodeFromAddress = this.caseManualNormalizeAddressKey(node.fromAddress || '');
        const isAddressNode = node.type === 'address';
        const isTxNode = node.type === 'tx' || node.type === 'transaction';
        let score = 0;
        if (targetAddress && nodeAddress && nodeAddress === targetAddress) score += isAddressNode ? 720 : 220;
        if (targetAddress && nodeToAddress && nodeToAddress === targetAddress) score += isAddressNode ? 260 : 90;
        if (candidateKey && nodeKey && candidateKey === nodeKey) score += isAddressNode ? (targetAddress && nodeAddress === targetAddress ? 280 : 130) : 85;
        if (candidateHash && nodeHash && this.caseManualTransactionHashesMatch(candidateHash, nodeHash)) score += 125;
        if (candidatePath && nodePath && candidatePath === nodePath) score += 90;
        if (targetAddress && nodeFromAddress && nodeFromAddress === targetAddress && nodeAddress !== targetAddress) score -= 140;
        if (fromAddress && nodeAddress && nodeAddress === fromAddress && nodeAddress !== targetAddress) score -= 260;
        if (isAddressNode) score += 35;
        if (isTxNode) score -= 55;
        if (selectedId && this.caseManualNormalizeText(node.id || '') === selectedId && targetAddress && nodeAddress === targetAddress) score += 24;
        return score;
      };
      let best = null;
      let bestScore = -1;
      nodes.forEach((node) => {
        const score = nodeScore(node);
        if (score > bestScore) {
          best = node;
          bestScore = score;
        }
      });
      return bestScore > 0 ? best : selectedNode;
    },

    getCaseManualStartViewportWorkIndicatorAnchorState(candidate = null, node = null) {
      const targetAddress = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate || {}));
      const fromAddress = this.caseManualNormalizeAddressKey(this.caseManualStartCandidateCounterpartyAddress(candidate || {}));
      const anchorAddress = this.caseManualNormalizeAddressKey(node?.address || node?.targetAddress || node?.toAddress || '');
      const nodeToAddress = this.caseManualNormalizeAddressKey(node?.toAddress || '');
      const nodeFromAddress = this.caseManualNormalizeAddressKey(node?.fromAddress || '');
      const anchorIsTarget = Boolean(
        targetAddress
          && node?.type === 'address'
          && (
            anchorAddress === targetAddress
            || (nodeToAddress === targetAddress && anchorAddress !== nodeFromAddress)
          )
      );
      const anchorIsFrom = Boolean(fromAddress && anchorAddress && anchorAddress === fromAddress && !anchorIsTarget);
      return {
        targetAddress,
        fromAddress,
        anchorAddress,
        anchorIsTarget,
        anchorIsFrom
      };
    },

    caseManualStartViewportWorkIndicatorActionClass(action = '') {
      const normalized = this.normalizeCaseManualStartHopInspectorActionKey?.(action) || this.caseManualNormalizeText(action).toLowerCase();
      if (normalized === 'path-ending' || /path.?ending|end.?path/iu.test(normalized)) return 'path-ending';
      if (normalized === 'swap' || /swap/iu.test(normalized)) return 'swap';
      if (normalized === 'bridge' || /bridge/iu.test(normalized)) return 'bridge';
      if (normalized === 'recommended' || /recommend/iu.test(normalized)) return 'recommended';
      return 'trace-through';
    },

    beginCaseManualStartViewportWorkIndicator(action = 'trace-through', candidate = null, options = {}) {
      if (!this.caseInvestigationVisualizerManualStartMode || !this.showCaseInvestigationVisualizerModal) return 0;
      this.clearCaseManualStartViewportWorkIndicator({ immediate: true });
      const selectedCandidate = candidate && typeof candidate === 'object'
        ? candidate
        : (this.caseManualStartHopInspectorPreviewCandidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || null);
      const anchorNode = this.findCaseManualStartViewportWorkIndicatorNode(selectedCandidate);
      const generation = Number(this.caseManualStartViewportWorkIndicator?.generation || 0) + 1;
      const actionClass = this.caseManualStartViewportWorkIndicatorActionClass(action);
      const candidateKey = this.buildCaseManualStartHopInspectorCandidateKey(selectedCandidate);
      const pathLabel = this.caseManualNormalizeText(selectedCandidate?.pathLabel || selectedCandidate?.label || 'selected path');
      const address = this.caseManualNormalizeText(this.caseManualStartCandidateEndpointAddress(selectedCandidate) || anchorNode?.address || 'selected address');
      const defaultMessage = actionClass === 'path-ending'
        ? `Marking ${pathLabel} as a path ending near ${address}.`
        : actionClass === 'swap'
          ? `Applying swap result near ${pathLabel} / ${address}.`
          : actionClass === 'bridge'
            ? `Applying bridge result near ${pathLabel} / ${address}.`
            : `Applying trace result near ${pathLabel} / ${address}.`;
      this.caseManualStartViewportWorkIndicator = {
        active: true,
        finishing: false,
        action: actionClass,
        message: this.caseManualNormalizeText(options.message) || defaultMessage,
        candidateKey,
        candidate: selectedCandidate ? { ...selectedCandidate } : null,
        anchor: {
          x: Number.isFinite(Number(anchorNode?.x)) ? Number(anchorNode.x) : (Number.isFinite(Number(selectedCandidate?.x)) ? Number(selectedCandidate.x) : null),
          y: Number.isFinite(Number(anchorNode?.y)) ? Number(anchorNode.y) : (Number.isFinite(Number(selectedCandidate?.y)) ? Number(selectedCandidate.y) : null)
        },
        startedAtMs: Date.now(),
        generation,
        clearTimer: null,
        safetyTimer: null
      };
      const clearSafety = () => {
        if (this.caseManualStartViewportWorkIndicator?.generation === generation) {
          this.clearCaseManualStartViewportWorkIndicator({ immediate: true });
        }
      };
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
        this.caseManualStartViewportWorkIndicator.safetyTimer = window.setTimeout(clearSafety, 45000);
      } else {
        this.caseManualStartViewportWorkIndicator.safetyTimer = setTimeout(clearSafety, 45000);
      }
      return generation;
    },

    clearCaseManualStartViewportWorkIndicator(options = {}) {
      const state = this.caseManualStartViewportWorkIndicator || {};
      [state.clearTimer, state.safetyTimer].forEach((timer) => {
        if (!timer) return;
        if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timer);
        else clearTimeout(timer);
      });
      if (options.immediate || !state.active) {
        this.caseManualStartViewportWorkIndicator = {
          active: false,
          finishing: false,
          action: '',
          message: '',
          candidateKey: '',
          candidate: null,
          anchor: { x: null, y: null },
          startedAtMs: 0,
          generation: Number(state.generation || 0),
          clearTimer: null,
          safetyTimer: null
        };
        return;
      }
      const generation = Number(state.generation || 0);
      this.caseManualStartViewportWorkIndicator = {
        ...state,
        active: true,
        finishing: true,
        clearTimer: null,
        safetyTimer: null
      };
      const finish = () => {
        if (this.caseManualStartViewportWorkIndicator?.generation === generation) {
          this.clearCaseManualStartViewportWorkIndicator({ immediate: true });
        }
      };
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
        this.caseManualStartViewportWorkIndicator.clearTimer = window.setTimeout(finish, 520);
      } else {
        this.caseManualStartViewportWorkIndicator.clearTimer = setTimeout(finish, 520);
      }
    },

    finishCaseManualStartViewportWorkIndicator(generation = 0) {
      if (generation && this.caseManualStartViewportWorkIndicator?.generation !== generation) return;
      this.clearCaseManualStartViewportWorkIndicator({ immediate: false });
    },

    caseManualFormatInteger(value = 0) {
      const number = Number(value);
      if (!Number.isFinite(number)) return '0';
      try {
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.trunc(number));
      } catch (error) {
        return String(Math.trunc(number));
      }
    },

    getCaseManualStartHopInspectorTraceProgressFromTool(tool = null) {
      const embeddedTool = tool || this.getCaseManualStartHopInspectorTool();
      const progress = embeddedTool?.traceFetchProgress || {};
      const traceFetch = progress.trace_fetch && typeof progress.trace_fetch === 'object' ? progress.trace_fetch : {};
      const fetched = this.caseManualNumberFromFirstUsable([traceFetch.fetched_count, progress.fetched_count, progress.fetched_transaction_count, embeddedTool?.traceFetchFetchedCount, Array.isArray(embeddedTool?.traceInspectorRows) ? embeddedTool.traceInspectorRows.length : null], 0);
      const expectedCandidate = this.caseManualNumberFromFirstUsable([traceFetch.expected_count, progress.expected_count, progress.expected_transaction_count, embeddedTool?.traceFetchExpectedCount, embeddedTool?.countLookupResult?.transactionCount, embeddedTool?.countLookupResult?.transaction_count, this.caseManualStartHopInspectorSnapshot?.transactionCount, this.caseManualStartHopInspectorSnapshot?.transaction_count, this.caseManualStartHopInspectorSnapshot?.countLookup?.transactionCount, this.caseManualStartHopInspectorSnapshot?.result?.transactionCount], null);
      const expected = Number.isFinite(expectedCandidate) && expectedCandidate > 0 ? Math.trunc(expectedCandidate) : 100;
      const assumedExpected = !(Number.isFinite(expectedCandidate) && expectedCandidate > 0);
      const priorFetched = Number(this.caseManualStartHopInspectorTraceProgressLastFetched || 0);
      const actualFetched = Number.isFinite(fetched) ? Math.max(0, Math.trunc(fetched)) : 0;
      const elapsedMs = Math.max(0, Date.now() - Number(this.caseManualStartHopInspectorTraceLoadingStartedAtMs || Date.now()));
      const syntheticFetched = assumedExpected && actualFetched <= priorFetched
        ? Math.min(Math.max(1, expected - 4), Math.max(1, Math.floor(elapsedMs / 850)))
        : 0;
      const cleanFetched = Math.max(actualFetched, syntheticFetched, priorFetched);
      const status = this.caseManualNormalizeText(traceFetch.status || progress.trace_status || progress.status || embeddedTool?.traceFetchStatusLabel || 'fetching');
      const message = this.caseManualNormalizeText(embeddedTool?.statusMessage || traceFetch.message || traceFetch.last_error || traceFetch.last_provider_message || progress.message || `Fetching provider transaction history (${this.caseManualFormatInteger(cleanFetched)} fetched)…`);
      const pulse = Number(this.caseManualStartHopInspectorTraceProgress?.pulse || 0) + (cleanFetched !== priorFetched ? 1 : 0);
      this.caseManualStartHopInspectorTraceProgressLastFetched = cleanFetched;
      return { fetched: cleanFetched, expected, assumedExpected, status, message, pulse };
    },

    refreshCaseManualStartHopInspectorTraceProgress() {
      if (!this.caseManualStartHopInspectorTraceLoading) return;
      this.caseManualStartHopInspectorTraceProgress = this.getCaseManualStartHopInspectorTraceProgressFromTool();
    },

    clearCaseManualStartHopInspectorTraceProgressTimer() {
      if (this.caseManualStartHopInspectorTraceProgressTimer) {
        if (typeof window !== 'undefined' && typeof window.clearInterval === 'function') window.clearInterval(this.caseManualStartHopInspectorTraceProgressTimer);
        else clearInterval(this.caseManualStartHopInspectorTraceProgressTimer);
      }
      this.caseManualStartHopInspectorTraceProgressTimer = null;
    },

    startCaseManualStartHopInspectorTraceLoading(title = 'Fetching trace preview…') {
      this.clearCaseManualStartHopInspectorTraceProgressTimer();
      this.caseManualStartHopInspectorTraceLoading = true;
      this.caseManualStartHopInspectorTraceLoadingTitle = title;
      this.caseManualStartHopInspectorTraceLoadingStartedAtMs = Date.now();
      this.caseManualStartHopInspectorTraceProgressLastFetched = 0;
      this.caseManualStartHopInspectorTraceProgress = this.getCaseManualStartHopInspectorTraceProgressFromTool();
      const tick = () => this.refreshCaseManualStartHopInspectorTraceProgress();
      if (typeof window !== 'undefined' && typeof window.setInterval === 'function') this.caseManualStartHopInspectorTraceProgressTimer = window.setInterval(tick, 350);
      else this.caseManualStartHopInspectorTraceProgressTimer = setInterval(tick, 350);
    },

    stopCaseManualStartHopInspectorTraceLoading() {
      this.refreshCaseManualStartHopInspectorTraceProgress();
      this.clearCaseManualStartHopInspectorTraceProgressTimer();
      this.caseManualStartHopInspectorTraceLoading = false;
    },

    splitCaseManualStartPreviewAmountToken(amountValue = '', tokenValue = '') {
      const amountText = this.caseManualNormalizeText(amountValue);
      const explicitToken = this.caseManualNormalizeText(tokenValue);
      const fundsToken = this.caseManualTokenFromFundsFollowed(amountText);
      const token = explicitToken || fundsToken || '';
      const lowerAmount = amountText.toLowerCase();
      const lowerToken = token.toLowerCase();
      const strippedAmount = lowerToken && lowerAmount.endsWith(lowerToken)
        ? amountText.slice(0, Math.max(0, amountText.length - token.length)).trim()
        : amountText;
      const formattedAmount = this.caseManualFormatHopInspectorAmountOnly(amountText || strippedAmount, token);
      const amount = formattedAmount || strippedAmount || amountText || '0.00';
      return { amount, token };
    },

    splitCaseManualStartPreviewAddressList(value = '') {
      return this.caseManualNormalizeText(value)
        .split(/[\r\n,;]+/u)
        .map(part => this.caseManualNormalizeText(part))
        .filter(Boolean);
    },

    buildCaseManualStartPreviewBtcEndpointList(value = '', amountText = '', prefix = 'endpoint') {
      const addresses = this.splitCaseManualStartPreviewAddressList(value);
      const compactAmount = this.caseManualFormatHopInspectorAmountWithToken(amountText, this.caseManualTokenFromFundsFollowed(amountText) || 'BTC') || this.caseManualNormalizeText(amountText);
      const usable = addresses.length ? addresses : ['pending'];
      return usable.slice(0, 8).map((address, index) => {
        const labelAddress = address === 'pending' ? 'Pending' : this.caseManualShorten(address, 7, 4);
        const labelAmount = index === 0 ? compactAmount : '';
        return {
          key: `${prefix}-${index}-${address}`,
          address: labelAddress,
          amount: labelAmount,
          label: [labelAddress, labelAmount].filter(Boolean).join(' · ')
        };
      });
    },

    async copyCaseManualStartHopInspectorPreviewHash(item = {}) {
      const hash = this.caseManualNormalizeText(item.hash || item.txHash || item.transactionHash || item.row?.[CASE_MANUAL_START_COL.TX_HASH]);
      if (!hash) return;
      let copied = false;
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(hash);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }
      if (!copied && typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.value = hash;
        textarea.setAttribute('readonly', 'readonly');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        } finally {
          document.body.removeChild(textarea);
        }
      }
      const message = copied ? `Copied ${this.caseManualShorten(hash, 10, 8)} to clipboard.` : 'Could not copy this transaction hash automatically.';
      this.caseManualStartHopInspectorCopyStatus = message;
      this.caseManualStartHopInspectorPreviewStatus = message;
      if (typeof this.showCaseToast === 'function') this.showCaseToast(message);
    },

    buildCaseManualStartPreviewDirectionInfo(row = [], candidate = {}) {
      const selectedAddress = this.caseManualNormalizeText(this.caseManualStartCandidateEndpointAddress(candidate)).toLowerCase();
      const fromAddress = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_ADDRESS]).toLowerCase();
      const toAddress = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TO_ADDRESS]).toLowerCase();
      if (selectedAddress && fromAddress === selectedAddress && toAddress !== selectedAddress) {
        return { className: 'is-outgoing', symbol: '▶', label: 'Outgoing from selected address' };
      }
      if (selectedAddress && toAddress === selectedAddress && fromAddress !== selectedAddress) {
        return { className: 'is-incoming', symbol: '▶', label: 'Incoming to selected address' };
      }
      return { className: 'is-neutral', symbol: '•', label: 'Related transaction' };
    },

    caseManualStartHopInspectorPreviewDirectionClass(item = {}) {
      return [
        'case-manual-start-hop-inspector__preview-direction',
        item.directionClass || 'is-neutral'
      ].filter(Boolean);
    },

    getCaseManualStartHopInspectorTool() {
      const ref = this.$refs.caseManualStartHopInspectorTool88;
      return Array.isArray(ref) ? ref[0] : ref;
    },

    openCaseManualStartResolvedSolveWarning(candidate = {}) {
      this.caseManualStartResolvedSolveWarningCandidate = candidate?.caseManualStartCandidate ? candidate : null;
      this.caseManualStartResolvedSolveWarningChecked = false;
      this.caseManualStartResolvedSolveWarningStatus = 'This path ending already appears to have a meaningful ending story. Continuing may duplicate or over-trace a completed path.';
      this.caseManualStartResolvedSolveWarningOpen = true;
    },

    closeCaseManualStartResolvedSolveWarning() {
      this.caseManualStartResolvedSolveWarningOpen = false;
      this.caseManualStartResolvedSolveWarningCandidate = null;
      this.caseManualStartResolvedSolveWarningChecked = false;
      this.caseManualStartResolvedSolveWarningStatus = '';
    },

    confirmCaseManualStartResolvedSolveWarning() {
      if (!this.caseManualStartResolvedSolveWarningChecked) {
        this.caseManualStartResolvedSolveWarningStatus = 'Check the acknowledgement before intentionally solving past a resolved ending.';
        return;
      }
      const candidate = this.caseManualStartResolvedSolveWarningCandidate;
      this.closeCaseManualStartResolvedSolveWarning();
      this.openCaseManualStartHopInspectorFromSelection({ candidate, bypassResolvedWarning: true });
    },

    caseManualStartReadTool88Settings() {
      const settings = { ...CASE_MANUAL_START_TOOL88_DEFAULT_SETTINGS };
      try {
        if (typeof localStorage === 'undefined') return settings;
        const raw = localStorage.getItem(CASE_MANUAL_START_TOOL88_SETTINGS_STORAGE_KEY);
        if (!raw) return settings;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return settings;
        if (['forward', 'reverse'].includes(parsed.traceDirection)) settings.traceDirection = parsed.traceDirection;
        if (['NEXT_CHRONOLOGICAL', 'FIFO', 'LIFO'].includes(parsed.hopLogicType)) settings.hopLogicType = parsed.hopLogicType;
        if (['J', 'W', 'P'].includes(parsed.tool88NoteColumn) && Number(parsed.settingsVersion || 1) >= CASE_MANUAL_START_TOOL88_SETTINGS_SCHEMA_VERSION) settings.tool88NoteColumn = parsed.tool88NoteColumn === 'P' ? 'W' : parsed.tool88NoteColumn;
        ['enableAdditionalServiceChecks', 'enableDepositEntityHopExtension', 'enableNativeDirectSpendMatchGuard', 'enableSessionDataCache', 'flowCompletionValueTooLowEnabled', 'showAutomationCallouts'].forEach(key => {
          if (typeof parsed[key] === 'boolean') settings[key] = parsed[key];
        });
        const depth = Number(parsed.flowCompletionMaxDepth);
        if (Number.isFinite(depth) && depth > 0) settings.flowCompletionMaxDepth = Math.min(500, Math.trunc(depth));
        ['lowValueThresholdBtc', 'lowValueThresholdNative', 'lowValueThresholdStable', 'lowValueThresholdOther', 'directAutomationSpeedMultiplier'].forEach(key => {
          const value = Number(parsed[key]);
          if (Number.isFinite(value) && value >= 0) settings[key] = value;
        });
        if (typeof parsed.flowCompletionTraceThroughEntitiesText === 'string') settings.flowCompletionTraceThroughEntitiesText = parsed.flowCompletionTraceThroughEntitiesText;
      } catch (error) {
        console.warn('Could not read Tool 88 settings for Manual Solve viewport.', error);
      }
      return settings;
    },

    caseManualStartWriteTool88Settings(settings = {}) {
      const merged = { ...CASE_MANUAL_START_TOOL88_DEFAULT_SETTINGS, ...(settings || {}) };
      const normalized = {
        settingsVersion: CASE_MANUAL_START_TOOL88_SETTINGS_SCHEMA_VERSION,
        traceDirection: ['forward', 'reverse'].includes(merged.traceDirection) ? merged.traceDirection : 'forward',
        hopLogicType: ['NEXT_CHRONOLOGICAL', 'FIFO', 'LIFO'].includes(merged.hopLogicType) ? merged.hopLogicType : 'NEXT_CHRONOLOGICAL',
        selectedExampleKey: merged.selectedExampleKey || 'basic',
        tool88NoteColumn: merged.tool88NoteColumn === 'J' ? 'J' : 'W',
        enableAdditionalServiceChecks: merged.enableAdditionalServiceChecks !== false,
        enableDepositEntityHopExtension: merged.enableDepositEntityHopExtension === true,
        enableNativeDirectSpendMatchGuard: merged.enableNativeDirectSpendMatchGuard === true,
        enableSessionDataCache: merged.enableSessionDataCache !== false,
        flowCompletionMaxDepth: Math.max(1, Math.min(500, Math.trunc(Number(merged.flowCompletionMaxDepth) || CASE_MANUAL_START_TOOL88_DEFAULT_SETTINGS.flowCompletionMaxDepth))),
        flowCompletionValueTooLowEnabled: merged.flowCompletionValueTooLowEnabled !== false,
        lowValueThresholdBtc: Math.max(0, Number(merged.lowValueThresholdBtc) || 0),
        lowValueThresholdNative: Math.max(0, Number(merged.lowValueThresholdNative) || 0),
        lowValueThresholdStable: Math.max(0, Number(merged.lowValueThresholdStable) || 0),
        lowValueThresholdOther: Math.max(0, Number(merged.lowValueThresholdOther) || 0),
        flowCompletionTraceThroughEntitiesText: this.caseManualNormalizeText(merged.flowCompletionTraceThroughEntitiesText),
        directAutomationSpeedMultiplier: Math.max(0.5, Math.min(3, Number(merged.directAutomationSpeedMultiplier) || 1)),
        showAutomationCallouts: merged.showAutomationCallouts !== false
      };
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(CASE_MANUAL_START_TOOL88_SETTINGS_STORAGE_KEY, JSON.stringify(normalized));
        }
      } catch (error) {
        console.warn('Could not save Tool 88 settings from Manual Solve viewport.', error);
      }
      return normalized;
    },

    caseManualStartNativeDirectSpendToolOptions(settings = null) {
      const normalized = settings || this.caseManualStartReadTool88Settings();
      const enabled = normalized?.enableNativeDirectSpendMatchGuard === true;
      return {
        enableNativeDirectSpendMatchGuard: enabled,
        caseBuddyForceStrictNativeFifo: !enabled,
        strictFifoNativeSpend: !enabled,
        disableNativeDirectSpendMatchGuard: !enabled
      };
    },

    caseManualStartApplyNativeDirectSpendToolFlags(tool, settings = null) {
      if (!tool) return;
      const options = this.caseManualStartNativeDirectSpendToolOptions(settings);
      if ('enableNativeDirectSpendMatchGuard' in tool) tool.enableNativeDirectSpendMatchGuard = options.enableNativeDirectSpendMatchGuard === true;
      if ('caseBuddyNativeDirectSpendGuardForcedOff' in tool) tool.caseBuddyNativeDirectSpendGuardForcedOff = options.disableNativeDirectSpendMatchGuard === true;
    },

    syncCaseManualStartSettingsToEmbeddedTool88(settings = null) {
      const normalized = settings || this.caseManualStartReadTool88Settings();
      const tool = this.getCaseManualStartHopInspectorTool?.();
      if (!tool) return normalized;
      if (typeof tool.applyCaseBuddyPreferredSettings === 'function') {
        tool.applyCaseBuddyPreferredSettings({
          traceDirection: normalized.traceDirection,
          hopLogicType: normalized.hopLogicType,
          enableAdditionalServiceChecks: normalized.enableAdditionalServiceChecks,
          enableDepositEntityHopExtension: normalized.enableDepositEntityHopExtension,
          ...this.caseManualStartNativeDirectSpendToolOptions(normalized)
        });
      } else {
        if ('traceDirection' in tool) tool.traceDirection = normalized.traceDirection;
        if ('hopLogicType' in tool) tool.hopLogicType = normalized.hopLogicType;
        if ('enableAdditionalServiceChecks' in tool) tool.enableAdditionalServiceChecks = normalized.enableAdditionalServiceChecks !== false;
        if ('enableDepositEntityHopExtension' in tool) tool.enableDepositEntityHopExtension = normalized.enableDepositEntityHopExtension === true;
        this.caseManualStartApplyNativeDirectSpendToolFlags(tool, normalized);
      }
      this.caseManualStartApplyNativeDirectSpendToolFlags(tool, normalized);
      if ('enableSessionDataCache' in tool) tool.enableSessionDataCache = normalized.enableSessionDataCache !== false;
      if ('flowCompletionMaxDepth' in tool) tool.flowCompletionMaxDepth = normalized.flowCompletionMaxDepth;
      if ('flowCompletionValueTooLowEnabled' in tool) tool.flowCompletionValueTooLowEnabled = normalized.flowCompletionValueTooLowEnabled !== false;
      ['lowValueThresholdBtc', 'lowValueThresholdNative', 'lowValueThresholdStable', 'lowValueThresholdOther', 'flowCompletionTraceThroughEntitiesText'].forEach(key => {
        if (key in tool) tool[key] = normalized[key];
      });
      return normalized;
    },

    caseAddressMonitoringPayloadKey(payload = {}) {
      return [
        this.caseManualNormalizeText(payload.case_file_id || payload.caseFileId || ''),
        this.caseManualNormalizeText(payload.source_path_label || ''),
        this.caseManualNormalizeText(payload.transaction_hash || '').toLowerCase(),
        this.caseManualNormalizeAddressKey(payload.monitor_address || ''),
        this.caseManualNormalizeText(payload.token_symbol || '').toUpperCase(),
        this.caseManualNormalizeText(payload.monitor_funds_amount || ''),
        this.caseManualNormalizeText(payload.left_bounds_amount || '')
      ].join('|');
    },

    async postCaseAddressMonitoringApi(path = '', body = {}) {
      const response = await authenticatedFetch(`/api/casebuddy/monitoring/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const json = await response.json().catch(() => null);
      if (!response.ok || !json || json.status === 'error') {
        const requestId = response.headers?.get?.('X-Request-ID')
          || json?.request_id
          || json?.requestId
          || '';
        const base = json?.message || `Address Monitoring request failed: ${path}`;
        throw new Error(requestId ? `${base} (Request ${requestId})` : base);
      }
      return json;
    },

    async openCaseAddressMonitoringModal() {
      this.showCaseAddressMonitoringModal = true;
      await this.$nextTick();
      const page = this.$refs.caseAddressMonitoringPage;
      if (page && typeof page.loadItems === 'function') await page.loadItems().catch(() => {});
    },

    closeCaseAddressMonitoringModal() {
      this.showCaseAddressMonitoringModal = false;
    },

    handleCaseAddressMonitoringItemsLoaded(summary = {}) {
      if (!this.caseAddressMonitoringStatus) return;
      const filteredCount = Number(summary.filteredCount ?? summary.count ?? 0);
      if (!Number.isFinite(filteredCount)) return;
      if (filteredCount <= 0 && this.caseAddressMonitoringLastPayloads?.length) {
        this.caseAddressMonitoringStatus = `${this.caseAddressMonitoringStatus} Use All my monitoring if the current-case filter hides older items.`;
      }
    },

    caseAddressMonitoringAmountToken(value = '', fallbackToken = '') {
      const text = this.caseManualNormalizeText(value).replace(/,/gu, '');
      const match = text.match(/-?\d+(?:\.\d+)?(?:e[+-]?\d+)?/iu);
      const amount = match ? Number(match[0]) : NaN;
      const after = match ? text.slice(match.index + match[0].length).trim() : '';
      const token = this.caseManualNormalizeText(after || fallbackToken).replace(/^[()]+|[(),.;]+$/gu, '').split(/\s+/u)[0] || '';
      return {
        amount: Number.isFinite(amount) ? amount : null,
        amountText: Number.isFinite(amount) ? String(amount) : '',
        token
      };
    },

    caseAddressMonitoringCleanNumber(value = '') {
      const parsed = this.caseAddressMonitoringAmountToken(value, '');
      return parsed.amount != null && Number.isFinite(parsed.amount) ? String(parsed.amount) : '';
    },

    caseAddressMonitoringDormantResultFromRow(row = [], candidate = null) {
      const normalized = this.caseManualEnsureRowWidth(row || [], Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const pseudo = {
        ...(candidate || {}),
        row: normalized,
        rawRow: normalized,
        pathLabel: this.caseManualNormalizeText(candidate?.pathLabel || normalized[CASE_MANUAL_START_COL.PATH_LABEL]),
        hash: this.caseManualNormalizeText(candidate?.hash || candidate?.txHash || candidate?.transactionHash || normalized[CASE_MANUAL_START_COL.TX_HASH]),
        fromAddress: this.caseManualNormalizeText(candidate?.fromAddress || normalized[CASE_MANUAL_START_COL.FROM_ADDRESS]),
        toAddress: this.caseManualNormalizeText(candidate?.toAddress || candidate?.targetAddress || normalized[CASE_MANUAL_START_COL.TO_ADDRESS]),
        targetAddress: this.caseManualNormalizeText(candidate?.targetAddress || candidate?.toAddress || normalized[CASE_MANUAL_START_COL.TO_ADDRESS]),
        fundsFollowed: this.caseManualNormalizeText(candidate?.fundsFollowed || normalized[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]),
        systemNotes: this.caseManualNormalizeText(candidate?.systemNotes || normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES]),
        caseNotes: this.caseManualNormalizeText(candidate?.caseNotes || normalized[CASE_MANUAL_START_COL.CASE_NOTES]),
        analystNotes: this.caseManualNormalizeText(candidate?.analystNotes || normalized[CASE_MANUAL_START_COL.ANALYST_NOTES]),
        continuesAs: this.caseManualNormalizeText(candidate?.continuesAs || normalized[CASE_MANUAL_START_COL.CONTINUES_AS])
      };
      return this.caseManualBuildDormantResultFromCandidate?.(pseudo) || null;
    },

    caseAddressMonitoringFindMainRowForCandidate(candidate = null) {
      if (!candidate) return { row: null, rowIndex: -1 };
      const matrix = this.getCaseManualStartMainMatrix();
      const normalizedHash = this.caseManualNormalizeText(candidate.hash || candidate.txHash || candidate.transactionHash || '').toLowerCase();
      const normalizedTo = this.caseManualNormalizeAddressKey(candidate.targetAddress || candidate.toAddress || candidate.address || '');
      const normalizedPath = this.caseManualNormalizeText(candidate.pathLabel || '').toLowerCase();
      const directIndex = Number(candidate.rowIndex);
      if (Number.isInteger(directIndex) && directIndex >= 0 && directIndex < matrix.length) {
        const direct = this.caseManualEnsureRowWidth(matrix[directIndex] || [], Math.max(CASE_MANUAL_START_COLUMN_COUNT, matrix[directIndex]?.length || 0));
        const directHash = this.caseManualNormalizeText(direct[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        const directTo = this.caseManualNormalizeAddressKey(direct[CASE_MANUAL_START_COL.TO_ADDRESS]);
        const directPath = this.caseManualNormalizeText(direct[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        if ((!normalizedHash || !directHash || directHash === normalizedHash)
          && (!normalizedTo || !directTo || directTo === normalizedTo)
          && (!normalizedPath || !directPath || directPath === normalizedPath)) {
          return { row: direct, rowIndex: directIndex };
        }
      }
      let best = { row: null, rowIndex: -1, score: -1 };
      matrix.forEach((rawRow, rowIndex) => {
        if (rowIndex === 0 && this.caseManualIsHeaderRow(rawRow)) return;
        const row = this.caseManualEnsureRowWidth(rawRow || [], Math.max(CASE_MANUAL_START_COLUMN_COUNT, rawRow?.length || 0));
        let score = 0;
        const hash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        const to = this.caseManualNormalizeAddressKey(row[CASE_MANUAL_START_COL.TO_ADDRESS]);
        const path = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        if (normalizedHash && hash === normalizedHash) score += 10;
        if (normalizedTo && to === normalizedTo) score += 6;
        if (normalizedPath && path === normalizedPath) score += 3;
        if (score > best.score) best = { row, rowIndex, score };
      });
      return best.score > 0 ? { row: best.row, rowIndex: best.rowIndex } : { row: null, rowIndex: -1 };
    },

    caseAddressMonitoringFindDormantDeliverableRowForCandidate(candidate = null, found = {}) {
      if (!candidate) return null;
      const rows = Array.isArray(this.dormantAddressDeliverableState?.monitoringRows)
        ? this.dormantAddressDeliverableState.monitoringRows
        : [];
      if (!rows.length) return null;
      const candidateHash = this.caseManualNormalizeText(candidate.hash || candidate.txHash || candidate.transactionHash || '').toLowerCase();
      const candidatePath = this.caseManualNormalizeText(candidate.pathLabel || '').toLowerCase();
      const candidateAddress = this.caseManualNormalizeAddressKey(candidate.targetAddress || candidate.toAddress || candidate.address || '');
      const rowNumber = Number.isInteger(found?.rowIndex) && found.rowIndex >= 0
        ? found.rowIndex + 1
        : Number(candidate.spreadsheetRowNumber || candidate.rowNumber || 0);
      let best = null;
      let bestScore = 0;
      rows.forEach((row) => {
        let score = 0;
        const rowHash = this.caseManualNormalizeText(row.relatedTransactionHash || row.transactionHash || row.hash || '').toLowerCase();
        const rowPath = this.caseManualNormalizeText(row.pathLabel || row.sourcePathLabel || '').toLowerCase();
        const rowAddress = this.caseManualNormalizeAddressKey(row.address || row.monitorAddress || row.toAddress || '');
        const rowSheetNumber = Number(row.sheetRowNumber || row.sourceRowNumber || row.rowNumber || 0);
        if (candidateHash && rowHash && (rowHash === candidateHash || rowHash.includes(candidateHash) || candidateHash.includes(rowHash))) score += 8;
        if (candidateAddress && rowAddress && rowAddress === candidateAddress) score += 6;
        if (candidatePath && rowPath && rowPath === candidatePath) score += 3;
        if (rowNumber && rowSheetNumber && rowNumber === rowSheetNumber) score += 4;
        if (score > bestScore) {
          best = row;
          bestScore = score;
        }
      });
      return bestScore >= 6 ? best : null;
    },

    caseAddressMonitoringPayloadForCandidate(candidate = null, context = {}) {
      if (!candidate) return null;
      const found = this.caseAddressMonitoringFindMainRowForCandidate(candidate);
      const row = found.row || this.caseManualEnsureRowWidth(candidate.rawRow || candidate.row || [], CASE_MANUAL_START_COLUMN_COUNT);
      if (!row || !row.length) return null;
      const dormant = this.caseAddressMonitoringDormantResultFromRow(row, candidate);
      if (!dormant) {
        const deliverableRow = this.caseAddressMonitoringFindDormantDeliverableRowForCandidate(candidate, found);
        return deliverableRow ? this.caseAddressMonitoringPayloadForDormantRow(deliverableRow) : null;
      }
      return this.buildCaseAddressMonitoringPayloadFromMainRow(row, {
        ...context,
        candidate,
        rowIndex: found.rowIndex,
        dormantResult: dormant,
        forceDormant: true
      });
    },

    buildCaseAddressMonitoringPayloadFromMainRow(row = [], options = {}) {
      const normalized = this.caseManualEnsureRowWidth(row || [], Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const pathLabel = this.caseManualNormalizeText(options.candidate?.pathLabel || normalized[CASE_MANUAL_START_COL.PATH_LABEL]);
      const transactionHash = this.caseManualNormalizeText(options.candidate?.hash || options.candidate?.txHash || options.candidate?.transactionHash || normalized[CASE_MANUAL_START_COL.TX_HASH]);
      const fromAddress = this.caseManualNormalizeText(options.candidate?.fromAddress || normalized[CASE_MANUAL_START_COL.FROM_ADDRESS]);
      const monitorAddress = this.caseManualNormalizeText(options.candidate?.targetAddress || options.candidate?.toAddress || normalized[CASE_MANUAL_START_COL.TO_ADDRESS]);
      const token = this.caseManualNormalizeText(options.candidate?.token || normalized[CASE_MANUAL_START_COL.TOKEN]).toUpperCase();
      const transactionAmount = this.caseAddressMonitoringCleanNumber(options.candidate?.amount || normalized[CASE_MANUAL_START_COL.AMOUNT]);
      const funds = this.caseAddressMonitoringAmountToken(options.candidate?.fundsFollowed || normalized[CASE_MANUAL_START_COL.FUNDS_FOLLOWED], token);
      const dormant = options.dormantResult || this.caseAddressMonitoringDormantResultFromRow(normalized, options.candidate);
      const dormantAmount = dormant ? this.caseAddressMonitoringAmountToken(this.caseManualDormantAmountText?.(dormant) || dormant.dormantAmountText || dormant.remainingDormantAmountText || dormant.amountText || '', token) : null;
      const monitorAmount = dormantAmount?.amount != null && dormantAmount.amount > 0
        ? dormantAmount
        : funds;
      const leftBounds = this.caseAddressMonitoringCleanNumber(normalized[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || normalized[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY]) || '0';
      const priorBalance = this.caseAddressMonitoringCleanNumber(normalized[CASE_MANUAL_START_COL.PRIOR_BALANCE]);
      const hasRequired = transactionHash && monitorAddress && token && monitorAmount?.amount != null && monitorAmount.amount > 0;
      if (!hasRequired) return null;
      const partial = dormant ? this.caseManualDormantResultIsPartial?.(dormant) === true : false;
      const sourceRow = this.caseManualEnsureRowWidth(normalized.slice(), CASE_MANUAL_START_COLUMN_COUNT);
      return {
        created_from: options.createdFrom || 'case_buddy_visualizer',
        monitor_kind: partial ? 'partial_dormant_remainder' : 'dormant_path_ending',
        label: [this.caseDisplayName || this.currentProjectFileName || 'Case Buddy', pathLabel ? `Path ${pathLabel}` : '', `${monitorAmount.amountText || monitorAmount.amount} ${monitorAmount.token || token}`.trim()].filter(Boolean).join(' · '),
        case_file_id: this.currentProjectFileId || '',
        case_name: this.caseDisplayName || this.currentProjectFileName || '',
        source_sheet: 'main',
        source_path_label: pathLabel,
        transaction_hash: transactionHash,
        from_address: fromAddress,
        monitor_address: monitorAddress,
        transaction_utc: this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.DATE]),
        monitor_after_utc: this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.DATE]),
        transaction_amount: transactionAmount || (monitorAmount.amountText || String(monitorAmount.amount)),
        token_symbol: monitorAmount.token || token,
        source_funds_followed_amount: funds.amountText || (funds.amount != null ? String(funds.amount) : ''),
        monitor_funds_amount: monitorAmount.amountText || String(monitorAmount.amount),
        left_bounds_amount: leftBounds,
        prior_balance_amount: priorBalance,
        from_hash: this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FROM_HASH]),
        continues_as_at_creation: this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.CONTINUES_AS]),
        chain_id: this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.CHAIN_ID]),
        chain_name: this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.CHAIN_NAME]),
        hop_logic: this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.HOP_LOGIC]) || 'FIFO',
        system_notes: this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES]),
        analyst_notes: this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.ANALYST_NOTES]),
        tool88_notes: this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.CASE_NOTES]),
        to_attribution: this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.ATTRIBUTION]),
        from_attribution: this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FROM_ATTRIBUTION]),
        source_row_json: sourceRow,
        tool88_input_json: {
          source: 'case_buddy_add_to_monitoring',
          created_from: options.createdFrom || 'case_buddy_visualizer',
          row_index: Number.isInteger(options.rowIndex) ? options.rowIndex : null,
          source_row: sourceRow,
          dormant_result: dormant || null,
          candidate: options.candidate || null
        }
      };
    },

    caseAddressMonitoringPayloadForDormantRow(deliverableRow = {}) {
      if (!deliverableRow || typeof deliverableRow !== 'object') return null;
      const sheetRowNumber = Number(deliverableRow.sheetRowNumber || deliverableRow.sourceRowNumber || deliverableRow.rowNumber || 0);
      const matrix = this.getCaseManualStartMainMatrix();
      const rowIndex = Number.isInteger(sheetRowNumber) && sheetRowNumber > 0 ? sheetRowNumber - 1 : -1;
      const row = rowIndex >= 0 && rowIndex < matrix.length
        ? this.caseManualEnsureRowWidth(matrix[rowIndex] || [], Math.max(CASE_MANUAL_START_COLUMN_COUNT, matrix[rowIndex]?.length || 0))
        : null;
      if (row) {
        const payload = this.buildCaseAddressMonitoringPayloadFromMainRow(row, {
          createdFrom: 'case_buddy_dormant_table',
          rowIndex,
          forceDormant: true,
          dormantResult: this.caseAddressMonitoringDormantResultFromRow(row)
        });
        if (payload) return payload;
      }
      const monitorAddress = this.caseManualNormalizeText(deliverableRow.address || deliverableRow.monitorAddress || deliverableRow.toAddress || '');
      const transactionHash = this.caseManualNormalizeText(deliverableRow.relatedTransactionHash || deliverableRow.transactionHash || deliverableRow.hash || '');
      const funds = this.caseAddressMonitoringAmountToken(deliverableRow.fundsFollowed || deliverableRow.fundsFollowedDisplay || deliverableRow.amountAndToken || '', '');
      const amountToken = this.caseAddressMonitoringAmountToken(deliverableRow.amountAndToken || '', funds.token);
      const monitorAmount = funds.amount != null && funds.amount > 0 ? funds : amountToken;
      const token = monitorAmount.token || amountToken.token || funds.token || '';
      if (!monitorAddress || !transactionHash || !token || monitorAmount.amount == null || monitorAmount.amount <= 0) return null;
      const sourceRow = Array.from({ length: CASE_MANUAL_START_COLUMN_COUNT }, () => '');
      sourceRow[CASE_MANUAL_START_COL.PATH_LABEL] = this.caseManualNormalizeText(deliverableRow.pathLabel || deliverableRow.sourcePathLabel || '');
      sourceRow[CASE_MANUAL_START_COL.TX_HASH] = transactionHash;
      sourceRow[CASE_MANUAL_START_COL.TO_ADDRESS] = monitorAddress;
      sourceRow[CASE_MANUAL_START_COL.AMOUNT] = amountToken.amountText || monitorAmount.amountText || String(monitorAmount.amount);
      sourceRow[CASE_MANUAL_START_COL.TOKEN] = token;
      sourceRow[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = `${monitorAmount.amountText || monitorAmount.amount} ${token}`.trim();
      sourceRow[CASE_MANUAL_START_COL.CONTINUES_AS] = 'Path ends.';
      sourceRow[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = this.caseAddressMonitoringCleanNumber(deliverableRow.leftBoundsValue || deliverableRow.leftBoundsData || '') || '0';
      sourceRow[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = sourceRow[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY];
      return {
        created_from: 'case_buddy_dormant_table',
        monitor_kind: 'dormant_path_ending',
        label: [this.caseDisplayName || this.currentProjectFileName || 'Case Buddy', 'Dormant', `${monitorAmount.amountText || monitorAmount.amount} ${token}`.trim()].filter(Boolean).join(' · '),
        case_file_id: this.currentProjectFileId || '',
        case_name: this.caseDisplayName || this.currentProjectFileName || '',
        source_sheet: 'main',
        source_path_label: sourceRow[CASE_MANUAL_START_COL.PATH_LABEL],
        transaction_hash: transactionHash,
        from_address: '',
        monitor_address: monitorAddress,
        transaction_utc: '',
        monitor_after_utc: '',
        transaction_amount: amountToken.amountText || monitorAmount.amountText || String(monitorAmount.amount),
        token_symbol: token,
        source_funds_followed_amount: monitorAmount.amountText || String(monitorAmount.amount),
        monitor_funds_amount: monitorAmount.amountText || String(monitorAmount.amount),
        left_bounds_amount: sourceRow[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || '0',
        prior_balance_amount: '',
        from_hash: '',
        continues_as_at_creation: 'Path ends.',
        chain_id: '',
        chain_name: '',
        hop_logic: 'FIFO',
        system_notes: 'Created from Case Buddy dormant address monitoring deliverable.',
        analyst_notes: '',
        tool88_notes: '',
        to_attribution: '',
        from_attribution: '',
        source_row_json: sourceRow,
        tool88_input_json: {
          source: 'case_buddy_dormant_table',
          source_row: sourceRow,
          deliverable_row: deliverableRow
        }
      };
    },

    async createCaseAddressMonitoringItems(payloads = [], options = {}) {
      const uniquePayloads = [];
      const seen = new Set();
      (Array.isArray(payloads) ? payloads : []).forEach((payload) => {
        if (!payload) return;
        const key = this.caseAddressMonitoringPayloadKey(payload);
        if (seen.has(key)) return;
        seen.add(key);
        uniquePayloads.push(payload);
      });
      if (!uniquePayloads.length) {
        this.showCaseToast?.('No eligible dormant-funds monitoring item was found in the current selection.');
        return null;
      }
      this.caseAddressMonitoringBusy = true;
      this.caseAddressMonitoringStatus = 'Adding monitoring item(s)…';
      const results = [];
      try {
        for (const payload of uniquePayloads) {
          // eslint-disable-next-line no-await-in-loop
          const result = await this.postCaseAddressMonitoringApi('create_item.php', payload);
          results.push(result);
        }
        this.caseAddressMonitoringLastPayloads = uniquePayloads;
        this.caseAddressMonitoringLastResult = { results, addedAtUtc: new Date().toISOString() };
        const created = results.filter(result => !/already|existing|duplicate/iu.test(result?.message || '')).length;
        const existing = uniquePayloads.length - created;
        const message = existing > 0
          ? `${created} monitoring item${created === 1 ? '' : 's'} added; ${existing} already existed.`
          : `${uniquePayloads.length} monitoring item${uniquePayloads.length === 1 ? '' : 's'} added.`;
        this.caseAddressMonitoringStatus = message;
        this.showCaseToast?.(`${message} Open Address Monitoring to review or check them.`);
        if (options.open !== false) this.openCaseAddressMonitoringModal();
        await this.$nextTick();
        const page = this.$refs.caseAddressMonitoringPage;
        if (page && typeof page.loadItems === 'function') await page.loadItems().catch(() => {});
        return { created, existing, results };
      } catch (error) {
        const message = error?.message || 'Could not add the monitoring item.';
        this.caseAddressMonitoringStatus = message;
        this.showCaseToast?.(message);
        return null;
      } finally {
        this.caseAddressMonitoringBusy = false;
      }
    },

    async addSelectedCaseVisualizerNodesToMonitoring() {
      return this.createCaseAddressMonitoringItems(this.caseAddressMonitoringSelectedPayloads, { open: true });
    },

    async addManualSolveSelectedCandidateToMonitoring() {
      const candidate = this.caseManualStartNodeActionTrayCandidate || this.caseManualStartSingleSelectedCandidate || null;
      const payload = this.caseAddressMonitoringPayloadForCandidate(candidate, { createdFrom: 'case_buddy_manual_solve_tray' });
      return this.createCaseAddressMonitoringItems(payload ? [payload] : [], { open: true });
    },

    async addDormantAddressRowToMonitoring(row = {}) {
      const payload = this.caseAddressMonitoringPayloadForDormantRow(row);
      return this.createCaseAddressMonitoringItems(payload ? [payload] : [], { open: true });
    },

    async addVisibleDormantAddressRowsToMonitoring() {
      const payloads = (this.caseAddressMonitoringDormantRowsForAdd || [])
        .map(row => this.caseAddressMonitoringPayloadForDormantRow(row))
        .filter(Boolean);
      return this.createCaseAddressMonitoringItems(payloads, { open: true });
    },

    toggleCaseManualStartNodeActionTray() {
      this.caseManualStartNodeActionTrayOpen = !this.caseManualStartNodeActionTrayOpen;
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('case_manual_start_node_action_tray_open', this.caseManualStartNodeActionTrayOpen ? 'true' : 'false');
        }
      } catch (error) {
        // Local storage is optional; the tray should still toggle for this session.
      }
    },

    caseManualStartNodeActionTrayStatus(message = '') {
      const clean = this.caseManualNormalizeText(message);
      if (!clean) return;
      this.caseInvestigationVisualizerStatus = clean;
      this.caseManualStartHopInspectorStatus = clean;
      if (typeof this.showCaseToast === 'function') this.showCaseToast(clean);
    },

        async openCaseManualStartNodeActionTrayAttribution() {
      const candidate = this.caseManualStartNodeActionTrayPrepareCandidate({ requirePathEnding: false });
      if (!candidate) return null;
      const rowIndex = this.findCaseManualStartMainRowIndexForCandidate(candidate);
      if (rowIndex < 0) {
        this.caseManualStartNodeActionTrayStatus('Could not locate the selected Manual Solve row in Main Data. Attribution was not changed.');
        return null;
      }
      const row = this.caseManualEnsureRowWidth((this.getCaseManualStartMainMatrix()[rowIndex] || []).slice(), CASE_MANUAL_START_COLUMN_COUNT);
      const attributionColumnIndex = this.caseManualStartCandidateAttributionColumnIndex(candidate);
      const fallbackLabel = this.caseManualStartCandidateIsReverse(candidate)
        ? candidate.fromAttributionLabel
        : (candidate.toAttributionLabel || candidate.attribution);
      this.caseManualStartNodeActionTrayAttributionCandidate = candidate;
      this.caseManualStartNodeActionTrayAttributionDraft = this.caseManualCleanAttributionLabel(row[attributionColumnIndex] || fallbackLabel || '');
      this.caseManualStartNodeActionTrayAttributionStatus = '';
      this.caseManualStartNodeActionTrayAttributionOpen = true;
      await this.$nextTick();
      const inputRef = this.$refs.caseManualStartNodeActionTrayAttributionInput;
      const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;
      if (input && typeof input.focus === 'function') input.focus();
      if (input && typeof input.select === 'function') input.select();
      return candidate;
    },

    closeCaseManualStartNodeActionTrayAttribution() {
      this.caseManualStartNodeActionTrayAttributionOpen = false;
      this.caseManualStartNodeActionTrayAttributionStatus = '';
      this.caseManualStartNodeActionTrayAttributionCandidate = null;
    },

    async applyCaseManualStartNodeActionTrayAttribution() {
      const attribution = this.caseManualNormalizeText(this.caseManualStartNodeActionTrayAttributionDraft);
      if (!attribution) {
        this.caseManualStartNodeActionTrayAttributionStatus = 'Enter an attribution label before applying.';
        return null;
      }
      const result = await this.changeCaseManualStartSelectedAttribution({
        attribution,
        candidate: this.caseManualStartNodeActionTrayAttributionCandidate || this.caseManualStartNodeActionTrayCandidate
      });
      if (result?.changed || result?.rowIndex >= 0) {
        this.closeCaseManualStartNodeActionTrayAttribution();
      } else if (!this.caseManualStartNodeActionTrayAttributionStatus) {
        this.caseManualStartNodeActionTrayAttributionStatus = this.caseManualStartHopInspectorStatus || 'Attribution was not changed.';
      }
      return result;
    },

    caseManualStartNodeActionTrayPrepareCandidate(options = {}) {
      const candidate = this.caseManualStartNodeActionTrayCandidate
        || this.caseManualStartSingleSelectedCandidate
        || this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection({ quiet: true })
        || null;
      if (!candidate?.caseManualStartCandidate || this.caseInvestigationVisualizerSelectedCount !== 1 || !this.caseManualStartNodeActionTraySelectedNodeIsReceivingSide) {
        const endpointLabel = candidate && this.caseManualStartCandidateIsReverse(candidate) ? 'source' : 'receiving';
        this.caseManualStartNodeActionTrayStatus(`Select exactly one ${endpointLabel} Manual Solve address node before using the action tray.`);
        return null;
      }
      if (options.requirePathEnding !== false && !this.isCaseManualStartPathEndingCandidate(candidate)) {
        this.caseManualStartNodeActionTrayStatus('The selected Manual Solve node is not a current Path ends row. Select an unresolved ending for this tray action.');
        return null;
      }
      this.caseManualStartHopInspectorCandidate = candidate;
      this.caseInvestigationVisualizerSelectedCandidate = candidate;
      const selectedKey = this.buildCaseManualStartHopInspectorCandidateKey(this.caseManualStartSingleSelectedCandidate);
      const candidateKey = this.buildCaseManualStartHopInspectorCandidateKey(candidate);
      if (!selectedKey || selectedKey !== candidateKey) this.selectCaseManualStartPathEndingNode(candidate);
      return candidate;
    },

    async runCaseManualStartNodeActionTrayAction(action = '') {
      const actionKey = this.caseManualNormalizeText(action).toLowerCase();
      if (actionKey === 'manual-work') return this.markCaseManualStartSelectedFurtherManualWork();
      if (actionKey === 'accept-report-boundary') {
        const candidate = this.caseManualStartNodeActionTrayPrepareCandidate({ requirePathEnding: true });
        return candidate ? this.openCaseManualStartReportBoundaryConfirmation([candidate], { source: 'manual-solve-tray' }) : null;
      }
      if (actionKey === 'reopen-report-boundary') {
        const candidate = this.caseManualStartNodeActionTrayPrepareCandidate({ requirePathEnding: true });
        return candidate ? this.reopenCaseManualStartCandidateReportBoundary(candidate) : null;
      }
      if (actionKey === 'add-monitoring') return this.addManualSolveSelectedCandidateToMonitoring();
      if (actionKey === 'change-attribution') return this.openCaseManualStartNodeActionTrayAttribution();
      const candidate = this.caseManualStartNodeActionTrayPrepareCandidate({ requirePathEnding: true });
      if (!candidate) return null;
      const toolActions = new Set(['trace-through', 'path-ending', 'swap', 'bridge']);
      if (!toolActions.has(actionKey)) return null;
      if (this.caseInvestigationVisualizerInteractionLocked || this.caseManualStartViewportAutoActive) {
        this.caseManualStartNodeActionTrayStatus('Manual Solve is busy or locked; stop the current operation before using tray actions.');
        return null;
      }
      if (actionKey === 'trace-through' && this.caseManualStartTraceAnywayBlocked) {
        this.caseManualStartNodeActionTrayStatus(this.caseManualStartTraceAnywayBlockedMessage);
        return null;
      }
      this.openCaseManualStartHopInspectorFromSelection({ candidate, bypassResolvedWarning: true });
      await this.$nextTick();
      const optionsByAction = {
        'trace-through': { apply: false, preview: true },
        'path-ending': { apply: true },
        swap: { apply: true, preview: true },
        bridge: { apply: true, preview: true }
      };
      return this.runCaseManualStartHopInspectorToolAction(actionKey, optionsByAction[actionKey] || { apply: true });
    },

    openCaseManualStartReportBoundaryConfirmation(candidates = [], options = {}) {
      const unique = [];
      const seen = new Set();
      (Array.isArray(candidates) ? candidates : [candidates]).forEach((candidate) => {
        if (!candidate?.caseManualStartCandidate) return;
        const rowIndex = this.findCaseManualStartMainRowIndexForCandidate(candidate);
        if (rowIndex < 0) return;
        const key = `row:${rowIndex}`;
        if (seen.has(key)) return;
        seen.add(key);
        const currentRow = this.getCaseManualStartMainMatrix()?.[rowIndex] || [];
        const currentCandidate = {
          ...candidate,
          rowIndex,
          row: currentRow,
          rawRow: currentRow,
          caseManualStartCandidate: true
        };
        if (!this.caseManualStartCandidateCanBeAcceptedAsReportBoundary(currentCandidate, currentRow)) return;
        unique.push(currentCandidate);
      });
      if (!unique.length) {
        const message = 'No eligible incomplete path endings were found. The case was not changed.';
        if (options.source === 'quick-lookup') this.caseQuickLookupSetState({ detail: message });
        else this.caseManualStartNodeActionTrayStatus(message);
        return false;
      }
      this.caseManualStartReportBoundaryConfirmation = {
        visible: true,
        candidates: unique,
        source: this.caseManualNormalizeText(options.source || 'manual-solve'),
        applying: false,
        acknowledged: false,
        returnToLookup: options.returnToLookup === true,
        status: ''
      };
      this.$nextTick(() => {
        const modal = this.$refs.caseManualStartReportBoundaryConfirmationModal;
        const element = Array.isArray(modal) ? modal[0] : modal;
        element?.focus?.();
      });
      return true;
    },

    closeCaseManualStartReportBoundaryConfirmation() {
      if (this.caseManualStartReportBoundaryConfirmation?.applying) return false;
      this.caseManualStartReportBoundaryConfirmation = {
        visible: false,
        candidates: [],
        source: '',
        applying: false,
        acknowledged: false,
        returnToLookup: false,
        status: ''
      };
      return true;
    },

    openCaseQuickLookupReportBoundaryConfirmation() {
      if (!this.caseQuickLookupCanAcceptReportBoundaries) return false;
      const result = this.assessCaseQuickLookupActiveFlow(this.caseQuickLookup?.activeHash);
      const assessment = result?.assessment || null;
      if (
        !assessment
          || Number(assessment.unresolvedCount || 0) > 0
          || Number(assessment.choiceCount || 0) > 0
          || !Array.isArray(assessment.manualReview)
          || !assessment.manualReview.length
      ) {
        this.refreshCaseQuickLookupManualSolveResult({ source: 'report-boundary-recheck' });
        return false;
      }
      const eligible = assessment.manualReview.filter((candidate) => {
        const currentRow = this.findCurrentCaseManualStartMainRowForCandidate?.(candidate) || [];
        return this.caseManualStartCandidateCanBeAcceptedAsReportBoundary?.(candidate, currentRow) === true;
      });
      if (eligible.length !== assessment.manualReview.length) {
        this.refreshCaseQuickLookupManualSolveResult({ source: 'report-boundary-eligibility-recheck' });
        return false;
      }
      return this.openCaseManualStartReportBoundaryConfirmation(eligible, {
        source: 'quick-lookup',
        returnToLookup: true
      });
    },

    toggleCaseManualStartSelectedReportBoundary() {
      const candidate = this.caseManualStartSelectedReportBoundaryCandidate;
      if (!candidate) {
        this.caseManualStartNodeActionTrayStatus('Select one unresolved ending address or transaction before using this case action.');
        return false;
      }
      if (this.caseManualStartCandidateHasAcceptedReportBoundary(candidate)) {
        return this.reopenCaseManualStartCandidateReportBoundary(candidate);
      }
      return this.openCaseManualStartReportBoundaryConfirmation([candidate], { source: 'manual-solve' });
    },

    async confirmCaseManualStartReportBoundaryAcceptance() {
      const state = this.caseManualStartReportBoundaryConfirmation || {};
      if (!state.acknowledged || state.applying || !Array.isArray(state.candidates) || !state.candidates.length) return false;
      this.caseManualStartReportBoundaryConfirmation = { ...state, applying: true, status: 'Saving the accepted report boundaries…' };
      try {
        const result = await this.acceptCaseManualStartCandidatesAsReportBoundaries(state.candidates, {
          source: state.source || 'manual-solve',
          returnToLookup: state.returnToLookup === true
        });
        if (!result?.changedCount) {
          this.caseManualStartReportBoundaryConfirmation = {
            ...this.caseManualStartReportBoundaryConfirmation,
            applying: false,
            status: result?.message || 'No report boundaries were changed.'
          };
          return false;
        }
        this.caseManualStartReportBoundaryConfirmation = {
          visible: false,
          candidates: [],
          source: '',
          applying: false,
          acknowledged: false,
          returnToLookup: false,
          status: ''
        };
        return true;
      } catch (error) {
        this.caseManualStartReportBoundaryConfirmation = {
          ...this.caseManualStartReportBoundaryConfirmation,
          applying: false,
          status: error?.message || 'The report boundaries could not be saved.'
        };
        return false;
      }
    },

    async acceptCaseManualStartCandidatesAsReportBoundaries(candidates = [], options = {}) {
      const matrix = this.getCaseManualStartMainMatrix().map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)));
      const uniqueByRow = new Map();
      (Array.isArray(candidates) ? candidates : [candidates]).forEach((candidate) => {
        if (!candidate?.caseManualStartCandidate) return;
        const rowIndex = this.findCaseManualStartMainRowIndexForCandidate(candidate);
        if (rowIndex <= 0 || rowIndex >= matrix.length || uniqueByRow.has(rowIndex)) return;
        uniqueByRow.set(rowIndex, candidate);
      });
      if (!uniqueByRow.size) return { changedCount: 0, message: 'No eligible Main Data rows were found.' };

      const acceptedAtUtc = new Date().toISOString();
      const changedRows = [];
      uniqueByRow.forEach((candidate, rowIndex) => {
        const row = this.caseManualEnsureRowWidth(matrix[rowIndex].slice(), Math.max(CASE_MANUAL_START_COLUMN_COUNT, matrix[rowIndex].length || 0));
        const liveCandidate = { ...candidate, rowIndex, row, rawRow: row, caseManualStartCandidate: true };
        if (!this.caseManualStartCandidateCanBeAcceptedAsReportBoundary(liveCandidate, row)) return;
        const before = JSON.stringify(row);
        const reverse = this.caseManualStartCandidateIsReverse(liveCandidate);
        const attributionColumnIndex = reverse ? CASE_MANUAL_START_COL.FROM_ATTRIBUTION : CASE_MANUAL_START_COL.ATTRIBUTION;
        const targetAddress = this.caseManualNormalizeText(this.caseManualStartCandidateEndpointAddress(liveCandidate));
        row[CASE_MANUAL_START_COL.CONTINUES_AS] = 'Path ends.';
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualAppendFurtherManualWorkSystemNote(row[CASE_MANUAL_START_COL.SYSTEM_NOTES]);
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualAppendAcceptedReportBoundarySystemNote(row[CASE_MANUAL_START_COL.SYSTEM_NOTES]);
        if (!this.caseManualCleanAttributionLabel(row[attributionColumnIndex] || '')) row[attributionColumnIndex] = 'Unknown';
        row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
          row[CASE_MANUAL_START_COL.CASE_NOTES],
          `Case Buddy Manual Solve: accepted incomplete ending as a report boundary; action=${CASE_MANUAL_START_ACCEPTED_REPORT_BOUNDARY_ACTION}; direction=${reverse ? 'reverse' : 'forward'}${targetAddress ? `; target=${targetAddress}` : ''}; accepted_at_utc=${acceptedAtUtc}.`
        );
        matrix[rowIndex] = this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(row));
        if (JSON.stringify(matrix[rowIndex]) !== before) changedRows.push(rowIndex);
      });

      if (!changedRows.length) return { changedCount: 0, message: 'The selected endings were already accepted as report boundaries.' };
      const changedCount = changedRows.length;
      await this.replaceCaseManualStartLogicalSheetMatrix(
        'main',
        matrix,
        `Accepted ${changedCount} incomplete Manual Solve ending${changedCount === 1 ? '' : 's'} as ${changedCount === 1 ? 'a report boundary' : 'report boundaries'}.`,
        { autosaveDelayMs: 850 }
      );

      this.caseManualStartHopInspectorOutputRows = [];
      this.caseManualStartHopInspectorPreviewRows = [];
      this.caseManualStartHopInspectorPreviewOpen = false;
      const message = `${changedCount} incomplete path ending${changedCount === 1 ? ' is' : 's are'} now accepted as ${changedCount === 1 ? 'a report boundary' : 'report boundaries'}. Further manual tracing remains recommended and will be disclosed in the report.`;
      this.caseManualStartHopInspectorStatus = message;
      this.caseInvestigationVisualizerStatus = message;

      if (this.showCaseInvestigationVisualizerModal && this.caseInvestigationVisualizerManualStartMode) {
        await this.refreshCaseManualStartAfterDirectRowMutation({ selectNextUnresolved: true });
      } else {
        await this.$nextTick();
      }
      if (options.returnToLookup || this.caseQuickLookup?.activeHash) {
        this.refreshCaseQuickLookupManualSolveResult({ source: 'accepted-report-boundaries' });
      }
      this.showCaseToast?.(message);
      return { changedCount, rowIndexes: changedRows, message };
    },

    async reopenCaseManualStartCandidateReportBoundary(candidate = null) {
      if (!candidate?.caseManualStartCandidate) return false;
      const matrix = this.getCaseManualStartMainMatrix().map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)));
      const rowIndex = this.findCaseManualStartMainRowIndexForCandidate(candidate);
      if (rowIndex <= 0 || rowIndex >= matrix.length) {
        this.caseManualStartNodeActionTrayStatus('Could not locate the selected report boundary in Main Data.');
        return false;
      }
      const row = this.caseManualEnsureRowWidth(matrix[rowIndex].slice(), Math.max(CASE_MANUAL_START_COLUMN_COUNT, matrix[rowIndex].length || 0));
      if (!this.caseManualStartCandidateHasAcceptedReportBoundary({ ...candidate, row, rawRow: row }, row)) {
        this.caseManualStartNodeActionTrayStatus('This ending is not currently accepted as a report boundary.');
        return false;
      }
      row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualRemoveAcceptedReportBoundaryText(row[CASE_MANUAL_START_COL.SYSTEM_NOTES]);
      row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualRemoveAcceptedReportBoundaryText(row[CASE_MANUAL_START_COL.CASE_NOTES]);
      matrix[rowIndex] = this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(row));
      await this.replaceCaseManualStartLogicalSheetMatrix('main', matrix, 'Reopened an accepted Manual Solve report boundary for further analysis.', { autosaveDelayMs: 850 });
      const message = 'The selected report boundary is open for analysis again. It will block report readiness until it is solved or accepted again.';
      this.caseManualStartHopInspectorStatus = message;
      this.caseInvestigationVisualizerStatus = message;
      if (this.showCaseInvestigationVisualizerModal && this.caseInvestigationVisualizerManualStartMode) {
        await this.refreshCaseManualStartAfterDirectRowMutation({ selectNextUnresolved: false });
      } else {
        await this.$nextTick();
      }
      if (this.caseQuickLookup?.activeHash) this.refreshCaseQuickLookupManualSolveResult({ source: 'reopened-report-boundary' });
      this.showCaseToast?.(message);
      return true;
    },

    findCaseManualStartMainRowIndexForCandidate(candidate = null) {
      if (!candidate?.caseManualStartCandidate) return -1;
      const matrix = this.getCaseManualStartMainMatrix();
      const rows = Array.isArray(matrix) ? matrix : [];
      if (!rows.length) return -1;
      const candidateHash = this.caseManualNormalizeText(candidate.hash || candidate.transactionHash || candidate.txHash || '').toLowerCase();
      const candidatePath = this.caseManualNormalizeText(candidate.pathLabel || candidate.label || '').toLowerCase();
      const candidateTo = this.caseManualNormalizeAddressKey(candidate.toAddress || candidate.targetAddress || candidate.address || '');
      const candidateFrom = this.caseManualNormalizeAddressKey(candidate.fromAddress || '');
      const candidateFromHash = this.caseManualNormalizeText(candidate.fromHash || '').toLowerCase();
      const rowMatches = (row = []) => {
        const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
        const rowHash = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        const rowPath = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.PATH_LABEL]).toLowerCase();
        const rowTo = this.caseManualNormalizeAddressKey(normalized[CASE_MANUAL_START_COL.TO_ADDRESS]);
        const rowFrom = this.caseManualNormalizeAddressKey(normalized[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        const rowFromHash = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
        const hashMatches = Boolean(candidateHash && rowHash && this.caseManualTransactionHashesMatch(rowHash, candidateHash));
        const pathMatches = Boolean(candidatePath && rowPath && rowPath === candidatePath);
        const toMatches = Boolean(candidateTo && rowTo && rowTo === candidateTo);
        const fromMatches = Boolean(candidateFrom && rowFrom && rowFrom === candidateFrom);
        const fromHashMatches = Boolean(candidateFromHash && rowFromHash && this.caseManualTransactionHashesMatch(rowFromHash, candidateFromHash));
        if (hashMatches && (pathMatches || toMatches || fromMatches || fromHashMatches || (!candidatePath && !candidateTo))) return true;
        if (pathMatches && toMatches && (!candidateFrom || fromMatches)) return true;
        return Boolean(!candidateHash && pathMatches && toMatches);
      };
      const explicitRowIndex = Number.isInteger(candidate.rowIndex) ? candidate.rowIndex : -1;
      if (explicitRowIndex >= 0 && explicitRowIndex < rows.length && rowMatches(rows[explicitRowIndex])) return explicitRowIndex;
      return rows.findIndex((row, index) => index > 0 && rowMatches(row));
    },

    caseManualStartCandidateHasFurtherManualWork(candidate = null, currentRow = null) {
      if (!candidate?.caseManualStartCandidate) return false;
      const row = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const resolvedRow = Array.isArray(currentRow) && currentRow.length
        ? currentRow
        : (this.findCurrentCaseManualStartMainRowForCandidate(candidate) || []);
      const hasCurrentRow = Array.isArray(resolvedRow) && resolvedRow.length > CASE_MANUAL_START_COL.TX_HASH
        && Boolean(this.caseManualNormalizeText(resolvedRow[CASE_MANUAL_START_COL.TX_HASH]));
      if (hasCurrentRow) {
        const continuesAs = this.caseManualNormalizeContinuesAsText(resolvedRow[CASE_MANUAL_START_COL.CONTINUES_AS] || '');
        if (continuesAs && !/^Path\s+ends\.?$/iu.test(continuesAs)) return false;
      }
      const text = (hasCurrentRow
        ? [
            resolvedRow[CASE_MANUAL_START_COL.SYSTEM_NOTES],
            resolvedRow[CASE_MANUAL_START_COL.ANALYST_NOTES],
            resolvedRow[CASE_MANUAL_START_COL.CASE_NOTES]
          ]
        : [
            candidate.systemNotes,
            candidate.analystNotes,
            candidate.caseNotes,
            row[CASE_MANUAL_START_COL.SYSTEM_NOTES],
            row[CASE_MANUAL_START_COL.ANALYST_NOTES],
            row[CASE_MANUAL_START_COL.CASE_NOTES]
          ])
        .map(value => this.caseManualNormalizeText(value))
        .filter(Boolean)
        .join('\n');
      return /further\s+manual\s+work\s+is\s+required\s+to\s+resolve\s+this\s+path/iu.test(text)
        || /requires?\s+further\s+manual\s+work/iu.test(text);
    },

    caseManualStartCandidateCanBeAcceptedAsReportBoundary(candidate = null, currentRow = null) {
      if (!candidate?.caseManualStartCandidate || !this.isCaseManualStartPathEndingCandidate(candidate)) return false;
      if (this.caseManualStartCandidateNeedsOutputChoice?.(candidate)) return false;
      const resolvedRow = Array.isArray(currentRow) && currentRow.length
        ? currentRow
        : (this.findCurrentCaseManualStartMainRowForCandidate(candidate) || []);
      if (this.caseManualStartCandidateHasAcceptedReportBoundary?.(candidate, resolvedRow)) return false;
      return Boolean(
        this.caseManualStartCandidateHasFurtherManualWork?.(candidate, resolvedRow)
          || this.isCaseManualStartUnresolvedEndingCandidate?.(candidate)
      );
    },

    caseManualStartTextHasAcceptedReportBoundary(value = '') {
      const text = this.caseManualNormalizeText(value);
      if (!text) return false;
      return /action\s*=\s*accepted-report-boundary/iu.test(text)
        || /report\s+boundary\s+accepted\s+by\s+the\s+investigator/iu.test(text)
        || /accepted\s+(?:incomplete\s+)?(?:path\s+)?ending\s+as\s+(?:an?\s+)?report\s+boundary/iu.test(text);
    },

    caseManualStartRowHasAcceptedReportBoundary(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const continuesAs = this.caseManualNormalizeContinuesAsText(normalized[CASE_MANUAL_START_COL.CONTINUES_AS] || '');
      if (!/^Path\s+ends\.?$/iu.test(continuesAs)) return false;
      return this.caseManualStartTextHasAcceptedReportBoundary([
        normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        normalized[CASE_MANUAL_START_COL.ANALYST_NOTES],
        normalized[CASE_MANUAL_START_COL.CASE_NOTES]
      ].filter(Boolean).join('\n'));
    },

    caseManualStartCandidateHasAcceptedReportBoundary(candidate = null, currentRow = null) {
      if (!candidate?.caseManualStartCandidate) return false;
      const resolvedRow = Array.isArray(currentRow) && currentRow.length
        ? currentRow
        : (this.findCurrentCaseManualStartMainRowForCandidate(candidate) || []);
      if (this.caseManualStartRowHasAcceptedReportBoundary(resolvedRow)) return true;
      const row = Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []);
      const continuesAs = this.caseManualNormalizeContinuesAsText(
        resolvedRow?.[CASE_MANUAL_START_COL.CONTINUES_AS]
          || row?.[CASE_MANUAL_START_COL.CONTINUES_AS]
          || candidate.continuesAs
          || ''
      );
      if (!/^Path\s+ends\.?$/iu.test(continuesAs)) return false;
      return this.caseManualStartTextHasAcceptedReportBoundary([
        candidate.systemNotes,
        candidate.analystNotes,
        candidate.caseNotes,
        row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row?.[CASE_MANUAL_START_COL.ANALYST_NOTES],
        row?.[CASE_MANUAL_START_COL.CASE_NOTES]
      ].filter(Boolean).join('\n'));
    },

    caseManualAppendAcceptedReportBoundarySystemNote(value = '') {
      const lines = this.caseManualDeduplicateMultilineText(value)
        .split(/\r?\n/u)
        .map(line => line.trim())
        .filter(Boolean)
        .filter(line => !/report\s+boundary\s+accepted\s+by\s+the\s+investigator/iu.test(line));
      lines.push(CASE_MANUAL_START_ACCEPTED_REPORT_BOUNDARY_SYSTEM_NOTE);
      return this.caseManualAppendUniqueLine('', lines.join('\n'));
    },

    caseManualRemoveAcceptedReportBoundaryText(value = '') {
      return this.caseManualDeduplicateMultilineText(value)
        .split(/\r?\n/u)
        .map(line => line.trim())
        .filter(Boolean)
        .filter(line => !this.caseManualStartTextHasAcceptedReportBoundary(line))
        .join('\n');
    },

    caseManualRemoveManualWorkText(value = '') {
      return this.caseManualDeduplicateMultilineText(value)
        .split(/\r?\n/u)
        .map(line => line.trim())
        .filter(Boolean)
        .filter(line => !/further\s+manual\s+work\s+is\s+required\s+to\s+resolve\s+this\s+path/iu.test(line))
        .filter(line => !/(?:requires?|selected\s+ending\s+requires?)\s+further\s+manual\s+work/iu.test(line))
        .join('\n');
    },

    caseManualNormalizeAcceptedReportBoundaryForContinuation(row = []) {
      const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      const continuesAs = this.caseManualNormalizeContinuesAsText(normalized[CASE_MANUAL_START_COL.CONTINUES_AS] || '');
      if (/^Path\s+ends\.?$/iu.test(continuesAs)) return normalized;
      normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualRemoveManualWorkText(
        this.caseManualRemoveAcceptedReportBoundaryText(normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES])
      );
      normalized[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualRemoveManualWorkText(
        this.caseManualRemoveAcceptedReportBoundaryText(normalized[CASE_MANUAL_START_COL.CASE_NOTES])
      );
      return normalized;
    },

    caseManualStartAcceptedReportBoundaryRows() {
      const matrix = this.getCaseManualStartMainMatrix?.() || [];
      const rows = [];
      matrix.forEach((sourceRow, rowIndex) => {
        if (!Array.isArray(sourceRow) || this.caseManualIsHeaderRow(sourceRow)) return;
        const row = this.caseManualEnsureRowWidth(sourceRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceRow.length || 0));
        if (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH])) return;
        if (!this.caseManualStartRowHasAcceptedReportBoundary(row)) return;
        const notes = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.CASE_NOTES]);
        const directionMatch = notes.match(/direction\s*=\s*(forward|reverse)/iu);
        const direction = directionMatch ? directionMatch[1].toLowerCase() : 'forward';
        const attributionColumn = direction === 'reverse' ? CASE_MANUAL_START_COL.FROM_ATTRIBUTION : CASE_MANUAL_START_COL.ATTRIBUTION;
        const addressColumn = direction === 'reverse' ? CASE_MANUAL_START_COL.FROM_ADDRESS : CASE_MANUAL_START_COL.TO_ADDRESS;
        rows.push({
          rowIndex,
          pathLabel: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL]) || `row ${rowIndex + 1}`,
          hash: this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]),
          address: this.caseManualNormalizeText(row[addressColumn]),
          attribution: this.caseManualCleanAttributionLabel(row[attributionColumn] || '') || 'Unknown',
          direction
        });
      });
      return rows;
    },

    caseManualAppendFurtherManualWorkSystemNote(value = '') {
      const manualWorkSentence = 'Further manual work is required to resolve this path.';
      const pathEndsNote = 'Path ends.';
      const lines = this.caseManualDeduplicateMultilineText(value)
        .split(/\r?\n/u)
        .map(line => line.trim())
        .filter(Boolean);
      const hasPathEndsAnywhere = lines.some(line => /path\s*ends/iu.test(line));
      const existingIndex = lines.findIndex(line => /further\s+manual\s+work\s+is\s+required\s+to\s+resolve\s+this\s+path/iu.test(line));
      if (existingIndex >= 0) {
        const existing = lines[existingIndex];
        lines[existingIndex] = /path\s*ends/iu.test(existing) || hasPathEndsAnywhere
          ? existing
          : `${existing.replace(/[.\s]+$/u, '')}. ${pathEndsNote}`;
      } else {
        lines.push(hasPathEndsAnywhere ? manualWorkSentence : `${manualWorkSentence} ${pathEndsNote}`);
      }
      return this.caseManualAppendUniqueLine('', lines.join('\n'));
    },

    caseManualBuildAttributionSystemNote(existingNotes = '', attribution = '', options = {}) {
      const cleanAttribution = this.caseManualCleanAttributionLabel(attribution) || this.caseManualNormalizeText(attribution);
      if (!cleanAttribution) return this.caseManualNormalizeText(existingNotes);
      const previous = this.caseManualCleanAttributionLabel(options.previousAttribution || '');
      const pathEnds = options.pathEnds === true;
      const lines = this.caseManualDeduplicateMultilineText(existingNotes)
        .split(/\r?\n/u)
        .map(line => line.trim())
        .filter(Boolean)
        .filter((line) => {
          if (!previous) return true;
          const normalized = line.replace(/\s*Path\s+ends\.?\s*$/iu, '').replace(/\.+$/u, '').trim();
          return normalized.toLowerCase() !== previous.toLowerCase();
        });
      const alreadyHasAttribution = lines.some(line => line.toLowerCase().includes(cleanAttribution.toLowerCase()));
      const existingHasPathEnds = lines.some(line => /path\s*ends/iu.test(line));
      if (!alreadyHasAttribution) lines.push(pathEnds && !existingHasPathEnds ? `${cleanAttribution}. Path ends.` : `${cleanAttribution}.`);
      return this.caseManualAppendUniqueLine('', lines.join('\n'));
    },

    async refreshCaseManualStartAfterDirectRowMutation(options = {}) {
      await this.refreshCaseInvestigationVisualizer({ preserveFlow: true });
      if (options.selectNextUnresolved) {
        const nextCandidate = this.getCaseManualStartActivePathEndingCandidate?.();
        const nextCandidateIsUnresolved = this.isCaseManualStartUnresolvedEndingCandidate(nextCandidate);
        if (nextCandidateIsUnresolved) {
          const selectedNextCandidate = this.selectCaseManualStartPathEndingNode(nextCandidate) || nextCandidate;
          if (this.caseManualStartHopInspectorOpen) {
            this.caseManualStartHopInspectorCandidate = selectedNextCandidate;
            this.resetCaseManualStartHopInspectorDecisionStateForCandidate(selectedNextCandidate, { force: true });
            this.queueCaseManualStartHopInspectorAutoCheck(selectedNextCandidate, { force: true, delayMs: 450, reason: 'Manual Solve selected the next unresolved ending after the direct tray update.' });
          }
        } else {
          this.caseManualStartHopInspectorCandidate = null;
          this.caseInvestigationVisualizerSelectedCandidate = null;
          this.caseInvestigationVisualizerSelectedNodeId = '';
          this.caseInvestigationVisualizerSelectedNodeIds = [];
          this.queueCaseManualStartViewportCompletionCheck('direct tray update and no unresolved endings remain');
        }
      }
      await this.$nextTick();
    },

    caseManualStartCandidateHasInvalidZeroReverseAllocation(candidate = null) {
      if (!candidate?.caseManualStartCandidate || !this.caseManualStartCandidateIsReverse(candidate)) return false;
      const currentRow = this.findCurrentCaseManualStartMainRowForCandidate(candidate);
      const row = Array.isArray(currentRow)
        ? currentRow
        : (Array.isArray(candidate.rawRow) ? candidate.rawRow : (Array.isArray(candidate.row) ? candidate.row : []));
      if (!row.length || !this.caseManualRowHasExplicitReverseAllocationEvidence(row)) return false;
      const fromHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_HASH] || candidate.fromHash || '');
      if (!fromHash) return false;
      const fundsText = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || candidate.fundsFollowed || '');
      const funds = this.caseManualParseNumericAmount(fundsText);
      const amount = this.caseManualParseNumericAmount(row[CASE_MANUAL_START_COL.AMOUNT] || candidate.amountText || '');
      if (!fundsText || !Number.isFinite(funds) || Math.abs(funds) > 1e-12 || !Number.isFinite(amount) || amount <= 1e-12) return false;
      const terminalText = [
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row[CASE_MANUAL_START_COL.CASE_NOTES],
        row[CASE_MANUAL_START_COL.CONTINUES_AS],
        row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION],
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      return !/(?:accepted-report-boundary|manual-path-ending|address-poisoning-ending|likely\s+address\s+poisoning|further\s+manual\s+work|action\s*=\s*reverse-swap)/iu.test(terminalText);
    },

    caseManualStartViewportAutoSignatureForCandidate(candidate = null) {
      if (!candidate?.caseManualStartCandidate) return '';
      return [
        this.caseManualStartOutputChoiceDirectionForCandidate(candidate),
        this.caseManualNormalizeText(candidate.pathLabel || candidate.label || '').toLowerCase(),
        this.caseManualNormalizeText(candidate.hash || candidate.txHash || candidate.transactionHash || '').toLowerCase(),
        this.caseManualNormalizeAddressKey(this.caseManualStartCandidateEndpointAddress(candidate)),
        this.caseManualNormalizeAddressKey(this.caseManualStartCandidateCounterpartyAddress(candidate)),
        this.caseManualNormalizeText(candidate.fromHash || '').toLowerCase(),
        this.caseManualNormalizeText(candidate.fundsFollowed || candidate.amountText || '').toLowerCase(),
        this.caseManualNormalizeText(candidate.leftBoundsNumeric || candidate.leftBounds || '').toLowerCase(),
      ].join('|');
    },

    caseManualStartClearViewportAutoTraceNoProgress(candidate = null) {
      if (!this.caseManualStartViewportAuto || !candidate?.caseManualStartCandidate) return;
      const key = this.buildCaseManualStartHopInspectorCandidateKey(candidate)
        || this.caseManualStartViewportAutoSignatureForCandidate(candidate);
      if (!key) return;
      const counts = { ...(this.caseManualStartViewportAuto.traceNoProgressCounts || {}) };
      if (!Object.prototype.hasOwnProperty.call(counts, key)) return;
      delete counts[key];
      this.caseManualStartViewportAuto.traceNoProgressCounts = counts;
    },

    async caseManualStartConfirmViewportAutoTraceNoProgress(candidate = null, options = {}) {
      if (!candidate?.caseManualStartCandidate) {
        return { confirmed: false, resolved: true, candidate: null };
      }
      const settleDelays = Array.isArray(options.settleDelays) && options.settleDelays.length
        ? options.settleDelays
        : [0, 120, 300, 650, 1100];
      let refreshedCandidate = this.caseManualStartCandidateWithCurrentMainRow(candidate);
      for (let index = 0; index < settleDelays.length; index += 1) {
        if (!this.caseManualStartViewportAutoActive || this.caseManualStartViewportAuto?.stopRequested) {
          return { confirmed: false, resolved: false, cancelled: true, candidate: refreshedCandidate || candidate };
        }
        const delayMs = Math.max(0, Number(settleDelays[index]) || 0);
        if (delayMs > 0) await new Promise(resolve => setTimeout(resolve, delayMs));
        await this.$nextTick();
        await this.caseManualSolveYieldToBrowser?.(1);
        refreshedCandidate = this.caseManualStartCandidateWithCurrentMainRow(refreshedCandidate || candidate);
        if (!refreshedCandidate?.caseManualStartCandidate) {
          return { confirmed: false, resolved: true, candidate: refreshedCandidate };
        }
        const hasOccurrenceChild = this.caseManualStartCandidateHasOccurrenceSpecificChild(refreshedCandidate);
        const remainsUnresolved = this.isCaseManualStartUnresolvedEndingCandidate(refreshedCandidate);
        if (hasOccurrenceChild || !remainsUnresolved) {
          return {
            confirmed: false,
            resolved: true,
            candidate: refreshedCandidate,
            hasOccurrenceChild
          };
        }
      }
      return { confirmed: true, resolved: false, candidate: refreshedCandidate || candidate };
    },

    async caseManualStartHandleViewportAutoTraceNoProgress(candidate = null, options = {}) {
      if (!this.caseManualStartViewportAutoActive || !candidate?.caseManualStartCandidate) {
        return { handled: false, count: 0, marked: false };
      }
      const key = this.buildCaseManualStartHopInspectorCandidateKey(candidate)
        || this.caseManualStartViewportAutoSignatureForCandidate(candidate);
      if (!key) return { handled: false, count: 0, marked: false };
      const counts = { ...(this.caseManualStartViewportAuto.traceNoProgressCounts || {}) };
      const count = (Number(counts[key]) || 0) + 1;
      counts[key] = count;
      this.caseManualStartViewportAuto.traceNoProgressCounts = counts;
      if (count < CASE_MANUAL_START_VIEWPORT_AUTO_TRACE_NO_PROGRESS_LIMIT) {
        const message = `Manual Solve Auto is retrying this trace because the first result did not create a continuation for the selected path (${count}/${CASE_MANUAL_START_VIEWPORT_AUTO_TRACE_NO_PROGRESS_LIMIT}).`;
        this.caseManualStartViewportAuto.status = message;
        this.caseInvestigationVisualizerStatus = message;
        this.caseManualStartHopInspectorOutputRows = [];
        this.caseManualStartHopInspectorPreviewRows = [];
        this.caseManualStartHopInspectorPreviewOpen = false;
        this.resetCaseManualStartHopInspectorDecisionStateForCandidate(candidate, { force: true });
        this.queueCaseManualStartHopInspectorAutoCheck(candidate, {
          force: true,
          delayMs: 450,
          reason: 'The prior trace result made no occurrence-specific progress, so Flow Solver checks are being refreshed once before Auto sets the path aside.',
        });
        return { handled: true, count, marked: false };
      }
      const confirmation = await this.caseManualStartConfirmViewportAutoTraceNoProgress(candidate);
      if (!confirmation.confirmed) {
        delete counts[key];
        this.caseManualStartViewportAuto.traceNoProgressCounts = counts;
        if (confirmation.cancelled) {
          return { handled: true, count, marked: false, cancelled: true, result: confirmation };
        }
        const message = confirmation.hasOccurrenceChild
          ? 'Manual Solve Auto confirmed that the trace continuation arrived while the result was settling. Continuing with the remaining paths.'
          : 'Manual Solve Auto rechecked the current Main Data and confirmed that this ending no longer requires manual work.';
        this.caseManualStartViewportAuto.status = message;
        this.caseInvestigationVisualizerStatus = message;
        return { handled: true, count, marked: false, resolvedWhileSettling: true, result: confirmation };
      }
      delete counts[key];
      this.caseManualStartViewportAuto.traceNoProgressCounts = counts;
      const confirmedCandidate = confirmation.candidate?.caseManualStartCandidate ? confirmation.candidate : candidate;
      const directionLabel = this.caseManualStartCandidateIsReverse(confirmedCandidate) ? 'Reverse' : 'Forward';
      const reasonPrefix = this.caseManualNormalizeText(options.reason);
      const reason = `${reasonPrefix ? `${reasonPrefix} ` : ''}${directionLabel} trace-through made no occurrence-specific progress after ${count} automatic attempts and remained unresolved after a settling recheck. Manual trace review required.`;
      const marked = await this.markCaseManualStartCandidateFurtherManualWork(confirmedCandidate, reason);
      if (marked?.changed || marked?.rowIndex >= 0) {
        const message = 'Manual Solve Auto set aside a trace that made no progress and will continue with the remaining paths.';
        this.caseManualStartViewportAuto.status = message;
        this.caseInvestigationVisualizerStatus = message;
        return { handled: true, count, marked: true, result: marked };
      }
      return { handled: true, count, marked: false, result: marked };
    },

    caseManualStartSpecialActionDidNotApply(result = null, actionKey = '') {
      if (!result || typeof result !== 'object') return false;
      const normalizedAction = this.caseManualNormalizeText(actionKey).toLowerCase();
      const resultAction = this.caseManualNormalizeText(result.caseBuddySpecialAction).toLowerCase();
      return Boolean(
        normalizedAction
          && resultAction === normalizedAction
          && result.caseBuddySpecialActionApplied === false
      );
    },

    caseManualStartBridgeActionProducedNoOutput(result = null) {
      if (!result || typeof result !== 'object') return false;
      const activeCandidate = this.caseManualStartHopInspectorCandidate || this.caseManualStartHopInspectorSelectedCandidate || null;
      const rows = this.extractCaseManualStartSnapshotRows(result, { candidate: activeCandidate });
      const rowCount = Array.isArray(rows) ? rows.filter(row => Array.isArray(row) && this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH])).length : 0;
      const text = [
        result.status,
        result.integrationAction,
        result.message,
        result.guardrail?.reason,
        result.guardrail?.message,
        result.result?.bridge?.status,
        result.result?.bridge?.message,
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' | ').toLowerCase();
      const explicitFailure = /manual-bridge-review|manual-bridge-no-output|bridge-produced-no-output|no bridge candidate was safe|bridge solver[^|.]*no(?:t)? produce|did not produce an exportable destination-chain receive row|open full tool 88 to review bridge candidates manually/iu.test(text);
      if (explicitFailure) return true;
      const bridgeAction = /manual-bridge|bridge preview|bridge solver|bridge result|integrationaction[^|]*bridge/iu.test(text);
      if (!bridgeAction) return false;
      const hasValidBridgeChild = rowCount > 0 && this.caseManualPreviewRowsContainTraceChild(rows, activeCandidate, result || {});
      return !hasValidBridgeChild;
    },

    caseManualStartSwapActionProducedNoOutput(result = null, candidate = null) {
      const activeCandidate = candidate?.caseManualStartCandidate
        ? candidate
        : (this.caseManualStartHopInspectorCandidate || this.caseManualStartHopInspectorSelectedCandidate || null);
      const currentRow = activeCandidate?.caseManualStartCandidate
        ? this.findCurrentCaseManualStartMainRowForCandidate(activeCandidate)
        : null;
      const currentText = Array.isArray(currentRow)
        ? [
            currentRow[CASE_MANUAL_START_COL.SYSTEM_NOTES],
            currentRow[CASE_MANUAL_START_COL.CASE_NOTES],
            currentRow[CASE_MANUAL_START_COL.HOP_LOGIC],
          ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' | ')
        : '';
      if (/tool\s*88\s*:\s*reverse\s+swap\s+solved|action\s*=\s*reverse-swap[^\n]*direction\s*=\s*reverse|reverse[-_\s]*swap\s+funds\s+followed/iu.test(currentText)) {
        return false;
      }
      if (!result || typeof result !== 'object') return true;
      if (this.caseManualNormalizeText(result.caseBuddySpecialAction).toLowerCase() === 'swap') {
        return result.caseBuddySpecialActionProducedOutput === false
          || result.caseBuddySpecialActionApplied === false;
      }
      const activeHash = this.caseManualNormalizeText(
        activeCandidate?.hash || activeCandidate?.txHash || activeCandidate?.transactionHash || ''
      ).toLowerCase();
      const rows = this.extractCaseManualStartSnapshotRows(result, { candidate: activeCandidate });
      const hasSwapRow = (Array.isArray(rows) ? rows : []).some((row) => {
        if (!Array.isArray(row)) return false;
        const rowHash = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
        if (activeHash && rowHash && !this.caseManualTransactionHashesMatch(activeHash, rowHash)) return false;
        const rowText = [
          row[CASE_MANUAL_START_COL.SYSTEM_NOTES],
          row[CASE_MANUAL_START_COL.HOP_LOGIC],
          row[CASE_MANUAL_START_COL.CASE_NOTES],
        ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
        return /funds\s+are\s+swapped\s+from|swap\s+solved|reverse[-_\s]*swap|\bswap\s*\//iu.test(rowText);
      });
      if (hasSwapRow) return false;
      const text = [
        result.status,
        result.integrationAction,
        result.message,
        result.guardrail?.reason,
        result.guardrail?.message,
        result.result?.swap?.status,
        result.result?.swap?.message,
        result.result?.swap?.summary,
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' | ').toLowerCase();
      const explicitFailure = /manual-swap-review|manual-swap-no-output|swap-produced-no-output|apply-swap-no-output|recommendation-produced-no-output|no automatic swap candidate was safe|no swap candidate was safe|swap needs manual|could not produce|no output rows|open full tool 88 to review the transfer table/iu.test(text);
      if (explicitFailure) return true;
      return /manual-swap|swap preview|swap solver|swap result/iu.test(text) && !hasSwapRow;
    },

        async markCaseManualStartCandidateFurtherManualWork(candidate = null, reason = '') {
      if (!candidate?.caseManualStartCandidate) return null;
      const matrix = this.getCaseManualStartMainMatrix().map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)));
      const rowIndex = this.findCaseManualStartMainRowIndexForCandidate(candidate);
      if (rowIndex < 0 || rowIndex >= matrix.length) {
        this.caseManualStartNodeActionTrayStatus('Could not locate the selected Manual Solve row in Main Data. Main Data was left unchanged.');
        return null;
      }
      const row = this.caseManualEnsureRowWidth(matrix[rowIndex].slice(), Math.max(CASE_MANUAL_START_COLUMN_COUNT, matrix[rowIndex].length || 0));
      const before = JSON.stringify(row);
      const targetAddress = this.caseManualStartCandidateEndpointAddress({ ...candidate, row, rawRow: row });
      row[CASE_MANUAL_START_COL.CONTINUES_AS] = 'Path ends.';
      row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualAppendFurtherManualWorkSystemNote(row[CASE_MANUAL_START_COL.SYSTEM_NOTES]);
      row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
        row[CASE_MANUAL_START_COL.CASE_NOTES],
        `Case Buddy Manual Solve: ${this.caseManualNormalizeText(reason) || 'selected ending requires further manual work'}; action=manual-path-ending${targetAddress ? `; target=${targetAddress}` : ''}.`
      );
      matrix[rowIndex] = this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(row));
      if (JSON.stringify(matrix[rowIndex]) === before) {
        this.caseManualStartNodeActionTrayStatus('This selected ending was already marked as requiring further manual work.');
        return { changed: false, rowIndex };
      }
      await this.replaceCaseManualStartLogicalSheetMatrix('main', matrix, 'Manual Solve marked a selected ending as requiring further manual work.', { autosaveDelayMs: 850 });
      this.caseManualStartHopInspectorOutputRows = [];
      this.caseManualStartHopInspectorPreviewRows = [];
      this.caseManualStartHopInspectorPreviewOpen = false;
      this.caseManualStartHopInspectorStatus = 'Marked selected ending as requiring further manual work and refreshed the Manual Solve viewport.';
      this.caseInvestigationVisualizerStatus = this.caseManualStartHopInspectorStatus;
      await this.refreshCaseManualStartAfterDirectRowMutation({ selectNextUnresolved: true });
      return { changed: true, rowIndex };
    },

    async markCaseManualStartSelectedFurtherManualWork() {
      const candidate = this.caseManualStartNodeActionTrayPrepareCandidate({ requirePathEnding: true });
      return this.markCaseManualStartCandidateFurtherManualWork(
        candidate,
        'investigator marked this selected ending as requiring further manual work'
      );
    },

        async changeCaseManualStartSelectedAttribution(options = {}) {
      const candidate = options.candidate?.caseManualStartCandidate
        ? options.candidate
        : this.caseManualStartNodeActionTrayPrepareCandidate({ requirePathEnding: false });
      if (!candidate) return null;
      const matrix = this.getCaseManualStartMainMatrix().map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)));
      const rowIndex = this.findCaseManualStartMainRowIndexForCandidate(candidate);
      if (rowIndex < 0 || rowIndex >= matrix.length) {
        this.caseManualStartNodeActionTrayStatus('Could not locate the selected Manual Solve row in Main Data. Attribution was not changed.');
        return null;
      }
      const reverse = this.caseManualStartCandidateIsReverse(candidate);
      const endpointColumnIndex = this.caseManualStartCandidateEndpointColumnIndex(candidate);
      const attributionColumnIndex = this.caseManualStartCandidateAttributionColumnIndex(candidate);
      const selectedRow = this.caseManualEnsureRowWidth(matrix[rowIndex].slice(), Math.max(CASE_MANUAL_START_COLUMN_COUNT, matrix[rowIndex].length || 0));
      const fallbackAttribution = reverse
        ? candidate.fromAttributionLabel
        : (candidate.attribution || candidate.toAttributionLabel);
      const selectedPreviousAttribution = this.caseManualCleanAttributionLabel(selectedRow[attributionColumnIndex] || fallbackAttribution || '');
      const address = this.caseManualNormalizeText(
        this.caseManualStartCandidateEndpointAddress({ ...candidate, row: selectedRow, rawRow: selectedRow })
          || (reverse ? 'selected source address' : 'selected receiving address')
      );
      let draft = Object.prototype.hasOwnProperty.call(options, 'attribution')
        ? options.attribution
        : undefined;
      if (draft === undefined) {
        const columnLabel = reverse ? 'Column X' : 'Column K';
        const promptMessage = `Enter the attribution label to write to ${columnLabel} for ${address}:`;
        draft = typeof window !== 'undefined' && typeof window.prompt === 'function'
          ? window.prompt(promptMessage, selectedPreviousAttribution || '')
          : '';
      }
      if (draft === null) return null;
      const attribution = this.caseManualCleanAttributionLabel(draft) || this.caseManualNormalizeText(draft);
      if (!attribution) {
        const message = 'No attribution was entered. Main Data was left unchanged.';
        this.caseManualStartNodeActionTrayAttributionStatus = message;
        this.caseManualStartNodeActionTrayStatus(message);
        return null;
      }
      const selectedAddressKey = this.caseManualNormalizeAddressKey(address);
      const targetIndexes = [];
      matrix.forEach((row, index) => {
        if (index === 0 && this.caseManualIsHeaderRow(row)) return;
        const rowEndpointKey = this.caseManualNormalizeAddressKey(row?.[endpointColumnIndex] || '');
        if (selectedAddressKey && rowEndpointKey === selectedAddressKey) targetIndexes.push(index);
      });
      if (!targetIndexes.length) targetIndexes.push(rowIndex);
      const beforeByIndex = new Map(targetIndexes.map(index => [index, JSON.stringify(matrix[index] || [])]));
      targetIndexes.forEach(index => {
        const row = this.caseManualEnsureRowWidth((matrix[index] || []).slice(), Math.max(CASE_MANUAL_START_COLUMN_COUNT, matrix[index]?.length || 0));
        const previousAttribution = this.caseManualCleanAttributionLabel(row[attributionColumnIndex] || (index === rowIndex ? selectedPreviousAttribution : '') || '');
        const pathEnds = /path\s*ends/iu.test(this.caseManualNormalizeContinuesAsText(row[CASE_MANUAL_START_COL.CONTINUES_AS] || (index === rowIndex ? candidate.continuesAs : '') || ''));
        row[attributionColumnIndex] = attribution;
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualBuildAttributionSystemNote(row[CASE_MANUAL_START_COL.SYSTEM_NOTES], attribution, { previousAttribution, pathEnds });
        row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
          row[CASE_MANUAL_START_COL.CASE_NOTES],
          pathEnds
            ? `Case Buddy Manual Solve: investigator marked this selected path ending by attribution update; action=manual-path-ending; attribution=${attribution}.`
            : `Case Buddy Manual Solve: investigator changed selected ${reverse ? 'source' : 'receiving'}-address attribution to ${attribution}.`
        );
        matrix[index] = this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(row));
      });
      const changedIndexes = targetIndexes.filter(index => JSON.stringify(matrix[index] || []) !== beforeByIndex.get(index));
      if (!changedIndexes.length) {
        this.caseManualStartNodeActionTrayStatus(`The selected ${reverse ? 'source' : 'receiving'} address rows already have that attribution.`);
        return { changed: false, rowIndexes: targetIndexes, attribution };
      }
      const endpointLabel = reverse ? 'source' : 'receiving';
      const rowCountLabel = changedIndexes.length === 1 ? `1 ${endpointLabel} row` : `${changedIndexes.length} ${endpointLabel} rows`;
      await this.replaceCaseManualStartLogicalSheetMatrix('main', matrix, `Manual Solve changed attribution for ${address}.`, { autosaveDelayMs: 850 });
      this.caseManualStartHopInspectorStatus = `Changed attribution to ${attribution} on ${rowCountLabel} and refreshed the Manual Solve viewport.`;
      this.caseInvestigationVisualizerStatus = this.caseManualStartHopInspectorStatus;
      await this.refreshCaseManualStartAfterDirectRowMutation({ selectNextUnresolved: false });
      return { changed: true, rowIndexes: changedIndexes, attribution };
    },

    caseManualSolveEditableAmountFieldKey(label = '') {
      const clean = this.caseManualNormalizeText(label).toLowerCase();
      if (clean === 'funds followed') return 'fundsFollowed';
      if (clean === 'left bounds') return 'leftBounds';
      return '';
    },

    caseManualSolveCanEditSelectedAmountRow(label = '') {
      const field = this.caseManualSolveEditableAmountFieldKey(label);
      return Boolean(field && this.caseManualSolveSelectedEditableAmountCandidate?.caseManualStartCandidate);
    },

    caseManualSolveAmountEditButtonTitle(label = '') {
      const field = this.caseManualSolveEditableAmountFieldKey(label);
      if (field === 'leftBounds') return 'Edit left bounds for this Manual Solve row';
      if (field === 'fundsFollowed') return 'Edit funds followed for this Manual Solve row';
      return 'Edit Manual Solve amount';
    },

    caseManualSolveAmountEditorCandidate() {
      const key = this.caseManualNormalizeText(this.caseManualSolveAmountEditor?.candidateKey || '');
      const selected = this.caseManualSolveSelectedEditableAmountCandidate || null;
      if (selected?.caseManualStartCandidate && (!key || this.buildCaseManualStartHopInspectorCandidateKey(selected) === key)) return selected;
      const allCandidates = (this.caseInvestigationVisualizerNodes || [])
        .map(node => node?.caseCandidate || node?.candidate || null)
        .filter(candidate => candidate?.caseManualStartCandidate);
      return allCandidates.find(candidate => this.buildCaseManualStartHopInspectorCandidateKey(candidate) === key) || selected || null;
    },

    caseManualSolveAmountEditorRow(candidate = null) {
      const rowIndex = Number.isInteger(this.caseManualSolveAmountEditor?.rowIndex)
        ? this.caseManualSolveAmountEditor.rowIndex
        : this.findCaseManualStartMainRowIndexForCandidate(candidate);
      const matrix = this.getCaseManualStartMainMatrix();
      if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex >= matrix.length) return null;
      return this.caseManualEnsureRowWidth(matrix[rowIndex] || [], Math.max(CASE_MANUAL_START_COLUMN_COUNT, matrix[rowIndex]?.length || 0));
    },

    caseManualNormalizeEditableAmountNumberText(value = '') {
      const numericText = this.caseManualExtractDecimalAmountText(value);
      if (!numericText) return '';
      const parsed = Number(numericText);
      if (!Number.isFinite(parsed)) return '';
      const negative = numericText.startsWith('-');
      const unsigned = numericText.replace(/^[-+]/u, '');
      let [whole = '0', fraction = ''] = unsigned.split('.');
      whole = (whole || '0').replace(/^0+(?=\d)/u, '') || '0';
      fraction = (fraction || '').replace(/[^0-9]/gu, '');
      return `${negative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
    },

    caseManualParseEditableAmount(value = '') {
      const numericText = this.caseManualNormalizeEditableAmountNumberText(value);
      if (!numericText) return NaN;
      const parsed = Number(numericText);
      return Number.isFinite(parsed) ? parsed : NaN;
    },

    caseManualFormatEditableAmountNumberText(value = '', options = {}) {
      const numericText = this.caseManualNormalizeEditableAmountNumberText(value);
      if (!numericText) return '';
      const negative = numericText.startsWith('-');
      const unsigned = numericText.replace(/^-/u, '');
      const [whole = '0', fraction = ''] = unsigned.split('.');
      const wholeText = options.grouping === false ? whole : this.caseManualAddCommasToWholeNumber(whole);
      return `${negative ? '-' : ''}${wholeText}${fraction ? `.${fraction}` : ''}`;
    },

    caseManualFormatEditedAmountForColumn(value = '', token = '', options = {}) {
      const amountText = this.caseManualFormatEditableAmountNumberText(value, { grouping: options.grouping !== false });
      if (!amountText) return this.caseManualNormalizeText(value);
      if (options.includeToken === false) return amountText;
      const tokenText = this.caseManualNormalizeText(token || this.caseManualTokenFromFundsFollowed(value)).toUpperCase();
      return `${amountText}${tokenText ? ` ${tokenText}` : ''}`.trim();
    },

    caseManualRewriteLeftBoundsDisplayForAmountEdit(existingValue = '', updates = {}) {
      const existing = String(existingValue ?? '').replace(/\r\n?/gu, '\n');
      const lines = existing ? existing.split('\n') : [];
      if (Object.prototype.hasOwnProperty.call(updates, 'leftBoundsDisplay')) {
        const display = this.caseManualNormalizeText(updates.leftBoundsDisplay) || '0.00';
        if (lines.length) lines[0] = display;
        else lines.push(display);
      }
      let text = lines.join('\n');
      if (updates.fundsFollowedDisplay) {
        const followedDisplay = this.caseManualNormalizeText(updates.fundsFollowedDisplay);
        const followedDetail = this.caseManualNormalizeText(updates.fundsFollowedDetail || followedDisplay);
        if (/This transaction follows\s+/iu.test(text)) {
          text = text.replace(/This transaction follows\s+[-+]?\d[\d,]*(?:\.\d+)?(?:\s+[A-Za-z][A-Za-z0-9._/-]{0,15})?/iu, `This transaction follows ${followedDisplay}`);
        } else if (text) {
          text = `${text}\nThis transaction follows ${followedDisplay}.`;
        } else {
          text = `0.00\nThis transaction follows ${followedDisplay}.`;
        }
        text = text.replace(/Funds Followed:\s*[-+]?\d[\d,]*(?:\.\d+)?(?:\s+[A-Za-z][A-Za-z0-9._/-]{0,15})?/giu, `Funds Followed: ${followedDetail}`);
      }
      return text;
    },

    async openCaseManualSolveAmountEditor(label = '') {
      const field = this.caseManualSolveEditableAmountFieldKey(label);
      const candidate = this.caseManualSolveSelectedEditableAmountCandidate;
      if (!field || !candidate?.caseManualStartCandidate) return null;
      const rowIndex = this.findCaseManualStartMainRowIndexForCandidate(candidate);
      if (rowIndex < 0) {
        this.caseInvestigationVisualizerStatus = 'Could not locate the selected Manual Solve row in Main Data. The amount editor was not opened.';
        return null;
      }
      const row = this.caseManualEnsureRowWidth((this.getCaseManualStartMainMatrix()[rowIndex] || []).slice(), CASE_MANUAL_START_COLUMN_COUNT);
      const token = this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TOKEN] || candidate.token || this.caseManualTokenFromFundsFollowed(row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]) || this.caseManualTokenFromFundsFollowed(candidate.fundsFollowed) || '').toUpperCase();
      const currentValue = field === 'leftBounds'
        ? (row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] || this.caseManualFirstLine(row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY]) || candidate.leftBoundsNumeric || candidate.leftBoundsDisplay || '0')
        : (row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] || candidate.fundsFollowed || '');
      const numericDraft = this.caseManualNormalizeEditableAmountNumberText(currentValue) || this.caseManualNormalizeText(currentValue).replace(/\b[A-Za-z][A-Za-z0-9._/-]*\b\s*$/u, '').trim();
      this.caseManualSolveAmountEditor = {
        open: true,
        field,
        draft: numericDraft,
        token,
        status: '',
        candidateKey: this.buildCaseManualStartHopInspectorCandidateKey(candidate),
        rowIndex
      };
      await this.$nextTick();
      const inputRef = this.$refs.caseManualSolveAmountEditorInput;
      const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;
      if (input && typeof input.focus === 'function') input.focus();
      if (input && typeof input.select === 'function') input.select();
      return candidate;
    },

    closeCaseManualSolveAmountEditor() {
      this.caseManualSolveAmountEditor = {
        open: false,
        field: '',
        draft: '',
        token: '',
        status: '',
        candidateKey: '',
        rowIndex: -1
      };
    },

    caseManualSolveMarkHopInspectorStaleAfterAmountEdit(candidate = null, message = '') {
      const candidateKey = this.buildCaseManualStartHopInspectorCandidateKey(candidate);
      const inspectorKey = this.buildCaseManualStartHopInspectorCandidateKey(this.caseManualStartHopInspectorCandidate);
      if (!candidateKey || !inspectorKey || candidateKey !== inspectorKey) return;
      this.caseManualStartHopInspectorSnapshot = null;
      this.caseManualStartHopInspectorOutputRows = [];
      this.caseManualStartHopInspectorOutputTsv = '';
      this.caseManualStartHopInspectorLoadedCandidateKey = '';
      this.caseManualStartHopInspectorPreviewOpen = false;
      this.caseManualStartHopInspectorPreviewRows = [];
      this.caseManualStartHopInspectorPreviewSnapshot = null;
      this.caseManualStartHopInspectorPreviewCandidate = null;
      this.caseManualStartHopInspectorPreviewAction = '';
      this.caseManualStartHopInspectorPreviewStatus = '';
      this.caseManualStartHopInspectorStatus = message || 'Manual Solve amounts changed. Run Flow Solver checks again before applying a hop action.';
    },

    async applyCaseManualSolveAmountEdit() {
      if (!this.caseManualSolveAmountEditor?.open) return null;
      const field = this.caseManualSolveAmountEditor.field;
      const amount = this.caseManualParseEditableAmount(this.caseManualSolveAmountEditor.draft);
      if (!Number.isFinite(amount)) {
        this.caseManualSolveAmountEditor.status = 'Enter a valid numeric amount.';
        return null;
      }
      const candidate = this.caseManualSolveAmountEditorCandidate();
      if (!candidate?.caseManualStartCandidate) {
        this.caseManualSolveAmountEditor.status = 'The selected Manual Solve row is no longer available.';
        return null;
      }
      const matrix = this.getCaseManualStartMainMatrix().map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)));
      const rowIndex = Number.isInteger(this.caseManualSolveAmountEditor.rowIndex)
        ? this.caseManualSolveAmountEditor.rowIndex
        : this.findCaseManualStartMainRowIndexForCandidate(candidate);
      if (rowIndex < 0 || rowIndex >= matrix.length) {
        this.caseManualSolveAmountEditor.status = 'Could not locate the selected Manual Solve row in Main Data.';
        return null;
      }
      const row = this.caseManualEnsureRowWidth((matrix[rowIndex] || []).slice(), Math.max(CASE_MANUAL_START_COLUMN_COUNT, matrix[rowIndex]?.length || 0));
      const token = this.caseManualNormalizeText(this.caseManualTokenFromFundsFollowed(this.caseManualSolveAmountEditor.draft) || this.caseManualSolveAmountEditor.token || row[CASE_MANUAL_START_COL.TOKEN] || candidate.token || '').toUpperCase();
      const transactionAmount = this.caseManualParseNumericAmount(row[CASE_MANUAL_START_COL.AMOUNT] || candidate.amount || '');
      if (field === 'fundsFollowed' && amount <= 0) {
        this.caseManualSolveAmountEditor.status = 'Funds followed must be greater than zero.';
        return null;
      }
      if (field === 'leftBounds') {
        if (amount < 0) {
          this.caseManualSolveAmountEditor.status = 'Left bounds cannot be negative.';
          return null;
        }
        if (Number.isFinite(transactionAmount) && transactionAmount > 0 && amount >= transactionAmount) {
          const maxDisplay = this.caseManualFormatTransactionAmountDisplay(String(transactionAmount), token, { includeToken: true });
          this.caseManualSolveAmountEditor.status = `Left bounds must be less than the known transaction amount (${maxDisplay}).`;
          return null;
        }
      }

      const before = JSON.stringify(row);
      const numericText = this.caseManualNormalizeEditableAmountNumberText(this.caseManualSolveAmountEditor.draft) || String(amount);
      if (field === 'fundsFollowed') {
        const display = this.caseManualFormatEditedAmountForColumn(numericText, token, { includeToken: true });
        const detailDisplay = this.caseManualFormatEditedAmountForColumn(numericText, token, { includeToken: true, grouping: false });
        row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = display;
        row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = this.caseManualRewriteLeftBoundsDisplayForAmountEdit(row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY], {
          fundsFollowedDisplay: display,
          fundsFollowedDetail: detailDisplay
        });
      } else if (field === 'leftBounds') {
        const display = this.caseManualFormatEditedAmountForColumn(numericText, token, { includeToken: false, grouping: true });
        row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = numericText;
        row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = this.caseManualRewriteLeftBoundsDisplayForAmountEdit(row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY], {
          leftBoundsDisplay: display
        });
      } else {
        this.caseManualSolveAmountEditor.status = 'Unknown Manual Solve amount field.';
        return null;
      }

      matrix[rowIndex] = this.caseManualMoveTool88OperationalNotesToColumnW(this.caseManualMigrateLegacyColumnPNotes(row));
      if (JSON.stringify(row) === before) {
        this.caseManualSolveAmountEditor.status = 'That value is already stored on the selected Main Data row.';
        return { changed: false, rowIndex };
      }

      const status = field === 'fundsFollowed'
        ? 'Manual Solve updated Funds Followed in Main Data and refreshed the viewport.'
        : 'Manual Solve updated Left Bounds in Main Data and refreshed the viewport.';
      this.caseManualSolveMarkHopInspectorStaleAfterAmountEdit(candidate, `${status} Run Tool 88 checks again before applying a hop action.`);
      await this.replaceCaseManualStartLogicalSheetMatrix('main', matrix, status, { autosaveDelayMs: 850 });
      this.caseInvestigationVisualizerStatus = status;
      await this.refreshCaseManualStartAfterDirectRowMutation({ selectNextUnresolved: false });
      this.closeCaseManualSolveAmountEditor();
      return { changed: true, rowIndex, field, amount, token };
    },

    openCaseManualStartSettings() {
      this.caseManualStartSettingsDraft = { ...this.caseManualStartReadTool88Settings(), tool88NoteColumn: 'W' };
      this.caseManualStartSettingsStatus = 'These settings mirror Tool 88 and are saved in the same browser settings store.';
      this.caseManualStartSettingsOpen = true;
      this.caseManualStartHopInspectorMounted = true;
      this.$nextTick(() => this.syncCaseManualStartSettingsToEmbeddedTool88(this.caseManualStartSettingsDraft));
    },

    closeCaseManualStartSettings() {
      this.caseManualStartSettingsOpen = false;
      this.caseManualStartSettingsStatus = '';
    },

    resetCaseManualStartSettings() {
      this.caseManualStartSettingsDraft = { ...CASE_MANUAL_START_TOOL88_DEFAULT_SETTINGS, tool88NoteColumn: 'W' };
      this.caseManualStartSettingsStatus = 'Reset to Tool 88 defaults. Click Save settings to apply.';
    },

    saveCaseManualStartSettings(options = {}) {
      const previous = this.caseManualStartReadTool88Settings();
      const saved = this.caseManualStartWriteTool88Settings({ ...this.caseManualStartSettingsDraft, tool88NoteColumn: 'W' });
      this.caseManualStartSettingsDraft = { ...saved, tool88NoteColumn: 'W' };
      this.syncCaseManualStartSettingsToEmbeddedTool88(saved);
      const solvingSettingChanged = previous.enableNativeDirectSpendMatchGuard !== saved.enableNativeDirectSpendMatchGuard
        || previous.hopLogicType !== saved.hopLogicType
        || previous.traceDirection !== saved.traceDirection;
      if (solvingSettingChanged) {
        this.caseManualStartHopInspectorSnapshot = null;
        this.caseManualStartHopInspectorOutputRows = [];
        this.caseManualStartHopInspectorOutputTsv = '';
        this.caseManualStartHopInspectorLoadedCandidateKey = '';
        this.caseManualStartHopInspectorPreviewRows = [];
        this.caseManualStartHopInspectorPreviewSnapshot = null;
        this.caseManualStartSettingsStatus = 'Manual Solve Tool 88 settings saved. Re-run checks for the selected ending to use the updated solving mode.';
        this.caseManualStartHopInspectorStatus = this.caseManualStartSettingsStatus;
      } else {
        this.caseManualStartSettingsStatus = 'Manual Solve Tool 88 settings saved.';
      }
      if (!this.caseManualStartSettingsStatus) this.caseManualStartSettingsStatus = 'Manual Solve Tool 88 settings saved.';
      if (options.quiet !== true && typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartSettingsStatus);
    },

    openCaseManualStartHopInspectorFromSelection(options = {}) {
      const candidate = options.candidate?.caseManualStartCandidate
        ? options.candidate
        : (this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection({ quiet: true }) || null);
      if (this.caseInvestigationVisualizerSelectedCount !== 1 && !options.candidate?.caseManualStartCandidate) {
        this.caseManualStartHopInspectorStatus = 'Select exactly one Manual Solve path ending before pressing Solve hop. Use Next when you want Case Buddy to choose the next unresolved ending.';
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        return;
      }
      if (!candidate?.caseManualStartCandidate) {
        this.caseManualStartHopInspectorStatus = 'The selected node is not a Manual Solve transaction/path ending.';
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        return;
      }
      if (!this.isCaseManualStartPathEndingCandidate(candidate)) {
        this.caseManualStartHopInspectorStatus = 'The selected Manual Solve node is not a Path ends / unresolved-ending row.';
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        return;
      }
      if (this.caseManualStartCandidateRequiresChoiceBeforeSolve(candidate)) {
        this.caseManualStartOutputChoiceCandidate = candidate;
        this.caseInvestigationVisualizerSelectedCandidate = candidate;
        this.caseManualStartHopInspectorStatus = `${this.caseManualStartOutputChoiceButtonLabel} is required before this ambiguous starting transaction can be solved.`;
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        this.openCaseManualStartOutputChoiceFromSelection();
        return;
      }
      if (!options.bypassResolvedWarning && this.caseManualStartCandidateLooksResolvedPathEnding(candidate)) {
        this.openCaseManualStartResolvedSolveWarning(candidate);
        return;
      }
      this.caseManualStartResolvedSolveWarningOpen = false;
      this.caseManualStartResolvedSolveWarningCandidate = null;
      this.caseManualStartResolvedSolveWarningChecked = false;
      this.caseManualStartHopInspectorCandidate = candidate;
      this.caseInvestigationVisualizerSelectedCandidate = candidate;
      const selectedKey = this.buildCaseManualStartHopInspectorCandidateKey(this.caseManualStartSingleSelectedCandidate);
      const candidateKey = this.buildCaseManualStartHopInspectorCandidateKey(candidate);
      if (!selectedKey || selectedKey !== candidateKey) this.selectCaseManualStartPathEndingNode(candidate);
      this.caseManualStartHopInspectorOpen = true;
      this.caseManualStartHopInspectorMounted = true;
      this.caseManualStartHopInspectorFullToolOpen = false;
      this.caseManualStartHopInspectorStopRequested = false;
      this.caseManualStartHopInspectorSnapshot = null;
      this.caseManualStartHopInspectorOutputRows = [];
      this.caseManualStartHopInspectorOutputTsv = '';
      this.caseManualStartHopInspectorLoadedCandidateKey = '';
      this.caseManualStartHopInspectorPreviewOpen = false;
      this.caseManualStartHopInspectorPreviewRows = [];
      this.caseManualStartHopInspectorPreviewSnapshot = null;
      this.caseManualStartHopInspectorPreviewCandidate = null;
      this.caseManualStartHopInspectorPreviewAction = '';
      this.caseManualStartHopInspectorPreviewStatus = '';
      this.caseManualStartHopInspectorPreviewApplied = false;
      this.caseManualStartHopInspectorPreviewServiceOverride = false;
      this.caseManualStartHopInspectorLastTraceServiceOverride = false;
      this.caseManualStartHopInspectorStatus = `Ready to inspect ${candidate.pathLabel || this.caseManualShorten(candidate.hash, 10, 8)}. Tool 88 checks will run automatically.`;
      this.queueCaseManualStartHopInspectorAutoCheck(candidate, { force: true });
    },

    closeCaseManualStartHopInspector() {
      if (this.caseManualStartHopInspectorRunning) this.requestCaseManualStartHopInspectorStop();
      this.clearCaseManualStartViewportWorkIndicator?.({ immediate: true });
      this.clearCaseManualStartHopInspectorAutoCheckTimer?.();
      this.stopCaseManualStartHopInspectorTraceLoading();
      this.caseManualStartHopInspectorPreviewOpen = false;
      this.caseManualStartHopInspectorPreviewServiceOverride = false;
      this.caseManualStartHopInspectorLastTraceServiceOverride = false;
      this.caseManualStartHopInspectorOpen = false;
    },

    requestCaseManualStartHopInspectorStop() {
      this.caseManualStartHopInspectorStopRequested = true;
      this.caseManualStartHopInspectorPreviewOpen = false;
      this.clearCaseManualStartViewportWorkIndicator?.({ immediate: true });
      this.caseManualStartHopInspectorStatus = 'Stop requested. Tool 88 will pause at the next safe point.';
      const tool = this.getCaseManualStartHopInspectorTool();
      if (tool && typeof tool.caseBuddyRequestTraceStop === 'function') {
        tool.caseBuddyRequestTraceStop('Case Buddy manual visual hop inspector stop requested.');
      } else if (tool && typeof tool.requestTraceFetchStop === 'function') {
        tool.requestTraceFetchStop('Case Buddy manual visual hop inspector stop requested.');
      }
      if (tool && typeof tool.caseBuddyRequestAutomationStop === 'function') {
        tool.caseBuddyRequestAutomationStop('Case Buddy manual visual hop inspector stop requested.');
      }
      this.refreshCaseManualStartHopInspectorTraceProgress();
    },


    requestCaseManualStartHopInspectorStopAndClosePreview() {
      this.requestCaseManualStartHopInspectorStop();
      const current = this.caseManualStartHopInspectorTraceProgress || {};
      this.caseManualStartHopInspectorTraceProgress = {
        ...current,
        status: 'stop-requested',
        message: 'Stop requested. The trace preview will stay closed if Tool 88 returns after the stop request.',
        pulse: !current.pulse
      };
      this.stopCaseManualStartHopInspectorTraceLoading();
      this.caseManualStartHopInspectorPreviewOpen = false;
      this.caseManualStartHopInspectorPreviewRows = [];
      this.caseManualStartHopInspectorPreviewSnapshot = null;
      this.caseManualStartHopInspectorPreviewAction = '';
      this.caseManualStartHopInspectorPreviewServiceOverride = false;
      this.caseManualStartHopInspectorPreviewStatus = 'Trace preview fetch was stopped. Late Tool 88 rows will be ignored until checks are refreshed.';
    },

    caseManualStartHopInspectorSnapshotHasManualServiceOverride(snapshot = {}) {
      if (!snapshot || typeof snapshot !== 'object') return false;
      if (snapshot.serviceStopOverride === true || snapshot.manualServiceOverride === true || snapshot.caseBuddyManualServiceOverride === true) return true;
      if (snapshot.result?.trace?.serviceStopOverride === true || snapshot.result?.trace?.manualServiceOverride === true) return true;
      if (snapshot.lastRunMeta?.serviceStopOverride === true || snapshot.lastRunMeta?.manualServiceOverride === true) return true;
      const text = [
        snapshot.message,
        snapshot.status,
        snapshot.integrationAction,
        snapshot.result?.trace?.serviceOverrideReason,
        snapshot.lastRunMeta?.serviceOverrideReason,
        snapshot.reportText
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      return /manual\s+service\s*\/\s*hot[-\s]?wallet\s+override|additional\s+service\s+checks\s+were\s+bypassed|service\s+warning\s+override|bypass(?:ed)?\s+the\s+.*hot[-\s]?wallet/iu.test(text);
    },

    caseManualStartHopInspectorSnapshotLooksUnknownServicePathEnding(snapshot = {}) {
      if (!snapshot || typeof snapshot !== 'object') return false;
      const rowTexts = [];
      const collectRowText = (rows) => {
        if (!Array.isArray(rows)) return;
        rows.forEach((row) => {
          if (Array.isArray(row)) {
            rowTexts.push([
              row[CASE_MANUAL_START_COL.SYSTEM_NOTES],
              row[CASE_MANUAL_START_COL.ATTRIBUTION],
              row[CASE_MANUAL_START_COL.ANALYST_NOTES],
              row[CASE_MANUAL_START_COL.CASE_NOTES],
              row[CASE_MANUAL_START_COL.FROM_LABEL],
              row[CASE_MANUAL_START_COL.CONTINUES_AS]
            ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' '));
            return;
          }
          if (row && typeof row === 'object') {
            rowTexts.push([
              row.systemNotes,
              row.system_notes,
              row.notes,
              row.attribution,
              row.attributionData,
              row.entity,
              row.toAttributionLabel,
              row.to_attribution_label,
              row.analystNotes,
              row.analyst_notes,
              row.caseNotes,
              row.case_notes,
              row.tool88Notes,
              row.tool88_notes,
              row.internalNotes,
              row.message,
              row.status,
              row.summary,
              row.reason,
              row.fromLabel,
              row.from_label,
              row.continuesAs,
              row.continues_as,
              row.continues,
              row.visualEndpointStatus,
              row.labelCategory,
              row.decision?.title,
              row.decision?.summary,
              row.decision?.action
            ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' '));
          }
        });
      };
      collectRowText(snapshot.outputRows);
      collectRowText(snapshot.result?.outputRows);
      collectRowText(snapshot.rows);
      const text = [
        snapshot.status,
        snapshot.message,
        snapshot.integrationAction,
        snapshot.decision?.action,
        snapshot.decision?.title,
        snapshot.decision?.summary,
        snapshot.decision?.buttonLabel,
        Array.isArray(snapshot.decision?.reasons) ? snapshot.decision.reasons.join(' ') : '',
        snapshot.result?.pathEnding?.reason,
        snapshot.result?.pathEnding?.note,
        snapshot.result?.pathEnding?.specialTreatment,
        snapshot.result?.summary,
        ...rowTexts
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      if (!text) return false;
      const hasUnknown = /\bunknown\b/iu.test(text) || /unattributed/iu.test(text);
      const hasServiceShape = /unattributed\s+exchange\s+hot\s+wallet|features\s+of\s+an\s+unattributed\s+exchange|exchange\s+hot\s+wallet|hot[-\s]?wallet|service\s+address|service[-\s]?pattern|service\s*\/\s*hot|potential\s+service|additional\s+service\s+checks|high\s+stablecoin\s+flow|high\s+erc20\s+transfer\s+count|trace\s+through\s+again\s+only\s+if/iu.test(text);
      const hasStopShape = /path\s*ends|manual-path-ending|tool\s*88\s+stopped|action\s*=\s*manual-path-ending|specialTreatment\s*[:=]\s*potential_service_address/iu.test(text)
        || snapshot.result?.pathEnding?.specialTreatment === 'potential_service_address'
        || snapshot.decision?.action === 'path_end';
      return Boolean(hasUnknown && hasServiceShape && hasStopShape);
    },

    caseManualStartHopInspectorActionLabel(action = '') {
      const actionKey = this.caseManualNormalizeText(action).toLowerCase();
      if (actionKey === 'path-ending' || actionKey === 'path_end') return 'Path ending';
      if (actionKey === 'swap') return 'Swap';
      if (actionKey === 'bridge') return 'Bridge';
      if (actionKey === 'recommended') return 'Recommended action';
      return 'Trace through';
    },

    labelCaseManualStartHopInspectorAction(action = '') {
      return this.caseManualStartHopInspectorActionLabel(action);
    },

    caseManualStartHopInspectorActionNeedsReviewPreview(action = '') {
      const actionKey = this.caseManualNormalizeText(action).toLowerCase();
      return actionKey === 'swap' || actionKey === 'bridge';
    },

    caseManualStartCurrentHashSet() {
      const matrix = this.getCaseManualStartMainMatrix();
      const rows = Array.isArray(matrix) ? matrix : [];
      const dataRows = rows.length && this.caseManualIsHeaderRow(rows[0]) ? rows.slice(1) : rows;
      return new Set(dataRows
        .map(row => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase())
        .filter(Boolean));
    },

    caseManualStartSnapshotActionLooksTraceThrough(snapshot = {}) {
      if (!snapshot || typeof snapshot !== 'object') return false;
      const text = [
        snapshot.integrationAction,
        snapshot.status,
        snapshot.message,
        snapshot.decision?.action,
        snapshot.decision?.title,
        snapshot.decision?.summary,
        snapshot.decision?.buttonLabel,
        snapshot.result?.trace?.summary,
        snapshot.result?.summary,
        snapshot.selectedEntry?.continuesAs
      ].map(value => this.caseManualNormalizeText(value)).filter(Boolean).join(' ');
      if (/path[-_\s]*end|manual[-_\s]*path[-_\s]*ending|swap|bridge/iu.test(text)) return false;
      return /trace[-_\s]*through|trace\s+through|exit\s+row|next\s+hop|continues\s+as/iu.test(text);
    },

    buildCaseManualStartHopInspectorPreviewEvidenceLines(snapshot = {}) {
      const lines = [];
      const pushLine = value => {
        const clean = this.caseManualNormalizeText(value);
        if (!clean) return;
        if (lines.some(line => line.toLowerCase() === clean.toLowerCase())) return;
        lines.push(clean);
      };
      if (this.caseManualStartHopInspectorPreviewRequiresServiceOverride || this.caseManualStartHopInspectorSnapshotHasManualServiceOverride(snapshot)) {
        pushLine('Warning acknowledged: Tool 88 previously flagged this address as a possible Unknown service / hot-wallet endpoint; this trace preview bypassed that assessor for this run only.');
      }
      pushLine(snapshot?.decision?.summary);
      if (Array.isArray(snapshot?.decision?.reasons)) snapshot.decision.reasons.slice(0, 4).forEach(pushLine);
      pushLine(snapshot?.result?.summary);
      pushLine(snapshot?.result?.swap?.title);
      pushLine(snapshot?.result?.swap?.summary);
      pushLine(snapshot?.result?.swap?.note);
      pushLine(snapshot?.result?.bridge?.title);
      pushLine(snapshot?.result?.bridge?.summary);
      pushLine(snapshot?.result?.bridge?.note);
      const reportText = this.caseManualNormalizeText(snapshot?.reportText);
      if (reportText) {
        reportText
          .split(/\r?\n/u)
          .map(line => this.caseManualNormalizeText(line))
          .filter(line => line && !/^[-=]{3,}$/u.test(line))
          .slice(0, 10)
          .forEach(pushLine);
      }
      return lines.slice(0, 8);
    },

    captureCaseManualStartTraceInspectorRows(tool = null) {
      const source = tool || this.getCaseManualStartHopInspectorTool?.();
      const rows = Array.isArray(source?.traceInspectorRows) ? source.traceInspectorRows : [];
      return rows.map(row => ({ ...(row || {}) }));
    },

    attachCaseManualStartTraceInspectorRows(snapshot = {}, tool = null) {
      const rows = this.captureCaseManualStartTraceInspectorRows(tool);
      const enriched = { ...(snapshot || {}) };
      const uniqueRows = (rowGroups = []) => {
        const output = [];
        const seen = new Set();
        rowGroups.forEach((group) => {
          this.normalizeCaseManualStartHopOutputRows(group || [])
            .filter(row => this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true }))
            .filter(row => !this.caseManualRowLooksLikeTool88FakeStatusRow(row))
            .forEach((row) => {
              const key = this.caseManualStartRowKey(row);
              if (!key || seen.has(key)) return;
              seen.add(key);
              output.push(row);
            });
        });
        return output;
      };
      const safeCallRows = (methodName) => {
        if (!tool || typeof tool[methodName] !== 'function') return [];
        try {
          return tool[methodName]() || [];
        } catch {
          return [];
        }
      };
      const rowsToTsv = (rowGroup = []) => (Array.isArray(rowGroup) ? rowGroup : [])
        .map(row => this.caseManualEnsureRowWidth(row, CASE_MANUAL_START_COLUMN_COUNT).slice(0, CASE_MANUAL_START_COLUMN_COUNT)
          .map((cell) => {
            const value = String(cell ?? '');
            return /["\t\n\r]/u.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
          })
          .join('\t'))
        .join('\n');
      const traceExitRows = uniqueRows([
        tool?.traceResult?.exitRows,
        tool && typeof tool.getCaseBuddyExportableTraceRows === 'function' ? tool.getCaseBuddyExportableTraceRows(tool.traceResult?.exitRows || []) : [],
        enriched.traceExitRows,
        enriched.caseBuddyTraceExitRows,
        enriched.caseBuddyToolTraceExitRows,
        enriched.caseBuddyRecoveredTraceExitRows,
        enriched.exitRows,
        enriched.result?.traceExitRows,
        enriched.result?.caseBuddyTraceExitRows,
        enriched.result?.trace?.exitRows,
        enriched.result?.exitRows,
        enriched.dormantResult?.exitRows,
        enriched.result?.dormantResult?.exitRows
      ]);
      const toolOutputRows = uniqueRows([
        enriched.outputRows,
        enriched.caseBuddyToolOutputRows,
        enriched.caseBuddySnapshotOutputRows,
        enriched.result?.outputRows,
        safeCallRows('buildCaseBuddySnapshotOutputRows'),
        safeCallRows('getCurrentOutputRowsForExport'),
        tool?.outputSnapshotRows,
        tool?.$refs?.outputSpreadsheet?.getData?.()
      ]);
      if (rows.length) enriched.caseBuddyTraceInspectorRows = rows;
      if (traceExitRows.length) {
        enriched.traceExitRows = traceExitRows;
        enriched.caseBuddyTraceExitRows = traceExitRows;
        enriched.caseBuddyToolTraceExitRows = traceExitRows;
        if (!Array.isArray(enriched.exitRows) || !enriched.exitRows.length) enriched.exitRows = traceExitRows;
        enriched.result = {
          ...(enriched.result || {}),
          trace: {
            ...(enriched.result?.trace || {}),
            exitRows: Array.isArray(enriched.result?.trace?.exitRows) && enriched.result.trace.exitRows.length
              ? enriched.result.trace.exitRows
              : traceExitRows
          }
        };
      }
      if (toolOutputRows.length) {
        enriched.caseBuddyToolOutputRows = toolOutputRows;
        const existingRows = this.normalizeCaseManualStartHopOutputRows(enriched.outputRows || []);
        const dormant = this.caseManualExtractDormantResultFromSnapshot(enriched || {});
        const activeCandidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || null;
        const existingHasContinuation = this.caseManualPreviewRowsContainTraceChild(existingRows, activeCandidate, enriched)
          || (this.caseManualDormantResultIsPartial(dormant) && this.caseManualRowsContainDormantTraceChild(existingRows, dormant, activeCandidate, enriched));
        const toolRowsHaveContinuation = this.caseManualPreviewRowsContainTraceChild(toolOutputRows, activeCandidate, enriched)
          || (this.caseManualDormantResultIsPartial(dormant) && this.caseManualRowsContainDormantTraceChild(toolOutputRows, dormant, activeCandidate, enriched));
        if ((!existingRows.length || !existingHasContinuation) && toolRowsHaveContinuation) {
          enriched.outputRows = toolOutputRows;
          enriched.outputTsv = rowsToTsv(toolOutputRows);
        }
      }
      return enriched;
    },

    caseManualStartTraceRowsLookUsable(rows = [], dormantResult = null, activeCandidate = null, snapshot = {}) {
      const candidate = activeCandidate || this.caseManualStartHopInspectorPreviewCandidate || this.caseManualStartHopInspectorCandidate || this.caseManualStartHopInspectorSelectedCandidate || null;
      if (this.caseManualDormantResultIsPartial(dormantResult)) {
        return this.caseManualRowsContainDormantTraceChild(rows, dormantResult, candidate, snapshot);
      }
      return this.caseManualPreviewRowsContainTraceChild(rows, candidate, snapshot);
    },

    async recoverCaseManualStartTraceSnapshotFromTool(tool = null, snapshot = {}, activeCandidate = null, options = {}) {
      const action = options.action || 'trace-through';
      let result = this.attachCaseManualStartTraceInspectorRows(snapshot || {}, tool);
      let dormant = this.caseManualExtractDormantResultFromSnapshot(result || {});
      let rows = this.caseManualSelectFreshHopInspectorRows(result || {}, this.caseManualStartHopInspectorOutputRows, activeCandidate);
      if (this.caseManualStartTraceRowsLookUsable(rows, dormant, activeCandidate, result || {})) {
        return { snapshot: result, rows, dormantResult: dormant, recovered: false };
      }

      if (!tool) return { snapshot: result, rows, dormantResult: dormant, recovered: false };
      const delays = [0, 80, 180, 360, 640];
      for (const delayMs of delays) {
        if (delayMs > 0) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
        await this.$nextTick();
        const rebuilt = typeof tool.buildCaseBuddyHopSolverSnapshot === 'function'
          ? tool.buildCaseBuddyHopSolverSnapshot({
              includeRows: true,
              allowUnverifiedTraceOutput: true,
              acceptUnverifiedTraceOutput: true,
              allowCaseBuddyRecoveredTraceOutput: true,
              status: 'decision-ready',
              integrationAction: `manual-${action || 'trace-through'}`,
              message: `Case Buddy recovered Tool 88 ${this.caseManualStartHopInspectorActionLabel(action)} rows after the provider result settled.`
            })
          : {};
        result = this.attachCaseManualStartTraceInspectorRows({ ...(result || {}), ...(rebuilt || {}) }, tool);
        dormant = this.caseManualExtractDormantResultFromSnapshot(result || {});
        rows = this.caseManualSelectFreshHopInspectorRows(result || {}, this.caseManualStartHopInspectorOutputRows, activeCandidate);
        if (this.caseManualStartTraceRowsLookUsable(rows, dormant, activeCandidate, result || {})) {
          return { snapshot: result, rows, dormantResult: dormant, recovered: true };
        }
      }
      return { snapshot: result, rows, dormantResult: dormant, recovered: false };
    },

    extractCaseManualStartTraceInspectorRows(snapshot = {}) {
      const candidates = [
        snapshot?.caseBuddyTraceInspectorRows,
        snapshot?.traceInspectorRows,
        snapshot?.result?.traceInspectorRows,
        snapshot?.result?.caseBuddyTraceInspectorRows,
        snapshot?.result?.trace?.inspectorRows
      ];
      for (const candidate of candidates) {
        if (Array.isArray(candidate) && candidate.length) return candidate;
      }
      return this.captureCaseManualStartTraceInspectorRows();
    },

    buildCaseManualStartTraceInspectorRowMap(snapshot = {}) {
      const map = new Map();
      this.extractCaseManualStartTraceInspectorRows(snapshot).forEach((row) => {
        const hash = this.caseManualNormalizeText(row?.hash || row?.txHash || row?.transactionHash || row?.transaction_hash || row?.raw?.hash || row?.raw?.transactionHash).toLowerCase();
        if (!hash) return;
        const rows = map.get(hash) || [];
        rows.push(row);
        map.set(hash, rows);
      });
      return map;
    },

    scoreCaseManualStartTraceInspectorRowForPreview(inspectorRow = {}, previewRow = [], fallbackToken = '', candidateAddress = '') {
      if (!inspectorRow || typeof inspectorRow !== 'object') return -1;
      const fromAddress = this.caseManualNormalizeText(previewRow?.[CASE_MANUAL_START_COL.FROM_ADDRESS]).toLowerCase();
      const toAddress = this.caseManualNormalizeText(previewRow?.[CASE_MANUAL_START_COL.TO_ADDRESS]).toLowerCase();
      const rowFrom = this.caseManualNormalizeText(inspectorRow.fromAddress || inspectorRow.from || inspectorRow.raw?.from || inspectorRow.raw?.from_address).toLowerCase();
      const rowTo = this.caseManualNormalizeText(inspectorRow.toAddress || inspectorRow.to || inspectorRow.raw?.to || inspectorRow.raw?.to_address).toLowerCase();
      const previewToken = this.caseManualNormalizeText(fallbackToken || previewRow?.[CASE_MANUAL_START_COL.TOKEN] || this.caseManualTokenFromFundsFollowed(previewRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED])).toLowerCase();
      const rowToken = this.caseManualNormalizeText(inspectorRow.token || inspectorRow.tokenSymbol || inspectorRow.token_symbol || inspectorRow.assetSymbol || inspectorRow.symbol || inspectorRow.raw?.tokenSymbol || inspectorRow.raw?.token_symbol).toLowerCase();
      const amountText = this.formatCaseManualStartTraceInspectorAmountForPreview(inspectorRow, fallbackToken);
      const kindText = this.caseManualNormalizeText([inspectorRow.kindLabel, inspectorRow.transactionKind, inspectorRow.raw?.transaction_kind, inspectorRow.raw?.source_kind, inspectorRow.raw?.type].filter(Boolean).join(' ')).toLowerCase();
      const selected = this.caseManualNormalizeText(candidateAddress).toLowerCase();
      let score = 0;
      if (amountText && !/^0(?:\.0+)?(?:\s|$)/u.test(amountText)) score += 8;
      if (rowToken && previewToken && rowToken === previewToken) score += 5;
      if (rowFrom && fromAddress && rowFrom === fromAddress) score += 4;
      if (rowTo && toAddress && rowTo === toAddress) score += 4;
      if (selected && (rowFrom === selected || rowTo === selected)) score += 2;
      if (/erc20|trc20|token|transfer/u.test(kindText)) score += 2;
      if (inspectorRow.nonBalanceAffecting || inspectorRow.failedOrReverted) score -= 6;
      return score;
    },

    pickCaseManualStartTraceInspectorRowForPreview(inspectorRows = [], previewRow = [], fallbackToken = '', candidateAddress = '') {
      const rows = Array.isArray(inspectorRows) ? inspectorRows : (inspectorRows ? [inspectorRows] : []);
      if (!rows.length) return null;
      let bestRow = rows[0];
      let bestScore = this.scoreCaseManualStartTraceInspectorRowForPreview(bestRow, previewRow, fallbackToken, candidateAddress);
      rows.slice(1).forEach((row) => {
        const score = this.scoreCaseManualStartTraceInspectorRowForPreview(row, previewRow, fallbackToken, candidateAddress);
        if (score > bestScore) {
          bestScore = score;
          bestRow = row;
        }
      });
      return bestRow;
    },

    formatCaseManualStartTraceInspectorAmountForPreview(row = null, fallbackToken = '') {
      if (!row || typeof row !== 'object') return '';
      const token = this.caseManualNormalizeText(
        row.token || row.tokenSymbol || row.token_symbol || row.assetSymbol || row.symbol ||
        row.raw?.tokenSymbol || row.raw?.token_symbol || row.raw?.symbol || fallbackToken
      );
      const formatAmount = value => this.caseManualFormatHopInspectorAmountWithToken(value, token);
      const displayCandidates = [
        row.raw?.amount_formatted,
        row.raw?.amountFormatted,
        row.raw?.amount_decimal,
        row.raw?.value_formatted,
        row.raw?.displayAmount,
        row.raw?.valueText,
        row.raw?.amountText,
        row.amount_formatted,
        row.amountFormatted,
        row.amount_decimal,
        row.value_formatted,
        row.displayAmount,
        row.valueText,
        row.transferAmountText,
        row.amountText
      ];
      for (const candidate of displayCandidates) {
        const formatted = formatAmount(candidate);
        if (formatted) return formatted;
      }
      const decimalsRaw = this.caseManualNormalizeText(
        row.tokenDecimal ?? row.token_decimals ?? row.tokenDecimals ?? row.decimals ??
        row.raw?.tokenDecimal ?? row.raw?.token_decimals ?? row.raw?.tokenDecimals ?? row.raw?.decimals ?? ''
      );
      const decimals = Number(decimalsRaw);
      const rawAtomicCandidates = [
        row.raw?.value_raw,
        row.raw?.raw_value,
        row.raw?.amount_raw,
        row.raw?.value,
        row.value_raw,
        row.raw_value,
        row.amount_raw
      ];
      if (Number.isFinite(decimals) && decimals > 0) {
        for (const candidate of rawAtomicCandidates) {
          const rawText = this.caseManualNormalizeText(candidate).replace(/,/gu, '');
          if (!/^\d+$/u.test(rawText)) continue;
          const padded = rawText.padStart(decimals + 1, '0');
          const whole = padded.slice(0, -decimals).replace(/^0+(?=\d)/u, '') || '0';
          const fraction = padded.slice(-decimals);
          const formatted = formatAmount(`${whole}.${fraction}`);
          if (formatted) return formatted;
        }
      }
      const rawAmount = row.amount ?? row.value ?? row.transferAmount ?? row.raw?.amount ?? row.raw?.transferAmount;
      const formattedRawAmount = formatAmount(rawAmount);
      return formattedRawAmount || '';
    },

    buildCaseManualStartTraceInspectorBtcEndpointList(row = null, side = 'input', prefix = 'btc') {
      if (!row || typeof row !== 'object') return [];
      const rawList = side === 'input'
        ? (row.btcInputs || row.inputs || row.raw?.btc_inputs || row.raw?.inputs || [])
        : (row.btcOutputs || row.outputs || row.raw?.btc_outputs || row.raw?.outputs || []);
      if (!Array.isArray(rawList) || !rawList.length) return [];
      return rawList.slice(0, 12).map((entry, index) => {
        const address = this.caseManualNormalizeText(entry?.address || entry?.fromAddress || entry?.toAddress || entry?.addr || entry?.scriptPubKey?.addresses?.[0] || entry);
        const token = this.caseManualNormalizeText(entry?.token || entry?.tokenSymbol || entry?.assetSymbol || entry?.symbol || 'BTC');
        const rawAmount = entry?.amountText ?? entry?.valueText ?? entry?.displayAmount ?? entry?.amount ?? entry?.value ?? '';
        const amountText = this.caseManualFormatHopInspectorAmountWithToken(rawAmount, token);
        const label = [address ? this.caseManualShorten(address, 7, 5) : `${side} ${index + 1}`, amountText].filter(Boolean).join(' · ');
        return { key: `${prefix}-${side}-${index}-${address || amountText || 'entry'}`, label };
      });
    },

    buildCaseManualStartSwapPreviewItems(snapshot = {}) {
      const swap = snapshot?.result?.swap || null;
      if (!swap) return [];
      const rows = Array.isArray(swap.explorerRows) ? swap.explorerRows : [];
      const sentToken = this.caseManualNormalizeText(swap.sent?.token || swap.sent?.tokenSymbol || '');
      const receivedToken = this.caseManualNormalizeText(swap.received?.token || swap.received?.tokenSymbol || '');
      const txHash = this.caseManualNormalizeText(swap.txHash || rows[0]?.hash || '');
      const initiatingAddress = this.caseManualNormalizeText(swap.initiatingAddress || '');
      const swapContract = this.caseManualNormalizeText(swap.swapContractAddress || '');
      const buildAmountParts = (amount, token) => {
        const text = [this.caseManualNormalizeText(amount), this.caseManualNormalizeText(token)].filter(Boolean).join(' ');
        return this.splitCaseManualStartPreviewAmountToken(text || 'Amount pending', token);
      };
      const makeLeg = (row = {}, index = 0) => {
        const direction = this.caseManualNormalizeText(row.direction || '').toLowerCase();
        const token = this.caseManualNormalizeText(row.token || row.tokenSymbol || '');
        const isInputLeg = direction === 'outgoing' && (!sentToken || token.toUpperCase() === sentToken.toUpperCase());
        const isOutputLeg = direction === 'incoming' && (!receivedToken || token.toUpperCase() === receivedToken.toUpperCase());
        const rawAmount = row.amount ?? row.amountText ?? row.valueText ?? '';
        const amountText = this.caseManualFormatHopInspectorAmountOnly(rawAmount, token) || this.caseManualNormalizeText(row.amountText || row.valueText || row.amount || '');
        const amountParts = buildAmountParts(amountText, token);
        const fromAddress = this.caseManualNormalizeText(row.fromAddress || row.from || '');
        const toAddress = this.caseManualNormalizeText(row.toAddress || row.to || '');
        return {
          key: `swap-leg-${index}-${row.key || [direction, token, fromAddress, toAddress].join('-')}`,
          row: [],
          index: 1000 + index,
          hash: txHash,
          shortHash: txHash ? this.caseManualShorten(txHash, 10, 8) : 'Swap tx',
          pathLabel: '',
          fromHash: '',
          isDeposit: false,
          isNew: false,
          isBranch: false,
          isPathEnding: false,
          isUnknownService: false,
          isSwapLeg: true,
          isSwapInputLeg: isInputLeg,
          isSwapOutputLeg: isOutputLeg,
          amountText: [amountText, token].filter(Boolean).join(' '),
          primaryAmount: amountParts.amount,
          tokenSymbol: amountParts.token,
          dateLabel: this.caseManualFormatHopInspectorCardDateLabel(row.dateText) || row.dateText || 'Same transaction',
          routeText: [fromAddress ? this.caseManualShorten(fromAddress, 8, 6) : 'Pending from', toAddress ? this.caseManualShorten(toAddress, 8, 6) : 'Pending to'].join(' → '),
          badges: [
            isInputLeg ? 'swap input selected' : '',
            isOutputLeg ? 'solved swap output' : '',
            row.kindLabel || row.kind || '',
            row.contractAddress ? `contract ${this.caseManualShorten(row.contractAddress, 8, 6)}` : ''
          ].filter(Boolean),
          directionClass: isInputLeg ? 'is-outgoing' : (isOutputLeg ? 'is-incoming' : 'is-neutral'),
          directionSymbol: isInputLeg ? '⇢' : (isOutputLeg ? '⇠' : '•'),
          directionLabel: isInputLeg ? 'Swap input / sent side' : (isOutputLeg ? 'Solved swap output / received side' : 'Other same-hash transfer part'),
          isBtcLike: false,
          btcInputs: [],
          btcOutputs: [],
          title: isInputLeg ? 'Swap input selected' : (isOutputLeg ? 'Solved swap output' : 'Other same-hash transfer part'),
          details: [
            { label: 'From', value: fromAddress },
            { label: 'To', value: toAddress },
            { label: 'Role', value: isInputLeg ? 'Funds sent into the swap' : (isOutputLeg ? 'Funds returned from the swap as the new token' : 'Related same-hash transfer') },
            { label: 'Initiating address', value: initiatingAddress },
            { label: 'Swap contract/router', value: swapContract },
            { label: 'Details', value: this.caseManualNormalizeText(row.detailText || '') }
          ].filter(item => this.caseManualNormalizeText(item.value))
        };
      };
      const builtRows = rows.length ? rows.slice(0, 12).map(makeLeg) : [];
      if (builtRows.length) return builtRows;
      const sentParts = buildAmountParts(swap.sent?.amount, sentToken);
      const receivedParts = buildAmountParts(swap.received?.amount, receivedToken);
      return [
        {
          key: 'swap-leg-summary-input', row: [], index: 1000, hash: txHash, shortHash: txHash ? this.caseManualShorten(txHash, 10, 8) : 'Swap tx', pathLabel: '', fromHash: '', isDeposit: false, isNew: false, isBranch: false, isPathEnding: false, isUnknownService: false, isSwapLeg: true, isSwapInputLeg: true, isSwapOutputLeg: false, amountText: [sentParts.amount, sentParts.token].filter(Boolean).join(' '), primaryAmount: sentParts.amount, tokenSymbol: sentParts.token, dateLabel: 'Swap input', routeText: [initiatingAddress ? this.caseManualShorten(initiatingAddress, 8, 6) : 'initiator', swapContract ? this.caseManualShorten(swapContract, 8, 6) : 'swap contract'].join(' → '), badges: ['swap input selected'], directionClass: 'is-outgoing', directionSymbol: '⇢', directionLabel: 'Swap input / sent side', isBtcLike: false, btcInputs: [], btcOutputs: [], title: 'Swap input selected', details: []
        },
        {
          key: 'swap-leg-summary-output', row: [], index: 1001, hash: txHash, shortHash: txHash ? this.caseManualShorten(txHash, 10, 8) : 'Swap tx', pathLabel: '', fromHash: '', isDeposit: false, isNew: false, isBranch: false, isPathEnding: false, isUnknownService: false, isSwapLeg: true, isSwapInputLeg: false, isSwapOutputLeg: true, amountText: [receivedParts.amount, receivedParts.token].filter(Boolean).join(' '), primaryAmount: receivedParts.amount, tokenSymbol: receivedParts.token, dateLabel: 'Swap output', routeText: [swapContract ? this.caseManualShorten(swapContract, 8, 6) : 'swap contract', initiatingAddress ? this.caseManualShorten(initiatingAddress, 8, 6) : 'initiator'].join(' → '), badges: ['solved swap output'], directionClass: 'is-incoming', directionSymbol: '⇠', directionLabel: 'Solved swap output / received side', isBtcLike: false, btcInputs: [], btcOutputs: [], title: 'Solved swap output', details: []
        }
      ];
    },

    buildCaseManualStartHopInspectorPreviewItems(rows = []) {
      const normalizedRows = this.normalizeCaseManualStartHopOutputRows(rows);
      const previewSnapshot = this.caseManualStartHopInspectorPreviewSnapshot || this.caseManualStartHopInspectorSnapshot || {};
      const traceInspectorRowMap = this.buildCaseManualStartTraceInspectorRowMap(previewSnapshot);
      const rawDataRows = (normalizedRows.length && this.caseManualIsHeaderRow(normalizedRows[0]) ? normalizedRows.slice(1) : normalizedRows)
        .filter(row => this.caseManualRowHasEntryData(row));
      const dataEntries = [];
      let activePreviewEntry = null;
      rawDataRows.forEach((row, rowIndex) => {
        const entryRow = this.caseManualEnsureRowWidth(row || [], Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
        const entryHash = this.caseManualNormalizeText(entryRow[CASE_MANUAL_START_COL.TX_HASH]);
        if (entryHash || !activePreviewEntry) {
          activePreviewEntry = { row: entryRow, rows: [entryRow], index: rowIndex };
          dataEntries.push(activePreviewEntry);
        } else {
          activePreviewEntry.rows.push(entryRow);
        }
      });
      const collectEntryColumnValues = (entryRows = [], columnIndex = 0) => {
        const seen = new Set();
        return (Array.isArray(entryRows) ? entryRows : [])
          .map(row => this.caseManualNormalizeText(row?.[columnIndex]))
          .filter((value) => {
            if (!value) return false;
            const key = value.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
      };
      const currentHashes = this.caseManualStartCurrentHashSet();
      const candidate = this.caseManualStartHopInspectorCandidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseInvestigationVisualizerSelectedCandidate || {};
      const candidateHash = this.caseManualNormalizeText(candidate.hash || candidate.txHash || candidate.transactionHash).toLowerCase();
      const candidateAddress = this.caseManualNormalizeText(this.caseManualStartCandidateEndpointAddress(candidate)).toLowerCase();
      const candidateRow = this.caseManualEnsureRowWidth(candidate.rawRow || candidate.row || [], CASE_MANUAL_START_COLUMN_COUNT);
      const candidateRowHash = this.caseManualNormalizeText(candidateRow[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase();
      const items = [];
      const seenKeys = new Set();
      const pushItemFromRow = (row, index, options = {}) => {
        const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
        const entryRows = Array.isArray(options.entryRows) && options.entryRows.length
          ? options.entryRows.map(entryRow => this.caseManualEnsureRowWidth(entryRow || [], Math.max(CASE_MANUAL_START_COLUMN_COUNT, entryRow?.length || 0)))
          : [normalized];
        const entryFromAddresses = collectEntryColumnValues(entryRows, CASE_MANUAL_START_COL.FROM_ADDRESS);
        const entryToAddresses = collectEntryColumnValues(entryRows, CASE_MANUAL_START_COL.TO_ADDRESS);
        const hash = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TX_HASH]);
        const hashKey = hash.toLowerCase();
        if (hashKey && !this.caseManualIsLikelyTransactionHash(hash)) return;
        const traceInspectorRows = hashKey ? (traceInspectorRowMap.get(hashKey) || []) : [];
        const traceInspectorRow = this.pickCaseManualStartTraceInspectorRowForPreview(traceInspectorRows, normalized, normalized[CASE_MANUAL_START_COL.TOKEN], candidateAddress);
        if (!hashKey && !options.forceCandidate) return;
        const itemKey = `${hashKey || 'candidate'}-${index}-${options.forceCandidate ? 'candidate' : 'row'}`;
        if (seenKeys.has(itemKey)) return;
        seenKeys.add(itemKey);
        const fromHash = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FROM_HASH]);
        const pathLabel = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.PATH_LABEL]);
        const amount = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.AMOUNT]);
        const token = this.caseManualNormalizeText(traceInspectorRow?.token || traceInspectorRow?.tokenSymbol || traceInspectorRow?.assetSymbol || traceInspectorRow?.symbol || traceInspectorRow?.raw?.tokenSymbol || normalized[CASE_MANUAL_START_COL.TOKEN]) || this.caseManualTokenFromFundsFollowed(normalized[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]) || '';
        const fundsFollowed = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
        const continuesAs = this.caseManualNormalizeContinuesAsText(normalized[CASE_MANUAL_START_COL.CONTINUES_AS]);
        const attribution = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.ATTRIBUTION]);
        const notes = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.SYSTEM_NOTES] || normalized[CASE_MANUAL_START_COL.CASE_NOTES] || normalized[CASE_MANUAL_START_COL.ANALYST_NOTES]);
        const fromAddress = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        const toAddress = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.TO_ADDRESS]);
        const chainName = this.caseManualNormalizeText(normalized[CASE_MANUAL_START_COL.CHAIN_NAME]);
        const isDeposit = options.forceCandidate || (candidateHash && hashKey === candidateHash) || (candidateRowHash && hashKey === candidateRowHash);
        const isNew = Boolean(hashKey && !currentHashes.has(hashKey));
        const isBranch = Boolean(fromHash && candidateHash && fromHash.toLowerCase() === candidateHash && !isDeposit);
        const fromMatches = candidateAddress && fromAddress.toLowerCase().includes(candidateAddress);
        const toMatches = candidateAddress && toAddress.toLowerCase().includes(candidateAddress);
        const inspectorDirection = this.caseManualNormalizeText(traceInspectorRow?.direction).toLowerCase();
        const directionKey = ['incoming', 'outgoing', 'self'].includes(inspectorDirection)
          ? inspectorDirection
          : (fromMatches && !toMatches ? 'outgoing' : (toMatches && !fromMatches ? 'incoming' : (isBranch ? 'outgoing' : (isDeposit ? 'incoming' : 'neutral'))));
        const directionLabel = directionKey === 'outgoing'
          ? 'Outgoing from selected ending'
          : (directionKey === 'incoming' ? 'Incoming to selected ending' : (directionKey === 'self' ? 'Self-transfer / internal movement' : 'Related transaction'));
        const isPathEnding = /path\s*ends/iu.test(continuesAs);
        const isUnknownService = this.caseManualStartCandidateHasUnknownServiceEndpoint({
          row: normalized,
          rawRow: normalized,
          continuesAs,
          attribution,
          toAttributionLabel: attribution
        });
        const traceInspectorAmount = this.formatCaseManualStartTraceInspectorAmountForPreview(traceInspectorRow, token);
        const rowAmountText = this.caseManualFormatHopInspectorAmountWithToken(amount, token);
        const fundsFollowedDisplay = this.caseManualFormatHopInspectorAmountWithToken(fundsFollowed, token);
        const traceChainText = this.caseManualNormalizeText(traceInspectorRow?.chain || traceInspectorRow?.chainName || traceInspectorRow?.network || traceInspectorRow?.assetSymbol || traceInspectorRow?.token || traceInspectorRow?.kindLabel || traceInspectorRow?.transactionKind || '');
        const isBtcLike = /\b(bitcoin|btc)\b/iu.test([token, fundsFollowed, chainName, notes, traceChainText].filter(Boolean).join(' ')) || Number(traceInspectorRow?.btcInputCount || 0) > 0 || Number(traceInspectorRow?.btcOutputCount || 0) > 0;
        const dateLabel = this.caseManualFormatHopInspectorCardDateLabel(traceInspectorRow?.dateText || normalized[CASE_MANUAL_START_COL.DATE]) || this.caseManualFormatVisualDateLabel(normalized[CASE_MANUAL_START_COL.DATE]) || traceInspectorRow?.dateText || normalized[CASE_MANUAL_START_COL.DATE] || 'Date pending';
        const actualAmountText = traceInspectorAmount || rowAmountText || [amount, token].filter(Boolean).join(' ');
        const amountSource = actualAmountText || fundsFollowedDisplay || fundsFollowed || 'Amount pending';
        const amountParts = this.splitCaseManualStartPreviewAmountToken(amountSource, token);
        const routeText = [fromAddress ? this.caseManualShorten(fromAddress, 8, 6) : 'Pending from', toAddress ? this.caseManualShorten(toAddress, 8, 6) : 'Pending to'].join(' → ');
        const continuesBadge = (() => {
          if (!continuesAs || isPathEnding) return '';
          const childHashes = this.caseManualExtractHashTokensFromContinuesAs(continuesAs);
          if (childHashes.length > 1) return `continues as ${childHashes.length} branches`;
          if (childHashes.length === 1) return `continues as ${this.caseManualShorten(childHashes[0], 8, 6)}`;
          return this.caseManualShorten(continuesAs, 28, 8);
        })();
        const badges = [
          isDeposit ? '★ selected' : '',
          isBranch ? 'branch' : '',
          isPathEnding ? 'unresolved' : '',
          isUnknownService ? 'unknown service' : '',
          isBtcLike && (entryFromAddresses.length > 1 || Number(traceInspectorRow?.btcInputCount || 0) > 1) ? `${Math.max(entryFromAddresses.length, Number(traceInspectorRow?.btcInputCount || 0))} inputs` : '',
          isBtcLike && (entryToAddresses.length > 1 || Number(traceInspectorRow?.btcOutputCount || 0) > 1) ? `${Math.max(entryToAddresses.length, Number(traceInspectorRow?.btcOutputCount || 0))} outputs` : '',
          attribution ? this.caseManualShorten(attribution, 16, 8) : '',
          continuesBadge
        ].filter(Boolean);
        const btcAmount = traceInspectorAmount || actualAmountText || fundsFollowedDisplay || fundsFollowed;
        const detailPairs = [
          ['From', fromAddress],
          ['To', toAddress],
          ['Date', dateLabel],
          ['Transaction amount', traceInspectorAmount && traceInspectorAmount !== actualAmountText ? traceInspectorAmount : ''],
          ['Amount', actualAmountText],
          ['Funds followed', fundsFollowedDisplay || fundsFollowed],
          ['Attribution', attribution],
          ['Continues as', continuesAs],
          ['Notes', notes]
        ]
          .map(([label, value]) => ({ label, value: this.caseManualNormalizeText(value) }))
          .filter(item => item.value);
        items.push({
          key: itemKey,
          row: normalized,
          index,
          hash,
          shortHash: hash ? this.caseManualShorten(hash, 10, 8) : 'Selected ending',
          pathLabel,
          fromHash,
          isDeposit,
          isNew,
          isBranch,
          isPathEnding,
          isUnknownService,
          amountText: amountSource || 'Amount pending',
          primaryAmount: amountParts.amount,
          tokenSymbol: amountParts.token,
          dateLabel,
          routeText,
          badges,
          directionClass: `is-${directionKey}`,
          directionSymbol: directionKey === 'neutral' ? '•' : '▶',
          directionLabel,
          isBtcLike,
          btcInputs: traceInspectorRow && isBtcLike
            ? this.buildCaseManualStartTraceInspectorBtcEndpointList(traceInspectorRow, 'input', itemKey)
            : this.buildCaseManualStartPreviewBtcEndpointList(entryFromAddresses.join('\n') || fromAddress, btcAmount, `${itemKey}-input`),
          btcOutputs: traceInspectorRow && isBtcLike
            ? this.buildCaseManualStartTraceInspectorBtcEndpointList(traceInspectorRow, 'output', itemKey)
            : this.buildCaseManualStartPreviewBtcEndpointList(entryToAddresses.join('\n') || toAddress, btcAmount, `${itemKey}-output`),
          title: isDeposit ? 'Transaction of interest' : (isBranch ? 'Branch / candidate exit' : (isNew ? 'Candidate next hop' : 'Existing row')),
          details: detailPairs
        });
      };
      if (candidateHash && !rawDataRows.some(row => this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase() === candidateHash)) {
        const row = candidateRow.slice();
        if (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TX_HASH])) row[CASE_MANUAL_START_COL.TX_HASH] = candidate.hash || candidate.txHash || candidate.transactionHash || '';
        if (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.PATH_LABEL])) row[CASE_MANUAL_START_COL.PATH_LABEL] = candidate.pathLabel || '';
        if (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FROM_ADDRESS])) row[CASE_MANUAL_START_COL.FROM_ADDRESS] = candidate.fromAddress || '';
        if (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.TO_ADDRESS])) row[CASE_MANUAL_START_COL.TO_ADDRESS] = candidate.toAddress || '';
        if (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED])) row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = candidate.fundsFollowed || '';
        if (!this.caseManualNormalizeText(row[CASE_MANUAL_START_COL.CONTINUES_AS])) row[CASE_MANUAL_START_COL.CONTINUES_AS] = candidate.continuesAs || 'Path ends.';
        pushItemFromRow(row, -1, { forceCandidate: true });
      }
      dataEntries.forEach((entry) => pushItemFromRow(entry.row, entry.index, { entryRows: entry.rows }));
      if (/swap/iu.test(this.caseManualNormalizeText(this.caseManualStartHopInspectorPreviewAction || previewSnapshot?.integrationAction || ''))) {
        this.buildCaseManualStartSwapPreviewItems(previewSnapshot).forEach((item) => {
          if (!seenKeys.has(item.key)) {
            seenKeys.add(item.key);
            items.push(item);
          }
        });
      }
      return items.sort((a, b) => {
        if (a.isDeposit && !b.isDeposit) return -1;
        if (!a.isDeposit && b.isDeposit) return 1;
        if (a.isBranch && !b.isBranch) return -1;
        if (!a.isBranch && b.isBranch) return 1;
        return a.index - b.index;
      });
    },

    caseManualStartHopInspectorPreviewItemClass(item = {}) {
      return [
        'case-manual-start-hop-inspector__preview-item',
        item.isDeposit ? 'is-deposit' : '',
        item.isNew ? 'is-new' : '',
        item.isBranch ? 'is-branch' : '',
        item.isPathEnding ? 'is-path-ending' : '',
        item.isUnknownService ? 'is-unknown-service' : '',
        item.isSwapLeg ? 'is-swap-leg' : '',
        item.isSwapInputLeg ? 'is-swap-input-leg' : '',
        item.isSwapOutputLeg ? 'is-swap-output-leg' : '',
        item.directionClass || '',
        item.isBtcLike ? 'is-btc-like' : ''
      ].filter(Boolean);
    },

    caseManualPreviewActionLooksTraceThrough(action = '') {
      const text = this.caseManualNormalizeText(action);
      return /trace|through|manual-trace/iu.test(text || '');
    },

    caseManualPreviewRowsContainTraceChild(rows = [], activeCandidate = null, snapshot = null) {
      const candidate = activeCandidate || this.caseManualStartHopInspectorPreviewCandidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || null;
      const candidateRow = Array.isArray(candidate?.rawRow) ? candidate.rawRow : (Array.isArray(candidate?.row) ? candidate.row : []);
      const parentHash = this.caseManualNormalizeText(candidate?.hash || candidate?.txHash || candidate?.transactionHash || candidateRow[CASE_MANUAL_START_COL.TX_HASH]);
      const parentKey = parentHash && this.caseManualIsLikelyTransactionHash(parentHash) ? parentHash.toLowerCase() : '';
      const parentEndpointAddress = this.caseManualNormalizeAddressKey(
        this.caseManualStartCandidateEndpointAddress(candidate || {})
      );
      const reverse = this.caseManualStartCandidateIsReverse(candidate || {});
      const traceExitHashes = this.caseManualSnapshotTraceExitHashSet(snapshot || {}, null);
      const usableRows = (Array.isArray(rows) ? rows : []).filter((row) => {
        if (!this.caseManualManualSolveRowShouldRetain(row, { allowBtcContinuationRows: true })) return false;
        if (this.caseManualRowLooksLikeTool88FakeStatusRow(row)) return false;
        const childHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]);
        return this.caseManualIsLikelyTransactionHash(childHash);
      });
      if (!parentKey) {
        return Boolean(traceExitHashes.size && usableRows.some((row) => traceExitHashes.has(this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]).toLowerCase())));
      }
      return usableRows.some(row => {
        const childHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.TX_HASH]);
        const childKey = childHash.toLowerCase();
        if (childKey === parentKey) return false;
        const fromHash = this.caseManualNormalizeText(row?.[CASE_MANUAL_START_COL.FROM_HASH]).toLowerCase();
        const fromHashMatchesParent = Boolean(parentKey && fromHash && this.caseManualTransactionHashesMatch(fromHash, parentKey));
        if (fromHashMatchesParent) return true;
        if (parentKey && fromHash) {
          // Explicit From Hash is occurrence identity. If it points at a sibling,
          // do not let same-address matching claim this as the selected hop child.
          return false;
        }
        const childConnectionAddress = this.caseManualNormalizeAddressKey(
          row?.[reverse ? CASE_MANUAL_START_COL.TO_ADDRESS : CASE_MANUAL_START_COL.FROM_ADDRESS]
        );
        const connectionMatchesEndpoint = Boolean(
          parentEndpointAddress
            && childConnectionAddress
            && childConnectionAddress === parentEndpointAddress
        );
        if (connectionMatchesEndpoint) return true;
        if (!traceExitHashes.has(childKey)) return false;
        return !parentKey && !parentEndpointAddress;
      });
    },

    openCaseManualStartHopInspectorPreview(rows = [], snapshot = {}, options = {}) {
      const previewCandidate = options.candidate || options.activeCandidate || this.caseManualStartHopInspectorPreviewCandidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || null;
      const dormantResult = this.caseManualExtractDormantResultFromSnapshot(snapshot);
      let normalizedRows = this.normalizeCaseManualStartHopOutputRows(rows)
        .filter(row => this.caseManualRowHasEntryData(row))
        .filter(row => !this.caseManualRowLooksLikeTool88FakeStatusRow(row));
      const previewAction = this.caseManualNormalizeText(options.action || snapshot?.integrationAction || 'trace-through') || 'trace-through';
      normalizedRows = this.caseManualFilterRowsToSelectedHop(normalizedRows, snapshot || {}, previewCandidate, {
        includeParent: true,
        includeChildren: true,
        fallbackParent: true,
        action: previewAction,
        force: this.caseManualPreviewActionLooksTraceThrough(previewAction) || Boolean(dormantResult)
      });
      if (dormantResult) normalizedRows = this.caseManualBuildDormantRowsForApply(normalizedRows, snapshot, previewCandidate);
      const previewLooksTraceThrough = this.caseManualPreviewActionLooksTraceThrough(previewAction);
      const previewHasTraceChild = this.caseManualPreviewRowsContainTraceChild(normalizedRows, previewCandidate, snapshot);
      const previewHasPartialDormantChild = this.caseManualRowsContainDormantTraceChild(normalizedRows, dormantResult, previewCandidate, snapshot);
      if (previewLooksTraceThrough && ((!dormantResult && !previewHasTraceChild) || (this.caseManualDormantResultIsPartial(dormantResult) && !previewHasPartialDormantChild))) {
        const message = this.caseManualDormantResultIsPartial(dormantResult)
          ? 'Trace preview detected dormant funds plus a continuation, but no valid child transaction row was available for the continuation. Case Buddy did not open or apply the preview.'
          : 'Trace preview did not return a valid new child transaction for the selected ending. Case Buddy did not open or apply the preview, so Tool 88 status rows cannot enter Main Data.';
        this.caseManualStartHopInspectorPreviewRows = [];
        this.caseManualStartHopInspectorPreviewSnapshot = snapshot || this.caseManualStartHopInspectorSnapshot || null;
        this.caseManualStartHopInspectorPreviewAction = previewAction;
        this.caseManualStartHopInspectorPreviewApplied = false;
        this.caseManualStartHopInspectorPreviewOpen = false;
        this.caseManualStartHopInspectorPreviewStatus = message;
        this.caseManualStartHopInspectorStatus = message;
        this.caseManualStartHopInspectorOutputRows = [];
        if (typeof this.showCaseToast === 'function') this.showCaseToast(message);
        return false;
      }
      this.caseManualStartHopInspectorPreviewRows = normalizedRows;
      if (normalizedRows.length) this.caseManualStartHopInspectorOutputRows = normalizedRows;
      this.caseManualStartHopInspectorPreviewSnapshot = snapshot || this.caseManualStartHopInspectorSnapshot || null;
      this.caseManualStartHopInspectorPreviewCandidate = previewCandidate;
      this.caseManualStartHopInspectorPreviewAction = previewAction;
      this.caseManualStartHopInspectorPreviewApplied = false;
      this.caseManualStartHopInspectorPreviewServiceOverride = Boolean(
        options.serviceOverride ||
        this.caseManualStartHopInspectorSnapshotHasManualServiceOverride(snapshot || {}) ||
        this.caseManualStartHopInspectorLastTraceServiceOverride
      );
      this.caseManualStartHopInspectorPreviewOpen = true;
      const actionLabel = this.caseManualStartHopInspectorActionLabel(this.caseManualStartHopInspectorPreviewAction);
      const defaultMessage = dormantResult
        ? this.caseManualStartHopInspectorPreviewBody
        : (this.caseManualStartHopInspectorPreviewServiceOverride
          ? `${actionLabel} is staged after bypassing the Unknown service / hot-wallet assessor for this one run. Review the provider rows, then acknowledge and apply or return to the hop controls.`
          : `${actionLabel} is staged. Review the provider rows, then confirm apply or return to the hop controls.`);
      this.caseManualStartHopInspectorPreviewStatus = options.message || defaultMessage;
      this.caseManualStartHopInspectorStatus = this.caseManualStartHopInspectorPreviewStatus;
      this.$nextTick(() => this.scrollCaseManualStartHopInspectorPreviewToDeposit({ smooth: false }));
    },

    closeCaseManualStartHopInspectorPreview() {
      this.caseManualStartHopInspectorPreviewOpen = false;
      this.caseManualStartHopInspectorPreviewStatus = '';
      this.caseManualStartHopInspectorPreviewServiceOverride = false;
      this.caseManualStartHopInspectorPreviewCandidate = null;
    },

    scrollCaseManualStartHopInspectorPreviewToDeposit(options = {}) {
      const panel = this.$refs.caseManualStartHopInspectorPanel;
      const root = Array.isArray(panel) ? panel[0] : panel;
      if (!root || typeof root.querySelector !== 'function') return;
      const target = root.querySelector('[data-manual-hop-preview-deposit="true"]') || root.querySelector('.case-manual-start-hop-inspector__preview-item');
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'center', behavior: options.smooth === false ? 'auto' : 'smooth' });
      }
    },

    async confirmCaseManualStartHopInspectorPreviewApply() {
      if ((!this.caseManualStartHopInspectorPreviewHasRows && !this.caseManualStartHopInspectorPreviewIsDormant) || this.caseManualStartHopInspectorRunning) return null;
      const previewAction = this.caseManualStartHopInspectorPreviewAction || this.caseManualStartHopInspectorPreviewSnapshot?.integrationAction || '';
      const actionLooksTraceThrough = this.caseManualPreviewActionLooksTraceThrough(previewAction);
      const previewSnapshot = this.caseManualStartHopInspectorPreviewSnapshot || {};
      const previewCandidate = this.caseManualStartHopInspectorPreviewCandidate || this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || null;
      const previewDormantResult = this.caseManualExtractDormantResultFromSnapshot(previewSnapshot);
      const previewHasTraceChild = this.caseManualPreviewRowsContainTraceChild(this.caseManualStartHopInspectorPreviewRows, previewCandidate, previewSnapshot);
      const previewHasPartialDormantChild = this.caseManualRowsContainDormantTraceChild(this.caseManualStartHopInspectorPreviewRows, previewDormantResult, previewCandidate, previewSnapshot);
      if (actionLooksTraceThrough && ((!this.caseManualStartHopInspectorPreviewIsDormant && !previewHasTraceChild) || (this.caseManualDormantResultIsPartial(previewDormantResult) && !previewHasPartialDormantChild))) {
        const message = this.caseManualDormantResultIsPartial(previewDormantResult)
          ? 'Trace preview has a dormant remainder plus continuation, but the continuation row is missing. Nothing was applied; rerun Trace through after Tool 88 finishes loading the selected address.'
          : 'Trace preview did not return a valid new child transaction for the selected ending. Nothing was applied; rerun Trace through after Tool 88 finishes loading the selected address.';
        this.caseManualStartHopInspectorPreviewStatus = message;
        this.caseManualStartHopInspectorStatus = message;
        if (typeof this.showCaseToast === 'function') this.showCaseToast(message);
        return null;
      }
      const result = await this.applyCaseManualStartHopInspectorOutputRows({
        rows: this.caseManualStartHopInspectorPreviewRows,
        snapshot: this.caseManualStartHopInspectorPreviewSnapshot || this.caseManualStartHopInspectorSnapshot || {},
        fromPreview: true,
        action: previewAction,
        candidate: previewCandidate
      });
      if (result) {
        this.caseManualStartHopInspectorPreviewApplied = true;
        this.caseManualStartHopInspectorPreviewOpen = false;
      }
      return result;
    },

    handleCaseManualStartHopInspectorSnapshot(eventName = 'case-buddy-status', payload = {}) {
      const snapshot = payload?.detail || payload || {};
      const previousSnapshot = this.caseManualStartHopInspectorSnapshot || {};
      if (snapshot && typeof snapshot === 'object') {
        this.caseManualStartHopInspectorSnapshot = snapshot;
        const snapshotCandidate = this.caseManualStartHopInspectorPreviewCandidate || this.caseManualStartHopInspectorCandidate || this.caseManualStartHopInspectorSelectedCandidate || null;
        const activeKey = this.buildCaseManualStartHopInspectorCandidateKey(snapshotCandidate);
        if (activeKey && !this.caseManualStartHopInspectorLoadedCandidateKey) this.caseManualStartHopInspectorLoadedCandidateKey = activeKey;
        let rows = this.extractCaseManualStartSnapshotRows(snapshot, { candidate: snapshotCandidate });
        const snapshotHasServiceOverride = this.caseManualStartHopInspectorSnapshotHasManualServiceOverride(snapshot);
        const snapshotCandidateHasMeaningfulEndpoint = snapshotCandidate?.caseManualStartCandidate
          && this.caseManualStartCandidateHasMeaningfulEndpointAttribution(snapshotCandidate);
        const snapshotLooksUnknownService = !snapshotCandidateHasMeaningfulEndpoint && (this.caseManualStartHopInspectorSnapshotLooksUnknownServicePathEndingForCandidate(snapshot, snapshotCandidate)
          || this.caseManualStartHopInspectorRowsContainUnknownServicePathEndingEvidenceForCandidate(rows, snapshotCandidate));
        const previousLookedLikeUnknownService = !snapshotCandidateHasMeaningfulEndpoint && snapshotCandidate?.caseManualStartCandidate
          && this.caseManualStartHopInspectorSnapshotLooksUnknownServicePathEndingForCandidate(previousSnapshot, snapshotCandidate);
        if (rows.length && (snapshotLooksUnknownService || previousLookedLikeUnknownService) && !snapshotHasServiceOverride) {
          rows = rows.map(row => {
            const normalized = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
            normalized[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
              normalized[CASE_MANUAL_START_COL.CASE_NOTES],
              'Tool 88: Unknown service / hot-wallet-like path ending evidence; review before tracing through again.'
            );
            return normalized;
          });
        }
        if (rows.length) {
          const incomingCount = this.caseManualCountTransactionRows(rows);
          const existingCount = this.caseManualCountTransactionRows(this.caseManualStartHopInspectorOutputRows);
          const actionText = [eventName, snapshot?.integrationAction, snapshot?.status].map(value => this.caseManualNormalizeText(value)).join(' ');
          const snapshotIsTraceResult = this.caseManualSnapshotLooksTraceThroughResult(snapshot, eventName);
          const activeCandidateForRows = snapshotCandidate;
          const rowsContainTraceChild = snapshotIsTraceResult && this.caseManualPreviewRowsContainTraceChild(rows, activeCandidateForRows, snapshot);
          const dormant = this.caseManualExtractDormantResultFromSnapshot(snapshot);
          const rowsContainPartialDormantChild = this.caseManualRowsContainDormantTraceChild(rows, dormant, activeCandidateForRows, snapshot);
          const smallerReplacementIsIntentional = /flow-complete|path-ending|manual-path-ending|low-value|swap|bridge/iu.test(actionText)
            || rowsContainTraceChild
            || (this.caseManualDormantResultIsPartial(dormant) && rowsContainPartialDormantChild);
          if (!this.caseManualStartHopInspectorOutputRows.length || incomingCount >= existingCount || smallerReplacementIsIntentional) {
            this.caseManualStartHopInspectorOutputRows = rows;
          }
        }
        const tsv = snapshot.outputTsv || snapshot.result?.outputTsv || '';
        if (tsv) this.caseManualStartHopInspectorOutputTsv = tsv;
        if (snapshotHasServiceOverride) {
          this.caseManualStartHopInspectorStatus = snapshot.message || 'Trace-through preview ran after bypassing the Unknown service / hot-wallet assessor for this one acknowledged run.';
        } else if (snapshotLooksUnknownService || previousLookedLikeUnknownService) {
          this.caseManualStartHopInspectorStatus = 'Tool 88 marked this as an Unknown service/hot-wallet-like path ending. Click the Recommendation card to finalize that ending in the visual and Main Data, or use the side tray for Trace anyway preview if you want to bypass the hot-wallet assessor for one reviewable run.';
          const pendingNode = this.findCaseManualStartPathEndingNode?.(snapshotCandidate);
          if (pendingNode?.id) {
            this.caseInvestigationVisualizerSelectedNodeId = pendingNode.id;
            this.caseInvestigationVisualizerSelectedNodeIds = [pendingNode.id];
            if (snapshotCandidate?.caseManualStartCandidate) this.caseInvestigationVisualizerSelectedCandidate = snapshotCandidate;
          }
        } else {
          this.caseManualStartHopInspectorStatus = snapshot.message || snapshot.status || this.caseManualStartHopInspectorStatus;
        }
      }
      this.caseManualStartHopInspectorLastEvent = eventName;
    },

    getCaseManualStartHopInspectorProgressRuntime() {
      let runtime = CASE_MANUAL_SOLVE_PROGRESS_RUNTIME.get(this);
      if (!runtime) {
        runtime = createCaseManualSolveProgressRuntime();
        CASE_MANUAL_SOLVE_PROGRESS_RUNTIME.set(this, runtime);
      }
      return runtime;
    },

    clearCaseManualStartHopInspectorProgressQueue(options = {}) {
      const runtime = CASE_MANUAL_SOLVE_PROGRESS_RUNTIME.get(this);
      if (!runtime) return;
      if (runtime.frame && typeof window !== 'undefined') {
        if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(runtime.frame);
        else if (typeof window.clearTimeout === 'function') window.clearTimeout(runtime.frame);
      }
      if (runtime.timer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(runtime.timer);
      runtime.frame = 0;
      runtime.timer = 0;
      if (options.discard !== true && runtime.pendingPayload) {
        const payload = runtime.pendingPayload;
        runtime.pendingPayload = null;
        this.handleCaseManualStartHopInspectorSnapshot('case-buddy-progress', payload);
        this.refreshCaseManualStartHopInspectorTraceProgress();
        runtime.lastFlushedAtMs = Date.now();
      } else {
        runtime.pendingPayload = null;
      }
      if (options.dispose === true) CASE_MANUAL_SOLVE_PROGRESS_RUNTIME.delete(this);
    },

    flushCaseManualStartHopInspectorProgressQueue() {
      const runtime = this.getCaseManualStartHopInspectorProgressRuntime();
      if (runtime.frame && typeof window !== 'undefined') {
        if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(runtime.frame);
        else if (typeof window.clearTimeout === 'function') window.clearTimeout(runtime.frame);
      }
      if (runtime.timer && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(runtime.timer);
      runtime.frame = 0;
      runtime.timer = 0;
      const payload = runtime.pendingPayload;
      runtime.pendingPayload = null;
      if (!payload) return;
      this.handleCaseManualStartHopInspectorSnapshot('case-buddy-progress', payload);
      this.refreshCaseManualStartHopInspectorTraceProgress();
      runtime.lastFlushedAtMs = Date.now();
    },

    queueCaseManualStartHopInspectorProgress(payload) {
      const runtime = this.getCaseManualStartHopInspectorProgressRuntime();
      runtime.pendingPayload = payload;
      if (runtime.frame || runtime.timer) return;
      const elapsed = Date.now() - Number(runtime.lastFlushedAtMs || 0);
      const delayMs = Math.max(0, CASE_MANUAL_SOLVE_PROGRESS_RECONCILE_MS - elapsed);
      const scheduleFrame = () => {
        runtime.timer = 0;
        const run = () => {
          runtime.frame = 0;
          this.flushCaseManualStartHopInspectorProgressQueue();
        };
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') runtime.frame = window.requestAnimationFrame(run);
        else if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') runtime.frame = window.setTimeout(run, 16);
        else run();
      };
      if (delayMs > 0 && typeof window !== 'undefined' && typeof window.setTimeout === 'function') runtime.timer = window.setTimeout(scheduleFrame, delayMs);
      else scheduleFrame();
    },

    handleCaseManualStartHopInspectorStatus(payload) {
      this.flushCaseManualStartHopInspectorProgressQueue();
      this.handleCaseManualStartHopInspectorSnapshot('case-buddy-status', payload);
    },

    handleCaseManualStartHopInspectorProgress(payload) {
      // Progress can arrive many times while provider rows are being fetched.
      // Reconcile at a readable 8-ish Hz instead of making the entire parent
      // component react to every event.
      this.queueCaseManualStartHopInspectorProgress(payload);
    },

    handleCaseManualStartHopInspectorResult(payload) {
      this.flushCaseManualStartHopInspectorProgressQueue();
      this.handleCaseManualStartHopInspectorSnapshot('case-buddy-result', payload);
    },

    handleCaseManualStartHopInspectorGuardrail(payload) {
      this.flushCaseManualStartHopInspectorProgressQueue();
      this.handleCaseManualStartHopInspectorSnapshot('case-buddy-guardrail', payload);
    },

    handleCaseManualStartHopInspectorFlowComplete(payload) {
      this.flushCaseManualStartHopInspectorProgressQueue();
      this.handleCaseManualStartHopInspectorSnapshot('case-buddy-flow-complete', payload);
    },

    async loadCaseManualStartHopInspectorCandidate(options = {}) {
      const candidate = options.candidate?.caseManualStartCandidate
        ? options.candidate
        : (this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection({ quiet: true }));
      if (!candidate?.caseManualStartCandidate) {
        this.caseManualStartHopInspectorStatus = 'No manual-start path ending is selected.';
        return null;
      }
      const candidateKey = this.buildCaseManualStartHopInspectorCandidateKey(candidate);
      if (options.expectedCandidateKey && candidateKey !== options.expectedCandidateKey) return null;
      if (!options.force && candidateKey && this.caseManualStartHopInspectorSnapshot && this.caseManualStartHopInspectorLoadedCandidateKey === candidateKey && this.caseManualStartSnapshotMatchesCandidate(this.caseManualStartHopInspectorSnapshot, candidate)) {
        return this.caseManualStartHopInspectorSnapshot;
      }
      this.caseManualStartHopInspectorCandidate = candidate;
      this.caseManualStartHopInspectorMounted = true;
      this.caseManualStartHopInspectorRunning = true;
      this.caseManualStartHopInspectorStopRequested = false;
      this.caseManualStartHopInspectorStatus = options.automatic
        ? 'Auto-running Tool 88 checks for this selected ending…'
        : 'Loading Tool 88 checks for this selected ending…';
      await this.$nextTick();
      try {
        const tool = this.getCaseManualStartHopInspectorTool();
        if (!tool || typeof tool.caseBuddyOpenSelectedHop !== 'function') throw new Error('Embedded Tool 88 is not ready yet.');
        const preferredSettings = this.syncCaseManualStartSettingsToEmbeddedTool88();
        if (typeof tool.caseBuddyClearStaleStopState === 'function') {
          tool.caseBuddyClearStaleStopState('Case Buddy Manual Solve opened a fresh selected hop.');
        }
        const snapshot = await tool.caseBuddyOpenSelectedHop(candidate, {
          rows: this.caseManualStartRowsForTool88(),
          includeRows: true,
          startInAdvancedView: false,
          resetCaseBuddyStopState: true,
          traceDirection: preferredSettings.traceDirection,
          hopLogicType: preferredSettings.hopLogicType,
          enableAdditionalServiceChecks: preferredSettings.enableAdditionalServiceChecks,
          enableDepositEntityHopExtension: preferredSettings.enableDepositEntityHopExtension,
          ...this.caseManualStartNativeDirectSpendToolOptions(preferredSettings)
        });
        this.handleCaseManualStartHopInspectorSnapshot('case-buddy-result', snapshot || {});
        if (candidateKey) this.caseManualStartHopInspectorLoadedCandidateKey = candidateKey;
        return snapshot;
      } catch (error) {
        this.caseManualStartHopInspectorStatus = `Tool 88 check failed: ${error?.message || 'unknown error'}.`;
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        return null;
      } finally {
        this.caseManualStartHopInspectorRunning = false;
      }
    },

    async runCaseManualStartHopInspectorRecommendation(options = {}) {
      if (this.caseManualStartHopInspectorRunning) return null;
      const actionCandidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection({ quiet: true }) || null;
      if (actionCandidate?.caseManualStartCandidate) this.caseManualStartHopInspectorCandidate = actionCandidate;
      const snapshot = await this.ensureCaseManualStartHopInspectorChecksReady({ candidate: actionCandidate, force: options.force === true });
      if (!snapshot) return null;
      const preRecommendedActionKey = this.resolveCaseManualStartHopInspectorRecommendedActionKey();
      if (preRecommendedActionKey === 'bridge') {
        return await this.runCaseManualStartHopInspectorToolAction('bridge', {
          apply: options.apply !== false,
          preview: true,
          force: true,
        });
      }
      this.caseManualStartHopInspectorRunning = true;
      this.caseManualStartHopInspectorStopRequested = false;
      const recommendedLooksTrace = this.caseManualStartSnapshotActionLooksTraceThrough(snapshot || {});
      const recommendedNeedsTraceRecovery = preRecommendedActionKey === 'trace-through' || (!preRecommendedActionKey && recommendedLooksTrace);
      this.caseManualStartHopInspectorStatus = options.apply === false
        ? 'Previewing Tool 88 recommended action for this hop…'
        : 'Using Tool 88 recommended action and applying it to the visual…';
      if (recommendedNeedsTraceRecovery) {
        this.startCaseManualStartHopInspectorTraceLoading('Fetching recommended trace preview…');
      }
      try {
        const stagedRowsBeforeTool = this.caseManualSelectFreshHopInspectorRows(snapshot || {}, this.caseManualStartHopInspectorOutputRows, actionCandidate);
        const addressPoisoningEvidence = this.caseManualStartAddressPoisoningEvidenceForCandidate(actionCandidate, { snapshot });
        if (preRecommendedActionKey === 'path-ending' && addressPoisoningEvidence?.isMatch) {
          const currentRow = this.findCurrentCaseManualStartMainRowForCandidate(actionCandidate);
          const sourceRows = stagedRowsBeforeTool.length
            ? stagedRowsBeforeTool
            : (Array.isArray(currentRow) && currentRow.length ? [currentRow] : this.buildCaseManualStartPathEndingFallbackRows(snapshot || {}));
          const sanitizedRows = sourceRows.map(row => this.caseManualAnnotateAddressPoisoningPathEndingRow(row, addressPoisoningEvidence, actionCandidate));
          this.caseManualStartHopInspectorOutputRows = sanitizedRows;
          if (options.apply !== false) {
            return await this.applyCaseManualStartHopInspectorOutputRows({
              rows: sanitizedRows,
              snapshot: snapshot || {},
              action: 'path-ending',
              candidate: actionCandidate
            });
          }
          this.caseManualStartHopInspectorStatus = `Case Buddy staged ${sanitizedRows.length} likely address-poisoning path-ending row${sanitizedRows.length === 1 ? '' : 's'}. Click Apply staged result to update the visual.`;
          return snapshot;
        }
        if (
          preRecommendedActionKey === 'path-ending'
            && stagedRowsBeforeTool.length
            && this.caseManualStartHopInspectorRowsContainUnknownServicePathEndingEvidenceForCandidate(stagedRowsBeforeTool, actionCandidate)
            && !this.caseManualStartHopInspectorSnapshotHasManualServiceOverride(snapshot || {})
        ) {
          const sanitizedRows = this.caseManualAnnotatePathEndingRowsWithAttribution(stagedRowsBeforeTool, snapshot || {});
          this.caseManualStartHopInspectorOutputRows = sanitizedRows;
          if (options.apply !== false) {
            return await this.applyCaseManualStartHopInspectorOutputRows({
              rows: sanitizedRows,
              snapshot: snapshot || {},
              action: 'path-ending',
              candidate: actionCandidate
            });
          }
          this.caseManualStartHopInspectorStatus = `Tool 88 staged ${sanitizedRows.length} Unknown service / hot-wallet path-ending row${sanitizedRows.length === 1 ? '' : 's'}. Click Apply staged result to update the visual.`;
          return snapshot;
        }
        const tool = this.getCaseManualStartHopInspectorTool();
        if (!tool) throw new Error('Embedded Tool 88 cannot run the recommended action.');
        this.syncCaseManualStartSettingsToEmbeddedTool88();
        let rawResult = null;
        if (preRecommendedActionKey === 'bridge' || preRecommendedActionKey === 'swap') {
          rawResult = await this.runCaseManualStartHopInspectorSpecialPreviewAction(tool, preRecommendedActionKey);
        } else {
          if (typeof tool.caseBuddyRunNextAction !== 'function') throw new Error('Embedded Tool 88 cannot run the recommended action.');
          const preferredSettings = this.caseManualStartReadTool88Settings();
          rawResult = await tool.caseBuddyRunNextAction({
            includeRows: true,
            forceCurrentRecommendation: true,
            resetCaseBuddyStopState: true,
            allowUnverifiedTraceOutput: true,
            acceptUnverifiedTraceOutput: true,
            pauseOnUnverifiedOutput: false,
            allowCaseBuddyRecoveredTraceOutput: true,
            ...this.caseManualStartNativeDirectSpendToolOptions(preferredSettings),
          });
        }
        let result = this.attachCaseManualStartTraceInspectorRows(rawResult || {}, tool);
        if (recommendedNeedsTraceRecovery) {
          const recoveredTrace = await this.recoverCaseManualStartTraceSnapshotFromTool(tool, result || {}, actionCandidate, { action: 'trace-through' });
          result = recoveredTrace.snapshot || result;
          if (recoveredTrace.recovered) {
            result = {
              ...(result || {}),
              caseBuddyManualTraceRowsRecovered: true,
              message: result?.message || 'Case Buddy recovered Tool 88 trace-through rows after the provider result settled.'
            };
          }
        }
        this.handleCaseManualStartHopInspectorSnapshot('case-buddy-result', result || {});
        this.refreshCaseManualStartHopInspectorTraceProgress();
        if (this.caseManualStartSpecialActionDidNotApply(result, preRecommendedActionKey)) {
          this.caseManualStartHopInspectorOutputRows = [];
          this.caseManualStartHopInspectorPreviewRows = [];
          this.caseManualStartHopInspectorStatus = this.caseManualNormalizeText(result?.message)
            || `Tool 88 could not safely apply the ${preRecommendedActionKey} action. Nothing was written to Main Data or Raw Data.`;
          if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
          return result;
        }
        if (this.caseManualStartHopInspectorStopRequested) {
          this.caseManualStartHopInspectorStatus = 'Trace preview fetch was stopped before Case Buddy applied anything.';
          return result;
        }
        let sanitizedOutputRows = this.caseManualSelectFreshHopInspectorRows(result || snapshot || {}, this.caseManualStartHopInspectorOutputRows, actionCandidate);
        const recommendedActionKey = preRecommendedActionKey || this.resolveCaseManualStartHopInspectorRecommendedActionKey();
        if (recommendedActionKey === 'path-ending') {
          sanitizedOutputRows = sanitizedOutputRows.length
            ? this.caseManualAnnotatePathEndingRowsWithAttribution(sanitizedOutputRows, result || snapshot || {})
            : this.buildCaseManualStartPathEndingFallbackRows(result || snapshot || {});
        }
        const recommendedResultLooksTrace = recommendedActionKey === 'path-ending'
          ? false
          : this.caseManualStartSnapshotActionLooksTraceThrough(result || snapshot || {});
        const recommendedDormantResult = this.caseManualExtractDormantResultFromSnapshot(result || snapshot || {});
        if (recommendedDormantResult) sanitizedOutputRows = this.caseManualBuildDormantRowsForApply(sanitizedOutputRows, result || snapshot || {}, actionCandidate);
        const adjustedOutputCount = sanitizedOutputRows.length;
        if (recommendedResultLooksTrace && adjustedOutputCount && ((!recommendedDormantResult && !this.caseManualPreviewRowsContainTraceChild(sanitizedOutputRows, actionCandidate, result || snapshot || {})) || (this.caseManualDormantResultIsPartial(recommendedDormantResult) && !this.caseManualRowsContainDormantTraceChild(sanitizedOutputRows, recommendedDormantResult, actionCandidate, result || snapshot || {})))) {
          this.caseManualStartHopInspectorOutputRows = [];
          this.caseManualStartHopInspectorStatus = this.caseManualDormantResultIsPartial(recommendedDormantResult)
            ? 'Tool 88 recommended action detected dormant funds plus a continuation, but did not provide the continuation child row. Nothing was applied.'
            : 'Tool 88 recommended action did not provide a valid new child transaction for the selected ending. Nothing was applied.';
          if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
          const autoResolution = await this.caseManualStartHandleViewportAutoTraceNoProgress(actionCandidate, { reason: this.caseManualStartHopInspectorStatus });
          if (autoResolution?.handled) return result;
          return result;
        }
        const shouldPreviewTrace = adjustedOutputCount && (options.preview === true
          || recommendedDormantResult
          || (options.previewTrace !== false && recommendedResultLooksTrace));
        if (shouldPreviewTrace) {
          const previewAction = recommendedActionKey || (recommendedResultLooksTrace ? 'trace-through' : 'recommended');
          this.openCaseManualStartHopInspectorPreview(sanitizedOutputRows, result || snapshot || {}, {
            action: previewAction,
            candidate: actionCandidate,
            message: recommendedDormantResult
              ? 'Dormant funds detected'
              : (previewAction === 'swap'
                ? `Recommended swap staged ${adjustedOutputCount} row${adjustedOutputCount === 1 ? '' : 's'}. Review the swap preview before applying it to the visual.`
                : (previewAction === 'bridge'
                  ? `Recommended bridge staged ${adjustedOutputCount} row${adjustedOutputCount === 1 ? '' : 's'}. Review the bridge preview before applying it to the visual.`
                  : `Recommended action staged ${adjustedOutputCount} row${adjustedOutputCount === 1 ? '' : 's'}. Review the trace preview before applying it to the visual.`))
          });
          return result;
        }
        if (options.apply !== false) {
          if (adjustedOutputCount) {
            await this.applyCaseManualStartHopInspectorOutputRows({ rows: sanitizedOutputRows, snapshot: result || snapshot || {}, action: recommendedActionKey || (recommendedResultLooksTrace ? 'trace-through' : 'recommended'), candidate: actionCandidate });
          } else {
            this.caseManualStartHopInspectorStatus = 'Tool 88 completed the recommended action, but no output rows were returned to apply. Open Full Tool 88 to review the staged result.';
            if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
            if (recommendedResultLooksTrace || recommendedNeedsTraceRecovery) {
              await this.caseManualStartHandleViewportAutoTraceNoProgress(actionCandidate, { reason: this.caseManualStartHopInspectorStatus });
            }
          }
        } else if (adjustedOutputCount) {
          this.caseManualStartHopInspectorStatus = recommendedDormantResult
            ? 'Dormant funds detected. Click Apply staged result to update Main Data.'
            : `Recommended action staged ${adjustedOutputCount} row${adjustedOutputCount === 1 ? '' : 's'}. Click Apply staged result to update the visual.`;
        }
        return result;
      } catch (error) {
        this.caseManualStartHopInspectorStatus = `Tool 88 recommended action failed: ${error?.message || 'unknown error'}.`;
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        return null;
      } finally {
        this.stopCaseManualStartHopInspectorTraceLoading();
        this.caseManualStartHopInspectorRunning = false;
      }
    },

    async runCaseManualStartHopInspectorSpecialPreviewAction(tool, actionKey = 'swap') {
      if (!tool) throw new Error('Embedded Tool 88 is not ready yet.');
      const normalizedAction = actionKey === 'bridge' ? 'bridge' : 'swap';
      if (normalizedAction === 'swap') {
        if (typeof tool.openSwapExplorer !== 'function') throw new Error('Tool 88 swap explorer is unavailable.');
        await tool.openSwapExplorer();
        const candidate = tool.swapAutomaticCandidate || tool.selectedSwapCandidate || null;
        if (!candidate?.canApply) {
          const baseResult = typeof tool.buildCaseBuddyHopSolverSnapshot === 'function'
            ? tool.buildCaseBuddyHopSolverSnapshot({
                includeRows: false,
                status: 'manual-review',
                integrationAction: 'manual-swap-review',
                message: 'Tool 88 opened the swap explorer, but no automatic swap candidate was safe to apply. Open Full Tool 88 to review the transfer table and choose manually.'
              })
            : { status: 'manual-review', integrationAction: 'manual-swap-review' };
          return {
            ...(baseResult || {}),
            outputRows: [],
            outputTsv: '',
            caseBuddySpecialAction: 'swap',
            caseBuddySpecialActionApplied: false,
            caseBuddySpecialActionProducedOutput: false,
          };
        }
        if (typeof tool.applySwapCandidate !== 'function') throw new Error('Tool 88 swap apply action is unavailable.');
        tool.applySwapCandidate(candidate);
        const appliedRows = Array.isArray(tool.swapApplyResult?.rows)
          ? tool.swapApplyResult.rows.filter(row => Array.isArray(row))
          : (Array.isArray(tool.swapApplyResult?.row) ? [tool.swapApplyResult.row] : []);
        const baseResult = typeof tool.buildCaseBuddyHopSolverSnapshot === 'function'
          ? tool.buildCaseBuddyHopSolverSnapshot({
              includeRows: true,
              status: 'decision-ready',
              integrationAction: 'manual-swap',
              message: 'Tool 88 automatic swap solver staged a swap result. Review the swap preview before applying it to Main and Raw Data.'
            })
          : { status: 'decision-ready', integrationAction: 'manual-swap', outputRows: appliedRows };
        return {
          ...(baseResult || {}),
          caseBuddySpecialAction: 'swap',
          caseBuddySpecialActionApplied: appliedRows.length > 0,
          caseBuddySpecialActionProducedOutput: appliedRows.length > 0,
          ...(appliedRows.length ? { outputRows: appliedRows } : {}),
        };
      }

      if (typeof tool.openBridgeSolver !== 'function') throw new Error('Tool 88 bridge solver is unavailable.');
      await tool.openBridgeSolver();
      let candidate = tool.bridgeBestCandidate || tool.selectedBridgeCandidate || null;
      if (!candidate?.canApply) {
        await tool.openBridgeSolver({ bruteForce: true });
        candidate = tool.bridgeBestCandidate || tool.selectedBridgeCandidate || null;
      }
      if (!candidate?.canApply) {
        return typeof tool.buildCaseBuddyHopSolverSnapshot === 'function'
          ? {
              ...tool.buildCaseBuddyHopSolverSnapshot({
                includeRows: false,
                status: 'manual-review',
                integrationAction: 'manual-bridge-review',
                message: 'Tool 88 opened the bridge solver, but no bridge candidate was safe to apply automatically. Open Full Tool 88 to review bridge candidates manually.'
              }),
              outputRows: [],
              outputTsv: ''
            }
          : { status: 'manual-review', integrationAction: 'manual-bridge-review', outputRows: [], outputTsv: '' };
      }
      if (typeof tool.applyBridgeCandidate !== 'function') throw new Error('Tool 88 bridge apply action is unavailable.');
      tool.applyBridgeCandidate(candidate);
      return typeof tool.buildCaseBuddyHopSolverSnapshot === 'function'
        ? tool.buildCaseBuddyHopSolverSnapshot({
            includeRows: true,
            status: 'decision-ready',
            integrationAction: 'manual-bridge',
            message: 'Tool 88 bridge solver staged a bridge result. Review the bridge preview before applying it to Main and Raw Data.'
          })
        : { status: 'decision-ready', integrationAction: 'manual-bridge', outputRows: [] };
    },

    async runCaseManualStartHopInspectorToolAction(action = 'trace-through', options = {}) {
      if (this.caseManualStartHopInspectorRunning) return null;
      const actionCandidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartHopInspectorCandidate || this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection({ quiet: true }) || null;
      if (actionCandidate?.caseManualStartCandidate) this.caseManualStartHopInspectorCandidate = actionCandidate;
      const snapshot = await this.ensureCaseManualStartHopInspectorChecksReady({ candidate: actionCandidate, force: options.force === true });
      if (!snapshot) return null;
      this.caseManualStartHopInspectorRunning = true;
      this.caseManualStartHopInspectorStopRequested = false;
      const actionKey = this.caseManualNormalizeText(action).toLowerCase();
      const actionLabel = actionKey === 'path-ending'
        ? 'path ending'
        : (actionKey === 'swap' ? 'swap preview' : (actionKey === 'bridge' ? 'bridge preview' : 'trace through'));
      const actionNeedsReviewPreview = this.caseManualStartHopInspectorActionNeedsReviewPreview(actionKey);
      this.caseManualStartHopInspectorStatus = actionNeedsReviewPreview
        ? `Building Tool 88 ${actionLabel} for review…`
        : (options.apply === false
          ? `Staging Tool 88 ${actionLabel} action…`
          : `Running Tool 88 ${actionLabel} action and applying it to the visual…`);
      const actionLooksTrace = actionKey !== 'path-ending' && actionKey !== 'swap' && actionKey !== 'bridge';
      const handleAutoTraceNoProgress = async (reason = '') => (
        actionLooksTrace
          ? this.caseManualStartHandleViewportAutoTraceNoProgress(actionCandidate, { reason })
          : null
      );
      const serviceOverrideForTrace = Boolean(actionLooksTrace && this.caseManualStartHopInspectorTraceOverrideNeeded);
      if (serviceOverrideForTrace && this.caseManualStartTraceAnywayBlocked) {
        this.caseManualStartHopInspectorStatus = this.caseManualStartTraceAnywayBlockedMessage;
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        return null;
      }
      this.caseManualStartHopInspectorLastTraceServiceOverride = serviceOverrideForTrace;
      if (actionLooksTrace || actionNeedsReviewPreview) {
        this.startCaseManualStartHopInspectorTraceLoading(actionNeedsReviewPreview
          ? `Building ${actionLabel} with Tool 88…`
          : (serviceOverrideForTrace
            ? `Fetching ${actionLabel} preview with hot-wallet warning override…`
            : `Fetching ${actionLabel} preview…`));
      }
      try {
        const tool = this.getCaseManualStartHopInspectorTool();
        if (!tool) throw new Error('Embedded Tool 88 is not ready yet.');
        this.syncCaseManualStartSettingsToEmbeddedTool88();
        let rawResult = null;
        if (actionKey === 'path-ending') {
          if (typeof tool.markPathEnding !== 'function') throw new Error('Tool 88 path-ending action is unavailable.');
          await tool.markPathEnding();
        } else if (actionKey === 'swap' || actionKey === 'bridge') {
          rawResult = await this.runCaseManualStartHopInspectorSpecialPreviewAction(tool, actionKey);
        } else {
          if (typeof tool.traceThrough !== 'function') throw new Error('Tool 88 trace-through action is unavailable.');
          const selectedKey = this.buildCaseManualStartHopInspectorCandidateKey(actionCandidate || this.caseManualStartHopInspectorCandidate || this.caseManualStartHopInspectorSelectedCandidate);
          const reloadedSnapshot = await this.loadCaseManualStartHopInspectorCandidate({
            force: true,
            automatic: true,
            expectedCandidateKey: selectedKey,
            candidate: actionCandidate
          });
          if (!reloadedSnapshot) throw new Error('Tool 88 could not reload the selected unresolved ending before trace-through. Main Data was left unchanged.');
          if (typeof tool.caseBuddyClearStaleStopState === 'function') {
            tool.caseBuddyClearStaleStopState('Case Buddy Manual Solve is starting a fresh trace-through preview.');
          }
          const traceThroughSettings = this.caseManualStartReadTool88Settings();
          await tool.traceThrough(serviceOverrideForTrace ? {
            allowPotentialServiceTraceOverride: true,
            overridePotentialServiceStop: true,
            serviceOverride: true,
            resetCaseBuddyStopState: true,
            ...this.caseManualStartNativeDirectSpendToolOptions(traceThroughSettings),
            serviceOverrideReason: 'Case Buddy manual hop inspector: investigator chose Trace anyway after Tool 88 flagged Unknown service / hot-wallet behavior.',
          } : {
            resetCaseBuddyStopState: true,
            ...this.caseManualStartNativeDirectSpendToolOptions(traceThroughSettings),
          });
        }
        if (!rawResult) {
          rawResult = typeof tool.buildCaseBuddyHopSolverSnapshot === 'function'
            ? tool.buildCaseBuddyHopSolverSnapshot({
                includeRows: true,
                status: options.apply === false ? 'decision-ready' : 'applied',
                integrationAction: `manual-${actionKey || 'trace-through'}`,
                message: options.apply === false
                  ? `Tool 88 ${actionLabel} action is ready to apply.`
                  : `Tool 88 ${actionLabel} action completed and is being applied to the visual.`
              })
            : { status: 'decision-ready', integrationAction: `manual-${actionKey || 'trace-through'}`, outputRows: [] };
        }
        let result = this.attachCaseManualStartTraceInspectorRows(rawResult || {}, tool);
        if (serviceOverrideForTrace) {
          result = {
            ...(result || {}),
            caseBuddyManualServiceOverride: true,
            manualServiceOverride: true,
            serviceStopOverride: true,
            message: result?.message || 'Trace-through preview fetched after bypassing Tool 88 Unknown service / hot-wallet stopping for this one acknowledged run.',
          };
        }
        if (actionLooksTrace) {
          const recoveredTrace = await this.recoverCaseManualStartTraceSnapshotFromTool(tool, result || {}, actionCandidate, { action: actionKey || 'trace-through' });
          result = recoveredTrace.snapshot || result;
          if (recoveredTrace.recovered) {
            result = {
              ...(result || {}),
              caseBuddyManualTraceRowsRecovered: true,
              message: result?.message || 'Case Buddy recovered Tool 88 trace-through rows after the provider result settled.'
            };
          }
        }
        this.handleCaseManualStartHopInspectorSnapshot('case-buddy-result', result || {});
        this.refreshCaseManualStartHopInspectorTraceProgress();
        if (this.caseManualStartSpecialActionDidNotApply(result, actionKey)) {
          this.caseManualStartHopInspectorOutputRows = [];
          this.caseManualStartHopInspectorPreviewRows = [];
          this.caseManualStartHopInspectorStatus = this.caseManualNormalizeText(result?.message)
            || `Tool 88 could not safely apply the ${actionLabel} action. Nothing was written to Main Data or Raw Data.`;
          if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
          return result;
        }
        const actionDormantResult = this.caseManualExtractDormantResultFromSnapshot(result || {});
        const resultRowsFromSnapshot = this.caseManualSelectFreshHopInspectorRows(result || {}, this.caseManualStartHopInspectorOutputRows, actionCandidate);
        if (actionLooksTrace && ((!actionDormantResult && !this.caseManualSnapshotHasValidTraceExitRows(result || {}) && !this.caseManualPreviewRowsContainTraceChild(resultRowsFromSnapshot, actionCandidate, result || {})) || (this.caseManualDormantResultIsPartial(actionDormantResult) && !this.caseManualRowsContainDormantTraceChild(resultRowsFromSnapshot, actionDormantResult, actionCandidate, result || {})))) {
          this.caseManualStartHopInspectorOutputRows = [];
          this.caseManualStartHopInspectorPreviewRows = [];
          this.caseManualStartHopInspectorStatus = this.caseManualDormantResultIsPartial(actionDormantResult)
            ? 'Tool 88 detected dormant funds plus a continuation, but Case Buddy could not find the continuation child transaction row. Main Data and Raw Data were left unchanged.'
            : 'Tool 88 did not produce a valid trace-through next-hop transaction hash. Main Data and Raw Data were left unchanged.';
          if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
          await handleAutoTraceNoProgress(this.caseManualStartHopInspectorStatus);
          return result;
        }
        if (this.caseManualStartHopInspectorStopRequested) {
          this.caseManualStartHopInspectorStatus = `Tool 88 ${actionLabel} action was stopped before Case Buddy applied anything.`;
          return result;
        }
        let sanitizedOutputRows = resultRowsFromSnapshot;
        if (actionDormantResult) {
          sanitizedOutputRows = this.caseManualBuildDormantRowsForApply(sanitizedOutputRows, result || {}, actionCandidate);
        } else if (actionKey === 'path-ending') {
          sanitizedOutputRows = sanitizedOutputRows.length
            ? this.caseManualAnnotatePathEndingRowsWithAttribution(sanitizedOutputRows, result || {})
            : this.buildCaseManualStartPathEndingFallbackRows(result || {});
        }
        const outputCount = sanitizedOutputRows.length;
        if (actionLooksTrace && outputCount && ((!actionDormantResult && !this.caseManualPreviewRowsContainTraceChild(sanitizedOutputRows, actionCandidate, result || {})) || (this.caseManualDormantResultIsPartial(actionDormantResult) && !this.caseManualRowsContainDormantTraceChild(sanitizedOutputRows, actionDormantResult, actionCandidate, result || {})))) {
          this.caseManualStartHopInspectorOutputRows = [];
          this.caseManualStartHopInspectorStatus = this.caseManualDormantResultIsPartial(actionDormantResult)
            ? `Tool 88 ${actionLabel} returned dormant-remainder rows, but no valid continuation child transaction for the selected ending. Nothing was applied.`
            : `Tool 88 ${actionLabel} returned rows, but none were a valid new child transaction for the selected ending. Nothing was applied.`;
          if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
          await handleAutoTraceNoProgress(this.caseManualStartHopInspectorStatus);
          return result;
        }
        const shouldPreviewTrace = outputCount && (options.preview === true
          || actionNeedsReviewPreview
          || (actionKey === 'trace-through' && options.previewTrace !== false));
        if (shouldPreviewTrace) {
          const previewMessage = actionDormantResult
            ? 'Dormant funds detected'
            : (actionNeedsReviewPreview
              ? `Tool 88 staged a ${actionKey} result with ${outputCount} row${outputCount === 1 ? '' : 's'}. Review the solver evidence before applying it to the visual, Main Data, and Raw Data.`
              : (serviceOverrideForTrace
                ? `Tool 88 ${actionLabel} staged ${outputCount} row${outputCount === 1 ? '' : 's'} after bypassing the Unknown service / hot-wallet assessor for this one run. Review the provider details, then acknowledge before applying.`
                : `Tool 88 ${actionLabel} staged ${outputCount} row${outputCount === 1 ? '' : 's'}. Review the provider details before applying it to the visual.`));
          this.openCaseManualStartHopInspectorPreview(sanitizedOutputRows, result || {}, {
            action: actionKey || 'trace-through',
            candidate: actionCandidate,
            serviceOverride: serviceOverrideForTrace,
            message: previewMessage
          });
          return result;
        }
        if (options.apply !== false) {
          if (outputCount) {
            await this.applyCaseManualStartHopInspectorOutputRows({ rows: sanitizedOutputRows, snapshot: result || {}, action: actionKey || 'trace-through', candidate: actionCandidate });
          } else {
            this.caseManualStartHopInspectorStatus = actionDormantResult
              ? 'Dormant funds were detected, but Case Buddy could not build the dormant Main Data update row. Open Full Tool 88 to review the provider result.'
              : (serviceOverrideForTrace
                ? `Tool 88 ${actionLabel} ran with the Unknown service / hot-wallet assessor bypassed, but no output rows were returned to apply. Open Full Tool 88 to review the provider result.`
                : `Tool 88 ${actionLabel} action completed, but no output rows were returned to apply. Open Full Tool 88 to review the staged result.`);
            if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
            await handleAutoTraceNoProgress(this.caseManualStartHopInspectorStatus);
          }
        } else if (outputCount) {
          this.caseManualStartHopInspectorStatus = `Tool 88 ${actionLabel} staged ${outputCount} row${outputCount === 1 ? '' : 's'}. Click Apply staged result to update the visual.`;
        }
        return result;
      } catch (error) {
        this.caseManualStartHopInspectorStatus = `Tool 88 ${actionLabel} action failed: ${error?.message || 'unknown error'}.`;
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        return null;
      } finally {
        this.stopCaseManualStartHopInspectorTraceLoading();
        this.caseManualStartHopInspectorRunning = false;
      }
    },

    openFullCaseHopSolverFromManualStart() {
      const candidate = this.caseManualStartHopInspectorSelectedCandidate || this.caseManualStartSingleSelectedCandidate;
      if (!candidate?.caseManualStartCandidate) {
        this.caseManualStartHopInspectorStatus = 'Select one Manual Solve path-ending node before opening Full Tool 88 from this viewport.';
        if (typeof this.showCaseToast === 'function') this.showCaseToast(this.caseManualStartHopInspectorStatus);
        return;
      }
      this.caseManualStartHopInspectorCandidate = candidate;
      this.caseInvestigationVisualizerSelectedCandidate = candidate;
      const selectedKey = this.buildCaseManualStartHopInspectorCandidateKey(this.caseManualStartSingleSelectedCandidate);
      const candidateKey = this.buildCaseManualStartHopInspectorCandidateKey(candidate);
      if (!selectedKey || selectedKey !== candidateKey) this.selectCaseManualStartPathEndingNode(candidate);
      this.syncCaseManualStartSettingsToEmbeddedTool88();
      this.caseManualStartHopInspectorFullToolOpen = true;
      this.openCaseHopSolverFromInvestigationVisualizer({ forceFull: true });
    },

    openCaseHopSolverFromInvestigationVisualizer(options = {}) {
      const candidate = this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection({ quiet: true });
      if (candidate?.caseManualStartCandidate && !options.forceFull) {
        this.caseManualStartHopInspectorCandidate = candidate;
        this.openCaseManualStartHopInspectorFromSelection();
        return;
      }
      if (candidate?.caseManualStartCandidate) {
        this.caseHopSolverCandidate = candidate;
        this.caseHopSolverPlaceholderCandidate = null;
        this.caseHopSolverStatusTitle = 'Solve hop';
        this.caseHopSolverStatus = 'Tool 88 loaded this manually visualized starting/path-ending row. Use Simple view for one hop at a time, or Advanced view for full controls.';
        this.caseHopSolverRunning = false;
        this.caseHopSolverStopRequested = false;
        this.caseHopSolverOutputRows = [];
        this.caseHopSolverOutputTsv = '';
        this.caseHopSolverLastEvent = 'manual visual start candidate selected';
        this.caseHopSolverRawApplyStatus = '';
        this.caseHopSolverViewMode = 'simple';
        this.caseHopSolverStartInAdvancedView = false;
        this.showCaseHopSolverModal = true;
        this.$nextTick(() => {
          if (typeof this.loadCaseHopSolverSelectedCandidate === 'function') {
            this.loadCaseHopSolverSelectedCandidate();
          }
        });
        return;
      }
      return this.callFirstCaseBuddyOriginalMethod('openCaseHopSolverFromInvestigationVisualizer', [options]);
    },

    selectCaseHopSolverNextUnresolved() {
      if (this.shouldUseCaseWorkflowRawDataTool88Headless?.({ source: 'selectCaseHopSolverNextUnresolved' })) {
        return this.runCaseWorkflowRawDataTool88Headless({ source: 'selectCaseHopSolverNextUnresolved' });
      }
      if (this.caseInvestigationVisualizerState?.source === 'case-buddy-manual-start-visualizer') {
        const flows = this.caseInvestigationVisualizerState.flows || [];
        if (!flows.length) return;
        const startIndex = Math.max(0, Number(this.caseInvestigationVisualizerActiveIndex) || 0);
        const nextIndex = flows.length > 1 ? (startIndex + 1) % flows.length : startIndex;
        this.renderCaseInvestigationVisualizerAtIndex(nextIndex, { mode: 'single' });
        const candidate = flows[nextIndex]?.candidate || flows[nextIndex]?.entries?.[0] || null;
        this.selectCaseManualStartPathEndingNode(candidate);
        const selectedCandidate = this.getCaseManualStartActivePathEndingCandidate(candidate) || candidate;
        this.caseInvestigationVisualizerSelectedCandidate = selectedCandidate;
        this.caseHopSolverCandidate = selectedCandidate;
        this.caseHopSolverStatus = candidate ? `Selected ${candidate.pathLabel || candidate.hash} for manual Tool 88 hop solving.` : 'No unresolved manual-start candidate was found.';
        this.$nextTick(() => {
          if (candidate && this.showCaseHopSolverModal && typeof this.loadCaseHopSolverSelectedCandidate === 'function') {
            this.loadCaseHopSolverSelectedCandidate();
          }
        });
        return;
      }
      return this.callFirstCaseBuddyOriginalMethod('selectCaseHopSolverNextUnresolved');
    },

    openCaseHopSolverNextUnresolved() {
      if (this.shouldUseCaseWorkflowRawDataTool88Headless?.({ source: 'openCaseHopSolverNextUnresolved' })) {
        return this.runCaseWorkflowRawDataTool88Headless({ source: 'openCaseHopSolverNextUnresolved' });
      }
      if (this.caseInvestigationVisualizerState?.source === 'case-buddy-manual-start-visualizer') {
        this.selectCaseHopSolverNextUnresolved();
        this.showCaseHopSolverModal = true;
        return;
      }
      return this.callFirstCaseBuddyOriginalMethod('openCaseHopSolverNextUnresolved');
    },


    getCaseManualSolveVisualGroupVisualKey(visual = this.caseInvestigationVisualizerCurrentVisual) {
      return this.getCaseManualSolveVisualColorVisualKey(visual);
    },

    getCaseManualSolveVisualGroupsState(create = false) {
      const existing = this.caseSupportState?.manualSolveVisualGroups;
      const hasState = existing && typeof existing === 'object' && !Array.isArray(existing);
      if (!hasState && !create) return null;
      const groups = hasState
        ? {
          schemaVersion: 1,
          ...existing,
          byVisualKey: existing.byVisualKey && typeof existing.byVisualKey === 'object' && !Array.isArray(existing.byVisualKey) ? { ...existing.byVisualKey } : {}
        }
        : { schemaVersion: 1, byVisualKey: {} };
      if (create && (!hasState || existing !== groups)) {
        this.caseSupportState = {
          ...(this.caseSupportState || createDefaultCaseSupportState()),
          manualSolveVisualGroups: groups
        };
      }
      return groups;
    },

    getCaseManualSolveVisualGroupBucket(visual = this.caseInvestigationVisualizerCurrentVisual, create = false) {
      const visualKey = this.getCaseManualSolveVisualGroupVisualKey(visual);
      if (!visualKey) return null;
      const state = this.getCaseManualSolveVisualGroupsState(create);
      if (!state) return null;
      const existing = state.byVisualKey?.[visualKey];
      if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
        return {
          schemaVersion: 1,
          ...existing,
          groups: existing.groups && typeof existing.groups === 'object' && !Array.isArray(existing.groups) ? existing.groups : {}
        };
      }
      if (!create) return null;
      const bucket = { schemaVersion: 1, groups: {}, nextSequence: 1 };
      state.byVisualKey[visualKey] = bucket;
      this.caseSupportState = {
        ...(this.caseSupportState || createDefaultCaseSupportState()),
        manualSolveVisualGroups: state
      };
      return bucket;
    },

    cloneCaseManualSolveVisualGroups() {
      const source = this.caseSupportState?.manualSolveVisualGroups || null;
      if (!source || typeof source !== 'object') return null;
      try {
        return JSON.parse(JSON.stringify(source));
      } catch {
        return null;
      }
    },

    restoreCaseManualSolveVisualGroupsFromHistory(groups = null) {
      let next = { schemaVersion: 1, byVisualKey: {} };
      if (groups && typeof groups === 'object') {
        try {
          next = JSON.parse(JSON.stringify(groups));
        } catch {
          next = { schemaVersion: 1, byVisualKey: {} };
        }
      }
      this.caseSupportState = {
        ...(this.caseSupportState || createDefaultCaseSupportState()),
        manualSolveVisualGroups: next
      };
      this.caseManualSolveVisualGroupRuntimeNodesById = {};
      this.touchCaseManualSolveVisualGroupSupportMetadata('Manual Solve visual group undo/redo updated support metadata.', { preserveDirtySince: true });
    },

    writeCaseManualSolveVisualGroupBucket(visual, bucket, reason = 'Manual Solve visual groups updated.') {
      const visualKey = this.getCaseManualSolveVisualGroupVisualKey(visual);
      const state = this.getCaseManualSolveVisualGroupsState(true) || { schemaVersion: 1, byVisualKey: {} };
      const byVisualKey = { ...(state.byVisualKey || {}) };
      const groups = bucket?.groups && typeof bucket.groups === 'object' && !Array.isArray(bucket.groups) ? bucket.groups : {};
      if (bucket && Object.keys(groups).length) {
        byVisualKey[visualKey] = {
          schemaVersion: 1,
          ...bucket,
          groups,
          updatedAtUtc: new Date().toISOString()
        };
      } else {
        delete byVisualKey[visualKey];
      }
      this.caseSupportState = {
        ...(this.caseSupportState || createDefaultCaseSupportState()),
        manualSolveVisualGroups: { schemaVersion: 1, byVisualKey }
      };
      this.caseManualSolveVisualGroupRuntimeNodesById = {};
      this.touchCaseManualSolveVisualGroupSupportMetadata(reason);
    },

    touchCaseManualSolveVisualGroupSupportMetadata(reason = 'Manual Solve visual groups updated.', options = {}) {
      const now = new Date().toISOString();
      const state = this.caseSupportState || createDefaultCaseSupportState();
      this.caseSupportState = {
        ...state,
        dirty: true,
        dirtySinceUtc: options.preserveDirtySince ? (state.dirtySinceUtc || now) : (state.dirtySinceUtc || now),
        lastMutation: {
          source: 'manual_solve_visual_group_metadata',
          details: { reason },
          utc: now,
          revision: Number(state.caseDataRevision) || 0
        }
      };
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
    },

    caseManualSolveVisualNodeIsGroupNode(node = {}) {
      return String(node?.type || '').toLowerCase() === 'manualgroup';
    },

    caseManualSolveVisualNodeCanBelongToGroup(node = {}) {
      const type = String(node?.type || '').toLowerCase();
      return ['address', 'tx', 'amount'].includes(type);
    },

    caseManualSolveVisualGroupMemberKeyForNode(node = {}, visual = this.caseInvestigationVisualizerCurrentVisual) {
      if (!this.caseManualSolveVisualNodeCanBelongToGroup(node)) return '';
      return this.caseManualSolveVisualStableKeyPart(this.getManualSolveVisualNodeColorKey(node, visual));
    },

    caseManualSolveVisualNodeIsCollapsedGroupMember(node = {}, groupedMemberKeys = this.caseManualSolveVisualGroupedMemberKeySet) {
      if (!this.caseInvestigationVisualizerManualStartMode || this.caseManualSolveVisualNodeIsGroupNode(node)) return false;
      const key = this.caseManualSolveVisualGroupMemberKeyForNode(node, this.caseInvestigationVisualizerCurrentVisual);
      return !!key && groupedMemberKeys?.has?.(key);
    },

    caseManualSolveVisualGroupNodeId(group = {}) {
      return `case-manual-solve-group-${this.caseManualSolveVisualStableKeyPart(group?.id || '')}`;
    },

    caseManualSolveVisualGroupNewId() {
      const suffix = Math.random().toString(36).slice(2, 9);
      return `group-${Date.now().toString(36)}-${suffix}`;
    },

    caseManualSolveVisualGroupMemberNodes(group = {}, rawNodes = this.caseInvestigationVisualizerRawNodes, visual = this.caseInvestigationVisualizerCurrentVisual) {
      const memberKeys = new Set((Array.isArray(group.memberKeys) ? group.memberKeys : [])
        .map(key => this.caseManualSolveVisualStableKeyPart(key))
        .filter(Boolean));
      if (!memberKeys.size) return [];
      return (Array.isArray(rawNodes) ? rawNodes : [])
        .map((node) => {
          const key = this.caseManualSolveVisualGroupMemberKeyForNode(node, visual);
          return key && memberKeys.has(key) ? { ...node, groupMemberKey: key } : null;
        })
        .filter(Boolean);
    },

    caseManualSolveVisualGroupMemberTitle(node = {}) {
      return this.caseManualNormalizeText(node.pathLabel || node.shortHash || node.shortAddress || node.amountText || node.labelText || node.id || 'Node');
    },

    caseManualSolveVisualGroupMemberMeta(node = {}) {
      const type = String(node?.type || 'node').toLowerCase();
      const primary = type === 'address'
        ? this.caseManualNormalizeText(node.address || node.toAddress || node.shortAddress)
        : this.caseManualNormalizeText(node.hash || node.transactionHash || node.shortHash || node.amountText);
      const secondary = this.caseManualNormalizeText(node.fundsFollowed || node.token || node.tokenSymbol || '');
      return [primary ? this.caseManualShorten(primary, 12, 10) : type, secondary].filter(Boolean).join(' · ');
    },

    caseManualSolveBuildVisualGroupNode(group = {}, index = 0, rawNodes = this.caseInvestigationVisualizerRawNodes, visual = this.caseInvestigationVisualizerCurrentVisual) {
      const members = this.caseManualSolveVisualGroupMemberNodes(group, rawNodes, visual);
      if (!members.length) return null;
      const id = this.caseManualSolveVisualGroupNodeId(group);
      const defaultName = `Group ${index + 1}`;
      const name = this.caseManualNormalizeText(group.name || defaultName) || defaultName;
      const savedX = Number(group.x);
      const savedY = Number(group.y);
      const centroid = members.reduce((memo, node) => {
        memo.x += Number(node?.x) || 0;
        memo.y += Number(node?.y) || 0;
        return memo;
      }, { x: 0, y: 0 });
      const runtime = this.caseManualSolveVisualGroupRuntimeNodesById?.[id] || null;
      const drag = this.caseManualSolveVisualGroupDrag || {};
      const draggingThisGroup = Boolean(drag.active && Array.isArray(drag.groupIds) && drag.groupIds.includes(group.id));
      const runtimeX = Number(runtime?.x);
      const runtimeY = Number(runtime?.y);
      const x = draggingThisGroup && Number.isFinite(runtimeX) ? runtimeX : (Number.isFinite(savedX) ? savedX : centroid.x / members.length);
      const y = draggingThisGroup && Number.isFinite(runtimeY) ? runtimeY : (Number.isFinite(savedY) ? savedY : centroid.y / members.length);
      const color = this.caseManualSolveVisualGroupResolvedColor(group.color, { treatLegacyDefault: true });
      const counts = members.reduce((memo, node) => {
        const type = String(node?.type || 'node').toLowerCase();
        memo[type] = (memo[type] || 0) + 1;
        return memo;
      }, {});
      const memberSummary = [counts.tx ? `${counts.tx} tx` : '', counts.address ? `${counts.address} address` : '', counts.amount ? `${counts.amount} amount` : ''].filter(Boolean).join(' · ') || `${members.length} nodes`;
      const detailRows = [
        ['Visual group', name],
        ['Grouped nodes', members.length],
        ['Transactions', counts.tx || 0],
        ['Addresses', counts.address || 0],
        ['Amount nodes', counts.amount || 0],
        ['Members', members.slice(0, 14).map(node => this.caseManualSolveVisualGroupMemberTitle(node)).join(', ')],
        ['Note', 'Visual-only Manual Solve group. Main Data and Raw Data were not changed.']
      ];
      const next = {
        id,
        type: 'manualGroup',
        labelCategory: 'manual-group',
        x,
        y,
        boxWidth: 154,
        boxHeight: 118,
        groupId: group.id,
        groupName: name,
        groupColor: color,
        memberCount: members.length,
        memberSummary,
        memberKeys: (Array.isArray(group.memberKeys) ? group.memberKeys : []).slice(),
        groupRecord: group,
        detailRows,
        shortAddress: name,
        labelText: name
      };
      const cache = this.caseManualSolveVisualGroupRuntimeNodesById || {};
      const existing = cache[id];
      if (existing && typeof existing === 'object') {
        Object.keys(existing).forEach((key) => { if (!(key in next)) delete existing[key]; });
        Object.assign(existing, next);
        cache[id] = existing;
      } else {
        cache[id] = next;
      }
      this.caseManualSolveVisualGroupRuntimeNodesById = cache;
      return cache[id];
    },

    caseManualSolveVisualGroupByMemberKeyMap() {
      const map = new Map();
      this.caseManualSolveVisualGroupsForCurrentVisual.forEach((group) => {
        const groupNodeId = this.caseManualSolveVisualGroupNodeId(group);
        (Array.isArray(group.memberKeys) ? group.memberKeys : []).forEach((key) => {
          const clean = this.caseManualSolveVisualStableKeyPart(key);
          if (clean) map.set(clean, { group, groupNodeId });
        });
      });
      return map;
    },

    caseManualSolveVisualGroupEdgesForCurrentVisual(edges = [], hidden = new Set(), visibleNodeIds = new Set()) {
      const rawNodes = this.caseInvestigationVisualizerRawNodes;
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      const nodeById = new Map(rawNodes.map(node => [node?.id, node]).filter(([id]) => !!id));
      const groupMap = this.caseManualSolveVisualGroupByMemberKeyMap();
      const groupedEndpoint = (nodeId = '') => {
        const node = nodeById.get(nodeId);
        if (!node) return nodeId;
        const key = this.caseManualSolveVisualGroupMemberKeyForNode(node, visual);
        const groupHit = key ? groupMap.get(key) : null;
        return groupHit?.groupNodeId || nodeId;
      };
      const output = new Map();
      (Array.isArray(edges) ? edges : []).forEach((edge) => {
        if (!edge?.from || !edge?.to || hidden.has(edge.from) || hidden.has(edge.to)) return;
        const from = groupedEndpoint(edge.from);
        const to = groupedEndpoint(edge.to);
        if (!from || !to || from === to) return;
        if (visibleNodeIds?.size && (!visibleNodeIds.has(from) || !visibleNodeIds.has(to))) return;
        const key = `${from}->${to}|${edge.dashed ? 'dashed' : 'solid'}|${edge.caseManualSwapLinearized ? 'swap' : ''}|${edge.caseManualBridgeLinearized ? 'bridge' : ''}`;
        if (output.has(key)) return;
        output.set(key, {
          ...edge,
          id: `grouped-${edge.id || key}`,
          from,
          to,
          caseManualSolveGroupedEdge: true,
          originalFrom: edge.from,
          originalTo: edge.to
        });
      });
      return Array.from(output.values());
    },

    caseManualSolveVisualGroupSelectionMemberKeys(nodes = []) {
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      const keys = new Set();
      (Array.isArray(nodes) ? nodes : []).forEach((node) => {
        if (this.caseManualSolveVisualNodeIsGroupNode(node)) {
          const group = node.groupRecord || this.caseManualSolveVisualGroupsForCurrentVisual.find(item => item.id === node.groupId);
          (Array.isArray(group?.memberKeys) ? group.memberKeys : []).forEach(key => {
            const clean = this.caseManualSolveVisualStableKeyPart(key);
            if (clean) keys.add(clean);
          });
          return;
        }
        const key = this.caseManualSolveVisualGroupMemberKeyForNode(node, visual);
        if (key) keys.add(key);
      });
      return Array.from(keys);
    },

    groupCaseManualSolveSelectedNodes() {
      if (!this.caseManualSolveCanGroupSelection) return;
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      const before = this.captureCaseInvestigationVisualizerVisualEditState();
      const bucket = this.getCaseManualSolveVisualGroupBucket(visual, true) || { schemaVersion: 1, groups: {}, nextSequence: 1 };
      const selectedNodes = this.caseInvestigationVisualizerSelectedNodes;
      const selectedGroups = this.caseManualSolveSelectedGroups;
      const memberKeys = this.caseManualSolveVisualGroupSelectionMemberKeys(selectedNodes);
      if (memberKeys.length < 2) {
        this.caseManualSolveVisualGroupStatus = 'Select at least two visual nodes, or a group plus another node, before grouping.';
        return;
      }
      const groups = { ...(bucket.groups || {}) };
      selectedGroups.forEach((group) => { delete groups[group.id]; });
      const centroidNodes = selectedNodes.filter(node => !this.caseManualSolveVisualNodeIsGroupNode(node) || selectedGroups.some(group => group.id === node.groupId));
      const centroid = centroidNodes.reduce((memo, node) => {
        memo.x += Number(node?.x) || 0;
        memo.y += Number(node?.y) || 0;
        memo.count += 1;
        return memo;
      }, { x: 0, y: 0, count: 0 });
      const nextSequence = Math.max(1, Number(bucket.nextSequence) || 1);
      const id = this.caseManualSolveVisualGroupNewId();
      const group = {
        id,
        name: `Group ${nextSequence}`,
        color: this.caseManualSolveVisualGroupDefaultColor(),
        memberKeys,
        x: centroid.count ? centroid.x / centroid.count : 0,
        y: centroid.count ? centroid.y / centroid.count : 0,
        createdSequence: nextSequence,
        createdAtUtc: new Date().toISOString()
      };
      groups[id] = group;
      this.pushCaseInvestigationVisualizerVisualEditHistory(before);
      this.writeCaseManualSolveVisualGroupBucket(visual, { schemaVersion: 1, groups, nextSequence: nextSequence + 1 }, 'Manual Solve visual group created.');
      this.caseManualSolveExpandedGroupId = '';
      this.caseInvestigationVisualizerSelectedNodeId = this.caseManualSolveVisualGroupNodeId(group);
      this.caseInvestigationVisualizerSelectedNodeIds = [this.caseInvestigationVisualizerSelectedNodeId];
      this.caseInvestigationVisualizerSelectedCandidate = null;
      this.caseManualSolveVisualGroupNameDraft = group.name;
      this.caseManualSolveVisualGroupColorDraft = group.color;
      this.caseManualSolveVisualGroupStatus = `Grouped ${memberKeys.length} visual node${memberKeys.length === 1 ? '' : 's'}.`;
      this.caseInvestigationVisualizerStatus = 'Manual Solve visual group created. Main Data and Raw Data were not changed.';
    },

    ungroupCaseManualSolveSelectedGroups() {
      const selectedGroups = this.caseManualSolveSelectedGroups;
      if (!selectedGroups.length) return;
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      const before = this.captureCaseInvestigationVisualizerVisualEditState();
      const bucket = this.getCaseManualSolveVisualGroupBucket(visual, false);
      if (!bucket) return;
      const groups = { ...(bucket.groups || {}) };
      const restoredKeys = [];
      selectedGroups.forEach((group) => {
        (Array.isArray(group.memberKeys) ? group.memberKeys : []).forEach(key => restoredKeys.push(this.caseManualSolveVisualStableKeyPart(key)));
        delete groups[group.id];
      });
      this.pushCaseInvestigationVisualizerVisualEditHistory(before);
      this.writeCaseManualSolveVisualGroupBucket(visual, { ...bucket, groups }, 'Manual Solve visual group removed.');
      this.caseManualSolveExpandedGroupId = '';
      this.caseManualSolveVisualGroupStatus = `Ungrouped ${selectedGroups.length} visual group${selectedGroups.length === 1 ? '' : 's'}.`;
      this.caseInvestigationVisualizerStatus = 'Manual Solve visual group removed. Main Data and Raw Data were not changed.';
      this.$nextTick(() => {
        const selectedIds = this.caseInvestigationVisualizerRawNodes
          .filter(node => restoredKeys.includes(this.caseManualSolveVisualGroupMemberKeyForNode(node, visual)))
          .map(node => node.id)
          .filter(Boolean);
        this.caseInvestigationVisualizerSelectedNodeIds = selectedIds;
        this.caseInvestigationVisualizerSelectedNodeId = selectedIds[0] || '';
      });
    },

    syncCaseManualSolveSelectedGroupDraft(group = null) {
      if (!group || !this.caseInvestigationVisualizerManualStartMode) return;
      const record = group.groupRecord || group;
      this.caseManualSolveVisualGroupNameDraft = this.caseManualNormalizeText(record.name || group.groupName || group.labelText || '') || 'Visual group';
      this.caseManualSolveVisualGroupColorDraft = this.caseManualSolveVisualGroupResolvedColor(record.color || group.groupColor || '', { treatLegacyDefault: true });
    },

    applyCaseManualSolveSelectedGroupDetails() {
      const group = this.caseManualSolveSelectedGroup;
      if (!group) return;
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      const bucket = this.getCaseManualSolveVisualGroupBucket(visual, false);
      if (!bucket) return;
      const groups = { ...(bucket.groups || {}) };
      const existing = groups[group.id];
      if (!existing) return;
      const name = this.caseManualNormalizeText(this.caseManualSolveVisualGroupNameDraft) || existing.name || 'Visual group';
      const color = this.caseManualSolveVisualGroupResolvedColor(this.caseManualSolveVisualGroupNormalizedColor || existing.color, { treatLegacyDefault: false });
      const before = this.captureCaseInvestigationVisualizerVisualEditState();
      groups[group.id] = { ...existing, name, color, updatedAtUtc: new Date().toISOString() };
      this.pushCaseInvestigationVisualizerVisualEditHistory(before);
      this.writeCaseManualSolveVisualGroupBucket(visual, { ...bucket, groups }, 'Manual Solve visual group details updated.');
      this.caseManualSolveVisualGroupStatus = 'Group details updated.';
      this.caseInvestigationVisualizerStatus = 'Manual Solve visual group details updated. Main Data was not changed.';
    },

    openCaseManualSolveExpandedGroupPanel(group = null) {
      const selectedGroup = group || this.caseManualSolveSelectedGroup;
      if (!selectedGroup?.id) return;
      this.caseManualSolveExpandedGroupId = selectedGroup.id;
    },

    closeCaseManualSolveExpandedGroupPanel() {
      this.caseManualSolveExpandedGroupId = '';
    },

    onCaseInvestigationVisualizerNodeDoubleClick(node = {}) {
      if (this.caseInvestigationVisualizerManualStartMode && this.caseManualSolveVisualNodeIsGroupNode(node)) {
        this.openCaseManualSolveExpandedGroupPanel(node.groupRecord || this.caseManualSolveSelectedGroup || { id: node.groupId });
        return;
      }
      return undefined;
    },

    caseManualSolveSvgPointFromEvent(event = null) {
      if (!event) return null;
      const svgRef = this.$refs.caseInvestigationVisualizerSvg;
      const svg = Array.isArray(svgRef) ? svgRef[0] : svgRef;
      const clientX = Number(event.clientX);
      const clientY = Number(event.clientY);
      if (!Number.isFinite(clientX) || !Number.isFinite(clientY) || !svg) return null;
      if (typeof svg.createSVGPoint === 'function' && typeof svg.getScreenCTM === 'function') {
        try {
          const matrix = svg.getScreenCTM();
          if (matrix) {
            const point = svg.createSVGPoint();
            point.x = clientX;
            point.y = clientY;
            const transformed = point.matrixTransform(matrix.inverse());
            return { x: transformed.x, y: transformed.y };
          }
        } catch {
          // Fall back to viewBox math.
        }
      }
      const rect = typeof svg.getBoundingClientRect === 'function' ? svg.getBoundingClientRect() : null;
      const viewport = this.caseInvestigationVisualizerViewport || {};
      if (!rect || !rect.width || !rect.height) return null;
      const x = (Number(viewport.x) || 0) + ((clientX - rect.left) / rect.width) * (Number(viewport.width) || 1);
      const y = (Number(viewport.y) || 0) + ((clientY - rect.top) / rect.height) * (Number(viewport.height) || 1);
      return { x, y };
    },

    caseManualSolveFindDropTargetGroup(point = null) {
      if (!point) return null;
      const panel = this.caseManualSolveExpandedGroupPanel;
      if (panel && point.x >= panel.x && point.x <= panel.x + panel.width && point.y >= panel.y && point.y <= panel.y + panel.height) {
        return this.caseManualSolveVisualGroupsForCurrentVisual.find(group => group.id === panel.id) || null;
      }
      return this.caseManualSolveVisualGroupNodesForCurrentVisual.find((node) => {
        const width = Number(node.boxWidth) || 154;
        const height = Number(node.boxHeight) || 118;
        const x = Number(node.x) || 0;
        const y = Number(node.y) || 0;
        return point.x >= x - width / 2 && point.x <= x + width / 2 && point.y >= y - height / 2 && point.y <= y + height / 2;
      })?.groupRecord || null;
    },

    caseManualSolveAddSelectedNodesToGroup(groupId = '', nodes = this.caseInvestigationVisualizerSelectedNodes) {
      if (!groupId || !Array.isArray(nodes) || !nodes.length) return false;
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      const bucket = this.getCaseManualSolveVisualGroupBucket(visual, false);
      if (!bucket?.groups?.[groupId]) return false;
      const groups = { ...(bucket.groups || {}) };
      const target = { ...groups[groupId] };
      const memberKeys = new Set((Array.isArray(target.memberKeys) ? target.memberKeys : []).map(key => this.caseManualSolveVisualStableKeyPart(key)).filter(Boolean));
      const beforeCount = memberKeys.size;
      nodes.forEach((node) => {
        if (this.caseManualSolveVisualNodeIsGroupNode(node)) {
          if (node.groupId === groupId) return;
          const source = groups[node.groupId];
          (Array.isArray(source?.memberKeys) ? source.memberKeys : []).forEach(key => {
            const clean = this.caseManualSolveVisualStableKeyPart(key);
            if (clean) memberKeys.add(clean);
          });
          delete groups[node.groupId];
          return;
        }
        const key = this.caseManualSolveVisualGroupMemberKeyForNode(node, visual);
        if (key) memberKeys.add(key);
      });
      if (memberKeys.size === beforeCount) return false;
      const groupNode = this.caseManualSolveVisualGroupNodesForCurrentVisual.find(node => node.groupId === groupId) || null;
      groups[groupId] = {
        ...target,
        memberKeys: Array.from(memberKeys),
        x: Number(groupNode?.x) || Number(target.x) || 0,
        y: Number(groupNode?.y) || Number(target.y) || 0,
        updatedAtUtc: new Date().toISOString()
      };
      this.writeCaseManualSolveVisualGroupBucket(visual, { ...bucket, groups }, 'Manual Solve visual nodes added to group.');
      this.caseInvestigationVisualizerSelectedNodeId = this.caseManualSolveVisualGroupNodeId(groups[groupId]);
      this.caseInvestigationVisualizerSelectedNodeIds = [this.caseInvestigationVisualizerSelectedNodeId];
      this.caseManualSolveVisualGroupStatus = `Added ${memberKeys.size - beforeCount} node${memberKeys.size - beforeCount === 1 ? '' : 's'} to ${groups[groupId].name || 'group'}.`;
      return true;
    },

    caseManualSolveTryDropSelectionIntoGroup(event = null) {
      if (!this.caseInvestigationVisualizerManualStartMode || !this.caseInvestigationVisualizerSelectedNodes.length || !event || event.type === 'pointercancel') return false;
      const point = this.caseManualSolveSvgPointFromEvent(event);
      const target = this.caseManualSolveFindDropTargetGroup(point);
      if (!target?.id) return false;
      const selected = this.caseInvestigationVisualizerSelectedNodes;
      const onlyTarget = selected.length === 1 && this.caseManualSolveVisualNodeIsGroupNode(selected[0]) && selected[0].groupId === target.id;
      if (onlyTarget) return false;
      const before = this.captureCaseInvestigationVisualizerVisualEditState();
      const changed = this.caseManualSolveAddSelectedNodesToGroup(target.id, selected);
      if (changed) {
        this.pushCaseInvestigationVisualizerVisualEditHistory(before);
        this.caseInvestigationVisualizerStatus = 'Selected visual nodes were added to the Manual Solve group. Main Data was not changed.';
      }
      return changed;
    },

    caseManualSolvePersistSelectedGroupPositions() {
      if (!this.caseInvestigationVisualizerManualStartMode || !this.caseManualSolveSelectedGroups.length) return;
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      const bucket = this.getCaseManualSolveVisualGroupBucket(visual, false);
      if (!bucket) return;
      const groups = { ...(bucket.groups || {}) };
      let changed = false;
      this.caseInvestigationVisualizerSelectedNodes.forEach((node) => {
        if (!this.caseManualSolveVisualNodeIsGroupNode(node) || !groups[node.groupId]) return;
        const x = Number(node.x);
        const y = Number(node.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        if (Math.abs((Number(groups[node.groupId].x) || 0) - x) > 0.5 || Math.abs((Number(groups[node.groupId].y) || 0) - y) > 0.5) {
          groups[node.groupId] = { ...groups[node.groupId], x, y, updatedAtUtc: new Date().toISOString() };
          changed = true;
        }
      });
      if (changed) this.writeCaseManualSolveVisualGroupBucket(visual, { ...bucket, groups }, 'Manual Solve visual group position updated.');
    },

    caseManualSolveVisualGroupDebugSummary() {
      return {
        visualKey: this.getCaseManualSolveVisualGroupVisualKey(this.caseInvestigationVisualizerCurrentVisual),
        expandedGroupId: this.caseManualSolveExpandedGroupId || '',
        groupCount: this.caseManualSolveVisualGroupsForCurrentVisual.length,
        selectedGroupIds: this.caseManualSolveSelectedGroups.map(group => group.id),
        groups: this.caseManualSolveVisualGroupsForCurrentVisual.map(group => ({
          id: group.id,
          name: group.name || '',
          color: group.color || '',
          memberCount: Array.isArray(group.memberKeys) ? group.memberKeys.length : 0,
          x: group.x,
          y: group.y
        }))
      };
    },

    caseManualSolveNormalizeColor(value = '') {
      const text = String(value || '').trim();
      const longMatch = text.match(/^#?([0-9a-f]{6})$/i);
      if (longMatch) return `#${longMatch[1].toLowerCase()}`;
      const shortMatch = text.match(/^#?([0-9a-f]{3})$/i);
      if (!shortMatch) return '';
      return `#${shortMatch[1].toLowerCase().split('').map(ch => `${ch}${ch}`).join('')}`;
    },

    caseManualSolveVisualGroupDefaultColor() {
      return '#4f46e5';
    },

    caseManualSolveVisualGroupResolvedColor(value = '', options = {}) {
      const normalized = this.caseManualSolveNormalizeColor(value);
      if (!normalized) return this.caseManualSolveVisualGroupDefaultColor();
      if (options.treatLegacyDefault && normalized === '#a78bfa') return this.caseManualSolveVisualGroupDefaultColor();
      return normalized;
    },

    caseManualSolveColorLuminance(color = '') {
      const normalized = this.caseManualSolveNormalizeColor(color);
      if (!normalized) return 0;
      const channel = offset => parseInt(normalized.slice(offset, offset + 2), 16) / 255;
      const convert = value => (value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4));
      return (0.2126 * convert(channel(1))) + (0.7152 * convert(channel(3))) + (0.0722 * convert(channel(5)));
    },

    caseManualSolveVisualGroupUsesDarkText(node = {}) {
      const color = this.caseManualSolveVisualGroupResolvedColor(node.groupColor || node.groupRecord?.color || '', { treatLegacyDefault: true });
      return this.caseManualSolveColorLuminance(color) > 0.54;
    },

    caseManualSolveVisualGroupTextStyle(node = {}) {
      const darkText = this.caseManualSolveVisualGroupUsesDarkText(node);
      return {
        fill: darkText ? '#111827' : '#ffffff',
        stroke: darkText ? 'rgba(255, 255, 255, 0.68)' : 'rgba(15, 23, 42, 0.38)'
      };
    },

    caseManualSolveVisualGroupInnerStyle(node = {}) {
      const darkText = this.caseManualSolveVisualGroupUsesDarkText(node);
      return {
        fill: darkText ? 'rgba(255, 255, 255, 0.36)' : 'rgba(255, 255, 255, 0.16)',
        stroke: darkText ? 'rgba(17, 24, 39, 0.24)' : 'rgba(255, 255, 255, 0.66)'
      };
    },

    caseManualSolveVisualNodeIsColorable(node = {}) {
      return ['address', 'tx', 'amount', 'manualgroup'].includes(String(node?.type || '').toLowerCase());
    },

    caseManualSolveVisualStableKeyPart(value = '') {
      return normalizeCell(value)
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
        .slice(0, 240);
    },

    caseManualSolveVisualDetailValue(node = {}, label = '') {
      const target = this.caseManualSolveVisualStableKeyPart(label);
      const rows = Array.isArray(node?.detailRows) ? node.detailRows : [];
      const match = rows.find((row) => {
        const rowLabel = Array.isArray(row) ? row[0] : '';
        return this.caseManualSolveVisualStableKeyPart(rowLabel) === target;
      });
      return Array.isArray(match) ? normalizeCell(match[1]) : '';
    },

    getCaseManualSolveVisualColorVisualKey(visual = this.caseInvestigationVisualizerCurrentVisual) {
      const source = visual && typeof visual === 'object' ? visual : {};
      return normalizeCell(source.key || source.flowKey || source.id || [source.source, source.mainHash, source.mainLabel].filter(Boolean).join('|')) || 'manual-solve-current-visual';
    },

    getCaseManualSolveVisualColorOverrides(create = false) {
      const existing = this.caseSupportState?.manualSolveVisualColorOverrides;
      const hasState = existing && typeof existing === 'object' && !Array.isArray(existing);
      if (!hasState && !create) return null;
      const overrides = hasState
        ? {
          schemaVersion: 1,
          ...existing,
          byVisualKey: existing.byVisualKey && typeof existing.byVisualKey === 'object' && !Array.isArray(existing.byVisualKey) ? { ...existing.byVisualKey } : {}
        }
        : { schemaVersion: 1, byVisualKey: {} };
      if (create && (!hasState || existing !== overrides)) {
        this.caseSupportState = {
          ...(this.caseSupportState || createDefaultCaseSupportState()),
          manualSolveVisualColorOverrides: overrides
        };
      }
      return overrides;
    },

    getCaseManualSolveVisualColorBucket(visual = this.caseInvestigationVisualizerCurrentVisual, create = false) {
      const visualKey = this.getCaseManualSolveVisualColorVisualKey(visual);
      if (!visualKey) return null;
      const overrides = this.getCaseManualSolveVisualColorOverrides(create);
      if (!overrides) return null;
      const existing = overrides.byVisualKey?.[visualKey];
      if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
        return {
          schemaVersion: 1,
          ...existing,
          nodeColors: existing.nodeColors && typeof existing.nodeColors === 'object' && !Array.isArray(existing.nodeColors) ? existing.nodeColors : {},
          edgeColors: existing.edgeColors && typeof existing.edgeColors === 'object' && !Array.isArray(existing.edgeColors) ? existing.edgeColors : {}
        };
      }
      if (!create) return null;
      const bucket = { schemaVersion: 1, nodeColors: {}, edgeColors: {} };
      overrides.byVisualKey[visualKey] = bucket;
      this.caseSupportState = {
        ...(this.caseSupportState || createDefaultCaseSupportState()),
        manualSolveVisualColorOverrides: overrides
      };
      return bucket;
    },

    cloneCaseManualSolveVisualColorOverrides() {
      const source = this.caseSupportState?.manualSolveVisualColorOverrides || null;
      if (!source || typeof source !== 'object') return null;
      try {
        return JSON.parse(JSON.stringify(source));
      } catch {
        return null;
      }
    },

    restoreCaseManualSolveVisualColorOverridesFromHistory(overrides = null) {
      let next = { schemaVersion: 1, byVisualKey: {} };
      if (overrides && typeof overrides === 'object') {
        try {
          next = JSON.parse(JSON.stringify(overrides));
        } catch {
          next = { schemaVersion: 1, byVisualKey: {} };
        }
      }
      this.caseSupportState = {
        ...(this.caseSupportState || createDefaultCaseSupportState()),
        manualSolveVisualColorOverrides: next
      };
      this.touchCaseManualSolveVisualColorSupportMetadata('Manual Solve visual color undo/redo updated support metadata.', { preserveDirtySince: true });
    },

    getManualSolveVisualNodeColorKey(node = {}, visual = this.caseInvestigationVisualizerCurrentVisual) {
      if (!node || typeof node !== 'object') return '';
      const type = this.caseManualSolveVisualStableKeyPart(node.type || 'node') || 'node';
      if (this.caseManualSolveVisualNodeIsGroupNode(node)) {
        return [
          `source:${this.caseManualSolveVisualStableKeyPart(visual?.source || 'case-buddy-manual-start-visualizer')}`,
          `main:${this.caseManualSolveVisualStableKeyPart(visual?.mainHash || visual?.flowKey || visual?.key || '')}`,
          `type:manualgroup`,
          `group:${this.caseManualSolveVisualStableKeyPart(node.groupId || node.id || '')}`
        ].filter(Boolean).join('|');
      }
      const detailSpreadsheetRow = this.caseManualSolveVisualDetailValue(node, 'Spreadsheet row');
      const detailHash = this.caseManualSolveVisualDetailValue(node, 'Transaction hash');
      const detailFrom = this.caseManualSolveVisualDetailValue(node, 'From address');
      const detailTo = this.caseManualSolveVisualDetailValue(node, 'To address');
      const detailPath = this.caseManualSolveVisualDetailValue(node, 'Path label');
      const hashPart = this.caseManualSolveVisualStableKeyPart(node.fullHash || node.transactionHash || node.txHash || node.hash || node.sourceTransactionHash || detailHash);
      const addressPart = this.caseManualSolveVisualStableKeyPart(node.fullAddress || node.normalizedAddress || node.address || node.toAddress || node.outputAddress || detailTo);
      const pathPart = this.caseManualSolveVisualStableKeyPart(node.pathLabel || detailPath);
      const fromHashPart = this.caseManualSolveVisualStableKeyPart(node.fromHash || node.fromHashText || '');
      const fromPart = this.caseManualSolveVisualStableKeyPart(node.fromAddress || detailFrom);
      const toPart = this.caseManualSolveVisualStableKeyPart(node.toAddress || detailTo);
      const amountPart = type === 'amount'
        ? `amount:${this.caseManualSolveVisualStableKeyPart(node.amountText)}|funds:${this.caseManualSolveVisualStableKeyPart(node.fundsFollowed)}|token:${this.caseManualSolveVisualStableKeyPart(node.tokenType || node.token)}`
        : '';
      const logicalParts = [
        pathPart ? `path:${pathPart}` : '',
        hashPart ? `hash:${hashPart}` : '',
        addressPart ? `addr:${addressPart}` : '',
        fromHashPart ? `fromhash:${fromHashPart}` : '',
        fromPart ? `from:${fromPart}` : '',
        toPart ? `to:${toPart}` : '',
        amountPart
      ].filter(Boolean);
      const fallbackParts = logicalParts.length ? [] : [
        node.sourceVisualNodeId ? `source-node:${this.caseManualSolveVisualStableKeyPart(node.sourceVisualNodeId)}` : '',
        node.originalId ? `original:${this.caseManualSolveVisualStableKeyPart(node.originalId)}` : '',
        node.graphId ? `graph:${this.caseManualSolveVisualStableKeyPart(node.graphId)}` : '',
        node.groupId ? `group:${this.caseManualSolveVisualStableKeyPart(node.groupId)}` : '',
        node.ownerTxGroupId ? `owner:${this.caseManualSolveVisualStableKeyPart(node.ownerTxGroupId)}` : '',
        detailSpreadsheetRow ? `row:${this.caseManualSolveVisualStableKeyPart(detailSpreadsheetRow)}` : '',
        node.id ? `id:${this.caseManualSolveVisualStableKeyPart(node.id)}` : ''
      ].filter(Boolean);
      const parts = [
        `source:${this.caseManualSolveVisualStableKeyPart(visual?.source || 'case-buddy-manual-start-visualizer')}`,
        `main:${this.caseManualSolveVisualStableKeyPart(visual?.mainHash || visual?.flowKey || visual?.key || '')}`,
        `type:${type}`,
        ...logicalParts,
        ...fallbackParts
      ].filter(Boolean);
      return parts.join('|');
    },

    getManualSolveVisualEdgeColorKey(edge = {}, visual = this.caseInvestigationVisualizerCurrentVisual) {
      if (!edge || typeof edge !== 'object') return '';
      const nodes = Array.isArray(visual?.nodes) ? visual.nodes : this.caseInvestigationVisualizerNodes;
      const nodeById = new Map(nodes.map(node => [node?.id, node]).filter(([id]) => !!id));
      const fromNode = nodeById.get(edge.from);
      const toNode = nodeById.get(edge.to);
      const fromKey = fromNode ? this.getManualSolveVisualNodeColorKey(fromNode, visual) : this.caseManualSolveVisualStableKeyPart(edge.from);
      const toKey = toNode ? this.getManualSolveVisualNodeColorKey(toNode, visual) : this.caseManualSolveVisualStableKeyPart(edge.to);
      return [
        `source:${this.caseManualSolveVisualStableKeyPart(visual?.source || 'case-buddy-manual-start-visualizer')}`,
        `main:${this.caseManualSolveVisualStableKeyPart(visual?.mainHash || visual?.flowKey || visual?.key || '')}`,
        `kind:${this.caseManualSolveVisualStableKeyPart(edge.kind || '')}`,
        `from:${fromKey}`,
        `to:${toKey}`
      ].filter(Boolean).join('|');
    },

    getManualSolveVisualNodeColor(node = {}, visual = this.caseInvestigationVisualizerCurrentVisual) {
      if (!this.caseInvestigationVisualizerManualStartMode) return null;
      const key = this.getManualSolveVisualNodeColorKey(node, visual);
      if (!key) return null;
      const bucket = this.getCaseManualSolveVisualColorBucket(visual, false);
      const color = bucket?.nodeColors?.[key] || null;
      if (!color || typeof color !== 'object') return null;
      const fill = this.caseManualSolveNormalizeColor(color.fill);
      const stroke = this.caseManualSolveNormalizeColor(color.stroke);
      const text = this.caseManualSolveNormalizeColor(color.text);
      return fill || stroke || text ? { ...color, fill, stroke, text } : null;
    },

    getManualSolveVisualEdgeColor(edge = {}, visual = this.caseInvestigationVisualizerCurrentVisual) {
      if (!this.caseInvestigationVisualizerManualStartMode) return '';
      const key = this.getManualSolveVisualEdgeColorKey(edge, visual);
      const bucket = this.getCaseManualSolveVisualColorBucket(visual, false);
      const explicit = this.caseManualSolveNormalizeColor(bucket?.edgeColors?.[key]?.stroke);
      if (explicit) return explicit;
      return this.getCaseManualSolveVisualRelatedTransactionEdgeColor(edge, visual, bucket);
    },

    getCaseManualSolveVisualRelatedTransactionEdgeColor(edge = {}, visual = this.caseInvestigationVisualizerCurrentVisual, bucket = null) {
      const colorBucket = bucket || this.getCaseManualSolveVisualColorBucket(visual, false);
      if (!colorBucket) return '';
      const nodes = Array.isArray(visual?.nodes) ? visual.nodes : this.caseInvestigationVisualizerNodes;
      const nodeById = new Map(nodes.map(node => [node?.id, node]).filter(([id]) => !!id));
      const fromNode = nodeById.get(edge.from);
      const toNode = nodeById.get(edge.to);
      const directTxNodes = [fromNode, toNode].filter(node => String(node?.type || '').toLowerCase() === 'tx');
      for (const node of directTxNodes) {
        const color = this.caseManualSolveNormalizeColor(colorBucket.nodeColors?.[this.getManualSolveVisualNodeColorKey(node, visual)]?.fill);
        if (color) return color;
      }
      const relatedOwners = [fromNode, toNode]
        .map(node => this.caseManualSolveVisualStableKeyPart(node?.ownerTxGroupId || node?.sourceTransactionHash || ''))
        .filter(Boolean);
      if (!relatedOwners.length) return '';
      const txNodes = nodes.filter(node => String(node?.type || '').toLowerCase() === 'tx');
      for (const txNode of txNodes) {
        const txIdentifiers = [txNode.id, txNode.groupId, txNode.fullHash, txNode.transactionHash, txNode.hash]
          .map(value => this.caseManualSolveVisualStableKeyPart(value))
          .filter(Boolean);
        if (!txIdentifiers.some(id => relatedOwners.includes(id))) continue;
        const color = this.caseManualSolveNormalizeColor(colorBucket.nodeColors?.[this.getManualSolveVisualNodeColorKey(txNode, visual)]?.fill);
        if (color) return color;
      }
      return '';
    },

    caseManualSolveVisualNodeFillStyle(node = {}) {
      if (!this.caseManualSolveVisualNodeIsColorable(node)) return {};
      const heatMapColor = this.caseManualSolveHeatMapNodeColor(node);
      if (heatMapColor) return { fill: heatMapColor };
      const color = this.getManualSolveVisualNodeColor(node, this.caseInvestigationVisualizerCurrentVisual);
      if (color?.fill) return { fill: color.fill };
      if (this.caseManualSolveVisualNodeIsGroupNode(node)) return { fill: this.caseManualSolveVisualGroupResolvedColor(node.groupColor || node.groupRecord?.color || '', { treatLegacyDefault: true }) };
      return {};
    },

    caseManualSolveVisualEdgeDisplayColor(edge = {}, visual = this.caseInvestigationVisualizerCurrentVisual) {
      const heatMapColor = this.caseManualSolveHeatMapEdgeColor(edge);
      if (heatMapColor) return heatMapColor;
      const configuredColor = this.getManualSolveVisualEdgeColor(edge, visual);
      if (configuredColor) return configuredColor;
      if (edge?.isBridgeEdge || edge?.bridge || edge?.caseManualBridgeLinearized) return '#7c3aed';
      return '';
    },


    caseManualSolveVisualEdgeHasBridgeBadge(edge = {}) {
      return Boolean(edge?.bridgeBadge || edge?.bridge || edge?.isBridgeEdge || edge?.caseManualBridgeLinearized);
    },

    caseManualSolveVisualBridgeEdgeBadgeText(edge = {}) {
      const label = this.caseManualNormalizeText(edge?.bridgeLabel || edge?.bridgeBadge?.label || 'BRIDGE') || 'BRIDGE';
      return label.length > 18 ? `${label.slice(0, 15)}…` : label.toUpperCase() === 'BRIDGE' ? 'BRIDGE' : label;
    },

    caseManualSolveVisualBridgeEdgeBadgeTransform(edge = {}) {
      const nodeMap = this.caseInvestigationVisualizerNodeMap || new Map();
      const fromNode = nodeMap.get(edge?.from) || null;
      const toNode = nodeMap.get(edge?.to) || null;
      const fallback = edge?.bridgeBadge || {};
      const x = Number.isFinite(Number(fallback.x))
        ? Number(fallback.x)
        : ((Number(fromNode?.x) || 0) + (Number(toNode?.x) || 0)) / 2;
      const y = Number.isFinite(Number(fallback.y))
        ? Number(fallback.y)
        : (((Number(fromNode?.y) || 0) + (Number(toNode?.y) || 0)) / 2) - 34;
      return `translate(${x} ${y})`;
    },
    caseManualSolveVisualEdgeStyle(edge = {}) {
      const color = this.caseManualSolveVisualEdgeDisplayColor(edge, this.caseInvestigationVisualizerCurrentVisual);
      return color ? { stroke: color } : {};
    },

    caseManualSolveVisualColorMarkerId(color = '') {
      const normalized = this.caseManualSolveNormalizeColor(color);
      return normalized ? `case-manual-solve-visual-arrow-${normalized.slice(1)}` : 'case-manual-solve-visual-arrow-default';
    },

    caseManualSolveVisualEdgeMarkerEnd(edge = {}) {
      const color = this.caseManualSolveVisualEdgeDisplayColor(edge, this.caseInvestigationVisualizerCurrentVisual);
      if (color) return `url(#${this.caseManualSolveVisualColorMarkerId(color)})`;
      if (edge?.isBridgeEdge || edge?.bridge || edge?.caseManualBridgeLinearized) return `url(#${this.caseManualSolveVisualColorMarkerId('#7c3aed')})`;
      return edge?.dashed ? 'url(#case-investigation-visual-arrow-muted-live)' : 'url(#case-investigation-visual-arrow-live)';
    },

    collectManualSolveVisualEdgesForSelectedNodeColors(nodes = [], visual = this.caseInvestigationVisualizerCurrentVisual) {
      const selectedNodesForGroups = Array.isArray(nodes) ? nodes : [];
      const selectedGroupNodeIds = new Set(selectedNodesForGroups.filter(node => this.caseManualSolveVisualNodeIsGroupNode(node)).map(node => node.id).filter(Boolean));
      if (selectedGroupNodeIds.size) {
        return (Array.isArray(this.caseInvestigationVisualizerEdges) ? this.caseInvestigationVisualizerEdges : [])
          .filter(edge => selectedGroupNodeIds.has(edge?.from) || selectedGroupNodeIds.has(edge?.to));
      }
      const selectedTxNodes = selectedNodesForGroups
        .filter(node => String(node?.type || '').toLowerCase() === 'tx');
      if (!selectedTxNodes.length) return [];
      const sourceEdges = Array.isArray(this.caseInvestigationVisualizerEdges) ? this.caseInvestigationVisualizerEdges : [];
      const visualNodes = Array.isArray(visual?.nodes) ? visual.nodes : this.caseInvestigationVisualizerNodes;
      const nodeById = new Map(visualNodes.map(node => [node?.id, node]).filter(([id]) => !!id));
      const relatedNodeIds = new Set();
      const relatedTxIdentifiers = new Set();

      selectedTxNodes.forEach((node) => {
        if (node?.id) relatedNodeIds.add(node.id);
        [node.id, node.groupId, node.fullHash, node.transactionHash, node.txHash, node.hash, node.sourceTransactionHash]
          .map(value => this.caseManualSolveVisualStableKeyPart(value))
          .filter(Boolean)
          .forEach(value => relatedTxIdentifiers.add(value));
      });

      visualNodes.forEach((candidate) => {
        if (!candidate?.id) return;
        const candidateIdentifiers = [candidate.ownerTxGroupId, candidate.sourceTransactionHash, candidate.transactionHash, candidate.txHash, candidate.hash]
          .map(value => this.caseManualSolveVisualStableKeyPart(value))
          .filter(Boolean);
        if (candidateIdentifiers.some(value => relatedTxIdentifiers.has(value))) {
          relatedNodeIds.add(candidate.id);
        }
      });

      const relatedEdges = new Map();
      let changed = true;
      while (changed) {
        changed = false;
        sourceEdges.forEach((edge) => {
          if (!edge?.from || !edge?.to) return;
          const touchesRelated = relatedNodeIds.has(edge.from) || relatedNodeIds.has(edge.to);
          if (!touchesRelated) return;
          const edgeKey = edge.id || `${edge.from}->${edge.to}`;
          if (!relatedEdges.has(edgeKey)) {
            relatedEdges.set(edgeKey, edge);
            changed = true;
          }
          const fromNode = nodeById.get(edge.from);
          const toNode = nodeById.get(edge.to);
          [fromNode, toNode].forEach((node) => {
            if (!node?.id) return;
            const type = String(node.type || '').toLowerCase();
            if (type === 'amount' || type === 'tx') {
              if (!relatedNodeIds.has(node.id)) {
                relatedNodeIds.add(node.id);
                changed = true;
              }
            }
          });
        });
      }
      return Array.from(relatedEdges.values());
    },
    collectManualSolveVisualNodesForSelectedNodeColorFill(nodes = [], visual = this.caseInvestigationVisualizerCurrentVisual) {
      const selectedNodes = (Array.isArray(nodes) ? nodes : [])
        .filter(node => this.caseManualSolveVisualNodeIsColorable(node));
      const visualNodes = Array.isArray(visual?.nodes) ? visual.nodes : this.caseInvestigationVisualizerNodes;
      const output = new Map();
      selectedNodes.forEach((node) => {
        if (node?.id) output.set(node.id, node);
        if (String(node?.type || '').toLowerCase() !== 'tx') return;
        const txIdentifiers = [node.id, node.groupId, node.fullHash, node.transactionHash, node.txHash, node.hash, node.sourceTransactionHash]
          .map(value => this.caseManualSolveVisualStableKeyPart(value))
          .filter(Boolean);
        visualNodes.forEach((candidate) => {
          if (!this.caseManualSolveVisualNodeIsColorable(candidate)) return;
          if (String(candidate?.type || '').toLowerCase() !== 'amount') return;
          const candidateIdentifiers = [candidate.ownerTxGroupId, candidate.sourceTransactionHash, candidate.transactionHash, candidate.txHash, candidate.hash]
            .map(value => this.caseManualSolveVisualStableKeyPart(value))
            .filter(Boolean);
          if (candidateIdentifiers.some(id => txIdentifiers.includes(id)) && candidate.id) output.set(candidate.id, candidate);
        });
      });
      return Array.from(output.values());
    },
    writeCaseManualSolveVisualColorBucket(visual, bucket, reason = 'Manual Solve visual colors updated.') {
      const visualKey = this.getCaseManualSolveVisualColorVisualKey(visual);
      const overrides = this.getCaseManualSolveVisualColorOverrides(true) || { schemaVersion: 1, byVisualKey: {} };
      const byVisualKey = { ...(overrides.byVisualKey || {}) };
      if (bucket && (Object.keys(bucket.nodeColors || {}).length || Object.keys(bucket.edgeColors || {}).length)) {
        byVisualKey[visualKey] = {
          schemaVersion: 1,
          nodeColors: { ...(bucket.nodeColors || {}) },
          edgeColors: { ...(bucket.edgeColors || {}) },
          updatedAtUtc: new Date().toISOString()
        };
      } else {
        delete byVisualKey[visualKey];
      }
      this.caseSupportState = {
        ...(this.caseSupportState || createDefaultCaseSupportState()),
        manualSolveVisualColorOverrides: {
          schemaVersion: 1,
          byVisualKey
        }
      };
      this.touchCaseManualSolveVisualColorSupportMetadata(reason);
    },

    touchCaseManualSolveVisualColorSupportMetadata(reason = 'Manual Solve visual colors updated.', options = {}) {
      const now = new Date().toISOString();
      const state = this.caseSupportState || createDefaultCaseSupportState();
      this.caseSupportState = {
        ...state,
        dirty: true,
        dirtySinceUtc: options.preserveDirtySince ? (state.dirtySinceUtc || now) : (state.dirtySinceUtc || now),
        lastMutation: {
          source: 'manual_solve_visual_color_metadata',
          details: { reason },
          utc: now,
          revision: Number(state.caseDataRevision) || 0
        }
      };
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
    },

    toggleCaseManualSolveVisualColorPanel() {
      this.caseManualSolveVisualColorPanelOpen = !this.caseManualSolveVisualColorPanelOpen;
      if (this.caseManualSolveVisualColorPanelOpen) this.caseManualSolveVisualColorStatus = '';
    },

    openCaseManualSolveVisualColorPicker() {
      if (this.caseInvestigationVisualizerInteractionLocked) return;
      const pickerRef = this.$refs.caseManualSolveVisualColorPicker;
      const picker = Array.isArray(pickerRef) ? pickerRef[0] : pickerRef;
      if (picker && typeof picker.click === 'function') picker.click();
    },

    applyManualSolveSelectedNodeColor(color = '') {
      if (!this.caseInvestigationVisualizerManualStartMode) return;
      const normalized = this.caseManualSolveNormalizeColor(color);
      if (!normalized) {
        this.caseManualSolveVisualColorStatus = 'Choose a valid hex color first.';
        return;
      }
      const selectedNodes = this.caseInvestigationVisualizerSelectedNodes;
      if (!selectedNodes.length) {
        this.caseManualSolveVisualColorStatus = 'Select one or more visual nodes first.';
        return;
      }
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      const before = this.captureCaseInvestigationVisualizerVisualEditState();
      const bucket = this.getCaseManualSolveVisualColorBucket(visual, true) || { schemaVersion: 1, nodeColors: {}, edgeColors: {} };
      const now = new Date().toISOString();
      const nodeColors = { ...(bucket.nodeColors || {}) };
      const edgeColors = { ...(bucket.edgeColors || {}) };
      const fillNodes = this.collectManualSolveVisualNodesForSelectedNodeColorFill(selectedNodes, visual);
      if (!fillNodes.length) {
        this.caseManualSolveVisualColorStatus = 'Select an address, transaction, or amount node to recolor.';
        return;
      }
      fillNodes.forEach((node) => {
        const key = this.getManualSolveVisualNodeColorKey(node, visual);
        if (!key) return;
        nodeColors[key] = {
          ...(nodeColors[key] || {}),
          fill: normalized,
          updatedAtUtc: now
        };
      });
      const relatedEdges = this.collectManualSolveVisualEdgesForSelectedNodeColors(selectedNodes, visual);
      relatedEdges.forEach((edge) => {
        const key = this.getManualSolveVisualEdgeColorKey(edge, visual);
        if (!key) return;
        edgeColors[key] = {
          ...(edgeColors[key] || {}),
          stroke: normalized,
          updatedAtUtc: now
        };
      });
      this.writeCaseManualSolveVisualColorBucket(visual, { schemaVersion: 1, nodeColors, edgeColors }, 'Manual Solve selected visual nodes recolored.');
      this.pushCaseInvestigationVisualizerVisualEditHistory(before);
      const edgeCount = relatedEdges.length;
      this.caseManualSolveVisualColorStatus = `Colored ${fillNodes.length} node${fillNodes.length === 1 ? '' : 's'}${edgeCount ? ` and ${edgeCount} related arrow${edgeCount === 1 ? '' : 's'}` : ''}.`;
      this.caseInvestigationVisualizerStatus = 'Manual Solve visual colors updated. Main Data and Tool 88 state were not changed.';
    },

    resetManualSolveSelectedNodeColors() {
      if (!this.caseInvestigationVisualizerManualStartMode) return;
      const selectedNodes = this.caseInvestigationVisualizerSelectedNodes;
      if (!selectedNodes.length) return;
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      const bucket = this.getCaseManualSolveVisualColorBucket(visual, false);
      if (!bucket) return;
      const before = this.captureCaseInvestigationVisualizerVisualEditState();
      const nodeColors = { ...(bucket.nodeColors || {}) };
      const edgeColors = { ...(bucket.edgeColors || {}) };
      let removedNodes = 0;
      let removedEdges = 0;
      this.collectManualSolveVisualNodesForSelectedNodeColorFill(selectedNodes, visual).forEach((node) => {
        const key = this.getManualSolveVisualNodeColorKey(node, visual);
        if (key && nodeColors[key]) {
          delete nodeColors[key];
          removedNodes += 1;
        }
      });
      this.collectManualSolveVisualEdgesForSelectedNodeColors(selectedNodes, visual).forEach((edge) => {
        const key = this.getManualSolveVisualEdgeColorKey(edge, visual);
        if (key && edgeColors[key]) {
          delete edgeColors[key];
          removedEdges += 1;
        }
      });
      if (!removedNodes && !removedEdges) {
        this.caseManualSolveVisualColorStatus = 'The selected nodes do not have custom visual colors.';
        return;
      }
      this.writeCaseManualSolveVisualColorBucket(visual, { schemaVersion: 1, nodeColors, edgeColors }, 'Manual Solve selected visual node colors reset.');
      this.pushCaseInvestigationVisualizerVisualEditHistory(before);
      this.caseManualSolveVisualColorStatus = `Reset ${removedNodes} selected node color${removedNodes === 1 ? '' : 's'}${removedEdges ? ` and ${removedEdges} related arrow color${removedEdges === 1 ? '' : 's'}` : ''}.`;
      this.caseInvestigationVisualizerStatus = 'Manual Solve selected visual colors reset. Main Data was not changed.';
    },

    resetManualSolveAllNodeColorsForCurrentVisual() {
      if (!this.caseInvestigationVisualizerManualStartMode) return;
      const visual = this.caseInvestigationVisualizerCurrentVisual;
      const bucket = this.getCaseManualSolveVisualColorBucket(visual, false);
      if (!bucket || (!Object.keys(bucket.nodeColors || {}).length && !Object.keys(bucket.edgeColors || {}).length)) {
        this.caseManualSolveVisualColorStatus = 'This visual does not have custom colors.';
        return;
      }
      const before = this.captureCaseInvestigationVisualizerVisualEditState();
      const nodeCount = Object.keys(bucket.nodeColors || {}).length;
      const edgeCount = Object.keys(bucket.edgeColors || {}).length;
      this.writeCaseManualSolveVisualColorBucket(visual, null, 'Manual Solve all visual colors reset for current visual.');
      this.pushCaseInvestigationVisualizerVisualEditHistory(before);
      this.caseManualSolveVisualColorStatus = `Reset all custom colors in this visual (${nodeCount} node${nodeCount === 1 ? '' : 's'}, ${edgeCount} arrow${edgeCount === 1 ? '' : 's'}).`;
      this.caseInvestigationVisualizerStatus = 'Manual Solve visual colors reset for the current visual. Main Data was not changed.';
    },

    caseFlowVisualEdgeClass(edge = {}) {
      const classes = ['case-flow-visual-edge'];
      if (edge?.dashed) classes.push('is-dashed');
      if (edge?.isBridgeEdge || edge?.bridge || edge?.caseManualBridgeLinearized) classes.push('is-bridge-edge');
      if (edge?.isSwapEdge || edge?.swap || edge?.caseManualSwapLinearized) classes.push('is-swap-edge');
      if (edge?.transactionOnly || edge?.caseInvestigationTransactionOnly) classes.push('case-flow-visual-edge--transaction-only');
      return classes;
    },

    caseManualVisualNodeLabelCategory(node = {}) {
      const rawCategory = this.caseManualNormalizeText(node?.labelCategory || '').replace(/[^a-z0-9_-]+/giu, '-').toLowerCase();
      const candidate = node?.caseCandidate || node?.candidate || node || {};
      if (this.caseManualStartCandidateHasAddressPoisoning(candidate) || rawCategory === 'address-poisoning') return 'address-poisoning';
      if (this.caseManualVisualNodeHasMeaningfulEndpointAttribution(node) || this.caseManualStartCandidateHasMeaningfulEndpointAttribution(candidate)) {
        if (rawCategory === 'unknown-service' || rawCategory === 'path-ending' || candidate?.isPathEnding) return 'path-ending';
      }
      const hasTransientAttentionStyling = rawCategory === 'unknown-service'
        || rawCategory === 'path-ending'
        || Boolean(candidate?.isPathEnding)
        || Boolean(node?.alertRing);
      if (hasTransientAttentionStyling && this.caseManualStartCandidateAttentionVisualIsDeferred(candidate)) return 'regular';
      if (this.caseManualVisualNodeHasPendingUnknownServicePathEnding(node)) return 'unknown-service';
      return rawCategory || 'regular';
    },

    caseManualVisualNodeAlertRing(node = {}) {
      const candidate = node?.caseCandidate || node?.candidate || node || {};
      if (this.caseManualStartCandidateHasAddressPoisoning(candidate) || this.caseManualVisualNodeLabelCategory(node) === 'address-poisoning') return true;
      if (this.caseManualVisualNodeHasMeaningfulEndpointAttribution(node) || this.caseManualStartCandidateHasMeaningfulEndpointAttribution(candidate)) return false;
      const rawCategory = this.caseManualNormalizeText(node?.labelCategory || '').replace(/[^a-z0-9_-]+/giu, '-').toLowerCase();
      const mayShowAttentionRing = Boolean(candidate?.isPathEnding || node?.alertRing || rawCategory === 'unknown-service' || rawCategory === 'path-ending');
      if (mayShowAttentionRing && this.caseManualStartCandidateAttentionVisualIsDeferred(candidate)) return false;
      if (this.caseManualVisualNodeHasPendingUnknownServicePathEnding(node)) return true;
      return this.caseManualVisualNodeLabelCategory(node) === 'unknown-service' && Boolean(node?.alertRing);
    },

    caseManualVisualNodeLabelLines(node = {}) {
      const lines = Array.isArray(node?.labelLines) ? node.labelLines.filter(Boolean) : [];
      const candidate = node?.caseCandidate || node?.candidate || node || {};
      const meaningfulEndpointLabel = this.caseManualVisualNodeMeaningfulEndpointAttributionLabel(node) || this.caseManualCandidateMeaningfulEndpointAttributionLabel(candidate);
      if (meaningfulEndpointLabel) {
        const shortLabel = this.caseManualShortAttributionLabel(meaningfulEndpointLabel, 30);
        const cleaned = lines.filter((line) => {
          const cleanLine = this.caseManualNormalizeText(line);
          return !/^unknown$/iu.test(cleanLine)
            && !/^path\s*ends$/iu.test(cleanLine)
            && !/unresolved\s+ending/iu.test(cleanLine)
            && !/hot[-\s]?wallet[-\s]?like\s+ending/iu.test(cleanLine);
        });
        if (shortLabel && !cleaned.some(line => this.caseManualCleanAttributionLabel(line).toLowerCase() === this.caseManualCleanAttributionLabel(shortLabel).toLowerCase())) cleaned.unshift(shortLabel);
        return cleaned;
      }
      const hasAttentionLabel = Boolean(candidate?.isPathEnding) || lines.some((line) => {
        const cleanLine = this.caseManualNormalizeText(line);
        return /^unknown$/iu.test(cleanLine)
          || /^path\s*ends$/iu.test(cleanLine)
          || /unresolved\s+(?:ending|source)/iu.test(cleanLine)
          || /hot[-\s]?wallet[-\s]?like\s+ending/iu.test(cleanLine)
          || /manual\s+work/iu.test(cleanLine);
      });
      if (hasAttentionLabel && this.caseManualStartCandidateAttentionVisualIsDeferred(candidate)) {
        return lines.filter((line) => {
          const cleanLine = this.caseManualNormalizeText(line);
          return !/^unknown$/iu.test(cleanLine)
            && !/^path\s*ends$/iu.test(cleanLine)
            && !/unresolved\s+(?:ending|source)/iu.test(cleanLine)
            && !/hot[-\s]?wallet[-\s]?like\s+ending/iu.test(cleanLine)
            && !/manual\s+work/iu.test(cleanLine);
        });
      }
      if (this.caseManualVisualNodeHasPendingUnknownServicePathEnding(node)) {
        const cleaned = lines.filter((line) => {
          const cleanLine = this.caseManualNormalizeText(line);
          return !/^unknown$/iu.test(cleanLine)
            && !/^path\s*ends$/iu.test(cleanLine)
            && !/unresolved\s+ending/iu.test(cleanLine)
            && !/hot[-\s]?wallet[-\s]?like\s+ending/iu.test(cleanLine);
        });
        if (!cleaned.some(line => /hot[-\s]?wallet|service\s*\/\s*hot/iu.test(this.caseManualNormalizeText(line)))) cleaned.push('Hot-wallet-like ending');
        return ['Unknown', ...cleaned];
      }
      if (!lines.length) return [];
      if (this.caseManualStartCandidateHasMeaningfulEndpointAttribution(candidate)) {
        return lines.filter(line => !/^unknown$/iu.test(this.caseManualNormalizeText(line)));
      }
      return lines;
    },

    caseFlowVisualNodeClass(node = {}) {
      const type = (this.caseManualNormalizeText(node.type || 'node') || 'node').toLowerCase();
      const category = this.caseManualVisualNodeLabelCategory(node);
      const selected = this.caseFlowVisualsSelectedNodeId && node.id === this.caseFlowVisualsSelectedNodeId;
      return [
        'case-flow-visual-node',
        `case-flow-visual-node--${type}`,
        category ? `case-flow-visual-node--${category}` : '',
        selected ? 'is-selected' : ''
      ].filter(Boolean);
    },

    caseInvestigationVisualizerNodeIsSingleSelected(node = {}) {
      if (!node?.id || node.type !== 'address') return false;
      if (this.caseInvestigationVisualizerSelectedCount !== 1) return false;
      return this.caseInvestigationVisualizerSelectedNodeIdSet?.has?.(node.id) || node.id === this.caseInvestigationVisualizerSelectedNodeId;
    },

    toggleCaseManualSolveVisualLegend() {
      if (!this.caseInvestigationVisualizerCurrentVisual) return;
      this.caseManualSolveVisualLegendOpen = !this.caseManualSolveVisualLegendOpen;
    },

    closeCaseManualSolveVisualLegend() {
      this.caseManualSolveVisualLegendOpen = false;
    },

    caseManualSolveVisualLegendItemIsEnabled(item = {}) {
      const condition = this.caseManualNormalizeText(item.enabledWhen || 'always').toLowerCase();
      if (!condition || condition === 'always') return true;
      const nodes = Array.isArray(this.caseInvestigationVisualizerVisibleNodes) ? this.caseInvestigationVisualizerVisibleNodes : [];
      const edges = Array.isArray(this.caseInvestigationVisualizerEdges) ? this.caseInvestigationVisualizerEdges : [];
      const visual = this.caseInvestigationVisualizerCurrentVisual || {};
      const entries = Array.isArray(visual.entries) ? visual.entries : [];
      const normalize = value => this.caseManualNormalizeText(value).toLowerCase();
      const nodeText = node => [
        node?.labelCategory,
        node?.badge,
        node?.visualEndpointStatus,
        node?.caseManualSwapLinearized ? 'swap' : '',
        node?.caseManualSwapContinuation ? 'swap' : '',
        node?.caseCandidate?.visualEndpointStatus,
        node?.caseCandidate?.systemNotes,
        node?.caseCandidate?.caseNotes,
        node?.caseCandidate?.attribution,
        node?.candidate?.visualEndpointStatus,
        node?.candidate?.systemNotes,
        node?.candidate?.caseNotes,
        node?.candidate?.attribution
      ].map(normalize).filter(Boolean).join(' ');
      const entryText = entry => [
        entry?.labelCategory,
        entry?.visualEndpointStatus,
        entry?.systemNotes,
        entry?.caseNotes,
        entry?.attribution,
        Array.isArray(entry) ? entry[CASE_MANUAL_START_COL.SYSTEM_NOTES] : '',
        Array.isArray(entry) ? entry[CASE_MANUAL_START_COL.ATTRIBUTION] : '',
        Array.isArray(entry) ? entry[CASE_MANUAL_START_COL.CASE_NOTES] : ''
      ].map(normalize).filter(Boolean).join(' ');

      if (condition === 'dashed-edge') return edges.some(edge => edge?.dashed || /dashed|muted|secondary/iu.test(normalize(edge?.className || edge?.kind || edge?.relationship)));
      if (condition === 'swap') {
        return nodes.some(node => /\bswap\b|swap-related|post-swap|pre-swap/iu.test(nodeText(node)))
          || edges.some(edge => edge?.caseManualSwapLinearized || /\bswap\b/iu.test(normalize(edge?.className || edge?.kind || edge?.relationship || edge?.notes)))
          || entries.some(entry => /\bswap\b|swapped|post-swap|pre-swap/iu.test(entryText(entry)));
      }
      if (condition === 'bridge') {
        return nodes.some(node => /\bbridge\b|bridge-related|bridged|bridge-out|bridge-in/iu.test(nodeText(node)))
          || edges.some(edge => /\bbridge\b|bridge-edge/iu.test(normalize(edge?.className || edge?.kind || edge?.relationship || edge?.notes)))
          || entries.some(entry => /\bbridge\b|bridged|bridge-out|bridge-in/iu.test(entryText(entry)));
      }
      if (condition === 'hot-wallet') {
        return nodes.some(node => /unknown-service|hot[-_\s]?wallet|deposit[-_\s]?hot[-_\s]?wallet|deposit[-_\s]?endpoint/iu.test(nodeText(node)))
          || entries.some(entry => /unknown-service|hot[-_\s]?wallet|deposit[-_\s]?hot[-_\s]?wallet|deposit[-_\s]?endpoint/iu.test(entryText(entry)));
      }
      return false;
    },

    caseInvestigationVisualizerNodeClass(node = {}) {
      const type = (this.caseManualNormalizeText(node.type || 'node') || 'node').toLowerCase();
      const category = this.caseManualVisualNodeLabelCategory(node);
      const selected = this.caseInvestigationVisualizerSelectedNodeIdSet?.has?.(node.id) || node.id === this.caseInvestigationVisualizerSelectedNodeId;
      const heatMapStat = this.caseManualSolveHeatMapActive ? this.caseManualSolveHeatMapNodeStat(node, this.caseManualSolveHeatMapState) : null;
      return [
        'case-flow-visual-node',
        'case-investigation-visualizer-node',
        `case-flow-visual-node--${type}`,
        category ? `case-flow-visual-node--${category}` : '',
        selected ? 'is-selected' : '',
        this.caseInvestigationVisualizerNodeIsSingleSelected(node) ? 'is-single-selected' : '',
        this.caseManualSolveHeatMapActive && this.caseManualSolveVisualNodeIsColorable(node) ? 'is-heat-map-colored' : '',
        heatMapStat?.statusClass || (this.caseManualSolveHeatMapActive && this.caseManualSolveVisualNodeIsColorable(node) ? 'is-heat-map-error' : '')
      ].filter(Boolean);
    },

    caseWorkflowUiNextTick() {
      return new Promise((resolve) => {
        this.$nextTick(() => resolve());
      });
    },

    showWorkflowUiToast(message = '') {
      const text = normalizeCell(message);
      if (!text) return;
      if (typeof this.showCaseToast === 'function') {
        this.showCaseToast(text);
        return;
      }
      this.caseToast = text;
      if (this.caseToastTimer) window.clearTimeout(this.caseToastTimer);
      this.caseToastTimer = window.setTimeout(() => {
        this.caseToast = '';
        this.caseToastTimer = null;
      }, 5200);
    },

    workflowUiSetModalVisible(modalKey = '', visible = false) {
      if (!modalKey) return;
      try {
        this[modalKey] = !!visible;
      } catch (error) {
        console.warn(`Case Buddy could not set workflow modal flag ${modalKey}.`, error);
      }
      if (this.$data && Object.prototype.hasOwnProperty.call(this.$data, modalKey)) {
        try {
          this.$data[modalKey] = !!visible;
        } catch (error) {
          console.warn(`Case Buddy could not set workflow modal data flag ${modalKey}.`, error);
        }
      }
    },

    closeWorkflowUiModalWithToast(modalKey = '', message = '', options = {}) {
      const closeKnownModal = () => this.workflowUiSetModalVisible(modalKey, false);
      closeKnownModal();
      this.$nextTick(() => closeKnownModal());
      if (typeof window !== 'undefined') {
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(closeKnownModal);
        }
        [0, 25, 75, 150, 300, 650, 1200, 1800].forEach((delay) => {
          window.setTimeout(closeKnownModal, delay);
        });
      }
      this.showWorkflowUiToast(message);
      if (options.nextStepKey) {
        this.scheduleCaseWorkflowStepScrollByKey(options.nextStepKey, {
          skipCompletedTarget: options.skipCompletedTarget !== false,
          delayMs: options.delayMs ?? 280,
          block: options.block || 'center'
        });
      }
    },

    async refreshWorkflowUiModalRows(refreshMethodName = '') {
      if (refreshMethodName && typeof this[refreshMethodName] === 'function') {
        try {
          const result = this[refreshMethodName]();
          if (result && typeof result.then === 'function') await result;
        } catch (error) {
          console.warn(`Case Buddy could not refresh workflow modal rows with ${refreshMethodName}.`, error);
        }
      }
      await this.caseWorkflowUiNextTick();
    },

    workflowNetworkLookupLooksComplete() {
      return this.networkLookupRows.length > 0 && this.networkLookupPendingCount === 0;
    },

    workflowDateLookupLooksComplete() {
      return this.dateLookupRows.length > 0 && this.dateLookupPendingCount === 0;
    },

    workflowMainPathLabelsLookComplete() {
      return this.mainPathLabelRows.length > 0 && this.mainPathLabelNeedsReviewCount === 0 && this.mainPathLabelChangeCount === 0;
    },

    completeNetworkLookupModalAndContinue() {
      this.closeWorkflowUiModalWithToast(
        'showNetworkLookupModal',
        'Transaction networks are complete. Continue to UTC dates.',
        { nextStepKey: 'dates' }
      );
    },

    async runNetworkDiscoveryLookupAndContinue(...args) {
      if (this.workflowNetworkLookupLooksComplete()) {
        this.completeNetworkLookupModalAndContinue();
        return true;
      }
      if (this.networkLookupSelectedCount === 0) {
        this.networkLookupStatus = this.networkLookupPendingCount > 0
          ? 'Select at least one missing-network row before running the lookup.'
          : 'No network lookup is needed for the currently detected main transactions.';
        return false;
      }
      const result = await this.callFirstCaseBuddyOriginalMethod('runNetworkDiscoveryLookup', args);
      await this.refreshWorkflowUiModalRows('refreshNetworkLookupRows');
      if (this.workflowNetworkLookupLooksComplete()) {
        this.closeWorkflowUiModalWithToast(
          'showNetworkLookupModal',
          'Network lookup succeeded. All detected starting/main transactions now have Chain Id and Chain Name.',
          { nextStepKey: 'dates' }
        );
      }
      return result;
    },

    async runNetworkDiscoveryLookup(...args) {
      return this.runNetworkDiscoveryLookupAndContinue(...args);
    },

    async runDateLookupWorkflowAndContinue(...args) {
      if (this.dateLookupSortOnlyMode) {
        return this.completeDateLookupSortAndContinue(...args);
      }
      if (this.dateLookupSelectedCount === 0) {
        this.dateLookupStatus = this.dateLookupPendingCount > 0
          ? 'Select at least one missing-date row before running the date lookup.'
          : 'No date lookup is needed for the currently detected main transactions.';
        return false;
      }
      const result = await this.callFirstCaseBuddyOriginalMethod('runMissingDateLookup', args);
      await this.refreshWorkflowUiModalRows('refreshDateLookupRows');
      if (this.workflowDateLookupLooksComplete()) {
        return this.completeDateLookupSortAndContinue();
      }
      this.dateLookupStatus = 'Date lookup finished, but at least one detected main transaction still needs a valid UTC date.';
      return result;
    },

    async runMissingDateLookup(...args) {
      return this.runDateLookupWorkflowAndContinue(...args);
    },

    async completeDateLookupSortAndContinue(...args) {
      const result = await this.callFirstCaseBuddyOriginalMethod('sortMainEntriesByDateFromDateModal', args);
      await this.reconcileCaseRootChronologyAndPathLabels({ source: 'date-modal-complete-sort', sort: false, silent: true });
      await this.refreshWorkflowUiModalRows('refreshDateLookupRows');
      this.closeWorkflowUiModalWithToast(
        'showDateLookupModal',
        this.dateLookupSortPreview?.chronologyPass
          ? 'Main transactions are confirmed in UTC date order. Continue to assigning path labels.'
          : 'Main transactions were sorted by UTC date. Continue to assigning path labels.',
        { nextStepKey: 'labels' }
      );
      return result;
    },

    async sortMainEntriesByDateFromDateModal(...args) {
      const sortOnlyMode = !!this.dateLookupSortOnlyMode;
      const result = await this.callFirstCaseBuddyOriginalMethod('sortMainEntriesByDateFromDateModal', args);
      await this.reconcileCaseRootChronologyAndPathLabels({ source: 'date-modal-sort', sort: false, silent: false });
      await this.refreshWorkflowUiModalRows('refreshDateLookupRows');
      if (this.showDateLookupModal && (sortOnlyMode || this.workflowDateLookupLooksComplete())) {
        this.closeWorkflowUiModalWithToast(
          'showDateLookupModal',
          'Date ordering step complete. Continue to assigning path labels.',
          { nextStepKey: 'labels' }
        );
      }
      return result;
    },

    completeMainPathLabelModalAndContinue() {
      this.closeWorkflowUiModalWithToast(
        'showMainPathLabelModal',
        'Main transaction labels are current. Continue to case-details review.',
        { nextStepKey: 'caseDetails' }
      );
    },

    async applyMainPathLabelsAndContinue(...args) {
      if (this.workflowMainPathLabelsLookComplete()) {
        this.completeMainPathLabelModalAndContinue();
        return true;
      }
      if (this.mainPathLabelRows.length === 0) {
        this.mainPathLabelStatus = 'No eligible top-level main transactions were detected.';
        return false;
      }
      if (this.mainPathLabelNeedsReviewCount > 0) {
        this.mainPathLabelStatus = 'Resolve the path-label review items before continuing.';
        return false;
      }
      const result = await this.callFirstCaseBuddyOriginalMethod('applyMainPathLabels', args);
      await this.refreshWorkflowUiModalRows('refreshMainPathLabelRows');
      if (this.workflowMainPathLabelsLookComplete()) {
        this.closeWorkflowUiModalWithToast(
          'showMainPathLabelModal',
          'Main transaction path labels were assigned. Continue to case-details review.',
          { nextStepKey: 'caseDetails' }
        );
      }
      return result;
    },

    async applyMainPathLabels(...args) {
      return this.applyMainPathLabelsAndContinue(...args);
    },

    isCaseDetailsReviewWorkflowStep(step = {}) {
      const tag = normalizeCell(step.tag || step.id || '').toUpperCase();
      const title = normalizeCell(step.title || '').toLowerCase();
      const description = normalizeCell(step.description || '').toLowerCase();
      if (tag.includes('CASE_DETAILS') || tag.includes('EARLY_NARRATIVE') || tag.includes('NARRATIVE_REVIEW')) return true;
      if (title.includes('review case details') || title.includes('early narrative')) return true;
      return description.includes('case details') && description.includes('review');
    },

    isCaseDetailsReviewRecordAction(action = {}, step = {}) {
      if (!this.isCaseDetailsReviewWorkflowStep(step)) return false;
      const haystack = [
        action.id,
        action.key,
        action.label,
        action.title,
        action.method,
        action.handler,
        action.action
      ].map(value => normalizeCell(value).toLowerCase()).join(' ');
      if (!haystack.trim()) return true;
      return /record|mark|complete|confirm|done|manual/u.test(haystack);
    },

    openCaseDetailsReviewAcknowledgement(step = null, action = null) {
      this.caseDetailsReviewPendingStep = step;
      this.caseDetailsReviewPendingAction = action;
      this.caseDetailsReviewAcknowledgementReadFiles = false;
      this.caseDetailsReviewAcknowledgementStatus = '';
      this.showCaseDetailsReviewAcknowledgementModal = true;
    },

    cancelCaseDetailsReviewAcknowledgement() {
      this.showCaseDetailsReviewAcknowledgementModal = false;
      this.caseDetailsReviewPendingStep = null;
      this.caseDetailsReviewPendingAction = null;
      this.caseDetailsReviewAcknowledgementReadFiles = false;
      this.caseDetailsReviewAcknowledgementStatus = '';
    },

    async confirmCaseDetailsReviewAcknowledgement() {
      if (!this.caseDetailsReviewAcknowledgementReadFiles) {
        this.caseDetailsReviewAcknowledgementStatus = 'Confirm that you read the external case files before recording this review.';
        return;
      }

      const pendingStep = this.caseDetailsReviewPendingStep;
      const pendingAction = this.caseDetailsReviewPendingAction;
      this.caseDetailsReviewAcknowledgementBypass = true;
      try {
        if (pendingAction) {
          await this.callCaseBuddyOriginalMethod(caseBuddyWorkflowMethods, 'runWorkflowAction', [pendingAction, pendingStep]);
        } else if (pendingStep) {
          await this.callCaseBuddyOriginalMethod(caseBuddyWorkflowMethods, 'toggleManualWorkflowComplete', [pendingStep]);
        }
        await this.caseWorkflowUiNextTick();
        this.showCaseDetailsReviewAcknowledgementModal = false;
        this.showCaseDetailsEditor = false;
        this.caseDetailsReviewPendingStep = null;
        this.caseDetailsReviewPendingAction = null;
        this.caseDetailsReviewAcknowledgementReadFiles = false;
        this.caseDetailsReviewAcknowledgementStatus = '';
        this.showWorkflowUiToast('Case details and early narrative review recorded. Continue to the next intake step.');
      } finally {
        this.caseDetailsReviewAcknowledgementBypass = false;
      }
    },

    caseBuddySplitRoleAttributionRow(row = []) {
      const source = this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0));
      if (this.caseManualIsHeaderRow(source)) {
        const header = this.caseManualEnsureRowWidth(source, CASE_MANUAL_START_COLUMN_COUNT);
        header[CASE_MANUAL_START_COL.ATTRIBUTION] = 'To Address Attribution';
        header[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] = 'From Address Attribution';
        return header;
      }
      return this.caseManualNormalizeAttributionRoleColumnsForRow(source);
    },

    async normalizeCaseBuddyAttributionColumnsForLogicalSheet(sheetKey = 'main', reason = 'Separated From and To address attribution columns.') {
      const matrix = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet(sheetKey) : [];
      if (!Array.isArray(matrix) || !matrix.length) return { changed: false, rows: [] };
      const rows = matrix.map(row => this.caseBuddySplitRoleAttributionRow(row));
      const changed = rows.some((row, rowIndex) => {
        const oldRow = this.caseManualEnsureRowWidth(matrix[rowIndex] || [], Math.max(CASE_MANUAL_START_COLUMN_COUNT, matrix[rowIndex]?.length || 0));
        return row.length !== oldRow.length || row.some((cell, cellIndex) => this.caseManualNormalizeText(cell) !== this.caseManualNormalizeText(oldRow[cellIndex]));
      });
      if (!changed) return { changed: false, rows };
      await this.replaceCaseManualStartLogicalSheetMatrix(sheetKey, rows, reason, { autosaveDelayMs: 900 });
      return { changed: true, rows };
    },

    isCaseWorkflowAddressAttributionFillStep(step = {}) {
      const tag = this.caseWorkflowStepIdentity(step);
      const title = this.caseWorkflowStepTitleText(step);
      const actionText = [
        step?.action,
        step?.actionId,
        step?.key,
        step?.method,
        step?.handler,
        step?.primaryAction,
        step?.status,
        step?.description,
        ...(Array.isArray(step?.actionIds) ? step.actionIds : []),
        ...(Array.isArray(step?.actions) ? step.actions.map(action => [action?.id, action?.key, action?.label, action?.method].join(' ')) : [])
      ].map(value => normalizeCell(value).toLowerCase()).join(' ');
      if (tag === 'ADDRESS_ATTRIBUTION_FILL') return true;
      return /fill(?:ing)?\s+(?:missing\s+)?address\s+attribution|address[_\s-]*attribution[_\s-]*fill|open[_\s-]*case[_\s-]*attribution[_\s-]*fill/.test(`${title} ${actionText}`);
    },

    getCaseWorkflowAddressAttributionFillState() {
      const analysis = this.buildCaseBuddyRoleAwareAddressAttributionAnalysis(this.getMatrixForLogicalSheet('main')) || {};
      const groups = Array.isArray(analysis.groups) ? analysis.groups : [];
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const basisRows = groups
        .map(group => ({
          role: normalizeCell(group.role),
          addressKey: normalizeCell(group.addressKey).toLowerCase(),
          occurrenceCount: Number(group.occurrenceCount) || 0,
          missingOccurrenceCount: Number(group.missingOccurrenceCount) || 0,
          currentAttributions: Array.isArray(group.currentAttributions) ? group.currentAttributions.map(value => normalizeAttributionValue(value)).filter(Boolean).sort() : []
        }))
        .sort((a, b) => `${a.role}|${a.addressKey}`.localeCompare(`${b.role}|${b.addressKey}`));
      const basisHash = `address-attribution-${this.caseWorkflowStableStringHash(JSON.stringify(basisRows))}`;
      return {
        ...analysis,
        currentRevision,
        basisHash,
        complete: (Number(analysis.missingCount) || 0) === 0,
        basisRows
      };
    },

    caseWorkflowAddressAttributionFillRecordIsCurrent(record = null, state = null) {
      if (!record || typeof record !== 'object') return false;
      const currentState = state || this.getCaseWorkflowAddressAttributionFillState();
      if ((Number(currentState.missingCount) || 0) > 0) return false;
      if (record.basisHash && currentState.basisHash && record.basisHash === currentState.basisHash) return true;
      const recordRevision = Number(record.revision ?? record.checkedRevision ?? record.caseDataRevision) || 0;
      if (recordRevision && recordRevision === (Number(currentState.currentRevision) || 0)) return true;
      return !!(record.completedAtUtc || record.checkedAtUtc || record.appliedAtUtc || record.reviewedAtUtc) && (Number(currentState.missingCount) || 0) === 0;
    },

    recordCaseWorkflowAddressAttributionFillCurrent(context = {}) {
      const state = context.state || this.getCaseWorkflowAddressAttributionFillState();
      if ((Number(state.missingCount) || 0) > 0) return null;
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const record = {
        ...(this.caseSupportState.workflowState?.addressAttributionFillReview || {}),
        completedAtUtc: now,
        checkedAtUtc: now,
        appliedAtUtc: context.appliedAtUtc || now,
        revision: currentRevision,
        basisHash: state.basisHash || '',
        roleAware: true,
        addressCount: Number(state.addressCount) || 0,
        groupCount: Number(state.groupCount) || 0,
        missingCount: 0,
        source: context.source || 'role-aware-address-attribution-fill',
        changedCount: Number(context.changedCount) || 0,
        unknownFilled: context.unknownFilled === true
      };
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) },
        autoStepCompletionByTag: { ...((this.caseSupportState.workflowState || {}).autoStepCompletionByTag || {}) }
      };
      workflow.addressAttributionFillReview = record;
      workflow.autoStepCompletionByTag.ADDRESS_ATTRIBUTION_FILL = {
        ...(workflow.autoStepCompletionByTag.ADDRESS_ATTRIBUTION_FILL || {}),
        ...record,
        tag: 'ADDRESS_ATTRIBUTION_FILL'
      };
      ['ADDRESS_ATTRIBUTION_FILL', 'ADDRESS_ATTRIBUTION_FILLED', 'ADDRESS_ATTRIBUTION_REVIEWED', 'ADDRESS_ATTRIBUTION_COMPLETED'].forEach((code) => {
        workflow.signalCompleteByCode[code] = {
          ...(workflow.signalCompleteByCode[code] || {}),
          completedAtUtc: workflow.signalCompleteByCode[code]?.completedAtUtc || now,
          checkedAtUtc: now,
          revision: currentRevision,
          basisHash: state.basisHash || '',
          source: context.source || 'role-aware-address-attribution-fill',
          stepTag: 'ADDRESS_ATTRIBUTION_FILL',
          roleAware: true
        };
      });
      const checkResult = {
        id: 'address_attribution_fill',
        status: 'pass',
        message: state.summary || 'All visible To/From address attribution cells are filled.',
        revision: currentRevision,
        checkedAtUtc: now,
        details: {
          roleAware: true,
          basisHash: state.basisHash || '',
          addressCount: Number(state.addressCount) || 0,
          groupCount: Number(state.groupCount) || 0,
          missingCount: 0,
          source: context.source || 'role-aware-address-attribution-fill'
        }
      };
      const checkResults = { ...(this.caseSupportState.checkResultsById || {}) };
      ['address_attribution_fill', 'address_attribution', 'address_attributions'].forEach((id) => {
        checkResults[id] = { ...(checkResults[id] || {}), ...checkResult, id };
      });
      this.caseSupportState.workflowState = workflow;
      this.caseSupportState.checkResultsById = checkResults;
      this.persistCaseSupportState?.();
      return record;
    },

    reconcileCaseWorkflowAddressAttributionFillCompletionState(context = {}) {
      const state = this.getCaseWorkflowAddressAttributionFillState();
      if ((Number(state.missingCount) || 0) > 0) return state;
      this.recordCaseWorkflowAddressAttributionFillCurrent({
        state,
        source: context.source || 'role-aware-address-attribution-reconcile'
      });
      return state;
    },

    applyCaseWorkflowAddressAttributionFillCurrentState(view = {}) {
      const state = this.getCaseWorkflowAddressAttributionFillState();
      if ((Number(state.missingCount) || 0) > 0) {
        return {
          ...view,
          complete: false,
          stale: false,
          blocked: false,
          status: view.status === 'complete' ? 'unchecked' : (view.status || 'unchecked'),
          statusLabel: `Needs K/X · ${state.missingCount} missing`,
          completionSummary: state.summary || `${state.missingCount} role-specific attribution cell${state.missingCount === 1 ? '' : 's'} still need Column K/Column X coverage.`,
          sources: {
            ...(view.sources || {}),
            roleAwareAttribution: true
          }
        };
      }
      const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      return {
        ...view,
        complete: true,
        stale: false,
        blocked: false,
        status: 'complete',
        statusLabel: view.statusLabel && /^complete/i.test(view.statusLabel) ? view.statusLabel : 'Complete',
        statusClass: 'workflow-step-card--complete',
        staleReason: '',
        completionSummary: state.summary || 'All visible To/From address attribution cells are filled.',
        sources: {
          ...(view.sources || {}),
          signal: true,
          check: true,
          manual: !!view.sources?.manual,
          roleAwareAttribution: true
        },
        checkResult: {
          ...(view.checkResult || {}),
          id: 'address_attribution_fill',
          status: 'pass',
          message: state.summary || 'All visible To/From address attribution cells are filled.',
          revision: Number(this.caseSupportState?.caseDataRevision) || 0,
          checkedAtUtc: now,
          details: {
            ...((view.checkResult || {}).details || {}),
            roleAware: true,
            basisHash: state.basisHash || '',
            missingCount: 0
          }
        }
      };
    },

    async runCaseWorkflowAddressAttributionFillAutomationStep(context = {}) {
      if (!this.caseAutomation?.skipResolvableReview && !context.force) return { handled: false, reason: 'skip-resolvable-reviews-off' };
      const stateBefore = this.getCaseWorkflowAddressAttributionFillState();
      if ((Number(stateBefore.missingCount) || 0) === 0) {
        this.recordCaseWorkflowAddressAttributionFillCurrent({
          state: stateBefore,
          source: context.source || 'role-aware-address-attribution-already-complete'
        });
        return { handled: true, success: true, reason: 'already-complete', state: stateBefore };
      }
      this.appendCaseWorkflowAutomationLog?.('Filling missing role-specific address attribution as Unknown…', {
        stepTag: 'ADDRESS_ATTRIBUTION_FILL',
        action: 'open_case_attribution_fill_panel',
        source: context.source || 'role-aware-address-attribution-auto'
      });
      this.refreshAddressAttributionRows();
      this.addressAttributionRows = (Array.isArray(this.addressAttributionRows) ? this.addressAttributionRows : [])
        .map((row) => {
          if ((Number(row.missingOccurrenceCount) || 0) <= 0) return this.caseBuddyAddressAttributionGroupStatus(row);
          return this.caseBuddyAddressAttributionGroupStatus({ ...row, attributionDraft: 'Unknown', include: true }, { preserveInclude: true });
        });
      const applyResult = await this.applyAddressAttributionRows();
      await this.normalizeCaseBuddyAttributionColumnsForLogicalSheet('main', 'Normalized Column K / Column X after automatic Unknown attribution fill.');
      const stateAfter = this.getCaseWorkflowAddressAttributionFillState();
      if ((Number(stateAfter.missingCount) || 0) === 0) {
        this.showAddressAttributionModal = false;
        this.recordCaseWorkflowAddressAttributionFillCurrent({
          state: stateAfter,
          changedCount: Number(applyResult?.changedCount) || 0,
          unknownFilled: true,
          source: context.source || 'role-aware-address-attribution-auto'
        });
        this.addressAttributionStatus = 'All role-specific address attribution cells are filled. Auto used Unknown for outstanding blanks.';
        return { handled: true, success: true, reason: 'filled-unknown', applyResult, state: stateAfter };
      }
      this.addressAttributionStatus = stateAfter.summary || 'Some role-specific attribution cells still need attention.';
      if (this.caseAutomation) {
        this.caseAutomation = {
          ...(this.caseAutomation || {}),
          active: false,
          busy: false,
          paused: true,
          status: 'Fill address attribution in main data',
          detail: this.addressAttributionStatus,
          manualPause: {
            type: 'manual-pause',
            pauseKind: 'address-attribution-fill',
            stepTag: 'ADDRESS_ATTRIBUTION_FILL',
            stepTitle: 'Fill address attribution in main data',
            title: 'Fill address attribution in main data',
            message: this.addressAttributionStatus,
            primaryLabel: 'Fill Attribution',
            primaryMethod: 'openAddressAttributionModal',
            openedAtUtc: new Date().toISOString()
          }
        };
      }
      return { handled: true, success: false, reason: 'still-missing-attribution', state: stateAfter, applyResult };
    },

    async runCaseWorkflowAddressAttributionFillGateIfNeeded(options = {}) {
      const step = options.step || this.recommendedWorkflowStep || null;
      const state = this.getCaseWorkflowAddressAttributionFillState();
      if ((Number(state.missingCount) || 0) === 0) {
        if (this.isCaseWorkflowAddressAttributionFillStep(step) || options.force) {
          this.recordCaseWorkflowAddressAttributionFillCurrent({
            state,
            source: options.source || 'role-aware-address-attribution-gate-current'
          });
          return { handled: this.isCaseWorkflowAddressAttributionFillStep(step), success: true, reason: 'already-complete', state };
        }
        return { handled: false, success: true, reason: 'not-target-step-already-complete', state };
      }
      if (!this.isCaseWorkflowAddressAttributionFillStep(step) && !options.force) return { handled: false, reason: 'not-address-attribution-step', state };
      return this.runCaseWorkflowAddressAttributionFillAutomationStep({
        source: options.source || 'role-aware-address-attribution-gate',
        force: options.force === true
      });
    },

    caseBuddyAddressAttributionRoleDefinitions() {
      return [
        {
          role: 'to',
          roleLabel: 'To',
          columnLabel: 'Column K',
          addressColumn: CASE_MANUAL_START_COL.TO_ADDRESS,
          attributionColumn: CASE_MANUAL_START_COL.ATTRIBUTION
        },
        {
          role: 'from',
          roleLabel: 'From',
          columnLabel: 'Column X',
          addressColumn: CASE_MANUAL_START_COL.FROM_ADDRESS,
          attributionColumn: CASE_MANUAL_START_COL.FROM_ATTRIBUTION
        }
      ];
    },

    caseBuddyAttributionRoleDefinition(role = '') {
      const normalizedRole = this.caseManualNormalizeText(role).toLowerCase();
      return this.caseBuddyAddressAttributionRoleDefinitions().find(definition => definition.role === normalizedRole) || this.caseBuddyAddressAttributionRoleDefinitions()[0];
    },

    caseBuddyCleanAddressAttributionForRole(value = '', role = 'to', row = []) {
      const definition = this.caseBuddyAttributionRoleDefinition(role);
      if (definition.role === 'from') return this.caseManualCanonicalizeFromAttributionForColumnX(value);
      return this.caseManualSanitizeSwapServiceAttributionForColumnK(value, row);
    },

    caseBuddyAddressAttributionGroupStatus(row = {}, options = {}) {
      const missingCount = Number(row.missingOccurrenceCount) || 0;
      const draft = normalizeAttributionValue(row.attributionDraft || '');
      const current = normalizeAttributionValue(row.currentAttribution || '');
      const hasConflict = Boolean(row.hasConflict);
      let status = 'needs-value';
      let statusLabel = 'Needs value';
      let message = `Add ${row.columnLabel || 'the correct column'} attribution or Unknown.`;
      if (missingCount <= 0) {
        status = 'complete';
        statusLabel = 'Complete';
        message = `${row.columnLabel || 'Attribution column'} already has coverage.`;
      } else if (draft) {
        status = 'ready';
        if (current && this.caseManualNormalizeText(current).toLowerCase() === this.caseManualNormalizeText(draft).toLowerCase()) statusLabel = 'Propagate';
        else if (this.caseManualNormalizeText(draft).toLowerCase() === 'unknown') statusLabel = 'Use Unknown';
        else statusLabel = 'Ready';
        message = `Will fill ${missingCount} blank ${row.columnLabel || 'attribution'} cell${missingCount === 1 ? '' : 's'}.`;
      } else if (hasConflict) {
        statusLabel = 'Needs choice';
        message = `This ${row.roleLabel || 'role'} has conflicting existing labels. Choose the ${row.columnLabel || 'column'} value to apply.`;
      }
      const include = status === 'ready'
        ? (options.preserveInclude === true && typeof row.include === 'boolean' ? row.include : true)
        : false;
      return { ...row, status, statusLabel, message, include };
    },

    buildCaseBuddyRoleAwareAddressAttributionAnalysis(matrix = []) {
      const sourceRows = normalizeCaseMatrix(Array.isArray(matrix) ? matrix : []);
      const roleDefinitions = this.caseBuddyAddressAttributionRoleDefinitions();
      const groups = new Map();
      const ensureGroup = (definition, address, addressKey, rowIndex) => {
        const key = `${definition.role}|${addressKey}`;
        if (!groups.has(key)) {
          groups.set(key, {
            id: key,
            key,
            role: definition.role,
            roleLabel: definition.roleLabel,
            columnLabel: definition.columnLabel,
            address,
            addressKey,
            firstSpreadsheetRow: rowIndex + 1,
            occurrenceCount: 0,
            missingOccurrenceCount: 0,
            currentAttribution: '',
            currentAttributions: [],
            hasConflict: false,
            sideSummary: '',
            occurrences: []
          });
        }
        const group = groups.get(key);
        if ((rowIndex + 1) < group.firstSpreadsheetRow) group.firstSpreadsheetRow = rowIndex + 1;
        if (!group.address && address) group.address = address;
        return group;
      };

      sourceRows.forEach((sourceRow, rowIndex) => {
        const row = this.caseManualEnsureRowWidth(sourceRow, Math.max(CASE_MANUAL_START_COLUMN_COUNT, sourceRow?.length || 0));
        if (rowIndex === 0 && this.caseManualIsHeaderRow(row)) return;
        if (!rowHasMeaningfulData(row)) return;
        roleDefinitions.forEach((definition) => {
          const address = this.caseManualNormalizeText(row[definition.addressColumn] || '');
          const addressKey = this.caseManualNormalizeAddressKey(address) || address.toLowerCase();
          if (!addressKey) return;
          const currentAttribution = this.caseBuddyCleanAddressAttributionForRole(row[definition.attributionColumn] || '', definition.role, row);
          const group = ensureGroup(definition, address, addressKey, rowIndex);
          group.occurrenceCount += 1;
          if (!currentAttribution) group.missingOccurrenceCount += 1;
          else if (!group.currentAttributions.some(value => value.toLowerCase() === currentAttribution.toLowerCase())) group.currentAttributions.push(currentAttribution);
          group.occurrences.push({
            rowIndex,
            spreadsheetRowNumber: rowIndex + 1,
            role: definition.role,
            roleLabel: definition.roleLabel,
            columnLabel: definition.columnLabel,
            address,
            addressKey,
            addressColumn: definition.addressColumn,
            attributionColumn: definition.attributionColumn,
            currentAttribution
          });
        });
      });

      const allGroups = Array.from(groups.values())
        .map((group) => {
          const currentAttributions = Array.isArray(group.currentAttributions) ? group.currentAttributions : [];
          const hasConflict = currentAttributions.length > 1;
          const currentAttribution = currentAttributions.length === 1 ? currentAttributions[0] : '';
          const base = {
            ...group,
            hasConflict,
            currentAttribution,
            attributionDraft: currentAttribution,
            sideSummary: `${group.occurrenceCount} ${group.roleLabel}${group.occurrenceCount === 1 ? '' : ''} · ${group.columnLabel}`,
            searchText: [group.address, group.roleLabel, group.columnLabel, currentAttributions.join(' ')].join(' ').toLowerCase()
          };
          return this.caseBuddyAddressAttributionGroupStatus(base);
        })
        .sort((a, b) => (a.firstSpreadsheetRow - b.firstSpreadsheetRow) || String(a.address).localeCompare(String(b.address)) || String(a.role).localeCompare(String(b.role)));

      const rows = allGroups.filter(group => group.missingOccurrenceCount > 0);
      const missingCount = allGroups.reduce((total, group) => total + (Number(group.missingOccurrenceCount) || 0), 0);
      const readyCount = rows.filter(row => row.status === 'ready').length;
      const completeCount = allGroups.filter(group => (Number(group.missingOccurrenceCount) || 0) === 0).length;
      const addressKeys = new Set(allGroups.map(group => group.addressKey).filter(Boolean));
      const summary = missingCount === 0
        ? 'All visible To/From address attribution cells are filled.'
        : `${missingCount} role-specific attribution cell${missingCount === 1 ? '' : 's'} still need a Column K/Column X value.`;
      return {
        roleAware: true,
        addressCount: addressKeys.size,
        groupCount: allGroups.length,
        missingCount,
        readyCount,
        completeCount,
        rows,
        groups: allGroups,
        summary
      };
    },

    refreshAddressAttributionRows() {
      const previousRows = Array.isArray(this.addressAttributionRows) ? this.addressAttributionRows : [];
      const previousById = new Map(previousRows.map(row => [row.id, row]));
      const analysis = this.buildCaseBuddyRoleAwareAddressAttributionAnalysis(this.getMatrixForLogicalSheet('main'));
      this.addressAttributionRows = (analysis.rows || []).map((row) => {
        const previous = previousById.get(row.id) || {};
        const merged = {
          ...row,
          attributionDraft: previous.attributionDraft !== undefined ? previous.attributionDraft : row.attributionDraft,
          include: typeof previous.include === 'boolean' ? previous.include : row.include
        };
        return this.caseBuddyAddressAttributionGroupStatus(merged, { preserveInclude: true });
      });
      this.addressAttributionStatus = analysis.summary || '';
      return this.addressAttributionRows;
    },

    refreshAddressAttributionRowStatuses() {
      this.addressAttributionRows = (Array.isArray(this.addressAttributionRows) ? this.addressAttributionRows : [])
        .map(row => this.caseBuddyAddressAttributionGroupStatus(row, { preserveInclude: false }));
    },

    caseBuddyParseAddressAttributionPaste(text = '') {
      const lines = String(text || '').split(/\r?\n/u).map(line => line.trim()).filter(Boolean);
      const parsed = [];
      let headers = null;
      const cellLooksLikeAddress = value => /^0x[a-f0-9]{6,}$/iu.test(this.caseManualNormalizeText(value)) || /^[13bc][a-z0-9]{20,}$/iu.test(this.caseManualNormalizeText(value));
      const headerRole = value => {
        const textValue = this.caseManualNormalizeText(value).toLowerCase();
        if (/\bfrom\b|sender|source/u.test(textValue)) return 'from';
        if (/\bto\b|receiver|recipient|destination/u.test(textValue)) return 'to';
        return '';
      };
      lines.forEach((line, lineIndex) => {
        const cells = line.split(/\t|,/u).map(cell => cell.trim());
        if (!cells.length) return;
        const lower = cells.map(cell => cell.toLowerCase());
        const looksHeader = lower.some(cell => /address|attribution|entity|label|service|from|to/u.test(cell)) && !cells.some(cellLooksLikeAddress);
        if (lineIndex === 0 && looksHeader) {
          headers = lower;
          return;
        }
        let addressIndex = -1;
        let attributionIndex = -1;
        let role = '';
        if (headers) {
          addressIndex = headers.findIndex(cell => /address/u.test(cell));
          attributionIndex = headers.findIndex(cell => /attribution|entity|label|service/u.test(cell));
          if (addressIndex >= 0) role = headerRole(headers[addressIndex]);
        }
        if (addressIndex < 0) addressIndex = cells.findIndex(cellLooksLikeAddress);
        if (attributionIndex < 0) attributionIndex = cells.findIndex((cell, index) => index !== addressIndex && normalizeAttributionValue(cell));
        if (addressIndex < 0 || attributionIndex < 0) return;
        const address = this.caseManualNormalizeText(cells[addressIndex]);
        const attribution = this.caseManualCleanAttributionLabel(cells[attributionIndex]);
        const addressKey = this.caseManualNormalizeAddressKey(address) || address.toLowerCase();
        if (!addressKey || !attribution) return;
        parsed.push({ address, addressKey, attribution, role });
      });
      return parsed;
    },

    parseAddressAttributionPasteIntoRows() {
      const parsed = this.caseBuddyParseAddressAttributionPaste(this.addressAttributionPaste || '');
      if (!parsed.length) {
        this.refreshAddressAttributionRowStatuses();
        return;
      }
      const byAddressRole = new Map();
      parsed.forEach((item) => {
        if (item.role) byAddressRole.set(`${item.role}|${item.addressKey}`, item.attribution);
        else byAddressRole.set(`any|${item.addressKey}`, item.attribution);
      });
      this.addressAttributionRows = (Array.isArray(this.addressAttributionRows) ? this.addressAttributionRows : [])
        .map((row) => {
          const roleKey = `${row.role}|${row.addressKey}`;
          const anyKey = `any|${row.addressKey}`;
          const attribution = byAddressRole.get(roleKey) || byAddressRole.get(anyKey) || '';
          if (!attribution) return row;
          return { ...row, attributionDraft: attribution, include: true };
        });
      this.refreshAddressAttributionRowStatuses();
    },

    handleAddressAttributionCellPaste(event, index) {
      const text = event?.clipboardData?.getData?.('text') || '';
      if (!text || !/\r?\n|\t/u.test(text)) return;
      event.preventDefault();
      const parsed = this.caseBuddyParseAddressAttributionPaste(text);
      if (!parsed.length) return;
      const rows = Array.isArray(this.addressAttributionRows) ? this.addressAttributionRows.slice() : [];
      let cursor = Math.max(0, Number(index) || 0);
      parsed.forEach((item) => {
        const directIndex = rows.findIndex(row => row.addressKey === item.addressKey && (!item.role || row.role === item.role));
        const targetIndex = directIndex >= 0 ? directIndex : cursor;
        if (!rows[targetIndex]) return;
        rows[targetIndex] = { ...rows[targetIndex], attributionDraft: item.attribution, include: true };
        cursor = targetIndex + 1;
      });
      this.addressAttributionRows = rows.map(row => this.caseBuddyAddressAttributionGroupStatus(row));
    },

    clearAddressAttributionDraft() {
      this.addressAttributionPaste = '';
      this.addressAttributionRows = (Array.isArray(this.addressAttributionRows) ? this.addressAttributionRows : [])
        .map(row => this.caseBuddyAddressAttributionGroupStatus({ ...row, attributionDraft: row.currentAttribution || '', include: Boolean(row.currentAttribution) }));
      this.addressAttributionStatus = 'Cleared staged attribution values.';
    },

    async applyAddressAttributionRows() {
      if (this.addressAttributionRunning) return { changed: false, skipped: true };
      const selectedRows = this.addressAttributionSelectedRows || [];
      if (!selectedRows.length) {
        this.addressAttributionStatus = 'No selected role-specific attribution rows are ready to apply.';
        return { changed: false, skipped: true };
      }
      this.addressAttributionRunning = true;
      try {
        const matrix = this.getMatrixForLogicalSheet('main');
        const nextMatrix = (Array.isArray(matrix) ? matrix : []).map(row => this.caseManualEnsureRowWidth(row, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row?.length || 0)));
        let changedCount = 0;
        let skippedExistingCount = 0;
        selectedRows.forEach((reviewRow) => {
          const definition = this.caseBuddyAttributionRoleDefinition(reviewRow.role);
          const draft = this.caseBuddyCleanAddressAttributionForRole(reviewRow.attributionDraft, definition.role);
          if (!draft) return;
          (Array.isArray(reviewRow.occurrences) ? reviewRow.occurrences : []).forEach((occurrence) => {
            const rowIndex = Number(occurrence.rowIndex);
            const columnIndex = Number(occurrence.attributionColumn ?? definition.attributionColumn);
            if (!Number.isInteger(rowIndex) || rowIndex < 0 || !nextMatrix[rowIndex]) return;
            const row = nextMatrix[rowIndex];
            const current = this.caseBuddyCleanAddressAttributionForRole(row[columnIndex] || '', definition.role, row);
            const currentKey = this.caseManualNormalizeText(current).toLowerCase();
            const draftKey = this.caseManualNormalizeText(draft).toLowerCase();
            if (currentKey && currentKey !== draftKey && !this.addressAttributionOverwriteExisting) {
              skippedExistingCount += 1;
              return;
            }
            if (currentKey === draftKey) return;
            row[columnIndex] = draft;
            changedCount += 1;
          });
        });
        if (changedCount > 0) {
          await this.replaceCaseManualStartLogicalSheetMatrix('main', nextMatrix, `Filled ${changedCount} role-specific address attribution cell${changedCount === 1 ? '' : 's'} in Main Data.`, { autosaveDelayMs: 900 });
          await this.normalizeCaseBuddyAttributionColumnsForLogicalSheet('main', 'Normalized Column K / Column X after role-specific attribution fill.');
        }
        this.refreshAddressAttributionRows();
        const remaining = this.addressAttributionAnalysis?.missingCount || 0;
        this.addressAttributionStatus = changedCount > 0
          ? `Applied ${changedCount} role-specific attribution cell${changedCount === 1 ? '' : 's'}.${skippedExistingCount ? ` Skipped ${skippedExistingCount} existing value${skippedExistingCount === 1 ? '' : 's'}; enable overwrite to replace them.` : ''}`
          : `No blank attribution cells changed.${skippedExistingCount ? ` Skipped ${skippedExistingCount} existing value${skippedExistingCount === 1 ? '' : 's'}; enable overwrite to replace them.` : ''}`;
        if (remaining === 0) {
          this.recordWorkflowSignal?.('ADDRESS_ATTRIBUTION_FILL', 'role-aware-address-attribution-fill', {
            silent: true,
            status: 'pass',
            reason: 'Role-specific Column K / Column X attribution coverage is complete.'
          });
        }
        return { changed: changedCount > 0, changedCount, skippedExistingCount, remaining };
      } finally {
        this.addressAttributionRunning = false;
      }
    },

    async fillOutstandingAddressAttributionsAsUnknown() {
      if (!Array.isArray(this.addressAttributionRows) || this.addressAttributionRows.length === 0) this.refreshAddressAttributionRows();
      this.addressAttributionRows = (Array.isArray(this.addressAttributionRows) ? this.addressAttributionRows : [])
        .map((row) => {
          if ((Number(row.missingOccurrenceCount) || 0) <= 0) return this.caseBuddyAddressAttributionGroupStatus(row);
          return this.caseBuddyAddressAttributionGroupStatus({ ...row, attributionDraft: 'Unknown', include: true });
        });
      this.addressAttributionStatus = 'Staged Unknown for all remaining role-specific blank attribution cells. Apply to write Column K/Column X.';
      return { changed: false, staged: true, rows: this.addressAttributionRows.length };
    },

    isCaseWorkflowRawToMainPreparationStep(step = {}) {
      const tag = normalizeCell(step?.stepTag || step?.tag || step?.id || step?.workflowTag || '').toUpperCase();
      const text = [step?.stepTitle, step?.title, step?.description, step?.action, step?.method, step?.id, step?.tag]
        .map(value => normalizeCell(value).toLowerCase())
        .join(' ');
      return tag === 'RAW_TO_MAIN_PREPARATION' || /prepare\s+raw\s+data\s+into\s+main|raw[_\s-]*to[_\s-]*main|tool\s*68|sapling\s+preparation/.test(text);
    },

    getCaseWorkflowRawToMainPreparationStep() {
      const recommended = this.recommendedWorkflowStep;
      if (this.isCaseWorkflowRawToMainPreparationStep(recommended)) return recommended;
      const definitions = this.caseWorkflowGroupsWithEarlyWwwUsdReferences()
        .flatMap(group => Array.isArray(group.steps) ? group.steps : []);
      return definitions.find(step => this.isCaseWorkflowRawToMainPreparationStep(step)) || {
        tag: 'RAW_TO_MAIN_PREPARATION',
        title: 'Prepare raw data into main data'
      };
    },

    caseWorkflowRawToMainHashKey(value = '') {
      const text = normalizeCell(value);
      if (!text) return '';
      return /^0x/i.test(text) ? text.toLowerCase() : text;
    },

    findCaseWorkflowUnresolvedTool88NextHopRows(rows = []) {
      const matrix = normalizeCaseMatrix(Array.isArray(rows) ? rows : []);
      return matrix
        .map((row, index) => ({ row, index }))
        .filter(({ row, index }) => index > 0 && rowHasMeaningfulData(row))
        .filter(({ row }) => {
          const continuesAs = normalizeCell(row?.[CASE_MANUAL_START_COL.CONTINUES_AS]).toLowerCase();
          const tool88Notes = normalizeCell(row?.[CASE_MANUAL_START_COL.CASE_NOTES] || row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES] || '');
          if (!/^path\s+ends\.?$/i.test(continuesAs)) return false;
          if (!/tool\s*88[:\s].*selected\s+this\s+transaction\s+as\s+the\s+next\s+hop/i.test(tool88Notes)) return false;
          return !/flow:\s*assessed|path[-\s]*ending|dormant|low[-\s]*value|bridge|swap|service|manual\s+review|complete/i.test(tool88Notes);
        })
        .map(({ row, index }) => ({
          rowIndex: index,
          spreadsheetRowNumber: index + 1,
          hash: normalizeCell(row?.[CASE_MANUAL_START_COL.TX_HASH]),
          fromHash: normalizeCell(row?.[CASE_MANUAL_START_COL.FROM_HASH]),
          continuesAs: normalizeCell(row?.[CASE_MANUAL_START_COL.CONTINUES_AS]),
          tool88Notes: normalizeCell(row?.[CASE_MANUAL_START_COL.CASE_NOTES] || row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES] || '')
        }));
    },

    getCaseWorkflowRawToMainPreparationSummary() {
      try {
        const mainMatrix = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('main') : [];
        const rawMatrix = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('raw') : [];
        const mainRows = normalizeCaseMatrix(Array.isArray(mainMatrix) ? mainMatrix : []);
        const rawRows = normalizeCaseMatrix(Array.isArray(rawMatrix) ? rawMatrix : []);
        const coverage = this.buildCaseBuddyMainRawDataCoverage(mainRows, rawRows);
        const coverageRows = Array.isArray(coverage?.coverageRows) ? coverage.coverageRows : [];
        const missingRows = coverageRows.filter(row => normalizeCell(row?.status).toLowerCase() !== 'covered');
        const rawCoverageComplete = coverageRows.length > 0 && missingRows.length === 0;
        const unresolvedTool88NextHopRows = this.findCaseWorkflowUnresolvedTool88NextHopRows(rawRows);
        const rawFlowUnresolved = unresolvedTool88NextHopRows.length > 0;
        const knownRootLabels = coverageRows.map(row => normalizeCell(row?.pathLabel)).filter(Boolean);
        const preparedRows = coverageRows.map((coverageRow) => {
          const candidateRowIndex = Number.isInteger(Number(coverageRow?.rowIndex)) ? Number(coverageRow.rowIndex) : Number(coverageRow?.spreadsheetRowNumber) - 1;
          const rowIndex = Number.isInteger(candidateRowIndex) && candidateRowIndex >= 0 ? candidateRowIndex : -1;
          const mainRow = rowIndex >= 0 && rowIndex < mainRows.length ? mainRows[rowIndex] : [];
          const hash = normalizeCell(coverageRow?.hash || coverageRow?.transactionHash || mainRow?.[CASE_MANUAL_START_COL.TX_HASH] || '');
          const hashKey = this.caseWorkflowRawToMainHashKey(hash);
          const fromAddress = normalizeCell(mainRow?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
          const toAddress = normalizeCell(mainRow?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
          const utcDate = normalizeCell(mainRow?.[CASE_MANUAL_START_COL.DATE]);
          const amount = normalizeCell(mainRow?.[CASE_MANUAL_START_COL.AMOUNT]);
          const token = normalizeCell(mainRow?.[CASE_MANUAL_START_COL.TOKEN]);
          const fundsFollowed = normalizeCell(mainRow?.[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]);
          const continuesAs = normalizeCell(mainRow?.[CASE_MANUAL_START_COL.CONTINUES_AS]);
          const rootPathLabel = normalizeCell(coverageRow?.pathLabel || mainRow?.[CASE_MANUAL_START_COL.PATH_LABEL]);
          const rootToAddressKey = this.caseWorkflowRootAddressKey(coverageRow?.toAddress || toAddress);
          const duplicateHashRoot = coverageRow?.duplicateHash === true;
          const hasRootDetails = !!(hash && fromAddress && toAddress && utcDate && amount && token && fundsFollowed && continuesAs);
          const hasPreparedChild = !!hashKey && mainRows.slice(1).some((row, index) => {
            const actualRowIndex = index + 1;
            if (actualRowIndex === rowIndex) return false;
            const fromHash = this.caseWorkflowRawToMainHashKey(row?.[CASE_MANUAL_START_COL.FROM_HASH]);
            const rowHash = this.caseWorkflowRawToMainHashKey(row?.[CASE_MANUAL_START_COL.TX_HASH]);
            const childPathLabel = normalizeCell(row?.[CASE_MANUAL_START_COL.PATH_LABEL]);
            const childFromAddressKey = this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
            const belongsToThisRoot = !duplicateHashRoot
              || this.caseWorkflowPathLabelBelongsToRoot(childPathLabel, rootPathLabel, knownRootLabels)
              || (!!rootToAddressKey && childFromAddressKey === rootToAddressKey);
            if (fromHash && fromHash === hashKey && belongsToThisRoot) return true;
            return !!rowHash && continuesAs.toLowerCase().includes(rowHash.toLowerCase()) && belongsToThisRoot;
          });
          const hasTerminalOrContinuation = /path\s+ends|continues\s+as/i.test(continuesAs) || hasPreparedChild;
          const prepared = rawCoverageComplete && !rawFlowUnresolved && hasRootDetails && hasTerminalOrContinuation;
          return {
            coverageRow,
            rowIndex,
            spreadsheetRowNumber: rowIndex >= 0 ? rowIndex + 1 : coverageRow?.spreadsheetRowNumber || '',
            hash,
            pathLabel: rootPathLabel,
            toAddress: coverageRow?.toAddress || toAddress,
            rootInstanceKey: coverageRow?.rootInstanceKey || this.caseWorkflowRootInstanceKey({
              rowIndex,
              pathLabel: rootPathLabel,
              hash,
              toAddress: coverageRow?.toAddress || toAddress
            }),
            duplicateHash: duplicateHashRoot,
            prepared,
            hasRootDetails,
            hasPreparedChild,
            hasTerminalOrContinuation
          };
        });
        const unpreparedRows = preparedRows.filter(row => !row.prepared);
        return {
          coverage,
          coverageRows,
          missingRows,
          rawCoverageComplete,
          rawFlowUnresolved,
          unresolvedTool88NextHopRows,
          preparedRows,
          unpreparedRows,
          totalRows: coverageRows.length,
          preparedCount: preparedRows.length - unpreparedRows.length,
          missingCount: missingRows.length,
          isComplete: rawCoverageComplete && !rawFlowUnresolved && coverageRows.length > 0 && unpreparedRows.length === 0
        };
      } catch (error) {
        return {
          coverage: null,
          coverageRows: [],
          missingRows: [],
          rawCoverageComplete: false,
          rawFlowUnresolved: false,
          unresolvedTool88NextHopRows: [],
          preparedRows: [],
          unpreparedRows: [],
          totalRows: 0,
          preparedCount: 0,
          missingCount: 0,
          isComplete: false,
          error
        };
      }
    },

    getCaseWorkflowRawToMainPreparationSignalCode(step = {}) {
      return normalizeCell(step?.signalCode || step?.signal || step?.completionSignal || step?.completionSignalCode || 'RAW_TO_MAIN_PREPARATION');
    },

    recordCaseWorkflowRawToMainPreparationComplete(context = {}) {
      if (!this.caseSupportState) this.caseSupportState = createDefaultCaseSupportState();
      const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const step = context.step || this.getCaseWorkflowRawToMainPreparationStep();
      const signalCode = this.getCaseWorkflowRawToMainPreparationSignalCode(step) || 'RAW_TO_MAIN_PREPARATION';
      const summary = context.summary || this.getCaseWorkflowRawToMainPreparationSummary();
      const workflow = {
        ...(this.caseSupportState.workflowState || {}),
        signalCompleteByCode: { ...((this.caseSupportState.workflowState || {}).signalCompleteByCode || {}) }
      };
      const signalCodes = Array.from(new Set([
        signalCode,
        'RAW_TO_MAIN_PREPARATION',
        'RAW_DATA_PREPARED_TO_MAIN',
        'TOOL68_PREPARATION_COMPLETE',
        'TOOL_68_MAIN_DATA_PREPARED'
      ].map(code => normalizeCell(code).toUpperCase()).filter(Boolean)));
      signalCodes.forEach((code) => {
        workflow.signalCompleteByCode[code] = {
          ...(workflow.signalCompleteByCode[code] || {}),
          completedAtUtc: workflow.signalCompleteByCode[code]?.completedAtUtc || now,
          checkedAtUtc: now,
          appliedAtUtc: now,
          revision: currentRevision,
          source: context.source || 'workflow-raw-to-main-preparation',
          stepTag: normalizeCell(step?.tag || 'RAW_TO_MAIN_PREPARATION'),
          preparedRows: Number(summary.preparedCount || 0),
          totalRows: Number(summary.totalRows || 0)
        };
      });
      this.caseSupportState.workflowState = workflow;
      const checkIds = ['sapling_prepared', 'raw_to_main_preparation', 'raw_to_main', 'tool68_preparation'];
      const checkResults = { ...(this.caseSupportState.checkResultsById || {}) };
      checkIds.forEach((id) => {
        checkResults[id] = {
          ...(checkResults[id] || {}),
          id,
          status: 'pass',
          message: `${Number(summary.preparedCount || 0)}/${Number(summary.totalRows || 0)} raw-backed starting transaction${Number(summary.totalRows || 0) === 1 ? '' : 's'} prepared into Main Data.`,
          details: {
            ...((checkResults[id] || {}).details || {}),
            source: context.source || 'workflow-raw-to-main-preparation',
            stepTag: normalizeCell(step?.tag || 'RAW_TO_MAIN_PREPARATION'),
            preparedRows: Number(summary.preparedCount || 0),
            totalRows: Number(summary.totalRows || 0),
            derivedFromCurrentRows: true
          },
          revision: currentRevision,
          checkedAtUtc: now,
          derivedFromCurrentRows: true
        };
      });
      this.caseSupportState.checkResultsById = checkResults;
      if (typeof this.recordWorkflowSignal === 'function') {
        try {
          this.recordWorkflowSignal(signalCode, context.source || 'workflow-raw-to-main-preparation', { silent: true });
        } catch (error) {
          console.warn('Case Buddy could not record raw-to-main workflow signal through the workflow helper.', error);
        }
      }
      if (typeof this.queueCaseSupportStatePersist === 'function') this.queueCaseSupportStatePersist(120);
      else if (typeof this.persistCaseSupportState === 'function') this.persistCaseSupportState();
      return { signalCode, summary };
    },

    applyCaseWorkflowRawToMainPreparationCurrentState(view = {}, definition = {}) {
      const summary = this.getCaseWorkflowRawToMainPreparationSummary();
      if (!summary.isComplete) return view;
      const currentRevision = Number(this.caseSupportState?.caseDataRevision) || 0;
      const existingCheck = this.caseSupportState?.checkResultsById?.sapling_prepared || this.caseSupportState?.checkResultsById?.raw_to_main_preparation || null;
      const hasSignal = this.workflowStepHasRecordedSignal(view) || this.workflowStepHasRecordedSignal(definition);
      const checkedAtUtc = existingCheck?.checkedAtUtc || this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
      return {
        ...view,
        complete: true,
        stale: false,
        blocked: false,
        status: 'complete',
        statusLabel: `Complete · ${summary.preparedCount}/${summary.totalRows} prepared`,
        statusClass: 'workflow-step-card--complete',
        staleReason: '',
        sources: {
          ...(view.sources || {}),
          check: true,
          signal: !!((view.sources || {}).signal || hasSignal),
          manual: !!(view.sources || {}).manual
        },
        checkResult: {
          ...(existingCheck || {}),
          id: existingCheck?.id || 'sapling_prepared',
          status: 'pass',
          message: `${summary.preparedCount}/${summary.totalRows} raw-backed starting transaction${summary.totalRows === 1 ? '' : 's'} prepared into Main Data.`,
          revision: currentRevision,
          checkedAtUtc,
          details: {
            ...((existingCheck || {}).details || {}),
            derivedFromCurrentRows: true,
            preparedRows: summary.preparedCount,
            totalRows: summary.totalRows
          }
        },
        completionSummary: `${summary.preparedCount}/${summary.totalRows} raw-backed starting transaction${summary.totalRows === 1 ? '' : 's'} prepared into Main Data.`
      };
    },

    caseWorkflowRawSetDescriptorForCoverageRow(coverageRow = {}, rawMatrix = null) {
      const rawRows = normalizeCaseMatrix(Array.isArray(rawMatrix)
        ? rawMatrix
        : (typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('raw') : []));
      const rootIndex = Number(coverageRow?.matchedRawRootRowIndex);
      if (!Number.isInteger(rootIndex) || rootIndex < 0 || rootIndex >= rawRows.length) return null;

      const mainRows = typeof this.getMatrixForLogicalSheet === 'function'
        ? this.getMatrixForLogicalSheet('main')
        : [];
      const knownRootLabels = this.caseWorkflowCollectRootTransactions(Array.isArray(mainRows) ? mainRows : [])
        .map(root => root.pathLabel)
        .filter(Boolean);
      const rawRoots = this.caseWorkflowCollectRootTransactions(rawRows, {
        knownRootLabels,
        allowNonRootLabels: false
      }).sort((left, right) => left.rowIndex - right.rowIndex);
      const position = rawRoots.findIndex(root => Number(root.rowIndex) === rootIndex);
      const nextRootIndex = position >= 0 && rawRoots[position + 1]
        ? Number(rawRoots[position + 1].rowIndex)
        : rawRows.length;
      const rows = rawRows.slice(rootIndex, Math.max(rootIndex + 1, nextRootIndex)).map(row => row.slice());
      if (!rows.length) return null;
      const rootRow = rows[0].slice();
      return {
        rootRowIndex: rootIndex,
        rootRowNumber: rootIndex + 1,
        rootRow,
        rows,
        hash: normalizeCell(rootRow?.[CASE_MANUAL_START_COL.TX_HASH]),
        pathLabel: normalizeCell(rootRow?.[CASE_MANUAL_START_COL.PATH_LABEL]),
        toAddress: normalizeCell(rootRow?.[CASE_MANUAL_START_COL.TO_ADDRESS])
      };
    },

    caseWorkflowTool68FindCoverageRow(toolRow = {}, index = 0, coverageRows = []) {
      const rows = Array.isArray(coverageRows) ? coverageRows : [];
      const spreadsheetRowNumber = Number(toolRow?.spreadsheetRowNumber || toolRow?.mainRowNumber || toolRow?.rowNumber || 0);
      const pathKey = this.caseWorkflowRootIdentityKey(toolRow?.pathLabel || toolRow?.mainPathLabel || toolRow?.effectivePathLabel || '');
      const targetKey = this.caseWorkflowRootAddressKey(toolRow?.toAddress || toolRow?.targetAddress || toolRow?.toAddressHint || '');
      const hashKey = this.caseWorkflowTool68PreparationRowHashKey(toolRow);
      return rows.find(row => spreadsheetRowNumber > 0 && Number(row?.spreadsheetRowNumber) === spreadsheetRowNumber)
        || rows.find(row => {
          if (this.caseWorkflowRawToMainHashKey(row?.hash || row?.transactionHash || '') !== hashKey) return false;
          const rowPathKey = this.caseWorkflowRootIdentityKey(row?.pathLabel || '');
          const rowTargetKey = this.caseWorkflowRootAddressKey(row?.toAddress || '');
          if (pathKey && rowPathKey && pathKey !== rowPathKey) return false;
          if (targetKey && rowTargetKey && targetKey !== rowTargetKey) return false;
          return Boolean(pathKey || targetKey || row?.duplicateHash !== true);
        })
        || rows[index]
        || null;
    },

    caseWorkflowTool68ApplyRawSetDescriptor(row = {}, descriptor = null) {
      if (!descriptor) return row;
      const next = {
        ...(row || {}),
        rawRootRowIndex: descriptor.rootRowIndex,
        rawRootIndex: descriptor.rootRowIndex,
        rawRootRowNumber: descriptor.rootRowNumber,
        rawRootRow: descriptor.rootRow.slice(),
        rawRootHash: descriptor.hash,
        rawRootPathLabel: descriptor.pathLabel,
        rawRootToAddress: descriptor.toAddress,
        rawSetRows: descriptor.rows.map(rawRow => rawRow.slice()),
        matchedRawRows: descriptor.rows.map(rawRow => rawRow.slice()),
        hasRawSet: true
      };

      const replaceRowArrayField = (key) => {
        if (Object.prototype.hasOwnProperty.call(next, key) && Array.isArray(next[key])) {
          next[key] = descriptor.rows.map(rawRow => rawRow.slice());
        }
      };
      ['rawRows', 'matchedRawSetRows', 'rawEntryRows', 'sourceRows'].forEach(replaceRowArrayField);

      const replaceSetField = (key) => {
        if (!Object.prototype.hasOwnProperty.call(next, key)) return;
        if (Array.isArray(next[key])) {
          next[key] = descriptor.rows.map(rawRow => rawRow.slice());
          return;
        }
        if (next[key] && typeof next[key] === 'object') {
          next[key] = {
            ...next[key],
            rootRowIndex: descriptor.rootRowIndex,
            rootRowNumber: descriptor.rootRowNumber,
            rootRow: descriptor.rootRow.slice(),
            rows: descriptor.rows.map(rawRow => rawRow.slice()),
            hash: descriptor.hash,
            pathLabel: descriptor.pathLabel,
            toAddress: descriptor.toAddress
          };
        }
      };
      replaceSetField('rawSet');
      replaceSetField('matchedRawSet');
      return next;
    },

    reconcileCaseWorkflowTool68PreparationRootInstances(rows = null) {
      const list = Array.isArray(rows)
        ? rows
        : (Array.isArray(this.tool68PreparationRows) ? this.tool68PreparationRows : []);
      const coverage = this.getCaseWorkflowRawDataCoverageSummary();
      const coverageRows = Array.isArray(coverage?.coverageRows) ? coverage.coverageRows : [];
      const rawRows = typeof this.getMatrixForLogicalSheet === 'function'
        ? this.getMatrixForLogicalSheet('raw')
        : [];
      const reconciled = list.map((row, index) => {
        const coverageRow = this.caseWorkflowTool68FindCoverageRow(row, index, coverageRows);
        if (!coverageRow) return row;
        const descriptor = this.caseWorkflowRawSetDescriptorForCoverageRow(coverageRow, rawRows);
        const enriched = {
          ...(row || {}),
          coverageRow,
          rootInstanceKey: coverageRow.rootInstanceKey || this.caseWorkflowRootInstanceKey(coverageRow),
          duplicateHash: coverageRow.duplicateHash === true,
          matchedRawRootRowIndex: Number(coverageRow.matchedRawRootRowIndex),
          rawRootRowNumber: coverageRow.rawRootRowNumber || row?.rawRootRowNumber || '',
          rawRootPathLabel: coverageRow.rawRootPathLabel || row?.rawRootPathLabel || '',
          rawRootToAddress: coverageRow.rawRootToAddress || row?.rawRootToAddress || '',
          rawCovered: normalizeCell(coverageRow.status).toLowerCase() === 'covered'
        };
        return this.caseWorkflowTool68ApplyRawSetDescriptor(enriched, descriptor);
      });
      this.tool68PreparationRows = reconciled;
      return reconciled;
    },

    async refreshTool68PreparationRows(...args) {
      const result = await this.callFirstCaseBuddyOriginalMethod('refreshTool68PreparationRows', args);
      this.reconcileCaseWorkflowTool68PreparationRootInstances();
      return result;
    },

    async refreshCaseWorkflowTool68PreparationRowsForAuto() {
      if (!this.showTool68PreparationModal && typeof this.openTool68PreparationModal === 'function') {
        this.caseWorkflowTool68PreparationOpenedByAuto = true;
        const openResult = this.openTool68PreparationModal({ source: 'workflow-auto-raw-to-main', silent: true });
        if (openResult && typeof openResult.then === 'function') await openResult;
        if (typeof this.caseWorkflowUiNextTick === 'function') await this.caseWorkflowUiNextTick();
        else await this.$nextTick();
      }
      if (typeof this.refreshTool68PreparationRows === 'function') {
        const result = this.refreshTool68PreparationRows();
        if (result && typeof result.then === 'function') await result;
      } else {
        const result = this.callFirstCaseBuddyOriginalMethod('refreshTool68PreparationRows', []);
        if (result && typeof result.then === 'function') await result;
      }
      if (typeof this.caseWorkflowUiNextTick === 'function') await this.caseWorkflowUiNextTick();
      else await this.$nextTick();

      this.reconcileCaseWorkflowTool68PreparationRootInstances();
      return this.tool68PreparationRows;
    },

    caseWorkflowTool68PreparationRowHashKey(row = {}) {
      return this.caseWorkflowRawToMainHashKey(
        row?.transactionHash
          || row?.hash
          || row?.mainHash
          || row?.rawRootHash
          || row?.rootHash
          || row?.coverageRow?.hash
          || row?.coverageRow?.transactionHash
          || ''
      );
    },

    caseWorkflowTool68PreparationRowStableRootKey(row = {}) {
      const coverageRow = row?.coverageRow || {};
      return this.caseWorkflowRootInstanceKey({
        hash: row?.transactionHash || row?.hash || row?.mainHash || row?.rawRootHash || row?.rootHash || coverageRow?.hash || coverageRow?.transactionHash || '',
        pathLabel: row?.pathLabel || row?.mainPathLabel || row?.effectivePathLabel || coverageRow?.pathLabel || '',
        toAddress: row?.toAddress || row?.targetAddress || row?.toAddressHint || coverageRow?.toAddress || ''
      }, { includeRowIndex: false });
    },

    caseWorkflowTool68PreparationRowInstanceKey(row = {}, index = 0) {
      const explicit = normalizeCell(row?.rootInstanceKey || row?.coverageRow?.rootInstanceKey || '');
      if (explicit) return explicit;
      const spreadsheetRowNumber = Number(row?.spreadsheetRowNumber || row?.mainRowNumber || row?.rowNumber || row?.coverageRow?.spreadsheetRowNumber || 0);
      return this.caseWorkflowRootInstanceKey({
        hash: row?.transactionHash || row?.hash || row?.mainHash || row?.rawRootHash || row?.rootHash || row?.coverageRow?.hash || row?.coverageRow?.transactionHash || '',
        pathLabel: row?.pathLabel || row?.mainPathLabel || row?.effectivePathLabel || row?.coverageRow?.pathLabel || '',
        toAddress: row?.toAddress || row?.targetAddress || row?.toAddressHint || row?.coverageRow?.toAddress || '',
        rowIndex: spreadsheetRowNumber > 0 ? spreadsheetRowNumber - 1 : index
      });
    },

    caseWorkflowTool68PreparationRowSelectionKey(row = {}, index = 0) {
      return this.caseWorkflowTool68PreparationRowInstanceKey(row, index)
        || normalizeCell(row?.id || row?.key || row?.mainKey || this.caseWorkflowTool68PreparationRowHashKey(row) || `tool68-row-${index}`);
    },

    caseWorkflowTool68PreparationRowNeedsAutoRun(row = {}, options = {}) {
      if (!row || typeof row !== 'object') return false;
      const canProcess = row.canProcess !== false || options.allowReprocessLocked === true;
      if (!canProcess) return false;
      if (row.rawCovered === false && !row.hasRawSet && !row.rawSet && !row.matchedRawSet) return false;
      const statusText = [row.status, row.statusLabel, row.message, row.summary, row.warning, row.problem]
        .map(value => normalizeCell(value).toLowerCase())
        .join(' ');
      const hashKey = this.caseWorkflowTool68PreparationRowHashKey(row);
      const stableRootKey = this.caseWorkflowTool68PreparationRowStableRootKey(row);
      const unpreparedRootKeys = options.unpreparedRootKeys instanceof Set ? options.unpreparedRootKeys : new Set();
      const unpreparedHashes = options.unpreparedHashes instanceof Set ? options.unpreparedHashes : new Set();
      if (stableRootKey && unpreparedRootKeys.has(stableRootKey)) return true;
      // Hash-only fallback is safe only for non-duplicate roots. Duplicate BTC
      // roots must match their path label / selected Column D address.
      if (row?.coverageRow?.duplicateHash !== true && row?.duplicateHash !== true && hashKey && unpreparedHashes.has(hashKey)) return true;
      if (row.hasNewerRawSet || row.newerRawSet || row.requiresReprocessConfirmation || row.reprocessAvailable || row.reprocessRequired) return true;
      if (!row.processed) return true;
      if (/processed\s+detected|already\s+prepared|reprocess|newer\s+raw|prepared\s+detected/.test(statusText)) return true;
      return options.includeProcessedWhenSummaryIncomplete === true && row.processed === true;
    },

    selectCaseWorkflowTool68RowsForAuto(rows = [], summary = null, options = {}) {
      const list = Array.isArray(rows) ? rows : [];
      const targetSummary = summary || this.getCaseWorkflowRawToMainPreparationSummary();
      const unpreparedRows = Array.isArray(targetSummary?.unpreparedRows) ? targetSummary.unpreparedRows : [];
      const unpreparedRootKeys = new Set(unpreparedRows
        .map(row => this.caseWorkflowRootInstanceKey({
          hash: row?.hash || row?.coverageRow?.hash || row?.coverageRow?.transactionHash || '',
          pathLabel: row?.pathLabel || row?.coverageRow?.pathLabel || '',
          toAddress: row?.toAddress || row?.coverageRow?.toAddress || ''
        }, { includeRowIndex: false }))
        .filter(Boolean));
      const unpreparedHashes = new Set(unpreparedRows
        .filter(row => row?.duplicateHash !== true && row?.coverageRow?.duplicateHash !== true)
        .map(row => this.caseWorkflowRawToMainHashKey(row?.hash || row?.coverageRow?.hash || row?.coverageRow?.transactionHash || ''))
        .filter(Boolean));
      const includeProcessedWhenSummaryIncomplete = targetSummary?.rawCoverageComplete === true && targetSummary?.isComplete !== true && (unpreparedRootKeys.size > 0 || unpreparedHashes.size > 0);
      let selectedPairs = list
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => this.caseWorkflowTool68PreparationRowNeedsAutoRun(row, {
          unpreparedRootKeys,
          unpreparedHashes,
          includeProcessedWhenSummaryIncomplete,
          allowReprocessLocked: options.allowReprocessLocked === true
        }));

      if (!selectedPairs.length && targetSummary?.rawCoverageComplete === true && targetSummary?.isComplete !== true) {
        selectedPairs = list
          .map((row, index) => ({ row, index }))
          .filter(({ row }) => row?.canProcess !== false && row?.rawCovered !== false);
      }

      const selectedKeys = new Set(selectedPairs.map(({ row, index }) => this.caseWorkflowTool68PreparationRowSelectionKey(row, index)));
      let needsReprocessConfirm = false;
      const nextRows = list.map((row, index) => {
        const include = selectedKeys.has(this.caseWorkflowTool68PreparationRowSelectionKey(row, index));
        if (include && (row?.processed || row?.requiresReprocessConfirmation || row?.hasNewerRawSet || row?.newerRawSet || row?.reprocessAvailable || row?.reprocessRequired)) needsReprocessConfirm = true;
        return { ...(row || {}), include };
      });
      this.tool68PreparationRows = nextRows;
      if (needsReprocessConfirm) this.tool68PreparationConfirmReprocess = true;
      return {
        selectedRows: selectedPairs.map(pair => pair.row),
        selectedCount: selectedPairs.length,
        needsReprocessConfirm,
        selectedKeys
      };
    },

    async runCaseWorkflowRawToMainPreparationAuto(options = {}) {
      const step = options.step || this.getCaseWorkflowRawToMainPreparationStep();
      const forceReprocess = options.forceReprocess === true;
      const tool68ModalWasOpen = Boolean(this.showTool68PreparationModal);
      const initialSummary = this.getCaseWorkflowRawToMainPreparationSummary();
      this.recordCaseWorkflowSaplingPreparationAudit?.({
        stage: 'raw-to-main-auto-start',
        source: options.source || 'workflow-raw-to-main-auto',
        modalWasOpen: tool68ModalWasOpen,
        initialComplete: Boolean(initialSummary?.isComplete),
        rawCoverageComplete: Boolean(initialSummary?.rawCoverageComplete),
        totalRows: Number(initialSummary?.totalRows || 0),
        preparedCount: Number(initialSummary?.preparedCount || 0)
      });
      if (initialSummary.isComplete && !forceReprocess) {
        this.recordCaseWorkflowRawToMainPreparationComplete({
          step,
          summary: initialSummary,
          source: options.source || 'workflow-raw-to-main-current'
        });
        if (this.caseWorkflowTool68PreparationOpenedByAuto && !this.tool68PreparationRunning) {
          this.showTool68PreparationModal = false;
          this.caseWorkflowTool68PreparationOpenedByAuto = false;
        }
        this.recordCaseWorkflowSaplingPreparationAudit?.({
          stage: 'raw-to-main-auto-already-complete',
          source: options.source || 'workflow-raw-to-main-current',
          modalWasOpen: tool68ModalWasOpen,
          modalClosed: Boolean(!this.showTool68PreparationModal),
          totalRows: Number(initialSummary?.totalRows || 0),
          preparedCount: Number(initialSummary?.preparedCount || 0)
        });
        return { handled: true, success: true, status: 'complete', source: 'workflow-raw-to-main-current', summary: initialSummary };
      }
      if (!initialSummary.rawCoverageComplete) {
        return { handled: false, success: false, status: 'raw-coverage-incomplete', summary: initialSummary };
      }

      const previousConfirmReprocess = this.tool68PreparationConfirmReprocess;
      let rows = await this.refreshCaseWorkflowTool68PreparationRowsForAuto();
      if (!rows.length) {
        const setupResult = this.callCaseBuddyOriginalMethod(caseBuddyAutomationMethods, 'automationPrepareRawIntoMain', []);
        if (setupResult && typeof setupResult.then === 'function') await setupResult;
        rows = await this.refreshCaseWorkflowTool68PreparationRowsForAuto();
        const afterSetupSummary = this.getCaseWorkflowRawToMainPreparationSummary();
        if (afterSetupSummary.isComplete && !forceReprocess) {
          this.recordCaseWorkflowRawToMainPreparationComplete({
            step,
            summary: afterSetupSummary,
            source: options.source || 'workflow-raw-to-main-original-setup'
          });
          return { handled: true, success: true, status: 'complete', source: 'workflow-raw-to-main-original-setup', summary: afterSetupSummary };
        }
      }

      let selection = this.selectCaseWorkflowTool68RowsForAuto(rows, initialSummary);
      if (selection.needsReprocessConfirm) {
        this.tool68PreparationConfirmReprocess = true;
        if (typeof this.caseWorkflowUiNextTick === 'function') await this.caseWorkflowUiNextTick();
        else await this.$nextTick();
        rows = await this.refreshCaseWorkflowTool68PreparationRowsForAuto();
        selection = this.selectCaseWorkflowTool68RowsForAuto(rows, initialSummary, { allowReprocessLocked: true });
      }
      if (!selection.selectedRows.length && initialSummary.rawCoverageComplete && (!initialSummary.isComplete || forceReprocess)) {
        rows = Array.isArray(this.tool68PreparationRows) ? this.tool68PreparationRows : rows;
        const forcedPairs = rows
          .map((row, index) => ({ row, index }))
          .filter(({ row }) => row?.canProcess !== false && row?.rawCovered !== false);
        if (forcedPairs.length) {
          const forcedKeys = new Set(forcedPairs.map(({ row, index }) => this.caseWorkflowTool68PreparationRowSelectionKey(row, index)));
          this.tool68PreparationRows = rows.map((row, index) => ({
            ...(row || {}),
            include: forcedKeys.has(this.caseWorkflowTool68PreparationRowSelectionKey(row, index))
          }));
          this.tool68PreparationConfirmReprocess = true;
          selection = {
            selectedRows: forcedPairs.map(pair => pair.row),
            selectedCount: forcedPairs.length,
            needsReprocessConfirm: true,
            selectedKeys: forcedKeys
          };
        }
      }
      if (!selection.selectedRows.length) {
        if (!this.showTool68PreparationModal) this.tool68PreparationConfirmReprocess = previousConfirmReprocess;
        return { handled: false, success: false, status: 'no-tool68-rows-selected', summary: initialSummary, tool68Rows: rows };
      }

      this.tool68PreparationStatus = selection.needsReprocessConfirm
        ? 'Workflow Auto is reprocessing the selected raw-backed flow because current Main Data does not yet reflect the latest raw set.'
        : 'Workflow Auto is preparing selected raw-backed flow(s) into Main Data.';

      if (typeof this.activateLogicalSheet === 'function') {
        await this.activateLogicalSheet('main');
        if (typeof this.caseWorkflowUiNextTick === 'function') await this.caseWorkflowUiNextTick();
        else await this.$nextTick();
      }

      let runResult = null;
      try {
        runResult = await this.runTool68PreparationForSelectedRows({
          source: options.source || 'workflow-raw-to-main-auto',
          workflowAuto: true,
          skipResolvableReviews: !!this.caseAutomation?.skipResolvableReview,
          confirmReprocess: true
        });
      } finally {
        if (!this.showTool68PreparationModal) this.tool68PreparationConfirmReprocess = previousConfirmReprocess;
      }
      await this.refreshCaseWorkflowTool68PreparationRowsForAuto();
      const finalSummary = this.getCaseWorkflowRawToMainPreparationSummary();
      if (finalSummary.isComplete) {
        this.recordCaseWorkflowRawToMainPreparationComplete({
          step,
          summary: finalSummary,
          source: options.source || 'workflow-raw-to-main-auto'
        });
        if (this.caseWorkflowTool68PreparationOpenedByAuto && !tool68ModalWasOpen && !this.tool68PreparationRunning) {
          this.showTool68PreparationModal = false;
          this.caseWorkflowTool68PreparationOpenedByAuto = false;
        }
        this.recordCaseWorkflowSaplingPreparationAudit?.({
          stage: 'raw-to-main-auto-complete',
          source: options.source || 'workflow-raw-to-main-auto',
          modalWasOpen: tool68ModalWasOpen,
          modalClosed: Boolean(!this.showTool68PreparationModal),
          totalRows: Number(finalSummary?.totalRows || 0),
          preparedCount: Number(finalSummary?.preparedCount || 0),
          runResultStatus: normalizeCell(runResult?.status || runResult?.reason || '')
        });
        return { handled: true, success: true, status: 'complete', source: options.source || 'workflow-raw-to-main-auto', result: runResult, summary: finalSummary };
      }
      this.recordCaseWorkflowSaplingPreparationAudit?.({
        stage: 'raw-to-main-auto-needs-review',
        source: options.source || 'workflow-raw-to-main-auto',
        modalWasOpen: tool68ModalWasOpen,
        modalOpen: Boolean(this.showTool68PreparationModal),
        totalRows: Number(finalSummary?.totalRows || 0),
        preparedCount: Number(finalSummary?.preparedCount || 0)
      });
      return { handled: true, success: false, status: 'needs-review', source: options.source || 'workflow-raw-to-main-auto', result: runResult, summary: finalSummary };
    },

    async automationPrepareRawIntoMain(...args) {
      if (this.caseAutomation?.skipResolvableReview || this.caseAutomation?.active || this.caseAutomation?.busy) {
        const step = this.getCaseWorkflowRawToMainPreparationStep();
        const autoResult = await this.runCaseWorkflowRawToMainPreparationAuto({
          source: 'automation-raw-to-main-preparation',
          step
        });
        if (autoResult?.success) return autoResult;
        const summary = autoResult?.summary || this.getCaseWorkflowRawToMainPreparationSummary();
        const message = autoResult?.status === 'raw-coverage-incomplete'
          ? 'Raw Data coverage is not complete yet, so Case Buddy cannot prepare the graph automatically.'
          : autoResult?.status === 'no-tool68-rows-selected'
            ? 'Tool 68 detected raw-data preparation rows, but none were safe to select automatically. Review the preparation panel.'
            : 'Tool 68 could not verify that Raw Data was prepared into Main Data. Review the preparation panel.';
        const now = this.emptyCaseStarterNudgeNowUtc?.() || new Date().toISOString();
        this.caseAutomation = {
          ...(this.caseAutomation || {}),
          active: false,
          busy: false,
          paused: true,
          status: 'Raw-to-main preparation needs attention',
          detail: message,
          manualPause: {
            type: 'manual-pause',
            pauseKind: 'raw-to-main-preparation',
            stepTag: normalizeCell(step?.tag || step?.stepTag || 'RAW_TO_MAIN_PREPARATION'),
            stepTitle: step?.title || step?.stepTitle || 'Prepare raw data into main data',
            title: 'Raw-to-main preparation needs attention',
            message,
            primaryLabel: 'Open preparation panel',
            primaryMethod: 'openTool68PreparationModal',
            openedAtUtc: now,
            summary: {
              rawCoverageComplete: summary?.rawCoverageComplete === true,
              preparedRows: Number(summary?.preparedCount || 0),
              totalRows: Number(summary?.totalRows || 0),
              missingRows: Number(summary?.missingCount || 0)
            }
          }
        };
        this.showCaseAutomationPauseModal = true;
        return { ...(autoResult || {}), handled: true, success: false, paused: true, message };
      }
      const preservedColumnH = this.captureCaseWorkflowColumnHRootValues();
      const result = await this.callCaseBuddyOriginalMethod(caseBuddyAutomationMethods, 'automationPrepareRawIntoMain', args);
      await this.restoreCaseWorkflowColumnHRootValues(preservedColumnH, {
        reason: 'Preserved root Column H USD values after raw-to-main preparation.'
      });
      return result;
    },

    async runTool68PreparationForSelectedRows(...args) {
      const preservedColumnH = this.captureCaseWorkflowColumnHRootValues();
      const result = await this.callFirstCaseBuddyOriginalMethod('runTool68PreparationForSelectedRows', args);
      await this.normalizeCaseBuddyAttributionColumnsForLogicalSheet('main', 'Separated From/To address attribution after raw-data preparation.');
      await this.restoreCaseWorkflowColumnHRootValues(preservedColumnH, {
        reason: 'Preserved root Column H USD values after Tool 68 rewrote prepared graph rows.'
      });
      await this.caseWorkflowUiNextTick?.();
      const summary = this.getCaseWorkflowRawToMainPreparationSummary?.();
      if (result !== false && summary?.isComplete) {
        this.recordCaseWorkflowRawToMainPreparationComplete({
          summary,
          source: 'tool68-preparation-post-run'
        });
      }
      return result;
    },

    async appendRawDataImportToRawSheet(...args) {
      const result = await this.callFirstCaseBuddyOriginalMethod('appendRawDataImportToRawSheet', args);
      await this.normalizeCaseBuddyAttributionColumnsForLogicalSheet('raw', 'Separated From/To address attribution after raw-data import.');
      return result;
    },

    async runWorkflowChecks(...args) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyWorkflowMethods, 'runWorkflowChecks', args);
      const resolved = result && typeof result.then === 'function' ? await result : result;
      this.reconcileCaseWorkflowWwwUsdReferenceCompletionState({ source: 'run-workflow-checks' });
      return resolved;
    },

    async runWorkflowChecksFromStaleBadge(...args) {
      const result = this.callCaseBuddyOriginalMethod(caseBuddyWorkflowMethods, 'runWorkflowChecksFromStaleBadge', args);
      const resolved = result && typeof result.then === 'function' ? await result : result;
      this.reconcileCaseWorkflowWwwUsdReferenceCompletionState({ source: 'run-workflow-checks-stale-badge' });
      return resolved;
    },

    async runWorkflowAction(action, step) {
      this.markEmptyCaseStarterNudgeInvestigationStarted('workflow-action-started');
      if (this.isStartingMainTransactionsWorkflowStep(step) && this.isStartingMainTransactionsCheckConfirmAction(action)) {
        return this.confirmStartingMainTransactionsWorkflowStepFromCurrentRows(step, action);
      }
      if (this.isCaseWorkflowWwwUsdReferenceStep(step)) {
        const actionText = [action?.id, action?.key, action?.action, action?.label, action?.method].map(value => normalizeCell(value).toLowerCase()).join(' ');
        if (/stage/.test(actionText) && typeof this.stageWwwUsdReferenceReviewAudit === 'function') return this.stageWwwUsdReferenceReviewAudit();
        if (/open_main|main[_\s-]*sheet|main\s+data/.test(actionText) && typeof this.activateLogicalSheet === 'function') return this.activateLogicalSheet('main');
        if (this.caseAutomation?.skipResolvableReview || this.caseAutomation?.active || this.caseAutomation?.busy) {
          return this.runCaseWorkflowWwwUsdReferenceAutomationStep({
            step,
            action,
            source: 'workflow-action-www-usd-references'
          });
        }
        return this.openWwwUsdReferenceReviewModal();
      }
      if (this.caseWorkflowRawDataActionLooksLikeFetch(action, step) && this.caseWorkflowRawDataCoverageIsComplete()) {
        this.markCaseWorkflowRawDataCoverageCurrent('workflow-current-raw-data-coverage-action', { step, persist: true });
        this.showWorkflowUiToast?.('Raw-data coverage is already complete. Continue to the next workflow step.');
        return { handled: true, success: true, status: 'success', source: 'workflow-current-raw-data-coverage-action' };
      }
      if (this.caseWorkflowRawDataActionLooksLikeFetch(action, step) && (this.caseWorkflowShouldUseTool88RawData(step) || this.caseWorkflowRawDataTool88Running || this.caseWorkflowTool88RawRunning)) {
        return this.automationFetchRawDataViaConfiguredSource({
          source: 'workflow-action-raw-data-fetch',
          action,
          step,
          workflowStepTag: normalizeCell(step?.tag || 'RAW_DATA_FETCH'),
          stepTag: normalizeCell(step?.tag || 'RAW_DATA_FETCH'),
          stepTitle: step?.title || 'Load raw data for main transactions'
        });
      }
      if (!this.caseDetailsReviewAcknowledgementBypass && this.isCaseDetailsReviewRecordAction(action, step)) {
        this.openCaseDetailsReviewAcknowledgement(step, action);
        return undefined;
      }
      const isAddressColorStep = this.isCaseWorkflowAddressColorLikeCellsAction(action, step);
      const activeSheetBefore = isAddressColorStep ? await this.prepareCaseWorkflowAddressColorLikeCellsRun(step) : '';
      const originalResult = this.callCaseBuddyOriginalMethod(caseBuddyWorkflowMethods, 'runWorkflowAction', [action, step]);
      const resolved = originalResult && typeof originalResult.then === 'function' ? await originalResult : originalResult;
      if (isAddressColorStep) {
        await this.caseWorkflowUiNextTick?.();
        this.recordCaseWorkflowAddressColorLikeCellsCurrent({
          step,
          action,
          source: 'workflow-address-color-like-cells-action',
          activeSheetRestored: activeSheetBefore
        });
      }
      if (this.caseWorkflowAutoSoftStepConfig(step) || this.caseWorkflowAutoSoftStepConfig(action)) {
        this.recordCaseWorkflowAutoSoftStepComplete(step, action, {
          source: 'workflow-action-auto-soft-completion',
          actionId: normalizeCell(action?.id || action?.key || action?.action || '')
        });
      }
      return resolved;
    },

    toggleManualWorkflowComplete(step) {
      this.markEmptyCaseStarterNudgeInvestigationStarted('workflow-step-manual-completion');
      if (!this.caseDetailsReviewAcknowledgementBypass && this.isCaseDetailsReviewWorkflowStep(step) && !step?.sources?.manual) {
        this.openCaseDetailsReviewAcknowledgement(step, null);
        return undefined;
      }
      return this.callCaseBuddyOriginalMethod(caseBuddyWorkflowMethods, 'toggleManualWorkflowComplete', [step]);
    },

    caseWorkflowRawDataContextText(options = {}) {
      const plan = this.caseAutomation?.lastPlan || {};
      const recommended = this.recommendedWorkflowStep || {};
      const pause = this.caseAutomation?.manualPause || {};
      return [
        options?.source,
        options?.workflowStepTag,
        options?.stepTag,
        options?.stepTitle,
        options?.workflowAction,
        options?.action,
        options?.step?.tag,
        options?.step?.title,
        options?.workflowStep?.tag,
        options?.workflowStep?.title,
        plan.stepTag,
        plan.tag,
        plan.stepTitle,
        plan.title,
        recommended.tag,
        recommended.id,
        recommended.title,
        pause.stepTag,
        pause.stepTitle
      ].map(value => normalizeCell(value)).filter(Boolean).join(' ');
    },

    isCaseWorkflowRawDataFetchContext(options = {}) {
      if (this.caseWorkflowAllStepsComplete() && !options?.forceTool88) return false;
      if (options?.caseWorkflowRawData === true || options?.caseBuddyRawDataAutomation === true || options?.workflowRawData === true || options?.rawDataAutomation === true) return true;
      const explicitStep = options?.step || options?.workflowStep || null;
      if (explicitStep && this.caseWorkflowRawDataStepIsCurrent(explicitStep)) return true;
      if (!explicitStep && this.caseWorkflowRawDataStepIsCurrent()) return true;
      const text = this.caseWorkflowRawDataContextText(options).toLowerCase();
      return /\braw[_\s-]*data[_\s-]*(?:fetch|load|loading)|load\s+raw\s+data|raw\s+data\s+for\s+main|raw_data_fetch|use_workspace_raw_import|workspace[_\s-]*raw/.test(text);
    },

    shouldUseCaseWorkflowRawDataTool88Headless(options = {}) {
      if (options?.forceVisible || options?.manualStart || this.caseHopSolverManualStartMode) return false;
      if (this.caseWorkflowAllStepsComplete() && !options?.forceTool88) return false;
      if (!this.caseAutomationPreferHopSolverRawData && !options?.forceTool88) return false;
      const explicitRawContext = options?.caseWorkflowRawData === true ||
        options?.caseBuddyRawDataAutomation === true ||
        options?.workflowRawData === true ||
        options?.rawDataAutomation === true ||
        options?.forceTool88 === true;
      const rawStepIsCurrent = this.caseWorkflowRawDataStepIsCurrent(options?.step || options?.workflowStep || null);
      const rawSolverIsLive = this.caseWorkflowRawDataTool88Running || this.caseWorkflowTool88RawRunning;
      if (!explicitRawContext && !rawStepIsCurrent && !rawSolverIsLive) return false;
      if (this.caseWorkflowRawDataCoverageIsComplete() && !rawSolverIsLive && !options?.forceTool88) return false;
      return this.isCaseWorkflowRawDataFetchContext(options) && !!(this.caseAutomation?.active || options?.forceTool88 || options?.caseWorkflowRawData || rawSolverIsLive);
    },

    async ensureCaseWorkflowRawDataTool88Host() {
      this.caseWorkflowRawDataTool88HostMounted = true;
      await this.$nextTick();
      let tool = this.$refs.caseWorkflowRawDataTool88;
      if (tool) return tool;
      await new Promise(resolve => setTimeout(resolve, 0));
      await this.$nextTick();
      tool = this.$refs.caseWorkflowRawDataTool88;
      if (!tool) throw new Error('Hidden Tool 88 raw-data solver did not mount.');
      return tool;
    },

    getCaseWorkflowRawDataTool88InputRows(options = {}) {
      const matrix = typeof this.getMatrixForLogicalSheet === 'function'
        ? this.getMatrixForLogicalSheet('main')
        : this.emptyCaseStarterGetMatrix?.('main');
      const normalized = this.caseWorkflowNormalizeAttributionMatrix(Array.isArray(matrix) ? matrix : []);
      if (!normalized.length) return [];

      const missingOnly = options.missingOnly !== false;
      const rawMatrix = typeof this.getMatrixForLogicalSheet === 'function'
        ? this.getMatrixForLogicalSheet('raw')
        : [];
      const coverage = this.buildCaseBuddyMainRawDataCoverage(normalized, Array.isArray(rawMatrix) ? rawMatrix : []);
      const targetCoverageRows = (missingOnly && Array.isArray(coverage?.coverageRows) && coverage.coverageRows.length
        ? (Array.isArray(coverage?.missingRows)
          ? coverage.missingRows
          : coverage.coverageRows.filter(row => normalizeCell(row?.status).toLowerCase() !== 'covered'))
        : (Array.isArray(coverage?.coverageRows) ? coverage.coverageRows : []))
        .slice()
        .sort((left, right) => {
          const leftRow = Number.isFinite(Number(left?.rowIndex)) ? Number(left.rowIndex) : Number.MAX_SAFE_INTEGER;
          const rightRow = Number.isFinite(Number(right?.rowIndex)) ? Number(right.rowIndex) : Number.MAX_SAFE_INTEGER;
          return leftRow - rightRow;
        });
      const recordSelection = options.recordSelection !== false;

      if (!targetCoverageRows.length) {
        if (recordSelection) this.caseWorkflowRawDataTool88SelectedInputRoots = [];
        return missingOnly ? [] : normalized;
      }

      const passCoverageRows = [];
      const duplicateHashesUsedThisPass = new Set();
      // Workflow raw-data automation solves one investigator-defined root per
      // pass by default. Tool 88 must finish every branch and unresolved ending
      // for that main transaction before Case Buddy commits the root block and
      // advances to the next transaction. Batching unrelated roots made Simple
      // View appear to jump between roots and allowed untouched root placeholders
      // to affect the active root's flow-completion state. An explicit caller may
      // still raise this limit for a specialized/manual integration.
      const maxRootsPerPass = Math.max(
        1,
        Math.min(
          8,
          Number(
            options.maxRootsPerPass
              ?? options.workflowRawDataMaxRootsPerPass
              ?? options.maxRootInputsPerPass
              ?? 1
          ) || 1
        )
      );
      targetCoverageRows.forEach((row) => {
        if (passCoverageRows.length >= maxRootsPerPass) return;
        const hashKey = this.caseWorkflowRawToMainHashKey(row?.hash || row?.transactionHash || '');
        if (row?.duplicateHash && hashKey) {
          if (duplicateHashesUsedThisPass.has(hashKey)) return;
          duplicateHashesUsedThisPass.add(hashKey);
        }
        passCoverageRows.push(row);
      });

      const selectedRootIndices = Array.from(new Set(
        passCoverageRows
          .map(row => Number.isInteger(Number(row?.rowIndex))
            ? Number(row.rowIndex)
            : (Number.isFinite(Number(row?.spreadsheetRowNumber)) ? Number(row.spreadsheetRowNumber) - 1 : -1))
          .filter(index => index >= 0 && index < normalized.length)
      )).sort((left, right) => left - right);

      const header = normalized.length && this.caseManualIsHeaderRow?.(normalized[0])
        ? normalized[0].slice()
        : CASE_MANUAL_START_CANONICAL_HEADERS.slice();
      const output = [header];
      selectedRootIndices.forEach((rootIndex) => {
        output.push(normalized[rootIndex].slice());
        // Preserve continuation rows whose Column B is intentionally blank and
        // which belong to the selected starting entry. A populated Column B
        // starts the next transaction entry and therefore ends this block.
        for (let index = rootIndex + 1; index < normalized.length; index += 1) {
          const row = normalized[index];
          if (normalizeCell(row?.[CASE_MANUAL_START_COL.TX_HASH])) break;
          if (!rowHasMeaningfulData(row)) break;
          output.push(row.slice());
        }
      });

      if (recordSelection) {
        this.caseWorkflowRawDataTool88SelectedInputRoots = passCoverageRows.map(row => ({
          id: row?.id || '',
          rootInstanceKey: row?.rootInstanceKey || '',
          rowIndex: Number(row?.rowIndex),
          spreadsheetRowNumber: row?.spreadsheetRowNumber || '',
          pathLabel: row?.pathLabel || '',
          hash: row?.hash || row?.transactionHash || '',
          toAddress: row?.toAddress || '',
          duplicateHash: Boolean(row?.duplicateHash)
        }));
      }
      return this.caseWorkflowNormalizeAttributionMatrix(output);
    },

    caseWorkflowRawDataTool88RowStats(rows = []) {
      const list = Array.isArray(rows) ? rows : [];
      const meaningfulRows = list.filter(row => rowHasMeaningfulData(row));
      const txHashes = meaningfulRows
        .map(row => normalizeCell(row?.[1]))
        .filter(Boolean);
      return {
        rowCount: meaningfulRows.length,
        txHashCount: txHashes.length,
        hashes: txHashes
      };
    },

    pickCaseWorkflowRawDataTool88Rows(snapshot = {}, tool = null, options = {}) {
      const candidates = [];
      const allowInputSnapshotFallback = options.allowInputSnapshotFallback === true;
      const pushRows = (label, rows, candidateOptions = {}) => {
        if (!Array.isArray(rows) || !rows.length) return;
        const normalized = this.caseWorkflowNormalizeAttributionMatrix(rows);
        const stats = this.caseWorkflowRawDataTool88RowStats(normalized);
        if (!stats.rowCount || !stats.txHashCount) return;
        const generated = candidateOptions.generated !== false;
        candidates.push({
          label,
          rows: normalized,
          stats,
          generated,
          priority: Number(candidateOptions.priority) || 0
        });
      };
      pushRows('snapshot.outputRows', snapshot?.outputRows, { priority: 110 });
      pushRows('snapshot.caseBuddySnapshotOutputRows', snapshot?.caseBuddySnapshotOutputRows, { priority: 105 });
      pushRows('snapshot.caseBuddyToolOutputRows', snapshot?.caseBuddyToolOutputRows, { priority: 102 });
      pushRows('snapshot.caseBuddyWorkflowVisualRows', snapshot?.caseBuddyWorkflowVisualRows, { priority: 100 });
      pushRows('snapshot.traceExitRows', snapshot?.traceExitRows, { priority: 96 });
      pushRows('snapshot.exitRows', snapshot?.exitRows, { priority: 94 });
      pushRows('tool.currentOutputRows', typeof tool?.getCurrentOutputRowsForExport === 'function' ? tool.getCurrentOutputRowsForExport() : [], { priority: 92 });
      pushRows('tool.outputSnapshotRows', tool?.outputSnapshotRows, { priority: 86 });
      pushRows('tool.traceResult.exitRows', tool?.traceResult?.exitRows, { priority: 80 });
      if (allowInputSnapshotFallback) {
        pushRows('tool.inputSnapshotRows', tool?.inputSnapshotRows, { priority: 1, generated: false });
      }
      const eligible = candidates.filter(candidate => candidate.generated || allowInputSnapshotFallback);
      eligible.sort((left, right) => {
        if (right.generated !== left.generated) return right.generated ? 1 : -1;
        if (right.stats.txHashCount !== left.stats.txHashCount) return right.stats.txHashCount - left.stats.txHashCount;
        if (right.stats.rowCount !== left.stats.rowCount) return right.stats.rowCount - left.stats.rowCount;
        if (right.priority !== left.priority) return right.priority - left.priority;
        return 0;
      });
      this.caseWorkflowRawDataTool88LastCandidateSources = candidates.map(candidate => ({
        label: candidate.label,
        rowCount: candidate.stats.rowCount,
        txHashCount: candidate.stats.txHashCount,
        generated: candidate.generated,
        priority: candidate.priority,
        sampleHashes: candidate.stats.hashes.slice(0, 8)
      }));
      const chosen = eligible[0] || { label: '', rows: [], stats: { rowCount: 0, txHashCount: 0, hashes: [] }, generated: false };
      this.caseWorkflowRawDataTool88LastChosenSource = chosen.label || '';
      return chosen;
    },


    caseWorkflowRawDataTool88ShortText(value = '', head = 8, tail = 6) {
      const text = normalizeCell(value);
      if (!text) return '';
      if (text.length <= head + tail + 3) return text;
      return `${text.slice(0, head)}…${text.slice(-tail)}`;
    },

    buildCaseWorkflowAutomationDebugSnapshot() {
      const mainRows = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('main') : [];
      const rawRows = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('raw') : [];
      const addressColorBasis = this.getCaseWorkflowAddressColorBasis();
      const addressColorRecord = this.getCaseWorkflowAddressColorLikeCellsRecord();
      const workflowGroups = (Array.isArray(this.workflowGroupsView) ? this.workflowGroupsView : []).map(group => ({
        id: group.id || '',
        title: group.title || group.label || '',
        completed: group.completed || 0,
        steps: (Array.isArray(group.steps) ? group.steps : []).map(step => ({
          tag: this.caseWorkflowStepIdentity(step),
          title: step.title || step.stepTitle || '',
          complete: Boolean(step.complete),
          stale: Boolean(step.stale),
          blocked: Boolean(step.blocked),
          status: step.status || '',
          statusLabel: step.statusLabel || '',
          sources: step.sources || {},
          actionIds: (Array.isArray(step.actions) ? step.actions : []).map(action => normalizeCell(action.id || action.key || action.action || action.method || action.label)).filter(Boolean)
        }))
      }));
      const rawCoverage = this.getCaseWorkflowRawDataCoverageSummary?.() || { coverageRows: [], missingRows: [] };
      const rawToMainPreparation = this.getCaseWorkflowRawToMainPreparationSummary?.() || {
        totalRows: 0,
        preparedCount: 0,
        unpreparedRows: [],
        rawCoverageComplete: false,
        isComplete: false
      };
      return {
        kind: 'case-buddy-workflow-automation-debug',
        copiedAtUtc: new Date().toISOString(),
        caseDataRevision: this.caseSupportState?.caseDataRevision || 0,
        currentProjectFileId: this.currentProjectFileId || '',
        activeInputSheetName: normalizeCell(this.$refs.inputSpreadsheet?.activeSheetName || this.$refs.inputSpreadsheet?.getActiveSheet?.()?.name || ''),
        preferences: {
          skipResolvableReviews: Boolean(this.caseAutomation?.skipResolvableReview),
          preferTool88RawData: Boolean(this.caseAutomationPreferHopSolverRawData),
          rawDataSourceMode: this.caseAutomationRawDataSourceMode || '',
          showcaseOverlayEnabled: Boolean(this.caseAutomationShowcaseOverlayEnabled)
        },
        simpleLaunchAutoStart: {
          visible: Boolean(this.caseSimpleLaunchVisible),
          minimized: Boolean(this.caseSimpleLaunchMinimized),
          autoRequested: Boolean(this.caseSimpleLaunchAutoRequested),
          autoStarting: Boolean(this.caseSimpleLaunchAutoStarting),
          skipResolvableReviews: Boolean(this.caseSkipResolvableReviewsEnabled),
          status: this.caseSimpleLaunchStatus || '',
          attemptId: Number(this.caseSimpleLaunchAutoStartAttemptId) || 0,
          pendingWorkflowScrollTimerCount: Array.isArray(this.caseWorkflowScheduledScrollTimers) ? this.caseWorkflowScheduledScrollTimers.length : 0,
          recentAudit: (Array.isArray(this.caseSimpleLaunchAutoStartAudit) ? this.caseSimpleLaunchAutoStartAudit : []).slice(0, 12)
        },
        automation: {
          active: Boolean(this.caseAutomation?.active),
          busy: Boolean(this.caseAutomation?.busy),
          paused: Boolean(this.caseAutomation?.paused),
          status: this.caseAutomation?.status || '',
          detail: this.caseAutomation?.detail || '',
          lastPlan: this.caseAutomation?.lastPlan || null,
          manualPause: this.caseAutomation?.manualPause || null,
          repeatedStepKey: this.caseAutomation?.lastRepeatedStepKey || '',
          repeatedStepCount: Number(this.caseAutomation?.repeatedStepCount) || 0,
          noActionStreak: Number(this.caseAutomation?.noActionStreak) || 0,
          waitingStreak: Number(this.caseAutomation?.waitingStreak) || 0,
          log: (Array.isArray(this.caseAutomation?.log) ? this.caseAutomation.log : []).slice(0, 36)
        },
        workflow: {
          progress: this.workflowProgress || {},
          recommendedStep: this.recommendedWorkflowStep ? {
            tag: this.caseWorkflowStepIdentity(this.recommendedWorkflowStep),
            title: this.recommendedWorkflowStep.title || this.recommendedWorkflowStep.stepTitle || '',
            status: this.recommendedWorkflowStep.status || '',
            statusLabel: this.recommendedWorkflowStep.statusLabel || '',
            complete: Boolean(this.recommendedWorkflowStep.complete),
            stale: Boolean(this.recommendedWorkflowStep.stale),
            blocked: Boolean(this.recommendedWorkflowStep.blocked),
            sources: this.recommendedWorkflowStep.sources || {}
          } : null,
          groups: workflowGroups,
          supportStateSummary: {
            manualCompleteByTag: Object.keys(this.caseSupportState?.workflowState?.manualCompleteByTag || {}),
            signalCompleteByCode: Object.keys(this.caseSupportState?.workflowState?.signalCompleteByCode || {}),
            checkResultIds: Object.keys(this.caseSupportState?.checkResultsById || {}),
            compactPersistenceActive: Boolean(this.caseSupportCompactPersistenceActive),
            compactPersistenceFileId: this.caseSupportCompactPersistenceFileId || '',
            compactPersistenceMode: this.caseSupportCompactPersistenceMode || '',
            compactPersistenceLastSavedBytes: Number(this.caseSupportCompactPersistenceLastSavedBytes) || 0
          },
          liveIntake: this.caseIntakeRailLiveDataState || null
        },
        addressColor: {
          basis: {
            basisHash: addressColorBasis.basisHash,
            rowCount: addressColorBasis.rowCount,
            addressCount: addressColorBasis.addressCount,
            sample: addressColorBasis.basisRows
          },
          record: addressColorRecord,
          recordCurrent: this.caseWorkflowAddressColorLikeCellsRecordIsCurrent(addressColorRecord, addressColorBasis),
          status: this.addressColoringStatus || '',
          lastRunSummary: this.addressColoringLastRunSummary || null
        },
        addressAttributionFill: (() => {
          const state = this.getCaseWorkflowAddressAttributionFillState();
          const workflow = this.caseSupportState?.workflowState || {};
          return {
            roleAware: true,
            complete: Boolean(state.complete),
            missingCount: Number(state.missingCount) || 0,
            addressCount: Number(state.addressCount) || 0,
            groupCount: Number(state.groupCount) || 0,
            basisHash: state.basisHash || '',
            reviewRecord: workflow.addressAttributionFillReview || null,
            checkResult: this.caseSupportState?.checkResultsById?.address_attribution_fill || null,
            rowCount: Array.isArray(this.addressAttributionRows) ? this.addressAttributionRows.length : 0,
            rows: (Array.isArray(state.rows) ? state.rows : []).slice(0, 24).map(row => ({
              id: row.id || '',
              role: row.role || '',
              roleLabel: row.roleLabel || '',
              columnLabel: row.columnLabel || '',
              address: row.address || '',
              occurrenceCount: Number(row.occurrenceCount) || 0,
              missingOccurrenceCount: Number(row.missingOccurrenceCount) || 0,
              currentAttribution: row.currentAttribution || '',
              status: row.status || '',
              statusLabel: row.statusLabel || ''
            }))
          };
        })(),
        rawCoverage: {
          totalRows: rawCoverage.coverageRows?.length || 0,
          missingRows: (rawCoverage.coverageRows || []).filter(row => row.status !== 'covered').slice(0, 24),
          summary: rawCoverage.summary || ''
        },
        rawToMainPreparation: {
          modalOpen: Boolean(this.showTool68PreparationModal),
          modalOpenedByAuto: Boolean(this.caseWorkflowTool68PreparationOpenedByAuto),
          running: Boolean(this.tool68PreparationRunning),
          statusText: this.tool68PreparationStatus || '',
          confirmReprocess: Boolean(this.tool68PreparationConfirmReprocess),
          selectedCount: Number(this.tool68PreparationSelectedCount || 0),
          processedCount: Number(this.tool68PreparationProcessedCount || 0),
          unprocessedCount: Number(this.tool68PreparationUnprocessedCount || 0),
          lastRunSummary: this.tool68PreparationLastRunSummary || null,
          recentAudit: (Array.isArray(this.caseWorkflowSaplingPreparationAudit) ? this.caseWorkflowSaplingPreparationAudit : []).slice(0, 12),
          totalRows: rawToMainPreparation.totalRows || 0,
          preparedCount: rawToMainPreparation.preparedCount || 0,
          rawCoverageComplete: Boolean(rawToMainPreparation.rawCoverageComplete),
          complete: Boolean(rawToMainPreparation.isComplete),
          unpreparedRows: (rawToMainPreparation.unpreparedRows || []).slice(0, 24).map(row => ({
            rowIndex: row.rowIndex,
            spreadsheetRowNumber: row.spreadsheetRowNumber,
            hash: row.hash,
            pathLabel: row.pathLabel || row.coverageRow?.pathLabel || '',
            toAddress: row.toAddress || row.coverageRow?.toAddress || '',
            rootInstanceKey: row.rootInstanceKey || row.coverageRow?.rootInstanceKey || '',
            duplicateHash: Boolean(row.duplicateHash || row.coverageRow?.duplicateHash),
            hasRootDetails: Boolean(row.hasRootDetails),
            hasPreparedChild: Boolean(row.hasPreparedChild),
            hasTerminalOrContinuation: Boolean(row.hasTerminalOrContinuation)
          })),
          tool68Rows: (Array.isArray(this.tool68PreparationRows) ? this.tool68PreparationRows : []).slice(0, 24).map(row => ({
            id: row.id || '',
            hash: row.hash || row.transactionHash || '',
            status: row.status || '',
            statusLabel: row.statusLabel || '',
            rootInstanceKey: row.rootInstanceKey || this.caseWorkflowTool68PreparationRowInstanceKey(row),
            duplicateHash: Boolean(row.duplicateHash || row.coverageRow?.duplicateHash),
            pathLabel: row.pathLabel || row.coverageRow?.pathLabel || '',
            toAddress: row.toAddress || row.coverageRow?.toAddress || '',
            rawRootRowNumber: row.rawRootRowNumber || row.coverageRow?.rawRootRowNumber || '',
            rawRootPathLabel: row.rawRootPathLabel || row.coverageRow?.rawRootPathLabel || '',
            rawRootToAddress: row.rawRootToAddress || row.coverageRow?.rawRootToAddress || '',
            rawSetRowCount: Array.isArray(row.rawSetRows)
              ? row.rawSetRows.length
              : (Array.isArray(row.rawSet) ? row.rawSet.length : (Array.isArray(row.rawSet?.rows) ? row.rawSet.rows.length : 0)),
            fieldKeys: Object.keys(row || {}).sort(),
            processed: Boolean(row.processed),
            hasNewerRawSet: Boolean(row.hasNewerRawSet),
            requiresReprocessConfirmation: Boolean(row.requiresReprocessConfirmation),
            canProcess: Boolean(row.canProcess),
            include: Boolean(row.include)
          }))
        },
        columnHUsdReferences: (() => {
          const state = this.getCaseWorkflowWwwUsdReferenceDirectState();
          const workflow = this.caseSupportState?.workflowState || {};
          const check = this.caseSupportState?.checkResultsById?.www_usd_references || null;
          return {
            recentWriteAudit: (Array.isArray(this.caseWorkflowColumnHWriteAudit) ? this.caseWorkflowColumnHWriteAudit : []).slice(0, 20),
            autoApplyRecord: workflow.columnHUsdReferenceAutoApply || null,
            reviewRecord: workflow.whatWentWhereUsdReferenceReview || null,
            checkResult: check ? {
              id: check.id || 'www_usd_references',
              status: check.status || '',
              message: check.message || check.summary || '',
              revision: Number(check.revision ?? check.checkedRevision ?? check.caseDataRevision) || 0,
              checkedAtUtc: check.checkedAtUtc || ''
            } : null,
            canonicalMainWritesPreserveColumnH: true,
            guardRunning: Boolean(this.caseWorkflowWwwUsdReferenceAutoGuardRunning),
            guardTimerPending: Boolean(this.caseWorkflowWwwUsdReferenceAutoGuardTimer),
            resumeTimerPending: Boolean(this.caseWorkflowWwwUsdReferenceAutoResumeTimer),
            referenceRowCount: state.referenceRowCount || 0,
            usableReferenceCount: state.usableReferenceCount || 0,
            missingUsdReferenceCount: state.missingUsdReferenceCount || 0,
            columnHComplete: Boolean(state.columnHComplete),
            reviewIsCurrent: Boolean(state.reviewIsCurrent),
            summary: state.summary || '',
            rows: (state.referenceRows || []).slice(0, 24).map(row => ({
              rowIndex: row.rowIndex,
              spreadsheetRowNumber: row.spreadsheetRowNumber,
              pathLabel: row.pathLabel,
              hash: row.transactionHash,
              currentColumnH: row.currentColumnH,
              hasUsableColumnH: Boolean(row.hasUsableColumnH),
              amount: row.referenceAmount,
              token: row.referenceToken,
              utcDate: row.utcDate,
              problemText: row.problemText || ''
            }))
          };
        })(),
        generatedDeliverableAutomation: (() => {
          const workflow = this.caseSupportState?.workflowState || {};
          const tagList = Array.from(CASE_WORKFLOW_STATEFUL_REVIEW_TAGS || []);
          const states = {};
          tagList.forEach((tag) => {
            const config = this.caseWorkflowStatefulGeneratedReviewConfig?.(tag);
            const state = config ? this.caseWorkflowStatefulGeneratedReviewReadState(config) : null;
            states[tag] = this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(state) || null;
          });
          return {
            guardRunning: Boolean(this.caseWorkflowStatefulGeneratedReviewAutoGuardRunning),
            guardTimerPending: Boolean(this.caseWorkflowStatefulGeneratedReviewAutoGuardTimer),
            resumeTimerPending: Boolean(this.caseWorkflowStatefulGeneratedReviewAutoResumeTimer),
            backgroundAutomationActive: Boolean(this.caseWorkflowBackgroundAutomationActive),
            chainRunning: Boolean(this.caseWorkflowStatefulGeneratedReviewChainRunning),
            stopToken: Number(this.caseWorkflowStatefulGeneratedReviewStopToken) || 0,
            recentUserStop: Boolean(this.caseWorkflowStatefulGeneratedReviewUserStopIsRecent?.()),
            uiSuppressed: Boolean(this.caseWorkflowReviewAutoUiSuppressed),
            uiSuppressDepth: Number(this.caseWorkflowReviewAutoUiSuppressDepth) || 0,
            lastGuardRunAtMs: Number(this.caseWorkflowStatefulGeneratedReviewAutoGuardLastRunAtMs) || 0,
            lastGuardTag: this.caseWorkflowStatefulGeneratedReviewAutoGuardLastTag || '',
            lastGuardSource: this.caseWorkflowStatefulGeneratedReviewAutoGuardLastSource || '',
            recentAudit: [
              ...(Array.isArray(this.caseWorkflowStatefulGeneratedReviewRuntimeAudit) ? this.caseWorkflowStatefulGeneratedReviewRuntimeAudit : []),
              ...(Array.isArray(workflow.generatedDeliverableAutoAudit) ? workflow.generatedDeliverableAutoAudit : [])
            ].slice(0, 24),
            recordsByTag: {
              ...(workflow.statefulGeneratedReviewByTag || {}),
              ...(this.caseWorkflowStatefulGeneratedReviewRuntimeRecordByTag || {})
            },
            cachedBuildableAddendumState: this.sanitizeCaseWorkflowStatefulGeneratedReviewStateForAudit?.(this.caseWorkflowLastBuildableAddendumState) || null,
            liveStatesByTag: states
          };
        })(),
        tool88RawData: {
          running: Boolean(this.caseWorkflowRawDataTool88Running),
          hostMounted: Boolean(this.caseWorkflowRawDataTool88HostMounted),
          lastError: this.caseWorkflowRawDataTool88LastError || '',
          activity: this.caseAutomationTool88Activity || null,
          progress: this.caseAutomationTool88Progress || null,
          lastSnapshotStatus: this.caseWorkflowRawDataTool88LastSnapshot?.status || '',
          lastSnapshotMessage: this.caseWorkflowRawDataTool88LastSnapshot?.message || '',
          lastSnapshotAction: this.caseWorkflowRawDataTool88LastSnapshot?.integrationAction || '',
          selectedInputRoots: (Array.isArray(this.caseWorkflowRawDataTool88SelectedInputRoots) ? this.caseWorkflowRawDataTool88SelectedInputRoots : []).map(row => ({ ...row })),
          coveragePasses: (Array.isArray(this.caseWorkflowRawDataTool88CoveragePasses) ? this.caseWorkflowRawDataTool88CoveragePasses : []).slice(-24),
          rawGraphAudit: this.caseWorkflowTool88SafeDebugClone(this.caseWorkflowRawDataTool88LastGraphAudit || null)
        },
        rowStats: {
          main: this.caseWorkflowRawDataTool88RowStats(Array.isArray(mainRows) ? mainRows : []),
          raw: this.caseWorkflowRawDataTool88RowStats(Array.isArray(rawRows) ? rawRows : [])
        }
      };
    },

    async copyCaseWorkflowAutomationDebugSnapshot() {
      const snapshot = this.buildCaseWorkflowAutomationDebugSnapshot();
      const text = JSON.stringify(snapshot, null, 2);
      let copied = false;
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }
      if (!copied && typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', 'readonly');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        } finally {
          document.body.removeChild(textarea);
        }
      }
      const message = copied
        ? `Copied workflow Auto debug log (${snapshot.workflow?.progress?.completed || 0}/${snapshot.workflow?.progress?.total || 0} steps).`
        : 'Could not copy workflow Auto debug log automatically.';
      if (typeof this.showCaseToast === 'function') this.showCaseToast(message);
      else this.showWorkflowUiToast?.(message);
      return copied;
    },

    caseWorkflowRawDataTool88DebugRowSummary(row = [], index = null) {
      const source = Array.isArray(row) ? row : [];
      return {
        index,
        pathLabel: normalizeCell(source[CASE_MANUAL_START_COL.PATH_LABEL]),
        hash: normalizeCell(source[CASE_MANUAL_START_COL.TX_HASH]),
        shortHash: this.caseWorkflowRawDataTool88ShortText(source[CASE_MANUAL_START_COL.TX_HASH], 10, 8),
        fromAddress: normalizeCell(source[CASE_MANUAL_START_COL.FROM_ADDRESS]),
        toAddress: normalizeCell(source[CASE_MANUAL_START_COL.TO_ADDRESS]),
        date: normalizeCell(source[CASE_MANUAL_START_COL.DATE]),
        amount: normalizeCell(source[CASE_MANUAL_START_COL.AMOUNT]),
        token: normalizeCell(source[CASE_MANUAL_START_COL.TOKEN]),
        systemNotes: normalizeCell(source[CASE_MANUAL_START_COL.SYSTEM_NOTES]),
        attribution: normalizeCell(source[CASE_MANUAL_START_COL.ATTRIBUTION]),
        fundsFollowed: normalizeCell(source[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]),
        priorBalance: normalizeCell(source[CASE_MANUAL_START_COL.PRIOR_BALANCE]),
        fromHash: normalizeCell(source[CASE_MANUAL_START_COL.FROM_HASH]),
        continuesAs: normalizeCell(source[CASE_MANUAL_START_COL.CONTINUES_AS]),
        leftBoundsDisplay: normalizeCell(source[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY]),
        leftBoundsNumeric: normalizeCell(source[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC]),
        chainId: normalizeCell(source[CASE_MANUAL_START_COL.CHAIN_ID]),
        chainName: normalizeCell(source[CASE_MANUAL_START_COL.CHAIN_NAME]),
        hopLogic: normalizeCell(source[CASE_MANUAL_START_COL.HOP_LOGIC]),
        tool88Notes: normalizeCell(source[CASE_MANUAL_START_COL.CASE_NOTES])
      };
    },

    caseWorkflowRawDataTool88RowsFromCompactVisualRows(rows = []) {
      return (Array.isArray(rows) ? rows : [])
        .map((item) => {
          if (Array.isArray(item)) return item;
          const row = Array.from({ length: CASE_MANUAL_START_COLUMN_COUNT }, () => '');
          row[CASE_MANUAL_START_COL.PATH_LABEL] = normalizeCell(item?.pathLabel);
          row[CASE_MANUAL_START_COL.TX_HASH] = normalizeCell(item?.hash || item?.transactionHash);
          row[CASE_MANUAL_START_COL.FROM_ADDRESS] = normalizeCell(item?.fromAddress);
          row[CASE_MANUAL_START_COL.TO_ADDRESS] = normalizeCell(item?.toAddress);
          row[CASE_MANUAL_START_COL.DATE] = normalizeCell(item?.date || item?.utcDate);
          row[CASE_MANUAL_START_COL.AMOUNT] = normalizeCell(item?.amount);
          row[CASE_MANUAL_START_COL.TOKEN] = normalizeCell(item?.token || item?.tokenSymbol);
          row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = normalizeCell(item?.systemNotes);
          row[CASE_MANUAL_START_COL.ATTRIBUTION] = normalizeCell(item?.toAttribution || item?.toAttributionLabel || item?.attribution);
          row[CASE_MANUAL_START_COL.FROM_ATTRIBUTION] = normalizeCell(item?.fromAttribution || item?.fromAttributionLabel);
          row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED] = normalizeCell(item?.fundsFollowed);
          row[CASE_MANUAL_START_COL.PRIOR_BALANCE] = normalizeCell(item?.priorBalance);
          row[CASE_MANUAL_START_COL.FROM_HASH] = normalizeCell(item?.fromHash);
          row[CASE_MANUAL_START_COL.CONTINUES_AS] = normalizeCell(item?.continuesAs);
          row[CASE_MANUAL_START_COL.LEFT_BOUNDS_DISPLAY] = normalizeCell(item?.leftBoundsDisplay);
          row[CASE_MANUAL_START_COL.LEFT_BOUNDS_NUMERIC] = normalizeCell(item?.leftBoundsNumeric);
          row[CASE_MANUAL_START_COL.CHAIN_ID] = normalizeCell(item?.chainId);
          row[CASE_MANUAL_START_COL.CHAIN_NAME] = normalizeCell(item?.chainName);
          row[CASE_MANUAL_START_COL.HOP_LOGIC] = normalizeCell(item?.hopLogic);
          row[CASE_MANUAL_START_COL.CASE_NOTES] = normalizeCell(item?.tool88Notes || item?.caseNotes);
          return this.caseManualNormalizeAttributionRoleColumnsForRow(row);
        })
        .filter(row => rowHasMeaningfulData(row));
    },

    caseWorkflowRawDataTool88ExtractVisualRowsFromPayload(snapshot = {}) {
      const candidates = [];
      const pushRows = (label, rows) => {
        if (!Array.isArray(rows) || !rows.length) return;
        const normalized = this.caseWorkflowRawDataTool88RowsFromCompactVisualRows(rows);
        const stats = this.caseWorkflowRawDataTool88RowStats(normalized);
        if (!stats.txHashCount) return;
        candidates.push({ label, rows: normalized, stats });
      };
      pushRows('snapshot.caseBuddyWorkflowVisualRows', snapshot?.caseBuddyWorkflowVisualRows);
      pushRows('snapshot.outputRows', snapshot?.outputRows);
      pushRows('snapshot.caseBuddySnapshotOutputRows', snapshot?.caseBuddySnapshotOutputRows);
      pushRows('snapshot.caseBuddyToolOutputRows', snapshot?.caseBuddyToolOutputRows);
      pushRows('snapshot.traceExitRows', snapshot?.traceExitRows);
      pushRows('snapshot.exitRows', snapshot?.exitRows);

      if (!candidates.length && this.caseWorkflowRawDataTool88Running) {
        const tool = this.$refs.caseWorkflowRawDataTool88;
        try {
          if (tool && typeof tool.buildCaseBuddyHopSolverSnapshot === 'function') {
            const liveSnapshot = tool.buildCaseBuddyHopSolverSnapshot({
              includeRows: true,
              status: snapshot?.status || 'workflow-raw-data-live-preview',
              integrationAction: snapshot?.integrationAction || 'workflow-raw-data-live-preview',
              message: snapshot?.message || 'Live Tool 88 raw-data preview.'
            });
            pushRows('tool.liveSnapshot.caseBuddyWorkflowVisualRows', liveSnapshot?.caseBuddyWorkflowVisualRows);
            pushRows('tool.liveSnapshot.outputRows', liveSnapshot?.outputRows);
          }
        } catch (error) {
          // Live visual rows are diagnostic-only; never let a preview probe interrupt Auto.
        }
      }

      candidates.sort((left, right) => {
        if (right.stats.txHashCount !== left.stats.txHashCount) return right.stats.txHashCount - left.stats.txHashCount;
        if (right.stats.rowCount !== left.stats.rowCount) return right.stats.rowCount - left.stats.rowCount;
        return 0;
      });
      return candidates[0]?.rows || [];
    },

    clearCaseWorkflowRawDataTool88PortalCameraFrame() {
      const camera = this.caseWorkflowRawDataTool88PortalCamera;
      const frame = Number(camera?.raf) || 0;
      if (frame && typeof window !== 'undefined') {
        if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame);
        else if (typeof window.clearTimeout === 'function') window.clearTimeout(frame);
      }
      if (camera) camera.raf = 0;
    },

    resetCaseWorkflowRawDataTool88PortalCamera(options = {}) {
      this.clearCaseWorkflowRawDataTool88PortalCameraFrame?.();
      const targetX = Number(options.targetX);
      const targetY = Number(options.targetY);
      const currentX = Number.isFinite(targetX) ? targetX : 210;
      const currentY = Number.isFinite(targetY) ? targetY : 150;
      this.caseWorkflowRawDataTool88PortalCamera = {
        initialized: false,
        currentX,
        currentY,
        startX: currentX,
        startY: currentY,
        targetX: currentX,
        targetY: currentY,
        focusKey: normalizeCell(options.focusKey || ''),
        caseKey: normalizeCell(options.caseKey || ''),
        runKey: normalizeCell(options.runKey || ''),
        raf: 0,
        transitionStartedAtMs: 0,
        transitionDurationMs: 0,
        transitionProgress: 1,
        lastUpdatedAtMs: Date.now()
      };
    },

    caseWorkflowRawDataTool88PortalCameraNowMs() {
      if (typeof performance !== 'undefined' && typeof performance.now === 'function') return performance.now();
      return Date.now();
    },

    caseWorkflowRawDataTool88PortalCameraEaseProgress(progress = 1) {
      const t = Math.max(0, Math.min(1, Number(progress) || 0));
      return t * t * t * (t * (t * 6 - 15) + 10);
    },

    caseWorkflowRawDataTool88PortalCameraDuration(distance = 0) {
      const minDuration = Math.max(120, Number(CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_MIN_DURATION_MS) || 720);
      const baseDuration = Math.max(minDuration, Number(CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_BASE_DURATION_MS) || 1050);
      const maxDuration = Math.max(baseDuration, Number(CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_MAX_DURATION_MS) || 1850);
      const maxDistance = Math.max(1, Number(CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_DISTANCE_FOR_MAX_MS) || 1300);
      const distanceRatio = Math.max(0, Math.min(1, (Number(distance) || 0) / maxDistance));
      return Math.round(baseDuration + (maxDuration - baseDuration) * distanceRatio);
    },

    scheduleCaseWorkflowRawDataTool88PortalCameraFrame() {
      const camera = this.caseWorkflowRawDataTool88PortalCamera;
      if (!camera || camera.raf || !CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_ENABLED) return;
      const runner = () => this.stepCaseWorkflowRawDataTool88PortalCamera();
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        camera.raf = window.requestAnimationFrame(runner);
      } else if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
        camera.raf = window.setTimeout(runner, 16);
      }
    },

    stepCaseWorkflowRawDataTool88PortalCamera() {
      const camera = this.caseWorkflowRawDataTool88PortalCamera;
      if (!camera) return;
      camera.raf = 0;
      if (!CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_ENABLED) {
        camera.currentX = camera.targetX;
        camera.currentY = camera.targetY;
        camera.startX = camera.targetX;
        camera.startY = camera.targetY;
        camera.transitionProgress = 1;
        return;
      }
      const targetX = Number(camera.targetX) || 0;
      const targetY = Number(camera.targetY) || 0;
      const startX = Number.isFinite(Number(camera.startX)) ? Number(camera.startX) : Number(camera.currentX) || 0;
      const startY = Number.isFinite(Number(camera.startY)) ? Number(camera.startY) : Number(camera.currentY) || 0;
      const duration = Math.max(1, Number(camera.transitionDurationMs) || CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_BASE_DURATION_MS);
      const startedAt = Number(camera.transitionStartedAtMs) || this.caseWorkflowRawDataTool88PortalCameraNowMs();
      const elapsed = Math.max(0, this.caseWorkflowRawDataTool88PortalCameraNowMs() - startedAt);
      const linearProgress = Math.max(0, Math.min(1, elapsed / duration));
      const easedProgress = this.caseWorkflowRawDataTool88PortalCameraEaseProgress(linearProgress);
      camera.currentX = startX + (targetX - startX) * easedProgress;
      camera.currentY = startY + (targetY - startY) * easedProgress;
      camera.transitionProgress = linearProgress;
      camera.lastUpdatedAtMs = Date.now();
      const remainingDistance = Math.hypot(targetX - (Number(camera.currentX) || 0), targetY - (Number(camera.currentY) || 0));
      if (linearProgress >= 1 || remainingDistance <= CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_SNAP_DISTANCE) {
        camera.currentX = targetX;
        camera.currentY = targetY;
        camera.startX = targetX;
        camera.startY = targetY;
        camera.transitionProgress = 1;
        return;
      }
      this.scheduleCaseWorkflowRawDataTool88PortalCameraFrame();
    },

    caseWorkflowRawDataTool88PortalCameraTransform(targetX = 0, targetY = 0, focusKey = '') {
      const x = Number(targetX) || 0;
      const y = Number(targetY) || 0;
      if (!CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_ENABLED) {
        this.clearCaseWorkflowRawDataTool88PortalCameraFrame?.();
        return `translate(${x} ${y})`;
      }
      if (!this.caseWorkflowRawDataTool88PortalCamera) this.resetCaseWorkflowRawDataTool88PortalCamera?.();
      const camera = this.caseWorkflowRawDataTool88PortalCamera;
      const caseKey = normalizeCell(this.caseWorkflowRawDataTool88CurrentCaseKey?.() || '');
      const runKey = normalizeCell(this.caseWorkflowRawDataTool88RunKey || '');
      const nextFocusKey = normalizeCell(focusKey || '');
      const identityChanged = camera.caseKey !== caseKey || camera.runKey !== runKey;
      if (!camera.initialized || identityChanged) {
        camera.initialized = true;
        camera.currentX = x;
        camera.currentY = y;
        camera.startX = x;
        camera.startY = y;
        camera.targetX = x;
        camera.targetY = y;
        camera.focusKey = nextFocusKey;
        camera.caseKey = caseKey;
        camera.runKey = runKey;
        camera.transitionStartedAtMs = 0;
        camera.transitionDurationMs = 0;
        camera.transitionProgress = 1;
        camera.lastUpdatedAtMs = Date.now();
        this.clearCaseWorkflowRawDataTool88PortalCameraFrame?.();
        return `translate(${x} ${y})`;
      }
      const targetChanged = Math.abs((Number(camera.targetX) || 0) - x) > 0.05 || Math.abs((Number(camera.targetY) || 0) - y) > 0.05 || camera.focusKey !== nextFocusKey;
      if (targetChanged) {
        const currentXValue = Number(camera.currentX);
        const currentYValue = Number(camera.currentY);
        const startX = Number.isFinite(currentXValue) ? currentXValue : x;
        const startY = Number.isFinite(currentYValue) ? currentYValue : y;
        const distance = Math.hypot(x - startX, y - startY);
        camera.startX = startX;
        camera.startY = startY;
        camera.targetX = x;
        camera.targetY = y;
        camera.focusKey = nextFocusKey;
        camera.transitionStartedAtMs = this.caseWorkflowRawDataTool88PortalCameraNowMs();
        camera.transitionDurationMs = this.caseWorkflowRawDataTool88PortalCameraDuration(distance);
        camera.transitionProgress = 0;
        camera.lastUpdatedAtMs = Date.now();
        if (distance <= CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_SNAP_DISTANCE) {
          camera.currentX = x;
          camera.currentY = y;
          camera.startX = x;
          camera.startY = y;
          camera.transitionProgress = 1;
          this.clearCaseWorkflowRawDataTool88PortalCameraFrame?.();
        } else {
          this.scheduleCaseWorkflowRawDataTool88PortalCameraFrame();
        }
      } else if (Math.hypot((Number(camera.targetX) || 0) - (Number(camera.currentX) || 0), (Number(camera.targetY) || 0) - (Number(camera.currentY) || 0)) > CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_SNAP_DISTANCE) {
        this.scheduleCaseWorkflowRawDataTool88PortalCameraFrame();
      }
      const currentXValue = Number(camera.currentX);
      const currentYValue = Number(camera.currentY);
      const currentX = Number.isFinite(currentXValue) ? currentXValue : x;
      const currentY = Number.isFinite(currentYValue) ? currentYValue : y;
      return `translate(${currentX.toFixed(2)} ${currentY.toFixed(2)})`;
    },

    caseWorkflowRawDataTool88PortalCaseRoots() {
      const matrix = typeof this.getMatrixForLogicalSheet === 'function'
        ? this.getMatrixForLogicalSheet('main')
        : this.emptyCaseStarterGetMatrix?.('main');
      const normalized = this.caseWorkflowNormalizeAttributionMatrix(Array.isArray(matrix) ? matrix : []);
      return this.caseWorkflowCollectRootTransactions(normalized);
    },

    caseWorkflowRawDataTool88PortalRootStableKey(root = {}) {
      return this.caseWorkflowRootInstanceKey({
        hash: root?.hash || root?.transactionHash || root?.txHash || '',
        pathLabel: root?.pathLabel || root?.label || '',
        toAddress: root?.toAddress || root?.targetAddress || '',
        rowIndex: Number.isInteger(Number(root?.rowIndex)) ? Number(root.rowIndex) : -1
      }, { includeRowIndex: false });
    },

    caseWorkflowRawDataTool88PortalRows(caseRootsInput = null) {
      const stateMatchesCurrentCase = this.caseWorkflowRawDataTool88VisualStateMatchesCurrentCase?.() !== false;
      const rawSolverIsLive = !!(
        this.caseWorkflowRawDataTool88Running ||
        this.caseWorkflowTool88RawRunning ||
        this.caseAutomationTool88Activity?.active ||
        this.caseAutomationTool88Progress?.active
      );
      const liveSources = [];
      if (stateMatchesCurrentCase || rawSolverIsLive) {
        liveSources.push(
          this.caseWorkflowRawDataTool88VisualRows,
          this.caseWorkflowRawDataTool88LastSnapshot?.caseBuddyWorkflowVisualRows,
          this.caseWorkflowRawDataTool88LastSnapshot?.outputRows,
          this.caseWorkflowRawDataTool88LastRows
        );
      }
      // This source deliberately contains only the current duplicate-safe Tool 88
      // pass. It remains useful for live detail, but it must not define the whole
      // case-root count shown in Simple View.
      liveSources.push(this.getCaseWorkflowRawDataTool88InputRows?.({ recordSelection: false }));
      let bestRows = [];
      let bestScore = 0;
      liveSources.forEach((rows) => {
        const normalized = this.caseWorkflowRawDataTool88RowsFromCompactVisualRows(rows);
        const stats = this.caseWorkflowRawDataTool88RowStats(normalized);
        const score = stats.txHashCount * 10 + stats.rowCount;
        if (score > bestScore) {
          bestScore = score;
          bestRows = normalized;
        }
      });

      // Completed one-root passes already live in the Raw Data sheet. Merge the
      // current in-memory pass into those persisted blocks for the preview instead
      // of choosing only one source. This keeps Paths 1-4 visible while Path 5 is
      // being solved, and duplicate-hash roots remain separate because the same
      // root-block merger uses Path Label plus investigator-selected To Address.
      const persistedRawMatrix = typeof this.getMatrixForLogicalSheet === 'function'
        ? this.getMatrixForLogicalSheet('raw')
        : [];
      const persistedRawRows = this.caseWorkflowNormalizeAttributionMatrix(
        Array.isArray(persistedRawMatrix) ? persistedRawMatrix : []
      );
      const previewMerge = bestRows.length
        ? this.mergeCaseWorkflowRawRows(persistedRawRows, bestRows)
        : { rows: persistedRawRows };
      const mergedRows = this.caseWorkflowNormalizeAttributionMatrix(
        Array.isArray(previewMerge?.rows) ? previewMerge.rows : []
      ).map(row => Array.isArray(row) ? row.slice() : row);
      if (!mergedRows.length) mergedRows.push(CASE_MANUAL_START_CANONICAL_HEADERS.slice());

      const caseRoots = Array.isArray(caseRootsInput)
        ? caseRootsInput
        : this.caseWorkflowRawDataTool88PortalCaseRoots();
      if (!caseRoots.length) return mergedRows;
      const rootHashCounts = new Map();
      caseRoots.forEach((root) => {
        if (!root?.hashKey) return;
        rootHashCounts.set(root.hashKey, (rootHashCounts.get(root.hashKey) || 0) + 1);
      });

      const findMatchingRootRowIndex = (root = {}) => {
        const candidates = [];
        mergedRows.forEach((row, rowIndex) => {
          if (!Array.isArray(row) || this.caseManualIsHeaderRow?.(row)) return;
          const hashKey = this.caseWorkflowRawToMainHashKey(row?.[CASE_MANUAL_START_COL.TX_HASH]);
          if (!hashKey || hashKey !== root.hashKey) return;
          candidates.push({
            rowIndex,
            pathKey: this.caseWorkflowRootIdentityKey(row?.[CASE_MANUAL_START_COL.PATH_LABEL]),
            targetKey: this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.TO_ADDRESS])
          });
        });
        if (!candidates.length) return -1;
        const exact = candidates.find(candidate =>
          !!root.pathKey && !!root.toAddressKey &&
          candidate.pathKey === root.pathKey && candidate.targetKey === root.toAddressKey
        );
        if (exact) return exact.rowIndex;
        const targetMatch = root.toAddressKey
          ? candidates.find(candidate => candidate.targetKey === root.toAddressKey)
          : null;
        if (targetMatch) return targetMatch.rowIndex;
        const pathMatch = root.pathKey
          ? candidates.find(candidate => candidate.pathKey === root.pathKey)
          : null;
        if (pathMatch) return pathMatch.rowIndex;
        // A hash-only match is safe only when this hash represents one case root.
        // Duplicate-hash roots must remain separate by path label / Column D.
        return (rootHashCounts.get(root.hashKey) || 0) === 1 ? candidates[0].rowIndex : -1;
      };

      caseRoots.forEach((root) => {
        const matchingIndex = findMatchingRootRowIndex(root);
        if (matchingIndex < 0) {
          mergedRows.push(Array.isArray(root.row) ? root.row.slice() : []);
          return;
        }
        const existing = Array.isArray(mergedRows[matchingIndex]) ? mergedRows[matchingIndex].slice() : [];
        const source = Array.isArray(root.row) ? root.row : [];
        const width = Math.max(CASE_MANUAL_START_CANONICAL_HEADERS.length, existing.length, source.length);
        while (existing.length < width) existing.push('');
        for (let columnIndex = 0; columnIndex < width; columnIndex += 1) {
          if (!normalizeCell(existing[columnIndex]) && normalizeCell(source[columnIndex])) {
            existing[columnIndex] = source[columnIndex];
          }
        }
        // Preserve the exact spreadsheet root identity even if a live Tool 88
        // preview omitted its label or normalized the selected BTC output.
        if (root.pathLabel) existing[CASE_MANUAL_START_COL.PATH_LABEL] = root.pathLabel;
        if (root.hash) existing[CASE_MANUAL_START_COL.TX_HASH] = root.hash;
        if (root.toAddress) existing[CASE_MANUAL_START_COL.TO_ADDRESS] = root.toAddress;
        mergedRows[matchingIndex] = existing;
      });

      return this.caseWorkflowNormalizeAttributionMatrix(mergedRows);
    },

    buildCaseWorkflowRawDataTool88PortalState() {
      const caseRoots = this.caseWorkflowRawDataTool88PortalCaseRoots();
      const rows = this.caseWorkflowRawDataTool88PortalRows(caseRoots);
      const knownRootLabels = caseRoots.map(root => root.pathLabel).filter(Boolean);
      const caseRootHashCounts = new Map();
      caseRoots.forEach((root) => {
        if (!root?.hashKey) return;
        caseRootHashCounts.set(root.hashKey, (caseRootHashCounts.get(root.hashKey) || 0) + 1);
      });
      const duplicateHashRootCount = caseRoots.filter(root => (caseRootHashCounts.get(root.hashKey) || 0) > 1).length;
      const duplicateHashGroupCount = Array.from(caseRootHashCounts.values()).filter(count => count > 1).length;

      const selectedPassRoots = Array.isArray(this.caseWorkflowRawDataTool88SelectedInputRoots)
        ? this.caseWorkflowRawDataTool88SelectedInputRoots
        : [];
      const currentPassRootKeys = new Set(
        selectedPassRoots
          .map(root => this.caseWorkflowRawDataTool88PortalRootStableKey(root))
          .filter(Boolean)
      );
      const completedPassRootKeys = new Set();
      try {
        const currentCoverage = this.getCaseWorkflowRawDataCoverageSummary?.() || null;
        (Array.isArray(currentCoverage?.coveredRows) ? currentCoverage.coveredRows : []).forEach((root) => {
          const key = this.caseWorkflowRawDataTool88PortalRootStableKey(root);
          if (key) completedPassRootKeys.add(key);
        });
      } catch {
        // The mini-view remains usable even if coverage is temporarily unavailable.
      }
      (Array.isArray(this.caseWorkflowRawDataTool88CoveragePasses) ? this.caseWorkflowRawDataTool88CoveragePasses : [])
        .filter(pass => pass?.progressed === true || Number(pass?.finishedWithMissingCount) < Number(pass?.startedWithMissingCount))
        .forEach((pass) => {
          const completedRoots = Array.isArray(pass?.completedRoots) && pass.completedRoots.length
            ? pass.completedRoots
            : [];
          if (completedRoots.length) {
            completedRoots.forEach((root) => {
              const key = this.caseWorkflowRawDataTool88PortalRootStableKey(root);
              if (key) completedPassRootKeys.add(key);
            });
            return;
          }
          // Compatibility for older in-memory records. Only infer all selected
          // roots as complete when that pass contained exactly one root.
          const selectedRoots = Array.isArray(pass?.selectedInputRoots) ? pass.selectedInputRoots : [];
          if (selectedRoots.length === 1) {
            const key = this.caseWorkflowRawDataTool88PortalRootStableKey(selectedRoots[0]);
            if (key) completedPassRootKeys.add(key);
          }
        });
      const rawSolverIsLive = !!(
        this.caseWorkflowRawDataTool88Running ||
        this.caseWorkflowTool88RawRunning ||
        this.caseAutomationTool88Activity?.active ||
        this.caseAutomationTool88Progress?.active
      );

      const findCaseRootForRow = (row = []) => {
        const hashKey = this.caseWorkflowRawToMainHashKey(row?.[CASE_MANUAL_START_COL.TX_HASH]);
        if (!hashKey) return null;
        const pathKey = this.caseWorkflowRootIdentityKey(row?.[CASE_MANUAL_START_COL.PATH_LABEL]);
        const targetKey = this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
        const candidates = caseRoots.filter(root => root.hashKey === hashKey);
        if (!candidates.length) return null;
        const exact = candidates.find(root =>
          !!root.pathKey && !!root.toAddressKey && root.pathKey === pathKey && root.toAddressKey === targetKey
        );
        if (exact) return exact;
        const targetMatch = targetKey ? candidates.find(root => root.toAddressKey === targetKey) : null;
        if (targetMatch) return targetMatch;
        const pathMatch = pathKey ? candidates.find(root => root.pathKey === pathKey) : null;
        if (pathMatch) return pathMatch;
        return candidates.length === 1 ? candidates[0] : null;
      };

      const findCaseRootByPathFamily = (pathLabel = '') => {
        if (!normalizeCell(pathLabel)) return null;
        return caseRoots.find(root =>
          root.pathLabel && this.caseWorkflowPathLabelBelongsToRoot(pathLabel, root.pathLabel, knownRootLabels)
        ) || null;
      };

      const seen = new Set();
      const entries = [];
      rows.forEach((row, index) => {
        if (!Array.isArray(row)) return;
        const hash = normalizeCell(row[CASE_MANUAL_START_COL.TX_HASH]);
        const hashKey = hash.toLowerCase();
        if (!hash || /^transaction hash$/i.test(hash)) return;
        const pathLabel = normalizeCell(row[CASE_MANUAL_START_COL.PATH_LABEL]);
        const pathKey = this.caseWorkflowRootIdentityKey(pathLabel);
        const toAddress = normalizeCell(row[CASE_MANUAL_START_COL.TO_ADDRESS]);
        const toAddressKey = this.caseWorkflowRootAddressKey(toAddress);
        const fromHash = normalizeCell(row[CASE_MANUAL_START_COL.FROM_HASH]);
        const fromHashKey = this.caseWorkflowRawToMainHashKey(fromHash);
        const fromAddress = normalizeCell(row[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        const fromAddressKey = this.caseWorkflowRootAddressKey(fromAddress);
        const directCaseRoot = findCaseRootForRow(row);
        const familyCaseRoot = directCaseRoot || findCaseRootByPathFamily(pathLabel);
        const rootStableKey = familyCaseRoot
          ? this.caseWorkflowRawDataTool88PortalRootStableKey(familyCaseRoot)
          : '';
        const baseEntryKey = directCaseRoot
          ? `root|${rootStableKey}`
          : `entry|${hashKey}|${pathKey}|${toAddressKey}|${fromHashKey}`;
        if (seen.has(baseEntryKey)) return;
        seen.add(baseEntryKey);
        const visualKey = `${baseEntryKey}|source:${index}`
          .toLowerCase()
          .replace(/[^a-z0-9_-]+/gu, '-')
          .replace(/^-+|-+$/gu, '')
          .slice(0, 220) || `entry-${index}`;
        const isCaseRoot = !!directCaseRoot;
        const inCurrentPass = !!rootStableKey && currentPassRootKeys.has(rootStableKey);
        const completedEarlierPass = !!rootStableKey && completedPassRootKeys.has(rootStableKey);
        entries.push({
          index,
          entryKey: baseEntryKey,
          visualKey,
          pathLabel,
          pathKey,
          hash,
          hashKey,
          fromAddress,
          fromAddressKey,
          toAddress,
          toAddressKey,
          fromHash,
          fromHashKey,
          amount: normalizeCell(row[CASE_MANUAL_START_COL.AMOUNT]),
          token: normalizeCell(row[CASE_MANUAL_START_COL.TOKEN]),
          fundsFollowed: normalizeCell(row[CASE_MANUAL_START_COL.FUNDS_FOLLOWED]),
          continuesAs: normalizeCell(row[CASE_MANUAL_START_COL.CONTINUES_AS]),
          attribution: normalizeCell(row[CASE_MANUAL_START_COL.ATTRIBUTION]),
          caseRoot: isCaseRoot,
          rootStableKey,
          rootPathLabel: familyCaseRoot?.pathLabel || '',
          currentPassRoot: isCaseRoot && inCurrentPass,
          completedEarlierPassRoot: isCaseRoot && completedEarlierPass,
          deferredRoot: isCaseRoot && rawSolverIsLive && currentPassRootKeys.size > 0 && !inCurrentPass && !completedEarlierPass
        });
      });

      if (!entries.length || !this.caseAutomationShowcaseTool88Visible) {
        return {
          available: false,
          nodes: [],
          edges: [],
          transform: '',
          focusLabel: '',
          summary: '',
          caseRootCount: caseRoots.length,
          currentPassRootCount: currentPassRootKeys.size,
          completedPassRootCount: completedPassRootKeys.size,
          deferredRootCount: 0,
          duplicateHashRootCount,
          duplicateHashGroupCount,
          transactionEntryCount: 0,
          rootInstances: []
        };
      }

      const byHash = new Map();
      entries.forEach((entry) => {
        if (!byHash.has(entry.hashKey)) byHash.set(entry.hashKey, []);
        byHash.get(entry.hashKey).push(entry);
      });
      const resolveParent = (entry) => {
        if (!entry?.fromHashKey) return null;
        let candidates = (byHash.get(entry.fromHashKey) || []).filter(candidate => candidate.entryKey !== entry.entryKey);
        if (!candidates.length) return null;
        const addressMatches = entry.fromAddressKey
          ? candidates.filter(candidate => candidate.toAddressKey && candidate.toAddressKey === entry.fromAddressKey)
          : [];
        if (addressMatches.length) candidates = addressMatches;
        if (entry.rootStableKey) {
          const rootMatches = candidates.filter(candidate => candidate.rootStableKey === entry.rootStableKey);
          if (rootMatches.length) candidates = rootMatches;
        }
        if (entry.pathLabel) {
          const familyMatches = candidates.filter(candidate =>
            candidate.rootPathLabel && this.caseWorkflowPathLabelBelongsToRoot(entry.pathLabel, candidate.rootPathLabel, knownRootLabels)
          );
          if (familyMatches.length) candidates = familyMatches;
        }
        candidates.sort((left, right) => {
          const leftBefore = left.index <= entry.index ? 0 : 1;
          const rightBefore = right.index <= entry.index ? 0 : 1;
          if (leftBefore !== rightBefore) return leftBefore - rightBefore;
          return Math.abs(entry.index - left.index) - Math.abs(entry.index - right.index);
        });
        return candidates[0] || null;
      };

      const parentByEntryKey = new Map();
      entries.forEach((entry) => {
        const parent = resolveParent(entry);
        if (parent) parentByEntryKey.set(entry.entryKey, parent);
      });
      const depthMemo = new Map();
      const computeDepth = (entry, visiting = new Set()) => {
        if (!entry?.entryKey) return 0;
        if (depthMemo.has(entry.entryKey)) return depthMemo.get(entry.entryKey);
        if (visiting.has(entry.entryKey)) return 0;
        visiting.add(entry.entryKey);
        const parent = parentByEntryKey.get(entry.entryKey) || null;
        const depth = parent ? computeDepth(parent, visiting) + 1 : 0;
        visiting.delete(entry.entryKey);
        const safeDepth = Math.max(0, Math.min(40, depth));
        depthMemo.set(entry.entryKey, safeDepth);
        return safeDepth;
      };

      entries.forEach(entry => { entry.depth = computeDepth(entry); });
      const depthGroups = new Map();
      entries.forEach((entry) => {
        const depth = Number(entry.depth) || 0;
        if (!depthGroups.has(depth)) depthGroups.set(depth, []);
        depthGroups.get(depth).push(entry);
      });
      [...depthGroups.values()].forEach((group) => {
        group.sort((left, right) => {
          const leftLabel = left.pathLabel || '';
          const rightLabel = right.pathLabel || '';
          return leftLabel.localeCompare(rightLabel, undefined, { numeric: true, sensitivity: 'base' }) || left.index - right.index;
        });
        const center = (group.length - 1) / 2;
        const laneSpacing = group.length > 1
          ? Math.max(48, Math.min(86, 258 / (group.length - 1)))
          : 86;
        group.forEach((entry, groupIndex) => {
          entry.lane = groupIndex;
          entry.x = Number(entry.depth || 0) * 220;
          entry.y = (groupIndex - center) * laneSpacing;
        });
      });

      const selectedHash = normalizeCell(this.caseAutomationTool88Activity?.targetHash || this.caseAutomationTool88Progress?.selectedHash || '').toLowerCase();
      const selectedTarget = this.caseWorkflowRootAddressKey(this.caseAutomationTool88Activity?.targetAddress || this.caseAutomationTool88Progress?.selectedTarget || '');
      const selectedLabel = this.caseWorkflowRootIdentityKey(this.caseAutomationTool88Activity?.targetLabel || this.caseAutomationTool88Progress?.selectedLabel || '');
      // During a live pass, queued roots are context only. Never let an
      // empty/stale selected target make the camera jump to the last queued root;
      // keep focus on the active root until Tool 88 emits a descendant selection.
      const focusCandidates = rawSolverIsLive
        ? entries.filter(entry => !entry.deferredRoot)
        : entries;
      const focusPool = focusCandidates.length ? focusCandidates : entries;
      const focusEntry = focusPool.find(entry =>
        selectedHash && selectedTarget && entry.hashKey === selectedHash && entry.toAddressKey === selectedTarget && (!selectedLabel || entry.pathKey === selectedLabel)
      )
        || focusPool.find(entry => selectedHash && selectedTarget && entry.hashKey === selectedHash && entry.toAddressKey === selectedTarget)
        || focusPool.find(entry => selectedLabel && selectedTarget && entry.pathKey === selectedLabel && entry.toAddressKey === selectedTarget)
        || focusPool.find(entry => selectedLabel && selectedHash && entry.pathKey === selectedLabel && entry.hashKey === selectedHash)
        || focusPool.find(entry => selectedTarget && entry.toAddressKey === selectedTarget)
        || focusPool.find(entry => selectedLabel && entry.pathKey === selectedLabel)
        || focusPool.find(entry => selectedHash && entry.hashKey === selectedHash)
        || focusPool.find(entry => entry.currentPassRoot)
        || focusPool.find(entry => entry.caseRoot)
        || focusPool[0];

      const nodes = [];
      const edges = [];
      entries.forEach((entry) => {
        const txId = `tx-${entry.visualKey}`;
        const addrId = `addr-${entry.visualKey}`;
        const txFocus = focusEntry && focusEntry.entryKey === entry.entryKey;
        const hasTo = !!entry.toAddress;
        nodes.push({
          id: txId,
          type: 'tx',
          x: entry.x,
          y: entry.y,
          transform: `translate(${entry.x} ${entry.y})`,
          label: entry.pathLabel || 'Tx',
          subLabel: this.caseWorkflowRawDataTool88ShortText(entry.hash, 6, 4),
          focus: txFocus && !hasTo,
          caseRoot: entry.caseRoot,
          currentPassRoot: entry.currentPassRoot,
          deferredRoot: entry.deferredRoot,
          className: [
            'case-automation-showcase__tool88-portal-node',
            'case-automation-showcase__tool88-portal-node--tx',
            entry.deferredRoot ? 'is-deferred-root' : '',
            entry.currentPassRoot ? 'is-current-pass-root' : '',
            entry.completedEarlierPassRoot ? 'is-completed-root' : '',
            txFocus && !hasTo ? 'is-focus' : ''
          ].filter(Boolean).join(' ')
        });
        if (hasTo) {
          nodes.push({
            id: addrId,
            type: 'address',
            x: entry.x + 110,
            y: entry.y,
            transform: `translate(${entry.x + 110} ${entry.y})`,
            label: this.caseWorkflowRawDataTool88ShortText(entry.toAddress, 7, 5),
            subLabel: entry.completedEarlierPassRoot
              ? 'complete'
              : (entry.deferredRoot ? 'queued' : (entry.attribution || 'address')),
            focus: txFocus || (selectedTarget && entry.toAddressKey === selectedTarget),
            caseRoot: entry.caseRoot,
            currentPassRoot: entry.currentPassRoot,
            deferredRoot: entry.deferredRoot,
            className: [
              'case-automation-showcase__tool88-portal-node',
              'case-automation-showcase__tool88-portal-node--address',
              entry.deferredRoot ? 'is-deferred-root' : '',
              entry.currentPassRoot ? 'is-current-pass-root' : '',
              entry.completedEarlierPassRoot ? 'is-completed-root' : '',
              (txFocus || (selectedTarget && entry.toAddressKey === selectedTarget)) ? 'is-focus' : ''
            ].filter(Boolean).join(' ')
          });
          edges.push({
            id: `edge-${entry.visualKey}-tx-to-address`,
            d: `M ${entry.x + 40} ${entry.y} C ${entry.x + 68} ${entry.y} ${entry.x + 76} ${entry.y} ${entry.x + 86} ${entry.y}`,
            className: [
              'case-automation-showcase__tool88-portal-edge',
              'case-automation-showcase__tool88-portal-edge--transfer',
              entry.deferredRoot ? 'is-deferred-root' : ''
            ].filter(Boolean).join(' ')
          });
        }
        const parent = parentByEntryKey.get(entry.entryKey) || null;
        if (parent) {
          const fromX = parent.x + 110 + 24;
          const fromY = parent.y;
          const toX = entry.x - 42;
          const toY = entry.y;
          const midX = Math.max(fromX + 36, (fromX + toX) / 2);
          edges.push({
            id: `edge-${parent.visualKey}-to-${entry.visualKey}`,
            d: `M ${fromX} ${fromY} C ${midX} ${fromY} ${midX} ${toY} ${toX} ${toY}`,
            className: [
              'case-automation-showcase__tool88-portal-edge',
              focusEntry && focusEntry.entryKey === entry.entryKey ? 'is-focus' : ''
            ].filter(Boolean).join(' ')
          });
        }
      });

      const focusX = focusEntry ? focusEntry.x + (focusEntry.toAddress ? 110 : 0) : 0;
      const focusY = focusEntry ? focusEntry.y : 0;
      const targetTranslateX = 210 - focusX;
      const targetTranslateY = 150 - focusY;
      const focusKey = [
        this.caseWorkflowRawDataTool88CurrentCaseKey?.() || '',
        this.caseWorkflowRawDataTool88RunKey || '',
        focusEntry?.entryKey || '',
        selectedTarget
      ].join('|');
      const transform = this.caseWorkflowRawDataTool88PortalCameraTransform(targetTranslateX, targetTranslateY, focusKey);
      const unresolved = Number(this.caseAutomationTool88Activity?.unresolvedCount ?? this.caseAutomationTool88Progress?.openEndings);
      const transactionEntryCount = entries.length;
      const currentPassRootCount = caseRoots.filter(root => currentPassRootKeys.has(this.caseWorkflowRawDataTool88PortalRootStableKey(root))).length;
      const completedPassRootCount = caseRoots.filter(root => completedPassRootKeys.has(this.caseWorkflowRawDataTool88PortalRootStableKey(root))).length;
      const deferredRoots = caseRoots.filter((root) => {
        const key = this.caseWorkflowRawDataTool88PortalRootStableKey(root);
        return rawSolverIsLive && currentPassRootKeys.size > 0 && !currentPassRootKeys.has(key) && !completedPassRootKeys.has(key);
      });
      const summaryParts = [];
      if (caseRoots.length) summaryParts.push(`${caseRoots.length} case root${caseRoots.length === 1 ? '' : 's'}`);
      if (completedPassRootCount > 0) {
        summaryParts.push(`${completedPassRootCount} completed root${completedPassRootCount === 1 ? '' : 's'} retained`);
      }
      if (rawSolverIsLive && currentPassRootCount > 0) {
        summaryParts.push(`${currentPassRootCount} in current Tool 88 pass`);
        if (deferredRoots.length) summaryParts.push(`${deferredRoots.length} queued for later one-root passes`);
      }
      if (transactionEntryCount > caseRoots.length || !caseRoots.length) {
        summaryParts.push(`${transactionEntryCount} transaction${transactionEntryCount === 1 ? '' : 's'} in view`);
      }
      if (Number.isFinite(unresolved)) summaryParts.push(`${Math.max(0, unresolved)} unresolved in current pass`);
      if (Number(this.caseAutomationTool88Activity?.rawRows || this.caseAutomationTool88Activity?.rowsAppended || this.caseAutomationTool88Progress?.outputRows) > 0) {
        const rawRows = Number(this.caseAutomationTool88Activity?.rawRows || this.caseAutomationTool88Activity?.rowsAppended || this.caseAutomationTool88Progress?.outputRows);
        summaryParts.push(`${rawRows.toLocaleString('en-US')} raw row${rawRows === 1 ? '' : 's'} staged`);
      }
      const rootInstances = caseRoots.map((root) => {
        const rootStableKey = this.caseWorkflowRawDataTool88PortalRootStableKey(root);
        return {
          rootInstanceKey: this.caseWorkflowRootInstanceKey(root),
          stableKey: rootStableKey,
          rowIndex: Number(root.rowIndex),
          spreadsheetRowNumber: root.spreadsheetRowNumber,
          pathLabel: root.pathLabel,
          hash: root.hash,
          toAddress: root.toAddress,
          duplicateHash: (caseRootHashCounts.get(root.hashKey) || 0) > 1,
          inCurrentPass: currentPassRootKeys.has(rootStableKey),
          completedEarlierPass: completedPassRootKeys.has(rootStableKey),
          deferred: deferredRoots.some(deferredRoot => this.caseWorkflowRawDataTool88PortalRootStableKey(deferredRoot) === rootStableKey)
        };
      });
      return {
        available: true,
        nodes,
        edges,
        transform,
        focusLabel: focusEntry ? `${focusEntry.pathLabel || 'Path'} · ${this.caseWorkflowRawDataTool88ShortText(focusEntry.hash, 10, 8)}` : 'Tool 88 raw-data flow',
        summary: summaryParts.join(' · '),
        caseRootCount: caseRoots.length,
        currentPassRootCount,
        completedPassRootCount,
        deferredRootCount: deferredRoots.length,
        duplicateHashRootCount,
        duplicateHashGroupCount,
        transactionEntryCount,
        rootInstances
      };
    },

    caseWorkflowTool88DebugClipText(value = '', limit = 6000) {
      const text = normalizeCell(value);
      const maxLength = Math.max(100, Number(limit) || 6000);
      return text.length > maxLength ? `${text.slice(0, maxLength)}… [truncated ${text.length - maxLength} chars]` : text;
    },

    caseWorkflowTool88CompactRunStateForDebug(runState = null) {
      const source = runState && typeof runState === 'object' ? runState : {};
      const compactStep = step => {
        if (!step || typeof step !== 'object') return null;
        return {
          atUtc: step.atUtc || '',
          stepNumber: Number(step.stepNumber) || 0,
          action: normalizeCell(step.action || step.integrationAction || ''),
          status: normalizeCell(step.status || ''),
          message: this.caseWorkflowTool88DebugClipText(step.message || '', 900),
          selectedEntry: step.selectedEntry ? {
            key: normalizeCell(step.selectedEntry.key || ''),
            pathLabel: normalizeCell(step.selectedEntry.pathLabel || ''),
            txHash: normalizeCell(step.selectedEntry.txHash || step.selectedEntry.hash || ''),
            shortHash: normalizeCell(step.selectedEntry.shortHash || ''),
            spreadsheetRowNumber: Number.isFinite(Number(step.selectedEntry.spreadsheetRowNumber)) ? Number(step.selectedEntry.spreadsheetRowNumber) : null
          } : null,
          selectedTargetAddress: normalizeCell(step.selectedTargetAddress || ''),
          decisionAction: normalizeCell(step.decisionAction || ''),
          flowComplete: step.flowComplete === true,
          flowCompletion: step.flowCompletion || null,
          outputRowCount: Number(step.outputRowCount) || 0,
          exitRowCount: Number(step.exitRowCount) || 0,
          simpleResultReadyForContinue: step.simpleResultReadyForContinue === true,
          traceResultPresent: step.traceResultPresent === true,
          traceOutputMutationVerified: step.traceOutputMutationVerified === true,
          needsManualReview: step.needsManualReview === true,
          needsTargetSelection: step.needsTargetSelection === true,
          unverifiedOutput: step.unverifiedOutput === true,
          stateVisitCount: Number(step.stateVisitCount) || 0,
          noProgressCount: Number(step.noProgressCount) || 0,
          guardrailReason: normalizeCell(step.guardrailReason || '')
        };
      };
      return {
        active: source.active === true,
        stopRequested: source.stopRequested === true,
        startedAtMs: Number(source.startedAtMs) || 0,
        finishedAtMs: Number(source.finishedAtMs) || 0,
        stepCount: Number(source.stepCount) || 0,
        researchedEntryCount: Number(source.researchedEntryCount) || (Array.isArray(source.researchedEntryKeys) ? source.researchedEntryKeys.length : 0),
        researchedEntryKeys: Array.isArray(source.researchedEntryKeys) ? source.researchedEntryKeys.slice(-40) : [],
        repeatedActionCount: Number(source.repeatedActionCount) || 0,
        noProgressCount: Number(source.noProgressCount) || 0,
        currentStateVisitCount: Number(source.currentStateVisitCount) || 0,
        status: normalizeCell(source.status || ''),
        detail: this.caseWorkflowTool88DebugClipText(source.detail || '', 1200),
        lastGuardrail: source.lastGuardrail ? {
          reason: normalizeCell(source.lastGuardrail.reason || ''),
          message: this.caseWorkflowTool88DebugClipText(source.lastGuardrail.message || '', 1200)
        } : null,
        lastResultSummary: compactStep(source.lastResultSummary || source.lastResult),
        recentSteps: (Array.isArray(source.recentSteps) ? source.recentSteps : []).slice(0, 16).map(compactStep).filter(Boolean),
        recentStateKeys: (Array.isArray(source.recentStateKeys) ? source.recentStateKeys : []).slice(-18).map(value => this.caseWorkflowTool88DebugClipText(value, 500)),
        flowCompleteConfirmations: Number(source.flowCompleteConfirmations) || 0
      };
    },

    caseWorkflowTool88CompactSnapshotForDebug(snapshot = null, options = {}) {
      if (!snapshot || typeof snapshot !== 'object') return snapshot == null ? snapshot : this.caseWorkflowTool88DebugClipText(snapshot, 1200);
      const outputRows = Array.isArray(snapshot.outputRows) ? snapshot.outputRows : [];
      const exitRows = Array.isArray(snapshot.exitRows) ? snapshot.exitRows : [];
      const traceExitRows = Array.isArray(snapshot.traceExitRows) ? snapshot.traceExitRows : [];
      const resultTraceRows = Array.isArray(snapshot.result?.trace?.exitRows) ? snapshot.result.trace.exitRows : [];
      const includeRowSamples = options.includeRowSamples !== false;
      const selected = snapshot.selectedEntry || null;
      const flow = snapshot.flowCompletion || null;
      const result = snapshot.result || {};
      const trace = result.trace || null;
      const pathEnding = result.pathEnding || null;
      const swap = result.swap || null;
      const bridge = result.bridge || null;
      return {
        integrationVersion: normalizeCell(snapshot.integrationVersion || ''),
        status: normalizeCell(snapshot.status || ''),
        message: this.caseWorkflowTool88DebugClipText(snapshot.message || '', 2400),
        integrationAction: normalizeCell(snapshot.integrationAction || ''),
        traceDirection: normalizeCell(snapshot.traceDirection || ''),
        hopLogicType: normalizeCell(snapshot.hopLogicType || ''),
        guardrail: snapshot.guardrail ? {
          reason: normalizeCell(snapshot.guardrail.reason || ''),
          message: this.caseWorkflowTool88DebugClipText(snapshot.guardrail.message || '', 2400)
        } : null,
        selectedEntry: selected ? {
          key: normalizeCell(selected.key || ''),
          pathLabel: normalizeCell(selected.pathLabel || ''),
          txHash: normalizeCell(selected.txHash || selected.hash || ''),
          shortHash: normalizeCell(selected.shortHash || ''),
          spreadsheetRowNumber: Number.isFinite(Number(selected.spreadsheetRowNumber)) ? Number(selected.spreadsheetRowNumber) : null,
          fromAddress: normalizeCell(selected.fromAddress || ''),
          toAddress: normalizeCell(selected.toAddress || '')
        } : null,
        selectedTargetAddress: normalizeCell(snapshot.selectedTargetAddress || ''),
        transactionCount: snapshot.transactionCount ?? snapshot.knownTransactionCount ?? null,
        countLookup: snapshot.countLookup ? {
          transactionCount: snapshot.countLookup.transactionCount ?? snapshot.countLookup.transaction_count ?? null,
          source: normalizeCell(snapshot.countLookup.source || snapshot.countLookup.provider || ''),
          classification: normalizeCell(snapshot.countLookup.classification || ''),
          message: this.caseWorkflowTool88DebugClipText(snapshot.countLookup.message || snapshot.countLookup.summary || '', 1200)
        } : null,
        decision: snapshot.decision ? {
          action: normalizeCell(snapshot.decision.action || ''),
          status: normalizeCell(snapshot.decision.status || ''),
          title: normalizeCell(snapshot.decision.title || ''),
          summary: this.caseWorkflowTool88DebugClipText(snapshot.decision.summary || '', 1800),
          reasons: (Array.isArray(snapshot.decision.reasons) ? snapshot.decision.reasons : []).slice(0, 8).map(value => this.caseWorkflowTool88DebugClipText(value, 1000))
        } : null,
        result: {
          trace: trace ? {
            exitRowCount: Number(trace.exitRowCount) || resultTraceRows.length,
            outputMutationVerified: trace.outputMutationVerified === true,
            preventPathEnding: trace.preventPathEnding === true,
            analysisDirection: normalizeCell(trace.analysisDirection || ''),
            serviceStopOverride: trace.serviceStopOverride === true,
            serviceOverrideReason: this.caseWorkflowTool88DebugClipText(trace.serviceOverrideReason || '', 1200)
          } : null,
          pathEnding: pathEnding ? {
            reason: this.caseWorkflowTool88DebugClipText(pathEnding.reason || '', 1800),
            note: this.caseWorkflowTool88DebugClipText(pathEnding.note || '', 1800),
            specialTreatment: normalizeCell(pathEnding.specialTreatment || ''),
            dormantAmountText: normalizeCell(pathEnding.dormantAmountText || ''),
            lowValueAuto: pathEnding.lowValueAuto === true
          } : null,
          swap: swap ? {
            title: normalizeCell(swap.title || ''),
            summary: this.caseWorkflowTool88DebugClipText(swap.summary || '', 1600),
            note: this.caseWorkflowTool88DebugClipText(swap.note || '', 1600),
            insertedRowCount: Number(swap.insertedRowCount) || 0,
            txHash: normalizeCell(swap.txHash || '')
          } : null,
          bridge: bridge ? {
            title: normalizeCell(bridge.title || ''),
            summary: this.caseWorkflowTool88DebugClipText(bridge.summary || '', 1600),
            note: this.caseWorkflowTool88DebugClipText(bridge.note || '', 1600),
            insertedRowCount: Number(bridge.insertedRowCount) || 0,
            destinationAddress: normalizeCell(bridge.destinationAddress || ''),
            destinationNetwork: normalizeCell(bridge.destinationNetwork || '')
          } : null
        },
        flowComplete: snapshot.flowComplete === true,
        flowCompletion: flow ? {
          done: flow.done === true,
          totalEntries: Number(flow.totalEntries) || 0,
          endingCount: Number(flow.endingCount) || 0,
          incompleteCount: Number(flow.incompleteCount) || 0,
          completeCount: Number(flow.completeCount) || 0,
          summary: this.caseWorkflowTool88DebugClipText(flow.summary || '', 1800)
        } : null,
        canContinue: snapshot.canContinue === true,
        needsManualReview: snapshot.needsManualReview === true,
        needsTargetSelection: snapshot.needsTargetSelection === true,
        unverifiedOutput: snapshot.unverifiedOutput === true,
        isProcessing: snapshot.isProcessing === true,
        warnings: (Array.isArray(snapshot.warnings) ? snapshot.warnings : []).slice(-12).map(value => this.caseWorkflowTool88DebugClipText(value, 1600)),
        reportTail: this.caseWorkflowTool88DebugClipText(snapshot.reportText || '', 5000),
        rowCounts: {
          outputRows: outputRows.length,
          exitRows: exitRows.length,
          traceExitRows: traceExitRows.length,
          resultTraceExitRows: resultTraceRows.length
        },
        outputRowsSample: includeRowSamples ? this.caseWorkflowRawDataTool88SummarizeRowsForDebug(outputRows, Number(options.rowSampleLimit) || 12) : [],
        exitRowsSample: includeRowSamples ? this.caseWorkflowRawDataTool88SummarizeRowsForDebug(exitRows.length ? exitRows : resultTraceRows, 8) : [],
        runState: this.caseWorkflowTool88CompactRunStateForDebug(snapshot.runState || null),
        stallDiagnostics: this.caseWorkflowTool88CompactDebugValue(snapshot.stallDiagnostics || null, { maxDepth: 5, maxArrayItems: 20 }, 0, new WeakSet(), 'stallDiagnostics'),
        rawGraphAudit: this.caseWorkflowTool88CompactDebugValue(snapshot.rawGraphAudit || null, { maxDepth: 5, maxArrayItems: 24 }, 0, new WeakSet(), 'rawGraphAudit'),
        rawGraphRepair: this.caseWorkflowTool88CompactDebugValue(snapshot.rawGraphRepair || null, { maxDepth: 5, maxArrayItems: 24 }, 0, new WeakSet(), 'rawGraphRepair'),
        generatedAtUtc: normalizeCell(snapshot.generatedAtUtc || '')
      };
    },

    caseWorkflowTool88CompactDebugValue(value = null, options = {}, depth = 0, seen = new WeakSet(), key = '') {
      if (value == null) return value;
      if (typeof value === 'string') {
        const lowerKey = normalizeCell(key).toLowerCase();
        const limit = /report|outputtsv|text|payload/.test(lowerKey) ? 5000 : 2400;
        return this.caseWorkflowTool88DebugClipText(value, limit);
      }
      if (typeof value === 'number' || typeof value === 'boolean') return value;
      if (typeof value === 'function') return '[function omitted]';
      if (typeof value !== 'object') return this.caseWorkflowTool88DebugClipText(value, 1200);
      if (seen.has(value)) return '[circular or repeated reference omitted]';
      seen.add(value);

      const maxDepth = Math.max(2, Number(options.maxDepth) || 6);
      if (depth >= maxDepth) {
        if (Array.isArray(value)) return { omittedAtDepth: depth, itemCount: value.length };
        return { omittedAtDepth: depth, keyCount: Object.keys(value).length };
      }

      if (!Array.isArray(value) && (
        'integrationVersion' in value ||
        ('status' in value && ('outputRows' in value || 'flowCompletion' in value || 'selectedEntry' in value) && ('integrationAction' in value || 'runState' in value))
      )) {
        return this.caseWorkflowTool88CompactSnapshotForDebug(value, { includeRowSamples: true, rowSampleLimit: 10 });
      }

      if (Array.isArray(value)) {
        const maxArrayItems = Math.max(4, Number(options.maxArrayItems) || 30);
        const looksLikeRows = value.length > 0 && value.slice(0, Math.min(4, value.length)).every(item => Array.isArray(item));
        if (looksLikeRows) {
          return {
            rowCount: value.length,
            sample: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(value, Math.min(16, maxArrayItems))
          };
        }
        const items = value.slice(0, maxArrayItems).map((item, index) => this.caseWorkflowTool88CompactDebugValue(item, options, depth + 1, seen, `${key}[${index}]`));
        if (value.length > maxArrayItems) items.push({ omittedItemCount: value.length - maxArrayItems });
        return items;
      }

      if (/runstate/i.test(key)) return this.caseWorkflowTool88CompactRunStateForDebug(value);
      const output = {};
      const entries = Object.entries(value);
      const maxKeys = Math.max(12, Number(options.maxKeys) || 70);
      for (const [childKey, childValue] of entries.slice(0, maxKeys)) {
        if (childKey === 'outputTsv') {
          output.outputTsvLength = normalizeCell(childValue).length;
          continue;
        }
        if (childKey === 'reportText') {
          output.reportTail = this.caseWorkflowTool88DebugClipText(childValue || '', 5000);
          continue;
        }
        if (childKey === 'lastResult' && value.lastResultSummary) continue;
        output[childKey] = this.caseWorkflowTool88CompactDebugValue(childValue, options, depth + 1, seen, childKey);
      }
      if (entries.length > maxKeys) output.omittedKeyCount = entries.length - maxKeys;
      return output;
    },

    caseWorkflowTool88SafeDebugClone(value = null) {
      try {
        return this.caseWorkflowTool88CompactDebugValue(value, { maxDepth: 6, maxArrayItems: 30, maxKeys: 70 }, 0, new WeakSet(), 'root');
      } catch (error) {
        return {
          cloneError: error?.message || String(error || 'Could not compact Tool 88 debug payload.'),
          textPreview: this.caseWorkflowTool88DebugClipText(value, 5000)
        };
      }
    },

    recordCaseWorkflowRawDataTool88RunEvent(eventName = 'event', payload = {}) {
      const compactPayload = {
        status: normalizeCell(payload?.status),
        message: normalizeCell(payload?.message),
        integrationAction: normalizeCell(payload?.integrationAction),
        flowComplete: payload?.flowComplete === true,
        flowCompletion: payload?.flowCompletion || null,
        needsManualReview: payload?.needsManualReview === true,
        needsTargetSelection: payload?.needsTargetSelection === true,
        unverifiedOutput: payload?.unverifiedOutput === true,
        guardrail: payload?.guardrail || null,
        selectedEntry: payload?.selectedEntry || null,
        selectedTargetAddress: normalizeCell(payload?.selectedTargetAddress),
        outputRowCount: Array.isArray(payload?.outputRows) ? payload.outputRows.filter(row => rowHasMeaningfulData(row)).length : 0,
        traceExitRowCount: Array.isArray(payload?.traceExitRows) ? payload.traceExitRows.filter(row => rowHasMeaningfulData(row)).length : 0,
        exitRowCount: Array.isArray(payload?.exitRows) ? payload.exitRows.filter(row => rowHasMeaningfulData(row)).length : 0
      };
      const event = {
        atUtc: new Date().toISOString(),
        eventName: normalizeCell(eventName || 'event'),
        payload: compactPayload
      };
      const existing = Array.isArray(this.caseWorkflowRawDataTool88RunEvents) ? this.caseWorkflowRawDataTool88RunEvents : [];
      this.caseWorkflowRawDataTool88RunEvents = [event, ...existing].slice(0, 80);
      return event;
    },

    captureCaseWorkflowRawDataTool88LastRunRecord(stage = 'captured', patch = {}) {
      const tool = this.$refs.caseWorkflowRawDataTool88 || null;
      const mainRows = this.getCaseWorkflowRawDataTool88InputRows?.({ recordSelection: false }) || [];
      const rawRows = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('raw') : [];
      const coverage = this.buildCaseBuddyMainRawDataCoverage(
        typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('main') : mainRows,
        Array.isArray(rawRows) ? rawRows : []
      );
      let liveSnapshot = null;
      try {
        if (tool && typeof tool.buildCaseBuddyHopSolverSnapshot === 'function') {
          liveSnapshot = tool.buildCaseBuddyHopSolverSnapshot({
            includeRows: false,
            status: `workflow-raw-data-debug-${normalizeCell(stage) || 'captured'}`,
            integrationAction: 'capture-last-run-debug',
            message: `Case Buddy captured hidden Tool 88 raw-data state at ${normalizeCell(stage) || 'captured'}.`
          });
        }
      } catch (error) {
        liveSnapshot = { error: error?.message || String(error || 'Could not capture live Tool 88 snapshot.') };
      }
      const record = {
        kind: 'case-buddy-workflow-tool88-last-run-record',
        capturedAtUtc: new Date().toISOString(),
        stage: normalizeCell(stage || 'captured'),
        runKey: normalizeCell(this.caseWorkflowRawDataTool88RunKey || ''),
        caseKey: normalizeCell(this.caseWorkflowRawDataTool88CurrentCaseKey?.() || ''),
        preferences: {
          skipResolvableReviews: Boolean(this.caseAutomation?.skipResolvableReview),
          preferTool88RawData: Boolean(this.caseAutomationPreferHopSolverRawData),
          rawDataSourceMode: this.caseAutomationRawDataSourceMode || ''
        },
        automation: {
          active: Boolean(this.caseAutomation?.active),
          busy: Boolean(this.caseAutomation?.busy),
          paused: Boolean(this.caseAutomation?.paused),
          status: this.caseAutomation?.status || '',
          detail: this.caseAutomation?.detail || '',
          lastPlan: this.caseWorkflowTool88SafeDebugClone(this.caseAutomation?.lastPlan || null),
          manualPause: this.caseWorkflowTool88SafeDebugClone(this.caseAutomation?.manualPause || null)
        },
        activity: this.caseWorkflowTool88SafeDebugClone(this.caseAutomationTool88Activity || null),
        progress: this.caseWorkflowTool88SafeDebugClone(this.caseAutomationTool88Progress || null),
        lastSnapshot: this.caseWorkflowTool88SafeDebugClone(this.caseWorkflowRawDataTool88LastSnapshot || null),
        liveSnapshot: this.caseWorkflowTool88SafeDebugClone(liveSnapshot),
        patch: this.caseWorkflowTool88SafeDebugClone(patch || {}),
        runEvents: this.caseWorkflowTool88SafeDebugClone(this.caseWorkflowRawDataTool88RunEvents || []),
        lastRows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(this.caseWorkflowRawDataTool88LastRows, 80),
        visualRows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(this.caseWorkflowRawDataTool88VisualRows, 80),
        rawGraphAudit: this.caseWorkflowTool88SafeDebugClone(this.caseWorkflowRawDataTool88LastGraphAudit || null),
        rawCoverage: {
          totalRows: coverage.coverageRows?.length || 0,
          missingRows: (coverage.coverageRows || []).filter(row => row.status !== 'covered').slice(0, 40),
          summary: coverage.summary || ''
        },
        mainRows: {
          stats: this.caseWorkflowRawDataTool88RowStats(mainRows),
          sample: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(mainRows, 40)
        },
        rawRows: {
          stats: this.caseWorkflowRawDataTool88RowStats(rawRows),
          sample: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(rawRows, 60)
        }
      };
      this.caseWorkflowRawDataTool88LastRunRecord = record;
      return record;
    },

    updateCaseAutomationTool88ActivityFromSnapshot(eventName = 'case-buddy-status', payload = {}) {
      let snapshot = payload && typeof payload === 'object' ? payload : {};
      const currentCaseKey = this.caseWorkflowRawDataTool88CurrentCaseKey();
      const stateCaseKey = normalizeCell(this.caseWorkflowRawDataTool88VisualCaseKey || '');
      const rawSolverIsLive = this.caseWorkflowRawDataTool88Running || this.caseWorkflowTool88RawRunning;
      if (stateCaseKey && stateCaseKey !== currentCaseKey && !rawSolverIsLive) return;
      if (!stateCaseKey || rawSolverIsLive) this.caseWorkflowRawDataTool88VisualCaseKey = currentCaseKey;
      const existingRowsAppended = Number(this.caseAutomationTool88Activity?.rowsAppended || 0) || (Array.isArray(this.caseWorkflowRawDataTool88LastRows) ? this.caseWorkflowRawDataTool88LastRows.filter(row => rowHasMeaningfulData(row)).length : 0);
      const snapshotRowsAppended = Number(snapshot.rowsAppended || snapshot.rawRows || 0);
      const adverseText = [snapshot.status, snapshot.message, snapshot.detail, eventName].map(value => normalizeCell(value).toLowerCase()).join(' ');
      if ((existingRowsAppended > 0 || snapshotRowsAppended > 0) && /not-mounted|wayfinder\s+bridge|did\s+not\s+finish\s+automatically|output\s+insertion\s+was\s+not\s+verified|raw-data\s+loading\s+needs\s+attention/.test(adverseText)) {
        const rowCount = Math.max(existingRowsAppended, snapshotRowsAppended);
        snapshot = {
          ...snapshot,
          status: snapshot.flowComplete === true || this.caseAutomationTool88Activity?.flowComplete === true || eventName === 'case-buddy-flow-complete' ? 'flow-complete' : 'raw-data-imported',
          message: snapshot.flowComplete === true || this.caseAutomationTool88Activity?.flowComplete === true || eventName === 'case-buddy-flow-complete'
            ? `Tool 88 completed the raw-data flow and appended ${rowCount} raw row${rowCount === 1 ? '' : 's'}.`
            : `Tool 88 appended ${rowCount} raw row${rowCount === 1 ? '' : 's'} to Raw Data.`,
          detail: snapshot.detail && !/not-mounted|wayfinder\s+bridge|output\s+insertion\s+was\s+not\s+verified/i.test(snapshot.detail)
            ? snapshot.detail
            : `Tool 88 raw-data automation appended ${rowCount} raw row${rowCount === 1 ? '' : 's'} to Raw Data.`,
          rowsAppended: rowCount,
          rawRows: rowCount,
          flowComplete: snapshot.flowComplete === true || this.caseAutomationTool88Activity?.flowComplete === true || eventName === 'case-buddy-flow-complete'
        };
      }
      if (this.caseWorkflowRawDataTool88Running || this.caseWorkflowTool88RawRunning) {
        this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88('Tool 88 is resolving raw-data coverage for this workflow step.');
      }
      const flow = snapshot.flowCompletion || {};
      const selected = snapshot.selectedEntry || {};
      const rows = Array.isArray(snapshot.outputRows) ? snapshot.outputRows : [];
      const visualRows = this.caseWorkflowRawDataTool88ExtractVisualRowsFromPayload(snapshot);
      if (visualRows.length) this.caseWorkflowRawDataTool88VisualRows = visualRows;
      const now = new Date().toISOString();
      const message = normalizeCell(snapshot.message || snapshot.guardrail?.message || snapshot.status || eventName);
      const logEntry = message ? {
        id: `workflow-tool88-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        message
      } : null;
      const existingLog = Array.isArray(this.caseAutomationTool88Activity?.log) ? this.caseAutomationTool88Activity.log : [];
      this.caseAutomationTool88Activity = {
        ...(this.caseAutomationTool88Activity || {}),
        active: this.caseWorkflowRawDataTool88Running || snapshot.isProcessing === true || eventName === 'case-buddy-progress',
        startedAtUtc: this.caseAutomationTool88Activity?.startedAtUtc || now,
        updatedAtUtc: now,
        status: normalizeCell(snapshot.status || eventName),
        title: 'Tool 88 raw-data solver',
        message,
        detail: message,
        eventName,
        integrationAction: normalizeCell(snapshot.integrationAction),
        stepCount: Math.max(Number(this.caseAutomationTool88Activity?.stepCount) || 0, Number(snapshot.runState?.stepCount) || 0),
        researchedCount: Math.max(Number(this.caseAutomationTool88Activity?.researchedCount) || 0, Number(snapshot.runState?.researchedEntryKeys?.length) || 0),
        entryCount: Number(flow.totalEntries) || Number(snapshot.runState?.totalEntries) || Number(this.caseAutomationTool88Activity?.entryCount) || 0,
        endingCount: Number(flow.endingCount) || Number(this.caseAutomationTool88Activity?.endingCount) || 0,
        completeCount: Number(flow.completeCount) || Number(this.caseAutomationTool88Activity?.completeCount) || 0,
        unresolvedCount: Number.isFinite(Number(flow.incompleteCount)) ? Number(flow.incompleteCount) : this.caseAutomationTool88Activity?.unresolvedCount,
        flowComplete: snapshot.flowComplete === true || flow.done === true || this.caseAutomationTool88Activity?.flowComplete === true,
        flowCompleteConfirmations: Number(snapshot.runState?.flowCompleteConfirmations) || Number(this.caseAutomationTool88Activity?.flowCompleteConfirmations) || 0,
        rawRows: Math.max(Number(this.caseAutomationTool88Activity?.rawRows) || 0, rows.filter(row => rowHasMeaningfulData(row)).length),
        targetLabel: normalizeCell(selected.pathLabel || this.caseAutomationTool88Activity?.targetLabel),
        targetHash: normalizeCell(selected.txHash || selected.hash || this.caseAutomationTool88Activity?.targetHash),
        targetAddress: normalizeCell(snapshot.selectedTargetAddress || this.caseAutomationTool88Activity?.targetAddress),
        guardrailMessage: normalizeCell(snapshot.guardrail?.message || snapshot.guardrail?.reason || ''),
        unresolvedBridgeAccepted: Boolean(snapshot.workflowRawDataUnsolvedBridgeAccepted),
        unresolvedBridgeNote: normalizeCell(snapshot.workflowRawDataUnsolvedBridgeNote || ''),
        log: logEntry ? [logEntry, ...existingLog].slice(0, 5) : existingLog.slice(0, 5)
      };
      this.caseAutomationTool88Progress = {
        ...(this.caseAutomationTool88Progress || {}),
        active: this.caseWorkflowRawDataTool88Running,
        title: 'Tool 88 raw-data solver',
        detail: message,
        status: normalizeCell(snapshot.status || eventName),
        action: normalizeCell(snapshot.integrationAction),
        stepCount: Number(this.caseAutomationTool88Activity.stepCount) || 0,
        researchedCount: Number(this.caseAutomationTool88Activity.researchedCount) || 0,
        outputRows: Math.max(Number(this.caseAutomationTool88Progress?.outputRows) || 0, rows.filter(row => rowHasMeaningfulData(row)).length),
        totalEntries: Number(flow.totalEntries) || Number(this.caseAutomationTool88Progress?.totalEntries) || 0,
        endingCount: Number(flow.endingCount) || Number(this.caseAutomationTool88Progress?.endingCount) || 0,
        completeEndings: Number(flow.completeCount) || Number(this.caseAutomationTool88Progress?.completeEndings) || 0,
        openEndings: Number(flow.incompleteCount) || Number(this.caseAutomationTool88Progress?.openEndings) || 0,
        selectedLabel: normalizeCell(selected.pathLabel || ''),
        selectedHash: normalizeCell(selected.txHash || selected.hash || ''),
        selectedTarget: normalizeCell(snapshot.selectedTargetAddress || ''),
        updatedAtUtc: now,
        events: [
          { id: `workflow-tool88-progress-${Date.now()}`, label: message || normalizeCell(snapshot.status || eventName) },
          ...(Array.isArray(this.caseAutomationTool88Progress?.events) ? this.caseAutomationTool88Progress.events : [])
        ].filter(item => item.label).slice(0, 8)
      };
      this.caseWorkflowRawDataTool88LastSnapshot = snapshot;
      this.recordCaseWorkflowRawDataTool88RunEvent?.(eventName, snapshot);
      const terminalEvent = /guardrail|flow-complete/i.test(eventName);
      if (terminalEvent || this.caseWorkflowRawDataTool88Running !== true) {
        this.captureCaseWorkflowRawDataTool88LastRunRecord?.(`event:${eventName}`, { snapshot });
      }
    },

    handleCaseWorkflowRawDataTool88Status(payload) {
      this.updateCaseAutomationTool88ActivityFromSnapshot('case-buddy-status', payload);
    },

    handleCaseWorkflowRawDataTool88Progress(payload) {
      this.updateCaseAutomationTool88ActivityFromSnapshot('case-buddy-progress', payload);
    },

    handleCaseWorkflowRawDataTool88Result(payload) {
      this.updateCaseAutomationTool88ActivityFromSnapshot('case-buddy-result', payload);
    },

    handleCaseWorkflowRawDataTool88Guardrail(payload) {
      this.updateCaseAutomationTool88ActivityFromSnapshot('case-buddy-guardrail', payload);
    },

    handleCaseWorkflowRawDataTool88FlowComplete(payload) {
      this.updateCaseAutomationTool88ActivityFromSnapshot('case-buddy-flow-complete', payload);
    },


    caseWorkflowRawDataTool88SummarizeRowsForDebug(rows = [], limit = 28) {
      return this.caseWorkflowRawDataTool88RowsFromCompactVisualRows(rows)
        .slice(0, limit)
        .map((row, index) => this.caseWorkflowRawDataTool88DebugRowSummary(row, index));
    },

    buildCaseWorkflowRawDataTool88DebugSnapshot() {
      const tool = this.$refs.caseWorkflowRawDataTool88 || null;
      const mainRows = this.getCaseWorkflowRawDataTool88InputRows?.({ recordSelection: false }) || [];
      const rawRows = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('raw') : [];
      const coverage = this.buildCaseBuddyMainRawDataCoverage(
        typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('main') : mainRows,
        Array.isArray(rawRows) ? rawRows : []
      );
      const lastSnapshot = this.caseWorkflowRawDataTool88LastSnapshot || {};
      let liveToolSnapshot = null;
      try {
        if (tool && typeof tool.buildCaseBuddyHopSolverSnapshot === 'function') {
          liveToolSnapshot = tool.buildCaseBuddyHopSolverSnapshot({
            includeRows: false,
            status: 'workflow-raw-data-debug-snapshot',
            integrationAction: 'copy-debug',
            message: 'Debug snapshot copied from Case Buddy workflow raw-data Tool 88 host.'
          });
        }
      } catch (error) {
        liveToolSnapshot = { error: error?.message || String(error || 'Could not build live Tool 88 snapshot.') };
      }
      const pickedLast = this.pickCaseWorkflowRawDataTool88Rows(lastSnapshot, tool);
      const pickedLive = liveToolSnapshot && !liveToolSnapshot.error ? this.pickCaseWorkflowRawDataTool88Rows(liveToolSnapshot, tool) : null;
      return {
        kind: 'case-buddy-workflow-tool88-raw-data-debug',
        copiedAtUtc: new Date().toISOString(),
        caseDataRevision: this.caseSupportState?.caseDataRevision || 0,
        preferences: {
          skipResolvableReviews: Boolean(this.caseAutomation?.skipResolvableReview),
          preferTool88RawData: Boolean(this.caseAutomationPreferHopSolverRawData),
          rawDataSourceMode: this.caseAutomationRawDataSourceMode || '',
          showcaseOverlayEnabled: Boolean(this.caseAutomationShowcaseOverlayEnabled)
        },
        automation: {
          active: Boolean(this.caseAutomation?.active),
          busy: Boolean(this.caseAutomation?.busy),
          paused: Boolean(this.caseAutomation?.paused),
          status: this.caseAutomation?.status || '',
          detail: this.caseAutomation?.detail || '',
          lastPlan: this.caseAutomation?.lastPlan || null,
          manualPause: this.caseAutomation?.manualPause || null,
          log: (Array.isArray(this.caseAutomation?.log) ? this.caseAutomation.log : []).slice(0, 24)
        },
        rawCoverage: {
          totalRows: coverage.coverageRows?.length || 0,
          missingRows: (coverage.coverageRows || []).filter(row => row.status !== 'covered').slice(0, 24),
          summary: coverage.summary || ''
        },
        autoContinue: {
          guardActive: this.caseWorkflowRawDataTool88AutoContinueGuardActive(),
          guardUntilMs: Number(this.caseWorkflowRawDataTool88AutoContinueGuardUntilMs) || 0,
          timerPending: Boolean(this.caseWorkflowRawDataTool88AutoContinueTimer),
          inFlight: Boolean(this.caseWorkflowRawDataTool88AutoContinueInFlight),
          lastAutoContinueAtUtc: this.caseWorkflowRawDataTool88LastAutoContinueAtUtc || '',
          reason: this.caseWorkflowRawDataTool88AutoContinueReason || '',
          recentUserStop: this.caseWorkflowRawDataTool88UserStopIsRecent(),
          stopToken: Number(this.caseWorkflowRawDataTool88StopToken) || 0,
          userStopRequestedAtMs: Number(this.caseWorkflowRawDataTool88UserStopRequestedAtMs) || 0
        },
        persistentLastRun: this.caseWorkflowTool88SafeDebugClone(this.caseWorkflowRawDataTool88LastRunRecord || null),
        runEvents: this.caseWorkflowTool88SafeDebugClone(this.caseWorkflowRawDataTool88RunEvents || []),
        tool88Activity: {
          running: Boolean(this.caseWorkflowRawDataTool88Running),
          hostMounted: Boolean(this.caseWorkflowRawDataTool88HostMounted),
          lastError: this.caseWorkflowRawDataTool88LastError || '',
          activity: this.caseAutomationTool88Activity || null,
          progress: this.caseAutomationTool88Progress || null,
          lastSnapshotStatus: lastSnapshot?.status || '',
          lastSnapshotMessage: lastSnapshot?.message || '',
          lastSnapshotAction: lastSnapshot?.integrationAction || '',
          lastSnapshotSelectedTarget: lastSnapshot?.selectedTargetAddress || '',
          lastSnapshotSelectedEntry: lastSnapshot?.selectedEntry || null,
          lastSnapshotFlowCompletion: lastSnapshot?.flowCompletion || null,
          lastSnapshotGuardrail: lastSnapshot?.guardrail || null,
          lastSnapshotWarnings: lastSnapshot?.warnings || [],
          unresolvedBridgeAccepted: Boolean(lastSnapshot?.workflowRawDataUnsolvedBridgeAccepted),
          unresolvedBridgeRowIndex: Number.isFinite(Number(lastSnapshot?.workflowRawDataUnsolvedBridgeRowIndex)) ? Number(lastSnapshot.workflowRawDataUnsolvedBridgeRowIndex) : null,
          unresolvedBridgeNote: lastSnapshot?.workflowRawDataUnsolvedBridgeNote || '',
          lastSnapshotRowCounts: {
            outputRows: Array.isArray(lastSnapshot?.outputRows) ? lastSnapshot.outputRows.length : 0,
            compactVisualRows: Array.isArray(lastSnapshot?.caseBuddyWorkflowVisualRows) ? lastSnapshot.caseBuddyWorkflowVisualRows.length : 0,
            traceExitRows: Array.isArray(lastSnapshot?.traceExitRows) ? lastSnapshot.traceExitRows.length : 0,
            lastRows: Array.isArray(this.caseWorkflowRawDataTool88LastRows) ? this.caseWorkflowRawDataTool88LastRows.length : 0,
            visualRows: Array.isArray(this.caseWorkflowRawDataTool88VisualRows) ? this.caseWorkflowRawDataTool88VisualRows.length : 0
          },
          pickedLastRows: {
            label: pickedLast.label,
            stats: pickedLast.stats,
            rows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(pickedLast.rows, 32)
          },
          pickedLiveRows: pickedLive ? {
            label: pickedLive.label,
            stats: pickedLive.stats,
            rows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(pickedLive.rows, 32)
          } : null,
          visualPortal: {
            available: Boolean(this.caseAutomationShowcaseTool88PortalState.available),
            focusLabel: this.caseAutomationShowcaseTool88PortalState.focusLabel || '',
            summary: this.caseAutomationShowcaseTool88PortalState.summary || '',
            nodeCount: this.caseAutomationShowcaseTool88PortalState.nodes?.length || 0,
            edgeCount: this.caseAutomationShowcaseTool88PortalState.edges?.length || 0,
            caseRootCount: Number(this.caseAutomationShowcaseTool88PortalState.caseRootCount || 0),
            currentPassRootCount: Number(this.caseAutomationShowcaseTool88PortalState.currentPassRootCount || 0),
            completedPassRootCount: Number(this.caseAutomationShowcaseTool88PortalState.completedPassRootCount || 0),
            deferredRootCount: Number(this.caseAutomationShowcaseTool88PortalState.deferredRootCount || 0),
            duplicateHashRootCount: Number(this.caseAutomationShowcaseTool88PortalState.duplicateHashRootCount || 0),
            duplicateHashGroupCount: Number(this.caseAutomationShowcaseTool88PortalState.duplicateHashGroupCount || 0),
            transactionEntryCount: Number(this.caseAutomationShowcaseTool88PortalState.transactionEntryCount || 0),
            rootInstances: this.caseWorkflowTool88SafeDebugClone(this.caseAutomationShowcaseTool88PortalState.rootInstances || []),
            smoothCameraEnabled: CASE_WORKFLOW_TOOL88_PORTAL_SMOOTH_CAMERA_ENABLED,
            camera: {
              initialized: Boolean(this.caseWorkflowRawDataTool88PortalCamera?.initialized),
              currentX: Number(this.caseWorkflowRawDataTool88PortalCamera?.currentX || 0),
              currentY: Number(this.caseWorkflowRawDataTool88PortalCamera?.currentY || 0),
              startX: Number(this.caseWorkflowRawDataTool88PortalCamera?.startX || 0),
              startY: Number(this.caseWorkflowRawDataTool88PortalCamera?.startY || 0),
              targetX: Number(this.caseWorkflowRawDataTool88PortalCamera?.targetX || 0),
              targetY: Number(this.caseWorkflowRawDataTool88PortalCamera?.targetY || 0),
              transitionDurationMs: Number(this.caseWorkflowRawDataTool88PortalCamera?.transitionDurationMs || 0),
              transitionProgress: Number(this.caseWorkflowRawDataTool88PortalCamera?.transitionProgress || 0),
              focusKey: normalizeCell(this.caseWorkflowRawDataTool88PortalCamera?.focusKey || '')
            }
          }
        },
        hiddenTool88: tool ? {
          mounted: true,
          isProcessing: Boolean(tool.isProcessing),
          selectedEntryKey: tool.selectedEntryKey || '',
          selectedTargetAddress: tool.selectedTargetAddress || '',
          parsedEntryCount: Array.isArray(tool.parsedEntries) ? tool.parsedEntries.length : null,
          outputSnapshotRows: Array.isArray(tool.outputSnapshotRows) ? tool.outputSnapshotRows.length : null,
          inputSnapshotRows: Array.isArray(tool.inputSnapshotRows) ? tool.inputSnapshotRows.length : null,
          flowCompletionResult: tool.flowCompletionResult ? {
            done: tool.flowCompletionResult.done === true,
            totalEntries: Number(tool.flowCompletionResult.totalEntries) || 0,
            endingCount: Number(tool.flowCompletionResult.endingCount) || 0,
            completeCount: Number(tool.flowCompletionResult.completeCount) || 0,
            incompleteCount: Number(tool.flowCompletionResult.incompleteCount) || 0,
            depthSkippedCount: Number(tool.flowCompletionResult.depthSkippedCount) || 0,
            summary: this.caseWorkflowTool88DebugClipText(tool.flowCompletionResult.summary || '', 1800),
            nextIncomplete: Array.isArray(tool.flowCompletionResult.incomplete) && tool.flowCompletionResult.incomplete.length ? this.caseWorkflowTool88CompactDebugValue(tool.flowCompletionResult.incomplete[0], { maxDepth: 4, maxArrayItems: 8 }, 0, new WeakSet(), 'nextIncomplete') : null
          } : null,
          countLookupResult: tool.countLookupResult ? {
            transactionCount: tool.countLookupResult.transactionCount ?? tool.countLookupResult.transaction_count ?? null,
            source: normalizeCell(tool.countLookupResult.source || tool.countLookupResult.provider || ''),
            classification: normalizeCell(tool.countLookupResult.classification || ''),
            summary: this.caseWorkflowTool88DebugClipText(tool.countLookupResult.summary || tool.countLookupResult.message || '', 1200)
          } : null,
          runState: typeof tool.buildCaseBuddyAutomationRunStateSnapshot === 'function'
            ? tool.buildCaseBuddyAutomationRunStateSnapshot(tool.caseBuddyIntegrationRunState)
            : this.caseWorkflowTool88CompactRunStateForDebug(tool.caseBuddyIntegrationRunState || null),
          traceResult: tool.traceResult ? {
            status: normalizeCell(tool.traceResult.status || ''),
            exitRowCount: Array.isArray(tool.traceResult.exitRows) ? tool.traceResult.exitRows.length : 0,
            outputMutationVerified: tool.traceResult.outputMutationVerified === true,
            preventPathEnding: tool.traceResult.preventPathEnding === true,
            verification: this.caseWorkflowTool88CompactDebugValue(tool.traceResult.outputMutationVerification || null, { maxDepth: 4, maxArrayItems: 12 }, 0, new WeakSet(), 'traceVerification')
          } : null,
          simpleResultReadyForContinue: tool.simpleResultReadyForContinue === true,
          simpleRecommendationApplied: tool.simpleRecommendationApplied === true,
          simpleViewMessage: tool.simpleViewMessage || '',
          statusMessage: tool.statusMessage || '',
          reportTail: normalizeCell(tool.reportText || '').slice(-5000),
          liveSnapshot: liveToolSnapshot ? {
            status: liveToolSnapshot.status || '',
            message: liveToolSnapshot.message || liveToolSnapshot.error || '',
            integrationAction: liveToolSnapshot.integrationAction || '',
            selectedTargetAddress: liveToolSnapshot.selectedTargetAddress || '',
            selectedEntry: liveToolSnapshot.selectedEntry || null,
            flowComplete: Boolean(liveToolSnapshot.flowComplete),
            flowCompletion: liveToolSnapshot.flowCompletion || null,
            guardrail: liveToolSnapshot.guardrail || null,
            stallDiagnostics: this.caseWorkflowTool88CompactDebugValue(liveToolSnapshot.stallDiagnostics || null, { maxDepth: 5, maxArrayItems: 20 }, 0, new WeakSet(), 'stallDiagnostics'),
            runState: this.caseWorkflowTool88CompactRunStateForDebug(liveToolSnapshot.runState || null),
            outputRows: Array.isArray(liveToolSnapshot.outputRows) ? liveToolSnapshot.outputRows.length : 0,
            compactVisualRows: Array.isArray(liveToolSnapshot.caseBuddyWorkflowVisualRows) ? liveToolSnapshot.caseBuddyWorkflowVisualRows.length : 0,
            traceExitRows: Array.isArray(liveToolSnapshot.traceExitRows) ? liveToolSnapshot.traceExitRows.length : 0
          } : null
        } : { mounted: false },
        mainRows: {
          stats: this.caseWorkflowRawDataTool88RowStats(mainRows),
          sample: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(mainRows, 20)
        },
        rawRows: {
          stats: this.caseWorkflowRawDataTool88RowStats(rawRows),
          sample: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(rawRows, 20)
        }
      };
    },

    async copyCaseWorkflowRawDataTool88DebugSnapshot() {
      const snapshot = this.buildCaseWorkflowRawDataTool88DebugSnapshot();
      const text = JSON.stringify(snapshot, null, 2);
      let copied = false;
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }
      if (!copied && typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', 'readonly');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        } finally {
          document.body.removeChild(textarea);
        }
      }
      this.caseWorkflowRawDataTool88LastDebugCopiedAtUtc = snapshot.copiedAtUtc;
      const rowCount = snapshot.tool88Activity?.pickedLiveRows?.stats?.rowCount || snapshot.tool88Activity?.pickedLastRows?.stats?.rowCount || 0;
      const payloadKb = Math.max(1, Math.round(text.length / 1024));
      const message = copied
        ? `Copied compact workflow Tool 88 debug log (${rowCount} candidate row${rowCount === 1 ? '' : 's'}, about ${payloadKb} KB).`
        : 'Could not copy workflow Tool 88 debug log automatically.';
      if (typeof this.showCaseToast === 'function') this.showCaseToast(message);
      else this.showWorkflowUiToast?.(message);
      return copied;
    },

    caseWorkflowRawDataTool88Guardrails(options = {}) {
      const speed = Math.max(0.5, Math.min(3, Number(this.caseAutomation?.speedMultiplier) || 1));
      return {
        maxElapsedMs: 15 * 60 * 1000,
        maxEntriesResearched: 160,
        maxLoopSteps: 260,
        maxConsecutiveSameAction: 3,
        maxNoProgressSteps: 4,
        maxStateVisits: 3,
        recentStateWindow: 18,
        maxAutoLowValue: 50,
        flowCompleteSettleMs: 650,
        flowCompleteConfirmations: 2,
        pauseOnManualReview: true,
        pauseOnTargetAmbiguity: true,
        pauseOnUnverifiedOutput: false,
        speedMultiplier: speed,
        delayMs: Math.max(80, Math.round(220 / speed)),
        ...(options.guardrails || {})
      };
    },

    caseWorkflowRawDataTool88SnapshotText(snapshot = {}, tool = null) {
      const parts = [];
      const push = (value) => {
        const text = normalizeCell(value);
        if (text) parts.push(text);
      };
      const selected = snapshot?.selectedEntry || tool?.selectedEntry || {};
      const currentDecision = snapshot?.decision || snapshot?.automaticDecision || tool?.decision || tool?.automaticDecision || {};
      const transactionSignal = snapshot?.transactionSignal || snapshot?.transactionSignalResult || tool?.transactionSignalResult || {};
      const bridgeResult = snapshot?.result?.bridge || snapshot?.bridge || tool?.bridgeApplyResult || tool?.bridgeResult || {};

      // Bridge fallback evidence must describe the currently selected ending.
      // Do not include Tool 88's full report or Case Buddy's progress history:
      // those contain legitimate bridge decisions from earlier branches in the
      // same root and can otherwise contaminate a later ordinary BTC trace.
      push(snapshot?.status);
      push(snapshot?.message);
      push(snapshot?.detail);
      push(snapshot?.guardrail?.reason);
      push(snapshot?.guardrail?.message);
      push(snapshot?.simpleViewMessage);
      push(snapshot?.selectedTargetAddress);
      push(selected?.attribution || selected?.toAttribution || selected?.toAttributionLabel || selected?.label);
      push(currentDecision?.action);
      push(currentDecision?.title);
      push(currentDecision?.summary);
      (Array.isArray(currentDecision?.reasons) ? currentDecision.reasons : []).forEach(push);
      push(transactionSignal?.classification);
      push(transactionSignal?.summary);
      push(transactionSignal?.recommendation);
      push(transactionSignal?.evidence);
      push(bridgeResult?.status);
      push(bridgeResult?.message);
      push(bridgeResult?.summary);
      push(tool?.simpleViewMessage);
      push(tool?.statusMessage);
      return parts.join('\n');
    },

    caseWorkflowRawDataTool88ExplicitBridgeEvidence(snapshot = {}, tool = null, rows = []) {
      const selected = snapshot?.selectedEntry || tool?.selectedEntry || {};
      const normalizeAction = (value) => normalizeCell(value).toLowerCase().replace(/[_\s]+/g, '-');
      const bridgeActions = new Set(['bridge', 'manual-bridge', 'treat-as-bridge', 'bridge-review']);
      const currentDecisionAction = [
        snapshot?.decision?.action,
        snapshot?.automaticDecision?.action,
        tool?.decision?.action,
        tool?.automaticDecision?.action,
        tool?.selectedAction,
        selected?.recommendedAction,
        selected?.action
      ].map(normalizeAction).find(Boolean) || '';

      if (currentDecisionAction) {
        if (bridgeActions.has(currentDecisionAction)) {
          return { found: true, source: 'current-decision-action', value: currentDecisionAction };
        }
        // A current trace/path-ending/review decision outranks stale bridge text
        // produced by another branch in the same root flow.
        return { found: false, source: 'current-decision-non-bridge', value: currentDecisionAction };
      }

      const structuredActions = [
        snapshot?.recommendedAction,
        snapshot?.recommendedActionKey,
        snapshot?.guardrail?.action,
        snapshot?.flowCompletion?.action,
        tool?.recommendedAction,
        tool?.recommendedActionKey,
        selected?.recommendedAction,
        selected?.action
      ].map(normalizeAction).filter(Boolean);
      if (structuredActions.some(action => bridgeActions.has(action))) {
        return { found: true, source: 'structured-action', value: structuredActions.find(action => bridgeActions.has(action)) || 'bridge' };
      }

      const list = Array.isArray(rows) ? rows : [];
      const selectedRowIndex = this.caseWorkflowRawDataTool88FindBridgeFallbackRowIndex(list, snapshot, tool);
      const selectedRows = [];
      if (selectedRowIndex >= 0 && Array.isArray(list[selectedRowIndex])) {
        selectedRows.push(list[selectedRowIndex]);
        for (let index = selectedRowIndex + 1; index < list.length; index += 1) {
          const row = list[index];
          if (!Array.isArray(row)) break;
          if (normalizeCell(row?.[CASE_MANUAL_START_COL.TX_HASH])) break;
          selectedRows.push(row);
        }
      }
      const rowText = selectedRows
        .map(row => [
          row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
          row?.[CASE_MANUAL_START_COL.ATTRIBUTION],
          row?.[CASE_MANUAL_START_COL.CONTINUES_AS],
          row?.[CASE_MANUAL_START_COL.CASE_NOTES]
        ].map(value => normalizeCell(value)).join(' '))
        .join('\n');
      let combined = `${this.caseWorkflowRawDataTool88SnapshotText(snapshot, tool)}\n${rowText}`.toLowerCase();

      // These phrases merely explain that no bridge marker was found or were
      // generated by Case Buddy's old fallback. They are not bridge evidence.
      combined = combined
        .replace(/(?:does\s+not|did\s+not|doesn't|didn't)\s+show\s+(?:an?\s+)?(?:obvious\s+)?swap\s*\/\s*bridge\s+marker[^.]*\.?/gi, ' ')
        .replace(/no\s+(?:obvious\s+|explicit\s+)?(?:swap\s*\/\s*)?bridge\s+(?:marker|signal|evidence)[^.]*\.?/gi, ' ')
        .replace(/without\s+(?:an?\s+)?(?:swap\s*\/\s*)?bridge\s+(?:marker|signal|evidence)[^.]*\.?/gi, ' ')
        .replace(/review\s+attribution\s+and\s+count\s+before\s+tracing\s+through\.?/gi, ' ')
        .replace(/tool\s*88\s+workflow\s+raw-data\s+automation:\s*bridge\s+solver\s+could\s+not\s+complete\s+automatically[^.]*\.?/gi, ' ')
        .replace(/bridge\s*\/\s*swap\s+service\s+bridge\s+could\s+not\s+be\s+solved\s+automatically[^.]*\.?/gi, ' ')
        .replace(/further\s+manual\s+bridge\s+review\s+is\s+required\.?/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      const explicitMatch = combined.match(
        /\b(?:wormhole|stargate|across|orbiter|hop\s+protocol|hopprotocol|li\.fi|thorchain|chainflip|rango|transitswap|transit\s*swap|swft|bridgers|bkbridge)\b|\bbridge[_\s-]?like\b|\bcross[-\s]?chain\b|\bdestination\s+chain\b|\bbridge\s+(?:event|contract|router|service|deposit|withdrawal)\b|\b(?:classified|classification|recommend(?:ed|ation)?|treat(?:ed)?|action)\s*(?:as|=|:)?\s*(?:a\s+)?bridge\b|\bmanual[-\s]?bridge\b/i
      );
      return explicitMatch
        ? { found: true, source: 'selected-ending-text', value: explicitMatch[0] }
        : { found: false, source: '', value: '' };
    },

    caseWorkflowRawDataTool88LooksLikeBridgeStall(snapshot = {}, tool = null, rows = []) {
      const bridgeEvidence = this.caseWorkflowRawDataTool88ExplicitBridgeEvidence(snapshot, tool, rows);
      if (!bridgeEvidence.found) return false;

      const stallText = [
        snapshot?.status,
        snapshot?.message,
        snapshot?.detail,
        snapshot?.guardrail?.reason,
        snapshot?.guardrail?.message,
        snapshot?.simpleViewMessage,
        tool?.simpleViewMessage,
        tool?.statusMessage
      ].map(value => normalizeCell(value)).filter(Boolean).join(' ').toLowerCase();
      const mentionsStall = /same\s+action\s+fingerprint|repeated\s+action|guardrail|paused|manual\s+selection|advanced\s+review|needs\s+manual|could\s+not\s+(?:solve|continue)|did\s+not\s+(?:finish|produce|return)|not\s+mounted|solver\s+opened|no\s+exportable|no[-\s]?output|without\s+(?:an?\s+)?exportable|produce\s+an\s+exportable\s+output\s+row|manual\s+review|max[-\s]?(?:elapsed|steps|entries)/.test(stallText);
      const selectedRowIndex = this.caseWorkflowRawDataTool88FindBridgeFallbackRowIndex(rows, snapshot, tool);
      const hasSelectedOutputRow = selectedRowIndex >= 0 && rowHasMeaningfulData(rows?.[selectedRowIndex]);
      return hasSelectedOutputRow && mentionsStall;
    },

    caseWorkflowRawDataTool88BridgeFallbackNote(snapshot = {}, tool = null) {
      const selected = snapshot?.selectedEntry || {};
      const target = normalizeCell(snapshot?.selectedTargetAddress || tool?.selectedTargetAddress || selected?.toAddress || selected?.targetAddress || '');
      const attribution = normalizeCell(selected?.attribution || selected?.toAttribution || selected?.toAttributionLabel || '');
      const service = attribution || (/transitswap/i.test(this.caseWorkflowRawDataTool88SnapshotText(snapshot, tool)) ? 'TransitSwap' : 'bridge/swap service');
      const targetPhrase = target ? ` at ${target}` : '';
      return `${service} bridge could not be solved automatically${targetPhrase}. Further manual bridge review is required. Path ends.`;
    },

    caseWorkflowRawDataTool88FindBridgeFallbackRowIndex(rows = [], snapshot = {}, tool = null) {
      const list = Array.isArray(rows) ? rows : [];
      const selected = snapshot?.selectedEntry || {};
      const selectedHash = this.caseWorkflowRawToMainHashKey(
        selected?.txHash
          || selected?.hash
          || selected?.transactionHash
          || tool?.selectedEntry?.txHash
          || tool?.selectedEntry?.hash
          || ''
      );
      const selectedTarget = this.caseWorkflowRootAddressKey(
        snapshot?.selectedTargetAddress
          || tool?.selectedTargetAddress
          || selected?.toAddress
          || selected?.targetAddress
          || ''
      );
      const selectedPathLabel = this.caseWorkflowRootIdentityText(
        selected?.pathLabel
          || selected?.label
          || snapshot?.selectedPathLabel
          || tool?.selectedEntry?.pathLabel
          || ''
      );
      const selectedPathKey = this.caseWorkflowRootIdentityKey(selectedPathLabel);
      const knownRootLabels = list
        .map(row => this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]))
        .filter(Boolean);
      const bridgeLike = (row = []) => /\bbridge\b|bridg(?:e|ed|ing)|transitswap|transit\s*swap|swft|bridge\s*like|bridge\/swap|complex\s+transaction|non-transfer\s+logs|receipt\s+has\s+\d+\s+logs/i.test([
        row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row?.[CASE_MANUAL_START_COL.ATTRIBUTION],
        row?.[CASE_MANUAL_START_COL.CASE_NOTES]
      ].map(value => normalizeCell(value)).join(' '));
      const descriptor = (row = [], index = -1) => {
        const pathLabel = this.caseWorkflowRootIdentityText(row?.[CASE_MANUAL_START_COL.PATH_LABEL]);
        const pathKey = this.caseWorkflowRootIdentityKey(pathLabel);
        const toKey = this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
        return {
          index,
          row,
          hashKey: this.caseWorkflowRawToMainHashKey(row?.[CASE_MANUAL_START_COL.TX_HASH]),
          pathLabel,
          pathKey,
          toKey,
          bridge: bridgeLike(row)
        };
      };
      const candidates = list.map(descriptor);
      const preferBridge = (items = []) => {
        const bridge = items.find(item => item.bridge);
        return bridge || items[0] || null;
      };
      const sameHash = selectedHash
        ? candidates.filter(item => item.hashKey === selectedHash)
        : [];

      // A duplicate transaction hash can represent several independent BTC roots.
      // When Tool 88 supplied a selected Column D target or path label, require that
      // identity before annotating a bridge fallback. Never let root 5's bridge row
      // stand in for root 6 merely because their transaction hashes are identical.
      if (selectedTarget && sameHash.length) {
        const exactTarget = sameHash.filter(item => item.toKey && item.toKey === selectedTarget);
        const chosen = preferBridge(exactTarget);
        if (chosen) return chosen.index;
      }
      if (selectedPathKey && sameHash.length && !selectedTarget) {
        const exactPath = sameHash.filter(item => item.pathKey === selectedPathKey);
        const exactChoice = preferBridge(exactPath);
        if (exactChoice) return exactChoice.index;
        const pathFamily = sameHash.filter(item => this.caseWorkflowPathLabelBelongsToRoot(
          item.pathLabel,
          selectedPathLabel,
          knownRootLabels
        ));
        const familyChoice = preferBridge(pathFamily);
        if (familyChoice) return familyChoice.index;
      }
      if (sameHash.length && !selectedTarget && !selectedPathKey) {
        const chosen = preferBridge(sameHash);
        if (chosen) return chosen.index;
      }

      if (selectedTarget) {
        const byTarget = preferBridge(candidates.filter(item => item.toKey && item.toKey === selectedTarget));
        if (byTarget) return byTarget.index;
      }
      if (selectedPathKey && !selectedTarget) {
        const byPath = preferBridge(candidates.filter(item => item.pathKey === selectedPathKey));
        if (byPath) return byPath.index;
      }

      // With an explicit root identity, failing closed is safer than annotating a
      // different same-hash root. The caller can pause for review without corrupting
      // the other root instance.
      if (selectedHash || selectedTarget || selectedPathKey) return -1;
      for (let index = list.length - 1; index >= 0; index -= 1) {
        if (bridgeLike(list[index])) return index;
      }
      return list.length ? list.length - 1 : -1;
    },

    caseWorkflowRawDataTool88AnnotateUnsolvedBridgeRows(rows = [], snapshot = {}, tool = null) {
      const normalized = this.caseWorkflowNormalizeAttributionMatrix(Array.isArray(rows) ? rows : []);
    //  const bridgeEvidence = this.caseWorkflowRawDataTool88ExplicitBridgeEvidence(snapshot, tool, normalized);
      const bridgeFailureText = this.caseWorkflowRawDataTool88SnapshotText(snapshot, tool).toLowerCase();
      const bridgeNoOutput = /bridge recommendation did not produce|bridge solver[^.]*did not produce|did not produce an exportable(?: destination-chain receive)? output row|did not produce an exportable output row|no exportable destination-chain receive row|no bridge candidate was safe|manual bridge review|required|guardrail-paused|paused at|raw-data-graph-invalid|raw graph/i.test(bridgeFailureText);
      let rowIndex = this.caseWorkflowRawDataTool88FindBridgeFallbackRowIndex(normalized, snapshot, tool);
      if (rowIndex < 0 && bridgeNoOutput) {
        const bridgeLikeRow = (row = []) => /bridge|transitswap|transit\s*swap|swft|destination\s+chain|cross[-\s]?chain|manual\s+bridge\s+review/i.test([
          row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
          row?.[CASE_MANUAL_START_COL.ATTRIBUTION],
          row?.[CASE_MANUAL_START_COL.CASE_NOTES],
          row?.[CASE_MANUAL_START_COL.CONTINUES_AS],
        ].map(value => normalizeCell(value)).join(' '));
        for (let index = normalized.length - 1; index >= 0; index -= 1) {
          if (rowHasMeaningfulData(normalized[index]) && bridgeLikeRow(normalized[index])) {
            rowIndex = index;
            break;
          }
        }
      }
      if (rowIndex < 0) return { rows: normalized, applied: false, rowIndex: -1, note: '' };
      const bridgeStall = this.caseWorkflowRawDataTool88LooksLikeBridgeStall(snapshot, tool, normalized);
      if (!bridgeStall && !bridgeNoOutput) {
        return { rows: normalized, applied: false, rowIndex: -1, note: '' };
      }
      const note = this.caseWorkflowRawDataTool88BridgeFallbackNote(snapshot, tool);
      const nextRows = normalized.map((row, index) => {
        if (index !== rowIndex) return row;
        const next = row.slice();
        next[CASE_MANUAL_START_COL.CONTINUES_AS] = 'Path ends.';
        next[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseManualAppendUniqueLine(next[CASE_MANUAL_START_COL.SYSTEM_NOTES], note);
        const text = this.caseWorkflowRawDataTool88SnapshotText(snapshot, tool);
        if (!normalizeCell(next[CASE_MANUAL_START_COL.ATTRIBUTION]) && /transitswap/i.test(text)) {
          next[CASE_MANUAL_START_COL.ATTRIBUTION] = 'TransitSwap';
        }
        next[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseManualAppendUniqueLine(
          next[CASE_MANUAL_START_COL.CASE_NOTES],
          'Tool 88 workflow raw-data automation: bridge solver could not complete automatically; this row was treated as an unsolved bridge path ending for raw-data coverage.'
        );
        return next;
      });
      return { rows: nextRows, applied: true, rowIndex, note };
    },

    caseWorkflowRawDataTool88CompletionForBridgeFallback(snapshot = {}, annotation = {}, appendResult = {}) {
      const base = snapshot?.flowCompletion || {};
      const coverage = appendResult?.coverage || this.getCaseWorkflowRawDataCoverageSummary();
      const totalEntries = Number(coverage?.totalRows || base.totalEntries || annotation?.rows?.length || appendResult.appendedCount || 0) || 0;
      const incompleteCount = Math.max(0, Number(coverage?.missingCount || 0) || 0);
      const completeCount = Math.max(0, totalEntries - incompleteCount);
      const endingCount = Math.max(1, Number(base.endingCount || 0) || (annotation?.applied ? 1 : 0));
      const done = totalEntries > 0 && incompleteCount === 0;
      return {
        ...base,
        done,
        totalEntries,
        endingCount,
        completeCount,
        incompleteCount,
        depthSkippedCount: Number(base.depthSkippedCount || 0) || 0,
        summary: done
          ? 'All distinct starting roots now have raw-data coverage. Tool 88 stopped at an unsolved bridge and marked that bridge ending for manual review.'
          : `Tool 88 marked an unsolved bridge ending for manual review, but ${incompleteCount} distinct starting root${incompleteCount === 1 ? '' : 's'} still need raw data.`
      };
    },

    caseWorkflowRawDataTool88StripPathEndsSentence(value = '') {
      return normalizeCell(value)
        .replace(/(?:^|\n)\s*Path\s+ends\.?\s*(?=\n|$)/giu, '\n')
        .replace(/\bPath\s+ends\.?\s*/giu, '')
        .replace(/\n{3,}/gu, '\n\n')
        .trim();
    },

    caseWorkflowRawDataTool88CollapseExactDuplicateRows(rows = []) {
      const matrix = this.caseWorkflowNormalizeAttributionMatrix(Array.isArray(rows) ? rows : []);
      const seen = new Set();
      const nextRows = [];
      const removed = [];
      matrix.forEach((row, rowIndex) => {
        if (!Array.isArray(row) || !rowHasMeaningfulData(row) || this.caseManualIsHeaderRow?.(row)) {
          nextRows.push(Array.isArray(row) ? row.slice() : row);
          return;
        }
        const key = row
          .slice(0, Math.max(CASE_MANUAL_START_COLUMN_COUNT, row.length))
          .map(value => normalizeCell(value).trim())
          .join('\u001f');
        if (!key || !seen.has(key)) {
          if (key) seen.add(key);
          nextRows.push(row.slice());
          return;
        }
        removed.push({
          rowIndex,
          spreadsheetRowNumber: rowIndex + 1,
          pathLabel: normalizeCell(row?.[CASE_MANUAL_START_COL.PATH_LABEL]),
          hash: normalizeCell(row?.[CASE_MANUAL_START_COL.TX_HASH]),
          fromHash: normalizeCell(row?.[CASE_MANUAL_START_COL.FROM_HASH]),
          toAddress: normalizeCell(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
        });
      });
      return {
        rows: nextRows,
        removedCount: removed.length,
        removed: removed.slice(0, 24),
      };
    },

    caseWorkflowRawDataTool88CollectGraphEntries(rows = []) {
      const matrix = this.caseWorkflowNormalizeAttributionMatrix(Array.isArray(rows) ? rows : []);
      const entries = [];
      let current = null;
      matrix.forEach((row, rowIndex) => {
        if (!Array.isArray(row) || !rowHasMeaningfulData(row)) return;
        if (this.caseManualIsHeaderRow?.(row)) return;
        const hash = normalizeCell(row?.[CASE_MANUAL_START_COL.TX_HASH]).trim();
        if (hash) {
          current = {
            key: `raw-entry-${rowIndex}-${this.caseWorkflowRawToMainHashKey(hash)}`,
            startRowIndex: rowIndex,
            endRowIndex: rowIndex,
            row,
            rows: [{ row, rowIndex }],
            hash,
            hashKey: this.caseWorkflowRawToMainHashKey(hash),
            pathLabel: normalizeCell(row?.[CASE_MANUAL_START_COL.PATH_LABEL]).trim(),
            pathKey: this.caseWorkflowRootIdentityKey(row?.[CASE_MANUAL_START_COL.PATH_LABEL]),
            fromHash: normalizeCell(row?.[CASE_MANUAL_START_COL.FROM_HASH]).trim(),
            fromHashKey: this.caseWorkflowRawToMainHashKey(row?.[CASE_MANUAL_START_COL.FROM_HASH]),
            fromAddress: normalizeCell(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]).trim(),
            fromAddressKey: this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]),
            fromAddressKeys: new Set(),
            toAddressKeys: new Set(),
          };
          const fromKey = this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
          const toKey = this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
          if (fromKey) current.fromAddressKeys.add(fromKey);
          if (toKey) current.toAddressKeys.add(toKey);
          entries.push(current);
          return;
        }
        if (!current) return;
        current.endRowIndex = rowIndex;
        current.rows.push({ row, rowIndex });
        const fromKey = this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]);
        const toKey = this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]);
        if (fromKey) current.fromAddressKeys.add(fromKey);
        if (toKey) current.toAddressKeys.add(toKey);
      });
      return { rows: matrix, entries };
    },

    caseWorkflowRawDataTool88DirectionFromText(value = '') {
      const text = normalizeCell(value);
      if (!text) return '';
      if (/(?:direction\s*=\s*reverse\b|\btool\s*88\s*:\s*reverse\b|\breverse\s+(?:fifo|lifo|next[_\s-]*chronological|trace|traced|source|funding)\b)/iu.test(text)) return 'reverse';
      if (/(?:direction\s*=\s*forward\b|\btool\s*88\s*:\s*forward\b|\bforward\s+(?:fifo|lifo|next[_\s-]*chronological|trace|traced)\b)/iu.test(text)) return 'forward';
      return '';
    },

    caseWorkflowRawDataTool88EntryDirection(entry = {}) {
      const rows = Array.isArray(entry?.rows) && entry.rows.length
        ? entry.rows.map(item => item?.row).filter(Array.isArray)
        : (Array.isArray(entry?.row) ? [entry.row] : []);
      const text = rows.flatMap(row => [
        row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row?.[CASE_MANUAL_START_COL.ANALYST_NOTES],
        row?.[CASE_MANUAL_START_COL.CASE_NOTES],
      ]).map(value => normalizeCell(value)).filter(Boolean).join('\n');
      return this.caseWorkflowRawDataTool88DirectionFromText(text);
    },

    caseWorkflowRawDataTool88AddressSetsOverlap(leftValues = [], rightValues = []) {
      const left = new Set(Array.from(leftValues || []).map(value => normalizeCell(value)).filter(Boolean));
      const right = new Set(Array.from(rightValues || []).map(value => normalizeCell(value)).filter(Boolean));
      if (!left.size || !right.size) return false;
      return Array.from(left).some(value => right.has(value));
    },

    caseWorkflowRawDataTool88AcceptedReportBoundaryEntries(rows = []) {
      const graph = this.caseWorkflowRawDataTool88CollectGraphEntries(rows);
      return graph.entries.filter((entry) => {
        const row = entry?.row || entry?.rows?.[0]?.row || [];
        return this.caseManualStartRowHasAcceptedReportBoundary?.(row) === true;
      });
    },

    caseWorkflowRawDataTool88EntryMatchesAcceptedReportBoundary(entry = {}, acceptedEntries = []) {
      if (!entry?.hashKey) return false;
      return (Array.isArray(acceptedEntries) ? acceptedEntries : []).some((accepted) => {
        if (!accepted?.hashKey || accepted.hashKey !== entry.hashKey) return false;
        if (accepted.fromHashKey && entry.fromHashKey && accepted.fromHashKey !== entry.fromHashKey) return false;

        const entryDirection = this.caseWorkflowRawDataTool88EntryDirection(entry);
        const acceptedDirection = this.caseWorkflowRawDataTool88EntryDirection(accepted);
        if (entryDirection && acceptedDirection && entryDirection !== acceptedDirection) return false;
        const direction = entryDirection || acceptedDirection;

        const entryBranchKey = this.caseWorkflowRawDataTool88BranchIdentityKey(entry);
        const acceptedBranchKey = this.caseWorkflowRawDataTool88BranchIdentityKey(accepted);
        if (entryBranchKey && acceptedBranchKey && entryBranchKey === acceptedBranchKey) return true;

        const entryRow = entry?.row || entry?.rows?.[0]?.row || [];
        const acceptedRow = accepted?.row || accepted?.rows?.[0]?.row || [];
        const entryOutputIndex = this.caseWorkflowRawDataTool88OutputIndexFromRow(entryRow);
        const acceptedOutputIndex = this.caseWorkflowRawDataTool88OutputIndexFromRow(acceptedRow);
        if (Number.isInteger(entryOutputIndex) && Number.isInteger(acceptedOutputIndex)
          && entryOutputIndex !== acceptedOutputIndex) return false;
        const entryToken = normalizeCell(entryRow?.[CASE_MANUAL_START_COL.TOKEN]).trim().toUpperCase();
        const acceptedToken = normalizeCell(acceptedRow?.[CASE_MANUAL_START_COL.TOKEN]).trim().toUpperCase();
        if (entryToken && acceptedToken && entryToken !== acceptedToken) return false;
        const entryAmount = this.caseManualParseNumericAmount(entryRow?.[CASE_MANUAL_START_COL.AMOUNT]);
        const acceptedAmount = this.caseManualParseNumericAmount(acceptedRow?.[CASE_MANUAL_START_COL.AMOUNT]);
        if (Number.isFinite(entryAmount) && Number.isFinite(acceptedAmount)) {
          const tolerance = Math.max(1e-9, Math.max(Math.abs(entryAmount), Math.abs(acceptedAmount)) * 1e-9);
          if (Math.abs(entryAmount - acceptedAmount) > tolerance) return false;
        }

        const entryEndpointKeys = direction === 'reverse' ? entry.fromAddressKeys : entry.toAddressKeys;
        const acceptedEndpointKeys = direction === 'reverse' ? accepted.fromAddressKeys : accepted.toAddressKeys;
        if (entryEndpointKeys?.size && acceptedEndpointKeys?.size) {
          return this.caseWorkflowRawDataTool88AddressSetsOverlap(entryEndpointKeys, acceptedEndpointKeys);
        }

        if (entry.fromAddressKeys?.size && accepted.fromAddressKeys?.size
          && !this.caseWorkflowRawDataTool88AddressSetsOverlap(entry.fromAddressKeys, accepted.fromAddressKeys)) return false;
        if (entry.toAddressKeys?.size && accepted.toAddressKeys?.size
          && !this.caseWorkflowRawDataTool88AddressSetsOverlap(entry.toAddressKeys, accepted.toAddressKeys)) return false;
        return true;
      });
    },

    caseWorkflowRawDataTool88ChildrenForEntry(parent = {}, entries = [], options = {}) {
      if (!parent?.hashKey) return [];
      return (Array.isArray(entries) ? entries : []).filter(child => {
        if (!child || child.key === parent.key || !child.hashKey) return false;
        if (child.fromHashKey !== parent.hashKey) return false;

        // Column O still points the earlier/source transaction back to the
        // later transaction in Reverse. Address continuity changes direction:
        // Forward uses parent D -> child C; Reverse uses parent C -> child D.
        const direction = normalizeCell(options.direction).toLowerCase()
          || this.caseWorkflowRawDataTool88EntryDirection(parent)
          || this.caseWorkflowRawDataTool88EntryDirection(child)
          || 'forward';
        const parentEndpointKeys = direction === 'reverse'
          ? parent.fromAddressKeys
          : parent.toAddressKeys;
        const childConnectionKeys = direction === 'reverse'
          ? child.toAddressKeys
          : child.fromAddressKeys;
        if (!parentEndpointKeys?.size || !childConnectionKeys?.size) return true;
        return this.caseWorkflowRawDataTool88AddressSetsOverlap(parentEndpointKeys, childConnectionKeys);
      });
    },

    caseWorkflowRawDataTool88OutputIndexFromRow(row = []) {
      const notes = [
        row?.[CASE_MANUAL_START_COL.CASE_NOTES],
        row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
      ].map(value => normalizeCell(value)).filter(Boolean).join('\n');
      const match = notes.match(/(?:(?:BTC\s+)?output|log|event|transfer)\s+index\s+(\d+)/iu);
      return match ? Number(match[1]) : null;
    },

    caseWorkflowRawDataTool88BranchIdentityKey(entry = {}) {
      const row = entry?.row || entry?.rows?.[0]?.row || [];
      const token = normalizeCell(row?.[CASE_MANUAL_START_COL.TOKEN]).trim().toUpperCase();
      const chain = normalizeCell(
        row?.[CASE_MANUAL_START_COL.CHAIN_ID]
          || row?.[CASE_MANUAL_START_COL.CHAIN_NAME]
      ).trim().toLowerCase();
      const outputIndex = this.caseWorkflowRawDataTool88OutputIndexFromRow(row);
      const amount = this.caseManualParseNumericAmount(row?.[CASE_MANUAL_START_COL.AMOUNT]);
      const toAddresses = Array.from(entry?.toAddressKeys || []).sort().join(',');
      const amountFallback = Number.isInteger(outputIndex)
        ? ''
        : (Number.isFinite(amount) ? `amount:${this.caseManualFormatAmount(amount)}` : '');

      // Stable identity describes the on-chain output/transfer. Column M funds
      // followed and Columns R/S left bounds are allocation results, so using them
      // as identity made one output appear new after every recalculation.
      return [
        entry?.hashKey || this.caseWorkflowRawToMainHashKey(row?.[CASE_MANUAL_START_COL.TX_HASH]),
        entry?.fromHashKey || this.caseWorkflowRawToMainHashKey(row?.[CASE_MANUAL_START_COL.FROM_HASH]),
        entry?.fromAddressKey || this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]),
        toAddresses || this.caseWorkflowRootAddressKey(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
        token,
        chain,
        Number.isInteger(outputIndex) ? `branch:${outputIndex}` : amountFallback,
      ].join('|');
    },

    caseWorkflowRawDataTool88CollapseStableDuplicateEntries(rows = []) {
      const graph = this.caseWorkflowRawDataTool88CollectGraphEntries(rows);
      const nextRows = graph.rows.map(row => Array.isArray(row) ? row.slice() : row);
      const seenBranches = new Set();
      const rangesToRemove = [];
      const removed = [];

      graph.entries.forEach((entry) => {
        const row = entry?.row || entry?.rows?.[0]?.row || [];
        const rowCount = Array.isArray(entry?.rows) ? entry.rows.length : 0;
        if (!entry?.fromHashKey || entry?.pathKey || rowCount !== 1) return;
        const branchKey = this.caseWorkflowRawDataTool88BranchIdentityKey(entry);
        if (!branchKey) return;
        if (!seenBranches.has(branchKey)) {
          seenBranches.add(branchKey);
          return;
        }
        const start = Math.max(0, Number(entry?.startRowIndex || 0));
        const end = Math.max(start, Number(entry?.endRowIndex ?? start));
        rangesToRemove.push({ start, end });
        removed.push({
          rowIndex: start,
          spreadsheetRowNumber: start + 1,
          pathLabel: normalizeCell(row?.[CASE_MANUAL_START_COL.PATH_LABEL]),
          hash: normalizeCell(row?.[CASE_MANUAL_START_COL.TX_HASH]),
          fromHash: normalizeCell(row?.[CASE_MANUAL_START_COL.FROM_HASH]),
          fromAddress: normalizeCell(row?.[CASE_MANUAL_START_COL.FROM_ADDRESS]),
          toAddress: normalizeCell(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
          outputIndex: this.caseWorkflowRawDataTool88OutputIndexFromRow(row),
          branchKey,
        });
      });

      rangesToRemove
        .sort((left, right) => right.start - left.start)
        .forEach(({ start, end }) => nextRows.splice(start, end - start + 1));

      return {
        rows: nextRows,
        removedCount: removed.length,
        removed: removed.slice(0, 24),
      };
    },

    caseWorkflowRawDataTool88UniqueChildrenForEntry(parent = {}, entries = [], options = {}) {
      const seenBranches = new Set();
      return this.caseWorkflowRawDataTool88ChildrenForEntry(parent, entries, options).filter((child) => {
        const branchKey = this.caseWorkflowRawDataTool88BranchIdentityKey(child);
        if (!branchKey || seenBranches.has(branchKey)) return false;
        seenBranches.add(branchKey);
        return true;
      });
    },

    caseWorkflowRawDataTool88RepairGraphRows(rows = []) {
      const duplicateCollapse = this.caseWorkflowRawDataTool88CollapseExactDuplicateRows(rows);
      const stableDuplicateCollapse = this.caseWorkflowRawDataTool88CollapseStableDuplicateEntries(
        duplicateCollapse.rows
      );
      const graph = this.caseWorkflowRawDataTool88CollectGraphEntries(stableDuplicateCollapse.rows);
      const nextRows = graph.rows.map(row => Array.isArray(row) ? row.slice() : row);
      let repairedContinuationCount = 0;
      const repairs = [];
      graph.entries.forEach(parent => {
        const children = this.caseWorkflowRawDataTool88UniqueChildrenForEntry(parent, graph.entries);
        const childHashes = children
          .map(child => normalizeCell(child.hash).trim())
          .filter(Boolean);
        if (!childHashes.length) return;
        const rowIndex = Number(parent.startRowIndex);
        const row = Array.isArray(nextRows[rowIndex]) ? nextRows[rowIndex].slice() : [];
        const desired = `Continues as ${childHashes.join(', ')}`;
        const current = normalizeCell(row?.[CASE_MANUAL_START_COL.CONTINUES_AS]).trim();
        if (current === desired) return;
        if (current && !/^path\s+ends\.?$/iu.test(current) && !/^continues\s+as\b/iu.test(current)) return;
        row[CASE_MANUAL_START_COL.CONTINUES_AS] = desired;
        row[CASE_MANUAL_START_COL.SYSTEM_NOTES] = this.caseWorkflowRawDataTool88StripPathEndsSentence(
          row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES]
        );
        row[CASE_MANUAL_START_COL.CASE_NOTES] = this.caseWorkflowRawDataTool88StripPathEndsSentence(
          row?.[CASE_MANUAL_START_COL.CASE_NOTES]
        );
        nextRows[rowIndex] = row;
        repairedContinuationCount += 1;
        repairs.push({
          rowIndex,
          spreadsheetRowNumber: rowIndex + 1,
          pathLabel: normalizeCell(row?.[CASE_MANUAL_START_COL.PATH_LABEL]),
          hash: normalizeCell(row?.[CASE_MANUAL_START_COL.TX_HASH]),
          previous: current,
          next: desired,
          childCount: childHashes.length,
        });
      });
      return {
        rows: nextRows,
        changed: repairedContinuationCount > 0
          || duplicateCollapse.removedCount > 0
          || stableDuplicateCollapse.removedCount > 0,
        repairedContinuationCount,
        removedExactDuplicateCount: Number(duplicateCollapse.removedCount || 0),
        removedExactDuplicates: duplicateCollapse.removed,
        removedStableBranchDuplicateCount: Number(stableDuplicateCollapse.removedCount || 0),
        removedStableBranchDuplicates: stableDuplicateCollapse.removed,
        repairs: repairs.slice(0, 24),
      };
    },

    caseWorkflowRawDataTool88ParseContinuationHashes(value = '') {
      const text = normalizeCell(value).trim();
      if (!/^continues\s+as\b/iu.test(text)) return [];
      return text
        .replace(/^continues\s+as\s*/iu, '')
        .split(',')
        .map(part => normalizeCell(part).trim())
        .filter(Boolean)
        .map(part => part.replace(/\s+output\s+\d+.*$/iu, '').trim())
        .filter(hash => !!this.caseWorkflowRawToMainHashKey(hash));
    },

    caseWorkflowRawDataTool88IsUnknownAttribution(value = '') {
      return /^(?:unknown|unattributed|none|n\/?a|no\s+(?:hit|attribution)|not\s+attributed)$/iu.test(normalizeCell(value).trim());
    },

    caseWorkflowRawDataTool88StripNegativeMemoEvidenceText(value = '') {
      const text = normalizeCell(value).toLowerCase();
      if (!text) return '';
      const markerPattern = /op[_\s-]?return|\bmemo\b/iu;
      const negativePattern = new RegExp([
        '(?:\\bno\\b|\\bnot\\b|\\bwithout\\b|\\babsent\\b|\\bmissing\\b|\\bnone\\b).{0,96}(?:op[_\\s-]?return|\\bmemo\\b)',
        '(?:op[_\\s-]?return|\\bmemo\\b).{0,96}(?:\\bnot\\s+(?:found|detected|present|observed|included|contained)\\b|\\babsent\\b|\\bmissing\\b|\\bnone\\b)',
        '(?:does|did|do|could|can|was|were|is|are)\\s+not.{0,96}(?:op[_\\s-]?return|\\bmemo\\b)',
        '(?:failed|unable)\\s+to.{0,64}(?:find|detect|observe|identify).{0,64}(?:op[_\\s-]?return|\\bmemo\\b)',
      ].join('|'), 'iu');
      return text
        .split(/[.!?;]\s+|[\r\n]+/u)
        .filter(fragment => !markerPattern.test(fragment) || !negativePattern.test(fragment))
        .join(' ')
        .trim();
    },

    caseWorkflowRawDataTool88HasPositiveMemoEvidence(value = '') {
      const rawText = normalizeCell(value).toLowerCase();
      if (!rawText) return false;
      if (/\bbtc_(?:op_return_memo|op_return_present|memo_present)\b/iu.test(rawText)) return true;
      const text = this.caseWorkflowRawDataTool88StripNegativeMemoEvidenceText(rawText);
      if (!text || !/(?:op[_\s-]?return|\bmemo\b)/iu.test(text)) return false;
      return /(?:tool\s*88\s+signal\s+check:\s*btc\s+(?:op[_\s-]?return|memo)(?:\s*\/\s*(?:op[_\s-]?return|memo))?\s+(?:found|detected|present)|(?:op[_\s-]?return|\bmemo\b).{0,80}\b(?:found|detected|present|observed|included|contained)\b|\b(?:found|detected|present|observed|included|contained)\b.{0,80}(?:op[_\s-]?return|\bmemo\b)|\b(?:has|contains?|includes?)\b.{0,80}(?:op[_\s-]?return|\bmemo\b)|(?:op[_\s-]?return|\bmemo\b)(?:\s+(?:payload|text|value|message|data))?\s*[:=]\s*\S)/iu.test(text);
    },

    caseWorkflowRawDataTool88TerminalHasStory(row = [], options = {}) {
      const noteText = [
        row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES],
        row?.[CASE_MANUAL_START_COL.ANALYST_NOTES],
        row?.[CASE_MANUAL_START_COL.CASE_NOTES],
      ].map(value => normalizeCell(value)).filter(Boolean).join('\n');
      const direction = normalizeCell(options.direction).toLowerCase()
        || this.caseWorkflowRawDataTool88DirectionFromText(noteText);
      const attributionColumns = direction === 'reverse'
        ? [CASE_MANUAL_START_COL.FROM_ATTRIBUTION]
        : direction === 'forward'
          ? [CASE_MANUAL_START_COL.ATTRIBUTION]
          : [CASE_MANUAL_START_COL.ATTRIBUTION];
      const hasRelevantAttribution = attributionColumns.some((column) => {
        const attribution = normalizeCell(row?.[column]).trim();
        return attribution && !this.caseWorkflowRawDataTool88IsUnknownAttribution(attribution);
      });
      if (hasRelevantAttribution || options.acceptedReportBoundary === true) return true;
      if (!noteText) return false;
      const reverseSwapContinuation = direction === 'reverse'
        && /tool\s*88\s*:\s*reverse\s+swap\s+solved|action\s*=\s*reverse-swap[^\n]*direction\s*=\s*reverse|reverse[-_\s]*swap\s+funds\s+followed/iu.test(noteText);
      if (reverseSwapContinuation) return false;
      if (this.caseWorkflowRawDataTool88HasPositiveMemoEvidence(noteText)) return true;
      return /(?:path\s+ends\s+at\s+\S+|(?:a\s+portion\s+of\s+the\s+)?funds?\s+(?:remain|remains|remained)\b|remains\s+at\s+the\s+(?:receiving\s+)?address|dormant|brick\s*wall|no\s+qualifying\s+(?:outgoing|exit)|no\s+spendable|deposit\s+address|hot\s+wallet|(?:known|meaningful|attributed)\s+(?:service|exchange|entity)|(?:service|exchange)\s+address|funds\s+are\s+swapped|swap\s+solved|(?:known|recognized|named)\s+bridge|bridge\s+(?:could\s+not\s+be\s+solved|requires?\s+manual\s+review)|low[-\s]*value|manual[-\s]*path[-\s]*ending|(?:likely\s+)?address[-_\s]*poison(?:ing)?|poisoning[-_\s]*dust|address-poisoning-ending|report\s+boundary\s+accepted|accepted-report-boundary|automatic\s+decision:\s*(?:the\s+selected\s+address|path\s+ends)|flow:\s*assessed;\s*action\s*=\s*(?:complete|done|manual-path-ending|accepted-report-boundary|address-poisoning-ending|dormant-ending|service-stop|low-value-ended|swap|bridge)|(?:followed\s+btc|funds?)\s+(?:was|were|is|are)?\s*consumed\s+by\s+(?:the\s+)?miner\s+fee)/iu.test(noteText);
    },

    caseWorkflowRawDataTool88AuditGraphRows(rows = [], options = {}) {
      const graph = this.caseWorkflowRawDataTool88CollectGraphEntries(rows);
      const acceptedReportBoundaryEntries = Array.isArray(options.acceptedReportBoundaryEntries)
        ? options.acceptedReportBoundaryEntries
        : [];
      const graphDirection = normalizeCell(options.direction).toLowerCase()
        || (Array.isArray(graph?.entries) ? graph.entries : [])
          .map(entry => this.caseWorkflowRawDataTool88EntryDirection(entry))
          .find(Boolean)
        || '';
      const issues = [];
      const issueKeys = new Set();
      const addIssue = (code, entry = null, rowIndex = -1, detail = {}, blocking = true) => {
        const key = `${code}|${Number(rowIndex)}|${entry?.key || ''}|${normalizeCell(detail?.hash || '')}`;
        if (issueKeys.has(key)) return;
        issueKeys.add(key);
        issues.push({
          code,
          blocking,
          rowIndex: Number(rowIndex),
          spreadsheetRowNumber: Number(rowIndex) >= 0 ? Number(rowIndex) + 1 : '',
          pathLabel: normalizeCell(entry?.pathLabel || detail?.pathLabel || ''),
          hash: normalizeCell(entry?.hash || detail?.hash || ''),
          toAddress: normalizeCell(entry?.row?.[CASE_MANUAL_START_COL.TO_ADDRESS] || detail?.toAddress || ''),
          message: normalizeCell(detail?.message || code),
          ...detail,
        });
      };
      const hashCounts = new Map();
      graph.entries.forEach(entry => {
        if (entry.hashKey) hashCounts.set(entry.hashKey, (hashCounts.get(entry.hashKey) || 0) + 1);
      });

      graph.rows.forEach((row, rowIndex) => {
        if (!Array.isArray(row) || !rowHasMeaningfulData(row) || this.caseManualIsHeaderRow?.(row)) return;
        const hash = normalizeCell(row?.[CASE_MANUAL_START_COL.TX_HASH]).trim();
        const fromHash = normalizeCell(row?.[CASE_MANUAL_START_COL.FROM_HASH]).trim();
        const tokenText = [
          row?.[CASE_MANUAL_START_COL.TOKEN],
          row?.[CASE_MANUAL_START_COL.CHAIN_ID],
          row?.[CASE_MANUAL_START_COL.CHAIN_NAME],
        ].map(value => normalizeCell(value).toLowerCase()).join(' ');
        const btc = /\b(?:btc|bitcoin)\b/u.test(tokenText)
          || /^bc1|^[13][a-km-zA-HJ-NP-Z1-9]{25,}$/u.test(normalizeCell(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]).trim());
        if (!hash && fromHash && btc) {
          addIssue('btc-branch-missing-transaction-hash', null, rowIndex, {
            hash: '',
            pathLabel: normalizeCell(row?.[CASE_MANUAL_START_COL.PATH_LABEL]),
            toAddress: normalizeCell(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
            message: 'A BTC branch row has a parent hash but no transaction hash in Column B, so it cannot become an independent graph entry.',
          });
        }
      });

      graph.entries.forEach(entry => {
        const row = entry.row || [];
        const rowIndex = Number(entry.startRowIndex);
        const direction = graphDirection
          || this.caseWorkflowRawDataTool88EntryDirection(entry)
          || 'forward';
        const acceptedReportBoundary = this.caseWorkflowRawDataTool88EntryMatchesAcceptedReportBoundary(
          entry,
          acceptedReportBoundaryEntries
        );
        const children = this.caseWorkflowRawDataTool88UniqueChildrenForEntry(entry, graph.entries, { direction });
        const childHashCounts = new Map();
        children.forEach((child) => {
          if (child.hashKey) childHashCounts.set(child.hashKey, (childHashCounts.get(child.hashKey) || 0) + 1);
        });
        const q = normalizeCell(row?.[CASE_MANUAL_START_COL.CONTINUES_AS]).trim();
        const pathEnds = /^path\s+ends\.?$/iu.test(q);
        const continues = /^continues\s+as\b/iu.test(q);
        const notes = `${normalizeCell(row?.[CASE_MANUAL_START_COL.SYSTEM_NOTES])}\n${normalizeCell(row?.[CASE_MANUAL_START_COL.CASE_NOTES])}`;
        const traced = /tool\s*88:.*traced\s+through|action\s*=\s*traced-through/iu.test(notes);
        const tokenText = [
          row?.[CASE_MANUAL_START_COL.TOKEN],
          row?.[CASE_MANUAL_START_COL.CHAIN_ID],
          row?.[CASE_MANUAL_START_COL.CHAIN_NAME],
        ].map(value => normalizeCell(value).toLowerCase()).join(' ');
        const btc = /\b(?:btc|bitcoin)\b/u.test(tokenText)
          || /^bc1|^[13][a-km-zA-HJ-NP-Z1-9]{25,}$/u.test(normalizeCell(row?.[CASE_MANUAL_START_COL.TO_ADDRESS]).trim());
        const root = !entry.fromHashKey;

        if (root && btc) {
          const required = [
            ['Column C From Address', CASE_MANUAL_START_COL.FROM_ADDRESS],
            ['Column D To Address', CASE_MANUAL_START_COL.TO_ADDRESS],
            ['Column E UTC Date', CASE_MANUAL_START_COL.DATE],
            ['Column F Amount', CASE_MANUAL_START_COL.AMOUNT],
            ['Column G Token', CASE_MANUAL_START_COL.TOKEN],
            ['Column M Funds Followed', CASE_MANUAL_START_COL.FUNDS_FOLLOWED],
          ];
          const missing = required.filter(([, column]) => !normalizeCell(row?.[column]).trim()).map(([label]) => label);
          if (missing.length) {
            addIssue('btc-root-missing-required-details', entry, rowIndex, {
              missingFields: missing,
              message: `BTC raw root is missing required Tool 68 details: ${missing.join(', ')}.`,
            });
          }
        }

        if (children.length && pathEnds) {
          addIssue('linked-child-but-parent-path-ends', entry, rowIndex, {
            childHashes: children.map(child => child.hash),
            message: 'This row has linked child transactions but Column Q still says Path ends.',
          });
        }
        if (traced && pathEnds) {
          addIssue('traced-row-still-path-ends', entry, rowIndex, {
            message: 'Tool 88 notes say the address was traced through, but Column Q still says Path ends.',
          });
        }
        if (!children.length && !pathEnds && !continues) {
          addIssue('missing-terminal-or-continuation', entry, rowIndex, {
            message: 'This raw entry has neither a continuation link nor a reviewed path-ending state.',
          });
        }
        if (!children.length && pathEnds && !this.caseWorkflowRawDataTool88TerminalHasStory(row, {
          direction,
          acceptedReportBoundary,
        })) {
          addIssue('path-ending-without-story', entry, rowIndex, {
            message: 'Column Q says Path ends, but the row has no direction-relevant attribution, accepted report boundary, dormant-funds explanation, or other path-ending rationale.',
          });
        }
        if (continues) {
          const references = this.caseWorkflowRawDataTool88ParseContinuationHashes(q);
          const referencedCounts = new Map();
          references.forEach((hash) => {
            const key = this.caseWorkflowRawToMainHashKey(hash);
            if (key) referencedCounts.set(key, (referencedCounts.get(key) || 0) + 1);
          });
          referencedCounts.forEach((required, hashKey) => {
            const available = childHashCounts.get(hashKey) || 0;
            if (available < required) {
              addIssue('continues-as-reference-missing-child', entry, rowIndex, {
                referencedHash: references.find(hash => this.caseWorkflowRawToMainHashKey(hash) === hashKey) || hashKey,
                requiredCount: required,
                availableCount: available,
                message: `Column Q references ${required} child branch occurrence${required === 1 ? '' : 's'}, but only ${available} matching branch entr${available === 1 ? 'y was' : 'ies were'} exported.`,
              });
            }
          });
          childHashCounts.forEach((required, hashKey) => {
            const available = referencedCounts.get(hashKey) || 0;
            if (available < required) {
              addIssue('linked-child-missing-from-continues-as', entry, rowIndex, {
                childHash: children.find(child => child.hashKey === hashKey)?.hash || hashKey,
                requiredCount: required,
                availableCount: available,
                message: `The raw graph contains ${required} distinct linked child branch${required === 1 ? '' : 'es'}, but Column Q references only ${available}.`,
              });
            }
          });
        }
      });

      const counts = {};
      issues.forEach(issue => { counts[issue.code] = (counts[issue.code] || 0) + 1; });
      const blockingIssues = issues.filter(issue => issue.blocking !== false);
      return {
        generatedAtUtc: new Date().toISOString(),
        rowCount: graph.rows.filter(row => Array.isArray(row) && rowHasMeaningfulData(row) && !this.caseManualIsHeaderRow?.(row)).length,
        entryCount: graph.entries.length,
        duplicateHashEntryCount: Array.from(hashCounts.values()).filter(count => count > 1).reduce((sum, count) => sum + count, 0),
        duplicateHashGroupCount: Array.from(hashCounts.values()).filter(count => count > 1).length,
        blockingIssueCount: blockingIssues.length,
        issueCount: issues.length,
        counts,
        examples: issues.slice(0, 24),
        isValid: blockingIssues.length === 0,
      };
    },


    caseWorkflowRawDataTool88PrepareRootBlockCommit(rows = [], selectedRoots = []) {
      const graph = this.caseWorkflowRawDataTool88CollectGraphEntries(rows);
      const rootEntries = graph.entries.filter(entry => !entry.fromHashKey);
      const selected = Array.isArray(selectedRoots) && selectedRoots.length
        ? selectedRoots
        : rootEntries.map((entry, index) => ({
            id: `solver-root-${index}`,
            pathLabel: entry.pathLabel,
            hash: entry.hash,
            toAddress: normalizeCell(entry?.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
            rowIndex: entry.startRowIndex
          }));

      const descriptors = selected.map((root, index) => {
        const hash = normalizeCell(
          root?.hash
            || root?.transactionHash
            || root?.txHash
            || root?.row?.[CASE_MANUAL_START_COL.TX_HASH]
            || ''
        ).trim();
        const pathLabel = normalizeCell(
          root?.pathLabel
            || root?.label
            || root?.row?.[CASE_MANUAL_START_COL.PATH_LABEL]
            || ''
        ).trim();
        const toAddress = normalizeCell(
          root?.toAddress
            || root?.targetAddress
            || root?.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]
            || ''
        ).trim();
        return {
          index,
          id: normalizeCell(root?.id || `selected-root-${index}`),
          rootInstanceKey: normalizeCell(root?.rootInstanceKey)
            || this.caseWorkflowRootInstanceKey({
              hash,
              pathLabel,
              toAddress,
              rowIndex: Number(root?.rowIndex)
            }),
          stableRootKey: this.caseWorkflowRootInstanceKey({
            hash,
            pathLabel,
            toAddress
          }, { includeRowIndex: false }),
          hash,
          hashKey: this.caseWorkflowRawToMainHashKey(hash),
          pathLabel,
          pathKey: this.caseWorkflowRootIdentityKey(pathLabel),
          toAddress,
          toAddressKey: this.caseWorkflowRootAddressKey(toAddress),
          spreadsheetRowNumber: root?.spreadsheetRowNumber || '',
          duplicateHash: root?.duplicateHash === true
        };
      }).filter(root => root.hashKey);

      const descriptorHashCounts = new Map();
      descriptors.forEach((root) => {
        descriptorHashCounts.set(root.hashKey, (descriptorHashCounts.get(root.hashKey) || 0) + 1);
      });

      const usedRootEntryKeys = new Set();
      const claimedEntryKeys = new Set();
      const blocks = [];

      descriptors.forEach((descriptor) => {
        let bestEntry = null;
        let bestScore = -1;
        rootEntries.forEach((entry) => {
          if (!entry?.hashKey || entry.hashKey !== descriptor.hashKey) return;
          if (usedRootEntryKeys.has(entry.key)) return;
          const entryPathKey = this.caseWorkflowRootIdentityKey(entry.pathLabel);
          const entryTargetKey = this.caseWorkflowRootAddressKey(
            entry?.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]
          );
          const pathMatch = !!descriptor.pathKey && entryPathKey === descriptor.pathKey;
          const targetMatch = !!descriptor.toAddressKey && entryTargetKey === descriptor.toAddressKey;
          const duplicateHash = descriptor.duplicateHash === true
            || (descriptorHashCounts.get(descriptor.hashKey) || 0) > 1;

          // Duplicate-hash roots must match either the root path label or the
          // investigator-selected Column D address. A hash-only match would
          // collapse distinct BTC flows.
          if (duplicateHash && !pathMatch && !targetMatch) return;

          let score = 10;
          if (pathMatch) score += 50;
          if (targetMatch) score += 70;
          if (pathMatch && targetMatch) score += 100;
          if (!descriptor.pathKey && !descriptor.toAddressKey && !duplicateHash) score += 20;
          if (score > bestScore) {
            bestEntry = entry;
            bestScore = score;
          }
        });

        if (!bestEntry) {
          blocks.push({
            root: descriptor,
            found: false,
            valid: false,
            rows: [],
            rowCount: 0,
            entryCount: 0,
            audit: {
              generatedAtUtc: new Date().toISOString(),
              rowCount: 0,
              entryCount: 0,
              blockingIssueCount: 1,
              issueCount: 1,
              counts: { 'selected-root-output-missing': 1 },
              examples: [{
                code: 'selected-root-output-missing',
                blocking: true,
                pathLabel: descriptor.pathLabel,
                hash: descriptor.hash,
                toAddress: descriptor.toAddress,
                message: 'Tool 88 did not retain an output root matching this selected starting transaction.'
              }],
              isValid: false
            },
            repair: {
              changed: false,
              repairedContinuationCount: 0,
              repairs: []
            }
          });
          return;
        }

        usedRootEntryKeys.add(bestEntry.key);
        const queue = [bestEntry];
        const blockEntries = [];
        const visited = new Set();
        while (queue.length) {
          const entry = queue.shift();
          if (!entry?.key || visited.has(entry.key)) continue;
          if (claimedEntryKeys.has(entry.key) && entry.key !== bestEntry.key) continue;
          visited.add(entry.key);
          blockEntries.push(entry);
          this.caseWorkflowRawDataTool88ChildrenForEntry(entry, graph.entries).forEach((child) => {
            if (!visited.has(child.key)) queue.push(child);
          });
        }
        blockEntries.forEach(entry => claimedEntryKeys.add(entry.key));

        const rowIndices = Array.from(new Set(
          blockEntries.flatMap(entry =>
            (Array.isArray(entry?.rows) ? entry.rows : [])
              .map(item => Number(item?.rowIndex))
              .filter(rowIndex => Number.isInteger(rowIndex) && rowIndex >= 0)
          )
        )).sort((left, right) => left - right);
        const blockRows = rowIndices
          .map(rowIndex => graph.rows[rowIndex])
          .filter(row => Array.isArray(row) && rowHasMeaningfulData(row))
          .map(row => row.slice());
        const repair = this.caseWorkflowRawDataTool88RepairGraphRows(blockRows);
        const audit = this.caseWorkflowRawDataTool88AuditGraphRows(repair.rows);
        blocks.push({
          root: descriptor,
          found: true,
          valid: audit.blockingIssueCount === 0,
          rows: repair.rows,
          rowCount: repair.rows.filter(row => Array.isArray(row) && rowHasMeaningfulData(row)).length,
          entryCount: blockEntries.length,
          sourceStartRowIndex: Number(bestEntry.startRowIndex),
          sourceEndRowIndex: rowIndices.length ? rowIndices[rowIndices.length - 1] : Number(bestEntry.endRowIndex),
          audit,
          repair: {
            changed: Boolean(repair.changed),
            repairedContinuationCount: Number(repair.repairedContinuationCount || 0),
            removedExactDuplicateCount: Number(repair.removedExactDuplicateCount || 0),
            removedExactDuplicates: Array.isArray(repair.removedExactDuplicates) ? repair.removedExactDuplicates.slice(0, 24) : [],
            removedStableBranchDuplicateCount: Number(repair.removedStableBranchDuplicateCount || 0),
            removedStableBranchDuplicates: Array.isArray(repair.removedStableBranchDuplicates) ? repair.removedStableBranchDuplicates.slice(0, 24) : [],
            repairs: Array.isArray(repair.repairs) ? repair.repairs.slice(0, 24) : []
          }
        });
      });

      const validBlocks = blocks.filter(block => block.valid && block.rows.length);
      const invalidBlocks = blocks.filter(block => !block.valid);
      const commitRows = validBlocks.flatMap(block => block.rows.map(row => row.slice()));
      const unassignedEntries = graph.entries.filter(entry => !claimedEntryKeys.has(entry.key));

      return {
        selectedRootCount: descriptors.length,
        foundRootCount: blocks.filter(block => block.found).length,
        validRootCount: validBlocks.length,
        invalidRootCount: invalidBlocks.length,
        commitRowCount: commitRows.length,
        commitRows,
        blocks,
        invalidRoots: invalidBlocks.map(block => ({
          rootInstanceKey: block.root?.rootInstanceKey || '',
          pathLabel: block.root?.pathLabel || '',
          hash: block.root?.hash || '',
          toAddress: block.root?.toAddress || '',
          found: block.found === true,
          rowCount: Number(block.rowCount || 0),
          blockingIssueCount: Number(block.audit?.blockingIssueCount || 0),
          counts: block.audit?.counts || {},
          examples: Array.isArray(block.audit?.examples) ? block.audit.examples.slice(0, 6) : []
        })),
        blockSummaries: blocks.map(block => ({
          rootInstanceKey: block.root?.rootInstanceKey || '',
          stableRootKey: block.root?.stableRootKey || '',
          pathLabel: block.root?.pathLabel || '',
          hash: block.root?.hash || '',
          toAddress: block.root?.toAddress || '',
          duplicateHash: block.root?.duplicateHash === true,
          found: block.found === true,
          valid: block.valid === true,
          rowCount: Number(block.rowCount || 0),
          entryCount: Number(block.entryCount || 0),
          sourceStartRowIndex: Number.isFinite(Number(block.sourceStartRowIndex)) ? Number(block.sourceStartRowIndex) : '',
          sourceEndRowIndex: Number.isFinite(Number(block.sourceEndRowIndex)) ? Number(block.sourceEndRowIndex) : '',
          blockingIssueCount: Number(block.audit?.blockingIssueCount || 0),
          issueCounts: block.audit?.counts || {},
          issueExamples: Array.isArray(block.audit?.examples) ? block.audit.examples.slice(0, 6) : [],
          repairedContinuationCount: Number(block.repair?.repairedContinuationCount || 0),
          removedExactDuplicateCount: Number(block.repair?.removedExactDuplicateCount || 0),
          removedStableBranchDuplicateCount: Number(block.repair?.removedStableBranchDuplicateCount || 0)
        })),
        unassignedEntryCount: unassignedEntries.length,
        unassignedEntries: unassignedEntries.slice(0, 12).map(entry => ({
          pathLabel: entry.pathLabel || '',
          hash: entry.hash || '',
          fromHash: entry.fromHash || '',
          toAddress: normalizeCell(entry?.row?.[CASE_MANUAL_START_COL.TO_ADDRESS]),
          sourceStartRowIndex: Number(entry.startRowIndex)
        }))
      };
    },

    async appendCaseWorkflowRawDataTool88RowsToRawData(rows = [], options = {}) {
      const repair = this.caseWorkflowRawDataTool88RepairGraphRows(rows);
      const rawRows = this.caseWorkflowNormalizeAttributionMatrix(repair.rows).filter(row => rowHasMeaningfulData(row));
      if (!rawRows.length) throw new Error('Tool 88 did not return raw-data rows to append.');
      const rawGraphAudit = this.caseWorkflowRawDataTool88AuditGraphRows(rawRows);
      this.caseWorkflowRawDataTool88LastGraphAudit = rawGraphAudit;
      if (rawGraphAudit.blockingIssueCount > 0) {
        const firstIssue = rawGraphAudit.examples?.[0]?.message || 'Tool 88 raw rows failed the graph-integrity audit.';
        throw new Error(`Tool 88 raw rows were not appended because ${rawGraphAudit.blockingIssueCount} graph-integrity issue${rawGraphAudit.blockingIssueCount === 1 ? '' : 's'} remain. ${firstIssue}`);
      }
      const currentRaw = typeof this.getMatrixForLogicalSheet === 'function' ? this.getMatrixForLogicalSheet('raw') : [];
      const mergeResult = this.mergeCaseWorkflowRawRows(Array.isArray(currentRaw) ? currentRaw : [], rawRows);
      const nextRaw = Array.isArray(mergeResult?.rows) ? mergeResult.rows : rawRows;
      if (mergeResult.changed !== false) {
        await this.replaceCaseManualStartLogicalSheetMatrix(
          'raw',
          nextRaw,
          options.reason || `Appended ${rawRows.length} Tool 88 raw-data row${rawRows.length === 1 ? '' : 's'} from workflow automation.`,
          { autosaveDelayMs: 850 }
        );
      }
      this.caseWorkflowRawDataTool88LastRows = rawRows;
      this.caseAutomationTool88Activity = {
        ...(this.caseAutomationTool88Activity || {}),
        rowsAppended: rawRows.length,
        rawRows: rawRows.length,
        actualRowsAppended: Number(mergeResult?.appendedCount || 0),
        rowsReplaced: Number(mergeResult?.replacedCount || 0),
        completedAtUtc: new Date().toISOString(),
        active: false,
        message: `Tool 88 loaded ${rawRows.length} raw-data row${rawRows.length === 1 ? '' : 's'}.`,
        detail: `Tool 88 loaded ${rawRows.length} raw-data row${rawRows.length === 1 ? '' : 's'}.`
      };
      if (typeof this.refreshRawDataImportCoverage === 'function') this.refreshRawDataImportCoverage();
      if (typeof this.queueCaseSummaryRefresh === 'function') this.queueCaseSummaryRefresh(120);

      const coverage = this.getCaseWorkflowRawDataCoverageSummary();
      if (coverage.isComplete && typeof this.recordWorkflowSignal === 'function') {
        try {
          this.recordWorkflowSignal('RAW_DATA_FETCH', 'tool88_raw_data_solver', { silent: true, rowCount: rawRows.length });
        } catch {
          // Some workflow definitions use step-specific signal codes. Distinct
          // root-instance coverage remains the source of truth.
        }
      }
      this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88(
        coverage.isComplete
          ? `Tool 88 loaded ${rawRows.length} raw-data row${rawRows.length === 1 ? '' : 's'} and all starting roots are covered.`
          : `Tool 88 loaded ${rawRows.length} raw-data row${rawRows.length === 1 ? '' : 's'}; ${coverage.missingCount} distinct starting root${coverage.missingCount === 1 ? '' : 's'} still need raw data.`
      );
      return {
        rows: nextRaw,
        appendedCount: rawRows.length,
        actualAppendedCount: Number(mergeResult?.appendedCount || 0),
        replacedCount: Number(mergeResult?.replacedCount || 0),
        changed: mergeResult?.changed !== false,
        coverage,
        rawGraphAudit,
        graphRepair: {
          changed: Boolean(repair.changed),
          repairedContinuationCount: Number(repair.repairedContinuationCount || 0),
          removedExactDuplicateCount: Number(repair.removedExactDuplicateCount || 0),
          removedExactDuplicates: Array.isArray(repair.removedExactDuplicates) ? repair.removedExactDuplicates.slice(0, 24) : [],
          removedStableBranchDuplicateCount: Number(repair.removedStableBranchDuplicateCount || 0),
          removedStableBranchDuplicates: Array.isArray(repair.removedStableBranchDuplicates) ? repair.removedStableBranchDuplicates.slice(0, 24) : [],
          repairs: Array.isArray(repair.repairs) ? repair.repairs.slice(0, 24) : []
        }
      };
    },

    async runCaseWorkflowRawDataTool88Headless(options = {}) {
      if (this.caseWorkflowAllStepsComplete() && !options?.forceTool88) {
        this.caseWorkflowFinalizeAutomationIfComplete('Workflow is already complete; skipping stale Tool 88 raw-data request.');
        return { handled: false, reason: 'workflow-complete' };
      }
      if (this.caseWorkflowRawDataCoverageIsComplete() && !options?.forceTool88 && !this.caseWorkflowRawDataStepIsCurrent(options?.step || options?.workflowStep || null)) {
        return { handled: false, reason: 'raw-data-coverage-complete' };
      }
      if (this.caseWorkflowRawDataTool88Running) {
        return this.waitForCaseWorkflowRawDataTool88Run({ source: 'runCaseWorkflowRawDataTool88Headless' });
      }
      const inputRows = this.getCaseWorkflowRawDataTool88InputRows({
        missingOnly: options.workflowRawDataMissingOnly !== false,
        maxRootsPerPass: options.maxRootsPerPass
          ?? options.workflowRawDataMaxRootsPerPass
          ?? options.maxRootInputsPerPass
          ?? 1
      });
      const inputStats = this.caseWorkflowRawDataTool88RowStats(inputRows);
      if (!inputStats.txHashCount) throw new Error('No Main Data transaction hashes are available for Tool 88 raw-data solving.');
      const selectedInputRoots = (Array.isArray(this.caseWorkflowRawDataTool88SelectedInputRoots)
        ? this.caseWorkflowRawDataTool88SelectedInputRoots
        : []).map(root => ({ ...root }));
      const activeRoot = selectedInputRoots[0] || null;
      const activeRootLabel = normalizeCell(activeRoot?.pathLabel || '');
      const activeRootHash = normalizeCell(activeRoot?.hash || activeRoot?.transactionHash || '');
      const activeRootTarget = normalizeCell(activeRoot?.toAddress || '');
      const activeRootName = activeRootLabel
        ? `Path ${activeRootLabel}`
        : (activeRootHash ? this.caseWorkflowRawDataTool88ShortText(activeRootHash, 10, 8) : 'the next main transaction');
      const loadingMessage = `Loading ${activeRootName} into hidden Tool 88 raw-data automation. Case Buddy will keep this root active until every branch is resolved or a review guardrail is reached.`;

      const runKey = `workflow-tool88-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const currentCaseKey = this.caseWorkflowRawDataTool88CurrentCaseKey();
      this.resetCaseWorkflowRawDataTool88VisualState('Starting a fresh Tool 88 raw-data run for the current case.', {
        stopTool88: false,
        keepHost: true,
        keepRunning: true,
        clearAutoContinue: false,
        clearVisibleSolverOutput: true,
        runKey,
        caseKey: currentCaseKey
      });

      this.caseWorkflowRawDataTool88Running = true;
      this.caseWorkflowRawDataTool88RunKey = runKey;
      this.caseWorkflowRawDataTool88VisualCaseKey = currentCaseKey;
      this.caseHopSolverAutomationRunning = true;
      this.caseHopSolverRunning = false;
      this.caseWorkflowRawDataTool88LastError = '';
      this.caseWorkflowRawDataTool88VisualRows = [];
      this.closeCaseWorkflowRawDataVisibleSolverArtifacts('Hidden workflow Tool 88 raw-data solver is running.');
      this.showCaseHopSolverModal = false;
      const now = new Date().toISOString();
      this.caseAutomationTool88Activity = {
        ...(this.caseAutomationTool88Activity || {}),
        active: true,
        startedAtUtc: now,
        updatedAtUtc: now,
        completedAtUtc: '',
        status: 'loading-input',
        title: 'Tool 88 raw-data solver',
        message: loadingMessage,
        detail: loadingMessage,
        rawRows: 0,
        rowsAppended: 0,
        flowComplete: false,
        targetLabel: activeRootLabel,
        targetHash: activeRootHash,
        targetAddress: activeRootTarget,
        guardrailMessage: '',
        log: [{ id: `workflow-tool88-start-${Date.now()}`, message: `Starting hidden Tool 88 raw-data solver for ${activeRootName}.` }]
      };
      this.caseAutomationTool88Progress = {
        ...(this.caseAutomationTool88Progress || {}),
        active: true,
        title: 'Tool 88 raw-data solver',
        detail: loadingMessage,
        status: 'loading-input',
        selectedLabel: activeRootLabel,
        selectedHash: activeRootHash,
        selectedTarget: activeRootTarget,
        updatedAtUtc: now
      };
      this.caseWorkflowRawDataTool88RunEvents = [];
      this.recordCaseWorkflowRawDataTool88RunEvent?.('case-buddy-headless-started', {
        status: 'loading-input',
        message: loadingMessage,
        integrationAction: 'workflow-raw-data-headless-start',
        activeRoot: activeRoot ? { ...activeRoot } : null,
        selectedEntry: activeRoot ? {
          key: activeRoot.rootInstanceKey || activeRoot.id || '',
          pathLabel: activeRootLabel,
          txHash: activeRootHash,
          toAddress: activeRootTarget,
          spreadsheetRowNumber: activeRoot.spreadsheetRowNumber || ''
        } : null,
        selectedTargetAddress: activeRootTarget,
        outputRows: []
      });
      this.captureCaseWorkflowRawDataTool88LastRunRecord?.('started', { inputStats, options });

      let finalSnapshot = null;
      try {
        const tool = await this.ensureCaseWorkflowRawDataTool88Host();
        const commonOptions = {
          caseWorkflowRawData: true,
          caseBuddyRawDataAutomation: true,
          rawDataAutomation: true,
          allowUnverifiedTraceOutput: true,
          acceptUnverifiedTraceOutput: true,
          pauseOnUnverifiedOutput: false,
          includeRows: false,
          progressIncludeRows: true,
          forceCurrentRecommendation: true,
          traceDirection: 'forward',
          enableAdditionalServiceChecks: true,
          enableDepositEntityHopExtension: false,
          enableNativeDirectSpendMatchGuard: false,
          caseBuddyForceStrictNativeFifo: true,
          strictFifoNativeSpend: true,
          disableNativeDirectSpendMatchGuard: true,
          // Skip/rubber-stamp mode may safely record a positively identified
          // BTC bridge/OP_RETURN endpoint as a reviewed path ending when Tool 88
          // cannot produce a destination row yet. Tool 88 still requires strong
          // bridge evidence and leaves non-BTC or ambiguous cases for review.
          skipResolvableReviews: Boolean(this.caseAutomation?.skipResolvableReview),
          rubberStampResolvableReviews: Boolean(this.caseAutomation?.skipResolvableReview),
          autoFinalizeRecognizedBtcBridgeEndings: Boolean(this.caseAutomation?.skipResolvableReview),
          // Root context is diagnostic and defensive. Case Buddy normally sends
          // exactly one root row per pass, and Tool 88 exposes this identity in
          // progress/debug snapshots so queued roots cannot be mistaken for the
          // transaction currently being solved.
          workflowRawDataRoot: activeRoot ? { ...activeRoot } : null,
          caseBuddyActiveRoot: activeRoot ? { ...activeRoot } : null,
          caseBuddyActiveRootExclusive: true
        };
        await tool.caseBuddyLoadRowsForHopSolver(inputRows, {
          ...commonOptions,
          selectNextUnresolved: true,
          includeRows: false
        });
        const baseGuardrails = this.caseWorkflowRawDataTool88Guardrails(options);
        const maxAutomationSegments = Math.max(1, Math.min(24, Number(
          options.workflowRawDataMaxAutomationSegments
            ?? options.maxAutomationSegments
            ?? 12
        ) || 12));
        const continuationElapsedMs = Math.max(60 * 1000, Math.min(
          Number(baseGuardrails.maxElapsedMs || 15 * 60 * 1000),
          Number(options.workflowRawDataContinuationSegmentMs ?? 5 * 60 * 1000) || 5 * 60 * 1000
        ));
        const automationSegments = [];
        const rowFingerprint = (rows = []) => {
          const compactRows = (Array.isArray(rows) ? rows : [])
            .filter(row => Array.isArray(row) && rowHasMeaningfulData(row))
            .map(row => row.slice(0, CASE_MANUAL_START_COL.FROM_ATTRIBUTION + 1).map(value => normalizeCell(value)));
          return compactRows.length
            ? `tool88-segment-${this.caseWorkflowStableStringHash(JSON.stringify(compactRows))}`
            : '';
        };
        const guardrailReasonFor = (snapshot = {}) => normalizeCell(
          snapshot?.guardrail?.reason
            || snapshot?.runState?.lastGuardrail?.reason
            || snapshot?.stallDiagnostics?.runState?.lastGuardrail?.reason
        ).toLowerCase();
        const buildPassInspection = (snapshot = {}, allowBridgeFallback = false) => {
          const rowsSnapshot = typeof tool.buildCaseBuddyHopSolverSnapshot === 'function'
            ? tool.buildCaseBuddyHopSolverSnapshot({
                ...commonOptions,
                includeRows: true,
                status: snapshot?.status || 'workflow-raw-data-complete',
                integrationAction: snapshot?.integrationAction || 'workflow-raw-data-automation',
                message: snapshot?.message || 'Tool 88 raw-data automation completed.'
              })
            : snapshot;
          const chosenRows = this.pickCaseWorkflowRawDataTool88Rows(
            rowsSnapshot || snapshot || {},
            tool,
            { allowInputSnapshotFallback: false }
          );
          const bridgeAnnotation = allowBridgeFallback && chosenRows.rows.length
            ? this.caseWorkflowRawDataTool88AnnotateUnsolvedBridgeRows(chosenRows.rows, rowsSnapshot || snapshot || {}, tool)
            : {
                applied: false,
                rows: chosenRows.rows,
                rowIndex: -1,
                note: ''
              };
          const rawGraphRepair = this.caseWorkflowRawDataTool88RepairGraphRows(
            bridgeAnnotation.applied ? bridgeAnnotation.rows : chosenRows.rows
          );
          const candidateRows = rawGraphRepair.rows;
          const rootBlockCommit = this.caseWorkflowRawDataTool88PrepareRootBlockCommit(
            candidateRows,
            this.caseWorkflowRawDataTool88SelectedInputRoots
          );
          const exportRows = rootBlockCommit.commitRows;
          const rawGraphCandidateAudit = this.caseWorkflowRawDataTool88AuditGraphRows(candidateRows);
          const rawGraphAudit = exportRows.length
            ? this.caseWorkflowRawDataTool88AuditGraphRows(exportRows)
            : rawGraphCandidateAudit;
          const rawFlowState = ((rowsSnapshot || snapshot || {}).flowCompletion || snapshot?.flowCompletion || {});
          return {
            finalRowsSnapshot: rowsSnapshot,
            chosen: chosenRows,
            bridgeAnnotation,
            rawGraphRepair,
            candidateRows,
            rootBlockCommit,
            exportRows,
            rawGraphCandidateAudit,
            rawGraphAudit,
            rawFlowState,
            rawUnresolvedCount: Number(rawFlowState.incompleteCount ?? rawFlowState.unresolvedCount ?? 0) || 0,
            rawUnverifiedOutput: (rowsSnapshot || snapshot || {}).unverifiedOutput === true || snapshot?.unverifiedOutput === true
          };
        };

        let previousCandidateFingerprint = rowFingerprint(inputRows);
        let passInspection = null;
        for (let segmentIndex = 0; segmentIndex < maxAutomationSegments; segmentIndex += 1) {
          const segmentNumber = segmentIndex + 1;
          const segmentGuardrails = {
            ...baseGuardrails,
            ...(segmentIndex > 0 ? { maxElapsedMs: continuationElapsedMs } : {})
          };
          const segmentStopSnapshot = await tool.caseBuddyRunAutomationLoop({
            ...commonOptions,
            includeRows: true,
            guardrails: segmentGuardrails
          });
          finalSnapshot = segmentStopSnapshot;
          const guardrailReason = guardrailReasonFor(segmentStopSnapshot);
          const safeBudgetGuardrail = ['max-elapsed', 'max-steps', 'max-entries'].includes(guardrailReason);
          let boundarySettlement = null;

          // A timed segment can stop immediately after selecting a deterministic
          // low-value ending. Settle those no-network endings before deciding
          // whether another continuation segment is necessary.
          if (safeBudgetGuardrail && typeof tool.caseBuddySettleImmediateAutoEndings === 'function') {
            boundarySettlement = await tool.caseBuddySettleImmediateAutoEndings({
              ...commonOptions,
              includeRows: true,
              maxAutoLowValue: Number(segmentGuardrails.maxAutoLowValue || baseGuardrails.maxAutoLowValue || 50)
            });
            const boundaryAutoEndedCount = Number(boundarySettlement?.boundarySettlement?.settledCount || 0);
            if (boundaryAutoEndedCount > 0) {
              const boundaryFlowComplete = boundarySettlement?.flowComplete === true
                || boundarySettlement?.flowCompletion?.done === true;
              finalSnapshot = {
                ...boundarySettlement,
                status: boundaryFlowComplete ? 'flow-complete' : normalizeCell(segmentStopSnapshot?.status || 'guardrail-paused'),
                message: boundaryFlowComplete
                  ? normalizeCell(boundarySettlement?.message)
                  : `${normalizeCell(segmentStopSnapshot?.message)} ${normalizeCell(boundarySettlement?.message)}`.trim(),
                guardrail: boundaryFlowComplete ? null : (segmentStopSnapshot?.guardrail || null),
                segmentBudgetGuardrail: segmentStopSnapshot?.guardrail || null
              };
            }
          }

          passInspection = buildPassInspection(finalSnapshot, false);
          const segmentBridgeFallbackAllowed = Boolean(commonOptions.skipResolvableReviews || commonOptions.rubberStampResolvableReviews);
          const segmentBridgeEvidence = segmentBridgeFallbackAllowed && !safeBudgetGuardrail
            ? this.caseWorkflowRawDataTool88ExplicitBridgeEvidence(
                finalSnapshot,
                tool,
                passInspection?.candidateRows || passInspection?.chosen?.rows || []
              )
            : { found: false };
          if (segmentBridgeEvidence.found) {
            const bridgePassInspection = buildPassInspection(finalSnapshot, true);
            if (bridgePassInspection?.bridgeAnnotation?.applied) {
              passInspection = bridgePassInspection;
              finalSnapshot = {
                ...(finalSnapshot || {}),
                workflowRawDataUnsolvedBridgeAccepted: true,
                workflowRawDataUnsolvedBridgeRowIndex: bridgePassInspection.bridgeAnnotation.rowIndex,
                workflowRawDataUnsolvedBridgeNote: bridgePassInspection.bridgeAnnotation.note || '',
                outputRows: bridgePassInspection.candidateRows,
                message: normalizeCell(finalSnapshot?.message) || 'Tool 88 marked an unsolved bridge ending for manual review and continued the raw-data flow.',
              };
              if (typeof tool.caseBuddyLoadRowsForHopSolver === 'function') {
                await tool.caseBuddyLoadRowsForHopSolver(bridgePassInspection.candidateRows, {
                  ...commonOptions,
                  source: 'workflow-raw-data-unsolved-bridge-annotated',
                  selectNextUnresolved: true,
                  includeRows: true,
                  resetCaseBuddyStopState: true,
                  resetCaseBuddyStopReason: 'Workflow raw-data Auto marked an unsolved bridge endpoint for manual review and continued.'
                });
              }
            }
          }
          const candidateFingerprint = rowFingerprint(passInspection.candidateRows);
          const madeMaterialProgress = Boolean(candidateFingerprint)
            && candidateFingerprint !== previousCandidateFingerprint;
          const segmentRecord = {
            segmentNumber,
            maxSegments: maxAutomationSegments,
            maxElapsedMs: Number(segmentGuardrails.maxElapsedMs || 0),
            status: normalizeCell(finalSnapshot?.status),
            message: normalizeCell(finalSnapshot?.message),
            guardrailReason,
            madeMaterialProgress,
            candidateFingerprint,
            candidateRowCount: passInspection.candidateRows.length,
            selectedRootCount: Number(passInspection.rootBlockCommit.selectedRootCount || 0),
            validRootCount: Number(passInspection.rootBlockCommit.validRootCount || 0),
            invalidRootCount: Number(passInspection.rootBlockCommit.invalidRootCount || 0),
            unresolvedCount: Number(passInspection.rawUnresolvedCount || 0),
            flowComplete: finalSnapshot?.flowComplete === true || passInspection.rawFlowState?.done === true,
            noProgressCount: Number(segmentStopSnapshot?.runState?.noProgressCount || 0),
            stateVisitCount: Number(segmentStopSnapshot?.runState?.currentStateVisitCount || 0),
            boundaryAutoEndedCount: Number(boundarySettlement?.boundarySettlement?.settledCount || 0),
            boundarySettlement: boundarySettlement?.boundarySettlement || null
          };
          automationSegments.push(segmentRecord);
          this.captureCaseWorkflowRawDataTool88LastRunRecord?.('after-automation-loop-segment', {
            segment: segmentRecord,
            finalSnapshot,
            chosen: {
              label: passInspection.chosen.label,
              stats: passInspection.chosen.stats,
              rows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(passInspection.chosen.rows, 40)
            },
            rawGraphRootBlocks: passInspection.rootBlockCommit.blockSummaries,
            rawGraphCandidateAudit: passInspection.rawGraphCandidateAudit
          });

          if (!passInspection.chosen.rows.length) break;
          if (passInspection.rootBlockCommit.validRootCount > 0) break;

          const bridgeFallbackContinuation = Boolean(passInspection?.bridgeAnnotation?.applied)
            && segmentNumber < maxAutomationSegments
            && passInspection.rootBlockCommit.validRootCount <= 0
            && finalSnapshot?.flowComplete !== true
            && passInspection.rawFlowState?.done !== true;
          const canContinueSamePass = segmentNumber < maxAutomationSegments
            && (
              (safeBudgetGuardrail && madeMaterialProgress && passInspection.rawUnresolvedCount > 0)
              || bridgeFallbackContinuation
            )
            && finalSnapshot?.flowComplete !== true
            && passInspection.rawFlowState?.done !== true;
          if (!canContinueSamePass) break;

          const nextSegmentNumber = segmentNumber + 1;
          const continueMessage = bridgeFallbackContinuation
            ? `Tool 88 marked an unsolved bridge ending for manual review. Continuing the same in-memory root flow in segment ${nextSegmentNumber} of ${maxAutomationSegments} so the remaining paths can still be solved.`
            : `Tool 88 reached its ${guardrailReason.replace(/^max-/, '') || 'segment'} budget while still making progress. Continuing the same in-memory root flow in segment ${nextSegmentNumber} of ${maxAutomationSegments}; ${passInspection.rawUnresolvedCount} ending${passInspection.rawUnresolvedCount === 1 ? '' : 's'} remain.`;
          this.caseAutomationTool88Activity = {
            ...(this.caseAutomationTool88Activity || {}),
            active: true,
            status: 'continuing-pass',
            message: continueMessage,
            detail: continueMessage,
            guardrailMessage: '',
            updatedAtUtc: new Date().toISOString(),
            log: [
              { id: `workflow-tool88-segment-${Date.now()}-${nextSegmentNumber}`, message: continueMessage },
              ...(Array.isArray(this.caseAutomationTool88Activity?.log) ? this.caseAutomationTool88Activity.log : [])
            ].slice(0, 16)
          };
          this.recordCaseWorkflowRawDataTool88RunEvent?.('case-buddy-automation-segment-continuing', {
            status: 'continuing-pass',
            message: continueMessage,
            integrationAction: 'workflow-raw-data-segment-continuation',
            flowComplete: false,
            flowCompletion: passInspection.rawFlowState,
            guardrail: null,
            outputRows: passInspection.candidateRows
          });
          this.pushCaseWorkflowRawTool88Log?.(continueMessage, {
            eventName: 'case-buddy-tool88-segment-continuation',
            key: `tool88-segment-${runKey}-${nextSegmentNumber}`
          });
          previousCandidateFingerprint = candidateFingerprint;
          await Promise.resolve();
        }

        if (!passInspection?.chosen?.rows?.length) {
          throw new Error(finalSnapshot?.message || 'Tool 88 completed without exportable raw-data rows.');
        }

        // Case Buddy may synthesize an unsolved-bridge ending only when the
        // current selected ending itself has explicit bridge evidence. A generic
        // elapsed/step/entry budget is never bridge evidence and must not convert
        // an ordinary BTC trace into a bridge path ending.
        const finalStopReason = normalizeCell(
          automationSegments[automationSegments.length - 1]?.guardrailReason
            || guardrailReasonFor(finalSnapshot)
        ).toLowerCase();
        const budgetOnlyStop = ['max-elapsed', 'max-steps', 'max-entries'].includes(finalStopReason);
        const finalBridgeEvidence = this.caseWorkflowRawDataTool88ExplicitBridgeEvidence(
          finalSnapshot,
          tool,
          passInspection?.candidateRows || passInspection?.chosen?.rows || []
        );
        passInspection = buildPassInspection(finalSnapshot, finalBridgeEvidence.found && !budgetOnlyStop);
        const {
          finalRowsSnapshot,
          chosen,
          bridgeAnnotation,
          rawGraphRepair,
          candidateRows,
          rootBlockCommit,
          exportRows,
          rawGraphCandidateAudit,
          rawGraphAudit,
          rawFlowState,
          rawUnresolvedCount,
          rawUnverifiedOutput
        } = passInspection;
        this.captureCaseWorkflowRawDataTool88LastRunRecord?.('after-automation-loop', {
          finalSnapshot,
          workflowRawDataAutomationSegments: automationSegments
        });
        this.captureCaseWorkflowRawDataTool88LastRunRecord?.('after-row-pick', {
          finalSnapshot,
          finalRowsSnapshot,
          chosen: {
            label: chosen.label,
            stats: chosen.stats,
            rows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(chosen.rows, 80)
          },
          workflowRawDataAutomationSegments: automationSegments
        });
        this.caseWorkflowRawDataTool88LastGraphAudit = rawGraphAudit;
        this.caseWorkflowRawDataTool88VisualRows = candidateRows;
        this.caseHopSolverOutputRows = exportRows;
        this.caseHopSolverOutputTsv = exportRows.map(row => (Array.isArray(row) ? row : []).join('\t')).join('\n');
        this.caseHopSolverSnapshot = finalRowsSnapshot || finalSnapshot;
        this.caseHopSolverLastEvent = 'workflow raw-data Tool 88 headless complete';
        this.caseHopSolverStatus = exportRows.length
          ? `Tool 88 retained ${rootBlockCommit.validRootCount} complete root flow${rootBlockCommit.validRootCount === 1 ? '' : 's'} (${exportRows.length} raw row${exportRows.length === 1 ? '' : 's'}) from this pass.`
          : `Tool 88 did not retain a complete Tool 68-ready root flow from ${chosen.label}.`;
        const rawFlowComplete = bridgeAnnotation.applied
          || (finalRowsSnapshot || finalSnapshot || {}).flowComplete === true
          || finalSnapshot?.flowComplete === true
          || rawFlowState.done === true;
        const passGraphComplete = rootBlockCommit.selectedRootCount > 0
          && rootBlockCommit.validRootCount === rootBlockCommit.selectedRootCount;
        const rootBlockDebug = {
          selectedRootCount: rootBlockCommit.selectedRootCount,
          foundRootCount: rootBlockCommit.foundRootCount,
          validRootCount: rootBlockCommit.validRootCount,
          invalidRootCount: rootBlockCommit.invalidRootCount,
          commitRowCount: rootBlockCommit.commitRowCount,
          unassignedEntryCount: rootBlockCommit.unassignedEntryCount,
          blocks: rootBlockCommit.blockSummaries,
          invalidRoots: rootBlockCommit.invalidRoots,
          unassignedEntries: rootBlockCommit.unassignedEntries,
          automationSegmentCount: automationSegments.length,
          automationSegments
        };

        if (!exportRows.length || rawGraphAudit.blockingIssueCount > 0) {
          const firstRejectedRoot = rootBlockCommit.invalidRoots?.[0] || null;
          const firstIssue = firstRejectedRoot?.examples?.[0]?.message
            || rawGraphCandidateAudit.examples?.[0]?.message
            || 'Tool 88 did not retain a complete Tool 68-ready root flow.';
          const lastAutomationSegment = automationSegments.length
            ? automationSegments[automationSegments.length - 1]
            : null;
          const continuationBudgetExhausted = automationSegments.length >= maxAutomationSegments
            && ['max-elapsed', 'max-steps', 'max-entries'].includes(lastAutomationSegment?.guardrailReason || '')
            && Number(lastAutomationSegment?.unresolvedCount || rawUnresolvedCount || 0) > 0;
          const remainingEndingCount = Number(lastAutomationSegment?.unresolvedCount || rawUnresolvedCount || 0);
          const solverStopDetail = normalizeCell(
            lastAutomationSegment?.message
              || finalSnapshot?.guardrail?.message
              || finalSnapshot?.message
          );
          const solverStopIsGenericGraphMessage = /stopped before writing malformed raw data|no complete starting-root block was safe to save/i.test(solverStopDetail);
          const message = continuationBudgetExhausted
            ? `Tool 88 preserved this in-memory root flow across ${automationSegments.length} automation segments, but no complete Tool 68-ready starting-root block was available yet. ${remainingEndingCount} ending${remainingEndingCount === 1 ? '' : 's'} remain unresolved. ${firstIssue}`
            : (solverStopDetail && !solverStopIsGenericGraphMessage
              ? `Tool 88 stopped before this starting-root flow was complete. ${solverStopDetail} Remaining graph issue: ${firstIssue}`
              : `Tool 88 stopped before writing malformed Raw Data. No complete starting-root block was safe to save from this pass. ${firstIssue}`);
          finalSnapshot = {
            ...(finalRowsSnapshot || finalSnapshot || {}),
            status: continuationBudgetExhausted ? 'raw-data-continuation-exhausted' : 'raw-data-graph-invalid',
            message,
            guardrail: {
              reason: continuationBudgetExhausted ? 'raw-data-continuation-exhausted' : 'raw-graph-integrity',
              message
            },
            outputRows: [],
            rowsAppended: 0,
            flowComplete: false,
            flowCompletion: rawFlowState,
            solverFlowCompletion: rawFlowState,
            rawGraphAudit,
            rawGraphCandidateAudit,
            rawGraphRootBlocks: rootBlockDebug,
            rawGraphRepair: {
              changed: Boolean(rawGraphRepair.changed),
              repairedContinuationCount: Number(rawGraphRepair.repairedContinuationCount || 0),
              removedExactDuplicateCount: Number(rawGraphRepair.removedExactDuplicateCount || 0),
              removedExactDuplicates: Array.isArray(rawGraphRepair.removedExactDuplicates) ? rawGraphRepair.removedExactDuplicates.slice(0, 24) : [],
              removedStableBranchDuplicateCount: Number(rawGraphRepair.removedStableBranchDuplicateCount || 0),
              removedStableBranchDuplicates: Array.isArray(rawGraphRepair.removedStableBranchDuplicates) ? rawGraphRepair.removedStableBranchDuplicates.slice(0, 24) : [],
              repairs: Array.isArray(rawGraphRepair.repairs) ? rawGraphRepair.repairs.slice(0, 24) : []
            },
            workflowRawDataCandidateRowCount: candidateRows.length,
            workflowRawDataActiveRoot: activeRoot ? { ...activeRoot } : null,
            workflowRawDataSelectedInputRoots: selectedInputRoots.map(root => ({ ...root })),
            workflowRawDataIncomplete: true,
            workflowRawDataPassIncomplete: true,
            workflowRawDataCoverageComplete: false,
            workflowRawDataPartialCommit: false,
            workflowRawDataCommittedRootCount: 0,
            workflowRawDataRejectedRootCount: rootBlockCommit.invalidRootCount,
            workflowRawDataAutomationSegments: automationSegments,
            workflowRawDataUnsolvedBridgeAccepted: false,
            workflowRawDataUnsolvedBridgeRowIndex: -1,
            workflowRawDataUnsolvedBridgeNote: ''
          };
          this.caseWorkflowRawDataTool88LastError = message;
          this.updateCaseAutomationTool88ActivityFromSnapshot('case-buddy-guardrail', finalSnapshot);
          this.captureCaseWorkflowRawDataTool88LastRunRecord?.('raw-graph-invalid', {
            finalSnapshot,
            finalRowsSnapshot,
            chosen: {
              label: chosen.label,
              stats: chosen.stats,
              rows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(chosen.rows, 80)
            },
            bridgeAnnotation,
            rawGraphAudit,
            rawGraphCandidateAudit,
            rawGraphRootBlocks: rootBlockDebug,
            rawGraphRepair,
            candidateRows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(candidateRows, 80),
            exportRows: []
          });
          return finalSnapshot;
        }

        // A Tool 88 pass can legitimately end because of elapsed-time or loop
        // guardrails after earlier starting roots are already structurally complete.
        // Save only those complete root blocks now. The outer coverage loop will
        // select the remaining roots for a fresh pass instead of discarding all work.
        const appendResult = await this.appendCaseWorkflowRawDataTool88RowsToRawData(exportRows, {
          reason: bridgeAnnotation.applied
            ? `Workflow Auto saved ${rootBlockCommit.validRootCount} complete Tool 88 root flow${rootBlockCommit.validRootCount === 1 ? '' : 's'} (${exportRows.length} raw row${exportRows.length === 1 ? '' : 's'}) and marked an unsolved bridge ending for manual review.`
            : `Workflow Auto saved ${rootBlockCommit.validRootCount} complete Tool 88 root flow${rootBlockCommit.validRootCount === 1 ? '' : 's'} (${exportRows.length} raw row${exportRows.length === 1 ? '' : 's'}).`
        });
        this.captureCaseWorkflowRawDataTool88LastRunRecord?.('after-raw-append', {
          finalSnapshot,
          finalRowsSnapshot,
          chosen: {
            label: chosen.label,
            stats: chosen.stats,
            rows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(chosen.rows, 80)
          },
          bridgeAnnotation,
          rawGraphAudit,
          rawGraphCandidateAudit,
          rawGraphRootBlocks: rootBlockDebug,
          rawGraphRepair,
          appendResult,
          candidateRows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(candidateRows, 80),
          exportRows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(exportRows, 80)
        });
        const coverageAfterAppend = appendResult?.coverage || this.getCaseWorkflowRawDataCoverageSummary();
        const globalCoverageComplete = coverageAfterAppend?.isComplete === true;
        if (globalCoverageComplete && typeof this.recordCaseWorkflowTool88RawDataCompletion === 'function') {
          this.recordCaseWorkflowTool88RawDataCompletion({
            ...appendResult,
            outputRowCount: exportRows.length,
            flowComplete: true,
            unresolvedBridgeAccepted: bridgeAnnotation.applied
          }, this.getCaseWorkflowRawDataFetchStep?.());
        }
        const completedFlow = bridgeAnnotation.applied
          ? this.caseWorkflowRawDataTool88CompletionForBridgeFallback(finalRowsSnapshot || finalSnapshot || {}, bridgeAnnotation, {
              ...appendResult,
              coverage: coverageAfterAppend
            })
          : {
              ...((finalRowsSnapshot || finalSnapshot || {}).flowCompletion || finalSnapshot?.flowCompletion || {}),
              done: globalCoverageComplete,
              totalEntries: Number(coverageAfterAppend?.totalRows || 0),
              completeCount: Number(coverageAfterAppend?.coveredCount || 0),
              incompleteCount: Number(coverageAfterAppend?.missingCount || 0),
              summary: globalCoverageComplete
                ? 'All distinct starting roots have raw-data coverage.'
                : `${Number(coverageAfterAppend?.missingCount || 0)} distinct starting root${Number(coverageAfterAppend?.missingCount || 0) === 1 ? '' : 's'} still need raw data.`
            };
        const remainingRoots = (Array.isArray(coverageAfterAppend?.missingRows) ? coverageAfterAppend.missingRows : []).map(row => ({
          id: row?.id || '',
          rootInstanceKey: row?.rootInstanceKey || '',
          rowIndex: Number(row?.rowIndex),
          spreadsheetRowNumber: row?.spreadsheetRowNumber || '',
          pathLabel: row?.pathLabel || '',
          hash: row?.hash || row?.transactionHash || '',
          toAddress: row?.toAddress || '',
          duplicateHash: Boolean(row?.duplicateHash)
        }));
        finalSnapshot = {
          ...(finalRowsSnapshot || finalSnapshot || {}),
          status: globalCoverageComplete
            ? (bridgeAnnotation.applied ? 'raw-data-appended-bridge-review' : 'flow-complete')
            : 'raw-data-partial-saved',
          message: globalCoverageComplete
            ? (bridgeAnnotation.applied
              ? `Tool 88 saved ${rootBlockCommit.validRootCount} completed root flow${rootBlockCommit.validRootCount === 1 ? '' : 's'} (${appendResult.appendedCount} raw row${appendResult.appendedCount === 1 ? '' : 's'}), completed distinct root coverage, and marked an unsolved bridge ending for manual review.`
              : `Tool 88 saved ${rootBlockCommit.validRootCount} completed root flow${rootBlockCommit.validRootCount === 1 ? '' : 's'} (${appendResult.appendedCount} raw row${appendResult.appendedCount === 1 ? '' : 's'}) and completed distinct root coverage.`)
            : `Tool 88 saved ${rootBlockCommit.validRootCount} completed root flow${rootBlockCommit.validRootCount === 1 ? '' : 's'} (${appendResult.appendedCount} raw row${appendResult.appendedCount === 1 ? '' : 's'}); ${Number(coverageAfterAppend?.missingCount || 0)} distinct starting root${Number(coverageAfterAppend?.missingCount || 0) === 1 ? '' : 's'} remain for a fresh pass.`,
          // The underlying Tool 88 loop may have ended on an elapsed-time or
          // no-progress guardrail. Once complete root blocks were safely saved,
          // that solver-local guardrail must not pause the outer coverage loop.
          guardrail: null,
          outputRows: exportRows,
          rowsAppended: appendResult.appendedCount,
          rowsReplaced: Number(appendResult.replacedCount || 0),
          flowComplete: globalCoverageComplete,
          flowCompletion: completedFlow,
          solverFlowCompletion: rawFlowState,
          workflowRawDataPassFlowComplete: passGraphComplete,
          workflowRawDataSolverFlowComplete: rawFlowComplete,
          workflowRawDataSolverUnresolvedCount: rawUnresolvedCount,
          workflowRawDataSolverUnverifiedOutput: rawUnverifiedOutput,
          rawGraphAudit,
          rawGraphCandidateAudit,
          rawGraphRootBlocks: rootBlockDebug,
          rawGraphRepair: {
            changed: Boolean(rawGraphRepair.changed),
            repairedContinuationCount: Number(rawGraphRepair.repairedContinuationCount || 0),
            removedExactDuplicateCount: Number(rawGraphRepair.removedExactDuplicateCount || 0),
            removedStableBranchDuplicateCount: Number(rawGraphRepair.removedStableBranchDuplicateCount || 0),
            repairs: Array.isArray(rawGraphRepair.repairs) ? rawGraphRepair.repairs.slice(0, 24) : []
          },
          workflowRawDataCandidateRowCount: candidateRows.length,
          workflowRawDataActiveRoot: activeRoot ? { ...activeRoot } : null,
          workflowRawDataSelectedInputRoots: selectedInputRoots.map(root => ({ ...root })),
          workflowRawDataCoverageComplete: globalCoverageComplete,
          workflowRawDataCoverage: {
            totalRows: Number(coverageAfterAppend?.totalRows || 0),
            coveredCount: Number(coverageAfterAppend?.coveredCount || 0),
            missingCount: Number(coverageAfterAppend?.missingCount || 0),
            missingRows: remainingRoots,
            duplicateHashGroups: coverageAfterAppend?.coverage?.duplicateHashGroups || coverageAfterAppend?.duplicateHashGroups || []
          },
          workflowRawDataIncomplete: !globalCoverageComplete,
          workflowRawDataPassIncomplete: !passGraphComplete,
          workflowRawDataPartialCommit: !globalCoverageComplete || rootBlockCommit.invalidRootCount > 0,
          workflowRawDataCommittedRootCount: rootBlockCommit.validRootCount,
          workflowRawDataRejectedRootCount: rootBlockCommit.invalidRootCount,
          workflowRawDataAutomationSegments: automationSegments,
          workflowRawDataUnsolvedBridgeAccepted: bridgeAnnotation.applied,
          workflowRawDataUnsolvedBridgeRowIndex: bridgeAnnotation.rowIndex,
          workflowRawDataUnsolvedBridgeNote: bridgeAnnotation.note || ''
        };
        const tool88CompletionEventName = finalSnapshot.flowComplete === true
          ? 'case-buddy-flow-complete'
          : (finalSnapshot.workflowRawDataPartialCommit === true
            ? 'case-buddy-result'
            : (finalSnapshot.guardrail || finalSnapshot.workflowRawDataIncomplete ? 'case-buddy-guardrail' : 'case-buddy-result'));
        this.updateCaseAutomationTool88ActivityFromSnapshot(tool88CompletionEventName, finalSnapshot);
        this.captureCaseWorkflowRawDataTool88LastRunRecord?.('completed', {
          finalSnapshot,
          finalRowsSnapshot,
          chosen: {
            label: chosen.label,
            stats: chosen.stats,
            rows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(chosen.rows, 80)
          },
          bridgeAnnotation,
          rawGraphAudit,
          rawGraphCandidateAudit,
          rawGraphRootBlocks: rootBlockDebug,
          rawGraphRepair,
          appendResult,
          candidateRows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(candidateRows, 80),
          exportRows: this.caseWorkflowRawDataTool88SummarizeRowsForDebug(exportRows, 80)
        });
        this.suppressCaseWorkflowRawDataLegacyPauseDuringTool88(finalSnapshot.message || 'Tool 88 raw-data automation completed.');
        return finalSnapshot;
      } catch (error) {
        const message = error?.message || 'Tool 88 raw-data automation failed.';
        this.caseWorkflowRawDataTool88LastError = message;
        this.caseAutomationTool88Activity = {
          ...(this.caseAutomationTool88Activity || {}),
          active: false,
          guardrailMessage: message,
          message,
          detail: message,
          updatedAtUtc: new Date().toISOString()
        };
        this.captureCaseWorkflowRawDataTool88LastRunRecord?.('error', { error: message, finalSnapshot });
        throw error;
      } finally {
        this.caseWorkflowRawDataTool88Running = false;
        this.caseHopSolverAutomationRunning = false;
        this.caseAutomationTool88Progress = {
          ...(this.caseAutomationTool88Progress || {}),
          active: false,
          updatedAtUtc: new Date().toISOString()
        };
      }
    },

    async runCaseHopSolverAutomationLoop(options = {}) {
      if (this.shouldUseCaseWorkflowRawDataTool88Headless(options)) {
        return this.runCaseWorkflowRawDataTool88Headless({ ...options, source: options.source || 'runCaseHopSolverAutomationLoop' });
      }
      return this.callCaseBuddyOriginalMethod(caseBuddyHopSolverMethods, 'runCaseHopSolverAutomationLoop', [options]);
    },

    async runCaseHopSolverNextAction(options = {}) {
      if (this.shouldUseCaseWorkflowRawDataTool88Headless(options)) {
        return this.runCaseWorkflowRawDataTool88Headless({ ...options, source: options.source || 'runCaseHopSolverNextAction' });
      }
      return this.callCaseBuddyOriginalMethod(caseBuddyHopSolverMethods, 'runCaseHopSolverNextAction', [options]);
    },

    async loadCaseHopSolverSelectedCandidate(options = {}) {
      if (this.shouldUseCaseWorkflowRawDataTool88Headless(options)) {
        return this.runCaseWorkflowRawDataTool88Headless({ ...options, source: options.source || 'loadCaseHopSolverSelectedCandidate' });
      }
      const selectedCandidate = options.candidate
        || this.caseHopSolverCandidate
        || this.caseManualStartHopInspectorSelectedCandidate
        || this.caseManualStartHopInspectorCandidate
        || this.caseInvestigationVisualizerSelectedCandidate
        || {};
      const savedSettings = this.caseManualStartReadTool88Settings?.() || {};
      const traceDirection = this.caseManualNormalizeText(
        options.traceDirection || this.caseManualStartOutputChoiceDirectionForCandidate(selectedCandidate) || savedSettings.traceDirection || 'forward'
      ).toLowerCase() === 'reverse' ? 'reverse' : 'forward';
      const hopLogicType = this.caseManualNormalizeText(
        options.hopLogicType || options.hopLogic || selectedCandidate.hopLogic || savedSettings.hopLogicType || 'FIFO'
      ).toUpperCase();
      const effectiveOptions = {
        ...options,
        traceDirection,
        hopLogicType: ['FIFO', 'LIFO', 'NEXT_CHRONOLOGICAL'].includes(hopLogicType) ? hopLogicType : 'FIFO',
      };
      const tool = this.getCaseHopSolverTool88Ref?.();
      if (tool?.applyCaseBuddyPreferredSettings) {
        tool.applyCaseBuddyPreferredSettings({ ...savedSettings, ...effectiveOptions });
      }
      return this.callCaseBuddyOriginalMethod(caseBuddyHopSolverMethods, 'loadCaseHopSolverSelectedCandidate', [effectiveOptions]);
    },

    async appendCaseHopSolverOutputToRawData(options = {}) {
      if (this.shouldUseCaseWorkflowRawDataTool88Headless(options) && Array.isArray(this.caseWorkflowRawDataTool88LastRows) && this.caseWorkflowRawDataTool88LastRows.length) {
        return this.appendCaseWorkflowRawDataTool88RowsToRawData(this.caseWorkflowRawDataTool88LastRows, options);
      }
      return this.callCaseBuddyOriginalMethod(caseBuddyHopSolverMethods, 'appendCaseHopSolverOutputToRawData', [options]);
    },

    stopCaseAutomation(message = 'Automation stopped.') {
      if (this.caseWorkflowRawDataTool88ShouldSuppressStop(message)) {
        this.caseAutomation = {
          ...(this.caseAutomation || {}),
          active: true,
          busy: false,
          paused: false,
          manualPause: null,
          status: 'Continuing after Tool 88 raw data',
          detail: 'Ignored a stale stop/review signal after Tool 88 loaded Raw Data; workflow Auto is continuing.'
        };
        this.scheduleCaseWorkflowRawDataTool88AutoContinue([], {
          source: 'suppressed-stale-stop',
          reason: 'Suppressed stale workflow stop after Tool 88 raw-data loading.',
          delayMs: 180
        });
        return { handled: true, suppressed: true, reason: 'tool88-raw-data-auto-continue' };
      }
      const stopMessageText = normalizeCell(message).toLowerCase();
      const userInitiatedStop = /user|stop auto|stopped at manual pause|case buddy stop auto/.test(stopMessageText);
      const restoreSimpleLaunchAfterStop = userInitiatedStop && Boolean(
        this.caseSimpleLaunchAutoRequested
        || this.caseSimpleLaunchAutoStarting
        || this.caseSimpleLaunchMinimized
      );
      if (userInitiatedStop) {
        this.cancelCaseWorkflowBackgroundAutomationResume?.({ source: 'stop-case-automation' });
      }
      const compactTool88 = this.getCaseManualStartHopInspectorTool?.();
      if (compactTool88 && typeof compactTool88.caseBuddyRequestAutomationStop === 'function') {
        try {
          compactTool88.caseBuddyRequestAutomationStop('Case Buddy Stop Auto was pressed.');
        } catch (error) {
          console.warn('Could not forward Case Buddy Stop Auto request to compact embedded Tool 88.', error);
        }
      }
      const tool88 = this.$refs.caseHopSolverTool88;
      if (tool88 && typeof tool88.caseBuddyRequestAutomationStop === 'function') {
        try {
          tool88.caseBuddyRequestAutomationStop('Case Buddy Stop Auto was pressed.');
        } catch (error) {
          console.warn('Could not forward Case Buddy Stop Auto request to embedded Tool 88.', error);
        }
      }
      const workflowRawTool88 = this.$refs.caseWorkflowRawDataTool88;
      if (workflowRawTool88 && typeof workflowRawTool88.caseBuddyRequestAutomationStop === 'function') {
        try {
          workflowRawTool88.caseBuddyRequestAutomationStop('Case Buddy Stop Auto was pressed.');
        } catch (error) {
          console.warn('Could not forward Case Buddy Stop Auto request to hidden workflow Tool 88.', error);
        }
      }
      if (this.caseHopSolverRunning && typeof this.requestCaseHopSolverStop === 'function') {
        try {
          this.requestCaseHopSolverStop();
        } catch (error) {
          console.warn('Could not request Case Buddy hop solver stop.', error);
        }
      }
      const stopResult = this.callCaseBuddyOriginalMethod(caseBuddyAutomationMethods, 'stopCaseAutomation', [message]);
      if (restoreSimpleLaunchAfterStop) {
        this.caseSimpleLaunchAutoRequested = false;
        this.caseSimpleLaunchAutoStarting = false;
        this.caseSimpleLaunchMinimized = false;
        this.caseSimpleLaunchDismissed = false;
        this.caseSimpleLaunchStatus = 'Auto stopped. Simple View is still open so you can review progress or restart Auto.';
        this.recordCaseSimpleLaunchAutoStartAudit?.({
          stage: 'user-stopped-auto-restored-simple-view',
          message: normalizeCell(message)
        });
      }
      return stopResult;
    },
  }
};
</script>


<style scoped>

.spreadsheet-tools-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  margin: 0.28rem 0 0.4rem;
  border: 1px solid rgba(148, 163, 184, 0.42);
  border-radius: 14px;
  background:
    linear-gradient(180deg, rgba(248, 250, 252, 0.98), rgba(241, 245, 249, 0.96));
  color: #0f172a;
  padding: 0.52rem 0.68rem;
  text-align: left;
  cursor: pointer;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72), 0 1px 2px rgba(15, 23, 42, 0.06);
  transition:
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.16s ease;
}

.spreadsheet-tools-toggle:hover {
  border-color: rgba(37, 99, 235, 0.44);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.82), 0 6px 16px rgba(37, 99, 235, 0.12);
}

.spreadsheet-tools-toggle:focus-visible {
  outline: 3px solid rgba(96, 165, 250, 0.34);
  outline-offset: 2px;
}

.spreadsheet-tools-toggle__chevron {
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.82rem;
  font-weight: 900;
}

.spreadsheet-tools-toggle__text {
  display: grid;
  gap: 0.06rem;
  min-width: 0;
  flex: 1 1 auto;
}

.spreadsheet-tools-toggle__text strong {
  color: #0f172a;
  font-size: 0.8rem;
  line-height: 1.15;
}

.spreadsheet-tools-toggle__text small {
  overflow: hidden;
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spreadsheet-tools-toggle__hint {
  flex: 0 0 auto;
  border: 1px solid rgba(37, 99, 235, 0.2);
  border-radius: 999px;
  background: #ffffff;
  color: #1d4ed8;
  padding: 0.22rem 0.52rem;
  font-size: 0.68rem;
  font-weight: 900;
}

.spreadsheet-tools-expanded {
  display: contents;
}

.spreadsheet-tools-end-controls {
  display: grid;
  flex: 0 0 auto;
  align-self: center;
  gap: 0.28rem;
  margin-left: auto;
}

.spreadsheet-tools-end-controls .autosave-toggle {
  margin: 0;
}

.spreadsheet-tools-hide-btn {
  width: 100%;
  justify-content: center;
  white-space: nowrap;
}

@media (max-width: 720px) {
  .spreadsheet-tools-toggle {
    align-items: flex-start;
  }

  .spreadsheet-tools-toggle__text small {
    white-space: normal;
  }
}

.case-title-group {
  min-width: 0;
}

.case-title-inline {
  max-width: min(100%, clamp(18rem, 46vw, 52rem));
  min-width: 0;
}

.case-title-inline__display,
.case-title-inline__editor-heading {
  display: block;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-title-inline__display {
  cursor: text;
}

.case-title-inline__display:focus-visible {
  outline: 2px solid rgba(59, 130, 246, 0.45);
  outline-offset: 4px;
  border-radius: 8px;
}

.case-title-inline__input {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  border: 0;
  border-radius: 8px;
  padding: 0;
  margin: 0;
  background: transparent;
  box-shadow: inset 0 -2px 0 rgba(37, 99, 235, 0.32);
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  line-height: inherit;
  outline: none;
}

.case-title-inline__input:focus {
  box-shadow: inset 0 -2px 0 rgba(37, 99, 235, 0.65);
}

@media (max-width: 920px) {
  .case-title-inline {
    max-width: 100%;
  }
}

.case-workflow-tool88-raw-host {
  position: fixed;
  left: -10000px;
  top: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.case-workflow-tool88-headless-host {
  position: fixed;
  left: -10000px;
  top: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  contain: layout style paint;
}

.case-workflow-tool88-headless-host :deep(.tool88-page) {
  width: 1280px;
  min-width: 1280px;
}

.case-automation-tool88-debug-card {
  display: grid;
  gap: 0.75rem;
  padding: 0.85rem 0.95rem;
  border: 1px solid rgba(37, 99, 235, 0.18);
  border-radius: 16px;
  background: rgba(239, 246, 255, 0.9);
}

.case-automation-tool88-debug-card--workflow {
  margin-top: 0.85rem;
}

.case-automation-tool88-debug-card > div:first-child {
  display: grid;
  gap: 0.2rem;
}

.case-automation-tool88-debug-card span {
  color: #1d4ed8;
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.case-automation-tool88-debug-card strong {
  color: #1e3a8a;
  font-size: 0.9rem;
}

.case-automation-tool88-debug-card small {
  color: #475569;
  line-height: 1.35;
}

.case-automation-tool88-debug-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.case-automation-tool88-debug-card__chips span {
  display: inline-flex;
  gap: 0.35rem;
  align-items: center;
  padding: 0.34rem 0.5rem;
  border: 1px solid rgba(37, 99, 235, 0.16);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  color: #334155;
  font-size: 0.72rem;
  letter-spacing: 0;
  text-transform: none;
}

.case-automation-tool88-debug-card__chips strong {
  color: #1d4ed8;
  font-size: 0.68rem;
}

.workflow-fast-complete-card {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: center;
  padding: 0.82rem 0.9rem;
  border: 1px solid #bbf7d0;
  border-radius: 16px;
  background: #f0fdf4;
  color: #14532d;
}

.workflow-fast-complete-card div {
  display: grid;
  gap: 0.12rem;
  min-width: min(100%, 26rem);
}

.workflow-fast-complete-card span {
  color: #15803d;
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.workflow-fast-complete-card strong {
  color: #14532d;
  font-size: 0.88rem;
}

.workflow-fast-complete-card small {
  color: #166534;
  font-size: 0.72rem;
  line-height: 1.35;
}

.case-details-review-ack-modal {
  display: grid;
  gap: 0.72rem;
  width: min(520px, calc(100vw - 2rem));
}

.case-details-review-ack-modal__notice {
  display: grid;
  gap: 0.24rem;
  padding: 0.78rem 0.86rem;
  border: 1px solid #fde68a;
  border-radius: 14px;
  background: #fffbeb;
  color: #92400e;
  line-height: 1.4;
}

.case-details-review-ack-modal__notice strong {
  color: #78350f;
}

.case-details-review-ack-modal__checkbox {
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  padding: 0.72rem 0.82rem;
  border: 1px solid #bbf7d0;
  border-radius: 14px;
  background: #f0fdf4;
  color: #14532d;
  font-weight: 800;
  line-height: 1.35;
}

.case-details-review-ack-modal__checkbox input {
  margin-top: 0.12rem;
}

.case-manual-start-hop-inspector {
  position: absolute;
  z-index: 12;
  width: min(440px, calc(100% - 36px));
  max-height: calc(100% - 36px);
  overflow: auto;
  border: 1px solid rgba(15, 23, 42, 0.18);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
  padding: 16px;
  backdrop-filter: blur(10px);
  cursor: default;
  touch-action: auto;
  overscroll-behavior: contain;
}

.case-manual-start-hop-inspector.is-dragging {
  cursor: grabbing;
  user-select: none;
}

.case-manual-start-hop-inspector__drag-handle {
  cursor: grab;
  user-select: none;
  touch-action: none;
}

.case-manual-start-hop-inspector.is-dragging .case-manual-start-hop-inspector__drag-handle {
  cursor: grabbing;
}

.case-manual-start-hop-inspector__header-actions,
.case-manual-start-hop-inspector__header-actions * {
  cursor: auto;
}

.case-manual-start-hop-inspector__header-actions .toolbar-btn:not(:disabled) {
  cursor: pointer;
}

.case-manual-start-hop-inspector__header-actions .toolbar-btn:disabled {
  cursor: not-allowed;
}

.case-manual-start-hop-inspector__head,
.case-manual-start-hop-inspector__header,
.case-manual-start-hop-inspector__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.case-manual-start-hop-inspector__head,
.case-manual-start-hop-inspector__header {
  justify-content: space-between;
  margin-bottom: 10px;
}

.case-manual-start-hop-inspector__head h4,
.case-manual-start-hop-inspector__header strong {
  margin: 0;
  font-size: 1rem;
}

.case-manual-start-hop-inspector__head small,
.case-manual-start-hop-inspector__header small {
  display: block;
  color: #64748b;
  margin-top: 2px;
}

.case-manual-start-hop-inspector__header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.case-manual-start-hop-inspector__busy-notice {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  margin: 8px 0 12px;
  padding: 10px 11px;
  border: 1px solid rgba(59, 130, 246, 0.24);
  border-radius: 14px;
  background: rgba(239, 246, 255, 0.78);
  color: #1e3a8a;
}

.case-manual-start-hop-inspector__busy-notice strong,
.case-manual-start-hop-inspector__busy-notice small {
  display: block;
}

.case-manual-start-hop-inspector__busy-notice small {
  margin-top: 2px;
  color: #334155;
  line-height: 1.35;
}

.case-manual-start-hop-inspector__busy-spinner {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 3px solid rgba(59, 130, 246, 0.2);
  border-top-color: rgba(59, 130, 246, 0.88);
  animation: case-manual-hop-loading-spin 0.9s linear infinite;
}

.case-manual-start-hop-inspector__status {
  margin: 8px 0 12px;
  color: #334155;
  line-height: 1.4;
}

.case-manual-start-hop-inspector__summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.case-manual-start-hop-inspector__summary div {
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 12px;
  padding: 8px;
  background: rgba(248, 250, 252, 0.9);
}

.case-manual-start-hop-inspector__summary span {
  display: block;
  font-size: 0.72rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.case-manual-start-hop-inspector__summary strong {
  display: block;
  margin-top: 2px;
  color: #0f172a;
  overflow-wrap: anywhere;
}

.case-manual-start-hop-inspector__summary small {
  display: block;
  margin-top: 4px;
  color: #64748b;
  line-height: 1.25;
}

.case-manual-start-hop-inspector__summary--simplified {
  grid-template-columns: minmax(0, 1fr) minmax(112px, max-content);
  align-items: start;
}

.case-manual-start-hop-inspector__recommendation-card {
  width: 100%;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 12px;
  padding: 8px;
  background: rgba(248, 250, 252, 0.9);
  text-align: left;
  font: inherit;
  cursor: default;
}

.case-manual-start-hop-inspector__recommendation-card:not(:disabled) {
  cursor: pointer;
  border-color: rgba(59, 130, 246, 0.34);
  background: rgba(239, 246, 255, 0.9);
  box-shadow: 0 0 0 2px rgba(191, 219, 254, 0.55);
}

.case-manual-start-hop-inspector__recommendation-card:not(:disabled):hover {
  border-color: rgba(37, 99, 235, 0.58);
  box-shadow: 0 0 0 3px rgba(191, 219, 254, 0.78), 0 10px 24px rgba(15, 23, 42, 0.08);
}

.case-manual-start-hop-inspector__recommendation-card:disabled {
  color: inherit;
}

.case-manual-start-hop-inspector__check-card {
  width: max-content;
  min-width: 112px;
  max-width: 150px;
  justify-self: end;
  align-self: start;
}

.case-manual-start-hop-inspector__check-card strong {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.case-manual-start-hop-inspector__check-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.case-manual-start-hop-inspector__check-dot {
  width: 0.62rem;
  height: 0.62rem;
  flex: 0 0 auto;
  border-radius: 999px;
  display: inline-block;
  background: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
}

.case-manual-start-hop-inspector__check-dot.is-needed {
  background: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
}

.case-manual-start-hop-inspector__check-dot.is-running {
  background: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.16);
  animation: case-manual-hop-check-pulse 1.1s ease-in-out infinite;
}

.case-manual-start-hop-inspector__check-dot.is-ready {
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.14);
}

.case-manual-start-completion-effect__actions {
  display: flex;
  justify-content: center;
  margin-top: 0.65rem;
}

.case-manual-start-completion-effect__actions .toolbar-btn {
  min-width: 13.5rem;
}

.case-manual-start-completion-effect__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.7rem;
  height: 1.7rem;
  min-width: 1.7rem;
  min-height: 1.7rem;
  padding: 0;
  line-height: 1;
  text-align: center;
  appearance: none;
}

@keyframes case-manual-hop-check-pulse {
  0%, 100% {
    opacity: 0.58;
    transform: scale(0.92);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

@media (max-width: 360px) {
  .case-manual-start-hop-inspector__summary--simplified {
    grid-template-columns: 1fr;
  }

  .case-manual-start-hop-inspector__check-card {
    width: auto;
    max-width: none;
    justify-self: stretch;
  }
}

.case-manual-start-hop-inspector__actions {
  justify-content: flex-start;
}

.case-manual-start-hop-inspector__recommended-action {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-color: rgba(34, 197, 94, 0.92) !important;
  box-shadow:
    0 0 0 2px rgba(187, 247, 208, 0.72),
    0 8px 20px rgba(22, 163, 74, 0.18) !important;
}

.case-manual-start-hop-inspector__recommended-action::before {
  content: '';
  position: absolute;
  inset: -260%;
  z-index: 0;
  background: conic-gradient(from 0deg, rgba(255,255,255,0), rgba(255,255,255,0.22), rgba(187,247,208,0.42), rgba(255,255,255,0.12), rgba(34,197,94,0.2), rgba(255,255,255,0));
  opacity: 0.3;
  filter: blur(8px);
  transform-origin: center;
  animation: case-manual-start-recommended-shine 4.8s linear infinite;
}

.case-manual-start-hop-inspector__recommended-action > span {
  position: relative;
  z-index: 1;
}

.case-manual-start-hop-inspector__recommended-action:disabled::before {
  opacity: 0.28;
}

@keyframes case-manual-start-recommended-shine {
  to {
    transform: rotate(360deg);
  }
}

.case-manual-start-hop-inspector__preview {
  margin-top: 14px;
  border: 1px solid rgba(59, 130, 246, 0.26);
  border-radius: 16px;
  background: rgba(239, 246, 255, 0.72);
  padding: 12px;
}

.case-manual-start-hop-inspector__preview-header,
.case-manual-start-hop-inspector__preview-header-actions,
.case-manual-start-hop-inspector__preview-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.case-manual-start-hop-inspector__preview-header {
  justify-content: space-between;
}

.case-manual-start-hop-inspector__preview-header strong,
.case-manual-start-hop-inspector__preview-header small {
  display: block;
}

.case-manual-start-hop-inspector__preview-header strong {
  color: #0f172a;
}

.case-manual-start-hop-inspector__preview-header small {
  color: #475569;
  margin-top: 2px;
}

.case-manual-start-hop-inspector__preview-header--compact {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 6px;
  flex-wrap: nowrap;
  padding-bottom: 5px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.58);
}

.case-manual-start-hop-inspector__preview-headline {
  min-width: 0;
}

.case-manual-start-hop-inspector__preview-headline strong {
  display: block;
  overflow: hidden;
  color: #0f172a;
  font-size: 0.82rem;
  line-height: 1.14;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-start-hop-inspector__preview-headline small {
  display: block;
  overflow: hidden;
  margin-top: 1px;
  color: #64748b;
  font-size: 0.68rem;
  line-height: 1.18;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-start-hop-inspector__preview-header--compact .case-manual-start-hop-inspector__preview-header-actions {
  flex: 0 0 auto;
  flex-wrap: nowrap;
  align-self: start;
  justify-content: flex-end;
  gap: 4px;
  margin-left: 0;
}

.case-manual-start-hop-inspector__preview-header--compact .toolbar-btn--icon {
  width: 24px;
  min-width: 24px;
  height: 24px;
  min-height: 24px;
  padding: 0;
  font-size: 0.92rem;
}

.case-manual-start-hop-inspector__return-dot {
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  padding: 0;
  border: 1px solid rgba(37, 99, 235, 0.35);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #2563eb;
  font-size: 0.78rem;
  line-height: 1;
  cursor: pointer;
}

.case-manual-start-hop-inspector__return-dot:hover {
  border-color: rgba(37, 99, 235, 0.75);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.case-manual-start-hop-inspector__preview-status {
  margin: 8px 0;
  color: #334155;
  line-height: 1.4;
}

.case-manual-start-hop-inspector__preview-scroll {
  display: grid;
  flex: 1 1 auto;
  align-content: start;
  gap: 5px;
  min-height: 0;
  overflow: auto;
  padding-right: 4px;
  scrollbar-gutter: stable;
}

.case-manual-start-hop-inspector__preview-evidence {
  margin: 6px 0 8px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.75);
  overflow: hidden;
}

.case-manual-start-hop-inspector__preview-evidence--after-list {
  flex: 0 0 auto;
  margin: 4px 0 0;
}

.case-manual-start-hop-inspector__preview-evidence summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 26px;
  padding: 4px 7px;
  color: #1e293b;
  cursor: pointer;
  list-style: none;
}

.case-manual-start-hop-inspector__preview-evidence summary::-webkit-details-marker {
  display: none;
}

.case-manual-start-hop-inspector__preview-evidence summary::before {
  content: '▸';
  flex: 0 0 auto;
  color: #2563eb;
  font-size: 0.76rem;
  font-weight: 900;
  transition: transform 0.16s ease;
}

.case-manual-start-hop-inspector__preview-evidence[open] summary::before {
  transform: rotate(90deg);
}

.case-manual-start-hop-inspector__preview-evidence strong {
  flex: 1 1 auto;
  min-width: 0;
  color: #1e293b;
  font-size: 0.82rem;
}

.case-manual-start-hop-inspector__preview-evidence summary span {
  flex: 0 0 auto;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  white-space: nowrap;
}

.case-manual-start-hop-inspector__preview-evidence ul {
  margin: 0;
  max-height: min(11rem, 24dvh);
  overflow: auto;
  overscroll-behavior: contain;
  padding: 0 9px 8px 26px;
  color: #475569;
  font-size: 0.76rem;
  line-height: 1.3;
}

.case-manual-start-hop-inspector__check-hint {
  color: #64748b;
  font-size: 0.78rem;
  align-self: center;
}

.case-manual-start-hop-inspector__override-warning {
  display: grid;
  gap: 2px;
  padding: 6px 8px;
  border: 1px solid #f59e0b;
  border-radius: 9px;
  background: #fffbeb;
  color: #78350f;
  font-size: 0.76rem;
}

.case-manual-start-hop-inspector__override-warning strong {
  font-size: 0.84rem;
}

.case-manual-start-hop-inspector__preview-list {
  display: grid;
  gap: 5px;
  max-height: min(420px, 54vh);
  overflow: auto;
  padding-right: 4px;
}

.case-manual-start-hop-inspector__preview-item {
  display: flex;
  align-items: stretch;
  gap: 6px;
  min-height: 42px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 9px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(241, 245, 249, 0.92));
  padding: 3px 5px;
  overflow: hidden;
  overflow-wrap: anywhere;
  cursor: context-menu;
}

.case-manual-start-hop-inspector__preview-item.is-deposit {
  border-color: rgba(37, 99, 235, 0.62);
  box-shadow: inset 3px 0 0 rgba(37, 99, 235, 0.5);
}

.case-manual-start-hop-inspector__preview-item.is-new,
.case-manual-start-hop-inspector__preview-item.is-branch {
  border-color: rgba(22, 163, 74, 0.42);
}

.case-manual-start-hop-inspector__preview-item.is-path-ending {
  border-style: dashed;
}

.case-manual-start-hop-inspector__preview-item.is-unknown-service {
  border-color: rgba(220, 38, 38, 0.52);
  box-shadow: inset 3px 0 0 rgba(220, 38, 38, 0.38);
}

.case-manual-start-hop-inspector__preview-item.is-swap-leg {
  border-color: rgba(124, 58, 237, 0.36);
  background: linear-gradient(180deg, rgba(250, 245, 255, 0.98), rgba(245, 243, 255, 0.9));
}

.case-manual-start-hop-inspector__preview-item.is-swap-input-leg {
  box-shadow: inset 4px 0 0 rgba(234, 88, 12, 0.42);
}

.case-manual-start-hop-inspector__preview-item.is-swap-output-leg {
  border-color: rgba(124, 58, 237, 0.62);
  box-shadow: inset 4px 0 0 rgba(124, 58, 237, 0.54), 0 8px 18px rgba(124, 58, 237, 0.12);
}

.case-manual-start-hop-inspector__preview-item.is-swap-output-leg .case-manual-start-hop-inspector__preview-card-header span,
.case-manual-start-hop-inspector__preview-item.is-swap-output-leg .case-manual-start-hop-inspector__preview-primary strong {
  color: #6d28d9;
}

.case-manual-start-hop-inspector__preview-direction {
  flex: 0 0 14px;
  display: grid;
  place-items: center;
  align-self: stretch;
  font-size: 1rem;
  font-weight: 900;
  color: #64748b;
}

.case-manual-start-hop-inspector__preview-direction.is-incoming,
.case-manual-start-hop-inspector__preview-item.is-incoming .case-manual-start-hop-inspector__preview-direction {
  color: #22c55e;
}

.case-manual-start-hop-inspector__preview-direction.is-outgoing,
.case-manual-start-hop-inspector__preview-item.is-outgoing .case-manual-start-hop-inspector__preview-direction {
  color: #ef4444;
}

.case-manual-start-hop-inspector__preview-card-body {
  flex: 1 1 auto;
  min-width: 0;
  display: grid;
  gap: 2px;
  overflow: hidden;
}

.case-manual-start-hop-inspector__preview-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: #64748b;
  font-size: 0.64rem;
  line-height: 1.05;
  text-transform: uppercase;
  letter-spacing: 0.035em;
}

.case-manual-start-hop-inspector__preview-card-header > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-start-hop-inspector__preview-card-header strong {
  color: #475569;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
}

.case-manual-start-hop-inspector__interest-star {
  color: #f59e0b;
  font-size: 0.95rem;
  margin-right: 3px;
  vertical-align: -1px;
}

.case-manual-start-hop-inspector__branch-mark {
  color: #92400e;
  font-size: 0.85rem;
  margin-right: 3px;
}

.case-manual-start-hop-inspector__preview-primary {
  display: grid;
  grid-template-columns: minmax(68px, auto) max-content minmax(78px, 1fr);
  align-items: baseline;
  gap: 5px;
  min-width: 0;
}

.case-manual-start-hop-inspector__preview-primary strong {
  color: #111827;
  font-size: 0.94rem;
  line-height: 1.03;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.case-manual-start-hop-inspector__preview-primary span {
  color: #334155;
  font-size: 0.82rem;
  font-weight: 800;
  white-space: nowrap;
}

.case-manual-start-hop-inspector__preview-primary small {
  color: #64748b;
  font-size: 0.68rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-start-hop-inspector__preview-route {
  min-width: 0;
  max-width: 100%;
  color: #64748b;
  font-size: 0.69rem;
  line-height: 1.14;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-start-hop-inspector__preview-badges {
  display: flex;
  flex-wrap: nowrap;
  gap: 2px;
  min-width: 0;
  max-height: 1.45em;
  overflow: hidden;
}

.case-manual-start-hop-inspector__preview-badges span {
  display: inline-block;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  border-radius: 999px;
  background: rgba(226, 232, 240, 0.9);
  color: #475569;
  padding: 1px 5px;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-start-hop-inspector__preview-btc-io {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 6px;
  margin-top: 2px;
}

.case-manual-start-hop-inspector__preview-btc-list {
  display: grid;
  gap: 2px;
  max-height: 74px;
  overflow: auto;
  border: 1px solid rgba(203, 213, 225, 0.72);
  border-radius: 8px;
  background: rgba(248, 250, 252, 0.9);
  padding: 4px 5px;
  color: #475569;
  font-size: 0.7rem;
  line-height: 1.25;
}

.case-manual-start-hop-inspector__preview-btc-list strong {
  color: #0f172a;
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.case-manual-start-hop-inspector__preview-details-toggle {
  margin-top: 0;
  color: #64748b;
  font-size: 0.68rem;
  min-width: 0;
}

.case-manual-start-hop-inspector__preview-details-toggle summary {
  cursor: pointer;
  line-height: 1.2;
}

.case-manual-start-hop-inspector__preview-details-toggle[open] {
  max-height: 154px;
  overflow: hidden;
}

.case-manual-start-hop-inspector__preview-details {
  display: grid;
  grid-template-columns: minmax(64px, 0.3fr) minmax(0, 1fr);
  gap: 2px 6px;
  max-height: 128px;
  margin: 3px 0 0;
  padding: 4px 6px;
  overflow: auto;
  overscroll-behavior: contain;
  border: 1px solid rgba(203, 213, 225, 0.58);
  border-radius: 8px;
  background: rgba(248, 250, 252, 0.72);
  font-size: 0.68rem;
  line-height: 1.2;
  scrollbar-gutter: stable;
}

.case-manual-start-hop-inspector__preview-detail-row {
  display: contents;
}

.case-manual-start-hop-inspector__preview-detail-label {
  color: #64748b;
}

.case-manual-start-hop-inspector__preview-detail-value {
  min-width: 0;
  color: #1e293b;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.case-manual-start-hop-inspector__preview-actions {
  position: sticky;
  bottom: 0;
  z-index: 3;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin: 6px -12px -12px;
  padding: 7px 12px 9px;
  border-top: 1px solid rgba(226, 232, 240, 0.92);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.88), rgba(255, 255, 255, 0.98) 44%);
  box-shadow: 0 -10px 22px rgba(15, 23, 42, 0.07);
}

.case-manual-start-hop-inspector__preview-actions .toolbar-btn {
  min-height: 1.62rem;
  padding: 0.18rem 0.52rem;
  font-size: 0.7rem;
}

.case-manual-start-hop-inspector.is-loading-trace {
  width: min(500px, calc(100% - 36px));
}

.case-manual-start-hop-inspector.is-preview-mode {
  display: flex;
  flex-direction: column;
  width: min(520px, calc(100% - 36px));
  height: min(760px, calc(100% - 36px), calc(100dvh - 108px));
  overflow: hidden;
}

.case-manual-start-hop-inspector__loading {
  display: grid;
  place-items: center;
  min-height: 300px;
  border: 1px solid rgba(59, 130, 246, 0.22);
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.86), rgba(248, 250, 252, 0.96));
  padding: 18px;
}

.case-manual-start-hop-inspector__loading-center {
  width: min(360px, 100%);
  display: grid;
  justify-items: center;
  gap: 10px;
  text-align: center;
}

.case-manual-start-hop-inspector__loading-orb {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: 4px solid rgba(37, 99, 235, 0.18);
  border-top-color: rgba(37, 99, 235, 0.82);
  animation: case-manual-hop-loading-spin 1s linear infinite;
}

.case-manual-start-hop-inspector__loading-center strong {
  color: #0f172a;
  font-size: 1rem;
}

.case-manual-start-hop-inspector__loading-center small {
  color: #475569;
  line-height: 1.35;
}

.case-manual-start-hop-inspector__loading-track {
  width: 100%;
  height: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(203, 213, 225, 0.82);
  box-shadow: inset 0 1px 3px rgba(15, 23, 42, 0.16);
}

.case-manual-start-hop-inspector__loading-fill {
  display: block;
  height: 100%;
  min-width: 7%;
  border-radius: inherit;
  background: linear-gradient(90deg, rgba(37, 99, 235, 0.84), rgba(34, 197, 94, 0.84));
  transition: width 260ms ease;
}

.case-manual-start-hop-inspector__loading-meta {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: #334155;
  font-size: 0.78rem;
  font-weight: 700;
}

@keyframes case-manual-hop-loading-spin {
  to { transform: rotate(360deg); }
}

.case-manual-start-hop-inspector__preview--takeover {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0;
  min-height: 0;
  margin-top: 0;
  max-height: none;
  overflow: hidden;
}

.case-manual-start-hop-inspector__preview--takeover .case-manual-start-hop-inspector__preview-header,
.case-manual-start-hop-inspector__preview--takeover .case-manual-start-hop-inspector__preview-status,
.case-manual-start-hop-inspector__preview--takeover .case-manual-start-hop-inspector__override-warning,
.case-manual-start-hop-inspector__preview--takeover .case-manual-start-hop-inspector__preview-evidence,
.case-manual-start-hop-inspector__preview--takeover .case-manual-start-hop-inspector__preview-actions {
  flex: 0 0 auto;
}

.case-manual-start-hop-inspector__preview--takeover .case-manual-start-hop-inspector__preview-scroll {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 5px;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
}

.case-manual-start-hop-inspector__preview--takeover .case-manual-start-hop-inspector__preview-list {
  flex: 0 0 auto;
  min-height: 0;
  max-height: none;
  overflow: visible;
  padding-right: 1px;
}

.case-manual-start-hop-inspector__preview-item.is-branch {
  border-color: rgba(180, 83, 9, 0.58);
  background: linear-gradient(180deg, rgba(255, 251, 235, 0.98), rgba(254, 243, 199, 0.9));
  box-shadow: inset 4px 0 0 rgba(180, 83, 9, 0.42);
}

.case-manual-start-hop-inspector__preview-item.is-branch .case-manual-start-hop-inspector__preview-badges span:first-child {
  background: rgba(251, 191, 36, 0.28);
  color: #92400e;
}

/* Transaction Lookup Auto uses a compact, visual-only inspector. The regular
   Manual hop inspector remains available as soon as Auto is stopped or when
   Manual Solve was opened from the Advanced Workspace. */
.case-manual-start-hop-inspector.is-quick-auto {
  width: min(336px, calc(100% - 36px));
  max-height: none;
  overflow: hidden;
  border-color: rgba(96, 165, 250, 0.42);
  background:
    radial-gradient(circle at 18% 8%, rgba(56, 189, 248, 0.16), transparent 34%),
    radial-gradient(circle at 86% 88%, rgba(34, 197, 94, 0.12), transparent 36%),
    linear-gradient(155deg, rgba(5, 15, 32, 0.97), rgba(12, 27, 52, 0.96));
  box-shadow:
    0 22px 58px rgba(2, 8, 23, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  color: #e8f2ff;
  padding: 12px;
  backdrop-filter: blur(18px) saturate(145%);
}

.case-manual-start-hop-inspector.is-quick-auto .case-manual-start-hop-inspector__header {
  flex-wrap: nowrap;
  min-height: 38px;
  margin: 0 0 8px;
  padding: 0 2px 8px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
}

.case-manual-start-hop-inspector.is-quick-auto .case-manual-start-hop-inspector__header strong {
  display: block;
  max-width: 292px;
  overflow: hidden;
  color: #f8fbff;
  font-size: 0.86rem;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-start-hop-inspector.is-quick-auto .case-manual-start-hop-inspector__header small {
  margin-top: 3px;
  color: #8fb5dc;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.case-manual-start-hop-inspector__quick-auto {
  display: grid;
  gap: 9px;
}

.case-manual-start-hop-inspector__quick-auto-visual {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 78px;
  overflow: hidden;
  border: 1px solid rgba(96, 165, 250, 0.16);
  border-radius: 15px;
  background:
    linear-gradient(rgba(96, 165, 250, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(96, 165, 250, 0.045) 1px, transparent 1px),
    rgba(2, 10, 24, 0.5);
  background-size: 18px 18px;
}

.case-manual-start-hop-inspector__quick-auto-orbit {
  position: relative;
  width: 64px;
  height: 64px;
}

.case-manual-start-hop-inspector__quick-auto-ring,
.case-manual-start-hop-inspector__quick-auto-core,
.case-manual-start-hop-inspector__quick-auto-success-burst {
  position: absolute;
  inset: 50% auto auto 50%;
  border-radius: 999px;
  transform: translate(-50%, -50%);
}

.case-manual-start-hop-inspector__quick-auto-ring {
  border-style: solid;
  animation: case-manual-quick-auto-spin 2.8s linear infinite;
}

.case-manual-start-hop-inspector__quick-auto-ring--outer {
  width: 62px;
  height: 62px;
  border-width: 2px;
  border-color: rgba(56, 189, 248, 0.14) rgba(56, 189, 248, 0.72) rgba(34, 197, 94, 0.18) rgba(96, 165, 250, 0.32);
}

.case-manual-start-hop-inspector__quick-auto-ring--middle {
  width: 47px;
  height: 47px;
  border-width: 2px;
  border-color: rgba(34, 197, 94, 0.58) transparent rgba(56, 189, 248, 0.58) transparent;
  animation-duration: 1.9s;
  animation-direction: reverse;
}

.case-manual-start-hop-inspector__quick-auto-ring--inner {
  width: 33px;
  height: 33px;
  border-width: 1px;
  border-color: transparent rgba(147, 197, 253, 0.82) transparent rgba(74, 222, 128, 0.62);
  animation-duration: 1.25s;
}

.case-manual-start-hop-inspector__quick-auto-core {
  display: grid;
  place-items: center;
  width: 27px;
  height: 27px;
  background: linear-gradient(145deg, rgba(30, 64, 175, 0.96), rgba(8, 145, 178, 0.92));
  box-shadow: 0 0 22px rgba(56, 189, 248, 0.42);
  animation: case-manual-quick-auto-core-pulse 1.55s ease-in-out infinite;
}

.case-manual-start-hop-inspector__quick-auto-core svg {
  width: 19px;
  height: 19px;
  overflow: visible;
  fill: rgba(226, 242, 255, 0.96);
  stroke: rgba(226, 242, 255, 0.96);
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.case-manual-start-hop-inspector__quick-auto-core svg circle,
.case-manual-start-hop-inspector__quick-auto-core svg rect {
  stroke-width: 0;
}

.case-manual-start-hop-inspector__quick-auto-signal {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.case-manual-start-hop-inspector__quick-auto-signal span {
  position: absolute;
  width: 3px;
  height: 3px;
  border-radius: 999px;
  background: #7dd3fc;
  box-shadow: 0 0 9px rgba(125, 211, 252, 0.9);
  opacity: 0.15;
  animation: case-manual-quick-auto-signal 2.4s ease-in-out infinite;
}

.case-manual-start-hop-inspector__quick-auto-signal span:nth-child(1) { left: 12%; top: 28%; animation-delay: -0.2s; }
.case-manual-start-hop-inspector__quick-auto-signal span:nth-child(2) { left: 23%; top: 68%; animation-delay: -1.1s; }
.case-manual-start-hop-inspector__quick-auto-signal span:nth-child(3) { left: 34%; top: 18%; animation-delay: -1.8s; }
.case-manual-start-hop-inspector__quick-auto-signal span:nth-child(4) { left: 46%; top: 78%; animation-delay: -0.7s; }
.case-manual-start-hop-inspector__quick-auto-signal span:nth-child(5) { left: 57%; top: 22%; animation-delay: -1.4s; }
.case-manual-start-hop-inspector__quick-auto-signal span:nth-child(6) { left: 68%; top: 72%; animation-delay: -2.1s; }
.case-manual-start-hop-inspector__quick-auto-signal span:nth-child(7) { left: 79%; top: 31%; animation-delay: -0.4s; }
.case-manual-start-hop-inspector__quick-auto-signal span:nth-child(8) { left: 88%; top: 63%; animation-delay: -1.65s; }
.case-manual-start-hop-inspector__quick-auto-signal span:nth-child(9) { left: 52%; top: 49%; animation-delay: -0.95s; }

.case-manual-start-hop-inspector__quick-auto-copy {
  display: grid;
  gap: 2px;
  text-align: center;
}

.case-manual-start-hop-inspector__quick-auto-copy strong {
  color: #f8fbff;
  font-size: 0.88rem;
  letter-spacing: -0.01em;
}

.case-manual-start-hop-inspector__quick-auto-copy small {
  min-height: 2.4em;
  color: #a9c3df;
  font-size: 0.68rem;
  line-height: 1.25;
}

.case-manual-start-hop-inspector__quick-auto-stages {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 5px;
}

.case-manual-start-hop-inspector__quick-auto-stages > span {
  height: 4px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(71, 85, 105, 0.58);
}

.case-manual-start-hop-inspector__quick-auto-stages i {
  display: block;
  width: 0;
  height: 100%;
  border-radius: inherit;
}

.case-manual-start-hop-inspector__quick-auto-stages .is-complete i {
  width: 100%;
  background: linear-gradient(90deg, #22c55e, #4ade80);
}

.case-manual-start-hop-inspector__quick-auto-stages .is-active i {
  width: 72%;
  background: linear-gradient(90deg, #38bdf8, #60a5fa, #4ade80);
  animation: case-manual-quick-auto-stage-sweep 1.15s ease-in-out infinite alternate;
}

.case-manual-start-hop-inspector__quick-auto-progress {
  position: relative;
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(51, 65, 85, 0.76);
  box-shadow: inset 0 1px 3px rgba(2, 8, 23, 0.72);
}

.case-manual-start-hop-inspector__quick-auto-progress > span {
  display: block;
  height: 100%;
  min-width: 8%;
  border-radius: inherit;
  background: linear-gradient(90deg, #2563eb, #38bdf8, #22c55e);
  box-shadow: 0 0 15px rgba(56, 189, 248, 0.44);
  transition: width 260ms ease;
}

.case-manual-start-hop-inspector__quick-auto-trace {
  display: grid;
  gap: 2px;
  min-height: 84px;
  padding: 4px 7px 5px;
  border: 1px solid rgba(96, 165, 250, 0.17);
  border-radius: 13px;
  background: rgba(2, 8, 23, 0.42);
}

.case-manual-start-hop-inspector__quick-auto-trace svg {
  display: block;
  width: 100%;
  height: 70px;
}

.case-manual-start-hop-inspector__quick-auto-trace > small {
  overflow: hidden;
  color: #7f9fbe;
  font-size: 0.58rem;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-start-hop-inspector__quick-auto-trace-edge {
  fill: none;
  stroke: rgba(125, 211, 252, 0.52);
  stroke-width: 1.6;
  stroke-dasharray: 4 4;
  animation: case-manual-quick-auto-edge-flow 1.1s linear infinite;
}

.case-manual-start-hop-inspector__quick-auto-trace-node :is(circle, rect, polygon) {
  fill: #1d4ed8;
  stroke: #bfdbfe;
  stroke-width: 1.4;
}

.case-manual-start-hop-inspector__quick-auto-trace-node.is-deposit :is(circle, rect, polygon),
.case-manual-start-hop-inspector__quick-auto-trace-node.is-ending :is(circle, rect, polygon) {
  fill: #0f766e;
  stroke: #99f6e4;
}

.case-manual-start-hop-inspector__quick-auto-trace-node.is-new :is(circle, rect, polygon) {
  fill: #15803d;
  stroke: #bbf7d0;
}

.case-manual-start-hop-inspector__quick-auto-trace-node.is-branch :is(circle, rect, polygon) {
  fill: #b45309;
  stroke: #fde68a;
}

.case-manual-start-hop-inspector__quick-auto-trace-node.is-swap circle:first-child {
  fill: #7c3aed;
  stroke: #ddd6fe;
}

.case-manual-start-hop-inspector__quick-auto-trace-node.is-swap circle:last-child {
  fill: #0891b2;
  stroke: #cffafe;
}

.case-manual-start-hop-inspector__quick-auto-trace-node {
  animation: case-manual-quick-auto-node-pulse 1.5s ease-in-out infinite alternate;
}

.case-manual-start-hop-inspector__quick-auto-trace-node:nth-of-type(2n) { animation-delay: -0.45s; }
.case-manual-start-hop-inspector__quick-auto-trace-node:nth-of-type(3n) { animation-delay: -0.9s; }

.case-manual-start-hop-inspector__quick-auto-trace-overflow {
  fill: #93c5fd;
  font-size: 9px;
  font-weight: 900;
}

.case-manual-start-hop-inspector__quick-auto-activity {
  display: flex;
  align-items: end;
  justify-content: center;
  gap: 4px;
  height: 29px;
  padding: 4px 8px;
  border: 1px solid rgba(96, 165, 250, 0.13);
  border-radius: 12px;
  background: rgba(2, 8, 23, 0.32);
}

.case-manual-start-hop-inspector__quick-auto-activity span {
  width: 5px;
  height: 7px;
  border-radius: 999px;
  background: linear-gradient(180deg, #7dd3fc, #2563eb);
  transform-origin: bottom;
  animation: case-manual-quick-auto-equalizer 1.05s ease-in-out infinite alternate;
}

.case-manual-start-hop-inspector__quick-auto-activity span:nth-child(2n) { animation-delay: -0.25s; }
.case-manual-start-hop-inspector__quick-auto-activity span:nth-child(3n) { animation-delay: -0.52s; }
.case-manual-start-hop-inspector__quick-auto-activity span:nth-child(4n) { animation-delay: -0.78s; }

.case-manual-start-hop-inspector.is-quick-auto-phase-resolved {
  border-color: rgba(74, 222, 128, 0.6);
  box-shadow: 0 22px 58px rgba(2, 8, 23, 0.42), 0 0 0 1px rgba(74, 222, 128, 0.18);
}

.case-manual-start-hop-inspector.is-quick-auto-phase-resolved .case-manual-start-hop-inspector__quick-auto-core {
  background: linear-gradient(145deg, #15803d, #22c55e);
  box-shadow: 0 0 28px rgba(74, 222, 128, 0.58);
  animation: none;
}

.case-manual-start-hop-inspector.is-quick-auto-phase-resolved .case-manual-start-hop-inspector__quick-auto-ring {
  border-color: rgba(74, 222, 128, 0.52);
  animation-duration: 5s;
}

.case-manual-start-hop-inspector__quick-auto-success-burst {
  width: 28px;
  height: 28px;
  border: 2px solid rgba(134, 239, 172, 0.82);
  animation: case-manual-quick-auto-success-burst 0.95s ease-out infinite;
}

.case-manual-start-hop-inspector.is-quick-auto-phase-stopping .case-manual-start-hop-inspector__quick-auto-core {
  background: linear-gradient(145deg, #b45309, #f59e0b);
}

@keyframes case-manual-quick-auto-spin {
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

@keyframes case-manual-quick-auto-core-pulse {
  0%, 100% { transform: translate(-50%, -50%) scale(0.94); }
  50% { transform: translate(-50%, -50%) scale(1.06); }
}

@keyframes case-manual-quick-auto-signal {
  0%, 100% { opacity: 0.12; transform: scale(0.55); }
  50% { opacity: 0.9; transform: scale(1.35); }
}

@keyframes case-manual-quick-auto-stage-sweep {
  from { width: 28%; opacity: 0.7; }
  to { width: 100%; opacity: 1; }
}

@keyframes case-manual-quick-auto-edge-flow {
  to { stroke-dashoffset: -8; }
}

@keyframes case-manual-quick-auto-node-pulse {
  from { opacity: 0.62; }
  to { opacity: 1; }
}

@keyframes case-manual-quick-auto-equalizer {
  from { height: 5px; opacity: 0.42; }
  to { height: 22px; opacity: 1; }
}

@keyframes case-manual-quick-auto-success-burst {
  0% { opacity: 0.8; transform: translate(-50%, -50%) scale(0.8); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(2.25); }
}

@media (max-width: 430px) {
  .case-manual-start-hop-inspector.is-quick-auto {
    width: min(310px, calc(100% - 24px));
    padding: 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .case-manual-start-hop-inspector__quick-auto-ring,
  .case-manual-start-hop-inspector__quick-auto-core,
  .case-manual-start-hop-inspector__quick-auto-signal span,
  .case-manual-start-hop-inspector__quick-auto-stages .is-active i,
  .case-manual-start-hop-inspector__quick-auto-trace-edge,
  .case-manual-start-hop-inspector__quick-auto-trace-node,
  .case-manual-start-hop-inspector__quick-auto-activity span,
  .case-manual-start-hop-inspector__quick-auto-success-burst {
    animation: none !important;
  }
}

.case-manual-start-quick-actions {
  position: absolute;
  left: 18px;
  top: 18px;
  z-index: 11;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  max-width: min(620px, calc(100% - 36px));
  border: 1px solid rgba(59, 130, 246, 0.28);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 14px 38px rgba(15, 23, 42, 0.16);
  padding: 12px 14px;
  backdrop-filter: blur(10px);
}

.case-manual-start-quick-actions strong,
.case-manual-start-quick-actions small {
  display: block;
}

.case-manual-start-quick-actions strong {
  color: #0f172a;
  font-size: 0.95rem;
}

.case-manual-start-quick-actions small {
  color: #475569;
  margin-top: 2px;
}

.case-manual-start-quick-actions__buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}


.case-manual-start-solve-warning,
.case-manual-start-settings-panel {
  position: absolute;
  right: 18px;
  z-index: 40;
  width: min(460px, calc(100% - 36px));
  max-height: min(620px, calc(100% - 48px));
  overflow: auto;
  display: grid;
  gap: 10px;
  border-radius: 18px;
  color: #0f172a;
  padding: 12px;
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.2);
  backdrop-filter: blur(10px);
}

.case-manual-start-solve-warning {
  top: 108px;
  border: 1px solid rgba(239, 68, 68, 0.42);
  background: linear-gradient(180deg, rgba(254, 242, 242, 0.98), rgba(255, 247, 237, 0.98));
}

.case-manual-start-settings-panel {
  top: 108px;
  border: 1px solid rgba(59, 130, 246, 0.34);
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.98), rgba(248, 250, 252, 0.98));
}

.case-manual-start-solve-warning__header,
.case-manual-start-settings-panel__header,
.case-manual-start-solve-warning__actions,
.case-manual-start-settings-panel__actions {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.case-manual-start-solve-warning__header strong,
.case-manual-start-solve-warning__header small,
.case-manual-start-settings-panel__header strong,
.case-manual-start-settings-panel__header small {
  display: block;
}

.case-manual-start-solve-warning__header strong,
.case-manual-start-settings-panel__header strong {
  font-size: 0.94rem;
  color: #0f172a;
}

.case-manual-start-solve-warning__header small,
.case-manual-start-settings-panel__header small,
.case-manual-start-solve-warning p,
.case-manual-start-settings-panel__note,
.case-manual-start-settings-panel__status {
  margin: 0;
  color: #64748b;
  font-size: 0.76rem;
  line-height: 1.38;
}

.case-manual-start-solve-warning__status {
  padding: 7px 9px;
  border: 1px solid rgba(252, 165, 165, 0.72);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
  color: #991b1b !important;
  font-weight: 800;
}

.case-manual-start-solve-warning__checkbox,
.case-manual-start-settings-panel__checkbox {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 9px;
  border: 1px solid rgba(203, 213, 225, 0.75);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.76);
  color: #334155;
  font-size: 0.78rem;
  font-weight: 750;
  line-height: 1.35;
}

.case-manual-start-solve-warning__checkbox input,
.case-manual-start-settings-panel__checkbox input {
  flex: 0 0 auto;
  margin-top: 2px;
}

.case-manual-start-settings-panel__checkbox span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.case-manual-start-settings-panel__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.case-manual-start-settings-panel__grid label:not(.case-manual-start-settings-panel__checkbox) {
  display: grid;
  gap: 4px;
  min-width: 0;
  color: #475569;
  font-size: 0.68rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.035em;
}

.case-manual-start-settings-panel__grid select,
.case-manual-start-settings-panel__grid input[type='number'] {
  min-width: 0;
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #ffffff;
  color: #0f172a;
  font: inherit;
  padding: 0.42rem 0.52rem;
}

.case-manual-start-settings-panel__actions,
.case-manual-start-solve-warning__actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}


.case-manual-start-output-choice {
  position: absolute;
  z-index: 38;
  width: min(420px, calc(100% - 36px));
  max-height: min(560px, calc(100% - 48px));
  overflow: auto;
  display: grid;
  gap: 10px;
  border: 1px solid rgba(245, 158, 11, 0.45);
  border-radius: 18px;
  background: rgba(255, 251, 235, 0.96);
  color: #0f172a;
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.18);
  padding: 12px;
  backdrop-filter: blur(10px);
}

.case-manual-start-output-choice__header,
.case-manual-start-output-choice__actions {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.case-manual-start-output-choice__header strong,
.case-manual-start-output-choice__header small {
  display: block;
}

.case-manual-start-output-choice__header small,
.case-manual-start-output-choice__status,
.case-manual-start-output-choice__empty,
.case-manual-start-output-choice__item small {
  color: #64748b;
  font-size: 0.74rem;
  line-height: 1.35;
}

.case-manual-start-output-choice__status {
  margin: 0;
}

.case-manual-start-output-choice__actions {
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
}

.case-manual-start-output-choice__list {
  display: grid;
  gap: 8px;
}

.case-manual-start-output-choice__item {
  display: grid;
  gap: 4px;
  width: 100%;
  border: 1px solid rgba(251, 191, 36, 0.56);
  border-radius: 14px;
  background: #ffffff;
  color: #0f172a;
  padding: 10px;
  text-align: left;
  cursor: pointer;
}

.case-manual-start-output-choice__item:hover,
.case-manual-start-output-choice__item.is-selected {
  border-color: #2563eb;
  background: #eff6ff;
}

.case-manual-start-output-choice__item-title {
  color: #92400e;
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.case-manual-start-output-choice__item code {
  color: #334155;
  font-size: 0.75rem;
  overflow-wrap: anywhere;
}

.case-manual-start-node-action-tray {
  position: absolute;
  top: 50%;
  right: 0.42rem;
  z-index: 36;
  display: grid;
  gap: 0.12rem;
  align-items: center;
  justify-items: center;
  width: 2.12rem;
  padding: 0.2rem 0.16rem;
  border: 1px solid rgba(148, 163, 184, 0.52);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.93);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.16);
  backdrop-filter: blur(10px);
  transform: translateY(-50%);
  pointer-events: auto;
}

.case-manual-start-node-action-tray.is-collapsed {
  width: 1.3rem;
  padding: 0.14rem 0.08rem;
  border-radius: 999px 0 0 999px;
  right: 0;
  background: rgba(248, 250, 252, 0.84);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
}

.case-manual-start-node-action-tray__button,
.case-manual-start-node-action-tray__toggle,
.case-manual-start-node-action-tray__peek {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.58rem;
  height: 1.58rem;
  min-width: 1.58rem;
  min-height: 1.58rem;
  padding: 0;
  border: 1px solid rgba(203, 213, 225, 0.95);
  border-radius: 8px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 950;
  line-height: 1;
  text-align: center;
  cursor: pointer;
  transition: transform 0.14s ease, border-color 0.14s ease, background 0.14s ease, color 0.14s ease, box-shadow 0.14s ease, opacity 0.14s ease;
}

.case-manual-start-node-action-tray__button > span,
.case-manual-start-node-action-tray__toggle > span,
.case-manual-start-node-action-tray__peek > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  line-height: 1;
  transform: translateY(-0.01rem);
  pointer-events: none;
}

.case-manual-start-node-action-tray__toggle,
.case-manual-start-node-action-tray__peek {
  width: 1.36rem;
  height: 1.02rem;
  min-width: 1.36rem;
  min-height: 1.02rem;
  border-radius: 999px;
  color: #475569;
  font-size: 0.82rem;
}

.case-manual-start-node-action-tray__peek {
  width: 1.06rem;
  min-width: 1.06rem;
  border-color: transparent;
  background: transparent;
}

.case-manual-start-node-action-tray__button:hover:not(:disabled),
.case-manual-start-node-action-tray__button:focus-visible:not(:disabled),
.case-manual-start-node-action-tray__toggle:hover,
.case-manual-start-node-action-tray__toggle:focus-visible,
.case-manual-start-node-action-tray__peek:hover,
.case-manual-start-node-action-tray__peek:focus-visible {
  border-color: #60a5fa;
  background: #eff6ff;
  color: #1d4ed8;
  box-shadow: 0 8px 16px rgba(37, 99, 235, 0.18);
  outline: none;
  transform: translateY(-1px);
}

.case-manual-start-node-action-tray__button.is-recommended:not(:disabled) {
  border-color: #16a34a;
  background: linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%);
  color: #14532d;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.12), 0 10px 20px rgba(22, 163, 74, 0.18);
}

.case-manual-start-node-action-tray__button.is-secondary:not(:disabled) {
  border-color: #c7d2fe;
  background: linear-gradient(180deg, #eef2ff 0%, #ffffff 100%);
  color: #3730a3;
}

.case-manual-start-node-action-tray__button:disabled {
  cursor: not-allowed;
  opacity: 0.38;
  transform: none;
  box-shadow: none;
}

.case-manual-start-node-action-tray-popover {
  position: absolute;
  top: 50%;
  right: 3.15rem;
  z-index: 38;
  display: grid;
  gap: 0.55rem;
  width: min(20rem, calc(100% - 4.4rem));
  padding: 0.7rem;
  border: 1px solid rgba(147, 197, 253, 0.78);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 20px 46px rgba(15, 23, 42, 0.18);
  backdrop-filter: blur(12px);
  transform: translateY(-50%);
  pointer-events: auto;
}

.case-manual-start-node-action-tray-popover__header {
  display: flex;
  justify-content: space-between;
  gap: 0.65rem;
  align-items: flex-start;
}

.case-manual-start-node-action-tray-popover__header strong,
.case-manual-start-node-action-tray-popover__header small {
  display: block;
}

.case-manual-start-node-action-tray-popover__header strong {
  color: #0f172a;
  font-size: 0.86rem;
}

.case-manual-start-node-action-tray-popover__header small {
  margin-top: 0.14rem;
  color: #64748b;
  font-size: 0.68rem;
  line-height: 1.25;
}

.case-manual-start-node-action-tray-popover__close {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.55rem;
  height: 1.55rem;
  min-width: 1.55rem;
  padding: 0;
  border: 1px solid rgba(203, 213, 225, 0.9);
  border-radius: 999px;
  background: #ffffff;
  color: #475569;
  font-size: 0;
  line-height: 0;
  cursor: pointer;
}

.case-manual-start-node-action-tray-popover__close-icon {
  position: absolute;
  inset: 0;
  display: block;
  color: inherit;
  pointer-events: none;
}

.case-manual-start-node-action-tray-popover__close:hover,
.case-manual-start-node-action-tray-popover__close:focus-visible {
  border-color: #60a5fa;
  background: #eff6ff;
  color: #1d4ed8;
  outline: none;
}


.case-manual-start-node-action-tray-popover__close-icon::before,
.case-manual-start-node-action-tray-popover__close-icon::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0.72rem;
  height: 0.11rem;
  border-radius: 999px;
  background: currentColor;
  transform-origin: center;
}

.case-manual-start-node-action-tray-popover__close-icon::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.case-manual-start-node-action-tray-popover__close-icon::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.case-manual-start-node-action-tray-popover__field {
  display: grid;
  gap: 0.24rem;
  color: #475569;
  font-size: 0.66rem;
  font-weight: 850;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.case-manual-start-node-action-tray-popover__field input {
  width: 100%;
  min-width: 0;
  min-height: 2rem;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #ffffff;
  color: #0f172a;
  font: 0.82rem/1.2 inherit;
  padding: 0.4rem 0.55rem;
  text-transform: none;
  letter-spacing: normal;
}

.case-manual-start-node-action-tray-popover__field input:focus {
  outline: none;
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.16);
}

.case-manual-start-node-action-tray-popover__status {
  margin: 0;
  color: #92400e;
  font-size: 0.72rem;
  font-weight: 750;
  line-height: 1.3;
}

.case-manual-start-node-action-tray-popover__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  justify-content: flex-end;
}

.case-flow-visuals-manual-work-markers {
  pointer-events: none;
}

.case-flow-visual-manual-work-bubble {
  fill: rgba(255, 247, 237, 0.12);
  stroke: #f97316;
  stroke-width: 4px;
  stroke-dasharray: 11 6;
  filter: drop-shadow(0 0 9px rgba(249, 115, 22, 0.28));
}

.case-flow-visual-manual-work-label {
  fill: #9a3412;
  stroke: rgba(255, 255, 255, 0.94);
  stroke-width: 3px;
  paint-order: stroke;
  font-size: 14px;
  font-weight: 950;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.case-flow-visuals-report-boundary-markers {
  pointer-events: none;
}

.case-flow-visual-report-boundary-bubble {
  fill: rgba(238, 242, 255, 0.14);
  stroke: #6366f1;
  stroke-width: 4px;
  stroke-dasharray: 4 5;
  filter: drop-shadow(0 0 10px rgba(79, 70, 229, 0.3));
}

.case-flow-visual-report-boundary-check {
  fill: #4f46e5;
  stroke: #ffffff;
  stroke-width: 3px;
}

.case-flow-visual-report-boundary-checkmark {
  fill: none;
  stroke: #ffffff;
  stroke-width: 3px;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.case-flow-visual-report-boundary-label {
  fill: #3730a3;
  stroke: rgba(255, 255, 255, 0.96);
  stroke-width: 3px;
  paint-order: stroke;
  font-size: 14px;
  font-weight: 950;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.case-manual-start-work-indicator {
  pointer-events: none;
  opacity: 0.96;
  transform-box: fill-box;
  transform-origin: center;
  transition: opacity 0.42s ease, transform 0.42s ease;
}

.case-manual-start-work-indicator.is-finishing {
  opacity: 0;
  transform: scale(0.94);
}

.case-manual-start-work-indicator__anchor-haze {
  fill: url(#case-flow-visual-manual-solve-address-glow);
  opacity: 0.75;
  animation: case-manual-start-work-haze 1.8s ease-in-out infinite;
}

.case-manual-start-work-indicator__anchor-ring {
  fill: none;
  stroke: rgba(37, 99, 235, 0.78);
  stroke-width: 4px;
  stroke-dasharray: 9 7;
  filter: drop-shadow(0 0 7px rgba(96, 165, 250, 0.48));
  animation: case-manual-start-work-ring 1.35s linear infinite;
}

.case-manual-start-work-indicator--path-ending .case-manual-start-work-indicator__anchor-ring {
  stroke: rgba(22, 163, 74, 0.82);
  filter: drop-shadow(0 0 7px rgba(34, 197, 94, 0.42));
}

.case-manual-start-work-indicator--swap .case-manual-start-work-indicator__anchor-ring {
  stroke: rgba(217, 119, 6, 0.86);
  filter: drop-shadow(0 0 7px rgba(251, 191, 36, 0.48));
}

.case-manual-start-work-indicator--bridge .case-manual-start-work-indicator__anchor-ring {
  stroke: rgba(124, 58, 237, 0.84);
  filter: drop-shadow(0 0 7px rgba(168, 85, 247, 0.46));
}


.case-manual-start-work-indicator__lane {
  fill: none;
  stroke: rgba(59, 130, 246, 0.72);
  stroke-width: 4px;
  stroke-dasharray: 12 8;
  stroke-linecap: round;
  filter: drop-shadow(0 0 7px rgba(59, 130, 246, 0.32));
  animation: case-manual-start-work-lane 0.9s linear infinite;
}

.case-manual-start-work-indicator--swap .case-manual-start-work-indicator__lane {
  stroke: rgba(245, 158, 11, 0.78);
}

.case-manual-start-work-indicator--bridge .case-manual-start-work-indicator__lane {
  stroke: rgba(124, 58, 237, 0.78);
}

.case-manual-start-work-indicator__ghost-address circle:first-child {
  fill: rgba(239, 246, 255, 0.5);
  stroke: rgba(37, 99, 235, 0.68);
  stroke-width: 3px;
  stroke-dasharray: 8 6;
  filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.2));
}

.case-manual-start-work-indicator--swap .case-manual-start-work-indicator__ghost-address circle:first-child {
  fill: rgba(255, 251, 235, 0.5);
  stroke: rgba(245, 158, 11, 0.72);
}

.case-manual-start-work-indicator--bridge .case-manual-start-work-indicator__ghost-address circle:first-child {
  fill: rgba(245, 243, 255, 0.52);
  stroke: rgba(124, 58, 237, 0.72);
}

.case-manual-start-work-indicator__ghost-address circle:last-child {
  fill: none;
  stroke: rgba(30, 64, 175, 0.42);
  stroke-width: 3px;
  stroke-linecap: round;
}

.case-manual-start-work-indicator__resolve-stamp path {
  fill: none;
  stroke: rgba(22, 163, 74, 0.9);
  stroke-width: 7px;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 0 6px rgba(34, 197, 94, 0.35));
  animation: case-manual-start-work-stamp 1.2s ease-in-out infinite;
}


@keyframes case-manual-start-work-ring {
  to { stroke-dashoffset: -64; }
}

@keyframes case-manual-start-work-lane {
  to { stroke-dashoffset: -42; }
}

@keyframes case-manual-start-work-haze {
  0%, 100% { opacity: 0.54; transform: scale(0.94); }
  50% { opacity: 0.88; transform: scale(1.04); }
}

@keyframes case-manual-start-work-stamp {
  0%, 100% { opacity: 0.72; transform: scale(0.96); }
  50% { opacity: 1; transform: scale(1.04); }
}

.case-manual-solve-bottom-resizer {
  margin: 0.34rem 0 -0.08rem;
  color: #2563eb;
}

.case-manual-solve-bottom-resizer::before {
  background: linear-gradient(90deg, rgba(37, 99, 235, 0.1), rgba(37, 99, 235, 0.5), rgba(37, 99, 235, 0.1));
}

.case-manual-solve-bottom-resizer span {
  border-color: rgba(37, 99, 235, 0.24);
  background: rgba(248, 251, 255, 0.98);
  color: #1d4ed8;
}

.case-manual-solve-bottom-resizer:hover span,
.case-manual-solve-bottom-resizer:focus-visible span,
.case-manual-solve-bottom-resizer.is-active span {
  border-color: #2563eb;
  background: #dbeafe;
}

.case-manual-solve-bottom-resizer.is-collapsed span {
  border-style: dashed;
  color: #1e40af;
}

.case-manual-solve-bottom-resizer.is-collapsed:hover span,
.case-manual-solve-bottom-resizer.is-collapsed:focus-visible span,
.case-manual-solve-bottom-resizer.is-collapsed.is-active span {
  border-style: solid;
}

.case-manual-solve-bottom-panel {
  max-height: var(--case-manual-solve-bottom-height, 17.5rem);
  scrollbar-width: thin;
}

.case-manual-solve-evaluation-panel.case-manual-solve-bottom-panel {
  height: var(--case-manual-solve-bottom-height, 17.5rem);
  min-height: 0;
  grid-template-rows: auto auto auto minmax(0, 1fr) auto;
  overflow: auto;
}

.case-manual-solve-evaluation-panel.case-manual-solve-bottom-panel .case-manual-solve-evaluation-panel__body,
.case-manual-solve-evaluation-panel.case-manual-solve-bottom-panel .case-manual-solve-evaluation-panel__card {
  min-height: 0;
}

.case-manual-solve-evaluation-panel.case-manual-solve-bottom-panel .case-manual-solve-evaluation-panel__card {
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
}

.case-manual-solve-evaluation-panel.case-manual-solve-bottom-panel .case-manual-solve-evaluation-panel__ending-list {
  max-height: none;
  min-height: 0;
}

.case-manual-solve-evaluation-panel.case-manual-solve-bottom-panel .case-manual-solve-evaluation-panel__token-list {
  min-height: 0;
  overflow: auto;
}

.case-manual-solve-viewport-controls.case-manual-solve-bottom-panel[open] {
  height: var(--case-manual-solve-bottom-height, 17.5rem);
  min-height: 0;
  overflow: auto;
}

.case-manual-solve-bottom-panel.is-resizing {
  user-select: none;
}

.case-investigation-visualizer-modal--manual-bottom-resizing {
  cursor: ns-resize;
}

.case-manual-solve-viewport-controls {
  margin-top: 0.18rem;
  border: 1px solid rgba(191, 219, 254, 0.8);
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.74), rgba(248, 250, 252, 0.96));
  color: #0f172a;
  overflow: hidden;
}

.case-manual-solve-viewport-controls summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.52rem 0.68rem;
  cursor: pointer;
  list-style: none;
}

.case-manual-solve-viewport-controls summary::-webkit-details-marker {
  display: none;
}

.case-manual-solve-viewport-controls summary span {
  color: #1e3a8a;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.case-manual-solve-viewport-controls summary small {
  color: #475569;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.25;
  text-align: right;
}

.case-manual-solve-viewport-controls__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.85rem;
  padding: 0 0.68rem 0.62rem;
}

.case-manual-solve-viewport-controls__field {
  display: grid;
  gap: 0.2rem;
  min-width: 12.5rem;
  color: #334155;
  font-size: 0.68rem;
  font-weight: 850;
}

.case-manual-solve-viewport-controls__field select {
  min-height: 1.72rem;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #ffffff;
  color: #0f172a;
  padding: 0.26rem 0.46rem;
  font: inherit;
}

.case-manual-solve-viewport-controls__field select:focus {
  outline: none;
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.16);
}

.case-manual-solve-viewport-controls__grid .network-lookup-modal__checkbox {
  min-height: 1.55rem;
  margin: 0;
  font-size: 0.74rem;
  line-height: 1.2;
}


.case-manual-solve-evaluation-panel {
  display: grid;
  gap: 0.55rem;
  margin-top: 0.18rem;
  border: 1px solid #bfdbfe;
  border-radius: 16px;
  background:
    radial-gradient(circle at 0% 0%, rgba(59, 130, 246, 0.12), transparent 34%),
    linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
  padding: 0.66rem;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
}

.case-manual-solve-evaluation-panel__header {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
}

.case-manual-solve-evaluation-panel__header div {
  display: grid;
  gap: 0.08rem;
  min-width: 0;
}

.case-manual-solve-evaluation-panel__header span {
  color: #2563eb;
  font-size: 0.64rem;
  font-weight: 900;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.case-manual-solve-evaluation-panel__header strong {
  color: #0f172a;
  font-size: 0.95rem;
  line-height: 1.15;
}

.case-manual-solve-evaluation-panel__header small {
  color: #475569;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.3;
}

.case-manual-solve-evaluation-panel__close {
  position: relative;
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  width: 1.76rem;
  height: 1.76rem;
  min-width: 1.76rem;
  min-height: 1.76rem;
  padding: 0;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: #ffffff;
  color: #1d4ed8;
  cursor: pointer;
}

.case-manual-solve-evaluation-panel__close span,
.case-manual-solve-evaluation-panel__close span::before,
.case-manual-solve-evaluation-panel__close span::after {
  position: absolute;
  display: block;
  pointer-events: none;
}

.case-manual-solve-evaluation-panel__close span {
  inset: 0;
}

.case-manual-solve-evaluation-panel__close span::before,
.case-manual-solve-evaluation-panel__close span::after {
  content: '';
  left: 50%;
  top: 50%;
  width: 0.78rem;
  height: 0.12rem;
  border-radius: 999px;
  background: currentColor;
  transform-origin: center;
}

.case-manual-solve-evaluation-panel__close span::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.case-manual-solve-evaluation-panel__close span::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.case-manual-solve-evaluation-panel__empty {
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #ffffff;
  color: #64748b;
  padding: 0.7rem;
  font-size: 0.78rem;
  font-weight: 700;
}

.case-manual-solve-evaluation-panel__metrics {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(8.5rem, 0.7fr);
  gap: 0.48rem;
}

.case-manual-solve-evaluation-panel__metrics article,
.case-manual-solve-evaluation-panel__card {
  min-width: 0;
  border: 1px solid #dbeafe;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.56rem;
}

.case-manual-solve-evaluation-panel__metrics article {
  display: grid;
  gap: 0.1rem;
}

.case-manual-solve-evaluation-panel__metrics span,
.case-manual-solve-evaluation-panel__card header small {
  color: #64748b;
  font-size: 0.62rem;
  font-weight: 850;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.case-manual-solve-evaluation-panel__metrics strong {
  overflow: hidden;
  color: #0f172a;
  font-size: 0.94rem;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-solve-evaluation-panel__metrics small {
  color: #64748b;
  font-size: 0.68rem;
  line-height: 1.25;
}

.case-manual-solve-evaluation-panel__metric--percent strong {
  color: #1d4ed8;
  font-size: 1.72rem;
  letter-spacing: -0.04em;
}

.case-manual-solve-evaluation-panel__body {
  display: grid;
  grid-template-columns: minmax(14rem, 0.72fr) minmax(0, 1.28fr);
  gap: 0.55rem;
  min-height: 0;
}

.case-manual-solve-evaluation-panel__card {
  display: grid;
  gap: 0.46rem;
}

.case-manual-solve-evaluation-panel__card header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.35rem;
  align-items: baseline;
}

.case-manual-solve-evaluation-panel__card header strong {
  color: #0f172a;
  font-size: 0.82rem;
}

.case-manual-solve-evaluation-panel__card p {
  margin: 0;
  color: #64748b;
  font-size: 0.72rem;
}

.case-manual-solve-evaluation-panel__token-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.case-manual-solve-evaluation-panel__token-list span {
  display: grid;
  gap: 0.02rem;
  min-width: 6.8rem;
  padding: 0.38rem 0.45rem;
  border: 1px solid #e0e7ff;
  border-radius: 11px;
  background: #f8fafc;
}

.case-manual-solve-evaluation-panel__token-list strong {
  color: #0f172a;
  font-size: 0.78rem;
}

.case-manual-solve-evaluation-panel__token-list small {
  color: #2563eb;
  font-size: 0.62rem;
  font-weight: 900;
}

.case-manual-solve-evaluation-panel__token-list em {
  color: #64748b;
  font-size: 0.66rem;
  font-style: normal;
  font-weight: 800;
}

.case-manual-solve-evaluation-panel__ending-list {
  display: grid;
  gap: 0.3rem;
  max-height: 11.8rem;
  overflow: auto;
  padding-right: 0.15rem;
}

.case-manual-solve-evaluation-panel__ending-list article {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 0.45rem;
  align-items: center;
  min-width: 0;
  padding: 0.42rem 0.48rem;
  border: 1px solid #e2e8f0;
  border-radius: 11px;
  background: #ffffff;
}

.case-manual-solve-evaluation-panel__ending-list article > div {
  display: grid;
  gap: 0.04rem;
  min-width: 0;
}

.case-manual-solve-evaluation-panel__ending-list strong,
.case-manual-solve-evaluation-panel__ending-list small,
.case-manual-solve-evaluation-panel__ending-list span,
.case-manual-solve-evaluation-panel__ending-list em {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-solve-evaluation-panel__ending-list strong {
  color: #0f172a;
  font-size: 0.72rem;
}

.case-manual-solve-evaluation-panel__ending-list small {
  color: #64748b;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.66rem;
}

.case-manual-solve-evaluation-panel__ending-list span {
  color: #334155;
  font-size: 0.7rem;
  font-weight: 850;
}

.case-manual-solve-evaluation-panel__ending-list em {
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 0.14rem 0.38rem;
  font-size: 0.66rem;
  font-style: normal;
  font-weight: 900;
}

.case-manual-solve-evaluation-panel__ending--issue em {
  background: #fff7ed;
  color: #9a3412;
}

.case-manual-solve-evaluation-panel__issues {
  border: 1px solid #fed7aa;
  border-radius: 12px;
  background: #fff7ed;
  color: #7c2d12;
  padding: 0.45rem 0.55rem;
}

.case-manual-solve-evaluation-panel__issues summary {
  display: flex;
  justify-content: space-between;
  gap: 0.6rem;
  cursor: pointer;
  font-size: 0.76rem;
  font-weight: 900;
}

.case-manual-solve-evaluation-panel__issues summary small {
  color: #9a3412;
  font-size: 0.66rem;
}

.case-manual-solve-evaluation-panel__issues ul {
  display: grid;
  gap: 0.3rem;
  margin: 0.45rem 0 0;
  padding-left: 1.05rem;
  color: #7c2d12;
  font-size: 0.72rem;
  line-height: 1.35;
}

.case-manual-solve-evaluation-panel__issues li small {
  display: block;
  color: #9a3412;
}

.case-manual-solve-evaluation-panel__heatmap {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(10rem, 0.48fr) auto;
  gap: 0.45rem;
  align-items: center;
  min-width: 0;
  padding: 0.48rem 0.52rem;
  border: 1px solid #dbeafe;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.84);
}

.case-manual-solve-evaluation-panel__heatmap.is-active {
  border-color: rgba(168, 85, 247, 0.48);
  background:
    radial-gradient(circle at 0% 0%, rgba(168, 85, 247, 0.15), transparent 38%),
    linear-gradient(180deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.92));
  color: #e5e7eb;
}

.case-manual-solve-evaluation-panel__heatmap-main {
  display: grid;
  gap: 0.08rem;
  min-width: 0;
}

.case-manual-solve-evaluation-panel__heatmap-toggle {
  min-height: 1.2rem;
  margin: 0;
  font-size: 0.76rem;
}

.case-manual-solve-evaluation-panel__heatmap.is-active .case-manual-solve-evaluation-panel__heatmap-toggle {
  color: #f8fafc;
}

.case-manual-solve-evaluation-panel__heatmap-main small {
  overflow: hidden;
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 750;
  line-height: 1.22;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-solve-evaluation-panel__heatmap.is-active .case-manual-solve-evaluation-panel__heatmap-main small {
  color: #cbd5e1;
}

.case-manual-solve-evaluation-panel__heatmap-slider {
  display: grid;
  gap: 0.12rem;
  min-width: 0;
  color: #475569;
  font-size: 0.66rem;
  font-weight: 850;
}

.case-manual-solve-evaluation-panel__heatmap.is-active .case-manual-solve-evaluation-panel__heatmap-slider {
  color: #e2e8f0;
}

.case-manual-solve-evaluation-panel__heatmap-slider input {
  width: 100%;
}

.case-manual-solve-evaluation-panel__heatmap-legend {
  display: flex;
  align-items: center;
  gap: 0.08rem;
  min-width: 8.6rem;
  justify-content: flex-end;
}

.case-manual-solve-evaluation-panel__heatmap-legend span {
  width: 0.9rem;
  height: 1.4rem;
  border: 1px solid rgba(15, 23, 42, 0.24);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28);
}

.case-manual-solve-evaluation-panel__heatmap-legend span:first-child {
  border-radius: 999px 0 0 999px;
}

.case-manual-solve-evaluation-panel__heatmap-legend span:last-child {
  border-radius: 0 999px 999px 0;
}

.case-investigation-visualizer-viewport.is-manual-heat-map {
  background:
    radial-gradient(circle at 18% 8%, rgba(124, 58, 237, 0.18), transparent 30%),
    radial-gradient(circle at 78% 24%, rgba(220, 38, 38, 0.12), transparent 28%),
    linear-gradient(180deg, #020617 0%, #0f172a 100%);
}

.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-bg {
  fill: #020617;
}

.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-edge {
  opacity: 0.96;
  stroke-width: 4.2;
  filter: drop-shadow(0 0 0.18rem rgba(255, 255, 255, 0.16));
}

.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node.is-heat-map-colored .case-flow-visual-tx-rect,
.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node.is-heat-map-colored .case-flow-visual-address-circle,
.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node.is-heat-map-colored .case-flow-visual-amount-rect {
  stroke: rgba(248, 250, 252, 0.92);
  stroke-width: 3.6;
  filter:
    drop-shadow(0 0 0.18rem rgba(15, 23, 42, 0.9))
    drop-shadow(0 0 0.48rem rgba(255, 255, 255, 0.16));
}

.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node.is-heat-map-over-initial .case-flow-visual-tx-rect,
.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node.is-heat-map-over-initial .case-flow-visual-address-circle,
.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node.is-heat-map-over-initial .case-flow-visual-amount-rect {
  filter:
    drop-shadow(0 0 0.12rem rgba(255, 255, 255, 0.94))
    drop-shadow(0 0 0.58rem rgba(168, 85, 247, 0.62));
}

.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node.is-heat-map-error .case-flow-visual-tx-rect,
.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node.is-heat-map-error .case-flow-visual-address-circle,
.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node.is-heat-map-error .case-flow-visual-amount-rect {
  stroke-dasharray: 9 7;
  filter:
    drop-shadow(0 0 0.16rem rgba(255, 255, 255, 0.92))
    drop-shadow(0 0 0.62rem rgba(232, 121, 249, 0.68));
}

.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node.is-heat-map-zero .case-flow-visual-tx-rect,
.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node.is-heat-map-zero .case-flow-visual-address-circle,
.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node.is-heat-map-zero .case-flow-visual-amount-rect {
  filter:
    drop-shadow(0 0 0.16rem rgba(255, 255, 255, 0.9))
    drop-shadow(0 0 0.62rem rgba(56, 189, 248, 0.68));
}

.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-node text {
  fill: #f8fafc;
  stroke: rgba(2, 6, 23, 0.72);
  stroke-width: 3px;
  paint-order: stroke fill;
}

.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-path-bg {
  fill: rgba(15, 23, 42, 0.92);
  stroke: rgba(248, 250, 252, 0.72);
}

.case-investigation-visualizer-viewport.is-manual-heat-map .case-flow-visual-address-label {
  fill: #e0f2fe;
  stroke: rgba(2, 6, 23, 0.92);
  stroke-width: 3.5px;
  paint-order: stroke fill;
}

@media (max-width: 940px) {
  .case-manual-solve-evaluation-panel__metrics,
  .case-manual-solve-evaluation-panel__body,
  .case-manual-solve-evaluation-panel__heatmap {
    grid-template-columns: 1fr;
  }

  .case-manual-solve-evaluation-panel__heatmap-legend {
    justify-content: stretch;
  }

  .case-manual-solve-evaluation-panel__heatmap-legend span {
    flex: 1 1 auto;
  }

  .case-manual-solve-evaluation-panel__ending-list article {
    grid-template-columns: 1fr;
    align-items: start;
  }
}

.case-manual-start-hop-inspector__hidden-tool {
  position: absolute;
  width: 1px;
  height: 1px;
  min-width: 1px;
  min-height: 1px;
  overflow: hidden;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  clip-path: inset(50%);
  contain: strict;
}

.case-investigation-visualizer-viewport {
  contain: layout paint;
}

.case-investigation-visualizer-svg {
  touch-action: none;
  user-select: none;
}

.case-investigation-visualizer-viewport.is-camera-moving .case-manual-start-automation-callout-layer {
  visibility: hidden;
}

.case-investigation-visualizer-viewport.is-camera-moving .case-investigation-visualizer-svg {
  shape-rendering: optimizeSpeed;
  text-rendering: optimizeSpeed;
}

.case-investigation-visualizer-viewport.is-camera-moving .case-flow-visual-edge,
.case-investigation-visualizer-viewport.is-camera-moving .case-flow-visual-tx-rect,
.case-investigation-visualizer-viewport.is-camera-moving .case-flow-visual-address-circle,
.case-investigation-visualizer-viewport.is-camera-moving .case-flow-visual-amount-rect,
.case-investigation-visualizer-viewport.is-camera-moving .case-flow-visual-manual-work-bubble,
.case-investigation-visualizer-viewport.is-camera-moving .case-flow-visual-report-boundary-bubble,
.case-investigation-visualizer-viewport.is-camera-moving .case-flow-visual-dormant-glow,
.case-investigation-visualizer-viewport.is-camera-moving .case-flow-visual-address-alert-ring,
.case-investigation-visualizer-viewport.is-camera-moving .case-investigation-visualizer-address-selected-ring,
.case-investigation-visualizer-viewport.is-camera-moving .case-manual-start-work-indicator * {
  filter: none !important;
}

.case-investigation-visualizer-viewport.is-camera-moving .case-manual-start-work-indicator * {
  animation-play-state: paused !important;
}


.case-flow-visual-address-alert-ring {
  fill: none;
  stroke: #dc2626;
  stroke-width: 5px;
  stroke-dasharray: 7 4;
  filter: drop-shadow(0 0 6px rgba(220, 38, 38, 0.38));
  pointer-events: none;
}

.case-flow-visual-node.is-selected .case-flow-visual-address-alert-ring,
.case-investigation-visualizer-node.is-selected .case-flow-visual-address-alert-ring {
  stroke-width: 6px;
}


.case-flow-visual-address-hexagon {
  stroke-linejoin: round;
}

.case-flow-visual-address-circle-swap,
.case-flow-visual-address-circle-contract,
.case-flow-visual-address-circle-swap-related {
  fill: #7c3aed;
  stroke: #4c1d95;
  stroke-width: 5px;
  filter: drop-shadow(0 0 8px rgba(124, 58, 237, 0.32));
}

.case-flow-visual-address-label-swap,
.case-flow-visual-address-label-contract,
.case-flow-visual-address-label-swap-related {
  fill: #6d28d9;
  font-weight: 900;
}

.case-flow-visual-address-circle-unknown-service {
  fill: #2563eb;
  stroke: #dc2626;
  stroke-width: 6px;
}

.case-flow-visual-node--unknown-service .case-flow-visual-address-circle {
  stroke: #dc2626;
  stroke-width: 5px;
  filter: drop-shadow(0 0 8px rgba(220, 38, 38, 0.45));
}

.case-flow-visual-address-label-unknown-service {
  fill: #b91c1c;
  font-weight: 800;
}

.case-flow-visual-node--unknown-service .case-flow-visual-address-text {
  fill: #ffffff;
  font-weight: 800;
}


.case-flow-visual-address-circle-address-poisoning {
  fill: #fdf2f8;
  stroke: #f59e0b;
  stroke-width: 6px;
}

.case-flow-visual-node--address-poisoning .case-flow-visual-address-circle {
  stroke: #f59e0b;
  stroke-width: 6px;
  stroke-dasharray: 9 4;
  filter: drop-shadow(0 0 9px rgba(219, 39, 119, 0.48));
}

.case-flow-visual-node--address-poisoning .case-flow-visual-address-alert-ring {
  stroke: #db2777;
  stroke-dasharray: 5 4;
  filter: drop-shadow(0 0 7px rgba(219, 39, 119, 0.48));
}

.case-flow-visual-address-label-address-poisoning {
  fill: #be185d;
  font-weight: 900;
}

.case-flow-visual-node--address-poisoning .case-flow-visual-address-text {
  fill: #831843;
  font-weight: 900;
}


.case-flow-visual-node--manualgroup,
.case-flow-visual-node--manualGroup {
  cursor: grab;
  pointer-events: all;
}

.case-flow-visual-node--manualgroup:active,
.case-flow-visual-node--manualGroup:active {
  cursor: grabbing;
}

.case-flow-visual-node--manualgroup .case-manual-solve-group-node__shadow,
.case-flow-visual-node--manualGroup .case-manual-solve-group-node__shadow {
  fill: rgba(15, 23, 42, 0.18);
  pointer-events: none;
}

.case-manual-solve-group-node__body {
  fill: #4f46e5;
  stroke: #312e81;
  stroke-width: 3px;
  vector-effect: non-scaling-stroke;
  filter: drop-shadow(0 10px 18px rgba(49, 46, 129, 0.2));
  pointer-events: all;
}

.case-manual-solve-group-node__inner {
  fill: rgba(255, 255, 255, 0.16);
  stroke: rgba(255, 255, 255, 0.66);
  stroke-width: 1.5px;
  vector-effect: non-scaling-stroke;
  pointer-events: none;
}

.case-flow-visual-node--manualgroup.is-selected .case-manual-solve-group-node__body,
.case-flow-visual-node--manualGroup.is-selected .case-manual-solve-group-node__body {
  stroke: #0f172a;
  stroke-width: 4.5px;
  filter: drop-shadow(0 0 0.32rem rgba(59, 130, 246, 0.55)) drop-shadow(0 12px 20px rgba(15, 23, 42, 0.22));
}

.case-manual-solve-group-node__title,
.case-manual-solve-group-node__meta,
.case-manual-solve-group-node__hint {
  fill: #ffffff;
  paint-order: stroke;
  stroke: rgba(15, 23, 42, 0.38);
  stroke-width: 2px;
  stroke-linejoin: round;
  font-weight: 900;
  pointer-events: none;
}

.case-manual-solve-group-node__hitbox {
  fill: transparent;
  stroke: transparent;
  pointer-events: all;
}

.case-manual-solve-group-node__title {
  font-size: 17px;
}

.case-manual-solve-group-node__meta,
.case-manual-solve-group-node__hint {
  font-size: 11px;
  opacity: 0.95;
}

.case-manual-solve-expanded-group-panel {
  filter: drop-shadow(0 18px 36px rgba(15, 23, 42, 0.22));
}

.case-manual-solve-expanded-group-panel__backdrop {
  fill: rgba(248, 250, 252, 0.98);
  stroke: rgba(59, 130, 246, 0.55);
  stroke-width: 2.5px;
  vector-effect: non-scaling-stroke;
}

.case-manual-solve-expanded-group-panel__header {
  fill: rgba(219, 234, 254, 0.94);
}

.case-manual-solve-expanded-group-panel__title {
  fill: #1e3a8a;
  font-size: 19px;
  font-weight: 950;
}

.case-manual-solve-expanded-group-panel__summary {
  fill: #475569;
  font-size: 12px;
  font-weight: 800;
}

.case-manual-solve-expanded-group-panel__close rect {
  fill: rgba(255, 255, 255, 0.96);
  stroke: rgba(37, 99, 235, 0.3);
  stroke-width: 1.4px;
}

.case-manual-solve-expanded-group-panel__close line {
  stroke: #1d4ed8;
  stroke-width: 2.5px;
  stroke-linecap: round;
}

.case-manual-solve-expanded-group-panel__member rect {
  fill: #ffffff;
  stroke: rgba(148, 163, 184, 0.74);
  stroke-width: 1.4px;
  vector-effect: non-scaling-stroke;
}

.case-manual-solve-expanded-group-panel__member-title {
  fill: #0f172a;
  font-size: 12px;
  font-weight: 900;
}

.case-manual-solve-expanded-group-panel__member-meta,
.case-manual-solve-expanded-group-panel__empty {
  fill: #64748b;
  font-size: 10px;
  font-weight: 760;
}

.case-manual-solve-visual-group-card__controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.45rem;
  align-items: end;
}

.case-manual-solve-visual-group-card__controls label {
  display: grid;
  gap: 0.18rem;
  color: #475569;
  font-size: 0.66rem;
  font-weight: 850;
}

.case-manual-solve-visual-group-card__controls input[type='text'] {
  min-height: 2rem;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 0.32rem 0.48rem;
  color: #0f172a;
  font: 0.78rem/1.2 inherit;
}

.case-manual-solve-visual-group-card__controls input[type='color'] {
  width: 2.7rem;
  height: 2rem;
  min-height: 2rem;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #ffffff;
  padding: 0.12rem;
}


.case-manual-solve-selected-value {
  display: inline-flex;
  gap: 0.34rem;
  align-items: center;
  max-width: 100%;
}

.case-manual-solve-selected-value__edit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.28rem;
  height: 1.28rem;
  min-width: 1.28rem;
  min-height: 1.28rem;
  padding: 0;
  border: 1px solid rgba(37, 99, 235, 0.28);
  border-radius: 7px;
  background: rgba(239, 246, 255, 0.96);
  color: #1d4ed8;
  line-height: 0;
  cursor: pointer;
}

.case-manual-solve-selected-value__edit-icon {
  display: block;
  width: 0.86rem;
  height: 0.86rem;
  flex: 0 0 auto;
  fill: currentColor;
  transform: translateY(-0.01rem);
}

.case-manual-solve-selected-value__edit:hover:not(:disabled),
.case-manual-solve-selected-value__edit:focus-visible:not(:disabled) {
  border-color: #2563eb;
  background: #dbeafe;
  outline: none;
}

.case-manual-solve-selected-value__edit:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.case-manual-solve-amount-editor {
  display: grid;
  gap: 0.55rem;
  margin-top: 0.65rem;
  padding: 0.68rem;
  border: 1px solid rgba(37, 99, 235, 0.25);
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.96), rgba(255, 255, 255, 0.98));
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.08);
}

.case-manual-solve-amount-editor__header {
  display: flex;
  justify-content: space-between;
  gap: 0.65rem;
  align-items: flex-start;
}

.case-manual-solve-amount-editor__header strong,
.case-manual-solve-amount-editor__header small {
  display: block;
}

.case-manual-solve-amount-editor__header strong {
  color: #0f172a;
  font-size: 0.82rem;
}

.case-manual-solve-amount-editor__header small,
.case-manual-solve-amount-editor__help {
  margin: 0.12rem 0 0;
  color: #475569;
  font-size: 0.68rem;
  line-height: 1.3;
}

.case-manual-solve-amount-editor__close {
  position: relative;
  display: grid;
  place-items: center;
  width: 1.58rem;
  height: 1.58rem;
  min-width: 1.58rem;
  min-height: 1.58rem;
  border: 1px solid rgba(37, 99, 235, 0.22);
  border-radius: 999px;
  background: #ffffff;
  color: #1d4ed8;
  cursor: pointer;
}

.case-manual-solve-amount-editor__close span::before,
.case-manual-solve-amount-editor__close span::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0.72rem;
  height: 0.11rem;
  border-radius: 999px;
  background: currentColor;
  transform-origin: center;
}

.case-manual-solve-amount-editor__close span::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.case-manual-solve-amount-editor__close span::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.case-manual-solve-amount-editor__field {
  display: grid;
  gap: 0.25rem;
  color: #334155;
  font-size: 0.68rem;
  font-weight: 850;
}

.case-manual-solve-amount-editor__input-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.35rem;
  align-items: center;
}

.case-manual-solve-amount-editor__input-row input {
  width: 100%;
  min-width: 0;
  min-height: 2rem;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #ffffff;
  color: #0f172a;
  font: 0.82rem/1.2 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  padding: 0.34rem 0.5rem;
}

.case-manual-solve-amount-editor__input-row input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  outline: none;
}

.case-manual-solve-amount-editor__input-row span {
  display: inline-flex;
  align-items: center;
  min-height: 2rem;
  padding: 0 0.48rem;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.72rem;
  font-weight: 900;
}

.case-manual-solve-amount-editor__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  justify-content: flex-end;
}

.case-manual-solve-amount-editor__status {
  margin: 0;
  padding: 0.45rem 0.52rem;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 0.7rem;
  font-weight: 800;
  line-height: 1.3;
}

.case-workflow-complete-backdrop {
  z-index: 5605;
}

.case-workflow-complete-modal {
  width: min(34rem, calc(100vw - 2rem));
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.9rem;
  align-items: start;
  padding: 1.1rem;
  border-color: #bbf7d0;
  background:
    radial-gradient(circle at 8% 0%, rgba(34, 197, 94, 0.18), transparent 36%),
    linear-gradient(180deg, #ffffff 0%, #f8fff9 100%);
}

.case-workflow-complete-modal__badge {
  display: grid;
  place-items: center;
  width: 2.65rem;
  height: 2.65rem;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  font-size: 1.35rem;
  font-weight: 950;
  box-shadow: inset 0 0 0 1px #86efac;
}

.case-workflow-complete-modal__body {
  min-width: 0;
}

.case-workflow-complete-modal__body h3 {
  margin: 0;
  color: #0f172a;
  font-size: 1.18rem;
}

.case-workflow-complete-modal__body p:last-child {
  margin: 0.35rem 0 0;
  color: #475569;
  line-height: 1.45;
}

.case-workflow-complete-modal__actions {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 0.42rem;
  justify-content: flex-end;
}


.agent-modal.case-investigation-visualizer-modal--manual-start:not(.case-investigation-visualizer-modal--fullscreen) .template-deliverable-modal__preview-header.case-investigation-visualizer-modal__viewport-toolbar > .case-investigation-visualizer-modal__viewport-actions {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
}

.case-manual-solve-get-data__toolbar-button {
  font-weight: 900;
  letter-spacing: 0.04em;
}

.case-manual-solve-get-data-panel {
  position: absolute;
  z-index: 92;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  width: min(640px, calc(100% - 1.5rem));
  height: min(520px, calc(100% - 1.5rem));
  min-width: min(29rem, calc(100% - 1.5rem));
  min-height: min(22rem, calc(100% - 1.5rem));
  overflow: hidden;
  border: 1px solid rgba(59, 130, 246, 0.25);
  border-radius: 18px;
  background: rgba(248, 250, 252, 0.97);
  box-shadow: 0 24px 56px rgba(15, 23, 42, 0.24);
  backdrop-filter: blur(14px);
}

.case-manual-solve-get-data-panel.is-dragging {
  user-select: none;
  box-shadow: 0 28px 68px rgba(15, 23, 42, 0.3);
}

.case-manual-solve-get-data-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.7rem;
  min-width: 0;
  padding: 0.72rem 0.78rem 0.58rem;
  border-bottom: 1px solid rgba(191, 219, 254, 0.95);
  background:
    radial-gradient(circle at 8% 0%, rgba(59, 130, 246, 0.18), transparent 38%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(239, 246, 255, 0.94));
  cursor: move;
}

.case-manual-solve-get-data-panel__title {
  display: grid;
  gap: 0.1rem;
  min-width: 0;
}

.case-manual-solve-get-data-panel__title strong {
  color: #0f172a;
  font-size: 0.92rem;
  font-weight: 950;
  letter-spacing: 0.06em;
}

.case-manual-solve-get-data-panel__title small {
  overflow: hidden;
  color: #475569;
  font-size: 0.68rem;
  font-weight: 750;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-solve-get-data-panel__close {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 1.72rem;
  height: 1.72rem;
  min-width: 1.72rem;
  min-height: 1.72rem;
  margin: -0.12rem -0.16rem 0 0;
  padding: 0;
  border: 1px solid rgba(37, 99, 235, 0.22);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #1d4ed8;
  appearance: none;
  cursor: pointer;
}

.case-manual-solve-get-data-panel__close span,
.case-manual-solve-get-data-panel__close span::before,
.case-manual-solve-get-data-panel__close span::after {
  position: absolute;
  display: block;
  pointer-events: none;
}

.case-manual-solve-get-data-panel__close span {
  inset: 0;
}

.case-manual-solve-get-data-panel__close span::before,
.case-manual-solve-get-data-panel__close span::after {
  content: '';
  left: 50%;
  top: 50%;
  width: 0.76rem;
  height: 0.12rem;
  border-radius: 999px;
  background: currentColor;
  transform-origin: center;
}

.case-manual-solve-get-data-panel__close span::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.case-manual-solve-get-data-panel__close span::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.case-manual-solve-get-data-panel__close:hover,
.case-manual-solve-get-data-panel__close:focus-visible {
  border-color: #2563eb;
  background: #ffffff;
  outline: none;
}

.case-manual-solve-get-data-panel__textarea {
  width: calc(100% - 1.16rem);
  min-width: 0;
  min-height: 0;
  margin: 0.58rem;
  resize: none;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 0.58rem 0.64rem;
  background: #ffffff;
  color: #0f172a;
  font: 0.72rem/1.45 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  tab-size: 2;
  white-space: pre;
  overflow: auto;
}

.case-manual-solve-get-data-panel__textarea:focus {
  border-color: #2563eb;
  outline: 2px solid rgba(37, 99, 235, 0.15);
  outline-offset: 1px;
}

.case-manual-solve-get-data-panel__footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.62rem 0.62rem;
  border-top: 1px solid rgba(226, 232, 240, 0.95);
  background: rgba(255, 255, 255, 0.82);
}

.case-manual-solve-get-data-panel__status {
  min-width: min(100%, 18rem);
  color: #475569;
  font-size: 0.68rem;
  font-weight: 750;
  line-height: 1.25;
}

.case-manual-solve-get-data-panel__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.28rem;
}

.case-manual-solve-get-data-panel__actions .toolbar-btn {
  min-height: 1.48rem;
  padding: 0.13rem 0.42rem;
  font-size: 0.66rem;
}


.header-action-btn--monitoring,
.toolbar-btn--monitoring {
  border-color: #0891b2;
  background: linear-gradient(135deg, #ecfeff 0%, #dbeafe 100%);
  color: #0e7490;
}

.header-action-btn--monitoring:hover:not(:disabled),
.header-action-btn--monitoring:focus-visible:not(:disabled),
.toolbar-btn--monitoring:hover:not(:disabled),
.toolbar-btn--monitoring:focus-visible:not(:disabled) {
  border-color: #0e7490;
  background: linear-gradient(135deg, #cffafe 0%, #bfdbfe 100%);
  color: #155e75;
  box-shadow: 0 10px 22px rgba(14, 116, 144, 0.16);
}

.toolbar-btn--compact {
  min-height: 1.45rem;
  padding: 0.12rem 0.42rem;
  font-size: 0.66rem;
}

.case-address-monitoring-modal-backdrop {
  z-index: 5650;
}

.agent-modal.case-address-monitoring-modal {
  width: min(1480px, calc(100vw - 1.25rem));
  max-height: min(94vh, 980px);
  overflow: auto;
  padding: 1rem;
}


.case-simple-launch__full-workspace {
  margin-left: auto;
}

@media (max-width: 760px) {
  .case-simple-launch__full-workspace {
    margin-left: 0;
  }
}

.case-manual-solve-bridge-edge-badge {
  pointer-events: none;
}

.case-manual-solve-bridge-edge-badge rect {
  fill: #7c3aed;
  stroke: #111827;
  stroke-width: 2.4;
  filter: drop-shadow(0 5px 10px rgba(15, 23, 42, 0.18));
}

.case-manual-solve-bridge-edge-badge text {
  fill: #ffffff;
  stroke: #111827;
  stroke-width: 3px;
  paint-order: stroke;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.case-flow-visual-edge.is-bridge-edge {
  stroke: #7c3aed !important;
  stroke-width: 4.8 !important;
}



.case-manual-solve-selected-value--fifo {
  align-items: center;
  gap: 0.45rem;
}

.case-manual-solve-selected-value__inspect {
  appearance: none;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  cursor: pointer;
  font: inherit;
  font-size: 0.64rem;
  font-weight: 850;
  padding: 0.14rem 0.48rem;
  white-space: nowrap;
}

.case-manual-solve-selected-value__inspect:hover:not(:disabled),
.case-manual-solve-selected-value__inspect:focus-visible:not(:disabled) {
  border-color: #2563eb;
  background: #dbeafe;
}

.case-manual-solve-selected-value__inspect:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

/* FIFO inspector: viewport-wide modal and compact selected-node diagnostics */
.case-manual-solve-selected-value__text {
  min-width: 0;
  overflow-wrap: anywhere;
}

.case-flow-visuals-detail-table td {
  overflow-wrap: anywhere;
}

.case-manual-fifo-inspector-modal {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: grid;
  place-items: stretch;
  padding: clamp(0.45rem, 1.2vw, 1rem);
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(4px);
}

.case-manual-fifo-inspector {
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 0.82rem;
  justify-self: center;
  align-self: center;
  width: min(1720px, calc(100vw - 1rem));
  height: min(1040px, calc(100vh - 1rem));
  max-height: calc(100vh - 1rem);
  overflow: hidden;
  border: 1px solid #cbd5e1;
  border-radius: 24px;
  background: #f8fafc;
  box-shadow: 0 28px 90px rgba(15, 23, 42, 0.38);
  color: #0f172a;
}

.case-manual-fifo-inspector__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  padding: 1rem 1.1rem 0;
}

.case-manual-fifo-inspector__header strong {
  display: block;
  font-size: 1rem;
  font-weight: 900;
}

.case-manual-fifo-inspector__header small {
  color: #64748b;
  font-size: 0.74rem;
  font-weight: 700;
}

.case-manual-fifo-inspector__header-actions {
  display: inline-flex;
  gap: 0.4rem;
  align-items: center;
  justify-content: flex-end;
}

.case-manual-fifo-inspector__close {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #ffffff;
  color: #334155;
  cursor: pointer;
  font-size: 1.25rem;
  font-weight: 900;
  line-height: 1;
}

.case-manual-fifo-inspector__cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: 0.55rem;
  padding: 0 1.1rem;
}

.case-manual-fifo-inspector__card {
  display: grid;
  gap: 0.14rem;
  min-width: 0;
  padding: 0.72rem;
  border: 1px solid #dbe7f3;
  border-radius: 16px;
  background: #ffffff;
}

.case-manual-fifo-inspector__card span {
  color: #64748b;
  font-size: 0.64rem;
  font-weight: 850;
  letter-spacing: 0.035em;
  text-transform: uppercase;
}

.case-manual-fifo-inspector__card strong {
  overflow: hidden;
  color: #0f172a;
  font-size: 0.86rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-fifo-inspector__card small,
.case-manual-fifo-inspector__stage small {
  color: #64748b;
  font-size: 0.68rem;
  line-height: 1.32;
}

.case-manual-fifo-inspector__body {
  display: grid;
  grid-template-columns: minmax(22rem, 0.92fr) minmax(32rem, 1.08fr);
  gap: 0.82rem;
  min-height: 0;
  overflow: hidden;
  padding: 0 1.1rem 1.1rem;
}

.case-manual-fifo-inspector__overview,
.case-manual-fifo-inspector__tables {
  display: grid;
  align-content: start;
  gap: 0.7rem;
  min-height: 0;
  overflow: auto;
  padding-right: 0.18rem;
}

.case-manual-fifo-inspector__section-heading {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.35rem 0.75rem;
  align-items: baseline;
}

.case-manual-fifo-inspector__section-heading strong {
  color: #0f172a;
  font-size: 0.82rem;
  font-weight: 900;
}

.case-manual-fifo-inspector__section-heading small {
  color: #64748b;
  font-size: 0.66rem;
  line-height: 1.35;
}

.case-manual-fifo-inspector__equation {
  display: grid;
  gap: 0.52rem;
  padding: 0.7rem;
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 18px;
  background: linear-gradient(180deg, #eff6ff 0%, #ffffff 100%);
}

.case-manual-fifo-inspector__equation-chain {
  display: grid;
  gap: 0.46rem;
}

.case-manual-fifo-inspector__equation-step {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.5rem;
  align-items: stretch;
}

.case-manual-fifo-inspector__equation-step:first-child {
  grid-template-columns: minmax(0, 1fr);
}

.case-manual-fifo-inspector__equation-step > div {
  display: grid;
  gap: 0.12rem;
  min-width: 0;
  padding: 0.66rem 0.72rem;
  border: 1px solid #dbe7f3;
  border-left-width: 6px;
  border-radius: 15px;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.05);
}

.case-manual-fifo-inspector__equation-operator {
  display: grid;
  place-items: center;
  width: 1.85rem;
  border-radius: 999px;
  background: #111827;
  color: #ffffff;
  font-size: 0.92rem;
  font-weight: 950;
}

.case-manual-fifo-inspector__equation-step span:not(.case-manual-fifo-inspector__equation-operator) {
  color: #64748b;
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.case-manual-fifo-inspector__equation-step strong {
  overflow: hidden;
  color: #0f172a;
  font-size: 0.92rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-fifo-inspector__equation-step small {
  color: #64748b;
  font-size: 0.67rem;
  line-height: 1.3;
}

.case-manual-fifo-inspector__equation-step--prior > div { border-left-color: #38bdf8; }
.case-manual-fifo-inspector__equation-step--left > div { border-left-color: #64748b; }
.case-manual-fifo-inspector__equation-step--followed > div { border-left-color: #22c55e; }
.case-manual-fifo-inspector__equation-step--fees > div { border-left-color: #f97316; }

.case-manual-fifo-inspector__allocation {
  display: grid;
  gap: 0.46rem;
  padding: 0.7rem;
  border: 1px solid rgba(147, 197, 253, 0.7);
  border-radius: 18px;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
}

.case-manual-fifo-inspector__allocation-header {
  display: flex;
  flex-wrap: wrap;
  gap: 0.28rem 0.6rem;
  justify-content: space-between;
  align-items: baseline;
}

.case-manual-fifo-inspector__allocation-header strong {
  color: #0f172a;
  font-size: 0.8rem;
  font-weight: 900;
}

.case-manual-fifo-inspector__allocation-header small {
  color: #64748b;
  font-size: 0.66rem;
  line-height: 1.35;
}

.case-manual-fifo-bar {
  display: flex;
  min-height: 1.28rem;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.55);
  border-radius: 999px;
  background: #e2e8f0;
}

.case-manual-fifo-bar__segment { min-width: 0.4rem; transition: flex-basis 0.18s ease; }
.case-manual-fifo-bar__segment--prior { background: #bae6fd; }
.case-manual-fifo-bar__segment--left { background: #cbd5e1; }
.case-manual-fifo-bar__segment--followed { background: #86efac; }
.case-manual-fifo-bar__segment--fees { background: #fdba74; }

.case-manual-fifo-bar__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.case-manual-fifo-bar__legend-item {
  display: inline-flex;
  gap: 0.26rem;
  align-items: center;
  padding: 0.18rem 0.42rem;
  border: 1px solid rgba(203, 213, 225, 0.85);
  border-radius: 999px;
  background: #fff;
  color: #334155;
  font-size: 0.63rem;
  font-weight: 800;
}

.case-manual-fifo-bar__legend-item i {
  width: 0.58rem;
  height: 0.58rem;
  border-radius: 999px;
  background: #cbd5e1;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
}

.case-manual-fifo-bar__legend-item small { color: #64748b; font-weight: 800; }
.case-manual-fifo-bar__legend .case-manual-fifo-bar__segment--prior i { background: #bae6fd; }
.case-manual-fifo-bar__legend .case-manual-fifo-bar__segment--left i { background: #cbd5e1; }
.case-manual-fifo-bar__legend .case-manual-fifo-bar__segment--followed i { background: #86efac; }
.case-manual-fifo-bar__legend .case-manual-fifo-bar__segment--fees i { background: #fdba74; }

.case-manual-fifo-inspector__timeline {
  display: grid;
  gap: 0.55rem;
  min-height: 0;
  padding: 0;
}

.case-manual-fifo-inspector__stage {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.65rem;
  align-items: start;
  padding: 0.78rem;
  border: 1px solid #dbe7f3;
  border-radius: 18px;
  background: #ffffff;
}

.case-manual-fifo-inspector__stage-index {
  display: grid;
  place-items: center;
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 999px;
  background: #111827;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 900;
}

.case-manual-fifo-inspector__stage strong { display: block; margin-bottom: 0.15rem; color: #0f172a; font-size: 0.86rem; }
.case-manual-fifo-inspector__stage p { margin: 0 0 0.14rem; color: #334155; font-size: 0.76rem; font-weight: 750; line-height: 1.35; }

.case-manual-fifo-inspector__details {
  margin: 0;
  border: 1px solid #dbe7f3;
  border-radius: 16px;
  background: #ffffff;
  overflow: hidden;
}

.case-manual-fifo-inspector__details[open] { box-shadow: 0 10px 26px rgba(15, 23, 42, 0.05); }
.case-manual-fifo-inspector__details summary { cursor: pointer; padding: 0.62rem 0.78rem; color: #0f172a; font-size: 0.78rem; font-weight: 900; }
.case-manual-fifo-inspector__details pre { max-height: 18rem; overflow: auto; margin: 0; padding: 0.75rem; border-top: 1px solid #e2e8f0; background: #0f172a; color: #e2e8f0; font-size: 0.7rem; line-height: 1.45; white-space: pre-wrap; }
.case-manual-fifo-inspector__table-wrap { max-height: min(22rem, 52vh); overflow: auto; border-top: 1px solid #e2e8f0; }
.case-manual-fifo-inspector__table { width: 100%; border-collapse: collapse; font-size: 0.72rem; }
.case-manual-fifo-inspector__table th,
.case-manual-fifo-inspector__table td { padding: 0.45rem 0.65rem; border-bottom: 1px solid #eef2f7; vertical-align: top; text-align: left; }
.case-manual-fifo-inspector__table th { width: 12rem; color: #64748b; font-weight: 850; }
.case-manual-fifo-inspector__table--events th { width: auto; }
.case-manual-fifo-inspector__table code { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; color: #0f172a; font-size: 0.68rem; word-break: break-all; }
.case-manual-fifo-inspector__warnings { display: grid; gap: 0.4rem; max-height: 14rem; overflow: auto; margin: 0; padding: 0.7rem 1rem 0.85rem 1.55rem; border-top: 1px solid #e2e8f0; color: #334155; font-size: 0.73rem; line-height: 1.38; }

@media (max-width: 1180px) {
  .case-manual-fifo-inspector__body {
    grid-template-columns: 1fr;
    overflow: auto;
  }
}

@media (max-width: 920px) {
  .case-manual-fifo-inspector__cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

</style>

<style scoped src="../assets/css/case-buddy.css"></style>

<style scoped>
/* Simplified workflow labels keep their existing typography while becoming actions. */
.case-simple-launch__step .case-simple-launch__step-text-action {
  appearance: none;
  display: inline;
  width: auto;
  min-width: 0;
  height: auto;
  min-height: 0;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  color: inherit;
  cursor: pointer;
  font: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  line-height: inherit;
  text-align: inherit;
  text-transform: inherit;
  vertical-align: baseline;
}

.case-simple-launch__step .case-simple-launch__step-text-action:hover:not(:disabled),
.case-simple-launch__step .case-simple-launch__step-text-action:focus-visible:not(:disabled),
.case-simple-launch__step-button:hover:not(:disabled) > strong,
.case-simple-launch__step-button:focus-visible:not(:disabled) > strong {
  text-decoration: underline;
  text-decoration-thickness: 0.1em;
  text-underline-offset: 0.16em;
}

.case-simple-launch__step .case-simple-launch__step-text-action:focus-visible {
  border-radius: 3px;
  outline: 2px solid currentColor;
  outline-offset: 3px;
}

.case-simple-launch__step .case-simple-launch__step-text-action:disabled {
  cursor: not-allowed;
}

/* Match the simplified-view dimming only when this modal was opened there. */
.agent-modal-backdrop.starting-main-modal-backdrop--simple-launch {
  background: rgba(15, 23, 42, 0.72);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
}

/* Keep advanced case/file/check summaries available without making them the
   first thing a newer investigator has to process. */
.case-overview-panel__eyebrow-row {
  display: flex;
  min-width: 0;
  align-items: center;
}

.case-overview-panel__eyebrow-row .eyebrow-label {
  margin: 0;
  font-size: clamp(0.78rem, 1.15vw, 1.12rem); /*  font-size: clamp(0.98rem, 1.45vw, 1.22rem); */
  font-weight: 900;
  line-height: 1.1;
}

.case-header-action-stack {
  display: flex;
  min-width: 0;
  flex: 0 1 auto;
  align-items: flex-end;
  flex-direction: column;
  gap: 0.28rem;
}

.case-header-action-stack .case-header-actions {
  justify-content: flex-end;
}

.case-overview-details-toggle {
  appearance: none;
  display: inline-grid;
  width: 2.15rem;
  min-width: 2.15rem;
  height: 1.45rem;
  place-items: center;
  align-self: center;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  font: inherit;
  line-height: 1;
  transition: background 0.16s ease, color 0.16s ease;
}

.case-overview-details-toggle:hover {
  background: rgba(226, 232, 240, 0.72);
  color: #334155;
}

.case-overview-details-toggle:focus-visible {
  outline: 2px solid rgba(59, 130, 246, 0.45);
  outline-offset: 2px;
}

.case-overview-details-toggle__triangle {
  display: block;
  font-size: 0.9rem;
  font-weight: 900;
  line-height: 1;
}

.case-overview-extra-actions {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 0.48rem;
  flex-wrap: wrap;
}

@media (max-width: 920px) {
  .case-header-action-stack {
    width: 100%;
    align-items: stretch;
  }

  .case-header-action-stack .case-header-actions {
    justify-content: flex-start;
  }

  .case-overview-extra-actions {
    justify-content: flex-start;
  }
}

.output-sheet-foldout-toggle__chevron,
.case-section-foldout-toggle__chevron {
  display: inline-block;
  line-height: 1;
  transform: rotate(-90deg);
  transform-origin: center;
  transition: transform 0.16s ease;
}

.output-sheet-foldout-toggle__chevron.is-open,
.case-section-foldout-toggle__chevron.is-open {
  transform: rotate(0deg);
}

/* Output keeps its foldout control in one stable top-right position. Expanded
   spreadsheet tools use a second row, so opening the panel does not move the cursor target. */
.sheet-panel--output > .sheet-panel__header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.58rem 0.68rem;
}

.sheet-panel--output > .sheet-panel__header > .sheet-panel__heading {
  grid-column: 1;
  grid-row: 1;
  min-width: 0;
}

.sheet-panel--output > .sheet-panel__header > .sheet-panel__header-actions {
  grid-column: 1 / -1;
  grid-row: 2;
  width: 100%;
  min-width: 0;
  padding-right: 0;
  justify-content: flex-end;
}

.output-sheet-panel-toggle {
  position: static;
  z-index: auto;
  grid-column: 2;
  grid-row: 1;
  min-width: 7.85rem;
  align-self: center;
  justify-content: center;
  margin: 0;
}

/* Deliverables is an outer foldout around its four existing nested groups. */
.deliverables-panel > .sheet-panel__header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0.58rem 0.68rem;
}

.deliverables-panel > .sheet-panel__header > .sheet-panel__heading {
  grid-column: 1;
  grid-row: 1;
  min-width: 0;
}

.deliverables-panel > .sheet-panel__header > .sheet-panel__header-actions {
  grid-column: 2;
  grid-row: 1;
  min-width: 0;
  padding-right: 0;
}

.deliverables-panel-toggle {
  position: static;
  z-index: auto;
  grid-column: 3;
  grid-row: 1;
  min-width: 9.35rem;
  align-self: center;
  justify-content: center;
  margin: 0;
}

@media (max-width: 760px) {
  .sheet-panel--output > .sheet-panel__header,
  .deliverables-panel > .sheet-panel__header {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
  }

  .sheet-panel--output > .sheet-panel__header > .sheet-panel__header-actions,
  .deliverables-panel > .sheet-panel__header > .sheet-panel__header-actions {
    grid-column: 1 / -1;
    grid-row: 2;
    width: 100%;
    justify-content: flex-start;
  }

  .output-sheet-panel-toggle,
  .deliverables-panel-toggle {
    grid-column: 2;
    grid-row: 1;
    align-self: start;
  }
}

/* Output remains mounted so tools and project hydration keep their existing
   spreadsheet ref, while the visible panel folds down to one compact header. */
.sheet-panel.sheet-panel--output-collapsed {
  grid-template-rows: auto !important;
  height: auto !important;
  min-height: 0 !important;
}

.sheet-panel--output-collapsed .sheet-panel__header {
  margin-bottom: 0;
}

.output-sheet-heading-line {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
}

.output-sheet-heading-line h2 {
  margin: 0;
}

.output-sheet-foldout-summary {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.34);
  border-radius: 999px;
  background: #f8fafc;
  color: #64748b;
  padding: 0.2rem 0.48rem;
  font-size: 0.65rem;
  font-weight: 850;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.output-sheet-foldout-summary--has-data {
  border-color: rgba(59, 130, 246, 0.24);
  background: #eff6ff;
  color: #1d4ed8;
}

.output-sheet-foldout-summary--pending {
  border-color: rgba(245, 158, 11, 0.32);
  background: #fffbeb;
  color: #92400e;
}

.output-sheet-foldout-toggle,
.case-section-foldout-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
}

/* These outer foldouts keep their existing child controls mounted. In particular,
   the workflow's own nested details retain their open/closed state. */
.case-foldout-panel__content {
  display: contents;
}

.sheet-panel.case-foldout-panel--collapsed {
  grid-template-rows: auto !important;
  height: auto !important;
  min-height: 0 !important;
}

.case-foldout-panel--collapsed .sheet-panel__header {
  margin-bottom: 0;
}

/* The workflow foldout control occupies one stable top-right grid cell whether
   the section is open or closed. It stays in normal layout flow instead of
   hovering over the collapsed panel edge. */
.workflow-panel > .sheet-panel__header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0.58rem 0.68rem;
}

.workflow-panel > .sheet-panel__header > .sheet-panel__heading {
  grid-column: 1;
  grid-row: 1;
  min-width: 0;
}

.workflow-panel > .sheet-panel__header > .sheet-panel__header-actions {
  grid-column: 2;
  grid-row: 1;
  min-width: 0;
  padding-right: 0;
}

.workflow-panel:not(.case-foldout-panel--collapsed) > .sheet-panel__header > .sheet-panel__header-actions {
  grid-column: 1 / -1;
  grid-row: 2;
  width: 100%;
  justify-content: flex-end;
}

.case-workflow-panel-toggle {
  position: static;
  z-index: auto;
  grid-column: 3;
  grid-row: 1;
  min-width: 8.35rem;
  align-self: center;
  justify-content: center;
  margin: 0;
}

@media (max-width: 760px) {
  .workflow-panel > .sheet-panel__header {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    padding-top: 0;
  }

  .workflow-panel > .sheet-panel__header > .sheet-panel__heading {
    grid-column: 1;
    grid-row: 1;
  }

  .workflow-panel > .sheet-panel__header > .sheet-panel__header-actions,
  .workflow-panel:not(.case-foldout-panel--collapsed) > .sheet-panel__header > .sheet-panel__header-actions {
    grid-column: 1 / -1;
    grid-row: 2;
    width: 100%;
    padding-right: 0;
    justify-content: flex-start;
  }

  .case-workflow-panel-toggle {
    grid-column: 2;
    grid-row: 1;
    min-width: 8.35rem;
    align-self: start;
    margin-left: 0;
  }
}

.case-input-flow-story-view-toggle {
  display: inline-flex;
  flex: 0 0 10.75rem;
  width: 10.75rem;
  min-width: 10.75rem;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  white-space: nowrap;
}

/* Keep the add-root control inside the Case Workspace viewport. It overlays
   the lower-right corner without creating another footer row or changing the
   height of the Raw Data And Graph Build area below. */
#case-input .sheet-grid:not(.sheet-grid--fullscreen) {
  position: relative;
}

.case-workspace-add-root {
  position: absolute;
  z-index: 560;
  right: 0.62rem;
  bottom: 0.62rem;
  display: block;
  margin: 0;
  padding: 0;
  pointer-events: none;
}

.case-workspace-add-root__button {
  appearance: none;
  box-sizing: border-box;
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  place-items: center;
  border: 1px solid rgba(37, 99, 235, 0.3);
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #eff6ff 100%);
  box-shadow: 0 5px 14px rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
  cursor: pointer;
  line-height: 0;
  pointer-events: auto;
  touch-action: manipulation;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease, background 0.16s ease;
}

.case-workspace-add-root__icon {
  display: block;
  width: 1.35rem;
  height: 1.35rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.35;
  stroke-linecap: round;
}

.case-workspace-add-root__button:hover:not(:disabled) {
  border-color: rgba(37, 99, 235, 0.56);
  background: linear-gradient(180deg, #ffffff 0%, #dbeafe 100%);
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.18);
  transform: translateY(-1px);
}

.case-workspace-add-root__button:focus-visible {
  outline: 3px solid rgba(59, 130, 246, 0.34);
  outline-offset: 2px;
}

.case-workspace-add-root__button:disabled {
  cursor: not-allowed;
  opacity: 0.48;
  transform: none;
}

/* Read-only, lazily rendered Main Data story layer. The workbook remains mounted
   underneath and becomes visible again without changing its data or active tab. */
.sheet-grid--flow-story-open {
  position: relative;
  isolation: isolate;
  overflow: hidden;
}

.case-input-flow-story {
  position: absolute;
  inset: 0;
  z-index: 500;
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  border-radius: inherit;
  background:
    radial-gradient(circle at 12% 0%, rgba(219, 234, 254, 0.7), transparent 34%),
    linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
  color: #0f172a;
}

.case-input-flow-story__header {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;
  padding: 0.82rem 0.95rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.34);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.05);
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
}

.case-input-flow-story__heading {
  min-width: 0;
}

.case-input-flow-story__eyebrow {
  display: block;
  margin-bottom: 0.1rem;
  color: #2563eb;
  font-size: 0.65rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.case-input-flow-story__heading h3 {
  margin: 0;
  color: #0f172a;
  font-size: clamp(1rem, 1.6vw, 1.25rem);
  line-height: 1.15;
}

.case-input-flow-story__heading p {
  margin: 0.2rem 0 0;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.35;
}

.case-input-flow-story__header-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.5rem;
}

.case-input-flow-story__heat-legend {
  display: inline-flex;
  align-items: center;
  gap: 0.32rem;
  color: #64748b;
  font-weight: 800;
  white-space: nowrap;
}

.case-input-flow-story__heat-legend small {
  font-size: 0.6rem;
}

.case-input-flow-story__heat-legend i {
  display: block;
  width: 4.8rem;
  height: 0.38rem;
  border-radius: 999px;
  background: linear-gradient(90deg, #1d4ed8 0%, #60a5fa 35%, #dbeafe 45%, #fde047 55%, #f59e0b 72%, #dc2626 100%);
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.12);
}

.case-input-flow-story__heat-no-data {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  margin-left: 0.12rem;
}

.case-input-flow-story__heat-no-data-swatch {
  display: block;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: #94a3b8;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.16);
}

.case-input-flow-story__heat-toggle,
.case-input-flow-story__close {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  cursor: pointer;
  font: inherit;
  font-weight: 900;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease, background 0.16s ease;
}

.case-input-flow-story__heat-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  min-height: 2rem;
  border-radius: 999px;
  padding: 0.34rem 0.62rem;
  font-size: 0.7rem;
  white-space: nowrap;
}

.case-input-flow-story__heat-toggle.is-active {
  border-color: rgba(220, 38, 38, 0.34);
  background: #fff7ed;
  color: #9a3412;
}

.case-input-flow-story__heat-toggle-dot {
  width: 0.62rem;
  height: 0.62rem;
  border-radius: 999px;
  background: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.14);
}

.case-input-flow-story__heat-toggle.is-active .case-input-flow-story__heat-toggle-dot {
  background: linear-gradient(135deg, #2563eb, #fde047 50%, #dc2626);
  box-shadow: 0 0 0 2px rgba(249, 115, 22, 0.18);
}

.case-input-flow-story__close {
  display: grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
  border-radius: 10px;
  padding: 0;
  font-size: 1.15rem;
  line-height: 1;
}

.case-input-flow-story__heat-toggle:hover,
.case-input-flow-story__close:hover {
  border-color: #60a5fa;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.14);
  transform: translateY(-1px);
}

.case-input-flow-story__heat-toggle:focus-visible,
.case-input-flow-story__close:focus-visible,
.case-input-flow-story__empty:focus-visible,
.case-input-flow-story-card__retry:focus-visible {
  outline: 3px solid rgba(59, 130, 246, 0.34);
  outline-offset: 2px;
}

.case-input-flow-story__list {
  display: grid;
  flex: 1 1 auto;
  align-content: start;
  gap: 0.8rem;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0.85rem;
  scrollbar-gutter: stable;
  overscroll-behavior: contain;
}

.case-input-flow-story-card {
  display: grid;
  grid-template-columns: minmax(10rem, 13.5rem) minmax(0, 1fr);
  gap: 0.85rem;
  min-width: 0;
  min-height: 12.5rem;
  padding: 0.72rem;
  border: 1px solid rgba(148, 163, 184, 0.34);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.065);
  content-visibility: auto;
  contain-intrinsic-size: 215px;
}

.case-input-flow-story-card__meta {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  padding: 0.58rem 0.62rem;
  border-right: 1px solid #e2e8f0;
}

.case-input-flow-story-card__meta > span:first-child {
  color: #64748b;
  font-size: 0.64rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.case-input-flow-story-card__meta > strong {
  display: block;
  overflow: hidden;
  margin-top: 0.12rem;
  color: #0f172a;
  font-size: clamp(1.45rem, 3vw, 2.35rem);
  font-weight: 950;
  letter-spacing: -0.04em;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-input-flow-story-card__meta dl {
  display: grid;
  gap: 0.36rem;
  min-width: 0;
  margin: 0.8rem 0 0;
}

.case-input-flow-story-card__meta dl div {
  display: grid;
  grid-template-columns: 2.3rem minmax(0, 1fr);
  gap: 0.36rem;
  min-width: 0;
  align-items: baseline;
}

.case-input-flow-story-card__meta dt {
  color: #94a3b8;
  font-size: 0.61rem;
  font-weight: 900;
  text-transform: uppercase;
}

.case-input-flow-story-card__meta dd {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: #475569;
  font-size: 0.69rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-input-flow-story-card__meta code {
  color: inherit;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: inherit;
}

.case-input-flow-story-card__amount {
  align-self: flex-start;
  max-width: 100%;
  overflow: hidden;
  margin-top: 0.72rem;
  border: 1px solid rgba(37, 99, 235, 0.2);
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 0.26rem 0.52rem;
  font-size: 0.69rem;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-input-flow-story-card__stage {
  position: relative;
  display: grid;
  min-width: 0;
  min-height: 11rem;
  place-items: center;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background:
    linear-gradient(rgba(148, 163, 184, 0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(148, 163, 184, 0.08) 1px, transparent 1px),
    #f8fafc;
  background-size: 24px 24px;
}

.case-input-flow-story-card__stage.is-loading::after {
  position: absolute;
  inset: 0;
  content: '';
  pointer-events: none;
  background: linear-gradient(110deg, transparent 20%, rgba(255, 255, 255, 0.72) 46%, transparent 72%);
  transform: translateX(-100%);
  animation: case-input-flow-story-shimmer 1.25s ease-in-out infinite;
}

.case-input-flow-story-card__limit-note {
  position: absolute;
  top: 0.5rem;
  right: 0.55rem;
  z-index: 2;
  border: 1px solid rgba(148, 163, 184, 0.34);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  color: #64748b;
  padding: 0.22rem 0.42rem;
  font-size: 0.58rem;
  font-weight: 850;
  line-height: 1;
  pointer-events: none;
}

.case-input-flow-story-card__svg {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 11rem;
  padding: 0.52rem;
  overflow: visible;
}

.case-input-flow-story-card__edge {
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 3;
  opacity: 0.82;
  vector-effect: non-scaling-stroke;
}

.case-input-flow-story-card__node rect,
.case-input-flow-story-card__node circle {
  stroke-width: 2;
  vector-effect: non-scaling-stroke;
  filter: drop-shadow(0 3px 4px rgba(15, 23, 42, 0.16));
  transition: fill 0.18s ease, stroke 0.18s ease;
}

.case-input-flow-story-card__node.is-heat rect,
.case-input-flow-story-card__node.is-heat circle {
  stroke-width: 1.7;
}

.case-input-flow-story-card__node.is-ending rect,
.case-input-flow-story-card__node.is-ending circle {
  stroke-width: 3;
}

.case-input-flow-story-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: min(80%, 31rem);
  opacity: 0.42;
}

.case-input-flow-story-placeholder__node,
.case-input-flow-story__empty-visual i {
  display: block;
  flex: 0 0 auto;
  width: 1.7rem;
  height: 1.7rem;
  border: 2px solid #94a3b8;
  background: #cbd5e1;
}

.case-input-flow-story-placeholder__node.is-address,
.case-input-flow-story__empty-visual i.is-address {
  border-radius: 999px;
}

.case-input-flow-story-placeholder__node.is-transaction,
.case-input-flow-story__empty-visual i.is-transaction {
  width: 2.8rem;
  border-radius: 7px;
}

.case-input-flow-story-placeholder__edge,
.case-input-flow-story__empty-visual b {
  position: relative;
  display: block;
  flex: 1 1 4rem;
  height: 2px;
  background: #94a3b8;
}

.case-input-flow-story-placeholder__edge::after,
.case-input-flow-story__empty-visual b::after {
  position: absolute;
  top: 50%;
  right: -1px;
  width: 0;
  height: 0;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-left: 7px solid #94a3b8;
  content: '';
  transform: translateY(-50%);
}

.case-input-flow-story-card__retry {
  display: inline-flex;
  align-items: center;
  gap: 0.42rem;
  border: 1px solid #fca5a5;
  border-radius: 999px;
  background: #fff1f2;
  color: #be123c;
  padding: 0.45rem 0.72rem;
  cursor: pointer;
  font: inherit;
  font-size: 0.72rem;
  font-weight: 900;
}

.case-input-flow-story-card__solve {
  position: absolute;
  z-index: 4;
  right: 3.85rem;
  bottom: 0.58rem;
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  place-items: center;
  border: 1px solid rgba(37, 99, 235, 0.32);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 5px 14px rgba(15, 23, 42, 0.12);
  color: #1d4ed8;
  cursor: pointer;
  line-height: 0;
  backdrop-filter: blur(6px);
  transition: border-color 0.16s ease, box-shadow 0.16s ease, color 0.16s ease, transform 0.16s ease, background 0.16s ease;
}

.case-input-flow-story-card__solve svg {
  display: block;
  width: 1.42rem;
  height: 1.42rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.case-input-flow-story-card__solve:hover:not(:disabled) {
  border-color: rgba(37, 99, 235, 0.62);
  background: #eff6ff;
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.18);
  color: #1e40af;
  transform: translateY(-1px);
}

.case-input-flow-story-card__solve:focus-visible {
  outline: 3px solid rgba(59, 130, 246, 0.34);
  outline-offset: 2px;
}

.case-input-flow-story-card__solve:disabled {
  cursor: not-allowed;
  opacity: 0.45;
  transform: none;
}

.case-input-flow-story__empty {
  display: grid;
  width: 100%;
  min-height: 18rem;
  place-items: center;
  align-content: center;
  gap: 0.52rem;
  border: 1px dashed #94a3b8;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.72);
  color: #475569;
  padding: 1.4rem;
  cursor: pointer;
  font: inherit;
  text-align: center;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease, background 0.16s ease;
}

.case-input-flow-story__empty:hover {
  border-color: #60a5fa;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 16px 36px rgba(37, 99, 235, 0.12);
  transform: translateY(-1px);
}

.case-input-flow-story__empty-visual {
  display: flex;
  width: min(30rem, 88%);
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.45rem;
  opacity: 0.58;
}

.case-input-flow-story__empty strong {
  color: #0f172a;
  font-size: 1rem;
}

.case-input-flow-story__empty small {
  max-width: 34rem;
  color: #64748b;
  font-size: 0.74rem;
  font-weight: 700;
  line-height: 1.45;
}

.case-input-flow-story__empty em {
  margin-top: 0.2rem;
  border-radius: 999px;
  background: #2563eb;
  color: #ffffff;
  padding: 0.42rem 0.72rem;
  font-size: 0.72rem;
  font-style: normal;
  font-weight: 900;
}

.case-input-flow-story__empty--error {
  border-color: #fda4af;
  background: #fff1f2;
}

.case-input-flow-story__empty-icon {
  display: grid;
  width: 2.7rem;
  height: 2.7rem;
  place-items: center;
  border-radius: 999px;
  background: #ffffff;
  color: #be123c;
  font-size: 1.35rem;
  font-weight: 900;
}

.case-input-flow-story-skeleton {
  display: block;
  border-radius: 999px;
  background: #e2e8f0;
  animation: case-input-flow-story-pulse 1.35s ease-in-out infinite;
}

.case-input-flow-story-skeleton--eyebrow { width: 4.5rem; height: 0.48rem; }
.case-input-flow-story-skeleton--title { width: 6.8rem; height: 1.7rem; margin-top: 0.38rem; }
.case-input-flow-story-skeleton--line { width: 92%; height: 0.58rem; margin-top: 0.75rem; }
.case-input-flow-story-skeleton--short { width: 68%; margin-top: 0.38rem; }

.case-input-flow-story-restore > span:first-child {
  font-size: 0.92rem;
  line-height: 1;
}

@keyframes case-input-flow-story-pulse {
  0%, 100% { opacity: 0.52; }
  50% { opacity: 0.92; }
}

@keyframes case-input-flow-story-shimmer {
  to { transform: translateX(100%); }
}

@media (max-width: 980px) {
  .case-input-flow-story__heat-legend {
    display: none;
  }

  .case-input-flow-story-card {
    grid-template-columns: minmax(8.5rem, 10.5rem) minmax(0, 1fr);
  }
}

@media (max-width: 720px) {
  .case-input-flow-story__header {
    align-items: flex-start;
  }

  .case-input-flow-story__heading p {
    display: none;
  }

  .case-input-flow-story-card {
    grid-template-columns: 1fr;
  }

  .case-input-flow-story-card__meta {
    display: grid;
    grid-template-columns: minmax(5rem, auto) minmax(0, 1fr);
    gap: 0.35rem 0.75rem;
    align-items: center;
    border-right: 0;
    border-bottom: 1px solid #e2e8f0;
  }

  .case-input-flow-story-card__meta > strong {
    font-size: 1.55rem;
  }

  .case-input-flow-story-card__meta dl {
    grid-column: 1 / -1;
    margin-top: 0.35rem;
  }

  .case-input-flow-story-card__amount {
    grid-column: 1 / -1;
    margin-top: 0.2rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .case-input-flow-story-card__stage.is-loading::after,
  .case-input-flow-story-skeleton {
    animation: none;
  }
}


/* Right-side intake workflow assistant ------------------------------------ */
.case-page-shell.case-page-shell--with-intake-rail {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr) clamp(14.5rem, 17vw, 18rem);
  align-items: start;
  column-gap: 1rem;
}

.case-page-shell--with-intake-rail > .case-scroll-nav:not(.case-intake-rail) {
  grid-column: 1;
}

.case-page-shell--with-intake-rail > .case-page-main {
  grid-column: 2;
  min-width: 0;
}

.case-page-shell--with-intake-rail > .case-intake-rail {
  grid-column: 3;
}

.case-scroll-nav.case-intake-rail {
  display: grid;
  width: 100%;
  min-width: 0;
  align-content: start;
  gap: 0.7rem;
  overflow-x: hidden;
  padding: 0.72rem;
  scrollbar-width: thin;
}

.case-intake-rail__header {
  display: grid;
  gap: 0.28rem;
  min-width: 0;
}

.case-intake-rail__eyebrow {
  color: #2563eb;
  font-size: 0.63rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.case-intake-rail__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.case-intake-rail__title-row strong {
  color: #0f172a;
  font-size: 0.94rem;
}

.case-intake-rail__title-row > span {
  display: inline-grid;
  min-width: 1.55rem;
  height: 1.55rem;
  place-items: center;
  border: 1px solid rgba(37, 99, 235, 0.18);
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 0 0.38rem;
  font-size: 0.68rem;
  font-weight: 900;
}

.case-intake-rail__header p {
  margin: 0;
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.4;
}

.case-intake-rail__steps {
  display: grid;
  gap: 0.3rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.case-intake-rail__steps li {
  display: grid;
  grid-template-columns: 1.35rem minmax(0, 1fr);
  align-items: center;
  gap: 0.42rem;
  min-width: 0;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 10px;
  background: rgba(248, 250, 252, 0.86);
  padding: 0.34rem 0.4rem;
  color: #64748b;
}

.case-intake-rail__steps li > span {
  display: inline-grid;
  width: 1.25rem;
  height: 1.25rem;
  place-items: center;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  font-size: 0.62rem;
  font-weight: 900;
}

.case-intake-rail__steps li small {
  overflow: hidden;
  font-size: 0.66rem;
  font-weight: 800;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-intake-rail__steps li.case-intake-step--complete {
  border-color: rgba(34, 197, 94, 0.24);
  background: #f0fdf4;
  color: #166534;
}

.case-intake-rail__steps li.case-intake-step--complete > span {
  background: #22c55e;
  color: #ffffff;
}

.case-intake-rail__steps li.case-intake-step--active {
  border-color: rgba(37, 99, 235, 0.35);
  background: #eff6ff;
  color: #1d4ed8;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.08);
}

.case-intake-rail__steps li.case-intake-step--error,
.case-intake-rail__steps li.case-intake-step--stopped {
  border-color: rgba(244, 63, 94, 0.25);
  background: #fff1f2;
  color: #be123c;
}

.case-intake-rail__prepare-btn {
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr);
  align-items: center;
  gap: 0.58rem;
  width: 100%;
  border: 1px solid #1d4ed8;
  border-radius: 14px;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #ffffff;
  padding: 0.68rem 0.72rem;
  cursor: pointer;
  text-align: left;
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.2);
  transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
}

.case-intake-rail__prepare-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(37, 99, 235, 0.26);
}

.case-intake-rail__prepare-btn:focus-visible {
  outline: 3px solid rgba(96, 165, 250, 0.42);
  outline-offset: 2px;
}

.case-intake-rail__prepare-btn:disabled {
  cursor: not-allowed;
  opacity: 0.48;
  box-shadow: none;
}

.case-intake-rail__prepare-btn > span:last-child {
  display: grid;
  gap: 0.08rem;
  min-width: 0;
}

.case-intake-rail__prepare-btn strong {
  font-size: 0.78rem;
  font-weight: 900;
}

.case-intake-rail__prepare-btn small {
  overflow: hidden;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.61rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-intake-rail__prepare-icon {
  display: inline-grid;
  width: 1.9rem;
  height: 1.9rem;
  place-items: center;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  font-size: 1rem;
  font-weight: 900;
}

.case-intake-rail__ready {
  display: grid;
  grid-template-columns: 1.8rem minmax(0, 1fr);
  align-items: center;
  gap: 0.55rem;
  border: 1px solid #bbf7d0;
  border-radius: 14px;
  background: #f0fdf4;
  color: #166534;
  padding: 0.62rem;
}

.case-intake-rail__ready > span {
  display: inline-grid;
  width: 1.7rem;
  height: 1.7rem;
  place-items: center;
  border-radius: 999px;
  background: #22c55e;
  color: #ffffff;
  font-weight: 900;
}

.case-intake-rail__ready > div {
  display: grid;
  gap: 0.08rem;
  min-width: 0;
}

.case-intake-rail__ready strong {
  font-size: 0.72rem;
}

.case-intake-rail__ready small,
.case-intake-rail__busy-note {
  color: #64748b;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1.35;
}

.case-intake-rail__ready small {
  color: #15803d;
}

.case-intake-rail__continuation {
  display: grid;
  gap: 0.55rem;
  min-width: 0;
  border-top: 1px solid rgba(148, 163, 184, 0.28);
  padding-top: 0.68rem;
}

.case-intake-rail__continuation--primary {
  border-top: 0;
  padding-top: 0;
}

.case-intake-rail__continuation--primary .case-intake-rail__continuation-header > div > span {
  color: #2563eb;
}

.case-intake-rail__continuation-header {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.case-intake-rail__continuation-header > div {
  display: grid;
  gap: 0.08rem;
  min-width: 0;
}

.case-intake-rail__continuation-header span {
  color: #64748b;
  font-size: 0.58rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.case-intake-rail__continuation-header strong {
  color: #0f172a;
  font-size: 0.76rem;
}

.case-intake-rail__recheck {
  appearance: none;
  flex: 0 0 auto;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #2563eb;
  padding: 0.18rem 0.24rem;
  cursor: pointer;
  font: inherit;
  font-size: 0.59rem;
  font-weight: 900;
}

.case-intake-rail__recheck:hover:not(:disabled) {
  background: #eff6ff;
}

.case-intake-rail__recheck:focus-visible {
  outline: 2px solid rgba(59, 130, 246, 0.42);
  outline-offset: 2px;
}

.case-intake-rail__recheck:disabled {
  cursor: not-allowed;
  opacity: 0.48;
}

.case-intake-rail__continuation-detail {
  margin: 0;
  color: #64748b;
  font-size: 0.64rem;
  font-weight: 700;
  line-height: 1.4;
}

.case-intake-rail__continuation-steps {
  display: grid;
  gap: 0.34rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.case-intake-rail__continuation-steps li {
  display: grid;
  grid-template-columns: 1.45rem minmax(0, 1fr);
  align-items: center;
  gap: 0.46rem;
  min-width: 0;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 11px;
  background: #f8fafc;
  padding: 0.42rem 0.46rem;
}

.case-intake-rail__continuation-steps li > span {
  display: inline-grid;
  width: 1.35rem;
  height: 1.35rem;
  place-items: center;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  font-size: 0.62rem;
  font-weight: 900;
}

.case-intake-rail__continuation-steps li > div {
  display: grid;
  gap: 0.04rem;
  min-width: 0;
}

.case-intake-rail__continuation-steps strong {
  color: #334155;
  font-size: 0.66rem;
  font-weight: 900;
}

.case-intake-rail__continuation-steps small {
  overflow: hidden;
  color: #64748b;
  font-size: 0.57rem;
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-intake-rail__issue-toggle {
  appearance: none;
  justify-self: start;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #b45309;
  padding: 0.12rem 0;
  cursor: pointer;
  font: inherit;
  font-size: 0.56rem;
  font-weight: 900;
  line-height: 1.2;
  text-decoration: underline;
  text-decoration-color: rgba(180, 83, 9, 0.35);
  text-underline-offset: 2px;
}

.case-intake-rail__issue-toggle:hover {
  color: #92400e;
  text-decoration-color: currentColor;
}

.case-intake-rail__issue-toggle:focus-visible {
  outline: 2px solid rgba(245, 158, 11, 0.5);
  outline-offset: 2px;
}

.case-intake-rail__findings {
  display: grid;
  gap: 0.42rem;
  border: 1px solid rgba(245, 158, 11, 0.34);
  border-radius: 11px;
  background: #fffdf5;
  padding: 0.5rem;
}

.case-intake-rail__findings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.case-intake-rail__findings-header strong {
  color: #78350f;
  font-size: 0.64rem;
}

.case-intake-rail__findings-header button {
  appearance: none;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #92400e;
  padding: 0.12rem 0.2rem;
  cursor: pointer;
  font: inherit;
  font-size: 0.55rem;
  font-weight: 900;
}

.case-intake-rail__findings-header button:hover,
.case-intake-rail__findings-header button:focus-visible {
  background: #fef3c7;
}

.case-intake-rail__findings ol {
  display: grid;
  gap: 0.3rem;
  margin: 0;
  padding-left: 1.08rem;
}

.case-intake-rail__findings li,
.case-intake-rail__findings > small {
  color: #78350f;
  font-size: 0.57rem;
  font-weight: 700;
  line-height: 1.35;
}

.case-intake-rail__continuation-steps li.case-intake-continuation-step--complete {
  border-color: rgba(34, 197, 94, 0.28);
  background: #f0fdf4;
}

.case-intake-rail__continuation-steps li.case-intake-continuation-step--complete > span {
  background: #22c55e;
  color: #ffffff;
}

.case-intake-rail__continuation-steps li.case-intake-continuation-step--complete strong,
.case-intake-rail__continuation-steps li.case-intake-continuation-step--complete small {
  color: #166534;
}

.case-intake-rail__continuation-steps li.case-intake-continuation-step--checking {
  border-color: #93c5fd;
  background: #eff6ff;
}

.case-intake-rail__continuation-steps li.case-intake-continuation-step--checking > span {
  background: #2563eb;
  color: #ffffff;
}

.case-intake-rail__continuation-steps li.case-intake-continuation-step--attention {
  border-color: rgba(245, 158, 11, 0.34);
  background: #fffbeb;
}

.case-intake-rail__continuation-steps li.case-intake-continuation-step--attention > span {
  background: #f59e0b;
  color: #ffffff;
}

.case-intake-rail__continuation-steps li.case-intake-continuation-step--stale,
.case-intake-rail__continuation-steps li.case-intake-continuation-step--error {
  border-color: rgba(148, 163, 184, 0.42);
  background: #f8fafc;
}

.case-intake-rail__continuation-action small {
  white-space: normal;
}

.case-intake-rail__ready--continuation {
  border-color: #86efac;
  background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
}



.case-intake-rail__attribution {
  border-top-color: rgba(99, 102, 241, 0.28);
}

.case-intake-rail__attribution-steps li.case-intake-continuation-step--checking {
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.08);
}

.case-intake-rail__review-mode {
  display: block;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  padding: 0.34rem 0.42rem;
  font-size: 0.56rem;
  font-weight: 800;
  line-height: 1.35;
}

.case-intake-rail__attribution-action {
  background: linear-gradient(135deg, #4f46e5, #4338ca);
  border-color: #4338ca;
  box-shadow: 0 10px 24px rgba(79, 70, 229, 0.2);
}

.case-intake-rail__attribution-action:hover:not(:disabled) {
  box-shadow: 0 14px 28px rgba(79, 70, 229, 0.26);
}

.case-intake-rail__ready--attribution {
  border-color: #a7f3d0;
  background: linear-gradient(135deg, #ecfdf5, #f0fdf4);
}

.case-attribution-workflow-lock .case-intake-preparation-lock__card {
  border-color: rgba(199, 210, 254, 0.5);
}

.case-attribution-workflow-lock .case-intake-preparation-lock__header span:first-child {
  color: #4f46e5;
}

.case-attribution-workflow-lock .case-intake-preparation-lock__meter > span {
  background: linear-gradient(90deg, #4f46e5, #22c55e);
}

.case-intake-rail__cleanup {
  border-top-color: rgba(13, 148, 136, 0.28);
}

.case-intake-rail__cleanup-steps li.case-intake-continuation-step--checking {
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.09);
}

.case-intake-rail__cleanup-action {
  border-color: #0f766e;
  background: linear-gradient(135deg, #0d9488, #0f766e);
  box-shadow: 0 10px 24px rgba(13, 148, 136, 0.2);
}

.case-intake-rail__cleanup-action:hover:not(:disabled) {
  box-shadow: 0 14px 28px rgba(13, 148, 136, 0.26);
}

.case-intake-rail__ready--cleanup {
  border-color: #99f6e4;
  background: linear-gradient(135deg, #f0fdfa, #ecfdf5);
}

.case-intake-rail__stage-progress {
  min-width: 0;
  border-top: 1px solid rgba(148, 163, 184, 0.24);
  padding: 0.1rem 0.08rem 0;
}

.case-intake-rail__stage-progress ol {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  align-items: start;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
}

.case-intake-rail__stage-progress li {
  position: relative;
  display: grid;
  min-width: 0;
  justify-items: center;
  color: #94a3b8;
}

.case-intake-rail__stage-progress li:not(:last-child)::after {
  content: '';
  position: absolute;
  z-index: 0;
  top: 0.57rem;
  left: calc(50% + 0.66rem);
  width: calc(100% - 1.32rem);
  height: 0.18rem;
  border-radius: 999px;
  background: #e2e8f0;
}

.case-intake-rail__stage-progress li.is-complete:not(:last-child)::after {
  background: #86efac;
}

.case-intake-rail__stage-progress li > span {
  position: relative;
  box-sizing: border-box;
  z-index: 1;
  display: inline-grid;
  width: 1.32rem;
  height: 1.32rem;
  place-items: center;
  border: 2px solid #cbd5e1;
  border-radius: 999px;
  background: #ffffff;
  color: #64748b;
  font-size: 0.56rem;
  font-weight: 950;
  line-height: 1;
}

.case-intake-rail__stage-progress li.is-complete > span {
  border-color: #22c55e;
  background: #22c55e;
  color: #ffffff;
}

.case-intake-rail__stage-progress li.is-current > span {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
  box-shadow: 0 0 0 0.22rem rgba(59, 130, 246, 0.14);
}

.case-intake-rail__stage-progress li.is-upcoming > span {
  background: #f8fafc;
}

.case-cleanup-workflow-lock .case-intake-preparation-lock__card {
  border-color: rgba(153, 246, 228, 0.58);
}

.case-cleanup-workflow-lock .case-intake-preparation-lock__header span:first-child {
  color: #0f766e;
}

.case-cleanup-workflow-lock .case-intake-preparation-lock__meter > span {
  background: linear-gradient(90deg, #0d9488, #22c55e);
}

.case-investigation-visualizer-modal--manual-start .case-investigation-visualizer-viewport {
  position: relative;
}

.case-manual-solve-intro {
  position: absolute;
  inset: 0;
  z-index: 180;
  display: grid;
  place-items: center;
  padding: clamp(0.75rem, 2.4vw, 1.5rem);
  overflow: auto;
  overscroll-behavior: contain;
  background: rgba(15, 23, 42, 0.72);
  backdrop-filter: blur(5px);
}

.case-manual-solve-intro__card {
  position: relative;
  display: grid;
  gap: 0.9rem;
  width: min(43rem, 100%);
  max-height: calc(100% - 0.25rem);
  overflow: auto;
  border: 1px solid rgba(191, 219, 254, 0.78);
  border-radius: 20px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.99), rgba(248, 250, 252, 0.98));
  padding: clamp(1rem, 2.6vw, 1.5rem);
  box-shadow: 0 28px 72px rgba(2, 6, 23, 0.42);
}

.case-manual-solve-intro__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: inline-grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  color: #475569;
  cursor: pointer;
}

.case-manual-solve-intro__close span::before,
.case-manual-solve-intro__close span::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0.78rem;
  height: 0.12rem;
  border-radius: 999px;
  background: currentColor;
}

.case-manual-solve-intro__close span::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.case-manual-solve-intro__close span::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.case-manual-solve-intro__close:hover,
.case-manual-solve-intro__close:focus-visible {
  border-color: #60a5fa;
  background: #eff6ff;
  color: #1d4ed8;
  outline: none;
}

.case-manual-solve-intro__heading {
  display: grid;
  gap: 0.35rem;
  padding-right: 2.4rem;
  text-align: center;
}

.case-manual-solve-intro__eyebrow {
  color: #2563eb;
  font-size: 0.68rem;
  font-weight: 950;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.case-manual-solve-intro__heading h3 {
  margin: 0;
  color: #0f172a;
  font-size: clamp(1.2rem, 2.5vw, 1.65rem);
  line-height: 1.15;
}

.case-manual-solve-intro__heading p,
.case-manual-solve-intro__choices p,
.case-manual-solve-intro__note {
  margin: 0;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 620;
  line-height: 1.48;
}

.case-manual-solve-intro__choices {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: 0.65rem;
}

.case-manual-solve-intro__choices article {
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr);
  gap: 0.65rem;
  align-items: start;
  border: 1px solid #dbeafe;
  border-radius: 14px;
  background: #f8fbff;
  padding: 0.75rem;
}

.case-manual-solve-intro__choice-number {
  display: inline-grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 0.78rem;
  font-weight: 950;
}

.case-manual-solve-intro__choices strong {
  display: block;
  margin-bottom: 0.2rem;
  color: #0f172a;
  font-size: 0.85rem;
}

.case-manual-solve-intro__note {
  border-left: 3px solid #f59e0b;
  border-radius: 0 10px 10px 0;
  background: #fffbeb;
  padding: 0.65rem 0.75rem;
}

.case-manual-solve-intro__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.55rem;
}

.case-manual-solve-intro__actions .toolbar-btn {
  min-width: 7.4rem;
  justify-content: center;
}

.case-manual-solve-intro__auto {
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.22);
}

.case-manual-solve-intro__preference {
  display: inline-flex;
  justify-self: end;
  align-items: center;
  gap: 0.5rem;
  color: #475569;
  font-size: 0.72rem;
  font-weight: 720;
  cursor: pointer;
}

.case-manual-solve-intro__preference input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.case-manual-solve-intro__toggle {
  position: relative;
  width: 2rem;
  height: 1.08rem;
  flex: 0 0 auto;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #e2e8f0;
  transition: background 0.18s ease, border-color 0.18s ease;
}

.case-manual-solve-intro__toggle > span {
  position: absolute;
  top: 50%;
  left: 0.16rem;
  width: 0.72rem;
  height: 0.72rem;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.25);
  transform: translateY(-50%);
  transition: transform 0.18s ease;
}

.case-manual-solve-intro__preference input:checked + .case-manual-solve-intro__toggle {
  border-color: #2563eb;
  background: #2563eb;
}

.case-manual-solve-intro__preference input:checked + .case-manual-solve-intro__toggle > span {
  transform: translate(0.88rem, -50%);
}

.case-manual-solve-intro__preference input:focus-visible + .case-manual-solve-intro__toggle {
  outline: 3px solid rgba(96, 165, 250, 0.32);
  outline-offset: 2px;
}

.case-manual-solve-intro__choice--recommended {
  border-color: #93c5fd !important;
  background: linear-gradient(145deg, #eff6ff, #f8fbff) !important;
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.08);
}

.case-manual-solve-intro__auto-all {
  min-width: 10.8rem !important;
  box-shadow: 0 12px 26px rgba(37, 99, 235, 0.24);
}

.case-manual-solve-graph-list__header {
  gap: 0.55rem;
}

.case-manual-solve-graph-list__auto-all {
  flex: 0 0 auto;
  min-height: 2rem;
  padding-inline: 0.62rem;
  font-size: 0.72rem;
}

.case-manual-solve-graph-list__status {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  margin-top: 0.18rem;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  background: #eff6ff;
  padding: 0.14rem 0.42rem;
  color: #1d4ed8;
  font-size: 0.64rem;
  font-style: normal;
  font-weight: 850;
  line-height: 1.1;
}

.case-manual-solve-graph-list__status.is-complete {
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #15803d;
}

.case-manual-solve-graph-list__status.is-attention {
  border-color: #fde68a;
  background: #fffbeb;
  color: #a16207;
}

.case-manual-solve-graph-list__status.is-manual {
  border-color: #fdba74;
  background: #fff7ed;
  color: #c2410c;
}

.case-manual-solve-graph-list__status.is-qualified {
  border-color: #c7d2fe;
  background: #eef2ff;
  color: #4338ca;
}

.case-manual-solve-all-flows-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483500;
  display: grid;
  place-items: center;
  padding: 1rem;
  overflow: auto;
  overscroll-behavior: contain;
}

.case-manual-solve-all-flows-overlay__scrim {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(5px);
}

.case-manual-solve-all-flows-overlay__card,
.case-manual-solve-all-flows-result {
  position: relative;
  z-index: 1;
  width: min(35rem, 100%);
  max-height: calc(100vh - 2rem);
  overflow: auto;
  border: 1px solid rgba(191, 219, 254, 0.82);
  border-radius: 22px;
  background: linear-gradient(150deg, rgba(255, 255, 255, 0.995), rgba(248, 250, 252, 0.99));
  padding: clamp(1rem, 2.7vw, 1.45rem);
  box-shadow: 0 30px 80px rgba(2, 6, 23, 0.46);
}

.case-manual-solve-all-flows-overlay__card {
  display: grid;
  gap: 0.9rem;
}

.case-manual-solve-all-flows-overlay__card header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.case-manual-solve-all-flows-overlay__card header > div,
.case-manual-solve-all-flows-result__heading {
  display: grid;
  gap: 0.22rem;
}

.case-manual-solve-all-flows-overlay__card header span,
.case-manual-solve-all-flows-result__heading > span {
  color: #2563eb;
  font-size: 0.68rem;
  font-weight: 950;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.case-manual-solve-all-flows-overlay__card header h3,
.case-manual-solve-all-flows-result__heading h3 {
  margin: 0;
  color: #0f172a;
  font-size: 1.28rem;
  line-height: 1.2;
}

.case-manual-solve-all-flows-overlay__card header > strong {
  display: inline-grid;
  min-width: 4.2rem;
  min-height: 2.35rem;
  place-items: center;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.82rem;
  font-weight: 950;
}

.case-manual-solve-all-flows-overlay__current {
  display: grid;
  gap: 0.18rem;
  border: 1px solid #dbeafe;
  border-radius: 15px;
  background: #f8fbff;
  padding: 0.78rem;
}

.case-manual-solve-all-flows-overlay__current span {
  color: #64748b;
  font-size: 0.66rem;
  font-weight: 850;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.case-manual-solve-all-flows-overlay__current strong {
  color: #0f172a;
  font-size: 0.98rem;
}

.case-manual-solve-all-flows-overlay__current small,
.case-manual-solve-all-flows-overlay__card > p,
.case-manual-solve-all-flows-result__heading p {
  margin: 0;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 620;
  line-height: 1.48;
}

.case-manual-solve-all-flows-overlay__meter {
  height: 0.62rem;
  overflow: hidden;
  border-radius: 999px;
  background: #e2e8f0;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.08);
}

.case-manual-solve-all-flows-overlay__meter > span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #2563eb, #06b6d4, #22c55e);
  transition: width 0.28s ease;
}

.case-manual-solve-all-flows-overlay__metrics,
.case-manual-solve-all-flows-result__metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.55rem;
}

.case-manual-solve-all-flows-overlay__metrics article,
.case-manual-solve-all-flows-result__metrics article {
  display: grid;
  gap: 0.08rem;
  border: 1px solid #e2e8f0;
  border-radius: 13px;
  background: #ffffff;
  padding: 0.6rem;
  text-align: center;
}

.case-manual-solve-all-flows-overlay__metrics strong,
.case-manual-solve-all-flows-result__metrics strong {
  color: #0f172a;
  font-size: 1.02rem;
}

.case-manual-solve-all-flows-overlay__metrics small,
.case-manual-solve-all-flows-result__metrics small {
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 760;
}

.case-manual-solve-all-flows-overlay__card > .toolbar-btn {
  justify-self: center;
  min-width: 10rem;
}

.case-manual-solve-all-flows-result {
  display: grid;
  grid-template-columns: 3rem minmax(0, 1fr);
  gap: 0.85rem 0.9rem;
}

.case-manual-solve-all-flows-result__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: inline-grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
}

.case-manual-solve-all-flows-result__close span::before,
.case-manual-solve-all-flows-result__close span::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0.8rem;
  height: 0.12rem;
  border-radius: 999px;
  background: currentColor;
}

.case-manual-solve-all-flows-result__close span::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.case-manual-solve-all-flows-result__close span::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.case-manual-solve-all-flows-result__icon {
  display: grid;
  width: 2.8rem;
  height: 2.8rem;
  place-items: center;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  font-size: 1.25rem;
  font-weight: 950;
  box-shadow: inset 0 0 0 1px #fcd34d;
}

.case-manual-solve-all-flows-result__icon.is-complete {
  background: #dcfce7;
  color: #15803d;
  box-shadow: inset 0 0 0 1px #86efac;
}

.case-manual-solve-all-flows-result__heading {
  min-width: 0;
  padding-right: 2.2rem;
}

.case-manual-solve-all-flows-result__metrics,
.case-manual-solve-all-flows-result__findings,
.case-manual-solve-all-flows-result__actions {
  grid-column: 1 / -1;
}

.case-manual-solve-all-flows-result__metrics {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.case-manual-solve-all-flows-result__findings {
  display: grid;
  gap: 0.45rem;
  border: 1px solid #fed7aa;
  border-radius: 15px;
  background: #fff7ed;
  padding: 0.72rem;
}

.case-manual-solve-all-flows-result__findings > strong {
  color: #9a3412;
  font-size: 0.8rem;
}

.case-manual-solve-all-flows-result__findings ul {
  display: grid;
  gap: 0.42rem;
  max-height: 13rem;
  margin: 0;
  padding: 0;
  overflow: auto;
  list-style: none;
}

.case-manual-solve-all-flows-result__findings li {
  display: grid;
  gap: 0.08rem;
  border: 1px solid rgba(253, 186, 116, 0.62);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.78);
  padding: 0.5rem 0.58rem;
}

.case-manual-solve-all-flows-result__findings li span {
  color: #7c2d12;
  font-size: 0.76rem;
  font-weight: 880;
}

.case-manual-solve-all-flows-result__findings li small {
  color: #9a3412;
  font-size: 0.68rem;
  line-height: 1.4;
}

.case-manual-solve-all-flows-result__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

@media (max-width: 560px) {
  .case-manual-solve-all-flows-overlay__card header {
    display: grid;
  }

  .case-manual-solve-all-flows-overlay__card header > strong {
    justify-self: start;
  }

  .case-manual-solve-all-flows-overlay__metrics {
    grid-template-columns: 1fr;
  }

  .case-manual-solve-all-flows-result {
    grid-template-columns: 1fr;
  }

  .case-manual-solve-all-flows-result__heading,
  .case-manual-solve-all-flows-result__metrics,
  .case-manual-solve-all-flows-result__findings,
  .case-manual-solve-all-flows-result__actions {
    grid-column: 1;
  }
}

.case-manual-solve-close-confirm {
  position: fixed;
  inset: 0;
  z-index: 2147483600;
  display: grid;
  place-items: center;
  background: rgba(15, 23, 42, 0.58);
  padding: 1rem;
  backdrop-filter: blur(3px);
}

.case-manual-solve-close-confirm__card {
  display: grid;
  grid-template-columns: 2.4rem minmax(0, 1fr);
  gap: 0.75rem 0.9rem;
  width: min(28rem, 100%);
  border: 1px solid rgba(191, 219, 254, 0.7);
  border-radius: 18px;
  background: #ffffff;
  padding: 1rem;
  box-shadow: 0 24px 64px rgba(2, 6, 23, 0.38);
}

.case-manual-solve-close-confirm__icon {
  display: inline-grid;
  width: 2.35rem;
  height: 2.35rem;
  place-items: center;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 1.05rem;
  font-weight: 950;
}

.case-manual-solve-close-confirm__card > div:nth-child(2) {
  display: grid;
  gap: 0.2rem;
  min-width: 0;
}

.case-manual-solve-close-confirm__card span {
  color: #2563eb;
  font-size: 0.64rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.case-manual-solve-close-confirm__card h3 {
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
}

.case-manual-solve-close-confirm__card p {
  margin: 0;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 650;
  line-height: 1.45;
}

.case-manual-solve-close-confirm__actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 0.55rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 0.75rem;
}

.case-manual-report-boundary-confirm {
  position: fixed;
  inset: 0;
  z-index: 2147483605;
  display: grid;
  place-items: center;
  overflow: auto;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.68);
  backdrop-filter: blur(5px);
}

.case-manual-report-boundary-confirm__card {
  position: relative;
  display: grid;
  grid-template-columns: 2.7rem minmax(0, 1fr);
  gap: 0.8rem 1rem;
  width: min(36rem, 100%);
  max-height: min(42rem, calc(100vh - 2rem));
  overflow: auto;
  border: 1px solid rgba(165, 180, 252, 0.85);
  border-radius: 20px;
  background: #ffffff;
  padding: 1.15rem;
  box-shadow: 0 28px 74px rgba(2, 6, 23, 0.45);
}

.case-manual-report-boundary-confirm__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
  border: 0;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
}

.case-manual-report-boundary-confirm__close:hover,
.case-manual-report-boundary-confirm__close:focus-visible {
  background: #eef2ff;
}

.case-manual-report-boundary-confirm__close span,
.case-manual-report-boundary-confirm__close span::after {
  position: absolute;
  width: 0.9rem;
  height: 2px;
  border-radius: 999px;
  background: #475569;
  content: '';
  transform: rotate(45deg);
}

.case-manual-report-boundary-confirm__close span::after {
  inset: 0;
  transform: rotate(90deg);
}

.case-manual-report-boundary-confirm__icon {
  display: grid;
  width: 2.6rem;
  height: 2.6rem;
  place-items: center;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 1.1rem;
  font-weight: 950;
}

.case-manual-report-boundary-confirm__heading {
  display: grid;
  gap: 0.25rem;
  min-width: 0;
  padding-right: 2rem;
}

.case-manual-report-boundary-confirm__heading > span {
  color: #4f46e5;
  font-size: 0.66rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.case-manual-report-boundary-confirm__heading h3 {
  margin: 0;
  color: #0f172a;
  font-size: 1.15rem;
}

.case-manual-report-boundary-confirm__heading p,
.case-manual-report-boundary-confirm__notice p {
  margin: 0;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 650;
  line-height: 1.5;
}

.case-manual-report-boundary-confirm__list {
  grid-column: 1 / -1;
  display: grid;
  gap: 0.45rem;
  max-height: 13rem;
  overflow: auto;
  margin: 0;
  padding: 0;
  list-style: none;
}

.case-manual-report-boundary-confirm__list li {
  display: grid;
  grid-template-columns: minmax(5rem, auto) minmax(0, 1fr) auto;
  gap: 0.45rem 0.75rem;
  align-items: center;
  border: 1px solid #e0e7ff;
  border-radius: 12px;
  background: #f8fafc;
  padding: 0.62rem 0.72rem;
}

.case-manual-report-boundary-confirm__list strong {
  color: #312e81;
  font-size: 0.78rem;
}

.case-manual-report-boundary-confirm__list span {
  min-width: 0;
  overflow: hidden;
  color: #334155;
  font-size: 0.76rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.case-manual-report-boundary-confirm__list small {
  color: #64748b;
  font-size: 0.66rem;
  font-weight: 800;
}

.case-manual-report-boundary-confirm__notice {
  grid-column: 1 / -1;
  display: grid;
  gap: 0.25rem;
  border-left: 4px solid #6366f1;
  border-radius: 10px;
  background: #eef2ff;
  padding: 0.7rem 0.8rem;
}

.case-manual-report-boundary-confirm__notice strong {
  color: #312e81;
  font-size: 0.78rem;
}

.case-manual-report-boundary-confirm__acknowledgement {
  grid-column: 1 / -1;
  display: flex;
  gap: 0.65rem;
  align-items: flex-start;
  color: #334155;
  font-size: 0.78rem;
  font-weight: 750;
  line-height: 1.4;
}

.case-manual-report-boundary-confirm__acknowledgement input {
  flex: 0 0 auto;
  margin-top: 0.15rem;
}

.case-manual-report-boundary-confirm__status {
  grid-column: 1 / -1;
  margin: 0;
  color: #b45309;
  font-size: 0.75rem;
  font-weight: 800;
}

.case-manual-report-boundary-confirm__actions {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.55rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 0.85rem;
}

@media (max-width: 640px) {
  .case-manual-report-boundary-confirm {
    align-items: start;
    padding: 0.65rem;
  }

  .case-manual-report-boundary-confirm__card {
    grid-template-columns: 2.45rem minmax(0, 1fr);
    gap: 0.7rem 0.75rem;
    max-height: calc(100vh - 1.3rem);
    border-radius: 16px;
    padding: 0.9rem;
  }

  .case-manual-report-boundary-confirm__list li {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .case-manual-report-boundary-confirm__list span {
    grid-column: 1 / -1;
    grid-row: 2;
    white-space: normal;
  }

  .case-manual-report-boundary-confirm__actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .case-manual-report-boundary-confirm__actions .toolbar-btn {
    width: 100%;
    justify-content: center;
  }
}


/* Blocking progress view used only while an explicit guided run is active. */
.case-intake-preparation-lock {
  position: fixed;
  inset: 0;
  z-index: 2147482000;
  display: grid;
  place-items: center;
  padding: 1rem;
  overflow: auto;
  overscroll-behavior: contain;
}

.case-intake-preparation-lock__scrim {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.78);
  backdrop-filter: blur(7px);
}

.case-intake-preparation-lock__card {
  position: relative;
  display: grid;
  width: min(36rem, 100%);
  max-height: calc(100vh - 2rem);
  gap: 0.9rem;
  overflow: auto;
  border: 1px solid rgba(191, 219, 254, 0.42);
  border-radius: 24px;
  background: #ffffff;
  padding: 1.15rem;
  box-shadow: 0 30px 80px rgba(2, 6, 23, 0.42);
}

.case-intake-preparation-lock__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.case-intake-preparation-lock__header > div {
  display: grid;
  gap: 0.24rem;
  min-width: 0;
}

.case-intake-preparation-lock__header span:first-child {
  color: #2563eb;
  font-size: 0.67rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.case-intake-preparation-lock__header h2 {
  margin: 0;
  color: #0f172a;
  font-size: 1.28rem;
}

.case-intake-preparation-lock__header p {
  margin: 0;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.45;
}

.case-intake-preparation-lock__root-count {
  flex: 0 0 auto;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8 !important;
  padding: 0.35rem 0.55rem;
  font-size: 0.66rem !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  white-space: nowrap;
}

.case-intake-preparation-lock__meter {
  width: 100%;
  height: 0.52rem;
  overflow: hidden;
  border-radius: 999px;
  background: #e2e8f0;
}

.case-intake-preparation-lock__meter > span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #2563eb, #22c55e);
  transition: width 0.28s ease;
}

.case-intake-preparation-lock__steps {
  display: grid;
  gap: 0.42rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.case-intake-preparation-lock__steps li {
  display: grid;
  grid-template-columns: 1.8rem minmax(0, 1fr);
  align-items: center;
  gap: 0.62rem;
  border: 1px solid #e2e8f0;
  border-radius: 13px;
  background: #f8fafc;
  padding: 0.56rem 0.62rem;
}

.case-intake-preparation-lock__steps li > span:last-child {
  display: grid;
  gap: 0.08rem;
  min-width: 0;
}

.case-intake-preparation-lock__steps strong {
  color: #0f172a;
  font-size: 0.76rem;
}

.case-intake-preparation-lock__steps small {
  color: #64748b;
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1.3;
}

.case-intake-preparation-lock__step-marker {
  display: inline-grid;
  width: 1.65rem;
  height: 1.65rem;
  place-items: center;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  font-size: 0.68rem;
  font-weight: 900;
}

.case-intake-preparation-lock__steps li.case-intake-step--complete {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.case-intake-preparation-lock__steps li.case-intake-step--complete .case-intake-preparation-lock__step-marker {
  background: #22c55e;
  color: #ffffff;
}

.case-intake-preparation-lock__steps li.case-intake-step--active {
  border-color: #93c5fd;
  background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
}

.case-intake-preparation-lock__steps li.case-intake-step--active .case-intake-preparation-lock__step-marker {
  background: #2563eb;
  color: #ffffff;
  animation: case-intake-preparation-pulse 1s ease-in-out infinite;
}

.case-intake-preparation-lock__steps li.case-intake-step--error,
.case-intake-preparation-lock__steps li.case-intake-step--stopped {
  border-color: #fda4af;
  background: #fff1f2;
}

.case-intake-preparation-lock__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 0.82rem;
}

.case-intake-preparation-lock__footer p {
  margin: 0;
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.4;
}

.case-intake-preparation-lock__stop {
  flex: 0 0 auto;
  white-space: nowrap;
}

/* Manual Solve startup is a real blocking operation. Keep Case Buddy mounted,
   but make the workspace inert until the visual is ready or the user cancels. */
.spreadsheet-tool--manual-visualizer-opening > :not(.case-manual-start-opening-lock) {
  pointer-events: none;
  user-select: none;
}

.case-manual-start-opening-lock__card {
  width: min(39rem, 100%);
}

.case-manual-start-opening-lock__footer {
  align-items: flex-end;
}

.case-manual-start-opening-lock__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 0.55rem;
}

.case-manual-start-opening-lock__actions .toolbar-btn {
  min-width: 6.7rem;
  white-space: nowrap;
}

.case-manual-start-opening-lock__actions .toolbar-btn:disabled {
  cursor: wait;
  opacity: 0.58;
}

@keyframes case-intake-preparation-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(0.9); opacity: 0.72; }
}

@media (max-width: 1180px) {
  .case-page-shell.case-page-shell--with-intake-rail {
    grid-template-columns: max-content minmax(0, 1fr);
    row-gap: 0.75rem;
  }

  .case-page-shell--with-intake-rail > .case-scroll-nav:not(.case-intake-rail) {
    grid-column: 1;
    grid-row: 1 / span 2;
  }

  .case-page-shell--with-intake-rail > .case-intake-rail {
    grid-column: 2;
    grid-row: 1;
    max-height: none;
  }

  .case-page-shell--with-intake-rail > .case-page-main {
    grid-column: 2;
    grid-row: 2;
  }

  .case-intake-rail__steps {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .case-intake-rail__continuation-steps {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .case-intake-rail__attribution-steps,
  .case-intake-rail__cleanup-steps {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .case-intake-rail__steps li {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }
}

@media (max-width: 760px) {
  .case-intake-rail__steps {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .case-intake-rail__continuation-steps,
  .case-intake-rail__attribution-steps,
  .case-intake-rail__cleanup-steps {
    grid-template-columns: 1fr;
  }

  .case-manual-solve-close-confirm__actions {
    align-items: stretch;
    flex-direction: column;
  }

  .case-intake-rail__steps li:last-child {
    grid-column: 1 / -1;
  }

  .case-intake-preparation-lock__header,
  .case-intake-preparation-lock__footer {
    align-items: stretch;
    flex-direction: column;
  }

  .case-intake-preparation-lock__root-count,
  .case-intake-preparation-lock__stop {
    align-self: flex-start;
  }

  .case-manual-start-opening-lock__actions {
    width: 100%;
  }

  .case-manual-start-opening-lock__actions .toolbar-btn {
    flex: 1 1 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .case-intake-preparation-lock__steps li.case-intake-step--active .case-intake-preparation-lock__step-marker {
    animation: none;
  }

  .case-intake-rail__prepare-btn,
  .case-intake-preparation-lock__meter > span {
    transition: none;
  }
}



/* Lookup-first presentation shell. It orchestrates existing Case Buddy loaders,
   Flow Solver controls, and report automation without creating another case model. */
.case-quick-lookup {
  position: fixed;
  inset: 0;
  z-index: 5400;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  color: #e2e8f0;
  background: #07111f;
  isolation: isolate;
  transition: opacity 0.18s ease;
}

.case-quick-lookup--behind-modal {
  z-index: 20;
  pointer-events: none;
}

.case-quick-lookup__backdrop {
  position: absolute;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  background:
    radial-gradient(circle at 50% 40%, rgba(37, 99, 235, 0.2), transparent 34%),
    radial-gradient(circle at 12% 15%, rgba(6, 182, 212, 0.12), transparent 30%),
    radial-gradient(circle at 88% 82%, rgba(59, 130, 246, 0.14), transparent 34%),
    linear-gradient(145deg, #07111f 0%, #0b1728 48%, #091321 100%);
}

.case-quick-lookup__backdrop::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0.22;
  background-image:
    linear-gradient(rgba(148, 163, 184, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(148, 163, 184, 0.06) 1px, transparent 1px);
  background-size: 42px 42px;
  mask-image: radial-gradient(circle at center, #000 0%, transparent 78%);
}

.case-quick-lookup__network {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.24;
  filter: drop-shadow(0 0 12px rgba(56, 189, 248, 0.2));
}

.case-quick-lookup__network path {
  fill: none;
  stroke: #60a5fa;
  stroke-width: 1.4;
  stroke-dasharray: 5 11;
}

.case-quick-lookup__network circle {
  fill: #0ea5e9;
  stroke: rgba(219, 234, 254, 0.9);
  stroke-width: 2;
}

.case-quick-lookup__orb {
  position: absolute;
  width: 28rem;
  height: 28rem;
  border-radius: 999px;
  filter: blur(70px);
  opacity: 0.12;
}

.case-quick-lookup__orb--one { left: -12rem; top: 10%; background: #0ea5e9; }
.case-quick-lookup__orb--two { right: -10rem; bottom: -8rem; background: #4f46e5; }

.case-quick-lookup__topbar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem clamp(1rem, 3vw, 2.4rem);
}

.case-quick-lookup .sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.case-quick-lookup__brand,
.case-quick-lookup__view-nav button,
.case-quick-lookup__utility-dock button,
.case-quick-lookup__settings button {
  font: inherit;
}

.case-quick-lookup__brand {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  border: 0;
  padding: 0;
  background: transparent;
  color: #f8fafc;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 950;
  letter-spacing: 0.16em;
}

.case-quick-lookup__brand-mark {
  display: grid;
  width: 2.15rem;
  height: 2.15rem;
  place-items: center;
  border: 1px solid rgba(147, 197, 253, 0.28);
  border-radius: 11px;
  background: rgba(15, 23, 42, 0.62);
  box-shadow: 0 10px 30px rgba(2, 132, 199, 0.16);
}

.case-quick-lookup__brand-mark svg { width: 1.55rem; height: 1.55rem; }
.case-quick-lookup__brand-mark circle { fill: #38bdf8; }
.case-quick-lookup__brand-mark rect { fill: #facc15; }
.case-quick-lookup__brand-mark path { stroke: #e2e8f0; stroke-width: 2; }
.case-quick-lookup__brand:disabled { cursor: not-allowed; opacity: 0.5; }

.case-quick-lookup__view-nav {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.22rem;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.38);
  backdrop-filter: blur(14px);
}

.case-quick-lookup__view-nav button {
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.42rem 0.72rem;
  font-size: 0.68rem;
  font-weight: 780;
}

.case-quick-lookup__view-nav button:hover:not(:disabled),
.case-quick-lookup__view-nav button:focus-visible:not(:disabled) {
  background: rgba(30, 41, 59, 0.9);
  color: #e0f2fe;
  outline: none;
}

.case-quick-lookup__view-nav button:disabled { cursor: not-allowed; opacity: 0.45; }

.case-quick-lookup__main {
  display: grid;
  min-height: 0;
  place-items: center;
  overflow: auto;
  padding: 1rem clamp(1rem, 4vw, 3rem) 5.8rem;
}

.case-quick-lookup__hero {
  display: grid;
  width: min(59rem, 100%);
  place-items: center;
}

.case-quick-lookup__lookup-card,
.case-quick-lookup__choice-card,
.case-quick-lookup__progress-card,
.case-quick-lookup__result-card {
  position: relative;
  display: grid;
  width: 100%;
  gap: 0.7rem;
  justify-items: center;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 28px;
  background: linear-gradient(145deg, rgba(15, 23, 42, 0.78), rgba(15, 32, 52, 0.62));
  padding: clamp(1.35rem, 4vw, 2.6rem);
  box-shadow: 0 32px 90px rgba(2, 6, 23, 0.48), inset 0 1px rgba(255, 255, 255, 0.04);
  text-align: center;
  backdrop-filter: blur(22px);
}

.case-quick-lookup__lookup-card { max-width: 57rem; }
.case-quick-lookup__choice-card { max-width: 54rem; justify-items: stretch; text-align: left; }
.case-quick-lookup__progress-card,
.case-quick-lookup__result-card { max-width: 40rem; }

.case-quick-lookup__logo {
  display: grid;
  width: 4.4rem;
  height: 4.4rem;
  place-items: center;
  margin-bottom: 0.1rem;
  border: 1px solid rgba(125, 211, 252, 0.28);
  border-radius: 22px;
  background: radial-gradient(circle at 30% 20%, rgba(56, 189, 248, 0.22), transparent 55%), rgba(15, 23, 42, 0.7);
  box-shadow: 0 18px 50px rgba(14, 165, 233, 0.16);
}

.case-quick-lookup__logo svg { width: 3.2rem; height: 3.2rem; }
.case-quick-lookup__logo circle { fill: #38bdf8; stroke: #bae6fd; stroke-width: 1.4; }
.case-quick-lookup__logo rect { fill: #facc15; stroke: #fef9c3; stroke-width: 1.4; }
.case-quick-lookup__logo path { stroke: #e2e8f0; stroke-width: 2.5; stroke-linecap: round; }

.case-quick-lookup__eyebrow,
.case-quick-lookup__choice-card header > span {
  margin: 0;
  color: #7dd3fc;
  font-size: 0.68rem;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.case-quick-lookup h1 {
  max-width: 46rem;
  margin: 0;
  color: #f8fafc;
  font-size: clamp(1.65rem, 4vw, 3.1rem);
  font-weight: 900;
  letter-spacing: -0.04em;
  line-height: 1.06;
}

.case-quick-lookup__choice-card h1 { font-size: clamp(1.45rem, 3vw, 2.2rem); }

.case-quick-lookup__lead,
.case-quick-lookup__choice-card header p,
.case-quick-lookup__progress-card > p,
.case-quick-lookup__result-card > p {
  max-width: 43rem;
  margin: 0;
  color: #aebed2;
  font-size: clamp(0.82rem, 1.6vw, 1rem);
  font-weight: 560;
  line-height: 1.58;
}

.case-quick-lookup__form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  width: 100%;
  margin-top: 0.75rem;
  gap: 0.55rem;
  border: 1px solid rgba(147, 197, 253, 0.26);
  border-radius: 18px;
  background: rgba(2, 6, 23, 0.55);
  padding: 0.48rem;
  box-shadow: 0 18px 55px rgba(2, 6, 23, 0.34), 0 0 0 1px rgba(37, 99, 235, 0.04);
}

.case-quick-lookup__form:focus-within {
  border-color: rgba(56, 189, 248, 0.72);
  box-shadow: 0 18px 55px rgba(2, 6, 23, 0.34), 0 0 0 4px rgba(14, 165, 233, 0.11);
}

.case-quick-lookup__form input {
  min-width: 0;
  min-height: 3.35rem;
  border: 0;
  outline: 0;
  background: transparent;
  color: #f8fafc;
  padding: 0 0.85rem;
  font: 0.9rem/1.3 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
}

.case-quick-lookup__form input::placeholder { color: #64748b; }

.case-quick-lookup__form button,
.case-quick-lookup__primary-btn,
.case-quick-lookup__secondary-btn,
.case-quick-lookup__text-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 2.65rem;
  border-radius: 13px;
  cursor: pointer;
  padding: 0.55rem 1rem;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 850;
}

.case-quick-lookup__form button,
.case-quick-lookup__primary-btn {
  border: 1px solid #60a5fa;
  background: linear-gradient(135deg, #2563eb, #0284c7);
  color: #fff;
  box-shadow: 0 10px 26px rgba(37, 99, 235, 0.26);
}

.case-quick-lookup__form button { min-width: 8.1rem; }
.case-quick-lookup__form button svg { width: 1.05rem; height: 1.05rem; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

.case-quick-lookup__form button:hover:not(:disabled),
.case-quick-lookup__primary-btn:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
.case-quick-lookup__form button:disabled,
.case-quick-lookup__primary-btn:disabled,
.case-quick-lookup__secondary-btn:disabled,
.case-quick-lookup__text-btn:disabled { cursor: not-allowed; opacity: 0.48; transform: none; }

.case-quick-lookup__secondary-btn {
  border: 1px solid rgba(148, 163, 184, 0.26);
  background: rgba(30, 41, 59, 0.78);
  color: #dbeafe;
}

.case-quick-lookup__text-btn {
  min-height: auto;
  border: 0;
  background: transparent;
  color: #7dd3fc;
  padding: 0.45rem 0.6rem;
}

.case-quick-lookup__secondary-btn:hover:not(:disabled),
.case-quick-lookup__text-btn:hover:not(:disabled) { color: #fff; border-color: rgba(125, 211, 252, 0.52); }

.case-quick-lookup__input-note {
  margin: 0;
  color: #71839a;
  font-size: 0.68rem;
  font-weight: 650;
}

.case-quick-lookup__notice {
  display: grid;
  width: 100%;
  gap: 0.12rem;
  margin-top: 0.35rem;
  border: 1px solid rgba(248, 113, 113, 0.28);
  border-radius: 13px;
  background: rgba(127, 29, 29, 0.18);
  padding: 0.65rem 0.75rem;
  text-align: left;
}

.case-quick-lookup__notice strong { color: #fecaca; font-size: 0.76rem; }
.case-quick-lookup__notice span { color: #fca5a5; font-size: 0.72rem; line-height: 1.4; }

.case-quick-lookup__choice-card header { display: grid; gap: 0.36rem; }
.case-quick-lookup__choice-filter { display: grid; gap: 0.36rem; }
.case-quick-lookup__choice-filter > label { color: #cbd5e1; font-size: 0.72rem; font-weight: 800; }
.case-quick-lookup__choice-filter-control { position: relative; display: flex; align-items: center; }
.case-quick-lookup__choice-filter-control > svg { position: absolute; left: 0.78rem; width: 1rem; height: 1rem; fill: none; stroke: #64748b; stroke-width: 1.8; stroke-linecap: round; pointer-events: none; }
.case-quick-lookup__choice-filter-control input { width: 100%; min-height: 2.75rem; border: 1px solid rgba(148, 163, 184, 0.26); border-radius: 13px; background: rgba(2, 6, 23, 0.66); color: #f8fafc; padding: 0.65rem 2.7rem 0.65rem 2.35rem; font: 0.78rem/1.3 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; }
.case-quick-lookup__choice-filter-control input::placeholder { color: #64748b; }
.case-quick-lookup__choice-filter-control input:focus { border-color: rgba(56, 189, 248, 0.82); outline: none; box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.12); }
.case-quick-lookup__choice-filter-control > button { position: absolute; right: 0.42rem; display: grid; width: 2rem; height: 2rem; place-items: center; border: 0; border-radius: 9px; background: transparent; color: #94a3b8; cursor: pointer; padding: 0; }
.case-quick-lookup__choice-filter-control > button:hover,
.case-quick-lookup__choice-filter-control > button:focus-visible { background: rgba(51, 65, 85, 0.72); color: #f8fafc; outline: none; }
.case-quick-lookup__choice-filter-control > button svg { width: 0.9rem; height: 0.9rem; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; }
.case-quick-lookup__choice-filter > small { color: #7c8ca1; font-size: 0.68rem; }
.case-quick-lookup__choice-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.55rem; max-height: min(42vh, 28rem); overflow: auto; padding: 0.15rem; }
.case-quick-lookup__choice {
  display: grid;
  min-width: 0;
  gap: 0.25rem;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 15px;
  background: rgba(15, 23, 42, 0.7);
  color: #e2e8f0;
  cursor: pointer;
  padding: 0.8rem;
  text-align: left;
}
.case-quick-lookup__choice:hover,
.case-quick-lookup__choice:focus-visible { border-color: rgba(96, 165, 250, 0.72); outline: none; }
.case-quick-lookup__choice--selected { border-color: #38bdf8; background: rgba(3, 105, 161, 0.24); box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.1); }
.case-quick-lookup__choice span { color: #7dd3fc; font-size: 0.62rem; font-weight: 900; letter-spacing: 0.08em; text-transform: uppercase; }
.case-quick-lookup__choice strong { color: #f8fafc; font-size: 0.9rem; }
.case-quick-lookup__choice small { overflow: hidden; color: #94a3b8; font: 0.68rem/1.35 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; text-overflow: ellipsis; white-space: nowrap; }
.case-quick-lookup__choice-empty { display: grid; justify-items: center; gap: 0.35rem; border: 1px dashed rgba(148, 163, 184, 0.28); border-radius: 15px; background: rgba(15, 23, 42, 0.4); padding: 1.1rem; text-align: center; }
.case-quick-lookup__choice-empty strong { color: #e2e8f0; font-size: 0.82rem; }
.case-quick-lookup__choice-empty span { color: #94a3b8; font-size: 0.72rem; }
.case-quick-lookup__choice-empty button { margin-top: 0.2rem; }
.case-quick-lookup__choice-actions,
.case-quick-lookup__result-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 0.5rem; margin-top: 0.35rem; }

.case-quick-lookup__progress-card > small { color: #7c8ca1; font-size: 0.7rem; line-height: 1.45; }
.case-quick-lookup__progress-card code,
.case-quick-lookup__result-card code { max-width: 100%; overflow: hidden; border: 1px solid rgba(148, 163, 184, 0.14); border-radius: 999px; background: rgba(2, 6, 23, 0.46); color: #93c5fd; padding: 0.35rem 0.7rem; font-size: 0.68rem; text-overflow: ellipsis; white-space: nowrap; }
.case-quick-lookup__spinner { position: relative; display: flex; width: 4rem; height: 4rem; align-items: center; justify-content: center; gap: 0.22rem; border: 1px solid rgba(96, 165, 250, 0.22); border-radius: 999px; background: rgba(30, 64, 175, 0.12); }
.case-quick-lookup__spinner span { width: 0.34rem; height: 0.34rem; border-radius: 999px; background: #38bdf8; animation: caseQuickLookupPulse 1.1s ease-in-out infinite; }
.case-quick-lookup__spinner span:nth-child(2) { animation-delay: 0.15s; }
.case-quick-lookup__spinner span:nth-child(3) { animation-delay: 0.3s; }
@keyframes caseQuickLookupPulse { 0%, 100% { opacity: 0.25; transform: translateY(0); } 50% { opacity: 1; transform: translateY(-0.22rem); } }
.case-quick-lookup__meter { width: 100%; height: 0.48rem; overflow: hidden; border-radius: 999px; background: rgba(51, 65, 85, 0.78); }
.case-quick-lookup__meter span { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #2563eb, #38bdf8, #22c55e); transition: width 0.28s ease; }

.case-quick-lookup__result-icon { display: grid; width: 4.1rem; height: 4.1rem; place-items: center; border: 1px solid rgba(74, 222, 128, 0.36); border-radius: 999px; background: rgba(22, 101, 52, 0.2); color: #86efac; }
.case-quick-lookup__result-card--partial .case-quick-lookup__result-icon { border-color: rgba(251, 191, 36, 0.38); background: rgba(146, 64, 14, 0.2); color: #fde68a; }
.case-quick-lookup__result-icon svg { width: 2rem; height: 2rem; fill: none; stroke: currentColor; stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round; }

.case-quick-lookup__case-pill {
  position: fixed;
  left: clamp(1rem, 3vw, 2.4rem);
  bottom: 1.15rem;
  display: inline-flex;
  max-width: min(20rem, calc(100vw - 11rem));
  align-items: center;
  gap: 0.45rem;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.52);
  color: #8293aa;
  padding: 0.4rem 0.65rem;
  font-size: 0.65rem;
  font-weight: 720;
  text-overflow: ellipsis;
  white-space: nowrap;
  backdrop-filter: blur(14px);
}
.case-quick-lookup__case-pill span { flex: 0 0 auto; width: 0.42rem; height: 0.42rem; border-radius: 999px; background: #22c55e; box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.12); }

.case-quick-lookup__utility-dock {
  position: fixed;
  right: clamp(1rem, 3vw, 2.4rem);
  bottom: 1rem;
  display: flex;
  gap: 0.45rem;
}
.case-quick-lookup__utility-dock button {
  display: inline-flex;
  width: 2.7rem;
  height: 2.7rem;
  box-sizing: border-box;
  flex: 0 0 2.7rem;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 13px;
  background: rgba(15, 23, 42, 0.72);
  color: #a5b4c7;
  cursor: pointer;
  appearance: none;
  padding: 0;
  line-height: 0;
  box-shadow: 0 14px 36px rgba(2, 6, 23, 0.28);
  backdrop-filter: blur(14px);
}
.case-quick-lookup__utility-dock button:hover:not(:disabled),
.case-quick-lookup__utility-dock button:focus-visible:not(:disabled) { border-color: rgba(125, 211, 252, 0.62); color: #e0f2fe; outline: none; }
.case-quick-lookup__utility-dock button:disabled { cursor: not-allowed; opacity: 0.42; }
.case-quick-lookup__utility-dock svg { display: block; width: 1.25rem; height: 1.25rem; flex: 0 0 auto; margin: 0; overflow: visible; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }

.case-quick-lookup__settings {
  position: fixed;
  right: clamp(1rem, 3vw, 2.4rem);
  bottom: 4.25rem;
  display: grid;
  width: min(22rem, calc(100vw - 2rem));
  max-height: calc(100vh - 6rem);
  gap: 0.65rem;
  overflow: auto;
  border: 1px solid rgba(125, 211, 252, 0.28);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.96);
  padding: 0.85rem;
  box-shadow: 0 25px 70px rgba(2, 6, 23, 0.58);
  backdrop-filter: blur(22px);
}
.case-quick-lookup__settings header { display: flex; min-height: 2.1rem; align-items: flex-start; justify-content: space-between; gap: 0.8rem; padding-right: 2.35rem; }
.case-quick-lookup__settings header > div { display: grid; gap: 0.1rem; }
.case-quick-lookup__settings header span { color: #7dd3fc; font-size: 0.6rem; font-weight: 900; letter-spacing: 0.1em; text-transform: uppercase; }
.case-quick-lookup__settings header strong { color: #f8fafc; font-size: 0.95rem; }
.case-quick-lookup__settings-close {
  position: absolute;
  top: 0.58rem;
  right: 0.58rem;
  z-index: 2;
  display: grid;
  width: 2rem;
  height: 2rem;
  box-sizing: border-box;
  place-items: center;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  appearance: none;
  padding: 0;
  line-height: 0;
}
.case-quick-lookup__settings-close:hover,
.case-quick-lookup__settings-close:focus-visible { background: rgba(148, 163, 184, 0.12); color: #f8fafc; outline: none; }
.case-quick-lookup__settings-close svg { display: block; width: 1rem; height: 1rem; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; }
.case-quick-lookup__settings > label { display: grid; gap: 0.28rem; color: #aebed2; font-size: 0.67rem; font-weight: 780; }
.case-quick-lookup__settings select,
.case-quick-lookup__settings input[type='number'] { width: 100%; min-height: 2.35rem; border: 1px solid #334155; border-radius: 10px; background: #0f172a; color: #e2e8f0; padding: 0.35rem 0.5rem; }
.case-quick-lookup__settings-check { grid-template-columns: auto minmax(0, 1fr); align-items: center; }
.case-quick-lookup__settings footer { display: flex; justify-content: flex-end; gap: 0.4rem; border-top: 1px solid rgba(148, 163, 184, 0.14); padding-top: 0.65rem; }

.case-simple-launch__eyebrow-action {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
  font: inherit;
}
.case-simple-launch__eyebrow-action:hover,
.case-simple-launch__eyebrow-action:focus-visible { color: #0ea5e9; text-decoration: underline; text-underline-offset: 0.2em; outline: none; }

@media (max-width: 720px) {
  .case-quick-lookup__topbar { align-items: flex-start; }
  .case-quick-lookup__brand > span:last-child { display: none; }
  .case-quick-lookup__view-nav { gap: 0.08rem; }
  .case-quick-lookup__view-nav button { padding: 0.38rem 0.5rem; font-size: 0.62rem; }
  .case-quick-lookup__main { align-items: start; padding-top: 2rem; }
  .case-quick-lookup__form { grid-template-columns: 1fr; }
  .case-quick-lookup__form button { min-height: 3rem; }
  .case-quick-lookup__choice-list { grid-template-columns: 1fr; }
  .case-quick-lookup__case-pill { max-width: calc(100vw - 8.5rem); }
}

@media (max-height: 650px) {
  .case-quick-lookup__topbar { padding-top: 0.65rem; padding-bottom: 0.4rem; }
  .case-quick-lookup__main { place-items: start center; padding-top: 0.5rem; }
  .case-quick-lookup__lookup-card,
  .case-quick-lookup__choice-card,
  .case-quick-lookup__progress-card,
  .case-quick-lookup__result-card { padding: 1rem 1.15rem; border-radius: 20px; }
  .case-quick-lookup__logo { width: 3.35rem; height: 3.35rem; }
  .case-quick-lookup__logo svg { width: 2.5rem; height: 2.5rem; }
}


/* Transaction Lookup: compact case switcher. */
.case-quick-lookup__case-switcher {
  position: fixed;
  left: clamp(1rem, 3vw, 2.4rem);
  bottom: 1.15rem;
  z-index: 4;
  max-width: min(22rem, calc(100vw - 11rem));
}
.case-quick-lookup__case-pill {
  position: static;
  display: inline-flex;
  width: auto;
  max-width: 100%;
  min-height: 2.15rem;
  box-sizing: border-box;
  align-items: center;
  gap: 0.45rem;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.62);
  color: #a9b7c9;
  cursor: pointer;
  appearance: none;
  padding: 0.4rem 0.62rem;
  font: inherit;
  font-size: 0.65rem;
  font-weight: 760;
  line-height: 1;
  box-shadow: 0 14px 36px rgba(2, 6, 23, 0.22);
  backdrop-filter: blur(14px);
}
.case-quick-lookup__case-pill:hover:not(:disabled),
.case-quick-lookup__case-pill:focus-visible:not(:disabled) {
  border-color: rgba(125, 211, 252, 0.52);
  color: #f8fafc;
  outline: none;
}
.case-quick-lookup__case-pill:disabled { cursor: not-allowed; opacity: 0.5; }
.case-quick-lookup__case-pill-dot {
  flex: 0 0 auto;
  width: 0.42rem;
  height: 0.42rem;
  border-radius: 999px;
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.12);
}
.case-quick-lookup__case-pill .case-quick-lookup__case-pill-label {
  width: auto;
  height: auto;
  flex: 1 1 auto;
  overflow: hidden;
  border-radius: 0;
  background: transparent;
  padding: 0;
  box-shadow: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.case-quick-lookup__case-pill-chevron {
  display: block;
  width: 0.82rem;
  height: 0.82rem;
  flex: 0 0 auto;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.case-quick-lookup__case-menu {
  position: absolute;
  left: 0;
  bottom: calc(100% + 0.55rem);
  display: grid;
  width: min(21rem, calc(100vw - 2rem));
  max-height: min(52vh, 23rem);
  overflow: hidden;
  border: 1px solid rgba(125, 211, 252, 0.24);
  border-radius: 17px;
  background: rgba(15, 23, 42, 0.97);
  box-shadow: 0 24px 70px rgba(2, 6, 23, 0.62);
  backdrop-filter: blur(22px);
}
.case-quick-lookup__case-menu > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  padding: 0.72rem 0.82rem 0.62rem;
}
.case-quick-lookup__case-menu > header strong { color: #f8fafc; font-size: 0.76rem; }
.case-quick-lookup__case-menu > header small { color: #7dd3fc; font-size: 0.66rem; font-weight: 850; }
.case-quick-lookup__case-menu-list { display: grid; max-height: min(38vh, 16rem); overflow: auto; padding: 0.34rem; }
.case-quick-lookup__case-menu-list button,
.case-quick-lookup__case-menu-add {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.58rem;
  width: 100%;
  border: 0;
  border-radius: 11px;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  padding: 0.58rem 0.62rem;
  text-align: left;
}
.case-quick-lookup__case-menu-list button:hover:not(:disabled),
.case-quick-lookup__case-menu-list button:focus-visible:not(:disabled),
.case-quick-lookup__case-menu-list button.is-current {
  background: rgba(30, 64, 175, 0.22);
  color: #f8fafc;
  outline: none;
}
.case-quick-lookup__case-menu-list button:disabled,
.case-quick-lookup__case-menu-add:disabled { cursor: not-allowed; opacity: 0.48; }
.case-quick-lookup__case-menu-list button > span:nth-child(2) { display: grid; min-width: 0; gap: 0.12rem; }
.case-quick-lookup__case-menu-list strong { overflow: hidden; font-size: 0.72rem; text-overflow: ellipsis; white-space: nowrap; }
.case-quick-lookup__case-menu-list small { color: #71839a; font-size: 0.61rem; }
.case-quick-lookup__case-menu-list svg { width: 1rem; height: 1rem; fill: none; stroke: #86efac; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.case-quick-lookup__case-menu-dot { width: 0.48rem; height: 0.48rem; border-radius: 999px; background: #475569; }
.case-quick-lookup__case-menu-list button.is-current .case-quick-lookup__case-menu-dot { background: #22c55e; box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.12); }
.case-quick-lookup__case-menu-add {
  grid-template-columns: 1.4rem minmax(0, 1fr);
  border-top: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 0;
  color: #bae6fd;
  padding: 0.68rem 0.82rem;
}
.case-quick-lookup__case-menu-add:hover:not(:disabled),
.case-quick-lookup__case-menu-add:focus-visible:not(:disabled) { background: rgba(14, 116, 144, 0.16); color: #f0f9ff; outline: none; }
.case-quick-lookup__case-menu-add span { display: grid; width: 1.35rem; height: 1.35rem; place-items: center; border-radius: 8px; background: rgba(56, 189, 248, 0.14); font-size: 1rem; line-height: 1; }
.case-quick-lookup__case-menu-add strong { font-size: 0.72rem; }

/* Full report package: lead with the report itself, then show package controls and status. */
.case-full-package-modal__post-preview {
  display: grid;
  gap: 0.8rem;
  margin-top: 0.9rem;
}
.case-full-package-modal__description {
  margin: 0;
  color: #64748b;
  font-size: 0.76rem;
  line-height: 1.5;
}

/* Case Management: keep thumbnails and at-a-glance identity visible; disclose the technical detail only on demand. */
.case-file-browser-modal--streamlined {
  width: min(72rem, calc(100vw - 2rem));
  max-height: min(92vh, 58rem);
  border: 1px solid #dbe4ef;
  background: #f8fafc;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.28);
}
.case-file-browser-modal--streamlined .case-file-browser-modal__header { padding-bottom: 0.55rem; }
.case-file-browser-modal--streamlined .case-file-browser-modal__header > div > p:last-child { max-width: 34rem; color: #64748b; }
.case-file-browser-quiet-summary { display: flex; align-items: baseline; gap: 0.32rem; color: #64748b; padding: 0.05rem 0.1rem 0.35rem; font-size: 0.7rem; }
.case-file-browser-quiet-summary strong { color: #0f172a; font-size: 0.88rem; }
.case-file-browser-controls--streamlined {
  display: grid;
  grid-template-columns: minmax(16rem, 1fr) auto auto;
  align-items: center;
  gap: 0.55rem;
}
.case-file-browser-controls--streamlined .case-file-browser-search { display: flex; min-width: 0; margin: 0; align-items: center; }
.case-file-browser-controls--streamlined .case-file-browser-search input {
  display: block;
  width: 100%;
  height: 2.55rem;
  min-height: 2.55rem;
  box-sizing: border-box;
  margin: 0;
  border-color: #cbd5e1;
  border-radius: 11px;
  background: #fff;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
}
.case-file-browser-new-case-btn {
  display: inline-flex;
  min-width: 7.2rem;
  height: 2.55rem;
  min-height: 2.55rem;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  box-sizing: border-box;
  white-space: nowrap;
}
.case-file-browser-new-case-btn svg { width: 1rem; height: 1rem; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; }
.case-file-browser-options-toggle {
  display: inline-flex;
  height: 2.55rem;
  min-height: 2.55rem;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  gap: 0.42rem;
  border: 1px solid #cbd5e1;
  border-radius: 11px;
  background: #fff;
  color: #475569;
  cursor: pointer;
  padding: 0.5rem 0.78rem;
  font-size: 0.72rem;
  font-weight: 800;
  white-space: nowrap;
}
.case-file-browser-options-toggle:hover, .case-file-browser-options-toggle:focus-visible { border-color: #94a3b8; background: #f8fafc; color: #1e293b; outline: none; }
.case-file-browser-options-toggle__chevron { color: #94a3b8; font-size: 0.78rem; line-height: 1; }
.case-file-browser-options__panel {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: repeat(2, minmax(10rem, 1fr));
  width: 100%;
  box-sizing: border-box;
  margin-top: -0.05rem;
  gap: 0.55rem;
  border: 1px solid #dbe4ef;
  border-radius: 14px;
  background: #fff;
  padding: 0.72rem;
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.12);
}
.case-file-browser-options__panel label { display: grid; gap: 0.24rem; color: #64748b; font-size: 0.66rem; font-weight: 800; }
.case-file-browser-options__panel select { width: 100%; min-height: 2.3rem; }
.case-file-browser-options__panel .toolbar-btn { min-height: 2.3rem; justify-content: center; }
.case-file-browser-modal--streamlined .case-file-browser-layout { min-height: 0; gap: 0.82rem; }
.case-file-browser-modal--streamlined .case-file-browser-list { min-width: 0; padding-right: 0.12rem; }
.case-file-browser-modal--streamlined .case-file-browser-card {
  grid-template-columns: minmax(8.75rem, 37%) minmax(0, 1fr);
  min-height: 7.5rem;
  align-items: stretch;
  gap: 0.72rem;
  border: 1px solid #dbe4ef;
  border-radius: 16px;
  background: #fff;
  padding: 0.58rem;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.055);
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}
.case-file-browser-modal--streamlined .case-file-browser-card:hover,
.case-file-browser-modal--streamlined .case-file-browser-card:focus-visible {
  border-color: #94a3b8;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.11);
  transform: translateY(-1px);
}
.case-file-browser-modal--streamlined .case-file-browser-card--selected {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.14), 0 10px 26px rgba(37, 99, 235, 0.12);
}
.case-file-browser-modal--streamlined .case-file-browser-card__thumb {
  display: grid;
  min-height: 6.35rem;
  place-items: center;
  overflow: hidden;
  border: 1px solid #dbe4ef;
  border-radius: 13px;
  background: radial-gradient(circle at 50% 46%, #ffffff 0, #f8fbff 52%, #eef4fb 100%);
}
.case-file-browser-preview-svg--mini { display: block; width: 100%; height: 100%; box-sizing: border-box; padding: 0.32rem; }
.case-file-browser-preview-bg { fill: transparent; }
.case-file-browser-preview-edge { fill: none; stroke: #94a3b8; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; }
.case-file-browser-preview-node--address circle { fill: #2563eb; stroke: #dbeafe; stroke-width: 4; }
.case-file-browser-preview-node--tx rect { fill: #facc15; stroke: #a16207; stroke-width: 3; }
.case-file-browser-preview-node--service circle { fill: #0f766e; stroke: #ccfbf1; }
.case-file-browser-preview-node--ending circle { stroke: #f59e0b; stroke-width: 5; }
.case-file-browser-modal--streamlined .case-file-browser-card__content { display: grid; align-content: center; gap: 0.5rem; min-width: 0; }
.case-file-browser-modal--streamlined .case-file-browser-card__content header { align-items: start; }
.case-file-browser-modal--streamlined .case-file-browser-card__content h4 { color: #0f172a; line-height: 1.2; }
.case-file-browser-card__at-a-glance { display: flex; flex-wrap: wrap; align-items: center; gap: 0.35rem; color: #475569; font-size: 0.7rem; }
.case-file-browser-card__at-a-glance span { display: inline-flex; align-items: baseline; gap: 0.22rem; }
.case-file-browser-card__at-a-glance strong { color: #0f172a; font-size: 0.86rem; }
.case-file-browser-card__at-a-glance em { border-radius: 999px; background: #edf4ff; color: #315c9d; padding: 0.22rem 0.42rem; font-size: 0.61rem; font-style: normal; font-weight: 800; }
.case-file-browser-card__saved { color: #94a3b8; font-size: 0.62rem; }
.case-file-browser-details {
  border: 1px solid #dbe4ef;
  border-radius: 17px;
  background: #fff;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
}
.case-file-browser-details__title { align-items: flex-start; gap: 0.8rem; }
.case-file-browser-details__actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 0.42rem; }
.case-file-browser-delete-case-btn { background: #fff; }
.case-file-browser-details__summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.45rem; }
.case-file-browser-details__summary article { display: grid; min-width: 0; gap: 0.16rem; border: 1px solid #e2e8f0; border-radius: 11px; background: #f8fafc; padding: 0.52rem 0.58rem; }
.case-file-browser-details__summary strong { overflow: hidden; color: #0f172a; font-size: 0.82rem; text-overflow: ellipsis; white-space: nowrap; }
.case-file-browser-details__summary span { color: #64748b; font-size: 0.58rem; font-weight: 760; text-transform: uppercase; letter-spacing: 0.04em; }
.case-file-browser-preview-panel { overflow: hidden; border: 1px solid #dbe4ef; border-radius: 15px; background: #f8fafc; }
.case-file-browser-preview-stage { background: radial-gradient(circle at 50% 45%, #ffffff 0, #f8fbff 58%, #eef4fb 100%); }
.case-file-browser-details__more { border: 1px solid #e2e8f0; border-radius: 13px; background: #fff; }
.case-file-browser-details__more > summary { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; cursor: pointer; list-style: none; padding: 0.68rem 0.78rem; }
.case-file-browser-details__more > summary::-webkit-details-marker { display: none; }
.case-file-browser-details__more > summary::after { content: '⌄'; color: #94a3b8; font-size: 0.9rem; }
.case-file-browser-details__more[open] > summary::after { content: '⌃'; }
.case-file-browser-details__more > summary span { color: #334155; font-size: 0.72rem; font-weight: 850; }
.case-file-browser-details__more > summary small { margin-left: auto; color: #94a3b8; font-size: 0.6rem; text-align: right; }
.case-file-browser-details__more-body { display: grid; gap: 0.75rem; border-top: 1px solid #e2e8f0; padding: 0.75rem; }

@media (max-width: 900px) {
  .case-file-browser-controls--streamlined { grid-template-columns: minmax(0, 1fr) auto auto; }
  .case-file-browser-details__summary { grid-template-columns: 1fr; }
}

@media (max-width: 720px) {
  .case-quick-lookup__case-switcher { max-width: calc(100vw - 8.5rem); }
  .case-file-browser-controls--streamlined { grid-template-columns: minmax(0, 1fr) auto; }
  .case-file-browser-controls--streamlined .case-file-browser-search { grid-column: 1 / -1; }
  .case-file-browser-new-case-btn { grid-column: 1; justify-self: start; }
  .case-file-browser-options-toggle { grid-column: 2; }
  .case-file-browser-options__panel { grid-column: 1 / -1; grid-template-columns: 1fr; }
  .case-file-browser-modal--streamlined .case-file-browser-card { grid-template-columns: 7rem minmax(0, 1fr); }
  .case-file-browser-details__title { align-items: stretch; }
  .case-file-browser-details__actions { justify-content: flex-start; }
  .case-file-browser-details__more > summary small { display: none; }
}

@media (max-width: 480px) {
  .case-file-browser-controls--streamlined { grid-template-columns: 1fr 1fr; }
  .case-file-browser-new-case-btn,
  .case-file-browser-options-toggle { width: 100%; min-width: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .case-quick-lookup__spinner span { animation: none; opacity: 0.8; }
  .case-quick-lookup__meter span,
  .case-quick-lookup__form button,
  .case-quick-lookup__primary-btn { transition: none; }
}


/* Collapsed outer sections act like one large disclosure target. The existing
   top-right controls remain available and stop propagation so closing a section
   does not immediately reopen it through the header click handler. */
.case-foldout-section-header--clickable {
  cursor: pointer;
  transition: background-color 0.16s ease, box-shadow 0.16s ease;
}

.case-foldout-section-header--clickable:hover {
  background: rgba(248, 250, 252, 0.78);
}

.case-foldout-section-header--clickable:focus-visible {
  outline: 2px solid rgba(37, 99, 235, 0.72);
  outline-offset: 3px;
}

/* The scrolling section rail is an optional advanced-workspace aid. New users
   get a single-column workspace by default; the right Workflow Assistant keeps
   its existing position whenever it is present. */
.case-page-shell.case-page-shell--without-left-nav {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  column-gap: 0;
}

.case-page-shell--without-left-nav > .case-page-main {
  grid-column: 1;
  min-width: 0;
}

.case-page-shell.case-page-shell--without-left-nav.case-page-shell--with-intake-rail {
  grid-template-columns: minmax(0, 1fr) clamp(14.5rem, 17vw, 18rem);
  column-gap: 1rem;
}

.case-page-shell--without-left-nav.case-page-shell--with-intake-rail > .case-page-main {
  grid-column: 1;
}

.case-page-shell--without-left-nav.case-page-shell--with-intake-rail > .case-intake-rail {
  grid-column: 2;
}

@media (max-width: 1180px) {
  .case-page-shell.case-page-shell--without-left-nav.case-page-shell--with-intake-rail {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 0.75rem;
  }

  .case-page-shell--without-left-nav.case-page-shell--with-intake-rail > .case-intake-rail {
    grid-column: 1;
    grid-row: 1;
  }

  .case-page-shell--without-left-nav.case-page-shell--with-intake-rail > .case-page-main {
    grid-column: 1;
    grid-row: 2;
  }
}


/* Advanced Workspace responsive layout -----------------------------------
   Desktop keeps the two sticky side rails. Once the Workflow Assistant moves
   above the workspace, it must return to ordinary document flow instead of
   remaining a sticky overlay. On phone-sized screens every primary region is
   stacked in one real column, and the optional section navigation becomes a
   compact horizontal scroller rather than reserving a side column. */
@media (max-width: 1180px) {
  .case-page-shell.case-page-shell--with-intake-rail > .case-intake-rail,
  .case-page-shell.case-page-shell--without-left-nav.case-page-shell--with-intake-rail > .case-intake-rail {
    position: static !important;
    inset: auto !important;
    top: auto !important;
    right: auto !important;
    bottom: auto !important;
    left: auto !important;
    z-index: auto;
    width: 100%;
    max-width: 100%;
    max-height: none !important;
    overflow: visible;
    align-self: stretch;
    box-sizing: border-box;
    transform: none !important;
  }
}

@media (max-width: 900px) {
  .case-page-shell,
  .case-page-shell.case-page-shell--with-intake-rail,
  .case-page-shell.case-page-shell--without-left-nav,
  .case-page-shell.case-page-shell--without-left-nav.case-page-shell--with-intake-rail {
    display: flex !important;
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .case-page-shell > .case-scroll-nav:not(.case-intake-rail) {
    position: static !important;
    inset: auto !important;
    top: auto !important;
    right: auto !important;
    bottom: auto !important;
    left: auto !important;
    order: 1;
    display: flex;
    flex-flow: row nowrap;
    align-items: stretch;
    align-self: stretch;
    gap: 0.32rem;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    height: auto;
    min-height: 0;
    max-height: none;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0.38rem;
    box-sizing: border-box;
    transform: none !important;
    overscroll-behavior-inline: contain;
    scrollbar-width: thin;
    -webkit-overflow-scrolling: touch;
  }

  .case-page-shell > .case-scroll-nav:not(.case-intake-rail) .case-scroll-nav__item {
    display: inline-grid;
    flex: 0 0 4rem;
    grid-template-rows: auto auto;
    place-items: center;
    gap: 0.12rem;
    width: 4rem;
    min-width: 4rem;
    min-height: 3.15rem;
    margin: 0;
    padding: 0.34rem 0.4rem;
    box-sizing: border-box;
    white-space: nowrap;
  }

  .case-page-shell > .case-scroll-nav:not(.case-intake-rail) .case-scroll-nav__icon {
    line-height: 1;
  }

  .case-page-shell > .case-scroll-nav:not(.case-intake-rail) .case-scroll-nav__label {
    max-width: 100%;
    overflow: hidden;
    font-size: 0.61rem;
    line-height: 1.05;
    text-overflow: ellipsis;
  }

  .case-page-shell > .case-intake-rail {
    position: static !important;
    inset: auto !important;
    top: auto !important;
    right: auto !important;
    bottom: auto !important;
    left: auto !important;
    order: 2;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    height: auto;
    min-height: 0;
    max-height: none !important;
    overflow: visible !important;
    align-self: stretch;
    box-sizing: border-box;
    transform: none !important;
  }

  .case-page-shell > .case-page-main {
    order: 3;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }
}

@media (max-width: 520px) {
  .case-page-shell > .case-scroll-nav:not(.case-intake-rail) {
    gap: 0.24rem;
    padding: 0.3rem;
  }

  .case-page-shell > .case-scroll-nav:not(.case-intake-rail) .case-scroll-nav__item {
    flex-basis: 3.55rem;
    width: 3.55rem;
    min-width: 3.55rem;
    min-height: 2.9rem;
    padding-inline: 0.28rem;
  }

  .case-scroll-nav.case-intake-rail {
    padding: 0.62rem;
  }
}

</style>
